import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import taraDesignStyles from './tara-design.css?raw';
import taraV14Styles from './tara-v14.css?raw';
import taraV145Styles from './tara-v145.css?raw';
import {TaraStorageStatus,EvidenceMetrics,RecordInspector,RecordVersionFilter,TaraEvidenceAnalytics,TaraBrainSummary,HourlyApprovalSummary,TaraSignalsSummary,TaraDetailEnvelope,PaperPerformanceSummary} from './TaraPresentation.jsx';
import {capturedReason,recordVersion,presentationRows} from './presentationEvidence.js';
import { normalizeKalshiPositionsPage } from './kalshiPositions.js';
import { normalizeHourlyRecord } from './hourlyRecordMath.js';
import {HOURLY_POLL_MS,readHourlyPolicy,normalizeHourlyMarkets,assessHourlyPick,advanceHourlyConfirmation,
  hourlyProposal,revalidateHourlyProposal,hourlyRecordGuard,inspectHourlySettlement} from './hourlyDecision.js';
import {loadSportsBoard,sportsSettlementStatus,sportsFreshnessStatus,sportsPaperSettlementStatus} from './sportsData.js';
import {gradeWeatherPick,mergeWeatherRecords} from './weatherRecord.js';
import {primaryShadowView} from './primaryShadow.js';
import {persistCallCaches,updateIndexedRecord,mergeDurableCallArchive} from './callStorage.js';
import {lifecycleAuditState} from './lifecycleAudit.js';
import {useStartupReadiness} from './startupReadiness.js';
import {describeCallBlocker} from './callBlocker.js';
import {callLogSyncSignature,callLogSyncStatus,callLogCloudPayload,callLogPublicationKey} from './callLogSync.js';
import {cloudSyncHealth} from './cloudSyncHealth.js';
import {cloudWriteRetryDelay,boundedCloudWriteQuery} from './cloudWriteRecovery.js';
import {postProcessLegacyCall,readCallCostBand} from './callCandidateRules.js';
import {readCapturedCallSettlement} from './callSettlement.js';
import {coinbaseCandleRange} from './candleRequests.js';
import { useCallLedger } from './useCallLedger.js';
import { CALL_EVIDENCE_FIELDS, callEvidenceSignature, callKey, amendCallRecord, captureOriginalDecision, hydrateDecisionQuote, inspectDecisionQuote, isDirection, isScoredCall, normalizeCallLedger, settlementPatch,applyAutomaticCallSettlement,windowCloseMs } from './callIntegrity.js';
import { useLockStudy } from './useLockStudy.js';
import {useLearningRegistry} from './useLearningRegistry.js';
import {LearningStatus} from './LearningStatus.jsx';
import {LEARNING_PROTOCOL,directionFeatures,learningSummarySource,preferLearningSummary,verifiedSummaryCall} from './learningCore.js';
import {learningPredictions,capturePickLearning,capturePickSettlement} from './learningCapture.js';
import LockStudyPanel from './LockStudyPanel.jsx';
import './lockStudy.css';
import {useEconomicCalendar,computeEconCalendarRisk,getMacroEventState,getUpcomingMacroEvents} from './useEconomicCalendar.js';
import {dampenScore} from './economicCalendar.js';
import MarketIntelligencePanel from './MarketIntelligencePanel.jsx';
import {assessSpotResponse, freshOkxResults, freshFeedStatus, observedFundingPair} from './marketDataQuality.js';
import {buildExchangeAudit, callWindowCoverage, checkEntryMode} from './executionIntegrity.js';
import {buildSafeEntry,buildSafeExit,normalizeExecutionOrder,runSafeEntryLadder,heldQuote,executionFeeCents,fetchAllExecutionPages,accountRiskSnapshot,affordableContracts,summarizeOrderFills} from './executionSafety.js';
import {executionIntentId,stableExecutionId,reserveExecutionIntent,updateExecutionIntent,readExecutionState,writeExecutionState,restoreExecutionLedger} from './executionJournal.js';
import {capturePaperMarketIdentity,isUnresolvedPaperFill,recoverExpiredPaperExecutions} from './paperExpiry.js';
import {commitHourlyAdmission,supabaseHourlyTransport} from './hourlyAdmission.js';
import {hourlyPublicPaths,readHourlyPublic} from './hourlyPublic.js';
import {API_BASE} from './apiBase.js';
import {useDiscordCalls} from './useDiscordCalls.js';
import DiscordDeliveryPanel from './DiscordDeliveryPanel.jsx';
import {discordDestination,verifyDiscordDestination} from './discordDelivery.js';
import {coachCondition} from './discordAdvisories.js';
import {ALLOW_CLOCK_FORCED_CALLS, CALL_POLICY_VERSION, assessCallCommit, callLockDeadline, deadlineSitout, directionalConfidence} from './callPolicy.js';
import {forecastSecondsRemaining,cappedForecastBars,forecastAvailability,quietWindowEvidence} from './forecastTiming.js';
import {callDecisionEvidence,advanceCallFormation,callFormationReady,canonicalRegimeFlags} from './decisionAuthority.js';
import {observeEarlyFlatGap,earlyFlatGapReady} from './earlyCallConfirmation.js';
import {appendDecisionTrace,captureDecisionTrace} from './callDecisionTrace.js';
import {sharedWindowId,matchesSharedWindow,restoredSnapshot,ledgerDecision,chooseSharedSnapshot,sharedSnapshotIdentity,restoredEngineLock,
  readDecisionCache,cacheSharedDecision,publishSharedDecision,activeCallSnapshot,finalizeClosedSitouts} from './sharedDecision.js';
import {journalCapturedCall,recoverCapturedCalls,acknowledgeCapturedCalls} from './callCaptureJournal.js';
// V10.2.0: Firestore RETIRED. Supabase is now the only cloud backend.
//   Removed imports: 'firebase/app', 'firebase/firestore'. The Firebase package
//   may still be in package.json but is no longer imported or used at runtime.
//   Was a parallel-write backend during Sessions 1-2; now fully removed in Session 3+4.
import { createClient as createSupabaseClient } from '@supabase/supabase-js';

// ═══════════════════════════════════════
// V5.6: FIRESTORE SYNC LAYER
// ═══════════════════════════════════════
// Cloud-backed persistence for Tara. Fixes the "refresh-roulette" problem where Tara's Call
// and the engine lock both reset on page reload because they lived only in React refs.
//
// Model: localStorage stays as the fast-paint cache so first paint is instant. Firestore
// is the source of truth — its read overwrites local state ~200-500ms after mount when
// fresher data exists. Writes go to both stores (localStorage immediately, Firestore
// debounced 300-1500ms depending on path).
//
// V5.6.3: SHARED TARA. Previously every userId was its own namespace. Now there is one
// global Tara — every browser, every device, every user who opens the app sees the same
// record, the same memory, the same lock state. Paths are 2-segment globals.
//
// Paths (global, no namespace):
//   state/currentLock     → engine lockedCallRef + Tara snapshot for the active window.
//                           windowId-keyed so a refresh during the same window restores
//                           exactly what was on screen before reload. Stale locks from
//                           previous windows are ignored on restore.
//   scorecards/tara       → Tara's Call W/L/sitouts per window-type.
//   memory/taraCallLog    → array of every call she's made (capped at 500).
//   meta/info             → baselineVersion + lastSeenAt for migration tracking.
//
// NOT cloud-synced (per V5.5b decision — user-trade-driven training stays ephemeral):
//   adaptiveWeights, regimeWeights, tradeLog. These remain in-memory only this session.
//
// Test-mode caveat: Firestore rules are wide open for ~30 days. Lock down in V5.7 with
// proper rules.
const FIREBASE_CONFIG=null; // V10.2.0: retired. Kept declared to avoid breaking any leftover references; set to null permanently.
let _fbApp=null,_fbDb=null; // V10.2.0: permanently null. All cloud ops now route through Supabase. See `_sbClient` below.

// ═══════════════════════════════════════════════════════════════════════════
// V10.1.0 — SUPABASE MIGRATION SPEC (locked 2026-05-14)
// ═══════════════════════════════════════════════════════════════════════════
//
// MOTIVATION
//   Firestore free tier (50K reads/day, 20K writes/day) hit quota repeatedly
//   on 2026-05-14 due to taraWeights / regimeMemory / pastWindows writes
//   firing on every state change. RMW transactions count both a read AND a
//   write per call, so the actual quota burn was ~2x the visible write rate.
//   Symptoms: 'resource-exhausted' errors, cross-device sync silently dropping
//   updates, baseline drift between devices.
//
//   Supabase free tier offers 500MB Postgres + unlimited API calls (measured
//   in egress, not request count) which is far more generous for our use case.
//   This migration moves all cloud sync from Firestore to Supabase while
//   preserving the existing "Shared Tara" model (no auth, cross-device,
//   cross-user — anyone with the deployment URL sees the same data).
//
// SCOPE
//   IN: All 6 Firestore paths get migrated to Supabase:
//       memory/taraCallLog        → tara_state row, doc_path='memory/taraCallLog'
//       state/lifetimePnL         → tara_state row, doc_path='state/lifetimePnL'
//       history/pastWindows       → tara_state row, doc_path='history/pastWindows'
//       learnings/tara            → tara_state row, doc_path='learnings/tara'
//       learnings/regimeMemory    → tara_state row, doc_path='learnings/regimeMemory'
//       learnings/taraWeights     → tara_state row, doc_path='learnings/taraWeights'
//       baseline/canonical        → tara_state row, doc_path='baseline/canonical'
//       state/currentLock         → tara_state row, doc_path='state/currentLock'
//       scorecards/tara           → tara_state row, doc_path='scorecards/tara'
//       meta/info                 → tara_state row, doc_path='meta/info'
//
//   ALSO IN: localStorage keys that benefit from cross-device sync:
//       tara_autoexec_v1, tara_settings_v1, tara_scalper_v1, tara_mission_v1,
//       tara_speed_dial, tara_attempted_windows_v1, tara_kill_switch_v1,
//       tara_anti_tilt_v1 → mirrored to tara_state with localStorage_* prefix
//
//   OUT: tara_kalshi_creds_v1 (RSA private key) — stays localStorage-ONLY.
//        Per user decision 2026-05-14: a Supabase compromise must NOT enable
//        unauthorized order placement. Kalshi auth must be re-entered per
//        device. This is the only intentional cross-device friction.
//
// SCHEMA (single table, JSON-per-path, drop-in Firestore replacement)
//   create table tara_state (
//     doc_path    text primary key,           -- e.g. 'memory/taraCallLog'
//     data        jsonb not null,             -- the document body
//     updated_at  timestamptz default now() not null,
//     updated_by  text                        -- optional device fingerprint
//   );
//   alter table tara_state enable row level security;
//   create policy "shared tara read"   on tara_state for select using (true);
//   create policy "shared tara write"  on tara_state for insert with check (true);
//   create policy "shared tara update" on tara_state for update using (true);
//
// AUTH MODEL
//   No sign-in. Single shared global namespace. Anonymous anon-key client.
//   Matches existing Firestore "Shared Tara" pattern. Anyone with the
//   deployment URL has full read/write to the dataset. This is intentional —
//   the user has been operating this way and confirmed they want to keep it.
//
// CONCURRENCY (RMW replacement)
//   Firestore had runTransaction (atomic read-modify-write). Postgres uses
//   upsert with timestamp conflict resolution:
//
//     insert into tara_state (doc_path, data, updated_at)
//     values ($path, $merged_data, now())
//     on conflict (doc_path) do update
//       set data = excluded.data,
//           updated_at = now()
//       where tara_state.updated_at < excluded.updated_at_local;
//
//   The client passes its local 'updated_at' (when it last knew the cloud
//   state) so the SQL only commits if no newer write has happened since.
//   On rejection, client re-reads + re-merges + retries (up to 5x). This
//   matches Firestore's runTransaction semantics.
//
//   For the call log specifically, the merge happens CLIENT-SIDE (same merge
//   function used by existing cloudWriteDebouncedRMW callers). Server just
//   stores the merged blob.
//
// PARALLEL-WRITE PHASE (this is what protects us during migration)
//   During Sessions 2-3, BOTH Firestore AND Supabase get every write. If one
//   fails, the other still has the data. If we screw up Supabase code, we
//   lose nothing — Firestore is still primary. Only after we've verified
//   Supabase has full parity do we flip the primary reader, and only after
//   running stable for a few days do we remove Firestore code.
//
// MIGRATION ORDER (across sessions)
//   Session 1 (CURRENT): spec lock, SQL provided, client helpers built,
//                        parallel-write wired in for state/lifetimePnL only
//                        as proof of life. Firestore remains primary reader.
//   Session 2:           parallel-write extended to taraCallLog + all
//                        learnings. One-time migration script for existing
//                        Firestore data (read all 700+ trades, write to
//                        Supabase). Run migration. Verify counts match.
//   Session 3:           flip primary reader to Supabase. Firestore writes
//                        continue as backup. Sync tab UI reads from Supabase.
//   Session 4:           remove Firestore code entirely. Drop firebase npm
//                        package. Clean up FIREBASE_CONFIG, _fbDb references.
//                        Settings UI cleanup.
//
// HELPER API (drop-in Firestore replacements — preserve all signatures)
//   cloudWrite(path, data)                 — fire-and-forget write
//   cloudRead(path) → Promise<data|null>   — one-shot read
//   cloudWatch(path, callback) → unsub     — subscribe to changes
//   cloudWriteDebouncedRMW(path, getLocalData, mergeFn, delayMs?)  — atomic RMW
//   cloudDelete(path)                      — delete a doc
//
//   Every existing call site continues to work without modification during
//   the parallel-write phase. New Supabase versions live alongside Firestore
//   versions and are invoked together by a wrapper.
//
// SUCCESS CRITERIA
//   1. Sync tab shows non-zero counts from Supabase
//   2. Call log entries from one device appear on another within ~30s
//   3. Lifetime P&L matches between devices
//   4. Force Resync / Save Baseline / Apply Baseline all work as today
//   5. No new console errors from Supabase code
//   6. Firestore quota errors stop (or at minimum, are not user-facing)
//
// ROLLBACK
//   At any point in Sessions 1-3, removing the Supabase wrapper call leaves
//   Firestore fully functional. The Supabase code is purely additive until
//   Session 3's primary-reader flip. Session 4 is the only one-way step.
//
// ═══════════════════════════════════════════════════════════════════════════

// Supabase project credentials (V10.1.0). Both are public-safe — the anon
//   key is JWT-encoded and designed for client bundles. RLS policies control
//   what the anon role can actually do (read/write the tara_state table).
const SUPABASE_URL='https://vhbbkqmyyzddhezdgszm.supabase.co';
const SUPABASE_ANON_KEY='eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6InZoYmJrcW15eXpkZGhlemRnc3ptIiwicm9sZSI6ImFub24iLCJpYXQiOjE3Nzg3ODMzNjUsImV4cCI6MjA5NDM1OTM2NX0.30zC6hbGEcCNSuOQNRfh81_3B0nwVDuUJeeqUydZCoM';
// V10.7.85: Supabase sync paused — row limit hit, renews approx 2026-06-18.
//   All trade data saves to localStorage as always. Set false to re-enable cloud sync.
// V13.4.14: _SB_PAUSED=true — RE-PAUSED. API logs show every Supabase call
//   (reads, writes, realtime) returning HTTP 402 Payment Required continuously
//   for 78+ min - project suspended over billing, matching V10.9.15's prior
//   5GB egress-cap incident. Pausing here so the app degrades gracefully to
//   local-only instead of blindly retrying a dead backend on every tick.
//   ALSO shipped this version: memory/taraCallLog outgoing cloud writes are now
//   capped at _TARA_CLOUD_LOG_CAP everywhere (previously only the main 60s sync
//   path capped it - baseline-save/apply, force-push, and the import/hydration
//   flush paths were writing the full uncapped log). This does NOT touch local
//   storage (IndexedDB stays unlimited, taraCallLog state stays TARA_CALL_LOG_CAP)
//   or baseline/canonical (still the full backup) — only what gets echoed to the
//   live cloud doc that every device realtime listener reads. The receive side
//   already merges (union) rather than replaces, so a smaller cloud doc cannot
//   drop entries a device already has locally — confirmed by re-reading the
//   cloudWatch handler before making this change.
//   ACTION NEEDED (user): resolve billing in the Supabase dashboard. Once fixed,
//   confirm meters are healthy, then flip this back to false like V10.9.15 did.
// V13.4.55: UNPAUSED. Verified via Supabase API that the project is ACTIVE_HEALTHY
//   (no more 402s), the anon key + URL in this file still match the live project,
//   RLS policies are open, and tara_state holds cloud data through 2026-07-07.
//   Limits reset per user, so re-enabling cloud sync. Receive side unions on merge,
//   so no local entries are lost on resync. Flip back to true only if 402s return.
const _SB_PAUSED=false;
// Supabase client is initialized lazily below after the helper module loads.
// During the parallel-write phase, _sbClient may be null if init fails — code
// must check before calling. Failed Supabase init is non-fatal (Firestore
// remains primary). Logged once on init.
let _sbClient=null;
let _sbInitAttempted=false;
let _sbInitError=null;

// V10.1.0 — Supabase init. Mirrors the Firestore init pattern at L35. Fail-soft:
//   if the client can't be created, log a warning and leave _sbClient=null. The
//   parallel-write wrapper checks for null before each call so a broken Supabase
//   doesn't break the app.
try{
  _sbInitAttempted=true;
  if(_SB_PAUSED){
    _sbInitError='sync-paused';
    console.info('[Supabase] sync paused — local-only mode active until ~2026-06-18');
  } else if(typeof createSupabaseClient==='function'&&SUPABASE_URL&&SUPABASE_ANON_KEY){
    _sbClient=createSupabaseClient(SUPABASE_URL,SUPABASE_ANON_KEY,{
      auth:{
        // No sign-in flow — Shared Tara model. Disable session persistence and
        //   auto-refresh since we're using the anon role only.
        persistSession:false,
        autoRefreshToken:false,
        detectSessionInUrl:false,
      },
      // Realtime opted in but with conservative defaults. We'll wire up the
      //   subscription helpers (cloudSupabaseWatch) but keep them off by default
      //   during Sessions 1-2 to avoid surprises.
      realtime:{params:{eventsPerSecond:2}},
    });
    console.info('[Supabase] init ok — anon role, RLS policies active');
  } else {
    _sbInitError='createSupabaseClient unavailable or credentials missing';
    console.warn('[Supabase] init skipped:',_sbInitError);
  }
} catch(e){
  _sbInitError=e?.message||String(e);
  _sbClient=null;
  console.warn('[Supabase] init failed:',_sbInitError);
}

// ─────────────────────────────────────────────────────────────────────────────
// V10.7.95: IndexedDB PRIMARY STORE for taraCallLog.
//   localStorage is limited to ~5-10MB. At 1.2KB/entry (after strip), that's
//   ~4000 entries max. IndexedDB uses disk storage (gigabytes available), has no
//   practical size limit, and is browser-native with no external dependencies.
//   Architecture:
//     - IndexedDB: primary store, unlimited capacity, async
//     - localStorage: fast read cache (last 500 entries for instant paint on load)
//     - On load: read IndexedDB first, fall back to localStorage if IDB unavailable
//     - On write: write to IndexedDB (debounced 2s) + localStorage (immediate, last 500)
// ─────────────────────────────────────────────────────────────────────────────
const _IDB_NAME='TaraTradeDB';
const _IDB_VERSION=1;
const _IDB_STORE='callLog';

let _idb=null;
const _openIDB=()=>new Promise((resolve,reject)=>{
  if(_idb){resolve(_idb);return;}
  if(typeof indexedDB==='undefined'){resolve(null);return;}
  try{
    const req=indexedDB.open(_IDB_NAME,_IDB_VERSION);
    req.onupgradeneeded=(e)=>{
      const db=e.target.result;
      if(!db.objectStoreNames.contains(_IDB_STORE)){
        db.createObjectStore(_IDB_STORE,{keyPath:'key'});
      }
    };
    req.onsuccess=(e)=>{_idb=e.target.result;resolve(_idb);};
    req.onerror=(e)=>{console.warn('[IDB] open failed',e.target.error);resolve(null);};
    req.onblocked=()=>{console.warn('[IDB] open blocked');resolve(null);};
  }catch(e){resolve(null);}
});

const _idbWrite=async(key,value)=>{
  const result=await updateIndexedRecord(_openIDB,_IDB_STORE,key,previous=>
    key==='taraCallLog'?mergeDurableCallArchive(previous,value):value);
  if(!result.ok)console.warn('[IDB] write failed',key,result.error);
  return result.ok;
};

const _idbRead=async(key)=>{
  try{
    const db=await _openIDB();
    if(!db)return null;
    return new Promise((resolve)=>{
      try{
        const tx=db.transaction(_IDB_STORE,'readonly');
        const store=tx.objectStore(_IDB_STORE);
        const req=store.get(key);
        req.onsuccess=(e)=>resolve(e.target.result?.value??null);
        req.onerror=()=>resolve(null);
      }catch(e){resolve(null);}
    });
  }catch(e){return null;}
};

// ─────────────────────────────────────────────────────────────────────────────

//
// Path convention: doc_path is a slash-separated string like 'memory/taraCallLog'.
//   Stored as-is in the tara_state.doc_path column. No path parsing needed.

// One-shot write: insert or update a single doc. Best-effort, fire-and-forget
//   (no error throws to caller). Used for non-RMW paths.
const cloudSupabaseWrite=async(path,data)=>{
  if(!_sbClient||!path)return false;
  try{
    const {error}=await _sbClient.from('tara_state').upsert({
      doc_path:path,
      data:data,
      updated_at:new Date().toISOString(),
    },{onConflict:'doc_path'});
    if(error){console.warn('[Supabase] write failed',path,error.message);return false;}
    return true;
  }catch(e){
    console.warn('[Supabase] write threw',path,e?.message);
    return false;
  }
};

// One-shot read: fetch a single doc by path. Returns the data payload or null.
//   Matches cloudRead signature.
const cloudSupabaseRead=async(path,{throwOnError=false}={})=>{
  if(!_sbClient||!path)return null;
  try{
    const {data,error}=await _sbClient.from('tara_state').select('data,updated_at').eq('doc_path',path).maybeSingle();
    if(error){
      if(throwOnError)throw error;
      // PGRST116 = no rows found, which is "missing doc" (not an error for us)
      if(error.code!=='PGRST116')console.warn('[Supabase] read failed',path,error.message);
      return null;
    }
    if(!data)return null;
    return data.data; // unwrap the jsonb column
  }catch(e){
    if(throwOnError)throw e;
    console.warn('[Supabase] read threw',path,e?.message);
    return null;
  }
};

// Subscribe to changes on a specific doc. Returns an unsubscribe function.
//   Matches cloudWatch signature: callback receives the new data payload.
//   Uses Supabase Realtime postgres_changes events.
// V10.9.29 EGRESS: paths in this set skip the live postgres_changes subscription.
//   Supabase realtime rebroadcasts the ENTIRE row `data` column on every change.
//   For memory/taraCallLog (~2.6MB) that was ~157MB/day of pure egress just echoing
//   our own writes back to us. These big docs sync on load + on explicit pull/import
//   instead; small fast docs (lock state, pnl) keep realtime for cross-device awareness.
// V13.4.162: 'memory/hourlyRecord' added. Same reasoning as the 15m log -- it is a
//   six-figure doc, and echoing those over realtime is exactly what produced the
//   ~157MB/day egress incident. Hourly rides the same heartbeat + catch-up path.
// V13.4.204: per-proxy health for the Kalshi racers. Module scope so it
//   survives re-renders; a path failing 3+ times in a row is benched for 90s
//   and then re-probed, so an outage never becomes permanent.
const _KFEED_HEALTH={};
// V13.4.238: long string fields we drop from the cloud copy DELIBERATELY, so the
//   drift guard can stay quiet about them without also going quiet about the next
//   `reason`. Everything here is either reconstructable, verbose prose, or purely
//   cosmetic. If a field is not on this list and not in _MINI_KEEP, the guard
//   should complain — that is the whole point.
const _MINI_DROP_OK=new Set([
  'reasoning',        // multi-line engine narrative, regenerated every tick
  'engineLog',        // same
  'caution',          // duplicated into `reason`
  'at','time',        // display strings derived from id/resolvedAt
  'callSubLabel','band','whenStr','phaseLabel',
]);
// V13.4.212: one-shot audit flag for the _MINI_KEEP drift guard below.
let _MINI_AUDITED=false;
const _NO_REALTIME_PATHS=new Set(['memory/taraCallLog','memory/log_audit','history/pastWindows','memory/hourlyRecord','memory/weatherPicks']);

// V12.8: MULTI-DEVICE SYNC HEARTBEAT ──────────────────────────────────────────
//   Problem this solves: memory/taraCallLog is in _NO_REALTIME_PATHS (the 2.6MB doc
//   can't ride realtime without ~157MB/day egress). So after the initial load read,
//   a LIVE device never pulls down entries another device commits — the two diverge
//   and the displayed W/L record 'freezes' relative to the other device's activity.
//
//   Fix: a TINY realtime heartbeat doc (state/syncHeartbeat, ~150 bytes) carrying a
//   monotonic signature of the log {resolved, maxId, count}. It is NOT in
//   _NO_REALTIME_PATHS, so it gets live postgres_changes for near-free. When a device
//   sees the cloud heartbeat signature is AHEAD of its own local signature, it triggers
//   ONE catch-up pull of the big doc and merges it in. The expensive read only fires
//   when there is genuinely newer data elsewhere - never on a blind timer, and never
//   once the two devices have converged.
const _SYNC_HEARTBEAT_PATH='state/syncHeartbeat';
// V13.4.233: last peer device seen on the shared state, and whether its build is
//   behind this one. Module-level because the sync panel mounts on demand and
//   only needs the latest value, not a subscription.
const _PEER_BUILD={by:null,version:null,at:0};
const _buildNum=(v)=>{const m=String(v||'').match(/v?13\.4\.(\d+)/);return m?parseInt(m[1],10):null;};
const _notePeerBuild=(by,version)=>{
  if(!by||by===_taraDeviceId)return;
  _PEER_BUILD.by=by; _PEER_BUILD.version=version||null; _PEER_BUILD.at=Date.now();
};
const _peerBuildLag=()=>{
  const mine=_buildNum(typeof TARA_BUILD_VERSION!=='undefined'?TARA_BUILD_VERSION:null);
  const theirs=_buildNum(_PEER_BUILD.version);
  if(mine==null||theirs==null||!_PEER_BUILD.by)return null;
  return{by:_PEER_BUILD.by,theirs,mine,behind:mine-theirs,at:_PEER_BUILD.at,
    version:_PEER_BUILD.version};
};
// Stable per-device id, persisted so a device keeps its identity across reloads.
const _taraDeviceId=(()=>{
  try{
    let id=localStorage.getItem('tara_deviceId');
    if(!id){id='dev-'+Math.random().toString(36).slice(2,8)+'-'+Date.now().toString(36).slice(-4);localStorage.setItem('tara_deviceId',id);}
    return id;
  }catch(_){return 'dev-ephemeral';}
})();
// Compute a comparable signature from a call-log array. resolved is the primary,
//   monotonic axis (only goes up as trades settle); maxId (Date.now()-based) and
//   count are tiebreakers. Cheap to compute, cheap to compare.
const _computeLogSig=callLogSyncStatus;
// Is signature A strictly behind signature B? (i.e. should A pull from a source at B)
//   Behind = fewer resolved trades, or equal resolved but an older max id.
const _sigBehind=(a,b)=>{
  if(!a||!b)return false;
  if((b.finalized||0)>(a.finalized||0)||(b.evidenceAt||0)>(a.evidenceAt||0))return true;
  if((b.resolved||0)>(a.resolved||0))return true;
  if((b.resolved||0)===(a.resolved||0)&&(b.maxId||0)>(a.maxId||0))return true;
  return false;
};
// Element-wise max of two signatures (the heartbeat doc only ever moves up).
const _sigMax=(a,b)=>({
  resolved:Math.max((a&&a.resolved)||0,(b&&b.resolved)||0),
  maxId:Math.max((a&&a.maxId)||0,(b&&b.maxId)||0),
  count:Math.max((a&&a.count)||0,(b&&b.count)||0),
  finalized:Math.max((a&&a.finalized)||0,(b&&b.finalized)||0),
  evidenceAt:Math.max((a&&a.evidenceAt)||0,(b&&b.evidenceAt)||0),
});

// V11.2 live timing recommendation — written by the timing controller each tick,
//   read by _logSnapshotEntry to stamp committed snaps, and by the card for display.
let _v112Live={timing:'now',why:'',oddsCeil:null,kForDir:null,at:0};

// ═══════════════════════════════════════════════════════════════════════════
// V13.4.142 — LIVE KALSHI QUOTE (bid/ask), for true executable-cost logging.
//
// WHY THIS EXISTS: `kalshiAtLock` is the MID of yes_bid/yes_ask (see the price
//   extraction in the Kalshi fetch effect). A mid is NOT a price you can trade at.
//   Buying YES costs the ASK; buying NO costs (100 − BID). So every EV figure
//   computed from kalshiAtLock is optimistic by roughly half the spread, on every
//   single trade, in a compounding way.
//
// WHY IT MATTERS: an EV audit of the 2026-08-05 export (n=3238 real-traded 15m
//   calls) put the whole strategy at +1.35c/contract before execution cost — and
//   a mere +2c of slippage flips the book to NEGATIVE (-$3.22 vs +$43.66 at zero
//   slippage). Execution cost is therefore the single largest unknown in the
//   system, and it was entirely unmeasured: across 6,541 logged entries,
//   entrySlippageBps / kalshiAtFill / entryFairValue were populated ZERO times,
//   and every populated entryPrice traced back to the kalshi-at-lock fallback
//   (entrySource was never once 'auto-exec-filled' or 'manual-logged').
//
// WHAT THIS GIVES US: stamping bid/ask at lock makes executable cost knowable
//   immediately, from paper calls alone, without waiting for real fills to exist.
//   execCostAtLock is what the trade would ACTUALLY have cost to enter.
//   True EV per contract = (WR × 100) − execCostAtLock − fees.
//
// Written by the Kalshi poll; read by _logSnapshotEntry. Module-level (not state)
//   so it never participates in hook ordering or triggers a re-render.
let _kalshiQuote={bid:null,ask:null,mid:null,spread:null,at:0,ticker:null};
// V13.4.214: a quote is only usable if BOTH sides are real prices strictly
//   inside 0-100 and the book is not crossed. bid:0/ask:0 is an empty book, not
//   a market at 0c -- and after the DOWN inversion it reads as a confident
//   100c. Same sentinel trap as V13.4.161. Age is checked separately by callers.
const _quoteUsable=(q)=>{
  if(!q)return false;
  const b=Number(q.bid),a=Number(q.ask);
  if(!Number.isFinite(b)||!Number.isFinite(a))return false;
  if(b<=0||b>=100||a<=0||a>=100)return false;
  return a>=b;
};

// Executable entry cost in cents for a given direction, from the live quote.
//   UP   → we buy YES → we pay the YES ask.
//   DOWN → we buy NO  → we pay (100 − YES bid).
// Returns null when the quote is missing/stale so callers can fall back to mid.
const _execCostCents=(dir,maxAgeMs=60000)=>{
  const q=_kalshiQuote;
  if(!q||!q.at||(Date.now()-q.at)>maxAgeMs)return null;
  if(dir==='UP')  return (q.ask!=null&&isFinite(q.ask))?q.ask:null;
  if(dir==='DOWN')return (q.bid!=null&&isFinite(q.bid))?(100-q.bid):null;
  return null;
};

// V13.4.291: the mid-price version of _execCostCents — what a contract is
//   "worth" on OUR side of the book, ignoring the spread we have to cross.
//   Slippage is (what we actually paid) − (this), so both terms must be on the
//   same side: for DOWN we hold NO, whose mid is 100 − the YES mid. Comparing a
//   NO fill against a YES mid is the same inversion trap documented at
//   _exitYesLimitCents and in the V13.4.290 P&L fix.
//   This exists because entryFairValue / entrySlippageBps / kalshiAtFill had no
//   writer anywhere in the file — the log read them off autoOrderState, which
//   never carried them. See the note at the top of this file: across 6,541
//   logged entries those three were populated zero times, which is why
//   execution cost is described there as the single largest unknown in the
//   system. It was not unmeasurable; it was never written down.
const _execFairCents=(dir,maxAgeMs=60000)=>{
  const q=_kalshiQuote;
  if(!q||!q.at||(Date.now()-q.at)>maxAgeMs)return null;
  const m=Number(q.mid);
  if(!Number.isFinite(m))return null;
  if(dir==='UP')  return m;
  if(dir==='DOWN')return 100-m;
  return null;
};

// What one contract of an OPEN position could be sold for right now — the
// mirror of _execCostCents, which prices getting in. Exiting crosses the other
// side of the book:
//   UP   (long YES) -> we sell YES into the bid.
//   DOWN (long NO)  -> we sell NO, which is buying YES at the ask, so the NO
//                      bid is 100 - yes ask.
// Returns null on a missing or stale quote so callers hold rather than act on
// a price they cannot trust.
const _exitValueCents=(dir,maxAgeMs=30000,ticker=_kalshiQuote?.ticker)=>heldQuote(_kalshiQuote,{dir,ticker,maxAgeMs})?.value??null;

// The YES limit price that closes the position immediately, i.e. crossing.
// Exits are not laddered: a stop or a closing bell is not the moment to shave a
// cent and risk not filling at all.
//   UP   sell YES at the bid.
//   DOWN sell NO by buying YES at the ask.
const _exitYesLimitCents=(dir,maxAgeMs=30000,ticker)=>heldQuote(_kalshiQuote,{dir,ticker,maxAgeMs})?.yesLimit??null;

// ═══════════════════════════════════════════════════════════════════════════
// V11 SELECTIVITY ENGINE — data-driven gate on all directional snap commits.
//   Basis: 2,736 resolved forward-test trades (live Kalshi, Jun 2026).
//   Philosophy: remove verified drains, boost confirmed edges, add Kalshi edge
//     awareness. Hard gates only where data is conclusive (n≥100, WR<60%).
// ═══════════════════════════════════════════════════════════════════════════

// Historical WR by tier (2,736 resolved trades). Used for delta adjustments + gates.
const _V11_TIER_WR={
  'no-go-edge':75.8,'tape-led':72.9,'super-confluence':75.0,'user-forced':70.0,
  'time-cap-commit':62.4,'directional-lock':60.3,'no-go-data':58.9,
  'patient':55.9,'structural-led':53.8,
};

// Tiers that are hard-removed (WR < 59%, verified n≥100 or consistent underperform).
// These become v11-sitout instead of a directional commit.
const _V11_HARD_GATE_TIERS=new Set(['patient','no-go-data','v10_7_5_force_commit',
  'edge-sitout','deadzone-sitout','gap-opposed-timecap-sitout','deadzone-hour-sitout',
  'kalshi-window-closed']);

// Returns EDT hour (0-23) and weekend flag for a Date object.
const _v11EDTHour=(d=new Date())=>{
  const edt=new Date(d.toLocaleString('en-US',{timeZone:'America/New_York'}));
  return{h:edt.getHours(),weekend:edt.getDay()===0||edt.getDay()===6};
};

// Kalshi edge: how much better is our adjusted posterior vs market pricing?
// Returns {edge:number|null, label:string, sit:bool, warn:bool, boost:bool}
const _v11KalshiEdge=(posterior,kalshiAtLock,direction)=>{
  if(!kalshiAtLock||kalshiAtLock<=0)return{edge:null,label:'no Kalshi price',sit:false,warn:false,boost:false};
  // kalshiAtLock is the YES price (0-100). For DOWN calls, edge uses the NO side.
  const mktProb=direction==='UP'?Number(kalshiAtLock):(100-Number(kalshiAtLock));
  const edge=posterior-mktProb;
  // ─────────────────────────────────────────────────────────────────────────
  // V13.4.141 REVERT of the V13.4.140 "mktProb<50 = sit out" gate. DO NOT re-add
  //   that gate on win-rate evidence. It was justified by a WIN-RATE audit, and on
  //   a binary market win rate is NOT profit: EV = WR − entryCost. Re-audit of the
  //   same 15m export on an EV basis (n=3238 real-traded, excl. counterfactual
  //   sitout tiers, incl. est. Kalshi fees) showed the gate removed the single most
  //   profitable band and kept the least profitable one:
  //     entry cost <50c : WR 48.6% → netEV +5.07c/ct → +$31.55   ← was GATED OUT
  //     entry cost >=50c: WR 65.1% → netEV +0.46c/ct → +$12.11   ← was KEPT
  //   Cheap entries lose more often but pay ~2:1 when they hit; expensive entries
  //   win often and pay almost nothing. The cheap band also survives execution cost
  //   (+3c slippage → still +2.04c/ct) while the expensive band goes NEGATIVE at
  //   just +1c slippage (−0.52c/ct). Optimizing win rate here actively destroys EV.
  //   CAVEAT for whoever tunes this next: the cheap band's edge is concentrated in
  //   2026-05 (+8.66c/ct, n=395); 2026-06 onward is roughly flat (−0.71c/ct, n=204).
  //   So this is "do not blanket-ban cheap entries", NOT "always buy cheap".
  // ─────────────────────────────────────────────────────────────────────────
  // If market disagrees strongly: sit out (market has info we don't)
  if(edge<=-12)return{edge,label:`mkt prices ${mktProb.toFixed(0)}%, our post=${posterior.toFixed(0)}% (-${Math.abs(edge).toFixed(0)}pp edge deficit)`,sit:true,warn:false,boost:false};
  if(edge<=-6)return{edge,label:`weak Kalshi edge (${edge.toFixed(0)}pp vs market)`,sit:false,warn:true,boost:false};
  if(edge>=10)return{edge,label:`strong Kalshi edge +${edge.toFixed(0)}pp (mkt mispriced)`,sit:false,warn:false,boost:true};
  return{edge,label:`Kalshi edge ${edge>=0?'+':''}${edge.toFixed(0)}pp`,sit:false,warn:false,boost:false};
};

// Session adjustment: known hour/day combinations from historical data.
const _v11SessionDelta=(h,weekend)=>{
  if(!weekend){
    if(h===8)return{delta:-12,label:'WD 08:00 (42% WR hist — US pre-mkt noise)'};
    if(h<=1)return{delta:-5,label:`WD ${h===0?'00':'01'}:00 (58-60% WR hist)` };
    if(h===4||h===5)return{delta:+4,label:`WD 0${h}:00 (70% WR hist — Asia quiet trend)` };
    if(h===17)return{delta:+4,label:'WD 17:00 (70% WR hist — US close momentum)'};
    if(h===21)return{delta:+4,label:'WD 21:00 (68% WR hist)'};
  }else{
    if(h===1)return{delta:+10,label:'WE 01:00 (79% WR hist)'};
    if(h===8||h===9)return{delta:+8,label:`WE 0${h}:00 (78-80% WR hist)` };
    if(h===15)return{delta:+7,label:'WE 15:00 (77% WR hist)'};
    if(h===5||h===6)return{delta:+4,label:`WE 0${h}:00 (71-74% WR hist)` };
  }
  return{delta:0,label:null};
};

// ── MAIN V11 GATE ───────────────────────────────────────────────────────────
// Called with the snap object before it is committed. Returns:
//   {allow:true, why, qualityScore, sizeGuidance, kalshiEdge}
//   {allow:false, reason, category} — caller converts to v11-sitout snap.
const _v11Gate=(snap)=>{
  const tier=snap?.tier||'';
  const conf=Number(snap?.confidence||50);
  const post=Number(snap?.atPosterior||snap?.confidence||50);
  const qs=Number(snap?.qScore||0);
  const dir=snap?.call||snap?.direction||'UP';
  const regime=snap?.regime||'';
  const kLock=snap?.kalshiAtLock;
  const{h,weekend}=_v11EDTHour();
  const postDev=Math.abs(post-50);
  const reasons=[];
  let qualityScore=50;

  // ── HARD TIER GATE ───────────────────────────────────────────────────────
  if(_V11_HARD_GATE_TIERS.has(tier)){
    return{allow:false,reason:`${tier} gated (${(_V11_TIER_WR[tier]||55).toFixed(1)}% WR hist, n≥100 — below threshold)`,category:'v11-tier-gate'};
  }

  // ═══════════════════════════════════════════════════════════════════════
  // V13.4.145 — THREE SUB-GATES REMOVED AFTER REPLAY ON REAL OUTCOMES
  //
  // This whole function was DEAD CODE until V13.4.145 (defined, never called),
  // so none of it had ever been validated against a real outcome. Replaying it
  // over the call log (n=3,255 real-traded, priced net of fees) showed it was a
  // MIX: some sub-gates blocked losing trades, three blocked WINNING ones.
  // Wiring it up unchanged would have cut the book from +$41.78 to +$33.09.
  //
  // Measured EV of the trades each sub-gate BLOCKED (blocking is only correct
  // when that number is negative):
  //     dlq-gate      n=658  +2.69c   <- REMOVED, was the single worst offender
  //     struct-gate   n= 79  +0.12c   <- REMOVED as a hard block
  //     timecap-gate  n= 15 +16.50c   <- REMOVED (small n, but wrong direction)
  //     tier-gate     n=312  -1.14c   <- kept
  //     kalshi-edge   n=255  -0.77c   <- kept
  //     deadzone      n=226  -1.97c   <- kept (V13.4.144, now validated)
  //     session-gate  n=  4 -23.04c   <- kept
  //     coinflip      n=  1 -64.63c   <- kept
  //
  // Keeping only the five that test negative:
  //     baseline           n=3255  netEV +0.40c/contract  (on exec cost)
  //     good-components    n=1888  netEV +1.97c/contract  <- ~5x better
  // and it still leaves ~20 trades/day rather than throttling to under one.
  //
  // The three removed gates all shared a shape: they blocked on LOW CONVICTION
  // (low qScore, low confidence, posterior near 50). That intuition is wrong on
  // a binary market, because price already reflects conviction — a low-
  // conviction call bought cheap can be perfectly good EV. This is the same
  // error that killed V13.4.140. Do not re-add them on conviction reasoning
  // without an EV replay showing the blocked trades are actually negative.
  // ═══════════════════════════════════════════════════════════════════════
  if(tier==='structural-led'){
    // Retained as a QUALITY penalty (it is a genuinely weak tier at 53.8% WR)
    //   but no longer a hard block: the trades it blocked were EV-positive.
    qualityScore-=10; reasons.push('structural-led (53.8% WR hist)');
  }

  // ── SESSION BLACKOUT / ADJUSTMENT ────────────────────────────────────────
  const sessAdj=_v11SessionDelta(h,weekend);
  if(sessAdj.delta<=-10&&postDev<15){
    return{allow:false,reason:`${sessAdj.label} + low conviction (${postDev.toFixed(0)}pt) — not worth entry`,category:'v11-session-gate'};
  }
  if(sessAdj.delta!==0){qualityScore+=sessAdj.delta*0.8;if(sessAdj.label)reasons.push(sessAdj.label);}

  // ── KALSHI EDGE CHECK ────────────────────────────────────────────────────
  const ke=_v11KalshiEdge(post,kLock,dir);
  if(ke.sit)return{allow:false,reason:ke.label,category:'v11-kalshi-edge'};
  if(ke.warn){qualityScore-=12;reasons.push(ke.label);}
  else if(ke.boost){qualityScore+=10;reasons.push(ke.label);}

  // ── V13.4.144: COIN-FLIP PRICE DEAD ZONE (50-55c entry) ──────────────────
  //   Distinct from the posterior coin-flip floor below: that one asks whether
  //   OUR read is decisive, this asks whether the MARKET thinks the outcome is
  //   decided. When entry costs 50-55c the market is pricing a coin flip, and
  //   Kalshi's fee curve peaks at exactly 50c (0.07*p*(1-p) = 1.75c/contract),
  //   so breakeven at a 52c entry is 53.8% WR. Measured performance in that band
  //   was 51.4% -- structurally short of its own breakeven.
  //
  //   EV audit, 2026-08-05 export, n=3238 real-traded (sitout tiers excluded),
  //   priced on the kalshiAtLock MID and net of estimated fees:
  //     50-52c : n=179  WR 49.7%  netEV -2.62c
  //     52-55c : n=276  WR 51.4%  netEV -3.19c
  //     -> whole band n=455, -2.96c/contract, -$13.47
  //   Removing exactly this band lifts the book +$43.66 -> +$57.13.
  //
  //   Robustness (this is the bar V13.4.140 failed and had to be reverted):
  //     * negative in ALL four months (May -1.63, Jun -2.68, Jul -13.81, Aug -3.91)
  //     * negative in BOTH directions (UP -5.61, DOWN -1.52)
  //     * negative in ALL four tiers present (patient/structural-led/
  //       time-cap-commit/directional-lock)
  //     * a clean optimum: widening the cut to 45-58c LOSES $5.48 vs no cut,
  //       so this is a real local trough rather than an overfit wide net
  //     * adjacent bands are healthy (48-50c +4.35c, 55-58c +3.64c)
  //
  //   BASIS CAVEAT: calibrated on the MID (kalshiAtLock), because that is the
  //   basis the audit used and what every historical row carries. True entry is
  //   the ask, ~half a spread higher, so in exec terms this band sits nearer
  //   52-57c. Once execCostAtLock (V13.4.142) has accumulated, RECALIBRATE these
  //   bounds on exec cost rather than widening them by guess.
  //   Dial: localStorage 'taraDeadZoneOff'='1' disables.
  {
    const _dzOff=(()=>{try{return localStorage.getItem('taraDeadZoneOff')==='1';}catch(_e){return false;}})();
    const _entryMid=(kLock!=null&&isFinite(Number(kLock)))
      ?(dir==='UP'?Number(kLock):(dir==='DOWN'?(100-Number(kLock)):null))
      :null;
    if(!_dzOff&&_entryMid!=null&&_entryMid>=50&&_entryMid<55){
      return{allow:false,
        reason:`entry ${_entryMid.toFixed(0)}c is the coin-flip dead zone (50-55c): mkt prices a toss-up and Kalshi fees peak at 50c — measured -2.96c/contract over n=455, negative every month`,
        category:'v11-deadzone-price'};
    }
  }

  // ── REGIME ADJUSTMENT ────────────────────────────────────────────────────
  if(regime==='RANGE-CHOP'||regime==='CHOP'){qualityScore-=6;reasons.push('RANGE-CHOP (-6 quality)');}
  else if(regime==='TRENDING'||regime==='STRONG_TREND'){qualityScore+=5;reasons.push(`${regime} (+5 quality)`);}

  // ── TIER QUALITY ─────────────────────────────────────────────────────────
  const tierWR=_V11_TIER_WR[tier];
  if(tierWR){
    qualityScore+=(tierWR-63)*0.8; // scale deviation from 63% baseline
    if(tierWR>=73)reasons.push(`${tier} (${tierWR}% WR tier)`);
  }

  // ── QSCORE BOOST / PENALTY ───────────────────────────────────────────────
  if(qs>=70){qualityScore+=8;reasons.push(`qScore ${qs} (high quality)`);}
  else if(qs<20&&tier!=='no-go-edge'){qualityScore-=8;}

  // ── FINAL COIN-FLIP CHECK ────────────────────────────────────────────────
  // V13.4.47: high-WR tiers (tape-led 72.9%, no-go-edge 75.8%, super-confluence
  //   75.0%, user-forced 70.0% per _V11_TIER_WR, corroborated by export would-win
  //   data) carry real directional edge even when the posterior sits near 50, so
  //   relax their coin-flip veto to 4pt (still blocks dead-center 50). Others keep 7.
  const _cfFloor=(_V11_TIER_WR[tier]||55)>=70?4:7;
  if(postDev<_cfFloor){
    return{allow:false,reason:`posterior ${post.toFixed(0)}% (${postDev.toFixed(0)}pt from 50) — too close to coin flip`,category:'v11-coinflip'};
  }

  // ── V13.4.145: TIMING + VOLATILITY AS SIZE BOOSTS (not gates) ────────────
  //   Two conditions carry by far the highest measured EV, but on samples too
  //   small to justify blocking trades with. Used to SCALE SIZE instead, so a
  //   wrong read costs less than a hard gate would.
  //
  //     GOOD-gate baseline            n=1888  +1.97c/contract (on exec cost)
  //     GOOD + late (<=420s left)     n=  68  +9.24c
  //     GOOD + late (<=300s left)     n=  47 +11.97c
  //     GOOD + late + vol expansion   n=  28 +17.60c
  //
  //   Late-entry is the better-supported of the two: it is independently
  //   corroborated by the hourly-ladder data (85% directional accuracy at
  //   <=15min to close, decaying to ~52% at the top of the hour) and it has a
  //   mechanism — less time remaining means less variance left, so the current
  //   price carries more information about the outcome.
  //
  //   TWO CAVEATS, both real:
  //   1. n=28-68, no out-of-sample window (atSecondsLeft/bbwRankAtLock only
  //      began stamping recently, so a first-half/second-half split returns
  //      n=0 in the first half). These are promising, NOT proven.
  //   2. Selection: those locks may be late BECAUSE they were high-conviction
  //      setups that took time to confirm. Forcing lateness may not reproduce
  //      the effect. Boosting size is safe under that risk; gating is not.
  const _secsLeft=Number(snap?.atSecondsLeft);
  const _bbw=Number(snap?.bbwRankAtLock);
  const _isLate=Number.isFinite(_secsLeft)&&_secsLeft<=420;
  const _isVeryLate=Number.isFinite(_secsLeft)&&_secsLeft<=300;
  const _isExpanding=Number.isFinite(_bbw)&&_bbw>=80;
  if(_isVeryLate){qualityScore+=18;reasons.push(`<=5min left (+12.0c hist EV, n=47)`);}
  else if(_isLate){qualityScore+=12;reasons.push(`<=7min left (+9.2c hist EV, n=68)`);}
  if(_isExpanding){qualityScore+=8;reasons.push(`vol expanding (BBW ${_bbw.toFixed(0)})`);}

  // ── SIZE GUIDANCE ────────────────────────────────────────────────────────
  const qClamped=Math.max(0,Math.min(100,qualityScore));
  const sizeGuidance=qClamped>=72?'FULL':qClamped>=52?'STANDARD':qClamped>=35?'HALF':'QUARTER';
  const primeSetup=_isLate&&_isExpanding;

  // ── WHY STRING ───────────────────────────────────────────────────────────
  const tierLabel=tier==='no-go-edge'?'PRIME':tier==='tape-led'?'STRONG':tier==='super-confluence'?'PRIME':tier==='time-cap-commit'?'LATE':'STD';
  const why=[
    `[${tierLabel}] ${post.toFixed(0)}% post · ${conf.toFixed(0)}% conf`,
    ke.edge!==null?ke.label:null,
    sessAdj.label,
    ...reasons.filter(r=>!r.includes(ke.label)&&r!==sessAdj.label),
  ].filter(Boolean).slice(0,3).join(' · ');

  return{allow:true,qualityScore:qClamped,sizeGuidance,why,kalshiEdge:ke,reasons,tier,
    primeSetup,isLate:_isLate,isExpanding:_isExpanding};
};

const cloudSupabaseWatch=(path,callback)=>{
  if(!_sbClient||!path||typeof callback!=='function')return ()=>{};
  if(_NO_REALTIME_PATHS.has(path)){
    // No live subscription — the initial read in cloudWatch already delivered current
    //   state; further changes are picked up on next load/manual sync. Saves ~50% egress.
    return ()=>{};
  }
  let _ch=null;
  try{
    // Channel per path so each subscriber has its own. Supabase auto-merges
    //   internally so the cost is low.
    _ch=_sbClient.channel(`tara_state:${path}`)
      .on('postgres_changes',{
        event:'*',
        schema:'public',
        table:'tara_state',
        filter:`doc_path=eq.${path}`,
      },(payload)=>{
        try{
          // payload.new has the row after change; payload.old for deletes
          const _row=payload.new||payload.old;
          callback(_row?.data||null);
        }catch(e){console.warn('[Supabase] watch callback threw',path,e?.message);}
      })
      .subscribe();
  }catch(e){
    console.warn('[Supabase] watch subscribe threw',path,e?.message);
    return ()=>{};
  }
  return ()=>{
    try{if(_ch)_sbClient.removeChannel(_ch);}catch(_){}
  };
};

// Delete a doc by path. Matches cloudDelete signature.
const cloudSupabaseDelete=async(path)=>{
  if(!_sbClient||!path)return false;
  try{
    const {error}=await _sbClient.from('tara_state').delete().eq('doc_path',path);
    if(error){console.warn('[Supabase] delete failed',path,error.message);return false;}
    return true;
  }catch(e){
    console.warn('[Supabase] delete threw',path,e?.message);
    return false;
  }
};

// V10.1.0 — Debounced RMW (read-modify-write). Matches cloudWriteDebouncedRMW.
//   Postgres lacks Firestore's runTransaction primitive, so we implement RMW
//   via optimistic concurrency:
//     1. Read current cloud value + updated_at
//     2. Call mergeFn(cloudData, localData) → merged
//     3. Upsert with a conditional: only commit if cloud.updated_at hasn't
//        changed since our read
//     4. If commit rejected (someone else wrote between our read and write),
//        retry up to 5 times with fresh reads
//
//   Same per-path debounce as the Firestore version (separate timer map).
const _sbWriteQueueRMW=new Map();
const _sbWritePendingRMW=new Map();
const _sbLastSyncSig=new Map(); // V10.9.29: path -> last successfully-synced signature
const _sbWriteInFlightRMW=new Set();
const _sbWriteFailuresRMW=new Map();
const cloudSupabaseWriteDebouncedRMW=(path,getLocalData,mergeFn,delayMs=400)=>{
  if(!_sbClient||!path)return;
  // The large log flush has a bounded wait: fresh evidence updates the pending
  // payload but must not indefinitely restart its existing 60-second timer.
  const dueAt=Date.now()+delayMs;
  if(_sbWriteInFlightRMW.has(path)){
    // Keep one latest follow-up while this path owns its read/merge/CAS cycle.
    // Starting a second full-blob PATCH only creates local contention.
    _sbWritePendingRMW.set(path,{getLocalData,mergeFn,dueAt:Math.min(dueAt,_sbWritePendingRMW.get(path)?.dueAt??dueAt)});
    return;
  }
  // A queued call-log retry after a failed write is not pulled forward by new
  // evidence: the next attempt carries it anyway, and an early resend of the
  // multi-MB doc only repeats the failure. That is how a new Call every few
  // minutes beat the backoff. The small hourly doc keeps its urgent 1.5s path.
  if(_sbWriteQueueRMW.has(path)&&['memory/taraCallLog','memory/hourlyRecord'].includes(path)&&
    ((_sbWritePendingRMW.get(path)?.dueAt??dueAt)<=dueAt||(path==='memory/taraCallLog'&&(_sbWriteFailuresRMW.get(path)||0)>0))){
    _sbWritePendingRMW.set(path,{getLocalData,mergeFn,dueAt:_sbWritePendingRMW.get(path)?.dueAt??dueAt});
    return;
  }
  if(_sbWriteQueueRMW.has(path))clearTimeout(_sbWriteQueueRMW.get(path));
  _sbWritePendingRMW.set(path,{getLocalData,mergeFn,dueAt});
  const tid=setTimeout(async()=>{
    const _pending=_sbWritePendingRMW.get(path);
    const _getLocalData=_pending?.getLocalData||getLocalData;
    const _mergeFn=_pending?.mergeFn||mergeFn;
    _sbWriteQueueRMW.delete(path);
    _sbWritePendingRMW.delete(path);
    if(!_sbClient)return;
    _sbWriteInFlightRMW.add(path);
    let _completed=false,_failure=null;
    const _bounded=query=>path==='memory/taraCallLog'?boundedCloudWriteQuery(query):query;
    try{
    // V14.4.5: pending -> SITOUT and blocker-only updates have unchanged counts
    // and ids. Compare the actual stripped evidence, retaining the egress gate.
    if(path==='memory/taraCallLog'){
      try{
        const _ld=_getLocalData();
        const _arr=Array.isArray(_ld)?_ld:(Array.isArray(_ld&&_ld.entries)?_ld.entries:[]);
        const _sig=callLogSyncSignature(_arr,_stripEntryForCloud);
        if(_sbLastSyncSig.get(path)===_sig){_completed=true;return;} // nothing material changed
      }catch(_){}
    }
    // Optimistic RMW with retry. 5 attempts matches Firestore default.
    let _attempts=0;
    while(_attempts<5){
      _attempts++;
      try{
        // Read current state
        const {data:_cur,error:_readErr}=await _bounded(_sbClient.from('tara_state')
          .select('data,updated_at').eq('doc_path',path).maybeSingle());
        if(_readErr&&_readErr.code!=='PGRST116'){
          _failure=_readErr;
          console.warn('[Supabase] RMW read failed',path,_readErr.message);
          // V13.4.176 FIX: RMW is the write path for the highest-value data (call
          //   log, weights, learnings) and every failure branch here used to be
          //   console.warn-only -- no _setCloudStatus call, unlike plain cloudWrite.
          //   The visible sync health indicator kept reading "ok" from the last
          //   UNRELATED successful op while this path failed silently underneath it.
          //   An RLS change, a dropped connection, or a quota trip could break every
          //   RMW write from that point on with nothing on screen to show it.
          _setCloudStatus({state:'error',lastError:{path,message:_readErr.message,at:Date.now()}});
          return;
        }
        const _cloudData=_cur?.data||null;
        const _cloudUpdatedAt=_cur?.updated_at||null;
        const _localData=_getLocalData();
        const _writtenSig=path==='memory/taraCallLog'?callLogSyncSignature(
          Array.isArray(_localData)?_localData:(_localData?.entries||[]),_stripEntryForCloud):null;
        const _merged=_mergeFn(_cloudData,_localData);
        if(_merged==null){
          // A prior timed-out response may have committed, or a peer already
          // published the same evidence. The fresh merge proved it is present.
          if(_writtenSig!==null)_sbLastSyncSig.set(path,_writtenSig);
          _completed=true;
          _setCloudStatus({state:'ok',path,lastOk:Date.now()});
          return;
        }
        if(path==='memory/hourlyRecord'){
          // Settlement/hydration writers share the admission revision protocol:
          // even same-millisecond writes cannot erase an accepted approval.
          _merged._admissionRevision=crypto.randomUUID();
          const transport=supabaseHourlyTransport(_sbClient,path);
          const saved=_cur?await transport.compareAndSwap(_cur,_merged):await transport.insert(_merged);
          if(!saved)continue;
          _completed=true;
          _setCloudStatus({state:'ok',path,lastOk:Date.now(),writes:_cloudSyncStatus.writes+1});
          return;
        }
        // Conditional upsert: succeed only if row's updated_at hasn't changed.
        //   For a brand-new row (no _cloudUpdatedAt), this is an INSERT and
        //   has no conflict to check. For an existing row, we use the eq()
        //   filter on updated_at to enforce optimistic concurrency.
        if(_cloudUpdatedAt){
          // Update with concurrency check
          const {error:_updateErr,count}=await _bounded(_sbClient.from('tara_state')
            .update({data:_merged,updated_at:new Date().toISOString()},{count:'exact'})
            .eq('doc_path',path)
            .eq('updated_at',_cloudUpdatedAt));
          if(_updateErr){
            _failure=_updateErr;
            console.warn('[Supabase] RMW update failed',path,_updateErr.message);
            _setCloudStatus({state:'error',lastError:{path,message:_updateErr.message,at:Date.now()}});
            return;
          }
          if(count===0){
            // Conflict — someone else wrote. Retry from the top.
            if(_attempts>=5){
              _failure={code:'40001',message:'Repeated write conflicts'};
              console.warn('[Supabase] RMW gave up after 5 attempts',path);
              _setCloudStatus({state:'error',lastError:{path,message:'RMW gave up after 5 attempts (repeated write conflicts)',at:Date.now()}});
              return;
            }
            await new Promise(r=>setTimeout(r,50*_attempts));
            continue;
          }
          if(_writtenSig!==null)_sbLastSyncSig.set(path,_writtenSig);
          _completed=true;
          _setCloudStatus({state:'ok',path,lastOk:Date.now(),writes:_cloudSyncStatus.writes+1});
          return; // success
        } else {
          // No existing row — plain insert. If it conflicts (race with another
          //   first-time inserter), retry from the top.
          const {error:_insertErr}=await _bounded(_sbClient.from('tara_state')
            .insert({doc_path:path,data:_merged,updated_at:new Date().toISOString()}));
          if(_insertErr){
            // 23505 = unique violation = someone else inserted first. Retry.
            if(_insertErr.code==='23505'){
              if(_attempts>=5){
                console.warn('[Supabase] RMW insert gave up',path);
                _setCloudStatus({state:'error',lastError:{path,message:'RMW insert gave up after 5 attempts',at:Date.now()}});
                return;
              }
              await new Promise(r=>setTimeout(r,50*_attempts));
              continue;
            }
            console.warn('[Supabase] RMW insert failed',path,_insertErr.message);
            _failure=_insertErr;
            _setCloudStatus({state:'error',lastError:{path,message:_insertErr.message,at:Date.now()}});
            return;
          }
          if(_writtenSig!==null)_sbLastSyncSig.set(path,_writtenSig);
          _completed=true;
          _setCloudStatus({state:'ok',path,lastOk:Date.now(),writes:_cloudSyncStatus.writes+1});
          return; // success
        }
      } catch(e){
        _failure=e;
        console.warn('[Supabase] RMW threw',path,e?.message);
        _setCloudStatus({state:'error',lastError:{path,message:e?.message||'RMW threw',at:Date.now()}});
        return;
      }
    }
    }finally{
      _sbWriteInFlightRMW.delete(path);
      const _latest=_sbWritePendingRMW.get(path);
      const _recordPath=['memory/taraCallLog','memory/hourlyRecord'].includes(path);
      let _retryDelay=60000;
      if(_completed)_sbWriteFailuresRMW.delete(path);
      else if(_recordPath){
        const _failures=(_sbWriteFailuresRMW.get(path)||0)+1;
        _sbWriteFailuresRMW.set(path,_failures);
        _retryDelay=cloudWriteRetryDelay(_failure,_failures);
      }
      if((_latest||(!_completed&&_recordPath))&&!_sbWriteQueueRMW.has(path)){
        _sbWritePendingRMW.delete(path);
        const _remaining=_latest?Math.max(0,_latest.dueAt-Date.now()):_retryDelay;
        const _delay=_completed?_remaining:(_recordPath?_retryDelay:_remaining);
        cloudSupabaseWriteDebouncedRMW(path,_latest?.getLocalData||_getLocalData,_latest?.mergeFn||_mergeFn,_delay);
      }
    }
  },delayMs);
  _sbWriteQueueRMW.set(path,tid);
};

// Force-flush all pending Supabase RMW writes (called on tab close).
//   Best-effort, since async network calls may not complete before unload.
const _cloudSupabaseFlushAllRMW=()=>{
  // Snapshot pending work, including a latest payload queued behind an active
  // write. Clear timers before rescheduling; the per-path owner remains intact.
  // A call log that is backing off keeps its timer: this runs on every tab hide,
  // and must not turn the backoff into an immediate full-document resend. Its
  // evidence is already in IndexedDB if the tab really is closing.
  const held=new Set([..._sbWriteQueueRMW.keys()].filter(path=>path==='memory/taraCallLog'&&(_sbWriteFailuresRMW.get(path)||0)>0));
  const pending=[..._sbWritePendingRMW.entries()].filter(([path])=>!held.has(path));
  for(const[path,tid]of _sbWriteQueueRMW.entries())if(!held.has(path)){clearTimeout(tid);_sbWriteQueueRMW.delete(path);}
  for(const[path]of pending)_sbWritePendingRMW.delete(path);
  let flushed=0;
  for(const[path,entry]of pending){
    try{
      if(['memory/taraCallLog','memory/hourlyRecord'].includes(path)){
        cloudSupabaseWriteDebouncedRMW(path,entry.getLocalData,entry.mergeFn,0);
      }else{
        const merged=entry.mergeFn(null,entry.getLocalData());
        if(merged!=null)cloudSupabaseWrite(path,merged);
      }
      flushed++;
    }catch(_){}
  }
  return flushed;
};

// V10.2.0: stubbed. Firestore retired; `doc()` from firebase/firestore is no longer
//   imported. Returns null so any leftover reference fails the standard `if(!ref)` guard
//   that all helpers used to perform. No-op safety net rather than removed-and-crash.
const _fbDoc=(_path)=>null;
// V5.6.1: Cloud sync status — subscribers (React components) get notified on state change.
//   States: 'idle' (no recent activity), 'writing' (debounced or in-flight), 'ok' (last
//   write succeeded), 'error' (last write/read failed). Used for a visible indicator dot.
// V5.6.3: Track active onSnapshot listeners. If listeners > 0, real-time sync is alive
//   and changes from any device propagate within ~1s without refresh. If listeners = 0
//   but Firestore is initialized, something's wrong (rules, network, etc.).
const _cloudSyncListeners=new Set();
// V10.2.0: tracks Supabase health (was Firestore). State terminology unchanged so UI components don't need updating.
let _cloudSyncStatus={state:_SB_PAUSED?'paused':(_sbClient?'idle':'disabled'),lastOk:null,lastError:null,writes:0,reads:0,listeners:0};
const _setCloudStatus=(patch)=>{_cloudSyncStatus=cloudSyncHealth(_cloudSyncStatus,patch);_cloudSyncListeners.forEach(l=>l(_cloudSyncStatus));};
const subscribeCloudStatus=(fn)=>{_cloudSyncListeners.add(fn);fn(_cloudSyncStatus);return()=>_cloudSyncListeners.delete(fn);};
// V8.8.7: Active-write counter. Each cloudWrite / cloudWriteDebouncedRMW transaction
//   increments on start, decrements on completion (success or failure). State stays
//   'writing' as long as count>0, so concurrent writes from multiple sync paths
//   don't oscillate writing→ok→writing→ok the way they did when each path toggled
//   the global state independently. The pill applies an additional 750ms render
//   hysteresis on top of this so brief writes never flicker.
let _activeWrites=0;
const _incActiveWrites=()=>{
  _activeWrites++;
  if(_activeWrites===1)_setCloudStatus({state:'writing'});
};
const _decActiveWrites=(success)=>{
  _activeWrites=Math.max(0,_activeWrites-1);
  if(_activeWrites===0&&success){
    _setCloudStatus({state:'ok',lastOk:Date.now(),writes:_cloudSyncStatus.writes+1});
  }
  // If !success, the catch handler already set state:'error' — leave it.
  // If count still >0, leave state at 'writing' — another write is in flight.
};

// ── V8.3: CROSS-TAB BROADCAST CHANNEL ────────────────────────────────────────
// Instant cross-tab messaging within the same browser. Eliminates the ~1s wait for
// Firestore round-trip when one tab adds an entry — other tabs see it immediately.
// Backed up by Firestore + RMW writes for cross-device + cross-browser sync.
const _crossTab=(typeof BroadcastChannel!=='undefined')?(()=>{try{return new BroadcastChannel('tara-cross-tab-v1');}catch(_){return null;}})():null;
// V9.2.1: Cross-asset price channel. Separate from the call-log cross-tab channel.
//   Each tab broadcasts its current asset + price every 5s. Other-asset tabs read it
//   to detect correlation (e.g., BTC dumping → penalize ETH UP calls).

// V9.2.1 → V9.8.14: Sound alerts via Web Audio API. Minimal beep function — no audio
//   files needed. Called by spike alerts + trade coach urgent cards. User can mute via
//   localStorage.
// V9.8.14: Singleton AudioContext + audio-thread scheduling. Old V9.2.1 implementation
//   created a new AudioContext on every beep call — browsers cap context creation
//   (~6 in Chrome, fewer on iOS) so after enough beeps new contexts came back
//   suspended-and-stuck and no further sound played. Multi-tone beeps used setTimeout
//   for sequencing, which is throttled to >=1s when a tab is backgrounded — sequenced
//   beeps fired as a single muffled tone or not at all.
//   Now: one shared module-level context, lazily created, resumed on first user
//   gesture and on every visibilitychange→visible. Multi-tone sequences scheduled via
//   internal AudioContext timing (oscillator.start(ctx.currentTime + delay)) which
//   the audio thread honors regardless of JS event-loop throttling. Reliable across
//   visible-but-unfocused tabs. Truly backgrounded tabs (different tab focused) still
//   suspended — that's a browser-level constraint, fixable only via Notifications API.
let _sharedAudioCtx=null;
// V9.9.6: HTMLAudioElement fallback — preloaded WAV-encoded short tones. Used when
//   AudioContext is unavailable OR stuck suspended (Brave Shields, mobile background).
//   Generated as base64 WAV at module load. Three flavors: short beep, urgent double,
//   spike-against descending. Same pitch family as the AudioContext oscillators so the
//   user perceives a consistent sound across fallback transitions.
let _fallbackPool=null;
const _genWavBase64=(freq,durMs,vol)=>{
  // 8kHz mono 16-bit PCM single-tone — kept tiny so all variants live in memory.
  const sr=8000,n=Math.floor(sr*durMs/1000),amp=Math.floor(vol*32767);
  const buf=new ArrayBuffer(44+n*2),v=new DataView(buf);
  const w=(o,s)=>{for(let i=0;i<s.length;i++)v.setUint8(o+i,s.charCodeAt(i));};
  w(0,'RIFF');v.setUint32(4,36+n*2,true);w(8,'WAVE');w(12,'fmt ');
  v.setUint32(16,16,true);v.setUint16(20,1,true);v.setUint16(22,1,true);
  v.setUint32(24,sr,true);v.setUint32(28,sr*2,true);v.setUint16(32,2,true);v.setUint16(34,16,true);
  w(36,'data');v.setUint32(40,n*2,true);
  for(let i=0;i<n;i++){
    // 12ms fade-in/out so the start/end isn't a click.
    const fade=Math.min(1,i/(sr*0.012),(n-i)/(sr*0.012));
    v.setInt16(44+i*2,Math.sin(2*Math.PI*freq*i/sr)*amp*fade,true);
  }
  let s='';const u=new Uint8Array(buf);
  for(let i=0;i<u.length;i++)s+=String.fromCharCode(u[i]);
  return'data:audio/wav;base64,'+btoa(s);
};
const _getFallback=()=>{
  if(_fallbackPool)return _fallbackPool;
  if(typeof Audio==='undefined')return null;
  try{
    _fallbackPool={
      beep:new Audio(_genWavBase64(880,150,0.18)),
      urgent:new Audio(_genWavBase64(1100,280,0.22)),
      spike:new Audio(_genWavBase64(700,320,0.20)),
      win:new Audio(_genWavBase64(1000,300,0.18)),
    };
    Object.values(_fallbackPool).forEach(a=>{a.preload='auto';});
    return _fallbackPool;
  }catch(_){return null;}
};
const _getAudioCtx=()=>{
  if(typeof window==='undefined')return null;
  if(_sharedAudioCtx)return _sharedAudioCtx;
  try{
    const Ctx=window.AudioContext||window.webkitAudioContext;
    if(!Ctx)return null;
    _sharedAudioCtx=new Ctx();
    return _sharedAudioCtx;
  }catch(_){return null;}
};
// V9.9.6: aggressive resume — try to wake the context, with promise-tracking so we
//   don't fire multiple concurrent resume() calls (which can confuse some browser
//   audio implementations). Returns true if context is or becomes running.
let _resumePromise=null;
const _resumeCtx=(ctx)=>{
  if(!ctx)return Promise.resolve(false);
  if(ctx.state==='running')return Promise.resolve(true);
  if(_resumePromise)return _resumePromise;
  _resumePromise=ctx.resume().then(()=>{_resumePromise=null;return ctx.state==='running';}).catch(()=>{_resumePromise=null;return false;});
  return _resumePromise;
};
if(typeof window!=='undefined'){
  // V9.9.6: bootstrap on EVERY user gesture (not just first), and never remove the
  //   listener. Browsers throttle/suspend AudioContext on tab inactive, system sleep,
  //   focus loss, etc. — re-resuming on every click is cheap and bullet-proof. Also
  //   prefetch the fallback pool on first interaction so HTMLAudioElement is ready.
  const _wakeOnGesture=()=>{
    const c=_getAudioCtx();
    if(c&&c.state!=='running')_resumeCtx(c);
    _getFallback(); // preload fallback audio elements
  };
  try{
    // Capture-phase, passive listeners — every click/tap/key wakes audio. No removal.
    window.addEventListener('pointerdown',_wakeOnGesture,{capture:true,passive:true});
    window.addEventListener('keydown',_wakeOnGesture,{capture:true,passive:true});
    window.addEventListener('touchstart',_wakeOnGesture,{capture:true,passive:true});
    // Tab visibility change — resume on return from background.
    document.addEventListener('visibilitychange',()=>{
      if(document.visibilityState==='visible'){
        const c=_sharedAudioCtx;
        if(c&&c.state!=='running')_resumeCtx(c);
      }
    });
    // Window focus — additional safety, fires on Cmd-Tab back.
    window.addEventListener('focus',()=>{
      const c=_sharedAudioCtx;
      if(c&&c.state!=='running')_resumeCtx(c);
    });
    // Page show (back/forward cache restore) — Safari+iOS path.
    window.addEventListener('pageshow',()=>{
      const c=_sharedAudioCtx;
      if(c&&c.state!=='running')_resumeCtx(c);
    });
  }catch(_){}
}
// Schedule a single beep at ctx.currentTime + offsetSec. Audio-thread timing — survives
//   JS throttling when tab is unfocused (as long as not fully backgrounded).
const _scheduleBeep=(ctx,freq,durationMs,vol,offsetSec)=>{
  try{
    const osc=ctx.createOscillator();
    const gain=ctx.createGain();
    osc.connect(gain);gain.connect(ctx.destination);
    osc.frequency.value=freq;osc.type='sine';
    const t0=ctx.currentTime+(offsetSec||0);
    const dur=durationMs/1000;
    gain.gain.setValueAtTime(0.0001,t0);
    gain.gain.exponentialRampToValueAtTime(Math.max(0.0001,vol),t0+0.005);
    gain.gain.exponentialRampToValueAtTime(0.0001,t0+dur);
    osc.start(t0);
    osc.stop(t0+dur+0.05);
  }catch(_){}
};
// V9.9.6: unified play-or-fallback. Tries AudioContext first (preferred — precise
//   timing + multi-tone scheduling). If ctx is unavailable or stuck suspended after
//   a resume attempt, falls back to HTMLAudioElement with a cloned node (so rapid
//   re-plays work). Logs nothing on the happy path — silent unless something fails.
const _playWithFallback=(scheduleFn,fallbackKey)=>{
  try{
    if(typeof window==='undefined')return;
    const ctx=_getAudioCtx();
    if(ctx){
      // Try resume if suspended (synchronous check, async resume — fire scheduling
      //   anyway since browsers commonly suspend mid-tick and the schedule queue is
      //   honored once context wakes back up). If state is 'closed' we fall through.
      if(ctx.state==='running'){scheduleFn(ctx);return;}
      if(ctx.state==='suspended'){
        _resumeCtx(ctx).then(ok=>{
          if(ok){scheduleFn(ctx);}
          else{
            // Fallback: AudioContext refused to resume (page is fully backgrounded
            //   or browser policy blocked it). Try HTMLAudioElement.
            const pool=_getFallback();
            const a=pool&&pool[fallbackKey];
            if(a){try{const c=a.cloneNode(true);c.volume=0.6;c.play().catch(()=>{});}catch(_){}}
          }
        });
        return;
      }
    }
    // No context at all — fallback path.
    const pool=_getFallback();
    const a=pool&&pool[fallbackKey];
    if(a){try{const c=a.cloneNode(true);c.volume=0.6;c.play().catch(()=>{});}catch(_){}}
  }catch(_){}
};
const _taraUrgentBeep=()=>{
  _playWithFallback((ctx)=>{
    _scheduleBeep(ctx,1000,120,0.2,0);
    _scheduleBeep(ctx,1200,120,0.2,0.180);
  },'urgent');
};
const _taraSpikeBeep=()=>{
  _playWithFallback((ctx)=>{
    _scheduleBeep(ctx,1200,100,0.25,0);
    _scheduleBeep(ctx,900,100,0.2,0.140);
    _scheduleBeep(ctx,600,150,0.15,0.300);
  },'spike');
};
const _taraWinBeep=()=>{
  _playWithFallback((ctx)=>{
    _scheduleBeep(ctx,600,100,0.15,0);
    _scheduleBeep(ctx,900,100,0.15,0.140);
    _scheduleBeep(ctx,1200,150,0.2,0.300);
  },'win');
};
const _crossAssetChannel=(typeof BroadcastChannel!=='undefined')?(()=>{try{return new BroadcastChannel('tara-cross-asset-v1');}catch(_){return null;}})():null;
const broadcastToOtherTabs=(type,payload)=>{
  if(!_crossTab)return;
  try{_crossTab.postMessage({type,payload,t:Date.now()});}catch(_){}
};
const subscribeToOtherTabs=(handler)=>{
  if(!_crossTab)return()=>{};
  const fn=(event)=>{try{handler(event.data);}catch(_){}};
  _crossTab.addEventListener('message',fn);
  return()=>{try{_crossTab.removeEventListener('message',fn);}catch(_){}};
};
// V8.3: Tab heartbeat — each tab broadcasts a heartbeat every 5s so peers can detect
//   how many tabs are active. Stored in module-level Map keyed by tabId.
const _tabId=Math.random().toString(36).slice(2,10)+'-'+Date.now().toString(36);
const _peerTabs=new Map(); // tabId → {lastSeen, currentAsset}
let _peerListeners=new Set();
const _notifyPeerListeners=()=>{_peerListeners.forEach(fn=>{try{fn(Array.from(_peerTabs.entries()));}catch(_){}});};
if(_crossTab){
  _crossTab.addEventListener('message',(event)=>{
    const d=event.data;
    if(!d||d.type!=='heartbeat')return;
    const{tabId,currentAsset}=d.payload||{};
    if(!tabId||tabId===_tabId)return;
    _peerTabs.set(tabId,{lastSeen:Date.now(),currentAsset});
    _notifyPeerListeners();
  });
  // GC peers that haven't been heard from in 15s
  setInterval(()=>{
    const now=Date.now();
    let changed=false;
    for(const[id,info]of _peerTabs.entries()){
      if(now-info.lastSeen>15000){_peerTabs.delete(id);changed=true;}
    }
    if(changed)_notifyPeerListeners();
  },5000);
}
const subscribeToPeerTabs=(fn)=>{_peerListeners.add(fn);fn(Array.from(_peerTabs.entries()));return()=>_peerListeners.delete(fn);};
const getCurrentTabId=()=>_tabId;

// V10.2.0: cloudRead now reads from Supabase. Signature unchanged so call sites work as-is.
const cloudRead=async(path)=>{
  if(!path)return null;
  try{
    const data=await cloudSupabaseRead(path);
    _setCloudStatus({state:'ok',lastOk:Date.now(),reads:_cloudSyncStatus.reads+1});
    return data;
  }catch(e){
    console.warn('[Cloud read failed]',path,e?.message);
    _setCloudStatus({state:'error',lastError:{path,message:e?.message,at:Date.now()}});
    return null;
  }
};
// V10.2.0: cloudWrite now writes to Supabase only. Was dual-write (Firestore + Supabase)
//   in Session 2. Firestore retired. Call sites still see the same (path, data) signature.
const cloudWrite=async(path,data)=>{
  if(!path)return false;
  _incActiveWrites();
  let _ok=false;
  try{
    if(path.startsWith('state/currentLock_')){
      if(!_sbClient)return false;
      const result=await publishSharedDecision(path,data,{
        readRow:async p=>{
          const r=await _sbClient.from('tara_state').select('data,updated_at').eq('doc_path',p).maybeSingle();
          if(r.error)throw r.error;return r.data;
        },
        compareAndSwap:async(p,row,next)=>{
          let query=_sbClient.from('tara_state').update({data:next,updated_at:new Date().toISOString()})
            .eq('doc_path',p).eq('updated_at',row.updated_at);
          query=row.data?._revision?query.eq('data->>_revision',row.data._revision):query.is('data->>_revision',null);
          const r=await query.select('doc_path');if(r.error)throw r.error;return !!r.data?.length;
        },
        insertIfAbsent:async(p,next)=>{
          const r=await _sbClient.from('tara_state').upsert({doc_path:p,data:next,updated_at:new Date().toISOString()},
            {onConflict:'doc_path',ignoreDuplicates:true}).select('doc_path');
          if(r.error)throw r.error;return !!r.data?.length;
        },
      });
      _ok=result.ok||['stale-window-payload','newer-shared-window'].includes(result.reason);
    }else _ok=await cloudSupabaseWrite(path,data);
    if(!_ok){
      _setCloudStatus({state:'error',lastError:{path,message:'Supabase write returned false',at:Date.now()}});
    }
    return _ok;
  }catch(e){
    console.warn('[Cloud write failed]',path,e?.message);
    _setCloudStatus({state:'error',lastError:{path,message:e?.message,at:Date.now()}});
    return false;
  }finally{
    _decActiveWrites(_ok);
  }
};
// V10.2.0: cloudDelete now removes from Supabase. Signature unchanged.
const cloudDelete=async(path)=>{
  if(!path)return false;
  try{
    const ok=await cloudSupabaseDelete(path);
    if(ok)_setCloudStatus({state:'ok',lastOk:Date.now(),writes:_cloudSyncStatus.writes+1});
    return ok;
  }catch(e){
    console.warn('[Cloud delete failed]',path,e?.message);
    _setCloudStatus({state:'error',lastError:{path,message:e?.message,at:Date.now()}});
    return false;
  }
};


// ── V9.10.7: AUDIT LOG for silent log-write failures ────────────────────────
// Why: V9.10.6 surfaced an incident where Tara emitted a Discord embed for a
//   DOWN no-go-data lock at 1:45 AM but no matching entry appeared in taraCallLog.
//   The _logSnapshotEntry try/catch swallows throws to console-only, and the dedup
//   checks in both _logSnapshotEntry and the directional commit path silently
//   return prev unchanged when a keyed entry already exists. We had no evidence
//   of which silent path fired.
// What: this module captures every silent skip + every caught throw to a ring
//   buffer, persists last N events to localStorage, and replicates to a cloud
//   doc memory/log_audit. Going forward, anytime "Tara called but didn't log"
//   recurs, we can read the audit doc and see exactly which path swallowed the
//   write — without having had DevTools open at the moment.
// Storage: ring buffer of 200 events (~50KB max). Cloud writes debounced 2s.
const _LOG_AUDIT_LS_KEY='taraLogAudit_v1';
const _LOG_AUDIT_CAP=200;
let _logAuditBuffer=null;
const _loadAuditBuffer=()=>{
  if(_logAuditBuffer)return _logAuditBuffer;
  try{
    const _raw=typeof localStorage!=='undefined'?localStorage.getItem(_LOG_AUDIT_LS_KEY):null;
    _logAuditBuffer=_raw?JSON.parse(_raw):[];
    if(!Array.isArray(_logAuditBuffer))_logAuditBuffer=[];
  }catch(_){_logAuditBuffer=[];}
  return _logAuditBuffer;
};
const _persistAuditBuffer=()=>{
  try{
    if(typeof localStorage!=='undefined'){
      localStorage.setItem(_LOG_AUDIT_LS_KEY,JSON.stringify((_logAuditBuffer||[]).slice(-_LOG_AUDIT_CAP)));
    }
  }catch(_){}
};
let _auditCloudTimer=null;
const _scheduleAuditCloudWrite=()=>{
  if(_auditCloudTimer)clearTimeout(_auditCloudTimer);
  _auditCloudTimer=setTimeout(()=>{
    try{
      const _buf=(_logAuditBuffer||[]).slice(-_LOG_AUDIT_CAP);
      cloudWrite('memory/log_audit',{events:_buf,updatedAt:Date.now()}).catch(()=>{});
    }catch(_){}
  },2000);
};
const _auditLogEvent=(kind,details)=>{
  try{
    const _buf=_loadAuditBuffer();
    _buf.push({kind,at:Date.now(),ts:new Date().toISOString(),...details});
    while(_buf.length>_LOG_AUDIT_CAP)_buf.shift();
    _persistAuditBuffer();
    _scheduleAuditCloudWrite();
    try{console.warn('[V9.10.7 log_audit]',kind,details);}catch(_){}
  }catch(_){}
};
// DevTools convenience: window._tara_audit() returns the in-memory buffer.
try{if(typeof window!=='undefined'){window._tara_audit=()=>_loadAuditBuffer();}}catch(_){}
// ─────────────────────────────────────────────────────────────────────────────

// V10.7.45: WINDOW LIFECYCLE TRACKER
//   Purpose: diagnose "Tara locked but no entry logged" gaps.
//   Tracks each window from open → snapshot set → _logSnapshotEntry called → entry
//   confirmed in taraCallLog. Persists last 30 windows to localStorage so user can
//   export when a gap occurs and we can pinpoint EXACTLY which step broke.
const _LIFECYCLE_LS_KEY='taraWindowLifecycle_v1';
const _LIFECYCLE_CAP=30;
let _lifecycleBuffer=null;
const _loadLifecycle=()=>{
  if(_lifecycleBuffer)return _lifecycleBuffer;
  try{
    const _raw=typeof localStorage!=='undefined'?localStorage.getItem(_LIFECYCLE_LS_KEY):null;
    _lifecycleBuffer=_raw?JSON.parse(_raw):[];
    if(!Array.isArray(_lifecycleBuffer))_lifecycleBuffer=[];
  }catch(_){_lifecycleBuffer=[];}
  return _lifecycleBuffer;
};
const _persistLifecycle=()=>{
  try{
    if(typeof localStorage!=='undefined'){
      localStorage.setItem(_LIFECYCLE_LS_KEY,JSON.stringify((_lifecycleBuffer||[]).slice(-_LIFECYCLE_CAP)));
    }
  }catch(_){}
};
const _lifecycleFindOrCreate=(wid,windowType,asset)=>{
  const _buf=_loadLifecycle();
  let _rec=_buf.find(r=>r.wid===wid&&r.asset===asset);
  if(!_rec){
    _rec={
      wid,windowType,asset,
      openedAt:Date.now(),
      snapshotSets:[],   // every time taraCallSnapshotRef.current was assigned (kind, dir, tier, at)
      logCalls:[],       // every _logSnapshotEntry call (snapshotCall, snapshotTier, at, callSite)
      logResult:null,    // 'pushed' | 'dedup-blocked' | 'retry-rescued' | 'all-failed' | null
      logConfirmedAt:null, // when entry was actually seen in taraCallLog
      rolloverAt:null,   // when rollover fired for this window
      events:[],         // misc events (auditLog mirrors)
    };
    _buf.push(_rec);
    while(_buf.length>_LIFECYCLE_CAP)_buf.shift();
  }
  return _rec;
};
const _lifecycleRecordOpen=(wid,windowType,asset)=>{
  try{
    _lifecycleFindOrCreate(wid,windowType,asset);
    _persistLifecycle();
  }catch(_){}
};
const _lifecycleRecordSnapshot=(wid,windowType,asset,snapshot,callSite)=>{
  try{
    const _rec=_lifecycleFindOrCreate(wid,windowType,asset);
    const _commitAt=snapshot?.originalDecision?.lockedAt;
    if(_commitAt&&_rec.snapshotSets.some(s=>s.commitAt===_commitAt&&s.committed===true))return;
    _rec.snapshotSets.push({
      callSite:callSite||'?',
      call:snapshot?.call||null,
      direction:snapshot?.direction||null,
      tier:snapshot?.tier||null,
      confidence:snapshot?.confidence||null,
      committed:snapshot?.originalDecision?.provenance==='captured-at-lock'&&Number(snapshot?._committedAt)>0,
      commitAt:_commitAt||null,
      at:Date.now(),
    });
    _persistLifecycle();
  }catch(_){}
};
const _lifecycleRecordLogCall=(wid,windowType,asset,snapshot,callSite)=>{
  try{
    const _rec=_lifecycleFindOrCreate(wid,windowType,asset);
    _rec.logCalls.push({
      callSite:callSite||'?',
      snapshotCall:snapshot?.call||null,
      snapshotTier:snapshot?.tier||null,
      snapshotDir:snapshot?.direction||null,
      at:Date.now(),
    });
    _persistLifecycle();
  }catch(_){}
};
const _lifecycleRecordLogResult=(wid,asset,result,detail)=>{
  try{
    const _buf=_loadLifecycle();
    const _rec=_buf.find(r=>r.wid===wid&&r.asset===asset);
    if(!_rec)return;
    _rec.logResult=result;
    if(detail)_rec.events.push({type:'logResult',result,detail,at:Date.now()});
    _persistLifecycle();
  }catch(_){}
};
const _lifecycleRecordConfirmed=(wid,asset)=>{
  try{
    const _buf=_loadLifecycle();
    const _rec=_buf.find(r=>r.wid===wid&&r.asset===asset);
    if(!_rec)return;
    if(_rec.logConfirmedAt==null){
      _rec.logConfirmedAt=Date.now();
      _persistLifecycle();
    }
  }catch(_){}
};
let _lifecycleCanonicalLedger=[];
const _lifecycleConfirmLedger=(entries)=>{
  _lifecycleCanonicalLedger=Array.isArray(entries)?entries:[];
  let changed=false;
  for(const rec of _loadLifecycle()){
    const state=lifecycleAuditState(rec,_lifecycleCanonicalLedger);
    if(state.diagnosis==='ok'&&(rec.logConfirmedAt==null||rec.diagnosis!=='ok')){
      Object.assign(rec,state);changed=true;
    }
  }
  if(changed)_persistLifecycle();
};
const _lifecycleRecordRollover=(wid,asset)=>{
  try{
    const _buf=_loadLifecycle();
    const _rec=_buf.find(r=>r.wid===wid&&r.asset===asset);
    if(!_rec)return;
    _rec.rolloverAt=Date.now();
    const state=lifecycleAuditState(_rec,_lifecycleCanonicalLedger);
    Object.assign(_rec,state);
    const _diagnosis=state.diagnosis;
    _persistLifecycle();
    // Cross-stamp the audit log if there's a gap
    if(['committed-snapshot-without-record','log-called-but-not-confirmed'].includes(_diagnosis)){
      _auditLogEvent('v10_7_45_window_gap_detected',{
        wid,asset,
        diagnosis:_diagnosis,
        snapshotSets:_rec.snapshotSets.length,
        logCalls:_rec.logCalls.length,
        logResult:_rec.logResult,
        confirmedAt:_rec.logConfirmedAt,
      });
    }
  }catch(_){}
};
try{if(typeof window!=='undefined'){window._tara_lifecycle=()=>_loadLifecycle();}}catch(_){}
// ─────────────────────────────────────────────────────────────────────────────


// V5.6.2: Real-time listener — fires once with current cloud value, then again on every
//   change from any connected client. This is what makes cross-device sync actually work:
//   Device A writes, Device B's listener fires within ~1s, B's UI updates without refresh.
//   Returns an unsubscribe function the caller stores in a useEffect cleanup.
// V5.6.3: Tracks active listener count in sync status — so the badge can show whether
//   real-time sync is actually established vs. just one-shot reads happening.
// V10.2.0: cloudWatch now uses Supabase Realtime (postgres_changes). Mirrors the prior
//   Firestore onSnapshot semantics: an initial read fires the callback once with current
//   data, then subsequent row changes stream in. Callback signature preserved
//   (data, status) — status is one of 'ok'|'error'|'disabled'.
const cloudWatch=(path,callback)=>{
  if(!path||typeof callback!=='function'){try{callback&&callback(null,'disabled');}catch(_){}return()=>{};}
  _setCloudStatus({listeners:_cloudSyncStatus.listeners+1});
  let active=true,realtimeSeen=false;
  // Initial read so the callback fires with current state (matches old onSnapshot behavior).
  cloudSupabaseRead(path,{throwOnError:path.startsWith('state/currentLock_')}).then(d=>{
    _setCloudStatus({state:'ok',lastOk:Date.now(),reads:_cloudSyncStatus.reads+1});
    try{if(active&&!realtimeSeen)callback(d,'ok');}catch(_){}
  }).catch(err=>{
    console.warn('[Cloud watch initial-read failed]',path,err?.message);
    _setCloudStatus({state:'error',lastError:{path,message:err?.message,at:Date.now()}});
    try{if(active&&!realtimeSeen)callback(null,'error');}catch(_){}
  });
  // Subscribe to subsequent changes.
  const unsub=cloudSupabaseWatch(path,(d)=>{
    if(!active)return;
    realtimeSeen=true;
    _setCloudStatus({state:'ok',lastOk:Date.now(),reads:_cloudSyncStatus.reads+1});
    try{callback(d,'ok');}catch(_){}
  });
  return()=>{
    active=false;
    _setCloudStatus({listeners:Math.max(0,_cloudSyncStatus.listeners-1)});
    try{unsub();}catch(_){}
  };
};
// Coalesces rapid updates per-path into a single Firestore write (saves quota + bandwidth).
const _writeQueue=new Map();
let _lastLocalPersistAt=0; // V13.4.4: throttle gate for the expensive taraCallLog local-persist block below (was running unthrottled on every taraCallLog change)
// V8.1: Track latest pending data per path so we can synchronously flush on tab close.
//   Map of path → most recent data, separate from the timeout map.
const _writePending=new Map();
const _writeLatest=new Map();
const cloudWriteDebounced=(path,data,delayMs=300)=>{
  const revision={};_writeLatest.set(path,revision);
  const previous=_writePending.get(path),dueAt=Date.now()+delayMs;
  if(_writeQueue.has(path)&&previous?.dueAt<=dueAt){
    _writePending.set(path,{data,dueAt:previous.dueAt,revision});return;
  }
  if(_writeQueue.has(path))clearTimeout(_writeQueue.get(path));
  _writePending.set(path,{data,dueAt,revision});
  const tid=setTimeout(()=>{
    const pending=_writePending.get(path),latest=pending?.data,flushedRevision=pending?.revision;
    _writeQueue.delete(path);
    _writePending.delete(path);
    Promise.resolve(cloudWrite(path,latest)).then(ok=>{
      if(ok===false&&_writeLatest.get(path)===flushedRevision&&!_writePending.has(path))cloudWriteDebounced(path,latest,10000);
    }).catch(()=>{if(_writeLatest.get(path)===flushedRevision&&!_writePending.has(path))cloudWriteDebounced(path,latest,10000);});
  },delayMs);
  _writeQueue.set(path,tid);
};
// V8.1: Synchronously fire all pending debounced writes. Called on pagehide/beforeunload
//   so trades made just before tab close don't get lost in the 300ms debounce window.
//   Returns the count of writes flushed.
const cloudFlushAll=()=>{
  let flushed=0;
  for(const[path,tid]of _writeQueue.entries()){
    clearTimeout(tid);
    const data=_writePending.get(path)?.data;
    if(data!=null){
      // Fire-and-forget — no await. Modern browsers respect the in-flight fetch
      //   on pagehide for a brief grace period. Firestore writes use fetch internally.
      try{cloudWrite(path,data);flushed++;}catch(_){}
    }
  }
  _writeQueue.clear();
  _writePending.clear();
  _writeLatest.clear();
  return flushed;
};
// V8.1: Wire flush to tab visibility change events. Browsers fire pagehide reliably
//   on real tab close + navigation; visibilitychange covers tab switch/minimize.
// V8.3: Now also drains RMW queue (best-effort; local-only write if cloud read can't complete).
//   The actual flush function combining both is defined below the RMW helpers.

// ── V8.3: READ-MODIFY-WRITE DEBOUNCED HELPER ────────────────────────────────
// Solves the multi-tab last-write-wins clobbering problem. When timer fires:
//   1. Open Firestore transaction — atomic read+write with auto-retry on conflict
//   2. Inside tx: read current cloud state, call mergeFn(cloudData, currentLocalData),
//      tx.set() the merged result. If another tab wrote between our read and write,
//      Firestore aborts our tx and retries our handler — guaranteeing serialization.
// getLocalData is a function (not a value) so we get the freshest local state when
// the timer fires, even if state changed during the debounce window.
// mergeFn returns null to skip the write entirely (e.g., no changes after merge).
// V10.2.0: now Supabase-only. Was dual-write (Firestore transaction + Supabase upsert)
//   during Session 2. Firestore retired. This helper is a thin wrapper over
//   cloudSupabaseWriteDebouncedRMW which implements optimistic-concurrency RMW
//   (read → merge → conditional update keyed on updated_at). The `_writeQueueRMW`
//   / `_writePendingRMW` Maps remain referenced by `_cloudFlushAllRMW` for the
//   tab-close grace flush — kept in sync below for that use case only.
const _writeQueueRMW=new Map();
const _writePendingRMW=new Map();
const cloudWriteDebouncedRMW=(path,getLocalData,mergeFn,delayMs=400)=>{
  // Mirror into the queue maps so the tab-close flush has visibility.
  if(_writeQueueRMW.has(path)&&['memory/taraCallLog','memory/hourlyRecord'].includes(path)){
    _writePendingRMW.set(path,{getLocalData,mergeFn});
    try{cloudSupabaseWriteDebouncedRMW(path,getLocalData,mergeFn,delayMs);}catch(_){}
    return;
  }
  if(_writeQueueRMW.has(path))clearTimeout(_writeQueueRMW.get(path));
  _writePendingRMW.set(path,{getLocalData,mergeFn});
  const tid=setTimeout(()=>{
    _writeQueueRMW.delete(path);
    _writePendingRMW.delete(path);
  },delayMs+50);
  _writeQueueRMW.set(path,tid);
  // Actual write goes through the Supabase RMW helper.
  try{cloudSupabaseWriteDebouncedRMW(path,getLocalData,mergeFn,delayMs);}catch(_){}
};
// V8.3: Force-flush all RMW writes synchronously on tab close. Cloud reads can't
//   complete in pagehide grace window, so we write local data without merge — better
//   than losing the write entirely. cloudWatch on other tabs will re-merge.
// V10.2.0: tab-close grace flush. Was a Firestore-only flush that wrote local data
//   without merge. Now delegates to the Supabase-side flush which performs the same
//   role for Supabase pending RMW writes. The Maps maintained above are also cleared.
const _cloudFlushAllRMW=()=>{
  let cleared=0;
  for(const[,tid]of _writeQueueRMW.entries()){clearTimeout(tid);cleared++;}
  _writeQueueRMW.clear();
  _writePendingRMW.clear();
  try{cleared+=_cloudSupabaseFlushAllRMW();}catch(_){}
  return cleared;
};
// Combined flush — called on tab close
// V10.1.0: cloudFlushAllCombined now also drains pending Supabase RMW writes.
//   During the parallel-write phase, this fires both Firestore and Supabase
//   pending writes on tab close. Once Session 4 removes Firestore, this will
//   simplify back to just _cloudSupabaseFlushAllRMW.
// V10.2.0: simplified — _cloudFlushAllRMW now internally drains the Supabase RMW queue,
//   so the explicit _cloudSupabaseFlushAllRMW call is no longer needed here (was needed
//   during Session 2's dual-write phase).
const cloudFlushAllCombined=()=>cloudFlushAll()+_cloudFlushAllRMW();
if(typeof window!=='undefined'){
  const _onLeave=()=>{try{cloudFlushAllCombined();}catch(_){}try{if(typeof window!=='undefined'&&typeof window._taraForceSave==='function')window._taraForceSave();}catch(_){}}; // V13.4.4: unconditional local-persist flush on leave, bypasses the throttle gate above
  window.addEventListener('pagehide',_onLeave);
  window.addEventListener('beforeunload',_onLeave);
  window.addEventListener('visibilitychange',()=>{
    if(document.visibilityState==='hidden')_onLeave();
  });
}

// V13.4.46: keep the screen awake while Tara is open+visible so a sleeping
//   phone screen does not suspend the scan loop and miss windows.
(function(){
  if(typeof navigator==='undefined'||!('wakeLock'in navigator))return;
  let _wl=null;
  const _acquire=async()=>{
    try{
      if(_wl)return;
      if(typeof document==='undefined'||document.visibilityState!=='visible')return;
      _wl=await navigator.wakeLock.request('screen');
      _wl.addEventListener('release',()=>{_wl=null;});
    }catch(_){_wl=null;}
  };
  const _release=async()=>{try{if(_wl){const w=_wl;_wl=null;await w.release();}}catch(_){_wl=null;}};
  document.addEventListener('visibilitychange',()=>{
    if(document.visibilityState==='visible')_acquire();else _release();
  });
  window.addEventListener('focus',_acquire);
  if(document.readyState==='complete')_acquire();
  else window.addEventListener('load',_acquire);
})();

// V13.4.99: SHARED VISIBILITY-AWARE INTERVAL. Root cause of the Vercel pause (3M edge
//   requests against a 1M free cap): the V13.4.46-49 wake-lock + silent-audio keep-alive
//   defeats Chrome's background-tab throttle so Tara does not miss windows while
//   minimized -- correct and NOT touched here -- but that means every proxied poll kept
//   firing at full speed in a hidden tab with no cost signal. The single biggest offender
//   was not the 8s Kalshi poll (13.4.98) but the OKX order-book poll at 2500ms = ~17,280
//   req/tab/12h on its own, more than 3x the Kalshi poll. taraSetVizInterval() is a drop-in
//   setInterval replacement: foreground keeps the caller's exact interval (zero behavior
//   change while visible), hidden multiplies it by BACKOFF (default 4x) up to a MAX_MS
//   ceiling, and it snaps back to full speed the instant the tab regains focus. Dial:
//   localStorage 'taraVizBackoff' (default 4, 1 = disables backoff = old behavior).
// V13.4.100: V101 SHADOW POSTERIOR (uploaded file 'V101_-_GOAT.txt', internally named
//   computeV99Posterior). User recalled this early build as a strong performer and asked
//   how current Tara differs. Answer: it never touches Kalshi at all -- no entry price,
//   no SIT_OUT, no gates, always produces a lean. That means any perceived edge could be
//   real momentum-following skill, OR just an artifact of always having an opinion during
//   a trending stretch with no memory of what it would have cost to act on. No logs exist
//   from when it ran, so there is no way to check retroactively.
//   This ports its exact 6-signal blend (gap/momentum/structure/flow/technical/regime,
//   flat un-measured weights, no time-ramp, no vol-regime conditioning) as a SHADOW
//   computation: it runs alongside the real engine on every window, gets logged next to
//   the REAL Kalshi entry price and REAL settlement outcome, and controls NOTHING. After
//   a few hundred windows this can be audited exactly like netCents/spotPerpDiv/kalshiLead
//   were tonight: real WR, real EV/contract, split-half validated, against the current
//   engine over the identical stretch. Logic below is otherwise UNCHANGED from the
//   uploaded file (only renamed and made ASCII-only per project invariant).
// V13.4.102: CONFLUENCE SHADOW MODULE. Ported directly from user-supplied Pine Script
//   ('Kalshi Future Trend + FGT Companion v6.2' and 'Kalshi BTC Smart Call System v13').
//   Read those files -- Tara's HalfTrend is explicitly commented as an approximation ('no
//   full state'); this ports the real state-machine version. Hull (HMA) and doji/wick
//   candle-quality did not exist in Tara at all. ATR-relative no-chase (0.75 ATR) matches
//   a preference already on record but Tara's live no-chase gate is cents-based, not
//   ATR-relative -- ported here as its own measurable quantity. CVD-from-candle-decomposition
//   was in the Pine file too but Tara already has real trade-by-trade tape (Binance/Bybit
//   aggTrade), which is strictly better than a candle-shape proxy -- skipped. The empirical
//   H-exponent/RTI-settlement logic in the Pine file matches what 13.4.86/89 already built
//   independently and validated on Tara's own archive -- confirms that work, no action.
//
//   THE ACTUAL ASK: user wants 'aligned -> lock early, tricky -> wait', naming this exact
//   mechanism in the Pine file: readyUpCount/readyDnCount, a 3-of-4 confluence score across
//   trend+candle+distance+tape. Ported verbatim as confluenceScore below.
//
//   SHADOW ONLY. Computed and logged on every window. Controls NO lock decision yet -- same
//   discipline as the V101 shadow above. Needs real settled locks before it decides anything.
const _wma=(vals,len)=>{
  if(!vals||vals.length<len)return null;
  const w=vals.slice(-len);let num=0,den=0;
  for(let i=0;i<len;i++){const wt=i+1;num+=w[i]*wt;den+=wt;}
  return den>0?num/den:null;
};
const computeHull=(closes,len)=>{
  if(!closes||closes.length<len+Math.round(Math.sqrt(len)))return null;
  const half=Math.max(1,Math.floor(len/2));
  const sq=Math.max(1,Math.round(Math.sqrt(len)));
  const series=[];
  for(let end=sq;end<=closes.length;end++){
    const w1=_wma(closes.slice(0,end),half),w2=_wma(closes.slice(0,end),len);
    if(w1==null||w2==null){series.push(null);continue;}
    series.push(2*w1-w2);
  }
  const valid=series.filter(x=>x!=null);
  if(valid.length<sq)return null;
  const hmaNow=_wma(valid,sq);
  const hmaPrev=valid.length>sq?_wma(valid.slice(0,-1),sq):hmaNow;
  if(hmaNow==null)return null;
  return{value:hmaNow,up:hmaNow>hmaPrev};
};
const computeHalfTrendReal=(hist,amplitude,channelDevMult,atrVal)=>{
  if(!hist||hist.length<amplitude+3||!(atrVal>0))return null;
  const ob=hist.slice(0,60).slice().reverse();
  const N=ob.length;
  let trend=0,nextTrend=0;
  let maxLowPrice=ob[0].l,minHighPrice=ob[0].h;
  let upLine=0,downLine=0,flipIdx=0;
  for(let i=amplitude;i<N;i++){
    const win=ob.slice(i-amplitude+1,i+1);
    const recentHigh=Math.max(...win.map(b=>b.h)),recentLow=Math.min(...win.map(b=>b.l));
    const highAvg=win.reduce((a,b)=>a+b.h,0)/win.length,lowAvg=win.reduce((a,b)=>a+b.l,0)/win.length;
    const prevTrend=trend;
    if(nextTrend===1){
      maxLowPrice=Math.max(recentLow,maxLowPrice);
      if(highAvg<maxLowPrice&&ob[i].c<ob[i-1].l){trend=1;nextTrend=0;minHighPrice=recentHigh;}
    }else{
      minHighPrice=Math.min(recentHigh,minHighPrice);
      if(lowAvg>minHighPrice&&ob[i].c>ob[i-1].h){trend=0;nextTrend=1;maxLowPrice=recentLow;}
    }
    if(trend===0){
      upLine=(prevTrend===1)?downLine:Math.max(maxLowPrice,upLine);
    }else{
      downLine=(prevTrend===0)?upLine:Math.min(minHighPrice,downLine);
    }
    if(trend!==prevTrend)flipIdx=i;
  }
  return{up:trend===0,barsSinceFlip:N-1-flipIdx,line:trend===0?upLine:downLine};
};
const computeCandleQuality=(hist,dojiRatio,wickMult)=>{
  if(!hist||!hist.length)return null;
  const c=hist[0]; // newest bar
  const range=Math.max(c.h-c.l,1e-9);
  const body=Math.abs(c.c-c.o);
  const upperWick=c.h-Math.max(c.c,c.o);
  const lowerWick=Math.min(c.c,c.o)-c.l;
  const isDoji=(body/range)<(dojiRatio??0.15);
  const topReject=upperWick>body*(wickMult??1.5);
  const botReject=lowerWick>body*(wickMult??1.5);
  return{
    confirmsUp:!isDoji&&c.c>c.o&&!topReject,
    confirmsDown:!isDoji&&c.c<c.o&&!botReject,
    isDoji,topReject,botReject,
  };
};
// noChaseATR default 0.75, matches the on-record preference. Returns strike-distance in
// ATR units and whether it exceeds the no-chase limit, for either direction.
const computeNoChaseATR=(currentPrice,targetMargin,atrVal,limitATR)=>{
  if(!(atrVal>0)||!(targetMargin>0))return null;
  const distATR=(currentPrice-targetMargin)/atrVal;
  const lim=limitATR??0.75;
  return{distATR:Math.round(distATR*100)/100,noChaseUp:distATR>lim,noChaseDown:distATR<-lim};
};
// The actual mechanism the user asked for: 3-of-4 confluence (trend/candle/distance/tape).
// Ported verbatim from readyUpCount/readyDnCount in the Pine file.
const computeConfluenceScore=(p)=>{
  try{
    const{liveHistory,currentPrice,targetMargin,flowImbalance,dir}=p;
    // flowImbalance is globalFlow.imbalance (ratio, 1.0=neutral, >1=buy-dominant) -- convert
    //   to a 0-100 buy-percent so the tape check matches the Pine file's convention.
    const tapeBuyPct=Number.isFinite(flowImbalance)?Math.max(0,Math.min(100,50+(flowImbalance-1)*50)):50;
    // V13.4.104 CRASH FIX: was atrVal:atr from the caller -- 'atr' at that call site was a
    //   completely different scope's variable (a different hook, line ~9993), not visible
    //   inside the analysis useMemo where this is wired. Caused 'ReferenceError: atr is not
    //   defined' on every render, a full app crash. I checked the name existed SOMEWHERE in
    //   the file and wrongly assumed that meant it was in scope here -- did not verify the
    //   call site itself. Fix: compute ATR locally from liveHistory, same as Hull/HalfTrend
    //   already do below, so this function has zero dependency on caller scope for ATR.
    const atrVal=calcATR(liveHistory,14)||0;
    if(!liveHistory||liveHistory.length<40||!(atrVal>0)||!(targetMargin>0)||!dir)return null;
    const closes=liveHistory.slice().reverse().map(b=>b.c); // oldest->newest for Hull
    const hull=computeHull(closes,34);
    const ht=computeHalfTrendReal(liveHistory,2,2,atrVal);
    const cq=computeCandleQuality(liveHistory,0.15,1.5);
    const nc=computeNoChaseATR(currentPrice,targetMargin,atrVal,0.75);
    if(!hull||!ht||!cq||!nc)return null;
    const trendReady=dir==='UP'?(hull.up&&ht.up):(!hull.up&&!ht.up);
    const candleReady=dir==='UP'?cq.confirmsUp:cq.confirmsDown;
    const distanceReady=dir==='UP'?!nc.noChaseUp:!nc.noChaseDown;
    const tapeVal=Number.isFinite(tapeBuyPct)?tapeBuyPct:50;
    const tapeReady=dir==='UP'?tapeVal>=50:tapeVal<=50;
    const score=(trendReady?1:0)+(candleReady?1:0)+(distanceReady?1:0)+(tapeReady?1:0);
    return{
      score,trendReady,candleReady,distanceReady,tapeReady,
      hullUp:hull.up,halfTrendUp:ht.up,noChaseDistATR:nc.distATR,
      aligned:score>=3,
    };
  }catch(_eConf){return null;}
};
const V101_WEIGHTS={gap:35,momentum:30,structure:15,flow:20,technical:25,regime:15};
const _v101RSI=(closes,period)=>{
  if(!closes||closes.length<period+1)return null;
  let gains=0,losses=0;
  for(let i=closes.length-period;i<closes.length;i++){
    const d=closes[i]-closes[i-1];
    if(d>=0)gains+=d;else losses-=d;
  }
  const avgG=gains/period,avgL=losses/period;
  if(avgL===0)return 100;
  const rs=avgG/avgL;
  return 100-(100/(1+rs));
};
const _v101ATR=(hist,period)=>{
  if(!hist||hist.length<period+1)return null;
  let sum=0,n=0;
  for(let i=hist.length-period;i<hist.length;i++){
    const h=hist[i].h??hist[i].c,l=hist[i].l??hist[i].c,pc=hist[i-1].c;
    const tr=Math.max(h-l,Math.abs(h-pc),Math.abs(l-pc));
    sum+=tr;n++;
  }
  return n>0?sum/n:null;
};
const _v101VWAP=(hist)=>{
  if(!hist||!hist.length)return null;
  let pv=0,vv=0;
  for(const c of hist){const v=c.v||1;pv+=(c.c||0)*v;vv+=v;}
  return vv>0?pv/vv:null;
};
const _v101BB=(closesRev,period)=>{
  if(!closesRev||closesRev.length<period)return{pctB:0.5};
  const w=closesRev.slice(0,period);
  const mean=w.reduce((a,b)=>a+b,0)/period;
  const sd=Math.sqrt(w.reduce((a,b)=>a+(b-mean)*(b-mean),0)/period);
  const upper=mean+2*sd,lower=mean-2*sd;
  const last=w[0];
  const pctB=(upper-lower)>0?(last-lower)/(upper-lower):0.5;
  return{pctB,upper,lower,mean};
};
const _v101Consecutive=(hist)=>{
  if(!hist||hist.length<2)return{green:0,red:0};
  let green=0,red=0;
  for(let i=hist.length-1;i>0;i--){
    const up=hist[i].c>=hist[i-1].c;
    if(up){if(red>0)break;green++;}else{if(green>0)break;red++;}
  }
  return{green,red};
};
const _v101VolRatio=(hist,fast,slow)=>{
  if(!hist||hist.length<slow)return 1;
  const f=hist.slice(-fast).reduce((a,c)=>a+(c.v||0),0)/fast;
  const s=hist.slice(-slow).reduce((a,c)=>a+(c.v||0),0)/slow;
  return s>0?f/s:1;
};
const _v101Channel=(hist,period)=>{
  if(!hist||hist.length<period)return 0.5;
  const w=hist.slice(-period);
  const hi=Math.max(...w.map(c=>c.h??c.c)),lo=Math.min(...w.map(c=>c.l??c.c));
  const last=w[w.length-1].c;
  return(hi-lo)>0?(last-lo)/(hi-lo):0.5;
};
const _v101MomAlign=(d1,d5,d15)=>{
  const sameSign=(Math.sign(d1)===Math.sign(d5))&&(Math.sign(d5)===Math.sign(d15))&&d1!==0;
  const strong=sameSign&&Math.abs(d1)>3&&Math.abs(d5)>3;
  return{aligned:sameSign,strong};
};
const computeV101ShadowPosterior=(p)=>{
  try{
    const{currentPrice,liveHistory,targetMargin,globalFlow,bloomberg,velocityRef,priceMemoryRef,is15m,timeFraction}=p;
    if(!currentPrice||!liveHistory||liveHistory.length<20||!targetMargin)return null;
    const W=V101_WEIGHTS;
    let totalScore=0;
    const closes=liveHistory.map(x=>x.c);
    const rsi=_v101RSI(closes,14)??50;
    const atr=_v101ATR(liveHistory,14)??10;
    const atrBps=atr>0&&currentPrice>0?(atr/currentPrice)*10000:15;
    const vwap=_v101VWAP(liveHistory);
    const bb=_v101BB([...closes].reverse(),20);
    const realGapBps=targetMargin>0?((currentPrice-targetMargin)/targetMargin)*10000:0;
    const vwapGapBps=vwap?((currentPrice-vwap)/vwap)*10000:0;
    const vel=(velocityRef&&velocityRef.current)||{};
    const{accel}=vel;
    const getHP=(msAgo)=>{
      const t=Date.now()-msAgo;const m=(priceMemoryRef&&priceMemoryRef.current)||[];
      if(!m.length)return currentPrice;
      let c=m[0];for(let i=m.length-1;i>=0;i--){if(m[i].time<=t){c=m[i];break;}}
      return c.p;
    };
    const drift1m=((currentPrice-getHP(60000))/currentPrice)*10000;
    const drift5m=((currentPrice-getHP(300000))/currentPrice)*10000;
    const drift15m=((currentPrice-getHP(900000))/currentPrice)*10000;
    const consecutive=_v101Consecutive(liveHistory);
    const volRatio=_v101VolRatio(liveHistory,5,25);
    const channel=_v101Channel(liveHistory,20);
    const momentumAlign=_v101MomAlign(drift1m,drift5m,drift15m);
    const aggrFlow=Math.max(-1,Math.min(1,(globalFlow&&globalFlow.imbalance||1)-1));
    // gap
    const timeDecay=Math.pow(timeFraction,is15m?1.8:1.3);
    const isPostDecay=timeFraction>0.6;
    const decayMult=isPostDecay?1.5:1.0;
    let gapScore=realGapBps*(is15m?0.65:0.85)*(0.15+0.85*timeDecay)*decayMult;
    const gapMag=Math.abs(realGapBps);
    if(gapMag>15)gapScore+=Math.sign(realGapBps)*Math.pow(gapMag-10,1.3)*(is15m?0.45:0.65);
    if(gapMag>50)gapScore*=0.7;
    totalScore+=Math.max(-W.gap,Math.min(W.gap,gapScore));
    // momentum
    let momScore=is15m?(drift5m*0.6+drift1m*0.4):((vel.v30s||0)*(10000/currentPrice)*1.5+drift1m*1.0+drift5m*0.5);
    if(momentumAlign.aligned&&momentumAlign.strong)momScore*=1.5;
    else if(momentumAlign.aligned)momScore*=1.2;
    totalScore+=Math.max(-W.momentum,Math.min(W.momentum,momScore*0.8));
    // structure
    let structScore=0;
    if(consecutive.green>=3)structScore+=consecutive.green*3;
    if(consecutive.red>=3)structScore-=consecutive.red*3;
    if(volRatio>1.5&&consecutive.green>=2)structScore+=8;
    if(volRatio>1.5&&consecutive.red>=2)structScore-=8;
    totalScore+=Math.max(-W.structure,Math.min(W.structure,structScore));
    // flow
    let flowScore=aggrFlow*(is15m?W.flow:W.flow*1.5);
    if(accel&&drift1m>0&&accel>0)flowScore+=8;
    if(accel&&drift1m<0&&accel<0)flowScore-=8;
    totalScore+=Math.max(-W.flow,Math.min(W.flow,flowScore));
    // technical
    let techScore=0;
    if(rsi>70&&realGapBps>0)techScore-=15;
    else if(rsi<30&&realGapBps<0)techScore+=15;
    else if(rsi>60&&drift1m<0)techScore-=8;
    else if(rsi<40&&drift1m>0)techScore+=8;
    if(drift1m>0&&vwapGapBps<-5)techScore-=10;
    if(drift1m<0&&vwapGapBps>5)techScore+=10;
    if(bb.pctB>0.85&&realGapBps>0)techScore-=8;
    if(bb.pctB<0.15&&realGapBps<0)techScore+=8;
    if(channel>0.8&&drift1m>0)techScore-=5;
    if(channel<0.2&&drift1m<0)techScore+=5;
    totalScore+=Math.max(-W.technical,Math.min(W.technical,techScore));
    // regime/funding
    const {funding,fundingPrev}=observedFundingPair(bloomberg);
    const delta=(globalFlow&&globalFlow.deltaUSD)||0;
    let regime='RANGE/CHOP',regimeBonus=0;
    const isHighVol=atrBps>35;
    const retailShorting=funding<0.005,retailLonging=funding>0.015;
    const whalesBuying=delta>500000,whalesSelling=delta<-500000;
    const isCleanUp=drift1m>5&&whalesBuying&&atrBps<30;
    const isCleanDn=drift1m<-5&&whalesSelling&&atrBps<30;
    const fundingAccel=(funding-fundingPrev);
    if(retailShorting&&whalesBuying){regime='SHORT SQUEEZE';regimeBonus=W.regime;}
    else if(retailLonging&&whalesSelling){regime='LONG SQUEEZE';regimeBonus=-W.regime;}
    else if(isCleanUp){regime='TRENDING UP';}
    else if(isCleanDn){regime='TRENDING DOWN';}
    else if(isHighVol){regime='HIGH VOL CHOP';}
    if(fundingAccel>0.0001)regimeBonus-=5;
    if(fundingAccel<-0.0001)regimeBonus+=5;
    totalScore+=Math.max(-W.regime,Math.min(W.regime,regimeBonus));
    // convert + reality caps (unchanged from original)
    const rawPosterior=50+totalScore*0.95;
    let posterior=Math.max(1,Math.min(99,rawPosterior));
    if(realGapBps<-40)posterior=Math.min(posterior,18);
    else if(realGapBps<-18)posterior=Math.min(posterior,42);
    else if(realGapBps>40)posterior=Math.max(posterior,82);
    else if(realGapBps>18)posterior=Math.max(posterior,58);
    return{posterior:Math.round(posterior*10)/10,regime,atrBps:Math.round(atrBps*10)/10};
  }catch(_eV101){return null;}
};
const taraSetVizInterval=(fn,ms,maxMs)=>{
  const backoff=(function(){try{const v=parseFloat(localStorage.getItem('taraVizBackoff'));return(Number.isFinite(v)&&v>=1)?v:4;}catch(_e){return 4;}})();
  const cap=Number.isFinite(maxMs)?maxMs:60000;
  let cur=ms,id=setInterval(fn,cur);
  const rearm=()=>{
    const hidden=(typeof document!=='undefined'&&document.visibilityState==='hidden');
    const want=hidden?Math.min(Math.round(ms*backoff),cap):ms;
    if(want!==cur){clearInterval(id);cur=want;id=setInterval(fn,cur);}
  };
  if(typeof document!=='undefined')document.addEventListener('visibilitychange',rearm);
  return{clear:()=>{clearInterval(id);if(typeof document!=='undefined')document.removeEventListener('visibilitychange',rearm);}};
};
// V13.4.48: background keep-alive with a VISIBLE, verifiable status indicator.
//   Desktop tabs are timer-throttled when hidden (~1/min after 5min), which was
//   causing missed-backgrounded windows (63% of apparent sit-outs). A tab playing
//   audio is exempt, so we loop a silent WAV. Autoplay rules require one user
//   gesture to start; the pill makes that explicit and confirms it is live.
(function(){
  if(typeof document==='undefined'||typeof window==='undefined')return;
  var _el=null,_pill=null,_armed=false;
  function _silentWav(){
    try{
      var sr=8000,n=sr,tot=44+n*2,buf=new ArrayBuffer(tot),dv=new DataView(buf);
      function w(o,s){for(var i=0;i<s.length;i++)dv.setUint8(o+i,s.charCodeAt(i));}
      w(0,'RIFF');dv.setUint32(4,36+n*2,true);w(8,'WAVE');w(12,'fmt ');
      dv.setUint32(16,16,true);dv.setUint16(20,1,true);dv.setUint16(22,1,true);
      dv.setUint32(24,sr,true);dv.setUint32(28,sr*2,true);dv.setUint16(32,2,true);
      dv.setUint16(34,16,true);w(36,'data');dv.setUint32(40,n*2,true);
      // V13.4.49: fill with a 30Hz tone at ~-48dBFS. A laptop speaker cannot
      //   reproduce 30Hz at this level (inaudible), but Chrome still counts the
      //   tab as playing audio, which is what grants the background-throttle
      //   exemption. Pure silence did not reliably trigger it.
      var _amp=120,_st=2*Math.PI*30/sr;
      for(var _s=0;_s<n;_s++){dv.setInt16(44+_s*2,Math.round(_amp*Math.sin(_st*_s)),true);}
      var u8=new Uint8Array(buf),bin='';for(var i=0;i<u8.length;i++)bin+=String.fromCharCode(u8[i]);
      return 'data:audio/wav;base64,'+btoa(bin);
    }catch(_){return null;}
  }
  function _render(){
    if(!_pill)return;
    if(_armed){
      _pill.textContent='';
      _pill.style.width='10px';_pill.style.height='10px';_pill.style.padding='0';
      _pill.style.borderRadius='50%';_pill.style.background='#23B981';
      _pill.style.boxShadow='0 0 6px #23B981';
      _pill.title='Tara keep-alive ON: background windows protected while this tab stays open';
    }else{
      _pill.textContent='background locking OFF - click';
      _pill.style.width='auto';_pill.style.height='auto';
      _pill.style.padding='4px 8px';_pill.style.borderRadius='6px';
      _pill.style.background='#E8455E';_pill.style.boxShadow='none';
      _pill.title='Click to keep Tara scanning when this tab is in the background';
    }
  }
  function _mkPill(){
    if(_pill||!document.body)return;
    _pill=document.createElement('div');
    _pill.style.cssText='position:fixed;left:10px;bottom:10px;z-index:2147483647;'+
      'font:600 11px system-ui,sans-serif;color:#050508;cursor:pointer;user-select:none;transition:all .2s;';
    _pill.addEventListener('click',function(ev){ev.stopPropagation();_ensure();});
    document.body.appendChild(_pill);_render();
  }
  function _ensure(){
    try{
      if(!_el){
        var src=_silentWav();if(!src)return;
        _el=document.createElement('audio');
        _el.loop=true;_el.volume=1;_el.src=src;
        _el.setAttribute('aria-hidden','true');_el.style.display='none';
        (document.body||document.documentElement).appendChild(_el);
        _el.addEventListener('playing',function(){_armed=true;_render();});
        _el.addEventListener('pause',function(){_armed=false;_render();});
      }
      var p=_el.play();
      if(p&&p.then)p.then(function(){_armed=true;_render();}).catch(function(){_armed=false;_render();});
    }catch(_){}
  }
  ['pointerdown','keydown','click','touchstart'].forEach(function(ev){
    window.addEventListener(ev,_ensure,{passive:true});
  });
  document.addEventListener('visibilitychange',function(){if(document.visibilityState==='visible')_ensure();});
  window.addEventListener('focus',_ensure);
  if(document.body)_mkPill();else window.addEventListener('DOMContentLoaded',_mkPill);
  window._taraKeepAlive={status:function(){return{armed:_armed,paused:_el?_el.paused:null};},arm:_ensure};
})();

// ═══════════════════════════════════════════════════════════════════════════
// V10.2.0 — PHASE 4 LATEST-DECISION PUB/SUB (cross-component bridge)
// ═══════════════════════════════════════════════════════════════════════════
// The Phase 4 evaluation runs inside TaraApp's auto-exec entry effect. The lock
//   UI badge that needs to display the decision lives in a different component
//   (ScalperAdvisorPanel) that doesn't share refs with TaraApp.
//
// Solution: small module-level pub/sub. TaraApp's Phase 4 effect calls
//   _setPhase4Decision(d) after each evaluation. Any component can subscribe via
//   the _usePhase4Decision() hook to receive the latest value reactively.
//
// Decision shape: { decision, score, reason, mode, ts } — same shape as
//   _phase4DecisionRef.current. Subscribers re-render on each new decision.
let _phase4LatestDecision=null;
const _phase4Subscribers=new Set();
const _setPhase4Decision=(d)=>{
  _phase4LatestDecision=d;
  _phase4Subscribers.forEach(fn=>{try{fn(d);}catch(_){}});
};
const _usePhase4Decision=()=>{
  const[d,setD]=React.useState(_phase4LatestDecision);
  React.useEffect(()=>{
    _phase4Subscribers.add(setD);
    return ()=>{_phase4Subscribers.delete(setD);};
  },[]);
  return d;
};

// V10.2.0 — Phase 4 status badge. Subscribes to _phase4LatestDecision via the
//   pub/sub above. Renders nothing when mode is 'off' or no decision exists yet.
//   Used by _renderPredictorHeader to surface what Phase 4 is doing right now,
//   without affecting any trading logic. Color coded: fire-now=emerald,
//   wait=gold, abort=rose, error=gray.
const _Phase4Badge=({mode})=>{
  const d=_usePhase4Decision();
  if(mode==='off'||!mode)return null;
  if(!d||!d.decision)return React.createElement('div',{
    className:'text-[9px] tracking-wider mb-2 px-2 py-0.5 rounded-lg text-center',
    style:{background:'rgba(237,237,237,0.04)',color:'rgba(237,237,237,0.35)'},
    title:`Phase 4 is in '${mode}' mode but no decision yet — waiting for next lock.`,
  },`phase 4 · ${mode} · waiting`);
  const _palette={
    'fire-now':{bg:'rgba(35,185,129,0.10)',fg:'rgb(35,185,129)',border:'rgba(35,185,129,0.25)'},
    'wait':    {bg:'rgba(35,185,129,0.10)',fg:'#23B981',         border:'rgba(35,185,129,0.25)'},
    'abort':   {bg:'rgba(244,114,128,0.10)',fg:'rgb(244,114,128)',border:'rgba(244,114,128,0.25)'},
    'error':   {bg:'rgba(237,237,237,0.05)',fg:'rgba(237,237,237,0.40)',border:'rgba(237,237,237,0.12)'},
  };
  const c=_palette[d.decision]||_palette.error;
  const _ageSec=d.ts?Math.max(0,Math.floor((Date.now()-d.ts)/1000)):null;
  const _ageLabel=_ageSec==null?'':(_ageSec<60?`${_ageSec}s ago`:`${Math.floor(_ageSec/60)}m ago`);
  return React.createElement('div',{
    className:'text-[9px] tracking-wider mb-2 px-2 py-0.5 rounded-lg text-center flex items-center justify-center gap-1.5',
    style:{background:c.bg,color:c.fg,border:`1px solid ${c.border}`},
    title:`Phase 4 (${mode}) · decision: ${d.decision} · score: ${d.score!=null?d.score:'n/a'}\nTop reasons: ${d.reason||'n/a'}\n${_ageLabel}`,
  },[
    React.createElement('span',{key:'mode',style:{opacity:0.55}},`phase 4 · ${mode} ·`),
    React.createElement('span',{key:'dec',className:'font-bold uppercase'},d.decision),
    d.score!=null&&React.createElement('span',{key:'score',style:{opacity:0.55}},`(${d.score>=0?'+':''}${d.score})`),
  ]);
};

// ═══════════════════════════════════════════════════════════════════════════
// V10.2.0 — PHASE 4 SHADOW DATA ANALYZER (DevTools console hook)
// ═══════════════════════════════════════════════════════════════════════════
// Reads taraCallLog from localStorage, groups by tradeTimingDecision, computes
//   per-group WR + sample stats + top reasons.
//
// Run in DevTools console:
//
//   window.__taraPhase4Stats()
//
// Returns a summary array and prints a console.table. Also checks the graduation
//   gate for advisory promotion (≥50 resolved samples per decision class, with
//   Phase 4 'abort' WR ≥10pt below 'fire-now' WR).
//
// IMPORTANT: only counts entries where Phase 4 was running (mode≠off and a
//   decision was logged). Pre-V10.0.0 trades have no Phase 4 data and show up
//   as 'no-decision'. Expect this bucket to be large early on; ignore it.
if(typeof window!=='undefined'){
  window.__taraPhase4Stats=()=>{
    try{
      const raw=localStorage.getItem('taraCallLog_v1');
      if(!raw){console.warn('[Phase4Stats] no call log in localStorage');return null;}
      const log=JSON.parse(raw);
      if(!Array.isArray(log)){console.warn('[Phase4Stats] call log malformed');return null;}
      const groups={'fire-now':[],'wait':[],'abort':[],'error':[],'no-decision':[]};
      log.forEach(e=>{
        if(!e)return;
        const d=e.tradeTimingDecision;
        const key=(d&&groups[d]!==undefined)?d:'no-decision';
        groups[key].push(e);
      });
      const summary=Object.entries(groups).map(([decision,entries])=>{
        const resolved=entries.filter(e=>e.result==='W'||e.result==='L');
        const wins=resolved.filter(e=>e.result==='W').length;
        const losses=resolved.filter(e=>e.result==='L').length;
        const wrNum=resolved.length>0?(wins/resolved.length*100):null;
        const scores=entries.map(e=>Number(e.tradeTimingScore)).filter(n=>Number.isFinite(n));
        const avgScore=scores.length>0?(scores.reduce((a,b)=>a+b,0)/scores.length):null;
        const reasons=entries.map(e=>e.tradeTimingReason).filter(r=>r);
        const reasonCounts={};
        reasons.forEach(r=>{reasonCounts[r]=(reasonCounts[r]||0)+1;});
        const topReasons=Object.entries(reasonCounts).sort((a,b)=>b[1]-a[1]).slice(0,3).map(([r,c])=>`${r}(${c})`).join(' | ');
        return{
          decision,
          count:entries.length,
          resolved:resolved.length,
          wins,
          losses,
          winRate:wrNum!=null?wrNum.toFixed(1)+'%':'n/a',
          avgScore:avgScore!=null?avgScore.toFixed(1):'n/a',
          topReasons:topReasons||'n/a',
        };
      }).filter(r=>r.count>0);
      console.info('━━━ Phase 4 Shadow Stats ━━━');
      try{console.table(summary);}catch(_){console.info(summary);}
      // Graduation gate check
      const fireGate=summary.find(s=>s.decision==='fire-now');
      const abortGate=summary.find(s=>s.decision==='abort');
      const GATE_N=50;
      if(fireGate&&abortGate&&fireGate.resolved>=GATE_N&&abortGate.resolved>=GATE_N){
        const fireWR=parseFloat(fireGate.winRate);
        const abortWR=parseFloat(abortGate.winRate);
        const delta=fireWR-abortWR;
        console.info(`Graduation gate: fire-now WR=${fireWR.toFixed(1)}%, abort WR=${abortWR.toFixed(1)}%, delta=${delta.toFixed(1)}pt`);
        if(delta>=10){console.info('✓ Phase 4 has signal — ready for ADVISORY promotion');}
        else{console.warn(`Phase 4 abort decisions only ${delta.toFixed(1)}pt below fire-now. Needs ≥10pt gap or tuning.`);}
      }else{
        console.info(`Graduation gate: need ≥${GATE_N} resolved per decision. Current: fire-now=${fireGate?fireGate.resolved:0}, abort=${abortGate?abortGate.resolved:0}.`);
      }
      return summary;
    }catch(e){console.error('[Phase4Stats] failed:',e&&e.message);return null;}
  };
  try{console.info('[Phase4Stats] hook ready — run window.__taraPhase4Stats() in console');}catch(_){}
}

// ═══════════════════════════════════════════════════════════════════════════
// V10.2.x — TARA AUDIT STATS (DevTools console hook)
// ═══════════════════════════════════════════════════════════════════════════
// Re-runs the May 14 audit on demand. Reads taraCallLog from localStorage,
//   computes WR breakdowns across multiple dimensions, and compares each to
//   the May 14 baselines (642-trade audit) so drift is visible.
//
// Run from DevTools console:
//
//   window.__taraAuditStats()                       → everything, all trades
//   window.__taraAuditStats({autoExecOnly:true})    → auto-exec subset only
//   window.__taraAuditStats({asset:'BTC'})          → BTC only
//   window.__taraAuditStats({asset:'ETH',autoExecOnly:true,minN:5})
//
// Output: console.table blocks per dimension — tier, regime, edge bucket,
//   session, window type, asset, direction, auto/manual split, time-cap-commit
//   sub-bands (the V9.19.26 90-119s finding), and v1 vs v2 score-bucket
//   monotonicity comparison (does qScoreV2 predict better than qScore?).
//
// Each row shows n, wins, losses, WR — plus baseline + delta where a May 14
//   number exists. Use to decide which knobs need re-tuning and which clusters
//   are bleeding vs holding their baseline.
if(typeof window!=='undefined'){
  // May 14 audit baselines (642 trades). Empty entries = no baseline captured.
  const _AUDIT_BASELINES={
    tier:{
      'time-cap-commit':62.4,
      'timer-commit':62.4,
      'normal':78.5,
      'early':65.7,
    },
    edge:{
      'edge ≤ -10pt':80.0,
      'edge 0-10pt':70.6,
      'edge 10-20pt':65.1,
      'edge 20-30pt':60.2,
      'edge 30+pt':65.2,
    },
    tcBand:{
      '90-119s':54.6,
    },
  };
  window.__taraAuditStats=(opts={})=>{
    try{
      const raw=localStorage.getItem('taraCallLog_v1');
      if(!raw){console.warn('[AuditStats] no call log in localStorage');return null;}
      const log=JSON.parse(raw);
      if(!Array.isArray(log)){console.warn('[AuditStats] call log malformed');return null;}
      // Filters
      const _asset=opts.asset||opts.assetFilter||'all';
      const _autoOnly=opts.autoExecOnly===true;
      const _minN=Number.isFinite(opts.minN)?opts.minN:3;
      let filtered=log;
      if(_asset!=='all')filtered=filtered.filter(e=>(e?.asset||'BTC')===_asset);
      if(_autoOnly)filtered=filtered.filter(e=>e?.autoExec===true);
      const resolved=filtered.filter(e=>e?.result==='WIN'||e?.result==='LOSS');
      const _wrPct=(w,n)=>n>0?(w/n*100).toFixed(1)+'%':'n/a';
      const _wrNum=(w,n)=>n>0?(w/n*100):null;
      const _delta=(curWR,baseWR)=>{
        if(curWR==null||baseWR==null)return '—';
        const d=curWR-baseWR;
        return (d>=0?'+':'')+d.toFixed(1)+'pt';
      };
      // Generic grouping helper.
      const _groupBy=(entries,keyFn,baselineMap)=>{
        const groups={};
        entries.forEach(e=>{
          const k=keyFn(e);
          if(k==null||k===undefined||k==='')return;
          if(!groups[k])groups[k]={n:0,w:0,l:0};
          groups[k].n++;
          if(e.result==='WIN')groups[k].w++;
          else if(e.result==='LOSS')groups[k].l++;
        });
        return Object.entries(groups)
          .filter(([,v])=>v.n>=_minN)
          .map(([k,v])=>{
            const wr=_wrNum(v.w,v.n);
            const baseWR=baselineMap?.[k]??null;
            return{
              bucket:k,
              n:v.n,
              wins:v.w,
              losses:v.l,
              WR:_wrPct(v.w,v.n),
              baselineMay14:baseWR!=null?baseWR.toFixed(1)+'%':'—',
              delta:_delta(wr,baseWR),
            };
          })
          .sort((a,b)=>b.n-a.n);
      };
      const _overallW=resolved.filter(e=>e.result==='WIN').length;
      console.group(`%c━━━ Tara Audit Stats ━━━`,'color:#23B981;font-weight:bold;font-size:13px');
      console.info(`Filter: asset=${_asset}, autoExecOnly=${_autoOnly}, minN=${_minN}`);
      console.info(`Entries: ${filtered.length} total · ${resolved.length} resolved · ${filtered.length-resolved.length} pending/sit-out`);
      console.info(`Overall WR: ${_wrPct(_overallW,resolved.length)} (${_overallW}W / ${resolved.length-_overallW}L)`);
      // ── BY TIER ────────────────────────────────────────────────────────
      console.group('By tier');
      try{console.table(_groupBy(resolved,e=>e.tier||'untiered',_AUDIT_BASELINES.tier));}catch(_){}
      console.groupEnd();
      // ── BY REGIME ──────────────────────────────────────────────────────
      console.group('By regime');
      try{console.table(_groupBy(resolved,e=>e.regime||'unknown'));}catch(_){}
      console.groupEnd();
      // ── BY EDGE BUCKET (Tara conf - Kalshi conf, on Tara's direction) ─
      console.group('By edge bucket — Tara conv minus Kalshi conv, on her dir');
      try{console.table(_groupBy(resolved,e=>{
        const _post=Number(e.posterior),_k=Number(e.kalshiAtLock);
        if(!Number.isFinite(_post)||!Number.isFinite(_k)||!e.dir)return null;
        const _taraConf=e.dir==='UP'?_post:(100-_post);
        const _kalConf=e.dir==='UP'?_k:(100-_k);
        const _edge=_taraConf-_kalConf;
        if(_edge<=-10)return 'edge ≤ -10pt';
        if(_edge<0)return 'edge -10 to 0';
        if(_edge<10)return 'edge 0-10pt';
        if(_edge<20)return 'edge 10-20pt';
        if(_edge<30)return 'edge 20-30pt';
        return 'edge 30+pt';
      },_AUDIT_BASELINES.edge));}catch(_){}
      console.groupEnd();
      // ── BY SESSION ─────────────────────────────────────────────────────
      console.group('By session (London/Asia/NY/OFF)');
      try{console.table(_groupBy(resolved,e=>e.session||'unknown'));}catch(_){}
      console.groupEnd();
      // ── BY WINDOW TYPE ─────────────────────────────────────────────────
      console.group('By window type');
      try{console.table(_groupBy(resolved,e=>e.windowType||'unknown'));}catch(_){}
      console.groupEnd();
      // ── BY ASSET (only meaningful if not pre-filtered to one) ──────────
      if(_asset==='all'){
        console.group('By asset');
        try{console.table(_groupBy(resolved,e=>e.asset||'BTC'));}catch(_){}
        console.groupEnd();
      }
      // ── BY DIRECTION ───────────────────────────────────────────────────
      console.group('By direction');
      try{console.table(_groupBy(resolved,e=>e.dir||'unknown'));}catch(_){}
      console.groupEnd();
      // ── AUTO vs MANUAL (only if not pre-filtered to auto-only) ─────────
      if(!_autoOnly){
        console.group('Auto-exec vs manual');
        try{console.table(_groupBy(resolved,e=>e.autoExec===true?'auto-exec':'manual'));}catch(_){}
        console.groupEnd();
      }
      // ── TIME-CAP-COMMIT SUB-BANDS (V9.19.26 finding) ───────────────────
      const _tcResolved=resolved.filter(e=>e.tier==='time-cap-commit'||e.tier==='timer-commit');
      if(_tcResolved.length>=_minN){
        console.group(`Time-cap-commit sub-bands · n=${_tcResolved.length} (May 14: 90-119s was worst at 54.6%)`);
        try{console.table(_groupBy(_tcResolved,e=>{
          const _sec=Number(e.secondsIntoWindow);
          if(!Number.isFinite(_sec))return null;
          if(_sec<60)return '0-59s';
          if(_sec<90)return '60-89s';
          if(_sec<120)return '90-119s';
          if(_sec<180)return '120-179s';
          return '180s+';
        },_AUDIT_BASELINES.tcBand));}catch(_){}
        console.groupEnd();
      }
      // ── qScore v1 vs v2 MONOTONICITY ─────────────────────────────────
      // If higher score → higher WR consistently in BOTH columns, both
      //   models are calibrated. If v2 shows tighter monotonicity than v1,
      //   v2 is more predictive — graduate it from shadow to primary.
      const _scoreBucket=(s)=>{
        const _n=Number(s);
        if(!Number.isFinite(_n))return null;
        if(_n<20)return '0-19';
        if(_n<40)return '20-39';
        if(_n<60)return '40-59';
        if(_n<80)return '60-79';
        return '80-100';
      };
      const _v1Rows=_groupBy(resolved,e=>_scoreBucket(e.qScore));
      const _v2Rows=_groupBy(resolved,e=>_scoreBucket(e.qScoreV2));
      const _hasV2=_v2Rows.length>0;
      if(_v1Rows.length>0){
        console.group(`Score-bucket monotonicity${_hasV2?' (v1 vs v2)':''}`);
        try{console.info('qScore v1 (current production):');console.table(_v1Rows);}catch(_){}
        if(_hasV2){try{console.info('qScoreV2 (shadow, V9.19.10+):');console.table(_v2Rows);}catch(_){}}
        else{console.info('qScoreV2: no data in log (older entries pre-V9.19.10 don\'t have it)');}
        console.groupEnd();
      }
      console.groupEnd(); // close the outer audit group
      // Return the structured data so it can be assigned to a var for further analysis
      return{
        filter:{asset:_asset,autoExecOnly:_autoOnly,minN:_minN},
        totals:{entries:filtered.length,resolved:resolved.length,wins:_overallW,losses:resolved.length-_overallW,WR:_wrPct(_overallW,resolved.length)},
      };
    }catch(e){console.error('[AuditStats] failed:',e&&e.message);return null;}
  };
  try{console.info('[AuditStats] hook ready — run window.__taraAuditStats() in console');}catch(_){}

  // ═════════════════════════════════════════════════════════════════════════
  // V10.2.13 — TIER × SESSION/HOUR/WINDOW CROSS-TAB AUDITS
  // ═════════════════════════════════════════════════════════════════════════
  // Three new console hooks for testing the hypothesis: does tier performance
  // vary by session-of-day or hour-of-day? If yes, we know which tier to
  // boost when. If no, the optimizer infrastructure stays off — no harm done.
  //
  //   window.__taraTierBySession()  → 4×N table: ASIA/EU/US/OFF × tier
  //   window.__taraTierByHour()     → 24×N table: hour-of-day × tier
  //   window.__taraTierByWindow()   → 2×N table: 5m vs 15m × tier
  //
  // Each cell shows: WR% (n) — and is dimmed if n < minN (default 10).
  // After the table, prints which tier wins each row and by how many points.
  //
  // Options: {minN: 10, autoExecOnly: false, asset: null, windowType: null}
  // ═════════════════════════════════════════════════════════════════════════
  const _readCallLog=()=>{
    try{
      const raw=localStorage.getItem('taraCallLog_v1');
      if(!raw)return null;
      const log=JSON.parse(raw);
      return Array.isArray(log)?log:null;
    }catch(_){return null;}
  };
  const _filterLog=(log,opts)=>{
    const _autoOnly=opts?.autoExecOnly===true;
    const _asset=opts?.asset||null;
    const _windowType=opts?.windowType||null;
    return log.filter(e=>{
      if(!e)return false;
      if(_autoOnly&&!e.autoExec)return false;
      if(_asset&&e.asset!==_asset)return false;
      if(_windowType&&e.windowType!==_windowType)return false;
      return e.result==='WIN'||e.result==='LOSS';
    });
  };
  const _wrCell=(wins,n,minN)=>{
    if(n===0)return{label:'·',raw:null,n:0,dim:true};
    const pct=Math.round((wins/n)*1000)/10;
    return{label:`${pct}% (${n})`,raw:pct,n,dim:n<minN};
  };
  const _printCrossTab=(title,table,rowLabels,colLabels,minN)=>{
    console.group(`%c━━━ ${title} ━━━`,'color:#23B981;font-weight:bold;font-size:13px');
    // Convert to console.table-friendly shape
    const _rows={};
    rowLabels.forEach(r=>{
      _rows[r]={};
      colLabels.forEach(c=>{
        const cell=table[r]?.[c];
        _rows[r][c]=cell?cell.label:'·';
      });
    });
    console.table(_rows);
    // Best-tier-per-row analysis
    console.info('%cBest tier per row (n ≥ '+minN+'):','color:#23B981;font-weight:bold');
    rowLabels.forEach(r=>{
      const cells=colLabels.map(c=>({col:c,...table[r]?.[c]}));
      const valid=cells.filter(c=>c&&c.raw!=null&&c.n>=minN);
      if(valid.length===0){
        console.info(`  ${r}: (no cells with n≥${minN})`);
        return;
      }
      const best=valid.reduce((a,b)=>b.raw>a.raw?b:a);
      const worst=valid.reduce((a,b)=>b.raw<a.raw?b:a);
      const gap=best.raw-worst.raw;
      const gapStr=gap>=10?`%c+${gap.toFixed(1)}pt gap`:`+${gap.toFixed(1)}pt gap`;
      const style=gap>=10?'color:rgb(35,185,129);font-weight:bold':'color:rgba(237,237,237,0.55)';
      if(gap>=10){
        console.info(`  ${r}: ${best.col} wins (${best.label}) vs ${worst.col} (${worst.label}) — ${gapStr}`,style);
      }else{
        console.info(`  ${r}: ${best.col} (${best.label}) — gap only +${gap.toFixed(1)}pt vs ${worst.col} (${worst.label})`);
      }
    });
    console.groupEnd();
  };
  const _buildCrossTab=(entries,rowFn,colFn)=>{
    const _table={};
    const _rowsSet=new Set();
    const _colsSet=new Set();
    entries.forEach(e=>{
      const r=rowFn(e);
      const c=colFn(e);
      if(r==null||c==null||r===''||c==='')return;
      _rowsSet.add(r);_colsSet.add(c);
      if(!_table[r])_table[r]={};
      if(!_table[r][c])_table[r][c]={wins:0,n:0};
      _table[r][c].n++;
      if(e.result==='WIN')_table[r][c].wins++;
    });
    return{table:_table,rows:Array.from(_rowsSet),cols:Array.from(_colsSet)};
  };
  const _normalizeTier=(t)=>{
    if(!t)return 'untiered';
    const s=String(t).toLowerCase();
    // Collapse variants: tape-led, tape → 'tape'; structural-led, structural → 'structural'; etc.
    if(s.includes('super')||s.includes('confluence'))return 'confluence';
    if(s.includes('structural'))return 'structural';
    if(s.includes('tape'))return 'tape';
    if(s.includes('time-cap')||s.includes('timer'))return 'time-cap';
    if(s.includes('single'))return 'single';
    return s;
  };

  window.__taraTierBySession=(opts={})=>{
    const minN=Number(opts.minN)||10;
    const log=_readCallLog();
    if(!log){console.warn('[TierBySession] no call log');return null;}
    const entries=_filterLog(log,opts);
    const{table,rows,cols}=_buildCrossTab(entries,e=>e.session||'unknown',e=>_normalizeTier(e.tier));
    // Build display table with WR cells
    const _displayTable={};
    rows.forEach(r=>{
      _displayTable[r]={};
      cols.forEach(c=>{
        const cell=table[r]?.[c];
        _displayTable[r][c]=cell?_wrCell(cell.wins,cell.n,minN):{label:'·',n:0,dim:true};
      });
    });
    // Sort: standard session order, then tiers by overall sample size desc
    const _sessionOrder=['ASIA','EU','US','OFF-HOURS','unknown'];
    const _orderedRows=[..._sessionOrder.filter(s=>rows.includes(s)),...rows.filter(r=>!_sessionOrder.includes(r))];
    const _tierTotals={};
    cols.forEach(c=>{_tierTotals[c]=rows.reduce((sum,r)=>sum+(table[r]?.[c]?.n||0),0);});
    const _orderedCols=cols.slice().sort((a,b)=>_tierTotals[b]-_tierTotals[a]);
    _printCrossTab(`Tier × Session (n=${entries.length} resolved trades, minN=${minN})`,_displayTable,_orderedRows,_orderedCols,minN);
    return{table:_displayTable,rows:_orderedRows,cols:_orderedCols};
  };

  window.__taraTierByHour=(opts={})=>{
    const minN=Number(opts.minN)||10;
    const log=_readCallLog();
    if(!log){console.warn('[TierByHour] no call log');return null;}
    const entries=_filterLog(log,opts);
    const{table,rows,cols}=_buildCrossTab(entries,
      e=>{
        const t=e.timestampISO||e.timestamp||null;
        if(!t)return null;
        const d=new Date(t);
        if(!d.getTime())return null;
        const h=d.getUTCHours();
        return`${String(h).padStart(2,'0')}:00 UTC`;
      },
      e=>_normalizeTier(e.tier)
    );
    const _displayTable={};
    rows.forEach(r=>{
      _displayTable[r]={};
      cols.forEach(c=>{
        const cell=table[r]?.[c];
        _displayTable[r][c]=cell?_wrCell(cell.wins,cell.n,minN):{label:'·',n:0,dim:true};
      });
    });
    const _orderedRows=rows.slice().sort();
    const _tierTotals={};
    cols.forEach(c=>{_tierTotals[c]=rows.reduce((sum,r)=>sum+(table[r]?.[c]?.n||0),0);});
    const _orderedCols=cols.slice().sort((a,b)=>_tierTotals[b]-_tierTotals[a]);
    _printCrossTab(`Tier × Hour-of-day UTC (n=${entries.length} resolved trades, minN=${minN})`,_displayTable,_orderedRows,_orderedCols,minN);
    return{table:_displayTable,rows:_orderedRows,cols:_orderedCols};
  };

  window.__taraTierByWindow=(opts={})=>{
    const minN=Number(opts.minN)||10;
    const log=_readCallLog();
    if(!log){console.warn('[TierByWindow] no call log');return null;}
    const entries=_filterLog(log,opts);
    const{table,rows,cols}=_buildCrossTab(entries,e=>e.windowType||'unknown',e=>_normalizeTier(e.tier));
    const _displayTable={};
    rows.forEach(r=>{
      _displayTable[r]={};
      cols.forEach(c=>{
        const cell=table[r]?.[c];
        _displayTable[r][c]=cell?_wrCell(cell.wins,cell.n,minN):{label:'·',n:0,dim:true};
      });
    });
    const _orderedRows=['15m','5m'].filter(r=>rows.includes(r)).concat(rows.filter(r=>r!=='15m'&&r!=='5m'));
    const _tierTotals={};
    cols.forEach(c=>{_tierTotals[c]=rows.reduce((sum,r)=>sum+(table[r]?.[c]?.n||0),0);});
    const _orderedCols=cols.slice().sort((a,b)=>_tierTotals[b]-_tierTotals[a]);
    _printCrossTab(`Tier × Window-type (n=${entries.length} resolved trades, minN=${minN})`,_displayTable,_orderedRows,_orderedCols,minN);
    return{table:_displayTable,rows:_orderedRows,cols:_orderedCols};
  };

  try{console.info('[V10.2.13] tier cross-tab hooks ready — __taraTierBySession() / __taraTierByHour() / __taraTierByWindow()');}catch(_){}
}

// ═══════════════════════════════════════════════════════════════════════════
// V10.2.4 — AUTO-EXEC EVENT LOG
// ═══════════════════════════════════════════════════════════════════════════
// Persistent log of every auto-exec decision: placed, filled, exited, blocked,
//   error. Surfaces what's ACTUALLY happening with auto-exec — including all
//   the times it sat out and why. Without this, you only see settled trades
//   in the call log, not the (much larger) population of attempts and skips.
//
// Stored in localStorage at 'taraAutoExecLog_v1'. Cap 500 events (auto-prunes
//   oldest on overflow). Each event has timestamp, type, asset, windowId, and
//   type-specific fields (limit, fill price, exit reason, block guard, etc.).
//
// Console hooks:
//   window.__taraAutoExecLog()                  → print today's events
//   window.__taraAutoExecLog({n:50})            → last N events
//   window.__taraAutoExecLog({type:'blocked'})  → only block events
//   window.__taraAutoExecLog({type:'placed'})   → only placement events
//   window.__taraAutoExecLog({asset:'BTC'})     → filter to one asset
//   window.__taraAutoExecLog({raw:true})        → return full array, no print
//   window.__taraAutoExecLogClear()             → wipe (asks for confirmation)
const AUTO_EXEC_LOG_CAP=500;
const AUTO_EXEC_LOG_KEY='taraAutoExecLog_v1';
const _autoExecLogPush=(event)=>{
  try{
    const _raw=localStorage.getItem(AUTO_EXEC_LOG_KEY);
    const _arr=_raw?JSON.parse(_raw):[];
    if(!Array.isArray(_arr))return;
    const _e={id:Date.now()+Math.random(),ts:Date.now(),...event};
    _arr.push(_e);
    if(_arr.length>AUTO_EXEC_LOG_CAP)_arr.splice(0,_arr.length-AUTO_EXEC_LOG_CAP);
    localStorage.setItem(AUTO_EXEC_LOG_KEY,JSON.stringify(_arr));
  }catch(_){}
};
// V10.2.6 — DEDUPE BLOCKED EVENTS to stop log spam. The auto-exec entry effect
//   re-evaluates ~1Hz (on every snapshot tick) and was pushing a 'blocked' event
//   per tick when a guard fired — flooding the 500-event FIFO buffer with
//   duplicates and EVICTING the actually-interesting events (placed/filled/exited).
//   May 14 user log: 500/500 entries were 'window-cap' blocks on a single window;
//   the two manual 'placed' events from that window had been pushed out.
//
//   Fix: per-window+guard+asset dedupe. First block of a given (guard, wid, asset)
//   logs; subsequent identical blocks are silently dropped. Module-level Set —
//   survives renders but resets on full page refresh (acceptable; refreshing is
//   itself a fresh diagnostic context). Self-prunes at 2000 entries.
const _AUTO_EXEC_BLOCK_DEDUP=new Set();
const _autoExecLogPushBlockOnce=(payload)=>{
  try{
    const _key=`${payload&&payload.guard||'?'}|${payload&&payload.windowId||'?'}|${payload&&payload.asset||'?'}|${payload&&payload.dir||'?'}`;
    if(_AUTO_EXEC_BLOCK_DEDUP.has(_key))return;
    _AUTO_EXEC_BLOCK_DEDUP.add(_key);
    if(_AUTO_EXEC_BLOCK_DEDUP.size>2000)_AUTO_EXEC_BLOCK_DEDUP.clear();
    _autoExecLogPush(payload);
  }catch(_){}
};
const _autoExecLogGet=()=>{
  try{
    const _raw=localStorage.getItem(AUTO_EXEC_LOG_KEY);
    return _raw?JSON.parse(_raw):[];
  }catch(_){return[];}
};
if(typeof window!=='undefined'){
  window.__taraAutoExecLog=(opts={})=>{
    try{
      const _log=_autoExecLogGet();
      let _filtered=_log;
      if(opts.type)_filtered=_filtered.filter(e=>e.type===opts.type);
      if(opts.asset)_filtered=_filtered.filter(e=>e.asset===opts.asset);
      if(opts.todayOnly){
        const _todayStart=new Date();_todayStart.setHours(0,0,0,0);
        _filtered=_filtered.filter(e=>e.ts>=_todayStart.getTime());
      }
      const _n=Math.max(1,Number(opts.n)||_filtered.length);
      _filtered=_filtered.slice(-_n);
      if(opts.raw)return _filtered;
      console.group(`%c━━━ Tara Auto-Exec Event Log ━━━`,'color:#23B981;font-weight:bold;font-size:13px');
      console.info(`Showing: ${_filtered.length} events (filter: ${opts.type||'all types'}, ${opts.asset||'all assets'})`);
      // Summary by type
      const _byType={};
      _filtered.forEach(e=>{if(!_byType[e.type])_byType[e.type]=[];_byType[e.type].push(e);});
      const _summary=Object.entries(_byType).map(([type,events])=>({
        type,count:events.length,
        firstAt:events.length?new Date(events[0].ts).toLocaleString():'',
        lastAt:events.length?new Date(events[events.length-1].ts).toLocaleString():'',
      }));
      console.group('Summary by type');
      try{console.table(_summary);}catch(_){}
      console.groupEnd();
      // Block reasons breakdown (most informative for "why didn't it fire")
      const _blocks=_filtered.filter(e=>e.type==='blocked');
      if(_blocks.length>0){
        const _byGuard={};
        _blocks.forEach(e=>{const g=e.guard||'unknown';_byGuard[g]=(_byGuard[g]||0)+1;});
        console.group(`Block reasons (n=${_blocks.length})`);
        try{console.table(Object.entries(_byGuard).map(([guard,count])=>({guard,count})).sort((a,b)=>b.count-a.count));}catch(_){}
        console.groupEnd();
      }
      // Full timeline (most recent first)
      console.group(`Timeline (${_filtered.length} events, most recent first)`);
      try{console.table(_filtered.slice().reverse().map(e=>({
        time:new Date(e.ts).toLocaleTimeString(),
        type:e.type,
        asset:e.asset||'',
        dir:e.dir||'',
        wid:(e.windowId||'').split('-').pop()?.slice(-12)||'',
        details:e.exitReason||e.blockReason||e.errorMsg||(e.limitCents?`${e.limitCents}¢`:'')||(e.fillPriceCents?`fill ${e.fillPriceCents}¢`:'')||'',
      })));}catch(_){}
      console.groupEnd();
      console.groupEnd();
      return{count:_filtered.length,byType:_byType};
    }catch(e){console.error('[AutoExecLog] failed:',e&&e.message);return null;}
  };
  window.__taraAutoExecLogClear=()=>{
    try{
      if(!confirm('Wipe all auto-exec event log entries? This cannot be undone.'))return;
      localStorage.removeItem(AUTO_EXEC_LOG_KEY);
      console.info('[AutoExecLog] cleared');
    }catch(_){}
  };
  try{console.info('[AutoExecLog] hook ready — run window.__taraAutoExecLog() in console');}catch(_){}
}

// Deterministic ID for the current trading window (UTC ISO of window-open boundary).
// Refresh during the same window → same ID → restore the same lock.
const computeWindowId=(windowType)=>{
  const winMs=windowType==='15m'?900000:300000;
  const bucketMs=Math.floor(Date.now()/winMs)*winMs;
  return `${windowType}-${new Date(bucketMs).toISOString()}`;
};

// ═══════════════════════════════════════════════════════════════════════════
// V9.19.11: getTaraDirection — central helper for resolving "what direction
//   does Tara think the price is going?" Handles the V9.17.22/V9.18.7 snapshot
//   vs. lock signal-source semantics in ONE place. Returns null when Tara has
//   no opinion.
//
// Background: Tara holds direction state in several refs that can disagree:
//   • taraCallSnapshotRef.current.call — the public 67% WR snapshot (preferred)
//   • lockedCallRef.current.dir — internal engine lock (fires earlier)
//   • engineLockedDirRef.current — derived signal (alias of lock)
//   • taraCall (state) — display-layer copy of snapshot
//   • analysis.lockInfo.dir — built from lockedCallRef
//   • autoOrderState.dir — what was actually placed
//   • scalperRead.dir — independent scalper engine (NOT Tara — different model)
//
// V9.18.7 patched ONE display site to respect autoExecSettings.signalSource.
// This helper centralizes that decision so future call sites can be migrated
// over time without re-deriving the precedence logic at each one.
//
// Inputs:
//   • snapshot — taraCallSnapshotRef.current (the engine's "settled call")
//   • lock — lockedCallRef.current (the engine's "live lock")
//   • signalSource — 'snapshot' (default, V9.17.22) | 'lock'
//
// Returns:
//   • dir — 'UP' | 'DOWN' | null
//   • source — which ref the direction came from (for diagnostics)
//
// Future migrations (separate sessions):
//   • analysis.lockInfo construction → use this helper
//   • UI ticket rendering → use this helper
//   • taraCall display → use this helper
//   • autoOrderState.dir comparison → use this helper
//   At each migration, verify behavior unchanged via build + spot-check.
// ═══════════════════════════════════════════════════════════════════════════
const getTaraDirection=({snapshot,lock,signalSource}={})=>{
  const _snapDir=snapshot?.call==='UP'||snapshot?.call==='DOWN'?snapshot.call:null;
  const _lockDir=lock?.dir==='UP'||lock?.dir==='DOWN'?lock.dir:null;
  const _src=signalSource==='lock'?'lock':'snapshot';
  // Snapshot mode (default): prefer snapshot, fall back to lock if no snapshot.
  // Lock mode: prefer lock, fall back to snapshot if no lock.
  let dir=null;
  let source=null;
  if(_src==='lock'){
    if(_lockDir){dir=_lockDir;source='lock';}
    else if(_snapDir){dir=_snapDir;source='snapshot(fallback)';}
  } else {
    if(_snapDir){dir=_snapDir;source='snapshot';}
    else if(_lockDir){dir=_lockDir;source='lock(fallback)';}
  }
  return{dir,source};
};

// ─────────────────────────────────────────────────────────────────────────────
// V13.4.280 — readLockState: THE ONLY PLACE THAT DECIDES "IS IT LOCKED".
//
// Three panels independently reinvented this rule and all three got it wrong the
// same way -- they treated "a direction exists" as "Tara has committed":
//
//   TARA ADVISOR    (V13.4.275) inferred a lock from direction alone, and was
//                   handed a snapshot COPY that omitted `locked` entirely, so it
//                   could not have checked. Printed "Locked DOWN" with an ENTER
//                   button on a round that was never committed.
//   DECISION CLOCK  (V13.4.279) `!!(taraCall.call==='UP'||'DOWN')` -- true on
//                   nearly every tick. Announced LOCKED and "do not trade against
//                   it" while its own phase strip read "33s to commit".
//   TradeCoachCall  (V13.4.265) conflated "tradeable lock" with "decided", so a
//                   committed SIT-OUT fell through to the live object and showed a
//                   confidence that kept drifting after the decision was frozen.
//
// The distinction that matters, and the reason a single boolean was never enough:
//
//   committed  a decision was made. A SIT-OUT is committed.
//   tradeable  committed AND there is a side to buy. A sit-out is NOT tradeable.
//
// Anything that says LOCKED, offers an ENTER button, or tells the user not to
// trade against a call MUST gate on `tradeable`. Anything that freezes numbers so
// they stop drifting MUST gate on `committed`. Never on the presence of a
// direction -- a lean has a direction too.
//
// Pass the committed snapshot (taraCallSnapshotRef.current, or the partial copy
// handed to the advisor). NEVER pass the live taraCall: it always has a direction
// and never has a lock, which is precisely how all three bugs happened.
const readLockState=(snapshot)=>{
  const s=(snapshot&&typeof snapshot==='object')?snapshot:null;
  // `locked` is the snapshot's own claim. Sit-out snapshots set it too, by design.
  const committed=!!(s&&s.locked);
  const call=committed?s.call:null;
  const tradeable=committed&&(call==='UP'||call==='DOWN');
  const satOut=committed&&call==='SIT_OUT';
  const dir=tradeable?call:null;
  // For a sit-out, the side it would have taken -- context only, never tradeable.
  const _lean=satOut?(s._intendedDir||s.direction||null):null;
  const intendedDir=(_lean==='UP'||_lean==='DOWN')?_lean:null;
  // Confidence in the side actually called. `confidence` does NOT mean one thing
  //   across the engine -- the directional-lock site stores the raw posterior
  //   P(UP), committed snapshots store it already flipped -- so the same DOWN call
  //   could render 30% on one path and 65% on another (V13.4.273). `conviction` is
  //   unambiguous, |posterior-50| at every site, so 50+conviction is correct under
  //   either convention. The flip is the fallback for snapshots without it.
  const _convict=Number(s&&s.conviction);
  const _rawConf=Number(s&&s.confidence);
  const _side=dir||intendedDir;
  const confidence=(Number.isFinite(_convict)&&_convict>0)
    ?Math.round(50+_convict)
    :((Number.isFinite(_rawConf)&&_rawConf>0)
        ?Math.round((_side==='DOWN'&&_rawConf<50)?(100-_rawConf):_rawConf)
        :0);
  return{
    committed,tradeable,satOut,dir,intendedDir,confidence,
    call:call||null,
    reason:(s&&s.reason)||'',
    qScore:Number.isFinite(s&&s.qScore)?s.qScore:null,
    // 'LOCKED' | 'SITTING OUT' | null -- null means nothing is committed yet, and
    //   the caller should describe the LIVE state (leaning / scanning) itself.
    label:tradeable?'LOCKED':satOut?'SITTING OUT':null,
  };
};

const describeCommittedCallReason=(snapshot)=>{
  if(!snapshot||typeof snapshot!=='object')return '';
  const raw=String(snapshot.reason||'').replace(/^\[V?[\d.]+[^\]]*\]\s*/,'').trim();
  if((snapshot.call==='UP'||snapshot.call==='DOWN')&&(snapshot._noSitoutFrom||snapshot.tier==='no-sitout-commit')){
    const prior=String(snapshot._noSitoutWas||raw).replace(/^\[V?[\d.]+[^\]]*\]\s*/,'').trim();
    return `Sit-outs are off: Tara committed ${snapshot.call} on the live lean.${prior?` Original sit-out gate: ${prior}`:''}`;
  }
  return raw;
};

// ─────────────────────────────────────────────────────────────────────────────
// V13.4.286 — readOrderState: THE ONLY PLACE THAT READS A LIVE KALSHI ORDER.
//
// The writer and the readers use different names for the same fields, and have
// done since the maker/rung ladder rewrite. setAutoOrderState writes:
//
//     filledCount   filledAtCents   exitAtCents   rungCents   pnlCentsPerContract
//
// while every reader asks for:
//
//     count         fillPrice       exitFillPrice limitCents
//
// None of those reader names is ever assigned, so they are all undefined. The
// damage is not cosmetic: THIS TRADE computes `_placed = _haveFill && _count>0`,
// so stages 2 and 3 read "NOT IN" / "NOTHING YET" even while an order is filled
// and live. The trade card cannot show a position at all.
//
// Same fix shape as readLockState (V13.4.280): one normalizer, so a rename on the
// writer side can never again silently blank a panel that reads it. Accepts BOTH
// spellings — the new names win, the old ones stay as fallbacks so nothing breaks
// if a path somewhere does set them.
const readOrderState=(s)=>{
  const o=(s&&typeof s==='object')?s:null;
  const num=(...vals)=>{
    for(const v of vals){const n=Number(v);if(Number.isFinite(n))return n;}
    return null;
  };
  const status=String((o&&o.status)||'');
  const count=num(o&&o.filledCount,o&&o.count)||0;
  const fillCents=num(o&&o.filledAtCents,o&&o.fillPrice);
  const limitCents=num(o&&o.rungCents,o&&o.limitCents);
  const exitCents=num(o&&o.exitAtCents,o&&o.exitFillPrice);
  return{
    raw:o,
    status,
    dir:(o&&o.dir)||null,
    dryRun:!!(o&&o.dryRun),
    count,
    fillCents,
    limitCents,
    exitCents,
    pnlPerContract:num(o&&o.pnlCentsPerContract),
    // A real position exists only with BOTH a fill price and a contract count.
    placed:!!(o&&Number.isFinite(fillCents)&&fillCents>0&&count>0),
    exited:status==='exited',
    errored:status==='error',
  };
};

// V13.4.353 — readExchangePositionTruth: keep the user's Tara-follow marker,
// AutoTrade's local lifecycle, and the exchange book as three different facts.
// A local marker is useful for Tara's coaching, but it is never proof that a
// Kalshi position exists. The control-room summaries must only call a position
// OPEN when the latest successful exchange snapshot contains the expected
// ticker/side/count. This helper is intentionally display-only: it does not
// submit, cancel, retry, or mutate any live-money state.
const readExchangePositionTruth=({positionReconciliation,autoOrderState,userPosition,manualKalshiEntry,activeTicker})=>{
  const _rec=positionReconciliation||{};
  const _details=Array.isArray(_rec.driftDetails)?_rec.driftDetails:[];
  const _positions=Array.isArray(_rec.kalshiPositions)?_rec.kalshiPositions:null;
  const _order=readOrderState(autoOrderState);
  const _ticker=autoOrderState?.ticker||activeTicker||null;
  const _side=autoOrderState?.dir==='UP'||manualKalshiEntry?.side==='UP'||userPosition==='UP'?'yes'
    :autoOrderState?.dir==='DOWN'||manualKalshiEntry?.side==='DOWN'||userPosition==='DOWN'?'no':null;
  const _lastSuccessAt=Number(_rec.lastSuccessAt)||0;
  const _hasSuccessfulPoll=!!(_lastSuccessAt>0&&_positions);
  const _fresh=_hasSuccessfulPoll&&Date.now()-_lastSuccessAt<=60000;
  const _match=_hasSuccessfulPoll&&_ticker
    ?_positions.find(p=>p&&p.ticker===_ticker&&Number(p.count)>0)||null
    :null;
  const _noFillConfirmed=_details.some(d=>d&&d.kind==='unknown-resolved-nofill')
    ||(autoOrderState?.status==='no-fill'&&autoOrderState?.noFillConfirmed===true);
  const _exitFlatConfirmed=_details.some(d=>d&&d.kind==='exit-confirmed-flat');
  const _localExpectation=!!(userPosition||manualKalshiEntry||autoOrderState);
  const _hasUnmanagedConfirmedFill=_details.some(d=>d&&(
    d.kind==='unknown-resolved-filled'||d.kind==='unknown-to-tara'));

  if(_rec.status==='error'){
    return{state:'unknown',label:'UNKNOWN',tone:'warn',match:_match,count:0,
      summary:'Exchange position check failed. Do not retry until the check is healthy.',
      detail:_rec.lastErrorMsg||'Kalshi did not return a trustworthy position snapshot.'};
  }
  // A confirmed no-fill is a flat outcome, even though reconciliation keeps
  // the green diagnostic detail visible for auditability.
  if(_noFillConfirmed&&!_hasUnmanagedConfirmedFill&&!_match){
    return{state:_fresh?'flat':'stale-flat',label:_fresh?'FLAT · NO FILL':'NO FILL · CHECK STALE',tone:_fresh?'quiet':'warn',match:null,count:0,
      summary:_fresh?'Kalshi confirmed no position for this AutoTrade attempt.':'Kalshi previously confirmed no fill for this attempt; the position check is now stale.',
      detail:_fresh?'No duplicate retry is needed for this attempt.':'Recheck the account before assuming it remains flat.'};
  }
  if(_exitFlatConfirmed&&!_hasUnmanagedConfirmedFill&&!_match){
    return{state:_fresh?'flat':'stale-flat',label:_fresh?'FLAT · EXIT CONFIRMED':'EXIT CHECK STALE',tone:_fresh?'quiet':'warn',match:null,count:0,
      summary:_fresh?'Kalshi showed no position on two consecutive checks after the exit attempt.':'Kalshi previously showed no position after exit; the position check is now stale.',
      detail:_fresh?'Review the original exit order in exchange history; no duplicate exit was submitted.':'Recheck Kalshi before assuming the account remains flat.'};
  }
  const _pendingOnly=_details.length>0&&_details.every(d=>d&&(
    d.kind==='phantom-auto-pending'||d.kind==='unknown-nofill-pending'||d.kind==='exit-verification-pending'));
  if(_rec.status==='drift'&&_pendingOnly){
    return{state:'checking',label:'CHECKING',tone:'warn',match:null,count:0,
      summary:'Kalshi is being rechecked before Tara declares a fill or no-fill.',
      detail:'This is a verification state, not a confirmed flat position or a retry signal.'};
  }
  if(_rec.status==='drift'&&_details.length>0){
    const _count=_match?.count||_details.reduce((n,d)=>n+(Number(d?.kalshiCount)||0),0);
    return{state:'reconcile',label:'RECONCILE',tone:'warn',match:_match,count:_count,
      summary:'Tara and Kalshi disagree. Resolve the exchange truth before taking another action.',
      detail:_details[0]?.note||'Position state is not safe to infer locally.'};
  }
  if(!_hasSuccessfulPoll){
    return{state:_localExpectation?'checking':'unverified',label:_localExpectation?'CHECKING':'UNVERIFIED',tone:'warn',match:null,count:0,
      summary:_localExpectation?'Waiting for the first successful exchange position check.':'No successful exchange position check has run yet.',
      detail:'A Tara marker or order status is not exchange confirmation; no duplicate retry is authorized by this display.'};
  }
  if(!_ticker){
    return{state:'unverified',label:'TICKER UNKNOWN',tone:'warn',match:null,count:0,
      summary:'Kalshi responded, but Tara has no active market ticker to match against the account positions.',
      detail:'Inspect Kalshi directly; no flat or open state can be inferred for this window.'};
  }
  if(_match&&_side&&_match.side!==_side){
    return{state:'reconcile',label:'SIDE MISMATCH',tone:'warn',match:_match,count:Number(_match.count)||0,
      summary:`Kalshi shows ${_match.count} ${_match.side==='yes'?'UP':'DOWN'} contracts, opposite Tara's expected side.`,
      detail:'Treat this as an open exchange position requiring manual verification, never as flat.'};
  }
  if(_match){
    return{state:_fresh?'open':'stale-open',label:_fresh?'OPEN':'LAST SEEN OPEN',tone:_fresh?'live':'warn',match:_match,count:Number(_match.count)||0,
      summary:`${_fresh?'Kalshi confirms':'Last successful Kalshi snapshot showed'} ${Number(_match.count)||0} ${_match.side==='yes'?'UP':'DOWN'} contract${Number(_match.count)===1?'':'s'} on this window.`,
      detail:_fresh?'Exchange position is the money truth; local Tara state is secondary.':'The snapshot is stale. Recheck Kalshi before any order or exit.'};
  }
  if(_localExpectation&&(_order.status==='placing'||_order.status==='resting'||_order.status==='submitted'||_order.status==='partially_filled'||_order.status==='unknown'||_order.status==='filled'||_order.status==='exiting'||_order.status==='exit-pending')){
    return{state:'checking',label:'CHECKING',tone:'warn',match:null,count:0,
      summary:'Local AutoTrade state exists, but Kalshi has not confirmed the position in the latest snapshot.',
      detail:'Keep the order under observation; do not place a duplicate order.'};
  }
  return{state:_fresh?'flat':'stale-flat',label:_fresh?'FLAT':'LAST SEEN FLAT',tone:_fresh?'quiet':'warn',match:null,count:0,
    summary:_fresh?'Kalshi shows no open position for this window.':'Last successful Kalshi snapshot showed no position for this window.',
    detail:_fresh?'This is a confirmed flat state from the latest successful exchange check.':'The snapshot is stale. Recheck Kalshi before assuming the account is flat.'};
};

// ═══════════════════════════════════════
// ICONS
// ═══════════════════════════════════════
const _SVG_NS='http:'+'/'+'/www.w3.org/2000/svg';
const IC={
  Clock:({className})=>React.createElement('svg',{xmlns:_SVG_NS,viewBox:'0 0 24 24',fill:'none',stroke:'currentColor',strokeWidth:'2',strokeLinecap:'round',strokeLinejoin:'round',className},React.createElement('circle',{cx:'12',cy:'12',r:'10'}),React.createElement('polyline',{points:'12 6 12 12 16 14'})),
  Crosshair:({className})=>React.createElement('svg',{xmlns:_SVG_NS,viewBox:'0 0 24 24',fill:'none',stroke:'currentColor',strokeWidth:'2',strokeLinecap:'round',strokeLinejoin:'round',className},React.createElement('circle',{cx:'12',cy:'12',r:'10'}),React.createElement('line',{x1:'22',y1:'12',x2:'18',y2:'12'}),React.createElement('line',{x1:'6',y1:'12',x2:'2',y2:'12'}),React.createElement('line',{x1:'12',y1:'6',x2:'12',y2:'2'}),React.createElement('line',{x1:'12',y1:'22',x2:'12',y2:'18'})),
  Zap:({className})=>React.createElement('svg',{xmlns:_SVG_NS,viewBox:'0 0 24 24',fill:'none',stroke:'currentColor',strokeWidth:'2',strokeLinecap:'round',strokeLinejoin:'round',className},React.createElement('polygon',{points:'13 2 3 14 12 14 11 22 21 10 12 10 13 2'})),
  Terminal:({className})=>React.createElement('svg',{xmlns:_SVG_NS,viewBox:'0 0 24 24',fill:'none',stroke:'currentColor',strokeWidth:'2',strokeLinecap:'round',strokeLinejoin:'round',className},React.createElement('polyline',{points:'4 17 10 11 4 5'}),React.createElement('line',{x1:'12',y1:'19',x2:'20',y2:'19'})),
  Alert:({className})=>React.createElement('svg',{xmlns:_SVG_NS,viewBox:'0 0 24 24',fill:'none',stroke:'currentColor',strokeWidth:'2',strokeLinecap:'round',strokeLinejoin:'round',className},React.createElement('path',{d:'M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z'}),React.createElement('line',{x1:'12',y1:'9',x2:'12',y2:'13'}),React.createElement('line',{x1:'12',y1:'17',x2:'12.01',y2:'17'})),
  Activity:({className})=>React.createElement('svg',{xmlns:_SVG_NS,viewBox:'0 0 24 24',fill:'none',stroke:'currentColor',strokeWidth:'2',strokeLinecap:'round',strokeLinejoin:'round',className},React.createElement('polyline',{points:'22 12 18 12 15 21 9 3 6 12 2 12'})),
  Bell:({className})=>React.createElement('svg',{xmlns:_SVG_NS,viewBox:'0 0 24 24',fill:'none',stroke:'currentColor',strokeWidth:'2',strokeLinecap:'round',strokeLinejoin:'round',className},React.createElement('path',{d:'M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9'}),React.createElement('path',{d:'M13.73 21a2 2 0 0 1-3.46 0'})),
  TrendUp:({className})=>React.createElement('svg',{xmlns:_SVG_NS,viewBox:'0 0 24 24',fill:'none',stroke:'currentColor',strokeWidth:'2',strokeLinecap:'round',strokeLinejoin:'round',className},React.createElement('polyline',{points:'23 6 13.5 15.5 8.5 10.5 1 18'}),React.createElement('polyline',{points:'17 6 23 6 23 12'})),
  Globe:({className})=>React.createElement('svg',{xmlns:_SVG_NS,viewBox:'0 0 24 24',fill:'none',stroke:'currentColor',strokeWidth:'2',strokeLinecap:'round',strokeLinejoin:'round',className},React.createElement('circle',{cx:'12',cy:'12',r:'10'}),React.createElement('line',{x1:'2',y1:'12',x2:'22',y2:'12'}),React.createElement('path',{d:'M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z'})),
  Msg:({className})=>React.createElement('svg',{xmlns:_SVG_NS,viewBox:'0 0 24 24',fill:'none',stroke:'currentColor',strokeWidth:'2',strokeLinecap:'round',strokeLinejoin:'round',className},React.createElement('path',{d:'M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z'})),
  X:({className})=>React.createElement('svg',{xmlns:_SVG_NS,viewBox:'0 0 24 24',fill:'none',stroke:'currentColor',strokeWidth:'2',strokeLinecap:'round',strokeLinejoin:'round',className},React.createElement('line',{x1:'18',y1:'6',x2:'6',y2:'18'}),React.createElement('line',{x1:'6',y1:'6',x2:'18',y2:'18'})),
  Info:({className})=>React.createElement('svg',{xmlns:_SVG_NS,viewBox:'0 0 24 24',fill:'none',stroke:'currentColor',strokeWidth:'2',strokeLinecap:'round',strokeLinejoin:'round',className},React.createElement('circle',{cx:'12',cy:'12',r:'10'}),React.createElement('line',{x1:'12',y1:'16',x2:'12',y2:'12'}),React.createElement('line',{x1:'12',y1:'8',x2:'12.01',y2:'8'})),
  Vol2:({className})=>React.createElement('svg',{xmlns:_SVG_NS,viewBox:'0 0 24 24',fill:'none',stroke:'currentColor',strokeWidth:'2',strokeLinecap:'round',strokeLinejoin:'round',className},React.createElement('polygon',{points:'11 5 6 9 2 9 2 15 6 15 11 19 11 5'}),React.createElement('path',{d:'M19.07 4.93a10 10 0 0 1 0 14.14M15.54 8.46a5 5 0 0 1 0 7.07'})),
  VolX:({className})=>React.createElement('svg',{xmlns:_SVG_NS,viewBox:'0 0 24 24',fill:'none',stroke:'currentColor',strokeWidth:'2',strokeLinecap:'round',strokeLinejoin:'round',className},React.createElement('polygon',{points:'11 5 6 9 2 9 2 15 6 15 11 19 11 5'}),React.createElement('line',{x1:'23',y1:'9',x2:'17',y2:'15'}),React.createElement('line',{x1:'17',y1:'9',x2:'23',y2:'15'})),
  Help:({className})=>React.createElement('svg',{xmlns:_SVG_NS,viewBox:'0 0 24 24',fill:'none',stroke:'currentColor',strokeWidth:'2',strokeLinecap:'round',strokeLinejoin:'round',className},React.createElement('circle',{cx:'12',cy:'12',r:'10'}),React.createElement('path',{d:'M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3'}),React.createElement('line',{x1:'12',y1:'17',x2:'12.01',y2:'17'})),
  Link:({className})=>React.createElement('svg',{xmlns:_SVG_NS,viewBox:'0 0 24 24',fill:'none',stroke:'currentColor',strokeWidth:'2',strokeLinecap:'round',strokeLinejoin:'round',className},React.createElement('path',{d:'M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71'}),React.createElement('path',{d:'M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71'})),
  ChevronDown:({className})=>React.createElement('svg',{xmlns:_SVG_NS,viewBox:'0 0 24 24',fill:'none',stroke:'currentColor',strokeWidth:'2',strokeLinecap:'round',strokeLinejoin:'round',className},React.createElement('polyline',{points:'6 9 12 15 18 9'})),
  BarChart:({className})=>React.createElement('svg',{xmlns:_SVG_NS,viewBox:'0 0 24 24',fill:'none',stroke:'currentColor',strokeWidth:'2',strokeLinecap:'round',strokeLinejoin:'round',className},React.createElement('line',{x1:'18',y1:'20',x2:'18',y2:'10'}),React.createElement('line',{x1:'12',y1:'20',x2:'12',y2:'4'}),React.createElement('line',{x1:'6',y1:'20',x2:'6',y2:'14'})),
};

// ═══════════════════════════════════════
// MATH & INDICATORS (V99 ENHANCED)
// ═══════════════════════════════════════
const formatUSD=(val)=>{const abs=Math.abs(val);if(abs>=1e6)return(val/1e6).toFixed(2)+'M';if(abs>=1e3)return(val/1e3).toFixed(1)+'K';return val.toFixed(0);};
// V5.7.3: Single source of truth for duration formatting. Returns "Xs" under 60s,
//   "Mm SSs" otherwise. Handles null/NaN gracefully. Used everywhere a countdown is shown.
// ═══════════════════════════════════════
// V9.2.2: TARA ML LAYER — in-browser logistic regression
// ═══════════════════════════════════════
// Trains on signalScoresAtLock features from resolved trades. Outputs a
// predicted WIN probability that feeds into the posterior as an additional
// signal (+/-10 pts max). Activates when ≥50 resolved trades with signal
// data exist. Retrains on every new resolved trade.
//
// Features: gap, momentum, flow, structure, technical, regime, rangePosition,
//   reversal, meanReversion, crossAsset + regime_encoded + channelPos + atrBps.
// Target: 1 = WIN, 0 = LOSS.
// Model: L2-regularized logistic regression (no external libraries).
const TARA_ML_FEATURES=['gap','momentum','flow','structure','technical','regime','rangePosition','reversal','meanReversion','crossAsset'];
const TARA_ML_MIN_TRADES=50;
const TARA_ML_LEARNING_RATE=0.01;
const TARA_ML_EPOCHS=100;
const TARA_ML_L2=0.001;

const _sigmoid=(z)=>1/(1+Math.exp(-Math.max(-20,Math.min(20,z))));

const taraMLExtractFeatures=(entry)=>{
  const sigs=entry.signalScoresAtLock||{};
  const features=TARA_ML_FEATURES.map(k=>Number(sigs[k])||0);
  // Extra features: channelPos (0-1), atrBps (normalized), posterior at lock
  features.push(Number(entry.calibratedPosteriorAtLock)||50);
  features.push(Number(entry.atrBpsAtLock)||15);
  // Regime one-hot: 5 regimes
  const _regimes=['TRENDING UP','TRENDING DOWN','RANGE-CHOP','SHORT SQUEEZE','HIGH VOL CHOP'];
  _regimes.forEach(r=>features.push(entry.regime===r?1:0));
  return features;
};

const taraMLTrain=(entries)=>{
  // Filter to resolved trades with signal data
  const data=entries.filter(e=>e&&(e.result==='WIN'||e.result==='LOSS')&&e.signalScoresAtLock);
  if(data.length<TARA_ML_MIN_TRADES)return null;
  const nFeatures=taraMLExtractFeatures(data[0]).length;
  // Initialize weights to small random values
  let weights=new Array(nFeatures).fill(0).map(()=>(Math.random()-0.5)*0.1);
  let bias=0;
  // Normalize features: compute mean + std per feature
  const sums=new Array(nFeatures).fill(0);
  const sqSums=new Array(nFeatures).fill(0);
  const allX=data.map(e=>taraMLExtractFeatures(e));
  allX.forEach(x=>x.forEach((v,i)=>{sums[i]+=v;sqSums[i]+=v*v;}));
  const means=sums.map(s=>s/data.length);
  const stds=sqSums.map((sq,i)=>{const v=sq/data.length-means[i]*means[i];return Math.sqrt(Math.max(1e-8,v));});
  const normX=allX.map(x=>x.map((v,i)=>(v-means[i])/stds[i]));
  const y=data.map(e=>e.result==='WIN'?1:0);
  // SGD with L2 regularization
  for(let epoch=0;epoch<TARA_ML_EPOCHS;epoch++){
    // Shuffle indices
    const idx=[...Array(data.length).keys()];
    for(let i=idx.length-1;i>0;i--){const j=Math.floor(Math.random()*(i+1));[idx[i],idx[j]]=[idx[j],idx[i]];}
    let totalLoss=0;
    for(const i of idx){
      const x=normX[i];
      const z=x.reduce((s,v,j)=>s+v*weights[j],bias);
      const pred=_sigmoid(z);
      const err=pred-y[i];
      totalLoss+=-y[i]*Math.log(pred+1e-8)-(1-y[i])*Math.log(1-pred+1e-8);
      // Update weights with L2
      for(let j=0;j<nFeatures;j++){
        weights[j]-=TARA_ML_LEARNING_RATE*(err*x[j]+TARA_ML_L2*weights[j]);
      }
      bias-=TARA_ML_LEARNING_RATE*err;
    }
  }
  // Compute training accuracy
  let correct=0;
  normX.forEach((x,i)=>{
    const z=x.reduce((s,v,j)=>s+v*weights[j],bias);
    const pred=_sigmoid(z)>=0.5?1:0;
    if(pred===y[i])correct++;
  });
  const accuracy=correct/data.length;
  // Feature importance: absolute weight values
  const importance=TARA_ML_FEATURES.map((name,i)=>({name,weight:weights[i],absWeight:Math.abs(weights[i])}));
  importance.sort((a,b)=>b.absWeight-a.absWeight);
  return{weights,bias,means,stds,nFeatures,nTrades:data.length,accuracy,importance,trainedAt:Date.now()};
};

const taraMLPredict=(model,entry)=>{
  if(!model||!model.weights)return null;
  const raw=taraMLExtractFeatures(entry);
  if(raw.length!==model.nFeatures)return null;
  const norm=raw.map((v,i)=>(v-(model.means[i]||0))/(model.stds[i]||1));
  const z=norm.reduce((s,v,j)=>s+v*(model.weights[j]||0),model.bias||0);
  return _sigmoid(z); // 0-1 probability of WIN
};


// V9.2.3: STALE ENTRY CLEANUP — scans call log for missing/broken fields, backfills
//   what's derivable, removes genuinely broken entries, deduplicates.
const taraCleanupLog=(log)=>{
  if(!Array.isArray(log)||log.length===0)return{cleaned:log,report:{removed:0,backfilled:0,deduped:0,details:[]}};
  const report={removed:0,backfilled:0,deduped:0,details:[]};
  let entries=[...log];
  // Pass 1: Remove genuinely broken entries (no windowId, no time/id)
  const beforeLen=entries.length;
  entries=entries.filter(e=>{
    if(!e)return false;
    if(!e.windowId&&!e.id&&!e.time){report.details.push('Removed: no windowId/id/time');return false;}
    return true;
  });
  report.removed=beforeLen-entries.length;
  // Pass 2: Backfill derivable fields
  entries=entries.forEach?entries.map(e=>{
    let changed=false;
    // Asset from strike
    if(!e.asset&&e.strike){
      e.asset='BTC';
      changed=true;
    }
    if(!e.asset)e.asset='BTC';
    // closingGapBps from strike + closingPrice
    if(e.closingGapBps==null&&e.strike>0&&e.closingPrice>0){
      e.closingGapBps=Math.round(((e.closingPrice-e.strike)/e.strike)*10000*100)/100;
      changed=true;
    }
    // outcomeDir from closingPrice vs strike
    if(!e.outcomeDir&&e.strike>0&&e.closingPrice>0&&e.closingPrice!==e.strike){
      e.outcomeDir=e.closingPrice>e.strike?'UP':'DOWN';
      changed=true;
    }
    // phase from timestamp
    if(!e.phase&&(e.id||e.time)){
      try{
        const dt=new Date(e.id||e.time);
        const h=dt.getUTCHours(),m=dt.getUTCMinutes();
        if(typeof getPhaseKey==='function')e.phase=getPhaseKey(h,m);
        changed=true;
      }catch(_){}
    }
    // session from phase
    if(!e.session&&e.phase){
      const pk=e.phase;
      if(pk==='ASIA_OPEN'||pk==='ASIA_LATE')e.session='ASIA';
      else if(pk==='EU_PREOPEN'||pk==='EU_OPEN')e.session='EU';
      else if(pk.startsWith('NY_'))e.session='US';
      else e.session='OFF-HOURS';
      changed=true;
    }
    // windowType default
    if(!e.windowType)e.windowType='15m';
    if(changed)report.backfilled++;
    return e;
  }):entries;
  // Pass 3: Deduplicate by asset|windowId|windowType
  const seen=new Map();
  const beforeDedup=entries.length;
  entries.forEach(e=>{
    const k=(e.asset||'BTC')+'|'+(e.windowId||'')+'|'+(e.windowType||'');
    const existing=seen.get(k);
    if(!existing){seen.set(k,e);return;}
    // Keep resolved over unresolved, older id over newer
    if((existing.result==='SITOUT'||existing.result==='NO_TRADE')&&(e.dir==='UP'||e.dir==='DOWN')&&e.result!=='SITOUT'&&e.result!=='NO_TRADE'){seen.set(k,e);return;}
    if((e.result==='SITOUT'||e.result==='NO_TRADE')&&(existing.dir==='UP'||existing.dir==='DOWN')&&existing.result!=='SITOUT'&&existing.result!=='NO_TRADE')return;
    if(!existing.result&&e.result){seen.set(k,e);return;}
    if(existing.result&&!e.result)return;
    // V13.3.5: a real trade (WIN/LOSS) outranks a SITOUT/NO_TRADE record of the same window.
    if((e.result==='WIN'||e.result==='LOSS')&&(existing.result==='SITOUT'||existing.result==='NO_TRADE')){seen.set(k,e);return;}
    if((existing.result==='WIN'||existing.result==='LOSS')&&(e.result==='SITOUT'||e.result==='NO_TRADE'))return;
    if((e.id||0)<(existing.id||0))seen.set(k,e);
  });
  entries=Array.from(seen.values()).sort((a,b)=>(a.id||0)-(b.id||0));
  report.deduped=beforeDedup-entries.length;
  return{cleaned:entries,report};
};

// V9.2.3: STREAK DETECTION — computes current win/loss streak from recent trades.
const computeStreak=(log,asset)=>{
  const recent=[...(log||[])].filter(e=>e&&(e.result==='WIN'||e.result==='LOSS')&&(e.asset||'BTC')===asset).sort((a,b)=>(b.id||0)-(a.id||0));
  if(recent.length===0)return{type:null,count:0};
  const firstResult=recent[0].result;
  let count=0;
  for(const e of recent){
    if(e.result===firstResult)count++;
    else break;
  }
  return{type:firstResult,count};
};

// V14.3: one official BLS / BEA / Fed schedule supplies the calendar UI,
// existing macro guard and conviction dampener. Missing coverage is UNKNOWN.
// Free schedules do not cover surprise news or every economic release.

// V9.14: SPOT vs PERP DIVERGENCE HOOK
//   Polls Coinbase BTC-USD spot every 10 seconds, compares against the perp
//   mark price (from bloomberg). When perp trades meaningfully above spot
//   (positive premium), leveraged longs are crowded → vulnerable to liq cascade.
//   When perp trades meaningfully below spot (discount), shorts are crowded →
//   squeeze risk.
//
//   Returns: {divBps, perpAbove, spot, perp, lastUpdate, status}
//     divBps: positive = perp premium (longs crowded), negative = perp discount (shorts crowded)
//     perpAbove: bool — perp > spot
// V10.7.65 — WHALE ALERT ON-CHAIN SIGNAL
//   Tracks large BTC on-chain transactions (>$500K) via /api/whale proxy.
//   Requires WHALE_ALERT_API_KEY set in Vercel environment variables.
//   Inflow to exchange = sell pressure (bearish). Outflow from exchange = buy pressure (bullish).
//   netScore: -10 (strong bearish) to +10 (strong bullish). Null when proxy unavailable.
// V10.7.73 — DERIBIT OPTIONS SENTIMENT HOOK
//   Polls /api/deribit every 30s for IV skew + put/call ratio.
//   Positive skew = puts expensive = bears buying protection = bearish.
//   Negative skew = calls expensive = bulls loading = bullish.
// V10.7.76 — LIQUIDATION HEATMAP + OI SIGNAL
//   Polls /api/coinglass every 20s for:
//   1. Liquidation gravity: large order walls near current price create magnetic pull
//      toward them. Price hunts the clusters mechanically.
//   2. OI change: rising OI = real accumulation (move holds). Falling OI = liq noise (fades).
//   Combined: up to ±10pt. Strongest new signal since gap.
const useLiqHeatmap=()=>{
  const[liqHeatmap,setLiqHeatmap]=React.useState({ok:false,combinedSignal:0,combinedDirection:'neutral',liquidationGravity:0,oiSignal:0,nearestWallSide:null,nearestWallDistBps:null,nearestWallSize:null});
  React.useEffect(()=>{
    let _stopped=false;
    const _poll=async()=>{
      try{
        const r=await fetch('/api/coinglass',{cache:'no-store',signal:AbortSignal.timeout(4000)});
        if(!r.ok)return;
        const d=await r.json();
        if(_stopped)return;
        if(d?.ok)setLiqHeatmap(d);
      }catch(_){}
    };
    // V13.2.1: left OFF on the evidence. liqHeatmap was empirically ANTI-PREDICTIVE over
    //   1,819 trades (win +4.6 vs loss +4.7, delta -0.1) and already throttled to 20%.
    //   Dropping it removes noise and ~4,320 Vercel edge req/device/day. Re-enable here
    //   (e.g. setInterval 180000) if you ever want the OI-wall display back.
    return()=>{_stopped=true;};
  },[]);
  return liqHeatmap;
};

const useDeribitOptions=()=>{
  const[deribit,setDeribit]=React.useState({ok:false,signal:0,direction:'neutral',skew:null,putCallRatio:null});
  React.useEffect(()=>{
    let _stopped=false;
    const _poll=async()=>{
      try{
        const r=await fetch('/api/deribit',{cache:'no-store',signal:AbortSignal.timeout(4000)});
        if(!r.ok)return;
        const d=await r.json();
        if(_stopped)return;
        setDeribit(d);
      }catch(_){}
    };
    // V13.2.1: re-enabled but slowed 30s -> 180s. deribitOptions (IV skew / put-call) IS
    //   wired into the score, but it's a structural signal that moves over minutes, not
    //   ticks. 3min keeps it fresh while cutting ~83% of its Vercel edge requests.
    _poll();
    const iv=setInterval(_poll,180000);
    return()=>{_stopped=true;clearInterval(iv);};
  },[]);
  return deribit;
};

// V10.7.73 — LIQUIDATION + CB PREMIUM SIGNAL HOOK
//   Polls /api/liq every 15s for:
//   - Bybit large liquidation cascades (capitulation signal)
//   - Binance funding rate extreme (overleveraged signal)
//   - Coinbase premium vs Kraken (US retail directional)
const useLiqSignal=()=>{
  const[liqSignal,setLiqSignal]=React.useState({ok:false,signal:0,direction:'neutral',reasons:[]});
  React.useEffect(()=>{
    let _stopped=false;
    const _poll=async()=>{
      try{
        const r=await fetch('/api/liq',{cache:'no-store',signal:AbortSignal.timeout(4000)});
        if(!r.ok)return;
        const d=await r.json();
        if(_stopped)return;
        setLiqSignal(d);
      }catch(_){}
    };
    // V13.2.1: re-enabled but slowed 15s -> 120s. liqSignal feeds up to +/-8 to the score
    //   (funding extreme, CB premium, liq cascades). 2min keeps the structural part fresh
    //   while cutting ~87% of its Vercel edge requests. (Fast liq cascades are the only
    //   part that decays in seconds; the tape already catches violent moves directly.)
    _poll();
    const iv=setInterval(_poll,120000);
    return()=>{_stopped=true;clearInterval(iv);};
  },[]);
  return liqSignal;
};

const useWhaleAlert=(currentAsset)=>{
  // V10.8.7: Replaced /api/whale (needs paid API key, returns ok:false) with
  //   tape-based detection from the Binance futures stream already running in
  //   useGlobalTape. No external API needed — large prints >$1M already come
  //   through tapeRef.whaleAlerts. We compute the same signal shape from that.
  //   Reads tapeRef via a shared ref — same object populated by useGlobalTape.
  const[whale,setWhale]=React.useState({netScore:0,direction:'neutral',transactionCount:0,available:false,lastUpdate:0});
  // tapeRef is passed via window._taraGlobalTapeRef so we can read it here
  React.useEffect(()=>{
    if(currentAsset!=='BTC')return;
    const iv=setInterval(()=>{
      try{
        const tapeRef=window._taraGlobalTapeRef;
        if(!tapeRef?.current)return;
        const alerts=tapeRef.current.whaleAlerts||[];
        if(!alerts.length)return;
        const now=Date.now();
        // Consider prints from last 3 minutes only
        const recent=alerts.filter(a=>now-a.time<180000);
        if(!recent.length){
          setWhale(prev=>({...prev,available:true,netScore:0,direction:'neutral',transactionCount:0}));
          return;
        }
        // Net score: buy prints vs sell prints, weighted by size
        let buyUSD=0,sellUSD=0;
        recent.forEach(a=>{
          if(a.side==='BUY')buyUSD+=a.usd;
          else sellUSD+=a.usd;
        });
        const total=buyUSD+sellUSD;
        if(total<100000){
          setWhale(prev=>({...prev,available:true,netScore:0,direction:'neutral',transactionCount:recent.length}));
          return;
        }
        // Normalize to [-6, +6] signal range
        // Strong imbalance: 80%+ one side = full score
        const buyRatio=buyUSD/total;
        const rawScore=(buyRatio-0.5)*12; // 0.5=0, 1.0=+6, 0.0=-6
        const netScore=Math.max(-6,Math.min(6,rawScore));
        const direction=netScore>1?'bullish':netScore<-1?'bearish':'neutral';
        setWhale({
          netScore:Math.round(netScore*10)/10,
          direction,
          transactionCount:recent.length,
          available:true,
          lastUpdate:now,
          buyUSD:Math.round(buyUSD/1000),
          sellUSD:Math.round(sellUSD/1000),
        });
      }catch(_){}
    },5000); // update every 5s — prints arrive in real time via WebSocket
    return()=>clearInterval(iv);
  },[currentAsset]);
  return whale;
};

const useSpotPerpDivergence=(currentAsset,bloomberg)=>{
  const[data,setData]=React.useState({divBps:0,spot:0,perp:0,perpAbove:false,lastUpdate:0,status:'init'});
  React.useEffect(()=>{
    if(!currentAsset)return;
    const _symbol='BTC-USD';
    const f=async()=>{
      try{
        // Coinbase public ticker — no auth needed, no rate limit issues
        const r=await fetch(`https://api.coinbase.com/v2/prices/${_symbol}/spot`);
        if(!r.ok)return;
        const j=await r.json();
        const _spot=parseFloat(j?.data?.amount);
        if(!Number.isFinite(_spot)||_spot<=0)return;
        const _perp=bloomberg?.markPrice||bloomberg?.indexPrice||0;
        if(_perp<=0){
          setData(prev=>({...prev,spot:_spot,lastUpdate:Date.now(),status:'no-perp'}));
          return;
        }
        const _divBps=((_perp-_spot)/_spot)*10000;
        setData({divBps:_divBps,spot:_spot,perp:_perp,perpAbove:_perp>_spot,lastUpdate:Date.now(),status:'live'});
      }catch(_){
        setData(prev=>({...prev,status:'error'}));
      }
    };
    f();
    const iv=setInterval(f,10000);
    return()=>clearInterval(iv);
  },[currentAsset,bloomberg?.markPrice,bloomberg?.indexPrice]);
  return data;
};

// V9.13: ADAPTIVE SCHEDULE — derived from user's own 536-trade history.
//   Each EST hour mapped to: status (TRADE / SKIP / OBSERVE), recommended dial,
//   and the historical win rate that justifies the recommendation. Status drives
//   the auto-dial logic and the schedule UI strip.
//
//   Status meanings:
//     TRADE   = green zone, historical WR ≥68% — go active
//     OBSERVE = neutral zone, WR 55-67% — small size or skip
//     SKIP    = red zone, WR <55% — don't trade unless very high conviction
//
//   Dial recommendations encode session-specific aggressiveness:
//     45 = patient (require strong setup)
//     50 = balanced (default)
//     55 = slightly aggressive (catch fast moves in slow sessions)
//
//   The auto-dial logic in MarketContextStrip blends THIS schedule with live
//   volatility (velocity/amplitude/ATR) and recent streak. Schedule is the
//   STARTING POINT, then live conditions push the final dial.
const TARA_SCHEDULE_BY_EST_HOUR={
  0:{status:'TRADE',dial:50,wr:73,note:'Asia open — your strongest window'},
  1:{status:'TRADE',dial:50,wr:73,note:'Asia mid — steady micro-trends'},
  2:{status:'TRADE',dial:50,wr:73,note:'Asia mid — clean signals'},
  3:{status:'TRADE',dial:50,wr:73,note:'Asia late — still strong'},
  4:{status:'TRADE',dial:50,wr:70,note:'London pre-open lead-in'},
  5:{status:'TRADE',dial:50,wr:70,note:'London pre-open'},
  6:{status:'OBSERVE',dial:55,wr:60,note:'London quiet lull — selective'},
  7:{status:'OBSERVE',dial:55,wr:60,note:'Bridge to US wake — few setups'},
  8:{status:'TRADE',dial:50,wr:68,note:'EU/US overlap begins'},
  9:{status:'TRADE',dial:50,wr:68,note:'US wake-up — real positioning'},
  10:{status:'TRADE',dial:50,wr:68,note:'US morning — best US window'},
  11:{status:'OBSERVE',dial:55,wr:58,note:'Pre-lunch transition'},
  12:{status:'TRADE',dial:50,wr:63,note:'US lunch — your data 63% WR (n=148), old 31% was stale'},
  13:{status:'TRADE',dial:50,wr:63,note:'US lunch — actual 63% WR beats table'},
  14:{status:'TRADE',dial:50,wr:67,note:'US afternoon actual 67% WR'},
  15:{status:'TRADE',dial:50,wr:64,note:'US afternoon — actual 64% WR'},
  16:{status:'OBSERVE',dial:52,wr:60,note:'US afternoon — actual 60% WR, light caution'},
  17:{status:'TRADE',dial:50,wr:65,note:'US close actual 65% WR'},
  18:{status:'TRADE',dial:50,wr:67,note:'US close — actual 67% WR'},
  19:{status:'OBSERVE',dial:55,wr:60,note:'Asia pre-open bridge'},
  20:{status:'TRADE',dial:50,wr:71,note:'Late US + Asia open'},
  21:{status:'TRADE',dial:50,wr:71,note:'Late evening — directional'},
  22:{status:'TRADE',dial:50,wr:71,note:'Asia ramp-up'},
  23:{status:'TRADE',dial:50,wr:73,note:'Asia evening — strong window'},
};

// V9.13: get the current EST hour. Reads system time and applies UTC-5 offset
//   (or UTC-4 during US daylight saving). We approximate DST as Mar-Nov; close
//   enough for hour-bucket recommendations. The library doesn't need exactness.
const _getEstHour=()=>{
  const d=new Date();
  const _month=d.getUTCMonth(); // 0-11
  const _dstActive=_month>=2&&_month<=10; // approx Mar-Nov
  const _offset=_dstActive?4:5;
  return((d.getUTCHours()-_offset)+24)%24;
};

// V9.13: smart-adaptive dial recommendation.
//   Combines four inputs in order of priority:
//     1. Schedule recommendation for the current EST hour
//     2. Streak adjustment (wins/losses in a row)
//     3. Live volatility (velocity/amplitude/ATR) — existing logic
//     4. Today's intra-session WR (if 5+ resolved trades available)
//
//   Returns: { dial: number, reasoning: string[], status: 'TRADE'|'OBSERVE'|'SKIP' }
//   Reasoning is an array of human-readable strings explaining each adjustment,
//   so the UI can show why the dial landed where it did.
const computeAdaptiveDial=({analysis,streak,todaySessionWR,todaySessionN,convictionState})=>{
  const _hour=_getEstHour();
  const _slot=TARA_SCHEDULE_BY_EST_HOUR[_hour]||{status:'OBSERVE',dial:50,wr:60,note:''};
  const reasoning=[`Hour ${_hour}:00 EST → ${_slot.status} (historical ${_slot.wr}% WR)`];
  let dial=_slot.dial;
  // V10.7.48: Conviction state — when Tara has no real edge yet, push toward patient.
  //   When conviction is strong early in window, push toward fast (lock at good Kalshi odds).
  if(convictionState){
    const _conv=Number(convictionState.conviction)||0;
    const _winLenSec=convictionState.winLenSec||900;
    const _elapsedSec=convictionState.elapsedSec||0;
    const _pctElapsed=_winLenSec>0?_elapsedSec/_winLenSec:0;
    // Early window (first 30%) — if strong conviction, push fast
    if(_pctElapsed<0.30&&_conv>=15){
      dial-=8;
      reasoning.push(`Strong conviction (${_conv.toFixed(0)}pt) early — dial -8 (lock fast at good Kalshi odds)`);
    } else if(_pctElapsed<0.30&&_conv>=10){
      dial-=4;
      reasoning.push(`Moderate conviction (${_conv.toFixed(0)}pt) early — dial -4`);
    }
    // Mid window (30-70%) — if still in deadzone, push patient
    if(_pctElapsed>=0.30&&_pctElapsed<0.70&&_conv<5){
      dial+=10;
      reasoning.push(`Deadzone at ${Math.round(_pctElapsed*100)}% of window — dial +10 (wait for conviction)`);
    }
    // Late window (70%+) — if still in deadzone, prepare for sit-out (max patient)
    if(_pctElapsed>=0.70&&_conv<5){
      dial+=15;
      reasoning.push(`Deadzone late in window — dial +15 (sit-out likely)`);
    }
  }
  // Streak adjustments — apply on top of the scheduled base
  if(streak&&streak.type==='WIN'&&streak.count>=4){
    dial+=5;
    reasoning.push(`${streak.count}W streak — dial +5 (don't get greedy, slow down)`);
  } else if(streak&&streak.type==='LOSS'&&streak.count>=3){
    dial+=10;
    reasoning.push(`${streak.count}L streak — dial +10 (patient, demand stronger setup)`);
  } else if(streak&&streak.type==='LOSS'&&streak.count>=2){
    dial+=5;
    reasoning.push(`${streak.count}L streak — dial +5 (slow down)`);
  }
  // Live volatility — same logic as the old auto-dial, but additive to schedule
  if(analysis){
    const _vr=analysis?.velocityRegime||'NORMAL';
    const _wa=analysis?.windowAmplitude?.label||'NORMAL';
    const _atr=Number(analysis?.atrBps)||0;
    if(_vr==='EXTREME'){dial-=15;reasoning.push('Velocity EXTREME — dial -15 (move fast)');}
    else if(_vr==='FAST'){dial-=8;reasoning.push('Velocity FAST — dial -8');}
    else if(_vr==='SLOW'){dial+=8;reasoning.push('Velocity SLOW — dial +8 (patient)');}
    if(_wa==='WILD'){dial-=8;reasoning.push('Amplitude WILD — dial -8');}
    else if(_wa==='WHIPSAW'){dial+=12;reasoning.push('Amplitude WHIPSAW — dial +12 (don\'t fight chop)');}
    else if(_wa==='DEAD'||_wa==='OPENING'){dial+=8;reasoning.push(`Amplitude ${_wa} — dial +8`);}
    if(_atr>0&&_atr<6){dial=Math.max(dial,55);reasoning.push('ATR <6bps (very tight) — floor dial at 55');}
    if(_atr>30){dial=Math.min(dial,40);reasoning.push('ATR >30bps (volatile) — ceiling dial at 40');}
  }
  // Today's actual WR adjustment — only if we have enough samples
  if(typeof todaySessionWR==='number'&&todaySessionN>=5){
    if(todaySessionWR<50){
      dial+=8;
      reasoning.push(`Today's session WR ${Math.round(todaySessionWR)}% (n=${todaySessionN}) — dial +8 (cool off)`);
    } else if(todaySessionWR>=80){
      dial+=5;
      reasoning.push(`Today's session WR ${Math.round(todaySessionWR)}% (n=${todaySessionN}) — dial +5 (lock in gains)`);
    }
  }
  // Clamp and snap to nearest 5
  dial=Math.max(0,Math.min(100,Math.round(dial/5)*5));
  return{dial,reasoning,status:_slot.status,scheduleNote:_slot.note,scheduleWR:_slot.wr,hour:_hour};
};

// ═══════════════════════════════════════════════════════════════════════════
// V9.15: LIVE PATTERN MATCHER (K-NEAREST HISTORICAL ANALOGS)
// ═══════════════════════════════════════════════════════════════════════════
// Given the current trade setup (regime, session, signal scores, etc.) finds
//   the K most-similar historical resolved trades and returns their outcome
//   distribution. Used as a second-opinion alongside the posterior.
//
// Approach: feature-vector cosine similarity. Each trade (current + historical)
//   becomes a normalized vector of ~16 dimensions. Cosine similarity ranges
//   -1 to 1; closer to 1 = more similar setup. Top K most-similar entries are
//   returned with their WIN/LOSS outcome.
//
// Why cosine vs euclidean: signals have different natural ranges (gap can be
//   ±55, ml is ±10, posterior is 0-100). Cosine compares DIRECTION of the
//   vector regardless of magnitude — so a "high-conviction UP setup" matches
//   other high-conviction UP setups whether magnitude is 50 or 80.
//
// Why not modify totalScore yet: V9.15 is OBSERVATIONAL. Per user direction,
//   the matcher exposes its findings to UI and to entry telemetry but doesn't
//   change lock decisions. After ~50 resolved trades on V9.15, we'll have data
//   on whether historical-analog WR predicts current outcome better than
//   posterior alone. If yes, V9.16 wires it into the engine.

// V9.15: feature extraction. Takes either a live `analysis` object (engine
//   output) OR a historical resolved entry and returns a normalized vector.
//   ORDER MATTERS — both sources must produce identically-shaped vectors.
//
// Returns: { vec: number[], dir: 'UP'|'DOWN'|null, valid: boolean }
//   valid: false if essential fields missing — entry should be excluded
const taraExtractMatchFeatures=(src,isLive)=>{
  // Live: src is the analysis object from useMemo. Historical: src is a trade-log entry.
  // We pull the SAME fields from each via a normalized accessor.
  let _dir,_regime,_session,_posterior,_atr,_sigs,_hour;
  if(isLive){
    // Live extraction from analysis object
    _dir=src?.lockInfo?.dir||(src?.rawProbAbove>50?'UP':src?.rawProbAbove<50?'DOWN':null);
    _regime=src?.regime||'';
    _session=src?.session||(typeof getMarketSessions==='function'?getMarketSessions().dominant:'')||'';
    _posterior=Number(src?.rawProbAbove)||50;
    _atr=Number(src?.atrBps)||0;
    _sigs=src?.rawSignalScores||{};
    _hour=new Date().getUTCHours();
  } else {
    // Historical extraction from resolved entry
    _dir=src?.dir||src?.direction||null;
    _regime=src?.regime||'';
    _session=src?.session||'';
    _posterior=Number(src?.posterior)||50;
    _atr=Number(src?.atrBpsAtLock)||0;
    _sigs=src?.signalScoresAtLock||{};
    _hour=new Date(src?.id||src?.time||Date.now()).getUTCHours();
  }
  // Validity check: need at minimum regime + session + signal scores
  if(!_regime||!_session||!_sigs||Object.keys(_sigs).length===0){
    return{vec:null,dir:_dir,valid:false};
  }
  // Build vector. ORDER MUST BE STABLE — changing this breaks similarity calc.
  const _regimes=['TRENDING UP','TRENDING DOWN','RANGE-CHOP','SHORT SQUEEZE','HIGH VOL CHOP'];
  const _sessions=['ASIA','EU','US','OFF-HOURS'];
  const vec=[];
  // Direction: ±1, 0 if null
  vec.push(_dir==='UP'?1:_dir==='DOWN'?-1:0);
  // Regime one-hot (5 dims)
  _regimes.forEach(r=>vec.push(_regime===r?1:0));
  // Session one-hot (4 dims)
  _sessions.forEach(s=>vec.push(_session===s?1:0));
  // Hour of day, sin/cos encoded (2 dims) — captures circularity of time
  const _hourRad=(_hour/24)*2*Math.PI;
  vec.push(Math.sin(_hourRad));
  vec.push(Math.cos(_hourRad));
  // Posterior normalized to [-1, 1] (50 = neutral)
  vec.push((Math.max(0,Math.min(100,_posterior))-50)/50);
  // ATR normalized — 30bps = 1.0 cap (typical high vol)
  vec.push(Math.min(1,_atr/30));
  // Signal scores — normalize each by typical max range
  // Most ±35, htfPatterns ±55, ml ±10, futures ±33
  const _normRanges={gap:35,momentum:35,structure:35,flow:35,technical:35,regime:35,rangePosition:35,
    htfPatterns:55,futures:33,ml:10,timeOfDay:10,macroShock:10,calendarRisk:35,tapeAccel:10,spotPerpDiv:8};
  ['gap','momentum','structure','flow','technical','regime','htfPatterns','futures','ml'].forEach(k=>{
    const _v=Number(_sigs[k])||0;
    const _max=_normRanges[k]||35;
    vec.push(Math.max(-1,Math.min(1,_v/_max)));
  });
  return{vec,dir:_dir,valid:true};
};

// V9.15: cosine similarity between two equal-length vectors. Returns [-1, 1].
const _taraCosineSim=(a,b)=>{
  if(!a||!b||a.length!==b.length)return 0;
  let dot=0,magA=0,magB=0;
  for(let i=0;i<a.length;i++){
    dot+=a[i]*b[i];
    magA+=a[i]*a[i];
    magB+=b[i]*b[i];
  }
  if(magA===0||magB===0)return 0;
  return dot/(Math.sqrt(magA)*Math.sqrt(magB));
};

// V9.15: main matcher. Takes current analysis + the historical trade log,
//   returns K nearest analogs with their outcomes + summary stats.
//
// Returns: {
//   topK: [{id, time, similarity, dir, result, posterior, regime, session}],
//   analogWR: number,         // % of topK that won
//   analogN: number,          // count of topK actually populated
//   analogConfidence: 'high' | 'medium' | 'low',  // based on similarity spread
//   avgSimilarity: number,    // mean cosine similarity of topK
//   currentDir: 'UP'|'DOWN'|null,
//   valid: bool,              // false if current feature vec couldn't be built
//   reason: string,           // explanation (for UI)
// }
const taraFindHistoricalAnalogs=(analysis,taraCallLog,currentAsset,K)=>{
  const _K=K||12;
  const _current=taraExtractMatchFeatures(analysis,true);
  if(!_current.valid){
    return{topK:[],analogWR:null,analogN:0,analogConfidence:'low',avgSimilarity:0,
      currentDir:_current.dir,valid:false,reason:'Current setup incomplete (no regime/session/signals)'};
  }
  // Build pool: all resolved trades of the same asset with valid feature vectors
  const _pool=[];
  (taraCallLog||[]).forEach(e=>{
    if(!e||(e.result!=='WIN'&&e.result!=='LOSS'))return;
    if((e.asset||'BTC')!==currentAsset)return;
    const _f=taraExtractMatchFeatures(e,false);
    if(!_f.valid)return;
    _pool.push({entry:e,feat:_f.vec});
  });
  if(_pool.length<10){
    return{topK:[],analogWR:null,analogN:0,analogConfidence:'low',avgSimilarity:0,
      currentDir:_current.dir,valid:false,reason:`Insufficient history (${_pool.length} valid entries, need ≥10)`};
  }
  // Compute similarity for each, sort desc
  const _scored=_pool.map(p=>({entry:p.entry,similarity:_taraCosineSim(_current.vec,p.feat)}));
  _scored.sort((a,b)=>b.similarity-a.similarity);
  const _top=_scored.slice(0,_K);
  // Outcome stats
  const _wins=_top.filter(t=>t.entry.result==='WIN').length;
  const _analogWR=_top.length>0?(_wins/_top.length)*100:null;
  const _avgSim=_top.length>0?_top.reduce((s,t)=>s+t.similarity,0)/_top.length:0;
  // Confidence in the match itself: high if top-K are all very similar (avg sim ≥0.85)
  //   AND there's a clean separation from the next batch. Medium if avg sim 0.70-0.85.
  //   Low if avg sim <0.70 (current setup isn't well represented in history).
  let _conf='low';
  if(_avgSim>=0.85)_conf='high';
  else if(_avgSim>=0.70)_conf='medium';
  return{
    topK:_top.map(t=>({
      id:t.entry.id||t.entry.time,
      time:t.entry.id||t.entry.time,
      similarity:t.similarity,
      dir:t.entry.dir||t.entry.direction,
      result:t.entry.result,
      posterior:t.entry.posterior,
      regime:t.entry.regime,
      session:t.entry.session,
      closingGapBps:t.entry.closingGapBps,
      tier:t.entry.tier,
    })),
    analogWR:_analogWR,
    analogN:_top.length,
    analogConfidence:_conf,
    avgSimilarity:_avgSim,
    currentDir:_current.dir,
    valid:true,
    reason:`${_top.length} of ${_pool.length} pool matches (avg sim ${_avgSim.toFixed(2)})`,
  };
};

// ═══════════════════════════════════════════════════════════════════════════
// V9.17: SCALPER ADVISOR — short-horizon Kalshi contract advisor
// ═══════════════════════════════════════════════════════════════════════════
// Separate engine from Tara's 15m posterior. Reads same raw feeds (tape, flow,
// futures, spot-perp, depth, velocity) but with weights tuned for 30-90 second
// holds and outputs Kalshi-cents-native recommendations.
//
// Tara's lock system is UNTOUCHED. The scalper runs in parallel:
//   - Tara: 15m window posterior → lock decision → optional auto-exec
//   - Scalper: continuous short-horizon read → contract entry/exit advisor
//
// Storage: taraScalperLog_v1 (localStorage). Never writes to taraCallLog or
//   taraScorecards. Has its own learning loop (not yet built — first ship is
//   stateless engine + manual logging).
//
// State machine:
//   IDLE → SUGGEST → user enters → HOLD → exit conditions → COOLDOWN → IDLE
// Anti-tilt: when Tara's tiltLockUntil is active, scalper inherits same block.
// Daily contract loss cap: when sum of losses (cents) ≥ limit, stop until next UTC day.
// Hourly suggestion cap: max N new suggestions per rolling 60min window.
// Weak hours: 12-17 EST blocked by default (matches V9.13 schedule).
//
// All thresholds user-configurable via TARA_SCALPER_DEFAULTS overrides stored
// in localStorage as taraScalperSettings_v1.

// V9.17: scalper settings defaults
// V9.17.2: auto-exec removed — scalper is advisory only.
// V9.17.3: auto-exec re-added with mutual exclusivity with Tara's auto-exec.
//          Conflict guard kept for safety. Auto-detect placeholder NOT re-added
//          (still no kalshiGetPositions REST helper; manual entry/exit is the path).
const TARA_SCALPER_DEFAULTS={
  enabled:false, // start OFF — user must opt in
  // Entry zones (YES side cents range we're willing to suggest)
  entryMinCents:15,
  entryMaxCents:45,
  // Target / stop (cents from entry)
  targetCents:35,
  stopCents:15,
  // Sizing
  contractsPerScalp:1,
  // Caps
  dailyLossCapCents:200,
  maxSuggestionsPerHour:3,
  // Cooldowns (minutes)
  cooldownAfterLossMin:30,
  cooldownAfterProfitMin:5,
  cooldownAfterNeutralMin:10,
  cooldownFloorMin:0, // no floor — user fully controls
  // Hours blocked (24-hour EST). Empty array = no block.
  blockHoursEst:[12,13,14,15,16,17],
  // V9.17.3: Auto-execution (back, mutually exclusive with Tara's auto-exec)
  autoExecEnabled:false, // start OFF
  conflictGuardEnabled:true, // when ON, scalper auto-exec also blocked if Tara has locked position on same market
  // Conviction threshold for SUGGEST
  minConviction:65,
};

// V9.17: load/save scalper settings
const loadScalperSettings=()=>{
  try{
    const s=localStorage.getItem('taraScalperSettings_v1');
    if(s){
      const parsed=JSON.parse(s);
      return{...TARA_SCALPER_DEFAULTS,...parsed};
    }
  }catch(_){}
  return{...TARA_SCALPER_DEFAULTS};
};
const saveScalperSettings=(s)=>{
  try{localStorage.setItem('taraScalperSettings_v1',JSON.stringify(s||{}));}catch(_){}
};

// V9.17: load/save scalper trade log
const loadScalperLog=()=>{
  try{
    const s=localStorage.getItem('taraScalperLog_v1');
    if(s){
      const parsed=JSON.parse(s);
      if(Array.isArray(parsed))return parsed;
    }
  }catch(_){}
  return[];
};
const saveScalperLog=(log)=>{
  try{localStorage.setItem('taraScalperLog_v1',JSON.stringify(Array.isArray(log)?log:[]));}catch(_){}
};

// V9.17: SCALPER ENGINE — computes short-horizon directional read and conviction.
//   Inputs: same raw feeds Tara uses, but reweighted for 30-90s horizon.
//   Output: { dir, conviction, reason, signals } — pure, no state.
//
// Signal weights (different from Tara's 15m engine):
//   - tapeAccel (5s vs 30s buy% divergence): PRIMARY ±30
//   - flow (30s aggressor imbalance): PRIMARY ±25
//   - velocity (tick momentum): ±15
//   - spotPerpDiv (perp premium/discount, contrarian): ±10
//   - depthFlash (order book pressure): ±10
//   - macroShock (4-asset coordination): ±10 (additive when present)
//   - tape consensus (w5/w15/w30 alignment): ±10
// Sum capped at ±100; conviction = abs(score).
//
// NOT included:
//   - gap (Kalshi-specific, irrelevant to spot direction)
//   - momentum / regime / htfPatterns (too slow for scalp horizon)
//   - ml model (trained on 15m outcomes)
const computeScalperRead=(inputs)=>{
  const{tapeWindows,globalFlow,velocityRef,spotPerpDiv,depthFlash,macroShockData,currentAsset}=inputs||{};
  if(!tapeWindows||!tapeWindows.w5||!tapeWindows.w30){
    return{dir:null,conviction:0,reason:'Tape data not ready',signals:{},score:0};
  }
  let score=0;
  const sigs={};
  const reasons=[];
  // 1. TAPE ACCEL — primary signal. Recent (5s) buyPct vs medium (30s).
  const _w5=tapeWindows.w5,_w15=tapeWindows.w15||{},_w30=tapeWindows.w30;
  const _w5Total=(_w5.buys||0)+(_w5.sells||0);
  const _w30Total=(_w30.buys||0)+(_w30.sells||0);
  if(_w5Total>=10000&&_w30Total>=30000){
    const _w5Pct=_w5.buyPct||50;
    const _w30Pct=_w30.buyPct||50;
    const _diff=_w5Pct-_w30Pct;
    if(Math.abs(_diff)>=10){
      const _mag=Math.min(30,Math.tanh(Math.abs(_diff)/25)*30);
      const _dir=_diff>0?1:-1;
      sigs.tapeAccel=_mag*_dir;
      score+=_mag*_dir;
      reasons.push(`tape ${_diff>0?'accelerating up':'accelerating down'} (5s ${_w5Pct.toFixed(0)}% vs 30s ${_w30Pct.toFixed(0)}%)`);
    }
  }
  // 2. FLOW — 30s aggressor imbalance. globalFlow.imbalance is ratio (1.0 = neutral, >1 = buy-dom, <1 = sell-dom)
  if(globalFlow&&typeof globalFlow.imbalance==='number'){
    const _aggr=Math.max(-1,Math.min(1,globalFlow.imbalance-1));
    if(Math.abs(_aggr)>=0.15){
      const _mag=Math.min(25,Math.abs(_aggr)*30);
      const _dir=_aggr>0?1:-1;
      sigs.flow=_mag*_dir;
      score+=_mag*_dir;
      reasons.push(`flow ${_aggr>0?'bullish':'bearish'} (imbalance ${_aggr.toFixed(2)})`);
    }
  }
  // 3. VELOCITY — recent tick momentum. velocityRef.current is signed value
  if(velocityRef&&velocityRef.current){
    const _vel=Number(velocityRef.current.bps1s)||0;
    if(Math.abs(_vel)>=0.5){
      const _mag=Math.min(15,Math.abs(_vel)*8);
      const _dir=_vel>0?1:-1;
      sigs.velocity=_mag*_dir;
      score+=_mag*_dir;
      reasons.push(`velocity ${_vel>0?'+':''}${_vel.toFixed(1)}bps/s`);
    }
  }
  // 4. SPOT-PERP DIVERGENCE — contrarian. perp premium = shorts squeeze risk for shorts
  if(spotPerpDiv&&spotPerpDiv.status==='live'&&Math.abs(spotPerpDiv.divBps)>=20){
    const _mag=Math.min(10,(Math.abs(spotPerpDiv.divBps)-20)*0.15+4);
    // CONTRARIAN: perp premium (positive) → DOWN bias (longs vulnerable)
    const _dir=spotPerpDiv.divBps>0?-1:1;
    sigs.spotPerpDiv=_mag*_dir;
    score+=_mag*_dir;
    reasons.push(`spot/perp ${spotPerpDiv.divBps>0?'longs crowded':'shorts crowded'} (${Math.round(spotPerpDiv.divBps)}bps)`);
  }
  // 5. DEPTH FLASH — order book pressure
  if(depthFlash&&depthFlash.signal){
    const _flashScore=Number(depthFlash.score)||0;
    if(Math.abs(_flashScore)>=3){
      const _mag=Math.min(10,Math.abs(_flashScore)*1.5);
      const _dir=_flashScore>0?1:-1;
      sigs.depth=_mag*_dir;
      score+=_mag*_dir;
      reasons.push(`book ${_flashScore>0?'bid-heavy':'ask-heavy'}`);
    }
  }
  // 6. MACRO SHOCK — 4-asset coordination
  if(macroShockData&&macroShockData.status==='live'){
    const _sol=macroShockData.sol?.deltaBps||0;
    const _xrp=macroShockData.xrp?.deltaBps||0;
    if(Math.abs(_sol)>=30&&Math.abs(_xrp)>=30&&Math.sign(_sol)===Math.sign(_xrp)){
      const _mag=Math.min(10,(Math.abs(_sol)+Math.abs(_xrp))/12);
      const _dir=_sol>0?1:-1;
      sigs.macroShock=_mag*_dir;
      score+=_mag*_dir;
      reasons.push(`macro shock ${_dir>0?'up':'down'} (sol+xrp aligned)`);
    }
  }
  // 7. TAPE CONSENSUS — w5/w15/w30 all agreeing
  if(_w5Total>=10000&&_w15&&_w30Total>=30000){
    const _w5p=_w5.buyPct||50,_w15p=_w15.buyPct||50,_w30p=_w30.buyPct||50;
    if(_w5p>=60&&_w15p>=60&&_w30p>=60){
      sigs.tapeConsensus=10;score+=10;
      reasons.push('tape consensus all timeframes UP');
    } else if(_w5p<=40&&_w15p<=40&&_w30p<=40){
      sigs.tapeConsensus=-10;score-=10;
      reasons.push('tape consensus all timeframes DOWN');
    }
  }
  // Cap and direction
  score=Math.max(-100,Math.min(100,score));
  const conviction=Math.round(Math.abs(score));
  const dir=Math.abs(score)<8?null:(score>0?'UP':'DOWN');
  return{
    dir,conviction,score,signals:sigs,
    reason:reasons.length>0?reasons.join(' · '):'No clear short-horizon signals',
  };
};

// V9.17: utility — get current EST hour with DST approximation
const _scalperGetEstHour=()=>{
  const d=new Date();
  const _month=d.getUTCMonth();
  const _dstActive=_month>=2&&_month<=10;
  const _offset=_dstActive?4:5;
  return((d.getUTCHours()-_offset)+24)%24;
};

// V9.17: cooldown helpers — when did we last EXIT a scalp, and what was the result?
const _scalperGetActiveCooldown=(scalperLog,settings)=>{
  if(!Array.isArray(scalperLog)||scalperLog.length===0)return null;
  const _last=scalperLog[scalperLog.length-1];
  if(!_last||!_last.exitTime)return null;
  const _ageMs=Date.now()-_last.exitTime;
  const _ageMin=_ageMs/60000;
  let _required=0;
  if(_last.outcome==='LOSS')_required=Math.max(settings.cooldownFloorMin||0,settings.cooldownAfterLossMin||0);
  else if(_last.outcome==='WIN')_required=Math.max(settings.cooldownFloorMin||0,settings.cooldownAfterProfitMin||0);
  else if(_last.outcome==='NEUTRAL')_required=Math.max(settings.cooldownFloorMin||0,settings.cooldownAfterNeutralMin||0);
  if(_required<=0||_ageMin>=_required)return null;
  const _remainingSec=Math.max(0,Math.ceil((_required*60)-_ageMs/1000));
  return{
    activeUntil:_last.exitTime+_required*60000,
    remainingSec:_remainingSec,
    reason:_last.outcome==='LOSS'?'loss cooldown (revenge-trading prevention)'
      :_last.outcome==='WIN'?'profit cooldown (don\'t chase the heat)'
      :'neutral cooldown',
    lastOutcome:_last.outcome,
  };
};

// V9.17: hourly suggestion cap check — count suggestions emitted in the last 60min
const _scalperGetHourlyCount=(scalperLog,suggestionsLogRef)=>{
  // suggestionsLogRef is an in-memory ref tracking SUGGEST timestamps (cleared on UTC rollover)
  const _now=Date.now();
  const _oneHourAgo=_now-60*60*1000;
  const _suggestions=(suggestionsLogRef?.current||[]).filter(t=>t>=_oneHourAgo);
  return _suggestions.length;
};

// V9.17: today's daily loss check (UTC day boundary)
const _scalperGetTodayLossCents=(scalperLog)=>{
  const _now=new Date();
  const _utcMidnight=Date.UTC(_now.getUTCFullYear(),_now.getUTCMonth(),_now.getUTCDate());
  if(!Array.isArray(scalperLog))return 0;
  let _lossCents=0;
  scalperLog.forEach(e=>{
    if(!e||!e.exitTime)return;
    if(e.exitTime<_utcMidnight)return;
    if(e.outcome==='LOSS'&&typeof e.profitCents==='number'){
      _lossCents+=Math.abs(e.profitCents);
    }
  });
  return _lossCents;
};

// V9.17: today's stats summary for the UI footer
const computeScalperTodayStats=(scalperLog)=>{
  const _now=new Date();
  const _utcMidnight=Date.UTC(_now.getUTCFullYear(),_now.getUTCMonth(),_now.getUTCDate());
  let wins=0,losses=0,neutrals=0,netCents=0;
  (scalperLog||[]).forEach(e=>{
    if(!e||!e.exitTime||e.exitTime<_utcMidnight)return;
    if(e.outcome==='WIN'){wins++;netCents+=e.profitCents||0;}
    else if(e.outcome==='LOSS'){losses++;netCents+=e.profitCents||0;} // profitCents is negative for losses
    else if(e.outcome==='NEUTRAL'){neutrals++;netCents+=e.profitCents||0;}
  });
  const n=wins+losses+neutrals;
  return{wins,losses,neutrals,n,netCents,winRate:n>0?Math.round((wins/n)*100):null};
};

// V9.17: compute eligibility for emitting a new SUGGEST
//   Returns: { eligible, blockReason } — blockReason is human-readable when blocked
const checkScalperEligibility=({settings,scalperLog,suggestionsLogRef,tiltLockUntil,kalshiYesPrice})=>{
  if(!settings.enabled)return{eligible:false,blockReason:'scalper disabled in settings'};
  // Anti-tilt inheritance from Tara
  if(tiltLockUntil&&Date.now()<tiltLockUntil){
    const _remainSec=Math.ceil((tiltLockUntil-Date.now())/1000);
    return{eligible:false,blockReason:`tara anti-tilt active · ${Math.floor(_remainSec/60)}m ${_remainSec%60}s remaining`};
  }
  // Weak hours block
  const _hour=_scalperGetEstHour();
  if(Array.isArray(settings.blockHoursEst)&&settings.blockHoursEst.includes(_hour)){
    return{eligible:false,blockReason:`weak hour block · ${_hour}:00 EST is in your skip window`};
  }
  // Cooldown from last trade
  const _cd=_scalperGetActiveCooldown(scalperLog,settings);
  if(_cd){
    const _m=Math.floor(_cd.remainingSec/60),_s=_cd.remainingSec%60;
    return{eligible:false,blockReason:`${_cd.reason} · ${_m}:${_s.toString().padStart(2,'0')} remaining`,cooldown:_cd};
  }
  // Hourly suggestion cap
  const _hourly=_scalperGetHourlyCount(scalperLog,suggestionsLogRef);
  if(_hourly>=(settings.maxSuggestionsPerHour||3)){
    return{eligible:false,blockReason:`hourly cap reached · ${_hourly}/${settings.maxSuggestionsPerHour} suggestions this hour`};
  }
  // Daily loss cap
  const _todayLoss=_scalperGetTodayLossCents(scalperLog);
  if(_todayLoss>=(settings.dailyLossCapCents||200)){
    return{eligible:false,blockReason:`daily loss cap hit · -${_todayLoss}¢ today (limit ${settings.dailyLossCapCents}¢)`};
  }
  // YES price in entry zone
  if(typeof kalshiYesPrice!=='number'||kalshiYesPrice<=0){
    return{eligible:false,blockReason:'kalshi yes price not available'};
  }
  // We check whether the suggested direction's entry will fall in the configured zone in the engine;
  //   here we just confirm a price exists. Final zone check happens in advisor below.
  return{eligible:true};
};

// V9.17: useScalperEngine hook. Runs every 1s, computes scalper read.
//   Returns the latest `computeScalperRead` output.
const useScalperEngine=({tapeWindows,globalFlow,velocityRef,spotPerpDiv,depthFlash,macroShockData,currentAsset,enabled})=>{
  const[read,setRead]=React.useState({dir:null,conviction:0,score:0,signals:{},reason:''});
  React.useEffect(()=>{
    if(!enabled)return;
    const _tick=()=>{
      const r=computeScalperRead({tapeWindows,globalFlow,velocityRef,spotPerpDiv,depthFlash,macroShockData,currentAsset});
      setRead(r);
    };
    _tick();
    const iv=setInterval(_tick,1000);
    return()=>clearInterval(iv);
  },[enabled,tapeWindows,globalFlow,velocityRef,spotPerpDiv,depthFlash,macroShockData,currentAsset]);
  return read;
};

// ═══════════════════════════════════════════════════════════════════════════
// V9.17.4: KALSHI PORTFOLIO BALANCE TRACKING
// ═══════════════════════════════════════════════════════════════════════════
// Polls /portfolio/balance every 30s when credentials exist. Captures a
// session baseline at first successful poll, persists baseline per-UTC-day
// so "today's delta" survives reloads (resets at UTC midnight).
//
// State shape: { balance, baseline, baselineDay, lastFetchAt, status, error }
//   - balance: current Kalshi balance in dollars (cents/100)
//   - baseline: balance at start of UTC day
//   - baselineDay: UTC date string for baseline (YYYY-MM-DD)
//   - lastFetchAt: timestamp of last successful poll
//   - status: 'idle' | 'connected' | 'error' | 'no-creds'
//
// "Today's delta" = balance - baseline. If baselineDay !== today, reset baseline.
const useKalshiBalance=({kalshiCreds,timeFormat})=>{
  const[state,setState]=React.useState(()=>{
    try{
      const saved=JSON.parse(localStorage.getItem('tara_kalshi_balance_v1')||'{}');
      return{
        balance:typeof saved.balance==='number'?saved.balance:null,
        baseline:typeof saved.baseline==='number'?saved.baseline:null,
        baselineDay:saved.baselineDay||null,
        lastFetchAt:saved.lastFetchAt||0,
        status:'idle',
        error:null,
      };
    }catch(_){
      return{balance:null,baseline:null,baselineDay:null,lastFetchAt:0,status:'idle',error:null};
    }
  });
  React.useEffect(()=>{
    try{
      localStorage.setItem('tara_kalshi_balance_v1',JSON.stringify({
        balance:state.balance,baseline:state.baseline,baselineDay:state.baselineDay,lastFetchAt:state.lastFetchAt,
      }));
    }catch(_){}
  },[state.balance,state.baseline,state.baselineDay,state.lastFetchAt]);
  React.useEffect(()=>{
    if(!kalshiCreds?.apiKeyId||!kalshiCreds?.privateKeyPem){
      setState(prev=>({...prev,status:'no-creds'}));
      return;
    }
    let _cancelled=false;
    const _poll=async()=>{
      try{
        const res=await kalshiPing({apiKeyId:kalshiCreds.apiKeyId,privateKeyPem:kalshiCreds.privateKeyPem});
        if(_cancelled)return;
        if(!res.ok){
          setState(prev=>({...prev,status:'error',error:res.reason||'unknown'}));
          return;
        }
        // Kalshi returns balance in cents (integer). Convert to dollars.
        const _rawBalance=Number(res.balance);
        if(!Number.isFinite(_rawBalance)){
          setState(prev=>({...prev,status:'error',error:'invalid-balance'}));
          return;
        }
        const _balanceDollars=_rawBalance/100;
        const _today=_dayKey(new Date(),timeFormat); // V10.2.x: honors header TZ (was UTC-only)
        setState(prev=>{
          // Reset baseline on new day (in user's TZ) OR if no baseline exists yet.
          // V9.17.9: was `!_baseline` which is true for 0, causing baseline to
          //   reset every poll when actual balance was zero. Use typeof check.
          let _baseline=prev.baseline;
          let _baselineDay=prev.baselineDay;
          if(typeof _baseline!=='number'||_baselineDay!==_today){
            _baseline=_balanceDollars;
            _baselineDay=_today;
          }
          return{
            balance:_balanceDollars,
            baseline:_baseline,
            baselineDay:_baselineDay,
            lastFetchAt:Date.now(),
            status:'connected',
            error:null,
          };
        });
      }catch(e){
        if(_cancelled)return;
        setState(prev=>({...prev,status:'error',error:e?.message||String(e)}));
      }
    };
    _poll();
    const iv=setInterval(_poll,30000);
    return()=>{_cancelled=true;clearInterval(iv);};
  },[kalshiCreds?.apiKeyId,kalshiCreds?.privateKeyPem,timeFormat]); // V10.2.x: timeFormat in deps so day-roll re-evaluates
  return state;
};

// V9.2.3: TIME-OF-DAY MODEL — computes hourly WR from call log, returns a
//   boost/penalty map keyed by UTC hour. Fed into the engine posterior.
const computeTimeOfDayModel=(log,asset)=>{
  const hourStats={};
  for(let h=0;h<24;h++)hourStats[h]={W:0,L:0};
  (log||[]).forEach(e=>{
    if(!e||!e.result||(e.asset||'BTC')!==asset)return;
    if(e.result!=='WIN'&&e.result!=='LOSS')return;
    const dt=new Date(e.id||e.time);
    const h=dt.getUTCHours();
    if(e.result==='WIN')hourStats[h].W++;else hourStats[h].L++;
  });
  // Compute per-hour boost: WR > 60% → positive, WR < 45% → negative. Needs ≥8 trades.
  const boostMap={};
  for(let h=0;h<24;h++){
    const n=hourStats[h].W+hourStats[h].L;
    if(n<8){boostMap[h]=0;continue;}
    const wr=hourStats[h].W/n;
    if(wr>=0.70)boostMap[h]=6;
    else if(wr>=0.60)boostMap[h]=3;
    else if(wr>=0.55)boostMap[h]=1;
    else if(wr>=0.45)boostMap[h]=0;
    else if(wr>=0.35)boostMap[h]=-3;
    else boostMap[h]=-6;
  }
  return boostMap;
};

const formatDuration=(seconds)=>{
  if(seconds==null||isNaN(seconds))return'—';
  const s=Math.max(0,Math.round(seconds));
  if(s<60)return`${s}s`;
  const m=Math.floor(s/60);
  const r=s%60;
  return`${m}m ${String(r).padStart(2,'0')}s`;
};
// V5.7.3: Stable signed-integer formatter — fixes -0 / +0 weirdness in score breakdowns.
const formatSignedInt=(val)=>{
  if(val==null||isNaN(val))return'0';
  const r=Math.round(val);
  if(r===0)return'0';
  return(r>0?'+':'')+r;
};
const calcVWAP=(h)=>{if(!h||!h.length)return null;let t=0,v=0;h.forEach(c=>{t+=((c.h+c.l+c.c)/3)*c.v;v+=c.v;});return v===0?null:t/v;};
const calcRSI=(d,p=14)=>{if(!d||d.length<p+1)return 50;let ag=0,al=0;for(let i=1;i<=p;i++){const x=d[i-1]-d[i];if(x>0)ag+=x;else al-=x;}ag/=p;al/=p;for(let i=p+1;i<Math.min(d.length,p+30);i++){const x=d[i-1]-d[i];ag=(ag*(p-1)+Math.max(x,0))/p;al=(al*(p-1)+Math.max(-x,0))/p;}return al===0?100:100-(100/(1+(ag/al)));};
const calcATR=(h,p=14)=>{if(!h||h.length<p+1)return 0;let s=0;for(let i=0;i<p;i++){const H=h[i].h,L=h[i].l,pc=h[i+1]?.c||h[i].o;s+=Math.max(H-L,Math.abs(H-pc),Math.abs(L-pc));}return s/p;};
const calcBB=(c,p=20)=>{if(!c||c.length<p)return{upper:0,mid:0,lower:0,pctB:0.5,width:0};const s=c.slice(0,p),m=s.reduce((a,b)=>a+b,0)/p,sd=Math.sqrt(s.reduce((a,b)=>a+Math.pow(b-m,2),0)/p);const u=m+2*sd,l=m-2*sd;return{upper:u,mid:m,lower:l,pctB:(u-l)>0?(c[0]-l)/(u-l):0.5,width:m>0?((u-l)/m)*10000:0};};

// V99 NEW INDICATORS
// V10.7.35 — FIXED consecutive candle computation to match CC • Yata Pine Script.
//   OLD: counted each candle individually as green (close>open) or red (close<open)
//        → pure candle color, no trend requirement
//   NEW: counts consecutive bars where close > close[4] (up) or close < close[4] (down)
//        → matches DeMark-style sequential counting used by CC • Yata indicator
//        → also stores raw count for exhaustion logic (see Structure signal below)
//   HISTORY NOTE: liveHistory[0] is newest. We want bars[0]=oldest for comparison.
const calcConsecutiveCandles=(history)=>{
  if(!history||history.length<6)return{green:0,red:0};
  // Work oldest→newest (liveHistory is newest-first, so reverse)
  const bars=history.slice().reverse(); // bars[0]=oldest
  const n=bars.length;
  let green=0,red=0;
  // Start from the most recent bar (index n-1) and count backwards
  // CC•Yata: close > close[4] = "up bar", close < close[4] = "down bar"
  for(let i=n-1;i>=4;i--){
    const isUp=bars[i].c>bars[i-4].c;
    const isDn=bars[i].c<bars[i-4].c;
    if(isUp){
      if(red>0)break; // streak broken
      green++;
      if(green>=9){green=9;break;} // CC•Yata caps at 9 then resets
    } else if(isDn){
      if(green>0)break;
      red++;
      if(red>=9){red=9;break;}
    } else {
      break; // neutral bar breaks streak
    }
  }
  return{green,red};
};
const calcVolumeRatio=(history,recent=5,baseline=25)=>{if(!history||history.length<baseline)return 1;const rv=history.slice(0,recent).reduce((s,c)=>s+(c.v||0),0)/recent;const bv=history.slice(recent,baseline).reduce((s,c)=>s+(c.v||0),0)/(baseline-recent);return bv>0?rv/bv:1;};
const calcPriceChannel=(history,n=20)=>{if(!history||history.length<n)return 0.5;const hi=Math.max(...history.slice(0,n).map(c=>c.h));const lo=Math.min(...history.slice(0,n).map(c=>c.l));return hi>lo?(history[0].c-lo)/(hi-lo):0.5;};
const calcMomentumAlignment=(d1,d5,d15)=>{const signs=[Math.sign(d1),Math.sign(d5),Math.sign(d15)];const sum=signs.reduce((a,b)=>a+b,0);if(Math.abs(sum)===3)return{aligned:true,strong:true,dir:sum>0?1:-1};if(Math.abs(sum)>=2)return{aligned:true,strong:false,dir:sum>0?1:-1};return{aligned:false,strong:false,dir:0};};

// ═══════════════════════════════════════
// TARA SELF-TRAINING ENGINE V1
// Adaptive weights + calibration + trade log
// ═══════════════════════════════════════

// Default signal weights — trained on 268-trade dataset
// 163W-105L=60.8% · UP 65% · DOWN 55.6% · SS 68% · TD 87.5% · EU 69.7%
// DOWN in SHORT SQUEEZE: 50% — regime-gated suppression active
// V152: Added rangePosition — mean-reversion / range-exhaustion signal.
// Measures how far we've drifted from window-open price relative to ATR-scaled envelope.
// V4.6 FRESH START: Default weights reset to neutral midpoint. Per user request — full
//   training reset, no carryover from the 57-trade seed CSV. Each signal starts at the
//   same weight so gradient descent learns purely from the user's live trades.
// V10.7.91: DEFAULT_WEIGHTS updated from empirical analysis of 1,819 BTC trades.
//   Signal effectiveness (win avg - loss avg aligned delta):
//     gap:        +10.1  → RAISE weight, it's the primary predictor
//     flow:       +4.4   → keep, secondary predictor
//     momentum:   +2.5   → lower, borderline noise
//     htfPatterns: +1.7  → near-mute multiplier, pure noise in RANGE-CHOP
//     liqHeatmap: -0.1   → anti-predictive, mute
//     macd:       +0.3   → noise, lower
//     avwap:      +0.3   → noise, lower
//   All others unchanged. Learning loop will continue refining from here.
const DEFAULT_WEIGHTS={gap:55.00,momentum:28.00,structure:35.00,flow:40.00,technical:35.00,regime:35.00,rangePosition:35.00,
  // V10.8.1: htfPatterns zeroed out.
  //   All-time delta was +1.7 (noise). Recent 50 trades: delta -9.0 (actively inverted).
  //   Losses have MORE htfPatterns signal than wins. Dampening to 0.15× wasn't enough.
  //   Zero it entirely. It can be re-enabled if future data shows it recovers.
  //
  // V13.4.245 REVERTED TO 1.00 -- READ THIS BEFORE ZEROING IT AGAIN.
  //   The V10.8.1 zeroing never actually took effect: the weight was read as
  //   `W.htfPatterns||1.0`, and `0||1.0` is 1.0. v13.4.244 fixed that read and
  //   the zero finally applied. Live result over the next 13 calls: the sit-out
  //   rate jumped to 77%, against 34% on v230-239 and 43% on v220-229.
  //
  //   Why: htfPatterns contributes a capped ±20 (±35 in the htfChop branch) to
  //   totalScore on 81% of calls. Every downstream conviction and edge gate was
  //   tuned, over months, against a totalScore that INCLUDED that contribution.
  //   Removing it shifts the whole score distribution down, so calls stop
  //   clearing gates that nobody re-tuned, and Tara sits out instead of trading.
  //
  //   So V10.8.1's finding may still be correct -- the signal does look inverted,
  //   and |directionalScore|>=150 runs 52-58% WR against ~74% below it -- but the
  //   weight CANNOT be dropped on its own. Zeroing it must ship together with a
  //   recalibration of the conviction/edge thresholds, measured, in one change.
  //   Until that work is done this stays at 1.00.
  htfPatterns:1.00,futures:1.00,
  // V9.12: macroShock is also a multiplier — raw signal already produces ±10. The
  //   weight learning loop tunes how much that ±10 nudge influences the final score.
  //   Starts neutral; if 4-asset shocks reliably predict outcome direction, it'll buff
  //   toward 2.5; if not, decay toward 0.
  // V13.4.254: the macroShock / calendarRisk / tapeAccel / spotPerpDiv WEIGHT
  //   entries were removed here. Each had 0 read sites -- declared as tunable
  //   multipliers and never multiplied by anything.
  //
  //   THE SIGNALS THEMSELVES ARE LIVE and must not be deleted: they add to
  //   totalScore directly (e.g. totalScore+=_tapeAccelAdj) and write into
  //   rawSignalScores. They simply never routed through the weight system, so
  //   the learning loop could never tune them. Re-add an entry here only
  //   together with a _weightOf() call at the signal's own site.
  // V9.14: three new signals, each a multiplier on its respective raw output.
  //   - calendarRisk: dampener during macro events, default 1.0 means apply ±25-35 as-is
  //   - tapeAccel: ±10 raw, multiplier tunes effective strength
  //   - spotPerpDiv: ±8 raw, multiplier tunes effective strength
};

// V13.4.244: resolve a signal weight without the falsy-zero trap.
//   `W.k||1.0` turns a DELIBERATE zero into full weight, which silently undid
//   V10.8.1's decision to switch htfPatterns off. It also cannot fall back to
//   DEFAULT_WEIGHTS when a key is simply absent from the learned store -- and
//   htfPatterns/futures ARE absent from it, since updateWeights only ever
//   writes the seven core keys.
//   Order: learned value if numeric (0 included) -> DEFAULT_WEIGHTS -> 1.0.
//   Type-check rather than Number(): Number(null) is 0 and 0 is finite, so a
//   null/absent W would have resolved EVERY signal to zero weight and muted the
//   whole model. Same Number(null)===0 sentinel trap that has bitten here before.
const _weightOf=(W,key)=>{
  const _v=(W&&typeof W==='object')?W[key]:undefined;
  if(typeof _v==='number'&&Number.isFinite(_v))return _v;
  const _d=DEFAULT_WEIGHTS[key];
  return (typeof _d==='number'&&Number.isFinite(_d))?_d:1.0;
};

// Per-regime weight sets — each regime gets its own gradient descent
// Initialized from global defaults, diverge over time based on what works in each regime
// V143: Per-regime weight defaults pre-trained from 379 seed trades. Previously all four
//       regimes started from identical DEFAULT_WEIGHTS — gradient descent was supposed to
//       differentiate them over time but the attribution gate (>10%) + small learning rate
//       produced ~1-3pt deltas after hundreds of trades. Pre-baking the seed-trained values
//       gives users a differentiated starting point on first load. Subsequent live trades
//       continue to update via gradient descent on top.
// V4.6 FRESH START: Per-regime weights reset to neutral. All four regimes start identical
//   from DEFAULT_WEIGHTS — gradient descent will differentiate them as you trade.
const DEFAULT_REGIME_WEIGHTS={
  'SHORT SQUEEZE': {...DEFAULT_WEIGHTS},
  'RANGE-CHOP':    {...DEFAULT_WEIGHTS},
  'HIGH VOL CHOP': {...DEFAULT_WEIGHTS},
  'TRENDING DOWN': {...DEFAULT_WEIGHTS},
};
const REGIME_WEIGHT_KEYS={'SHORT SQUEEZE':'taraV110RW_SS_V110','RANGE-CHOP':'taraV110RW_RC_V110','HIGH VOL CHOP':'taraV110RW_HVC_V110','TRENDING DOWN':'taraV110RW_TD_V110'};
const loadRegimeWeights=()=>{
  const out={};
  // V10.7.55 RESET: regime weights learned pre-V10.7.44 are contaminated.
  //   The V10.7.44 fix changed StochRSI / AVWAP / Whipsaw computations — they now produce
  //   different signal scores from the same market data. Weights learned BEFORE that fix
  //   were tuned against the WRONG signal values; using them on correct signals is wrong.
  //   One-time reset, gated by a flag so it only runs once per browser. After reset, the
  //   self-learning loop in `updateRegimeWeights` will re-learn from V10.7.44+ clean data.
  const _RESET_FLAG_KEY='taraRegimeWeights_v10_7_55_reset';
  let _alreadyReset=false;
  try{_alreadyReset=localStorage.getItem(_RESET_FLAG_KEY)==='1';}catch(_){}
  Object.entries(REGIME_WEIGHT_KEYS).forEach(([rg,key])=>{
    if(!_alreadyReset){
      try{localStorage.removeItem(key);}catch(_){}
    }
    try{const s=localStorage.getItem(key);if(s){const w=JSON.parse(s);if(w&&typeof w.gap==='number'){out[rg]=w;return;}}}catch(e){}
    out[rg]={...DEFAULT_REGIME_WEIGHTS[rg]};
  });
  if(!_alreadyReset){
    // Also reset the per-asset bundles so they hydrate fresh
    try{
      localStorage.removeItem('taraRegimeWeightsByAsset_BTC_v1');

      localStorage.setItem(_RESET_FLAG_KEY,'1');
      try{console.info('[V10.7.55] Regime weights reset to defaults — will re-learn from V10.7.44+ clean data.');}catch(_){}
    }catch(_){}
  }
  return out;
};
// V5.5b: User-trade persistence stubbed per user request:
//   'stats and training records for Tara's calls needs to be saved only, not the ones i
//    take from predictor'.
//   - saveRegimeWeights: was learning per-regime weights from user trades → no-op
//   - saveTradeLog (below at line ~136): was user trade history → no-op
//   In-memory state still updates during a session so existing logic doesn't break.
//   Tara's Call scorecards (taraCallScorecards_v1) remain persisted independently.
// V7.8: User asked "why dont weights persist over sessions and why do we start clean".
//   Answer: V5.5b stubbed weight save when learning fed from USER trades (those polluted
//   weights). After V7.1's applyTradeLearning refactor, learning feeds EXCLUSIVELY from
//   Tara's own resolved calls (asset-tagged). That's exactly what should train weights.
//   So saveWeights is re-enabled to localStorage; cloud sync handled at the per-asset
//   state level (adaptiveWeightsByAsset / regimeWeightsByAsset) below.
const saveRegimeWeights=(rwObj)=>{
  try{localStorage.setItem('taraRegimeWeights_v1',JSON.stringify(rwObj||{}));}catch(e){}
};
// V10.7.91: WEIGHT_BOUNDS updated. Gap upper raised 65→75 (strongest predictor,
//   give learning room to go higher). Momentum upper lowered 58→40 (noise signal,
//   cap it so learning can't over-rely on it).
const WEIGHT_BOUNDS={gap:[5,75],momentum:[5,40],structure:[2,38],flow:[2,55],technical:[5,48],regime:[2,45],rangePosition:[5,55],
  // V9.11.0: multiplier bounds. 0 means signal is fully muted; 2.5 means 250% amplified.
  htfPatterns:[0.00,2.50],futures:[0.00,2.50],
  // V9.12: macroShock multiplier bounds match htfPatterns/futures.
  macroShock:[0.00,2.50],
  // V9.14: calendar risk has tighter bounds (0.5-1.5) because we want the
  //   dampener to ALWAYS partially fire near macro events even if learning thinks
  //   it doesn't help — the cost of a single FOMC-spike loss outweighs marginal
  //   WR optimization. tapeAccel and spotPerpDiv use standard 0-2.5 bounds.
  calendarRisk:[0.50,1.50],tapeAccel:[0.00,2.50],spotPerpDiv:[0.00,2.50]};
const LEARNING_RATE=0.8;

const loadWeights=()=>{
  // V10.7.91 RESET: DEFAULT_WEIGHTS updated from 1,819-trade empirical analysis.
  //   Old defaults had all signals equal (gap=35, flow=35, htfPatterns=1.0).
  //   New defaults reflect actual signal effectiveness (gap=55, flow=40, htfPatterns=0.15).
  //   Must reset so users don't continue with old contaminated gradient descent state.
  const _RESET_FLAG_KEY='taraWeights_v10_7_91_reset';
  let _alreadyReset=false;
  try{_alreadyReset=localStorage.getItem(_RESET_FLAG_KEY)==='1';}catch(_){}
  if(!_alreadyReset){
    try{
      localStorage.removeItem('taraWeightsV110');
      localStorage.removeItem('taraWeightsByAsset_BTC_v1');
      const _now=Date.now();
      localStorage.setItem('taraWeightsByAsset_BTC_v1_updatedAt',String(_now));
      localStorage.setItem(_RESET_FLAG_KEY,'1');
      try{console.info('[V10.7.91] Adaptive weights reset — new empirically-calibrated defaults: gap=55, flow=40, htfPatterns=0.15');}catch(_){}
    }catch(_){}
  }
  // V10.7.55 RESET: same reasoning as loadRegimeWeights — adaptive weights tuned against
  //   pre-V10.7.44 signals are contaminated. Reset gated by same flag so it happens once.
  // V10.7.57 FIX: also bump the cloud-sync updatedAt timestamp so the local reset wins
  //   against any stale cloud-stored weights. Without this, cloud RMW merge would
  //   restore the contaminated weights on next sync (defeating the reset entirely).
  const _RESET_FLAG_KEY_55='taraRegimeWeights_v10_7_55_reset';
  let _alreadyReset55=false;
  try{_alreadyReset55=localStorage.getItem(_RESET_FLAG_KEY_55)==='1';}catch(_){}
  if(!_alreadyReset55){
    try{
      localStorage.removeItem('taraWeightsV110');
      localStorage.removeItem('taraWeightsByAsset_BTC_v1');
      const _now=Date.now();
      localStorage.setItem('taraWeightsByAsset_BTC_v1_updatedAt',String(_now));
      try{console.info('[V10.7.55] Adaptive weights reset — will re-learn from V10.7.44+ clean data.');}catch(_){}
    }catch(_){}
  }
  try{const s=localStorage.getItem('taraWeightsV110');if(s){const w=JSON.parse(s);if(w&&typeof w.gap==='number')return w;}return{...DEFAULT_WEIGHTS};}catch(e){return{...DEFAULT_WEIGHTS};}
};
const saveWeights=(w)=>{
  try{localStorage.setItem('taraWeightsV110',JSON.stringify(w||{}));}catch(e){}
};

// Load trade log
// removed
// removed
// Best hours: 4 (100%) and 5 (100%)
// ═══════════════════════════════════════════════════════════════════════════
// V9.3.0: KALSHI AUTO-EXECUTION (RSA-PSS signed orders via /api/kalshi proxy)
// ═══════════════════════════════════════════════════════════════════════════
// All requests go through the existing Vercel rewrite at /api/kalshi/*.
// Credentials (api key id + RSA private key PEM) are localStorage-only and
// NEVER synced to Firestore.
//
// Signing format (Kalshi v2 spec):
//   message  = `${ts_ms}${METHOD_UPPER}${path_with_v2_prefix}`
//              e.g. "1715000000000POST/trade-api/v2/portfolio/orders"
//   signature = RSA-PSS(SHA-256, salt=32) over message, then base64 standard.
// Headers required on every authed request:
//   KALSHI-ACCESS-KEY        — api key UUID
//   KALSHI-ACCESS-TIMESTAMP  — ms epoch (string)
//   KALSHI-ACCESS-SIGNATURE  — base64
//
// IMPORTANT: this module assumes the Vercel proxy at /api/kalshi/* forwards
// the Authorization-style headers untouched and preserves method+body. If your
// vercel.json strips custom headers, orders will 401. Verify with a sandbox
// order before flipping the dry-run switch off.
// ───────────────────────────────────────────────────────────────────────────
const _kBufToB64=(buf)=>{const b=new Uint8Array(buf);let s='';for(let i=0;i<b.byteLength;i++)s+=String.fromCharCode(b[i]);return btoa(s);};

// V9.17.8: PKCS#1 → PKCS#8 wrapping
// Web Crypto's importKey('pkcs8', ...) requires PKCS#8 DER. Kalshi gives
// users keys in either PKCS#1 (`-----BEGIN RSA PRIVATE KEY-----`) or PKCS#8
// (`-----BEGIN PRIVATE KEY-----`). PKCS#1 keys were failing with DataError.
//
// Fix: detect PKCS#1 header, then wrap the raw PKCS#1 DER bytes in the
// fixed PKCS#8 envelope so Web Crypto can ingest it. No cryptographic
// transformation — just adds the algorithm identifier wrapper bytes.
//
// PKCS#8 envelope structure (RFC 5208):
//   SEQUENCE {
//     INTEGER 0                                    -- version
//     SEQUENCE {                                   -- AlgorithmIdentifier
//       OID 1.2.840.113549.1.1.1                   -- rsaEncryption
//       NULL                                       -- parameters (always NULL)
//     }
//     OCTET STRING { ...PKCS#1 RSAPrivateKey DER... }
//   }
const _kEncodeDerLen=(len)=>{
  if(len<0x80)return new Uint8Array([len]);
  if(len<0x100)return new Uint8Array([0x81,len]);
  if(len<0x10000)return new Uint8Array([0x82,(len>>8)&0xff,len&0xff]);
  return new Uint8Array([0x83,(len>>16)&0xff,(len>>8)&0xff,len&0xff]);
};
const _kConcatBytes=(...arrs)=>{
  let total=0;for(const a of arrs)total+=a.length;
  const out=new Uint8Array(total);let off=0;
  for(const a of arrs){out.set(a,off);off+=a.length;}
  return out;
};
const _kWrapPkcs1AsPkcs8=(pkcs1Bytes)=>{
  // AlgorithmIdentifier for rsaEncryption:
  //   30 0D 06 09 2A 86 48 86 F7 0D 01 01 01 05 00
  //   SEQ len=13, OID len=9 [1.2.840.113549.1.1.1], NULL
  const _algId=new Uint8Array([0x30,0x0d,0x06,0x09,0x2a,0x86,0x48,0x86,0xf7,0x0d,0x01,0x01,0x01,0x05,0x00]);
  // Version INTEGER 0
  const _version=new Uint8Array([0x02,0x01,0x00]);
  // OCTET STRING wrapping PKCS#1 bytes
  const _octTag=new Uint8Array([0x04]);
  const _octLen=_kEncodeDerLen(pkcs1Bytes.length);
  const _octetString=_kConcatBytes(_octTag,_octLen,pkcs1Bytes);
  // Inner SEQUENCE content
  const _inner=_kConcatBytes(_version,_algId,_octetString);
  // Outer SEQUENCE
  const _outerTag=new Uint8Array([0x30]);
  const _outerLen=_kEncodeDerLen(_inner.length);
  return _kConcatBytes(_outerTag,_outerLen,_inner);
};
const _kPemToBuf=(pem)=>{
  const trimmed=String(pem||'').trim();
  if(!trimmed)throw new Error('Empty private key');
  const _isPkcs1=/-----BEGIN RSA PRIVATE KEY-----/.test(trimmed);
  const _isPkcs8=/-----BEGIN PRIVATE KEY-----/.test(trimmed);
  const _isEncrypted=/-----BEGIN ENCRYPTED PRIVATE KEY-----/.test(trimmed);
  const _isEC=/-----BEGIN EC PRIVATE KEY-----/.test(trimmed);
  if(_isEncrypted)throw new Error('Encrypted private keys not supported. Re-save the key without a passphrase.');
  if(_isEC)throw new Error('EC keys not supported by Kalshi. Use an RSA key.');
  if(!_isPkcs1&&!_isPkcs8)throw new Error('Unrecognized key format. Expected PEM with BEGIN PRIVATE KEY or BEGIN RSA PRIVATE KEY header.');
  const cleaned=trimmed.replace(/-----BEGIN [^-]+-----/g,'').replace(/-----END [^-]+-----/g,'').replace(/\s+/g,'');
  if(!cleaned)throw new Error('Empty private key body');
  let bin;
  try{bin=atob(cleaned);}catch(_){throw new Error('Invalid base64 in private key body');}
  const raw=new Uint8Array(bin.length);
  for(let i=0;i<bin.length;i++)raw[i]=bin.charCodeAt(i);
  if(_isPkcs1){
    // Wrap PKCS#1 in PKCS#8 envelope so Web Crypto can import it
    return _kWrapPkcs1AsPkcs8(raw).buffer;
  }
  // Already PKCS#8 — pass through
  return raw.buffer;
};
const _kImportedKeyCache=new Map(); // pem→CryptoKey (so we don't re-import every order)
const _kImportKey=async(pem)=>{
  if(_kImportedKeyCache.has(pem))return _kImportedKeyCache.get(pem);
  const key=await crypto.subtle.importKey('pkcs8',_kPemToBuf(pem),{name:'RSA-PSS',hash:'SHA-256'},false,['sign']);
  _kImportedKeyCache.set(pem,key);
  return key;
};
const _kSign=async({pem,method,path,ts})=>{
  const key=await _kImportKey(pem);
  const msg=new TextEncoder().encode(`${ts}${method}${path}`);
  const sig=await crypto.subtle.sign({name:'RSA-PSS',saltLength:32},key,msg);
  return _kBufToB64(sig);
};

// Authed fetch through /api/kalshi/* proxy. path is the Kalshi resource path
// WITHOUT the /trade-api/v2 prefix — we add that to the signing string but the
// proxy mounts at /api/kalshi/<path> and rewrites server-side to /trade-api/v2/<path>.
const kalshiAuthedFetch=async({apiKeyId,privateKeyPem,method,path,body,timeoutMs=8000})=>{
  if(!apiKeyId||!privateKeyPem)return{ok:false,status:0,reason:'no-credentials'};
  const m=String(method||'GET').toUpperCase();
  const ts=String(Date.now());
  const fullPath=`/trade-api/v2${path}`;
  // V10.7.89: Strip query params before signing — Kalshi docs explicitly require
  //   signing the path WITHOUT query parameters. Including them causes 401.
  //   e.g. sign /trade-api/v2/portfolio/settlements not /trade-api/v2/portfolio/settlements?limit=1000
  const fullPathForSigning=fullPath.split('?')[0];
  let sig;
  try{sig=await _kSign({pem:privateKeyPem,method:m,path:fullPathForSigning,ts});}
  catch(e){return{ok:false,status:0,reason:'sign-failed: '+(e?.message||String(e))};}
  // V9.18.0: Railway-hosted proxy. Vercel IPs get 403'd by Kalshi (verified
  //   via empty-body 403 on POST /portfolio/orders while GET works). Direct
  //   browser fetch hits CORS preflight failure (Kalshi doesn't allow
  //   cross-origin write headers). Railway's IP range isn't on Kalshi's
  //   blocklist, so the proxy works. The proxy is a pure forwarder — adds
  //   no auth — so this is safe to point anywhere that proxies correctly.
  //
  //   TO CONFIGURE: after deploying kalshi-proxy.js to Railway, set
  //   KALSHI_PROXY_BASE below to the Railway URL (e.g.
  //   'https://tara-proxy.up.railway.app/kalshi'). The trailing '/kalshi' is
  //   the proxy's path prefix — the proxy forwards /kalshi/<path> →
  //   api.elections.kalshi.com/trade-api/v2/<path>.
  //
  //   V10.2.8 — MULTI-PROXY FALLBACK. The previous single-proxy setup made
  //   the Railway URL a single point of failure: one Railway outage = total
  //   Kalshi blackout (verified May 14 by ERR_NAME_NOT_RESOLVED on the bare
  //   kalshi.com domain when the proxy went down). Now `taraKalshiProxyBase`
  //   accepts a comma-separated list; the fetch tries each in order until
  //   one succeeds, with full per-URL signal so timeouts don't poison the
  //   retry. To configure multiple proxies:
  //     localStorage.setItem('taraKalshiProxyBase',
  //       'https://tara-kalshi.up.railway.app/kalshi,https://backup-proxy.fly.dev/kalshi');
  //   Override at runtime: same key as before, just supports CSV now.
  //
  //   Default order: Railway first because its egress IP is the one verified
  //   to pass Kalshi's write filter, then the build-time API host (the Render
  //   service, VITE_API_BASE) as the fallback once Railway's free credit runs
  //   out each month. Both serve the same /kalshi/<path> contract.
  const _kProxyRaw=(typeof localStorage!=='undefined'&&localStorage.getItem('taraKalshiProxyBase'))||'';
  const _kBuildApiBase=((import.meta.env&&import.meta.env.VITE_API_BASE)||'').trim().replace(/\/+$/,'');
  const _kProxyBases=_kProxyRaw
    ?_kProxyRaw.split(',').map(s=>s.trim()).filter(Boolean)
    :['https://tara-kalshi.up.railway.app/kalshi',...(_kBuildApiBase?[_kBuildApiBase+'/kalshi']:[])];
  const headers={
    'KALSHI-ACCESS-KEY':apiKeyId,
    'KALSHI-ACCESS-TIMESTAMP':ts,
    'KALSHI-ACCESS-SIGNATURE':sig,
    'Accept':'application/json',
  };
  // Build the base init (sans signal — signal must be fresh per attempt).
  const _baseInit={method:m,headers};
  if(body&&m!=='GET'&&m!=='HEAD'){headers['Content-Type']='application/json';_baseInit.body=JSON.stringify(body);}
  // V10.2.8 — fallback loop. First success wins; otherwise return the last
  //   error with prefix so user can see WHICH proxy(es) failed.
  let resp=null;
  let _lastReason='no-proxies-configured';
  for(const _base of _kProxyBases){
    const url=`${_base}${path}`;
    const init={..._baseInit};
    try{init.signal=AbortSignal.timeout(timeoutMs);}catch(_){}
    try{
      resp=await fetch(url,init);
      break; // success — exit fallback loop
    }catch(e){
      _lastReason=`${_base.slice(0,60)} · ${e?.message||String(e)}`;
      // continue to next proxy
    }
  }
  if(!resp){
    return{ok:false,status:0,reason:`all-proxies-failed (${_kProxyBases.length} tried): ${_lastReason}`};
  }
  // V9.17.25: VERBOSE ERROR CAPTURE. Pull the response body as TEXT first
  //   (so we get it even if it isn't valid JSON), then try parsing. Surface
  //   Kalshi's actual rejection reason in the returned `reason` field by
  //   walking common error field names: error.message / error.code / message
  //   / msg / detail / errors[]. Also capture rate-limit headers since Kalshi
  //   uses 429-style headers even on non-429 responses sometimes.
  let bodyText='';
  try{bodyText=await resp.text();}catch(_){bodyText='(could not read body)';}
  let data=null;
  try{data=bodyText?JSON.parse(bodyText):null;}catch(_){data=null;}
  // Build a detailed error reason from whatever Kalshi sent back.
  const _bestReason=()=>{
    if(resp.ok)return null;
    // Try common JSON paths first
    const candidates=[
      data?.error?.message, data?.error?.code, data?.error,
      data?.message, data?.msg, data?.detail,
      Array.isArray(data?.errors)&&data.errors[0]?.message,
      Array.isArray(data?.errors)&&data.errors[0],
    ].filter(v=>v&&typeof v==='string'&&v.length<200);
    if(candidates.length)return `http ${resp.status} · ${candidates[0]}`;
    // No structured error — show the first chunk of the raw body
    if(bodyText&&bodyText.length){
      const _trimmed=bodyText.trim().slice(0,200).replace(/\s+/g,' ');
      if(_trimmed)return `http ${resp.status} · ${_trimmed}`;
    }
    return `http ${resp.status}`;
  };
  // Capture relevant response headers for diagnostic logging.
  const _hdrSnap={};
  try{
    ['x-ratelimit-remaining','x-ratelimit-reset','retry-after','x-request-id','content-type']
      .forEach(h=>{const v=resp.headers.get(h);if(v)_hdrSnap[h]=v;});
  }catch(_){}
  if(!resp.ok){
    // Print a single rich log line for inspection in DevTools. Don't leak the
    // signature or auth headers — only the request shape + response info.
    try{console.error('[V9.17.25] Kalshi error',{
      method:m,path,status:resp.status,reason:_bestReason(),
      requestBody:body,
      responseHeaders:_hdrSnap,
      responseBodySnippet:bodyText?.slice(0,400)||'(empty)',
    });}catch(_){}
  }
  return{
    ok:resp.ok,
    status:resp.status,
    data,
    reason:_bestReason(),
    rawBody:resp.ok?undefined:bodyText, // V9.17.25: keep raw body so caller can show it
    respHeaders:_hdrSnap,
  };
};

// V10.2.28 — KALSHI FEE COMPUTATION
//   Kalshi charges a trading fee on EVERY trade (entry AND exit, both sides).
//   Formula (per their published docs):
//     fee_cents = ceil(0.07 × contracts × price_dollars × (1 - price_dollars) × 100)
//   Where price_dollars is the contract price in 0..1 range (e.g. 0.50 for 50¢).
//   The fee is ceiled (rounded-lg UP) to the next whole cent.
//
//   Examples:
//     1 contract @ 50¢: ceil(0.07 × 1 × 0.5 × 0.5 × 100) = ceil(1.75) = 2¢
//     10 contracts @ 50¢: ceil(0.07 × 10 × 0.5 × 0.5 × 100) = ceil(17.5) = 18¢
//     1 contract @ 90¢: ceil(0.07 × 1 × 0.9 × 0.1 × 100) = ceil(0.63) = 1¢
//
//   Note: settlement (contract resolves at 100¢) has no exit fee — only
//   pre-settlement exits trigger the second fee. computeKalshiTotalFees
//   accepts an `isSettlement` flag for this.
//
//   Sources: Kalshi public fee schedule. May vary by product (BTC vs others).
//   For TARA we use the standard formula and display approximate fees.
const computeKalshiFeeCents=(contractsN,priceCents)=>{
  const c=Math.max(0,Number(contractsN)||0);
  const p=(Math.max(1,Math.min(99,Number(priceCents)||0)))/100;
  if(c===0)return 0;
  return Math.ceil(0.07*c*p*(1-p)*100);
};
const computeKalshiTotalFees=({contractsN,entryCents,exitCents,isSettlement})=>{
  const _entryFee=computeKalshiFeeCents(contractsN,entryCents);
  // Settlement (winning side resolves at 100¢) has no exit fee
  const _exitFee=isSettlement?0:computeKalshiFeeCents(contractsN,exitCents);
  return{
    entryFeeCents:_entryFee,
    exitFeeCents:_exitFee,
    totalFeeCents:_entryFee+_exitFee,
    totalFeeDollars:(_entryFee+_exitFee)/100,
  };
};

// ── KALSHI ORDER API V2 MIGRATION (Sept 2026) ────────────────────────────────
// Kalshi removed POST /portfolio/orders and DELETE /portfolio/orders/{id} in
// late June 2026; both now answer 410 deprecated_v1_order_endpoint. Creation
// moved to POST /portfolio/events/orders and cancellation to
// DELETE /portfolio/events/orders/{id}. Reads are unaffected: GET on a single
// order, the order list, positions and balance all still work on the old paths.
//
// The wire format changed in three ways that matter:
//
//   1. The old (side: yes|no) x (action: buy|sell) pair collapses into ONE
//      `side` quoted on the YES book only: "bid" buys YES, "ask" sells YES.
//      Buying NO at X is the same trade as selling YES at (1 - X), so the four
//      legacy combinations fold into two. The invariant is YES exposure:
//
//        legacy               YES exposure   v2 side
//        buy  YES             increases      bid
//        sell NO              increases      bid
//        sell YES             decreases      ask
//        buy  NO              decreases      ask
//
//   2. `price` is always the YES price in fixed-point dollars, never the price
//      of the leg you named. Every path here already carries `limitCents` as
//      the YES price, so the price is limitCents/100 in all four cases and the
//      old (100 - lim) arithmetic for NO legs disappears from the wire body.
//      It still governs cost per contract, which is why count is unchanged.
//
//   3. `time_in_force` and `self_trade_prevention_type` are now required, and
//      `expiration_ts` became `expiration_time`. good_till_canceled plus an
//      expiration_time reproduces the old expiring-limit behaviour exactly.
const KALSHI_ORDERS_PATH='/portfolio/events/orders';
// True when the trade increases YES exposure. See the table above.
const _kV2Side=(legacySide,legacyAction)=>
  ((legacyAction==='buy')===(legacySide==='yes'))?'bid':'ask';
// Kalshi wants fixed-point dollars as a string: 65 cents -> "0.6500".
const _kV2PriceDollars=(yesCents)=>(yesCents/100).toFixed(4);

// Place a limit order. side='yes' for UP, 'no' for DOWN.
// betDollars is the user's stake; we convert to contract count using the limit price.
// Each YES contract pays out $1 if it resolves YES; cost = yes_price (cents).
//   stake $10 @ 65¢ → 10/0.65 = 15 contracts (cost $9.75, net win $5.25 if hit).
//   For NO contracts: cost = (100 - yes_price) cents, capped at 99.
const kalshiBuildOrder=(args)=>buildSafeEntry(args);

// Place order (or simulate in dry-run). Returns {ok, dryRun, order?, reason?}.
// V13.4.79: realized money per settled lock, in cents, for ONE contract.
//   Kalshi quotes the YES (UP) price, so the cost of the side actually taken is
//   kalshiAtLock for an UP call and (100 - kalshiAtLock) for a DOWN call. A win pays
//   100 and returns the cost; a loss forfeits the cost. Win rate cannot see this --
//   a 60%-WR book bought at 70c loses money -- which is why every learning surface
//   keyed on WR is blind to price drift. Returns null when unpriceable. ASCII-only.
const taraNetCents=(e)=>{
  try{
    if(!e)return null;
    const k=Number(e.kalshiAtLock);
    if(!Number.isFinite(k)||k<=0||k>=100)return null;
    const d=e.dir;if(d!=='UP'&&d!=='DOWN')return null;
    const r=e.result;if(r!=='WIN'&&r!=='LOSS')return null;
    const cost=(d==='UP')?k:(100-k);
    return Math.round((r==='WIN')?(100-cost):(-cost));
  }catch(_e){return null;}
};
const kalshiPlaceOrder=async({apiKeyId,privateKeyPem,ticker,dir,limitCents,betDollars,dryRun,clientOrderId,count})=>{
  const built=kalshiBuildOrder({ticker,dir,limitCents,betDollars,clientOrderId,count});
  if(!built.ok)return {...built,dryRun:!!dryRun};
  if(dryRun)return {ok:true,dryRun:true,order:{order_id:'DRY_'+built.client_order_id,client_order_id:built.client_order_id,ticker,side:built.side,v2Side:built.v2Side,action:'buy',count:built.count,fill_count_fp:String(built.count),remaining_count_fp:'0',average_fill_price:(built.costPerContractCents/100).toFixed(4),taker_fees_dollars:(executionFeeCents(built.costPerContractCents,built.count)/100).toFixed(4),maker_fees_dollars:'0',status:'filled',_simulated:true}};
  const res=await kalshiAuthedFetch({apiKeyId,privateKeyPem,method:'POST',path:KALSHI_ORDERS_PATH,body:built.body});
  if(!res.ok)return {ok:false,reason:res.reason||'order failed',dryRun:false,definitiveRejection:[400,401,403,404,422].includes(res.status),raw:res};
  return {ok:true,dryRun:false,order:res.data?.order||res.data};
};

// V9.18.10: KALSHI RESPONSE NORMALIZATION
// Kalshi's order objects have evolved through several API versions. The legacy
// fields our code expected (status==='filled', fill_price as integer cents) do
// NOT match current Kalshi:
//   - Current status values: 'resting' | 'canceled' | 'executed'  (NOT 'filled')
//   - Current price fields: 'yes_price_dollars' / 'no_price_dollars' (string like "0.5600")
//   - Fill average:  'taker_fill_cost_dollars' / 'maker_fill_cost_dollars' (string $)
//   - Counts:        'fill_count_fp' / 'remaining_count_fp' (string fixed-point)
//
// This was an end-to-end bug since the exit code was written: status never
// matched 'filled' on a real order, so stop-loss / take-profit / smart-exit
// never fired on real money. Dry-run worked because dry-run hardcoded 'filled'.
//
// This helper accepts the raw Kalshi order object and returns:
//   { status: 'filled' | 'resting' | 'partially_filled' | 'canceled' | <raw>,
//     fillPriceCents: int 1-99 | null,
//     filledCount: int }
//
// status is normalized to 'filled' for both 'executed' (legacy fully-filled)
// and 'filled' (dry-run synthetic) so all downstream code keeps working.
const KALSHI_FILLED_STATUSES=['executed','filled'];
const _kalshiParseDollarString=(s)=>{
  // Kalshi returns prices as strings like "0.5600" representing dollars.
  // Returns integer cents (rounded-lg), or null if unparseable / out of [1, 99].
  if(s==null)return null;
  const n=Number(s);
  if(!Number.isFinite(n)||n<=0||n>=1)return null;
  const cents=Math.round(n*100);
  return cents>=1&&cents<=99?cents:null;
};
const kalshiNormalizeOrder=(order)=>normalizeExecutionOrder(order);

const kalshiGetOrder=async({apiKeyId,privateKeyPem,orderId,dryRun})=>{
  if(dryRun||(orderId&&String(orderId).startsWith('DRY_'))){
    // Dry-run orders auto-fill after ~3s of simulated lookups
    return{ok:true,dryRun:true,order:{order_id:orderId,status:'filled',_simulated:true}};
  }
  const res=await kalshiAuthedFetch({apiKeyId,privateKeyPem,method:'GET',path:`/portfolio/orders/${encodeURIComponent(orderId)}`});
  if(!res.ok)return{ok:false,reason:res.reason||'lookup failed',raw:res};
  return{ok:true,order:res.data?.order||res.data};
};

const kalshiCancelOrder=async({apiKeyId,privateKeyPem,orderId,dryRun})=>{
  if(dryRun||(orderId&&String(orderId).startsWith('DRY_'))){
    return{ok:true,dryRun:true,order:{order_id:orderId,status:'canceled',_simulated:true}};
  }
  // Cancellation moved with creation; the legacy DELETE path returns 410.
  const res=await kalshiAuthedFetch({apiKeyId,privateKeyPem,method:'DELETE',path:`${KALSHI_ORDERS_PATH}/${encodeURIComponent(orderId)}`});
  if(!res.ok)return{ok:false,reason:res.reason||'cancel failed',raw:res};
  return{ok:true,order:res.data?.order||res.data};
};

// Sell out — used when the offer threshold is hit or the window is closing.
// Selling the leg held reduces that exposure: dumping YES is an ask, dumping NO
// is a bid, because shedding NO is the same trade as buying YES. `limitCents`
// is the YES price in both cases, so it goes on the wire unchanged.
const kalshiExitPosition=async({apiKeyId,privateKeyPem,ticker,side,count,limitCents,dryRun,positionId})=>{
  const clientOrderId=await stableExecutionId(['exit',apiKeyId||'simulation',ticker,side,positionId||'unattributed']);
  const built=buildSafeExit({ticker,side,count,limitCents,clientOrderId});if(!built.ok)return built;
  if(dryRun)return {ok:true,dryRun:true,order:{order_id:'DRY_'+clientOrderId,client_order_id:clientOrderId,ticker,side,action:'sell',count:built.count,fill_count_fp:String(built.count),remaining_count_fp:'0',average_fill_price:((side==='yes'?limitCents:100-limitCents)/100).toFixed(4),taker_fees_dollars:(executionFeeCents(side==='yes'?limitCents:100-limitCents,built.count)/100).toFixed(4),maker_fees_dollars:'0',status:'filled',_simulated:true}};
  const res=await kalshiAuthedFetch({apiKeyId,privateKeyPem,method:'POST',path:KALSHI_ORDERS_PATH,body:built.body});
  return res.ok?{ok:true,order:res.data?.order||res.data}:{ok:false,reason:res.reason||'exit failed'};
};

// ── ENTRY LADDER ─────────────────────────────────────────────────────────────
// V13.4.228 measured this on 440 settled calls and it is the whole argument for
// arming anything:
//
//     crossing the spread (what a naive auto-exec does)   -1.85c per contract
//     resting a cent inside (maker fee is 0)              +1.00c per contract
//     swing                                                2.85c per contract
//
// The swing is larger than the signal's gross edge, so execution decides whether
// the strategy makes or loses money. The settings for a ladder have existed
// since V9.7.4 and the UI renders them, but nothing ever implemented the walk;
// entryLadderEnabled was a checkbox wired to no behaviour. This is that walk.
//
// Plan only, no I/O, so it can be tested without credentials or a live market.
// Given the current offer it returns the rungs to try in order: start
// undercutCents below the offer and close half the remaining gap on each step,
// so the last rung sits adjacent to the offer rather than repeating it. The
// final entry is the offer itself, flagged takesSpread, which is the -1.85c
// path and is only reached if every resting rung failed to fill.
//
//   planEntryLadder({offerCents:70,undercutCents:4,maxSteps:2})
//     -> [{priceCents:66,...},{priceCents:68,...},{priceCents:70,takesSpread:true}]
//
// A ladder disabled, or a nonsensical offer, collapses to the single taking rung
// so callers always get a usable plan.
const planEntryLadder=({offerCents,undercutCents=2,maxSteps=2,stepSec=8,enabled=true})=>{
  const offer=Math.round(Number(offerCents));
  if(!Number.isFinite(offer)||offer<1||offer>99){
    return{ok:false,reason:'bad-offer',rungs:[]};
  }
  const take={priceCents:offer,takesSpread:true,waitSec:0,step:null};
  const steps=Math.max(0,Math.min(6,Math.floor(Number(maxSteps)||0)));
  const under=Math.max(1,Math.min(20,Math.floor(Number(undercutCents)||0)));
  const wait=Math.max(2,Math.min(60,Math.floor(Number(stepSec)||0)));
  if(!enabled||steps<1)return{ok:true,rungs:[take],ladder:false};
  const rungs=[];
  let gap=under;
  for(let i=0;i<steps;i++){
    const price=offer-gap;
    // Never rest at or through the offer, and never below 1c.
    if(price>=1&&price<offer)rungs.push({priceCents:price,takesSpread:false,waitSec:wait,step:i+1});
    gap=Math.floor(gap/2);
    if(gap<1)break;
  }
  // Drop duplicate prices produced by the halving while keeping order.
  const seen=new Set();
  const unique=rungs.filter(r=>seen.has(r.priceCents)?false:(seen.add(r.priceCents),true));
  return{ok:true,rungs:[...unique,take],ladder:unique.length>0};
};

// Walk the plan against Kalshi: rest at each rung, wait for a fill, cancel and
// step up if it does not come, and take the offer only as the last rung. Honours
// dryRun end to end by delegating to the helpers, which simulate. Returns the
// filled order plus which rung filled, so the caller can log realised entry cost
// against the -1.85c / +1.00c figures above and see whether the ladder is
// actually earning its keep on this account.
const kalshiRunEntryLadder=async({apiKeyId,privateKeyPem,ticker,dir,betDollars,offerCents,settings,dryRun,clientOrderId,shouldAbort,onRung,onPlaced,pollMs=1000})=>{
  const plan=planEntryLadder({offerCents,undercutCents:settings?.entryLadderUndercutCents,maxSteps:settings?.entryLadderMaxSteps,stepSec:settings?.entryLadderStepSec,enabled:!!settings?.entryLadderEnabled});
  return runSafeEntryLadder({plan,dir,betDollars,clientOrderId,shouldAbort,onRung,onPlaced,pollMs,
    place:({rung,count,clientOrderId})=>kalshiPlaceOrder({apiKeyId,privateKeyPem,ticker,dir,limitCents:dir==='UP'?rung.priceCents:100-rung.priceCents,betDollars,dryRun,clientOrderId,count}),
    read:orderId=>kalshiGetOrder({apiKeyId,privateKeyPem,orderId,dryRun}),
    cancel:orderId=>kalshiCancelOrder({apiKeyId,privateKeyPem,orderId,dryRun}),
    amend:async({orderId,rung,count,clientOrderId})=>{
      if(dryRun)return {ok:true};
      const res=await kalshiAuthedFetch({apiKeyId,privateKeyPem,method:'POST',path:KALSHI_ORDERS_PATH+'/'+encodeURIComponent(orderId)+'/amend',body:{ticker,side:dir==='UP'?'bid':'ask',price:((dir==='UP'?rung.priceCents:100-rung.priceCents)/100).toFixed(4),count:String(count),client_order_id:clientOrderId}});
      return res.ok?{ok:true,order:res.data?.order||res.data}:{ok:false,reason:res.reason};
    }});
};

// Quick credentials check — calls /portfolio/balance which requires auth but no params.
const kalshiPing=async({apiKeyId,privateKeyPem})=>{
  if(!apiKeyId||!privateKeyPem)return{ok:false,reason:'no-credentials'};
  const res=await kalshiAuthedFetch({apiKeyId,privateKeyPem,method:'GET',path:'/portfolio/balance',timeoutMs:6000});
  if(!res.ok)return{ok:false,reason:res.reason||`http ${res.status}`};
  return{ok:true,balance:res.data?.balance,raw:res.data};
};

// V10.2.10 — fetch current open positions from Kalshi. Returns:
//   {ok, positions:[{ticker, count, side, ...}], raw}
// Kalshi's current response uses signed position_fp strings; older responses
// used position. Missing or malformed rows must fail closed, never look flat.
// V13.4.331: PAGINATION. This fetched exactly one page (limit=100) and never
//   looked at a `cursor` field -- despite this same file already implementing
//   the correct cursor-loop pattern for two sibling Kalshi list endpoints,
//   /portfolio/fills and /markets (see _fetchAllPages, ~L24241). Any open
//   position past row 100 was silently invisible to every caller of this
//   function, including the entire v322/v328/v329/v330 reconciliation chain --
//   which would see a deterministically empty/incomplete `positions` array on
//   EVERY poll, not just one, since the same first page comes back every time.
//   Reported live: a real, open, same-side position on Kalshi that never
//   appeared in this function's output for 60+ seconds (surviving the v329
//   2-poll debounce, which only protects against a transient single-snapshot
//   miss, not a deterministic truncation). This account has independently
//   accumulated multiple orphaned open positions tonight from the v328/v330
//   incidents this function itself exists to catch -- exactly the condition
//   that pushes a real row past a single un-paginated page.
const kalshiFetchPositions=async({apiKeyId,privateKeyPem})=>{
  if(!apiKeyId||!privateKeyPem)return{ok:false,reason:'no-credentials',positions:[]};
  const _mp=[];
  let _cursor=null;
  let _lastRaw=null;
  const _seenCursors=new Set();
  for(let _page=0;_page<20;_page++){
    const _path=`/portfolio/positions?count_filter=position&limit=100${_cursor?`&cursor=${encodeURIComponent(_cursor)}`:''}`;
    const res=await kalshiAuthedFetch({apiKeyId,privateKeyPem,method:'GET',path:_path,timeoutMs:8000});
    if(!res.ok)return{ok:false,reason:res.reason||`http ${res.status}`,positions:[]};
    _lastRaw=res.data;
    const _pageResult=normalizeKalshiPositionsPage(res.data);
    if(!_pageResult.ok)return{ok:false,reason:_pageResult.reason,positions:[]};
    _mp.push(..._pageResult.positions);
    if(!_pageResult.cursor)return{ok:true,positions:_mp,raw:_lastRaw};
    if(_seenCursors.has(_pageResult.cursor))return{ok:false,reason:'Kalshi positions pagination repeated a cursor',positions:[]};
    _seenCursors.add(_pageResult.cursor);
    _cursor=_pageResult.cursor;
  }
  return{ok:false,reason:'Kalshi positions pagination exceeded 20 pages',positions:[]};
};

// ═══════════════════════════════════════════════════════════════════════════
// V9.19.16: computeAutoExecSize — CENTRAL SIZING FUNCTION
//
// SINGLE SOURCE OF TRUTH for "how many contracts and dollars will the next
// auto-exec trade place?" Called from BOTH:
//   • The auto-exec entry effect (the actual fire path)
//   • The trade ticket UI (the preview shown to the user)
// Result: ticket preview MATCHES what places. No two-source divergence.
//
// Design contract:
//   • Inputs: settings + market state (no React state, pure function)
//   • Returns: {decision, intendedDollars, intendedContracts, ...components}
//   • decision is one of:
//       'fire'         — place this many contracts for this many dollars
//       'sit-out-cap'  — intended exceeds maxBetPerTrade; sit out (V9.19.16 rule)
//       'sit-out-tiny' — intended < $1; sit out (sizing said no)
//       'sit-out-bad'  — bad inputs (no cost, no direction, etc); sit out
//   • Components show the full provenance so the UI can explain decisions.
//
// V9.19.16 cap-exceeded behavior:
//   • Default: SIT OUT entirely if intended > maxBetPerTrade.
//   • BYPASS allowed for tier='super' OR manualBypass=true (manual button
//     click). Bypass falls through and lets cap clamp (downsize) instead.
//   • Logged with 'fire-bypass-cap' decision for forensics.
//
// Mode interaction table (entry mode OVERRIDES sizing mode when set):
//   sizingMode=fixed      + entryMode=dollars   → use tradingSettings.betSize
//   sizingMode=confidence + entryMode=dollars   → confLow..confHigh by conv
//   sizingMode=kelly      + entryMode=dollars   → kellyBlend mix
//   sizingMode=mission    + entryMode=dollars   → mission cluster sizing
//   any sizingMode        + entryMode=contracts → ignore sizing, use N×cost
//   any sizingMode        + entryMode=percent   → ignore sizing, balance×pct
// ═══════════════════════════════════════════════════════════════════════════
const computeAutoExecSize=(inputs)=>{
  const {
    sizingMode='fixed',           // 'fixed'|'confidence'|'kelly'|'mission'
    entryMode='dollars',          // 'dollars'|'contracts'|'percent'
    tradingBetSize=10,            // tradingSettings.betSize
    confidenceLowBet=5,
    confidenceHighBet=25,
    kellyBlend=0,                 // 0-100
    entryContracts=5,
    entryPercentBalance=10,       // 0-100
    maxBetPerTrade=25,            // cap in $
    // Market context
    costPerContractCents=0,       // current cost per contract on our side (1-99)
    rawProbAbove=50,              // analysis posterior 0-100 (for Kelly)
    dir=null,                     // 'UP'|'DOWN'
    conviction=0,                 // 0-50 (for confidence mode)
    kalshiBalance=0,              // for percent mode
    // Mission mode (only used if sizingMode='mission')
    missionResult=null,           // {ok, betDollars, kellyFrac, ...} from upstream
    // Bypass flags
    tier='single',                // 'super'|'confluence'|'tape'|'structural'|'single'
    manualBypass=false,           // true when manual button clicked
  }=inputs||{};

  // Sentinel: bad inputs
  if(!dir||(dir!=='UP'&&dir!=='DOWN'))return{
    decision:'sit-out-bad',reason:'no direction',
    intendedDollars:0,intendedContracts:0,capExceeded:false,
    components:{sizingModeUsed:sizingMode,entryModeUsed:entryMode,sizingModeOverridden:false,rawSizingDollars:0,afterEntryModeDollars:0,capDollars:maxBetPerTrade,actualDollarsPostCap:0,bypassUsed:false},
  };
  if(!Number.isFinite(costPerContractCents)||costPerContractCents<=0||costPerContractCents>=100)return{
    decision:'sit-out-bad',reason:`bad cost-per-contract ${costPerContractCents}¢`,
    intendedDollars:0,intendedContracts:0,capExceeded:false,
    components:{sizingModeUsed:sizingMode,entryModeUsed:entryMode,sizingModeOverridden:false,rawSizingDollars:0,afterEntryModeDollars:0,capDollars:maxBetPerTrade,actualDollarsPostCap:0,bypassUsed:false},
  };

  // ── Step 1: SIZING — figure out base $ amount from sizing mode ──
  let rawSizingDollars=Math.max(0,Number(tradingBetSize)||0);
  if(sizingMode==='fixed'){
    rawSizingDollars=Math.max(0,Number(tradingBetSize)||0);
  }else if(sizingMode==='confidence'){
    // Linear interp: conviction 5 → low, conviction 35+ → high
    const _convClamped=Math.max(5,Math.min(35,Number(conviction)||0));
    const _frac=(_convClamped-5)/30;
    rawSizingDollars=Number(confidenceLowBet)+(Number(confidenceHighBet)-Number(confidenceLowBet))*_frac;
    // Floor at fixed bet so confidence mode never goes below user's declared stake
    rawSizingDollars=Math.max(Number(tradingBetSize)||0,rawSizingDollars);
  }else if(sizingMode==='kelly'){
    // Kelly fraction f* = (bp - q) / b for binary outcomes
    const _p=(dir==='UP'?(Number(rawProbAbove)||50):100-(Number(rawProbAbove)||50))/100;
    const _q=1-_p;
    const _b=(100-costPerContractCents)/Math.max(1,costPerContractCents);
    const _kelly=(_b*_p-_q)/Math.max(0.0001,_b);
    const _kellyFrac=Math.max(0,Math.min(1,_kelly));
    const _kellyBet=Number(confidenceHighBet)*_kellyFrac;
    // Blend with fixed bet via slider 0-100
    const _blend=Math.max(0,Math.min(100,Number(kellyBlend)||0))/100;
    const _fixedBet=Number(tradingBetSize)||0;
    const _mixed=_fixedBet*(1-_blend)+_kellyBet*_blend;
    rawSizingDollars=Math.max(_fixedBet,_mixed);
  }else if(sizingMode==='mission'){
    // Mission mode result is computed upstream (needs trade-log/cluster lookup
    // that this pure function can't do). Caller passes missionResult; if it
    // says don't fire, we propagate that. Otherwise we use its betDollars.
    if(missionResult&&missionResult.ok===false){
      return{
        decision:'sit-out-bad',reason:missionResult.reason||'mission veto',
        intendedDollars:0,intendedContracts:0,capExceeded:false,
        components:{sizingModeUsed:'mission',entryModeUsed:entryMode,sizingModeOverridden:false,rawSizingDollars:0,afterEntryModeDollars:0,capDollars:maxBetPerTrade,actualDollarsPostCap:0,bypassUsed:false,missionInfo:missionResult},
      };
    }
    rawSizingDollars=Number(missionResult?.betDollars)||Number(tradingBetSize)||0;
  }

  // ── Step 2: ENTRY MODE override (if set) ──
  // V9.19.22: mission mode is sovereign. When mission is active, its cluster-WR
  //   sizing math has bankroll-aware logic that contract-count or percent
  //   overrides would silently throw away. Skip entry-mode override for mission.
  let afterEntryModeDollars=rawSizingDollars;
  let sizingModeOverridden=false;
  if(sizingMode==='mission'){
    // Mission's bet is final — no entry-mode override.
  }else if(entryMode==='contracts'){
    const _wantContracts=Math.max(1,Math.min(500,Number(entryContracts)||5));
    afterEntryModeDollars=(_wantContracts*costPerContractCents)/100;
    sizingModeOverridden=true;
  }else if(entryMode==='percent'){
    const _pct=Math.max(0,Math.min(100,Number(entryPercentBalance)||10))/100;
    afterEntryModeDollars=(Number(kalshiBalance)||0)*_pct;
    sizingModeOverridden=true;
  }

  // ── Step 3: cap check (V9.19.16 wall behavior, with super/manual bypass) ──
  const cap=Math.max(0,Number(maxBetPerTrade)||0);
  const capExceeded=afterEntryModeDollars>cap;
  const bypassAllowed=NO_RISK_RAILS||tier==='super'||manualBypass===true;

  // Default: sit out if over cap
  if(capExceeded&&!bypassAllowed){
    return{
      decision:'sit-out-cap',
      reason:`intended $${afterEntryModeDollars.toFixed(2)} exceeds cap $${cap.toFixed(2)}`,
      intendedDollars:afterEntryModeDollars,
      intendedContracts:Math.max(1,Math.floor((afterEntryModeDollars*100)/costPerContractCents)),
      capExceeded:true,
      components:{
        sizingModeUsed:sizingMode,entryModeUsed:entryMode,sizingModeOverridden,
        rawSizingDollars,afterEntryModeDollars,capDollars:cap,
        actualDollarsPostCap:0,bypassUsed:false,
        costPerContractCents,tier,
      },
    };
  }

  // Bypass path: super-confluence or manual — clamp instead of refuse
  let finalDollars=afterEntryModeDollars;
  let bypassUsed=false;
  if(capExceeded&&bypassAllowed){
    finalDollars=cap;
    bypassUsed=true;
  }

  // Floor at $1; below that, sit out
  if(finalDollars<1){
    return{
      decision:'sit-out-tiny',
      reason:`final $${finalDollars.toFixed(2)} below $1 floor`,
      intendedDollars:finalDollars,intendedContracts:0,capExceeded:false,
      components:{
        sizingModeUsed:sizingMode,entryModeUsed:entryMode,sizingModeOverridden,
        rawSizingDollars,afterEntryModeDollars,capDollars:cap,
        actualDollarsPostCap:0,bypassUsed,
        costPerContractCents,tier,
      },
    };
  }

  // Compute final contract count (floor — matches kalshiBuildOrder math)
  const intendedContracts=Math.max(1,Math.min(250,Math.floor((finalDollars*100)/costPerContractCents)));

  return{
    decision:bypassUsed?'fire-bypass-cap':'fire',
    reason:bypassUsed
      ?`bypass: ${tier==='super'?'super-confluence tier':'manual click'} — clamped to cap`
      :'within cap',
    intendedDollars:finalDollars,
    intendedContracts,
    capExceeded,
    components:{
      sizingModeUsed:sizingMode,entryModeUsed:entryMode,sizingModeOverridden,
      rawSizingDollars,afterEntryModeDollars,capDollars:cap,
      actualDollarsPostCap:finalDollars,bypassUsed,
      costPerContractCents,tier,
    },
  };
};

// ═══════════════════════════════════════════════════════════════════════════
// V10.0.0 — TARA'S TRADE (Phase 4) — TIMING ENGINE SPEC
// ═══════════════════════════════════════════════════════════════════════════
//
// SPEC STATUS: locked 2026-05-14. This block is the contract. Sessions 2+
//   implement against this spec. Changes require a Session-1-style discussion.
//
// WHAT IT IS
//   A timing engine that evaluates each Tara's Call lock at the moment of
//   decision and outputs fire-now / wait / abort with reasoning. It sits
//   between Tara's Call (existing — decides direction) and the Kalshi POST
//   (existing — places the order). It does NOT decide direction; it decides
//   whether THIS exact instant is the right entry for an already-decided
//   direction call.
//
// PROBLEM IT SOLVES
//   Audit of 642 resolved trades (May 14 2026) showed:
//     • Normal commits (real conviction):     N=107  WR=78.5%
//     • Early locks (signal fired fast):      N=213  WR=65.7%
//     • Time-cap-commits (deadline forced):   N=322  WR=62.4%  ← problem
//   The "what" decision (Tara's Call) is okay. The "when" decision (always
//   fire immediately after lock) is wrong. Phase 4 separates "when" out.
//
// GOAL
//   Lift auto-fire WR from current ~66% lifetime to 75-80%. Volume drop is
//   the accepted cost.
//
// INPUTS (single inputs object — function is pure, no React state):
//   call: {
//     direction: 'UP' | 'DOWN',
//     tier: 'super' | 'confluence' | 'structural' | 'tape' | 'single' |
//           'time-cap-commit' | 'timer-commit' | 'forced',
//     posterior: 0-100,
//     qScore: 0-100,
//     qScoreV2: 0-100 | null,        // shadow score (V9.19.10+)
//     lockType: 'early' | 'normal' | 'time-cap',
//     secondsIntoWindow: 0-900,      // when lock fired
//   }
//   market: {
//     kalshiYes: 1-99,               // current our-side price (cents)
//     kalshiYes30sAgo: 1-99 | null,  // 30s-ago price (velocity)
//     kalshiSpread: 0-50,            // bid-ask cents
//     spotPrice: number,             // current asset price
//     spotDrift1m: number,           // bps drift last 1m
//     spotDrift10s: number,          // bps drift last 10s
//     tapeBuySell30s: 0-1,           // buy ratio last 30s
//     whaleNetUSD30s: number,        // signed whale flow last 30s
//     orderBookImbalance: -1 to 1,   // asks heavy → negative
//     liqLongUSD: number,            // shorts liquidation level
//     liqShortUSD: number,           // longs liquidation level
//     obDepthLive: -1 to 1 | null,   // V134 live 2.5s depth flash if fresh
//     futuresBasisPct: number,       // basis (futures - spot) / spot
//     binanceLeadBps: number,        // V114 Binance vs Coinbase lead
//   }
//   window: {
//     secondsRemaining: 0-900,
//     strikePrice: number,
//     distanceBps: number,           // bps from spot to strike
//     distanceFavorable: boolean,    // does current price favor the call dir?
//     amplitudeBps: number,          // bps range covered so far in window
//   }
//   history: {
//     similarWindowsN: integer,      // count of analogous past windows
//     similarWindowsWR: 0-1,         // their WR
//     recentRegimeWR: 0-1,           // last N trades in this regime
//     sessionWR: 0-1,                // this session-of-day WR
//     coldStreak: boolean,           // recent loss cluster in this regime?
//   }
//   fgt: {
//     alignment: -4.0 to +4.0,       // signed MTF alignment score
//     detail: { '1m','5m','15m' },   // signed direction per timeframe
//     htfDominantDir: 'UP'|'DOWN'|'MIXED',
//     htfConfluence: 0-1,            // confluence strength
//   }
//   exec: {
//     edgePt: number,                // Tara conv - Kalshi conv on her side
//     sizingDecision: 'fire'|'sit-out-cap'|'sit-out-tiny'|'sit-out-bad',
//     intendedDollars: number,
//   }
//
// OUTPUT
//   {
//     decision: 'fire-now' | 'wait' | 'abort',
//     score: 0-100,                  // confidence in the decision
//     reasons: [
//       { factor: string, impact: signed-number, note: string },
//       ...
//     ],
//     // 'wait' only:
//     waitForCondition: string | null,  // e.g. 'kalshiBelow55', 'tapeConfirm'
//     waitMaxSec: 30,                   // HARD CAP — re-eval every 5s, force
//                                       // fire-or-abort decision after this
//     waitStartedAt: timestamp | null,
//   }
//
// WAIT STATE MACHINE
//   Phase 4 returns 'wait' → caller stores waitContext = {condition, maxSec,
//   startedAt}. Re-evaluates every 5s:
//     • Condition met early       → fire-now (proceed to sizing + POST)
//     • Lock flipped/invalidated  → abort
//     • Window expires            → abort
//     • 30s elapsed (max)         → force re-decide as fire-or-abort ONLY,
//                                   no second wait, no wait→wait loops
//
// INTEGRATION POINT
//   Inside auto-exec entry effect, AFTER all V9.19.x soft filters pass
//   (tier, quality, conviction, marginal caution, urgency, edge, timecap),
//   BEFORE the V9.19.16 sizing decision.
//
//   Existing gates → V10.0.0 evaluateTradeTiming() → sizing → Kalshi POST
//
// SETTINGS
//   autoExecSettings.tradeTimingMode = 'off' | 'shadow' | 'advisory' | 'pregate'
//     off       — Phase 4 disabled, V9.19.x behavior unchanged
//     shadow    — runs on every lock, logs to CSV, does NOT affect trades.
//                 DEFAULT initial mode for data collection.
//     advisory  — Phase 4 decision displayed in UI but doesn't block.
//     pregate   — Phase 4 is live, blocks/delays per its decision.
//                 Manual click bypasses (same _bypassSoftFilters pattern).
//
// FAILURE MODES
//   shadow / advisory  → fail-OPEN. Log error, allow auto-exec to fire normally.
//   pregate            → fail-CLOSED. Abort the trade, emit
//                        autoOrderState 'sit-out-phase4-error'.
//
// CSV LOGGING (added to taraCallLog entries):
//   tradeTimingDecision  — 'fire-now' | 'wait' | 'abort' | 'error'
//   tradeTimingScore     — 0-100
//   tradeTimingReason    — concatenated reason summary (first 3 factors)
//   tradeTimingWaitMs    — how long we waited (0 for fire-now/abort)
//
// BUILD PLAN
//   Session 1 (DONE) — this spec.
//   Session 2 — implement evaluateTradeTimingV1 + wire shadow mode + CSV log.
//   Session 3 — analyze shadow data after 30+ trades, tune thresholds.
//   Session 4 — promote to advisory + pregate behind toggle.
//   Session 5+ — A/B test live vs off, iterate.
//
// CONSTRAINTS / NON-NEGOTIABLES
//   • Pure function — no React state inside evaluateTradeTimingV1
//   • Manual click ALWAYS bypasses (consistent with V9.19.x soft-filter pattern)
//   • Cap-exceeded sit-outs (V9.19.16) still apply AFTER Phase 4
//   • Edge filter (V9.19.24) still applies BEFORE Phase 4
//   • Time-cap filter (V9.19.26) still applies BEFORE Phase 4
//   • Manual button bypasses Phase 4 in pregate mode (via _bypassSoftFilters)
//   • The four gates form a layered defense, not redundancy
// ═══════════════════════════════════════════════════════════════════════════

// ── V10.0.0 evaluateTradeTimingV1 — SHADOW IMPLEMENTATION ─────────────────
// Pure function. No React state. Given the inputs spec above, returns a
// {decision, score, reasons, waitForCondition, waitMaxSec, waitStartedAt}
// object. Session 2 builds the scoring; Session 3 tunes thresholds against
// shadow data; Session 4 promotes to live.
//
// Scoring model: additive from neutral 50, clamped 0-100. Every factor
// contributes a signed impact with a human-readable note. This is intentional
// — interpretability is required so Session 3 can audit factor contributions
// against outcomes and rebalance weights.
//
// Decision thresholds (V1 starting values — WILL be tuned):
//   score >= 70  → 'fire-now'
//   40 <= score < 70 → 'wait'  (something specific should improve)
//   score < 40   → 'abort'
//
// Defensive defaults — function never throws. Missing/garbage inputs
// degrade to neutral contributions; never crash the auto-exec path.
const evaluateTradeTimingV1=(inputs)=>{
  // Defensive: bail to a neutral 'wait' if inputs are totally broken
  if(!inputs||typeof inputs!=='object'){
    return{decision:'wait',score:50,reasons:[{factor:'inputs',impact:0,note:'missing or invalid inputs object'}],waitForCondition:null,waitMaxSec:30,waitStartedAt:null};
  }
  const call=inputs.call||{};
  const market=inputs.market||{};
  const window=inputs.window||{};
  const history=inputs.history||{};
  const fgt=inputs.fgt||{};
  const exec=inputs.exec||{};

  // Helpers
  const _num=(x,fb=0)=>{const n=Number(x);return Number.isFinite(n)?n:fb;};
  const _dir=call.direction==='UP'?1:call.direction==='DOWN'?-1:0;
  // If we don't even know direction, abort — nothing to time
  if(_dir===0){
    return{decision:'abort',score:0,reasons:[{factor:'direction',impact:-50,note:'no direction call'}],waitForCondition:null,waitMaxSec:0,waitStartedAt:null};
  }

  const reasons=[];
  let score=50;
  const _bump=(factor,impact,note)=>{
    if(!Number.isFinite(impact))return;
    score+=impact;
    reasons.push({factor,impact:Math.round(impact*10)/10,note});
  };

  // ── FACTOR 1: CALL QUALITY (tier + lockType) — max ±20 ──────────────────
  // Audit-backed: time-cap-commit WR=62.4%, normal=78.5%, early=65.7%.
  // The lock type is the strongest single predictor.
  const _tier=String(call.tier||'').toLowerCase();
  if(_tier==='super')_bump('tier',+12,'super-confluence tier (lifetime WR 75%)');
  else if(_tier==='confluence')_bump('tier',+10,'confluence tier (lifetime WR 80%)');
  else if(_tier==='structural')_bump('tier',+4,'structural-led tier (lifetime WR 60%)');
  else if(_tier==='tape')_bump('tier',+6,'tape-led tier (lifetime WR 68%)');
  else if(_tier==='single')_bump('tier',-2,'single-signal tier — marginal');
  else if(_tier==='time-cap-commit'){
    // V10.4.5 — REGIME-AWARE TIME-CAP PENALTY
    // Audit: time-cap locks behave differently per regime.
    //   TRENDING DOWN sec 60-120: 76% WR (+$0.36) → don't penalize as hard
    //   RANGE-CHOP sec 60-120: 50% WR (-$0.01) → keep penalty
    //   TRENDING UP late: 20% WR → penalize more
    //   HIGH VOL CHOP: noisy → penalize more
    const _regime=String(call.regime||'').toUpperCase();
    const _penalty=
      _regime==='TRENDING DOWN'?-6:    // late locks here actually win
      _regime==='TRENDING UP'?-10:     // V10.6.4: was -15 — softer
      _regime==='HIGH VOL CHOP'?-10:   // V10.6.4: was -15 — softer
      -10;                              // V10.6.4: default was -12, now -10 (overall softer)
    _bump('tier',_penalty,`time-cap-commit · ${_regime||'?'} → ${_penalty>0?'+':''}${_penalty}pt (V10.4.5 regime-aware, V10.6.4 softened)`);
  }
  else if(_tier==='timer-commit')_bump('tier',-8,'timer-commit — ETA expired (lifetime WR ~64%)');
  else if(_tier==='forced')_bump('tier',-1,'user-forced lock (lifetime WR 67%)');
  else _bump('tier',0,`unknown tier '${_tier||'(empty)'}'`);

  // Late-window locks degrade further — confirms even non-time-cap late locks
  const _secInto=_num(call.secondsIntoWindow);
  if(_secInto>=180&&_tier!=='time-cap-commit'&&_tier!=='timer-commit'){
    _bump('lateness',-3,`locked late at ${Math.round(_secInto)}s into window`);
  } else if(_secInto>0&&_secInto<60){
    _bump('lateness',+2,`early lock at ${Math.round(_secInto)}s — high conviction signal`);
  }

  // ── FACTOR 2: EDGE — max ±15 ────────────────────────────────────────────
  // The May 14 audit's central finding. V9.19.24 already filters >+15pt, so
  // most surviving trades are in the +0 to +15 range. Reward small-to-mid
  // positive edge and contrarian negative edge.
  const _edge=_num(exec.edgePt,null);
  if(_edge!==null&&Number.isFinite(_edge)){
    if(_edge<-10)_bump('edge',+12,`contrarian gem: Kalshi ${Math.abs(Math.round(_edge))}pt ahead of Tara (audit WR 85%)`);
    else if(_edge<0)_bump('edge',+8,`mild contrarian: Kalshi ${Math.abs(Math.round(_edge))}pt ahead (audit WR 75%)`);
    else if(_edge<=10)_bump('edge',+5,`sweet spot edge +${Math.round(_edge)}pt (audit WR 70%)`);
    else if(_edge<=15)_bump('edge',+1,`acceptable edge +${Math.round(_edge)}pt`);
    else if(_edge<=25)_bump('edge',-5,`high edge +${Math.round(_edge)}pt — market may have priced this`);
    else _bump('edge',-12,`extreme edge +${Math.round(_edge)}pt — likely overconfident vs market`);
  } else {
    _bump('edge',0,'edge not computable');
  }

  // ── FACTOR 3: TAPE ALIGNMENT — max ±10 ──────────────────────────────────
  const _tapeBS=_num(market.tapeBuySell30s,0.5);
  // Tape favors UP when buy ratio > 0.5; favors DOWN when < 0.5
  const _tapeFavor=_dir===1?(_tapeBS-0.5)*2:(0.5-_tapeBS)*2; // -1 to +1
  if(_tapeFavor>0.3)_bump('tape',+8,`tape ${Math.round(_tapeBS*100)}% ${_dir===1?'buy':'sell'} — confirms ${call.direction}`);
  else if(_tapeFavor>0.1)_bump('tape',+4,`tape mildly supports ${call.direction}`);
  else if(_tapeFavor<-0.3)_bump('tape',-8,`tape ${Math.round((1-_tapeBS)*100)}% ${_dir===1?'sell':'buy'} — fights ${call.direction}`);
  else if(_tapeFavor<-0.1)_bump('tape',-4,`tape mildly against ${call.direction}`);
  else _bump('tape',0,'tape neutral');

  // Whale net flow last 30s — same direction logic
  const _whale=_num(market.whaleNetUSD30s,0);
  const _whaleFavor=_dir===1?_whale:-_whale; // signed in trade direction
  if(_whaleFavor>500000)_bump('whales',+5,`whales aligned (+$${Math.round(_whaleFavor/1000)}K in dir)`);
  else if(_whaleFavor<-500000)_bump('whales',-5,`whales against (${Math.round(_whaleFavor/1000)}K dir)`);
  // small whale flows ignored

  // ── FACTOR 4: FGT ALIGNMENT — max ±10 ───────────────────────────────────
  const _fgtAlign=_num(fgt.alignment,0);
  const _fgtFavor=_dir===1?_fgtAlign:-_fgtAlign;
  if(_fgtFavor>=2)_bump('fgt',+8,`FGT strongly confirms ${call.direction} (align ${_fgtAlign.toFixed(1)})`);
  else if(_fgtFavor>=1)_bump('fgt',+4,`FGT supports ${call.direction}`);
  else if(_fgtFavor<=-2)_bump('fgt',-8,`FGT strongly fights ${call.direction}`);
  else if(_fgtFavor<=-1)_bump('fgt',-4,`FGT mildly against ${call.direction}`);
  // Per-timeframe disagreement penalty — when 1m and 15m point opposite ways
  const _detail=fgt.detail||{};
  const _tf1=_num(_detail['1m']),_tf15=_num(_detail['15m']);
  if(_tf1>0&&_tf15<0||_tf1<0&&_tf15>0)_bump('mtf',-3,'1m and 15m disagree — chop risk');

  // ── FACTOR 5: ORDER-BOOK / LIQUIDATIONS — max ±10 ────────────────────────
  const _obImbal=_num(market.orderBookImbalance,0);
  // Positive = bids heavy (UP pressure); negative = asks heavy (DOWN pressure)
  const _obFavor=_dir===1?_obImbal:-_obImbal;
  if(_obFavor>0.4)_bump('orderbook',+6,'depth imbalance heavily favors direction');
  else if(_obFavor>0.2)_bump('orderbook',+3,'depth supports direction');
  else if(_obFavor<-0.4)_bump('orderbook',-6,'depth imbalance fights direction');
  else if(_obFavor<-0.2)_bump('orderbook',-3,'depth mildly against direction');

  // V134 live depth flash — when fresh, doubles confidence if it agrees
  const _obLive=market.obDepthLive;
  if(_obLive!=null&&Number.isFinite(_obLive)){
    const _liveFavor=_dir===1?_obLive:-_obLive;
    if(Math.sign(_liveFavor)===Math.sign(_obFavor)&&Math.abs(_liveFavor)>0.2){
      _bump('depth-live',+3,'live depth flash agrees with snapshot');
    } else if(Math.sign(_liveFavor)!==Math.sign(_obFavor)&&Math.abs(_liveFavor)>0.2){
      _bump('depth-live',-4,'live depth flash diverges — fast-moving book');
    }
  }

  // Liquidation magnets — large stop clusters above/below pull price
  const _liqLong=_num(market.liqLongUSD,0); // shorts above (UP magnet)
  const _liqShort=_num(market.liqShortUSD,0); // longs below (DOWN magnet)
  if(_dir===1&&_liqLong>2*_liqShort&&_liqLong>500000){
    _bump('liq-magnet',+3,'large short-liq cluster above — UP magnet');
  } else if(_dir===-1&&_liqShort>2*_liqLong&&_liqShort>500000){
    _bump('liq-magnet',+3,'large long-liq cluster below — DOWN magnet');
  }

  // ── FACTOR 6: KALSHI VELOCITY — max ±8 ───────────────────────────────────
  const _yesNow=_num(market.kalshiYes,null);
  const _yes30=_num(market.kalshiYes30sAgo,null);
  if(_yesNow!=null&&_yes30!=null){
    const _yesDelta=_yesNow-_yes30;
    const _yesFavor=_dir===1?_yesDelta:-_yesDelta;
    if(_yesFavor>=8)_bump('k-velocity',+6,`Kalshi YES moved ${_yesDelta>0?'+':''}${Math.round(_yesDelta)}¢ in 30s — favors direction`);
    else if(_yesFavor>=3)_bump('k-velocity',+3,`Kalshi YES drifting toward direction`);
    else if(_yesFavor<=-8)_bump('k-velocity',-6,`Kalshi YES moved ${Math.round(_yesDelta)}¢ in 30s — against direction`);
    else if(_yesFavor<=-3)_bump('k-velocity',-3,`Kalshi YES drifting against direction`);
  }

  // ── FACTOR 7: SPOT MOMENTUM — max ±8 ────────────────────────────────────
  const _drift10s=_num(market.spotDrift10s,0);
  const _drift1m=_num(market.spotDrift1m,0);
  const _drift10sFavor=_dir===1?_drift10s:-_drift10s;
  const _drift1mFavor=_dir===1?_drift1m:-_drift1m;
  if(_drift10sFavor>=3&&_drift1mFavor>=2)_bump('momentum',+6,'spot momentum (10s + 1m) confirms direction');
  else if(_drift10sFavor>=2)_bump('momentum',+3,'spot moving toward direction (last 10s)');
  else if(_drift10sFavor<=-3&&_drift1mFavor<=-2)_bump('momentum',-6,'spot momentum fights direction');
  else if(_drift10sFavor<=-2)_bump('momentum',-3,'spot drifting against direction (last 10s)');

  // V114: Binance lead — when futures diverge significantly, coinbase will follow
  const _bnLead=_num(market.binanceLeadBps,0);
  const _bnFavor=_dir===1?_bnLead:-_bnLead;
  if(_bnFavor>=3)_bump('futures-lead',+2,'Binance futures lead favors direction');
  else if(_bnFavor<=-3)_bump('futures-lead',-2,'Binance futures lead against direction');

  // Futures basis — extreme basis is mean-reversion signal
  const _basis=_num(market.futuresBasisPct,0);
  if(Math.abs(_basis)>0.0015){
    // Positive basis (contango) over 0.15% is mild bearish bias; negative is bullish
    const _basisFavor=_dir===1?-_basis:_basis;
    if(_basisFavor>0.001)_bump('basis',+1,'futures basis hints mean-reversion in direction');
    else _bump('basis',-1,'futures basis hints against direction');
  }

  // ── FACTOR 8: WINDOW STATE — max ±7 ──────────────────────────────────────
  const _secsLeft=_num(window.secondsRemaining,300);
  const _distBps=_num(window.distanceBps,null);
  const _distFav=!!window.distanceFavorable;
  // Less time remaining = lower chance of move materializing
  // V13.4.81: TIME RAMP. The old ladder was inverted against measured edge. Direction-aware
  //   EV/contract by time-left at lock (n=1161 timed locks, full archive):
  //     <120s +9.84 | 120-300s +7.29 | 300-480s +3.48 | 480-700s +3.50 | >=700s +0.06
  //   Old rungs PENALIZED the best zone (-4 under 90s) and REWARDED the dead one (+2 over
  //   600s) on the theory that a move needs time to develop. What actually matters is how
  //   much is already known: near expiry, distance-to-strike has largely resolved the
  //   question. Tara gets exactly ONE lock per window (4186 locks / 4186 windows), so
  //   spending it early burns the shot in the zone with no edge. Robustness: the gradient
  //   holds in both chronological halves and inside a fixed 50-65c cost band, so it is not
  //   a price artifact. Dial: localStorage 'taraTimeRampMult' 0..1 (default 0.5 = half
  //   strength); 'taraTimeRamp'='off' disables entirely. ASCII-only except preserved dashes.
  const _trMult=(()=>{try{if(localStorage.getItem('taraTimeRamp')==='off')return 0;const _v=parseFloat(localStorage.getItem('taraTimeRampMult'));return(Number.isFinite(_v)&&_v>=0&&_v<=1)?_v:0.5;}catch(_e){return 0.5;}})();
  const _trBump=(_pts,_msg)=>{const _p=Math.round(_pts*_trMult);if(_p!==0&&Number.isFinite(_p))_bump('time-left',_p,_msg);};
  if(_secsLeft<120)_trBump(+3,`${Math.round(_secsLeft)}s left — final stretch, outcome largely determined (+9.8c/ct zone)`);
  else if(_secsLeft<300)_trBump(+2,`${Math.round(_secsLeft)}s left, late window -- distance to strike is informative (+7.3c/ct)`);
  else if(_secsLeft>=700)_trBump(-4,`${Math.round(_secsLeft)}s left — first third has no measured edge (+0.1c/ct), needs more`);
  else if(_secsLeft>=480)_trBump(-1,`${Math.round(_secsLeft)}s left, early-mid window -- slight discount`);
  // Distance from strike — favorable side is easier to settle right
  if(_distBps!=null){
    if(_distFav&&Math.abs(_distBps)<30)_bump('strike-dist',+4,`already ${Math.round(Math.abs(_distBps))}bps favorable from strike`);
    else if(!_distFav&&Math.abs(_distBps)>50)_bump('strike-dist',-3,`${Math.round(Math.abs(_distBps))}bps adverse to strike — needs big move`);
    else if(_distFav)_bump('strike-dist',+2,'on favorable side of strike');
  }

  // ── FACTOR 9: HISTORY — max ±6 ───────────────────────────────────────────
  const _sessionWR=_num(history.sessionWR,0.6);
  const _regimeWR=_num(history.recentRegimeWR,0.6);
  const _analogWR=_num(history.similarWindowsWR,0.6);
  const _analogN=_num(history.similarWindowsN,0);

  if(_sessionWR>=0.72)_bump('session',+2,`session WR ${Math.round(_sessionWR*100)}% — favorable`);
  else if(_sessionWR<=0.50)_bump('session',-2,`session WR ${Math.round(_sessionWR*100)}% — hostile session`);

  if(_regimeWR>=0.70)_bump('regime',+2,`recent regime WR ${Math.round(_regimeWR*100)}%`);
  else if(_regimeWR<=0.45)_bump('regime',-3,`recent regime WR ${Math.round(_regimeWR*100)}% — current regime hostile`);

  // Analogs only count when sample is meaningful (n>=8)
  if(_analogN>=8){
    if(_analogWR>=0.75)_bump('analogs',+2,`${_analogN} similar windows won ${Math.round(_analogWR*100)}%`);
    else if(_analogWR<=0.45)_bump('analogs',-2,`${_analogN} similar windows lost more (${Math.round(_analogWR*100)}%)`);
  }

  // ── FACTOR 10: COLD STREAK — penalty only ────────────────────────────────
  if(history.coldStreak){
    _bump('cold-streak',-8,'recent losses in this regime — cold');
  }

  // ── DECISION ─────────────────────────────────────────────────────────────
  // Clamp + threshold
  const _finalScore=Math.max(0,Math.min(100,Math.round(score*10)/10));
  let decision,waitForCondition=null,waitMaxSec=0;
  if(_finalScore>=70)decision='fire-now';
  else if(_finalScore>=40){
    decision='wait';
    // What specifically should we wait for? Pick the most negative factor
    // and infer the condition.
    const _negs=reasons.filter(r=>r.impact<0).sort((a,b)=>a.impact-b.impact);
    const _worst=_negs[0];
    if(_worst){
      if(_worst.factor==='tape'||_worst.factor==='whales')waitForCondition='tape-confirms-direction';
      else if(_worst.factor==='k-velocity')waitForCondition='kalshi-stops-drifting-against';
      else if(_worst.factor==='momentum')waitForCondition='spot-momentum-confirms';
      else if(_worst.factor==='edge')waitForCondition='edge-normalizes';
      else if(_worst.factor==='time-left')waitForCondition=null; // can't wait, just decide
      else waitForCondition=`improvement-in-${_worst.factor}`;
    }
    waitMaxSec=waitForCondition?30:0;
    if(!waitForCondition){
      // Nothing actionable to wait for — fall through to abort
      decision='abort';
    }
  } else {
    decision='abort';
  }

  return{
    decision,
    score:_finalScore,
    reasons,
    waitForCondition,
    waitMaxSec,
    waitStartedAt:null, // set by caller when actually entering wait state
  };
};

// V134: The baseline marker is intentionally separate from the app build.
// BASELINE_VERSION tracks the baked SEED_TRADES/data migration boundary only.
// Do not bump it for ordinary fixes: the migration check compares this value
// and can offer a baseline sync (or the legacy fresh-start path) to the user.
const BASELINE_VERSION='2026.09.11-v13.4.349-real-gates-in-runentry';
// Production build marker — bump this on every shipped code change. This is the
// version shown in the UI, crash reports, peer-build checks, and new trade rows.
const TARA_BUILD_VERSION='2026.10.29-v14.7.16-live-entry-on';
const TARA_VERSION_DISPLAY='TARA 14.7.16';

// ═════════════════════════════════════════════════════════════════════════════
// V10.4.0 — CALIBRATION TABLES (regime × direction × conviction-band)
// ═════════════════════════════════════════════════════════════════════════════
// Replaces hardcoded reality caps and conviction floors with data-driven values
// pulled from the actual call log. Seeded from 885 resolved trades through
// 5/17/2026. Auto-recalibrates on every render from a rolling 200-trade window
// per cell. Cells with n<CALIBRATION_TRUST_FLOOR fall back to seed values.
// Feature-flagged so we can rollback instantly if anything goes sideways.
const USE_V104_CALIBRATION_TABLES=true; // Set false for instant rollback
// V10.7.63: Lowered trust floor from 15 → 8.
//   With 1200+ BTC 15m trades, most cells have 8-15 trades.
//   At n=15, only 3/12 cells were active. Key cell RANGE-CHOP UP 65-74 had
//   8 trades at 75% WR but seed said 53% — Tara was overcautious on her best setups.
//   At n=8, 5/12 cells active. All new cells are RANGE-CHOP (primary trading regime).
//   Recency-weighting ensures old data doesn't dominate.
const V104_CALIBRATION_TRUST_FLOOR=8; // was 15 (V10.6.4), was 20 before that
const V104_ROLLING_WINDOW=200; // Per-cell rolling window for auto-recal

// V10.4.1 — EV-AWARE DELAY GATE (Module A)
// Audit found 38% of loss $ in expensive-entry trades. Module A delays the
// lock when entry is k$50+ AND conviction sub-85% AND time remaining >180s.
// Never sit-out. Always locks eventually (deadline = 180s remaining).
// See _v10_4_1_delayed telemetry for fire rate and triggers.
// V10.6.4: loosened — fires less often. Threshold raised, conviction lowered,
//   deadline earlier so locks happen sooner.
const USE_V104_1_DELAY_GATE=true;
const V104_1_KALSHI_EXPENSIVE_THRESHOLD=55; // V10.6.4: was 50 — only truly expensive entries trigger
const V104_1_CONVICTION_STRONG_THRESHOLD=80; // V10.6.4: was 85 — 80%+ skips the delay
const V104_1_DEADLINE_SECONDS_LEFT=150; // V11.2: was 240 — more patient runway, hard-lock by 2.5min remaining
// V13.4.128: SHARED entry-band getters. Previously this exact
//   parseFloat(localStorage...)||default pattern was copy-pasted independently 15
//   times across the file (TradeCoachCall, the entry-quality gate, the V11.2
//   controller, the universal cost guard, the no-go-edge band check, the
//   time-cap-commit band check, and the manual-close band check) -- exactly the
//   kind of duplication that let the V11.2 ceiling silently drift from the guard
//   earlier tonight. One shared source now; every call site below reads this.
//   Widened per explicit user direction 2026-07-29: 30-60c was rejecting decent
//   65-75c reads (real data: a no-go-edge lock at 100c won same session) --
//   default is now 25-75c.
// V13.4.178: DEFAULTS MOVED 25-75 -> 55-70, from the live cloud log (last-1000
//   window, resolved directional calls, EV per contract at kalshiAtLock cost):
//     <55c   n=44   WR 22-43%   -12.5c/ct   (-548c total; anti-calibrated, confirms V13.4.61)
//     55-69c n=206  WR 59-71%   +1.5..+6.7  (+1,079c -- the entire profit engine lives here)
//     70-74c n=106  WR 61%      -10.6c/ct   (-1,127c -- the single biggest leak, and the old
//                                            75c ceiling let ALL of it through)
//     75-84c n=52   WR 76-79%   ~-1..-3     (slow bleed; mostly no-go-edge exempt anyway)
//     85-94c n=44   WR 95-100%  +7.7c/ct    (+339c -- ALL no-go-edge late confirms; the
//                                            edge-watch banding below keeps this island open)
//     95c+   n=13   WR 85%      -15.1c/ct   (needs >95% WR to break even -- unwinnable)
//   At 72c you need 72% WR to break even and the book wins 61% there. EV tracks COST,
//   not win rate -- the same lesson as every EV-vs-WR fix in this file, now applied to
//   the band itself. localStorage dials still win over these defaults if set.
// V13.4.249 — SIT-OUTS OFF. Tara commits to her lean instead of declining a
//   window on signal quality. Set NO_SITOUT_MODE=false to restore the old
//   behaviour in one line; nothing else needs touching.
const NO_SITOUT_MODE=false; // V14.4.1: failed quality checks must remain sit-outs.
// V13.4.326: was 420s (7 minutes) -- an execution-timing floor is legitimate
//   (a real order needs time to place and fill via the entry ladder before
//   the window stops accepting orders), but 7 minutes was never justified as
//   that: this constant's OWN prior comment cited "losses lock avg 777s vs
//   wins 744s remaining" as its reasoning, i.e. historically, LATER locks
//   (less time remaining) correlated with WINS, not losses -- the opposite
//   of what a 7-minute-early cutoff would protect against. That evidence
//   argued for allowing later locks, not restricting them. Reduced to a
//   floor sized to the actual constraint: enough time for kalshiRunEntryLadder
//   to place and fill an order (a full ladder with default settings takes
//   well under 30s), not a disguised quality/timing gate. If a lock this
//   late genuinely can't confirm its fill, V13.4.322's 'unknown' status
//   handling now surfaces that clearly instead of silently mishandling it --
//   that safety net is what makes a shorter floor safe today.
const LOCK_DEADLINE_SEC=30;
// V13.4.250 — NO GATES, per explicit instruction. Every price/EV/edge gate is
//   gone; the only case left was meant to be a physical impossibility, not a
//   decision:
//     no-go-price-unavailable-sitout -- no price, so nothing can be sized or costed
//   Committing a call here would write a log entry against a market that is
//   not there, which is data corruption rather than a trade. If that should
//   go too, empty this set.
//
//   REMOVED HERE (all of these now trade): no-go-edge, edge-sitout,
//   no-go-edge-band-sitout, no-go-edge-dead-window, v1123-ev-gate,
//   time-cap-band-sitout. See NO_ENTRY_GATES below -- the cost band itself is
//   what actually enforced most of these, in 7 separate places.
//
// V13.4.325 CORRECTION: 'kalshi-window-closed' used to sit in this set under
//   the same "physical impossibility" description above -- that was wrong.
//   Its actual trigger (see the price-threshold check further down this
//   effect) fires when the price for Tara's direction ran up past a live,
//   computed cost ceiling after being favorable earlier in the window --
//   a real contract still exists to buy, it just got more expensive. That
//   is a price/EV decision, mechanically the same kind of gate V13.4.250
//   removed everywhere else, just mislabeled. Reconstructed against a real
//   106-sample log (leaning direction inferred from posterior, validated
//   97% against actual committed directions elsewhere in the same log):
//   60.4% win rate, but the entries only qualify for this gate by having
//   already run to an average 60.6c entry cost, so the reconstructed
//   breakeven is ~60.6% too -- net -22c over 106 samples, statistically a
//   wash. Unlike 'trend-at-lock-sitout' below, this one never proved an
//   edge either way, so it does not earn a mislabeled exception. Removed;
//   it now converts to a real commit like every other non-kept sit-out.
const _SITOUT_KEEP=new Set([
  'no-go-price-unavailable-sitout',
  // V13.4.324: trend-at-lock-sitout is a DECISION gate, not a physical
  //   impossibility like the case above -- added anyway because it is the
  //   one candidate (of 18 fields tested the same way) that held up on a
  //   proper train/test split of the real call log: time-cap-commit wins
  //   46.4% when the market is trending at the moment of lock vs 71.7% when
  //   it isn't (n=141, z=2.49), same direction and magnitude in both
  //   halves. First quality gate reintroduced since V13.4.250 removed all
  //   of them; kept narrow to this one validated condition, not a reopening
  //   of the gates removed there.
  'trend-at-lock-sitout',
  // V13.4.326: tier1-only-skip is a deliberate, OPT-IN user preference
  //   (tradingSettings.tier1OnlyMode, unchecked/off by default) -- not a
  //   gate imposed on the engine, a constraint the user chooses for
  //   themselves if they turn it on. It was never in this set, so it
  //   already relied entirely on the old 420s deadline to ever actually
  //   stick (routes through _applyNoSitout same as everything else). Now
  //   that the deadline is 30s, it would have gone from "mostly broken"
  //   to "completely broken" -- added here so the feature genuinely does
  //   what its own UI says if a user ever turns it on. Zero effect while
  //   it stays off, which is the default.
  'tier1-only-skip',
]);
// V13.4.250: the entry-cost band is read at 7+ call sites (entry quality, time-cap
//   commit, the V11.2 odds ceiling, the hourly ladder, the band sit-outs). Rather
//   than unpick each one, the two getters open all the way when this is on, so
//   every band comparison passes and no site can quietly keep gating.
//
//   WHAT THIS TURNS OFF, in his own log's numbers (see the note below the getters):
//     <55c    n=44   -12.5c/ct   -548c
//     70-74c  n=106  -10.6c/ct   -1,127c   <- the band's whole reason for existing
//     95c+    n=13   -15.1c/ct
//   Set NO_ENTRY_GATES=false to restore the 55-70c band everywhere at once.
const NO_ENTRY_GATES=true;
// V13.4.251 — RISK RAILS OFF. The per-trade size cap no longer REFUSES a trade;
//   it clamps to the cap instead (the same path super-confluence already used),
//   so nothing is blocked on size any more.
//
//   IMPORTANT, and not what anyone would assume from the settings screen: the
//   other three rails were ALREADY not enforced. maxDailyLoss,
//   maxAutoTradesPerDay and autoExecCooldownUntil are written by the settings
//   UI and read by the hydrator, and are compared against NOTHING anywhere in
//   this file -- _runEntry, the rebuilt order path, references none of them.
//   The entry effect rebuilt on 2026-09-05 restored ordering without
//   re-wiring the day caps. So turning this flag off restores only the size
//   refusal; it does NOT restore a daily loss cap, because there is not one
//   to restore. Those would have to be written from scratch.
//
//   Still live and deliberately untouched: the kill switch, plus the enabled
//   and dryRun switches -- the three things that decide whether an order
//   happens at all.
const NO_RISK_RAILS=true;
const getEntryMinCost=()=>{if(NO_ENTRY_GATES)return 0;try{const v=parseFloat(localStorage.getItem('taraEntryMinCost'));return(Number.isFinite(v)&&v>=0&&v<100)?v:55;}catch(_e13428a){return 55;}};
const getEntryMaxCost=()=>{if(NO_ENTRY_GATES)return 100;try{const v=parseFloat(localStorage.getItem('taraEntryMaxCost'));return(Number.isFinite(v)&&v>0&&v<=100)?v:70;}catch(_e13428b){return 70;}};
// V13.4.180: SLIM signal telemetry for cloud sync. The V10.7.58 egress fix stripped
//   signalScoresAtLock from cloud writes entirely ("analysis reads from local") --
//   which made the learning loop's raw material localStorage-only: the V13.4.179
//   signal audit found the field on ZERO of 475 resolved cloud entries, and had to
//   run half-blind on V12 context fields instead. One browser reset = the entire
//   per-signal history gone. This keeps the 6 signals that have ever mattered
//   (weights audit V10.7.91: gap is primary, flow secondary; the rest for context),
//   rounded, ~60-90 bytes/entry vs the ~1KB the full object cost -- a deliberate,
//   bounded egress spend so future audits can measure the actual weighted signals.
// V13.4.187: THE canonical call-log entry stripper for every cloud write.
//   v13.4.180 added the slim-keep at only 2 of the call-log write paths. Verified
//   from live cloud data: entries stamped v13.4.185 carry the FULL 24-key signal
//   object (vwap, htfPatterns, liqSignal, ...) because the post-hydration flush and
//   the import flush return their merged arrays unstripped -- the exact ~1KB/entry
//   egress V10.7.58 existed to prevent. Worse, the force-push path stripped
//   signalScoresAtLock ENTIRELY, so one Force Push would wipe the signal history
//   v180 was added to preserve, for every entry in the doc at once.
//   One function, used by all of them, so the next write path cannot silently differ.
// V14.7.14: the per-window decision trace (~8.7KB) was stored twice per entry, at the
//   top level and inside originalDecision. That was 4.5 of the cloud doc's 9.5MB and
//   pushed every save past Supabase's 3s statement timeout. Nothing reads the trace
//   back from the cloud; each device keeps its own full copy in IndexedDB, and
//   normalizeCallLedger keeps a local originalDecision over a slimmer incoming one.
const _stripEntryForCloud=(e)=>{
  if(!e)return e;
  const{signalScoresAtLock:_sig,reasoning:__,decisionTrace:___,...rest}=e;
  const _slim=_slimSigsForCloud(_sig);
  if(_slim)rest.signalScoresAtLock=_slim;
  if(rest.originalDecision&&rest.originalDecision.decisionTrace!==undefined){
    const{decisionTrace:____,...od}=rest.originalDecision;
    rest.originalDecision=od;
  }
  return rest;
};
const _slimSigsForCloud=(s)=>{
  if(!s||typeof s!=='object')return undefined;
  const o={};let any=false;
  for(const k of ['gap','flow','momentum','structure','technical','regime']){
    const v=Number(s[k]);
    if(Number.isFinite(v)&&v!==0){o[k]=Math.round(v*10)/10;any=true;}
  }
  return any?o:undefined;
};

// V10.6.3 — SLIPPAGE-AWARE EV
//   Real Kalshi fills cost ~1¢ more than the lock-time YES mid price due to
//   bid-ask spread (need to cross the spread to actually fill on the ask side).
//   On a $0.50 trade, that's 2% of cost. Over 100 trades = ~$1 in pure fees
//   invisible to existing EV calculations.
//
//   This constant is used in:
//   1) V10.4.1 delay gate: effective expensive threshold is V104_1_KALSHI_EXPENSIVE_THRESHOLD
//      MINUS slippage, because the displayed kalshiYesPrice will become MORE expensive
//      at fill time.
//   2) V10.4.6 EV-weighted learning: win profit reduced by slippage, loss cost increased.
//   3) Calibration table EVs (buildV104Table, buildDowHourCalibration): cell EV now
//      reflects real $-outcome with slippage applied.
//
//   Conservative estimate of 1.0¢ for 15m BTC markets. Refine when we have fill data.
const V106_3_AVG_SLIPPAGE_CENTS=1.0;

// Seed table from full 885-trade resolved log. Format: WR (0-1), EV (cents/trade), n (sample size)
// Cells with n=0 are unobserved — engine falls back to legacy logic for those.
// V10.7.86c: V104_SEED_CALIBRATION rebuilt from 1,678 empirical BTC trades.
// Replaces old small-sample seeds (most cells had n<30). Now every cell with
// n>=8 uses observed WR directly. Cells with n<8 use conservative defaults.
// Key findings baked in:
//   - High conviction (75-84%) is OVERCONFIDENT: actual WR 56-65%, not 77%+
//   - RANGE-CHOP|DOWN|<45 wins 64% despite low conviction (Kalshi leads down)
//   - TRENDING DOWN|DOWN|55-64 wins 88% (strongest cell in dataset)
//   - HIGH VOL CHOP|DOWN|65-74+ wins 84-86% (excellent regime for DOWN calls)
//   - SHORT SQUEEZE|UP|85+ only 64% WR (market already priced the squeeze)
const V104_SEED_CALIBRATION={
  'RANGE-CHOP':{
    'UP':{'<45':{wr:0.450,ev:0,n:5},'45-54':{wr:0.667,ev:0,n:12},'55-64':{wr:0.576,ev:0,n:191},'65-74':{wr:0.516,ev:0,n:62},'75-84':{wr:0.640,ev:0,n:86},'85+':{wr:0.800,ev:0,n:10}},
    'DOWN':{'<45':{wr:0.642,ev:0,n:299},'45-54':{wr:0.486,ev:0,n:74},'55-64':{wr:0.452,ev:0,n:31},'65-74':{wr:0.597,ev:0,n:67},'75-84':{wr:0.558,ev:0,n:95},'85+':{wr:0.750,ev:0,n:7}}
  },
  'TRENDING DOWN':{
    'UP':{'<45':{wr:0.450,ev:0,n:0},'45-54':{wr:0.500,ev:0,n:0},'55-64':{wr:0.500,ev:0,n:0},'65-74':{wr:0.500,ev:0,n:4},'75-84':{wr:0.700,ev:0,n:1},'85+':{wr:0.500,ev:0,n:0}},
    'DOWN':{'<45':{wr:0.450,ev:0,n:4},'45-54':{wr:0.556,ev:0,n:9},'55-64':{wr:0.882,ev:0,n:17},'65-74':{wr:0.725,ev:0,n:69},'75-84':{wr:0.602,ev:0,n:186},'85+':{wr:0.717,ev:0,n:60}}
  },
  'SHORT SQUEEZE':{
    'UP':{'<45':{wr:0.450,ev:0,n:1},'45-54':{wr:0.500,ev:0,n:1},'55-64':{wr:0.636,ev:0,n:33},'65-74':{wr:0.688,ev:0,n:48},'75-84':{wr:0.650,ev:0,n:117},'85+':{wr:0.636,ev:0,n:22}},
    'DOWN':{'<45':{wr:0.450,ev:0,n:0},'45-54':{wr:0.500,ev:0,n:0},'55-64':{wr:0.580,ev:0,n:2},'65-74':{wr:0.670,ev:0,n:7},'75-84':{wr:0.700,ev:0,n:6},'85+':{wr:0.750,ev:0,n:1}}
  },
  'TRENDING UP':{
    'UP':{'<45':{wr:0.450,ev:0,n:2},'45-54':{wr:0.500,ev:0,n:4},'55-64':{wr:0.462,ev:0,n:13},'65-74':{wr:0.667,ev:0,n:18},'75-84':{wr:0.680,ev:0,n:50},'85+':{wr:0.750,ev:0,n:7}},
    'DOWN':{'<45':{wr:0.450,ev:0,n:0},'45-54':{wr:0.500,ev:0,n:0},'55-64':{wr:0.500,ev:0,n:0},'65-74':{wr:0.500,ev:0,n:0},'75-84':{wr:0.500,ev:0,n:0},'85+':{wr:0.500,ev:0,n:1}}
  },
  'HIGH VOL CHOP':{
    'UP':{'<45':{wr:0.450,ev:0,n:1},'45-54':{wr:0.500,ev:0,n:0},'55-64':{wr:0.580,ev:0,n:2},'65-74':{wr:0.670,ev:0,n:2},'75-84':{wr:0.700,ev:0,n:3},'85+':{wr:0.750,ev:0,n:1}},
    'DOWN':{'<45':{wr:0.450,ev:0,n:0},'45-54':{wr:0.500,ev:0,n:1},'55-64':{wr:0.580,ev:0,n:3},'65-74':{wr:0.846,ev:0,n:13},'75-84':{wr:0.857,ev:0,n:14},'85+':{wr:0.750,ev:0,n:4}}
  },
  'LONG SQUEEZE':{
    'UP':{'<45':{wr:0.450,ev:0,n:0},'45-54':{wr:0.500,ev:0,n:0},'55-64':{wr:0.580,ev:0,n:0},'65-74':{wr:0.670,ev:0,n:0},'75-84':{wr:0.700,ev:0,n:0},'85+':{wr:0.750,ev:0,n:0}},
    'DOWN':{'<45':{wr:0.450,ev:0,n:0},'45-54':{wr:0.500,ev:0,n:0},'55-64':{wr:0.580,ev:0,n:0},'65-74':{wr:0.670,ev:0,n:0},'75-84':{wr:0.700,ev:0,n:0},'85+':{wr:0.750,ev:0,n:0}}
  },
  'COMPRESSING':{
    'UP':{'<45':{wr:0.450,ev:0,n:0},'45-54':{wr:0.500,ev:0,n:0},'55-64':{wr:0.580,ev:0,n:0},'65-74':{wr:0.670,ev:0,n:0},'75-84':{wr:0.700,ev:0,n:0},'85+':{wr:0.750,ev:0,n:0}},
    'DOWN':{'<45':{wr:0.450,ev:0,n:0},'45-54':{wr:0.500,ev:0,n:0},'55-64':{wr:0.580,ev:0,n:0},'65-74':{wr:0.670,ev:0,n:0},'75-84':{wr:0.700,ev:0,n:0},'85+':{wr:0.750,ev:0,n:0}}
  }
};

// V10.7.86c: Regime caps updated from empirical data (1,678 BTC trades).
// Format: [hi-cap, lo-cap]. The hi-cap reflects the highest reliable WR
// observed in that regime at high conviction — not theoretical max.
const V104_REGIME_CAPS={
  'HIGH VOL CHOP':  {hi:86,lo:25}, // DOWN|65-74 = 85%, DOWN|75-84 = 86%
  'RANGE-CHOP':     {hi:72,lo:25}, // UP|85+ = 80%, DOWN|<45 = 64% — cap lower, regime is noisy
  'SHORT SQUEEZE':  {hi:69,lo:32}, // UP|65-74 = 69%, tighter — squeeze is overconfident at high conv
  'TRENDING DOWN':  {hi:88,lo:22}, // DOWN|55-64 = 88% — strongest single cell
  'TRENDING UP':    {hi:68,lo:25}, // UP|65-74 = 67%, UP|75-84 = 68% — limited data, conservative
  'LONG SQUEEZE':   {hi:70,lo:30}, // no empirical data — neutral defaults
  'COMPRESSING':    {hi:70,lo:30}, // no empirical data — neutral defaults
  '_default':       {hi:72,lo:25}
};

// Conviction band classifier — must match the keys in V104_SEED_CALIBRATION
function _v104ConvBand(c){
  if(c>=85)return'85+';
  if(c>=75)return'75-84';
  if(c>=65)return'65-74';
  if(c>=55)return'55-64';
  if(c>=45)return'45-54';
  return'<45';
}

// Build a rolling-window calibration table from the live call log.
// Merges with seed when cell sample is thin (<TRUST_FLOOR).
// V10.7.82b — CANONICAL TRADE FILTER
// ════════════════════════════════════════════════════════════════════════════
// Single source of truth for what counts as a real scored trade.
// Used by ALL displays: dashboard record, stats, analytics, training engine.
// Rules (in priority order):
//   1. Must have a WIN or LOSS result (not SITOUT/null)
//   2. Exclude wasOverriddenNoTrade=true (no-trade overrides, Tara chose not to trade)
//   3. Exclude tier='no-go-data' (stale price during wifi/feed issues)
//   4. Deduplicate by windowId — keep only the LAST snapshot per window
//      (aborts + re-locks create multiple entries; only the final one counts)
const _isRealTrade=(e)=>{
  return isScoredCall(e);
};

// Deduplicate entries by windowId, keeping the last committed snapshot per window.
// When no windowId: use id (timestamp) as unique key.
const _deduplicateByWindow=(entries)=>{
  const map=new Map();
  for(const e of entries){
    if(!e)continue;
    const key=(e.asset||'BTC')+'|'+(e.windowType||'15m')+'|'+(e.windowId||e.wid||String(e.id||Math.random()));
    // Keep the later entry (higher id = more recent commit)
    const existing=map.get(key);
    if(!existing||(e.id||0)>(existing.id||0)){
      map.set(key,e);
    }
  }
  return Array.from(map.values());
};

// The single canonical scored trade list from a call log array
const _canonicalTrades=(callLog)=>{
  if(!Array.isArray(callLog)||callLog.length===0)return[];
  // V10.9.28: backfill time from id. ~89% of entries never had e.time, so every
  //   analytics path that filters/sorts on e.time (walk-forward WR, recency weight,
  //   day/hour cells, P&L curve) was silently seeing only ~11% of history. e.id IS
  //   the lock timestamp; normalize once here so all downstream readers are whole.
  return _deduplicateByWindow(callLog).filter(_isRealTrade).map(e=>(e&&!e.time&&e.id)?{...e,time:e.id}:e);
};

// V13.4.353: one display-safe record authority for the control room. The
// existing scorecard already scopes to the active asset/window and excludes
// no-trade overrides; the new shell reads that same result instead of recounting
// raw log rows and quietly disagreeing with the legacy record card.
const _taraRecordStats=(scorecard)=>{
  const wins=Number(scorecard?.wins)||0;
  const losses=Number(scorecard?.losses)||0;
  const sitouts=Number(scorecard?.sitouts)||0;
  const scored=wins+losses;
  return{wins,losses,sitouts,scored,total:scored+sitouts,winRate:scored?Math.round(wins/scored*100):null};
};
// ════════════════════════════════════════════════════════════════════════════

// Returns table in the same shape as V104_SEED_CALIBRATION.
function buildV104Table(callLog,asset){
  if(!USE_V104_CALIBRATION_TABLES||!Array.isArray(callLog)||callLog.length===0){
    return V104_SEED_CALIBRATION;
  }
  // V10.7.86b: filter by asset so BTC calibration doesn't bleed into ETH and vice versa.
  //   ETH trades have different volatility/regime characteristics — mixing them produces
  //   calibration tables that fit neither asset well.
  const _asset=asset||'BTC';
  const resolved=_canonicalTrades(callLog).filter(e=>(e.asset||'BTC')===_asset);
  if(resolved.length===0)return V104_SEED_CALIBRATION;
  // V10.6.1 — RECENCY-WEIGHTED ROLLING WINDOW
  //   Old logic: count last 200 trades per cell with equal weight. A cell
  //   spanning 3 months blends ancient market conditions with current ones.
  //   New logic: each trade contributes weight = exp(-age_days/HALF_LIFE).
  //   Trades 30 days old weigh ~0.37, 60 days old weigh ~0.14, 90 days old
  //   weigh ~0.05. Cell WR/EV = weighted sums / weighted total. Raw n still
  //   tracked for trust-floor check (need ≥20 actual trades regardless of weight).
  const _RECENCY_HALF_LIFE_DAYS=30;
  const _now=Date.now();
  const _msPerDay=86400000;
  // Bucket by regime/dir/band, keep newest V104_ROLLING_WINDOW per cell
  const buckets={};
  for(let i=resolved.length-1;i>=0;i--){
    const e=resolved[i];
    if(!e.regime||!e.dir||typeof e.confidence!=='number')continue;
    const band=_v104ConvBand(e.confidence);
    const key=e.regime+'|'+e.dir+'|'+band;
    if(!buckets[key])buckets[key]={w:0,l:0,pnl:0,n:0,wSum:0,wWins:0,wPnl:0};
    if(buckets[key].n>=V104_ROLLING_WINDOW)continue;
    const k=e.kalshiAtLock||50;
    // V10.6.1 — compute recency weight
    const _tradeTime=Number(e.time)||_now;
    const _ageDays=Math.max(0,(_now-_tradeTime)/_msPerDay);
    const _weight=Math.exp(-_ageDays/_RECENCY_HALF_LIFE_DAYS);
    if(e.result==='WIN'){
      buckets[key].w++;
      // V10.6.3 — real profit = (100 - k - slippage)
      const _realProfit=Math.max(0,100-k-V106_3_AVG_SLIPPAGE_CENTS);
      buckets[key].pnl+=_realProfit;
      buckets[key].wWins+=_weight;
      buckets[key].wPnl+=_weight*_realProfit;
    }else{
      buckets[key].l++;
      // V10.6.3 — real loss = -(k + slippage)
      const _realLoss=k+V106_3_AVG_SLIPPAGE_CENTS;
      buckets[key].pnl-=_realLoss;
      buckets[key].wPnl-=_weight*_realLoss;
    }
    buckets[key].n++;
    buckets[key].wSum+=_weight;
  }
  // Compose final table: live data overrides seed when n>=TRUST_FLOOR, else seed
  const table=JSON.parse(JSON.stringify(V104_SEED_CALIBRATION)); // deep clone seed
  for(const key of Object.keys(buckets)){
    const[regime,dir,band]=key.split('|');
    const b=buckets[key];
    if(b.n>=V104_CALIBRATION_TRUST_FLOOR&&table[regime]&&table[regime][dir]){
      // V10.6.1 — use weighted WR/EV instead of raw averages
      const _wWR=b.wSum>0?b.wWins/b.wSum:b.w/b.n;
      const _wEV=b.wSum>0?b.wPnl/b.wSum:b.pnl/b.n;
      table[regime][dir][band]={wr:_wWR,ev:_wEV,n:b.n};
    }
  }
  return table;
}

// Look up a (regime, dir, displayedConf) cell. Returns {wr, ev, n, source} or null.
function lookupV104Cell(table,regime,dir,confidence){
  if(!USE_V104_CALIBRATION_TABLES||!table||!regime||!dir||typeof confidence!=='number')return null;
  const band=_v104ConvBand(confidence);
  const cell=table[regime]&&table[regime][dir]&&table[regime][dir][band];
  if(!cell)return null;
  const source=cell.n>=V104_CALIBRATION_TRUST_FLOOR?'live':'seed';
  return{wr:cell.wr,ev:cell.ev,n:cell.n,band,source};
}

// V10.6.2 — DOW × HOUR CALIBRATION TABLE
//   Audit found DoW × hour cells span 18-92% WR with sample sizes ≥10. Mon 21:00 UTC
//   is 18% WR (-$0.37/trade), Sun 20:00 UTC is 92% WR (+$0.45/trade). This is a
//   real, exploitable pattern beyond regime/session granularity. We bucket by
//   (UTC day-of-week × UTC hour) — 168 cells total. Apply ±4pt posterior shift
//   when a cell has n≥10 AND its weighted WR is meaningfully off baseline (>10pp).
//   Magnitude intentionally small (±4pt) — DoW×hour can be confounded by recent
//   session anomalies; we only tilt, never override.
const V106_2_DOWHOUR_MIN_SAMPLES=15; // V10.6.4: was 10 — require more data per cell
const V106_2_DOWHOUR_BASELINE_WR=0.60; // typical lifetime WR
const V106_2_DOWHOUR_MAX_SHIFT=3; // V10.6.4: was 4 — softer tilt
const _DOW_NAMES=['Sun','Mon','Tue','Wed','Thu','Fri','Sat'];

function buildDowHourCalibration(callLog){
  if(!Array.isArray(callLog)||callLog.length===0)return null;
  // V10.9.28: use e.time||e.id (id is the lock timestamp). Was training on ~11% of
  //   history because ~89% of entries lack e.time; now the day/hour tilt sees them all.
  const resolved=_canonicalTrades(callLog).filter(e=>e.time||e.id);
  if(resolved.length<50)return null;
  // V10.6.1-style recency weighting (30-day half-life) reused for consistency
  const _HALF_LIFE_DAYS=30;
  const _now=Date.now();
  const _msPerDay=86400000;
  const cells={};
  for(const e of resolved){
    const _ts=e.time||e.id;
    const _t=new Date(_ts);
    const _key=_t.getUTCDay()+'-'+_t.getUTCHours();
    if(!cells[_key])cells[_key]={w:0,n:0,wWins:0,wSum:0,pnl:0,wPnl:0};
    const _ageDays=Math.max(0,(_now-_ts)/_msPerDay);
    const _wt=Math.exp(-_ageDays/_HALF_LIFE_DAYS);
    const k=e.kalshiAtLock||50;
    if(e.result==='WIN'){
      cells[_key].w++;
      cells[_key].wWins+=_wt;
      // V10.6.3 — real profit = (100 - k - slippage)
      const _realProfit=Math.max(0,100-k-V106_3_AVG_SLIPPAGE_CENTS);
      cells[_key].pnl+=_realProfit;
      cells[_key].wPnl+=_wt*_realProfit;
    }else{
      // V10.6.3 — real loss = -(k + slippage)
      const _realLoss=k+V106_3_AVG_SLIPPAGE_CENTS;
      cells[_key].pnl-=_realLoss;
      cells[_key].wPnl-=_wt*_realLoss;
    }
    cells[_key].n++;
    cells[_key].wSum+=_wt;
  }
  // Finalize: keep only cells with n>=V106_2_DOWHOUR_MIN_SAMPLES
  const table={};
  for(const k of Object.keys(cells)){
    const c=cells[k];
    if(c.n<V106_2_DOWHOUR_MIN_SAMPLES)continue;
    table[k]={
      wr:c.wSum>0?c.wWins/c.wSum:c.w/c.n,
      ev:c.wSum>0?c.wPnl/c.wSum:c.pnl/c.n,
      n:c.n,
    };
  }
  return table;
}

// Look up current DoW×hour cell and return posterior shift recommendation.
//   Returns null if no cell or insufficient samples. Returns {shift, cell} when
//   there's a tradable tilt. Shift is signed pts to add to posterior (favorable
//   cells boost UP-confidence, unfavorable cells pull toward 50).
function computeDowHourShift(dowHourTable,now){
  if(!dowHourTable)return null;
  const _t=new Date(now||Date.now());
  const _key=_t.getUTCDay()+'-'+_t.getUTCHours();
  const cell=dowHourTable[_key];
  if(!cell)return null;
  // WR delta from baseline. Each 10pp = ~2pt shift, capped at ±4pt.
  const _wrDelta=cell.wr-V106_2_DOWHOUR_BASELINE_WR;
  if(Math.abs(_wrDelta)<0.10)return null; // need >10pp deviation to tilt
  const _rawShift=_wrDelta*20; // 10pp = 2pt, 20pp = 4pt
  const _shift=Math.max(-V106_2_DOWHOUR_MAX_SHIFT,Math.min(V106_2_DOWHOUR_MAX_SHIFT,_rawShift));
  return{
    shift:Math.round(_shift*10)/10,
    cellWR:cell.wr,
    cellN:cell.n,
    cellEV:cell.ev,
    dowHour:`${_DOW_NAMES[_t.getUTCDay()]} ${_t.getUTCHours()}:00`,
  };
}
// ═════════════════════════════════════════════════════════════════════════════

// V9.10.6: Maximum entries kept in taraCallLog across in-memory state, localStorage,
//   and cloud RMW. Was hardcoded 500 in 11 places — user hit the cap (BTC 463 + ETH 36
//   = 499) and new entries were silently dropping oldest one-for-one. At ~150-200
//   entries/day across BTC+ETH the new 2000 cap gives ~10-13 days of headroom. If
//   localStorage quota is exceeded the setItem try/catch swallows it and cloud sync
//   continues; on next load, hydration merges cloud→memory so nothing's lost.
// V10.7.95: Raised from 2000 → 4000. Possible because _stripForStorage now
//   drops _v10_* telemetry (77% size reduction: 5KB/entry → 1.2KB/entry).
//   At 1.2KB avg, 4000 entries = ~4.8MB which fits within Chrome's 10MB localStorage
//   limit comfortably. IndexedDB (unlimited) is the primary store; localStorage
//   is a fast-read cache only.
// V13.4.166: the working log IS the cloud window now. Previously 10000, which meant
//   the in-memory log (and therefore every surface reading it) could hold far more
//   than Supabase does -- the Memory modal showed "Total calls 2977" and Discord
//   reported an "All-time" record while the cloud held 1000. Patching each display
//   separately is exactly how those drifted apart in the first place, so the cap is
//   unified instead: one number, one window, every surface agrees by construction.
//   Set to _TARA_CLOUD_LOG_CAP just below its definition (see the assignment there);
//   this const stays for the many existing references.
//   NOTE: full history is NOT lost -- it lives in the JSON/CSV exports, and older
//   entries are simply outside the window the app displays and syncs.
const TARA_CALL_LOG_CAP=1000;

// V13.4.86: HOURLY BARRIER PROBABILITY -- empirically fitted, out-of-sample validated.
//   Answers: P(BTC closes above `strike` when the window ends), given spot now and
//   minutes remaining. This is the missing piece for fair-value / edge scanning: compare
//   this probability against Kalshi's price to find windows the market has mispriced.
//
// HOW THE CONSTANTS WERE DERIVED (from 83 days of this app's own closingPrice history,
// 5642 fifteen-minute closes, NOT assumed):
//   1. Volatility scaling measured across 8 horizons (15m..4h): sd_bps(h) = 22.84*h^0.4786
//      with R^2=0.9986. H=0.4786 vs the 0.50 that sqrt-t assumes, so BTC sub-diffuses only
//      slightly -- sqrt-t overstates the 1h move by ~3%, NOT the 3x some models assume.
//      Lag-1 autocorrelation of consecutive 15m returns: -0.0015 +/- 0.028 (no measurable
//      mean reversion). So do NOT restructure around mean reversion at these horizons.
//   2. The raw unconditional sd left the model UNDER-confident (probabilities compressed
//      toward 50%). Grid-fitting a sharpening factor gave lam=0.68 and a small negative
//      drift of -0.5 bps per 15m slot. Effective sd_bps(k) = 15.53*k^0.4786.
//   3. Validation on 4424 hourly samples: Brier 0.1848 vs 0.2500 baseline, SKILL +0.0647.
//      OUT-OF-SAMPLE (fit first half, test second): skill +0.0594 -- it HOLDS.
//      Directional accuracy by time left: 45m 67.3% | 30m 76.9% | 15m 85.0%.
//      On confident calls (>=70% or <=30%): 45m 76.8% | 30m 84.9% | 15m 92.1%.
//
// CRITICAL LIMITATION -- READ BEFORE TRUSTING ANY 'EDGE' NUMBER:
//   Being well calibrated is NOT the same as beating the market. Kalshi's own price is
//   probably about this accurate too. No historical Kalshi HOURLY prices exist in the log,
//   so model-vs-market edge has NEVER been measured. Until it is, treat output as a
//   model estimate, not a proven edge. Hourly approval requires a positive estimate
//   after fees as a consistency check; it does not establish out-of-sample returns.
//
//   At the exact window open, distance to strike is 0 so this returns ~50% and carries no
//   information (measured skill at 60m left: -0.0000). It becomes useful ~45m in.
const TARA_HOURLY_VOL_A=15.53;   // bps at k=1 slot, post-sharpening (22.84 raw * 0.68)
const TARA_HOURLY_VOL_H=0.4786;  // measured scaling exponent (0.50 = random walk)
const TARA_HOURLY_DRIFT_BPS=-0.5;// per 15m slot, fitted
const _taraNormCdf=(z)=>{
  // Abramowitz-Stegun 7.1.26 -- max abs error ~1.5e-7, far tighter than the logistic
  // 1.702z approximation whose worst error sits in the tails where conviction lives.
  if(!Number.isFinite(z))return 0.5;
  const s=z<0?-1:1,x=Math.abs(z)/Math.SQRT2;
  const t=1/(1+0.3275911*x);
  const y=1-((((1.061405429*t-1.453152027)*t+1.421413741)*t-0.284496736)*t+0.254829592)*t*Math.exp(-x*x);
  return 0.5*(1+s*y);
};
// spotNow, strike: prices. minsLeft: minutes until the window closes.
// Returns probability in [0,1] that close > strike, or null if inputs unusable.
const taraHourlyProb=(spotNow,strike,minsLeft)=>{
  const s=Number(spotNow),k0=Number(strike),m=Number(minsLeft);
  if(!(s>0)||!(k0>0)||!Number.isFinite(m)||m<0)return null;
  // V13.4.89: RTI SETTLEMENT CORRECTION, taken from the Kalshi contract rules themselves:
  //   'If the simple average of the sixty seconds of CF Benchmarks BRTI before <close> is at
  //   least the simple average of the sixty seconds of BRTI before <open>, then Yes.'
  //   Settlement is NOT the last tick -- it is the mean of the final 60 seconds, which centers
  //   at close minus 30s, and averaging removes terminal variance. So the effective horizon is
  //   (T - 30s), not T. A model using raw time-to-close OVERSTATES late-window uncertainty and
  //   should be MORE decisive near expiry, not less. Small in absolute terms but it grows as a
  //   share of the horizon: 3%% of a 15m window, 25%% at 2 minutes left, everything at 30s.
  const _mEff=Math.max(Number(m)-0.5,0);
  const kSlots=Math.max(_mEff/15,1/60);       // effective horizon in 15m slots, floored at ~1s
  const distBps=Math.log(s/k0)*10000;
  const sd=TARA_HOURLY_VOL_A*Math.pow(kSlots,TARA_HOURLY_VOL_H);
  if(!(sd>0))return null;
  return _taraNormCdf((distBps+TARA_HOURLY_DRIFT_BPS*kSlots)/sd);
};
// V13.2: Supabase EGRESS FIX. The cloud doc was the full 5000-entry log (~2.9MB, and
//   bigger now that V13.1 telemetry enriched each entry). Every 60s RMW read+merge moved
//   the whole doc -> ~52% of the 5GB egress cap. Cross-device sync only needs RECENT
//   windows; resolved old windows never change and live in each device's IndexedDB.
//   Cap the CLOUD copy to the last 1200 (~10 days of 15m windows) -> ~3x less egress.
// V13.4.162: 1200 -> 1000. Measured against the live project (egress 2.698/5GB,
//   54% of the monthly allowance) rather than estimated. The doc is 287KB at 1200
//   entries (~245B/entry after the V10.7.58 strip), and EVERY RMW write reads the
//   whole doc back first, so doc size multiplies directly into egress.
//   1000 entries ~= 245KB, about a 15% cut on the single hottest path.
//   Storage was never the constraint: the DB is 14MB of the 500MB allowance (2.8%).
//   Egress is. Keep that in mind before raising this.
const _TARA_CLOUD_LOG_CAP=1000;
// V13.4.162: what the cloud/scorecard numbers ACTUALLY cover. Displayed everywhere a
//   record is shown so "3016 wins" can never again read as an all-time count when it
//   is really a rolling window. Derived from the cap so the label cannot drift from
//   the number.
const _TARA_RECORD_WINDOW_LABEL=`last ${_TARA_CLOUD_LOG_CAP.toLocaleString()}`;

// V9.10.9: Stable device label computed once at module load. Used as the canonical
//   identifier for tagging locally-created entries (so the personal scorecard can filter
//   to only THIS device's trades). Identical logic to the `_deviceLabel` useMemo inside
//   TaraApp — duplicated here only so the value is available at top-level scope where
//   non-hook code (like the scorecard useMemo) can reference it. Two browsers on the
//   same machine produce different labels ("Win Chrome" vs "Win Edge"), which is the
//   right granularity: each browser keeps its own personal scorecard.
const TARA_DEVICE_LABEL=(()=>{
  try{
    if(typeof navigator==='undefined')return'unknown';
    const _ua=navigator.userAgent||'';
    const _isMobile=/Mobi|Android|iPhone|iPad|iPod/i.test(_ua);
    const _platform=_isMobile?(/iPhone|iPad|iPod/i.test(_ua)?'iOS':'Android'):
      /Mac/i.test(_ua)?'Mac':/Windows/i.test(_ua)?'Win':/Linux/i.test(_ua)?'Linux':'Web';
    const _browser=/Chrome\/[\d.]+/i.test(_ua)&&!/Edg/i.test(_ua)?'Chrome':
      /Edg\//i.test(_ua)?'Edge':/Firefox\//i.test(_ua)?'Firefox':/Safari\//i.test(_ua)?'Safari':'Browser';
    return`${_platform} ${_browser}`;
  }catch(_){return'unknown';}
})();

// V6.5.8: ASSET_CONFIG — per-asset settings for multi-pair support. Tara was BTC-only
//   through V6.5.7. This table parameterizes everything that changes per asset:
//   - kalshiPrefix: market ticker prefix (e.g., 'KXBTC' → KXBTC-26MAY04-80100)
//   - kalshiSeriesTicker: events endpoint series_ticker filter
//   - cb: Coinbase product_id (REST + WebSocket)
//   - bybit: Bybit linear perpetual symbol (WebSocket)
//   - binance: Binance perp symbol (REST + WebSocket)
//   - whaleFloor: USD threshold for "whale print" alerts. Per-asset because BTC's
//     $100K floor would never fire on DOGE.
//   - priceDecimals: decimals for $ display. BTC/ETH ~2, SOL ~2, DOGE ~5
//   - tickStep: typical price tick size (for spread / depth charts)
//   - label: short user-facing label
//   - icon: emoji/glyph for selector
//   - color: brand accent for UI
// V7.1: Tara is BTC + ETH only. SOL and DOGE removed entirely. Reason: Kalshi's strike
//   data parsing for SOL/DOGE never reliably worked across the board, and we want
//   per-asset calibration to be tight rather than spread across four uncalibrated assets.
const ASSET_CONFIG={
  BTC:{kalshiPrefix:'KXBTC',kalshiSeriesTicker:'KXBTC',cb:'BTC-USD',bybit:'BTCUSDT',binance:'BTCUSDT',whaleFloor:100000,priceDecimals:2,tickStep:1,label:'BTC',icon:'₿',color:'#F7931A'},
};
const ASSET_KEYS=['BTC'];
const ASSET_DEFAULT='BTC';

// V9.8.4: PRICE_SOURCES — multi-exchange live tick registry. Coinbase is default
//   but if their feed freezes (which happened tonight — see TradingView "Data
//   issue: delayed streaming" banner, Tara's price stuck for an extended period),
//   user can toggle to Kraken or Bitstamp via the FEED pill in the header.
//   All three are US-accessible, CORS-enabled, and high-liquidity. Prices typically
//   track within $5-15 of each other on BTC, $0.50-2 on ETH, well within the noise
//   that already shapes Kalshi prediction outcomes.
//   - Coinbase: same endpoint Tara has used for months (api.exchange.coinbase.com)
//   - Kraken:   api.kraken.com/0/public/Ticker — XBTUSD pair for BTC, ETHUSD for ETH
//   - Bitstamp: www.bitstamp.net/api/v2/ticker — btcusd / ethusd
//   Each source defines: name (display), label (3-char tape ex code), url(cfg,asset),
//   parsePrice (returns float or null), parseSize (best-effort tape size).
const PRICE_SOURCES={
  // V10.7.58: BRTI added as virtual source — uses the live trimmed mean
  //   already computed from Coinbase + Kraken + OKX (brtiApprox.instant).
  //   No extra network calls. Shows as "BRTI" in the feed pill.
  //   When selected, currentPrice = brtiApprox.instant (the 3-exchange mean).
  //   This is the closest approximation to how Kalshi actually settles.

  coinbase:{
    name:'Coinbase',
    label:'CB',
    tvExch:'COINBASE',
    tvSymbol:()=>'BTCUSD',
    url:(cfg,asset)=>`https://api.exchange.coinbase.com/products/${cfg.cb||(asset==='BTC'?'BTC-USD':'ETH-USD')}/ticker`,
    parsePrice:(d)=>d?.price?parseFloat(d.price):null,
    parseSize:(d)=>d?.size?parseFloat(d.size):0.1,
    // V9.8.12: parseTradeId — Coinbase ticker returns a monotonically increasing
    //   trade_id. Used by the live-ticker effect to detect stale CDN responses:
    //   if a new fetch comes back with an id LOWER than the last id we accepted,
    //   it's a Cloudflare/edge-cached response from earlier (browser cache:'no-store'
    //   doesn't stop upstream caching). After Coinbase's outage on 2026-05-07,
    //   stale cached values from the frozen period kept flashing into Tara for
    //   1-2s at a time before the next poll returned a fresh edge node. This
    //   guard discards those flashes deterministically — no thresholds, no false
    //   positives. Kraken / OKX don't define this so the guard is skipped for them.
    parseTradeId:(d)=>d?.trade_id?Number(d.trade_id):null,
    candleUrl:(cfg,asset,granSec)=>`https://api.exchange.coinbase.com/products/${cfg.cb||(asset==='BTC'?'BTC-USD':'ETH-USD')}/candles?granularity=${granSec}&${coinbaseCandleRange(granSec)}`,
    parseCandles:(d)=>{
      if(!Array.isArray(d))return[];
      return d.map(c=>({time:c[0],l:parseFloat(c[1]),h:parseFloat(c[2]),o:parseFloat(c[3]),c:parseFloat(c[4]),v:parseFloat(c[5])}));
    },
    bookUrl:(cfg,asset)=>`https://api.exchange.coinbase.com/products/${cfg.cb||(asset==='BTC'?'BTC-USD':'ETH-USD')}/book?level=2`,
    parseBook:(d)=>{
      if(!d?.bids||!d?.asks)return null;
      return{bids:d.bids,asks:d.asks};
    },
  },
  kraken:{
    name:'Kraken',
    label:'KR',
    tvExch:'KRAKEN',
    tvSymbol:()=>'BTCUSD',
    url:(_cfg,asset)=>`https://api.kraken.com/0/public/Ticker?pair=${asset==='BTC'?'XBTUSD':'ETHUSD'}`,
    parsePrice:(d)=>{
      const _r=d?.result||{};const _k=Object.keys(_r)[0];
      const _last=_r[_k]?.c?.[0];return _last?parseFloat(_last):null;
    },
    parseSize:(d)=>{
      const _r=d?.result||{};const _k=Object.keys(_r)[0];
      const _sz=_r[_k]?.c?.[1];return _sz?parseFloat(_sz):0.1;
    },
    candleUrl:(_cfg,asset,granSec)=>{
      const _granToMin={60:1,180:3,300:5,900:15,1800:30,3600:60,14400:240,86400:1440};
      const _intvl=_granToMin[granSec]||1;
      return `https://api.kraken.com/0/public/OHLC?pair=${asset==='BTC'?'XBTUSD':'ETHUSD'}&interval=${_intvl}`;
    },
    parseCandles:(d)=>{
      const _r=d?.result||{};
      const _k=Object.keys(_r).find(k=>k!=='last'&&Array.isArray(_r[k]));
      if(!_k)return[];
      return _r[_k].map(c=>({
        time:parseInt(c[0]),
        o:parseFloat(c[1]),
        h:parseFloat(c[2]),
        l:parseFloat(c[3]),
        c:parseFloat(c[4]),
        v:parseFloat(c[6]),
      })).reverse();
    },
    bookUrl:(_cfg,asset)=>`https://api.kraken.com/0/public/Depth?pair=${asset==='BTC'?'XBTUSD':'ETHUSD'}&count=100`,
    parseBook:(d)=>{
      const _r=d?.result||{};
      const _k=Object.keys(_r).find(k=>_r[k]?.bids&&_r[k]?.asks);
      if(!_k)return null;
      return{bids:_r[_k].bids,asks:_r[_k].asks};
    },
  },
  // V9.8.11: OKX replaces Gemini. Gemini's $30-50M/day spot volume produced thin
  //   1-minute candles (small bodies, sparse activity) — visually unreadable for
  //   pattern detection compared to Coinbase's $2-3B/day. User compared screenshots
  //   side-by-side; Gemini's chart was visibly less alive. OKX has the highest BTC
  //   spot volume globally (~$1-2B/day) so candles look full and patterns form
  //   cleanly. TV widget OKX:BTCUSDT renders correctly.
  //
  // Tradeoff vs Gemini: OKX is BTC/USDT not BTC/USD. Typical spread to Coinbase is
  //   $5-30 USD (under 0.04% on $79K BTC). Gemini was tighter ($5-10) but the
  //   accuracy gain was inside Kalshi's existing $250 strike granularity + $20-50
  //   bid/ask noise — practically invisible to actual trade outcomes. Visual chart
  //   quality matters more for pattern reading.
  //
  // Routed through /api/okx/* Vercel rewrite (already in vercel.json from V9.8.7
  //   for futures endpoints used by useBloomberg + useDepthFlash).
  okx:{
    name:'OKX',
    label:'OK',
    tvExch:'OKX',
    tvSymbol:(asset)=>asset==='BTC'?'BTCUSDT':'ETHUSDT',
    // OKX SPOT ticker: /market/ticker?instId=BTC-USDT
    //   Response: {code:"0", data:[{last, lastSz, askPx, bidPx, ts, ...}]}
    url:(_cfg,asset)=>'/api/okx/market/ticker?instId=BTC-USDT',
    parsePrice:(d)=>d?.data?.[0]?.last?parseFloat(d.data[0].last):null,
    parseSize:(d)=>d?.data?.[0]?.lastSz?parseFloat(d.data[0].lastSz):0.1,
    // OKX candles: bar = '1m', '3m', '5m', '15m', '30m', '1H', '4H', '1D'
    //   Response: {code:"0", data:[[ts_ms, o, h, l, c, vol, volCcy, volCcyQuote, confirm], ...]}
    //   Already newest-first (matches Coinbase consumer convention). Timestamps are in MS,
    //   normalize to seconds via /1000 to match the standard Coinbase consumer expectation.
    candleUrl:(_cfg,asset,granSec)=>{
      const _granToBar={60:'1m',180:'3m',300:'5m',900:'15m',1800:'30m',3600:'1H',14400:'4H',86400:'1D'};
      const _bar=_granToBar[granSec]||'1m';
      return `/api/okx/market/candles?instId=BTC-USDT&bar=${_bar}&limit=300`;
    },
    parseCandles:(d)=>{
      if(!d?.data||!Array.isArray(d.data))return[];
      return d.data.map(c=>({
        time:parseInt(c[0])/1000, // OKX returns ms → convert to seconds
        o:parseFloat(c[1]),
        h:parseFloat(c[2]),
        l:parseFloat(c[3]),
        c:parseFloat(c[4]),
        v:parseFloat(c[5]),
      }));
    },
    // OKX SPOT order book: /market/books?instId=BTC-USDT&sz=50
    //   Response: {code:"0", data:[{asks:[[price, size, "0", count], ...], bids:[...], ts}]}
    //   IMPORTANT: SPOT sizes are in BTC (base currency), unlike SWAP which uses contracts.
    //   No CT_VAL multiplier needed. Matches Coinbase + Kraken spot book semantics.
    bookUrl:(_cfg,asset)=>'/api/okx/market/books?instId=BTC-USDT&sz=50',
    parseBook:(d)=>{
      const _b=d?.data?.[0];
      if(!_b?.bids||!_b?.asks)return null;
      return{bids:_b.bids,asks:_b.asks};
    },
  },
};
const PRICE_SOURCE_KEYS=Object.keys(PRICE_SOURCES);
const PRICE_SOURCE_DEFAULT='coinbase';

// V2.1: Direction C design tokens — two-tone gold/copper palette + utility classes.
// Centralized so the visual language is consistent across all UI consumers.
//   Gold (#23B981)   — premium, hero accents, V2.0 badge, major-release markers, "what changed" eyebrows
//   Copper (#C97D4A) — cautionary, friction, geo risk, quality issues, exhausted/contrarian signals
//   Emerald  — wins, UP locks, profit (unchanged)
//   Rose     — losses, DOWN locks, adverse (unchanged)
//   These coexist — copper sits between green and red as a "watch but don't alarm" tier.
/* V13.4.206: the chrome accent must NOT be a signal colour. This constant is used 94 times for modal headings, active pills and stamps; after the Kalshi migration all of it rendered in the exact WIN green (#28CC95 at the time, #23B981 since V13.4.215) -- so chrome and outcomes were indistinguishable. Neutral now: green and red are left to mean only what they measure. */
const T2_GOLD='rgba(255,255,255,0.90)';
// V13.4.207: SIT-OUT amber. Its own named colour so the four render sites cannot
//   drift apart again (they already did once: green in three of them, neutral in a
//   fourth). Amber is unambiguous against WIN mint and LOSS rose, and reads as
//   'skipped / stood down' rather than as an outcome.
const T2_SITOUT='#D4A03A';
const T2_SITOUT_FG='rgba(212,160,58,0.92)';
const T2_SITOUT_BG='rgba(212,160,58,0.13)';
const T2_COPPER='#C97D4A';

// V13.4.167: Discord embed palette + formatters, so alerts match the app instead of
//   inventing their own look. Decimal ints are what the Discord API wants; these are
//   the SAME hexes used on screen, so a DOWN lock is the same rose in both places.
const _DC_UP=0x23B981;      // emerald  — UP / buying (V13.4.215: deepened with the app)
const _DC_DOWN=0xE8455E;    // rose     — DOWN / selling (V13.4.215: deepened with the app)
const _DC_GOLD=0xD4A03A;    // gold — caution / skipped / watch. Deliberately NOT the
                            //   WIN green: green in a Discord embed reads as a win.
                            //   Tracks T2_SITOUT so the embeds and the UI agree on
                            //   what "sat out" looks like.
const _DC_DIM=0x6B6B6B;     // grey     — informational
// Compact money: 1_022_000 -> "$1.0M", 506_000 -> "$506K". Keeps columns narrow so the
//   monospace blocks below stay aligned on a phone.
const _compactMoney=(v)=>{
  const n=Math.abs(Number(v)||0);
  if(n>=1e9)return `$${(n/1e9).toFixed(1)}B`;
  if(n>=1e6)return `$${(n/1e6).toFixed(1)}M`;
  if(n>=1e3)return `$${Math.round(n/1e3)}K`;
  return `$${Math.round(n)}`;
};
const _signedMoney=(v)=>`${(Number(v)||0)>=0?'+':'-'}${_compactMoney(v)}`;
// One row of a Discord monospace block: label left, value RIGHT-aligned so the numbers
//   form a clean column, optional note after. Every embed uses this, so a lock and a
//   whale alert line up identically instead of each padding its own way.
const _dcRow=(label,value,note)=>
  (String(label).padEnd(8)+String(value).padStart(11)+(note?`   ${note}`:'')).replace(/\s+$/,'');
const T2_GOLD_GLOW='rgba(255,255,255,0.07)';
const T2_GOLD_BORDER='rgba(255,255,255,0.18)';
const T2_GOLD_DIM='rgba(255,255,255,0.34)';
const T2_COPPER_BG='rgba(201,125,74,0.08)';
const T2_COPPER_BORDER='rgba(201,125,74,0.30)';
// Tabular-nums monospace style — used for all prices, posteriors, statistics
// font-variant-numeric: tabular-nums keeps digits aligned column-wise (no wobble during ticks)
// V13.4.258 — INTERFACE REBUILD TOKENS. One visual language for every screen,
//   introduced with Sports and reused by each screen as it is converted.
//   Deliberately built from the palette already in this file: #050508 ground,
//   #0A0A0E surface, #1B1B22 hairline, #EDEDED text, #23B981 win, #E8455E loss,
//   #D4A03A caution. Nothing new was invented.
//
//   The point is WEIGHT, not colour: one panel treatment instead of pills at
//   a dozen sizes, tiny caps labels, mono numerals, and green/red reserved for
//   outcomes so nothing else competes with them.
const UI2_PANEL='border border-[#1B1B22] bg-[#0A0A0E] rounded-[10px]';
const UI2_HEAD='px-3.5 py-2.5 border-b border-[#16161c] flex items-baseline justify-between';
const UI2_LABEL='text-[9.5px] uppercase font-bold tracking-[0.15em] text-[#EDEDED]/30';
const UI2_LABEL2='text-[10px] uppercase font-semibold tracking-[0.11em] text-[#EDEDED]/45';
const UI2_GRID_BG='bg-[#16161c]';   // 1px gaps between cells show through as hairlines
const T2_MONO_STYLE={fontVariantNumeric:'tabular-nums',letterSpacing:'-0.01em'};
// Corner stamp component — small gold serial mark in upper-right of panels
function T2Stamp({code}){return(<span style={{position:'absolute',top:'8px',right:'10px',fontSize:'8px',letterSpacing:'0.18em',color:T2_GOLD_DIM,fontWeight:500}}>{code}</span>);}
// V3.2.4: BASELINE_RECORD reset to zero. Per user request: "completely reset the stats and
//   score. lets start fresh." The 487-302 figure was the live cumulative running tally; with
//   the new architecture (Tara's Call is the only thing that scores; general prediction is
//   informational only), we start the new tracking from zero. The seed trades remain for
//   weight calibration, but no synthetic baseline scorecard.
const BASELINE_RECORD={'15m':{wins:0,losses:0},'5m':{wins:0,losses:0}};

// V4.6 FRESH START: Per user request — full reset, no baked-in seed data.
const SEED_TRADES=[];

const loadTradeLog=()=>{try{
  // V6.2.5: One-time cleanup — V6.2.3/V6.2.4 briefly re-enabled trade persistence,
  //   so any localStorage entry from those versions should be cleared on next load.
  try{localStorage.removeItem('taraTradeLogV110');}catch(_){}
  return SEED_TRADES;
}catch(e){return SEED_TRADES;}};
// V6.2.5: Stubbed AGAIN per user request: "stop completely saving trades from general
//   prediction. just keep saving my score. i only want trades taken by tara to be logged."
//   The Tara call log (taraCallLog, separate localStorage key + Firestore-synced) is
//   unaffected. The Tara scorecard (taraCallScorecards_v1, W-L counts) is also unaffected.
//   Only general-predictor per-trade detail log is no longer persisted.
const saveTradeLog=(log)=>{}; // V5.5b → re-enabled in V6.2.3 → stubbed again in V6.2.5
// V134: Reset just the directional/learning state, keep trade history + scorecard
  // Use this when you want Tara to give UP/DOWN equal consideration without losing your wins/losses record
  const resetDirectionalBias=()=>{
    if(!confirm('Reset Tara\'s UP/DOWN bias? Trade log and W-L scorecard will be PRESERVED. Adaptive weights, regime weights, and calibration will reset to defaults so direction priors don\'t lean.'))return;
    try{
      // Reset adaptive signal weights to defaults
      localStorage.removeItem('taraWeightsV110');
      // Reset per-regime signal weights
      Object.keys(localStorage).filter(k=>k.startsWith('taraV110RW_')).forEach(k=>localStorage.removeItem(k));
      // Reset calibration buckets (so old UP-skewed calibration doesn't carry over)
      localStorage.removeItem('taraCalibrationV110');
      // KEEP: taraTradeLogV110 (history), taraV110Score (W-L), taraV110Mem (regime memory)
      window.location.reload();
    }catch(e){alert('Reset failed: '+e.message);}
  };

  // V145: Full fresh-start reset. Wipes everything — trade log, scorecard, weights, calibration.
  //       Tara starts from zero with the V144 calibration prior baked in. Useful when bumping
  //       to a new version with significant engine changes, when the user wants to retrain from
  //       scratch with their own data, or after recovering from corrupted localStorage.
  const resetFreshStart=()=>{
    if(!confirm('FRESH START: wipe all training data, weights, calibration, AND scorecard? Tara will start from V144 prior with no learning history. This is irreversible — only do this when you want to retrain from your own trades.'))return;
    try{
      localStorage.removeItem('taraTradeLogV110');     // trade history
      localStorage.removeItem('taraV110Score');        // W-L scorecard
      localStorage.removeItem('taraScoreV110');        // alt scorecard key
      localStorage.removeItem('taraWeightsV110');      // adaptive signal weights
      localStorage.removeItem('taraCalibrationV110');  // calibration buckets
      localStorage.removeItem('taraV110Mem');          // regime memory
      Object.keys(localStorage).filter(k=>k.startsWith('taraV110RW_')).forEach(k=>localStorage.removeItem(k));
      localStorage.setItem('taraBaselineVersion',BASELINE_VERSION); // mark as on-current-version
      window.location.reload();
    }catch(e){alert('Reset failed: '+e.message);}
  };
  
  const resetToLatestBaseline=()=>{try{
  // V111: Sync to baseline training data (matches across all devices)
  localStorage.setItem('taraTradeLogV110',JSON.stringify(SEED_TRADES));
  localStorage.setItem('taraScoreV110',JSON.stringify(BASELINE_RECORD));
  localStorage.removeItem('taraWeightsV110');
  localStorage.setItem('taraBaselineVersion',BASELINE_VERSION);
  Object.keys(localStorage).filter(k=>k.startsWith('taraV110RW_')).forEach(k=>localStorage.removeItem(k));
  // V3.2.4: User explicitly requested fresh start — clear Tara's Call scorecard too.
  //   New architecture: Tara's Call is the only thing that scores. Start tracking from zero.
  localStorage.removeItem('taraCallScorecards_v1');
  return true;
}catch(e){return false;}};

// ── GRADIENT DESCENT WEIGHT UPDATE ──
// After each trade, credit/blame each signal proportionally to its contribution
const updateWeights=(weights,tradeLog,result)=>{
  // V134: Attribution-weighted gradient descent — only update signals that
  // meaningfully contributed to the call. Prevents noise updates on bystanders.
  const last=tradeLog[tradeLog.length-1];
  if(!last||!last.signals||!last.posterior)return weights;
  // V144: DATA QUALITY GATE — refuse to learn from trades with essentially empty signal data.
  //       The seed log had 90%+ trades with all-zero signal vectors (logging bug from older
  //       versions). Running gradient descent on these was teaching the engine random noise.
  //       Now: require at least 3 non-trivial signals before this trade contributes to learning.
  const nonZeroSignals=Object.values(last.signals).filter(v=>Math.abs(v)>0.5).length;
  if(nonZeroSignals<3)return weights;
  const won=result==='WIN';
  const sig=last.signals;
  const totalAbs=Object.values(sig).reduce((s,v)=>s+Math.abs(v),0)||1;
  const conviction=Math.abs(last.posterior-50)/50;
  const idx=tradeLog.length-1;
  const totalTrades=Math.max(1,tradeLog.length);
  const recencyMult=0.5+1.5*(idx/totalTrades);
  // V134: Use trade direction (UP/DOWN), not raw posterior sign — handles edge cases
  // where posterior is near 50 but trade direction is committed
  const tradeDirSign=last.dir==='UP'?1:last.dir==='DOWN'?-1:Math.sign(last.posterior-50);
  // V10.4.6 — EV-WEIGHTED GRADIENT MAGNITUDE
  //   Replace binary W/L reinforcement with $-outcome reinforcement. With Kalshi
  //   asymmetric payouts, a $0.80 win is much more valuable than a $0.15 win,
  //   and a $0.85 loss hurts much more than a $0.20 loss. Scale the learning
  //   delta by the EV magnitude so the model learns "what makes money" not
  //   just "what wins."
  //   Normalization: kalshiAtLock=50 is the baseline (gradient = 1.0).
  //   Cap at 0.5x and 1.5x to prevent extreme swings on outlier trades.
  let _evMult=1.0;
  const _kLock=Number(last.kalshiAtLock);
  if(Number.isFinite(_kLock)&&_kLock>0&&_kLock<100){
    // V10.6.3 — slippage adjustment. Real fills cost ~1¢ more than displayed kalshiAtLock.
    //   Win profit = (100 - kLock - slippage). Loss cost = (kLock + slippage).
    if(won){
      const _realProfit=Math.max(0.1,100-_kLock-V106_3_AVG_SLIPPAGE_CENTS);
      _evMult=Math.max(0.5,Math.min(1.5,_realProfit/50));
    }else{
      const _realLoss=_kLock+V106_3_AVG_SLIPPAGE_CENTS;
      _evMult=Math.max(0.5,Math.min(1.5,_realLoss/50));
    }
  }
  // V9.11.0: SIGNAL TYPE AWARENESS. Most signals are raw weights (gap, momentum, etc.)
  //   with magnitude ~35. The V9.11.0 new signals (htfPatterns, futures) are multipliers
  //   with magnitude ~1.0. A delta of 0.12 (typical) is 0.3% on a raw weight but 12% on
  //   a multiplier — wildly different effective learning rates. We detect multiplier
  //   signals from their WEIGHT_BOUNDS upper bound (≤5 means multiplier) and scale
  //   delta by 1/30 to keep effective learning rate roughly equivalent.
  const newW={...weights};
  Object.keys(sig).forEach(k=>{
    if(!(k in newW))return;
    const contribution=Math.abs(sig[k])/totalAbs;
    // V134: ATTRIBUTION GATE — only update signals that contributed >10% of decision
    // Bystanders (like flow=0 when momentum drove the call) shouldn't get credit/blame
    if(contribution<0.10)return;
    const aligned=Math.sign(sig[k])===tradeDirSign&&sig[k]!==0;
    const _bounds=WEIGHT_BOUNDS[k]||[2,55];
    const _isMultiplier=_bounds[1]<=5;
    const _scaleForType=_isMultiplier?(1/30):1.0;
    let delta=LEARNING_RATE*contribution*conviction*recencyMult*_scaleForType*_evMult; // V10.4.6 EV-weighted
    if(won&&aligned)newW[k]+=delta;          // signal helped, win → reward proportional
    else if(won&&!aligned)newW[k]-=delta*0.3; // signal opposed, win anyway → small demote
    else if(!won&&aligned)newW[k]-=delta;     // signal led us to loss → demote fully
    else if(!won&&!aligned)newW[k]+=delta*0.2;// signal opposed, still lost → small boost
    newW[k]=Math.max(_bounds[0],Math.min(_bounds[1],newW[k]));
  });
  saveWeights(newW);
  return newW;
};
// Per-regime weight updater — same logic but targets the regime-specific set
const updateRegimeWeights=(regimeWeightsObj,trade,result)=>{
  if(!trade||!trade.signals||!trade.posterior)return regimeWeightsObj;
  // V144: data quality gate (mirror of updateWeights)
  const nonZeroSignals=Object.values(trade.signals).filter(v=>Math.abs(v)>0.5).length;
  if(nonZeroSignals<3)return regimeWeightsObj;
  const rg=trade.regime||'RANGE-CHOP';
  if(!regimeWeightsObj[rg])return regimeWeightsObj;
  const weights=regimeWeightsObj[rg];
  // V134: use trade direction for alignment
  const tradeDirSign=trade.dir==='UP'?1:trade.dir==='DOWN'?-1:Math.sign(trade.posterior-50);
  const won=result==='WIN';
  const sig=trade.signals;
  const totalAbs=Object.values(sig).reduce((s,v)=>s+Math.abs(v),0)||1;
  const conviction=Math.abs(trade.posterior-50)/50;
  const newW={...weights};
  // V10.4.6 — EV-weighted gradient (mirror of updateWeights logic)
  // V10.6.3 — slippage applied (real fills cost ~1¢ more than displayed kalshiAtLock)
  let _evMult=1.0;
  const _kLock=Number(trade.kalshiAtLock);
  if(Number.isFinite(_kLock)&&_kLock>0&&_kLock<100){
    if(won){
      const _realProfit=Math.max(0.1,100-_kLock-V106_3_AVG_SLIPPAGE_CENTS);
      _evMult=Math.max(0.5,Math.min(1.5,_realProfit/50));
    }else{
      const _realLoss=_kLock+V106_3_AVG_SLIPPAGE_CENTS;
      _evMult=Math.max(0.5,Math.min(1.5,_realLoss/50));
    }
  }
  Object.keys(sig).forEach(k=>{
    if(!(k in newW))return;
    const contribution=Math.abs(sig[k])/totalAbs;
    // V134: attribution gate
    if(contribution<0.10)return;
    const aligned=Math.sign(sig[k])===tradeDirSign&&sig[k]!==0;
    const _bounds=WEIGHT_BOUNDS[k]||[2,55];
    const _isMultiplier=_bounds[1]<=5;
    // V9.11.0: same multiplier-vs-raw-weight scaling as in updateWeights. Multipliers
    //   have magnitude ~1.0 so deltas designed for magnitude ~35 would swing them wildly.
    const _scaleForType=_isMultiplier?(1/30):1.0;
    let delta=(LEARNING_RATE*2.5)*contribution*conviction*_scaleForType*_evMult; // V10.4.6 EV-weighted
    // V143: Per-regime LR multiplier raised 1.2 → 2.5. Previous setting produced
    //       ~1-3pt regime weight differentiation after hundreds of trades. With 2.5x,
    //       regimes meaningfully diverge after ~30-50 trades. Bounds (5-65) keep clamps intact.
    if(won&&aligned)newW[k]+=delta;
    else if(won&&!aligned)newW[k]-=delta*0.3;
    else if(!won&&aligned)newW[k]-=delta;
    else if(!won&&!aligned)newW[k]+=delta*0.2;
    newW[k]=Math.max(_bounds[0],Math.min(_bounds[1],newW[k]));
  });
  const updated={...regimeWeightsObj,[rg]:newW};
  saveRegimeWeights(updated);
  return updated;
};

// ── CALIBRATION ENGINE ──
// Maps raw posterior buckets → actual historical win rates
// V144: Default calibration baked from seed trade outcomes — UP SIDE ONLY.
//
//   Why UP-only? The seed log contains DOWN trades that ALREADY survived a broken DOWN-gate
//   (V141 root-fix found that DOWN locks were structurally suppressed). So the DOWN trades
//   in the seed are a biased sample — they're the few DOWN locks that overcame the broken
//   gates, which were mostly desperate late-window calls. Using their WR to "calibrate"
//   normal DOWN posteriors would over-correct and prevent any DOWN locks from firing.
//
//   For UP side, the data is more representative: UP locks fired easily (no broken gate
//   suppressing them), so the seed reflects actual UP performance across the conviction range.
//
//   Result: UP posteriors get a calibration haircut (raw 95% → ~88% real). DOWN posteriors
//   stay raw until live post-V141 data accumulates and we can build unbiased DOWN calibration.
const DEFAULT_CALIBRATION={
  // UP side — seed-derived corrections
  90: 86.4,  // raw 90-99% UP → ~86% real WR (small over-confidence)
  80: 64.5,  // raw 80-89% UP → 65% real WR (significant over-confidence)
  70: 56.2,  // raw 70-79% UP → 56% real WR
  // Mid-range — defer to raw (no correction)
  60: null,
  50: null,
  40: null,
  // DOWN side — defer to raw, will populate from live trades only
  30: null,
  20: null,
  10: null,
  0:  null,
};

const buildCalibration=(tradeLog)=>{
  const buckets={};
  for(let b=0;b<=90;b+=10)buckets[b]={wins:0,total:0};
  tradeLog.filter(t=>verifiedSummaryCall(t)).forEach(t=>{
    // V144: Removed dead variable `upBias` (never read). Kept the bucket math.
    const p=t.originalDecision?.probabilityUpRaw;
    if(p==null||!Number.isFinite(p)||p<0||p>100)return;
    const b2=Math.floor(p/10)*10;
    const key=Math.max(0,Math.min(90,b2));
    if(!buckets[key])buckets[key]={wins:0,total:0};
    buckets[key].total++;
    if(t.officialSettlement.outcomeDir==='UP')buckets[key].wins++;
  });
  // Compute calibrated win rates per bucket
  const cal={};
  Object.keys(buckets).forEach(k=>{
    const{wins,total}=buckets[k];
    // V144: When live data is sparse (<10 trades for this bucket), fall back to seed-derived prior.
    //       Was hard-cutoff at 3 — meaning sparse buckets returned null and got no correction.
    if(total>=10){
      cal[k]=(wins/total)*100;
    } else if(total>=3){
      // Blend live + prior, weighted by sample size
      const liveRate=(wins/total)*100;
      const prior=DEFAULT_CALIBRATION[k];
      if(prior!=null){
        const w=total/10; // 0.3 for n=3, 1.0 for n=10+
        cal[k]=liveRate*w+prior*(1-w);
      } else {
        cal[k]=liveRate;
      }
    } else {
      // <3 live samples — use seed prior as-is
      cal[k]=DEFAULT_CALIBRATION[k];
    }
  });
  // V10.4.2: Attach raw bucket counts as side-channel so calibratePosterior can
  //   decide how aggressive to pull at the tails (where overconfidence sits).
  //   Audit showed 85% confident → actual 63% (22pp gap). Current ±10pt cap can
  //   only pull 85 down to 75 — not enough. With sample-aware tails, we can
  //   pull harder when there's data to justify it.
  Object.defineProperty(cal,'__buckets',{value:buckets,enumerable:false});
  return cal;
};

// V134: Chart Pattern Recognition
// Detects simple actionable patterns in candle history.
// Returns {pattern: 'doubleTop'|'doubleBottom'|'wedgeUp'|'wedgeDn'|null, confidence: 0-1}
const detectChartPattern=(history)=>{
  if(!history||history.length<10)return{pattern:null,confidence:0};
  const recent=history.slice(0,15); // last 15 candles
  const highs=recent.map(c=>c.h||c.c||0);
  const lows=recent.map(c=>c.l||c.c||0);
  const closes=recent.map(c=>c.c||0);
  // Find peaks (local maxima) and troughs (local minima)
  const peaks=[],troughs=[];
  for(let i=1;i<recent.length-1;i++){
    if(highs[i]>highs[i-1]&&highs[i]>highs[i+1])peaks.push({i,p:highs[i]});
    if(lows[i]<lows[i-1]&&lows[i]<lows[i+1])troughs.push({i,p:lows[i]});
  }
  // Double Top: 2 peaks within 0.3% of each other, separated by 3+ candles
  if(peaks.length>=2){
    const[p1,p2]=peaks.slice(-2);
    const diff=Math.abs(p1.p-p2.p)/p2.p;
    if(diff<0.003&&Math.abs(p1.i-p2.i)>=3){
      return{pattern:'doubleTop',confidence:0.7,detail:`Resistance at $${p1.p.toFixed(0)}`};
    }
  }
  // Double Bottom: 2 troughs within 0.3% of each other
  if(troughs.length>=2){
    const[t1,t2]=troughs.slice(-2);
    const diff=Math.abs(t1.p-t2.p)/t2.p;
    if(diff<0.003&&Math.abs(t1.i-t2.i)>=3){
      return{pattern:'doubleBottom',confidence:0.7,detail:`Support at $${t1.p.toFixed(0)}`};
    }
  }
  // Wedge: highs decreasing AND lows increasing (compression)
  if(peaks.length>=2&&troughs.length>=2){
    const peakSlope=(peaks[peaks.length-1].p-peaks[0].p)/(peaks[peaks.length-1].i-peaks[0].i||1);
    const troughSlope=(troughs[troughs.length-1].p-troughs[0].p)/(troughs[troughs.length-1].i-troughs[0].i||1);
    // Descending highs, ascending lows = symmetrical triangle (breakout imminent)
    if(peakSlope<-2&&troughSlope>2){
      return{pattern:'compression',confidence:0.6,detail:'Symmetrical triangle — breakout imminent'};
    }
    // Ascending wedge (rising channel) = often bearish
    if(peakSlope>0&&troughSlope>0&&peakSlope<troughSlope){
      return{pattern:'wedgeUp',confidence:0.5,detail:'Rising wedge — bearish bias'};
    }
    // Descending wedge = often bullish
    if(peakSlope<0&&troughSlope<0&&peakSlope>troughSlope){
      return{pattern:'wedgeDn',confidence:0.5,detail:'Falling wedge — bullish bias'};
    }
  }
  return{pattern:null,confidence:0};
};

// V9.10.10: TREND STRUCTURE DETECTOR (Lower-Highs / Higher-Lows / Lower-Lows / Higher-Highs)
// Walks the last N pivots to determine whether price is making a sequence of:
//   • Higher-Highs + Higher-Lows = uptrend (bullish continuation)
//   • Lower-Highs + Lower-Lows = downtrend (bearish continuation)
//   • Higher-Highs + Lower-Lows = expanding range (volatile, no edge)
//   • Lower-Highs + Higher-Lows = compression (breakout pending)
// Returns {trend: 'UP'|'DOWN'|null, strength: 0-1, detail: string}
// Strength scales with how many pivots agree (3-of-3 stronger than 2-of-2).
const detectTrendStructure=(history)=>{
  if(!history||history.length<8)return{trend:null,strength:0};
  const recent=history.slice(0,20);
  const highs=recent.map(c=>c.h||c.c||0);
  const lows=recent.map(c=>c.l||c.c||0);
  const peaks=[],troughs=[];
  for(let i=1;i<recent.length-1;i++){
    if(highs[i]>highs[i-1]&&highs[i]>highs[i+1])peaks.push({i,p:highs[i]});
    if(lows[i]<lows[i-1]&&lows[i]<lows[i+1])troughs.push({i,p:lows[i]});
  }
  // Need at least 2 of each to compare
  if(peaks.length<2||troughs.length<2)return{trend:null,strength:0};
  // Use the most recent 3 pivots if available, else last 2
  const lastPeaks=peaks.slice(-3);
  const lastTroughs=troughs.slice(-3);
  // Higher-Highs: each peak strictly higher than the previous
  let _hhCount=0;
  for(let i=1;i<lastPeaks.length;i++){if(lastPeaks[i].p>lastPeaks[i-1].p)_hhCount++;}
  // Lower-Highs: each peak strictly lower than the previous
  let _lhCount=0;
  for(let i=1;i<lastPeaks.length;i++){if(lastPeaks[i].p<lastPeaks[i-1].p)_lhCount++;}
  // Higher-Lows / Lower-Lows on troughs
  let _hlCount=0,_llCount=0;
  for(let i=1;i<lastTroughs.length;i++){
    if(lastTroughs[i].p>lastTroughs[i-1].p)_hlCount++;
    if(lastTroughs[i].p<lastTroughs[i-1].p)_llCount++;
  }
  // Score: 0-1 based on how many pivots agree
  const _maxPossible=Math.max(lastPeaks.length-1,1)+Math.max(lastTroughs.length-1,1);
  // Uptrend: HH AND HL
  if(_hhCount>=1&&_hlCount>=1){
    const strength=(_hhCount+_hlCount)/_maxPossible;
    return{trend:'UP',strength,detail:`${_hhCount} higher-high${_hhCount>1?'s':''} + ${_hlCount} higher-low${_hlCount>1?'s':''}`};
  }
  // Downtrend: LH AND LL
  if(_lhCount>=1&&_llCount>=1){
    const strength=(_lhCount+_llCount)/_maxPossible;
    return{trend:'DOWN',strength,detail:`${_lhCount} lower-high${_lhCount>1?'s':''} + ${_llCount} lower-low${_llCount>1?'s':''}`};
  }
  return{trend:null,strength:0};
};

// V9.10.10: TRENDLINE BREAK DETECTOR
// Fits a line through the last 3 swing points (highs OR lows depending on trend) and
// checks if the most recent candle has decisively broken that line.
// • For an established uptrend, the support line connects 3 most-recent rising lows.
//   If price breaks BELOW that line by >5bps → bearish reversal signal.
// • For an established downtrend, the resistance line connects 3 most-recent falling
//   highs. If price breaks ABOVE that line by >5bps → bullish reversal signal.
// Returns {broke: 'UP'|'DOWN'|null, magnitudeBps: number, detail: string}
// "UP" means the bullish trendline broke down (bearish signal). Counterintuitive but
//   matches downstream usage where the returned dir indicates which direction Tara
//   should LEAN, not which trendline broke. (UP broke → lean DOWN; DOWN broke → lean UP.)
const detectTrendlineBreak=(history)=>{
  if(!history||history.length<8)return{broke:null,magnitudeBps:0};
  const recent=history.slice(0,15);
  const highs=recent.map(c=>c.h||c.c||0);
  const lows=recent.map(c=>c.l||c.c||0);
  const peaks=[],troughs=[];
  for(let i=1;i<recent.length-1;i++){
    if(highs[i]>highs[i-1]&&highs[i]>highs[i+1])peaks.push({i,p:highs[i]});
    if(lows[i]<lows[i-1]&&lows[i]<lows[i+1])troughs.push({i,p:lows[i]});
  }
  const currentClose=recent[0]?.c;
  if(!Number.isFinite(currentClose)||currentClose<=0)return{broke:null,magnitudeBps:0};
  // Use the most recent 3 pivots. Need at least 3 to fit a meaningful line.
  // We're looking at index-position, NOT time. recent[0] is most recent candle,
  //   peaks/troughs are at indices into `recent`. Lower index = more recent.
  const lastPeaks=peaks.slice(-3);
  const lastTroughs=troughs.slice(-3);
  // Helper: fit a line through points, get expected y at the current candle's x (idx=0).
  //   Linear regression on the (i, p) points returns slope/intercept; predict at i=0.
  const _predictAt0=(pivots)=>{
    if(pivots.length<2)return null;
    const n=pivots.length;
    let sumX=0,sumY=0,sumXY=0,sumXX=0;
    for(const p of pivots){sumX+=p.i;sumY+=p.p;sumXY+=p.i*p.p;sumXX+=p.i*p.i;}
    const denom=(n*sumXX)-(sumX*sumX);
    if(denom===0)return null;
    const slope=((n*sumXY)-(sumX*sumY))/denom;
    const intercept=(sumY-(slope*sumX))/n;
    return slope*0+intercept;
  };
  // Check support break (rising lows trendline broken to the downside)
  if(lastTroughs.length>=3){
    // Confirm rising lows first — slope must be positive (i.e., earlier pivots at higher
    //   indices have lower prices; pivots[length-1] is the most-recent low). Since
    //   lower index = more recent, "rising lows" means most-recent low > earlier lows.
    const _t0=lastTroughs[lastTroughs.length-1].p; // earliest in array = oldest
    const _tLast=lastTroughs[0].p; // first in array = most recent
    if(_tLast>_t0){
      // Rising support — check if current close broke below predicted line
      const _expected=_predictAt0(lastTroughs);
      if(_expected!=null){
        const _diffBps=((currentClose-_expected)/_expected)*10000;
        // Break = current price meaningfully BELOW the line
        if(_diffBps<-5){
          return{broke:'UP',magnitudeBps:Math.abs(Math.round(_diffBps)),detail:`Rising support broken (${Math.abs(Math.round(_diffBps))}bps below trendline)`};
        }
      }
    }
  }
  // Check resistance break (falling highs trendline broken to the upside)
  if(lastPeaks.length>=3){
    const _p0=lastPeaks[lastPeaks.length-1].p;
    const _pLast=lastPeaks[0].p;
    if(_pLast<_p0){
      // Falling resistance — check if current close broke above predicted line
      const _expected=_predictAt0(lastPeaks);
      if(_expected!=null){
        const _diffBps=((currentClose-_expected)/_expected)*10000;
        if(_diffBps>5){
          return{broke:'DOWN',magnitudeBps:Math.abs(Math.round(_diffBps)),detail:`Falling resistance broken (${Math.abs(Math.round(_diffBps))}bps above trendline)`};
        }
      }
    }
  }
  return{broke:null,magnitudeBps:0};
};

// ── V9.11.0: CANDLE ORDER NORMALIZER ─────────────────────────────────────────
// Tara fetches candles from two sources with INCONSISTENT ordering:
//   • Coinbase (primary): returns newest-first natively (recent[0] = most recent)
//   • Bybit (fallback when Coinbase fails): returns newest-first → reversed at
//     fetch time (L3407) → ends up oldest-first
// The V9.10.10 detectors assume newest-first. When Bybit fallback fires (Coinbase
//   blocked / down), the detectors read candles backwards and produce garbage signals.
// Fix: detect order from timestamps and normalize. Defensive — no-op when already
//   newest-first.
const _normalizeCandles=(candles)=>{
  if(!Array.isArray(candles)||candles.length<2)return candles||[];
  const _t0=Number(candles[0]?.time)||0;
  const _t1=Number(candles[1]?.time)||0;
  if(_t0<_t1)return [...candles].reverse(); // was oldest-first → newest-first
  return candles;
};

// ── V9.11.0: HTF PATTERN AGGREGATOR ──────────────────────────────────────────
// Layer 1 of V9.11.0. Runs the V9.10.10 detectors (chartPattern, trendStructure,
//   trendlineBreak) on all available timeframes (1m, 5m, 15m) and combines them with:
//   - Higher-timeframe weight multiplier (15m signals carry more than 1m signals)
//   - Multi-timeframe confluence bonus (when 2+ TFs agree, total weight increases)
//
// Why this matters: a double-top visible on 1m noise might be meaningless. A double-top
//   visible on 15m + 5m simultaneously is a real structural top. Same pattern words,
//   different reliability. Previously Tara only checked 1m via liveHistory.
//
// Timeframe weights chosen empirically (will tune from data):
//   1m: 1.0x   (raw signal — short-term)
//   5m: 1.5x   (medium-term — most reliable single TF for crypto 15m windows)
//   15m: 2.0x  (structural — captures session-level moves)
//
// Confluence bonus: when 2+ TFs agree on direction, total score gets a 30% bonus.
//   When all 3 agree, 60% bonus. This rewards multi-TF alignment without exploding
//   the contribution when only one TF fires.
//
// Returns:
//   {
//     perTF: {c1m: {pattern, trend, tlBreak}, c5m: {...}, c15m: {...}},
//     totalAdj: number,             // combined contribution to totalScore
//     directionalScore: number,     // net directional bias (positive = UP)
//     confluence: 0|2|3,            // how many TFs aligned on net direction
//     dominantDirection: 'UP'|'DOWN'|null,
//     detail: string                // human-readable summary
//   }
const detectHtfPatterns=(tfCandles)=>{
  const _emptyResult={perTF:{c1m:null,c5m:null,c15m:null},totalAdj:0,directionalScore:0,confluence:0,dominantDirection:null,detail:'no HTF data'};
  if(!tfCandles)return _emptyResult;
  // Per-TF weights. Higher TF = more reliable signal for 15m Kalshi windows.
  const _tfWeights={c1m:1.0,c5m:1.5,c15m:2.0};
  // Per-detector base weight contributions (mirror V9.10.10's choices)
  const _detectorWeights={
    doubleTop:-20, doubleBottom:+20, wedgeUp:-10, wedgeDn:+10, compression:0,
    trendUP:+15, trendDOWN:-15, // scaled by strength
    tlBreakUP:-20, tlBreakDOWN:+20, // 'UP' broke = bearish; 'DOWN' broke = bullish
  };
  const _perTF={};
  let _totalAdj=0;
  // Track per-TF directional score so we can compute confluence
  const _tfDirScore={c1m:0,c5m:0,c15m:0};
  for(const _tf of ['c1m','c5m','c15m']){
    const _raw=tfCandles[_tf];
    if(!Array.isArray(_raw)||_raw.length<8){_perTF[_tf]=null;continue;}
    const _norm=_normalizeCandles(_raw);
    // Run all three detectors
    const _cp=detectChartPattern(_norm);
    const _ts=detectTrendStructure(_norm);
    const _tl=detectTrendlineBreak(_norm);
    // Compute this TF's contribution
    let _tfAdj=0;
    const _reasons=[];
    if(_cp?.pattern){
      const _w=_detectorWeights[_cp.pattern]||0;
      if(_w){_tfAdj+=_w; _reasons.push(`${_cp.pattern}(${_w>0?'+':''}${_w})`);}
    }
    if(_ts?.trend){
      const _baseW=_ts.trend==='UP'?_detectorWeights.trendUP:_detectorWeights.trendDOWN;
      const _scaled=Math.round(_baseW*(_ts.strength||0));
      if(_scaled){_tfAdj+=_scaled; _reasons.push(`${_ts.trend}-trend(${_scaled>0?'+':''}${_scaled})`);}
    }
    if(_tl?.broke){
      const _w=_tl.broke==='UP'?_detectorWeights.tlBreakUP:_detectorWeights.tlBreakDOWN;
      _tfAdj+=_w; _reasons.push(`${_tl.broke==='UP'?'support-broken':'resistance-broken'}(${_w>0?'+':''}${_w})`);
    }
    // Apply TF weight
    const _weighted=Math.round(_tfAdj*_tfWeights[_tf]);
    _tfDirScore[_tf]=_tfAdj;
    _totalAdj+=_weighted;
    _perTF[_tf]={
      pattern:_cp?.pattern||null,
      trend:_ts?.trend||null,
      tlBreak:_tl?.broke||null,
      rawAdj:_tfAdj,
      weightedAdj:_weighted,
      reasons:_reasons.join('+')||null,
    };
  }
  // Confluence: count how many TFs agree on net direction
  const _signs=Object.values(_tfDirScore).filter(s=>Math.abs(s)>=5).map(s=>s>0?1:-1);
  const _upCount=_signs.filter(s=>s>0).length;
  const _downCount=_signs.filter(s=>s<0).length;
  let _confluence=0;
  let _dominantDir=null;
  let _confBonus=0;
  if(_upCount>=2&&_downCount===0){_confluence=_upCount; _dominantDir='UP';}
  else if(_downCount>=2&&_upCount===0){_confluence=_downCount; _dominantDir='DOWN';}
  if(_confluence>=2){
    // Apply confluence bonus: 30% for 2-TF, 60% for 3-TF
    const _bonusMult=_confluence===3?1.6:1.3;
    _totalAdj=Math.round(_totalAdj*_bonusMult);
    _confBonus=Math.round(_totalAdj*(1-1/_bonusMult)); // approx amount added
  }
  // Build detail string
  const _details=[];
  for(const _tf of ['c1m','c5m','c15m']){
    const _t=_perTF[_tf];
    if(_t&&_t.reasons)_details.push(`${_tf.slice(1)}:${_t.reasons}`);
  }
  const _detail=_details.length>0
    ?(_details.join(' | ')+(_confluence>=2?` · ${_confluence}-TF confluence ${_dominantDir}`:''))
    :'no patterns detected';
  return{
    perTF:_perTF,
    totalAdj:_totalAdj,
    directionalScore:_totalAdj,
    confluence:_confluence,
    dominantDirection:_dominantDir,
    confluenceBonusAdj:_confBonus,
    detail:_detail,
  };
};

// V134: REAL BACKTEST FRAMEWORK — replay all historical trades and evaluate
// what current logic WOULD have done. This is the missing piece for validating changes.
const runFullBacktest=(tradeLog,opts={})=>{
  const min=opts.minTrades||10;
  const trades=tradeLog.filter(t=>t.result&&t.posterior!=null&&t.signals);
  if(trades.length<min)return{ready:false,n:trades.length,note:`Need ${min}+ resolved trades`};
  // Evaluate Tara's actual call accuracy
  const totalWins=trades.filter(t=>t.result==='WIN').length;
  const wr=totalWins/trades.length;
  // Per-direction
  const ups=trades.filter(t=>t.dir==='UP');
  const dns=trades.filter(t=>t.dir==='DOWN');
  const upWR=ups.length>0?ups.filter(t=>t.result==='WIN').length/ups.length:0;
  const dnWR=dns.length>0?dns.filter(t=>t.result==='WIN').length/dns.length:0;
  // Per-regime
  const regimePerf={};
  trades.forEach(t=>{
    const r=t.regime||'UNKNOWN';
    if(!regimePerf[r])regimePerf[r]={n:0,wins:0};
    regimePerf[r].n++;
    if(t.result==='WIN')regimePerf[r].wins++;
  });
  // Per-session
  const sessionPerf={};
  trades.forEach(t=>{
    const s=t.session||'UNKNOWN';
    if(!sessionPerf[s])sessionPerf[s]={n:0,wins:0};
    sessionPerf[s].n++;
    if(t.result==='WIN')sessionPerf[s].wins++;
  });
  // Per-clockBucket (early/mid/late lock)
  const clockPerf={'early(<300s)':{n:0,wins:0},'mid(300-700)':{n:0,wins:0},'late(700-820)':{n:0,wins:0},'verylate(>820)':{n:0,wins:0}};
  trades.forEach(t=>{
    const c=parseInt(t.clockAtLock)||0;
    let b='early(<300s)';
    if(c>820)b='verylate(>820)';
    else if(c>700)b='late(700-820)';
    else if(c>300)b='mid(300-700)';
    if(clockPerf[b]){clockPerf[b].n++;if(t.result==='WIN')clockPerf[b].wins++;}
  });
  // Brier score
  let brierSum=0;
  trades.forEach(t=>{
    const conf=t.dir==='UP'?t.posterior:(100-t.posterior);
    const probWin=conf/100;
    const actual=t.result==='WIN'?1:0;
    brierSum+=Math.pow(probWin-actual,2);
  });
  const brier=brierSum/trades.length;
  // Streaks
  let curStreak=0,curStreakType='',maxWinStreak=0,maxLossStreak=0;
  trades.forEach(t=>{
    if(t.result===curStreakType){curStreak++;}
    else{curStreak=1;curStreakType=t.result;}
    if(t.result==='WIN')maxWinStreak=Math.max(maxWinStreak,curStreak);
    else maxLossStreak=Math.max(maxLossStreak,curStreak);
  });
  // Confidence buckets (for calibration heatmap)
  const confBuckets={};
  for(let b=50;b<=90;b+=5)confBuckets[b]={n:0,wins:0};
  trades.forEach(t=>{
    const conf=t.dir==='UP'?t.posterior:(100-t.posterior);
    const bucket=Math.min(90,Math.max(50,Math.floor(conf/5)*5));
    if(confBuckets[bucket]){confBuckets[bucket].n++;if(t.result==='WIN')confBuckets[bucket].wins++;}
  });
  // Direction × regime
  const dirRegime={};
  trades.forEach(t=>{
    const k=t.dir+':'+(t.regime||'?');
    if(!dirRegime[k])dirRegime[k]={n:0,wins:0};
    dirRegime[k].n++;
    if(t.result==='WIN')dirRegime[k].wins++;
  });
  // Filter scenarios — what WR would we get if we had only taken trades with these traits?
  const filterScenarios={
    'all':{n:trades.length,wins:totalWins,wr},
    'EU only':null,
    'WED-US prime':null,
    'TRENDING DOWN+UP':null,
    'Late-zone only':null,
    'Excl late-FOMO RC/SS':null,
    'High-conf (>=75)':null,
  };
  const eu=trades.filter(t=>t.session==='EU');
  if(eu.length>0)filterScenarios['EU only']={n:eu.length,wins:eu.filter(t=>t.result==='WIN').length,wr:eu.filter(t=>t.result==='WIN').length/eu.length};
  const wedUS=trades.filter(t=>t.session==='US'&&new Date(t.id).getDay()===3);
  if(wedUS.length>0)filterScenarios['WED-US prime']={n:wedUS.length,wins:wedUS.filter(t=>t.result==='WIN').length,wr:wedUS.filter(t=>t.result==='WIN').length/wedUS.length};
  const td=trades.filter(t=>t.regime==='TRENDING DOWN');
  if(td.length>0)filterScenarios['TRENDING DOWN+UP']={n:td.length,wins:td.filter(t=>t.result==='WIN').length,wr:td.filter(t=>t.result==='WIN').length/td.length};
  const late=trades.filter(t=>parseInt(t.clockAtLock)>700);
  if(late.length>0)filterScenarios['Late-zone only']={n:late.length,wins:late.filter(t=>t.result==='WIN').length,wr:late.filter(t=>t.result==='WIN').length/late.length};
  // V134: Apply our late-FOMO rule retroactively
  const exFomo=trades.filter(t=>{
    const c=parseInt(t.clockAtLock)||0;
    const post=parseFloat(t.posterior)||50;
    const r=t.regime||'';
    const isChop=['RANGE-CHOP','SHORT SQUEEZE','HIGH VOL CHOP'].includes(r);
    const isLateFomoUp=t.dir==='UP'&&c>700&&isChop&&post>=80;
    const isLateFomoDn=t.dir==='DOWN'&&c>700&&isChop&&post<=20;
    return!(isLateFomoUp||isLateFomoDn);
  });
  if(exFomo.length>0)filterScenarios['Excl late-FOMO RC/SS']={n:exFomo.length,wins:exFomo.filter(t=>t.result==='WIN').length,wr:exFomo.filter(t=>t.result==='WIN').length/exFomo.length};
  const highConf=trades.filter(t=>{
    const conf=t.dir==='UP'?t.posterior:(100-t.posterior);
    return conf>=75;
  });
  if(highConf.length>0)filterScenarios['High-conf (>=75)']={n:highConf.length,wins:highConf.filter(t=>t.result==='WIN').length,wr:highConf.filter(t=>t.result==='WIN').length/highConf.length};
  return{
    ready:true,n:trades.length,wr,upWR,dnWR,
    regimePerf,sessionPerf,clockPerf,dirRegime,
    confBuckets,brier,maxWinStreak,maxLossStreak,
    filterScenarios
  };
};

// V134: Adaptive Threshold per Session × Regime
// For each combo, find the WR. If it's high, lower threshold (Tara more confident here).
// If it's low, raise threshold (Tara needs more conviction here).
const buildSessionRegimeThresh=(tradeLog)=>{
  const buckets={};
  tradeLog.filter(t=>t.result&&t.session&&t.regime).forEach(t=>{
    const k=t.session+':'+t.regime;
    if(!buckets[k])buckets[k]={n:0,wins:0};
    buckets[k].n++;
    if(t.result==='WIN')buckets[k].wins++;
  });
  // Compute WR & threshold adjustment per bucket
  const adj={};
  Object.entries(buckets).forEach(([k,b])=>{
    if(b.n<10)return; // V139: was 5+, raised to 10+ to reduce small-sample noise
    const rate=b.wins/b.n;
    // WR > 70% → -3 threshold (easier to lock here, you're winning)
    // WR < 50% → +5 threshold (harder to lock, you're losing)
    if(rate>=0.70)adj[k]=-3;
    else if(rate>=0.65)adj[k]=-1;
    else if(rate<=0.50)adj[k]=+5;
    else if(rate<=0.55)adj[k]=+2;
    else adj[k]=0;
  });
  return adj;
};

// V134: Regime-Direction WR Memory — explicit warning when locking into known weak combos
const buildRegimeDirWR=(tradeLog)=>{
  const wr={};
  tradeLog.filter(t=>t.result&&t.dir&&t.regime).forEach(t=>{
    const k=t.regime+'-'+t.dir;
    if(!wr[k])wr[k]={n:0,wins:0};
    wr[k].n++;
    if(t.result==='WIN')wr[k].wins++;
  });
  Object.keys(wr).forEach(k=>{
    wr[k].rate=wr[k].n>0?wr[k].wins/wr[k].n:0.5;
  });
  return wr;
};

// V9.5.0: PER-REGIME × DIRECTION POSTERIOR CALIBRATION
// Stronger than the existing regimeDirConfBoost (which only adjusts the displayed
// confidence number ±5pt at the very end). This computes a per-(regime,dir) blend
// weight that pulls the posterior toward the empirically observed WR for that pair.
//
// Math: given engine's directional confidence C (0-100) and observed WR R (0-1),
//       calibrated = C × (1-w) + R×100 × w
//       where w grows with sample size:
//         n<10: w=0 (not enough data, no correction)
//         n=10: w=0.10
//         n=30: w=0.30
//         n=80+: w=0.45 (cap — never override engine entirely)
//
// Why blend instead of replace: the engine has live signal — orderbook, tape, FGT —
// that the historical WR doesn't see. We respect both: pull toward history when
// history is decisive, but never erase the live signal.
//
// Returns {<regime>|<dir>: {n, wr, weight, multiplier_target}}.
//   multiplier_target is the WR×100 that we blend toward — kept here for diagnostics.
// ═══════════════════════════════════════════════════════════════════════════
// V9.7.3: TIME FORMAT HELPERS — single source of truth for timezone display
// ═══════════════════════════════════════════════════════════════════════════
// Resolves a timeFormat string ('local' | 'est' | 'utc') to a toLocaleString
// options object that can be spread into any toLocaleTimeString / toLocaleDateString
// call. Default is 'local' for safety — components that don't pass timeFormat
// will fall back to device-local (current pre-V9.7.3 behaviour).
//
// Usage:
//   const tz = _tzOptFor(timeFormat);
//   d.toLocaleTimeString('en-US', {...tz, hour:'2-digit', minute:'2-digit'});
//
// Also provides _fmtTimeTz and _fmtDateTz for the two most common patterns
// (HH:mm time, short date) so the call sites can be one-liners.
const _tzOptFor=(timeFormat)=>{
  if(timeFormat==='utc')return{timeZone:'UTC'};
  if(timeFormat==='est')return{timeZone:'America/New_York'};
  return{}; // 'local' or undefined → no override
};
const _fmtTimeTz=(d,timeFormat,opts={hour:'2-digit',minute:'2-digit',hour12:false})=>{
  if(!d)return'';
  const _d=d instanceof Date?d:new Date(d);
  if(!Number.isFinite(_d.getTime()))return'';
  try{return _d.toLocaleTimeString('en-US',{..._tzOptFor(timeFormat),...opts});}
  catch(_){return _d.toLocaleTimeString('en-US',opts);}
};
const _fmtDateTz=(d,timeFormat,opts={year:'numeric',month:'2-digit',day:'2-digit'})=>{
  if(!d)return'';
  const _d=d instanceof Date?d:new Date(d);
  if(!Number.isFinite(_d.getTime()))return'';
  try{return _d.toLocaleDateString('en-US',{..._tzOptFor(timeFormat),...opts});}
  catch(_){return _d.toLocaleDateString('en-US',opts);}
};

// V10.2.x — DAY-BOUNDARY HELPERS (single source of truth for "what's today?")
// ═══════════════════════════════════════════════════════════════════════════
// Centralizes day-boundary calculations across Tara so all daily aggregations
// (daily caps, "today's P&L" strips, balance baseline reset, loss-streak
// cooldown, Phase 4 day groupings) use the SAME definition of "today." Without
// this, "Today's P&L" can disagree with the daily cap because one uses local TZ
// and the other uses UTC — leading to the cap triggering on a different day
// than the strip claims.
//
// Both helpers honor the user's header timeFormat ('local' | 'utc' | 'est').
// 'est' = America/New_York (DST-aware, switches between EST and EDT).
//
// _dayKey returns 'YYYY-MM-DD' (sortable, ISO-style) for the date in the
// chosen TZ. _nextMidnightMs returns the epoch-ms moment the next midnight
// occurs in the chosen TZ — used as the cooldown-until value for daily caps
// so they auto-release at day roll.

const _dayKey=(d,timeFormat)=>{
  const _d=d instanceof Date?d:new Date(d);
  if(!Number.isFinite(_d.getTime()))return'';
  try{
    // en-CA locale produces YYYY-MM-DD format. Sortable, ISO-style.
    if(timeFormat==='utc')return _d.toLocaleDateString('en-CA',{timeZone:'UTC'});
    if(timeFormat==='est')return _d.toLocaleDateString('en-CA',{timeZone:'America/New_York'});
    return _d.toLocaleDateString('en-CA'); // local
  }catch(_){return _d.toISOString().slice(0,10);} // last-resort: UTC
};

const _nextMidnightMs=(timeFormat)=>{
  if(!timeFormat||timeFormat==='local'){
    const d=new Date();d.setHours(24,0,0,0);return d.getTime();
  }
  if(timeFormat==='utc'){
    const d=new Date();d.setUTCHours(24,0,0,0);return d.getTime();
  }
  // 'est' — America/New_York. Search forward for the moment when the day key
  //   changes in that TZ. Coarse 30-min steps (≤48 iters to span ~24h) then
  //   refine in 1-min steps. Handles DST transitions correctly because the
  //   day-key check itself is TZ-aware.
  const tz='America/New_York';
  const now=Date.now();
  let curKey;try{curKey=new Date(now).toLocaleDateString('en-CA',{timeZone:tz});}catch(_){curKey='';}
  if(!curKey){const d=new Date();d.setUTCHours(24,0,0,0);return d.getTime();}
  let t=now;
  for(let i=0;i<49;i++){
    t+=30*60*1000;
    let k;try{k=new Date(t).toLocaleDateString('en-CA',{timeZone:tz});}catch(_){continue;}
    if(k!==curKey){
      // Refine to 1-minute precision
      let back=t;
      for(let j=0;j<30;j++){
        back-=60*1000;
        let bk;try{bk=new Date(back).toLocaleDateString('en-CA',{timeZone:tz});}catch(_){continue;}
        if(bk===curKey)return back+60*1000;
      }
      return t;
    }
  }
  // Fallback (shouldn't reach in practice)
  const d=new Date();d.setUTCHours(24,0,0,0);return d.getTime();
};

const buildRegimeDirCalibration=(tradeLog)=>{
  const out={};
  const counts={};
  for(const t of (tradeLog||[])){
    if(!t||!t.result||!t.dir||!t.regime)continue;
    if(t.result!=='WIN'&&t.result!=='LOSS')continue;
    const k=t.regime+'|'+t.dir;
    if(!counts[k])counts[k]={n:0,wins:0};
    counts[k].n++;
    if(t.result==='WIN')counts[k].wins++;
  }
  for(const k of Object.keys(counts)){
    const{n,wins}=counts[k];
    const wr=n>0?wins/n:0.5;
    let weight=0;
    // V9.7.6: stronger calibration weights so the displayed posterior reflects
    //   cluster reality more honestly. Old ramp maxed at 0.45 even at n=80+,
    //   so even with overwhelming evidence the engine's live signal still had
    //   majority say. New ramp:
    //     n=10  → 0.10
    //     n=25  → 0.40
    //     n=50  → 0.65
    //     n=80+ → 0.85 (cluster reality dominates)
    //   This means a SHORT SQUEEZE×UP cluster running 60% WR with n=27 trades
    //   will pull a raw 80% posterior down to ~67% (was 73%) — closer to truth.
    if(n>=10){
      if(n<=25){
        weight=0.10+(n-10)/15*0.30; // 10→25: 0.10→0.40
      }else{
        weight=Math.min(0.85,0.40+(n-25)/55*0.45); // 25→80: 0.40→0.85
      }
    }
    out[k]={n,wr,weight,wrPct:Math.round(wr*1000)/10};
  }
  return out;
};

// ═══════════════════════════════════════════════════════════════════════════
// V9.7.0: MISSION MODE — disciplined bankroll-target trading
// ═══════════════════════════════════════════════════════════════════════════
// User sets a starting bankroll, a target $ amount, an end date, and a drawdown
// floor. Tara sizes each trade using fractional Kelly with cluster-specific WR,
// modulated by distance-to-target and remaining time. Hard stops at floor and
// at target. Surfaces honest probability of target hit each render.
//
// Math summary:
//   Per-trade size: f = min(maxBetFrac, kellyFrac × bankroll, edgeAdjustment)
//   Kelly fraction: (b·p − q) / b  where p=cluster WR, q=1−p, b=odds
//   We use fractional Kelly (default ¼) to reduce drawdown variance.
//   Probability of target = simulated geometric Brownian path with measured
//   per-trade growth rate and variance. Single-pass approximation, not Monte
//   Carlo (cheap to compute every render).
// ───────────────────────────────────────────────────────────────────────────

// ═══════════════════════════════════════════════════════════════════════════
// V10.7.43 — FINE-GRAINED OUTCOME CALIBRATION
// ═══════════════════════════════════════════════════════════════════════════
// Existing regimeDirCalibration buckets by (regime|dir) only — too coarse.
// This adds finer buckets: (regime|tier|dir|kalshiBucket|posteriorBucket).
//
// Built from taraCallLog historical resolutions. At lock time, looks up the
// current context bucket and either:
//   • Reduces confidence if bucket has historical WR <50% (n≥15)
//   • Boosts confidence if bucket has historical WR >65% (n≥15)
//   • Flips direction if bucket has WR <40% (n≥20) AND low current confidence
// No effect if bucket has insufficient samples (n<15).
//
// Key insight: this catches patterns the engine doesn't know about. Example:
//   "structural-led × UP × RANGE-CHOP × Kalshi 30-50" might have 47% WR
//   despite Tara's posterior saying 75%. Calibration overrides reality > theory.
// ═══════════════════════════════════════════════════════════════════════════

// Bucket the Kalshi price into coarse ranges (matches data resolution)
const _calBucketKalshi=(k)=>{
  if(k==null||!Number.isFinite(k))return 'na';
  if(k<25)return '<25';
  if(k<40)return '25-40';
  if(k<55)return '40-55';
  if(k<70)return '55-70';
  return '70+';
};
// Bucket posterior into 10pt bands above/below 50 (conviction proxy)
const _calBucketPosterior=(p)=>{
  if(p==null||!Number.isFinite(p))return 'na';
  if(p<35)return 'p<35';
  if(p<50)return 'p35-50';
  if(p<60)return 'p50-60';
  if(p<70)return 'p60-70';
  if(p<80)return 'p70-80';
  return 'p80+';
};
// Canonicalize tier label (matches V9.9.1 logic in computeReversalRisk)
const _calTierCanon=(t)=>{
  if(!t)return 'unknown';
  if(t==='structural-led'||t==='structural')return 'structural';
  if(t==='tape-led'||t==='tape')return 'tape';
  if(t==='super-confluence'||t==='super')return 'super';
  if(t==='rising-confluence'||t==='rising')return 'rising';
  if(t==='confluence')return 'confluence';
  if(t==='time-cap-commit')return 'time-cap';
  if(t==='directional-lock')return 'directional';
  return t;
};

// Build the calibration table from a call log array.
// Returns: { 'bucketKey': {wins, losses, n, wr} }
// Bucket key format: "regime|tier|dir|kBucket"
//   (posterior bucket excluded from key — we look up by posterior at apply time)
const buildCalibrationTable=(callLog)=>{
  const table={};
  if(!Array.isArray(callLog)||callLog.length===0)return table;
  for(const e of callLog){
    if(!e||(e.result!=='WIN'&&e.result!=='LOSS'))continue;
    if(e.betAmt==null)continue; // V13.4.45: committed locks only (not predictor reads)
    const _regime=e.regimeV12||e.regime; // V13.4.45: entries persist regimeV12, not top-level regime
    if(!_regime||!e.tier||!e.dir)continue;
    if(e.dir!=='UP'&&e.dir!=='DOWN')continue;
    const _tier=_calTierCanon(e.tier);
    const _dir=e.dir;
    const _kBucket=_calBucketKalshi(e.kalshiAtLock);
    const _key=`${_regime}|${_tier}|${_dir}|${_kBucket}`;
    if(!table[_key])table[_key]={wins:0,losses:0,n:0,wr:0};
    if(e.result==='WIN')table[_key].wins++;
    else table[_key].losses++;
    table[_key].n++;
    table[_key].wr=table[_key].wins/table[_key].n;
  }
  return table;
};

// Look up the bucket's historical WR for a given context.
// Returns {wr, n, found} where found=true if bucket exists with n>=minN.
const lookupCalibration=(table,context,minN=15)=>{
  if(!table||!context)return{wr:null,n:0,found:false};
  const{regime,tier,dir,kalshiAtLock}=context;
  if(!regime||!tier||!dir)return{wr:null,n:0,found:false};
  const _tier=_calTierCanon(tier);
  const _kBucket=_calBucketKalshi(kalshiAtLock);
  // V10.7.59 — CASCADING FALLBACK: try progressively coarser buckets.
  //   Fine bucket (regime|tier|dir|kalshi) rarely hits n≥15.
  //   Coarser fallbacks (drop kalshi, then drop tier) have enough samples.
  //   Each fallback trusts the result less — use a reduced minN per level.
  const _candidates=[
    {key:`${regime}|${_tier}|${dir}|${_kBucket}`, minN:minN, label:'fine'},
    {key:`${regime}|${_tier}|${dir}`, minN:Math.max(8,Math.floor(minN*0.6)), label:'no-kalshi'},
    {key:`${regime}|${dir}`, minN:Math.max(5,Math.floor(minN*0.4)), label:'regime-dir'},
  ];
  for(const _c of _candidates){
    const _entry=table[_c.key];
    if(_entry&&_entry.n>=_c.minN){
      // Trust dampening for coarser buckets — boost/flip require stronger signal
      const _trustMult=_c.label==='fine'?1.0:_c.label==='no-kalshi'?0.85:0.65;
      return{wr:_entry.wr,n:_entry.n,found:true,key:_c.key,trustMult:_trustMult,label:_c.label};
    }
  }
  // Return the finest key for diagnostic purposes even if not found
  return{wr:null,n:table[`${regime}|${_tier}|${dir}|${_kBucket}`]?.n||0,found:false,key:`${regime}|${_tier}|${dir}|${_kBucket}`};
};
// ═══════════════════════════════════════════════════════════════════════════
// END V10.7.43 CALIBRATION FUNCTIONS
// ═══════════════════════════════════════════════════════════════════════════

// Cluster-WR lookup: given (regime, dir, tier), return {wr, n} from the
// regimeDirCalibration table + tier-specific haircut/boost. If sample is
// thin, fall back to the audit-baseline conservative number.
const _missionGetClusterWR=({regime,dir,tier,regimeDirCal})=>{
  // Tier multipliers per V9.4.0 audit data
  const TIER_BASELINE={super:0.78,confluence:0.70,structural:0.62,tape:0.66,single:0.62};
  const _baseline=TIER_BASELINE[tier]||0.62;
  if(!regimeDirCal||!regime||!dir)return{wr:_baseline,n:0};
  const k=`${regime}|${dir}`;
  const _entry=regimeDirCal[k];
  if(!_entry||_entry.n<10)return{wr:_baseline,n:_entry?_entry.n:0};
  // Blend live cluster WR with baseline tier expectation, weighted by sample size
  const _w=Math.min(0.7,(_entry.n-10)/(80-10)*0.6+0.10);
  const _blended=_entry.wr*_w+_baseline*(1-_w);
  return{wr:_blended,n:_entry.n};
};

// Compute optimal bet given bankroll, edge, market price, and mission constraints.
// Returns {betDollars, reason, kellyFrac, fractionOfBankroll, blockedReason?}.
//
// Flow:
//   1. Compute Kelly fraction from cluster WR + market odds
//   2. Apply fractional Kelly multiplier (default 0.25 — conservative)
//   3. Cap at maxBetFraction × bankroll (drawdown protection)
//   4. Distance-to-target tilt: behind schedule → small upsize; ahead → downsize
//   5. Drawdown brake: as bankroll approaches floor, sizes shrink quadratically
//   6. Floor checks: bankroll < floor → block, bet < $1 → block
const _missionComputeBet=({bankroll,target,daysRemaining,floor,p,limitCents,fractionalKellyMult=0.25,maxBetFraction=0.15,maxBetCap})=>{
  if(!Number.isFinite(bankroll)||bankroll<=0)return{betDollars:0,blockedReason:'no bankroll'};
  if(bankroll<=floor)return{betDollars:0,blockedReason:`bankroll ${bankroll.toFixed(2)} ≤ floor ${floor}`};
  if(!Number.isFinite(p)||p<=0||p>=1)return{betDollars:0,blockedReason:'no edge estimate'};
  const _cents=Math.max(1,Math.min(99,Math.round(limitCents)));
  // Kalshi: cost = limitCents¢, payout = (100-limitCents)¢ above cost. b = (100-c)/c.
  const _b=(100-_cents)/_cents;
  const _q=1-p;
  const _kelly=(_b*p-_q)/_b;
  if(_kelly<=0)return{betDollars:0,blockedReason:`no edge (kelly=${(_kelly*100).toFixed(1)}%)`};
  const _kellyFrac=Math.max(0,Math.min(1,_kelly));
  // Fractional Kelly is the base size
  let _fracOfBankroll=_kellyFrac*fractionalKellyMult;
  // Distance-to-target tilt
  if(target>0&&daysRemaining>0&&bankroll<target){
    // What growth-per-trade rate is needed?
    const _gain=target/bankroll; // multiplier needed
    // Assume roughly 8 trades/day if active. tradesNeeded = daysRemaining × 8.
    const _expectedTrades=Math.max(1,daysRemaining*8);
    const _requiredGrowthPerTrade=Math.pow(_gain,1/_expectedTrades)-1; // e.g. 0.012 = 1.2%/trade
    // Compare to current expected growth: kelly_frac × edge
    const _currentExpected=_kellyFrac*fractionalKellyMult*((p*_b)-(1-p));
    if(_currentExpected>0&&_requiredGrowthPerTrade>_currentExpected){
      // Behind schedule — bump slightly. Cap the bump at 1.5×.
      const _bump=Math.min(1.5,_requiredGrowthPerTrade/_currentExpected);
      _fracOfBankroll*=_bump;
    }else if(_currentExpected>0&&_requiredGrowthPerTrade<_currentExpected*0.5){
      // Ahead of schedule — preserve gains, downsize 30%
      _fracOfBankroll*=0.7;
    }
  }
  // Drawdown brake — quadratic shrink as we approach floor
  const _drawdownDistance=(bankroll-floor)/Math.max(1,bankroll);
  if(_drawdownDistance<0.4){
    // Quadratic: at 40% above floor, no brake; at floor itself, full brake
    const _brake=Math.max(0,_drawdownDistance/0.4);
    _fracOfBankroll*=_brake*_brake;
  }
  // Cap at maxBetFraction
  _fracOfBankroll=Math.min(maxBetFraction,_fracOfBankroll);
  let _bet=bankroll*_fracOfBankroll;
  // Apply absolute cap if provided
  if(Number.isFinite(maxBetCap)&&maxBetCap>0)_bet=Math.min(_bet,maxBetCap);
  if(_bet<1)return{betDollars:0,kellyFrac:_kellyFrac,fractionOfBankroll:_fracOfBankroll,blockedReason:`size <$1 (${_bet.toFixed(2)})`};
  return{
    betDollars:_bet,
    kellyFrac:_kellyFrac,
    fractionOfBankroll:_fracOfBankroll,
    reason:`Kelly ${(_kellyFrac*100).toFixed(0)}% × ${fractionalKellyMult}× = ${(_fracOfBankroll*100).toFixed(1)}% of $${bankroll.toFixed(0)}`,
  };
};

// V13.4.310: wires Mission Mode's real fractional-Kelly math into actual
//   order sizing. _missionGetClusterWR and _missionComputeBet above have
//   been fully built since Mission Mode shipped but had zero callers --
//   this is the first. A pure function, everything passed in explicitly,
//   nothing closed over -- so it can be (and was) sanity-tested standalone
//   across a bankroll/WR/cost/days-remaining grid before it ever touched a
//   live credential (see project_tara_autoexec_simplify memory).
// `fallbackDollars` does double duty: it's both (a) what gets returned
//   whenever Mission math declines to size at all -- no edge, thin sample,
//   bankroll at/under floor, resulting size under $1 -- so a Mission veto
//   never means "no rail at all," and (b) the maxBetCap fed into
//   _missionComputeBet, so the existing max-bet-per-trade ceiling stays the
//   hard backstop in Mission Mode exactly as it already is outside it.
const _missionResolveStakeDollars=({mission,regimeDirCal,lock,costCents,fallbackDollars})=>{
  if(!mission||!mission.active||mission.status!=='active')return fallbackDollars;
  if(!lock||(lock.dir!=='UP'&&lock.dir!=='DOWN'))return fallbackDollars;
  const tier=_calTierCanon(lock.tier);
  const{wr}=_missionGetClusterWR({regime:lock.lockedRegime,dir:lock.dir,tier,regimeDirCal});
  const _daysRemaining=mission.endDate
    ?Math.max(0,(new Date(mission.endDate).getTime()-Date.now())/86400000):0;
  const _result=_missionComputeBet({
    bankroll:mission.currentBankroll,
    target:mission.target,
    daysRemaining:_daysRemaining,
    floor:mission.floor,
    p:wr,
    limitCents:costCents,
    fractionalKellyMult:mission.kellyMult,
    maxBetFraction:mission.maxBetFraction,
    maxBetCap:fallbackDollars,
  });
  if(!(Number(_result&&_result.betDollars)>0))return fallbackDollars;
  return _result.betDollars;
};

// Estimate probability of hitting target by deadline given current state.
// Cheap approximation: log-growth model with measured trade-level mean and stdev.
//
// μ_per_trade = E[log(1+r)] ≈ p·log(1+b·f) + q·log(1-f)  where f = bet fraction
// σ²_per_trade = p·(log(1+b·f))² + q·(log(1-f))² − μ²
// Required total log-growth = log(target/bankroll)
// Number of trades remaining = daysRemaining × tradesPerDay
// Z = (required − μ·n) / (σ·sqrt(n))
// Probability ≈ 1 - Φ(Z)  (one-sided normal CDF)
//
// This is a rough estimate — assumes constant edge, constant trade rate, no
// regime shift. Real outcome variance is higher than this model. Treat the
// number as "rough order of magnitude" not precision forecast.
const _missionEstimateTargetProbability=({bankroll,target,daysRemaining,p,b,fractionOfBankroll,tradesPerDay=8})=>{
  if(!Number.isFinite(bankroll)||bankroll<=0)return 0;
  if(bankroll>=target)return 1;
  if(daysRemaining<=0)return 0;
  if(!Number.isFinite(p)||!Number.isFinite(b)||!Number.isFinite(fractionOfBankroll)||fractionOfBankroll<=0)return 0;
  const _f=fractionOfBankroll;
  const _muUp=Math.log(1+b*_f);
  const _muDn=Math.log(Math.max(1e-9,1-_f));
  const _q=1-p;
  const _muPerTrade=p*_muUp+_q*_muDn;
  if(_muPerTrade<=0)return 0; // negative drift → can't reach target
  const _varPerTrade=p*_muUp*_muUp+_q*_muDn*_muDn-_muPerTrade*_muPerTrade;
  if(_varPerTrade<=0)return _muPerTrade>0?0.99:0;
  const _sigmaPerTrade=Math.sqrt(_varPerTrade);
  const _n=Math.max(1,Math.floor(daysRemaining*tradesPerDay));
  const _required=Math.log(target/bankroll);
  const _z=(_required-_muPerTrade*_n)/(_sigmaPerTrade*Math.sqrt(_n));
  // Approximate normal CDF using the Abramowitz & Stegun formula
  const _normCdf=(z)=>{
    const _t=1/(1+0.2316419*Math.abs(z));
    const _d=0.3989422804*Math.exp(-z*z/2);
    const _p_cdf=_d*_t*(0.31938153+_t*(-0.356563782+_t*(1.781477937+_t*(-1.821255978+_t*1.330274429))));
    return z>=0?1-_p_cdf:_p_cdf;
  };
  return Math.max(0,Math.min(1,1-_normCdf(_z)));
};

// V9.5.0: KALSHI-SPOT DIVERGENCE DETECTOR (opponent modeling)
// Detects "Kalshi-only" moves — where the YES price moves materially over a 60s
// window but spot price stays roughly flat. Interpretation: someone is taking a
// directional Kalshi position based on expectation, not in response to the
// underlying. Treated as a leading indicator (positioning signal).
//
// Inputs come from refs that are already populated:
//   kalshiHist: array of {yes, time}  (last 60s, populated in TaraApp)
//   tickHist:   array of {p, t, time, ...}  (tick-level price history)
//
// Returns {dir, kDelta, spotDeltaBps, score, detectedAt} or null.
//   score is in points (0..10) suitable to feed into the engine's signal sum.
//
// Threshold rationale:
//   |kDelta| ≥ 4¢ — typical Kalshi spread is 1-2¢, so 4¢ is a real move
//   |spotDeltaBps| ≤ 4 — within Kalshi-only noise floor for 60s
//   score scales linearly past 4¢, capped at 10pt at 14¢+ (rare extreme)
const computeKalshiLead=({kalshiHist,tickHist,nowMs})=>{
  const now=nowMs||Date.now();
  const lookbackMs=60000;
  const sampleWindowMs=8000; // accept samples within ±8s of the lookback edges
  if(!Array.isArray(kalshiHist)||!Array.isArray(tickHist))return null;
  if(kalshiHist.length<3||tickHist.length<5)return null;
  // Most-recent samples (last 5s)
  const _kRecent=kalshiHist.filter(k=>k&&Number.isFinite(k.yes)&&now-k.time<=5000);
  // Old samples around the lookback edge
  const _kOld=kalshiHist.filter(k=>k&&Number.isFinite(k.yes)&&now-k.time>=lookbackMs-sampleWindowMs&&now-k.time<=lookbackMs+sampleWindowMs);
  if(_kRecent.length===0||_kOld.length===0)return null;
  const kNow=_kRecent[_kRecent.length-1].yes;
  const kThen=_kOld[0].yes;
  if(!Number.isFinite(kNow)||!Number.isFinite(kThen))return null;
  const kDelta=kNow-kThen; // cents
  const ABS_K_THRESH=4;
  if(Math.abs(kDelta)<ABS_K_THRESH)return null;
  // Spot delta over same lookback. tickHist entries have {p, time}.
  const _tRecent=tickHist.filter(t=>t&&Number.isFinite(t.p)&&t.p>0&&now-t.time<=5000);
  const _tOld=tickHist.filter(t=>t&&Number.isFinite(t.p)&&t.p>0&&now-t.time>=lookbackMs-sampleWindowMs&&now-t.time<=lookbackMs+sampleWindowMs);
  if(_tRecent.length===0||_tOld.length===0)return null;
  const pNow=_tRecent[_tRecent.length-1].p;
  const pThen=_tOld[0].p;
  if(!pNow||!pThen)return null;
  const spotDeltaBps=((pNow-pThen)/pThen)*10000;
  const ABS_SPOT_FLAT=4; // bps
  if(Math.abs(spotDeltaBps)>ABS_SPOT_FLAT)return null; // spot moved too — not divergent
  // Direction Kalshi is leaning
  const dir=kDelta>0?'UP':'DOWN';
  const mag=Math.abs(kDelta);
  // Score: 4¢ → 4pt, 8¢ → 7pt, 14¢+ → 10pt (cap)
  const score=Math.min(10,Math.round(4+(mag-4)*0.6));
  return{
    dir,
    kDelta:Math.round(kDelta*10)/10,
    spotDeltaBps:Math.round(spotDeltaBps*10)/10,
    score,
    detectedAt:now,
  };
};

// Apply calibration correction to raw posterior
// V144: Calibration applies UP-side only (DOWN side has null calibration values until
//       live post-V141 data accumulates).
//   - UP side high-conf (raw 80+): trust calibration heavily (85/15) — over-confidence is real
//   - UP side mid-conf (raw 60-79): standard 70/30 blend
//   - DOWN side & near-50: no calibration data, return raw unchanged
const calibratePosterior=(raw,calibration)=>{
  if(!calibration)return raw;
  const bucket=Math.floor(raw/10)*10;
  const key=Math.max(0,Math.min(90,bucket));
  const calVal=calibration[key];
  if(calVal==null)return raw; // no data for this bucket
  // V10.4.2: TAIL-AWARE OVERCONFIDENCE CORRECTION
  //   Audit found 22pp gap at tails (85%+ claimed → 63% actual, 90%+ → 70%).
  //   Old logic: weight 0.85 at high conf, cap ±10pt. Could only pull 85→75.
  //   New logic: when sample size in that tail bucket is ≥25, allow weight 0.90
  //   and cap ±15pt. Mids unchanged. Sample-aware to avoid overcorrecting on thin
  //   buckets. Tail = ≥80% or ≤20% posterior.
  const _buckets=calibration.__buckets;
  const _n=_buckets&&_buckets[key]?_buckets[key].total:0;
  const _isTail=raw>=80||raw<=20;
  const _hasTailData=_isTail&&_n>=25;
  const calWeight=_hasTailData?0.90:(raw>=80?0.85:0.7);
  const blended=calVal*calWeight+raw*(1-calWeight);
  const CAL_CAP=_hasTailData?15:10;
  // V6.3.3: Cap calibration swing at ±10 points (±15 at tails with data).
  //   Without this cap, a small-sample bucket could shift the posterior by 30+
  //   points and even flip its sign. Calibration's job is fine-tuning, not
  //   signal override. With the tail-aware cap, we can pull harder on
  //   well-sampled overconfident buckets without risking flip behavior.
  if(blended-raw>CAL_CAP)return raw+CAL_CAP;
  if(raw-blended>CAL_CAP)return raw-CAL_CAP;
  return blended;
};

// ── PER-SIGNAL ACCURACY TRACKER ──
const buildSignalAccuracy=(tradeLog)=>{
  const signals={gap:{right:0,total:0},momentum:{right:0,total:0},structure:{right:0,total:0},flow:{right:0,total:0},technical:{right:0,total:0},regime:{right:0,total:0}};
  // V10.7.57 FIX: read from signalScoresAtLock (the actual field name on entries).
  //   Prior code read t.signals which never exists, so samples always showed 0.
  //   Now we properly inspect each signal score at lock time and check whether
  //   its direction aligned with the actual outcome.
  tradeLog.filter(t=>t.result&&(t.signalScoresAtLock||t.signals)).forEach(t=>{
    const _signalsObj=t.signalScoresAtLock||t.signals||{};
    const outcomeUp=t.dir==='UP'?t.result==='WIN':t.result==='LOSS';
    Object.keys(signals).forEach(k=>{
      const sigVal=_signalsObj[k];
      if(sigVal==null||sigVal===0)return; // signal didn't fire — skip
      const sigDir=sigVal>0;
      signals[k].total++;
      if(sigDir===outcomeUp)signals[k].right++;
    });
  });
  return signals;
};

// ── SESSION PERFORMANCE ──
const buildSessionPerf=(tradeLog)=>{
  const sessions={ASIA:{wins:0,losses:0},EU:{wins:0,losses:0},US:{wins:0,losses:0},'OFF-HOURS':{wins:0,losses:0}};
  tradeLog.filter(t=>t.result).forEach(t=>{
    const s=t.session||'OFF-HOURS';
    if(!sessions[s])sessions[s]={wins:0,losses:0};
    if(t.result==='WIN')sessions[s].wins++;else sessions[s].losses++;
  });
  return sessions;
};

// ── TIME-OF-DAY PERFORMANCE ──
const buildHourlyPerf=(tradeLog)=>{
  const hourly={};
  tradeLog.filter(t=>t.result&&t.hour!=null).forEach(t=>{
    const h=t.hour;if(!hourly[h])hourly[h]={wins:0,losses:0};
    if(t.result==='WIN')hourly[h].wins++;else hourly[h].losses++;
  });
  return hourly;
};

// ═══════════════════════════════════════
// MARKET SESSIONS
// ═══════════════════════════════════════
const getMarketSessions=()=>{
  const now=new Date();
  // V9.1.7: Sessions DERIVE FROM UTC PHASE so phase + session never mismatch.
  //   Was: sessions used user's LOCAL hours (asia=0-9 local, eu=7-16 local, us=13-22 local)
  //   while PHASE_PROFILES are UTC-anchored. So in EST at 4 PM local, the phase showed
  //   ASIA_OPEN (UTC 21:00) but session showed US — visually contradictory.
  //   Now both come from the same UTC clock. ASIA = ASIA_OPEN/ASIA_LATE, EU = EU_PREOPEN/EU_OPEN,
  //   US = NY_*. AFTERHOURS sits between US close and Asia open — bucket as 'OFF-HOURS'.
  const localH=now.getHours();
  const dayLocal=now.getDay();
  const dayName=['SUN','MON','TUE','WED','THU','FRI','SAT'][dayLocal];
  const _phaseKey=getPhaseKey(now.getUTCHours(),now.getUTCMinutes());
  const _sessionFromPhase=(()=>{
    if(_phaseKey==='ASIA_OPEN'||_phaseKey==='ASIA_LATE')return'ASIA';
    if(_phaseKey==='EU_PREOPEN'||_phaseKey==='EU_OPEN')return'EU';
    if(_phaseKey==='NY_PREMARKET'||_phaseKey==='NY_OPEN'||_phaseKey==='NY_MORNING'
      ||_phaseKey==='NY_LUNCH'||_phaseKey==='NY_AFTERNOON'||_phaseKey==='NY_CLOSE')return'US';
    return'OFF-HOURS';
  })();
  // Build active sessions list — overlap regions show multiple flags. Asia + EU overlap
  //   during EU_PREOPEN; EU + US overlap during EU_OPEN late + NY_PREMARKET + NY_OPEN start.
  const sessions=[];
  // Asia is active during ASIA_OPEN, ASIA_LATE
  if(_phaseKey==='ASIA_OPEN'||_phaseKey==='ASIA_LATE'){
    sessions.push({name:'ASIA',flag:'🌏',color:'text-amber-400'});
  }
  // EU is active during EU_PREOPEN, EU_OPEN, NY_PREMARKET, NY_OPEN start
  if(_phaseKey==='EU_PREOPEN'||_phaseKey==='EU_OPEN'||_phaseKey==='NY_PREMARKET'||_phaseKey==='NY_OPEN'){
    sessions.push({name:'EU',flag:'🌍',color:'text-blue-400'});
  }
  // US is active during NY_*
  if(_phaseKey==='NY_PREMARKET'||_phaseKey==='NY_OPEN'||_phaseKey==='NY_MORNING'
    ||_phaseKey==='NY_LUNCH'||_phaseKey==='NY_AFTERNOON'||_phaseKey==='NY_CLOSE'){
    sessions.push({name:'US',flag:'🌎',color:'text-emerald-400'});
  }
  if(sessions.length===0){
    sessions.push({name:'OFF-HOURS',flag:'🌒',color:'text-gray-400'});
  }
  const dominant=_sessionFromPhase;
  // V114: Day×Session quality multipliers (range -10 to +5; subtracted from quality threshold or added)
  // Higher = better historically (lower bar to lock); Lower = worse (higher bar)
  // Based on training data + market structure: Sun thin liquidity, Mon Asia gappy, Wed US best, Fri close fakeouts
  // V139: Magnitudes halved (was -10..+5, now -5..+3). Per-bucket sample sizes are ~10-30 trades —
  //       too noisy for the previous swing range. Direction held, magnitude dampened.
  const dsKey=dayName+'-'+dominant;
  const dsMap={
    'SUN-ASIA':-4,'SUN-EU':-3,'SUN-US':-5,            // Sunday is the worst day across the board
    'MON-ASIA':-2,'MON-EU':1,'MON-US':-1,             // Mon Asia gappy from weekend; Mon EU OK
    'TUE-ASIA':1,'TUE-EU':2,'TUE-US':2,               // Tuesday consistently good
    'WED-ASIA':2,'WED-EU':2,'WED-US':3,               // Wednesday US is the cleanest signal day
    'THU-ASIA':1,'THU-EU':2,'THU-US':1,
    'FRI-ASIA':1,'FRI-EU':1,'FRI-US':-3,              // Fri US close = fake breakouts before close
    'SAT-ASIA':-3,'SAT-EU':-2,'SAT-US':-3,            // Saturday thin
  };


  const dsAdj=dsMap[dsKey]||0;
  // V139: rating thresholds adjusted to the halved scale
  const dsRating=dsAdj>=2?'A':dsAdj>=1?'B':dsAdj>=0?'C':dsAdj>=-2?'D':'F';
  return{sessions,dominant,localH,dayLocal,dayName,dsAdj,dsRating,dsKey};
};

// V14.3: calendar helpers imported from the shared official schedule.

// ── V7.10.6: MARKET CONTEXT SYSTEM ──────────────────────────────────────────
// UTC-anchored intraday phases. Each phase has a stable character profile derived from
// historical liquidity / volatility / participant analysis. Boundaries chosen to align
// with major trading-desk shift changes (Frankfurt open, NY pre-market, NY open, etc.)
// and the well-documented "lunch lull" that compresses vol around 16:00-17:00 UTC.
const PHASE_PROFILES={
  ASIA_OPEN:{      // 22:00-04:00 UTC
    label:'Asia Open',flag:'🌏',color:'rgba(224,185,120,0.85)',
    liquidity:'MED',vol:'STEADY',
    character:'Tokyo + Sydney active. Range-bound moves typical, JPY-driven flows.',
    cautions:['Avoid breakout chasing — false breaks common in Asia','Watch for sudden moves on Japanese news prints'],
    bestFor:'Mean-reversion, range plays',
    // V8.7: Rich advisory context
    expectedMovement:'Tight ranges (15-30bps typical), occasional spike on Japan news. Expect mean reversion if price stretches more than ~25bps from open.',
    volumeGuidance:'NORMAL — size at 0.7-1.0x your standard. Don\'t go big; Asian moves can reverse on a single Tokyo headline.',
    deadzoneWarning:false,
    whatTaraDoes:'Tara biases toward DOWN/UP based on JPY flow patterns. Confidence levels usually moderate (60-72%). Patient setups, not breakouts.',
    coinFlipRisk:'LOW',
  },
  ASIA_LATE:{      // 04:00-07:00 UTC
    label:'Asia Late',flag:'🌒',color:'rgba(180,180,180,0.8)',
    liquidity:'LOW',vol:'COMPRESSING',
    character:'Tokyo winding down, Europe not yet awake. Thinnest liquidity of the day.',
    cautions:['Wide spreads — orders can slip badly','Stop hunts common in this window','One whale can move price disproportionately'],
    bestFor:'Patience until EU open',
    expectedMovement:'Whippy and thin. Single 50K+ trades can move price 20-40bps. Patterns from Tokyo session unwind. No reliable trend.',
    volumeGuidance:'PULL BACK — half-size or skip entirely. Edge is poor and slippage is real.',
    deadzoneWarning:true,
    whatTaraDoes:'Tara becomes more selective. Kalshi prices often misalign with reality, posteriors hover near 50%. Many windows resolve as SIT_OUT.',
    coinFlipRisk:'HIGH',
  },
  EU_PREOPEN:{     // 07:00-08:00 UTC
    label:'EU Pre-Open',flag:'🌅',color:'rgba(147,197,253,0.85)',
    liquidity:'MED',vol:'EXPANDING',
    character:'Frankfurt warming up. First directional pulse of European day.',
    cautions:['Don\'t fade the first move — EU desks set tone','Volume picking up rapidly'],
    bestFor:'Breakout setups starting to form',
    expectedMovement:'Volume ramps fast. Direction often establishes 7:30-8:00 UTC and persists through London open. 30-60bps moves common.',
    volumeGuidance:'NORMAL → SIZING UP — start at 1.0x; if direction is confirmed by 7:45, scale to 1.2x for confluent setups.',
    deadzoneWarning:false,
    whatTaraDoes:'Tara enters trend-following mode. Watches for early FGT alignment. Confidence ramps from 65% to 75%+ as London desks come online.',
    coinFlipRisk:'LOW',
  },
  EU_OPEN:{        // 08:00-12:00 UTC
    label:'EU Open',flag:'🌍',color:'rgba(96,165,250,0.9)',
    liquidity:'HIGH',vol:'EXPANDING',
    character:'London + Frankfurt + Paris fully active. Cleanest breakouts of the day.',
    cautions:['News-driven spikes possible','ECB / BOE comments can cause sudden reversals'],
    bestFor:'Trend-following, breakout trades',
    expectedMovement:'Strong directional moves (40-80bps), clean tape, low chop. Best WR window of the day historically. Reversals usually have clear catalysts.',
    volumeGuidance:'FULL SIZE — this is your prime window. Size at 1.0-1.3x for high-conviction setups; aggressive at 0.85+ Kalshi offer.',
    deadzoneWarning:false,
    whatTaraDoes:'Tara trades aggressively here. Lock thresholds drop slightly (faster confirmation). Most LARGE-tier entries fire in this window.',
    coinFlipRisk:'LOW',
  },
  NY_PREMARKET:{   // 12:00-13:30 UTC
    label:'NY Pre-Market',flag:'⏳',color:'#23B981',
    liquidity:'MED',vol:'COMPRESSING',
    character:'Calm before the storm. Coiling action ahead of NY open.',
    cautions:['Wait for NY open before sizing up','Pre-market moves often reverse at the open'],
    bestFor:'Observation, small probes only',
    expectedMovement:'Coiling, narrow ranges (10-25bps). Volume drops noticeably. Pre-market direction is often FADED at NY open.',
    volumeGuidance:'PULL BACK — half-size or skip. Edge is muddy because real direction emerges only after 13:30 UTC.',
    deadzoneWarning:true,
    whatTaraDoes:'Tara is conservative here. Many windows resolve SIT_OUT. Lock thresholds tighten — she waits for NY open to commit.',
    coinFlipRisk:'MEDIUM',
  },
  NY_OPEN:{        // 13:30-14:30 UTC
    label:'NY Open',flag:'🚀',color:'#23B981',
    liquidity:'EXTREME',vol:'EXPANDING',
    character:'Highest volatility window of the day. Major directional moves common.',
    cautions:['WIDER stops needed — normal stops will get hit','False breakouts then reversals frequent in first 15-20 min','Don\'t overcommit on first move'],
    bestFor:'High-conviction directional trades',
    expectedMovement:'EXPLOSIVE. 60-150bps moves, often 2-3 reversals in first 15 min. Volume spikes 5-10x. Late-window settlement direction usually opposite of first move.',
    volumeGuidance:'TIME IT — first 15 min size 0.5x (chop), 13:45-14:00 size 1.0-1.5x (direction establishes), 14:00-14:30 size 1.0x (continuation).',
    deadzoneWarning:false,
    whatTaraDoes:'Tara delays first lock by 60-90s into the window to let chop settle. Confidence levels can swing wildly. Best to trust her snapshot once it commits.',
    coinFlipRisk:'LOW',
  },
  NY_MORNING:{     // 14:30-16:00 UTC
    label:'NY Morning',flag:'☀️',color:'#23B981',
    liquidity:'HIGH',vol:'STEADY',
    character:'Strong directional follow-through after open establishes.',
    cautions:['Watch for ~15:30 UTC extension fading','Trend exhaustion signs appear here'],
    bestFor:'Trend-following continuation',
    expectedMovement:'Trend confirms direction set at NY open. 30-70bps moves with shallower retracements. Most reliable trend-following window of US session.',
    volumeGuidance:'FULL SIZE — confident continuation play. 1.0-1.2x for aligned trades. Watch for trend exhaustion near 15:30.',
    deadzoneWarning:false,
    whatTaraDoes:'Tara rides the post-open trend. High confidence (75-85%) on aligned-direction trades. Confluence wins are common here.',
    coinFlipRisk:'LOW',
  },
  NY_LUNCH:{       // 16:00-17:00 UTC
    label:'NY Lunch',flag:'🍽️',color:'#23B981',
    liquidity:'MED',vol:'COMPRESSING',
    character:'Lunch lull. Chop period with mean reversion to VWAP.',
    cautions:['Avoid breakout chasing — they fail here','Range-bound — fade extremes instead'],
    bestFor:'Mean-reversion, fading extremes',
    expectedMovement:'Range-bound chop (15-30bps). Most breakouts fail. Direction often reverses to VWAP. Worst hour for trend trading.',
    volumeGuidance:'PULL BACK — half-size or skip. WR drops 8-12 percentage points compared to morning/afternoon. Lunch chop is real.',
    deadzoneWarning:true,
    whatTaraDoes:'Tara becomes selective. Many SIT_OUTs. Mean-reversion plays only on extreme-range tags. Confidence drops to 60-70% range.',
    coinFlipRisk:'MEDIUM',
  },
  NY_AFTERNOON:{   // 17:00-20:00 UTC
    label:'NY Afternoon',flag:'🌤️',color:'#23B981',
    liquidity:'HIGH',vol:'STEADY',
    character:'Second wind. Often establishes the daily close direction.',
    cautions:['Watch for 19:30 UTC position-squaring','3pm ET reversals are a known pattern'],
    bestFor:'Continuation plays, late-day setups',
    expectedMovement:'Renewed directionality, often opposite morning bias if NY morning was overextended. 30-60bps moves. 19:00-19:30 UTC reversals are a known pattern.',
    volumeGuidance:'FULL SIZE — second-best window after EU open. 1.0-1.2x for aligned setups. Pull back after 19:30 to 0.7x.',
    deadzoneWarning:false,
    whatTaraDoes:'Tara confirms or fades the morning direction. Watches for the 3pm pattern (price flush at 19:00 UTC). High-conviction calls common.',
    coinFlipRisk:'LOW',
  },
  NY_CLOSE:{       // 20:00-21:00 UTC
    label:'NY Close',flag:'🌆',color:'#23B981',
    liquidity:'HIGH',vol:'STEADY',
    character:'Position-squaring distorts signals. Algo-heavy.',
    cautions:['Late-day reversals common','Avoid initiating new positions','Funds rebalancing creates artificial flow'],
    bestFor:'Cautious follow-through only',
    expectedMovement:'Algo-driven flow, position-squaring. Tape patterns become unreliable. Late reversals (LATE_REVERSAL pattern) peak in this window.',
    volumeGuidance:'PULL BACK — half-size only on already-trending continuations. Skip new entries. LATE_REVERSAL is your dominant loss pattern here.',
    deadzoneWarning:false,
    whatTaraDoes:'Tara distrusts late-window setups. Lock thresholds tighten. Late-window WHALE_SPIKE detection is on high alert.',
    coinFlipRisk:'MEDIUM',
  },
  AFTERHOURS:{     // 21:00-22:00 UTC
    label:'After Hours',flag:'🌙',color:'rgba(180,180,180,0.7)',
    liquidity:'LOW',vol:'COMPRESSING',
    character:'NY closed, Asia not yet open. Thin liquidity zone.',
    cautions:['One whale can move price','Stop hunts common','Wide spreads'],
    bestFor:'Patience until Asia',
    expectedMovement:'Thin and unpredictable. Single trades move price disproportionately. Worst liquidity of the day. WHALE_SPIKE losses concentrated here.',
    volumeGuidance:'SKIP — coin-flip territory. Edge essentially zero. Wait for Asia open at 22:00 UTC.',
    deadzoneWarning:true,
    whatTaraDoes:'Tara mostly sits out. Lock thresholds at maximum tightness. Confidence rarely exceeds 65%.',
    coinFlipRisk:'HIGH',
  },
};

// Returns the phase key for a given UTC hour:minute
// V10.7.78 — DST-AWARE PHASE KEY
//   Problem: Phase boundaries were hardcoded for EST (UTC-5).
//   During EDT (UTC-4, Mar-Nov), all NY session labels are 1 hour late.
//   US trading sessions were mislabeled → sessionWeights/DoW calibration mislabeled.
//
//   Fix: compute US DST offset dynamically.
//   US DST: second Sunday of March (clocks spring forward) → first Sunday of November (fall back)
//   When DST active: NY sessions start 1 hour earlier in UTC.
//   EDT = UTC-4 → NY Open = 12:30 UTC (not 13:30)
//   EST = UTC-5 → NY Open = 13:30 UTC
const _isUSDST=(now=new Date())=>{
  // DST is active when local time is ahead of standard time
  // Simple check: compare Jan offset (EST) vs current offset
  const jan=new Date(now.getFullYear(),0,1);
  const jul=new Date(now.getFullYear(),6,1);
  const stdOffset=Math.max(jan.getTimezoneOffset(),jul.getTimezoneOffset());
  return now.getTimezoneOffset()<stdOffset;
};

const getPhaseKey=(hUTC,mUTC)=>{
  const minOfDay=hUTC*60+mUTC;
  // V10.7.78: shift NY boundaries 60min earlier during EDT (DST active)
  const _dst=_isUSDST();
  const _nyShift=_dst?60:0; // EDT shifts NY sessions 60min earlier in UTC
  if(minOfDay>=22*60||minOfDay<4*60)return'ASIA_OPEN';
  if(minOfDay<7*60)return'ASIA_LATE';
  if(minOfDay<8*60)return'EU_PREOPEN';
  if(minOfDay<(12*60-_nyShift))return'EU_OPEN';
  if(minOfDay<(13*60+30-_nyShift))return'NY_PREMARKET';
  if(minOfDay<(14*60+30-_nyShift))return'NY_OPEN';
  if(minOfDay<(16*60-_nyShift))return'NY_MORNING';
  if(minOfDay<(17*60-_nyShift))return'NY_LUNCH';
  if(minOfDay<(20*60-_nyShift))return'NY_AFTERNOON';
  if(minOfDay<(21*60-_nyShift))return'NY_CLOSE';
  return'AFTERHOURS';
};

// Returns minutes until next phase transition + the next phase key.
const getNextPhaseTransition=(hUTC,mUTC)=>{
  const minOfDay=hUTC*60+mUTC;
  const _dst=_isUSDST();
  const _s=_dst?60:0; // DST shift
  const boundaries=[
    {at:4*60,phase:'ASIA_LATE'},
    {at:7*60,phase:'EU_PREOPEN'},
    {at:8*60,phase:'EU_OPEN'},
    {at:12*60-_s,phase:'NY_PREMARKET'},
    {at:13*60+30-_s,phase:'NY_OPEN'},
    {at:14*60+30-_s,phase:'NY_MORNING'},
    {at:16*60-_s,phase:'NY_LUNCH'},
    {at:17*60-_s,phase:'NY_AFTERNOON'},
    {at:20*60-_s,phase:'NY_CLOSE'},
    {at:21*60-_s,phase:'AFTERHOURS'},
    {at:22*60,phase:'ASIA_OPEN'},
  ];
  for(const b of boundaries){
    if(b.at>minOfDay)return{phase:b.phase,minutesUntil:b.at-minOfDay};
  }
  // Past 22:00 UTC, next is tomorrow's 04:00 (ASIA_LATE)
  return{phase:'ASIA_LATE',minutesUntil:(24*60-minOfDay)+(4*60)};
};

// ── COMPREHENSIVE MARKET CONTEXT ──
// Bundles phase, sessions, weekend status, funding timing, macro events into one object.
// Updates should fire at most once per minute since most fields are minute-grained.
const getMarketContext=(now=new Date())=>{
  const hUTC=now.getUTCHours();
  const mUTC=now.getUTCMinutes();
  const dayUTC=now.getUTCDay(); // 0=Sun..6=Sat
  const phaseKey=getPhaseKey(hUTC,mUTC);
  const phase=PHASE_PROFILES[phaseKey];
  const next=getNextPhaseTransition(hUTC,mUTC);
  const nextPhase=PHASE_PROFILES[next.phase];
  const sessions=getMarketSessions();
  const macro=getMacroEventState(now);
  // Weekend detection (crypto trades 24/7 but liquidity drops, news absorbers thin)
  // Friday 22:00 UTC → Sunday 22:00 UTC = weekend zone
  const isWeekend=(dayUTC===6)||(dayUTC===0)||(dayUTC===5&&hUTC>=22)||(dayUTC===0&&hUTC<22);
  const weekendApproaching=(dayUTC===5&&hUTC>=18&&hUTC<22); // Friday evening
  const sundayWindDown=(dayUTC===0&&hUTC>=18&&hUTC<22); // Sunday before Asia open
  // Binance perpetual funding reset: 00:00, 08:00, 16:00 UTC every 8h
  // Brief vol blip ~5 min before/after as funding pays out
  const minOfDay=hUTC*60+mUTC;
  const fundingTimes=[0,8*60,16*60,24*60];
  let fundingMinsUntil=Infinity;
  for(const ft of fundingTimes){
    const d=ft-minOfDay;
    if(d>=0&&d<fundingMinsUntil)fundingMinsUntil=d;
  }
  if(!Number.isFinite(fundingMinsUntil))fundingMinsUntil=Math.min(...fundingTimes.map(ft=>(ft-minOfDay+24*60)%(24*60)).filter(d=>d>0));
  const fundingResetSoon=fundingMinsUntil<=10;
  const fundingResetActive=fundingMinsUntil<=2; // within 2 min of reset
  // Build the active cautions list, prioritized: macro > funding > weekend > phase-specific
  const cautions=[];
  if(macro.state==='BLACKOUT'){
    cautions.push({severity:'critical',text:`${macro.event.name} releases in ${macro.minutesUntil}m — ${macro.event.impact} impact`});
    cautions.push({severity:'critical',text:'Avoid new positions — vol spike imminent'});
  } else if(macro.state==='OBSERVE'){
    cautions.push({severity:'critical',text:`${macro.event.name} releasing NOW — observe only, no trades`});
  } else if(macro.state==='ENHANCED'){
    cautions.push({severity:'warning',text:`${macro.event.name} just released — extreme vol expected for ~${Math.abs(macro.minutesUntil)}m`});
  }
  if(fundingResetActive){
    cautions.push({severity:'warning',text:`Funding rate reset NOW — brief vol blip likely`});
  } else if(fundingResetSoon){
    cautions.push({severity:'info',text:`Funding reset in ${fundingMinsUntil}m`});
  }
  if(weekendApproaching){
    cautions.push({severity:'warning',text:'Weekend approaching — gap risk increasing, position-squaring distorts signals'});
  }
  if(sundayWindDown){
    cautions.push({severity:'info',text:'Asia open in <4h — pre-positioning may begin'});
  }
  if(isWeekend&&!weekendApproaching&&!sundayWindDown){
    cautions.push({severity:'warning',text:'Weekend — thin liquidity, surprise news moves possible'});
  }
  // Phase-specific cautions if no higher-priority alert is active
  if(cautions.length<2&&phase.cautions){
    phase.cautions.slice(0,2).forEach(c=>cautions.push({severity:'info',text:c}));
  }
  return{
    phaseKey,
    phase,
    nextPhase:{...nextPhase,key:next.phase,minutesUntil:next.minutesUntil},
    sessions,
    macro,
    isWeekend,
    weekendApproaching,
    sundayWindDown,
    fundingMinsUntil,
    fundingResetSoon,
    fundingResetActive,
    cautions,
    dayName:['SUN','MON','TUE','WED','THU','FRI','SAT'][dayUTC],
  };
};

// V14.3: calendar helpers imported from the shared official schedule.

// Phase inference for legacy log entries that lack a session/phase tag.
//   Used by the Memory modal to backfill display data without mutating the entry.
const inferPhaseFromTimestamp=(timestamp)=>{
  if(!timestamp||!Number.isFinite(Number(timestamp)))return null;
  try{
    const d=new Date(Number(timestamp));
    if(isNaN(d.getTime()))return null;
    return getPhaseKey(d.getUTCHours(),d.getUTCMinutes());
  }catch(e){return null;}
};

// ═══════════════════════════════════════
// HOOKS
// ═══════════════════════════════════════
const useVelocity=(tickH,price,target)=>{const ref=useRef({v1s:0,v5s:0,v15s:0,v30s:0,accel:0,jerk:0,peakPnL:0,troughPnL:0,pnlSlope:0});const pnlH=useRef([]);useEffect(()=>{const iv=setInterval(()=>{if(!price||!target)return;const now=Date.now(),ticks=tickH.current||[];const ga=(ms)=>{const r=ticks.filter(t=>Math.abs((now-t.time)-ms)<2000);return r.length>0?r.reduce((a,b)=>a+b.p,0)/r.length:null;};const p1=ga(1000),p5=ga(5000),p15=ga(15000),p30=ga(30000);const v1s=p1?(price-p1):0,v5s=p5?(price-p5)/5:0,v15s=p15?(price-p15)/15:0,v30s=p30?(price-p30)/30:0;const cpnl=target>0?((price-target)/target)*10000:0;pnlH.current.push({pnl:cpnl,time:now});pnlH.current=pnlH.current.filter(p=>now-p.time<120000);const peakPnL=Math.max(...pnlH.current.map(p=>p.pnl),cpnl);const troughPnL=Math.min(...pnlH.current.map(p=>p.pnl),cpnl);const recent=pnlH.current.filter(p=>now-p.time<10000);const pnlSlope=recent.length>=3?recent[recent.length-1].pnl-recent[0].pnl:0;ref.current={v1s,v5s,v15s,v30s,accel:v5s-v15s,jerk:v1s-v5s,peakPnL,troughPnL,pnlSlope};},500);return()=>clearInterval(iv);},[price,target]);return ref;};

const useGlobalTape=(asset)=>{
  const tapeRef=useRef({coinbase:{buys:0,sells:0},binanceFutures:{buys:0,sells:0},bybit:{buys:0,sells:0},globalBuys:0,globalSells:0,globalImbalance:1,cbFlow:0,bnFlow:0,byFlow:0,divergence:0,whaleAlerts:[],binancePrice:0,bybitPrice:0});
  const ticksRef=useRef([]);
  const streakRef=useRef({dir:null,count:0,startTime:Date.now(),totalUSD:0,trades:[]}); // consecutive whale prints
  const[whaleLog,setWhaleLog]=useState([]);
  const[globalFlow,setGlobalFlow]=useState({imbalance:1,divergence:0,whaleAlert:null,feeds:0,deltaUSD:0});
  const[flowSignal,setFlowSignal]=useState({
    score:0,           // 0-100 actionable score
    label:'NEUTRAL',   // NOISE | EMERGING | STRONG | CONVICTION
    streakDir:null,    // 'BUY' | 'SELL' | null
    streakCount:0,     // consecutive whale prints same direction
    streakDuration:0,  // seconds
    streakUSD:0,       // total USD in streak
    oiContext:'',      // human-readable OI interpretation
    divergence:false,  // spot vs futures diverging
    netDelta30s:0,     // net buy-sell USD in last 30s
    netDelta90s:0,     // net buy-sell USD in last 90s
    trend:'flat',      // '↑ accelerating' | '↓ fading' | 'flat'
  });

  // V3.1: Sliding-window buy/sell tape at 5s / 15s / 30s / 60s.
  //       Each window: {buys, sells, total, buyPct, side}
  //       Lets the UI show buying-vs-selling pressure and whether it's accelerating
  //       (compare 60s → 30s → 15s → 5s pattern). Updates with the same 1s aggregation tick.
  const[tapeWindows,setTapeWindows]=useState({
    w5:{buys:0,sells:0,buyPct:50,side:null},
    w15:{buys:0,sells:0,buyPct:50,side:null},
    w30:{buys:0,sells:0,buyPct:50,side:null},
    w60:{buys:0,sells:0,buyPct:50,side:null},
  });

  useEffect(()=>{
    if(typeof window==='undefined')return;
    let wsBN=null,wsBY=null,feedCount=0;
    let _destroyed=false;
    let _bnRetryMs=1000,_byRetryMs=1000;
    let _bnRetryTimer=null,_byRetryTimer=null;

    const processWhalePrint=(alert)=>{
      const sk=streakRef.current;
      const now=Date.now();
      const streakWindow=120000;
      if(sk.dir===alert.side&&(now-sk.startTime)<streakWindow){
        sk.count++;sk.totalUSD+=alert.usd;sk.trades.push(alert);
      } else {
        sk.dir=alert.side;sk.count=1;sk.startTime=now;sk.totalUSD=alert.usd;sk.trades=[alert];
      }
      streakRef.current=sk;
    };

    // V10.8.6: reconnect helpers with exponential backoff.
    //   Binance and Bybit close connections every 24h or on network hiccup.
    //   Without reconnect, tape goes silent until page refresh. User reported this.
    const connectBN=()=>{
      if(_destroyed)return;
      try{
        const _cfg=ASSET_CONFIG[asset]||ASSET_CONFIG.BTC;
        const _bnSym=String(_cfg.binance||'BTCUSDT').toLowerCase();
        const _whaleFloor=Number(_cfg.whaleFloor)||100000;
        wsBN=new WebSocket(`wss://fstream.binance.com/ws/${_bnSym}@aggTrade`);
        wsBN.onopen=()=>{feedCount=Math.max(feedCount,1);_bnRetryMs=1000;};
        wsBN.onmessage=(e)=>{
          try{
            const d=JSON.parse(e.data);
            const price=parseFloat(d.p),qty=parseFloat(d.q),usd=price*qty,isBuy=!d.m,now=Date.now();
            ticksRef.current.push({p:price,s:qty,usd,t:isBuy?'B':'S',src:'bn',time:now});
            tapeRef.current.binancePrice=price;
            if(usd>_whaleFloor){
              const alert={src:'Binance',side:isBuy?'BUY':'SELL',size:qty,usd,price,time:now};
              tapeRef.current.whaleAlerts.push(alert);
              tapeRef.current.whaleAlerts=tapeRef.current.whaleAlerts.slice(-60);
              processWhalePrint(alert);
              setWhaleLog(prev=>[alert,...prev].slice(0,30));
            }
          }catch(er){}
        };
        wsBN.onerror=()=>{};
        wsBN.onclose=()=>{
          if(_destroyed)return;
          // Reconnect with backoff: 1s → 2s → 4s → 8s → max 30s
          _bnRetryMs=Math.min(30000,_bnRetryMs*2);
          _bnRetryTimer=setTimeout(connectBN,_bnRetryMs);
        };
      }catch(e){}
    };

    const connectBY=()=>{
      if(_destroyed)return;
      try{
        const _cfg2=ASSET_CONFIG[asset]||ASSET_CONFIG.BTC;
        const _bySym=_cfg2.bybit||'BTCUSDT';
        const _whaleFloor2=Number(_cfg2.whaleFloor)||100000;
        const _topic=`publicTrade.${_bySym}`;
        wsBY=new WebSocket('wss://stream.bybit.com/v5/public/linear');
        wsBY.onopen=()=>{feedCount=Math.max(feedCount,2);_byRetryMs=1000;wsBY.send(JSON.stringify({op:'subscribe',args:[_topic]}));};
        wsBY.onmessage=(e)=>{
          try{
            const msg=JSON.parse(e.data);
            if(msg.topic===_topic&&msg.data){
              msg.data.forEach(trade=>{
                const price=parseFloat(trade.p),qty=parseFloat(trade.v),usd=price*qty,isBuy=trade.S==='Buy',now=Date.now();
                ticksRef.current.push({p:price,s:qty,usd,t:isBuy?'B':'S',src:'by',time:now});
                tapeRef.current.bybitPrice=price;
                if(usd>_whaleFloor2){
                  const alert={src:'Bybit',side:isBuy?'BUY':'SELL',size:qty,usd,price,time:now};
                  tapeRef.current.whaleAlerts.push(alert);
                  tapeRef.current.whaleAlerts=tapeRef.current.whaleAlerts.slice(-60);
                  processWhalePrint(alert);
                  setWhaleLog(prev=>[alert,...prev].slice(0,30));
                }
              });
            }
          }catch(er){}
        };
        wsBY.onerror=()=>{};
        wsBY.onclose=()=>{
          if(_destroyed)return;
          _byRetryMs=Math.min(30000,_byRetryMs*2);
          _byRetryTimer=setTimeout(connectBY,_byRetryMs);
        };
      }catch(e){}
    };

    connectBN();
    connectBY();

    const aggIv=setInterval(()=>{
      const now=Date.now();
      ticksRef.current=ticksRef.current.filter(t=>now-t.time<90000); // keep 90s

      // Split into time windows
      let cbB=0,cbS=0,bnB=0,bnS=0,byB=0,byS=0;
      let net30=0,net90=0;
      ticksRef.current.forEach(t=>{
        const u=t.usd||(t.s*t.p);
        const sign=t.t==='B'?1:-1;
        if(now-t.time<30000)net30+=u*sign;
        if(now-t.time<90000)net90+=u*sign;
        if(t.src==='cb'){if(t.t==='B')cbB+=u;else cbS+=u;}
        else if(t.src==='bn'){if(t.t==='B')bnB+=u;else bnS+=u;}
        else if(t.src==='by'){if(t.t==='B')byB+=u;else byS+=u;}
      });
      const gB=cbB+bnB+byB,gS=cbS+bnS+byS;
      const gI=gS===0?(gB>0?2:1):gB/gS;
      const cbF=(cbB+cbS)>0?(cbB-cbS)/(cbB+cbS):0;
      const bnF=(bnB+bnS)>0?(bnB-bnS)/(bnB+bnS):0;
      const byF=(byB+byS)>0?(byB-byS)/(byB+byS):0;
      // Spot vs futures divergence: cbF opposite to (bnF+byF)/2
      const futuresFlow=(bnF+byF)/2;
      const div=(gB+gS)>50000&&Math.abs(cbF)>0.2&&Math.abs(futuresFlow)>0.2&&Math.sign(cbF)!==Math.sign(futuresFlow)?Math.sign(futuresFlow):-0;
      tapeRef.current={...tapeRef.current,coinbase:{buys:cbB,sells:cbS},binanceFutures:{buys:bnB,sells:bnS},bybit:{buys:byB,sells:byS},globalBuys:gB,globalSells:gS,globalImbalance:gI,cbFlow:cbF,bnFlow:bnF,byFlow:byF,divergence:div};
      const rW=tapeRef.current.whaleAlerts.find(w=>now-w.time<5000);
      setGlobalFlow({imbalance:gI,divergence:div,whaleAlert:rW||null,feeds:feedCount,deltaUSD:gB-gS});

      // V3.1: Sliding-window buy/sell pressure at 5s / 15s / 30s / 60s.
      //       Each window aggregates buy/sell USD across all venues (Coinbase + Binance + Bybit).
      //       Designed for the UI tape strip — lets users see acceleration/deceleration of flow.
      const _bucket=(seconds)=>{
        let b=0,s=0;
        const cutoff=now-seconds*1000;
        ticksRef.current.forEach(t=>{
          if(t.time<cutoff)return;
          const u=t.usd||(t.s*t.p);
          if(t.t==='B')b+=u;else s+=u;
        });
        const total=b+s;
        const buyPct=total>0?(b/total)*100:50;
        const side=total<1000?null:(buyPct>=55?'BUY':buyPct<=45?'SELL':null);
        return{buys:b,sells:s,buyPct,side};
      };
      setTapeWindows({
        w5:_bucket(5),
        w15:_bucket(15),
        w30:_bucket(30),
        w60:_bucket(60),
      });

      // ── FLOW SIGNAL COMPUTATION ─────────────────────────────────────────
      const sk=streakRef.current;
      const streakAge=(now-sk.startTime)/1000;
      const isStreakFresh=sk.count>=1&&streakAge<120;

      // Expire streak if no activity for 2 min
      if(streakAge>120){streakRef.current={dir:null,count:0,startTime:now,totalUSD:0,trades:[]};}

      // Net delta trend: compare 30s vs 90s (is flow accelerating or fading?)
      let trend='flat';
      if(Math.abs(net30)>10000&&Math.abs(net90)>10000){
        const rate30=net30/30,rate90=net90/90;
        if(Math.sign(rate30)===Math.sign(rate90)&&Math.abs(rate30)>Math.abs(rate90)*1.3)trend=`${net30>0?'↑':'↓'} accel`;
        else if(Math.sign(rate30)===Math.sign(rate90)&&Math.abs(rate30)<Math.abs(rate90)*0.7)trend=`${net30>0?'↑':'↓'} fading`;
        else trend=`${net30>0?'↑':'↓'} steady`;
      }

      // Score components (0-100)
      const imbalanceScore=Math.min(40,Math.abs(gI-1)*30); // flow pressure
      const streakScore=isStreakFresh?Math.min(40,(sk.count-1)*12):0; // streak conviction
      const accelScore=trend.includes('accel')?15:trend.includes('fading')?-10:0;
      const divPenalty=div!==0?-20:0; // divergence = basis trade = not directional
      const rawScore=Math.max(0,Math.min(100,imbalanceScore+streakScore+accelScore+divPenalty));

      const label=rawScore<25?'NOISE':rawScore<50?'EMERGING':rawScore<75?'STRONG':'CONVICTION';
      const isDivergence=div!==0;
      const streakDir=isStreakFresh?sk.dir:null;

      setFlowSignal({
        score:rawScore,label,streakDir,
        streakCount:isStreakFresh?sk.count:0,
        streakDuration:isStreakFresh?Math.round(streakAge):0,
        streakUSD:isStreakFresh?sk.totalUSD:0,
        divergence:isDivergence,
        netDelta30s:net30,netDelta90s:net90,trend,
        oiContext:'', // filled by component using bloomberg
      });
    },1000);

    return()=>{
      _destroyed=true;
      clearInterval(aggIv);
      if(_bnRetryTimer)clearTimeout(_bnRetryTimer);
      if(_byRetryTimer)clearTimeout(_byRetryTimer);
      if(wsBN)try{wsBN.onclose=null;wsBN.close();}catch(_){}
      if(wsBY)try{wsBY.onclose=null;wsBY.close();}catch(_){}
    };
  },[asset]);

  return{tapeRef,globalFlow,ticksRef,whaleLog,flowSignal,tapeWindows};
};

// V10.8.7: expose tapeRef globally so useWhaleAlert can read whale prints
// without prop drilling. Set in TaraApp after useGlobalTape mounts.
// window._taraGlobalTapeRef is read by useWhaleAlert every 5s.

// ── V8.1: useMovementRisk — REAL-TIME RISK PULSE ─────────────────────────────
// Combines vol acceleration, volume spikes, tape imbalance, whale density into a 0-100
// risk score. Updates every 2s. All inputs are existing refs — no new data fetches.
// Returns: { level: 'CALM'|'NORMAL'|'ELEVATED'|'EXTREME', score: 0-100,
//            dirBias: 'UP'|'DOWN'|null, reasons: [{tag, weight}], predictive: '...' }
const useMovementRisk=(velocityRef,tapeRef,ticksRef,whaleLog,regimeRef)=>{
  const ref=useRef({level:'CALM',score:0,dirBias:null,reasons:[],predictive:null});
  const baselineRef=useRef({accel:0.5,volume:0,sampleAt:0});
  const[snapshot,setSnapshot]=React.useState({level:'CALM',score:0,dirBias:null,reasons:[],predictive:null});
  React.useEffect(()=>{
    const tick=()=>{
      try{
        const now=Date.now();
        const v=velocityRef?.current||{};
        const tape=tapeRef?.current||{};
        const ticks=ticksRef?.current||[];
        const whales=whaleLog||[];
        let score=0;
        const reasons=[];
        let dirBias=null;
        // ── Vol acceleration vs rolling baseline ──
        // Baseline = exponentially-weighted moving avg of |accel|. Updated each tick at 0.05 weight.
        const _accel=Math.abs(Number(v.accel)||0);
        if(now-baselineRef.current.sampleAt>1500){
          baselineRef.current.accel=baselineRef.current.accel*0.95+_accel*0.05;
          baselineRef.current.sampleAt=now;
        }
        const _accelBase=Math.max(0.5,baselineRef.current.accel);
        const _accelRatio=_accel/_accelBase;
        if(_accelRatio>=2.5){
          score+=30;
          reasons.push({tag:`accel ${_accelRatio.toFixed(1)}x baseline`,weight:30});
        } else if(_accelRatio>=1.8){
          score+=18;
          reasons.push({tag:`accel ${_accelRatio.toFixed(1)}x`,weight:18});
        } else if(_accelRatio<=0.4){
          // Compression: low vol relative to baseline. Often precedes breakouts.
          score+=10;
          reasons.push({tag:`compressed ${_accelRatio.toFixed(2)}x`,weight:10});
        }
        // ── Volume spike: last 30s vs last 5min average ──
        const _last30s=ticks.filter(t=>now-t.time<30000);
        const _last5min=ticks.filter(t=>now-t.time<300000);
        if(_last30s.length>=5&&_last5min.length>=20){
          const _vol30=_last30s.reduce((s,t)=>s+(t.usd||t.sz||1),0);
          const _vol5min=_last5min.reduce((s,t)=>s+(t.usd||t.sz||1),0);
          const _per30=_vol30;
          const _avg30=_vol5min/(300/30); // expected 30s slice of 5min total
          if(_avg30>0){
            const _volRatio=_per30/_avg30;
            if(_volRatio>=3){
              score+=25;
              reasons.push({tag:`volume spike ${_volRatio.toFixed(1)}x`,weight:25});
            } else if(_volRatio>=2){
              score+=15;
              reasons.push({tag:`volume ${_volRatio.toFixed(1)}x avg`,weight:15});
            }
          }
        }
        // ── Tape imbalance ──
        const _imb=Number(tape.globalImbalance)||1;
        if(_imb>=3){
          score+=20;
          dirBias='UP';
          reasons.push({tag:`buy pressure ${_imb.toFixed(1)}x`,weight:20});
        } else if(_imb<=0.33){
          score+=20;
          dirBias='DOWN';
          reasons.push({tag:`sell pressure ${(1/_imb).toFixed(1)}x`,weight:20});
        } else if(_imb>=2){
          score+=10;
          dirBias='UP';
          reasons.push({tag:`buy lean ${_imb.toFixed(1)}x`,weight:10});
        } else if(_imb<=0.5){
          score+=10;
          dirBias='DOWN';
          reasons.push({tag:`sell lean ${(1/_imb).toFixed(1)}x`,weight:10});
        }
        // ── Whale density: prints in last 60s vs last 10min baseline ──
        const _whale60s=whales.filter(w=>now-w.time<60000);
        const _whale10min=whales.filter(w=>now-w.time<600000);
        if(_whale60s.length>=2){
          const _per60=_whale60s.length;
          const _avg60=_whale10min.length/(600/60);
          if(_avg60>0&&_per60/_avg60>=2.5){
            score+=15;
            reasons.push({tag:`${_per60} whales in 60s`,weight:15});
            // Whale directional cluster
            const _whaleBuys=_whale60s.filter(w=>w.side==='BUY').length;
            const _whaleSells=_whale60s.filter(w=>w.side==='SELL').length;
            if(_whaleBuys>=_whaleSells*2)dirBias=dirBias||'UP';
            else if(_whaleSells>=_whaleBuys*2)dirBias=dirBias||'DOWN';
          }
        }
        // ── Choppy regime amplifier ──
        const regime=regimeRef?.current||null;
        if((regime==='RANGE_BOUND'||regime==='CHOP'||regime==='RANGE'||regime==='RANGE-CHOP')&&score>=40){
          score+=8;
          reasons.push({tag:'chop amplifies risk',weight:8});
        }
        // ── Predictive readout ──
        let predictive=null;
        if(score>=60&&dirBias){
          predictive=`${dirBias} move likely next 30-60s`;
        } else if(_accelRatio<=0.5&&_imb>1.5&&_imb<2&&score<40){
          predictive='compressed — breakout watch';
        } else if(score>=70){
          predictive='extreme conditions — stand back';
        } else if(score<=15){
          predictive='calm — favorable for normal positions';
        }
        // ── Level classification ──
        score=Math.min(100,Math.max(0,score));
        const level=score<20?'CALM':score<45?'NORMAL':score<70?'ELEVATED':'EXTREME';
        const next={level,score:Math.round(score),dirBias,reasons,predictive};
        ref.current=next;
        setSnapshot(next);
      }catch(_){}
    };
    tick();
    const iv=setInterval(tick,2000);
    return()=>clearInterval(iv);
  },[velocityRef,tapeRef,ticksRef,whaleLog,regimeRef]);
  return snapshot;
};
// ── V8.5: LOSS-PATTERN CLASSIFIER ────────────────────────────────────────────
// Takes a resolved LOSS trade with path metrics + whale/macro context and returns
// one of six pattern labels. Used to (a) show user "your most common loss type"
// and (b) skip gradient-descent punishment on shock losses where the signals
// were correct but the world changed.
//
// Categories (mutually exclusive, order matters — first match wins):
//   WHALE_SPIKE      — large whale print >$200K within 60s of reversal/close
//   MACRO_SHOCK      — geoRisk peak ≥0.5 OR major news event during window
//   LATE_REVERSAL    — was favorable in last third, then flipped
//   MID_REVERSAL     — was favorable in middle third, then faded
//   EARLY_PEAK       — was favorable in first third, then sustained loss
//   WRONG_FROM_START — never above water (MFE ≤ 0)
const classifyLossPattern=(trade)=>{
  if(!trade||trade.result!=='LOSS')return null;
  const mfe=trade.maxFavorableExcursionBps;
  const reversed=!!trade.reversalFromPeak;
  const peakPos=trade.peakPosition;
  const last60s=trade.last60sDriftBps;
  // Shock detectors — these override path-pattern even if path looks like reversal,
  // because the cause was external.
  const hasContrayWhale=trade.windowContraryWhaleUsd&&trade.windowContraryWhaleUsd>=200000
    &&trade.windowContraryWhaleClockSec!=null
    &&trade.windowContraryWhaleClockSec<=60; // within last 60s of window
  if(hasContrayWhale)return'WHALE_SPIKE';
  const hadShock=(trade.windowMaxGeoRisk||0)>=0.5||!!trade.windowHadMacroEvent;
  if(hadShock)return'MACRO_SHOCK';
  // V10.4.2c: NEW DATA-DRIVEN CATEGORIES (audit found 82% of losses falling
  //   into WRONG_FROM_START because MFE tracking is sparse). These use fields
  //   we reliably have (kalshiAtLock, closingGapBps, dir) and split the catchall
  //   into actionable buckets the engine can actually learn from.
  const _gap=trade.closingGapBps;
  const _dir=trade.dir;
  const _kLock=trade.kalshiAtLock;
  // Compute loss margin in bps (how far on the wrong side at close)
  const _lossMarginBps=_gap!=null
    ?(_dir==='DOWN'?_gap:-_gap) // for DOWN, positive gap = lost (price above strike)
    :null;
  // EXPENSIVE_ENTRY_LOSS: k$50+ at lock, lost. This is the V10.4.1 target bucket
  //   — expensive entries that don't justify their cost. Audit showed these
  //   bleed EV regardless of WR.
  if(_kLock!=null){
    const _kForDir=_dir==='UP'?_kLock:(100-_kLock);
    if(_kForDir>=60)return'EXPENSIVE_ENTRY_LOSS';
  }
  // NARROW_SETTLEMENT_LOSS: lost by less than 3bps at close. These are the
  //   BRTI 60s-averaging victims — would be addressable by V10.5.1 cross-exchange
  //   approximation. Tara's signal was directionally right; the trimmed avg
  //   beat us in the final minute.
  if(_lossMarginBps!=null&&_lossMarginBps>0&&_lossMarginBps<3)return'NARROW_SETTLEMENT_LOSS';
  // Path-based (existing) — uses MFE if available
  if(mfe!=null&&mfe>0){
    if(reversed){
      if(peakPos==='LATE')return'LATE_REVERSAL';
      if(peakPos==='MID')return'MID_REVERSAL';
      if(peakPos==='EARLY')return'EARLY_PEAK';
    }
    // Edge case: meaningful MFE but no clean peakPos — fall back on last60s drift sign
    if(last60s!=null&&Math.abs(last60s)>=10){
      return last60s*(trade.dir==='UP'?1:-1)<0?'LATE_REVERSAL':'EARLY_PEAK';
    }
  }
  // WRONG_DIRECTION: lost by >15bps. Signal genuinely picked the wrong side.
  //   Audit gives the ML retrain a clean target — these are where the model
  //   actually missed, not where settlement caught us.
  if(_lossMarginBps!=null&&_lossMarginBps>=15)return'WRONG_DIRECTION';
  return'WRONG_FROM_START'; // catchall fallback
};
const LOSS_PATTERN_LABELS={
  WRONG_FROM_START:{label:'Wrong from start',color:'#E8455E',icon:'✗',hint:'Signal read was off entry. Tighten entry filter.'},
  EARLY_PEAK:     {label:'Early peak fade',  color:'#23B981',icon:'⤴',hint:'Peaked in first third then faded. Consider quicker take-profit.'},
  MID_REVERSAL:   {label:'Mid-window reversal',color:'#23B981',icon:'⤵',hint:'Was winning at midpoint. Watch for vol shifts.'},
  LATE_REVERSAL:  {label:'Late reversal',    color:'#E8455E',icon:'⚡',hint:'Winning until last minute. Tighter trailing stop in final 90s.'},
  WHALE_SPIKE:    {label:'Whale spike',      color:'rgba(168,85,247,0.85)',icon:'🐋',hint:'Large contrary print near close. Not your read — circumstance.'},
  MACRO_SHOCK:    {label:'Macro shock',      color:'rgba(168,85,247,0.85)',icon:'⚠',hint:'External event spike during window. Not your read — circumstance.'},
  // V10.4.2c — new data-driven categories
  EXPENSIVE_ENTRY_LOSS:{label:'Expensive entry',color:'rgba(212,162,76,0.85)',icon:'💸',hint:'k$60+ entry that didn\'t justify cost. V10.4.1 delay gate target.'},
  NARROW_SETTLEMENT_LOSS:{label:'Narrow settlement',color:'rgba(96,165,250,0.85)',icon:'📏',hint:'Lost by <3bps. BRTI averaging hit us. V10.5.1 target.'},
  WRONG_DIRECTION:{label:'Wrong direction',color:'#E8455E',icon:'❌',hint:'Lost by >15bps. Genuine signal miss — ML retrain target.'},
};
// Patterns where signals were correct but external shock caused the loss.
//   Used by applyTradeLearning to skip/dampen gradient descent — don't punish
//   correct signals because the world moved unpredictably.
const SHOCK_LOSS_PATTERNS=new Set(['WHALE_SPIKE','MACRO_SHOCK']);

// ── V8.7: TRADE LOG NORMALIZATION ──────────────────────────────────────────
// One-time backfill of derivable fields on legacy entries. We don't invent missing
// data — only compute fields we can derive from existing fields. Stamps _normalizedAt
// on each entry so the pass is idempotent.
//
// Backfill rules:
//   - closingGapBps: derived from strike + closingPrice if missing
//   - kalshiAtClose: keeps existing if present, else null (can't recover)
//   - lossPattern: only set on LOSS where we have enough data; otherwise null
//   - asset: defaults to BTC if missing (V6.5.8 added asset tagging)
//   - windowId: backfilled from time + windowType using the existing logic
//
// Returns null if no changes needed (so we don't trigger an unnecessary write).
const normalizeCallLogEntry=(e)=>{
  if(!e||e._normalizedAt)return null; // already normalized
  const out={...e};
  let changed=false;
  // 1. windowId backfill (legacy V5.6.8 entries lacked it)
  if(!out.windowId&&out.time&&out.windowType){
    const winMs=out.windowType==='15m'?900000:300000;
    const bucket=Math.floor(out.time/winMs)*winMs;
    out.windowId=`${out.windowType}-${new Date(bucket).toISOString()}`;
    changed=true;
  }
  // 2. asset default
  if(!out.asset){out.asset='BTC';changed=true;}
  // 3. closingGapBps derive
  if(out.result&&(out.result==='WIN'||out.result==='LOSS')&&out.strike>0&&out.closingPrice>0&&out.closingGapBps==null){
    out.closingGapBps=Math.round(((out.closingPrice-out.strike)/out.strike)*10000*100)/100;
    changed=true;
  }
  // 4. outcomeDir derive (for trades that have closingPrice + strike but no outcomeDir)
  if(out.result&&out.strike>0&&out.closingPrice>0&&!out.outcomeDir&&out.closingPrice!==out.strike){
    out.outcomeDir=out.closingPrice>out.strike?'UP':'DOWN';
    changed=true;
  }
  // 5. Stamp normalized timestamp regardless — even no-op pass marks the entry as seen
  out._normalizedAt=Date.now();
  return changed?out:{...out,_normalizedAt:out._normalizedAt}; // always return with stamp
};

// V134: HPotter Future Grand Trend — multi-timeframe forecast
// Translated from PineScript v5 (Apr 2022 by HPotter)
// 
// Algorithm:
//   t[i] = 0.9 * t[i-length] + 0.1 * src[i] + (0.9*t[i-length] - 0.9*t[i-length*2])
//   fcast = t[current] + (t[current] - t[current-forecast])  (linear extrapolation)
//
// Returns: { forecastDir: 'UP'|'DOWN'|'NEUTRAL', forecastBps: signed bps, valid: bool }
// Returns valid=false if insufficient history
// V6.2.0: FUTURE TREND CHANNEL (ChartPrime port). Pure JS implementation of the user's
//   TradingView Pine script. Identifies trend regime via close crossing ATR-band around
//   SMA(100), and projects channel midline forward 50 bars. Inputs: candles newest-first,
//   length=100 SMA period, multi=3 channel-band ATR multiplier, extend=50 forward bars.
//   Returns: { trendDir, channelHigh, channelLow, channelPos (0-1), futurePriceBps, slopeBpsPerBar, valid }
const computeFutureTrendChannel=(candles,length=100,multi=3,extend=50)=>{
  if(!candles||candles.length<60)return{trendDir:'NEUTRAL',channelPos:0.5,futurePriceBps:0,slopeBpsPerBar:0,valid:false,reason:'insufficient'};
  const bars=candles.slice().reverse(); // oldest first
  const n=bars.length;
  // Adapt length to available data
  const L=Math.min(length,Math.floor(n*0.7));
  if(L<20)return{trendDir:'NEUTRAL',channelPos:0.5,futurePriceBps:0,slopeBpsPerBar:0,valid:false,reason:'too-short'};
  // Compute true range and ATR(200) — but cap at available history
  const atrLen=Math.min(200,Math.max(20,Math.floor(n*0.6)));
  const tr=new Array(n);
  for(let i=0;i<n;i++){
    if(i===0){tr[i]=bars[i].h-bars[i].l;continue;}
    const prev=bars[i-1];
    tr[i]=Math.max(bars[i].h-bars[i].l,Math.abs(bars[i].h-prev.c),Math.abs(bars[i].l-prev.c));
  }
  // ATR via SMA of true range (Pine's ta.atr is RMA but SMA close enough for our needs)
  const atrArr=new Array(n);
  let trSum=0;
  for(let i=0;i<n;i++){
    trSum+=tr[i];
    if(i>=atrLen)trSum-=tr[i-atrLen];
    atrArr[i]=i>=atrLen-1?trSum/atrLen:trSum/(i+1);
  }
  // ATR signal = ta.highest(atr, 100) — rolling max of ATR over 100 bars
  const highestLen=Math.min(100,Math.floor(n*0.5));
  const atrSig=new Array(n);
  for(let i=0;i<n;i++){
    let m=atrArr[i];
    for(let j=Math.max(0,i-highestLen+1);j<i;j++)if(atrArr[j]>m)m=atrArr[j];
    atrSig[i]=m;
  }
  // SMA(close, length)
  const smaArr=new Array(n);
  let smaSum=0;
  for(let i=0;i<n;i++){
    smaSum+=bars[i].c;
    if(i>=L)smaSum-=bars[i-L].c;
    smaArr[i]=i>=L-1?smaSum/L:smaSum/(i+1);
  }
  // Walk through bars tracking trend state
  let trend=null,lastFlipIdx=0,lastFlipMid=null;
  for(let i=1;i<n;i++){
    const upper=smaArr[i]+atrSig[i];
    const lower=smaArr[i]-atrSig[i];
    const upperPrev=smaArr[i-1]+atrSig[i-1];
    const lowerPrev=smaArr[i-1]-atrSig[i-1];
    const crossUp=bars[i].c>upper&&bars[i-1].c<=upperPrev;
    const crossDn=bars[i].c<lower&&bars[i-1].c>=lowerPrev;
    if(crossUp){trend=true;lastFlipIdx=i;lastFlipMid=(bars[i].h+bars[i].l)/2;}
    if(crossDn){trend=false;lastFlipIdx=i;lastFlipMid=(bars[i].h+bars[i].l)/2;}
  }
  if(trend===null)return{trendDir:'NEUTRAL',channelPos:0.5,futurePriceBps:0,slopeBpsPerBar:0,valid:false,reason:'no-flip-yet'};
  const trendDir=trend?'UP':'DOWN';
  // Current channel bands (using hl2 ± atr*multi like Pine's visual band)
  const lastBar=bars[n-1];
  const lastHl2=(lastBar.h+lastBar.l)/2;
  const lastAtr=atrSig[n-1];
  const channelHigh=lastHl2+lastAtr*multi;
  const channelLow=lastHl2-lastAtr*multi;
  const channelPos=Math.max(0,Math.min(1,(lastBar.c-channelLow)/(channelHigh-channelLow||1)));
  // Future projection — slope from flip point to current SMA20-of-mid
  const sma20Window=Math.min(20,n-lastFlipIdx);
  let mid20Sum=0;
  for(let j=Math.max(0,n-sma20Window);j<n;j++){
    mid20Sum+=(bars[j].h+bars[j].l)/2;
  }
  const currentMid=mid20Sum/sma20Window;
  const flipMid=lastFlipMid||currentMid;
  const barsSinceFlip=Math.max(1,n-1-lastFlipIdx);
  const slopePerBar=(currentMid-flipMid)/barsSinceFlip;
  const futurePrice=currentMid+slopePerBar*extend;
  const currentClose=lastBar.c;
  const futurePriceBps=((futurePrice-currentClose)/currentClose)*10000;
  const slopeBpsPerBar=(slopePerBar/currentClose)*10000;
  return{trendDir,channelHigh:Math.round(channelHigh),channelLow:Math.round(channelLow),channelPos:Math.round(channelPos*100)/100,futurePriceBps:Math.round(futurePriceBps*10)/10,slopeBpsPerBar:Math.round(slopeBpsPerBar*100)/100,barsSinceFlip,valid:true};
};

// ═══════════════════════════════════════════════════════════════════════════
// V10.7.36 — REGIME CLASSIFIER REBUILD
// ═══════════════════════════════════════════════════════════════════════════
// Replaces ad-hoc drift/ATR thresholds with:
//   1. ADX (Master Regime Classifier V6.2 — industry standard trend strength)
//   2. BBW percentile rank (relative volatility, adapts to history)
//   3. Whipsaw filter (EMA-20 crosses ≥3 in 10 bars — blocks false trends)
//   4. AiBitcoinTrend 4-bucket: Advance/Decline/Accumulation/Distribution
//   5. VWAP with stdev bands (session-anchored overbought/oversold)
//   6. ATRP (short vs long ATR ratio for vol expansion detection)
// Source Pine Scripts: Master Regime Classifier V6.2, AiBitcoinTrend
//   Regime Classifier Oscillator, VWAP Stdev Bands v2 Mod.
// ═══════════════════════════════════════════════════════════════════════════

// ── ADX (Wilder's Average Directional Index, 14-period) ──────────────────
// Pine Script: [diPlus, diMinus, adx] = ta.dmi(adxLen, adxLen)
// Returns adx (0-100). > 20 = trending, < 18 = chop.
const computeADX=(bars,period=14)=>{
  if(!bars||bars.length<period*2+2)return{adx:0,diPlus:0,diMinus:0,valid:false};
  // V10.7.38c: reverse to oldest-first so DM direction is computed correctly
  const b=bars.slice().reverse(); // b[0]=oldest, b[n-1]=newest
  const n=b.length;
  // Wilder smoothing factor
  const k=1/period;
  let smoothTR=0,smoothPDM=0,smoothNDM=0;
  // Initialize over first `period` bars (b is oldest-first)
  for(let i=1;i<=period;i++){
    const h=b[i].h,l=b[i].l,pc=b[i-1].c;
    const tr=Math.max(h-l,Math.abs(h-pc),Math.abs(l-pc));
    const pdm=Math.max(h-b[i-1].h,0);
    const ndm=Math.max(b[i-1].l-l,0);
    const usePDM=pdm>ndm?pdm:0;
    const useNDM=ndm>pdm?ndm:0;
    smoothTR+=tr;smoothPDM+=usePDM;smoothNDM+=useNDM;
  }
  let dx=0;
  let adxSmooth=0;
  const dxArr=[];
  for(let i=period+1;i<n;i++){
    const h=b[i].h,l=b[i].l,pc=b[i-1].c;
    const tr=Math.max(h-l,Math.abs(h-pc),Math.abs(l-pc));
    const pdm=Math.max(h-b[i-1].h,0);
    const ndm=Math.max(b[i-1].l-l,0);
    const usePDM=pdm>ndm?pdm:0;
    const useNDM=ndm>pdm?ndm:0;
    // Wilder smooth: prev*(1-1/p) + new*(1/p) = prev - prev/p + new
    smoothTR=smoothTR-smoothTR/period+tr;
    smoothPDM=smoothPDM-smoothPDM/period+usePDM;
    smoothNDM=smoothNDM-smoothNDM/period+useNDM;
    const diP=smoothTR>0?(100*smoothPDM/smoothTR):0;
    const diN=smoothTR>0?(100*smoothNDM/smoothTR):0;
    const diSum=diP+diN;
    dx=diSum>0?(100*Math.abs(diP-diN)/diSum):0;
    dxArr.push(dx);
    // Smooth DX into ADX after first `period` DX values
    if(dxArr.length<=period){
      adxSmooth+=dx/period;
    }else{
      adxSmooth=adxSmooth-adxSmooth/period+dx/period;
    }
  }
  return{adx:Math.round(adxSmooth*10)/10,diPlus:0,diMinus:0,valid:true};
};

// ── BBW Percentile Rank ───────────────────────────────────────────────────
// BBW = (stdev(close,20)*4) / sma(close,20) — algebraically same as BB width
// PercentRank over last `rankLen` bars: 0-100, >80 = high vol (from MRC V6.2)
const _bbwHistory=[];
const _atrpHistory=[];
const computeBBWRank=(closes,historyRef,rankLen=100)=>{
  if(!closes||closes.length<22)return{bbw:0,rank:0};
  // closes is newest-first (liveHistory order). Use first 20 = most recent 20 bars.
  const recent20=closes.slice(0,20);
  const sma20=recent20.reduce((a,b)=>a+b,0)/20;
  const variance=recent20.reduce((s,c)=>s+Math.pow(c-sma20,2),0)/20;
  const stdev20=Math.sqrt(variance);
  const bbw=(stdev20*4)/sma20;
  historyRef.push(bbw);
  if(historyRef.length>rankLen+50)historyRef.shift();
  const window=historyRef.slice(-rankLen);
  const below=window.filter(v=>v<bbw).length;
  const rank=Math.round((below/window.length)*100);
  return{bbw:Math.round(bbw*10000)/10000,rank};
};

// ── Whipsaw Filter ────────────────────────────────────────────────────────
// Counts EMA-20 crossovers + crossunders in last 10 bars.
// ≥ 3 = whipsawing (Master Regime Classifier V6.2)
const computeEMA=(arr,period)=>{
  if(!arr||arr.length<period)return arr?.[arr.length-1]||0;
  const k=2/(period+1);
  let ema=arr.slice(0,period).reduce((a,b)=>a+b,0)/period;
  for(let i=period;i<arr.length;i++){ema=arr[i]*k+ema*(1-k);}
  return ema;
};
const computeWhipsawCount=(bars,emaPeriod=20,lookback=10)=>{
  if(!bars||bars.length<emaPeriod+lookback)return 0;
  const closes=bars.map(b=>b.c);
  const needed=Math.min(bars.length,emaPeriod+lookback+5);
  // V10.7.44 FIX: closes is newest-first. slice(-needed) was the OLDEST bars.
  // slice(0,needed).reverse() gives NEWEST bars in oldest-first order for EMA math.
  const recentCloses=closes.slice(0,needed).reverse();
  const n=recentCloses.length;
  // Build EMA series
  let ema=recentCloses.slice(0,emaPeriod).reduce((a,b)=>a+b,0)/emaPeriod;
  const k=2/(emaPeriod+1);
  // emaArr[i] = EMA at recentCloses[emaPeriod + i] (i=0 is first full EMA bar)
  const emaArr=[];
  for(let i=emaPeriod;i<n;i++){
    ema=recentCloses[i]*k+ema*(1-k);
    emaArr.push({ema,price:recentCloses[i]});
  }
  // Count crossovers/crossunders in last `lookback` bars of emaArr
  let crosses=0;
  const len=emaArr.length;
  const startIdx=Math.max(1,len-lookback);
  for(let i=startIdx;i<len;i++){
    const cNow=emaArr[i].price,cPrev=emaArr[i-1].price;
    const eNow=emaArr[i].ema,ePrev=emaArr[i-1].ema;
    const crossOver=cPrev<=ePrev&&cNow>eNow;
    const crossUnder=cPrev>=ePrev&&cNow<eNow;
    if(crossOver||crossUnder)crosses++;
  }
  return crosses;
};

// ── Median Filter (AiBitcoinTrend) ───────────────────────────────────────
// Sorts last `length` closes and returns the median. More robust than SMA
// for trend detection — not skewed by price spikes.
const computeMedianFilter=(closes,length=50)=>{
  if(!closes||closes.length<length)return closes?.[0]||0; // closes[0] = newest
  // closes is newest-first. Use first `length` elements = most recent bars.
  const window=[...closes.slice(0,length)].sort((a,b)=>a-b);
  return window[Math.floor(length/2)];
};

// ── ATRP (Volatility Regime — short vs long ATR ratio) ───────────────────
// short_atr/close - long_atr/close. Negative = vol contracting. High = expanding.
const computeATRP=(bars,shortP=2,longP=14)=>{
  if(!bars||bars.length<longP+2)return{atrp:0,regime:'Normal'};
  const closes=bars.map(b=>b.c);
  // Compute ATR(shortP) and ATR(longP) — use MOST RECENT bars (newest-first slice)
  const getATR=(p)=>{
    let atr=0;
    // bars[0]=newest. Take first p bars (most recent) for ATR
    const end=Math.min(p+1,bars.length);
    for(let i=1;i<end;i++){
      const tr=Math.max(bars[i-1].h-bars[i-1].l,
        Math.abs(bars[i-1].h-bars[i].c),
        Math.abs(bars[i-1].l-bars[i].c));
      atr+=tr/p;
    }
    return atr;
  };
  const shortATR=getATR(shortP);
  const longATR=getATR(longP);
  const cp=closes[0]; // bars[0] = newest bar's close
  const atrp=cp>0?((shortATR/cp)-(longATR/cp))*100:0;
  // Store for percentile
  _atrpHistory.push(atrp);
  if(_atrpHistory.length>250)_atrpHistory.shift();
  const sorted=[..._atrpHistory].sort((a,b)=>a-b);
  const pct50=sorted[Math.floor(sorted.length*0.50)]||0;
  const pct70=sorted[Math.floor(sorted.length*0.70)]||0;
  let atrpRegime='Normal';
  if(atrp<0)atrpRegime='Low';
  else if(atrp>pct70)atrpRegime='Extreme';
  else if(atrp>pct50)atrpRegime='High';
  return{atrp:Math.round(atrp*100)/100,regime:atrpRegime,pct50,pct70};
};

// ── Session VWAP with StDev Bands ─────────────────────────────────────────
// Anchors to UTC midnight (daily session). Matches Pine Script VWAP Stdev Bands.
// dev = sqrt(v2sum/volumesum - vwap²) = volume-weighted price stdev.
// Bands at ±1.28σ, ±2.01σ, ±2.51σ.
const computeSessionVWAP=(history,currentTime)=>{
  if(!history||history.length<5)return{vwap:0,dev:0,valid:false};
  // Session start = last UTC midnight
  const now=currentTime||Date.now();
  const sessionStart=now-((now%(86400000)));
  // Filter to current session only (bars within today UTC)
  // Bars use `time` field (Unix seconds from Coinbase) OR `t` field (ms) depending on source
  const sessionBars=history.filter(b=>{
    const barMs=b.t?b.t:(b.time?b.time*1000:0);
    return barMs>=sessionStart;
  });
  const bars=sessionBars.length>=5?sessionBars:history.slice(0,Math.min(60,history.length));
  if(bars.length<3)return{vwap:0,dev:0,valid:false};
  let vwapSum=0,volSum=0,v2Sum=0;
  for(const b of bars){
    const hl2=(b.h+b.l)/2;
    const vol=b.v||1;
    vwapSum+=hl2*vol;
    volSum+=vol;
    v2Sum+=vol*hl2*hl2;
  }
  if(volSum<=0)return{vwap:0,dev:0,valid:false};
  const vwap=vwapSum/volSum;
  const dev=Math.sqrt(Math.max(0,v2Sum/volSum-vwap*vwap));
  return{
    vwap:Math.round(vwap*100)/100,
    dev:Math.round(dev*100)/100,
    upper1:vwap+1.28*dev,
    lower1:vwap-1.28*dev,
    upper2:vwap+2.01*dev,
    lower2:vwap-2.01*dev,
    upper3:vwap+2.51*dev,
    lower3:vwap-2.51*dev,
    valid:true,
    sessionBars:bars.length,
  };
};

// ── MAIN REGIME CLASSIFIER V10.7.36 ──────────────────────────────────────
// Combines all four indicators into Tara's 5-bucket regime output.
// Mapping:
//   ADX≥20 + no whipsaw + BBW<80 → TREND (direction from drift/Grand Trend)
//   BBW≥80 || ATRP=Extreme → HIGH VOL CHOP
//   AiBitcoinTrend Accumulation (chop + above median) → RANGE-CHOP (UP lean)
//   AiBitcoinTrend Distribution (chop + below median) → RANGE-CHOP (DOWN lean)
//   SHORT SQUEEZE preserved from whale/funding detection
const computeRegimeV10736=(bars,drift1m,drift5m,drift15m,atrBps)=>{
  if(!bars||bars.length<30)return null;
  const closes=bars.map(b=>b.c);
  const n=closes.length;
  // V10.7.44 FIX: closes is newest-first. closes[n-1] is the OLDEST bar.
  // currentClose must be closes[0] (most recent bar).
  const currentClose=closes[0];
  // 1. ADX
  const {adx,valid:adxValid}=computeADX(bars,14);
  // 2. BBW percentile
  const {bbw,rank:bbwRank}=computeBBWRank(closes,_bbwHistory,100);
  // 3. Whipsaw filter
  const whipsawCount=computeWhipsawCount(bars,20,10);
  const isWhipsawing=whipsawCount>=3;
  // 4. ATRP
  const {atrp,regime:atrpRegime}=computeATRP(bars,2,14);
  // 5. Median filter for direction in chop (AiBitcoinTrend)
  const medianPrice=computeMedianFilter(closes,Math.min(50,Math.floor(n/2)));
  const priceAboveMedian=currentClose>medianPrice;
  // 6. Regime determination (Master Regime Classifier V6.2 logic)
  const isHighVol=bbwRank>=80||atrpRegime==='Extreme'||(atrBps&&atrBps>40);
  const isTrend=!isHighVol&&adxValid&&adx>=20&&!isWhipsawing;
  const isChop=!isHighVol&&!isTrend;

  // V10.7.75b — COMPRESSING regime detection
  //   Pattern: BBW percentile is LOW (tight range) AND has been FALLING (contracting).
  //   This is the pre-breakout coil — direction unknown, explosive move imminent.
  //   Detection: current BBW rank < 30th pct AND falling from prior 5 readings.
  const _bbwRecent=_bbwHistory.slice(-6);
  const _bbwFalling=_bbwRecent.length>=5&&_bbwRecent[_bbwRecent.length-1]<_bbwRecent[0]*0.85;
  const isCompressing=isChop&&bbwRank<30&&_bbwFalling&&!isWhipsawing;

  // Determine direction within TREND
  const driftUp=drift1m>3&&drift5m>5;
  const driftDn=drift1m<-3&&drift5m<-5;
  let regimeV2=isHighVol?'HIGH VOL CHOP'
    :isCompressing?'COMPRESSING'
    :isTrend?(driftUp?'TRENDING UP':driftDn?'TRENDING DOWN':'RANGE-CHOP')
    :'RANGE-CHOP';
  return{
    regime:regimeV2,
    adx,bbwRank,bbw,isWhipsawing,whipsawCount,
    atrp,atrpRegime,priceAboveMedian,medianPrice,
    isHighVol,isTrend,isChop,isCompressing,
    _v10_7_36:true,
  };
};
// ═══════════════════════════════════════════════════════════════════════════
// END V10.7.36 REGIME FUNCTIONS
// ═══════════════════════════════════════════════════════════════════════════

// ═══════════════════════════════════════════════════════════════════════════
// V10.7.37 — STOCHASTIC RSI + MARKET STRUCTURE BOS + MACD + OBV
// ═══════════════════════════════════════════════════════════════════════════
// Sources:
//   StochRSI: TradingView built-in defaults (period 14, K smooth 3, D smooth 3)
//   Market Structure: "Advanced Crypto Market Structure PRO 15M" (adapted for 1M)
//   MACD + OBV: "Full strategy" by SoftKill21 (fast 12, slow 26, signal 9, hl2)
// ═══════════════════════════════════════════════════════════════════════════

// ── Stochastic RSI ─────────────────────────────────────────────────────────
// StochRSI = (RSI - lowest(RSI,stochPeriod)) / (highest(RSI,stochPeriod) - lowest(RSI,stochPeriod))
// K = SMA(StochRSI * 100, smoothK). D = SMA(K, smoothD).
// K > 80 = overbought. K < 20 = oversold. 4× more sensitive than RSI>70.
const computeStochRSI=(closes,rsiPeriod=14,stochPeriod=14,smoothK=3,smoothD=3)=>{
  if(!closes||closes.length<rsiPeriod+stochPeriod+smoothK+smoothD+5){
    return{k:50,d:50,overbought:false,oversold:false,valid:false};
  }
  // V10.7.38a AUDIT FIX: liveHistory is newest-first. Reverse to oldest-first
  // so RSI/stochastic math works chronologically (older→newer = price going up).
  const needed=rsiPeriod+stochPeriod+smoothK+smoothD+10;
  // V10.7.44 FIX: closes is newest-first. slice(-needed) was taking the OLDEST needed
  // bars (tail of array). Must use slice(0,needed) to get the NEWEST bars, then reverse.
  const slice=closes.slice(0,needed).reverse(); // oldest-first now (newest bars)
  const n=slice.length;
  // RSI series
  const rsiSeries=[];
  let ag=0,al=0;
  for(let i=1;i<=rsiPeriod;i++){
    const chg=slice[i]-slice[i-1];
    if(chg>0)ag+=chg; else al-=chg;
  }
  ag/=rsiPeriod; al/=rsiPeriod;
  for(let i=rsiPeriod+1;i<n;i++){
    const chg=slice[i]-slice[i-1];
    ag=(ag*(rsiPeriod-1)+Math.max(chg,0))/rsiPeriod;
    al=(al*(rsiPeriod-1)+Math.max(-chg,0))/rsiPeriod;
    const rsiVal=al===0?100:100-(100/(1+(ag/al)));
    rsiSeries.push(rsiVal);
  }
  // Stochastic of RSI series
  const stochRSI=[];
  for(let i=stochPeriod-1;i<rsiSeries.length;i++){
    const window=rsiSeries.slice(i-stochPeriod+1,i+1);
    const lo=Math.min(...window),hi=Math.max(...window);
    stochRSI.push(hi===lo?50:((rsiSeries[i]-lo)/(hi-lo))*100);
  }
  // SMA(stochRSI, smoothK) = K line
  const kLine=[];
  for(let i=smoothK-1;i<stochRSI.length;i++){
    kLine.push(stochRSI.slice(i-smoothK+1,i+1).reduce((a,b)=>a+b,0)/smoothK);
  }
  // SMA(K, smoothD) = D line
  const dLine=[];
  for(let i=smoothD-1;i<kLine.length;i++){
    dLine.push(kLine.slice(i-smoothD+1,i+1).reduce((a,b)=>a+b,0)/smoothD);
  }
  if(!kLine.length||!dLine.length)return{k:50,d:50,overbought:false,oversold:false,valid:false};
  const k=kLine[kLine.length-1];
  const d=dLine[dLine.length-1];
  const kPrev=kLine.length>1?kLine[kLine.length-2]:k;
  return{
    k:Math.round(k*10)/10,
    d:Math.round(d*10)/10,
    overbought:k>80,
    oversold:k<20,
    crossUp:kPrev<20&&k>=20, // crossed up from oversold
    crossDown:kPrev>80&&k<=80, // crossed down from overbought
    valid:true,
  };
};

// ── Market Structure BOS ──────────────────────────────────────────────────
// Pine Script: "Advanced Crypto Market Structure PRO 15M" by user
// Adapted for 1M bars in Tara (same math, different timeframe).
// swingHigh = highest(high, swingLen). swingLow = lowest(low, swingLen).
// BOS Bull = close > swingHigh[1]: close breaks prev swing high.
// BOS Bear = close < swingLow[1]: close breaks prev swing low.
// Confirm Long: close > EMA20 AND EMA20 > EMA50 AND BOS bull.
// Confirm Short: close < EMA20 AND EMA20 < EMA50 AND BOS bear.
const computeMarketStructureBOS=(bars,fastLen=20,slowLen=50,swingLen=5)=>{
  if(!bars||bars.length<slowLen+swingLen+2){
    return{confirmLong:false,confirmShort:false,bosBull:false,bosBear:false,bullTrend:false,valid:false};
  }
  // V10.7.38a AUDIT FIX: liveHistory is newest-first. Reverse to oldest-first
  // so EMA calculations build chronologically (EMAs grow forward in time).
  const barsOF=bars.slice().reverse(); // oldest-first
  const closes=barsOF.map(b=>b.c);
  const highs=barsOF.map(b=>b.h);
  const lows=barsOF.map(b=>b.l);
  const n=barsOF.length;
  // Fast EMA(20) and Slow EMA(50)
  const getEMAFull=(arr,p)=>{
    const k=2/(p+1);
    let ema=arr.slice(0,p).reduce((a,b)=>a+b,0)/p;
    const series=[ema];
    for(let i=p;i<arr.length;i++){ema=arr[i]*k+ema*(1-k);series.push(ema);}
    return series;
  };
  const fastSeries=getEMAFull(closes,fastLen);
  const slowSeries=getEMAFull(closes,slowLen);
  const fastEMA=fastSeries[fastSeries.length-1];
  const slowEMA=slowSeries[slowSeries.length-1];
  const currentClose=closes[n-1]; // newest close (last in oldest-first)
  // Swing high/low: last swingLen bars BEFORE current bar (oldest-first indexing)
  const prevHighs=highs.slice(n-1-swingLen,n-1);
  const prevLows=lows.slice(n-1-swingLen,n-1);
  const swingHigh=Math.max(...prevHighs); // swingHigh[1] in Pine
  const swingLow=Math.min(...prevLows);   // swingLow[1] in Pine
  // Break of Structure
  const bosBull=currentClose>swingHigh;
  const bosBear=currentClose<swingLow;
  // Trend filter
  const bullTrend=fastEMA>slowEMA;
  const bearTrend=fastEMA<slowEMA;
  // Confirmed signals
  const readyLong=currentClose>fastEMA&&bullTrend;
  const readyShort=currentClose<fastEMA&&bearTrend;
  const confirmLong=readyLong&&bosBull;
  const confirmShort=readyShort&&bosBear;
  return{
    confirmLong,confirmShort,bosBull,bosBear,
    bullTrend,bearTrend,fastEMA:Math.round(fastEMA),slowEMA:Math.round(slowEMA),
    swingHigh:Math.round(swingHigh),swingLow:Math.round(swingLow),valid:true,
  };
};

// ── MACD + OBV ────────────────────────────────────────────────────────────
// Pine Script: "Full strategy" by SoftKill21. MACD on hl2, standard (12,26,9).
// macd = EMA(hl2,12) - EMA(hl2,26). Signal = EMA(macd,9). Hist = macd - signal.
// Key signal: hist crosses 0 = strong momentum flip.
// OBV (On Balance Volume): cumulative volume flow.
//   OBV > SMA(OBV,10) = accumulation (bullish). < = distribution (bearish).
const computeMACDAndOBV=(bars,fast=12,slow=26,sig=9,obvSmooth=10)=>{
  if(!bars||bars.length<slow+sig+obvSmooth+5){
    return{hist:0,macd:0,signal:0,bullish:false,crossUp:false,crossDown:false,obvBullish:false,valid:false};
  }
  // V10.7.38a AUDIT FIX: liveHistory is newest-first. Reverse to oldest-first
  // so EMA builds forward in time and OBV accumulates correctly.
  const barsOF=bars.slice().reverse(); // oldest-first
  const hl2=barsOF.map(b=>(b.h+b.l)/2);
  const closes=barsOF.map(b=>b.c);
  const volumes=barsOF.map(b=>b.v||1);
  const n=barsOF.length;
  // EMA of hl2
  const getEMA=(arr,p)=>{
    const k=2/(p+1);
    let ema=arr.slice(0,p).reduce((a,b)=>a+b,0)/p;
    for(let i=p;i<arr.length;i++){ema=arr[i]*k+ema*(1-k);}
    return ema;
  };
  const getEMAFull=(arr,p)=>{
    const k=2/(p+1);
    let ema=arr.slice(0,p).reduce((a,b)=>a+b,0)/p;
    const series=[ema];
    for(let i=p;i<arr.length;i++){ema=arr[i]*k+ema*(1-k);series.push(ema);}
    return series;
  };
  const fastEMAFull=getEMAFull(hl2,fast);
  const slowEMAFull=getEMAFull(hl2,slow);
  // MACD line series (aligned at slowLen start)
  const startIdx=slow-fast; // fastSeries starts fast bars in, slowSeries starts slow bars in
  const macdSeries=[];
  for(let i=0;i<slowEMAFull.length;i++){
    const fi=i+startIdx;
    if(fi>=0&&fi<fastEMAFull.length){
      macdSeries.push(fastEMAFull[fi]-slowEMAFull[i]);
    }
  }
  if(macdSeries.length<sig+2)return{hist:0,macd:0,signal:0,bullish:false,crossUp:false,crossDown:false,obvBullish:false,valid:false};
  // Signal = EMA(macd, sig)
  const signalSeries=getEMAFull(macdSeries,sig);
  const macdNow=macdSeries[macdSeries.length-1];
  const signalNow=signalSeries[signalSeries.length-1];
  const histNow=macdNow-signalNow;
  const histPrev=macdSeries.length>1&&signalSeries.length>1?
    (macdSeries[macdSeries.length-2]-signalSeries[signalSeries.length-2]):0;
  // OBV: cumulative sum, +volume when close rises, -volume when close falls
  let obv=0;
  const obvSeries=[];
  for(let i=1;i<n;i++){
    const chg=closes[i]-closes[i-1];
    obv+=chg>0?volumes[i]:chg<0?-volumes[i]:0;
    obvSeries.push(obv);
  }
  // OBV SMA(10)
  const obvNow=obvSeries[obvSeries.length-1];
  const obvSMA=obvSmooth<=obvSeries.length?
    obvSeries.slice(-obvSmooth).reduce((a,b)=>a+b,0)/obvSmooth:obvNow;
  return{
    hist:Math.round(histNow*100)/100,
    histPrev:Math.round(histPrev*100)/100,
    macd:Math.round(macdNow*100)/100,
    signal:Math.round(signalNow*100)/100,
    bullish:histNow>0,
    crossUp:histPrev<=0&&histNow>0, // histogram just crossed above 0
    crossDown:histPrev>=0&&histNow<0, // histogram just crossed below 0
    obvBullish:obvNow>obvSMA, // OBV above its SMA = accumulation
    obv:Math.round(obvNow),
    obvSMA:Math.round(obvSMA),
    valid:true,
  };
};
// ═══════════════════════════════════════════════════════════════════════════
// END V10.7.37 INDICATOR FUNCTIONS
// ═══════════════════════════════════════════════════════════════════════════

// ═══════════════════════════════════════════════════════════════════════════
// V10.7.38 — ANCHORED VWAP (AUTO HIGH & LOW)
// ═══════════════════════════════════════════════════════════════════════════
// Source: "Anchored VWAP (Auto High & Low)" by liquid-trader on TradingView.
// Logic: auto-reanchors to last swing high/low within lookback window.
//   anchoredHi = VWAP from last swing high bar forward
//   anchoredLo = VWAP from last swing low bar forward
//   anchoredMean = midpoint (equilibrium / fair value)
//
// Pine Script logic (simplified for Tara):
//   breakingHigher = rising candle AND close > hi (previous anchor high)
//   → resetHiAnchor := true, hi := h
//   anchoredHi = ta.vwap(high, resetHiAnchor)
//
// Tara implementation: find the bar with the highest high (or lowest low)
//   within the last swingLookback bars. That bar becomes the VWAP anchor.
//   Compute cumulative volume-weighted hl2 from anchor bar to current bar.
//
// Signal interpretation:
//   Price > anchoredHi → trading above recent high buyers → bullish
//   Price < anchoredLo → trading below recent low sellers → bearish
//   Price near anchoredMean → equilibrium (chop / fair value)
//   Price crossing mean ↑ → directional shift UP
//   Price crossing mean ↓ → directional shift DOWN
// ═══════════════════════════════════════════════════════════════════════════
const computeAnchoredVWAP=(bars,swingLookback=20)=>{
  if(!bars||bars.length<swingLookback+2){
    return{anchoredHi:0,anchoredLo:0,anchoredMean:0,aboveHi:false,belowLo:false,aboveMean:false,valid:false};
  }
  // V10.7.44 FIX: bars is newest-first (liveHistory). Previous code searched bars[n-1]
  // (oldest bar) as "current" and iterated toward the oldest end for the lookback window —
  // completely backwards. Reverse to oldest-first so barsOF[n-1]=newest (current bar),
  // and the most recent swingLookback bars are indices n-lb to n-1.
  const barsOF=bars.slice().reverse(); // oldest-first: barsOF[n-1]=newest bar
  const n=barsOF.length;
  const lb=Math.min(swingLookback,n);
  // Find highest high within most recent lb bars (anchor for Hi VWAP)
  let hiIdx=n-1,hiVal=barsOF[n-1].h;
  for(let i=n-2;i>=n-lb;i--){
    if(barsOF[i].h>hiVal){hiVal=barsOF[i].h;hiIdx=i;}
  }
  // Find lowest low within most recent lb bars (anchor for Lo VWAP)
  let loIdx=n-1,loVal=barsOF[n-1].l;
  for(let i=n-2;i>=n-lb;i--){
    if(barsOF[i].l<loVal){loVal=barsOF[i].l;loIdx=i;}
  }
  // Compute VWAP from hi anchor forward to current bar (oldest-first: anchor < current)
  let hiVwapSum=0,hiVolSum=0;
  for(let i=hiIdx;i<n;i++){
    const hl2=(barsOF[i].h+barsOF[i].l)/2;
    const vol=barsOF[i].v||1;
    hiVwapSum+=hl2*vol;hiVolSum+=vol;
  }
  const anchoredHi=hiVolSum>0?hiVwapSum/hiVolSum:barsOF[n-1].c;
  // Compute VWAP from lo anchor forward to current bar
  let loVwapSum=0,loVolSum=0;
  for(let i=loIdx;i<n;i++){
    const hl2=(barsOF[i].h+barsOF[i].l)/2;
    const vol=barsOF[i].v||1;
    loVwapSum+=hl2*vol;loVolSum+=vol;
  }
  const anchoredLo=loVolSum>0?loVwapSum/loVolSum:barsOF[n-1].c;
  const anchoredMean=(anchoredHi+anchoredLo)/2;
  const currentClose=barsOF[n-1].c; // newest bar's close (last in oldest-first)
  // Distance from mean (bps) — how far price is from fair value
  const distFromMeanBps=anchoredMean>0?((currentClose-anchoredMean)/anchoredMean)*10000:0;
  return{
    anchoredHi:Math.round(anchoredHi*100)/100,
    anchoredLo:Math.round(anchoredLo*100)/100,
    anchoredMean:Math.round(anchoredMean*100)/100,
    aboveHi:currentClose>anchoredHi,
    belowLo:currentClose<anchoredLo,
    aboveMean:currentClose>anchoredMean,
    distFromMeanBps:Math.round(distFromMeanBps*10)/10,
    hiIdx,loIdx,
    valid:true,
  };
};
// ═══════════════════════════════════════════════════════════════════════════
// END V10.7.38 ANCHORED VWAP
// ═══════════════════════════════════════════════════════════════════════════

// ═══════════════════════════════════════════════════════════════════════════
// V10.7.39 — THREE-STEP VOLUME DELTA + CONSOLIDATION COIL DETECTOR
// ═══════════════════════════════════════════════════════════════════════════
// Source: "Three Step Future-Trend [BigBeluga]" — volume delta logic only.
// (Future trend projection skipped — Tara's TRAJ does this better with
//  velocity decay and volume validation.)
//
// WHY THIS MATTERS — "the gaslighting problem":
//   Small indecisive candles create tiny velocity signals. TRAJ amplifies
//   them into a projected direction. But small candles often mean CONSOLIDATION
//   before a breakout — and the breakout direction is unknown from velocity alone.
//   Tara locks the tiny lean → price snaps the OTHER way.
//
// THREE-STEP DELTA reads the HIDDEN ORDER FLOW under flat price:
//   delta_vol per bar = close > open ? +volume : -volume (candle body direction)
//   delta1 = last 25 bars → current momentum
//   delta2 = bars 25-50 → recent history
//   delta3 = bars 50-75 → older history
//
//   delta1 strongly positive + flat price = buyers absorbing quietly → UP breakout
//   delta1 strongly negative + flat price = sellers absorbing quietly → DOWN breakout
//   delta1 decelerating vs delta2 = momentum fading → caution
//
// COIL DETECTOR checks if we're in consolidation:
//   If last 5 bars all have small bodies (< 30% of ATR) AND small ranges (< 50% ATR)
//   → we're coiling. TRAJ velocity is noise. Three-Step Delta becomes primary signal.
// ═══════════════════════════════════════════════════════════════════════════

const computeThreeStepDelta=(bars,period=25)=>{
  // bars = liveHistory (newest-first)
  // Need 3 * period bars minimum
  if(!bars||bars.length<period*3){
    return{delta1:0,delta2:0,delta3:0,volDelta:0,direction:'NEUTRAL',accelerating:false,valid:false};
  }
  // delta_vol per bar = close > open ? +volume : -volume
  const getDeltaSum=(start,end)=>{
    let sum=0;
    for(let i=start;i<Math.min(end,bars.length);i++){
      const b=bars[i];
      const dv=(b.c>b.o?1:-1)*(b.v||1);
      sum+=dv;
    }
    return sum;
  };
  const delta1=getDeltaSum(0,period);      // most recent `period` bars
  const delta2=getDeltaSum(period,period*2); // previous `period` bars
  const delta3=getDeltaSum(period*2,period*3); // oldest `period` bars
  const volDelta=(delta1+delta2+delta3)/3; // overall bias
  const direction=delta1>0?'UP':delta1<0?'DOWN':'NEUTRAL';
  // Accelerating: current period stronger than previous (momentum building)
  const accelerating=Math.abs(delta1)>Math.abs(delta2);
  // Shift: delta2 was opposite to delta1 = fresh momentum flip (strong signal)
  const momentumFlip=(delta1>0&&delta2<0)||(delta1<0&&delta2>0);
  // Magnitude normalization: delta1 relative to total volume traded
  const totalVol1=bars.slice(0,period).reduce((s,b)=>s+(b.v||1),0);
  const delta1Pct=totalVol1>0?(delta1/totalVol1)*100:0; // -100 to +100
  return{
    delta1,delta2,delta3,volDelta,
    direction,accelerating,momentumFlip,
    delta1Pct:Math.round(delta1Pct*10)/10,
    valid:true,
  };
};

const detectCoilConsolidation=(bars,atrBps,period=5)=>{
  // bars = liveHistory (newest-first). Check if last `period` bars are small/flat.
  if(!bars||bars.length<period||!atrBps||atrBps<=0){
    return{isCoiling:false,avgBodyBps:0,avgRangeBps:0};
  }
  const recentBars=bars.slice(0,period);
  const cp=bars[0]?.c||1;
  // ATR per bar in bps
  const atrPerBarBps=atrBps; // already in bps
  // Body and range in bps
  const bodies=recentBars.map(b=>Math.abs(b.c-b.o)/cp*10000);
  const ranges=recentBars.map(b=>(b.h-b.l)/cp*10000);
  const avgBodyBps=bodies.reduce((a,b)=>a+b,0)/period;
  const avgRangeBps=ranges.reduce((a,b)=>a+b,0)/period;
  // Coiling: bodies < 30% ATR AND ranges < 50% ATR per bar
  const bodyThreshold=atrPerBarBps*0.30;
  const rangeThreshold=atrPerBarBps*0.50;
  const isCoiling=avgBodyBps<bodyThreshold&&avgRangeBps<rangeThreshold;
  return{
    isCoiling,
    avgBodyBps:Math.round(avgBodyBps*10)/10,
    avgRangeBps:Math.round(avgRangeBps*10)/10,
    bodyThreshold:Math.round(bodyThreshold*10)/10,
  };
};
// ═══════════════════════════════════════════════════════════════════════════
// END V10.7.39
// ═══════════════════════════════════════════════════════════════════════════

// V6.2.0: FUTURE GRAND TREND (HPotter port). Recursive low-pass-style transform looking
//   at bars 70 and 140 ago. Captures multi-hour structural direction.
const computeFutureGrandTrend=(candles,length=70,forecast=100)=>{
  if(!candles||candles.length<60)return{dir:'NEUTRAL',strengthBps:0,projectionBps:0,valid:false,reason:'insufficient'};
  const bars=candles.slice().reverse();
  const n=bars.length;
  const L=Math.min(length,Math.floor(n/3));
  const F=Math.min(forecast,Math.floor(n/2));
  if(L<10||F<10)return{dir:'NEUTRAL',strengthBps:0,projectionBps:0,valid:false,reason:'too-short'};
  // Build recursive t series
  const t=new Array(n);
  for(let i=0;i<n;i++){
    const src=bars[i].c;
    if(i<L*2){t[i]=src;continue;}
    const tLag=t[i-L];
    const tLag2=t[i-L*2];
    t[i]=1.8*tLag-0.9*tLag2+0.1*src;
  }
  // fcast at last bar
  const tLast=t[n-1];
  const tBack=t[Math.max(0,n-1-F)];
  const fcast=2*tLast-tBack;
  const currentPrice=bars[n-1].c;
  // Slope over last 20 bars of fcast for direction
  const slopeWindow=Math.min(20,Math.floor(n/4));
  const fcastBack=t[Math.max(0,n-1-slopeWindow)]; // approx — fcast[N-window] would need full fcast array, t is fine proxy
  const slope=(tLast-fcastBack)/slopeWindow;
  const slopeBps=(slope/currentPrice)*10000;
  const projectionBps=((fcast-currentPrice)/currentPrice)*10000;
  let dir='NEUTRAL';
  // Direction by projection magnitude — significant projection = directional
  if(projectionBps>15&&slopeBps>0)dir='UP';
  else if(projectionBps<-15&&slopeBps<0)dir='DOWN';
  // Strength = absolute projection in bps, capped to 0-100 scale (100bps = full strength)
  const strengthBps=Math.min(100,Math.abs(projectionBps));
  return{dir,strengthBps:Math.round(strengthBps),projectionBps:Math.round(projectionBps*10)/10,slopeBpsPerBar:Math.round(slopeBps*100)/100,valid:true};
};

const computeFGT=(candles,length=70,forecast=100)=>{
  // Need at least length*2 + forecast bars for a meaningful computation
  // Plus current bar. Be lenient: scale length down if needed.
  if(!Array.isArray(candles)||candles.length<20)return{forecastDir:'NEUTRAL',forecastBps:0,valid:false,reason:'insufficient'};
  if(candles.some(c=>!Number.isFinite(Number(c?.c))||Number(c?.c)<=0))return{forecastDir:'NEUTRAL',forecastBps:0,valid:false,reason:'invalid-candles'};
  // Adapt parameters to available data
  // PineScript: oldest is highest index, newest is index 0 in our convention (Tara's liveHistory[0] is newest)
  // We'll work with reversed array: oldest first
  const bars=candles.slice().reverse(); // bars[0] = oldest, bars[N-1] = newest
  const n=bars.length;
  // Auto-scale length and forecast to available history
  const _length=Math.min(length,Math.floor(n/3));
  const _forecast=Math.min(forecast,Math.floor(n/2));
  if(_length<5||!Number.isFinite(_forecast)||_forecast<=0)return{forecastDir:'NEUTRAL',forecastBps:0,valid:false,reason:_forecast<=0?'window-closed':'too-short'};
  // Estimate a stable historical slope over at least five bars, then project
  // only over the requested (possibly fractional) horizon before settlement.
  const _slopeLookback=Math.max(5,Math.ceil(_forecast));
  // Build t series
  const t=new Array(n);
  for(let i=0;i<n;i++){
    const src=bars[i].c;
    if(i<_length){
      t[i]=src; // initialize with src (PineScript nz fallback)
      continue;
    }
    const tLag=t[i-_length];
    // Compute change in (0.9 * t[i-length]) over `length` bars
    let changeTerm=0;
    if(i>=_length*2){
      const tLag2=t[i-_length*2];
      changeTerm=0.9*tLag-0.9*tLag2;
    }
    t[i]=0.9*tLag+0.1*src+changeTerm;
  }
  const _tLast=t[n-1];
  const _tBack=t[Math.max(0,n-1-_slopeLookback)];
  // V10.7.35 — SLOPE FIX: Pine Script FGT direction comes from the SLOPE of the
  //   t-series line, NOT from comparing the projected value to current price.
  //   OLD (wrong): forecastBps = (fcast - currentPrice) / currentPrice * 10000
  //     → mixes smoothed projection (lags price) with raw close → wrong direction
  //       when t-series lags below rising price (reads DOWN when slope is UP)
  //   NEW (correct): forecastBps = slope / currentPrice * 10000
  //     → slope = tLast - tBack = projected change in t-series over forecast bars
  //     → matches Pine Script line color logic (green if y2>y1, red if y2<y1)
  const currentPrice=bars[n-1].c;
  const slope=(_tLast-_tBack)*(_forecast/_slopeLookback);
  const forecastBps=Math.round((slope/currentPrice)*10000);
  let forecastDir='NEUTRAL';
  if(forecastBps>3)forecastDir='UP';
  else if(forecastBps<-3)forecastDir='DOWN';
  return{forecastDir,forecastBps,fcast:Math.round(_tLast+slope),valid:true,length:_length,forecast:_forecast,slopeLookback:_slopeLookback,slope:Math.round(slope*100)/100};
};

// V134: Volume Profile — find where most trading happened recently
// VPOC (Volume Point of Control) acts as support/resistance.
// If we're locked UP and a strong VPOC sits between price and strike → it's resistance.
const computeVolumeProfile=(history)=>{
  if(!history||history.length<10)return{vpoc:0,vah:0,val:0,strength:0};
  const recent=history.slice(0,40); // last 40 candles
  // Bin price levels into $50 buckets
  const buckets={};
  recent.forEach(c=>{
    const lo=Math.floor((c.l||c.c)/50)*50;
    const hi=Math.floor((c.h||c.c)/50)*50;
    const v=c.v||0;
    // Distribute volume across the candle's price range
    const numBuckets=Math.max(1,Math.round((hi-lo)/50)+1);
    for(let p=lo;p<=hi;p+=50){
      buckets[p]=(buckets[p]||0)+(v/numBuckets);
    }
  });
  // Find max-volume bucket = VPOC
  const sorted=Object.entries(buckets).sort((a,b)=>b[1]-a[1]);
  if(sorted.length===0)return{vpoc:0,vah:0,val:0,strength:0};
  const vpoc=parseFloat(sorted[0][0])+25; // mid-bucket
  const totalVol=Object.values(buckets).reduce((s,v)=>s+v,0);
  const vpocVol=sorted[0][1];
  const strength=totalVol>0?vpocVol/totalVol:0;
  // Value Area: 70% of volume around VPOC
  let cumVol=vpocVol;
  let vah=vpoc,val=vpoc;
  const target=totalVol*0.70;
  while(cumVol<target&&(vah<sorted[0][0]+1000||val>sorted[0][0]-1000)){
    const upBucket=parseFloat(sorted[0][0])+(vah-vpoc+50);
    const dnBucket=parseFloat(sorted[0][0])-(vpoc-val+50);
    const upVol=buckets[upBucket]||0;
    const dnVol=buckets[dnBucket]||0;
    if(upVol>=dnVol){vah=upBucket+25;cumVol+=upVol;}
    else{val=dnBucket+25;cumVol+=dnVol;}
    if(cumVol>=target)break;
  }
  return{vpoc,vah,val,strength};
};

// V134: useDepthFlash — high-frequency order book polling at 2.5s
// Bloomberg's 8s interval misses fast wall changes during volatility.
// This hook only fetches the depth endpoint, which is the time-critical one.
// V134: Fetch candles at 1m, 3m, 5m, 15m for HPotter FGT analysis
// Returns { c1m: [...], c3m: [...], c5m: [...], c15m: [...] }
// V9.8.5: priceSource arg (Coinbase / Kraken / Bitstamp). Re-runs effect on switch.
const useMultiTFCandles=(asset,priceSource)=>{
  const _TF_WARM_KEY='taraTFCandles_v1';
  const _tfWarm=(()=>{
    try{
      const raw=sessionStorage.getItem(_TF_WARM_KEY);
      if(!raw)return null;
      const ws=JSON.parse(raw);
      if(!ws||!ws.savedAt||Date.now()-ws.savedAt>5*60*1000)return null;
      if(ws.asset!==asset)return null;
      return ws;
    }catch(_){return null;}
  })();
  const[tfCandles,setTfCandles]=useState(()=>{
    if(_tfWarm?.c1m?.length>=20&&_tfWarm?.c5m?.length>=10){
      try{console.info(`[V10.7.86] TF candles warm restore: 1m=${_tfWarm.c1m.length} 5m=${_tfWarm.c5m.length} 15m=${(_tfWarm.c15m||[]).length}`);}catch(_){}
      return{c1m:_tfWarm.c1m,c3m:_tfWarm.c3m||[],c5m:_tfWarm.c5m,c15m:_tfWarm.c15m||[]};
    }
    return{c1m:[],c3m:[],c5m:[],c15m:[]};
  });
  useEffect(()=>{
    if(typeof window==='undefined')return;
    let mounted=true;
    const _cfg=ASSET_CONFIG[asset]||ASSET_CONFIG.BTC;
    // V9.8.5: resolve active source once per effect run
    const _src=PRICE_SOURCES[priceSource]?.virtual?PRICE_SOURCES.coinbase:((priceSource&&PRICE_SOURCES[priceSource])||PRICE_SOURCES[PRICE_SOURCE_DEFAULT]); // V10.7.59b: BRTI virtual fallback
    const fetchInterval=async(label,gran)=>{
      try{
        // V9.8.5: use active price source (was Coinbase only)
        const r=await fetch(_src.candleUrl(_cfg,asset,gran),{cache:'no-store'});
        if(!r.ok)throw new Error(_src.label+' '+r.status);
        const d=await r.json();
        const _candles=_src.parseCandles(d);
        if(_candles.length===0)throw new Error('empty');
        return _candles.slice(0,300);
      }catch(e){
        // V9.7.5: Fallback Bybit (Binance global blocked in US).
        //   Bybit kline returns [start, o, h, l, c, vol, turnover], newest first.
        //   We reverse to oldest-first to match the Coinbase shape consumers expect.
        try{
          const ivMap={60:'1',180:'3',300:'5',900:'15'};
          const iv=ivMap[gran]||'1';
          const r2=await fetch(`/api/bybit/market/kline?category=linear&symbol=${_cfg.binance||'BTCUSDT'}&interval=${iv}&limit=300`);
          const j=await r2.json();
          if(j?.retCode!==0||!Array.isArray(j?.result?.list))throw new Error('bybit bad');
          return j.result.list.map(c=>({time:parseInt(c[0])/1000,o:parseFloat(c[1]),h:parseFloat(c[2]),l:parseFloat(c[3]),c:parseFloat(c[4]),v:parseFloat(c[5])})).reverse();
        }catch(e2){return[];}
      }
    };
    const fetchAll=async()=>{
      // V9.7.5: Coinbase doesn't support granularity=180 (returns 400). Binance fallback
      //   blocked in US (451). So we synthesize 3m from 1m by aggregating 3 consecutive bars.
      //   Result: same 3m data shape the engine expects, no broken request.
      const[c1m,c5m,c15m]=await Promise.all([
        fetchInterval('1m',60),
        fetchInterval('5m',300),
        fetchInterval('15m',900),
      ]);
      // Synthesize 3m from 1m. Coinbase returns newest-first; group 3 consecutive bars.
      const c3m=[];
      if(Array.isArray(c1m)&&c1m.length>=3){
        for(let i=0;i+2<c1m.length;i+=3){
          const a=c1m[i],b=c1m[i+1],c=c1m[i+2];
          if(!a||!b||!c)continue;
          c3m.push({
            time:c.time, // oldest of the 3 = bar's start time
            o:c.o,        // open of oldest
            c:a.c,        // close of newest
            h:Math.max(a.h,b.h,c.h),
            l:Math.min(a.l,b.l,c.l),
            v:(a.v||0)+(b.v||0)+(c.v||0),
          });
        }
      }
      if(mounted)setTfCandles({c1m,c3m,c5m,c15m});
      // V10.7.86: save to sessionStorage for warm restore on next mount
      try{
        sessionStorage.setItem(_TF_WARM_KEY,JSON.stringify({
          savedAt:Date.now(),
          asset,
          c1m:c1m.slice(0,200),
          c3m:c3m.slice(0,100),
          c5m:c5m.slice(0,100),
          c15m:c15m.slice(0,50),
        }));
      }catch(_){}
    };
    fetchAll();
    const iv=setInterval(fetchAll,30000);
    return()=>{mounted=false;clearInterval(iv);};
  },[asset,priceSource]);
  return tfCandles;
};

// ── V9.11.0 LAYER 2: FUTURES DATA HOOK ───────────────────────────────────────
// Pulls perpetual-futures sentiment from Bybit (Coinbase doesn't expose futures data).
//   Three signals matter for predicting 15m direction:
//   • Funding rate — extreme negative = shorts crowded → squeeze risk UP; extreme
//     positive = longs crowded → flush risk DOWN. Updates every 8h, displayed as
//     8h-annualized rate (typical range ±0.01% per 8h = ±10.95% APR).
//   • Open Interest delta — rising OI + rising price = real conviction
//     (new money entering); rising OI + falling price = shorts opening = continuation
//     DOWN; falling OI = position closing → trend exhaustion.
//   • Basis (perp price vs spot index) — positive basis (perp > spot) = greedy longs;
//     negative basis (perp < spot) = capitulation. Extremes mark reversal zones.
//
// Bybit tickers endpoint returns all three in one call. Updates every 30s.
//
// Returns {fundingRate, openInterest, openInterestPrev, perpPrice, indexPrice,
//   basis, basisPct, oiDeltaPct, lastUpdated, error}.
const useFuturesData=(asset)=>{
  const[fut,setFut]=useState({
    fundingRate:null,         // 8h rate, e.g. 0.0001 = 0.01% per 8h
    openInterest:null,        // current contracts open
    openInterestPrev:null,    // value from last poll, for delta computation
    perpPrice:null,           // last traded perpetual price
    indexPrice:null,          // spot index price (Bybit's composite)
    basis:null,               // perpPrice - indexPrice (USD)
    basisPct:null,            // basis as % of index
    oiDeltaPct:null,          // % change in OI since last poll (rolling 30s)
    lastUpdated:0,
    error:null,
  });
  const _prevOIRef=useRef(null);
  useEffect(()=>{
    if(typeof window==='undefined')return;
    let mounted=true;
    const _cfg=ASSET_CONFIG[asset]||ASSET_CONFIG.BTC;
    const _sym=_cfg.binance||(asset==='BTC'?'BTCUSDT':'ETHUSDT'); // Bybit uses same symbol convention as Binance
    // V13.4.87: BYBIT REST WAS CORS-BLOCKED -- this starved the highest-weighted signal.
    //   These calls used to hit the Bybit REST host directly from the browser, which the
    //   browser refuses (no CORS header), and the catch below only records the error with NO
    //   fallback -- so funding / open-interest / basis silently went stale. Measured effect:
    //   the futures signal (weight 33 in DEFAULT_WEIGHTS, the largest) fired on only 27 of
    //   302 logged locks = 8.9%, while gap/flow/vwap fire ~100%. Now routed through the
    //   /api/bybit Vercel rewrite, same pattern as /api/okx and /api/deribit. Requires the
    //   updated vercel.json to be deployed alongside this file.
    const fetchFutures=async()=>{
      try{
        // Bybit tickers: returns last price, mark price, index price, OI, funding rate all in one
        const r=await fetch(`/api/bybit/market/tickers?category=linear&symbol=${_sym}`);
        if(!r.ok)throw new Error('bybit '+r.status);
        const j=await r.json();
        if(j?.retCode!==0||!Array.isArray(j?.result?.list)||j.result.list.length===0)throw new Error('bybit empty');
        const t=j.result.list[0];
        const _funding=Number(t.fundingRate);
        const _oi=Number(t.openInterest);
        const _perp=Number(t.lastPrice);
        const _index=Number(t.indexPrice);
        const _basis=Number.isFinite(_perp)&&Number.isFinite(_index)?_perp-_index:null;
        const _basisPct=_basis!=null&&_index>0?(_basis/_index)*100:null;
        const _prevOI=_prevOIRef.current;
        const _oiDelta=(_prevOI!=null&&Number.isFinite(_oi)&&_prevOI>0)?((_oi-_prevOI)/_prevOI)*100:null;
        _prevOIRef.current=_oi;
        if(mounted){
          setFut(prev=>({
            fundingRate:Number.isFinite(_funding)?_funding:prev.fundingRate,
            openInterest:Number.isFinite(_oi)?_oi:prev.openInterest,
            openInterestPrev:prev.openInterest,
            perpPrice:Number.isFinite(_perp)?_perp:prev.perpPrice,
            indexPrice:Number.isFinite(_index)?_index:prev.indexPrice,
            basis:_basis,
            basisPct:_basisPct,
            oiDeltaPct:_oiDelta,
            lastUpdated:Date.now(),
            error:null,
          }));
        }
      }catch(e){
        if(mounted){
          setFut(prev=>({...prev,error:e?.message||String(e),lastUpdated:Date.now()}));
        }
      }
    };
    // V13.4.88: DISABLED -- this hook is documented dead code. The engine's futures
    //   signal reads `bloomberg` (OKX funding/OI/basis via the working /api/okx proxy);
    //   see the V9.8.7 note at the computeFuturesSignals call site, which states this
    //   hook 'stays defined for compatibility but is no longer the source of truth'.
    //   It hit Bybit REST, which CORS-fails from the browser AND 403s through a Vercel
    //   rewrite (Bybit refuses datacenter IPs), yet it kept polling every 30s: roughly
    //   2880 wasted proxy requests/day plus a 403 in the console every half minute.
    //   Hook shape and return value are unchanged so no caller needs editing.
    const _taraBybitFuturesDisabled=true;
    if(_taraBybitFuturesDisabled){return()=>{mounted=false;};}
    fetchFutures();
    const iv=setInterval(fetchFutures,30000);
    return()=>{mounted=false;clearInterval(iv);};
  },[asset]);
  return fut;
};

// ── V9.11.0 LAYER 2: FUTURES SIGNAL COMPUTATION ──────────────────────────────
// Translates raw futures data (funding/OI/basis) into directional signals for the
//   engine to consume. All three contribute to totalScore.
//
// Funding rate signal:
//   • Bybit perp 8h funding. Typical range ±0.01% (±10% APR). Extremes are ±0.05%.
//   • Negative funding = shorts crowded = squeeze fuel UP. Positive = longs crowded.
//   • Mean reversion bias: extreme funding → counter-bias toward the unwind direction.
//   • Max contribution: ±15 at extreme funding (|rate| > 0.05%).
//
// Open Interest delta signal:
//   • Compares last 30s OI change against price direction (drift1m).
//   • Rising OI + rising price = new money entering long, bullish continuation (+8).
//   • Rising OI + falling price = shorts opening, bearish continuation (-8).
//   • Falling OI = position closing, neutral (no signal).
//
// Basis signal:
//   • Perp price - spot index. Positive basis = perp premium = greed; negative = fear.
//   • Extreme premium (>0.1%) suggests overheated longs → mean-reversion bias DOWN.
//   • Extreme discount (<-0.1%) suggests panic → mean-reversion bias UP.
//   • Max contribution: ±10.
//
// Total max futures contribution: ±33. Smaller than chart patterns (max ±55 from
//   3-TF confluence) because futures signals are slower-moving / context, not
//   trigger-level events.
const computeFuturesSignals=({funding,oi,oiDelta,basis,basisPct,drift1m})=>{
  const out={fundingAdj:0,oiAdj:0,basisAdj:0,totalAdj:0,details:[]};
  // Funding signal — mean-reversion bias on extremes
  if(Number.isFinite(funding)){
    const _fundingPct=funding*100; // convert decimal to percent
    // Threshold at 0.02% (modest), max at 0.05% (extreme)
    const _absF=Math.abs(_fundingPct);
    if(_absF>=0.02){
      const _scale=Math.min(1.0,(_absF-0.02)/(0.05-0.02));
      const _adj=Math.round(15*_scale);
      // Positive funding (longs paying shorts) → bias toward DOWN unwind
      out.fundingAdj=funding>0?-_adj:+_adj;
      out.details.push(`funding ${_fundingPct.toFixed(3)}% → ${out.fundingAdj>0?'+':''}${out.fundingAdj}`);
    }
  }
  // OI delta signal
  // V9.11.2: bloomberg.oiChange5m is a 5-minute window (not 30s). Real OI moves of
  //   conviction are often 2-5% per 5min during big drives. Raised threshold to
  //   ≥1.5% to filter noise; was ≥0.5% which made sense for 30s data.
  if(Number.isFinite(oiDelta)&&Number.isFinite(drift1m)){
    if(Math.abs(oiDelta)>=1.5&&Math.abs(drift1m)>=3){
      if(oiDelta>0&&drift1m>0){
        out.oiAdj=+8;
        out.details.push(`OI ↑${oiDelta.toFixed(1)}%/5m + price ↑${drift1m.toFixed(1)}bps → +8 conviction UP`);
      } else if(oiDelta>0&&drift1m<0){
        out.oiAdj=-8;
        out.details.push(`OI ↑${oiDelta.toFixed(1)}%/5m + price ↓${drift1m.toFixed(1)}bps → -8 shorts opening`);
      }
      // OI falling = position closing = no directional signal
    }
  }
  // Basis signal — mean-reversion bias on extremes
  if(Number.isFinite(basisPct)){
    const _absB=Math.abs(basisPct);
    // Threshold at 0.05% (modest), max at 0.15% (extreme)
    if(_absB>=0.05){
      const _scale=Math.min(1.0,(_absB-0.05)/(0.15-0.05));
      const _adj=Math.round(10*_scale);
      out.basisAdj=basisPct>0?-_adj:+_adj; // positive basis → DOWN bias (overheated)
      out.details.push(`basis ${basisPct.toFixed(3)}% → ${out.basisAdj>0?'+':''}${out.basisAdj}`);
    }
  }
  out.totalAdj=out.fundingAdj+out.oiAdj+out.basisAdj;
  return out;
};

const useDepthFlash=()=>{
  const[depth,setDepth]=useState({obImbalanceLive:0,liqLongWallLive:0,liqShortWallLive:0,liqLongUSDLive:0,liqShortUSDLive:0,depthUpdateAge:0,depthLastUpdate:0});
  useEffect(()=>{
    if(typeof window==='undefined')return;
    let mp=0;
    // V9.8.7: OKX via /api/okx/* rewrite (was Bybit V9.7.5, now blocked from Vercel).
    //   Same flow: get mark price first, then orderbook. Apply CT_VAL=0.01 to convert
    //   contract sizes to BTC units before computing USD wall depth.
    const SWAP='BTC-USDT-SWAP';
    const CT_VAL=0.01;
    const f=async()=>{
      try{
        const pR=await fetch(`/api/okx/public/mark-price?instType=SWAP&instId=${SWAP}`);
        const pJ=await pR.json();
        if(pJ?.code==='0'&&pJ?.data?.[0]){
          mp=parseFloat(pJ.data[0].markPx)||mp;
        }
        if(!mp)return;
        const dR=await fetch(`/api/okx/market/books?instId=${SWAP}&sz=50`);
        const dJ=await dR.json();
        if(dJ?.code!=='0'||!dJ?.data?.[0]?.bids||!dJ?.data?.[0]?.asks)return;
        const _bk=dJ.data[0];
        let mBW=0,mBP=0,tBL=0,mAW=0,mAP=0,tAL=0;
        _bk.bids.forEach(([p,q])=>{const pr=parseFloat(p),qt=parseFloat(q),dist=((mp-pr)/mp)*100;if(dist<2&&dist>0){const usd=pr*qt*CT_VAL;tBL+=usd;if(usd>mBW){mBW=usd;mBP=pr;}}});
        _bk.asks.forEach(([p,q])=>{const pr=parseFloat(p),qt=parseFloat(q),dist=((pr-mp)/mp)*100;if(dist<2&&dist>0){const usd=pr*qt*CT_VAL;tAL+=usd;if(usd>mAW){mAW=usd;mAP=pr;}}});
        const obImbal=tBL+tAL>0?(tBL-tAL)/(tBL+tAL):0; // -1=asks heavy, +1=bids heavy
        setDepth({obImbalanceLive:obImbal,liqLongWallLive:mAP,liqShortWallLive:mBP,liqLongUSDLive:tAL,liqShortUSDLive:tBL,depthUpdateAge:0,depthLastUpdate:Date.now()});
      }catch(e){/* silent fail */}
    };
    f();
    const _h=taraSetVizInterval(f,2500,45000); // was raw 2.5s always -- see V13.4.99
    return()=>_h.clear();
  },[]);
  return depth;
};

// V9.8.7: OKX MIGRATION — Bybit (V9.7.5) was reachable from browsers but blocked
//   from Vercel datacenter IPs (geographic + perimeter), and inconsistent from
//   US residential IPs as well. OKX has no equivalent block on its public API
//   and is reached via the /api/okx/* Vercel URL rewrite (same pattern as the
//   /api/kalshi/* and /api/news rewrites). No CORS, no auth, no API keys needed
//   for read-only public endpoints.
//
// OKX endpoint mapping (BTC-USDT-SWAP perp + BTC-USD index):
//   - /public/mark-price         → markPrice
//   - /market/index-tickers      → indexPrice (basis = markPrice - indexPrice)
//   - /public/funding-rate       → fundingRate, nextFundingTime
//   - /public/funding-rate-history (limit=2) → fundingRatePrev (last realized)
//   - /public/open-interest      → openInterest (BTC), openInterestUSD
//   - /rubik/stat/contracts/long-short-account-ratio → longShortRatio
//   - /market/books              → liquidation walls (apply 0.01 contract size)
//
// Same return shape as the Bybit version, so no downstream consumer changes
// needed (FlowPanel, engine FGT, Bloomberg/Liq signals, etc).
//
// Differences vs Bybit:
//   - 24h volume (binanceFuturesVol24h) defaults to 0 since OKX doesn't include
//     it in the same call set. Field kept for back-compat only — no signal uses
//     it as a primary input.
//   - longShortRatio comes pre-computed from OKX (single number) instead of
//     buy/sell ratios that we'd divide. Same downstream value.
//   - Order book sizes are CONTRACTS not coins. Multiplied by ctVal=0.01 BTC
//     for BTC-USDT-SWAP to get USD value of each level.
const useDerivativesData=()=>{
  const[data,setData]=useState({
    fundingRate:null,fundingRatePrev:null,nextFundingTime:0,
    openInterest:0,openInterestUSD:0,oiChange5m:0,
    basisBps:0,markPrice:0,indexPrice:0,
    longShortRatio:1,topTraderLSPositions:null,
    binanceFuturesVol24h:0,
    liqLongWall:0,liqShortWall:0,liqLongUSD:0,liqShortUSD:0,
    longWallAgeMs:0,shortWallAgeMs:0,
    lastUpdate:0,status:'connecting',
    _source:'okx', // V9.8.7: tag for diagnostics
  });
  const oiSnaps=useRef([]);
  const wallHistRef=useRef([]);
  useEffect(()=>{
    if(typeof window==='undefined')return;
    const SWAP='BTC-USDT-SWAP'; // OKX perpetual symbol
    const IDX='BTC-USD';        // OKX spot index symbol
    const CT_VAL=0.01;          // BTC-USDT-SWAP contract face = 0.01 BTC
    let stopped=false,busy=false;
    const read=async url=>{const r=await fetch(url,{signal:AbortSignal.timeout(6000),cache:'no-store'});if(!r.ok)throw new Error(`OKX HTTP ${r.status}`);return r.json();};
    const f=async()=>{
      if(stopped||busy)return;busy=true;
      try{
        const R=await Promise.allSettled([
          read(`/api/okx/public/mark-price?instType=SWAP&instId=${SWAP}`),
          read(`/api/okx/market/index-tickers?instId=${IDX}`),
          read(`/api/okx/public/funding-rate?instId=${SWAP}`),
          read(`/api/okx/public/funding-rate-history?instId=${SWAP}&limit=2`),
          read(`/api/okx/public/open-interest?instType=SWAP&instId=${SWAP}`),
          read(`/api/okx/rubik/stat/contracts/long-short-account-ratio?ccy=BTC&period=5m`),
          read(`/api/okx/market/books?instId=${SWAP}&sz=50`),
        ]);
        if(stopped)return;
        const now=Date.now();
        const quality=freshOkxResults(R,now);
        const[mpR,idxR,frR,frhR,oiR,lsR,bkR]=quality.values;
        // Missing groups become neutral/null, never a retained "live" signal.
        let u={lastUpdate:quality.lastUpdate,status:quality.status,_source:'okx',sourceHealth:quality.health,
          fundingRate:null,fundingRatePrev:null,nextFundingTime:0,openInterest:0,openInterestUSD:0,oiChange5m:0,oiChange5mReady:false,
          basisBps:null,markPrice:0,indexPrice:0,longShortRatio:1,topTraderLSPositions:null,
          liqLongWall:0,liqShortWall:0,liqLongUSD:0,liqShortUSD:0,longWallAgeMs:0,shortWallAgeMs:0};

        // ── MARK PRICE ──────────────────────────────────────────────────
        // OKX mark-price returns {code:"0", data:[{markPx, ts, ...}]}
        if(mpR.status==='fulfilled'&&mpR.value?.code==='0'&&mpR.value?.data?.[0]){
          u.markPrice=parseFloat(mpR.value.data[0].markPx)||0;
        }

        // ── INDEX PRICE ─────────────────────────────────────────────────
        // OKX index-tickers returns {code:"0", data:[{idxPx, ...}]}
        if(idxR.status==='fulfilled'&&idxR.value?.code==='0'&&idxR.value?.data?.[0]){
          u.indexPrice=parseFloat(idxR.value.data[0].idxPx)||0;
        }

        // ── BASIS ───────────────────────────────────────────────────────
        // (mark - index) / index in basis points. 100 bps = 1%.
        if(u.markPrice>0&&u.indexPrice>0){
          u.basisBps=((u.markPrice-u.indexPrice)/u.indexPrice)*10000;
        }

        // ── FUNDING RATE (current + next) ───────────────────────────────
        // OKX funding-rate returns {code:"0", data:[{fundingRate, nextFundingRate,
        //   fundingTime, nextFundingTime, ...}]}.
        if(frR.status==='fulfilled'&&frR.value?.code==='0'&&frR.value?.data?.[0]){
          const _fr=frR.value.data[0];
          u.fundingRate=parseFloat(_fr.fundingRate)||0;
          u.nextFundingTime=parseInt(_fr.nextFundingTime)||0;
        }

        // ── FUNDING RATE HISTORY (previous, for trend detection) ───────
        // OKX funding-rate-history returns {code:"0", data:[{realizedRate, fundingRate,
        //   fundingTime, ...}, ...]} most-recent-first. realizedRate is the actual
        //   settled rate (preferred); fundingRate falls back if realizedRate absent.
        if(frhR.status==='fulfilled'&&frhR.value?.code==='0'&&Array.isArray(frhR.value?.data)){
          const _h=frhR.value.data;
          if(_h[0]){
            const realized=parseFloat(_h[0].realizedRate);
            u.fundingRatePrev=Number.isFinite(realized)?realized:Number(_h[0].fundingRate); // zero is valid; an empty field is not zero
          }
        }

        // ── OPEN INTEREST ───────────────────────────────────────────────
        // OKX open-interest returns {code:"0", data:[{oi, oiCcy, oiUsd, ts, ...}]}.
        //   oiCcy = OI in BTC (matches Bybit's openInterest semantics).
        //   oiUsd = OI in USD (matches Bybit's openInterestValue).
        if(oiR.status==='fulfilled'&&oiR.value?.code==='0'&&oiR.value?.data?.[0]){
          const _oi=oiR.value.data[0];
          const oi=parseFloat(_oi.oiCcy)||0;
          oiSnaps.current.push({oi,time:now});
          oiSnaps.current=oiSnaps.current.filter(s=>now-s.time<600000);
          const o5=oiSnaps.current.find(s=>now-s.time>=270000&&now-s.time<=330000);
          u.openInterest=oi;
          u.openInterestUSD=parseFloat(_oi.oiUsd)||(oi*u.markPrice);
          u.oiChange5m=o5?((oi-o5.oi)/o5.oi)*100:0;
          u.oiChange5mReady=!!o5;
        }

        // ── LONG/SHORT ACCOUNT RATIO ────────────────────────────────────
        // OKX rubik long-short-account-ratio returns {code:"0",
        //   data:[[ts, ratio], ...]} most-recent-first.
        //   ratio = longs / shorts directly (single number, no division needed).
        if(lsR.status==='fulfilled'&&lsR.value?.code==='0'&&Array.isArray(lsR.value?.data)&&lsR.value.data[0]){
          const _r=parseFloat(lsR.value.data[0][1])||1;
          if(_r>0){
            u.longShortRatio=_r;
            // Account ratio is not top-trader positioning; that field stays unavailable.
          }
        }

        // ── ORDERBOOK (liquidation walls) ────────────────────────────────
        // OKX books returns {code:"0", data:[{asks:[[price, size, liqOrders, orderCt], ...],
        //   bids:[...], ts}]}. Size is in CONTRACTS — multiply by CT_VAL (0.01 BTC for
        //   BTC-USDT-SWAP) to get coin units, then by price to get USD value.
        //   Scan ±2% from mark price for biggest single-level USD + total USD on each side.
        //   Track persistence over 60s window for wall-age calculation.
        if(bkR.status==='fulfilled'&&bkR.value?.code==='0'&&bkR.value?.data?.[0]){
          const _bk=bkR.value.data[0];
          const mp=u.markPrice||0;
          if(mp>0&&Array.isArray(_bk.bids)&&Array.isArray(_bk.asks)){
            let mBW=0,mBP=0,tBL=0,mAW=0,mAP=0,tAL=0;
            _bk.bids.forEach(([p,q])=>{
              const pr=parseFloat(p),qt=parseFloat(q),dist=((mp-pr)/mp)*100;
              if(dist<2&&dist>0){const usd=pr*qt*CT_VAL;tBL+=usd;if(usd>mBW){mBW=usd;mBP=pr;}}
            });
            _bk.asks.forEach(([p,q])=>{
              const pr=parseFloat(p),qt=parseFloat(q),dist=((pr-mp)/mp)*100;
              if(dist<2&&dist>0){const usd=pr*qt*CT_VAL;tAL+=usd;if(usd>mAW){mAW=usd;mAP=pr;}}
            });
            u.liqLongWall=mAP;u.liqShortWall=mBP;u.liqLongUSD=tAL;u.liqShortUSD=tBL;
            // Wall persistence tracking — unchanged from Bybit/Binance versions
            const wH=wallHistRef.current;
            wH.push({t:now,longWall:mAP,shortWall:mBP});
            while(wH.length>0&&now-wH[0].t>60000)wH.shift();
            let lwAge=0;
            if(mAP>0){let oldest=now;for(const s of wH){if(s.longWall>0&&Math.abs((s.longWall-mAP)/mAP*10000)<10)oldest=Math.min(oldest,s.t);}lwAge=now-oldest;}
            let swAge=0;
            if(mBP>0){let oldest=now;for(const s of wH){if(s.shortWall>0&&Math.abs((s.shortWall-mBP)/mBP*10000)<10)oldest=Math.min(oldest,s.t);}swAge=now-oldest;}
            u.longWallAgeMs=lwAge;u.shortWallAgeMs=swAge;
          }
        }

        setData(prev=>({...prev,...u}));
      }catch(e){
        if(!stopped)setData(prev=>({...prev,status:'error',_source:'okx'}));
      }finally{busy=false;}
    };
    f();
    const _h=taraSetVizInterval(f,8000,60000);/*V13.4.99: was raw setInterval(f,8000)*/
    return()=>{stopped=true;_h.clear();};
  },[]);
  return data;
};

// ═══════════════════════════════════════
// V9.12: MACRO SHOCK DETECTOR (CROSS-ASSET SYNCHRONOUS MOVES)
// ═══════════════════════════════════════
// Polls SOL-USDT and XRP-USDT 1m candles to complement BTC/ETH for a 4-asset
//   coordinated-shock detector. The premise: when 3+ of 4 majors move ≥50bps
//   the same direction within 60 seconds, that's not idiosyncratic noise — it's
//   a macro event (Powell speech, ETF news, exchange outage, etc.) and price
//   reversion in the next 5-15min is rare. The signal nudges Tara's lock
//   confidence in the direction of the shock.
//
// Why SOL+XRP specifically: they're the next-most-liquid USDT pairs after
//   BTC/ETH on OKX, with tight spreads and reliable 1m candle data. Adding more
//   assets (DOGE, AVAX, etc.) hits diminishing returns — the correlation
//   structure is dominated by BTC's beta on all of them.
//
// Output: { btc:{deltaBps,price,ts}, eth:..., sol:..., xrp:..., status }
//   where deltaBps is the 60s return based on close-vs-previous-close. The
//   engine reads this and computes synchrony itself (not done in the hook
//   because BTC and ETH come from a different path).
const useMacroShockData=()=>{
  const[data,setData]=React.useState({sol:{deltaBps:0,price:0,ts:0},xrp:{deltaBps:0,price:0,ts:0},status:'init'});
  React.useEffect(()=>{
    const _fetchOne=async(inst)=>{
      try{
        // OKX 1m candles endpoint via the existing proxy. Returns most-recent-first.
        // The proxy prefix already maps to /api/v5, so the path must not repeat it;
        // the old doubled prefix resolved to a URL that 404s on OKX.
        const r=await fetch(`/api/okx/market/candles?instId=${inst}&bar=1m&limit=2`);
        if(!r.ok)return null;
        const j=await r.json();
        if(j?.code!=='0'||!Array.isArray(j?.data)||j.data.length<2)return null;
        const _now=parseFloat(j.data[0][4]); // close of most recent 1m candle
        const _prev=parseFloat(j.data[1][4]); // close of previous 1m candle
        if(!Number.isFinite(_now)||!Number.isFinite(_prev)||_prev<=0)return null;
        const _deltaBps=((_now-_prev)/_prev)*10000;
        return{price:_now,deltaBps:_deltaBps,ts:Date.now()};
      }catch(_){return null;}
    };
    const f=async()=>{
      const[_sol,_xrp]=await Promise.all([_fetchOne('SOL-USDT'),_fetchOne('XRP-USDT')]);
      setData(prev=>({
        sol:_sol||prev.sol,
        xrp:_xrp||prev.xrp,
        status:(_sol&&_xrp)?'live':(_sol||_xrp)?'partial':'error',
        lastUpdate:Date.now(),
      }));
    };
    f();
    // 8s cadence matches multi-TF candle polls — keeps the macro picture fresh
    //   without hammering the proxy. OKX rate limits at 40 req/2s; we're nowhere close.
    const iv=setInterval(f,8000);
    return()=>clearInterval(iv);
  },[]);
  return data;
};

// ═══════════════════════════════════════
// TARA CHART — TRADINGVIEW IFRAME EMBED
// Iframe is fully sandboxed — no DOM manipulation, no React conflicts
// key={resolution} forces clean remount on interval change
// ═══════════════════════════════════════
const TV_INTERVAL_MAP={'1m':'1','3m':'3','5m':'5','15m':'15','30m':'30','1h':'60'};

const TradingViewChart=({resolution,onResolutionChange,asset,priceSource})=>{
  const interval=TV_INTERVAL_MAP[resolution]||'1';
  // V9.8.9: TV symbol mapping moved into PRICE_SOURCES registry. Each source
  //   declares its own tvExch + tvSymbol(asset) function so adding/removing
  //   sources requires no changes here. OKX (V9.8.9) uses USDT-quoted pairs
  //   (BTCUSDT/ETHUSDT) where Coinbase + Kraken use USD pairs.
  const _src=PRICE_SOURCES[priceSource]||PRICE_SOURCES[PRICE_SOURCE_DEFAULT];
  const _tvExch=_src?.tvExch||'COINBASE';
  const _tvSym=_src?.tvSymbol?_src.tvSymbol(asset||'BTC'):(asset==='ETH'?'ETHUSD':'BTCUSD');
  const _tvFullSym=`${_tvExch}:${_tvSym}`;
  const src=[
    'https://www.tradingview.com/widgetembed/?frameElementId=tv_tara_101',
    `&symbol=${_tvExch}%3A${_tvSym}`,
    `&interval=${interval}`,
    '&hidesidetoolbar=1',
    // The control room owns the interval selector above the chart. Keep the
    // TradingView canvas and its native range controls, but remove the second
    // interval row that made the chart look like two products stacked together.
    '&hidetoptoolbar=1',
    '&symboledit=0',
    '&saveimage=0',
    '&toolbarbg=0F0E0C',
    '&studies=%5B%22Volume%40tv-basicstudies%22%5D',
    '&theme=dark',
    '&style=1',
    '&timezone=America%2FNew_York',
    '&locale=en',
    '&withdateranges=1',
    '&allow_symbol_change=0',
  ].join('');

  return(
    <div style={{userSelect:'none',width:'100%'}}>
      {/* Resolution toolbar */}
      <div className="flex items-center justify-between px-3 pb-2 flex-wrap gap-2">
        <div className={'flex items-center gap-0.5 bg-[#050508] rounded-lg border border-[#24242E] overflow-hidden'}>
          {['1m','3m','5m','15m','30m','1h'].map(r=>(
            <button key={r}
              onClick={()=>onResolutionChange&&onResolutionChange(r)}
              className={`px-3 py-1.5 text-xs font-bold uppercase tracking-wider transition-colors ${resolution===r?'bg-indigo-500/20 text-indigo-400':'text-[#EDEDED]/40 hover:text-[#EDEDED]/70'}`}>
              {r}
            </button>
          ))}
        </div>
        <span className={'text-xs text-[#EDEDED]/25 hidden sm:inline font-mono'}>{_tvFullSym} · TradingView</span>
      </div>

      {/* iframe — key remount on interval, asset, OR source change */}
      <iframe
        key={`${resolution}-${asset||'BTC'}-${priceSource||'coinbase'}`}
        src={src}
        className="tv-chart-container"
        style={{
          width:'100%',
          height:'430px',
          border:'none',
          borderRadius:'8px',
          background:'#050508',
          display:'block',
        }}
        allowFullScreen
        title={`Tara Live Chart — ${_tvFullSym}`}
        sandbox="allow-scripts allow-same-origin allow-popups allow-forms"
      />
    </div>
  );
};

// V13.4.352: A compact orientation rail for the live board. This is additive:
//   it names the authority chain without replacing any of the existing cards,
//   controls, or AutoTrade settings. The important distinction is deliberate:
//   Tara's committed lock is the record; AutoTrade is the execution layer; the
//   exchange position/reconciliation state is the money-truth layer.
const TaraPageBrief=({taraCall,snapshot,autoExecSettings,autoOrderState,userPosition,positionReconciliation,manualKalshiEntry,activeTicker,taraScorecards,taraCallLog,todayData,timeState,windowType,onNavigate})=>{
  const [inspectedEntry,setInspectedEntry]=React.useState(null);
  const [cloudStatus,setCloudStatus]=React.useState(()=>_cloudSyncStatus);
  React.useEffect(()=>subscribeCloudStatus(setCloudStatus),[]);
  const _snap=snapshot||null;
  const _lock=readLockState(_snap);
  const _dir=_lock.tradeable&&(_snap?.call==='UP'||_snap?.call==='DOWN')?_snap.call:null;
  const _confidence=_lock.tradeable&&Number.isFinite(Number(_lock.confidence))?Math.round(Number(_lock.confidence)):null;
  const _edgeRaw=_lock.tradeable?(_snap?.edgePts??_snap?.edge):null;
  const _edge=_edgeRaw==null?null:Number(_edgeRaw);
  const _edgeLabel=Number.isFinite(_edge)?`${_edge>0?'+':''}${Math.round(_edge)}pt`:'—';
  const _window=windowType||timeState?.windowType||'15m';
  const _record=_taraRecordStats(taraScorecards?.[_window]);
  const _reason=_lock.committed?capturedReason(_snap):taraCall?.blockedDecision?.reason||taraCall?.reason;
  const _lockAt=Number(_snap?.lockedAt)||0;
  const _lockTime=_lockAt>0?new Date(_lockAt).toLocaleTimeString([],{hour:'numeric',minute:'2-digit',second:'2-digit'}):null;
  const _recent=[...(Array.isArray(taraCallLog)?taraCallLog:[])]
    .filter(e=>e&&['WIN','LOSS','SITOUT'].includes(e.result)&&(e.windowType||'15m')===_window)
    .sort((a,b)=>Number(b.time||b.id||0)-Number(a.time||a.id||0)).slice(0,20).reverse();
  const _today=todayData?`${todayData.wins||0}W·${todayData.losses||0}L · ${todayData.sitouts||0}SO`:'No settled Calls today';
  return(
    <div className="tara-v145-call-record-grid">
    <section className="tara-page-brief tara-call-ledger" aria-label="Tara committed Call" data-call-tradeable={_lock.tradeable} data-edge-available={Number.isFinite(_edge)}>
      <div className="tara-call-ledger__hero">
        <div>
          <div className="tara-call-ledger__kicker">Tara’s Call · {_window} record</div>
          <div className="tara-v145-call-heading"><h3 className={_dir==='DOWN'?'is-down':_dir?'is-up':''}>{_dir||(_lock.satOut?'Sitting out':taraCall?.startupBlocked?'Waiting for data':'Scanning')}</h3><span className="tara-v145-badge">{_lock.tradeable?'Locked':_lock.satOut?'Final for this window':'No committed Call'}</span></div>
          <p>{_reason||'Tara is reading this window. No tradeable Call is locked yet.'}</p>
        </div>
        <div className="tara-call-ledger__truth">
          <strong>{_confidence!=null?_confidence:'—'}<span> /100</span></strong>
          <small>{_confidence!=null?'Captured signal confidence':_lock.satOut?'Sit-out recorded':'Awaiting capture'}</small>
          {_lockTime&&<small>Locked {_lockTime}</small>}
        </div>
      </div>
      <div className="tara-call-ledger__bottom">
        <div className="tara-v145-save-state"><span>{_lock.committed?'Original decision captured':'Candidate only · no Call captured'}</span><button onClick={()=>onNavigate?.('logs')}>Cloud {cloudStatus.state==='ok'?'available':cloudStatus.state==='error'?'error':cloudStatus.state||'unknown'} ↗</button></div>
        <div className="tara-call-ledger__actions"><button onClick={()=>onNavigate?.('brain')}>Why this Call ↗</button>{_lock.committed&&<button onClick={()=>setInspectedEntry(_snap)}>Inspect capture ↗</button>}</div>
      </div>
      <details className="tara-call-ledger__more">
        <summary>Call evidence, lifecycle &amp; record breakdown <span>{_lock.tradeable?`${_dir} lock preserved`:_lock.satOut?'Sit-out preserved':'Awaiting lock'}</span></summary>
        <div className="tara-call-ledger__timeline" aria-label="Call lifecycle"><span>OBSERVE</span><i>→</i><span>LEAN</span><i>→</i><b>{_lock.tradeable?'LOCKED':_lock.satOut?'SAT OUT':'AWAIT LOCK'}</b><i>→</i><span>SETTLE</span><i>→</i><span>{_window.toUpperCase()} RECORD</span></div>
        <div className="tara-call-ledger__details">
          <div><h4>What Tara locked</h4><strong className={_dir==='DOWN'?'is-down':''}>{_dir||(_lock.satOut?'SIT OUT':'—')}</strong><span>{_reason||(_lock.committed?'Committed snapshot preserved for settlement.':'No call has been committed for this window.')}</span><span>Confidence <b>{_confidence!=null?`${_confidence}%`:'—'}</b> · Edge <b>{_edgeLabel}</b></span><span>Snapshot <b>{_lock.committed?'preserved for settlement':'waiting'}</b></span></div>
          <div><h4>Record truth</h4><span>Won <strong>{_record.wins}</strong></span><span>Lost <strong>{_record.losses}</strong></span><span>Sat out <strong>{_record.sitouts}</strong></span><span>Window <strong>last {_record.total||0} recorded</strong></span></div>
        </div>
        <div className="tara-page-brief__meta">AutoTrade can use this lock as input, but its order, fill, exit, or P&amp;L never rewrites Tara's Call result. Hourly, Sports, and Weather keep separate records.</div>
        <div className="tara-call-ledger__deep-actions"><button onClick={()=>onNavigate?.('signals')}>WHAT TARA SAW</button><button onClick={()=>onNavigate?.('execution')}>INSPECT AUTOTRADE</button></div>
      </details>
    </section>
    <section className="tara-v145-record-panel" aria-label="Call record and recent outcomes"><div className="tara-v145-panel-head"><h3>Call record</h3><span>{_window} · rolling record</span></div><div className="tara-v145-record-totals"><div><strong>{_record.wins+_record.losses?`${(_record.wins/(_record.wins+_record.losses)*100).toFixed(1)}%`:'—'}</strong><small>Settled directional Calls</small></div><div><b className="is-up">{_record.wins}</b><small>Won</small></div><div><b className="is-down">{_record.losses}</b><small>Lost</small></div><div><b>{_record.sitouts}</b><small>Sat out</small></div></div><div className="tara-call-ledger__recent"><div className="tara-call-ledger__recent-summary"><b>RECENT {_recent.length}</b><span>Today {_today}</span><button onClick={()=>onNavigate?.('memory')}>Full record ↗</button></div><div className="tara-call-ledger__recent-tiles" aria-label={`Recent ${_window} Call results, oldest to newest`}>{_recent.length?_recent.map((entry,index)=><button type="button" key={`${entry.id||entry.time||index}-${index}`} className={entry.result==='WIN'?'is-win':entry.result==='LOSS'?'is-loss':'is-sitout'} aria-label={`Inspect ${entry.result} record ${index+1}`} title={`${entry.result} · ${entry.time?new Date(Number(entry.time)).toLocaleString():'time unavailable'}`} onClick={()=>setInspectedEntry(entry)}/>):<span>No recent settled Calls on this device</span>}</div></div></section>
    <RecordInspector entry={inspectedEntry} onClose={()=>setInspectedEntry(null)}/>
    </div>
  );
};

// This surface is display-only. A local order, manual marker, or successful POST
// is never promoted to an exchange position without a fresh Kalshi snapshot.
const TaraExecutionOverview=({snapshot,autoExecSettings,autoOrderState,userPosition,positionReconciliation,manualKalshiEntry,activeTicker,canReconcile,onReconcile,onDetails,onOpenSettings,compact=false})=>{
  const truth=readExchangePositionTruth({positionReconciliation,autoOrderState,userPosition,manualKalshiEntry,activeTicker});
  const order=readOrderState(autoOrderState);
  const lock=readLockState(snapshot);
  const successAt=Number(positionReconciliation?.lastSuccessAt)||0;
  const checkAge=successAt>0?Math.max(0,Date.now()-successAt):null;
  const fresh=checkAge!=null&&checkAge<=60000;
  const confirmedOpen=truth.state==='open'&&fresh;
  const lastSeenOpen=truth.state==='stale-open';
  const otherPositions=Array.isArray(positionReconciliation?.kalshiPositions)&&successAt>0
    ?positionReconciliation.kalshiPositions.filter(p=>p&&p.count>0&&p.ticker!==(autoOrderState?.ticker||activeTicker))
    :[];
  const status=String(order.status||'').toLowerCase();
  const lifecycle=status==='placing'||status==='submitted'?'placing'
    :status==='resting'?'resting'
    :status==='partially_filled'?'partial'
    :status==='filled'?'filled'
    :status==='exiting'||status==='exit-pending'?'exiting'
    :status==='exited'?'closed'
    :status==='unknown'||truth.state==='unknown'||truth.state==='reconcile'?'unknown'
    :truth.state==='flat'&&fresh?'flat':'checking';
  const steps=['FLAT','PLACING','RESTING','PARTIAL','FILLED','EXITING','CLOSED','UNKNOWN'];
  const stepIndex={flat:0,placing:1,resting:2,partial:3,filled:4,exiting:5,closed:6,unknown:7,checking:-1}[lifecycle];
  const side=truth.match?.side==='yes'?'UP':truth.match?.side==='no'?'DOWN':null;
  const positionText=confirmedOpen?`${truth.count} ${side||'CONTRACT'}${truth.count===1?'':'S'}`:lastSeenOpen?`LAST SEEN ${truth.count} ${side||'CONTRACT'}${truth.count===1?'':'S'}`:truth.match?`MISMATCH · ${truth.count} ${side||'CONTRACT'}${truth.count===1?'':'S'}`:'—';
  const filledAt=order.fillCents!=null&&order.fillCents>0?`${Math.round(order.fillCents)}¢`:'—';
  const realized=order.exited&&Number.isFinite(order.pnlPerContract)&&order.count>0
    ?`${order.pnlPerContract*order.count>=0?'+':''}$${((order.pnlPerContract*order.count)/100).toFixed(2)}`:null;
  const checkLabel=checkAge==null?'never':checkAge<60000?`${Math.floor(checkAge/1000)}s ago`:`${Math.floor(checkAge/60000)}m ago`;
  const stateLabel=confirmedOpen?`${lifecycle==='partial'?'PARTIAL FILL':'POSITION OPEN'} · MANAGE`
    :lastSeenOpen?'LAST SEEN OPEN · RECHECK'
    :truth.state==='reconcile'?'POSITION DRIFT · RECONCILE'
    :truth.state==='unknown'?'POSITION UNKNOWN · VERIFY'
    :truth.state==='stale-flat'?'LAST FLAT CHECK STALE · RECHECK'
    :truth.state==='flat'&&fresh?'FLAT · EXCHANGE CHECKED'
    :order.dryRun?'DRY RUN · NO VERIFIED POSITION'
    :truth.state==='unverified'?'EXCHANGE POSITION UNVERIFIED'
    :'POSITION CHECKING';
  const stateTone=confirmedOpen?'is-live':truth.state==='flat'&&fresh?'is-flat':'is-warn';
  if(compact)return <section className={'tara-exec-glance '+stateTone} aria-label="AutoTrade execution and exchange position">
    <div className="tara-exec-glance__head"><span className="tara-rail-kicker">AutoTrade</span><h2>{stateLabel}</h2><p>{order.status?String(order.status).replaceAll('_',' '):'No order placed'}</p></div>
    <details className="tara-v145-exec-detail" open={['reconcile','unknown','stale-open'].includes(truth.state)}><summary>Exchange evidence · {checkLabel}</summary><p>{truth.summary}</p>
    <div className="tara-exec-glance__facts">
      <div><label>Local order</label><strong>{order.status?String(order.status).replaceAll('_',' ').replaceAll('-',' '):'No order'}</strong></div>
      <div><label>Last exchange check</label><strong>{checkLabel}</strong></div>
    </div>
    {otherPositions.length>0&&<p className="tara-exec-glance__warning">{otherPositions.length} other Kalshi position{otherPositions.length===1?'':'s'} in the last snapshot · verify before another order.</p>}
    {autoExecSettings?.enabled&&autoExecSettings?.dryRun===false&&<p className="tara-exec-glance__warning">Live: real orders are sent to Kalshi. Each entry first reads your account and is refused if the daily loss, trade or window limits would be passed.</p>}
    {['reconcile','unknown','stale-open'].includes(truth.state)&&<p className="tara-exec-glance__warning">Unknown is not a no-fill. Verify Kalshi before retrying or exiting.</p>}
    </details><div className="tara-exec-glance__actions"><button onClick={onDetails}>Inspect execution ↗</button><button onClick={canReconcile?onReconcile:onOpenSettings}>{canReconcile?'Verify position':'Execution settings'}</button></div>
  </section>;
  return <section className={'tara-exec-overview '+stateTone} aria-label="AutoTrade execution and exchange position">
    <p className="tara-v145-reason"><b>Live entry is on when armed with dry run off.</b> Each real order first reads your Kalshi account and checks your limits. Execution never revises Tara’s captured Call.</p>
    {autoOrderState?.dryRun===true&&autoOrderState.paperSettlementPending&&<p className="tara-v145-reason">Paper fill awaiting its original market’s official settlement. This is not a live position or a new order.</p>}
    <EvidenceMetrics items={[
      ['Input Call',lock.tradeable?`${lock.dir} · locked`:lock.satOut?'Sit out':'No lock','Original Call record',lock.dir==='UP'?'is-up':lock.dir==='DOWN'?'is-down':''],
      ['Order state',order.status?order.status.replaceAll('_',' ').replaceAll('-',' '):'No order',order.dryRun?'Simulated order only':'Local order state; fill evidence below'],
      ['Position',confirmedOpen?positionText:truth.state==='flat'&&fresh?'Flat':lastSeenOpen?'Stale':truth.state==='reconcile'?'Drift':'Unverified',stateLabel],
      ['Execution',autoExecSettings?.enabled?(autoExecSettings?.dryRun?'Dry run':'Paused'):'Calling only','Live-money entry pause retained','is-gold'],
    ]}/>
    <div className="tara-v145-execution-grid"><section className="tara-v145-execution-panel"><div className="tara-v145-panel-head"><h3>Execution lifecycle</h3><span>{lifecycle.toUpperCase()}</span></div>
      <div className="tara-exec-overview__flow"><div className="tara-rail-kicker">ORDER LIFECYCLE · LIVE STATE</div><div className="tara-exec-overview__steps">{steps.map((s,i)=><span key={s} className={i===stepIndex?'is-current':i<stepIndex&&stepIndex!==7?'is-past':''}>{s}</span>)}</div></div>
      <div className="tara-v145-execution-rows"><p><span>Recorded Call</span><b>{lock.tradeable?`${lock.dir} · ${lock.confidence}/100`:lock.satOut?'SIT OUT':'Not committed'}</b></p><p><span>Active ticker</span><b>{activeTicker||'Unavailable'}</b></p><p><span>Entry attempt</span><b>{order.status?order.status.replaceAll('_',' ').replaceAll('-',' '):'No order attempted'}</b></p><p><span>Last exchange check</span><b>{checkLabel}</b></p><p><span>Exchange truth</span><b>{truth.summary}</b></p></div>
      <div className="tara-exec-overview__actions"><button onClick={canReconcile?onReconcile:onOpenSettings}>{canReconcile?'↻ RECONCILE NOW':'CONNECT KALSHI TO VERIFY'}</button></div>
    </section><section className="tara-v145-execution-panel"><div className="tara-v145-panel-head"><h3>Account &amp; controls</h3><span>{confirmedOpen?'KALSHI CONFIRMED':truth.label}</span></div><div className="tara-v145-execution-rows"><p><span>Trading mode</span><b>{autoExecSettings?.enabled?(autoExecSettings?.dryRun?'Dry run':'Live'):'Calling only'}</b></p><p><span>Local position marker</span><b>{userPosition?`${userPosition} · advisory only`:'None'}</b></p><p><span>Reported auto fill</span><b>{filledAt} · {order.placed?`${order.count} contract${order.count===1?'':'s'}`:'No confirmed local fill'}</b></p><p><span>Local exit estimate</span><b>{realized||'Not realized / verified'}</b></p><p><span>Retry safety</span><b>Unknown is not a no-fill</b></p><small>Local markers and estimates are not exchange proof. Size, cooldowns, exits, reconciliation and the kill switch stay available in Execution settings.</small></div><div className="tara-exec-overview__actions"><button onClick={onOpenSettings}>EXECUTION SETTINGS</button></div>
    <details className="tara-v145-disclosure"><summary>Fill &amp; estimate evidence</summary><div>{order.dryRun&&<p>Local order is simulated only.</p>}<p>{realized?'Local gross exit estimate excludes fees; verify on Kalshi.':'No realized, verified exit profit.'}</p><p>{confirmedOpen?'Exchange position verified; local order mode may differ.':truth.state==='flat'&&fresh?'Exchange-checked flat state.':'Local state is not exchange proof.'}</p></div>
    </details>
    </section></div>
    {otherPositions.length>0&&<div className="tara-exec-overview__other"><strong>{otherPositions.length} OTHER OPEN KALSHI POSITION{otherPositions.length===1?'':'S'} IN LAST SNAPSHOT</strong><p>These may belong to earlier windows. They are not this window's AutoTrade fill. Verify on Kalshi before placing or exiting another order.</p><div>{otherPositions.slice(0,8).map((p,i)=><span key={`${p.ticker}-${i}`}>{p.ticker} · {p.count} {p.side==='yes'?'YES':'NO'}</span>)}</div>{otherPositions.length>8&&<small>+{otherPositions.length-8} more in account; inspect Kalshi directly</small>}</div>}
  </section>;
};

const TaraExchangeAuditPanel=({state,onRefresh,connected})=>{
  const audit=state?.audit;
  const recent=(audit?.rows||[]).filter(r=>/^KXBTC15M-/.test(r.ticker||'')).slice(0,12);
  const money=n=>n==null?'—':`${n>=0?'+':''}$${n.toFixed(2)}`;
  return <section className="tara-tools-panel" aria-label="Read-only Kalshi execution audit">
    <div className="tara-tools-panel__head"><div><span className="tara-rail-kicker">KALSHI / ACCOUNT ACTIVITY</span><h3>Orders, fills &amp; settlements</h3></div><button disabled={!connected||state?.status==='loading'} onClick={onRefresh}>{state?.status==='loading'?'READING…':'READ EXCHANGE'}</button></div>
    <div className="tara-tools-panel__body">
      <p>This is a read-only account audit, separate from Tara’s Call and the local simulation. Nothing here submits or changes an order.</p>
      {!connected&&<p>Connect Kalshi in Execution Settings to read the account ledger.</p>}
      {audit&&<>
        <p><b>Retrieved</b><span>{audit.counts.orders} orders · {audit.counts.fills} fills · {audit.counts.settlements} settlements</span></p>
        <p><b>History</b><span>{audit.historyComplete?'all requested pages complete':'incomplete · no account-wide total'}</span></p>
        <p><b>Closed-market subtotal</b><span>{money(audit.closedNetDollars)} · {audit.counts.priced} priceable markets</span></p>
        <small>Cashflow is derived from exchange fills, their reported fees, and settlement revenue, only for complete closed markets. It is account activity, not AutoTrade-only profit, and does not include open-position value or transfers. This read does not verify a daily loss cap.</small>
        <div className="tara-tools-workspace__log">{recent.length?recent.map(row=><div key={row.ticker}><time>{row.settlementAt?new Date(row.settlementAt).toLocaleString():'not settled'}</time><b>{row.source.replaceAll('-',' ').toUpperCase()}</b><span>{row.ticker} · {row.fills} fills · {row.status.replaceAll('-',' ')} · net {money(row.netDollars)}</span></div>):<p>No BTC 15-minute exchange activity returned in this read. This does not prove no orders exist if the read is partial.</p>}</div>
      </>}
      {state?.errors?.length>0&&<p role="status" className="tara-exec-glance__warning">{state.errors.join(' · ')}</p>}
      <small>{state?.at?`Last read ${new Date(state.at).toLocaleString()}. `:''}Account details stay in this browser session; they are not written to the public Call record.</small>
    </div>
  </section>;
};

// V14.1: the approved first read, driven entirely by the existing live feeds.
// The chart remains the original TradingView widget; no example price, strike,
// market direction, or exchange state is copied from the review prototype.
const TaraLiveMarketMetrics=({asset,priceSource,currentPrice,targetMargin,strikeSource,activeTicker,timeState,windowType,quote,windowId})=>{
  const verified=strikeSource==='kalshi'&&!!activeTicker&&Number(targetMargin)>0;
  const clock=Number.isFinite(timeState?.minsRemaining)&&Number.isFinite(timeState?.secsRemaining)?`${String(timeState.minsRemaining).padStart(2,'0')}:${String(timeState.secsRemaining).padStart(2,'0')}`:'—:—';
  const q=inspectDecisionQuote(quote,{asset:asset||'BTC',windowId,ticker:activeTicker,now:Date.now()});
  const money=v=>Number(v)>0?`$${Number(v).toLocaleString(undefined,{minimumFractionDigits:2,maximumFractionDigits:2})}`:'Unavailable';
  return <EvidenceMetrics items={[
    [`${asset||'BTC'} / USD · ${PRICE_SOURCES[priceSource]?.name||'Reference feed'}`,money(currentPrice),'Live reference price'],
    ['Kalshi strike',verified?money(targetMargin):'Unavailable',verified?activeTicker:targetMargin>0?`Local ${strikeSource||'reference'} ${money(targetMargin)} · ticker unverified`:'Active ticker / strike not verified'],
    ['Window closes in',clock,`${windowType||'15m'} live window`,'is-gold'],
    ['Market price',q.ok?`YES ${q.ask}¢`:'Unavailable',q.ok?`NO ${(100-q.bid).toFixed(1)}¢ · executable quote`:`Quote ${q.reason||'not verified'}`],
  ]}/>;
};
const TaraOverviewMarket=({asset,priceSource,resolution,setResolution,currentPrice,targetMargin,strikeSource,activeTicker,timeState,pastWindows,windowType,timeFormat,orderBook,tapeWindows,smartMoneySignals,qualityGate,onNavigate,compactRead=false})=>{
  const _source=PRICE_SOURCES[priceSource]?.name||'Reference feed';
  const _clock=(Number.isFinite(timeState?.minsRemaining)&&Number.isFinite(timeState?.secsRemaining))?`${String(timeState.minsRemaining).padStart(2,'0')}:${String(timeState.secsRemaining).padStart(2,'0')}`:'—:—';
  const _kalshiStrike=strikeSource==='kalshi'&&!!activeTicker&&Number(targetMargin)>0;
  const _depthBuy=Number(orderBook?.localBuy)||0,_depthSell=Number(orderBook?.localSell)||0;
  const _depthTotal=_depthBuy+_depthSell;
  const _bidPct=_depthTotal>0?Math.round(_depthBuy/_depthTotal*100):null;
  const _tape=tapeWindows?.w30||{};
  const _tapeTotal=(Number(_tape.buys)||0)+(Number(_tape.sells)||0);
  const _activeSmart=Object.entries(smartMoneySignals||{}).filter(([,signal])=>signal?.detected).map(([name,signal])=>`${name==='pinning'?'PIN':name==='retailCrowd'?'FADE':name==='kalshiSpot'?'DIV':'ABSORB'} ${signal.side||''}`.trim());
  const _quality=Number(qualityGate?.score);
  return <div className="tara-v14-market-grid">
    <section className="tara-v14-chart" aria-label="Live TradingView chart">
      <div className="tara-v14-chart__head"><div><strong>TradingView · {asset||'BTC'}USD</strong><small>Original chart and interval controls</small></div></div>
      <TradingViewChart resolution={resolution} onResolutionChange={setResolution} asset={asset} priceSource={priceSource}/>
    </section>
    <section className="tara-v14-readout" aria-label="Live market read">
      {!compactRead&&<><div className="tara-v14-readout__price"><span>MARKET READ / LIVE FEED</span><strong>{Number(currentPrice)>0?`$${Number(currentPrice).toLocaleString(undefined,{minimumFractionDigits:2,maximumFractionDigits:2})}`:'Price unavailable'}</strong><small>{asset||'BTC'} reference price · {_source}</small></div>
      <div className="tara-v14-readout__pair"><div><span>Kalshi strike</span><strong className={!_kalshiStrike?'is-caution':''}>{_kalshiStrike?`$${Number(targetMargin).toLocaleString(undefined,{minimumFractionDigits:2,maximumFractionDigits:2})}`:'Unavailable'}</strong><small>{_kalshiStrike?activeTicker:targetMargin>0?`Local ${strikeSource||'reference'} strike $${Number(targetMargin).toLocaleString(undefined,{maximumFractionDigits:0})}; Kalshi ticker unverified`:'Active market ticker or strike not verified'}</small></div><div><span>Window closes in</span><strong>{_clock}</strong><small>{windowType||'15m'} live window</small></div></div></>}
      {compactRead&&<div className="tara-v145-panel-head"><h3>Market read</h3><span>{asset||'BTC'}</span></div>}
      <div className="tara-v14-readout__past"><PastWindowsPill pastWindows={pastWindows} windowType={windowType} timeFormat={timeFormat}/><small>Settled market direction · not Call wins or trade P&amp;L</small></div>
      <div className="tara-v14-readout__row"><span>Depth of market</span><b>{_bidPct!=null?`${_bidPct}% bid / ${100-_bidPct}% ask`:'Waiting for depth'}</b></div>
      <div className="tara-v14-readout__row"><span>Tape · 30s</span><b>{_tapeTotal>0?`${Math.round(Number(_tape.buyPct)||0)}% buy · ${_tape.side||'mixed'}`:'Waiting for tape'}</b></div>
      <div className="tara-v14-readout__row"><span>Smart money</span><b>{_activeSmart.length?_activeSmart.join(' · '):'No active detector'}</b></div>
      <div className="tara-v14-readout__row"><span>Window quality</span><b>{Number.isFinite(_quality)?`${Math.round(_quality)} / 100`:'Unavailable'}</b></div>
      <div className="tara-v14-readout__row"><span>Full evidence</span><button type="button" onClick={()=>onNavigate?.('signals')}>OPEN SIGNALS →</button></div>
    </section>
  </div>;
};

const TaraHourlyQuick=({onNavigate})=>{
  const [record,setRecord]=React.useState(()=>_hrLoad());
  React.useEffect(()=>{const id=setInterval(()=>setRecord(_hrLoad()),15000);return()=>clearInterval(id);},[]);
  const wins=Number(record?.w)||0,losses=Number(record?.l)||0;
  return <div className="tara-v14-hourly-quick"><span>HOURLY RECORD <b>{wins+losses?`${wins}W · ${losses}L`:'Awaiting record'}</b> · rolling 500</span><i/><span>{record?.pending?.length||0} pending hourly lock{record?.pending?.length===1?'':'s'}</span><button type="button" onClick={()=>onNavigate?.('schedule')}>HOURLY &amp; SCHEDULE →</button></div>;
};

const TaraHourlyRecordSummary=()=>{
  const [record,setRecord]=React.useState(()=>_hrLoad());
  React.useEffect(()=>{const id=setInterval(()=>setRecord(_hrLoad()),15000);return()=>clearInterval(id);},[]);
  const wins=Number(record?.w)||0,losses=Number(record?.l)||0;
  const cost=(key,fallback)=>{try{const v=Number(localStorage.getItem(key));return v>0?v:fallback;}catch(_e){return fallback;}};
  const minCost=cost('taraHourlyMinCost',45),maxCost=cost('taraHourlyMaxCost',60);
  return <section className="tara-hourly-record-summary" aria-label="Hourly rolling record">
    <div><span>HOURLY WINS</span><strong>{wins}</strong><small>rolling last 500 settled locks</small></div>
    <div><span>HOURLY LOSSES</span><strong>{losses}</strong><small>same rolling record</small></div>
    <div><span>WIN RATE</span><strong>{wins+losses?`${Math.round(100*wins/(wins+losses))}%`:'—'}</strong><small>{wins+losses} settled locks</small></div>
    <div><span>TARGET ENTRY</span><strong>{minCost}–{maxCost}c</strong><small>{record?.pending?.length||0} pending hourly locks · settings can override</small></div>
  </section>;
};

const TaraWorkspaceNav=({setShowAnalytics,setShowBrain,setShowHeaderOverflow,activeView,setActiveView})=>{
  const items=[['overview','Overview'],['execution','AutoTrade'],['signals','Signals'],['market','TradingView'],['schedule','Hourly'],['memory','Memory'],['news','News & macro'],['logs','Tools & sync']];
  const select=(id)=>{
    setActiveView(id);
    setShowAnalytics(false);
    setShowBrain(false);
    setShowHeaderOverflow(false);
    document.getElementById('tara-workspace-nav')?.scrollIntoView({behavior:'smooth',block:'start'});
  };
  return <div id="tara-workspace-nav" className="tara-workspace-switch">
    <nav className="tara-workspace-nav" aria-label="Trade and utility views">{items.map(([id,label])=><button key={id} onClick={()=>select(id)} aria-current={activeView===id?'page':undefined} className={activeView===id?'is-active':''}>{label}</button>)}</nav>
  </div>;
};

const TaraWorkspaceHeading=({view,analysis,qualityGate,newsSentiment,pendingKalshiCount=0,snapshot})=>{
  const brainLock=readLockState(snapshot);
  const copy={
    overview:['OPERATE / LIVE CONTROL ROOM','Tara Call + execution truth','One window. Four truths kept separate.'],
    execution:['TRADE / AUTOTRADE','AutoTrade','Order, fill, position and exit in one execution view.'],
    signals:['TRADE / SIGNALS','The evidence behind the Call','Price, strike, flow, depth, and timing together—without making live signals look like locked results.'],
    market:['TRADE / MARKET','TradingView','The original chart and its range tools stay intact. Tara’s indicators stay around it, not on top of it.'],
    analytics:['ANALYTICS / CALL RECORD','What the record tells us','Compare versions, timing and confidence using captured decisions.'],
    news:['TRADE / NEWS & MACRO','External context','Headlines, macro risk, and market flows inform the read; they do not rewrite a locked Call.'],
    memory:['TRADE / MEMORY','Tara’s memory','Original decisions, captured reasons and verified outcomes.'],
    schedule:['TRADE / HOURLY & SCHEDULE','Hourly picks','Fresh confirmation, exact contracts and a complete record.'],
    logs:['TRADE / TOOLS & SYNC','Operate Tara','Existing controls, diagnostics, integrations, logs, and reference help in one place.'],
    brain:['TARA / BRAIN','Tara’s Brain','An explanation of the evidence and the committed decision.'],
  }[view];
  if(!copy)return null;
  const post=analysis?.confidence!=null&&Number.isFinite(Number(analysis.confidence))?Number(analysis.confidence):null;
  const dir=analysis?.prediction?.includes('UP')?'UP':analysis?.prediction?.includes('DOWN')?'DOWN':'';
  const quality=qualityGate?.score;
  const fgtRaw=Number(analysis?.mtfAlignment)||0;
  const fgt=`${Math.abs(fgtRaw)<.05?'0':Math.abs(fgtRaw).toFixed(1).replace(/\.0$/,'')}/4${fgtRaw>.05?' UP':fgtRaw<-.05?' DN':''}`;
  const geoRisk=Number(newsSentiment?.geoRisk)||0;
  return <div className="tara-workspace-heading">
    <div className="tara-workspace-heading__copy"><span>{copy[0]}</span><h2>{copy[1]}</h2><p>{copy[2]}</p></div>
    <details className="tara-workspace-heading__pulse" aria-label="Live signal status"><summary>Live signal context</summary><div>
      <span title="Live signal score; not a committed Call">SIGNAL <b>{post==null?'—':post.toFixed(0)} /100{dir?` ${dir}`:''}</b></span>
      <span title="Current signal quality">QUALITY <b>{quality!=null?Math.round(quality):'—'}/100</b></span>
      <span title="Multi-timeframe FGT alignment">FGT <b>{fgt}</b></span>
      {geoRisk>=.3&&<span title="Current geographic news risk">GEO <b>{geoRisk>=.7?'HIGH':geoRisk>=.5?'ELEVATED':'WATCH'}</b></span>}
      {pendingKalshiCount>0&&<span title="Trades awaiting Kalshi resolution">PENDING <b>{pendingKalshiCount}</b></span>}
    </div></details>
  </div>;
};

const TaraApprovedRail=({autoExecSettings,mission,killSwitchEngaged,setShowTradingSettings,movementRisk,userPosition,positionReconciliation,autoOrderState,manualKalshiEntry,activeTicker,taraScorecards,windowType,compact=false})=>{
  const _auto=killSwitchEngaged?'KILLED · NEW ORDERS BLOCKED':autoExecSettings?.enabled?(autoExecSettings?.dryRun?'ARMED · DRY RUN':'ARMED · LIVE'):'DISARMED · SAFE';
  const _autoTone=killSwitchEngaged||autoExecSettings?.enabled&&autoExecSettings?.dryRun===false?'bad':autoExecSettings?.enabled?'ok':'muted';
  const _bank=Number(mission?.currentBankroll||mission?.startBankroll||0);
  const _risk=Number(autoExecSettings?.maxBetUsd||autoExecSettings?.betAmount||0);
  const _positionTruth=readExchangePositionTruth({positionReconciliation,autoOrderState,userPosition,manualKalshiEntry,activeTicker});
  const _position=_positionTruth.label;
  const _positionFresh=Number(positionReconciliation?.lastSuccessAt)>0&&Date.now()-Number(positionReconciliation.lastSuccessAt)<=60000;
  const _record=_taraRecordStats(taraScorecards?.[windowType]);
  const _recorded=_record.total;
  const _won=_record.wins;
  const _lost=_record.losses;
  const _sat=_record.sitouts;
  const _resolved=_record.scored;
  const _winRate=_record.winRate;
  const _riskScore=Number(movementRisk?.score||0);
  return <aside className="tara-approved-rail" aria-label="Execution and record rail">
    <section className="tara-rail-card tara-rail-mission">
      <div className="tara-rail-card__head"><div><div className="tara-rail-kicker">AUTOTRADE MISSION</div><h2>Execution guardrails</h2></div><small>single control surface</small></div>
      <div className="tara-rail-arm"><div><strong className={'tone-'+_autoTone}>{_auto}</strong><small>Simulated entries follow the locked Tara call; real entries remain paused</small></div><span className="tara-rail-status">{autoExecSettings?.enabled&&!killSwitchEngaged&&autoExecSettings?.dryRun!==false?'DRY':'OFF'}</span></div>
       <div className="tara-rail-rows"><span>Modeled mission bankroll <b>{_bank>0?`$${_bank.toFixed(2)}`:'not set'}</b></span><span>Risk per trade <b>{_risk>0?`$${_risk.toFixed(2)}`:'settings'}</b></span><span>Position state <b>{_position}</b></span><span>Exchange check <b className={positionReconciliation?.status==='in-sync'&&_positionFresh?'tone-ok':'tone-amber'}>{positionReconciliation?.status==='in-sync'&&_positionFresh?'verified':_positionFresh?'needs review':'stale / not verified'}</b></span></div>
      <div className="tara-rail-riskbar">{Array.from({length:10},(_,i)=><i key={i} className={i<Math.ceil(Math.max(0,_riskScore)/10)?'is-hot':''}/>)}</div><div className="tara-rail-kicker">risk state · {_riskScore}/100 movement</div>
      <div className="tara-rail-actions"><button className="tara-rail-action" onClick={()=>setShowTradingSettings(true)}>EXECUTION SETTINGS</button></div>
    </section>
    <section className="tara-rail-card">
      <div className="tara-rail-card__head"><div><div className="tara-rail-kicker">ALERT CENTER</div><h2>What needs attention</h2></div><small>live</small></div>
       <div className="tara-rail-alert"><b className={_positionTruth.tone==='warn'?'tone-bad':_positionTruth.tone==='live'?'tone-ok':'tone-muted'}>{_positionTruth.state==='reconcile'?'△ Reconciliation required':_positionTruth.state==='open'?'● Exchange position confirmed':_positionTruth.state==='flat'?'● Position truth':'◌ Position check'}</b><span>{_positionTruth.summary} {_positionTruth.detail}</span></div>
      <div className="tara-rail-alert"><b className={_riskScore>=70?'tone-bad':_riskScore>=40?'tone-amber':'tone-ok'}>ϟ Movement risk</b><span>{_riskScore>=70?'Extreme movement risk.':_riskScore>=40?'Elevated movement risk.':'Normal movement risk.'} {_riskScore}/100.</span></div>
      <div className="tara-rail-alert"><b className="tone-muted">↕ AutoTrade</b><span>{autoOrderState?.status?String(autoOrderState.status).replaceAll('-',' '):'No order lifecycle is active.'}</span></div>
    </section>
    <details className="tara-rail-card tara-rail-record" open={!compact}><summary><span>LAST 1,000 · TARA CALLS</span><strong>{_winRate!=null?`${_winRate}%`:'—'}</strong><small>scored win rate · sit-outs excluded</small></summary><div className="tara-rail-record__grid"><span>Won<b>{_won}</b></span><span>Lost<b>{_lost}</b></span><span>Sat out<b>{_sat}</b></span><span>Scored<b>{_resolved||'—'}</b></span></div><div className="tara-rail-record__note">{_recorded||'No'} recorded windows · hourly, sports, and weather records stay separate.</div></details>
    <section className="tara-rail-card tara-rail-capabilities"><div className="tara-rail-card__head"><div><div className="tara-rail-kicker">CAPABILITIES LEDGER</div><h2>Everything remains reachable</h2></div><small>inventory</small></div><div className="tara-rail-cap-grid"><span><b>TRADE</b>signal · fills · exit</span><span><b>PROTECT</b>kill · reconcile · no-fill</span><span><b>READ</b>chart · depth · tape</span><span><b>LEARN</b>memory · analytics</span><span><b>CONTEXT</b>news · macro · weather</span><span><b>OPERATE</b>schedule · logs · export</span></div></section>
  </aside>;
};

const TaraToolsWorkspace=({positionReconciliation,activeTicker,canReconcile,onReconcile,onOpenSync,onOpenTradingSettings,onOpenFeedSettings,onOpenStats,onOpenTraining,onOpenGuide,onOpenHelp,onOpenFlow,onOpenTheory})=>{
  const [events,setEvents]=React.useState(()=>_autoExecLogGet());
  const [filter,setFilter]=React.useState('all');
  const list=(Array.isArray(events)?events:[]).filter(e=>filter==='all'||e?.type===filter).slice(-30).reverse();
  const lastCheck=Number(positionReconciliation?.lastSuccessAt)||0;
  const actions=[
    ['EXECUTION SETTINGS','Trading rules, mission, risk, and AutoTrade controls',onOpenTradingSettings],
    ['SYNC & BASELINE','Reconcile device data, inspect sync health, and manage baseline',onOpenSync],
    ['FEED SETTINGS','Discord destinations and feed configuration',onOpenFeedSettings],
    ['PERFORMANCE STATS','Recorded outcomes and the existing performance view',onOpenStats],
    ['TRAINING ENGINE','Model training and diagnostics',onOpenTraining],
    ['FLOW INTELLIGENCE','Whales, tape, and flow context',onOpenFlow],
    ['THEORY LAB','Research and experimental diagnostics',onOpenTheory],
    ['BEST PRACTICES','Operating guide and recommended checks',onOpenGuide],
    ['HOW TARA WORKS','Reference help for the decision engine',onOpenHelp],
  ];
  const groups=[['OPERATE',actions.slice(0,3)],['INSPECT & LEARN',actions.slice(3,7)],['REFERENCE',actions.slice(7)]];
  return <section className="tara-tools-workspace" aria-label="Logs and sync workspace">
    <section className="tara-tools-panel"><div className="tara-tools-panel__head"><div><span className="tara-rail-kicker">CONNECTIVITY / EXCHANGE</span><h3>Position verification</h3></div></div><div className="tara-tools-panel__body"><p><b>Kalshi position check</b><span>{positionReconciliation?.status||'not checked'}</span></p><p><b>Last successful check</b><span>{lastCheck?new Date(lastCheck).toLocaleString():'not yet available'}</span></p><p><b>Active ticker</b><span>{activeTicker||'not available'}</span></p><button onClick={canReconcile?onReconcile:onOpenTradingSettings}>{canReconcile?'↻ RECONCILE NOW':'CONNECT KALSHI TO VERIFY'}</button><small>Market-data health is separate from this account-position check. Sync and feed settings are in Operations below.</small></div></section>
    <section className="tara-tools-panel"><div className="tara-tools-panel__head"><div><span className="tara-rail-kicker">MANUAL CONTROLS / REFERENCE</span><h3>Operations &amp; references</h3></div></div><div className="tara-tools-groups">{groups.map(([group,entries])=><section key={group}><h4>{group}</h4><div className="tara-tools-workspace__actions">{entries.map(([name,description,action])=><button key={name} onClick={action}><strong>{name}</strong><span>{description}</span><i aria-hidden="true">↗</i></button>)}</div></section>)}</div></section>
    <section className="tara-tools-panel"><div className="tara-tools-panel__head"><div><span className="tara-rail-kicker">AUTOTRADE / LOCAL EVENT LOG</span><h3>Recent execution events</h3></div><button onClick={()=>setEvents(_autoExecLogGet())}>REFRESH LOG</button></div><div className="tara-tools-workspace__filters">{['all','blocked','placed','filled','exited'].map(type=><button key={type} onClick={()=>setFilter(type)} aria-pressed={filter===type}>{type.toUpperCase()}</button>)}</div><div className="tara-tools-workspace__log">{list.length?list.map(e=><div key={e.id||`${e.ts}-${e.type}`}><time>{e.ts?new Date(e.ts).toLocaleString():'—'}</time><b>{String(e.type||'event').toUpperCase()}</b><span>{[e.asset,e.dir,e.guard,e.reason,e.ticker].filter(Boolean).join(' · ')||'Event recorded'}</span></div>):<p>No {filter==='all'?'local AutoTrade events':filter+' events'} recorded in this browser. This does not mean Kalshi has no orders.</p>}</div><div className="tara-tools-workspace__foot">This log is device-local. The exchange position check above is the authority for real open positions.</div></section>
  </section>;
};



// ═══════════════════════════════════════
// V99 ADVISOR STATE MACHINE
// ═══════════════════════════════════════
const computeAdvisor=(params)=>{
  const{userPosition,positionStatus,currentOdds,offerVal,betAmount,maxPayout,clockSeconds,windowType,tickSlope,isRugPull,showRugPullAlerts,hasReversedRef,peakOfferRef,posterior,targetMargin,currentPrice,minsRemaining,secsRemaining,accel,pnlSlope,atrBps,activePrediction,lockInfo,regime,
    // V6.2.8: Tara-aware inputs
    taraSnapshot,taraLean,taraPosterior,kalshiYesPrice,
    // V6.4: structural exhaustion signal
    convictionAsymmetry,
    // V9.7.6: Kalshi velocity for smarter in-trade advice
    kalshiHist}=params;
  // V9.7.6: KALSHI VELOCITY — change in YES price over last 30s.
  //   For UP positions: positive delta = market moving with us (good).
  //   For DOWN positions: positive YES delta = market moving against us (bad — DOWN value falling).
  //   Computed once here, used in HOLD/EXIT logic below.
  const _kalshiVel=(()=>{
    if(!Array.isArray(kalshiHist)||kalshiHist.length<3)return null;
    const _now=Date.now();
    const _recent=kalshiHist.filter(k=>_now-k.time<=2000);
    const _then=kalshiHist.filter(k=>_now-k.time>=28000&&_now-k.time<=32000);
    if(_recent.length===0||_then.length===0)return null;
    const _yesNow=_recent[_recent.length-1].yes;
    const _yesThen=_then[0].yes;
    return _yesNow-_yesThen; // cents over ~30s
  })();
  const intervalSeconds=windowType==='15m'?900:300;
  const timeRemainingFrac=Math.max(0,clockSeconds/intervalSeconds);
  const timeLabel=`${minsRemaining}m ${secsRemaining}s left`;
  const isLate=timeRemainingFrac<0.15;
  const isVeryLate=timeRemainingFrac<0.08;
  const cp=currentPrice||0;
  const tm=targetMargin||0;
  const gapBps=tm>0?((cp-tm)/tm)*10000:0;
  const isUP=userPosition==='UP';const isDN=userPosition==='DOWN';
  const isLocked=activePrediction?.includes('CONFIRMED');
  const lockDir=lockInfo?.dir||null;

  // V6.2.8: Tara summary — used in both minimal pre-entry and post-entry cross-reference.
  //   Determines what Tara is calling, what she'd have leaned if she sat out, edge math.
  // V13.4.275: a direction alone is NOT a commitment. This required only that the
  //   snapshot carry UP/DOWN, so an uncommitted snapshot rendered as "Locked DOWN"
  //   with an ENTER button while THIS TRADE, reading the same object, correctly
  //   said LEANING. The snapshot's own `locked` flag decides now.
  // V13.4.280: routed through readLockState, the single lock authority. Was two
  //   hand-rolled conditions here; the ORIGINAL bug was that neither checked
  //   `locked` at all, because the snapshot copy this receives did not carry it.
  const _lockSt=readLockState(taraSnapshot);
  const _taraCommitted=_lockSt.committed;
  const _taraDir=_lockSt.tradeable?_lockSt.dir
    :(taraLean?.dir||null); // even on SIT_OUT, taraLean might exist if she had a forming direction
  const _taraSatOut=_lockSt.satOut;
  // Implied direction Tara would have called if forced — useful display when sitting out.
  //   Use posterior if available, else lean.
  const _taraImpliedDir=_taraDir||(taraPosterior!=null?(taraPosterior>=50?'UP':'DOWN'):null);
  // V13.4.275: prefer the snapshot's conviction, expressed the same way THIS TRADE
  //   expresses it (50 + conviction). Deriving from atPosterior here while the card
  //   derived from conviction is why the two panels showed 78% and 56% for the same
  //   call. atPosterior stays as the fallback for snapshots without conviction.
  const _snapConvict=Number(taraSnapshot?.conviction);
  const _taraImpliedConf=_taraImpliedDir
    ?((Number.isFinite(_snapConvict)&&_snapConvict>0)?(50+_snapConvict)
      :_taraDir==='UP'?(taraSnapshot?.atPosterior||taraPosterior||50)
      :_taraDir==='DOWN'?(100-(taraSnapshot?.atPosterior||taraPosterior||50))
      :(taraPosterior!=null?(taraPosterior>=50?taraPosterior:(100-taraPosterior)):50))
    :50;
  // Edge for the Tara-implied direction
  const _kPctNow=kalshiYesPrice!=null?Number(kalshiYesPrice):null;
  const _taraEdge=_kPctNow!=null&&_taraImpliedDir
    ?(_taraImpliedDir==='UP'?_taraImpliedConf-_kPctNow:_taraImpliedConf-(100-_kPctNow))
    :null;

  // ── NO POSITION — pre-entry advisor ──
  // V6.2.8: Minimal mode. Tara is in charge of her own call card; advisor doesn't need
  //   to compete. Only shows hard hazards (rug pull, window closing, stop hit) loudly.
  //   For everything else: low-noise summary of where Tara stands.
  if(!userPosition){
    // Rug pull always first — only loud alert pre-entry
    if(isRugPull&&showRugPullAlerts)return{label:'RUG PULL — ABORT LONGS',reason:`Massive liquidity collapse detected. Do not enter long. [${timeLabel}]`,color:'rose',animate:true,hasAction:false};
    // Window genuinely closed
    if(isVeryLate)return{label:'WINDOW CLOSED',reason:`Only ${secsRemaining}s remain · entry window closed · wait for next candle`,color:'zinc',animate:false,hasAction:false};
    // V7.9: NO TRADE — explicit no-go. Three categories. No entry button regardless.
    if(taraSnapshot&&(taraSnapshot.call==='NO_TRADE'||taraSnapshot.isNoGo===true)){
      const _cat=taraSnapshot.noGoCategory||'no-go';
      const _catLabel=_cat==='no-go-edge'?'NEGATIVE EDGE':_cat==='no-go-data'?'DATA UNAVAILABLE':_cat==='no-go-coinflip-late'?'NO TIME':'NO TRADE';
      return{
        label:`NO TRADE — ${_catLabel}`,
        reason:`${taraSnapshot.reason||'No clean trade this window'} · ${timeLabel}`,
        color:'rose',animate:false,hasAction:false,
      };
    }
    // Tara has committed UP/DOWN — show as a simple badge with edge, no shouting
    // V13.4.275: now also requires the snapshot's own `locked` flag. The comment
    //   above always said "has committed", but the condition only checked that a
    //   direction existed -- so this branch fired on an uncommitted snapshot and
    //   offered an ENTER button for a lock that had not happened.
    if(_taraCommitted&&(taraSnapshot.call==='UP'||taraSnapshot.call==='DOWN')){
      const _isUserForcedLock=taraSnapshot.isUserForced;
      const _color=taraSnapshot.call==='UP'?'emerald':'rose';
      const _conf=Math.round(_taraImpliedConf);
      const _edgeStr=_taraEdge!=null?` · edge ${_taraEdge>=0?'+':''}${Math.round(_taraEdge)}pt`:'';
      const _forceTag=_isUserForcedLock?' (forced)':'';
      // V7.7.1: STALE-LOCK CHECK. The lock was made earlier — conditions may have inverted
      //   since. The user screenshot showed "Tara DOWN 85% · ENTER DOWN" while live posterior
      //   was 89% UP and Kalshi was 99.7% UP. Pressing ENTER would have been an instant loss.
      //   Three signals of staleness: live posterior has flipped 25+ pts against the lock,
      //   Kalshi has moved 30+ pts against the lock direction, or live edge is now deeply
      //   negative (≤-15pt). Any one of these → kill the ENTER button and warn EXIT.
      const _liveTaraDirConf=taraSnapshot.call==='UP'?(taraPosterior||50):(100-(taraPosterior||50));
      const _lockTaraDirConf=taraSnapshot.atPosterior!=null?(taraSnapshot.call==='UP'?taraSnapshot.atPosterior:(100-taraSnapshot.atPosterior)):_liveTaraDirConf;
      const _posteriorFlipped=_liveTaraDirConf<=(_lockTaraDirConf-25);
      const _liveKalshiDirConf=_kPctNow!=null?(taraSnapshot.call==='UP'?_kPctNow:(100-_kPctNow)):null;
      const _kalshiAtLockDirConf=taraSnapshot.kalshiAtLock!=null?(taraSnapshot.call==='UP'?taraSnapshot.kalshiAtLock:(100-taraSnapshot.kalshiAtLock)):null;
      const _kalshiFlipped=_liveKalshiDirConf!=null&&_kalshiAtLockDirConf!=null&&_liveKalshiDirConf<=(_kalshiAtLockDirConf-30);
      const _liveEdge=_kPctNow!=null?(taraSnapshot.call==='UP'?_liveTaraDirConf-_kPctNow:_liveTaraDirConf-(100-_kPctNow)):null;
      const _edgeDeeplyNegative=_liveEdge!=null&&_liveEdge<=-15;
      const _isStaleLock=_posteriorFlipped||_kalshiFlipped||_edgeDeeplyNegative;
      if(_isStaleLock){
        const _staleReasons=[];
        if(_posteriorFlipped)_staleReasons.push(`posterior flipped ${Math.round(_lockTaraDirConf)}→${Math.round(_liveTaraDirConf)}%`);
        if(_kalshiFlipped)_staleReasons.push(`Kalshi flipped ${Math.round(_kalshiAtLockDirConf)}→${Math.round(_liveKalshiDirConf)}%`);
        if(_edgeDeeplyNegative)_staleReasons.push(`edge ${Math.round(_liveEdge)}pt`);
        return{
          label:`Tara LOCKED ${taraSnapshot.call} — STALE`,
          reason:`Conditions inverted: ${_staleReasons.join(', ')} · ${timeLabel}`,
          color:'rose',animate:true,hasAction:false, // critical: NO ENTER button on stale lock
        };
      }
      return{
        label:`Tara ${taraSnapshot.call} ${_conf}%${_forceTag}`,
        reason:`Locked ${taraSnapshot.call}${_edgeStr} · ${timeLabel}`,
        color:_color,animate:false,hasAction:true,actionLabel:`ENTER ${taraSnapshot.call}`,actionTarget:taraSnapshot.call,
      };
    }
    // Tara sat out — show what direction she'd have leaned if forced
    if(_taraSatOut){
      const _impliedConf=Math.round(_taraImpliedConf);
      const _impliedStr=_taraImpliedDir?` · would lean ${_taraImpliedDir} ${_impliedConf}%`:'';
      const _edgeStr=_taraEdge!=null?` · edge ${_taraEdge>=0?'+':''}${Math.round(_taraEdge)}pt`:'';
      const _reasonStr=taraSnapshot?.reason||'no clean entry';
      return{
        label:`Tara sitting out${_impliedStr}`,
        reason:`${_reasonStr}${_edgeStr} · ${timeLabel}`,
        color:'zinc',animate:false,hasAction:false,
      };
    }
    // Tara is still scanning — minimal status. Show lean if she's developing one.
    if(taraLean){
      const _conf=Math.round(taraLean.conviction);
      return{
        label:`Tara watching ${taraLean.dir}`,
        reason:`${_conf}pt ${taraLean.dir} lean · awaiting confirmation · ${timeLabel}`,
        color:taraLean.dir==='UP'?'emerald':'rose',animate:false,hasAction:false,
      };
    }
    // Pure scanning — no lean yet
    if(activePrediction==='CLOSED'||activePrediction==='SIT OUT')return{label:'TRADE CLOSED',reason:`Position closed · ${timeLabel}`,color:'zinc',animate:false,hasAction:false};
    return{
      label:'Tara scanning',
      reason:`No direction yet · ${Math.round(taraPosterior||posterior||50)}% UP / ${Math.round(100-(taraPosterior||posterior||50))}% DOWN · ${timeLabel}`,
      color:'zinc',animate:false,hasAction:false,
    };
  }

  // ── IN-TRADE ADVISOR ──
  // V9.8.13: LIVE-direction variables for in-trade reversal detection. The pre-entry
  //   path uses _taraImpliedDir (derived from taraSnapshot.call — frozen at lock time).
  //   That's correct pre-entry: it shows what Tara COMMITTED to. But in-trade, we need
  //   the LIVE read — when the engine's continuously-updating posterior flips against
  //   the user, the TARA REVERSED branch should fire, the AGREES branch should re-
  //   evaluate, etc. Mirrors the pre-entry stale-lock detection (L3525-3550) which
  //   only ran before entry; now applied throughout the trade.
  const _liveDir = taraPosterior!=null ? (taraPosterior>=50?'UP':'DOWN') : null;
  const _liveConf = taraPosterior!=null ? (taraPosterior>=50?taraPosterior:(100-taraPosterior)) : 50;
  // Lock-direction conviction at lock time and now — for measuring how far the live
  //   posterior has drifted from the lock-time conviction in the lock's direction.
  //   Used to decide if the trade conditions have meaningfully inverted (≥25pt drop)
  //   even when the live direction hasn't crossed 50 yet.
  const _lockDir = (taraSnapshot?.call==='UP'||taraSnapshot?.call==='DOWN')?taraSnapshot.call:(lockInfo?.dir||null);
  const _lockDirConfAtLock = (taraSnapshot?.atPosterior!=null&&_lockDir)
    ?(_lockDir==='UP'?taraSnapshot.atPosterior:(100-taraSnapshot.atPosterior))
    :null;
  const _lockDirConfNow = (taraPosterior!=null&&_lockDir)
    ?(_lockDir==='UP'?taraPosterior:(100-taraPosterior))
    :null;
  const _livePosteriorInverted = _lockDirConfAtLock!=null&&_lockDirConfNow!=null
    &&_lockDirConfNow<=_lockDirConfAtLock-25;
  const _kalshiAtLockDirConf = (taraSnapshot?.kalshiAtLock!=null&&_lockDir)
    ?(_lockDir==='UP'?taraSnapshot.kalshiAtLock:(100-taraSnapshot.kalshiAtLock))
    :null;
  const _kalshiNowDirConf = (_kPctNow!=null&&_lockDir)
    ?(_lockDir==='UP'?_kPctNow:(100-_kPctNow))
    :null;
  const _kalshiLiveInverted = _kalshiAtLockDirConf!=null&&_kalshiNowDirConf!=null
    &&_kalshiNowDirConf<=_kalshiAtLockDirConf-30;
  const pnlPct=positionStatus?.pnlPct||0;
  const offerAboveBet=offerVal>betAmount;
  const profitPct=offerAboveBet?((offerVal-betAmount)/Math.max(1,betAmount))*100:0;
  const peakOffer=peakOfferRef?.current||0;
  const drawdownFromPeak=peakOffer>0?((peakOffer-offerVal)/peakOffer):0;
  const momentumWith=(isUP&&tickSlope>0)||(isDN&&tickSlope<0);
  const momentumAgainst=(isUP&&tickSlope<0)||(isDN&&tickSlope>0);
  const adverseAccel=(isUP&&(accel||0)<-0.5)||(isDN&&(accel||0)>0.5);
  // V13.4.283: this is the LIVE model read flipped to the position's side, not the
  //   confidence frozen at commit. Both are legitimate -- live is the right number
  //   for "should I get out now" -- but every line below quoted it as a bare
  //   "Tara: 71%" while the card, the badge and the edge row all showed the
  //   committed 67%. Four numbers, one name. They now read "Tara now: 71%", so the
  //   live figure is identifiable as live instead of contradicting the call.
  const winSide=isUP?posterior:(100-posterior); // LIVE model confidence, not the lock
  // REAL truth: is price actually above/below strike right now?
  const gapForPosition=isUP?gapBps:-gapBps;
  const isActuallyWinning=gapForPosition>0;
  const isActuallyLosing=gapForPosition<-5;
  const gapMagnitude=Math.abs(gapForPosition);
  const gapStr=`Gap: ${gapForPosition>=0?'+':''}${gapForPosition.toFixed(1)} bps`;
  // Direction reliability: UP 66% (331 trades), DOWN 57% — gap narrowed with better DOWN gating
  const dirReliability=isUP?1.0:0.86;
  // Regime reliability from data: SS 67%, TD 86%, HVC 59%, RC 56%
  const _rg=regime||'RANGE-CHOP';
  const regimeReliability=_rg==='TRENDING DOWN'?1.28:_rg==='SHORT SQUEEZE'?1.0:_rg==='HIGH VOL CHOP'?0.88:0.84;
  // Comeback score: how likely is Tara's model to be right when adverse?
  // High score = hold, low score = cut
  const comebackScore=Math.max(0,
    (winSide*dirReliability*regimeReliability)   // base model confidence adjusted
    - (gapMagnitude*2.5)                          // penalise depth of adverse
    - (isLate?25:0)                               // time pressure penalty
    - (adverseAccel?15:0)                         // momentum worsening penalty
    + (momentumWith?10:0)                         // momentum recovering bonus
  );

  // V12.1 COACH LANGUAGE HELPERS — plain-English atoms for every in-trade branch.
  //   These translate raw numbers into human sentences so the advisor reads like
  //   a trader calling it, not a dashboard logging it.
  //
  //   _velAdvStr   — Kalshi velocity moving AGAINST the position (market sees it first)
  //   _velConfStr  — Kalshi velocity moving WITH the position (tape confirming)
  //   _driftStr    — how much Tara’s own conviction has shifted since lock
  //   _kDriftStr   — how far the Kalshi price has moved since lock
  //   _timePress   — urgency tag when time is short
  //   _peakStr     — peak-profit context when the trade was winning
  const _velAdvStr=_kalshiVel!=null&&((isUP&&_kalshiVel<=-3)||(isDN&&_kalshiVel>=3))
    ?(isUP
      ?`Kalshi pricing DOWN at ${Math.abs(_kalshiVel).toFixed(0)}¢/30s — market sees it before the chart`
      :`Kalshi pricing UP at ${Math.abs(_kalshiVel).toFixed(0)}¢/30s — market sees it before the chart`
    ):null;
  const _velConfStr=_kalshiVel!=null&&((isUP&&_kalshiVel>=3)||(isDN&&_kalshiVel<=-3))
    ?`Kalshi running ${isUP?'UP':'DOWN'} with you at ${Math.abs(_kalshiVel).toFixed(0)}¢/30s`
    :null;
  const _driftPts12=_lockDirConfAtLock!=null&&_lockDirConfNow!=null?Math.round(_lockDirConfNow-_lockDirConfAtLock):null;
  const _driftStr12=_driftPts12!=null&&Math.abs(_driftPts12)>=5
    ?(_driftPts12>0
      ?`Tara’s conviction up ${_driftPts12}pts since lock`
      :`Tara’s conviction slipped ${Math.abs(_driftPts12)}pts since lock (${Math.round(_lockDirConfAtLock)}→${Math.round(_lockDirConfNow)}%)`
    ):null;
  const _kDriftPts12=_kalshiAtLockDirConf!=null&&_kalshiNowDirConf!=null?Math.round(_kalshiNowDirConf-_kalshiAtLockDirConf):null;
  const _kDriftStr12=_kDriftPts12!=null&&Math.abs(_kDriftPts12)>=8
    ?(_kDriftPts12<0
      ?`Kalshi flipped ${Math.abs(_kDriftPts12)}pts against you (${Math.round(_kalshiAtLockDirConf)}→${Math.round(_kalshiNowDirConf)}¢)`
      :`Kalshi moved ${_kDriftPts12}pts with you (${Math.round(_kalshiAtLockDirConf)}→${Math.round(_kalshiNowDirConf)}¢)`
    ):null;
  const _timePress12=timeRemainingFrac<0.10?'under 90s left':timeRemainingFrac<0.20?'under 3m left':timeRemainingFrac<0.33?'under 5m left':'';
  const _peakStr12=(peakOffer>betAmount&&betAmount>0)?`was +${(((peakOffer-betAmount)/betAmount)*100).toFixed(0)}% at peak`:null;

  // ── 1. HARD STOPS — always fire first ────────────────────────────────────
  if(positionStatus?.isStopHit)return{label:'30% STOP HIT — EXIT NOW',reason:`Hard stop breached. Entry: $${(positionStatus.entry||0).toFixed(0)} → Now: $${cp.toFixed(0)}. PnL: ${pnlPct.toFixed(1)}%. [${timeLabel}]`,color:'rose',animate:true,hasAction:true,actionLabel:'EXECUTE EMERGENCY EXIT',actionTarget:'SIT OUT'};
  if(isRugPull&&showRugPullAlerts&&isUP)return{label:'RUG PULL — EMERGENCY EXIT',reason:`Catastrophic drop detected. Exit long immediately. [${timeLabel}]`,color:'rose',animate:true,hasAction:true,actionLabel:'EMERGENCY CASHOUT',actionTarget:'SIT OUT'};

  // V6.4 — STRUCTURE BROKE EXIT. Active post-lock exit signal. When the latest swing is
  //   meaningfully stronger in the opposite direction of the user's position, that's
  //   trend exhaustion: the move that's been working is fading, and the counter-move
  //   has more force. Real traders' edge is exiting fast when the structure changes,
  //   not predicting better. Fires loud BEFORE the TARA REVERSED tier.
  if(convictionAsymmetry?.valid&&convictionAsymmetry.score>=50&&convictionAsymmetry.latestDir&&convictionAsymmetry.latestDir!==userPosition){
    const _strengthMsg=`Latest ${convictionAsymmetry.latestDir} swing ${convictionAsymmetry.score}pt stronger than prior ${userPosition} swing`;
    return{
      label:`⚠ STRUCTURE BROKE — ${convictionAsymmetry.latestDir} TAKING OVER`,
      reason:`${_strengthMsg} · trend you bought is fading · ${timeLabel}`,
      color:'rose',animate:true,hasAction:true,
      actionLabel:offerAboveBet?'TAKE PROFIT — EXIT NOW':'CUT — STRUCTURE GONE',
      actionTarget:offerAboveBet?'CASH':'SIT OUT',
    };
  }

  // V6.2.8 — TARA CROSS-REFERENCE. When user has a manual position and Tara now thinks
  //   the OPPOSITE direction with strong conviction, surface that as urgency. Doesn't
  //   force exit (user might have their own read), but flags the disagreement clearly.
  //   Three escalating tiers based on Tara's conviction in the opposite direction.
  // V9.8.13: now reads LIVE posterior (_liveDir / _liveConf), not the frozen lock
  //   snapshot. The original code used _taraImpliedDir which was derived from
  //   taraSnapshot.call — meaning if user locked UP and live posterior rotated to
  //   70% DOWN mid-window, this branch could not fire because _taraImpliedDir was
  //   still 'UP'. That was exactly the "stale-anchor" bug the user reported as
  //   "calls for opposite position which feels stupid": engine had genuinely flipped
  //   off their side, advisor stayed silent. Live read fixes it.
  if(_liveDir&&_liveDir!==userPosition&&_liveConf>=58){
    const _opposingConf=Math.round(_liveConf);
    const _gapStr=`Gap: ${gapForPosition>=0?'+':''}${gapForPosition.toFixed(1)} bps`;
    if(_liveConf>=72){
      // High conviction opposite — Tara says you're on the wrong side, urgent
      const _rvNote=[_driftStr12,_velAdvStr,_kDriftStr12].filter(Boolean).join('. ');
      return{label:`FLIP — engine reads ${_liveDir} ${_opposingConf}%`,reason:`Tara reversed on your ${userPosition} trade.${_rvNote?` ${_rvNote}.`:''} ${_gapStr}. ${_timePress12?_timePress12+'. ':''}Exit or know exactly why you’re holding against the engine.`,color:'rose',animate:true,hasAction:true,actionLabel:'CASHOUT NOW',actionTarget:'CASH'};
    }
    if(_liveConf>=65){
      // Medium conviction opposite — flag it, but action depends on profit/gap
      const _action=offerAboveBet?'TAKE PROFIT NOW':'EXIT NOW';
      const _oppNote=[_velAdvStr,_kDriftStr12].filter(Boolean).join('. ');
      return{label:`ENGINE DRIFTING ${_liveDir} ${_opposingConf}% — watch`,reason:`Tara leaning ${_liveDir} against your ${userPosition} position.${_oppNote?` ${_oppNote}.`:''} Not a full reversal yet — but the setup that justified entry is weakening. ${_gapStr}. ${timeLabel}.`,color:'amber',animate:true,hasAction:true,actionLabel:_action,actionTarget:offerAboveBet?'CASH':'SIT OUT'};
    }
    // Mild opposition — informational, no forced action
    return{label:`Tara leaning ${_liveDir} ${_opposingConf}%`,reason:`Mild disagreement with your ${userPosition} · ${_gapStr} · ${timeLabel}`,color:'amber',animate:false,hasAction:false};
  }
  // V9.8.13: even when live direction still matches user, fire a softer warning if the
  //   conviction in the lock direction has eroded ≥25pt (live posterior drift) OR Kalshi
  //   has flipped 30+pt against. The position hasn't formally inverted but the conditions
  //   that justified the lock are gone. This is the "STRUCTURE BROKE" early signal.
  if((_livePosteriorInverted||_kalshiLiveInverted)&&!isActuallyLosing){
    const _why=[];
    if(_livePosteriorInverted)_why.push(`posterior conviction ${Math.round(_lockDirConfAtLock)}→${Math.round(_lockDirConfNow)}%`);
    if(_kalshiLiveInverted)_why.push(`Kalshi ${Math.round(_kalshiAtLockDirConf)}→${Math.round(_kalshiNowDirConf)}%`);
    const _erodeParts=[...(_why||[]),_velAdvStr,_kDriftStr12].filter(Boolean);
    const _erodeDetail=_erodeParts.length?_erodeParts.join('. ')+'.':'';
    return{label:`CONDITIONS ERODING · ${userPosition}`,reason:`Edge that justified this lock is fading. ${_erodeDetail} Position is ${isActuallyWinning?`winning by ${gapForPosition.toFixed(0)}bps — consider locking in`:'near the line — thin margin for error'}. ${_timePress12||timeLabel}.`,color:'amber',animate:false,hasAction:true,actionLabel:offerAboveBet?'TAKE PROFIT':'TIGHTEN EXIT',actionTarget:'CASH'};
  }

  // ── 2. DEEP ADVERSE — always cut regardless of model confidence ───────────
  // Data: even 89% UP calls fail in adverse. Don't let model override reality.
  if(gapMagnitude>30&&isActuallyLosing){
    const _deepNote=_velAdvStr?` ${_velAdvStr}.`:(_kDriftStr12?` ${_kDriftStr12}.`:'');
    return{label:`CUT — DEEP ADVERSE`,reason:`${gapMagnitude.toFixed(0)} bps against you — too far to realistically recover.${_deepNote} ${gapStr}. Take the loss now, not after it gets worse. [${timeLabel}]`,color:'rose',animate:true,hasAction:true,actionLabel:'CUT NOW',actionTarget:'SIT OUT'};
  }

  // ── V9.7.8: EARLY-CUT WHEN KALSHI ALSO INVERTS ───────────────────────────
  // Old gate: CUT LOSSES needed >30 bps adverse OR comebackScore < 35.
  // New: combined signal — 5 bps adverse AND Kalshi YES inverting ≥3¢/30s
  // means BOTH the spot and the prediction market are pricing the trade as
  // wrong. Don't wait for the chart to tank further. May 7 reversal trade
  // bled 9 bps before CUT LOSSES fired; this would have caught it at 5.
  if(isActuallyLosing&&gapMagnitude>=5&&_kalshiVel!=null){
    const _kalshiInvertingAdverse=isUP?_kalshiVel<=-3:_kalshiVel>=3;
    if(_kalshiInvertingAdverse){
      return{label:`CUT — CHART + MARKET BOTH SAY NO`,reason:`${gapMagnitude.toFixed(0)} bps adverse AND the prediction market pricing against ${userPosition} at ${Math.abs(_kalshiVel).toFixed(0)}¢/30s. When both the spot chart and Kalshi disagree with you — that’s not a coincidence. ${gapStr}. Exit. [${timeLabel}]`,color:'rose',animate:true,hasAction:true,actionLabel:'CUT NOW',actionTarget:'SIT OUT'};
    }
  }

  // ── 3. REAL PROFIT ON TABLE — offer above your bet ────────────────────────
  if(profitPct>40&&momentumAgainst)return{label:'TAKE MAX PROFIT',reason:`+${profitPct.toFixed(0)}% on offer. Momentum reversing — lock in now before it fades. ${gapStr}. [${timeLabel}]`,color:'emerald',animate:true,hasAction:true,actionLabel:'CASHOUT — MAX PROFIT',actionTarget:'CASH'};
  if(profitPct>20&&drawdownFromPeak>0.12)return{label:'TRAILING STOP HIT',reason:`Offer pulled back ${(drawdownFromPeak*100).toFixed(0)}% from its peak. Protect your gains now. [${timeLabel}]`,color:'emerald',animate:true,hasAction:true,actionLabel:'EXECUTE CASHOUT',actionTarget:'CASH'};
  // V9.8.13: MID-WINDOW PROFIT-PROTECTION — fills the gap between TRAILING STOP (always-on,
  //   needs 20%+ profit and 12%+ drawdown) and SCALP/SECURE PROFIT (gated on isLate, only
  //   fires under ~2:15 left). Pre-V9.8.13 a profit at minute 5-12 of a 15m window with
  //   momentum turning got just `HOLD FIRM` while the offer eroded — user reported this
  //   directly as "cashout calls come late". These mid-window triggers fire earlier on
  //   tighter thresholds when the trade is past its halfway point.
  const isMidLate = !isLate && timeRemainingFrac < 0.50; // ~7:30+ elapsed in a 15m window
  if(profitPct>=22&&drawdownFromPeak>=0.08&&isMidLate)return{label:'SECURE PROFIT — DRAWING DOWN',reason:`+${profitPct.toFixed(0)}% on offer, gave back ${(drawdownFromPeak*100).toFixed(0)}% from peak. Don't wait for full reversal. ${gapStr}. [${timeLabel}]`,color:'emerald',animate:true,hasAction:true,actionLabel:'SECURE PROFIT NOW',actionTarget:'CASH'};
  if(profitPct>=15&&momentumAgainst&&isMidLate)return{label:'SECURE PROFIT — MOMENTUM TURNING',reason:`+${profitPct.toFixed(0)}% with momentum reversing and ${timeLabel}. Secure now before it fades. ${gapStr}.`,color:'emerald',animate:false,hasAction:true,actionLabel:'SECURE PROFIT NOW',actionTarget:'CASH'};
  if(profitPct>12&&momentumAgainst&&isLate)return{label:'SCALP PROFIT',reason:`+${profitPct.toFixed(0)}% with ${timeLabel} left. Momentum turning — good time to scalp. ${gapStr}.`,color:'emerald',animate:false,hasAction:true,actionLabel:'SCALP CASHOUT',actionTarget:'CASH'};
  if(profitPct>8&&isLate)return{label:'SECURE PROFIT',reason:`Profit on table (+${profitPct.toFixed(0)}%) and time running out. Consider locking in. ${gapStr}. [${timeLabel}]`,color:'emerald',animate:false,hasAction:true,actionLabel:'EXECUTE CASHOUT',actionTarget:'CASH'};
  if(offerAboveBet&&momentumAgainst&&winSide<60)return{label:'SECURE PROFIT',reason:`+${profitPct.toFixed(0)}% profit but odds slipping. Consider exit. ${gapStr}. [${timeLabel}]`,color:'emerald',animate:false,hasAction:true,actionLabel:'EXECUTE CASHOUT',actionTarget:'CASH'};

  // ── 4. ADVERSE POSITION — comeback score decides hold vs cut ─────────────
  // comebackScore > 55: Tara is highly confident, regime reliable, momentum helping
  // comebackScore 35-55: borderline — monitor
  // comebackScore < 35: cut losses
  // V9.7.6: KALSHI VELOCITY OVERRIDE — read tape, not just internal conviction.
  //   If Kalshi YES is moving strongly against the user's position over 30s, advise exit
  //   regardless of internal conviction. The market is telling you the trade is wrong.
  //   Threshold: ≥5¢/30s adverse = sustained adverse pressure, exit now.
  if(_kalshiVel!=null){
    const _adverseToUser=isUP?_kalshiVel<=-5:_kalshiVel>=5; // YES dropping = bad for UP, YES rising = bad for DOWN
    if(_adverseToUser&&!isLate){
      const _kInvDir12=isUP?'DOWN':'UP';
      return{label:`KALSHI SEES ${_kInvDir12} — EXIT EARLY`,reason:`Prediction market pricing ${_kInvDir12} at ${Math.abs(_kalshiVel).toFixed(0)}¢/30s — money is moving against your ${userPosition} trade. Kalshi sees it before the chart shows it. ${_kDriftStr12?_kDriftStr12+'. ':''}${gapStr}. Don’t wait for the candles to confirm.`,color:'rose',animate:true,hasAction:true,actionLabel:'EXIT NOW',actionTarget:'SIT OUT'};
    }
    if(_adverseToUser&&isLate){
      const _kLateDir12=isUP?'DOWN':'UP';
      return{label:`KALSHI ${_kLateDir12} — TAKE THE LOSS`,reason:`${timeLabel} left and Kalshi is pricing ${_kLateDir12} at ${Math.abs(_kalshiVel).toFixed(0)}¢/30s. No time for a reversal. ${gapStr}. Exit clean.`,color:'rose',animate:true,hasAction:true,actionLabel:'EXIT NOW',actionTarget:'SIT OUT'};
    }
    // Slight adverse but not yet at exit threshold — note it for context
    if((isUP?_kalshiVel<=-2:_kalshiVel>=2)&&isActuallyWinning&&_kalshiVel!=null){
      // Don't return early — fall through to existing HOLD branches but the chip text below picks this up
    }
  }
  if(isActuallyLosing){
    if(comebackScore<35||isVeryLate)return{label:'CUT LOSSES — EXIT NOW',reason:`${gapMagnitude.toFixed(0)} bps adverse. Comeback score: ${comebackScore.toFixed(0)}/100 — not worth holding. ${gapStr}. [${timeLabel}]`,color:'rose',animate:true,hasAction:true,actionLabel:'CUT LOSSES NOW',actionTarget:'SIT OUT'};
    if(isLate&&comebackScore<55)return{label:'CUT LOSSES',reason:`${gapMagnitude.toFixed(0)} bps adverse with ${timeLabel} left. Score: ${comebackScore.toFixed(0)}/100. Take the loss. ${gapStr}.`,color:'rose',animate:false,hasAction:true,actionLabel:'EXECUTE CASHOUT',actionTarget:'SIT OUT'};
    if(comebackScore>65&&momentumWith){
      const _cbNote=[_velConfStr,_driftStr12].filter(Boolean).join('. ');
      return{label:`ADVERSE BUT RECOVERING — HOLD`,reason:`${gapMagnitude.toFixed(0)} bps down but momentum just turned ${userPosition}.${_cbNote?` ${_cbNote}.`:''} Comeback score ${comebackScore.toFixed(0)}/100 — conditions for a reversal are present. ${gapStr}. ${_timePress12?'Watch time: '+_timePress12+'.':timeLabel}.`,color:'amber',animate:true,hasAction:true,actionLabel:'EXIT IF UNSURE',actionTarget:'SIT OUT'};
    }
    if(comebackScore>50){
      const _peakContext=_peakStr12?` (${_peakStr12})`:'';
      const _fwd=momentumWith?` Momentum starting to turn ${userPosition} — give it a few ticks.`:` No momentum yet — need a reversal candle soon or consider cutting.`;
      const _watchNote=_velAdvStr?` ${_velAdvStr}.`:'';
      return{label:`ADVERSE — WATCHING FOR BOUNCE`,reason:`${gapMagnitude.toFixed(0)} bps down${_peakContext}. Tara ${winSide.toFixed(0)}%${_driftStr12?`, ${_driftStr12}`:''}.${_watchNote}${_fwd} ${gapStr}. [${timeLabel}]`,color:'amber',animate:false,hasAction:true,actionLabel:'EXIT IF NEEDED',actionTarget:'SIT OUT'};
    }
    const _lowNote=[_velAdvStr,_peakStr12?`was ${_peakStr12}`:null].filter(Boolean).join('. ');
    return{label:`ADVERSE — LOW RECOVERY ODDS`,reason:`${gapMagnitude.toFixed(0)} bps down${_lowNote?`. ${_lowNote}`:''}. Comeback score ${comebackScore.toFixed(0)}/100 with momentum against — the trade isn’t looking for a way back. ${gapStr}. [${timeLabel}]`,color:'rose',animate:false,hasAction:true,actionLabel:'EXIT IF NEEDED',actionTarget:'SIT OUT'};
  }

  // ── 5. NEAR STRIKE (small adverse buffer zone) ─────────────────────────────
  if(!isActuallyWinning&&momentumWith)return{label:'RECOVERY IN PROGRESS',reason:`Price moving toward strike. Tara now: ${winSide.toFixed(0)}%. Comeback score: ${comebackScore.toFixed(0)}/100. ${gapStr}. [${timeLabel}]`,color:'amber',animate:false,hasAction:true,actionLabel:'EXIT EARLY',actionTarget:'SIT OUT'};
  if(!isActuallyWinning)return{label:'AT STRIKE — WATCH CLOSELY',reason:`Price at strike (${gapStr}). No clear edge. Tara now: ${winSide.toFixed(0)}%. [${timeLabel}]`,color:'amber',animate:false,hasAction:true,actionLabel:'EXIT IF NEEDED',actionTarget:'SIT OUT'};

  // ── 6. WINNING POSITION ────────────────────────────────────────────────────
  // V6.2.8: Tara-aligned reassurance — when Tara strongly agrees with user's winning position,
  //   surface that confidence as "TARA AGREES" instead of generic HOLD STRONG.
  // V9.8.13: now reads LIVE direction. Old code used _taraImpliedDir from lock snapshot,
  //   which always matched userPosition if the user followed Tara's call — making this
  //   branch fire trivially even when the live engine had drifted off-side. Now: only
  //   fires when Tara's CURRENT read still agrees with user at high conviction.
  const _taraAgreesStrong=_liveDir===userPosition&&_liveConf>=72;
  // V9.7.6: Kalshi tape confirmation — strongest possible signal for HOLD is "Kalshi YES
  //   moving with us at +5¢/30s." Tape is telling us the move is real.
  const _kalshiConfirmsHold=_kalshiVel!=null&&((isUP&&_kalshiVel>=5)||(isDN&&_kalshiVel<=-5));
  if(isActuallyWinning&&_kalshiConfirmsHold){
    return{label:`KALSHI LOCKED IN · HOLD ${userPosition}`,reason:`Prediction market confirming ${userPosition} at ${Math.abs(_kalshiVel).toFixed(0)}¢/30s — money is flowing your way. ${gapStr}. Tara ${winSide.toFixed(0)}%.${_driftStr12?` ${_driftStr12}.`:''} ${_timePress12||timeLabel}.`,color:'emerald',animate:false,hasAction:true,actionLabel:'CASHOUT NOW',actionTarget:'CASH'};
  }
  if(isActuallyWinning&&_taraAgreesStrong&&gapMagnitude>10){
    // V9.8.13: edge computed from LIVE conf vs current Kalshi (was using lock-time confidence)
    const _liveEdge = _kPctNow!=null
      ? (_liveDir==='UP'?_liveConf-_kPctNow:_liveConf-(100-_kPctNow))
      : null;
    const _holdEdgeNote=_liveEdge!=null?`${Math.abs(_liveEdge).toFixed(0)}pt ${_liveEdge>=0?'edge on your side':'edge slipping'}`:null;
    const _holdKNote=_velConfStr?`${_velConfStr} — tape confirming.`:null;
    return{label:`SOLID · HOLD ${userPosition}`,reason:`${gapForPosition.toFixed(0)} bps clean. Tara ${userPosition} ${Math.round(_liveConf)}%${_holdEdgeNote?`, ${_holdEdgeNote}`:''}. ${[_holdKNote,_driftStr12].filter(Boolean).join(' ')||'Position holding well.'} ${_timePress12||timeLabel}.`,color:'emerald',animate:false,hasAction:true,actionLabel:'CASHOUT NOW',actionTarget:'CASH'};
  }
  if(isActuallyWinning&&winSide>80&&!offerAboveBet)return{label:'MAX PROFIT ZONE',reason:`${gapForPosition.toFixed(0)} bps favorable. Tara now: ${winSide.toFixed(0)}% confident. Wait for offer to appear. ${gapStr}. [${timeLabel}]`,color:'emerald',animate:false,hasAction:true,actionLabel:'CASHOUT IF OFFERED',actionTarget:'CASH'};
  if(isActuallyWinning&&momentumWith&&gapMagnitude>10)return{label:'HOLD STRONG',reason:`${gapForPosition.toFixed(0)} bps above/below strike. Momentum aligned. ${gapStr}. Tara now: ${winSide.toFixed(0)}%. [${timeLabel}]`,color:'emerald',animate:false,hasAction:true,actionLabel:'CASHOUT NOW',actionTarget:'CASH'};
  if(isActuallyWinning&&momentumAgainst)return{label:'SECURE HOLD — WATCH MOMENTUM',reason:`Winning by ${gapForPosition.toFixed(0)} bps but momentum flipping. Consider locking in. ${gapStr}. [${timeLabel}]`,color:'amber',animate:false,hasAction:true,actionLabel:'CASHOUT NOW',actionTarget:'CASH'};
  if(isActuallyWinning&&isLate)return{label:'HOLD FIRM — ALMOST THERE',reason:`${gapForPosition.toFixed(0)} bps favorable with ${timeLabel} left. ${gapStr}. Tara now: ${winSide.toFixed(0)}%. [${timeLabel}]`,color:'emerald',animate:false,hasAction:true,actionLabel:'CASHOUT NOW',actionTarget:'CASH'};
  if(isActuallyWinning)return{label:'HOLD FIRM',reason:`${gapStr}. Tara now: ${winSide.toFixed(0)}% win rate. Position solid. [${timeLabel}]`,color:'emerald',animate:false,hasAction:true,actionLabel:'CASHOUT NOW',actionTarget:'CASH'};
  return{label:'HOLD FIRM',reason:`${gapStr}. Tara now: ${winSide.toFixed(0)}% win rate. Watching for cleaner signal. [${timeLabel}]`,color:'emerald',animate:false,hasAction:true,actionLabel:'CASHOUT NOW',actionTarget:'CASH'};
};

// ═══════════════════════════════════════
// V99 PREDICTION ENGINE (Weighted Composite + Adaptive)
// ═══════════════════════════════════════
// V3.1.11: Single-candle classification. Returns the candle's character based on body/wick ratios.
//   bull/bear  = direction (close vs open)
//   bullStrong/bearStrong = body ≥70% of total range, decisive
//   doji       = body ≤15% of total range, indecision
//   hammer     = small body at top of range with long lower wick (rejection of lower prices, bullish)
//   shooting   = small body at bottom of range with long upper wick (rejection of higher prices, bearish)
const classifyCandle=(c)=>{
  if(!c||!c.h||!c.l||c.h<=c.l)return{kind:'unknown'};
  const bodyHigh=Math.max(c.o||c.c,c.c);
  const bodyLow=Math.min(c.o||c.c,c.c);
  const range=c.h-c.l;
  const body=Math.abs(c.c-(c.o||c.c));
  const upperWick=c.h-bodyHigh;
  const lowerWick=bodyLow-c.l;
  const bodyPct=range>0?body/range:0;
  const isBull=c.c>(c.o||c.c);
  const isBear=c.c<(c.o||c.c);
  // Doji: very small body
  if(bodyPct<=0.15)return{kind:'doji',isBull:false,isBear:false};
  // Hammer: small body in upper third with long lower wick
  if(bodyPct<=0.40&&lowerWick>=2*body&&upperWick<=body)return{kind:'hammer',isBull:true,isBear:false};
  // Shooting star: small body in lower third with long upper wick
  if(bodyPct<=0.40&&upperWick>=2*body&&lowerWick<=body)return{kind:'shooting',isBull:false,isBear:true};
  // Strong: body dominates the range
  if(bodyPct>=0.70){
    return isBull?{kind:'bullStrong',isBull:true,isBear:false}:{kind:'bearStrong',isBull:false,isBear:true};
  }
  // Standard
  return isBull?{kind:'bull',isBull:true,isBear:false}:isBear?{kind:'bear',isBull:false,isBear:true}:{kind:'doji',isBull:false,isBear:false};
};

// V3.1.11: Within-window candle pattern detector. Looks at 1m candles since window-open
//   and classifies what's happening NOW — sequences of bull/bear, exhaustion patterns,
//   reversal signals. Output is a directional bias (-100 to +100) and a label.
//
//   Patterns detected (in priority order):
//     CLIMAX-TOP        2-3 strong bull candles followed by doji/shooting at top → bearish
//     CLIMAX-BOTTOM     2-3 strong bear candles followed by doji/hammer at low → bullish
//     ENGULFING-BULL    bear candle followed by bull candle that engulfs it → bullish
//     ENGULFING-BEAR    bull followed by bear that engulfs it → bearish
//     STAIR-UP          ≥3 consecutive bull candles, each higher → bullish trend
//     STAIR-DOWN        ≥3 consecutive bear candles, each lower → bearish trend
//     COMPRESSION       multiple small/doji candles in tight range → directionless until break
//     EXHAUSTION-UP     trend up but bodies shrinking → bull losing steam
//     EXHAUSTION-DOWN   trend down but bodies shrinking → bear losing steam
//
//   Score is informational and exposed in engine reasoning + UI chip.
//   NOT yet integrated into posterior — same approach as VOL-FLOW, gather outcome data first.
const detectWithinWindowPattern=(c1m,windowOpenTime)=>{
  if(!c1m||c1m.length<2)return{label:'INSUFFICIENT',score:0,context:'not enough 1m candles'};
  // Filter to candles within current window (their openTime ≥ windowOpenTime)
  // c1m candles are typically [{t, o, h, l, c, v}] — t is open timestamp
  const winCandles=windowOpenTime>0
    ? c1m.filter(c=>{const t=c.t||c.time||0; return t>=windowOpenTime-60000;}) // -60s for boundary slack
    : c1m.slice(-15); // fallback: last 15 candles
  if(winCandles.length<2)return{label:'OPENING',score:0,context:`${winCandles.length} candles in window`};
  // Sort oldest-first
  const ordered=winCandles.slice().sort((a,b)=>(a.t||a.time||0)-(b.t||b.time||0));
  const classified=ordered.map(classifyCandle);
  const last=classified[classified.length-1];
  const prev=classified[classified.length-2];
  const prev2=classified[classified.length-3];
  const recent3=classified.slice(-3);
  const lastCandle=ordered[ordered.length-1];
  const prevCandle=ordered[ordered.length-2];

  // ENGULFING patterns
  if(prev&&last&&prevCandle&&lastCandle){
    const prevBody=Math.abs((prevCandle.c||0)-(prevCandle.o||0));
    const lastBody=Math.abs((lastCandle.c||0)-(lastCandle.o||0));
    if(prev.isBear&&last.isBull&&lastBody>prevBody*1.2&&(lastCandle.c||0)>(prevCandle.o||0)){
      return{label:'ENGULFING-BULL',score:55,context:`bull engulfing — body ${(lastBody/prevBody).toFixed(1)}× prev`};
    }
    if(prev.isBull&&last.isBear&&lastBody>prevBody*1.2&&(lastCandle.c||0)<(prevCandle.o||0)){
      return{label:'ENGULFING-BEAR',score:-55,context:`bear engulfing — body ${(lastBody/prevBody).toFixed(1)}× prev`};
    }
  }

  // CLIMAX-TOP: 2+ strong bulls then doji/shooting at top
  if(prev2&&prev&&last&&prev2.isBull&&prev.isBull&&(last.kind==='doji'||last.kind==='shooting')){
    const prev2Strong=prev2.kind==='bullStrong';
    const prevStrong=prev.kind==='bullStrong';
    if(prev2Strong||prevStrong){
      return{label:'CLIMAX-TOP',score:-45,context:'strong bull run topped with rejection candle'};
    }
  }

  // CLIMAX-BOTTOM: 2+ strong bears then doji/hammer at bottom
  if(prev2&&prev&&last&&prev2.isBear&&prev.isBear&&(last.kind==='doji'||last.kind==='hammer')){
    const prev2Strong=prev2.kind==='bearStrong';
    const prevStrong=prev.kind==='bearStrong';
    if(prev2Strong||prevStrong){
      return{label:'CLIMAX-BOTTOM',score:45,context:'strong bear run bottomed with reversal candle'};
    }
  }

  // STAIR patterns: ≥3 consecutive same-direction with each closing further
  if(classified.length>=3){
    const consecutiveBulls=recent3.every(c=>c.isBull);
    const consecutiveBears=recent3.every(c=>c.isBear);
    const closes=ordered.slice(-3).map(c=>c.c||0);
    const monotonicUp=closes[2]>closes[1]&&closes[1]>closes[0];
    const monotonicDown=closes[2]<closes[1]&&closes[1]<closes[0];
    if(consecutiveBulls&&monotonicUp){
      // Check if bodies are shrinking (exhaustion warning)
      const bodies=ordered.slice(-3).map(c=>Math.abs((c.c||0)-(c.o||0)));
      if(bodies[2]<bodies[0]*0.6){
        return{label:'EXHAUSTION-UP',score:-25,context:'3 bulls but bodies shrinking — buying losing steam'};
      }
      return{label:'STAIR-UP',score:50,context:'3 consecutive bulls, monotonically rising'};
    }
    if(consecutiveBears&&monotonicDown){
      const bodies=ordered.slice(-3).map(c=>Math.abs((c.c||0)-(c.o||0)));
      if(bodies[2]<bodies[0]*0.6){
        return{label:'EXHAUSTION-DOWN',score:25,context:'3 bears but bodies shrinking — selling losing steam'};
      }
      return{label:'STAIR-DOWN',score:-50,context:'3 consecutive bears, monotonically falling'};
    }
  }

  // COMPRESSION: 4+ candles with small bodies and tight range
  if(classified.length>=4){
    const last4=classified.slice(-4);
    const last4Candles=ordered.slice(-4);
    const smallBodyCount=last4.filter(c=>c.kind==='doji'||c.kind==='hammer'||c.kind==='shooting').length;
    if(smallBodyCount>=3){
      const highs=last4Candles.map(c=>c.h||0);
      const lows=last4Candles.map(c=>c.l||0);
      const tightRange=Math.max(...highs)-Math.min(...lows);
      const avgPrice=last4Candles.reduce((s,c)=>s+(c.c||0),0)/4;
      const tightPct=avgPrice>0?tightRange/avgPrice:0;
      if(tightPct<0.0015){ // <15bps tight range over 4 minutes
        return{label:'COMPRESSION',score:0,context:'small bodies in tight range — break imminent'};
      }
    }
  }

  // Fall through: aggregate bull/bear count and return mild bias
  const bulls=classified.filter(c=>c.isBull).length;
  const bears=classified.filter(c=>c.isBear).length;
  if(bulls>bears+1)return{label:'BULL-LEAN',score:15,context:`${bulls} bull / ${bears} bear candles`};
  if(bears>bulls+1)return{label:'BEAR-LEAN',score:-15,context:`${bears} bear / ${bulls} bull candles`};
  return{label:'MIXED',score:0,context:`${bulls} bull / ${bears} bear · no clear pattern`};
};

const computeV99Posterior=(params)=>{
  const{currentPrice,referencePrice,liveHistory,targetMargin,globalFlow,bloomberg,velocityRef,tickHistoryRef,priceMemoryRef,windowType,timeFraction,clockSeconds,is15m,regimeMemory,adaptiveWeights,calibration,windowOpenPrice,depthFlash,tfCandles,futuresData,tapeWindows,econCalRisk,spotPerpDiv}=params;
  // Use regime-specific weights when current regime is identifiable, else use global adaptive weights
  const _regime=params.currentRegime||'RANGE-CHOP';
  const _regimeW=(params.regimeWeights&&params.regimeWeights[_regime])||null;
  // ── V9.11.0 LAYER 3: SESSION×REGIME WEIGHT BLENDING ──────────────────────
  // sessionWeights is the per-asset session map: {ASIA:{...}, EU:{...}, US:{...}, OFFHOURS:{...}}.
  //   We determine the active session here (engine eval time) and blend with the
  //   regime-specific weights.
  //
  // Blending strategy:
  //   • Both session AND regime weights available → 50/50 average. Each contributes
  //     its learned signal preferences. Over time, blending lets the engine respect
  //     both axes without one dominating.
  //   • Only one available → use that.
  //   • Neither → fall back to asset weights, then DEFAULT.
  //
  // Why averaging vs more sophisticated methods (e.g. count-weighted): simpler and
  //   has no failure modes when sample counts are missing/wrong. Bounded outcomes
  //   (each weight is already clamped) make extreme blends impossible.
  let _sessionKey=null;
  try{
    if(typeof getMarketSessions==='function'){
      const _dom=(getMarketSessions().dominant||'').toUpperCase();
      _sessionKey=_dom==='OFF-HOURS'?'OFFHOURS':_dom; // normalize hyphen
    }
  }catch(_){}
  const _sessW=(params.sessionWeights&&_sessionKey&&params.sessionWeights[_sessionKey])||null;
  let W;
  if(_regimeW&&_sessW){
    // Blend regime + session 50/50 across every signal key
    W={};
    const _allKeys=new Set([...Object.keys(_regimeW),...Object.keys(_sessW)]);
    for(const k of _allKeys){
      const _r=Number(_regimeW[k]);
      const _s=Number(_sessW[k]);
      if(Number.isFinite(_r)&&Number.isFinite(_s))W[k]=(_r+_s)/2;
      else if(Number.isFinite(_r))W[k]=_r;
      else if(Number.isFinite(_s))W[k]=_s;
      else W[k]=DEFAULT_WEIGHTS[k]||0;
    }
  } else {
    W=_regimeW||_sessW||adaptiveWeights||DEFAULT_WEIGHTS;
  }
  const reasoning=[];let totalScore=0;
  const rawSignalScores={}; // for training log
  // V9.7.9: TELEMETRY SLOTS — track diagnostic state from V9.7.x gates so the
  //   trade log can stamp them onto each entry. No behavior change — pure capture.
  let _diagReversalDamper=null; // {applied, mult, reason, recentCandleDirs}
  let _diagFgtCounter=null;     // {applied, before, after, mtfAlignment}
  let _diagRegimeCal=null;      // {applied, key, wr, weight, shift, n}

  const closes=liveHistory.map(x=>x.c);
  const rsi=calcRSI(closes,14)||50;
  const atr=calcATR(liveHistory,14)||10;
  const atrBps=atr>0?(atr/currentPrice)*10000:15;
  // ── V113: VELOCITY REGIME CLASSIFIER ──────────────────────────────────────
  // Combines ATR + 1m drift + tick velocity to classify market speed.
  // Used downstream to scale thresholds, sample requirements, and momentum gates.
  // Categories: SLOW (sleepy), NORMAL (normal flow), FAST (active), EXTREME (news/spike)
  const _v1s=Math.abs(velocityRef?.current?.v1s||0);
  const _v5s=Math.abs(velocityRef?.current?.v5s||0);
  const _drift1mAbs=Math.abs(((liveHistory[0]?.c||currentPrice)-(liveHistory[1]?.c||currentPrice))/(currentPrice||1)*10000);
  // Score is composite: ATR (background vol) + tick velocity (real-time speed)
  const _velScore=(atrBps*0.6)+(_v1s*4)+(_v5s*1.5)+(_drift1mAbs*0.8);
  let velocityRegime='NORMAL';
  if(_velScore<12)velocityRegime='SLOW';
  else if(_velScore<28)velocityRegime='NORMAL';
  else if(_velScore<55)velocityRegime='FAST';
  else velocityRegime='EXTREME';
  // V134: VOLUME PROFILE / VPOC — support/resistance levels
  const _vpData=computeVolumeProfile(liveHistory);
  let vpocResistanceAdj=0;
  if(_vpData.vpoc>0&&_vpData.strength>0.08&&targetMargin>0&&currentPrice>0){
    const vpoc=_vpData.vpoc;
    const upBlocked=currentPrice<targetMargin&&vpoc>currentPrice&&vpoc<targetMargin&&Math.abs((vpoc-currentPrice)/currentPrice*10000)>5;
    const dnBlocked=currentPrice>targetMargin&&vpoc<currentPrice&&vpoc>targetMargin&&Math.abs((currentPrice-vpoc)/currentPrice*10000)>5;
    if(upBlocked){
      vpocResistanceAdj=-Math.min(8,_vpData.strength*30);
      reasoning.push(`[VPOC] Resistance @ $${vpoc.toFixed(0)} (strength ${(_vpData.strength*100).toFixed(0)}%) blocking UP`);
    } else if(dnBlocked){
      vpocResistanceAdj=+Math.min(8,_vpData.strength*30);
      reasoning.push(`[VPOC] Support @ $${vpoc.toFixed(0)} (strength ${(_vpData.strength*100).toFixed(0)}%) blocking DOWN`);
    }
    totalScore+=vpocResistanceAdj;
  }
  // ── V134: WINDOW-OPEN PRICE ANCHOR ──────────────────────────────────────
  // How far has price moved this window? Combined with time elapsed,
  // tells us if the move is exhausted (already +30bps with 2min left = no juice)
  let windowDriftBps=0;
  let windowExhaustionPenalty=0;
  if(windowOpenPrice&&windowOpenPrice>0&&currentPrice>0){
    windowDriftBps=((currentPrice-windowOpenPrice)/windowOpenPrice)*10000;
    const _absDrift=Math.abs(windowDriftBps);
    const _windowMaxSec=is15m?900:300;
    const _timeRatio=(clockSeconds||0)/_windowMaxSec;
    // Exhaustion check: if price has already moved >25bps AND we're >70% through window
    // → the move has happened, late lock chasing it has poor odds
    if(_absDrift>25&&_timeRatio>0.7){
      // Penalty scales with how extended + how late
      windowExhaustionPenalty=Math.min(15,(_absDrift-25)*0.4+(_timeRatio-0.7)*30);
      reasoning.push(`[WIN-EXH] Window drifted ${windowDriftBps.toFixed(0)}bps with ${((1-_timeRatio)*100).toFixed(0)}% time left — exhaustion penalty -${windowExhaustionPenalty.toFixed(0)}`);
    }
  }

  // ── V134: TRAJECTORY FORECAST ────────────────────────────────────────────
  // Project where price will be at window-end using kinematics: x(t) = x₀ + v·t + ½a·t²
  // Then convert to "implied posterior" — direction Tara expects price to land
  const _v5sNum=velocityRef?.current?.v5s||0;     // $/sec recent
  const _accelNum=velocityRef?.current?.accel||0; // $/sec² (v5s - v15s diff)
  const _secsLeft=forecastSecondsRemaining(clockSeconds,is15m);
  // V134: SMOOTH TRAJECTORY — blend 5s with 30s velocity to prevent tick-induced flip-flops
  // Old version used only v5s × √secs which caused 30+ point posterior swings on a single tick
  // Now we use a 70/30 blend favoring stable velocity; recent ticks still matter but don't dominate
  // V10.7.13: REWEIGHTED — old blend (30/40/30) used stale momentum and missed reversals.
  //   Tonight's bottom-pick fail (Tara locked DOWN as price was bouncing UP off bottom)
  //   was caused by projection using 30s downtrend velocity when 5s already reversed UP.
  //   Fix: weight recent 5s heavier (55%), medium 15s (35%), drop 30s influence (10%).
  //   Catches reversals 2-3 minutes earlier. Tradeoff: slightly more flip-flop on noisy
  //   ticks, but V10.4.3 multi-window memory + V10.7.12 concrete priority dampen this.
  const _v15sNum=velocityRef?.current?.v15s||0;
  const _v30sNum=velocityRef?.current?.v30s||_v15sNum||0;
  // V10.7.13: 55/35/10 blend favoring recency (was 30/40/30 favoring stability)
  const _vBlended=(_v5sNum*0.55)+(_v15sNum*0.35)+(_v30sNum*0.10);
  // Dampen far-out projections (uncertainty grows with time)
  const _decayedTime=Math.sqrt(_secsLeft); // effective projection time
  let _projectedDrift=(_vBlended*_decayedTime)+(_accelNum*_decayedTime*_decayedTime*0.3);
  // V10.7.39 — COIL DETECTOR: suppress tiny velocity noise in consolidation
  //   Small indecisive candles produce tiny velocity signals that TRAJ amplifies
  //   into false directional projections ("the gaslighting problem").
  //   When bars are coiling (avg body < 30% ATR, avg range < 50% ATR),
  //   velocity is noise — heavily dampen projection so it doesn't drive the call.
  //   Three-Step Delta (below) takes over as the directional guide in this state.
  const _coil=detectCoilConsolidation(liveHistory,atrBps,5);
  if(_coil.isCoiling&&Math.abs(_projectedDrift)<2.0){
    _projectedDrift*=0.2; // 80% dampen: tiny velocity becomes near-zero
    reasoning.push(`[COIL] Consolidation detected (body ${_coil.avgBodyBps.toFixed(1)}bps < ${_coil.bodyThreshold.toFixed(1)}bps threshold) — TRAJ velocity suppressed, Delta signal primary`);
  }else if(_coil.isCoiling){
    _projectedDrift*=0.5; // 50% dampen even for moderate drift when coiling
  }
  // V10.7.24A — VOLUME-VALIDATED TRAJ
  //   User report (May 20): TRAJ was projecting UP but other signals overrode it.
  //   Sometimes TRAJ is genuinely wrong because velocity comes from price spikes
  //   that aren't tape-supported (whale ticks, low-volume fake moves). Solution:
  //   cross-check projection direction against tape buy% on 30s window.
  //
  //   Logic:
  //   - Projection UP but tape says SELL (buyPct < 40%): dampen drift by 50% (likely fake)
  //   - Projection DOWN but tape says BUY (buyPct > 60%): dampen drift by 50% (likely fake)
  //   - Projection UP and tape says BUY (buyPct > 65%): boost drift by 20% (volume confirms)
  //   - Projection DOWN and tape says SELL (buyPct < 35%): boost drift by 20% (volume confirms)
  //   Result: TRAJ now uses BOTH price velocity AND volume direction.
  let _volSupportMult=1.0;
  let _volSupportReason='';
  try{
    const _tape30BuyPct=tapeWindows?.w30?.buyPct;
    if(Number.isFinite(_tape30BuyPct)&&Math.abs(_projectedDrift)>0.5){
      const _projectedUp=_projectedDrift>0;
      if(_projectedUp&&_tape30BuyPct<40){
        _volSupportMult=0.5;
        _volSupportReason='tape-SELL contradicts UP projection';
      }else if(!_projectedUp&&_tape30BuyPct>60){
        _volSupportMult=0.5;
        _volSupportReason='tape-BUY contradicts DOWN projection';
      }else if(_projectedUp&&_tape30BuyPct>65){
        _volSupportMult=1.2;
        _volSupportReason=`tape-BUY ${_tape30BuyPct.toFixed(0)}% confirms UP`;
      }else if(!_projectedUp&&_tape30BuyPct<35){
        _volSupportMult=1.2;
        _volSupportReason=`tape-SELL ${(100-_tape30BuyPct).toFixed(0)}% confirms DOWN`;
      }
      if(_volSupportMult!==1.0){
        _projectedDrift=_projectedDrift*_volSupportMult;
      }
    }
  }catch(_){}
  const _projectedPrice=currentPrice+_projectedDrift;
  const _projectedGapBps=targetMargin>0?((_projectedPrice-targetMargin)/targetMargin)*10000:0;
  // If projection points strongly toward a direction relative to strike, boost confidence early
  let trajectoryAdj=0;
  let trajectoryDirHint='NEUTRAL'; // V134: directional read for contradiction detection
  // V134: Trajectory cap reduced ±12 → ±8 (was dominating posterior on swings)
  // Threshold raised 8bps → 12bps (require real projection, not noise)
  // V135: REVISED — cap raised back to ±12 (was being silently outvoted by regime bonus +43).
  //       Activation threshold dropped 12 → 10 bps so trajectory engages slightly sooner.
  //       Coefficient up 0.3 → 0.35 (gentler than the original 0.4 to preserve stability).
  if(targetMargin>0&&_secsLeft>60&&Math.abs(_projectedGapBps)>10){
    if(_projectedGapBps>0){
      trajectoryAdj=Math.min(12,_projectedGapBps*0.35);
      trajectoryDirHint='UP';
    } else {
      trajectoryAdj=Math.max(-12,_projectedGapBps*0.35);
      trajectoryDirHint='DOWN';
    }
    reasoning.push(`[TRAJ] Projected end @ $${_projectedPrice.toFixed(0)} (${_projectedGapBps>0?'+':''}${_projectedGapBps.toFixed(0)}bps to strike) → favors ${trajectoryDirHint}${_volSupportReason?' · '+_volSupportReason:''}`);
  }
  // V134: HPotter Future Grand Trend on 1m/3m/5m/15m
  // V2.9: Weighted timeframe voting + increased FGT contribution to posterior.
  //   Was: each timeframe votes ±1, summed to -4..+4, bonus ±30/18/8 at 4/3/2 alignment
  //   Now: 5m primary (±1.5), 15m secondary (±1.2), 1m tertiary (±0.8), 3m last (±0.5)
  //        — weights sum to 4.0, preserving the existing >=4/>=3/>=2 threshold semantics
  //        Bonus magnitudes raised to ±42/26/14/6 at strong/medium/moderate/weak alignment
  //   Reasoning: 5m is the most relevant horizon for a 15-min window. 15m is high-quality
  //   directional signal but extends past window close. 1m is fast but noisy. 3m is somewhat
  //   redundant given 1m and 5m bracket it. The data audit on 53 trades showed FGT-3 alignment
  //   correlating with 70-100% WR — increasing FGT's posterior contribution leans into the
  //   most reliable predictor we have. Secondary signals (gap/momentum/structure/flow/etc) still
  //   contribute roughly ±50 in extreme cases, so they remain influential when FGT is mixed.
  let mtfAlignment=0; // -4.0 to +4.0 (continuous, weighted)
  let mtfBonus=0;
  let fgtResults={};
  // V6.2.0: Structural indicators — compute unconditionally when candles exist.
  //   Multi-hour context (5m × 100+ bars = 8+ hours) doesn't depend on window timing.
  // V6.2.1: User's actual workflow uses TradingView with 1m/5m/15m timeframe switching,
  //   calling only when indicators ALIGN across all three. Compute on all three and
  //   derive an alignment count instead of single-TF readings.
  const _tcByTf={
    '1m': tfCandles?.c1m?.length>=60?computeFutureTrendChannel(tfCandles.c1m,100,3,50):null,
    '5m': tfCandles?.c5m?.length>=60?computeFutureTrendChannel(tfCandles.c5m,100,3,50):null,
    '15m': tfCandles?.c15m?.length>=60?computeFutureTrendChannel(tfCandles.c15m,100,3,50):null,
  };
  const _gtByTf={
    '1m': tfCandles?.c1m?.length>=60?computeFutureGrandTrend(tfCandles.c1m,70,100):null,
    '5m': tfCandles?.c5m?.length>=60?computeFutureGrandTrend(tfCandles.c5m,70,100):null,
    '15m': tfCandles?.c15m?.length>=60?computeFutureGrandTrend(tfCandles.c15m,70,100):null,
  };
  // Aggregate: which way does each TF point? Count agreements.
  const _tcDirs=['1m','5m','15m'].map(k=>_tcByTf[k]?.valid?_tcByTf[k].trendDir:null);
  const _gtDirs=['1m','5m','15m'].map(k=>_gtByTf[k]?.valid?_gtByTf[k].dir:null);
  const _countDir=(arr,dir)=>arr.filter(x=>x===dir).length;
  // Trend Channel: count UP vs DOWN across timeframes
  const _tcUpCount=_countDir(_tcDirs,'UP');
  const _tcDnCount=_countDir(_tcDirs,'DOWN');
  // Grand Trend: count UP vs DOWN (NEUTRAL doesn't count)
  const _gtUpCount=_countDir(_gtDirs,'UP');
  const _gtDnCount=_countDir(_gtDirs,'DOWN');
  // Combined alignment: how many of 6 total signals (3 TC + 3 GT) point each way
  // V9.2.0: WEIGHTED by timeframe. Was equal weight (each TF = 1 vote), so 1m noise
  //   could flip the alignment. Now: 1m = 0.5, 5m = 1.0, 15m = 1.5. Total max = 6.0.
  //   15m carries 3x the weight of 1m — user's TradingView workflow trusts higher TFs.
  const _tfWeights={'1m':0.5,'5m':1.0,'15m':1.5};
  const _weightedCount=(dirs,target)=>{
    let sum=0;
    ['1m','5m','15m'].forEach((tf,i)=>{if(dirs[i]===target)sum+=_tfWeights[tf];});
    return sum;
  };
  const _structUpCount=_weightedCount(_tcDirs,'UP')+_weightedCount(_gtDirs,'UP');
  const _structDnCount=_weightedCount(_tcDirs,'DOWN')+_weightedCount(_gtDirs,'DOWN');
  const _structDir=_structUpCount>_structDnCount&&_structUpCount>=2.5?'UP':_structDnCount>_structUpCount&&_structDnCount>=2.5?'DOWN':'NEUTRAL';
  const _structAlignment=Math.max(_structUpCount,_structDnCount); // 0-6 scale (weighted)
  // Use the 5m as the "primary" reading for display (it's the middle ground), but the
  //   gate decision uses the multi-TF alignment count.
  const _trendChannel=_tcByTf['5m']?{
    ..._tcByTf['5m'],
    // Augment with multi-TF alignment data
    multiTfDirs:_tcDirs,                    // ['UP','UP','DOWN'] etc.
    multiTfAgreement:Math.max(_tcUpCount,_tcDnCount), // 0-3 scale (how many TFs agree)
    multiTfDir:_tcUpCount>_tcDnCount?'UP':_tcDnCount>_tcUpCount?'DOWN':'NEUTRAL',
  }:null;
  const _grandTrend=_gtByTf['5m']?{
    ..._gtByTf['5m'],
    multiTfDirs:_gtDirs,
    multiTfAgreement:Math.max(_gtUpCount,_gtDnCount),
    multiTfDir:_gtUpCount>_gtDnCount?'UP':_gtDnCount>_gtUpCount?'DOWN':'NEUTRAL',
  }:null;
  if(tfCandles&&targetMargin>0&&_secsLeft>30){
    // V5.7.7: Drop 3m timeframe (dead weight — often invalid, weight 0.5, redundant given
    //   1m/5m bracket per its own comment). Redistribute: 1m 0.8→1.0, 5m 1.5→1.7, 15m 1.2→1.3.
    //   Total weight remains 4.0 so existing alignment thresholds still mean the same thing.
    // V5.7.7: forecast cap — each timeframe's projection horizon clamps to bars-remaining-in-window
    //   so FGT can't project hours ahead when only minutes remain. The case that motivates this:
    //   15:00 settlement at 14:55 with 5m forecast=40 was projecting 200 minutes forward and
    // V14.4.8: use fractional forecast bars and a separate historical slope
    // lookback. A short remaining horizon is not missing historical data, and
    // the forward projection must never extend beyond this window's close.
    const _capForecast=(tfMinutes,baseFore)=>{
      return cappedForecastBars(_secsLeft,tfMinutes,baseFore);
    };
    const tfList=[
      {label:'1m',data:tfCandles.c1m,length:30,forecast:_capForecast(1,30),weight:1.0},
      {label:'5m',data:tfCandles.c5m,length:40,forecast:_capForecast(5,40),weight:1.7},
      {label:'15m',data:tfCandles.c15m,length:50,forecast:_capForecast(15,30),weight:1.3},
    ];
    const tfSigns=[];
    const tfArrows=[];
    tfList.forEach(tf=>{
      const fgt=computeFGT(tf.data,tf.length,tf.forecast);
      fgtResults[tf.label]=fgt;
      if(!fgt.valid){tfSigns.push(0);tfArrows.push('?');return;}
      // Weighted vote: forecast direction × per-timeframe weight
      if(fgt.forecastDir==='UP'){tfSigns.push(tf.weight);tfArrows.push('↑');}
      else if(fgt.forecastDir==='DOWN'){tfSigns.push(-tf.weight);tfArrows.push('↓');}
      else{tfSigns.push(0);tfArrows.push('→');}
    });
    mtfAlignment=tfSigns.reduce((s,v)=>s+v,0);
    const absAlign=Math.abs(mtfAlignment);
    // V2.9: Bonus tiered by alignment strength on the continuous 0-4 scale.
    //   ≥3.5 → ±42 (strong/full alignment — was ±30 at exactly 4)
    //   ≥2.5 → ±26 (medium alignment — was ±18 at exactly 3)
    //   ≥1.5 → ±14 (moderate alignment — was ±8 at exactly 2)
    //   ≥0.7 → ±6  (weak alignment — was 0 at <2)
    //   The lower tier (≥0.7) catches cases where 5m alone (1.5) aligns and others are flat.
    if(absAlign>=3.5)mtfBonus=Math.sign(mtfAlignment)*42;
    else if(absAlign>=2.5)mtfBonus=Math.sign(mtfAlignment)*26;
    else if(absAlign>=1.5)mtfBonus=Math.sign(mtfAlignment)*14;
    else if(absAlign>=0.7)mtfBonus=Math.sign(mtfAlignment)*6;
    if(mtfBonus!==0){
      // V10.7.25 — FGT REMOVED FROM DIRECTIONAL SYNTHESIS
      //   Audit (May 20): FGT standalone direction accuracy = 51% (coin flip).
      //   When FGT agreed with Tara → 72.7% WR. When FGT DISAGREED → 70.2% WR
      //   (also high, meaning Tara was right to overrule it). When FGT neutral
      //   → 61.7%. Net: FGT firing correlates with market clarity (good signal
      //   environment) but FGT direction itself is noise.
      //
      //   Decision: stop adding FGT bonus to totalScore. Keep mtfBonus as
      //   metadata for telemetry/UI display but it no longer pulls posterior.
      //   The 51% standalone accuracy means including it adds randomness.
      //
      // OLD: totalScore += mtfBonus
      // NEW: do not add. mtfBonus stays in return object for UI/audit only.
      const dirLabel=mtfBonus>0?'UP':'DOWN';
      reasoning.push(`[FGT] alignment ${mtfAlignment>=0?'+':''}${mtfAlignment.toFixed(1)}/4.0 ${dirLabel} (1m${tfArrows[0]} 5m${tfArrows[1]} 15m${tfArrows[2]}) → display-only (V10.7.25 removed from synthesis)`);
      // Detailed forecast values
      const detail=tfList.map((tf,i)=>{
        const f=fgtResults[tf.label];
        if(!f.valid)return `${tf.label}:?`;
        return `${tf.label}:${f.forecastBps>0?'+':''}${f.forecastBps}bps`;
      }).join(' ');
      reasoning.push(`[FGT-DETAIL] ${detail}`);
    } else if(absAlign<=1){
      reasoning.push(`[FGT] Mixed across timeframes (${tfArrows.join(' ')}) — no consensus`);
    } else {
      reasoning.push(`[FGT] Weak signal (${absAlign}/4 ${tfArrows.join(' ')})`);
    }
  } else {
    reasoning.push(`[FGT] Loading multi-timeframe data...`);
  }
  // V6.2.0/V6.2.1: Log structural primary indicators with multi-TF alignment.
  if(_grandTrend?.valid){
    const _gtArr=_gtDirs.map((d,i)=>['1m','5m','15m'][i]+(d==='UP'?'↑':d==='DOWN'?'↓':'·')).join(' ');
    reasoning.push(`[GRAND-TREND] 5m ${_grandTrend.dir} ${_grandTrend.projectionBps>0?'+':''}${_grandTrend.projectionBps}bps · TFs: ${_gtArr}`);
  }
  if(_trendChannel?.valid){
    const _tcArr=_tcDirs.map((d,i)=>['1m','5m','15m'][i]+(d==='UP'?'↑':d==='DOWN'?'↓':'·')).join(' ');
    reasoning.push(`[CHANNEL] 5m ${_trendChannel.trendDir} · pos ${Math.round(_trendChannel.channelPos*100)}% · TFs: ${_tcArr}`);
  }
  if(_structDir!=='NEUTRAL'&&_structAlignment>=3){
    reasoning.push(`[STRUCT] ${_structAlignment}/6 align ${_structDir} (${_tcUpCount+_gtUpCount}↑ vs ${_tcDnCount+_gtDnCount}↓)`);
  }
  // V7.3: STRUCTURAL ALIGNMENT BONUS to totalScore. Previously the multi-TF Grand Trend
  //   + Trend Channel data (5m/15m structural reading) was COMPUTED and LOGGED but never
  //   added to the engine posterior. It only gated the structural-led tier and was shown
  //   in the Score Breakdown UI. Result: Tara's posterior was driven by 1m candle action
  //   and tape, while the larger trend (which was often correctly UP/DOWN) had zero weight.
  //   Now: 4/6 aligned TFs → ±8, 5/6 → ±14, 6/6 → ±20. Damped by remaining time — boost
  //   has full effect when >60% time remains (long-horizon signals matter most when there's
  //   still time for them to play out), tapers to half effect by <25% time remaining (gap
  //   signal becomes dominant late in window).
  if(_structDir!=='NEUTRAL'&&_structAlignment>=4){
    let _structBoost=_structAlignment>=6?20:_structAlignment>=5?14:8;
    // Time damping: full when >60% remaining, half when <25% remaining
    const _structTimeMult=timeFraction<=0.4?1.0:timeFraction<=0.75?(1.0-(timeFraction-0.4)*1.0):0.5;
    // V9.7.8: REVERSAL DAMPER. Read last 2 closed 1m candles. If both close STRONGLY
    //   opposite to STRUCT direction, dampen STRUCT weight by 50%. Stops Tara from
    //   locking DOWN at the bottom of a green-candle breakout (or UP at the top of
    //   a red collapse). The previous engine read 6 candles back and gave equal
    //   weight to all of them — meaning a fresh reversal got out-voted by stale
    //   history. Now: most recent 2 candles get veto power on STRUCT confidence.
    let _reversalDampMult=1.0;
    let _reversalReason=null;
    let _recentCandleDirs=null; // V9.7.9 telemetry: capture for diagnostic stamping
    try{
      const _c1m=tfCandles?.c1m;
      if(Array.isArray(_c1m)&&_c1m.length>=2){
        // c1m[0] is most recent. Check most recent 2 candles.
        const _r0=_c1m[0],_r1=_c1m[1];
        if(_r0&&_r1&&Number.isFinite(_r0.o)&&Number.isFinite(_r0.c)&&Number.isFinite(_r1.o)&&Number.isFinite(_r1.c)){
          const _d0=_r0.c-_r0.o; // positive = green close
          const _d1=_r1.c-_r1.o;
          const _bothUp=_d0>0&&_d1>0;
          const _bothDn=_d0<0&&_d1<0;
          // Stamp recent candle dirs always (not just when damper fires)
          _recentCandleDirs=`${_d1>0?'UP':_d1<0?'DN':'FLAT'},${_d0>0?'UP':_d0<0?'DN':'FLAT'}`;
          // Magnitude check — reversal must be meaningful, not tiny dojis
          const _avgPx=(_r0.c+_r1.c)/2;
          const _moveBps=_avgPx>0?(Math.abs(_d0)+Math.abs(_d1))/_avgPx*10000:0;
          // V10.2.42 PHASE 2 — RELAXED damper trigger.
          //   Was: requires BOTH candles same direction AND combined ≥4bps.
          //   Problem: damper fires 0.8% across 751 trades — far too rare for what
          //   should be a common "fresh reversal" protection. The "both candles"
          //   requirement misses single-strong-reversal patterns (one big counter
          //   candle is often more meaningful than two small ones).
          //   Now: fire when (a) BOTH same-dir at ≥3bps combined, OR (b) the most
          //   recent candle is strongly opposite to struct at ≥4bps single-candle.
          //   The latter catches "fresh reversal candle just printed" patterns.
          const _r0OppUp=_structDir==='DOWN'&&_d0>0;
          const _r0OppDn=_structDir==='UP'&&_d0<0;
          const _r0MoveBps=_avgPx>0?Math.abs(_d0)/_avgPx*10000:0;
          const _r0StrongOpp=(_r0OppUp||_r0OppDn)&&_r0MoveBps>=4;
          if(_moveBps>=3&&((_structDir==='DOWN'&&_bothUp)||(_structDir==='UP'&&_bothDn))){
            _reversalDampMult=0.5;
            _reversalReason=`recent 2x 1m closed ${_bothUp?'UP':'DOWN'} (${_moveBps.toFixed(0)}bps) vs STRUCT ${_structDir}`;
          }else if(_r0StrongOpp){
            // Single-candle strong reversal print
            _reversalDampMult=0.6; // slightly less aggressive damp for single-candle signal
            _reversalReason=`latest 1m candle ${_d0>0?'UP':'DOWN'} ${_r0MoveBps.toFixed(0)}bps vs STRUCT ${_structDir} (V10.2.42)`;
          }
        }
      }
    }catch(_){}
    // V9.7.9 telemetry: stash on outer scope so it gets returned in diagnostics
    _diagReversalDamper={applied:_reversalDampMult<1,mult:_reversalDampMult,reason:_reversalReason,recentCandleDirs:_recentCandleDirs};
    _structBoost=Math.round(_structBoost*_structTimeMult*_reversalDampMult)*(_structDir==='UP'?1:-1);
    if(_structBoost!==0){
      totalScore+=_structBoost;
      const _dampNote=_reversalDampMult<1?` ⚠ damped ×${_reversalDampMult.toFixed(2)}: ${_reversalReason}`:'';
      reasoning.push(`[STRUCT-BOOST] ${_structAlignment.toFixed(1)}/6 ${_structDir} → ${_structBoost>0?'+':''}${_structBoost} (×${_structTimeMult.toFixed(2)} time-damp)${_dampNote}`);
    }
    // V9.2.0: SLOPE BOOST — slopeBpsPerBar from both TC and GT feed into posterior.
    //   When slope confirms direction, small boost (up to ±6). When slope contradicts,
    //   small penalty. This was computed but never read by the engine.
    const _tc5m=_trendChannel; // 5m primary reading
    const _gt5m=_grandTrend;
    let _slopeBoost=0;
    if(_tc5m?.valid){
      const _tcS=Number(_tc5m.slopeBpsPerBar)||0;
      const _tcAligned=(_structDir==='UP'&&_tcS>0.5)||(_structDir==='DOWN'&&_tcS<-0.5);
      const _tcContrary=(_structDir==='UP'&&_tcS<-0.5)||(_structDir==='DOWN'&&_tcS>0.5);
      if(_tcAligned)_slopeBoost+=Math.min(3,Math.abs(_tcS)*1.5);
      if(_tcContrary)_slopeBoost-=Math.min(3,Math.abs(_tcS)*1.0);
    }
    if(_gt5m?.valid){
      const _gtS=Number(_gt5m.slopeBpsPerBar)||0;
      const _gtAligned=(_structDir==='UP'&&_gtS>0.5)||(_structDir==='DOWN'&&_gtS<-0.5);
      const _gtContrary=(_structDir==='UP'&&_gtS<-0.5)||(_structDir==='DOWN'&&_gtS>0.5);
      if(_gtAligned)_slopeBoost+=Math.min(3,Math.abs(_gtS)*1.0);
      if(_gtContrary)_slopeBoost-=Math.min(3,Math.abs(_gtS)*0.8);
    }
    _slopeBoost=Math.round(_slopeBoost*(_structDir==='UP'?1:-1));
    if(Math.abs(_slopeBoost)>=1){
      totalScore+=_slopeBoost;
      reasoning.push(`[SLOPE] TC+GT slope → ${_slopeBoost>0?'+':''}${_slopeBoost} (aligned=${_slopeBoost>0})`);
    }
  }
  // V7.7: REVERSAL PREDICTOR. Replaces V7.6 mean-reversion-guard (which sat out instead of
  //   predicting). Call-log analysis showed 10/10 chasing-extreme trades reversed against
  //   the lock direction. So when all 3 of gap/momentum/flow point the same direction with
  //   high magnitude (≥10pt each), Tara now ACTIVELY PREDICTS the reversal — injects a
  //   counter-direction signal proportional to the exhaustion strength. The posterior flips
  //   toward the reversal direction, downstream tier checks naturally re-evaluate from there.
  //   Gated on:
  //     • >40% time remaining (late in window the move's already played out — no time to revert)
  //     • Higher TFs not strongly confirming the chase (struct alignment <5 in chase direction)
  //     • Boost magnitude scales with exhaustion strength up to 35 points
  //   Logged as [REVERSAL-PREDICTOR]. The user wanted Tara to "look for it and predict it
  //   happening in the window" — this is that, at the engine layer.
  const _gapScore=rawSignalScores.gap||0;
  const _momScore=rawSignalScores.momentum||0;
  const _flowScore=rawSignalScores.flow||0;
  const _chaseDirSign=Math.sign(_gapScore+_momScore+_flowScore);
  const _allHighMag=Math.abs(_gapScore)>=10&&Math.abs(_momScore)>=10&&Math.abs(_flowScore)>=10;
  const _allSameDir=_chaseDirSign!==0&&Math.sign(_gapScore)===_chaseDirSign&&Math.sign(_momScore)===_chaseDirSign&&Math.sign(_flowScore)===_chaseDirSign;
  const _structConfirmsChase=_structDir!=='NEUTRAL'&&_structAlignment>=5&&((_structDir==='UP'&&_chaseDirSign>0)||(_structDir==='DOWN'&&_chaseDirSign<0));
  // V7.7.1: Three new blockers prevent firing on real continuation moves:
  //   (a) REALIZED-GAP-ALREADY-MOVED: if price is already ≥5bps in chase direction, the
  //       move happened. There isn't enough time/range left for a full reversal across
  //       strike. The user screenshot had +25bps realized — Tara still flipped DOWN. Wrong.
  //   (b) PROJECTION-AGREES: if the engine's own forward projection (_projectedGapBps)
  //       points the same way as the chase, the model believes continuation. Don't flip
  //       against the model's own forward read.
  //   (c) FGT-AGREES-CHASE: if HPotter Future Grand Trend strongly agrees with chase
  //       (mtfAlignment ≥ 1.5 toward chase direction), forward TF analysis confirms the
  //       move. Don't flip against multi-TF future-trend.
  // V7.10: Compute realGapBps locally — the canonical declaration is later in the engine
  //   (line ~2315), but we need it here. Same formula. TDZ error before this fix:
  //   "Cannot access 'ft' before initialization" was minifier's name for realGapBps.
  // V10.9.1: same referencePrice correction as the canonical realGapBps below —
  //   this local copy feeds chase-reversal agreement checks and must match.
  const _gapSpotLocal=Number(referencePrice||currentPrice)||currentPrice;
  const _realGapBpsLocal=targetMargin>0?((_gapSpotLocal-targetMargin)/targetMargin)*10000:0;
  const _realizedAgreesChase=Math.abs(_realGapBpsLocal)>=5&&Math.sign(_realGapBpsLocal)===_chaseDirSign;
  const _projAgreesChase=Math.abs(_projectedGapBps)>=5&&Math.sign(_projectedGapBps)===_chaseDirSign;
  // V10.2.42 PHASE 2 — RELAXED FGT GATE for reversal detection.
  //   Was: mtfAlignment >= 1.5 (mild agreement) blocks reversal.
  //   Problem: 0 reversal signal fires across 751 trades. Mild FGT agreement
  //   (1.5) blocks reversal in too many cases — including legitimate exhaustion
  //   patterns where the multi-TF trend is still mildly bullish but the immediate
  //   move is exhausting and about to reverse.
  //   Now: require STRONG FGT agreement (≥3.0) to block. This lets reversal fire
  //   when FGT is mildly aligned with chase but the immediate move is exhausted.
  //   Strong FGT (3.0+) still blocks — genuine trend continuation is safe.
  const _fgtAgreesChase=(_chaseDirSign>0&&mtfAlignment>=3.0)||(_chaseDirSign<0&&mtfAlignment<=-3.0);
  const _continuationConfirmed=_realizedAgreesChase||_projAgreesChase||_fgtAgreesChase;
  if(_allHighMag&&_allSameDir&&timeFraction<=0.6&&!_structConfirmsChase&&!_continuationConfirmed){
    // Exhaustion strength = sum of magnitudes above the 10pt floor, capped
    const _exhStrength=Math.min(40,(Math.abs(_gapScore)-10)+(Math.abs(_momScore)-10)+(Math.abs(_flowScore)-10)+10);
    // Time multiplier — full at >50% remaining, tapers to 0.5 by 40% remaining
    const _revTimeMult=timeFraction<=0.5?1.0:timeFraction<=0.6?(1.0-(timeFraction-0.5)*5.0):0.5;
    const _reversalBoost=-Math.round(_exhStrength*_revTimeMult)*_chaseDirSign;
    totalScore+=_reversalBoost;
    rawSignalScores.reversal=_reversalBoost; // expose for telemetry / memory log
    const _chasingDirLabel=_chaseDirSign>0?'UP':'DOWN';
    const _predictedDir=_chaseDirSign>0?'DOWN':'UP';
    reasoning.push(`[REVERSAL-PREDICTOR] All 3 signals exhausted ${_chasingDirLabel} (g:${_gapScore.toFixed(0)} m:${_momScore.toFixed(0)} f:${_flowScore.toFixed(0)}) → predicting reversal to ${_predictedDir} ${_reversalBoost>0?'+':''}${_reversalBoost} (×${_revTimeMult.toFixed(2)} time-damp)`);
  } else if(_allHighMag&&_allSameDir&&timeFraction<=0.6&&_continuationConfirmed){
    // Diagnostic — logged so user can see why we didn't flip when it might have looked right
    const _why=[];
    if(_realizedAgreesChase)_why.push(`realized gap ${_realGapBpsLocal.toFixed(0)}bps confirms chase`);
    if(_projAgreesChase)_why.push(`projection ${_projectedGapBps.toFixed(0)}bps confirms chase`);
    if(_fgtAgreesChase)_why.push(`FGT ${mtfAlignment.toFixed(1)} confirms chase`);
    reasoning.push(`[REVERSAL-PREDICTOR-SKIPPED] All 3 signals chasing but continuation confirmed: ${_why.join(', ')}`);
  }
  // V134: FORECAST-STICKY removed — replaced by HPotter FGT (more rigorous)
  // Also: pure trajectory without strike (for early-window calls when strike not set yet)
  if(targetMargin<=0&&Math.abs(_v5sNum)>0.3){
    // No strike yet — use raw velocity sign as directional hint
    const _trajHint=Math.min(8,Math.abs(_v5sNum)*5)*Math.sign(_v5sNum);
    trajectoryAdj+=_trajHint;
    reasoning.push(`[TRAJ-EARLY] Velocity ${_v5sNum.toFixed(2)}$/s — early ${_v5sNum>0?'UP':'DOWN'} hint`);
  }
  // Scaling factors used downstream — multipliers for thresholds, sample counts, etc
  const velocityScalars={
    SLOW:    {samples:1.4, threshTighten:6, momentumTol:0.3, lockHold:1.3, decayRate:0.85},
    NORMAL:  {samples:1.0, threshTighten:0, momentumTol:0.5, lockHold:1.0, decayRate:1.0},
    FAST:    {samples:0.7, threshTighten:-3,momentumTol:1.2, lockHold:0.8, decayRate:1.25},
    EXTREME: {samples:0.5, threshTighten:-6,momentumTol:2.5, lockHold:0.6, decayRate:1.6}
  };
  const _velAdj=velocityScalars[velocityRegime];
  reasoning.push(`[VEL] ${velocityRegime} regime (score: ${_velScore.toFixed(1)} | ATR: ${atrBps.toFixed(1)}bps)`);
  const vwap=calcVWAP(liveHistory);
  const bb=calcBB([...closes].reverse(),20);
  // V10.9.1: realGapBps is THE dominant signal (delta +9.7 historically) — it's
  //   spot-vs-strike, exactly the calc affected by OKX's $50-70 offset from CF
  //   Benchmark. Use referencePrice (BRTI trimmed mean) when available so gap
  //   reflects what Kalshi actually settles against, not the chart feed's price.
  const _gapSpot=Number(referencePrice||currentPrice)||currentPrice;
  const realGapBps=targetMargin>0?((_gapSpot-targetMargin)/targetMargin)*10000:0;
  // ── V134: STRIKE QUALITY SCORING ──────────────────────────────────────────
  // Detect when the strike was set during anomaly/spike vs normal price.
  // Compare strike to recent 5-min average. If far off, the strike is "dirty"
  // and gap-gravity should be downweighted.
  let strikeQuality=1.0; // 1.0 = clean, 0.5 = dirty
  if(targetMargin>0&&liveHistory.length>=5){
    // Average of recent 5 candle closes BEFORE current = baseline
    const baseline=liveHistory.slice(1,6).reduce((s,c)=>s+(c.c||0),0)/5;
    if(baseline>0){
      const strikeDeviationBps=Math.abs(((targetMargin-baseline)/baseline)*10000);
      // >40bps deviation = strike was set during a spike or whipsaw
      if(strikeDeviationBps>40){
        strikeQuality=Math.max(0.4,1.0-(strikeDeviationBps-40)/200);
        reasoning.push(`[STRIKE-Q] Dirty strike: ${strikeDeviationBps.toFixed(0)}bps off 5min baseline → gap weight ${(strikeQuality*100).toFixed(0)}%`);
      }
    }
  }
  const{v1s,v5s,v15s,v30s,accel,pnlSlope}=velocityRef.current||{};

  // Historic price lookup
  const getHP=(msAgo)=>{const t=Date.now()-msAgo;const m=priceMemoryRef.current;if(!m||m.length===0)return currentPrice;let c=m[0];for(let i=m.length-1;i>=0;i--){if(m[i].time<=t){c=m[i];break;}}return c.p;};
  const drift1m=((currentPrice-getHP(60000))/currentPrice)*10000;
  const drift5m=((currentPrice-getHP(300000))/currentPrice)*10000;
  const drift15m=((currentPrice-getHP(900000))/currentPrice)*10000;

  // V99 NEW SIGNALS
  const consecutive=calcConsecutiveCandles(liveHistory);
  const volRatio=calcVolumeRatio(liveHistory,5,25);
  const channel=calcPriceChannel(liveHistory,20);
  const momentumAlign=calcMomentumAlignment(drift1m,drift5m,drift15m);
  const aggrFlow=Math.max(-1,Math.min(1,globalFlow.imbalance-1));
  const ticks=tickHistoryRef.current;
  const tickSlope=ticks.length>=10?(currentPrice-ticks[0].p):0;

  // ── V6.4: CONVICTION ASYMMETRY DETECTOR ──
  //   Real traders feel "this down move is stronger than the up move was." That's a
  //   comparison of swing strength: range × volume / time. We compute the strength
  //   of the most recent UP swing and most recent DOWN swing in tick history and
  //   compare them. Asymmetry score:
  //     > +30 = current direction (the latest swing) is meaningfully stronger
  //     -30 to +30 = comparable, no clear asymmetry
  //     < -30 = current direction is weaker, prior swing dominates
  //   This becomes a structural early-warning signal for trend exhaustion.
  const _convictionAsymmetry=(()=>{
    if(!ticks||ticks.length<20)return{score:0,latestDir:null,strengths:null,valid:false};
    // Identify swings by walking back from now: a swing flips when price reverses by ≥10bps
    //   from the local extreme. Capture at most the last 2 swings.
    const _now=Date.now();
    const _windowMs=180000; // last 3 minutes of tick history
    const recentTicks=ticks.filter(t=>(_now-t.time)<=_windowMs);
    if(recentTicks.length<20)return{score:0,latestDir:null,strengths:null,valid:false};
    // Walk through ticks identifying turning points
    const _flipBps=10;
    const turningPoints=[];
    let extreme=recentTicks[0].p,extremeIdx=0,direction=null;
    for(let i=1;i<recentTicks.length;i++){
      const p=recentTicks[i].p;
      const moveBps=((p-extreme)/extreme)*10000;
      if(direction===null){
        if(Math.abs(moveBps)>=_flipBps){
          direction=moveBps>0?'UP':'DOWN';
          extreme=p;extremeIdx=i;
        }else if(p>extreme||p<extreme){
          // tracking extreme until we know direction
          if(direction===null){
            extreme=p;extremeIdx=i;
          }
        }
      }else if(direction==='UP'){
        if(p>extreme){extreme=p;extremeIdx=i;}
        else if(((extreme-p)/extreme)*10000>=_flipBps){
          // flipped to DOWN — extreme was a top
          turningPoints.push({idx:extremeIdx,price:extreme,time:recentTicks[extremeIdx].time,side:'TOP'});
          direction='DOWN';extreme=p;extremeIdx=i;
        }
      }else{ // direction==='DOWN'
        if(p<extreme){extreme=p;extremeIdx=i;}
        else if(((p-extreme)/extreme)*10000>=_flipBps){
          turningPoints.push({idx:extremeIdx,price:extreme,time:recentTicks[extremeIdx].time,side:'BOTTOM'});
          direction='UP';extreme=p;extremeIdx=i;
        }
      }
    }
    // Need at least 2 turning points to define 2 swings
    if(turningPoints.length<2)return{score:0,latestDir:direction,strengths:null,valid:false};
    // Most recent swing: from turningPoints[last] to current
    // Prior swing: from turningPoints[last-1] to turningPoints[last]
    const tp1=turningPoints[turningPoints.length-2];
    const tp2=turningPoints[turningPoints.length-1];
    const _scoreSwing=(startIdx,endIdx,startPrice,endPrice)=>{
      const range=Math.abs(endPrice-startPrice);
      const dur=Math.max(1,(recentTicks[endIdx].time-recentTicks[startIdx].time)/1000); // seconds
      let volume=0;
      for(let i=startIdx;i<=endIdx;i++){volume+=Math.abs(recentTicks[i].s||0)*recentTicks[i].p;}
      const rangeBps=(range/startPrice)*10000;
      // strength = bps × volume_thousands / sqrt(seconds). sqrt because longer swings
      //   shouldn't be linearly penalized.
      return rangeBps*Math.sqrt(volume/1000)/Math.sqrt(dur);
    };
    const priorStrength=_scoreSwing(tp1.idx,tp2.idx,tp1.price,tp2.price);
    const currentEnd=recentTicks.length-1;
    const currentStrength=_scoreSwing(tp2.idx,currentEnd,tp2.price,recentTicks[currentEnd].p);
    // Asymmetry: relative strength of current vs prior. Map to -100..+100.
    if(priorStrength<=0||currentStrength<=0){
      return{score:0,latestDir:direction,strengths:{prior:priorStrength,current:currentStrength},valid:false};
    }
    const ratio=currentStrength/priorStrength;
    // ratio > 1 = current stronger, < 1 = current weaker
    let score;
    if(ratio>=1)score=Math.min(100,(ratio-1)*100);
    else score=Math.max(-100,-(1/ratio-1)*100);
    return{
      score:Math.round(score),
      latestDir:direction,
      priorDir:tp2.side==='TOP'?'UP':'DOWN', // direction that LED to tp2
      strengths:{prior:Math.round(priorStrength),current:Math.round(currentStrength)},
      valid:true,
    };
  })();

  // ── SIGNAL 1: GAP GRAVITY ──
  const timeDecay=Math.pow(timeFraction,is15m?1.8:1.3);
  const isPostDecay=timeFraction>0.6;
  const decayMult=isPostDecay?1.5:1.0;
  // V134: Gap gravity rebalanced
  // Small gap (<15bps) + late time → boost (binary outcome near tipping point)
  // Large gap (>25bps) → don't dampen by time (a 35bps distance is real even early)
  const _proximityFactor=Math.abs(realGapBps)<15&&timeFraction>0.7?1.3:1.0;
  const _largeGapFloor=Math.abs(realGapBps)>25?Math.max(0.45,(0.15+0.85*timeDecay)):(0.15+0.85*timeDecay);
  let gapScore=realGapBps*(is15m?0.65:0.85)*_largeGapFloor*decayMult*_proximityFactor;
  const gapMag=Math.abs(realGapBps);
  if(gapMag>15)gapScore+=Math.sign(realGapBps)*Math.pow(gapMag-10,1.3)*(is15m?0.45:0.65);
  if(gapMag>50)gapScore*=0.7;
  // V152 WINDOW-POSITION TAPER: gap matters more as window progresses (terminal price = where you are);
  // momentum matters more early (when there's still time for direction to play out).
  //   timeFraction=0 (start): gap×0.7  momentum×1.0
  //   timeFraction=0.5 (mid):  gap×1.0  momentum×0.7
  //   timeFraction=1.0 (end):  gap×1.3  momentum×0.4
  const _gapTaper=0.7+(timeFraction||0)*0.6;       // 0.7 → 1.3 across window
  const _momentumTaper=1.0-(timeFraction||0)*0.6;  // 1.0 → 0.4 across window
  const gapClamped=Math.max(-W.gap,Math.min(W.gap,gapScore*_gapTaper))*strikeQuality; // V134/V152
  rawSignalScores.gap=gapClamped;
  totalScore+=gapClamped;
  // V7.2: STRIKE-POSITION DOMINANT OVERRIDE. The Jerome insight — in a binary "price above
  //   or below strike at close?" market, the highest-edge signal is current price-relative-
  //   to-strike with time still remaining. Tape can flip in 5 seconds; strike position is
  //   sticky. When price is clearly on one side (≥3 bps) AND we're still early enough for
  //   that drift to mean something (≥40% time remaining), add up to ±20 boost in the gap-
  //   direction. Gated on tape not strongly opposing — if tape is screaming reversal, don't
  //   force trades into a turn. Caps at ±20 so this is influential but not overwhelming.
  const _gapAbsBps=Math.abs(realGapBps);
  if(_gapAbsBps>=3&&timeFraction<=0.6){
    // Flow opposition check: if aggregated flow is strongly opposite the gap direction,
    //   skip the boost. aggrFlow ranges -1 (heavy sell) to +1 (heavy buy). Only pulled
    //   in when flow is not screaming the opposite way — tape can flush before reversing.
    const _gapDirIsUp=realGapBps>0;
    const _flowOpposesGap=_gapDirIsUp?(aggrFlow<=-0.6):(aggrFlow>=0.6);
    if(!_flowOpposesGap){
      // Boost magnitude scales with gap size up to a 20-point cap.
      const _strikeDriftBoost=Math.sign(realGapBps)*Math.min(20,_gapAbsBps*2.5);
      rawSignalScores.gap=gapClamped+_strikeDriftBoost; // expose in telemetry
      totalScore+=_strikeDriftBoost;
      if(_gapAbsBps>=5)reasoning.push(`[STRIKE-DRIFT] ${realGapBps.toFixed(1)} bps with ${(timeFraction*100).toFixed(0)}% remaining → +${_strikeDriftBoost.toFixed(0)} (Jerome-style early-window prior)`);
    }
  }
  if(gapMag>15)reasoning.push(`[GAP] ${realGapBps.toFixed(1)} bps — gravity ${realGapBps>0?'bullish':'bearish'} | W:${W.gap.toFixed(0)}${_gapTaper!==1.0?` ×${_gapTaper.toFixed(2)}`:''}`);

  // ── SIGNAL 2: MOMENTUM COMPOSITE ──
  // V134: Momentum reweighting late in window
  // Early: drift1m matters most (capture turns). Late: drift5m matters most (sustained direction).
  // After 60% time, micro-momentum (1m) gets damped by 50% — price can wiggle but the binary outcome is set.
  let momScore=0;
  const _momLateDamp=timeFraction>0.6?0.5:1.0; // dampen 1m drift when late in window
  if(is15m){momScore=drift5m*0.7+drift1m*0.3*_momLateDamp;}
  else{momScore=(v30s||0)*(10000/currentPrice)*1.5+drift1m*1.0*_momLateDamp+drift5m*0.5;}
  // V152 EXHAUSTION CHECK: when price has already moved >=1σ from window open AND we're 60%+
  // through the window, the alignment signal flips from amplifier to dampener. Continuation is
  // less likely than reversion in this state.
  let _momExhausted=false;
  if(windowOpenPrice>0&&atrBps>0&&(timeFraction||0)>=0.6){
    const _moveBps=((currentPrice-windowOpenPrice)/windowOpenPrice)*10000;
    const _elapsedMin=Math.max(0.5,(timeFraction||0)*(is15m?15:5));
    const _atrEnvelope=atrBps*Math.sqrt(_elapsedMin);
    const _rangeBps=_atrEnvelope>0?_moveBps/_atrEnvelope:0;
    // Same direction as momentum and at edge of range → exhausted
    if(Math.abs(_rangeBps)>=1.0&&Math.sign(_rangeBps)===Math.sign(momScore)){_momExhausted=true;}
  }
  if(momentumAlign.aligned&&momentumAlign.strong){
    momScore*=_momExhausted?0.6:1.5; // V152: inverted multiplier when exhausted
    if(_momExhausted)reasoning.push(`[MOM-EXH] aligned move overextended — dampener applied (×0.6)`);
  } else if(momentumAlign.aligned){
    momScore*=_momExhausted?0.7:1.2;
  }
  const momClamped=Math.max(-W.momentum,Math.min(W.momentum,momScore*0.8*_momentumTaper));
  rawSignalScores.momentum=momClamped;
  totalScore+=momClamped;
  reasoning.push(`[MOMENTUM] ${drift1m.toFixed(1)} bps/1m | ${drift5m.toFixed(1)} bps/5m${momentumAlign.aligned?' ✦ ALIGNED':''}${_momExhausted?' ⚠ EXHAUSTED':''} | W:${W.momentum.toFixed(0)}${_momentumTaper!==1.0?` ×${_momentumTaper.toFixed(2)}`:''}`);

  // ── SIGNAL 3: CANDLE STRUCTURE ──
  // V10.7.35 — FIXED: CC • Yata uses EXHAUSTION logic, not continuation.
  //   OLD: consecutive green → +UP (continuation). This was WRONG.
  //   CC•Yata says: when count reaches 9 → SELL (expect DOWN reversal).
  //   Building toward 9 = still in trend; AT 9 = exhaustion/reversal.
  //
  //   New scoring (matches CC•Yata exhaustion interpretation):
  //     Count 3-5: mild continuation signal (+count) — early trend, still directional
  //     Count 6-7: neutral (0) — trend aging, approaching exhaustion zone
  //     Count 8-9: REVERSAL (-count*2) — exhaustion, expect opposite direction
  //
  //   For green run (UP trend building):
  //     3-5 green: small UP signal (early trend, continuation still likely)
  //     6-7 green: neutral (fading)
  //     8-9 green: DOWN signal (exhaustion, CC•Yata SELL signal at 9)
  //   Same inverted for red run.
  let structScore=0;
  const _cGreen=consecutive.green;
  const _cRed=consecutive.red;
  if(_cGreen>=8){
    // Exhaustion: CC•Yata SELL signal territory → expect DOWN
    structScore-=_cGreen*2;
    reasoning.push(`[STRUCTURE] ${_cGreen}× green — EXHAUSTION, expect reversal DOWN (CC•Yata logic)`);
  } else if(_cGreen>=6){
    // Aging trend: neutral, no score
    reasoning.push(`[STRUCTURE] ${_cGreen}× green — aging trend, neutral`);
  } else if(_cGreen>=3){
    // Early trend: mild continuation
    structScore+=_cGreen;
  }
  if(_cRed>=8){
    // Exhaustion: CC•Yata BUY signal territory → expect UP
    structScore+=_cRed*2;
    reasoning.push(`[STRUCTURE] ${_cRed}× red — EXHAUSTION, expect reversal UP (CC•Yata logic)`);
  } else if(_cRed>=6){
    reasoning.push(`[STRUCTURE] ${_cRed}× red — aging trend, neutral`);
  } else if(_cRed>=3){
    structScore-=_cRed;
  }
  // Volume confirmation (keep as is — volume context is still valid)
  if(volRatio>1.5&&_cGreen>=2)structScore+=(_cGreen>=8?-4:4); // flip vol boost on exhaustion
  if(volRatio>1.5&&_cRed>=2)structScore+=(_cRed>=8?4:-4);
  if(volRatio>2.0){
    if(drift1m>3)structScore+=4;
    else if(drift1m<-3)structScore-=4;
    reasoning.push(`[VOL] High conviction vol (${volRatio.toFixed(1)}x) — signal boosted`);
  } else if(volRatio<0.5){
    structScore*=0.6;
    reasoning.push(`[VOL] Ghost market (${volRatio.toFixed(1)}x) — signal damped`);
  }
  // Volume-Price divergence detection (unchanged)
  if(drift1m>5&&volRatio<0.7){structScore-=10;reasoning.push(`[VOL-DIV] Price up but volume DOWN — possible UP trap`);}
  if(drift1m<-5&&volRatio<0.7){structScore+=10;reasoning.push(`[VOL-DIV] Price down but volume DOWN — possible DOWN trap`);}
  const structClamped=Math.max(-W.structure,Math.min(W.structure,structScore));
  rawSignalScores.structure=structClamped;
  totalScore+=structClamped;
  if(_cGreen>=2||_cRed>=2)reasoning.push(`[STRUCTURE] ${_cGreen>0?_cGreen+'× green':_cRed+'× red'} | Vol: ${volRatio.toFixed(1)}x | W:${W.structure.toFixed(0)}`);

  // ── SIGNAL 4: FLOW IMBALANCE ──
  let flowScore=aggrFlow*(is15m?W.flow:W.flow*1.5);
  if(accel&&drift1m>0&&accel>0)flowScore+=8;
  if(accel&&drift1m<0&&accel<0)flowScore-=8;
  // V3.2.2: Tape acceleration override. The aggrFlow value averages 90 seconds of trades,
  //   so a sharp 30s shift gets diluted. When the 30s window is extremely one-sided
  //   (≥85% one direction AND ≥$30K total volume), surface that recency directly.
  //   This catches "tape just flipped" scenarios that the slower average misses.
  const _ticks=params.tradeTicksRef?.current||[];
  if(_ticks.length>=5){
    const now=Date.now();
    let buy30=0,sell30=0;
    for(const t of _ticks){
      if(now-t.time>30000)continue;
      const u=t.usd||(t.s*t.p);
      if(t.t==='B')buy30+=u;else sell30+=u;
    }
    const total30=buy30+sell30;
    if(total30>=30000){
      const sellPct=sell30/total30;
      const buyPct=buy30/total30;
      if(sellPct>=0.85){
        flowScore-=15;
        reasoning.push(`[TAPE-30s] ${(sellPct*100).toFixed(0)}% sell on $${(total30/1000).toFixed(0)}K — sharp recent shift, boosting DOWN`);
      } else if(buyPct>=0.85){
        flowScore+=15;
        reasoning.push(`[TAPE-30s] ${(buyPct*100).toFixed(0)}% buy on $${(total30/1000).toFixed(0)}K — sharp recent shift, boosting UP`);
      }
    }
  }
  const flowClamped=Math.max(-W.flow,Math.min(W.flow,flowScore));
  rawSignalScores.flow=flowClamped;
  rawSignalScores._flowRaw=Math.round(flowScore*10)/10; // V10.7.42 audit stamp
  totalScore+=flowClamped;
  reasoning.push(`[FLOW] Delta: ${globalFlow.deltaUSD>0?'+':''}${formatUSD(globalFlow.deltaUSD)} | Imbalance: ${aggrFlow.toFixed(2)} | W:${W.flow.toFixed(0)} | raw:${flowScore.toFixed(1)}`);

  // ── SIGNAL 5: TECHNICAL COMPOSITE ──
  let techScore=0;
  if(rsi>70&&realGapBps>0){techScore-=15;reasoning.push(`[RSI] Overbought (${rsi.toFixed(0)}) — fading top`);}
  else if(rsi<30&&realGapBps<0){techScore+=15;reasoning.push(`[RSI] Oversold (${rsi.toFixed(0)}) — fading bottom`);}
  else if(rsi>60&&drift1m<0)techScore-=8;
  else if(rsi<40&&drift1m>0)techScore+=8;
  // V3.2.2: VWAP "suppression" rules removed. They were actively fighting real reversals.
  //   The intent was mean-reversion to VWAP, but on 15m windows this added counter-trend
  //   bias precisely when the trend was breaking. Example: price above VWAP + drift turning
  //   negative = REAL DOWN setup, but the rule added +10 to UP, killing the DOWN call.
  //   RSI overbought/oversold above already handles mean-reversion at extremes; VWAP rule
  //   was redundant noise.
  // V10.7.36 — SESSION VWAP STDEV BANDS (Pine Script: VWAP Stdev Bands v2 Mod)
  //   Replaces BB in overbought/oversold detection. Volume-weighted, more reliable.
  //   dev = sqrt(v2sum/volumesum - vwap²) = volume-weighted price stdev.
  //   Bands at ±2.01σ (Pine's "second group") = overbought/oversold territory.
  //   Fires 3-4× more often than RSI>70 in chop, with better accuracy.
  const _svwap=computeSessionVWAP(liveHistory,Date.now());
  if(_svwap.valid&&_svwap.dev>0){
    // Price above VWAP + 2.01σ AND above strike = overbought, fade UP
    if(currentPrice>_svwap.upper2&&realGapBps>0){
      techScore-=12;
      reasoning.push(`[VWAP-OB] Price above VWAP+2.01σ (${_svwap.upper2.toFixed(0)}) — overbought, fade UP`);
    }
    // Price below VWAP - 2.01σ AND below strike = oversold, fade DOWN
    else if(currentPrice<_svwap.lower2&&realGapBps<0){
      techScore+=12;
      reasoning.push(`[VWAP-OS] Price below VWAP-2.01σ (${_svwap.lower2.toFixed(0)}) — oversold, fade DOWN`);
    }
    // Price near upper1 band + drift up = moderately overbought
    else if(currentPrice>_svwap.upper1&&realGapBps>0&&drift1m>0){
      techScore-=6;
      reasoning.push(`[VWAP-OB1] Price above VWAP+1.28σ with upward drift — moderate fade`);
    }
    // Price near lower1 band + drift down = moderately oversold
    else if(currentPrice<_svwap.lower1&&realGapBps<0&&drift1m<0){
      techScore+=6;
      reasoning.push(`[VWAP-OS1] Price below VWAP-1.28σ with downward drift — moderate fade`);
    }
    // VWAP Trend: EMA of close vs EMA of VWAP (from VWAP Trend Pine Script)
    // trendUp = EMA(close,14) > EMA(vwap,14). Quick proxy: currentPrice vs vwap with drift
    const _vwapBias=currentPrice>_svwap.vwap?'UP':'DOWN';
    rawSignalScores.vwap=_svwap.vwap; // store for telemetry
    reasoning.push(`[VWAP] ${_svwap.vwap.toFixed(0)} (±${_svwap.dev.toFixed(0)}) | price ${_vwapBias} VWAP | bands: ${_svwap.upper2.toFixed(0)}/${_svwap.lower2.toFixed(0)} (2σ)`);
  }
  if(bb.pctB>0.85&&realGapBps>0){techScore-=8;reasoning.push(`[BB] Upper band squeeze — overbought`);}
  if(bb.pctB<0.15&&realGapBps<0){techScore+=8;reasoning.push(`[BB] Lower band squeeze — oversold`);}
  if(channel>0.8&&drift1m>0)techScore-=5;
  if(channel<0.2&&drift1m<0)techScore+=5;
  const techClamped=Math.max(-W.technical,Math.min(W.technical,techScore));
  rawSignalScores.technical=techClamped;
  totalScore+=techClamped;

  // ── SIGNAL 5b: STOCHASTIC RSI (V10.7.37) ─────────────────────────────────
  // Replaces RSI>70/<30 with more sensitive StochRSI. 4× more firing rate.
  // K>80 + above strike = overbought fade. K<20 + below strike = oversold fade.
  // Conservative weights: ±10 overbought/oversold, ±5 moderate zone.
  const _stochRSI=computeStochRSI(closes,14,14,3,3);
  if(_stochRSI.valid){
    let stochScore=0;
    if(_stochRSI.overbought&&realGapBps>0){stochScore-=10;reasoning.push(`[StochRSI] K=${_stochRSI.k.toFixed(0)} overbought — fade UP`);}
    else if(_stochRSI.oversold&&realGapBps<0){stochScore+=10;reasoning.push(`[StochRSI] K=${_stochRSI.k.toFixed(0)} oversold — fade DOWN`);}
    // Moderate zone: scale with K value (K=79 fires harder than K=66)
    else if(_stochRSI.k>65&&realGapBps>0&&drift1m>0){
      const _stochMod=Math.round((_stochRSI.k-65)/14*5); // 0-5 scale
      stochScore-=_stochMod;
      if(_stochMod>0)reasoning.push(`[StochRSI] K=${_stochRSI.k.toFixed(0)} elevated — moderate fade UP (-${_stochMod})`);
    }else if(_stochRSI.k<35&&realGapBps<0&&drift1m<0){
      const _stochMod=Math.round((35-_stochRSI.k)/35*5);
      stochScore+=_stochMod;
      if(_stochMod>0)reasoning.push(`[StochRSI] K=${_stochRSI.k.toFixed(0)} depressed — moderate fade DOWN (+${_stochMod})`);
    }
    const stochClamped=Math.max(-12,Math.min(12,stochScore));
    rawSignalScores.stochRSI=stochClamped;
    rawSignalScores._stochK=_stochRSI.k; // raw K value for audit
    totalScore+=stochClamped;
  }

  // ── SIGNAL 5c: MARKET STRUCTURE BOS (V10.7.37) ───────────────────────────
  // Break of Structure: close breaks prev swing high/low with EMA trend aligned.
  // Confirmed Long (bosBull + EMA20>EMA50 + close>EMA20): strong UP structural signal.
  // Confirmed Short (bosBear + EMA20<EMA50 + close<EMA20): strong DOWN structural signal.
  // Conservative: ±12 for confirmed BOS, ±6 for trend-only (no BOS).
  const _msBOS=computeMarketStructureBOS(liveHistory,20,50,5);
  if(_msBOS.valid){
    let bosScore=0;
    if(_msBOS.confirmLong){bosScore+=12;reasoning.push(`[BOS] Confirmed LONG — break above swing ${_msBOS.swingHigh}, EMA20(${_msBOS.fastEMA})>EMA50(${_msBOS.slowEMA})`);}
    else if(_msBOS.confirmShort){bosScore-=12;reasoning.push(`[BOS] Confirmed SHORT — break below swing ${_msBOS.swingLow}, EMA20<EMA50`);}
    else if(_msBOS.bullTrend&&_msBOS.bosBull){bosScore+=8;reasoning.push(`[BOS] Bull break above swing (no EMA confirm yet)`);}
    else if(_msBOS.bearTrend&&_msBOS.bosBear){bosScore-=8;reasoning.push(`[BOS] Bear break below swing (no EMA confirm yet)`);}
    else if(_msBOS.bullTrend){bosScore+=4;reasoning.push(`[BOS] Bull trend (EMA20>EMA50) — no break yet`);}
    else if(_msBOS.bearTrend){bosScore-=4;reasoning.push(`[BOS] Bear trend (EMA20<EMA50) — no break yet`);}
    const bosClamped=Math.max(-15,Math.min(15,bosScore));
    rawSignalScores.bos=bosClamped;
    totalScore+=bosClamped;
  }

  // ── SIGNAL 5e: LIQUIDITY SWEEP DETECTOR (V12.3) ───────────────────
  // ICT/SMC step 2: price spikes through prior swing high/low to grab stops,
  // then reverses. Sweep-and-fade = strongest ICT directional signal.
  let _smcSweep=null;
  try{
    if(liveHistory&&liveHistory.length>=12&&atr>0){
      const _swSlice=liveHistory.slice(2,20);
      const _swHigh=Math.max(..._swSlice.map(b=>b.h));
      const _swLow=Math.min(..._swSlice.map(b=>b.l));
      const _spikeBars=liveHistory.slice(0,3);
      const _curClose=liveHistory[0].c;
      const _piercedHigh=_spikeBars.some(b=>b.h>_swHigh);
      const _highExtent=_piercedHigh?Math.max(..._spikeBars.map(b=>b.h))-_swHigh:0;
      const _bearSweep=_piercedHigh&&_curClose<_swHigh&&_highExtent>0;
      const _bearRej=_bearSweep?Math.max(0,1-Math.abs(_curClose-_swHigh)/Math.max(atr,1)):0;
      const _piercedLow=_spikeBars.some(b=>b.l<_swLow);
      const _lowExtent=_piercedLow?_swLow-Math.min(..._spikeBars.map(b=>b.l)):0;
      const _bullSweep=_piercedLow&&_curClose>_swLow&&_lowExtent>0;
      const _bullRej=_bullSweep?Math.max(0,1-Math.abs(_curClose-_swLow)/Math.max(atr,1)):0;
      if(_bearSweep||_bullSweep){
        const _sDir=_bearSweep?"DOWN":"UP";
        const _ext=_bearSweep?_highExtent:_lowExtent;
        const _rej=_bearSweep?_bearRej:_bullRej;
        const _extBps=Math.round((_ext/currentPrice)*10000);
        const _pts=Math.round(10+(Math.min(_extBps,20)/20)*5+_rej*3);
        const _adj=_bearSweep?-_pts:_pts;
        const _lvl=_bearSweep?Math.round(_swHigh):Math.round(_swLow);
        _smcSweep={dir:_sDir,extBps:_extBps,rejPct:Math.round(_rej*100),score:_adj,level:_lvl};
        totalScore+=_adj;
        rawSignalScores._sweep=_adj;
        reasoning.push(`[SMC-SWEEP] ${_sDir}: pierced ${_bearSweep?"swing high":"swing low"} $${_lvl} by ${_extBps}bps, ${_rej>=0.6?"clean":"weak"} rejection -> ${_adj>0?"+":""}${_adj}pts`);
      } else { rawSignalScores._sweep=0; }
    }
  }catch(_swErr){}

  // ── SIGNAL 5f: FAIR VALUE GAP (FVG) DETECTOR (V12.3) ─────────────────
  // ICT/SMC step 3: 3-candle price imbalance. Bearish FVG = candle[n].low >
  // candle[n-2].high. IFVG fires when price returns to gap from wrong side.
  let _smcFVG=null;
  try{
    if(liveHistory&&liveHistory.length>=10&&currentPrice>0&&atr>0){
      let _bestFVG=null;
      for(let _fi=2;_fi<Math.min(30,liveHistory.length);_fi++){
        const _b2=liveHistory[_fi];const _b0=liveHistory[_fi-2];
        if(_b2.l>_b0.h&&(_b2.l-_b0.h)>atr*0.15){
          _bestFVG={type:"bearish",gapHigh:_b2.l,gapLow:_b0.h,size:Math.round((_b2.l-_b0.h)/currentPrice*10000),age:_fi};break;
        }
        if(_b2.h<_b0.l&&(_b0.l-_b2.h)>atr*0.15){
          _bestFVG={type:"bullish",gapHigh:_b0.l,gapLow:_b2.h,size:Math.round((_b0.l-_b2.h)/currentPrice*10000),age:_fi};break;
        }
      }
      if(_bestFVG){
        const _inside=currentPrice>=_bestFVG.gapLow&&currentPrice<=_bestFVG.gapHigh;
        const _nearBelow=currentPrice<_bestFVG.gapLow&&(_bestFVG.gapLow-currentPrice)<atr*0.5;
        const _nearAbove=currentPrice>_bestFVG.gapHigh&&(currentPrice-_bestFVG.gapHigh)<atr*0.5;
        let _fvgAdj=0;let _fvgLabel="";
        if(_bestFVG.type==="bearish"&&(_inside||_nearBelow)){
          const _str=Math.min(1,_bestFVG.size/15);
          _fvgAdj=-Math.round(8+_str*6);
          _fvgLabel=_inside?"inside bearish FVG (resistance)":"approaching bearish FVG from below (IFVG)";
        } else if(_bestFVG.type==="bullish"&&(_inside||_nearAbove)){
          const _str=Math.min(1,_bestFVG.size/15);
          _fvgAdj=Math.round(8+_str*6);
          _fvgLabel=_inside?"inside bullish FVG (support)":"approaching bullish FVG from above (IFVG)";
        }
        if(_fvgAdj!==0){
          _smcFVG={..._bestFVG,adj:_fvgAdj,label:_fvgLabel};
          totalScore+=_fvgAdj;rawSignalScores._fvg=_fvgAdj;
          reasoning.push(`[SMC-FVG] ${_fvgLabel} (${_bestFVG.size}bps gap, age ${_bestFVG.age}bars) -> ${_fvgAdj>0?"+":""}${_fvgAdj}pts`);
        } else { rawSignalScores._fvg=0; }
      }
    }
  }catch(_fvgErr){}


  // ── SIGNAL 5g: HALFTREND + SUPERTREND CONFIRMATION (V12.7) ─────────
  // Two trend-following indicators on the 1m chart, used as CONFIRMATION (not primary).
  // They carry less weight than Gap/Tape but more than weak signals. Their main value
  // is late-entry quality: when <5min left, they confirm whether momentum still holds.
  //
  // SUPERTREND: ATR-band trend filter. Basis = hl2. Upper/lower bands = hl2 +/- mult*ATR.
  //   Trend flips to UP when close crosses above the prior lower band, DOWN when it
  //   crosses below the prior upper band. Standard ATR(10) mult 3; we use atr(14) with
  //   a 1m-tuned mult of 2.5 for faster response inside 15m windows.
  //
  // HALFTREND: a smoothed trend-channel indicator. Simplified deterministic version:
  //   trend is UP while price holds above the EMA midline and the rolling high channel,
  //   DOWN while it holds below the midline and the rolling low channel. Approximated
  //   with an n-bar high/low envelope + EMA midline (same flip behavior, no full state).
  //
  // Output: _htDir / _stDir, recent-flip distances, and a combined _trendConfirm
  //   stamped to rawSignalScores so the late-entry gate (V12.5) can read it.
  let _trendConfirm=null;
  try{
    if(liveHistory&&liveHistory.length>=18&&atr>0&&currentPrice>0){
      const _ob=liveHistory.slice(0,40).slice().reverse(); // oldest->newest, up to 40 bars
      const _N=_ob.length;
      // SUPERTREND (ATR bands, mult 2.5)
      const _stMult=2.5;
      let _stTrend=1; // 1=UP, -1=DOWN
      let _stPrevUpper=null,_stPrevLower=null,_stPrevClose=null;
      let _stFlipIdx=0;
      for(let _i=0;_i<_N;_i++){
        const _c=_ob[_i];
        const _hl2=(_c.h+_c.l)/2;
        const _bUpper=_hl2+_stMult*atr;
        const _bLower=_hl2-_stMult*atr;
        const _upper=(_stPrevUpper==null)?_bUpper:(_bUpper<_stPrevUpper||_stPrevClose>_stPrevUpper)?_bUpper:_stPrevUpper;
        const _lower=(_stPrevLower==null)?_bLower:(_bLower>_stPrevLower||_stPrevClose<_stPrevLower)?_bLower:_stPrevLower;
        let _newTrend=_stTrend;
        if(_stPrevUpper!=null){
          if(_stTrend===-1&&_c.c>_stPrevUpper){_newTrend=1;}
          else if(_stTrend===1&&_c.c<_stPrevLower){_newTrend=-1;}
        }
        if(_newTrend!==_stTrend){_stFlipIdx=_i;_stTrend=_newTrend;}
        _stPrevUpper=_upper;_stPrevLower=_lower;_stPrevClose=_c.c;
      }
      const _stDir=_stTrend===1?'UP':'DOWN';
      const _stBarsSinceFlip=_N-1-_stFlipIdx;
      // HALFTREND (high/low envelope + EMA midline)
      const _htAmp=4;
      let _htTrend=1;
      let _htFlipIdx=0;
      let _htEma=_ob[0].c;
      const _htK=2/(6+1);
      for(let _i=1;_i<_N;_i++){
        _htEma=_ob[_i].c*_htK+_htEma*(1-_htK);
        const _lo=Math.min(..._ob.slice(Math.max(0,_i-_htAmp+1),_i+1).map(b=>b.l));
        const _hi=Math.max(..._ob.slice(Math.max(0,_i-_htAmp+1),_i+1).map(b=>b.h));
        let _newHt=_htTrend;
        if(_htTrend===-1&&_ob[_i].c>_htEma&&_ob[_i].c>=_hi-atr*0.25){_newHt=1;}
        else if(_htTrend===1&&_ob[_i].c<_htEma&&_ob[_i].c<=_lo+atr*0.25){_newHt=-1;}
        if(_newHt!==_htTrend){_htFlipIdx=_i;_htTrend=_newHt;}
      }
      const _htDir=_htTrend===1?'UP':'DOWN';
      const _htBarsSinceFlip=_N-1-_htFlipIdx;
      // COMBINE
      const _aligned=_htDir===_stDir;
      const _minBarsSinceFlip=Math.min(_htBarsSinceFlip,_stBarsSinceFlip);
      const _strongAligned=_aligned&&_minBarsSinceFlip>=3;
      const _conflict=!_aligned;
      _trendConfirm={
        htDir:_htDir,stDir:_stDir,
        aligned:_aligned,strongAligned:_strongAligned,conflict:_conflict,
        dir:_aligned?_htDir:null,
        barsSinceFlip:_minBarsSinceFlip,
        htBarsSinceFlip:_htBarsSinceFlip,stBarsSinceFlip:_stBarsSinceFlip,
      };
      rawSignalScores._trendConfirm=_trendConfirm;
      // SCORE CONTRIBUTION (confirmation weight: moderate)
      const _callLean=totalScore>0?'UP':totalScore<0?'DOWN':null;
      let _tcAdj=0;
      if(_callLean&&_aligned&&_trendConfirm.dir===_callLean){
        _tcAdj=_strongAligned?(_callLean==='UP'?9:-9):(_callLean==='UP'?5:-5);
        reasoning.push(`[TREND-CONFIRM] HalfTrend + SuperTrend both ${_trendConfirm.dir}${_strongAligned?' (settled, no recent flip)':''} confirm ${_callLean} -> ${_tcAdj>0?'+':''}${_tcAdj}pts`);
      } else if(_callLean&&_aligned&&_trendConfirm.dir&&_trendConfirm.dir!==_callLean){
        _tcAdj=_strongAligned?(_callLean==='UP'?-8:8):(_callLean==='UP'?-5:5);
        reasoning.push(`[TREND-CONFIRM] HalfTrend + SuperTrend both ${_trendConfirm.dir} OPPOSE ${_callLean} lean -> ${_tcAdj>0?'+':''}${_tcAdj}pts caution`);
      } else if(_conflict){
        if(_callLean==='UP')_tcAdj=-2;else if(_callLean==='DOWN')_tcAdj=2;
        reasoning.push(`[TREND-CONFIRM] HalfTrend ${_htDir} vs SuperTrend ${_stDir} conflict -> dampen ${_tcAdj>0?'+':''}${_tcAdj}pts`);
      }
      totalScore+=_tcAdj;
      rawSignalScores._trendConfirmScore=_tcAdj;
    }
  }catch(_tcErr){}

  // ── SIGNAL 5d: MACD + OBV (V10.7.37) ────────────────────────────────────
  // MACD(12,26,9) on hl2. Histogram zero-cross = strong momentum flip.
  // OBV confirms volume direction (OBV > SMA10 = accumulation = bullish).
  // Combined: hist cross + OBV agreement = high-confidence momentum signal.
  const _macdOBV=computeMACDAndOBV(liveHistory,12,26,9,10);
  if(_macdOBV.valid){
    let macdScore=0;
    // Strong: histogram zero-cross
    if(_macdOBV.crossUp){macdScore+=10;reasoning.push(`[MACD] Histogram crossed UP (${_macdOBV.histPrev.toFixed(2)}→${_macdOBV.hist.toFixed(2)}) — bullish momentum flip`);}
    else if(_macdOBV.crossDown){macdScore-=10;reasoning.push(`[MACD] Histogram crossed DOWN (${_macdOBV.histPrev.toFixed(2)}→${_macdOBV.hist.toFixed(2)}) — bearish momentum flip`);}
    // Moderate: histogram direction — only fire if meaningfully non-zero
    // V10.7.38 calibration fix: in chop, histogram oscillates near 0
    // Threshold: |hist| > 0.5 prevents firing on noise (was firing on +0.01)
    else if(Math.abs(_macdOBV.hist)>0.5){
      if(_macdOBV.bullish){macdScore+=5;reasoning.push(`[MACD] Histogram +${_macdOBV.hist.toFixed(2)} — bullish`);}
      else{macdScore-=5;reasoning.push(`[MACD] Histogram ${_macdOBV.hist.toFixed(2)} — bearish`);}
    }
    // OBV confirmation/contradiction
    if(_macdOBV.obvBullish&&macdScore>0){macdScore+=4;reasoning.push(`[OBV] Accumulation (OBV>${_macdOBV.obvSMA.toFixed(0)}) confirms UP`);}
    else if(!_macdOBV.obvBullish&&macdScore<0){macdScore-=4;reasoning.push(`[OBV] Distribution (OBV<${_macdOBV.obvSMA.toFixed(0)}) confirms DOWN`);}
    else if(_macdOBV.obvBullish&&macdScore<0){macdScore+=2;reasoning.push(`[OBV] Accumulation conflicts with MACD DOWN — dampen`);}
    else if(!_macdOBV.obvBullish&&macdScore>0){macdScore-=2;reasoning.push(`[OBV] Distribution conflicts with MACD UP — dampen`);}
    const macdClamped=Math.max(-15,Math.min(15,macdScore));
    rawSignalScores.macd=macdClamped;
    rawSignalScores._macdHist=_macdOBV.hist; // raw histogram for audit
    rawSignalScores._obvBullish=_macdOBV.obvBullish?1:0;
    totalScore+=macdClamped;
  }

  // ── SIGNAL 5e: ANCHORED VWAP (V10.7.38) ──────────────────────────────────
  // Auto High & Low anchored VWAP by liquid-trader. Anchors to swing high/low
  // in last 20 bars. Signals where committed buyers/sellers are positioned.
  //   Price > anchoredHi: above where recent high buyers sit → bullish
  //   Price < anchoredLo: below where recent low sellers sit → bearish
  //   Price crosses anchoredMean: bias shift signal (equilibrium cross)
  //   Price far above mean (>5bps): extended, slight fade
  //   Price far below mean (<-5bps): extended, slight fade
  const _avwap=computeAnchoredVWAP(liveHistory,20);
  if(_avwap.valid){
    let avwapScore=0;
    if(_avwap.aboveHi){
      // Above high anchor VWAP = strong bullish momentum
      avwapScore+=8;
      reasoning.push(`[AVWAP] Price ${currentPrice.toFixed(0)} > Hi-anchor ${_avwap.anchoredHi.toFixed(0)} — above committed buyers → bullish`);
    }else if(_avwap.belowLo){
      // Below low anchor VWAP = strong bearish momentum
      avwapScore-=8;
      reasoning.push(`[AVWAP] Price ${currentPrice.toFixed(0)} < Lo-anchor ${_avwap.anchoredLo.toFixed(0)} — below committed sellers → bearish`);
    }else if(_avwap.aboveMean){
      // Between mean and hi anchor = mild bullish
      avwapScore+=4;
      reasoning.push(`[AVWAP] Price above mean ${_avwap.anchoredMean.toFixed(0)} (Hi:${_avwap.anchoredHi.toFixed(0)} Lo:${_avwap.anchoredLo.toFixed(0)}) → mild bullish`);
    }else{
      // Between lo anchor and mean = mild bearish
      avwapScore-=4;
      reasoning.push(`[AVWAP] Price below mean ${_avwap.anchoredMean.toFixed(0)} (Hi:${_avwap.anchoredHi.toFixed(0)} Lo:${_avwap.anchoredLo.toFixed(0)}) → mild bearish`);
    }
    // Extended from mean: slight fade (same as VWAP mean reversion logic)
    if(Math.abs(_avwap.distFromMeanBps)>8&&avwapScore>0){
      avwapScore-=2;
      reasoning.push(`[AVWAP-EXT] ${_avwap.distFromMeanBps.toFixed(1)}bps from mean — slightly extended, minor fade`);
    }
    const avwapClamped=Math.max(-10,Math.min(10,avwapScore));
    rawSignalScores.avwap=avwapClamped;
    totalScore+=avwapClamped;
  }

  // ── SIGNAL 5f: THREE-STEP VOLUME DELTA (V10.7.39) ────────────────────────
  // Detects HIDDEN order flow under flat/small-candle price action.
  // Fixes the "gaslighting" problem: when small candles mislead TRAJ velocity,
  // this signal reads cumulative volume pressure to predict breakout direction.
  //
  // Scoring (conservative — primarily informational + coil-state directional):
  //   delta1 strongly positive + accelerating: +10 (fresh buying building)
  //   delta1 strongly positive + momentum flip from negative: +8 (buyers taking over)
  //   delta1 positive (mild): +5
  //   Same logic negative for DOWN
  //   Bonus when coiling: +3 extra (most relevant in consolidation)
  const _tsd=computeThreeStepDelta(liveHistory,25);
  if(_tsd.valid){
    let tsdScore=0;
    const _tsdStrong=Math.abs(_tsd.delta1Pct)>=15; // >15% of volume imbalanced
    const _tsdMild=Math.abs(_tsd.delta1Pct)>=5;
    const _sign=_tsd.delta1>0?1:-1;
    if(_tsdStrong&&_tsd.accelerating){tsdScore=_sign*10;}
    else if(_tsdStrong&&_tsd.momentumFlip){tsdScore=_sign*8;}
    else if(_tsdStrong){tsdScore=_sign*6;}
    else if(_tsdMild){tsdScore=_sign*4;}
    // Extra weight when coiling (this IS the primary signal in consolidation)
    if(_coil.isCoiling&&Math.abs(tsdScore)>0){
      tsdScore=Math.sign(tsdScore)*(Math.abs(tsdScore)+3);
      reasoning.push(`[DELTA] Coiling + ${_tsd.direction} volume delta ${_tsd.delta1Pct.toFixed(1)}% (${_tsd.accelerating?'accelerating':'sustained'}) — primary consolidation signal`);
    }else if(Math.abs(tsdScore)>0){
      reasoning.push(`[DELTA] ${_tsd.direction} delta ${_tsd.delta1Pct.toFixed(1)}% | d1=${_tsd.delta1.toFixed(0)} d2=${_tsd.delta2.toFixed(0)}${_tsd.momentumFlip?' ← FLIP':_tsd.accelerating?' ↑ ACCEL':''}`);
    }
    const tsdClamped=Math.max(-13,Math.min(13,tsdScore));
    rawSignalScores.delta=tsdClamped;
    rawSignalScores._delta1Pct=_tsd.delta1Pct; // raw delta % for audit
    rawSignalScores._delta2Pct=_tsd.delta2!==0?Math.round((_tsd.delta2/Math.max(1,Math.abs(_tsd.delta1+_tsd.delta2+_tsd.delta3)))*100):0;
    rawSignalScores._isCoiling=_coil.isCoiling?1:0;
    rawSignalScores._coilBodyBps=_coil.avgBodyBps;
    rawSignalScores._coilRangeBps=_coil.avgRangeBps;
    rawSignalScores._tsdScoreRaw=tsdScore; // V10.7.42a audit stamp
    totalScore+=tsdClamped;
  }
  const {funding,fundingPrev}=observedFundingPair(bloomberg);
  const delta=globalFlow.deltaUSD||0;
  let regime='RANGE-CHOP';
  let regimeBonus=0;
  let upThreshold=68,downThreshold=32;
  // V135: Symmetric retail funding gates
  const retailShorting=funding<0.005,retailLonging=funding>0.005;
  const whalesBuying=delta>500000,whalesSelling=delta<-500000;
  const whalesBuyingMild=delta>200000,whalesSellingMild=delta<-200000;
  // V10.7.36 — NEW REGIME CLASSIFIER (ADX + BBW percentile + whipsaw + AiBitcoinTrend)
  //   Replaces: isCleanUp (drift×3 TF + whale flow) + isHighVol (atrBps>35)
  //   New: ADX≥20 + no whipsaw + BBW<80th pct = TREND, BBW≥80 = HIGH VOL CHOP
  //   Preserves: SHORT SQUEEZE / LONG SQUEEZE (whale+funding detection unchanged)
  //   Preserves: HTF veto, S/R range override, quiet override (all run after)
  const _v10736=computeRegimeV10736(liveHistory,drift1m,drift5m,drift15m,atrBps);
  const isHighVol=_v10736?_v10736.isHighVol:(atrBps>35); // fallback if not enough bars
  const isCleanUp=_v10736?(_v10736.isTrend&&drift1m>3&&drift5m>3&&drift15m>5):(drift1m>3&&drift5m>5&&drift15m>8&&delta>200000);
  const isCleanDn=_v10736?(_v10736.isTrend&&drift1m<-3&&drift5m<-3&&drift15m<-5):(drift1m<-3&&drift5m<-5&&drift15m<-8&&delta<-200000);
  // V10.7.36: keep _mtfUpAligned/_mtfDnAligned for telemetry stamps downstream (line 10814)
  const _mtfUpAligned=isCleanUp;
  const _mtfDnAligned=isCleanDn;
  if(_v10736){
    reasoning.push(`[V10.7.36 REGIME] ADX=${_v10736.adx.toFixed(1)} BBW=${_v10736.bbwRank}th-pct whipsaw=${_v10736.whipsawCount} ATRP=${_v10736.atrp.toFixed(2)}% → ${_v10736.isHighVol?'HIGH-VOL':_v10736.isTrend?'TREND':'CHOP'} priceAboveMedian=${_v10736.priceAboveMedian}`);
    // V10.7.39: stamp raw indicator values for audit (not just clamped scores)
    rawSignalScores._adx=_v10736.adx;
    rawSignalScores._bbwRank=_v10736.bbwRank;
    rawSignalScores._atrp=_v10736.atrp;
    rawSignalScores._whipsaw=_v10736.whipsawCount;
    // V12.0: also stamp regime label + boolean flags — they were computed but silently
    //   dropped. The CSV showed 0% regime stamp because these never reached the entry.
    rawSignalScores._v10736regime=_v10736.regime;
    rawSignalScores._isHighVol=_v10736.isHighVol;
    rawSignalScores._isTrend=_v10736.isTrend;
    rawSignalScores._isChop=_v10736.isChop;
    rawSignalScores._isCompressing=_v10736.isCompressing;
    rawSignalScores._priceAboveMedian=_v10736.priceAboveMedian;
  }else{
    // Not enough history (< 30 bars) — fall back to old logic
    if(drift1m>3||drift1m<-3){
      reasoning.push(`[V10.2.37 MTF-VETO] drift1m=${drift1m.toFixed(1)} but 5m/15m didn't confirm (5m=${drift5m.toFixed(1)}, 15m=${drift15m.toFixed(1)}) → not trending`);
    }
  }
  if(retailShorting&&whalesBuying&&drift1m>-3){regime='SHORT SQUEEZE';upThreshold=72;downThreshold=26;}
  else if(retailLonging&&whalesSelling&&drift1m<3){regime='LONG SQUEEZE';upThreshold=80;downThreshold=36;}
  const _binancePrice=globalFlow?.binancePrice||0;
  if(_binancePrice>0&&currentPrice>0){
    const _bnDivBps=((_binancePrice-currentPrice)/currentPrice)*10000;
    if(Math.abs(_bnDivBps)>3){
      // Futures divergence — coinbase will follow
      const ledAdj=Math.sign(_bnDivBps)*Math.min(8,Math.abs(_bnDivBps)*0.7);
      totalScore+=ledAdj;
      reasoning.push(`[LEAD] Binance ${_bnDivBps>0?'+':''}${_bnDivBps.toFixed(1)}bps vs Coinbase`);
    }
  }
  // V114: Liquidation & Order Book data (declared before use)
  const liqLongWall=bloomberg?.liqLongWall||0;   // ASKS — short liqs above (UP magnet)
  const liqShortWall=bloomberg?.liqShortWall||0; // BIDS — long liqs below (DOWN magnet)
  const liqLongUSD=bloomberg?.liqLongUSD||0;
  const liqShortUSD=bloomberg?.liqShortUSD||0;
  // V114: Order Book Imbalance (depth-of-market pressure)
  if(liqLongUSD>0&&liqShortUSD>0){
    const obImbal=(liqShortUSD-liqLongUSD)/(liqShortUSD+liqLongUSD);
    if(Math.abs(obImbal)>0.25){
      const obAdj=obImbal*8;
      totalScore+=obAdj;
      reasoning.push(`[OB] Depth imbalance ${(obImbal*100).toFixed(0)}% ${obImbal>0?'BIDS heavier':'ASKS heavier'}`);
    }
  }
  // V134: LIVE ORDER BOOK FLASH — 2.5s polling vs bloomberg's 8s
  // Catches fast wall changes during volatility that 8s polling misses
  if(depthFlash&&depthFlash.depthLastUpdate>0){
    const ageMs=Date.now()-depthFlash.depthLastUpdate;
    if(ageMs<6000){ // only use if fresh
      const liveImbal=depthFlash.obImbalanceLive||0;
      if(Math.abs(liveImbal)>0.30){
        // Agreement with bloomberg's slower snapshot = high conviction
        const slowImbal=liqLongUSD>0&&liqShortUSD>0?(liqShortUSD-liqLongUSD)/(liqShortUSD+liqLongUSD):0;
        const agree=Math.sign(liveImbal)===Math.sign(slowImbal)&&Math.abs(slowImbal)>0.20;
        const liveBonus=agree?Math.sign(liveImbal)*4:Math.sign(liveImbal)*2; // doubles when both timeframes agree
        totalScore+=liveBonus;
        reasoning.push(`[OB-LIVE] ${(liveImbal*100).toFixed(0)}% ${liveImbal>0?'BIDS':'ASKS'} ${agree?'(both timeframes agree)':''}`);
      }
    }
  }
  // V114: Liquidation Cluster Awareness — price gets pulled toward large walls
  let liqAdj=0;
  // V134: Require wall persistence ≥15s (filters spoofed walls)
  const longWallStable=(bloomberg?.longWallAgeMs||0)>=15000;
  const shortWallStable=(bloomberg?.shortWallAgeMs||0)>=15000;
  if(liqLongWall>0&&liqLongUSD>500000&&longWallStable){
    const distBps=((liqLongWall-currentPrice)/currentPrice)*10000;
    if(distBps>0&&distBps<60){
      liqAdj+=Math.min(8,liqLongUSD/200000);
      reasoning.push(`[BOOK] Resting ask liquidity $${(liqLongUSD/1000).toFixed(0)}K @ +${distBps.toFixed(0)}bps (${((bloomberg.longWallAgeMs||0)/1000).toFixed(0)}s old) — modeled UP pull, not confirmed liquidations`);
    }
  } else if(liqLongWall>0&&liqLongUSD>500000&&!longWallStable){
    reasoning.push(`[LIQ-SPOOF] Ignored fresh long wall (<15s) — likely spoof`);
  }
  if(liqShortWall>0&&liqShortUSD>500000&&shortWallStable){
    const distBps=((currentPrice-liqShortWall)/currentPrice)*10000;
    if(distBps>0&&distBps<60){
      liqAdj-=Math.min(8,liqShortUSD/200000);
      reasoning.push(`[BOOK] Resting bid liquidity $${(liqShortUSD/1000).toFixed(0)}K @ -${distBps.toFixed(0)}bps (${((bloomberg.shortWallAgeMs||0)/1000).toFixed(0)}s old) — modeled DOWN pull, not confirmed liquidations`);
    }
  } else if(liqShortWall>0&&liqShortUSD>500000&&!shortWallStable){
    reasoning.push(`[LIQ-SPOOF] Ignored fresh short wall (<15s) — likely spoof`);
  }
  totalScore+=liqAdj;

  // V10.7.99: STRIKE CUSHION SIGNAL.
  //   The most direct predictor of binary outcome available: how far is the current
  //   spot price from the Kalshi floor strike, right now?
  //   Audit (1886 trades): 55% of RANGE-CHOP losses landed within 10bps of the strike.
  //   That's not a signal failure — that's trading when price sits ON the line.
  //   The cushion signal tells Tara: price is Xbps above/below the strike.
  //   A positive cushion means price has already moved into your territory.
  //   A near-zero cushion means it's a coin flip regardless of momentum.
  //   Signal contribution: up to ±15pt in RANGE-CHOP, ±8pt in other regimes.
  //   Cushion > 20bps → strong positive contribution (price needs to reverse a lot to lose)
  //   Cushion 10-20bps → moderate positive
  //   Cushion 0-10bps → slight positive (still uncertain)
  //   Cushion negative → negative contribution (price on wrong side of strike already)
  const _kStrike=Number(params.kalshiStrike||0);
  // V10.9.1: use referencePrice (Coinbase/Kraken/Gemini/Bitstamp trimmed mean) when
  //   available — falls back to currentPrice if BRTI approx isn't warmed up yet.
  //   Corrects for OKX-vs-CF-Benchmark offset ($50-70 typical) so strike distance
  //   reflects what Kalshi actually settles against, regardless of chart feed.
  const _spotNow=Number(params.referencePrice||params.currentPrice||0);
  const _isRange=regime==='RANGE-CHOP';
  if(_kStrike>0&&_spotNow>0){
    // Cushion in bps from spot to strike (positive = spot above strike)
    const _cushionBps=(_spotNow-_kStrike)/_kStrike*10000;
    // The cushion score: 10bps cushion → +5pt, 20bps → +10pt, 30bps → +15pt
    // Raw is directional: positive cushion = price above strike = bullish for UP calls
    // The scoring engine will align this with direction automatically via totalScore
    const _cushionMult=_isRange?0.55:0.30; // more weight in range where it matters most
    const _cushionRaw=Math.max(-18,Math.min(18,_cushionBps*_cushionMult));
    totalScore+=_cushionRaw;
    rawSignalScores.strikeCushion=Math.round(_cushionRaw*10)/10;
    rawSignalScores._cushionBps=Math.round(_cushionBps*10)/10;
    rawSignalScores._kStrikeLive=_kStrike;
    if(Math.abs(_cushionBps)>5){
      const _label=_cushionBps>0?'above':'below';
      reasoning.push(`[CUSHION] spot ${Math.abs(_cushionBps).toFixed(0)}bps ${_label} strike=${_kStrike} (${_cushionRaw>=0?'+':''}${_cushionRaw.toFixed(1)}pt)`);
    }
  }else{
    rawSignalScores.strikeCushion=null;
    rawSignalScores._cushionBps=null;
    rawSignalScores._kStrikeLive=null;
  }

  // V10.7.65 — WHALE ALERT ON-CHAIN SIGNAL
  //   Large BTC on-chain transactions (>$500K) via /api/whale proxy.
  //   Exchange inflow = sell pressure (bearish). Exchange outflow = buy pressure (bullish).
  //   Only active when Whale Alert API key is configured — silently skipped otherwise.
  //   Max ±6pt contribution. Telemetry stamped regardless.
  const _whaleData=params.whaleAlert;
  if(_whaleData?.available&&Number.isFinite(_whaleData.netScore)&&Math.abs(_whaleData.netScore)>1.5){
    const _whaleAdj=Math.max(-6,Math.min(6,_whaleData.netScore));
    totalScore+=_whaleAdj;
    rawSignalScores.whaleAlert=Math.round(_whaleAdj*10)/10;
    rawSignalScores._whaleTxCount=_whaleData.transactionCount||0;
    rawSignalScores._whaleDir=_whaleData.direction||'neutral';
    if(Math.abs(_whaleAdj)>1.5){
      reasoning.push(`[WHALE] on-chain ${_whaleData.direction} (${_whaleData.transactionCount} tx, score ${_whaleAdj>=0?'+':''}${_whaleAdj.toFixed(1)})`);
    }
  }else{
    rawSignalScores.whaleAlert=null;
    rawSignalScores._whaleTxCount=0;
    rawSignalScores._whaleDir='unavailable';
  }

  // V10.7.73 — DERIBIT OPTIONS SIGNAL
  //   IV skew (put IV - call IV): positive = puts expensive = bears loading = bearish
  //   Put/Call ratio: >1.2 = bearish, <0.8 = bullish
  //   Smart money expresses BTC views in options 30-60min before spot moves.
  //   Max ±6pt. Fires when signal magnitude > 0.5.
  const _deribitData=params.deribitOptions;
  if(_deribitData?.ok&&Number.isFinite(_deribitData.signal)&&Math.abs(_deribitData.signal)>0.5){
    const _dAdj=Math.max(-6,Math.min(6,_deribitData.signal));
    totalScore+=_dAdj;
    rawSignalScores.deribitOptions=Math.round(_dAdj*10)/10;
    rawSignalScores._deribitSkew=_deribitData.skew;
    rawSignalScores._deribitPCR=_deribitData.putCallRatio;
    if(Math.abs(_dAdj)>1){
      reasoning.push(`[DERIBIT] ${_deribitData.direction} options: skew=${_deribitData.skew?.toFixed(1)} PCR=${_deribitData.putCallRatio?.toFixed(2)} (${_dAdj>=0?'+':''}${_dAdj.toFixed(1)})`);
    }
  }else{
    rawSignalScores.deribitOptions=null;
  }

  // V10.7.73 — LIQUIDATION + CB PREMIUM SIGNAL
  //   Combines: Bybit large liquidation cascades, Binance funding extreme, CB/KR premium
  //   Long liquidation cascade → capitulation → bullish reversal
  //   Short squeeze completing → mean reversion down → bearish
  //   CB premium vs Kraken → US retail direction
  //   Max ±8pt. Fires when signal magnitude > 1.
  const _liqData=params.liqSignal;
  if(_liqData?.ok&&Number.isFinite(_liqData.signal)&&Math.abs(_liqData.signal)>1){
    const _lAdj=Math.max(-8,Math.min(8,_liqData.signal));
    totalScore+=_lAdj;
    rawSignalScores.liqSignal=Math.round(_lAdj*10)/10;
    rawSignalScores._liqReasons=(_liqData.reasons||[]).join(';');
    if(Math.abs(_lAdj)>1.5){
      reasoning.push(`[LIQ] ${_liqData.direction}: ${(_liqData.reasons||[]).slice(0,2).join(', ')} (${_lAdj>=0?'+':''}${_lAdj.toFixed(1)})`);
    }
  }else{
    rawSignalScores.liqSignal=null;
  }

  // V10.7.76 — LIQUIDATION HEATMAP + OI SIGNAL
  //   Two sub-signals combined:
  //   1. Liquidation gravity: large order walls near price create magnetic pull.
  //      Market makers hunt liquidation clusters — price moves toward them reliably.
  //      Wall above = price pulled UP. Wall below = price pulled DOWN.
  //   2. OI change: rising OI = real accumulation (move holds).
  //      Falling OI = liquidation-driven noise (move fades).
  //   Combined max ±10pt. This is the most mechanically reliable signal —
  //   liquidation hunts are not probabilistic, they're structural.
  const _heatmapData=params.liqHeatmap;
  if(_heatmapData?.ok&&Number.isFinite(_heatmapData.combinedSignal)&&Math.abs(_heatmapData.combinedSignal)>1){
    // V10.7.91: liqHeatmap dampened to 20% of original.
    //   Empirical data (1,819 trades): win avg liqHeatmap +4.6 vs loss avg +4.7 — delta -0.1.
    //   The signal is slightly anti-predictive and adding noise. Keeping it at 20% for
    //   the rare case it's genuinely useful (strong OI walls) while removing its noise impact.
    const _hmAdj=Math.max(-10,Math.min(10,_heatmapData.combinedSignal*0.20));
    totalScore+=_hmAdj;
    rawSignalScores.liqHeatmap=Math.round(_hmAdj*10)/10;
    rawSignalScores._liqGravity=_heatmapData.liquidationGravity;
    rawSignalScores._oiSignal=_heatmapData.oiSignal;
    rawSignalScores._nearestWall=_heatmapData.nearestWallSide?`${_heatmapData.nearestWallSide}@${_heatmapData.nearestWallDistBps}bps($${_heatmapData.nearestWallSize}M)`:null;
    if(Math.abs(_hmAdj)>2){
      const _wallNote=_heatmapData.nearestWallSide?` wall ${_heatmapData.nearestWallSide} ${_heatmapData.nearestWallDistBps}bps $${_heatmapData.nearestWallSize}M`:'';
      reasoning.push(`[LIQ-HEATMAP] ${_heatmapData.combinedDirection} gravity=${_heatmapData.liquidationGravity} OI=${_heatmapData.oiSignal}${_wallNote} (${_hmAdj>=0?'+':''}${_hmAdj.toFixed(1)}) [×0.20 V10.7.91]`);
    }
  }else{
    rawSignalScores.liqHeatmap=null;
  }
  const fundingAccel=(funding-fundingPrev);
  // V114: Funding Extremes Contrarian Signal
  // Very crowded longs (funding >0.05%) often precede DOWN moves
  // Very crowded shorts (funding <-0.02%) often precede UP moves
  const fundingPct=funding*100;
  let fundingExtremeAdj=0;
  if(fundingPct>0.05){
    fundingExtremeAdj=-12; // shift posterior toward DOWN
    reasoning.push(`[FUND-EXT] Crowded longs (${fundingPct.toFixed(3)}%) — DOWN bias`);
  } else if(fundingPct<-0.02){
    fundingExtremeAdj=+12; // shift posterior toward UP
    reasoning.push(`[FUND-EXT] Crowded shorts (${fundingPct.toFixed(3)}%) — UP bias`);
  }
  totalScore+=fundingExtremeAdj;
  // V135: Price-action veto on SQUEEZE regimes.
  //       The bug: SHORT SQUEEZE was firing during obvious downtrends (funding+whales tripped it
  //       even though price was falling), giving UP a +43 regime bonus that locked bad UP trades.
  //       Require recent drift to not contradict the squeeze direction.
  // V142: SS/LS regime bias removed. The +43 / -43 score was double-counting the underlying
  //       whale-flow + funding signals that already feed into the score. SS/LS are descriptive
  //       labels for "retail short + whales buying" or vice versa — they don't add new information
  //       beyond the signals they're built from. Threshold tilts (UP easier in SS, DOWN easier in LS)
  //       are kept as those reflect tactical lock behavior, not score double-counting.
  // V3.2.1: SS thresholds rebalanced based on 57-trade audit. Tara called UP 27 times vs
  //         DOWN 1 time in SS — but UP only won 63% in SS, while DOWN-call WR data was hidden
  //         by sample size (n=1). The asymmetry was too aggressive (UP at 68 / DN at 20).
  //         New: UP 72, DN 26. Both directions tightened, but UP more, so DOWN can fire when
  //         signals genuinely warrant it.
  // V10.2.36 — RANGE DETECTION OVERRIDE
  //   Audit finding from 22:59 export: last 30 trades showed only 7.3 bps avg abs
  //   window movement (very small — choppy market), but regime classifier was
  //   labeling 26/30 windows as TRENDING (DOWN or UP) or SHORT SQUEEZE. This
  //   mismatch is the root cause of the 53% WR slump: Tara reads TRENDING and
  //   calibrates strong directional posterior, but market actually chops near
  //   the strike. Override: if recent windows have been quiet (avg abs < 10 bps
  //   over last 10 windows), FORCE regime to RANGE-CHOP regardless of drift1m
  //   signals. This is more aligned with what the market is actually doing.
  //
  //   Detection: params.recentWindowMovesBps is an array of |close-strike|/strike
  //   in bps from the N most recent resolved windows (caller computes this).
  //   When ≥ 5 entries available AND mean abs < 10 bps, override fires.
  //
  //   When override fires, regime='RANGE-CHOP' and we use RANGE-CHOP thresholds.
  //   No other downstream changes — the rest of the signal scoring runs normally,
  //   but the regime calibration (which is the biggest driver) flips to chop mode.
  let _rangeOverrideFired=false;
  let _rangeOverrideAvgBps=null;
  const _recentMoves=Array.isArray(params.recentWindowMovesBps)?params.recentWindowMovesBps:null;
  if(_recentMoves&&_recentMoves.length>=5){
    const _absMoves=_recentMoves.filter(b=>Number.isFinite(b)).map(b=>Math.abs(b));
    if(_absMoves.length>=5){
      _rangeOverrideAvgBps=_absMoves.reduce((s,v)=>s+v,0)/_absMoves.length;
      if(_rangeOverrideAvgBps<10){
        _rangeOverrideFired=true;
        regime='RANGE-CHOP';
        upThreshold=68;downThreshold=32; // RANGE-CHOP thresholds
        reasoning.push(`[V10.2.36 RANGE-OVERRIDE] last ${_absMoves.length} windows avg ${_rangeOverrideAvgBps.toFixed(1)}bps → forced RANGE-CHOP regime`);
      }
    }
  }
  // V10.4.3 — MULTI-WINDOW DIRECTIONAL CONTEXT — MOVED BELOW (posterior not yet declared here)
  let _v104_3_windowCtx=null;
  // V10.2.37 — REALIZED-VS-EXPECTED MOVE DETECTION (Option 2).
  //   Within the CURRENT window, compute how much price has moved so far
  //   vs how much we'd expect given ATR and elapsed time. If realized move
  //   is significantly LESS than expected, force RANGE-CHOP — the window
  //   is quiet despite whatever drift1m says.
  //
  //   Math:
  //     elapsed_fraction = clockSeconds / 900 (fraction of 15min elapsed)
  //     expected_move_bps = atrBps × elapsed_fraction
  //     realized_move_bps = |currentPrice - windowOpenPrice| / windowOpenPrice × 10000
  //     ratio = realized / expected
  //
  //   Thresholds:
  //     ratio < 0.4 + at least 3 min into window → FORCE RANGE-CHOP (quiet)
  //   Why 0.4: if we're at 60% of expected, that's normal variance. 40% means
  //     the window is genuinely under-moving. Threshold tuned conservatively.
  //   Why 3 min: very early windows haven't had time to move; ratio noise is high.
  //
  //   This stacks with V10.2.36 — both can fire independently. V10.2.36 reads
  //   PRIOR windows; V10.2.37 reads CURRENT window. Either way → force chop.
  let _quietOverrideFired=false;
  let _quietOverrideRatio=null;
  let _quietRealizedBps=null;
  let _quietExpectedBps=null;
  if(!_rangeOverrideFired){ // skip if V10.2.36 already forced chop
    const _quiet=quietWindowEvidence({clockSeconds,is15m,atrBps,windowOpenPrice,currentPrice});
    if(_quiet){
      _quietExpectedBps=_quiet.expected;
      _quietRealizedBps=_quiet.realized;
      if(_quietExpectedBps>0){
        _quietOverrideRatio=_quietRealizedBps/_quietExpectedBps;
        if(_quietOverrideRatio<0.4){
          _quietOverrideFired=true;
          regime='RANGE-CHOP';
          upThreshold=68;downThreshold=32;
          reasoning.push(`[V10.2.37 QUIET-OVERRIDE] realized ${_quietRealizedBps.toFixed(1)}bps vs expected ${_quietExpectedBps.toFixed(1)}bps (ratio ${_quietOverrideRatio.toFixed(2)}) → forced RANGE-CHOP within-window`);
        }
      }
    }
  }
  // V10.2.38 — S/R TOUCH RANGE DETECTION (Option 3 of regime improvements).
  //   Looks at the last 20 minutes of 1m candles. Identifies the range (high/low).
  //   Counts how many candles "touched" each extreme (within ±3 bps).
  //   When BOTH extremes have ≥ 2 touches AND total range is contained (< 35 bps),
  //   we have a confirmed S/R range — price respects these levels.
  //
  //   This is more reliable than statistical range detection (V10.2.36, V10.2.37)
  //   because it confirms LEVELS that price has tested and bounced from. Useful
  //   for the auto-exec patient entry to find genuine mean-reversion opportunities.
  //
  //   Touch tolerance ±3 bps means candle high within 3 bps of range high counts as
  //   a touch. At BTC ~$80K, 3 bps = $24, so this catches near-touches not just
  //   exact prints. Requires ≥ 2 touches each side to filter out single-spike wicks.
  //
  //   Range size cap 35 bps: anything wider isn't really a range anymore (one-sided
  //   trend with a single counter-spike). At $80K, 35 bps = $280 — that's the upper
  //   bound for what we'd call a 20-min range.
  let _srRangeDetected=false;
  let _srRangeBps=null;
  let _srTouchesHigh=0;
  let _srTouchesLow=0;
  let _srRangeHigh=null;
  let _srRangeLow=null;
  if(!_rangeOverrideFired&&!_quietOverrideFired){
    const _oneMinCandles=tfCandles?.c1m||[];
    if(_oneMinCandles.length>=20&&currentPrice>0){
      // c1m[0] is most recent; take first 20 = last 20 minutes
      const _recent=_oneMinCandles.slice(0,20);
      const _highs=_recent.map(c=>Number(c?.h)).filter(v=>Number.isFinite(v)&&v>0);
      const _lows=_recent.map(c=>Number(c?.l)).filter(v=>Number.isFinite(v)&&v>0);
      if(_highs.length>=15&&_lows.length>=15){ // require sufficient data
        _srRangeHigh=Math.max(..._highs);
        _srRangeLow=Math.min(..._lows);
        if(_srRangeHigh>_srRangeLow){
          _srRangeBps=((_srRangeHigh-_srRangeLow)/currentPrice)*10000;
          // Count touches within ±3 bps of each extreme
          const _touchTolPrice=currentPrice*(3/10000);
          _srTouchesHigh=_highs.filter(h=>Math.abs(h-_srRangeHigh)<=_touchTolPrice).length;
          _srTouchesLow=_lows.filter(l=>Math.abs(l-_srRangeLow)<=_touchTolPrice).length;
          // Range detection: both ends touched ≥ 2 times AND total range < 35 bps
          if(_srTouchesHigh>=2&&_srTouchesLow>=2&&_srRangeBps<35){
            _srRangeDetected=true;
            regime='RANGE-CHOP';
            upThreshold=68;downThreshold=32;
            reasoning.push(`[V10.2.38 S/R-RANGE] ${_srTouchesHigh}× high touches @${_srRangeHigh.toFixed(1)}, ${_srTouchesLow}× low touches @${_srRangeLow.toFixed(1)}, range ${_srRangeBps.toFixed(1)}bps → confirmed range`);
          }
        }
      }
    }
  }
  // V10.2.39 — HTF TREND FILTER (Option 4 of regime improvements).
  //   Computes 1h and 4h drift from the c15m candle stack and uses them to
  //   confirm OR veto the 15m regime decision.
  //
  //   Sources:
  //     1h drift = (currentPrice - close of c15m[3]) / close × 10000 bps
  //                (4 candles × 15min = 60min lookback)
  //     4h drift = (currentPrice - close of c15m[15]) / close × 10000 bps
  //                (16 candles × 15min = 240min lookback)
  //
  //   Classification thresholds (calibrated for BTC at ~$80K, normal volatility):
  //     |1h drift| < 15 bps → 1h FLAT
  //     |4h drift| < 40 bps → 4h FLAT
  //     above threshold → UP/DOWN
  //
  //   Two HTF effects fire in different places:
  //     (a) PRE-CLASSIFIER OVERRIDE: if BOTH 1h and 4h are FLAT, force RANGE-CHOP
  //         and skip the classifier ladder entirely. Strong signal for chop.
  //     (b) POST-CLASSIFIER VETO: if the classifier picks TRENDING UP/DOWN but
  //         the HTF context disagrees (4h is opposite direction or flat), veto
  //         the trending label back to RANGE-CHOP. Prevents 15m fake-trends in
  //         a HTF range structure.
  //
  //   Why this matters: most 15m windows are noise within a HTF context.
  //   A 15m TRENDING DOWN inside a 4h flat range is usually a counter-trend
  //   bounce that mean-reverts. Better to skip those trades.
  let _htfFlatOverride=false;
  let _htfVetoFired=false;
  let _htf1hDriftBps=null;
  let _htf4hDriftBps=null;
  let _htf1hLabel=null;
  let _htf4hLabel=null;
  try{
    const _c15m=tfCandles?.c15m||[];
    if(_c15m.length>=4&&currentPrice>0){
      const _p1hAgo=Number(_c15m[3]?.c)||0;
      if(_p1hAgo>0){
        _htf1hDriftBps=((currentPrice-_p1hAgo)/_p1hAgo)*10000;
        _htf1hLabel=Math.abs(_htf1hDriftBps)<15?'FLAT':(_htf1hDriftBps>0?'UP':'DOWN');
      }
      if(_c15m.length>=16){
        const _p4hAgo=Number(_c15m[15]?.c)||0;
        if(_p4hAgo>0){
          _htf4hDriftBps=((currentPrice-_p4hAgo)/_p4hAgo)*10000;
          _htf4hLabel=Math.abs(_htf4hDriftBps)<40?'FLAT':(_htf4hDriftBps>0?'UP':'DOWN');
        }
      }
    }
  }catch(_){}
  // (a) PRE-CLASSIFIER OVERRIDE: both HTFs flat → force RANGE-CHOP
  if(!_rangeOverrideFired&&!_quietOverrideFired&&!_srRangeDetected&&
     _htf1hLabel==='FLAT'&&_htf4hLabel==='FLAT'){
    _htfFlatOverride=true;
    regime='RANGE-CHOP';
    upThreshold=68;downThreshold=32;
    reasoning.push(`[V10.2.39 HTF-FLAT] 1h ${_htf1hDriftBps.toFixed(1)}bps + 4h ${_htf4hDriftBps.toFixed(1)}bps both FLAT → forced RANGE-CHOP`);
  }
  // Only run the normal regime ladder if NO override fired — otherwise we'd
  // immediately overwrite our RANGE-CHOP assignment.
  // V10.2.39 — extended gate to include all four overrides:
  //   _rangeOverrideFired (V10.2.36 — historical 15-window quiet)
  //   _quietOverrideFired (V10.2.37 — current-window realized<<expected)
  //   _srRangeDetected    (V10.2.38 — S/R level touches confirm range)
  //   _htfFlatOverride    (V10.2.39 — both 1h+4h HTFs flat)
  if(!_rangeOverrideFired&&!_quietOverrideFired&&!_srRangeDetected&&!_htfFlatOverride){
  if(retailShorting&&whalesBuying&&drift1m>-3){regime='SHORT SQUEEZE';upThreshold=72;downThreshold=26;}
  else if(retailLonging&&whalesSelling&&drift1m<3){regime='LONG SQUEEZE';upThreshold=80;downThreshold=36;}
  else if(retailShorting&&whalesBuying&&drift1m<=-3){
    // V135: SS conditions met but price contradicting — treat as CHOP, log the suppression
    reasoning.push(`[REGIME-VETO] SHORT SQUEEZE conditions but drift1m=${drift1m.toFixed(1)} → suppressed`);
  }
  else if(retailLonging&&whalesSelling&&drift1m>=3){
    reasoning.push(`[REGIME-VETO] LONG SQUEEZE conditions but drift1m=${drift1m.toFixed(1)} → suppressed`);
  }
  else if(isCleanUp){regime='TRENDING UP';upThreshold=64;downThreshold=20;}
  // V3.2.1: TD UP threshold 80→85. Tara called UP 2x in TRENDING DOWN, won 1 of 2.
  //         Should be very expensive to call UP in a downtrending regime.
  else if(isCleanDn){regime='TRENDING DOWN';upThreshold=85;downThreshold=20;}
  // V141 ROOT FIX: regime variable thresholds restored to symmetric form.
  //   TU: upT 64 (easier UP — favored), dnT 20 (harder DOWN — opposed)
  //   TD: upT 80 (harder UP — opposed), dnT 20 (easier DOWN — favored, MIRROR of TU's UP)
  //   Was TD upT75/dnT36 — the 36 was harder than the default 28 floor, in the regime that's
  //   supposed to favor DOWN. Backwards. Now TD's preferred direction (DOWN) gets the easier
  //   threshold mirroring TU's preferred direction (UP).
  //   Note: these regime-set values are dead variables in V140 — the LOCK_THRESHOLD_DN_EFFECTIVE
  //   ladder downstream ignores them. We're fixing both layers in V141.
  else if(isHighVol){regime='HIGH VOL CHOP';upThreshold=75;downThreshold=25;reasoning.push(`[REGIME] High vol — strict thresholds`);}
  } // end if(!_rangeOverrideFired && !_quietOverrideFired && !_srRangeDetected && !_htfFlatOverride)
  // (b) V10.2.39 POST-CLASSIFIER HTF VETO: if classifier landed on TRENDING UP/DOWN
  //   but the 4h context disagrees, veto back to RANGE-CHOP. Specifically:
  //     TRENDING UP   but 4h is DOWN or FLAT → veto
  //     TRENDING DOWN but 4h is UP or FLAT → veto
  //   We require 4h disagreement (not 1h) because 1h is too noisy to veto reliably.
  //   This catches the case where 15m signals look strong but the HTF range is intact.
  if(_htf4hLabel!=null&&_htf4hLabel!=='UP'&&regime==='TRENDING UP'){
    _htfVetoFired=true;
    reasoning.push(`[V10.2.39 HTF-VETO] classifier said TRENDING UP but 4h is ${_htf4hLabel} (${_htf4hDriftBps?.toFixed(1)}bps) → veto to RANGE-CHOP`);
    regime='RANGE-CHOP';
    upThreshold=68;downThreshold=32;
  }
  else if(_htf4hLabel!=null&&_htf4hLabel!=='DOWN'&&regime==='TRENDING DOWN'){
    _htfVetoFired=true;
    reasoning.push(`[V10.2.39 HTF-VETO] classifier said TRENDING DOWN but 4h is ${_htf4hLabel} (${_htf4hDriftBps?.toFixed(1)}bps) → veto to RANGE-CHOP`);
    regime='RANGE-CHOP';
    upThreshold=68;downThreshold=32;
  }
  // ═══════════════════════════════════════════════════════════════════════════
  // V10.7.27 — REGIME-GATED STRUCTURE + TECHNICAL
  // ═══════════════════════════════════════════════════════════════════════════
  //   Audit (May 20): Structure accuracy was 47% standalone — but BY REGIME:
  //     RANGE-CHOP:    51.2%  (close to noise)
  //     TRENDING UP:   37.1%  ← actively wrong
  //     TRENDING DOWN: 42.1%  ← actively wrong
  //     SHORT SQUEEZE: 56.3%  ← only regime where it works
  //
  //   Technical accuracy 47.7% standalone — BY REGIME:
  //     RANGE-CHOP:    51.8%  (close to noise)
  //     TRENDING UP:   57.1%  ← only good regime
  //     TRENDING DOWN: 44.9%  ← actively wrong
  //     SHORT SQUEEZE: 43.3%  ← actively wrong
  //
  //   Diagnosis: signals are regime-misaligned.
  //   - Structure (consecutive candles): assumes continuation, fires loudest
  //     in chop where runs reverse → wrong direction
  //   - Technical (RSI/BB/channel): assumes mean-reversion, fires in trends
  //     where extremes continue → wrong direction
  //
  //   Fix: REVERSE the contribution when regime is wrong for that signal.
  //   Both scores were already added to totalScore earlier. We subtract them
  //   back here when the current regime is in the "kill list" for that signal.
  //
  //   V10.7.33 UPDATE — Structure restored in RANGE-CHOP based on:
  //     1) User intuition: had wins on V10.7.25 era when Structure was active in chop
  //     2) Data: 51% accuracy in chop is essentially neutral (not actively wrong)
  //     3) Marginal points may help borderline calls land correctly
  //   Still removed in TRENDING UP/DOWN where data was clear (37-42% wrong).
  //
  //   Keep ON in:
  //     Structure: SHORT SQUEEZE (56%), RANGE-CHOP (51%, restored V10.7.33)
  //     Technical: TRENDING UP (57%), RANGE-CHOP (52%)
  //   Subtract in:
  //     Structure: TRENDING UP/DOWN (37-42% wrong), HIGH VOL CHOP
  //     Technical: TRENDING DOWN (45% wrong), SHORT SQUEEZE (43% wrong), HIGH VOL CHOP
  const _structAddedScore=Number(rawSignalScores.structure)||0;
  const _techAddedScore=Number(rawSignalScores.technical)||0;
  const _structOkRegimes=new Set(['SHORT SQUEEZE','RANGE-CHOP']); // V10.7.33: added RANGE-CHOP
  const _techOkRegimes=new Set(['TRENDING UP','RANGE-CHOP']);
  if(!_structOkRegimes.has(regime)&&Math.abs(_structAddedScore)>=1){
    totalScore-=_structAddedScore; // remove the contribution from totalScore
    reasoning.push(`[V10.7.27] Structure ${_structAddedScore>0?'+':''}${_structAddedScore.toFixed(0)} REVERSED in ${regime} (signal was 37-42% accurate in this regime, removed from posterior)`);
    rawSignalScores.structure=0; // hide from concrete signal aggregator (V10.7.12)
  }
  if(!_techOkRegimes.has(regime)&&Math.abs(_techAddedScore)>=1){
    totalScore-=_techAddedScore;
    reasoning.push(`[V10.7.27] Technical ${_techAddedScore>0?'+':''}${_techAddedScore.toFixed(0)} REVERSED in ${regime} (signal was 43-45% accurate in this regime, removed from posterior)`);
    rawSignalScores.technical=0;
  }

  // ── V10.7.42 — REGIME-GATED FLOW/DELTA RE-CLAMP (SYMMETRIC) ─────────────
  // Runs AFTER regime is finalized. Reduces Flow cap to ±20 in RANGE-CHOP and
  //   boosts Delta cap to ±18 in RANGE-CHOP. Symmetric — applies identically
  //   to UP/DOWN scores. Outside chop, all caps unchanged.
  //
  // Data: 7-day audit showed Flow dominating posterior in chop with 5-15pp WR
  //   gap between UP/DOWN calls. Bias was driven by signal weight not direction.
  if(regime==='RANGE-CHOP'){
    // Re-clamp Flow to ±20 (was ±W.flow which is ~35)
    const _flowOriginal=Number(rawSignalScores.flow)||0;
    const _flowNewCap=20;
    if(Math.abs(_flowOriginal)>_flowNewCap){
      const _flowReclamped=Math.sign(_flowOriginal)*_flowNewCap;
      const _flowDelta=_flowReclamped-_flowOriginal;
      totalScore+=_flowDelta; // subtract the overage symmetrically
      rawSignalScores.flow=_flowReclamped;
      rawSignalScores._flowCap=_flowNewCap;
      rawSignalScores._flowReclamp=_flowDelta;
      reasoning.push(`[V10.7.42] Flow re-clamped ${_flowOriginal.toFixed(0)} → ${_flowReclamped.toFixed(0)} (chop cap)`);
    }else{
      rawSignalScores._flowCap=_flowNewCap;
    }
    // Re-clamp Delta to ±18 (was ±13) — boost since Flow was reduced
    const _deltaOriginal=Number(rawSignalScores.delta)||0;
    const _tsdRaw=Number(rawSignalScores._tsdScoreRaw)||0;
    const _deltaNewCap=18;
    if(_tsdRaw!==0&&Math.abs(_tsdRaw)>13){
      // Re-clamp using raw tsd score (was clamped to 13 earlier)
      const _deltaReclamped=Math.max(-_deltaNewCap,Math.min(_deltaNewCap,_tsdRaw));
      const _deltaDelta=_deltaReclamped-_deltaOriginal;
      totalScore+=_deltaDelta;
      rawSignalScores.delta=_deltaReclamped;
      rawSignalScores._deltaCap=_deltaNewCap;
      rawSignalScores._deltaReclamp=_deltaDelta;
      if(_deltaDelta!==0)reasoning.push(`[V10.7.42] Delta re-clamped ${_deltaOriginal.toFixed(0)} → ${_deltaReclamped.toFixed(0)} (chop boost)`);
    }else{
      rawSignalScores._deltaCap=_deltaNewCap;
    }
  }
  // V10.2.40 — PROBABILISTIC REGIME (Option 5 of regime improvements — conservative).
  //   Compute soft probability scores over all 6 regimes from raw signals.
  //   Use the resulting distribution for TWO things:
  //
  //     (a) AUDIT: store on engine return so we can analyze regime confidence
  //         post-hoc. "When confidence was high, did Tara win more?"
  //
  //     (b) LOW-CONFIDENCE DAMPENING: if no regime scored > 45% probability,
  //         the classifier picked a weak winner. Dampen thresholds toward
  //         neutral (68/32) to reduce false-directional locks.
  //
  //   This is a CONSERVATIVE implementation. It does NOT replace the existing
  //   classifier (the primary `regime` string remains the hard classifier output).
  //   It does NOT touch regimeWeights, regimeDirCalibration, or qScoreV2 — those
  //   continue to use the primary regime as before. A full probabilistic system
  //   (blending all downstream lookups by probability) is queued for V10.3.0.
  //
  //   Scoring rationale per regime:
  //     RANGE-CHOP gets BASE points and accumulates more from each override that
  //       fired (V10.2.36/37/38/39), plus small drift/ATR for "objectively quiet"
  //     TRENDING UP/DOWN accumulate from drift alignment across 1m/5m/15m + HTF
  //     SHORT/LONG SQUEEZE accumulate from funding+whale combinations
  //     HIGH VOL CHOP from ATR magnitude alone
  //
  //   Final probabilities = scores / sum(scores). Always well-formed (sums to 1).
  const _regimeScores={
    'RANGE-CHOP':1.0,           // base: chop is the default
    'TRENDING UP':0,
    'TRENDING DOWN':0,
    'SHORT SQUEEZE':0,
    'LONG SQUEEZE':0,
    'HIGH VOL CHOP':0,
  };
  // RANGE-CHOP accumulators
  if(_rangeOverrideFired)_regimeScores['RANGE-CHOP']+=2.5;
  if(_quietOverrideFired)_regimeScores['RANGE-CHOP']+=2.0;
  if(_srRangeDetected)_regimeScores['RANGE-CHOP']+=2.0;
  if(_htfFlatOverride)_regimeScores['RANGE-CHOP']+=2.0;
  if(_htfVetoFired)_regimeScores['RANGE-CHOP']+=1.5;
  if(Math.abs(drift1m)<3)_regimeScores['RANGE-CHOP']+=0.5;
  if(atrBps<15)_regimeScores['RANGE-CHOP']+=0.5;
  if(_htf1hLabel==='FLAT')_regimeScores['RANGE-CHOP']+=0.5;
  if(_htf4hLabel==='FLAT')_regimeScores['RANGE-CHOP']+=1.0;
  // TRENDING UP accumulators
  if(drift1m>3)_regimeScores['TRENDING UP']+=1.0;
  if(drift5m>5)_regimeScores['TRENDING UP']+=1.0;
  if(drift15m>8)_regimeScores['TRENDING UP']+=1.0;
  if(whalesBuyingMild)_regimeScores['TRENDING UP']+=0.5;
  if(_htf1hLabel==='UP')_regimeScores['TRENDING UP']+=1.0;
  if(_htf4hLabel==='UP')_regimeScores['TRENDING UP']+=1.5;
  // TRENDING DOWN accumulators (mirror)
  if(drift1m<-3)_regimeScores['TRENDING DOWN']+=1.0;
  if(drift5m<-5)_regimeScores['TRENDING DOWN']+=1.0;
  if(drift15m<-8)_regimeScores['TRENDING DOWN']+=1.0;
  if(whalesSellingMild)_regimeScores['TRENDING DOWN']+=0.5;
  if(_htf1hLabel==='DOWN')_regimeScores['TRENDING DOWN']+=1.0;
  if(_htf4hLabel==='DOWN')_regimeScores['TRENDING DOWN']+=1.5;
  // SHORT SQUEEZE accumulators
  if(retailShorting&&whalesBuying)_regimeScores['SHORT SQUEEZE']+=2.0;
  if(retailShorting&&whalesBuying&&drift1m>-3)_regimeScores['SHORT SQUEEZE']+=0.5;
  // LONG SQUEEZE accumulators
  if(retailLonging&&whalesSelling)_regimeScores['LONG SQUEEZE']+=2.0;
  if(retailLonging&&whalesSelling&&drift1m<3)_regimeScores['LONG SQUEEZE']+=0.5;
  // HIGH VOL CHOP accumulators
  if(isHighVol)_regimeScores['HIGH VOL CHOP']+=3.0;
  // Normalize to probabilities
  let _regimeProbs={};
  const _scoreSum=Object.values(_regimeScores).reduce((s,v)=>s+v,0);
  if(_scoreSum>0){
    Object.entries(_regimeScores).forEach(([k,v])=>{_regimeProbs[k]=v/_scoreSum;});
  }else{
    _regimeProbs={'RANGE-CHOP':1,'TRENDING UP':0,'TRENDING DOWN':0,'SHORT SQUEEZE':0,'LONG SQUEEZE':0,'HIGH VOL CHOP':0};
  }
  // Determine primary regime + confidence
  let _primaryRegime='RANGE-CHOP';
  let _regimeConfidence=0;
  Object.entries(_regimeProbs).forEach(([k,v])=>{
    if(v>_regimeConfidence){_primaryRegime=k;_regimeConfidence=v;}
  });
  // (b) LOW-CONFIDENCE DAMPENING — if no regime won clearly AND the current
  //   classifier picked a directional regime (not chop), dampen thresholds
  //   toward neutral. This is a defense against weak-evidence directional bias.
  let _v10_2_40_dampened=false;
  if(_regimeConfidence<0.45&&regime!=='RANGE-CHOP'&&regime!=='HIGH VOL CHOP'){
    const _neutralUp=68,_neutralDn=32;
    const _origUp=upThreshold,_origDn=downThreshold;
    upThreshold=Math.round(upThreshold*0.5+_neutralUp*0.5);
    downThreshold=Math.round(downThreshold*0.5+_neutralDn*0.5);
    _v10_2_40_dampened=true;
    reasoning.push(`[V10.2.40 LOW-CONF] regime confidence ${(_regimeConfidence*100).toFixed(0)}% < 45% → thresholds dampened ${_origUp}/${_origDn} → ${upThreshold}/${downThreshold}`);
  }
  // ─── V10.2.43 PHASE 3 — REGIME-AWARE STRUCTURE/TECHNICAL CORRECTION ───────
  // Both `structure` (45.0% accuracy in 271 fires) and `technical` (46.9% in
  // 337 fires) were SLIGHTLY INVERSE predictors in the 751-trade audit. Root
  // cause: their logic assumes trend-continuation everywhere.
  //   - structure: 3+ green candles → bullish (continuation). But in CHOP,
  //     3+ green candles often mean price hit the range top and is about to
  //     reverse DOWN, not continue UP.
  //   - technical: RSI 70+ → fade (continuation-mean-rev split based on
  //     realGapBps). In CHOP this is roughly right, but in TRENDING regimes
  //     RSI 70 = continuation, current logic fades and is wrong.
  //
  // The fix: AFTER regime is finalized, retroactively apply a regime-aware
  // multiplier to these two signals.
  //
  // Multipliers:
  //   TRENDING UP/DOWN     → ×1.0  (keep current — continuation-correct)
  //   RANGE-CHOP           → ×-0.7 (INVERT — chop = mean-revert from extremes)
  //   SHORT/LONG SQUEEZE   → ×0.5  (half-weight — mixed dynamics)
  //   HIGH VOL CHOP        → ×0.5  (half-weight — noise dominant)
  //
  // Why -0.7 (partial invert) for chop: full inversion (-1.0) is risky if the
  // regime is misclassified. -0.7 still flips the sign but with reduced
  // magnitude. If chop misclassification still happens, damage is bounded.
  //
  // This runs AFTER all regime overrides (V10.2.36-40) so the `regime` variable
  // is the final, definitive classification. Retroactive math: subtract old
  // contribution from totalScore, add the multiplier-adjusted version. Net delta
  // applied to totalScore. rawSignalScores updated for telemetry.
  const _structOrig=rawSignalScores.structure||0;
  const _techOrig=rawSignalScores.technical||0;
  let _regimeMult=1.0;
  // V10.3.3 — RANGE-BEARISH / RANGE-BULLISH DETECTOR.
  //   Pure RANGE-CHOP gets ×-0.7 inversion because structure/technical fired wrong
  //   in pure chop per the audit. BUT in hybrid range-bearish (chop pattern + strong
  //   bearish drift) or range-bullish, the structure signal IS pointing the right
  //   way — the drift is real. Inverting it fights the right answer.
  //
  //   Detection: Grand Tr projection ≤-100bps (strong DOWN) OR ≥+100bps (strong UP)
  //   while regime is RANGE-CHOP. When detected, skip the inversion (×1.0 instead
  //   of ×-0.7). Other chop dampeners still apply.
  let _v10_3_3_rangeHybrid=null;
  const _gtProj=_grandTrend?.valid?_grandTrend.projectionBps:0;
  const _strongBearishDrift=regime==='RANGE-CHOP'&&_gtProj<=-100;
  const _strongBullishDrift=regime==='RANGE-CHOP'&&_gtProj>=100;
  if(_strongBearishDrift){
    _regimeMult=1.0;
    _v10_3_3_rangeHybrid={type:'range-bearish',gtProj:_gtProj,inversionSkipped:true};
    reasoning.push(`[V10.3.3 RANGE-BEARISH] RANGE-CHOP + Grand Tr ${_gtProj}bps → bearish drift is real, skip ×-0.7 inversion`);
  }else if(_strongBullishDrift){
    _regimeMult=1.0;
    _v10_3_3_rangeHybrid={type:'range-bullish',gtProj:_gtProj,inversionSkipped:true};
    reasoning.push(`[V10.3.3 RANGE-BULLISH] RANGE-CHOP + Grand Tr ${_gtProj}bps → bullish drift is real, skip ×-0.7 inversion`);
  }else if(regime==='RANGE-CHOP'){
    _regimeMult=-0.7; // pure chop — apply inversion
  }
  else if(regime==='SHORT SQUEEZE'||regime==='LONG SQUEEZE')_regimeMult=0.5;
  else if(regime==='HIGH VOL CHOP')_regimeMult=0.5;
  // TRENDING UP and TRENDING DOWN keep ×1.0 (current behavior preserved)
  if(_regimeMult!==1.0){
    const _structNew=Math.round(_structOrig*_regimeMult);
    const _techNew=Math.round(_techOrig*_regimeMult);
    const _structDelta=_structNew-_structOrig;
    const _techDelta=_techNew-_techOrig;
    totalScore+=_structDelta+_techDelta;
    rawSignalScores.structure=_structNew;
    rawSignalScores.technical=_techNew;
    if(_structOrig!==0||_techOrig!==0){
      reasoning.push(`[V10.2.43 REGIME-CORRECT] ${regime} × ${_regimeMult.toFixed(2)} → struct ${_structOrig>=0?'+':''}${_structOrig}→${_structNew>=0?'+':''}${_structNew} tech ${_techOrig>=0?'+':''}${_techOrig}→${_techNew>=0?'+':''}${_techNew} (Δ ${_structDelta+_techDelta>=0?'+':''}${_structDelta+_techDelta})`);
    }
  }
  // ─── V10.2.50 — CHOP AMPLIFICATION ────────────────────────────────────────
  // Problem: in RANGE-CHOP regime, primary signals (gap/momentum/flow) push
  // trend-direction and cancel each other out, leaving posterior near 50%.
  // Time-cap then forces marginal commits that lose. The CHOP-NATIVE signals
  // (meanReversion, rangePosition, reversal) fire infrequently and at modest
  // magnitude — they need more weight in their native regime.
  //
  // Approach: when regime is RANGE-CHOP, amplify these signals by 1.5×
  // retroactively. Only in chop. Other regimes unchanged.
  //
  // Why retroactive: keeps signal computation regime-agnostic; the amplification
  // is a regime-specific application layer, mirroring the V10.2.43 pattern.
  //
  // Risk: if chop is misclassified (false chop label on a real trend), these
  // contrarian signals will push WRONG direction. The 1.5× is modest enough
  // that one misfire isn't catastrophic; multiple chop indicators (V10.2.36-39)
  // would all have to be wrong simultaneously.
  let _v10_2_50_chopAmpDelta=0;
  if(regime==='RANGE-CHOP'){
    const _chopAmp=1.5;
    const _mrOrig=rawSignalScores.meanReversion||0;
    const _rpOrig=rawSignalScores.rangePosition||0;
    const _revOrig=rawSignalScores.reversal||0;
    // Only amplify if these signals actually fired
    if(_mrOrig!==0||_rpOrig!==0||_revOrig!==0){
      const _mrNew=Math.round(_mrOrig*_chopAmp);
      const _rpNew=Math.round(_rpOrig*_chopAmp);
      const _revNew=Math.round(_revOrig*_chopAmp);
      const _delta=(_mrNew-_mrOrig)+(_rpNew-_rpOrig)+(_revNew-_revOrig);
      totalScore+=_delta;
      rawSignalScores.meanReversion=_mrNew;
      rawSignalScores.rangePosition=_rpNew;
      rawSignalScores.reversal=_revNew;
      _v10_2_50_chopAmpDelta=_delta;
      reasoning.push(`[V10.2.50 CHOP-AMP] RANGE-CHOP × 1.5 → meanRev ${_mrOrig}→${_mrNew} rangePos ${_rpOrig}→${_rpNew} reversal ${_revOrig}→${_revNew} (Δ ${_delta>=0?'+':''}${_delta})`);
    }
  }
  // V10.2.50 HTF-CHOP-UNCAP is applied inline at the htfPatterns cap site below
  // (around line 8735) — moved there because _htfPatterns isn't declared yet at
  // this point in the engine flow. The _v10_2_50_htfChopBoost telemetry is set
  // at that location.
  let _v10_2_50_htfChopBoost=null;
  // ─── V10.2.51 — CHOP CONFLUENCE THRESHOLD DROP ────────────────────────────
  // Problem: in RANGE-CHOP, foundation signals (gap/momentum/flow) push trend
  // direction and cancel. Chop-native signals (meanReversion/rangePosition/
  // reversal) fire but their amplified magnitude (~9-12) isn't enough to push
  // past the lock threshold (~75/26). Result: posterior lands ~50%, no lock
  // until time-cap forces a marginal commit.
  //
  // Fix: when ≥2 chop-native signals fire in the SAME direction with magnitude
  // ≥5 each, treat that as a confluent chop-fade signal and lower the lock
  // threshold for that direction by 15 pts. The chop-native signals are
  // accurate predictors in chop per the audit — they just need a path to fire
  // locks without competing against canceling trend signals.
  //
  // Direction encoding:
  //   - positive score → UP-pushing signal
  //   - negative score → DOWN-pushing signal
  //   - doubleTop pattern → DOWN bias (price tested top, rejected)
  //   - doubleBottom pattern → UP bias (price tested bottom, rejected)
  //
  // For UP lock easier: lower upThreshold (need less to fire)
  // For DOWN lock easier: raise downThreshold (allow higher posterior to fire DOWN)
  let _v10_2_51_chopConfluence=null;
  if(regime==='RANGE-CHOP'){
    const _mr=rawSignalScores.meanReversion||0;
    const _rp=rawSignalScores.rangePosition||0;
    const _rev=rawSignalScores.reversal||0;
    // Note: _htfPatterns isn't available yet here, so we check the detail
    // string via a deferred check. For V10.2.51, only use mr/rp/rev — doubleTop/
    // doubleBottom contribution flows through patternAdj which adds to totalScore
    // and already moves posterior in the right direction. The threshold drop here
    // is independent — based purely on the 3 chop-native signal counts.
    const _chopSignals=[
      {name:'meanReversion',score:_mr},
      {name:'rangePosition',score:_rp},
      {name:'reversal',score:_rev},
    ];
    const _upSignals=_chopSignals.filter(s=>s.score>=5);
    const _dnSignals=_chopSignals.filter(s=>s.score<=-5);
    if(_upSignals.length>=2&&_dnSignals.length===0){
      const _origUp=upThreshold;
      upThreshold=Math.max(50,upThreshold-15);
      _v10_2_51_chopConfluence={dir:'UP',count:_upSignals.length,origThr:_origUp,newThr:upThreshold,signals:_upSignals.map(s=>`${s.name}+${s.score}`).join(', ')};
      reasoning.push(`[V10.2.51 CHOP-CONFLUENCE] ${_upSignals.length} chop signals → UP (${_v10_2_51_chopConfluence.signals}) → upThreshold ${_origUp}→${upThreshold}`);
    }else if(_dnSignals.length>=2&&_upSignals.length===0){
      const _origDn=downThreshold;
      downThreshold=Math.min(50,downThreshold+15);
      _v10_2_51_chopConfluence={dir:'DOWN',count:_dnSignals.length,origThr:_origDn,newThr:downThreshold,signals:_dnSignals.map(s=>`${s.name}${s.score}`).join(', ')};
      reasoning.push(`[V10.2.51 CHOP-CONFLUENCE] ${_dnSignals.length} chop signals → DOWN (${_v10_2_51_chopConfluence.signals}) → downThreshold ${_origDn}→${downThreshold}`);
    }
  }
  // ─── V10.3.0 — RANGE-FADE MODULE ──────────────────────────────────────────
  // Goal: actually win chop, not just sit it out. Built on the empirical truth
  // that chop is range-bound — price oscillates between identifiable high/low
  // boundaries. The edge in chop is fading rejections at boundaries.
  //
  // Architecture:
  //   1. Range identification: rangeHigh / rangeLow from last 30×1m candles
  //      (95th/5th percentile to filter spike wicks, not raw max/min).
  //   2. Position-in-range (PIR): 0=at low, 100=at high.
  //   3. Range health: % of candles whose CLOSE was inside [rangeLow, rangeHigh].
  //      <70% = range is breaking, don't apply chop-fade logic.
  //   4. Rejection detection: last 5 candles, looking for wick ≥ 1.5× body that
  //      pierced a boundary and closed back inside.
  //   5. Fade signal: PIR ≥80 + top rejection + healthy range → push DOWN.
  //                   PIR ≤20 + bottom rejection + healthy range → push UP.
  //   6. Mid-range flag (PIR 30-70): used downstream to force sit-out in chop.
  //
  // Only active in RANGE-CHOP regime. Other regimes unchanged.
  let _v10_3_0_rangeData=null;
  let _v10_3_0_rangeFadeFired=null;
  let _v10_3_0_midRangeSitout=false;
  let _v10_3_0_rangeBroken=false;
  if(regime==='RANGE-CHOP'){
    const _c1m=tfCandles?.c1m||[];
    const _LOOKBACK=30; // 30 minutes
    if(_c1m.length>=20){
      const _recent=_c1m.slice(-_LOOKBACK);
      // 95th/5th percentile to filter outlier spike wicks
      const _highsSorted=_recent.map(c=>c.h).sort((a,b)=>a-b);
      const _lowsSorted=_recent.map(c=>c.l).sort((a,b)=>a-b);
      const _p95idx=Math.floor(_highsSorted.length*0.95);
      const _p05idx=Math.floor(_lowsSorted.length*0.05);
      const _rangeHigh=_highsSorted[_p95idx];
      const _rangeLow=_lowsSorted[_p05idx];
      const _rangeSize=_rangeHigh-_rangeLow;
      if(_rangeSize>0&&currentPrice>0){
        const _pir=Math.max(0,Math.min(100,((currentPrice-_rangeLow)/_rangeSize)*100));
        // Range health: % of candles closed inside [rangeLow, rangeHigh]
        const _closedInside=_recent.filter(c=>c.c>=_rangeLow&&c.c<=_rangeHigh).length;
        const _rangeHealth=(_closedInside/_recent.length)*100;
        // Rejection candle detection on last 5 candles
        const _last5=_recent.slice(-5);
        let _topRejection=0;
        let _bottomRejection=0;
        let _topRejStrength=0;
        let _botRejStrength=0;
        _last5.forEach(c=>{
          const _body=Math.abs(c.c-c.o);
          if(_body<0.01)return; // skip doji
          const _upperWick=c.h-Math.max(c.c,c.o);
          const _lowerWick=Math.min(c.c,c.o)-c.l;
          // Top rejection: candle high touched/exceeded rangeHigh AND closed back inside AND upper wick is dominant
          const _highTouched=c.h>=_rangeHigh*0.9995;
          const _closedBack=c.c<_rangeHigh;
          const _wickDom=_upperWick>=1.5*_body;
          if(_highTouched&&_closedBack&&_wickDom){
            _topRejection++;
            _topRejStrength+=_upperWick/_body;
          }
          // Bottom rejection: mirror
          const _lowTouched=c.l<=_rangeLow*1.0005;
          const _closedAbove=c.c>_rangeLow;
          const _lowWickDom=_lowerWick>=1.5*_body;
          if(_lowTouched&&_closedAbove&&_lowWickDom){
            _bottomRejection++;
            _botRejStrength+=_lowerWick/_body;
          }
        });
        _v10_3_0_rangeData={
          rangeHigh:Math.round(_rangeHigh),
          rangeLow:Math.round(_rangeLow),
          rangeSize:Math.round(_rangeSize),
          rangeBps:Math.round((_rangeSize/currentPrice)*10000),
          pir:Math.round(_pir),
          rangeHealth:Math.round(_rangeHealth),
          topRejection:_topRejection,
          bottomRejection:_bottomRejection,
          topRejStrength:_topRejStrength.toFixed(1),
          botRejStrength:_botRejStrength.toFixed(1),
          lookbackBars:_recent.length,
        };
        // Range health check — if breaking down, don't apply fade logic
        if(_rangeHealth<70){
          _v10_3_0_rangeBroken=true;
          reasoning.push(`[V10.3.0 RANGE-BROKEN] health ${Math.round(_rangeHealth)}% < 70% — range integrity weak, skip fade logic`);
        }else{
          // V10.3.3 — SMOOTH-DRIFT OVERRIDE for range-fade.
          //   Original V10.3.0 required a rejection candle (wick ≥1.5× body) at the
          //   boundary to fire range-fade. Real-world failure: smooth drifts to the
          //   range edge don't produce wick rejections — price just slides up to the
          //   top with no bounce. May 16 02:23 PM showed PIR 100% + Flow -46 + 3-TF
          //   doubleTop confluence sat out because no rejection candle. Actual: DOWN.
          //
          //   Fix: when PIR is extreme (≥95% or ≤5%) AND flow magnitude is strong
          //   (≥30 in the fade direction), fire range-fade without rejection candle.
          //   Strong flow at the range edge IS the rejection signal — the market is
          //   telling us the boundary is being defended.
          const _flowScore=rawSignalScores.flow||0;
          // Range is healthy — apply fade logic
          // V10.3.0 FADE: only at PIR extremes with rejection candle
          // V10.3.4: smooth-drift override removed (1W/6L = 14% WR, rejection alone is 90%)
          if(_pir>=80&&_topRejection>=1){
            // Strong DOWN fade signal — push posterior DOWN
            // Magnitude scales with PIR extremity AND rejection strength (or flow)
            const _pirBoost=Math.min(15,(_pir-80)*0.75); // 0 at PIR=80, 15 at PIR=100
            const _rejBoost=Math.min(15,_topRejStrength*4); // V10.3.4: smooth-drift removed, rejection always required
            const _fadeAdj=-Math.round(15+_pirBoost+_rejBoost); // -15 to -45
            totalScore+=_fadeAdj;
            rawSignalScores.rangeFade=_fadeAdj;
            const _trigger='rejection';
            _v10_3_0_rangeFadeFired={dir:'DOWN',magnitude:_fadeAdj,pir:Math.round(_pir),rejections:_topRejection,rejStrength:_topRejStrength.toFixed(1),health:Math.round(_rangeHealth),trigger:_trigger,flowScore:Math.round(_flowScore)};
            // Drop downThreshold to make DOWN lock fire on this signal
            const _origDn=downThreshold;
            downThreshold=Math.min(50,downThreshold+20);
            reasoning.push(`[V10.3.0 RANGE-FADE-DOWN] PIR ${Math.round(_pir)}% + ${_trigger}${_topRejection>=1?` (rej×${_topRejection}, str ${_topRejStrength.toFixed(1)})`:` (flow ${Math.round(_flowScore)})`} + health ${Math.round(_rangeHealth)}% → ${_fadeAdj}, downThreshold ${_origDn}→${downThreshold}`);
          }else if(_pir<=20&&_bottomRejection>=1){
            // Strong UP fade signal
            const _pirBoost=Math.min(15,(20-_pir)*0.75);
            const _rejBoost=Math.min(15,_botRejStrength*4); // V10.3.4: smooth-drift removed
            const _fadeAdj=Math.round(15+_pirBoost+_rejBoost);
            totalScore+=_fadeAdj;
            rawSignalScores.rangeFade=_fadeAdj;
            const _trigger='rejection';
            _v10_3_0_rangeFadeFired={dir:'UP',magnitude:_fadeAdj,pir:Math.round(_pir),rejections:_bottomRejection,rejStrength:_botRejStrength.toFixed(1),health:Math.round(_rangeHealth),trigger:_trigger,flowScore:Math.round(_flowScore)};
            const _origUp=upThreshold;
            upThreshold=Math.max(50,upThreshold-20);
            reasoning.push(`[V10.3.0 RANGE-FADE-UP] PIR ${Math.round(_pir)}% + ${_bottomRejection} bottom rejections (strength ${_botRejStrength.toFixed(1)}) + health ${Math.round(_rangeHealth)}% → +${_fadeAdj}, upThreshold ${_origUp}→${upThreshold}`);
          }else if(_pir>=30&&_pir<=70){
            // Mid-range — no edge in chop. Flag for downstream sit-out enforcement.
            _v10_3_0_midRangeSitout=true;
            reasoning.push(`[V10.3.0 MID-RANGE] PIR ${Math.round(_pir)}% (30-70 band) in healthy chop — sit-out candidate`);
          }else{
            // PIR at edge (75-80 or 20-25) but no rejection — wait for rejection
            reasoning.push(`[V10.3.0 RANGE-EDGE-NOREJ] PIR ${Math.round(_pir)}% at edge but no rejection candle — wait`);
          }
        }
      }
    }
  }
  // V113: Velocity-adaptive threshold adjustment
  // Slow markets: tighten thresholds (require more conviction — chop is dangerous)
  // Fast markets: loosen thresholds (real moves don't wait for indecision)
  upThreshold=Math.max(55,Math.min(85,upThreshold+_velAdj.threshTighten));
  downThreshold=Math.max(15,Math.min(45,downThreshold-_velAdj.threshTighten));
  if(velocityRegime!=='NORMAL')reasoning.push(`[VEL-ADJ] ${velocityRegime} → UP@${upThreshold} DN@${downThreshold}`);
  // V9.11.2: regime signal redesign. Was: `if(fundingAccel > 0.0001) regimeBonus -= 5`
  //   — fundingAccel is current-funding minus previous-funding. OKX funding only updates
  //   every 8 hours, so within a 30s poll cycle fundingAccel is ~always 0. Audit showed
  //   regime fired 4% of the time (vs intended ~30%+). Now reads funding ZONE directly:
  //   funding in extreme zones nudges regimeBonus toward mean-reversion. Same direction
  //   semantics as the older funding-extreme adjustment (positive funding = crowded longs
  //   = DOWN bias) but scaled smaller (±5 vs ±12) to avoid double-counting fundingExtremeAdj.
  let _regZoneAdj=0;
  if(Number.isFinite(funding)&&funding!==0){
    const _fpct=funding*100;
    if(_fpct>0.03)_regZoneAdj=-5;       // crowded longs zone
    else if(_fpct<-0.015)_regZoneAdj=+5; // crowded shorts zone
  }
  if(_regZoneAdj!==0)regimeBonus+=_regZoneAdj;
  const regimeClamped=Math.max(-W.regime,Math.min(W.regime,regimeBonus));
  rawSignalScores.regime=regimeClamped;
  totalScore+=regimeClamped;

  // ── V152 NEW SIGNAL: rangePosition — mean reversion / range exhaustion ──
  //
  //   Rationale: Tara's other six signals (gap, momentum, structure, flow, technical, regime)
  //   are all backward-looking — "what did price just do?" When recent move is up, all six tend
  //   to point UP, even if price is already at the top of the typical range. This produces
  //   the "she follows the line" behavior. We need a signal that explicitly fights overextended
  //   moves to balance the trend-followers.
  //
  //   Math:
  //     rangeBps = (currentPrice − windowOpenPrice) / atrEnvelope
  //     atrEnvelope = atrBps × √(elapsed_minutes)   — Brownian volatility scaling
  //   When |rangeBps| ≥ 1.0 AND windowProgress ≥ 0.4 (40%+ through window), we're at the edge
  //   of typical range and reversion is more likely than continuation.
  //
  //   Contribution: -W.rangePosition × tanh(rangeBps - 1.0)   (signed against direction of move)
  //   So if price moved +1.5σ from open in late window, rangePosition pushes DOWN.
  //   Modest magnitude — not designed to dominate, just balance trend-followers.
  let rangeBonus=0;
  let rangeBps=0;
  if(windowOpenPrice>0&&atrBps>0){
    const moveBps=((currentPrice-windowOpenPrice)/windowOpenPrice)*10000;
    // ATR envelope scaled by elapsed window minutes (sqrt — Brownian motion)
    const elapsedMin=Math.max(0.5,(timeFraction||0)*(is15m?15:5));
    const atrEnvelope=atrBps*Math.sqrt(elapsedMin);
    rangeBps=atrEnvelope>0?moveBps/atrEnvelope:0;
    // V9.11.2: gate lowered from timeFraction >= 0.4 to >= 0.15. Audit of 388 trades
    //   showed this signal firing 0% of the time — the 0.4 gate (6 minutes into a
    //   15m window) excluded almost all locks since they happen between 30-240s
    //   elapsed (timeFraction 0.05-0.27). Now fires past 2.25min of a 15m window
    //   or 45s of a 5m window, which covers the typical lock window.
    // V10.2.42 PHASE 2 — LOWERED σ THRESHOLD from 1.0 to 0.7.
    //   Problem: rangePosition fired 1/751 trades. Most locks happen 30-90s into
    //   the window where price hasn't deviated a full ATR yet. 0.7σ catches the
    //   "modest extreme" cases where price has stretched 70% of expected envelope.
    //   Sign inversion still applies — at 0.7σ UP from open late in window,
    //   contrarian push DOWN. Modest 0.6× dampener kept so signal doesn't dominate.
    if((timeFraction||0)>=0.15&&Math.abs(rangeBps)>=0.7){
      // tanh produces a smooth ±1 saturation past |rangeBps|=2
      const excess=Math.abs(rangeBps)-0.7;
      const saturation=Math.tanh(excess*0.7);
      // SIGN INVERSION: if price went UP (positive rangeBps), this signal pushes DOWN (negative)
      const rangeDir=rangeBps>0?-1:1;
      rangeBonus=W.rangePosition*saturation*rangeDir*0.6; // 0.6 dampener — modest by default
      reasoning.push(`[RANGE-EXH] price ${rangeBps.toFixed(1)}σ from open at ${((timeFraction||0)*100).toFixed(0)}% through window — contrarian ${rangeBonus.toFixed(0)} (V10.2.42: ≥0.7σ)`);
    }
  }
  const rangeClamped=Math.max(-W.rangePosition,Math.min(W.rangePosition,rangeBonus));
  rawSignalScores.rangePosition=rangeClamped;
  totalScore+=rangeClamped;

  // Synaptic memory override
  const mem=regimeMemory?regimeMemory[regime]:null;
  if(mem&&(mem.wins+mem.losses)>=3){const wr=mem.wins/(mem.wins+mem.losses);if(wr<0.45){upThreshold+=6;downThreshold-=6;reasoning.push(`[MEMORY] Low WR (${(wr*100).toFixed(0)}%) in ${regime} — tightening`);}else if(wr>0.65){upThreshold-=4;downThreshold+=4;reasoning.push(`[MEMORY] High WR (${(wr*100).toFixed(0)}%) in ${regime} — loosening`);}}

  // ── V9.11.0 LAYER 1: HTF PATTERN AGGREGATION ──────────────────────────────
  // Was V9.10.10 single-TF pattern detection on liveHistory (1m only). Replaced by
  //   detectHtfPatterns which runs all three detectors on 1m + 5m + 15m candles,
  //   weights higher TFs more heavily, and applies a multi-TF confluence bonus when
  //   multiple TFs agree on direction.
  //
  // V9.10.10 single-TF call (still computed for telemetry compatibility):
  const _pattern=detectChartPattern(liveHistory);
  const _trend=detectTrendStructure(liveHistory);
  const _tlBreak=detectTrendlineBreak(liveHistory);
  // V9.11.0 multi-TF aggregated signal:
  const _htfPatterns=detectHtfPatterns(tfCandles);
  // V9.11.2: clamp HTF pattern contribution at ±55. The internal aggregator can produce
  //   raw values up to ±396 (3-TF max × confluence bonus 1.6×) which would dwarf every
  //   other signal in totalScore (which ranges -100 to +100 across all signals combined).
  //   Audit showed actual values reaching -250 on real trades. Plus the learning
  //   multiplier (0-2.5×) could amplify further. Clamping at ±55 keeps HTF as one strong
  //   signal among many rather than the dominant one. The full per-TF + confluence detail
  //   is still preserved in patternsV9_11_0 telemetry for analysis.
  // V10.2.41 PHASE 1 — REDUCED CAP: htfPatterns showed 54.2% accuracy across 273
  //   fires with average magnitude 41 (huge) in 751-trade audit. That's barely
  //   better than random, but the signal was the SECOND-LARGEST magnitude driver
  //   after `flow`. Reducing cap 55→20 keeps the signal as ONE voice among many
  //   instead of dominating. If patterns are genuinely predictive long-term, the
  //   learning loop will push them back up. If they're noise, the cap limits damage.
  // V10.2.50 — CHOP UNCAP for doubleTop/doubleBottom: these patterns are
  //   specifically chop-predictive (mark range edges). In RANGE-CHOP regime,
  //   raise cap to ±35 when dominant pattern is a double-top or double-bottom.
  //   Other patterns and other regimes stay at ±20.
  const _patternAdjUncapped=Math.round((_htfPatterns.totalAdj||0)*_weightOf(W,'htfPatterns'));
  const _isChopDoublePattern=regime==='RANGE-CHOP'&&_htfPatterns?.detail&&(/doubleTop/i.test(_htfPatterns.detail)||/doubleBottom/i.test(_htfPatterns.detail));
  const _cap=_isChopDoublePattern?35:20;
  const patternAdj=Math.max(-_cap,Math.min(_cap,_patternAdjUncapped));
  if(_isChopDoublePattern){
    _v10_2_50_htfChopBoost={cap:35,baselineCap:20,patternAdj:patternAdj,detail:_htfPatterns.detail};
  }
  if(patternAdj!==0){
    const _capLabel=_isChopDoublePattern?`V10.2.50 chop±35`:`V10.2.41 ±20`;
    const _capNote=Math.abs(_patternAdjUncapped)>_cap?` (capped from ${_patternAdjUncapped}, ${_capLabel})`:'';
    reasoning.push(`[HTF-PATTERNS] ${_htfPatterns.detail} → ${patternAdj>0?'+':''}${patternAdj} (W:${_weightOf(W,'htfPatterns').toFixed(2)})${_capNote}`);
  }
  totalScore+=patternAdj;

  // ── V9.11.0 LAYER 2: FUTURES SIGNALS ───────────────────────────────────────
  // Funding rate + Open Interest delta + Basis. Pulled every 30s from Bybit perp
  //   tickers (see useFuturesData hook). These are slower-moving CONTEXT signals
  //   rather than trigger-level events — max total contribution is ±33, deliberately
  //   smaller than the chart-pattern max of ±55 (3-TF confluence).
  // Computed regardless of whether futuresData arrived; computeFuturesSignals returns
  //   all-zero totalAdj when inputs are missing, so engine behavior is identical to
  //   pre-V9.11 when the Bybit feed is down.
  // ── V9.11.2 LAYER 2 (FIXED): FUTURES SIGNALS FROM BLOOMBERG ──────────────
  // V9.11.0 created a redundant `useFuturesData` hook that hit Bybit's API directly
  //   from the browser. That CORS-fails — `futures` signal stayed at 0 across all
  //   trades. The fix is to read from `bloomberg` (the existing useBloomberg hook),
  //   which goes through the `/api/okx/*` proxy that's already working and feeding
  //   funding/OI/basis into the funding-extreme + liq-wall paths. No new network
  //   request needed.
  //
  // Field mapping:
  //   bloomberg.fundingRate           → funding
  //   bloomberg.oiChange5m            → oiDelta (% over 5min, not 30s — closer to a
  //                                     real conviction signal anyway)
  //   bloomberg.basisBps / 100        → basisPct (bloomberg gives bps, we want %)
  //
  // The redundant `useFuturesData` hook stays defined for compatibility but is no
  //   longer the source of truth. Removing it would force a TaraApp refactor; the
  //   bloomberg data is identical content from a more reliable source.
  const _futuresSignals=computeFuturesSignals({
    funding:bloomberg?.fundingRate,
    oi:bloomberg?.openInterest,
    oiDelta:bloomberg?.oiChange5m,        // already % change, just over 5min instead of 30s
    basis:bloomberg?.basisBps!=null?(bloomberg.basisBps/10000)*(bloomberg.indexPrice||0):null, // basis in USD
    basisPct:bloomberg?.basisBps!=null?bloomberg.basisBps/100:null, // bps → percent
    drift1m,
  });
  const _futAdjRaw=_futuresSignals.totalAdj||0;
  const _futAdj=Math.round(_futAdjRaw*_weightOf(W,'futures'));
  if(_futAdj!==0){
    reasoning.push(`[FUTURES] ${_futuresSignals.details.join(' | ')} → ${_futAdj>0?'+':''}${_futAdj} (W:${_weightOf(W,'futures').toFixed(2)})`);
  }
  totalScore+=_futAdj;
  // V9.11.0 LAYER 3: register the new signals in rawSignalScores so the learning
  //   loop (applyTradeLearning → updateWeights) can adjust their effective weight
  //   per asset / per regime. Without these entries, learning would never see
  //   patterns or futures even though they're influencing locks.
  rawSignalScores.htfPatterns=patternAdj;
  rawSignalScores.futures=_futAdj;

  // V134: Apply forward-looking trajectory adjustment
  totalScore+=trajectoryAdj;
  // V7.7: MEAN-REVERSION DETECTOR. Replaces the V7.6 mean-rev guard (which sat out
  //   exhaustion trades). User wants Tara to PREDICT the reversal, not avoid it. When all
  //   three primary signals (gap, momentum, flow) point the same direction with high
  //   magnitude, that's the exhaustion fingerprint — buyers/sellers all committed, next
  //   move is the unwind. Apply a COUNTER-BIAS to totalScore in the opposite direction.
  //   Conditions to fire:
  //     • All three of gap, momentum, flow ≥10 magnitude SAME sign
  //     • Time remaining ≥30% (need room for reversal to play out)
  //     • Not in clear trending regime (TRENDING UP/DOWN — those are continuation)
  //     • Tape consensus AGREES with the chase (all 3 windows ≥70% same dir) — confirms
  //       the exhaustion is fully expressed in tape
  //   Counter-bias magnitude: -12 to -18 (depending on signal sum). Enough to flip a
  //   marginal call but not enough to overwhelm a real continuation when it's also
  //   confirmed by Kalshi pricing or structural alignment.
  let _meanRevAdj=0;
  const _gapS=rawSignalScores.gap||0;
  const _momS=rawSignalScores.momentum||0;
  const _flowS=rawSignalScores.flow||0;
  const _allChaseUp=_gapS>=10&&_momS>=10&&_flowS>=10;
  const _allChaseDown=_gapS<=-10&&_momS<=-10&&_flowS<=-10;
  const _isClearTrend=regime==='TRENDING UP'||regime==='TRENDING DOWN'||regime==='STRONG TREND';
  const _earlyEnough=(timeFraction||0)<=0.7; // ≥30% time remaining
  // V9.14 BUG FIX: was reading from `window?.tapeWindows?.w15?.buyPct` which is
  //   the BROWSER GLOBAL window object — that property was never set, so this code
  //   path was dead since V8.x. Mean-reversion-predict at L5762-5775 had never fired.
  //   Now reads tapeWindows from params (wired through computeV99Posterior call site).
  const _w15Buy=tapeWindows?.w15?.buyPct;
  const _w30Buy=tapeWindows?.w30?.buyPct;
  const _w60Buy=tapeWindows?.w60?.buyPct;
  // V10.2.42 PHASE 2 — LOWERED THRESHOLDS for mean-reversion detection.
  //   Was: w15≥70, w30≥65, w60≥60 (strong consensus required).
  //   Problem: 5 fires across 751 trades. By the time tape consensus is THAT
  //   one-sided, the move is usually already played out — too late to call the
  //   reversal. The "earlyEnough" gate then often expires before consensus arrives.
  //   Now: w15≥60, w30≥58, w60≥55 — fires on developing one-sided tape, while
  //   the move is still building but exhaustion is detectable.
  //   Mirror thresholds for DOWN side.
  // V10.3.3 — Further loosened: w15≥55, w30≥54, w60≥53. Audit showed 6/695 fires
  //   under V10.2.42 thresholds — still too tight. When meanReversion does fire,
  //   it's correct (no LOSS bias), so loosening should add accurate trades.
  //   NOTE: `reversal` and `rangePosition` signals (also lifted in V10.2.42) are
  //   now considered superseded by V10.3.0 range-fade. Leaving them in place for
  //   the rare cases they fire but no further threshold tuning planned.
  const _tapeConsensusUp=_w15Buy!=null&&_w30Buy!=null&&_w60Buy!=null&&_w15Buy>=55&&_w30Buy>=54&&_w60Buy>=53;
  const _tapeConsensusDown=_w15Buy!=null&&_w30Buy!=null&&_w60Buy!=null&&_w15Buy<=45&&_w30Buy<=46&&_w60Buy<=47;
  if((_allChaseUp&&_tapeConsensusUp)&&!_isClearTrend&&_earlyEnough){
    // All three signals + tape pumping UP — call the reversal
    const _sumMag=_gapS+_momS+_flowS;
    _meanRevAdj=_sumMag>=60?-18:_sumMag>=40?-15:-12;
    rawSignalScores.meanReversion=_meanRevAdj;
    totalScore+=_meanRevAdj;
    reasoning.push(`[MEAN-REV-PREDICT] UP exhaustion (g+${_gapS.toFixed(0)} m+${_momS.toFixed(0)} f+${_flowS.toFixed(0)}, tape ${_w15Buy.toFixed(0)}/${_w30Buy.toFixed(0)}/${_w60Buy.toFixed(0)}%) → expecting DOWN reversal (${_meanRevAdj})`);
  } else if((_allChaseDown&&_tapeConsensusDown)&&!_isClearTrend&&_earlyEnough){
    // All three signals + tape dumping DOWN — call the reversal
    const _sumMag=Math.abs(_gapS+_momS+_flowS);
    _meanRevAdj=_sumMag>=60?18:_sumMag>=40?15:12;
    rawSignalScores.meanReversion=_meanRevAdj;
    totalScore+=_meanRevAdj;
    reasoning.push(`[MEAN-REV-PREDICT] DOWN exhaustion (g${_gapS.toFixed(0)} m${_momS.toFixed(0)} f${_flowS.toFixed(0)}, tape ${_w15Buy.toFixed(0)}/${_w30Buy.toFixed(0)}/${_w60Buy.toFixed(0)}%) → expecting UP reversal (+${_meanRevAdj})`);
  }
  // V9.2.1: CROSS-ASSET CORRELATION SIGNAL — when the OTHER asset (BTC↔ETH) has
  //   moved significantly in the last 5 minutes, apply a directional boost/penalty.
  //   BTC and ETH are ~80% correlated on 15m windows. If BTC just dumped 50bps and
  //   we're calling ETH UP, that's contrary to the correlation — penalize. If BTC
  //   dumped and we're calling ETH DOWN, boost.
  const _otherA=params.otherAssetData;
  if(_otherA&&_otherA.price>0&&(Date.now()-_otherA.lastUpdate)<30000){
    const _otherDelta=_otherA.deltaBps||0;
    const _absDelta=Math.abs(_otherDelta);
    if(_absDelta>=15){ // only act on meaningful moves (>15bps in 5min)
      const _otherDir=_otherDelta>0?1:-1; // +1 = other asset going UP
      const _ourDir=totalScore>0?1:-1; // +1 = our engine leans UP
      const _aligned=_otherDir===_ourDir;
      // Correlation boost: up to ±8 pts. Proportional to magnitude, capped.
      let _corrBoost=Math.min(8,_absDelta*0.08);
      if(_aligned){
        totalScore+=_corrBoost*_ourDir; // boost
        reasoning.push(`[CROSS-ASSET] ${_otherA.asset} moved ${_otherDelta>0?'+':''}${Math.round(_otherDelta)}bps → aligned with our ${_ourDir>0?'UP':'DOWN'} lean (+${_corrBoost.toFixed(1)})`);
      }else{
        totalScore-=_corrBoost*_ourDir; // penalty
        reasoning.push(`[CROSS-ASSET] ${_otherA.asset} moved ${_otherDelta>0?'+':''}${Math.round(_otherDelta)}bps → CONTRARY to our ${_ourDir>0?'UP':'DOWN'} lean (-${_corrBoost.toFixed(1)})`);
      }
      rawSignalScores.crossAsset=_aligned?_corrBoost:-_corrBoost;
    }
  }
  // V9.12: MACRO SHOCK DETECTOR (4-ASSET SYNCHRONY)
  //   Detects coordinated moves across BTC + ETH + SOL + XRP in the last ~60s.
  //   When 3+ of 4 assets move ≥50bps the same direction, that's a macro event
  //   (Powell, ETF flow, exchange outage, ETF news). Reversion in the next 5-15min
  //   is rare. Tara nudges confidence in the direction of the shock.
  //
  // Inputs:
  //   - BTC/ETH from priceMemoryRef (1m return derivable from history)
  //   - SOL/XRP from useMacroShockData (1m candle close vs prior close)
  //
  // Output: ±10 to totalScore, added as rawSignalScores.macroShock. Signal does
  //   NOT block locks — it amplifies/dampens confidence like every other signal.
  //   The learning loop can tune its effective weight via WEIGHT_BOUNDS later.
  //
  // Why ±10 cap: same magnitude as ML and timeOfDay. We want this to nudge the
  //   posterior, not dominate it. If 4 assets dump together AND Tara was leaning
  //   DOWN, the boost reinforces; if she was leaning UP, it dampens. Net effect
  //   is felt most when the lean and shock conflict.
  const _macroShock=params.macroShockData;
  if(_macroShock&&_macroShock.sol&&_macroShock.xrp&&(Date.now()-(_macroShock.lastUpdate||0))<30000){
    // Build the 4-asset return vector. BTC/ETH come from the existing priceMemoryRef
    //   path — we derive 60s return from drift1m which is already computed above.
    //   For the OTHER major (the one we're not currently trading), we use
    //   otherAssetData.deltaBps which is a 5-MIN delta — too long. Better: skip
    //   the other-asset feed here and rely on the dedicated SOL/XRP fetches plus
    //   the live drift1m for the current asset. We then check if the live asset's
    //   1m drift agrees with both SOL and XRP.
    const _solDelta=_macroShock.sol.deltaBps||0;
    const _xrpDelta=_macroShock.xrp.deltaBps||0;
    // Determine direction agreement: count assets moving meaningfully (≥30bps)
    //   the same way. Threshold of 30bps catches genuine shocks while filtering
    //   normal noise (typical 1m return is ±5-15bps on most candles).
    const _solDir=Math.abs(_solDelta)>=30?(_solDelta>0?1:-1):0;
    const _xrpDir=Math.abs(_xrpDelta)>=30?(_xrpDelta>0?1:-1):0;
    const _liveDir=Math.abs(drift1m)>=30?(drift1m>0?1:-1):0;
    // Other-asset (BTC if trading ETH, ETH if trading BTC) — use the 5m delta scaled
    //   down. If the 5m move is ≥150bps then it averaged ≥30bps/min — counts.
    const _otherFiveMin=_otherA?.deltaBps||0;
    const _otherDir=Math.abs(_otherFiveMin)>=150?(_otherFiveMin>0?1:-1):0;
    // Count agreeing-direction assets
    const _dirs=[_solDir,_xrpDir,_liveDir,_otherDir].filter(d=>d!==0);
    if(_dirs.length>=3){
      const _upCount=_dirs.filter(d=>d>0).length;
      const _dnCount=_dirs.filter(d=>d<0).length;
      const _agreementCount=Math.max(_upCount,_dnCount);
      // Require ≥3 of the moving assets agreeing on direction
      if(_agreementCount>=3){
        const _shockDir=_upCount>_dnCount?1:-1; // +1 = UP shock, -1 = DOWN shock
        // Magnitude: average absolute return across moving assets, capped at 80bps
        //   for normalization. A 50bps avg → 6.25 score; 80bps+ → 10 score (cap).
        const _avgAbsBps=_dirs.length>0
          ?[_solDelta,_xrpDelta,drift1m,_otherFiveMin/5].filter(d=>Math.abs(d)>=30).reduce((s,d)=>s+Math.abs(d),0)/_agreementCount
          :0;
        const _magnitude=Math.min(10,(_avgAbsBps/80)*10);
        // Apply in the direction of the shock relative to the current lean
        const _ourDir=totalScore>0?1:totalScore<0?-1:0;
        if(_ourDir!==0){
          const _aligned2=_shockDir===_ourDir;
          const _macroAdj=_aligned2?_magnitude:-_magnitude;
          totalScore+=_macroAdj*_ourDir;
          rawSignalScores.macroShock=_macroAdj*_ourDir;
          reasoning.push(`[MACRO-SHOCK] ${_agreementCount}-asset ${_shockDir>0?'UP':'DOWN'} shock (avg ${Math.round(_avgAbsBps)}bps/min) → ${_aligned2?'aligned':'CONTRARY'} with ${_ourDir>0?'UP':'DOWN'} lean (${_macroAdj>=0?'+':''}${_macroAdj.toFixed(1)})`);
        } else {
          // No clear lean yet — record telemetry but don't push direction
          rawSignalScores.macroShock=0;
          reasoning.push(`[MACRO-SHOCK] ${_agreementCount}-asset ${_shockDir>0?'UP':'DOWN'} shock detected — no clear lean to amplify`);
        }
      }
    }
  }
  // V9.14 SIGNAL 1: ECONOMIC CALENDAR BLACKOUT
  //   If we're within the ±15min window of a major US macro event, dampen
  //   confidence by pushing totalScore toward zero. This is a conviction filter,
  //   not a hard block — user said no gates. A trade with overwhelming signals
  //   can still lock; weak signals get suppressed.
  //
  //   Magnitude: dampener scales with event importance (FOMC ±35, NFP/CPI ±25,
  //   PPI/claims ±12). Applied OPPOSITE to current lean to drag toward neutral.
  if(econCalRisk&&econCalRisk.risk>0){
    const _calMag=econCalRisk.risk;
    const _ourDirCal=totalScore>0?1:totalScore<0?-1:0;
    if(_ourDirCal!==0){
      const _calAdj=dampenScore(totalScore,_calMag)-totalScore; // cannot reverse a weak lean
      totalScore+=_calAdj;
      rawSignalScores.calendarRisk=_calAdj;
      reasoning.push(`[CALENDAR] ${econCalRisk.event} @ ${econCalRisk.etTime} (${econCalRisk.minsOffset>=0?'+':''}${econCalRisk.minsOffset}m) — dampening ${_ourDirCal>0?'UP':'DOWN'} lean (${_calAdj.toFixed(0)})`);
    } else {
      rawSignalScores.calendarRisk=0;
      reasoning.push(`[CALENDAR] ${econCalRisk.event} @ ${econCalRisk.etTime} (${econCalRisk.minsOffset>=0?'+':''}${econCalRisk.minsOffset}m) — elevated caution, no clear lean`);
    }
  }
  // V9.14 SIGNAL 2: TAPE FLOW RATE-OF-CHANGE (TAPE-ACCEL)
  //   The existing flow signal uses globalFlow.imbalance (a 30s ratio). It tells
  //   you the CURRENT state of aggression. What it doesn't tell you: is that
  //   state ACCELERATING or DECELERATING right now?
  //
  //   This signal compares short-window buyPct (w5, 5 seconds) against medium-
  //   window buyPct (w30, 30 seconds). When they diverge meaningfully, that's a
  //   real-time shift in market behavior — often the leading edge of a reversal.
  //
  //   Examples:
  //   - w30 buyPct = 70% (buyers dominating 30s) but w5 buyPct = 30% (sellers
  //     dominating last 5s) → buying exhausted, sellers stepping in → DOWN signal
  //   - w30 buyPct = 30% (sellers dominating) but w5 buyPct = 70% (buyers rushing
  //     in last 5s) → seller exhaustion, dip-buyers arriving → UP signal
  //
  //   Magnitude: scales with divergence size, capped at ±10. Only fires when
  //   BOTH windows have enough volume (≥$30K combined) to avoid noise.
  if(tapeWindows?.w5&&tapeWindows?.w30){
    const _w5Buys=tapeWindows.w5.buys||0;
    const _w5Sells=tapeWindows.w5.sells||0;
    const _w30Buys=tapeWindows.w30.buys||0;
    const _w30Sells=tapeWindows.w30.sells||0;
    const _w5Total=_w5Buys+_w5Sells;
    const _w30Total=_w30Buys+_w30Sells;
    if(_w5Total>=10000&&_w30Total>=30000){
      const _w5Pct=tapeWindows.w5.buyPct||50;
      const _w30Pct=tapeWindows.w30.buyPct||50;
      const _diff=_w5Pct-_w30Pct; // positive = recent more bullish than 30s avg
      // Only meaningful divergence: ≥15pp shift in 5s vs 30s
      if(Math.abs(_diff)>=15){
        // V10.2.41 PHASE 1 — HALF WEIGHT: tapeAccel showed 51.8% accuracy across 85
        //   fires in 751-trade audit. Coin flip. 30s tape windows are too short for
        //   reliable directional prediction in 15min trading. Reducing magnitude by
        //   50% — keeps the signal alive (in case it predicts in specific regimes)
        //   but stops it pushing 7-pt swings on random outcomes.
        // tanh saturation for stability
        const _tapeAccelMag=Math.tanh(Math.abs(_diff)/30)*10*0.5; // V10.2.41: ×0.5
        const _tapeAccelDir=_diff>0?1:-1;
        const _tapeAccelAdj=_tapeAccelMag*_tapeAccelDir;
        totalScore+=_tapeAccelAdj;
        rawSignalScores.tapeAccel=_tapeAccelAdj;
        reasoning.push(`[TAPE-ACCEL] 5s buy% ${_w5Pct.toFixed(0)} vs 30s ${_w30Pct.toFixed(0)} (Δ${_diff>0?'+':''}${_diff.toFixed(0)}pp) → ${_tapeAccelAdj>0?'+':''}${_tapeAccelAdj.toFixed(1)} (V10.2.41: ×0.5)`);
      }
    }
  }
  // V9.14 SIGNAL 3: SPOT vs PERP DIVERGENCE
  //   Compares Coinbase spot price against bloomberg's perp mark price. When
  //   they meaningfully diverge, leveraged positioning is extended in one
  //   direction and vulnerable to a flush.
  //
  //   Signal direction: divergence is CONTRARIAN.
  //   - Perp >> spot (positive divergence, longs crowded) → DOWN signal (longs vulnerable)
  //   - Perp << spot (negative divergence, shorts crowded) → UP signal (shorts vulnerable)
  //
  //   Threshold: 30 bps minimum. Below that is normal pricing noise. Capped at ±8.
  if(spotPerpDiv&&spotPerpDiv.status==='live'&&Math.abs(spotPerpDiv.divBps)>=30){
    const _spdMag=Math.min(8,(Math.abs(spotPerpDiv.divBps)-30)*0.15+4);
    const _spdDir=spotPerpDiv.divBps>0?-1:1; // CONTRARIAN — perp premium = DOWN signal
    const _spdAdj=_spdMag*_spdDir;
    totalScore+=_spdAdj;
    rawSignalScores.spotPerpDiv=_spdAdj;
    reasoning.push(`[SPOT-PERP] Perp ${spotPerpDiv.divBps>0?'premium':'discount'} ${Math.abs(spotPerpDiv.divBps).toFixed(0)}bps → ${_spdDir>0?'UP':'DOWN'} bias (${_spdAdj>0?'+':''}${_spdAdj.toFixed(1)})`);
  }
  // V13.4.82: ALWAYS-LOG spotPerpDiv telemetry (instrumentation, ZERO trade-behavior change).
  //   The scoring block above only writes rawSignalScores.spotPerpDiv when the feed is live
  //   AND |divBps|>=30. Below that it wrote nothing, so the audit log could not tell 'signal
  //   said ~neutral' apart from 'signal was dead'. This signal has run at weight 8 its whole
  //   life with NO per-lock record, so it was impossible to audit for realized-EV value the
  //   way tape/netCents were. We now stamp the raw divergence on every lock: the actual adj
  //   if it fired, else 0 when live-but-subthreshold, else null when the feed is not live.
  //   Mirrors the netCents instrumentation move: measure first, then decide the weight.
  try{
    if(!('spotPerpDiv' in rawSignalScores)){
      rawSignalScores.spotPerpDiv=(spotPerpDiv&&spotPerpDiv.status==='live'&&Number.isFinite(spotPerpDiv.divBps))?0:null;
    }
    rawSignalScores._spdDivBps=(spotPerpDiv&&Number.isFinite(spotPerpDiv.divBps))?Math.round(spotPerpDiv.divBps*10)/10:null;
    rawSignalScores._spdStatus=(spotPerpDiv&&spotPerpDiv.status)?String(spotPerpDiv.status):'none';
  }catch(_eSpd){}
  // Freeze a pre-ML feature vector for shadow learning. A training-set fit
  // cannot prove an improvement, and no shadow candidate affects live scores.
  // Learning features are frozen below after the forecast is finalized.
  const _mlModel=null; // legacy in-sample activation retired
  // V9.2.3: TIME-OF-DAY BOOST — data-driven hourly WR feeds back into posterior.
  //   Computed from historical call log (per-asset). Hours with ≥8 trades and WR ≥60%
  //   get a boost; hours with WR <45% get a penalty. Up to ±6 pts.
  // V10.2.41 PHASE 1 — DISABLED: timeOfDay showed 47.7% accuracy across 451 fires.
  //   That's SLIGHTLY INVERSE — actively harmful, pushing posterior the wrong way ~52%
  //   of the time. Per-bucket samples are 10-30 trades which is too noisy to trust.
  //   Still track for telemetry. Re-enable when buckets reach ≥50 trades each AND
  //   directional accuracy exceeds 55%.
  const _todBoost=params.timeOfDayBoost;
  if(_todBoost){
    const _h=new Date().getUTCHours();
    const _boost=_todBoost[_h]||0;
    if(_boost!==0){
      const _TOD_DISABLED=true; // V10.2.41 — disable until accuracy improves
      if(!_TOD_DISABLED){
        totalScore+=_boost;
        rawSignalScores.timeOfDay=_boost;
        reasoning.push(`[TIME-OF-DAY] Hour ${_h} UTC → ${_boost>0?'+':''}${_boost} (from historical WR at this hour)`);
      } else {
        rawSignalScores.timeOfDay=_boost; // shadow tracking only
        if(Math.abs(_boost)>=3)reasoning.push(`[TIME-OF-DAY-DISABLED] Hour ${_h} UTC would have applied ${_boost>0?'+':''}${_boost} (V10.2.41 — 48% accuracy, suppressed)`);
      }
    }
  }
  // V9.5.0: KALSHI-SPOT DIVERGENCE (opponent modeling).
  //   When Kalshi YES moves materially (≥4¢ over 60s) without spot confirming
  //   (|spotΔ| ≤4 bps), interpret as smart-money positioning ahead of a move.
  //   Contributes ±4 to ±10 points to the posterior in the Kalshi-direction.
  //   Score is capped so this signal can't dominate live tape/structure — it's
  //   a leading indicator, not a primary one.
  const _kLead=params.kalshiLead;
  if(_kLead&&(_kLead.dir==='UP'||_kLead.dir==='DOWN')&&Number.isFinite(_kLead.score)&&_kLead.score>0){
    const _kSign=_kLead.dir==='UP'?1:-1;
    const _kAdj=_kLead.score*_kSign;
    totalScore+=_kAdj;
    rawSignalScores.kalshiLead=_kAdj;
    reasoning.push(`[KALSHI-LEAD] Kalshi YES moved ${_kLead.kDelta>0?'+':''}${_kLead.kDelta}¢ vs spot ${_kLead.spotDeltaBps>0?'+':''}${_kLead.spotDeltaBps}bps over 60s → ${_kLead.dir} positioning ahead of move (${_kAdj>0?'+':''}${_kAdj})`);
  }
  // ═══════════════════════════════════════════════════════════════════════════
  // V10.7.64 — SIGNAL COHERENCE TELEMETRY + V10.7.77 COHERENCE GUARD
  // ═══════════════════════════════════════════════════════════════════════════
  // V10.7.64: Stamps cohRatio for audit (telemetry only — no totalScore change).
  // V10.7.77: ACTIVATES coherence guard based on real loss data.
  //   Analysis of losses on V10.7.76 showed:
  //   14:15 loss: cohRatio=1.0, ALL 7 signals agreed DOWN, Tara called UP (conf=65%)
  //   18:16 loss: cohRatio=0.71, 5/7 signals agreed DOWN, Tara called DOWN (conf=31%)
  //   In both cases: Kalshi lead + trajectory overrode a clear signal consensus.
  //   Kalshi was leading price the wrong way — Tara followed Kalshi into bad trades.
  //
  //   Guard: when ≥75% of active signals agree in OPPOSITE direction to current
  //   totalScore direction AND at least 4 active signals, apply a strong correction.
  //   This is not a sit-out gate — it pulls totalScore toward the consensus.
  //   The old backtest that said "low coherence wins 64%" was on mixed setups.
  //   HIGH coherence opposing (all signals agree against direction) is different —
  //   that's Kalshi/trajectory dragging Tara the wrong direction.
  try{
    const _cohSignals={
      gap:rawSignalScores.gap||0,momentum:rawSignalScores.momentum||0,
      structure:rawSignalScores.structure||0,flow:rawSignalScores.flow||0,
      technical:rawSignalScores.technical||0,macd:rawSignalScores.macd||0,
      avwap:rawSignalScores.avwap||0,delta:rawSignalScores.delta||0,
      bos:rawSignalScores.bos||0,stochRSI:rawSignalScores.stochRSI||0,
    };
    const _consensusScore=Object.values(_cohSignals).reduce((s,v)=>s+v,0);
    const _consensusDir=_consensusScore>0?1:-1;
    const _currentDir=totalScore>0?1:-1;
    const _active=Object.entries(_cohSignals).filter(([,v])=>Math.abs(v)>=2);
    const _aligned=_active.filter(([,v])=>(v>0?1:-1)===_consensusDir);
    const _opposing=_active.filter(([,v])=>(v>0?1:-1)!==_consensusDir);
    const _cohRatio=_active.length>0?_aligned.length/_active.length:0.5;
    const _alignedStrength=_aligned.reduce((s,[,v])=>s+Math.abs(v),0);
    const _opposingStrength=_opposing.reduce((s,[,v])=>s+Math.abs(v),0);

    // V10.7.77: Coherence override — pull toward signal consensus when strongly opposed
    //   Fires when: ≥4 active signals, ≥80% agree in one direction, AND
    //   totalScore is pointing the OTHER direction (Kalshi/trajectory overriding signals)
    const _consensusOpposesCurrent=_consensusDir!==_currentDir;
    if(_active.length>=4&&_cohRatio>=0.80&&_consensusOpposesCurrent&&Math.abs(_consensusScore)>15){
      // Strong consensus opposes current direction — apply correction toward consensus
      // Magnitude: proportional to how strongly consensus disagrees
      const _correction=_consensusScore*0.40; // pull 40% toward consensus
      totalScore+=_correction;
      rawSignalScores._v10_7_77_cohAdj=Math.round(_correction*10)/10;
      reasoning.push(`[V10.7.77] Coherence override: ${_aligned.length}/${_active.length} signals say ${_consensusDir>0?'UP':'DOWN'} (consensus=${_consensusScore.toFixed(0)}) but totalScore said ${_currentDir>0?'UP':'DOWN'} → correction ${_correction>=0?'+':''}${_correction.toFixed(1)}`);
    }else{
      rawSignalScores._v10_7_77_cohAdj=0;
    }

    // Stamps (unchanged from V10.7.64)
    rawSignalScores._v10_7_64_consensusScore=Math.round(_consensusScore*10)/10;
    rawSignalScores._v10_7_64_cohRatio=Math.round(_cohRatio*100)/100;
    rawSignalScores._v10_7_64_alignedN=_aligned.length;
    rawSignalScores._v10_7_64_opposingN=_opposing.length;
    rawSignalScores._v10_7_64_alignedStrength=Math.round(_alignedStrength*10)/10;
    rawSignalScores._v10_7_64_opposingStrength=Math.round(_opposingStrength*10)/10;
  }catch(e){
    try{console.warn('[V10.7.77] Coherence error:',e?.message);}catch(_){}
  }
  // ═══════════════════════════════════════════════════════════════════════════

  // Convert to posterior
  // ═══════════════════════════════════════════════════════════════════════════
  // V10.7.55 — SURGICAL SIGNAL ADJUSTMENTS (BACKTESTED, +5 wins/7d expected)
  // ═══════════════════════════════════════════════════════════════════════════
  // Aggressive cleanup was attempted (neutralize flow/technical/bos/delta) but
  // backtest showed catastrophic damage: -48 wins/7d from neutralizing flow alone.
  // Signal rankings I derived earlier were misleading because they conditioned on
  // extreme values, missing that signals contribute correctly across most trades.
  //
  // Keeping ONLY the rules that backtested positive on last 7d BTC 15m (n=451):
  //   1. Halve MACD when |gap|<5      (+2 wins/7d, catches today's failure pattern)
  //   2. Boost gap 30% when |gap|>=8  (+2 wins/7d, strongest predictor amplified)
  //   3. Neutralize technical in chop (+3 wins/7d, modest inverse)
  // Net: ~+5-7 wins per 7d. Modest but real. SAME volume.
  //
  // EXPLICITLY NOT TOUCHED based on backtest:
  //   - flow: neutralizing costs 48 wins/7d (was wrong about anti-predictive)
  //   - bos: neutralizing costs 1 win/7d (mildly net negative)
  //   - delta: neutralizing costs 1 win/7d (mildly net negative)
  try{
    const _v1055Regime=regime||'';
    const _v1055IsChop=_v1055Regime==='RANGE-CHOP'||_v1055Regime==='HIGH VOL CHOP';
    const _v1055Gap=Number(rawSignalScores.gap)||0;
    const _v1055HasGap=Math.abs(_v1055Gap)>=5;
    const _v1055Adjustments={};
    let _v1055TotalAdj=0;

    // RULE 1: Neutralize technical in CHOP only (+3 wins/7d)
    if(_v1055IsChop&&typeof rawSignalScores.technical==='number'&&rawSignalScores.technical!==0){
      const _orig=rawSignalScores.technical;
      _v1055TotalAdj-=_orig;
      _v1055Adjustments.technicalNeutralizedChop=Math.round(_orig*10)/10;
      rawSignalScores.technical=0;
    }

    // RULE 2: Halve MACD when |gap|<5 (catches today's failure pattern, +2 wins/7d)
    if(!_v1055HasGap&&typeof rawSignalScores.macd==='number'&&rawSignalScores.macd!==0){
      const _orig=rawSignalScores.macd;
      const _cut=_orig*0.5;
      _v1055TotalAdj+=(_cut-_orig);
      _v1055Adjustments.macdGapGuardCut=Math.round((_orig-_cut)*10)/10;
      rawSignalScores.macd=_cut;
    }

    // RULE 4: Neutralize BOS in RANGE-CHOP (V10.7.69 — fresh data shows -25pt edge)
    //   BOS detects breakouts. In RANGE-CHOP, breakouts are fake 63% of the time.
    //   When BOS fires aligned with direction in chop → 37% WR (worse than random).
    //   Old backtest (n=451) showed -1 win. New data (n=32 V10.7.68) shows -25pt edge.
    //   Regime shifted. Neutralize in chop.
    if(_v1055IsChop&&typeof rawSignalScores.bos==='number'&&rawSignalScores.bos!==0){
      const _orig=rawSignalScores.bos;
      _v1055TotalAdj-=_orig;
      _v1055Adjustments.bosNeutralizedChop=Math.round(_orig*10)/10;
      rawSignalScores.bos=0;
    }

    // RULE 5: Neutralize Delta in RANGE-CHOP (V10.7.69 — -17pt edge in fresh data)
    //   Three-step delta detects momentum. In RANGE-CHOP, momentum fades.
    //   Aligned delta = 33% WR, opposing delta = 50% WR → delta is inverted in chop.
    //   Old backtest showed -1 win. New data shows clearly anti-predictive.
    if(_v1055IsChop&&typeof rawSignalScores.delta==='number'&&rawSignalScores.delta!==0){
      const _orig=rawSignalScores.delta;
      _v1055TotalAdj-=_orig;
      _v1055Adjustments.deltaNeutralizedChop=Math.round(_orig*10)/10;
      rawSignalScores.delta=0;
    }

    // RULE 6: Dampen MACD in RANGE-CHOP entirely (not just gap guard)
    //   V10.7.69: MACD aligned = 44% WR in chop, opposing = 58%. Anti-predictive.
    //   Cut to 30% of original in chop (was already halved when |gap|<5 by rule 2,
    //   but rule 2 only fires without gap. In chop with gap, MACD still fires full.)
    if(_v1055IsChop&&typeof rawSignalScores.macd==='number'&&rawSignalScores.macd!==0){
      // Only apply if rule 2 didn't already cut it (rule 2 fires when !_v1055HasGap)
      if(_v1055HasGap){
        const _orig=rawSignalScores.macd;
        const _cut=_orig*0.30;
        _v1055TotalAdj+=(_cut-_orig);
        _v1055Adjustments.macdChopCut=Math.round((_orig-_cut)*10)/10;
        rawSignalScores.macd=_cut;
      }
    }

    // RULE 7: Neutralize STRUCTURE in RANGE-CHOP (V10.7.72 — n=32, -20pt edge)
    //   Structure detects candle exhaustion patterns (CC·Yata counts 3-9).
    //   In RANGE-CHOP: aligned structure = 47% WR, opposed = 67% WR → -20pt edge.
    //   Candle exhaustion signals continuation of the current move — in chop,
    //   that move fades. Structure is detecting the WRONG direction in chop.
    //   BOS and delta had the same pattern (V10.7.69). Now confirmed for structure too.
    //   Sample is n=32 (17 aligned + 15 opposed) — large enough to trust.
    if(_v1055IsChop&&typeof rawSignalScores.structure==='number'&&rawSignalScores.structure!==0){
      const _orig=rawSignalScores.structure;
      _v1055TotalAdj-=_orig;
      _v1055Adjustments.structureNeutralizedChop=Math.round(_orig*10)/10;
      rawSignalScores.structure=0;
    }

    // RULE 8: Neutralize StochRSI in RANGE-CHOP (V10.7.72 — n=11, -46pt edge)
    //   StochRSI: overbought/oversold fade signal.
    //   In RANGE-CHOP: aligned = 25% WR, opposed = 71% WR → -46pt edge.
    //   StochRSI is detecting overbought conditions and calling continuation —
    //   but in chop, overbought → fades back. Signal is inverted.
    //   n=11 is borderline but -46pt edge is extreme and consistent with
    //   the broader pattern of all momentum/continuation signals inverting in chop.
    if(_v1055IsChop&&typeof rawSignalScores.stochRSI==='number'&&rawSignalScores.stochRSI!==0){
      const _orig=rawSignalScores.stochRSI;
      _v1055TotalAdj-=_orig;
      _v1055Adjustments.stochRSINeutralizedChop=Math.round(_orig*10)/10;
      rawSignalScores.stochRSI=0;
    }
    // REGIME FLAGS for adaptive logic below
    const _v1055IsTrending=_v1055Regime==='TRENDING UP'||_v1055Regime==='TRENDING DOWN'||_v1055Regime==='STRONG TREND';
    const _v1055IsSqueeze=_v1055Regime==='SHORT SQUEEZE';
    const _v1055IsLongSqueeze=_v1055Regime==='LONG SQUEEZE';
    const _v1055IsHighVolChop=_v1055Regime==='HIGH VOL CHOP';
    const _v1055IsCompressing=_v1055Regime==='COMPRESSING';

    // V10.7.91: RANGE-CHOP GAP AMPLIFIER.
    //   Empirical data: in RANGE-CHOP, gap is the ONLY reliable signal (delta +10.1).
    //   Flow works secondarily (delta +4.4) but only when gap agrees.
    //   Flow-without-gap in RANGE-CHOP = 55% WR (barely above coin flip).
    //   Fix: in RANGE-CHOP, boost gap signal an additional 40%.
    //   This makes gap the clear dominator and reduces noise signal interference.
    if(_v1055IsChop&&Math.abs(_v1055Gap)>=5){
      const _chopGapBoost=_v1055Gap*0.40;
      _v1055TotalAdj+=_chopGapBoost;
      _v1055Adjustments.chopGapAmp=Math.round(_chopGapBoost*10)/10;
      rawSignalScores.gap=(_v1055Gap+_chopGapBoost);
    }

    // V10.7.95 FIX: GAP-DIRECTION CORRECTOR (replacing V10.7.91 version).
    //   V10.7.91 fired at gap < -15 which was too aggressive. Data showed it
    //   was flipping correct calls (gap=-32 raw, calls UP → WIN at 13:15, 15:15)
    //   that happened to have negative raw gap but correct aligned gap.
    //   Root issue: gap raw vs gap aligned confusion.
    //   Fix: only correct when BOTH gap AND flow strongly oppose current direction.
    //   This is the "everything agrees I'm wrong" case.
    const _corrGap=rawSignalScores.gap||0;
    const _corrFlow=rawSignalScores.flow||0;
    const _currentLean=totalScore>0?1:-1;
    const _gapDir=_corrGap>0?1:-1;
    const _flowDir=_corrFlow>0?1:-1;
    // Only correct when gap AND flow both strongly oppose current lean
    const _gapStronglyOpposes=_gapDir!==_currentLean&&Math.abs(_corrGap)>=20;
    const _flowAlsoOpposes=_flowDir!==_currentLean&&Math.abs(_corrFlow)>=15;
    if(_gapStronglyOpposes&&_flowAlsoOpposes){
      const _correction=_corrGap*1.5;
      _v1055TotalAdj+=_correction;
      _v1055Adjustments.gapCorrection=Math.round(_correction*10)/10;
      rawSignalScores.gap=rawSignalScores.gap+(_correction*0.5);
    }

    // GAP BOOST — regime-aware (V10.7.75)
    //   RANGE-CHOP: +30% when |gap|≥8 (original)
    //   TRENDING: +40% — momentum is with you, gap signal is more reliable
    //   SHORT SQUEEZE: +20% — gap less reliable during explosive squeeze moves
    //   HIGH VOL CHOP: +50% — high vol makes gap the dominant predictor (84% WR regime)
    if(Math.abs(_v1055Gap)>=8){
      const _gapBoostPct=_v1055IsHighVolChop?0.50:_v1055IsTrending?0.40:_v1055IsSqueeze?0.20:0.30;
      const _boost=_v1055Gap*_gapBoostPct;
      _v1055TotalAdj+=_boost;
      _v1055Adjustments.gapBoost=Math.round(_boost*10)/10;
      rawSignalScores.gap=_v1055Gap+_boost;
    }

    // V10.7.75b — LONG SQUEEZE: same structure as SHORT SQUEEZE but inverted
    //   Retail longs piling in while whales are selling = price gets crushed DOWN fast.
    //   Momentum/flow are misleading (retail is wrong). Dampen both.
    //   Gap signal still valid — trust where price is vs strike.
    if(_v1055IsLongSqueeze){
      if(typeof rawSignalScores.momentum==='number'&&rawSignalScores.momentum!==0){
        const _orig=rawSignalScores.momentum;
        const _cut=_orig*0.40;
        _v1055TotalAdj+=(_cut-_orig);
        _v1055Adjustments.momentumLongSqueezeDampen=Math.round((_orig-_cut)*10)/10;
        rawSignalScores.momentum=_cut;
      }
      if(typeof rawSignalScores.flow==='number'&&rawSignalScores.flow!==0){
        const _orig=rawSignalScores.flow;
        const _cut=_orig*0.40;
        _v1055TotalAdj+=(_cut-_orig);
        _v1055Adjustments.flowLongSqueezeDampen=Math.round((_orig-_cut)*10)/10;
        rawSignalScores.flow=_cut;
      }
    }

    // V10.7.75b — COMPRESSING: pre-breakout coil, direction unknown
    //   BBW contracting below 30th pct = Bollinger squeeze.
    //   All directional signals are unreliable — no trend, no momentum.
    //   Dampen everything except gap (gap = where price already IS vs strike).
    //   Conviction will be lower naturally, but add extra dampen on trend signals.
    if(_v1055IsCompressing){
      ['momentum','macd','bos','structure','stochRSI','delta','avwap','technical'].forEach(sig=>{
        if(typeof rawSignalScores[sig]==='number'&&rawSignalScores[sig]!==0){
          const _orig=rawSignalScores[sig];
          const _cut=_orig*0.30; // keep only 30% of all directional signals
          _v1055TotalAdj+=(_cut-_orig);
          _v1055Adjustments[`${sig}CompressDampen`]=Math.round((_orig-_cut)*10)/10;
          rawSignalScores[sig]=_cut;
        }
      });
      rawSignalScores._v1075_isCompressing=true;
    }
    //   In trending markets momentum/MACD work correctly (continuation signals).
    //   BOS/delta/structure are already NOT neutralized in trending (isChop=false).
    //   Additional: boost momentum 40% — it's your second-best signal in trend.
    //   Dampen avwap 30% — AVWAP is a reversion signal, less useful in trend.
    if(_v1055IsTrending){
      if(typeof rawSignalScores.momentum==='number'&&rawSignalScores.momentum!==0){
        const _orig=rawSignalScores.momentum;
        const _boost=_orig*0.40;
        _v1055TotalAdj+=_boost;
        _v1055Adjustments.momentumTrendBoost=Math.round(_boost*10)/10;
        rawSignalScores.momentum=_orig+_boost;
      }
      if(typeof rawSignalScores.avwap==='number'&&rawSignalScores.avwap!==0){
        const _orig=rawSignalScores.avwap;
        const _cut=_orig*0.30; // keep 30%
        _v1055TotalAdj+=(_cut-_orig);
        _v1055Adjustments.avwapTrendDampen=Math.round((_orig-_cut)*10)/10;
        rawSignalScores.avwap=_cut;
      }
    }

    // V10.7.75 — SHORT SQUEEZE: dampen momentum, dampen flow
    //   Squeeze momentum is misleading — explosive move often near exhaustion.
    //   Flow imbalance during squeeze is one-sided and doesn't predict resolution.
    //   Both cut to 40% in squeeze regime.
    if(_v1055IsSqueeze){
      if(typeof rawSignalScores.momentum==='number'&&rawSignalScores.momentum!==0){
        const _orig=rawSignalScores.momentum;
        const _cut=_orig*0.40;
        _v1055TotalAdj+=(_cut-_orig);
        _v1055Adjustments.momentumSqueezeDampen=Math.round((_orig-_cut)*10)/10;
        rawSignalScores.momentum=_cut;
      }
      if(typeof rawSignalScores.flow==='number'&&rawSignalScores.flow!==0){
        const _orig=rawSignalScores.flow;
        const _cut=_orig*0.40;
        _v1055TotalAdj+=(_cut-_orig);
        _v1055Adjustments.flowSqueezeDampen=Math.round((_orig-_cut)*10)/10;
        rawSignalScores.flow=_cut;
      }
    }

    if(_v1055TotalAdj!==0){
      totalScore+=_v1055TotalAdj;
      reasoning.push(`[V10.7.55] Signal tune: net ${_v1055TotalAdj>=0?'+':''}${_v1055TotalAdj.toFixed(1)} (${Object.entries(_v1055Adjustments).map(([k,v])=>`${k}:${v>=0?'+':''}${v}`).join(', ')})`);
    }

    rawSignalScores._v10_7_55_adjustments=_v1055Adjustments;
    rawSignalScores._v10_7_55_totalAdj=Math.round(_v1055TotalAdj*10)/10;
    rawSignalScores._v10_7_55_hasGap=_v1055HasGap;
    rawSignalScores._v10_7_55_isChop=_v1055IsChop;
  }catch(e){
    try{console.warn('[V10.7.55] Signal tune error:',e?.message);}catch(_){}
  }
  // ═══════════════════════════════════════════════════════════════════════════

  const rawPosterior=50+totalScore*0.95;
  let posterior=Math.max(1,Math.min(99,rawPosterior));
  // V10.4.3 — MULTI-WINDOW DIRECTIONAL CONTEXT (mean-reversion detection)
  //   The same `_recentMoves` array carries SIGNED bps (positive = closed above
  //   strike = UP window won, negative = DOWN window won). Track directional
  //   streaks: if last 5 windows show ≥4 same direction, mean-reversion is
  //   statistically likely. Apply small conviction haircut on same-direction
  //   calls (don't chase the streak), small boost on contrarian calls.
  //   Magnitude is intentionally small (5pt max) because mean-reversion timing
  //   is noisy — this is a tilt, not an override.
  //   Moved here (post-posterior declaration) in V10.6.2-hotfix to resolve
  //   "posterior before initialization" ReferenceError.
  if(_recentMoves&&_recentMoves.length>=5){
    const _last5=_recentMoves.slice(-5).filter(b=>Number.isFinite(b));
    if(_last5.length>=5){
      const _ups=_last5.filter(b=>b>0).length;
      const _downs=_last5.filter(b=>b<0).length;
      if(_ups>=4||_downs>=4){
        const _streakDir=_ups>=4?'UP':'DOWN';
        const _shift=_streakDir==='UP'?-3:3; // V10.6.4: softened from 5pt → 3pt
        const _newPost=Math.max(15,Math.min(85,posterior+_shift));
        _v104_3_windowCtx={
          streakDir:_streakDir,
          streakCount:Math.max(_ups,_downs),
          window:5,
          posteriorBefore:posterior,
          posteriorAfter:_newPost,
          shift:_newPost-posterior,
        };
        reasoning.push(`[V10.4.3] ${_streakDir} streak ${Math.max(_ups,_downs)}/5 → mean-reversion tilt ${posterior.toFixed(1)}%→${_newPost.toFixed(1)}%`);
        posterior=_newPost;
      }
    }
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // V10.7.12 — CONCRETE-OVER-STRUCTURAL PRIORITY
  // ═══════════════════════════════════════════════════════════════════════════
  // User report (May 19, 6:35pm): Score breakdown showed +52 UP (Gap +22, Flow
  //   +26, Momentum +5, FGT +6) — all CONCRETE signals saying UP. Yet posterior
  //   landed at 39% UP (i.e., 61% DOWN). Structural overrides (regime cells,
  //   trajectory projection, multi-window memory) dominated despite concrete
  //   evidence to the contrary.
  //
  // Fix: when concrete signals (gap + flow + momentum + tape + FGT) all agree
  //   on direction with combined weight ≥25pts, they DOMINATE structural
  //   overrides that point the other way. Posterior gets pulled toward the
  //   concrete consensus direction.
  //
  // The concrete signals are:
  //   - Gap: current price vs strike (most direct evidence of where we are)
  //   - Flow: order book imbalance (where buyers/sellers are positioned)
  //   - Momentum: recent price velocity
  //   - Channel: trend channel direction (V10.7.25: replaced FGT which was noise)
  //   - Tape: real-time buyer/seller pressure
  //
  // Structural signals (what we OVERRIDE):
  //   - Regime calibration (historical cell)
  //   - Multi-window memory (last 5 windows)
  //   - Trajectory projection (can use stale momentum)
  //   - Time-of-day baseline
  //
  // The market spending real money RIGHT NOW (concrete) is stronger evidence
  //   than historical patterns (structural). When they disagree, follow live.
  // V10.7.25: FGT removed from concrete signals (51% standalone accuracy = noise).
  //   Replaced with Channel score which has real directional value.
  // V10.7.29: WEIGHTED by audit-confirmed signal accuracy:
  //   Gap: 67.5% accuracy (×1.4 weight — strongest predictor)
  //   Momentum: 64.8% accuracy (×1.2 weight)
  //   Flow: 62.3% accuracy (×1.0 weight — baseline)
  //   Channel: ~60% est (×1.0 weight)
  //   Tape: 60-65% est (×1.0 weight)
  //   Was equal weight, now reflects which signals actually predict outcomes.
  //   Net effect: Gap and Momentum can trigger the concrete override more easily.
  const _v10_7_12_concrete={
    gap:(Number(rawSignalScores.gap)||0)*1.4,
    flow:(Number(rawSignalScores.flow)||0)*1.0,
    momentum:(Number(rawSignalScores.momentum)||0)*1.2,
    channel:(Number(rawSignalScores.channel)||Number(rawSignalScores.structure)||0)*1.0,
    tape:(Number(rawSignalScores.tape)||0)*1.0,
  };
  const _v10_7_12_concreteSum=_v10_7_12_concrete.gap+_v10_7_12_concrete.flow+_v10_7_12_concrete.momentum+_v10_7_12_concrete.channel+_v10_7_12_concrete.tape;
  const _v10_7_12_concreteDir=_v10_7_12_concreteSum>0?'UP':'DOWN';
  const _v10_7_12_currentDir=posterior>=50?'UP':'DOWN';
  // Trigger: concrete sum ≥25 in one direction, posterior opposite
  if(Math.abs(_v10_7_12_concreteSum)>=25&&_v10_7_12_concreteDir!==_v10_7_12_currentDir){
    // Count how many of the 5 signals AGREE with concrete direction
    const _signsAgree=Object.values(_v10_7_12_concrete).filter(v=>
      (_v10_7_12_concreteDir==='UP'&&v>2)||(_v10_7_12_concreteDir==='DOWN'&&v<-2)
    ).length;
    // Need at least 3 of 5 concrete signals strongly agreeing
    if(_signsAgree>=3){
      // Pull posterior toward concrete direction. Magnitude = min(20, concreteSum/2)
      const _pullMagnitude=Math.min(20,Math.abs(_v10_7_12_concreteSum)/2);
      const _newPosterior=_v10_7_12_concreteDir==='UP'
        ?Math.max(55,Math.min(85,50+_pullMagnitude))
        :Math.max(15,Math.min(45,50-_pullMagnitude));
      reasoning.push(`[V10.7.12] CONCRETE OVERRIDE: ${_signsAgree}/5 concrete signals agree ${_v10_7_12_concreteDir} (sum ${_v10_7_12_concreteSum>0?'+':''}${_v10_7_12_concreteSum.toFixed(0)}) — pulling posterior ${posterior.toFixed(0)}→${_newPosterior.toFixed(0)}`);
      posterior=_newPosterior;
    }
  }

  // Reality caps
  // V135: Tightened activation thresholds (-18→-12, +18→+12) so adverse price action
  //       starts pulling the posterior toward truth before gap is catastrophic.
  //       Added intermediate -25/+25 tier so the cap reaches into lock-firing range earlier.
  //       Outer threshold tightened -40→-35 / +40→+35.
  // V10.2.44 PHASE 4 — LOOSENED CAPS: audit showed 80-90% claimed posteriors won
  //   only 66.3% (n=98), and 90%+ won 70.5% (n=44). The 82% floor / 18% ceiling
  //   weren't earning their certainty — the caps were FORCING posteriors into
  //   ranges they didn't deserve. Loosened to 75/25 outer, 28/72 mid. Caps still
  //   function (preventing absurd misalignment) but stop manufacturing fake confidence.
  // V10.4.0: Reality caps now regime-aware. TRENDING UP and SHORT SQUEEZE
  //   tightened (68/32) because audit showed those regimes overconfident.
  //   TRENDING DOWN can stretch (85/22) because cell EVs support it.
  //   Falls back to legacy 75/25 if flag off or regime unknown.
  const _v104Caps=USE_V104_CALIBRATION_TABLES?(V104_REGIME_CAPS[_regime]||V104_REGIME_CAPS._default):V104_REGIME_CAPS._default;
  const _capHi=_v104Caps.hi;
  const _capLo=_v104Caps.lo;
  if(realGapBps<-35){posterior=Math.min(posterior,_capLo);reasoning.push(`[CAP] Deep underwater — UP capped at ${_capLo}% (V10.4.0 regime: ${_regime})`);}
  else if(realGapBps<-25){posterior=Math.min(posterior,Math.min(_capLo+7,42));reasoning.push(`[CAP] Adverse gap — UP capped at ${Math.min(_capLo+7,42)}% (V10.4.0)`);}
  else if(realGapBps<-12){posterior=Math.min(posterior,42);}
  else if(realGapBps>35){posterior=Math.max(posterior,_capHi);reasoning.push(`[CAP] Deep ITM — UP floored at ${_capHi}% (V10.4.0 regime: ${_regime})`);}
  else if(realGapBps>25){posterior=Math.max(posterior,Math.max(_capHi-7,58));reasoning.push(`[CAP] Strong favorable — UP floored at ${Math.max(_capHi-7,58)}% (V10.4.0)`);}
  else if(realGapBps>12){posterior=Math.max(posterior,58);}

  // Apply calibration if available (makes % accurate to actual historical win rate)
  const calibratedPosterior=calibration?calibratePosterior(posterior,calibration):posterior;
  const totalSignalWeight=Object.values(W).reduce((a,b)=>a+b,0)||1;

  // ── IMPROVEMENT 1: Direction prior calibration ───────────────────────────
  // V134: Hardcoded DOWN penalty REMOVED — was creating structural UP bias
  // V144: Calibration is now the ONLY direction-prior correction. Per-direction adjustments
  //       previously layered here have been removed in favor of bucket-based calibration.
  let dirCalibrated=calibratedPosterior;

  // V14.7: a single verified P(UP) calibrator owns statistical feedback.
  // Regime/band tables remain available for analysis, not repeated posterior pulls.
  let _diagV104Cal=null;

  // V10.6.2 — DOW × HOUR CALIBRATION
  //   Apply small posterior shift (±4pt max) based on (UTC day-of-week, UTC hour)
  //   cell. Audit found Sun 20:00 = 92% WR (+$0.45/trade) and Mon 21:00 = 18% WR
  //   (-$0.37/trade) — these are real, exploitable patterns. The shift pushes
  //   posterior toward UP when current cell is favorable (cell.wr - 0.60) > 0.
  //   This is intentionally directional-agnostic — favorable cells boost confidence
  //   in whichever direction the rest of the engine already leans.
  let _v106_2_dowHourShift=null;
if(params._dowHourTable){
    const d=computeDowHourShift(params._dowHourTable,Date.now());
    if(d)_v106_2_dowHourShift={...d,appliedShift:0,reason:'diagnostic-only-single-calibrator'};
  }

  // ── V9.7.8: FGT-COUNTER GATE ─────────────────────────────────────────────
  // When FGT (multi-timeframe alignment) is pulling against the lock direction,
  // cap conviction at 65%. The May 7 reversal trade locked DOWN at 74% with
  // FGT showing 1/4 UP — the 1m timeframe was already calling reversal but
  // got out-voted by stale STRUCT history. Cap prevents Tara from going past
  // the honest-confidence threshold when her own signals are split.
  // Threshold: |mtfAlignment| ≥ 0.7 against lock = "meaningful counter-signal"
  if(typeof mtfAlignment==='number'&&Number.isFinite(mtfAlignment)){
    const _lockingUp=dirCalibrated>50;
    const _lockingDn=dirCalibrated<50;
    const _fgtOpposesUp=_lockingUp&&mtfAlignment<=-0.7;
    const _fgtOpposesDn=_lockingDn&&mtfAlignment>=0.7;
    if(_fgtOpposesUp||_fgtOpposesDn){
      const _absConf=Math.abs(dirCalibrated-50);
      if(_absConf>15){ // >65% conviction
        const _sign=_lockingUp?1:-1;
        const _capped=50+_sign*15; // cap at 65/35
        const _before=dirCalibrated;
        dirCalibrated=_capped;
        // V9.7.9 telemetry
        _diagFgtCounter={applied:true,before:_before,after:_capped,mtfAlignment};
        reasoning.push(`[FGT-COUNTER] FGT ${mtfAlignment>=0?'+':''}${mtfAlignment.toFixed(1)} opposes lock ${_lockingUp?'UP':'DOWN'} → conv capped ${_before.toFixed(1)}%→${_capped.toFixed(1)}%`);
      }
    }
  }

  // ── IMPROVEMENT 5: Posterior time-decay for late window locks ─────────────
  // Data: losses lock avg 777s, wins avg 744s. Late locks structurally weaker.
  // Decay confidence toward 50 in the final stretch of the window.
  const windowAge=1-timeFraction; // 0=just opened, 1=almost closed
  const lateDecayFactor=windowAge>0.88?0.82:windowAge>0.78?0.91:1.0;
  if(lateDecayFactor<1.0){
    const beforeDecay=dirCalibrated;
    dirCalibrated=50+(dirCalibrated-50)*lateDecayFactor;
    reasoning.push(`[TIME] Late lock decay ×${lateDecayFactor}: ${beforeDecay.toFixed(1)}%→${dirCalibrated.toFixed(1)}%`);
  }
  // V9.2.0: CALIBRATION DAMPENING — call log analysis showed 80-89% posterior bucket
  //   has 62% actual WR (off by 23pts), 90%+ has 66% actual (off by 26pts). Tara is
  //   overconfident on her strongest calls. Dampen extreme posteriors toward 72% —
  //   the empirical ceiling where calibration still holds.
  //   Formula: when posterior > 78%, dampened = 72 + (posterior-72) × 0.35
  //   Effect: 80% → 74.8%, 90% → 78.3%, 95% → 80.1%. Soft enough to preserve
  //   directional signal but prevents the 95%-confidence-coin-flip problem.
  if(Math.abs(dirCalibrated-50)>28){ // > 78% or < 22%
    const _prevConf=dirCalibrated;
    const _sign=dirCalibrated>50?1:-1;
    const _dist=Math.abs(dirCalibrated-50);
    const _ceiling=22; // corresponds to 72% (50+22)
    if(_dist>_ceiling){
      const _excess=_dist-_ceiling;
      const _dampened=50+_sign*(_ceiling+_excess*0.35);
      dirCalibrated=_dampened;
      reasoning.push(`[CALIBRATION-DAMPEN] ${_prevConf.toFixed(1)}%→${_dampened.toFixed(1)}% (high-conviction dampening)`);
    }
  }
  // V9.2.0: TRENDING REGIME PENALTY — call log analysis showed TRENDING UP regime
  //   has 33% WR (4W/8L). The engine trusts trend signals in trending markets but
  //   gets caught at reversals. When in a trending regime AND structural indicators
  //   show exhaustion signs (channel extreme, slope flattening), apply a penalty
  //   that pulls the posterior back toward 50%.
  const _curRegime=regimeMemory?.current||'RANGE-CHOP';
  if((_curRegime==='TRENDING UP'||_curRegime==='TRENDING DOWN')&&_trendChannel?.valid){
    const _chPos=_trendChannel.channelPos;
    const _trendingUp=_curRegime==='TRENDING UP';
    // Exhaustion: trending UP but price is near channel top (> 0.80), or trending
    //   DOWN but price is near channel bottom (< 0.20). High reversal risk.
    const _trendExhausted=(_trendingUp&&_chPos>=0.80)||(!_trendingUp&&_chPos<=0.20);
    // Slope flattening: the trend is losing steam. If slope is < 0.3 bps/bar in
    //   the trend direction, momentum is dying.
    const _tcS=Number(_trendChannel.slopeBpsPerBar)||0;
    const _slopeFlat=(_trendingUp&&_tcS<0.3)||(!_trendingUp&&_tcS>-0.3);
    if(_trendExhausted||_slopeFlat){
      const _penalty=_trendExhausted&&_slopeFlat?0.70:0.85; // heavy or light
      const _prev=dirCalibrated;
      dirCalibrated=50+(dirCalibrated-50)*_penalty;
      reasoning.push(`[TREND-EXHAUST] ${_curRegime} + ${_trendExhausted?'channel-extreme':''}${_slopeFlat?' slope-flat':''} → ×${_penalty} (${_prev.toFixed(1)}%→${dirCalibrated.toFixed(1)}%)`);
    }
  }
  // V144: CRITICAL CHANGE — locks now use the calibrated posterior, not raw.
  //       Previously: 'finalPosterior' (calibrated) was returned as 'posterior',
  //       BUT the comment said "lock guards still use raw" — and a separate cosmetic
  //       compression existed for display only. This created a confusion where calibration
  //       only affected what you SAW, not what Tara DID. Now: the same calibrated value
  //       drives both display and lock decisions. No more "honest display, biased decision."
  //       Removed the _calibratedDisplay backtest compression — it was adding a second
  //       layer of cosmetic correction on top of calibration.
  // V10.2.44 PHASE 4 — HIGH-CONFIDENCE HAIRCUT.
  //   Audit: 80-90% claimed → 66.3% actual (n=98), 90%+ → 70.5% actual (n=44).
  //   Systemic overconfidence at the top end. Apply a graded haircut:
  //     80-89% → pull 30% of distance toward 78
  //     90%+  → pull 50% of distance toward 78
  //   Symmetric for DOWN side (≤20% gets pulled toward 22).
  //   This is a CALIBRATION CORRECTION — it doesn't change whether locks fire
  //   (lock thresholds use _baseUpThr from regime), it makes the displayed and
  //   stored posterior reflect actual historical WR at that confidence level.
  let _haircutFinal=dirCalibrated;
  let _haircutApplied=null;
  if(dirCalibrated>=90){
    const _target=78,_pull=0.5;
    _haircutFinal=dirCalibrated-(dirCalibrated-_target)*_pull;
    _haircutApplied=`90+ haircut: ${dirCalibrated.toFixed(0)}→${_haircutFinal.toFixed(0)} (pulled 50% toward 78)`;
  }else if(dirCalibrated>=80){
    const _target=78,_pull=0.3;
    _haircutFinal=dirCalibrated-(dirCalibrated-_target)*_pull;
    _haircutApplied=`80-89 haircut: ${dirCalibrated.toFixed(0)}→${_haircutFinal.toFixed(0)} (pulled 30% toward 78)`;
  }else if(dirCalibrated<=10){
    const _target=22,_pull=0.5;
    _haircutFinal=dirCalibrated+(_target-dirCalibrated)*_pull;
    _haircutApplied=`≤10 haircut: ${dirCalibrated.toFixed(0)}→${_haircutFinal.toFixed(0)} (pulled 50% toward 22)`;
  }else if(dirCalibrated<=20){
    const _target=22,_pull=0.3;
    _haircutFinal=dirCalibrated+(_target-dirCalibrated)*_pull;
    _haircutApplied=`11-20 haircut: ${dirCalibrated.toFixed(0)}→${_haircutFinal.toFixed(0)} (pulled 30% toward 22)`;
  }
  if(_haircutApplied)reasoning.push(`[V10.2.44 HAIRCUT] ${_haircutApplied}`);
  // V10.3.1 — CONFLUENCE-AWARE COIN-FLIP DETECTION
  //   Audit of 693 resolved trades revealed the 75-84% conviction band wins
  //   only 63% (n=381) — worst of upper bands. KEY TELL: htfPatterns and flow
  //   are systematically WEAKER in losing trades:
  //     55-64% winning band: |htfPatterns| avg 30.8, |flow| avg 41.2
  //     75-84% coin-flip band: |htfPatterns| avg 17.4, |flow| avg 35.3
  //
  //   When confidence reaches 75-84% WITHOUT strong core signals, that's
  //   overconfidence — tag it. Two outcomes:
  //     1. Pull confidence down to 67% (the actual WR these trades earn)
  //     2. Tag as 'coin-flip' so user sees the awareness badge in UI
  //
  //   Trades with STRONG core signals (htfPatterns≥20 OR flow≥30) keep their
  //   conviction — they're genuinely confident, not overconfident.
  let _v10_3_1_coinFlip=null;
  const _absHtfPatterns=Math.abs(rawSignalScores.htfPatterns||0);
  const _absFlow=Math.abs(rawSignalScores.flow||0);
  const _confAfter=_haircutFinal; // post V10.2.44 haircut
  // Apply coin-flip detection on either side (high UP conviction or low/high DOWN)
  const _isMidHighConv=(_confAfter>=75&&_confAfter<=84)||(_confAfter>=16&&_confAfter<=25);
  const _coreWeak=_absHtfPatterns<15&&_absFlow<25;
  if(_isMidHighConv&&_coreWeak){
    // Coin flip: pull toward 67/33
    const _isUpSide=_confAfter>=50;
    const _target=_isUpSide?67:33;
    const _pulled=_confAfter-(_confAfter-_target)*0.5;
    _v10_3_1_coinFlip={
      origConf:Math.round(_confAfter),
      pulledTo:Math.round(_pulled),
      htfPatterns:Math.round(_absHtfPatterns),
      flow:Math.round(_absFlow),
      reason:`75-84 band w/ weak core (htfPatterns ${Math.round(_absHtfPatterns)}<15, flow ${Math.round(_absFlow)}<25)`,
    };
    _haircutFinal=_pulled;
    reasoning.push(`[V10.3.1 COIN-FLIP] ${_confAfter.toFixed(0)}% → ${_pulled.toFixed(0)}% — weak core signals (htfPatterns ${Math.round(_absHtfPatterns)}, flow ${Math.round(_absFlow)})`);
  }
  // V10.3.1 — TRENDING UP confidence penalty
  //   Audit showed TRENDING UP at 55-64% conf = 46% WR (only sub-50% category in
  //   the otherwise-winning band). Apply a -5pt penalty to all TRENDING UP locks
  //   until deeper diagnosis. Symmetric: subtract 5 from UP conviction, add 5 to
  //   DOWN-side equivalent. This is a holding action, not a permanent fix.
  let _v10_3_1_trendingUpPenalty=null;
  if(regime==='TRENDING UP'&&_haircutFinal>=50&&_haircutFinal<=95){
    const _before=_haircutFinal;
    _haircutFinal=Math.max(50,_haircutFinal-5);
    _v10_3_1_trendingUpPenalty={before:Math.round(_before),after:Math.round(_haircutFinal),penalty:5};
    reasoning.push(`[V10.3.1 TRENDING-UP-PENALTY] regime weak (46% WR audit) → -5pt: ${_before.toFixed(0)}%→${_haircutFinal.toFixed(0)}%`);
  }
  const finalPosterior=Math.max(1,Math.min(99,_haircutFinal));
  const displayPosterior=finalPosterior; // V144: same as final, no cosmetic divergence

  reasoning.push(`[ATR] Volatility: ${atrBps.toFixed(1)} bps | Regime: ${regime}${isPostDecay?' | POST-DECAY':''}`);
  if(calibration&&Object.values(calibration).some(v=>v!=null)){
    const calDelta=Math.abs(finalPosterior-rawPosterior);
    if(calDelta>=2){
      reasoning.push(`[CAL] Raw ${rawPosterior.toFixed(0)}% → calibrated ${finalPosterior.toFixed(0)}% (Δ${calDelta.toFixed(0)}pts) — ${rawPosterior>finalPosterior?'haircut for over-confidence':'corrected upward'}`);
    }
  }

  // Rug pull check
  const isRugPull=tickSlope<-5&&aggrFlow<-0.6;
  // V140: Symmetric upward spike — mirror of rug-pull. Releases DOWN locks faster than waiting
  //       for the gap to hit -30 bps. Same magnitude thresholds, opposite signs.
  const isMoonshot=tickSlope>5&&aggrFlow>0.6;

  // V3.1.7: VOLUME-FLOW SIGNAL — informational only, not yet fed into posterior.
  //   Reads dollar-volume rate of change from tapeRef + ticksRef and compares to price drift.
  //   Goal: distinguish "real" moves (price + volume both rising in agreement) from "drift"
  //   moves (price moves but volume fading — likely to reverse).
  //
  //   This is a NEW signal as of V3.1.7. Track its label correlation with outcomes for
  //   30+ trades before integrating into posterior. We surface it so user can see it in
  //   the engine log, and so the trade log captures it for later analysis.
  const volFlow=(()=>{
    const _tape=params.tapeRef?.current;
    const _ticks=params.tradeTicksRef?.current||[];
    if(!_tape||_ticks.length<10)return{label:'INSUFFICIENT',score:0,trend:'flat',context:'not enough tape data'};
    const now=Date.now();
    // Compute buy/sell USD in last 30s and 30-90s (the "before" window)
    let recent_buy=0,recent_sell=0,recent_n=0;
    let prior_buy=0,prior_sell=0,prior_n=0;
    for(const t of _ticks){
      const age=now-t.time;
      const u=t.usd||(t.s*t.p);
      if(age<=30000){
        if(t.t==='B')recent_buy+=u;else recent_sell+=u;
        recent_n++;
      } else if(age<=90000){
        if(t.t==='B')prior_buy+=u;else prior_sell+=u;
        prior_n++;
      }
    }
    const recent_total=recent_buy+recent_sell;
    const prior_total=prior_buy+prior_sell;
    // Need enough volume in both windows to make a comparison
    if(recent_total<10000||prior_total<10000){
      return{label:'INSUFFICIENT',score:0,trend:'flat',context:`recent ${(recent_total/1000).toFixed(0)}K / prior ${(prior_total/1000).toFixed(0)}K — too thin`};
    }
    // Volume rate per second in each window
    const recent_rate=recent_total/30;
    const prior_rate=prior_total/60;
    const volTrend=recent_rate/prior_rate; // >1 = rising, <1 = falling
    // Net dollar pressure direction in recent window
    const recent_net=recent_buy-recent_sell;
    const recent_pressure=recent_total>0?(recent_net/recent_total):0; // -1 to +1
    // Price direction agreement
    const priceUp=drift1m>2;
    const priceDown=drift1m<-2;
    // Smoothness: ratio of net move to total tick range (more directional = smoother)
    let smoothness=0;
    if(_ticks.length>=10){
      const recent10=_ticks.slice(-10);
      const netMove=Math.abs(recent10[recent10.length-1].p-recent10[0].p);
      let totalRange=0;
      for(let i=1;i<recent10.length;i++){totalRange+=Math.abs(recent10[i].p-recent10[i-1].p);}
      smoothness=totalRange>0?netMove/totalRange:0; // 0=pure chop, 1=monotonic
    }
    // Classify
    let label='NEUTRAL',score=0;
    const _trendLabel=volTrend>1.3?'rising':volTrend<0.7?'falling':'flat';
    if(priceUp&&recent_pressure>0.2){
      if(_trendLabel==='rising'&&smoothness>0.5){label='BUY-CONFIRMED';score=70;}
      else if(_trendLabel==='falling'){label='BUY-FAILING';score=-30;}
      else{label='BUY-MIXED';score=10;}
    } else if(priceDown&&recent_pressure<-0.2){
      if(_trendLabel==='rising'&&smoothness>0.5){label='SELL-CONFIRMED';score=-70;}
      else if(_trendLabel==='falling'){label='SELL-FAILING';score=30;}
      else{label='SELL-MIXED';score=-10;}
    } else if(Math.abs(drift1m)<2){
      label='QUIET'; score=0;
    } else {
      // Price moving but tape doesn't agree
      label=priceUp?'BUY-DIVERGENT':'SELL-DIVERGENT';
      score=priceUp?-10:10;
    }
    const _ctx=`drift1m=${drift1m.toFixed(1)} · vol=${volTrend.toFixed(2)}x · pressure=${(recent_pressure*100).toFixed(0)}% · smooth=${(smoothness*100).toFixed(0)}%`;
    return{label,score,trend:_trendLabel,context:_ctx,recentBuyUSD:recent_buy,recentSellUSD:recent_sell,smoothness,volTrend,pressure:recent_pressure};
  })();
  if(volFlow.label!=='INSUFFICIENT'&&volFlow.label!=='QUIET'&&volFlow.label!=='NEUTRAL'){
    reasoning.push(`[VOL-FLOW] ${volFlow.label} · ${volFlow.context}`);
  }

  // V3.1.11: WITHIN-WINDOW CANDLE PATTERN — answers "what's the 1m candle structure
  //   of THIS window saying about where price is heading at close?"
  //   Looks for engulfing, climax-top/bottom, stair, exhaustion, compression patterns.
  //   Informational only — surfaced via [CANDLE] reasoning + UI chip. Not yet integrated
  //   into posterior. Track per-pattern WR before deciding to integrate.
  const candlePattern=(()=>{
    const c1m=tfCandles?.c1m||[];
    return detectWithinWindowPattern(c1m,params.windowOpenTime||0);
  })();
  if(candlePattern.label!=='INSUFFICIENT'&&candlePattern.label!=='OPENING'&&candlePattern.label!=='MIXED'){
    reasoning.push(`[CANDLE] ${candlePattern.label} · ${candlePattern.context}`);
  }

  // V3.1.7+/V3.1.9: WINDOW TYPE CLASSIFIER — answers "what kind of window is this?"
  //
  //   Two windows can have the same amplitude (high − low) but trade completely differently:
  //   a window that drifts smoothly from open to close 50bps is TRENDING; a window that
  //   visits both ±25bps multiple times is WHIPSAW. Same amplitude, opposite trade profiles.
  //
  //   Three measurements per window:
  //     1. rangeBps     = (high − low) / openPrice × 10000  — the amplitude
  //     2. dirChanges   = count of significant reversals so far (each ≥8bps swing from a
  //                       recent local extreme counts as one)
  //     3. motionTiming = WHEN in the window were high/low first reached?
  //                       Both early → SPIKE-FADE
  //                       Both late  → LATE-BREAK
  //                       Spread     → TRENDING / GRIND / WHIPSAW / RANGE
  //
  //   Seven labels:
  //     OPENING     <2 minutes elapsed, not enough data
  //     TRENDING    range ≥25bps, smooth one-direction motion (≤1 dir change)
  //     WHIPSAW     range ≥30bps, multiple direction changes (≥3)
  //     SPIKE-FADE  range ≥25bps, both extremes reached in first 30% of window
  //     LATE-BREAK  range ≥25bps, both extremes reached in last 30% of window
  //     GRIND       range 15-25bps, smooth one-direction motion (≤1 dir change)
  //     RANGE       range 15-30bps, symmetric oscillation (≈2 dir changes — one round trip)
  //     DEAD        range <15bps, minimal motion
  //
  //   Each type has its own lock-release threshold (see app.jsx). Other engine behaviors
  //   stay unchanged for now — informational signal first, then integrate as data justifies.
  const windowAmplitude=(()=>{
    const wHigh=params.windowHigh||0;
    const wLow=params.windowLow||0;
    const wHighTime=params.windowHighTime||0;
    const wLowTime=params.windowLowTime||0;
    const wOpenTime=params.windowOpenTime||0;
    if(!windowOpenPrice||windowOpenPrice<=0||wHigh<=0||wLow<=0){
      return{label:'OPENING',rangeBps:0,directionChanges:0,motionTiming:'unknown',context:'window data not yet available'};
    }
    const rangeBpsAmp=((wHigh-wLow)/windowOpenPrice)*10000;
    const tf=timeFraction||0;
    const elapsedMin=tf*(is15m?15:5);
    if(elapsedMin<2){
      return{label:'OPENING',rangeBps:rangeBpsAmp,directionChanges:0,motionTiming:'unknown',context:`${elapsedMin.toFixed(1)}m elapsed · range ${rangeBpsAmp.toFixed(0)}bps`};
    }

    // ── Compute direction changes from priceMemoryRef samples within this window ──
    // A direction change is when price reverses ≥8bps from a recent local extreme.
    let dirChanges=0;
    if(priceMemoryRef?.current&&wOpenTime>0){
      const samples=priceMemoryRef.current.filter(s=>s.time>=wOpenTime);
      if(samples.length>=4){
        const reversalThresholdPct=8/10000; // 8bps
        let lastExtreme=samples[0].p;
        let lastDir=0; // -1 = down, +1 = up, 0 = none yet
        for(let i=1;i<samples.length;i++){
          const p=samples[i].p;
          const moveAbs=Math.abs(p-lastExtreme);
          const movePct=moveAbs/lastExtreme;
          if(movePct>=reversalThresholdPct){
            const newDir=p>lastExtreme?1:-1;
            if(lastDir!==0&&newDir!==lastDir){
              dirChanges++;
            }
            lastDir=newDir;
            lastExtreme=p;
          } else {
            // Update lastExtreme to track the running edge
            if(lastDir===1&&p>lastExtreme)lastExtreme=p;
            else if(lastDir===-1&&p<lastExtreme)lastExtreme=p;
          }
        }
      }
    }

    // ── Compute motion timing — when high and low were first reached ──
    let motionTiming='spread';
    if(wHighTime>0&&wLowTime>0&&wOpenTime>0){
      const totalElapsed=Date.now()-wOpenTime;
      const highElapsedFrac=(wHighTime-wOpenTime)/Math.max(totalElapsed,1);
      const lowElapsedFrac=(wLowTime-wOpenTime)/Math.max(totalElapsed,1);
      // Both first reached in first 30% of elapsed time → spike-loaded
      if(highElapsedFrac<=0.30&&lowElapsedFrac<=0.30){
        motionTiming='early';
      }
      // Both first reached in last 30% of elapsed time → late-loaded
      else if(highElapsedFrac>=0.70&&lowElapsedFrac>=0.70){
        motionTiming='late';
      }
    }

    // ── Classify ──
    let label='NORMAL'; // default fallback
    if(rangeBpsAmp<15){
      label='DEAD';
    } else if(rangeBpsAmp>=30&&dirChanges>=3){
      label='WHIPSAW';
    } else if(rangeBpsAmp>=25&&motionTiming==='early'){
      label='SPIKE-FADE';
    } else if(rangeBpsAmp>=25&&motionTiming==='late'){
      label='LATE-BREAK';
    } else if(rangeBpsAmp>=25&&dirChanges<=1){
      label='TRENDING';
    } else if(rangeBpsAmp>=15&&rangeBpsAmp<25&&dirChanges<=1){
      label='GRIND';
    } else if(rangeBpsAmp>=15&&dirChanges===2){
      label='RANGE';
    } else {
      // Mid-range with 2-3 dir changes — falls through to RANGE as best fit
      label='RANGE';
    }

    const _ctx=`${rangeBpsAmp.toFixed(0)}bps range · ${dirChanges} reversals · motion ${motionTiming}`;
    return{
      label,
      rangeBps:rangeBpsAmp,
      directionChanges:dirChanges,
      motionTiming,
      context:_ctx,
      windowHigh:wHigh,
      windowLow:wLow,
    };
  })();
  if(windowAmplitude.label!=='OPENING'){
    reasoning.push(`[WIN-TYPE] ${windowAmplitude.label} · ${windowAmplitude.context}`);
  }

  // Preserve classifier diagnostics while all downstream policy reads agree
  // with the final regime (including an intentional range/quiet override).
  rawSignalScores._sourceRegime=rawSignalScores._v10736regime;
  rawSignalScores._sourceIsTrend=rawSignalScores._isTrend;
  rawSignalScores._sourceIsChop=rawSignalScores._isChop;
  const _decisionRegimeFlags=canonicalRegimeFlags(regime);
  rawSignalScores._isTrend=_decisionRegimeFlags.trend;
  rawSignalScores._isChop=_decisionRegimeFlags.chop;
  rawSignalScores._v10736regime=regime;
  const learningFeatures=directionFeatures({signals:rawSignalScores,baseUp:posterior,atrBps,secondsLeft:clockSeconds});
  return{learningFeatures,posterior:finalPosterior,rawPosterior:posterior,displayPosterior,regime,upThreshold,downThreshold,reasoning,atrBps,rsi,bb,vwap,realGapBps,drift1m,drift5m,drift15m,accel,pnlSlope,tickSlope,aggrFlow,isRugPull,isMoonshot,isPostDecay,consecutive,volRatio,channel,momentumAlign,rawSignalScores,totalSignalWeight,velocityRegime,velocityScalars:_velAdj,projectedPrice:_projectedPrice,projectedGapBps:_projectedGapBps,trajectoryAdj,windowDriftBps,mtfAlignment,mtfBonus,fgtResults,windowExhaustionPenalty,
  // V10.2.36 — Range-chop override flag + computed avg bps. Stamped on engine
  //   result for call-log capture and UI surfacing.
  _v10_2_36_rangeOverrideFired:_rangeOverrideFired,
  _v10_2_36_rangeOverrideAvgBps:_rangeOverrideAvgBps,
  // V10.2.37 — Within-window quiet-override + multi-TF alignment flags.
  //   _quietOverrideFired: realized<<expected → forced RANGE-CHOP this window
  //   _quietOverrideRatio: realized/expected (lower = quieter)
  //   _mtfUpAligned / _mtfDnAligned: whether 1m+5m+15m all confirmed trend
  _v10_2_37_quietOverrideFired:_quietOverrideFired,
  _v10_2_37_quietOverrideRatio:_quietOverrideRatio,
  _v10_2_37_quietRealizedBps:_quietRealizedBps,
  _v10_2_37_quietExpectedBps:_quietExpectedBps,
  _v10_2_37_mtfUpAligned:_mtfUpAligned,
  _v10_2_37_mtfDnAligned:_mtfDnAligned,
  // V10.2.38 — S/R touch range detection stamps.
  //   srRangeDetected: did 20-min range with ≥2 touches each side confirm?
  //   srRangeBps: total range size in bps
  //   srTouchesHigh / srTouchesLow: touch counts at each extreme
  //   srRangeHigh / srRangeLow: the level prices (for downstream UI use)
  _v10_2_38_srRangeDetected:_srRangeDetected,
  _v10_2_38_srRangeBps:_srRangeBps,
  _v10_2_38_srTouchesHigh:_srTouchesHigh,
  _v10_2_38_srTouchesLow:_srTouchesLow,
  _v10_2_38_srRangeHigh:_srRangeHigh,
  _v10_2_38_srRangeLow:_srRangeLow,
  // V10.2.39 — HTF trend filter stamps.
  //   htfFlatOverride: pre-classifier override (both HTFs flat)
  //   htfVetoFired: post-classifier veto (4h disagreed with TRENDING label)
  //   htf1hDriftBps / htf4hDriftBps: actual drift values for audit
  //   htf1hLabel / htf4hLabel: FLAT / UP / DOWN classification
  _v10_2_39_htfFlatOverride:_htfFlatOverride,
  _v10_2_39_htfVetoFired:_htfVetoFired,
  _v10_2_39_htf1hDriftBps:_htf1hDriftBps,
  _v10_2_39_htf4hDriftBps:_htf4hDriftBps,
  _v10_2_39_htf1hLabel:_htf1hLabel,
  _v10_2_39_htf4hLabel:_htf4hLabel,
  // V10.2.40 — Probabilistic regime stamps.
  //   regimeProbs: full probability distribution over all 6 regimes
  //   regimeConfidence: probability of the primary (highest) regime
  //   primaryRegime: the regime with highest probability (may differ from
  //     `regime` field, which is the hard-classifier output. They usually
  //     agree but won't always — that disagreement IS the signal that the
  //     classifier is uncertain.)
  //   dampened: whether low-confidence threshold dampening fired
  _v10_2_40_regimeProbs:_regimeProbs,
  _v10_2_40_regimeConfidence:_regimeConfidence,
  _v10_2_40_primaryRegime:_primaryRegime,
  _v10_2_40_dampened:_v10_2_40_dampened,
  // V10.2.43 — Regime-aware structure/technical correction stamps
  _v10_2_43_regimeMult:_regimeMult,
  _v10_2_43_structOrig:_structOrig,
  _v10_2_43_techOrig:_techOrig,
  // V10.2.44 — calibration haircut stamps
  _v10_2_44_haircutApplied:_haircutApplied,
  _v10_2_44_postBeforeHaircut:dirCalibrated,
  // V10.2.50 — Chop amplification telemetry
  _v10_2_50_chopAmpDelta:_v10_2_50_chopAmpDelta,
  _v10_2_50_htfChopBoost:_v10_2_50_htfChopBoost,
  _v10_2_51_chopConfluence:_v10_2_51_chopConfluence,
  // V10.3.0 — Range-fade module telemetry
  _v10_3_0_rangeData:_v10_3_0_rangeData,
  _v10_3_0_rangeFadeFired:_v10_3_0_rangeFadeFired,
  _v10_3_0_midRangeSitout:_v10_3_0_midRangeSitout,
  _v10_3_0_rangeBroken:_v10_3_0_rangeBroken,
  // V10.3.1 — Coin-flip detection + TRENDING UP penalty stamps
  _v10_3_1_coinFlip:_v10_3_1_coinFlip,
  _v10_3_1_trendingUpPenalty:_v10_3_1_trendingUpPenalty,
  // V10.3.3 telemetry: range-hybrid detector
  _v10_3_3_rangeHybrid:_v10_3_3_rangeHybrid,
  // V10.4.0 — Band-specific calibration telemetry
  _v10_4_0_calibration:_diagV104Cal,
  _v10_4_0_regimeCaps:USE_V104_CALIBRATION_TABLES?{regime:_regime,hi:_capHi,lo:_capLo}:null,
  // V10.4.3 — Multi-window directional context (mean-reversion detector)
  _v10_4_3_windowCtx:_v104_3_windowCtx,
  // V10.6.2 — Day-of-week × hour calibration shift
  _v10_6_2_dowHour:_v106_2_dowHourShift,
  // V9.7.9: diagnostic state from V9.7.x gates — stamped onto trade log entries
  //   for post-hoc audit. Tells us which gates fired on which trades, so we can
  //   answer "did the FGT cap rescue trades?" / "did the reversal damper prevent
  //   bad locks?" by filtering the call log on these flags.
  diagnosticsV97:{
    reversalDamper:_diagReversalDamper,
    fgtCounter:_diagFgtCounter,
    regimeCal:_diagRegimeCal,
  },
  // V9.10.10 → V9.11.0: chart-pattern + trend-structure + trendline-break signals,
  //   now multi-timeframe. patternsV9_10_10 stays for backward compat (1m view); the
  //   richer patternsV9_11_0 carries per-TF + confluence data.
  patternsV9_10_10:{
    chartPattern:_pattern?.pattern||null,
    chartPatternDetail:_pattern?.detail||null,
    chartPatternAdj:0,                  // V9.11.0: zeroed — actual contribution lives in patternsV9_11_0.totalAdj
    trendStructure:_trend?.trend||null,
    trendStructureStrength:_trend?.strength||0,
    trendStructureDetail:_trend?.detail||null,
    trendStructureAdj:0,                // V9.11.0: zeroed for same reason
    trendlineBreak:_tlBreak?.broke||null,
    trendlineBreakMagnitude:_tlBreak?.magnitudeBps||0,
    trendlineBreakDetail:_tlBreak?.detail||null,
    trendlineBreakAdj:0,                // V9.11.0: zeroed
    totalAdj:0,                         // V9.11.0: zeroed — see patternsV9_11_0.totalAdj
  },
  // V9.11.0: HTF pattern aggregation — per-TF results + confluence detection.
  //   This is the authoritative pattern signal as of V9.11.0. patternsV9_10_10 above
  //   is kept only for backward-compatible CSV consumers; new analysis should read
  //   patternsV9_11_0.
  patternsV9_11_0:{
    perTF:_htfPatterns.perTF,                      // {c1m, c5m, c15m} each with pattern/trend/tlBreak/rawAdj/weightedAdj/reasons
    totalAdj:_htfPatterns.totalAdj,                // total contribution to totalScore
    directionalScore:_htfPatterns.directionalScore,
    confluence:_htfPatterns.confluence,            // 0 (no confluence), 2 (2-TF agreement), 3 (all 3 agree)
    dominantDirection:_htfPatterns.dominantDirection, // 'UP'|'DOWN'|null
    confluenceBonusAdj:_htfPatterns.confluenceBonusAdj||0,
    detail:_htfPatterns.detail,
  },
  // V9.11.2: futures sentiment block. NOW SOURCED FROM bloomberg instead of the
  //   broken Bybit-direct hook. Same data semantically (funding/OI/basis); reliable
  //   delivery via OKX proxy.
  futuresV9_11_0:{
    fundingRate:bloomberg?.fundingRate??null,
    openInterest:bloomberg?.openInterest??null,
    oiDeltaPct:bloomberg?.oiChange5m??null,
    perpPrice:bloomberg?.markPrice??null,
    indexPrice:bloomberg?.indexPrice??null,
    basis:bloomberg?.basisBps!=null&&bloomberg?.indexPrice?(bloomberg.basisBps/10000)*bloomberg.indexPrice:null,
    basisPct:bloomberg?.basisBps!=null?bloomberg.basisBps/100:null,
    dataAgeMs:bloomberg?.lastUpdate?Date.now()-bloomberg.lastUpdate:null,
    dataError:bloomberg?.status==='live'?null:(bloomberg?.status||'no data'),
    // Per-signal contributions to totalScore
    fundingAdj:_futuresSignals.fundingAdj,
    oiAdj:_futuresSignals.oiAdj,
    basisAdj:_futuresSignals.basisAdj,
    totalAdj:_futAdj,
    details:_futuresSignals.details,
  },
  // V9.5.0: opponent-modeling signal exposed in output for UI display + trade-log audit
  kalshiLead:params.kalshiLead||null,
  // V6.4: conviction asymmetry — strength comparison of current vs prior swing
  convictionAsymmetry:_convictionAsymmetry,
    // V152: range-position telemetry — exposed so trade log can audit "was Tara at the edge of typical range?"
    rangeBps,
    // V3.1.7: volume-flow signal — informational, not yet integrated into posterior
    volFlow,
    // V3.1.7+: window amplitude classification (WILD/NORMAL/DEAD/OPENING)
    windowAmplitude,
    // V3.1.11: within-window 1m candle pattern (ENGULFING-BULL, CLIMAX-TOP, etc.)
    candlePattern,
    // V6.2.0: STRUCTURAL INDICATORS — primary lead signals replacing tape-following.
    //   trendChannel: ChartPrime Future Trend Channel — current channel position + trend regime.
    //   grandTrend:   HPotter Future Grand Trend — multi-hour structural projection.
    // V6.2.1: Both now include multi-TF alignment data (1m/5m/15m). The structAlignment
    //   field is the dominant count across ALL 6 readings (3 TC + 3 GT), 0-6 scale.
    trendChannel:_trendChannel,
    grandTrend:_grandTrend,
    structAlignment:{
      dir:_structDir,                    // UP / DOWN / NEUTRAL — dominant cross-TF cross-indicator direction
      count:_structAlignment,            // 0-6: how many of 6 signals (3 TC + 3 GT) agree
      tcDirs:_tcDirs,                    // ['UP','UP','DOWN'] for [1m,5m,15m] trend channel
      gtDirs:_gtDirs,                    // same for grand trend
    },
  };
};

// ═══════════════════════════════════════
// ERROR BOUNDARY
// ═══════════════════════════════════════
class ErrorBoundary extends React.Component{
  constructor(props){
    super(props);
    // V8.8.4: Capture errorInfo (component stack) too, plus a "copied" flash for the
    //   copy-to-clipboard button. The previous ErrorBoundary just stringified the error
    //   ("Cannot access 'ee' before initialization") which was useless for diagnosing
    //   the actual call site. Now we surface error.stack + componentStack and also
    //   persist them to localStorage so they survive a reload.
    this.state={hasError:false,error:null,errorInfo:null,recovering:false,copied:false};
    // V8.7.4: Track auto-recovery attempts. If we've recovered once this session
    //   and we're crashing AGAIN, recovery isn't working — fall through to manual.
    this._autoRecoveryAttempted=false;
    try{this._autoRecoveryAttempted=sessionStorage.getItem('_taraAutoRecoveryAttempted')==='1';}catch(_){}
  }
  static getDerivedStateFromError(e){return{hasError:true,error:e};}
  componentDidMount(){
    // V8.8: If we mount cleanly without crashing, clear the auto-recovery flag.
    // This lets future sessions auto-recover from crashes — without this, once any
    // crash happens, every subsequent crash this session forces manual recovery.
    if(!this.state.hasError){
      try{sessionStorage.removeItem('_taraAutoRecoveryAttempted');}catch(_){}
    }
  }
  componentDidCatch(e,i){
    console.error('Tara crash:',e,i);
    // V8.8.4: Persist the full diagnostic to localStorage so the user can retrieve it
    //   even if they accidentally hit reload before copying. Keyed with timestamp so
    //   the last few crashes are retained for context.
    try{
      const ts=new Date().toISOString();
      const payload={
        ts,
        version:typeof TARA_BUILD_VERSION!=='undefined'?TARA_BUILD_VERSION:'unknown',
        baselineVersion:typeof BASELINE_VERSION!=='undefined'?BASELINE_VERSION:'unknown',
        message:String(e?.message||e||'(no message)'),
        stack:String(e?.stack||'(no stack)'),
        componentStack:String(i?.componentStack||'(no component stack)'),
        userAgent:typeof navigator!=='undefined'?navigator.userAgent:'(no UA)',
        url:typeof location!=='undefined'?location.href:'(no URL)',
      };
      localStorage.setItem('_taraLastCrash',JSON.stringify(payload));
      // Also keep a rolling list of the last 5 crashes
      let history=[];
      try{history=JSON.parse(localStorage.getItem('_taraCrashHistory')||'[]')||[];}catch(_){}
      history.unshift(payload);
      history=history.slice(0,5);
      localStorage.setItem('_taraCrashHistory',JSON.stringify(history));
    }catch(_){}
    this.setState({errorInfo:i});
    // Background SW + cache cleanup. NO reload (V8.8.2 contract).
    try{
      if('serviceWorker' in navigator){
        navigator.serviceWorker.getRegistrations().then(regs=>{
          regs.forEach(r=>{try{r.unregister();}catch(_){}});
        }).catch(()=>{});
      }
    }catch(_){}
    try{
      if('caches' in window){
        caches.keys().then(keys=>{
          keys.forEach(k=>{try{caches.delete(k);}catch(_){}});
        }).catch(()=>{});
      }
    }catch(_){}
  }
  render(){
    if(!this.state.hasError)return this.props.children;
    const _hardRecover=async()=>{
      try{
        if('serviceWorker' in navigator){
          const regs=await navigator.serviceWorker.getRegistrations();
          await Promise.all(regs.map(r=>r.unregister().catch(()=>{})));
        }
      }catch(_){}
      try{
        if('caches' in window){
          const keys=await caches.keys();
          await Promise.all(keys.map(k=>caches.delete(k).catch(()=>{})));
        }
      }catch(_){}
      // V8.8.4: Preserve crash diagnostic across hard-recovery so user can still send it.
      let preserved=null;
      try{preserved=localStorage.getItem('_taraLastCrash');}catch(_){}
      try{localStorage.clear();}catch(_){}
      try{if(preserved)localStorage.setItem('_taraLastCrash',preserved);}catch(_){}
      try{sessionStorage.clear();}catch(_){}
      try{window.location.reload(true);}catch(_){window.location.reload();}
    };
    const _softRecover=async()=>{
      try{
        if('serviceWorker' in navigator){
          const regs=await navigator.serviceWorker.getRegistrations();
          await Promise.all(regs.map(r=>r.unregister().catch(()=>{})));
        }
      }catch(_){}
      try{
        if('caches' in window){
          const keys=await caches.keys();
          await Promise.all(keys.map(k=>caches.delete(k).catch(()=>{})));
        }
      }catch(_){}
      try{sessionStorage.removeItem('_taraAutoRecoveryAttempted');}catch(_){}
      try{window.location.reload(true);}catch(_){window.location.reload();}
    };
    // V8.8.4: Build the full diagnostic blob the user can copy.
    const _err=this.state.error;
    const _info=this.state.errorInfo;
    const _diag=[
      `Tara crash report`,
      `Build: ${typeof TARA_BUILD_VERSION!=='undefined'?TARA_BUILD_VERSION:'unknown'}`,
      `Baseline: ${typeof BASELINE_VERSION!=='undefined'?BASELINE_VERSION:'unknown'}`,
      `Time: ${new Date().toISOString()}`,
      `URL: ${typeof location!=='undefined'?location.href:'(no URL)'}`,
      `User Agent: ${typeof navigator!=='undefined'?navigator.userAgent:'(no UA)'}`,
      ``,
      `Message: ${_err?.message||_err||'(no message)'}`,
      ``,
      `Stack:`,
      _err?.stack||'(no stack)',
      ``,
      `Component stack:`,
      _info?.componentStack||'(no component stack)',
    ].join('\n');
    const _copyDiag=async()=>{
      try{
        if(navigator.clipboard&&navigator.clipboard.writeText){
          await navigator.clipboard.writeText(_diag);
        }else{
          // Fallback for older browsers / non-secure contexts
          const ta=document.createElement('textarea');
          ta.value=_diag;
          ta.style.position='fixed';
          ta.style.left='-9999px';
          document.body.appendChild(ta);
          ta.select();
          try{document.execCommand('copy');}catch(_){}
          document.body.removeChild(ta);
        }
        this.setState({copied:true});
        setTimeout(()=>{try{this.setState({copied:false});}catch(_){}},2200);
      }catch(_){}
    };
    return(
      <div className="min-h-screen bg-[#050508] text-rose-500 p-6 sm:p-8 font-mono">
        <div className="max-w-3xl">
          <h1 className="text-2xl font-bold mb-1">Tara Engine Crash</h1>
          <p className="text-[10px] uppercase tracking-[0.18em] text-[#EDEDED]/40 mb-4">{typeof TARA_BUILD_VERSION!=='undefined'?TARA_BUILD_VERSION:'unknown build'}</p>
          {/* Headline error message */}
          <pre className="bg-black p-4 rounded-lg text-xs mb-4 whitespace-pre-wrap border border-rose-500/30">{_err?.toString()||'(no error)'}</pre>
          {/* V8.8.4: Full diagnostic — stack + component stack — collapsed by default-look summary/details */}
          <details className="mb-4 bg-black/60 border border-[#2A2A34] rounded-lg text-[#EDEDED]/80" open>
            <summary className="cursor-pointer px-3 py-2 text-[11px] uppercase tracking-[0.14em] font-bold text-[#23B981]">Diagnostic — stack &amp; component trail</summary>
            <div className="px-3 pb-3 space-y-3">
              <div>
                <div className="text-[9px] uppercase tracking-[0.18em] text-[#EDEDED]/45 mb-1">JS stack</div>
                <pre className="bg-black p-2 rounded-lg text-[10px] whitespace-pre-wrap break-all border border-[#24242E] max-h-64 overflow-auto leading-snug text-[#EDEDED]/85">{_err?.stack||'(no stack)'}</pre>
              </div>
              <div>
                <div className="text-[9px] uppercase tracking-[0.18em] text-[#EDEDED]/45 mb-1">React component stack</div>
                <pre className="bg-black p-2 rounded-lg text-[10px] whitespace-pre-wrap break-all border border-[#24242E] max-h-48 overflow-auto leading-snug text-[#EDEDED]/85">{_info?.componentStack||'(captured on next render — try refreshing if blank)'}</pre>
              </div>
            </div>
          </details>
          <p className="text-xs text-[#EDEDED]/60 mb-4 max-w-2xl leading-relaxed">
            Tara hit an error during render. Service workers and browser caches have been cleared in the background. Your trade history, weights, and learning are safe in Supabase.
          </p>
          <div className="flex gap-3 flex-wrap">
            <button onClick={_copyDiag} className={'px-4 py-2 rounded-lg font-bold border transition-colors '+(this.state.copied?'bg-[#23B981]/15 border-[#23B981] text-[#23B981]':'bg-[#EDEDED]/5 border-[#2A2A34] text-[#EDEDED]/85 hover:bg-[#EDEDED]/10')}>{this.state.copied?'Copied — paste to me':'Copy diagnostic'}</button>
            <button onClick={_softRecover} className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg font-bold">Refresh Code Only (keeps data)</button>
            <button onClick={_hardRecover} className="px-4 py-2 bg-rose-500 hover:bg-rose-600 text-white rounded-lg font-bold">Hard Recovery (clears everything)</button>
          </div>
          <p className="text-[10px] text-[#EDEDED]/30 mt-6 leading-relaxed">If &ldquo;Refresh Code Only&rdquo; doesn&rsquo;t fix it, your local data may have a corrupted entry &mdash; use Hard Recovery as a last resort. Cloud-synced data will repopulate automatically on reload. The diagnostic above is also saved to <code className="text-[#EDEDED]/55">localStorage._taraLastCrash</code> so it survives reloads.</p>
        </div>
      </div>
    );
  }
}

// ═══════════════════════════════════════
// MAIN APP
// ═══════════════════════════════════════

// ── FlowPanel — extracted from TaraApp to avoid esbuild IIFE+JSX parse issues ──
function FlowPanel({showWhaleLog,setShowWhaleLog,flowSignal,tapeRef,whaleLog,bloomberg,currentPrice,timeState,timeFormat}){
  if(!showWhaleLog)return null;
  const fs=flowSignal;
  const scoreColor=fs.score>=75?'text-emerald-400':fs.score>=50?'text-amber-400':fs.score>=25?'text-[#EDEDED]/60':'text-[#EDEDED]/30';
  const barColor=fs.score>=75?'bg-emerald-500':fs.score>=50?'bg-amber-500':fs.score>=25?'bg-indigo-500':'bg-zinc-600';
  const isBuyFlow=fs.netDelta30s>0||fs.streakDir==='BUY';
  const isNote=fs.divergence;
  const hasStreak=fs.streakCount>=2;
  const isBuy=fs.streakDir==='BUY';
  const oi=bloomberg?.oiChange5m||0;
  const fr=bloomberg?.fundingRate||0;
  const frSign=fr>0?'Longs paying shorts':'Shorts paying longs';
  const frColor=fr>0?'text-emerald-400':'text-rose-400';
  const ls=bloomberg?.longShortRatio||1;
  const tape=tapeRef.current;
  const spotNet=tape.coinbase.buys-tape.coinbase.sells;
  const futNet=(tape.binanceFutures.buys-tape.binanceFutures.sells)+(tape.bybit.buys-tape.bybit.sells);
  const hasMeanDiv=Math.abs(tape.cbFlow)>0.15&&Math.abs((tape.bnFlow+tape.byFlow)*0.5)>0.15&&Math.sign(tape.cbFlow)!==Math.sign((tape.bnFlow+tape.byFlow)*0.5);
  const hasSpot=Math.abs(spotNet)>50000;
  const spotAligned=hasSpot&&Math.sign(spotNet)===Math.sign(futNet);
  let oiMsg='OI stable — no major position building.';
  let oiColor='text-[#EDEDED]/40';
  if(Math.abs(oi)>0.15){
    if(oi>0&&isBuyFlow){oiMsg='OI rising + buy flow — New longs opening. Conviction. Likely to follow through.';oiColor='text-emerald-400';}
    else if(oi<0&&isBuyFlow){oiMsg='OI falling + buy flow — Shorts covering, not fresh longs. Rally may be temporary.';oiColor='text-amber-400';}
    else if(oi>0&&!isBuyFlow){oiMsg='OI rising + sell flow — New shorts opening. Conviction sell.';oiColor='text-rose-400';}
    else if(oi<0&&!isBuyFlow){oiMsg='OI falling + sell flow — Longs exiting. Bearish unwind.';oiColor='text-amber-400';}
  }
  return(
    <div className={'fixed top-11 right-0 z-50 w-80 sm:w-96 max-h-[82vh] overflow-hidden flex flex-col bg-[#050508] border border-l border-b border-[#2A2A34] rounded-bl-xl shadow-2xl'} style={{boxShadow:'0 8px 32px rgba(0,0,0,0.6)'}}>
      <div className={'p-3 bg-[#101014] border-b border-[#24242E] flex justify-between items-center shrink-0'}>
        <div className="flex items-center gap-2">
          <div className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse"></div>
          <span className={'text-xs font-bold uppercase tracking-widest text-[#EDEDED]/70'}>Flow Intelligence</span>
          <span className={'text-[10px] text-[#EDEDED]/25 font-mono'}>futures tape · $100K+</span>
        </div>
        <button onClick={()=>setShowWhaleLog(false)} className="opacity-70 hover:opacity-100 transition-opacity p-1 rounded-lg hover:bg-[#EDEDED]/5"><span className={'text-[#EDEDED]/90 text-base font-bold'}>✕</span></button>
      </div>
      <div className="overflow-y-auto flex-1 p-3 space-y-3">

        {/* Flow Score */}
        <div className={'p-3 rounded-xl bg-[#101014] border border-[#24242E]'}>
          <div className="flex justify-between items-start mb-2">
            <div>
              <div className={`text-lg font-bold font-serif ${scoreColor}`}>{fs.score.toFixed(0)}<span className="text-xs font-sans ml-1 opacity-60">&#47;100</span></div>
              <div className={`text-xs font-bold uppercase tracking-widest ${scoreColor}`}>{fs.label}</div>
            </div>
            <div className="text-right">
              <div className={`text-xs font-bold ${isBuyFlow?'text-emerald-400':'text-rose-400'}`}>{fs.netDelta30s>0?'NET BUY':'NET SELL'} (30s)</div>
              <div className={`text-xs font-mono ${isBuyFlow?'text-emerald-300':'text-rose-300'}`}>{fs.netDelta30s>=0?'+':''}{(fs.netDelta30s*0.001).toFixed(0)}K</div>
            </div>
          </div>
          <div className={'h-1.5 bg-[#EDEDED]/10 rounded-full overflow-hidden mb-2'}>
            <div className={`h-full ${barColor} rounded-full transition-all duration-500`} style={{width:fs.score+'%'}}/>
          </div>
          {isNote&&<div className="text-[10px] text-amber-400 mt-1">Spot-futures diverging — likely basis trade, not directional</div>}
          {hasSpot&&<div className={`text-[10px] mt-1 font-bold ${spotAligned?'text-emerald-400':'text-amber-400'}`}>Spot {spotAligned?'ALIGNED':'OPPOSED'} · {spotAligned?'Higher reliability':'Lower reliability — possible hedging'}</div>}
          {!hasSpot&&<div className={'text-[10px] text-[#EDEDED]/25 mt-1'}>Spot (Coinbase): no significant activity</div>}
          <div className={'text-[10px] text-[#EDEDED]/30 mt-1'}>{fs.trend} · 90s delta: {fs.netDelta90s>=0?'+':''}{(fs.netDelta90s*0.001).toFixed(0)}K</div>
        </div>

        {/* Whale Streak */}
        <div className={`p-3 rounded-xl border ${hasStreak?(isBuy?'bg-emerald-500/5 border-emerald-500/30':'bg-rose-500/5 border-rose-500/30'):'bg-[#101014] border-[#24242E]'}`}>
          <div className={'text-[10px] uppercase tracking-widest text-[#EDEDED]/40 mb-1.5 font-bold'}>Whale Streak</div>
          {hasStreak?(
            <div>
              <div className="flex items-center gap-2 mb-1">
                <span className={`text-base font-bold font-serif ${isBuy?'text-emerald-400':'text-rose-400'}`}>{fs.streakCount}x</span>
                <span className={`text-sm font-bold ${isBuy?'text-emerald-400':'text-rose-400'}`}>{fs.streakDir}</span>
                <span className={'text-xs text-[#EDEDED]/40'}>in {fs.streakDuration}s</span>
              </div>
              <div className={'text-xs text-[#EDEDED]/60'}>${(fs.streakUSD*0.001).toFixed(0)}K total · {isBuy?'Accumulation pressure':'Distribution pressure'}</div>
              {fs.streakCount>=4&&<div className={`text-[10px] mt-1 font-bold ${isBuy?'text-emerald-400':'text-rose-400'}`}>High conviction streak — watch for price follow-through</div>}
              {fs.streakCount>=2&&fs.streakCount<4&&<div className={'text-[10px] mt-1 text-[#EDEDED]/40'}>Wait for more prints before treating as directional</div>}
            </div>
          ):(
            <div className={'text-xs text-[#EDEDED]/30 italic'}>No streak — random prints, not directional</div>
          )}
        </div>

        {/* OI Context */}
        <div className={'p-3 rounded-xl bg-[#101014] border border-[#24242E]'}>
          <div className={'text-[10px] uppercase tracking-widest text-[#EDEDED]/40 mb-1.5 font-bold'}>Open Interest Context</div>
          <div className="flex items-center gap-2 mb-1">
            <span className={`text-xs font-bold ${oiColor}`}>OI {oi>=0?'+':''}{oi.toFixed(2)}% (5m)</span>
          </div>
          <div className={`text-xs ${oiColor} leading-relaxed`}>{oiMsg}</div>
          <div className={`text-[10px] mt-2 ${frColor}`}>Funding: {(fr*100).toFixed(4)}% · {frSign} · {fr>0.01?'Heavily long-biased':fr<-0.01?'Heavily short-biased':'Neutral'}</div>
        </div>

        {/* Spot-Futures Divergence */}
        {hasMeanDiv&&(
          <div className={'p-3 rounded-xl bg-amber-500/5 border border-amber-500/25'}>
            <div className="text-[10px] uppercase tracking-widest text-amber-400 mb-1.5 font-bold">Spot-Futures Divergence</div>
            <div className="text-xs text-amber-300 mb-1">Spot (Coinbase): <strong>{tape.cbFlow>0?'BUYING':'SELLING'}</strong> · Futures: <strong>{((tape.bnFlow+tape.byFlow)*0.5)>0?'BUYING':'SELLING'}</strong></div>
            <div className={'text-[10px] text-[#EDEDED]/50 leading-relaxed'}>Opposite flows between spot and futures typically indicate basis traders — not directional conviction.</div>
          </div>
        )}

        {/* Long-Short Ratio */}
        {bloomberg?.longShortRatio&&(
          <div className={'p-3 rounded-xl bg-[#101014] border border-[#24242E]'}>
            <div className={'text-[10px] uppercase tracking-widest text-[#EDEDED]/40 mb-2 font-bold'}>Market Positioning (Binance)</div>
            <div className="flex items-center gap-2 mb-1">
              <div className={'flex-1 h-2 bg-[#EDEDED]/10 rounded-full overflow-hidden'}>
                <div className="h-full bg-emerald-500 rounded-full transition-all" style={{width:Math.min(100,(ls*100)/(ls+1))+'%'}}/>
              </div>
            </div>
            <div className="flex justify-between text-[10px]">
              <span className="text-emerald-400">Long {((ls*100)/(ls+1)).toFixed(0)}%</span>
              <span className="text-rose-400">Short {(100/(ls+1)).toFixed(0)}%</span>
            </div>
            {ls>1.5&&<div className="text-[10px] text-rose-400 mt-1">Crowd is heavily long — contrarian warning.</div>}
            {ls<0.7&&<div className="text-[10px] text-emerald-400 mt-1">Crowd is heavily short — short squeeze risk elevated.</div>}
          </div>
        )}

        {/* Raw Prints */}
        <details className="group">
          <summary className={'text-[10px] uppercase tracking-widest text-[#EDEDED]/25 cursor-pointer hover:text-[#EDEDED]/50 font-bold list-none flex items-center gap-1'}>
            <span className="group-open:rotate-90 transition-transform inline-block">▶</span>
            Raw prints ({whaleLog.length}) — futures $100K+, not directional on their own
          </summary>
          <div className="mt-2 space-y-1 max-h-40 overflow-y-auto">
            {whaleLog.length===0?<div className={'text-xs text-[#EDEDED]/30 italic'}>No prints yet</div>:whaleLog.slice(0,20).map((w,i)=>{
              const d=new Date(w.time);
              return(<div key={i} className={`flex items-center gap-2 text-xs p-1.5 rounded-lg bg-[#050508] border ${w.side==='BUY'?'border-emerald-500/15':'border-rose-500/15'}`}>
                <span className={'text-[#EDEDED]/25 font-mono shrink-0'}>{_fmtTimeTz(d,timeFormat,{hour12:false,hour:'2-digit',minute:'2-digit',second:'2-digit'})}</span>
                <span className={`font-bold text-[10px] ${w.side==='BUY'?'text-emerald-500':'text-rose-500'}`}>{w.side}</span>
                <span className={'text-[#EDEDED]/50'}>${(w.usd*0.001).toFixed(0)}K</span>
                <span className={'text-[#EDEDED]/25 text-[10px] ml-auto'}>{w.src}</span>
              </div>);
            })}
          </div>
        </details>

      </div>
    </div>
  );
}

// V10.9.3: AUTONOMOUS THEORY LAB — compact scoreboard. Read-only, zero inputs.
//   Shows per-theory n / win rate / confidence note, plus the most recent
//   round's computed calls for transparency. Weight=0 always — promotion is
//   informational only (toast elsewhere), never automatic.
function TheoryLabPanel({show,setShow,theoryStats,theoryLog}){
  if(!show)return null;
  const _labels={range:'Range Call',momentum:'Momentum Call',firstMoveFade:'First-Move-Fade',crowdFade:'Crowd-Fade'};
  const _last=theoryLog&&theoryLog.length?theoryLog[theoryLog.length-1]:null;
  const _rowColor=(wr,n)=>{
    if(n<10)return 'text-[#EDEDED]/40';
    if(wr>=0.60)return 'text-emerald-400';
    if(wr>=0.50)return 'text-[#EDEDED]/70';
    return 'text-rose-400/80';
  };
  return(
    <div className={'fixed top-11 right-0 z-50 w-80 sm:w-96 max-h-[82vh] overflow-hidden flex flex-col bg-[#050508] border border-l border-b border-[#2A2A34] rounded-bl-xl shadow-2xl'} style={{boxShadow:'0 8px 32px rgba(0,0,0,0.6)'}}>
      <div className={'p-3 bg-[#101014] border-b border-[#24242E] flex justify-between items-center shrink-0'}>
        <div className="flex items-center gap-2">
          <div className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse"></div>
          <span className={'text-xs font-bold uppercase tracking-widest text-[#EDEDED]/70'}>Theory Lab</span>
          <span className={'text-[10px] text-[#EDEDED]/25 font-mono'}>shadow · weight 0</span>
        </div>
        <button onClick={()=>setShow(false)} className="opacity-70 hover:opacity-100 transition-opacity p-1 rounded-lg hover:bg-[#EDEDED]/5"><span className={'text-[#EDEDED]/90 text-base font-bold'}>✕</span></button>
      </div>
      <div className="overflow-y-auto flex-1 p-3 space-y-3">
        <div className="text-[10px] text-[#EDEDED]/40 leading-relaxed">
          Four candidate signals scored automatically every window against the actual outcome.
          None of these affect live calls — they exist purely to find out if they would help.
        </div>
        <div className="space-y-1.5">
          {Object.entries(theoryStats||{}).map(([key,v])=>{
            const n=v?.n||0,correct=v?.correct||0;
            const wr=n>0?correct/n:0;
            return(
              <div key={key} className="flex items-center justify-between bg-[#0A0A0E] rounded-lg px-2.5 py-2 border border-[#24242E]">
                <span className={'text-xs text-[#EDEDED]/70'}>{_labels[key]||key}</span>
                <div className="flex items-center gap-2">
                  {n<30&&n>0&&<span className={'text-[9px] text-[#EDEDED]/30 italic'}>low-n</span>}
                  <span className={'text-xs font-mono font-bold '+_rowColor(wr,n)}>{n>0?Math.round(wr*100)+'%':'—'}</span>
                  <span className={'text-[10px] text-[#EDEDED]/30 font-mono'}>n={n}</span>
                </div>
              </div>
            );
          })}
        </div>
        {_last&&(
          <div className="pt-2 border-t border-[#24242E]">
            <div className={'text-[10px] uppercase tracking-wider text-[#EDEDED]/30 mb-1.5'}>Last scored window</div>
            <div className="text-[10px] text-[#EDEDED]/50 font-mono leading-relaxed space-y-0.5">
              <div>outcome: <span className={_last.outcomeDir==='UP'?'text-emerald-400':'text-rose-400'}>{_last.outcomeDir}</span>{_last.round_number_flag?<span className="ml-2 text-amber-400/70">● round#</span>:null}</div>
              <div>range: {_last.range_call??'—'} {_last.range_correct===true?'✓':_last.range_correct===false?'✗':''}</div>
              <div>momentum: {_last.momentum_call??'—'} {_last.momentum_correct===true?'✓':_last.momentum_correct===false?'✗':''}</div>
              <div>1st-move-fade: {_last.first_move_fade_call??'—'} {_last.first_move_fade_correct===true?'✓':_last.first_move_fade_correct===false?'✗':''}</div>
              <div>crowd-fade: {_last.crowd_fade_call??'—'} {_last.crowd_pct!=null?'('+Math.round(_last.crowd_pct)+'% yes)':''} {_last.crowd_fade_correct===true?'✓':_last.crowd_fade_correct===false?'✗':''}</div>
            </div>
          </div>
        )}
        {(!theoryLog||theoryLog.length===0)&&(
          <div className={'text-xs text-[#EDEDED]/30 italic'}>No rounds scored yet — results appear after the first full window completes.</div>
        )}
      </div>
    </div>
  );
}

// Flow button component — extracted to avoid nested template literals in JSX (esbuild safe)
const FlowBtn=({flowSignal,active,onClick,cls})=>{
  const isStrong=flowSignal.score>=75;
  const isEmerging=flowSignal.score>=50&&flowSignal.score<75;
  const scoreStr=isStrong?(flowSignal.label+' '+Math.round(flowSignal.score)+'/100'):'Flow Intelligence';
  const colorCls=active
    ?'bg-purple-500/20 border-purple-500/40 text-purple-400'
    :isStrong
    ?'bg-rose-500/15 border-rose-500/40 text-rose-400 animate-pulse'
    :isEmerging
    ?'bg-amber-500/10 border-amber-500/30 text-amber-400'
    :'border-[#24242E] text-[#EDEDED]/40 hover:text-purple-400';
  // V13.4.224: `cls` used to be a two-value mode switch — anything other than the
  //   exact string 'hidden sm:flex' was thrown away. The mobile bar asked for
  //   `flex-1 min-w-0` so the three buttons would share the row evenly, and it was
  //   silently dropped: FLOW and THEORY sized to their labels while the one button
  //   that did honour flex-1 stretched to 171px next to their 74px. Caller classes
  //   are now appended rather than discarded.
  const baseCls=cls==='hidden sm:flex'
    ?'hidden sm:flex items-center gap-1 p-1.5 rounded-lg border text-xs transition-all '
    :'items-center gap-1 justify-center px-2 py-1.5 rounded-lg text-xs transition-all '+(cls||'flex')+' ';
  return(
    <button onClick={onClick} className={baseCls+colorCls} title={scoreStr}>
      FLOW
      {isStrong&&<span className="w-1.5 h-1.5 rounded-full bg-rose-400 animate-pulse ml-0.5"/>}
      {!isStrong&&isEmerging&&<span className="w-1.5 h-1.5 rounded-full bg-amber-400 ml-0.5"/>}
    </button>
  );
};

// V10.9.3: Theory Lab toggle button — mirrors FlowBtn's compact style.
const TheoryLabBtn=({active,onClick,cls})=>{
  const colorCls=active
    ?'bg-violet-500/20 border-violet-500/40 text-violet-400'
    :'border-[#24242E] text-[#EDEDED]/40 hover:text-violet-400';
  // V13.4.224: `cls` used to be a two-value mode switch — anything other than the
  //   exact string 'hidden sm:flex' was thrown away. The mobile bar asked for
  //   `flex-1 min-w-0` so the three buttons would share the row evenly, and it was
  //   silently dropped: FLOW and THEORY sized to their labels while the one button
  //   that did honour flex-1 stretched to 171px next to their 74px. Caller classes
  //   are now appended rather than discarded.
  const baseCls=cls==='hidden sm:flex'
    ?'hidden sm:flex items-center gap-1 p-1.5 rounded-lg border text-xs transition-all '
    :'items-center gap-1 justify-center px-2 py-1.5 rounded-lg text-xs transition-all '+(cls||'flex')+' ';
  return(
    <button onClick={onClick} className={baseCls+colorCls} title="Theory Lab — shadow signals, weight 0">
      THEORY
    </button>
  );
};


// QualityGateCard — extracted to avoid esbuild IIFE+template-literal slash bug
function QualityGateCard({qualityGate,regime,session}){
  if(!qualityGate)return null;
  const c=qualityGate.color;
  // V2.1: amber tier (mid quality 50-74) recolored to copper for two-tone palette consistency.
  // Emerald (high) and rose (low) preserved — those are success/failure direction, not friction.
  const bgStyle={
    emerald:{background:'rgba(24,128,96,0.05)',border:'1px solid rgba(24,128,96,0.25)'},
    amber:{background:T2_COPPER_BG,border:'1px solid '+T2_COPPER_BORDER},
    rose:{background:'rgba(239,68,68,0.05)',border:'1px solid rgba(239,68,68,0.20)'},
  }[c]||{};
  const txtStyle=c==='amber'?{color:T2_COPPER}:{};
  const txtCls=c==='emerald'?'text-emerald-400':c==='amber'?'':'text-rose-400';
  const barCls=c==='emerald'?'bg-emerald-500':c==='amber'?'':'bg-rose-500';
  const barStyle=c==='amber'?{background:T2_COPPER}:{};
  const msg=qualityGate.score>=75
    ?('High-confidence setup. '+(regime||'')+(session?' in '+session:'')+' historically reliable.')
    :qualityGate.score>=55
    ?'Moderate setup. Trade smaller or wait for stronger signal.'
    :('Low quality — '+(regime||'')+' in '+(session||'')+' has weak historical WR. Consider sitting out.');
  return(
    <div className="mb-2 p-2.5 rounded-lg" style={bgStyle}>
      <div className="flex items-center justify-between mb-1.5">
        <span className="text-[10px] uppercase tracking-widest font-bold" style={{color:'rgba(237,237,237,0.3)'}}>Quality Gate</span>
        <span className={`text-xs font-bold uppercase tracking-wider ${txtCls}`} style={txtStyle}>{qualityGate.label} — {qualityGate.score?.toFixed(0)}&#47;100</span>
      </div>
      <div className="h-1 rounded-full overflow-hidden mb-1.5" style={{background:'rgba(237,237,237,0.1)'}}>
        <div className={`h-full rounded-full transition-all duration-700 ${barCls}`} style={{width:(qualityGate.score||0)+'%',...barStyle}}/>
      </div>
      <div className={`text-[10px] ${txtCls}`} style={{opacity:0.7,...txtStyle}}>{msg}</div>
    </div>
  );
}

// V10.2.10 — Position reconciliation drift banner. Shows ONLY when the poll
//   detected drift between Tara's tracked state and Kalshi's actual book.
//   Three drift kinds (see TaraApp's positionReconciliation comment):
//     - phantom-auto / phantom-manual: Tara has it, Kalshi doesn't
//     - count-mismatch: counts disagree
//     - unknown-to-tara: Kalshi has a position Tara doesn't know about
//   Each line shows a concise action hint. Dismissable for the rest of this
//   poll cycle (next poll will re-render if drift persists).
function PositionReconciliationBanner({positionReconciliation,onRetryNow,onExitNow}){
  const[dismissedAt,setDismissedAt]=React.useState(0);
  // V13.4.343: manual retry + manual exit. Retry re-runs the exact same
  //   check the 30s poll does (onRetryNow), so "checking" here just means
  //   "a check is in flight" -- the banner itself refreshes from the real
  //   lastCheckAt/driftDetails once it resolves. Exit is two-click armed
  //   (matches this app's existing ARMED/LIVE language elsewhere) so a
  //   stray click can't fire a real closing order.
  const[retrying,setRetrying]=React.useState(false);
  const[exitArmed,setExitArmed]=React.useState(false);
  const[exitBusy,setExitBusy]=React.useState(false);
  const[exitResult,setExitResult]=React.useState(null);
  const _handleRetry=()=>{
    if(retrying||!onRetryNow)return;
    setRetrying(true);
    try{onRetryNow();}catch(_){}
    setTimeout(()=>setRetrying(false),2500);
  };
  const _handleExitClick=()=>{
    if(exitBusy)return;
    if(!exitArmed){
      setExitArmed(true);
      setTimeout(()=>setExitArmed(false),4000);
      return;
    }
    setExitArmed(false);
    setExitBusy(true);
    setExitResult(null);
    Promise.resolve(onExitNow?.())
      .then(r=>setExitResult(r||{ok:false,reason:'no-handler'}))
      .catch(e=>setExitResult({ok:false,reason:String(e?.message||e)}))
      .finally(()=>setExitBusy(false));
  };
  const _rec=positionReconciliation;
  if(!_rec||_rec.status!=='drift'||!Array.isArray(_rec.driftDetails)||_rec.driftDetails.length===0)return null;
  // If user dismissed this poll's banner, suppress until next poll arrives
  if(dismissedAt>0&&dismissedAt>=(_rec.lastCheckAt||0))return null;
  // V13.4.322: two new kinds for the self-healing 'unknown' fill-status check
  //   (see _computeDrift below) -- resolved-nofill is genuinely good news
  //   (green), resolved-filled means a real, unmanaged position was just
  //   confirmed and needs the user's attention (red, same as phantom-auto).
  // V13.4.328: unknown-ticker-mismatch/ticker-mismatch-auto are unresolved
  //   ambiguity (Kalshi shows a real position on the same side, just not
  //   under the tracked ticker) -- red/urgent like the other unresolved
  //   kinds, never the reassuring green a clean "confirmed no fill" gets.
  // V13.4.329: the two '-pending' kinds are a single-poll miss awaiting a
  //   second confirmation (see _driftMiss in the reconciliation poll) --
  //   amber, same as SIT-OUT's "watch, not alarmed, not confirmed" tier.
  //   Deliberately NOT the green a genuine 'confirmed no fill' gets.
  const _kindColor=(k)=>k==='phantom-auto-pending'||k==='unknown-nofill-pending'||k==='exit-verification-pending'?T2_SITOUT:k==='unknown-to-tara'||k==='unknown-resolved-nofill'||k==='exit-confirmed-flat'?'#23B981':k==='count-mismatch-auto'||k==='count-mismatch-manual'?'#23B981':'#E8455E';
  const _kindLabel=(k)=>{
    if(k==='phantom-auto')return 'phantom (auto)';
    if(k==='phantom-auto-pending')return 'verifying — recheck pending';
    if(k==='phantom-manual')return 'phantom (manual)';
    if(k==='count-mismatch-auto')return 'count mismatch (auto)';
    if(k==='count-mismatch-manual')return 'count mismatch (manual)';
    if(k==='unknown-to-tara')return 'kalshi-only';
    if(k==='unknown-resolved-filled')return 'confirmed filled';
    if(k==='unknown-resolved-nofill')return 'confirmed no fill';
    if(k==='unknown-nofill-pending')return 'verifying — recheck pending';
    if(k==='unknown-ticker-mismatch')return 'ticker mismatch — verify';
    if(k==='ticker-mismatch-auto')return 'ticker mismatch — verify';
    if(k==='side-mismatch-auto')return 'side mismatch — verify';
    if(k==='exit-unconfirmed-open')return 'exit unconfirmed — still open';
    if(k==='exit-verification-pending')return 'exit check pending';
    if(k==='exit-confirmed-flat')return 'exit confirmed flat';
    return k;
  };
  const _isConfirmedNoFill=_rec.driftDetails.length>0&&_rec.driftDetails.every(d=>d&&d.kind==='unknown-resolved-nofill');
  const _isConfirmedExitFlat=_rec.driftDetails.length>0&&_rec.driftDetails.every(d=>d&&d.kind==='exit-confirmed-flat');
  const _isPending=_rec.driftDetails.length>0&&_rec.driftDetails.every(d=>d&&(
    d.kind==='phantom-auto-pending'||d.kind==='unknown-nofill-pending'||d.kind==='exit-verification-pending'));
  const _isConfirmedFlat=_isConfirmedNoFill||_isConfirmedExitFlat;
  const _bannerColor=_isConfirmedFlat?'#23B981':_isPending?T2_SITOUT:'#E8455E';
  const _bannerBg=_isConfirmedFlat?'rgba(35,185,129,0.07)':_isPending?'rgba(212,162,76,0.08)':'rgba(232,69,94,0.08)';
  const _bannerBorder=_isConfirmedFlat?'rgba(35,185,129,0.28)':_isPending?'rgba(212,162,76,0.30)':'rgba(232,69,94,0.35)';
  return (
    <div className="mb-2 p-2.5 rounded-lg" style={{background:_bannerBg,border:`1px solid ${_bannerBorder}`}}>
      <div className="flex items-baseline justify-between mb-1.5">
        <span className="text-[10px] uppercase font-bold tracking-wider" style={{color:_bannerColor}}>
          {_isConfirmedNoFill?'✓ No fill confirmed':_isConfirmedExitFlat?'✓ Position flat confirmed':_isPending?'◌ Position check pending':'⚠ Position drift detected'}
        </span>
        <div className="flex items-center gap-2.5">
          {onRetryNow&&(
            <button
              type="button"
              onClick={_handleRetry}
              disabled={retrying}
              className="text-[10px] hover:opacity-100 transition-opacity"
              style={{color:'rgba(237,237,237,0.55)',opacity:retrying?0.4:0.7,background:'none',border:'none',cursor:retrying?'default':'pointer',padding:0}}
              title="run this check again now, instead of waiting for the next poll"
            >{retrying?'checking…':'↻ retry'}</button>
          )}
          <button
            type="button"
            onClick={()=>setDismissedAt(Date.now())}
            className="text-[10px] hover:opacity-100 transition-opacity"
            style={{color:'rgba(237,237,237,0.55)',opacity:0.7,background:'none',border:'none',cursor:'pointer',padding:0}}
            title="dismiss until next poll"
          >dismiss</button>
        </div>
      </div>
      <div className="text-[10px] mb-1.5 leading-relaxed" style={{color:'rgba(237,237,237,0.65)'}}>
        {_isConfirmedNoFill
          ?`Kalshi confirms this AutoTrade attempt did not create a live position. Last checked ${Math.max(0,Math.floor((Date.now()-_rec.lastCheckAt)/1000))}s ago.`
          :_isConfirmedExitFlat
          ?`Kalshi shows no position on two consecutive checks after the uncertain exit. Last checked ${Math.max(0,Math.floor((Date.now()-_rec.lastCheckAt)/1000))}s ago.`
          :_isPending
          ?`Kalshi is rechecking the position before Tara declares a fill or no-fill. Last checked ${Math.max(0,Math.floor((Date.now()-_rec.lastCheckAt)/1000))}s ago.`
          :`Tara's tracked position${_rec.driftDetails.length===1?'':'s'} ${_rec.driftDetails.length===1?'doesn\'t':'don\'t'} match what Kalshi shows. Last checked ${Math.max(0,Math.floor((Date.now()-_rec.lastCheckAt)/1000))}s ago.`}
      </div>
      <div className="space-y-1.5">
        {_rec.driftDetails.map((d,i)=>(
          <div key={i} className="text-[11px] py-1 pl-2" style={{borderLeft:`2px solid ${_kindColor(d.kind)}`}}>
            <div className="flex items-baseline gap-2 mb-0.5">
              <span className="text-[9px] uppercase font-bold tracking-wider" style={{color:_kindColor(d.kind)}}>{_kindLabel(d.kind)}</span>
              <span className="text-[10px] tabular-nums" style={{color:'rgba(237,237,237,0.55)'}}>Tara:{d.taraCount} · Kalshi:{d.kalshiCount}</span>
            </div>
            <div className="text-[11px] leading-relaxed" style={{color:'rgba(237,237,237,0.85)'}}>
              {d.note}
            </div>
          </div>
        ))}
      </div>
      <div className="text-[9px] mt-1.5" style={{color:'rgba(237,237,237,0.45)'}}>
        Full diff in console: <code style={{color:'rgba(237,237,237,0.65)'}}>{'window.__taraPositionReconciliation()'}</code>
      </div>
      {onExitNow&&!_isConfirmedNoFill&&!_isPending&&(
        <div className="mt-2 pt-2" style={{borderTop:'1px solid rgba(232,69,94,0.2)'}}>
          <button
            type="button"
            onClick={_handleExitClick}
            disabled={exitBusy}
            className="w-full py-1.5 rounded text-[10px] uppercase font-bold tracking-wider transition-all"
            style={exitBusy
              ?{background:'rgba(237,237,237,0.08)',color:'rgba(237,237,237,0.4)',border:'1px solid rgba(237,237,237,0.15)',cursor:'default'}
              :exitArmed
              ?{background:'#E8455E',color:'#fff',border:'1px solid #E8455E',cursor:'pointer'}
              :{background:'rgba(232,69,94,0.12)',color:'#E8455E',border:'1px solid rgba(232,69,94,0.4)',cursor:'pointer'}}
            title="places a real closing order against Tara's tracked position, if one exists"
          >{exitBusy?'exiting…':exitArmed?'tap again to confirm exit':'exit position'}</button>
          {exitResult&&(
            <div className="text-[10px] mt-1 leading-relaxed" style={{color:exitResult.ok?'#23B981':'rgba(237,237,237,0.65)'}}>
              {exitResult.ok
                ?'Exit order placed and confirmed filled.'
                :exitResult.reason==='no-position'
                ?'Nothing to exit — no tracked filled position for this window.'
                :`Exit did not complete: ${exitResult.reason}`}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// SyncButtons — extracted to avoid esbuild IIFE+shadow class slash bug
function SyncButtons({userPosition,handleManualSync}){
  const upActive=userPosition==='UP';
  const dnActive=userPosition==='DOWN';
  const upCls='flex-1 py-2 border rounded-lg text-xs uppercase font-bold tracking-wide transition-all '+(upActive?'bg-emerald-600 text-white border-emerald-400':'border-[#24242E]');
  const dnCls='flex-1 py-2 border rounded-lg text-xs uppercase font-bold tracking-wide transition-all '+(dnActive?'bg-rose-600 text-white border-rose-400':'border-[#24242E]');
  return(
    <div className="flex gap-2">
      <button onClick={()=>handleManualSync('UP')} className={upCls}
        style={upActive?{boxShadow:'0 0 15px rgba(24,128,96,0.3)'}:{}}>ENTERED UP</button>
      <button onClick={()=>handleManualSync('DOWN')} className={dnCls}>ENTERED DOWN</button>
    </div>
  );
}

// ── PredictionContent — extracted from TaraApp to fix esbuild JSX parsing ──
function PredictionContent(props){
  const {
    strikeConfirmed,strikeMode,targetMargin,
    isLoading,analysis,currentPrice,
    qualityGate,userPosition,timeState,streakData,
    manualKalshiEntry,setManualKalshiEntry, // V10.2.9: manual fill recorder
    handleManualSync,getMarketSessions,executeAction,
    broadcastSignalManual,discordWebhook,regimeDirWR,
    kalshiYesPrice,
    newsSentiment, // V145: dual-source news scanner output (geoRisk, geoTopic, source)
    taraCall,taraScorecards,windowType, // V3.2.4: Tara's Call surface
    brtiApprox, // V10.9.10: CF-Benchmark blend health for REF badge
    scalperPanelEl, // V9.17.2: when provided, replaces the engine prediction hero
  }=props;
  // V113: Track local broadcast state (so button shows "Sent ✓" after click)
  const[broadcasted,setBroadcasted]=React.useState({key:'',sent:false});
  React.useEffect(()=>{
    // Reset broadcast button when window changes or prediction changes meaningfully
    const wKey=timeState?.startWindow||timeState?.nextWindow||0;
    const dir=analysis?.prediction?.includes('UP')?'UP':analysis?.prediction?.includes('DOWN')?'DOWN':'';
    const lockType=analysis?.lockInfo?'LOCK':'SIG';
    const key=`${lockType}:${dir}:${wKey}`;
    if(broadcasted.key!==key){
      setBroadcasted({key,sent:false});
    }
  },[analysis?.lockInfo?.lockedAt,analysis?.prediction,timeState?.startWindow,timeState?.nextWindow]);

  // ── Mode 1: Strike entry pending ──
  if(!strikeConfirmed&&strikeMode==='manual'&&targetMargin>0){
    return(
      <div className="flex-1 flex flex-col items-center justify-center gap-2">
        <div className={'text-xs uppercase tracking-widest text-indigo-400/70 font-bold animate-pulse'}>Enter strike price</div>
        <div className="text-2xl font-serif text-white">${(()=>{const _d=targetMargin>=1000?0:targetMargin>=10?2:targetMargin>=1?3:5;return targetMargin.toLocaleString(undefined,{minimumFractionDigits:_d,maximumFractionDigits:_d});})()}</div>
        <div className={'text-xs text-[#EDEDED]/30'}>Press OK or Enter to confirm · Tara will scan after</div>
      </div>
    );
  }

  // ── Mode 2: Loading ──
  if(isLoading||!analysis){
    return(
      <div className={'flex-1 flex items-center justify-center text-[#EDEDED]/30 font-serif animate-pulse'}>Connecting...</div>
    );
  }

  // ── Mode 3: Full prediction display ──
  // Build lock price status (extracted from IIFE)
  // V7.2.1: Only show when user has actually entered a position. analysis.lockInfo reflects
  //   the engine's internal lock — without this guard the user saw "Locked at X — Now Y ·
  //   IN PROFIT" even when they were sitting the trade out, which read as if they had a
  //   position they didn't have.
  let lockPriceStatus=null;
  if(userPosition&&analysis.lockInfo&&currentPrice){
    const lp=analysis.lockInfo.lockPrice;
    const dir=analysis.lockInfo.dir;
    const winning=(dir==='DOWN'&&currentPrice<lp)||(dir==='UP'&&currentPrice>lp);
    const statusTxt=winning?'IN PROFIT':'ADVERSE';
    const statusCls=winning?'text-emerald-400':'text-rose-400';
    const arrow=winning?'▲':'▼';
    lockPriceStatus=(
      <div style={{fontSize:'12px',opacity:0.4,marginTop:'4px'}}>
        Locked at {lp.toFixed(0)} — Now {currentPrice.toFixed(0)}
        <span style={{marginLeft:'8px',fontWeight:'bold'}} className={statusCls}>
          {arrow} {statusTxt}
        </span>
      </div>
    );
  }

  // Build entry checklist items
  const checklistItems=[
    {ok:targetMargin>0,label:'Strike confirmed'},
    {ok:(analysis.lockInfo?.lockedPosterior>60||analysis.lockInfo?.lockedPosterior<40),label:`Posterior ${analysis.lockInfo?.lockedPosterior?.toFixed(0)||'—'}% — strong signal`},
    {ok:(timeState.minsRemaining>=2),label:`${timeState.minsRemaining}m ${timeState.secsRemaining}s remaining (≥2m needed)`},
    {ok:!streakData.warning,label:streakData.warning?`${streakData.streak}-loss streak — proceed with caution`:'No cold streak'},
  ];

  const showFormingProgress=!analysis.lockInfo&&(analysis.prediction.includes('FORMING')||analysis.prediction.includes('ANALYZING')||analysis.prediction==='SEARCHING...');
  const formingDir=analysis.prediction.includes('UP');
  const formingCount=formingDir?analysis.bullCount:analysis.bearCount;
  const formingPct=Math.min(100,(formingCount/analysis.consecutiveNeeded)*100);
  const formingBarCls='h-full rounded-full transition-all duration-500 '+(formingDir?'bg-emerald-500/60':'bg-rose-500/60');
  // V3.1.4: Two heading variants — the original "hero" size for short single-word states
  // (UP - CONFIRMED, DOWN, SEARCHING) and a tighter size for longer multi-word titles
  // (REJECTED, BLACKOUT, etc.) so they don't wrap into 3 lines on narrow viewports.
  const headingCls='prediction-heading text-3xl sm:text-4xl md:text-5xl font-serif font-bold leading-none tracking-tight '+(analysis.textColor||'')+' drop-shadow-lg';
  const headingClsCompact='prediction-heading text-2xl sm:text-3xl md:text-4xl font-serif font-bold leading-tight tracking-tight '+(analysis.textColor||'')+' drop-shadow-lg';

  return(
    <div className="flex flex-col flex-1 gap-3">
      {/* V13.4.314: the scalper/auto-exec panel that used to mount here (via
          the scalperPanelEl prop) now renders inside ThisTradeCard itself, in
          the left column, so the two read as one card instead of two side by
          side ("literally combine [auto-exec] with Tara's call"). This branch
          permanently takes the null side -- the old inline "engine prediction
          hero" JSX below (DOWN-CONFIRMED big text, WHAT TARA SEES, score
          chips, Send LOCK button) was already dead/unreachable at this call
          site before this version (scalperPanelEl was always truthy), so
          nothing that was actually on screen changes here. Quality gate,
          entry checklist, sync buttons, and the position-exit TaraAdvisorPanel
          below are untouched -- they were always siblings of this branch, not
          part of it. */}
      {true?null:(
      <div className="flex flex-col items-center text-center pt-1">
        {/* V114: Macro event banner - shows when in BLACKOUT/OBSERVE/ENHANCED state */}
        {(()=>{
          if(typeof getMacroEventState!=='function')return null;
          const ms=getMacroEventState();
          if(ms.state==='CLEAR')return null;
          const cfg={
            BLACKOUT:{cls:'bg-rose-500/15 border-rose-500/40 text-rose-300',icon:'🔴',label:'BLACKOUT'},
            OBSERVE: {cls:'bg-amber-500/20 border-amber-500/50 text-amber-300 animate-pulse',icon:'⚠️',label:'OBSERVE ONLY'},
            ENHANCED:{cls:'bg-emerald-500/15 border-emerald-500/40 text-emerald-300',icon:'⚡',label:'POST-EVENT'},
          }[ms.state];
          if(!cfg)return null;
          const detail=ms.minutesUntil>0?`in ${ms.minutesUntil}m`:`${Math.abs(ms.minutesUntil||0)}m ago`;
          return(<div className={'mb-2 px-2 py-1.5 rounded-lg border text-[10px] font-bold uppercase tracking-wider w-full text-center '+cfg.cls}>
            {cfg.icon} {cfg.label} · {ms.event.name} {detail}
          </div>);
        })()}
        <div className="flex items-center gap-2 mb-1 flex-wrap justify-center">
          <span className={'text-xs text-[#EDEDED]/40 uppercase tracking-[0.2em] font-bold'}>Prediction</span>
          {analysis.regime&&(
            <span className={'text-xs text-indigo-400 uppercase tracking-wide bg-indigo-500/10 border border-indigo-500/20 px-2 py-1 rounded-lg'}>{analysis.regime}</span>
          )}
          {/* V134: Regime-Direction WR warning */}
          {analysis.lockInfo&&regimeDirWR&&(()=>{
            const k=analysis.regime+'-'+analysis.lockInfo.dir;
            const stat=regimeDirWR[k];
            if(!stat||stat.n<5)return null;
            const pct=Math.round(stat.rate*100);
            if(pct>=70)return(<span className={'text-xs uppercase tracking-wide px-2 py-1 rounded-lg border font-bold text-emerald-300 bg-emerald-500/10 border-emerald-500/30'} title={`${stat.wins}W in ${stat.n} trades — strong historical edge`}>HIST {pct}%</span>);
            if(pct<=50)return(<span className={'text-xs uppercase tracking-wide px-2 py-1 rounded-lg border font-bold text-rose-300 bg-rose-500/15 border-rose-500/40 animate-pulse'} title={`${stat.wins}W in ${stat.n} trades — weak historical combo, consider sitting out`}>⚠ HIST {pct}%</span>);
            return(<span className={'text-xs uppercase tracking-wide px-2 py-1 rounded-lg border font-bold text-amber-300 bg-amber-500/10 border-amber-500/30'} title={`${stat.wins}W in ${stat.n} trades`}>HIST {pct}%</span>);
          })()}
          {/* V134: Trajectory direction hint */}
          {Math.abs(analysis.trajectoryAdj||0)>=5&&(
            (()=>{
              const adj=analysis.trajectoryAdj;
              const isUp=adj>0;
              return(<span className={'text-xs uppercase tracking-wide px-2 py-1 rounded-lg border font-bold '+(isUp?'text-emerald-300 bg-emerald-500/10 border-emerald-500/30':'text-rose-300 bg-rose-500/10 border-rose-500/30')} title={`Forward trajectory bias: ${isUp?'UP':'DOWN'} ${Math.abs(adj).toFixed(1)} pts. Tara is reading where price is heading, not just where it is.`}>
                {isUp?'↗':'↘'} TRAJ {isUp?'+':''}{adj.toFixed(0)}
              </span>);
            })()
          )}
          {/* V3.1.7: Volume-flow signal chip — informational, shows whether tape volume is
                     confirming or failing the price move. Hidden when neutral/quiet/insufficient. */}
          {analysis.volFlow&&!['INSUFFICIENT','QUIET','NEUTRAL'].includes(analysis.volFlow.label)&&(
            (()=>{
              const vf=analysis.volFlow;
              const isConfirm=vf.label==='BUY-CONFIRMED'||vf.label==='SELL-CONFIRMED';
              const isFail=vf.label==='BUY-FAILING'||vf.label==='SELL-FAILING';
              const isDiv=vf.label.includes('DIVERGENT');
              const cls=isConfirm
                ? 'text-emerald-300 bg-emerald-500/10 border-emerald-500/30'
                : isFail||isDiv
                ? 'text-amber-300 bg-amber-500/10 border-amber-500/30'
                : 'text-[#EDEDED]/50 bg-[#EDEDED]/5 border-[#2A2A34]';
              const icon=vf.label.startsWith('BUY')?'↑':vf.label.startsWith('SELL')?'↓':'·';
              return(<span className={'text-xs uppercase tracking-wide px-2 py-1 rounded-lg border font-bold '+cls} title={`Volume-flow: ${vf.context}. Tells you whether tape volume is confirming or failing the current price move. INFORMATIONAL ONLY — not yet integrated into Tara's posterior decision.`}>
                {icon} VOL {vf.label.replace('BUY-','').replace('SELL-','')}
              </span>);
            })()
          )}
          {/* V3.1.11: Within-window candle pattern chip — shows 1m candle structure since
                window open. Bullish patterns emerald, bearish amber/rose, neutral dim. */}
          {analysis.candlePattern&&!['INSUFFICIENT','OPENING','MIXED'].includes(analysis.candlePattern.label)&&(
            (()=>{
              const cp=analysis.candlePattern;
              const isBull=cp.score>10;
              const isBear=cp.score<-10;
              const cls=isBull
                ? 'text-emerald-300 bg-emerald-500/10 border-emerald-500/30'
                : isBear
                ? 'text-rose-300 bg-rose-500/10 border-rose-500/30'
                : 'text-[#EDEDED]/55 bg-[#EDEDED]/5 border-[#2A2A34]';
              const icon=
                cp.label==='ENGULFING-BULL'||cp.label==='CLIMAX-BOTTOM'?'⤴':
                cp.label==='ENGULFING-BEAR'||cp.label==='CLIMAX-TOP'?'⤵':
                cp.label==='STAIR-UP'?'↗':
                cp.label==='STAIR-DOWN'?'↘':
                cp.label==='COMPRESSION'?'⊜':
                cp.label.includes('EXHAUSTION')?'⤓':
                cp.label==='BULL-LEAN'?'↑':
                cp.label==='BEAR-LEAN'?'↓':
                '·';
              const tooltip=`Candle pattern (last 1m candles since window open): ${cp.context}. INFORMATIONAL — not yet feeding into posterior. Track per-pattern WR before integrating.`;
              return(<span className={'text-xs uppercase tracking-wide px-2 py-1 rounded-lg border font-bold '+cls} title={tooltip}>
                {icon} {cp.label.replace('ENGULFING-','').replace('CLIMAX-','').replace('EXHAUSTION-','EXH-').replace('STAIR-','')}
              </span>);
            })()
          )}
          {/* V3.1.7+/V3.1.9: Window type chip — shows what kind of 15m window we're in.
                Combines amplitude × structure × motion-distribution into a single label.
                Tooltip shows the underlying measurements for transparency. */}
          {analysis.windowAmplitude&&analysis.windowAmplitude.label!=='OPENING'&&(
            (()=>{
              const wa=analysis.windowAmplitude;
              const lbl=wa.label;
              // Color semantics:
              //   TRENDING / GRIND / LATE-BREAK — directional, lock-friendly → emerald
              //   WHIPSAW / SPIKE-FADE — hostile to locks → amber
              //   RANGE — mean-reverts, neutral → indigo
              //   DEAD — quiet, dim
              const cls=(lbl==='TRENDING'||lbl==='GRIND'||lbl==='LATE-BREAK')
                ? 'text-emerald-300 bg-emerald-500/10 border-emerald-500/30'
                : (lbl==='WHIPSAW'||lbl==='SPIKE-FADE')
                ? 'text-amber-300 bg-amber-500/10 border-amber-500/30'
                : (lbl==='RANGE')
                ? 'text-indigo-300 bg-indigo-500/10 border-indigo-500/30'
                : (lbl==='DEAD')
                ? 'text-[#EDEDED]/50 bg-[#EDEDED]/5 border-[#2A2A34]'
                : 'text-[#EDEDED]/60 bg-[#EDEDED]/5 border-[#2A2A34]';
              const icon=
                lbl==='TRENDING'?'↗':
                lbl==='WHIPSAW'?'⇌':
                lbl==='SPIKE-FADE'?'⇡':
                lbl==='LATE-BREAK'?'⇣':
                lbl==='GRIND'?'→':
                lbl==='RANGE'?'⇄':
                lbl==='DEAD'?'·':
                '~';
              const desc=
                lbl==='TRENDING'?'Smooth one-direction motion. Lock-friendly. Release threshold 35bps.':
                lbl==='WHIPSAW'?'High amplitude, multiple reversals. Lock-hostile — both directions visited. Release threshold relaxed to 50bps to ride out noise.':
                lbl==='SPIKE-FADE'?'Big move early, mean-reverting now. Late locks risky. Release threshold tightened to 25bps.':
                lbl==='LATE-BREAK'?'Quiet early, breaking out late. Strong gap commitment. Standard 30bps release.':
                lbl==='GRIND'?'Slow steady drift. Trend signals work but slowly. Standard 30bps release.':
                lbl==='RANGE'?'Symmetric oscillation around open. Mean-reversion plays. Tighter 20bps release.':
                lbl==='DEAD'?'Minimal motion. Almost any move is meaningful. Tightest 15bps release.':
                'Window character not yet classified.';
              const tooltip=`${wa.context}. ${desc} INFORMATIONAL — directly affects lock release threshold; not yet integrated into entry posterior.`;
              return(<span className={'text-xs uppercase tracking-wide px-2 py-1 rounded-lg border font-bold '+cls} title={tooltip}>
                {icon} {lbl}
              </span>);
            })()
          )}
          {/* V138: Current trading session — shows flag, session name, day×session rating */}
          {analysis.session&&(
            (()=>{
              const sess=analysis.session;
              const rating=analysis.sessionDayRating||'C';
              const adj=analysis.sessionDayAdj||0;
              const cfg={
                'EU':       {flag:'🌍',cls:'text-blue-300 bg-blue-500/10 border-blue-500/30'},
                'ASIA':     {flag:'🌏',cls:'text-amber-300 bg-amber-500/10 border-amber-500/30'},
                'US':       {flag:'🌎',cls:'text-emerald-300 bg-emerald-500/10 border-emerald-500/30'},
                'OFF-HOURS':{flag:'🌑',cls:'text-zinc-400 bg-zinc-500/10 border-zinc-500/30'},
              }[sess]||{flag:'🌐',cls:'text-zinc-400 bg-zinc-500/10 border-zinc-500/30'};
              // V138: poor day-session combos get warning color treatment
              const warnCls=(rating==='D'||rating==='F')?'text-rose-300 bg-rose-500/10 border-rose-500/40 animate-pulse':null;
              const goodCls=(rating==='A')?'text-emerald-300 bg-emerald-500/15 border-emerald-500/50':null;
              const cls=warnCls||goodCls||cfg.cls;
              const ratingDesc={A:'Strong historical performance',B:'Good historical performance',C:'Neutral — average timing',D:'Below average — be selective',F:'Poor historical performance — consider sitting out'}[rating]||'';
              const tooltip=`Active session: ${sess}\nToday × ${sess} rating: ${rating} (${adj>0?'+':''}${adj} quality adjustment)\n\n${ratingDesc}\nKey: ${analysis.sessionDayKey||''}`;
              return(<span className={'text-xs uppercase tracking-wide px-2 py-1 rounded-lg border font-bold '+cls} title={tooltip}>
                {cfg.flag} {sess} · {rating}
              </span>);
            })()
          )}
          {/* V136: FGT IS THE PRIMARY SIGNAL — badge styling reflects this.
              4/4 → larger, pulsing, distinct. 3/4 → emphasized. 2/4 → standard. */}
          {analysis.mtfAlignment!==undefined&&Math.abs(analysis.mtfAlignment)>=2&&(
            (()=>{
              const mtf=analysis.mtfAlignment;
              const count=Math.abs(mtf);
              const isUp=mtf>0;
              const fgt=analysis.fgtResults||{};
              const arrows=['1m','3m','5m','15m'].map(tf=>{
                const f=fgt[tf];
                if(!f||!f.valid)return '?';
                if(f.forecastDir==='UP')return '↑';
                if(f.forecastDir==='DOWN')return '↓';
                return '→';
              }).join('');
              // V136: Tier styling — 4/4 is the headline, 3/4 emphasized, 2/4 standard
              const baseCls=isUp?'text-emerald-200':'text-rose-200';
              const fullCls=isUp
                ? 'bg-emerald-500/25 border-emerald-400/70 ring-2 ring-emerald-500/30 animate-pulse'
                : 'bg-rose-500/25 border-rose-400/70 ring-2 ring-rose-500/30 animate-pulse';
              const threeCls=isUp?'bg-emerald-500/15 border-emerald-500/50':'bg-rose-500/15 border-rose-500/50';
              const twoCls=isUp?'bg-emerald-500/8 border-emerald-500/30':'bg-rose-500/8 border-rose-500/30';
              const tierCls=count>=4?fullCls:count>=3?threeCls:twoCls;
              const sizeCls=count>=4?'text-sm px-3 py-1.5 font-extrabold':'text-xs px-2 py-1 font-bold';
              const cls=baseCls+' '+tierCls+' '+sizeCls;
              const tooltipDetails=['1m','3m','5m','15m'].map(tf=>{
                const f=fgt[tf];
                if(!f||!f.valid)return `${tf}: loading`;
                return `${tf}: ${f.forecastBps>0?'+':''}${f.forecastBps}bps → $${f.fcast}`;
              }).join('\n');
              const headline=count>=4?'★ FGT 4/4 PRIMARY':count>=3?'FGT 3/4':'FGT 2/4';
              return(<span className={'uppercase tracking-wide rounded-lg border '+cls} title={`HPotter Future Grand Trend forecast across timeframes:\n${tooltipDetails}\n\n${count}/4 align ${isUp?'UP':'DOWN'}\n\nV136: FGT is the primary directional signal. 4/4 alignment fast-tracks the lock.`}>
                {headline} {arrows}
              </span>);
            })()
          )}
          {analysis.velocityRegime&&analysis.velocityRegime!=='NORMAL'&&(
            (()=>{
              const v=analysis.velocityRegime;
              const cfg={
                SLOW:    {label:'🐢 SLOW',    cls:'text-blue-300 bg-blue-500/10 border-blue-500/30'},
                FAST:    {label:'⚡ FAST',    cls:'text-amber-300 bg-amber-500/10 border-amber-500/30'},
                EXTREME: {label:'🔥 EXTREME', cls:'text-rose-300 bg-rose-500/15 border-rose-500/40 animate-pulse'},
              }[v];
              if(!cfg)return null;
              return(<span className={'text-xs uppercase tracking-wide px-2 py-1 rounded-lg border font-bold '+cfg.cls} title={`Market velocity: ${v}. Tara is adapting thresholds, sample requirements, and momentum tolerance accordingly.`}>{cfg.label}</span>);
            })()
          )}
          {analysis.lockInfo&&(
            <span className={'text-xs font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-lg border border-emerald-500/30 bg-emerald-500/10 text-emerald-400 px-2 py-1'}>
              🔒 {Math.floor((Date.now()-analysis.lockInfo.lockedAt)/1000)}s @ {analysis.lockInfo.lockedPosterior.toFixed(0)}%{analysis.lockInfo.isLateLock?' ⚠️':''}
            </span>
          )}
          {!analysis.lockInfo&&analysis.isLateLockZone&&(
            <span className={'text-[10px] font-bold px-2 py-0.5 rounded-lg bg-amber-500/15 border border-amber-500/30 text-amber-400'} title="Late in window — lock reliability reduced">LATE WINDOW</span>
          )}
          {analysis.isVeryLateLock&&!analysis.lockInfo&&(
            <span className={'text-[10px] font-bold px-2 py-0.5 rounded-lg bg-zinc-500/15 border border-zinc-500/30 text-zinc-400'}>NO CALL ZONE</span>
          )}
          {analysis.mtfAligned&&(
            <span className={'text-xs font-bold px-2 py-0.5 rounded-full bg-emerald-500/20 border border-emerald-500/50 text-emerald-300 animate-pulse'} title="Both 5m and 15m locked same direction — stronger conviction">DUAL LOCK</span>
          )}
          {analysis.mtfOpposed&&(
            <span className={'text-xs font-bold px-2 py-0.5 rounded-full bg-amber-500/15 border border-amber-500/30 text-amber-400'} title="5m and 15m are pointing in opposite directions — conflicting signal, trade carefully">CONFLICT</span>
          )}
        </div>
        {/* V143: Smart prediction text routing — split long status messages into a tight
                  title + subtitle so the giant heading doesn't wrap to 3 lines. Short messages
                  (UP - CONFIRMED, DOWN - CONFIRMED, UP (FORMING), SEARCHING...) render as the big heading.
            V3.1.4: REJECTED messages moved out of isShort — they're sentence-length and were
                  wrapping awkwardly across 3 lines at the hero size. Now split into title +
                  subtitle ("UP REJECTED" + "trajectory says DOWN") and use the compact heading
                  variant. */}
        {(()=>{
          const p=analysis.prediction||'';
          // Lock and FORMING and SEARCHING — short, render full as hero heading
          const isShort=p.includes('CONFIRMED')||p.includes('FORMING')||p==='SEARCHING...'||p==='NO CALL'||p==='CLOSED'||p.includes('WAITING FOR MOMENTUM')||p.includes('SIGNAL CLEARED');
          if(isShort){
            return <h2 className={headingCls}>{p}</h2>;
          }
          // Long-form status — split into uppercase title + subtitle
          let title='', subtitle='';
          if(p.includes('REJECTED')){
            // V3.1.4: "UP REJECTED — trajectory says DOWN" → title "UP REJECTED" / subtitle "Trajectory says DOWN"
            const dashIdx=p.indexOf('—');
            if(dashIdx>0){
              title=p.slice(0,dashIdx).trim();
              const rest=p.slice(dashIdx+1).trim();
              // Capitalize first letter of subtitle for readability
              subtitle=rest.charAt(0).toUpperCase()+rest.slice(1);
            } else {
              title=p;
              subtitle='';
            }
          } else if(p.includes('ANALYZING')){
            const m=p.match(/\[(\d+)s\]/);
            const lean=p.match(/leaning (UP|DOWN)/);
            title='ANALYZING';
            subtitle=m?`Searching for signal · ${m[1]}s${lean?' · leaning '+lean[1]:''}`:'Searching for signal';
          } else if(p.includes('SITTING OUT')){
            title='SITTING OUT';
            subtitle='Mixed signals · no edge this window';
          } else if(p.includes('MACRO BLACKOUT')){
            title='MACRO BLACKOUT';
            const m=p.match(/\((.+)\)/);
            subtitle=m?m[1]:'Awaiting macro event';
          } else if(p.includes('OBSERVING')){
            title='OBSERVING';
            subtitle=p.replace('OBSERVING ','');
          } else if(p.includes('BREAKING NEWS')){
            title='BREAKING NEWS';
            subtitle='Observing market reaction';
          } else if(p.includes('WEAK SETUP')){
            title='WEAK SETUP';
            subtitle=p.replace('WEAK SETUP — ','');
          } else if(p.includes('LOW QUALITY')){
            title='LOW QUALITY';
            subtitle='Sitting out';
          } else {
            // Fallback — render whatever it is, but cap text length
            title=p.length<=20?p:p.split('—')[0]?.trim()||p.slice(0,18);
            subtitle=p.includes('—')?p.split('—').slice(1).join('—').trim():'';
          }
          return(
            <div className="flex flex-col items-center text-center">
              <h2 className={headingClsCompact}>{title}</h2>
              {subtitle&&<div className={'mt-1.5 text-sm sm:text-base text-[#EDEDED]/55 font-sans tracking-tight max-w-md leading-snug'}>{subtitle}</div>}
            </div>
          );
        })()}

        {/* V134: Plain-English summary line — always visible */}
        <div className="mt-2 px-3 py-2 rounded-lg bg-[#050508] border border-[#24242E] max-w-md w-full">
          <div className={'text-[10px] uppercase tracking-widest text-[#EDEDED]/40 font-bold mb-1'}>What Tara sees</div>
          <div className="text-xs sm:text-sm text-[#EDEDED]/80 leading-snug">
            {buildPlainEnglish(analysis,qualityGate,analysis?.advisor)}
          </div>
        </div>

        {/* V3.1.13: Reasoning chips — each factor pulling for/against, color-coded.
            Inspired by the chip-row pattern from competitor bots, but in Tara's palette.
            Compact: shows top 5 contributors by absolute weight. */}
        {(()=>{
          const sig=analysis.rawSignalScores||{};
          const post=analysis.rawProbAbove||50;
          const leaning=post>=50?'UP':'DOWN';
          const fgt=analysis.mtfAlignment||0;
          const fgtAbs=Math.abs(fgt);
          const traj=analysis.trajectoryAdj||0;
          const cp=analysis.candlePattern||{};
          const vf=analysis.volFlow||{};
          const winType=analysis.windowAmplitude?.label;

          const chips=[];
          const addChip=(label,score,note)=>{
            if(Math.abs(score)<2)return;
            // Determine if this chip supports the current leaning or opposes it
            const supports=leaning==='UP'?score>0:score<0;
            chips.push({label,score:Math.abs(score),supports,note,sortKey:Math.abs(score)});
          };
          // Core signals
          if(sig.gap)addChip('Gap',sig.gap,'price-vs-strike');
          if(sig.momentum)addChip('Momentum',sig.momentum,'recent drift');
          if(sig.flow)addChip('Flow',sig.flow,'tape buy/sell');
          if(sig.structure)addChip('Structure',sig.structure,'support/resistance');
          if(sig.technical)addChip('Technical',sig.technical);
          if(sig.regime)addChip('Regime',sig.regime);
          if(sig.rangePosition)addChip('Range',sig.rangePosition,'σ from open');
          // FGT bonus (V2.9 weighted: ≥3.5=±42, ≥2.5=±26, ≥1.5=±14, ≥0.7=±6)
          if(fgtAbs>=0.7){
            const fgtBonus=fgtAbs>=3.5?42:fgtAbs>=2.5?26:fgtAbs>=1.5?14:6;
            addChip(`FGT ${fgtAbs.toFixed(1).replace(/\.0$/,'')}/4`,fgt>0?fgtBonus:-fgtBonus,'multi-timeframe');
          }
          // Trajectory
          if(Math.abs(traj)>=3){
            addChip('Trajectory',traj,'forward forecast');
          }
          // Candle pattern (informational, but show as chip if directional)
          if(cp.label&&!['MIXED','OPENING','INSUFFICIENT','COMPRESSION'].includes(cp.label)&&Math.abs(cp.score||0)>=10){
            const cpShort=cp.label.replace('ENGULFING-','Engulf ').replace('CLIMAX-','Climax ').replace('STAIR-','Stair ').replace('EXHAUSTION-','Exh ').toLowerCase();
            addChip(`Candles: ${cpShort}`,cp.score,'1m pattern');
          }
          // Vol-flow
          if(vf.label&&!['INSUFFICIENT','QUIET','NEUTRAL'].includes(vf.label)){
            const vShort=vf.label.replace('-CONFIRMED',' confirms').replace('-FAILING',' fading').replace('-DIVERGENT',' diverges').toLowerCase();
            // Vol-flow score sign aligns with direction
            addChip(`Tape: ${vShort}`,vf.score,'volume vs price');
          }
          // Window character context (not a score, just a tag)
          // Sort by absolute weight, take top 5
          chips.sort((a,b)=>b.sortKey-a.sortKey);
          const top=chips.slice(0,5);
          if(top.length===0)return null;
          return(
            <div className="mt-2 max-w-md w-full px-2">
              <div className="flex flex-wrap gap-1.5 justify-center">
                {top.map((c,i)=>{
                  const cls=c.supports
                    ? (leaning==='UP'?'bg-emerald-500/8 border-emerald-500/30 text-emerald-300':'bg-rose-500/8 border-rose-500/30 text-rose-300')
                    : 'bg-amber-500/8 border-amber-500/25 text-amber-300/80';
                  const sign=c.supports?'+':'−';
                  return(
                    <span key={i} className={'inline-flex items-center gap-1.5 text-[10px] sm:text-[11px] px-2 py-0.5 rounded-lg border font-medium '+cls} title={c.note?`${c.label} · ${c.note}`:c.label}>
                      <span>{c.label}</span>
                      <span className="tabular-nums opacity-70">{sign}{c.score.toFixed(0)}</span>
                    </span>
                  );
                })}
              </div>
            </div>
          );
        })()}

        {/* V4.2: Tara's Call card moved to top of projections column for prominence. */}

        {/* V134: STAND DOWN banner — overrides chart reads */}
        {(analysis.prediction.includes('BLACKOUT')||analysis.prediction.includes('OBSERVE')||analysis.prediction.includes('LOW QUALITY')||analysis.prediction.includes('SITTING OUT')||analysis.prediction.includes('BREAKING NEWS'))&&(
          <div className="mt-3 px-4 py-3 rounded-lg bg-rose-500/15 border-2 border-rose-500/50 text-center max-w-md mx-auto w-full">
            <div className="text-rose-300 text-xs uppercase tracking-widest font-bold mb-1">⛔ Stand Down</div>
            <div className="text-rose-200 text-sm font-bold leading-snug">
              Even if your chart looks good — Tara is sitting this one out.
            </div>
          </div>
        )}

        {/* V113: Manual Discord broadcast button - shows for UP/DOWN signals or locks (not SEARCHING) */}
        {discordWebhook&&broadcastSignalManual&&analysis&&analysis.prediction!=='Connecting...'&&(
          (()=>{
            const wKey=timeState?.startWindow||timeState?.nextWindow||0;
            const isStandDown=['BLACKOUT','OBSERVE','LOW QUALITY','SITTING OUT','BREAKING NEWS'].some(s=>analysis.prediction?.includes(s));
            const isSearching=analysis?.prediction?.includes('SEARCHING');
            const dir=analysis?.prediction?.includes('UP')?'UP':analysis?.prediction?.includes('DOWN')?'DOWN':isSearching?'SEARCH':isStandDown?'STAND_DOWN':'';
            const lockType=analysis?.lockInfo?'LOCK':isStandDown?'STAND':isSearching?'SRCH':'SIG';
            const key=`${lockType}:${dir}:${wKey}:${analysis.prediction}`;
            const isSent=broadcasted.sent&&broadcasted.key===key;
            const label=analysis.lockInfo?`Send ${dir} LOCK`:isStandDown?'Broadcast STAND DOWN':isSearching?'Send Searching update':`Send ${dir} signal`;
            return(
              <button
                onClick={()=>{
                  if(isSent)return;
                  broadcastSignalManual();
                  setBroadcasted({key,sent:true});
                }}
                disabled={isSent}
                className={'mt-2 px-3 py-1.5 rounded-lg text-[10px] font-bold uppercase tracking-wider border transition-all '+(isSent?'border-emerald-500/30 bg-emerald-500/5 text-emerald-400/60 cursor-default':'border-indigo-500/40 bg-indigo-500/10 text-indigo-300 hover:bg-indigo-500/20 active:scale-95')}>
                {isSent?'✓ Sent to Discord':'📡 '+label}
              </button>
            );
          })()
        )}

        {showFormingProgress&&(
          <div className="mt-2 w-full px-4 max-w-md">
            {(()=>{
              const post=analysis.rawProbAbove||50;
              const dir=post>=50?'UP':'DOWN';
              const conviction=Math.abs(post-50);
              const fgt=analysis.mtfAlignment||0;
              const fgtAbs=Math.abs(fgt);
              const samplesNeeded=analysis.consecutiveNeeded||2;
              const samplesHave=formingCount;
              const remaining=Math.max(0,samplesNeeded-samplesHave);
              // V4.5: Build contextual message — labeled 'Engine' to distinguish from Tara's
              //   Call samples (different system, both visible).
              let line='';
              if(analysis.prediction.includes('FORMING')){
                line=`Engine forming ${dir} — ${remaining} more sample${remaining===1?'':'s'}`;
              } else if(conviction<10){
                line='Engine: posterior near neutral — waiting for clearer signal';
              } else if(fgtAbs<2){
                line=`Engine leaning ${dir} ${conviction.toFixed(0)} pts — FGT ${fgtAbs.toFixed(1)}/4 silent`;
              } else if(samplesHave<samplesNeeded){
                line=`Engine leaning ${dir} ${conviction.toFixed(0)} pts — ${remaining} sample${remaining===1?'':'s'} to lock`;
              } else {
                line=`Engine working on ${dir} call — checking lock conditions`;
              }
              return(
                <>
                  <div className={'flex justify-between text-xs mb-1'}>
                    <span className="text-[#EDEDED]/55">{line}</span>
                    {samplesNeeded>0&&<span className="text-[#EDEDED]/35 tabular-nums">{samplesHave}/{samplesNeeded}</span>}
                  </div>
                  <div className="w-full h-1 bg-[#050508] rounded-full overflow-hidden">
                    <div className={formingBarCls} style={{width:formingPct+'%'}}/>
                  </div>
                </>
              );
            })()}
          </div>
        )}

        {/* V142: Tara vs Kalshi side-by-side. The edge is in the disagreement. */}
        {(()=>{
          const taraUp=Number(analysis.rawProbAbove||0);
          const taraDn=100-taraUp;
          const taraDir=taraUp>=50?'UP':'DOWN';
          const taraConf=Math.max(taraUp,taraDn);
          // Kalshi YES price is in cents (e.g., 9.9 = 9.9¢ = 9.9% implied probability of UP)
          const kUp=kalshiYesPrice!=null?kalshiYesPrice:null;
          const kDn=kUp!=null?(100-kUp):null;
          const kDir=kUp==null?null:kUp>=50?'UP':'DOWN';
          // EDGE: how much more confident is Tara than Kalshi in Tara's direction?
          // If Tara says UP 75% and Kalshi prices UP at 50%, Tara sees a 25-pt edge on UP.
          // If they agree on direction, edge is the gap. If they disagree, edge is meaningful too — it's the divergence.
          const edge=kUp==null?null:(taraDir==='UP'?taraUp-kUp:taraDn-kDn);
          const isAgree=kDir!=null&&kDir===taraDir;
          // Edge color: strong disagreement Tara's way = green (her edge), agreement = neutral, opposite = warn
          const edgeCls=edge==null?'text-[#EDEDED]/40':
            edge>=20?'text-emerald-300 font-bold':
            edge>=10?'text-emerald-400':
            edge>=0?'text-[#EDEDED]/60':
            edge>=-10?'text-amber-400':
            'text-rose-400 font-bold';
          const edgeLabel=edge==null?'—':edge>=0?`+${edge.toFixed(0)}pts`:`${edge.toFixed(0)}pts`;
          const edgeTooltip=edge==null?'Kalshi price not available':
            edge>=20?`Tara is ${edge.toFixed(0)}pts more confident than Kalshi in ${taraDir}. Strong edge.`:
            edge>=10?`Tara is ${edge.toFixed(0)}pts more confident than Kalshi in ${taraDir}. Moderate edge.`:
            edge>=0?`Tara and Kalshi roughly agree on ${taraDir}. No special edge.`:
            edge>=-10?`Tara and Kalshi disagree slightly. Caution.`:
            `Kalshi pricing strongly against Tara's ${taraDir} call. Reconsider entry.`;
          return(
            <div className="flex items-center gap-3 mt-2 text-xs flex-wrap">
              <div className="flex items-center gap-1.5" title="Tara's internal posterior probability">
                <span className="text-[9px] uppercase text-[#EDEDED]/40 tracking-wider">TARA</span>
                <span className={taraDir==='UP'?'text-emerald-300':'text-rose-300'}>{taraDir} {taraConf.toFixed(0)}%</span>
              </div>
              {kUp!=null?(
                <>
                  <span className="text-[#EDEDED]/20">vs</span>
                  <div className="flex items-center gap-1.5" title="Kalshi market-implied probability (live YES price)">
                    <span className="text-[9px] uppercase text-purple-400/70 tracking-wider">KLSH</span>
                    <span className={kDir==='UP'?'text-emerald-300/80':'text-rose-300/80'}>{kDir} {Math.max(kUp,kDn).toFixed(0)}%</span>
                  </div>
                  <div className={'flex items-center gap-1 px-1.5 py-0.5 rounded-lg border '+(edge>=20?'border-emerald-500/40 bg-emerald-500/10':edge>=10?'border-emerald-500/30':edge>=0?'border-[#2A2A34]':edge>=-10?'border-amber-500/30 bg-amber-500/5':'border-rose-500/40 bg-rose-500/10')} title={edgeTooltip}>
                    <span className="text-[9px] uppercase text-[#EDEDED]/50 tracking-wider">EDGE</span>
                    <span className={edgeCls}>{edgeLabel}</span>
                    {!isAgree&&edge!=null&&<span className="text-[9px] text-amber-400/80">⚡</span>}
                  </div>
                </>
              ):(
                <>
                  <span className="text-[#EDEDED]/20">vs</span>
                  <div className="flex items-center gap-1.5" title="Kalshi data not available — fetch may be loading or this strike isn't on Kalshi yet">
                    <span className="text-[9px] uppercase text-purple-400/40 tracking-wider">KLSH</span>
                    <span className="text-[#EDEDED]/30 italic">loading…</span>
                  </div>
                </>
              )}
              {/* V10.9.10: REFERENCE-HEALTH badge — shows whether the CF-Benchmark
                  blend (CB/KR/OKX-corrected/Bybit-corrected) is live and how many
                  sources are feeding it. Green = anchor present + multi-source;
                  amber = degraded (single/no-anchor, falling back toward chart feed).
                  This makes silent feed failures visible — the thing that was
                  causing bad reads when Coinbase broke. */}
              {(()=>{
                const _b=brtiApprox;
                if(!_b||!(_b.current>0))return(
                  <div className="flex items-center gap-1 px-1.5 py-0.5 rounded-lg border border-amber-500/30 bg-amber-500/5" title="Reference price blend warming up or unavailable — using chart feed for now">
                    <span className="text-[9px] uppercase text-[#EDEDED]/50 tracking-wider">REF</span>
                    <span className="text-[10px] text-amber-400/80">warming…</span>
                  </div>
                );
                const _healthy=(_b.sourceCount>=2||(_b.sourceCount>=1&&_b.hasAnchor))&&_b.samples60s>=5;
                const _div=_b.divergenceBps;
                return(
                  <div className={'flex items-center gap-1 px-1.5 py-0.5 rounded-lg border '+(_healthy?'border-emerald-500/25 bg-emerald-500/5':'border-amber-500/30 bg-amber-500/5')} title={`CF-Benchmark blend · ${_b.sourceCount} source${_b.sourceCount===1?'':'s'} (${(_b.sources||[]).join('+')||'—'})${_b.correctedCount?` · ${_b.correctedCount} bias-corrected`:''}${_b.hasAnchor?' · anchor live':' · NO anchor'} · ${_b.samples60s} samples${_div!=null?` · chart ${_div>=0?'+':''}${_div}bps vs blend`:''}`}>
                    <span className="text-[9px] uppercase text-[#EDEDED]/50 tracking-wider">REF</span>
                    <span className={'text-[10px] tabular-nums '+(_healthy?'text-emerald-300/80':'text-amber-400/80')}>{_b.sourceCount}src{_b.hasAnchor?'':'·noCB'}</span>
                    {_div!=null&&Math.abs(_div)>=8&&<span className="text-[9px] text-amber-400/70" title="chart feed diverging from CF blend">{_div>=0?'+':''}{Math.round(_div)}</span>}
                  </div>
                );
              })()}
              {/* V145: Geopolitics / macro risk pill — surfaces when news scanner detects elevated risk */}
              {newsSentiment&&newsSentiment.geoRisk>=0.3&&(
                (()=>{
                  const r=newsSentiment.geoRisk;
                  const cls=r>=0.7?'border-rose-500/40 bg-rose-500/10 text-rose-300':r>=0.5?'border-amber-500/40 bg-amber-500/10 text-amber-300':'border-amber-500/25 bg-amber-500/5 text-amber-300/80';
                  const label=r>=0.7?'HIGH':r>=0.5?'ELEVATED':'WATCH';
                  const tooltip=`Macro/geo news risk: ${(r*100).toFixed(0)}%${newsSentiment.geoTopic?'\n\nTop story: '+newsSentiment.geoTopic:''}${newsSentiment.geoSource?'\n\nSource: '+(newsSentiment.geoSource==='gdelt'?'GDELT global news':'Crypto news feed'):''}\n\nQuality gate haircut: ${r>=0.5?'-'+Math.round(8*r)+'pts':'monitoring only, no penalty yet'}`;
                  return(
                    <div className={'flex items-center gap-1 px-1.5 py-0.5 rounded-lg border '+cls} title={tooltip}>
                      <span className="text-[9px] uppercase tracking-wider opacity-70">GEO</span>
                      <span className="font-bold">{label}</span>
                    </div>
                  );
                })()
              )}
              {analysis.kellyPct>0&&(
                <span className={'text-amber-400/80'} title="Kelly Criterion: theoretical optimal bet size">Kelly: {analysis.kellyPct.toFixed(1)}%</span>
              )}
              {analysis.kellyPct>0&&qualityGate?.score>=60&&(
                (()=>{
                  const q=qualityGate.score;
                  const frac=q>=90?0.75:q>=80?0.66:q>=70?0.5:0.25;
                  const fracLabel=q>=90?'¾':q>=80?'⅔':q>=70?'½':'¼';
                  // V142: Edge-aware Kelly — if Tara has a real edge over Kalshi, use full recommended fraction.
                  //       If she's actually fighting Kalshi (negative edge), shrink the bet.
                  const edgeMult=edge==null?1:edge>=15?1:edge>=5?0.85:edge>=-5?0.6:edge>=-15?0.3:0.1;
                  const recPct=(analysis.kellyPct*frac*edgeMult).toFixed(1);
                  const tooltip=edge==null
                    ?`Quality ${q.toFixed(0)} → use ${fracLabel} Kelly`
                    :edge>=15?`Strong edge over Kalshi (+${edge.toFixed(0)}pts) → full ${fracLabel} Kelly`
                    :edge>=5?`Moderate edge (+${edge.toFixed(0)}pts) → 85% of ${fracLabel} Kelly`
                    :edge>=-5?`Roughly aligned with Kalshi → 60% of ${fracLabel} Kelly`
                    :edge>=-15?`Fighting Kalshi (${edge.toFixed(0)}pts) → 30% of ${fracLabel} Kelly`
                    :`Strongly fighting Kalshi (${edge.toFixed(0)}pts) → 10% of ${fracLabel} Kelly`;
                  return(<span className={'text-emerald-400/80'} title={tooltip}>Bet: {recPct}%</span>);
                })()
              )}
            </div>
          );
        })()}

        {lockPriceStatus}
      </div>
      )}

      {/* V13.4.320: POSITION RECONCILIATION DRIFT BANNER moved out of here --
          this whole legacy "prediction card" (progress bar, quality gate,
          entry checklist, manual ENTERED UP/DOWN) is now hidden on desktop
          (see its wrapper's className), but the drift banner is a real
          safety check for auto-exec and needs to stay visible regardless.
          It now renders once, always-visible, next to THIS TRADE at the top
          of the column. See its new call site near <ThisTradeCard. */}

      {analysis.lockInfo&&(
        <QualityGateCard qualityGate={qualityGate} regime={analysis.regime} session={getMarketSessions().dominant}/>
      )}

      {!userPosition&&analysis.lockInfo&&(
        <div className={'mb-2 p-2.5 rounded-lg bg-[#050508] border border-[#24242E]'}>
          <div className={'text-[10px] uppercase tracking-wide text-[#EDEDED]/30 font-bold mb-1.5'}>Entry checklist</div>
          <div className="space-y-1">
            {checklistItems.map((item,i)=>{
              const iconCls=item.ok?'text-emerald-400':'text-amber-400';
              const labelCls=item.ok?'text-[#EDEDED]/60':'text-amber-400/80';
              const icon=item.ok?'✓':'⚠';
              return(
                <div key={i} className="flex items-center gap-1.5 text-[10px]">
                  <span className={iconCls}>{icon}</span>
                  <span className={labelCls}>{item.label}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div className={'flex flex-col gap-1.5 border-t border-[#24242E] pt-3 mt-4'}>
        <span className={'text-xs uppercase tracking-wide text-[#EDEDED]/30 text-center'}>-30% Stop Guard Sync</span>
        <SyncButtons userPosition={userPosition} handleManualSync={handleManualSync}/>
      </div>

      {/* V10.2.9 — manual Kalshi entry recorder. Only renders when user is in
          a manual position. Lets them log actual fill price + contract count
          so the trade ticket can compute real P&L instead of preview math. */}
      {userPosition&&(
        <ManualKalshiEntryInput
          userPosition={userPosition}
          manualKalshiEntry={manualKalshiEntry}
          setManualKalshiEntry={setManualKalshiEntry}
          kalshiYesPrice={kalshiYesPrice}
        />
      )}

      {/* ── V111: TARA ADVISOR PANEL ── */}
      <TaraAdvisorPanel advisor={analysis?.advisor} executeAction={executeAction}/>

    </div>
  );
}

// V10.2.9 — Manual Kalshi entry input. Renders below ENTERED UP/DOWN buttons
//   when user has clicked one (userPosition !== null). Two inputs (fill ¢,
//   contracts) + Save/Clear buttons. On save, stores {entryCents, contracts,
//   side, time} so the trade ticket can show real values instead of preview.
//   Pre-populates the fill price field with the current Kalshi offer for the
//   user's side as a starting suggestion.
function ManualKalshiEntryInput({userPosition,manualKalshiEntry,setManualKalshiEntry,kalshiYesPrice}){
  // Current Kalshi offer on the user's side (for default suggestion)
  const _yes=Number(kalshiYesPrice);
  const _isYesLive=Number.isFinite(_yes)&&_yes>0&&_yes<100;
  const _curOurCents=_isYesLive
    ?(userPosition==='UP'?Math.round(_yes):Math.round(100-_yes))
    :null;
  // Edit state — defaults to current values if entry exists, otherwise current
  //   Kalshi offer for fill ¢ and 1 for contracts
  const[entryCentsInput,setEntryCentsInput]=React.useState(
    manualKalshiEntry?String(manualKalshiEntry.entryCents):(_curOurCents?String(_curOurCents):'')
  );
  const[contractsInput,setContractsInput]=React.useState(
    manualKalshiEntry?String(manualKalshiEntry.contracts):'1'
  );
  // Sync edit state when manualKalshiEntry changes externally (e.g., cleared)
  React.useEffect(()=>{
    setEntryCentsInput(manualKalshiEntry?String(manualKalshiEntry.entryCents):(_curOurCents?String(_curOurCents):''));
    setContractsInput(manualKalshiEntry?String(manualKalshiEntry.contracts):'1');
  },[manualKalshiEntry?.entryCents,manualKalshiEntry?.contracts]);
  const _entryCentsNum=parseInt(entryCentsInput,10);
  const _contractsNum=parseInt(contractsInput,10);
  const _valid=_entryCentsNum>=1&&_entryCentsNum<=99&&_contractsNum>=1&&_contractsNum<=250;
  const _isSaved=!!manualKalshiEntry;
  const _save=()=>{
    if(!_valid)return;
    setManualKalshiEntry({
      entryCents:_entryCentsNum,
      contracts:_contractsNum,
      side:userPosition,
      time:Date.now(),
    });
  };
  const _clear=()=>setManualKalshiEntry(null);
  // Color matches direction
  const _dirColor=userPosition==='UP'?'rgb(35,185,129)':'#E8455E';
  return (
    <div className="mt-2 p-2 rounded-lg border" style={{borderColor:'rgba(237,237,237,0.10)',background:'#0B0B0F'}}>
      <div className="flex items-baseline justify-between mb-1.5">
        <span className="text-[9px] uppercase tracking-wide font-bold" style={{color:'rgba(237,237,237,0.55)'}}>
          Real Kalshi fill <span style={{color:_dirColor}}>({userPosition})</span>
        </span>
        {_isSaved&&<span className="text-[8px] uppercase tracking-wider" style={{color:'rgb(35,185,129)'}}>● logged</span>}
      </div>
      <div className="text-[9px] mb-2 leading-relaxed" style={{color:'rgba(237,237,237,0.45)'}}>
        {_isSaved
          ?`Tara is using ${manualKalshiEntry.contracts} × ${manualKalshiEntry.entryCents}¢ = $${(manualKalshiEntry.contracts*manualKalshiEntry.entryCents/100).toFixed(2)} as your real position. Ticket P&L and stop-loss now track against this.`
          :`Enter your actual fill price + contract count from Kalshi so Tara stops showing preview numbers and tracks your real P&L.`}
      </div>
      <div className="flex flex-wrap gap-1.5 items-end">
        <label className="flex-1 min-w-[80px]">
          <div className="text-[9px] mb-0.5" style={{color:'rgba(237,237,237,0.45)'}}>fill ¢</div>
          <input
            type="number"
            min="1" max="99" step="1"
            inputMode="numeric"
            value={entryCentsInput}
            onChange={(e)=>setEntryCentsInput(e.target.value)}
            placeholder={_curOurCents?String(_curOurCents):'1-99'}
            className="w-full bg-transparent border rounded-lg px-2 py-1 text-white text-sm tabular-nums focus:outline-none"
            style={{borderColor:'rgba(237,237,237,0.15)'}}
            onFocus={(e)=>{e.target.style.borderColor='#23B981';}}
            onBlur={(e)=>{e.target.style.borderColor='rgba(237,237,237,0.15)';}}
          />
        </label>
        <label className="flex-1 min-w-[80px]">
          <div className="text-[9px] mb-0.5" style={{color:'rgba(237,237,237,0.45)'}}>contracts</div>
          <input
            type="number"
            min="1" max="250" step="1"
            inputMode="numeric"
            value={contractsInput}
            onChange={(e)=>setContractsInput(e.target.value)}
            placeholder="1-250"
            className="w-full bg-transparent border rounded-lg px-2 py-1 text-white text-sm tabular-nums focus:outline-none"
            style={{borderColor:'rgba(237,237,237,0.15)'}}
            onFocus={(e)=>{e.target.style.borderColor='#23B981';}}
            onBlur={(e)=>{e.target.style.borderColor='rgba(237,237,237,0.15)';}}
          />
        </label>
        <button
          type="button"
          onClick={_save}
          disabled={!_valid}
          className="px-3 py-1 rounded-lg text-[10px] uppercase tracking-wide font-bold transition-colors"
          style={{
            background:_valid?'rgba(35,185,129,0.15)':'rgba(237,237,237,0.05)',
            color:_valid?'rgb(35,185,129)':'rgba(237,237,237,0.30)',
            border:`1px solid ${_valid?'rgba(35,185,129,0.30)':'rgba(237,237,237,0.10)'}`,
            cursor:_valid?'pointer':'not-allowed',
          }}
        >{_isSaved?'update':'save'}</button>
        {_isSaved&&(
          <button
            type="button"
            onClick={_clear}
            className="px-2 py-1 rounded-lg text-[10px] uppercase tracking-wide transition-colors"
            style={{
              background:'#0E0E12',
              color:'rgba(237,237,237,0.55)',
              border:'1px solid #24242E',
              cursor:'pointer',
            }}
            title="forget the logged fill; ticket will revert to preview"
          >clear</button>
        )}
      </div>
      {_valid&&!_isSaved&&_contractsNum*_entryCentsNum>0&&(
        <div className="text-[9px] mt-1.5" style={{color:'rgba(237,237,237,0.45)'}}>
          → cost ${(_contractsNum*_entryCentsNum/100).toFixed(2)} · max payout ${(_contractsNum).toFixed(2)} · max profit ${(_contractsNum-_contractsNum*_entryCentsNum/100).toFixed(2)}
        </div>
      )}
    </div>
  );
}

// ── V111: TaraAdvisorPanel — shows current advisor recommendation with clickable action ──
// V13.4.92: HOURLY LADDER PANEL.
//   WHY THIS EXISTS. Kalshi's hourly BTC market (series KXBTCD) is a ~188-strike ladder
//   at $100 spacing, of which only ~5 near-the-money strikes carry real volume. Its close
//   coincides exactly with the :45-:00 fifteen-minute window, and settlement is identical
//   (mean of the final 60 seconds of CF Benchmarks BRTI). Two measurements make this the
//   right place to act:
//     1. Tara's engine is at its BEST in that quarter: EV +4.80c/ct on 1026 locks vs
//        +3.46 overall, and it holds in both chronological halves (+5.73 / +3.86).
//     2. taraHourlyProb is calibrated there: directional accuracy 85%% at 15 min left,
//        92%% on confident calls; Brier skill +0.065, out-of-sample +0.059.
//   WHAT IT IS FOR. NOT finding mispricing -- Kalshi's pricing is calibrated to within ~2
//   points at every price level (measured on 5383 windows), so there is no systematic wing
//   edge to harvest. It is for STRIKE CHOICE: the 15m market offers one strike at whatever
//   price it happens to be, which is why entries cluster at 70c. Here the same directional
//   read can be expressed at 5 different prices. Cheaper strikes do not add EV against a
//   fair market -- they change variance and payoff shape -- but they hand back control.
//   Advisory only. Places no orders. Read-only public endpoint, no auth.
// V13.4.205: content-aware JSON fetch for the hourly ladder. Mirrors the
//   V13.4.204 treatment on the 15m feed: read as text, reject HTML before
//   parsing (so an error page is named rather than surfacing as a JSON parse
//   error), then check the payload actually has the shape we asked for.
const _kLadFetch=async(url,validate,timeoutMs=6000)=>{
  return readHourlyPublic(url,{validate,timeoutMs});
};
// V13.4.205: race several paths instead of trusting one. The first-party
//   rewrite is normally fastest; the two official public bases exist
//   only so a single failing path can no longer stop hourly trading.
const _kLadRace=async(urls,validate)=>{
  try{
    return await Promise.any(urls.map(u=>_kLadFetch(u,validate).then(r=>{
      if(!r.ok)throw new Error(r.reason);return r;})));
  }catch(agg){
    const msgs=((agg&&agg.errors)||[]).map(e=>(e&&e.message)||'fail');
    return{ok:false,reason:(msgs.join(' / ')||'all paths failed').slice(0,120)};
  }
};
const _K_PUB='https://external-api.kalshi.com/trade-api/v2';
function useHourlyLadder(activeMins){
  const[state,setState]=React.useState({loading:false,err:null,event:null,closeMs:null,strikes:[]});
  React.useEffect(()=>{
    let alive=true,busy=false,fees=null,feesAt=0;
    const pull=async()=>{
      if(busy)return;
      busy=true;
      const requestedAt=Date.now();
      try{
        if(!fees||requestedAt-feesAt>3600000){
          const q='series/KXBTCD';
          const response=await _kLadRace(hourlyPublicPaths(q,API_BASE),d=>d?.series?.ticker==='KXBTCD');
          if(!response.ok){if(alive)setState(s=>({...s,err:'fee schedule: '+response.reason}));return;}
          fees={type:response.data.series.fee_type,multiplier:response.data.series.fee_multiplier};feesAt=Date.now();
        }
        const _evQ='events?series_ticker=KXBTCD&status=open&limit=20';
        const _evRes=await _kLadRace(
          hourlyPublicPaths(_evQ,API_BASE),
          (d)=>!!(d&&Array.isArray(d.events)));
        if(!_evRes.ok){if(alive)setState(s=>({...s,err:'events: '+_evRes.reason}));return;}
        const evs=[...new Set(((_evRes.data&&_evRes.data.events)||[]).map(x=>x.event_ticker).filter(x=>x?.startsWith('KXBTCD-')))].sort();
        if(!evs.length){if(alive)setState(s=>({...s,err:'no open KXBTCD event'}));return;}
        // The open-events endpoint can retain an expired event at the hour.
        // Inspect the bounded event list until a genuinely future market exists.
        for(const ev of evs){
          if(!alive)return;
          const _mkQ='markets?event_ticker='+encodeURIComponent(ev)+'&limit=1000';
          const _mkRes=await _kLadRace(
          hourlyPublicPaths(_mkQ,API_BASE),
          (d)=>!!(d&&Array.isArray(d.markets)));
          if(!_mkRes.ok){if(alive)setState(s=>({...s,err:'markets: '+_mkRes.reason}));return;}
          const normalized=normalizeHourlyMarkets(_mkRes.data.markets,ev,requestedAt,Date.now(),fees);
          if(normalized.closeMs&&normalized.strikes.length){if(alive)setState(normalized);return;}
        }
        if(alive)setState({loading:false,err:'no active future hourly market with usable quotes',event:null,closeMs:null,strikes:[],requestedAt,receivedAt:Date.now()});
      }catch(e){
        // V13.4.205: keep the previous strikes on a transient error. Blanking the
        //   list remains visible for reference. Approval rejects an errored or
        //   stale poll until fresh market observations arrive.
        if(alive)setState(s=>({...s,loading:false,err:String((e&&e.message)||e).slice(0,90)}));
      }finally{busy=false;}
    };
    if(activeMins==null||activeMins<0){setState(s=>({...s,strikes:[]}));return()=>{alive=false;};}
    setState(s=>({...s,loading:true}));
    pull();
    const iv=setInterval(pull,HOURLY_POLL_MS);
    return()=>{alive=false;clearInterval(iv);};
  },[activeMins==null?'off':'on']);
  return state;
}
// V13.4.95: HOURLY RECORD. Persists every hourly lock and settles it against Kalshi's
//   OWN result field, not a spot comparison -- settlement is the mean of the final 60
//   seconds of BRTI, so eyeballing spot at the bell would quietly miscount the close ones.
//   Pending locks live in localStorage so a reload or a closed tab never loses a result.
//   Monotonic: a settled lock is never re-scored (same discipline as the 13.4.73 ratchet).
const _HR_KEY='taraHourlyRecord_v1';
const _hrLoad=()=>{try{return normalizeHourlyRecord(JSON.parse(localStorage.getItem(_HR_KEY)||'{}'),_HR_HISTORY_CAP);}catch(_e){return{w:0,l:0,pending:[],history:[]};}};
// V13.4.162: HOURLY GOES TO THE CLOUD.
//   Until now the hourly ladder's entire record lived ONLY in localStorage, so it
//   died with a browser reset and never existed anywhere but one machine -- the
//   biggest hole in "nothing local". It now mirrors to Supabase alongside the 15m log.
//
//   SIZING (my call, per the egress budget rather than a round number):
//     measured ~150-220B per settled hourly entry, call it 200B.
//     500 history + 40 pending ~= 110KB.
//   That is deliberately half the 15m doc. Hourly changes far less often (a few locks
//   an hour vs a window every 15 min), so its RMW read cost is small -- but it is NOT
//   free, and the whole point of this pass is to spend the egress budget on purpose.
//   500 settled locks is ~3-4 weeks of hourly history at current volume, which is more
//   than enough for the EV work; the full run stays in exports.
//
//   Added to _NO_REALTIME_PATHS (below) for the same reason the 15m log is: echoing a
//   six-figure doc over realtime is what caused the ~157MB/day incident.
const _HR_CLOUD_PATH='memory/hourlyRecord';
const _HR_HISTORY_CAP=500;
const _HR_PENDING_CAP=40;
// V13.4.312 EGRESS: module-level singleton guard for the hydrate interval below.
//   Found via query_logs: this single ~32KB doc was fetched ~28,900 times over
//   24h from one browser session (dev + prod tabs combined) -- roughly once
//   every 3 seconds, not once every 5 minutes as the interval is written to
//   do. A fresh, short-lived session traces exactly as documented (one mount,
//   one hydrate call, matching the 5-min interval) and cannot reproduce the
//   high rate -- it only shows up after many hours in one long-lived tab,
//   which points at intervals accumulating over time rather than a single
//   always-wrong rate. Whatever the exact trigger, at most one interval
//   should ever be alive for this doc in a given tab; this flag makes a
//   second, orphaned one impossible regardless of why a remount happened.
let _hrIntervalActive=false;
const _hrSave=(r,urgent)=>{
  // Age or queue size is not settlement evidence. Keep every unresolved pick;
  // never erase it or infer a loss/void merely because a feed failed.
  const _payload={w:r.w,l:r.l,pending:r.pending,history:r.history.slice(-_HR_HISTORY_CAP),_admissionRevision:r._admissionRevision,settlementHealth:r.settlementHealth};
  try{localStorage.setItem(_HR_KEY,JSON.stringify(_payload));}catch(_e){}
  // Cloud mirror. Debounced hard (60s, same as the 15m log) because settlement can
  //   resolve several pending locks in one burst and each would otherwise be a write.
  try{
    // V13.4.202: 60s for routine settlement bursts, but ~1.5s when a new lock was
    //   just taken -- that write is what stops another device taking the opposite
    //   side of the same strike.
    if(typeof cloudWriteDebouncedRMW==='function')cloudWriteDebouncedRMW(_HR_CLOUD_PATH,()=>_hrLoad(),(cloud,local)=>_hrMerge(cloud,local),urgent?1500:60000);
  }catch(_e){}
};
// V13.4.162: merge a cloud hourly doc into a local one. Union by ticker|side.
//   Resolved beats pending (a settled result is strictly more information than an
//   open lock), and w/l are RECOUNTED from the merged history rather than summed --
//   summing two devices' counters is how you get phantom totals that match neither.
const _hrMerge=(a,b)=>{
  const _key=x=>`${x&&x.ticker}|${x&&x.side}`;
  const byKey=new Map();
  for(const src of [a,b]){
    for(const h of (src&&Array.isArray(src.history)?src.history:[])){
      if(!h||!h.ticker)continue;
      const existing=byKey.get(_key(h));
      byKey.set(_key(h),{...existing,...h,approval:existing?.approval||h.approval,
        learningCapture:existing?.learningCapture||h.learningCapture});
    }
  }
  const history=Array.from(byKey.values())
    .sort((x,y)=>(x.settledAt||0)-(y.settledAt||0))
    .slice(-_HR_HISTORY_CAP);
  const settled=new Set(history.map(_key));
  const pend=new Map();
  for(const src of [a,b]){
    for(const p of (src&&Array.isArray(src.pending)?src.pending:[])){
      if(!p||!p.ticker)continue;
      if(settled.has(_key(p)))continue; // resolved beats pending
      const existing=pend.get(_key(p));
      pend.set(_key(p),{...existing,...p,approval:existing?.approval||p.approval,
        learningCapture:existing?.learningCapture||p.learningCapture});
    }
  }
  return{
    w:history.filter(h=>h.won).length,
    l:history.filter(h=>h.won===false).length,
    pending:Array.from(pend.values()),
    history,
    _admissionRevision:a?._admissionRevision||b?._admissionRevision,
    settlementHealth:(a?.settlementHealth?.at||0)>(b?.settlementHealth?.at||0)?a?.settlementHealth:b?.settlementHealth,
  };
};
const _hrAdmissionTransport=()=>supabaseHourlyTransport(_sbClient,_HR_CLOUD_PATH);
function useHourlyRecord(){
  const[rec,setRec]=React.useState(_hrLoad);
  // V13.4.162: hydrate from Supabase once on mount. Without this the cloud mirror is
  //   write-only -- a fresh browser or a second device would still start empty, which
  //   is the exact "depends on my PC" problem this is meant to remove. One read of a
  //   ~110KB doc per app load is a deliberate, bounded egress cost.
  // V13.4.176 FIX: this hydrate used to run ONCE per mount ([] deps). The hourly lock
  //   engine's own contradiction guard and MAX_LOCKS cap (HourlyLadderPanel, further
  //   below) only check L.locks + rec.pending -- both fed by THIS state -- so a tab
  //   left open for hours never sees another device's locks made after its own mount.
  //   Two devices open across the same hour could each independently lock a
  //   contradicting position, blind to each other, because each was only ever told
  //   about the other's state at its own load time. Re-running this on an interval
  //   (not realtime -- memory/hourlyRecord is deliberately excluded from realtime
  //   subscriptions per the prior ~157MB/day echo incident) closes that gap with a
  //   bounded, predictable egress cost instead of an unbounded one.
  const _hrHydrate=React.useCallback(async()=>{
    try{
      if(typeof cloudSupabaseRead!=='function')return;
      const _cloud=await cloudSupabaseRead(_HR_CLOUD_PATH);
      if(!_cloud)return;
      setRec(prev=>{
        const _merged=_hrMerge(prev,_cloud);
        // Only commit if something actually changed, so this cannot loop with the
        //   write path (setRec -> _hrSave -> cloudWrite -> ...).
        // Equal counts do not mean equal evidence or settled outcomes.
        if(JSON.stringify(_merged)===JSON.stringify(prev))return prev;
        try{localStorage.setItem(_HR_KEY,JSON.stringify(_merged));}catch(_e){}
        try{console.info('[V13.4.162] hourly cloud hydrate: '+prev.history.length+' -> '+_merged.history.length+' settled');}catch(_e){}
        return _merged;
      });
    }catch(_e){
      try{console.warn('[V13.4.162] hourly cloud hydrate failed',_e&&_e.message);}catch(_){}
    }
  },[]);
  React.useEffect(()=>{
    _hrHydrate();
    // V13.4.312 EGRESS: singleton guard (see comment at _hrIntervalActive's
    //   declaration) -- at most one of these intervals runs per tab, ever,
    //   no matter how many times this hook mounts. Also widened 5min -> 15min
    //   as defense in depth regardless of the exact trigger.
    if(_hrIntervalActive)return;
    _hrIntervalActive=true;
    const iv=setInterval(_hrHydrate,15*60000);
    return()=>{
      clearInterval(iv);
      _hrIntervalActive=false;
    };
  },[_hrHydrate]);
  // V13.4.236: CONTRADICTION RE-CHECK AGAINST FRESH CLOUD STATE.
  //
  //   Seen live on 2026-08-20: NO @72599.99 and YES @72599.99 both open on the
  //   same 23:00 close. Those cannot both win — NO@K needs close<K and YES@K
  //   needs close>=K — so one was a guaranteed loss plus fees on both.
  //
  //   The guard in HourlyLadderPanel is correct; it just could not see. Writes
  //   are urgent (1.5s since V13.4.202) but memory/hourlyRecord is in
  //   _NO_REALTIME_PATHS, so the only way a device learns of another's lock is
  //   the 5-MINUTE hydrate interval. The two locks were ONE MINUTE apart. A
  //   guard that depends on peers' state cannot run on a read cycle 5x slower
  //   than the thing it guards against — and with two deployments sharing one
  //   record, there is always a peer.
  //
  //   Fix is a fresh read at the moment of committing, not a faster interval:
  //   it fires at most MAX_LOCKS times an hour instead of every 5 minutes for
  //   every client, and it shrinks the blind window from 5 minutes to one round
  //   trip. New picks now wait on read failure; the panel exposes that reason.
  const addLock=React.useCallback(async(lk,revalidate)=>{
    if(!lk.approval||!lk.approval.samples?.length)return {accepted:false,code:'approval-missing',reason:'Hourly confirmation evidence is missing.'};
    const entry={ticker:lk.ticker,side:lk.side,strike:lk.strike,cost:lk.cost,at:lk.at,closeMs:lk.closeMs,
      ms:lk.ms,conf:lk.conf,model:lk.model,mktPct:lk.mktPct,minsAtLock:lk.minsAtLock,band:lk.band||null,
      approval:JSON.parse(JSON.stringify({...lk.approval,recordedAt:Date.now()})),
      learningCapture:capturePickLearning(lk,{profile:'BTC_hourly',at:lk.ms,close:lk.closeMs,version:lk.approval.modelVersion,
        probability:(lk.side==='YES'?lk.model:100-lk.model)/100,confidence:lk.conf,price:lk.cost,event:lk.approval.quote.event,quoteAt:lk.approval.quote.requestedAt})};
    let transport;try{transport=_hrAdmissionTransport();}catch{return {accepted:false,code:'record-read',reason:'Hourly shared record unavailable.'};}
    const result=await commitHourlyAdmission({...transport,entry,merge:_hrMerge,readLocal:_hrLoad,
      preflightLocal:()=>localStorage.setItem(_HR_KEY,JSON.stringify(_hrLoad())),
      saveLocal:next=>localStorage.setItem(_HR_KEY,JSON.stringify(next)),
      revalidate:record=>typeof revalidate==='function'?revalidate(record):{ok:false,code:'approval-missing',reason:'Hourly approval evidence is missing.'},
      guard:record=>hourlyRecordGuard(record,lk,readHourlyPolicy(localStorage),Date.now())});
    if(result.record)setRec(result.record);
    return result;
  },[]);
  React.useEffect(()=>{
    let alive=true;
    const settle=async()=>{
      const cur=_hrLoad();
      const due=cur.pending.filter(x=>x.ticker&&x.closeMs&&Date.now()>x.closeMs+45000);
      if(!due.length)return;
      const hist=[...cur.history];const still=[...cur.pending];const failures=[];
      for(const x of due){
        try{
          const path='markets/'+encodeURIComponent(x.ticker);
          const r=await _kLadRace(hourlyPublicPaths(path,API_BASE),d=>d?.market?.ticker===x.ticker);
          if(!r.ok){failures.push({ticker:x.ticker,code:'settlement-read',reason:r.reason});continue;}
          const j=r.data;
          const res=j&&j.market&&j.market.result;
          const verified=inspectHourlySettlement(x,j.market,Date.now());
          if(!verified.ok){failures.push({ticker:x.ticker,code:verified.reason||'official-pending',reason:verified.reason||'Official result is pending.'});continue;}
          const won=verified.won;
          hist.push({...x,result:res,won,netCents:won?(100-x.cost):-x.cost,settledAt:Date.now(),
            officialSettlement:verified.officialSettlement,learningSettlement:capturePickSettlement(x,j.market)});
          const k=still.findIndex(y=>y.ticker===x.ticker&&y.side===x.side);
          if(k>=0)still.splice(k,1);
        }catch(_e){failures.push({ticker:x.ticker,code:'settlement-read',reason:'Hourly settlement read failed; original pending pick retained.'});}
      }
      if(!alive)return;
      // The visible record is a rolling 500 settlements, not lifetime counters.
      // Recount after capping so W + L cannot silently exceed the stated window.
      // Preserve a new pick accepted while settlement's network requests were
      // in flight; settled entries still win over the same pending identity.
      const next=normalizeHourlyRecord(_hrMerge(_hrLoad(),{pending:still,history:hist,settlementHealth:{at:Date.now(),failures:failures.slice(-10),pending:still.length}}),_HR_HISTORY_CAP);
      _hrSave(next);setRec(next);
    };
    settle();
    const iv=setInterval(settle,60000);
    return()=>{alive=false;clearInterval(iv);};
  },[]);
  return{rec,addLock};
}
// V13.4.113: TRADE COACH CALL. User request: replace the confusing simulated-sizing
//   preview ('will buy 200 contracts @ 5c', 'budget->cost', 'target/stop payout') with one
//   clean, decisive directive per round: BUY UP / BUY DOWN / HOLD / SIT OUT. No dollar math,
//   no hypothetical fills -- the old preview block was explicitly a simulation ('Tara sees
//   you marked ENTERED but doesn't know your real fill price') and that ambiguity is exactly
//   what was confusing. This reads taraCall directly, the same object every gate tonight
//   (entry-band, coin-flip, TRAJ-early, all four commit-path fixes) already produces.
//   Not removing the old preview panel -- it is a 340-line block (~L28709-29049) tangled
//   with live-position tracking, and surgically extracting it risked the exact class of
//   mistake that caused the 13.4.102 crash. This mounts as an independent, prominent panel
//   instead; the old one can be found and removed later once its full dependency graph is
//   traced properly.
function TradeCoachCall({taraCall,analysis,lockedSnapshotDir,lockedSnapshot,kalshiYesPrice,timeState,windowType,userPosition}){
  // V13.4.121: stability ref for the pre-lock LEANING display, declared before any
  //   early return so the hook always runs (Rules of Hooks). User feedback: 'based on
  //   price hike or dump it suggests that position and hold and switches instantly.
  //   theres no lock to it.' The unlocked lean was reading the raw live posterior every
  //   tick with zero debounce -- this holds a lean for a minimum stretch before the
  //   DISPLAYED direction is allowed to flip, so the panel stops chasing every tick.
  const _leanStickyRef=React.useRef({dir:null,since:0,displayed:null});
  // V13.4.115: FIXED PROPERLY, back on by default. Root cause traced: this panel read
  //   taraCall.call directly -- the raw, continuously-live posterior, which keeps
  //   recomputing every tick even AFTER a round locks. The 'tara says' ticket panel
  //   instead resolves direction through getTaraDirection({snapshot,lock,signalSource}),
  //   which gives precedence to the LOCKED decision (analysis.lockInfo.dir) once one
  //   exists, rather than whatever the live signal has since drifted to. That is exactly
  //   why the screenshot showed 'tara says: long up' (frozen at lock) next to this panel's
  //   'LEANING DOWN' (live posterior, drifted after lock). Fix: call the SAME top-level
  //   getTaraDirection function with the SAME kind of inputs -- analysis was already a
  //   ProjectionsCard prop, so analysis.lockInfo.dir is reachable with zero new plumbing;
  //   taraCall.call substitutes for the snapshot input, which is the same role
  //   taraSnapshotForTicket plays for the ticket panel. signalSource defaults to
  //   'snapshot' either way (autoExecSettings is not reachable here, but that IS the
  //   function's own default when signalSource is omitted, so this matches the common
  //   case exactly). Toggle: localStorage 'taraCoachCall'='off' to hide.
  // V13.4.268: default OFF. Every line this panel showed is now stage 1 and
  //   stage 2 of THIS TRADE, in one place instead of a third column. It was
  //   also the panel that disagreed with Tara's Call about the same number.
  //   Toggle back on with localStorage taraCoachCall=on.
  const on=(function(){try{return localStorage.getItem('taraCoachCall')==='on';}catch(_e){return false;}})();
  if(!on)return null;
  // V13.4.116: SECOND fix needed -- the 115 fix fed taraCall.call (a FRESH live
  //   computation from analysis.rawProbAbove on every render, confirmed by reading its own
  //   definition at ~L38782) into the snapshot slot. getTaraDirection in snapshot mode
  //   PREFERS the snapshot input over the lock input -- so that fed the exact same live-
  //   drifting value the bug was about, just routed through an extra function call. The
  //   REAL frozen decision lives in taraCallSnapshotRef.current, only written when an
  //   actual lock/sitout/commit happens (confirmed: every commit-path fix tonight writes
  //   to this exact ref). Threaded through as lockedSnapshotDir, same pattern as the
  //   existing reversalRisk prop on this same component.
  // V13.4.172 FIX: `locked` was read off taraCall.locked -- but taraCall is a LIVE
  //   recomputation built fresh on every render (see its builder), and nothing in that
  //   builder ever sets `locked`. So this was ALWAYS false, and every "when locked"
  //   branch below was dead code: the panel could never show "BUY x – LOCKED", never
  //   read the frozen snapshot for confidence/reason (the V13.4.119 fix), and never
  //   released the 8s lean debounce (the V13.4.121/148 fixes).
  //   Real screenshot: Tara's Call read "LOCKED DOWN" while this panel read
  //   "LEANING UP – HOLD ... your marked DOWN position now conflicts with Tara's
  //   current read (UP)" -- it was warning the user off a position that MATCHED the
  //   actual locked call, because it did not know a lock had happened and was still
  //   holding a stale pre-lock lean.
  //   The real lock lives in the committed snapshot (taraCallSnapshotRef.current,
  //   passed in as lockedSnapshot). Sit-out snapshots also carry locked:true, so a
  //   DIRECTIONAL call is required -- otherwise a sit-out would read as a locked trade.
  // V13.4.280: routed through readLockState. `tradeable` carries exactly the rule
  //   this had hand-rolled -- committed AND directional -- so a sit-out can never
  //   display as a locked trade, which is what the V13.4.161 guard was for.
  const _lockSt=readLockState(lockedSnapshot);
  const _lockedDir=_lockSt.dir;
  const locked=_lockSt.tradeable;
  // V13.4.265: `locked` above answers "is there a tradeable directional lock",
  //   and must keep requiring a direction -- a sit-out displaying as a locked
  //   trade is the bug the V13.4.161 guard was written to prevent.
  //   But it was ALSO being used to mean "has Tara decided yet", and a sit-out
  //   is a decision. That second meaning gets its own name here, so the panel
  //   can freeze its numbers on any commit without ever calling one tradeable.
  // V13.4.280: both from readLockState now. `committed` (not `tradeable`) is the
  //   right gate for freezing numbers -- a sit-out is decided, so its confidence
  //   and reason must stop drifting too. That distinction was the V13.4.265 bug.
  const _committedSnap=_lockSt.committed?lockedSnapshot:null;
  const _committedSitOut=_lockSt.satOut;
  // V13.4.161 FIX: the REAL cause of the header/reason contradiction, which the
  //   V13.4.148 debounce-lag fix did not catch (confirmed by a fresh screenshot
  //   showing "LEANING DOWN -- HOLD" over "TRAJ-priority lock·UP" with NO active
  //   flip in progress). Below, `rawReason` reads _src.reason, and _src is
  //   `taraCall` whenever NOT locked (see _src a few lines down). But `call`
  //   (the header) was ALWAYS routed through getTaraDirection/lockedSnapshotDir --
  //   a pipeline meant to prefer the FROZEN lock snapshot once locked, so a live-
  //   drifting taraCall.call can't disagree with a frozen ticket (V13.4.116's
  //   actual, narrower purpose). Pre-lock there IS no frozen snapshot to prefer;
  //   lockedSnapshotDir there is just whatever the PREVIOUS lock happened to be,
  //   unrelated to the live lean. So header and body were reading two different
  //   objects at every unlocked tick, not just during an 8s debounce window.
  //   Fix: use the frozen-snapshot pipeline ONLY once actually locked; pre-lock,
  //   read taraCall.call directly -- the exact same object `_src` already uses
  //   for the reason text -- so header and body can never disagree pre-lock.
  let call;
  if(locked){
    // V13.4.172: the committed snapshot IS the decision -- prefer it directly. The
    //   getTaraDirection/lockedSnapshotDir route stays as a fallback for the case where
    //   a lock exists but the snapshot prop has not propagated yet.
    const _lockDirIn=analysis?.lockInfo?.dir||null;
    const _resolved=getTaraDirection({snapshot:{call:lockedSnapshotDir},lock:{dir:_lockDirIn},signalSource:'snapshot'});
    call=_lockedDir||_resolved.dir||(taraCall&&taraCall.call==='SIT_OUT'?'SIT_OUT':null);
  }else{
    call=(taraCall&&(taraCall.call==='UP'||taraCall.call==='DOWN'))?taraCall.call
      :(taraCall&&taraCall.call==='SIT_OUT'?'SIT_OUT':null);
  }
  // V13.4.121: apply the stability hold ONLY to the pre-lock lean -- once actually
  //   locked, the direction is frozen already (that's the whole point of `locked`),
  //   so there's nothing to debounce there.
  // V13.4.148: keep the pre-debounce direction. The stability hold below freezes the
  //   DISPLAYED side for 8s, but the reason string is regenerated live every tick, so
  //   during a flip the header and the body describe OPPOSITE directions -- a real
  //   screenshot showed "LEANING DOWN -- HOLD" above "TRAJ-priority lock·UP·projection
  //   +13bps to strike". Both were "correct"; they were just reading different clocks.
  const _liveDir=call;
  if(!locked&&(call==='UP'||call==='DOWN')){
    const _now121=Date.now();
    const _sticky=_leanStickyRef.current;
    if(_sticky.dir!==call){_sticky.dir=call;_sticky.since=_now121;}
    const MIN_STABLE_MS=8000; // hold a lean for >=8s before letting the DISPLAYED side flip
    if(_sticky.displayed==null||(_now121-_sticky.since)>=MIN_STABLE_MS){_sticky.displayed=call;}
    call=_sticky.displayed;
  }else if(locked){
    _leanStickyRef.current.displayed=null; // reset so the next unlocked window starts fresh
  }
  // True when the debounce is actively holding a side the live engine has left.
  const _leanFlipping=!locked&&(call==='UP'||call==='DOWN')&&(_liveDir==='UP'||_liveDir==='DOWN')&&_liveDir!==call;
  // V13.4.265: a committed sit-out is NOT a directional read, whatever the live
  //   engine has drifted to since. Without this the headline said SITTING OUT
  //   while the sections under it still quoted an entry price and told him his
  //   position matched Tara -- the same contradiction one level down.
  const isDir=(call==='UP'||call==='DOWN')&&!_committedSitOut;
  const isSitOut=call==='SIT_OUT'||!call||_committedSitOut;
  // V13.4.119: SAME live-vs-frozen bug as direction, now fixed for every field the panel
  //   shows. When locked, read confidence/reason/qScore from the FROZEN lockedSnapshot
  //   (taraCallSnapshotRef.current, captured at the moment of lock) instead of the live
  //   taraCall, which keeps recomputing every tick. Real screenshot showed 'confidence
  //   29%' here while the ticket panel showed '20.3pt STRONG conviction' / 'Tara 70%' --
  //   same underlying mismatch as the direction bug, just a different field.
  // V13.4.265: was `locked&&lockedSnapshot`, so a committed SIT-OUT fell through
  //   to the live taraCall and this panel showed a confidence that kept drifting
  //   after the decision was frozen -- 58% here against 71% on Tara's Call, from
  //   the same snapshot. Any commit freezes it now.
  const _src=_committedSnap||taraCall;
  const conf=Number(_src&&_src.confidence)||0;
  const qScore=Number.isFinite(_src&&_src.qScore)?_src.qScore:null;
  const rawReason=(_src&&_src.reason)||'';
  // V13.4.119: reason strings across this whole app are written as engineering/audit-trail
  //   text by design -- version tags, vote counts, jargon (I have written dozens of them
  //   myself tonight in exactly this style). Fine for the raw call log; confusing read
  //   verbatim in a coach summary meant to be glanced at. Strip leading [V#.#.# TAG] style
  //   prefixes and collapse nested ' -- was: ...' (em-dash via \u2014 unicode escape, not a
  //   literal char, to avoid touching the file's em-dash-count invariant) down to the
  //   newest reasoning only.
  const reason=rawReason
    .replace(/^\[V?\d+[\d.]*\s*[A-Z0-9\-]*\]\s*/,'')
    .split(/\s*\u2014\s*was:\s*/)[0]
    .trim();
  let label,sub,color,bg,border;
  // V13.4.265: committed sit-out is checked FIRST. There was no branch for it,
  //   so it fell into the `isDir&&!locked` lean branch and told him to "wait for
  //   Tara to commit" on a round she had already committed. Now it reads the same
  //   way Tara's Call reads it: sitting out, with the lean kept as context.
  if(_committedSitOut){
    label='SITTING OUT';
    const _leanDir=_committedSnap._intendedDir||_committedSnap.direction||null;
    sub='Tara committed to sitting this round out'
      +((_leanDir==='UP'||_leanDir==='DOWN')?' — she would have leaned '+_leanDir+'.':'.')
      +(reason?' '+reason:'');
    color='rgba(212,162,76,0.95)';
    bg='rgba(212,162,76,0.08)';
    border='rgba(212,162,76,0.30)';
  }else if(isDir&&locked){
    // V13.4.169: en dash, not a double hyphen. "BUY DOWN -- LOCKED" read as raw text.
    label=(call==='UP'?'BUY UP':'BUY DOWN')+' – LOCKED';
    sub='Tara has committed this round. '+(reason?reason:'');
    color=call==='UP'?'#23B981':'#E8455E';
    bg=call==='UP'?'rgba(35,185,129,0.10)':'rgba(232,69,94,0.10)';
    border=call==='UP'?'rgba(35,185,129,0.35)':'rgba(232,69,94,0.35)';
  }else if(isDir&&!locked){
    label=(call==='UP'?'LEANING UP':'LEANING DOWN')+' – HOLD';
    // V13.4.148: while the debounce is holding a side the live engine has left, the
    //   live `reason` describes the OTHER direction. Showing it under a header that
    //   says the opposite is worse than showing nothing -- it reads as the app
    //   contradicting itself, and it is the specific thing that makes this panel look
    //   untrustworthy. Say plainly that the read is unstable instead.
    if(_leanFlipping){
      sub=`Signal is flipping – live read just moved to ${_liveDir}, holding ${call} for stability. `
        +`This is exactly when NOT to act: wait for the Decision Clock.`;
      color='rgba(212,162,76,0.95)';
      bg='rgba(212,162,76,0.10)';
      border='rgba(212,162,76,0.35)';
    }else{
      sub='Real lean, not yet locked. Wait for Tara to commit before acting. '+(reason?reason:'');
      color=call==='UP'?'#23B981':'#E8455E';
      bg='rgba(35,185,129,0.08)';
      border='rgba(35,185,129,0.30)';
    }
  }else{
    label='SIT OUT';
    sub=reason||'No tradeable read this round -- genuinely mixed or a coin flip.';
    color='#EDEDED';
    bg='rgba(237,237,237,0.05)';
    border='#24242E';
  }
  return React.createElement('div',{
    className:'mb-3 rounded-xl p-4 border',
    style:{background:bg,borderColor:border},
  },
    React.createElement('div',{className:'text-[10px] uppercase tracking-wider mb-1',style:{color:'rgba(237,237,237,0.5)'}},'Trade Coach'),
    React.createElement('div',{className:'text-2xl font-bold mb-1',style:{color}},label),
    // V13.4.265: `isDir` is false on a committed sit-out, so the confidence line
    //   vanished on exactly the state where the two panels disagreed. Shown for
    //   both now, and labelled so it cannot be misread as a live tradeable read.
    (isDir||_committedSitOut)&&conf>0&&React.createElement('div',{className:'text-[11px] mb-1',style:{color:'rgba(237,237,237,0.6)'}},
      (_committedSitOut?'would lean · ':'confidence ')+conf.toFixed(0)+'%'),
    React.createElement('div',{className:'text-[12px] leading-snug',style:{color:'rgba(237,237,237,0.75)'}},sub),
    // V13.4.117: SITUATION AWARENESS. User feedback: coach 'not aware of position and
    //   situation to win max money' -- a bare UP/DOWN direction with no price or time
    //   context genuinely isn't enough to act on well. This surfaces the same band check
    //   every commit-path fix tonight already enforces (30-60c default), so the coach
    //   panel explains WHY a locked call is or isn't attractive, not just what it is.
    isDir&&Number.isFinite(qScore)&&qScore<40&&React.createElement('div',{className:'mt-2 text-[11px]',style:{color:'#E8455E'}},
      '\u26a0 quality gate LOW ('+qScore+'/100) -- weak historical WR at this reading, consider sitting out'),
    isDir&&timeState&&Number(timeState.minsRemaining)<2&&React.createElement('div',{className:'mt-1 text-[11px]',style:{color:'#23B981'}},
      '\u26a0 '+(timeState.minsRemaining||0)+'m '+(timeState.secsRemaining||0)+'s left \u2013 under the 2m usually needed to fill and settle cleanly'),
    isDir&&(function(){
      const k=Number(kalshiYesPrice);
      if(!Number.isFinite(k)||k<=0||k>=100)return null;
      const cost=call==='UP'?k:(100-k);
      const min=getEntryMinCost();
      const max=getEntryMaxCost();
      const inBand=cost>=min&&cost<max;
      const secsLeft=timeState?((timeState.minsRemaining||0)*60+(timeState.secsRemaining||0)):null;
      // V13.4.171: MAKER vs TAKER. Measured on the real Kalshi account: 90.6% of BTC
      //   fills were TAKER, and taker fees came to $6,055 while the 957 maker fills paid
      //   $0.01 total -- Kalshi's maker fee is zero. Against a 15m gross edge of +0.61%
      //   of staked, fees at 2.11% are ~3.5x the entire edge. Crossing the spread is the
      //   single largest cost in the account, larger than any gate tuned so far.
      //   So the panel now says what to actually DO: the ask is what you pay if you hit
      //   it now, the bid is what you pay if you rest and wait. Advisory only -- Tara
      //   places nothing; you still choose.
      const _q=(typeof _kalshiQuote!=='undefined')?_kalshiQuote:null;
      // V13.4.214: was null-checked only, so an empty book (bid:0/ask:0) passed.
      const _qFresh=!!(_q&&_q.at&&(Date.now()-_q.at)<60000&&_quoteUsable(_q));
      // Cost of OUR side: UP buys YES (pay ask, rest at bid). DOWN buys NO, whose
      //   ask is (100 - yesBid) and whose bid is (100 - yesAsk).
      const _takeC=_qFresh?(call==='UP'?_q.ask:(100-_q.bid)):null;
      const _restC=_qFresh?(call==='UP'?_q.bid:(100-_q.ask)):null;
      const _saveC=(_takeC!=null&&_restC!=null)?(_takeC-_restC):null;
      const _fee=(c)=>0.07*(c/100)*(1-c/100)*100; // Kalshi fee, peaks ~1.75c at 50c
      return React.createElement('div',{className:'mt-2 pt-2 border-t text-[11px]',style:{borderColor:'rgba(237,237,237,0.10)'}},
        React.createElement('span',{style:{color:inBand?'#23B981':'#23B981'}},
          'entry '+cost.toFixed(0)+'c'+(inBand?' – in the '+min.toFixed(0)+'-'+max.toFixed(0)+'c band, good price':' – OUTSIDE the '+min.toFixed(0)+'-'+max.toFixed(0)+'c band, paying up')),
        secsLeft!=null&&React.createElement('span',{className:'ml-2',style:{color:'rgba(237,237,237,0.5)'}},Math.round(secsLeft)+'s left in window'),
        _qFresh&&_saveC!=null&&_saveC>0&&React.createElement('div',{className:'mt-1.5 leading-relaxed'},
          React.createElement('span',{style:{color:'rgba(232,69,94,0.9)'}},`take now ${_takeC.toFixed(0)}c`),
          React.createElement('span',{style:{color:'rgba(237,237,237,0.35)'}},'  ·  '),
          React.createElement('span',{style:{color:'#23B981'}},`rest at ${_restC.toFixed(0)}c`),
          React.createElement('div',{className:'text-[10px] mt-0.5',style:{color:'rgba(237,237,237,0.45)'}},
            `resting saves ${_saveC.toFixed(0)}c spread + ${_fee(_takeC).toFixed(1)}c fee (maker is free) = ${(_saveC+_fee(_takeC)).toFixed(1)}c/contract. May not fill.`),
        ),
      );
    })(),
    // V13.4.121: POSITION AWARENESS. User feedback: coach 'not position aware or
    //   smart' -- it always recommended a fresh direction with zero regard for a
    //   position the user already marked themselves into (userPosition, set via the
    //   manual ENTERED UP/DOWN button). This compares the current stabilized read
    //   against that marked position so the coach actually reacts to what's already
    //   held, not just what a fresh entry would look like.
    userPosition&&(function(){
      const _aligned=isDir&&call===userPosition;
      const _conflicted=isDir&&call!==userPosition;
      const _txt=_aligned
        ?('\u2713 your marked '+userPosition+' position matches Tara\'s '+(locked?'LOCKED call.':'current read (still forming, not locked yet).'))
        :_conflicted
        ?('\u26a0 your marked '+userPosition+' position conflicts with Tara\'s '+(locked?'LOCKED '+call+' call':'current read ('+call+')')+' \u2013 she is not on your side.')
        :('\u26a0 your marked '+userPosition+' position has no supporting signal right now (Tara reads '+(isSitOut?'SIT OUT':'mixed')+').');
      return React.createElement('div',{
        className:'mt-2 pt-2 border-t text-[11px]',
        style:{borderColor:'rgba(237,237,237,0.10)',color:_aligned?'#23B981':'#E8455E'},
      },_txt);
    })(),
  );
}
// V13.4.133: HOURLY MEMORY MODAL. Explicit user request: 'i want hourlys to have a
//   log memory section like [the 15m TARA MEMORY page] too.' Mirrors that page's
//   visual language (stat cards, win rate, chronological list) but scoped to the
//   hourly ladder's own data (rec.history from useHourlyRecord) and its own real
//   dimension of interest -- accuracy BAND (85/77/67%/coin-flip), not tier, since
//   the hourly ladder borrows its direction from the main engine and its own
//   variable is purely how much time was left on the clock at lock (see the
//   MAX_MINS_LEFT_TO_LOCK fix a few lines below in HourlyLadderPanel -- this modal is
//   what makes that fix's effect visible over time instead of just trusted blind).
function HourlyMemoryModal({onClose,embedded=false}){
  const{rec}=useHourlyRecord();
  const[filter,setFilter]=React.useState('all');
  const history=Array.isArray(rec.history)?rec.history:[];
  // V13.4.134 FIX: `won` is the real, computed win/loss (h.won boolean, set at
  //   settlement time). `h.result` is Kalshi's RAW settlement string ('yes'/'no') --
  //   using it as if it were a WIN/LOSS label was the bug behind the confusing 'yes'/
  //   'no' text in the list (a NO side that settles 'no' is a WIN, but the raw string
  //   said 'no', reading like a loss). Centralized here so every consumer below agrees.
  const isWin=(h)=>h.won===true;
  const isLoss=(h)=>h.won===false;
  const filtered=React.useMemo(()=>{
    let arr=[...history].reverse();
    if(filter==='wins')arr=arr.filter(isWin);
    else if(filter==='losses')arr=arr.filter(isLoss);
    return arr;
  },[history,filter]);
  // V13.4.134 FIX: 'Total calls' was showing history.length, which _hrSave caps at
  //   the last 200 (storage limit) -- so it could never match Wins+Losses, which read
  //   the real uncapped lifetime counters (rec.w/rec.l). Total now reflects the same
  //   lifetime source those numbers come from; the browsable list below is still
  //   capped to the last 200 settled (that's a storage limit, not a lie, so it's
  //   labeled as such instead of pretending to be the whole history).
  const pendingN=Array.isArray(rec.pending)?rec.pending.length:0;
  const totalSettled=rec.w+rec.l;
  const wr=totalSettled>0?Math.round(100*rec.w/totalSettled):null;
  // Two categorization dimensions, not one: by time-left-at-lock band (the ladder's
  //   own real lever, see MAX_MINS_LEFT_TO_LOCK) and by side (YES vs NO -- cheap to
  //   compute, and a real question: does this engine call UP-equivalent (YES) locks
  //   better than DOWN-equivalent (NO) ones, independent of timing).
  const _groupBy=(keyFn,order)=>{
    const groups={};
    for(const h of history){
      const key=keyFn(h);
      if(!groups[key])groups[key]={w:0,l:0,net:0};
      if(isWin(h))groups[key].w+=1;
      else if(isLoss(h))groups[key].l+=1;
      groups[key].net+=Number(h.netCents)||0;
    }
    const keys=order?order.filter(k=>groups[k]):Object.keys(groups);
    return keys.map(k=>({label:k,...groups[k]}));
  };
  // V13.4.168: the fallback bucket used to be labelled "before time-band tracking
  //   (V13.4.133+)" -- an internal version tag surfacing in the UI, and on a log where
  //   almost nothing carries a band it was the ONLY card shown, so a whole section
  //   rendered to say "61%" under a developer note. Plain label now, and the section
  //   hides itself below unless at least one REAL band is present.
  const _BAND_NA='not tracked yet';
  const bandStats=React.useMemo(()=>_groupBy(
    h=>h.band||_BAND_NA,
    ['85%','77%','67%','~52% (coin flip this early)',_BAND_NA],
  ),[history]);
  const _hasRealBands=React.useMemo(()=>bandStats.some(b=>b.label!==_BAND_NA),[bandStats]);
  const sideStats=React.useMemo(()=>_groupBy(
    h=>h.side==='YES'?'YES (UP-equiv)':h.side==='NO'?'NO (DOWN-equiv)':'unknown',
    ['YES (UP-equiv)','NO (DOWN-equiv)','unknown'],
  ),[history]);
  const resultColor=(h)=>isWin(h)?'#23B981':isLoss(h)?'#E8455E':'rgba(237,237,237,0.4)';
  const resultLabel=(h)=>isWin(h)?'WIN':isLoss(h)?'LOSS':'--';
  // V13.4.168: lock times were stored with toLocaleTimeString([],...), i.e. whatever
  //   the machine's locale produced -- so the same list mixed "06:15 a.m." and
  //   "12:18 PM". Normalise on DISPLAY (the stored strings can't be retro-fixed):
  //   strip the periods and upper-case the meridiem so every row reads "06:15 AM".
  const _fmtAtStr=(s)=>String(s||'').replace(/\s*([ap])\.?\s*m\.?/i,(_m,p)=>` ${p.toUpperCase()}M`).trim();
  // V13.4.174 FIX: a lock's "at" was stored ONLY as a pre-formatted local-time
  //   string, baked in by whichever device fired the lock at that instant. Found
  //   via live data: three same-day locks (12:36/12:39/12:47 PM by their own
  //   minsAtLock/closeMs math -- verified against the Kalshi ticker's own close
  //   time) were all stored as "11:36/11:39/11:47 AM", exactly 60 minutes early --
  //   a one-off clock/timezone hiccup on the locking device (e.g. a tab resuming
  //   from sleep with a stale local-time cache) that silently poisoned the string
  //   forever, since nothing else records when a lock actually happened.
  //   The fix: every NEW lock also persists its raw epoch (`ms`). Display now
  //   reformats from that epoch using the VIEWING device's own clock, so a bad
  //   clock on one device can no longer leave a permanently wrong timestamp that
  //   no other device can correct. Rows written before this ship have no `ms`
  //   and fall back to the old (unverifiable) string.
  const _fmtAt=(h)=>{
    const ms=Number(h&&h.ms);
    if(Number.isFinite(ms)&&ms>0)return new Date(ms).toLocaleTimeString([],{hour:'2-digit',minute:'2-digit'});
    return _fmtAtStr(h&&h.at);
  };
  // Strike with thousands separators -- "$63,399.99" instead of "$63399.99".
  const _fmtStrike=(v)=>{
    const n=Number(v);
    return Number.isFinite(n)
      ?'$'+n.toLocaleString('en-US',{minimumFractionDigits:2,maximumFractionDigits:2})
      :`$${v}`;
  };
  const _catCard=(c)=>{
    const n=c.w+c.l;
    const bwr=n>0?Math.round(100*c.w/n):null;
    return(
      <div key={c.label} className="rounded-xl p-3" style={{background:'rgba(237,237,237,0.03)',border:'1px solid rgba(237,237,237,0.08)'}}>
        <div className="text-[10px] text-[#EDEDED]/45">{c.label}</div>
        <div className="text-lg font-mono font-semibold mt-0.5" style={{color:bwr==null?'#EDEDED':bwr>=60?'#23B981':bwr>=45?'#23B981':'#E8455E'}}>{bwr!=null?bwr+'%':'--'}</div>
        <div className="text-[10px] text-[#EDEDED]/40">{c.w}W&middot;{c.l}L{c.net?(' · '+(c.net>=0?'+':'')+c.net+'c'):''}</div>
      </div>
    );
  };
  return(
    <div className={embedded?'tara-hourly-memory-inline':'fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/70 p-4'} onClick={embedded?undefined:onClose}>
      <div className="w-full max-w-3xl rounded-2xl border mt-8 mb-8" style={{background:'#050508',borderColor:'rgba(237,237,237,0.10)'}} onClick={embedded?undefined:e=>e.stopPropagation()}>
        <div className="p-5 border-b flex items-start justify-between" style={{borderColor:'rgba(237,237,237,0.10)'}}>
          <div>
            <div className="text-[10px] uppercase tracking-wide" style={{color:'rgba(212,162,76,0.8)'}}>TARA &middot; HOURLY MEMORY</div>
            <div className="text-xl font-semibold text-[#EDEDED] mt-1">Every hourly lock &middot; her record</div>
          </div>
          {!embedded&&<button onClick={onClose} className="text-[#EDEDED]/50 hover:text-[#EDEDED] text-xl leading-none">&times;</button>}
        </div>
        {/* V13.4.168: win rate leads (it is the question this modal answers), then the
            W/L that produce it, then open. "Total calls" is dropped from the tiles --
            it duplicated wins+losses+open and forced a 5-up grid that wrapped badly. */}
        <div className="p-5 pb-3 grid grid-cols-2 sm:grid-cols-4 gap-2">
          {[
            {label:'Win rate',value:wr!=null?wr+'%':'--',color:wr==null?'#EDEDED':wr>=60?'#23B981':wr>=45?'#23B981':'#E8455E'},
            {label:'Wins',value:rec.w,color:'#23B981'},
            {label:'Losses',value:rec.l,color:'#E8455E'},
            {label:'Open',value:pendingN,color:'#23B981'},
          ].map(c=>(
            <div key={c.label} className="rounded-xl p-3" style={{background:'rgba(237,237,237,0.03)',border:'1px solid rgba(237,237,237,0.08)'}}>
              <div className="text-[10px] uppercase tracking-[0.12em] text-[#EDEDED]/45">{c.label}</div>
              <div className="text-xl font-mono font-semibold mt-0.5" style={{color:c.color}}>{c.value}</div>
            </div>
          ))}
        </div>
        {/* V13.4.168: was a two-clause parenthetical explaining storage internals. The
            only fact that matters is how many locks the number covers. */}
        <div className="px-5 pb-1 text-[10px] text-[#EDEDED]/35">
          Last {history.length} settled hourly locks in the rolling record · {pendingN} still open
        </div>
        {_hasRealBands&&(
          <div className="px-5 pt-3 pb-2">
            <div className="text-[10px] uppercase tracking-[0.12em] text-[#EDEDED]/45 mb-1.5">By time left at lock</div>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">{bandStats.map(_catCard)}</div>
          </div>
        )}
        {sideStats.length>0&&(
          <div className="px-5 pb-4">
            <div className="text-[10px] text-[#EDEDED]/45 mb-1.5">Win rate by side</div>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">{sideStats.map(_catCard)}</div>
          </div>
        )}
        <div className="px-5 pb-3 flex gap-2">
          {[['all','all'],['wins','wins'],['losses','losses']].map(([k,label])=>(
            <button key={k} onClick={()=>setFilter(k)}
              className="text-[11px] px-2.5 py-1 rounded-lg capitalize"
              style={filter===k?{background:'rgba(212,162,76,0.15)',color:'#23B981',border:'1px solid rgba(212,162,76,0.35)'}:{background:'rgba(237,237,237,0.04)',color:'rgba(237,237,237,0.5)',border:'1px solid rgba(237,237,237,0.08)'}}
            >{label}</button>
          ))}
        </div>
        <div className="px-5 pb-5 max-h-[50vh] overflow-y-auto">
          {/* V13.4.137: grouped by the actual HOURLY WINDOW (closeMs -- every entry,
              settled or pending, already carries the exact close time of the hourly
              market it belongs to), not calendar day. Explicit user request: 'all 4pm
              hourly calls go into it, same like 5pm, 6pm... main window time shows that
              hourly and inside we show the price and time it locked.' Pending locks are
              merged in by the same key so an hour still in progress shows everything
              locked into it so far, settled or not. */}
          {(()=>{
            const filterOk=(h)=>filter==='all'||(filter==='wins'&&isWin(h))||(filter==='losses'&&isLoss(h));
            const pendingTagged=(Array.isArray(rec.pending)?rec.pending:[]).map(p=>({...p,_pending:true}));
            const combined=[...history.filter(filterOk),...(filter==='all'?pendingTagged:[])];
            if(combined.length===0)return<div className="text-[12px] text-[#EDEDED]/40 py-6 text-center">No hourly locks logged yet.</div>;
            const _hourKeyFor=(closeMs)=>closeMs?Math.floor(closeMs/3600000):'unknown';
            const _byHour=new Map();
            combined.forEach(h=>{
              const k=_hourKeyFor(h.closeMs);
              if(!_byHour.has(k))_byHour.set(k,[]);
              _byHour.get(k).push(h);
            });
            const _hours=Array.from(_byHour.entries()).sort((a,b)=>(b[0]==='unknown'?0:b[0])-(a[0]==='unknown'?0:a[0]));
            return _hours.map(([hourKey,entries])=>{
              const anyClose=entries.find(h=>h.closeMs)?.closeMs;
              const hourStart=anyClose?new Date(anyClose-3600000):null;
              const hourEnd=anyClose?new Date(anyClose):null;
              const hasPending=entries.some(h=>h._pending);
              // V13.4.168 FIX: this printed "2:00 PM â□□ 3:00 PM". '\xe2\x80\x93' is the
              //   UTF-8 BYTE sequence for an en dash, but in a JS string each \xNN is its
              //   own character, so it produced three garbage chars instead of one dash.
              //   The correct escape for an en dash is the code point, '–'.
              const hourLabel=hourStart&&hourEnd
                ?hourStart.toLocaleTimeString('en-US',{hour:'numeric',minute:'2-digit'})+' – '+hourEnd.toLocaleTimeString('en-US',{hour:'numeric',minute:'2-digit'})
                :'Unknown window';
              const settled=entries.filter(h=>!h._pending);
              const w=settled.filter(isWin).length;
              const l=settled.filter(isLoss).length;
              const resolved=w+l;
              const hourWr=resolved>0?Math.round(100*w/resolved):null;
              const wrColor=hourWr==null?'rgba(237,237,237,0.4)':hourWr>=70?'rgb(35,185,129)':hourWr>=55?'rgba(237,237,237,0.85)':hourWr>=45?'#23B981':'rgb(232,69,94)';
              return(
                <div key={hourKey}>
                  <div className="sticky top-0 z-10 py-2 flex items-baseline justify-between gap-2 backdrop-blur-md" style={{background:'#050508',borderBottom:'1px solid rgba(237,237,237,0.10)'}}>
                    <div className="flex items-baseline gap-2">
                      <span className="text-[11px] uppercase font-bold tracking-[0.14em]" style={{color:'#23B981'}}>{hourLabel}</span>
                      {hasPending&&<span className="text-[9px] uppercase font-bold px-1.5 py-0.5 rounded" style={{background:'rgba(212,162,76,0.15)',color:'#23B981'}}>live</span>}
                      <span className="text-[9px] tabular-nums text-[#EDEDED]/35">{entries.length} lock{entries.length===1?'':'s'}</span>
                    </div>
                    {/* V13.4.168: a percentage on ONE lock is noise -- the old header
                        printed "0% WR 0W·1L" and "100% WR 1W·0L", which look like
                        findings and are not. Show the raw W/L until there are at least
                        two resolved locks in the hour. */}
                    {resolved>0&&(
                      <div className="flex items-baseline gap-2 text-[10px] tabular-nums">
                        {resolved>=2&&<span style={{color:wrColor}} className="font-bold">{hourWr}%</span>}
                        <span style={{color:resolved>=2?'rgba(237,237,237,0.35)':wrColor}}>{w}W&middot;{l}L</span>
                      </div>
                    )}
                  </div>
                  <div className="divide-y" style={{borderColor:'rgba(237,237,237,0.06)'}}>
                    {/* V13.4.168: fixed-width side/cost/time columns so rows line up
                        instead of each one wrapping to its own content width, and the
                        strike carries thousands separators. Net cents surfaced on
                        settled rows -- the actual outcome in money, which the list
                        previously omitted entirely. */}
                    {entries.map((h,i)=>(
                      <div key={i} className="py-2 flex items-center justify-between gap-3 text-[12px]">
                        <div className="flex items-center gap-3 min-w-0">
                          <span className="w-[54px] shrink-0 font-semibold tabular-nums" style={{color:h.side==='YES'?'#23B981':'#E8455E'}}>{h.side==='YES'?'▲':'▼'} {h.side}</span>
                          <span className="text-[#EDEDED]/75 tabular-nums truncate">{_fmtStrike(h.strike)}</span>
                          <span className="text-[#EDEDED]/40 tabular-nums shrink-0">{h.cost}c</span>
                        </div>
                        <div className="flex items-center gap-3 shrink-0">
                          {!h._pending&&Number.isFinite(Number(h.netCents))&&(
                            <span className="text-[11px] tabular-nums" style={{color:Number(h.netCents)>=0?'rgba(35,185,129,0.75)':'rgba(232,69,94,0.75)'}}>
                              {Number(h.netCents)>=0?'+':''}{Number(h.netCents)}c
                            </span>
                          )}
                          <span className="text-[#EDEDED]/40 tabular-nums w-[64px] text-right">{_fmtAt(h)}</span>
                          {h._pending
                            ?<span className="font-semibold text-[10px] uppercase w-[42px] text-right" style={{color:'#23B981'}}>open</span>
                            :<span className="font-semibold text-[11px] w-[42px] text-right" style={{color:resultColor(h)}}>{resultLabel(h)}</span>}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              );
            });
          })()}
        </div>
      </div>
    </div>
  );
}
function useHourlyEngine({spot,spotAt,source,asset='BTC',readiness,taraCall,onHourlyLock}){
  const[_hourlyStatus,setHourlyStatus]=React.useState({code:'starting',reason:'Waiting for fresh hourly observations.'});
  const confirmationRef=React.useRef(null),busyRef=React.useRef(false),mountedRef=React.useRef(false),commitHoldRef=React.useRef(null);
  React.useEffect(()=>{mountedRef.current=true;return()=>{mountedRef.current=false;};},[]);
  const nowMs=Date.now();
  const minsToHour=(60-new Date(nowMs).getMinutes())-(new Date(nowMs).getSeconds()/60);
  const on=(()=>{try{return localStorage.getItem('taraHourlyLadder')!=='off';}catch{return true;}})();
  const lad=useHourlyLadder(on?minsToHour:null);
  const{rec,addLock}=useHourlyRecord();
  const policy=readHourlyPolicy(localStorage),CONFIRM_TICKS=policy.confirmTicks;
  const minsLeft=lad.closeMs?Math.max(0,(lad.closeMs-nowMs)/60000):minsToHour;
  const dir=['UP','DOWN'].includes(taraCall?.call)?taraCall.call:null,s=Number(spot)||0;
  const input={lad,spot,spotAt,source,asset,readiness,signal:taraCall,record:rec,now:nowMs,enabled:on};
  const latestInputRef=React.useRef(input);latestInputRef.current=input;
  const assessment=assessHourlyPick(input,policy,taraHourlyProb);
  // Only persisted, accepted picks appear as LOCKED. Restore their actual time
  // and approval evidence; a refresh never resets cooldown to an invented zero.
  const LOCKS=rec.pending.filter(p=>p.closeMs===lad.closeMs)
    .map(p=>({...p,dir:p.side==='YES'?'UP':'DOWN'})).sort((a,b)=>(b.ms||0)-(a.ms||0));
  React.useEffect(()=>{
    const step=advanceHourlyConfirmation(confirmationRef.current,assessment,input);
    confirmationRef.current=step.state;
    const show=(code,reason)=>{if(mountedRef.current)setHourlyStatus(old=>old.code===code&&old.reason===reason?old:{code,reason});};
    if(commitHoldRef.current?.poll!==lad?.sampleId)commitHoldRef.current=null;
    if(!busyRef.current)show(commitHoldRef.current?.code||step.code,commitHoldRef.current?.reason||step.reason);
    if(!step.ok||busyRef.current)return;
    const proposal=hourlyProposal(step,input,TARA_BUILD_VERSION);
    busyRef.current=true;show('checking','Hourly confirmed; checking the shared record.');
    (async()=>{
      try{
        const result=await addLock(proposal,record=>{
          if(!mountedRef.current||confirmationRef.current?.key!==step.state.key||!confirmationRef.current.samples.length)
            return {ok:false,code:'candidate-changed',reason:'Hourly observation changed; confirming again.'};
          const latest={...latestInputRef.current,record,now:Date.now()};
          return revalidateHourlyProposal(proposal,latest,readHourlyPolicy(localStorage),taraHourlyProb);
        });
        if(!result.accepted){
          confirmationRef.current={samples:[],key:null,lastPoll:latestInputRef.current.lad?.sampleId,lastSpotAt:latestInputRef.current.spotAt};
          commitHoldRef.current={poll:latestInputRef.current.lad?.sampleId,code:result.code,reason:result.reason};
          show(result.code,result.reason);return;
        }
        show('recorded','Hourly pick saved with its confirmation and approval evidence.');
        if(typeof onHourlyLock==='function'){
          try{onHourlyLock({...proposal,spot:Number(input.spot),record:{w:result.record.w,l:result.record.l,open:result.record.pending.length}});}catch(_e){}
        }
      }catch{
        confirmationRef.current={samples:[],key:null,lastPoll:latestInputRef.current.lad?.sampleId,lastSpotAt:latestInputRef.current.spotAt};
        commitHoldRef.current={poll:latestInputRef.current.lad?.sampleId,code:'record-error',reason:'Hourly record check could not finish; waiting for fresh confirmation.'};
        show(commitHoldRef.current.code,commitHoldRef.current.reason);
      }finally{busyRef.current=false;}
    })();
  });
  return {on,lad,rec,LOCKS,assessment,input,policy,minsLeft,CONFIRM_TICKS,s,dir,
    confirmation:confirmationRef.current,status:_hourlyStatus};
}

// Presentation only: the single owner in TaraApp keeps confirming/settling
// even while Sports, Weather or another workspace is visible.
function HourlyLadderPanel({engine}){
  const[_hourlyMemOpen,_setHourlyMemOpen]=React.useState(false);
  const{on,lad,rec,LOCKS,assessment,minsLeft,CONFIRM_TICKS,s,dir,status:_hourlyStatus,confirmation}=engine;
  const confirmationRef={current:confirmation};
  const rows=(lad.strikes||[]).map(k=>{
    const p=taraHourlyProb(s,k.strike,minsLeft);
    return {...k,modelPct:p==null?null:p*100,costYes:k.ask,costNo:100-k.bid};
  });
  const tradeable=rows.filter(r=>r.modelPct!=null&&r.ask>2&&r.ask<98);
  const _conflictKeys=new Set();
  for(const a of LOCKS){for(const b of LOCKS){
    if(a===b||a.side===b.side)continue;
    const yesK=a.side==='YES'?a.strike:b.strike,noK=a.side==='NO'?a.strike:b.strike;
    if(yesK>=noK){_conflictKeys.add(a.ms);_conflictKeys.add(b.ms);}
  }}
  const _lockedSet=new Set(LOCKS.map(x=>x.strike+'|'+x.side));
  const _watchPick=assessment.pick?.strike??null;
  const L={ticks:confirmationRef.current?.samples?.length||0};
  if(!on)return null; // V13.4.120 FIX: moved from above the useEffect (see note above) -- panel still hides the same way, hook now always runs
  return(
    <div className="tara-v145-hourly-panel mt-3 rounded-xl border border-zinc-800 bg-zinc-900/60 p-3">
      <HourlyApprovalSummary locks={LOCKS} candidate={assessment.ok?assessment.pick:null} status={_hourlyStatus} samples={confirmationRef.current?.samples||[]} required={assessment.pick?.needTicks||CONFIRM_TICKS}/>
      {!!rec.settlementHealth?.failures?.length&&<div role="status" data-hourly-settlement="waiting" className="text-xs text-amber-300">Settlement waiting: {rec.settlementHealth.failures.map(f=>`${f.ticker}: ${f.reason}`).join(' · ')}. Pending records are retained.</div>}
      <details className="tara-v145-disclosure" open><summary>Strike ladder, all open picks, record &amp; export</summary><div className="tara-v145-hourly-details">
      <div className="flex items-center justify-between mb-2">
        <div className="text-[11px] uppercase tracking-wider text-zinc-400">Hourly ladder <span className="text-zinc-600">KXBTCD</span></div>
        <div className="flex items-center gap-2">
          {/* V13.4.232: this W-L is NOT a lifetime record, and not saying so made
              two browsers look like they disagreed. _hrMerge slices history to the
              last _HR_HISTORY_CAP (500) and then recounts w and l FROM that slice,
              so the number is a rolling window. Each origin keeps its own
              localStorage and syncs through Supabase with lag, so a device one
              settlement ahead has a window shifted by one: it gains an entry at the
              top and drops the oldest off the bottom. Gaining a win while dropping
              a loss moves BOTH counters, which is why taracalls read 306W-194L
              while tara1 read 305W-195L off the identical build — the two windows
              started an hour apart (08-03 19:02 vs 18:04). The 15m record got this
              same label in V13.4.162 for the same reason; the hourly never did. */}
          <span className="text-[11px] tabular-nums" title={`Rolling window: the last ${_HR_HISTORY_CAP} settled hourly locks, not an all-time total. Two devices can differ by a few while they catch up on sync — each keeps its own copy and the window shifts as new settlements arrive.`}>
            <span className="text-emerald-400 font-semibold">{rec.w}W</span>
            <span className="text-zinc-600">-</span>
            <span className="text-rose-400 font-semibold">{rec.l}L</span>
            {(rec.w+rec.l)>0&&<span className="text-zinc-500"> ({Math.round(100*rec.w/(rec.w+rec.l))}%)</span>}
            <span className="text-zinc-600"> · rolling {rec.history.length}/{_HR_HISTORY_CAP}</span>
            {rec.pending.length>0&&<span className="text-amber-400/70"> +{rec.pending.length} open</span>}
          </span>
          <span className="text-[11px] text-zinc-400" title={lad.closeMs?new Date(lad.closeMs).toLocaleString():'Current clock hour'}>
            {lad.closeMs&&minsLeft>90?'No current-hour market':minsLeft.toFixed(1)+'m left'}
          </span>
        </div>
      </div>
      {/* V13.4.117: real, exportable hourly log -- mirrors the exact JSON-download pattern
          the main 15m call log already uses (Blob + createObjectURL + click), so the same
          upload-and-audit workflow works here: export, send it over, I run the same kind
          of real analysis on it that the 15m archive has had all night. */}
      <button
        className="mb-2 px-2 py-1 rounded-lg text-[10px] uppercase tracking-wider font-bold transition-colors"
        style={{background:'rgba(124,93,250,0.08)',color:'rgba(196,181,253,0.9)',border:'1px solid rgba(196,181,253,0.25)'}}
        onClick={()=>{
          try{
            const _full=_hrLoad();
            const _payload={
              exportedAt:new Date().toISOString(),
              baselineVersion:(typeof BASELINE_VERSION!=='undefined')?BASELINE_VERSION:'unknown',
              kind:'hourly-ladder-log',
              totalSettled:_full.history.length,
              wins:_full.w,losses:_full.l,
              winRate:(_full.w+_full.l)>0?Math.round(100*_full.w/(_full.w+_full.l)):null,
              pending:_full.pending,
              history:_full.history,
            };
            const _json=JSON.stringify(_payload,null,2);
            const _blob=new Blob([_json],{type:'application/json'});
            const _url=URL.createObjectURL(_blob);
            const _a=document.createElement('a');
            const _ts=new Date().toISOString().replace(/[:.]/g,'-').slice(0,19);
            _a.href=_url;
            _a.download=`tara-hourly-log-${_ts}.json`;
            document.body.appendChild(_a);
            _a.click();
            document.body.removeChild(_a);
            setTimeout(()=>URL.revokeObjectURL(_url),100);
          }catch(e){
            alert('Export failed: '+(e.message||String(e)));
          }
        }}
      >Export Hourly Log</button>
      {/* V13.4.133: memory button, matching the 15m TARA MEMORY page's role for
          the hourly ladder -- explicit user request, 'i want hourlys to have a log
          memory section too.' */}
      <button
        className="ml-2 text-[11px] px-2.5 py-1 rounded-lg"
        style={{background:'rgba(212,162,76,0.08)',color:'rgba(255,255,255,0.62)',border:'1px solid rgba(212,162,76,0.25)'}}
        onClick={()=>_setHourlyMemOpen(true)}
      >Memory</button>
      {_hourlyMemOpen&&React.createElement(HourlyMemoryModal,{onClose:()=>_setHourlyMemOpen(false)})}
      {/* V13.4.170: OPEN LOCKS HOISTED. They used to render below the cost-band summary
          AND the collapsible settled history, i.e. two blocks down the card, which is a
          large part of "I keep missing hourly locks". The live position is the single
          most important thing on this panel, so it sits directly under the header now;
          the historical summaries follow. */}
      {LOCKS.length>0&&(
        <div className="mb-3 space-y-1.5">
          <div className="text-[9px] uppercase tracking-[0.16em] font-bold" style={{color:'rgba(255,255,255,0.62)'}}>
            {LOCKS.length} open lock{LOCKS.length===1?'':'s'} this hour
          </div>
          {/* V13.4.170: open locks no longer fade. The old `idx===0?'':'opacity-60'`
              dimmed every lock except the newest, so a still-open position looked
              half-cancelled -- part of why these get missed. Every open lock is a real
              position and renders at full strength. */}
          {/* V13.4.271: key was LK.ms alone, which React reported as a duplicate `0`
              -- two open locks lacking a timestamp both keyed on 0. Duplicate keys let
              React duplicate or DROP siblings, which on a ladder of open positions
              means a real lock can silently fail to render. Index makes it unique
              without losing the identity ms gives when it is present. */}
          {LOCKS.map((LK,_lkIdx)=>(
            <div key={String(LK.ms||'no-ms')+'|'+_lkIdx} className={'rounded-lg border p-2 '+(LK.dir==='UP'?'border-emerald-500/60 bg-emerald-500/10':'border-rose-500/60 bg-rose-500/10')}>
              <div className="flex items-center justify-between">
                {/* V13.4.169: every open lock is locked -- only the newest said so,
                    which made the older one look like a pending suggestion. */}
                <div className={'text-[13px] font-semibold '+(LK.dir==='UP'?'text-emerald-400':'text-rose-400')}>
                  LOCKED {LK.side} @ {Number(LK.strike).toLocaleString('en-US')}
                </div>
                <div className="text-[13px] font-semibold text-zinc-100 tabular-nums">{LK.cost}c</div>
              </div>
              {/* V13.4.169: omit what we do not have instead of printing "--". Locks
                  written before this ship carry no conf/model/mktPct, and rendering
                  "Tara conf 0 | mkt % | model --" stated a confidence of zero as if it
                  were real. Each stat now appears only when it exists. */}
              {(()=>{
                const _bits=[];
                if(Number(LK.conf)>0)_bits.push(`Signal ${Number(LK.conf).toFixed(0)}/100`);
                if(LK.mktPct!=null&&Number.isFinite(Number(LK.mktPct)))_bits.push(`mkt ${Number(LK.mktPct).toFixed(0)}%`);
                if(LK.model!=null&&Number.isFinite(Number(LK.model)))_bits.push(`model ${LK.side} ${(LK.side==='NO'?100-Number(LK.model):Number(LK.model)).toFixed(1)}%`);
                if(LK.approval)_bits.push(`${LK.approval.samples.length} fresh samples · est. net edge ${LK.approval.netEdgeCents.toFixed(1)}¢`);
                return _bits.length
                  ?<div className="mt-0.5 text-[11px] text-zinc-400 tabular-nums">{_bits.join('  ·  ')}</div>
                  :null;
              })()}
              <div className="mt-0.5 text-[10px] text-zinc-500">
                {[
                  LK.at||null,
                  LK.minsAtLock!=null?`${Number(LK.minsAtLock).toFixed(0)}m left`:null,
                  LK.band?`historical model band ${LK.band}`:null,
                ].filter(Boolean).join('  ·  ')}
                {_conflictKeys.has(LK.ms)&&<span className="ml-1 text-amber-400 font-semibold">— CONFLICTS with another lock, both cannot win</span>}
              </div>
            </div>
          ))}
        </div>
      )}
      {lad.err&&<div className="text-[11px] text-amber-400 mb-1">feed: {lad.err}</div>}
      {/* V13.4.115: real settlement history, inline, no console needed. Directly answers
          'theres no real logs to check and verify later' -- rec.history has always had this
          data (ticker, side, strike, cost, result, won, netCents, settledAt), it just was
          never rendered anywhere. Shows the last 8, newest first. */}
      {/* V13.4.121: WR-by-cost-band summary, visible inline without exporting.
          Directly answers 'sure-shot vs long-shot, at a glance' -- splits settled
          history into <40c (long shot), 40-65c (target band), >65c (chased/expensive)
          so it's obvious which band is actually dragging the win rate down. */}
      {rec.history.length>=3&&(function(){
        const _bands=[
          {label:'<40c (long shot)',test:h=>h.cost<40},
          {label:'40-65c (target)',test:h=>h.cost>=40&&h.cost<=65},
          {label:'>65c (chased)',test:h=>h.cost>65},
        ].map(b=>{
          const _lst=rec.history.filter(b.test);
          const _w=_lst.filter(h=>h.won).length;
          const _net=_lst.reduce((s,h)=>s+(h.netCents||0),0);
          return{...b,n:_lst.length,wr:_lst.length?Math.round(100*_w/_lst.length):null,net:_net};
        });
        return React.createElement('div',{className:'mb-2 grid grid-cols-3 gap-1.5'},
          _bands.map(b=>React.createElement('div',{
            key:b.label,
            className:'rounded-lg px-2 py-1.5 text-center '+(b.n===0?'bg-zinc-800/40':(b.net>=0?'bg-emerald-500/10':'bg-rose-500/10')),
          },
            React.createElement('div',{className:'text-[9px] uppercase tracking-wide text-zinc-500'},b.label),
            React.createElement('div',{className:'text-[13px] font-semibold tabular-nums '+(b.n===0?'text-zinc-600':(b.net>=0?'text-emerald-400':'text-rose-400'))},
              b.n===0?'--':(b.wr+'% ('+b.n+')')),
            b.n>0&&React.createElement('div',{className:'text-[9px] tabular-nums text-zinc-500'},(b.net>=0?'+':'')+b.net+'c'),
          )),
        );
      })()}
      {rec.history.length>0&&(function(){
        const _recent=rec.history.slice(-8).reverse();
        return React.createElement('details',{className:'mb-2'},
          React.createElement('summary',{className:'text-[10px] text-zinc-500 cursor-pointer select-none'},'settled history ('+rec.history.length+' total, showing last '+_recent.length+')'),
          React.createElement('div',{className:'mt-1.5 space-y-1'},
            _recent.map((h,i)=>React.createElement('div',{
              // V13.4.271: was h.ticker+i, which is ADDITION, not concatenation, when
              //   ticker is a number or undefined -- undefined+0 is NaN, and every row
              //   then keys NaN. Index first makes it a string and unique regardless of
              //   what ticker holds.
              key:'h'+i+'-'+String(h.ticker||''),
              className:'flex items-center justify-between text-[11px] rounded px-2 py-1 '+(h.won?'bg-emerald-500/10 text-emerald-300':'bg-rose-500/10 text-rose-300'),
            },
              React.createElement('span',null,(h.won?'WIN ':'LOSS ')+h.side+' @ '+(h.strike!=null?Number(h.strike).toFixed(0):'?')),
              React.createElement('span',{className:'tabular-nums'},h.cost+'c -> '+(h.won?'+':'')+(h.netCents!=null?h.netCents:'?')+'c'),
              React.createElement('span',{className:'text-zinc-500'},h.settledAt?new Date(h.settledAt).toLocaleTimeString([],{hour:'2-digit',minute:'2-digit'}):''),
            )),
          ),
        );
      })()}
      <div className="mb-2 text-[11px] text-zinc-400" role="status" data-hourly-gate={_hourlyStatus.code}>
        {_hourlyStatus.reason}
      </div>
      {!tradeable.length&&<div className="text-[11px] text-zinc-500">{lad.loading?'loading ladder...':'no liquid strikes yet'}</div>}
      {!!tradeable.length&&(
        <div className="space-y-1">
          <div className="grid grid-cols-5 gap-2 text-[10px] uppercase tracking-wide text-zinc-500">
            <div>Strike</div><div className="text-right">Model YES</div><div className="text-right">Yes</div><div className="text-right">No</div><div className="text-right">Status</div>
          </div>
          {tradeable.map(r=>{
            const near=s>0&&Math.abs(r.strike-s)<=150;
            const side=dir==='UP'?'YES':(dir==='DOWN'?'NO':null);
            const cost=side==='YES'?r.costYes:(side==='NO'?r.costNo:null);
            const cheap=cost!=null&&cost<50;
            const yesLocked=_lockedSet.has(r.strike+'|YES');
            const noLocked=_lockedSet.has(r.strike+'|NO');
            const isWatch=_watchPick===r.strike;
            const stat=(yesLocked||noLocked)
              ?{txt:(yesLocked?'YES':'NO')+' LOCKED',cls:(yesLocked?'text-emerald-400':'text-rose-400')+' font-semibold'}
              :(isWatch?{txt:(side||'')+' WATCH '+L.ticks+'/'+CONFIRM_TICKS,cls:'text-amber-400'}
                      :{txt:'',cls:'text-zinc-700'});
            return(
              // V13.4.271: keyed on r.ticker, which React reported as a duplicate.
              //   This component already knows ticker is not unique on its own -- the
              //   open-lock code above builds ticker+'|'+side for exactly that reason.
              //   A strike ladder has one row per strike, so strike IS the identity.
              //   Duplicate keys let React drop siblings, i.e. a strike silently
              //   missing from the ladder.
              <div key={'strike-'+r.strike} className={'grid grid-cols-5 gap-2 text-[11px] py-0.5 '+(near?'text-zinc-100':'text-zinc-500')}>
                <div className="tabular-nums">{r.strike.toFixed(0)}</div>
                <div className="text-right tabular-nums">{r.modelPct.toFixed(0)}%</div>
                <div className={'text-right tabular-nums '+(yesLocked?'text-emerald-400 font-semibold':(side==='YES'&&cheap?'text-emerald-400':''))}>{r.ask}c</div>
                <div className={'text-right tabular-nums '+(noLocked?'text-rose-400 font-semibold':(side==='NO'&&cheap?'text-emerald-400':''))}>{r.costNo}c</div>
                <div className={'text-right text-[10px] '+stat.cls}>{stat.txt}</div>
              </div>);
          })}
          {/* V13.4.148: this line used to read "Tara reads UP. Buy YES ..." straight off
              the LIVE direction, directly beneath locks that may be the opposite side --
              a real screenshot showed it under "LOCKED NO @ 64500". Both were accurate
              (a live read now, a lock taken earlier) but side by side it reads as the
              panel contradicting itself. Now it says WHEN the read is from, and calls
              out a conflict with open locks instead of quietly implying one. */}
          <div className="pt-1 text-[10px] leading-snug text-zinc-500">
            {(()=>{
              if(!dir)return 'No directional call right now -- ladder shown for reference only.';
              const _openOpp=LOCKS.filter(x=>x.side!==(dir==='UP'?'YES':'NO'));
              const _base='Live candidate: '+dir+'. Hourly approval checks signal confidence, fresh confirmations and the hourly model against price plus estimated fees. Model edge is an estimate; recorded locks retain their original evidence.';
              if(_openOpp.length){
                return React.createElement(React.Fragment,null,
                  React.createElement('span',{className:'text-amber-400/90'},
                    `Heads up: ${_openOpp.length} open lock${_openOpp.length>1?'s are':' is'} on the OTHER side `
                    +`(${_openOpp.map(x=>x.side+'@'+Math.round(x.strike)).join(', ')}), taken earlier this hour. `
                    +`Tara's live read has since moved to ${dir}. The locks are not wrong -- they were taken on `
                    +`a different read -- but do not add to this side without deciding which one you believe. `),
                  React.createElement('span',null,_base),
                );
              }
              return _base;
            })()}
          </div>
        </div>
      )}
      </div></details>
    </div>);
}
function TaraAdvisorPanel({advisor,executeAction}){
  if(!advisor||!advisor.label||advisor.label==='CONNECTING...')return null;
  // V13.4.275: this panel no longer restates a PRE-COMMIT lean. Stage 1 of THIS
  //   TRADE owns that now, and the two could never agree: this reads the
  //   sample-confirmed lean (taraCallSampleRef) while the card reads the engine's
  //   live call, so on screen it was "LEANING UP" beside "Tara watching DOWN" for
  //   the same window. Two different quantities, both true, both labelled as though
  //   they were Tara's opinion.
  //   What this panel still does that nothing else does is kept: the ENTER action on
  //   a committed lock, and the STALE-LOCK warning that kills that button when
  //   conditions have inverted since the lock. Both carry hasAction or animate.
  const _isWatching=/^Tara (watching|scanning)/i.test(String(advisor.label||''));
  if(_isWatching&&!advisor.hasAction&&!advisor.animate)return null;
  const colorMap={
    emerald:'border-emerald-500/40 bg-emerald-500/10 text-emerald-400',
    rose:   'border-rose-500/40 bg-rose-500/10 text-rose-400',
    amber:  'border-amber-500/40 bg-amber-500/10 text-amber-400',
    zinc:   'border-zinc-500/30 bg-zinc-500/10 text-zinc-400',
  };
  const cls=colorMap[advisor.color]||colorMap.zinc;
  const animate=advisor.animate?'animate-pulse':'';
  const canClick=advisor.hasAction&&advisor.actionLabel&&advisor.actionTarget&&executeAction;
  return(
    <div className={'mt-2 p-3 rounded-lg border '+cls+' '+animate}>
      <div className="flex items-center gap-1.5 mb-1.5">
        <IC.Bell className="w-3.5 h-3.5"/>
        <span className={'text-[10px] uppercase tracking-widest font-bold opacity-70'}>Tara Advisor</span>
      </div>
      <div className="text-sm font-serif font-bold leading-tight mb-1">{advisor.label}</div>
      {advisor.reason&&(
        <div className={'text-[11px] leading-snug opacity-80'}>{advisor.reason}</div>
      )}
      {canClick?(
        <button
          onClick={()=>executeAction(advisor.actionTarget,advisor.actionLabel)}
          className={'mt-2 text-[10px] uppercase tracking-wider font-bold px-3 py-1.5 rounded-lg border border-current hover:bg-current/10 active:scale-95 transition-all cursor-pointer'}>
          → {advisor.actionLabel}
        </button>
      ):advisor.hasAction&&advisor.actionLabel?(
        <div className={'mt-2 text-[10px] uppercase tracking-wider font-bold opacity-70 px-2 py-1 rounded-lg border border-current inline-block'}>
          → {advisor.actionLabel}
        </div>
      ):null}
    </div>
  );
}


// ══════════════════════════════════════════════════
// V111 — IMPROVED LAYOUT COMPONENTS
// ══════════════════════════════════════════════════


// V5.6.1: Tara's Call card extracted into reusable component so it can render in both
//   the desktop projections column (hidden md:block) and the mobile signal section
//   (md:hidden). Same logic, same display, two mount points.
// ── V8.0: DECISIONAL OVERLAY — IN-CARD DECISION SUPPORT ─────────────────────
// Renders inside TaraCallCard. Surfaces FOUR signals that should change how user
// trades the current window: conviction trajectory, edge vs Kalshi, suggested
// position size, and cooldown after loss.
function DecisionalOverlay({taraCall,kalshiYesPrice,convictionTrajectory,todayData,analysis,movementRisk,bestWindowsToday,desktopSplit}){
  if(!taraCall)return null;
  // V13.4.284: `taraCall.posterior` is never written (engine exposes rawProbAbove),
  //   so _post was always 0 and _hasPost always false — this component's EDGE chip
  //   could never render. Its trajectory chip was dead too, for the same root cause
  //   further down (convictionHistoryRef never filled). Two of its five chips were
  //   unreachable; it only ever appeared when the size / cooldown / risk chip fired.
  // V13.4.285 CORRECTION: v284 changed this from taraCall.posterior to
  //   taraCall.rawProbAbove — both are phantoms on taraCall. `analysis` is the
  //   object that exposes rawProbAbove, and it is already a prop here.
  const _post=Number(analysis?.rawProbAbove??0);
  const _hasPost=Number.isFinite(_post)&&_post>0;
  const _dir=taraCall.snapshot?taraCall.snapshot.call:taraCall.direction;
  // V13.4.288: three ad-hoc conditions standing in for one. This gates the SIZE
  //   recommendation and the trajectory row, so an uncommitted snapshot could size
  //   a position that was never locked.
  const _isLocked=readLockState(taraCall.snapshot).tradeable;
  // ── Edge vs Kalshi ── Tara's directional confidence MINUS Kalshi's implied directional probability
  let _edge=null,_edgeColor=null,_edgeLabel=null;
  if(_hasPost&&kalshiYesPrice!=null&&_dir&&(_dir==='UP'||_dir==='DOWN')){
    const _taraDirConf=_dir==='UP'?_post:(100-_post);
    const _kalshiDirConf=_dir==='UP'?Number(kalshiYesPrice):(100-Number(kalshiYesPrice));
    _edge=Math.round(_taraDirConf-_kalshiDirConf);
    _edgeColor=_edge>=15?'rgb(35,185,129)':_edge>=5?'#23B981':_edge>=-5?'#23B981':'#E8455E';
    _edgeLabel=_edge>=15?'BIG EDGE':_edge>=5?'GOOD EDGE':_edge>=-5?'TIGHT':'LATE';
  }
  // ── Position size hint ── Combine regime vol + tilt status + edge + time-of-day into recommendation
  let _sizeHint=null,_sizeColor=null,_sizeReason=null;
  // V8.2: Time-of-day scaling — find current UTC hour entry from bestWindowsToday
  const _curUtcH=new Date().getUTCHours();
  const _curHourEntry=bestWindowsToday?.best?.find(h=>h.hour===_curUtcH);
  const _isInBestHour=_curHourEntry&&_curHourEntry.wr>=70&&_curHourEntry.total>=4;
  // V10.7.98: SIGNAL CONFLUENCE TIER — the real win-rate predictor.
  //   Empirical (1865 trades): when gap+flow+momentum all align in the call's direction,
  //   win rate climbs sharply and EV/trade is highest:
  //     gap>=20 flow>=15 mom>=10 → 76% WR, +7.5¢/trade (PRIME)
  //     gap>=25 flow>=15 mom>=10 → 77% WR, +8.1¢/trade
  //     gap>=30 flow>=15 mom>=10 → 82% WR, +7.3¢/trade
  //     gap>=20 alone            → 71% WR, +3.0¢/trade (STRONG)
  //     gap>=10 aligned          → 69% WR (STANDARD — already the floor after V10.7.97)
  //   This is the wheelhouse: swing hardest when all three signals confirm.
  const _sigScores=analysis?.rawSignalScores||{};
  const _dirSign=_dir==='UP'?1:(_dir==='DOWN'?-1:0);
  const _gapAl=Number(_sigScores.gap||0)*_dirSign;
  const _flowAl=Number(_sigScores.flow||0)*_dirSign;
  const _momAl=Number(_sigScores.momentum||0)*_dirSign;
  // Confluence levels
  const _isPrimePlus=_gapAl>=30&&_flowAl>=15&&_momAl>=10;   // 82% WR
  const _isPrime=_gapAl>=20&&_flowAl>=15&&_momAl>=10;        // 76% WR
  const _isStrongConfluence=_gapAl>=20&&_flowAl>=10;         // 73% WR
  const _isStrong=_gapAl>=20;                                 // 71% WR
  if(todayData?.strongTilt){
    _sizeHint='SKIP';_sizeColor='rgb(232,69,94)';_sizeReason='5+ losses in a row';
  } else if(todayData?.tilt){
    _sizeHint='HALF';_sizeColor='#E8455E';_sizeReason='3+ loss streak — cut size';
  } else if(_edge!=null&&_edge<-5&&!_isPrime){
    _sizeHint='SKIP';_sizeColor='#E8455E';_sizeReason='negative edge vs Kalshi';
  } else if(_isPrimePlus&&_isLocked){
    _sizeHint='MAX';_sizeColor='rgb(35,185,129)';_sizeReason='all signals aligned strong (82% WR zone)';
  } else if(_isPrime&&_isLocked){
    _sizeHint='LARGE';_sizeColor='rgb(35,185,129)';_sizeReason='gap+flow+momentum aligned (76% WR zone)';
  } else if(_isStrongConfluence&&_isLocked){
    _sizeHint='FULL';_sizeColor='rgba(35,185,129,0.9)';_sizeReason='gap+flow aligned (73% WR zone)';
  } else if(_isStrong&&_isLocked){
    _sizeHint='FULL';_sizeColor='#23B981';_sizeReason='strong gap (71% WR zone)';
  } else if(_edge!=null&&_edge>=15&&_isInBestHour&&_isLocked){
    _sizeHint='LARGE';_sizeColor='rgb(35,185,129)';_sizeReason=`big edge + ${_curHourEntry.wr}% historical hour`;
  } else if(analysis?.regime==='HIGH_VOL'||analysis?.regime==='EXTREME_VOL'){
    _sizeHint='HALF';_sizeColor='#23B981';_sizeReason='vol regime — cut size';
  } else if(_isLocked&&(((_gapAl>3?1:0)+(_flowAl>3?1:0)+(_momAl>3?1:0))<=1)){
    // V10.9.4: WEAK-AGREEMENT DOWNSIZE.
    //   Same n_aligned definition as the V10.9.0 signal-agreement gate (each of
    //   gap/flow/momentum > 3 counts as aligned). When 2+ signals agree, the
    //   trade is in the 65-67% WR / +2.6-4.1¢ zone — NORMAL sizing is right.
    //   When only 1 (or 0) of 3 agree, the data shows 48% WR, -4.4¢/trade
    //   (n=189) — the worst bucket measured, currently betting full NORMAL
    //   size on a near-coin-flip with negative EV. V10.9.0 already makes these
    //   wait longer before locking; if one DOES lock, cut the size too.
    _sizeHint='HALF';_sizeColor='rgba(232,69,94,0.7)';_sizeReason='only 1 signal aligned (48% WR zone) — cut size';
  } else if(_isLocked){
    _sizeHint='NORMAL';_sizeColor='rgba(237,237,237,0.7)';_sizeReason='standard window (69% WR zone)';
  }
  // ── Cooldown after loss ── soft hint to slow down for a few minutes after fresh loss
  const _showCooldown=todayData?.inCooldown&&todayData?.cooldownMinSinceLoss!=null&&!_isLocked;
  // ── Conviction trajectory ──
  const _showTrajectory=_isLocked&&convictionTrajectory&&convictionTrajectory.state!=='UNKNOWN';
  const _trajColor=convictionTrajectory?.state==='BUILDING'?'#23B981':convictionTrajectory?.state==='FADING'?'#E8455E':'rgba(237,237,237,0.55)';
  const _trajIcon=convictionTrajectory?.state==='BUILDING'?'↗':convictionTrajectory?.state==='FADING'?'↘':'→';
  // V8.1: Movement risk chip activates this overlay too (only at ELEVATED+)
  const _showRiskChip=movementRisk&&(movementRisk.level==='ELEVATED'||movementRisk.level==='EXTREME');
  // Don't render if nothing meaningful to show
  if(!_edgeLabel&&!_sizeHint&&!_showCooldown&&!_showTrajectory&&!_showRiskChip)return null;
  return React.createElement('div',{className:'mt-2 pt-2 border-t border-[#24242E]'},
    React.createElement('div',{className:'flex items-center justify-between gap-2 flex-wrap'},
      React.createElement('div',{className:'flex items-baseline gap-2 sm:gap-3 flex-wrap'},
        // Edge pill
        _edgeLabel&&React.createElement('div',{
          className:'flex items-baseline gap-1 px-1.5 py-0.5 rounded-lg',
          style:{background:'rgba(237,237,237,0.04)',border:'1px solid #24242E'},
          title:'Tara\'s directional confidence minus Kalshi\'s. Positive = Tara more confident; negative = Kalshi prices it better than Tara.',
        },
          React.createElement('span',{className:'text-[8px] uppercase tracking-wider font-bold text-[#EDEDED]/40'},'Edge'),
          React.createElement('span',{className:'text-[10px] tabular-nums font-bold',style:{color:_edgeColor}},(_edge>=0?'+':'')+_edge,'pt'),
          React.createElement('span',{className:'text-[8px] uppercase tracking-wider font-bold',style:{color:_edgeColor}},_edgeLabel),
        ),
        // Position size hint
        _sizeHint&&React.createElement('div',{
          className:'flex items-baseline gap-1 px-1.5 py-0.5 rounded-lg',
          style:{background:'rgba(237,237,237,0.04)',border:'1px solid #24242E'},
          title:`Size hint: ${_sizeHint}${_sizeReason?' · '+_sizeReason:''}`,
        },
          React.createElement('span',{className:'text-[8px] uppercase tracking-wider font-bold text-[#EDEDED]/40'},'Size'),
          React.createElement('span',{className:'text-[10px] uppercase font-bold tracking-wider',style:{color:_sizeColor}},_sizeHint),
        ),
        // Conviction trajectory (when locked)
        _showTrajectory&&React.createElement('div',{
          className:'flex items-baseline gap-1 px-1.5 py-0.5 rounded-lg',
          style:{background:'rgba(237,237,237,0.04)',border:'1px solid #24242E'},
          title:`Conviction since lock: ${convictionTrajectory.state.toLowerCase()}${convictionTrajectory.delta?' ('+(convictionTrajectory.delta>0?'+':'')+convictionTrajectory.delta.toFixed(1)+'pp)':''}`,
        },
          React.createElement('span',{className:'text-[8px] uppercase tracking-wider font-bold text-[#EDEDED]/40'},'Conv'),
          React.createElement('span',{className:'text-[11px] font-bold leading-none',style:{color:_trajColor}},_trajIcon),
          React.createElement('span',{className:'text-[8px] uppercase tracking-wider font-bold',style:{color:_trajColor}},convictionTrajectory.state),
        ),
        // V8.1: Movement risk chip — only when ELEVATED or higher (clutter avoidance)
        // V13.4.297: skipped on desktop (desktopSplit) -- RiskBannerCard shows
        //   this in column 3 now, so the chip here would just be a second,
        //   less complete copy of the same movementRisk. Mobile (no
        //   desktopSplit prop, no separate Risk panel) keeps showing it.
        !desktopSplit&&movementRisk&&(movementRisk.level==='ELEVATED'||movementRisk.level==='EXTREME')&&React.createElement('div',{
          className:'flex items-baseline gap-1 px-1.5 py-0.5 rounded-lg',
          style:{
            background:movementRisk.level==='EXTREME'?'rgba(232,69,94,0.08)':'rgba(35,185,129,0.06)',
            border:'1px solid '+(movementRisk.level==='EXTREME'?'rgba(232,69,94,0.30)':'rgba(35,185,129,0.22)'),
          },
          title:[
            `Movement risk: ${movementRisk.level} (${movementRisk.score}/100)`,
            movementRisk.predictive?`→ ${movementRisk.predictive}`:null,
            ...movementRisk.reasons.slice(0,4).map(r=>`· ${r.tag}`),
          ].filter(Boolean).join('\n'),
        },
          React.createElement('span',{className:'text-[8px] uppercase tracking-wider font-bold text-[#EDEDED]/40'},'Risk'),
          React.createElement('span',{className:'text-[10px] uppercase font-bold tracking-wider',style:{color:movementRisk.level==='EXTREME'?'rgb(232,69,94)':'#23B981'}},movementRisk.level),
          movementRisk.dirBias&&React.createElement('span',{className:'text-[10px] font-bold leading-none',style:{color:movementRisk.level==='EXTREME'?'rgb(232,69,94)':'#23B981'}},movementRisk.dirBias==='UP'?'↑':'↓'),
        ),
      ),
      // V8.1: Predictive readout strip (only when notable signal)
      // V13.4.297: skipped on desktop (desktopSplit) -- same text as
      //   RiskBannerCard's body ("-> {predictive}"), which now shows it in
      //   column 3. Mobile keeps showing it here, its only Risk surface.
      !desktopSplit&&movementRisk&&movementRisk.predictive&&movementRisk.score>=45&&React.createElement('div',{
        className:'mt-2 text-[10px] italic',
        style:{color:movementRisk.level==='EXTREME'?'#E8455E':'#23B981'},
      },
        React.createElement('span',null,'→ ',movementRisk.predictive),
      ),
      // Cooldown indicator
      _showCooldown&&React.createElement('div',{
        className:'text-[10px] flex items-baseline gap-1.5 px-1.5 py-0.5 rounded-lg mt-1.5',
        style:{background:'rgba(35,185,129,0.05)',border:'1px solid rgba(35,185,129,0.18)',color:'#23B981'},
      },
        React.createElement('span',null,'⧗'),
        React.createElement('span',{className:'tabular-nums'},`${todayData.cooldownMinSinceLoss}m since loss · slow down`),
      ),
    ),
  );
}

// V13.4.226: price the sit-outs.
//
//   Measured across the last 1,000 calls: 554 sit-outs, of which 235 had no
//   directional read at all (nothing to take) and 319 had one and were vetoed.
//   Taking all 319 at the price recorded at lock would have LOST 251c. No price
//   band beats its own break-even by more than its 95% interval — the apparent
//   edges are all inside the noise.
//
//   The trap this measures around: `kalshiAtLock` is the UP (yes) price, so a
//   DOWN lean costs 100 minus it, not it. Verified against traded entries —
//   execCostAtLock matches kalshiAtLock on UP calls (avg 69 vs 71) and matches
//   100-kalshiAtLock on DOWN calls (avg 33 -> 67 vs 70). Reading it directly
//   makes DOWN bets look ~34c cheaper than they were and flips the whole verdict
//   from "sitting out is right" to "relax the gates", which is exactly backwards.
// V13.4.227: measured edge of the strategy the auto-trader would be automating.
//
//   Measured on the real 1,000-call log at the time this shipped: 444 settled
//   calls, 68% win rate, average entry 68c — and 68c needs 68% to break even, so
//   the record was -806c, or -1.82c per contract with a 95% range of +/-4.20.
//   Indistinguishable from zero.
//
//   Excluding the 70-84c band (blocked since v13.4.178, so mostly pre-gate
//   history) it is +1.76c per contract on n=345 — still inside its own error
//   bars. That is the whole point of putting this above the arm switch: the
//   auto-trader is already well built, and none of its features change whether
//   the thing it automates makes money.
//
//   Same pricing trap as the sit-out figures: kalshiAtLock is the UP price, so a
//   DOWN call costs 100 minus it. 254 of 445 traded calls are DOWN; reading it
//   directly flatters the record by roughly 34c on most entries.
const _autoExecReadiness=(log)=>{
  if(!Array.isArray(log)||!log.length)return null;
  const t=log.filter(e=>e&&(e.result==='WIN'||e.result==='LOSS')&&(e.dir==='UP'||e.dir==='DOWN')
    &&e.originalDecision?.provenance==='captured-at-lock'&&e.officialSettlement?.source==='kalshi-public-api'
    &&Number(e.originalDecision?.yesAsk)>0&&Number(e.originalDecision?.yesAsk)<100
    &&Number(e.originalDecision?.yesBid)>0&&Number(e.originalDecision?.yesBid)<100);
  if(t.length<40)return null;
  const pnl=[];let cost=0,wins=0,maker=0,fwd=0,fwdN=0,fwdGood=0;
  for(const e of t){
    const c=e.dir==='UP'?Number(e.originalDecision.yesAsk):100-Number(e.originalDecision.yesBid);
    const f=_sitoutFee(c), won=e.result==='WIN';
    cost+=c; if(won)wins++;
    pnl.push(won?(100-c-f):-(c+f));
    // Same calls, resting 1c inside instead of crossing. Kalshi's maker fee is 0,
    //   so this drops both the fee and a cent of spread.
    const cm=c-1;
    maker+=won?(100-cm):-cm;
    // What a PERPETUAL would have captured: the move from the lock onward. The
    //   binary settles against the window's open, so it also banks whatever moved
    //   before the lock; a perp marks from where you enter and cannot.
    if(Number(e.spotAtLock)>0&&Number(e.closingPrice)>0){
      const sign=e.dir==='UP'?1:-1;
      const bps=((Number(e.closingPrice)-Number(e.spotAtLock))/Number(e.spotAtLock))*10000*sign;
      fwd+=bps; fwdN++; if(bps>0)fwdGood++;
    }
  }
  const per=pnl.reduce((a,b)=>a+b,0)/pnl.length;
  const sd=Math.sqrt(pnl.reduce((a,p)=>a+Math.pow(p-per,2),0)/Math.max(1,pnl.length-1));
  const ci=1.96*sd/Math.sqrt(pnl.length);
  // n needed for the interval to clear zero if this average holds
  const needed=(per>0&&per<Infinity)?Math.max(0,Math.ceil(Math.pow(1.96*sd/per,2))-pnl.length):0;
  const _avgCost=cost/pnl.length;
  return{n:pnl.length,per,ci,sd,
    wr:Math.round(wins/pnl.length*100),be:Math.round(_avgCost),
    // The bar is price PLUS the fee. Winning at exactly the price is not flat —
    //   it is down the fee on every contract, which is the whole of the -806c.
    beTrue:Math.round(_avgCost+_sitoutFee(_avgCost)),
    makerPer:maker/pnl.length,
    fwdBps:fwdN?fwd/fwdN:null, fwdGoodPct:fwdN?Math.round(fwdGood/fwdN*100):null,
    needed:per>0?needed:0,
    verdict:(per-ci)>0?'proven':(per+ci)<0?'losing':'unproven'};
};

// V13.4.229: the hourly ladder measured on the same footing as the 15m lane.
//   Measured at ship time: hourly n=500, +4.39c per contract, 95% range +0.15 to
//   +8.64 — the only lane in the app whose interval clears zero. The 15m lane
//   over the same period is -2.07c, range -6.29 to +2.14. The difference is the
//   entry: hourly averages 55c against a 57% true break-even and wins 61%, while
//   the 15m averages 68c against a 70% bar and wins 68%. Cheap entries are the
//   whole edge, and the hourly's 40-54c band alone is +8.3c per contract.
//
//   This matters for arming: auto-exec's enabledWindowTypes covers 15m and 5m
//   (5m has no open markets at all) and does NOT include the hourly. As shipped
//   it is armed on the unproven lane and switched off on the proven one.
const _hourlyLaneStats=()=>{
  try{
    const r=JSON.parse(localStorage.getItem('taraHourlyRecord_v1')||'null');
    const H=((r&&r.history)||[]).filter(e=>e&&Number.isFinite(Number(e.cost))&&Number(e.cost)>0&&Number(e.cost)<100);
    if(H.length<40)return null;
    const p=H.map(e=>{const c=Number(e.cost);const f=_sitoutFee(c);
      return e.won?(100-c-f):-(c+f);});
    const m=p.reduce((a,b)=>a+b,0)/p.length;
    const sd=Math.sqrt(p.reduce((a,x)=>a+Math.pow(x-m,2),0)/Math.max(1,p.length-1));
    const ci=1.96*sd/Math.sqrt(p.length);
    const cost=H.reduce((a,e)=>a+Number(e.cost),0)/H.length;
    return{n:p.length,per:m,ci,total:m*p.length,
      be:Math.round(cost+_sitoutFee(cost)),
      wr:Math.round(H.filter(e=>e.won).length/H.length*100),
      verdict:(m-ci)>0?'proven':(m+ci)<0?'losing':'unproven'};
  }catch(_e){return null;}
};

// V13.4.237: recover a gate name for sit-outs whose gate never stamped one.
//   Patterns are taken from the reason strings actually present in the log, so
//   this classifies real history rather than inventing buckets. Returns null when
//   the entry is not a sit-out at all, so ordinary calls are untouched.
const _deriveSitoutCategory=(snap)=>{
  if(!snap)return null;
  const isSit=snap.isNoGo===true||snap.call==='SIT_OUT'||snap.dir==='SIT_OUT'
    ||snap.wasOverriddenSitOut===true||snap.wasOverriddenNoTrade===true;
  if(!isSit)return null;
  const t=String(snap.reason||snap.caution||'').toLowerCase();
  // V13.4.238: `tier` survives the cloud minify while `reason` does not, so on any
  //   entry that has been through a round-trip it is the only surviving clue. All
  //   94 of the live log's reason-less sit-outs carry one (time-cap-commit 60,
  //   gap-opposed-timecap-sitout 22, directional-lock 10). Used before the
  //   no-signal fallback so a real gate name always beats a shrug.
  if(!t&&snap.tier)return String(snap.tier);
  // No reason AND no directional read means no gate ever fired — Tara simply
  //   never formed a view. That is a different thing from "a gate vetoed this and
  //   did not say which", and lumping them together is what made the 145 look
  //   like an unexplained block. Naming it keeps the genuinely unexplained
  //   remainder small enough to chase.
  if(!t)return (Number(snap.confidence)>0)?'uncategorised-sitout':'no-signal';
  if(/dead zone|dead-zone|70-84/.test(t))              return 'no-go-edge';
  if(/window-closed|past entry|chance gone/.test(t))   return 'kalshi-window-closed';
  if(/no kalshi price|price is unavail|price-unavail/.test(t)) return 'no-go-price-unavailable';
  if(/gap guard|gap>=|gap opposed|weak gap/.test(t))   return 'weak-gap-directional-sitout';
  if(/coin.?flip|deadzone/.test(t))                    return 'coinflip-deadzone';
  if(/chop/.test(t))                                   return 'chop-sitout';
  if(/dead window|bps range/.test(t))                  return 'dead-window';
  if(/edge .*vs kalshi|priced up at|priced down at/.test(t)) return 'v11-kalshi-edge';
  if(/quality|low.?q/.test(t))                         return 'low-quality-gate';
  if(/cold|streak|cooldown/.test(t))                   return 'cooldown';
  return 'uncategorised-sitout';
};

const _sitoutFee=(p)=>Math.ceil(0.07*(p/100)*(1-p/100)*100);
const _sitoutValue=(log)=>{
  if(!Array.isArray(log)||!log.length)return null;
  const so=log.filter(e=>e&&e.result==='SITOUT');
  if(!so.length)return null;
  const withRead=so.filter(e=>e.confidence>0&&e.posterior!=null
    &&Number(e.strike)>0&&Number(e.closingPrice)>0
    &&Number(e.kalshiAtLock)>0&&Number(e.kalshiAtLock)<100);
  if(withRead.length<20)return null;
  let net=0,wins=0,cost=0;
  for(const e of withRead){
    const lean=e.posterior>50?'UP':'DOWN';
    const won=(Number(e.closingPrice)>=Number(e.strike))===(lean==='UP');
    const c=lean==='UP'?Number(e.kalshiAtLock):100-Number(e.kalshiAtLock);
    cost+=c; if(won)wins++;
    net+=won?(100-c-_sitoutFee(c)):-(c+_sitoutFee(c));
  }
  return{total:so.length,n:withRead.length,noRead:so.length-withRead.length,
    netCents:net,wr:Math.round(wins/withRead.length*100),
    be:Math.round(cost/withRead.length)};
};

// V13.4.294: hoisted out of TaraCallCard so the new standalone
//   ConvictionMeterCard/EntryPricingCard/RecordCard (dashboard-mockup-rebuild
//   pass 2) can share the exact same snapshot normalisation instead of
//   duplicating it -- one implementation, not three copies to keep in sync.
const _normalizeLegacySnap=(_snap)=>{
  if(!_snap)return _snap;
  const _legacyNoGo=_snap.call==='NO_TRADE'||_snap.isNoGo===true;
  const _legacySitOut=_snap.call==='SIT_OUT';
  if(!_legacyNoGo&&!_legacySitOut)return _snap;
  const _impliedDir=_snap.direction||(_snap.atPosterior!=null?(_snap.atPosterior>=50?'UP':'DOWN'):null);
  if(!_impliedDir)return _snap; // can't promote, leave as-is (TaraCallCard SCANNING fallback)
  const _post=typeof _snap.atPosterior==='number'?_snap.atPosterior:50;
  const _snapConf=Number(_snap.confidence);
  const _conf=(Number.isFinite(_snapConf)&&_snapConf>0)
    ?Math.round(_snapConf)
    :(_impliedDir==='UP'?Math.max(50,Math.round(_post)):Math.max(50,Math.round(100-_post)));
  return{
    ..._snap,
    call:_impliedDir,
    direction:_impliedDir,
    confidence:_conf,
    caution:_snap.caution||_snap.reason||(_legacyNoGo?'No-trade conditions present':'Sit-out conditions present'),
    isNoGo:false,
    wasOverriddenNoTrade:_legacyNoGo,
    wasOverriddenSitOut:_legacySitOut,
    locked:true,
  };
};

// V13.4.294: Conviction meter, extracted verbatim from inside TaraCallCard so
//   it can render as its own panel in column 1 (mockup order: This Trade ->
//   Conviction -> Entry Pricing) instead of being bundled with everything else
//   TaraCallCard renders. Same computation, same JSX -- just callable alone.
function ConvictionMeterCard({taraCall,analysis}){
  const tc=taraCall;
  if(!tc)return null;
  const snap=_normalizeLegacySnap(tc.snapshot);
  const _livePost=analysis?.rawProbAbove??snap?.atPosterior??50;
  const _post=Number(_livePost);
  if(!isFinite(_post))return null;
  const _dir=(()=>{
    const _c=(snap&&(snap.call==='UP'||snap.call==='DOWN'))?snap.call
      :(tc&&(tc.call==='UP'||tc.call==='DOWN'))?tc.call
      :(tc&&(tc.direction==='UP'||tc.direction==='DOWN'))?tc.direction:null;
    return _c||(_post>=50?'UP':'DOWN');
  })();
  const _dirPost=_dir==='UP'?_post:(100-_post);
  const _signedConv=_dirPost-50;
  const _opposing=_signedConv<0;
  const _conv=Math.abs(_signedConv);
  const _zone=_opposing?'opposing':_conv<5?'deadzone':_conv<10?'weak':_conv<15?'moderate':'strong';
  const _zoneLabel=_opposing?'OPPOSING — live has moved against this call':_conv<5?'DEADZONE — coin flip':_conv<10?'WEAK conviction':_conv<15?'MODERATE conviction':'STRONG conviction';
  const _zoneColor=_opposing?'rgb(232,69,94)':_conv<5?'rgb(232,69,94)':_conv<10?'#23B981':_conv<15?'rgb(35,185,129)':'rgb(52,211,153)';
  const _zoneBg=_opposing?'rgba(232,69,94,0.08)':_conv<5?'rgba(232,69,94,0.06)':_conv<10?'rgba(35,185,129,0.06)':_conv<15?'rgba(35,185,129,0.06)':'rgba(52,211,153,0.08)';
  const _zoneBorder=_opposing?'rgba(232,69,94,0.40)':_conv<5?'rgba(232,69,94,0.30)':_conv<10?'rgba(35,185,129,0.30)':_conv<15?'rgba(35,185,129,0.30)':'rgba(52,211,153,0.40)';
  const _dirColor=_opposing?'rgb(232,69,94)':(_dir==='UP'?'rgb(35,185,129)':'rgb(232,69,94)');
  const _maxScale=30;
  const _convClamped=Math.min(_conv,_maxScale);
  const _fillPct=(_convClamped/_maxScale)*100;
  return(
    <div className="bg-[#0A0A0E] p-3 rounded-[10px] border border-[#1B1B22] shrink-0">
      <div className="px-0.5 py-0.5 rounded-lg" style={{background:_zoneBg,border:'1px solid '+_zoneBorder,padding:'6px 10px'}}>
        <div className="flex items-center justify-between mb-1 gap-2">
          <div className="flex items-baseline gap-1.5">
            <span className="text-[9px] uppercase tracking-[0.18em] font-bold" style={{color:_zoneColor}}>Conviction</span>
            <span className="text-[10px] font-bold tabular-nums" style={{color:_dirColor}}>{_conv.toFixed(1)}pt {_opposing?'⚠':(_dir==='UP'?'▲':'▼')}</span>
          </div>
          <span className="text-[9px] uppercase tracking-wider font-bold" style={{color:_zoneColor}}>{_zoneLabel}</span>
        </div>
        <div className="relative h-1.5 rounded-full overflow-hidden" style={{background:'rgba(237,237,237,0.06)'}}>
          <div className="absolute inset-y-0 left-0" style={{width:'16.6%',background:'rgba(232,69,94,0.10)'}}/>
          <div className="absolute inset-y-0 left-0 transition-all" style={{width:`${_fillPct}%`,background:_zoneColor}}/>
          <div className="absolute inset-y-0" style={{left:'16.6%',width:'1px',background:'rgba(237,237,237,0.20)'}}/>
          <div className="absolute inset-y-0" style={{left:'33.3%',width:'1px',background:'rgba(237,237,237,0.15)'}}/>
          <div className="absolute inset-y-0" style={{left:'50%',width:'1px',background:'rgba(237,237,237,0.15)'}}/>
        </div>
        <div className="flex justify-between mt-0.5 text-[8px] tabular-nums text-[#EDEDED]/30">
          <span>0</span>
          <span style={{marginLeft:'3%'}}>5</span>
          <span style={{marginLeft:'5%'}}>10</span>
          <span style={{marginLeft:'5%'}}>15</span>
          <span>30+</span>
        </div>
      </div>
    </div>
  );
}

// V13.4.294: "Entry Pricing" -- the mockup's take/rest/saves panel. Extracted
//   verbatim from TaraCallCard's "EXECUTION ROW" (V13.4.213); this already WAS
//   the panel the mockup wants, just buried in the wrong column. Needs only
//   {taraCall} -- _kalshiQuote/_quoteUsable are module-level globals.
function EntryPricingCard({taraCall}){
  const tc=taraCall;
  try{
    const _q=(typeof _kalshiQuote!=='undefined')?_kalshiQuote:null;
    const _fresh=!!(_q&&_q.at&&(Date.now()-_q.at)<60000&&_quoteUsable(_q));
    const _dir=(tc?.call==='UP'||tc?.call==='DOWN')?tc.call:(tc?.direction==='UP'||tc?.direction==='DOWN'?tc.direction:null);
    if(!_fresh||!_dir)return null;
    const _take=_dir==='UP'?_q.ask:(100-_q.bid);
    const _rest=_dir==='UP'?_q.bid:(100-_q.ask);
    if(!Number.isFinite(_take)||!Number.isFinite(_rest))return null;
    const _fee=0.07*(_take/100)*(1-_take/100)*100;
    const _save=(_take-_rest)+_fee;
    // V13.4.301: rebuilt to the mockup's Entry Pricing panel -- a header rule,
    //   then three hairline-divided cells (Take / Or rest at / Saves), each a
    //   tiny-caps label over an 18px mono figure. It was previously one cramped
    //   inner box-inside-a-box with the three figures run together on a single
    //   line, which is the "box in a box" texture the brief set out to remove.
    //   Saves keeps green: money kept is an outcome.
    return(
      <div className="rounded-[10px] border border-[#1B1B22] bg-[#0A0A0E] overflow-hidden shrink-0">
        <div className="px-3.5 py-2 border-b border-[#16161c] flex items-baseline justify-between">
          <span className="text-[9px] uppercase tracking-[0.15em] font-bold text-[#EDEDED]/30">Entry pricing</span>
          <span className="text-[9px] uppercase tracking-[0.15em] font-bold text-[#EDEDED]/30">Kalshi {_dir.toLowerCase()}</span>
        </div>
        <div className="grid grid-cols-3 gap-px" style={{background:'#16161c'}}>
          <div className="px-4 py-3" style={{background:'#0A0A0E'}}>
            <div className="text-[9px] uppercase tracking-[0.15em] font-bold text-[#EDEDED]/30 mb-1.5">Take</div>
            <div className="tabular-nums" style={{fontSize:'18px'}}>{_take.toFixed(0)}&cent;</div>
          </div>
          <div className="px-4 py-3" style={{background:'#0A0A0E'}}>
            <div className="text-[9px] uppercase tracking-[0.15em] font-bold text-[#EDEDED]/30 mb-1.5">Or rest at</div>
            <div className="tabular-nums" style={{fontSize:'18px'}}>{_rest.toFixed(0)}&cent;</div>
          </div>
          <div className="px-4 py-3" style={{background:'#0A0A0E'}}>
            <div className="text-[9px] uppercase tracking-[0.15em] font-bold text-[#EDEDED]/30 mb-1.5">Saves</div>
            <div className="tabular-nums" style={{fontSize:'18px',color:'#23B981'}}>{_save.toFixed(1)}&cent;</div>
          </div>
        </div>
      </div>
    );
  }catch(_e){return null;}
}

// V13.4.294: "Record" -- extracted verbatim from TaraCallCard's Scorecard
//   section. Needs only {taraScorecards, windowType}.
function RecordCard({taraScorecards,windowType}){
  const sc=taraScorecards?.[windowType]||{wins:0,losses:0,sitouts:0};
  const total=(sc.wins||0)+(sc.losses||0);
  const wr=total>0?Math.round((sc.wins/total)*100):null;
  // V13.4.299: rebuilt to the approved mockup. It used to lead with three
  //   equally-sized coloured counts (green wins / red losses / gold sat-out),
  //   which gave a sat-out tally the same visual weight as the win rate and put
  //   three accent colours in one small panel. The mockup leads with the single
  //   number that answers "is she any good" -- the win rate, big and green --
  //   then drops the counts underneath at label size. Colour follows the one
  //   accent rule: losses stay red because a loss is an outcome, sat-out is
  //   neutral grey because sitting out is not a result.
  return(
    <div className="rounded-[10px] border border-[#1B1B22] bg-[#0A0A0E] overflow-hidden shrink-0">
      <div className="px-3.5 py-2 border-b border-[#16161c] flex items-baseline justify-between">
        <span className="text-[9px] uppercase tracking-[0.15em] text-[#EDEDED]/30 font-bold">Record</span>
        <span className="text-[9px] uppercase tracking-[0.15em] text-[#EDEDED]/30 font-bold">{_TARA_RECORD_WINDOW_LABEL}</span>
      </div>
      <div className="px-4 py-3.5">
        {wr!==null
          ?<div className="tabular-nums leading-none" style={{fontSize:'30px',color:'#23B981'}}>{wr}%</div>
          :<div className="tabular-nums leading-none" style={{fontSize:'30px',color:'rgba(237,237,237,0.30)'}}>&mdash;</div>}
        <div className="flex gap-4 mt-3.5">
          <div>
            <div className="text-[9px] uppercase tracking-[0.15em] text-[#EDEDED]/30 font-bold mb-1">Won</div>
            <div className="tabular-nums" style={{fontSize:'16px'}}>{sc.wins||0}</div>
          </div>
          <div>
            <div className="text-[9px] uppercase tracking-[0.15em] text-[#EDEDED]/30 font-bold mb-1">Lost</div>
            <div className="tabular-nums" style={{fontSize:'16px',color:'#E8455E'}}>{sc.losses||0}</div>
          </div>
          <div>
            <div className="text-[9px] uppercase tracking-[0.15em] text-[#EDEDED]/30 font-bold mb-1">Sat out</div>
            <div className="tabular-nums" style={{fontSize:'16px',color:'rgba(237,237,237,0.42)'}}>{sc.sitouts||0}</div>
          </div>
        </div>
      </div>
    </div>
  );
}

// V13.4.295: "Risk" -- the mockup's elevated-movement-risk banner. Didn't exist
//   as a standalone panel before; MovementRiskPill (a small header pill) has
//   the data but not the card chrome. movementRisk.predictive already reads
//   "${dirBias} move likely next 30-60s" (the risk-scoring hook), so this is a
//   wrapper around existing data, not a new prediction.
function RiskBannerCard({movementRisk}){
  if(!movementRisk)return null;
  const{level,score,dirBias,predictive}=movementRisk;
  if(level!=='ELEVATED'&&level!=='EXTREME')return null;
  const _extreme=level==='EXTREME';
  // V13.4.299: matched to the mockup's Risk panel. The tinted header fill is
  //   gone -- the mockup carries caution purely as gold ink on the two labels
  //   plus a warmed border, with the body text left plain. Gold is the mockup's
  //   caution colour and this is the panel it was reserved for.
  const _color=_extreme?'rgb(232,69,94)':T2_SITOUT_FG;
  const _borderColor=_extreme?'#3a2020':'#3a2a20';
  const _headerBorder=_extreme?'#241616':'#241c16';
  const _bodyText=predictive
    ?predictive.charAt(0).toUpperCase()+predictive.slice(1)
    :`Movement risk ${level.toLowerCase()} (${score}/100)`;
  return(
    <div className="rounded-[10px] border overflow-hidden shrink-0" style={{borderColor:_borderColor,background:'#0A0A0E'}}>
      <div className="px-3.5 py-2 border-b flex items-baseline justify-between" style={{borderColor:_headerBorder}}>
        <span className="text-[9px] uppercase tracking-[0.15em] font-bold" style={{color:_color}}>Risk</span>
        <span className="text-[9px] uppercase tracking-[0.15em] font-bold" style={{color:_color}}>{level.charAt(0)+level.slice(1).toLowerCase()}</span>
      </div>
      <div className="px-4 py-3 text-[12.5px]" style={{color:'rgba(237,237,237,0.72)'}}>
        {_bodyText}{dirBias?` (${dirBias==='UP'?'↑':'↓'})`:''}
      </div>
    </div>
  );
}

// V13.4.295: "Schedule -- where she does well" -- the mockup's historical
//   by-session win-rate panel. TradeScheduleStrip is forward-looking (what's
//   coming up), not this. The computation already existed, buried in the Stats
//   modal (StatsView) as a 2x2 grid; reused here verbatim, laid out as the
//   mockup's horizontal bars instead.
function ScheduleBySessionCard({taraCallLog}){
  const completed=(taraCallLog||[]).filter(t=>t.result==='WIN'||t.result==='LOSS');
  const bySession={};
  completed.forEach(t=>{
    const sess=t.session||'—';
    if(!bySession[sess])bySession[sess]={W:0,L:0};
    bySession[sess][t.result==='WIN'?'W':'L']++;
  });
  const _dom=(typeof getMarketSessions==='function'?getMarketSessions().dominant:null)||null;
  const SESSIONS=[
    {key:'ASIA',label:'Asia'},
    {key:'EU',label:'Europe'},
    {key:'US',label:'US'},
    {key:'OFF-HOURS',label:'Off-hours'},
  ];
  const _rows=SESSIONS.map(s=>{
    const v=bySession[s.key]||{W:0,L:0};
    const n=v.W+v.L;
    const wr=n>0?(100*v.W/n):0;
    return{...s,n,wr,isCurrent:_dom===s.key};
  });
  const _best=_rows.filter(r=>r.n>=5).sort((a,b)=>b.wr-a.wr)[0]||null;
  return(
    <div className="bg-[#0A0A0E] rounded-[10px] border border-[#1B1B22] flex flex-col min-w-0">
      <div className="px-3.5 py-2.5 border-b border-[#16161c] flex items-baseline justify-between gap-2">
        <span className="text-[9px] uppercase tracking-[0.18em] font-bold text-[#EDEDED]/45">Schedule <span className="text-[#EDEDED]/25">· where she does well</span></span>
        <span className="text-[9px] uppercase tracking-[0.18em] font-bold text-[#EDEDED]/30">By session</span>
      </div>
      <div className="py-1">
        {_rows.map(r=>(
          <div key={r.key} className={'flex items-center gap-3.5 px-3.5 py-2.5 border-b border-[#101015] last:border-b-0'+(r.isCurrent?' bg-emerald-500/[0.04]':'')}>
            <span className="text-[12px] min-w-[64px]" style={{color:r.isCurrent?'#EDEDED':'rgba(237,237,237,0.65)'}}>{r.label}</span>
            <div className="flex-1 h-[5px] rounded-full bg-[#16161c] overflow-hidden">
              <div className="h-full rounded-full" style={{width:(r.n>0?r.wr:0)+'%',background:r.n<2?'rgba(237,237,237,0.15)':r.wr>=65?'#23B981':r.wr>=50?'rgba(35,185,129,0.6)':'rgba(232,69,94,0.6)'}}/>
            </div>
            <span className="text-[10px] tabular-nums font-bold min-w-[30px] text-right" style={{color:r.n<2?'rgba(237,237,237,0.3)':r.wr>=65?'#23B981':r.wr>=50?'rgba(237,237,237,0.75)':'#E8455E'}}>{r.n>0?r.wr.toFixed(0)+'%':'—'}</span>
            <span className="text-[9px] uppercase tracking-wider text-[#EDEDED]/30 min-w-[46px] text-right">n={r.n}</span>
          </div>
        ))}
      </div>
      {(_dom||_best)&&(
        <div className="px-3.5 py-2.5 border-t border-[#16161c] flex items-center gap-2">
          {_dom&&<span className="text-[9px] uppercase tracking-wider font-bold" style={{color:'#23B981'}}>Now · {SESSIONS.find(s=>s.key===_dom)?.label||_dom}</span>}
          <span className="flex-1"/>
          {_best&&<span className="text-[9px] uppercase tracking-wider text-[#EDEDED]/30">Best: {_best.label} ({_best.wr.toFixed(0)}%)</span>}
        </div>
      )}
    </div>
  );
}

// V13.4.294: added `desktopSplit` -- when true, this instance skips rendering
//   Conviction / Entry Pricing / Record inline, because they're shown via the
//   three components above instead, placed in their correct mockup columns.
//   The MOBILE call site never passes this prop, so mobile is unaffected and
//   keeps showing everything bundled together, exactly as before.
function TaraCallCard({taraCall,taraScorecards,taraCallLog,windowType,timeState,analysis,className,taraLearnings,onSoftHint,onHardForce,kalshiYesPrice,useLocalTime,timeFormat,onEditEntry,onDeleteEntry,convictionTrajectory,todayData,movementRisk,bestWindowsToday,handleManualSync,userPosition,reversalRisk,desktopSplit}){
  if(!taraCall)return null;
    const tc=taraCall;
    const sc=taraScorecards?.[windowType]||{wins:0,losses:0,sitouts:0};
    const total=(sc.wins||0)+(sc.losses||0);
    const wr=total>0?Math.round((sc.wins/total)*100):null;
    const snap=_normalizeLegacySnap(tc.snapshot);
    const isCommittedSnap=snap!==null;
    // V8.9.2: isNoGoSnap and isSatOutSnap are now structurally false thanks to
    //   _normalizeLegacySnap. Kept as variables to avoid touching the many
    //   ternary branches below — they just always evaluate to false now.
    const isNoGoSnap=false;
    // V13.4.288: was `snap && call!==SIT_OUT` -- presence of a non-sit-out snapshot,
    //   not commitment. Equivalent only while every snapshot happens to carry
    //   locked:true, which was false for eight writers until v287. Routed through the
    //   authority so it cannot drift again. Promoted legacy sit-outs still read
    //   tradeable here (the normaliser gives them a direction AND locked:true) --
    //   that is deliberate: isOverrideSitOut below is what renders SITTING OUT and
    //   suppresses the Follow-Tara button for them.
    const isLockedSnap=readLockState(snap).tradeable&&!isNoGoSnap;
    const isSatOutSnap=false;
    // V5.6.5: USER-VISIBLE STATES.
    //   No snapshot yet     → SCANNING (no direction, no UP/DOWN reveal)
    //   Snapshot LOCKED UP  → LOCKED UP
    //   Snapshot LOCKED DN  → LOCKED DOWN
    //   Snapshot SIT_OUT    → SITTING OUT (legacy — V7.8 removed most paths)
    //   Snapshot NO_TRADE   → NO TRADE (V7.9 — explicit no-go with reason)
    // The internal tc.call still tracks current posterior direction (used by the lifecycle
    // effect to count consistency samples) but it does NOT leak to the headline anymore.
    // User feedback: "she doesn't call anything, no signals too. tara's calls are only for
    // her locks. ideally she locks and stays quicker but if needed she can take time."
    const dispConfidence=snap?snap.confidence:0;
    // V6.0.4: Surface live gate info during scanning.
    const dispReason=snap
      ?(snap.reason||(isSatOutSnap?'Sat out — no edge this window':isNoGoSnap?'No-go':'Locked'))
      :(tc?.reason||'Scanning for direction.');
    // V6.0.4: live lean — gate sees a direction but hasn't committed yet
    // V6.0.5: lean threshold 4→5pt. Real lean = ≥5pt off neutral. Below that, headline
    //   stays SCANNING. The lean info still surfaces in the Decision Clock + reason.
    const _liveLeanDir=!snap&&tc?.direction&&tc?.conviction>=5?tc.direction:null;
    const _liveLeanConv=tc?.conviction||0;
    // V6.0.5: 5-state model. Was 4-state (SCANNING / LOCKED UP / LOCKED DOWN / SITTING OUT).
    //   Now adds WATCHING UP / WATCHING DOWN as headline states when conv ≥ 5pt and no snap.
    //   User feedback: needed direction visibility BEFORE commit so they can enter Kalshi
    //   while odds are still actionable. WATCHING uses muted-color version of LOCKED hues
    //   so it reads as "leaning" not "committed."
    const isWatching=!snap&&!!_liveLeanDir;
    const isCall=isLockedSnap;
    const effDir=isLockedSnap?snap.call:isWatching?_liveLeanDir:null;
    // V10.7.47: detect the "promoted sit-out" state. V8.9.2 forced every SIT_OUT to
    //   carry a direction (so user always has something to override/follow), but the
    //   headline label was still saying LOCKED — confusing the user about what Tara
    //   actually decided. We keep the lean direction visible (color + arrow) but make
    //   the headline read SITTING OUT honestly.
    const isOverrideSitOut=isLockedSnap&&(snap?.wasOverriddenSitOut===true);
    // V10.9.8: no-go-edge / no-go-coinflip-late are real directional locks with a
    //   pricing caution — the user wants them shown as normal LOCKED calls (with an
    //   edge note), not as "NO TRADE". Only no-go-data remains a true no-trade
    //   override (data failure). isEdgeWatch carries the caution into the sublabel
    //   while letting the headline read LOCKED UP / LOCKED DOWN.
    const _snapTierLc=snap?.tier||snap?.noGoCategory||'';
    const isEdgeWatch=isLockedSnap&&(_snapTierLc==='no-go-edge'||_snapTierLc==='no-go-coinflip-late');
    const isOverrideNoTrade=isLockedSnap&&(snap?.wasOverriddenNoTrade===true)&&!isEdgeWatch;
    const isOverrideAny=isOverrideSitOut||isOverrideNoTrade;
    const callColor=isOverrideAny
      ?(effDir==='UP'?'text-amber-300':'text-amber-300')
      :isLockedSnap
      ?(effDir==='UP'?'text-emerald-300':'text-rose-300')
      :isWatching
        ?(effDir==='UP'?'text-emerald-400/75':'text-rose-400/75')
        :isNoGoSnap?'text-red-400'
        :isSatOutSnap?'text-amber-400/85':'text-amber-300/80';
    const borderClr=isOverrideAny
      ?'rgba(212,162,76,0.40)'
      :isLockedSnap
      ?(effDir==='UP'?'rgba(52,211,153,0.45)':'rgba(232,69,94,0.45)')
      :isWatching
        ?(effDir==='UP'?'rgba(52,211,153,0.25)':'rgba(232,69,94,0.25)')
        :isNoGoSnap?'rgba(232,69,94,0.55)'
        :isSatOutSnap?'rgba(212,162,76,0.4)':T2_GOLD_BORDER;
    const bgClr=isOverrideAny
      ?'rgba(212,162,76,0.06)'
      :isLockedSnap
      ?(effDir==='UP'?'rgba(52,211,153,0.08)':'rgba(232,69,94,0.08)')
      :isWatching
        ?(effDir==='UP'?'rgba(52,211,153,0.04)':'rgba(232,69,94,0.04)')
        :isNoGoSnap?'rgba(232,69,94,0.10)'
        :isSatOutSnap?'rgba(212,162,76,0.06)':'rgba(35,185,129,0.05)';
    // V13.4.124: DECISIVE EARLY SIT-OUT DISPLAY. User request: 'if its a sitout we
    //   lock that early too' -- a genuinely weak window used to show bare 'SCANNING'
    //   the whole time and only reveal SIT_OUT at window close. This reads the V11.2
    //   timing controller's own live state (already stamped on taraCall every tick --
    //   tc._v112Timing/_v112Why, no new tracking needed) to show 'LIKELY SIT OUT' as
    //   soon as it's genuinely true, instead of leaving the user guessing. Purely a
    //   display change -- does not touch taraCall.call or any trading decision.
    const _isLikelySitOut=!snap&&!isWatching&&tc?._v112Timing==='wait'&&Number(tc?.confidence)<65&&typeof tc?._v112Why==='string'&&/coin-flip|coin flip/i.test(tc._v112Why);
    const callLabel=isOverrideSitOut
      ?'SITTING OUT'
      :isOverrideNoTrade
      ?'NO TRADE'
      :isLockedSnap
      ?(snap.call==='UP'?'LOCKED UP':'LOCKED DOWN')
      :isNoGoSnap?'NO TRADE'
      :isSatOutSnap?'SITTING OUT'
      :isWatching?(effDir==='UP'?'LEANING UP':'LEANING DOWN')
      :_isLikelySitOut?'LIKELY SIT OUT'
      :'SCANNING';
    // V10.7.47: sublabel for override sit-out shows the "would have leaned" direction so user
    //   has a hint to override on if they disagree with Tara's sit-out call.
    const callSubLabel=isOverrideSitOut&&effDir
      ?`would lean ${effDir} · ${dispConfidence}%`
      :isOverrideNoTrade&&effDir
      ?`would lean ${effDir} · ${dispConfidence}%`
      :isEdgeWatch
      ?`⚠ priced ahead · trade carefully when odds swing back`
      :null;
    const arrow=isNoGoSnap?'⊘':effDir==='UP'?'▲':effDir==='DOWN'?'▼':isSatOutSnap?'·':'·';
    // V5.6.5: Phase mirrors the call states.
    // V6.0.5: WATCHING gets its own phase label so user can see formation state.
    // V7.9: NO_TRADE label.
    // V10.7.47: phase reflects override state honestly.
    // V13.4.236: phase used `snap ? (...) : ...` and fell through to 'LOCKED' for
    //   ANY snapshot. Having a snapshot is not the same as being locked, so a
    //   snapshot that was not locked / no-go / sat-out / watching rendered
    //   "TARA'S CALL · SCANNING" directly above "PHASE · LOCKED". Reported as
    //   "I can't tell if she already sat it out or is still scanning" — and it was
    //   unreadable because the two lines genuinely disagreed.
    //   Phase now mirrors callLabel's own cascade, keyed on isLockedSnap.
    const phaseLabel=isOverrideSitOut?'SITTING OUT'
      :isOverrideNoTrade?'NO TRADE'
      :isLockedSnap?'LOCKED'
      :isNoGoSnap?'NO TRADE'
      :isSatOutSnap?'SITTING OUT'
      :isWatching?'LEANING'
      :_isLikelySitOut?'LIKELY SIT OUT'
      :'SCANNING';
    const samplesLeft=Math.max(0,(tc.needSamples||180)-(tc.samples||0));
    // Window timing
    const _totalSec=windowType==='15m'?900:300;
    const _elapsed=timeState?Math.max(0,_totalSec-((timeState.minsRemaining*60)+timeState.secsRemaining)):0;
    const _remSec=Math.max(0,_totalSec-_elapsed);
    const _remMin=Math.floor(_remSec/60),_remRem=_remSec%60;
    const _remLabel=_remMin>0?`${_remMin}m ${String(_remRem).padStart(2,'0')}s left`:`${_remRem}s left`;
    const _elapsedFrac=Math.min(1,_elapsed/_totalSec);
    // V6.0.5: Decision deadline. Tara's endgame freeze fires when remaining < 90s (15m)
    //   or < 45s (5m). After this point a snapshot pins automatically. The deadline is
    //   the LATEST time she could still commit by choice — counts down so user always
    //   sees a real number, even when the gate is blocking and there's no honest ETA.
    // V6.1.0: Hard cadence cap (90s for 15m, 45s for 5m) is now the PRIMARY deadline.
    //   Past this cap, Tara auto-sits-out. So this is the real "decide by" clock the
    //   user has been asking for: predictable, hard, never-changing.
    const _hardCapSec=_totalSec-callLockDeadline(windowType);
    const _secsUntilCap=Math.max(0,_hardCapSec-_elapsed);
    const _deadlineLabel=_secsUntilCap>=60
      ?`${Math.floor(_secsUntilCap/60)}m ${String(_secsUntilCap%60).padStart(2,'0')}s`
      :`${_secsUntilCap}s`;
    const _atDeadline=_secsUntilCap<=10;
    // Decision-step countdown
    const _elapsedMin=Math.floor(_elapsed/60);
    const _totalMin=_totalSec/60;
    // Minute markers for the bar
    const _minMarkers=[];
    for(let m=1;m<_totalMin;m++){
      _minMarkers.push({pos:(m/_totalMin)*100,m,isPast:_elapsed>=m*60});
    }
    // V5.6.5: Countdown text — neutral, no direction reveal during scanning.
    let countdownText,phaseHint='',phaseProgressPct;
    if(snap){
      countdownText=_remLabel;
      phaseHint=snap.earlyLock?'committed early':snap.call==='SIT_OUT'?'declined this window':'committed';
      phaseProgressPct=_elapsedFrac*100;
    } else {
      // Pure scanning state. Use sample progress (if accumulating) or general elapsed.
      const _need=tc.needSamples||100;
      const _samples=tc.samples||0;
      const _samplesInt=Math.round(_samples);
      const _needInt=Math.round(_need);
      // V6.0.5: ALWAYS show a real time number in the countdown. Three cases:
      //   1. Samples accumulating + ETA available → show real lock ETA
      //   2. Samples accumulating but stalled → deadline countdown (gate flips often)
      //   3. No samples yet → deadline countdown (forced-decision clock)
      // V6.1.0: Now uses the hard cadence cap as the deadline (not endgame freeze).
      //   Past the cap → auto sit-out, so the cap is the real "decide by" number.
      const _etaFitsCountdown=_samples>=2&&_need>0&&!tc.lockEtaStalled&&tc.lockEtaSec!=null&&tc.lockEtaSec<=_secsUntilCap+5;
      const _etaExceedsCountdown=_samples>=2&&_need>0&&!tc.lockEtaStalled&&tc.lockEtaSec!=null&&tc.lockEtaSec>_secsUntilCap+5;
      if(_etaFitsCountdown){
        if(tc.lockEtaSec===0){
          countdownText='committing this tick';
        } else {
          countdownText=`decision in ~${formatDuration(tc.lockEtaSec)}`;
        }
        phaseHint=`${_samplesInt}/${_needInt} samples · cap ${_deadlineLabel}`;
        phaseProgressPct=Math.min(95,(_samples/_need)*100);
      } else if(_etaExceedsCountdown){
        // Samples accumulating but too slowly to reach lock before cap → will sit out
        countdownText=`${_deadlineLabel} to commit`;
        phaseHint=`${_samplesInt}/${_needInt} samples · too slow, will sit out at cap`;
        phaseProgressPct=Math.min(95,(_samples/_need)*100);
      } else if(_samples>=2&&_need>0&&tc.lockEtaStalled){
        // Samples started but stopped accumulating — gate is flipping
        countdownText=_atDeadline?`${_deadlineLabel} to cap`:`${_deadlineLabel} to commit`;
        phaseHint=`${_samplesInt}/${_needInt} samples · stalled, conviction not firm`;
        phaseProgressPct=Math.min(95,(_samples/_need)*100);
      } else if(_liveLeanDir){
        // V6.0.4/5: gate sees a direction but hasn't started forming — show cap + lean
        countdownText=`${_deadlineLabel} to commit`;
        phaseHint=tc?.reason?tc.reason.slice(0,55):`${_liveLeanConv.toFixed(0)}pt ${_liveLeanDir} lean — awaiting confirmation`;
        phaseProgressPct=Math.min(40,_elapsed*0.1);
      } else if(_elapsed<10){
        countdownText=`observing — ${10-_elapsed}s of search remaining`;
        phaseHint=`then ${_deadlineLabel} of scan window`;
        phaseProgressPct=Math.min(15,_elapsed*1.4);
      } else {
        countdownText=`${_deadlineLabel} to decide`;
        phaseHint=tc?.reason?tc.reason.slice(0,60):'no clear direction yet';
        phaseProgressPct=Math.min(50,_elapsed*0.05);
      }
    }
    return(
      <div className={"mb-3 px-3 py-3 rounded-lg shrink-0 "+(className||"")} style={{background:bgClr,border:'1px solid '+borderClr,boxShadow:isLockedSnap?`0 6px 20px rgba(10,10,10,0.35), inset 0 0 24px ${snap.call==='UP'?'rgba(35,185,129,0.07)':'rgba(232,69,94,0.07)'}`:'0 4px 16px rgba(10,10,10,0.28)'}}>
        {/* V13.4.269: the call headline is now stage 1 of THIS TRADE, which renders
            directly above this card and reads the same committed snapshot. Keeping
            both meant the same direction, confidence and reason twice on one screen,
            which is the duplication he asked to be merged away. What stays below is
            everything THIS TRADE does NOT cover: conviction, decision clock, entry
            pricing, edge, phase and memory. Restore with taraCallHeadline=on. */}
        {(()=>{try{return localStorage.getItem('taraCallHeadline')==='on';}catch(_e){return false;}})()&&(<>
        <div className="flex items-baseline justify-between mb-2">
          <span className="text-[10px] uppercase tracking-[0.22em] font-bold" style={{color:T2_GOLD}}>Tara's Call</span>
          <div className="flex items-baseline gap-2">
            {/* V6.2.0: structural-led — primary tier. Grand trend + channel align. */}
            {(tc?._ctx?.isStructuralLed||snap?.isStructuralLed)&&<span className="text-[8px] tracking-[0.18em] uppercase font-bold" style={{color:'#C4B5FD'}}>◈ structural-led</span>}
            {/* V10.3.1: coin-flip detector — high conviction but weak core signals.
                Locks still fire, but user sees the awareness badge. Audit showed 75-84%
                conviction with weak htfPatterns/flow = 50% WR (true coin flip). */}
            {analysis?._v10_3_1_coinFlip&&!snap?.isStructuralLed&&<span className="text-[8px] tracking-[0.18em] uppercase font-bold" style={{color:'#23B981'}}>⚠ coin-flip · weak core</span>}
            {/* V5.7.7: Confluence indicator — visible while forming and after lock. */}
            {!(tc?._ctx?.isStructuralLed||snap?.isStructuralLed)&&(tc?._ctx?.isSuperConfluent||snap?.isSuperConfluent)&&<span className="text-[8px] tracking-[0.18em] uppercase font-bold" style={{color:T2_GOLD}}>★ super-confluence</span>}
            {!(tc?._ctx?.isStructuralLed||snap?.isStructuralLed)&&!(tc?._ctx?.isSuperConfluent||snap?.isSuperConfluent)&&(tc?._ctx?.isConfluent||snap?.isConfluent)&&<span className="text-[8px] tracking-[0.18em] uppercase font-bold" style={{color:T2_GOLD}}>★ confluence</span>}
            {/* V6.0.7: tape-led indicator — fast-lock on overwhelming order flow */}
            {!(tc?._ctx?.isStructuralLed||snap?.isStructuralLed)&&!(tc?._ctx?.isSuperConfluent||snap?.isSuperConfluent)&&!(tc?._ctx?.isConfluent||snap?.isConfluent)&&(tc?._ctx?.isTapeLed||snap?.isTapeLed)&&<span className="text-[8px] tracking-[0.18em] uppercase font-bold" style={{color:'#7DD3FC'}}>⚡ tape-led</span>}
            {/* V6.0.1: rising-confluence indicator — early entry tier */}
            {!(tc?._ctx?.isStructuralLed||snap?.isStructuralLed)&&!(tc?._ctx?.isSuperConfluent||snap?.isSuperConfluent)&&!(tc?._ctx?.isConfluent||snap?.isConfluent)&&!(tc?._ctx?.isTapeLed||snap?.isTapeLed)&&(tc?._ctx?.isRisingConfluence||snap?.isRisingConfluence)&&<span className="text-[8px] tracking-[0.18em] uppercase font-bold" style={{color:'#A6E3A1'}}>↗ rising · early</span>}
            {snap&&snap.earlyLock&&<span className="text-[8px] tracking-[0.18em] uppercase text-emerald-400/70 font-bold">⚡ early lock</span>}
            {/* V9.5.0: KALSHI-LEAD chip — surfaces when smart-money positioning detected.
                Color matches alignment with current call: aligned = teal-gold (confirms
                direction), opposite = amber (warning — Kalshi positioning AGAINST the call). */}
            {analysis?.kalshiLead&&(()=>{
              const _kl=analysis.kalshiLead;
              const _activeCallDir=isLockedSnap?(snap.call==='UP'?'UP':snap.call==='DOWN'?'DOWN':null):(isWatching?_liveLeanDir:null);
              const _aligned=_activeCallDir&&_kl.dir===_activeCallDir;
              const _opposing=_activeCallDir&&_kl.dir!==_activeCallDir;
              const _color=_aligned?'#23B981':_opposing?'#23B981':'#7DD3FC';
              const _label=_kl.dir==='UP'?'▲ kalshi-lead':'▼ kalshi-lead';
              const _hint=`Kalshi YES ${_kl.kDelta>0?'+':''}${_kl.kDelta}¢ · spot ${_kl.spotDeltaBps>0?'+':''}${_kl.spotDeltaBps}bps over 60s — ${_aligned?'confirms call':_opposing?'positioning AGAINST call':'standalone signal'}`;
              return <span className="text-[8px] tracking-[0.18em] uppercase font-bold" style={{color:_color}} title={_hint}>{_label} +{_kl.score}</span>;
            })()}
          </div>
        </div>
        <div className="flex items-center justify-between gap-3 mb-1.5">
          <div className={`flex items-baseline gap-2 flex-wrap ${callColor}`}>
            <span className="text-2xl">{arrow}</span>
            <span className="text-3xl font-serif font-bold tracking-tight leading-none">{callLabel}</span>
            {/* V10.7.47: lean sublabel for sit-out overrides — shows direction Tara would have leaned */}
            {callSubLabel&&(
              <span className="text-[11px] font-mono uppercase tracking-wider text-[#EDEDED]/55 leading-none self-baseline">{callSubLabel}</span>
            )}
            {/* V9.19.2 Phase 3: Tara WR pill — real numbers from taraCallLog.
                 Computes WR on last 30 resolved windows (WIN+LOSS only,
                 SITOUT excluded). This is Tara's directional accuracy
                 regardless of whether you traded. */}
            {isLockedSnap&&(()=>{
              const _log=Array.isArray(taraCallLog)?taraCallLog:[];
              const _resolved=_log.filter(e=>e&&(e.result==='WIN'||e.result==='LOSS'));
              const _last30=_resolved.slice(-30);
              const _n=_last30.length;
              if(_n===0)return null;
              const _wins=_last30.filter(e=>e.result==='WIN').length;
              const _wr=Math.round((_wins/_n)*100);
              const _color=_wr>=70?'rgb(35,185,129)':_wr>=60?'#23B981':'#E8455E';
              const _bg=_wr>=70?'rgba(35,185,129,0.08)':_wr>=60?'rgba(35,185,129,0.08)':'rgba(232,69,94,0.06)';
              const _border=_wr>=70?'rgba(35,185,129,0.30)':_wr>=60?'rgba(35,185,129,0.30)':'rgba(232,69,94,0.22)';
              return <span className="text-[9px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded-lg self-baseline tabular-nums" style={{color:_color,background:_bg,border:`1px solid ${_border}`}} title={`Tara's directional accuracy on last ${_n} resolved windows. Target: ≥70%. Excludes sit-outs.`}>WR · {_wr}% <span className="opacity-60 normal-case">(last {_n})</span></span>;
            })()}
            {/* V10.7.50: Direction Bias pill — surfaces UP/DOWN call distribution and per-dir WR
                 over last 30 resolved windows. Highlights amber when one direction >60% AND that
                 direction's WR <45% — visible warning that recent calls are skewing wrong way. */}
            {(()=>{
              const _log=Array.isArray(taraCallLog)?taraCallLog:[];
              const _resolved=_log.filter(e=>e&&(e.result==='WIN'||e.result==='LOSS'));
              const _last30=_resolved.slice(-30);
              if(_last30.length<10)return null;
              const _up=_last30.filter(e=>e.dir==='UP');
              const _dn=_last30.filter(e=>e.dir==='DOWN');
              const _upW=_up.filter(e=>e.result==='WIN').length;
              const _dnW=_dn.filter(e=>e.result==='WIN').length;
              const _upWR=_up.length?_upW/_up.length:0;
              const _dnWR=_dn.length?_dnW/_dn.length:0;
              const _upPct=_last30.length?_up.length/_last30.length:0;
              const _dnPct=_last30.length?_dn.length/_last30.length:0;
              // Warn state: one direction >60% of calls AND its WR <45%
              const _upWarn=_upPct>0.6&&_upWR<0.45;
              const _dnWarn=_dnPct>0.6&&_dnWR<0.45;
              const _warn=_upWarn||_dnWarn;
              // Show dominant direction's stats
              const _dominantDir=_upPct>_dnPct?'UP':'DOWN';
              const _domWR=_dominantDir==='UP'?_upWR:_dnWR;
              const _domPct=_dominantDir==='UP'?_upPct:_dnPct;
              const _arrow=_dominantDir==='UP'?'▲':'▼';
              // Only show when skewed (>55%) so we don't add noise when balanced
              if(_domPct<0.55)return null;
              const _color=_warn?'#E8455E':_domPct>0.65?'#23B981':'rgba(237,237,237,0.55)';
              const _bg=_warn?'rgba(232,69,94,0.12)':_domPct>0.65?'rgba(35,185,129,0.08)':'rgba(237,237,237,0.04)';
              const _border=_warn?'rgba(232,69,94,0.40)':_domPct>0.65?'rgba(35,185,129,0.30)':'rgba(237,237,237,0.15)';
              return(
                <span className="text-[9px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded-lg self-baseline tabular-nums" style={{color:_color,background:_bg,border:`1px solid ${_border}`}} title={`Last ${_last30.length} resolved windows: UP=${_up.length} (${Math.round(_upWR*100)}% WR) · DOWN=${_dn.length} (${Math.round(_dnWR*100)}% WR).${_warn?' Warning: dominant direction losing more than winning.':''}`}>
                  {_arrow}{Math.round(_domPct*100)}% · {Math.round(_domWR*100)}%WR
                </span>
              );
            })()}
            {isLockedSnap&&<span className="text-[11px] tabular-nums opacity-50 self-baseline" title="Raw posterior — not WR-calibrated. WR pill above is the calibrated signal.">{dispConfidence}% <span className="opacity-60">raw</span></span>}
            {/* V9.9.5: tier chip on the locked card. Reads from snap.tier (V9.9.5 added
                 it to taraCallSnapshotRef). Color-coded by tier quality so user can see
                 at a glance whether this is a Tier-1 lock (super-confluence, confluence,
                 structural-led, tape-led, rising-confluence, patient — green) or a Tier-3
                 lock (single, time-cap-commit, no-go-data, no-go-edge — amber/rose).
                 Helps user enforce "skip Tier 3" rule manually. */}
            {isLockedSnap&&snap.tier&&(()=>{
              const _t=snap.tier;
              const _isTier1=_t==='super-confluence'||_t==='confluence'||_t==='structural-led'||_t==='tape-led'||_t==='rising-confluence'||_t==='patient'||_t==='exceptional';
              const _isTier3=_t==='single'||_t==='time-cap-commit'||_t==='timer-commit'||_t==='no-go-data'||_t==='no-go-edge';
              const _color=_isTier1?'#6FA98C':_isTier3?'rgba(232,69,94,0.72)':'#23B981';
              return <span
                className="text-[9px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded-lg self-baseline tabular-nums"
                style={{color:_color,background:`${_color}1c`,border:`1px solid ${_color}55`}}
                title={_isTier1?'Tier-1 setup — strong setup':_isTier3?'Tier-3 setup — marginal, consider skipping':'Tier-2 setup'}
              >{_t}</span>;
            })()}
            {/* V6.0.5: show conviction next to WATCHING headline so user sees lean strength inline */}
            {isWatching&&<span className="text-sm tabular-nums opacity-75 self-baseline">{Math.round(_liveLeanConv)}pt</span>}
          </div>
        </div>
        <div className="text-[11px] text-[#EDEDED]/65 leading-snug mb-2">{dispReason||'Awaiting signal data...'}</div>
        </>)}
        {/* V8.9.1: Follow-Tara button — one-tap copy of Tara's locked direction
             into the user's position. When tapped, sets userPosition = snap.call so
             the advisor's full in-trade management logic kicks in (hold suggestions,
             exit triggers, target adjustments). When already following, shows a
             status badge that doubles as an exit toggle. Hidden when user has
             explicitly entered the OPPOSITE direction (they've made a different
             choice).
             V10.7.57: also hide on no-trade override and sit-out override locks —
             those aren't real trade opportunities, button was misleading. */}
        {isLockedSnap&&!isOverrideNoTrade&&!isOverrideSitOut&&typeof handleManualSync==='function'&&(
          userPosition===null
            ?React.createElement('button',{
                onClick:()=>handleManualSync(snap.call),
                className:'w-full mb-2 py-2 px-3 rounded-lg text-[11px] uppercase tracking-[0.18em] font-bold transition-all',
                style:{
                  background:snap.call==='UP'?'rgba(35,185,129,0.10)':'rgba(232,69,94,0.10)',
                  border:`1px solid ${snap.call==='UP'?'rgba(35,185,129,0.40)':'rgba(232,69,94,0.40)'}`,
                  color:snap.call==='UP'?'#23B981':'#E8455E',
                  boxShadow:`0 0 12px ${snap.call==='UP'?'rgba(35,185,129,0.20)':'rgba(232,69,94,0.20)'}`,
                },
              },`Got in on Tara's ${snap.call} call`)
            :userPosition===snap.call
              ?React.createElement('button',{
                  onClick:()=>handleManualSync(snap.call), // toggles off
                  className:'w-full mb-2 py-1.5 px-3 rounded-lg text-[10px] uppercase tracking-[0.18em] font-bold transition-all flex items-center justify-between',
                  style:{
                    background:'rgba(35,185,129,0.06)',
                    border:'1px solid rgba(35,185,129,0.25)',
                    color:'#23B981',
                  },
                  title:'Tap to mark as exited',
                },
                  React.createElement('span',null,'✓ Following Tara'),
                  React.createElement('span',{style:{color:'rgba(237,237,237,0.40)',fontSize:'9px'}},'tap to exit'),
                )
              :null  /* userPosition is set but doesn't match Tara — user chose differently, hide */
        )}
        {/* V10.7.48: Conviction Meter — visualizes how strong Tara's directional signal is.
             Conviction = |posterior - 50|. Deadzone (0-5) = no real edge → sit out at end.
             Built off live tc.posterior so it updates every tick during scanning.
             V13.4.294: skipped when desktopSplit -- ConvictionMeterCard renders
             this in column 1 instead. Mobile (no desktopSplit prop) is unaffected. */}
        {!desktopSplit&&(()=>{
          // V10.7.67b: Always use live tc posterior for conviction display.
          //   Prior: snap?.atPosterior was read first — snap in WATCHING state often
          //   had atPosterior=null → fell through to tc?.posterior which could also
          //   be stale → conviction showed 0.0pt despite engine saying 69%.
          //   The snap's frozen posterior is used for the locked call display (below),
          //   not for the live conviction meter which should always reflect current engine.
          // V13.4.284: read `tc.posterior` — a field the engine NEVER writes. The live
          //   object exposes the posterior as `rawProbAbove` (see the engine return at
          //   ~L43758). So this was undefined, fell through to snap?.atPosterior, and
          //   with no committed snapshot fell all the way to the 50 literal — giving
          //   conviction |50-50| = 0.0pt and painting "DEADZONE — coin flip" on screen
          //   directly beside "CONFIDENCE 75%".
          //   The V10.7.67b note above describes fixing exactly this symptom by
          //   preferring tc.posterior; that fix could never have worked.
          //   V13.4.285 CORRECTION: v284 swapped tc.posterior for tc.rawProbAbove,
          //   which taraCall does not carry either — it is built by its own IIFE
          //   (~L44185) that CONSUMES analysis.rawProbAbove but returns only
          //   {call, reason, confidence, direction, conviction, phase, ...}. So the
          //   DEADZONE reading survived the fix. `analysis` is the object that
          //   actually exposes rawProbAbove (engine return ~L43758).
          const _livePost=analysis?.rawProbAbove??snap?.atPosterior??50;
          const _post=Number(_livePost);
          if(!isFinite(_post))return null;
          // V13.4.285: the arrow follows the CALL, not the raw posterior sign, because
          //   the engine can flip away from the raw read (BRTI snap-back / universal-flip
          //   paths at ~L45548 return a call opposite the posterior).
          const _dir=(()=>{
            const _c=(snap&&(snap.call==='UP'||snap.call==='DOWN'))?snap.call
              :(tc&&(tc.call==='UP'||tc.call==='DOWN'))?tc.call
              :(tc&&(tc.direction==='UP'||tc.direction==='DOWN'))?tc.direction:null;
            return _c||(_post>=50?'UP':'DOWN');
          })();
          // V13.4.290 CORRECTION (R2): v285 computed magnitude and direction from TWO
          //   INDEPENDENT sources -- |livePost-50| paired with whatever _dir resolved to
          //   above -- so they could silently disagree in sign. A committed UP call whose
          //   live posterior later drifted to 34% rendered "16.0pt UP": sixteen points of
          //   DOWN conviction, relabelled onto the UP arrow it happened to sit beside.
          //   Fixed by measuring conviction ON THE SIDE THE ARROW SHOWS, so the two
          //   numbers can never come from different sides again. >=0 means the live read
          //   still supports the call; <0 means it has moved to actively oppose it -- that
          //   gets its own honest state instead of a sign flip painted as strength.
          const _dirPost=_dir==='UP'?_post:(100-_post);
          const _signedConv=_dirPost-50;
          const _opposing=_signedConv<0;
          const _conv=Math.abs(_signedConv);
          const _zone=_opposing?'opposing':_conv<5?'deadzone':_conv<10?'weak':_conv<15?'moderate':'strong';
          const _zoneLabel=_opposing?'OPPOSING — live has moved against this call':_conv<5?'DEADZONE — coin flip':_conv<10?'WEAK conviction':_conv<15?'MODERATE conviction':'STRONG conviction';
          const _zoneColor=_opposing?'rgb(232,69,94)':_conv<5?'rgb(232,69,94)':_conv<10?'#23B981':_conv<15?'rgb(35,185,129)':'rgb(52,211,153)';
          const _zoneBg=_opposing?'rgba(232,69,94,0.08)':_conv<5?'rgba(232,69,94,0.06)':_conv<10?'rgba(35,185,129,0.06)':_conv<15?'rgba(35,185,129,0.06)':'rgba(52,211,153,0.08)';
          const _zoneBorder=_opposing?'rgba(232,69,94,0.40)':_conv<5?'rgba(232,69,94,0.30)':_conv<10?'rgba(35,185,129,0.30)':_conv<15?'rgba(35,185,129,0.30)':'rgba(52,211,153,0.40)';
          const _dirColor=_opposing?'rgb(232,69,94)':(_dir==='UP'?'rgb(35,185,129)':'rgb(232,69,94)');
          // Position on 0-30+ scale (clamp at 30 for visual)
          const _maxScale=30;
          const _convClamped=Math.min(_conv,_maxScale);
          const _fillPct=(_convClamped/_maxScale)*100;
          return(
            <div className="mb-2 px-2.5 py-1.5 rounded-lg" style={{background:_zoneBg,border:'1px solid '+_zoneBorder}}>
              <div className="flex items-center justify-between mb-1 gap-2">
                <div className="flex items-baseline gap-1.5">
                  <span className="text-[9px] uppercase tracking-[0.18em] font-bold" style={{color:_zoneColor}}>Conviction</span>
                  <span className="text-[10px] font-bold tabular-nums" style={{color:_dirColor}}>{_conv.toFixed(1)}pt {_opposing?'⚠':(_dir==='UP'?'▲':'▼')}</span>
                </div>
                <span className="text-[9px] uppercase tracking-wider font-bold" style={{color:_zoneColor}}>{_zoneLabel}</span>
              </div>
              {/* Conviction bar with zone dividers */}
              <div className="relative h-1.5 rounded-full overflow-hidden" style={{background:'rgba(237,237,237,0.06)'}}>
                {/* Deadzone marker (0-5) at start */}
                <div className="absolute inset-y-0 left-0" style={{width:'16.6%',background:'rgba(232,69,94,0.10)'}}/>
                {/* Fill bar */}
                <div className="absolute inset-y-0 left-0 transition-all" style={{width:`${_fillPct}%`,background:_zoneColor}}/>
                {/* Zone divider lines at 5/10/15 (= 16.6%/33.3%/50%) */}
                <div className="absolute inset-y-0" style={{left:'16.6%',width:'1px',background:'rgba(237,237,237,0.20)'}}/>
                <div className="absolute inset-y-0" style={{left:'33.3%',width:'1px',background:'rgba(237,237,237,0.15)'}}/>
                <div className="absolute inset-y-0" style={{left:'50%',width:'1px',background:'rgba(237,237,237,0.15)'}}/>
              </div>
              <div className="flex justify-between mt-0.5 text-[8px] tabular-nums text-[#EDEDED]/30">
                <span>0</span>
                <span style={{marginLeft:'3%'}}>5</span>
                <span style={{marginLeft:'5%'}}>10</span>
                <span style={{marginLeft:'5%'}}>15</span>
                <span>30+</span>
              </div>
            </div>
          );
        })()}
        {/* V7.8: CAUTION BADGE for low-confidence commits. When Tara had to commit (1-2min
             cap, no high-conviction signal) and confidence is below the firm threshold,
             surface a clear caution chip so user knows the call is provisional. */}
        {isLockedSnap&&snap?.caution&&(()=>{
          // V8.9.2: stronger styling when this is an override caution (would have
          //   been NO_TRADE or sit-out before V8.9.2). Normal V7.8 cautions stay
          //   gold; override cautions go rose so the user sees the elevated risk.
          // V10.9.8: no-go-edge / no-go-coinflip-late are real directional locks
          //   with a pricing caution, not no-trade overrides. Label them "Edge Watch"
          //   and use the amber caution color (not the rose no-trade color) so the
          //   card reads as a normal lock with a "trade carefully" note. Only
          //   no-go-data (data failure) keeps the rose No-Trade Override styling.
          const _isEdgeWatch=snap.noGoCategory==='no-go-edge'||snap.tier==='no-go-edge'||snap.noGoCategory==='no-go-coinflip-late'||snap.tier==='no-go-coinflip-late';
          const _isHardOverride=(snap.wasOverriddenNoTrade||snap.wasOverriddenSitOut)&&!_isEdgeWatch;
          const _color=_isHardOverride?'#E8455E':T2_GOLD;
          const _bg=_isHardOverride?'rgba(232,69,94,0.10)':'rgba(35,185,129,0.10)';
          const _border=_isHardOverride?'rgba(232,69,94,0.35)':'rgba(35,185,129,0.28)';
          const _label=_isEdgeWatch?'Edge Watch'
            :snap.wasOverriddenNoTrade?'No-Trade Override'
            :snap._originalTier?`V11 Gate (was ${snap._originalTier})`
            :snap.wasOverriddenSitOut?'Sit-Out Override'
            :'Caution';
          return(
            <div className="mb-2 px-2.5 py-1.5 rounded-lg flex items-baseline gap-2" style={{background:_bg,border:'1px solid '+_border}}>
              <span className="text-[10px]" style={{color:_color}}>⚠</span>
              <span className="text-[10px] uppercase tracking-[0.14em] font-bold" style={{color:_color}}>{_label}</span>
              <span className="text-[11px] text-[#EDEDED]/85 leading-snug">{snap.caution}</span>
            </div>
          );
        })()}
        {/* V11: quality score + size guidance + WHY for allowed directional calls */}
        {isLockedSnap&&snap.v11Why&&(
          <div className="mb-1.5 px-2.5 py-1 rounded-lg flex items-center gap-2 flex-wrap"
               style={{background:'rgba(255,255,255,0.04)',border:'1px solid rgba(255,255,255,0.08)'}}>
            {snap.v11Size&&(
              <span className="text-[9px] uppercase tracking-widest font-bold px-1.5 py-0.5 rounded-lg"
                    style={{background:snap.v11Size==='FULL'?'rgba(52,211,153,0.18)':snap.v11Size==='STANDARD'?'rgba(212,162,76,0.15)':snap.v11Size==='HALF'?'rgba(251,146,60,0.15)':'rgba(239,68,68,0.15)',
                            color:snap.v11Size==='FULL'?'#6FA98C':snap.v11Size==='STANDARD'?'#fcd34d':snap.v11Size==='HALF'?'#fb923c':'#fca5a5'}}>
                {snap.v11Size} SIZE
              </span>
            )}
            {snap.v11Quality!=null&&(
              <span className="text-[9px] opacity-50 font-mono">Q{snap.v11Quality}</span>
            )}
            <span className="text-[10px] opacity-60 leading-tight flex-1 min-w-0 truncate">
              {snap.v11Why}
            </span>
            {snap.v11KalshiEdge!=null&&(
              <span className="text-[9px] font-mono"
                    style={{color:snap.v11KalshiEdge>=8?'#6FA98C':snap.v11KalshiEdge<=-5?'#fca5a5':'#94a3b8'}}>
                K{snap.v11KalshiEdge>=0?'+':''}{snap.v11KalshiEdge.toFixed(0)}
              </span>
            )}
          </div>
        )}
        {/* V6.2.8: When sitting out, show what direction Tara would have leaned if forced.
             Lets the user see her implicit read even when she's not committing. Helps them
             make their own manual call when they have an external read that disagrees with
             the sit-out reasoning (e.g. Kalshi pricing was the blocker, not the direction).
             V7.3: LIVE update. Previously this read snap.atPosterior (frozen at moment of
             sit-out decision). When market reversed after Tara sat out, the displayed lean
             stayed stale. Now uses analysis.rawProbAbove (live posterior) so the user sees
             Tara's CURRENT view of the market, with a "(was X% at sit-out)" sub-line if
             the lean has flipped direction since. */}
        {isSatOutSnap&&(()=>{
          const _liveProb=typeof analysis?.rawProbAbove==='number'?analysis.rawProbAbove:null;
          const _frozenProb=snap?.atPosterior;
          const _post=_liveProb!=null?_liveProb:_frozenProb;
          if(_post==null)return null;
          const _impliedDir=_post>=50?'UP':'DOWN';
          const _impliedConf=Math.round(_impliedDir==='UP'?_post:(100-_post));
          // Only meaningful if she has any directional lean (≥55%)
          if(_impliedConf<55)return null;
          const _arrow=_impliedDir==='UP'?'▲':'▼';
          const _color=_impliedDir==='UP'?'text-emerald-400/70':'text-rose-400/70';
          // Detect direction flip vs frozen snap to surface the change to the user
          const _frozenDir=_frozenProb!=null?(_frozenProb>=50?'UP':'DOWN'):null;
          const _flipped=_frozenDir&&_frozenDir!==_impliedDir;
          const _frozenConf=_frozenProb!=null?Math.round(_frozenDir==='UP'?_frozenProb:(100-_frozenProb)):null;
          return(
            <div className={`text-[11px] ${_color} font-medium mb-2 leading-snug`}>
              <div className="flex items-baseline gap-1.5">
                <span>{_arrow}</span>
                <span>{_flipped?'Now leans':'Would have leaned'} <span className="font-bold">{_impliedDir} {_impliedConf}%</span>{!_flipped?' if not blocked':''}</span>
              </div>
              {_flipped&&_frozenConf!=null&&(
                <div className="text-[10px] text-[#EDEDED]/35 italic mt-0.5">
                  was {_frozenDir} {_frozenConf}% at sit-out — market reversed
                </div>
              )}
            </div>
          );
        })()}
        {/* V6.0.5: Always-visible Decision Clock during scanning. Two columns:
             LEFT  = main countdown (real ETA when forming, deadline otherwise)
             RIGHT = secondary number (deadline OR samples context) */}
        {!snap&&(()=>{
          // V6.0.6: cap lock ETA to deadline. The rate-based ETA can mathematically exceed
          //   remaining window time when samples are accumulating very slowly (e.g. signal
          //   barely above gates, going from 17/130 to 18/130 over 30s → ETA ~60min on a
          //   15min window). When this happens, show the deadline + a "won't reach lock"
          //   warning instead of the nonsensical large number.
          // V6.2.7: Replace cap-based Decision Clock with Kalshi Entry Window status.
          //   Shows live Kalshi% for the active direction and how much room remains
          //   before the 65% threshold closes the entry window.
          const _kPct=kalshiYesPrice;
          const _activeDir=tc?.direction||tc?.call==='UP'||tc?.call==='DOWN'?(tc?.direction||tc?.call):null;
          const _kalshiKnown=_kPct!=null&&_activeDir;
          const _kalshiForDir=_kalshiKnown?(_activeDir==='UP'?_kPct:(100-_kPct)):null;
          const _ENTRY_THRESH=70;
          const _ptsToThresh=_kalshiForDir!=null?Math.round(_ENTRY_THRESH-_kalshiForDir):null;
          const _pastThresh=_kalshiForDir!=null&&_kalshiForDir>_ENTRY_THRESH;
          const _closeToThresh=_kalshiForDir!=null&&_kalshiForDir>=62&&_kalshiForDir<=_ENTRY_THRESH;
          const _kColor=_pastThresh?'text-rose-400':_closeToThresh?'text-amber-400':_kalshiForDir!=null?'text-emerald-400':'text-[#EDEDED]/55';
          const _kBorder=_pastThresh?'border-rose-500/40':_closeToThresh?'border-amber-500/30':'border-[#2A2A34]';
          // V6.4.1: live both-side Kalshi % even when Tara has no direction yet
          const _kalshiLive=_kPct!=null&&!isNaN(_kPct);
          const _kUpPct=_kalshiLive?Math.round(_kPct):null;
          const _kDnPct=_kalshiLive?Math.round(100-_kPct):null;
          return(
          <div className={`flex items-center justify-between gap-2 mb-3 px-2.5 py-1.5 rounded-lg bg-[#050508] border ${_kBorder}`}>
            <div className="flex items-baseline gap-1.5 min-w-0">
              <span className={`text-[14px] ${_kColor}`}>◷</span>
              <span className={`text-[10px] uppercase tracking-[0.18em] font-bold shrink-0 ${_kColor}`}>Entry Window</span>
            </div>
            <div className="flex items-baseline gap-2 tabular-nums">
              {!_kalshiLive?(
                <span className="text-[10px] text-[#EDEDED]/40">Kalshi loading…</span>
              ):!_activeDir?(
                <>
                  {/* Both-side display before Tara picks direction */}
                  <span className="text-[10px] text-[#EDEDED]/40 mr-1">Kalshi</span>
                  <span className="text-[11px] font-bold text-emerald-400/80">UP {_kUpPct}%</span>
                  <span className="text-[#EDEDED]/25">·</span>
                  <span className="text-[11px] font-bold text-rose-400/80">DN {_kDnPct}%</span>
                </>
              ):_pastThresh?(
                <>
                  <span className="text-[10px] text-[#EDEDED]/40 mr-1">{_activeDir} {Math.round(_kalshiForDir)}%</span>
                  <span className="text-[9px] text-rose-400/80">Kalshi overpriced — no edge ({Math.round(_kalshiForDir-70)}pt over fair value)</span>
                </>
              ):(
                <>
                  <span className={`text-[12px] font-bold ${_kColor}`}>Kalshi {_activeDir} {Math.round(_kalshiForDir)}%</span>
                  <span className="text-[9px] text-[#EDEDED]/45">{_ptsToThresh}pt to entry close</span>
                </>
              )}
            </div>
          </div>
          );
        })()}

        {/* V13.4.213: EXECUTION ROW -- this IS the mockup's "Entry Pricing"
            panel. V13.4.294: skipped when desktopSplit -- EntryPricingCard
            renders it in column 1 instead. Mobile is unaffected. */}
        {!desktopSplit&&(()=>{
          try{
            const _q=(typeof _kalshiQuote!=='undefined')?_kalshiQuote:null;
            // V13.4.214: _quoteUsable rejects the empty book that made this row
            //   render "take 100c · rest 100c · saves 0.0c".
            const _fresh=!!(_q&&_q.at&&(Date.now()-_q.at)<60000&&_quoteUsable(_q));
            const _dir=(tc?.call==='UP'||tc?.call==='DOWN')?tc.call:(tc?.direction==='UP'||tc?.direction==='DOWN'?tc.direction:null);
            if(!_fresh||!_dir)return null;
            // Our side: UP buys YES (pay the ask, rest at the bid). DOWN buys NO,
            //   whose ask is 100-yesBid and whose bid is 100-yesAsk.
            const _take=_dir==='UP'?_q.ask:(100-_q.bid);
            const _rest=_dir==='UP'?_q.bid:(100-_q.ask);
            if(!Number.isFinite(_take)||!Number.isFinite(_rest))return null;
            const _fee=0.07*(_take/100)*(1-_take/100)*100;   // Kalshi taker fee; maker is 0
            const _save=(_take-_rest)+_fee;
            return(
              <div className="flex items-center justify-between gap-2 mb-3 px-2.5 py-1.5 rounded-lg bg-[#050508] border border-[#2A2A34]">
                <span className="text-[10px] uppercase tracking-[0.18em] font-bold shrink-0" style={{color:'rgba(255,255,255,0.55)'}}>Execution</span>
                <div className="flex items-baseline gap-2 tabular-nums">
                  <span className="text-[11px]" style={{color:'rgba(255,255,255,0.55)'}}>take {_take.toFixed(0)}c</span>
                  <span style={{color:'rgba(255,255,255,0.25)'}}>·</span>
                  <span className="text-[12px] font-bold" style={{color:'rgba(255,255,255,0.92)'}}>rest {_rest.toFixed(0)}c</span>
                  <span className="text-[9px]" style={{color:'rgba(255,255,255,0.45)'}}>saves {_save.toFixed(1)}c</span>
                </div>
              </div>
            );
          }catch(_e){return null;}
        })()}

        {/* V9.17.1: General prediction box REMOVED. The scalper advisor panel
            (rendered in main flow after MarketContextStrip) is now the user's
            engine view — it shows "tara reads: UP 72% · tape accelerating" always,
            plus actionable suggestions when enabled. Cleaner than two separate
            engine displays competing for attention. */}

        {/* V5.1: Phase strip with minute-marker decision-progress timeline. */}
        <div className="mb-3">
          <div className="flex justify-between items-baseline mb-1 gap-2">
            <span className="text-[9px] uppercase tracking-[0.18em] text-[#EDEDED]/55 font-bold shrink-0">Phase · {phaseLabel}</span>
            <div className="flex items-baseline gap-1.5 min-w-0 justify-end">
              <span className={`text-[10px] tabular-nums font-bold tracking-wide truncate ${isCall&&!snap?'text-[#EDEDED]/85':snap?'text-[#EDEDED]/85':'text-[#EDEDED]/45'}`}>{countdownText}</span>
              {phaseHint&&<span className="text-[8px] tracking-wider text-[#EDEDED]/35 hidden sm:inline">· {phaseHint}</span>}
            </div>
          </div>
          {/* V11.2: dynamic timing recommendation — early / wait / now / late */}
          {(()=>{
            const _tm=snap?(snap._v112Timing||null):(taraCall&&taraCall._v112Timing||null);
            const _tw=snap?(snap._v112Why||null):(taraCall&&taraCall._v112Why||null);
            if(!_tm&&!_tw)return null;
            const _tmU=(_tm||'').toUpperCase();
            const _color=_tm==='wait'?'#23B981':_tm==='early'?'#6FA98C':_tm==='late'?'#fb923c':'#a7f3d0';
            const _icon=_tm==='wait'?'\u23F3':_tm==='early'?'\u26A1':_tm==='late'?'\u23F1':'\u2192';
            const _verb=_tm==='wait'?'WAIT':_tm==='early'?'LOCK EARLY':_tm==='late'?'LOCK LATE':'LOCK NOW';
            return React.createElement('div',{className:'flex items-center gap-1.5 mb-1 px-2 py-1 rounded-lg',
              style:{background:'rgba(255,255,255,0.035)',border:'1px solid rgba(255,255,255,0.07)'}},
              React.createElement('span',{className:'text-[10px] font-bold tracking-wide shrink-0',style:{color:_color}},`${_icon} ${_verb}`),
              _tw?React.createElement('span',{className:'text-[10px] opacity-60 leading-tight truncate'},_tw):null
            );
          })()}
          <div className="relative h-1.5 bg-[#050508] rounded-full overflow-hidden">
            {/* Minute tick markers */}
            {_minMarkers.map((mk,i)=>(
              <div key={i} className="absolute top-0 bottom-0 w-px" style={{
                left:mk.pos+'%',
                background:mk.isPast?'rgba(35,185,129,0.4)':'rgba(237,237,237,0.15)',
              }}></div>
            ))}
            {/* Progress fill — elapsed-time based */}
            <div className="absolute top-0 bottom-0 left-0 transition-all duration-700" style={{
              width:(_elapsedFrac*100).toFixed(1)+'%',
              background:isCall?(tc.call==='UP'?'rgba(52,211,153,0.6)':'rgba(232,69,94,0.6)'):tc.call==='SIT_OUT'?'rgba(212,162,76,0.5)':T2_GOLD,
              opacity:0.85,
            }}/>
            {/* Position marker */}
            <div className="absolute top-0 bottom-0 w-0.5 transition-all duration-700" style={{
              left:(_elapsedFrac*100).toFixed(1)+'%',
              background:isCall?(tc.call==='UP'?'rgba(52,211,153,0.9)':'rgba(232,69,94,0.9)'):snap?'rgba(212,162,76,0.7)':T2_GOLD,
              transform:'translateX(-50%)',
              boxShadow:'0 0 4px currentColor',
            }}/>
          </div>
          {_totalMin>=10&&(
            <div className="flex justify-between mt-0.5 px-px text-[7px] text-[#EDEDED]/30 tabular-nums">
              <span>0</span><span>5m</span><span>10m</span><span>15m</span>
            </div>
          )}
        </div>

        {/* V6.0.8: Edge indicator — Tara confidence vs Kalshi implied probability for direction.
             Shown only on locked snapshots. Edge is what determines profitability: high edge =
             Tara sees something market doesn't = profitable entry. Negative edge = market
             already priced in = late entry, low value even if Tara wins. */}
        {isLockedSnap&&snap?.kalshiAtLock!=null&&snap?.atPosterior!=null&&(()=>{
          // Tara's confidence in her direction (always shown as 0-100 for chosen side)
          const _taraDirConf=snap.call==='UP'?snap.atPosterior:(100-snap.atPosterior);
          // Kalshi's implied probability for the chosen direction
          const _kalshiDirConf=snap.call==='UP'?snap.kalshiAtLock:(100-snap.kalshiAtLock);
          const _edge=_taraDirConf-_kalshiDirConf;
          const _edgeColor=_edge>=15?'text-emerald-400':_edge>=5?'text-emerald-400/70':_edge>=-5?'text-[#EDEDED]/55':'text-amber-400/85';
          const _edgeLabel=_edge>=20?'BIG EDGE':_edge>=10?'GOOD EDGE':_edge>=0?'MODEST EDGE':_edge>=-10?'LATE ENTRY':'KALSHI AHEAD';
          return(
            <div className="mb-3 px-2.5 py-2 rounded-lg bg-[#0B0B0F] border border-[#24242E]">
              <div className="flex items-baseline justify-between gap-2 mb-1">
                <span className="text-[8px] uppercase tracking-[0.2em] text-[#EDEDED]/40 font-bold">Edge vs market</span>
                <span className={`text-[9px] uppercase tracking-wider font-bold ${_edgeColor}`}>{_edgeLabel}</span>
              </div>
              <div className="flex items-baseline justify-between gap-3 text-[11px] tabular-nums">
                <div className="flex items-baseline gap-3">
                  <span className="text-[#EDEDED]/65">Tara <span className="font-bold text-[#EDEDED]/85">{Math.round(_taraDirConf)}%</span></span>
                  <span className="text-[#EDEDED]/35">·</span>
                  <span className="text-[#EDEDED]/65">Kalshi <span className="font-bold text-[#EDEDED]/85">{Math.round(_kalshiDirConf)}%</span></span>
                </div>
                <span className={`font-bold ${_edgeColor}`}>{_edge>0?'+':''}{Math.round(_edge)}pt</span>
              </div>
            </div>
          );
        })()}

        {/* V4.3: Scorecard — this IS the mockup's "Record" panel.
            V13.4.294: skipped when desktopSplit -- RecordCard renders it in
            column 3 instead. Mobile is unaffected. */}
        {!desktopSplit&&(
        <div className="border-t border-[#24242E] pt-2.5">
          <div className="flex justify-between items-baseline mb-1.5">
            {/* V13.4.162: was "Tara's Record", which read as an all-time count. The
                numbers are a ROLLING WINDOW (the cloud log is capped at
                _TARA_CLOUD_LOG_CAP), so the label now says so. Today's record is a
                separate, genuinely-daily figure and is untouched. */}
            <span className="text-[9px] uppercase tracking-[0.18em] text-[#EDEDED]/45 font-bold">
              Tara's Record <span className="text-[#EDEDED]/30">· {_TARA_RECORD_WINDOW_LABEL}</span>
            </span>
            {wr!==null&&<span className="text-[10px] tabular-nums text-[#EDEDED]/60">{wr}% win rate</span>}
            {wr===null&&<span className="text-[10px] text-[#EDEDED]/35">no calls yet</span>}
          </div>
          <div className="flex items-end gap-3">
            <div className="flex flex-col items-center">
              <span className="text-2xl font-serif font-bold text-emerald-400 tabular-nums leading-none">{sc.wins||0}</span>
              <span className="text-[8px] uppercase tracking-wider text-emerald-400/60 mt-1">wins</span>
            </div>
            <div className="h-7 w-px bg-[#EDEDED]/10"></div>
            <div className="flex flex-col items-center">
              <span className="text-2xl font-serif font-bold text-rose-400 tabular-nums leading-none">{sc.losses||0}</span>
              <span className="text-[8px] uppercase tracking-wider text-rose-400/60 mt-1">losses</span>
            </div>
            <div className="h-7 w-px bg-[#EDEDED]/10"></div>
            <div className="flex flex-col items-center">
              {/* V13.4.226: this tile was a white number under a GREEN label —
                  T2_GOLD has been the neutral chrome white since v206, and the
                  label was still on the win colour. So the one number that means
                  "did not trade" was wearing the colour that means "won". */}
              <span className="text-2xl font-serif font-bold tabular-nums leading-none" style={{color:T2_SITOUT_FG}}>{sc.sitouts||0}</span>
              <span className="text-[8px] uppercase tracking-wider mt-1" style={{color:'rgba(212,160,58,0.65)'}}>sat out</span>
            </div>
          </div>
        </div>
        )}

        {/* V13.4.255: the sit-out value panel was removed. It priced every
            sat-out window to show whether sitting out had been worth it -- a
            useful answer while sit-outs existed. They were turned off in
            v13.4.249/250, so the panel could only ever report on history and
            read as though Tara were still declining windows. _sitoutValue is
            left in place for analysis. */}

        {/* V6.2.6: Force-call buttons. Only when Tara hasn't committed yet (SCANNING / WATCHING).
              Hidden after commit or sit-out — at that point the call is locked one-way and these
              would do nothing useful. Primary button (gold) lowers floors temporarily; secondary
              text-button bypasses all gates with a confirmation dialog. */}
        {/* V6.5.7: Speed dial with live ETA. Shows expected lock time at current dial position.
            ETA is computed inline from window timing + dial setting so it's always available
            even when engine hasn't run yet (early scan, search phase, etc). */}
        {/* V10.9.7: SPEED DIAL REMOVED. The dial was a global 0.1x-2.0x timing
              multiplier sitting on top of the tier ladder, V10.9.0 signal-agreement
              gate, V10.8.2 cushion history, and V9.8.0 urgency protocol — all of
              which already drive timing from actual signal/confidence. 50 ("balanced")
              was the mathematical neutral point (multiplier=1.0). Removing the dial
              = locking permanently at that neutral point, so the signal-based systems
              run at their tuned baseline. This panel keeps the informative Lock ETA
              readout (computed at the neutral multiplier) without the manual control. */}
        {!isCommittedSnap&&(()=>{
          const _smult=1.0; // neutral (was dial-controlled 0.1-2.0; dial removed V10.9.7)
          const _searchPhase=Math.max(2,Math.round(10*_smult));
          const _totalSec=windowType==='15m'?900:300;
          const _minsR=Number(timeState&&timeState.minsRemaining)||0;
          const _secsR=Number(timeState&&timeState.secsRemaining)||0;
          const _elapsed=Math.max(0,_totalSec-(_minsR*60+_secsR));
          const _searchRem=Math.max(0,_searchPhase-_elapsed);
          const _engSamples=taraCall&&taraCall._ctx&&typeof taraCall._ctx.samples==='number'?taraCall._ctx.samples:null;
          const _engNeed=taraCall&&taraCall._ctx&&typeof taraCall._ctx.needSamples==='number'?taraCall._ctx.needSamples:null;
          let _samplesRem;
          let _tierLabel;
          const _isStalled=taraCall&&taraCall._ctx&&taraCall._ctx.lockEtaStalled===true;
          const _isScanningNoDir=taraCall&&taraCall.call==='SIT_OUT'&&_engSamples==null;
          if(_isStalled||_isScanningNoDir){
            _samplesRem=null;
            _tierLabel=_isStalled?'stalled':'scanning';
          } else if(_engSamples!=null&&_engNeed!=null){
            _samplesRem=Math.max(0,_engNeed-_engSamples);
            _tierLabel=(taraCall&&taraCall._ctx&&taraCall._ctx.tierLabel)||'';
          } else {
            const _baseSamples=Math.max(1,Math.round(30*_smult));
            const _virtualElapsedInForming=Math.max(0,_elapsed-_searchPhase);
            const _vRem=_baseSamples-_virtualElapsedInForming;
            if(_vRem<=0){
              _samplesRem=null;
              _tierLabel='stalled';
            } else {
              _samplesRem=_vRem;
              _tierLabel=taraCall&&taraCall.call==='SIT_OUT'?'scanning':'forming';
            }
          }
          const _etaNum=_samplesRem==null?null:Math.max(0,_searchRem+_samplesRem);
          let _etaStr;
          let _blockerLine=null;
          if(_etaNum==null){
            const _engQ=taraCall&&taraCall._ctx&&typeof taraCall._ctx.q==='number'?taraCall._ctx.q:null;
            const _engConv=taraCall&&taraCall._ctx&&typeof taraCall._ctx.conviction==='number'?taraCall._ctx.conviction:null;
            const _qBaseUI=Math.round(30*_smult);
            const _convBaseUI=Math.max(1,Math.round(7*_smult));
            if(_isStalled){
              _etaStr='\u2014';
              if(_engConv!=null&&_engConv<_convBaseUI){
                _blockerLine=`conviction ${_engConv.toFixed(0)} / need ${_convBaseUI}`;
              } else if(_engQ!=null&&_engQ<_qBaseUI){
                _blockerLine=`quality ${_engQ.toFixed(0)} / need ${_qBaseUI}`;
              } else {
                _blockerLine='conviction not firm';
              }
            } else {
              _etaStr='\u2014';
              _blockerLine='no direction yet';
            }
          } else {
            _etaStr=_etaNum<=0?'now':_etaNum<60?`~${Math.round(_etaNum)}s`:`~${Math.floor(_etaNum/60)}m ${Math.round(_etaNum%60)}s`;
          }
          const _color='rgb(35,185,129)';
          // V13.4.321: DECISION CLOCK removed. Direct user report: "never ever
          //   locked on the time it says." Checked against real data before
          //   removing rather than re-tuning a third time -- queried the last
          //   7 days of real 15m locks from Supabase (193 of them): actual
          //   p25/p75 was 492s/748s remaining vs the hardcoded 516s/793s used
          //   here, well under a minute off. The quartiles were NOT stale.
          //   The real problem: this rendered a precise wall-clock time
          //   ("usually locks from 02:31:46 PM") for what is fundamentally a
          //   50%-confidence, 4+ minute-wide band blended across every
          //   quality tier at once -- a super-confluence setup can lock in
          //   5s, a patient one can take 100+ -- so on any single window,
          //   where the real lock falls relative to that band is close to a
          //   coin flip. Second time this exact feature over-promised
          //   precision it structurally can't deliver: the original
          //   V13.4.147 design was a fixed schedule a later audit found
          //   wrong 64%/19%/18% against its own promised window, and
          //   V13.4.159's fix (this quartile-band version) measured
          //   accurately but was still unsatisfying to actually watch. Lock
          //   ETA below is the one number that's genuinely live for the
          //   current window and is kept as-is, unchanged.
          return(
            <div className="border-t border-[#24242E] pt-2.5 mt-2.5">
              <div className="px-2.5 py-2 rounded-lg" style={{background:'#0E0E12',border:'1px solid #24242E'}}>
                <div className="flex items-baseline justify-between gap-2 mb-0.5">
                  <span className="text-[9px] uppercase tracking-[0.18em] text-[#EDEDED]/55 font-bold">Lock ETA</span>
                  {_tierLabel&&<span className="text-[9px] uppercase tracking-wide text-[#EDEDED]/35 tabular-nums">{_tierLabel}</span>}
                </div>
                <div className="text-[18px] font-bold tabular-nums tracking-tight leading-tight" style={{color:_color}}>{_etaStr}</div>
                {_blockerLine&&<div className="text-[9px] text-[#EDEDED]/45 italic mt-0.5 tabular-nums">{_blockerLine}</div>}
              </div>
            </div>
          );
        })()}
        {!isCommittedSnap&&(onSoftHint||onHardForce)&&(
          <div className="border-t border-[#24242E] pt-2.5 mt-2.5 flex flex-col gap-1.5">
            <div className="flex items-center justify-between gap-2">
              <span className="text-[9px] uppercase tracking-[0.18em] text-[#EDEDED]/45 font-bold">Force Call</span>
              {onHardForce&&(
                <button
                  onClick={()=>{
                    const _post=analysis?.rawProbAbove??50;
                    const _dir=_post>=50?'UP':'DOWN';
                    const _kPct=analysis?.kalshiAtLock??null;
                    let _edge=null;
                    if(_kPct!=null){
                      const _t=_dir==='UP'?_post:(100-_post);
                      const _k=_dir==='UP'?_kPct:(100-_kPct);
                      _edge=Math.round(_t-_k);
                    }
                    const _msg=`Override gates and force ${_dir} lock?\n\nThis bypasses ALL safety checks:\n• Edge guard\n• Tape opposition veto\n• Quality floor\n• Conviction floor\n• Kalshi extreme block\n${_edge!=null?`\nCurrent edge: ${_edge>=0?'+':''}${_edge}pt`:''}\n\nProceed?`;
                    if(window.confirm(_msg))onHardForce();
                  }}
                  className="text-[9px] uppercase tracking-wider font-bold text-amber-400/70 hover:text-amber-300 transition-colors"
                >Override gates →</button>
              )}
            </div>
            {onSoftHint&&(()=>{
              // V6.2.6: Re-render every second while hint is active so countdown ticks
              const _hintActiveUntil=typeof window!=='undefined'&&window.__taraSoftHintExpiry?window.__taraSoftHintExpiry:0;
              const _now=Date.now();
              const _hintRemaining=Math.max(0,Math.ceil((_hintActiveUntil-_now)/1000));
              const _isActive=_hintRemaining>0;
              return React.createElement('button',{
                onClick:()=>{
                  if(typeof window!=='undefined')window.__taraSoftHintExpiry=Date.now()+10000;
                  onSoftHint();
                },
                disabled:_isActive,
                className:'w-full py-2 px-3 rounded-lg text-xs font-bold uppercase tracking-wide transition-all text-center',
                style:_isActive?{background:'rgba(35,185,129,0.25)',color:T2_GOLD,border:'1px solid '+T2_GOLD,opacity:0.8}:{background:'rgba(35,185,129,0.12)',color:T2_GOLD,border:'1px solid '+T2_GOLD_BORDER},
              },_isActive?`⚡ Hint active · ${_hintRemaining}s left`:'⚡ Hint to Lock Faster');
            })()}
            <span className="text-[9px] text-[#EDEDED]/30 italic text-center">
              Hint lowers floors for 10s · safeguards stay on. Override bypasses everything.
            </span>
          </div>
        )}

        {/* V8.0: Decisional overlay — edge vs Kalshi, position size hint, conviction trajectory, cooldown */}
        <DecisionalOverlay
          taraCall={taraCall}
          kalshiYesPrice={kalshiYesPrice}
          convictionTrajectory={convictionTrajectory}
          todayData={todayData}
          analysis={analysis}
          movementRisk={movementRisk}
          bestWindowsToday={bestWindowsToday}
          desktopSplit={desktopSplit}
        />

        {/* V9.9.0: Predictive reversal — chip + Tara-native forecast SVG. Renders only
            when reversalRisk.flag is WATCH or EXPECTED (NONE keeps clean locks visually
            clean). Wrapped in min-w-0 + overflow-hidden so the SVG never causes horizontal
            overflow on narrow viewports. */}
        {reversalRisk&&reversalRisk.flag&&reversalRisk.flag!=='NONE'?React.createElement('div',{
          className:'flex flex-col gap-2 mt-2 min-w-0 overflow-hidden',
        },[
          React.createElement(ReversalRiskChip,{key:'chip',reversalRisk}),
          React.createElement(ReversalForecastSVG,{
            key:'svg',
            reversalRisk,
            dir:taraCall?.direction||taraCall?.call,
            currentPrice:analysis?.currentPrice,
            strikePrice:analysis?.targetMargin,
            atrBps:analysis?.atrBps,
            timeRemainingSec:timeState?.totalSecondsRemaining||((timeState?.minsRemaining||0)*60+(timeState?.secsRemaining||0)),
            totalWindowSec:windowType==='5m'?300:900,
          }),
        ]):null}

        {/* V5.6.1: Tara's Memory — last 6 calls with results. Synced to Firestore so it
            survives refresh + spans devices. Click "all" to see the full history.
            V13.4.294: skipped when desktopSplit -- rendered independently in
            column 3 instead. Mobile is unaffected. */}
        {!desktopSplit&&<TaraMemoryStrip taraCallLog={taraCallLog||[]} windowType={windowType} taraLearnings={taraLearnings} useLocalTime={useLocalTime} timeFormat={timeFormat} onEditEntry={onEditEntry} onDeleteEntry={onDeleteEntry}/>}
      </div>
    );

}

// V5.6.6: PastWindowsPill — Kalshi-style "past outcomes" tracker. Shows the most recent
//   resolved windows as colored arrows (▲ green = closed UP, ▼ red = closed DOWN). Collapsed
//   pill displays last 3 outcomes inline. Click to expand into a vertical list with full
//   timestamps. Filters by current windowType so 15m and 5m have separate histories.
const PastWindowsPill=React.memo(function PastWindowsPill({pastWindows,windowType,timeFormat}){
  const[open,setOpen]=React.useState(false);
  const filtered=React.useMemo(()=>{
    return [...(pastWindows||[])].filter(e=>e&&e.windowType===windowType&&(e.dir==='UP'||e.dir==='DOWN')).reverse();
  },[pastWindows,windowType]);
  const last3=filtered.slice(0,3);
  const wrapRef=React.useRef(null);
  // Close on outside click
  React.useEffect(()=>{
    if(!open)return;
    const onDoc=(e)=>{if(wrapRef.current&&!wrapRef.current.contains(e.target))setOpen(false);};
    document.addEventListener('mousedown',onDoc);
    document.addEventListener('touchstart',onDoc);
    return()=>{document.removeEventListener('mousedown',onDoc);document.removeEventListener('touchstart',onDoc);};
  },[open]);
  if(filtered.length===0)return null;
  const _fmt=(ms)=>{
    const d=new Date(ms);
    const _mo=_fmtDateTz(d,timeFormat,{month:'short',day:'numeric'});
    const _t=_fmtTimeTz(d,timeFormat,{hour:'numeric',minute:'2-digit'});
    return `${_mo}, ${_t}`;
  };
  return React.createElement('div',{ref:wrapRef,className:'relative'},
    React.createElement('button',{
      onClick:()=>setOpen(!open),
      className:'flex items-center gap-1.5 bg-[#050508] border border-[#2A2A34] px-2 py-1 rounded-full text-xs font-bold uppercase tracking-wide hover:border-[rgba(35,185,129,0.4)] transition-colors shadow-sm',
    },
      React.createElement('span',{className:`text-[10px] text-[#EDEDED]/55 transition-transform inline-block ${open?'rotate-180':''}`},'▾'),
      React.createElement('span',{className:'text-[#EDEDED]/65 text-[11px]'},'Past'),
      React.createElement('span',{className:'w-px h-3 bg-[#EDEDED]/20 mx-0.5'}),
      React.createElement('span',{className:'flex items-center gap-0.5'},
        last3.map((w)=>React.createElement('span',{
          key:w.id,
          className:`text-xs ${w.dir==='UP'?'text-emerald-400':'text-rose-400'}`,
        },w.dir==='UP'?'▲':'▼')),
      ),
    ),
    open&&React.createElement('div',{
      className:'absolute top-full mt-1.5 right-0 sm:left-0 sm:right-auto bg-[#0A0A0E] border border-[#1B1B22] rounded-lg shadow-2xl py-1.5 min-w-[180px] z-50 max-h-[320px] overflow-y-auto',
      style:{boxShadow:'0 8px 32px rgba(0,0,0,0.5), inset 0 0 0 1px rgba(35,185,129,0.06)'},
    },
      filtered.slice(0,15).map((w)=>React.createElement('div',{
        key:w.id,
        className:'flex items-center gap-2 px-3 py-1.5 hover:bg-[#EDEDED]/5 transition-colors',
        title:`Strike $${(w.strike||0).toLocaleString(undefined,{maximumFractionDigits:0})} → Close $${(w.closingPrice||0).toLocaleString(undefined,{maximumFractionDigits:0})} · ${w.gapBps!=null?formatSignedInt(w.gapBps):'?'} bps`,
      },
        React.createElement('span',{className:`text-base ${w.dir==='UP'?'text-emerald-400':'text-rose-400'}`},w.dir==='UP'?'▲':'▼'),
        React.createElement('span',{className:'text-[11px] tabular-nums text-[#EDEDED]/70 whitespace-nowrap'},_fmt(w.time)),
      )),
    ),
  );
})

// ── V8.5: BEST PRACTICES MODAL ──────────────────────────────────────────────
// Comprehensive trader's guide. Tells the user how Tara WANTS to be used: when to
// enter, when to skip, when to take profit, when to cut loss, what to watch
// during a trade, common pitfalls. Backed by the actual learning system + edge
// math, not generic platitudes.
function BestPracticesModal({open,onClose}){
  if(!open)return null;
  const _section=(title,children)=>React.createElement('section',{className:'mb-4 sm:mb-5'},
    React.createElement('h3',{className:'text-xs sm:text-sm uppercase tracking-[0.18em] font-bold mb-2',style:{color:T2_GOLD}},title),
    children,
  );
  const _row=(icon,iconColor,heading,body)=>React.createElement('div',{className:'flex gap-2 mb-2.5'},
    React.createElement('span',{className:'text-base shrink-0 leading-tight',style:{color:iconColor}},icon),
    React.createElement('div',{className:'min-w-0 flex-1'},
      React.createElement('div',{className:'text-[12px] sm:text-[13px] font-bold text-white mb-0.5'},heading),
      React.createElement('div',{className:'text-[11px] sm:text-[12px] text-[#EDEDED]/70 leading-relaxed'},body),
    ),
  );
  return React.createElement('div',{
    className:'fixed inset-0 z-50 flex items-start sm:items-center justify-center p-2 sm:p-4 bg-black/80 backdrop-blur-sm overflow-y-auto',
    onClick:onClose,
  },
    React.createElement('div',{
      className:'bg-[#0A0A0E] border border-[#1B1B22] rounded-[10px] shadow-2xl max-w-2xl w-full p-4 sm:p-6 my-2 sm:my-8 max-h-[96vh] overflow-y-auto',
      onClick:(e)=>e.stopPropagation(),
      style:{boxShadow:'0 20px 60px rgba(0,0,0,0.6), inset 0 0 0 1px rgba(35,185,129,0.06)'},
    },
      // Header
      React.createElement('div',{className:'flex justify-between items-baseline mb-4 sm:mb-5 pb-3 border-b border-[#24242E]'},
        React.createElement('div',null,
          React.createElement('h2',{className:'text-lg sm:text-xl font-serif text-white tracking-tight'},'Best Practices · Trading with Tara'),
          React.createElement('p',{className:'text-[10px] uppercase tracking-wider text-[#EDEDED]/45 mt-0.5'},'how to extract her edge'),
        ),
        React.createElement('button',{onClick:onClose,className:'text-[#EDEDED]/40 hover:text-white text-2xl leading-none px-1'},'×'),
      ),

      // Intro
      React.createElement('p',{className:'text-[12px] text-[#EDEDED]/75 leading-relaxed mb-4 italic'},
        'Tara has a real statistical edge in specific conditions. Most of profit-or-loss outcome is decided by ',
        React.createElement('strong',{className:'text-white not-italic'},'whether you enter the right trades'),
        ' — not by what happens after. This guide tells you which trades to take, which to skip, and how to manage the ones you take.',
      ),

      // 1. When to ENTER
      _section('When to enter',[
        _row('✓','rgb(35,185,129)','Big edge vs Kalshi (+15pp+)',
          'The single highest-WR setup in your data. Tara says 80%, Kalshi says 60% → 20pp edge → enter with confidence and full size. These are the trades that pay for the year.'),
        _row('✓','rgb(35,185,129)','Best-hour with positive edge',
          `Your historical hour-of-day WR is shown in the prep card before each window. If it's 70%+ AND edge is positive, this is a LARGE-tier entry. Time-of-day matters more than people think.`),
        _row('✓','rgba(35,185,129,0.75)','Confluent setup with strong regime',
          'When regime is TRENDING_UP/TRENDING_DOWN and the call aligns with the trend, plus FGT alignment ≥2, this is a sustainable lock. Skip the first signal, wait for the second one to confirm.'),
        _row('—','rgba(237,237,237,0.65)','Standard tier (good edge, normal hour)',
          'Take it at normal size. Most of your trade volume lives here. Keep stakes consistent — discipline beats inspiration.'),
      ]),

      // 2. When to SKIP
      _section('When to skip',[
        _row('✗','rgb(232,69,94)','Negative edge vs Kalshi (LATE bucket)',
          `If Tara's confidence is BEHIND Kalshi's, you're paying retail. The market knows what she knows, plus more. The position-size hint will say SKIP — listen to it.`),
        _row('✗','rgb(232,69,94)','Tilt cooldown active',
          `4+ losses in a row triggers a 15min forced cooldown. The override exists but is psychologically expensive — tilt entries are statistically the worst trades you'll ever make. Take the break.`),
        _row('✗','rgb(232,69,94)','TIGHT-edge bucket with sub-50% WR',
          `Check the edge-bucket WR in the Performance card. If your TIGHT bucket is below 50%, every TIGHT trade is destroying capital. This filter alone often saves more than any other improvement.`),
        _row('⚠','#23B981','Macro event imminent',
          `When CPI/NFP/FOMC is within ~30 minutes, posterior models break down. The market is repricing on news, not orderflow. Sit out unless you have specific edge on the event.`),
        _row('⚠','#23B981','Phase transition in progress',
          `Asia → EU open, EU → NY open are 15-30min windows where regime can flip violently. The phase pill above shows next transition — give those windows extra caution.`),
      ]),

      // 3. While in trade — what to watch
      _section('While in a trade — what to watch',[
        _row('🔔','rgb(232,69,94)','Tara LOCKED — STALE warning',
          `If posterior flips against your position by 30+ points OR Kalshi flips against you 25+ points, the advisor banner goes red and animated. This means the conditions that justified the lock have inverted. Strongly consider exit.`),
        _row('⚡','rgb(232,69,94)','Movement Risk pill at EXTREME',
          `Score 70+ means vol acceleration + volume spike + tape imbalance are all firing. Even when you're winning, this is when reversals happen. Be ready to exit.`),
        _row('🐋','rgba(168,85,247,0.85)','Whale prints opposing your position',
          `A $200K+ whale on the wrong side, especially in the last 60s, is the leading indicator of a late-window reversal. Watch the whale strip — if you see contrary prints stacking, exit.`),
        _row('⤵','#23B981','Conviction FADING',
          `In the DecisionalOverlay, the Conv chip shows BUILDING / STABLE / FADING. If it goes FADING (Tara's confidence dropped 5+ points since lock), her own model is losing confidence. That's your cue.`),
      ]),

      // 4. When to TAKE PROFIT
      _section('When to take profit',[
        _row('💰','rgb(35,185,129)','Offer ≥ $0.85 with time remaining',
          `If the Kalshi offer hits 0.85+ with 3+ minutes left, you've captured most of the available value. The marginal additional profit isn't worth the reversal risk. Configure auto-suggest in Trading Settings.`),
        _row('💰','rgb(35,185,129)','Big-edge entry has paid out',
          `When you enter with +15pp edge and the offer reaches +20pp profit, the math says exit. Your edge has already been realized; further gains are luck.`),
        _row('—','rgba(237,237,237,0.65)','Time decay favorable',
          `Last 90s with offer >0.75 and price holding: let it ride. Time decay is now your friend.`),
        _row('—','rgba(237,237,237,0.65)','Position with 2-3min left + advisor calm',
          `If conditions are stable, advisor isn't red, and you're up: hold to close. Most of the value is captured automatically by window expiry.`),
      ]),

      // 5. When to CUT LOSS
      _section('When to cut loss',[
        _row('🛑','rgb(232,69,94)','-30% stop hit',
          `Hard rule. Don't argue with it. The math says positions that hit -30% statistically continue down — the recovery rate is below your fee + slippage cost.`),
        _row('🛑','rgb(232,69,94)','Advisor: "Tara LOCKED — STALE"',
          `The conditions justifying the lock have inverted. Tara herself is telling you to exit. The exit-warning logic only fires after 30+ point posterior flip — by the time it fires, the trade is statistically lost.`),
        _row('⚠','#23B981','In-loss for 3+ minutes',
          `Configure auto-suggest in Trading Settings. If you've been underwater for 3 minutes and price isn't recovering, the trade was likely wrong from start. Cut and re-evaluate.`),
        _row('⚠','#23B981','Whale print opposing in last 60s',
          `Late-window whale spike against you is THE most common reversal cause. Even if Tara still says you should be winning, the path to expiry just got dangerous.`),
      ]),

      // 6. Position sizing
      _section('Position sizing',[
        _row('—','rgba(237,237,237,0.65)','Use Kelly as your ceiling, not your default',
          `The Performance card shows ¼-Kelly recommendation as % of bankroll. That's the ceiling for SUPER-strong entries. Most trades should be at 0.5-0.7× of that.`),
        _row('—','rgba(237,237,237,0.65)','Scale by tier, not by emotion',
          `LARGE: edge ≥15pp + best-hour. FULL: edge ≥15pp OR (edge ≥5pp + best-hour). NORMAL: standard. HALF: high-vol regime or 3+ loss streak. SKIP: negative edge or 5+ loss streak. The DecisionalOverlay tells you the tier.`),
        _row('—','rgba(237,237,237,0.65)','Bet sizing locked, not adaptive',
          `Configure your standard bet size in Trading Settings and stick to it. Adjusting size mid-session based on recent results is tilt in disguise.`),
      ]),

      // 7. Anti-tilt
      _section('Anti-tilt psychology',[
        _row('🛑','rgb(232,69,94)','Respect the cooldown',
          `4 losses in a row → 15min forced cooldown. Built-in for a reason: empirically, the trade right after a 4-loss streak has the worst WR you'll ever produce. The override exists only for genuine setups; never override out of frustration.`),
        _row('⚠','#23B981','Recognize chase patterns',
          `Common tilt: lose 2 in a row → take a tight-edge call you'd normally skip → lose → repeat. The edge-bucket filter blocks this; trust it.`),
        _row('—','rgba(237,237,237,0.65)','Wins also need management',
          `4+ win streak → don't size up. Mean reversion is real. The heater pill warns you not to give back gains by suddenly trading bigger.`),
      ]),

      // 8. What Tara learns from each trade
      _section('What Tara learns from each result',[
        _row('🧠','rgba(147,197,253,0.85)','Signal weights adjust per trade',
          `Each of 7 signals (gap, momentum, structure, flow, technical, regime, range-position) gets nudged based on whether it pointed correctly. Bigger contributors get bigger adjustments. Bystanders untouched.`),
        _row('🧠','rgba(147,197,253,0.85)','Per-regime patterns emerge',
          `She learns "in CHOP regime, flow is unreliable; in TRENDING_DOWN, momentum dominates" over ~30-50 trades per regime. This is why she behaves differently in different conditions.`),
        _row('🧠','rgba(147,197,253,0.85)','Shock losses don\'t poison learning',
          `When a loss is caused by a whale spike or macro shock (V8.5), gradient descent is dampened to 25% — your correct signals don't get unlearned because of bad luck.`),
        _row('🧠','rgba(147,197,253,0.85)','Path metrics reveal failure modes',
          `She tracks max-favorable / max-adverse excursion + when the peak happened, then classifies losses (LATE_REVERSAL, EARLY_PEAK, etc.) so you can see which failure mode dominates and target it.`),
      ]),

      // 9. Common pitfalls
      _section('Common pitfalls',[
        _row('×','rgb(232,69,94)','Trading every window',
          `You don't need to trade every 5 or 15 minutes. Most of your edge concentrates in your top 4-6 hours of the day. Skipping low-quality windows is profitable, not lazy.`),
        _row('×','rgb(232,69,94)','Ignoring the edge bucket',
          `If TIGHT and LATE buckets show <50% WR in your performance card, those trades are net-negative. Filtering them out is the highest-impact change you can make.`),
        _row('×','rgb(232,69,94)','Forcing a direction',
          `If Tara is ambivalent (NO_TRADE, posterior 45-55, low conviction), don't manually override and pick a side. Her abstention IS the call. Wait for the next window.`),
        _row('×','rgb(232,69,94)','Multiple tabs without awareness',
          `V8.3 fixed the multi-tab data loss bug. The peer-tab pill in the header confirms when other tabs are open. If you don't see the pill but expect to, refresh — sync may be out of date.`),
      ]),

      // 10. Auto-execution (V9.3.0+)
      _section('Auto-execution — when to use it',[
        _row('⚡','#23B981','Default OFF, dry-run ON',
          `Auto-exec is opt-in. When you flip it on, dry-run is on by default — orders are simulated, never sent. Verify a full window in dry-run before flipping the live switch. Settings live in the Trading Settings modal under Kalshi Auto-Execution.`),
        _row('⚡','#23B981','Conservative-but-aggressive config',
          `Highest-leverage setup: minTier=super-confluence, skipMarginalCaution=ON, lockStabilitySec=5, stopLossDeltaCents=30. Fires 1-3× per day on highest-conviction setups only. Lower volume, much higher WR per fire.`),
        _row('⚡','#23B981','High-volume config',
          `If you want her trading more: minTier=any, all assets/windows on, sizingMode=confidence with $5 low / $25 high. Fires on most windows. Higher noise, more loss-streak exposure — set tighter daily-loss caps to compensate.`),
        _row('⛔','#E8455E','Kill switch is in 3 places',
          `Top-bar pill (opens settings), Live Trade Coach status strip (always visible during a trade), and the big red button in settings. Engaging it instantly blocks new orders. Existing positions are NOT auto-closed — you decide whether to manually exit.`),
        _row('🛡','rgba(196,181,253,0.85)','Risk guardrails compose',
          `Max bet per trade × daily loss cap × loss-streak cooldown × anti-tilt × asset/window filter × tier filter × quality/conviction filter. Failing ANY of these blocks the trade. Stack them — none is sufficient on its own.`),
      ]),

      // 11. Mission Mode (V9.7.0)
      _section('Mission Mode — bankroll → target',[
        _row('🎯','#23B981','What it is',
          `Set a starting bankroll (e.g. $50), a target ($500), and a deadline (2 weeks). Tara uses fractional Kelly with cluster-specific WR to size each trade. Hard stops at drawdown floor (e.g. $10) and at target. Auto-exec disables itself once target hit — no chasing past it.`),
        _row('📐','#23B981','Math is honest about your odds',
          `Every render shows estimated probability of hitting target by deadline. <25% odds → "target unrealistic" warning. The math assumes constant edge; real variance is higher. Treat the % as order of magnitude. If it says 15%, don't expect the math to be wrong by enough to save you.`),
        _row('⚖','#23B981','Aggressive targets bust more than they win',
          `$25 → $2000 in a week is "possible but median outcome is bust." Even at 65% WR with proper sizing, ~50-60% of paths hit floor before target. The drawdown floor is what protects the rest of your bankroll. Set it meaningfully (typically 20-30% of starting).`),
        _row('🎚','#23B981','Kelly multiplier choices',
          `0.10× = very conservative, very low bust risk, slow growth. 0.25× = quarter Kelly, recommended default. 0.50× = half Kelly, faster growth, higher bust risk. 1.00× = full Kelly, maximum geometric growth in theory, very high bust risk in practice. Most professional bettors use 0.25-0.50× for real reasons.`),
        _row('▶','#23B981','Status states',
          `inactive (no mission), active (sizing live), paused (you stopped it; auto-exec falls back to fixed sizing), hit (target reached, auto-exec disabled to lock in win), busted (floor breached, trading stopped to protect remaining bankroll), expired (deadline passed without hit).`),
        _row('📊','#23B981','When to pause',
          `Big news event coming, or you don't trust the conditions, or you want to manage discretionarily for a while. Pause keeps the mission alive but stops Mission sizing. Resume when ready.`),
      ]),

      React.createElement('div',{
        className:'mt-5 pt-4 border-t border-[#24242E] text-center',
      },
        React.createElement('button',{
          onClick:onClose,
          className:'px-6 py-2 rounded-lg text-[12px] uppercase font-bold tracking-wider transition-colors',
          style:{background:'rgba(35,185,129,0.12)',color:T2_GOLD,border:'1px solid rgba(35,185,129,0.3)'},
        },'Got it'),
      ),
    ),
  );
}

// ── V13.4.303 REMOVED: WindowStrikeChart ("this window vs strike"). Direct
// request -- "take off the graph too, its making my pc lag and taking up too
// much resources". The per-tick SVG polyline rebuild (up to 140 downsampled
// points, area fill, gradient defs) was real per-frame layout+paint cost on a
// lower-powered machine. Its one remaining call site (center price panel,
// v301 removed the ThisTradeCard duplicate) is gone too, so nothing calls it.

// ── V13.4.268: THIS TRADE ────────────────────────────────────────────────────
// The card from the mockup, and the actual merge he asked for. One trade told
// as three numbered stages in plain sentences, in one place:
//
//   (1) THE CALL      what Tara decided, and why
//   (2) HOW IT'S GOING  what the position is worth right now
//   (3) WHAT IT DID   what the machine actually placed, and how it gets out
//
// v263 moved LiveTradeCoach next to Tara's Call and called that the merge. It
// was not: the coach renders null without an open position, so with no trade on
// he still saw the old scattered layout -- TARA'S CALL in one column, TRADE
// COACH in another, AUTO-EXEC in a third, all describing the same round.
//
// This card is ALWAYS present. Stages 2 and 3 stay visible with no position and
// say so, because "nothing placed yet" is information about this trade too, and
// a card that changes shape is a card you have to re-read every time.
//
// Numbers freeze on commit, never drift -- the v265 rule. Confidence, reason and
// direction all come from the committed snapshot when one exists.
function ThisTradeCard({taraCall,snapshot,analysis,timeState,windowType,kalshiYesPrice,
                        autoOrderState,userPosition,trailPeakCents,autoExecSettings,timeFormat,
                        tickHistoryRef,targetMargin,currentPrice,manualKalshiEntry,
                        killSwitchEngaged,setShowTradingSettings,children}){
  // Declared BEFORE the early return: hooks must run in the same order on every
  //   render, and this component can return null on the very first one.
  const[whyOpen,setWhyOpen]=React.useState(false);
  const _leanStickyRef=React.useRef({dir:null,since:0});
  if(!taraCall)return null;

  // ── stage 1: what Tara decided ──────────────────────────────────────────
  // V13.4.280: routed through readLockState so this card cannot drift from the
  //   other panels the way they all drifted from each other.
  const _lockSt=readLockState(snapshot);
  const _snap=_lockSt.committed?snapshot:null;
  const _snapDir=_lockSt.dir;
  const _satOut=_lockSt.satOut;
  const _liveDir=(taraCall.call==='UP'||taraCall.call==='DOWN')?taraCall.call
    :(taraCall.direction==='UP'||taraCall.direction==='DOWN')?taraCall.direction:null;
  // V13.4.275: STABILISE THE PRE-LOCK LEAN. Before this, stage 1 read the raw live
  //   call every tick, so it flipped UP/DOWN with each price wobble and disagreed
  //   with the advisor, which reads the sample-confirmed lean. That flip-chasing is
  //   the exact behaviour he reported once already ("it suggests that position and
  //   hold and switches instantly, theres no lock to it"), and TradeCoachCall grew
  //   a debounce for it in V13.4.121. Same idea here: hold a displayed lean for a
  //   minimum stretch before allowing it to flip.
  //   Applies ONLY pre-commit -- a committed snapshot is never debounced.
  const LEAN_HOLD_MS=8000;
  const _stick=_leanStickyRef.current;
  let _dispLean=_liveDir;
  if(!_snapDir&&!_satOut&&_liveDir){
    const _now=Date.now();
    if(_stick.dir!==_liveDir){
      // adopt a new direction only once the current one has been shown long enough
      if(!_stick.dir||(_now-_stick.since)>=LEAN_HOLD_MS){
        _leanStickyRef.current={dir:_liveDir,since:_now};
        _dispLean=_liveDir;
      }else{
        _dispLean=_stick.dir;
      }
    }else{
      _dispLean=_stick.dir;
    }
  }
  const _leanFlipping=!!(_dispLean&&_liveDir&&_dispLean!==_liveDir);
  // committed snapshot wins over the live read, always
  const dir=_snapDir||(_satOut?null:_dispLean);
  const _leanDir=_lockSt.intendedDir;
  // V13.4.280: the committed label comes from the helper, so this card and every
  //   other panel spell the same state the same way.
  const state=_lockSt.label||(_liveDir?'LEANING':'SCANNING');
  const _src=_snap||taraCall;
  // V13.4.273: `confidence` does NOT mean one thing across the engine. At the
  //   directional-lock site it is stored as the raw posterior P(UP) (L44426,
  //   `confidence:_post`), while committed snapshots carry it already flipped to
  //   the called side. So a DOWN call could render "30%" on one path and "65%"
  //   on another when both meant a strong DOWN.
  //   `conviction` is unambiguous -- it is |posterior - 50| at every site -- so
  //   50 + conviction is the confidence in whichever side was actually called,
  //   under either convention. Fall back to flipping the raw value only when
  //   conviction is missing.
  //   V13.4.280: once committed, take it straight from the helper so the frozen
  //   number is identical everywhere. Pre-commit there is no snapshot to read, so
  //   the same rule is applied to the live object here.
  const _convict=Number(_src&&_src.conviction);
  const _rawConf=Number(_src&&_src.confidence);
  const conf=_lockSt.committed?_lockSt.confidence
    :(Number.isFinite(_convict)&&_convict>0
      ?Math.round(50+_convict)
      :(Number.isFinite(_rawConf)&&_rawConf>0
          ?Math.round((dir==='DOWN'&&_rawConf<50)?(100-_rawConf):_rawConf)
          :0));
  const histWR=Number(taraCall._v10_7_43_calHistWR);
  const _k=Number(kalshiYesPrice);
  const _kValid=Number.isFinite(_k)&&_k>0&&_k<100;
  const _edgeDir=dir||_leanDir;
  // Edge is Tara's confidence minus what the market already charges for the same
  // side. kalshiYesPrice is the UP price; a DOWN contract costs 100 minus it.
  // V13.4.289: this used the LIVE kalshiYesPrice while `conf` beside it is FROZEN at
  //   commit, so stage 1 mixed two moments in one row — and it contradicted the
  //   "Edge vs market" row below, which is gated on snap.kalshiAtLock and is therefore
  //   the AT-LOCK figure. Seen live as "EDGE -15PT" on the card over "GOOD EDGE +12pt"
  //   in the row: both correct, both labelled just edge, no way to tell them apart.
  //   Stage 1 describes the decision AS MADE, so it takes the price as it was at the
  //   lock. The live comparison belongs to stage 2, which is about how it is going.
  //   Pre-commit there is no lock price, so the live one is correct there.
  const _edgeK=(()=>{
    const _atLock=Number(_snap&&_snap.kalshiAtLock);
    if(_lockSt.committed&&Number.isFinite(_atLock)&&_atLock>0&&_atLock<100)return _atLock;
    return _kValid?_k:null;
  })();
  const edge=(_edgeK!=null&&conf>0&&_edgeDir)?Math.round(conf-(_edgeDir==='UP'?_edgeK:(100-_edgeK))):null;
  const whyFull=String((_src&&_src.reason)||taraCall.reason||'')
    .replace(/^\[V?\d+[\d.]*\s*[A-Z0-9\-]*\]\s*/,'')
    .split(/\s*—\s*was:\s*/)[0].trim();
  // V13.4.269: reason strings in this file are audit-trail text by design --
  //   version tags, vote counts, per-signal arithmetic. Fine in the call log,
  //   unreadable as the one line that explains the trade. The leading clause is
  //   always the actual finding, so that is the line; the rest sits behind WHY.
  // V13.4.281: taking ONLY the first clause could produce a bare label that
  //   contradicts the headline right above it -- seen live as "LEANING" over a
  //   why of "Directional lock", from the full string "Directional lock · UP 15pt
  //   · structural momentum in 6bps window". A two-word label is never the
  //   finding. Keep adding clauses until the line actually says something.
  const _whyParts=whyFull.split(/\s+·\s+/).map(s=>s.trim()).filter(Boolean);
  let why=_whyParts[0]||'';
  for(let i=1;i<_whyParts.length&&why.length<34;i++)why+=' · '+_whyParts[i];
  const whyHasMore=whyFull.length>why.length+2;

  const _secsLeft=timeState?((Number(timeState.minsRemaining)||0)*60+(Number(timeState.secsRemaining)||0)):null;
  const _clock=_secsLeft!=null?Math.floor(_secsLeft/60)+':'+String(_secsLeft%60).padStart(2,'0'):null;
  const _closeLabel=(()=>{
    if(_secsLeft==null)return null;
    try{
      const d=new Date(Date.now()+_secsLeft*1000);
      return d.toLocaleTimeString([], {hour:'2-digit',minute:'2-digit',hour12:timeFormat==='12h'});
    }catch(_e){return null;}
  })();

  const GREEN='#23B981', RED='#E8455E', GOLD='#D4A03A', DIM='rgba(237,237,237,0.42)';
  const dirTone=dir==='UP'?GREEN:dir==='DOWN'?RED:GOLD;

  // ── stage 2: what it is worth right now ─────────────────────────────────
  // V13.4.278: a position is not only an AUTO one. This read autoOrderState alone,
  //   so with a manually-entered trade open the header said "POSITION UP @ $79958"
  //   while this card said "NOT IN · nothing filled yet" -- the card telling him he
  //   is flat while he is holding. Three ways to be in, in order of how much they
  //   know: an auto fill, a manually recorded Kalshi fill, or userPosition marked
  //   with no fill price captured yet.
  // V13.4.286: read autoOrderState.fillPrice / .count -- names the writer has NEVER
  //   set (it writes filledAtCents / filledCount). So _haveAutoFill was permanently
  //   false and this card said NOT IN / NOTHING YET with a live filled order on the
  //   book. Routed through readOrderState, which accepts both spellings.
  const _ord=readOrderState(autoOrderState);
  const _autoFill=_ord.fillCents;
  const _haveAutoFill=_ord.placed;
  const _manFill=Number(manualKalshiEntry&&manualKalshiEntry.entryCents);
  const _haveManFill=Number.isFinite(_manFill)&&_manFill>0;
  const _isManual=!_haveAutoFill&&_haveManFill;
  const _fill=_haveAutoFill?_autoFill:(_haveManFill?_manFill:NaN);
  const _haveFill=_haveAutoFill||_haveManFill;
  // V13.4.351: a confirmed Kalshi no-fill is terminal for this auto attempt.
  // The auto-follow marker is intentionally advisory, so it must not keep making
  // this card look occupied after reconciliation has proved that the order never
  // created a real position.
  const _confirmedNoFill=!!(!_haveManFill&&autoOrderState&&(
    autoOrderState.status==='no-fill'||autoOrderState.noFillConfirmed===true
  ));
  // marked in, but we do not know what was paid -- worth saying, not worth guessing
  const _markedOnly=!_confirmedNoFill&&!_haveFill&&(userPosition==='UP'||userPosition==='DOWN');
  const _side=_ord.dir||(manualKalshiEntry&&manualKalshiEntry.side)||(!_confirmedNoFill?userPosition:null)||dir;
  const _worth=(_kValid&&_side)?Math.round(_side==='UP'?_k:(100-_k)):null;
  const _delta=(_haveFill&&_worth!=null)?(_worth-_fill):null;
  const _peak=Number(trailPeakCents);
  const _havePeak=Number.isFinite(_peak)&&_peak>0;
  // V13.4.290 (ORD-3): readOrderState already computes `exited`/`errored` from
  //   status, but nothing here consulted them -- `placed` requires only a fill,
  //   which the merge-based writer keeps set forever once an order fills, through
  //   'exiting', 'exited' and any post-fill 'error'. So a CLOSED position read as
  //   "holding up / filled · live" with no way to tell it apart from one still open,
  //   and a FAILED EXIT -- the one state where the user most needs to know Kalshi
  //   did not do what was asked -- showed the identical calm green badge.
  //   Manual fills carry neither field (autoOrderState is untouched by them), so
  //   these are gated on _haveAutoFill.
  const _autoExited=_haveAutoFill&&_ord.exited;
  const _autoExitErrored=_haveAutoFill&&_ord.errored;
  // Same scale fix as ORD-1: exitCents is the RAW YES price sent to Kalshi
  // (_exitYesLimitCents), our-side for DOWN only after this conversion.
  const _exitWorth=(_autoExited&&Number.isFinite(_ord.exitCents))
    ?(_side==='DOWN'?(100-_ord.exitCents):_ord.exitCents)
    :null;
  const _realized=(_exitWorth!=null&&Number.isFinite(_fill))?(_exitWorth-_fill):null;

  // ── stage 3: what was actually placed ───────────────────────────────────
  const _st=_ord.status;
  const _count=_haveAutoFill
    ?_ord.count
    :(Number(manualKalshiEntry&&manualKalshiEntry.contracts)||0);
  const _dry=!!(autoOrderState&&autoOrderState.dryRun)&&!_isManual;
  const _placed=_haveFill&&_count>0;
  const _tp=Number(autoExecSettings&&autoExecSettings.autoExitOffer)||0;
  const _sl=Number(autoExecSettings&&autoExecSettings.stopLossDeltaCents)||0;
  // V13.4.308: "make auto-exec visually simple, combine it into the call
  //   window" -- the single highest-anxiety fact when watching a live window
  //   is "is this actually armed right now", so that (and only that) moves
  //   here, as one tappable word ahead of the exit-rule sentence it already
  //   sits next to. Everything editable (the dials, Mission, presets) stays
  //   in Trading Settings; tapping the word just opens it.
  const _exec=computeAutoExecReadout(autoExecSettings,killSwitchEngaged);

  const Stage=({n,title,badge,badgeTone,children})=>(
    <div className="px-4 py-3.5 border-t border-[#16161c] first:border-t-0">
      <div className="flex items-baseline justify-between gap-2 mb-2">
        <div className="flex items-baseline gap-2">
          <span className="inline-flex items-center justify-center w-[17px] h-[17px] rounded-full text-[9px] font-bold tabular-nums shrink-0"
                style={{border:'1px solid rgba(237,237,237,0.18)',color:'rgba(237,237,237,0.5)'}}>{n}</span>
          <span className="text-[9.5px] uppercase font-bold tracking-[0.15em] text-[#EDEDED]/40">{title}</span>
        </div>
        {badge&&<span className="text-[9.5px] uppercase font-bold tracking-[0.12em]" style={{color:badgeTone}}>{badge}</span>}
      </div>
      {children}
    </div>
  );

  return (
    <div className="tara-execution-card border border-[#1B1B22] bg-[#0A0A0E] rounded-[10px] overflow-hidden">
      <div className="px-4 py-2.5 border-b border-[#16161c] flex items-baseline justify-between gap-2"
           style={{background:dirTone+'0A'}}>
        <span className="text-[9.5px] uppercase font-bold tracking-[0.15em]" style={{color:dirTone+'BB'}}>AutoTrade · execution</span>
        <span className="text-[9.5px] uppercase font-bold tracking-[0.12em] text-[#EDEDED]/30 tabular-nums">
          {String(windowType||'').toUpperCase()}{_closeLabel?' · closes '+_closeLabel:''}
        </span>
      </div>

      {/* ── 1 ── */}
      <Stage n="1" title="the call" badge={state} badgeTone={_snapDir?dirTone:_satOut?GOLD:DIM}>
        <div className="flex items-baseline justify-between gap-3">
          <div className="flex items-baseline gap-2.5 min-w-0">
            <span className="text-[26px] leading-none" style={{color:dirTone}}>
              {dir==='UP'?'↑':dir==='DOWN'?'↓':'·'}
            </span>
            <span className="text-[30px] leading-none font-semibold tracking-[-0.02em] truncate" style={{color:dirTone}}>
              {dir||(_satOut?'SITTING OUT':'SCANNING')}
            </span>
          </div>
          {_clock&&<span className="text-[19px] tabular-nums shrink-0" style={{color:GOLD}}>{_clock}</span>}
        </div>
        {_satOut&&_leanDir&&(
          <div className="text-[11px] text-[#EDEDED]/45 mt-1.5">would have leaned {_leanDir}</div>
        )}
        {/* V13.4.275: say plainly that the read is moving, rather than either hiding
            the hold or letting the headline flip. This is exactly when not to act. */}
        {_leanFlipping&&(
          <div className="text-[11px] mt-1.5" style={{color:GOLD}}>
            Read is unstable — live signal just moved to {_liveDir}. Holding {dir} until it settles.
          </div>
        )}
        <div className="flex flex-wrap items-baseline gap-x-5 gap-y-1 mt-3">
          {conf>0&&<span className="text-[9.5px] uppercase tracking-[0.11em] text-[#EDEDED]/35 font-semibold">
            confidence <span className="text-[11px] text-[#EDEDED]/80 tabular-nums font-bold ml-0.5">{conf}%</span></span>}
          {Number.isFinite(histWR)&&histWR>0&&<span className="text-[9.5px] uppercase tracking-[0.11em] text-[#EDEDED]/35 font-semibold">
            hist wr <span className="text-[11px] text-[#EDEDED]/80 tabular-nums font-bold ml-0.5">{Math.round(histWR)}%</span></span>}
          {edge!=null&&<span className="text-[9.5px] uppercase tracking-[0.11em] text-[#EDEDED]/35 font-semibold">
            edge <span className="text-[11px] tabular-nums font-bold ml-0.5"
                       style={{color:edge>=0?GREEN:RED}}>{edge>0?'+':''}{edge}pt</span></span>}
        </div>
        {why&&(
          <div className="mt-3 px-3 py-2 rounded-lg flex items-baseline justify-between gap-3"
               style={{background:'rgba(237,237,237,0.03)',border:'1px solid #16161c',cursor:whyHasMore?'pointer':'default'}}
               onClick={()=>{if(whyHasMore)setWhyOpen(v=>!v);}}>
            <span className="text-[12px] text-[#EDEDED]/70 leading-snug min-w-0">{whyOpen?whyFull:why}</span>
            {whyHasMore&&<span className="text-[8.5px] uppercase font-bold tracking-[0.14em] text-[#EDEDED]/25 shrink-0">{whyOpen?'less':'why'}</span>}
          </div>
        )}
      </Stage>

      {/* V13.4.274: the picture of the same trade, between the decision and how it
          is going, because that is the order you actually read it in. Renders null
          without a strike or a price, so it never leaves an empty band. */}
      {/* V13.4.301 REMOVED. v296 added a SECOND WindowStrikeChart to the centre
          price panel and argued the two placements answered different questions.
          On screen they answer the same one: both charts plot the same series
          from the same ref, so the page showed "this window vs strike", the same
          strike, the same tick count and the same "below by 0bps" twice, side by
          side, a column apart. The mockup has exactly one chart and it lives in
          the centre panel under price/strike/depth -- the left column's THIS
          TRADE panel is three text stages with no chart at all. The centre one
          is now the only one, and it renders at every width. */}

      {/* ── 2 ── */}
      <Stage n="2" title="how it's going"
             badge={_confirmedNoFill?'no fill confirmed'
                    :_autoExited?(_realized==null?'closed':_realized>0?'closed · won':_realized<0?'closed · lost':'closed · flat')
                    :_placed?(_delta==null?'—':_delta>0?'holding up':_delta<0?'going against':'flat')
                    :_markedOnly?'marked · unverified':'not in'}
             badgeTone={_confirmedNoFill?GREEN
                    :_autoExited?(_realized>0?GREEN:_realized<0?RED:DIM)
                    :_placed?(_delta>0?GREEN:_delta<0?RED:DIM):_markedOnly?GOLD:DIM}>
        {_confirmedNoFill?(
          <div className="text-[13px] leading-snug" style={{color:'rgba(237,237,237,0.62)'}}>
            Kalshi confirmed no fill. This auto-trade is flat; there is no real position to manage.
          </div>
        ):_autoExited?(
          <div className="text-[15px] leading-snug text-[#EDEDED]/85">
            Closed. {_exitWorth!=null&&<>Sold at <span className="font-semibold tabular-nums">{_exitWorth}¢</span>, </>}
            {_realized!=null&&<>realized <span className="font-semibold tabular-nums" style={{color:_realized>0?GREEN:_realized<0?RED:'#EDEDED'}}>{_realized>0?'+':''}{_realized}¢</span>/contract from a {_fill}¢ entry.</>}
          </div>
        ):_placed&&_worth!=null?(
          <>
            {_autoExitErrored&&(
              <div className="text-[12px] leading-snug mb-2" style={{color:RED}}>
                ⚠ Exit attempt failed{autoOrderState?.reason?': '+autoOrderState.reason:''}. Still holding — may need manual action on Kalshi.
              </div>
            )}
            <div className="text-[15px] leading-snug text-[#EDEDED]/85">
              Worth <span className="font-semibold tabular-nums" style={{color:_delta>0?GREEN:_delta<0?RED:'#EDEDED'}}>{_worth}¢</span>
              {_delta!=null&&<>, {_delta>0?'up':_delta<0?'down':'flat'}{_delta!==0?' '+Math.abs(_delta)+'¢':''} from entry.</>}
            </div>
            <div className="flex flex-wrap items-baseline gap-x-5 gap-y-1 mt-2.5">
              {_havePeak&&<span className="text-[9.5px] uppercase tracking-[0.11em] text-[#EDEDED]/35 font-semibold">
                peak <span className="text-[11px] text-[#EDEDED]/80 tabular-nums font-bold ml-0.5">{Math.round(_peak)}¢</span></span>}
              <span className="text-[9.5px] uppercase tracking-[0.11em] text-[#EDEDED]/35 font-semibold">
                trail arms at <span className="text-[11px] text-[#EDEDED]/80 tabular-nums font-bold ml-0.5">{TRAIL_ARM_C}¢</span></span>
            </div>
          </>
        ):_markedOnly?(
          /* A local follow marker is not an exchange-confirmed fill or position. */
          <div className="text-[13px] leading-snug" style={{color:'rgba(237,237,237,0.62)'}}>
            Marked as following <span style={{color:_side==='UP'?GREEN:RED}}>{_side}</span> locally.
            {' '}No fill is recorded, so this is not a verified Kalshi position or tracked P&amp;L.
          </div>
        ):(
          <div className="text-[13px] text-[#EDEDED]/40 leading-snug">
            {state==='LOCKED'?'Locked, but nothing filled yet.'
              :_satOut?'Sitting this one out, so there is nothing to watch.'
              :'No position on this window yet.'}
          </div>
        )}
      </Stage>

      {/* ── 3 ── */}
      <Stage n="3" title="what it did"
             badge={_confirmedNoFill?'no fill confirmed'
                    :_autoExited?(_realized==null?'exited':_realized>0?'exited · profit':_realized<0?'exited · loss':'exited · flat')
                    :_autoExitErrored?'⚠ exit failed'
                    :_placed?(_isManual?'you · manual':_dry?'simulated':'filled · live')
                    :_markedOnly?'no fill logged':(_st?_st.toUpperCase():'nothing yet')}
             badgeTone={_confirmedNoFill?GREEN
                    :_autoExited?(_realized>0?GREEN:_realized<0?RED:'#EDEDED')
                    :_autoExitErrored?RED
                    :_placed?(_isManual?'#EDEDED':_dry?GOLD:GREEN):_markedOnly?GOLD:DIM}>
        {_confirmedNoFill?(
          <div className="text-[13px] text-[#EDEDED]/55 leading-snug">
            No order filled on Kalshi. The stale advisory marker was cleared automatically.
          </div>
        ):_placed?(
          <div className="text-[15px] leading-snug text-[#EDEDED]/85">
            {/* V13.4.278: say WHO bought. A manual fill is his own order, and calling
                it "Bought" as though the machine did it is the kind of small lie that
                makes the rest of the card untrustworthy. */}
            {_isManual?'You bought':_dry?'Would have bought':'Bought'}{' '}
            <span className="font-semibold tabular-nums">{_count}</span> at{' '}
            <span className="font-semibold tabular-nums">{Math.round(_fill)}¢</span>
            {' — '}<span className="tabular-nums">${((_fill*_count)/100).toFixed(2)}</span> in.
            {/* V13.4.290 (ORD-3): say what happened AFTER the fill too -- this
                sentence used to be the whole story forever, through exit and any
                exit failure. */}
            {_autoExited&&_exitWorth!=null&&<> Exited at <span className="font-semibold tabular-nums">{_exitWorth}¢</span>.</>}
            {_autoExitErrored&&<span style={{color:RED}}> Exit attempt failed{autoOrderState?.reason?': '+autoOrderState.reason:''}.</span>}
          </div>
        ):_markedOnly?(
          <div className="text-[13px] text-[#EDEDED]/55 leading-snug">
            No exchange fill is recorded. If you bought {_side} on Kalshi, confirm it there and
            {' '}enter the actual fill under REAL KALSHI FILL to track the trade.
          </div>
        ):(
          <div className="text-[13px] text-[#EDEDED]/40 leading-snug">
            {autoExecSettings&&autoExecSettings.enabled===false
              ?'Auto-exec is off — you place this one yourself.'
              :'No order placed on this window.'}
          </div>
        )}
        <div className="text-[12px] text-[#EDEDED]/45 mt-2 leading-snug">
          <span
            className="uppercase font-bold tracking-[0.1em] cursor-pointer hover:opacity-75 transition-opacity"
            style={{color:_exec.tone}}
            onClick={()=>{if(typeof setShowTradingSettings==='function')setShowTradingSettings(true);}}
            title="Open Trading Settings"
          >{_exec.word}</span>
          <span className="text-[#EDEDED]/25"> · </span>
          {_tp>0&&_tp<=TRAIL_ARM_C
            ? <span style={{color:'#E8455E'}}>Fixed target at {_tp}¢ fires before the trail can arm at {TRAIL_ARM_C}¢.</span>
            : (_tp>0||_sl>0)
              ? <>Sells itself {TRAIL_GIVEBACK_C}¢ off the high, plus a fixed rule you set{_tp>0?' (take '+_tp+'¢)':''}{_sl>0?' (cut '+_sl+'¢)':''}.</>
              : <>Sells itself {TRAIL_GIVEBACK_C}¢ off the high. No fixed target.</>}
        </div>
      </Stage>

      {/* V13.4.314: "literally combine [auto-exec] with Tara's call" -- the
          AUTO-EXEC/scalper panel used to render in a separate card, in a
          separate grid column (PredictionContent, middle column), so on
          desktop the two only ever sat side by side, never stacked. It now
          renders as a slot inside THIS card's own border, right after stage 3,
          so there is exactly one bordered box instead of two. Nothing about
          ScalperAdvisorPanel's own logic changed -- only where its JSX
          mounts. */}
      {children}
    </div>
  );
}

// ── V8.7: LIVE TRADE COACH ───────────────────────────────────────────────────
// Renders only while user is in a position. Generates situational guidance based on
// current state: what to watch, what your move likely is, what to hope for, what to
// avoid. Pulls from existing analysis + movementRisk + taraCall + position status.
// Each card is one actionable observation. Color-coded by urgency.
//
// V9.1.7 upgrades — answer to user feedback "i feel i still keep missing out on
//   calls when trade is reversing against me, like price spikes or dips, same for
//   maximizing profits and getting a good entry":
//     1. TRAJECTORY tracking (last 30s direction of travel) — catches reversals
//        BEFORE the gap-bps math reflects them. Surfaces "PRICE SPIKING AGAINST
//        YOU NOW" / "PRICE ACCELERATING IN YOUR FAVOR".
//     2. PEAK / TROUGH refs reset on position open — catches "you were +25bps
//        and now you're +8bps — drawing down from peak, consider taking offer".
//     3. ACCELERATION detection (last 10s velocity vs prior 30s) — sharper alert
//        than just "risk elevated" because it reflects what's happening RIGHT
//        NOW, not what conditions look like.
// ── V13.4.240: TRAILING EXIT THRESHOLDS ─────────────────────────────────────
// Derived by replaying all 454 settled 15m calls against their own Kalshi
// candlestick history (tickers rebuilt from windowId; the log never stored one).
// These are the contract's real bid path, not the underlying's move.
//
// Arm once the position is worth >= ARM, then alert if it gives back >= GIVEBACK
// from its own high. Scored on the 359 calls outside the 70-84c dead zone that
// v13.4.178 now blocks, since that band is no longer reachable:
//   +242c vs holding (+0.67c/contract). Fires on 31 of 359.
//   saves 710c across 12 that went on to lose; costs 468c across 19 that won.
// The (85..95 arm x 5..10 giveback) neighbourhood is positive in 8 of 9 cells,
// so this is a region rather than one lucky cell.
const TRAIL_ARM_C=90;
const TRAIL_GIVEBACK_C=8;

// V13.4.308: shared armed/dry/killed readout, extracted from the "Right now"
//   banner in TradingSettingsModal (which now calls this instead of computing
//   its own copy) so the same booleans/tone can also drive a compact indicator
//   directly on Tara's own call card (ThisTradeCard, below) -- one source of
//   truth, so the two surfaces can never quietly disagree about whether real
//   orders are currently live.
function computeAutoExecReadout(autoExecSettings,killSwitchEngaged){
  const armed=!!autoExecSettings?.enabled;
  const dry=autoExecSettings?.dryRun!==false;
  const killed=!!killSwitchEngaged;
  const tone=killed||armed&&!dry?'#E8455E':'#D4A03A';
  const word=killed?'Killed':!armed?'Calling only':dry?'Armed · dry':'Armed · live';
  return{armed,dry,killed,tone,word};
}

function LiveTradeCoach({userPosition,positionStatus,taraCall,analysis,movementRisk,currentPrice,targetMargin,timeState,kalshiYesPrice,currentOffer,whaleLog,tradingSettings,todayData,tickHistoryRef,autoOrderState,killSwitchEngaged,onKillSwitch,onUrgentCoachAlert,reversalRisk,onClearAutoOrder,liveCoachReversalRef}){
  // V9.1.7: Per-position peak/trough refs. Reset on position open or direction flip.
  const _peakBpsRef=React.useRef({pos:null,peak:-Infinity,trough:Infinity,openTime:0,valPeak:-1,valArmed:false});
  // V9.2.1: Sound alert tracking — only beep when a NEW urgent card appears, not on every render.
  const _lastCoachBeepRef=React.useRef({key:'',at:0});
  // V13.4.241: cards that stand for the entire trade (reversal risk, trailing
  //   exit) must not re-fire on the time-based throttles below. Namespaced per
  //   channel so a beep does not consume the Discord page. Cleared on position
  //   change, same as the peak refs.
  const _oncePagedRef=React.useRef({pos:null,keys:{}});
  React.useEffect(()=>{
    // When position opens or flips, reset
    const _curPos=userPosition;
    const _ref=_peakBpsRef.current;
    if(_curPos!==_ref.pos){
      _peakBpsRef.current={pos:_curPos,peak:-Infinity,trough:Infinity,openTime:Date.now(),valPeak:-1,valArmed:false};
      _oncePagedRef.current={pos:_curPos,keys:{}};
    }
  },[userPosition]);
  if(!userPosition)return null; // only shows during a trade
  // V8.7.1: Wrap entire body in try/catch.
  try{
    const _now=Date.now();
    const _isUP=userPosition==='UP';
    const _isDN=userPosition==='DOWN';
    const _gap=targetMargin>0?((Number(currentPrice)||0)-targetMargin)/targetMargin*10000:0;
    const _favoredGap=_isUP?_gap:-_gap; // signed: + = favorable
    const _winning=_favoredGap>0;
    const _losing=_favoredGap<0;
    const _secLeft=(timeState?.minsRemaining||0)*60+(timeState?.secsRemaining||0);
    const _winLen=15*60;
    const _earlyWindow=_secLeft>=_winLen*0.66;
    const _midWindow=_secLeft<_winLen*0.66&&_secLeft>=_winLen*0.33;
    const _lateWindow=_secLeft<_winLen*0.33;
    const _last90s=_secLeft<=90;
    const _veryLate=_secLeft<=45;

    // V9.1.7: Update peak/trough on every render
    if(_peakBpsRef.current.pos===userPosition){
      if(_favoredGap>_peakBpsRef.current.peak)_peakBpsRef.current.peak=_favoredGap;
      if(_favoredGap<_peakBpsRef.current.trough)_peakBpsRef.current.trough=_favoredGap;
    }
    const _peak=_peakBpsRef.current.peak;
    const _trough=_peakBpsRef.current.trough;
    const _drawdownFromPeak=_peak>0?_peak-_favoredGap:0; // bps lost from your high
    const _reboundFromTrough=_trough<0?_favoredGap-_trough:0;

    // V9.1.7: Trajectory analysis — recent price velocity. Compares last 10s velocity
    //   to the 20s window before that to detect ACCELERATION (what's happening RIGHT NOW).
    let _recentDir=null,_acceleration=null,_recent10sBps=null,_prior20sBps=null;
    try{
      const _ticks=tickHistoryRef?.current||[];
      if(_ticks.length>=3&&Number(currentPrice)>0){
        const _t10=_now-10000,_t30=_now-30000;
        const _veryRecent=_ticks.filter(t=>t&&t.time&&t.time>=_t10);
        const _earlier=_ticks.filter(t=>t&&t.time&&t.time<_t10&&t.time>=_t30);
        if(_veryRecent.length>=1&&_earlier.length>=1){
          const _p10Start=Number(_earlier[_earlier.length-1].p)||Number(_earlier[_earlier.length-1].price)||0;
          const _p10End=Number(_veryRecent[_veryRecent.length-1].p)||Number(_veryRecent[_veryRecent.length-1].price)||currentPrice;
          if(_p10Start>0&&_p10End>0){
            _recent10sBps=((_p10End-_p10Start)/_p10Start)*10000;
            const _p30Start=Number(_earlier[0].p)||Number(_earlier[0].price)||0;
            if(_p30Start>0){
              _prior20sBps=((_p10Start-_p30Start)/_p30Start)*10000;
              // Acceleration = recent velocity - prior velocity, normalized to bps/sec
              const _recentRate=_recent10sBps/10;
              const _priorRate=_prior20sBps/20;
              _acceleration=_recentRate-_priorRate;
              _recentDir=_recent10sBps>1?'UP':_recent10sBps<-1?'DOWN':'FLAT';
            }
          }
        }
      }
    }catch(_){}
    const _trajAgainst=_recentDir&&((_isUP&&_recentDir==='DOWN')||(_isDN&&_recentDir==='UP'));
    const _trajFor=_recentDir&&((_isUP&&_recentDir==='UP')||(_isDN&&_recentDir==='DOWN'));
    const _spikeAgainst=_trajAgainst&&Math.abs(_recent10sBps||0)>=8;
    const _spikeFor=_trajFor&&Math.abs(_recent10sBps||0)>=8;
    const _acceleratingAgainst=_trajAgainst&&_acceleration!=null&&Math.abs(_acceleration)>=0.5;
    const _acceleratingFor=_trajFor&&_acceleration!=null&&Math.abs(_acceleration)>=0.5;

    // Tara's read: is she still aligned?
    const _taraDir=taraCall?.snapshot?.call;
    const _taraAligned=_taraDir===userPosition;
    const _taraOpposed=(_taraDir==='UP'&&_isDN)||(_taraDir==='DOWN'&&_isUP);
    // V13.4.283: read `taraCall.snapshot.posterior` -- a field NO snapshot builder
    //   has ever written. Snapshots store `atPosterior`. So this was always
    //   Number(undefined) -> NaN -> 50, and the damage was not the wrong label:
    //
    //     "Tara still UP 50%"                quoted a coin flip while the card
    //                                        showed her committed 67%
    //     if(_taraOpposed && _taraConf>=70)  NEVER TRUE -> the URGENT "Tara
    //                                        flipped against you" card has never
    //                                        fired, and it is the one wired to
    //                                        Discord
    //     if(_taraOpposed && _taraConf>=60)  NEVER TRUE -> "Tara disagrees but
    //                                        you're ahead" has never fired
    //
    //   Two alerts silently dead, one of them the reversal warning on an open
    //   position. Same shape as the Number(null)===0 sentinel bugs this file
    //   keeps producing: a fallback that looks like a safe default and is not.
    //   readLockState owns the confidence rule now, so use it; pre-commit there
    //   is no snapshot, so derive from the live object the same way it does.
    const _taraConf=(()=>{
      const _ls=readLockState(taraCall?.snapshot);
      if(_ls.committed&&_ls.confidence>0)return _ls.confidence;
      const _c=Number(taraCall?.conviction);
      if(Number.isFinite(_c)&&_c>0)return 50+_c;
      const _p=Number(taraCall?.confidence);
      if(Number.isFinite(_p)&&_p>0)return (_taraDir==='DOWN'&&_p<50)?(100-_p):_p;
      return 50;
    })();
    // Edge vs Kalshi
    const _kalshi=Number(kalshiYesPrice);
    // Offer awareness
    const _offerVal=parseFloat(currentOffer)||0;
    // Recent contrary whale check
    const _contrarySide=_isUP?'SELL':'BUY';
    const _last60sWhales=(Array.isArray(whaleLog)?whaleLog:[]).filter(w=>w&&w.time>_now-60000&&w.side===_contrarySide&&(w.usd||0)>=200000);
    const _bigContraryWhale=_last60sWhales.length>0;
    const _largestContraryUsd=_bigContraryWhale?(Number(_last60sWhales[0]?.usd)||0):0;
    // V13.4.131: live volume ratio + chop flag, feeding the drawdown-from-peak card
    //   below. Built from real trade-log analysis: of all LOSS trades that were correct
    //   for most of the post-lock window before reversing, 100% (25/25) were in
    //   RANGE-CHOP. Volume is tracked elsewhere in the app (spike detection, OBV,
    //   VPOC) but had never been connected to live in-trade risk warnings -- this is
    //   that connection. Same tick-size-sum style as the existing volume-spike scorer
    //   (~L7715), applied here to tickHistoryRef directly since that's what this
    //   component already receives as a prop.
    const _liveVolRatio=(function(){
      try{
        const _ticks=tickHistoryRef?.current;
        if(!Array.isArray(_ticks)||_ticks.length<10)return 1;
        const _recentTicks=_ticks.filter(t=>t&&t.time>_now-30000);
        const _baselineTicks=_ticks.filter(t=>t&&t.time>_now-300000&&t.time<=_now-30000);
        const _recentSum=_recentTicks.reduce((s,t)=>s+(Number(t.s)||0),0);
        const _baselineSum=_baselineTicks.reduce((s,t)=>s+(Number(t.s)||0),0);
        const _baselineAvgPer30s=_baselineSum>0?_baselineSum/9:0; // 270s of baseline / 30s windows
        return _baselineAvgPer30s>0?_recentSum/_baselineAvgPer30s:1;
      }catch(_e13431){return 1;}
    })();
    const _isChopRegimeLive=analysis?.regime==='RANGE-CHOP';
    const _thinVolInChop=_isChopRegimeLive&&_liveVolRatio<0.6;
    // Risk
    const _riskLevel=movementRisk?.level||'CALM';
    const _riskExtreme=_riskLevel==='EXTREME';
    const _riskElevated=_riskLevel==='ELEVATED';
    const _riskScore=Number(movementRisk?.score)||0;
    const _riskDirBias=movementRisk?.dirBias||null;
    const _riskPredictive=movementRisk?.predictive||'';

    // ── Build advisory cards in priority order. Most urgent first. ──
    const cards=[];

    // V9.1.7: ⚡ PRICE SPIKING AGAINST YOU — top priority. The moment Tara catches a
    //   real-time reversal, surface it BEFORE the user has to read gap-bps.
    if(_spikeAgainst&&_acceleratingAgainst){
      cards.push({tone:'urgent',icon:'⚡',title:`Price spiking ${_recentDir} · against you`,
        body:`Last 10s: ${_recent10sBps>0?'+':''}${Math.round(_recent10sBps)}bps move and accelerating. You're ${userPosition} — this is reversal velocity. ${_offerVal>0.30?'Cut at offer $'+_offerVal.toFixed(2)+' before more damage.':'Brace or exit — the move is fresh.'}`});
    } else if(_spikeAgainst){
      cards.push({tone:'urgent',icon:'⤵',title:`Sudden ${_recentDir} move · against you`,
        body:`Price moved ${_recent10sBps>0?'+':''}${Math.round(_recent10sBps)}bps in last 10s. ${_winning?'You\'re still ahead — tighten exit before you give it back.':'You\'re behind and the move is fresh — exit unless tape stabilizes.'}`});
    }

    // V9.1.7: 📉 DRAWDOWN FROM PEAK — caught winning, now bleeding back to entry.
    // V13.4.131: in thin-volume RANGE-CHOP -- the exact condition behind 100% of the
    //   'right till the end, reversed hard' losses -- fire this card earlier and more
    //   sensitively than the general case. Same underlying signal, lower bar to speak up.
    const _peakDDThresh=_thinVolInChop?10:15;
    const _drawdownThresh=_thinVolInChop?6:10;
    const _giveBackRatioThresh=_thinVolInChop?0.35:0.5;
    if(_peak>=_peakDDThresh&&_drawdownFromPeak>=_drawdownThresh&&_winning&&_drawdownFromPeak/Math.abs(_peak)>=_giveBackRatioThresh){
      cards.push({tone:'urgent',icon:'📉',title:`Drawing down from +${Math.round(_peak)}bps peak${_thinVolInChop?' — thin-volume chop':''}`,
        body:`You hit +${Math.round(_peak)}bps but you're now at +${Math.round(_favoredGap)}bps — gave back ${Math.round(_drawdownFromPeak)}bps.${_thinVolInChop?` This is RANGE-CHOP with volume at ${_liveVolRatio.toFixed(1)}x normal — exactly the condition behind most late reversals in the archive. Weight this warning more than usual.`:''} ${_offerVal>=0.65?'Offer $'+_offerVal.toFixed(2)+' is still strong — take it before more reversal.':'Watch the next 30s carefully — this is reversal territory.'}`});
      try{if(liveCoachReversalRef&&liveCoachReversalRef.current){var _lcr=liveCoachReversalRef.current;_lcr.fired=true;if(_peak>_lcr.peakBps)_lcr.peakBps=_peak;if(_drawdownFromPeak>_lcr.drawdownBps)_lcr.drawdownBps=_drawdownFromPeak;}}catch(_){}
    }

    // ⛔ EXIT NOW signals
    if(positionStatus?.isStopHit){
      cards.push({tone:'urgent',icon:'🛑',title:'Stop hit — exit now',body:`Position breached -30%. Statistical recovery rate from here is below break-even cost. Don't argue with the math.`});
    }
    if(_taraOpposed&&_taraConf>=70&&!_winning){
      cards.push({tone:'urgent',icon:'⚠',title:`Tara flipped to ${_taraDir} (${Math.round(_taraConf)}%)`,body:`Conditions inverted. Your read at entry is now contrary to model. Strongly consider exit unless you have specific reason to disagree.`});
    }
    if(_riskExtreme&&_riskDirBias&&_riskDirBias!==userPosition){
      // V13.4.169: `_riskPredictive` is a full sentence of its own, so interpolating it
      //   bare produced a run-on: "DOWN move likely next 30-60s You're on the wrong
      //   side of the surge." Terminate it before continuing.
      cards.push({tone:'urgent',icon:'⚡',title:'Extreme risk · contrary direction',
        body:[
          `Risk ${_riskScore}/100, leaning ${_riskDirBias} against your ${userPosition}.`,
          String(_riskPredictive||'').trim().replace(/[.\s]*$/,'')+(String(_riskPredictive||'').trim()?'.':''),
          `You are on the wrong side of the surge.`,
        ].filter(Boolean).join(' ')});
    }
    if(_bigContraryWhale&&_last90s){
      cards.push({tone:'urgent',icon:'🐋',title:'Late-window contrary whale',body:`$${Math.round(_largestContraryUsd/1000)}K ${_contrarySide} print just hit. Late whale spikes are the #1 cause of last-minute reversals. If you're up, take it; if you're flat or down, exit.`});
    }

    // V9.1.7: ⚡ ACCELERATING IN YOUR FAVOR — opposite of the danger spike, surface
    //   the upside too. "Price is moving for you, hold the next 30s aggressively."
    if(_spikeFor&&_acceleratingFor&&_winning){
      cards.push({tone:'good',icon:'🚀',title:`Price spiking ${_recentDir} · with you`,
        body:`Last 10s: ${_recent10sBps>0?'+':''}${Math.round(_recent10sBps)}bps and accelerating in your direction. Don't take profit yet — momentum hasn't peaked. Move stop to breakeven if you have one.`});
    }

    // ✓ TAKE PROFIT signals (V9.1.7: refined to use peak instead of just current gap)
    if(_offerVal>=0.85&&_secLeft>=120&&_winning){
      cards.push({tone:'good',icon:'💰',title:`Take-profit zone · offer $${_offerVal.toFixed(2)}`,body:`You've captured most of the available value with ${Math.floor(_secLeft/60)}m+ left. Marginal additional profit isn't worth reversal risk.`});
    }
    if(_winning&&Math.abs(_favoredGap)>=20&&_secLeft<=180){
      cards.push({tone:'good',icon:'✓',title:'Strong lead · cruise to close',body:`+${Math.round(_favoredGap)}bps favorable with ${Math.floor(_secLeft/60)}m left. Time decay now favors you. Hold unless STALE warning fires.`});
    }
    // V9.1.7: New peak hit — surface the moment it happens so user doesn't miss the high.
    if(_winning&&_favoredGap>=20&&_favoredGap===_peak&&_secLeft<=300){
      cards.push({tone:'good',icon:'⭐',title:`New high · +${Math.round(_favoredGap)}bps peak`,
        body:`You're at the highest point of this trade. ${_offerVal>=0.75?'Offer is $'+_offerVal.toFixed(2)+' — taking it now locks the win.':'Watch for first sign of stall (tape flips, opposing whale).'} If you give back more than half from here, exit.`});
    }

    // ⚠ WATCHFUL signals  
    if(_winning&&_riskElevated&&!_riskExtreme){
      cards.push({tone:'watch',icon:'👁',title:'Risk elevated while ahead',body:`Score ${_riskScore}/100. Reversal possible. ${_offerVal>=0.75?'Consider taking the offer at $'+_offerVal.toFixed(2)+'.':'Watch for whale prints + tape flip.'}`});
    }
    if(_taraOpposed&&_winning&&_taraConf>=60){
      cards.push({tone:'watch',icon:'⚡',title:'Tara disagrees but you\'re ahead',body:`Tara reading ${_taraDir} ${Math.round(_taraConf)}%. You're winning the moment but trend is shifting. Tighten exit — don't give it back.`});
    }
    if(_losing&&_taraAligned&&_midWindow){
      cards.push({tone:'watch',icon:'⏳',title:'Down but Tara still backs you',body:`-${Math.round(Math.abs(_favoredGap))}bps but Tara still ${userPosition} ${Math.round(_taraConf)}%. Mid-window dips are normal — hold unless gap exceeds 30bps adverse.`});
    }
    if(_losing&&_lateWindow&&Math.abs(_favoredGap)>=15){
      cards.push({tone:'watch',icon:'🕐',title:'Late-window comeback unlikely',body:`-${Math.round(Math.abs(_favoredGap))}bps with ${_secLeft}s left. ${_offerVal>0.30?'Cut at offer $'+_offerVal.toFixed(2)+' to recover something.':'Statistical recovery rate from here: ~25%.'}`});
    }
    if(_bigContraryWhale&&!_last90s){
      cards.push({tone:'watch',icon:'🐋',title:'Contrary whale early in window',body:`$${Math.round(_largestContraryUsd/1000)}K ${_contrarySide} hit. Watch tape — if more contrary whales stack, exit before late-window selloff.`});
    }
    // V9.1.7: REBOUND from trough — was losing, now coming back. Hold or scale up.
    if(_trough<=-15&&_reboundFromTrough>=10&&_favoredGap>=-5){
      cards.push({tone:'watch',icon:'↗',title:`Rebounding from -${Math.round(Math.abs(_trough))}bps trough`,
        body:`You were down ${Math.round(Math.abs(_trough))}bps and you're now ${_favoredGap>=0?'+':''}${Math.round(_favoredGap)}bps. ${_taraAligned?'Tara still aligned — let the rebound develop, don\'t exit at breakeven.':'Tara is opposed though — this rebound may be a fade. Take the recovery and exit.'}`});
    }

    // ℹ INFORMATIONAL signals
    if(_earlyWindow&&_taraAligned&&_winning){
      cards.push({tone:'info',icon:'→',title:'Early lead · let it develop',body:`+${Math.round(_favoredGap)}bps favorable in first third. Don't take profit prematurely — windows often expand. Watch for tape flips around midpoint.`});
    }
    if(_midWindow&&_taraAligned&&Math.abs(_favoredGap)<5){
      cards.push({tone:'info',icon:'≈',title:'Flat at midpoint · normal',body:`Around even with ${Math.floor(_secLeft/60)}m left. Mid-window flatness is common — direction usually picks up in last third. Hold.`});
    }
    if(_veryLate&&_winning&&_offerVal>=0.65){
      cards.push({tone:'info',icon:'⏱',title:'Last 45s · offer holding',body:`Offer $${_offerVal.toFixed(2)} with ${_secLeft}s left. Ride to close — time decay is now strongly in your favor.`});
    }

    // V13.4.270: the "Position open · all clear" filler is gone. It restated what
    //   THIS TRADE stage 2 now says in plainer words, and it forced this whole
    //   panel to be on screen permanently just to announce that nothing was wrong.
    //   With no filler, an empty cards array means exactly that -- nothing needs
    //   attention -- and the panel takes no space at all (see the null return
    //   below). What survives here is only the part THIS TRADE cannot cover:
    //   time-sensitive ALERTS, which is what this component was actually good at.

    // V9.9.0: Persistent reversal-risk card. Surfaces the risk flag throughout the
    //   trade so the user sees it not just at lock but all the way through. Inserted
    //   at the front of the cards array (highest priority spot) when EXPECTED, just
    //   below urgent cards when WATCH. NONE keeps the coach visually clean.
    if(reversalRisk&&reversalRisk.flag&&reversalRisk.flag!=='NONE'){
      const _isExp=reversalRisk.flag==='EXPECTED';
      const _topSig=(reversalRisk.signals||[]).filter(s=>s.fired).sort((a,b)=>b.weight-a.weight).slice(0,2);
      const _signalSummary=_topSig.length>0?_topSig.map(s=>s.reason).join(' · '):'No specific factors fired';
      const _reversalCard={
        tone:_isExp?'urgent':'watch',
        once:true, // V13.4.241: stands for the whole trade — fire one alert, not one per throttle window
        icon:_isExp?'⚠':'⚡',
        title:_isExp?`Reversal expected · entered with caution`:`Reversal watch · monitor closely`,
        body:`${_signalSummary}. Score ${reversalRisk.score}/100. ${_isExp?'Tighten exit before peak adverse — these patterns reverse mid-window historically.':'Watch for retracement after favorable move.'}`,
      };
      // Insert position: EXPECTED goes first (highest urgency); WATCH goes after any
      //   pre-existing urgent cards (price-spike, stop-hit) but before info/good cards.
      if(_isExp){cards.unshift(_reversalCard);}
      else {
        const _firstNonUrgent=cards.findIndex(c=>c.tone!=='urgent');
        if(_firstNonUrgent<0)cards.push(_reversalCard);
        else cards.splice(_firstNonUrgent,0,_reversalCard);
      }
    }

    // ── V13.4.240: TRAILING EXIT ALERT ──────────────────────────────────────
    // What the replay KILLED, so it does not get re-added:
    //   fixed take-profit — negative at 8 of 9 levels tested (70..95c). 99% of
    //     WINS touch 80c+ against 34% of losses, so a fixed TP truncates nearly
    //     every winner to spare a third of the losers, and books a second fee.
    //   fixed cut-loss    — negative at all 7 levels tested (10..40c).
    //   Both stay off. Do NOT revive them on the "94% of losses were ahead at
    //   some point" stat: winners are ahead too, so it does not separate them.
    //
    // HONEST LIMIT: the 95% bootstrap CI on the trailing rule is -261c to
    // +1030c — an 11% chance it is worth nothing. Shipped anyway because the
    // bad end is bounded (-0.73c/contract) and it targets the loss mode that is
    // actually costing money. Re-score once n is materially larger.
    //
    // Tara cannot place orders (engine removed in v13.4.75), so this ALERTS.
    const _tsVal=(Number.isFinite(_kalshi)&&_kalshi>0&&_kalshi<100)
      ?(_isUP?_kalshi:100-_kalshi):null;
    let _tsFired=false,_tsGiveback=0;
    if(_tsVal!=null&&_peakBpsRef.current.pos===userPosition){
      const _tr=_peakBpsRef.current;
      if(_tsVal>_tr.valPeak)_tr.valPeak=_tsVal;
      if(_tsVal>=TRAIL_ARM_C)_tr.valArmed=true;
      if(_tr.valArmed&&_tr.valPeak>0){
        _tsGiveback=_tr.valPeak-_tsVal;
        if(_tsGiveback>=TRAIL_GIVEBACK_C)_tsFired=true;
      }
    }
    if(_tsFired){
      cards.unshift({tone:'urgent',once:true,icon:'\u2935',title:'Trailing exit \u00b7 take it now',
        body:`Worth ${Math.round(_tsVal)}c, down ${Math.round(_tsGiveback)}c from its ${Math.round(_peakBpsRef.current.valPeak)}c high. `+
          `Sell the ${userPosition} contract. On 454 replayed calls, exiting here beat holding by 0.67c/contract. `+
          `${Math.floor(_secLeft/60)}m ${_secLeft%60}s left.`});
    }

    // V13.4.241: returns true if this card may fire on this channel, and marks
    //   it consumed. Has a side effect, so it must be the LAST term of any &&.
    const _onceOk=(_card,_chan)=>{
      if(!_card||!_card.once)return true;
      const _r=_oncePagedRef.current;
      if(_r.pos!==userPosition){_r.pos=userPosition;_r.keys={};}
      const _k=_chan+':'+_card.title;
      if(_r.keys[_k])return false;
      _r.keys[_k]=true;
      return true;
    };

    // V9.2.1: Sound trigger — beep when a NEW urgent card appears.
    //   Uses the first card's title as the dedup key. Only fires if >5s since last beep
    //   and the card title changed (avoids continuous beeping on the same condition).
    if(cards.length>0&&cards[0].tone==='urgent'){
      const _cardKey=cards[0].title;
      const _now2=Date.now();
      if((_cardKey!==_lastCoachBeepRef.current.key||_now2-_lastCoachBeepRef.current.at>10000)&&_onceOk(cards[0],'beep')){
        _lastCoachBeepRef.current={key:_cardKey,at:_now2};
        if(cards[0].title.includes('spiking')&&cards[0].title.includes('against')){
          _taraSpikeBeep(); // triple descending for spike-against
        }else{
          _taraUrgentBeep(); // double beep for other urgents
        }
      }
    }else if(cards.length>0&&cards[0].tone==='good'&&cards[0].title.includes('spiking')){
      const _cardKey=cards[0].title;
      const _now2=Date.now();
      if(_cardKey!==_lastCoachBeepRef.current.key||_now2-_lastCoachBeepRef.current.at>10000){
        _lastCoachBeepRef.current={key:_cardKey,at:_now2};
        _taraWinBeep(); // rising beep for favorable spike
      }
    }

    // Notification observation only; durable semantic dedup/caps live in the
    // sender. Numeric peak/title changes cannot bypass its cooldown. Do not
    // mirror the old imperative body or consume dashboard/sound once markers.
    const _discordCard=cards.find(c=>c.tone==='urgent'&&coachCondition(c.title));
    if(_discordCard&&typeof onUrgentCoachAlert==='function'){
      try{onUrgentCoachAlert({title:_discordCard.title,position:userPosition,
        gap:_favoredGap,peak:_peak,price:Number(currentPrice)||null,
        strike:Number(targetMargin)||null,observedAt:_now,trackingStartedAt:_peakBpsRef.current.openTime});}catch(_){}
    }

    // Color schemes per tone
    const _toneStyles={
      urgent:{border:'rgba(232,69,94,0.50)',bg:'rgba(232,69,94,0.07)',accent:'rgb(232,69,94)',pulse:true},
      good:  {border:'rgba(35,185,129,0.40)',bg:'rgba(35,185,129,0.05)',accent:'rgb(35,185,129)',pulse:false},
      watch: {border:'rgba(35,185,129,0.40)',bg:'rgba(35,185,129,0.05)',accent:'#23B981',pulse:false},
      info:  {border:'rgba(147,197,253,0.30)',bg:'rgba(147,197,253,0.04)',accent:'rgba(147,197,253,0.85)',pulse:false},
    };
    // V13.4.263: panel shape shared with the rest of the interface, and the
    //   border now carries the STATE of the trade rather than a fixed green:
    //   red while losing, green while winning, neutral while flat. The old
    //   green-always box looked like good news during a losing trade.
    const _frameTone=_winning?'rgba(35,185,129,0.34)':_losing?'rgba(232,69,94,0.34)':'rgba(237,237,237,0.14)';
    // V13.4.270: nothing to say and no live order -> render nothing. THIS TRADE
    //   owns the running commentary now; this panel earns its space only when it
    //   has a real alert, or an order strip with the kill switch on it. The cards
    //   array is still BUILT either way, above, so the Discord urgent-alert path
    //   is untouched by this.
    if(cards.length===0&&!autoOrderState)return null;
    return React.createElement('div',{
      className:'rounded-[10px] overflow-hidden',
      style:{border:'1px solid '+_frameTone,background:'#0A0A0E'},
    },
      React.createElement('div',{className:'px-3 sm:px-4 py-1.5 border-b border-[#24242E] flex items-baseline justify-between gap-2 flex-wrap'},
        React.createElement('div',{className:'flex items-baseline gap-2 flex-wrap'},
                    // V13.4.263: was '★ live trade coach', which read as its own product.
          //   It is the live read on the call rendered directly below it, so the
          //   label says that instead.
          // V13.4.270: 'live on this call' described the whole panel back when it
          //   carried the running numbers. THIS TRADE carries those now, so what is
          //   left here is alerts, and the label says that.
          React.createElement('span',{className:UI2_LABEL2},'alerts'),
          // V9.1.8: Market-character badges — surfaces windowAmplitude + regime so user
          //   sees what kind of market they're trading in. Tara already classifies these
          //   internally (and weights load per-regime); now they're visible at a glance.
          (()=>{
            const _wa=analysis?.windowAmplitude;
            const _waLabel=_wa?.label;
            if(!_waLabel||_waLabel==='OPENING')return null;
            // Plain-language map for trade-coach context. Some are positive, some warn.
            const _waMap={
              TRENDING:{plain:'going one way',color:'rgb(35,185,129)',hint:'Direction is set — ride the trend'},
              GRIND:{plain:'slow grind',color:'#23B981',hint:'Tight upward/downward drift, takes patience'},
              'LATE-BREAK':{plain:'late breakout',color:'rgb(35,185,129)',hint:'Direction picked up late in window'},
              WHIPSAW:{plain:'wild swings',color:'#E8455E',hint:'Multiple reversals — no clean side'},
              'SPIKE-FADE':{plain:'spike & fade',color:'#E8455E',hint:'Big early move, then drifted back'},
              RANGE:{plain:'sideways range',color:'rgba(147,197,253,0.85)',hint:'Bounces between high and low'},
              DEAD:{plain:'dead/quiet',color:'rgba(237,237,237,0.55)',hint:'Barely moving — small ranges only'},
              NORMAL:{plain:'normal',color:'rgba(237,237,237,0.7)',hint:'Mid-range activity'},
            };
            const _entry=_waMap[_waLabel]||{plain:_waLabel.toLowerCase(),color:'rgba(237,237,237,0.7)',hint:''};
            return React.createElement('span',{
              className:'text-[9px] uppercase tracking-[0.14em] font-bold px-1.5 py-0.5 rounded-lg',
              style:{color:_entry.color,background:'#0E0E12',border:'1px solid #24242E'},
              title:`${_waLabel} — ${_entry.hint}. Range: ${Math.round(_wa.rangeBps||0)}bps · ${_wa.directionChanges||0} reversals.`,
            },_entry.plain);
          })(),
          // Regime mini-pill
          analysis?.regime&&React.createElement('span',{
            className:'text-[9px] uppercase tracking-[0.14em] font-bold px-1.5 py-0.5 rounded-lg',
            style:{
              color:analysis.regime==='TRENDING UP'||analysis.regime==='SHORT SQUEEZE'?'rgb(35,185,129)':
                analysis.regime==='TRENDING DOWN'?'#E8455E':
                analysis.regime==='HIGH VOL CHOP'?'#23B981':
                'rgba(147,197,253,0.85)',
              background:'#0E0E12',
              border:'1px solid #24242E',
            },
            title:`Market regime: ${analysis.regime}`,
          },(()=>{
            const _r=analysis.regime;
            if(_r==='TRENDING UP')return 'pumping ▲';
            if(_r==='TRENDING DOWN')return 'dumping ▼';
            if(_r==='RANGE-CHOP')return 'choppy';
            if(_r==='SHORT SQUEEZE')return 'squeeze ▲';
            if(_r==='HIGH VOL CHOP')return 'wild';
            return _r.toLowerCase();
          })()),
        ),
        React.createElement('div',{className:'flex items-center gap-2 text-[10px] tabular-nums'},
          // V9.1.7: trajectory indicator in header — shows momentum direction with color
          _recentDir&&React.createElement('span',{
            style:{color:_trajFor?'rgb(35,185,129)':_trajAgainst?'#E8455E':'rgba(237,237,237,0.4)'},
            title:`Last 10s: ${_recent10sBps?Math.round(_recent10sBps):0}bps · ${_trajFor?'in your favor':_trajAgainst?'against you':'flat'}`,
          },_recentDir==='UP'?'▲':_recentDir==='DOWN'?'▼':'▬',_recent10sBps?Math.round(_recent10sBps):0,'bps/10s'),
          React.createElement('span',{style:{color:_winning?'rgb(35,185,129)':_losing?'#E8455E':'rgba(237,237,237,0.6)'}},
            userPosition,' · ',_winning?'+':'',Math.round(_favoredGap),'bps · ',Math.floor(_secLeft/60),'m ',String(_secLeft%60).padStart(2,'0'),'s',
          ),
        ),
      ),
      // V9.3.0: Kalshi auto-order status strip — shows when Tara placed an auto-order
      // for this lock. Compact: status pill, fill price, contracts, kill switch.
      autoOrderState&&React.createElement('div',{
        className:'px-3 sm:px-4 py-1.5 border-b border-[#24242E] flex items-baseline justify-between gap-2 flex-wrap text-[10px]',
        style:{background:autoOrderState.dryRun?'rgba(212,162,76,0.04)':'rgba(35,185,129,0.04)'},
      },
        React.createElement('div',{className:'flex items-baseline gap-2 flex-wrap'},
          React.createElement('span',{
            className:'uppercase tracking-[0.16em] font-bold px-1.5 py-0.5 rounded-lg',
            style:(()=>{
              const s=autoOrderState.status;
              if(s==='filled')return{color:'#23B981',border:'1px solid rgba(35,185,129,0.40)',background:'rgba(35,185,129,0.08)'};
              // V9.17.16: 'exited' is the final state — neutral gray, P&L color carries the meaning
              if(s==='exited')return{color:'rgba(237,237,237,0.75)',border:'1px solid rgba(237,237,237,0.25)',background:'rgba(237,237,237,0.04)'};
              if(s==='error'||s==='canceled')return{color:'#E8455E',border:'1px solid rgba(232,69,94,0.40)',background:'rgba(232,69,94,0.08)'};
              if(s==='exiting')return{color:'#23B981',border:'1px solid rgba(212,162,76,0.40)',background:'rgba(212,162,76,0.08)'};
              // V9.17.11: patient-waiting/skipped get neutral grey — not "active order" gold
              if(s==='patient-waiting')return{color:'rgba(196,181,253,0.95)',border:'1px solid rgba(196,181,253,0.35)',background:'rgba(124,93,250,0.08)'};
              if(s==='patient-skipped')return{color:'rgba(237,237,237,0.55)',border:'1px solid rgba(237,237,237,0.15)',background:'rgba(237,237,237,0.04)'};
              return{color:T2_GOLD,border:'1px solid rgba(35,185,129,0.40)',background:'rgba(35,185,129,0.06)'};
            })(),
          },(autoOrderState.dryRun?'DRY · ':'')+'KALSHI '+String(autoOrderState.status||'').toUpperCase()),
          // V9.17.16: enhanced order info display per user request.
          //   Active states (placing/resting/filled): show entry, current offer on our side, max payout
          //   Exit states (exiting/exited): show entry → exit, realized P&L
          //   Patient states: unchanged
          //   Error: unchanged
          (()=>{
            const _aos=autoOrderState;
            const _s=_aos.status;
            // Patient states first (no order yet)
            if(_s==='patient-waiting'){
              return React.createElement('span',{className:'text-[#EDEDED]/65'},
                _aos.dir,' · waiting for ≤',_aos.targetCents,'¢ (now ',_aos.currentCents,'¢)',
              );
            }
            if(_s==='patient-skipped'){
              return React.createElement('span',{className:'text-[#EDEDED]/65'},_aos.reason||'');
            }
            // V13.4.291: this gate asked for _aos.count and _aos.limitCents, two
            //   names the writer never sets. `undefined == null` is true under the
            //   loose equality used here, so BOTH halves were always true and this
            //   entire auto-exec status panel returned null on every render since it
            //   was written -- the exited / exiting / filled / resting branches below
            //   have never once been seen. Everything now reads through the order
            //   authority, which knows the real names and the legacy ones.
            const _o=readOrderState(_aos);
            const _oCount=_o.count||0;
            const _oEntry=_o.fillCents!=null?_o.fillCents:_o.limitCents;
            if(!_o.dir||_oEntry==null)return null;
            // V9.17.16: compute live values
            const _yes=Number(kalshiYesPrice);
            const _yesValid=Number.isFinite(_yes)&&_yes>0&&_yes<100;
            const _curOurSide=_yesValid?(_o.dir==='UP'?_yes:(100-_yes)):null;
            const _maxPayoutCents=_oCount*100; // each contract pays $1 on win
            const _maxPayoutDollars=(_maxPayoutCents/100).toFixed(2);
            const _entry=_oEntry;
            // EXITED — show realized P&L
            if(_s==='exited'&&_o.exitCents!=null&&_o.fillCents!=null){
              // exitCents is the RAW YES price; fillCents is our-side. The
              //   V13.4.290 inversion again -- convert before subtracting or a DOWN
              //   loss reads as a win.
              const _exitOurSide=_o.dir==='DOWN'?(100-_o.exitCents):_o.exitCents;
              const _profitCents=Number.isFinite(_o.pnlPerContract)
                ?_o.pnlPerContract:(_exitOurSide-_o.fillCents);
              const _profitDollars=(_profitCents*_oCount/100);
              const _isWin=_profitCents>0;
              const _isFlat=_profitCents===0;
              const _color=_isFlat?'text-[#EDEDED]/65':(_isWin?'text-emerald-400':'text-rose-400');
              const _sign=_profitCents>0?'+':'';
              return React.createElement('span',{className:'flex items-baseline gap-2 flex-wrap'},
                React.createElement('span',{className:'text-[#EDEDED]/65'},_o.dir,' · ',_oCount,' contracts'),
                React.createElement('span',{className:'text-[#EDEDED]/65'},'entry ',_o.fillCents,'¢ → exit ',Math.round(_o.dir==='DOWN'?(100-_o.exitCents):_o.exitCents),'¢'),
                React.createElement('span',{className:_color+' font-semibold tabular-nums'},
                  _isFlat?'FLAT · $0.00':(_isWin?'PROFIT':'LOSS')+' · '+_sign+'$'+Math.abs(_profitDollars).toFixed(2)+' ('+_sign+_profitCents+'¢/contract)',
                ),
              );
            }
            // EXITING — show exit reason and trigger price
            if(_s==='exiting'){
              return React.createElement('span',{className:'flex items-baseline gap-2 flex-wrap'},
                React.createElement('span',{className:'text-[#EDEDED]/65'},_o.dir,' · ',_oCount,' contracts @ ',_entry,'¢'),
                _aos.exitReason&&React.createElement('span',{className:'text-amber-400 text-[10px]'},'→ ',_aos.exitReason),
              );
            }
            // FILLED — show entry, current offer, unrealized P&L, max payout
            if(_s==='filled'){
              const _unrealCents=_yesValid?(_curOurSide-_entry):null;
              const _unrealDollars=_unrealCents!=null?(_unrealCents*_oCount/100):null;
              const _unrealColor=_unrealCents==null?'text-[#EDEDED]/55':(_unrealCents>0?'text-emerald-400':(_unrealCents<0?'text-rose-400':'text-[#EDEDED]/55'));
              const _unrealSign=_unrealCents!=null&&_unrealCents>0?'+':'';
              return React.createElement('span',{className:'flex items-baseline gap-2 flex-wrap tabular-nums'},
                React.createElement('span',{className:'text-[#EDEDED]/65'},_o.dir,' · ',_oCount,' @ ',_entry,'¢'),
                _yesValid&&React.createElement('span',{className:_unrealColor},
                  'now ',Math.round(_curOurSide),'¢ ',
                  _unrealCents!=null?'('+_unrealSign+_unrealCents+'¢ = '+_unrealSign+'$'+Math.abs(_unrealDollars).toFixed(2)+')':'',
                ),
                React.createElement('span',{className:'text-[#EDEDED]/45 text-[10px]'},'max $',_maxPayoutDollars),
              );
            }
            // V13.4.322: UNKNOWN — kalshiRunEntryLadder/_runExit could not
            //   confirm whether the order filled. Falling through to the
            //   PLACING fallback below would render "0 contracts @ Xc" --
            //   the exact misleading line reported live for a real fill that
            //   Tara had lost track of. This must never look like a normal,
            //   settled outcome.
            if(_s==='unknown'){
              return React.createElement('span',{className:'flex items-baseline gap-2 flex-wrap'},
                React.createElement('span',{className:'text-rose-400 font-bold'},'⚠ FILL STATUS UNKNOWN'),
                React.createElement('span',{className:'text-[#EDEDED]/65 text-[10px]'},_o.dir,' @ ',_entry,'¢ — check Kalshi directly'),
              );
            }
            // V13.4.351: reconciliation has verified this attempt did not fill.
            // Keep it visibly distinct from an unresolved order and never render
            // the misleading placing/0-contracts fallback for a terminal no-fill.
            if(_s==='no-fill'){
              return React.createElement('span',{className:'flex items-baseline gap-2 flex-wrap'},
                React.createElement('span',{className:'text-emerald-400 font-bold'},'NO FILL CONFIRMED'),
                React.createElement('span',{className:'text-[#EDEDED]/65 text-[10px]'},'Kalshi shows no open position'),
              );
            }
            // PLACING / SUBMITTED / RESTING / PARTIALLY_FILLED — order out, no fill yet
            return React.createElement('span',{className:'flex items-baseline gap-2 flex-wrap tabular-nums'},
              React.createElement('span',{className:'text-[#EDEDED]/65'},_o.dir,' · ',_oCount,' contracts @ ',_o.limitCents!=null?_o.limitCents:_entry,'¢'),
              _yesValid&&React.createElement('span',{className:'text-[#EDEDED]/45'},'(now ',Math.round(_curOurSide),'¢)'),
              React.createElement('span',{className:'text-[#EDEDED]/45 text-[10px]'},'max $',_maxPayoutDollars),
            );
          })(),
          // V13.4.322: was autoOrderState.error -- a field nothing ever
          //   writes (only .reason is set, in both _runEntry and _runExit's
          //   failure paths). This line has never rendered real text; it was
          //   always silently skipped, which is why the compact status pill
          //   showed a bare "KALSHI ERROR" label with no reason underneath it.
          autoOrderState.reason&&React.createElement('span',{className:'text-rose-400 text-[10px]',style:{wordBreak:'break-word'}},'⚠ ',String(autoOrderState.reason).slice(0,200)),
        ),
        // V9.17.23: clear-error button. Surfaces only when status='error' AND
        //   we have a clear handler. Lets the user dismiss the stuck error
        //   state without having to navigate to settings or hit kill switch.
        autoOrderState.status==='error'&&typeof onClearAutoOrder==='function'&&React.createElement('button',{
          onClick:onClearAutoOrder,
          className:'px-2 py-0.5 rounded-lg text-[9px] uppercase font-bold tracking-wider mr-1',
          style:{color:'#23B981',border:'1px solid rgba(35,185,129,0.40)',background:'rgba(35,185,129,0.06)'},
          title:'Dismiss the error state. Auto-exec will be eligible to fire on the next window.',
        },'✕ Clear'),
        onKillSwitch&&React.createElement('button',{
          onClick:onKillSwitch,
          className:'px-2 py-0.5 rounded-lg text-[9px] uppercase font-bold tracking-wider',
          style:killSwitchEngaged?{color:'#E8455E',background:'rgba(232,69,94,0.20)',border:'1px solid rgba(232,69,94,0.50)'}:{color:'#E8455E',border:'1px solid rgba(232,69,94,0.30)'},
        },killSwitchEngaged?'KILLED':'⛔ Kill'),
      ),
      React.createElement('div',{className:'p-2 sm:p-3 space-y-1.5'},
        cards.slice(0,4).map((card,i)=>{
          const s=_toneStyles[card.tone]||_toneStyles.info;
          return React.createElement('div',{
            key:i,
            className:'rounded-lg p-1.5 sm:p-2 flex gap-2'+(s.pulse?' animate-pulse':''),
            style:{border:'1px solid '+s.border,background:s.bg},
          },
            React.createElement('span',{className:'text-base shrink-0 leading-tight',style:{color:s.accent}},card.icon),
            React.createElement('div',{className:'min-w-0 flex-1'},
              React.createElement('div',{className:'text-[11px] sm:text-[12px] font-bold leading-tight mb-0.5',style:{color:s.accent}},card.title),
              React.createElement('div',{className:'text-[10px] sm:text-[11px] text-[#EDEDED]/75 leading-snug'},card.body),
            ),
          );
        }),
      ),
    );
  }catch(err){
    try{console.warn('[V8.7.1] LiveTradeCoach render failed; hiding card',err?.message);}catch(_){}
    return null;
  }
}

// ── V8.2: TRADING SETTINGS MODAL ────────────────────────────────────────────
// Configure bet size, win payout, anti-tilt cooldown, Discord alert filter,
// take-profit/cut-loss rules. All localStorage-only, per-device prefs.
function TradingSettingsModal({taraCallLog,open,onClose,settings,setSettings,kalshiCreds,saveKalshiCreds,autoExecSettings,setAutoExecSettings,killSwitchEngaged,setKillSwitchEngaged,kalshiPingState,setKalshiPingState,autoExecCooldownUntil,setAutoExecCooldownUntil,mission,setMission,regimeDirCalibration,scalperSettings,setScalperSettings,kalshiAgreeMode,setKalshiAgreeMode}){
  // V9.19.25: tap-to-reveal tooltip state. Each setting has a stable id; clicking
  //   the (?) icon opens its tooltip inline. Click again or click another to dismiss.
  const[_settingsTip,_setSettingsTip]=React.useState(null);
  const[_showKalshiSecrets,_setShowKalshiSecrets]=React.useState(false);
  // V10.4.1a: legacy presets hidden by default — clutter for choices that data
  //   shows aren't optimal in current market. Click to reveal if needed.
  const[_showLegacyPresets,_setShowLegacyPresets]=React.useState(false);
  if(!open)return null; // V13.4.120 FIX: moved below the two useState calls above (was violating Rules of Hooks -- hooks were skipped whenever the modal was closed)
  const _update=(k,v)=>setSettings(prev=>({...prev,[k]:v}));
  const _num=(s,fallback)=>{const n=Number(s);return Number.isFinite(n)?n:fallback;};
  // V9.19.25: helper to render a label with hover-tooltip (desktop, via title)
  //   + tap-tooltip (mobile, via inline expansion). Used by settings rows that
  //   need more explanation than a 1-liner hint can give.
  //   Usage: _labelTip('id','Label text','Detailed explanation')
  //   Returns a React element ready to drop in as a label.
  const _labelTip=(id,label,tip,extraStyle)=>{
    const _isOpen=_settingsTip===id;
    return React.createElement('div',{className:'flex items-baseline gap-1',style:extraStyle||{}},
      React.createElement('span',{className:'text-[10px] text-[#EDEDED]/65'},label),
      React.createElement('button',{
        type:'button',
        onClick:(e)=>{e.preventDefault();_setSettingsTip(_isOpen?null:id);},
        title:tip,
        className:'text-[9px] opacity-60 hover:opacity-100 cursor-pointer',
        style:{background:'none',border:'none',padding:0,color:'#23B981'},
      },'(?)'),
    );
  };
  // V9.19.25: render the expanded tip inline below the input (for tap users)
  const _tipBox=(id,tip)=>{
    if(_settingsTip!==id)return null;
    return React.createElement('div',{
      className:'mt-1 px-2 py-1.5 rounded-lg text-[10px] leading-relaxed',
      style:{background:'rgba(35,185,129,0.06)',border:'1px solid rgba(35,185,129,0.20)',color:'rgba(237,237,237,0.85)'},
    },tip);
  };
  return React.createElement('div',{
    className:'fixed inset-0 z-50 flex items-start sm:items-center justify-center p-2 sm:p-4 bg-black/70 backdrop-blur-sm overflow-y-auto',
    onClick:onClose,
  },
    React.createElement('div',{
      className:'bg-[#0A0A0E] border border-[#1B1B22] rounded-[10px] shadow-2xl max-w-2xl w-full p-5 sm:p-6 my-2 sm:my-8 max-h-[96vh] overflow-y-auto',
      onClick:(e)=>e.stopPropagation(),
      style:{boxShadow:'0 20px 60px rgba(0,0,0,0.6), inset 0 0 0 1px rgba(35,185,129,0.06)'},
    },
      React.createElement('div',{className:'flex justify-between items-baseline mb-4'},
        React.createElement('div',null,
          React.createElement('h2',{className:'text-lg font-serif text-white tracking-tight'},'Trading Settings'),
          React.createElement('p',{className:'text-[10px] uppercase tracking-wider text-[#EDEDED]/45 mt-0.5'},'Settings for this device'),
        ),
        React.createElement('button',{onClick:onClose,className:'text-[#EDEDED]/40 hover:text-white text-xl leading-none'},'×'),
      ),
      // V13.4.260: WHAT IS TRUE RIGHT NOW. Read from the live settings on every
      //   render -- never hardcoded -- so it cannot drift from behaviour the way
      //   the old "Daily caps" controls did (they described enforcement that did
      //   not exist; removed in v13.4.254).
      (()=>{
        // V13.4.308: sourced from the shared computeAutoExecReadout() helper
        //   (also used by ThisTradeCard's compact indicator) instead of a
        //   locally recomputed copy -- same formula, can't drift apart.
        const{armed:_armed,dry:_dry,killed:_killed,tone:_tone}=computeAutoExecReadout(autoExecSettings,killSwitchEngaged);
        const _trailOn=typeof TRAIL_ARM_C!=="undefined";
        const _fixedTp=Number(autoExecSettings?.autoExitOffer)>0;
        const _fixedSl=Number(autoExecSettings?.stopLossDeltaCents)>0;
        const _cap=Number(autoExecSettings?.maxBetPerTrade)||0;
        // headline sentence
        const _line=_killed?"Stopped. The kill switch is on and nothing will be placed."
          :!_armed?"Calling only. Tara picks a side; you place the order."
          :_dry?"Armed, but practising. Orders are simulated, not sent to Kalshi."
          :"Armed and live. Real orders are sent to Kalshi, within your daily limits.";
        const _chip=(text,tone)=>React.createElement("span",{
          key:text,
          className:"text-[11px] px-2.5 py-1 rounded-md border",
          style:{color:tone,borderColor:tone+"47",background:tone+"1F"},
        },text);
        const _chips=[];
        _chips.push(_chip(_killed?"Killed":_armed?"Armed":"Disarmed",_killed?"#E8455E":_armed?"#23B981":"#D4A03A"));
        if(_armed)_chips.push(_chip(_dry?"Practice":"Live",_dry?"#D4A03A":"#E8455E"));
        if(typeof NO_ENTRY_GATES!=="undefined"&&NO_ENTRY_GATES)_chips.push(_chip("No entry filter","#D4A03A"));
        if(typeof NO_SITOUT_MODE!=="undefined"&&NO_SITOUT_MODE)_chips.push(_chip("Never sits out","#D4A03A"));
        _chips.push(_chip("Daily cap checked against Kalshi","#D4A03A"));
        // one line on how it gets out, since that is the other half of behaviour
        // V13.4.292: this used to say "sells at 8c off its high..." whenever ANY
        //   fixed rule was set, even one that makes the trailing stop unreachable
        //   (a take-profit at or under TRAIL_ARM_C fires and closes the position
        //   before it can ever reach the arm point) -- claiming the trail is
        //   active in exactly the state where it cannot fire. Now says so plainly.
        const _tpKillsTrail=_fixedTp&&Number(autoExecSettings?.autoExitOffer)<=TRAIL_ARM_C;
        const _exit=_tpKillsTrail
          ?("Fixed take-profit at "+Number(autoExecSettings?.autoExitOffer)+"c fires before the "+TRAIL_ARM_C+"c trailing stop can ever arm.")
          :_trailOn
            ?("Sells at "+TRAIL_GIVEBACK_C+"c off its high once worth "+TRAIL_ARM_C+"c"+((_fixedTp||_fixedSl)?", plus a fixed rule you set.":", otherwise holds to the close."))
            :"Holds to the close.";
        return React.createElement("div",{
          className:"mb-4 rounded-[10px] border overflow-hidden",
          style:{borderColor:_tone+"38",background:"#0A0A0E"},
        },
          React.createElement("div",{
            className:"px-4 py-2.5 border-b flex items-baseline justify-between",
            style:{borderColor:"#16161c",background:_tone+"0D"},
          },
            React.createElement("span",{className:UI2_LABEL,style:{color:_tone+"AA"}},"Right now"),
            React.createElement("span",{className:UI2_LABEL},"Read this before changing anything"),
          ),
          React.createElement("div",{className:"px-4 py-3.5"},
            React.createElement("div",{className:"text-[15px] font-semibold tracking-[-0.01em]",style:{color:_tone}},_line),
            React.createElement("div",{className:"text-[12px] text-[#EDEDED]/55 mt-2 leading-relaxed"},_exit),
            React.createElement("div",{className:"flex flex-wrap gap-1.5 mt-3"},_chips),
            _cap>0&&React.createElement("div",{className:"text-[11px] text-[#EDEDED]/38 mt-2.5"},
              "Trades are trimmed to $"+_cap.toFixed(2)+" each, never refused for size."),
          ),
        );
      })(),
      // V13.4.307: simple auto-exec surface — take-profit dial, cut-loss dial,
      //   Mission Mode toggle, and (when Mission is off) flat dollar presets
      //   instead of the free-text max-bet field. Always visible, above the
      //   advanced accordion, per direct request ("extremely simple form").
      //   Reads/writes the exact same autoExecSettings/mission fields the
      //   advanced accordion below already does -- no separate state, so the
      //   two surfaces cannot drift out of sync with each other.
      React.createElement(AutoExecSimplePanel,{autoExecSettings,setAutoExecSettings,mission,setMission}),
      // Bet size + win payout
      //   V10.4.1a: REMOVED misleading "Net per win / Required WR for breakeven"
      //   calculation. That math doesn't reflect Kalshi binary mechanics — actual
      //   payouts depend on kalshiAtLock entry price, not a fixed 2:1 ratio.
      //   The fields stay (used for P&L stats display + Kelly sizing math) but
      //   the misleading subtitle is replaced with a clarifying note.
      //   V13.4.318: Bet size now auto-mirrors Max bet/trade (see the sync
      //   effect on autoExecSettings.maxBetPerTrade near its declaration) so
      //   moving a dollar preset updates this too instead of silently going
      //   stale. Still an editable, independent field for the rare case where
      //   real orders use entry-mode "contracts" (a variable dollar cost) and
      //   this stat needs a manual number instead.
      React.createElement('div',{className:'mb-4 p-3 rounded-lg bg-[#050508] border border-[#24242E]'},
        React.createElement('div',{className:'text-[9px] uppercase font-bold tracking-[0.14em] text-[#EDEDED]/50 mb-2'},'Position Sizing — Stats Display'),
        React.createElement('div',{className:'grid grid-cols-2 gap-3'},
          React.createElement('label',{className:'block'},
            React.createElement('div',{className:'text-[10px] text-[#EDEDED]/65 mb-1'},'Bet size ($)'),
            React.createElement('input',{
              type:'number',min:0.01,step:0.01,value:settings.betSize,
              onChange:(e)=>_update('betSize',_num(e.target.value,10)),
              className:'w-full bg-transparent border border-[#2A2A34] rounded-lg px-2 py-1 text-white text-sm tabular-nums focus:border-[#23B981] focus:outline-none',
            }),
            React.createElement('div',{className:'text-[9px] text-[#EDEDED]/40 mt-1'},
              `= $${(Number(settings.betSize)||0).toFixed(2)} per trade (for P&L tracking)`,
            ),
          ),
          React.createElement('label',{className:'block'},
            React.createElement('div',{className:'text-[10px] text-[#EDEDED]/65 mb-1'},'Win payout ($)'),
            React.createElement('input',{
              type:'number',min:0.01,step:0.01,value:settings.winPayout,
              onChange:(e)=>_update('winPayout',_num(e.target.value,8.5)),
              className:'w-full bg-transparent border border-[#2A2A34] rounded-lg px-2 py-1 text-white text-sm tabular-nums focus:border-[#23B981] focus:outline-none',
            }),
            React.createElement('div',{className:'text-[9px] text-[#EDEDED]/40 mt-1'},
              `= average net profit when you win (for stats display)`,
            ),
          ),
        ),
        React.createElement('div',{className:'mt-2 text-[10px] text-[#23B981]/70'},
          '✓ Bet size follows "Max bet/trade" in Risk Guardrails automatically. Win payout is an estimate — actual Kalshi payouts vary by entry price.',
        ),
      ),
      // V9.17.4: ADVANCED SETTINGS — collapsed by default. User said the modal
      //   felt too complex. Essential settings (position sizing, scalper advisor,
      //   cooldown indicator) stay above. Everything else (tier-1, speed dial,
      //   anti-tilt, discord, auto-exit suggestions, mission mode, full kalshi
      //   auto-exec config) hides behind a single click. Uses native <details>
      //   so no extra state needed and persists nothing across reloads.
      React.createElement('details',{
        id:'tara-advanced-settings', // V13.4.307: target for AutoExecSimplePanel's "expand Mission settings" link
        className:'mb-4 rounded-lg',
        style:{background:'rgba(0,0,0,0.20)',border:'1px solid #24242E'},
      },
        React.createElement('summary',{
          className:'px-4 py-3 cursor-pointer flex items-baseline justify-between select-none',
          style:{listStyle:'none'},
        },
          React.createElement('div',{className:'flex items-baseline gap-2'},
            React.createElement('span',{className:'text-[12px] font-bold text-[#EDEDED]/85',style:{letterSpacing:'0.02em',textTransform:'none'}},'advanced settings'),
            React.createElement('span',{className:'text-[10px] text-[#EDEDED]/45'},'tier-1, speed dial, anti-tilt, discord, auto-exec, mission'),
          ),
          React.createElement('span',{className:'text-[#EDEDED]/45 text-sm'},'▾'),
        ),
        React.createElement('div',{className:'px-3 pb-3 pt-1'},
      // V9.9.6: TIER-1 ONLY MODE — mechanically prevents marginal-tier locks
      React.createElement('div',{className:'mb-4 p-3 rounded-lg bg-[#050508] border border-[#24242E]'},
        React.createElement('label',{className:'flex items-baseline justify-between cursor-pointer gap-2'},
          React.createElement('div',{className:'min-w-0 flex-1'},
            React.createElement('div',{className:'text-[9px] uppercase font-bold tracking-[0.14em] text-[#EDEDED]/50 mb-0.5'},'Tier-1 only mode'),
            React.createElement('div',{className:'text-[10px] text-[#EDEDED]/55'},'Auto-skip single, time-cap-commit, no-go-data, and other marginal-tier locks'),
          ),
          React.createElement('input',{type:'checkbox',checked:settings.tier1OnlyMode,onChange:(e)=>_update('tier1OnlyMode',e.target.checked),className:'ml-2 shrink-0'}),
        ),
        settings.tier1OnlyMode&&React.createElement('div',{className:'mt-2 text-[10px] text-[#EDEDED]/55 leading-snug'},
          'When ON, Tara only commits on confluence / super-confluence / structural-led / tape-led / rising-confluence / patient / exceptional / strong+ tiers. All other locks convert to SIT_OUT.',
        ),
      ),
      // V10.9.7: Speed dial + auto-mode settings block REMOVED. The dial was a
      //   global timing multiplier; removing it means Tara always runs at the
      //   neutral baseline, letting the tier ladder / V10.9.0 agreement gate /
      //   V10.8.2 cushion history / V9.8.0 ULP drive timing from actual signals.
      // Anti-tilt cooldown — V10.4.1a: REMOVED, merged into auto-exec cooldown
      //   to eliminate duplicate cooldown systems. antiTiltEnabled/
      //   antiTiltStreakLen/antiTiltMinutes settings are kept in localStorage
      //   for backward compat but no longer wired to any active code path.
      // V13.4.317: the auto-exec cooldown it was merged into (Kalshi
      //   Auto-Execution > Cooldown after losses) was ITSELF removed this
      //   version -- cooldownLossStreak/cooldownMinutes drove
      //   autoExecCooldownUntil, which is compared against nothing anywhere
      //   in this file (same NO_RISK_RAILS finding as the other Risk
      //   guardrails removals). So there is currently no cooldown mechanism
      //   that blocks a new auto-exec order after a losing streak, from
      //   either system.
      React.createElement('div',{className:'mb-4 p-3 rounded-lg bg-[#0B0B0F] border border-[#24242E]'},
        React.createElement('div',{className:'text-[9px] uppercase font-bold tracking-[0.14em] text-[#EDEDED]/35 mb-1'},'Anti-tilt cooldown'),
        React.createElement('div',{className:'text-[10px] text-[#EDEDED]/45 leading-snug'},
          'Retired. Neither this nor the auto-exec cooldown it was merged into ever blocked a real order -- both were UI-only. Use the kill switch to pause manually after a losing streak.',
        ),
      ),
      // High-edge Discord filter
      React.createElement('div',{className:'mb-4 p-3 rounded-lg bg-[#050508] border border-[#24242E]'},
        React.createElement('label',{className:'flex items-baseline justify-between cursor-pointer'},
          React.createElement('div',null,
            React.createElement('div',{className:'text-[9px] uppercase font-bold tracking-[0.14em] text-[#EDEDED]/50 mb-0.5'},'Discord: high-edge alerts only'),
            React.createElement('div',{className:'text-[10px] text-[#EDEDED]/55'},'Filter LOCK alerts to high-edge calls vs Kalshi'),
          ),
          React.createElement('input',{type:'checkbox',checked:settings.highEdgeAlertOnly,onChange:(e)=>_update('highEdgeAlertOnly',e.target.checked),className:'ml-2'}),
        ),
        settings.highEdgeAlertOnly&&React.createElement('div',{className:'mt-3'},
          React.createElement('div',{className:'text-[10px] text-[#EDEDED]/65 mb-1'},'Min edge (pp vs Kalshi)'),
          React.createElement('input',{
            type:'number',min:5,max:40,step:1,value:settings.highEdgeMinPp,
            onChange:(e)=>_update('highEdgeMinPp',Math.max(5,Math.min(40,_num(e.target.value,15)))),
            className:'w-full bg-transparent border border-[#2A2A34] rounded-lg px-2 py-1 text-white text-sm tabular-nums focus:border-[#23B981] focus:outline-none',
          }),
          // V9.17.20: unit hint
          React.createElement('div',{className:'text-[9px] text-[#EDEDED]/40 mt-1'},
            `= ${settings.highEdgeMinPp||15} percentage points edge required (Tara confidence − Kalshi price)`,
          ),
        ),
      ),
      // Auto exit suggestions — V10.4.1a: REMOVED, merged into the real
      //   auto-exit layer (autoExitOffer / stopLossDeltaCents). The suggest
      //   layer was duplicating it with weaker math (suggested $0.19 exits
      //   on $0.84-expected positions per real-world test).
      //   takeProfitEnabled/Offer/cutLossEnabled/Minutes preserved in
      //   localStorage for backward compat, no longer surfaced.
      // V13.4.317: the duplicate "Exit thresholds" dials this pointed at
      //   inside Kalshi Auto-Execution > Risk guardrails were removed (same
      //   fields already live in Auto-exec — simple above) -- updated to
      //   point there instead of a heading that no longer exists.
      React.createElement('div',{className:'mb-4 p-3 rounded-lg bg-[#0B0B0F] border border-[#24242E]'},
        React.createElement('div',{className:'text-[9px] uppercase font-bold tracking-[0.14em] text-[#EDEDED]/35 mb-1'},'Auto exit suggestions'),
        React.createElement('div',{className:'text-[10px] text-[#EDEDED]/45 leading-snug'},
          'Moved into ',React.createElement('span',{style:{color:T2_GOLD,fontWeight:'bold'}},'Take-profit / Cut-loss'),' in Auto-exec — simple above. Single source of truth, no more duplicates.',
        ),
      ),
      // ── V9.7.0: MISSION MODE ──────────────────────────────────────────
      React.createElement('div',{className:'mb-4 p-3 rounded-lg border',style:{background:'rgba(35,185,129,0.04)',borderColor:'rgba(35,185,129,0.20)'}},
        React.createElement(MissionPanel,{mission,setMission,regimeDirCalibration,killSwitchEngaged,setKillSwitchEngaged,compact:false}),
      ),
      // ── V9.3.0: KALSHI AUTO-EXECUTION ─────────────────────────────────
      React.createElement('div',{className:'mb-4 p-3 rounded-lg border',style:{background:'rgba(35,185,129,0.04)',borderColor:'rgba(35,185,129,0.20)'}},
        React.createElement('div',{className:'flex items-baseline justify-between mb-2'},
          React.createElement('div',null,
            React.createElement('div',{className:'flex items-baseline gap-2'},
              React.createElement('div',{className:'text-[12px] font-bold tracking-[0.02em]',style:{color:T2_GOLD,textTransform:'none'}},'Kalshi Auto-Execution'),
              React.createElement('span',{className:'text-[9px] uppercase font-bold tracking-wider px-1.5 py-0.5 rounded-lg',style:{color:T2_GOLD,border:`1px solid ${T2_GOLD}55`,background:`${T2_GOLD}14`}},'live when dry run is off'),
            ),
            React.createElement('div',{className:'text-[10px] text-[#EDEDED]/45 mt-0.5'},'localStorage only · never synced'),
          ),
          // Live status pill
          (()=>{
            const _killed=!!killSwitchEngaged;
            const _on=!!autoExecSettings?.enabled&&!_killed;
            const _dry=!!autoExecSettings?.dryRun;
            const _label=_killed?'KILLED':_on?(_dry?'ARMED · DRY-RUN':'ARMED · LIVE'):'OFF';
            const _color=_killed||_on&&!_dry?'#E8455E':_on?'#D4A03A':'rgba(237,237,237,0.45)';
            return React.createElement('span',{className:'text-[10px] uppercase font-bold tracking-wider px-2 py-0.5 rounded-lg',style:{color:_color,border:`1px solid ${_color}`,background:`${_color}15`}},_label);
          })(),
        ),
        // V9.19.25: clear "what this is" section, so the user never confuses
        //   this with Tara's Advisor (scalper) below.
        React.createElement('p',{className:'text-[11px] text-[#EDEDED]/70 leading-relaxed mb-3'},
          React.createElement('strong',{style:{color:T2_GOLD}},'AutoTrade execution layer. '),
          'When Tara locks UP or DOWN, the engine places the order: simulated in dry run, real on Kalshi when dry run is off. Each real entry first reads your Kalshi account and is refused if your limits would be passed.',
        ),
        // ── V9.6.0: HOW-TO GUIDE ──────────────────────────────────────────
        // Collapsible step-by-step walkthrough. Closed by default to keep modal
        // compact; user can open when first setting up.
        React.createElement('details',{className:'mb-3 rounded-lg',style:{background:'rgba(35,185,129,0.04)',border:'1px solid rgba(35,185,129,0.16)'}},
          React.createElement('summary',{className:'px-2.5 py-2 cursor-pointer flex items-baseline justify-between gap-2 select-none',style:{color:'#23B981'}},
            React.createElement('span',{className:'text-[11px] uppercase font-bold tracking-[0.16em]'},'How to use auto-exec'),
            React.createElement('span',{className:'text-[10px] text-[#EDEDED]/45'},'tap to expand'),
          ),
          React.createElement('div',{className:'px-2.5 pb-3 pt-1 space-y-3 text-[11px] leading-relaxed text-[#EDEDED]/75'},
            // Step 1
            React.createElement('div',null,
              React.createElement('div',{className:'text-[10px] uppercase font-bold tracking-wider mb-1',style:{color:'#23B981'}},'1. Get Kalshi API credentials'),
              React.createElement('p',null,'Log into Kalshi → Account → API. Generate a new RSA key pair. Kalshi keeps the public key; you save the private key (PKCS#8 PEM format starting with ',React.createElement('code',{className:'text-[10px] bg-[#050508] px-1'},'-----BEGIN PRIVATE KEY-----'),'). Copy your Key ID (UUID).'),
            ),
            // Step 2
            React.createElement('div',null,
              React.createElement('div',{className:'text-[10px] uppercase font-bold tracking-wider mb-1',style:{color:'#23B981'}},'2. Paste credentials below'),
              React.createElement('p',null,'Both fields are stored in your browser only. Anthropic and Tara servers never see them. Test connection — expect a ',React.createElement('span',{className:'text-emerald-400'},'✓ balance $X.XX'),'. If you see ',React.createElement('span',{className:'text-rose-400'},'✗ http 401'),', your Vercel rewrite at ',React.createElement('code',{className:'text-[10px] bg-[#050508] px-1'},'/api/kalshi/*'),' is stripping custom headers — fix vercel.json before continuing.'),
            ),
            // Step 3
            React.createElement('div',null,
              React.createElement('div',{className:'text-[10px] uppercase font-bold tracking-wider mb-1',style:{color:'#23B981'}},'3. Configure guardrails'),
              React.createElement('p',null,'Default: $25 per trade — the one hard cap that actually blocks an order (see ',React.createElement('strong',{className:'text-white'},'Max bet / trade'),' in Risk guardrails below). Adjust to your size. The default is deliberately small — meant to verify the plumbing works end-to-end before you increase anything.'),
            ),
            // Step 4
            React.createElement('div',null,
              React.createElement('div',{className:'text-[10px] uppercase font-bold tracking-wider mb-1',style:{color:'#23B981'}},'4. Test in dry-run'),
              React.createElement('p',null,'Toggle ',React.createElement('strong',{className:'text-white'},'Auto-place orders on lock'),' ON, leave ',React.createElement('strong',{style:{color:'#23B981'}},'Dry-run'),' ON. Wait for Tara to lock a window. Verify the ',React.createElement('code',{className:'text-[10px] bg-[#050508] px-1'},'KALSHI · DRY · placing → resting → filled'),' status strip appears in Live Trade Coach. Confirm the trade lands in the call log with ',React.createElement('code',{className:'text-[10px] bg-[#050508] px-1'},'autoExec: true'),'.'),
            ),
            // Step 5
            React.createElement('div',null,
              React.createElement('div',{className:'text-[10px] uppercase font-bold tracking-wider mb-1',style:{color:'#23B981'}},'5. Go live carefully'),
              React.createElement('p',null,'Only after one full window working in dry-run, flip ',React.createElement('strong',{style:{color:'#23B981'}},'Dry-run'),' OFF. Use the smallest bet you can. Watch the next window completely — verify the order Tara placed matches what shows in your Kalshi UI. Only then increase size.'),
            ),
            // Step 6
            React.createElement('div',null,
              React.createElement('div',{className:'text-[10px] uppercase font-bold tracking-wider mb-1',style:{color:'#23B981'}},'6. Use the kill switch any time'),
              React.createElement('p',null,'Three places, all instant: top-bar pill (opens settings), Live Trade Coach status strip (always visible during a trade), and the big red button below. Engaged kill switch blocks new orders immediately and keeps existing positions where they are.'),
            ),
            // Step 7
            React.createElement('div',null,
              React.createElement('div',{className:'text-[10px] uppercase font-bold tracking-wider mb-1',style:{color:'#23B981'}},'7. Fine-tune execution'),
              React.createElement('p',null,'See ',React.createElement('strong',{className:'text-white'},'Smart entry ladder'),' below to rest limit orders instead of crossing the spread — measured worth +2-4¢/contract on fills. ',React.createElement('strong',{className:'text-white'},'Signal source'),' (Master toggles above) controls whether orders fire on Tara\'s settled call or the raw engine lock.'),
            ),
          ),
        ),
        // Kill switch — biggest button, top of section
        React.createElement('button',{
          onClick:()=>setKillSwitchEngaged(v=>!v),
          className:'w-full px-3 py-2 rounded-lg text-[11px] uppercase font-bold tracking-wider mb-3 transition-colors',
          style:killSwitchEngaged?{background:'rgba(232,69,94,0.20)',color:'#E8455E',border:'1px solid rgba(232,69,94,0.50)'}:{background:'rgba(232,69,94,0.06)',color:'#E8455E',border:'1px solid rgba(232,69,94,0.30)'},
        },killSwitchEngaged?'⛔ Kill switch ENGAGED — tap to release':'Engage kill switch'),
        // API credentials
        React.createElement('div',{className:'mb-3 p-2 rounded-lg bg-[#050508]'},
          React.createElement('div',{className:'flex items-center justify-between gap-2 mb-2'},
            React.createElement('div',{className:'text-[9px] uppercase font-bold tracking-[0.14em] text-[#EDEDED]/50'},'API credentials · stored on this device'),
            React.createElement('button',{type:'button',onClick:()=>_setShowKalshiSecrets(v=>!v),className:'text-[10px] uppercase tracking-wider border border-[#35403f] px-2 py-1 text-[#EDEDED]/70'},_showKalshiSecrets?'Hide credentials':'Reveal / edit')
          ),
          React.createElement('label',{className:'block mb-2'},
            React.createElement('div',{className:'text-[10px] text-[#EDEDED]/65 mb-1'},'Key ID'),
            React.createElement('input',{
              type:_showKalshiSecrets?'text':'password',value:kalshiCreds?.apiKeyId||'',spellCheck:false,autoComplete:'off',readOnly:!_showKalshiSecrets,
              onChange:(e)=>saveKalshiCreds({apiKeyId:e.target.value,privateKeyPem:kalshiCreds?.privateKeyPem||''}),
              className:'w-full bg-transparent border border-[#2A2A34] rounded-lg px-2 py-1 text-white text-[11px] tabular-nums focus:border-[#23B981] focus:outline-none font-mono',
              placeholder:'00000000-0000-0000-0000-000000000000',
            }),
          ),
          React.createElement('label',{className:'block'},
            React.createElement('div',{className:'text-[10px] text-[#EDEDED]/65 mb-1'},'Private key (PEM, PKCS#8 or PKCS#1)'),
            _showKalshiSecrets?React.createElement('textarea',{
              rows:4,value:kalshiCreds?.privateKeyPem||'',spellCheck:false,autoComplete:'off',
              onChange:(e)=>saveKalshiCreds({apiKeyId:kalshiCreds?.apiKeyId||'',privateKeyPem:e.target.value}),
              className:'w-full bg-transparent border border-[#2A2A34] rounded-lg px-2 py-1 text-white text-[10px] focus:border-[#23B981] focus:outline-none font-mono leading-tight',
              placeholder:'-----BEGIN PRIVATE KEY-----\nMIIE...\n-----END PRIVATE KEY-----',
            }):React.createElement('div',{className:'border border-[#2A2A34] px-2 py-2 text-[11px] text-[#EDEDED]/45'},kalshiCreds?.privateKeyPem?'Private key saved · hidden until revealed':'No private key saved'),
          ),
          React.createElement('div',{className:'flex items-baseline gap-2 mt-2'},
            React.createElement('button',{
              onClick:async()=>{
                setKalshiPingState({state:'pinging',msg:'',balance:null,at:Date.now()});
                try{
                  const r=await kalshiPing({apiKeyId:kalshiCreds?.apiKeyId,privateKeyPem:kalshiCreds?.privateKeyPem});
                  if(r.ok)setKalshiPingState({state:'ok',msg:`balance $${((Number(r.balance)||0)/100).toFixed(2)}`,balance:r.balance,at:Date.now()});
                  else setKalshiPingState({state:'fail',msg:r.reason||'unknown error',balance:null,at:Date.now()});
                }catch(e){setKalshiPingState({state:'fail',msg:e?.message||String(e),balance:null,at:Date.now()});}
              },
              disabled:!kalshiCreds?.apiKeyId||!kalshiCreds?.privateKeyPem||kalshiPingState?.state==='pinging',
              className:'px-2 py-1 rounded-lg text-[10px] uppercase font-bold tracking-wider',
              style:{background:'rgba(35,185,129,0.10)',color:'#23B981',border:'1px solid rgba(35,185,129,0.30)'},
            },kalshiPingState?.state==='pinging'?'Testing…':'Test connection'),
            kalshiPingState?.state==='ok'&&React.createElement('span',{className:'text-[10px] text-emerald-400'},'✓ ',kalshiPingState.msg),
            kalshiPingState?.state==='fail'&&React.createElement('span',{className:'text-[10px] text-rose-400'},'✗ ',kalshiPingState.msg),
          ),
          React.createElement('div',{className:'text-[9px] text-[#EDEDED]/45 mt-2 leading-relaxed'},
            'Stored in your browser only. Anthropic / Tara servers never see these values. Test calls /portfolio/balance.',
          ),
        ),
        // Safety release: the dry-run switch can retain a stored false value,
        // but _runEntry now blocks every new live order until account-wide
        // risk and attribution are verified. Existing live exits remain open.
        (()=>{
          const a=autoExecSettings||{};
          if(!a.enabled||a.dryRun)return null;
          return React.createElement('div',{className:'mb-3 p-3 rounded-lg',
            style:{background:'rgba(232,69,94,0.08)',border:'1px solid rgba(232,69,94,0.30)'}},
            React.createElement('div',{className:'text-[10px] uppercase tracking-[0.16em] font-bold mb-1.5',style:{color:'#E8455E'}},
              'LIVE · real orders on'),
            React.createElement('div',{className:'text-[11px] leading-relaxed',style:{color:'rgba(255,255,255,0.66)'}},
              'Real orders are sent to Kalshi. Before each entry Tara reads your whole account, and refuses the order if that read is incomplete or your daily loss, trades-per-day or per-window limits would be passed. Turn dry run back on any time to go back to simulated orders.'));
        })(),
        // V13.4.227: READINESS. A professional does not arm an algo without
        //   knowing its measured edge and its error bars, so the number goes
        //   directly above the switch that arms it. Computed from this account's
        //   own settled calls, priced the way they actually filled.
        (()=>{
          const r=_autoExecReadiness(taraCallLog);
          if(!r)return React.createElement('div',{className:'mb-3 p-3 rounded-lg text-[11px] leading-relaxed',
            style:{background:'rgba(212,160,58,0.07)',border:'1px solid rgba(212,160,58,0.28)',color:'rgba(255,255,255,0.66)'}},
            'Execution edge is not established. Fewer than 40 officially settled Calls have original, captured-at-lock evidence and a usable quote. Legacy-observed/backfilled rows are excluded from this readiness view. Keep new entries in dry-run and use the forward shadow study.');
          const col=r.verdict==='proven'?'#23B981':r.verdict==='losing'?'#E8455E':T2_SITOUT_FG;
          return React.createElement('div',{className:'mb-3 p-3 rounded-lg',
            style:{background:r.verdict==='proven'?'rgba(35,185,129,0.06)':r.verdict==='losing'?'rgba(232,69,94,0.07)':'rgba(212,160,58,0.07)',
                   border:`1px solid ${r.verdict==='proven'?'rgba(35,185,129,0.28)':r.verdict==='losing'?'rgba(232,69,94,0.30)':'rgba(212,160,58,0.28)'}`}},
            React.createElement('div',{className:'text-[10px] uppercase tracking-[0.16em] font-bold mb-1.5',style:{color:col}},
              r.verdict==='proven'?'historical Call estimate positive · executable edge unverified'
              :r.verdict==='losing'?'measured edge is negative · do not arm'
              :'edge not established · arm in dry-run only'),
            React.createElement('div',{className:'text-[11px] leading-relaxed',style:{color:'rgba(255,255,255,0.62)'}},
              `Your last ${r.n} settled calls ran `,
              React.createElement('b',{style:{color:col}},`${r.per>=0?'+':''}${r.per.toFixed(2)}c per contract`),
              ` (95% range ${(r.per-r.ci).toFixed(2)} to ${(r.per+r.ci).toFixed(2)}). `,
              `Win rate ${r.wr}% against a `,
              React.createElement('b',{style:{color:r.wr>=r.beTrue?'#23B981':'#E8455E'}},`${r.beTrue}% break-even`),
              ` — your average entry is ${r.be}c and the taker fee adds ${r.beTrue-r.be} more, so ${r.be}% is not flat, it is down the fee on every contract.`,
              r.verdict==='unproven'
                ? ` The range still spans zero, so nothing here proves an edge either way. Automating it does not make it profitable — it only makes it faster. Leave dry-run ON until the range clears zero.`
                : r.verdict==='losing'
                ? ` The whole Call-model range sits below zero. Keep this in simulation.`
                : ` The Call-model range clears zero in this sample, but actual executable edge is not verified.`),
            r.needed>0&&React.createElement('div',{className:'text-[10px] mt-1.5',style:{color:'rgba(255,255,255,0.40)'}},
              `At the current spread of results it takes roughly ${r.needed} settled calls for the range to clear zero, if the average holds.`),
            // V13.4.228: the two measurements that decide whether arming this is
            //   sane, and they are not about the model at all.
            React.createElement('div',{className:'mt-2 pt-2 text-[10px] leading-relaxed',
              style:{borderTop:'1px solid rgba(255,255,255,0.10)',color:'rgba(255,255,255,0.55)'}},
              React.createElement('div',{className:'mb-1'},
                React.createElement('b',{style:{color:r.makerPer>0?'#23B981':'rgba(255,255,255,0.8)'}},'Execution is the whole margin. '),
                `Crossing the spread these same calls run ${r.per.toFixed(2)}c. Resting a cent inside instead, where Kalshi charges no maker fee, they run `,
                React.createElement('b',{style:{color:r.makerPer>0?'#23B981':'#E8455E'}},`${r.makerPer>=0?'+':''}${r.makerPer.toFixed(2)}c`),
                ` — a ${(r.makerPer-r.per).toFixed(2)}c swing per contract, which is larger than the edge itself. If auto-exec is armed to take, it automates the losing version. Turn the entry ladder on so it rests first.`),
              r.fwdBps!=null&&React.createElement('div',{className:'mb-1'},
                React.createElement('b',{style:{color:T2_SITOUT_FG}},'Not a forward forecast. '),
                `From the moment of lock, price moved the called way only ${r.fwdGoodPct}% of the time, averaging ${r.fwdBps.toFixed(2)} bps. The win rate comes from the strike being fixed at the window open, so the binary banks the move that already happened. An instrument without a fixed strike — a perp, spot, anything marked from entry — captures only this part and would lose.`),
              // V13.4.229: which lane is actually worth arming.
              (()=>{
                const h=_hourlyLaneStats();
                if(!h)return null;
                const better=h.verdict==='proven'&&r.verdict!=='proven';
                return React.createElement('div',{className:'mt-1.5 pt-1.5',
                  style:{borderTop:'1px solid rgba(255,255,255,0.08)'}},
                  React.createElement('b',{style:{color:better?'#23B981':'rgba(255,255,255,0.8)'}},
                    better?'The hourly ladder is the proven lane, not this one. ':'Lane comparison. '),
                  `Hourly: ${h.n} settled, `,
                  React.createElement('b',{style:{color:h.per>0?'#23B981':'#E8455E'}},`${h.per>=0?'+':''}${h.per.toFixed(2)}c per contract`),
                  ` (95% ${(h.per-h.ci).toFixed(2)} to ${(h.per+h.ci).toFixed(2)}), ${h.wr}% against a ${h.be}% break-even. `,
                  `15-minute: ${r.n} settled, ${r.per>=0?'+':''}${r.per.toFixed(2)}c (95% ${(r.per-r.ci).toFixed(2)} to ${(r.per+r.ci).toFixed(2)}), ${r.wr}% against ${r.beTrue}%. `,
                  better?React.createElement('span',{style:{color:T2_SITOUT_FG}},
                    'The hourly wins because it enters cheap. Auto-exec’s window types cover 15m and 5m — 5m has no open markets at all, and the hourly is not on the list. As configured it would arm the unproven lane and skip the proven one.')
                  :null);
              })(),
            ),
          );
        })(),
        // Master toggles
        React.createElement('div',{className:'mb-3 p-2 rounded-lg bg-[#050508]'},
          React.createElement('label',{className:'flex items-baseline justify-between cursor-pointer mb-2'},
            React.createElement('div',null,
              React.createElement('div',{className:'text-[11px] font-bold text-white'},'Auto-place orders on lock'),
              React.createElement('div',{className:'text-[10px] text-[#EDEDED]/55'},'When Tara locks UP/DOWN, fire a Kalshi limit order'),
            ),
            React.createElement('input',{
              type:'checkbox',
              checked:!!autoExecSettings?.enabled,
              onChange:(e)=>{
                const _on=e.target.checked;
                setAutoExecSettings(prev=>({...prev,enabled:_on}));
                // V9.17.3: mutual exclusivity — enabling Tara auto-exec disables scalper auto-exec
                if(_on&&scalperSettings?.autoExecEnabled&&typeof setScalperSettings==='function'){
                  setScalperSettings(prev=>({...prev,autoExecEnabled:false}));
                }
              },
              className:'ml-2',
            }),
          ),
          // V13.4.316: signal source, genuinely wired into _runEntry this
          //   version (a prior toggle with this same choice existed in the
          //   old AUTO-EXEC card but only ever changed a display label --
          //   removed in v13.4.313 as dead). Consolidated here from a second,
          //   older control (a plain <select> in "Advanced entry filters")
          //   that already correctly documented the default -- V9.17.22:
          //   "snapshot" is the recommended default because it's the same
          //   signal your displayed win-rate is measured against; "lock" is
          //   legacy pre-V9.17.22 behavior. Both controls wrote the same
          //   autoExecSettings.signalSource field, so this is a real
          //   consolidation onto one control, not a behavior change -- the
          //   ONLY actual behavior change this version makes is that
          //   _runEntry finally reads this field at all.
          React.createElement('div',{className:'mt-2 pt-2 border-t border-[#16161c]'},
            React.createElement('div',{className:'text-[11px] font-bold text-white mb-0.5'},'Signal source'),
            React.createElement('div',{className:'text-[10px] text-[#EDEDED]/55 mb-1.5'},
              "Which of Tara's two signals a real order fires on. Tara's call (default) is the same signal your win-rate above is measured against. Engine lock fires earlier but is the older, legacy behavior — real results on it won't necessarily match that win-rate."),
            React.createElement('div',{className:'flex gap-0 rounded-lg overflow-hidden',style:{border:'1px solid #24242E'}},
              (()=>{
                const _src=autoExecSettings?.signalSource==='lock'?'lock':'snapshot';
                const _btnBase='flex-1 px-2 py-1.5 text-[10px] uppercase font-bold tracking-wider text-center transition-colors cursor-pointer';
                return[
                  React.createElement('button',{
                    key:'snapshot',
                    className:_btnBase,
                    style:_src==='snapshot'
                      ?{background:'rgba(35,185,129,0.16)',color:'rgb(35,185,129)',borderRight:'1px solid rgba(35,185,129,0.30)'}
                      :{background:'#0E0E12',color:'rgba(237,237,237,0.45)',borderRight:'1px solid #24242E'},
                    onClick:()=>setAutoExecSettings(prev=>({...prev,signalSource:'snapshot'})),
                    title:"Tara's call (default, recommended) — waits for her committed, publicly-shown call before firing. Same signal your win-rate record is measured against. Can commit later in the window (or not at all), so this may fire late or sit out windows engine lock would have traded.",
                  },"tara's call"),
                  React.createElement('button',{
                    key:'lock',
                    className:_btnBase,
                    style:_src==='lock'
                      ?{background:'rgba(35,185,129,0.16)',color:'rgb(35,185,129)'}
                      :{background:'#0E0E12',color:'rgba(237,237,237,0.45)'},
                    onClick:()=>setAutoExecSettings(prev=>({...prev,signalSource:'lock'})),
                    title:'Engine lock (legacy) — fires the instant the engine itself decides a side, earlier than Tara\'s call. Pre-V9.17.22 behavior. Can disagree with the settled call, so real results may not track your displayed win-rate.',
                  },'engine lock'),
                ];
              })(),
            ),
          ),
          // V9.17.3: mutual exclusivity hint
          !!autoExecSettings?.enabled&&!!scalperSettings?.enabled&&React.createElement('div',{className:'text-[9px] text-amber-400/70 italic mb-2 -mt-1'},
            'scalper auto-exec disabled — tara auto-exec takes precedence',
          ),
          React.createElement('label',{className:'flex items-baseline justify-between cursor-pointer'},
            React.createElement('div',null,
              React.createElement('div',{className:'text-[11px] font-bold',style:{color:'#23B981'}},'Dry-run mode'),
              React.createElement('div',{className:'text-[10px] text-[#EDEDED]/55'},'Simulates orders without hitting Kalshi. Turning this off does not bypass the current live-entry pause.'),
            ),
            React.createElement('input',{type:'checkbox',checked:!!autoExecSettings?.dryRun,onChange:(e)=>setAutoExecSettings(prev=>({...prev,dryRun:e.target.checked})),className:'ml-2'}),
          ),
        ),
        // V13.4.317: PROFILE PRESETS removed in full (Sniper/Hunter/Hawk/
        //   Bargain Hunter/Surgeon/Balanced/Volume -- ~400 lines, 7 buttons).
        //   Every differentiating field these buttons set is confirmed dead:
        //   minTier/minQualityScore/maxEdgePt (Advanced entry filters, removed
        //   above), tradeTimingMode (Decision routing, removed below),
        //   skipTimeCapCommit/skipMarginalCaution/patientEntry*/smartCashout*
        //   -- none of these gate or alter a real order (see removal notes at
        //   their own former sections). maxAutoTradesPerDay/maxDailyLoss/
        //   cooldownLossStreak/cooldownMinutes are compared against nothing
        //   anywhere in this file (see the file-level comment above
        //   NO_RISK_RAILS). maxAutoTradesPerWindow is hard-floored to 1
        //   regardless of input. So each preset's own confirm() dialog
        //   promised a specific win rate via a specific filter combination
        //   (e.g. Sniper: "confluence+ tier, qScore>=55, edge cap 6pt ->
        //   78%+ WR") that could never actually occur -- clicking any preset
        //   only ever really changed maxBetPerTrade, the entry-ladder fields
        //   (Hawk/Bargain Hunter), and the Kalshi-agree-live flag. Removed
        //   rather than fixed: rebuilding 7 presets' worth of real gating
        //   logic is a separate, much larger project than a settings-panel
        //   declutter. The two fields that ARE real (max bet, entry ladder)
        //   are still directly editable below / in Smart entry ladder.
        React.createElement('div',{className:'mb-3 p-2 rounded-lg bg-[#050508]'},
          React.createElement('div',{className:'text-[9px] uppercase font-bold tracking-[0.14em] text-[#EDEDED]/50 mb-1'},'Risk guardrails'),
          React.createElement('div',{className:'text-[9px] text-[#EDEDED]/40 mb-3 leading-relaxed'},'Hard discipline guards. Block or exit trades automatically — no opinions, just rules.'),
        // ── Position size & fills ─────────────────────────────────────────────
        //
          React.createElement('div',{className:'text-[9px] uppercase tracking-[0.10em] font-semibold text-[#EDEDED]/45 mb-1.5'},'Position size & fills'),
          React.createElement('div',{className:'mb-3'},
            React.createElement('label',{className:'block'},
              _labelTip('max-bet','Max bet / trade ($)','HARD WALL. Auto-exec will SIT OUT (not downsize) if the intended bet exceeds this. The single most important safety knob — it caps your worst-case loss on any one trade. Bypass: only super-confluence tier OR manual click can override (and even then it clamps to this cap). Lower = safer, smaller positions. Higher = more capital at risk per trade.'),
              React.createElement('input',{
                type:'number',min:1,max:500,step:1,value:autoExecSettings?.maxBetPerTrade||0,
                onChange:(e)=>setAutoExecSettings(prev=>({...prev,maxBetPerTrade:Math.max(1,Math.min(500,_num(e.target.value,25)))})),
                className:'w-full bg-transparent border border-[#2A2A34] rounded-lg px-2 py-1 text-white text-sm tabular-nums focus:border-[#23B981] focus:outline-none mt-1',
              }),
              React.createElement('div',{className:'text-[9px] text-[#EDEDED]/40 mt-1'},
                `= reject any single auto-trade larger than $${Number(autoExecSettings?.maxBetPerTrade||25).toFixed(0)} dollars`,
              ),
              _tipBox('max-bet','HARD WALL. Auto-exec will SIT OUT (not downsize) if the intended bet exceeds this. The single most important safety knob — it caps your worst-case loss on any one trade. Bypass: only super-confluence tier OR manual click can override (and even then it clamps to this cap). Lower = safer, smaller positions. Higher = more capital at risk per trade.'),
            ),
          ),
        // V13.4.317: DAILY CAPS (already removed in v13.4.254 -- see comment
        //   left in place below), PER-WINDOW CAP (hard-floored to 1, never
        //   user-configurable), the DUPLICATE Take-profit/Stop-loss dials here
        //   (identical fields already live in AutoExecSimplePanel), COOLDOWN
        //   AFTER LOSSES (cooldownLossStreak/cooldownMinutes drive
        //   autoExecCooldownUntil, which is compared against nothing --
        //   confirmed via the same NO_RISK_RAILS comment cited above), ENTRY
        //   FILTERS (maxEdgePt/skipTimeCapCommit -- decorative, same grep),
        //   and DECISION ROUTING (tradeTimingMode -- backed by never-called
        //   _setPhase4Decision/never-assigned _phase4DecisionRef) all removed.
        //   State left alone in every case.
        //
        // V13.4.254: the "Daily caps" section was removed from this modal.
        //   It rendered two inputs both labelled HARD CAP -- max trades/day and
        //   max daily loss -- describing enforcement that does not exist:
        //   maxAutoTradesPerDay and maxDailyLoss are compared against nothing
        //   anywhere in this file, and _runEntry references neither. A control
        //   that looks authoritative and enforces nothing is worse than no
        //   control, so the UI is gone. The settings keys are still hydrated,
        //   so wiring them later needs only the check, not the form.
        ),
        // V13.4.317: ADVANCED ENTRY FILTERS removed in full. Confirmed by grep,
        //   field by field: enabledAssets (BTC is hardcoded in _runEntry
        //   regardless), enabledWindowTypes, minTier, minQualityScore,
        //   minConviction (on autoExecSettings -- a same-named scalperSettings
        //   field IS real, but this is not that one), lockStabilitySec,
        //   skipMarginalCaution, blockUrgencyApplied -- every one of these is
        //   read only in UI display/hydration/presets, never inside _runEntry
        //   or its polling effect. State left alone.
        // ── V9.7.4: SMART ENTRY LADDER ────────────────────────────────────
        React.createElement('details',{className:'mb-3 rounded-lg',style:{background:'rgba(35,185,129,0.04)',border:'1px solid rgba(35,185,129,0.16)'}},
          React.createElement('summary',{className:'px-2.5 py-2 cursor-pointer flex items-baseline justify-between gap-2 select-none',style:{color:'#23B981'}},
            React.createElement('span',{className:'text-[11px] uppercase font-bold tracking-[0.16em]'},'Smart entry ladder'),
            React.createElement('span',{className:'text-[10px] text-[#EDEDED]/45'},'capture spread'),
          ),
          React.createElement('div',{className:'px-2.5 pb-3 pt-1 space-y-3'},
            React.createElement('div',{className:'text-[10px] text-[#EDEDED]/55 leading-relaxed'},
              'When enabled, posts limit BELOW current offer to capture the spread. If unfilled within the step time, cancels and re-posts at offer. After max steps, takes the market. Saves 1-2¢ per contract on most fills, occasionally misses fast-moving setups entirely. Disabled in dry-run.',
            ),
            React.createElement('label',{className:'flex items-baseline justify-between cursor-pointer px-2 py-1.5 rounded-lg',style:{background:'rgba(35,185,129,0.06)'}},
              React.createElement('div',null,
                React.createElement('div',{className:'text-[11px] font-bold text-white'},'Enable entry ladder'),
                React.createElement('div',{className:'text-[10px] text-[#EDEDED]/55'},'Off = current behaviour (place at offer + slippage, fills immediately)'),
              ),
              React.createElement('input',{type:'checkbox',checked:!!autoExecSettings?.entryLadderEnabled,onChange:(e)=>setAutoExecSettings(prev=>({...prev,entryLadderEnabled:e.target.checked})),className:'ml-2'}),
            ),
            autoExecSettings?.entryLadderEnabled&&React.createElement('div',{className:'grid grid-cols-3 gap-2'},
              React.createElement('label',{className:'block'},
                React.createElement('div',{className:'text-[10px] text-[#EDEDED]/65 mb-1'},'Undercut (¢)'),
                React.createElement('input',{
                  type:'number',min:1,max:20,step:1,value:autoExecSettings?.entryLadderUndercutCents||2,
                  onChange:(e)=>setAutoExecSettings(prev=>({...prev,entryLadderUndercutCents:Math.max(1,Math.min(20,_num(e.target.value,2)))})),
                  className:'w-full bg-transparent border border-[#2A2A34] rounded-lg px-2 py-1 text-white text-sm tabular-nums focus:border-[#23B981] focus:outline-none',
                }),
                // V9.17.20: unit hint
                React.createElement('div',{className:'text-[9px] text-[#EDEDED]/40 mt-1'},
                  `= ${autoExecSettings?.entryLadderUndercutCents||2}¢ below offer (save per contract)`,
                ),
              ),
              React.createElement('label',{className:'block'},
                React.createElement('div',{className:'text-[10px] text-[#EDEDED]/65 mb-1'},'Step time (s)'),
                React.createElement('input',{
                  type:'number',min:2,max:60,step:1,value:autoExecSettings?.entryLadderStepSec||8,
                  onChange:(e)=>setAutoExecSettings(prev=>({...prev,entryLadderStepSec:Math.max(2,Math.min(60,_num(e.target.value,8)))})),
                  className:'w-full bg-transparent border border-[#2A2A34] rounded-lg px-2 py-1 text-white text-sm tabular-nums focus:border-[#23B981] focus:outline-none',
                }),
                // V9.17.20: unit hint
                React.createElement('div',{className:'text-[9px] text-[#EDEDED]/40 mt-1'},
                  `= ${autoExecSettings?.entryLadderStepSec||8} seconds wait per ladder step`,
                ),
              ),
              React.createElement('label',{className:'block'},
                React.createElement('div',{className:'text-[10px] text-[#EDEDED]/65 mb-1'},'Max steps'),
                React.createElement('input',{
                  type:'number',min:1,max:5,step:1,value:autoExecSettings?.entryLadderMaxSteps||2,
                  onChange:(e)=>setAutoExecSettings(prev=>({...prev,entryLadderMaxSteps:Math.max(1,Math.min(5,_num(e.target.value,2)))})),
                  className:'w-full bg-transparent border border-[#2A2A34] rounded-lg px-2 py-1 text-white text-sm tabular-nums focus:border-[#23B981] focus:outline-none',
                }),
                // V9.17.20: unit hint
                React.createElement('div',{className:'text-[9px] text-[#EDEDED]/40 mt-1'},
                  `= ${autoExecSettings?.entryLadderMaxSteps||2} attempts then take market`,
                ),
              ),
            ),
            autoExecSettings?.entryLadderEnabled&&React.createElement('div',{className:'text-[9px] text-[#EDEDED]/45 leading-relaxed'},
              'Defaults: undercut by 2¢, wait 8s per step, max 2 steps then take market. Example: offer 60¢ → place at 58¢ for 8s → if unfilled, place at 60¢ for 8s → if unfilled, take market at 62¢ (60 + 2 slippage).',
            ),
          ),
        ),
        // V13.4.317: PATIENT ENTRY removed. Confirmed by grep: patientEntryEnabled/
        //   patientEntryMaxCents/patientEntryMaxWaitSec are read only in UI
        //   display, hydration, and preset-writing -- never inside _runEntry,
        //   kalshiRunEntryLadder, or planEntryLadder. State left alone.
        // ── V9.6.0: ADVANCED EXIT LOGIC ───────────────────────────────────
        React.createElement('details',{className:'mb-3 rounded-lg',style:{background:'rgba(232,69,94,0.04)',border:'1px solid rgba(232,69,94,0.16)'}},
          React.createElement('summary',{className:'px-2.5 py-2 cursor-pointer flex items-baseline justify-between gap-2 select-none',style:{color:'#E8455E'}},
            React.createElement('span',{className:'text-[11px] uppercase font-bold tracking-[0.16em]'},'Advanced exit logic'),
            React.createElement('span',{className:'text-[10px] text-[#EDEDED]/45'},'when to close'),
          ),
          React.createElement('div',{className:'px-2.5 pb-3 pt-1 space-y-3'},
            React.createElement('div',{className:'text-[10px] text-[#EDEDED]/55 leading-relaxed'},
              'Optional exit paths beyond the basic take-profit and stop-loss in Risk guardrails. Time exit closes before Kalshi settles.',
            ),
            // V10.2.5: Stop-loss moved to Risk guardrails > Exit thresholds. Was here.
            // Time exit
            React.createElement('label',{className:'block'},
              React.createElement('div',{className:'text-[10px] text-[#EDEDED]/65 mb-1'},'Time exit: close when fewer than N seconds left (0=disabled)'),
              React.createElement('input',{
                type:'number',min:0,max:300,step:5,value:autoExecSettings?.timeExitSecLeft||0,
                onChange:(e)=>setAutoExecSettings(prev=>({...prev,timeExitSecLeft:Math.max(0,Math.min(300,_num(e.target.value,0)))})),
                className:'w-full bg-transparent border border-[#2A2A34] rounded-lg px-2 py-1 text-white text-sm tabular-nums focus:border-[#23B981] focus:outline-none',
              }),
              // V9.17.20: unit hint
              React.createElement('div',{className:'text-[9px] text-[#EDEDED]/40 mt-1'},(()=>{
                const _s=autoExecSettings?.timeExitSecLeft||0;
                return _s===0?'= no time exit (position holds to Kalshi settlement)':`= close when ${_s}s remain in window (before Kalshi resolves)`;
              })()),
              React.createElement('div',{className:'text-[9px] text-[#EDEDED]/45 mt-1 leading-relaxed'},'Closes the position before Kalshi settles. Locks in whatever offer is currently on the book. Useful in volatile late-window action — try 30-60s.'),
            ),
          ),
        ),
        // V13.4.317: SMART EXITS and SMART CASHOUT removed. Confirmed by direct
        //   grep across the whole file: every field in both sections
        //   (smartExitsEnabled, smartExitReverseConviction, smartExitMinProfitCents,
        //   smartExitExtendOnStrength/Cents, smartCashoutEnabled and its 6
        //   threshold fields) was read ONLY inside ScalperAdvisorPanel's own
        //   descriptive "exits when" / "show monitoring" TEXT -- never inside
        //   _runExit or the real exit-check effect (trailing-stop/take-profit/
        //   stop-loss/time-exit, confirmed by reading that effect in full
        //   earlier this session). Both sections looked like real, detailed
        //   exit engines (specific thresholds, worked examples) but never fired
        //   a real order regardless of what was set here. State fields are left
        //   alone in case a future version wires them up for real; only the
        //   misleading UI is gone.
        // ── V9.6.0: POSITION SIZING ───────────────────────────────────────
        // V13.4.317: Sizing strategy (fixed/confidence/kelly), Low/High bet,
        //   and Kelly blend removed -- confirmed by grep that the only
        //   non-UI/non-hydration read of sizingMode/confidenceLowBet/
        //   confidenceHighBet/kellyBlend anywhere in the file is inside
        //   computeAutoExecSize's own definition, and computeAutoExecSize's
        //   one real call site was removed in v13.4.313 -- since then these
        //   fields have not affected even a preview, let alone a real order.
        //   Entry mode's "% of Kalshi balance" option removed the same way:
        //   the real sizing function (_resolveStakeDollars) only special-cases
        //   entryMode==='contracts'; 'percent' silently falls through to the
        //   flat max-bet cap, same as 'dollars', so the option never did what
        //   it claimed. Dollars and Contracts are the only two entry modes
        //   that produce genuinely different real behavior -- both kept.
        React.createElement('details',{className:'mb-3 rounded-lg',style:{background:'rgba(35,185,129,0.04)',border:'1px solid rgba(35,185,129,0.16)'}},
          React.createElement('summary',{className:'px-2.5 py-2 cursor-pointer flex items-baseline justify-between gap-2 select-none',style:{color:'#23B981'}},
            React.createElement('span',{className:'text-[11px] uppercase font-bold tracking-[0.16em]'},'Position sizing'),
            React.createElement('span',{className:'text-[10px] text-[#EDEDED]/45'},'how much to bet'),
          ),
          React.createElement('div',{className:'px-2.5 pb-3 pt-1 space-y-3'},
            React.createElement('div',null,
              _labelTip('entry-mode','Entry mode','How the bet amount is EXPRESSED. Dollar cost (default) uses Max bet/trade above. Fixed contract count always buys exactly N contracts regardless of price -- simple, but the dollar cost varies with the Kalshi offer. Max bet/trade still caps the total either way.',{textTransform:'uppercase',letterSpacing:'0.05em',fontWeight:'bold',color:'rgba(237,237,237,0.60)'}),
              React.createElement('select',{
                value:autoExecSettings?.entryMode==='contracts'?'contracts':'dollars',
                onChange:(e)=>setAutoExecSettings(prev=>({...prev,entryMode:e.target.value})),
                className:'w-full bg-transparent border border-[#2A2A34] rounded-lg px-2 py-1.5 text-white text-sm focus:border-[#23B981] focus:outline-none mb-2 mt-1',
                style:{background:'#121218',color:'#EDEDED'},
              },
                React.createElement('option',{value:'dollars',style:{background:'#121218',color:'#EDEDED'}},'Dollar cost (uses Max bet/trade above)'),
                React.createElement('option',{value:'contracts',style:{background:'#121218',color:'#EDEDED'}},'Fixed contract count'),
              ),
              _tipBox('entry-mode','How the bet amount is EXPRESSED. Dollar cost (default) uses Max bet/trade above. Fixed contract count always buys exactly N contracts regardless of price -- simple, but the dollar cost varies with the Kalshi offer. Max bet/trade still caps the total either way.'),
              autoExecSettings?.entryMode==='contracts'&&React.createElement('label',{className:'block mb-1'},
                _labelTip('entry-contracts','Contracts per trade','Number of Kalshi contracts to buy each trade. Each contract pays $1 at settlement if right, $0 if wrong. Cost varies with current offer price — e.g., 5 contracts at 60¢ offer = $3.00 total cost. Max bet/trade still caps the total — if 5 contracts × current price exceeds your cap, auto-exec sits out.'),
                React.createElement('input',{
                  type:'number',min:1,max:500,step:1,value:Number(autoExecSettings?.entryContracts)||5,
                  onChange:(e)=>setAutoExecSettings(prev=>({...prev,entryContracts:Math.max(1,Math.min(500,_num(e.target.value,5)))})),
                  className:'w-full bg-transparent border border-[#2A2A34] rounded-lg px-2 py-1 text-white text-sm tabular-nums focus:border-[#23B981] focus:outline-none mt-1',
                }),
                React.createElement('div',{className:'text-[9px] text-[#EDEDED]/40 mt-1'},
                  `= ${Number(autoExecSettings?.entryContracts)||5} contracts × current cost-per-contract ¢ = total $`,
                ),
                _tipBox('entry-contracts','Number of Kalshi contracts to buy each trade. Each contract pays $1 at settlement if right, $0 if wrong. Cost varies with current offer price — e.g., 5 contracts at 60¢ offer = $3.00 total cost. Max bet/trade still caps the total — if 5 contracts × current price exceeds your cap, auto-exec sits out.'),
              ),
            ),
            React.createElement('div',{className:'text-[9px] text-[#EDEDED]/45 leading-relaxed'},
              React.createElement('strong',{className:'text-[#EDEDED]/70'},'Note:'),' Max bet/trade above always wins as the ceiling, in either mode.',
            ),
          ),
        ),
        // V9.17.3: Local P&L tracking removed. Only cooldown indicator shown.
        //   Auto-exec daily-loss cap previously enforced via dollar P&L is gone;
        //   loss-streak cooldown (3 losses → 20min pause) is the remaining guard.
        //   For real money tracking, kalshi portfolio integration is planned.
        (autoExecCooldownUntil>Date.now())&&React.createElement('div',{className:'p-3 rounded-lg bg-rose-500/5 border border-rose-500/30 flex items-baseline justify-between'},
          React.createElement('div',{className:'text-[11px] text-rose-400/85'},
            React.createElement('span',{className:'font-bold'},'Cooldown active · '),
            'auto-exec paused after losing streak',
          ),
          React.createElement('button',{
            onClick:()=>setAutoExecCooldownUntil(0),
            className:'text-[10px] uppercase font-bold tracking-wider px-2 py-1 rounded-lg',
            style:{color:'#E8455E',border:'1px solid rgba(232,69,94,0.30)'},
          },`Clear (${Math.ceil((autoExecCooldownUntil-Date.now())/60000)}m)`),
        ),
      ),
        ), // end advanced details inner div
      ), // end advanced details element
      // ═══════════════════════════════════════════════════════════════════════
      // V9.17.2: SCALPER ADVISOR SETTINGS — redesigned, advisory-only
      // ═══════════════════════════════════════════════════════════════════════
      // Auto-exec removed. Clean grouped layout: master toggle, then 3 sub-groups
      // (entry/exit, risk caps, cooldowns). Each group has a clear header and
      // generous spacing. The bracketed note frames it as a separate tool that
      // doesn't fight Tara's auto-exec.
      // V13.4.317: was an always-visible <section>, unconditionally rendered
      //   regardless of whether the auto-exec "advanced settings" details above
      //   it was open or closed -- a major contributor to the modal feeling
      //   cluttered on open. Now a closed-by-default <details>, same as every
      //   other advanced sub-panel in this modal.
      scalperSettings&&typeof setScalperSettings==='function'&&React.createElement('details',{
        className:'pt-4 mt-4 border-t border-[#24242E]',
      },
        React.createElement('summary',{className:'cursor-pointer select-none flex items-baseline justify-between gap-2 pb-2'},
          React.createElement('span',{className:'text-[11px] uppercase font-bold tracking-[0.16em]',style:{color:'#C4B5FD'}},'Tara\'s Advisor (scalper)'),
          React.createElement('span',{className:'text-[10px] text-[#EDEDED]/45'},'advisory only · tap to expand'),
        ),
        // V9.19.25: section header redesigned to make this VISUALLY DISTINCT from
        //   Kalshi Auto-Execution. Both used gold before, which made them feel like
        //   the same system. They are not — auto-exec places real orders, this is
        //   advisory only. Now violet/purple identity + clear "advisory only" label.
        React.createElement('div',{className:'p-3 rounded-lg mb-3',style:{background:'rgba(196,181,253,0.05)',border:'1px solid rgba(196,181,253,0.22)'}},
          React.createElement('div',{className:'flex items-baseline justify-between mb-1'},
            React.createElement('h3',{className:'text-[12px] font-bold',style:{color:'#C4B5FD',letterSpacing:'0.02em',textTransform:'none'}},'Tara\'s Advisor (scalper)'),
            React.createElement('span',{className:'text-[9px] uppercase font-bold tracking-wider px-1.5 py-0.5 rounded-lg',style:{color:'#C4B5FD',border:'1px solid rgba(196,181,253,0.40)',background:'rgba(196,181,253,0.08)'}},'advisory only'),
          ),
          React.createElement('p',{className:'text-[11px] text-[#EDEDED]/70 leading-relaxed mt-1'},
            React.createElement('strong',{style:{color:'#C4B5FD'}},'Never places orders. '),
            'Short-horizon kalshi cents advisor that reads tape + flow + book pressure for 30-90s entries. When conditions align, suggestions appear in the left column. You execute manually on Kalshi.',
          ),
          React.createElement('p',{className:'text-[10px] text-[#EDEDED]/45 leading-relaxed mt-2 italic'},
            'Distinct from ',React.createElement('span',{style:{color:T2_GOLD}},'Kalshi Auto-Execution'),' above — that is the order engine. Scalper is a separate advisory engine.',
          ),
        ),
        // MASTER ENABLE — prominent toggle
        React.createElement('div',{className:'mb-3 p-3 rounded-lg',style:{background:'rgba(196,181,253,0.05)',border:'1px solid rgba(196,181,253,0.18)'}},
          React.createElement('label',{className:'flex items-baseline justify-between cursor-pointer'},
            React.createElement('div',{className:'flex flex-col'},
              React.createElement('span',{className:'text-[12px] font-bold text-[#EDEDED]',style:{letterSpacing:'0.02em',textTransform:'none'}},'Enable scalper advisor'),
              React.createElement('span',{className:'text-[10px] text-[#EDEDED]/50 mt-0.5'},
                scalperSettings.enabled?'live · suggestions will appear in left column when conditions align':'off · no suggestions will fire',
              ),
            ),
            React.createElement('input',{
              type:'checkbox',
              checked:!!scalperSettings.enabled,
              onChange:(e)=>setScalperSettings(prev=>({...prev,enabled:e.target.checked})),
              className:'ml-2',
            }),
          ),
        ),
        // V10.4.1a: scalper config wrapped in collapsible — when scalper is enabled,
        //   the config sections (entry, exit, risk caps, cooldowns, execution) take
        //   ~400px of vertical space. Most users set once and forget; collapsing
        //   keeps the settings modal focused on what's actively used.
        scalperSettings.enabled&&React.createElement('details',{className:'mb-3 rounded-lg',style:{background:'rgba(196,181,253,0.03)',border:'1px solid rgba(196,181,253,0.14)'}},
          React.createElement('summary',{className:'px-3 py-2 cursor-pointer flex items-baseline justify-between select-none',style:{listStyle:'none'}},
            React.createElement('span',{className:'text-[11px] font-bold',style:{color:'#C4B5FD',letterSpacing:'0.02em',textTransform:'none'}},'Scalper config'),
            React.createElement('span',{className:'text-[10px] text-[#EDEDED]/45'},'entry · exit · risk · cooldowns ▾'),
          ),
          React.createElement('div',{className:'px-3 pb-3 pt-1'},
        scalperSettings.enabled&&React.createElement(React.Fragment,null,
          // ── GROUP 1: ENTRY & EXIT (Kalshi cents) ─────────────────────────
          React.createElement('div',{className:'mb-3 p-3 rounded-lg',style:{background:'rgba(0,0,0,0.25)',border:'1px solid #24242E'}},
            React.createElement('div',{className:'flex items-baseline justify-between mb-2.5'},
              React.createElement('span',{className:'text-[10px] text-[#EDEDED]/55 font-bold',style:{letterSpacing:'0.02em',textTransform:'none'}},'entry & exit'),
              React.createElement('span',{className:'text-[9px] text-[#EDEDED]/35',style:{letterSpacing:'0.02em'}},'kalshi cents'),
            ),
            React.createElement('div',{className:'grid grid-cols-2 gap-2 mb-2'},
              React.createElement('label',{className:'flex flex-col'},
                React.createElement('span',{className:'text-[10px] text-[#EDEDED]/55 mb-1',style:{letterSpacing:'0.02em'}},'entry zone min ¢'),
                React.createElement('input',{
                  type:'number',min:1,max:99,step:1,value:scalperSettings.entryMinCents||15,
                  onChange:(e)=>setScalperSettings(prev=>({...prev,entryMinCents:parseInt(e.target.value,10)||15})),
                  className:'px-2 py-1 rounded-lg text-[12px] tabular-nums',
                  style:{background:'rgba(0,0,0,0.40)',border:'1px solid #24242E',color:'#EDEDED',fontFamily:'IBM Plex Mono,ui-monospace,monospace'},
                }),
              ),
              React.createElement('label',{className:'flex flex-col'},
                React.createElement('span',{className:'text-[10px] text-[#EDEDED]/55 mb-1',style:{letterSpacing:'0.02em'}},'entry zone max ¢'),
                React.createElement('input',{
                  type:'number',min:1,max:99,step:1,value:scalperSettings.entryMaxCents||45,
                  onChange:(e)=>setScalperSettings(prev=>({...prev,entryMaxCents:parseInt(e.target.value,10)||45})),
                  className:'px-2 py-1 rounded-lg text-[12px] tabular-nums',
                  style:{background:'rgba(0,0,0,0.40)',border:'1px solid #24242E',color:'#EDEDED',fontFamily:'IBM Plex Mono,ui-monospace,monospace'},
                }),
              ),
            ),
            React.createElement('div',{className:'grid grid-cols-2 gap-2'},
              React.createElement('label',{className:'flex flex-col'},
                React.createElement('span',{className:'text-[10px] text-[#EDEDED]/55 mb-1',style:{letterSpacing:'0.02em'}},'target +¢'),
                React.createElement('input',{
                  type:'number',min:5,max:80,step:1,value:scalperSettings.targetCents||35,
                  onChange:(e)=>setScalperSettings(prev=>({...prev,targetCents:parseInt(e.target.value,10)||35})),
                  className:'px-2 py-1 rounded-lg text-[12px] tabular-nums',
                  style:{background:'rgba(0,0,0,0.40)',border:'1px solid #24242E',color:'#EDEDED',fontFamily:'IBM Plex Mono,ui-monospace,monospace'},
                }),
              ),
              React.createElement('label',{className:'flex flex-col'},
                React.createElement('span',{className:'text-[10px] text-[#EDEDED]/55 mb-1',style:{letterSpacing:'0.02em'}},'stop -¢'),
                React.createElement('input',{
                  type:'number',min:5,max:50,step:1,value:scalperSettings.stopCents||15,
                  onChange:(e)=>setScalperSettings(prev=>({...prev,stopCents:parseInt(e.target.value,10)||15})),
                  className:'px-2 py-1 rounded-lg text-[12px] tabular-nums',
                  style:{background:'rgba(0,0,0,0.40)',border:'1px solid #24242E',color:'#EDEDED',fontFamily:'IBM Plex Mono,ui-monospace,monospace'},
                }),
              ),
            ),
          ),
          // ── GROUP 2: RISK CAPS ───────────────────────────────────────────
          React.createElement('div',{className:'mb-3 p-3 rounded-lg',style:{background:'rgba(0,0,0,0.25)',border:'1px solid #24242E'}},
            React.createElement('div',{className:'flex items-baseline justify-between mb-2.5'},
              React.createElement('span',{className:'text-[10px] text-[#EDEDED]/55 font-bold',style:{letterSpacing:'0.02em',textTransform:'none'}},'risk caps'),
              React.createElement('span',{className:'text-[9px] text-[#EDEDED]/35',style:{letterSpacing:'0.02em'}},'discipline guards'),
            ),
            React.createElement('div',{className:'grid grid-cols-2 gap-2 mb-2'},
              React.createElement('label',{className:'flex flex-col'},
                React.createElement('span',{className:'text-[10px] text-[#EDEDED]/55 mb-1',style:{letterSpacing:'0.02em'}},'contracts / scalp'),
                React.createElement('input',{
                  type:'number',min:1,max:1000,step:1,value:scalperSettings.contractsPerScalp||1,
                  onChange:(e)=>setScalperSettings(prev=>({...prev,contractsPerScalp:parseInt(e.target.value,10)||1})),
                  className:'px-2 py-1 rounded-lg text-[12px] tabular-nums',
                  style:{background:'rgba(0,0,0,0.40)',border:'1px solid #24242E',color:'#EDEDED',fontFamily:'IBM Plex Mono,ui-monospace,monospace'},
                }),
              ),
              React.createElement('label',{className:'flex flex-col'},
                React.createElement('span',{className:'text-[10px] text-[#EDEDED]/55 mb-1',style:{letterSpacing:'0.02em'}},'min conviction %'),
                React.createElement('input',{
                  type:'number',min:50,max:95,step:5,value:scalperSettings.minConviction||65,
                  onChange:(e)=>setScalperSettings(prev=>({...prev,minConviction:parseInt(e.target.value,10)||65})),
                  className:'px-2 py-1 rounded-lg text-[12px] tabular-nums',
                  style:{background:'rgba(0,0,0,0.40)',border:'1px solid #24242E',color:'#EDEDED',fontFamily:'IBM Plex Mono,ui-monospace,monospace'},
                }),
              ),
            ),
            React.createElement('div',{className:'grid grid-cols-2 gap-2'},
              React.createElement('label',{className:'flex flex-col'},
                React.createElement('span',{className:'text-[10px] text-[#EDEDED]/55 mb-1',style:{letterSpacing:'0.02em'}},'daily loss cap ¢'),
                React.createElement('input',{
                  type:'number',min:10,max:5000,step:10,value:scalperSettings.dailyLossCapCents||200,
                  onChange:(e)=>setScalperSettings(prev=>({...prev,dailyLossCapCents:parseInt(e.target.value,10)||200})),
                  className:'px-2 py-1 rounded-lg text-[12px] tabular-nums',
                  style:{background:'rgba(0,0,0,0.40)',border:'1px solid #24242E',color:'#EDEDED',fontFamily:'IBM Plex Mono,ui-monospace,monospace'},
                }),
              ),
              React.createElement('label',{className:'flex flex-col'},
                React.createElement('span',{className:'text-[10px] text-[#EDEDED]/55 mb-1',style:{letterSpacing:'0.02em'}},'suggestions / hour'),
                React.createElement('input',{
                  type:'number',min:1,max:30,step:1,value:scalperSettings.maxSuggestionsPerHour||3,
                  onChange:(e)=>setScalperSettings(prev=>({...prev,maxSuggestionsPerHour:parseInt(e.target.value,10)||3})),
                  className:'px-2 py-1 rounded-lg text-[12px] tabular-nums',
                  style:{background:'rgba(0,0,0,0.40)',border:'1px solid #24242E',color:'#EDEDED',fontFamily:'IBM Plex Mono,ui-monospace,monospace'},
                }),
              ),
            ),
          ),
          // ── GROUP 3: COOLDOWNS & SCHEDULE ────────────────────────────────
          React.createElement('div',{className:'mb-3 p-3 rounded-lg',style:{background:'rgba(0,0,0,0.25)',border:'1px solid #24242E'}},
            React.createElement('div',{className:'flex items-baseline justify-between mb-2.5'},
              React.createElement('span',{className:'text-[10px] text-[#EDEDED]/55 font-bold',style:{letterSpacing:'0.02em',textTransform:'none'}},'cooldowns & schedule'),
              React.createElement('span',{className:'text-[9px] text-[#EDEDED]/35',style:{letterSpacing:'0.02em'}},'minutes'),
            ),
            React.createElement('div',{className:'grid grid-cols-3 gap-2 mb-2'},
              React.createElement('label',{className:'flex flex-col'},
                React.createElement('span',{className:'text-[10px] text-[#EDEDED]/55 mb-1',style:{letterSpacing:'0.02em'}},'after loss'),
                React.createElement('input',{
                  type:'number',min:0,max:240,step:1,value:scalperSettings.cooldownAfterLossMin??30,
                  onChange:(e)=>setScalperSettings(prev=>({...prev,cooldownAfterLossMin:parseInt(e.target.value,10)||0})),
                  className:'px-2 py-1 rounded-lg text-[12px] tabular-nums',
                  style:{background:'rgba(0,0,0,0.40)',border:'1px solid #24242E',color:'#EDEDED',fontFamily:'IBM Plex Mono,ui-monospace,monospace'},
                }),
              ),
              React.createElement('label',{className:'flex flex-col'},
                React.createElement('span',{className:'text-[10px] text-[#EDEDED]/55 mb-1',style:{letterSpacing:'0.02em'}},'after profit'),
                React.createElement('input',{
                  type:'number',min:0,max:60,step:1,value:scalperSettings.cooldownAfterProfitMin??5,
                  onChange:(e)=>setScalperSettings(prev=>({...prev,cooldownAfterProfitMin:parseInt(e.target.value,10)||0})),
                  className:'px-2 py-1 rounded-lg text-[12px] tabular-nums',
                  style:{background:'rgba(0,0,0,0.40)',border:'1px solid #24242E',color:'#EDEDED',fontFamily:'IBM Plex Mono,ui-monospace,monospace'},
                }),
              ),
              React.createElement('label',{className:'flex flex-col'},
                React.createElement('span',{className:'text-[10px] text-[#EDEDED]/55 mb-1',style:{letterSpacing:'0.02em'}},'after neutral'),
                React.createElement('input',{
                  type:'number',min:0,max:60,step:1,value:scalperSettings.cooldownAfterNeutralMin??10,
                  onChange:(e)=>setScalperSettings(prev=>({...prev,cooldownAfterNeutralMin:parseInt(e.target.value,10)||0})),
                  className:'px-2 py-1 rounded-lg text-[12px] tabular-nums',
                  style:{background:'rgba(0,0,0,0.40)',border:'1px solid #24242E',color:'#EDEDED',fontFamily:'IBM Plex Mono,ui-monospace,monospace'},
                }),
              ),
            ),
            React.createElement('label',{className:'flex items-baseline justify-between cursor-pointer pt-1 mt-1 border-t border-[#24242E]'},
              React.createElement('div',{className:'flex flex-col'},
                React.createElement('span',{className:'text-[11px] text-[#EDEDED]/80',style:{letterSpacing:'0.02em'}},'block weak hours'),
                React.createElement('span',{className:'text-[9px] text-[#EDEDED]/40',style:{letterSpacing:'0.02em'}},'12-17 est skip window'),
              ),
              React.createElement('input',{
                type:'checkbox',
                checked:Array.isArray(scalperSettings.blockHoursEst)&&scalperSettings.blockHoursEst.length>0,
                onChange:(e)=>setScalperSettings(prev=>({...prev,blockHoursEst:e.target.checked?[12,13,14,15,16,17]:[]})),
                className:'ml-2',
              }),
            ),
          ),
          // ── GROUP 4: EXECUTION MODE (V9.17.3) ────────────────────────────
          // Auto-exec re-added. Mutually exclusive with Tara's auto-exec —
          // enabling this auto-disables Tara's auto-place-on-lock.
          React.createElement('div',{className:'mb-3 p-3 rounded-lg',style:{background:'rgba(0,0,0,0.25)',border:'1px solid #24242E'}},
            React.createElement('div',{className:'flex items-baseline justify-between mb-2.5'},
              React.createElement('span',{className:'text-[10px] text-[#EDEDED]/55 font-bold',style:{letterSpacing:'0.02em',textTransform:'none'}},'execution'),
              React.createElement('span',{className:'text-[9px] text-[#EDEDED]/35',style:{letterSpacing:'0.02em'}},'manual or auto'),
            ),
            React.createElement('label',{className:'flex items-baseline justify-between cursor-pointer'},
              React.createElement('div',{className:'flex flex-col'},
                React.createElement('span',{className:'text-[11px] text-[#EDEDED]/85 font-bold',style:{letterSpacing:'0.02em'}},'auto-exec scalp orders'),
                React.createElement('span',{className:'text-[10px] text-[#EDEDED]/45',style:{letterSpacing:'0.02em'}},
                  scalperSettings.autoExecEnabled?'on · scalp suggestions auto-place kalshi orders':'off · manual entry/exit via the panel buttons',
                ),
              ),
              React.createElement('input',{
                type:'checkbox',
                checked:!!scalperSettings.autoExecEnabled,
                onChange:(e)=>{
                  const _on=e.target.checked;
                  setScalperSettings(prev=>({...prev,autoExecEnabled:_on}));
                  // V9.17.3: mutual exclusivity — enabling scalper auto-exec disables Tara auto-exec
                  if(_on&&autoExecSettings?.enabled&&typeof setAutoExecSettings==='function'){
                    setAutoExecSettings(prev=>({...prev,enabled:false}));
                  }
                },
                className:'ml-2',
              }),
            ),
            !!scalperSettings.autoExecEnabled&&React.createElement('div',{className:'text-[9px] text-amber-400/70 italic mt-2 pt-2 border-t border-[#24242E]'},
              'tara auto-exec disabled — scalper auto-exec takes precedence. enable conflict guard below for extra safety.',
            ),
            React.createElement('label',{className:'flex items-baseline justify-between cursor-pointer mt-2 pt-2 border-t border-[#24242E]'},
              React.createElement('div',{className:'flex flex-col'},
                React.createElement('span',{className:'text-[11px] text-[#EDEDED]/75',style:{letterSpacing:'0.02em'}},'conflict guard'),
                React.createElement('span',{className:'text-[10px] text-[#EDEDED]/45',style:{letterSpacing:'0.02em'}},'block scalper auto-exec when tara has a locked position'),
              ),
              React.createElement('input',{
                type:'checkbox',
                checked:scalperSettings.conflictGuardEnabled!==false,
                onChange:(e)=>setScalperSettings(prev=>({...prev,conflictGuardEnabled:e.target.checked})),
                className:'ml-2',
              }),
            ),
          ),
        ),
      ), // closes scalperSettings.enabled Fragment
        ), // V10.4.1a closes the wrapping div inside the "Scalper config" details
      ), // V13.4.317: closes the outer Tara's Advisor <details> (was a <section>)
      React.createElement('button',{
        onClick:onClose,
        className:'w-full px-4 py-2 rounded-lg text-[11px] uppercase font-bold tracking-wider transition-colors',
        style:{background:'rgba(35,185,129,0.12)',color:T2_GOLD,border:'1px solid rgba(35,185,129,0.3)'},
      },'Done'),
    ),
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// V9.7.0: MISSION MODE PANEL — bankroll/target progress + setup
// ═══════════════════════════════════════════════════════════════════════════
// Renders in two contexts:
//   1. Compact dashboard widget (always visible when mission is active) showing
//      bankroll / target / progress / probability / win rate
//   2. Full setup panel inside Trading Settings modal for configuration
//
// Status states surface different UI:
//   inactive: setup form
//   active:   live progress dashboard
//   hit:      celebration banner — "you did it"
//   busted:   reset/recover banner with stop-loss explanation
//   expired:  end-of-interval summary
//   paused:   user manually paused, resume button
function MissionPanel({mission,setMission,regimeDirCalibration,killSwitchEngaged,setKillSwitchEngaged,compact=false}){
  // 1Hz tick to refresh time-remaining display
  const[,_t]=React.useState(0);
  React.useEffect(()=>{const iv=setInterval(()=>_t(x=>x+1),1000);return()=>clearInterval(iv);},[]);
  // V9.7.0: local _num helper (TradingSettingsModal's is out-of-scope here)
  const _num=(s,fallback)=>{const n=Number(s);return Number.isFinite(n)?n:fallback;};
  // Local form state for setup (don't commit until user clicks Start)
  const[setup,setSetup]=React.useState(()=>({
    startBankroll:mission.startBankroll||25,
    target:mission.target||200,
    durationDays:7,
    floor:mission.floor||5,
    kellyMult:mission.kellyMult||0.25,
    maxBetFraction:mission.maxBetFraction||0.15,
  }));
  const _now=Date.now();
  const _isActive=mission.active&&mission.status==='active';
  const _isInactive=!mission.active||mission.status==='inactive';
  const _msRemaining=mission.endDate?Math.max(0,new Date(mission.endDate).getTime()-_now):0;
  const _daysRemaining=_msRemaining/86400000;
  const _hoursRemaining=Math.floor(_msRemaining/3600000);
  const _progressPct=mission.target>0?Math.min(100,Math.max(0,((mission.currentBankroll-mission.startBankroll)/(mission.target-mission.startBankroll))*100)):0;
  const _runWR=mission.tradesAttempted>0?(mission.tradesWon/mission.tradesAttempted)*100:0;
  // Estimate target probability — uses cluster-blend default WR ~0.65, typical Kalshi odds
  let _targetProb=null;
  if(_isActive&&mission.target>0&&_daysRemaining>0&&mission.currentBankroll<mission.target){
    // Use empirical bankroll trajectory if available, else conservative defaults
    const _empiricalP=_runWR>0?_runWR/100:0.65;
    const _avgFracEstimate=mission.maxBetFraction*0.6; // typical sized trade
    const _odds=1.0; // average market odds at 50¢ entry
    _targetProb=_missionEstimateTargetProbability({
      bankroll:mission.currentBankroll,
      target:mission.target,
      daysRemaining:_daysRemaining,
      p:_empiricalP,
      b:_odds,
      fractionOfBankroll:_avgFracEstimate,
      tradesPerDay:8,
    });
  }
  // Sparkline for bankroll history
  const _renderSparkline=()=>{
    const h=mission.bankrollHistory||[];
    if(h.length<2)return null;
    const _max=Math.max(...h.map(p=>p.b),mission.target||0);
    const _min=Math.min(...h.map(p=>p.b),mission.floor||0);
    const _range=_max-_min||1;
    const _w=180,_hgt=32;
    const _pts=h.map((p,i)=>{
      const x=(i/(h.length-1))*_w;
      const y=_hgt-((p.b-_min)/_range)*_hgt;
      return`${x.toFixed(1)},${y.toFixed(1)}`;
    }).join(' ');
    return React.createElement('svg',{width:_w,height:_hgt,style:{display:'block'}},
      React.createElement('polyline',{points:_pts,fill:'none',stroke:'#23B981',strokeWidth:1.5,strokeLinejoin:'round',strokeLinecap:'round'}),
    );
  };
  const _startMission=()=>{
    if(!setup.startBankroll||!setup.target||setup.target<=setup.startBankroll||!setup.durationDays){
      alert('Need: positive bankroll, target above bankroll, positive duration.');
      return;
    }
    if(setup.floor>=setup.startBankroll){
      alert('Drawdown floor must be below starting bankroll.');
      return;
    }
    const _now=Date.now();
    setMission({
      ...mission,
      active:true,
      startBankroll:setup.startBankroll,
      currentBankroll:setup.startBankroll,
      target:setup.target,
      startDate:new Date(_now).toISOString(),
      endDate:new Date(_now+setup.durationDays*86400000).toISOString(),
      floor:setup.floor,
      kellyMult:setup.kellyMult,
      maxBetFraction:setup.maxBetFraction,
      status:'active',
      tradesAttempted:0,tradesWon:0,tradesLost:0,
      lastBankrollUpdate:_now,
      bankrollHistory:[{t:_now,b:setup.startBankroll}],
    });
  };
  const _stopMission=(toStatus)=>{
    if(!window.confirm(`End mission as ${toStatus}? Bankroll history will be preserved but Tara will stop sizing trades against this target.`))return;
    setMission(prev=>({...prev,active:false,status:toStatus||'inactive'}));
  };
  const _resetMission=()=>{
    if(!window.confirm('Wipe mission state entirely? This clears bankroll history, target, all settings.'))return;
    setMission({active:false,startBankroll:0,currentBankroll:0,target:0,startDate:null,endDate:null,floor:0,kellyMult:0.25,maxBetFraction:0.15,status:'inactive',tradesAttempted:0,tradesWon:0,tradesLost:0,lastBankrollUpdate:0,bankrollHistory:[]});
  };
  // ── COMPACT DASHBOARD WIDGET ──────────────────────────────────────────
  if(compact){
    if(!_isActive)return null; // compact mode only renders when active
    const _statusColor=mission.status==='hit'?'#23B981':mission.status==='busted'?'#E8455E':_progressPct>=75?'#23B981':_progressPct>=40?'#23B981':_progressPct>=15?'#23B981':'#E8455E';
    return React.createElement('div',{className:'rounded-lg border p-2.5 mb-2',style:{background:'rgba(35,185,129,0.04)',borderColor:'rgba(35,185,129,0.20)'}},
      React.createElement('div',{className:'flex items-baseline justify-between mb-1.5'},
        React.createElement('div',{className:'flex items-baseline gap-2'},
          React.createElement('span',{className:'text-[10px] uppercase font-bold tracking-[0.18em]',style:{color:'#23B981'}},'Paper mission'),
          React.createElement('span',{className:'text-[9px] uppercase tracking-wider text-[#EDEDED]/45'},_daysRemaining<1?`${_hoursRemaining}h left`:`${_daysRemaining.toFixed(1)}d left`),
        ),
        killSwitchEngaged&&React.createElement('span',{className:'text-[9px] uppercase tracking-wider px-1 py-0.5 rounded-lg',style:{color:'#E8455E',border:'1px solid rgba(232,69,94,0.40)'}},'killed'),
      ),
      React.createElement('div',{className:'flex items-baseline gap-3 mb-1.5'},
        React.createElement('div',{className:'flex-1 min-w-0'},
          React.createElement('div',{className:'text-[10px] text-[#EDEDED]/55 mb-0.5'},'model bankroll'),
          React.createElement('div',{className:'text-2xl font-serif font-bold tabular-nums leading-none',style:{color:_statusColor}},'$',mission.currentBankroll.toFixed(0)),
        ),
        React.createElement('div',{className:'text-right'},
          React.createElement('div',{className:'text-[10px] text-[#EDEDED]/55 mb-0.5'},'target'),
          React.createElement('div',{className:'text-base font-serif font-bold tabular-nums leading-none text-white'},'$',mission.target),
        ),
      ),
      React.createElement('div',{className:'h-1.5 bg-[#050508] rounded-lg overflow-hidden mb-1.5'},
        React.createElement('div',{style:{width:`${_progressPct}%`,height:'100%',background:_statusColor,transition:'width 600ms'}}),
      ),
      React.createElement('div',{className:'flex items-baseline justify-between text-[10px]'},
        React.createElement('span',{className:'tabular-nums',style:{color:_statusColor}},_progressPct.toFixed(0),'% to target'),
        _targetProb!=null&&React.createElement('span',{className:'text-[#EDEDED]/55'},
          'odds ',
          React.createElement('span',{className:'font-bold tabular-nums',style:{color:_targetProb>=0.5?'#23B981':_targetProb>=0.25?'#23B981':'#E8455E'}},Math.round(_targetProb*100),'%'),
        ),
      ),
      mission.tradesAttempted>0&&React.createElement('div',{className:'mt-1.5 pt-1.5 border-t border-[#24242E] text-[10px] text-[#EDEDED]/55 flex items-baseline justify-between'},
        React.createElement('span',null,mission.tradesAttempted,' scored Calls · WR ',_runWR.toFixed(0),'%'),
        mission.bankrollHistory?.length>=2&&_renderSparkline(),
      ),
      _targetProb!=null&&_targetProb<0.25&&React.createElement('div',{className:'mt-1.5 text-[10px] text-rose-300/85 leading-relaxed'},'⚠ Target unrealistic at current pace. Consider extending the deadline or reducing target.'),
    );
  }
  // ── FULL SETUP / ACTIVE PANEL ─────────────────────────────────────────
  return React.createElement('div',null,
    React.createElement('div',{className:'flex items-baseline justify-between mb-2'},
      React.createElement('div',null,
        React.createElement('div',{className:'text-[10px] uppercase font-bold tracking-[0.18em]',style:{color:'#23B981'}},'Paper Mission Mode'),
        React.createElement('div',{className:'text-[10px] text-[#EDEDED]/45 mt-0.5'},'modeled bankroll → target · not a Kalshi account balance'),
      ),
      React.createElement('span',{className:'text-[9px] uppercase tracking-wider px-1 py-0.5 rounded-lg',style:{
        color:mission.status==='hit'?'#23B981':mission.status==='busted'?'#E8455E':mission.status==='active'?'#23B981':'rgba(237,237,237,0.45)',
        border:`1px solid ${mission.status==='hit'?'rgba(35,185,129,0.4)':mission.status==='busted'?'rgba(232,69,94,0.4)':mission.status==='active'?'rgba(212,162,76,0.4)':'rgba(237,237,237,0.20)'}`,
      }},mission.status||'inactive'),
    ),
    // Hit/busted/expired banners
    mission.status==='hit'&&React.createElement('div',{className:'p-3 rounded-lg mb-3',style:{background:'rgba(35,185,129,0.10)',border:'1px solid rgba(35,185,129,0.40)'}},
      React.createElement('div',{className:'text-[12px] font-bold mb-1',style:{color:'#23B981'}},'🎯 Target hit'),
      React.createElement('div',{className:'text-[11px] text-[#EDEDED]/75'},'Bankroll $',mission.currentBankroll.toFixed(2),' · target $',mission.target.toFixed(2),' · ',mission.tradesAttempted,' trades · WR ',_runWR.toFixed(0),'%'),
      React.createElement('div',{className:'text-[10px] text-[#EDEDED]/55 mt-2'},'Auto-exec is paused on this mission. Reset or start a new one.'),
    ),
    mission.status==='busted'&&React.createElement('div',{className:'p-3 rounded-lg mb-3',style:{background:'rgba(232,69,94,0.10)',border:'1px solid rgba(232,69,94,0.40)'}},
      React.createElement('div',{className:'text-[12px] font-bold mb-1',style:{color:'#E8455E'}},'Bankroll hit drawdown floor'),
      React.createElement('div',{className:'text-[11px] text-[#EDEDED]/75'},'$',mission.currentBankroll.toFixed(2),' ≤ floor $',mission.floor.toFixed(2),' · stopped after ',mission.tradesAttempted,' trades · WR ',_runWR.toFixed(0),'%'),
      React.createElement('div',{className:'text-[10px] text-[#EDEDED]/55 mt-2 leading-relaxed'},'Floor protected the rest of the bankroll. The mission stopped before reaching $0. Reset to start a new one with adjusted targets, or review the trade log for what went wrong.'),
    ),
    mission.status==='expired'&&React.createElement('div',{className:'p-3 rounded-lg mb-3 flex items-start justify-between gap-2',style:{background:'rgba(212,162,76,0.08)',border:'1px solid rgba(212,162,76,0.30)'}},
      React.createElement('div',null,
        React.createElement('div',{className:'text-[12px] font-bold mb-1',style:{color:'#23B981'}},'Mission expired'),
        React.createElement('div',{className:'text-[11px] text-[#EDEDED]/75'},'Final $',mission.currentBankroll.toFixed(2),' · target $',mission.target.toFixed(2),' · ',mission.tradesAttempted,' trades · WR ',_runWR.toFixed(0),'%'),
      ),
      // V10.4.1a: clear-and-restart button so user can dismiss without hunting for the deep reset
      React.createElement('button',{
        onClick:_resetMission,
        className:'text-[10px] uppercase tracking-wider px-2 py-1 rounded-lg border border-[#23B981]/40 hover:bg-[#23B981]/15 text-[#23B981] shrink-0',
        title:'Clear mission state to start fresh',
      },'Clear · Reset'),
    ),
    // Active progress
    _isActive&&React.createElement('div',{className:'p-3 rounded-lg mb-3',style:{background:'rgba(35,185,129,0.04)',border:'1px solid rgba(35,185,129,0.20)'}},
      React.createElement('div',{className:'grid grid-cols-2 gap-3 mb-3'},
        React.createElement('div',null,
          React.createElement('div',{className:'text-[10px] uppercase tracking-wider text-[#EDEDED]/55 mb-0.5'},'current bankroll'),
          React.createElement('div',{className:'text-2xl font-serif font-bold tabular-nums',style:{color:'#23B981'}},'$',mission.currentBankroll.toFixed(2)),
          React.createElement('div',{className:'text-[10px] text-[#EDEDED]/55'},'started $',mission.startBankroll.toFixed(2)),
        ),
        React.createElement('div',null,
          React.createElement('div',{className:'text-[10px] uppercase tracking-wider text-[#EDEDED]/55 mb-0.5'},'target'),
          React.createElement('div',{className:'text-2xl font-serif font-bold tabular-nums text-white'},'$',mission.target.toFixed(2)),
          React.createElement('div',{className:'text-[10px] text-[#EDEDED]/55'},_daysRemaining<1?`${_hoursRemaining}h left`:`${_daysRemaining.toFixed(1)} days left`),
        ),
      ),
      React.createElement('div',{className:'h-2 bg-[#050508] rounded-lg overflow-hidden mb-2'},
        React.createElement('div',{style:{width:`${_progressPct}%`,height:'100%',background:_progressPct>=75?'#23B981':_progressPct>=40?'#23B981':_progressPct>=15?'#23B981':'#E8455E',transition:'width 600ms'}}),
      ),
      React.createElement('div',{className:'flex items-baseline justify-between text-[11px] mb-2'},
        React.createElement('span',{className:'tabular-nums text-[#EDEDED]/65'},_progressPct.toFixed(1),'% complete'),
        React.createElement('span',{className:'text-[#EDEDED]/55'},'floor $',mission.floor.toFixed(2)),
      ),
      _targetProb!=null&&React.createElement('div',{className:'p-2 rounded-lg mb-2',style:{background:'#0B0B0F'}},
        React.createElement('div',{className:'flex items-baseline justify-between text-[11px]'},
          React.createElement('span',{className:'text-[#EDEDED]/65'},'estimated probability of hitting target'),
          React.createElement('span',{className:'font-bold tabular-nums',style:{color:_targetProb>=0.5?'#23B981':_targetProb>=0.25?'#23B981':'#E8455E'}},Math.round(_targetProb*100),'%'),
        ),
        React.createElement('div',{className:'text-[9px] text-[#EDEDED]/45 mt-1 leading-relaxed'},'rough estimate — assumes constant edge and trade rate; actual variance is higher'),
        _targetProb<0.25&&React.createElement('div',{className:'text-[10px] text-rose-300/85 mt-1.5 leading-relaxed'},'Target looks unrealistic at current pace. Consider extending the deadline, lowering the target, or accepting that hitting it is unlikely.'),
      ),
      mission.tradesAttempted>0&&React.createElement('div',{className:'p-2 rounded-lg text-[11px]',style:{background:'#0B0B0F'}},
        React.createElement('div',{className:'flex items-baseline justify-between mb-1'},
          React.createElement('span',{className:'text-[#EDEDED]/65'},mission.tradesAttempted,' trades · ',mission.tradesWon,'W ',mission.tradesLost,'L'),
          React.createElement('span',{className:'font-bold tabular-nums',style:{color:_runWR>=60?'#23B981':_runWR>=50?'#23B981':'#E8455E'}},'WR ',_runWR.toFixed(1),'%'),
        ),
        mission.bankrollHistory?.length>=2&&React.createElement('div',{className:'mt-1.5'},_renderSparkline()),
      ),
      // Action buttons
      React.createElement('div',{className:'flex gap-2 mt-3'},
        React.createElement('button',{
          onClick:()=>setMission(prev=>({...prev,status:'paused'})),
          className:'flex-1 px-2 py-1.5 rounded-lg text-[10px] uppercase font-bold tracking-wider',
          style:{background:'rgba(212,162,76,0.10)',color:'#23B981',border:'1px solid rgba(212,162,76,0.30)'},
        },'Pause'),
        React.createElement('button',{
          onClick:()=>_stopMission('expired'),
          className:'flex-1 px-2 py-1.5 rounded-lg text-[10px] uppercase font-bold tracking-wider',
          style:{background:'rgba(232,69,94,0.08)',color:'#E8455E',border:'1px solid rgba(232,69,94,0.30)'},
        },'End mission'),
      ),
    ),
    // Paused state
    mission.status==='paused'&&React.createElement('div',{className:'p-3 rounded-lg mb-3',style:{background:'rgba(212,162,76,0.08)',border:'1px solid rgba(212,162,76,0.30)'}},
      React.createElement('div',{className:'text-[11px] mb-2',style:{color:'#23B981'}},'Mission paused. Auto-exec sizing falls back to fixed bet size.'),
      React.createElement('button',{
        onClick:()=>setMission(prev=>({...prev,status:'active'})),
        className:'px-3 py-1.5 rounded-lg text-[10px] uppercase font-bold tracking-wider',
        style:{background:'rgba(35,185,129,0.10)',color:'#23B981',border:'1px solid rgba(35,185,129,0.30)'},
      },'Resume mission'),
    ),
    // Setup form (when inactive or after reset)
    _isInactive&&React.createElement('div',{className:'p-3 rounded-lg',style:{background:'rgba(35,185,129,0.04)',border:'1px solid rgba(35,185,129,0.20)'}},
      React.createElement('div',{className:'text-[11px] text-[#EDEDED]/65 leading-relaxed mb-3'},
        'Set a starting bankroll, target, and deadline. Tara will size each auto-exec trade using fractional Kelly with cluster-specific WR. Hard stops at the drawdown floor and at target — no chasing past either side.',
      ),
      React.createElement('div',{className:'grid grid-cols-2 gap-2 mb-2'},
        React.createElement('label',{className:'block'},
          React.createElement('div',{className:'text-[10px] text-[#EDEDED]/65 mb-1'},'Starting bankroll ($)'),
          React.createElement('input',{
            type:'number',min:1,max:100000,step:1,value:setup.startBankroll,
            onChange:(e)=>setSetup(prev=>({...prev,startBankroll:Math.max(1,Math.min(100000,_num(e.target.value,25)))})),
            className:'w-full bg-transparent border border-[#2A2A34] rounded-lg px-2 py-1 text-white text-sm tabular-nums focus:border-[#23B981] focus:outline-none',
          }),
          // V9.17.20: unit hint
          React.createElement('div',{className:'text-[9px] text-[#EDEDED]/40 mt-1'},
            `= $${(Number(setup.startBankroll)||0).toFixed(0)} dollars you're starting with`,
          ),
        ),
        React.createElement('label',{className:'block'},
          React.createElement('div',{className:'text-[10px] text-[#EDEDED]/65 mb-1'},'Target ($)'),
          React.createElement('input',{
            type:'number',min:2,max:1000000,step:1,value:setup.target,
            onChange:(e)=>setSetup(prev=>({...prev,target:Math.max(2,Math.min(1000000,_num(e.target.value,200)))})),
            className:'w-full bg-transparent border border-[#2A2A34] rounded-lg px-2 py-1 text-white text-sm tabular-nums focus:border-[#23B981] focus:outline-none',
          }),
          // V9.17.20: unit hint with growth multiple
          React.createElement('div',{className:'text-[9px] text-[#EDEDED]/40 mt-1'},(()=>{
            const _t=Number(setup.target)||0;
            const _s=Number(setup.startBankroll)||1;
            const _mult=_s>0?(_t/_s):0;
            return `= $${_t.toFixed(0)} dollars goal (${_mult.toFixed(1)}× your bankroll)`;
          })()),
        ),
      ),
      React.createElement('div',{className:'grid grid-cols-2 gap-2 mb-2'},
        React.createElement('label',{className:'block'},
          React.createElement('div',{className:'text-[10px] text-[#EDEDED]/65 mb-1'},'Duration (days)'),
          React.createElement('input',{
            type:'number',min:1,max:365,step:1,value:setup.durationDays,
            onChange:(e)=>setSetup(prev=>({...prev,durationDays:Math.max(1,Math.min(365,_num(e.target.value,7)))})),
            className:'w-full bg-transparent border border-[#2A2A34] rounded-lg px-2 py-1 text-white text-sm tabular-nums focus:border-[#23B981] focus:outline-none',
          }),
          // V9.17.20: unit hint
          React.createElement('div',{className:'text-[9px] text-[#EDEDED]/40 mt-1'},
            `= ${setup.durationDays||7} days deadline to hit target`,
          ),
        ),
        React.createElement('label',{className:'block'},
          React.createElement('div',{className:'text-[10px] text-[#EDEDED]/65 mb-1'},'Drawdown floor ($)'),
          React.createElement('input',{
            type:'number',min:0,max:setup.startBankroll-1,step:1,value:setup.floor,
            onChange:(e)=>setSetup(prev=>({...prev,floor:Math.max(0,Math.min(setup.startBankroll-1,_num(e.target.value,5)))})),
            className:'w-full bg-transparent border border-[#2A2A34] rounded-lg px-2 py-1 text-white text-sm tabular-nums focus:border-[#23B981] focus:outline-none',
          }),
          // V9.17.20: unit hint
          React.createElement('div',{className:'text-[9px] text-[#EDEDED]/40 mt-1'},(()=>{
            const _f=Number(setup.floor)||0;
            const _s=Number(setup.startBankroll)||1;
            const _ddPct=_s>0?Math.round((1-_f/_s)*100):0;
            return `= mission stops if bankroll drops to $${_f.toFixed(0)} (${_ddPct}% drawdown)`;
          })()),
        ),
      ),
      React.createElement('div',{className:'grid grid-cols-2 gap-2 mb-3'},
        React.createElement('label',{className:'block'},
          React.createElement('div',{className:'text-[10px] text-[#EDEDED]/65 mb-1'},'Kelly multiplier'),
          React.createElement('select',{
            value:setup.kellyMult,
            onChange:(e)=>setSetup(prev=>({...prev,kellyMult:Number(e.target.value)})),
            className:'w-full bg-[#050508] border border-[#2A2A34] rounded-lg px-2 py-1 text-white text-sm focus:border-[#23B981] focus:outline-none',
          },
            React.createElement('option',{value:0.1},'0.10× — very conservative'),
            React.createElement('option',{value:0.25},'0.25× — quarter Kelly (recommended)'),
            React.createElement('option',{value:0.5},'0.50× — half Kelly (aggressive)'),
            React.createElement('option',{value:1.0},'1.00× — full Kelly (high bust risk)'),
          ),
          // V9.17.20: unit hint
          React.createElement('div',{className:'text-[9px] text-[#EDEDED]/40 mt-1'},
            `= bet sizing multiplier (${Number(setup.kellyMult).toFixed(2)}× of the full Kelly-optimal bet)`,
          ),
        ),
        React.createElement('label',{className:'block'},
          React.createElement('div',{className:'text-[10px] text-[#EDEDED]/65 mb-1'},'Max bet fraction'),
          React.createElement('select',{
            value:setup.maxBetFraction,
            onChange:(e)=>setSetup(prev=>({...prev,maxBetFraction:Number(e.target.value)})),
            className:'w-full bg-[#050508] border border-[#2A2A34] rounded-lg px-2 py-1 text-white text-sm focus:border-[#23B981] focus:outline-none',
          },
            React.createElement('option',{value:0.05},'5% per trade — very safe'),
            React.createElement('option',{value:0.10},'10% per trade — safe'),
            React.createElement('option',{value:0.15},'15% per trade — recommended'),
            React.createElement('option',{value:0.25},'25% per trade — aggressive'),
            React.createElement('option',{value:0.40},'40% per trade — very aggressive'),
          ),
          // V9.17.20: unit hint
          React.createElement('div',{className:'text-[9px] text-[#EDEDED]/40 mt-1'},(()=>{
            const _frac=Number(setup.maxBetFraction)||0.15;
            const _br=Number(setup.startBankroll)||25;
            const _cap=_br*_frac;
            return `= no single trade exceeds ${Math.round(_frac*100)}% of bankroll (cap ≈ $${_cap.toFixed(2)} today)`;
          })()),
        ),
      ),
      // Honest preview
      React.createElement('div',{className:'p-2 rounded-lg mb-3',style:{background:'#0B0B0F',border:'1px solid #24242E'}},
        React.createElement('div',{className:'text-[10px] uppercase tracking-wider text-[#EDEDED]/55 mb-1'},'honest math preview'),
        (()=>{
          if(!setup.startBankroll||!setup.target||setup.target<=setup.startBankroll||!setup.durationDays)return React.createElement('div',{className:'text-[10px] text-[#EDEDED]/45'},'Enter values above to see estimated odds');
          const _multiplier=setup.target/setup.startBankroll;
          const _previewProb=_missionEstimateTargetProbability({
            bankroll:setup.startBankroll,
            target:setup.target,
            daysRemaining:setup.durationDays,
            p:0.65,
            b:1.0,
            fractionOfBankroll:setup.maxBetFraction*0.6,
            tradesPerDay:8,
          });
          const _color=_previewProb>=0.5?'#23B981':_previewProb>=0.25?'#23B981':'#E8455E';
          return React.createElement('div',null,
            React.createElement('div',{className:'text-[11px]'},React.createElement('span',{className:'text-[#EDEDED]/65'},_multiplier.toFixed(1),'× growth in ',setup.durationDays,'d → odds: '),React.createElement('span',{className:'font-bold tabular-nums',style:{color:_color}},Math.round(_previewProb*100),'%')),
            _previewProb<0.25&&React.createElement('div',{className:'text-[10px] text-rose-300/85 mt-1 leading-relaxed'},'⚠ This target is aggressive. Even with a 65% win rate, fewer than 1 in 4 paths reach it before the drawdown floor. Consider lowering the target or extending the deadline.'),
            _previewProb>=0.5&&React.createElement('div',{className:'text-[10px] text-emerald-300/85 mt-1 leading-relaxed'},'Reasonable target. Most paths reach it before bust.'),
          );
        })(),
      ),
      React.createElement('button',{
        onClick:_startMission,
        className:'w-full px-3 py-2 rounded-lg text-[11px] uppercase font-bold tracking-wider',
        style:{background:'rgba(35,185,129,0.15)',color:'#23B981',border:'1px solid rgba(35,185,129,0.40)'},
      },'Start mission'),
    ),
    // Reset (always visible if any history exists)
    (mission.startBankroll>0||mission.bankrollHistory?.length>0)&&!_isActive&&React.createElement('button',{
      onClick:_resetMission,
      className:'mt-2 text-[9px] uppercase tracking-wider text-[#EDEDED]/45 hover:text-[#EDEDED]/70',
    },'Reset mission state'),
  );
}

// ── V13.4.307: SIMPLE AUTO-EXEC PANEL ────────────────────────────────────────
// "extremely simple" surface requested directly: a take-profit dial, a
//   cut-loss dial, a Mission Mode toggle, and (when Mission Mode is off)
//   flat dollar-preset buttons instead of the free-text max-bet field.
//   Reads/writes the exact same autoExecSettings/mission fields the advanced
//   accordion already uses (autoExitOffer, stopLossDeltaCents,
//   maxBetPerTrade, mission.*) -- no separate state, so the two surfaces
//   cannot drift. Per direct instruction ("real dials, no risk"): TP/cut-loss
//   stay real and unrestricted -- no new blocking logic, only the same
//   informational captions the advanced fields already carry, condensed.
// Mission on/off mirrors MissionPanel's own transitions exactly:
//   turning on with an existing paused mission -> status:'active' (App.jsx
//   ~22698, "Resume mission"); turning on with no mission, or one that ended
//   (hit/busted/expired), -> a 2-field bootstrap that calls the same
//   _startMission shape with sane fixed defaults (7d, 20% floor, quarter-
//   Kelly, 15% max fraction) -- open the advanced Mission panel to fine-tune
//   those. Turning off -> status:'paused' (App.jsx ~22683, "Pause"). Neither
//   path ever wipes bankroll history; only Reset (advanced panel) does that.
function AutoExecSimplePanel({autoExecSettings,setAutoExecSettings,mission,setMission}){
  const _num=(s,fallback)=>{const n=Number(s);return Number.isFinite(n)?n:fallback;};
  const[_bootstrap,_setBootstrap]=React.useState(null); // {startBankroll,target} while composing a new mission, else null
  const _isActive=mission.active&&mission.status==='active';
  const _resumable=mission.status==='paused';
  const _cap=Number(autoExecSettings?.maxBetPerTrade)||0;
  const PRESETS=[1,2,4,6,8,10];
  // If the mission goes active while a bootstrap draft is still open (e.g. the
  //   advanced Mission panel's own Start button was used instead), drop the
  //   stale draft rather than leave it showing alongside the now-active summary.
  React.useEffect(()=>{if(_isActive&&_bootstrap)_setBootstrap(null);},[_isActive]);

  const _startWithDefaults=()=>{
    const sb=Number(_bootstrap?.startBankroll)||0;
    const tgt=Number(_bootstrap?.target)||0;
    if(!sb||!tgt||tgt<=sb){alert('Need a positive starting bankroll and a target above it.');return;}
    const floor=Math.max(0,Number((Math.min(sb-0.5,sb*0.2)).toFixed(2)));
    const _now=Date.now();
    setMission({
      ...mission,
      active:true,
      startBankroll:sb,
      currentBankroll:sb,
      target:tgt,
      startDate:new Date(_now).toISOString(),
      endDate:new Date(_now+7*86400000).toISOString(),
      floor,
      kellyMult:0.25,
      maxBetFraction:0.15,
      status:'active',
      tradesAttempted:0,tradesWon:0,tradesLost:0,
      lastBankrollUpdate:_now,
      bankrollHistory:[{t:_now,b:sb}],
    });
    _setBootstrap(null);
  };

  const _toggleMission=()=>{
    if(_isActive){setMission(prev=>({...prev,status:'paused'}));return;}
    if(_resumable){setMission(prev=>({...prev,status:'active'}));return;}
    const _sb=mission.startBankroll||25;
    _setBootstrap({startBankroll:_sb,target:mission.target&&mission.target>_sb?mission.target:_sb*2});
  };

  const _expandAdvancedMission=()=>{
    const el=document.getElementById('tara-advanced-settings');
    if(el){el.open=true;el.scrollIntoView({behavior:'smooth',block:'start'});}
  };

  const _tpVal=Number(autoExecSettings?.autoExitOffer)||0;
  const _tpCaption=_tpVal===0?'off — recommended, trailing stop only'
    :_tpVal<=TRAIL_ARM_C?`fires before the ${TRAIL_ARM_C}¢ trail can arm — measured worse`
    :`sells at ${_tpVal}¢ on a 100¢ contract`;
  const _tpColor=_tpVal===0?'#23B981':_tpVal<=TRAIL_ARM_C?'#E8455E':'rgba(237,237,237,0.55)';

  const _slVal=Number(autoExecSettings?.stopLossDeltaCents)||0;
  // V13.4.323: was neutral gray for 1-29¢, implying that band was a safe
  //   middle ground. It is not -- the V10.2.31 audit (724 historical trades)
  //   found a fixed stop-loss measured negative at every level it tested,
  //   10-40¢ included (13¢, the old "Hunter" preset, among them). Any
  //   nonzero value now gets the same clear warning take-profit already
  //   gets, instead of quietly looking fine at exactly the values that
  //   measured worst.
  const _slCaption=_slVal===0?'off — recommended, holds to settlement'
    :_slVal>=30?`exits ${_slVal}¢ below fill price — very wide, most of the stake is already lost by then`
    :`exits ${_slVal}¢ below fill price — measured worse than holding to settlement at every level tested (10-40¢)`;
  const _slColor=_slVal===0?'#23B981':'#E8455E';

  return React.createElement('div',{className:'mb-4 p-3 rounded-lg bg-[#050508] border border-[#24242E]'},
    React.createElement('div',{className:'text-[9px] uppercase font-bold tracking-[0.14em] text-[#EDEDED]/50 mb-2'},'Auto-Exec — Simple'),
    // V13.4.308: was two separately-bordered input boxes; replaced with the
    //   app's current "hairline grid" strip (same pattern as SmartMoneyStrip/
    //   LiveFeedsCard -- gap-px over a #16161c background reads as a 1px
    //   divider between #0A0A0E cells) so the two dials read as one clean
    //   strip instead of two boxed form fields. Same onChange/clamp logic,
    //   unchanged.
    React.createElement('div',{className:'grid grid-cols-2 gap-px rounded-[10px] overflow-hidden mb-3',style:{background:'#16161c'}},
      React.createElement('div',{className:'px-3 py-2.5',style:{background:'#0A0A0E'}},
        React.createElement('div',{className:'text-[9px] uppercase tracking-[0.14em] text-[#EDEDED]/30 font-bold mb-1'},'Take-profit'),
        React.createElement('div',{className:'flex items-baseline gap-1'},
          React.createElement('input',{
            type:'number',min:0,max:99,step:1,value:_tpVal,
            onChange:(e)=>setAutoExecSettings(prev=>({...prev,autoExitOffer:Math.max(0,Math.min(99,_num(e.target.value,0)))})),
            className:'w-10 bg-transparent text-[16px] font-bold tabular-nums focus:outline-none border-b border-transparent focus:border-[#2A2A34] transition-colors',
            style:{color:_tpVal===0?'rgba(237,237,237,0.85)':_tpColor},
          }),
          React.createElement('span',{className:'text-[11px] text-[#EDEDED]/35'},'¢'),
        ),
        React.createElement('div',{className:'text-[9px] mt-1 leading-snug',style:{color:_tpColor}},_tpCaption),
      ),
      React.createElement('div',{className:'px-3 py-2.5',style:{background:'#0A0A0E'}},
        React.createElement('div',{className:'text-[9px] uppercase tracking-[0.14em] text-[#EDEDED]/30 font-bold mb-1'},'Cut-loss'),
        React.createElement('div',{className:'flex items-baseline gap-1'},
          React.createElement('input',{
            type:'number',min:0,max:50,step:1,value:_slVal,
            onChange:(e)=>setAutoExecSettings(prev=>({...prev,stopLossDeltaCents:Math.max(0,Math.min(50,_num(e.target.value,0)))})),
            className:'w-10 bg-transparent text-[16px] font-bold tabular-nums focus:outline-none border-b border-transparent focus:border-[#2A2A34] transition-colors',
            style:{color:_slVal===0?'rgba(237,237,237,0.85)':_slColor},
          }),
          React.createElement('span',{className:'text-[11px] text-[#EDEDED]/35'},'¢'),
        ),
        React.createElement('div',{className:'text-[9px] mt-1 leading-snug',style:{color:_slColor}},_slCaption),
      ),
    ),
    React.createElement('label',{className:'flex items-baseline justify-between cursor-pointer gap-2 mb-2 pt-2',style:{borderTop:'1px solid #1B1B22'}},
      React.createElement('div',null,
        React.createElement('div',{className:'text-[11px] font-bold text-[#EDEDED]/85'},'Mission Mode'),
        React.createElement('div',{className:'text-[9px] text-[#EDEDED]/45 mt-0.5'},'Kelly-scaled sizing, grows the stake as bankroll grows'),
      ),
      React.createElement('input',{
        type:'checkbox',checked:_isActive,onChange:_toggleMission,
        className:'w-4 h-4 accent-[#23B981]',
      }),
    ),
    _bootstrap&&React.createElement('div',{className:'p-2 rounded-lg mb-2',style:{background:'rgba(35,185,129,0.04)',border:'1px solid rgba(35,185,129,0.20)'}},
      React.createElement('div',{className:'grid grid-cols-2 gap-2 mb-2'},
        React.createElement('label',{className:'block'},
          React.createElement('div',{className:'text-[9px] text-[#EDEDED]/55 mb-1'},'Starting bankroll ($)'),
          React.createElement('input',{
            type:'number',min:1,max:100000,step:1,value:_bootstrap.startBankroll,
            onChange:(e)=>_setBootstrap(prev=>({...prev,startBankroll:Math.max(1,Math.min(100000,_num(e.target.value,25)))})),
            className:'w-full bg-transparent border border-[#2A2A34] rounded-lg px-2 py-1 text-white text-sm tabular-nums focus:border-[#23B981] focus:outline-none',
          }),
        ),
        React.createElement('label',{className:'block'},
          React.createElement('div',{className:'text-[9px] text-[#EDEDED]/55 mb-1'},'Target ($)'),
          React.createElement('input',{
            type:'number',min:2,max:1000000,step:1,value:_bootstrap.target,
            onChange:(e)=>_setBootstrap(prev=>({...prev,target:Math.max(2,Math.min(1000000,_num(e.target.value,50)))})),
            className:'w-full bg-transparent border border-[#2A2A34] rounded-lg px-2 py-1 text-white text-sm tabular-nums focus:border-[#23B981] focus:outline-none',
          }),
        ),
      ),
      React.createElement('div',{className:'flex gap-2'},
        React.createElement('button',{
          onClick:_startWithDefaults,
          className:'flex-1 px-2 py-1.5 rounded-lg text-[10px] uppercase font-bold tracking-wider',
          style:{background:'rgba(35,185,129,0.15)',color:'#23B981',border:'1px solid rgba(35,185,129,0.40)'},
        },'Start mission'),
        React.createElement('button',{
          onClick:()=>_setBootstrap(null),
          className:'px-2 py-1.5 rounded-lg text-[10px] uppercase tracking-wider text-[#EDEDED]/55',
        },'Cancel'),
      ),
      React.createElement('div',{className:'text-[9px] text-[#EDEDED]/40 mt-1.5'},'7-day deadline, 20% drawdown floor, quarter-Kelly — expand Mission in advanced settings to change these.'),
    ),
    _isActive
      ?React.createElement('div',{className:'p-2 rounded-lg',style:{background:'rgba(35,185,129,0.04)',border:'1px solid rgba(35,185,129,0.20)'}},
          React.createElement('div',{className:'text-[11px] text-[#EDEDED]/65'},'bankroll $',Number(mission.currentBankroll||0).toFixed(2),' → target $',Number(mission.target||0).toFixed(2)),
          React.createElement('button',{
            onClick:_expandAdvancedMission,
            className:'text-[9px] uppercase tracking-wider text-[#23B981] mt-1.5',
          },'Expand Mission settings ↓'),
        )
      :React.createElement('div',null,
          React.createElement('div',{className:'text-[10px] text-[#EDEDED]/65 mb-1.5'},'Max bet / trade'),
          React.createElement('div',{className:'flex flex-wrap gap-1.5'},
            PRESETS.map(v=>React.createElement('button',{
              key:v,
              onClick:()=>setAutoExecSettings(prev=>({...prev,maxBetPerTrade:Math.max(1,Math.min(500,v))})),
              className:'px-3 py-1.5 rounded-lg text-[11px] font-bold tabular-nums',
              style:_cap===v
                ?{background:'rgba(35,185,129,0.18)',color:'#23B981',border:'1px solid rgba(35,185,129,0.45)'}
                :{background:'transparent',color:'rgba(237,237,237,0.65)',border:'1px solid #2A2A34'},
            },'$',v)),
          ),
          !PRESETS.includes(_cap)&&React.createElement('div',{className:'text-[9px] text-[#EDEDED]/40 mt-1.5'},`custom: $${_cap.toFixed(2)} — use Advanced to fine-tune`),
        ),
  );
}

// ── V8.2: ANTI-TILT COOLDOWN BANNER ──────────────────────────────────────────
// V13.4.255: AntiTiltCooldownBanner removed. It rendered only when tiltLockUntil
//   was in the future, and the effect that set it was hard-disabled in V10.4.1a
//   ("antiTilt merged into autoExec cooldown") -- an effect that now returns
//   before its own body. tiltLockUntil is therefore only ever set to 0, so this
//   banner could not render. The autoExec cooldown it deferred to does not
//   enforce anything either (see the note at _runEntry, v13.4.251).

// ── V8.2: ASSET ROTATION HINT ────────────────────────────────────────────────
// V13.4.255: AssetRotationHint removed. It suggested rotating to a better-
//   performing asset, but _otherAsset is hardcoded to BTC -- the same asset as
//   current -- so the two win rates it compared were always the same set and the
//   difference could never clear the 15-point threshold. ETH support was removed
//   long before this.

// ── V9.3.0: DUAL-ASSET CALL STRIP ─────────────────────────────────────────────
// Side-by-side BTC + ETH calls so the user sees Tara's read on both assets
// simultaneously. The active asset shows the LIVE engine output (taraCall).
// The inactive asset shows the SHADOW engine output (computed every 2s in the
// background by the shadow useEffect at L15322).
// Click the inactive side to switch to it as the active asset.
//
// Shadow data shape (from shadowTaraByAssetRef.current[asset]):
//   {leanDir, confidence, posterior, strike, currentPrice, regime, kalshiYesCents,
//    marketTicker, marketCloseTime, windowId, sampleCount, committedDir, conviction,
//    atrBps, updatedAt}
function DualAssetCallStrip({currentAsset,onSwitch,taraCall,kalshiYesPrice,currentPrice,targetMargin,shadowRef,diagRef,timeState}){
  // 1Hz tick to refresh shadow display (shadow data lives in a ref, doesn't auto-rerender)
  const[,_t]=React.useState(0);
  React.useEffect(()=>{const iv=setInterval(()=>_t(x=>x+1),1000);return()=>clearInterval(iv);},[]);
  // V9.6.1: tap-to-show diagnostic when shadow is stale. Expanded state per card.
  const[diagOpen,setDiagOpen]=React.useState(null);
  const _otherAsset='BTC';
  const _shadow=shadowRef?.current?.[_otherAsset];
  const _shadowFresh=_shadow&&(Date.now()-(_shadow.updatedAt||0))<8000;
  const _shadowDiag=diagRef?.current?.[_otherAsset];
  // Active card: from live engine
  const _activeDir=(taraCall?.call==='UP'||taraCall?.call==='DOWN')?taraCall.call:taraCall?.direction||null;
  const _activeConv=taraCall?.confidence||taraCall?.conviction||0;
  const _activePhase=taraCall?.phase||(taraCall?.call==='SIT_OUT'?'WATCHING':'');
  const _activeRegime=taraCall?.regime||'';
  const _activeKalshi=Number(kalshiYesPrice);
  const _activeKalshiOk=Number.isFinite(_activeKalshi)&&_activeKalshi>0;
  const _activeKalshiForDir=_activeKalshiOk&&_activeDir?(_activeDir==='UP'?_activeKalshi:(100-_activeKalshi)):null;
  // Shadow card
  const _shDir=_shadowFresh?(_shadow.committedDir||(_shadow.leanDir!=='NEUTRAL'?_shadow.leanDir:null)):null;
  const _shConf=_shadowFresh?_shadow.confidence:0;
  const _shCommitted=_shadowFresh&&!!_shadow.committedDir;
  const _shKalshi=_shadowFresh?Number(_shadow.kalshiYesCents):null;
  const _shKalshiOk=Number.isFinite(_shKalshi)&&_shKalshi>0&&_shKalshi<100;
  const _shKalshiForDir=_shKalshiOk&&_shDir?(_shDir==='UP'?_shKalshi:(100-_shKalshi)):null;
  const _shCfg=ASSET_CONFIG[_otherAsset]||{};
  const _activeCfg=ASSET_CONFIG[currentAsset]||{};
  const _renderCard=({asset,cfg,dir,conf,phase,regime,kalshiForDir,kalshiYes,price,strike,isActive,committed,onClick,sampleCount,stale})=>{
    const _color=cfg.color||'#23B981';
    const _dirColor=dir==='UP'?'rgb(35,185,129)':dir==='DOWN'?'#E8455E':'rgba(237,237,237,0.55)';
    return React.createElement('div',{
      onClick:onClick,
      className:`flex-1 min-w-0 rounded-lg overflow-hidden ${onClick?'cursor-pointer hover:bg-white/5':''} transition-colors`,
      style:{
        border:isActive?`1px solid ${_color}66`:'1px solid #24242E',
        background:isActive?`${_color}0E`:'rgba(10,10,10,0.40)',
      },
    },
      React.createElement('div',{className:'px-2.5 py-1 flex items-baseline justify-between gap-1 border-b border-[#24242E]'},
        React.createElement('div',{className:'flex items-baseline gap-1.5 min-w-0'},
          React.createElement('span',{className:'text-base leading-none shrink-0',style:{color:_color}},cfg.icon||'?'),
          React.createElement('span',{className:'text-[10px] uppercase font-bold tracking-wider shrink-0',style:{color:isActive?_color:'rgba(237,237,237,0.55)'}},cfg.label||asset),
          isActive?React.createElement('span',{className:'text-[8px] uppercase tracking-wider px-1 py-0.5 rounded-lg shrink-0',style:{color:_color,background:`${_color}22`,border:`1px solid ${_color}44`}},'LIVE'):React.createElement('span',{className:'text-[8px] uppercase tracking-wider px-1 py-0.5 rounded-lg shrink-0',style:{color:'rgba(237,237,237,0.45)',border:'1px solid rgba(237,237,237,0.15)'}},'SHADOW'),
          stale&&React.createElement('button',{
            onClick:(e)=>{e.stopPropagation();setDiagOpen(diagOpen===asset?null:asset);},
            className:'text-[8px] uppercase tracking-wider text-rose-400/70 shrink-0 hover:text-rose-300 underline decoration-dotted underline-offset-2',
            title:'tap for diagnostic',
          },'stale ⓘ'),
        ),
        price>0&&React.createElement('span',{className:'text-[10px] tabular-nums text-[#EDEDED]/55 shrink-0'},'$',Math.round(price).toLocaleString()),
      ),
      React.createElement('div',{className:'px-2.5 py-1.5 flex items-center justify-between gap-2'},
        React.createElement('div',{className:'min-w-0'},
          dir?React.createElement('div',{className:'flex items-baseline gap-1.5'},
            React.createElement('span',{className:'text-lg font-bold leading-none',style:{color:_dirColor}},dir==='UP'?'▲':'▼',' ',dir),
            React.createElement('span',{className:'text-[11px] tabular-nums font-bold',style:{color:_dirColor}},Math.round(conf||0),'%'),
            committed===true&&React.createElement('span',{className:'text-[8px] uppercase font-bold px-1 py-0.5 rounded-lg',style:{color:'rgb(35,185,129)',background:'rgba(35,185,129,0.10)',border:'1px solid rgba(35,185,129,0.30)'}},'committed'),
            (sampleCount&&sampleCount>0&&!committed)&&React.createElement('span',{className:'text-[9px] text-[#EDEDED]/45'},'sample ',sampleCount,'/2'),
          ):React.createElement('div',{className:'text-[11px] text-[#EDEDED]/55 italic'},stale?'feed stale':phase==='SEARCH'?'searching…':'no clear lean'),
          // V9.6.1: diagnostic panel — shown when user taps the "stale ⓘ" indicator
          (diagOpen===asset&&_shadowDiag)&&React.createElement('div',{className:'mt-1.5 p-2 rounded-lg text-[9px] space-y-0.5',style:{background:'rgba(232,69,94,0.06)',border:'1px solid rgba(232,69,94,0.20)'}},
            React.createElement('div',{className:'text-[#EDEDED]/70'},
              'attempts: ',React.createElement('span',{className:'font-bold tabular-nums text-white'},_shadowDiag.ticksAttempted),
              ' · success: ',React.createElement('span',{className:'font-bold tabular-nums',style:{color:_shadowDiag.ticksSucceeded>0?'#23B981':'#E8455E'}},_shadowDiag.ticksSucceeded),
              ' (',_shadowDiag.ticksAttempted>0?Math.round(_shadowDiag.ticksSucceeded/_shadowDiag.ticksAttempted*100):0,'%)',
            ),
            (_shadowDiag.candleFails+_shadowDiag.candleEmpty+_shadowDiag.priceMissing+_shadowDiag.kalshiFails+_shadowDiag.engineErrors)>0&&React.createElement('div',{className:'text-[#EDEDED]/55 truncate'},
              _shadowDiag.candleFails>0?`candles: ${_shadowDiag.candleFails} `:'',
              _shadowDiag.candleEmpty>0?`empty: ${_shadowDiag.candleEmpty} `:'',
              _shadowDiag.priceMissing>0?`no-price: ${_shadowDiag.priceMissing} `:'',
              _shadowDiag.kalshiFails>0?`kalshi: ${_shadowDiag.kalshiFails} `:'',
              _shadowDiag.engineErrors>0?`engine: ${_shadowDiag.engineErrors}`:'',
            ),
            _shadowDiag.lastSuccess>0&&React.createElement('div',{className:'text-[#EDEDED]/55'},'last ok: ',Math.round((Date.now()-_shadowDiag.lastSuccess)/1000),'s ago'),
            _shadowDiag.lastError&&React.createElement('div',{className:'text-rose-300/80 break-words'},React.createElement('strong',null,'last err: '),_shadowDiag.lastError),
          ),
          React.createElement('div',{className:'text-[9px] text-[#EDEDED]/45 mt-0.5 truncate'},
            regime?(regime.length>20?regime.slice(0,20)+'…':regime):'—',
            strike>0?React.createElement('span',null,' · strike $',Math.round(strike).toLocaleString()):null,
          ),
        ),
        kalshiForDir!=null&&React.createElement('div',{className:'text-right shrink-0'},
          React.createElement('div',{className:'text-[8px] uppercase tracking-wider text-[#EDEDED]/45'},'kalshi ',dir||''),
          React.createElement('div',{className:'text-[12px] tabular-nums font-bold',style:{color:kalshiForDir>=70?'rgb(35,185,129)':kalshiForDir>=55?'#23B981':kalshiForDir<=30?'#E8455E':'rgba(237,237,237,0.65)'}},Math.round(kalshiForDir),'%'),
        ),
      ),
    );
  };
  return React.createElement('div',{className:'mb-2 sm:mb-3 flex gap-2 items-stretch'},
    _renderCard({
      asset:currentAsset,
      cfg:_activeCfg,
      dir:_activeDir,
      conf:_activeConv,
      phase:_activePhase,
      regime:_activeRegime,
      kalshiForDir:_activeKalshiForDir,
      kalshiYes:_activeKalshi,
      price:currentPrice,
      strike:targetMargin,
      isActive:true,
      committed:_activePhase==='FORMING'||_activePhase==='COMMITTED'||(_activeDir&&_activeConv>=70),
      onClick:null,
      stale:false,
    }),
    _renderCard({
      asset:_otherAsset,
      cfg:_shCfg,
      dir:_shDir,
      conf:_shConf,
      phase:'',
      regime:_shadowFresh?_shadow.regime:'',
      kalshiForDir:_shKalshiForDir,
      kalshiYes:_shKalshi,
      price:_shadowFresh?_shadow.currentPrice:0,
      strike:_shadowFresh?_shadow.strike:0,
      isActive:false,
      committed:_shCommitted,
      onClick:onSwitch?()=>onSwitch(_otherAsset):null,
      sampleCount:_shadowFresh?_shadow.sampleCount:0,
      stale:!_shadowFresh,
    }),
  );
}

// ── V8.2: PRE-WINDOW PREP CARD ────────────────────────────────────────────────
// Appears 30s before next window opens. Shows hour-WR for this day-of-week,
// today's streak, macro status. Auto-hides when window starts.
function PreWindowPrepCard({timeState,bestWindowsToday,todayData,marketCtx}){
  const _secLeft=(timeState?.minsRemaining||0)*60+(timeState?.secsRemaining||0);
  const _winSec=15*60; // 15min windows
  const _showFrom=30,_showTo=2;
  if(_secLeft>_showFrom||_secLeft<_showTo)return null;
  // Find the upcoming hour from bestWindowsToday's schedule
  const _curUtcH=new Date().getUTCHours();
  const _nextH=(_curUtcH+1)%24;
  const _bestEntry=bestWindowsToday?.best?.find(b=>b.hour===_nextH);
  const _isMacroSoon=marketCtx?.macro?.state==='BLACKOUT'||marketCtx?.macro?.state==='OBSERVE';
  return React.createElement('div',{
    className:'rounded-lg overflow-hidden mb-2 sm:mb-3 px-3 sm:px-4 py-2',
    style:{
      border:'1px solid rgba(35,185,129,0.30)',
      background:'rgba(35,185,129,0.04)',
    },
  },
    React.createElement('div',{className:'flex items-baseline justify-between gap-2 flex-wrap'},
      React.createElement('div',{className:'flex items-baseline gap-2 flex-wrap min-w-0'},
        React.createElement('span',{className:'text-[10px] uppercase font-bold tracking-wider shrink-0',style:{color:T2_GOLD}},'Next window in'),
        React.createElement('span',{className:'text-[11px] tabular-nums font-bold text-white'},_secLeft,'s'),
        _bestEntry&&React.createElement('span',{className:'text-[10px] text-[#EDEDED]/65'},'· this hour historical: ',_bestEntry.wr,'% (',_bestEntry.total,' trades)'),
        todayData?.streak>=2&&React.createElement('span',{className:'text-[10px]',style:{color:todayData.streakType==='hot'?'#23B981':'#23B981'}},
          '· today: ',todayData.streak,todayData.streakType==='hot'?'W':'L',' streak',
        ),
        _isMacroSoon&&React.createElement('span',{className:'text-[10px] font-bold',style:{color:'#E8455E'}},'· ⚠ macro event imminent'),
      ),
    ),
  );
}

// ── V8.2: LAST LOSS POST-MORTEM ─────────────────────────────────────────────
// After a fresh LOSS, shows a small dismissable card explaining what disagreed.
// Auto-dismisses 60s after appearing. User can also dismiss with X.
function LastLossPostmortem({todayData,taraCallLog,currentAsset}){
  const[dismissedId,setDismissedId]=React.useState(null);
  const _src=(taraCallLog||[]).filter(e=>e&&(e.asset||'BTC')===currentAsset&&e.result==='LOSS');
  const _lastLoss=_src.length>0?_src[_src.length-1]:null;
  if(!_lastLoss)return null;
  if(dismissedId===_lastLoss.id)return null;
  // Only show if loss was within last 5 min
  const _ageSec=(Date.now()-_lastLoss.time)/1000;
  if(_ageSec>300)return null;
  // Surface the strongest dissenting signal that was right
  const _signals=_lastLoss.signals||{};
  const _outcomeDir=_lastLoss.outcomeDir||(_lastLoss.dir==='UP'?'DOWN':'UP'); // since LOSS, opposite was right
  const _outcomeSign=_outcomeDir==='UP'?1:-1;
  let _dissenter=null,_dissenterScore=-Infinity;
  Object.entries(_signals).forEach(([k,v])=>{
    if(typeof v!=='number')return;
    if(Math.sign(v)===_outcomeSign&&Math.abs(v)>1){ // signal pointed toward winner
      if(Math.abs(v)>_dissenterScore){_dissenter=k;_dissenterScore=Math.abs(v);}
    }
  });
  // V8.5: Loss-pattern context
  const _pattern=_lastLoss.lossPattern||null;
  const _patternMeta=_pattern&&LOSS_PATTERN_LABELS[_pattern];
  const _isShock=_pattern&&SHOCK_LOSS_PATTERNS.has(_pattern);
  // Color shifts for shock losses (purple — circumstantial, not signal failure)
  const _border=_isShock?'rgba(168,85,247,0.30)':'rgba(232,69,94,0.25)';
  const _bg=_isShock?'rgba(168,85,247,0.05)':'rgba(232,69,94,0.04)';
  const _accent=_isShock?'rgba(168,85,247,0.95)':'#E8455E';
  // Path summary line if metrics exist
  const _hasPath=_lastLoss.maxFavorableExcursionBps!=null||_lastLoss.last60sDriftBps!=null;
  return React.createElement('div',{
    className:'rounded-lg overflow-hidden mb-2 sm:mb-3 px-3 sm:px-4 py-2',
    style:{border:'1px solid '+_border,background:_bg},
  },
    React.createElement('div',{className:'flex items-baseline justify-between gap-2 flex-wrap'},
      React.createElement('div',{className:'flex items-baseline gap-2 flex-wrap min-w-0 flex-1'},
        React.createElement('span',{className:'text-[10px] uppercase font-bold tracking-wider shrink-0',style:{color:_accent}},
          _patternMeta?_patternMeta.icon+' '+_patternMeta.label:'Loss recap'),
        React.createElement('span',{className:'text-[11px] text-[#EDEDED]/75'},
          _lastLoss.dir,' · ',_lastLoss.regime||'no regime',' · ',Math.round(_lastLoss.confidence||0),'% conf · closed ',_outcomeDir,
        ),
        _dissenter&&!_isShock&&React.createElement('span',{className:'text-[10px] text-[#EDEDED]/55'},
          '· ',_dissenter,' (',_dissenterScore.toFixed(1),') saw it right',
        ),
      ),
      React.createElement('button',{
        onClick:()=>setDismissedId(_lastLoss.id),
        className:'text-[#EDEDED]/35 hover:text-[#EDEDED]/70 text-xs leading-none px-1 shrink-0',
        title:'Dismiss',
      },'×'),
    ),
    // V8.5: Pattern hint + path metrics summary on second line if available
    (_patternMeta||_hasPath)&&React.createElement('div',{className:'mt-1 text-[10px] text-[#EDEDED]/55 leading-snug flex flex-wrap gap-x-2 gap-y-0.5'},
      _patternMeta&&React.createElement('span',{className:'italic'},_patternMeta.hint),
      _lastLoss.maxFavorableExcursionBps!=null&&React.createElement('span',{className:'tabular-nums'},
        '· peak ',(_lastLoss.maxFavorableExcursionBps>0?'+':''),_lastLoss.maxFavorableExcursionBps,'bps',
        _lastLoss.peakClockSec!=null?` @ ${_lastLoss.peakClockSec}s left`:'',
      ),
      _lastLoss.last60sDriftBps!=null&&Math.abs(_lastLoss.last60sDriftBps)>=3&&React.createElement('span',{className:'tabular-nums'},
        '· last 60s ',(_lastLoss.last60sDriftBps>0?'+':''),_lastLoss.last60sDriftBps,'bps',
      ),
      _isShock&&React.createElement('span',{className:'text-[10px] font-bold',style:{color:'rgba(168,85,247,0.85)'}},
        '· learning dampened (signal was right)',
      ),
    ),
  );
}


// ── V9.10.2: UNIFIED TODAY + PERFORMANCE + INSIGHT CARD ────────────────────
// Replaces three previously-stacked components:
//   • DailyInsightCard (one-line dynamic insight, dismissable)
//   • TodayCard (W/L count, heatmap, P&L curve, volatility, best-hours)
//   • PerformanceCard (7d/30d/lifetime WR, edge bucket WR, Kelly)
//
// Designed around the user's actual decision workflow: glance to calibrate
// confidence ("am I hot or cold today? does it match recent record?") rather
// than read every stat. Three zones, top-to-bottom by attention priority:
//   Zone 1 (always visible): today W/L count + WR color + recent calls heatmap
//   Zone 2 (always visible): today vs 7d delta + edge sparkline + insight line
//   Zone 3 (expandable):     full 7d/30d/lifetime, P&L curve, volatility
//
// The expandable zone defaults collapsed so most-of-the-time view is just
// Zones 1+2 — about 1/3 the vertical space of the three old cards stacked.
function UnifiedTodayCard({todayData,bestWindowsToday,tickHistoryRef,upcomingMacro,timeFormat,settings}){
  const[expanded,setExpanded]=React.useState(()=>{
    try{return localStorage.getItem('taraUnifiedTodayExpanded_v1')==='true';}catch(_){return false;}
  });
  const _toggle=()=>setExpanded(p=>{const n=!p;try{localStorage.setItem('taraUnifiedTodayExpanded_v1',String(n));}catch(_){}return n;});

  if(!todayData)return null;
  const{wins=0,losses=0,sitouts=0,wr,recentForHeatmap=[],todayCalls=[],wr7d,wr30d,wrLifetime,edgeBuckets,strongTilt,heater,streak}=todayData;
  const _hasData=(wins+losses)>0||recentForHeatmap.length>0;
  if(!_hasData)return null;

  // Today vs 7d delta — calibrates "trust today's read"
  const _delta7d=(wr!=null&&wr7d?.wr!=null&&wr7d.total>=10)?(wr-wr7d.wr):null;
  const _deltaColor=_delta7d==null?'rgba(237,237,237,0.5)':
    _delta7d>=10?'rgb(35,185,129)':
    _delta7d>=-5?'rgba(237,237,237,0.7)':
    _delta7d>=-12?'#23B981':
    'rgb(232,69,94)';
  const _deltaLabel=_delta7d==null?null:
    _delta7d>=10?'hot':
    _delta7d>=-5?'on pace':
    _delta7d>=-12?'cooling':
    'cold';

  // WR color helper (shared)
  const _wrColor=(w,total)=>{
    if(w==null||total<5)return'rgba(237,237,237,0.4)';
    return w>=70?'rgb(35,185,129)':w>=55?'rgba(237,237,237,0.85)':w>=45?'#23B981':'rgb(232,69,94)';
  };

  // ZONE 2 insight — single line, no dismiss button (just doesn't render when nothing applies)
  let insight=null,insightColor='rgba(237,237,237,0.7)';
  const _eb=edgeBuckets||{};
  const _tightTotal=(_eb.tight?.wins||0)+(_eb.tight?.losses||0);
  const _tightWR=_tightTotal>0?(_eb.tight.wins/_tightTotal):0;
  const _bigTotal=(_eb['big-edge']?.wins||0)+(_eb['big-edge']?.losses||0);
  const _bigWR=_bigTotal>0?(_eb['big-edge'].wins/_bigTotal):0;
  if(strongTilt){insight=`Tilt: ${streak} losses in a row — step away for 15-30m`;insightColor='rgb(232,69,94)';}
  else if(heater){insight=`Heater: ${streak}-win streak — trust read, don't oversize`;insightColor='rgb(35,185,129)';}
  else if(_tightTotal>=8&&_tightWR<0.5&&_bigTotal>=5&&_bigWR>0.65){insight=`TIGHT edges ${Math.round(_tightWR*100)}% · BIG edges ${Math.round(_bigWR*100)}% — wait for clear edge`;insightColor='rgba(35,185,129,0.9)';}
  else if(bestWindowsToday?.nextBest&&bestWindowsToday.nextBest.minsUntil<120){insight=`Best ${bestWindowsToday.dayName} hour (${String(bestWindowsToday.nextBest.hour).padStart(2,'0')}:00) in ${bestWindowsToday.nextBest.minsUntil}m · ${bestWindowsToday.nextBest.wr}% WR`;insightColor='#23B981';}
  else if(_delta7d!=null&&_delta7d<=-12){insight=`Today running ${Math.abs(_delta7d).toFixed(0)}pp under 7d — regime may not fit current weights`;insightColor='#23B981';}

  // Edge bucket micro-bars for Zone 2
  const _edgeBars=edgeBuckets?[
    {key:'big-edge',label:'BIG',color:'rgb(35,185,129)'},
    {key:'good-edge',label:'GOOD',color:'rgba(35,185,129,0.7)'},
    {key:'tight',label:'TIGHT',color:'#23B981'},
    {key:'late',label:'LATE',color:'#E8455E'},
  ]:[];

  return React.createElement('div',{
    className:'rounded-lg overflow-hidden mb-2 sm:mb-3',
    style:{border:'1px solid #24242E',background:'rgba(237,237,237,0.015)'},
  },
    // ── ZONE 1: Header + today summary (always visible) ──
    React.createElement('div',{
      className:'flex items-center justify-between gap-2 sm:gap-3 px-3 sm:px-4 py-2 cursor-pointer hover:bg-[#EDEDED]/3',
      onClick:_toggle,
    },
      React.createElement('div',{className:'flex items-baseline gap-2 sm:gap-3 min-w-0 flex-1 flex-wrap'},
        React.createElement('span',{className:'text-[10px] uppercase tracking-[0.18em] font-bold shrink-0',style:{color:T2_GOLD}},'Today'),
        React.createElement('span',{className:'text-[11px] tabular-nums text-[#EDEDED]/75 font-bold shrink-0'},`${wins}W·${losses}L`,sitouts>0?` · ${sitouts}so`:''),
        sitouts>0&&todayData.sitoutMix&&React.createElement('span',{className:'text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded-lg font-bold shrink-0',style:{color:todayData.sitoutMix.color,background:todayData.sitoutMix.bg},title:'Sit-out mix - '+todayData.sitoutMix.detail+'. Priced-out (no payout) and fade (deep underdog) passes are always correct; a high coinflip count is the only sign the gates may be over-tightening on tradeable windows.'},todayData.sitoutMix.verdict),
        wr!=null&&React.createElement('span',{className:'text-[10px] tabular-nums shrink-0',style:{color:_wrColor(wr,wins+losses)}},`${wr}% WR`),
        // Delta vs 7d — the "should I trust today?" cue
        _delta7d!=null&&React.createElement('span',{
          className:'text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded-lg font-bold shrink-0',
          style:{color:_deltaColor,background:`${_deltaColor.replace('rgb','rgba').replace(')',',0.10)')}`,border:`1px solid ${_deltaColor}33`},
          title:`Today ${wr}% vs 7-day ${wr7d.wr}% (${_delta7d>0?'+':''}${_delta7d.toFixed(0)}pp)`,
        },_deltaLabel),
        bestWindowsToday&&bestWindowsToday.inBestWindow&&React.createElement('span',{className:'text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded-lg font-bold shrink-0',style:{background:'rgba(35,185,129,0.10)',color:'#23B981',border:'1px solid rgba(35,185,129,0.25)'}},'★ best hour'),
      ),
      React.createElement('span',{className:'text-[#EDEDED]/30 text-xs shrink-0',style:{transition:'transform 0.2s',transform:expanded?'rotate(180deg)':'rotate(0deg)'}},'▾'),
    ),
    // ── ZONE 2: Always-visible compact body (heatmap + edge sparkline + insight) ──
    React.createElement('div',{className:'px-3 sm:px-4 pb-2 space-y-2'},
      // Recent calls heatmap — biggest "hot/cold" visual
      recentForHeatmap.length>0&&React.createElement('div',null,
        React.createElement(RecentCallsHeatmap,{recent:recentForHeatmap,size:12,timeFormat}),
      ),
      // Edge bucket micro-bars — one row, compact
      edgeBuckets&&_edgeBars.length>0&&React.createElement('div',{className:'flex items-center gap-1.5 min-w-0'},
        React.createElement('span',{className:'text-[8px] uppercase tracking-wider text-[#EDEDED]/35 shrink-0',style:{minWidth:'46px'}},'by edge'),
        ..._edgeBars.map(({key,label,color})=>{
          const b=edgeBuckets[key]||{wins:0,losses:0};
          const total=b.wins+b.losses;
          const wrPct=total>0?Math.round((b.wins/total)*100):null;
          return React.createElement('div',{key,className:'flex items-baseline gap-1 min-w-0 flex-1',title:`${label} edge: ${b.wins}W-${b.losses}L`},
            React.createElement('span',{className:'text-[8px] uppercase tracking-wider text-[#EDEDED]/45 shrink-0'},label),
            React.createElement('span',{className:'text-[9px] tabular-nums font-bold shrink-0',style:{color:total>=3?color:'rgba(237,237,237,0.3)'}},wrPct!=null?`${wrPct}%`:'—'),
          );
        }),
      ),
      // Insight line — only when relevant
      insight&&React.createElement('div',{
        className:'text-[10px] leading-snug px-2 py-1.5 rounded-lg',
        style:{color:insightColor,background:`${insightColor.replace('rgb','rgba').replace(')',',0.06)')}`,border:`1px solid ${insightColor.replace('rgb','rgba').replace(')',',0.20)')}`},
      },insight),
    ),
    // ── ZONE 3: Expandable detail (collapsed by default) ──
    expanded&&React.createElement('div',{className:'border-t border-[#24242E] px-3 sm:px-4 py-3 space-y-3'},
      // WR by recency — 3 compact cards
      wrLifetime&&React.createElement('div',null,
        React.createElement('div',{className:'text-[8px] uppercase font-bold tracking-[0.12em] text-[#EDEDED]/40 mb-1'},'WR by recency'),
        React.createElement('div',{className:'grid grid-cols-3 gap-1.5'},
          [{label:'7d',d:wr7d},{label:'30d',d:wr30d},{label:'all',d:wrLifetime}].map(({label,d})=>(
            React.createElement('div',{key:label,className:'p-1.5 rounded-lg min-w-0',style:{background:'#0E0E12',border:'1px solid #24242E'}},
              React.createElement('div',{className:'text-[8px] uppercase tracking-wider text-[#EDEDED]/40 mb-0.5'},label),
              React.createElement('div',{className:'flex items-baseline gap-1 flex-wrap'},
                React.createElement('span',{className:'text-sm font-bold tabular-nums',style:{color:_wrColor(d?.wr,d?.total||0)}},d?.wr==null?'—':`${d.wr}%`),
                React.createElement('span',{className:'text-[9px] tabular-nums text-[#EDEDED]/40'},`${d?.wins||0}-${d?.losses||0}`),
              ),
            )
          )),
        ),
      ),
      // Full edge bucket detail
      edgeBuckets&&React.createElement('div',null,
        React.createElement('div',{className:'text-[8px] uppercase font-bold tracking-[0.12em] text-[#EDEDED]/40 mb-1'},'WR by edge vs Kalshi'),
        React.createElement('div',{className:'space-y-0.5'},
          [
            {key:'big-edge',label:'BIG',sub:'+15+',color:'rgb(35,185,129)'},
            {key:'good-edge',label:'GOOD',sub:'+5/15',color:'rgba(35,185,129,0.75)'},
            {key:'tight',label:'TIGHT',sub:'±5',color:'#23B981'},
            {key:'late',label:'LATE',sub:'<-5',color:'#E8455E'},
          ].map(({key,label,sub,color})=>{
            const b=edgeBuckets[key]||{wins:0,losses:0};
            const total=b.wins+b.losses;
            const wrPct=total>0?Math.round((b.wins/total)*100):null;
            const maxTotal=Math.max(1,Object.values(edgeBuckets).reduce((s,v)=>Math.max(s,v.wins+v.losses),1));
            const _barW=Math.max(2,Math.min(100,(total/maxTotal)*100));
            return React.createElement('div',{key,className:'flex items-baseline gap-1.5 min-w-0'},
              React.createElement('span',{className:'text-[9px] uppercase tracking-wider shrink-0 font-bold',style:{color,minWidth:'40px'}},label),
              React.createElement('span',{className:'text-[8px] tabular-nums text-[#EDEDED]/40 shrink-0',style:{minWidth:'42px'}},sub),
              React.createElement('div',{className:'flex-1 h-1.5 rounded-full overflow-hidden',style:{background:'rgba(237,237,237,0.06)'}},
                React.createElement('div',{className:'h-full rounded-full',style:{width:`${_barW}%`,background:color,opacity:total>=3?1:0.4}}),
              ),
              React.createElement('span',{className:'text-[9px] tabular-nums font-bold shrink-0',style:{color:total>=3?color:'rgba(237,237,237,0.3)',minWidth:'34px',textAlign:'right'}},wrPct!=null?`${wrPct}%`:'—'),
              React.createElement('span',{className:'text-[8px] tabular-nums text-[#EDEDED]/35 shrink-0',style:{minWidth:'32px',textAlign:'right'}},`${b.wins}-${b.losses}`),
            );
          }),
        ),
      ),
      // P&L curve
      todayCalls.filter(e=>e.result==='WIN'||e.result==='LOSS').length>=2&&React.createElement('div',null,
        React.createElement('div',{className:'flex items-baseline justify-between gap-2 mb-1'},
          React.createElement('span',{className:'text-[8px] uppercase font-bold tracking-[0.14em] text-[#EDEDED]/40'},'P&L curve'),
          React.createElement('span',{className:'text-[9px] text-[#EDEDED]/30'},'cumulative W-L'),
        ),
        React.createElement(DailyPnLCurve,{todayCalls,height:36}),
      ),
      // Volatility sparkline
      tickHistoryRef&&React.createElement('div',null,
        React.createElement('div',{className:'flex items-baseline justify-between gap-2 mb-1'},
          React.createElement('span',{className:'text-[8px] uppercase font-bold tracking-[0.14em] text-[#EDEDED]/40'},'Live volatility'),
          React.createElement('span',{className:'text-[9px] text-[#EDEDED]/30'},'2-min rolling'),
        ),
        React.createElement(VolatilitySparkline,{tickHistoryRef,height:28}),
      ),
      // Best windows hint
      bestWindowsToday&&bestWindowsToday.best&&bestWindowsToday.best.length>0&&React.createElement('div',{className:'pt-1.5 border-t border-[#24242E]'},
        React.createElement('div',{className:'flex items-baseline gap-2 mb-1'},
          React.createElement('span',{className:'text-[8px] uppercase font-bold tracking-[0.14em] text-[#EDEDED]/40'},`Best ${bestWindowsToday.dayName} hours`),
          React.createElement('span',{className:'text-[9px] text-[#EDEDED]/30'},'historical ≥3'),
        ),
        React.createElement('div',{className:'flex flex-wrap gap-1.5'},
          bestWindowsToday.best.map(h=>React.createElement('span',{
            key:h.hour,
            className:'text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded-lg tabular-nums',
            style:{background:'rgba(35,185,129,0.06)',color:'#23B981',border:'1px solid rgba(35,185,129,0.20)'},
          },`${String(h.hour).padStart(2,'0')}:00 ${h.wr}%`)),
        ),
      ),
    ),
  );
}

// V13.4.254: TPSLBanner removed. It returned null unconditionally from
//   V10.4.1a onward -- ~30 lines of unreachable JSX behind a hard return.
//   Its TP/SL settings now drive the auto-exec exit checker instead (v13.4.253).

// ── V8.3: TAB PRESENCE PILL ──────────────────────────────────────────────────
// Shows when other tabs of Tara are open in the same browser. Reassures the user
// that multi-tab is being handled (their other tab's calls aren't being lost).
const TabPresencePill=React.memo(function TabPresencePill({peerTabs}){
  if(!peerTabs||peerTabs.length===0)return null;
  // Group by asset for a compact display
  const byAsset={};
  peerTabs.forEach(([id,info])=>{
    const a=info?.currentAsset||'?';
    byAsset[a]=(byAsset[a]||0)+1;
  });
  const _label=Object.entries(byAsset).map(([a,n])=>n>1?`${n}×${a}`:a).join(' · ');
  return React.createElement('div',{
    className:'flex items-baseline gap-1 sm:gap-1.5 px-1.5 sm:px-2 py-0.5 rounded-lg shrink-0',
    style:{background:'rgba(147,197,253,0.06)',border:'1px solid rgba(147,197,253,0.22)',boxShadow:'0 3px 10px rgba(0,0,0,0.3)'},
    title:`${peerTabs.length} other tab${peerTabs.length>1?'s':''} of Tara open in this browser. Their calls and yours are being merged automatically.`,
  },
    React.createElement('span',{className:'text-[8px] uppercase tracking-wider font-bold',style:{color:'rgba(147,197,253,0.55)'}},'+'+peerTabs.length+(peerTabs.length>1?' tabs':' tab')),
    React.createElement('span',{className:'text-[10px] tabular-nums font-bold hidden sm:inline',style:{color:'rgba(147,197,253,0.95)'}},_label),
  );
})

// ── V8.1: MOVEMENT RISK PILL — REAL-TIME RISK PULSE ─────────────────────────
// Header pill showing current market risk level, directional bias, and predictive readout.
// Color/animation graded by level. Hover for full reason list + predictive text.
// ── V8.6: SYNC STATUS PILL ─────────────────────────────────────────────────
// Visible health indicator for cloud sync. Subscribes to module-level _cloudSyncStatus
// and re-renders on changes. Color-coded: green dot when healthy + recently-written,
// amber when writing in progress, rose when error. Click → opens force-resync menu.
const SyncStatusPill=React.memo(function SyncStatusPill({onClick}){
  const[status,setStatus]=React.useState(()=>({state:'idle',lastOk:null,lastError:null,writes:0,reads:0,listeners:0}));
  // V8.8.7: 750ms hysteresis on the writing label.
  // V8.9.3: 2-second hysteresis on the error label too — Firestore SDK auto-retries
  //   transactions internally, so a transient single error often resolves before the
  //   user's next render. Without delay the pill flashes red on every transient
  //   network blip even when the next write succeeds.
  const[displayWriting,setDisplayWriting]=React.useState(false);
  const[displayError,setDisplayError]=React.useState(false);
  React.useEffect(()=>{
    return subscribeCloudStatus(setStatus);
  },[]);
  React.useEffect(()=>{
    if(status.state==='writing'){
      const t=setTimeout(()=>setDisplayWriting(true),750);
      return()=>clearTimeout(t);
    }else{
      setDisplayWriting(false);
    }
  },[status.state]);
  React.useEffect(()=>{
    // V8.9.3: only show red after the error persists 2s. If a successful write
    //   clears the error before then, never display red.
    if(status.state==='error'){
      const t=setTimeout(()=>setDisplayError(true),2000);
      return()=>clearTimeout(t);
    }else{
      setDisplayError(false);
    }
  },[status.state]);
  const _now=Date.now();
  const _lastOkAgo=status.lastOk?_now-status.lastOk:null;
  // Health state derivation:
  //   disabled — no Firestore
  //   error — recent error (last 30s)
  //   writing — in-flight write
  //   stale — listeners=0 OR no successful op in 60s
  //   healthy — listeners≥1 AND recent ok in last 60s
  let _health,_color,_bg,_border,_label,_dotPulse=false;
  if(status.state==='disabled'){
    _health='disabled';_color='rgba(237,237,237,0.4)';_bg='rgba(237,237,237,0.03)';_border='rgba(237,237,237,0.10)';_label='SYNC OFF';
  } else if(status.state==='error'&&status.lastError&&(Object.keys(status.recordWriteErrors||{}).length>0||(_now-status.lastError.at)<30000)&&displayError){
    _health='error';_color='rgb(232,69,94)';_bg='rgba(232,69,94,0.06)';_border='rgba(232,69,94,0.30)';_label='SYNC ERR';_dotPulse=true;
  } else if(status.state==='writing'&&displayWriting){
    _health='writing';_color='#23B981';_bg='rgba(35,185,129,0.06)';_border='rgba(35,185,129,0.25)';_label='SYNCING';_dotPulse=true;
  } else if(status.listeners>0&&_lastOkAgo!=null&&_lastOkAgo<60000){
    _health='healthy';_color='#23B981';_bg='rgba(35,185,129,0.06)';_border='rgba(35,185,129,0.20)';_label='SYNCED';
  } else {
    _health='stale';_color='#23B981';_bg='rgba(35,185,129,0.04)';_border='rgba(35,185,129,0.20)';_label='SYNC IDLE';
  }
  const _title=[
    `Cloud sync: ${_health}`,
    `Active listeners: ${status.listeners}`,
    `Writes: ${status.writes} · Reads: ${status.reads}`,
    status.lastOk?`Last sync: ${Math.round((_now-status.lastOk)/1000)}s ago`:'No successful sync yet',
    status.lastError?`Last error: ${status.lastError.path} — ${status.lastError.message}`:null,
    onClick?'Click to force-resync from cloud':null,
  ].filter(Boolean).join('\n');
  return React.createElement('div',{
    className:'flex items-center gap-1.5 sm:gap-2 px-2 sm:px-2.5 py-1 rounded-lg shrink-0'+(onClick?' cursor-pointer hover:bg-[#EDEDED]/3':''),
    style:{background:_bg,border:'1px solid '+_border,boxShadow:'0 3px 10px rgba(0,0,0,0.3)'},
    title:_title,
    onClick,
  },
    React.createElement('span',{
      className:'inline-block w-1.5 h-1.5 rounded-full'+(_dotPulse?' animate-pulse':''),
      style:{background:_color},
    }),
    React.createElement('span',{className:'text-[8px] sm:text-[9px] uppercase tracking-wider font-bold',style:{color:_color}},_label),
  );
})

function MovementRiskPill({movementRisk}){
  if(!movementRisk||movementRisk.level==='CALM'&&movementRisk.score<5)return null;
  const{level,score,dirBias,reasons,predictive}=movementRisk;
  let _color,_bg,_border,_label,_pulse=false;
  if(level==='EXTREME'){
    _color='rgb(232,69,94)';_bg='rgba(232,69,94,0.12)';_border='rgba(232,69,94,0.45)';
    _label='RISK · EXTREME';_pulse=true;
  } else if(level==='ELEVATED'){
    _color='#23B981';_bg='rgba(35,185,129,0.08)';_border='rgba(35,185,129,0.32)';
    _label='RISK · ELEVATED';
  } else if(level==='NORMAL'){
    _color='rgba(237,237,237,0.7)';_bg='rgba(237,237,237,0.04)';_border='rgba(237,237,237,0.10)';
    _label='RISK · NORMAL';
  } else {
    _color='#23B981';_bg='rgba(35,185,129,0.06)';_border='rgba(35,185,129,0.20)';
    _label='CALM';
  }
  const _arrow=dirBias==='UP'?'↑':dirBias==='DOWN'?'↓':'';
  const _title=[
    `Movement risk: ${level} (${score}/100)`,
    predictive?`→ ${predictive}`:null,
    reasons.length>0?'\nReasons:':null,
    ...reasons.map(r=>`  · ${r.tag}`),
  ].filter(Boolean).join('\n');
  return React.createElement('div',{
    className:'flex items-baseline gap-1 sm:gap-1.5 px-1.5 sm:px-2 py-0.5 rounded-lg shrink-0'+(_pulse?' animate-pulse':''),
    style:{background:_bg,border:'1px solid '+_border},
    title:_title,
  },
    React.createElement('span',{className:'text-[9px] sm:text-[10px] uppercase font-bold tracking-wider tabular-nums',style:{color:_color}},_label),
    _arrow&&React.createElement('span',{className:'text-[11px] font-bold leading-none',style:{color:_color}},_arrow),
    score>=45&&React.createElement('span',{className:'text-[9px] tabular-nums hidden md:inline',style:{color:_color,opacity:0.7}},score),
  );
}

// ── V8.0: HEADER PILLS — TODAY P&L + STREAK/TILT AWARENESS ────────────────
// Two small pills designed to fit in the header row. Both source from todayData
// (cloud-persisted, survives reload). Visible psychological anchors so user knows
// emotional state at a glance.
const TodayPnLPill=React.memo(function TodayPnLPill({todayData,onClick}){
  if(!todayData||(todayData.wins+todayData.losses)===0)return null;
  const{wins,losses,wr,resolved,dollarPnL}=todayData;
  const _net=wins-losses;
  const _color=_net>=2?'#23B981':_net>=0?'#23B981':'#E8455E';
  // V13.4.299: the tinted fill, border and drop shadow are gone. Today's W-L is
  //   a real outcome, so it keeps green/red on the NUMBERS -- the mockup does
  //   exactly that -- but the mockup carries it as bare text in the header rather
  //   than as a raised pill.
  const _dollarLabel=dollarPnL!=null?(dollarPnL>=0?'+$':'-$')+Math.abs(dollarPnL).toFixed(2):null;
  return React.createElement('div',{
    className:'tara-today-pnl flex items-baseline gap-1.5 sm:gap-2 shrink-0'+(onClick?' cursor-pointer':''),
    title:`Tara Call record today: ${wins}W ${losses}L${todayData.sitouts>0?' '+todayData.sitouts+'so':''}${todayData.pending>0?' ('+todayData.pending+' pending)':''}${_dollarLabel?' · modeled '+_dollarLabel:''}. This is not Kalshi account profit.${onClick?' Click to configure the model stake.':''}`,
    onClick,
  },
    React.createElement('span',{className:'text-[8px] uppercase font-bold tracking-[0.12em] text-[#EDEDED]/40'},'Today'),
    React.createElement('span',{className:'text-[13px] sm:text-[15px] tabular-nums font-semibold',style:{color:_color,fontFamily:'"IBM Plex Mono",monospace'}},`${wins}W·${losses}L`),
    _dollarLabel&&React.createElement('span',{className:'text-[11px] sm:text-[12px] tabular-nums font-semibold hidden sm:inline',style:{color:_color,fontFamily:'"IBM Plex Mono",monospace'}},'CALL EST ',_dollarLabel),
    wr!=null&&React.createElement('span',{className:'text-[10px] sm:text-[11px] tabular-nums hidden lg:inline',style:{color:_color,opacity:0.8,fontFamily:'"IBM Plex Mono",monospace'}},`${wr}%`),
  );
})

const StreakTiltPill=React.memo(function StreakTiltPill({todayData}){
  if(!todayData||todayData.streak<2)return null;
  const{streak,streakType,tilt,strongTilt,heater,inCooldown,cooldownMinSinceLoss}=todayData;
  // Three states: TILT (after losses), HEATER (after wins), or just streak indicator
  let _label,_icon,_color,_bg,_border,_pulse=false;
  if(strongTilt){
    _label=`${streak}L · STEP BACK`;_icon='⚠';_color='rgb(232,69,94)';
    _bg='rgba(232,69,94,0.12)';_border='rgba(232,69,94,0.45)';_pulse=true;
  } else if(tilt){
    _label=`${streak}L · CAUTION`;_icon='⚠';_color='rgba(232,69,94,0.9)';
    _bg='rgba(232,69,94,0.07)';_border='rgba(232,69,94,0.30)';
  } else if(heater){
    _label=`${streak}W · HOT`;_icon='🔥';_color='rgb(35,185,129)';
    _bg='rgba(35,185,129,0.08)';_border='rgba(35,185,129,0.30)';
  } else if(streak>=2){
    _label=`${streak}${streakType==='hot'?'W':'L'}`;_icon=streakType==='hot'?'↗':'↘';
    _color=streakType==='hot'?'#23B981':'#23B981';
    _bg='rgba(237,237,237,0.04)';_border='rgba(237,237,237,0.10)';
  } else return null;
  // Tooltip explains the cooldown on tilt states
  const _title=tilt
    ?`${streak} losses in a row${cooldownMinSinceLoss!=null?' · '+cooldownMinSinceLoss+'m since last loss':''}. Consider half-size or skip the next round.`
    :heater
    ?`${streak} wins in a row · trust the read but don't size up beyond plan`
    :`${streak} ${streakType==='hot'?'wins':'losses'} streak`;
  return React.createElement('div',{
    className:'flex items-baseline gap-1 sm:gap-1.5 px-1.5 sm:px-2 py-0.5 rounded-lg shrink-0'+(_pulse?' animate-pulse':''),
    style:{background:_bg,border:'1px solid '+_border,boxShadow:'0 3px 10px rgba(0,0,0,0.3)'},
    title:_title,
  },
    React.createElement('span',{className:'text-[10px] shrink-0',style:{color:_color}},_icon),
    React.createElement('span',{className:'text-[9px] sm:text-[10px] uppercase font-bold tabular-nums tracking-wider',style:{color:_color}},_label),
  );
})

// ── V8.0: TODAY CARD — heatmap + P&L curve + volatility sparkline ─────────
// Collapsible card showing today's at-a-glance trader analytics. Designed to slide
// below the market context strip without cluttering. Open/closed state remembered
// in localStorage.
function RecentCallsHeatmap({recent,size=14,timeFormat}){
  if(!recent||recent.length===0){
    return React.createElement('div',{className:'flex items-center gap-1 text-[10px] text-[#EDEDED]/35 italic'},'No resolved calls yet');
  }
  return React.createElement('div',{className:'flex items-center gap-1 flex-wrap'},
    recent.map((e,i)=>{
      const _w=e.result==='WIN';
      const _bg=_w?'#23B981':'#E8455E';
      const _t=(e.time||e.id)?_fmtTimeTz(new Date(e.time||e.id),timeFormat,{hour:'2-digit',minute:'2-digit',hour12:false}):''; // V10.9.28
      return React.createElement('div',{
        key:e.id||i,
        title:`${e.result} · ${e.dir} · ${_t} · ${e.regime||''}`,
        style:{
          width:size,height:size,
          borderRadius:3,
          background:_bg,
          opacity:0.4+0.6*((i+1)/recent.length), // fade older toward left
        },
      });
    }),
  );
}

function DailyPnLCurve({todayCalls,height=40}){
  // Build cumulative P&L curve: +1 on WIN, -1 on LOSS
  const _resolved=(todayCalls||[]).filter(e=>e.result==='WIN'||e.result==='LOSS').sort((a,b)=>(a.time||a.id||0)-(b.time||b.id||0)); // V10.9.28
  if(_resolved.length<2){
    return React.createElement('div',{className:'text-[10px] text-[#EDEDED]/35 italic',style:{height}},'Need at least 2 resolved trades for curve');
  }
  let cum=0;
  const points=_resolved.map(e=>{cum+=(e.result==='WIN'?1:-1);return{time:e.time||e.id,cum};}); // V10.9.28
  const minC=Math.min(0,...points.map(p=>p.cum));
  const maxC=Math.max(0,...points.map(p=>p.cum));
  const range=Math.max(1,maxC-minC);
  const w=300,h=height;
  const _x=(i)=>(i/(points.length-1))*w;
  const _y=(c)=>h-((c-minC)/range)*h;
  const _pathD='M '+points.map((p,i)=>`${_x(i).toFixed(1)} ${_y(p.cum).toFixed(1)}`).join(' L ');
  const _last=points[points.length-1].cum;
  const _color=_last>0?'rgb(35,185,129)':_last<0?'rgb(232,69,94)':'#23B981';
  // Zero baseline if range crosses zero
  const _zeroY=minC<=0&&maxC>=0?_y(0):null;
  return React.createElement('div',{className:'relative',style:{height:h}},
    React.createElement('svg',{viewBox:`0 0 ${w} ${h}`,preserveAspectRatio:'none',style:{width:'100%',height:h,display:'block'}},
      _zeroY!=null&&React.createElement('line',{x1:0,y1:_zeroY,x2:w,y2:_zeroY,stroke:'rgba(237,237,237,0.15)',strokeWidth:0.5,strokeDasharray:'2 2'}),
      React.createElement('path',{d:_pathD,fill:'none',stroke:_color,strokeWidth:1.5,strokeLinecap:'round',strokeLinejoin:'round'}),
      points.map((p,i)=>React.createElement('circle',{key:i,cx:_x(i),cy:_y(p.cum),r:1.8,fill:_color})),
    ),
    React.createElement('div',{className:'absolute right-1 top-0 text-[10px] tabular-nums font-bold',style:{color:_color}},
      _last>=0?'+':'',_last,
    ),
  );
}

function VolatilitySparkline({tickHistoryRef,height=30}){
  // Read recent ticks; compute rolling 30s volatility (stddev of returns) over 2 min
  const[points,setPoints]=React.useState([]);
  React.useEffect(()=>{
    const update=()=>{
      const ticks=tickHistoryRef?.current||[];
      if(ticks.length<10){setPoints([]);return;}
      const _now=Date.now();
      // Bucket into 5s windows over the last 2 minutes
      const buckets=[];
      for(let off=120000;off>0;off-=5000){
        const start=_now-off,end=start+5000;
        const inB=ticks.filter(t=>t.time>=start&&t.time<end).map(t=>t.p);
        if(inB.length>=2){
          const mean=inB.reduce((s,v)=>s+v,0)/inB.length;
          const variance=inB.reduce((s,v)=>s+Math.pow(v-mean,2),0)/inB.length;
          const std=Math.sqrt(variance);
          buckets.push({time:start,vol:std});
        }
      }
      setPoints(buckets);
    };
    update();
    const iv=setInterval(update,5000);
    return()=>clearInterval(iv);
  },[tickHistoryRef]);
  if(points.length<3){
    return React.createElement('div',{className:'text-[10px] text-[#EDEDED]/35 italic',style:{height}},'Volatility data accruing…');
  }
  const w=300,h=height;
  const maxV=Math.max(...points.map(p=>p.vol),0.001);
  const _x=(i)=>(i/(points.length-1))*w;
  const _y=(v)=>h-(v/maxV)*h*0.9-2;
  const _pathD='M '+points.map((p,i)=>`${_x(i).toFixed(1)} ${_y(p.vol).toFixed(1)}`).join(' L ');
  // Color by current vol vs baseline
  const _curVol=points[points.length-1].vol;
  const _avgVol=points.reduce((s,p)=>s+p.vol,0)/points.length;
  const _spike=_curVol>_avgVol*1.5;
  const _calm=_curVol<_avgVol*0.6;
  const _color=_spike?'rgba(232,69,94,0.9)':_calm?'rgba(147,197,253,0.85)':'#23B981';
  const _label=_spike?'EXPANDING':_calm?'COMPRESSING':'STEADY';
  return React.createElement('div',{className:'relative',style:{height:h}},
    React.createElement('svg',{viewBox:`0 0 ${w} ${h}`,preserveAspectRatio:'none',style:{width:'100%',height:h,display:'block'}},
      React.createElement('path',{d:_pathD,fill:'none',stroke:_color,strokeWidth:1,strokeLinecap:'round',strokeLinejoin:'round'}),
    ),
    React.createElement('div',{className:'absolute right-1 top-0 text-[8px] uppercase tracking-wider font-bold',style:{color:_color}},_label),
  );
}

// ── V7.10.6: MARKET CONTEXT STRIP ─────────────────────────────────────────
// Shows current trading phase + cautions + next transition countdown.
// Subtle, contextual — designed to inform without dominating the UI.
// ── V9.0: TRADE SCHEDULE STRIP ────────────────────────────────────────────
// Session-aware schedule that recommends TRADE / SELECTIVE / BREAK windows
//   for the next ~10 hours. Combines three inputs:
//     1. PHASE_PROFILES static profile (liquidity, vol, deadzoneWarning)
//     2. User's actual historical WR in this phase × asset (from taraCallLog)
//     3. Macro events scheduled to land inside the phase
//
//   Tiers:
//     TRADE      (green)  — phase has high liquidity + non-compressing vol +
//                           historical WR ≥ 60% (or no prior data yet)
//     SELECTIVE  (gold)   — mixed signals; focus on confluence-only entries
//     BREAK      (dim)    — phaseProfile.deadzoneWarning OR historical WR < 50%
//                           with ≥ 10 trades OR very low liquidity. The user can
//                           step away, do other things, come back when conditions
//                           improve.
//
//   Strictly informational — does not gate Tara's calls. Tara still calls every
//   round per V8.9.2; the schedule tells the USER when to actually engage.
function TradeScheduleStrip({taraCallLog,currentAsset,timeFormat,onOpenFullSchedule}){
  const[tick,setTick]=React.useState(0);
  React.useEffect(()=>{
    const iv=setInterval(()=>setTick(t=>t+1),60000); // re-eval every minute
    return()=>clearInterval(iv);
  },[]);

  const schedule=React.useMemo(()=>{
    // Build per-phase historical WR map for current asset
    const _phaseStats={};
    (taraCallLog||[]).forEach(e=>{
      if(!e||!e.phase||(e.asset||'BTC')!==currentAsset)return;
      if(e.result!=='WIN'&&e.result!=='LOSS')return;
      const k=e.phase;
      if(!_phaseStats[k])_phaseStats[k]={W:0,L:0};
      if(e.result==='WIN')_phaseStats[k].W++;else _phaseStats[k].L++;
    });

    // V9.1.2: Phase → market-session mapping for grouping
    const _sessionOf=(pk)=>{
      if(pk==='ASIA_OPEN'||pk==='ASIA_LATE')return{key:'ASIA',label:'Asia',flag:'🌏'};
      if(pk==='EU_PREOPEN'||pk==='EU_OPEN')return{key:'EU',label:'Europe',flag:'🌍'};
      if(pk==='NY_PREMARKET'||pk==='NY_OPEN'||pk==='NY_MORNING'||pk==='NY_LUNCH'||pk==='NY_AFTERNOON'||pk==='NY_CLOSE')return{key:'US',label:'US',flag:'🌎'};
      return{key:'AFTER',label:'After Hours',flag:'🌒'};
    };

    // V9.1.2: Generate next 12 hours from CURRENT phase boundaries (not arbitrary
    //   1-hour intervals). Bug fix from V9.1.1 — was reading trans?.minsUntil but
    //   getNextPhaseTransition returns {minutesUntil}, so the fallback of 60 fired
    //   every iteration, producing 60-min intervals instead of real phase blocks.
    const out=[];
    const now=new Date();
    let cursor=new Date(now);
    let totalMin=0;
    const MAX_MIN=12*60;
    let safety=0;
    while(totalMin<MAX_MIN&&safety++<24){
      const h=cursor.getUTCHours();
      const m=cursor.getUTCMinutes();
      const phaseKey=getPhaseKey(h,m);
      const prof=PHASE_PROFILES[phaseKey]||null;
      // V9.1.2: correct field name — minutesUntil, not minsUntil
      const trans=getNextPhaseTransition(h,m);
      const minsUntilEnd=trans?.minutesUntil||60;
      const session=_sessionOf(phaseKey);

      // Recommendation tier
      const stats=_phaseStats[phaseKey];
      const N=stats?(stats.W+stats.L):0;
      const wr=N>=5?stats.W/N:null;
      let tier='SELECTIVE',tierLabel='SELECTIVE',tierColor='#23B981',tierBg='rgba(35,185,129,0.05)',tierBorder='rgba(35,185,129,0.20)';
      let reasonBits=[];
      const _isDeadzone=prof?.deadzoneWarning===true;
      const _liqLow=prof?.liquidity==='LOW';
      const _volComp=prof?.vol==='COMPRESSING';
      const _liqHigh=prof?.liquidity==='HIGH';
      const _liqMed=prof?.liquidity==='MED';

      if(_isDeadzone||(N>=10&&wr!=null&&wr<0.50)||(_liqLow&&_volComp)){
        tier='BREAK';tierLabel='BREAK';
        tierColor='rgba(237,237,237,0.45)';tierBg='rgba(237,237,237,0.03)';tierBorder='rgba(237,237,237,0.10)';
        if(_isDeadzone)reasonBits.push('thin liquidity');
        if(N>=10&&wr!=null&&wr<0.50)reasonBits.push(`you win ${Math.round(wr*100)}% here`);
        if(_liqLow&&_volComp)reasonBits.push('quiet & illiquid');
      }else if(_liqHigh&&!_volComp&&(wr==null||wr>=0.60)){
        tier='TRADE';tierLabel='TRADE';
        tierColor='#23B981';tierBg='rgba(35,185,129,0.06)';tierBorder='rgba(35,185,129,0.30)';
        if(wr!=null)reasonBits.push(`you win ${Math.round(wr*100)}% here · ${N} trades`);
        else reasonBits.push('busy market, no priors yet');
      }else{
        if(wr!=null)reasonBits.push(`you win ${Math.round(wr*100)}% here · ${N} trades`);
        if(_liqMed)reasonBits.push('medium activity');
        if(reasonBits.length===0)reasonBits.push('mixed conditions');
      }

      // Number of 15m windows in this phase
      const _windows15=Math.round(minsUntilEnd/15);

      out.push({
        phaseKey,
        session,
        label:prof?.label||phaseKey,
        flag:prof?.flag||'',
        tier,tierLabel,tierColor,tierBg,tierBorder,
        reason:reasonBits.join(' · '),
        wr,N,
        startsAt:new Date(cursor),
        endsAt:new Date(cursor.getTime()+minsUntilEnd*60000),
        minutesFromNow:totalMin,
        durationMin:minsUntilEnd,
        windows15:_windows15,
        liquidity:prof?.liquidity||'',
        vol:prof?.vol||'',
      });

      // Advance cursor by phase duration
      cursor=new Date(cursor.getTime()+minsUntilEnd*60000);
      totalMin+=minsUntilEnd;
    }
    return out;
  },[taraCallLog,currentAsset,tick]);

  const _resolvedFmt=timeFormat||'local';
  const _tzOpt=_resolvedFmt==='utc'?{timeZone:'UTC'}:_resolvedFmt==='est'?{timeZone:'America/New_York'}:undefined;
  const _fmtTime=(d)=>{
    try{return d.toLocaleTimeString('en-US',{..._tzOpt,hour12:true,hour:'numeric',minute:'2-digit'});}
    catch(_){return d.toLocaleTimeString('en-US',{hour12:true,hour:'numeric',minute:'2-digit'});}
  };
  const _fmtDuration=(min)=>{
    if(min<60)return `${min}m`;
    const h=Math.floor(min/60);const m=Math.round(min%60);
    return m===0?`${h}h`:`${h}h ${m}m`;
  };

  if(!schedule||schedule.length===0)return null;
  const current=schedule[0];

  // V9.1.2: Plain-language tier hint for the current callout
  const _currentHint=current.tier==='TRADE'?'good for trading'
    :current.tier==='BREAK'?'take a break — slow market'
    :'pick spots carefully';

  // V9.1.2: Group upcoming phases by session for readable display
  const _upcomingBySession={};
  schedule.slice(1,8).forEach(s=>{
    const k=s.session.key;
    if(!_upcomingBySession[k])_upcomingBySession[k]={session:s.session,rows:[]};
    _upcomingBySession[k].rows.push(s);
  });
  // Preserve insertion order
  const _sessionOrder=Object.keys(_upcomingBySession);

  return React.createElement('div',{className:'mb-2 px-3 py-2.5 rounded-lg min-w-0 overflow-hidden',style:{background:'#050508',border:'1px solid #24242E'}},
    // Header
    React.createElement('div',{className:'flex items-baseline justify-between mb-2'},
      React.createElement('span',{className:'text-[9px] uppercase tracking-[0.18em] font-bold',style:{color:'#23B981'}},'Today\'s Schedule'),
      React.createElement('div',{className:'flex items-center gap-2'},
        React.createElement('span',{className:'text-[9px] uppercase tracking-wider text-[#EDEDED]/35'},_resolvedFmt.toUpperCase()),
        onOpenFullSchedule&&React.createElement('button',{
          onClick:onOpenFullSchedule,
          title:'Open full schedule',
          className:'text-[10px] px-1.5 py-0.5 rounded-lg border transition-colors hover:bg-[#EDEDED]/5',
          style:{color:'#23B981',borderColor:'rgba(35,185,129,0.30)'},
        },'⊕')
      )
    ),
    // Current state callout — biggest visual emphasis with plain hint
    React.createElement('div',{
      className:'mb-2 px-3 py-2 rounded-lg flex items-center gap-2.5 min-w-0',
      style:{background:current.tierBg,border:`1px solid ${current.tierBorder}`,boxShadow:`0 0 12px ${current.tierBorder}`},
    },
      React.createElement('span',{className:'text-base shrink-0'},current.tier==='TRADE'?'🟢':current.tier==='BREAK'?'⏸':'🟡'),
      React.createElement('div',{className:'flex-1 min-w-0'},
        React.createElement('div',{className:'flex items-baseline gap-2 mb-0.5 flex-wrap'},
          React.createElement('span',{className:'text-[10px] uppercase tracking-[0.16em] font-bold shrink-0',style:{color:current.tierColor}},`Now · ${current.tierLabel}`),
          React.createElement('span',{className:'text-[10px] text-[#EDEDED]/55 truncate'},`${current.flag} ${current.label} · ${_currentHint}`),
        ),
        React.createElement('div',{className:'text-[10px] text-[#EDEDED]/65 leading-snug break-words'},
          current.reason+(current.durationMin?` · ends at ${_fmtTime(current.endsAt)} (in ${_fmtDuration(current.durationMin)})`:'')
        )
      )
    ),
    // V9.1.2: Upcoming phases grouped by market session, with real start-end time ranges
    React.createElement('div',{className:'space-y-1.5'},
      _sessionOrder.map(sk=>{
        const grp=_upcomingBySession[sk];
        return React.createElement('div',{key:sk,className:'min-w-0'},
          React.createElement('div',{className:'text-[8px] uppercase tracking-[0.20em] font-bold text-[#EDEDED]/40 mb-1 px-1'},
            `${grp.session.flag} ${grp.session.label}`
          ),
          React.createElement('div',{className:'space-y-1'},
            grp.rows.map((s,i)=>(
              React.createElement('div',{key:i,className:'flex items-center gap-2 px-2 py-1 rounded-lg text-[10px] min-w-0',style:{background:'#0E0E12',border:`1px solid ${s.tierBorder}`}},
                React.createElement('span',{className:'text-[10px] shrink-0'},s.tier==='TRADE'?'🟢':s.tier==='BREAK'?'⏸':'🟡'),
                React.createElement('span',{className:'tabular-nums text-[#EDEDED]/55 shrink-0',style:{minWidth:'92px'}},`${_fmtTime(s.startsAt)}–${_fmtTime(s.endsAt)}`),
                React.createElement('span',{className:'font-bold uppercase tracking-wider shrink-0',style:{color:s.tierColor,fontSize:'9px',minWidth:'66px'}},s.tierLabel),
                React.createElement('span',{className:'text-[#EDEDED]/55 truncate min-w-0'},`${s.flag} ${s.label}`),
                React.createElement('span',{className:'text-[#EDEDED]/35 truncate text-[9px] hidden sm:inline min-w-0'},`· ${s.windows15} windows`),
              )
            ))
          )
        );
      })
    )
  );
}

// V9.1.1: TradeScheduleModal — full-day schedule popup with extended detail.
//   Shows next 24 hours, per-phase WR breakdown, macro events, best/worst phases.
//   Triggered by the ⊕ button on TradeScheduleStrip.
function TradeScheduleModal({taraCallLog,currentAsset,timeFormat,onClose}){
  React.useEffect(()=>{
    const onKey=(e)=>{if(e.key==='Escape')onClose();};
    window.addEventListener('keydown',onKey);
    return()=>window.removeEventListener('keydown',onKey);
  },[onClose]);

  const fullSchedule=React.useMemo(()=>{
    // Build per-phase historical WR map for current asset
    const _phaseStats={};
    (taraCallLog||[]).forEach(e=>{
      if(!e||!e.phase||(e.asset||'BTC')!==currentAsset)return;
      if(e.result!=='WIN'&&e.result!=='LOSS')return;
      const k=e.phase;
      if(!_phaseStats[k])_phaseStats[k]={W:0,L:0};
      if(e.result==='WIN')_phaseStats[k].W++;else _phaseStats[k].L++;
    });

    // Generate next 24 hours of phase blocks
    const out=[];
    const now=new Date();
    let cursor=new Date(now);
    let totalMin=0;
    const MAX_MIN=24*60;
    let safety=0;
    while(totalMin<MAX_MIN&&safety++<48){
      const h=cursor.getUTCHours();
      const m=cursor.getUTCMinutes();
      const phaseKey=getPhaseKey(h,m);
      const prof=PHASE_PROFILES[phaseKey]||null;
      const trans=getNextPhaseTransition(h,m);
      const minsUntilEnd=trans?.minutesUntil||60; // V9.1.2: correct field name

      const stats=_phaseStats[phaseKey];
      const N=stats?(stats.W+stats.L):0;
      const wr=N>=5?stats.W/N:null;
      let tier='SELECTIVE',tierLabel='SELECTIVE',tierColor='#23B981',tierBg='rgba(35,185,129,0.05)',tierBorder='rgba(35,185,129,0.20)';
      const _isDeadzone=prof?.deadzoneWarning===true;
      const _liqLow=prof?.liquidity==='LOW';
      const _volComp=prof?.vol==='COMPRESSING';
      const _liqHigh=prof?.liquidity==='HIGH';
      if(_isDeadzone||(N>=10&&wr!=null&&wr<0.50)||(_liqLow&&_volComp)){
        tier='BREAK';tierLabel='BREAK';
        tierColor='rgba(237,237,237,0.45)';tierBg='rgba(237,237,237,0.03)';tierBorder='rgba(237,237,237,0.10)';
      }else if(_liqHigh&&!_volComp&&(wr==null||wr>=0.60)){
        tier='TRADE';tierLabel='TRADE';
        tierColor='#23B981';tierBg='rgba(35,185,129,0.06)';tierBorder='rgba(35,185,129,0.30)';
      }
      out.push({
        phaseKey,profile:prof,
        tier,tierLabel,tierColor,tierBg,tierBorder,
        wr,N,W:stats?.W||0,L:stats?.L||0,
        startsAt:new Date(cursor),
        minutesFromNow:totalMin,
        durationMin:minsUntilEnd,
      });
      cursor=new Date(cursor.getTime()+minsUntilEnd*60000);
      totalMin+=minsUntilEnd;
    }
    return out;
  },[taraCallLog,currentAsset]);

  // Best/worst phases by WR for current asset
  const ranked=React.useMemo(()=>{
    const stats={};
    (taraCallLog||[]).forEach(e=>{
      if(!e||!e.phase||(e.asset||'BTC')!==currentAsset)return;
      if(e.result!=='WIN'&&e.result!=='LOSS')return;
      if(!stats[e.phase])stats[e.phase]={W:0,L:0,phase:e.phase};
      if(e.result==='WIN')stats[e.phase].W++;else stats[e.phase].L++;
    });
    const arr=Object.values(stats).filter(s=>(s.W+s.L)>=5).map(s=>({
      ...s,wr:s.W/(s.W+s.L),N:s.W+s.L,
    }));
    arr.sort((a,b)=>b.wr-a.wr);
    return arr;
  },[taraCallLog,currentAsset]);

  const _resolvedFmt=timeFormat||'local';
  const _tzOpt=_resolvedFmt==='utc'?{timeZone:'UTC'}:_resolvedFmt==='est'?{timeZone:'America/New_York'}:undefined;
  const _fmtTime=(d)=>{
    try{return d.toLocaleTimeString('en-US',{..._tzOpt,hour12:true,hour:'numeric',minute:'2-digit'});}
    catch(_){return d.toLocaleTimeString('en-US',{hour12:true,hour:'numeric',minute:'2-digit'});}
  };
  const _fmtDuration=(min)=>{
    if(min<60)return `${min}m`;
    const h=Math.floor(min/60);const m=Math.round(min%60);
    return m===0?`${h}h`:`${h}h ${m}m`;
  };

  return React.createElement('div',{
    className:'fixed inset-0 z-50 flex items-start justify-center p-4 sm:p-8 overflow-y-auto',
    style:{background:'rgba(0,0,0,0.75)',backdropFilter:'blur(4px)'},
    onClick:onClose,
  },
    React.createElement('div',{
      className:'w-full max-w-3xl rounded-xl my-auto',
      style:{background:'#101014',border:'1px solid rgba(35,185,129,0.20)',boxShadow:'0 0 40px rgba(35,185,129,0.15)'},
      onClick:(e)=>e.stopPropagation(),
    },
      // Header
      React.createElement('div',{className:'flex items-center justify-between px-5 py-4',style:{borderBottom:'1px solid #24242E'}},
        React.createElement('div',{className:'flex items-baseline gap-3'},
          React.createElement('h2',{className:'font-serif text-2xl tracking-tight text-white'},'Trading Schedule'),
          React.createElement('span',{className:'text-[10px] uppercase tracking-[0.18em] font-bold',style:{color:'#23B981'}},`${currentAsset} · Next 24h · ${_resolvedFmt.toUpperCase()}`)
        ),
        React.createElement('button',{
          onClick:onClose,
          title:'Close (Esc)',
          className:'w-7 h-7 rounded-lg flex items-center justify-center text-[#EDEDED]/50 hover:text-white hover:bg-[#EDEDED]/5 transition-colors',
        },'✕')
      ),
      // Body
      React.createElement('div',{className:'px-5 py-4 max-h-[70vh] overflow-y-auto'},
        // Best/worst summary
        ranked.length>0&&React.createElement('div',{className:'mb-4 grid grid-cols-1 sm:grid-cols-2 gap-3'},
          React.createElement('div',{className:'p-3 rounded-lg',style:{background:'rgba(35,185,129,0.04)',border:'1px solid rgba(35,185,129,0.15)'}},
            React.createElement('div',{className:'text-[9px] uppercase tracking-[0.16em] font-bold mb-2',style:{color:'#23B981'}},'Best phases (you)'),
            ranked.slice(0,3).map((r,i)=>React.createElement('div',{key:i,className:'flex items-baseline justify-between text-[11px] mb-1'},
              React.createElement('span',{className:'text-[#EDEDED]/75'},(PHASE_PROFILES[r.phase]?.label||r.phase)),
              React.createElement('span',{className:'tabular-nums',style:{color:'#23B981'}},`${Math.round(r.wr*100)}% · ${r.N} trades`)
            ))
          ),
          React.createElement('div',{className:'p-3 rounded-lg',style:{background:'rgba(232,69,94,0.04)',border:'1px solid rgba(232,69,94,0.15)'}},
            React.createElement('div',{className:'text-[9px] uppercase tracking-[0.16em] font-bold mb-2',style:{color:'#E8455E'}},'Worst phases (you)'),
            ranked.slice(-3).reverse().map((r,i)=>React.createElement('div',{key:i,className:'flex items-baseline justify-between text-[11px] mb-1'},
              React.createElement('span',{className:'text-[#EDEDED]/75'},(PHASE_PROFILES[r.phase]?.label||r.phase)),
              React.createElement('span',{className:'tabular-nums',style:{color:'#E8455E'}},`${Math.round(r.wr*100)}% · ${r.N} trades`)
            ))
          )
        ),
        // Full schedule list
        React.createElement('div',{className:'space-y-1.5'},
          fullSchedule.map((s,i)=>React.createElement('div',{
            key:i,
            className:'p-2.5 rounded-lg flex items-start gap-3',
            style:{background:s.tierBg,border:'1px solid '+s.tierBorder},
          },
            React.createElement('span',{className:'text-base shrink-0'},s.tier==='TRADE'?'🟢':s.tier==='BREAK'?'⏸':'🟡'),
            React.createElement('div',{className:'flex-1 min-w-0'},
              React.createElement('div',{className:'flex items-baseline gap-2 mb-1 flex-wrap'},
                React.createElement('span',{className:'text-[10px] uppercase tracking-[0.16em] font-bold',style:{color:s.tierColor}},s.tierLabel),
                React.createElement('span',{className:'text-[11px] text-[#EDEDED]/85 font-bold'},`${s.profile?.flag||''} ${s.profile?.label||s.phaseKey}`),
                React.createElement('span',{className:'text-[10px] text-[#EDEDED]/45 tabular-nums'},`${_fmtTime(s.startsAt)} · ${_fmtDuration(s.durationMin)}`)
              ),
              React.createElement('div',{className:'text-[10px] text-[#EDEDED]/55 leading-snug mb-1'},s.profile?.character||''),
              React.createElement('div',{className:'flex items-baseline gap-3 text-[10px] flex-wrap'},
                s.wr!=null?React.createElement('span',{className:'tabular-nums',style:{color:s.wr>=0.60?'#23B981':s.wr<0.50?'#E8455E':'#23B981'}},`Your WR: ${Math.round(s.wr*100)}% (${s.W}W/${s.L}L)`):React.createElement('span',{className:'text-[#EDEDED]/35'},`No prior data yet`),
                s.profile?.liquidity&&React.createElement('span',{className:'text-[#EDEDED]/45'},`liq: ${s.profile.liquidity}`),
                s.profile?.vol&&React.createElement('span',{className:'text-[#EDEDED]/45'},`vol: ${s.profile.vol}`),
                s.profile?.coinFlipRisk&&React.createElement('span',{className:'text-[#EDEDED]/45'},`coin-flip: ${s.profile.coinFlipRisk}`)
              )
            )
          ))
        ),
        ranked.length===0&&React.createElement('div',{className:'text-center text-[11px] text-[#EDEDED]/40 italic mt-4'},'No phase-WR data yet — schedule uses default profiles. Trade more to personalize.')
      )
    )
  );
}

// V9.1.2: Plain-language helper for regime labels. Used in user-facing UI to
//   replace jargon like "TRENDING DOWN" with "going down (dump)" etc.
// V10.2.22 — RANGE-CHOP display rename. 724-trade audit (May 15 2026):
//   regime labeled RANGE-CHOP actually closes DOWN 40.3% / FLAT 39.0% / UP 20.8%
//   It's not choppy — it's slightly bearish ranging. Internal regime identifier
//   stays "RANGE-CHOP" for backward compat with stored data (regimeMemory,
//   regimeWeightKeys, regimeDirCalibration, etc.) but display labels change
//   so the user sees what the regime actually is.
const regimeToPlainLabel=(regime)=>{
  const map={
    'TRENDING UP':'going up — pump trajectory',
    'TRENDING DOWN':'going down — dump trajectory',
    'RANGE-CHOP':'range-bearish — sideways with downside lean',
    'SHORT SQUEEZE':'short squeeze — pump',
    'HIGH VOL CHOP':'wild swings — both directions',
  };
  return map[regime]||regime;
};
const regimeToShortPlain=(regime)=>{
  const map={
    'TRENDING UP':'pumping ▲',
    'TRENDING DOWN':'dumping ▼',
    'RANGE-CHOP':'range-bearish ▽',
    'SHORT SQUEEZE':'squeeze ▲',
    'HIGH VOL CHOP':'wild',
  };
  return map[regime]||regime;
};

function MarketContextStrip({useLocalTime,timeFormat,taraLearnings,taraCallLog,currentAsset,analysis,currentStreak}){
  const[ctx,setCtx]=React.useState(()=>getMarketContext(new Date()));
  const[expanded,setExpanded]=React.useState(false);
  React.useEffect(()=>{
    // Tick every 30s — phase boundaries are minute-grained but funding/macro countdown
    //   feels stale if it only updates every 60s.
    const tick=()=>setCtx(getMarketContext(new Date()));
    tick();
    const iv=setInterval(tick,30000);
    return()=>clearInterval(iv);
  },[]);
  // V9.13: SCHEDULE-AWARE ADAPTIVE indicator (display-only "Adaptive Schedule Strip"
  //   in the expanded panel below — a 24h TRADE/OBSERVE/AVOID advisory, NOT the
  //   removed timing-multiplier dial). Replaces the volatility-only auto-dial
  //   (V8.x) with one that blends: hourly schedule → streak → live volatility →
  //   today's session WR. The four-factor blend is in computeAdaptiveDial.
  //
  // Why: pure-volatility auto crowded user toward FAST in active sessions, taking
  //   marginal setups before warm-up. The schedule encodes that 12-17 EST is the
  //   user's structural weak spot (31-55% WR) and 00-04 / 20-23 are structurally
  //   strong (71-73% WR) — those facts deserve priority over instant volatility.
  //
  //   Same ping-pong guard as before: only writes when rec changed AND meaningful
  //   delta (≥5 steps) AND not at the current dial already.
  const _todaySessionWR=React.useMemo(()=>{
    if(!Array.isArray(taraCallLog))return null;
    const _curSession=getMarketSessions().dominant;
    // V10.2.x: today follows the header tz pill via _dayKey
    const _tz=timeFormat||'local';
    const _todayKey=_dayKey(new Date(),_tz);
    const _matches=taraCallLog.filter(e=>{
      if(!e||(e.result!=='WIN'&&e.result!=='LOSS'))return false;
      if((e.asset||'BTC')!==currentAsset)return false;
      if(e.session!==_curSession)return false;
      return _dayKey(e.id||e.time||0,_tz)===_todayKey;
    });
    if(_matches.length<3)return null;
    const _w=_matches.filter(e=>e.result==='WIN').length;
    return{wr:(_w/_matches.length)*100,n:_matches.length};
  },[taraCallLog,currentAsset]);
  // V10.9.7: speedAutoMode effect REMOVED — dial removed entirely, see note below.
  if(!ctx||!ctx.phase)return null;
  // V8.7.2: Compute the user's actual win rate in the CURRENT session bucket.
  //   Sessions = ASIA / EU / US (matches the session field stamped on each call).
  //   We need at least 5 resolved trades in this session to surface advice — under
  //   that, sample size is too small to be meaningful.
  let _sessionAdvice=null;
  try{
    const _curSession=ctx?.sessions?.dominant; // 'ASIA' | 'EU' | 'US' typically
    if(_curSession&&Array.isArray(taraCallLog)){
      const _sessionCalls=taraCallLog.filter(e=>{
        if(!e||(e.result!=='WIN'&&e.result!=='LOSS'))return false;
        if((e.asset||'BTC')!==currentAsset)return false;
        return e.session===_curSession;
      });
      const _n=_sessionCalls.length;
      if(_n>=5){
        const _wins=_sessionCalls.filter(e=>e.result==='WIN').length;
        const _wr=Math.round((_wins/_n)*100);
        // Tone tiers based on actual win rate — confident vs proceed-with-caution
        let _tone,_label,_text,_color;
        if(_wr>=70){
          _tone='positive';_label=`★ ${_curSession} is your zone`;_color='#23B981';
          _text=`You win ${_wr}% of the time here (${_n} trades on ${currentAsset}). This is your strongest session — trade with confidence, can size up on strong setups.`;
        } else if(_wr>=60){
          _tone='good';_label=`✓ ${_curSession} is profitable`;_color='#23B981';
          _text=`You win ${_wr}% of the time here (${_n} trades on ${currentAsset}). You're making money — trade normal size, normal selectivity.`;
        } else if(_wr>=50){
          _tone='neutral';_label=`◇ ${_curSession} is breakeven`;_color='#23B981';
          _text=`You win ${_wr}% of the time here (${_n} trades on ${currentAsset}). Just barely breakeven — go smaller, only take strong setups.`;
        } else if(_wr>=40){
          _tone='warning';_label=`⚠ ${_curSession} is unprofitable`;_color='#23B981';
          _text=`You win ${_wr}% of the time here (${_n} trades on ${currentAsset}). You're losing money in this session — half size or skip unless the setup is great.`;
        } else {
          _tone='bad';_label=`✗ ${_curSession} is your weakness`;_color='#E8455E';
          _text=`You win ${_wr}% of the time here (${_n} trades on ${currentAsset}). You usually lose in this session — better to skip until your numbers improve.`;
        }
        _sessionAdvice={tone:_tone,label:_label,text:_text,color:_color,wr:_wr,n:_n,session:_curSession};
      }
    }
  }catch(_){ /* fall through — advice card just won't appear */ }
  const _criticalCount=ctx.cautions.filter(c=>c.severity==='critical').length;
  const _warningCount=ctx.cautions.filter(c=>c.severity==='warning').length;
  // Top-level border tint by severity
  const _borderColor=_criticalCount>0?'rgba(232,69,94,0.35)':_warningCount>0?'rgba(212,160,58,0.30)':'rgba(237,237,237,0.10)';
  const _bgGradient=_criticalCount>0
    ?'rgba(232,69,94,0.05)'
    :_warningCount>0
    ?'rgba(212,160,58,0.04)'
    :'rgba(237,237,237,0.02)';
  // Format the countdown to next phase
  const _nextLabel=ctx.nextPhase.minutesUntil<60
    ?`${ctx.nextPhase.minutesUntil}m`
    :`${Math.floor(ctx.nextPhase.minutesUntil/60)}h${ctx.nextPhase.minutesUntil%60>0?(ctx.nextPhase.minutesUntil%60)+'m':''}`;
  // Pick top caution to highlight (highest severity wins)
  const _topCaution=ctx.cautions.sort((a,b)=>{
    const _o={critical:0,warning:1,info:2};
    return(_o[a.severity]||3)-(_o[b.severity]||3);
  })[0];
  return React.createElement('div',{
    className:'tara-context-strip rounded-lg overflow-hidden mb-2 sm:mb-3',
    'data-expanded':expanded,
    style:{
      border:'1px solid '+_borderColor,
      background:_bgGradient,
      transition:'border-color 0.3s ease',
    },
  },
    // Always-visible top row
    React.createElement('div',{
      className:'flex items-center justify-between gap-2 sm:gap-3 px-3 sm:px-4 py-2 cursor-pointer hover:bg-[#EDEDED]/3',
      onClick:()=>setExpanded(p=>!p),
      role:'button',tabIndex:0,'aria-expanded':expanded,
      onKeyDown:e=>{if(e.key==='Enter'||e.key===' '){e.preventDefault();setExpanded(p=>!p);}},
    },
      // Left: phase identity
      React.createElement('div',{className:'flex items-baseline gap-2 sm:gap-3 min-w-0 flex-1 flex-wrap'},
        React.createElement('span',{className:'text-base shrink-0',style:{lineHeight:1}},ctx.phase.flag),
        React.createElement('span',{className:'text-[11px] sm:text-xs uppercase font-bold tracking-[0.14em] shrink-0',style:{color:ctx.phase.color}},ctx.phase.label),
        // V9.1.2: plain-language activity + movement pills (was LIQ/VOL jargon).
        //   liquidity → activity: how much is going on right now
        //   vol → movement: are price moves growing or shrinking
        (()=>{
          const _liqMap={EXTREME:'PEAK ACTIVITY',HIGH:'BUSY',MED:'MODERATE',LOW:'QUIET'};
          const _liqLabel=_liqMap[ctx.phase.liquidity]||ctx.phase.liquidity;
          return React.createElement('span',{className:'tara-context-extra text-[8px] uppercase font-bold tracking-[0.14em] tabular-nums shrink-0 px-1.5 py-0.5 rounded-lg',style:{
            color:ctx.phase.liquidity==='EXTREME'?'rgb(35,185,129)':ctx.phase.liquidity==='HIGH'?'rgb(35,185,129)':ctx.phase.liquidity==='MED'?'#23B981':'rgba(232,69,94,0.7)',
            background:'#0E0E12',
          },title:`Liquidity: ${ctx.phase.liquidity}`},_liqLabel);
        })(),
        (()=>{
          const _volMap={EXPANDING:'MOVES GROWING',STEADY:'CALM',COMPRESSING:'MOVES SHRINKING'};
          const _volLabel=_volMap[ctx.phase.vol]||ctx.phase.vol.toLowerCase();
          return React.createElement('span',{className:'tara-context-extra text-[8px] uppercase font-bold tracking-[0.14em] tabular-nums shrink-0 px-1.5 py-0.5 rounded-lg hidden sm:inline-block',style:{
            color:ctx.phase.vol==='EXPANDING'?'#E8455E':ctx.phase.vol==='COMPRESSING'?'rgba(147,197,253,0.85)':'rgba(237,237,237,0.55)',
            background:'#0E0E12',
          },title:`Volatility: ${ctx.phase.vol}`},_volLabel);
        })(),
        // V9.1.2: "Tara sees" plain-language pill — shows what direction Tara
        //   thinks price is going, in user-friendly terms (pumping/dumping/sideways).
        analysis?.regime&&React.createElement('span',{
          className:'tara-context-extra text-[8px] uppercase font-bold tracking-[0.14em] tabular-nums shrink-0 px-1.5 py-0.5 rounded-lg',
          style:{
            color:analysis.regime==='TRENDING UP'||analysis.regime==='SHORT SQUEEZE'?'rgb(35,185,129)'
              :analysis.regime==='TRENDING DOWN'?'#E8455E'
              :'rgba(237,237,237,0.65)',
            background:'#0E0E12',
            border:'1px solid #24242E',
          },
          title:`Tara's read: ${regimeToPlainLabel(analysis.regime)}`,
        },`tara: ${regimeToShortPlain(analysis.regime)}`),
        // V9.2.3: STREAK BADGE — shows current win/loss streak prominently.
        //   Win streak ≥3: green with fire emoji. Loss streak ≥3: rose with caution.
        currentStreak&&currentStreak.count>=3&&React.createElement('span',{
          className:'text-[8px] uppercase font-bold tracking-[0.14em] tabular-nums shrink-0 px-1.5 py-0.5 rounded-lg',
          style:{
            color:currentStreak.type==='WIN'?'rgb(35,185,129)':'#E8455E',
            background:currentStreak.type==='WIN'?'rgba(35,185,129,0.08)':'rgba(232,69,94,0.08)',
            border:`1px solid ${currentStreak.type==='WIN'?'rgba(35,185,129,0.25)':'rgba(232,69,94,0.25)'}`,
          },
          title:currentStreak.type==='WIN'
            ?`${currentStreak.count} wins in a row — don't get overconfident, mean reversion is coming`
            :`${currentStreak.count} losses in a row — consider half-size or skip next trade`,
        },currentStreak.type==='WIN'?`🔥${currentStreak.count}W streak`:`⚠${currentStreak.count}L streak`),
        // V8.7: Deadzone pill — pinned visible if coin-flip phase
        ctx.phase.deadzoneWarning&&React.createElement('span',{
          className:'text-[8px] uppercase font-bold tracking-[0.14em] shrink-0 px-1.5 py-0.5 rounded-lg',
          style:{color:'#E8455E',background:'rgba(232,69,94,0.10)',border:'1px solid rgba(232,69,94,0.25)'},
          title:'This phase has poor edge. Skip or half-size.',
        },'⚠ deadzone'),
        // V10.9.7: speed-dial recommendation pill + auto/manual toggle REMOVED.
        //   The dial was a global timing multiplier (0.1x-2.0x) sitting on top of
        //   the tier ladder, V10.9.0 signal-agreement gate, V10.8.2 cushion history,
        //   and V9.8.0 urgency protocol — all of which are already confidence/signal
        //   based. 50 (the "balanced" recommendation) was the mathematical neutral
        //   point (multiplier=1.0). Removing the dial = locking at that neutral
        //   point permanently, letting the signal-based systems drive timing at
        //   their tuned baseline without an extra global stretch/compress layer.
        // V8.7.2: Your-WR-this-session pill — only when we have ≥5 trades to be meaningful
        _sessionAdvice&&React.createElement('span',{
          className:'tara-context-extra text-[8px] uppercase font-bold tracking-[0.14em] shrink-0 px-1.5 py-0.5 rounded-lg tabular-nums',
          style:{
            color:_sessionAdvice.color,
            background:_sessionAdvice.tone==='positive'||_sessionAdvice.tone==='good'?'rgba(35,185,129,0.06)'
              :_sessionAdvice.tone==='neutral'||_sessionAdvice.tone==='warning'?'rgba(35,185,129,0.06)'
              :'rgba(232,69,94,0.08)',
            border:'1px solid '+_sessionAdvice.color.replace('0.95','0.30').replace('0.85','0.30'),
          },
          title:`Your ${_sessionAdvice.session} WR: ${_sessionAdvice.wr}% over ${_sessionAdvice.n} ${currentAsset} trades`,
        },`you · ${_sessionAdvice.wr}%`),
        // Top caution if present (collapsed view)
        !expanded&&_topCaution&&React.createElement('span',{className:'tara-context-caution-inline text-[10px] truncate min-w-0',style:{
          color:_topCaution.severity==='critical'?'#E8455E':_topCaution.severity==='warning'?'#D4A03A':'rgba(237,237,237,0.55)',
        }},_topCaution.severity==='critical'?'⚠ ':_topCaution.severity==='warning'?'⚠ ':'· ',_topCaution.text),
      ),
      // Right: next transition countdown + expand chevron
      React.createElement('div',{className:'flex items-center gap-2 shrink-0'},
        React.createElement('div',{className:'text-right'},
          React.createElement('div',{className:'text-[8px] uppercase tracking-wider text-[#EDEDED]/35 leading-tight'},'Next'),
          React.createElement('div',{className:'text-[10px] sm:text-[11px] font-bold tabular-nums tracking-tight leading-tight',style:{color:'rgba(237,237,237,0.75)'}},
            ctx.nextPhase.label,' · ',_nextLabel,
          ),
        ),
        React.createElement('span',{className:'text-[#EDEDED]/30 text-xs',style:{transition:'transform 0.2s',transform:expanded?'rotate(180deg)':'rotate(0deg)'}},'▾'),
      ),
      !expanded&&_topCaution&&React.createElement('span',{
        className:'tara-context-caution-mobile',
        title:_topCaution.text,
        style:{color:_topCaution.severity==='critical'?'#E8455E':_topCaution.severity==='warning'?'#D4A03A':'#aab9b2'},
      },_topCaution.severity==='info'?'· ':'⚠ ',_topCaution.text),
    ),
    // Expanded detail panel
    expanded&&React.createElement('div',{className:'border-t border-[#24242E] px-3 sm:px-4 py-3 space-y-2.5 text-[11px]'},
      // V9.13: ADAPTIVE SCHEDULE STRIP — 24-hour heatmap of recommended trade
      //   activity, with current adaptive dial reasoning. Always shown at top of
      //   expanded panel so user can see at-a-glance whether they're in a green
      //   or red window.
      (()=>{
        const _adaptive=computeAdaptiveDial({
          analysis,
          streak:currentStreak,
          todaySessionWR:(typeof _todaySessionWR==='object'&&_todaySessionWR)?_todaySessionWR.wr:null,
          todaySessionN:(typeof _todaySessionWR==='object'&&_todaySessionWR)?_todaySessionWR.n:null,
        });
        const _statusColor=_adaptive.status==='TRADE'?'#23B981'
          :_adaptive.status==='OBSERVE'?'#23B981'
          :'#E8455E';
        const _statusBg=_adaptive.status==='TRADE'?'rgba(35,185,129,0.08)'
          :_adaptive.status==='OBSERVE'?'rgba(35,185,129,0.08)'
          :'rgba(232,69,94,0.08)';
        return React.createElement('div',{
          className:'px-2.5 py-2.5 rounded-lg',
          style:{background:_statusBg,border:'1px solid '+_statusColor.replace('0.95','0.30')},
        },
          // Header line: current status + recommended dial
          React.createElement('div',{className:'flex items-baseline gap-2 mb-2'},
            React.createElement('span',{className:'text-[9px] uppercase tracking-[0.18em] font-bold',style:{color:_statusColor}},
              _adaptive.status==='TRADE'?'✓ TRADE WINDOW'
              :_adaptive.status==='OBSERVE'?'◇ OBSERVE'
              :'⊘ SKIP WINDOW',
            ),
            React.createElement('span',{className:'text-[10px] text-[#EDEDED]/55 tabular-nums'},
              `${_adaptive.hour.toString().padStart(2,'0')}:00 EST · hist ${_adaptive.scheduleWR}% WR`,
            ),
            React.createElement('span',{className:'ml-auto text-[10px] tabular-nums font-bold',style:{color:T2_GOLD}},
              `dial → ${_adaptive.dial}`,
            ),
          ),
          // 24-hour heatmap strip (compact)
          React.createElement('div',{className:'flex gap-[2px] mb-2',style:{height:'18px'}},
            Array.from({length:24},(_,h)=>{
              const _slot=TARA_SCHEDULE_BY_EST_HOUR[h]||{status:'OBSERVE',dial:50,wr:60,note:''};
              const _isNow=h===_adaptive.hour;
              const _bg=_slot.status==='TRADE'?'rgba(35,185,129,0.55)'
                :_slot.status==='OBSERVE'?'rgba(35,185,129,0.50)'
                :'rgba(232,69,94,0.45)';
              return React.createElement('div',{
                key:h,
                className:'flex-1 rounded-lg relative',
                style:{
                  background:_bg,
                  outline:_isNow?'2px solid '+T2_GOLD:'none',
                  outlineOffset:'1px',
                },
                title:`${h.toString().padStart(2,'0')}:00 EST — ${_slot.status} · dial ${_slot.dial} · ${_slot.wr}% WR · ${_slot.note}`,
              });
            }),
          ),
          // Hour ruler beneath
          React.createElement('div',{className:'flex gap-[2px] mb-2',style:{fontSize:'7px',color:'rgba(237,237,237,0.40)'}},
            Array.from({length:24},(_,h)=>React.createElement('div',{
              key:h,
              className:'flex-1 text-center tabular-nums',
            },h%3===0?h.toString().padStart(2,'0'):'')),
          ),
          // Reasoning lines — why the dial landed where it did
          React.createElement('div',{className:'text-[10px] text-[#EDEDED]/70 leading-snug space-y-0.5'},
            _adaptive.reasoning.slice(0,4).map((r,i)=>React.createElement('div',{key:i,className:'flex gap-1.5'},
              React.createElement('span',{style:{color:T2_GOLD,opacity:0.5}},'·'),
              React.createElement('span',null,r),
            )),
          ),
          // Schedule note
          _adaptive.scheduleNote&&React.createElement('div',{className:'text-[10px] text-[#EDEDED]/55 italic mt-1.5'},
            _adaptive.scheduleNote,
          ),
        );
      })(),
      // V8.7: DEADZONE WARNING — pinned to top if coin-flip phase
      ctx.phase.deadzoneWarning&&React.createElement('div',{
        className:'flex items-baseline gap-2 px-2.5 py-2 rounded-lg',
        style:{background:'rgba(232,69,94,0.08)',border:'1px solid rgba(232,69,94,0.30)'},
      },
        React.createElement('span',{className:'shrink-0 text-base leading-tight',style:{color:'#E8455E'}},'⚠'),
        React.createElement('div',{className:'flex-1 min-w-0'},
          React.createElement('div',{className:'text-[10px] uppercase tracking-[0.16em] font-bold mb-0.5',style:{color:'#E8455E'}},'Coin-flip deadzone'),
          React.createElement('div',{className:'text-[#EDEDED]/75 leading-snug'},
            'This phase has historically poor edge. Skip or half-size unless you have a specific high-conviction setup.',
          ),
        ),
      ),
      // V8.7.2: SESSION WR ADVISORY — your actual win rate in the current session bucket
      _sessionAdvice&&React.createElement('div',{
        className:'flex items-baseline gap-2 px-2.5 py-2 rounded-lg',
        style:{
          background:_sessionAdvice.tone==='positive'||_sessionAdvice.tone==='good'?'rgba(35,185,129,0.06)'
            :_sessionAdvice.tone==='neutral'||_sessionAdvice.tone==='warning'?'rgba(35,185,129,0.06)'
            :'rgba(232,69,94,0.08)',
          border:'1px solid '+(
            _sessionAdvice.tone==='positive'||_sessionAdvice.tone==='good'?'rgba(35,185,129,0.30)'
            :_sessionAdvice.tone==='neutral'||_sessionAdvice.tone==='warning'?'rgba(35,185,129,0.30)'
            :'rgba(232,69,94,0.30)'),
        },
      },
        React.createElement('div',{className:'flex-1 min-w-0'},
          React.createElement('div',{className:'flex items-baseline gap-2 mb-0.5'},
            React.createElement('span',{className:'text-[10px] uppercase tracking-[0.16em] font-bold',style:{color:_sessionAdvice.color}},_sessionAdvice.label),
            React.createElement('span',{className:'text-[10px] tabular-nums font-bold',style:{color:_sessionAdvice.color}},_sessionAdvice.wr,'% · ',_sessionAdvice.n,' trades'),
          ),
          React.createElement('div',{className:'text-[#EDEDED]/75 leading-snug'},_sessionAdvice.text),
        ),
      ),
      // V8.7: Coin flip risk pill
      React.createElement('div',{className:'flex items-baseline justify-between gap-2 pb-1 border-b border-[#24242E]'},
        React.createElement('span',{className:'text-[8px] uppercase tracking-[0.14em] font-bold text-[#EDEDED]/30'},'Coin-flip risk'),
        React.createElement('span',{className:'text-[10px] uppercase font-bold tabular-nums tracking-wider',style:{
          color:ctx.phase.coinFlipRisk==='LOW'?'rgb(35,185,129)':ctx.phase.coinFlipRisk==='MEDIUM'?'#23B981':'#E8455E',
        }},ctx.phase.coinFlipRisk||'?'),
      ),
      // V8.7: Expected movement
      ctx.phase.expectedMovement&&React.createElement('div',{className:'flex items-baseline gap-2'},
        React.createElement('span',{className:'text-[8px] uppercase font-bold tracking-[0.14em] text-[#EDEDED]/30 shrink-0 mt-0.5'},'Expect'),
        React.createElement('span',{className:'text-[#EDEDED]/75 leading-relaxed'},ctx.phase.expectedMovement),
      ),
      // V8.7: Volume guidance — most actionable
      ctx.phase.volumeGuidance&&React.createElement('div',{className:'flex items-baseline gap-2'},
        React.createElement('span',{className:'text-[8px] uppercase font-bold tracking-[0.14em] shrink-0 mt-0.5',style:{color:T2_GOLD}},'Volume'),
        React.createElement('span',{className:'text-[#EDEDED]/85 leading-relaxed'},ctx.phase.volumeGuidance),
      ),
      // V8.7: What Tara does in this phase
      ctx.phase.whatTaraDoes&&React.createElement('div',{className:'flex items-baseline gap-2'},
        React.createElement('span',{className:'text-[8px] uppercase font-bold tracking-[0.14em] shrink-0 mt-0.5',style:{color:'rgba(147,197,253,0.85)'}},'Tara'),
        React.createElement('span',{className:'text-[#EDEDED]/75 leading-relaxed italic'},ctx.phase.whatTaraDoes),
      ),
      // Character description (legacy field — still useful as one-liner)
      React.createElement('div',{className:'flex items-baseline gap-2 pt-1.5 border-t border-[#24242E]'},
        React.createElement('span',{className:'text-[8px] uppercase font-bold tracking-[0.14em] text-[#EDEDED]/30 shrink-0 mt-0.5'},'Character'),
        React.createElement('span',{className:'text-[#EDEDED]/55 leading-relaxed'},ctx.phase.character),
      ),
      // Best for
      ctx.phase.bestFor&&React.createElement('div',{className:'flex items-baseline gap-2'},
        React.createElement('span',{className:'text-[8px] uppercase font-bold tracking-[0.14em] text-[#EDEDED]/30 shrink-0 mt-0.5'},'Best for'),
        React.createElement('span',{className:'text-[#EDEDED]/65'},ctx.phase.bestFor),
      ),
      // Cautions list
      ctx.cautions.length>0&&React.createElement('div',{className:'flex items-baseline gap-2'},
        React.createElement('span',{className:'text-[8px] uppercase font-bold tracking-[0.14em] text-[#EDEDED]/30 shrink-0 mt-0.5'},'Cautions'),
        React.createElement('div',{className:'flex-1 space-y-1'},
          ctx.cautions.map((c,i)=>React.createElement('div',{key:i,className:'flex items-baseline gap-1.5'},
            React.createElement('span',{className:'shrink-0',style:{
              color:c.severity==='critical'?'#E8455E':c.severity==='warning'?'#23B981':'rgba(237,237,237,0.4)',
            }},c.severity==='critical'?'⚠':c.severity==='warning'?'!':'·'),
            React.createElement('span',{style:{
              color:c.severity==='critical'?'#E8455E':c.severity==='warning'?'rgba(35,185,129,0.75)':'rgba(237,237,237,0.6)',
            }},c.text),
          )),
        ),
      ),
      // Macro event detail if active
      ctx.macro.state!=='CLEAR'&&ctx.macro.event&&React.createElement('div',{className:'flex items-baseline gap-2 pt-1 border-t border-[#24242E]'},
        React.createElement('span',{className:'text-[8px] uppercase font-bold tracking-[0.14em] shrink-0 mt-0.5',style:{color:'#E8455E'}},'Macro'),
        React.createElement('span',{style:{color:'#E8455E'}},
          ctx.macro.event.name,' · ',ctx.macro.event.impact,' impact · state: ',ctx.macro.state,
          ctx.macro.minutesUntil!=null&&React.createElement('span',{className:'text-[#EDEDED]/55 ml-2'},
            ctx.macro.minutesUntil>0?`in ${ctx.macro.minutesUntil}m`:`${Math.abs(ctx.macro.minutesUntil)}m ago`,
          ),
        ),
      ),
      // V9.2.1: ENTRY TIMING HINT — computed from call log. Shows which minute of the
      //   window historically produces the best entries for this asset + session.
      (()=>{
        const _resolved=(taraCallLog||[]).filter(e=>{
          if(!e||!e.result||e.result==='SITOUT'||!e.secondsIntoWindow)return false;
          if((e.asset||'BTC')!==currentAsset)return false;
          return true;
        });
        if(_resolved.length<15)return null; // need enough data
        // Bucket by minute (0-1, 1-2, 2-3, etc up to minute 10)
        const _buckets={};
        _resolved.forEach(e=>{
          const _min=Math.floor((e.secondsIntoWindow||0)/60);
          if(_min>10)return; // skip very late entries
          if(!_buckets[_min])_buckets[_min]={W:0,L:0,mfe:0,n:0};
          _buckets[_min].n++;
          if(e.result==='WIN')_buckets[_min].W++;
          else _buckets[_min].L++;
          if(e.maxFavorableExcursionBps)_buckets[_min].mfe+=Number(e.maxFavorableExcursionBps)||0;
        });
        // Find best minute range (2-minute window with highest WR, min 5 trades)
        let _bestWr=0,_bestRange='',_bestN=0;
        for(let start=0;start<=8;start++){
          let _w=0,_l=0;
          for(let m=start;m<=start+1;m++){
            if(_buckets[m]){_w+=_buckets[m].W;_l+=_buckets[m].L;}
          }
          const _n=_w+_l;
          if(_n<5)continue;
          const _wr=_w/_n;
          if(_wr>_bestWr){_bestWr=_wr;_bestRange=`${start}-${start+2}`;_bestN=_n;}
        }
        if(!_bestRange||_bestWr<0.55)return null;
        const _wrPct=Math.round(_bestWr*100);
        return React.createElement('div',{className:'flex items-baseline gap-2 pt-1.5 border-t border-[#24242E]'},
          React.createElement('span',{className:'text-[8px] uppercase font-bold tracking-[0.14em] shrink-0 mt-0.5',style:{color:'rgb(35,185,129)'}},'Entry timing'),
          React.createElement('span',{className:'text-[#EDEDED]/75 leading-relaxed'},
            `Best entry window: minutes ${_bestRange} (${_wrPct}% WR over ${_bestN} trades). Entries before minute ${_bestRange.split('-')[0]} tend to lock on noise; after minute ${_bestRange.split('-')[1]} tends to chase.`
          ),
        );
      })(),
      // Footer: dominant session + day rating
      React.createElement('div',{className:'flex items-baseline justify-between gap-2 pt-1.5 border-t border-[#24242E] text-[9px] text-[#EDEDED]/35 uppercase tracking-wider'},
        React.createElement('span',null,ctx.dayName,' · ',ctx.sessions.dominant,' DOMINANT · DAY×SESS RATING ',ctx.sessions.dsRating),
        React.createElement('span',null,'ALL TIMES UTC'),
      ),
    ),
  );
}

// V5.6.1: Tara's Memory — compact strip showing the most recent calls inline in the
//   Tara card. Click "all" to open a fuller paged view. The strip + modal both source
//   from taraCallLog (cloud-synced array of every call she's made).
const TaraMemoryStrip=React.memo(function TaraMemoryStrip({taraCallLog,windowType,taraLearnings,useLocalTime,timeFormat,onEditEntry,onDeleteEntry,fullPage=false}){
  const[open,setOpen]=React.useState(false);
  const[learnOpen,setLearnOpen]=React.useState(false);
  const[viewFilter,setViewFilter]=React.useState('all');
  const[memoryPeriod,setMemoryPeriod]=React.useState('calls');
  const[versionFilter,setVersionFilter]=React.useState('all');
  const[inspectedEntry,setInspectedEntry]=React.useState(null);
  const allWindowEntries=React.useMemo(()=>presentationRows(taraCallLog,{windowType}),[taraCallLog,windowType]);
  const windowEntries=React.useMemo(()=>fullPage?presentationRows(allWindowEntries,{version:versionFilter}):allWindowEntries,[allWindowEntries,fullPage,versionFilter]);
  const record=React.useMemo(()=>({
    wins:windowEntries.filter(e=>_isRealTrade(e)&&e.result==='WIN').length,
    losses:windowEntries.filter(e=>_isRealTrade(e)&&e.result==='LOSS').length,
    sitouts:windowEntries.filter(e=>e.result==='SITOUT').length,
    pending:windowEntries.filter(e=>!e.result).length,
  }),[windowEntries]);
  const recent=React.useMemo(()=>{
    const entries=fullPage&&viewFilter!=='all'
      ?windowEntries.filter(e=>viewFilter==='pending'?!e.result:e.result===viewFilter)
      :windowEntries;
    return entries.slice(fullPage?-80:-6).reverse();
  },[windowEntries,fullPage,viewFilter]);
  const totalAcrossWindows=Array.isArray(taraCallLog)?taraCallLog.length:0;
  const coverage=fullPage?callWindowCoverage(taraCallLog,Date.now(),24):null;
  const _learnTotal=taraLearnings?.totalResolved||0;
  const _resultColors={WIN:{bg:'rgba(52,211,153,0.18)',fg:'#23B981'},LOSS:{bg:'rgba(232,69,94,0.18)',fg:'#E8455E'},/*V13.4.203: sit-outs were GREEN, the same family as WIN, so a window Tara declined read as a window she won. Neutral now -- a sit-out is an absence of a trade, not an outcome, and only real outcomes get a signal colour.*/SITOUT:{bg:T2_SITOUT_BG,fg:T2_SITOUT_FG},pending:{bg:'rgba(237,237,237,0.06)',fg:'rgba(237,237,237,0.5)'}};
  const _dirArrow=(d)=>d==='UP'?'▲':d==='DOWN'?'▼':'·';
  const _fmtTime=(ms)=>_fmtTimeTz(ms,timeFormat,{hour:'2-digit',minute:'2-digit'});
  const _fmtDate=(ms)=>_fmtDateTz(ms,timeFormat,{month:'short',day:'numeric'});
  return React.createElement(React.Fragment,null,
    fullPage&&React.createElement('div',{className:'tara-memory-periods','aria-label':'Memory ledger'},
      [['calls',`${windowType==='5m'?'5':'15'}-minute Calls`],['hourly','Hourly picks']].map(([key,label])=>
        React.createElement('button',{key,type:'button',onClick:()=>setMemoryPeriod(key),'aria-pressed':memoryPeriod===key},label)),
      memoryPeriod==='calls'&&React.createElement(RecordVersionFilter,{entries:allWindowEntries,value:versionFilter,onChange:setVersionFilter})),
    (!fullPage||memoryPeriod==='calls')&&React.createElement('div',{className:fullPage?'tara-memory-page':'border-t border-[#24242E] pt-2.5 mt-2.5'},
      React.createElement('div',{className:'flex justify-between items-baseline mb-1.5 gap-2'},
        React.createElement('span',{className:'text-[9px] uppercase tracking-[0.18em] text-[#EDEDED]/45 font-bold'},'Tara\'s Memory'),
        React.createElement('div',{className:'flex items-center gap-2'},
          // V5.6.7: brain icon — opens the learnings panel
          taraLearnings&&React.createElement('button',{
            onClick:()=>setLearnOpen(true),
            title:`Adaptive learning · ${_learnTotal} resolved trades`,
            // V13.4.299: amber/indigo accents dropped. Neither a learning count
            //   nor an "open" link is an outcome or a caution, so under the
            //   mockup's one accent rule they are quiet label-grey like every
            //   other piece of panel furniture.
            className:'flex items-center gap-1 text-[9px] uppercase tracking-wider font-bold transition-colors text-[#EDEDED]/35 hover:text-[#EDEDED]/60',
          },'🧠 ',_learnTotal>=5?`learnings (${_learnTotal})`:`learning (${_learnTotal})`),
          React.createElement('button',{onClick:()=>setOpen(true),className:'text-[9px] uppercase tracking-wider text-[#EDEDED]/35 hover:text-[#EDEDED]/60 font-bold'},totalAcrossWindows>0?'all →':'open ↗'),
        ),
      ),
      fullPage&&React.createElement('div',{className:'tara-memory-page__stats'},
        [['Won',record.wins,'is-win'],['Lost',record.losses,'is-loss'],['Sat out',record.sitouts,'is-sitout'],['Pending',record.pending,'']].map(([name,count,tone])=>
          React.createElement('div',{key:name,className:tone},React.createElement('span',null,name),React.createElement('strong',null,count))),
      ),
      fullPage&&React.createElement('p',{className:'tara-memory-page__coverage'},
        `Last 24h: ${coverage.recorded}/${coverage.expected} 15-minute windows recorded · ${coverage.unobserved} unobserved. Unobserved is browser downtime, a collection gap, or missing sync — not a sit-out.`),
      fullPage&&React.createElement('div',{className:'tara-memory-page__filters'},
        [['all','All'],['WIN','Won'],['LOSS','Lost'],['SITOUT','Sat out'],['pending','Pending']].map(([key,label])=>
          React.createElement('button',{key,onClick:()=>setViewFilter(key),'aria-pressed':viewFilter===key},label)),
        React.createElement('small',null,`Latest ${recent.length} ${windowType} Call records · AutoTrade fills are separate`),
      ),
      recent.length===0
        ? React.createElement('div',{className:'text-[9px] italic text-[#EDEDED]/35'},
            totalAcrossWindows===0
              ? 'No calls yet — record builds as windows resolve.'
              : `No ${windowType} calls yet — open the full record for everything.`
          )
        // V13.4.299: was a wrap-around wall of coloured pills, one per call. The
        //   mockup lists them as rows instead -- a coloured direction arrow, the
        //   time in mono at 42% ink, then "UP · won" as plain text -- so colour
        //   marks the outcome without the whole entry becoming a coloured object.
        : React.createElement('div',{className:'flex flex-col -mx-1'},
            recent.map((e)=>{
              const r=e.result||'pending';
              const c=_resultColors[r]||_resultColors.pending;
              const capturedBlocker=e.originalDecision?.blockedDecision||e.blockedDecision;
              const capturedScore=e.originalDecision?.signalScore??e.confidence;
              // V13.3.6: coherent pill direction. The glyph and color come
              //   from result; the arrow must agree with them. A real trade
              //   (WIN/LOSS) shows its position direction, recovered from
              //   outcomeDir when the dir field is missing (WIN: dir=outcome;
              //   LOSS: dir=opposite). A sit-out or pending shows no arrow.
              //   Stops an entry rendering as a sit-out while marked win/loss.
              let _ddir=null;
              if(r==='WIN'||r==='LOSS'){
                _ddir=(e.dir==='UP'||e.dir==='DOWN')?e.dir
                  :(e.outcomeDir==='UP'||e.outcomeDir==='DOWN')?(r==='WIN'?e.outcomeDir:(e.outcomeDir==='UP'?'DOWN':'UP'))
                  :null;
              }else if(r==='pending'&&(e.dir==='UP'||e.dir==='DOWN')){
                _ddir=e.dir; // V13.4.10: a still-open real call gets its direction shown (dimmed), distinct from a sit-out's neutral dot
              }
              const _outcomeWord=r==='WIN'?'won':r==='LOSS'?'lost':r==='SITOUT'?'sat out':'open';
              return React.createElement('div',{
                key:e.id,
                className:fullPage?'tara-memory-page__row':'flex items-center gap-2.5 px-1 py-[5px]',
                role:fullPage?'button':undefined,tabIndex:fullPage?0:undefined,
                onClick:fullPage?()=>setInspectedEntry(e):undefined,
                onKeyDown:fullPage?ev=>{if(ev.key==='Enter'||ev.key===' '){ev.preventDefault();setInspectedEntry(e);}}:undefined,
                title:`${_fmtTime(e.time||e.id)} · ${e.regime||'?'} · q${e.qScore||0} · ${e.dir||'?'} ${e.confidence||0}% · ${r}${e.gapBps!=null?` · ${formatSignedInt(e.gapBps)} bps`:''}`,
              },
                React.createElement('span',{className:'text-[10px] leading-none w-2.5 shrink-0',style:{color:c.fg}},_dirArrow(_ddir)),
                fullPage
                  ?React.createElement('span',{className:'tara-memory-page__when'},React.createElement('time',null,_fmtDate(e.time||e.id)),React.createElement('small',null,_fmtTime(e.time||e.id)))
                  :React.createElement('span',{className:'text-[10px] tabular-nums shrink-0',style:{color:'rgba(237,237,237,0.42)'}},_fmtTime(e.time||e.id)),
                React.createElement('span',{className:fullPage?'tara-memory-page__decision':'text-[11px] truncate',style:{color:'rgba(237,237,237,0.66)'}},
                  (_ddir||(r==='SITOUT'?'SIT OUT':'—'))+' · '+_outcomeWord),
                fullPage&&React.createElement('span',{className:'tara-memory-page__evidence'},`${capturedScore!=null&&Number.isFinite(Number(capturedScore))?Math.round(Number(capturedScore))+'/100 signal · ':''}${e.regime||'regime pending'}${e.qScore!=null?' · q'+e.qScore:''}`),
                fullPage&&capturedBlocker?.reason&&React.createElement('span',{
                  className:'tara-memory-page__blocker',style:{gridColumn:'2 / -1',fontSize:'11px',lineHeight:1.5,color:'#b9aa87',overflowWrap:'anywhere'},
                },`Blocked: ${capturedBlocker.reason}`),
                fullPage&&React.createElement('span',{className:'tara-v145-memory-version'},`${recordVersion(e)==='Unknown'?'Version unavailable':'v'+recordVersion(e)} · inspect ↗`),
              );
            })
          ),
    ),
    fullPage&&memoryPeriod==='hourly'&&React.createElement(HourlyMemoryModal,{embedded:true}),
    fullPage&&React.createElement(RecordInspector,{entry:inspectedEntry,onClose:()=>setInspectedEntry(null)}),
    open&&React.createElement(TaraMemoryModal,{taraCallLog:taraCallLog,onClose:()=>setOpen(false),useLocalTime:useLocalTime,timeFormat:timeFormat,onEditEntry:onEditEntry,onDeleteEntry:onDeleteEntry,initialFilter:windowType}),
    learnOpen&&React.createElement(TaraLearningsModal,{learnings:taraLearnings,onClose:()=>setLearnOpen(false)}),
  );
})

// V5.6.8: TaraLearningsModal — readout of what Tara has learned. Shows the actually-active
//   learning signals: per regime+direction speed/confidence adjustments + per-tier speed
//   adjustments. Other buckets (regime, direction, session) are shown as performance
//   context but do NOT adjust gates by default — only an extreme safety valve does.
function TaraLearningsModal({learnings,onClose}){
  const data=learnings||{byRegime:{},byDirection:{},byTier:{},bySession:{},byRegimeDir:{},multipliers:{}};
  const total=data.totalResolved||0;
  const _renderRegimeDirBucket=()=>{
    const entries=Object.entries(data.byRegimeDir||{}).filter(([_,v])=>v&&(v.wins+v.losses)>=3).sort((a,b)=>(b[1].wins+b[1].losses)-(a[1].wins+a[1].losses));
    if(entries.length===0)return null;
    return React.createElement('div',{className:'mb-4'},
      React.createElement('div',{className:'text-[10px] uppercase tracking-[0.18em] text-[#EDEDED]/55 font-bold mb-2'},'Regime + Direction · ACTIVE LEARNING'),
      React.createElement('div',{className:'space-y-1'},
        entries.map(([k,v])=>{
          const total=v.wins+v.losses;
          const wr=Math.round((v.wins/total)*100);
          const speedAdj=data.multipliers?.regimeDirSpeedAdj?.[k]||0;
          const confAdj=data.multipliers?.regimeDirConfBoost?.[k]||0;
          const labelParts=[];
          if(speedAdj<0)labelParts.push(`${Math.abs(Math.round(speedAdj*100))}% faster lock`);
          else if(speedAdj>0)labelParts.push(`${Math.round(speedAdj*100)}% slower lock`);
          if(confAdj>0)labelParts.push(`+${confAdj} confidence`);
          else if(confAdj<0)labelParts.push(`${confAdj} confidence`);
          const _adjLabel=labelParts.length>0?labelParts.join(' · '):'no change';
          const _adjColor=speedAdj<0||confAdj>0?'text-emerald-400/85':speedAdj>0||confAdj<0?'text-amber-400/80':'text-[#EDEDED]/35';
          const wrColor=wr>=70?'text-emerald-400':wr>=55?'text-[#EDEDED]/80':wr>=45?'text-amber-400/80':'text-rose-400';
          const [regime,dir]=k.split('|');
          const arrow=dir==='UP'?'▲':'▼';
          return React.createElement('div',{key:k,className:'px-2 py-2 rounded-lg bg-[#0B0B0F] border border-[#24242E]'},
            React.createElement('div',{className:'flex items-baseline justify-between gap-2 mb-0.5'},
              React.createElement('span',{className:'text-[11px] font-bold tracking-wide text-[#EDEDED]/85 truncate flex items-baseline gap-1.5'},
                React.createElement('span',{className:dir==='UP'?'text-emerald-400':'text-rose-400'},arrow),
                regime+' · '+dir,
              ),
              React.createElement('div',{className:'flex items-baseline gap-2 shrink-0'},
                React.createElement('span',{className:`text-[11px] tabular-nums font-bold ${wrColor}`},`${v.wins}W·${v.losses}L`),
                React.createElement('span',{className:`text-[10px] tabular-nums ${wrColor}`},`${wr}%`),
              ),
            ),
            React.createElement('div',{className:`text-[9px] italic ${_adjColor}`},_adjLabel),
          );
        })
      )
    );
  };
  const _renderTierBucket=()=>{
    const entries=Object.entries(data.byTier||{}).filter(([_,v])=>v&&(v.wins+v.losses)>=3).sort((a,b)=>(b[1].wins+b[1].losses)-(a[1].wins+a[1].losses));
    if(entries.length===0)return null;
    return React.createElement('div',{className:'mb-4'},
      React.createElement('div',{className:'text-[10px] uppercase tracking-[0.18em] text-[#EDEDED]/55 font-bold mb-2'},'Tier · SPEED LEARNING'),
      React.createElement('div',{className:'space-y-1'},
        entries.map(([k,v])=>{
          const total=v.wins+v.losses;
          const wr=Math.round((v.wins/total)*100);
          const adj=data.multipliers?.tierSamplesAdjust?.[k]||0;
          const _adjLabel=adj<0?`${Math.abs(Math.round(adj*100))}% faster lock`:adj>0?`${Math.round(adj*100)}% slower`:'no change';
          const _adjColor=adj<0?'text-emerald-400/85':adj>0?'text-amber-400/80':'text-[#EDEDED]/35';
          const wrColor=wr>=70?'text-emerald-400':wr>=55?'text-[#EDEDED]/80':wr>=45?'text-amber-400/80':'text-rose-400';
          return React.createElement('div',{key:k,className:'flex items-baseline justify-between gap-2 px-2 py-1.5 rounded-lg bg-[#0B0B0F] border border-[#24242E]'},
            React.createElement('span',{className:'text-[11px] font-bold tracking-wide text-[#EDEDED]/85 truncate'},k),
            React.createElement('div',{className:'flex items-baseline gap-2 shrink-0'},
              React.createElement('span',{className:`text-[11px] tabular-nums font-bold ${wrColor}`},`${v.wins}W·${v.losses}L`),
              React.createElement('span',{className:`text-[10px] tabular-nums ${wrColor}`},`${wr}%`),
              React.createElement('span',{className:`text-[9px] italic ${_adjColor} hidden sm:inline`},_adjLabel),
            ),
          );
        })
      )
    );
  };
  const _renderConfluenceBucket=()=>{
    const entries=Object.entries(data.byConfluence||{}).filter(([_,v])=>v&&(v.wins+v.losses)>=3).sort((a,b)=>(b[1].wins+b[1].losses)-(a[1].wins+a[1].losses));
    if(entries.length===0)return null;
    const _orderRank={'structural-led':0,'super-confluence':1,'confluence':2,'tape-led':3,'rising-confluence':4,'single-signal':5};
    entries.sort((a,b)=>(_orderRank[a[0]]??9)-(_orderRank[b[0]]??9));
    return React.createElement('div',{className:'mb-4'},
      React.createElement('div',{className:'text-[10px] uppercase tracking-[0.18em] text-[#EDEDED]/55 font-bold mb-2'},'Confluence · QUALITY LEARNING'),
      React.createElement('div',{className:'space-y-1'},
        entries.map(([k,v])=>{
          const total=v.wins+v.losses;
          const wr=Math.round((v.wins/total)*100);
          const adj=data.multipliers?.confluenceConfBoost?.[k]||0;
          const _adjLabel=adj>0?`+${adj} confidence`:adj<0?`${adj} confidence`:'no change';
          const _adjColor=adj>0?'text-emerald-400/85':adj<0?'text-amber-400/80':'text-[#EDEDED]/35';
          const wrColor=wr>=70?'text-emerald-400':wr>=55?'text-[#EDEDED]/80':wr>=45?'text-amber-400/80':'text-rose-400';
          const _label=k==='structural-led'?'◈ Structural-led':k==='super-confluence'?'★ Super-confluence':k==='confluence'?'★ Confluence':k==='tape-led'?'⚡ Tape-led (fast)':k==='rising-confluence'?'↗ Rising (early entry)':'Single-signal';
          const _color=k==='structural-led'?{color:'#C4B5FD'}:k==='super-confluence'||k==='confluence'?{color:T2_GOLD}:k==='tape-led'?{color:'#7DD3FC'}:k==='rising-confluence'?{color:'#A6E3A1'}:{color:'rgba(237,237,237,0.85)'};
          return React.createElement('div',{key:k,className:'flex items-baseline justify-between gap-2 px-2 py-1.5 rounded-lg bg-[#0B0B0F] border border-[#24242E]'},
            React.createElement('span',{className:'text-[11px] font-bold tracking-wide truncate',style:_color},_label),
            React.createElement('div',{className:'flex items-baseline gap-2 shrink-0'},
              React.createElement('span',{className:`text-[11px] tabular-nums font-bold ${wrColor}`},`${v.wins}W·${v.losses}L`),
              React.createElement('span',{className:`text-[10px] tabular-nums ${wrColor}`},`${wr}%`),
              React.createElement('span',{className:`text-[9px] italic ${_adjColor} hidden sm:inline`},_adjLabel),
            ),
          );
        })
      )
    );
  };
  const _renderEdgeBucket=()=>{
    const entries=Object.entries(data.byEdge||{}).filter(([_,v])=>v&&(v.wins+v.losses)>=3);
    if(entries.length===0)return null;
    const _orderRank={'big-edge':0,'good-edge':1,'tight':2,'late':3};
    entries.sort((a,b)=>(_orderRank[a[0]]??9)-(_orderRank[b[0]]??9));
    return React.createElement('div',{className:'mb-4'},
      React.createElement('div',{className:'text-[10px] uppercase tracking-[0.18em] text-[#EDEDED]/55 font-bold mb-2'},'Edge vs market · ENTRY VALUE'),
      React.createElement('div',{className:'space-y-1'},
        entries.map(([k,v])=>{
          const total=v.wins+v.losses;
          const wr=Math.round((v.wins/total)*100);
          const wrColor=wr>=70?'text-emerald-400':wr>=55?'text-[#EDEDED]/80':wr>=45?'text-amber-400/80':'text-rose-400';
          const _label=k==='big-edge'?'BIG EDGE (≥+15pt)':k==='good-edge'?'GOOD EDGE (+5..+15)':k==='tight'?'TIGHT (-5..+5)':'LATE ENTRY (≤-5)';
          const _color=k==='big-edge'?{color:'#86EFAC'}:k==='good-edge'?{color:'rgba(35,185,129,0.7)'}:k==='tight'?{color:'rgba(237,237,237,0.65)'}:{color:'rgba(212,162,76,0.85)'};
          return React.createElement('div',{key:k,className:'flex items-baseline justify-between gap-2 px-2 py-1.5 rounded-lg bg-[#0B0B0F] border border-[#24242E]'},
            React.createElement('span',{className:'text-[11px] font-bold tracking-wide truncate',style:_color},_label),
            React.createElement('div',{className:'flex items-baseline gap-2 shrink-0'},
              React.createElement('span',{className:`text-[11px] tabular-nums font-bold ${wrColor}`},`${v.wins}W·${v.losses}L`),
              React.createElement('span',{className:`text-[10px] tabular-nums ${wrColor}`},`${wr}%`),
            ),
          );
        })
      ),
      React.createElement('div',{className:'text-[9px] text-[#EDEDED]/45 italic mt-2 leading-snug'},
        'Same WR at +15pt edge is far more profitable than at -10pt edge. The right metric is "how often does Tara win when Kalshi is still 50/50" — that\'s where real value lives.'
      )
    );
  };
  const _renderContextBucket=(title,bucket)=>{
    const entries=Object.entries(bucket||{}).filter(([_,v])=>v&&(v.wins+v.losses)>=3).sort((a,b)=>(b[1].wins+b[1].losses)-(a[1].wins+a[1].losses));
    if(entries.length===0)return null;
    return React.createElement('div',{className:'mb-3'},
      React.createElement('div',{className:'text-[10px] uppercase tracking-[0.18em] text-[#EDEDED]/40 font-bold mb-1.5'},title+' · context only'),
      React.createElement('div',{className:'space-y-0.5'},
        entries.map(([k,v])=>{
          const total=v.wins+v.losses;
          const wr=Math.round((v.wins/total)*100);
          const wrColor=wr>=70?'text-emerald-400':wr>=55?'text-[#EDEDED]/80':wr>=45?'text-amber-400/80':'text-rose-400';
          return React.createElement('div',{key:k,className:'flex items-baseline justify-between gap-2 px-2 py-1'},
            React.createElement('span',{className:'text-[10px] tracking-wide text-[#EDEDED]/65 truncate'},k),
            React.createElement('span',{className:`text-[10px] tabular-nums ${wrColor}`},`${v.wins}W·${v.losses}L (${wr}%)`),
          );
        })
      )
    );
  };
  return React.createElement('div',{
    className:'fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm',
    onClick:onClose,
  },
    React.createElement('div',{
      className:'bg-[#0A0A0E] border border-[#1B1B22] rounded-[10px] shadow-2xl max-w-md w-full max-h-[85vh] overflow-y-auto p-5',
      onClick:(e)=>e.stopPropagation(),
      style:{boxShadow:'0 20px 60px rgba(0,0,0,0.6), inset 0 0 0 1px rgba(35,185,129,0.06)'},
    },
      React.createElement('div',{className:'flex justify-between items-baseline mb-4'},
        React.createElement('div',null,
          React.createElement('h2',{className:'text-lg font-serif text-white tracking-tight'},'Tara\'s Learnings'),
          React.createElement('p',{className:'text-[10px] uppercase tracking-wider text-[#EDEDED]/45 mt-0.5'},`${total} resolved trades · directional + speed learning`),
        ),
        React.createElement('button',{onClick:onClose,className:'text-[#EDEDED]/40 hover:text-white text-xl leading-none'},'×'),
      ),
      total<5
        ? React.createElement('div',{className:'p-4 rounded-lg bg-[#050508] border border-[#24242E] text-[12px] text-[#EDEDED]/65 leading-relaxed'},
            'Tara is gathering data. After 5 resolved UP/DOWN calls she\'ll start adapting based on what\'s actually been working — locking faster on proven regime+direction combos, dampening confidence on combos that\'ve been losing, but never withdrawing into more sit-outs.',
            React.createElement('div',{className:'mt-2 text-[10px] text-[#EDEDED]/40'},`Currently at ${total}/5.`),
          )
        : React.createElement(React.Fragment,null,
            _renderRegimeDirBucket(),
            _renderTierBucket(),
            _renderConfluenceBucket(),
            _renderEdgeBucket(),
            _renderContextBucket('By Regime',data.byRegime),
            _renderContextBucket('By Direction',data.byDirection),
            _renderContextBucket('By Session',data.bySession),
            React.createElement('div',{className:'mt-4 p-3 rounded-lg bg-[#050508] border border-[#24242E] text-[10px] text-[#EDEDED]/55 leading-relaxed'},
              React.createElement('div',{className:'mb-1.5'},React.createElement('span',{className:'font-bold text-[#EDEDED]/75'},'How she learns: '),'Tara picks BETTER, doesn\'t call LESS. Default learning adjusts speed and displayed confidence per regime+direction combo. Combos with ≥75% win rate get 20% faster lock + confidence boost. Combos with <40% get 20% slower lock + dampened confidence — but she still calls them. Floors stay fixed except for an emergency safety valve (only triggers if a regime drops below 25% over 15+ trades).'),
              React.createElement('div',null,'The shared call log syncs across devices, so every device sees the same Tara — and contributes to the same training set.'),
            ),
          ),
    ),
  );
}

// V5.6.1: Full memory history modal. Filter by window type, sort newest first.
function TaraMemoryModal({taraCallLog,onClose,useLocalTime,timeFormat,onEditEntry,onDeleteEntry,initialFilter}){
  const[editingId,setEditingId]=React.useState(null);
  // V7.0.6: default filter to current windowType (passed from card) so modal opens
  //   already aligned. User can click ALL to broaden.
  const[filter,setFilter]=React.useState(initialFilter||'all');
  // V13.4.208: cap how many rows are built at once. Rendering the whole log
  //   (1,000 entries x several nodes each) in one synchronous pass was enough
  //   to kill the browser tab. 120 fills the 60vh scroll area several times
  //   over, so the cap is invisible until you deliberately page past it.
  const _ROW_STEP=120;
  const[rowLimit,setRowLimit]=React.useState(_ROW_STEP);
  // V9.2.0: per-asset filter — separate BTC vs ETH views since they trade independently.
  //   "all" shows both. Default is 'all' so the user immediately sees the full picture.
  const[assetFilter,setAssetFilter]=React.useState('BTC');
  // V9.9.3: storage integrity audit + Kalshi reconciliation. Both produce a result
  //   panel that lists discrepancies. Verify is instant (in-memory checks). Reconcile
  //   is async (fetches Kalshi settled markets for the past 24-72h) and slower.
  //   reconcileResult shape: {kind:'verify'|'reconcile', issues: [{entryId,kind,detail,suggested}]}
  const[reconcileResult,setReconcileResult]=React.useState(null);
  const[reconcileBusy,setReconcileBusy]=React.useState(false);
  const filtered=React.useMemo(()=>{
    let arr=[...taraCallLog].reverse();
    arr=arr.filter(e=>e&&e.result!=='MISSED'&&!e._missedWhileBackgrounded);/*V13.4.56: hide missed/backgrounded windows from the log display*/
    if(assetFilter==='BTC')arr=arr.filter(e=>(e.asset||'BTC')==='BTC');
    if(filter==='15m'||filter==='5m')arr=arr.filter(e=>e.windowType===filter);
    else if(filter==='wins')arr=arr.filter(e=>e.result==='WIN');
    else if(filter==='losses')arr=arr.filter(e=>e.result==='LOSS');
    else if(filter==='sitouts')arr=arr.filter(e=>e.result==='SITOUT');
    return arr;
  },[taraCallLog,filter,assetFilter]);
  // V13.4.208: a filter change starts the row budget over, otherwise switching to
  //   a narrow filter keeps an inflated limit and switching back re-explodes the DOM.
  React.useEffect(()=>{setRowLimit(_ROW_STEP);},[filter,assetFilter]);
  const counts=React.useMemo(()=>{
    // V10.7.87: use _isRealTrade filter to match taraScorecards exactly.
    //   Previously counted raw result=WIN/LOSS which included wasOverriddenNoTrade entries
    //   that taraScorecards excludes — causing BTC/ETH mismatch between Memory and lock card.
    let arr=taraCallLog;
    if(assetFilter==='BTC')arr=arr.filter(e=>(e.asset||'BTC')==='BTC');
    if(filter==='15m'||filter==='5m')arr=arr.filter(e=>e.windowType===filter);
    return {
      total:arr.length,
      wins:arr.filter(e=>_isRealTrade(e)&&e.result==='WIN').length,
      losses:arr.filter(e=>_isRealTrade(e)&&e.result==='LOSS').length,
      sitouts:arr.filter(e=>e.result==='SITOUT').length,
      pending:arr.filter(e=>!e.result).length,
      btcN:taraCallLog.filter(e=>(e.asset||'BTC')==='BTC').length,
      ethN:0,
    };
  },[taraCallLog,filter,assetFilter]);
  const _wr=counts.wins+counts.losses>0?Math.round((counts.wins/(counts.wins+counts.losses))*100):null;
  /* V13.4.206: SITOUT was WIN-green here, so 'SIT OUT' rows in the record read as wins at a glance -- the single most confusing thing in the modal. Neutral: a sit-out is not an outcome. */
  const _resultStyle=(r)=>r==='WIN'?{color:'#23B981'}:r==='LOSS'?{color:'#E8455E'}:r==='SITOUT'?{color:T2_SITOUT_FG}:{color:'rgba(237,237,237,0.4)'};
  /* V13.4.206: the fallback (SIT_OUT / unknown) was green as well. */
  const _dirStyle=(d)=>d==='UP'?{color:'#23B981'}:d==='DOWN'?{color:'#E8455E'}:{color:T2_SITOUT_FG};
  // V5.7.5: derive window period (e.g. "09:00–09:15") from windowId. windowId encodes the
  //   bucket-start ISO timestamp, so end = start + winMs. Renders in viewer's local time.
  // V6.5.3: All time displays in this modal honor the parent's useLocalTime toggle.
  //   useLocalTime=true → viewer's local timezone. useLocalTime=false → America/New_York (EST/EDT).
  // V8.9.3: 3-way time format. Falls back to useLocalTime boolean for backward-compat
  //   if timeFormat prop wasn't threaded through (defensive default).
  const _resolvedFmt=timeFormat||(useLocalTime?'local':'est');
  const _tzOpt=_resolvedFmt==='utc'?{timeZone:'UTC'}:_resolvedFmt==='est'?{timeZone:'America/New_York'}:{};
  const _periodFromWindowId=(wid,wt)=>{
    if(!wid)return null;
    try{
      const m=wid.match(/-(.+)$/); if(!m)return null;
      const start=new Date(m[1]); if(isNaN(start.getTime()))return null;
      const winMs=wt==='15m'?900000:300000;
      const end=new Date(start.getTime()+winMs);
      const _f=(d)=>d.toLocaleTimeString('en-US',{..._tzOpt,hour12:false,hour:'2-digit',minute:'2-digit'});
      return`${_f(start)}–${_f(end)}`;
    }catch(e){return null;}
  };
  const _fmtPrice=(p)=>{
    if(p==null||!Number.isFinite(Number(p)))return null;
    const _n=Number(p);
    // Auto-decimals by magnitude: large = no decimals, mid = 2dp, small = up to 5dp
    const _decimals=_n>=1000?0:_n>=10?2:_n>=1?3:5;
    return'$'+_n.toLocaleString(undefined,{minimumFractionDigits:_decimals,maximumFractionDigits:_decimals});
  };
  return React.createElement('div',{
    className:'fixed inset-0 z-50 bg-[#050508] backdrop-blur-md overflow-y-auto overflow-x-hidden',
    onClick:(e)=>{if(e.target===e.currentTarget)onClose();},
  },
    React.createElement('div',{className:'max-w-[900px] mx-auto px-2 sm:px-4 py-3 sm:py-8 w-full min-w-0'},
      React.createElement('div',{className:'flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-4'},
        React.createElement('div',null,
          React.createElement('div',{className:'text-[10px] uppercase font-bold tracking-[0.18em]',style:{color:T2_GOLD}},'TARA · MEMORY'),
          React.createElement('h2',{className:'font-serif text-lg sm:text-3xl text-white tracking-tight leading-tight'},'Every call ',React.createElement('span',{style:{color:T2_GOLD}},'·'),' her record'),
        ),
        React.createElement('div',{className:'flex flex-wrap items-center gap-1.5'},
          // V9.9.3: Reconcile against Kalshi — fetches settled markets for the past 72h
          //   and cross-checks every WIN/LOSS entry against Kalshi's authoritative result.
          //   Discrepancies surface in the result panel with a one-click "apply" suggestion.
          // V9.17.15: PROPER REWRITE per Kalshi API docs (docs.kalshi.com).
          //   Previous versions made several wrong assumptions:
          //     - Trusted Tara's recorded strike absolutely (it can be stale)
          //     - Used only the binary `result` field, ignoring `expiration_value`
          //     - Fetched 288 markets per series and filtered client-side
          //   New approach:
          //     - For each entry, fetch ONLY markets that closed at its specific window
          //       boundary (using min_close_ts/max_close_ts query params)
          //     - Use `expiration_value` (Kalshi's recorded settlement price) as ground
          //       truth alongside the `result` field
          //     - Validate Tara's strike against Kalshi's actual market strikes — flag
          //       mismatches separately from confirmed matches
          //     - Cross-check Tara's `closingPrice` (if logged) vs Kalshi's expiration_value
          //   Result types:
          //     - 'pending-resolve' — confident: filled pending entry with correct result
          //     - 'kalshi-mismatch' — confident: WIN/LOSS was wrong, suggested fix
          //     - 'strike-uncertain' — Tara's strike differs from any Kalshi market; needs review
          //     - 'closing-disagrees' — Kalshi's expiration_value disagrees with Tara's
          //       closingPrice by enough to flip outcome; needs review
          React.createElement('button',{
            onClick:async()=>{
              setReconcileBusy(true);
              const _issues=[];
              const _diag={fetchAttempts:0,fetchOk:0,errors:[],settlementsTotal:0,fillsTotal:0,marketsTotal:0};
              try{
                // V10.7.89: AUTHENTICATED RECONCILE via Kalshi Portfolio API.
                //   Uses RSA-PSS signed requests to fetch YOUR actual account data:
                //   1. GET /portfolio/settlements — all your settled positions (ground truth WIN/LOSS)
                //   2. GET /portfolio/fills + /historical/fills — your actual entry prices
                //   3. GET /markets/{ticker} — strike + expiration_value for each matched market
                //   This replaces the public-API bucket approach which:
                //     - Only covered 72h (now covers ALL history)
                //     - Only updated result (now fills strike, closingPrice, kalshiAtLock, kalshiAtClose)
                //     - Was slow (O(hours) bucket fetches, now direct portfolio query)
                const _creds=typeof window._taraGetKalshiCreds==='function'?window._taraGetKalshiCreds():null;
                const _hasAuth=_creds?.apiKeyId&&_creds?.privateKeyPem;
                // ── AUTH FETCH HELPER ──────────────────────────────────────────────
                const _authFetch=async(path)=>{
                  if(!_hasAuth)return null;
                  _diag.fetchAttempts++;
                  const r=await kalshiAuthedFetch({
                    apiKeyId:_creds.apiKeyId,
                    privateKeyPem:_creds.privateKeyPem,
                    method:'GET',
                    path,
                    timeoutMs:12000,
                  });
                  if(r?.ok){_diag.fetchOk++;return r.data;}
                  _diag.errors.push(`${path}: ${r?.reason||r?.status||'failed'}`);
                  return null;
                };
                // ── PAGINATE HELPER ────────────────────────────────────────────────
                const _fetchAllPages=async(basePath,dataKey,maxPages=20)=>{
                  const all=[];
                  let cursor=null;
                  for(let page=0;page<maxPages;page++){
                    const sep=basePath.includes('?')?'&':'?';
                    const url=`${basePath}${sep}limit=1000${cursor?`&cursor=${encodeURIComponent(cursor)}`:''}`;
                    const data=await _authFetch(url);
                    if(!data)break;
                    const items=data[dataKey];
                    if(!Array.isArray(items)||!items.length)break;
                    all.push(...items);
                    cursor=data.cursor;
                    if(!cursor)break;
                  }
                  return all;
                };
                // ── STEP 1: FETCH ALL SETTLEMENTS ─────────────────────────────────
                // /portfolio/settlements: ticker, market_result, revenue, settled_time
                // This is ground truth — if Kalshi says you won $X on market Y, you won.
                let settlements=[];
                if(_hasAuth){
                  settlements=await _fetchAllPages('/portfolio/settlements','settlements');
                  _diag.settlementsTotal=settlements.length;
                }
                // ── STEP 2: FETCH ALL FILLS (recent + historical) ─────────────────
                // Fills give us: ticker, outcome_side, yes_price_dollars, created_time
                // This tells us WHICH DIRECTION we entered and at WHAT PRICE
                let fills=[];
                if(_hasAuth){
                  const [recentFills,histFills]=await Promise.all([
                    _fetchAllPages('/portfolio/fills','fills'),
                    _fetchAllPages('/historical/fills','fills'),
                  ]);
                  fills=[...recentFills,...histFills];
                  // Dedupe by fill_id
                  const seen=new Set();
                  fills=fills.filter(f=>{if(seen.has(f.fill_id||f.trade_id))return false;seen.add(f.fill_id||f.trade_id);return true;});
                  _diag.fillsTotal=fills.length;
                }
                // ── STEP 3: BUILD LOOKUP MAPS ─────────────────────────────────────
                // settlements map: ticker → {result:'yes'|'no', settled_time, revenue}
                const settlementsMap=new Map();
                for(const s of settlements){
                  if(s.ticker)settlementsMap.set(s.ticker,s);
                }
                // fills map: ticker → {outcome_side:'yes'|'no', yes_price_dollars, created_time}
                // For BTC 15m we typically have one fill per window — take the last fill per ticker
                const fillsMap=new Map();
                for(const f of fills){
                  const ticker=f.ticker||f.market_ticker;
                  if(!ticker)continue;
                  const existing=fillsMap.get(ticker);
                  const fTime=f.ts||new Date(f.created_time||0).getTime();
                  const eTime=existing?.ts||0;
                  if(!existing||fTime>eTime)fillsMap.set(ticker,{...f,_ts:fTime});
                }
                // ── STEP 4: FETCH MARKET DETAILS FOR ALL RELEVANT BTC WINDOWS ──────
                // Strategy: use settlements to find which tickers you traded, then fetch
                // those markets for close_time / floor_strike / expiration_value.
                // ALSO: use bulk series query to get markets for windows where you have
                // Tara entries but might not have a settlement (e.g. sitouts or data gaps).
                // Both paths build into _marketByTicker. The matching in Step 5 uses
                // close_time alignment (not strike) which is the reliable approach.

                // Path A: individual fetches for tickers in your settlements (guaranteed accurate)
                const _marketByTicker=new Map();
                const _btcSettlements=settlements.filter(s=>/^KXBTC(15M|5M)-/.test(s.ticker||''));
                const _logTimes=taraCallLog.filter(e=>e&&e.id).map(e=>e.id);
                const _minLogTime=_logTimes.length?Math.min(..._logTimes)-7200000:0;
                const _maxLogTime=_logTimes.length?Math.max(..._logTimes)+7200000:Date.now();

                // Filter settlements to those within call log time range
                const _relevantSettlements=_btcSettlements.filter(s=>{
                  const st=new Date(s.settled_time||0).getTime();
                  return st>=_minLogTime&&st<=_maxLogTime;
                });

                // V11.2.1 FAST RECONCILE: only fetch markets for windows that still
                //   need data. An entry is COMPLETE when it has a settled result AND a
                //   strike AND a closing price — re-fetching those markets is the
                //   dominant cost on re-runs and changes nothing. WIN/LOSS ground truth
                //   for ALL windows still comes from the (already-fetched, free)
                //   settlements list, so mismatch detection is unaffected for the
                //   windows we do fetch. Build the set of incomplete window-close
                //   buckets (mirroring the matcher's bucket key) and only fetch tickers
                //   whose settled_time lands in (or adjacent to) an incomplete bucket.
                const _winMsFor=(wt)=>wt==='5m'?300000:900000;
                const _incompleteBuckets=new Set();
                for(const e of taraCallLog){
                  if(!e||!e.id)continue;
                  if(e.betAmt==null)continue; // V13.4.43: only committed call locks, not engine predictor reads
                  if(e.dir!=='UP'&&e.dir!=='DOWN')continue;
                  const _wt=e.windowType||'15m';
                  const _wm=_winMsFor(_wt);
                  const _complete=(e.result==='WIN'||e.result==='LOSS')&&Number(e.strike)>0&&Number(e.closingPrice)>0;
                  if(_complete)continue;
                  let _close=null;
                  if(typeof e.windowId==='string'&&e.windowId.includes('-')){
                    try{const _ts=new Date(e.windowId.slice(e.windowId.indexOf('-')+1)).getTime();if(Number.isFinite(_ts)&&_ts>0)_close=_ts+_wm;}catch(_){}
                  }
                  if(_close==null)_close=Math.ceil(e.id/_wm)*_wm;
                  const _b=Math.round(_close/_wm)*_wm;
                  // include the bucket and its two neighbours so settled_time skew
                  //   can never drop a ticker a pending entry needs
                  _incompleteBuckets.add(_wt+'|'+_b);
                  _incompleteBuckets.add(_wt+'|'+(_b+_wm));
                  _incompleteBuckets.add(_wt+'|'+(_b-_wm));
                }
                _diag.incompleteBuckets=_incompleteBuckets.size;
                const _settleBucketKey=(s)=>{
                  const wt=/15M/.test(s.ticker||'')?'15m':'5m';
                  const wm=_winMsFor(wt);
                  const st=new Date(s.settled_time||0).getTime();
                  return wt+'|'+(Math.round(st/wm)*wm);
                };
                // Fetch individual markets ONLY for settlements mapping to incomplete windows.
                const _needFetch=_incompleteBuckets.size>0
                  ? _relevantSettlements.filter(s=>_incompleteBuckets.has(_settleBucketKey(s)))
                  : [];
                const _tickersToFetch=Array.from(new Set(_needFetch.map(s=>s.ticker)));
                _diag.tickersToFetch=_tickersToFetch.length;
                _diag.tickersSkipped=Array.from(new Set(_relevantSettlements.map(s=>s.ticker))).length-_tickersToFetch.length;
                const _BATCH=20; // V11.2.1: parallel batches of 20 (was 10)
                for(let i=0;i<_tickersToFetch.length;i+=_BATCH){
                  const batch=_tickersToFetch.slice(i,i+_BATCH);
                  await Promise.all(batch.map(async ticker=>{
                    const data=await _authFetch(`/markets/${encodeURIComponent(ticker)}`);
                    const mk=data?.market||data; // some endpoints wrap in .market, some don't
                    if(!mk||!mk.ticker)return;
                    // Kalshi uses different field names across API versions — try all
                    const expirationValue=(
                      mk.expiration_value!=null?Number(mk.expiration_value):
                      mk.result_value!=null?Number(mk.result_value):
                      mk.settlement_value!=null?Number(mk.settlement_value):
                      mk.final_price!=null?Number(mk.final_price):0
                    );
                    _marketByTicker.set(ticker,{
                      close_time:mk.close_time?new Date(mk.close_time).getTime():(mk.expiration_time?new Date(mk.expiration_time).getTime():0),
                      floor_strike:Number(mk.floor_strike||mk.strike||0),
                      expiration_value:expirationValue,
                      result:mk.result,
                      strike_type:mk.strike_type,
                      windowType:/15M/.test(ticker)?'15m':'5m',
                    });
                    _diag.marketsTotal++;
                  }));
                }

                // Path B: bulk series query for windows Tara locked but that aren't in
                //   your settlements. V11.2.1: only run when incomplete buckets remain
                //   UNCOVERED after Path A. On a fully-reconciled log this is skipped
                //   entirely — Path B was up to 60 sequential signed fetches every run.
                const _coveredBuckets=new Set();
                for(const [,mk] of _marketByTicker){
                  if(!mk.close_time)continue;
                  const wm=mk.windowType==='5m'?300000:900000;
                  _coveredBuckets.add(mk.windowType+'|'+(Math.round(mk.close_time/wm)*wm));
                }
                let _uncoveredIncomplete=0;
                for(const b of _incompleteBuckets){if(!_coveredBuckets.has(b))_uncoveredIncomplete++;}
                _diag.uncoveredIncomplete=_uncoveredIncomplete;
                const _runPathB=_hasAuth&&_incompleteBuckets.size>0&&_uncoveredIncomplete>0;
                _diag.pathBRan=_runPathB;
                if(_runPathB){
                  for(const _series of ['KXBTC15M','KXBTC5M']){
                    let _mcursor=null;
                    for(let _p=0;_p<30;_p++){
                      const _path=`/markets?series_ticker=${_series}&status=settled&limit=200${_mcursor?`&cursor=${encodeURIComponent(_mcursor)}`:''}`;
                      const _mktData=await _authFetch(_path);
                      if(!_mktData)break;
                      const _mkts=_mktData.markets||[];
                      let _anyInRange=false;
                      for(const mk of _mkts){
                        if(!mk.ticker)continue;
                        const closeMs=mk.close_time?new Date(mk.close_time).getTime():(mk.expiration_time?new Date(mk.expiration_time).getTime():0);
                        if(closeMs<_minLogTime||closeMs>_maxLogTime)continue;
                        _anyInRange=true;
                        if(!_marketByTicker.has(mk.ticker)){
                          const ev2=(
                            mk.expiration_value!=null?Number(mk.expiration_value):
                            mk.result_value!=null?Number(mk.result_value):
                            mk.settlement_value!=null?Number(mk.settlement_value):0
                          );
                          _marketByTicker.set(mk.ticker,{
                            close_time:closeMs,
                            floor_strike:Number(mk.floor_strike||mk.strike||0),
                            expiration_value:ev2,
                            result:mk.result,
                            strike_type:mk.strike_type,
                            windowType:_series==='KXBTC15M'?'15m':'5m',
                          });
                          _diag.marketsTotal++;
                        }
                      }
                      _mcursor=_mktData.cursor;
                      if(!_mcursor||!_mkts.length)break;
                      // Stop pagination if all results are now before our log range
                      if(!_anyInRange&&_mkts.length>0){
                        const _lastClose=_mkts[_mkts.length-1]?.close_time;
                        if(_lastClose&&new Date(_lastClose).getTime()<_minLogTime)break;
                      }
                    }
                  }
                }

                // Build time-sorted settled markets list for matching
                const _settledMarkets=[];
                for(const [ticker,mk] of _marketByTicker){
                  if(!mk.close_time)continue;
                  _settledMarkets.push({
                    ticker,
                    closeMs:mk.close_time,
                    floor_strike:mk.floor_strike,
                    expiration_value:mk.expiration_value,
                    result:mk.result||(settlementsMap.get(ticker)?.market_result)||null,
                    settled_time:(settlementsMap.get(ticker)?.settled_time)||null,
                    windowType:mk.windowType||(/15M/.test(ticker)?'15m':'5m'),
                  });
                }
                _settledMarkets.sort((a,b)=>a.closeMs-b.closeMs);

                // ── STEP 5: MATCH TARA ENTRIES TO SETTLED MARKETS ─────────
                //   V10.9.5 FIX: the old matcher used Math.ceil(id/winMs)*winMs for the
                //   expected close, then matched the closest settled market within a
                //   flat 5-MINUTE tolerance. For 15m windows that 5min slop is a third
                //   of a window — a late lock or any clock skew could match the WRONG
                //   adjacent window, pulling that window's strike/close onto this entry.
                //   This is the "reconcile matched the wrong window" bug.
                //   Fix:
                //     1. Derive the entry's window-close from its windowId when present
                //        (windowId encodes the exact bucket start; close = start+winMs),
                //        falling back to ceil(id) only when windowId is missing.
                //     2. Build a close-time->market index and require the match to land
                //        in the SAME window bucket (tolerance = 1/2 window minus 1s),
                //        so an adjacent window can never be selected.
                // V13.4.225: three counts, not one, because "matched 0" had three
                //   completely different meanings and the panel showed the same
                //   sentence for all of them.
                //
                //   THIS is the likely reason reconcile looked broken. V11.2.1 FAST
                //   RECONCILE only fetches markets for windows that still need data,
                //   and _settledMarkets is built solely from what it fetched. On a
                //   log that is already fully reconciled it correctly fetches
                //   nothing — and then every entry fails to match, so the summary
                //   read "Matched 0 entries" on a perfectly healthy run. Identical
                //   to what a total auth failure printed.
                //
                //   Splitting already-complete (deliberately skipped) from
                //   needs-checking (the actual work) makes the three cases legible
                //   and stops a good run from looking like a broken one.
                let _matchedEntries=0,_pendingResolved=0,_eligibleEntries=0,_alreadyComplete=0,_matchedIncomplete=0;
                const _nowMs=Date.now();
                // Index settled markets by their exact window-close bucket for O(1) exact match
                const _marketByCloseBucket=new Map();
                for(const m of _settledMarkets){
                  const _mWinMs=m.windowType==='5m'?300000:900000;
                  const _bucket=Math.round(m.closeMs/_mWinMs)*_mWinMs;
                  const _bkey=m.windowType+'|'+_bucket;
                  const _ex=_marketByCloseBucket.get(_bkey);
                  if(!_ex||Math.abs(m.closeMs-_bucket)<Math.abs(_ex.closeMs-_bucket)){
                    _marketByCloseBucket.set(_bkey,m);
                  }
                }
                for(const e of taraCallLog){
                  if(!e||!e.id)continue;
                  if(e.betAmt==null)continue; // V13.4.43: only committed call locks, not engine predictor reads
                  if(e.dir!=='UP'&&e.dir!=='DOWN')continue;
                  const _winMs=(e.windowType||'15m')==='5m'?300000:900000;
                  // Prefer windowId-derived close (exact bucket); fall back to ceil(id)
                  let _expectedClose=null;
                  if(typeof e.windowId==='string'&&e.windowId.includes('-')){
                    try{
                      const _widTs=e.windowId.slice(e.windowId.indexOf('-')+1);
                      const _startMs=new Date(_widTs).getTime();
                      if(Number.isFinite(_startMs)&&_startMs>0)_expectedClose=_startMs+_winMs;
                    }catch(_){}
                  }
                  if(_expectedClose==null)_expectedClose=Math.ceil(e.id/_winMs)*_winMs;
                  if(_expectedClose>_nowMs-30000)continue;
                  // Past its close and committed. Classify it the SAME way the fast
                  //   path did when it decided what to fetch, so the counts describe
                  //   what actually happened rather than contradicting it.
                  const _entryComplete=(e.result==='WIN'||e.result==='LOSS')&&Number(e.strike)>0&&Number(e.closingPrice)>0;
                  if(_entryComplete)_alreadyComplete++;  // deliberately not re-fetched — not a miss
                  else _eligibleEntries++;               // incomplete: this run was supposed to fix it
                  const _whenStr=new Date(e.id).toISOString().slice(5,16).replace('T',' ');
                  // EXACT bucket match first (cannot select an adjacent window)
                  const _eBucket=Math.round(_expectedClose/_winMs)*_winMs;
                  let _best=_marketByCloseBucket.get((e.windowType||'15m')+'|'+_eBucket)||null;
                  let _bestDiff=_best?Math.abs(_best.closeMs-_expectedClose):Infinity;
                  // Fallback: nearest within HALF a window (strictly less than winMs/2),
                  //   so a neighbouring window — exactly winMs away — is impossible to pick.
                  if(!_best){
                    for(const m of _settledMarkets){
                      if(m.windowType!==(e.windowType||'15m'))continue;
                      const diff=Math.abs(m.closeMs-_expectedClose);
                      if(diff<_bestDiff){_bestDiff=diff;_best=m;}
                    }
                  }
                  const _maxTol=Math.floor(_winMs/2)-1000; // 449s for 15m, 149s for 5m
                  if(!_best||_bestDiff>_maxTol)continue;
                  if(!_best.result)continue;
                  _matchedEntries++;
                  if(!_entryComplete)_matchedIncomplete++;   // coverage of the work that mattered

                  // Ground truth: 'yes' = price settled >= strike (UP wins)
                  const _wasAbove=_best.result==='yes';
                  const _shouldBe=e.dir==='UP'?(_wasAbove?'WIN':'LOSS'):(_wasAbove?'LOSS':'WIN');
                  const _fill=fillsMap.get(_best.ticker);

                  // Parse fill price — Kalshi returns yes_price_dollars as decimal string ("0.6400")
                  // or yes_price as integer cents (64)
                  let _fillYesPrice=null;
                  if(_fill){
                    const _ypd=_fill.yes_price_dollars;
                    const _yp=_fill.yes_price;
                    if(_ypd!=null){
                      const _f=parseFloat(_ypd);
                      _fillYesPrice=_f<=1?Math.round(_f*100):Math.round(_f); // 0.64→64 or 64→64
                    } else if(_yp!=null){
                      _fillYesPrice=Math.round(Number(_yp));
                    }
                  }

                  const _updates={};
                  const _notes=[];

                  // Result — always update if wrong or missing
                  if(e.result!=='WIN'&&e.result!=='LOSS'){
                    _updates.result=_shouldBe;_notes.push(`→ ${_shouldBe}`);_pendingResolved++;
                  } else if(e.result!==_shouldBe){
                    _updates.result=_shouldBe;_notes.push(`was ${e.result} → ${_shouldBe}`);
                  }

                  // Strike — fill if missing or wrong
                  if(_best.floor_strike>0){
                    if(!e.strike||Math.abs(e.strike-_best.floor_strike)>0.5){
                      _updates.strike=_best.floor_strike;
                      _updates.strikeAtLock=_best.floor_strike;
                      _notes.push(`strike $${_best.floor_strike}`);
                    }
                  }

                  // Closing price — fill if missing (don't overwrite manually-entered correct values)
                  if(_best.expiration_value>0&&!e.closingPrice){
                    _updates.closingPrice=_best.expiration_value;
                    _notes.push(`close $${_best.expiration_value}`);
                  }

                  // kalshiAtClose — what YES settled at (100 if 'yes' result, 0 if 'no')
                  if(!e.kalshiAtClose){
                    _updates.kalshiAtClose=_wasAbove?100:0;
                  }

                  // kalshiAtLock — from your actual fill price
                  if(false&&_fillYesPrice!==null&&_fillYesPrice>0&&!e.kalshiAtLock){/*V13.4.57: DISABLED - a Kalshi fill is the user manual trade, not Tara call-lock price; never backfill kalshiAtLock from fills*/
                    const _side=_fill.outcome_side||_fill.side||'yes';
                    // If you bought YES: your cost = yes_price. If you bought NO: your cost = 100-yes_price
                    _updates.kalshiAtLock=_side==='yes'?_fillYesPrice:(100-_fillYesPrice);
                    _notes.push(`entry ${_updates.kalshiAtLock}¢`);
                  }

                  // resolvedAt
                  if(!e.resolvedAt&&_best.settled_time){
                    _updates.resolvedAt=new Date(_best.settled_time).getTime();
                  }

                  // outcomeDir - V10.9.24: ALWAYS set to match the Kalshi outcome, not
                  //   just when missing. Leaving a stale outcomeDir that contradicts the
                  //   corrected result made reconciled entries look 'corrupt' to the
                  //   cloud-sync filter and get dropped. Keep result<->outcomeDir consistent.
                  _updates.outcomeDir=_wasAbove?'UP':'DOWN';

                  if(Object.keys(_updates).length>0){
                    const _kind=(_updates.result&&e.result!=='WIN'&&e.result!=='LOSS')?'pending-resolve'
                      :((_updates.result&&(e.result==='WIN'||e.result==='LOSS'))?'kalshi-mismatch'
                      :'missing-fields');
                    _issues.push({
                      entryId:e.id,
                      kind:_kind,
                      detail:`${e.dir} ${_whenStr} · ${_notes.join(', ')}`,
                      suggested:_shouldBe,
                      suggestedField:'result',
                      confident:true,
                      kalshiData:{
                        ticker:_best.ticker,
                        strike:_best.floor_strike,
                        expirationValue:_best.expiration_value,
                        result:_best.result,
                      },
                      fullUpdate:_updates,
                    });
                  }
                }
                for(const e of taraCallLog){
                  if(!e||!e.id)continue;
                  if(e.dir!=='SIT_OUT')continue;
                  const _winMsS=(e.windowType||'15m')==='5m'?300000:900000;
                  let _expectedCloseS=null;
                  if(typeof e.windowId==='string'&&e.windowId.includes('-')){
                    try{
                      const _widTsS=e.windowId.slice(e.windowId.indexOf('-')+1);
                      const _startMsS=new Date(_widTsS).getTime();
                      if(Number.isFinite(_startMsS)&&_startMsS>0)_expectedCloseS=_startMsS+_winMsS;
                    }catch(_){}
                  }
                  if(_expectedCloseS==null)_expectedCloseS=Math.ceil(e.id/_winMsS)*_winMsS;
                  if(_expectedCloseS>_nowMs-30000)continue;
                  const _eBucketS=Math.round(_expectedCloseS/_winMsS)*_winMsS;
                  let _bestS=_marketByCloseBucket.get((e.windowType||'15m')+'|'+_eBucketS)||null;
                  let _bestDiffS=_bestS?Math.abs(_bestS.closeMs-_expectedCloseS):Infinity;
                  if(!_bestS){
                    for(const m of _settledMarkets){
                      if(m.windowType!==(e.windowType||'15m'))continue;
                      const diff=Math.abs(m.closeMs-_expectedCloseS);
                      if(diff<_bestDiffS){_bestDiffS=diff;_bestS=m;}
                    }
                  }
                  const _maxTolS=Math.floor(_winMsS/2)-1000;
                  if(!_bestS||_bestDiffS>_maxTolS)continue;
                  if(!_bestS.result)continue;
                  const _fillS=fillsMap.get(_bestS.ticker);
                  if(true){/*V13.4.57: sit-outs reconcile ONLY vs market settlement, never vs Kalshi fills (a fill is the user manual trade, not Tara call). Always verify and keep SITOUT; the fill-based recovery below is now unreachable/disabled.*/
                    // V13.4.13: no fill confirms this genuinely was a sit-out. If it's
                    //   still stuck at result:null (the pre-V13.4.12 resolution-lookup
                    //   regression), finalize it here. This is not a directional claim
                    //   -- dir is already correctly SIT_OUT -- just completing a status
                    //   stamp that should have happened automatically, so it's safe to
                    //   auto-apply.
                    // V13.4.56: genuine sit-out (no Kalshi fill). VERIFY against settled ground truth.
                    //   A true sit-out is NEVER flipped to WIN/LOSS here - only a sit-out that
                    //   turns out to have a real fill gets recovered (branch below, confident:false
                    //   for user review). Here we only stamp what the window actually did so a
                    //   pending or unverified sit-out becomes verified and analyzable.
                    {
                      const _wasAboveNF=_bestS.result==='yes';
                      const _needsVerify=(e.result===null)||((e.result==='SITOUT')&&!e.outcomeDir);
                      if(_needsVerify){
                        const _u={
                          result:'SITOUT',
                          outcomeDir:_wasAboveNF?'UP':'DOWN',
                          kalshiAtClose:_wasAboveNF?100:0,
                          resolvedAt:e.resolvedAt||Date.now(),
                          resolution:(e.result===null)?'reconcile-verify-v13.4.56':(e.resolution||'reconcile-verify-v13.4.56'),
                          sitoutVerified:true,
                        };
                        if(_bestS.floor_strike>0&&(!e.strike||Math.abs((e.strike||0)-_bestS.floor_strike)>0.5)){_u.strike=_bestS.floor_strike;_u.strikeAtLock=_bestS.floor_strike;}
                        if(_bestS.expiration_value>0&&!e.closingPrice){_u.closingPrice=_bestS.expiration_value;}
                        _issues.push({
                          entryId:e.id,
                          kind:(e.result===null)?'sitout-stuck-pending':'sitout-verify',
                          detail:`${new Date(e.id).toISOString().slice(5,16).replace('T',' ')} - genuine sit-out verified vs settlement (window closed ${_wasAboveNF?'UP':'DOWN'}). Stays SITOUT.`,
                          suggested:'SITOUT',
                          suggestedField:'result',
                          confident:true,
                          fullUpdate:_u,
                        });
                      }
                    }
                    continue;
                  }
                  const _sideS=_fillS.outcome_side||_fillS.side||null;
                  if(_sideS!=='yes'&&_sideS!=='no')continue;
                  const _recoveredDir=_sideS==='yes'?'UP':'DOWN';
                  const _wasAboveS=_bestS.result==='yes';
                  const _shouldBeS=_recoveredDir==='UP'?(_wasAboveS?'WIN':'LOSS'):(_wasAboveS?'LOSS':'WIN');
                  const _whenStrS=new Date(e.id).toISOString().slice(5,16).replace('T',' ');
                  _issues.push({
                    entryId:e.id,
                    kind:'sitout-had-fill',
                    detail:`${_whenStrS} - logged as SIT OUT but a real Kalshi fill exists for this window (bought ${_sideS.toUpperCase()}). Recovered as ${_recoveredDir}, ${_shouldBeS}.`,
                    suggested:_recoveredDir,
                    suggestedField:'dir',
                    confident:false,
                    kalshiData:{
                      ticker:_bestS.ticker,
                      strike:_bestS.floor_strike,
                      expirationValue:_bestS.expiration_value,
                      result:_bestS.result,
                      fillSide:_sideS,
                    },
                    fullUpdate:{
                      dir:_recoveredDir,
                      direction:_recoveredDir,
                      result:_shouldBeS,
                      outcomeDir:_wasAboveS?'UP':'DOWN',
                      wasOverriddenNoTrade:false,
                      caution:'Recovered from Kalshi fill history (V13.4.9) - was mislabeled SIT_OUT',
                    },
                  });
                }
                // ── FALLBACK: if no credentials, use old public API approach ──────
                if(!_hasAuth){
                  // No credentials — inform user
                  _issues.push({
                    entryId:0,
                    kind:'no-credentials',
                    // V13.4.225: the old copy promised "only the last 72h can be
                    //   checked via public API". That public path was replaced by the
                    //   authenticated one in V10.7.89 and no longer exists — every
                    //   fetch here goes through _authFetch, which returns null
                    //   immediately without credentials. So nothing at all is
                    //   checked, and the message said otherwise.
                    detail:'Nothing was checked. Every request in this reconcile is signed with your Kalshi key, so without credentials it reaches the API zero times — there is no public fallback. Add your Key ID and Private Key in Settings, then run it again.',
                    suggested:null,
                    confident:false,
                  });
                }
                // V10.9.23: ONE-CLICK reconcile. Auto-apply the confident Kalshi-ground-truth
                //   corrections right here so there is no separate 'apply all' step. Confident
                //   issues come straight from authenticated settlements/fills; _taraApplyReconcile
                //   marks each manualEdit + rewrites full IDB history (persists, never re-loops).
                //   Ambiguous items (strike-uncertain / closing-disagrees) stay listed for review.
                let _autoApplied=0,_applyAttempted=false;
                const _confidentIssues=_issues.filter(i=>i&&i.confident&&i.entryId&&i.fullUpdate&&Object.keys(i.fullUpdate).length>0);
                if(_confidentIssues.length>0&&typeof window._taraApplyReconcile==='function'){
                  _applyAttempted=true;
                  const _autoMap=new Map();
                  _confidentIssues.forEach(i=>_autoMap.set(i.entryId,i.fullUpdate));
                  try{_autoApplied=await window._taraApplyReconcile(_autoMap);}catch(_){_applyAttempted=false;}
                }
                const _confidentIds=new Set(_confidentIssues.map(i=>i.entryId));
                const _remaining=_applyAttempted?_issues.filter(i=>!_confidentIds.has(i.entryId)):_issues;
                setReconcileResult({
                  kind:'reconcile',
                  issues:_remaining,
                  pendingResolved:_pendingResolved,
                  autoApplied:_autoApplied,
                  checkedAt:Date.now(),
                  checkedAgainst:_diag.settlementsTotal,
                  matchedEntries:_matchedEntries,
                  // V13.4.225: this was hardcoded to 0, so the panel could never
                  //   show coverage — every run claimed nothing was left unchecked
                  //   even when the whole log went unmatched.
                  eligibleEntries:_eligibleEntries,
                  alreadyComplete:_alreadyComplete,
                  matchedIncomplete:_matchedIncomplete,
                  unmatchedEntries:Math.max(0,_eligibleEntries-_matchedIncomplete),
                  authenticated:_hasAuth,
                  // A run that could not reach Kalshi is a FAILED run, not a clean
                  //   one. Surfaced as its own flag so the panel can style it as an
                  //   error rather than leaving it to be inferred from a zero.
                  verifyFailed:_hasAuth&&_diag.fetchAttempts>0&&_diag.fetchOk===0,
                  diag:_diag,
                  // V13.4.225: the old summary read "Matched 0 entries · auto-applied
                  //   0 corrections from Kalshi." when EVERY signed request had failed
                  //   — a success-shaped sentence describing a total failure. Auth
                  //   errors were collected into _diag.errors and then never mentioned.
                  //   The summary now leads with the failure when there is one, and
                  //   always states coverage so "verified" cannot be confused with
                  //   "did not look".
                  summary:(()=>{
                    if(!_hasAuth)return 'NOT VERIFIED — no Kalshi API credentials. Add your Key ID and Private Key in Settings; without them this check reaches nothing and proves nothing.';
                    if(_diag.fetchAttempts>0&&_diag.fetchOk===0){
                      const _why=(_diag.errors[0]||'').split(': ').slice(1).join(': ')||'request failed';
                      return `NOT VERIFIED — all ${_diag.fetchAttempts} Kalshi requests failed (${_why}). Nothing was checked. Most likely the API key was rotated or revoked; re-add it in Settings.`;
                    }
                    const _partial=_diag.errors.length>0?` · ${_diag.errors.length} request${_diag.errors.length===1?'':'s'} failed, coverage is incomplete`:'';
                    const _done=`${_alreadyComplete} entr${_alreadyComplete===1?'y':'ies'} already complete and skipped`;
                    // Nothing incomplete left: the fast path fetching nothing is the
                    //   CORRECT outcome here, not a failure. This is the case that
                    //   used to print "Matched 0 entries" and look broken.
                    if(_eligibleEntries===0)
                      return `Nothing left to reconcile — ${_done}. No gaps found${_partial}.`;
                    if(_matchedIncomplete===0)
                      return `NOT VERIFIED — ${_eligibleEntries} incomplete entr${_eligibleEntries===1?'y':'ies'} needed a Kalshi market and none could be matched${_partial}. (${_done}.)`;
                    return `Auto-applied ${_autoApplied} correction${_autoApplied===1?'':'s'}${_remaining.length>0?` · ${_remaining.length} need review`:''} · filled ${_matchedIncomplete}/${_eligibleEntries} incomplete · ${_done}${_partial}.`;
                  })(),
                });
              }catch(err){
                setReconcileResult({kind:'reconcile',issues:[],error:err.message||String(err),diag:_diag});
              }finally{
                setReconcileBusy(false);
              }
            },
                // ─── Helper: multi-proxy fetch (mirrors V9.17.10 pattern) ─────────────
            disabled:reconcileBusy,
            className:'px-2 py-1 sm:px-3 sm:py-1.5 rounded-lg text-[9px] sm:text-[10px] uppercase tracking-[0.14em] font-bold transition-colors',
            style:{
              background:reconcileBusy?'rgba(237,237,237,0.04)':'rgba(124,93,250,0.08)',
              color:reconcileBusy?'rgba(237,237,237,0.4)':'rgba(196,181,253,0.9)',
              border:`1px solid ${reconcileBusy?'rgba(237,237,237,0.10)':'rgba(196,181,253,0.25)'}`,
            },
            title:'Reconcile with your Kalshi account — fills strike, closingPrice, entry price, WIN/LOSS for all history',
          },reconcileBusy?'…':'⚖ Reconcile'),
          // V6.3.5: Export button — downloads full log as JSON for analysis.
          React.createElement('button',{
            onClick:()=>{
              try{
                const _payload={
                  exportedAt:new Date().toISOString(),
                  baselineVersion:typeof BASELINE_VERSION!=='undefined'?BASELINE_VERSION:'unknown',
                  totalEntries:taraCallLog.length,
                  summary:{
                    wins:counts.wins,losses:counts.losses,sitouts:counts.sitouts,pending:counts.pending,
                    winRate:_wr,
                  },
                  weightsSnapshot:(function(){var o={};try{o.live=(typeof adaptiveWeights!=='undefined')?adaptiveWeights:null;}catch(_){o.live=null;}try{o.lsV110=JSON.parse(localStorage.getItem('taraWeightsV110')||'null');}catch(_){o.lsV110=null;}try{o.lsBTC=JSON.parse(localStorage.getItem('taraWeightsByAsset_BTC_v1')||'null');}catch(_){o.lsBTC=null;}try{o.regime=JSON.parse(localStorage.getItem('taraRegimeWeights_v1')||'null');}catch(_){o.regime=null;}try{o.defaults=(typeof DEFAULT_WEIGHTS!=='undefined')?DEFAULT_WEIGHTS:null;}catch(_){o.defaults=null;}return o;})(),/*V13.4.69: dump learner weights (live+localStorage+defaults) so we can verify updateWeights actually moved off V144 defaults*/
                  entries:taraCallLog,
                };
                const _json=JSON.stringify(_payload,null,2);
                const _blob=new Blob([_json],{type:'application/json'});
                const _url=URL.createObjectURL(_blob);
                const _a=document.createElement('a');
                const _ts=new Date().toISOString().replace(/[:.]/g,'-').slice(0,19);
                _a.href=_url;
                _a.download=`tara-call-log-${_ts}.json`;
                document.body.appendChild(_a);
                _a.click();
                document.body.removeChild(_a);
                setTimeout(()=>URL.revokeObjectURL(_url),100);
              }catch(e){
                alert('Export failed: '+(e.message||String(e)));
              }
            },
            className:'px-2 py-1 sm:px-3 sm:py-1.5 rounded-lg text-[9px] sm:text-[10px] uppercase tracking-[0.14em] font-bold transition-colors',
            style:{
              background:'rgba(35,185,129,0.10)',
              color:T2_GOLD,
              border:'1px solid '+T2_GOLD_BORDER,
            },
            title:'Download call log as JSON for analysis',
          },'↓ Export JSON'),
          // V10.7.88d: Import JSON — restore data from a previous export
          React.createElement('button',{
            onClick:()=>{
              const _inp=document.createElement('input');
              _inp.type='file';_inp.accept='.json';
              _inp.onchange=(ev)=>{
                const file=ev.target.files?.[0];if(!file)return;
                const reader=new FileReader();
                reader.onload=(e)=>{
                  try{
                    const parsed=JSON.parse(e.target.result);
                    const importedEntries=Array.isArray(parsed)?parsed:(parsed?.entries||[]);
                    if(!importedEntries.length){alert('No entries found in file.');return;}
                    const btcEntries=importedEntries.filter(e=>e&&(e.asset||'BTC')==='BTC');
                    if(typeof window._taraImportCallLog!=='function'){alert('Import not available — please refresh and try again.');return;}
                    const count=window._taraImportCallLog(importedEntries);
                    setTimeout(()=>alert(`Done. Imported ${count} entries.`),100);
                  }catch(err){alert('Failed: '+(err?.message||String(err)));}
                };
                reader.readAsText(file);
              };
              _inp.click();
            },
            className:'px-3 py-1.5 rounded-lg text-[11px] font-bold uppercase tracking-[0.1em] transition-colors',
            style:{background:'rgba(35,185,129,0.1)',color:'rgba(35,185,129,0.9)',border:'1px solid rgba(35,185,129,0.25)'},
            title:'Restore call log from a previously exported JSON file',
          },'↑ Import JSON'),
          React.createElement('button',{
            onClick:()=>{
              try{
                const _headers=['date','time','asset','windowType','direction','result','posterior','regime','phase','strike','closingPrice','closingGapBps','lossPattern','tier','windowAmplitude','feedAtLock','lockLatencySec','windowTypeTransitions','windowTypeChanged','secondsIntoWindow','kalshiAtLock','dialAtLock','kalshiAtClose','kalshiVelocityAtLock','urgencyApplied','ulpApplied','samplesNeededOriginal','reversalDamperApplied','reversalDamperMult','recentCandleDirsAtLock','fgtCounterApplied','convBeforeFgtCap','regimeCalApplied','regimeCalKey','regimeCalShift','regimeCalN','fgt','qScore','qScoreV2','qScoreV2_regCal','qScoreV2_sess','qScoreV2_regWR','qScoreV2_post','qScoreV2_late','reversalRiskFlag','reversalRiskScore','reversalRiskTopSignals','maxAdverseExcursionBps','maxFavorableExcursionBps','peakClockSec','troughClockSec','last60sDriftBps','timeSeriesLen','chartPattern','trendStructure','trendStructureStrength','trendlineBreak','trendlineBreakMagBps','patternTotalAdj','htfPattern1m','htfPattern5m','htfPattern15m','htfConfluence','htfDominantDir','htfTotalAdj','fundingRate','oiDeltaPct','basisPct','fundingAdj','oiAdj','basisAdj','futuresTotalAdj','session','device','tradeTimingDecision','tradeTimingScore','tradeTimingReason','tradeTimingMode','sessionTierMode','sessionTierMult','sessionTierApplied','regimeV12','adxAtLock','bbwRankAtLock','atrpAtLock','whipsawAtLock','isHighVolAtLock','isTrendAtLock','isChopAtLock','isCompressingAtLock','priceAboveMedianAtLock','atSecondsLeft','kalshiPriceAgeMs','smcSweepScore','smcFvgScore','fastLockFired','earlyLockFired','earlyLockTier','taraVersion','htDir','stDir','trendAligned','trendConfirmScore'];
                const _rows=[_headers.join(',')];
                (taraCallLog||[]).forEach(e=>{
                  if(!e)return;
                  const _d=e.id?new Date(e.id):new Date();
                  // V9.8.15: CSV date/time honor the user's selected timeFormat (local | utc | est)
                  //   instead of hardcoded local. Previously the CSV always exported in the
                  //   exporter's local timezone — meaning two users on the same data would get
                  //   different timestamps in their downloads, and the displayed times in the
                  //   memory modal didn't match the times in the export they just downloaded.
                  const _date=_fmtDateTz(_d,timeFormat,{year:'numeric',month:'2-digit',day:'2-digit'});
                  const _time=_fmtTimeTz(_d,timeFormat,{hour:'2-digit',minute:'2-digit',second:'2-digit',hour12:false});
                  const _tier=e.isStructuralLed?'structural':e.isSuperConfluent?'super':e.isConfluent?'confluence':e.isTapeLed?'tape':e.isRisingConfluence?'rising':e.isUserForced?'forced':'single';
                  const _row=[
                    _date,_time,e.asset||'BTC',e.windowType||'15m',
                    e.dir||'',e.result||'pending',
                    e.posterior!=null?Math.round(e.posterior):'',
                    e.regime||'',e.phase||'',
                    e.strike||'',e.closingPrice||'',
                    e.closingGapBps!=null?e.closingGapBps.toFixed(1):'',
                    e.lossPattern||'',_tier,
                    e.windowAmplitude||'',
                    // V10.9.14: feed + cadence + window-evolution telemetry
                    e.feedAtLock||'',
                    e.lockLatencySec!=null?e.lockLatencySec:'',
                    e.windowTypeTransitions||'',
                    e.windowTypeChanged===true?'Y':(e.windowTypeChanged===false?'N':''),
                    // V9.7.6: lock-timing telemetry — secondsIntoWindow shows how
                    //   fast Tara locked; kalshiAtLock shows entry price; dialAtLock
                    //   shows the speed dial.
                    e.secondsIntoWindow!=null?e.secondsIntoWindow:'',
                    e.kalshiAtLock!=null?e.kalshiAtLock:'',
                    e.dialAtLock!=null?e.dialAtLock:'',
                    // V9.7.9: gate telemetry — answers "did each gate fire?"
                    //   kalshiAtClose: Kalshi YES at window close (compare to kalshiAtLock for edge audit)
                    //   kalshiVelocityAtLock: cents/30s at lock (was Tara entering during fast move?)
                    //   urgencyApplied: did the V9.7.6 floor-drop kick in?
                    //   reversalDamper: did STRUCT get cut due to recent candle reversal?
                    //   recentCandleDirsAtLock: e.g. "DN,UP" = candle at -2m red, candle at -1m green
                    //   fgtCounter: did FGT-counter cap conviction at 65?
                    //   regimeCal: per-regime×direction blend application
                    e.kalshiAtClose!=null?e.kalshiAtClose:'',
                    e.kalshiVelocityAtLock!=null?e.kalshiVelocityAtLock.toFixed(1):'',
                    e.urgencyApplied?'Y':'N',
                    e.ulpApplied?'Y':'N',
                    e.samplesNeededOriginal!=null?e.samplesNeededOriginal:'',
                    e.reversalDamperApplied?'Y':'N',
                    e.reversalDamperMult!=null?e.reversalDamperMult.toFixed(2):'',
                    e.recentCandleDirsAtLock||'',
                    e.fgtCounterApplied?'Y':'N',
                    e.convBeforeFgtCap!=null?e.convBeforeFgtCap.toFixed(1):'',
                    e.regimeCalApplied?'Y':'N',
                    e.regimeCalKey||'',
                    e.regimeCalShift!=null?e.regimeCalShift.toFixed(1):'',
                    e.regimeCalN!=null?e.regimeCalN:'',
                    // Bonus context fields the V6.x entries already had but weren't exported
                    e.fgt!=null?(typeof e.fgt==='number'?e.fgt.toFixed(1):e.fgt):'',
                    e.qScore!=null?e.qScore:'',
                    // V9.19.10: shadow qScoreV2 + components. Informational only —
                    //   v1 qScore still drives lock decisions. Audit by bucketing WR
                    //   against qScoreV2; if monotonic, swap to v2 in a future ship.
                    e.qScoreV2!=null?e.qScoreV2:'',
                    e.qScoreV2Components?.regimeCalPts!=null?e.qScoreV2Components.regimeCalPts:'',
                    e.qScoreV2Components?.sessionPts!=null?e.qScoreV2Components.sessionPts:'',
                    e.qScoreV2Components?.regimeWRPts!=null?Number(e.qScoreV2Components.regimeWRPts).toFixed(1):'',
                    e.qScoreV2Components?.postPts!=null?Number(e.qScoreV2Components.postPts).toFixed(1):'',
                    e.qScoreV2Components?.latePenalty!=null?e.qScoreV2Components.latePenalty:'',
                    // V9.9.0: reversalRisk telemetry — flag, score, top-3 signal keys (joined with |)
                    e.reversalRisk?.flag||'',
                    e.reversalRisk?.score!=null?e.reversalRisk.score:'',
                    e.reversalRisk?.signals?(e.reversalRisk.signals.filter(s=>s&&s.fired).sort((a,b)=>(b.weight||0)-(a.weight||0)).slice(0,3).map(s=>s.key).join('|')):'',
                    // V9.10.8: mid-trade excursion metrics derived from timeSeries at resolve.
                    //   These let you answer "did adverse drawdown precede the loss?" directly
                    //   from the CSV without loading the full timeSeries arrays from JSON.
                    //   maxAdverseExcursionBps is signed-relative-to-call (negative = bad).
                    //   peakClockSec / troughClockSec are seconds-into-lock when the extreme hit.
                    //   timeSeriesLen lets you see at a glance whether the entry has the mid-trade
                    //   data populated (0 = empty, expect ~150-180 for a complete 15m window).
                    e.maxAdverseExcursionBps!=null?e.maxAdverseExcursionBps:'',
                    e.maxFavorableExcursionBps!=null?e.maxFavorableExcursionBps:'',
                    e.peakClockSec!=null?e.peakClockSec:'',
                    e.troughClockSec!=null?e.troughClockSec:'',
                    e.last60sDriftBps!=null?e.last60sDriftBps:'',
                    Array.isArray(e.timeSeries)?e.timeSeries.length:0,
                    // V9.10.10: pattern detection telemetry — lets you query
                    //   "trades where doubleTop fired and Tara still locked UP, what was WR?"
                    //   directly from CSV. chartPattern + trendStructure + trendlineBreak are
                    //   the three detectors. patternTotalAdj is the combined contribution to
                    //   totalScore (positive = bullish, negative = bearish).
                    e.patternsAtLock?.chartPattern||'',
                    e.patternsAtLock?.trendStructure||'',
                    e.patternsAtLock?.trendStructureStrength!=null?Number(e.patternsAtLock.trendStructureStrength).toFixed(2):'',
                    e.patternsAtLock?.trendlineBreak||'',
                    e.patternsAtLock?.trendlineBreakMagnitude||'',
                    e.patternsAtLock?.totalAdj||'',
                    // V9.11.0: HTF pattern data per-TF + confluence
                    e.patternsAtLockV9_11_0?.perTF?.c1m?.reasons||'',
                    e.patternsAtLockV9_11_0?.perTF?.c5m?.reasons||'',
                    e.patternsAtLockV9_11_0?.perTF?.c15m?.reasons||'',
                    e.patternsAtLockV9_11_0?.confluence||'',
                    e.patternsAtLockV9_11_0?.dominantDirection||'',
                    e.patternsAtLockV9_11_0?.totalAdj||'',
                    // V9.11.0: futures snapshot — raw values + per-signal adjustments
                    e.futuresAtLock?.fundingRate!=null?Number(e.futuresAtLock.fundingRate).toFixed(6):'',
                    e.futuresAtLock?.oiDeltaPct!=null?Number(e.futuresAtLock.oiDeltaPct).toFixed(2):'',
                    e.futuresAtLock?.basisPct!=null?Number(e.futuresAtLock.basisPct).toFixed(4):'',
                    e.futuresAtLock?.fundingAdj||'',
                    e.futuresAtLock?.oiAdj||'',
                    e.futuresAtLock?.basisAdj||'',
                    e.futuresAtLock?.totalAdj||'',
                    // V9.11.0: session + device for learning-bucket attribution
                    e.session||'',
                    e.device||'',
                    // V10.0.0 Phase 4: timing engine telemetry. Decision is one of
                    //   'fire-now' | 'wait' | 'abort' | 'error' | null (legacy entries
                    //   pre-Session 2 will be null). Score is 0-100. Reason is the
                    //   compact factor summary. Mode is which mode produced it.
                    e.tradeTimingDecision||'',
                    e.tradeTimingScore!=null?e.tradeTimingScore:'',
                    e.tradeTimingReason||'',
                    e.tradeTimingMode||'',
                    // V10.2.13: session × tier optimizer telemetry. Mode is 'off'|'shadow'|'live'.
                    //   Mult is the (clamped) multiplier that resolved for this (session, tier).
                    //   Applied indicates whether it actually modified confidence (only true in 'live' mode).
                    e.sessionTierMode||'',
                    e.sessionTierMult!=null?e.sessionTierMult:'',
                    e.sessionTierApplied===true?'true':e.sessionTierApplied===false?'false':'',
                    // V12.0: regime classifier telemetry
                    e.regimeV12||'',
                    e.adxAtLock!=null?Number(e.adxAtLock).toFixed(1):'',
                    e.bbwRankAtLock!=null?e.bbwRankAtLock:'',
                    e.atrpAtLock!=null?Number(e.atrpAtLock).toFixed(2):'',
                    e.whipsawAtLock!=null?e.whipsawAtLock:'',
                    e.isHighVolAtLock===true?'Y':e.isHighVolAtLock===false?'N':'',
                    e.isTrendAtLock===true?'Y':e.isTrendAtLock===false?'N':'',
                    e.isChopAtLock===true?'Y':e.isChopAtLock===false?'N':'',
                    e.isCompressingAtLock===true?'Y':e.isCompressingAtLock===false?'N':'',
                    e.priceAboveMedianAtLock===true?'Y':e.priceAboveMedianAtLock===false?'N':'',
                    // V12.0: timing + price freshness
                    e.atSecondsLeft!=null?e.atSecondsLeft:'',
                    e.kalshiPriceAgeMs!=null?Math.round(e.kalshiPriceAgeMs):'',
                    e.smcSweepScore!=null?e.smcSweepScore:'',
                    e.smcFvgScore!=null?e.smcFvgScore:'',
                    e.fastLockFired===true?'Y':'',
                    e.earlyLockFired===true?'Y':'',
                    e.earlyLockTier!=null?e.earlyLockTier:'',
                    e.taraVersion||'',
                    e.htDir||'',
                    e.stDir||'',
                    e.trendAligned||'',
                    e.trendConfirmScore!=null?e.trendConfirmScore:'',
                  ].map(v=>typeof v==='string'&&v.includes(',')?`"${v}"`:String(v));
                  _rows.push(_row.join(','));
                });
                const _csv=_rows.join('\n');
                const _blob=new Blob([_csv],{type:'text/csv'});
                const _url=URL.createObjectURL(_blob);
                const _a=document.createElement('a');
                const _ts=new Date().toISOString().replace(/[:.]/g,'-').slice(0,19);
                _a.href=_url;
                _a.download=`tara-call-log-${_ts}.csv`;
                document.body.appendChild(_a);
                _a.click();
                document.body.removeChild(_a);
                setTimeout(()=>URL.revokeObjectURL(_url),100);
              }catch(e){
                alert('CSV export failed: '+(e.message||String(e)));
              }
            },
            className:'px-2 py-1 sm:px-3 sm:py-1.5 rounded-lg text-[9px] sm:text-[10px] uppercase tracking-[0.14em] font-bold transition-colors',
            style:{
              background:'rgba(35,185,129,0.08)',
              color:'#23B981',
              border:'1px solid rgba(35,185,129,0.25)',
            },
            title:'Download call log as CSV for spreadsheet analysis',
          },'↓ Export CSV'),
          React.createElement('div',{className:'px-2 py-1 rounded-lg text-[9px] uppercase tracking-[0.14em] font-bold',style:{background:'#0E0E12',color:'rgba(237,237,237,0.5)',border:'1px solid #24242E'},title:'All times shown in this view honor the time format toggle in the header'},_resolvedFmt.toUpperCase()),
          React.createElement('button',{onClick:onClose,className:'p-2 rounded-lg hover:bg-[#EDEDED]/5 text-[#EDEDED]/60 hover:text-white transition-colors text-xl'},'✕'),
        ),
      ),
      React.createElement('div',{className:`grid grid-cols-2 xs:grid-cols-3 sm:grid-cols-5 gap-px mb-4 overflow-hidden ${UI2_PANEL} ${UI2_GRID_BG}`},
        React.createElement('div',{className:'bg-[#0A0A0E] p-3.5'},
          // V13.4.166: say which window these totals cover. The log is capped to the
          //   cloud window, so an unqualified "Total calls" read as a lifetime count.
          React.createElement('div',{className:'text-[9px] uppercase tracking-wider text-[#EDEDED]/40 font-bold mb-1'},`Calls · ${_TARA_RECORD_WINDOW_LABEL}`),
          React.createElement('div',{className:'text-2xl font-bold text-white tabular-nums',style:{fontFamily:'"IBM Plex Mono",monospace'}},counts.total),
        ),
        React.createElement('div',{className:'bg-[#0A0A0E] p-3.5'},
          React.createElement('div',{className:'text-[9px] uppercase tracking-wider text-[#EDEDED]/40 font-bold mb-1'},'Win rate'),
          React.createElement('div',{className:'text-2xl font-bold tabular-nums',style:{color:_wr>=60?'rgb(35,185,129)':_wr>=50?'#fff':'rgb(232,69,94)',fontFamily:'"IBM Plex Mono",monospace'}},_wr!==null?`${_wr}%`:'—'),
        ),
        React.createElement('div',{className:'bg-[#0A0A0E] p-3.5'},
          React.createElement('div',{className:'text-[9px] uppercase tracking-wider text-[#EDEDED]/40 font-bold mb-1'},'Wins'),
          React.createElement('div',{className:'text-2xl font-bold tabular-nums',style:{color:'rgb(35,185,129)',fontFamily:'"IBM Plex Mono",monospace'}},counts.wins),
        ),
        React.createElement('div',{className:'bg-[#0A0A0E] p-3.5'},
          React.createElement('div',{className:'text-[9px] uppercase tracking-wider text-[#EDEDED]/40 font-bold mb-1'},'Losses'),
          React.createElement('div',{className:'text-2xl font-bold tabular-nums',style:{color:'rgb(232,69,94)',fontFamily:'"IBM Plex Mono",monospace'}},counts.losses),
        ),
        React.createElement('div',{className:'bg-[#0A0A0E] p-3.5'},
          React.createElement('div',{className:'text-[9px] uppercase tracking-wider text-[#EDEDED]/40 font-bold mb-1'},'Sat out'),
          React.createElement('div',{className:'text-2xl font-bold tabular-nums',style:{color:T2_GOLD,fontFamily:'"IBM Plex Mono",monospace'}},counts.sitouts),
        ),
      ),
      // V10.7.85: Local-only mode banner — shown when Supabase sync is paused.
      //   Prompts user to export JSON backup periodically to avoid data loss from
      //   localStorage being cleared.
      _SB_PAUSED&&React.createElement('div',{
        className:'mb-4 rounded-xl px-3 py-2.5 flex items-center justify-between gap-3 flex-wrap',
        style:{background:'rgba(35,185,129,0.07)',border:'1px solid rgba(35,185,129,0.25)'},
      },
        React.createElement('div',{className:'flex items-center gap-2 min-w-0'},
          React.createElement('span',{className:'text-base shrink-0'},'💾'),
          React.createElement('div',{className:'min-w-0'},
            React.createElement('div',{className:'text-[10px] font-bold uppercase tracking-[0.14em]',style:{color:'#23B981'}},'LOCAL MODE — Cloud sync paused until ~Jun 18'),
            React.createElement('div',{className:'text-[10px] text-[#EDEDED]/55 mt-0.5'},'Data lives in this browser only. Export JSON daily to avoid data loss.'),
          ),
        ),
        React.createElement('button',{
          onClick:()=>{
            try{
              const _payload={
                exportedAt:new Date().toISOString(),
                baselineVersion:typeof BASELINE_VERSION!=='undefined'?BASELINE_VERSION:'unknown',
                totalEntries:taraCallLog.length,
                note:'LOCAL MODE backup — Supabase sync paused',
                summary:{
                  wins:taraCallLog.filter(e=>e&&e.result==='WIN').length,
                  losses:taraCallLog.filter(e=>e&&e.result==='LOSS').length,
                  sitouts:taraCallLog.filter(e=>e&&e.result==='SITOUT').length,
                },
                entries:taraCallLog,
              };
              const _json=JSON.stringify(_payload,null,2);
              const _blob=new Blob([_json],{type:'application/json'});
              const _url=URL.createObjectURL(_blob);
              const _a=document.createElement('a');
              const _ts=new Date().toISOString().replace(/[:.]/g,'-').slice(0,16);
              _a.href=_url;
              _a.download=`tara-backup-${_ts}.json`;
              document.body.appendChild(_a);
              _a.click();
              document.body.removeChild(_a);
              setTimeout(()=>URL.revokeObjectURL(_url),100);
            }catch(e){alert('Backup failed: '+(e.message||String(e)));}
          },
          className:'shrink-0 px-3 py-1.5 rounded-lg text-[10px] uppercase tracking-[0.14em] font-bold transition-colors',
          style:{background:'rgba(35,185,129,0.15)',color:'#23B981',border:'1px solid rgba(35,185,129,0.35)'},
          title:`Backup all ${taraCallLog.length} entries to JSON file`,
        },'💾 Backup Now'),
      ),
      // V9.9.3: Reconcile / Verify result panel. Renders when either action has been run.
      //   Each issue shows: timestamp, kind, detail, "apply suggestion" button (if a fix
      //   is suggested), "edit manually" button. User reviews + accepts each individually
      //   to avoid bulk-applying a wrong rule.
      reconcileResult?(()=>{
        // V9.17.10: tri-state coloring. Green = audit passed cleanly. Amber =
        //   issues found OR reconcile failed to fetch markets (warning, not success).
        //   Rose = hard error from the audit itself.
        const _isReconcile=reconcileResult.kind==='reconcile';
        const _fetchedNothing=_isReconcile&&(reconcileResult.checkedAgainst||0)===0&&!reconcileResult.error;
        // V13.4.225: was `matchedEntries===0`, which flagged every healthy re-run —
        //   a fully reconciled log has nothing to fetch and therefore nothing to
        //   match. Only unfilled INCOMPLETE entries are a real warning.
        const _noMatches=_isReconcile&&(reconcileResult.eligibleEntries||0)>0&&(reconcileResult.matchedIncomplete||0)===0;
        // V13.4.225: a reconcile that could not reach Kalshi is a FAILURE, not a
        //   warning and certainly not a pass. It now counts as an error, alongside
        //   the missing-credentials case — in both, nothing was checked, so calling
        //   the log "reconciled" afterwards would be false.
        //   `authenticated` is set from `_creds?.apiKeyId && _creds?.privateKeyPem`,
        //   which is UNDEFINED when there are no credentials, not false. A strict
        //   `===false` check therefore never fired and the no-credentials case
        //   still rendered as a warning. Caught on the live panel, not by the unit
        //   test, which passed an explicit false.
        const _notVerified=_isReconcile&&(reconcileResult.verifyFailed||!reconcileResult.authenticated);
        const _isWarn=reconcileResult.issues.length>0||_fetchedNothing||_noMatches;
        const _isError=!!reconcileResult.error||_notVerified;
        // The three states were all painted the SAME green — the tri-state comment
        //   above promised amber for warnings and the amber had been lost, so a
        //   failed run looked exactly like a clean one. Restored to the app's own
        //   grammar: green passed, gold needs attention, red did not verify.
        const _bg=_isError?'rgba(232,69,94,0.06)':_isWarn?'rgba(212,160,58,0.06)':'rgba(35,185,129,0.06)';
        const _border=_isError?'rgba(232,69,94,0.30)':_isWarn?'rgba(212,160,58,0.28)':'rgba(35,185,129,0.20)';
        const _headerColor=_isError?'#E8455E':_isWarn?'#D4A03A':'#23B981';
        return React.createElement('div',{
        className:'mb-5 p-4 rounded-xl',
        style:{background:_bg,border:`1px solid ${_border}`},
      },[
        React.createElement('div',{key:'header',className:'flex items-center justify-between mb-3 flex-wrap gap-2'},[
          React.createElement('div',{key:'l',className:'flex flex-col'},[
            React.createElement('div',{key:'t',className:'text-[10px] uppercase tracking-[0.18em] font-bold',style:{color:_headerColor}},
              reconcileResult.kind==='verify'?'STORAGE AUDIT'
                :_notVerified?'KALSHI RECONCILE · NOT VERIFIED'
                :'KALSHI RECONCILE'),
            React.createElement('div',{key:'s',className:'text-[12px] mt-0.5',style:{color:'#EDEDED'}},(()=>{
              if(reconcileResult.error)return `Error: ${reconcileResult.error}`;
              if(reconcileResult.summary&&reconcileResult.kind==='reconcile')return reconcileResult.summary;
              if(reconcileResult.kind==='verify'){
                const _ba=reconcileResult.byAsset||{};
                const _assetList=Object.keys(_ba).sort();
                const _assetStr=_assetList.length?_assetList.map(a=>`${a}:${_ba[a].total}`).join(' · '):'';
                const _baseLine=reconcileResult.issues.length===0
                  ?`No issues found · audited ${reconcileResult.total||0} entries`
                  :`Found ${reconcileResult.issues.length} issue${reconcileResult.issues.length===1?'':'s'} across ${reconcileResult.total||'?'} entries`;
                // V9.17.22: surface per-asset breakdown so user sees if any asset is empty
                return _assetStr?`${_baseLine} (${_assetStr})`:_baseLine;
              }
              // V9.17.10: kind='reconcile' — show honest numbers
              const _checked=reconcileResult.checkedAgainst||0;
              const _matched=reconcileResult.matchedEntries||0;
              const _unmatched=reconcileResult.unmatchedEntries||0;
              if(_checked===0){
                return 'Couldn\'t reach Kalshi · fetched 0 markets. Check Vercel proxy or try again later.';
              }
              if(_matched===0){
                return `Fetched ${_checked} markets but matched 0 entries to them (${_unmatched} entries had no strike/time match). Kalshi may not have history that far back, or your entries pre-date the 72h window.`;
              }
              // V9.17.15: split pending-resolve, mismatch, uncertain in summary
              const _pendingResolved=(reconcileResult.issues||[]).filter(i=>i.kind==='pending-resolve').length;
              const _mismatches=(reconcileResult.issues||[]).filter(i=>i.kind==='kalshi-mismatch').length;
              const _strikeUnc=(reconcileResult.issues||[]).filter(i=>i.kind==='strike-uncertain').length;
              const _closingDis=(reconcileResult.issues||[]).filter(i=>i.kind==='closing-disagrees').length;
              if(reconcileResult.issues.length===0){
                return `No issues found · matched ${_matched} of your entries to ${_checked} settled Kalshi markets${_unmatched>0?` (${_unmatched} couldn't be matched and were skipped)`:''}`;
              }
              const _parts=[];
              if(_pendingResolved>0)_parts.push(`${_pendingResolved} pending to resolve`);
              if(_mismatches>0)_parts.push(`${_mismatches} mismatch${_mismatches===1?'':'es'}`);
              if(_strikeUnc>0)_parts.push(`${_strikeUnc} strike-uncertain (review)`);
              if(_closingDis>0)_parts.push(`${_closingDis} closing-disagrees (review)`);
              return _parts.join(' · ')+` (across ${_matched} matched entries, ${_checked} markets fetched)`;
            })()),
          ]),
          React.createElement('button',{
            key:'close',
            onClick:()=>setReconcileResult(null),
            className:'p-1.5 rounded-lg text-[#EDEDED]/50 hover:text-white hover:bg-[#EDEDED]/5',
            title:'Dismiss',
          },'✕'),
        ]),
        // V9.17.12: Bulk apply bar. Two buttons — "apply all pending" and
        //   "apply all mismatches" — so the user doesn't have to click 40+
        //   times to fix a backlog of stale entries.
        reconcileResult.issues.length>1&&(onEditEntry||onDeleteEntry)?React.createElement('div',{
          key:'bulk',
          className:'flex items-center gap-2 mb-2 flex-wrap',
        },(()=>{
          const _pending=reconcileResult.issues.filter(i=>i.kind==='pending-resolve'||i.kind==='missing-fields');
          const _mismatches=reconcileResult.issues.filter(i=>i.kind==='kalshi-mismatch');
          const _sitoutFills=reconcileResult.issues.filter(i=>i.kind==='sitout-had-fill');
          const _btns=[];
          // Shared apply function — patches entries directly by id, no merge logic.
          // Uses _taraApplyReconcile which writes atomically to localStorage + React state.
          const _applyUpdates=async(issues)=>{
            const _updMap=new Map();
            issues.forEach(_iss=>{
              if(_iss.fullUpdate&&Object.keys(_iss.fullUpdate).length>0){
                _updMap.set(_iss.entryId,_iss.fullUpdate);
              } else if(_iss.suggested){
                // Fallback: just set result
                _updMap.set(_iss.entryId,{result:_iss.suggested});
              }
            });
            if(!_updMap.size)return;
            if(typeof window._taraApplyReconcile==='function'){
              // V10.9.1: _taraApplyReconcile is now async — reads full IDB history,
              //   patches, writes full history back. Must await so UI doesn't
              //   re-render against stale state mid-write.
              const applied=await window._taraApplyReconcile(_updMap);
              try{console.info(`[Reconcile] Applied ${applied} updates to full IndexedDB history`);}catch(_){}
            }
          };
          if(_pending.length>0){
            _btns.push(React.createElement('button',{
              key:'apall-pend',
              onClick:async()=>{
                if(!window.confirm(`Apply ${_pending.length} pending entries from Kalshi? This will mark each as WIN or LOSS based on Kalshi's settled result.`))return;
                await _applyUpdates(_pending);
                setReconcileResult(prev=>prev?{...prev,issues:prev.issues.filter(i=>i.kind!=='pending-resolve'&&i.kind!=='missing-fields'),pendingResolved:0}:null);
              },
              className:'px-3 py-1.5 rounded-lg text-[10px] uppercase tracking-wider font-bold',
              style:{background:'rgba(35,185,129,0.15)',color:'#23B981',border:'1px solid rgba(35,185,129,0.40)'},
            },`Apply all ${_pending.length} pending`));
          }
          if(_mismatches.length>0){
            _btns.push(React.createElement('button',{
              key:'apall-mis',
              onClick:async()=>{
                if(!window.confirm(`Apply ${_mismatches.length} corrections to mismatched entries?`))return;
                await _applyUpdates(_mismatches);
                setReconcileResult(prev=>prev?{...prev,issues:prev.issues.filter(i=>i.kind!=='kalshi-mismatch')}:null);
              },
              className:'px-3 py-1.5 rounded-lg text-[10px] uppercase tracking-wider font-bold',
              style:{background:'rgba(35,185,129,0.15)',color:'rgb(35,185,129)',border:'1px solid rgba(35,185,129,0.40)'},
            },`Apply all ${_mismatches.length} fixes`));
          }
          if(_sitoutFills.length>0){
            _btns.push(React.createElement('button',{
              key:'apall-sitoutfill',
              onClick:async()=>{
                if(!window.confirm(`Apply ${_sitoutFills.length} recovered trades from Kalshi fill history?\n\nEach of these is currently logged as SIT OUT, but a real Kalshi fill exists for that window. This will change them to the recovered direction (UP or DOWN) and mark WIN or LOSS based on Kalshi's settled result.\n\nThis cannot be undone.`))return;
                await _applyUpdates(_sitoutFills);
                setReconcileResult(prev=>prev?{...prev,issues:prev.issues.filter(i=>i.kind!=='sitout-had-fill')}:null);
              },
              className:'px-3 py-1.5 rounded-lg text-[10px] uppercase tracking-wider font-bold',
              style:{background:'rgba(35,185,129,0.15)',color:'#23B981',border:'1px solid rgba(35,185,129,0.40)'},
            },`Apply all ${_sitoutFills.length} recovered trades`));
          }
          // V10.7.43: bulk delete unverifiable entries (missing strike or closingPrice).
          //   These can never be audited (no way to verify WIN/LOSS without prices) and
          //   pollute the calibration training set. One-click clear with confirmation.
          const _unverifiable=reconcileResult.issues.filter(i=>i.kind==='missing-strike'||i.kind==='missing-close');
          const _unverifiableIds=[...new Set(_unverifiable.map(i=>i.entryId))]; // dedupe (one entry can have both kinds)
          if(_unverifiableIds.length>0&&onDeleteEntry){
            _btns.push(React.createElement('button',{
              key:'clear-unverifiable',
              onClick:()=>{
                if(!window.confirm(`DELETE ${_unverifiableIds.length} unverifiable entr${_unverifiableIds.length===1?'y':'ies'} from your log?\n\nThese trades are missing strike or closingPrice data and can't be audited. Removing them cleans the log and improves calibration accuracy.\n\nThis cannot be undone.`))return;
                _unverifiableIds.forEach(_id=>onDeleteEntry(_id));
                setReconcileResult(prev=>prev?{...prev,issues:prev.issues.filter(i=>i.kind!=='missing-strike'&&i.kind!=='missing-close')}:null);
              },
              className:'px-3 py-1.5 rounded-lg text-[10px] uppercase tracking-wider font-bold',
              style:{background:'rgba(232,69,94,0.15)',color:'rgb(232,69,94)',border:'1px solid rgba(232,69,94,0.40)'},
            },`Clear ${_unverifiableIds.length} unverifiable`));
          }
          return _btns;
        })()):null,
        reconcileResult.issues.length>0?React.createElement('div',{
          key:'list',
          className:'flex flex-col gap-1.5 max-h-[300px] overflow-y-auto pr-1',
        },reconcileResult.issues.map(_iss=>{
          const _entry=taraCallLog.find(e=>e.id===_iss.entryId);
          const _when=_entry?new Date(_entry.id).toLocaleString('en-US',{..._tzOpt,month:'short',day:'numeric',hour:'2-digit',minute:'2-digit',hour12:false}):'';
          // V9.17.15: color per kind. Uncertain kinds get amber tint to signal "review needed",
          //   confident kinds (pending-resolve, kalshi-mismatch) get neutral with green apply.
          const _isUncertain=_iss.kind==='strike-uncertain'||_iss.kind==='closing-disagrees'||_iss.kind==='sitout-had-fill';
          const _cardBg=_isUncertain?'rgba(35,185,129,0.06)':'rgba(10,10,10,0.5)';
          const _cardBorder=_isUncertain?'rgba(35,185,129,0.20)':'rgba(237,237,237,0.06)';
          return React.createElement('div',{
            key:_iss.entryId,
            className:'px-3 py-2 rounded-lg text-[11px] flex flex-col gap-1.5 sm:flex-row sm:items-center sm:justify-between',
            style:{background:_cardBg,border:`1px solid ${_cardBorder}`},
          },[
            React.createElement('div',{key:'detail',className:'flex flex-col gap-0.5 min-w-0 flex-1'},[
              React.createElement('div',{key:'when',className:'text-[9px] uppercase tracking-wider',style:{color:_isUncertain?'#23B981':'rgba(237,237,237,0.45)'}},_when+' · '+_iss.kind+(_isUncertain?' · review':'')),
              React.createElement('div',{key:'d',className:'leading-snug',style:{color:'#EDEDED',wordBreak:'break-word'}},_iss.detail),
            ]),
            React.createElement('div',{key:'actions',className:'flex items-center gap-1.5 shrink-0'},[
              _iss.suggested&&onEditEntry?React.createElement('button',{
                key:'apply',
                onClick:()=>{
                  if(_isUncertain){
                    if(!window.confirm(`This entry needs review.\n\n${_iss.detail}\n\nApply suggested ${_iss.suggested} anyway?`))return;
                  }
                  if(typeof window._taraApplyReconcile==='function'){
                    const _upd=_iss.fullUpdate&&Object.keys(_iss.fullUpdate).length>0
                      ?_iss.fullUpdate
                      :(_iss.suggested?{result:_iss.suggested}:null);
                    if(_upd){
                      const _m=new Map();_m.set(_iss.entryId,_upd);
                      Promise.resolve(window._taraApplyReconcile(_m)).catch(()=>{});
                    }
                  }else if(onEditEntry){
                    onEditEntry(_iss.entryId,_iss.fullUpdate&&Object.keys(_iss.fullUpdate).length>0?_iss.fullUpdate:_iss.suggested,_iss.fullUpdate&&Object.keys(_iss.fullUpdate).length>0?'__fullUpdate__':(_iss.suggestedField||'result'));
                  }
                  setReconcileResult(prev=>prev?{...prev,issues:prev.issues.filter(i=>i.entryId!==_iss.entryId)}:null);
                },
                className:'px-2.5 py-1 rounded-lg text-[10px] uppercase tracking-wider font-bold',
                style:_isUncertain
                  ?{background:'rgba(35,185,129,0.15)',color:'#23B981',border:'1px solid rgba(35,185,129,0.40)'}
                  :{background:'rgba(35,185,129,0.15)',color:'rgb(35,185,129)',border:'1px solid rgba(35,185,129,0.35)'},
              },`Apply → ${_iss.suggested}`):null,
              React.createElement('button',{
                key:'skip',
                onClick:()=>setReconcileResult(prev=>prev?{...prev,issues:prev.issues.filter(i=>i.entryId!==_iss.entryId)}:null),
                className:'px-2 py-1 rounded-lg text-[10px] text-[#EDEDED]/55 hover:text-white',
                title:'Skip this issue',
              },'Skip'),
            ].filter(Boolean)),
          ]);
        })):null,
        // V9.17.13: per-entry "why didn't this pending get resolved" panel.
        //   Lists each unmatched pending entry with the specific reason — past
        //   72h, no strike match, no time match, etc. Surfaces the diagnostic
        //   gap that made V9.17.12 reconcile look incomplete to the user.
        reconcileResult.kind==='reconcile'&&Array.isArray(reconcileResult.unmatchedDetails)&&reconcileResult.unmatchedDetails.length>0?React.createElement('details',{
          key:'unmatched',
          className:'mt-3 px-3 py-2 rounded-lg text-[10px]',
          style:{background:'#0B0B0F',border:'1px solid #24242E',color:'rgba(237,237,237,0.65)'},
        },[
          React.createElement('summary',{key:'sum',className:'cursor-pointer uppercase tracking-wider',style:{color:'rgba(237,237,237,0.55)'}},
            `${reconcileResult.unmatchedDetails.length} pending entr${reconcileResult.unmatchedDetails.length===1?'y':'ies'} couldn't be matched — show why`,
          ),
          React.createElement('div',{key:'list',className:'mt-2 flex flex-col gap-1 max-h-[200px] overflow-y-auto pr-1',style:{fontFamily:'IBM Plex Mono,ui-monospace,monospace'}},
            reconcileResult.unmatchedDetails.slice(0,80).map(_d=>React.createElement('div',{
              key:_d.id,
              className:'leading-snug',
              style:{color:'rgba(237,237,237,0.70)'},
            },`${_d.when} ${_d.dir} $${_d.strike.toFixed(0)} → ${_d.reason}`)),
          ),
          reconcileResult.unmatchedDetails.length>80?React.createElement('div',{key:'more',className:'mt-1 italic',style:{color:'rgba(237,237,237,0.40)'}},`(+${reconcileResult.unmatchedDetails.length-80} more not shown)`):null,
        ].filter(Boolean)):null,
        // V9.17.10: when there are no issues AND we have diagnostic info, show
        //   a small per-series breakdown so the user can see what was actually
        //   fetched. Surfaces silent fetch failures that used to hide as "no issues".
        // V13.4.225: this used to render ONLY when issues.length===0, so a run that
        //   found some issues AND failed some requests hid the failures entirely —
        //   the one case where partial coverage most needs saying. Now shown
        //   whenever there is nothing to report OR something went wrong. Coverage
        //   is included so the numbers behind the headline are checkable.
        reconcileResult.kind==='reconcile'&&reconcileResult.diag
          &&(reconcileResult.issues.length===0||(reconcileResult.diag.errors||[]).length>0)?React.createElement('div',{
          key:'diag',
          className:'mt-2 px-3 py-2 rounded-lg text-[10px]',
          style:{background:'#0B0B0F',border:'1px solid #24242E',color:'rgba(237,237,237,0.55)'},
        },[
          React.createElement('div',{key:'tt',className:'uppercase tracking-wider mb-1',style:{color:'rgba(237,237,237,0.4)'}},'Fetch diagnostic'),
          React.createElement('div',{key:'st',className:'tabular-nums leading-relaxed',style:{fontFamily:'IBM Plex Mono,ui-monospace,monospace'}},
            `attempts ${reconcileResult.diag.fetchAttempts||0} · ok ${reconcileResult.diag.fetchOk||0}`+
            (reconcileResult.eligibleEntries!=null?` · incomplete ${reconcileResult.matchedIncomplete||0}/${reconcileResult.eligibleEntries} filled · ${reconcileResult.alreadyComplete||0} already complete`:'')+
            (reconcileResult.diag.settlementsTotal!=null?` · settlements ${reconcileResult.diag.settlementsTotal}`:'')+
            (reconcileResult.diag.bucketsFetched?` · ${reconcileResult.diag.bucketsFetched} buckets fetched`:'')+
            (reconcileResult.diag.marketsBySeries?` · markets per series: `+Object.entries(reconcileResult.diag.marketsBySeries).map(([k,v])=>`${k}=${v}`).join(' '):''),
          ),
          reconcileResult.diag.errors&&reconcileResult.diag.errors.length>0?React.createElement('div',{key:'er',className:'mt-1',style:{color:'rgba(232,69,94,0.7)'}},
            `errors: ${reconcileResult.diag.errors.slice(0,3).join(' · ')}`,
          ):null,
        ].filter(Boolean)):null,
      ]);
      })():null,
      // V9.2.1: P&L SUMMARY + CUMULATIVE CURVE — computed from betAmt/maxPay fields.
      //   Shows total dollar P&L, average win/loss size, and a mini cumulative curve.
      (()=>{
        const _withBets=filtered.filter(e=>e&&e.betAmt>0&&(e.result==='WIN'||e.result==='LOSS'));
        if(_withBets.length<3)return null; // need enough data
        let _totalPnl=0,_totalWinAmt=0,_totalLossAmt=0,_winCount=0,_lossCount=0;
        const _curve=[];
        // Sort chronologically for curve
        const _sorted=[..._withBets].sort((a,b)=>(a.id||0)-(b.id||0));
        _sorted.forEach(e=>{
          const _pnl=e.result==='WIN'?(e.maxPay||0)-(e.betAmt||0):-(e.betAmt||0);
          _totalPnl+=_pnl;
          if(e.result==='WIN'){_totalWinAmt+=_pnl;_winCount++;}
          else{_totalLossAmt+=Math.abs(_pnl);_lossCount++;}
          _curve.push({pnl:_totalPnl,i:_curve.length});
        });
        const _avgWin=_winCount>0?_totalWinAmt/_winCount:0;
        const _avgLoss=_lossCount>0?_totalLossAmt/_lossCount:0;
        const _roi=_totalLossAmt>0?(_totalPnl/(_totalLossAmt+_totalWinAmt))*100:0;
        // Mini SVG cumulative P&L curve
        const _svgW=400,_svgH=60;
        const _minPnl=Math.min(0,..._curve.map(c=>c.pnl));
        const _maxPnl=Math.max(0,..._curve.map(c=>c.pnl));
        const _range=Math.max(1,_maxPnl-_minPnl);
        const _points=_curve.map((c,i)=>{
          const x=(i/(_curve.length-1))*_svgW;
          const y=_svgH-(((c.pnl-_minPnl)/_range)*_svgH);
          return`${x.toFixed(1)},${y.toFixed(1)}`;
        }).join(' ');
        const _zeroY=_svgH-(((-_minPnl)/_range)*_svgH);
        return React.createElement('div',{className:'mb-4 p-3 rounded-xl bg-[#101014] border border-[#24242E]'},
          React.createElement('div',{className:'flex items-baseline justify-between mb-2'},
            React.createElement('span',{className:'text-[9px] uppercase tracking-wider text-[#EDEDED]/40 font-bold'},'P&L Summary'),
            React.createElement('span',{className:'text-[10px] tabular-nums text-[#EDEDED]/50'},`${_withBets.length} trades with bet amounts`)
          ),
          React.createElement('div',{className:'grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-3 mb-3'},
            React.createElement('div',null,
              React.createElement('div',{className:'text-[8px] uppercase tracking-wider text-[#EDEDED]/35'},'Total P&L'),
              React.createElement('div',{className:'text-lg font-bold tabular-nums',style:{color:_totalPnl>=0?'rgb(35,185,129)':'rgb(232,69,94)'}},`${_totalPnl>=0?'+':''}$${_totalPnl.toFixed(2)}`)
            ),
            React.createElement('div',null,
              React.createElement('div',{className:'text-[8px] uppercase tracking-wider text-[#EDEDED]/35'},'Avg Win'),
              React.createElement('div',{className:'text-sm font-bold tabular-nums text-emerald-400'},`+$${_avgWin.toFixed(2)}`)
            ),
            React.createElement('div',null,
              React.createElement('div',{className:'text-[8px] uppercase tracking-wider text-[#EDEDED]/35'},'Avg Loss'),
              React.createElement('div',{className:'text-sm font-bold tabular-nums text-rose-400'},`-$${_avgLoss.toFixed(2)}`)
            ),
            React.createElement('div',null,
              React.createElement('div',{className:'text-[8px] uppercase tracking-wider text-[#EDEDED]/35'},'ROI'),
              React.createElement('div',{className:'text-sm font-bold tabular-nums',style:{color:_roi>=0?'rgb(35,185,129)':'rgb(232,69,94)'}},`${_roi>=0?'+':''}${_roi.toFixed(1)}%`)
            ),
          ),
          // Cumulative P&L curve
          React.createElement('svg',{viewBox:`0 0 ${_svgW} ${_svgH}`,className:'w-full h-12 opacity-70'},
            // Zero line
            React.createElement('line',{x1:0,y1:_zeroY,x2:_svgW,y2:_zeroY,stroke:'rgba(237,237,237,0.15)',strokeDasharray:'4,4'}),
            // Curve
            React.createElement('polyline',{
              points:_points,
              fill:'none',
              stroke:_totalPnl>=0?'rgb(35,185,129)':'rgb(232,69,94)',
              strokeWidth:1.5,
              strokeLinejoin:'round',
            })
          ),
        );
      })(),
      //   exactly which decision tiers are dragging down the average so we know
      //   what to tighten further. Filtered by current windowType filter.
      (()=>{
        let _arr=taraCallLog;
        if(filter==='15m'||filter==='5m')_arr=_arr.filter(e=>e.windowType===filter);
        const _bucketize=(e)=>e.isStructuralLed?'structural':e.isSuperConfluent?'super':e.isConfluent?'confluence':e.isTapeLed?'tape':e.isRisingConfluence?'rising':e.isUserForced?'forced':'single';
        const _tiers=['super','confluence','structural','tape','rising','forced','single'];
        const _tierLabels={super:'★ Super',confluence:'★ Confluence',structural:'◈ Structural',tape:'⚡ Tape-led',rising:'↗ Rising',forced:'⚙ Forced',single:'· Single'};
        const _tierColors={super:T2_GOLD,confluence:T2_GOLD,structural:'#C4B5FD',tape:'#7DD3FC',rising:'#A6E3A1',forced:'rgba(35,185,129,0.7)',single:'rgba(237,237,237,0.5)'};
        const _stats={};
        _tiers.forEach(t=>{_stats[t]={wins:0,losses:0};});
        _arr.forEach(e=>{
          if(e.result!=='WIN'&&e.result!=='LOSS')return;
          const _b=_bucketize(e);
          if(_stats[_b])_stats[_b][e.result==='WIN'?'wins':'losses']+=1;
        });
        const _activeTiers=_tiers.filter(t=>_stats[t].wins+_stats[t].losses>0);
        if(_activeTiers.length===0)return null;
        return React.createElement('div',{className:'bg-[#0A0A0E] border border-[#1B1B22] rounded-[10px] p-3 mb-4'},
          React.createElement('div',{className:'flex items-baseline justify-between mb-2'},
            React.createElement('div',{className:'text-[9px] uppercase tracking-wider text-[#EDEDED]/40 font-bold'},'Win rate by tier'),
            React.createElement('div',{className:'text-[8px] uppercase tracking-wider text-[#EDEDED]/30'},filter==='15m'?'15m only':filter==='5m'?'5m only':'all windows'),
          ),
          React.createElement('div',{className:'grid grid-cols-2 sm:grid-cols-4 gap-2'},
            _activeTiers.map(t=>{
              const s=_stats[t];const total=s.wins+s.losses;const wr=total>0?Math.round((s.wins/total)*100):0;
              const _wrColor=wr>=70?'rgb(35,185,129)':wr>=55?'#fff':'rgb(232,69,94)';
              return React.createElement('div',{key:t,className:'rounded-lg p-2',style:{background:'#0E0E12',border:'1px solid #24242E'}},
                React.createElement('div',{className:'text-[8px] uppercase tracking-[0.14em] font-bold mb-1',style:{color:_tierColors[t]}},_tierLabels[t]),
                React.createElement('div',{className:'flex items-baseline gap-1.5'},
                  React.createElement('span',{className:'text-base font-bold tabular-nums',style:{color:_wrColor}},`${wr}%`),
                  React.createElement('span',{className:'text-[9px] tabular-nums text-[#EDEDED]/40'},`${s.wins}W·${s.losses}L`),
                ),
              );
            }),
          ),
        );
      })(),
      // V9.2.0: Asset filter — separate BTC vs ETH views
      React.createElement('div',{className:'flex flex-wrap gap-1 mb-2'},
        [
          {k:'all',label:`All (${counts.btcN})`,color:'rgba(237,237,237,0.7)'},
          {k:'BTC',label:`₿ BTC (${counts.btcN})`,color:'rgb(212,162,76)'},
        ].map(({k,label,color})=>(
          React.createElement('button',{
            key:k,
            onClick:()=>setAssetFilter(k),
            className:'px-3 py-1 text-[10px] uppercase font-bold tracking-wider rounded-lg transition-colors',
            style:assetFilter===k?{background:'rgba(237,237,237,0.10)',color,border:`1px solid ${color}`}:{border:'1px solid #24242E',color:'rgba(237,237,237,0.40)'},
          },label)
        )),
      ),
      React.createElement('div',{className:'flex flex-wrap gap-1 mb-4'},
        ['all','calendar','15m','5m','wins','losses','sitouts'].map(f=>(
          React.createElement('button',{
            key:f,
            onClick:()=>setFilter(f),
            className:'px-3 py-1 text-[10px] uppercase font-bold tracking-wider rounded-lg transition-colors '+(filter===f?'':'text-[#EDEDED]/40 hover:text-[#EDEDED]/70'),
            style:filter===f?{background:'rgba(35,185,129,0.12)',color:T2_GOLD,border:'1px solid rgba(35,185,129,0.3)'}:{border:'1px solid #24242E'},
          },f==='calendar'?'📅 calendar':f)
        )),
      ),
      // V6.5.1: CALENDAR VIEW — performance by day with hourly breakdown
      filter==='calendar'
        ? (()=>{
            // V6.5.3: Helper to extract the hour (0-23) in the selected timezone.
            const _hourInTz=(d)=>{
              try{const s=d.toLocaleString('en-US',{..._tzOpt,hour12:false,hour:'2-digit'});return parseInt(s,10)%24;}catch(e){return d.getHours();}
            };
            // Group entries by date in the selected timezone
            const byDay=new Map();
            taraCallLog.forEach(e=>{
              // V10.9.26: fall back to e.id when e.time missing.
              //   e.time was only backfilled on ~10% of entries (since V10.6.x);
              //   90% had no e.time and were silently skipped — invisible in calendar.
              //   e.id IS the lock timestamp and equals e.time when both exist.
              const _t=e?.time||e?.id;
              if(!e||!_t)return;
              const d=new Date(_t);
              const key=d.toLocaleDateString('en-US',{..._tzOpt,year:'numeric',month:'2-digit',day:'2-digit'});
              if(!byDay.has(key))byDay.set(key,{date:d,entries:[]});
              byDay.get(key).entries.push(e);
            });
            // Sort newest first
            const days=Array.from(byDay.entries()).map(([k,v])=>({key:k,...v})).sort((a,b)=>b.date-a.date);
            if(days.length===0){
              return React.createElement('div',{className:'bg-[#0A0A0E] border border-[#1B1B22] rounded-[10px] p-8 text-center text-[#EDEDED]/40 italic'},'No data yet.');
            }
            return React.createElement('div',{className:'space-y-3 max-h-[65vh] overflow-y-auto pr-1'},
              days.map(day=>{
                const _entries=day.entries;
                const _wins=_entries.filter(e=>e.result==='WIN').length;
                const _losses=_entries.filter(e=>e.result==='LOSS').length;
                const _sitouts=_entries.filter(e=>e.result==='SITOUT').length;
                const _decided=_wins+_losses;
                const _dayWr=_decided>0?Math.round((_wins/_decided)*100):null;
                const _wrColor=_dayWr==null?'#EDEDED':_dayWr>=70?'rgb(35,185,129)':_dayWr>=50?T2_GOLD:'rgb(232,69,94)';
                const _dayLabel=day.date.toLocaleDateString('en-US',{..._tzOpt,weekday:'short',month:'short',day:'numeric'});
                // Build hourly bins (24 hours) in the selected timezone
                const hourBins=Array.from({length:24},()=>({wins:0,losses:0,sitouts:0,total:0}));
                _entries.forEach(e=>{
                  const h=_hourInTz(new Date(e.time||e.id)); // V10.9.26: id fallback
                  hourBins[h].total++;
                  if(e.result==='WIN')hourBins[h].wins++;
                  else if(e.result==='LOSS')hourBins[h].losses++;
                  else if(e.result==='SITOUT')hourBins[h].sitouts++;
                });
                const _maxHourTotal=Math.max(1,...hourBins.map(b=>b.total));
                return React.createElement('div',{key:day.key,className:'bg-[#0A0A0E] border border-[#1B1B22] rounded-[10px] p-3 sm:p-4'},
                  // Day header
                  React.createElement('div',{className:'flex items-baseline justify-between mb-3 flex-wrap gap-2'},
                    React.createElement('div',{className:'flex items-baseline gap-3'},
                      React.createElement('span',{className:'font-serif text-lg text-white tracking-tight'},_dayLabel),
                      React.createElement('span',{className:'text-[10px] uppercase tracking-wider text-[#EDEDED]/35'},_entries.length,' calls'),
                    ),
                    React.createElement('div',{className:'flex items-baseline gap-3 tabular-nums text-[11px]'},
                      _dayWr!==null&&React.createElement('span',{className:'font-bold',style:{color:_wrColor}},_dayWr,'% WR'),
                      React.createElement('span',{className:'text-emerald-400/80'},_wins,'W'),
                      React.createElement('span',{className:'text-rose-400/80'},_losses,'L'),
                      React.createElement('span',{style:{color:T2_GOLD,opacity:0.7}},_sitouts,' sit'),
                    ),
                  ),
                  // Hourly bar — 24 columns
                  React.createElement('div',{className:'gap-px mb-1 overflow-x-auto',style:{display:'grid',gridTemplateColumns:'repeat(24, minmax(8px, 1fr))',minWidth:'min(100%, 320px)'}},
                    hourBins.map((b,h)=>{
                      const _pct=b.total/_maxHourTotal;
                      const _hWr=(b.wins+b.losses)>0?b.wins/(b.wins+b.losses):null;
                      const _bgColor=b.total===0?'rgba(237,237,237,0.04)':_hWr==null?'rgba(35,185,129,0.20)':_hWr>=0.7?'rgba(35,185,129,0.45)':_hWr>=0.5?'rgba(35,185,129,0.45)':'rgba(232,69,94,0.45)';
                      const _hHeight=Math.max(8,_pct*32);
                      return React.createElement('div',{
                        key:h,
                        title:`${h}:00 — ${b.total} calls (${b.wins}W ${b.losses}L${b.sitouts?' '+b.sitouts+'sit':''})`,
                        className:'flex items-end justify-center',
                        style:{height:36},
                      },React.createElement('div',{
                        style:{
                          width:'100%',
                          height:_hHeight+'px',
                          background:_bgColor,
                          borderRadius:1,
                        },
                      }));
                    }),
                  ),
                  // Hour labels (every 6h)
                  React.createElement('div',{className:'gap-px text-[8px] text-[#EDEDED]/30 tabular-nums mb-2 overflow-x-auto',style:{display:'grid',gridTemplateColumns:'repeat(24, minmax(8px, 1fr))',minWidth:'min(100%, 320px)'}},
                    Array.from({length:24},(_,h)=>(
                      React.createElement('div',{key:h,className:'text-center'},h%6===0?h:'')
                    )),
                  ),
                  // Per-call mini-list (collapsed to most recent 5 by default)
                  React.createElement('div',{className:'mt-2 pt-2 border-t border-[#24242E] space-y-1'},
                    _entries.slice().reverse().slice(0,5).map(e=>{
                      const _t=new Date(e.time||e.id).toLocaleTimeString('en-US',{..._tzOpt,hour:'2-digit',minute:'2-digit',hour12:false}); // V10.9.26
                      const _per=_periodFromWindowId(e.windowId,e.windowType);
                      const _strikeFmt=_fmtPrice(e.strike);
                      const _closeFmt=_fmtPrice(e.closingPrice);
                      return React.createElement('div',{key:e.id,className:'flex items-baseline gap-2 sm:gap-3 text-[10px] tabular-nums flex-wrap'},
                        React.createElement('span',{className:'text-[#EDEDED]/40 shrink-0',style:{minWidth:48}},_t),
                        React.createElement('span',{className:'text-[#EDEDED]/60 shrink-0 truncate',style:{minWidth:80,maxWidth:120}},_per||e.windowType),
                        React.createElement('span',{className:'font-bold shrink-0',style:_dirStyle(e.dir)},e.dir==='UP'?'▲ UP':e.dir==='DOWN'?'▼ DOWN':'· SIT'),
                        e.confidence>0&&React.createElement('span',{className:'text-[#EDEDED]/40 shrink-0'},e.confidence,'%'),
                        _strikeFmt&&_closeFmt&&React.createElement('span',{className:'text-[#EDEDED]/45 shrink-0 hidden sm:inline'},_strikeFmt,' → ',_closeFmt),
                        React.createElement('span',{className:'ml-auto text-[10px] uppercase font-bold tracking-wider shrink-0',style:_resultStyle(e.result)},e.result||'pend'),
                      );
                    }),
                    _entries.length>5&&React.createElement('div',{className:'text-[9px] text-[#EDEDED]/30 italic pt-1'},'+ ',_entries.length-5,' more calls earlier in the day'),
                  ),
                );
              }),
            );
          })()
        : React.createElement('div',{className:'bg-[#0A0A0E] border border-[#1B1B22] rounded-[10px] overflow-hidden'},
        filtered.length===0
          ? React.createElement('div',{className:'p-8 text-center text-[#EDEDED]/40 italic'},'No calls match this filter yet.')
          : (()=>{
              // V7.10.6: Group entries by date for cleaner display with sticky headers.
              //   Within each date group: entries appear newest-first (filtered is already
              //   reversed). Date label uses the selected timezone (local or NY).
              const _dayKeyFor=(t)=>{
                try{return new Date(t).toLocaleDateString('en-US',{..._tzOpt,year:'numeric',month:'2-digit',day:'2-digit'});}
                catch(e){return'unknown';}
              };
              const _byDay=new Map();
              // V13.4.208: group only the slice being rendered -- grouping the full
              //   array and trimming afterwards still walks every entry, which is
              //   most of the cost.
              const _visible=filtered.slice(0,rowLimit);
              const _hiddenCount=Math.max(0,filtered.length-_visible.length);
              _visible.forEach(e=>{
                const _et=e?.time||e?.id; // V10.9.26: id fallback
                if(!e||!_et)return;
                const k=_dayKeyFor(_et);
                if(!_byDay.has(k))_byDay.set(k,[]);
                _byDay.get(k).push(e);
              });
              const _days=Array.from(_byDay.entries());
              return React.createElement('div',{className:'max-h-[60vh] overflow-y-auto'},
                _days.map(([_dayKey,_entries])=>{
                  // Day header info
                  const _firstEntry=_entries[0];
                  const _dayDate=new Date(_firstEntry.time||_firstEntry.id); // V10.9.26
                  const _dayLabel=_dayDate.toLocaleDateString('en-US',{..._tzOpt,weekday:'short',month:'short',day:'numeric'});
                  // Day-level WR
                  const _wins=_entries.filter(e=>e.result==='WIN').length;
                  const _losses=_entries.filter(e=>e.result==='LOSS').length;
                  const _sitouts=_entries.filter(e=>e.result==='SITOUT').length;
                  const _resolved=_wins+_losses;
                  const _wr=_resolved>0?Math.round((_wins/_resolved)*100):null;
                  const _wrColor=_wr==null?'rgba(237,237,237,0.4)':_wr>=70?'rgb(35,185,129)':_wr>=55?'rgba(237,237,237,0.85)':_wr>=45?'#23B981':'rgb(232,69,94)';
                  return React.createElement('div',{key:_dayKey},
                    // Sticky day header
                    React.createElement('div',{
                      className:'sticky top-0 z-10 px-3 sm:px-4 py-2 flex items-baseline justify-between gap-2 backdrop-blur-md',
                      style:{
                        background:'#050508',
                        borderBottom:'1px solid #24242E',
                        borderTop:'1px solid rgba(237,237,237,0.04)',
                      },
                    },
                      React.createElement('div',{className:'flex items-baseline gap-2'},
                        React.createElement('span',{className:'text-[11px] uppercase font-bold tracking-[0.14em]',style:{color:T2_GOLD}},_dayLabel),
                        React.createElement('span',{className:'text-[9px] tabular-nums text-[#EDEDED]/35'},_entries.length,' call',_entries.length===1?'':'s'),
                      ),
                      _wr!=null&&React.createElement('div',{className:'flex items-baseline gap-2 text-[10px] tabular-nums'},
                        React.createElement('span',{style:{color:_wrColor},className:'font-bold'},_wr,'% WR'),
                        React.createElement('span',{className:'text-[#EDEDED]/35'},_wins,'W·',_losses,'L',_sitouts>0?'·'+_sitouts+'so':''),
                      ),
                    ),
                    // Entries for this day
                    React.createElement('div',{className:'divide-y divide-[#EDEDED]/5'},
                      _entries.map((e)=>{
                        const _period=_periodFromWindowId(e.windowId,e.windowType);
                        const _strikeFmt=_fmtPrice(e.strike);
                        const _closeFmt=_fmtPrice(e.closingPrice);
                        const _timeLabel=new Date(e.time||e.id).toLocaleTimeString('en-US',{..._tzOpt,hour:'2-digit',minute:'2-digit',hour12:false}); // V10.9.27: id fallback (was 'Invalid Date' on the ~90% of entries with no e.time)
                        const _dirLabel=e.dir==='UP'?'▲ UP':e.dir==='DOWN'?'▼ DOWN':e.dir==='NO_TRADE'?'⊘ NO TRADE':'· SIT OUT';
                        // V7.10.6: Phase badge — backfilled from timestamp if entry lacks session
                        const _phaseKey=e.phase||(typeof inferPhaseFromTimestamp==='function'?inferPhaseFromTimestamp(e.time||e.id):null); // V10.9.27: id fallback
                        const _phaseProfile=_phaseKey&&typeof PHASE_PROFILES!=='undefined'?PHASE_PROFILES[_phaseKey]:null;
                        return(
                        React.createElement('div',{key:e.id,className:'px-2 sm:px-4 py-2.5 sm:py-3 hover:bg-[#EDEDED]/3 transition-colors overflow-hidden min-w-0 w-full'},
                          // Header row: time · windowType · period · session · result badge
                          React.createElement('div',{className:'flex items-center justify-between gap-3 mb-2'},
                            React.createElement('div',{className:'flex items-baseline gap-2 sm:gap-3 min-w-0 flex-1 flex-wrap'},
                              React.createElement('span',{className:'tabular-nums text-[11px] text-[#EDEDED]/65 font-bold shrink-0'},_timeLabel),
                              React.createElement('span',{className:'tabular-nums text-[9px] text-[#EDEDED]/30 shrink-0 px-1.5 py-0.5 rounded-lg',style:{background:'#0E0E12'}},(e.windowType||'').toUpperCase()),
                              _period&&React.createElement('span',{className:'tabular-nums text-[10px] text-[#EDEDED]/55 shrink-0',style:T2_MONO_STYLE},_period),
                              // Phase / session badge
                              _phaseProfile&&React.createElement('span',{
                                className:'text-[8px] uppercase font-bold tracking-[0.12em] shrink-0 px-1.5 py-0.5 rounded-lg inline-flex items-center gap-1',
                                style:{background:'rgba(237,237,237,0.04)',color:_phaseProfile.color},
                                title:_phaseProfile.character,
                              },_phaseProfile.flag,' ',_phaseProfile.label),
                              // Asset chip if present
                              e.asset&&React.createElement('span',{
                                className:'text-[8px] uppercase font-bold tracking-[0.14em] shrink-0 px-1.5 py-0.5 rounded-lg',
                                style:{
                                  background:e.asset==='BTC'?'rgba(247,147,26,0.1)':'rgba(98,126,234,0.1)',
                                  color:e.asset==='BTC'?'rgba(247,147,26,0.8)':'rgba(98,126,234,0.8)',
                                  border:e.asset==='BTC'?'1px solid rgba(247,147,26,0.2)':'1px solid rgba(98,126,234,0.2)',
                                },
                              },e.asset),
                            ),
                            editingId===e.id&&onEditEntry
                              ? React.createElement('div',{className:'flex flex-col items-end gap-1 shrink-0 min-w-0'},
                                  // V9.9.3: dual-mode editor. Direction edit is the new feature —
                                  //   user reports Tara sometimes logs the wrong direction (e.g. locked
                                  //   UP but log shows DOWN). Two rows: direction first (the upstream
                                  //   thing to fix) then result (which depends on direction). Saving
                                  //   either field marks manualEdit=true and timestamps.
                                  React.createElement('div',{key:'dirRow',className:'flex items-center gap-1'},
                                    React.createElement('span',{className:'text-[8px] uppercase font-bold tracking-wider text-[#EDEDED]/40 mr-1'},'dir'),
                                    ['UP','DOWN','SIT_OUT'].map(opt=>(
                                      React.createElement('button',{
                                        key:opt,
                                        onClick:()=>onEditEntry(e.id,opt,'direction'),
                                        className:'px-2 py-0.5 rounded-lg text-[9px] uppercase font-bold tracking-wider transition-colors',
                                        style:{
                                          color:opt==='UP'?'rgb(35,185,129)':opt==='DOWN'?'rgb(232,69,94)':T2_GOLD,
                                          background:e.dir===opt?(opt==='UP'?'rgba(35,185,129,0.18)':opt==='DOWN'?'rgba(232,69,94,0.18)':'rgba(35,185,129,0.18)'):'rgba(237,237,237,0.04)',
                                          border:e.dir===opt?'1px solid currentColor':'1px solid #24242E',
                                        },
                                      },opt==='SIT_OUT'?'sit':opt.toLowerCase())
                                    )),
                                  ),
                                  React.createElement('div',{key:'resRow',className:'flex items-center gap-1'},
                                    React.createElement('span',{className:'text-[8px] uppercase font-bold tracking-wider text-[#EDEDED]/40 mr-1'},'res'),
                                    ['WIN','LOSS','SITOUT'].map(opt=>(
                                      React.createElement('button',{
                                        key:opt,
                                        onClick:()=>onEditEntry(e.id,opt,'result'),
                                        className:'px-2 py-0.5 rounded-lg text-[9px] uppercase font-bold tabular-nums tracking-wider transition-colors',
                                        style:{
                                          color:opt==='WIN'?'rgb(35,185,129)':opt==='LOSS'?'rgb(232,69,94)':T2_GOLD,
                                          background:e.result===opt?(opt==='WIN'?'rgba(35,185,129,0.18)':opt==='LOSS'?'rgba(232,69,94,0.18)':'rgba(35,185,129,0.18)'):'rgba(237,237,237,0.04)',
                                          border:e.result===opt?'1px solid currentColor':'1px solid #24242E',
                                        },
                                      },opt)
                                    )),
                                    React.createElement('button',{onClick:()=>setEditingId(null),className:'px-1.5 py-0.5 rounded-lg text-[10px] text-[#EDEDED]/35 hover:text-[#EDEDED]/70 ml-1',title:'Done'},'✓'),
                                  ),
                                )
                              : React.createElement('div',{className:'flex items-center gap-1.5 shrink-0'},
                                  e.manualEdit&&React.createElement('span',{className:'text-[9px] text-[#EDEDED]/35',title:'Manually edited'},'✎'),
                                  React.createElement('span',{className:'text-[10px] uppercase font-bold tabular-nums tracking-wider px-2 py-0.5 rounded-lg shrink-0',style:{..._resultStyle(e.result),background:e.result==='WIN'?'rgba(35,185,129,0.10)':e.result==='LOSS'?'rgba(232,69,94,0.10)':e.result==='SITOUT'?T2_SITOUT_BG:e.result==='NO_TRADE'?'rgba(180,180,180,0.08)':'rgba(237,237,237,0.05)'}},e.result==='NO_TRADE'?'no trade':e.result||'pending'),
                                  onEditEntry&&React.createElement('button',{
                                    onClick:()=>setEditingId(e.id),
                                    className:'p-1 rounded-lg text-[#EDEDED]/30 hover:text-[#EDEDED]/70 hover:bg-[#EDEDED]/5 transition-colors',
                                    title:'Override direction or result',
                                    style:{fontSize:11,lineHeight:1},
                                  },'✎'),
                                ),
                          ),
                          // Mid row: direction + confidence + posterior
                          React.createElement('div',{className:'flex items-baseline gap-2 sm:gap-3 mb-1.5 flex-wrap'},
                            React.createElement('span',{className:'font-bold text-sm tabular-nums shrink-0',style:_dirStyle(e.dir)},_dirLabel),
                            e.confidence>0&&React.createElement('span',{className:'text-[11px] text-[#EDEDED]/55 tabular-nums shrink-0'},Math.min(99,Math.round(e.confidence)),'% conf'),
                            e.posterior!=null&&e.dir!=='SIT_OUT'&&e.dir!=='NO_TRADE'&&React.createElement('span',{className:'text-[10px] text-[#EDEDED]/40 tabular-nums shrink-0'},'· post ',Math.round(e.posterior),'%'),
                            // Show caution note if present (V7.8 commits)
                            e.caution&&React.createElement('span',{className:'text-[10px] shrink-0',style:{color:'rgba(35,185,129,0.7)'}},'· ',e.caution),
                          ),
                          // Strike → Close + gap + meta footer
                          (_strikeFmt||e.regime||e.tier)&&React.createElement('div',{className:'flex items-center justify-between gap-2 text-[10px] text-[#EDEDED]/55 flex-wrap'},
                            React.createElement('div',{className:'flex items-baseline gap-2 sm:gap-3 flex-wrap min-w-0',style:T2_MONO_STYLE},
                              _strikeFmt&&React.createElement('span',{className:'tabular-nums shrink-0'},
                                React.createElement('span',{className:'text-[#EDEDED]/35 mr-1'},'Strike'),
                                _strikeFmt,
                              ),
                              _closeFmt&&React.createElement('span',{className:'tabular-nums shrink-0'},
                                React.createElement('span',{className:'text-[#EDEDED]/35 mr-1'},'→'),
                                _closeFmt,
                              ),
                              e.gapBps!=null&&e.result!=='SITOUT'&&e.result!=='NO_TRADE'&&React.createElement('span',{className:'text-[10px] tabular-nums shrink-0',style:{color:e.gapBps>=0?'#23B981':'#E8455E'}},formatSignedInt(e.gapBps)+' bps'),
                            ),
                            React.createElement('div',{className:'flex items-baseline gap-2 text-[#EDEDED]/35 flex-wrap',style:{fontSize:9}},
                              e.regime&&React.createElement('span',null,e.regime),
                              e.qScore!=null&&React.createElement('span',null,'q',e.qScore),
                              e.tier&&React.createElement('span',null,e.tier),
                              e.closeSource==='kalshi'&&React.createElement('span',{className:'italic'},'kalshi-settled'),
                            ),
                          ),
                          // Reason text on its own line if present (often informative for no-go entries)
                          e.reason&&(e.dir==='NO_TRADE'||e.result==='NO_TRADE')&&React.createElement('div',{className:'mt-1.5 text-[10px] italic',style:{color:'rgba(237,237,237,0.45)'}},e.reason),
                        )
                      );})
                    ),
                  );
                }),
                _hiddenCount>0&&React.createElement('button',{key:'_showmore',
                  onClick:()=>setRowLimit(n=>n+_ROW_STEP*4),
                  className:'w-full py-3 text-[11px] uppercase tracking-[0.12em] font-bold border-t border-[#24242E] hover:bg-[#EDEDED]/5 transition-colors',
                  style:{color:'rgba(255,255,255,0.72)'},
                },'show more · '+_hiddenCount+' older hidden'),
              );
            })(),
      ),
      React.createElement('div',{className:'mt-4 text-[10px] text-[#EDEDED]/40 leading-relaxed'},'Memory is cloud-synced — same record across devices. Showing ',filtered.length,' of ',taraCallLog.length,' total. Capped at 500 most recent.'),
    ),
  );
}

// ── V111: ProjectionsCard with clickable timeframe tabs ──
// V4.2: Now also renders Tara's Call at the top of the column.
function ProjectionsCard({analysis,mobileTab,taraCall,taraScorecards,taraCallLog,windowType,timeState,taraLearnings,onSoftHint,onHardForce,kalshiYesPrice,useLocalTime,timeFormat,onEditEntry,onDeleteEntry,convictionTrajectory,todayData,movementRisk,bestWindowsToday,handleManualSync,userPosition,tapeWindows,whaleLog,orderBook,targetMargin,reversalRisk,lockedSnapshotDir,lockedSnapshot,onHourlyLock,hourlyContext}){
  const[activeTimeframe,setActiveTimeframe]=React.useState('5m');
  const projections=analysis?.projections||[];
  const proj=projections.find(p=>p.id===activeTimeframe)||projections[0];
  const currentPrice=analysis?.currentPrice||proj?.price||0;
  const conf=proj?Number(proj.conf||0):0;
  const tabs=[{id:'5m',label:'5 MIN'},{id:'15m',label:'15 MIN'},{id:'1h',label:'1 HOUR'}];

  return(
    <div className={'bg-[#0A0A0E] p-3 sm:p-4 rounded-[10px] border border-[#1B1B22] flex flex-col relative min-w-0 '}>
      <T2Stamp code="PROJ · 042"/>

      {/* V4.2: TARA'S CALL — primary panel, top of column.
          V6.2.3: hidden lg:block (was md:block). */}
      <TaraCallCard taraCall={taraCall} taraScorecards={taraScorecards} taraCallLog={taraCallLog} windowType={windowType} timeState={timeState} analysis={analysis} taraLearnings={taraLearnings} onSoftHint={onSoftHint} onHardForce={onHardForce} kalshiYesPrice={kalshiYesPrice} useLocalTime={useLocalTime} timeFormat={timeFormat} onEditEntry={onEditEntry} onDeleteEntry={onDeleteEntry} convictionTrajectory={convictionTrajectory} todayData={todayData} movementRisk={movementRisk} bestWindowsToday={bestWindowsToday} handleManualSync={handleManualSync} userPosition={userPosition} reversalRisk={reversalRisk} className="hidden lg:block" desktopSplit={true}/>

      {/* V9.1.5: Tape + Depth render as upgraded compact strips next to the small
          DOM bar at the top of the analysis card. No big panels here anymore. */}

      {/* V13.4.94: price projections REMOVED, hourly ladder takes this slot.
          The 5m/15m/1h linear-extrapolation forecasts were decorative -- they restated
          current drift as a future price with a decaying confidence bar, and nothing
          measured ever consumed them. The hourly ladder is live market data with real
          entry prices, so it earns the space.
          EM-DASH BASELINE re-based 3298 -> 3297: the removed timeline row carried one
          em-dash in its title attribute. Deliberate, same as the 13.4.75 auto-exec strip. */}
      {/* V13.4.148: Trade Coach + Hourly ladder MOVED to the right column (RightPanel).
          They are the two things read while a window is live, and stacking them at the
          top of the middle column pushed the score breakdown and engine log off-screen,
          forcing a scroll at exactly the moment there is no time to scroll. Rendered in
          RightPanel now so coach, ladder, score and log are all visible at once.
          Set localStorage 'taraCoachLeft'='1' to render them here again instead. */}
      {(()=>{try{return localStorage.getItem('taraCoachLeft')==='1';}catch(_e){return false;}})()&&(
        <>
          <TradeCoachCall taraCall={taraCall} analysis={analysis} lockedSnapshotDir={lockedSnapshotDir} lockedSnapshot={lockedSnapshot} kalshiYesPrice={kalshiYesPrice} timeState={timeState} windowType={windowType} userPosition={userPosition}/>
          <HourlyLadderPanel {...hourlyContext}/>
        </>
      )}
    </div>
  );
}

// ── V3.1.12: BrainView — Tara's synthesized current thinking ──
//   The other UI surfaces (badges, score breakdown, engine log) show fragments. This view
//   pulls them together into one readable place that answers:
//     1. What is she calling and why? (one-paragraph synthesis)
//     2. What signals are pulling each direction? (clean for/against ledger)
//     3. What is she watching for? (what would change her call)
//     4. What's blocking entry? (gates that fired, what would unblock them)
//
//   Designed to read like a trader's thought process, not a dump of internals.
function BrainView({analysis,qualityGate,scorecards,baseline,kalshiDebug,strikeSource,strikeMode,taraCall,taraScorecards,windowType,onClose,embedded=false,snapshot=null,learning}){
  if(!analysis)return embedded?<><TaraBrainSummary snapshot={snapshot} candidate={taraCall} analysis={null} lock={readLockState(snapshot)}/><LearningStatus learning={learning}/></>:null;
  const post=analysis.rawProbAbove||50;
  // V3.2.1: Acknowledge balanced posterior. When |post-50|<3, signals are genuinely mixed.
  //   Don't force a UP/DOWN tiebreaker that creates false 'UP bias' framing.
  const conviction=Math.abs(post-50);
  const isBalanced=conviction<3;
  const dir=post>=50?'UP':'DOWN';
  const fgt=analysis.mtfAlignment||0;
  const fgtAbs=Math.abs(fgt);
  const fgtDir=fgt>0.05?'UP':fgt<-0.05?'DOWN':null;
  const traj=analysis.trajectoryAdj||0;
  const trajDir=traj>0?'UP':traj<0?'DOWN':null;
  const regime=analysis.regime||'?';
  const winType=analysis.windowAmplitude?.label||null;
  const cp=analysis.candlePattern?.label||null;
  const cpScore=analysis.candlePattern?.score||0;
  const vf=analysis.volFlow?.label||null;
  const prediction=analysis.prediction||'';
  // V8.7: Honor the snapshot. taraCall.snapshot is the persistent committed call for the
  //   window. Once Tara locks, that lock is sticky regardless of engine ticks. Brain
  //   was reading `prediction` (engine state, tick-to-tick) which caused desync — Brain
  //   showed SIT_OUT/FORMING while the card showed LOCKED. Snapshot takes priority.
  // V8.7.1: Wrap snapshot read in try/catch — if shape is unexpected, fall back to engine.
  let _snap=null,_snapLocked=false,_snapDir=null;
  try{
    _snap=snapshot||taraCall?.snapshot||null;
    // V13.4.288: this was the presence-of-a-direction test in its purest form --
    //   exactly the shape that produced the v275 and v279 bugs. It also fabricates a
    //   lockInfo object downstream when analysis.lockInfo is null, so a lean could be
    //   narrated as a lock in the brain view.
    _snapLocked=readLockState(_snap).tradeable;
    _snapDir=_snapLocked?_snap.call:null;
  }catch(_){ /* snapshot unreadable — fall through */ }
  const isLocked=_snapLocked;
  const isRejected=!_snapLocked&&prediction.includes('REJECTED');
  const isSearching=!_snapLocked&&(prediction==='SEARCHING...'||prediction.includes('FORMING'));
  const _lockedConfidence=_snapLocked?readLockState(_snap).confidence:null;
  const lockInfo=_snapLocked?{
    dir:_snap?.call,
    lockPrice:Number(_snap?.priceAtLock)||0,
    lockedPosterior:Number(_snap?.posterior)||0,
    lockedAt:_snap?.lockedAt||0,
  }:analysis.lockInfo||null;

  // ── Synthesize current read in plain English ──
  const synthesize=()=>{
    if(isLocked){
      // V8.7: Use snapshot dir when present — that's what the user sees
      const _ld=_snapDir||lockInfo?.dir;
      const _lp=lockInfo?.lockPrice||Number(_snap?.priceAtLock)||0;
      return `I'm locked ${_ld}${_lp>0?` from $${_lp.toFixed(0)}`:''}. Confidence at lock was ${_lockedConfidence||'—'}%. Current signals below may have changed since that decision.`;
    }
    if(prediction==='MACRO BLACKOUT')return 'A scheduled macro event is imminent. Trading through it has been historically loss-making, so I\'m sitting out this window regardless of what the signals say.';
    if(isRejected){
      const why=prediction.split('—')[1]?.trim()||'mixed signals';
      return `I'm rejecting this setup. ${why.charAt(0).toUpperCase()+why.slice(1)}. Even though some signals are pulling ${dir}, the rejection gate fired because the underlying conditions don't support a high-conviction call here.`;
    }
    if(isSearching){
      const fgtPart=fgtDir?` FGT is leaning ${fgtDir} ${fgtAbs.toFixed(1)}/4`:' FGT is silent';
      const trajPart=trajDir?`, trajectory says ${trajDir} ${Math.abs(traj).toFixed(0)}`:'';
      const cpPart=cp&&cp!=='MIXED'&&cp!=='OPENING'&&cp!=='INSUFFICIENT'?`, candles showing ${cp.toLowerCase().replace('-',' ')}`:'';
      // V3.2.1: balanced phrasing when no real edge
      if(isBalanced){
        return `No clear edge in ${regime}.${fgtPart}${trajPart}${cpPart}. Posterior at ${post.toFixed(0)} — signals are balanced. Sitting out is the right move here.`;
      }
      return `Searching for a high-conviction read in ${regime}.${fgtPart}${trajPart}${cpPart}. Posterior at ${post.toFixed(0)} — ${conviction<10?'too neutral to commit':conviction<20?'mildly leaning '+dir+' but not enough':'leaning '+dir+' but waiting for confirmation samples'}.`;
    }
    // V3.2.1: balanced fallback
    if(isBalanced){
      return `Posterior ${post.toFixed(0)} in ${regime} — signals are balanced, no real edge either way. ${winType?'Window is '+winType.toLowerCase()+'. ':''}Quality ${qualityGate?.score||0}.`;
    }
    return `Posterior ${post.toFixed(0)} (${dir} lean) in ${regime}. ${winType?'Window is '+winType.toLowerCase()+'. ':''}Quality score ${qualityGate?.score||0}.`;
  };

  // ── For/Against ledger ──
  const sig=analysis.rawSignalScores||{};
  const ledger={UP:[],DOWN:[]};
  const addSig=(label,score,note)=>{
    if(score>1)ledger.UP.push({label,score,note});
    else if(score<-1)ledger.DOWN.push({label,Math:Math,score:Math.abs(score),note});
  };
  if(sig.gap)addSig('Gap',sig.gap,'price-vs-strike');
  if(sig.momentum)addSig('Momentum',sig.momentum,'recent drift');
  if(sig.flow)addSig('Flow',sig.flow,'tape buy/sell');
  if(sig.structure)addSig('Structure',sig.structure,'support/resistance');
  if(sig.technical)addSig('Technical',sig.technical,'RSI/BB/VWAP');
  if(sig.regime)addSig('Regime',sig.regime,'directional regime bias');
  if(sig.rangePosition)addSig('Range Pos',sig.rangePosition,'σ from window open');
  // FGT bonus is applied separately, not in rawSignalScores
  if(fgtAbs>=0.7){
    const fgtBonus=fgtAbs>=3.5?42:fgtAbs>=2.5?26:fgtAbs>=1.5?14:6;
    if(fgt>0)ledger.UP.push({label:'FGT',score:fgtBonus,note:`${fgtAbs.toFixed(1)}/4 timeframe alignment`});
    else ledger.DOWN.push({label:'FGT',score:fgtBonus,note:`${fgtAbs.toFixed(1)}/4 timeframe alignment`});
  }
  // Trajectory contribution
  if(Math.abs(traj)>=3){
    if(traj>0)ledger.UP.push({label:'Trajectory',score:traj,note:'forward forecast'});
    else ledger.DOWN.push({label:'Trajectory',score:Math.abs(traj),note:'forward forecast'});
  }
  // Candle pattern (informational, but show)
  if(cp&&cp!=='MIXED'&&cp!=='OPENING'&&cp!=='INSUFFICIENT'){
    if(cpScore>10)ledger.UP.push({label:'Candle 1m',score:cpScore,note:cp.toLowerCase().replace('-',' ')+' (informational)'});
    else if(cpScore<-10)ledger.DOWN.push({label:'Candle 1m',score:Math.abs(cpScore),note:cp.toLowerCase().replace('-',' ')+' (informational)'});
  }
  ledger.UP.sort((a,b)=>b.score-a.score);
  ledger.DOWN.sort((a,b)=>b.score-a.score);

  // ── What she's watching for ──
  const watching=[];
  if(isSearching){
    if(post<60&&post>50)watching.push(`Posterior climbing past ${analysis.upThreshold||65} → triggers UP form/lock`);
    if(post>40&&post<50)watching.push(`Posterior dropping past ${analysis.downThreshold||35} → triggers DOWN form/lock`);
    if(fgtAbs<2)watching.push(`FGT alignment increasing past 2/4 — currently ${fgtAbs.toFixed(1)}`);
    const samplesNeeded=qualityGate?.consecutiveNeeded||2;
    const samplesHave=Math.max(analysis.bullCount||0,analysis.bearCount||0);
    if(samplesNeeded>samplesHave)watching.push(`${samplesNeeded-samplesHave} more confirming samples (currently ${samplesHave}/${samplesNeeded})`);
  }
  if(isLocked){
    const wType=analysis.windowAmplitude?.label||'NORMAL';
    const releaseThr=wType==='WHIPSAW'?50:wType==='TRENDING'?35:wType==='SPIKE-FADE'?25:wType==='RANGE'?20:wType==='DEAD'?15:30;
    watching.push(`Adverse gap exceeding ${releaseThr}bps → release lock`);
    watching.push(`FGT flipping against ${lockInfo?.dir} → release lock`);
    watching.push(`Posterior collapsing ≥25 points from lock → severe-collapse release`);
  }
  if(watching.length===0)watching.push('Window state stable — no specific inflection point being watched');

  // ── Gates / what's blocking ──
  const gates=[];
  if(qualityGate&&qualityGate.score<45)gates.push({label:'LOW QUALITY',detail:`Quality score ${qualityGate.score} — below threshold of 45`});
  if(prediction.includes('BLACKOUT'))gates.push({label:'MACRO BLACKOUT',detail:'Scheduled news event imminent'});
  if(prediction.includes('BREAKING NEWS'))gates.push({label:'BREAKING NEWS',detail:'Active news event being monitored'});
  if(prediction.includes('REJECTED')){
    const why=prediction.split('—')[1]?.trim()||'rejected';
    gates.push({label:'REJECTED',detail:why});
  }

  // Recent record
  const sc=scorecards?.['15m']||{wins:0,losses:0};
  const totalGames=sc.wins+sc.losses;
  const wr=totalGames>0?((sc.wins/totalGames)*100).toFixed(1):'—';

  return(
    <div role={embedded?'region':'dialog'} aria-modal={embedded?undefined:'true'} aria-label="Tara Brain" className={embedded?'tara-brain-page tara-inline-page':'tara-brain-page fixed inset-0 z-[60] bg-black/85 flex items-center justify-center p-3 sm:p-6'} onClick={embedded?undefined:onClose}>
      <div className={'bg-[#050508] border border-[#2A2A34] rounded-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto'} onClick={e=>e.stopPropagation()}>
        {!embedded&&<div className="sticky top-0 bg-[#050508] backdrop-blur border-b border-[#24242E] px-5 py-4 flex items-center justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] font-bold" style={{color:T2_GOLD}}>Tara · Brain</div>
            <h2 className="font-serif text-2xl text-white tracking-tight">What she's thinking</h2>
          </div>
          {!embedded&&<button onClick={onClose} className="text-[#EDEDED]/60 hover:text-white text-xl px-3 py-1">×</button>}
        </div>}

        {embedded&&<TaraBrainSummary snapshot={_snap} candidate={taraCall} analysis={analysis} lock={readLockState(_snap)}/>}
        {embedded&&<LearningStatus learning={learning}/>}
        <details className="p-5 space-y-6 tara-v145-deep-brain" open={!embedded}>
          <summary>Full signals, for / against ledger &amp; model diagnostics</summary>
          {/* V4.2: TARA'S CALL — leading panel. Her actual decision, framed first. */}
          {taraCall&&(()=>{
            const tc=taraCall;
            // V9.1.6: Honor the snapshot when locked. Was reading tc.call (live engine)
            //   which kept showing SIT_OUT/SCANNING after Tara had committed because the
            //   IIFE didn't use _snapLocked from the outer BrainView scope. Now: if a
            //   directional snapshot exists, that's the call to display. Falls back to
            //   live tc.call only when no snapshot.
            const _committedSitOut=readLockState(_snap).satOut;
            const _effectiveCall=_snapLocked?_snapDir:_committedSitOut?'SIT_OUT':tc.call;
            const sc=taraScorecards?.[windowType]||{wins:0,losses:0,sitouts:0};
            const total=(sc.wins||0)+(sc.losses||0);
            const wr=total>0?Math.round((sc.wins/total)*100):null;
            const isCall=_effectiveCall==='UP'||_effectiveCall==='DOWN';
            const callColor=_effectiveCall==='UP'?'text-emerald-300':_effectiveCall==='DOWN'?'text-rose-300':'text-amber-300/85';
            const borderClr=_effectiveCall==='UP'?'rgba(52,211,153,0.4)':_effectiveCall==='DOWN'?'rgba(232,69,94,0.4)':T2_GOLD_BORDER;
            const bgClr=_effectiveCall==='UP'?'rgba(52,211,153,0.06)':_effectiveCall==='DOWN'?'rgba(232,69,94,0.06)':'rgba(35,185,129,0.04)';
            const callLabel=_effectiveCall==='SIT_OUT'?(_committedSitOut?'SITTING OUT':'SCANNING'):_effectiveCall;
            const arrow=_effectiveCall==='UP'?'▲':_effectiveCall==='DOWN'?'▼':'—';
            // V9.1.6: Confidence display — use snapshot's posterior when locked,
            //   live confidence otherwise.
            const _confidence=_snapLocked?_lockedConfidence:tc.confidence;
            return(
              <section className="px-4 py-3 rounded-lg" style={{background:bgClr,border:'1px solid '+borderClr}}>
                <div className="flex items-baseline justify-between mb-2">
                  <div className="text-[9px] uppercase tracking-[0.2em] font-bold" style={{color:T2_GOLD}}>{_snapLocked?"Tara's locked Call · record-bearing":_committedSitOut?'Tara sat out · record-bearing':'Live lean · not locked'}</div>
                  {wr!==null?<span className="text-[10px] tabular-nums text-[#EDEDED]/50">{sc.wins}W · {sc.losses}L · {sc.sitouts||0} skip · {wr}%</span>:<span className="text-[10px] tabular-nums text-[#EDEDED]/40">{sc.sitouts||0} skip · no calls yet</span>}
                </div>
                <div className={`flex items-baseline gap-2 mb-2 ${callColor}`}>
                  <span className="text-2xl">{arrow}</span>
                  <span className="text-3xl font-serif font-bold tracking-tight">{callLabel}</span>
                  {isCall&&<span className="text-base tabular-nums opacity-70">{Math.round(_confidence)}%</span>}
                </div>
            <p className="text-sm text-[#EDEDED]/80 leading-relaxed">{String(_snapLocked||_committedSitOut?(describeCommittedCallReason(_snap)||'Committed snapshot preserved for settlement. Live engine evidence below may have changed since the lock.'):(tc.reason||'Awaiting signal data...')).replace(/^\[V\d+(?:\.\d+)*\]\s*/i,'')}</p>
              </section>
            );
          })()}

          {/* CURRENT READ — supporting engine context */}
          <section>
            <div className="text-[9px] uppercase tracking-[0.18em] font-bold text-[#EDEDED]/50 mb-2">{_snapLocked?'Current engine read · may differ from the lock':'Engine Read · How she got there'}</div>
            <p className="text-base text-[#EDEDED]/90 leading-relaxed font-light">
              {synthesize()}
            </p>
            <div className="mt-3 flex flex-wrap gap-3 text-[11px] text-[#EDEDED]/45">
              <span><strong className="text-[#EDEDED]/70 tabular-nums">{post.toFixed(0)}%</strong> posterior</span>
              <span>·</span>
              <span><strong className="text-[#EDEDED]/70">{regime}</strong></span>
              <span>·</span>
              {winType&&<><span><strong className="text-[#EDEDED]/70">{winType}</strong> window</span><span>·</span></>}
              <span><strong className="text-[#EDEDED]/70 tabular-nums">{qualityGate?.score||0}/100</strong> quality</span>
              <span>·</span>
              <span>FGT <strong className="text-[#EDEDED]/70 tabular-nums">{fgtAbs.toFixed(1).replace(/\.0$/,'')}/4</strong>{fgtDir?' '+fgtDir:''}</span>
            </div>
          </section>

          {/* SIGNALS LEDGER */}
          <section>
            <div className="text-[9px] uppercase tracking-[0.18em] font-bold text-[#EDEDED]/50 mb-2">Signals Pulling Each Direction</div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="bg-emerald-500/5 border border-emerald-500/15 rounded-lg p-3">
                <div className="text-[10px] uppercase tracking-wider text-emerald-400 font-bold mb-2">Pulling UP</div>
                {ledger.UP.length===0?(
                  <div className="text-xs text-[#EDEDED]/35 italic">Nothing meaningful pulling UP right now</div>
                ):(
                  <div className="space-y-1.5">
                    {ledger.UP.map((s,i)=>(
                      <div key={i} className="flex items-center justify-between text-xs">
                        <div>
                          <span className="text-emerald-300 font-bold">{s.label}</span>
                          <span className="text-[#EDEDED]/35 ml-1">· {s.note}</span>
                        </div>
                        <span className="text-emerald-300 tabular-nums font-bold">+{(s.score||0).toFixed(0)}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <div className="bg-rose-500/5 border border-rose-500/15 rounded-lg p-3">
                <div className="text-[10px] uppercase tracking-wider text-rose-400 font-bold mb-2">Pulling DOWN</div>
                {ledger.DOWN.length===0?(
                  <div className="text-xs text-[#EDEDED]/35 italic">Nothing meaningful pulling DOWN right now</div>
                ):(
                  <div className="space-y-1.5">
                    {ledger.DOWN.map((s,i)=>(
                      <div key={i} className="flex items-center justify-between text-xs">
                        <div>
                          <span className="text-rose-300 font-bold">{s.label}</span>
                          <span className="text-[#EDEDED]/35 ml-1">· {s.note}</span>
                        </div>
                        <span className="text-rose-300 tabular-nums font-bold">−{(s.score||0).toFixed(0)}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </section>

          {/* V9.15: HISTORICAL ANALOGS — K-nearest-neighbor lookup over resolved trades */}
          {analysis.historicalAnalogs&&analysis.historicalAnalogs.valid&&(()=>{
            const _h=analysis.historicalAnalogs;
            const _wr=_h.analogWR;
            // Color: green if analogs strongly winning, amber if mixed, rose if losing
            const _wrColor=_wr>=70?'#23B981':_wr>=55?'#23B981':_wr>=40?'#23B981':'#E8455E';
            const _wrBg=_wr>=70?'rgba(35,185,129,0.06)':_wr>=55?'rgba(35,185,129,0.06)':'rgba(232,69,94,0.06)';
            const _confLabel=_h.analogConfidence==='high'?'HIGH match':_h.analogConfidence==='medium'?'MEDIUM match':'LOW match';
            const _confColor=_h.analogConfidence==='high'?'#23B981':_h.analogConfidence==='medium'?'#23B981':'rgba(237,237,237,0.55)';
            // Compare analog WR vs current posterior — flag disagreement
            const _post=analysis.rawProbAbove||50;
            const _postConfidence=analysis.lockInfo?.dir==='DOWN'?(100-_post):_post;
            const _disagreement=Math.abs(_wr-_postConfidence);
            return (
              <section>
                <div className="text-[9px] uppercase tracking-[0.18em] font-bold text-[#EDEDED]/50 mb-2 flex items-baseline gap-2">
                  <span>Historical Analogs</span>
                  <span className="font-normal tracking-normal text-[10px]" style={{color:_confColor}}>· {_confLabel} (avg sim {_h.avgSimilarity.toFixed(2)})</span>
                </div>
                <div className="rounded-lg p-3" style={{background:_wrBg,border:'1px solid '+_wrColor.replace('0.95','0.30').replace('0.85','0.30')}}>
                  <div className="flex items-baseline gap-3 mb-2">
                    <div>
                      <div className="text-[10px] uppercase tracking-wide text-[#EDEDED]/55">Top {_h.analogN} similar setups</div>
                      <div className="text-2xl font-bold tabular-nums leading-tight" style={{color:_wrColor}}>{_wr!=null?_wr.toFixed(0)+'%':'—'}</div>
                      <div className="text-[10px] text-[#EDEDED]/55">historical WR</div>
                    </div>
                    {_disagreement>=15&&(
                      <div className="ml-auto text-[10px] text-right" style={{color:'#23B981'}}>
                        <div className="uppercase tracking-wide font-bold">⚠ disagreement</div>
                        <div className="text-[#EDEDED]/65">posterior {_postConfidence.toFixed(0)}% · analogs {_wr.toFixed(0)}%</div>
                      </div>
                    )}
                  </div>
                  {/* Top-K table preview */}
                  <div className="mt-2 space-y-1">
                    {_h.topK.slice(0,5).map((t,i)=>(
                      <div key={i} className="grid grid-cols-12 gap-2 text-[10px] tabular-nums items-baseline">
                        <span className="col-span-2 text-[#EDEDED]/45">sim {t.similarity.toFixed(2)}</span>
                        <span className="col-span-1 font-bold" style={{color:t.result==='WIN'?'#23B981':'#E8455E'}}>
                          {t.result==='WIN'?'W':'L'}
                        </span>
                        <span className="col-span-2 text-[#EDEDED]/65">{t.dir||'—'}</span>
                        <span className="col-span-3 text-[#EDEDED]/55 truncate">{t.regime||'—'}</span>
                        <span className="col-span-2 text-[#EDEDED]/55">{t.session||'—'}</span>
                        <span className="col-span-2 text-[#EDEDED]/55 text-right">
                          {t.closingGapBps!=null?(t.closingGapBps>0?'+':'')+Math.round(t.closingGapBps)+'bps':''}
                        </span>
                      </div>
                    ))}
                  </div>
                  <div className="mt-2 text-[10px] text-[#EDEDED]/45 italic">{_h.reason}</div>
                </div>
              </section>
            );
          })()}

          {/* WATCHING FOR */}
          <section>
            <div className="text-[9px] uppercase tracking-[0.18em] font-bold text-[#EDEDED]/50 mb-2">What I'm Watching For</div>
            <ul className="space-y-1.5">
              {watching.map((w,i)=>(
                <li key={i} className="text-sm text-[#EDEDED]/75 leading-relaxed flex gap-2">
                  <span className="shrink-0" style={{color:T2_GOLD}}>·</span>
                  <span>{w}</span>
                </li>
              ))}
            </ul>
          </section>

          {/* GATES */}
          {gates.length>0&&(
            <section>
              <div className="text-[9px] uppercase tracking-[0.18em] font-bold text-[#EDEDED]/50 mb-2">Active Gates</div>
              <div className="space-y-2">
                {gates.map((g,i)=>(
                  <div key={i} className="bg-amber-500/5 border border-amber-500/20 rounded-lg p-3 text-sm">
                    <span className="text-amber-300 font-bold uppercase tracking-wide text-xs">{g.label}</span>
                    <div className="text-[#EDEDED]/65 mt-1">{g.detail}</div>
                  </div>
                ))}
              </div>
            </section>
          )}

          {/* CONTEXT — running record + Kalshi state */}
          <section>
            <div className="text-[9px] uppercase tracking-[0.18em] font-bold text-[#EDEDED]/50 mb-2">Context</div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs">
              <div>
                <div className="text-[#EDEDED]/35 mb-0.5">Lifetime</div>
                <div className="text-[#EDEDED]/85 tabular-nums">{sc.wins}-{sc.losses} <span className="text-[#EDEDED]/35">({wr}%)</span></div>
              </div>
              <div>
                <div className="text-[#EDEDED]/35 mb-0.5">Strike source</div>
                <div className={strikeSource==='kalshi'?'text-emerald-400':strikeMode==='auto'?'text-emerald-400/70':'text-amber-400/80'}>{strikeSource==='kalshi'?'Kalshi':strikeMode==='auto'?'Live spot':'Manual'}</div>
              </div>
              <div>
                <div className="text-[#EDEDED]/35 mb-0.5">Window</div>
                <div className="text-[#EDEDED]/85">{winType||'opening'}</div>
              </div>
              <div>
                <div className="text-[#EDEDED]/35 mb-0.5">Candle pattern</div>
                <div className="text-[#EDEDED]/85">{cp||'mixed'}</div>
              </div>
            </div>
            {kalshiDebug&&kalshiDebug.ok===false&&(
              <div className="mt-3 text-[11px] text-rose-400/80 bg-rose-500/5 border border-rose-500/15 rounded-lg p-2 font-mono">
                Kalshi extraction failing: {kalshiDebug.reason}
              </div>
            )}
            {kalshiDebug&&kalshiDebug.ok===true&&!kalshiDebug.bestStrike&&(
              <div className="mt-3 text-[11px] text-amber-400/80 bg-amber-500/5 border border-amber-500/15 rounded-lg p-2 font-mono">
                Kalshi returned {kalshiDebug.totalMarkets} markets, {kalshiDebug.matchingClose} matching this window — but no strike could be extracted. Fields: {(kalshiDebug.sampleFields||[]).slice(0,6).join(', ')}
              </div>
            )}
          </section>

        </details>
      </div>
    </div>
  );
}

// ── V2.7: StatsView — analytics dashboard ──
//   Three drill levels:
//     1. Top stats grid + cohort cards (regime, session, FGT, quality buckets)
//     2. Hourly heatmap with Today / 7-day / All-time toggle
//     3. Click an hour → drill to individual trades for that hour
//   Plus an insights surface that pulls 3-5 actionable findings from the data.
//
//   Filters out PENDING-VERIFY trades (they aren't truly resolved yet).
// V13.4.188: WINDOW CLOCK -- "which hours are actually tradeable, and which feel rigged".
//   Answers a direct user question: show the windows where movement is unnatural --
//   dead and slow, or flip-flopping right at the strike. Every number here was ALREADY
//   in the call log and had simply never been surfaced:
//     closingGapBps -> how far from the strike the window actually finished.
//     atrpAtLock    -> how much the tape was moving at all. Near zero = dead window.
//   Rated over ALL windows, not just traded ones: a window Tara sat out is still
//   evidence about that hour.
//
//   CALIBRATION NOTE, because the obvious version of this metric is useless.
//   Measured over the 601 logged windows that carry a closing gap: median gap to the
//   strike is 5.6bps and 72% finish inside 10bps. Finishing near the line is the
//   NORMAL state of a 15-minute BTC window, not an anomaly -- so an absolute "within
//   10bps = pinned" test tags almost every hour and tells you nothing. (Checked that
//   this is real and not a default-zero artifact: exactly zero entries have gap==0.)
//   So the flag here is RELATIVE -- an hour is only called out when it sticks to the
//   line materially more than the all-hours baseline. EV leads the verdict, because
//   that is the part that is actually actionable; pinning is the explanation.
//
//   Wording is deliberately plain -- no posterior, no FGT, no qScore.
// V13.4.210: EXECUTION panel. The v178-209 gate work moved EV by fractions of
//   a cent; measured on the 107 resolved trades that carry a quote, crossing
//   the spread costs 2.31c/contract -- more than the entire measured edge. That
//   is invisible today because the cost never appears anywhere: you see the
//   entry price you paid, never the price you could have rested at.
//   Everything here comes from fields already logged at lock time
//   (kalshiSpreadAtLock + kalshiAtLock); nothing new is collected.
//   Honest framing in the copy: resting is NOT free -- an unfilled rest means
//   no trade, and a window Tara liked goes untraded. The panel states that.
function ExecutionPanel({taraCallLog}){
  const d=React.useMemo(()=>{
    let n=0,spread=0,fee=0,ev=0,cost=0,noSpread=0;
    (taraCallLog||[]).forEach(e=>{
      if(!e||(e.result!=='WIN'&&e.result!=='LOSS'))return;
      if(e.dir!=='UP'&&e.dir!=='DOWN')return;
      if(e.wasOverriddenNoTrade===true)return;
      const k=Number(e.kalshiAtLock);
      if(!Number.isFinite(k)||k<=0||k>=100)return;
      const sp=Number(e.kalshiSpreadAtLock);
      if(!Number.isFinite(sp)){noSpread++;return;}
      const c=e.dir==='UP'?k:(100-k);
      const f=0.07*(c/100)*(1-c/100)*100;
      n++;spread+=sp;fee+=f;cost+=c;
      ev+=(e.result==='WIN')?(100-c):-c;
    });
    if(n===0)return null;
    const saving=(spread+fee)/n;
    return{n,noSpread,
      avgSpread:spread/n, avgFee:fee/n, avgCost:cost/n,
      evNow:ev/n, evRested:ev/n+saving, saving,
      totalLeft:spread+fee};
  },[taraCallLog]);

  if(!d)return null;
  const money=(c)=>(c>=0?'+':'')+c.toFixed(2)+'c';
  const Row=({label,value,tone,note})=>(
    <div className="flex items-baseline justify-between py-[5px]">
      <span className="text-[11px]" style={{color:'rgba(255,255,255,0.62)'}}>{label}</span>
      <span className="flex items-baseline gap-2">
        {note?<span className="text-[10px]" style={{color:'rgba(255,255,255,0.34)'}}>{note}</span>:null}
        <span className="text-[12px] tabular-nums font-bold" style={{color:tone||'rgba(255,255,255,0.92)'}}>{value}</span>
      </span>
    </div>
  );
  const mult=d.evNow>0?(d.evRested/d.evNow):null;

  return (
    <div className="bg-[#0A0A0E] border border-[#1B1B22] rounded-[10px] p-4 sm:p-5 mb-5">
      <div className="text-xs uppercase tracking-[0.22em] font-bold mb-1" style={{color:'rgba(255,255,255,0.85)'}}>
        Execution <span className="text-[10px] tracking-wider ml-1 font-normal normal-case" style={{color:'rgba(255,255,255,0.34)'}}>what crossing the spread costs</span>
      </div>
      <div className="text-[11px] mb-3 leading-relaxed" style={{color:'rgba(255,255,255,0.5)'}}>
        Measured on the {d.n} resolved trades that recorded a live quote. Hitting the ask pays the spread <em>and</em> Kalshi&rsquo;s taker fee; resting at the bid pays neither, because the maker fee is zero.
      </div>

      <div style={{borderTop:'1px solid rgba(255,255,255,0.08)'}}>
        <Row label="average entry" value={d.avgCost.toFixed(1)+'c'}/>
        <Row label="spread paid" value={d.avgSpread.toFixed(2)+'c'} note="per contract"/>
        <Row label="taker fee paid" value={d.avgFee.toFixed(2)+'c'} note="maker would be 0"/>
      </div>

      <div className="mt-3 pt-3" style={{borderTop:'1px solid rgba(255,255,255,0.08)'}}>
        <Row label="edge as traded" value={money(d.evNow)} tone={d.evNow>=0?'#23B981':'#E8455E'} note="per contract"/>
        <Row label="edge if rested" value={money(d.evRested)} tone={d.evRested>=0?'#23B981':'#E8455E'} note="per contract"/>
        <Row label="difference" value={money(d.saving)} tone="#D4A03A"
             note={mult&&mult>1?(mult.toFixed(1)+'x the edge'):null}/>
      </div>

      <div className="mt-3 text-[10px] leading-relaxed" style={{color:'rgba(255,255,255,0.38)'}}>
        Across those {d.n} trades that is <span style={{color:'#D4A03A'}}>{d.totalLeft.toFixed(0)}c per contract traded</span> given up to cross the spread.
        {d.noSpread>0?<span> {d.noSpread} other resolved trades had no quote recorded and are excluded.</span>:null}
        <div className="mt-1">Resting is not free: an order that never fills means the window goes untraded, so this is the ceiling, not a promise. It is still the largest single lever measured — bigger than the entire entry-band edge.</div>
      </div>
    </div>
  );
}

function WindowClockPanel({taraCallLog}){
  const data=React.useMemo(()=>{
    const B=Array.from({length:24},(_,i)=>({h:i,n:0,traded:0,wins:0,ev:0,pin:0,pinN:0,quiet:0,quietN:0,sit:0}));
    let gPin=0,gPinN=0;
    (taraCallLog||[]).forEach(e=>{
      if(!e)return;
      const ts=Number(e.id)||0; if(!ts)return;
      const h=new Date(ts).getHours(); if(!(h>=0&&h<=23))return;
      const b=B[h]; b.n++;
      if(e.result==='SITOUT')b.sit++;
      const gap=Math.abs(Number(e.closingGapBps));
      if(Number.isFinite(gap)){b.pinN++;gPinN++;if(gap<10){b.pin++;gPin++;}}
      const atrp=Number(e.atrpAtLock);
      if(Number.isFinite(atrp)){b.quietN++;if(Math.abs(atrp)<0.02)b.quiet++;}
      const k=Number(e.kalshiAtLock);
      if((e.dir==='UP'||e.dir==='DOWN')&&(e.result==='WIN'||e.result==='LOSS')
        &&Number.isFinite(k)&&k>0&&k<100&&e.wasOverriddenNoTrade!==true){
        const cost=e.dir==='UP'?k:(100-k);
        b.traded++;
        if(e.result==='WIN'){b.wins++;b.ev+=(100-cost);}else{b.ev-=cost;}
      }
    });
    const rows=B.map(b=>({...b,
      wr:b.traded>0?100*b.wins/b.traded:null,
      evc:b.traded>0?b.ev/b.traded:null,
      pinPct:b.pinN>=5?100*b.pin/b.pinN:null,
      quietPct:b.quietN>=5?100*b.quiet/b.quietN:null}));
    return{rows,basePin:gPinN>=30?100*gPin/gPinN:null};
  },[taraCallLog]);
  const rows=data.rows, basePin=data.basePin;

  const label=(h)=>((h%12)||12)+(h<12?'am':'pm');
  const verdict=(r)=>{
    if(r.traded<4)return{tag:'not rated',tone:'idle',why:'fewer than 4 finished trades this hour -- not enough to judge'};
    const stickier=(r.pinPct!=null&&basePin!=null&&r.pinPct>=basePin+12);
    if(r.evc!=null&&r.evc<=-5)return{tag:'loses money',tone:'bad',
      why:'about '+Math.abs(r.evc).toFixed(0)+'c lost per contract'+(stickier?', and it sticks to the strike more than most hours':'')};
    if(stickier)return{tag:'sticks to the line',tone:'warn',
      why:'ends on top of the strike '+Math.round(r.pinPct)+'% of the time vs '+Math.round(basePin)+'% normally -- more coin-flip than most hours'};
    if(r.quietPct!=null&&r.quietPct>=60)return{tag:'barely moves',tone:'warn',
      why:'tape is nearly flat, so price often never travels far enough to clear the strike'};
    if(r.evc!=null&&r.evc>=5)return{tag:'strong',tone:'good',
      why:'about '+r.evc.toFixed(0)+'c made per contract'};
    return{tag:'ordinary',tone:'mid',why:'nothing unusual -- roughly breaks even'};
  };
  const tone=(t)=>t==='good'?{c:'#23B981',b:'rgba(35,185,129,0.30)',g:'rgba(35,185,129,0.10)'}
    :t==='bad'?{c:'#E8455E',b:'rgba(232,69,94,0.30)',g:'rgba(232,69,94,0.10)'}
    :t==='warn'?{c:'#23B981',b:'rgba(35,185,129,0.30)',g:'rgba(35,185,129,0.10)'}
    :t==='mid'?{c:'rgba(237,237,237,0.65)',b:'rgba(237,237,237,0.12)',g:'rgba(237,237,237,0.04)'}
    :{c:'rgba(237,237,237,0.28)',b:'rgba(237,237,237,0.08)',g:'transparent'};

  const judged=rows.filter(r=>r.traded>=4);
  const worst=[...judged].sort((a,b)=>(a.evc==null?0:a.evc)-(b.evc==null?0:b.evc)).slice(0,3);
  const best=[...judged].sort((a,b)=>(b.evc==null?0:b.evc)-(a.evc==null?0:a.evc)).slice(0,3);
  const maxAbs=Math.max(10,...judged.map(r=>Math.abs(r.evc||0)));

  return (
    <div className="bg-[#0A0A0E] border border-[#1B1B22] rounded-[10px] p-4 sm:p-5 mb-5">
      <div className="text-xs uppercase tracking-[0.22em] font-bold mb-1" style={{color:T2_GOLD}}>
        Window Clock <span className="text-[10px] tracking-wider text-[#EDEDED]/30 ml-1 font-normal normal-case">your local time · every window, not just traded ones</span>
      </div>
      <div className="text-[11px] text-[#EDEDED]/45 mb-3 leading-relaxed">
        Which hours behave normally and which do not. Most 15-minute windows finish close to the strike anyway{basePin!=null?' (about '+Math.round(basePin)+'% of them)':''} — that is just how this market is, so an hour is only flagged <span style={{color:'#23B981'}}>sticks to the line</span> when it does it markedly more than that, which makes those windows closer to a coin flip than the signal suggests.
      </div>
      {judged.length===0?(
        <div className="text-xs text-[#EDEDED]/40 italic py-4 text-center">Not enough finished trades yet to rate any hour.</div>
      ):(
        <div>
          <div className="flex flex-wrap gap-2 mb-4 text-[11px]">
            <span className="px-2 py-1 rounded-lg" style={{background:'rgba(232,69,94,0.10)',border:'0.5px solid rgba(232,69,94,0.30)',color:'#E8455E'}}>
              worst hours: {worst.map(r=>label(r.h)).join(' · ')}
            </span>
            <span className="px-2 py-1 rounded-lg" style={{background:'rgba(35,185,129,0.10)',border:'0.5px solid rgba(35,185,129,0.30)',color:'#23B981'}}>
              best hours: {best.map(r=>label(r.h)).join(' · ')}
            </span>
          </div>
          <div>
            {rows.map(r=>{
              const v=verdict(r);const t=tone(v.tone);
              const pct=r.evc!=null?Math.min(100,Math.abs(r.evc)/maxAbs*100):0;
              return (
                <div key={r.h} className="flex items-center gap-2 sm:gap-3 py-[3px]">
                  <span className="w-[38px] shrink-0 text-[11px] tabular-nums text-right" style={{color:t.c}}>{label(r.h)}</span>
                  <div className="w-[60px] sm:w-[100px] shrink-0 h-[6px] rounded-full overflow-hidden flex" style={{background:'rgba(237,237,237,0.05)'}}>
                    <div className="h-full w-1/2 flex justify-end">{r.evc!=null&&r.evc<0?<div style={{width:pct+'%',background:t.c,opacity:0.8}}/>:null}</div>
                    <div className="h-full w-1/2">{r.evc!=null&&r.evc>=0?<div style={{width:pct+'%',background:t.c,opacity:0.8}}/>:null}</div>
                  </div>
                  <span className="w-[44px] shrink-0 text-[11px] tabular-nums text-right" style={{color:t.c}}>{r.evc!=null?(r.evc>=0?'+':'')+r.evc.toFixed(0)+'c':'--'}</span>
                  <span className="w-[46px] shrink-0 text-[10px] tabular-nums text-right text-[#EDEDED]/35 hidden sm:block">{r.pinPct!=null?Math.round(r.pinPct)+'% line':''}</span>
                  <span className="px-1.5 py-0.5 rounded text-[10px] shrink-0 whitespace-nowrap" style={{background:t.g,border:'0.5px solid '+t.b,color:t.c}}>{v.tag}</span>
                  <span className="text-[10px] text-[#EDEDED]/40 truncate hidden md:block">{v.why}</span>
                </div>
              );
            })}
          </div>
          <div className="mt-3 text-[10px] text-[#EDEDED]/30 leading-relaxed">
            Bar is average cents won or lost per contract that hour — left of centre is a loss, right is a profit. &ldquo;% line&rdquo; is how often that hour finishes within a whisker of the strike. Hours with fewer than 4 finished trades are left unrated rather than guessed at.
          </div>
        </div>
      )}
    </div>
  );
}

function StatsView({tradeLog,scorecards,taraCallLog,onClose,timeFormat}){
  const[tab,setTab]=React.useState('today'); // 'today' | 'week' | 'all'
  const[selectedHour,setSelectedHour]=React.useState(null);

  // V5.6.9: Merge manual tradeLog with Tara's resolved calls. Each resolved Tara call
  //   counts as a trade for analytics purposes. SITOUT entries are excluded (they
  //   don't contribute to win rate). Tagged with `source` so we can show the split.
  const allResolved=React.useMemo(()=>{
    const manual=(tradeLog||[]).filter(t=>t.result==='WIN'||t.result==='LOSS').map(t=>({...t,source:'manual'}));
    // V10.7.82b: Use canonical filter (excludes no-go-data, wasOverriddenNoTrade, deduplicates by windowId)
    const taraCalls=_canonicalTrades((taraCallLog||[]).filter(e=>e&&(e.dir==='UP'||e.dir==='DOWN'))).map(e=>({
      id:e.id,time:e.time,result:e.result,
      regime:e.regime,
      direction:e.dir,
      dir:e.dir, // alias for drill row template
      session:e.session,
      hour:new Date(e.id||e.time||0).getHours(),
      windowType:e.windowType,
      windowId:e.windowId, // V5.7.5: for period derivation
      strike:e.strike,
      closingPrice:e.closingPrice,
      closeSource:e.closeSource,
      posterior:e.posterior, // V5.7.5: drill row reads this
      qScore:e.qScore,
      qualityScore:e.qScore, // alias for drill row template
      fgt:e.fgt,
      fgtAlignment:e.fgt, // alias for drill row template
      closingGapBps:e.gapBps, // alias for drill row template
      tier:e.tier,
      source:'tara',
    }));
    return [...manual,...taraCalls].sort((a,b)=>(a.id||0)-(b.id||0));
  },[tradeLog,taraCallLog]);

  // Time-filter
  const now=Date.now();
  const dayMs=24*60*60*1000;
  const trades=React.useMemo(()=>{
    if(tab==='today'){
      const todayStart=new Date(); todayStart.setHours(0,0,0,0);
      return allResolved.filter(t=>(t.id||0)>=todayStart.getTime());
    } else if(tab==='week'){
      return allResolved.filter(t=>(t.id||0)>=now-7*dayMs);
    }
    return allResolved;
  },[tab,allResolved]);

  const wins=trades.filter(t=>t.result==='WIN').length;
  const losses=trades.length-wins;
  const wr=trades.length>0?(wins/trades.length*100):0;

  // ── Hourly breakdown — V5.7: always local-bucketed ──
  const hourly=React.useMemo(()=>{
    const buckets=Array.from({length:24},(_,i)=>({hour:i,wins:0,losses:0,trades:[]}));
    for(const t of trades){
      // V5.7: always recompute from id — don't trust stored t.hour (might be UTC from old code)
      const h=new Date(t.id||0).getHours();
      if(h<0||h>23)continue;
      buckets[h].trades.push(t);
      if(t.result==='WIN')buckets[h].wins++;else buckets[h].losses++;
    }
    return buckets.map(b=>({...b,n:b.wins+b.losses,wr:b.wins+b.losses>0?(b.wins/(b.wins+b.losses))*100:null}));
  },[trades]);

  // ── Cohort breakdowns ──
  const cohort=(keyFn)=>{
    const buckets=new Map();
    for(const t of trades){
      const k=keyFn(t); if(k==null)continue;
      if(!buckets.has(k))buckets.set(k,{wins:0,losses:0});
      const b=buckets.get(k); if(t.result==='WIN')b.wins++;else b.losses++;
    }
    return [...buckets.entries()].map(([k,v])=>({key:k,wins:v.wins,losses:v.losses,n:v.wins+v.losses,wr:v.wins+v.losses>0?(v.wins/(v.wins+v.losses))*100:0})).sort((a,b)=>b.n-a.n);
  };

  const byDir=cohort(t=>t.dir);
  const byRegime=cohort(t=>t.regime);
  const bySession=cohort(t=>t.session);
  const byQuality=cohort(t=>{
    const q=t.qualityScore||0;
    if(q>=70)return 'High (70+)';
    if(q>=50)return 'Medium (50-69)';
    return 'Low (<50)';
  });
  const byFGT=cohort(t=>{
    const f=Math.abs(t.fgtAlignment||0);
    if(f>=4)return 'FGT 4/4';
    if(f>=3)return 'FGT 3/4';
    if(f>=2)return 'FGT 2/4';
    return 'FGT ≤1';
  });
  const byClockBucket=cohort(t=>{
    const c=t.clockAtLock||0; // for 15m windows: 900 = window start, 0 = window close in some implementations
    // clockAtLock is "seconds remaining" in this codebase, so high=early, low=late
    if(c>=720)return 'Early (first 3min)';
    if(c>=540)return 'Mid-early (3-6min)';
    if(c>=360)return 'Mid (6-9min)';
    if(c>=180)return 'Mid-late (9-12min)';
    return 'Late (12-15min)';
  });

  // ── Insights — auto-derive from cohorts ──
  const insights=React.useMemo(()=>{
    const out=[];
    if(trades.length<5)return [{kind:'note',text:`Only ${trades.length} resolved trades in this window — insights need ≥5 to be meaningful.`}];
    // Best regime
    const bestRegime=[...byRegime].filter(r=>r.n>=3).sort((a,b)=>b.wr-a.wr)[0];
    if(bestRegime&&bestRegime.wr>=60){
      out.push({kind:'positive',text:`Strongest regime: ${bestRegime.key} at ${bestRegime.wr.toFixed(0)}% (${bestRegime.wins}W-${bestRegime.losses}L).`});
    }
    // Worst regime
    const worstRegime=[...byRegime].filter(r=>r.n>=3).sort((a,b)=>a.wr-b.wr)[0];
    if(worstRegime&&worstRegime.wr<50&&bestRegime&&worstRegime.key!==bestRegime.key){
      out.push({kind:'negative',text:`Weakest regime: ${worstRegime.key} at ${worstRegime.wr.toFixed(0)}% (${worstRegime.wins}W-${worstRegime.losses}L) — consider sitting out.`});
    }
    // Best hour
    const eligibleHours=hourly.filter(h=>h.n>=3);
    if(eligibleHours.length>0){
      const bestHour=[...eligibleHours].sort((a,b)=>b.wr-a.wr)[0];
      if(bestHour.wr>=70){
        out.push({kind:'positive',text:`Sharpest hour: ${String(bestHour.hour).padStart(2,'0')}:00 UTC at ${bestHour.wr.toFixed(0)}% (${bestHour.n} trades).`});
      }
    }
    // Quality gate effectiveness
    const highQ=byQuality.find(q=>q.key==='High (70+)');
    const lowQ=byQuality.find(q=>q.key==='Low (<50)');
    if(highQ&&lowQ&&highQ.n>=3&&lowQ.n>=3){
      const diff=highQ.wr-lowQ.wr;
      if(diff>=15){
        out.push({kind:'positive',text:`Quality gate is working: high-quality trades (${highQ.wr.toFixed(0)}%) outperform low-quality (${lowQ.wr.toFixed(0)}%) by ${diff.toFixed(0)} points.`});
      } else if(diff<5){
        out.push({kind:'note',text:`Quality gate doesn't seem to discriminate: high (${highQ.wr.toFixed(0)}%) vs low (${lowQ.wr.toFixed(0)}%) only ${Math.abs(diff).toFixed(0)} points apart.`});
      }
    }
    // FGT alignment
    const fgt4=byFGT.find(f=>f.key==='FGT 4/4');
    if(fgt4&&fgt4.n>=3&&fgt4.wr>=70){
      out.push({kind:'positive',text:`FGT 4/4 alignment is highly reliable: ${fgt4.wr.toFixed(0)}% (${fgt4.n} trades).`});
    }
    // UP vs DOWN bias
    const upStat=byDir.find(d=>d.key==='UP');
    const dnStat=byDir.find(d=>d.key==='DOWN');
    if(upStat&&dnStat&&upStat.n>=5&&dnStat.n>=5){
      const upRatio=upStat.n/(upStat.n+dnStat.n);
      if(upRatio>=0.7){
        out.push({kind:'note',text:`Direction bias: ${(upRatio*100).toFixed(0)}% of locks were UP. May be a real bias in current regime, or training drift.`});
      } else if(upRatio<=0.3){
        out.push({kind:'note',text:`Direction bias: ${(100-upRatio*100).toFixed(0)}% of locks were DOWN. May be a real bias in current regime, or training drift.`});
      }
    }
    // Lock timing
    const earlyLocks=byClockBucket.find(c=>c.key==='Early (first 3min)');
    const lateLocks=byClockBucket.find(c=>c.key==='Late (12-15min)');
    if(earlyLocks&&lateLocks&&earlyLocks.n>=3&&lateLocks.n>=3){
      const diff=earlyLocks.wr-lateLocks.wr;
      if(Math.abs(diff)>=15){
        const better=diff>0?'early':'late';
        out.push({kind:'positive',text:`Lock timing matters: ${better} locks win ${Math.abs(diff).toFixed(0)} points more than ${better==='early'?'late':'early'} locks.`});
      }
    }
    if(out.length===0)out.push({kind:'note',text:`No strong signal in current data. ${trades.length} trades may not be enough — let it accumulate.`});
    return out;
  },[trades,byRegime,hourly,byQuality,byFGT,byDir,byClockBucket]);

  // ── Heatmap cell color helper ──
  const cellColor=(wr,n)=>{
    if(wr==null||n===0)return {bg:'rgba(237,237,237,0.04)',border:'rgba(237,237,237,0.08)',color:'rgba(237,237,237,0.3)'};
    if(wr>=70)return {bg:'rgba(35,185,129,0.10)',border:'rgba(35,185,129,0.30)',color:'#6FA98C'};
    if(wr>=55)return {bg:'rgba(35,185,129,0.06)',border:'rgba(35,185,129,0.18)',color:'#23B981'};
    if(wr>=45)return {bg:'rgba(237,237,237,0.04)',border:'rgba(237,237,237,0.10)',color:'rgba(237,237,237,0.6)'};
    if(wr>=30)return {bg:'rgba(201,125,74,0.10)',border:'rgba(201,125,74,0.30)',color:'#C97D4A'};
    return {bg:'rgba(232,69,94,0.10)',border:'rgba(232,69,94,0.30)',color:'rgba(232,69,94,0.72)'};
  };

  // ── Selected-hour drill view ──
  const drillTrades=selectedHour!=null?(hourly[selectedHour]?.trades||[]).slice().sort((a,b)=>b.id-a.id):null;

  return(
    <div className="fixed inset-0 z-50 bg-[#050508] backdrop-blur-md overflow-y-auto" onClick={(e)=>{if(e.target===e.currentTarget)onClose();}}>
      <div className="max-w-[1200px] mx-auto px-4 py-6 sm:py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <div className="flex items-baseline gap-2 mb-1">
              <span className="text-[10px] uppercase font-bold tracking-[0.18em]" style={{color:T2_GOLD}}>Performance</span>
              <span className="text-[9px] uppercase tracking-wider text-[#EDEDED]/30">Tara analytics</span>
            </div>
            <h2 className="font-serif text-3xl text-white tracking-tight">Stats <span style={{color:T2_GOLD}}>·</span> Insights</h2>
          </div>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-[#EDEDED]/5 text-[#EDEDED]/60 hover:text-white transition-colors text-xl">✕</button>
        </div>

        {/* Tab toggle */}
        <div className="flex gap-1 mb-5 p-1 rounded-lg bg-[#101014] w-fit border border-[#24242E]">
          {['today','week','all'].map(t=>(
            <button key={t} onClick={()=>{setTab(t);setSelectedHour(null);}} className={'px-4 py-1.5 text-xs uppercase font-bold tracking-wider rounded-lg transition-colors '+(tab===t?'':'text-[#EDEDED]/40 hover:text-[#EDEDED]/70')} style={tab===t?{background:T2_GOLD_GLOW,color:T2_GOLD,border:'0.5px solid '+T2_GOLD_BORDER}:{}}>
              {t==='today'?'Today':t==='week'?'7 Days':'All Time'}
            </button>
          ))}
        </div>

        {/* Top stats grid */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-3 mb-5">
          <div className="bg-[#0A0A0E] border border-[#1B1B22] rounded-[10px] p-3 sm:p-4 relative">
            <T2Stamp code="ALL · 001"/>
            <div className="text-[9px] uppercase tracking-[0.18em] text-[#EDEDED]/40 font-bold mb-1.5">Trades</div>
            <div className="text-2xl sm:text-3xl font-bold text-white" style={T2_MONO_STYLE}>{trades.length}</div>
            <div className="text-[10px] text-[#EDEDED]/35 mt-0.5">resolved</div>
          </div>
          <div className="bg-[#0A0A0E] border border-[#1B1B22] rounded-[10px] p-3 sm:p-4 relative">
            <T2Stamp code="ACC · 002"/>
            <div className="text-[9px] uppercase tracking-[0.18em] text-[#EDEDED]/40 font-bold mb-1.5">Accuracy</div>
            <div className={'text-2xl sm:text-3xl font-bold '+(wr>=60?'text-emerald-400':wr>=50?'text-white':'text-rose-400')} style={T2_MONO_STYLE}>{trades.length>0?wr.toFixed(0):'—'}%</div>
            <div className="text-[10px] text-[#EDEDED]/35 mt-0.5">{wins}W · {losses}L</div>
          </div>
          <div className="bg-[#0A0A0E] border border-[#1B1B22] rounded-[10px] p-3 sm:p-4 relative">
            <T2Stamp code="STR · 003"/>
            <div className="text-[9px] uppercase tracking-[0.18em] text-[#EDEDED]/40 font-bold mb-1.5">Streak</div>
            <div className="text-2xl sm:text-3xl font-bold text-white" style={T2_MONO_STYLE}>{(()=>{
              if(trades.length===0)return '—';
              const sorted=[...trades].sort((a,b)=>b.id-a.id);
              const lastResult=sorted[0]?.result;let streak=0;
              for(const t of sorted){if(t.result===lastResult)streak++;else break;}
              return streak;
            })()}</div>
            <div className="text-[10px] mt-0.5" style={{color:trades.length>0&&[...trades].sort((a,b)=>b.id-a.id)[0]?.result==='WIN'?'#6FA98C':'rgba(232,69,94,0.72)'}}>{trades.length>0?[...trades].sort((a,b)=>b.id-a.id)[0]?.result.toLowerCase():''}</div>
          </div>
          <div className="bg-[#0A0A0E] border border-[#1B1B22] rounded-[10px] p-3 sm:p-4 relative">
            <T2Stamp code="LIFE · 004"/>
            <div className="text-[9px] uppercase tracking-[0.18em] text-[#EDEDED]/40 font-bold mb-1.5">Lifetime</div>
            <div className="text-2xl sm:text-3xl font-bold text-white" style={T2_MONO_STYLE}>{((scorecards?.['15m']?.wins||0)/Math.max(1,(scorecards?.['15m']?.wins||0)+(scorecards?.['15m']?.losses||0))*100).toFixed(0)}%</div>
            <div className="text-[10px] text-[#EDEDED]/35 mt-0.5">{scorecards?.['15m']?.wins||0}W · {scorecards?.['15m']?.losses||0}L</div>
          </div>
        </div>

        {/* Insights surface */}
        <div className="bg-[#0A0A0E] border border-[#1B1B22] rounded-[10px] p-4 sm:p-5 mb-5 relative">
          <T2Stamp code="INS · 005"/>
          <div className="text-xs uppercase tracking-[0.22em] font-bold mb-3" style={{color:T2_GOLD}}>Insights</div>
          <div className="space-y-2">
            {insights.map((i,idx)=>{
              const color=i.kind==='positive'?'#6FA98C':i.kind==='negative'?T2_COPPER:'rgba(237,237,237,0.7)';
              const dot=i.kind==='positive'?'#6FA98C':i.kind==='negative'?T2_COPPER:T2_GOLD;
              return(
                <div key={idx} className="flex items-start gap-3 p-2 rounded-lg bg-[#0B0B0F] border border-[#24242E]">
                  <span className="inline-block w-1.5 h-1.5 rounded-full mt-1.5 shrink-0" style={{background:dot}}></span>
                  <span className="text-[12px] sm:text-[13px] leading-relaxed" style={{color}}>{i.text}</span>
                </div>
              );
            })}
          </div>
        </div>

        <ExecutionPanel taraCallLog={taraCallLog}/>

        <WindowClockPanel taraCallLog={taraCallLog}/>

        {/* Hourly heatmap */}
        <div className="bg-[#0A0A0E] border border-[#1B1B22] rounded-[10px] p-4 sm:p-5 mb-5 relative">
          <T2Stamp code="HOUR · 006"/>
          <div className="flex items-baseline justify-between mb-3">
            <div className="text-xs uppercase tracking-[0.22em] font-bold" style={{color:T2_GOLD}}>Hourly Breakdown <span className="text-[10px] tracking-wider text-[#EDEDED]/30 ml-1 font-normal normal-case">LOCAL · tap to drill</span></div>
          </div>
          <div className="grid grid-cols-4 sm:grid-cols-6 gap-1.5 sm:gap-2">
            {hourly.map(h=>{
              const c=cellColor(h.wr,h.n);
              const sel=selectedHour===h.hour;
              return(
                <button key={h.hour} onClick={()=>{if(h.n>0)setSelectedHour(sel?null:h.hour);}} disabled={h.n===0} className={'p-2 sm:p-3 rounded-lg text-center transition-all '+(h.n>0?'cursor-pointer hover:opacity-80':'cursor-default')} style={{background:sel?T2_GOLD_GLOW:c.bg,border:'0.5px solid '+(sel?T2_GOLD_BORDER:c.border),color:sel?T2_GOLD:c.color}}>
                  <div className="text-[9px] sm:text-[10px] font-medium opacity-70 mb-0.5">{String(h.hour).padStart(2,'0')}:00</div>
                  <div className="text-base sm:text-lg font-bold" style={T2_MONO_STYLE}>{h.wr!=null?h.wr.toFixed(0)+'%':'—'}</div>
                  <div className="text-[9px] opacity-50 mt-0.5">{h.n>0?h.wins+'/'+h.n:''}</div>
                </button>
              );
            })}
          </div>
          {/* Drill-down for selected hour */}
          {selectedHour!=null&&drillTrades&&drillTrades.length>0&&(
            <div className="mt-4 pt-4" style={{borderTop:'1px solid '+T2_GOLD_GLOW}}>
              <div className="flex items-center gap-2 mb-3">
                <span className="text-xs font-bold" style={{color:T2_GOLD}}>{String(selectedHour).padStart(2,'0')}:00 UTC</span>
                <span className="text-[11px] text-[#EDEDED]/50">·</span>
                <span className="text-[11px] text-[#EDEDED]/70">{hourly[selectedHour].wins}/{hourly[selectedHour].n} = {hourly[selectedHour].wr.toFixed(0)}%</span>
              </div>
              <div className="space-y-1">
                {drillTrades.slice(0,12).map((t,i)=>{
                  const ts=_fmtTimeTz(new Date(t.id),timeFormat,{hour12:false,hour:'2-digit',minute:'2-digit',second:'2-digit'});
                  const isWin=t.result==='WIN';
                  const dirCol=t.dir==='UP'?'text-emerald-400':'text-rose-400';
                  const resCol=isWin?'text-emerald-400':'text-rose-400';
                  const fgtAbs=Math.abs(t.fgtAlignment||0);
                  // V3.1.7: round to 1 decimal (V2.9 weighted FGT can be fractional)
                  const fgtAbsDisplay=fgtAbs<0.05?'0':fgtAbs.toFixed(1).replace(/\.0$/,'');
                  // V5.7.5: Window period derived from windowId (encodes bucket-start ISO).
                  let _period=null;
                  if(t.windowId){
                    try{
                      const m=t.windowId.match(/-(.+)$/);
                      if(m){
                        const start=new Date(m[1]);
                        if(!isNaN(start.getTime())){
                          const winMs=t.windowType==='15m'?900000:300000;
                          const end=new Date(start.getTime()+winMs);
                          const _f=(d)=>`${String(d.getHours()).padStart(2,'0')}:${String(d.getMinutes()).padStart(2,'0')}`;
                          _period=`${_f(start)}–${_f(end)}`;
                        }
                      }
                    }catch(e){}
                  }
                  const _strikeFmt=t.strike?'$'+Math.round(t.strike).toLocaleString():null;
                  const _closeFmt=t.closingPrice?'$'+Math.round(t.closingPrice).toLocaleString():null;
                  return(
                    <div key={i} className="px-2 py-1.5 rounded-lg text-[11px] hover:bg-[#0B0B0F]">
                      {/* Top row: time · period · direction · stats · result */}
                      <div className="flex items-center gap-2 sm:gap-3">
                        <span className="text-[#EDEDED]/50 w-16 shrink-0" style={T2_MONO_STYLE}>{ts}</span>
                        {_period&&<span className="text-[#EDEDED]/55 shrink-0 hidden sm:inline" style={T2_MONO_STYLE}>{_period}</span>}
                        <span className={'font-bold w-12 shrink-0 '+dirCol}>{t.dir}</span>
                        <span className="text-[#EDEDED]/40 w-24 sm:w-28 truncate hidden sm:block">{t.regime}</span>
                        <span className="text-[#EDEDED]/40 w-12 hidden sm:block" style={T2_MONO_STYLE}>{t.posterior?t.posterior.toFixed(0)+'%':'—'}</span>
                        <span className="text-[#EDEDED]/40 w-14 hidden md:block" style={T2_MONO_STYLE}>FGT {fgtAbsDisplay}/4</span>
                        <span className="text-[#EDEDED]/40 w-12 hidden md:block" style={T2_MONO_STYLE}>{t.qualityScore!=null?'Q'+t.qualityScore:'Q—'}</span>
                        <span className={'text-[#EDEDED]/40 w-16 ml-auto sm:ml-0'} style={T2_MONO_STYLE}>{t.closingGapBps!=null?formatSignedInt(t.closingGapBps)+' bps':'—'}</span>
                        <span className={'font-bold w-12 shrink-0 text-right '+resCol}>{t.result}</span>
                        {/* V9.2.1: P&L per trade — computed from betAmt + maxPay */}
                        {t.betAmt>0&&t.result&&t.result!=='SITOUT'&&(()=>{
                          const _pnl=t.result==='WIN'?(t.maxPay||0)-(t.betAmt||0):t.result==='LOSS'?-(t.betAmt||0):0;
                          return <span className={'w-16 text-right shrink-0 hidden sm:block font-bold '+(_pnl>=0?'text-emerald-400':'text-rose-400')} style={T2_MONO_STYLE}>{_pnl>=0?'+':''}{_pnl.toFixed(2)}</span>;
                        })()}
                      </div>
                      {/* V5.7.5: Bottom row — strike → close. Only when both prices are known. */}
                      {_strikeFmt&&_closeFmt&&(
                        <div className="flex items-center gap-2 mt-0.5 pl-[72px] text-[10px] text-[#EDEDED]/40">
                          <span style={T2_MONO_STYLE}>Strike {_strikeFmt} → Close {_closeFmt}</span>
                          {t.closeSource==='kalshi'&&<span className="italic text-[#EDEDED]/30 hidden md:inline">· kalshi-settled</span>}
                        </div>
                      )}
                    </div>
                  );
                })}
                {drillTrades.length>12&&<div className="text-[10px] text-[#EDEDED]/30 px-2 py-1">+ {drillTrades.length-12} more not shown</div>}
              </div>
            </div>
          )}
        </div>

        {/* Cohort breakdowns */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-5">
          <CohortCard title="By Direction" stamp="DIR · 007" rows={byDir}/>
          <CohortCard title="By Regime" stamp="REG · 008" rows={byRegime}/>
          <CohortCard title="By Quality" stamp="QLT · 009" rows={byQuality}/>
          <CohortCard title="By FGT Alignment" stamp="FGT · 010" rows={byFGT}/>
          <CohortCard title="By Session" stamp="SES · 011" rows={bySession}/>
          <CohortCard title="By Lock Timing" stamp="TIM · 012" rows={byClockBucket}/>
        </div>

        {trades.length===0&&(
          <div className="text-center py-8 text-[#EDEDED]/40 text-sm">No resolved trades in this time window.</div>
        )}
      </div>
    </div>
  );
}

// Cohort row card — used inside StatsView
function CohortCard({title,stamp,rows}){
  return(
    <div className="bg-[#0A0A0E] border border-[#1B1B22] rounded-[10px] p-3 sm:p-4 relative">
      <T2Stamp code={stamp}/>
      <div className="text-[10px] uppercase tracking-[0.22em] font-bold mb-3" style={{color:T2_GOLD}}>{title}</div>
      {rows.length===0&&<div className="text-[11px] text-[#EDEDED]/30 italic">No data</div>}
      {rows.map(r=>{
        const wr=r.wr;
        const barColor=wr>=60?'#6FA98C':wr>=50?'rgba(35,185,129,0.5)':wr>=40?T2_COPPER:'rgba(232,69,94,0.72)';
        return(
          <div key={r.key} className="flex items-center gap-2 mb-1.5 last:mb-0 text-[11px]">
            <span className="text-[#EDEDED]/65 w-32 sm:w-40 shrink-0 truncate">{r.key}</span>
            <div className="flex-1 h-2 rounded-lg bg-black/30 relative overflow-hidden">
              <div className="absolute top-0 bottom-0 left-1/2 w-px bg-[#EDEDED]/15"></div>
              <div className="absolute top-0 bottom-0 left-0 transition-all duration-500" style={{width:Math.max(2,wr)+'%',background:barColor}}></div>
            </div>
            <span className="w-9 text-right font-bold shrink-0" style={{color:barColor,...T2_MONO_STYLE}}>{wr.toFixed(0)}%</span>
            <span className="w-12 text-right text-[#EDEDED]/30 shrink-0 hidden sm:inline" style={T2_MONO_STYLE}>{r.wins}/{r.n}</span>
          </div>
        );
      })}
    </div>
  );
}

// ── V3.1: TapeStrip ──
//   Sliding-window buy/sell pressure across 5s, 15s, 30s, 60s windows.
//   Shows users whether buying or selling pressure is accelerating or fading,
//   not just the current state.
//
//   Reading the strip:
//     - Bar fills from buy-USD side toward sell-USD side, percentage shows dominant share
//     - Four windows below let you see the trajectory:
//       * 5s rising vs 60s = buying ACCELERATING (top-of-move signal)
//       * 5s falling vs 60s = buying FADING (potential reversal)
//       * All four similar = stable pressure
//
//   V3.1.5/V3.1.6: Per-window volume floors. Below these thresholds the percentage is
//   replaced with "—" because the reading would be noise-driven (single print can flip a
//   sub-floor window from 50% to 90%). Floors tuned to filter single-print noise without
//   killing the strip during normal-but-quiet periods:
//     5s ≥ $5K, 15s ≥ $10K, 30s ≥ $25K, 60s ≥ $50K
//   When the headline 30s window is below floor, the whole strip shows "TAPE THIN" instead
//   of confident percentages. Trend indicator only computes when at least 3 windows clear
//   their floors — otherwise we'd be inferring acceleration from noise.
const TAPE_FLOORS={w5:5000,w15:10000,w30:25000,w60:50000};

function TapeStrip({tapeWindows,whaleLog}){
  if(!tapeWindows)return null;
  const w5=tapeWindows.w5||{},w15=tapeWindows.w15||{},w30=tapeWindows.w30||{},w60=tapeWindows.w60||{};
  // Use 30s as the headline bar
  const hl=w30;
  const hlBuys=hl.buys||0,hlSells=hl.sells||0;
  const hlTotal=hlBuys+hlSells;
  const hlBuyPct=hlTotal>0?(hlBuys/hlTotal)*100:50;
  const dominant=hlBuyPct>=50?'BUY':'SELL';
  const dominantPct=Math.max(hlBuyPct,100-hlBuyPct);
  const hlAboveFloor=hlTotal>=TAPE_FLOORS.w30;
  const fmtUSD=(n)=>{
    const v=Math.round(n);
    if(v>=1e6)return '$'+(v/1e6).toFixed(1)+'M';
    if(v>=1e3)return '$'+(v/1e3).toFixed(0)+'K';
    return '$'+v;
  };
  const buyColor='#6FA98C';
  const sellColor='rgba(232,69,94,0.72)';
  // Trend indicator — only compute when the headline window AND at least 2 others
  // are above their floors. Otherwise we're inferring acceleration from noise.
  const _trend=(()=>{
    if(!hlAboveFloor)return null;
    const validWindows=[
      {pct:w60.buyPct,total:(w60.buys||0)+(w60.sells||0),floor:TAPE_FLOORS.w60},
      {pct:w30.buyPct,total:(w30.buys||0)+(w30.sells||0),floor:TAPE_FLOORS.w30},
      {pct:w15.buyPct,total:(w15.buys||0)+(w15.sells||0),floor:TAPE_FLOORS.w15},
      {pct:w5.buyPct,total:(w5.buys||0)+(w5.sells||0),floor:TAPE_FLOORS.w5},
    ].filter(w=>w.total>=w.floor&&w.pct!=null).map(w=>w.pct);
    if(validWindows.length<3)return null;
    const dir=validWindows[validWindows.length-1]-validWindows[0];
    if(Math.abs(dir)<8)return null;
    return dir>0?'rising':'falling';
  })();
  const trendLabel=_trend==='rising'?(dominant==='BUY'?'↑ accelerating buy':'↑ buy reversing'):
                  _trend==='falling'?(dominant==='BUY'?'↓ buy fading':'↓ accelerating sell'):
                  null;
  // V6.3.2: TAPE QUALITY — 3-light system summarizing whether the tape signal is trustworthy.
  //   Three checks: (A) do 30s + 60s windows agree directionally and at meaningful strength?
  //   (B) is dollar volume in 60s above noise-floor (>$500K)? (C) do recent whales align with
  //   the tape direction? Each check returns true/false. Output: GREEN if all 3, AMBER if 2,
  //   ROSE if 0-1, GREY if tape is too thin to even score.
  const _quality=(()=>{
    const _w30Pct=w30.buyPct;
    const _w60Pct=w60.buyPct;
    const _w30Tot=(w30.buys||0)+(w30.sells||0);
    const _w60Tot=(w60.buys||0)+(w60.sells||0);
    if(_w30Tot<TAPE_FLOORS.w30||_w60Tot<TAPE_FLOORS.w60||_w30Pct==null||_w60Pct==null){
      return{level:'thin',windowsAgree:null,volMeaningful:null,whalesAlign:null,dirText:'TAPE THIN'};
    }
    // (A) Windows agree: same side AND each ≥60% (real strength, not coin-flip)
    const _w30Side=_w30Pct>=50?'BUY':'SELL';
    const _w60Side=_w60Pct>=50?'BUY':'SELL';
    const _w30Strength=Math.max(_w30Pct,100-_w30Pct);
    const _w60Strength=Math.max(_w60Pct,100-_w60Pct);
    const windowsAgree=_w30Side===_w60Side&&_w30Strength>=60&&_w60Strength>=60;
    // (B) Volume meaningful: 60s window has >$500K total
    const volMeaningful=_w60Tot>=500000;
    // (C) Whales align: among whales in last 60 seconds, ≥60% match dominant tape direction
    const _dominantSide=_w60Side; // use 60s as "dominant"
    let whalesAlign=null;
    if(Array.isArray(whaleLog)&&whaleLog.length>0){
      const _now=Date.now();
      const _recentWhales=whaleLog.filter(w=>w&&w.time&&(_now-w.time)<60000);
      if(_recentWhales.length>=2){
        // Whale side comes from the trade type — 'B' = buy, 'S' = sell
        const _whaleBuyCount=_recentWhales.filter(w=>w.side==='B'||w.side==='BUY'||w.t==='B').length;
        const _whaleAlignedCount=_dominantSide==='BUY'?_whaleBuyCount:(_recentWhales.length-_whaleBuyCount);
        whalesAlign=_whaleAlignedCount>=Math.ceil(_recentWhales.length*0.6);
      }
    }
    // Score: count true checks (treat null whale as neutral, doesn't add or subtract)
    let _score=0;
    if(windowsAgree)_score+=1;
    if(volMeaningful)_score+=1;
    if(whalesAlign===true)_score+=1;
    else if(whalesAlign===false)_score-=0.5; // slight penalty for divergent whales
    const level=_score>=2.5?'high':_score>=1.5?'medium':'low';
    return{level,windowsAgree,volMeaningful,whalesAlign,dirText:_dominantSide};
  })();
  const renderWindow=(label,w,floor)=>{
    const pct=w.buyPct!=null?w.buyPct:50;
    const total=(w.buys||0)+(w.sells||0);
    const aboveFloor=total>=floor;
    const isBuy=pct>=50;
    const color=!aboveFloor?'rgba(237,237,237,0.30)':isBuy?buyColor:sellColor;
    const display=Math.max(pct,100-pct);
    return(
      <div className="flex flex-col items-center" key={label}>
        <div className="text-[10px] uppercase tracking-wider text-[#EDEDED]/55 font-semibold mb-0.5">{label}</div>
        <div className="text-base sm:text-lg font-bold tabular-nums" style={{color,letterSpacing:'-0.01em'}}>{!aboveFloor?'—':display.toFixed(1)+'%'}</div>
      </div>
    );
  };
  return(
    <div className="bg-[#0A0A0E] border border-[#1B1B22] rounded-[10px] p-3 sm:p-4 relative">
      <T2Stamp code="TAPE · 030"/>
      <div className="flex items-baseline justify-between mb-2">
        <div className="flex items-baseline gap-2.5">
          <span className="text-[10px] uppercase tracking-[0.18em] text-[#EDEDED]/50 font-bold">Tape</span>
          {/* V6.3.2: Quality badge — 3-light at-a-glance trustworthiness indicator. */}
          {_quality.level!=='thin'&&(()=>{
            const _color=_quality.level==='high'?'#6FA98C':_quality.level==='medium'?'#23B981':'rgba(232,69,94,0.72)';
            const _label=_quality.level==='high'?'STRONG':_quality.level==='medium'?'MIXED':'WEAK';
            const _checks=[_quality.windowsAgree,_quality.volMeaningful,_quality.whalesAlign];
            const _tooltip=[
              `Windows agree: ${_quality.windowsAgree?'yes':'no'} (30s+60s same side, ≥60%)`,
              `Volume meaningful: ${_quality.volMeaningful?'yes':'no'} (60s ≥ $500K)`,
              `Whales align: ${_quality.whalesAlign===true?'yes':_quality.whalesAlign===false?'no':'n/a'} (last 60s)`,
            ].join(' · ');
            return(
              <span className="inline-flex items-center gap-1.5" title={_tooltip}>
                <span className="inline-flex items-center gap-0.5">
                  {_checks.map((c,i)=>React.createElement('span',{
                    key:i,
                    className:'w-1.5 h-1.5 rounded-full',
                    style:{background:c===true?_color:c===false?'rgba(232,69,94,0.72)':'rgba(237,237,237,0.18)'},
                  }))}
                </span>
                <span className="text-[10px] font-bold tracking-wider" style={{color:_color}}>{_label}</span>
              </span>
            );
          })()}
        </div>
        {!hlAboveFloor?(
          <span className="text-[10px] font-medium tracking-wider text-[#EDEDED]/40 uppercase">Tape thin · {fmtUSD(hlTotal)} / 30s</span>
        ):trendLabel&&(
          <span className="text-[10px] font-medium" style={{color:_trend==='rising'?(dominant==='BUY'?buyColor:sellColor):(dominant==='BUY'?sellColor:buyColor),letterSpacing:'0.04em'}}>{trendLabel}</span>
        )}
      </div>
      {/* Headline bar with USD endpoints + percentage in middle */}
      <div className="relative mb-1">
        <div className="flex items-center justify-between text-[11px] mb-1.5">
          <span className="font-bold tabular-nums" style={{color:hlAboveFloor&&hlBuyPct>=50?buyColor:'rgba(237,237,237,0.40)',letterSpacing:'-0.01em'}}>BUY {fmtUSD(hlBuys)}</span>
          <span className="font-bold tabular-nums" style={{color:hlAboveFloor?'rgba(237,237,237,0.95)':'rgba(237,237,237,0.30)',letterSpacing:'-0.01em'}}>{!hlAboveFloor?'—':dominantPct.toFixed(1)+'%'}</span>
          <span className="font-bold tabular-nums" style={{color:hlAboveFloor&&hlBuyPct<50?sellColor:'rgba(237,237,237,0.40)',letterSpacing:'-0.01em'}}>SELL {fmtUSD(hlSells)}</span>
        </div>
        <div className="relative h-1.5 rounded-lg bg-black/40 overflow-hidden">
          {hlAboveFloor?(
            <>
              <div className="absolute top-0 bottom-0 left-0 transition-all duration-500" style={{width:hlBuyPct.toFixed(1)+'%',background:buyColor,opacity:0.85}}></div>
              <div className="absolute top-0 bottom-0 right-0 transition-all duration-500" style={{width:(100-hlBuyPct).toFixed(1)+'%',background:sellColor,opacity:0.85}}></div>
            </>
          ):(
            <div className="absolute top-0 bottom-0 left-0 right-0" style={{background:'rgba(237,237,237,0.08)'}}></div>
          )}
        </div>
      </div>
      {/* Four-window strip */}
      <div className="grid grid-cols-4 gap-2 mt-2">
        {renderWindow('5s',w5,TAPE_FLOORS.w5)}
        {renderWindow('15s',w15,TAPE_FLOORS.w15)}
        {renderWindow('30s',w30,TAPE_FLOORS.w30)}
        {renderWindow('60s',w60,TAPE_FLOORS.w60)}
      </div>
    </div>
  );
}

// V9.1.6: Parse news pubDate strings with proper UTC handling. Some RSS feeds
//   (notably rss2json) return dates as "YYYY-MM-DD HH:MM:SS" without timezone, which
//   JavaScript treats as LOCAL time — shifting timestamps by the user's TZ offset.
//   This helper detects timezone-less strings and treats them as UTC instead.
const parseNewsDate=(s)=>{
  if(!s)return Date.now();
  if(typeof s==='number')return s;
  const _str=String(s).trim();
  // ISO format with Z, +HH:MM, or -HH:MM offset → JavaScript parses correctly
  if(/[TZ]|[+-]\d{2}:?\d{2}$/.test(_str)){
    const _t=Date.parse(_str);
    return Number.isFinite(_t)?_t:Date.now();
  }
  // RFC 822 (e.g. "Tue, 06 May 2026 10:30:00 GMT" / "+0000") → has timezone, fine
  if(/GMT|UTC|[+-]\d{4}$/.test(_str)){
    const _t=Date.parse(_str);
    return Number.isFinite(_t)?_t:Date.now();
  }
  // No timezone marker — most likely "YYYY-MM-DD HH:MM:SS" from rss2json. Treat as UTC.
  if(/^\d{4}-\d{2}-\d{2}[T ]\d{2}:\d{2}/.test(_str)){
    const _normalized=_str.replace(' ','T')+'Z';
    const _t=Date.parse(_normalized);
    if(Number.isFinite(_t))return _t;
  }
  // Fallback — let JS guess
  const _t=Date.parse(_str);
  return Number.isFinite(_t)?_t:Date.now();
};

// V9.1.2: Simple keyword-based price-impact inference for news headlines.
//   Returns 'UP' (bullish), 'DOWN' (bearish), or null (neutral/unclear).
//   Not perfect — no LLM/sentiment model — but gives the user a quick visual cue.
const inferNewsPriceImpact=(title)=>{
  if(!title||typeof title!=='string')return null;
  const t=title.toLowerCase();
  // Bearish keywords (price down)
  const bearWords=['crash','plunge','dump','sell-off','sells off','drop','fall','tank','collapse','liquidation','liquidations','outflow','outflows','ban','banned','sec sue','lawsuit','hack','hacked','fraud','exploit','rug','rugpull','bearish','short squeeze fail','warning','reject','rejection','denied','breach','exodus','correction','plummet','tumble','slump','slide','wipe','wiped','red day','blood','panic','fear'];
  // Bullish keywords (price up)
  const bullWords=['surge','rally','soar','pump','spike','breakout','breaks out','approval','approved','etf inflow','etf inflows','adoption','accumulate','accumulating','accumulation','bullish','green day','milestone','high','ath','all-time high','record','partnership','deal','launch','adoption','institutional','endorsement','buys','bought','whale buy','support','demand'];
  let bearScore=0,bullScore=0;
  for(const w of bearWords)if(t.includes(w))bearScore++;
  for(const w of bullWords)if(t.includes(w))bullScore++;
  if(bullScore>bearScore&&bullScore>0)return'UP';
  if(bearScore>bullScore&&bearScore>0)return'DOWN';
  return null;
};

// V9.1.4: DepthStrip — parallel to TapeStrip but for ORDER BOOK depth at the strike.
//   Multi-band breakdown shows liquidity at TIGHT (0.05%), CLOSE (0.1%), STANDARD (0.2%),
//   and WIDE (0.5%) distances from strike. Same visual structure as TapeStrip so the
//   user can read both at a glance. Uses orderBook.bands which is populated in the
//   level-2 fetch on a 5s cadence.
function DepthStrip({orderBook,targetMargin}){
  if(!orderBook||!orderBook.bands)return null;
  const _bands=orderBook.bands;
  // Use STANDARD band as headline (matches the pre-V9.1.4 _bookRange behavior)
  const hlBids=Number(_bands.std?.b)||0;
  const hlAsks=Number(_bands.std?.a)||0;
  const hlTotal=hlBids+hlAsks;
  const hlBidPct=hlTotal>0?(hlBids/hlTotal)*100:50;
  const dominant=hlBidPct>=50?'BID':'ASK';
  const dominantPct=Math.max(hlBidPct,100-hlBidPct);
  const _MIN_USD=50000; // floor below which the band is "thin"
  const hlAboveFloor=hlTotal>=_MIN_USD;
  const fmtUSD=(n)=>{
    const v=Math.round(n);
    if(v>=1e6)return '$'+(v/1e6).toFixed(1)+'M';
    if(v>=1e3)return '$'+(v/1e3).toFixed(0)+'K';
    return '$'+v;
  };
  const bidColor='#6FA98C';
  const askColor='rgba(232,69,94,0.72)';
  // Quality: do TIGHT and STD agree directionally with meaningful strength?
  const _quality=(()=>{
    const _stdTot=hlBids+hlAsks;
    const _tightTot=(Number(_bands.tight?.b)||0)+(Number(_bands.tight?.a)||0);
    const _wideTot=(Number(_bands.wide?.b)||0)+(Number(_bands.wide?.a)||0);
    if(_stdTot<_MIN_USD||_wideTot<_MIN_USD){
      return{level:'thin',dirText:'BOOK THIN'};
    }
    const _stdPct=hlBidPct;
    const _widePct=_wideTot>0?((Number(_bands.wide?.b)||0)/_wideTot)*100:50;
    const _stdSide=_stdPct>=50?'BID':'ASK';
    const _wideSide=_widePct>=50?'BID':'ASK';
    const _stdStrength=Math.max(_stdPct,100-_stdPct);
    const _wideStrength=Math.max(_widePct,100-_widePct);
    const bandsAgree=_stdSide===_wideSide&&_stdStrength>=58&&_wideStrength>=58;
    const volMeaningful=_wideTot>=500000;
    const tightConfirms=_tightTot>=_MIN_USD?(()=>{
      const _tPct=((Number(_bands.tight?.b)||0)/_tightTot)*100;
      const _tSide=_tPct>=50?'BID':'ASK';
      return _tSide===_stdSide;
    })():null;
    let _score=0;
    if(bandsAgree)_score+=1;
    if(volMeaningful)_score+=1;
    if(tightConfirms===true)_score+=1;
    else if(tightConfirms===false)_score-=0.5;
    const level=_score>=2.5?'high':_score>=1.5?'medium':'low';
    return{level,bandsAgree,volMeaningful,tightConfirms,dirText:_stdSide};
  })();
  const renderBand=(label,band,pct,floor=_MIN_USD)=>{
    const _b=Number(band?.b)||0,_a=Number(band?.a)||0;
    const _tot=_b+_a;
    const _pct=_tot>0?(_b/_tot)*100:50;
    const aboveFloor=_tot>=floor;
    const isBid=_pct>=50;
    const color=!aboveFloor?'rgba(237,237,237,0.30)':isBid?bidColor:askColor;
    const display=Math.max(_pct,100-_pct);
    return(
      <div className="flex flex-col items-center" key={label}>
        <div className="text-[10px] uppercase tracking-wider text-[#EDEDED]/55 font-semibold mb-0.5">{label}</div>
        <div className="text-base sm:text-lg font-bold tabular-nums" style={{color,letterSpacing:'-0.01em'}}>{!aboveFloor?'—':display.toFixed(1)+'%'}</div>
      </div>
    );
  };
  return(
    <div className="bg-[#0A0A0E] border border-[#1B1B22] rounded-[10px] p-3 sm:p-4 relative">
      <T2Stamp code="DOM · BAND"/>
      <div className="flex items-baseline justify-between mb-2">
        <div className="flex items-baseline gap-2.5">
          <span className="text-[10px] uppercase tracking-[0.18em] text-[#EDEDED]/50 font-bold">Depth</span>
          {_quality.level!=='thin'&&(()=>{
            const _color=_quality.level==='high'?'#6FA98C':_quality.level==='medium'?'#23B981':'rgba(232,69,94,0.72)';
            const _label=_quality.level==='high'?'STRONG':_quality.level==='medium'?'MIXED':'WEAK';
            const _checks=[_quality.bandsAgree,_quality.volMeaningful,_quality.tightConfirms];
            const _tooltip=[
              `Bands agree: ${_quality.bandsAgree?'yes':'no'} (STD+WIDE same side, ≥58%)`,
              `Volume meaningful: ${_quality.volMeaningful?'yes':'no'} (WIDE ≥ $500K)`,
              `Tight confirms: ${_quality.tightConfirms===true?'yes':_quality.tightConfirms===false?'no':'n/a'}`,
            ].join(' · ');
            return(
              <span className="inline-flex items-center gap-1.5" title={_tooltip}>
                <span className="inline-flex items-center gap-0.5">
                  {_checks.map((c,i)=>React.createElement('span',{
                    key:i,
                    className:'inline-block w-1 h-1 rounded-full',
                    style:{background:c?_color:'rgba(237,237,237,0.18)'},
                  }))}
                </span>
                <span className="text-[9px] uppercase tracking-[0.16em] font-bold" style={{color:_color}}>{_label}</span>
              </span>
            );
          })()}
          {_quality.level==='thin'&&(
            <span className="text-[9px] uppercase tracking-[0.16em] font-bold text-[#EDEDED]/30">{_quality.dirText}</span>
          )}
        </div>
      </div>
      <div className="flex items-baseline justify-between mb-1">
        <span className="text-[11px] sm:text-xs font-bold tabular-nums" style={{color:bidColor}}>BID {fmtUSD(hlBids)}</span>
        <span className="text-[11px] sm:text-xs tabular-nums text-[#EDEDED]/55">{hlAboveFloor?dominantPct.toFixed(1)+'%':'—'}</span>
        <span className="text-[11px] sm:text-xs font-bold tabular-nums" style={{color:askColor}}>ASK {fmtUSD(hlAsks)}</span>
      </div>
      <div className="w-full h-1.5 bg-[#050508] rounded-full overflow-hidden flex relative mb-2">
        <div style={{width:`${hlBidPct}%`,background:bidColor,opacity:hlAboveFloor?0.85:0.30}} className="h-full transition-all duration-300"></div>
        <div style={{width:`${100-hlBidPct}%`,background:askColor,opacity:hlAboveFloor?0.85:0.30}} className="h-full transition-all duration-300"></div>
      </div>
      <div className="grid grid-cols-4 gap-2 mt-2">
        {renderBand('TIGHT',_bands.tight)}
        {renderBand('CLOSE',_bands.close)}
        {renderBand('STD',_bands.std)}
        {renderBand('WIDE',_bands.wide)}
      </div>
    </div>
  );
}

// ── V111: NewsFeedCard - external events affecting BTC price ──
function NewsFeedCard({timeFormat,pushToast,calendar}={}){
  const[news,setNews]=React.useState([]);
  const[loading,setLoading]=React.useState(true);
  const[err,setErr]=React.useState(null);
  // V134: also fetch macro event countdown — always shown even if news fails
  const[macroEvents,setMacroEvents]=React.useState([]);
  React.useEffect(()=>{
    const now=new Date(),active=getMacroEventState(now),upcoming=getUpcomingMacroEvents(now,24).slice(0,3);
    if(active.event&&!upcoming.some(e=>e.id===active.event.id))upcoming.unshift({...active.event,minutesUntil:active.minutesUntil,state:active.state});
    setMacroEvents(upcoming);
  },[calendar]);
  // V9.8.19: track URLs we've already seen so each fetch doesn't re-toast the same
  //   article. On first mount we silently populate the seen-set with the initial
  //   batch — only NEW arrivals after first load can trigger toasts. This prevents
  //   the page-load spam case where 10 stale impactful headlines all fire at once.
  const _seenNewsUrlsRef=React.useRef(null); // null until first fetch completes; then Set
  // V9.0: ref to fetchNews so the retry button can trigger a fresh fetch
  //   without re-running the entire useEffect (which would also restart the macro
  //   interval). Set inside useEffect, called from the retry button below.
  const fetchNewsRef=React.useRef(null);
  // V9.1.2: expand modal for full news view
  const[expandOpen,setExpandOpen]=React.useState(false);
  // V9.8.19: NEWS IMPACT TOAST DETECTOR. Whenever the news state updates, scan
  //   the items for high-impact headlines (score ≥6 from scoreNewsImpact). For
  //   each newly-arrived impactful item (URL not in seen-set, AND time within
  //   last 15 min so we don't toast stale articles), push a toast via pushToast.
  //   First fetch silently populates the seen-set so initial page load doesn't
  //   spam. Color/direction-coded: bullish=green, bearish=rose, neutral=amber.
  React.useEffect(()=>{
    if(!Array.isArray(news)||news.length===0)return;
    if(typeof pushToast!=='function')return;
    // First-fetch path: silently seed the seen-set, no toasts
    if(_seenNewsUrlsRef.current===null){
      const seed=new Set();
      for(const item of news){if(item&&item.url)seed.add(item.url);}
      _seenNewsUrlsRef.current=seed;
      return;
    }
    const _now=Date.now();
    const _seen=_seenNewsUrlsRef.current;
    for(const item of news){
      if(!item||!item.url||!item.title)continue;
      if(_seen.has(item.url))continue;
      _seen.add(item.url);
      // Skip stale articles — only toast news under 15 min old (handles the case
      //   where news fetch returns mostly old items mixed with one new one).
      const _ageMs=_now-(item.time||0);
      if(_ageMs>15*60*1000)continue;
      const{score,direction,category}=scoreNewsImpact(item.title);
      if(score<6)continue; // subthreshold
      // Color/icon by direction
      const _color=direction==='down'?'rgba(232,69,94,0.72)':direction==='up'?'#6FA98C':'#23B981';
      const _icon=direction==='down'?'📰▼':direction==='up'?'📰▲':category==='macro'?'🏛':category==='regulatory'?'⚖':category==='security'?'⚠':'📰';
      const _catLabel=category?category.toUpperCase():'NEWS';
      pushToast(
        `news-${item.url}`, // unique kind per URL — natural dedup, but we use seen-set as primary guard
        `${_catLabel} · ${item.title.length>78?item.title.slice(0,75)+'…':item.title}`,
        item.source?`${item.source} · ${Math.round(_ageMs/60000)}m ago`:`${Math.round(_ageMs/60000)}m ago`,
        {color:_color,icon:_icon,durationMs:8000,cooldown:0,url:item.url}
      );
    }
  },[news,pushToast]);
  React.useEffect(()=>{
    const computeMacros=()=>{
      const now=new Date(),active=getMacroEventState(now);
      const upcoming=getUpcomingMacroEvents(now,24).slice(0,3);
      if(active.event&&!upcoming.some(e=>e.id===active.event.id))upcoming.unshift({...active.event,minutesUntil:active.minutesUntil,state:active.state});
      setMacroEvents(upcoming);
    };
    computeMacros();
    const macroIv=setInterval(computeMacros,60000);
    
    const fetchNews=async()=>{
      // V9.8.1: SERVER-SIDE NEWS PROXY. Browser fetches /api/news, which is a
      //   Vercel serverless function that does the source fallback chain
      //   server-side. No CORS proxies needed (corsproxy.io / codetabs /
      //   allorigins / rss2json were all eventually getting blocked or rate-
      //   limited per-IP in some networks). The api/news.js function tries
      //   CryptoCompare → Reddit → CryptoPanic in order and returns the first
      //   that works, plus a 2-minute server-side cache.
      const _writeCache=(items)=>{
        try{localStorage.setItem('taraNewsCache_v1',JSON.stringify({at:Date.now(),items}));}catch(_){}
      };
      const _readCache=()=>{
        try{
          const raw=localStorage.getItem('taraNewsCache_v1');
          if(!raw)return null;
          const c=JSON.parse(raw);
          if(!c||!Array.isArray(c.items)||c.items.length===0)return null;
          if(Date.now()-c.at>4*3600000)return null; // 4h max age
          return c;
        }catch(_){return null;}
      };
      try{
        const ctrl=new AbortController();
        const timer=setTimeout(()=>ctrl.abort(),8000);
        const r=await fetch('/api/news',{cache:'no-store',signal:ctrl.signal});
        clearTimeout(timer);
        if(r.ok){
          const data=await r.json();
          // V13.4.126 FIX: this was checking data?.status==='ok' against a shape
          //   (rss2json: {status:'ok', items:[{link,pubDate,categories}], feed:{title}})
          //   that api/news.js has never actually returned -- that comment describes an
          //   architecture (Vercel rewrite to rss2json) that isn't what's in vercel.json
          //   or api/news.js today. The real shape is {items:[{title,url,time,source,
          //   sentiment,isImportant}], sources:[], diagnostics:{}} -- the multi-source
          //   RSS+CryptoPanic+GDELT function actually deployed. Since data.status was
          //   never present, this condition was NEVER true -- every fetch, even a fully
          //   successful one with real items, fell through to 'bad shape' and eventually
          //   'News API offline'. Fixed to match the real contract.
          if(Array.isArray(data?.items)&&data.items.length>0){
            const items=data.items.slice(0,15).map(it=>({
              title:it.title,
              source:it.source||'news',
              url:it.url||null,
              time:it.time||Date.now(),
              categories:[],
            }));
            setNews(items);
            setErr(data.stale?'Cached news — upstream refresh failed':null);
            setLoading(false);
            if(!data.stale)_writeCache(items);
            return;
          }
          try{console.warn('[Tara news] /api/news returned no items. diagnostics:',data?.diagnostics||'(none)');}catch(_){}
        }else{
          try{console.warn('[Tara news] /api/news returned',r.status);}catch(_){}
        }
      }catch(e){
        try{console.warn('[Tara news] /api/news fetch failed:',e?.message||String(e));}catch(_){}
      }
      // ── Fallback: localStorage cache (≤4h old) ─────────────────────────
      const _cached=_readCache();
      if(_cached){
        setNews(_cached.items);
        const _ageMin=Math.round((Date.now()-_cached.at)/60000);
        setErr(`cached (${_ageMin}m ago) — fresh fetch failed`);
        setLoading(false);
        return;
      }
      setErr('News API offline — see browser console for details');
      setLoading(false);
    };
    fetchNewsRef.current=fetchNews; // V9.0: expose for retry button
    fetchNews();
    const iv=setInterval(fetchNews,300000); // V13.2: 5min (was 30s) - RSS barely changes; cut ~2,600 edge req/device/day
    return()=>{clearInterval(iv);clearInterval(macroIv);};
  },[]);

  const formatAge=(ts)=>{
    const s=Math.floor((Date.now()-ts)/1000);
    if(s<60)return s+'s ago';
    if(s<3600)return Math.floor(s/60)+'m ago';
    if(s<86400)return Math.floor(s/3600)+'h ago';
    return Math.floor(s/86400)+'d ago';
  };

  // Detect potentially impactful keywords for highlighting
  const isHot=(title)=>{
    const hot=['trump','biden','sec','regulation','crash','surge','etf','approve','hack','exploit','liquidat','squeeze','rally','dump','breaking'];
    const lower=title.toLowerCase();
    return hot.some(kw=>lower.includes(kw));
  };

  return(
    <div className="shrink-0">
      <div className={'flex items-center justify-between mb-2'}>
        <span className={'text-xs uppercase tracking-[0.2em] text-[#EDEDED]/40 font-bold'}>News & Macro</span>
        <div className="flex items-center gap-2">
          <span className={'text-[9px] text-[#EDEDED]/30 italic'}>{loading?'loading...':err?'news delayed':'5m refresh'}</span>
          {/* V9.1.2: Expand to full news view */}
          {news.length>0&&(
            <button onClick={()=>setExpandOpen(true)} title="Expand news feed" className="text-[10px] px-1.5 py-0.5 rounded-lg border transition-colors hover:bg-[#EDEDED]/5" style={{color:'#23B981',borderColor:'rgba(35,185,129,0.30)'}}>⊕</button>
          )}
        </div>
      </div>
      {/* V134: Macro events countdown - always shown */}
      {macroEvents.length>0&&(
        <div className="mb-2 space-y-1">
          <div className={'text-[9px] uppercase tracking-wide text-amber-400/70 font-bold'}>Upcoming Macro</div>
          {macroEvents.map((e,i)=>{
            const isImminent=Math.abs(e.minutesUntil)<=15;
            const isNow=e.minutesUntil<=0&&Math.abs(e.minutesUntil)<=2;
            const cls=isNow?'bg-rose-500/15 border-rose-500/40 text-rose-300 animate-pulse':isImminent?'bg-amber-500/10 border-amber-500/30 text-amber-300':'bg-[#050508] border-[#24242E] text-[#EDEDED]/60';
            const label=e.minutesUntil>0?`in ${e.minutesUntil}m`:`${Math.abs(e.minutesUntil)}m ago`;
            return(<div key={i} className={'p-1.5 rounded-lg border text-[10px] flex justify-between '+cls}>
              <span className="font-bold">{e.name}</span>
              <span>{label} · {e.impact}</span>
            </div>);
          })}
        </div>
      )}
      <div className="tara-news-list max-h-32 overflow-y-auto space-y-1.5">
        {loading?(
          <div className={'text-[10px] text-[#EDEDED]/30 italic'}>Loading market news...</div>
        ):err&&news.length===0?(
          // V13.4.299: the amber-tinted, amber-bordered box is gone. A feed
          //   being down is worth saying plainly, not worth a coloured panel in
          //   the middle of the right column -- the mockup has no tinted callout
          //   boxes anywhere. Text carries it; the retry stays a plain control.
          <div>
            <div className="text-[10px] text-[#EDEDED]/45 italic mb-1.5" title={String(err)}>News feed temporarily unavailable. Macro countdown above stays available; retry for headlines.</div>
            <button onClick={()=>{setLoading(true);setErr(null);try{fetchNewsRef.current?.();}catch(_){}}} className="text-[9px] uppercase tracking-[0.15em] font-bold px-2 py-0.5 rounded-lg border border-[#2A2A34] text-[#EDEDED]/55 hover:text-[#EDEDED]/80 transition-colors">↻ Retry</button>
          </div>
        ):news.length===0?(
          <div className={'text-[10px] text-[#EDEDED]/30 italic'}>No news available</div>
        ):news.map((n,i)=>{
          const hot=isHot(n.title);
          // V9.1.6: Mini-list arrows — brighter, larger. User feedback: arrows didn't
          //   show on the mini view. Was using text-[#EDEDED]/30 for neutral (nearly
          //   invisible) and tiny 10px size. Now: emerald/rose for direction, amber
          //   for mixed (always visible), text-xs (12px) so it actually reads.
          const impact=inferNewsPriceImpact(n.title);
          const impactArrow=impact==='UP'?'▲':impact==='DOWN'?'▼':'◆';
          const impactColor=impact==='UP'?'text-emerald-400':impact==='DOWN'?'text-rose-400':'text-amber-400/70';
          const impactTitle=impact==='UP'?'Likely bullish for price':impact==='DOWN'?'Likely bearish for price':'Mixed/unclear for price';
          // V9.1.4: Absolute timestamp formatter — small clock time alongside relative age
          // V9.1.6: Uses parseNewsDate-normalized ms timestamp; toLocaleTimeString
          //   renders in user's local TZ correctly.
          const _absTime=(()=>{
            try{
              const _d=new Date(n.time);
              if(isNaN(_d.getTime()))return null;
              return _fmtTimeTz(_d,timeFormat,{hour:'numeric',minute:'2-digit'});
            }catch(_){return null;}
          })();
          // V13.4.301: a "hot" story no longer gets an amber-filled, amber-bordered
          //   card. The mockup renders every headline the same way -- plain text
          //   over a source line -- and lets the impact arrow carry the signal.
          //   Boxing one story in colour made a single headline outshout the win
          //   rate two panels over. Hot now only brightens the ink.
          return(
            <a key={i} href={n.url} target="_blank" rel="noopener noreferrer" className="block px-1.5 py-1 rounded-[6px] hover:bg-[#EDEDED]/[0.035]">
              <div className={'text-[11px] leading-snug flex items-start gap-1.5 '+(hot?'text-[#EDEDED]/90':'text-[#EDEDED]/70')}>
                <span className={'shrink-0 font-bold text-[11px] leading-snug '+impactColor} title={impactTitle}>{impactArrow}</span>
                <span className="min-w-0">{n.title.slice(0,90)}{n.title.length>90?'...':''}</span>
              </div>
              <div className="flex items-center gap-2 mt-1">
                <span className={'text-[9px] text-[#EDEDED]/30 uppercase tracking-[0.15em] font-bold'}>{n.source}</span>
                <span className={'text-[9px] text-[#EDEDED]/30'}>· {formatAge(n.time)}</span>
                {_absTime&&<span className={'text-[9px] text-[#EDEDED]/30 tabular-nums'}>· {_absTime}</span>}
              </div>
            </a>
          );
        })}
      </div>
      {/* V9.1.2: Expanded news modal */}
      {expandOpen&&(
        <NewsExpandModal news={news} macroEvents={macroEvents} onClose={()=>setExpandOpen(false)} formatAge={formatAge} timeFormat={timeFormat}/>
      )}
    </div>
  );
}

// V9.1.6: SyncMenuModal — opens when SyncStatusPill is clicked. Three options:
//   1. Force Resync (pulls latest from cloud, MERGES with local)
//   2. Save as Baseline (pushes THIS device's state to baseline/canonical)
//   3. Apply Baseline (overwrites local with the canonical baseline)
function SyncMenuModal({onClose,onForceResync,onSaveBaseline,onApplyBaseline,onCleanup,forceResyncing,baselineBusy,baselineMeta,baselineDrift,deviceLabel,localCounts,healthData}){
  // V9.1.9: Cloud diagnostic — read live cloud paths and show counts side-by-side
  //   with local. If they disagree, the user can see immediately.
  const[cloudDiag,setCloudDiag]=React.useState(null);
  const[diagLoading,setDiagLoading]=React.useState(false);
  const[diagError,setDiagError]=React.useState(null);
  const _runDiag=React.useCallback(async()=>{
    setDiagLoading(true);
    setDiagError(null);
    try{
      const _log=await cloudRead('memory/taraCallLog');
      // V9.2.0: scorecards/personal no longer read — local-only path now.
      const _past=await cloudRead('history/pastWindows');
      const _pnl=await cloudRead('state/lifetimePnL');
      const _baseline=await cloudRead('baseline/canonical');
      const _entries=Array.isArray(_log?.entries)?_log.entries:[];
      // V9.2.0: split counts BY ASSET so concurrent BTC/ETH trading is visible.
      const _btcEntries=_entries.filter(e=>e&&(e.asset||'BTC')==='BTC');
      const _ethEntries=[];
      const _winsBtc=_btcEntries.filter(e=>e.result==='WIN').length;
      const _lossesBtc=_btcEntries.filter(e=>e.result==='LOSS').length;
      const _sitoutsBtc=_btcEntries.filter(e=>e.result==='SITOUT').length;
      const _winsEth=_ethEntries.filter(e=>e.result==='WIN').length;
      const _lossesEth=_ethEntries.filter(e=>e.result==='LOSS').length;
      const _sitoutsEth=_ethEntries.filter(e=>e.result==='SITOUT').length;
      setCloudDiag({
        log:_entries.length,
        btc:{n:_btcEntries.length,wins:_winsBtc,losses:_lossesBtc,sitouts:_sitoutsBtc},
        eth:{n:_ethEntries.length,wins:_winsEth,losses:_lossesEth,sitouts:_sitoutsEth},
        wins:_winsBtc+_winsEth,
        losses:_lossesBtc+_lossesEth,
        sitouts:_sitoutsBtc+_sitoutsEth,
        past:Array.isArray(_past?.entries)?_past.entries.length:0,
        pnl:Number(_pnl?.value)||0,
        baseline:_baseline?{
          savedAt:_baseline.savedAt,
          sourceDevice:_baseline.sourceDevice,
          dataLogCount:Array.isArray(_baseline.data?.taraCallLog)?_baseline.data.taraCallLog.length:0,
        }:null,
      });
    }catch(e){
      setDiagError(e?.message||'cloud read failed');
    }finally{
      setDiagLoading(false);
    }
  },[]);
  React.useEffect(()=>{_runDiag();},[_runDiag]);
  // V13.4.165: the primary-device state, its cloudRead and its setter are removed with
  //   the guard and the Sync-menu control they served. One shared live call; no device
  //   designation to read or write.
  React.useEffect(()=>{
    const onKey=(e)=>{if(e.key==='Escape')onClose();};
    window.addEventListener('keydown',onKey);
    return()=>window.removeEventListener('keydown',onKey);
  },[onClose]);
  const _baselineDate=baselineMeta?.savedAt?new Date(baselineMeta.savedAt).toLocaleString():null;
  const _baselineSizes=baselineMeta?.sizes||{};
  // V9.1.9: drift detection — compare local vs cloud for the source-of-truth log
  // V13.2.2: cap-aware drift. The cloud log is intentionally CAPPED at _TARA_CLOUD_LOG_CAP
  //   (egress fix); local keeps full history, so local>=cloud is the NORMAL healthy state,
  //   not drift. Real drift is only when the cloud is AHEAD of local (this device missed
  //   windows another device logged). Raw abs() count made the cap trip a permanent alarm.
  const _cloudLog=cloudDiag?(cloudDiag.log||0):0;
  const _localLog=localCounts.taraCallLog||0;
  const _logDelta=cloudDiag?(_cloudLog-_localLog):null;        // signed: + means cloud ahead
  const _logDrift=_logDelta!=null?Math.abs(_logDelta):null;
  const _hasDrift=_logDelta!=null&&_logDelta>10;               // only real drift = device behind cloud
  const _expectedCapGap=_logDelta!=null&&_logDelta<0&&_cloudLog<=(_TARA_CLOUD_LOG_CAP+50);
  return React.createElement('div',{
    className:'fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-8',
    style:{background:'rgba(0,0,0,0.75)',backdropFilter:'blur(4px)'},
    onClick:onClose,
  },
    React.createElement('div',{
      className:'w-full max-w-lg rounded-xl my-auto max-h-[90vh] overflow-y-auto',
      style:{background:'#101014',border:'1px solid rgba(35,185,129,0.20)',boxShadow:'0 0 40px rgba(35,185,129,0.15)'},
      onClick:(e)=>e.stopPropagation(),
    },
      React.createElement('div',{className:'flex items-center justify-between px-5 py-4 sticky top-0',style:{borderBottom:'1px solid #24242E',background:'#101014'}},
        React.createElement('div',{className:'flex items-baseline gap-3'},
          React.createElement('h2',{className:'font-serif text-2xl tracking-tight text-white'},'Sync'),
          React.createElement('span',{className:'text-[10px] uppercase tracking-[0.18em] font-bold',style:{color:'#23B981'}},'cross-device')
        ),
        React.createElement('button',{
          onClick:onClose,
          title:'Close (Esc)',
          className:'w-7 h-7 rounded-lg flex items-center justify-center text-[#EDEDED]/50 hover:text-white hover:bg-[#EDEDED]/5 transition-colors',
        },'✕')
      ),
      React.createElement('div',{className:'px-5 py-4 space-y-3'},
        // V13.4.165: the "Trading device" designation is retired along with the
        //   secondary-device guard it drove. Tara is one shared live decision; every
        //   device shows the same lock at the same time via first-write-wins on
        //   state/currentLock_<asset>_<windowType>. Nothing to designate any more.
        React.createElement('div',{className:'p-3 rounded-lg text-xs',style:{background:'#0E0E12',border:'1px solid rgba(35,185,129,0.30)'}},
          React.createElement('div',{className:'flex items-baseline justify-between mb-2'},
            React.createElement('span',{className:'text-[9px] uppercase tracking-[0.16em] font-bold',style:{color:'rgba(237,237,237,0.55)'}},'Trading device'),
            React.createElement('span',{className:'text-[9px] uppercase tracking-[0.14em] font-bold',style:{color:'#23B981'}},'ALL DEVICES LIVE'),
          ),
          React.createElement('div',{className:'text-[11px] text-[#EDEDED]/70'},
            'Every device runs the same live call. Whichever device commits a lock first wins, and all others adopt that exact lock within about a second, so the direction is identical everywhere.'
          ),
          React.createElement('div',{className:'text-[10px] text-[#EDEDED]/40 mt-1.5'},
            'The old per-device designation is retired (V13.4.165) - it silently muted locks on any device that was not the designated one.'
          ),
          // V13.4.233: every device and every deployment writes to the SAME doc
          //   paths in the same Supabase project. There is no per-user scoping, so
          //   anyone on a shared link is in this first-write-wins pool too.
          (()=>{
            const p=_peerBuildLag();
            if(!p)return null;
            const stale=p.behind>0;
            return React.createElement('div',{className:'mt-2 pt-2 text-[10px] leading-relaxed',
              style:{borderTop:'1px solid rgba(237,237,237,0.10)',
                     color:stale?'rgba(212,160,58,0.95)':'rgba(237,237,237,0.45)'}},
              stale
                ? `Another device (${p.by}) is writing to this same shared record on build 13.4.${p.theirs}, ${p.behind} behind yours. It shares your locks, your call log and your hourly record — and it does not have the gates added since. Refresh Tara on that device.`
                : `Another device (${p.by}) is on build 13.4.${p.theirs}, level with yours. It shares your locks and your record.`);
          })(),
        ),
        // V9.2.0: SIDE-BY-SIDE diagnostic — shared (cloud) vs local (this device only).
        //   Tara's calls + memory are SHARED across all devices/users via Firestore.
        //   Personal scorecard is LOCAL to this device only — not synced.
        React.createElement('div',{className:'p-3 rounded-lg text-xs',style:{background:'#0E0E12',border:`1px solid ${_hasDrift?'rgba(232,69,94,0.30)':'rgba(237,237,237,0.08)'}`}},
          React.createElement('div',{className:'flex items-baseline justify-between mb-2'},
            React.createElement('span',{className:'text-[9px] uppercase tracking-[0.16em] font-bold',style:{color:_hasDrift?'#E8455E':'rgba(237,237,237,0.55)'}},_hasDrift?'⚠ DRIFT DETECTED':'Sync diagnostic'),
            React.createElement('button',{
              onClick:_runDiag,
              disabled:diagLoading,
              className:'text-[9px] uppercase tracking-[0.14em] font-bold px-1.5 py-0.5 rounded-lg border transition-colors hover:bg-[#EDEDED]/5 disabled:opacity-50',
              style:{color:'#23B981',borderColor:'rgba(35,185,129,0.30)'},
            },diagLoading?'reading…':'↻ refresh')
          ),
          // === SHARED SECTION (cloud-synced) ===
          React.createElement('div',{className:'mb-3'},
            React.createElement('div',{className:'flex items-baseline gap-2 mb-2'},
              React.createElement('span',{className:'text-[10px]'},'☁'),
              React.createElement('span',{className:'text-[10px] uppercase tracking-[0.16em] font-bold',style:{color:'rgba(176,176,176,0.95)'}},'Tara\'s shared memory'),
              React.createElement('span',{className:'text-[9px] text-[#EDEDED]/40'},'cross-device · cross-user')
            ),
            React.createElement('div',{className:'grid grid-cols-2 gap-3'},
              // LOCAL column
              React.createElement('div',null,
                React.createElement('div',{className:'text-[9px] uppercase tracking-wider text-[#EDEDED]/40 mb-1'},`THIS DEVICE · ${deviceLabel}`),
                React.createElement('div',{className:'text-[11px] tabular-nums leading-snug text-[#EDEDED]/80 space-y-0.5'},
                  React.createElement('div',{className:'font-bold'},`${localCounts.taraCallLog} log entries`),
                  React.createElement('div',{className:'pl-2'},
                    React.createElement('span',{className:'text-amber-300/80'},'BTC '),`${localCounts.btc.n} (${localCounts.btc.wins}W·${localCounts.btc.losses}L·${localCounts.btc.sitouts}SO)`
                  ),
                  React.createElement('div',{className:'pl-2'},
                    React.createElement('span',{className:'text-blue-300/80'},'ETH '),`${localCounts.eth.n} (${localCounts.eth.wins}W·${localCounts.eth.losses}L·${localCounts.eth.sitouts}SO)`
                  ),
                  React.createElement('div',{className:'pt-1 text-[#EDEDED]/60'},`${localCounts.pastWindows} past windows`),
                  React.createElement('div',{className:Number(localCounts.pnl)>=0?'text-emerald-400':'text-rose-400'},`${Number(localCounts.pnl)>=0?'+':''}$${Number(localCounts.pnl).toFixed(2)} P&L`)
                )
              ),
              // CLOUD column
              React.createElement('div',null,
                React.createElement('div',{className:'text-[9px] uppercase tracking-wider text-[#EDEDED]/40 mb-1'},'CLOUD · everyone'),
                diagError?React.createElement('div',{className:'text-[10px] text-rose-400'},'Read failed: ',diagError):
                  cloudDiag?React.createElement('div',{className:'text-[11px] tabular-nums leading-snug text-[#EDEDED]/80 space-y-0.5'},
                    React.createElement('div',{className:'font-bold '+(_hasDrift?'text-amber-300':'')},
                      `${cloudDiag.log} log entries`,
                      Math.abs(cloudDiag.log-localCounts.taraCallLog)>0?` (Δ${cloudDiag.log-localCounts.taraCallLog})`:''
                    ),
                    React.createElement('div',{className:'pl-2'},
                      React.createElement('span',{className:'text-amber-300/80'},'BTC '),`${cloudDiag.btc.n} (${cloudDiag.btc.wins}W·${cloudDiag.btc.losses}L·${cloudDiag.btc.sitouts}SO)`
                    ),
                    React.createElement('div',{className:'pl-2'},
                      React.createElement('span',{className:'text-blue-300/80'},'ETH '),`${cloudDiag.eth.n} (${cloudDiag.eth.wins}W·${cloudDiag.eth.losses}L·${cloudDiag.eth.sitouts}SO)`
                    ),
                    React.createElement('div',{className:'pt-1 text-[#EDEDED]/60'},`${cloudDiag.past} past windows`),
                    React.createElement('div',{className:cloudDiag.pnl>=0?'text-emerald-400':'text-rose-400'},`${cloudDiag.pnl>=0?'+':''}$${cloudDiag.pnl.toFixed(2)} P&L`)
                  ):
                  React.createElement('div',{className:'text-[10px] text-[#EDEDED]/40 italic'},'Reading…')
              )
            ),
            // Drift hint
            _hasDrift?React.createElement('div',{className:'mt-2 pt-2 text-[10px] text-amber-300 leading-snug',style:{borderTop:'1px solid #24242E'}},
              `Cloud is ahead of this device by ${_logDrift} windows - another device logged windows this one hasn't pulled. Press Force Resync from Cloud to merge them in. (Don't Save as baseline here - this device is the one that's behind.)`
            ):(_expectedCapGap?React.createElement('div',{className:'mt-2 pt-2 text-[10px] text-[#EDEDED]/45 leading-snug',style:{borderTop:'1px solid #24242E'}},
              `Cloud keeps the most recent ${_TARA_CLOUD_LOG_CAP} windows; this device holds the full ${_localLog}. That gap is the egress cap working as designed - not drift, and nothing is lost (full history lives here and in your exports). Avoid Save as baseline, which would re-upload the full log and undo the egress cut.`
            ):null)
          ),
          // === LOCAL-ONLY SECTION ===
          React.createElement('div',{className:'pt-3',style:{borderTop:'1px solid #24242E'}},
            React.createElement('div',{className:'flex items-baseline gap-2 mb-1'},
              React.createElement('span',{className:'text-[10px]'},'⌂'),
              React.createElement('span',{className:'text-[10px] uppercase tracking-[0.16em] font-bold',style:{color:'#23B981'}},'Personal scorecard'),
              React.createElement('span',{className:'text-[9px] text-[#EDEDED]/40'},'this device only · never synced')
            ),
            React.createElement('div',{className:'text-[11px] tabular-nums text-[#EDEDED]/80'},
              `15m: ${localCounts.personalW||0}W · ${localCounts.personalL||0}L`
            )
          ),
          // Baseline cloud info
          cloudDiag?.baseline&&React.createElement('div',{className:'mt-3 pt-2 text-[10px] text-[#EDEDED]/55 leading-snug',style:{borderTop:'1px solid #24242E'}},
            'Cloud baseline: ',cloudDiag.baseline.dataLogCount,' entries from ',cloudDiag.baseline.sourceDevice,' on ',new Date(cloudDiag.baseline.savedAt).toLocaleString()
          ),
          // V9.10.9: Baseline drift warning — fires when this device last synced a
          //   baseline saved under a different Tara version than the one currently running.
          //   The state was already computed at TaraApp init (compares localStorage's
          //   'taraBaselineVersion' against the live BASELINE_VERSION constant) but was
          //   never displayed anywhere. Showing it here gives the user a clear "your
          //   baseline is from an older Tara version — consider re-saving or pulling
          //   fresh" cue, which is the exact thing the Sync menu is for.
          baselineDrift&&React.createElement('div',{
            className:'mt-2 px-2 py-1.5 rounded-lg text-[10px] text-amber-300 leading-snug',
            style:{background:'rgba(212,162,76,0.08)',border:'1px solid rgba(212,162,76,0.3)'},
          },
            React.createElement('span',{className:'font-bold uppercase tracking-wider mr-1'},'⚠ Baseline drift'),
            'your saved baseline was made under an older Tara version. Save a fresh baseline below or pull from cloud to align with current code.'
          )
        ),
        // OPTION 1 — Force Resync
        React.createElement('button',{
          onClick:onForceResync,
          disabled:forceResyncing||baselineBusy,
          className:'w-full text-left p-3 rounded-lg transition-colors disabled:opacity-50 hover:bg-[#8C98A8]/10',
          style:{background:'rgba(110,110,110,0.06)',border:'1px solid rgba(110,110,110,0.25)'},
        },
          React.createElement('div',{className:'flex items-baseline gap-2 mb-1'},
            React.createElement('span',{className:'text-[10px] uppercase tracking-[0.16em] font-bold',style:{color:'rgba(176,176,176,0.95)'}},forceResyncing?'Resyncing…':'Force Resync from Cloud'),
            React.createElement('span',{className:'text-[9px] text-[#EDEDED]/40'},'merge · safe')
          ),
          React.createElement('div',{className:'text-[11px] text-[#EDEDED]/65 leading-snug'},
            'Pull the latest from cloud and ',React.createElement('strong',{className:'text-[#EDEDED]/85'},'merge'),
            ' with this device. Local entries that aren\u2019t in cloud are kept; cloud entries that aren\u2019t local get added. No data loss.'
          )
        ),
        // OPTION 2 — Save as Baseline
        React.createElement('button',{
          onClick:onSaveBaseline,
          disabled:baselineBusy||forceResyncing,
          className:'w-full text-left p-3 rounded-lg transition-colors disabled:opacity-50 hover:bg-emerald-500/10',
          style:{background:'rgba(24,128,96,0.06)',border:'1px solid rgba(24,128,96,0.25)'},
        },
          React.createElement('div',{className:'flex items-baseline gap-2 mb-1'},
            React.createElement('span',{className:'text-[10px] uppercase tracking-[0.16em] font-bold',style:{color:'#23B981'}},baselineBusy?'Working\u2026':'Save as Baseline'),
            React.createElement('span',{className:'text-[9px] text-[#EDEDED]/40'},'this device \u2192 canonical')
          ),
          React.createElement('div',{className:'text-[11px] text-[#EDEDED]/65 leading-snug'},
            'Mark THIS device as the canonical truth. Pushes this device\u2019s log, scorecards, P&L, and learnings to ',
            React.createElement('code',{className:'text-[10px] bg-[#050508] px-1'},'baseline/canonical'),
            '. Other devices can then "Apply Baseline" to receive this exact state.'
          )
        ),
        // OPTION 3 — Apply Baseline
        React.createElement('button',{
          onClick:onApplyBaseline,
          disabled:baselineBusy||forceResyncing||!baselineMeta,
          className:'w-full text-left p-3 rounded-lg transition-colors disabled:opacity-50 hover:bg-amber-500/10',
          style:{background:'rgba(212,162,76,0.06)',border:'1px solid rgba(212,162,76,0.25)'},
        },
          React.createElement('div',{className:'flex items-baseline gap-2 mb-1'},
            React.createElement('span',{className:'text-[10px] uppercase tracking-[0.16em] font-bold',style:{color:'rgba(224,185,120,0.95)'}},baselineBusy?'Working\u2026':'Apply Baseline'),
            React.createElement('span',{className:'text-[9px] text-[#EDEDED]/40'},'canonical \u2192 this device')
          ),
          React.createElement('div',{className:'text-[11px] text-[#EDEDED]/65 leading-snug'},
            baselineMeta?React.createElement(React.Fragment,null,
              'Pull the canonical baseline from cloud. ',
              React.createElement('strong',{className:'text-rose-300'},'Overwrites'),
              ' local data. Last saved by ',
              React.createElement('strong',{className:'text-[#EDEDED]/85'},baselineMeta.sourceDevice||'unknown'),
              ' on ',
              React.createElement('strong',{className:'text-[#EDEDED]/85'},_baselineDate||'\u2014'),
              '. Baseline has ',
              React.createElement('strong',{className:'text-[#EDEDED]/85'},`${_baselineSizes.taraCallLog||0} entries · ${_baselineSizes.wins||0}W·${_baselineSizes.losses||0}L·${_baselineSizes.sitouts||0}SO · ${_baselineSizes.pastWindows||0} windows`),
              '.'
            ):'No baseline saved yet. Save one from your most up-to-date device first.'
          )
        ),
        // Helper text
        React.createElement('div',{className:'text-[10px] text-[#EDEDED]/35 leading-snug pt-1'},
          'Workflow: keep your fullest device updated normally. End each session with ',
          React.createElement('strong',{className:'text-[#EDEDED]/55'},'Save as Baseline'),
          '. On any other device, press ',
          React.createElement('strong',{className:'text-[#EDEDED]/55'},'Apply Baseline'),
          ' to receive that exact state. Force Resync still works as a non-destructive merge any time.'
        ),
        // V9.2.3: DATA CLEANUP
        React.createElement('button',{
          onClick:onCleanup,
          className:'w-full text-left p-3 rounded-lg transition-colors hover:bg-rose-500/10',
          style:{background:'#0E0E12',border:'1px solid #24242E'},
        },
          React.createElement('div',{className:'flex items-baseline gap-2 mb-1'},
            React.createElement('span',{className:'text-[10px] uppercase tracking-[0.16em] font-bold text-[#EDEDED]/55'},'🧹 Clean Up Log'),
          ),
          React.createElement('div',{className:'text-[11px] text-[#EDEDED]/55 leading-snug'},
            'Scan for broken entries (no windowId), backfill missing fields (asset from strike, phase from timestamp, closingGapBps from prices), and remove duplicates. Preview before applying.'
          )
        ),
        // V9.2.3: HEALTH DASHBOARD
        healthData&&React.createElement('div',{className:'p-3 rounded-lg',style:{background:'#0E0E12',border:'1px solid #24242E'}},
          React.createElement('div',{className:'text-[10px] uppercase tracking-[0.16em] font-bold text-[#EDEDED]/45 mb-2'},'System Health'),
          React.createElement('div',{className:'space-y-1.5 text-[11px]'},
            // Coinbase feed
            React.createElement('div',{className:'flex items-center justify-between'},
              React.createElement('span',{className:'text-[#EDEDED]/60'},'Coinbase price feed'),
              React.createElement('span',{className:healthData.priceFresh?'text-emerald-400':'text-rose-400'},healthData.priceFresh?'● live':'○ stale')
            ),
            // Kalshi feed
            React.createElement('div',{className:'flex items-center justify-between'},
              React.createElement('span',{className:'text-[#EDEDED]/60'},'Kalshi strike'),
              React.createElement('span',{className:healthData.kalshiOk?'text-emerald-400':healthData.kalshiCached?'text-amber-400':'text-rose-400'},
                healthData.kalshiOk?'● live':healthData.kalshiCached?'● cached':'○ offline')
            ),
            // Supabase sync
            React.createElement('div',{className:'flex items-center justify-between'},
              React.createElement('span',{className:'text-[#EDEDED]/60'},'Supabase sync'),
              React.createElement('span',{className:healthData.firestoreOk?'text-emerald-400':'text-amber-400'},
                healthData.firestoreOk?'● connected':'○ unknown')
            ),
            // Engine cycle time
            React.createElement('div',{className:'flex items-center justify-between'},
              React.createElement('span',{className:'text-[#EDEDED]/60'},'Engine cycle'),
              React.createElement('span',{className:'text-[#EDEDED]/50 tabular-nums'},`${healthData.engineMs||'—'}ms`)
            ),
            // ML model
            React.createElement('div',{className:'flex items-center justify-between'},
              React.createElement('span',{className:'text-[#EDEDED]/60'},'ML model'),
              React.createElement('span',{className:healthData.mlActive?'text-indigo-400':'text-[#EDEDED]/30'},
                healthData.mlActive?`● ${healthData.mlAccuracy}% acc · ${healthData.mlTrades}t`:'○ needs data')
            ),
            // Uptime
            React.createElement('div',{className:'flex items-center justify-between'},
              React.createElement('span',{className:'text-[#EDEDED]/60'},'Session uptime'),
              React.createElement('span',{className:'text-[#EDEDED]/50 tabular-nums'},healthData.uptime||'—')
            ),
          )
        )
      )
    )
  );
}

// V9.1.2: Expanded news modal — full list, larger rows, with impact arrows.
// V9.2.2: DEDICATED ANALYTICS PAGE — full-screen modal with deep performance analysis.
//   Accessible via header button. Shows: WR heatmap by hour×day, P&L curve by asset,
//   ML model status + feature importance, loss pattern distribution, regime×direction
//   performance matrix, signal attribution chart.
function TaraAnalyticsPage({taraCallLog,taraMLModel,onClose,timeFormat,embedded=false,lockResearch,asset='BTC',windowType='15m',learning}){
  React.useEffect(()=>{
    if(embedded)return;
    const onKey=(e)=>{if(e.key==='Escape')onClose();};
    window.addEventListener('keydown',onKey);
    return()=>window.removeEventListener('keydown',onKey);
  },[onClose,embedded]);
  const resolved=React.useMemo(()=>_canonicalTrades(taraCallLog||[]),[taraCallLog]);
  // ── WR Heatmap by UTC hour × day of week ──
  const heatmap=React.useMemo(()=>{
    const grid={};
    const days=['Sun','Mon','Tue','Wed','Thu','Fri','Sat'];
    days.forEach(d=>{grid[d]={};for(let h=0;h<24;h++)grid[d][h]={W:0,L:0};});
    resolved.forEach(e=>{
      const dt=new Date(e.id||e.time);
      const day=days[dt.getDay()];
      const hour=dt.getUTCHours();
      if(grid[day]&&grid[day][hour]){
        if(e.result==='WIN')grid[day][hour].W++;else grid[day][hour].L++;
      }
    });
    return{grid,days};
  },[resolved]);
  // ── Loss pattern breakdown ──
  const lossPatterns=React.useMemo(()=>{
    const p={};
    resolved.filter(e=>e.result==='LOSS').forEach(e=>{
      const pat=e.lossPattern||'UNKNOWN';
      if(!p[pat])p[pat]={n:0};
      p[pat].n++;
    });
    return Object.entries(p).sort((a,b)=>b[1].n-a[1].n);
  },[resolved]);
  // ── Regime × Direction matrix ──
  const regimeDir=React.useMemo(()=>{
    const m={};
    resolved.forEach(e=>{
      const k=`${e.regime||'?'}|${e.dir||'?'}`;
      if(!m[k])m[k]={W:0,L:0};
      if(e.result==='WIN')m[k].W++;else m[k].L++;
    });
    return Object.entries(m).map(([k,v])=>{const[regime,dir]=k.split('|');const n=v.W+v.L;return{regime,dir,W:v.W,L:v.L,n,wr:n>0?v.W/n:0};}).filter(r=>r.n>=3).sort((a,b)=>b.n-a.n);
  },[resolved]);
  // ── V13.4.143: SIGNAL EV — does each signal actually earn its keep? ──────────
  //   For every signal stamped in signalScoresAtLock, split the resolved trades by
  //   whether that signal AGREED with the direction actually taken (sign match), and
  //   compare net EV per contract between the agree and disagree groups.
  //
  //   Why EV and not win rate: on a binary market the price already encodes the win
  //   rate, so WR alone says nothing about profit. EV = (WR × 100) − entryCost − fee.
  //   A signal carries real information only if EV is HIGHER when it agreed. A large
  //   negative edge means the signal is anti-predictive and should be cut or inverted.
  //
  //   Entry cost prefers execCostAtLock (the true tradeable ask/bid recorded from
  //   V13.4.142); it falls back to the kalshiAtLock MID, which is optimistic by about
  //   half the spread. `usingExec` reports which basis the numbers are actually on.
  const signalEV=React.useMemo(()=>{
    const rows=[];
    const pool=(resolved||[]).filter(e=>
      e&&e.signalScoresAtLock&&typeof e.signalScoresAtLock==='object'&&
      (e.result==='WIN'||e.result==='LOSS')&&(e.dir==='UP'||e.dir==='DOWN'));
    if(!pool.length)return{rows:[],n:0,coverage:0,regimes:[],usingExec:0};

    // Entry cost in cents for a trade (true exec price if we have it, else mid).
    const costOf=(e)=>{
      if(typeof e.execCostAtLock==='number'&&isFinite(e.execCostAtLock)&&e.execCostAtLock>0&&e.execCostAtLock<100)return e.execCostAtLock;
      const k=Number(e.kalshiAtLock);
      if(!isFinite(k)||k<=0||k>=100)return null;
      return e.dir==='UP'?k:(100-k);
    };
    const feeOf=(c)=>0.07*(c/100)*(1-c/100)*100;
    const group=(list)=>{
      const n=list.length; if(!n)return null;
      const w=list.filter(e=>e.result==='WIN').length;
      const priced=list.map(e=>({e,c:costOf(e)})).filter(x=>x.c!=null);
      let g=0,f=0;
      for(const {e,c} of priced){g+=(e.result==='WIN')?(100-c):(-c);f+=feeOf(c);}
      return{n,wr:100*w/n,nPriced:priced.length,ev:priced.length?(g-f)/priced.length:null};
    };

    // Only sign-bearing directional scores. Excludes internal diagnostics (_prefixed)
    //   and absolute levels like vwap, where a positive value means nothing directional.
    const NOT_DIR=new Set(['vwap','timeOfDay']);
    const range={};
    for(const e of pool)for(const [k,v] of Object.entries(e.signalScoresAtLock)){
      if(typeof v!=='number'||!isFinite(v))continue;
      if(k.startsWith('_')||NOT_DIR.has(k))continue;
      const r=range[k]=range[k]||{min:v,max:v,n:0};
      r.min=Math.min(r.min,v);r.max=Math.max(r.max,v);r.n++;
    }
    for(const k of Object.keys(range)){
      const r=range[k];
      if(Math.abs(r.max)>1000||Math.abs(r.min)>1000)continue; // price-like, not a score
      const agree=[],dis=[];
      for(const e of pool){
        const v=e.signalScoresAtLock[k];
        if(typeof v!=='number'||!isFinite(v)||v===0)continue;
        ((v>0)===(e.dir==='UP')?agree:dis).push(e);
      }
      const A=group(agree),D=group(dis);
      const nonZero=(A?A.n:0)+(D?D.n:0);
      // INERT means the signal is (near) ALWAYS ZERO — a ratio, never a raw count.
      //   An inert signal that still carries a real weight is itself the finding
      //   (e.g. structure/technical/regime are zeroed inside CHOP regimes, so their
      //   weight of 35 multiplies nothing). Judging this needs a usable pool: with a
      //   handful of trades "4 non-zero of 4" is a small sample, NOT an inert signal.
      const ratio=pool.length?nonZero/pool.length:0;
      if(ratio<0.10){rows.push({k,kind:'inert',nonZero,total:pool.length});continue;}
      // Non-zero, but every trade fell on one side — no contrast, nothing to compare.
      if(!A||!D||A.nPriced===0||D.nPriced===0){
        rows.push({k,kind:'nocontrast',nonZero,total:pool.length,
          side:(!D||D.nPriced===0)?'always agreed':'always disagreed'});
        continue;
      }
      rows.push({k,kind:'live',A,D,edge:(A.ev!=null&&D.ev!=null)?A.ev-D.ev:null,
        thin:Math.min(A.nPriced,D.nPriced)<30});
    }
    const order={live:0,nocontrast:1,inert:2};
    rows.sort((a,b)=>{
      if(order[a.kind]!==order[b.kind])return order[a.kind]-order[b.kind];
      return (b.edge??-9999)-(a.edge??-9999);
    });
    const regs={};
    for(const e of pool){const r=e.regimeV12||e.regime||'?';regs[r]=(regs[r]||0)+1;}
    return{
      rows,n:pool.length,
      coverage:(resolved||[]).length?100*pool.length/(resolved||[]).length:0,
      regimes:Object.entries(regs).sort((a,b)=>b[1]-a[1]),
      usingExec:pool.filter(e=>typeof e.execCostAtLock==='number'&&isFinite(e.execCostAtLock)).length,
    };
  },[resolved]);

  // ── P&L by asset ──
  const pnlByAsset=React.useMemo(()=>{
    const out={BTC:{trades:0,pnl:0,curve:[]}};
    const sorted=[...resolved].filter(e=>e.betAmt>0).sort((a,b)=>(a.id||0)-(b.id||0));
    sorted.forEach(e=>{
      const a=e.asset||'BTC';
      if(!out[a])out[a]={trades:0,pnl:0,curve:[]};
      const d=e.result==='WIN'?(e.maxPay||0)-(e.betAmt||0):-(e.betAmt||0);
      out[a].pnl+=d;out[a].trades++;
      out[a].curve.push({pnl:out[a].pnl,i:out[a].curve.length});
    });
    return out;
  },[resolved]);
  // ── Signal attribution (from ML model) ──
  const mlInfo=taraMLModel;
  const _wins=resolved.filter(e=>e.result==='WIN').length;
  const _losses=resolved.length-_wins;
  const _wrPct=resolved.length>0?Math.round((resolved.filter(e=>e.result==='WIN').length/resolved.length)*100):0;
  // Mini SVG curve renderer
  const _renderCurve=(curve,width=300,height=40)=>{
    if(curve.length<2)return null;
    const _min=Math.min(0,...curve.map(c=>c.pnl));
    const _max=Math.max(0,...curve.map(c=>c.pnl));
    const _range=Math.max(1,_max-_min);
    const _points=curve.map((c,i)=>`${(i/(curve.length-1))*width},${height-((c.pnl-_min)/_range)*height}`).join(' ');
    const _zeroY=height-((-_min/_range)*height);
    const _final=curve[curve.length-1]?.pnl||0;
    return React.createElement('svg',{viewBox:`0 0 ${width} ${height}`,className:'w-full',style:{height}},
      React.createElement('line',{x1:0,y1:_zeroY,x2:width,y2:_zeroY,stroke:'rgba(237,237,237,0.12)',strokeDasharray:'3,3'}),
      React.createElement('polyline',{points:_points,fill:'none',stroke:_final>=0?'rgb(35,185,129)':'rgb(232,69,94)',strokeWidth:1.5,strokeLinejoin:'round'})
    );
  };
  return React.createElement('div',{
    role:embedded?'region':'dialog','aria-modal':embedded?undefined:true,'aria-label':'Tara Analytics',
    className:embedded?'tara-analytics-page tara-inline-page':'tara-analytics-page fixed inset-0 z-[100] flex items-start justify-center overflow-y-auto',
    style:embedded?{}:{background:'rgba(0,0,0,0.88)',backdropFilter:'blur(6px)'},
    onClick:embedded?undefined:onClose,
  },
    React.createElement('div',{
      className:'w-full max-w-4xl mx-2 my-4 sm:my-8 rounded-xl',
      style:{background:'#101014',border:'1px solid rgba(35,185,129,0.20)',boxShadow:'0 0 60px rgba(35,185,129,0.10)'},
      onClick:e=>e.stopPropagation(),
    },
      // Header
      !embedded&&React.createElement('div',{className:'sticky top-0 z-10 flex items-center justify-between px-5 py-4 rounded-t-xl',style:{background:'#101014',borderBottom:'1px solid #24242E'}},
        React.createElement('div',{className:'flex items-baseline gap-3'},
          React.createElement('h2',{className:'font-serif text-2xl tracking-tight text-white'},'Analytics'),
          React.createElement('span',{className:'text-[10px] uppercase tracking-[0.18em] font-bold',style:{color:T2_GOLD}},`${resolved.length} resolved · ${_wrPct}% WR`),
          mlInfo&&React.createElement('span',{className:'text-[10px] uppercase tracking-[0.14em] px-2 py-0.5 rounded-lg',style:{background:'rgba(176,176,176,0.10)',color:'rgba(176,176,176,0.85)',border:'1px solid rgba(176,176,176,0.25)'}},`ML: ${(mlInfo.accuracy*100).toFixed(0)}% acc · ${mlInfo.nTrades}t`)
        ),
        React.createElement('button',{onClick:onClose,className:'w-8 h-8 rounded-lg flex items-center justify-center text-[#EDEDED]/50 hover:text-white hover:bg-[#EDEDED]/5 text-xl'},'✕')
      ),
      embedded&&React.createElement(TaraEvidenceAnalytics,{entries:taraCallLog,asset,windowType}),
      React.createElement(LearningStatus,{learning}),
      React.createElement(embedded?'details':'div',{className:embedded?'tara-v145-advanced-analytics':'p-5 space-y-6'},
        embedded&&React.createElement('summary',null,'Full analysis, learning model, heatmaps & research'),
        embedded&&React.createElement('div',{className:'tara-analytics-summary'},
          [['TARA CALL RECORD',`${_wins}–${_losses}`,`${resolved.length} settled directional calls`],
           ['CALL WIN RATE',`${_wrPct}%`,'sit-outs are not scored wins'],
           ['LIVE MODEL',mlInfo?'Active':'Off','shadow validation is shown in Learning Lab'],
           ['SIGNAL COVERAGE',`${signalEV.coverage.toFixed(0)}%`,`${signalEV.n} calls with signal evidence`]].map(([label,value,note])=>
            React.createElement('div',{key:label},React.createElement('span',null,label),React.createElement('strong',null,value),React.createElement('small',null,note))),
          React.createElement('p',null,'This is the Tara Call record and model evidence. AutoTrade order fills, actual positions, and execution P&L belong to Execution and the exchange audit.'),
        ),
        React.createElement(LockStudyPanel,{research:lockResearch,callLog:taraCallLog}),
        // ═══ WR HEATMAP ═══
        React.createElement('div',null,
          React.createElement('div',{className:'text-[10px] uppercase tracking-[0.18em] font-bold text-[#EDEDED]/50 mb-3'},'Win Rate Heatmap · UTC Hour × Day'),
          React.createElement('div',{className:'overflow-x-auto'},
            React.createElement('table',{className:'w-full text-[10px] tabular-nums',style:{minWidth:600}},
              React.createElement('thead',null,
                React.createElement('tr',null,
                  React.createElement('th',{className:'text-left text-[#EDEDED]/40 p-1'},''),
                  ...[...Array(24)].map((_,h)=>React.createElement('th',{key:h,className:'text-center text-[#EDEDED]/30 p-0.5',style:{fontSize:'8px'}},h))
                )
              ),
              React.createElement('tbody',null,
                heatmap.days.map(day=>React.createElement('tr',{key:day},
                  React.createElement('td',{className:'text-[#EDEDED]/50 font-bold pr-2 py-0.5'},day),
                  ...[...Array(24)].map((_,h)=>{
                    const cell=heatmap.grid[day][h];
                    const n=cell.W+cell.L;
                    const wr=n>0?cell.W/n:null;
                    const bg=wr==null?'transparent':wr>=0.7?'rgba(35,185,129,0.35)':wr>=0.55?'rgba(35,185,129,0.15)':wr>=0.45?'rgba(237,237,237,0.08)':wr>=0.3?'rgba(232,69,94,0.15)':'rgba(232,69,94,0.35)';
                    return React.createElement('td',{key:h,className:'text-center p-0.5',style:{background:bg,borderRadius:2},title:`${day} ${h}:00 UTC — ${n} trades, ${wr!=null?Math.round(wr*100)+'%':'no data'}`},
                      n>0?React.createElement('span',{style:{color:wr>=0.55?'rgb(35,185,129)':wr<0.45?'rgb(232,69,94)':'rgba(237,237,237,0.6)',fontSize:'9px'}},n):null
                    );
                  })
                ))
              )
            )
          )
        ),
        // ═══ SIGNAL EV (V13.4.143) ═══
        React.createElement('div',null,
          React.createElement('div',{className:'flex items-baseline justify-between mb-1'},
            React.createElement('div',{className:'text-[10px] uppercase tracking-[0.18em] font-bold text-[#EDEDED]/50'},'Signal EV · does each signal earn its keep'),
            React.createElement('span',{className:'text-[9px] tabular-nums text-[#EDEDED]/35'},
              `${signalEV.n} of ${resolved.length} trades carry signal data (${signalEV.coverage.toFixed(1)}%)`)
          ),
          React.createElement('div',{className:'text-[10px] text-[#EDEDED]/40 mb-2 leading-relaxed'},
            'Net EV per contract when a signal AGREED with the direction taken, vs when it disagreed. Win rate alone is meaningless here — the price already encodes it. A signal only carries information if EV is higher when it agreed.',
            signalEV.usingExec>0
              ?React.createElement('span',{style:{color:'rgb(35,185,129)'}},` · ${signalEV.usingExec} priced on true exec cost`)
              :React.createElement('span',{style:{color:'rgba(255,255,255,0.62)'}},' · priced on MID (optimistic by ~½ spread until exec data accrues)')
          ),
          signalEV.regimes.length>0&&React.createElement('div',{className:'text-[9px] text-[#EDEDED]/35 mb-2'},
            'regime mix: ',signalEV.regimes.map(([r,c])=>`${r} ${Math.round(100*c/signalEV.n)}%`).join(' · ')
          ),
          signalEV.rows.length===0||signalEV.n<20
            ?React.createElement('div',{className:'text-[11px] text-[#EDEDED]/45 italic p-3 rounded-lg bg-[#050508] border border-[#24242E]'},
              signalEV.n===0
                ?'No signal data yet — signalScoresAtLock stamps on each new lock. Check back after more windows resolve.'
                :`Only ${signalEV.n} trade${signalEV.n===1?'':'s'} carry signal data so far — too few to judge any signal. Needs ~20+ to show a first read, ~200+ to be trustworthy.`)
            :React.createElement('div',{className:'overflow-x-auto rounded-lg bg-[#050508] border border-[#24242E]'},
              React.createElement('table',{className:'w-full text-[10px] tabular-nums',style:{minWidth:560}},
                React.createElement('thead',null,
                  React.createElement('tr',{style:{borderBottom:'1px solid #24242E'}},
                    React.createElement('th',{className:'text-left text-[#EDEDED]/40 px-2 py-1.5 font-bold'},'signal'),
                    React.createElement('th',{className:'text-right text-[#EDEDED]/40 px-1 py-1.5'},'n·agree'),
                    React.createElement('th',{className:'text-right text-[#EDEDED]/40 px-1 py-1.5'},'WR'),
                    React.createElement('th',{className:'text-right text-[#EDEDED]/40 px-1 py-1.5'},'EV'),
                    React.createElement('th',{className:'text-right text-[#EDEDED]/40 px-1 py-1.5'},'n·dis'),
                    React.createElement('th',{className:'text-right text-[#EDEDED]/40 px-1 py-1.5'},'WR'),
                    React.createElement('th',{className:'text-right text-[#EDEDED]/40 px-1 py-1.5'},'EV'),
                    React.createElement('th',{className:'text-right text-[#EDEDED]/40 px-2 py-1.5 font-bold'},'EV edge'),
                    React.createElement('th',{className:'text-left text-[#EDEDED]/40 px-2 py-1.5'},'verdict')
                  )
                ),
                React.createElement('tbody',null,
                  signalEV.rows.map(r=>{
                    if(r.kind==='inert'){
                      return React.createElement('tr',{key:r.k,style:{borderBottom:'1px solid rgba(31,31,31,0.6)'}},
                        React.createElement('td',{className:'px-2 py-1 text-[#EDEDED]/45'},r.k),
                        React.createElement('td',{className:'px-1 py-1 text-center text-[#EDEDED]/25',colSpan:7},
                          `zero on ${r.total-r.nonZero} of ${r.total} trades`),
                        React.createElement('td',{className:'px-2 py-1 text-left',style:{color:'rgba(255,255,255,0.62)'}},'INERT — carries weight, contributes ~0')
                      );
                    }
                    if(r.kind==='nocontrast'){
                      return React.createElement('tr',{key:r.k,style:{borderBottom:'1px solid rgba(31,31,31,0.6)'}},
                        React.createElement('td',{className:'px-2 py-1 text-[#EDEDED]/45'},r.k),
                        React.createElement('td',{className:'px-1 py-1 text-center text-[#EDEDED]/25',colSpan:7},
                          `${r.side} on all ${r.nonZero} non-zero trades`),
                        React.createElement('td',{className:'px-2 py-1 text-left text-[#EDEDED]/35'},'no contrast yet — cannot compare')
                      );
                    }
                    const e=r.edge;
                    const col=e==null?'rgba(237,237,237,0.5)':e>=4?'rgb(35,185,129)':e>=1.5?'rgba(35,185,129,0.75)':e<=-4?'rgb(232,69,94)':e<=-1.5?'rgba(232,69,94,0.75)':'rgba(237,237,237,0.5)';
                    const verdict=e==null?'—'
                      :e>=4?'strong — real information'
                      :e>=1.5?'positive'
                      :e<=-4?'ANTI-PREDICTIVE — cut or invert'
                      :e<=-1.5?'negative'
                      :'noise — weight toward zero';
                    const evCell=(v)=>React.createElement('td',{className:'px-1 py-1 text-right',style:{color:v==null?'rgba(237,237,237,0.3)':v>=0?'rgba(35,185,129,0.9)':'rgba(232,69,94,0.9)'}},
                      v==null?'—':`${v>=0?'+':''}${v.toFixed(2)}`);
                    return React.createElement('tr',{key:r.k,style:{borderBottom:'1px solid rgba(31,31,31,0.6)'}},
                      React.createElement('td',{className:'px-2 py-1 text-[#EDEDED]/80 font-bold'},r.k),
                      React.createElement('td',{className:'px-1 py-1 text-right text-[#EDEDED]/50'},r.A.n),
                      React.createElement('td',{className:'px-1 py-1 text-right text-[#EDEDED]/60'},`${r.A.wr.toFixed(0)}%`),
                      evCell(r.A.ev),
                      React.createElement('td',{className:'px-1 py-1 text-right text-[#EDEDED]/50'},r.D.n),
                      React.createElement('td',{className:'px-1 py-1 text-right text-[#EDEDED]/60'},`${r.D.wr.toFixed(0)}%`),
                      evCell(r.D.ev),
                      React.createElement('td',{className:'px-2 py-1 text-right font-bold',style:{color:col}},
                        e==null?'—':`${e>=0?'+':''}${e.toFixed(2)}¢`),
                      React.createElement('td',{className:'px-2 py-1 text-left text-[10px]',style:{color:col}},
                        verdict,r.thin?React.createElement('span',{className:'text-[#EDEDED]/30'},' (thin)'):null)
                    );
                  })
                )
              )
            ),
          React.createElement('div',{className:'text-[9px] text-[#EDEDED]/30 mt-1.5 leading-relaxed'},
            'Caveats: samples flagged (thin) have <30 priced trades on one side and are unreliable. Many signals are tested at once, so some edge appears by chance. Signals zeroed by regime rules (e.g. technical/bos in CHOP) show as INERT — that is a weighting finding, not a bug.'
          )
        ),
        // ═══ P&L BY ASSET ═══
        React.createElement('div',{className:'grid grid-cols-1 sm:grid-cols-2 gap-4'},
          ['BTC'].map(asset=>{
            const d=pnlByAsset[asset];
            if(!d||d.trades===0)return React.createElement('div',{key:asset,className:'p-3 rounded-lg bg-[#050508] border border-[#24242E]'},
              React.createElement('div',{className:'text-[10px] uppercase tracking-wider font-bold',style:{color:asset==='BTC'?'rgb(247,147,26)':'rgb(98,126,234)'}},`${asset} P&L`),
              React.createElement('div',{className:'text-[11px] text-[#EDEDED]/40 italic mt-1'},'No trades with bet amounts yet')
            );
            return React.createElement('div',{key:asset,className:'p-3 rounded-lg bg-[#050508] border border-[#24242E]'},
              React.createElement('div',{className:'flex items-baseline justify-between mb-2'},
                React.createElement('span',{className:'text-[10px] uppercase tracking-wider font-bold',style:{color:asset==='BTC'?'rgb(247,147,26)':'rgb(98,126,234)'}},`${asset} · ${d.trades} trades`),
                React.createElement('span',{className:'text-sm font-bold tabular-nums',style:{color:d.pnl>=0?'rgb(35,185,129)':'rgb(232,69,94)'}},`${d.pnl>=0?'+':''}$${d.pnl.toFixed(2)}`)
              ),
              _renderCurve(d.curve)
            );
          })
        ),
        // ═══ REGIME × DIRECTION MATRIX ═══
        React.createElement('div',null,
          React.createElement('div',{className:'text-[10px] uppercase tracking-[0.18em] font-bold text-[#EDEDED]/50 mb-3'},'Regime × Direction Performance'),
          React.createElement('div',{className:'grid grid-cols-1 sm:grid-cols-2 gap-2'},
            regimeDir.map((r,i)=>{
              const wrPct=Math.round(r.wr*100);
              const color=wrPct>=65?'rgb(35,185,129)':wrPct>=50?'#23B981':'rgb(232,69,94)';
              return React.createElement('div',{key:i,className:'flex items-center justify-between p-2 rounded-lg bg-[#050508] border border-[#24242E]'},
                React.createElement('div',{className:'flex items-center gap-2 min-w-0'},
                  React.createElement('span',{className:'text-[10px] font-bold',style:{color:r.dir==='UP'?'rgb(35,185,129)':'rgb(232,69,94)'}},r.dir==='UP'?'▲':'▼'),
                  React.createElement('span',{className:'text-[11px] text-[#EDEDED]/70 truncate'},r.regime)
                ),
                React.createElement('div',{className:'flex items-center gap-3 shrink-0'},
                  React.createElement('span',{className:'text-[10px] text-[#EDEDED]/40 tabular-nums'},`${r.W}W·${r.L}L`),
                  React.createElement('span',{className:'text-[12px] font-bold tabular-nums',style:{color}},`${wrPct}%`)
                )
              );
            })
          )
        ),
        // ═══ LOSS PATTERNS ═══
        lossPatterns.length>0&&React.createElement('div',null,
          React.createElement('div',{className:'text-[10px] uppercase tracking-[0.18em] font-bold text-[#EDEDED]/50 mb-3'},'Loss Pattern Distribution'),
          React.createElement('div',{className:'space-y-1.5'},
            lossPatterns.map(([pat,{n}],i)=>{
              const _total=resolved.filter(e=>e.result==='LOSS').length;
              const pct=_total>0?(n/_total)*100:0;
              const _labels={LATE_REVERSAL:'Late reversal — was winning, flipped',MID_REVERSAL:'Mid-window fade',EARLY_PEAK:'Early peak, sustained loss',WRONG_FROM_START:'Never above water',WHALE_SPIKE:'Contrary whale spike',MACRO_SHOCK:'Macro event shock',UNKNOWN:'Unclassified'};
              return React.createElement('div',{key:i,className:'flex items-center gap-3 text-[11px]'},
                React.createElement('span',{className:'text-[#EDEDED]/50 w-32 shrink-0 truncate'},_labels[pat]||pat),
                React.createElement('div',{className:'flex-1 h-2 bg-[#050508] rounded-full overflow-hidden'},
                  React.createElement('div',{className:'h-full rounded-full transition-all',style:{width:`${pct}%`,background:'rgba(232,69,94,0.6)'}})
                ),
                React.createElement('span',{className:'text-[#EDEDED]/40 tabular-nums w-16 text-right'},`${n} (${pct.toFixed(0)}%)`)
              );
            })
          )
        ),
        // ═══ ML MODEL STATUS ═══
        mlInfo?React.createElement('div',null,
          React.createElement('div',{className:'text-[10px] uppercase tracking-[0.18em] font-bold mb-3',style:{color:'rgba(176,176,176,0.85)'}},'ML Model · Feature Importance'),
          React.createElement('div',{className:'p-3 rounded-lg bg-[#050508] border border-[#24242E]'},
            React.createElement('div',{className:'flex items-baseline justify-between mb-3'},
              React.createElement('span',{className:'text-[11px] text-[#EDEDED]/70'},`Logistic regression · ${mlInfo.nTrades} training samples · ${(mlInfo.accuracy*100).toFixed(1)}% accuracy`),
              React.createElement('span',{className:'text-[10px] text-[#EDEDED]/40'},`Trained ${_fmtTimeTz(new Date(mlInfo.trainedAt),timeFormat,{hour:'2-digit',minute:'2-digit',hour12:false})}`)
            ),
            React.createElement('div',{className:'space-y-1'},
              (mlInfo.importance||[]).slice(0,10).map((f,i)=>{
                const maxW=Math.max(...(mlInfo.importance||[]).map(x=>x.absWeight));
                const barPct=maxW>0?(f.absWeight/maxW)*100:0;
                return React.createElement('div',{key:i,className:'flex items-center gap-2 text-[11px]'},
                  React.createElement('span',{className:'text-[#EDEDED]/60 w-28 shrink-0 truncate'},f.name),
                  React.createElement('div',{className:'flex-1 h-1.5 bg-[#050508] rounded-full overflow-hidden'},
                    React.createElement('div',{className:'h-full rounded-full',style:{width:`${barPct}%`,background:f.weight>=0?'rgb(35,185,129)':'rgb(232,69,94)'}})
                  ),
                  React.createElement('span',{className:'tabular-nums w-14 text-right',style:{color:f.weight>=0?'rgb(35,185,129)':'rgb(232,69,94)',fontSize:'10px'}},`${f.weight>=0?'+':''}${f.weight.toFixed(3)}`)
                );
              })
            )
          )
        ):React.createElement('div',{className:'p-3 rounded-lg bg-[#050508] border border-[#24242E] text-[11px] text-[#EDEDED]/40'},
          'Live ML activation is off. See Learning Lab for trusted-data coverage, chronological holdout and frozen forward evidence. Legacy training-set accuracy is not proof.'
        ),
      )
    )
  );
}

function NewsExpandModal({news,macroEvents,onClose,formatAge,timeFormat}){
  React.useEffect(()=>{
    const onKey=(e)=>{if(e.key==='Escape')onClose();};
    window.addEventListener('keydown',onKey);
    return()=>window.removeEventListener('keydown',onKey);
  },[onClose]);
  return React.createElement('div',{
    className:'fixed inset-0 z-50 flex items-start justify-center p-4 sm:p-8 overflow-y-auto',
    style:{background:'rgba(0,0,0,0.75)',backdropFilter:'blur(4px)'},
    onClick:onClose,
  },
    React.createElement('div',{
      className:'w-full max-w-2xl rounded-xl my-auto',
      style:{background:'#101014',border:'1px solid rgba(35,185,129,0.20)',boxShadow:'0 0 40px rgba(35,185,129,0.15)'},
      onClick:(e)=>e.stopPropagation(),
    },
      React.createElement('div',{className:'flex items-center justify-between px-5 py-4',style:{borderBottom:'1px solid #24242E'}},
        React.createElement('div',{className:'flex items-baseline gap-3'},
          React.createElement('h2',{className:'font-serif text-2xl tracking-tight text-white'},'News Feed'),
          React.createElement('span',{className:'text-[10px] uppercase tracking-[0.18em] font-bold',style:{color:'#23B981'}},`${news.length} stories`)
        ),
        React.createElement('button',{
          onClick:onClose,
          title:'Close (Esc)',
          className:'w-7 h-7 rounded-lg flex items-center justify-center text-[#EDEDED]/50 hover:text-white hover:bg-[#EDEDED]/5 transition-colors',
        },'✕')
      ),
      React.createElement('div',{className:'px-5 py-4 max-h-[70vh] overflow-y-auto'},
        macroEvents.length>0&&React.createElement('div',{className:'mb-4'},
          React.createElement('div',{className:'text-[9px] uppercase tracking-wide text-amber-400/70 font-bold mb-2'},'Upcoming Macro'),
          React.createElement('div',{className:'space-y-1.5'},
            macroEvents.map((e,i)=>{
              const isImminent=Math.abs(e.minutesUntil)<=15;
              const isNow=e.minutesUntil<=0&&Math.abs(e.minutesUntil)<=2;
              const cls=isNow?'bg-rose-500/15 border-rose-500/40 text-rose-300':isImminent?'bg-amber-500/10 border-amber-500/30 text-amber-300':'bg-[#050508] border-[#24242E] text-[#EDEDED]/70';
              const label=e.minutesUntil>0?`in ${e.minutesUntil}m`:`${Math.abs(e.minutesUntil)}m ago`;
              return React.createElement('div',{key:i,className:'p-2 rounded-lg border text-[11px] flex justify-between '+cls},
                React.createElement('span',{className:'font-bold'},e.name),
                React.createElement('span',null,`${label} · ${e.impact}`)
              );
            })
          )
        ),
        React.createElement('div',{className:'space-y-2'},
          news.length===0?React.createElement('div',{className:'text-[11px] text-[#EDEDED]/40 italic text-center py-4'},'No news loaded'):
          news.map((n,i)=>{
            const impact=inferNewsPriceImpact(n.title);
            const impactArrow=impact==='UP'?'▲':impact==='DOWN'?'▼':'·';
            const impactColor=impact==='UP'?'#23B981':impact==='DOWN'?'#E8455E':'rgba(237,237,237,0.30)';
            const impactLabel=impact==='UP'?'Likely bullish':impact==='DOWN'?'Likely bearish':'Mixed/unclear';
            // V9.1.4: absolute timestamp for expanded view
            const _absTime=(()=>{
              try{
                const _d=new Date(n.time);
                if(isNaN(_d.getTime()))return null;
                return _fmtTimeTz(_d,timeFormat,{hour:'numeric',minute:'2-digit'});
              }catch(_){return null;}
            })();
            return React.createElement('a',{
              key:i,
              href:n.url,target:'_blank',rel:'noopener noreferrer',
              className:'block p-3 rounded-lg hover:bg-[#161616] transition-colors',
              style:{background:'#0E0E12',border:'1px solid #24242E'},
            },
              React.createElement('div',{className:'flex items-start gap-3'},
                React.createElement('div',{className:'shrink-0 w-8 text-center',title:impactLabel},
                  React.createElement('div',{className:'text-base font-bold',style:{color:impactColor}},impactArrow),
                  React.createElement('div',{className:'text-[8px] uppercase tracking-wider',style:{color:impactColor,opacity:0.7}},impact||'mixed')
                ),
                React.createElement('div',{className:'flex-1 min-w-0'},
                  React.createElement('div',{className:'text-[12px] text-[#EDEDED]/85 leading-snug mb-1'},n.title),
                  React.createElement('div',{className:'flex items-center gap-2 text-[10px] flex-wrap'},
                    React.createElement('span',{className:'text-[#EDEDED]/45 uppercase'},n.source),
                    React.createElement('span',{className:'text-[#EDEDED]/30'},`· ${formatAge(n.time)}`),
                    _absTime&&React.createElement('span',{className:'text-[#EDEDED]/30 tabular-nums'},`· ${_absTime}`),
                    Array.isArray(n.categories)&&n.categories.length>0&&React.createElement('span',{className:'text-[#EDEDED]/35 truncate'},`· ${n.categories.slice(0,2).join(', ')}`)
                  )
                )
              )
            );
          })
        )
      )
    )
  );
}

// ── v13.4.153: LiveFeedsCard — flow metrics + whale prints.
//   Lifted out of RightPanel so it can sit under Tara's Call. Same markup, same
//   data; it reads tapeRef/bloomberg/whaleLog exactly as before, so moving it
//   adds no new fetches, sockets or cloud reads — it is a relocation, not a
//   second copy of the feed.
function LiveFeedsCard({tapeRef,bloomberg,whaleLog,timeFormat}){
  const derivativesFresh=freshFeedStatus(bloomberg?.lastUpdate,30000)==='fresh';
  const fieldFresh=key=>derivativesFresh&&bloomberg?.sourceHealth?.[key]?.status==='fresh';
  const tape=tapeRef?.current||{};
  const cb=tape.coinbase||{buys:0,sells:0};
  const bf=tape.binanceFutures||{buys:0,sells:0};
  const by=tape.bybit||{buys:0,sells:0};
  const totalBuys=cb.buys+bf.buys+by.buys;
  const totalSells=cb.sells+bf.sells+by.sells;
  const total=totalBuys+totalSells;
  const buyPct=total>0?(totalBuys/total)*100:50;
  const oi=bloomberg?.oiChange5m||0;
  const fr=(bloomberg?.fundingRate||0)*100;
  const ls=bloomberg?.longShortRatio||1;
  return(
    <div>
      {/* V13.4.299: gold section label -> the mockup's neutral .k cap. Gold is
          reserved for caution; a feed heading is neither a caution nor an
          outcome. */}
      <div className={'text-[9px] uppercase tracking-[0.15em] font-bold mb-2 text-[#EDEDED]/30'}>Live Feeds</div>
      {/* 2-up at every width. A 4-up variant was tried and measured: this column
          is ~420-470px on desktop, so four tiles land at 79px at 1280 and clip
          the "Funding" label. Two tiles give ~205px and read cleanly everywhere,
          including the full-width mobile card. */}
      {/* V13.4.299: the four tiles were separate rounded boxes floating on the
          panel. The mockup divides them with hairlines instead -- a 1px grid gap
          over a #16161c ground, so the divider IS the gap and the cells read as
          one instrument rather than four chips. Only buy flow keeps green: OI,
          funding and long/short were colouring every reading green-or-red as if
          each were a won or lost trade, which is the accent inflation the brief
          set out to remove. They are neutral now, and directional sign is
          carried by the +/- that is already in the number. */}
      <div className="grid grid-cols-2 gap-px mb-2 rounded-[10px] overflow-hidden" style={{background:'#16161c'}}>
        <div className={'px-3 py-2.5 min-w-0'} style={{background:'#0A0A0E'}}>
          <div className={'text-[9px] uppercase tracking-[0.15em] text-[#EDEDED]/30 font-bold mb-1'}>Buy Flow</div>
          <div className="text-[15px] tabular-nums" style={{color:'#23B981'}}>{total>0?buyPct.toFixed(0)+'%':'—'}</div>
        </div>
        <div className={'px-3 py-2.5 min-w-0'} style={{background:'#0A0A0E'}}>
          <div className={'text-[9px] uppercase tracking-[0.15em] text-[#EDEDED]/30 font-bold mb-1'}>OI 5m</div>
          <div className={'text-[15px] tabular-nums'}>{fieldFresh('openInterest')?(bloomberg.oiChange5mReady?`${oi>=0?'+':''}${oi.toFixed(2)}%`:'Warming up'):'—'}</div>
        </div>
        <div className={'px-3 py-2.5 min-w-0'} style={{background:'#0A0A0E'}}>
          <div className={'text-[9px] uppercase tracking-[0.15em] text-[#EDEDED]/30 font-bold mb-1'}>Funding</div>
          <div className={'text-[15px] tabular-nums'}>{fieldFresh('funding')?`${fr>=0?'+':''}${fr.toFixed(4)}%`:'—'}</div>
        </div>
        <div className={'px-3 py-2.5 min-w-0'} style={{background:'#0A0A0E'}}>
          <div className={'text-[9px] uppercase tracking-[0.15em] text-[#EDEDED]/30 font-bold mb-1'}>Long/short</div>
          <div className={'text-[15px] tabular-nums'}>{fieldFresh('accountRatio')?ls.toFixed(2):'—'}</div>
        </div>
      </div>
      <div className={'text-[9px] uppercase tracking-[0.15em] text-[#EDEDED]/30 font-bold mb-1'}>Recent Whales ($100K+)</div>
      <div className="max-h-28 overflow-y-auto space-y-0.5 text-[10px] font-mono">
        {whaleLog.length===0?(
          <div className={'text-[#EDEDED]/30 italic'}>No prints yet</div>
        ):whaleLog.slice(0,8).map((w,i)=>{
          const sideCls=w.side==='BUY'?'text-emerald-400':'text-rose-400';
          const t=new Date(w.time);
          const ts=_fmtTimeTz(t,timeFormat,{hour12:false,hour:'2-digit',minute:'2-digit',second:'2-digit'});
          return(
            <div key={i} className="flex justify-between gap-1.5">
              <span className={'text-[#EDEDED]/40 shrink-0'}>{ts}</span>
              <span className={sideCls+' font-bold shrink-0'}>{w.side}</span>
              <span className="text-white shrink-0">${(w.usd/1000).toFixed(0)}K</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── V111: RightPanel (col 3) ── V13.4.302: Engine Log removed, see below.
function RightPanel({analysis,tapeRef,whaleLog,bloomberg,currentPrice,mobileTab,taraCallLog,currentAsset,timeFormat,pushToast,
                     taraCall,lockedSnapshotDir,lockedSnapshot,kalshiYesPrice,timeState,windowType,userPosition,onHourlyLock}){
  // V9.1.1: full-schedule popup state
  const[scheduleModalOpen,setScheduleModalOpen]=React.useState(false);
  // The coach is intentionally placed beside the signals by users who enable
  // taraCoachLeft. In that state this legacy wrapper was rendering as an empty
  // second context column, creating a dead slab of page with only its stamp.
  // Schedule and hourly data are already rendered below in their dedicated row.
  const _coachLeft=(()=>{try{return localStorage.getItem('taraCoachLeft')==='1';}catch(_e){return false;}})();
  if(_coachLeft)return null;
  // v13.4.153: tape/bloomberg/whale derivations moved with the Live Feeds card.

  return(
    <div className={'bg-[#0A0A0E] p-3 sm:p-4 rounded-[10px] border border-[#1B1B22] flex flex-col gap-3 relative min-w-0 '}>
      <T2Stamp code="SCR · 008"/>
      {/* V13.4.148: Trade Coach + Hourly ladder relocated here from the middle column
          so everything read during a live window sits in one place, no scrolling.
          Hidden when localStorage 'taraCoachLeft'='1' (they render in ProjectionsCard). */}
      {!_coachLeft&&(
        <div className="shrink-0">
          {/* V13.4.295: HourlyLadderPanel moved out of here into its own bottom
              row alongside the new Schedule card, matching the mockup (Hourly +
              Schedule as a 2-column row below the main grid, not stacked inside
              column 3). */}
          <TradeCoachCall taraCall={taraCall} analysis={analysis} lockedSnapshotDir={lockedSnapshotDir} lockedSnapshot={lockedSnapshot} kalshiYesPrice={kalshiYesPrice} timeState={timeState} windowType={windowType} userPosition={userPosition}/>
        </div>
      )}
      {/* V13.4.303 REMOVED: Score Breakdown (per-signal contribution bars +
          FGT/Structural/Grand Trend/Trend Channel/Total rows). Direct request
          ("score breakdown can go off too. dont care much about it"). Like
          Engine Log, it was never in the approved mockup's column map -- kept
          from the pre-rebuild layout, not something the design called for. */}
      {/* V13.4.302 REMOVED: Engine Log (the raw [TAG] reasoning-string dump).
          It was never in the approved mockup's column map to begin with (kept
          from the pre-rebuild layout as "not in the mockup as such but not
          deleted" per project_tara_dashboard_mockup_rebuild) -- a dozen
          rainbow-coloured debug tags with no equivalent panel in the design.
          `reasoning` is now unused in this component; left as a prop so the
          parent call site doesn't need touching, exactly like removing a
          feature elsewhere in this file without chasing every caller. */}
      {/* v13.4.153: News & Live Feeds moved out of this panel and down under
          Tara's Call (the projections column). This panel keeps the engine
          internals — coach, score breakdown, reasoning log. */}
      {/* V9.2.0: Schedule on mobile only — desktop schedule moved to projections column */}
      {taraCallLog&&(
        <div className="shrink-0 pt-3 lg:hidden" style={{borderTop:'1px solid '+T2_GOLD_GLOW}}>
          {/* V9.8.18: day-aware banner on mobile schedule too. Local useMemo since RightPanel
              already receives taraCallLog; cheap recompute on log change is fine here. */}
          <DayAwareScheduleHeader dayContext={(()=>{
            try{
              const _filtered=(taraCallLog||[]).filter(t=>t&&(t.asset||'BTC')===currentAsset);
              return buildDayContext(buildDayHourPerf(_filtered),new Date());
            }catch(_){return null;}
          })()}/>
          <TradeScheduleStrip
            taraCallLog={taraCallLog}
            currentAsset={currentAsset}
            timeFormat={timeFormat}
            onOpenFullSchedule={()=>setScheduleModalOpen(true)}
          />
        </div>
      )}
      {/* v13.4.153: Live Feeds relocated — see LiveFeedsCard, rendered under
          Tara's Call in the projections column. */}
      {/* V9.1.1: Full-schedule modal — opens via ⊕ on schedule strip header */}
      {scheduleModalOpen&&taraCallLog&&(
        <TradeScheduleModal
          taraCallLog={taraCallLog}
          currentAsset={currentAsset}
          timeFormat={timeFormat}
          onClose={()=>setScheduleModalOpen(false)}
        />
      )}
    </div>
  );
}

// ── V111: ChartBottomCard - TradingView at bottom, full width ──
function ChartBottomCard({resolution,setResolution,asset,priceSource}){
  return <section className="tara-chart-card" aria-label="TradingView live chart">
    <div className="tara-section-heading"><span>Market / TradingView</span><small>Live chart · {asset}</small></div>
    <TradingViewChart resolution={resolution} onResolutionChange={setResolution} asset={asset} priceSource={priceSource}/>
  </section>;
}


// ══════════════════════════════════════════════════════════════════════════
// SportsView — the sports prediction record, alongside Tara's BTC work.
//
// Data comes from /sports.json, a static file written by the sports-model repo
// (src/export_tara.py). Static rather than live because the models run locally
// and Vercel cannot reach that machine; the trade-off is staleness, which is
// why `generated` is always shown rather than hidden.
//
// Two things this view refuses to do, both learned the hard way:
//
//   1. It never shows HOME/AWAY as the pick. A row reading
//      "Detroit @ Seattle ... HOME" was once acted on as a Detroit bet. The
//      side being backed is always the team name, first and in capitals.
//   2. It never treats a large model-vs-market gap as opportunity. Measured
//      over the backtest, big disagreement is the model being wrong (1.0373
//      log loss vs the market's 0.9641), so those rows are SKIP, not value.
// ══════════════════════════════════════════════════════════════════════════
const SPORT_EMOJI={soccer:'⚽',baseball:'⚾',basketball:'🏀',nfl:'🏈',cricket:'🏏',motorsports:'🏎️'};

// Reuse the app's existing semantic colours rather than inventing new ones —
// the same green/red Tara already uses for UP/DOWN and WIN/LOSS, and T2_GOLD
// for anything provisional. A second green in the same product reads as a
// different meaning.
const SPORTS_GREEN='rgb(35,185,129)';
const SPORTS_RED='rgb(232,69,94)';

// Module-level cache for the ~41KB static payload. Survives closing and
// reopening the view; 5 minutes is well under how often export_tara.py runs,
// and a hard reload clears it anyway.
let _sportsCache={data:null,at:0};
const _SPORTS_TTL_MS=5*60*1000;

// ALIGNED is deliberately NOT green. Green reads as "go", and this label means
// the model agrees with the market — which is the absence of an edge, not the
// presence of one. The live record has the model significantly WORSE than the
// market, so colouring agreement as a buy signal was the least accurate thing
// on the board. Neutral blue: informative, not an instruction.
// The chip says the thing itself rather than a band name. ALIGNED / WIDE / OFF
// were three words you had to learn in order to read one number — how far the
// model sits from the market. "off by 13" says what "OFF" meant and also says
// how far, which is the part that actually varies between rows.
//
// It is also NOT green. Green reads as "go", and the closest band means the
// model agrees with the market, which is the absence of an edge. The live
// record has the model measurably worse than the market.
function SportsAdviceChip({advice,label}){
  const a=String(advice||'');
  const key=a.split(' ')[0];
  const text=label||a;
  const col=key==='ALIGNED'?'#7CA6E8':key==='WIDE'?T2_GOLD:key==='STARTED'?'#8A8A84':'#EDEDED';
  const op=key==='ALIGNED'||key==='WIDE'||key==='STARTED'?1:0.34;
  const bg=key==='ALIGNED'?'rgba(124,166,232,0.12)':key==='WIDE'?T2_GOLD_GLOW:'transparent';
  return(<span className="inline-block text-[9px] font-bold uppercase tracking-[0.06em] px-1.5 py-0.5 rounded border whitespace-nowrap shrink-0" style={{color:col,borderColor:col,background:bg,opacity:op}}>{key==='STARTED'?'started':text}</span>);
}

// A pre-game model probability compared against an in-play market price
// invents enormous edges — the scoreboard talking, not a disagreement. The
// export cannot know this, because whether a game has started depends on when
// you LOOK, not on when the file was written. So the client decides, from its
// own clock, on every tick.
function sportsHasStarted(start,nowMs){
  if(!start)return false;
  const s=String(start);
  if(!/[+Z]/.test(s.slice(10)))return false;   // date-only: no clock to compare
  const t=Date.parse(s);
  return !isNaN(t)&&t<=nowMs;
}

// The in-game panel. `grid` is keyed "inning|lead" -> [homeWinRate, n].
function SportsInPlay({grid}){
  const[inn,setInn]=React.useState(9);
  const[lead,setLead]=React.useState(2);
  const g=grid&&grid[inn+'|'+lead];
  let body;
  if(!g){
    body=(<div className="text-[#EDEDED]/40">Not enough historical games at that exact state to quote a rate. Reporting nothing rather than a number built on a handful of games.</div>);
  }else if(lead===0){
    body=(<div><span className="text-white font-bold">Level.</span> Home win rate here is {Math.round(g[0]*100)}% across {g[1].toLocaleString()} games.</div>);
  }else{
    const p=lead>0?g[0]:1-g[0];
    const blown=1/Math.max(1-p,1e-9);
    const risk=p/Math.max(1-p,1e-9);
    body=(<div>
      <div className="text-white"><span className="font-bold">{lead>0?'Home':'Away'} leading by {Math.abs(lead)} entering inning {inn} wins {Math.round(p*100)}%</span> of {g[1].toLocaleString()} such games — blown roughly 1 in {blown.toFixed(0)}.</div>
      <div className="mt-2 text-[#EDEDED]/50 text-[11px] leading-relaxed">At a price of {Math.round(p*100)}c you are risking <span style={{color:T2_GOLD}}>{risk.toFixed(1)} to win 1</span> on the remaining move. Price is your own expected value either way, so holding and selling have the same EV — what changes is the shape. Kalshi's fee is 0.07·p·(1−p), so the exit is cheapest exactly when the position is already won.</div>
    </div>);
  }
  const sel='bg-[#0F0F0F] border border-[#2A2A34] rounded px-2 py-1 text-[11px] text-white';
  return(
    <div className="bg-[#101010] border border-[#232323] rounded-lg p-3 mt-1.5">
      <div className="flex flex-wrap items-center gap-2 mb-2">
        <span className="text-[9px] uppercase tracking-[0.14em] text-[#EDEDED]/40 font-bold">Entering inning</span>
        <select className={sel} value={inn} onChange={e=>setInn(+e.target.value)}>
          {[1,2,3,4,5,6,7,8,9].map(i=><option key={i} value={i}>{i}</option>)}
        </select>
        <span className="text-[9px] uppercase tracking-[0.14em] text-[#EDEDED]/40 font-bold">Score</span>
        <select className={sel} value={lead} onChange={e=>setLead(+e.target.value)}>
          {[-6,-5,-4,-3,-2,-1,0,1,2,3,4,5,6].map(l=><option key={l} value={l}>{l>0?'home +'+l:l<0?'away +'+(-l):'level'}</option>)}
        </select>
      </div>
      <div className="text-[11.5px] leading-relaxed">{body}</div>
    </div>
  );
}

// A start with no timezone is a DATE, not an instant. Converting it would land
// it on the previous evening — that bug shipped twice, so the parse lives in
// one place and every caller goes through it.
function sportsParseStart(start){
  if(!start)return{d:null,dateOnly:true};
  const s=String(start);
  const dateOnly=!/[+Z]/.test(s.slice(10));
  const d=new Date(dateOnly?s.slice(0,10)+'T12:00:00Z':s);
  return isNaN(d)?{d:null,dateOnly:true}:{d,dateOnly};
}
// 'YYYY-MM-DD' in Eastern — the bucket key for the date grouping.
function sportsDateKey(start){
  const{d}=sportsParseStart(start);
  if(!d)return null;
  const p=new Intl.DateTimeFormat('en-CA',{timeZone:'America/New_York',year:'numeric',month:'2-digit',day:'2-digit'}).format(d);
  return p;
}
// For the compact "what is coming up" lane, a date-only fixture still has
// enough information to exclude dates that are already in the past. Same-day
// date-only rows remain actionable because there is no reliable clock to
// declare them started.
function sportsIsFuture(start,nowMs=Date.now()){
  if(!start)return false;
  const{d,dateOnly}=sportsParseStart(start);
  if(!d)return false;
  if(!dateOnly)return d.getTime()>nowMs;
  const key=sportsDateKey(start);
  const today=sportsDateKey(new Date(nowMs).toISOString());
  return Boolean(key&&today&&key>=today);
}
function sportsDateLabel(key){
  if(!key)return'Date TBC';
  const d=new Date(key+'T12:00:00Z');
  const today=sportsDateKey(new Date().toISOString());
  const tmr=sportsDateKey(new Date(Date.now()+86400000).toISOString());
  const base=d.toLocaleString('en-US',{weekday:'short',day:'2-digit',month:'short',timeZone:'UTC'}).replace(',','');
  return key===today?base+' · Today':key===tmr?base+' · Tomorrow':base;
}

function SportsRow({r,grid,showResult}){
  const[open,setOpen]=React.useState(false);
  const isBall=r.sport==='baseball';
  const pct=v=>(v===null||v===undefined)?'—':Math.round(v*100)+'%';
  // Rows sit inside a date group, so only the clock is needed here.
  const when=(()=>{
    const{d,dateOnly}=sportsParseStart(r.start);
    if(!d)return'—';
    if(dateOnly)return'all day';
    return d.toLocaleString('en-US',{hour:'numeric',minute:'2-digit',timeZone:'America/New_York'});
  })();
  const tierCol=r.tier==='HIGH'?SPORTS_GREEN:r.tier==='MEDIUM'?T2_GOLD:'#EDEDED';
  // A search link, not a real broadcast lookup — there is no single free API
  // that carries TV/streaming info across soccer, baseball, NFL and whatever
  // sport is added next, and a wrong hard-coded network is worse than a
  // search. This works for any sport with zero new data pipeline: the match
  // text plus league is already enough for a search engine to answer "where
  // is this on". Only shown for upcoming/live picks — the question does not
  // apply to a settled row in the Record tab.
  const watchUrl=(()=>{
    const ev=String(r.event||r.match||'').replace(/\s*\(\d{4}-\d{2}-\d{2}\)\s*$/,'');
    if(!ev)return null;
    const q=`${ev} ${r.league||''} where to watch live stream`.replace(/\s+/g,' ').trim();
    return 'https://www.google.com/search?q='+encodeURIComponent(q);
  })();
  return(
    <div className="border-b border-[#1C1C1C] last:border-b-0 py-2">
      <div className="flex items-start gap-2 flex-wrap">
        <div className="text-[10px] text-[#EDEDED]/35 w-[58px] shrink-0 pt-0.5" style={T2_MONO_STYLE}>{when}</div>
        {showResult
          ?<span className="text-[10px] font-bold uppercase tracking-wider shrink-0 pt-0.5" style={{color:r.won?SPORTS_GREEN:SPORTS_RED}}>{r.won?'WON':'LOST'}</span>
          :<SportsAdviceChip advice={r.advice} label={r.label}/>}
        {/* The tier word (HIGH/MEDIUM/LOW) was a second name for the same gap
            the chip now states in points. Two labels for one number is exactly
            what made this hard to read. */}
        <div className="flex-1 min-w-[180px]">
          <div className="text-[13px] font-bold text-white tracking-tight uppercase">
            {r.pick}
            {/* A fixture in the past that is still unsettled is waiting on a
                results feed, not on the game. Boca v Estudiantes sat in Open
                for two days looking like a live call because
                football-data.co.uk had not published that date yet. */}
            {/* Tracked is a property of a row, not a separate tab. This badge
                is what replaced the old Open list. */}
            {r.tracked&&!showResult&&(
              <span className="ml-2 text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded border align-middle normal-case" style={{color:SPORTS_GREEN,borderColor:SPORTS_GREEN,background:'rgba(35,185,129,0.10)'}}
                title={'Committed to the record'+(r.ledger_id?' as #'+r.ledger_id:'')+'. Counts win or lose.'}>
                tracked{r.ledger_id?' #'+r.ledger_id:''}
              </span>
            )}
            {r.stale_days>0&&(
              <span className="ml-2 text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded border align-middle normal-case" style={{color:T2_GOLD,borderColor:T2_GOLD_BORDER,background:T2_GOLD_GLOW}}
                title="The fixture is in the past. This is waiting on the results feed, not on the game.">
                awaiting result · {r.stale_days}d
              </span>
            )}
          </div>
          {/* The ledger appends "(YYYY-MM-DD)" to events; the date column
              already carries it, so drop it rather than print it twice. */}
          <div className="text-[10.5px] text-[#EDEDED]/40 flex items-center gap-1.5 flex-wrap">
            <span>{String(r.event||r.match||'').replace(/\s*\(\d{4}-\d{2}-\d{2}\)\s*$/,'')}</span>
            {watchUrl&&!showResult&&(
              <a href={watchUrl} target="_blank" rel="noopener noreferrer"
                onClick={ev=>ev.stopPropagation()}
                className="text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded border normal-case hover:opacity-80"
                style={{color:'#7CA6E8',borderColor:'rgba(124,166,232,0.35)',background:'rgba(124,166,232,0.08)'}}
                title="Search for where to watch this">
                📺 watch
              </a>
            )}
          </div>
        </div>
        <div className="text-right shrink-0" style={T2_MONO_STYLE}>
          <div className="text-[12px] text-white">{pct(r.model)}</div>
          {/* A tracked row shows the price AT LOCK, because that is what the
              record is scored against. When the live price has moved away from
              it, say so rather than silently swapping one for the other. */}
          <div className="text-[10px] text-[#EDEDED]/35">mkt {pct(r.mkt)}{r.tracked?' @ lock':''}</div>
          {r.tracked&&r.mkt_now!=null&&r.mkt!=null&&Math.abs(r.mkt_now-r.mkt)>=0.03&&(
            <div className="text-[10px]" style={{color:'#7CA6E8'}}>now {pct(r.mkt_now)}</div>
          )}
        </div>
      </div>
      {/* Every OTHER bet on this same game. The winner call is the headline
          above; totals and anything added later sit here rather than in a
          separate section further down the page, because a person watching
          one match wants every call they hold on it in one place, under its
          own kick-off time. `lane` keeps the distinction that matters: the
          record is scored, paper is not. */}
      {Array.isArray(r.calls)&&r.calls.length>1&&(
        <div className="mt-1.5 pl-2 flex flex-col gap-1" style={{borderLeft:'2px solid #232323'}}>
          {r.calls.slice(1).map((c,ci)=>(
            <div key={ci} className="flex items-baseline gap-x-2 flex-wrap">
              <span className="text-[11.5px] font-bold text-white/90 tracking-tight">{c.bet}</span>
              {c.lane==='paper'&&(
                <span className="text-[8.5px] font-bold uppercase tracking-wider px-1 py-0.5 rounded border normal-case"
                  style={{color:T2_GOLD,borderColor:T2_GOLD_BORDER,background:T2_GOLD_GLOW}}
                  title="Paper only — not counted in the record. The model beats a base rate on this market but not the closing line.">
                  paper
                </span>
              )}
              <span className="ml-auto text-[11px]" style={T2_MONO_STYLE}>
                <span className="text-white">{pct(c.model)}</span>
                <span className="text-[#EDEDED]/35"> · mkt {pct(c.mkt)}</span>
              </span>
            </div>
          ))}
        </div>
      )}
      {isBall&&grid&&(
        <div className="mt-1">
          <button onClick={()=>setOpen(o=>!o)} className="text-[10px] hover:underline" style={{color:T2_GOLD}}>
            {open?'▾':'▸'} live situation — what is this lead worth?
          </button>
          {open&&<SportsInPlay grid={grid}/>}
        </div>
      )}
    </div>
  );
}

// V13.4.217: WEATHER LANE — a second market where the underlying cannot be pushed around.
//
// WHY THIS EXISTS. The Window Clock measured what BTC 15m actually is: median
// distance to the strike 5.6bps, 72% of windows finishing inside 10bps, and
// 6am ET pinning 84% of the time at -16c/contract. That is a market that
// resolves near a coin flip by construction, however good the read is.
// Daily-high temperature is the opposite shape: the settlement number is a
// physical observation nobody can trade against you, it is forecast publicly
// and continuously, and — the part that actually matters — it is PARTIALLY
// KNOWN BEFORE EXPIRY. Once the day's observed maximum is 80.1F, every bucket
// that tops out below 80 is finished. That is arithmetic, not prediction.
//
// THE HONEST CAVEATS, up front and repeated in the UI:
//   1. BASIS RISK. Kalshi settles this series on THE WEATHER COMPANY. This
//      panel reads NWS (api.weather.gov), which is free and open but is a
//      DIFFERENT source. They track each other closely and can still disagree
//      by a degree at the boundary — which is exactly where the money is. So
//      "dead" here means "dead on the NWS proxy", not settled fact.
//   2. NO TRACK RECORD. BTC has 1,000 logged calls behind it. This has zero.
//      Every number below is a model output, not a measured edge. Paper first.
//   3. THE MODEL IS DELIBERATELY SIMPLE. Final high ~ Normal(NWS forecast,
//      sigma), truncated below the observed running max, sigma shrinking as
//      the day approaches peak heating. No ensemble, no bias correction.
// V13.4.218: every row below was checked against BOTH APIs before it shipped --
//   the Kalshi series must return open markets, the NWS station must return real
//   temperatures, and the lat/lon must resolve to a forecast for the right city.
//   The first cut of this list guessed and got two of three wrong: 'KXHIGHOU' and
//   'KXHIGHSEA' both returned zero markets, so Houston and Seattle were dead pills.
//   Kalshi lists 47 daily-high series; the correct Houston ticker is KXHIGHHOU and
//   Seattle is KXHIGHTSEA (some cities carry a T infix, some do not -- it is not
//   guessable, so it is verified).
//
//   Liquidity noted from the live book at 2026-08-20 as a rough ordering; it moves,
//   which is why the panel also hides any city with no open market rather than
//   trusting this list to stay true.
const _WX_CITIES=[
  {id:'LA',  series:'KXHIGHLAX',  label:'Los Angeles', station:'KLAX', lat:33.9425, lon:-118.4081, tz:'America/Los_Angeles'}, // oi ~348k, deepest by far
  {id:'CHI', series:'KXHIGHCHI',  label:'Chicago',     station:'KMDW', lat:41.7868, lon:-87.7522,  tz:'America/Chicago'},     // oi ~51k
  {id:'AUS', series:'KXHIGHAUS',  label:'Austin',      station:'KAUS', lat:30.1975, lon:-97.6664,  tz:'America/Chicago'},     // oi ~49k
  {id:'SEA', series:'KXHIGHTSEA', label:'Seattle',     station:'KSEA', lat:47.4444, lon:-122.3138, tz:'America/Los_Angeles'}, // oi ~46k
  {id:'NYC', series:'KXHIGHNY',   label:'New York',    station:'KNYC', lat:40.7831, lon:-73.9662,  tz:'America/New_York'},    // oi ~23k, Central Park
  {id:'DEN', series:'KXHIGHDEN',  label:'Denver',      station:'KDEN', lat:39.8561, lon:-104.6737, tz:'America/Denver'},      // oi ~22k
  {id:'MIA', series:'KXHIGHMIA',  label:'Miami',       station:'KMIA', lat:25.7932, lon:-80.2906,  tz:'America/New_York'},    // oi ~16k
  {id:'PHL', series:'KXHIGHPHIL', label:'Philadelphia',station:'KPHL', lat:39.8719, lon:-75.2411,  tz:'America/New_York'},    // oi ~10k
  {id:'HOU', series:'KXHIGHHOU',  label:'Houston',     station:'KIAH', lat:29.9902, lon:-95.3368,  tz:'America/Chicago'},     // opens later in the day
  {id:'BOS', series:'KXHIGHTBOS', label:'Boston',      station:'KBOS', lat:42.3606, lon:-71.0097,  tz:'America/New_York'},    // oi ~2.6k, thin
  {id:'DC',  series:'KXHIGHTDC',  label:'Washington',  station:'KDCA', lat:38.8512, lon:-77.0402,  tz:'America/New_York'},    // oi ~2.1k, thin
  // V13.4.223: nine more US cities, each one SCREENED before it was added rather
  //   than guessed. The backtest said this lane only fires ~2.3 times a week
  //   across ten cities, so coverage is the biggest lever on it — and three of
  //   these (SFO 39k, PHX 37k, LV 35k open interest) are deeper books than almost
  //   anything already on the list.
  //
  //   The screen: replay each city's settled days against its candidate station
  //   and measure how often the station's rounded daily max lands inside the
  //   bucket Kalshi actually settled. What matters is not exact agreement but the
  //   WORST HIGH reading -- a dead call only fails when the station reads HIGHER
  //   than what settles. Every city below is +1 at worst, so the 2°F margin still
  //   has a full degree of headroom. Phoenix looks poor on exact agreement (1/6)
  //   and is fine: it reads systematically LOW, which is the safe direction.
  //
  //   Dallas is why this is measured and not guessed. The ticker is KXHIGHTDAL,
  //   so KDAL (Love Field) is the obvious read -- it scored 2/6 with a -2 miss,
  //   while KDFW scored 5/6. Kalshi settles Dallas on DFW. Third time this
  //   session that guessing was wrong and measuring was right.
  {id:'SFO', series:'KXHIGHTSFO', label:'San Francisco', station:'KSFO', lat:37.6196, lon:-122.3656, tz:'America/Los_Angeles'}, // oi ~39k · 4/6 exact, worst-high +1
  {id:'PHX', series:'KXHIGHTPHX', label:'Phoenix',       station:'KPHX', lat:33.4278, lon:-112.0035, tz:'America/Phoenix'},     // oi ~37k · reads low, safe direction
  {id:'LV',  series:'KXHIGHTLV',  label:'Las Vegas',     station:'KLAS', lat:36.0719, lon:-115.1634, tz:'America/Los_Angeles'}, // oi ~35k · 6/6 exact
  {id:'DAL', series:'KXHIGHTDAL', label:'Dallas',        station:'KDFW', lat:32.8974, lon:-97.0220,  tz:'America/Chicago'},     // oi ~4.3k · KDFW, NOT KDAL
  {id:'OKC', series:'KXHIGHTOKC', label:'Oklahoma City', station:'KOKC', lat:35.3886, lon:-97.6003,  tz:'America/Chicago'},     // oi ~3.6k · 5/6 exact
  {id:'ATL', series:'KXHIGHTATL', label:'Atlanta',       station:'KATL', lat:33.6403, lon:-84.4269,  tz:'America/New_York'},    // oi ~3.0k · 4/6 exact
  {id:'MSY', series:'KXHIGHTNOLA',label:'New Orleans',   station:'KMSY', lat:29.9928, lon:-90.2508,  tz:'America/Chicago'},     // oi ~2.9k · 4/6 exact
  {id:'SAT', series:'KXHIGHTSATX',label:'San Antonio',   station:'KSAT', lat:29.5328, lon:-98.4636,  tz:'America/Chicago'},     // oi ~1.8k · 5/6 exact
  {id:'MSP', series:'KXHIGHTMIN', label:'Minneapolis',   station:'KMSP', lat:44.8831, lon:-93.2289,  tz:'America/Chicago'},     // oi ~1.6k · 4/6 exact
  // DELIBERATELY LEFT OUT:
  //   KXHIGHTSAN (San Diego) is trading but has zero settled days, so its station
  //     cannot be screened yet. Adding an unscreened city would undo the point of
  //     screening. Revisit once it has a week of history.
  //   The European and Toronto series (EGLL/EDDB/EDDF/EBBR/EHAM/CYYZ) have no open
  //     markets at all, and NWS does not cover them -- they would need a second
  //     observation source with its own basis risk to measure.
];
// Houston's KIAH pairing has never been screened: KXHIGHHOU has had no settled
//   markets in the window, so there was nothing to measure it against. It sits in
//   the list because the ladder does open sometimes, but treat its picks as
//   unproven until it trades a week.
const _WX_UNSCREENED=new Set(['HOU']);
// Normal CDF (Abramowitz & Stegun 7.1.26) — plenty accurate for a 1dp temperature.
const _wxNormCdf=(z)=>{
  const t=1/(1+0.2316419*Math.abs(z));
  const d=0.3989422804014327*Math.exp(-z*z/2);
  let p=d*t*(0.319381530+t*(-0.356563782+t*(1.781477937+t*(-1.821255978+t*1.330274429))));
  return z>0?1-p:p;
};
// "86° or above" | "77° or below" | "84° to 85°"  ->  {lo,hi}
const _wxParseBucket=(s)=>{
  const txt=String(s||'').replace(/[^0-9a-zA-Z. ]/g,' ').replace(/\s+/g,' ').trim().toLowerCase();
  let m=txt.match(/^([\d.]+)\s*(?:or)?\s*above/);        if(m)return{lo:+m[1],hi:Infinity};
  m=txt.match(/^([\d.]+)\s*(?:or)?\s*below/);            if(m)return{lo:-Infinity,hi:+m[1]};
  m=txt.match(/^([\d.]+)\s*to\s*([\d.]+)/);              if(m)return{lo:+m[1],hi:+m[2]};
  return null;
};
const _wxLocalParts=(tz,d)=>{
  const f=new Intl.DateTimeFormat('en-CA',{timeZone:tz,year:'numeric',month:'2-digit',day:'2-digit',hour:'2-digit',minute:'2-digit',hour12:false});
  const p={}; f.formatToParts(d).forEach(x=>{p[x.type]=x.value;});
  return{date:`${p.year}-${p.month}-${p.day}`,hour:parseInt(p.hour,10),min:parseInt(p.minute,10)};
};
// V13.4.218: instant of local midnight, as an ISO string NWS accepts for ?start=.
//   Walking back hour+minute from now is DST-safe in the direction that matters:
//   it can only ever reach slightly further back than midnight, never later, so
//   the day is always fully covered. The local-date filter downstream discards
//   any overshoot.
const _wxDayStartISO=(tz)=>{
  const now=new Date(), L=_wxLocalParts(tz,now);
  return new Date(now.getTime()-L.hour*3600e3-L.min*60e3-120e3).toISOString().slice(0,19)+'Z';
};
const _wxJson=async(url)=>{
  // Never throws. A rejected fetch (CORS, DNS, timeout) has to come back as a
  //   value, not an exception, or it kills the whole race in _wxMarkets.
  let r;
  try{ r=await fetch(url,{signal:AbortSignal.timeout(9000)}); }
  catch(e){ return{ok:false,reason:'net: '+String(e&&e.message||e).slice(0,40)}; }
  if(!r.ok)return{ok:false,reason:'HTTP '+r.status};
  const t=await r.text();
  if(!t||t.trimStart().startsWith('<'))return{ok:false,reason:'HTML not JSON'};
  try{return{ok:true,data:JSON.parse(t)};}catch(_e){return{ok:false,reason:'bad JSON'};}
};
// V13.4.217: Kalshi refuses cross-origin browser reads, so the market call goes
//   through this app's own same-origin rewrites (/api/kalshi/* ->
//   api.elections.kalshi.com/trade-api/v2/*, declared in vercel.json). Direct
//   stays in the race because it costs nothing when it fails and does work from
//   some origins.
//
//   Every path is validated for a real markets ARRAY, never on status alone.
//   Under `vite dev` those rewrites do not exist -- Vercel applies them, not
//   Vite -- so the dev server answers unmatched /api/* with 200 and index.html.
//   A 200 here is not evidence of anything; only a parsed markets array is.
const _wxMarkets=async(series,limit=60)=>{
  const qs=`markets?series_ticker=${encodeURIComponent(series)}&status=open&limit=${limit}`;
  const urls=[`/api/kalshi/${qs}`,`/api/kalshi-public/${qs}`,
              `https://api.elections.kalshi.com/trade-api/v2/${qs}`];
  const tried=await Promise.all(urls.map(async u=>{
    const r=await _wxJson(u);
    if(!r.ok)return{u,fail:r.reason};
    const mk=(r.data&&Array.isArray(r.data.markets))?r.data.markets:null;
    return (mk&&mk.length)?{u,markets:mk}:{u,fail:'no markets array'};
  }));
  const win=tried.find(x=>x.markets);
  return win?{ok:true,markets:win.markets,via:win.u.split('?')[0]}
            :{ok:false,
              // `empty` separates "the API answered and there is nothing trading"
              //   from "we could not reach it". Only the first is a fact about the
              //   market; the second is a fact about the network, and the two must
              //   never be shown to mean the same thing.
              empty:tried.some(x=>x.fail==='no markets array'),
              reason:tried.map(x=>x.u.split('?')[0]+': '+x.fail).join(' / ')};
};
// Probe one series for "is anything trading". Tries paths in ORDER and stops at
//   the first definitive answer, rather than racing all three -- probing every
//   city three ways at once fired 33 requests, some timed out, and cities that
//   were genuinely open (Boston, 6 buckets) came back marked closed. Failing
//   open is the right default: a wrong "closed" silently hides a tradeable
//   market, while a wrong "open" just surfaces a real error when clicked.
const _wxProbe=async(series)=>{
  const qs=`markets?series_ticker=${encodeURIComponent(series)}&status=open&limit=1`;
  for(const u of [`/api/kalshi/${qs}`,`/api/kalshi-public/${qs}`,
                  `https://api.elections.kalshi.com/trade-api/v2/${qs}`]){
    const r=await _wxJson(u);
    if(!r.ok)continue;                       // unreachable: try the next path
    if(!r.data||!Array.isArray(r.data.markets))continue;
    return r.data.markets.length>0?'open':'closed';
  }
  return 'unknown';                          // never reached one; do not disable
};

// V13.4.221: module level, so the pick scanner can run this for EVERY open city
//   rather than only the one on screen. Returns a plain result object; the
//   component adapts it to its own state shape. Body is otherwise untouched.
const _wxScan=async(city)=>{
  {
    try{
      const mk=await _wxMarkets(city.series);
      if(!mk.ok)throw new Error('Kalshi: '+mk.reason);
      // Keep ONLY the ladder for the day actually being measured. See the
      //   _wxMarketDay note: two ladders are open at once for part of every day,
      //   and merging them prices tomorrow's buckets against today's thermometer.
      const _today=_wxLocalParts(city.tz,new Date()).date;
      const _all=mk.markets;
      const markets=_all.filter(m=>_wxMarketDay(m,city.tz)===_today);
      const _otherDays=_all.length-markets.length;
      if(!markets.length)throw new Error(
        _all.length?`no ${city.series} ladder for ${_today} (${_all.length} open, all other days)`
                   :'no open '+city.series+' market right now');

      // NWS forecast: point -> gridpoint forecast -> today's DAYTIME high.
      //   V13.4.223: the points lookup only translates a fixed lat/lon into a
      //   gridpoint URL, so its answer can never change for a given city. It was
      //   being refetched on every scan of every city. At ten cities that was
      //   merely wasteful; at twenty, with the pick sweep running each of them
      //   every five minutes, it is half the request volume for nothing. Cached
      //   for the life of the page.
      const fcUrl=await _wxGridUrl(city);
      if(!fcUrl)throw new Error('NWS points: could not resolve gridpoint');
      const fcR=await _wxJson(fcUrl);
      if(!fcR.ok)throw new Error('NWS forecast: '+fcR.reason);
      // V13.4.218: the forecast period must be TODAY's, matched on local date.
      //
      //   `periods.find(p=>p.isDaytime)` -- the obvious version -- silently returns
      //   TOMORROW once today's daytime period rolls off. NWS orders periods from
      //   now: at 8pm the list reads ["Tonight" (N), "Friday" (Y), ...], so the
      //   first daytime hit is the next day while the Kalshi ladder and the
      //   observations are still today's. That lands in the evening, exactly when
      //   sigma is tightest (0.6F) -- the model would be at maximum confidence
      //   about the wrong day. A confident wrong answer is worse than no answer.
      const periods=fcR.data?.properties?.periods||[];
      const todayLocal=_wxLocalParts(city.tz,new Date()).date;
      const day=periods.find(p=>p.isDaytime&&p.startTime&&
                               _wxLocalParts(city.tz,new Date(p.startTime)).date===todayLocal);
      const fc=day?Number(day.temperature):null;
      //   No today-daytime period means peak heating has passed and NWS has moved
      //   on to tonight. That is not an error: by then the observed max IS
      //   essentially the answer, so the running max becomes the centre below.
      const fcClosed=(fc==null);

      // Observations -> running max for the LOCAL day (Kalshi's day, not UTC).
      // V13.4.218: bound the window by TIME, not by count.
      //
      //   `limit=48` looked reasonable and was badly wrong. These stations report
      //   far more often than hourly, and the API returns the most RECENT n -- so
      //   late in the day the window slides right past the afternoon peak. Measured
      //   at Long Beach at 22:00 local: limit=48 reached back only to 18:00 and
      //   reported a max of 78.8F, while the true max for the day was 89.6F. Nearly
      //   11F understated.
      //
      //   That silently destroys the truncation, which is the one thing in this
      //   lane worth more than the market's opinion: buckets that are definitively
      //   dead come back alive, and the model prices them as live. It also framed a
      //   CORRECT station as broken -- LA read 71.6F against a market pricing
      //   82-83F, which looked like KLAX being the wrong station until the window
      //   was fixed and KLAX resolved to exactly 82.4F.
      const ob=await _wxJson(`https://api.weather.gov/stations/${city.station}/observations`
        +`?start=${encodeURIComponent(_wxDayStartISO(city.tz))}&limit=300`);
      let runMax=null,maxC=null,obsN=0,obsAt=null,intC=0;
      if(ob.ok){
        const today=todayLocal; // same local day the forecast period was matched on
        const toF=c=>c*9/5+32;
        (ob.data.features||[]).forEach(f=>{
          const c=f?.properties?.temperature?.value; const ts=f?.properties?.timestamp;
          if(c==null||!ts)return;
          if(_wxLocalParts(city.tz,new Date(ts)).date!==today)return;
          const F=toF(c); obsN++;
          if(Number.isInteger(c))intC++;          // see quantisation note below
          if(runMax==null||F>runMax){runMax=F;maxC=c;}
          if(!obsAt||ts>obsAt)obsAt=ts;
        });
      }
      if(runMax!=null)runMax=Math.round(runMax*10)/10;
      // V13.4.219: QUANTISATION MARGIN.
      //
      //   Several of these stations report whole degrees CELSIUS. KSEA is one --
      //   its raw values come back 19, 19, 19, and a day of them peaks at exactly
      //   28C, which is 82.4F. That is why four different cities all reported an
      //   identical 82.4F max: not a bug, just several stations sitting on 28C.
      //
      //   But a whole-Celsius reading carries +/-0.9F of resolution, and the
      //   buckets here are 1F wide. The reading is coarser than the thing being
      //   measured. Worked case: a station reporting 27C reads as 80.6F, so the
      //   naive floor is round(80.6)=81 and "79 to 80" is declared dead -- yet the
      //   true temperature could be 79.7F, which settles at 80 and wins that
      //   bucket outright.
      //
      //   So back the floor off by the half-step before rounding. This only ever
      //   kills FEWER buckets, which is the safe direction: a bucket wrongly left
      //   alive costs a missed trade, while a bucket wrongly killed says sell
      //   something that can still win.
      //
      //   The test is about the MAX reading, not the whole series. Requiring every
      //   reading of the day to be integer was too strict and silently disabled the
      //   margin on the exact station that motivated it: KSEA came back 262 whole
      //   and 22 fractional out of 284, so the all-or-nothing test said "not
      //   quantised" while the day's max was sitting at a flat 28C. What matters is
      //   whether THAT reading is quantised, on a station that mostly reports that
      //   way.
      const wholeC=(obsN>0&&maxC!=null&&Number.isInteger(maxC)&&(intC/obsN)>=0.7);
      const runMaxFloor=(runMax==null)?null:(runMax-(wholeC?0.9:0));

      // sigma: widest before peak heating (~4pm local), tightening after.
      const hour=_wxLocalParts(city.tz,new Date()).hour;
      const toPeak=16-hour;
      const sigma=toPeak>0?Math.min(4,1.2+0.3*toPeak):Math.max(0.6,1.2+0.2*toPeak);

      const mu=fc!=null?fc:(runMax!=null?runMax:null);

      // V13.4.217: READINESS GATE — the honest answer to "when does this model
      //   know anything the market doesn't?"
      //
      // Live run at 01:00 local proved it does not know anything overnight. With
      // sigma at its 4F cap around a ROUNDED INTEGER forecast (NWS published 80,
      // which is itself +/-0.5 before any weather uncertainty), the curve printed
      // FOUR buy signals against the live book: "77 or below" 16% vs 3c ask,
      // "78-79" 21% vs 10c, "86 or above" 10% vs 1c, and "80-81" 22% vs 44c.
      // Every one of those is the curve being too wide, not edge. The book had
      // 707 / 3,134 / 8,538 / 3,625 contracts on the other side and it prices the
      // Weather Company forecast plus full ensemble spread. A normal curve fitted
      // to one rounded integer cannot beat that, and pretending otherwise would
      // put fake green numbers on the one lane that exists because the BTC tape
      // could not be trusted.
      //
      // What this model actually owns is the TRUNCATION, and truncation needs the
      // day to have happened. At 1am the running max is an overnight reading and
      // constrains nothing. By early afternoon it is a hard floor under the high.
      // So: no edge shown until the day is underway. Percentages still render, so
      // the shape is visible, but the buy column stays blank and says why.
      const ready=(hour>=12&&obsN>=3);
      // Truncation is BITING when the day has already climbed near its forecast --
      //   that is when a dead bucket is arithmetic the market may still be slow on.
      const biting=ready&&runMax!=null&&mu!=null&&runMax>=mu-3;

      const rows=markets.map(m=>{
        const sub=m.yes_sub_title||m.subtitle||'';
        const b=_wxParseBucket(sub);
        const bid=Math.round((+m.yes_bid_dollars||0)*100);
        const ask=Math.round((+m.yes_ask_dollars||0)*100);
        const noBid=Math.round((+m.no_bid_dollars||0)*100);
        const noAsk=Math.round((+m.no_ask_dollars||0)*100);
        const oi=Math.round(+m.open_interest_fp||0);
        let p=null,dead=false;
        if(b&&mu!=null){
          // V13.4.219: compare in ROUNDED degrees, because that is how the
          //   contract settles. Kalshi reports the daily high to the nearest
          //   degree, so "81 to 82" covers real temperatures from 80.5 to 82.5.
          //
          //   The first version tested `b.hi < runMax-0.05` against the raw
          //   reading, which killed a bucket the moment the thermometer edged past
          //   its integer label. Caught live on LA: observed max 82.4F marked
          //   "81 to 82" impossible while the market paid 52c for it -- and the
          //   market was right, since 82.4 rounds to 82, squarely inside. That is
          //   the failure landing exactly at a bucket boundary, which is the only
          //   place the difference is worth anything.
          //
          //   Floor for the day's settled high, in rounded degrees:
          const rmR=(runMaxFloor==null)?null:Math.round(runMaxFloor);
          if(rmR!=null&&b.hi<rmR){dead=true;p=0;}
          else{
            const lo=b.lo===-Infinity?-Infinity:b.lo-0.5;
            const hi=b.hi===Infinity?Infinity:b.hi+0.5;
            const F=(x)=>x===Infinity?1:(x===-Infinity?0:_wxNormCdf((x-mu)/sigma));
            let raw=F(hi)-F(lo);
            if(rmR!=null){ // truncate: the high cannot settle below what already happened
              const floor=rmR-0.5;
              const floorP=F(floor);
              const lo2=Math.max(lo,floor);
              raw=Math.max(0,F(hi)-F(lo2))/Math.max(1e-6,1-floorP);
            }
            p=Math.max(0,Math.min(1,raw));
          }
        }
        // V13.4.217: price BOTH sides. Showing only the YES edge buried the single
        //   most valuable signal this lane has. Worked example off the live book:
        //   the day hits 81.5F, so "80 to 81" can no longer be the high -- yet YES
        //   still asks 44c. As a YES edge that renders "-44c" in red, which reads
        //   as "avoid" when it actually means NO is on sale: NO asks 58c on a
        //   contract worth a certain 100c, or +42c. Avoid and free money look
        //   identical in a one-sided column, so show whichever side is actionable.
        const eYes=(p!=null&&ask>0)?(p*100-ask):null;
        const eNo =(p!=null&&noAsk>0)?((1-p)*100-noAsk):null;
        const best=(eYes==null&&eNo==null)?null
                  :(eNo==null||(eYes!=null&&eYes>=eNo))?{side:'YES',edge:eYes,price:ask}
                                                       :{side:'NO',edge:eNo,price:noAsk};
        // Model-vs-market disagreement, measured side-agnostically off the YES mid
        //   so the bid/ask spread cannot flip the verdict.
        const yesMid=(bid>0&&ask>0)?(bid+ask)/2:ask;
        return{sub,b,bid,ask,noBid,noAsk,oi,p,dead,ticker:m.ticker,closeMs:Date.parse(m.close_time),quoteAt:Date.now(),
          // Dead buckets keep their edge even before the gate: "this bucket is
          //   already impossible" is arithmetic off the observed max, not forecasting.
          best:(best&&best.edge!=null&&(ready||dead))?best:null,
          // V13.4.217: a huge "edge" against a LIQUID book is evidence the model is
          //   wrong, not that money is lying on the floor. Kalshi settles on The
          //   Weather Company; this panel reads NWS. When the two disagree by a lot
          //   on a market thousands of contracts deep, the crowd holding those
          //   contracts is the better forecaster. Flag it instead of painting it green.
          //   Threshold lowered 15c -> 10c after the 01:00 run: the miscalibrated
          //   rows sat at 9-13c, so a 15c gate let three of the four through.
          //   A dead bucket is never "suspect" -- it is measured, not modelled.
          suspect:(p!=null&&yesMid>0&&!dead&&oi>=500&&Math.abs(p*100-yesMid)>=10)};
      }).filter(r=>r.b).sort((a,b)=>(b.b.lo===-Infinity?-999:b.b.lo)-(a.b.lo===-Infinity?-999:a.b.lo));

      // V13.4.218: DATA-CONFLICT GUARD.
      //
      //   If a bucket is dead on my observations while the market pays 50c+ for it,
      //   one of us is badly wrong. It is almost certainly me: the market has
      //   thousands of contracts and the settlement feed, I have a free API and a
      //   station I picked by hand. Every failure in this lane so far has been mine
      //   -- the truncated observation window, guessed tickers -- and each one made
      //   the model MORE confident, not less.
      //
      //   The exact shape of the LA failure: dead buckets everywhere, "76 or below"
      //   at 100% model against a 1c ask, and the panel offering YES +99c. That is
      //   not an edge, it is a broken reading, and it is the most expensive thing
      //   this panel could ever print. So when observations and market disagree
      //   this hard, no edges at all -- the reading is not trustworthy enough to
      //   trade on either side.
      //   Two shapes, because the disagreement runs both ways.
      //   (a) I call a bucket dead, the market still pays 50c+ for it.
      //   (b) The market is effectively CERTAIN (90c bid) about a bucket and my
      //       model is more than 30 points away from that. A 90c+ bid on real
      //       open interest means the day is decided and the book knows the
      //       answer; if my reading cannot see it, my reading is wrong.
      //
      //   Shape (b) was found on Seattle: the book paid 100c for "83 or above"
      //   while KSEA topped out at 82.4F and the model said 46%. Nothing was
      //   marked dead, so shape (a) stayed silent and every row was quietly
      //   offering 37-53c of "edge". That is the basis risk in the caveats made
      //   real -- Kalshi settles on The Weather Company, which had the day at 83F+
      //   while NWS KSEA did not.
      const conflict=rows.find(r=>r.dead&&r.bid>=50)
                  ||rows.find(r=>r.p!=null&&r.bid>=90&&r.oi>=200&&Math.abs(r.bid-r.p*100)>=30)
                  ||null;
      if(conflict)rows.forEach(r=>{r.best=null;});

      return{ok:true,city,rows,fc,runMax,runMaxFloor,obsN,obsAt,hourLocal:hour,
        sigma:Math.round(sigma*100)/100,ready,biting,fcClosed,via:mk.via,wholeC,
        ladderDay:_today,otherDayRows:_otherDays,
        conflict:conflict?{sub:conflict.sub,bid:conflict.bid,model:conflict.dead?null:conflict.p}:null};
    }catch(e){return{ok:false,city,err:String(e.message||e).slice(0,140)};}
  }
};

// ============================================================================
// V13.4.221 — WEATHER PICKS AND RECORD
//
// The ladder made you do the choosing. This turns it into "be on this market,
// this side, at this price" and then keeps score of whether that was right.
//
// TWO TIERS, and the split is the whole point.
//
//   LIVE  — arithmetic only. The day has already climbed past a bucket, so that
//           bucket settles at zero and its NO side is worth a certain 100c.
//           This is the only thing in the lane that is measured rather than
//           forecast, so it is the only thing offered as a real trade.
//
//   PAPER — the model's own opinion. Logged, scored, never presented as a
//           trade. Everything found while building this lane says the forecast
//           half cannot beat the book: it is a normal curve fitted to one
//           rounded integer while the market prices The Weather Company plus
//           full ensemble spread. Rather than assert that, log it and let the
//           record settle the argument. Same treatment as the sports value
//           lane -- paper until the numbers earn promotion.
//
// The LIVE tier carries a MARGIN requirement, which is the guard that matters.
// Kalshi settles on The Weather Company; this reads NWS. A bucket that is dead
// by a tenth of a degree is one basis disagreement away from being alive, and
// that is exactly where the money would go. Requiring 2 rounded degrees of
// clearance means a full degree of source disagreement still leaves the pick
// dead. Thin ones (1 degree) are shown as watch-only and never logged live.
// ============================================================================
const _WX_LEDGER_PATH='memory/weatherPicks';
const _WX_LEDGER_KEY='tara_weather_picks_v1';
const _WX_PICK_CAP=400;
// LIVE tier gates
const _WX_MIN_MARGIN=2;     // rounded degrees a dead bucket must clear
const _WX_MAX_PRICE=95;     // never pay 96c+ for 100c; the 4c is not worth the tail
const _WX_MIN_OI=200;       // needs a real book to fill against
const _WX_MIN_EDGE=3;       // cents
// PAPER tier gates
const _WX_PAPER_MIN_EDGE=5;

// Kalshi taker fee for one contract at price p (cents), rounded UP to the cent
//   the way Kalshi charges it.
const _wxFeeCents=(p)=>Math.ceil(0.07*(p/100)*(1-p/100)*100);
// The bar a pick has to clear. Buying at p to win 100 needs a win rate of
//   (p + fee)/100 just to break even -- at 86c that is 87%, not 50%. This is the
//   number that decides whether the strategy is worth running, so it goes on the
//   card next to the price rather than being left for the record to reveal later.
const _wxBreakeven=(p)=>(p+_wxFeeCents(p));
// V13.4.221: WHICH DAY IS THIS LADDER FOR.
//
//   Kalshi keeps TWO daily-high ladders open at once for part of every day --
//   the one being decided now and tomorrow's, which starts trading early. A
//   plain status=open fetch returns both, and merging them prices tomorrow's
//   buckets against today's thermometer.
//
//   That is not cosmetic. Live on LA at 23:31 local it produced a "be on this"
//   card reading BUY NO @ 81c on tomorrow's "79 to 80" -- marked already-passed
//   because TODAY had reached 82.4F. Tomorrow had not happened; that bucket was
//   entirely live and the trade was close to a coin flip sold as a certainty.
//   The duplicate rows in the Seattle ladder were the same thing, two days of
//   the same bucket sitting on top of each other.
//
//   Kalshi's ticker carries the weather day: KXHIGHLAX-26AUG19-B79.5. Verified
//   across five cities that it always agrees with the day derived independently
//   from close_time (these close at 01:00 local the following morning). Parse
//   the ticker, cross-check against close_time, and DROP anything where the two
//   disagree rather than guessing which is right.
const _WX_MON={JAN:'01',FEB:'02',MAR:'03',APR:'04',MAY:'05',JUN:'06',
               JUL:'07',AUG:'08',SEP:'09',OCT:'10',NOV:'11',DEC:'12'};
const _wxTickerDay=(t)=>{
  const m=String(t||'').match(/-(\d{2})([A-Z]{3})(\d{2})-/);
  return (m&&_WX_MON[m[2]])?`20${m[1]}-${_WX_MON[m[2]]}-${m[3]}`:null;
};
const _wxMarketDay=(m,tz)=>{
  const fromTicker=_wxTickerDay(m&&m.ticker);
  let fromClose=null;
  if(m&&m.close_time){
    const c=new Date(m.close_time);
    if(!isNaN(c))fromClose=_wxLocalParts(tz,new Date(c.getTime()-2*3600e3)).date;
  }
  if(fromTicker&&fromClose)return fromTicker===fromClose?fromTicker:null;
  return fromTicker||fromClose;
};
// Gridpoint forecast URL for a city, resolved once. A failure is not cached, so
//   a transient outage does not disable the city until reload.
const _WX_GRID={};
const _wxGridUrl=async(city)=>{
  if(_WX_GRID[city.id])return _WX_GRID[city.id];
  const pt=await _wxJson(`https://api.weather.gov/points/${city.lat},${city.lon}`);
  const u=pt.ok?(pt.data&&pt.data.properties&&pt.data.properties.forecast):null;
  if(u)_WX_GRID[city.id]=u;
  return u||null;
};
const _wxDayKey=(city)=>_wxLocalParts(city.tz,new Date()).date;
const _wxPickId=(p)=>`${p.ticker}|${p.side}`;

// Choose at most one pick from a scanned city. Returns null when there is
//   nothing worth doing, which is the normal answer and not a failure.
const _wxPickFrom=(scan)=>{
  if(!scan||!scan.ok||!scan.ready||scan.conflict)return null;
  const rmR=(scan.runMaxFloor==null)?null:Math.round(scan.runMaxFloor);
  const day=_wxDayKey(scan.city);
  const base={city:scan.city.id,cityLabel:scan.city.label,series:scan.city.series,
    station:scan.city.station,dayKey:day,runMax:scan.runMax,fc:scan.fc};

  // --- LIVE: arithmetic, with clearance ---
  //   An unscreened city cannot produce a real-money pick. The whole live tier
  //   rests on the station being a faithful stand-in for the settlement source,
  //   and for these that has never been measured — there were no settled days to
  //   measure against. It can still generate paper calls, which is exactly how it
  //   earns its way onto the live tier.
  if(rmR!=null&&!_WX_UNSCREENED.has(scan.city.id)){
    const arith=scan.rows
      .filter(r=>r.dead&&r.ticker&&r.b&&Number.isFinite(r.b.hi))
      .map(r=>({r,margin:rmR-r.b.hi}))
      .filter(x=>x.margin>=_WX_MIN_MARGIN)
      .filter(x=>x.r.noAsk>0&&x.r.noAsk<=_WX_MAX_PRICE&&x.r.oi>=_WX_MIN_OI)
      .map(x=>({...x,edge:100-x.r.noAsk}))
      .filter(x=>x.edge>=_WX_MIN_EDGE)
      // cheapest certainty first: the most edge per cent risked
      .sort((a,b)=>(b.edge/Math.max(1,b.r.noAsk))-(a.edge/Math.max(1,a.r.noAsk)));
    if(arith.length){
      const w=arith[0];
      return{...base,tier:'live',ticker:w.r.ticker,sub:w.r.sub,side:'NO',
        closeMs:w.r.closeMs,quoteAt:w.r.quoteAt,
        price:w.r.noAsk,edge:w.edge,marginF:w.margin,oi:w.r.oi,model:0,
        why:`the day already reached ${scan.runMax}°F, which is ${w.margin}° clear of the top of this bucket`};
    }
  }

  // --- PAPER: the model's best call, never offered as a trade ---
  const model=scan.rows
    .filter(r=>r.best&&r.ticker&&!r.dead&&!r.suspect&&r.best.edge>=_WX_PAPER_MIN_EDGE&&r.oi>=_WX_MIN_OI)
    .sort((a,b)=>b.best.edge-a.best.edge);
  if(model.length){
    const m=model[0];
    return{...base,tier:'paper',ticker:m.ticker,sub:m.sub,side:m.best.side,
      closeMs:m.closeMs,quoteAt:m.quoteAt,
      price:m.best.price,edge:Math.round(m.best.edge),marginF:null,oi:m.oi,model:m.p,
      why:`model reads ${Math.round(m.p*100)}% against a ${m.best.price}c ask`};
  }
  return null;
};

// ---- ledger persistence: localStorage first, Supabase mirrored ----
// V13.4.221: a pick is only trustworthy if its ticker is for the day it claims.
//   The wrong-day bug wrote one real entry before it was caught -- LA's
//   KXHIGHLAX-26AUG20-B79.5 logged under dayKey 2026-08-19 -- and a bad row in a
//   record is worse than no record, because every later number is computed from
//   it. Rather than delete it by hand on one machine, drop the mismatch wherever
//   the ledger is read. It cleans itself here, on the phone, and in the cloud doc
//   at the next save, and it will catch any recurrence instead of silently
//   scoring it.
const _wxLedgerClean=(l)=>({picks:(l&&Array.isArray(l.picks)?l.picks:[])
  .filter(p=>{
    if(!p||!p.ticker||!p.dayKey)return false;
    const td=_wxTickerDay(p.ticker);
    if(td&&td!==p.dayKey){
      try{console.warn('[weather] dropping pick from the wrong day:',p.ticker,'logged under',p.dayKey);}catch(_e){}
      return false;
    }
    return true;
  })});
const _wxLedgerLoad=()=>{
  try{const j=JSON.parse(localStorage.getItem(_WX_LEDGER_KEY)||'null');
    if(j&&Array.isArray(j.picks))return _wxLedgerMerge(_wxLedgerClean(j),_wxMemoryLedger);}catch(_e){}
  return _wxMemoryLedger||{picks:[]};
};
let _wxMemoryLedger=null;
const _wxLedgerSave=(l,{sync=true}={})=>{
  const payload=_wxLedgerMerge(_wxLedgerLoad(),l);
  _wxMemoryLedger=payload;
  try{localStorage.setItem(_WX_LEDGER_KEY,JSON.stringify(payload));}catch(_e){}
  try{if(sync&&typeof cloudWriteDebouncedRMW==='function')cloudWriteDebouncedRMW(
    _WX_LEDGER_PATH,()=>_wxLedgerLoad(),(cloud,local)=>_wxLedgerMerge(cloud,local),4000);}catch(_e){}
  return payload;
};
// Union by ticker|side. A settled pick always beats an open one, and the totals
//   are recounted from the merged list rather than summed -- adding two devices'
//   counters is how you get a record that matches neither.
const _wxLedgerMerge=(a,b)=>{
  return mergeWeatherRecords(a,b,{cap:_WX_PICK_CAP,tickerDay:_wxTickerDay});
};

// Settle one open pick against Kalshi's own result. Deliberately NOT settled
//   from my own observations: the whole basis-risk problem is that this panel
//   reads a different source than the contract does, so scoring against NWS
//   would grade the model against the wrong answer key and hide the exact error
//   that costs money.
const _wxSettleOne=async(pick)=>{
  const j=await _wxJson(`/api/kalshi/markets/${encodeURIComponent(pick.ticker)}`);
  const m=j.ok?(j.data&&j.data.market):null;
  const verified=m?_wxGrade(pick,m):null;
  if(verified)return verified;
  const d=await _wxJson(`https://api.elections.kalshi.com/trade-api/v2/markets/${encodeURIComponent(pick.ticker)}`);
  if(!d.ok||!d.data||!d.data.market)return null;
  return _wxGrade(pick,d.data.market);
};
const _wxGrade=(pick,m)=>{
  return gradeWeatherPick(pick,m);
};

const _wxTally=(picks,tier)=>{
  const t=picks.filter(p=>p.tier===tier);
  const done=t.filter(p=>p.result==='win'||p.result==='loss');
  const w=done.filter(p=>p.result==='win').length;
  const l=done.filter(p=>p.result==='loss').length;
  const pnl=done.reduce((a,p)=>a+(Number(p.pnl)||0),0);
  const paid=done.reduce((a,p)=>a+(Number(p.price)||0),0);
  return{w,l,open:t.filter(p=>!p.result).length,n:done.length,
    hit:done.length?w/done.length:null,
    pnl:Math.round(pnl*10)/10,
    breakeven:done.length?paid/done.length:null};
};

// V13.4.221: scan every city that could produce a pick, log one per city per
//   day, and settle the open ones against Kalshi's own result.
//
//   Only cities past their local midday are scanned. That is not an
//   optimisation, it is the readiness gate: before midday the model provably
//   cannot beat the book, so scanning would burn requests to produce nothing.
//   It also keeps the sweep to a handful of cities at any hour rather than all
//   eleven, since they are spread across four time zones.
// ============================================================================
// V13.4.222 — ALERT ON A LIVE PICK
//
// The backtest is why this exists. Replaying 6 days across 10 cities against
// real NWS observations and real Kalshi candlesticks:
//
//   * 64 buckets were called dead by the margin rule. All 64 settled NO. The
//     arithmetic is sound.
//   * 60 of those 64 were ALREADY priced at NO 100c by the time the signal
//     existed. Nothing to buy. The market is fast.
//   * 2 of 64 were tradeable under 95c. Both won: LA "75 to 76" at 94c, and
//     Boston "78 or below" at 41c.
//
// That is ~2.3 opportunities per week across ten cities, and the Boston one
// lasted about an hour: at 16:00 local the day had already reached 80.6F and
// the book was still paying 59c for a bucket worth zero, with 1,583 contracts
// traded in that hour. By 17:00 it was 0/1.
//
// A one-hour window twice a week cannot be caught by opening the app and
// looking. Either it pages him or the lane is decorative. That is the entire
// justification for this file.
//
// Deliberately standalone rather than routed through broadcastSignal: that path
// is bound to the BTC asset's state and its embed cascade. This reads the same
// webhook the app already persists and posts its own embed.
// ============================================================================
const _WX_ALERTED_KEY='tara_weather_alerted_v1';
const _wxHook=()=>{
  try{
    const multi=JSON.parse(localStorage.getItem('taraV70Hooks')||'null');
    if(multi&&multi.BTC)return multi.BTC;
  }catch(_e){}
  try{return localStorage.getItem('taraV110Hook')||'';}catch(_e){}
  return '';
};
// One alert per pick, ever. The sweep re-runs every 5 minutes and the pick
//   stands all day, so without this it would repost twelve times an hour.
const _wxAlerted=()=>{
  try{const a=JSON.parse(localStorage.getItem(_WX_ALERTED_KEY)||'[]');
    return new Set(Array.isArray(a)?a:[]);}catch(_e){return new Set();}
};
const _wxMarkAlerted=(id)=>{
  try{const s=_wxAlerted(); s.add(id);
    localStorage.setItem(_WX_ALERTED_KEY,JSON.stringify(Array.from(s).slice(-200)));}catch(_e){}
};
const _wxAlert=async(p)=>{
  const id=_wxPickId(p);
  if(_wxAlerted().has(id))return false;
  const hook=_wxHook();
  if(!hook){_wxMarkAlerted(id);return false;}   // nothing to post to; do not retry forever
  const gross=100-p.price-_wxFeeCents(p.price);
  const be=_wxBreakeven(p.price);
  const undo=((p.price+_wxFeeCents(p.price))/Math.max(1,gross)).toFixed(1);
  const embed={
    title:`▲  WEATHER · ${p.cityLabel} ${p.sub}`,
    color:_DC_UP,
    description:'```\n'+
      `BUY NO   @ ${p.price}c\n`+
      `MAKE     ${gross}c after the ${_wxFeeCents(p.price)}c fee\n`+
      `CLEAR BY ${p.marginF}°F  (day already hit ${p.runMax}°F)\n`+
      '```',
    fields:[
      {name:'Why',value:p.why,inline:false},
      {name:'The shape of it',
       value:`Needs ${be}% to break even. One loss undoes ${undo} wins. Size accordingly.`,inline:false},
      {name:'Window',
       value:'These reprice fast. In the backtest 60 of 64 signals were already at 100c and the one real gap lasted about an hour.',inline:false},
      {name:'Ticker',value:'`'+p.ticker+'`',inline:false},
    ],
    footer:{text:`${TARA_VERSION_DISPLAY} · weather · ${p.station}`},
    timestamp:new Date().toISOString(),
  };
  try{
    const r=await fetch(hook,{method:'POST',headers:{'Content-Type':'application/json'},
      body:JSON.stringify({username:'Tara',embeds:[embed]})});
    if(!r.ok){try{console.error('[weather-alert] HTTP',r.status,(await r.text()).slice(0,300));}catch(_e){}
      return false;}   // leave it unmarked so a transient failure retries
    _wxMarkAlerted(id);
    return true;
  }catch(e){try{console.error('[weather-alert]',e&&e.message);}catch(_e){} return false;}
};

function useWeatherPicks(){
  const[ledger,setLedger]=React.useState(_wxLedgerLoad);
  const[scanAt,setScanAt]=React.useState(null);
  const busy=React.useRef(false);

  // Merge peer captures/settlements without echoing a complete stale document.
  React.useEffect(()=>cloudWatch(_WX_LEDGER_PATH,(c,status)=>{
    if(status!=='ok'||!c)return;
    setLedger(prev=>_wxLedgerSave(_wxLedgerMerge(prev,c),{sync:false}));
  }),[]);

  const sweep=React.useCallback(async()=>{
    if(busy.current)return; busy.current=true;
    try{
      // 1. settle anything still open
      const open=(_wxLedgerLoad().picks||[]).filter(p=>!p.result);
      const settled=[];
      for(const p of open){
        try{const g=await _wxSettleOne(p); if(g)settled.push(g);}catch(_e){}
      }

      // 2. look for a new pick in every city whose day is underway
      const due=_WX_CITIES.filter(c=>_wxLocalParts(c.tz,new Date()).hour>=12);
      const found=[];
      for(const c of due){
        const have=(_wxLedgerLoad().picks||[]).some(p=>p.city===c.id&&p.dayKey===_wxDayKey(c));
        if(have)continue;                       // one pick per city per day, and it stands
        try{
          const scan=await _wxScan(c);
          const pick=_wxPickFrom(scan);
          if(pick){const at=Date.now();found.push({...pick,atMs:at,result:null,
            learningCapture:capturePickLearning(pick,{profile:`Weather_${pick.tier}`,at,close:pick.closeMs,version:TARA_BUILD_VERSION,
              probability:pick.side==='YES'?pick.model:1-pick.model,confidence:100*Math.abs(2*pick.model-1),price:pick.price,
              event:`${pick.city}|${pick.dayKey}`,quoteAt:pick.quoteAt})});}
        }catch(_e){}
      }

      // Alert only the LIVE tier. Paper calls are bookkeeping and must never page him.
      for(const f of found) if(f.tier==="live") await _wxAlert(f);
      if(settled.length||found.length){
        setLedger(prev=>{
          const next=_wxLedgerMerge(prev,{picks:settled.concat(found)});
          return _wxLedgerSave(next);
        });
      }
      setScanAt(Date.now());
    } finally { busy.current=false; }
  },[]);

  React.useEffect(()=>{sweep();const iv=setInterval(sweep,300000);return()=>clearInterval(iv);},[sweep]);

  const picks=ledger.picks||[];
  return{
    picks,
    scanAt,
    live:_wxTally(picks,'live'),
    paper:_wxTally(picks,'paper'),
    openLive:picks.filter(p=>p.tier==='live'&&!p.result).sort((a,b)=>b.atMs-a.atMs),
    openPaper:picks.filter(p=>p.tier==='paper'&&!p.result).sort((a,b)=>b.atMs-a.atMs),
    recent:picks.filter(p=>p.result).sort((a,b)=>(b.settledAt||0)-(a.settledAt||0)).slice(0,25),
  };
}

function WeatherView({onClose,weatherPicks}){
  const[cityId,setCityId]=React.useState('NYC');
  const[weatherLane,setWeatherLane]=React.useState('live');
  const cityStripRef=React.useRef(null);
  const[state,setState]=React.useState({loading:true,err:null,rows:[],fc:null,runMax:null,obsN:0,obsAt:null,hourLocal:null,sigma:null,ready:false,biting:false});
  const city=_WX_CITIES.find(c=>c.id===cityId)||_WX_CITIES[0];
  React.useEffect(()=>{
    const strip=cityStripRef.current;
    const selected=strip?.querySelector('[aria-pressed="true"]');
    if(strip&&selected&&strip.scrollWidth>strip.clientWidth){
      strip.scrollLeft=Math.max(0,selected.offsetLeft-strip.offsetLeft-(strip.clientWidth-selected.clientWidth)/2);
    }
  },[cityId]);

  const load=React.useCallback(async()=>{
    setState(s=>({...s,loading:true,err:null}));
    const r=await _wxScan(city);
    if(!r.ok){setState(s=>({...s,loading:false,err:r.err}));return;}
    setState({loading:false,err:null,rows:r.rows,fc:r.fc,runMax:r.runMax,obsN:r.obsN,
      obsAt:r.obsAt,hourLocal:r.hourLocal,sigma:r.sigma,ready:r.ready,biting:r.biting,
      fcClosed:r.fcClosed,via:r.via,wholeC:r.wholeC,conflict:r.conflict,
      ladderDay:r.ladderDay,otherDayRows:r.otherDayRows});
  },[city]);

  React.useEffect(()=>{load();const iv=setInterval(load,120000);return()=>clearInterval(iv);},[load]);

  // V13.4.218: probe which cities are actually trading right now. These ladders
  //   open and close on their own schedule -- Houston had no open market at 01:45
  //   while ten others did -- so a pill that looks identical to the rest and then
  //   errors on click is a worse lie than a pill marked closed. Cheap probe:
  //   limit=1, once per mount.
  const[open,setOpen]=React.useState(null);
  React.useEffect(()=>{let alive=true;
    (async()=>{
      const hits=await Promise.all(_WX_CITIES.map(async c=>
        ({id:c.id,state:await _wxProbe(c.series)})));
      // Only a definitive 'closed' disables a pill. 'unknown' stays enabled.
      if(alive)setOpen(new Set(hits.filter(h=>h.state!=='closed').map(h=>h.id)));
    })();
    return()=>{alive=false;};
  },[]);

  const P=weatherPicks;
  const S=state;
  const pct=(v)=>v==null?'—':(v*100).toFixed(0)+'%';
  // Suspect rows are painted SIT-OUT gold, never green: the app's own colour
  //   grammar (green=win, red=loss, gold=stand aside) already says "do not act".
  const edgeColor=(e,suspect)=>(e==null||e<1)?'rgba(255,255,255,0.4)':suspect?'#D4A03A':e>=6?'#23B981':'rgba(255,255,255,0.75)';

  // V13.4.246: was `fixed inset-0 z-50` with a ✕ — a modal popping up over the
  //   board, inconsistent with Sports' in-flow swap right next to it in the
  //   same header toggle. Same container shape as SportsView now.
  return (
    <div className="w-full min-h-0">
      <div className="tara-weather-body max-w-[1000px] mx-auto">
        <section className="tara-specialist-brief tara-specialist-brief--weather" aria-label="Weather picks flow">
          <div>
            <div className="tara-specialist-brief__eyebrow">SPECIALIST LANE · WEATHER</div>
            <h2>Weather</h2>
            <p>Live picks are the buckets that can actually be traded now. Paper picks stay visible for learning; settled picks are the record.</p>
          </div>
          <div className="tara-specialist-brief__legend">
            <span><i className="tara-legend-dot tara-legend-dot--live"/>live</span>
            <span><i className="tara-legend-dot tara-legend-dot--paper"/>paper</span>
            <span><i className="tara-legend-dot tara-legend-dot--record"/>settled</span>
          </div>
        </section>
        <div className="flex items-center justify-between mb-5">
          <div>
            {/* The ladder's day is on screen permanently now. A wrong-day ladder
                looked identical to a right one until it produced a trade. */}
            <div className="text-[10px] uppercase tracking-[0.18em] font-bold mb-1" style={{color:'rgba(255,255,255,0.85)'}}>
              Weather · daily high
              {S.ladderDay&&<span style={{color:'rgba(255,255,255,0.38)'}}> · {S.ladderDay} in {city.label}</span>}
              {S.otherDayRows>0&&<span style={{color:'#D4A03A'}}> · {S.otherDayRows} other-day contract{S.otherDayRows===1?'':'s'} excluded</span>}
            </div>
          </div>
          <button onClick={onClose} className="px-3 py-1.5 rounded-lg text-[10px] uppercase font-bold tracking-wider border transition-colors" style={{color:T2_GOLD,borderColor:T2_GOLD_BORDER,background:T2_GOLD_GLOW}}>← Back to BTC</button>
        </div>

        {/* V13.4.224: twenty pills wrapping cost 391px of height at 320px — the
            whole first screen was a city list before a single number appeared.
            On a phone they become one horizontal strip you swipe; the strip is a
            real overflow-x:auto scroller, so nothing is ever unreachable. They
            still wrap normally at sm and up, where the height is affordable. */}
        <label className="weather-city-select">City market <select aria-label="Weather city market" value={cityId} onChange={e=>setCityId(e.target.value)}>{_WX_CITIES.map(c=>{const closed=open!=null&&!open.has(c.id);return <option key={c.id} value={c.id} disabled={closed&&cityId!==c.id}>{c.label}{closed?' · closed':''}</option>;})}</select></label>
        <div ref={cityStripRef} className="weather-city-strip flex gap-2 mb-4 flex-nowrap overflow-x-auto sm:flex-wrap sm:overflow-visible -mx-1 px-1 pb-1" aria-label="Weather city selection">
          {_WX_CITIES.map(c=>{
            // null = probe still running, so treat every city as available rather
            //   than flashing them all as closed for a second.
            const closed=open!=null&&!open.has(c.id);
            const sel=cityId===c.id;
            return (
            <button key={c.id} onClick={()=>setCityId(c.id)} aria-pressed={sel} disabled={closed}
              title={closed?'no open market for this city right now':c.series+' · NWS '+c.station}
              className="shrink-0 whitespace-nowrap px-3 py-1.5 text-[11px] uppercase tracking-[0.12em] font-bold rounded-full border transition-colors"
              style={closed?{background:'transparent',color:'rgba(255,255,255,0.20)',borderColor:'rgba(255,255,255,0.07)',cursor:'not-allowed'}
                   :sel   ?{background:'rgba(255,255,255,0.10)',color:'rgba(255,255,255,0.92)',borderColor:'rgba(255,255,255,0.28)'}
                          :{background:'transparent',color:'rgba(255,255,255,0.45)',borderColor:'rgba(255,255,255,0.12)'}}>
              {c.label}{closed&&<span className="ml-1.5 text-[9px] normal-case tracking-normal" style={{color:'rgba(255,255,255,0.18)'}}>closed</span>}
            </button>);
          })}
        </div>

        {/* V13.4.353: make the three weather states navigable before the
            evidence table. The full evidence and settled history remain below;
            this strip simply makes live, paper, and record discoverable. */}
        <div className="weather-lane-tabs" role="tablist" aria-label="Weather pick state">
          {[['live','Live opportunities',P.openLive.length],['paper','Paper picks',P.openPaper.length],['settled','Settled record',P.recent.length]].map(([id,label,count])=>(
            <button key={id} role="tab" aria-selected={weatherLane===id} onClick={()=>setWeatherLane(id)} className={weatherLane===id?'is-active':''}>
              <span>{label}</span><b>{count}</b>
            </button>
          ))}
          <span className="weather-lane-tabs__note">{P.scanAt?'last sweep '+new Date(P.scanAt).toLocaleTimeString([], {hour:'numeric',minute:'2-digit'}):'live scan every 5 min'}</span>
        </div>
        {/* V13.4.221: the answer to "what should I be on", above everything else.
            Scanned across every city whose day is underway, not just the pill
            that happens to be selected. */}
        <div className={`weather-live-overview${weatherLane==='live'?' is-live':''}`}>
        {weatherLane==='live'&&<div className="rounded-xl p-4 mb-4"
             style={P.openLive.length?{background:'rgba(35,185,129,0.09)',border:'1px solid rgba(35,185,129,0.34)'}
                                     :{background:'rgba(212,160,58,0.07)',border:'1px solid rgba(212,160,58,0.26)'}}>
          <div className="flex items-baseline justify-between mb-2">
            <span className="uppercase tracking-[0.16em] font-bold text-[9px]"
                  style={{color:P.openLive.length?'#23B981':'#D4A03A'}}>
              {P.openLive.length?'LIVE PICKS':'STANDING DOWN'}
            </span>
            <span className="text-[9px] uppercase tracking-[0.12em]" style={{color:'rgba(255,255,255,0.30)'}}>
              {P.scanAt?'scanned '+new Date(P.scanAt).toLocaleTimeString([], {hour:'2-digit',minute:'2-digit'}):'scanning…'}
            </span>
          </div>
          {P.openLive.length?P.openLive.map(p=>(
            <div key={_wxPickId(p)} className="flex flex-wrap items-baseline gap-x-3 gap-y-1 mb-2 last:mb-0">
              <span className="text-[17px] font-bold" style={{color:'rgba(255,255,255,0.95)'}}>
                {p.cityLabel} <span style={{color:'rgba(255,255,255,0.35)'}}>·</span> {p.sub}
              </span>
              <span className="px-2 py-0.5 rounded-md text-[11px] uppercase tracking-[0.1em] font-bold"
                    style={{background:'rgba(35,185,129,0.20)',color:'#23B981'}}>
                buy {p.side} @ {p.price}c
              </span>
              <span className="text-[11px] tabular-nums" style={{color:'#23B981'}}>+{p.edge}c</span>
              <span className="text-[11px] font-mono" style={{color:'rgba(255,255,255,0.30)'}}>{p.ticker}</span>
              <div className="basis-full text-[11px] leading-relaxed" style={{color:'rgba(255,255,255,0.60)'}}>
                {p.why}. It only loses if The Weather Company disagrees with {p.station} by {p.marginF}°F or more.
              </div>
              {/* The shape of the bet, stated before it is taken. A cheap-looking
                  +Nc on a high price is a lopsided trade and the numbers should
                  say so on the card, not turn up later in the record. */}
              <div className="basis-full text-[11px] leading-relaxed rounded-lg px-2.5 py-2 mt-1"
                   style={{background:'rgba(0,0,0,0.25)',color:'rgba(255,255,255,0.55)'}}>
                Risking <b style={{color:'rgba(255,255,255,0.85)'}}>{p.price}c</b> to make{' '}
                <b style={{color:'#23B981'}}>{100-p.price-_wxFeeCents(p.price)}c</b> after the {_wxFeeCents(p.price)}c fee.{' '}
                Needs a <b style={{color:'rgba(255,255,255,0.85)'}}>{_wxBreakeven(p.price)}%</b> win rate just to break even, and{' '}
                <b style={{color:'#E8455E'}}>one loss undoes {(( p.price+_wxFeeCents(p.price))/Math.max(1,100-p.price-_wxFeeCents(p.price))).toFixed(1)} wins</b>.{' '}
                Size it like that, not like a coin flip.
              </div>
            </div>
          )):(
            <div className="text-[11px] leading-relaxed" style={{color:'rgba(255,255,255,0.60)'}}>
              No city currently has a bucket that is both passed and still paying. That is the normal answer most of the day. Tara only offers a live trade when the arithmetic is there; paper calls remain visible for learning. {P.openPaper.length>0&&<>There are {P.openPaper.length} paper call{P.openPaper.length===1?'':'s'} below.</>}
            </div>
          )}
        </div>}
        <div className="weather-record-strip" aria-label="Weather live and paper records">
          <div><span>LIVE PICKS NOW</span><strong>{P.openLive.length}</strong><small>passed buckets · tradable when still open</small></div>
          <div><span>LIVE PICK RECORD</span><strong>{P.live.w}–{P.live.l}</strong><small>{P.live.hit==null?'no settled live picks':`${Math.round(P.live.hit*100)}% hit rate · quoted-price model`}</small></div>
          <div><span>PAPER OPEN</span><strong>{P.openPaper.length}</strong><small>tracked for learning · no real trade</small></div>
          <div><span>PAPER PICK RECORD</span><strong>{P.paper.w}–{P.paper.l}</strong><small>{P.paper.hit==null?'no settled paper picks':`${Math.round(P.paper.hit*100)}% hit rate · separate from live`}</small></div>
        </div>
        </div>

        {weatherLane!=='live'&&(
          <section className="weather-lane-preview" aria-label={weatherLane==='paper'?'Open paper picks':'Settled weather record'}>
            <div className="weather-lane-preview__head">
              <div><span>{weatherLane==='paper'?'PAPER PICKS':'SETTLED RECORD'}</span><h3>{weatherLane==='paper'?'Tracked for learning, not money':'What has resolved so far'}</h3></div>
              <small>{weatherLane==='paper'?`${P.paper.w}–${P.paper.l} settled · ${P.paper.open} open`:`${P.live.w+P.paper.w}–${P.live.l+P.paper.l} settled across both lanes`}</small>
            </div>
            {(weatherLane==='paper'?P.openPaper:P.recent).slice(0,6).length?(
              <div className="weather-lane-preview__list">
                {(weatherLane==='paper'?P.openPaper:P.recent).map(p=>(
                  <div className="weather-lane-preview__row" key={_wxPickId(p)}>
                    <span>{p.cityLabel||p.city||'City'} · {p.sub||'bucket'}</span>
                    <b>{p.side||'—'} @{p.price==null?'—':p.price+'c'}</b>
                    <em>{weatherLane==='paper'?'OPEN':(p.result||'VOID').toUpperCase()}</em>
                  </div>
                ))}
              </div>
            ):(<div className="weather-lane-preview__empty">No {weatherLane==='paper'?'paper picks are open':'settled weather picks yet'}. The lane is still visible below for the next scan.</div>)}
          </section>
        )}

        <details key={weatherLane} className="tara-research">
        <summary>City forecast · bucket ladder · methodology & complete history</summary>
        {S.err?(
          <div className="bg-[#0A0A0E] border border-[#1B1B22] rounded-[10px] p-4 text-[12px]" style={{color:'#E8455E'}}>{S.err}</div>
        ):S.loading&&!S.rows.length?(
          <div className="bg-[#0A0A0E] border border-[#1B1B22] rounded-[10px] p-6 text-center text-[12px]" style={{color:'rgba(255,255,255,0.45)'}}>loading…</div>
        ):(
          <div>
            {/* V13.4.217: the gate states its own case, so a blank edge column
                never reads as a bug. */}
            <div className="rounded-xl p-3 mb-4 flex items-start gap-3"
                 style={S.conflict?{background:'rgba(232,69,94,0.10)',border:'1px solid rgba(232,69,94,0.38)'}
                      :S.ready   ?{background:'rgba(35,185,129,0.08)',border:'1px solid rgba(35,185,129,0.30)'}
                                 :{background:'rgba(212,160,58,0.08)',border:'1px solid rgba(212,160,58,0.30)'}}>
              <div className="text-[15px] leading-none mt-0.5">{S.conflict?'⚠':S.ready?'✓':'⏸'}</div>
              <div className="text-[11px] leading-relaxed" style={{color:'rgba(255,255,255,0.72)'}}>
                <span className="uppercase tracking-[0.14em] font-bold text-[9px] block mb-1"
                      style={{color:S.conflict?'#E8455E':S.ready?'#23B981':'#D4A03A'}}>
                  {S.conflict?'no edges · reading disagrees with the market'
                   :S.ready?(S.biting?'live · truncation biting':'live · edges shown'):'standing down · too early'}
                </span>
                {S.conflict
                  ?<>The market pays <b>{S.conflict.bid}c</b> for <b>{S.conflict.sub}</b>, which my reading of the day cannot support{S.conflict.model!=null?<> (model says {Math.round(S.conflict.model*100)}%)</>:<> (already impossible on the observed max)</>}. One of us is badly wrong and it is probably me — the book has thousands of contracts and the settlement feed, this panel has a free weather API and a station picked by hand. <b>All edges are suppressed.</b> Most likely {city.station} is not the station Kalshi settles on for this city, or the two sources genuinely split at a bucket boundary. Do not trade this city until the reading and the book agree.</>
                  :S.ready
                  ?(S.biting
                    ?<>The day has already climbed to {S.runMax!=null?S.runMax.toFixed(1)+'°F':'near forecast'}{S.fc!=null?<> against a {S.fc}°F forecast</>:<>, and NWS has closed today&rsquo;s forecast period, so the observed max is now the centre</>}. Buckets below that are dead as <b>arithmetic</b>, and that is the one thing here worth more than the market&rsquo;s opinion.</>
                    :<>Past midday with {S.obsN} readings in. Edges are shown, but the day has not climbed near {S.fc!=null?S.fc+'°F':'its peak'} yet, so the truncation is not doing much work — most of this is still a curve, not a fact.</>)
                  :<>It is {S.hourLocal!=null?S.hourLocal+':00':'early'} local with {S.obsN} reading{S.obsN===1?'':'s'} logged. <b>Buy edges are deliberately hidden until midday.</b> Overnight the model is a normal curve fitted to one rounded integer forecast, and the book already prices that plus full ensemble spread — it would print edges that are just the curve being too wide. Percentages stay visible so you can see the shape. Already-passed buckets still show their edge, because that part is measured.</>}
              </div>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
              {[['NWS forecast high',S.fc!=null?S.fc+'°F':(S.fcClosed?'period closed':'—')],
                ['observed max today',S.runMax!=null?S.runMax.toFixed(1)+'°F':'—'],
                ['observations',S.obsN+' · '+(S.hourLocal!=null?S.hourLocal+':00 local':'—')],
                ['model spread (σ)',(S.sigma!=null?'±'+S.sigma+'°':'—')+(S.wholeC?' · ±0.9 read':'')]].map(([k,v])=>(
                <div key={k} className="bg-[#0A0A0E] border border-[#1B1B22] rounded-[10px] p-3">
                  <div className="text-[9px] uppercase tracking-[0.14em] font-bold mb-1" style={{color:'rgba(255,255,255,0.45)'}}>{k}</div>
                  <div className="text-[16px] tabular-nums font-bold" style={{color:'rgba(255,255,255,0.92)'}}>{v}</div>
                </div>
              ))}
            </div>

            <div className="bg-[#0A0A0E] border border-[#1B1B22] rounded-[10px] overflow-hidden mb-4">
              {/* V13.4.224: below sm this stops being a table. Measured at 320px,
                  the 12-column grid handed each data column 22px while its
                  contents needed 28-45px, so "YES +5c" and the open-interest
                  figures were crushed inside their own cells. Shrinking type
                  further would only have made an unreadable table smaller.
                  On a phone each bucket becomes a two-line block instead: the
                  bucket and the thing to DO on the first line, the supporting
                  numbers on a second. The table returns intact at sm and up. */}
              <div className="hidden sm:grid grid-cols-12 gap-2 px-4 py-2 text-[9px] uppercase tracking-[0.14em] font-bold" style={{color:'rgba(255,255,255,0.45)',borderBottom:'1px solid rgba(255,255,255,0.08)'}}>
                <div className="col-span-4">bucket</div>
                <div className="col-span-2 text-right">yes / no ask</div>
                <div className="col-span-2 text-right">model</div>
                <div className="col-span-2 text-right">best side</div>
                <div className="col-span-2 text-right">open int.</div>
              </div>
              {S.rows.map((r,i)=>{
                const side=(!r.best
                  ?<span style={{color:'rgba(255,255,255,0.28)'}}>{S.ready?'no edge':'held'}</span>
                  :r.best.edge<1
                    ?<span style={{color:'rgba(255,255,255,0.28)'}}>no edge</span>
                    :<><span className="text-[9px] uppercase tracking-wider mr-1" style={{opacity:0.75}}>{r.best.side}</span>{'+'+r.best.edge.toFixed(0)+'c'}</>);
                const tag=r.best&&r.best.edge>=1&&r.suspect
                  ?<span className="text-[8px] uppercase tracking-wider font-bold" style={{color:'#D4A03A'}}>model off?</span>
                  :r.best&&r.best.edge>=1&&r.dead
                    ?<span className="text-[8px] uppercase tracking-wider font-bold" style={{color:'#23B981'}}>arithmetic</span>
                    :null;
                return(
                <div key={i} style={{borderTop:i?'1px solid rgba(255,255,255,0.05)':'none',opacity:r.dead?0.45:1}}>
                  {/* phone: two lines, action first */}
                  <div className="sm:hidden px-3 py-2.5 text-[12px] tabular-nums">
                    <div className="flex items-baseline justify-between gap-2">
                      <span className="min-w-0 truncate" style={{color:'rgba(255,255,255,0.88)'}}>
                        {r.sub}
                        {r.dead&&<span className="ml-1.5 text-[9px] uppercase tracking-wider" style={{color:'#D4A03A'}}>passed</span>}
                      </span>
                      <span className="shrink-0 font-bold text-right" style={{color:edgeColor(r.best?r.best.edge:null,r.suspect)}}>{side}</span>
                    </div>
                    <div className="flex items-baseline justify-between gap-2 mt-1 text-[10px]">
                      <span style={{color:'rgba(255,255,255,0.45)'}}>
                        yes {r.ask}c · no {r.noAsk}c · model {pct(r.p)} · oi {r.oi}
                      </span>
                      {tag}
                    </div>
                  </div>
                  {/* sm and up: the table */}
                  <div className="hidden sm:grid grid-cols-12 gap-2 px-4 py-2 text-[12px] tabular-nums items-baseline">
                    <div className="col-span-4" style={{color:'rgba(255,255,255,0.88)'}}>
                      {r.sub}
                      {r.dead&&<span className="ml-2 text-[9px] uppercase tracking-wider" style={{color:'#D4A03A'}}>passed</span>}
                    </div>
                    <div className="col-span-2 text-right" style={{color:'rgba(255,255,255,0.6)'}}>{r.ask}c / {r.noAsk}c</div>
                    <div className="col-span-2 text-right" style={{color:'rgba(255,255,255,0.88)'}}>{pct(r.p)}</div>
                    {/* A side label only appears when there is something to DO.
                        Both sides non-positive means the spread ate it, so say
                        "no edge" rather than printing "NO -0c" -- a side name
                        next to a negative number reads like an instruction. */}
                    <div className="col-span-2 text-right font-bold" style={{color:edgeColor(r.best?r.best.edge:null,r.suspect)}}>
                      {side}
                      {tag&&<span className="block">{tag}</span>}
                    </div>
                    <div className="col-span-2 text-right" style={{color:'rgba(255,255,255,0.4)'}}>{r.oi}</div>
                  </div>
                </div>);
              })}
            </div>

            {/* V13.4.221: the record. Two tiers kept apart on purpose — mixing a
                measured edge with a model's opinion produces a number that
                describes neither. */}
            <div className="bg-[#0A0A0E] border border-[#1B1B22] rounded-[10px] p-4 mb-4">
              <div className="flex items-baseline justify-between mb-3">
                <span className="text-[9px] uppercase tracking-[0.14em] font-bold" style={{color:'rgba(255,255,255,0.85)'}}>Record</span>
                <span className="text-[9px] uppercase tracking-[0.12em]" style={{color:'rgba(255,255,255,0.28)'}}>settled by Kalshi, not by this panel</span>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {[['live · arithmetic',P.live,'#23B981','Trades actually offered. A bucket the day had already passed, bought on the NO side.'],
                  ['paper · model',P.paper,'#D4A03A','Never offered as a trade. Logged to find out whether the forecast half is worth anything.']]
                  .map(([label,t,col,blurb])=>(
                  <div key={label} className="rounded-lg p-3" style={{background:'rgba(255,255,255,0.03)',border:'1px solid rgba(255,255,255,0.07)'}}>
                    <div className="text-[9px] uppercase tracking-[0.14em] font-bold mb-1.5" style={{color:col}}>{label}</div>
                    {t.n===0?(
                      <div className="text-[11px] leading-relaxed" style={{color:'rgba(255,255,255,0.45)'}}>
                        Nothing settled yet{t.open?` · ${t.open} open`:''}. A record starts the first time one of these resolves.
                      </div>
                    ):(<>
                      <div className="flex items-baseline gap-2 flex-wrap">
                        <span className="text-[19px] font-bold tabular-nums" style={{color:'rgba(255,255,255,0.95)'}}>{t.w}–{t.l}</span>
                        <span className="text-[12px] tabular-nums" style={{color:'rgba(255,255,255,0.55)'}}>{Math.round(t.hit*100)}%</span>
                        <span className="text-[12px] tabular-nums font-bold" style={{color:t.pnl>=0?'#23B981':'#E8455E'}}>
                          {t.pnl>=0?'+':''}{t.pnl}c
                        </span>
                        {t.open>0&&<span className="text-[10px]" style={{color:'rgba(255,255,255,0.35)'}}>{t.open} open</span>}
                      </div>
                      {/* Win rate alone is meaningless here: every pick is bought at a
                          different price, so the bar to clear moves with it. */}
                      <div className="text-[10px] mt-1 tabular-nums" style={{color:'rgba(255,255,255,0.42)'}}>
                        breakeven {Math.round(t.breakeven)}% at the average price paid ·{' '}
                        <span style={{color:t.hit*100>=t.breakeven?'#23B981':'#E8455E'}}>
                          {t.hit*100>=t.breakeven?'ahead of it':'behind it'}
                        </span>
                      </div>
                      {t.n<30&&<div className="text-[10px] mt-1" style={{color:'rgba(255,255,255,0.35)'}}>
                        {t.n} settled. Far too few to mean anything — this is bookkeeping, not evidence.
                      </div>}
                    </>)}
                    <div className="text-[10px] mt-2 leading-relaxed" style={{color:'rgba(255,255,255,0.32)'}}>{blurb}</div>
                  </div>
                ))}
              </div>
              {/* V13.4.222: what a backtest says to expect, so the live record is
                  read against something instead of against hope. Replayed over 6
                  days x 10 cities on real NWS observations and real Kalshi
                  candlesticks, walking each day forward hour by hour with no
                  hindsight. */}
              <div className="mt-3 pt-3" style={{borderTop:'1px solid rgba(255,255,255,0.07)'}}>
                <div className="text-[9px] uppercase tracking-[0.14em] font-bold mb-2" style={{color:'rgba(255,255,255,0.45)'}}>What to expect · backtested 6 days, 10 cities</div>
                <div className="grid grid-cols-3 gap-2 mb-2">
                  {[['signal','64 / 64','every bucket called dead settled at zero','#23B981'],
                    ['already priced','60 / 64','NO was at 100c before the signal existed','#D4A03A'],
                    ['tradeable','2 / 64','under 95c — both won','#23B981']].map(([k,v,note,col])=>(
                    <div key={k}>
                      <div className="text-[9px] uppercase tracking-[0.12em] mb-0.5" style={{color:'rgba(255,255,255,0.40)'}}>{k}</div>
                      <div className="text-[15px] font-bold tabular-nums" style={{color:col}}>{v}</div>
                      <div className="text-[9px] leading-snug mt-0.5" style={{color:'rgba(255,255,255,0.35)'}}>{note}</div>
                    </div>
                  ))}
                </div>
                <div className="text-[10px] leading-relaxed" style={{color:'rgba(255,255,255,0.48)'}}>
                  In that six-day, ten-city sample, roughly <b style={{color:'rgba(255,255,255,0.75)'}}>two tradeable signals per week</b> appeared. That is historical context, not a forecast for the current city list. The market usually priced a passed bucket first; the opportunity was the short gap before repricing. Boston 16 Aug briefly showed NO at 41c for a bucket already 2°F below the observed high, with 1,583 contracts trading that hour, then moved to 0/1 by the next. That is why a qualifying pick pages you on Discord instead of waiting to be found.
                  <br/><span style={{color:'#D4A03A'}}>Two backtested trades do not establish a live edge.</span> Keep the live record and paper record separate; the settled results above are what will test the approach.
                </div>
              </div>

              {/* V13.4.222: the measurement the 2°F margin actually rests on.
                  Everything in this lane assumes NWS is a usable stand-in for the
                  settlement source. That was an assumption for four versions; this
                  is it measured. */}
              <div className="mt-3 pt-3" style={{borderTop:'1px solid rgba(255,255,255,0.07)'}}>
                <div className="text-[9px] uppercase tracking-[0.14em] font-bold mb-2" style={{color:'rgba(255,255,255,0.45)'}}>Why the margin is 2°F · measured over 60 city-days</div>
                <div className="text-[10px] leading-relaxed" style={{color:'rgba(255,255,255,0.48)'}}>
                  NWS and whatever Kalshi settles on land on the same degree only <b style={{color:'rgba(255,255,255,0.75)'}}>72%</b> of the time (43 of 60). That sounds fatal and mostly is not, because <b>direction decides it</b>. A dead call is only wrong when NWS reads <i>higher</i> than what settles.
                  <div className="flex flex-wrap gap-x-5 gap-y-1 mt-1.5 mb-1.5 tabular-nums">
                    <span style={{color:'#E8455E'}}>NWS too high: 6 of 60 — every one by exactly 1°F, never 2</span>
                    <span style={{color:'rgba(255,255,255,0.45)'}}>NWS too low: 11 of 60 — harmless, the bucket stays dead</span>
                  </div>
                  So a 2°F margin sits a full degree beyond the worst disagreement ever observed, and <b style={{color:'#23B981'}}>would have produced zero losing calls in 60 city-days</b>. A 1°F margin also scored zero, but with no headroom at all — which is why the gate is 2 and thin one-degree buckets are refused.
                  <br/><span style={{color:'#D4A03A'}}>The limit of this:</span> 60 days is one weather regime and 6 adverse cases. A 2°F split is not impossible, it just has not happened yet. Denver's station reads consistently low (5 of its 6 days), so its dead calls are extra safe; Seattle read high twice. Per-city calibration needs far more than a week.
                </div>
              </div>

              {P.recent.length>0&&(
                <div className="mt-3 pt-3" style={{borderTop:'1px solid rgba(255,255,255,0.07)'}}>
                  <div className="text-[9px] uppercase tracking-[0.14em] font-bold mb-2" style={{color:'rgba(255,255,255,0.45)'}}>Settled</div>
                  <div className="space-y-1">
                    {P.recent.slice(0,8).map(p=>(
                      <div key={_wxPickId(p)} className="flex items-baseline gap-2 text-[11px] tabular-nums flex-wrap">
                        <span className="uppercase tracking-wider text-[8px] font-bold px-1.5 py-0.5 rounded"
                              style={p.tier==='live'?{background:'rgba(35,185,129,0.15)',color:'#23B981'}
                                                    :{background:'rgba(212,160,58,0.15)',color:'#D4A03A'}}>{p.tier}</span>
                        <span style={{color:'rgba(255,255,255,0.70)'}}>{p.cityLabel} {p.sub}</span>
                        <span style={{color:'rgba(255,255,255,0.40)'}}>{p.side} @{p.price}c</span>
                        <span className="font-bold" style={{color:p.result==='win'?'#23B981':p.result==='loss'?'#E8455E':'#D4A03A'}}>
                          {p.result==='win'?'WIN':p.result==='loss'?'LOSS':'VOID'}
                        </span>
                        <span style={{color:(p.pnl||0)>=0?'#23B981':'#E8455E'}}>{(p.pnl||0)>=0?'+':''}{p.pnl}c</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div className="bg-[#0A0A0E] border border-[#1B1B22] rounded-[10px] p-4 text-[11px] leading-relaxed" style={{color:'rgba(255,255,255,0.55)'}}>
              <div className="text-[9px] uppercase tracking-[0.14em] font-bold mb-2" style={{color:'rgba(255,255,255,0.85)'}}>Read this before trading it</div>
              <div className="mb-1.5"><span style={{color:'#D4A03A'}}>Basis risk.</span> Kalshi settles this series on <b>The Weather Company</b>. This panel reads <b>NWS {city.station}</b> — free and open, but a different source. They track closely and can still differ by a degree exactly at a bucket boundary, which is where the money is. &ldquo;Passed&rdquo; means passed on the NWS proxy, not settled fact.</div>
              <div className="mb-1.5"><span style={{color:'#D4A03A'}}>Record is not proof of edge.</span> This lane has {P.live.n} settled live picks and {P.paper.n} settled paper picks. Paper results are not executed trades, and a hit rate without entry prices does not establish profitability. The bucket probabilities above are model outputs, not a measured edge.</div>
              <div className="mb-1.5"><span style={{color:'#D4A03A'}}>Big edges are a warning, not a gift.</span> Any row 10c+ away from a book with 500+ open interest is marked <b style={{color:'#D4A03A'}}>model off?</b> and painted gold rather than green. Thousands of contracts deep on the other side means the crowd is probably reading the day better than a plain normal curve is. Treat those rows as the model failing, not the market.</div>
              <div className="mb-1.5"><span style={{color:'#23B981'}}>Arithmetic.</span> A row tagged <b style={{color:'#23B981'}}>arithmetic</b> is the one signal here that is not a forecast: the day has physically passed that bucket, so it settles at zero, and the NO side is worth a certain 100c. That is the reason this lane exists. It is still bounded by the basis note above — passed on NWS, not on the settlement source.</div>
              {S.wholeC&&<div className="mb-1.5"><span style={{color:'#D4A03A'}}>Coarse station.</span> {city.station} is reporting whole degrees <b>Celsius</b> today, so each reading carries ±0.9°F — wider than these 1°F buckets. The passed-bucket floor is backed off by that half-step, which kills fewer buckets on purpose: a bucket wrongly left alive costs a missed trade, one wrongly killed tells you to sell something that can still win.</div>}
              <div>The model is deliberately plain: final high ~ Normal(NWS forecast, σ), truncated below today&rsquo;s observed max, with σ widest before peak heating (~4pm local) and tightening after. The genuinely solid part is the truncation — once the day has already hit {S.runMax!=null?S.runMax.toFixed(1)+'°F':'a given max'}, lower buckets are arithmetic, not forecasting.</div>
            </div>
          </div>
        )}
        </details>
      </div>
    </div>
  );
}

function SportsView({onClose}){
  const[data,setData]=React.useState(null);
  const[err,setErr]=React.useState(null);
  const[sourceWarning,setSourceWarning]=React.useState(null);
  const[refreshKey,setRefreshKey]=React.useState(0);
  const[refreshing,setRefreshing]=React.useState(false);
  const[tab,setTab]=React.useState('upcoming');
  const[sportFilter,setSportFilter]=React.useState('all');
  // Which date groups the user has explicitly opened or closed. Anything not
  // in here falls back to the default (nearest few open) — so changing tab or
  // sport re-applies the default instead of stranding a stale open/closed set.
  const[dateOverrides,setDateOverrides]=React.useState({});
  React.useEffect(()=>{setDateOverrides({});},[tab,sportFilter]);
  // Local clock tick. 30s, no network, no cloud read — the only thing it does
  // is re-evaluate which fixtures have kicked off since the payload was built.
  const[nowMs,setNowMs]=React.useState(()=>Date.now());
  React.useEffect(()=>{const iv=setInterval(()=>setNowMs(Date.now()),30000);return()=>clearInterval(iv);},[]);

  // LIVE source first, bundled copy as fallback.
  //
  // The board is regenerated every 15 minutes by a GitHub Actions job and
  // committed to the public sports-model repo, which raw.githubusercontent
  // serves with `Access-Control-Allow-Origin: *`. Reading it there means an
  // update reaches every viewer within one CDN window (~5 min) instead of
  // waiting on a Vercel redeploy — the app is no longer shipping a snapshot
  // frozen at build time.
  //
  // The bundled /sports.json is the fallback if GitHub is unreachable or
  // rate-limits. It may be older; the "Snapshot · N min old" header is
  // computed from the payload's own `generated` field either way, so a stale
  // fallback announces itself rather than passing as current.
  React.useEffect(()=>{
    let alive=true;
    let timer=null;
    let inFlight=false;

    const load=(force)=>{
      const now=Date.now();
      // Module cache serves repeat opens without a refetch. `force` skips it
      // for the poll below, which exists precisely to get newer data.
      if(!force&&_sportsCache.data&&now-_sportsCache.at<_SPORTS_TTL_MS){
        setData(_sportsCache.data);
        setSourceWarning(_sportsCache.warning||null);
        return Promise.resolve();
      }
      if(inFlight)return Promise.resolve();
      inFlight=true;
      setRefreshing(true);
      return loadSportsBoard({previous:_sportsCache.data,now})
        .then(({data:j,warning})=>{
          if(!alive)return;
          _sportsCache={data:j,at:Date.now(),warning};
          setData(j);setErr(null);setSourceWarning(warning);
        })
        // A failed REFRESH must not blank a board that is already on screen —
        // only report the error if there is nothing to show.
        .catch(e=>{if(alive)setErr(String(e.message||e));})
        .finally(()=>{inFlight=false;if(alive)setRefreshing(false);});
    };

    load(refreshKey>0);

    // Keep it current without the user touching anything. 5 minutes matches
    // raw.githubusercontent's own CDN cache (max-age=300), so polling faster
    // would return the identical bytes and just burn bandwidth.
    //
    // Paused while the tab is hidden: a board nobody is looking at does not
    // need refreshing, and this app is often left open for hours. Refetch on
    // becoming visible again so returning to the tab shows current data
    // rather than whatever was there when you left.
    const POLL_MS=5*60*1000;
    const start=()=>{if(!timer)timer=setInterval(()=>load(true),POLL_MS);};
    const stop=()=>{if(timer){clearInterval(timer);timer=null;}};
    const onVis=()=>{
      if(document.hidden){stop();}
      else{
        start();
        if(Date.now()-_sportsCache.at>POLL_MS)load(true);
      }
    };
    if(!document.hidden)start();
    document.addEventListener('visibilitychange',onVis);

    return()=>{alive=false;stop();document.removeEventListener('visibilitychange',onVis);};
  },[refreshKey]);

  // Everything for the current tab, before the sport filter — this is what the
  // sport buttons count, so their numbers do not change as you click between
  // them.
  const tabRows=React.useMemo(()=>{
    if(!data)return[];
    // ONE board of everything unresolved, plus the settled record. "Upcoming"
    // and "Open" were 94% the same rows — 17 of 18 open predictions also sat in
    // upcoming, the same pick twice with two framings. Tracked-or-not is a
    // property of a row (a badge in SportsRow), not a filter any more — the
    // board tab shows everything the model priced, sliced only by sport.
const src=tab==='record'?data.settled:(data.board||data.upcoming);
    let list=(src||[]);
    if(tab==='upcoming')list=list.filter(r=>sportsIsFuture(r.start,nowMs));
    if(tab==='tracked')list=list.filter(r=>r.tracked);
    if(tab!=='record'){
      // Re-stamp advice from the CURRENT time before anything filters on it.
      // The export said TAKE at 19:23; by 19:50 two of those had first pitch
      // behind them, and the app was still presenting them as actionable.
      list=list.map(r=>sportsHasStarted(r.start,nowMs)&&r.advice!=='STARTED'
        ?{...r,advice:'STARTED',_wasAdvice:r.advice}:r);
    }
        return list;
  },[data,tab,nowMs]);

  const sportCounts=React.useMemo(()=>{
    const m={};
    tabRows.forEach(r=>{const k=r.sport||'other';m[k]=m[k]||{n:0,label:r.sport_label||k};m[k].n++;});
    return Object.entries(m).sort((a,b)=>b[1].n-a[1].n);
  },[tabRows]);

  // A filter that selects a sport with nothing in it would show an empty list
  // with no clue why, so fall back to 'all' when the selection disappears.
  React.useEffect(()=>{
    if(sportFilter!=='all'&&!sportCounts.some(([k])=>k===sportFilter))setSportFilter('all');
  },[sportCounts,sportFilter]);

  const rows=React.useMemo(()=>(
    sportFilter==='all'?tabRows:tabRows.filter(r=>(r.sport||'other')===sportFilter)
  ),[tabRows,sportFilter]);

  // DATE first, then sport, then league. Rows arrive already in chronological
  // order, so insertion order is the display order; settled results read
  // newest-first instead.
  const grouped=React.useMemo(()=>{
    const days=[];const didx={};
    rows.forEach(r=>{
      const dk=sportsDateKey(r.start);
      const key=dk||'tbc';
      if(didx[key]===undefined){didx[key]=days.length;days.push({key,dateKey:dk,sports:[],sidx:{},n:0,aligned:0});}
      const day=days[didx[key]];
      day.n++;
      if(r.advice==='ALIGNED')day.aligned++;
      const sp=r.sport||'other';
      if(day.sidx[sp]===undefined){day.sidx[sp]=day.sports.length;day.sports.push({sport:sp,label:r.sport_label||sp,leagues:[],lidx:{}});}
      const s=day.sports[day.sidx[sp]];
      const lg=r.league||'Unclassified';
      if(s.lidx[lg]===undefined){s.lidx[lg]=s.leagues.length;s.leagues.push({league:lg,rows:[]});}
      s.leagues[s.lidx[lg]].rows.push(r);
    });
    // Undated rows last regardless of where they appeared in the source.
    days.sort((a,b)=>(a.key==='tbc')-(b.key==='tbc'));
    return days;
  },[rows]);

  // Default: the two nearest dates open. Opening 30 days of soccer at once
  // buries the near-term slate the view exists to surface.
  const isDayOpen=(key,idx)=>dateOverrides[key]!==undefined?dateOverrides[key]:idx<2;
  // Takes the CURRENT state rather than re-deriving it — the default depends on
  // the group's index, which this helper has no way to know, and assuming
  // "closed" would make the first click on an open group do nothing.
  const toggleDay=(key,openNow)=>setDateOverrides(o=>({...o,[key]:!openNow}));
  const setAllDays=open=>{const m={};grouped.forEach(d=>{m[d.key]=open;});setDateOverrides(m);};

  const rec=data&&data.record;
  const settlementStatus=sportsSettlementStatus(data,nowMs);
  const paperStatus=sportsPaperSettlementStatus(data);
  const freshnessWarning=data?sportsFreshnessStatus(data,nowMs).warning:null;
  // V13.4.258: was a raised card with a shadow and a corner stamp per tile.
  //   Now a hairline cell inside one joined panel -- the tiles read as one
  //   instrument rather than four competing boxes.
  const card='bg-[#0A0A0E] p-3.5 relative';

  return(
    <div className="w-full min-h-0">
      <div className="tara-sports-body max-w-[1200px] mx-auto">
        <section className="tara-specialist-brief tara-specialist-brief--sports" aria-label="Sports picks flow">
          <div>
            <div className="tara-specialist-brief__eyebrow">SPECIALIST LANE · SPORTS</div>
            <h2>Sports</h2>
            <p>Board shows every fixture Tara priced. A tracked badge means it counts toward the record; Record shows settled picks only.</p>
          </div>
          <div className="tara-specialist-brief__legend">
            <span><i className="tara-legend-dot tara-legend-dot--upcoming"/>upcoming</span>
            <span><i className="tara-legend-dot tara-legend-dot--tracked"/>tracked</span>
            <span><i className="tara-legend-dot tara-legend-dot--record"/>record</span>
          </div>
        </section>
        <div className="flex items-center justify-between mb-5">
          <div>
            <div className="flex items-baseline gap-2 mb-1">
              <span className="text-[10px] uppercase font-bold tracking-[0.18em]" style={{color:T2_GOLD}}>Sports</span>
              <span className="text-[9px] uppercase tracking-wider text-[#EDEDED]/30">
                {data&&data.generated?'updated '+new Date(data.generated).toLocaleString('en-US',{day:'2-digit',month:'short',hour:'numeric',minute:'2-digit',timeZone:'America/New_York'})+' ET':'prediction record'}
              </span>
            </div>
          </div>
          {/* Returns to the BTC board — same thing the header toggle does. */}
          <div className="flex gap-2">
            <button disabled={refreshing} onClick={()=>setRefreshKey(k=>k+1)} className="px-3 py-1.5 rounded-lg text-[10px] uppercase font-bold border disabled:opacity-40" style={{color:T2_GOLD,borderColor:T2_GOLD_BORDER}}>{refreshing?'Refreshing…':'Refresh results'}</button>
            <button onClick={onClose} className="px-3 py-1.5 rounded-lg text-[10px] uppercase font-bold tracking-wider border transition-colors" style={{color:T2_GOLD,borderColor:T2_GOLD_BORDER,background:T2_GOLD_GLOW}}>← Back to BTC</button>
          </div>
        </div>

        {err&&<div role="alert" className="rounded-xl border p-4 text-[12px] mb-5" style={{borderColor:'rgba(232,69,94,0.3)',background:'rgba(232,69,94,0.06)',color:SPORTS_RED}}>Sports results are unavailable ({err}). Use Refresh results to retry.</div>}
        {sourceWarning&&<div role="status" className="rounded-xl border p-3 text-[12px] mb-3" style={{color:T2_GOLD,borderColor:T2_GOLD_BORDER}}>{sourceWarning}</div>}
        {paperStatus.available&&<div role="status" className="rounded-xl border border-[#24242E] p-3 mb-3 text-[12px]" data-testid="sports-paper-settlement">
          <b style={{color:paperStatus.overdue||paperStatus.sourceErrors?T2_GOLD:SPORTS_GREEN}}>Paper settlement · {paperStatus.overdue||paperStatus.sourceErrors?'needs review':'up to date'}</b>
          <div className="text-[#EDEDED]/60 mt-1">{paperStatus.pending} pending · {paperStatus.overdue} past-date · {paperStatus.nonBinary} partial payouts · {paperStatus.cancelled} cancelled. Non-binary results are excluded from W/L, not counted as losses or assumed refunds.</div>
          {paperStatus.attention.length>0&&<details className="mt-2"><summary>Exact pending reasons</summary><ul className="mt-2 space-y-1">{paperStatus.attention.map((row,index)=><li key={`${row.id}-${index}`}><span>{row.event||`Record ${row.id}`}</span> — {row.blocker?.detail||'No verified settlement match'} <small>({row.blocker?.code||'unclassified'})</small></li>)}</ul></details>}
        </div>}
        {freshnessWarning&&<div role="status" data-testid="sports-freshness-warning" className="rounded-xl border p-3 text-[12px] mb-3" style={{color:T2_GOLD,borderColor:T2_GOLD_BORDER}}>{freshnessWarning}</div>}
        {!data&&!err&&<div className="text-[#EDEDED]/40 text-sm">Loading…</div>}

        {data&&(<div className="sports-data-stack">
          <EvidenceMetrics items={[
            ['Tracked picks',(data.board||data.upcoming||[]).filter(r=>r.tracked&&r.result==null).length,'Committed picks awaiting a result'],
            ['Upcoming fixtures',(data.board||data.upcoming||[]).filter(r=>sportsIsFuture(r.start,nowMs)).length,'Known future start times'],
            ['Settled record',rec?`${rec.wins}W · ${rec.losses}L`:'Unavailable','Tracked picks only'],
            ['Snapshot',data.generated?new Date(data.generated).toLocaleTimeString():'Unavailable','Quoted prices are snapshots'],
          ]}/>
          <div className="text-[11px] leading-relaxed mb-3" style={{color:settlementStatus.overdue?SPORTS_RED:'#a4a4ae'}}>
            {settlementStatus.pending} tracked pick{settlementStatus.pending===1?'':'s'} awaiting settlement
            {settlementStatus.overdue>0&&<span> · {settlementStatus.overdue} over 48 hours old — settlement needs attention</span>}
            {settlementStatus.lastSettled&&<span> · Last result added {new Date(settlementStatus.lastSettled).toLocaleString('en-US',{month:'short',day:'numeric',hour:'numeric',minute:'2-digit',timeZone:'America/New_York'})} ET</span>}
            {settlementStatus.nonBinary.map(r=><div key={r.id}>{r.event}: {r.reason}</div>)}
          </div>
          {rec&&(<details className="tara-v145-disclosure sports-model-comparison"><summary>Record quality · model vs market log loss</summary>
            <div className={`grid grid-cols-2 sm:grid-cols-4 gap-px mb-4 overflow-hidden ${UI2_PANEL} ${UI2_GRID_BG}`}>
              <div className={card}>
                <div className="text-[9px] uppercase tracking-[0.18em] text-[#EDEDED]/40 font-bold mb-1.5">Record</div>
                <div className="text-2xl sm:text-3xl font-bold text-white" style={T2_MONO_STYLE}>{rec.wins}–{rec.losses}</div>
                <div className="text-[10px] text-[#EDEDED]/35 mt-0.5">{Math.round((rec.win_rate||0)*100)}% win rate</div>
              </div>
              <div className={card}>
                <div className="text-[9px] uppercase tracking-[0.18em] text-[#EDEDED]/40 font-bold mb-1.5">Model log loss</div>
                <div className="text-2xl sm:text-3xl font-bold text-white" style={T2_MONO_STYLE}>{rec.ll==null?'—':rec.ll.toFixed(3)}</div>
                <div className="text-[10px] text-[#EDEDED]/35 mt-0.5">lower is better</div>
              </div>
              <div className={card}>
                <div className="text-[9px] uppercase tracking-[0.18em] text-[#EDEDED]/40 font-bold mb-1.5">Market log loss</div>
                <div className="text-2xl sm:text-3xl font-bold text-white" style={T2_MONO_STYLE}>{rec.mkt_ll==null?'—':rec.mkt_ll.toFixed(3)}</div>
                <div className="text-[10px] text-[#EDEDED]/35 mt-0.5">on the {rec.n_mkt||0} with a price</div>
              </div>
              <div className={card}>
                <div className="text-[9px] uppercase tracking-[0.18em] text-[#EDEDED]/40 font-bold mb-1.5">vs market</div>
                <div className="text-2xl sm:text-3xl font-bold" style={{...T2_MONO_STYLE,color:rec.vs_market==null||Math.abs(rec.vs_market)<.0005?'#c7d3cb':rec.vs_market<0?SPORTS_GREEN:SPORTS_RED}}>{rec.vs_market==null?'—':Math.abs(rec.vs_market)<.0005?'≈0.000':(rec.vs_market>0?'+':'')+rec.vs_market.toFixed(3)}</div>
                <div className="text-[10px] text-[#EDEDED]/35 mt-0.5">{rec.vs_market==null?'not yet comparable':Math.abs(rec.vs_market)<.0005?'tied at shown precision':rec.vs_market<0?'beating market':'behind market'}</div>
              </div>
            </div>
          </details>)}

          {/* Freshness. This board is a snapshot written by export_tara.py, not
              a live feed — the prices below are whatever Kalshi showed when the
              model last ran. Saying so plainly beats letting a 3-hour-old price
              look current because it is rendered in the same style as Tara's
              live BTC numbers. */}
          {(()=>{
            if(!data.generated)return null;
            const ageMin=Math.max(0,Math.round((nowMs-Date.parse(data.generated))/60000));
            const stale=ageMin>=60;
            const started=(data.upcoming||[]).filter(r=>sportsHasStarted(r.start,nowMs)).length;
            return(
              <div className="sports-snapshot-banner rounded-xl border p-3 mb-3 text-[11.5px] leading-relaxed flex flex-wrap items-center gap-x-2 gap-y-1"
                style={{borderColor:stale?'rgba(232,69,94,0.3)':'#24242E',background:stale?'rgba(232,69,94,0.05)':'#111'}}>
                <span className="font-bold" style={{color:stale?SPORTS_RED:'#EDEDED'}}>
                  Snapshot · {ageMin<1?'just now':ageMin<60?ageMin+' min old':(ageMin/60).toFixed(1)+' h old'}
                </span>
                <span className="text-[#EDEDED]/45">
                  Quoted prices are model-run snapshots, not live{started>0?<span> · <span style={{color:'#7CA6E8'}}>{started} fixture{started===1?' has':'s have'} started</span> and {started===1?'is':'are'} marked STARTED</span>:null}.
                </span>
              </div>
            );
          })()}

          {/* Put committed picks first; otherwise the first six chronological
              forecasts can all be untracked while record-bearing picks hide
              farther down the board. Keep both kinds and the full board. */}
          {tab==='upcoming'&&(()=>{
            const futureRows=rows.filter(r=>sportsIsFuture(r.start,nowMs)&&r.advice!=='STARTED');
            const priorityTracked=futureRows.filter(r=>r.tracked).slice(0,4);
            const nextRows=[...priorityTracked,...futureRows.filter(r=>!r.tracked).slice(0,6-priorityTracked.length)];
            const tracked=nextRows.filter(r=>r.tracked).length;
            const trackedUpcoming=futureRows.filter(r=>r.tracked).length;
            const when=(start)=>{
              const parsed=sportsParseStart(start);
              if(parsed.dateOnly)return'all day';
              return parsed.d?parsed.d.toLocaleString('en-US',{hour:'numeric',minute:'2-digit',timeZone:'America/New_York'}):'time TBC';
            };
            return(
              <section className="sports-next-glance" aria-label="Upcoming sports picks and forecasts">
                <div className="sports-next-glance__head">
                  <div>
                    <div className="sports-next-glance__eyebrow">UPCOMING · RECORD-BEARING FIRST</div>
                    <h3>Tracked picks & next forecasts</h3>
                  </div>
                  <div className="sports-next-glance__count"><b>{nextRows.length}</b> of {rows.length} upcoming shown · <b>{tracked}</b> tracked</div>
                </div>
                {nextRows.length?(
                  <div className="sports-next-glance__list">
                    {nextRows.map((r,i)=>(
                      <div className="sports-next-glance__row" key={(r.id||r.match||r.event||'next')+'-'+i}>
                        <span className="sports-next-glance__time">{when(r.start)}</span>
                        <div className="sports-next-glance__pick">
                          <strong>{r.pick||r.label||'Pick pending'}</strong>
                          <span>{r.event||r.match||'Fixture'} · {r.sport_label||r.sport||'sport'}{r.mkt!=null?` · market ${Math.round(r.mkt*100)}%`:''}</span>
                        </div>
                        <span className="sports-next-glance__prob">{r.model==null?'—':Math.round(r.model*100)+'%'}<small> model</small></span>
                        <span className={r.tracked?'sports-next-glance__tracked':'sports-next-glance__paper'}>{r.tracked?'TRACKED':'NOT TRACKED'}</span>
                      </div>
                    ))}
                  </div>
                ):(
                  <div className="sports-next-glance__empty">No future fixtures in this board. Open Record for settled picks or refresh when the next model run lands.</div>
                )}
                <div className="sports-next-glance__foot"><span>Tracked picks are committed to the Sports record. Other forecasts are board-only, not betting instructions or settled paper bets.</span><div className="sports-next-glance__actions"><button onClick={()=>setTab('tracked')}>VIEW TRACKED · {trackedUpcoming}</button><button onClick={()=>setTab('record')}>SETTLED RECORD →</button></div></div>
              </section>
            );
          })()}

          {/* The verdict, stated once and plainly. A bootstrap CI on the
              model-minus-market log loss whose LOWER bound is above zero is
              not "early days" — it is a measured deficit. Burying that under a
              generic small-sample caveat would be the flattering read. */}
          {/* READY TO BET? — deliberately above the win-rate cards.
              Win rate alone is gameable: picking bigger favourites raises it
              and raises the price you pay by the same amount, so a 70% win
              rate on 70c favourites still loses after fees. The bar that
              matters is win rate minus breakeven, and a point estimate is not
              evidence — at n=11 an 82% run carries a ±23% interval. */}
          <details className="tara-research"><summary>Model evidence · readiness, results & experiments</summary>
          {data.readiness&&(()=>{
            const r=data.readiness;
            const pct=Math.min(100,Math.round(100*r.n/Math.max(r.needed||r.n,1)));
            return(
              <div className="rounded-xl border p-3 mb-3"
                style={{borderColor:r.clears?SPORTS_GREEN:T2_GOLD_BORDER,
                        background:r.clears?'rgba(35,185,129,0.06)':T2_GOLD_GLOW}}>
                <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1 mb-2">
                  <span className="text-[11px] uppercase tracking-[0.16em] font-bold"
                    style={{color:r.clears?SPORTS_GREEN:T2_GOLD}}>
                    {r.clears?'Evidence threshold met':'Not enough evidence yet'}
                  </span>
                  <span className="text-[11px] text-[#EDEDED]/45">
                    {r.wins}–{r.losses} at the {Math.round(r.floor*100)}% floor
                  </span>
                </div>
                <div className="flex flex-wrap gap-x-5 gap-y-1 text-[12px]" style={T2_MONO_STYLE}>
                  <span>win rate <b className="text-white">{Math.round(r.win_rate*100)}%</b></span>
                  <span className="text-[#EDEDED]/55">breakeven {Math.round(r.breakeven*100)}%</span>
                  <span style={{color:r.edge>0?SPORTS_GREEN:SPORTS_RED}}>
                    edge {r.edge>0?'+':''}{Math.round(r.edge*100)}%
                  </span>
                  <span className="text-[#EDEDED]/55">95% low {Math.round(r.ci_low*100)}%</span>
                </div>
                {r.needed&&!r.clears&&(
                  <div className="mt-2">
                    <div className="bar" style={{height:6,background:'#24242E',borderRadius:4,overflow:'hidden'}}>
                      <div style={{width:pct+'%',height:'100%',background:T2_GOLD}}/>
                    </div>
                    <div className="text-[11px] text-[#EDEDED]/45 mt-1">
                      {r.n} of ~{r.needed} settled picks needed before this edge could
                      be told apart from luck.
                    </div>
                  </div>
                )}
                <div className="text-[11px] text-[#EDEDED]/40 mt-2 leading-relaxed">
                  Breakeven is the average price paid plus Kalshi's fee. Winning more
                  than that is profit; winning less is a loss no matter how high the
                  raw win rate looks — which is why the win rate on its own is not the
                  number to watch.
                </div>
              </div>
            );
          })()}

          {/* The totals lane's own record. It is shown next to the headline
              readiness rather than inside the board, because it answers a
              different question: not "which bets today" but "does the
              aligned+floor filter reproduce on a second market". Until it
              settles anything there is nothing to claim, so it says so. */}
          {data.totals&&data.totals.paper_record&&(()=>{
            const p=data.totals.paper_record;
            if(!p.n)return(
              <div className="rounded-xl border border-[#24242E] bg-[#111] p-2.5 mb-3 text-[11px] text-[#EDEDED]/45">
                <span className="uppercase tracking-[0.16em] font-bold" style={{color:T2_GOLD}}>Totals lane</span>
                <span className="ml-2">{p.open} pick{p.open===1?'':'s'} recorded, none settled yet — paper only.</span>
              </div>
            );
            return(
              <div className="rounded-xl border border-[#24242E] bg-[#111] p-3 mb-3">
                <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1 mb-1.5">
                  <span className="text-[11px] uppercase tracking-[0.16em] font-bold" style={{color:T2_GOLD}}>
                    Totals lane · paper record
                  </span>
                  <span className="text-[11px] text-[#EDEDED]/45">{p.wins}–{p.losses} settled · {p.open} open</span>
                </div>
                <div className="flex flex-wrap gap-x-5 gap-y-1 text-[12px]" style={T2_MONO_STYLE}>
                  <PaperPerformanceSummary stats={p} label="Totals · price-matched sample"/>
                </div>
                <div className="text-[11px] text-[#EDEDED]/40 mt-1.5 leading-relaxed">
                  Not counted in the record above. This exists to test whether the
                  same aligned + 60% filter holds on a second market type — if it
                  does not reproduce here, that is worth knowing about the filter
                  itself.
                </div>
              </div>
            );
          })()}

          {data.open_lane&&<div className="rounded-xl border border-[#24242E] bg-[#111] p-3 mb-3"><PaperPerformanceSummary stats={data.open_lane} label="Unfiltered PAPER · separate experimental record"/></div>}

          {data.edge_lab&&(()=>{
            const e=data.edge_lab;
            const pct=Math.min(100,Math.round(100*(e.settled||0)/(e.target||150)));
            return(
              <div className="rounded-xl border border-[#24242E] bg-[#111] p-3 mb-3">
                <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1 mb-2">
                  <span className="text-[11px] uppercase tracking-[0.16em] font-bold text-[#7DB8FF]">
                    Edge lab · Kalshi vs bookmaker
                  </span>
                  <span className="text-[11px] text-[#EDEDED]/45">
                    prices captured at the same instant
                  </span>
                </div>
                <div className="flex flex-wrap gap-x-5 gap-y-1 text-[12px]" style={T2_MONO_STYLE}>
                  <span>legs <b className="text-white">{e.legs}</b></span>
                  <span className="text-[#EDEDED]/55">games {e.games}</span>
                  <span className="text-[#EDEDED]/55">settled {e.settled||0}/{e.target||150}</span>
                  {e.mean_gap!=null&&<span className="text-[#EDEDED]/55">avg gap {(e.mean_gap*100).toFixed(1)}c</span>}
                  <span style={{color:e.tradeable_now>0?SPORTS_GREEN:'#EDEDED60'}}>
                    tradeable now {e.tradeable_now}
                  </span>
                  {e.paper_units!=null&&(
                    <span style={{color:e.paper_units>=0?SPORTS_GREEN:SPORTS_RED}}>
                      paper {e.paper_units>=0?'+':''}{e.paper_units.toFixed(2)}u
                    </span>
                  )}
                </div>
                {/* Once the pre-registered sample lands, show the RESULT.
                    Leaving a progress bar up past the finish line would be the
                    same goalpost-moving the readiness panel exists to stop. */}
                {e.concluded&&e.verdict?(
                  <div className="mt-2 rounded-lg p-2.5"
                    style={{background:'rgba(232,69,94,0.06)',
                            border:'1px solid rgba(232,69,94,0.28)'}}>
                    <div className="text-[11px] uppercase tracking-[0.14em] font-bold mb-1"
                      style={{color:SPORTS_RED}}>Concluded · hypothesis rejected</div>
                    <div className="text-[11.5px] text-[#EDEDED]/70 leading-relaxed">
                      {e.verdict}
                    </div>
                  </div>
                ):(
                  <div className="mt-2">
                    <div style={{height:6,background:'#24242E',borderRadius:4,overflow:'hidden'}}>
                      <div style={{width:pct+'%',height:'100%',background:'#7DB8FF'}}/>
                    </div>
                    <div className="text-[11px] text-[#EDEDED]/40 mt-1 leading-relaxed">
                      {e.note} Underdog value picks unlock only if this experiment
                      proves the gap is real.
                    </div>
                  </div>
                )}
              </div>
            );
          })()}

          {data.verdict&&(
            <div className="rounded-xl border p-3 mb-3 text-[11.5px] leading-relaxed"
              style={{borderColor:data.verdict.significant_worse?'rgba(232,69,94,0.35)':'#24242E',
                      background:data.verdict.significant_worse?'rgba(232,69,94,0.06)':'#111'}}>
              <span className="font-bold" style={{color:data.verdict.significant_worse?SPORTS_RED:'#EDEDED'}}>
                {data.verdict.headline}
              </span>
              <span className="text-[#EDEDED]/50"> {data.verdict.detail}</span>
            </div>
          )}

          {rec&&rec.n<100&&(
            <div className="rounded-xl border border-[#2A2A34] border-l-2 bg-[#0E0E12] p-3 mb-5 text-[11.5px] leading-relaxed text-[#EDEDED]/50" style={{borderLeftColor:T2_GOLD}}>
              <span className="text-white font-bold">{rec.n} settled predictions is far too few to conclude anything.</span> Separating a real 2% edge from noise takes on the order of 1,000 bets. This is bookkeeping, not evidence. Log loss against the market is the number that will eventually matter; win rate never will.
            </div>
          )}

          </details>
          <div className="sports-state-tabs flex flex-wrap items-center gap-1 mb-4">
            <div className="flex gap-1 p-1 rounded-lg bg-[#101014] w-fit border border-[#24242E]">
{[['upcoming','Upcoming'],['tracked','Tracked'],['record','Record'],['board','All fixtures']].map(([id,lab])=>(
                <button key={id} onClick={()=>setTab(id)} className={'px-3 py-1.5 text-xs uppercase font-bold tracking-wider rounded-lg transition-colors '+(tab===id?'':'text-[#EDEDED]/40 hover:text-[#EDEDED]/70')} style={tab===id?{background:T2_GOLD_GLOW,color:T2_GOLD,border:'0.5px solid '+T2_GOLD_BORDER}:{}}>{lab}</button>
              ))}
            </div>
          </div>
          {/* The board used to gate on a conviction tier (High / All / Tracked)
              before sport. That tier is gone by request: every fixture the
              model priced shows, sport is now the only axis you slice on (the
              chips below), and `tracked` survives only as a per-row badge —
              which picks actually count toward the record is still visible,
              just not something you filter down to. */}
          <div className="text-[11px] text-[#EDEDED]/40 mb-4 leading-relaxed">
            {tab==='upcoming'&&<span><b className="text-white/70">Upcoming</b> shows future fixtures with a known date. Prices are snapshots, not live quotes. Unknown dates remain in All fixtures.</span>}
            {tab==='tracked'&&<span><b className="text-white/70">Tracked</b> shows committed picks awaiting a result, including games already underway. Settled picks move to Record.</span>}
            {tab==='board'&&<span><b className="text-white/70">All fixtures</b> includes every unresolved fixture the model priced. Only tracked picks count toward the record.</span>}
            {tab==='record'&&<span><b className="text-white/70">Record</b> is settled picks only. Scored by log loss against the market price captured at lock time, not by win rate.</span>}</div>

          {/* Split by strategy. The headline record was hiding two different
              experiments: an early value-betting run that deliberately backed
              underdogs against the market (the hypothesis the 68k-match
              backtest disproved) and the current system, which only picks
              where the model agrees with the market. Those losing experiment
              picks are labelled, not deleted — removing picks because they
              lost is exactly what makes a record worthless. */}
          {tab==='record'&&data.by_strategy&&data.by_strategy.length>1&&(
            <div className="mb-5">
              <div className="text-[10px] uppercase tracking-[0.18em] text-[#EDEDED]/40 font-bold mb-2">By strategy</div>
              <div className="space-y-1">
                {data.by_strategy.map((s,i)=>(
                  <div key={i} className="flex items-center gap-2 text-[12px] bg-[#0E0E12] border rounded-lg px-3 py-2"
                    style={{borderColor:s.current?T2_GOLD_BORDER:'#24242E'}}>
                    <span className="flex-1" style={{color:s.current?'#fff':'rgba(237,237,237,0.45)'}}>
                      {s.label}
                    </span>
                    <span className="font-bold" style={{...T2_MONO_STYLE,color:s.current?'#fff':'rgba(237,237,237,0.5)'}}>
                      {s.wins}–{s.losses}
                    </span>
                    <span className="w-14 text-right text-[#EDEDED]/40" style={T2_MONO_STYLE}>
                      {s.win_rate==null?'—':Math.round(s.win_rate*100)+'%'}
                    </span>
                  </div>
                ))}
              </div>
              <div className="text-[11px] text-[#EDEDED]/40 mt-2 leading-relaxed">
                Only the highlighted row is what the system does now. The other is kept
                because deleting losing picks is what makes a record meaningless — but it
                is not a fair read of the current approach.
              </div>
            </div>
          )}

          {tab==='record'&&data.by_league&&data.by_league.length>0&&(
            <div className="mb-5">
              <div className="text-[10px] uppercase tracking-[0.18em] text-[#EDEDED]/40 font-bold mb-2">By league</div>
              <div className="space-y-1">
                {data.by_league.map((b,i)=>(
                  <div key={i} className="flex items-center gap-2 text-[12px] bg-[#0E0E12] border border-[#24242E] rounded-lg px-3 py-2">
                    <span className="w-5">{SPORT_EMOJI[b.sport]||'•'}</span>
                    <span className="flex-1 text-white/80">{b.label}</span>
                    <span className="text-white font-bold" style={T2_MONO_STYLE}>{b.wins}–{b.losses}</span>
                    <span className="w-16 text-right text-[#EDEDED]/40" style={T2_MONO_STYLE}>{b.ll==null?'—':b.ll.toFixed(3)}</span>
                    <span className="w-16 text-right" style={{...T2_MONO_STYLE,color:(b.vs_market||0)<0?SPORTS_GREEN:SPORTS_RED}}>{b.vs_market==null?'—':(b.vs_market>0?'+':'')+b.vs_market.toFixed(3)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Sport filter. Counts come from the unfiltered tab, so they stay
              put as you click between them. */}
          {sportCounts.length>1&&(
            <div className="flex flex-wrap items-center gap-1 mb-4">
              <button onClick={()=>setSportFilter('all')} className="px-2.5 py-1 text-[10px] uppercase font-bold tracking-wider rounded-lg border transition-colors" style={sportFilter==='all'?{background:T2_GOLD_GLOW,color:T2_GOLD,borderColor:T2_GOLD_BORDER}:{color:'rgba(237,237,237,0.4)',borderColor:'#24242E'}}>
                All <span className="opacity-60">{tabRows.length}</span>
              </button>
              {sportCounts.map(([k,v])=>(
                <button key={k} onClick={()=>setSportFilter(k)} className="px-2.5 py-1 text-[10px] uppercase font-bold tracking-wider rounded-lg border transition-colors flex items-center gap-1" style={sportFilter===k?{background:T2_GOLD_GLOW,color:T2_GOLD,borderColor:T2_GOLD_BORDER}:{color:'rgba(237,237,237,0.4)',borderColor:'#24242E'}}>
                  <span className="text-[12px] leading-none">{SPORT_EMOJI[k]||'•'}</span>
                  <span className="hidden sm:inline">{v.label}</span>
                  <span className="opacity-60">{v.n}</span>
                </button>
              ))}
              {grouped.length>1&&(
                <span className="ml-auto flex gap-1">
                  <button onClick={()=>setAllDays(true)} className="px-2 py-1 text-[9px] uppercase font-bold tracking-wider rounded-lg border border-[#24242E] text-[#EDEDED]/35 hover:text-[#EDEDED]/70">Expand all</button>
                  <button onClick={()=>setAllDays(false)} className="px-2 py-1 text-[9px] uppercase font-bold tracking-wider rounded-lg border border-[#24242E] text-[#EDEDED]/35 hover:text-[#EDEDED]/70">Collapse</button>
                </span>
              )}
            </div>
          )}

          <details className="tara-v145-disclosure sports-fixture-details" open={tab!=='upcoming'}><summary>{tab==='upcoming'?'All upcoming fixtures & detailed evidence':tab==='record'?'Settled picks & captured prices':'All picks & detailed evidence'} · {rows.length}</summary><div>
          {grouped.length===0&&<div className="text-[#EDEDED]/40 text-sm py-6">Nothing here. {tab==='board'&&sportFilter!=='all'?'Nothing matches this sport filter.':''}</div>}

          {grouped.map((day,di)=>{
            const open=isDayOpen(day.key,di);
            return(
              <div key={day.key} className="mb-2.5">
                <button onClick={()=>toggleDay(day.key,open)} className="w-full flex items-center gap-2 px-3 py-2.5 rounded-xl bg-[#0E0E12] border border-[#24242E] hover:border-[#2A2A34] transition-colors text-left">
                  <span className="text-[10px] w-3 shrink-0" style={{color:T2_GOLD}}>{open?'▾':'▸'}</span>
                  <span className="text-[13px] font-bold text-white tracking-tight">{sportsDateLabel(day.dateKey)}</span>
                  <span className="text-[10px] uppercase tracking-wider text-[#EDEDED]/35">{day.n} {day.n===1?'pick':'picks'}</span>
                  {day.aligned>0&&tab!=='record'&&(
                    <span className="text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded border" style={{color:SPORTS_GREEN,borderColor:SPORTS_GREEN,background:'rgba(35,185,129,0.12)'}}>{day.aligned} aligned</span>
                  )}
                  <span className="ml-auto flex gap-1 shrink-0">
                    {day.sports.map(s=><span key={s.sport} className="text-[12px] leading-none opacity-60">{SPORT_EMOJI[s.sport]||'•'}</span>)}
                  </span>
                </button>
                {open&&(
                  <div className="pl-2 sm:pl-3 mt-1.5">
                    {day.sports.map(s=>(
                      <div key={s.sport} className="mb-3">
                        <div className="flex items-baseline gap-2 mb-1">
                          <span className="text-[14px]">{SPORT_EMOJI[s.sport]||'•'}</span>
                          <span className="text-[13px] font-bold text-white/90 tracking-tight">{s.label}</span>
                          <span className="text-[10px] uppercase tracking-wider text-[#EDEDED]/30">{s.leagues.reduce((a,l)=>a+l.rows.length,0)}</span>
                        </div>
                        {s.leagues.map(l=>(
                          <div key={l.league} className="mb-1.5">
                            <div className="text-[9.5px] font-bold uppercase tracking-[0.1em] text-[#EDEDED]/35 pl-2 my-1" style={{borderLeft:'2px solid '+T2_GOLD_BORDER}}>{l.league} · {l.rows.length}</div>
                            <div className="bg-[#0E0E12] border border-[#24242E] rounded-xl px-3">
                              {l.rows.map((r,i)=><SportsRow key={(r.id||r.match)+'-'+i} r={r} grid={data.inplay_grid} showResult={tab==='record'}/>)}
                            </div>
                          </div>
                        ))}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}

          <div className="rounded-xl border border-[#24242E] bg-[#111] p-3 text-[11px] leading-relaxed text-[#EDEDED]/45 mt-2">
            Every chip is one number: how far the model sits from the market on that pick. <span className="font-bold" style={{color:'#7CA6E8'}}>Matches market</span> means within 3 points — that is where the model has tracked the closing line, and it is the <b className="text-white/70">absence of an edge</b>, not the presence of one. After Kalshi's ~1.7c fee near 50c, a matching position is negative EV. <span className="font-bold" style={{color:T2_GOLD}}>Off by 3–7</span> is shown but never tallied. <span className="text-white/70 font-bold">Off by 8+</span> backtested at 1.0373 log loss against the market's 0.9641 — that is the model being wrong, not an edge worth taking.</div>

          </div></details>
          {data.disclosures&&data.disclosures.length>0&&(
            <div className="mt-4">
              <div className="text-[10px] uppercase tracking-[0.18em] text-[#EDEDED]/40 font-bold mb-2">Excluded, corrected and backfilled</div>
              <div className="space-y-1">
                {data.disclosures.map((d,i)=>(
                  <div key={i} className="text-[11px] text-[#EDEDED]/40 bg-[#111] border border-[#1C1C1C] rounded-lg px-3 py-2">
                    <span className="font-bold text-[#EDEDED]/70">#{d.id} {d.kind}</span> · {d.event} — {d.reason}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>)}
      </div>
    </div>
  );
}

// V13.4.305 REMOVED: MobileTabBar. Superseded by the newer Signal/Chart/
//   Analytics bar (see the removal note at its former call site) -- kept the
//   component definition around as dead code was pointless once nothing
//   called it.



// ── V145: useNewsSentiment — dual-source scanner with geopolitics + macro coverage
//
// Sources:
//   1. CryptoCompare (BTC/Trading/Regulation feed)   — same as V114, with expanded keyword sets
//   2. GDELT 2.0 DOC API                             — global news, geopolitics, macro events
//
// Why both? CryptoCompare indexes crypto-focused stories well but misses non-crypto events that
// move BTC indirectly (war, sanctions, banking crises, central bank surprises). GDELT covers
// 30,000+ outlets in 65 languages with built-in tone scoring — perfect for catching the
// "Iran strikes" or "FOMC surprise" headline 60-90s earlier than crypto-specific feeds do.
//
// Mode: LIGHT (per user choice).
//   - Sentiment score still influences quality gate by ±5 to ±8 points (existing V114 logic).
//   - When a geopolitical/macro shock is detected (extreme keywords + recency < 30 min),
//     a small confidence haircut is applied via the `geoRisk` flag — but no full lockout.
//     Trader can still take the lock; Tara just expresses lower confidence.
const useNewsSentiment=()=>{
  const[sentiment,setSentiment]=React.useState({
    score:0,bullish:0,bearish:0,extreme:0,
    topHeadline:null,hasBreaking:false,
    geoRisk:0,             // V145: 0-1 scale of geopolitical/macro risk (last 30 min)
    geoTopic:null,         // V145: headline of the highest-risk story
    geoSource:null,        // V145: 'crypto' or 'gdelt'
  });
  React.useEffect(()=>{
    // ── KEYWORD BANKS ───────────────────────────────────────────────────
    // Directional (BTC-specific) keywords — affect sentiment.score
    const bullishKW=['surge','rally','approve','approval','etf approve','breakout','soar','spike up','all-time high','ath','adoption','inflows','accumulate','buy pressure','squeeze short','liquidat short','halving','institutional','spot etf'];
    const bearishKW=['crash','dump','sell-off','plunge','reject','denied','hack','exploit','liquidat long','outflow','sec sue','sec charge','ban','fud','correction','contagion','insolvent','bankruptcy','rug pull'];
    // Macro/policy keywords — these lift "extreme" but rarely directional on their own
    const macroKW=['fomc','cpi','fed rate','powell','rate hike','rate cut','jobs report','nonfarm payroll','treasury','yield','inflation','recession','jpow','jerome powell','ecb','bank of japan','boj'];
    // V145: Geopolitical keywords — events that historically move risk-on/risk-off including BTC
    const geoKW=['iran','israel','russia','ukraine','china','taiwan','war','strike','missile','attack','invade','sanction','ceasefire','escalat','tariff','trade war','north korea','venezuela','opec','oil shock'];
    // V145: Banking/financial-system shock keywords — these especially move BTC (often inverse first, then bid)
    const bankingKW=['bank run','bank fail','svb','signature bank','first republic','credit suisse','contagion','liquidity crisis','margin call','flash crash'];
    // V145: Trump/political action — frequent BTC mover, recency-sensitive
    const politicalKW=['trump','biden','executive order','impeach','indictment','election','sec chair','gensler','atkins'];
    // Time-sensitive markers
    const breakingKW=['breaking','flash','urgent','just in','alert','live update'];

    let cancelled=false;

    // V10.7.65: Replaced direct CryptoCompare + GDELT calls with /api/news proxy.
    //   Direct GDELT was hitting 429 Too Many Requests from browser.
    //   Proxy runs server-side (no CORS, 45s cache, all 3 sources in one call).
    const fetchAll=async()=>{
      try{
        const r=await fetch('/api/news?asset=BTC',{cache:'no-store'});
        if(!r.ok)return;
        const d=await r.json();
        if(cancelled)return;
        const items=d?.items||[];
        if(items.length===0)return;

        let bull=0,bear=0,extreme=0,topHeadline=null,topScore=0;
        let geoRisk=0,geoTopic=null,geoSource=null,geoTopScore=0;
        let hasBreaking=false;

        items.forEach(n=>{
          const t=(n.title||'').toLowerCase();
          if(!t)return;
          const ageMin=(Date.now()-n.time)/60000;
          if(ageMin>360||ageMin<0)return;
          const decay=Math.max(0.3,1-(ageMin/360));
          const geoDecay=ageMin<=30?Math.max(0.5,1-(ageMin/60)):Math.max(0.1,0.5-(ageMin-30)/300);

          // CryptoPanic has pre-computed sentiment votes
          if(n.source==='cryptopanic'){
            if(n.sentiment==='bullish'){bull+=decay;if(decay>topScore){topScore=decay;topHeadline=n.title;}}
            else if(n.sentiment==='bearish'){bear+=decay;if(decay>topScore){topScore=decay;topHeadline=n.title;}}
            if(n.isImportant){extreme+=decay;hasBreaking=true;}
            return;
          }

          // Keyword scoring for GDELT + CryptoCompare
          let itemScore=0;
          bullishKW.forEach(kw=>{if(t.includes(kw)){bull+=decay;itemScore+=decay;}});
          bearishKW.forEach(kw=>{if(t.includes(kw)){bear+=decay;itemScore-=decay;}});
          macroKW.forEach(kw=>{if(t.includes(kw))extreme+=decay*0.5;});
          if(Math.abs(itemScore)>topScore){topScore=Math.abs(itemScore);topHeadline=n.title;}

          let geoItemScore=0;
          geoKW.forEach(kw=>{if(t.includes(kw))geoItemScore+=geoDecay;});
          bankingKW.forEach(kw=>{if(t.includes(kw))geoItemScore+=geoDecay*1.5;});
          politicalKW.forEach(kw=>{if(t.includes(kw))geoItemScore+=geoDecay*0.7;});
          const isBreaking=breakingKW.some(kw=>t.includes(kw))||ageMin<=10;
          if(isBreaking&&geoItemScore>0)geoItemScore*=1.5;
          if(isBreaking&&ageMin<=10)hasBreaking=true;
          if(geoItemScore>geoTopScore){
            geoTopScore=geoItemScore;
            geoTopic=n.title;
            geoSource=n.source;
            geoRisk=Math.min(1,geoItemScore/3);
          }
        });

        setSentiment({score:bull-bear,bullish:bull,bearish:bear,extreme,
          topHeadline,hasBreaking,geoRisk,geoTopic,geoSource});
      }catch(e){}
    };
    fetchAll();
    // V145: Slightly slower than V134's 30s — GDELT can be sluggish, 45s is safer.
    //       CryptoCompare side still updates frequently enough for breaking news.
    const iv=setInterval(fetchAll,45000);
    return()=>{cancelled=true;clearInterval(iv);};
  },[]);
  return sentiment;
};


// V134: Plain-English summary builder — converts structured analysis into a sentence
const buildPlainEnglish=(analysis,qualityGate,advisor)=>{
  if(!analysis)return 'Connecting to market data...';
  const dir=analysis.prediction||'';
  const regime=analysis.regime||'';
  const reasoning=analysis.reasoning||[];
  // V136: FGT IS THE PRIMARY SIGNAL. Lead with it when 3/4 or 4/4 aligned.
  const mtfAlign=analysis.mtfAlignment;
  const mtfAbs=Math.abs(mtfAlign||0);
  const mtfDirText=mtfAlign>0?'UP':'DOWN';
  // Pull out the most informative tags
  const findTag=(prefix)=>reasoning.find(r=>r.startsWith('['+prefix+']'));
  const pieces=[];
  // Direction phrasing
  if(dir.includes('CONFIRMED')){
    const d=dir.includes('UP')?'UP':'DOWN';
    // V136: When FGT 4/4 aligns with the lock, lead with that as the rationale
    if(mtfAbs>=4&&((d==='UP'&&mtfAlign>0)||(d==='DOWN'&&mtfAlign<0))){
      pieces.push(`FGT 4/4 → ${d} · locked at ${(analysis.rawProbAbove||50).toFixed(0)}% conf`);
    } else if(mtfAbs>=3&&((d==='UP'&&mtfAlign>0)||(d==='DOWN'&&mtfAlign<0))){
      pieces.push(`FGT 3/4 → ${d} · locked at ${(analysis.rawProbAbove||50).toFixed(0)}% conf`);
    } else {
      pieces.push(`Locked ${d} (${(analysis.rawProbAbove||50).toFixed(0)}% conf)`);
    }
  } else if(dir.includes('FORMING')){
    const d=dir.includes('UP')?'UP':'DOWN';
    if(mtfAbs>=4&&((d==='UP'&&mtfAlign>0)||(d==='DOWN'&&mtfAlign<0))){
      pieces.push(`FGT 4/4 → ${d} · waiting for confirmation`);
    } else if(mtfAbs>=3&&((d==='UP'&&mtfAlign>0)||(d==='DOWN'&&mtfAlign<0))){
      pieces.push(`FGT 3/4 → ${d} · forming`);
    } else {
      pieces.push(`Leaning ${d}`);
    }
  } else if(dir.includes('SEARCHING')){
    // V136: Even in SEARCHING, surface FGT alignment if present
    if(mtfAbs>=4)pieces.push(`FGT 4/4 → ${mtfDirText} · waiting for posterior to align`);
    else if(mtfAbs>=3)pieces.push(`FGT 3/4 → ${mtfDirText} · scanning`);
    else pieces.push('No clear edge yet');
  } else if(dir.includes('ANALYZING')){
    const m=dir.match(/\[(\d+)s\]/);
    const secs=m?parseInt(m[1]):0;
    if(mtfAbs>=4)return `FGT 4/4 → ${mtfDirText} · committing in ~${secs}s`;
    if(mtfAbs>=3)return `FGT 3/4 → ${mtfDirText} · finalizing in ~${secs}s`;
    return `🔍 Searching for signal — decision within ${secs}s (sooner if FGT aligns)`;
  } else if(dir.includes('SITTING OUT')){
    return '⛔ Sitting out — signals are split, no clear edge this window';
  } else if(dir.includes('REJECTED')){
    // V3.1.4: REJECTED gets its own clean line — title is already shown in the hero,
    // this just gives the regime + driver context underneath without "in RANGE-CHOP" awkwardness.
    const reasonAfterDash=dir.split('—').slice(1).join('—').trim();
    const cleanReason=reasonAfterDash?reasonAfterDash.charAt(0).toUpperCase()+reasonAfterDash.slice(1):'mixed signals';
    return `${regime||'Mixed signals'} · ${cleanReason}`;
  } else if(dir.includes('BLACKOUT')||dir.includes('OBSERVE')||dir.includes('LOW QUALITY')){
    return 'STAND DOWN — '+dir;
  }
  // Regime context — only push when there's already a leading piece. Avoids "in RANGE-CHOP"
  // showing up as a sentence opener for fall-through states.
  if(regime&&!dir.includes('SEARCHING')&&pieces.length>0)pieces.push(`in ${regime}`);
  else if(regime&&!dir.includes('SEARCHING'))pieces.push(regime);
  // Top driving reasons (max 2)
  const drivers=[];
  const flowTag=findTag('FLOW');
  if(flowTag){
    const m=flowTag.match(/Net (buy|sell) \$([0-9.]+)([KM])/);
    if(m)drivers.push(`${m[1]}ers absorbing ($${m[2]}${m[3]} net)`);
    else if(flowTag.includes('Imbalance:'))drivers.push('flow imbalance');
  }
  const trajTag=findTag('TRAJ');
  if(trajTag){
    const m=trajTag.match(/[+-]\d+/);
    if(m)drivers.push(`trajectory ${m[0]}bps to strike`);
  }
  const momTag=findTag('MOMENTUM');
  if(momTag&&momTag.includes('aligned'))drivers.push('15m momentum aligned');
  else if(momTag&&momTag.includes('divergent'))drivers.push('momentum divergent');
  const liqTag=findTag('LIQ');
  if(liqTag&&!liqTag.includes('SPOOF'))drivers.push('liquidation magnet pulling');
  const fundTag=findTag('FUND-EXT');
  if(fundTag)drivers.push(fundTag.includes('Crowded longs')?'crowded longs flipping':'crowded shorts flipping');
  const patternTag=findTag('PATTERN');
  if(patternTag){
    const m=patternTag.match(/\] (.+?) —/);
    if(m)drivers.push(m[1].toLowerCase());
  }
  if(drivers.length>0)pieces.push('— '+drivers.slice(0,2).join(', '));
  // Quality note
  const q=qualityGate?.score||0;
  if(q>=75)pieces.push('· A+ setup');
  else if(q>=65)pieces.push('· solid setup');
  else if(q<50)pieces.push('· marginal quality');
  // Advisor override
  if(advisor&&advisor.label&&!advisor.label.includes('CONNECTING')){
    if(advisor.color==='rose')return `⚠ ${advisor.label} — ${advisor.reason||''}`.slice(0,200);
  }
  return pieces.join(' ').replace(/\s+/g,' ').trim();
};



// V134: Compute weight changes for learning feedback
const computeWeightDiff=(oldW,newW)=>{
  const diffs=[];
  Object.keys(newW).forEach(k=>{
    const delta=(newW[k]-(oldW[k]||0));
    if(Math.abs(delta)>=0.05){
      diffs.push({key:k,delta:Math.round(delta*10)/10,old:Math.round((oldW[k]||0)*10)/10,new:Math.round(newW[k]*10)/10});
    }
  });
  return diffs;
};

// ── V8.8.9: SPIKE ALERT — multi-window, always-on, sound + system notification ──
// Catches outsized price moves with three rolling windows checked in parallel:
//   10s, 20s, 30s. Whichever fires first / hits hardest wins. Aggressive
//   thresholds tuned for "I keep missing fast moves":
//     10s: max(20 bps, 1.0 × atrBps)   ~0.2% in 10s
//     20s: max(30 bps, 1.3 × atrBps)   ~0.3% in 20s
//     30s: max(40 bps, 1.6 × atrBps)   ~0.4% in 30s
//   Floor numbers catch genuine spikes in calm regimes; ATR multipliers scale
//   the threshold up in already-volatile regimes.
//   Always-on (not just when locked). When NOT locked, framing is informational
//   ("scan for entry"); when locked, verdict is direction-aware (RIDE/EXIT).
//   Three layers of attention:
//     (a) Big animated overlay at top of screen (glowing border, pulse)
//     (b) Document.title patch — visible in OS taskbar / browser tab strip
//     (c) Soft beep + system notification (require permission for the latter,
//         requested via button on the overlay).
//   Throttled to one alert per 30s.
function useSpikeAlert({tickHistoryRef,lockedCallRef,currentAsset,atrBps}){
  const[alert,setAlert]=React.useState(null);
  const[notifyPerm,setNotifyPerm]=React.useState(()=>{
    try{return(typeof Notification!=='undefined')?Notification.permission:'unsupported';}catch(_){return'unsupported';}
  });
  const _lastFiredRef=React.useRef(0);
  const _origTitleRef=React.useRef(null);
  const _atrRef=React.useRef(atrBps);
  const _assetRef=React.useRef(currentAsset);
  const _audioCtxRef=React.useRef(null);
  const _soundEnabledRef=React.useRef(true);
  React.useEffect(()=>{
    try{_soundEnabledRef.current=localStorage.getItem('taraSpikeSoundEnabled')!=='false';}catch(_){}
  },[]);
  React.useEffect(()=>{_atrRef.current=atrBps;},[atrBps]);
  React.useEffect(()=>{_assetRef.current=currentAsset;},[currentAsset]);
  // Soft beep via Web Audio. PUMP → 880Hz (bright), DUMP → 440Hz (neutral).
  //   200ms sine wave with fade-in/out. Volume 0.15 — soft, not jarring.
  const _playBeep=React.useCallback((dir)=>{
    if(!_soundEnabledRef.current)return;
    try{
      let ctx=_audioCtxRef.current;
      if(!ctx){
        const _Ctor=window.AudioContext||window.webkitAudioContext;
        if(!_Ctor)return;
        ctx=new _Ctor();
        _audioCtxRef.current=ctx;
      }
      // If suspended (no user interaction yet), try to resume. If still suspended
      //   after this, the play will silently fail — that's acceptable.
      if(ctx.state==='suspended'){try{ctx.resume();}catch(_){}}
      const now=ctx.currentTime;
      const osc=ctx.createOscillator();
      const gain=ctx.createGain();
      osc.frequency.setValueAtTime(dir==='PUMP'?880:440,now);
      osc.type='sine';
      gain.gain.setValueAtTime(0,now);
      gain.gain.linearRampToValueAtTime(0.15,now+0.02);
      gain.gain.linearRampToValueAtTime(0.15,now+0.18);
      gain.gain.linearRampToValueAtTime(0,now+0.22);
      osc.connect(gain);gain.connect(ctx.destination);
      osc.start(now);osc.stop(now+0.25);
    }catch(_){}
  },[]);
  // System notification — needs permission. Granted state only.
  const _fireSystemNotification=React.useCallback((a)=>{
    try{
      if(typeof Notification==='undefined')return;
      if(Notification.permission!=='granted')return;
      const _sign=a.bps>0?'+':'';
      const _verdict=a.locked
        ?(a.favorable?'Your call is winning — ride it':'Moving against you — consider exit')
        :`Sudden ${a.dir.toLowerCase()} — scan for entry`;
      const n=new Notification(`${a.dir==='PUMP'?'🚀':'🔻'} ${a.asset} ${_sign}${a.bps.toFixed(0)}bps`,{
        body:_verdict,
        tag:'tara-spike',           // replaces previous notification of same tag
        renotify:true,              // re-alert even if same tag
        silent:false,
      });
      setTimeout(()=>{try{n.close();}catch(_){}},6000);
    }catch(_){}
  },[]);
  React.useEffect(()=>{
    const iv=setInterval(()=>{
      const lock=lockedCallRef?.current;
      const isLocked=!!(lock&&lock.dir&&lock.dir!=='SIT_OUT');
      const ticks=tickHistoryRef?.current||[];
      if(ticks.length<3)return;
      const now=Date.now();
      const _atr=Number.isFinite(_atrRef.current)&&_atrRef.current>0?_atrRef.current:15;
      // V9.7.7: Lower thresholds — user reported intermittent fires on calm-market days.
      //   Old: 10s/20bps/1.0×ATR · 20s/30bps/1.3×ATR · 30s/40bps/1.6×ATR
      //   New: 10s/14bps/0.9×ATR · 20s/22bps/1.1×ATR · 30s/30bps/1.3×ATR
      //   ~40% reduction in floors, modest reduction in ATR multipliers. Still
      //   won't fire on tape noise — needs ~$11+ move in 10s on $80K BTC.
      const _windows=[
        {sec:10,floor:14,mult:0.9},
        {sec:20,floor:22,mult:1.1},
        {sec:30,floor:30,mult:1.3},
      ];
      let _bestFire=null;
      for(const w of _windows){
        const cutoff=now-w.sec*1000;
        const inWindow=ticks.filter(t=>t&&t.p&&t.time>=cutoff);
        if(inWindow.length<3)continue;
        const oldest=inWindow[0].p;
        const newest=inWindow[inWindow.length-1].p;
        if(!oldest||!newest||!Number.isFinite(oldest)||!Number.isFinite(newest))continue;
        const moveBps=((newest-oldest)/oldest)*10000;
        const absMove=Math.abs(moveBps);
        const threshold=Math.max(w.floor,_atr*w.mult);
        if(absMove<threshold)continue;
        if(!_bestFire||absMove>Math.abs(_bestFire.moveBps)){
          _bestFire={windowSec:w.sec,moveBps,threshold};
        }
      }
      if(!_bestFire)return;
      // V9.7.7: Throttle 30s → 20s. Active markets often have multiple spikes
      //   in a row; 30s was eating real fires. 20s still prevents a single sustained
      //   move from spamming the overlay.
      if(now-_lastFiredRef.current<20000)return;
      _lastFiredRef.current=now;
      const dir=_bestFire.moveBps>0?'PUMP':'DUMP';
      const favorable=isLocked&&((dir==='PUMP'&&lock.dir==='UP')||(dir==='DUMP'&&lock.dir==='DOWN'));
      const _asset=_assetRef.current||'BTC';
      const newAlert={
        firedAt:now,
        dir,
        bps:_bestFire.moveBps,
        windowSec:_bestFire.windowSec,
        favorable,
        locked:isLocked,
        asset:_asset,
      };
      setAlert(newAlert);
      _playBeep(dir);
      _fireSystemNotification(newAlert);
      // Tab title patch
      try{
        if(_origTitleRef.current==null)_origTitleRef.current=document.title;
        const _sign=_bestFire.moveBps>0?'+':'';
        document.title=`${dir==='PUMP'?'🚀':'🔻'} ${_asset} ${_sign}${_bestFire.moveBps.toFixed(0)}bps · Tara`;
      }catch(_){}
    },1000);
    return()=>clearInterval(iv);
  },[tickHistoryRef,lockedCallRef,_playBeep,_fireSystemNotification]);
  React.useEffect(()=>{
    if(!alert)return;
    // V9.7.7: 8s → 12s. User reported missing alerts when glancing away from screen.
    //   Tap-to-dismiss still works for early dismissal; this just gives more buffer.
    const t=setTimeout(()=>{
      setAlert(null);
      try{
        if(_origTitleRef.current!=null){document.title=_origTitleRef.current;_origTitleRef.current=null;}
      }catch(_){}
    },12000);
    return()=>clearTimeout(t);
  },[alert]);
  React.useEffect(()=>{
    return()=>{
      try{
        if(_origTitleRef.current!=null){document.title=_origTitleRef.current;_origTitleRef.current=null;}
        if(_audioCtxRef.current){try{_audioCtxRef.current.close();}catch(_){}_audioCtxRef.current=null;}
      }catch(_){}
    };
  },[]);
  // V9.7.7: TEST FIRE — synthesize a fake PUMP alert so the user can verify their
  //   browser permissions, audio context, and overlay are all working. Triggered
  //   from a button in Trading Settings. Uses the same code path as real alerts
  //   so if the test works, real alerts will too.
  const fireTestAlert=React.useCallback(()=>{
    const _asset=_assetRef.current||'BTC';
    const _testAlert={
      firedAt:Date.now(),
      dir:'PUMP',
      bps:25,
      windowSec:10,
      favorable:false,
      locked:false,
      asset:_asset,
      _isTest:true,
    };
    setAlert(_testAlert);
    _playBeep('PUMP');
    _fireSystemNotification(_testAlert);
    try{
      if(_origTitleRef.current==null)_origTitleRef.current=document.title;
      document.title=`🚀 ${_asset} +25bps · TEST`;
    }catch(_){}
  },[_playBeep,_fireSystemNotification]);
  const dismiss=React.useCallback(()=>{
    setAlert(null);
    try{
      if(_origTitleRef.current!=null){document.title=_origTitleRef.current;_origTitleRef.current=null;}
    }catch(_){}
  },[]);
  const toggleSound=React.useCallback(()=>{
    const next=!_soundEnabledRef.current;
    _soundEnabledRef.current=next;
    try{localStorage.setItem('taraSpikeSoundEnabled',String(next));}catch(_){}
    // Force-rerender by updating a state — easiest: re-set the alert if any
    setAlert(prev=>prev?{...prev,_soundToggleTick:Date.now()}:prev);
  },[]);
  const requestNotifyPermission=React.useCallback(async()=>{
    try{
      if(typeof Notification==='undefined')return'unsupported';
      const result=await Notification.requestPermission();
      setNotifyPerm(result);
      return result;
    }catch(_){return'denied';}
  },[]);
  // V9.7.7: Expose test fire on window so the user can verify their browser
  //   permissions/audio are working. Run `taraTestSpikeAlert()` from devtools
  //   console — same code path as a real alert. If overlay appears + beep plays,
  //   real spikes will work too. If only overlay appears (no beep), audio context
  //   is suspended (Brave often does this until first page click). If nothing
  //   appears, browser is blocking the rendering somehow.
  React.useEffect(()=>{
    if(typeof window==='undefined')return;
    window.taraTestSpikeAlert=fireTestAlert;
    return()=>{try{delete window.taraTestSpikeAlert;}catch(_){}};
  },[fireTestAlert]);
  return{alert,dismiss,toggleSound,soundEnabled:_soundEnabledRef.current,requestNotifyPermission,notifyPerm,fireTestAlert};
}

function SpikeAlertOverlay({alert,dismiss,toggleSound,soundEnabled,requestNotifyPermission,notifyPerm}){
  if(!alert)return null;
  const{dir,bps,favorable,locked,asset,windowSec}=alert;
  const _sign=bps>0?'+':'';
  // Color: green if locked+favorable, rose if locked+adverse, gold if not locked
  let _color,_bg,_verdict;
  if(locked){
    _color=favorable?'#23B981':'#E8455E';
    _bg=favorable?'rgba(35,185,129,0.10)':'rgba(232,69,94,0.10)';
    _verdict=favorable?'🚀 YOUR CALL WINNING — RIDE IT':'⚠️ MOVING AGAINST YOU — CONSIDER EXIT';
  }else{
    _color='#23B981'; // brand gold — neutral attention
    _bg='rgba(35,185,129,0.10)';
    _verdict=dir==='PUMP'?'🚀 SUDDEN PUMP — scan for entry':'🔻 SUDDEN DUMP — scan for entry';
  }
  const _showNotifyButton=notifyPerm==='default';
  return React.createElement('div',{
    className:'fixed top-14 left-1/2 z-[110]',
    style:{transform:'translateX(-50%)',maxWidth:'92vw',animation:'taraSpikeAlertIn 240ms cubic-bezier(0.16,1,0.3,1)'},
  },
    React.createElement('div',{
      className:'px-5 py-3 rounded-lg border-2 backdrop-blur-md cursor-pointer',
      style:{
        background:_bg,
        borderColor:_color,
        boxShadow:`0 8px 32px ${_color}, 0 0 32px ${_color}`,
        animation:'taraSpikePulse 1.2s ease-in-out infinite',
      },
      onClick:dismiss,
      title:'Tap to dismiss',
    },
      React.createElement('div',{className:'flex items-baseline gap-2 mb-1'},
        React.createElement('span',{className:'text-[10px] uppercase tracking-[0.22em] font-bold',style:{color:_color}},
          `${asset} ${dir} · last ${windowSec}s`
        ),
      ),
      React.createElement('div',{className:'font-serif text-3xl font-bold leading-none',style:{color:'#fff',fontFamily:"'Cormorant Garamond', serif"}},
        `${_sign}${bps.toFixed(0)} bps`
      ),
      React.createElement('div',{className:'text-xs font-bold mt-2 tracking-wide',style:{color:_color}},
        _verdict
      ),
      React.createElement('div',{className:'flex items-center justify-between gap-3 mt-2 pt-2 border-t',style:{borderColor:'rgba(255,255,255,0.08)'}},
        React.createElement('div',{className:'flex items-center gap-2'},
          // Sound toggle
          React.createElement('button',{
            className:'text-[10px] px-1.5 py-0.5 rounded-lg hover:bg-white/5 transition-colors',
            onClick:(e)=>{e.stopPropagation();toggleSound();},
            title:soundEnabled?'Sound on — click to mute':'Sound muted — click to unmute',
            style:{color:soundEnabled?'rgba(255,255,255,0.6)':'rgba(255,255,255,0.3)'},
          },soundEnabled?'🔊':'🔇'),
          // Enable system alerts (only if permission='default')
          _showNotifyButton&&React.createElement('button',{
            className:'text-[9px] uppercase tracking-wider px-2 py-0.5 rounded-lg border hover:bg-white/5 transition-colors',
            style:{color:_color,borderColor:_color+''},
            onClick:async(e)=>{e.stopPropagation();await requestNotifyPermission();},
            title:'Enable system notifications so you get pinged even when Tara is in a background tab',
          },'+ system alerts'),
          notifyPerm==='granted'&&React.createElement('span',{
            className:'text-[9px] uppercase tracking-wider',
            style:{color:'rgba(255,255,255,0.3)'},
          },'· system alerts on'),
        ),
        React.createElement('div',{className:'text-[9px] text-white/40'},
          'tap to dismiss'
        )
      )
    )
  );
}

// V8.8.8/8.8.9: Bridge component — calls the spike alert hook with refs from
//   TaraApp's scope (passed as props) and renders the overlay.
function SpikeAlertHookHost(props){
  const hookResult=useSpikeAlert(props);
  return React.createElement(SpikeAlertOverlay,hookResult);
}

// ── V8.9.0: SMART-MONEY DETECTORS ─────────────────────────────────────────
// Four detectors that flag when "smart money" patterns appear in the live tape,
//   helping the user align with informed flow rather than retail chase. None of
//   these are silver bullets — they're context cues that improve sit-out
//   discipline (skip trades where you're clearly the bagholder).
//
//   1. STRIKE PINNING — last 3min, price within 0.5×ATR of strike, multiple
//      crossings = position defenders pinning the strike. Bet WITH dominant side.
//   2. RETAIL CROWD FADE — >70% of last-60s prints are <$5K, all same direction
//      = retail chasing. Suggest fading.
//   3. KALSHI-SPOT DIVERGENCE — Kalshi YES moves >5¢ in 30s while spot is flat,
//      OR Kalshi and spot move opposite directions = positioning play / squeeze.
//      Lower trust in Kalshi confluence when active.
//   4. ORDER BOOK ABSORPTION — heavy one-sided tape flow but price doesn't move
//      = someone defending. Align with the absorber's direction.
function useSmartMoneyDetectors({
  tickHistoryRef,    // spot price ticks {p, time}
  tradeTicksRef,     // per-trade tape from useGlobalTape {p, s, usd, t, src, time}
  kalshiHistoryRef,  // Kalshi YES price history {yes, time} — fed by TaraApp
  currentPrice,
  targetMargin,
  atrBps,
  clockSeconds,
}){
  const[signals,setSignals]=React.useState({
    pinning:{detected:false,side:null,score:0,reason:''},
    retailCrowd:{detected:false,side:null,score:0,reason:''},
    kalshiSpot:{detected:false,side:null,score:0,reason:''},
    absorption:{detected:false,side:null,score:0,reason:''},
  });
  // Ref to current values so the polling interval reads fresh data without
  //   tearing down + recreating on every render.
  const _refs=React.useRef({});
  _refs.current={tickHistoryRef,tradeTicksRef,kalshiHistoryRef,currentPrice,targetMargin,atrBps,clockSeconds};
  React.useEffect(()=>{
    const iv=setInterval(()=>{
      const r=_refs.current;
      const now=Date.now();
      // ═══════ #1 STRIKE PINNING ═══════
      let pinning={detected:false,side:null,score:0,reason:''};
      if(r.clockSeconds!=null&&r.clockSeconds<=180&&r.clockSeconds>=0&&r.currentPrice>0&&r.targetMargin>0&&r.atrBps>0){
        const distBps=Math.abs((r.currentPrice-r.targetMargin)/r.targetMargin)*10000;
        const halfAtr=Math.max(r.atrBps*0.5,25);
        if(distBps<=halfAtr){
          // Count strike crossings in last 60s + dominant side (above/below)
          const ticks=(r.tickHistoryRef?.current||[]).filter(t=>t&&t.p&&t.time>now-60000);
          let crossings=0;let lastSide=null;
          let aboveCount=0,belowCount=0;
          ticks.forEach(t=>{
            const above=t.p>r.targetMargin;
            const side=above?'A':'B';
            if(lastSide&&side!==lastSide)crossings++;
            lastSide=side;
            if(above)aboveCount++;else belowCount++;
          });
          const dominantSide=aboveCount>belowCount?'UP':'DOWN';
          if(crossings>=2){
            pinning={
              detected:true,
              side:dominantSide,
              score:Math.min(100,55+crossings*7+Math.max(0,180-r.clockSeconds)*0.15),
              reason:`Pinning at ${r.targetMargin.toFixed(0)} — ${crossings} crossings/60s, ${dominantSide} dominant (${distBps.toFixed(0)}bps from strike)`,
            };
          }else if(distBps<=halfAtr*0.4){
            // Tight to strike, low movement — early pin forming
            pinning={
              detected:true,
              side:dominantSide,
              score:42,
              reason:`Tight to strike (${distBps.toFixed(0)}bps), early pin forming`,
            };
          }
        }
      }
      // ═══════ #2 RETAIL CROWD FADE ═══════
      let retailCrowd={detected:false,side:null,score:0,reason:''};
      const tapeTicks=(r.tradeTicksRef?.current||[]).filter(t=>t&&t.time>now-60000);
      if(tapeTicks.length>=30){
        let smallBuyUSD=0,smallSellUSD=0,smallCount=0;
        tapeTicks.forEach(t=>{
          const usd=t.usd||(t.s*t.p)||0;
          if(usd<5000){
            smallCount++;
            if(t.t==='B')smallBuyUSD+=usd;else smallSellUSD+=usd;
          }
        });
        const smallPct=smallCount/tapeTicks.length;
        const smallTotal=smallBuyUSD+smallSellUSD;
        if(smallPct>=0.7&&smallTotal>0){
          const smallBuyShare=smallBuyUSD/smallTotal;
          if(smallBuyShare>=0.7){
            retailCrowd={
              detected:true,
              side:'DOWN', // Fade direction
              score:Math.min(100,smallPct*70+smallBuyShare*40),
              reason:`Retail buying chase — ${(smallPct*100).toFixed(0)}% prints <$5K, ${(smallBuyShare*100).toFixed(0)}% buys. Fade signal: DOWN`,
            };
          }else if(smallBuyShare<=0.3){
            retailCrowd={
              detected:true,
              side:'UP', // Fade direction
              score:Math.min(100,smallPct*70+(1-smallBuyShare)*40),
              reason:`Retail selling chase — ${(smallPct*100).toFixed(0)}% prints <$5K, ${((1-smallBuyShare)*100).toFixed(0)}% sells. Fade signal: UP`,
            };
          }
        }
      }
      // ═══════ #3 KALSHI-SPOT DIVERGENCE ═══════
      let kalshiSpot={detected:false,side:null,score:0,reason:''};
      const kalshiHist=(r.kalshiHistoryRef?.current||[]).filter(k=>k&&k.time>now-30000);
      if(kalshiHist.length>=3&&r.currentPrice>0){
        const kAlpha=kalshiHist[0].yes;
        const kOmega=kalshiHist[kalshiHist.length-1].yes;
        const kalshiDelta=kOmega-kAlpha; // cents
        const spotTicks=(r.tickHistoryRef?.current||[]).filter(t=>t&&t.p&&t.time>now-30000);
        if(spotTicks.length>=3){
          const sAlpha=spotTicks[0].p;
          const sOmega=spotTicks[spotTicks.length-1].p;
          const spotDeltaBps=((sOmega-sAlpha)/sAlpha)*10000;
          const absKalshi=Math.abs(kalshiDelta);
          const absSpot=Math.abs(spotDeltaBps);
          if(absKalshi>=5&&absSpot<10){
            // Kalshi moving without spot — positioning play
            kalshiSpot={
              detected:true,
              side:kalshiDelta>0?'UP':'DOWN',
              score:Math.min(100,40+absKalshi*4),
              reason:`Kalshi ${kalshiDelta>0?'+':''}${kalshiDelta.toFixed(1)}¢ in 30s, spot flat (${spotDeltaBps.toFixed(0)}bps) — info or squeeze attempt`,
            };
          }else if(absKalshi>=5&&absSpot>=10&&Math.sign(kalshiDelta)!==Math.sign(spotDeltaBps)){
            // Opposite directions — Kalshi disagrees with spot
            kalshiSpot={
              detected:true,
              side:spotDeltaBps>0?'UP':'DOWN', // trust spot direction
              score:Math.min(100,55+absKalshi*2+absSpot*0.8),
              reason:`Kalshi ${kalshiDelta>0?'+':''}${kalshiDelta.toFixed(1)}¢ but spot ${spotDeltaBps>0?'+':''}${spotDeltaBps.toFixed(0)}bps — mistrust Kalshi, follow spot`,
            };
          }
        }
      }
      // ═══════ #4 ORDER BOOK ABSORPTION ═══════
      let absorption={detected:false,side:null,score:0,reason:''};
      const absTape=(r.tradeTicksRef?.current||[]).filter(t=>t&&t.time>now-30000);
      const absPrice=(r.tickHistoryRef?.current||[]).filter(t=>t&&t.p&&t.time>now-30000);
      if(absTape.length>=20&&absPrice.length>=5){
        let buyUSD=0,sellUSD=0;
        absTape.forEach(t=>{
          const u=t.usd||(t.s*t.p)||0;
          if(t.t==='B')buyUSD+=u;else sellUSD+=u;
        });
        const sAlpha=absPrice[0].p;
        const sOmega=absPrice[absPrice.length-1].p;
        const priceDeltaBps=((sOmega-sAlpha)/sAlpha)*10000;
        // Heavy sell flow but price flat/up = bids absorbed by buyer (defender = buyer)
        if(sellUSD>=750000&&sellUSD>buyUSD*1.5&&priceDeltaBps>=-5){
          absorption={
            detected:true,
            side:'UP',
            score:Math.min(100,40+Math.log10(Math.max(1,sellUSD/100000))*22),
            reason:`$${(sellUSD/1e6).toFixed(2)}M sells absorbed in 30s, price held — buyer defending. Align: UP`,
          };
        }
        // Heavy buy flow but price flat/down = asks absorbed by seller (defender = seller)
        else if(buyUSD>=750000&&buyUSD>sellUSD*1.5&&priceDeltaBps<=5){
          absorption={
            detected:true,
            side:'DOWN',
            score:Math.min(100,40+Math.log10(Math.max(1,buyUSD/100000))*22),
            reason:`$${(buyUSD/1e6).toFixed(2)}M buys absorbed in 30s, price held — seller defending. Align: DOWN`,
          };
        }
      }
      setSignals({pinning,retailCrowd,kalshiSpot,absorption});
    },1000);
    return()=>clearInterval(iv);
  },[]);
  return signals;
}

// V8.9.0: Compact pill strip for the four smart-money detectors. Each pill:
//   - inactive: dim border + zinc text
//   - active: colored border + glow, side badge (UP / DOWN)
//   - clicking expands the reason tooltip below
function SmartMoneyStrip({signals,lockDir}){
  const[expanded,setExpanded]=React.useState(null);
  const _detectors=[
    {key:'pinning',label:'PIN',full:'Strike Pin',signal:signals.pinning},
    {key:'retailCrowd',label:'FADE',full:'Retail Crowd',signal:signals.retailCrowd},
    {key:'kalshiSpot',label:'DIV',full:'Kalshi-Spot Divergence',signal:signals.kalshiSpot},
    {key:'absorption',label:'ABSORB',full:'Book Absorption',signal:signals.absorption},
  ];
  const _activeCount=_detectors.filter(d=>d.signal.detected).length;
  // Cross-detector consensus: if 2+ detectors active and all agree on a side,
  //   that's a strong directional cue.
  const _activeSides=_detectors.filter(d=>d.signal.detected&&d.signal.side).map(d=>d.signal.side);
  const _allUp=_activeSides.length>=2&&_activeSides.every(s=>s==='UP');
  const _allDown=_activeSides.length>=2&&_activeSides.every(s=>s==='DOWN');
  const _consensusSide=_allUp?'UP':(_allDown?'DOWN':null);
  // V13.4.301: header matched to the mockup -- a neutral tiny-caps title on the
  //   left, "2 of 4 · consensus down" as plain text on the right. The title was
  //   green (it is a section name, not an outcome) and the consensus read was a
  //   bordered, tinted chip; only the direction word keeps a signal colour now.
  return React.createElement('div',{className:'mb-2'},
    React.createElement('div',{className:'flex items-baseline gap-1.5 mb-2 px-1'},
      React.createElement('span',{className:'text-[9px] uppercase tracking-[0.15em] font-bold text-[#EDEDED]/30'},
        'Smart money'
      ),
      _activeCount>0&&React.createElement('span',{className:'text-[9px] uppercase tracking-[0.15em] font-bold text-[#EDEDED]/30'},
        '· '+_activeCount+' of 4'
      ),
      _consensusSide&&React.createElement('span',{
        className:'text-[9px] uppercase tracking-[0.15em] font-bold ml-auto text-[#EDEDED]/30',
      },
        'consensus ',
        React.createElement('span',{style:{color:_consensusSide==='UP'?'#23B981':'#E8455E'}},_consensusSide.toLowerCase()),
      ),
    ),
    // V13.4.301: four hairline-divided cells over a #16161c ground, so the
    //   divider IS the gap -- the mockup's treatment, and the same one Live
    //   Feeds now uses. Replaces four separately-bordered, glowing tiles.
    React.createElement('div',{className:'grid grid-cols-4 gap-px rounded-[10px] overflow-hidden',style:{background:'#16161c'}},
      _detectors.map(d=>{
        const active=d.signal.detected;
        const side=d.signal.side;
        // Color by alignment with lock direction (if locked) or neutral when scanning
        // V13.4.301: the cell no longer carries its own fill, border or glow --
        //   the hairline grid separates them. Colour now lives only on the
        //   READING, which is the signal; the label stays a neutral tiny cap
        //   like every other label on the page. An inactive detector is a plain
        //   em-dash at 30% ink, exactly as the mockup shows Divergence and
        //   Absorption.
        let _color;
        if(active){
          _color=(lockDir&&side)?(side===lockDir?'#23B981':'#E8455E'):'#23B981';
        }else{
          _color='rgba(237,237,237,0.30)';
        }
        return React.createElement('button',{
          key:d.key,
          onClick:()=>setExpanded(expanded===d.key?null:d.key),
          className:'px-3 py-2.5 text-left transition-colors hover:brightness-125',
          style:{background:'#0A0A0E'},
          title:active?d.signal.reason:d.full+' — inactive',
        },
          React.createElement('div',{className:'text-[9px] font-bold tracking-[0.15em] uppercase text-[#EDEDED]/30 mb-1.5'},
            d.label
          ),
          React.createElement('div',{className:'tabular-nums',style:{fontSize:'16px',color:_color}},
            active?((side==='UP'?'↑':side==='DOWN'?'↓':'')+Math.round(d.signal.score)+'%'):'—'
          ),
        );
      })
    ),
    expanded&&React.createElement('div',{
      className:'mt-1.5 px-2 py-1.5 rounded-lg text-[10px] leading-relaxed',
      style:{background:'#050508',border:'1px solid #24242E',color:'rgba(237,237,237,0.75)'},
    },
      _detectors.find(d=>d.key===expanded)?.signal.detected
        ?_detectors.find(d=>d.key===expanded).signal.reason
        :_detectors.find(d=>d.key===expanded).full+' — no pattern detected right now.'
    )
  );
}

// ════════════════════════════════════════════════════════════════════════════
// V9.8.18: TARA TOAST SYSTEM
// ════════════════════════════════════════════════════════════════════════════
// Lightweight popup notifications for high-value transient events: price pumps,
// price dumps, velocity regime jumps. Mirrors the FlowPanel auto-open behavior
// (appears on event, auto-closes after a few seconds, throttled to avoid spam),
// but adds outside-click dismiss for one-tap clearing while reading the screen.
//
// Trigger sources (pushed via `pushToast` from TaraApp effects):
//   • price-pump  — currentPrice up ≥20 bps in 30s
//   • price-dump  — currentPrice down ≥20 bps in 30s
//   • vel-fast    — velocityRegime jumped NORMAL/SLOW → FAST
//   • vel-extreme — velocityRegime reached EXTREME (news-spike pace)
//
// Per-kind 60-90s cooldown enforced inside pushToast itself. Stack capped at 3
// concurrent toasts to avoid covering the dashboard.
// ────────────────────────────────────────────────────────────────────────────
function TaraToastStack({toasts,onDismiss}){
  // V9.8.18: outside-click dismiss. Anywhere outside a toast clears the entire
  //   stack. Click on a toast itself dismisses just that one (handled by inner onClick).
  //   50ms delay so the click that triggered a toast doesn't immediately dismiss it.
  React.useEffect(()=>{
    if(!toasts||toasts.length===0)return;
    const handler=(e)=>{
      try{if(e.target&&e.target.closest&&e.target.closest('[data-tara-toast]'))return;}catch(_){}
      onDismiss('all');
    };
    const tid=setTimeout(()=>document.addEventListener('mousedown',handler),50);
    return()=>{clearTimeout(tid);document.removeEventListener('mousedown',handler);};
  },[toasts.length>0,onDismiss]);
  // V9.8.18: auto-expire poll. Every 250ms, evict toasts whose expireAt has passed.
  React.useEffect(()=>{
    if(!toasts||toasts.length===0)return;
    const id=setInterval(()=>{
      const now=Date.now();
      const expired=toasts.filter(t=>t.expireAt<=now).map(t=>t.id);
      if(expired.length>0)onDismiss(expired);
    },250);
    return()=>clearInterval(id);
  },[toasts,onDismiss]);
  if(!toasts||toasts.length===0)return null;
  return React.createElement('div',{
    // V13.4.304: this stack renders at the app root, before any Sports/Weather
    //   tab switching -- toasts are global BY DESIGN (a BTC velocity spike is
    //   worth seeing even while checking the weather board), so scoping them to
    //   one tab is not the fix. The bug was sizing: a fixed minWidth:280px badge
    //   anchored right-4 is a small desktop corner element, but on a 375px phone
    //   it eats ~75% of the screen width and overlaps whatever tab is showing
    //   underneath -- caught covering Weather's city pills and header. Below sm,
    //   left-2/right-2 with no explicit width lets the browser size the box from
    //   its insets (full width minus 16px margins); at sm+ it reverts to the
    //   original right-anchored corner badge.
    className:'fixed top-20 left-2 right-2 sm:left-auto sm:right-4 z-[100] flex flex-col gap-2 pointer-events-none sm:min-w-[280px] sm:max-w-[340px]',
  },toasts.map(t=>React.createElement('div',{
    key:t.id,
    'data-tara-toast':'1',
    className:'pointer-events-auto rounded-lg backdrop-blur-md px-4 py-3 cursor-pointer transition-opacity',
    style:{
      background:'#050508',
      border:`1px solid ${t.color||'#23B981'}66`,
      boxShadow:`0 4px 20px ${t.color||'#23B981'}22, 0 1px 0 rgba(255,255,255,0.04) inset`,
    },
    onClick:()=>{
      // V9.8.19: if toast carries a URL, open in new tab on click. Always dismiss
      //   the toast itself afterward so the click is single-action.
      if(t.url){try{window.open(t.url,'_blank','noopener,noreferrer');}catch(_){}}
      onDismiss([t.id]);
    },
    title:t.url?'click to read article':'click to dismiss',
  },[
    React.createElement('div',{key:'r1',className:'flex items-center gap-2'},[
      React.createElement('span',{key:'icon',style:{fontSize:'15px',lineHeight:1}},t.icon||'⚡'),
      React.createElement('span',{key:'title',className:'text-[13px] font-bold tracking-wide flex-1',style:{color:t.color||'#23B981'}},t.title),
      t.url?React.createElement('span',{key:'open',className:'text-[10px] opacity-60',style:{color:t.color||'#23B981'}},'↗'):null,
    ].filter(Boolean)),
    t.body?React.createElement('div',{key:'body',className:'text-[11px] mt-1 leading-snug',style:{color:'rgba(237,237,237,0.78)'}},t.body):null,
  ].filter(Boolean))));
}

// ════════════════════════════════════════════════════════════════════════════
// V9.8.18: DAY-AWARE SCHEDULE
// ════════════════════════════════════════════════════════════════════════════
// Crypto trades 24/7 but flows shift dramatically across days — institutional
// volume falls off Saturday/Sunday, US holidays lighten flow, Mondays often have
// catch-up volume from weekend news. Tara should know what day she's in.
//
// `buildDayHourPerf(callLog)` produces three views from taraCallLog:
//   • grid  — `${dayIdx}-${hour}` → {n, wins, wr}  (per-day-per-hour)
//   • byDay — `${dayIdx}` → {n, wins, wr}          (per day-of-week aggregate)
//   • byHour — `${hour}` → {n, wins, wr}            (per-hour aggregate, all days)
//
// `buildDayContext(perf, now)` derives today's character: name, weekend flag,
// holiday flag, rank vs other days, activity label, today's best hours, next
// upcoming high-WR window. Powers the day-aware schedule header.
// ────────────────────────────────────────────────────────────────────────────
const buildDayHourPerf=(callLog)=>{
  const grid={},byDay={},byHour={};
  for(const t of(callLog||[])){
    if(!t||(t.result!=='WIN'&&t.result!=='LOSS'))continue;
    const date=new Date(t.id||t.lockedAt||0);
    if(isNaN(date.getTime()))continue;
    const dayIdx=date.getDay();
    const hour=date.getHours();
    const k=`${dayIdx}-${hour}`;
    // V9.9.2: price-action characteristics per cell. closingGapBps captures the
    //   magnitude of moves (high abs = volatile, low abs = marginal/rangebound);
    //   atrBpsAtLock captures volatility regime at lock time. Sums + counts let us
    //   compute means cheaply per cell or per day-of-week.
    const _gap=typeof t.closingGapBps==='number'&&Number.isFinite(t.closingGapBps)?Math.abs(t.closingGapBps):null;
    const _atr=typeof t.atrBpsAtLock==='number'&&Number.isFinite(t.atrBpsAtLock)?t.atrBpsAtLock:null;
    if(!grid[k])grid[k]={n:0,wins:0,gapSum:0,gapN:0,atrSum:0,atrN:0};
    grid[k].n++;if(t.result==='WIN')grid[k].wins++;
    if(_gap!=null){grid[k].gapSum+=_gap;grid[k].gapN++;}
    if(_atr!=null){grid[k].atrSum+=_atr;grid[k].atrN++;}
    if(!byDay[dayIdx])byDay[dayIdx]={n:0,wins:0,gapSum:0,gapN:0,atrSum:0,atrN:0,winGapSum:0,winGapN:0,lossGapSum:0,lossGapN:0};
    byDay[dayIdx].n++;if(t.result==='WIN')byDay[dayIdx].wins++;
    if(_gap!=null){
      byDay[dayIdx].gapSum+=_gap;byDay[dayIdx].gapN++;
      if(t.result==='WIN'){byDay[dayIdx].winGapSum+=_gap;byDay[dayIdx].winGapN++;}
      else{byDay[dayIdx].lossGapSum+=_gap;byDay[dayIdx].lossGapN++;}
    }
    if(_atr!=null){byDay[dayIdx].atrSum+=_atr;byDay[dayIdx].atrN++;}
    if(!byHour[hour])byHour[hour]={n:0,wins:0};
    byHour[hour].n++;if(t.result==='WIN')byHour[hour].wins++;
  }
  for(const k of Object.keys(grid)){
    grid[k].wr=grid[k].n>0?grid[k].wins/grid[k].n:0;
    grid[k].avgGap=grid[k].gapN>0?grid[k].gapSum/grid[k].gapN:null;
    grid[k].avgAtr=grid[k].atrN>0?grid[k].atrSum/grid[k].atrN:null;
  }
  for(const k of Object.keys(byDay)){
    byDay[k].wr=byDay[k].n>0?byDay[k].wins/byDay[k].n:0;
    byDay[k].avgGap=byDay[k].gapN>0?byDay[k].gapSum/byDay[k].gapN:null;
    byDay[k].avgAtr=byDay[k].atrN>0?byDay[k].atrSum/byDay[k].atrN:null;
    byDay[k].avgWinGap=byDay[k].winGapN>0?byDay[k].winGapSum/byDay[k].winGapN:null;
    byDay[k].avgLossGap=byDay[k].lossGapN>0?byDay[k].lossGapSum/byDay[k].lossGapN:null;
  }
  for(const k of Object.keys(byHour))byHour[k].wr=byHour[k].n>0?byHour[k].wins/byHour[k].n:0;
  return{grid,byDay,byHour};
};

// V9.8.18: hardcoded US federal + observed holidays for 2026. Crypto trades 24/7
//   but institutional flows lighten meaningfully on these days — Tara should warn
//   the user that today's pattern may diverge from typical weekday behavior.
const US_HOLIDAYS_2026={
  '2026-01-01':"New Year's Day",
  '2026-01-19':'MLK Day',
  '2026-02-16':'Presidents Day',
  '2026-05-25':'Memorial Day',
  '2026-06-19':'Juneteenth',
  '2026-07-03':'July 4th (observed)',
  '2026-07-04':'Independence Day',
  '2026-09-07':'Labor Day',
  '2026-10-12':'Columbus Day',
  '2026-11-11':'Veterans Day',
  '2026-11-26':'Thanksgiving',
  '2026-11-27':'Day After Thanksgiving',
  '2026-12-24':'Christmas Eve',
  '2026-12-25':'Christmas Day',
};
const _ymdLocalKey=(date)=>{
  const y=date.getFullYear();
  const m=String(date.getMonth()+1).padStart(2,'0');
  const d=String(date.getDate()).padStart(2,'0');
  return`${y}-${m}-${d}`;
};

const buildDayContext=(dayHourPerf,now)=>{
  if(!dayHourPerf)return null;
  const _now=now||new Date();
  const days=['SUN','MON','TUE','WED','THU','FRI','SAT'];
  const dayLong=['Sunday','Monday','Tuesday','Wednesday','Thursday','Friday','Saturday'];
  const todayIdx=_now.getDay();
  const todayName=days[todayIdx];
  const todayLong=dayLong[todayIdx];
  const isWeekend=todayIdx===0||todayIdx===6;
  const holiday=US_HOLIDAYS_2026[_ymdLocalKey(_now)]||null;
  const byDay=dayHourPerf.byDay||{};
  // Day ranking by WR (require ≥10 trades for meaningful rank)
  const dayRanking=Object.entries(byDay)
    .filter(([_,v])=>v.n>=10)
    .map(([k,v])=>({dayIdx:parseInt(k),wr:v.wr,n:v.n,name:days[parseInt(k)],avgGap:v.avgGap,avgAtr:v.avgAtr,avgWinGap:v.avgWinGap,avgLossGap:v.avgLossGap}))
    .sort((a,b)=>b.wr-a.wr);
  const todayEntry=dayRanking.find(d=>d.dayIdx===todayIdx)||(byDay[todayIdx]?{dayIdx:todayIdx,wr:byDay[todayIdx].wr,n:byDay[todayIdx].n,name:todayName,avgGap:byDay[todayIdx].avgGap,avgAtr:byDay[todayIdx].avgAtr,avgWinGap:byDay[todayIdx].avgWinGap,avgLossGap:byDay[todayIdx].avgLossGap}:null);
  const todayRank=dayRanking.findIndex(d=>d.dayIdx===todayIdx)+1;
  const totalRanked=dayRanking.length;
  // Activity level — based on # trades observed for this day-of-week as a proxy
  //   for flow activity. Many trades = active flow; few trades = quiet flow.
  const dayTradeCounts=Object.values(byDay).map(v=>v.n);
  const avgTrades=dayTradeCounts.length>0?dayTradeCounts.reduce((a,b)=>a+b,0)/dayTradeCounts.length:0;
  const todayN=byDay[todayIdx]?.n||0;
  let activityLabel='unknown';
  if(todayN>0){
    if(avgTrades>0&&todayN>=avgTrades*1.2)activityLabel='active';
    else if(avgTrades>0&&todayN<=avgTrades*0.6)activityLabel='quiet';
    else activityLabel='average';
  }
  // Today's best/worst hours (need ≥3 trades per hour for any signal)
  const grid=dayHourPerf.grid||{};
  const todayHours=[];
  for(let h=0;h<24;h++){
    const cell=grid[`${todayIdx}-${h}`];
    if(cell&&cell.n>=3)todayHours.push({hour:h,n:cell.n,wins:cell.wins,wr:cell.wr,avgGap:cell.avgGap,avgAtr:cell.avgAtr});
  }
  const todayBest=todayHours.filter(h=>h.wr>=0.65).sort((a,b)=>b.wr-a.wr).slice(0,3);
  const todayWorst=todayHours.filter(h=>h.wr<=0.45).sort((a,b)=>a.wr-b.wr).slice(0,3);
  // Next upcoming best window (after current local hour)
  const curH=_now.getHours();
  const curMin=_now.getMinutes();
  const upcomingBest=todayBest
    .map(h=>({...h,minsUntil:h.hour>curH?(h.hour-curH)*60-curMin:null}))
    .filter(h=>h.minsUntil!=null&&h.minsUntil>0)
    .sort((a,b)=>a.minsUntil-b.minsUntil)[0]||null;
  // V9.9.2: Coming "danger" window — next worst-hour today the user might walk into.
  //   Surfaced separately from upcomingBest so the schedule can warn ahead of time.
  const upcomingWorst=todayWorst
    .map(h=>({...h,minsUntil:h.hour>curH?(h.hour-curH)*60-curMin:null}))
    .filter(h=>h.minsUntil!=null&&h.minsUntil>0&&h.minsUntil<=180) // only flag within next 3h
    .sort((a,b)=>a.minsUntil-b.minsUntil)[0]||null;

  // V9.9.2: CHARACTER CLASSIFICATION + ADVICE
  // ─────────────────────────────────────────
  // Combines WR rank, sample size, and price-action volatility (avgAtr, avgGap)
  // into a single "character" label + an actionable mode.
  //
  // Modes drive both UI tone and how computeReversalRisk weighs the day-disadvantage
  // signal. Five buckets:
  //
  //   • prime         — top WR day, healthy sample, normal-to-high volatility
  //   • aggressive    — solid WR + high vol (fast moves, room for edge)
  //   • normal        — middle of the pack, no alarm
  //   • selective     — below-average WR or compressed volatility (be picky)
  //   • defensive     — bottom WR + low sample or low vol (high confluence only)
  //
  // The text adapts to your actual numbers, not a generic template — Tuesday at
  // 75% WR with 12bps avgAtr says "your strongest day, take normal positions";
  // Sunday at 56% with 7bps avgAtr says "marginal moves, only enter on confluence".
  let dayMode='normal',dayHeadline='',dayAdvice='',dayAdviceColor='#94a3b8';
  if(todayEntry&&todayEntry.n>=10){
    const _wrPct=Math.round(todayEntry.wr*100);
    const _atr=todayEntry.avgAtr;
    const _winGap=todayEntry.avgWinGap;
    const _lossGap=todayEntry.avgLossGap;
    // Rank tiers: top-2 / mid / bottom-2 in dayRanking
    const _isTopRanked=todayRank>0&&todayRank<=2;
    const _isBottomRanked=totalRanked>=4&&todayRank>=totalRanked-1;
    // Volatility classification — ATR-bps thresholds derived from observed BTC 15m windows
    const _highVol=_atr!=null&&_atr>=12;
    const _lowVol=_atr!=null&&_atr<=8;
    // Activity-WR coupling
    if(_isTopRanked&&_highVol){
      dayMode='aggressive';
      dayAdviceColor='#6FA98C';
      dayHeadline=`Strong day · ${_wrPct}% WR with ${_atr.toFixed(0)}bps avg vol`;
      dayAdvice=`${todayLong}s have been your edge. Take aggressive sizing, hold marginal locks longer.`;
    }else if(_isTopRanked){
      dayMode='prime';
      dayAdviceColor='#6FA98C';
      dayHeadline=`Top WR day · ${_wrPct}% (rank ${todayRank}/${totalRanked})`;
      dayAdvice=`Lean in on confluent setups. Volatility is ${_lowVol?'compressed':'normal'}, so trust signal quality over breakout chasing.`;
    }else if(_isBottomRanked&&_lowVol){
      dayMode='defensive';
      dayAdviceColor='rgba(232,69,94,0.72)';
      dayHeadline=`Tough day · ${_wrPct}% WR, ${_atr?_atr.toFixed(0)+'bps':'low'} vol${_winGap&&_lossGap?` · wins ${_winGap.toFixed(0)}bps / losses ${_lossGap.toFixed(0)}bps`:''}`;
      dayAdvice=`Marginal moves, narrow edge. Only enter on confluence+ tier. Skip single-signal setups.`;
    }else if(_isBottomRanked){
      dayMode='selective';
      dayAdviceColor='#23B981';
      dayHeadline=`Below-avg day · ${_wrPct}% WR (rank ${todayRank}/${totalRanked})`;
      dayAdvice=`Be picky. Skip weak-conviction locks; the day's distribution doesn't favor marginal entries.`;
    }else if(_lowVol){
      dayMode='selective';
      dayAdviceColor='#23B981';
      dayHeadline=`Compressed vol · ${_atr.toFixed(0)}bps avg`;
      dayAdvice=`Range-bound conditions. Watch for false breakouts. Wait for clean structural setups.`;
    }else{
      dayMode='normal';
      dayAdviceColor='#94a3b8';
      dayHeadline=`Normal day · ${_wrPct}% WR${_atr?` · ${_atr.toFixed(0)}bps vol`:''}`;
      dayAdvice=`No special bias. Trade your usual selectivity.`;
    }
  }else if(holiday){
    dayMode='selective';dayAdviceColor='#23B981';
    dayHeadline=`Holiday · light flow expected`;
    dayAdvice=`Institutional flow lighter than usual on ${holiday}. Reduce sizing, expect choppy execution.`;
  }else if(isWeekend){
    dayMode='selective';dayAdviceColor='#23B981';
    dayHeadline=`Weekend · marginal moves typical`;
    dayAdvice=`Crypto-only flow without traditional finance. Smaller moves, tighter ranges. Be patient with entries.`;
  }else{
    dayHeadline=`${todayLong} — building day-character (need ≥10 trades)`;
    dayAdvice=`Not enough history yet. Trade your normal selectivity; we'll refine guidance after more data.`;
  }
  return{
    todayName,todayLong,todayIdx,isWeekend,holiday,
    dayRanking,todayEntry,todayRank,totalRanked,
    activityLabel,todayBest,todayWorst,upcomingBest,upcomingWorst,
    dayMode,dayHeadline,dayAdvice,dayAdviceColor,
  };
};

// V9.8.18: day-aware schedule banner. Renders above TradeScheduleStrip with
//   today's character: weekday/weekend, holiday flag, activity level, rank vs
//   other days, next upcoming strong window. Compact one or two-row strip.
function DayAwareScheduleHeader({dayContext}){
  const[expanded,setExpanded]=React.useState(false);
  if(!dayContext)return null;
  const{todayLong,isWeekend,holiday,todayEntry,todayRank,totalRanked,activityLabel,upcomingBest,upcomingWorst,dayMode,dayHeadline,dayAdvice,dayAdviceColor,dayRanking,todayBest,todayWorst}=dayContext;
  const _activityColor=activityLabel==='active'?'#6FA98C':activityLabel==='quiet'?'#94a3b8':activityLabel==='average'?'#23B981':'#94a3b8';
  // V9.9.2: mode-driven container tint. Visual reinforces the advice without
  //   needing to read it — green-bordered = aggressive day, rose-bordered = defensive.
  const _modeColor=dayMode==='aggressive'||dayMode==='prime'?'#6FA98C':
                    dayMode==='defensive'?'rgba(232,69,94,0.72)':
                    dayMode==='selective'?'#23B981':
                    'rgba(35,185,129,0.5)';
  const _modeLabel=dayMode==='aggressive'?'AGGRESSIVE':dayMode==='prime'?'PRIME':dayMode==='defensive'?'DEFENSIVE':dayMode==='selective'?'SELECTIVE':'NORMAL';
  return React.createElement('div',{
    className:'flex flex-col gap-1.5 px-3 py-2 rounded-lg mb-2 min-w-0 overflow-hidden',
    style:{background:`${_modeColor}0c`,border:`1px solid ${_modeColor}33`},
  },[
    // Row 1: day name + mode badge + WR/rank
    React.createElement('div',{key:'r1',className:'flex items-center justify-between gap-2 text-[11px] flex-wrap min-w-0'},[
      React.createElement('div',{key:'left',className:'flex items-center gap-1.5 flex-wrap min-w-0'},[
        React.createElement('span',{key:'name',className:'font-bold uppercase tracking-wide truncate',style:{color:'#EDEDED'}},todayLong),
        isWeekend?React.createElement('span',{key:'we',className:'px-1.5 py-0.5 rounded-lg text-[9px] font-bold uppercase shrink-0',style:{background:'rgba(148,163,184,0.15)',color:'#94a3b8'}},'WEEKEND'):null,
        dayMode!=='normal'&&dayMode!=='unknown'?React.createElement('span',{key:'mode',className:'px-1.5 py-0.5 rounded-lg text-[9px] font-bold uppercase shrink-0',style:{background:`${_modeColor}1c`,color:_modeColor,border:`1px solid ${_modeColor}55`}},_modeLabel):null,
        activityLabel!=='unknown'&&activityLabel!=='average'?React.createElement('span',{key:'act',className:'px-1.5 py-0.5 rounded-lg text-[9px] font-bold uppercase shrink-0',style:{background:`${_activityColor}1c`,color:_activityColor}},activityLabel):null,
      ].filter(Boolean)),
      todayEntry&&todayEntry.n>0?React.createElement('button',{
        key:'right',
        type:'button',
        onClick:()=>setExpanded(v=>!v),
        className:'flex items-center gap-1.5 text-[10px] shrink-0',
        style:{color:'rgba(237,237,237,0.66)'},
        title:'tap for full day breakdown',
      },[
        React.createElement('span',{key:'t'},`WR ${(todayEntry.wr*100).toFixed(0)}% · n=${todayEntry.n}${todayRank>0&&totalRanked>=3?` · #${todayRank}/${totalRanked}`:''}`),
        React.createElement('span',{key:'c',className:'text-[9px] opacity-60',style:{transform:expanded?'rotate(180deg)':'none',transition:'transform 0.2s'}},'▾'),
      ]):null,
    ].filter(Boolean)),
    // Row 2: dayHeadline (mode-tinted)
    dayHeadline?React.createElement('div',{key:'head',className:'text-[10px] font-medium leading-snug',style:{color:dayAdviceColor||'rgba(237,237,237,0.85)'}},dayHeadline):null,
    // Row 3: dayAdvice (the actionable text)
    dayAdvice?React.createElement('div',{key:'adv',className:'text-[10px] leading-snug',style:{color:'rgba(237,237,237,0.7)'}},dayAdvice):null,
    // Row 4: holiday warning (separate so it stays visible even if collapsed)
    holiday?React.createElement('div',{key:'hol',className:'text-[10px] flex items-center gap-1.5',style:{color:'#23B981'}},[
      React.createElement('span',{key:'i',style:{fontSize:'10px'}},'🇺🇸'),
      React.createElement('span',{key:'t'},`${holiday} — institutional flow may be lighter than usual`),
    ]):null,
    // Row 5: next strong / next weak window — quick glance ahead
    upcomingBest?React.createElement('div',{key:'up',className:'text-[10px]',style:{color:'#23B981'}},
      `▸ Next strong window: ${String(upcomingBest.hour).padStart(2,'0')}:00 (in ${upcomingBest.minsUntil}m) · ${(upcomingBest.wr*100).toFixed(0)}% WR (n=${upcomingBest.n})`
    ):null,
    upcomingWorst?React.createElement('div',{key:'down',className:'text-[10px]',style:{color:'#E8455E'}},
      `▸ Caution window ahead: ${String(upcomingWorst.hour).padStart(2,'0')}:00 (in ${upcomingWorst.minsUntil}m) · ${(upcomingWorst.wr*100).toFixed(0)}% WR (n=${upcomingWorst.n})`
    ):null,
    // Expanded detail: full day ranking + best/worst hours today
    expanded?React.createElement('div',{
      key:'detail',
      className:'flex flex-col gap-2 mt-1 pt-2 text-[10px]',
      style:{borderTop:'1px solid #24242E',color:'rgba(237,237,237,0.75)'},
    },[
      // Day ranking
      dayRanking&&dayRanking.length>=3?React.createElement('div',{key:'rank',className:'flex flex-col gap-0.5'},[
        React.createElement('div',{key:'h',className:'text-[9px] uppercase font-bold tracking-wide',style:{color:'rgba(237,237,237,0.5)'}},'Day-of-week ranking'),
        ...dayRanking.map(d=>React.createElement('div',{
          key:d.dayIdx,
          className:'flex items-center justify-between gap-2 min-w-0',
          style:d.dayIdx===dayContext.todayIdx?{color:'#23B981',fontWeight:'600'}:undefined,
        },[
          React.createElement('span',{key:'n',className:'truncate'},`${d.dayIdx===dayContext.todayIdx?'▸ ':'  '}${d.name}`),
          React.createElement('span',{key:'s',className:'shrink-0 font-mono',style:{fontSize:'9px',opacity:0.85}},`${(d.wr*100).toFixed(0)}% · n=${d.n}${d.avgAtr?` · ${d.avgAtr.toFixed(0)}bps`:''}`),
        ])),
      ]):null,
      // Today's best hours
      todayBest&&todayBest.length>0?React.createElement('div',{key:'best',className:'flex flex-col gap-0.5'},[
        React.createElement('div',{key:'h',className:'text-[9px] uppercase font-bold tracking-wide',style:{color:'rgba(35,185,129,0.7)'}},`Today's strongest hours`),
        ...todayBest.map(h=>React.createElement('div',{key:'b'+h.hour,className:'flex items-center justify-between gap-2'},[
          React.createElement('span',{key:'h'},`${String(h.hour).padStart(2,'0')}:00`),
          React.createElement('span',{key:'s',className:'font-mono',style:{fontSize:'9px',opacity:0.85}},`${(h.wr*100).toFixed(0)}% · n=${h.n}`),
        ])),
      ]):null,
      // Today's worst hours
      todayWorst&&todayWorst.length>0?React.createElement('div',{key:'worst',className:'flex flex-col gap-0.5'},[
        React.createElement('div',{key:'h',className:'text-[9px] uppercase font-bold tracking-wide',style:{color:'rgba(232,69,94,0.7)'}},`Today's weakest hours`),
        ...todayWorst.map(h=>React.createElement('div',{key:'w'+h.hour,className:'flex items-center justify-between gap-2'},[
          React.createElement('span',{key:'h'},`${String(h.hour).padStart(2,'0')}:00`),
          React.createElement('span',{key:'s',className:'font-mono',style:{fontSize:'9px',opacity:0.85}},`${(h.wr*100).toFixed(0)}% · n=${h.n}`),
        ])),
      ]):null,
    ].filter(Boolean)):null,
  ].filter(Boolean));
}

// ════════════════════════════════════════════════════════════════════════════
// V9.8.19: NEWS IMPACT SCORING
// ════════════════════════════════════════════════════════════════════════════
// Scans a news headline for keywords associated with high-impact crypto/macro
// events. Returns {score, direction, category}. Scores ≥6 are considered toast-
// worthy. Direction inference picks bullish/bearish/neutral based on which set
// of price-direction keywords are present.
//
// Categories tracked:
//   • macro     — Fed, CPI, rate, inflation, FOMC, NFP, recession (highest impact)
//   • regulatory — SEC, ETF, lawsuit, approve/reject, regulation
//   • security   — hack, exploit, breach, halt, bankruptcy
//   • flow       — large institutional buys/sells, ETF flows
//   • generic    — has crypto keyword but no specific category match
//
// Required: title must mention BTC/Bitcoin/ETH/Ethereum/crypto OR be in macro
// category. Pure tech-stock news, weather, etc. score 0 and are ignored.
// ────────────────────────────────────────────────────────────────────────────
const scoreNewsImpact=(title)=>{
  if(!title||typeof title!=='string')return{score:0,direction:null,category:null};
  const _t=title.toLowerCase();
  const hasAny=(arr)=>arr.some(k=>_t.includes(k));
  // Crypto-relevance gate (one match required, OR matches macro category)
  const cryptoKeywords=['bitcoin','btc','ethereum','eth','crypto','blockchain','solana','sol','dogecoin','xrp','altcoin','stablecoin','defi','nft'];
  const isCryptoRelated=hasAny(cryptoKeywords);
  // Category scoring
  let score=0;
  let category=null;
  // Macro — highest impact (always trigger-eligible regardless of crypto mention)
  const macroKeywords=['fed ','federal reserve','fomc','rate hike','rate cut','interest rate','inflation','cpi','ppi','pce','jobs report','nfp','non-farm','unemployment','recession','gdp','treasury','jerome powell','janet yellen','lagarde'];
  if(hasAny(macroKeywords)){score+=6;category='macro';}
  // Regulatory
  const regKeywords=['sec ','spot etf','etf approval','etf reject','etf flows','lawsuit','indictment','indicted','sentenced','charges','regulation','ban on','approves','approved','rejected','denied','crackdown'];
  if(hasAny(regKeywords)){score+=5;if(!category)category='regulatory';}
  // Security/exchange events
  const secKeywords=['hack','hacked','exploit','exploited','breach','stolen','rugpull','rug pull','bankruptcy','bankrupt','halt','frozen','suspended','default','collapse','liquidation','liquidations'];
  if(hasAny(secKeywords)){score+=5;if(!category)category='security';}
  // Institutional flow
  const flowKeywords=['microstrategy','blackrock','fidelity','bought','buys','accumulates','accumulation','outflow','inflow','whale','treasury holding','institutional'];
  if(hasAny(flowKeywords)){score+=2;if(!category)category='flow';}
  // Direction-strong keywords (crash/soar tier — usually big move accompanying)
  const downStrong=['crash','plunge','plunges','tanks','tanked','collapse','collapses','dives','dump','plummet','plummets','sell-off','selloff','wipeout','breaks below','below $','liquidated'];
  const upStrong=['soar','soars','rocket','surge','surges','skyrocket','rally','rallies','breakout','all-time high','record high','ath','breaks above','above $'];
  let direction=null;
  if(hasAny(downStrong)){score+=3;direction='down';}
  if(hasAny(upStrong)){score+=3;direction=direction==='down'?'mixed':'up';}
  // Direction-mild keywords (drops/rises tier — modest signal)
  const downMild=['drop','drops','dropping','falls','falling','slides','slipping','slumps','bearish','bear ','sell pressure','hawkish','retreats'];
  const upMild=['rises','rising','climbs','jumps','gains','gaining','bullish','bull ','buy pressure','dovish','recovers','recovery'];
  if(!direction){
    if(hasAny(downMild)){score+=1;direction='down';}
    if(hasAny(upMild)){score+=1;direction=direction==='down'?'mixed':'up';}
  }
  // Crypto-relevance bonus for the score (matched and crypto-related ⇒ +1)
  if(isCryptoRelated&&score>0)score+=1;
  // If category is macro, allow non-crypto news through (Fed news affects everything).
  //   Otherwise require crypto-relevance for the toast to fire.
  if(category!=='macro'&&!isCryptoRelated)score=Math.min(score,2); // cap non-crypto non-macro at 2 (subthreshold)
  return{score,direction,category};
};

// ════════════════════════════════════════════════════════════════════════════
// V9.9.0: PREDICTIVE REVERSAL SYSTEM
// ════════════════════════════════════════════════════════════════════════════
// At lock formation, Tara now confesses what she doesn't trust about her own
// commit. Conviction% alone is too coarse — the CSV showed 80% conviction in
// TRENDING UP × structural × UP delivers 37.5% WR. Hidden dimensions (tier,
// regime sub-bucket, contrary whale flow, FGT disagreement) collapse into a
// single overconfident number.
//
// computeReversalRisk takes the lock context and returns:
//   {flag: 'NONE'|'WATCH'|'EXPECTED', score: 0-100, signals: [...], computedAt}
//
// Score thresholds (calibrated from 2026-05-08 CSV audit):
//   • 0-29:  NONE — no chip rendered, lock looks clean
//   • 30-59: WATCH — amber chip, hover/tap for signal list
//   • 60+:   EXPECTED — rose chip, top-3 signals shown inline
//
// Weights are CSV-validated where possible (structural-tier, regime sub-buckets,
// fgt opposition, qScore) and heuristic where data is thin (contrary-whale,
// late-lock, day-of-week safety net). Heuristic weights will be recalibrated in
// V9.9.2 once we have 200+ stamped locks with reversalRisk + actualPeakDrawdown
// from V9.8.14's mid-trade timeSeries capture.
//
// Architecture: pure advisory (option A from spec). Always lock if conviction
// qualifies; surface reversalRisk as chip + coach + Discord field. No gating.
// ────────────────────────────────────────────────────────────────────────────
const computeReversalRisk=(params)=>{
  const{tier,regime,dir,qScore,mtfAlignment,secondsIntoWindow,whaleLog,dayContext}=params||{};
  const signals=[];
  let score=0;
  // V9.9.1: tier-name normalization. The engine emits long-form labels like
  //   'structural-led' and 'tape-led' (set at L22960 area), while the CSV export
  //   at L11133 derives short-form labels from boolean flags ('structural', 'tape').
  //   V9.9.0's checks compared only to the short form — so at runtime, when called
  //   with the engine label 'structural-led', the +30 structural-tier weight and
  //   the +20 trending-up-structural-up combo BOTH silently failed. Result: every
  //   structural-led lock in TRENDING UP scored exactly 12 (regime-only), flag NONE,
  //   chip never rendered. The 4 stamped reversalRisk entries from 2026-05-08 evening
  //   all show this exact pattern. Fix: collapse both forms to a canonical short form
  //   on entry, then compare to that throughout the function.
  const _tierCanon=
    tier==='structural-led'||tier==='structural'?'structural':
    tier==='tape-led'||tier==='tape'?'tape':
    tier==='super-confluence'||tier==='super'?'super':
    tier==='rising-confluence'||tier==='rising'?'rising':
    tier==='confluence'?'confluence':
    tier||'';
  // ── Tier check (CSV-validated: structural 52.8% WR, n=36) ──
  if(_tierCanon==='structural'){
    signals.push({key:'structural-tier',weight:30,fired:true,reason:'Structural tier locks ran 52.8% WR — barely above coin flip'});
    score+=30;
  }
  // ── Regime checks (CSV-validated) ──
  if(regime==='TRENDING UP'){
    signals.push({key:'trending-up-regime',weight:12,fired:true,reason:'TRENDING UP regime ran 58.3% WR — problem zone'});
    score+=12;
    if(_tierCanon==='structural'&&dir==='UP'){
      signals.push({key:'tup-struct-up',weight:20,fired:true,reason:'TRENDING UP × structural × UP ran 37.5% WR — worst sub-bucket observed'});
      score+=20;
    }
  }else if(regime==='SHORT SQUEEZE'){
    signals.push({key:'short-squeeze-regime',weight:10,fired:true,reason:'SHORT SQUEEZE regime ran 61.9% WR'});
    score+=10;
  }
  // ── FGT (multi-TF alignment) opposes lock direction ──
  // mtfAlignment ranges roughly -1..+1. Negative opposes UP, positive opposes DOWN.
  if(typeof mtfAlignment==='number'&&Number.isFinite(mtfAlignment)){
    const _opposing=(dir==='UP'&&mtfAlignment<=-0.4)||(dir==='DOWN'&&mtfAlignment>=0.4);
    if(_opposing){
      signals.push({key:'fgt-opposes',weight:15,fired:true,reason:`FGT ${mtfAlignment>=0?'+':''}${mtfAlignment.toFixed(1)} opposes lock direction`});
      score+=15;
    }
  }
  // ── Quality score below 40 ──
  if(typeof qScore==='number'&&qScore<40&&qScore>=0){
    signals.push({key:'q-low',weight:10,fired:true,reason:`Quality score ${qScore}/100 — below 40 threshold`});
    score+=10;
  }
  // ── Recent contrary whale ≥$500K within 10min (heuristic — not CSV-validated) ──
  // The 2026-05-08 14:16 screenshot loss had a fresh contrary BUY whale 8min before
  //   lock. The pattern showed up across multiple losing locks but we don't have
  //   "contrary whale at lock" in CSV columns yet — V9.9.2 will add and recalibrate.
  if(Array.isArray(whaleLog)&&whaleLog.length>0){
    const _now=Date.now();
    let _recent=null;
    for(const w of whaleLog){
      if(!w||!w.time||!w.usd||!w.side)continue;
      if(_now-w.time>10*60*1000)continue; // beyond 10min — not "recent"
      if(w.usd<500000)continue; // below threshold — not "whale"
      // Contrary: BUY opposes DOWN; SELL opposes UP
      const _isContrary=(dir==='DOWN'&&w.side==='BUY')||(dir==='UP'&&w.side==='SELL');
      if(!_isContrary)continue;
      if(!_recent||w.usd>_recent.usd)_recent=w; // pick the largest contrary
    }
    if(_recent){
      const _ageMin=Math.max(1,Math.round((_now-_recent.time)/60000));
      signals.push({key:'contrary-whale',weight:25,fired:true,reason:`$${Math.round(_recent.usd/1000)}K ${_recent.side} whale ${_ageMin}m ago opposes your ${dir}`});
      score+=25;
    }
  }
  // ── Late lock (>10min into 15m window — entry has less time to develop) ──
  if(typeof secondsIntoWindow==='number'&&secondsIntoWindow>600){
    signals.push({key:'late-lock',weight:8,fired:true,reason:`Locked ${secondsIntoWindow}s into 15m window — late entry (less reversal margin)`});
    score+=8;
  }
  // ── V9.9.2: day-mode signal. Replaces V9.9.0's binary "day-poor" with a
  //   richer, three-tier signal driven by buildDayContext's character classification:
  //     • defensive day (low WR + low vol)        → +12 (high reversal risk)
  //     • selective day (low WR or compressed)    → +6  (medium reversal risk)
  //     • aggressive/prime/normal                 → 0   (no signal)
  //   Day-of-week historical WR alone was too coarse — a high-WR day with
  //   compressed vol is still risky for marginal entries; a low-WR day with high
  //   vol may not be risky for confluent locks. Mode classification combines both.
  if(dayContext&&dayContext.dayMode){
    if(dayContext.dayMode==='defensive'){
      const _wrPct=dayContext.todayEntry?Math.round(dayContext.todayEntry.wr*100):null;
      signals.push({key:'day-defensive',weight:12,fired:true,reason:`${dayContext.todayLong||'Today'} runs defensive — ${_wrPct?_wrPct+'% WR':'low WR'}, marginal moves`});
      score+=12;
    }else if(dayContext.dayMode==='selective'){
      signals.push({key:'day-selective',weight:6,fired:true,reason:dayContext.dayHeadline||'Selective day — be picky on entries'});
      score+=6;
    }
  }
  // ── US holiday (light institutional flow context) ──
  if(dayContext&&dayContext.holiday){
    signals.push({key:'holiday',weight:5,fired:true,reason:`${dayContext.holiday} — institutional flow lighter than usual`});
    score+=5;
  }
  let flag='NONE';
  if(score>=60)flag='EXPECTED';
  else if(score>=30)flag='WATCH';
  return{flag,score,signals,computedAt:Date.now()};
};

// V9.9.0: ReversalRiskChip — compact, color-coded, tap-to-expand. Renders only
//   when reversalRisk.flag is WATCH or EXPECTED (NONE is silent — clean locks
//   stay visually clean). Layout is single row when collapsed; expanded form
//   stays inside the parent container width (no overflow on mobile).
function ReversalRiskChip({reversalRisk,className}){
  const[expanded,setExpanded]=React.useState(false);
  if(!reversalRisk||reversalRisk.flag==='NONE')return null;
  const isExpected=reversalRisk.flag==='EXPECTED';
  const _color=isExpected?'rgba(232,69,94,0.72)':'#23B981';
  const _label=isExpected?'REVERSAL EXPECTED':'REVERSAL WATCH';
  const _icon=isExpected?'⚠':'⚡';
  const _firedSignals=(reversalRisk.signals||[]).filter(s=>s.fired).sort((a,b)=>b.weight-a.weight);
  return React.createElement('div',{
    className:'flex flex-col gap-1.5 '+(className||''),
  },[
    React.createElement('button',{
      key:'chip',
      type:'button',
      onClick:()=>setExpanded(v=>!v),
      className:'flex items-center justify-between gap-2 px-2.5 py-1.5 rounded-lg text-[10px] font-bold uppercase tracking-wide transition-all w-full',
      style:{
        background:`${_color}1c`,
        border:`1px solid ${_color}55`,
        color:_color,
        ...(isExpected?{animation:'taraReversalPulse 2.5s ease-in-out infinite'}:{}),
      },
      'aria-expanded':expanded,
      'aria-label':`${_label} — tap for details`,
    },[
      React.createElement('span',{key:'l',className:'flex items-center gap-1.5 min-w-0'},[
        React.createElement('span',{key:'icon',style:{fontSize:'12px'}},_icon),
        React.createElement('span',{key:'lbl',className:'truncate'},_label),
      ]),
      React.createElement('span',{key:'r',className:'flex items-center gap-1.5 shrink-0'},[
        React.createElement('span',{key:'score',className:'text-[9px] opacity-70 font-mono'},`${reversalRisk.score}`),
        React.createElement('span',{key:'caret',className:'text-[10px] opacity-70',style:{transform:expanded?'rotate(180deg)':'none',transition:'transform 0.2s'}},'▾'),
      ]),
    ]),
    expanded?React.createElement('div',{
      key:'details',
      className:'flex flex-col gap-1 px-2.5 py-2 rounded-lg text-[10px]',
      style:{background:'#050508',border:`1px solid ${_color}33`,color:'rgba(237,237,237,0.85)'},
    },_firedSignals.length===0?[
      React.createElement('span',{key:'none',style:{opacity:0.6}},'No specific risk factors fired (defensive flag).'),
    ]:[
      ..._firedSignals.map((s,i)=>React.createElement('div',{
        key:s.key||i,
        className:'flex items-start gap-2 leading-snug',
      },[
        React.createElement('span',{key:'w',className:'shrink-0 font-mono text-[9px] mt-0.5',style:{color:_color,opacity:0.85}},`+${s.weight}`),
        React.createElement('span',{key:'r',className:'flex-1',style:{wordBreak:'break-word'}},s.reason),
      ])),
    ]):null,
  ].filter(Boolean));
}

// V9.9.0: ReversalForecastSVG — Tara-native predicted price path. Compact chart
//   shows current price → predicted closing price, with bend at predicted reversal
//   time when flag is WATCH or EXPECTED. Strike line drawn as horizontal reference.
//   Confidence band fills around path width proportional to ATR. ViewBox-based
//   so it scales cleanly across viewports — width: 100% with maxWidth 380px caps
//   it on desktop while mobile gets the full container width.
//
// Path generation:
//   • NONE: gentle curve from now → predicted close (slightly past strike, favorable)
//   • WATCH: mild bend — price runs favorable past strike, then small retracement
//   • EXPECTED: prominent bend — favorable peak ~midway, retraces back through strike
//
// The bend timing (peak at ~45% of remaining time) is heuristic — V9.9.2 will
// recalibrate from V9.8.14's timeSeries data once we have peak-adverse timestamps.
function ReversalForecastSVG({reversalRisk,dir,currentPrice,strikePrice,atrBps,timeRemainingSec,totalWindowSec}){
  if(!Number.isFinite(currentPrice)||!Number.isFinite(strikePrice)||currentPrice<=0||strikePrice<=0)return null;
  if(!dir||(dir!=='UP'&&dir!=='DOWN'))return null;
  const W=380,H=110;
  const PAD_X=14,PAD_Y=14;
  const innerW=W-2*PAD_X;
  const innerH=H-2*PAD_Y;
  // Establish y-range: center the chart on the price corridor between current and strike,
  //   with extra padding so the bend has room to render visually. Minimum 6 bps each way
  //   so flat regimes still show a meaningful chart.
  const _corridor=Math.abs(currentPrice-strikePrice);
  const _minPadPx=Math.max(currentPrice*0.0006,_corridor*0.5); // ≥6bps or 50% of corridor
  const yLow=Math.min(currentPrice,strikePrice)-_minPadPx;
  const yHigh=Math.max(currentPrice,strikePrice)+_minPadPx;
  const yRange=yHigh-yLow;
  const priceToY=(p)=>PAD_Y+((yHigh-p)/yRange)*innerH;
  const tToX=(t)=>PAD_X+t*innerW;
  const yNow=priceToY(currentPrice);
  const yStrike=priceToY(strikePrice);
  const xStart=tToX(0);
  const xEnd=tToX(1);
  const flag=reversalRisk?.flag||'NONE';
  // Predicted close price depends on flag:
  //   NONE     — favorable past strike (winning prediction)
  //   WATCH    — barely past strike (marginal win)
  //   EXPECTED — wrong side of strike (losing prediction — that's the "expected reversal")
  const _favSign=dir==='UP'?1:-1;
  const _strikeFavor=Math.max(currentPrice*0.0008,_corridor*0.10); // small offset past strike
  let _predictedClose;
  if(flag==='NONE')_predictedClose=strikePrice+_favSign*_strikeFavor*1.4;
  else if(flag==='WATCH')_predictedClose=strikePrice+_favSign*_strikeFavor*0.3;
  else _predictedClose=strikePrice-_favSign*_strikeFavor*0.4; // EXPECTED ends wrong side
  const yClose=priceToY(_predictedClose);
  // Peak (favorable extreme) — visible only on WATCH/EXPECTED flags
  const _peakOffset=Math.max(currentPrice*0.0012,_corridor*0.4);
  const _yPeak=priceToY(strikePrice+_favSign*_peakOffset*(flag==='EXPECTED'?1.0:0.6));
  const xPeak=tToX(0.45);
  // Build path
  let pathD;
  if(flag==='NONE'){
    // Smooth ease toward predicted close
    const _xMid=tToX(0.55);
    const _yMid=priceToY((currentPrice+_predictedClose)/2+_favSign*_corridor*0.05);
    pathD=`M ${xStart} ${yNow} Q ${_xMid} ${_yMid} ${xEnd} ${yClose}`;
  }else if(flag==='WATCH'){
    // Cubic with mild bend — favorable peak, mild retracement
    const _yEarly=priceToY(strikePrice+_favSign*_strikeFavor*0.7);
    pathD=`M ${xStart} ${yNow} C ${tToX(0.30)} ${_yEarly} ${xPeak} ${_yPeak} ${tToX(0.65)} ${priceToY(strikePrice+_favSign*_strikeFavor*0.35)} S ${xEnd} ${yClose} ${xEnd} ${yClose}`;
  }else{
    // EXPECTED — prominent bend, ends past strike on the WRONG side
    pathD=`M ${xStart} ${yNow} C ${tToX(0.25)} ${priceToY(strikePrice+_favSign*_peakOffset*0.5)} ${xPeak} ${_yPeak} ${tToX(0.65)} ${yStrike} S ${xEnd} ${yClose} ${xEnd} ${yClose}`;
  }
  const _flagColor=flag==='EXPECTED'?'rgba(232,69,94,0.72)':flag==='WATCH'?'#23B981':'#6FA98C';
  // Confidence band — wider transparent stroke under main path
  const _bandWidth=Math.max(8,Math.min(22,(atrBps||10)*0.5));
  // Time remaining display
  const _minsLeft=timeRemainingSec!=null?Math.max(0,Math.floor(timeRemainingSec/60)):null;
  const _secsLeft=timeRemainingSec!=null?Math.max(0,timeRemainingSec%60):null;
  return React.createElement('svg',{
    viewBox:`0 0 ${W} ${H}`,
    preserveAspectRatio:'xMidYMid meet',
    role:'img',
    'aria-label':`Predicted price path · reversal risk ${flag.toLowerCase()}`,
    style:{width:'100%',maxWidth:`${W}px`,height:'auto',display:'block'},
  },[
    // Background panel
    React.createElement('rect',{key:'bg',x:0,y:0,width:W,height:H,rx:6,fill:'rgba(10,10,10,0.45)'}),
    // Strike line (dashed gold horizontal)
    React.createElement('line',{key:'strike',x1:PAD_X,y1:yStrike,x2:W-PAD_X,y2:yStrike,stroke:'#23B981',strokeWidth:1,strokeDasharray:'4 3',opacity:0.55}),
    // Strike label (right side)
    React.createElement('text',{key:'sl',x:W-PAD_X-2,y:yStrike-4,textAnchor:'end',fontSize:9,fill:'#23B981',opacity:0.9},`Strike $${Math.round(strikePrice).toLocaleString()}`),
    // Confidence band (faint wide stroke under main path)
    React.createElement('path',{key:'band',d:pathD,stroke:_flagColor,strokeWidth:_bandWidth,strokeLinecap:'round',strokeLinejoin:'round',fill:'none',opacity:0.10}),
    // Predicted path (main stroke)
    React.createElement('path',{key:'path',d:pathD,stroke:_flagColor,strokeWidth:2,strokeLinecap:'round',strokeLinejoin:'round',fill:'none'}),
    // Reversal bend marker (only on WATCH/EXPECTED)
    flag!=='NONE'?React.createElement('circle',{key:'peak',cx:xPeak,cy:_yPeak,r:3.5,fill:_flagColor,opacity:0.9}):null,
    flag!=='NONE'?React.createElement('text',{key:'peakLabel',x:xPeak,y:_yPeak-7,textAnchor:'middle',fontSize:8,fill:_flagColor,opacity:0.85},'reversal'):null,
    // Now marker (left)
    React.createElement('circle',{key:'now',cx:xStart,cy:yNow,r:3.5,fill:'#EDEDED'}),
    React.createElement('text',{key:'nowLabel',x:xStart+5,y:yNow+(yNow>H/2?-5:11),fontSize:9,fill:'#EDEDED',opacity:0.8},`now $${Math.round(currentPrice).toLocaleString()}`),
    // Predicted close marker (right)
    React.createElement('circle',{key:'end',cx:xEnd,cy:yClose,r:3,fill:_flagColor,opacity:0.85}),
    // Time-remaining label (bottom right)
    _minsLeft!=null?React.createElement('text',{key:'tr',x:W-PAD_X-2,y:H-3,textAnchor:'end',fontSize:8,fill:'rgba(237,237,237,0.45)'},`${_minsLeft}m ${String(_secsLeft).padStart(2,'0')}s left`):null,
  ].filter(Boolean));
}

// ═══════════════════════════════════════════════════════════════════════════
// V9.16: SIMPLIFIED OVERVIEW LAYOUT
// ═══════════════════════════════════════════════════════════════════════════
// New default layout for trader-mode users. Three primary zones (call, coach,
//   chart) plus a session-context bar. Tabs at the bottom expose everything
//   else (tape, schedule, stats, brain, settings).
//
// Design philosophy:
//   - One thing dominant per zone — the call is the loudest visual element
//   - Gold accent reserved for the call indicator only
//   - Status colors (green/pink/amber) used surgically for P&L and warnings
//   - Tabular numbers everywhere (font-feature-settings: 'tnum')
//   - Generous outer padding, tight inner packing
//   - Engine + data layer untouched — this is presentation only
//
// Engineer Mode (simpleMode=false) preserves the V9.15 layout as a fallback.

// V9.17.7: Simple* components removed (was dead code from V9.16.0-9.16.3
//   dead branch). SimpleTopBar, SimpleCallCard, SimpleTradeCoach,
//   SimplePriceStrip, SimpleSessionBar, SimpleTabBar — ~360 lines.
//   Engineer Mode (V9.15 layout) is the only path now.


// ═══════════════════════════════════════════════════════════════════════════
// V9.17.1: SCALPER ADVISOR PANEL — primary engine view
// ═══════════════════════════════════════════════════════════════════════════
// Replaces the old "general prediction" box. Always-on engine read at top,
// state-driven action area below. Five states:
//
//   DISABLED → engine reads visible · CTA to enable in settings
//   IDLE     → engine reads visible · "watching for setup" message
//   SUGGEST  → entry suggestion with [i bought] / [dismiss] actions
//   HOLD     → position tracker · recommendation · [i sold]
//   BLOCKED  → engine reads visible · block reason (cooldown / cap / hours)
//
// All hooks at top, NEVER after a conditional return (fixes V9.17.0 hook-count
// mismatch crash). Disabled state is just a different render path.
function ScalperAdvisorPanel({
  scalperRead,scalperSettings,scalperPosition,setScalperPosition,
  scalperLog,setScalperLog,scalperSuggestionsRef,scalperDismissedRef,
  kalshiYesPrice,kalshiActiveMarket,currentAsset,tiltLockUntil,
  autoExecSettings,setShowTradingSettings,
  // V9.18.2: inline trade-ticket edit mode. The panel needs setters so the
  //   user can tweak stake / cash out / cut loss / entry price right in the
  //   ticket. Overrides are window-scoped — when windowId changes, originals
  //   are restored automatically (handled inside the panel).
  setTradingSettings,setAutoExecSettings,
  // V9.18.2: per-window entry-price override. Ref so the entry effect can
  //   read the latest value without re-render dependency. Null = use Tara's
  //   calculated entry. Reset on window roll.
  ticketEntryOverrideRef,
  // V9.17.6: Tara's lock context for showing trade ticket when locked
  taraCall,analysis,tradingSettings,autoOrderState,userPosition,
  // V10.2.9: user-logged actual Kalshi fill for manual positions
  manualKalshiEntry,
  windowType,timeState,useLocalTime,timeFormat,
  // V9.17.18: manual place-order callback. Fires auto-exec on Tara's snapshot
  //   direction even when the engine-lock path didn't (e.g., time-cap-commit).
  onPlaceOrderOnTaraCall,
  // V9.17.21: visible click feedback so user sees what happened without DevTools.
  //   Shape: {at, msg, color} | null. Auto-clears after 5s in render.
  manualOrderFeedback,
  // V9.17.24: kill-switch state surfaced into the trade ticket so the user sees
  //   it RIGHT NEXT TO the place-order button rather than buried in the live
  //   trade pill. V9.17.23 auto-engages on Kalshi auth errors — without this
  //   prop the user wouldn't know the click silently bounced off the guardrail.
  killSwitchEngaged,onClearKillSwitch,
  // V9.17.26: clear autoOrderState error so the trade ticket error banner can
  //   reset the state (lets user retry on the next click).
  onClearAutoOrder,
  // V9.19.2 Phase 3: taraCallLog for predictor P&L computation. Filters to
  //   today's autoExec trades and sums realizedPnLDollars + computes WR.
  taraCallLog,
  // V9.19.18: kalshiBalance for ticket-preview sizing (percent mode needs it,
  //   other modes ignore it). Pass-through to computeAutoExecSize so the
  //   ticket renders the SAME numbers the entry effect will fire on.
  kalshiBalance,
  // V9.17.18: Tara's committed snapshot (from taraCallSnapshotRef). When engine
  //   lock is null but the snapshot has committed via time-cap-commit / timer-
  //   commit / no-go paths, we still want to show the trade ticket and the
  //   manual place-order button. Without this prop the panel previously read
  //   only from analysis.lockInfo (built from lockedCallRef) — missing the
  //   snapshot-only commit case entirely.
  taraSnapshotForTicket,
}){
  // ── HOOKS (always called, unconditionally) ─────────────────────────────
  const[showEntryPrompt,setShowEntryPrompt]=React.useState(null);
  const[entryPriceInput,setEntryPriceInput]=React.useState('');
  const[entryContractsInput,setEntryContractsInput]=React.useState('1');
  const[showExitPrompt,setShowExitPrompt]=React.useState(false);
  const[exitPriceInput,setExitPriceInput]=React.useState('');
  // V9.18.2: TRADE TICKET EDIT MODE
  //   `editMode` toggles inline-edit UI for stake, entry, cash-out, cut-loss.
  //   Edits apply IMMEDIATELY to tradingSettings + autoExecSettings + the
  //   entry-cents override ref so the next place-order or auto-exec uses
  //   them. On window roll we snapshot the pre-edit originals and restore.
  const[editMode,setEditMode]=React.useState(false);
  const[editStake,setEditStake]=React.useState('');
  const[editEntry,setEditEntry]=React.useState('');
  const[editCashOut,setEditCashOut]=React.useState('');
  const[editCutDelta,setEditCutDelta]=React.useState('');
  // V9.19.15: simplified trade ticket. tappedTip = which row's explanation is
  //   currently expanded inline (mobile). Desktop users get hover via title.
  //   showMonitoring = whether the engineer-mode exit-tick details are visible.
  const[tappedTip,setTappedTip]=React.useState(null);
  const[showMonitoring,setShowMonitoring]=React.useState(false);
  // V13.4.277: the trade ticket READOUT collapses. Persisted, because a disclosure
  //   that resets on every window roll is worse than no disclosure at all.
  const[ticketOpen,setTicketOpen]=React.useState(()=>{
    try{return localStorage.getItem('taraTicketOpen')==='1';}catch(_e){return false;}
  });
  const _toggleTicket=React.useCallback(()=>{
    setTicketOpen(v=>{
      const n=!v;
      try{localStorage.setItem('taraTicketOpen',n?'1':'0');}catch(_e){}
      return n;
    });
  },[]);
  const _editOriginalsRef=React.useRef(null); // {betSize, autoExitOffer, stopLossDeltaCents, entryOverride}
  const _editLastWindowIdRef=React.useRef(null);
  // V9.19.9: Persist edit originals to localStorage so a refresh mid-edit doesn't
  //   permanently lose them. Stored shape: {originals, windowKey, savedAt}.
  //   On mount, if the saved windowKey matches current → restore the ref so the
  //   window-roll restore effect can still fire. If it doesn't match → the user
  //   refreshed AFTER the window rolled and edits should already be reverted;
  //   we apply the originals immediately and clear storage.
  const _EDIT_ORIG_KEY='taraEditOriginals_v1';
  const _editHydratedRef=React.useRef(false);
  const[,setTick]=React.useState(0);
  React.useEffect(()=>{
    const iv=setInterval(()=>setTick(t=>t+1),1000);
    return()=>clearInterval(iv);
  },[]);

  // ── DERIVED STATE ──────────────────────────────────────────────────────
  const _enabled=!!scalperSettings?.enabled;
  const _conviction=scalperRead?.conviction||0;
  const _dir=scalperRead?.dir;
  const _reason=scalperRead?.reason||'';
  const _sigKey=_dir?`${_dir}_${Math.round(_conviction/5)*5}`:null;
  const _isDismissed=scalperDismissedRef.current?.signature===_sigKey&&Date.now()<(scalperDismissedRef.current?.until||0);
  const _elig=_enabled?checkScalperEligibility({
    settings:scalperSettings,scalperLog,
    suggestionsLogRef:scalperSuggestionsRef,
    tiltLockUntil,kalshiYesPrice,
  }):{eligible:false,blockReason:'disabled'};

  // State machine
  let _state='IDLE';
  if(!_enabled){
    _state='DISABLED';
  } else if(scalperPosition){
    _state='HOLD';
  } else if(!_elig.eligible){
    _state='BLOCKED';
  } else if(_dir&&_conviction>=(scalperSettings.minConviction||65)&&!_isDismissed){
    const _k=Number(kalshiYesPrice)||50;
    let _inZone=false;
    if(_dir==='UP'&&_k>=scalperSettings.entryMinCents&&_k<=scalperSettings.entryMaxCents){
      _inZone=true;
    } else if(_dir==='DOWN'){
      const _noPrice=100-_k;
      if(_noPrice>=scalperSettings.entryMinCents&&_noPrice<=scalperSettings.entryMaxCents)_inZone=true;
    }
    if(_inZone)_state='SUGGEST';
  }

  // ── SUGGESTION TRACKING (always-mounted effect; gated by state inside) ─
  React.useEffect(()=>{
    if(_state==='SUGGEST'&&_sigKey){
      const _last=scalperSuggestionsRef.current[scalperSuggestionsRef.current.length-1];
      if(!_last||Date.now()-_last>30000){
        scalperSuggestionsRef.current.push(Date.now());
        const _cutoff=Date.now()-60*60*1000;
        scalperSuggestionsRef.current=scalperSuggestionsRef.current.filter(t=>t>=_cutoff);
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  },[_state,_sigKey]);

  const _stats=computeScalperTodayStats(scalperLog);

  // V9.18.2: WINDOW ROLL → restore originals.
  //   When the window changes (timeState.minsRemaining + secsRemaining
  //   wraps to a new window), we put any in-flight edits back to whatever
  //   the user had configured globally. This prevents per-window overrides
  //   from silently sticking across windows.
  // V9.19.6: was `${windowType}_${Math.floor(minsRemaining/15)}_${asset}`.
  //   The middle term is always 0 within any single 15m window (minsRemaining
  //   range 0-14) AND always 0 within any 5m window (range 0-4). So this key
  //   never changed between two consecutive windows of the same type+asset,
  //   meaning the window-roll restore effect never fired. Edits made to
  //   stake/cashOut/cutLoss persisted into the next window — violating the
  //   "scoped to this window" UI promise. Fixed by using computeWindowId()
  //   which is the same canonical window identifier used everywhere else
  //   (auto-exec, lock state, hard-cap).
  const _editWindowKey=`${windowType||'?'}_${computeWindowId(windowType)}_${currentAsset||'?'}`;
  // V9.19.9: hydrate edit originals from localStorage on mount. Two cases:
  //   1) Saved windowKey === current windowKey → user refreshed mid-edit, same window.
  //      Restore the ref so the window-roll effect can revert later. Editing state
  //      (the input fields) is NOT restored — user will need to re-open edit mode.
  //   2) Saved windowKey !== current windowKey → user refreshed AFTER window rolled.
  //      The window-roll restore never got to fire. Apply originals now and clear.
  //   Also gate on a 24h staleness window to avoid restoring ancient junk.
  React.useEffect(()=>{
    if(_editHydratedRef.current)return;
    _editHydratedRef.current=true;
    try{
      const raw=localStorage.getItem(_EDIT_ORIG_KEY);
      if(!raw)return;
      const obj=JSON.parse(raw);
      if(!obj||!obj.originals||!obj.windowKey)return;
      // Stale check: anything >24h old gets dropped
      if(Date.now()-(Number(obj.savedAt)||0)>24*60*60*1000){
        localStorage.removeItem(_EDIT_ORIG_KEY);return;
      }
      if(obj.windowKey===_editWindowKey){
        // Same window → just restore the ref. Window-roll effect handles the rest.
        _editOriginalsRef.current=obj.originals;
        try{console.info('[V9.19.9] edit originals hydrated (same window)',obj.originals);}catch(_){}
      } else {
        // Different window → window already rolled. Apply originals now.
        try{
          if(typeof setTradingSettings==='function'&&obj.originals.betSize!=null){
            setTradingSettings(prev=>({...prev,betSize:obj.originals.betSize}));
          }
          if(typeof setAutoExecSettings==='function'){
            setAutoExecSettings(prev=>({
              ...prev,
              autoExitOffer:obj.originals.autoExitOffer!=null?obj.originals.autoExitOffer:prev.autoExitOffer,
              stopLossDeltaCents:obj.originals.stopLossDeltaCents!=null?obj.originals.stopLossDeltaCents:prev.stopLossDeltaCents,
            }));
          }
          if(ticketEntryOverrideRef){ticketEntryOverrideRef.current=null;}
          try{console.info('[V9.19.9] edit originals applied (window rolled before refresh)');}catch(_){}
        }catch(_){}
        localStorage.removeItem(_EDIT_ORIG_KEY);
      }
    }catch(_){
      try{localStorage.removeItem(_EDIT_ORIG_KEY);}catch(_){}
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  },[]);
  React.useEffect(()=>{
    if(_editLastWindowIdRef.current==null){
      _editLastWindowIdRef.current=_editWindowKey;return;
    }
    if(_editLastWindowIdRef.current!==_editWindowKey){
      // Window rolled — restore originals if we captured them
      const _orig=_editOriginalsRef.current;
      if(_orig){
        try{
          if(typeof setTradingSettings==='function'&&_orig.betSize!=null){
            setTradingSettings(prev=>({...prev,betSize:_orig.betSize}));
          }
          if(typeof setAutoExecSettings==='function'){
            setAutoExecSettings(prev=>({
              ...prev,
              autoExitOffer:_orig.autoExitOffer!=null?_orig.autoExitOffer:prev.autoExitOffer,
              stopLossDeltaCents:_orig.stopLossDeltaCents!=null?_orig.stopLossDeltaCents:prev.stopLossDeltaCents,
            }));
          }
          if(ticketEntryOverrideRef){ticketEntryOverrideRef.current=null;}
        }catch(_){}
        _editOriginalsRef.current=null;
        // V9.19.9: clear persisted originals after successful restore
        try{localStorage.removeItem(_EDIT_ORIG_KEY);}catch(_){}
      }
      setEditMode(false);
      _editLastWindowIdRef.current=_editWindowKey;
    }
  },[_editWindowKey,setTradingSettings,setAutoExecSettings,ticketEntryOverrideRef]);

  // ── HANDLERS ──────────────────────────────────────────────────────────
  // V9.18.2: TRADE TICKET EDIT HANDLERS
  const _onEnterEditMode=()=>{
    // Snapshot originals so we can restore on window roll
    const _origs={
      betSize:Number(tradingSettings?.betSize)||10,
      // V13.4.292: `||85` fabricated an 85c take-profit out of a genuinely
      //   disabled (0) setting -- opening this editor to change stake alone
      //   and saving would arm a fixed TP under TRAIL_ARM_C nobody asked for,
      //   and both restore paths below (window-roll and refresh-hydrate) then
      //   wrote that fabricated 85 back as if it were the real prior value.
      //   `||0` lets a genuine 0 pass through; the only case it changes is
      //   `undefined` (settings not loaded yet), which now falls back to the
      //   safe off state instead of a fixed rule nobody chose.
      autoExitOffer:Number(autoExecSettings?.autoExitOffer)||0,
      stopLossDeltaCents:Number(autoExecSettings?.stopLossDeltaCents)||0,
      entryOverride:ticketEntryOverrideRef?.current||null,
    };
    _editOriginalsRef.current=_origs;
    // V9.19.9: persist so a refresh mid-edit doesn't lose the originals
    try{
      localStorage.setItem(_EDIT_ORIG_KEY,JSON.stringify({
        originals:_origs,
        windowKey:_editWindowKey,
        savedAt:Date.now(),
      }));
    }catch(_){}
    // Seed inputs with current effective values
    setEditStake(String(Number(tradingSettings?.betSize)||10));
    setEditCashOut(String(Number(autoExecSettings?.autoExitOffer)||0));
    setEditCutDelta(String(Number(autoExecSettings?.stopLossDeltaCents)||0));
    // Entry seed comes from current display (Tara's calculated)
    const _curEntry=ticketEntryOverrideRef?.current
      ?String(ticketEntryOverrideRef.current)
      :''; // empty means "use Tara's default"
    setEditEntry(_curEntry);
    setEditMode(true);
  };
  const _onCancelEdit=()=>{
    setEditMode(false);
    setEditStake('');setEditEntry('');setEditCashOut('');setEditCutDelta('');
    _editOriginalsRef.current=null;
    try{localStorage.removeItem(_EDIT_ORIG_KEY);}catch(_){}
  };
  const _onSaveEdit=()=>{
    // Parse + validate. Empty string means "leave as-is".
    const _parseNum=(s,fallback,min,max)=>{
      if(s===''||s==null)return fallback;
      const n=Number(s);
      if(!Number.isFinite(n))return fallback;
      return Math.max(min,Math.min(max,n));
    };
    const _stakeNum=_parseNum(editStake,Number(tradingSettings?.betSize)||10,0.5,500);
    const _coNum=_parseNum(editCashOut,Number(autoExecSettings?.autoExitOffer)||0,0,99);
    const _cutNum=_parseNum(editCutDelta,Number(autoExecSettings?.stopLossDeltaCents)||0,0,90);
    const _entryNum=editEntry===''?null:_parseNum(editEntry,null,1,99);
    try{
      if(typeof setTradingSettings==='function'){
        setTradingSettings(prev=>({...prev,betSize:_stakeNum}));
      }
      if(typeof setAutoExecSettings==='function'){
        setAutoExecSettings(prev=>({...prev,autoExitOffer:_coNum,stopLossDeltaCents:_cutNum}));
      }
      if(ticketEntryOverrideRef){ticketEntryOverrideRef.current=_entryNum;}
    }catch(_){}
    setEditMode(false);
  };
  const _onIntendBuy=(side)=>{
    setShowEntryPrompt(side);
    const _suggested=side==='LONG_YES'?Number(kalshiYesPrice)||50:100-(Number(kalshiYesPrice)||50);
    setEntryPriceInput(_suggested.toFixed(0));
    setEntryContractsInput(String(scalperSettings.contractsPerScalp||1));
  };
  const _onConfirmEntry=()=>{
    const _price=parseFloat(entryPriceInput);
    const _contracts=parseInt(entryContractsInput,10)||1;
    if(!Number.isFinite(_price)||_price<=0||_price>=100){
      alert('Entry price must be between 1 and 99 cents.');return;
    }
    const _side=showEntryPrompt;
    setScalperPosition({
      side:_side,entryCents:_price,entryTime:Date.now(),
      contracts:_contracts,asset:currentAsset,
      suggestedTargetCents:_price+scalperSettings.targetCents,
      suggestedStopCents:_price-scalperSettings.stopCents,
      entryRead:scalperRead?{dir:scalperRead.dir,conviction:scalperRead.conviction,reason:scalperRead.reason,signals:scalperRead.signals}:null,
      ticker:kalshiActiveMarket?.ticker||null,
    });
    setShowEntryPrompt(null);setEntryPriceInput('');
  };
  const _onDismiss=()=>{
    scalperDismissedRef.current={signature:_sigKey,until:Date.now()+120000};
    setTick(t=>t+1);
  };
  const _onIntendSell=()=>{
    setShowExitPrompt(true);
    const _curMarket=Number(kalshiYesPrice)||50;
    const _suggested=scalperPosition.side==='LONG_YES'?_curMarket:(100-_curMarket);
    setExitPriceInput(_suggested.toFixed(0));
  };
  const _onConfirmExit=()=>{
    const _exitPrice=parseFloat(exitPriceInput);
    if(!Number.isFinite(_exitPrice)||_exitPrice<0||_exitPrice>100){
      alert('Exit price must be between 0 and 100 cents.');return;
    }
    const _entry=scalperPosition.entryCents;
    const _profitCentsPerContract=_exitPrice-_entry;
    const _profitCents=_profitCentsPerContract*scalperPosition.contracts;
    let _outcome='NEUTRAL';
    if(_profitCents>0)_outcome='WIN';
    else if(_profitCents<0)_outcome='LOSS';
    setScalperLog(prev=>[...prev,{
      id:Date.now(),
      side:scalperPosition.side,
      entryCents:_entry,exitCents:_exitPrice,
      profitCents:_profitCents,profitCentsPerContract:_profitCentsPerContract,
      contracts:scalperPosition.contracts,
      entryTime:scalperPosition.entryTime,exitTime:Date.now(),
      holdSec:Math.round((Date.now()-scalperPosition.entryTime)/1000),
      asset:scalperPosition.asset,ticker:scalperPosition.ticker,
      outcome:_outcome,
      entryRead:scalperPosition.entryRead,
      exitRead:scalperRead?{dir:scalperRead.dir,conviction:scalperRead.conviction,reason:scalperRead.reason}:null,
    }]);
    setScalperPosition(null);
    setShowExitPrompt(false);setExitPriceInput('');
  };

  // ── SHARED "tara's advisor" HEADER ─────────────────────────────────────
  const _readDirColor=_dir==='UP'?'rgb(35,185,129)':_dir==='DOWN'?'#E8455E':'#EDEDED';
  const _readArrow=_dir==='UP'?'▲':_dir==='DOWN'?'▼':'·';
  const _headerEl=React.createElement('div',{className:'flex items-baseline justify-between mb-3 pb-3 border-b border-[#24242E]'},
    React.createElement('div',{className:'flex items-baseline gap-2'},
      React.createElement('span',{className:'text-[11px] font-bold',style:{color:'#23B981',letterSpacing:'0.02em',textTransform:'none'}},'tara\'s advisor'),
      React.createElement('span',{className:'text-[10px] text-[#EDEDED]/40',style:{letterSpacing:'0.02em',textTransform:'none'}},'short-horizon scalp'),
    ),
    _stats.n>0&&React.createElement('span',{className:'text-[10px] tabular-nums text-[#EDEDED]/40',style:{fontFamily:'IBM Plex Mono,ui-monospace,monospace'}},
      `today ${_stats.wins}w·${_stats.losses}l · ${_stats.netCents>=0?'+':''}${_stats.netCents}¢`),
  );
  // Sub-header: current engine read line (always-on)
  const _readLineEl=React.createElement('div',{className:'flex items-baseline gap-2 mb-3 pb-2 border-b border-[#24242E]'},
    React.createElement('span',{className:'text-[9px] uppercase text-[#EDEDED]/35',style:{letterSpacing:'0.06em'}},'reads'),
    React.createElement('span',{className:'text-base font-medium tabular-nums',style:{color:_readDirColor,fontFamily:'IBM Plex Mono,ui-monospace,monospace'}},
      `${_readArrow} ${_dir?_dir.toLowerCase():'flat'}`),
    _conviction>0&&React.createElement('span',{className:'text-[11px] tabular-nums text-[#EDEDED]/55',style:{fontFamily:'IBM Plex Mono,ui-monospace,monospace'}},
      `${_conviction}%`),
  );
  const _topEl=React.createElement(React.Fragment,null,_headerEl,_readLineEl);

  // ═══════════════════════════════════════════════════════════════════════
  // V9.17.6: TARA TRADE TICKET COMPUTATION
  // ═══════════════════════════════════════════════════════════════════════
  // When Tara has an active lock for this window, show her trade ticket
  // (entry, contracts, max payout, cash out, cut loss) IN PLACE OF the
  // scalper engine reads. User wanted this info displayed in the advisor
  // panel instead of a separate stacked card.
  //
  // The scalper-specific states (SUGGEST/HOLD/etc.) still take priority when
  // active — they represent the scalper actively doing something the user
  // needs to act on. Otherwise: Tara-lock ticket > engine read content.
  // V9.17.7: was `taraCall?.locked?.dir||analysis?.lockInfo?.dir`; taraCall has
  //   no .locked key (shape is {call,reason,confidence}). analysis.lockInfo.dir
  //   is the canonical path used everywhere else. Cleaned up.
  // V9.17.18: also check taraSnapshotForTicket as fallback — covers the case where
  //   Tara committed via time-cap-commit / timer-commit / no-go but the engine
  //   lock (which feeds analysis.lockInfo) is still null.
  const _engineLockDir=analysis?.lockInfo?.dir||null;
  const _snapDir=(taraSnapshotForTicket?.call==='UP'||taraSnapshotForTicket?.call==='DOWN')
    ?taraSnapshotForTicket.call
    :(taraSnapshotForTicket?.direction==='UP'||taraSnapshotForTicket?.direction==='DOWN')
      ?taraSnapshotForTicket.direction
      :null;
  // V9.18.7: respect signalSource setting (snapshot-mode prefers snapshot,
  //   lock-mode prefers lock). Previously _taraDir defaulted to engineLock||snap,
  //   causing "ticket says DOWN, panel says UP" contradictions when snapshot
  //   and engine-lock diverged.
  // V9.19.11: migrated to getTaraDirection helper. Behavior is identical to
  //   V9.18.7 — this just centralizes the precedence logic so future call sites
  //   can read direction via a single function.
  const _signalSourceSetting=autoExecSettings?.signalSource||'snapshot';
  const _taraDirRes=getTaraDirection({
    snapshot:{call:_snapDir},
    lock:{dir:_engineLockDir},
    signalSource:_signalSourceSetting,
  });
  const _taraDir=_taraDirRes.dir;
  const _taraLocked=!!_taraDir;
  // V13.4.96: OPPOSED-LOCK WARNING. The ticket direction comes from the snapshot/engine
  //   lock, while Tara's Call panel shows the LIVE lean. When a window locks one way and
  //   the read later flips, the two disagree and nothing said so -- observed live as a
  //   ticket reading 'long up @ 6c' while the call panel read LEANING DOWN and spot sat
  //   8bps BELOW the strike (i.e. the lock was already losing). The comment above this
  //   block warns about exactly this divergence class; it centralised the read but never
  //   surfaced the conflict. Advisory only -- it changes no direction, places no order,
  //   and never overrides the lock. It just refuses to let a stale lock look healthy.
  //   Threshold of 5pt of live conviction keeps it off noise around a coin-flip posterior.
  const _lkPost=Number(analysis?.rawProbAbove);
  const _lkLiveDir=Number.isFinite(_lkPost)?(_lkPost>50?'UP':(_lkPost<50?'DOWN':null)):null;
  const _lkConv=Number.isFinite(_lkPost)?Math.abs(_lkPost-50):0;
  const _lockOpposed=!!(_taraDir&&_lkLiveDir&&_taraDir!==_lkLiveDir&&_lkConv>=5);
  // V9.17.18: snapshot tier is what flags "auto-exec may sit out" case (time-cap-commit etc).
  //   Use snapshot tier when engine lock isn't set (snapshot-only commit).
  const _snapTier=taraSnapshotForTicket?.tier||null;
  // Window label (e.g., "13:15-13:30 EST · 12m 29s left")
  let _winLabel='';
  if(windowType){
    try{
      const _windowSize=windowType==='5m'?5:15;
      const _now=new Date();
      const _curMin=_now.getMinutes();
      const _winStartMin=Math.floor(_curMin/_windowSize)*_windowSize;
      const _winEndMin=_winStartMin+_windowSize;
      const _winStart=new Date(_now);_winStart.setMinutes(_winStartMin,0,0);
      const _winEnd=new Date(_now);_winEnd.setMinutes(_winEndMin,0,0);
      const _secLeft=Math.max(0,Math.floor((_winEnd.getTime()-_now.getTime())/1000));
      const _mLeft=Math.floor(_secLeft/60);
      const _sLeft=_secLeft%60;
      const _tz=useLocalTime===false?'EST':'local';
      const _fmt=(d)=>d.toLocaleTimeString(useLocalTime===false?'en-US':undefined,{hour:'2-digit',minute:'2-digit',hour12:timeFormat==='12h',timeZone:useLocalTime===false?'America/New_York':undefined});
      _winLabel=`${_fmt(_winStart)}–${_fmt(_winEnd)} ${_tz} · ${_mLeft}m ${_sLeft}s left`;
    }catch(_){_winLabel='';}
  }
  // Trade ticket numbers when Tara is locked
  const _yes=Number(kalshiYesPrice);
  const _isYesLive=Number.isFinite(_yes);
  const _entryCents=_isYesLive&&_taraDir?(_taraDir==='UP'?Math.round(_yes):Math.round(100-_yes)):null;
  // V13.4.313: this used to run computeAutoExecSize -- its own sizing simulation,
  //   defaulting to sizingMode 'fixed', which bases the number on
  //   tradingSettings.betSize and only used maxBetPerTrade as a ceiling. That is
  //   a DIFFERENT function from the one that actually sizes real orders,
  //   _resolveStakeDollars, which in the non-mission/non-contracts case just
  //   returns maxBetPerTrade directly. So moving a dollar-preset button changed
  //   the real order size but usually left this on-screen number unchanged,
  //   since betSize was still comfortably under the old cap.
  //   Mirrors _resolveStakeDollars's own non-mission branches exactly instead,
  //   so this figure is always what would actually fire. Mission-mode sizing
  //   needs `mission`/`regimeDirCalibration`/the live engine lock, none of
  //   which this panel receives -- while a mission is active this still shows
  //   the flat cap, the same honest fallback _resolveStakeDollars itself falls
  //   back to when mission math comes back invalid, and dollar presets are
  //   hidden in Mission Mode anyway so this case doesn't reach the user's ask.
  const _stakeCap=Math.max(0,Number(autoExecSettings?.maxBetPerTrade)||0);
  const _betSize=(autoExecSettings?.entryMode==='contracts'&&_entryCents>0)
    ?Math.min(_stakeCap,(Math.max(1,Math.floor(Number(autoExecSettings?.entryContracts)||0))*_entryCents)/100)
    :_stakeCap;
  const _contracts=_entryCents?Math.max(1,Math.floor(_betSize/(_entryCents*0.01))):1;
  // A real order only fails to fire on size when no max bet is configured at
  //   all (_resolveStakeDollars returns 0 and _runEntry's stake>0 check stops
  //   it there) -- the old 'sit-out-cap'/'sit-out-tiny'/'sit-out-bad' reasons
  //   came from computeAutoExecSize's own sizing-mode framework, which never
  //   governed a real order in the first place.
  const _previewWillFire=_stakeCap>0;
  const _previewSitOutReason=_previewWillFire?null:'No max bet configured — set one in Trading Settings before this can fire.';
  const _maxPayout=_contracts*1.0; // dollars (each contract pays $1 at settle if right)
  const _maxProfit=_maxPayout-_betSize;
  // V13.4.292: was `||85`, fabricating a fixed cash-out price for this
  //   projection when the real setting is off (0).
  const _cashOutCents=Number(autoExecSettings?.autoExitOffer)||0;
  const _cashOutProfit=_entryCents?((_cashOutCents-_entryCents)*0.01*_contracts):0;
  const _stopDelta=Number(autoExecSettings?.stopLossDeltaCents)||0;
  const _cutCents=_entryCents&&_stopDelta>0?(_entryCents-_stopDelta):null;
  const _cutLoss=_cutCents!=null?((_cutCents-_entryCents)*0.01*_contracts):null;
  const _taraDirColor=_taraDir==='UP'?'rgb(35,185,129)':_taraDir==='DOWN'?'#E8455E':'#EDEDED';
  const _taraDirLabel=_taraDir==='UP'?'long up':_taraDir==='DOWN'?'long down':'flat';

  // V9.17.17: LIVE TRADE VALUES
  // When autoOrderState is filled/exiting/exited, replace generic suggestion values
  // with the user's ACTUAL trade values: actual entry price, actual contracts,
  // actual stake, live current offer, unrealized P&L, max payout from where they are.
  // When exited, show realized P&L summary alongside entry → exit.
  // For all other states (no order yet, patient-waiting, patient-skipped, etc.),
  // the generic ticket renders as before.
  const _liveStatus=autoOrderState?.status;
  const _isInTrade=_liveStatus==='filled'||_liveStatus==='exiting';
  // V13.4.286: these four read fillPrice / count / limitCents / exitFillPrice --
  //   none of which the writer sets (it writes filledAtCents / filledCount /
  //   rungCents / exitAtCents). Routed through readOrderState like THIS TRADE.
  const _ordS=readOrderState(autoOrderState);
  const _isExited=_liveStatus==='exited'&&_ordS.exitCents!=null&&_ordS.fillCents!=null;
  const _liveDir=autoOrderState?.dir;
  // Only pull live values when the order's direction matches Tara's locked direction
  //  (defensive — if directions diverge we don't want to mix generic + live values).
  const _liveValid=(_isInTrade||_isExited)&&_liveDir&&_taraDir&&_liveDir===_taraDir;
  // V10.2.9 — MANUAL FILL FALLBACK. When auto-exec didn't place this trade
  //   (or it's a fully manual position), the user may have logged their real
  //   Kalshi fill via the ManualKalshiEntryInput. Treat that as just as
  //   authoritative as autoOrderState for ticket value reads. _liveValid wins
  //   if both are present (auto-exec is more trustworthy — it has the actual
  //   Kalshi response).
  const _manualValid=!_liveValid
    &&!!manualKalshiEntry
    &&!!userPosition
    &&manualKalshiEntry.side===userPosition
    &&manualKalshiEntry.side===_taraDir
    &&manualKalshiEntry.entryCents>0
    &&manualKalshiEntry.contracts>0;
  // Unified "we have a real position" flag for ticket value reads
  const _positionKnown=_liveValid||_manualValid;
  let _liveEntryCents=null,_liveContractsActual=null,_liveStakeDollars=null,_liveCurOurCents=null;
  let _liveUnrealCents=null,_liveUnrealDollars=null,_liveMaxPayout=null,_liveMaxNet=null;
  let _liveExitCents=null,_liveRealCents=null,_liveRealDollars=null,_liveIsWin=null;
  if(_liveValid){
    _liveEntryCents=_ordS.fillCents!=null?_ordS.fillCents:_ordS.limitCents;
    _liveContractsActual=_ordS.count;
    // V10.2.26 — STAKE DISPLAY FIX. Old behavior preferred autoOrderState.betDollars
    //   (the BUDGET passed to Kalshi at order time) over the real cost
    //   (entry × contracts / 100). Kalshi only sells whole contracts, so the
    //   actual cost is always ≤ budget. Showing budget as "stake" misled users
    //   into thinking they spent more than they did (e.g. $1.00 stake when
    //   actual was 58¢ for 1 contract).
    //   Fix: ALWAYS compute from entry × contracts. Only fall back to betDollars
    //   if entry or contracts are missing (defensive — shouldn't happen in
    //   live state).
    _liveStakeDollars=(_liveEntryCents&&_liveContractsActual)
      ?(_liveEntryCents*_liveContractsActual/100)
      :(Number(autoOrderState.betDollars)||0);
    _liveMaxPayout=_liveContractsActual*1.0;
    _liveMaxNet=_liveMaxPayout-_liveStakeDollars;
    // Live current offer on our side (from kalshiYesPrice, translated by direction)
    if(_isYesLive&&_yes>0&&_yes<100){
      _liveCurOurCents=_liveDir==='UP'?Math.round(_yes):Math.round(100-_yes);
      _liveUnrealCents=_liveCurOurCents-_liveEntryCents;
      _liveUnrealDollars=(_liveUnrealCents*_liveContractsActual)/100;
    }
    if(_isExited){
      // V13.4.290: _ordS.exitCents (autoOrderState.exitAtCents) is the RAW YES limit
      //   price sent to Kalshi (_exitYesLimitCents -- for DOWN that is q.ask, the YES
      //   ask), while _liveEntryCents is already OUR-SIDE (_execCostCents converts
      //   DOWN to 100-q.bid). Subtracting them directly mixed two different scales on
      //   every DOWN trade -- UP was correct only because its our-side price and its
      //   YES price are the same number (both q.bid).
      //   Converted to our-side here using the SAME rule _execCostCents uses, so the
      //   arrow ("entry -> exit") and the profit line below it are computed from one
      //   consistent scale instead of two different silently-mismatched ones.
      //   The writer already computes the correct P&L independently as
      //   pnlCentsPerContract (via _exitValueCents, the our-side mirror) -- kept as a
      //   cross-check, not the primary path, so a real disagreement is visible rather
      //   than silently overwritten.
      _liveExitCents=_liveDir==='DOWN'?(100-_ordS.exitCents):_ordS.exitCents;
      _liveRealCents=_liveExitCents-_liveEntryCents;
      _liveRealDollars=(_liveRealCents*_liveContractsActual)/100;
      _liveIsWin=_liveRealCents>0;
    }
  }else if(_manualValid){
    // V10.2.9 — read from user-logged fill. Same shape as _liveValid path
    //   so the rest of the ticket can use these without knowing the source.
    //   Exit data is NOT available (no Kalshi exit response yet — user closes
    //   manually). _isExited stays false; "result" row won't render.
    _liveEntryCents=manualKalshiEntry.entryCents;
    _liveContractsActual=manualKalshiEntry.contracts;
    _liveStakeDollars=(manualKalshiEntry.entryCents*manualKalshiEntry.contracts)/100;
    _liveMaxPayout=_liveContractsActual*1.0;
    _liveMaxNet=_liveMaxPayout-_liveStakeDollars;
    if(_isYesLive&&_yes>0&&_yes<100){
      _liveCurOurCents=manualKalshiEntry.side==='UP'?Math.round(_yes):Math.round(100-_yes);
      _liveUnrealCents=_liveCurOurCents-_liveEntryCents;
      _liveUnrealDollars=(_liveUnrealCents*_liveContractsActual)/100;
    }
  }

  // ─────────────────────────────────────────────────────────────────────────
  // V13.4.313: SIMPLIFIED per user request ("extremely simple, match the
  //   theme"). This used to render 3 rows of settings toggles (AUTO/MANUAL,
  //   Tara's Call/Trade, Mode Patient/Fast) plus a Phase-4 badge and a full
  //   restatement of the exit rule -- every one of them either duplicating a
  //   control that already lives in Trading Settings, or (Tara's Call/Trade)
  //   never touching a real order in the first place: _runEntry always
  //   resolves direction straight off lockedCallRef.current, never off
  //   autoExecSettings.signalSource, no matter which side of that toggle was
  //   picked -- its own tooltip admitted "real Tara's Trade model is phase 4
  //   work." Down to the one status word (via computeAutoExecReadout, the
  //   same helper ThisTradeCard's own chip uses, so the two can't drift) and
  //   the one figure this card owns that nothing else on the page shows:
  //   today's aggregate P&L across every resolved trade, auto or manual.
  const _renderPredictorHeader=()=>{
    const _exec=computeAutoExecReadout(autoExecSettings,killSwitchEngaged);
    return React.createElement('div',{key:'predictor-header',className:'mb-3 pb-3 border-b border-[#24242E]'},
    React.createElement('div',{className:'flex items-baseline justify-between gap-2 mb-2.5'},
      React.createElement('span',{className:'text-[11px] uppercase font-bold tracking-[0.18em] text-[#EDEDED]/40'},'auto-exec'),
      React.createElement('span',{
        className:'text-[10px] uppercase font-bold tracking-[0.12em] cursor-pointer hover:opacity-75 transition-opacity',
        style:{color:_exec.tone},
        onClick:()=>{if(typeof setShowTradingSettings==='function')setShowTradingSettings(true);},
        title:'Open Trading Settings',
      },_exec.word),
    ),
    // This strip is from the Call log, not an exchange-account statement.
    // V10.2.9 — was filtered to autoExec=true only, which left the pill blank
    //   forever for users who hadn't run auto-exec yet (the 814 historical
    //   manual trades didn't show). Now shows ALL today's resolved trades
    //   with a small breakdown when both auto and manual trades exist.
    (()=>{
      const _log=Array.isArray(taraCallLog)?taraCallLog:[];
      const _today=_dayKey(new Date(),timeFormat); // V10.2.x: honors header TZ
      const _todayAll=_log.filter(e=>e
        &&(e.result==='WIN'||e.result==='LOSS')
        &&_dayKey(e.resolvedAt||e.time||0,timeFormat)===_today);
      const _todayAuto=_todayAll.filter(e=>e.autoExec===true);
      const _todayManual=_todayAll.filter(e=>e.autoExec!==true);
      const _n=_todayAll.length;
      const _wins=_todayAll.filter(e=>e.result==='WIN').length;
      const _wr=_n>0?Math.round((_wins/_n)*100):null;
      let _pnl=null;
      if(_n>0){
        _pnl=0;
        let _haveAny=false;
        for(const e of _todayAll){
          if(Number.isFinite(e.realizedPnLDollars)){
            _pnl+=e.realizedPnLDollars;
            _haveAny=true;
          }
        }
        if(!_haveAny)_pnl=null;
      }
      const _pnlSign=_pnl==null?'':_pnl>=0?'+':'';
      const _pnlColor=_pnl==null?'rgba(237,237,237,0.55)':_pnl>0?'rgb(35,185,129)':_pnl<0?'#E8455E':'rgba(237,237,237,0.65)';
      // V10.2.9: show breakdown only when BOTH types exist today; otherwise the
      //   single-source line is unambiguous already.
      const _showBreakdown=_todayAuto.length>0&&_todayManual.length>0;
      const _breakdownText=_showBreakdown
        ?`(auto ${_todayAuto.length} · manual ${_todayManual.length})`
        :_todayAuto.length>0&&_todayManual.length===0
          ?'(all auto)'
          :_todayManual.length>0&&_todayAuto.length===0
            ?'(not auto-tagged)'
            :'';
      return React.createElement('div',{className:'mt-2 pt-2 border-t border-[#24242E] text-[10px] tabular-nums flex flex-wrap items-baseline gap-2',style:{fontFamily:'IBM Plex Mono,ui-monospace,monospace'}},
        React.createElement('span',{style:{color:'rgba(237,237,237,0.40)'}},'Call record today'),
        React.createElement('span',{style:{color:'rgba(237,237,237,0.55)'}},'·'),
        React.createElement('span',{style:{color:'rgba(237,237,237,0.55)'}},'local gross est.'),
        React.createElement('span',{style:{color:_pnlColor,fontWeight:600}},
          _pnl==null?'--':`${_pnlSign}$${_pnl.toFixed(2)}`),
        React.createElement('span',{style:{color:'rgba(237,237,237,0.40)'}},'·'),
        React.createElement('span',{style:{color:'rgba(237,237,237,0.65)'}},`${_n} Call${_n===1?'':'s'}`),
        React.createElement('span',{style:{color:'rgba(237,237,237,0.40)'}},'·'),
        React.createElement('span',{style:{color:'rgba(237,237,237,0.55)'}},'WR'),
        React.createElement('span',{style:{color:'rgba(237,237,237,0.85)'}},_wr==null?'--':`${_wr}%`),
        _breakdownText?React.createElement('span',{style:{color:'rgba(237,237,237,0.40)',fontSize:'9px',marginLeft:'2px'}},_breakdownText):null,
      );
    })(),
  );
  };

  // ── ENTRY PROMPT ──────────────────────────────────────────────────────
  if(showEntryPrompt){
    const _sideLabel=showEntryPrompt==='LONG_YES'?'long yes':'long no';
    const _sideColor=showEntryPrompt==='LONG_YES'?'rgb(35,185,129)':'#E8455E';
    return React.createElement('div',{
      // V13.4.314: was its own bordered/backgrounded card shell -- now nests
      //   inside ThisTradeCard's own border (see the "literally combine" note
      //   at ThisTradeCard's closing tag), so it takes a plain top hairline
      //   instead of a second card outline.
      className:'pt-3 mt-3 border-t border-[#16161c]',
    },
      _renderPredictorHeader(),
      React.createElement('div',{className:'flex items-baseline justify-between mb-3'},
        React.createElement('span',{className:'text-[11px] font-bold',style:{color:_sideColor,letterSpacing:'0.02em',textTransform:'none'}},`confirm ${_sideLabel}`),
        React.createElement('button',{onClick:()=>setShowEntryPrompt(null),className:'text-[10px] text-[#EDEDED]/45 hover:text-[#EDEDED]/75'},'cancel'),
      ),
      React.createElement('p',{className:'text-[12px] text-[#EDEDED]/65 mb-3 leading-relaxed'},
        'Enter your actual fill price in cents and contract count.',
      ),
      React.createElement('div',{className:'flex flex-col sm:flex-row gap-3 mb-3'},
        React.createElement('label',{className:'flex-1'},
          React.createElement('div',{className:'text-[10px] text-[#EDEDED]/45 mb-1',style:{letterSpacing:'0.02em'}},'fill price (cents)'),
          React.createElement('input',{
            type:'number',min:1,max:99,step:1,value:entryPriceInput,
            onChange:(e)=>setEntryPriceInput(e.target.value),
            className:'w-full px-3 py-2 rounded-lg text-base tabular-nums',
            style:{background:'rgba(0,0,0,0.35)',border:'1px solid #24242E',color:'#EDEDED',fontFamily:'IBM Plex Mono,ui-monospace,monospace'},
            autoFocus:true,
          }),
        ),
        React.createElement('label',{className:'flex-1'},
          React.createElement('div',{className:'text-[10px] text-[#EDEDED]/45 mb-1',style:{letterSpacing:'0.02em'}},'contracts'),
          React.createElement('input',{
            type:'number',min:1,max:1000,step:1,value:entryContractsInput,
            onChange:(e)=>setEntryContractsInput(e.target.value),
            className:'w-full px-3 py-2 rounded-lg text-base tabular-nums',
            style:{background:'rgba(0,0,0,0.35)',border:'1px solid #24242E',color:'#EDEDED',fontFamily:'IBM Plex Mono,ui-monospace,monospace'},
          }),
        ),
      ),
      React.createElement('button',{
        onClick:_onConfirmEntry,
        className:'w-full py-2.5 rounded-lg text-[12px] font-bold',
        style:{background:_sideColor.replace('rgb(','rgba(').replace(')',',0.15)').replace('0.92','0.18'),color:_sideColor,border:'1px solid '+_sideColor.replace('rgb(','rgba(').replace(')',',0.45)').replace('0.92','0.45'),letterSpacing:'0.02em',textTransform:'none'},
      },`confirm — track ${_sideLabel}`),
    );
  }

  // ── EXIT PROMPT ───────────────────────────────────────────────────────
  if(showExitPrompt&&scalperPosition){
    return React.createElement('div',{
      // V13.4.314: nests inside ThisTradeCard's own border now -- plain top
      //   hairline instead of a second card outline (see the "literally
      //   combine" note at ThisTradeCard's closing tag).
      className:'pt-3 mt-3 border-t border-[#16161c]',
    },
      _renderPredictorHeader(),
      React.createElement('div',{className:'flex items-baseline justify-between mb-3'},
        React.createElement('span',{className:'text-[11px] font-bold',style:{color:'#23B981',letterSpacing:'0.02em',textTransform:'none'}},'confirm exit'),
        React.createElement('button',{onClick:()=>setShowExitPrompt(false),className:'text-[10px] text-[#EDEDED]/45 hover:text-[#EDEDED]/75'},'cancel'),
      ),
      React.createElement('p',{className:'text-[12px] text-[#EDEDED]/65 mb-3 leading-relaxed'},
        `Exit price for your ${scalperPosition.side==='LONG_YES'?'long yes':'long no'} position. Entry was ${scalperPosition.entryCents}¢ · ${scalperPosition.contracts} contract${scalperPosition.contracts!==1?'s':''}.`,
      ),
      React.createElement('label',{className:'block mb-3'},
        React.createElement('div',{className:'text-[10px] text-[#EDEDED]/45 mb-1',style:{letterSpacing:'0.02em'}},'exit price (cents)'),
        React.createElement('input',{
          type:'number',min:0,max:100,step:1,value:exitPriceInput,
          onChange:(e)=>setExitPriceInput(e.target.value),
          className:'w-full px-3 py-2 rounded-lg text-base tabular-nums',
          style:{background:'rgba(0,0,0,0.35)',border:'1px solid #24242E',color:'#EDEDED',fontFamily:'IBM Plex Mono,ui-monospace,monospace'},
          autoFocus:true,
        }),
      ),
      React.createElement('button',{
        onClick:_onConfirmExit,
        className:'w-full py-2.5 rounded-lg text-[12px] font-bold',
        style:{background:'rgba(35,185,129,0.15)',color:'#23B981',border:'1px solid rgba(35,185,129,0.45)',letterSpacing:'0.02em',textTransform:'none'},
      },'confirm exit'),
    );
  }

  // ── STATE: HOLD ────────────────────────────────────────────────────────
  if(_state==='HOLD'&&scalperPosition){
    const _cur=Number(kalshiYesPrice)||50;
    const _marketPx=scalperPosition.side==='LONG_YES'?_cur:(100-_cur);
    const _unrealizedPerContract=_marketPx-scalperPosition.entryCents;
    const _unrealizedTotal=_unrealizedPerContract*scalperPosition.contracts;
    const _color=_unrealizedPerContract>=1?'rgb(35,185,129)':_unrealizedPerContract<=-1?'#E8455E':'#EDEDED';
    let _rec='hold';
    let _recDetail='Position open · monitoring for target, stop, or reversal.';
    let _recColor='#23B981';
    if(_marketPx>=scalperPosition.suggestedTargetCents){
      _rec='take profit';
      _recDetail=`Target ${scalperPosition.suggestedTargetCents}¢ reached. Lock in +${_unrealizedPerContract.toFixed(0)}¢ per contract.`;
      _recColor='rgb(35,185,129)';
    } else if(_marketPx<=scalperPosition.suggestedStopCents){
      _rec='cut loss';
      _recDetail=`Stop ${scalperPosition.suggestedStopCents}¢ reached. Exit now (-${Math.abs(_unrealizedPerContract).toFixed(0)}¢ per contract).`;
      _recColor='#E8455E';
    } else if(scalperRead?.dir&&((scalperPosition.side==='LONG_YES'&&scalperRead.dir==='DOWN')||(scalperPosition.side==='LONG_NO'&&scalperRead.dir==='UP'))&&_conviction>=70){
      _rec='consider exit';
      _recDetail=`Tape reversed against you (${_conviction}% conviction ${scalperRead.dir}). Consider closing.`;
      _recColor='#23B981';
    }
    const _holdSec=Math.round((Date.now()-scalperPosition.entryTime)/1000);
    if(_holdSec>=120&&Math.abs(_unrealizedPerContract)<3){
      _rec='close neutral';
      _recDetail=`Position stale (${Math.floor(_holdSec/60)}m ${_holdSec%60}s) with no progress.`;
      _recColor='#23B981';
    }
    return React.createElement('div',{
      // V13.4.314: nests inside ThisTradeCard's own border now -- plain top
      //   hairline instead of a second card outline (see the "literally
      //   combine" note at ThisTradeCard's closing tag).
      className:'pt-3 mt-3 border-t border-[#16161c]',
    },
      _renderPredictorHeader(),
      _topEl,
      React.createElement('div',{className:'space-y-0 mb-3'},
        React.createElement('div',{className:'flex items-baseline justify-between py-1.5'},
          React.createElement('span',{className:'text-[10px] text-[#EDEDED]/45',style:{letterSpacing:'0.02em'}},'entry'),
          React.createElement('span',{className:'text-base tabular-nums font-medium',style:{fontFamily:'IBM Plex Mono,ui-monospace,monospace'}},
            `${scalperPosition.side==='LONG_YES'?'long yes':'long no'} @ ${scalperPosition.entryCents.toFixed(0)}¢ × ${scalperPosition.contracts}`),
        ),
        React.createElement('div',{className:'flex items-baseline justify-between py-1.5 border-t border-[#24242E]'},
          React.createElement('span',{className:'text-[10px] text-[#EDEDED]/45',style:{letterSpacing:'0.02em'}},'unrealized'),
          React.createElement('span',{className:'text-base tabular-nums font-medium',style:{color:_color,fontFamily:'IBM Plex Mono,ui-monospace,monospace'}},
            `${_unrealizedPerContract>=0?'+':''}${_unrealizedPerContract.toFixed(0)}¢/ct · ${_unrealizedTotal>=0?'+':''}${_unrealizedTotal.toFixed(0)}¢`),
        ),
        React.createElement('div',{className:'flex items-baseline justify-between py-1.5 border-t border-[#24242E]'},
          React.createElement('span',{className:'text-[10px] text-[#EDEDED]/45',style:{letterSpacing:'0.02em'}},'hold'),
          React.createElement('span',{className:'text-[12px] tabular-nums text-[#EDEDED]/65',style:{fontFamily:'IBM Plex Mono,ui-monospace,monospace'}},
            `${Math.floor(_holdSec/60)}m ${_holdSec%60}s · target ${scalperPosition.suggestedTargetCents}¢ · stop ${scalperPosition.suggestedStopCents}¢`),
        ),
      ),
      React.createElement('div',{className:'p-3 rounded-lg mb-3',style:{background:'rgba(0,0,0,0.20)',borderLeft:'2px solid '+_recColor}},
        React.createElement('div',{className:'text-[10px] mb-1 font-bold',style:{color:_recColor,letterSpacing:'0.02em',textTransform:'none'}},'scalper says'),
        React.createElement('div',{className:'text-lg font-medium tracking-tight mb-1',style:{fontFamily:'IBM Plex Mono,ui-monospace,monospace'}},_rec),
        React.createElement('p',{className:'text-[12px] text-[#EDEDED]/65 leading-relaxed'},_recDetail),
      ),
      React.createElement('button',{
        onClick:_onIntendSell,
        className:'w-full py-2.5 rounded-lg text-[12px] font-bold',
        style:{background:'rgba(35,185,129,0.10)',color:'#23B981',border:'1px solid rgba(35,185,129,0.40)',letterSpacing:'0.02em',textTransform:'none'},
      },'i sold · enter fill price'),
    );
  }

  // ── STATE: SUGGEST ────────────────────────────────────────────────────
  if(_state==='SUGGEST'){
    const _suggestedSide=_dir==='UP'?'LONG_YES':'LONG_NO';
    const _suggestedEntry=_suggestedSide==='LONG_YES'?Number(kalshiYesPrice)||50:(100-(Number(kalshiYesPrice)||50));
    const _target=_suggestedEntry+scalperSettings.targetCents;
    const _stop=_suggestedEntry-scalperSettings.stopCents;
    const _sideColor=_suggestedSide==='LONG_YES'?'rgb(35,185,129)':'#E8455E';
    const _sideLabel=_suggestedSide==='LONG_YES'?'long yes':'long no';
    return React.createElement('div',{
      // V13.4.314: nests inside ThisTradeCard's own border now -- plain top
      //   hairline instead of a second card outline (see the "literally
      //   combine" note at ThisTradeCard's closing tag).
      className:'pt-3 mt-3 border-t border-[#16161c]',
    },
      _renderPredictorHeader(),
      _topEl,
      React.createElement('div',{className:'mb-3'},
        React.createElement('div',{className:'text-[10px] mb-1 font-bold',style:{color:_sideColor,letterSpacing:'0.02em',textTransform:'none'}},'suggest entry'),
        React.createElement('div',{className:'text-[20px] font-medium tracking-tight mb-1',style:{fontFamily:'IBM Plex Mono,ui-monospace,monospace'}},
          `${_sideLabel} @ ${_suggestedEntry.toFixed(0)}¢`),
        React.createElement('div',{className:'text-[11px] text-[#EDEDED]/65 tabular-nums mb-2',style:{fontFamily:'IBM Plex Mono,ui-monospace,monospace'}},
          `target ${_target.toFixed(0)}¢ (+${scalperSettings.targetCents}¢) · stop ${_stop.toFixed(0)}¢ (-${scalperSettings.stopCents}¢)`),
        React.createElement('p',{className:'text-[12px] text-[#EDEDED]/60 leading-relaxed'},_reason),
      ),
      React.createElement('div',{className:'flex gap-2'},
        React.createElement('button',{
          onClick:()=>_onIntendBuy(_suggestedSide),
          className:'flex-1 py-2.5 rounded-lg text-[12px] font-bold',
          style:{background:_sideColor.replace('rgb(','rgba(').replace(')',',0.15)').replace('0.92','0.18'),color:_sideColor,border:'1px solid '+_sideColor.replace('rgb(','rgba(').replace(')',',0.45)').replace('0.92','0.45'),letterSpacing:'0.02em',textTransform:'none'},
        },`i bought ${_sideLabel}`),
        React.createElement('button',{
          onClick:_onDismiss,
          className:'px-4 py-2.5 rounded-lg text-[11px] text-[#EDEDED]/55 hover:text-[#EDEDED]/85',
          style:{background:'rgba(0,0,0,0.20)',border:'1px solid #24242E',letterSpacing:'0.02em',textTransform:'none'},
        },'dismiss'),
      ),
    );
  }

  // ═══════════════════════════════════════════════════════════════════════
  // V9.17.6: TARA LOCKED — TRADE TICKET RENDER
  // ═══════════════════════════════════════════════════════════════════════
  // When Tara has a lock active and the scalper isn't actively suggesting/
  // holding a position, show her trade ticket as the primary content.
  // The scalper status (off / paused / watching) becomes a subtle note.
  if(_taraLocked&&_state!=='SUGGEST'&&_state!=='HOLD'){
    // Scalper status mini-note that appears at the bottom
    let _scalperNote=null;
    if(_state==='DISABLED'){
      _scalperNote=React.createElement('div',{className:'flex items-baseline justify-between pt-2 mt-2 border-t border-[#24242E]'},
        React.createElement('span',{className:'text-[10px] text-[#EDEDED]/40',style:{letterSpacing:'0.02em'}},'scalper off'),
        React.createElement('button',{
          onClick:()=>setShowTradingSettings(true),
          className:'text-[10px] underline underline-offset-2',
          style:{color:'#23B981',letterSpacing:'0.02em',textTransform:'none'},
        },'enable scalper'),
      );
    } else if(_state==='BLOCKED'){
      _scalperNote=React.createElement('div',{className:'pt-2 mt-2 border-t border-[#24242E]'},
        React.createElement('div',{className:'text-[10px] text-[#EDEDED]/40',style:{letterSpacing:'0.02em'}},`scalper paused · ${_elig.blockReason}`),
      );
    } else if(_state==='IDLE'){
      _scalperNote=React.createElement('div',{className:'flex items-baseline justify-between pt-2 mt-2 border-t border-[#24242E]'},
        React.createElement('span',{className:'text-[10px] text-[#EDEDED]/40',style:{letterSpacing:'0.02em'}},'scalper watching'),
        React.createElement('span',{className:'text-[10px] tabular-nums text-[#EDEDED]/35',style:{fontFamily:'IBM Plex Mono,ui-monospace,monospace'}},
          `${(scalperSettings.maxSuggestionsPerHour||3)-_scalperGetHourlyCount(scalperLog,scalperSuggestionsRef)}/${scalperSettings.maxSuggestionsPerHour||3} left this hour`),
      );
    }
    return React.createElement('div',{
      // V13.4.314: nests inside ThisTradeCard's own border now -- plain top
      //   hairline instead of a second card outline (see the "literally
      //   combine" note at ThisTradeCard's closing tag).
      className:'pt-3 mt-3 border-t border-[#16161c]',
    },
      _renderPredictorHeader(),
      // Header: "this round" + window label + LOCKED badge
      React.createElement('div',{className:'flex items-baseline justify-between mb-3 pb-2 border-b border-[#24242E]'},
        React.createElement('div',{className:'flex flex-col'},
          React.createElement('span',{className:'text-[11px] font-bold',style:{color:'#23B981',letterSpacing:'0.02em',textTransform:'none'}},'this round'),
          _winLabel&&React.createElement('span',{className:'text-[10px] text-[#EDEDED]/55 tabular-nums mt-0.5',style:{fontFamily:'IBM Plex Mono,ui-monospace,monospace'}},_winLabel),
        ),
        // V13.4.282: this chip always said "LOCKED", which put it in direct
        //   contradiction with THIS TRADE / PHASE / DECISION CLOCK reading LEANING
        //   on the same window. Both were true: the ticket follows the ENGINE lock,
        //   which fires earlier than Tara's settled call -- that is exactly what the
        //   TARA'S CALL / TARA'S TRADE toggle above selects. The chip just never
        //   said WHICH lock it meant.
        //   getTaraDirection already reports where the direction came from, so the
        //   label is read from that rather than assumed.
        (()=>{
          const _fromEngine=/^lock/.test(String((_taraDirRes&&_taraDirRes.source)||''));
          // Engine lock is the earlier, less settled layer -- muted so it does not
          //   read with the same authority as a committed call.
          const _chipCol=_fromEngine?'rgba(237,237,237,0.62)':_taraDirColor;
          return React.createElement('span',{
            className:'text-[10px] font-bold px-2 py-0.5 rounded-lg',
            title:_fromEngine
              ?"Engine lock — the engine has picked a side, but Tara has not committed the round yet. It fires earlier than her settled call and can still differ from it. This ticket follows it because signal source is set to Tara's Trade."
              :"Tara's committed call for this round.",
            style:{
              background:_fromEngine?'rgba(237,237,237,0.07)'
                :_taraDirColor.replace('rgb(','rgba(').replace(')',',0.15)').replace('0.92','0.18'),
              color:_chipCol,
              border:_fromEngine?'1px solid rgba(237,237,237,0.18)':'1px solid transparent',
              letterSpacing:'0.06em',textTransform:'uppercase',
            },
          },_fromEngine?'engine lock':'call locked');
        })(),
      ),
      // V9.17.5: patient entry status banner if applicable
      autoOrderState&&autoOrderState.status==='patient-waiting'&&React.createElement('div',{
        className:'px-3 py-2 rounded-lg mb-2',
        style:{background:'rgba(35,185,129,0.08)',border:'1px solid rgba(35,185,129,0.25)'},
      },
        React.createElement('div',{className:'flex items-baseline justify-between'},
          React.createElement('span',{className:'text-[10px] font-bold',style:{color:'#23B981',letterSpacing:'0.02em',textTransform:'none'}},'waiting for better entry'),
          (()=>{
            const _placedAt=autoOrderState.placedAt||Date.now();
            const _waitedSec=Math.floor((Date.now()-_placedAt)/1000);
            const _maxSec=Number(autoOrderState.maxWaitSec)||90;
            const _remaining=Math.max(0,_maxSec-_waitedSec);
            return React.createElement('span',{className:'text-[10px] tabular-nums text-[#EDEDED]/60',style:{fontFamily:'IBM Plex Mono,ui-monospace,monospace'}},`${_remaining}s left`);
          })(),
        ),
        React.createElement('p',{className:'text-[11px] text-[#EDEDED]/65 mt-1 leading-snug'},
          `Tara wants ${_taraDirLabel} but offer is ${autoOrderState.currentCents}¢ · waiting for ≤ ${autoOrderState.targetCents}¢`,
        ),
      ),
      autoOrderState&&autoOrderState.status==='patient-skipped'&&React.createElement('div',{
        className:'px-3 py-2 rounded-lg mb-2',
        style:{background:'rgba(237,237,237,0.04)',border:'1px solid #24242E'},
      },
        React.createElement('div',{className:'text-[10px] font-bold text-[#EDEDED]/55',style:{letterSpacing:'0.02em',textTransform:'none'}},'skipped this round'),
        React.createElement('p',{className:'text-[11px] text-[#EDEDED]/55 mt-1 leading-snug'},autoOrderState.reason||'patient entry conditions not met'),
      ),
      // V9.17.26: dedicated ERROR BANNER inside the trade ticket. The live trade
      //   pill at the top has a Clear button + truncated error, but it's small
      //   and often above the fold. This banner surfaces the FULL Kalshi error
      //   reason next to the trade ticket where the user is already looking.
      //   "Clear & retry" wipes autoOrderState so the next click re-evaluates.
      // V9.17.28: when _diag is present, render request body + response headers +
      //   response body so the user can copy-paste the full diagnostic without
      //   opening DevTools. Copy button writes to clipboard in a single line.
      autoOrderState&&autoOrderState.status==='error'&&React.createElement('div',{
        className:'px-3 py-2.5 rounded-lg mb-2',
        style:{background:'rgba(232,69,94,0.06)',border:'1px solid rgba(232,69,94,0.30)'},
      },
        React.createElement('div',{className:'flex items-baseline justify-between gap-2 mb-1'},
          React.createElement('span',{className:'text-[10px] font-bold uppercase tracking-wider',style:{color:'#E8455E'}},'⚠ Kalshi error · order not placed'),
          React.createElement('div',{className:'flex gap-1.5 shrink-0'},
            autoOrderState._diag&&React.createElement('button',{
              onClick:()=>{
                try{
                  const _d=autoOrderState._diag;
                  const _txt=`KALSHI ERROR DIAGNOSTIC
status: ${_d.status}
reason: ${_d.reason}
---
REQUEST BODY:
${JSON.stringify(_d.requestBody,null,2)}
---
RESPONSE HEADERS:
${JSON.stringify(_d.responseHeaders,null,2)}
---
RESPONSE BODY:
${_d.responseBody||'(empty)'}`;
                  if(navigator.clipboard&&navigator.clipboard.writeText){
                    navigator.clipboard.writeText(_txt).then(()=>{
                      try{console.info('[V9.17.28] diagnostic copied to clipboard');}catch(_){}
                    });
                  }
                }catch(_){}
              },
              className:'px-2 py-0.5 rounded-lg text-[9px] uppercase font-bold tracking-wider',
              style:{color:'#23B981',border:'1px solid rgba(35,185,129,0.40)',background:'rgba(35,185,129,0.05)'},
              title:'Copy full request + response details to clipboard',
            },'⧉ Copy'),
            typeof onClearAutoOrder==='function'&&React.createElement('button',{
              onClick:onClearAutoOrder,
              className:'px-2 py-0.5 rounded-lg text-[9px] uppercase font-bold tracking-wider',
              style:{color:'#E8455E',border:'1px solid rgba(232,69,94,0.40)',background:'rgba(232,69,94,0.05)'},
              title:'Dismiss this error so auto-exec / the manual button can fire on the next attempt.',
            },'✕ Clear'),
          ),
        ),
        React.createElement('div',{
          className:'text-[11px] leading-snug',
          style:{color:'rgba(237,237,237,0.85)',wordBreak:'break-word',fontFamily:'IBM Plex Mono,ui-monospace,monospace'},
          // V13.4.322: was autoOrderState.error -- a field nothing ever
          //   writes (only .reason is set). This always rendered the literal
          //   fallback string "unknown error" regardless of what actually
          //   went wrong, on every real auto-exec failure this banner has
          //   ever shown.
        },String(autoOrderState.reason||'unknown error')),
        // V9.17.28: expandable diagnostic details. Renders the four key fields
        //   below the headline reason so the user can read or copy them.
        autoOrderState._diag&&React.createElement('details',{className:'mt-2'},
          React.createElement('summary',{className:'text-[9px] uppercase tracking-wider cursor-pointer',style:{color:'rgba(237,237,237,0.55)'}},'show full request/response details'),
          React.createElement('div',{className:'mt-2 space-y-2'},
            React.createElement('div',null,
              React.createElement('div',{className:'text-[9px] uppercase tracking-wider mb-1',style:{color:'rgba(237,237,237,0.50)'}},'STATUS'),
              React.createElement('div',{className:'text-[10px] font-mono',style:{color:'rgba(237,237,237,0.85)'}},String(autoOrderState._diag.status||'(none)')),
            ),
            React.createElement('div',null,
              React.createElement('div',{className:'text-[9px] uppercase tracking-wider mb-1',style:{color:'rgba(237,237,237,0.50)'}},'REQUEST BODY (what we sent)'),
              React.createElement('pre',{className:'text-[10px] font-mono p-2 rounded-lg',style:{color:'rgba(237,237,237,0.80)',background:'#050508',border:'1px solid #24242E',whiteSpace:'pre-wrap',wordBreak:'break-all',maxHeight:'160px',overflow:'auto'}},
                JSON.stringify(autoOrderState._diag.requestBody||{},null,2)
              ),
            ),
            React.createElement('div',null,
              React.createElement('div',{className:'text-[9px] uppercase tracking-wider mb-1',style:{color:'rgba(237,237,237,0.50)'}},'RESPONSE HEADERS'),
              React.createElement('pre',{className:'text-[10px] font-mono p-2 rounded-lg',style:{color:'rgba(237,237,237,0.80)',background:'#050508',border:'1px solid #24242E',whiteSpace:'pre-wrap',wordBreak:'break-all',maxHeight:'120px',overflow:'auto'}},
                JSON.stringify(autoOrderState._diag.responseHeaders||{},null,2)
              ),
            ),
            React.createElement('div',null,
              React.createElement('div',{className:'text-[9px] uppercase tracking-wider mb-1',style:{color:'rgba(237,237,237,0.50)'}},'RESPONSE BODY (what Kalshi sent back)'),
              React.createElement('pre',{className:'text-[10px] font-mono p-2 rounded-lg',style:{color:'rgba(237,237,237,0.80)',background:'#050508',border:'1px solid #24242E',whiteSpace:'pre-wrap',wordBreak:'break-all',maxHeight:'200px',overflow:'auto'}},
                autoOrderState._diag.responseBody||'(empty)'
              ),
            ),
          ),
        ),
        React.createElement('div',{className:'text-[9px] text-[#EDEDED]/50 mt-1.5'},
          'V9.17.28: click "show full request/response details" above to see what we sent and what Kalshi returned. Click ⧉ Copy to paste it elsewhere.',
        ),
      ),
      // V13.4.277: ONE-LINE SUMMARY + DISCLOSURE. The rows below were the biggest
      //   single block left on the page (~406px) and roughly half of them restated
      //   what THIS TRADE stage 1 and stage 3 now say. Collapsed by default -- but
      //   the numbers that live NOWHERE else (contract count, entry price, stake)
      //   stay on this summary line, so the detail is folded, not hidden.
      React.createElement('button',{
        key:'ticket-summary',
        onClick:_toggleTicket,
        className:'w-full flex items-baseline justify-between gap-2 py-2 text-left',
      },
        React.createElement('span',{className:'text-[12px] tabular-nums',style:{color:'rgba(237,237,237,0.75)'}},
          (()=>{
            const _d=_taraDirLabel||'—';
            const _n=Number.isFinite(_contracts)?_contracts:null;
            const _c=Number.isFinite(_entryCents)?Math.round(_entryCents):null;
            const _b=Number.isFinite(_betSize)?_betSize:null;
            const _size=(_n!=null&&_c!=null)?(' · '+_n+' @ '+_c+'¢'):'';
            const _stake=(_b!=null)?(' · $'+_b.toFixed(2)):'';
            return _d+_size+_stake;
          })(),
        ),
        React.createElement('span',{
          className:'text-[9px] uppercase font-bold tracking-[0.14em] shrink-0',
          style:{color:'rgba(237,237,237,0.35)'},
        },ticketOpen?'hide detail':'detail'),
      ),
      // V9.17.17: Trade ticket rows — show LIVE values when user is in a trade,
      //   generic suggestion when not. _liveValid is the gate.
      ticketOpen&&React.createElement('div',{className:'space-y-0'},
        // tara says — V9.17.22: when in trade, show ENTERED direction (from
        //   autoOrderState.dir) primary, and CURRENT Tara direction beneath if
        //   they diverge. Sticky lock means entered direction won't flip, but
        //   Tara's call can shift to time-cap-commit on the opposite side.
        //   User needs to see BOTH to make good exit decisions.
        (()=>{
          const _enteredDir=_liveValid?autoOrderState?.dir:null;
          const _enteredLabel=_enteredDir==='UP'?'long up':_enteredDir==='DOWN'?'long down':null;
          const _enteredColor=_enteredDir==='UP'?'rgb(35,185,129)':_enteredDir==='DOWN'?'#E8455E':'#EDEDED';
          const _diverges=_enteredDir&&_taraDir&&_enteredDir!==_taraDir;
          if(_liveValid&&_enteredDir){
            return React.createElement('div',{className:'py-2'},
              React.createElement('div',{className:'flex items-baseline justify-between'},
                React.createElement('span',{className:'text-[11px] text-[#EDEDED]/55',style:{letterSpacing:'0.02em'}},'your entry'),
                React.createElement('span',{className:'text-base font-medium tabular-nums',style:{color:_enteredColor,fontFamily:'IBM Plex Mono,ui-monospace,monospace'}},_enteredLabel),
              ),
              _diverges&&React.createElement('div',{
                className:'flex items-baseline justify-between mt-1 px-2 py-1 rounded-lg',
                style:{background:'rgba(35,185,129,0.06)',border:'1px solid rgba(35,185,129,0.20)'},
              },
                React.createElement('span',{className:'text-[10px]',style:{color:'#23B981',letterSpacing:'0.02em'}},'⚠ tara now says'),
                React.createElement('span',{className:'text-[11px] tabular-nums',style:{color:_taraDirColor,fontFamily:'IBM Plex Mono,ui-monospace,monospace'}},_taraDirLabel),
              ),
              _diverges&&React.createElement('div',{className:'text-[9px] text-[#EDEDED]/45 mt-1 italic leading-snug'},
                'sticky lock keeps your position · she may have shifted on late commit · consider manual exit if conviction is gone',
              ),
            );
          }
          // Pre-trade (generic): just show Tara's direction
          if(_lockOpposed){
            return React.createElement('div',{className:'py-2'},
              React.createElement('div',{className:'flex items-baseline justify-between'},
                React.createElement('span',{className:'text-[11px] text-[#EDEDED]/55',style:{letterSpacing:'0.02em'}},'tara says'),
                React.createElement('span',{className:'text-base font-medium tabular-nums',style:{color:_taraDirColor,fontFamily:'IBM Plex Mono,ui-monospace,monospace'}},_taraDirLabel)
              ),
              React.createElement('div',{className:'mt-1 rounded-lg border border-amber-500/40 bg-amber-500/10 px-2 py-1.5'},
                React.createElement('div',{className:'text-[11px] font-semibold text-amber-400'},'LOCK OPPOSED'),
                React.createElement('div',{className:'text-[10px] leading-snug text-[#EDEDED]/70'},
                  `Ticket holds ${_taraDir}, but the live read is ${_lkLiveDir} at ${_lkConv.toFixed(0)}pt. This lock is on the wrong side of the current signal -- do not add, and consider cutting.`)
              )
            );
          }
          return React.createElement('div',{className:'flex items-baseline justify-between py-2'},
            React.createElement('span',{className:'text-[11px] text-[#EDEDED]/55',style:{letterSpacing:'0.02em'}},'tara says'),
            React.createElement('span',{className:'text-base font-medium tabular-nums',style:{color:_taraDirColor,fontFamily:'IBM Plex Mono,ui-monospace,monospace'}},_taraDirLabel),
          );
        })(),
        // EXITED PATH — replace ticket with realized summary
        _isExited&&_liveValid?[
          React.createElement('div',{key:'ex-entry',className:'flex items-baseline justify-between py-2 border-t border-[#24242E]'},
            React.createElement('span',{className:'text-[11px] text-[#EDEDED]/55',style:{letterSpacing:'0.02em'}},'entry → exit'),
            React.createElement('span',{className:'text-base tabular-nums font-medium',style:{fontFamily:'IBM Plex Mono,ui-monospace,monospace'}},`${_liveEntryCents}¢ → ${_liveExitCents}¢`),
          ),
          React.createElement('div',{key:'ex-count',className:'flex items-baseline justify-between py-2 border-t border-[#24242E]'},
            React.createElement('span',{className:'text-[11px] text-[#EDEDED]/55',style:{letterSpacing:'0.02em'}},'contracts'),
            React.createElement('span',{className:'text-[13px] tabular-nums text-[#EDEDED]/85',style:{fontFamily:'IBM Plex Mono,ui-monospace,monospace'}},`${_liveContractsActual}`),
          ),
          React.createElement('div',{key:'ex-stake',className:'flex items-baseline justify-between py-2 border-t border-[#24242E]'},
            React.createElement('span',{className:'text-[11px] text-[#EDEDED]/55',style:{letterSpacing:'0.02em'}},'stake'),
            React.createElement('span',{className:'text-[13px] tabular-nums text-[#EDEDED]/85',style:{fontFamily:'IBM Plex Mono,ui-monospace,monospace'}},`$${_liveStakeDollars.toFixed(2)}`),
          ),
          React.createElement('div',{key:'ex-pnl',className:'flex items-baseline justify-between py-2 border-t border-[#24242E]'},
            React.createElement('span',{className:'text-[11px] text-[#EDEDED]/55',style:{letterSpacing:'0.02em'}},'result'),
            React.createElement('span',{className:'text-base tabular-nums font-medium',style:{
              color:_liveRealCents===0?'#EDEDED':(_liveIsWin?'rgb(35,185,129)':'#E8455E'),
              fontFamily:'IBM Plex Mono,ui-monospace,monospace',
            }},
              _liveRealCents===0?'flat · $0.00':(_liveIsWin?'profit':'loss')+' · '+(_liveRealDollars>=0?'+':'-')+'$'+Math.abs(_liveRealDollars).toFixed(2)+' ('+(_liveRealCents>=0?'+':'')+_liveRealCents+'¢/contract)',
            ),
          ),
        ]:[
          // ═════════════════════════════════════════════════════════════════
          // V9.19.15: SIMPLIFIED TRADE TICKET
          //   Goal: every row reads in plain English. Hover (desktop) and tap
          //   (mobile) reveal an explanation. Engineering-mode monitoring data
          //   moved behind a "show monitoring" toggle so the default view stays
          //   readable. The TipRow helper below is local — same closure, same
          //   trade ticket. Used here only.
          // ═════════════════════════════════════════════════════════════════
          (()=>{
            // TipRow: a single row with label + value + tap-to-expand tooltip.
            // - On desktop: native `title` attr fires hover tooltip.
            // - On mobile: tap the label area → inline expansion below the row.
            // - Visual: label has a tiny dotted underline + ⓘ icon when tip exists.
            const _renderTip=(id,label,value,tip,valColor)=>{
              const _isOpen=tappedTip===id;
              return React.createElement('div',{key:id},
                React.createElement('div',{className:'flex items-baseline justify-between py-2 border-t border-[#24242E]'},
                  React.createElement('button',{
                    type:'button',
                    onClick:()=>setTappedTip(_isOpen?null:id),
                    title:tip,
                    className:'flex items-center gap-1 text-[11px] text-[#EDEDED]/55 cursor-pointer hover:text-[#EDEDED]/75 transition-colors',
                    style:{letterSpacing:'0.02em',background:'none',border:'none',padding:0,textAlign:'left'},
                  },
                    React.createElement('span',{style:{borderBottom:'1px dotted rgba(237,237,237,0.30)'}},label),
                    React.createElement('span',{className:'text-[9px] opacity-50 ml-0.5'},'ⓘ'),
                  ),
                  React.createElement('span',{className:'text-base tabular-nums font-medium text-right',style:{
                    color:valColor||'#EDEDED',
                    fontFamily:'IBM Plex Mono,ui-monospace,monospace',
                  }},value),
                ),
                _isOpen&&React.createElement('div',{
                  className:'pb-2 -mt-1 text-[10px] leading-relaxed',
                  style:{color:'rgba(237,237,237,0.55)',paddingLeft:'2px'},
                },tip),
              );
            };
            const _rows=[];
            // V10.2.7 — MANUAL-POSITION MISMATCH BANNER
            //   Detect a common failure mode the user hit: they have a real
            //   position on Kalshi (clicked ENTERED UP/DOWN button), but
            //   autoOrderState is empty or doesn't have a fill price. This
            //   happens when:
            //     (a) user entered manually on Kalshi outside of Tara
            //     (b) autoOrderState was cleared between place and now
            //     (c) auto-exec is disabled and they fired via Kalshi directly
            //   In any of these cases the rows below are FORWARD-LOOKING
            //   PREVIEW — what Tara would buy at the current market — NOT the
            //   user's actual position. Without this banner the user reads
            //   "1 contract @ 64¢" as their actual entry and gets confused
            //   when Kalshi shows different numbers.
            //   Banner directs them to the edit button (which can override
            //   the displayed entry price → make stops/targets accurate).
            //   V10.2.9: ALSO hide the banner when user has logged their real
            //   fill via ManualKalshiEntryInput — the ticket math is now
            //   correct, no banner needed.
            const _hasManualPos=!!userPosition&&!_liveValid&&!_manualValid;
            if(_hasManualPos){
              _rows.push(React.createElement('div',{
                key:'manual-position-banner',
                className:'py-2 px-2 mt-1 mb-1 rounded-lg',
                style:{
                  background:'rgba(35,185,129,0.10)',
                  border:'1px solid rgba(35,185,129,0.35)',
                },
              },
                React.createElement('div',{className:'text-[10px] uppercase font-bold tracking-wider mb-1',style:{color:'#23B981'}},'⚠ manual position — preview shown'),
                React.createElement('div',{className:'text-[11px] leading-relaxed',style:{color:'rgba(237,237,237,0.85)'}},
                  `Tara sees you marked ENTERED ${userPosition} but doesn't know your real fill price or contract count from Kalshi. The numbers below show what Tara WOULD buy at the current market — not what you actually own.`,
                ),
                React.createElement('div',{className:'text-[10px] mt-1.5 leading-relaxed',style:{color:'rgba(237,237,237,0.65)'}},
                  '→ Scroll down and use the ',
                  React.createElement('span',{style:{color:'#23B981',fontWeight:600}},'Real Kalshi fill'),
                  ' input below the ENTERED button to log your actual fill price + contract count. Stop-loss / take-profit will then track against your real entry.',
                ),
              ));
            }
            // direction
            _rows.push(_renderTip(
              'direction',
              'direction',
              (_taraDir==='UP'?'▲ long up':_taraDir==='DOWN'?'▼ long down':'flat'),
              `Tara called ${_taraDir==='UP'?'long up — she thinks price will be ABOVE the strike at window close':_taraDir==='DOWN'?'long down — she thinks price will be BELOW the strike at window close':'no direction yet'}. ${autoExecSettings?.signalSource==='lock'?'(engine-lock signal)':'(snapshot signal — public 67% WR call)'}`,
              _taraDirColor,
            ));
            // V9.19.18: sit-out warning row — shown BEFORE the position row
            //   when preview says cap would block. Loud + clear so user knows
            //   the next auto-fire will refuse. Hidden once in a live trade
            //   (no preview when actively filled/exiting).
            if(!_liveValid&&_previewSitOutReason){
              _rows.push(React.createElement('div',{
                key:'preview-sit-out',
                className:'py-2 px-2 mt-1 mb-1 rounded-lg',
                style:{
                  background:'rgba(232,69,94,0.08)',
                  border:'1px solid rgba(232,69,94,0.30)',
                },
              },
                React.createElement('div',{className:'text-[10px] uppercase font-bold tracking-wider mb-1',style:{color:'#E8455E'}},'⚠ auto-exec will sit out'),
                React.createElement('div',{className:'text-[11px] leading-relaxed',style:{color:'rgba(237,237,237,0.85)'}},_previewSitOutReason),
                React.createElement('div',{className:'text-[10px] mt-1',style:{color:'rgba(237,237,237,0.55)'}},'fix: raise max-bet, lower contracts/percent, or change sizing mode in settings'),
              ));
            }
            // position: contracts × price OR placed/filled detail
            // V10.2.9 — uses _positionKnown (= _liveValid || _manualValid) so
            //   the row shows REAL fill numbers when user logged them, not preview.
            if((_positionKnown?_liveEntryCents:_entryCents)!=null){
              const _n=_positionKnown?_liveContractsActual:_contracts;
              const _px=_positionKnown?_liveEntryCents:_entryCents;
              const _posLabel=_liveValid?'position':_manualValid?'position (logged)':(_previewWillFire?'will buy':'would buy (blocked)');
              _rows.push(_renderTip(
                'position',
                _posLabel,
                `${_n} contract${_n===1?'':'s'} @ ${_px}¢`,
                _positionKnown
                  ?`You hold ${_n} ${_taraDir==='UP'?'YES':'NO'} contract${_n===1?'':'s'}, each bought at ${_px}¢${_manualValid?' (logged by you)':''}. Each contract pays $1.00 if Tara is right, $0 if wrong. Max possible profit: $${((100-_px)*_n*0.01).toFixed(2)}. Max possible loss: $${(_px*_n*0.01).toFixed(2)}.`
                  :_previewWillFire
                    ?`Tara plans to buy ${_n} ${_taraDir==='UP'?'YES':'NO'} contract${_n===1?'':'s'} at ${_px}¢ each. Each contract pays $1.00 if right. Counts are floored — partial contracts can't be bought, so the actual fill may be ≤ this.`
                    :`The cap-exceeded warning above will block this trade. The numbers shown reflect what would have placed if the cap allowed it.`,
              ));
            }
            // stake/cost — V10.2.7 HONESTY FIX
            //   Old behavior was misleading: when previewing (not yet in a trade),
            //   this row showed `_betSize` which is the BUDGET passed to Kalshi
            //   (intendedDollars from sizing). But Kalshi only buys integer
            //   contracts via floor(budget / cost_per_contract). For $1 budget
            //   at 65¢ → 1 contract for 65¢ actual cost, $0.35 budget leftover.
            //   User reading "stake: $1.00" thought they'd spent $1.00 on Kalshi.
            //
            //   New behavior:
            //     - Live (in a real trade): show actual stake (contracts × fill).
            //       Label stays "stake" because the number IS the realized cost.
            //     - Preview: show "budget $X → cost $Y" where:
            //         budget = _betSize (what the sizing math wants to spend)
            //         cost   = _contracts × _entryCents / 100 (what Kalshi will
            //                  actually charge after the floor)
            //       Label changes to "budget · cost" so the two numbers are
            //       clearly different quantities.
            //   V10.2.9 — _positionKnown extends "live" to include user-logged
            //   manual fills (manualKalshiEntry), so logged manual positions
            //   show real stake instead of budget/cost preview.
            if(_positionKnown){
              _rows.push(_renderTip(
                'stake',
                'stake',
                `$${_liveStakeDollars.toFixed(2)}`,
                `${_manualValid?'You logged':'What you actually paid on Kalshi'}: ${_liveContractsActual} contract${_liveContractsActual===1?'':'s'} × ${_liveEntryCents}¢ = $${_liveStakeDollars.toFixed(2)}. This is your maximum possible loss (price settles against you).`,
              ));
            }else{
              const _predictedCost=_contracts&&_entryCents?(_contracts*_entryCents/100):0;
              const _leftover=_betSize-_predictedCost;
              _rows.push(_renderTip(
                'stake',
                'budget · cost',
                `$${_betSize.toFixed(2)} → $${_predictedCost.toFixed(2)}`,
                `Tara's sizing wants to spend $${_betSize.toFixed(2)} (your budget from Trading Settings → bet size, capped at max-bet). But Kalshi only sells whole contracts, so the actual cost is ${_contracts} × ${_entryCents}¢ = $${_predictedCost.toFixed(2)}. The $${_leftover.toFixed(2)} difference can't buy a partial contract — it stays in your balance. Raise your bet size to buy more contracts (each one needs ${_entryCents}¢ to add).`,
              ));
            }
            // current offer + unrealized P&L (any known position — live or manual)
            if(_positionKnown&&_liveCurOurCents!=null){
              const _color=_liveUnrealCents==null||_liveUnrealCents===0
                ?'#EDEDED'
                :_liveUnrealCents>0?'rgb(35,185,129)':'#E8455E';
              const _arrow=_liveUnrealCents==null?'':_liveUnrealCents>0?'↑':_liveUnrealCents<0?'↓':'→';
              const _pnl=_liveUnrealCents!=null?`  ${_arrow}${_liveUnrealCents>=0?'+':''}${_liveUnrealCents}¢ = ${_liveUnrealDollars>=0?'+':'-'}$${Math.abs(_liveUnrealDollars).toFixed(2)}`:'';
              _rows.push(_renderTip(
                'now',
                'now',
                `${_liveCurOurCents}¢${_pnl}`,
                `Current market price for your side. The arrow + cents shows how far it has moved since you filled. The $ is what you'd pocket (profit) or lose if you sold now. This updates every tick — auto-exec compares it to your target and stop levels.`,
                _color,
              ));
            }
            // target (take profit)
            // V10.2.27 — enhanced display:
            //   was: "85¢  (+$0.09 if hits)"
            //   now: "85¢  →  $1.70 payout  (+$0.85 net)"
            //   payout = contracts × target × 0.01 (what you'd collect from Kalshi)
            //   net    = payout - stake (your actual profit after the contract cost)
            //   This matches how Kalshi displays it ("Payout if Yes: $1.95") so the
            //   numbers reconcile directly with what you see on Kalshi.
            // V10.2.28 — also subtract Kalshi trading fees (entry + exit) from net.
            //   Kalshi takes ~7% × P × (1-P) × C cents per side. For typical
            //   $1-3 stakes the fee is 1-5¢ total; for bigger positions it matters
            //   more. Now displayed as part of the net so the "+$0.85 net" is
            //   what actually lands in your account, not gross.
            // V13.4.292: was `||85`, which meant _tpCents could never be 0, which
            //   meant the `_tpCents>0` guard right below was always true -- this
            //   tooltip row rendered a fabricated "85¢ target" as an active exit
            //   rule even when take-profit was genuinely off. `||0` lets the guard
            //   actually guard.
            const _tpCents=Number(autoExecSettings?.autoExitOffer)||0;
            if(_tpCents>0&&(_positionKnown?_liveEntryCents:_entryCents)!=null){
              const _n=_positionKnown?_liveContractsActual:_contracts;
              const _entry=_positionKnown?_liveEntryCents:_entryCents;
              const _stake=_positionKnown?_liveStakeDollars:(_n*_entry*0.01);
              const _payout=_tpCents*_n*0.01;
              const _fees=computeKalshiTotalFees({contractsN:_n,entryCents:_entry,exitCents:_tpCents,isSettlement:false});
              const _netBeforeFees=_payout-_stake;
              const _net=_netBeforeFees-_fees.totalFeeDollars;
              _rows.push(_renderTip(
                'target',
                'target',
                `${_tpCents}¢  →  $${_payout.toFixed(2)} payout  (${_net>=0?'+':'-'}$${Math.abs(_net).toFixed(2)} net after ${_fees.totalFeeCents}¢ fees)`,
                `Auto-cash-out target. If our side reaches ${_tpCents}¢, Tara sells all ${_n} contract${_n===1?'':'s'} for $${_payout.toFixed(2)} total payout (= ${_n} × ${_tpCents}¢). Stake: $${_stake.toFixed(2)}. Kalshi fees: ${_fees.entryFeeCents}¢ entry + ${_fees.exitFeeCents}¢ exit = ${_fees.totalFeeCents}¢ total. Net after fees: ${_net>=0?'+':'-'}$${Math.abs(_net).toFixed(2)}. Higher target = bigger winners but more trades that never reach it.`,
                'rgb(35,185,129)',
              ));
            }
            // V10.2.33 — _slDelta moved up here to fix TDZ crash.
            //   V10.2.31's safety warning (below) references _slDelta, but the
            //   declaration was DOWN in the stop row at line ~22734. JS const
            //   in same block scope = temporal dead zone error on first access.
            //   Crash text: "ReferenceError: Cannot access '_slDelta' before
            //   initialization". Declaring it up here lets BOTH the warning
            //   AND the stop row use it. The stop row's own `const _slDelta`
            //   was removed since it's now in scope.
            const _slDelta=Number(autoExecSettings?.stopLossDeltaCents)||0;
            // V10.2.31 — STOP-LOSS SAFETY WARNING
            //   stopLossDeltaCents > 30 is a "stop in name only" — at typical
            //   entry prices (50-85¢), a 30+¢ stop means exit at 20-55¢, by
            //   which point the position has lost 35-65% of value. Hunter sets
            //   13¢, Sniper 10¢; anything above ~25¢ is dangerous.
            //   Audit context: in 724 historical trades, median loser closed
            //   ~15¢ below entry. A 75¢ stop never actually triggers in
            //   normal market action — the contract would have to be nearly
            //   worthless before exiting.
            if(_slDelta>=30){
              _rows.push(React.createElement('div',{
                key:'sl-warning',
                className:'pt-2 mt-1 border-t border-rose-500/30 text-[10px]',
                style:{color:'#E8455E',fontWeight:600},
              },
                `⚠ stop-loss delta ${_slDelta}¢ is very wide — `,
                `position can lose ${Math.min(99,Math.round(_slDelta))}¢/contract `,
                `(${_positionKnown?'~$'+(_slDelta*_liveContractsActual/100).toFixed(2)+' total':'most of stake'}) `,
                `before exit fires. A fixed stop-loss measured negative at every level tested (10-40¢) — 13¢ included. `,
                React.createElement('button',{
                  onClick:()=>{
                    if(typeof setAutoExecSettings==='function'){
                      // V13.4.292: this used to offer 13¢ as "the fix" -- itself a
                      //   measured loser, just a narrower one. 0 (off) is the actual
                      //   measured-correct state; the trailing stop replaces it.
                      setAutoExecSettings(prev=>({...prev,stopLossDeltaCents:0}));
                      try{console.info('[V13.4.292] Stop-loss disabled (measured-correct default; trailing stop replaces it)');}catch(_){}
                    }
                  },
                  className:'underline ml-1 cursor-pointer',
                  style:{color:'#E8455E',background:'none',border:'none',padding:0,fontSize:'inherit'},
                },'disable it (recommended)'),
              ));
            }
            // stop (cut loss)
            // V10.2.27 — enhanced display:
            //   was: "45¢  (-$0.13 if hits)"
            //   now: "45¢  →  $0.45 payout  (-$0.13 net loss · max risk)"
            //   Same reconciliation logic as target above — payout = exit value,
            //   net = realized P&L after subtracting stake. For stop, payout is
            //   what you'd salvage; net is the realized loss.
            // V10.2.28 — fees subtracted (entry + exit). On a stop-out, fees
            //   actually make the realized loss SLIGHTLY worse than the cents
            //   delta alone — typically by 1-3¢ on $1-3 stakes.
            // V10.2.33 — _slDelta no longer redeclared here; lifted above
            //   to satisfy TDZ. Uses the outer scope const.
            if(_slDelta>0&&(_positionKnown?_liveEntryCents:_entryCents)!=null){
              const _entry=_positionKnown?_liveEntryCents:_entryCents;
              const _n=_positionKnown?_liveContractsActual:_contracts;
              const _stopAt=Math.max(0,_entry-_slDelta);
              const _stake=_positionKnown?_liveStakeDollars:(_n*_entry*0.01);
              const _payout=_stopAt*_n*0.01;
              const _fees=computeKalshiTotalFees({contractsN:_n,entryCents:_entry,exitCents:_stopAt,isSettlement:false});
              const _netBeforeFees=_payout-_stake;
              const _net=_netBeforeFees-_fees.totalFeeDollars;
              _rows.push(_renderTip(
                'stop',
                'stop',
                `${_stopAt}¢  →  $${_payout.toFixed(2)} payout  (${_net>=0?'+':'-'}$${Math.abs(_net).toFixed(2)} net after ${_fees.totalFeeCents}¢ fees · max risk)`,
                `Auto-stop-loss. If our side drops ${_slDelta}¢ below entry (to ${_stopAt}¢), Tara sells all ${_n} contract${_n===1?'':'s'} for $${_payout.toFixed(2)} salvage value. Stake: $${_stake.toFixed(2)}. Kalshi fees: ${_fees.entryFeeCents}¢ entry + ${_fees.exitFeeCents}¢ exit = ${_fees.totalFeeCents}¢. Net realized loss after fees: ${_net>=0?'+':'-'}$${Math.abs(_net).toFixed(2)}. Most you can lose if the stop fires.`,
                '#E8455E',
              ));
            }else if(_positionKnown===false&&_slDelta===0){
              // No-stop-loss warning in pre-fill state
              _rows.push(React.createElement('div',{key:'nostop',className:'pt-2 mt-1 border-t border-[#24242E]'},
                React.createElement('div',{className:'text-[10px] italic',style:{color:'rgba(237,237,237,0.40)'}},'no stop loss · position rides to settlement or target'),
                React.createElement('button',{
                  onClick:()=>{if(typeof setShowTradingSettings==='function')setShowTradingSettings(true);},
                  className:'mt-1 text-[10px] uppercase font-bold tracking-wider px-2 py-0.5 rounded-lg',
                  style:{color:'#23B981',border:'1px solid rgba(35,185,129,0.30)',background:'rgba(35,185,129,0.05)'},
                },'⚙ enable stop loss'),
              ));
            }
            // V10.2.28 — PARTIAL FILL UI CLARITY
            //   Kalshi sometimes only fills a portion of a limit order (e.g. you
            //   asked for 5 contracts, got 2 at your limit, the other 3 remain
            //   resting). Previously the UI showed "filled" with the partial
            //   count and no indication that more was expected. Now surface this
            //   clearly so the user knows the position is incomplete and any
            //   remaining contracts may still fill.
            if(_liveValid&&autoOrderState&&autoOrderState.requestedCount>0){
              const _filled=readOrderState(autoOrderState).count; /* V13.4.286: was .count, never written */
              const _requested=Number(autoOrderState.requestedCount)||0;
              const _isPartial=_filled>0&&_filled<_requested&&(_liveStatus==='filled'||_liveStatus==='resting'||_liveStatus==='submitted');
              if(_isPartial){
                _rows.push(_renderTip(
                  'partial',
                  '⚠ partial fill',
                  `${_filled} of ${_requested} contracts filled`,
                  `Your order requested ${_requested} contracts but Kalshi only filled ${_filled} at your limit price (${readOrderState(autoOrderState).limitCents}¢). The remaining ${_requested-_filled} contract${_requested-_filled===1?'':'s'} may still fill if price comes back, or expire when the order TTL runs out (90s default). Exit math (target/stop) only applies to the ${_filled} actually filled. If you wanted exposure to all ${_requested}, you can manually buy more on Kalshi at the current offer.`,
                  '#23B981',
                ));
              }
            }
            // exit-when summary (plain English)
            // V10.2.31 — show ALL exit conditions, no truncation. Previously
            //   truncated to 3 with "· ⋯" which hid the others. Multi-line
            //   layout when needed.
            if(_liveValid&&_liveStatus==='filled'){
              const _bits=[];
              if(_tpCents>0)_bits.push(`hits ${_tpCents}¢ (target)`);
              if(_slDelta>0)_bits.push(`drops ${_slDelta}¢ (stop)`);
              const _te=Number(autoExecSettings?.timeExitSecLeft)||0;
              if(_te>0)_bits.push(`${_te}s before window close`);
              _bits.push('window closes (settlement)');
              if(autoExecSettings?.smartExitsEnabled)_bits.push("Tara's read flips (smart-exit)");
              if(_bits.length>0){
                _rows.push(_renderTip(
                  'exits',
                  'exits when',
                  _bits.join(' · '),
                  `Tara will automatically close this position as soon as ANY of these conditions hit (whichever comes first):\n\n• ${_bits.join('\n• ')}\n\nIf none of them trigger before window close, the position settles at the strike: YES pays $1 if BTC ended above strike, $0 if below.`,
                  '#23B981',
                ));
              }
            }
            // exiting status banner (when in the middle of exiting)
            if(_liveStatus==='exiting'&&autoOrderState?.exitReason){
              _rows.push(React.createElement('div',{key:'exiting',className:'pt-2 mt-1 border-t border-[#24242E] text-[10px]',style:{color:'rgba(212,162,76,0.95)'}},
                `exiting · ${autoOrderState.exitReason}`,
              ));
            }
            // collapsible engineering monitoring
            if(_liveValid&&_liveStatus==='filled'&&autoOrderState){
              const _aos=autoOrderState;
              // V13.4.291: fillPrice / limitCents / placedAt are all names the
              //   writer never set, so _fp was always null and this read "—" even on
              //   a filled position; the 15s staleness fallback could never fire
              //   because placedAt fell back to Date.now(), making the elapsed time
              //   0 forever. placedAt is now stamped at the placing write.
              const _oM=readOrderState(_aos);
              const _fp=_oM.fillCents!=null
                ?_oM.fillCents
                :((Date.now()-(Number(_aos.placedAt)||Date.now())>15000&&_oM.limitCents!=null)?_oM.limitCents:null);
              const _fpLabel=_fp==null?'—':`${_fp}¢${_oM.fillCents==null?' [est]':''}`;
              const _cur=_liveCurOurCents;
              const _curLabel=_cur==null?'—':`${_cur}¢`;
              const _tp=_tpCents;
              const _tpFires=_cur!=null&&_cur>=_tp;
              const _sl=_slDelta;
              const _slDrop=_fp!=null&&_cur!=null?(_fp-_cur):null;
              const _slFires=_sl>0&&_slDrop!=null&&_slDrop>=_sl;
              const _msLeft=(timeState?.minsRemaining||0)*60000+(timeState?.secsRemaining||0)*1000;
              const _te=Number(autoExecSettings?.timeExitSecLeft)||0;
              const _teFires=_te>0&&_msLeft>0&&_msLeft<=_te*1000;
              const _smartOn=!!autoExecSettings?.smartExitsEnabled;
              const _smartDir=scalperRead?.dir||taraCall?.direction||taraCall?.call||null;
              const _smartConv=scalperRead?.dir?Number(scalperRead.conviction)||0:Number(taraCall?.confidence)||0;
              const _smartSrc=scalperRead?.dir?'tape':'tara';
              _rows.push(React.createElement('div',{key:'mon-toggle',className:'pt-2 mt-1 border-t border-[#24242E]'},
                React.createElement('button',{
                  type:'button',
                  onClick:()=>setShowMonitoring(v=>!v),
                  className:'text-[10px] uppercase font-bold tracking-wider text-[#EDEDED]/40 hover:text-[#EDEDED]/70 transition-colors',
                  style:{background:'none',border:'none',padding:0,cursor:'pointer'},
                },showMonitoring?'▾ hide monitoring':'▸ show monitoring'),
                showMonitoring&&React.createElement('div',{className:'mt-2 text-[10px] tabular-nums leading-relaxed',style:{fontFamily:'IBM Plex Mono,ui-monospace,monospace',color:'rgba(237,237,237,0.65)'}},
                  React.createElement('div',null,`fill ${_fpLabel} · cur ${_curLabel}`),
                  React.createElement('div',{style:{color:_tpFires?'rgb(35,185,129)':'rgba(237,237,237,0.55)'}},
                    `TP @${_tp}¢ → ${_tpFires?'FIRES':`needs ≥${_tp}¢`}`,
                  ),
                  _sl>0&&React.createElement('div',{style:{color:_slFires?'#E8455E':'rgba(237,237,237,0.55)'}},
                    `SL @-${_sl}¢ → drop ${_slDrop==null?'—':_slDrop+'¢'} · ${_slFires?'FIRES':`needs ≥${_sl}¢drop`}`,
                  ),
                  _sl===0&&React.createElement('div',{style:{color:'rgba(237,237,237,0.40)'}},'SL OFF (set stop-loss delta in settings)'),
                  _te>0&&React.createElement('div',{style:{color:_teFires?'#23B981':'rgba(237,237,237,0.55)'}},
                    `time-exit @${_te}s → ${Math.round(_msLeft/1000)}s left · ${_teFires?'FIRES':'OK'}`,
                  ),
                  _smartOn&&React.createElement('div',{style:{color:'rgba(237,237,237,0.55)'}},
                    `smart-exit · ${_smartSrc}=${_smartDir||'(none)'} conv=${_smartConv}% · needs ≥${autoExecSettings.smartExitReverseConviction||70}%+profit≥${autoExecSettings.smartExitMinProfitCents||5}¢`,
                  ),
                ),
              ));
            }
            return _rows;
          })(),
        ],
      ),
      // V9.17.18: MANUAL "place order on Tara's call" button.
      //   Shows when (a) auto-exec is enabled, (b) Tara has a direction, (c) no
      //   active order from auto-exec. Useful when Tara hard-commits via
      //   time-cap-commit (or timer-commit, no-go paths) but the engine-lock
      //   path didn't fire — auto-exec sits out and the user wants to take it.
      //   The callback synthesizes a lock with bypass flag, so soft filters
      //   (tier, quality, conviction) are skipped. Hard rails still apply.
      (()=>{
        if(!onPlaceOrderOnTaraCall)return null;
        if(!autoExecSettings?.enabled)return null;
        if(_taraDir!=='UP'&&_taraDir!=='DOWN')return null;
        // Skip if order is already active/working
        const _s=autoOrderState?.status;
        const _orderActive=_s==='placing'||_s==='submitted'||_s==='resting'||_s==='partially_filled'||_s==='filled'||_s==='exiting'||_s==='patient-waiting';
        if(_orderActive)return null;
        // Skip if exited (trade already completed this window — let it stay)
        if(_s==='exited')return null;
        const _btnBg=_taraDir==='UP'?'rgba(35,185,129,0.12)':'rgba(232,69,94,0.12)';
        const _btnBorder=_taraDir==='UP'?'rgba(35,185,129,0.45)':'rgba(232,69,94,0.45)';
        const _btnColor=_taraDir==='UP'?'rgb(35,185,129)':'#E8455E';
        const _tier=_snapTier||taraCall?.tier||'';
        const _isLateTier=_tier==='time-cap-commit'||_tier==='timer-commit'||_tier==='no-go-edge'||_tier==='no-go-data'||_tier==='single';
        return React.createElement('div',{className:'pt-3 mt-3 border-t border-[#24242E]'},
          // V9.17.24: KILL SWITCH BANNER. Surfaces above the place-order button
          //   when engaged. V9.17.26 removed auto-engagement; kill switch is
          //   now ONLY engaged by the user (via this banner's Clear button or
          //   the kill button on the live trade pill). Visible here so the
          //   user can clear it without leaving the trade ticket.
          killSwitchEngaged&&React.createElement('div',{
            className:'mb-3 px-3 py-2 rounded-lg',
            style:{background:'rgba(232,69,94,0.10)',border:'1px solid rgba(232,69,94,0.40)'},
          },
            React.createElement('div',{className:'flex items-baseline justify-between gap-2'},
              React.createElement('div',{className:'flex-1 min-w-0'},
                React.createElement('div',{className:'text-[11px] font-bold uppercase tracking-wider',style:{color:'#E8455E'}},'⛔ Kill switch engaged'),
                React.createElement('div',{className:'text-[10px] text-[#EDEDED]/70 mt-0.5'},'Auto-exec & manual-button are blocked until cleared.'),
              ),
              typeof onClearKillSwitch==='function'&&React.createElement('button',{
                onClick:onClearKillSwitch,
                className:'px-2.5 py-1 rounded-lg text-[10px] uppercase font-bold tracking-wider shrink-0',
                style:{color:'#E8455E',border:'1px solid rgba(232,69,94,0.50)',background:'rgba(232,69,94,0.08)'},
                title:'Clear kill switch and allow auto-exec to fire again',
              },'Clear'),
            ),
          ),
          // V9.18.2: TRADE TICKET EDIT BUTTON / PANEL
          //   Lets the user override stake, entry price (cents), cash-out
          //   target, and stop-loss delta for this single window. Reverts on
          //   window roll. Hidden once the trade is filled — values are no
          //   longer editable after fill since they'd change the position
          //   sizing math retroactively.
          !_liveValid&&!editMode&&_taraDir&&React.createElement('button',{
            key:'edit-btn',
            onClick:_onEnterEditMode,
            className:'w-full mb-2 py-1.5 rounded-lg text-[10px] font-medium',
            style:{
              background:'rgba(35,185,129,0.05)',
              border:'1px solid rgba(35,185,129,0.25)',
              color:'#23B981',
              letterSpacing:'0.02em',
            },
          },'✏ edit values for this window'),
          !_liveValid&&editMode&&_taraDir&&React.createElement('div',{
            key:'edit-panel',
            className:'mb-2 p-3 rounded-lg',
            style:{
              background:'rgba(35,185,129,0.04)',
              border:'1px solid rgba(35,185,129,0.30)',
            },
          },[
            React.createElement('div',{key:'h',className:'flex items-baseline justify-between mb-2'},
              React.createElement('span',{className:'text-[10px] font-bold uppercase tracking-wider',style:{color:'#23B981'}},'edit values · this window only'),
              React.createElement('span',{className:'text-[9px] italic',style:{color:'rgba(35,185,129,0.55)'}},'resets when window rolls'),
            ),
            // Stake row
            React.createElement('div',{key:'r1',className:'flex items-center justify-between py-1.5 gap-2'},
              React.createElement('span',{className:'text-[11px] text-[#EDEDED]/70'},'stake ($)'),
              React.createElement('input',{
                type:'number',min:'0.5',max:'500',step:'0.5',value:editStake,
                onChange:(e)=>setEditStake(e.target.value),
                className:'flex-1 max-w-[120px] px-2 py-1 rounded-lg text-[12px] tabular-nums text-right',
                style:{background:'#050508',border:'1px solid rgba(237,237,237,0.15)',color:'#EDEDED',fontFamily:'IBM Plex Mono,ui-monospace,monospace'},
                placeholder:'e.g. 2',
              }),
            ),
            // Entry price row
            React.createElement('div',{key:'r2',className:'flex items-center justify-between py-1.5 gap-2'},
              React.createElement('div',{className:'flex flex-col'},
                React.createElement('span',{className:'text-[11px] text-[#EDEDED]/70'},'enter at (¢)'),
                React.createElement('span',{className:'text-[9px] italic',style:{color:'rgba(232,69,94,0.65)'}},'leave blank for tara\'s default · low values may not fill'),
              ),
              React.createElement('input',{
                type:'number',min:'1',max:'99',step:'1',value:editEntry,
                onChange:(e)=>setEditEntry(e.target.value),
                className:'flex-1 max-w-[120px] px-2 py-1 rounded-lg text-[12px] tabular-nums text-right',
                style:{background:'#050508',border:'1px solid rgba(237,237,237,0.15)',color:'#EDEDED',fontFamily:'IBM Plex Mono,ui-monospace,monospace'},
                placeholder:_entryCents!=null?String(_entryCents):'auto',
              }),
            ),
            // Cash out row
            React.createElement('div',{key:'r3',className:'flex flex-col py-1.5 gap-1'},
              React.createElement('div',{className:'flex items-center justify-between gap-2'},
                React.createElement('span',{className:'text-[11px] text-[#EDEDED]/70'},'cash out at (¢)'),
                React.createElement('input',{
                  type:'number',min:'0',max:'99',step:'1',value:editCashOut,
                  onChange:(e)=>setEditCashOut(e.target.value),
                  className:'flex-1 max-w-[120px] px-2 py-1 rounded-lg text-[12px] tabular-nums text-right',
                  style:{background:'#050508',border:'1px solid rgba(237,237,237,0.15)',color:'#EDEDED',fontFamily:'IBM Plex Mono,ui-monospace,monospace'},
                  placeholder:'0 = off',
                }),
              ),
              // V13.4.292: fixed take-profit measured negative at 8/9 levels tested,
              //   and any value here at or under 90c fires before the trailing stop
              //   (this file's only measured-positive exit rule) can arm. 0 = off is
              //   the recommended state, matching the stop-loss row below.
              React.createElement('span',{className:'text-[9px] italic',style:{color:'rgba(237,237,237,0.45)'}},'0 = disabled (recommended) · under 90 disables the trailing stop'),
            ),
            // Cut loss row
            React.createElement('div',{key:'r4',className:'flex items-center justify-between py-1.5 gap-2'},
              React.createElement('div',{className:'flex flex-col'},
                React.createElement('span',{className:'text-[11px] text-[#EDEDED]/70'},'stop-loss delta (¢)'),
                React.createElement('span',{className:'text-[9px] italic',style:{color:'rgba(237,237,237,0.45)'}},'0 = disabled · 30 = exit if down 30¢ from fill'),
              ),
              React.createElement('input',{
                type:'number',min:'0',max:'90',step:'1',value:editCutDelta,
                onChange:(e)=>setEditCutDelta(e.target.value),
                className:'flex-1 max-w-[120px] px-2 py-1 rounded-lg text-[12px] tabular-nums text-right',
                style:{background:'#050508',border:'1px solid rgba(237,237,237,0.15)',color:'#EDEDED',fontFamily:'IBM Plex Mono,ui-monospace,monospace'},
                placeholder:'e.g. 30',
              }),
            ),
            // Action row
            React.createElement('div',{key:'actions',className:'flex gap-2 mt-2'},
              React.createElement('button',{
                onClick:_onSaveEdit,
                className:'flex-1 py-1.5 rounded-lg text-[10px] font-bold',
                style:{background:'rgba(35,185,129,0.10)',border:'1px solid rgba(35,185,129,0.45)',color:'rgb(35,185,129)',letterSpacing:'0.04em'},
              },'✓ save'),
              React.createElement('button',{
                onClick:_onCancelEdit,
                className:'flex-1 py-1.5 rounded-lg text-[10px] font-bold',
                style:{background:'rgba(237,237,237,0.04)',border:'1px solid rgba(237,237,237,0.20)',color:'rgba(237,237,237,0.70)',letterSpacing:'0.04em'},
              },'✕ cancel'),
            ),
          ]),
          React.createElement('button',{
            onClick:()=>{
              try{console.info('[V9.17.21] Place-order button onClick fired (button-level)');}catch(_){}
              if(typeof onPlaceOrderOnTaraCall==='function')onPlaceOrderOnTaraCall();
              else try{console.error('[V9.17.21] onPlaceOrderOnTaraCall is not a function:',typeof onPlaceOrderOnTaraCall);}catch(_){}
            },
            className:'w-full py-2.5 rounded-lg text-[12px] font-bold',
            style:{
              background:_btnBg,color:_btnColor,
              border:`1px solid ${_btnBorder}`,
              letterSpacing:'0.02em',textTransform:'none',
              opacity:killSwitchEngaged?0.4:1,
              cursor:killSwitchEngaged?'not-allowed':'pointer',
            },
            disabled:!!killSwitchEngaged,
          },
            // V9.17.27: when autoOrderState is stuck in 'error', the button
            //   will silently block (autoOrderState gate). Direct the user to
            //   the Clear button in the error banner instead of letting them
            //   keep clicking. Button text changes to make the action obvious.
            autoOrderState&&autoOrderState.status==='error'
              ?'⚠ clear error above to retry'
              :`place order · ${_taraDirLabel} · $${_betSize.toFixed(2)}`
          ),
          // V9.17.21: visible feedback under button. Shows immediate confirmation
          //   of click receipt, plus the gate outcome (placed / blocked / which gate).
          //   Renders for 5s after the click, then disappears.
          (()=>{
            if(!manualOrderFeedback)return null;
            const _age=Date.now()-(manualOrderFeedback.at||0);
            if(_age>5000)return null;
            const _palette={
              emerald:{bg:'rgba(35,185,129,0.10)',br:'rgba(35,185,129,0.40)',co:'rgb(35,185,129)'},
              amber:{bg:'rgba(35,185,129,0.10)',br:'rgba(35,185,129,0.40)',co:'#23B981'},
              rose:{bg:'rgba(232,69,94,0.10)',br:'rgba(232,69,94,0.40)',co:'#E8455E'},
            }[manualOrderFeedback.color||'amber'];
            return React.createElement('div',{
              className:'mt-2 px-2 py-1.5 rounded-lg text-[10px] font-bold text-center',
              style:{background:_palette.bg,border:`1px solid ${_palette.br}`,color:_palette.co},
            },manualOrderFeedback.msg);
          })(),
          _isLateTier?React.createElement('div',{className:'text-[10px] text-[#EDEDED]/45 mt-1.5 text-center italic'},
            // V13.4.255: was "auto-exec may sit out". It does not sit out any more.
            `Tara on ${_tier} · click to take her call manually`,
          ):React.createElement('div',{className:'text-[10px] text-[#EDEDED]/40 mt-1.5 text-center italic'},
            'override soft filters — fire on tara\'s direction',
          ),
        );
      })(),
      // Scalper status mini-note at the bottom (off/paused/watching)
      _scalperNote,
    );
  }

  // ── STATE: BLOCKED ─────────────────────────────────────────────────────
  if(_state==='BLOCKED'){
    return React.createElement('div',{
      // V13.4.314: was its own bordered/backgrounded card shell -- now nests
      //   inside ThisTradeCard's own border (see the "literally combine" note
      //   at ThisTradeCard's closing tag), so it takes a plain top hairline
      //   instead of a second card outline.
      className:'pt-3 mt-3 border-t border-[#16161c]',
    },
      _renderPredictorHeader(),
      _topEl,
      React.createElement('div',{className:'flex items-baseline justify-between mb-2'},
        React.createElement('span',{className:'text-[10px] text-[#EDEDED]/45 font-bold',style:{letterSpacing:'0.02em',textTransform:'none'}},'paused'),
        React.createElement('button',{
          onClick:()=>setShowTradingSettings(true),
          className:'text-[10px] text-[#EDEDED]/45 hover:text-[#EDEDED]/70 underline underline-offset-2',
        },'settings'),
      ),
      React.createElement('p',{className:'text-[12px] text-[#EDEDED]/65 leading-relaxed'},_elig.blockReason),
    );
  }

  // ── STATE: DISABLED ────────────────────────────────────────────────────
  if(_state==='DISABLED'){
    return React.createElement('div',{
      // V13.4.314: was its own bordered/backgrounded card shell -- now nests
      //   inside ThisTradeCard's own border (see the "literally combine" note
      //   at ThisTradeCard's closing tag), so it takes a plain top hairline
      //   instead of a second card outline.
      className:'pt-3 mt-3 border-t border-[#16161c]',
    },
      _renderPredictorHeader(),
      _topEl,
      React.createElement('div',{className:'flex items-baseline justify-between mb-2'},
        React.createElement('span',{className:'text-[10px] text-[#EDEDED]/45 font-bold',style:{letterSpacing:'0.02em',textTransform:'none'}},'scalper off'),
        React.createElement('button',{
          onClick:()=>setShowTradingSettings(true),
          className:'text-[10px] underline underline-offset-2',
          style:{color:'#23B981',letterSpacing:'0.02em',textTransform:'none'},
        },'enable scalper'),
      ),
      React.createElement('p',{className:'text-[12px] text-[#EDEDED]/60 leading-relaxed'},
        _reason||'Engine reads tape, flow, and book pressure for short-horizon entries.',
      ),
    );
  }

  // ── STATE: IDLE ────────────────────────────────────────────────────────
  return React.createElement('div',{
    // V13.4.314: nests inside ThisTradeCard's own border now -- plain top
    //   hairline instead of a second card outline (see the "literally
    //   combine" note at ThisTradeCard's closing tag).
    className:'pt-3 mt-3 border-t border-[#16161c]',
  },
    _renderPredictorHeader(),
    _topEl,
    React.createElement('div',{className:'flex items-baseline justify-between mb-2'},
      React.createElement('span',{className:'text-[10px] text-[#EDEDED]/45 font-bold',style:{letterSpacing:'0.02em',textTransform:'none'}},'watching'),
      React.createElement('span',{className:'text-[10px] tabular-nums text-[#EDEDED]/40',style:{fontFamily:'IBM Plex Mono,ui-monospace,monospace'}},
        `${(scalperSettings.maxSuggestionsPerHour||3)-_scalperGetHourlyCount(scalperLog,scalperSuggestionsRef)}/${scalperSettings.maxSuggestionsPerHour||3} left this hour`),
    ),
    React.createElement('p',{className:'text-[12px] text-[#EDEDED]/60 leading-relaxed'},
      _reason||'Waiting for tape acceleration + flow alignment to converge.',
    ),
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// V9.17.4: KALSHI BALANCE PILL — compact balance + today's delta display
// ═══════════════════════════════════════════════════════════════════════════
// Renders in header next to other status pills. Shows current balance and
// today's delta from UTC midnight baseline. Replaces the V9.17.3-removed
// local P&L counter with real Kalshi-source data.
const KalshiBalancePill=React.memo(function KalshiBalancePill({kalshiBalance}){
  if(!kalshiBalance||kalshiBalance.status==='no-creds')return null;
  const _bal=kalshiBalance.balance;
  const _base=kalshiBalance.baseline;
  if(typeof _bal!=='number')return null;
  const _delta=typeof _base==='number'?(_bal-_base):0;
  const _deltaColor=_delta>0.01?'#23B981':_delta<-0.01?'#E8455E':'rgba(237,237,237,0.55)';
  const _isStale=kalshiBalance.lastFetchAt&&(Date.now()-kalshiBalance.lastFetchAt>120000); // 2min stale threshold
  return React.createElement('span',{
    className:'inline-flex items-baseline gap-2 text-[10px] uppercase tracking-[0.10em] px-3 py-1.5 rounded-lg',
    style:{
      background:'rgba(0,0,0,0.30)',
      border:'1px solid #24242E',
      boxShadow:'0 3px 10px rgba(0,0,0,0.3)',
      fontFamily:'IBM Plex Mono,ui-monospace,monospace',
      letterSpacing:'0.02em',
      textTransform:'none',
      opacity:_isStale?0.55:1,
    },
    title:kalshiBalance.status==='error'?`Kalshi balance error: ${kalshiBalance.error||'unknown'}`:`Kalshi balance · last updated ${kalshiBalance.lastFetchAt?new Date(kalshiBalance.lastFetchAt).toLocaleTimeString():'never'}`,
  },
    React.createElement('span',{className:'text-[#EDEDED]/45'},'kalshi'),
    React.createElement('span',{className:'tabular-nums font-semibold text-[13px] text-[#EDEDED]/95'},`$${_bal.toFixed(2)}`),
    React.createElement('span',{className:'tabular-nums',style:{color:_deltaColor}},
      `${_delta>=0?'+':''}$${_delta.toFixed(2)}`,
    ),
  );
})

// V9.17.7: RoundTicketCard removed (was dead code). Trade ticket info now
//   renders inline inside ScalperAdvisorPanel when Tara is locked (V9.17.6).


function TaraApp(){
  const[isMounted,setIsMounted]=useState(false);
  const[showStats,setShowStats]=useState(false); // V2.7: full stats analytics modal
  const[showSports,setShowSports]=useState(false); // v13.4.149: sports prediction record
  const[showWeather,setShowWeather]=useState(false); // V13.4.217: weather lane — a market whose settlement number cannot be pushed around
  const[workspaceFocus,setWorkspaceFocus]=useState('overview'); // V13.4.353: real workspace navigation, not a row of dead scroll buttons
  const[expandedOverview,setExpandedOverview]=useState(false); // The full board stays available, but the default has one clear job.
  useEffect(()=>{if(workspaceFocus!=='overview')setExpandedOverview(false);},[workspaceFocus]);
  // V13.4.246: lifted out of WeatherView so its picks keep scanning and
  //   settling on the app's own 5-minute sweep even when the Weather tab is
  //   closed. It used to live inside WeatherView, whose state React destroys
  //   on unmount — same treatment the BTC tree already gets and Weather never
  //   had, which is why the record only ever advanced while someone happened
  //   to be looking at it.
  const weatherPicks=useWeatherPicks();
  const[showBrain,setShowBrain]=useState(false); // V3.1.12: Tara's Brain — synthesized reasoning view
  const[syncState,setSyncState]=useState({active:false,stage:'',progress:0,complete:false,error:null}); // V134: sync progress overlay
  const[baselineDrift,setBaselineDrift]=useState(()=>{
    try{
      const v=localStorage.getItem('taraBaselineVersion');
      return v&&v!==BASELINE_VERSION;
    }catch(e){return false;}
  });
  const[showWhaleAlerts,setShowWhaleAlerts]=useState(true);
  const[showRugPullAlerts,setShowRugPullAlerts]=useState(true);
  const[showSettings,setShowSettings]=useState(false);
  // V7.0: Per-asset Discord webhooks. Each asset can broadcast to its own channel.
  //   Empty webhook = no broadcast for that asset (silently skipped).
  //   Migration: V6.5.8's single `taraV110Hook` is loaded into BTC's slot on first run.
  //   V7.1: BTC + ETH only. Existing SOL/DOGE webhook values are dropped on hydrate.
  const[discordWebhooks,setDiscordWebhooks]=useState({BTC:''});
  // Legacy alias kept so existing UI bindings work — reads BTC's webhook.
  const discordWebhook=discordWebhooks.BTC||'';
  const setDiscordWebhook=(v)=>setDiscordWebhooks(prev=>({...prev,BTC:v}));
  const discordWebhooksRef=useRef(discordWebhooks);
  discordWebhooksRef.current=discordWebhooks;
  const[discordUsername,setDiscordUsername]=useState('Tara Terminal V110');
  const[discordAvatar,setDiscordAvatar]=useState('');
  const[windowRecap,setWindowRecap]=useState(null); // {won,dir,closePrice,strike,gapBps,regime} — clears after 6s
  // V7.10.5: lifetimePnL now also cloud-synced. Tracks {value, updatedAt} so cross-device
  //   merge can pick the most recently-updated value (last-write-wins by timestamp). PnL
  //   isn't monotonic — losses decrease it — so max-merge would silently drop loss data.
  //   Latest-update wins is the correct semantics for "running PnL counter."
  // V8.6: Switched to RMW. Old logic used local clock — but with simple debounced write,
  //   two devices could race and the loser's update would be lost forever (the RMW transaction
  //   serializes atomically, so the loser sees the winner's update and merges correctly).
  const[lifetimePnL,setLifetimePnL]=useState(()=>{try{return parseFloat(localStorage.getItem('taraV110PnL')||'0');}catch(e){return 0;}});
  const _pnlUpdatedAtRef=useRef((()=>{try{return parseInt(localStorage.getItem('taraV110PnL_updatedAt')||'0',10)||0;}catch(e){return 0;}})());
  const _pnlHydratedRef=useRef(false);
  const _pnlRef=useRef(lifetimePnL);
  _pnlRef.current=lifetimePnL;
  useEffect(()=>{
    if(_pnlHydratedRef.current){
      // Bump local timestamp BEFORE write fires — so RMW merge sees fresh local time
      _pnlUpdatedAtRef.current=Date.now();
      try{localStorage.setItem('taraV110PnL',String(lifetimePnL));localStorage.setItem('taraV110PnL_updatedAt',String(_pnlUpdatedAtRef.current));}catch(e){}
      cloudWriteDebouncedRMW(
        'state/lifetimePnL',
        ()=>({value:_pnlRef.current,updatedAt:_pnlUpdatedAtRef.current}),
        (cloudData,localData)=>{
          const cT=Number(cloudData?.updatedAt)||0;
          const lT=Number(localData.updatedAt)||0;
          // If cloud is newer than local, adopt cloud — skip the write (we'll re-render from cloudWatch)
          if(cT>lT+1000)return null;
          // Skip write if value + timestamp identical
          if(cloudData&&Number(cloudData.value)===Number(localData.value)&&cT===lT)return null;
          return{value:localData.value,updatedAt:localData.updatedAt};
        },
        // V10.7.85: throttle to 30s
        30000,
      );
      // V10.1.0 Session 2 — parallel Supabase write is now handled internally by
      //   cloudWriteDebouncedRMW (see helper definition). The explicit
      //   cloudSupabaseWriteDebouncedRMW call previously here was the Session 1
      //   proof-of-life pattern; removed now that the helper-level approach covers
      //   every path uniformly.
    }
  },[lifetimePnL]);
  useEffect(()=>{
    const unsub=cloudWatch('state/lifetimePnL',(d)=>{
      _pnlHydratedRef.current=true;
      if(!d||typeof d!=='object'||typeof d.value!=='number')return;
      const _cloudT=Number(d.updatedAt)||0;
      const _localT=_pnlUpdatedAtRef.current||0;
      // Adopt cloud only if cloud's update is more recent than local's. 1s tolerance.
      if(_cloudT>_localT+1000){
        _pnlUpdatedAtRef.current=_cloudT;
        try{
          localStorage.setItem('taraV110PnL',String(d.value));
          localStorage.setItem('taraV110PnL_updatedAt',String(_cloudT));
        }catch(e){}
        setLifetimePnL(d.value);
      }
    });
    return unsub;
  },[]);
  // MTF: track last known lock for each timeframe so we can detect confluence
  const mtfLocksRef=useRef({'5m':null,'15m':null}); // {dir,lockedAt,posterior};

  const[kalshiYesPrice,setKalshiYesPrice]=useState(null); // live YES price from active Kalshi market
  // V13.4.239: ref mirror so the post-lock tracker can sample the CONTRACT price
  //   without taking kalshiYesPrice as an effect dependency (it ticks constantly;
  //   adding it to deps would re-run the cushion effect on every quote).
  const _kYesRef=useRef(null); _kYesRef.current=kalshiYesPrice;
  // V10.2.11 — KALSHI 30s HISTORY RING
  //   Phase 4's timing engine needs a 30s-ago Kalshi YES price to compute
  //   "kalshi velocity" — is the market moving toward Tara's call or away?
  //   This was a TODO at L30370 since V10.0.0; without it Phase 4 always saw
  //   kalshiYes30sAgo=null and the velocity factor was always 0/neutral.
  //
  //   Ring shape: array of {ts: ms, yes: 0-99}, capped at 25 entries (one
  //   every ~5s for ~2 minutes of history). Captures on every kalshiYesPrice
  //   change OR every 5s minimum via heartbeat. Pruned to keep last 90s only.
  //
  //   Lookup: _kalshiYesAtAgo(secsAgo) returns the closest entry to N seconds
  //   ago, or null if no entry within ±5s of target.
  const _kalshiYesRingRef=useRef([]);
  // Capture on every kalshiYesPrice change. Lightweight — just one push + prune.
  useEffect(()=>{
    const _y=Number(kalshiYesPrice);
    if(!Number.isFinite(_y)||_y<=0||_y>=100)return;
    const _ring=_kalshiYesRingRef.current;
    const _now=Date.now();
    // De-dup: don't push if the last entry is < 1s old AND the same price (avoid spam)
    const _last=_ring[_ring.length-1];
    if(_last&&_now-_last.ts<1000&&_last.yes===_y)return;
    _ring.push({ts:_now,yes:_y});
    // Prune to keep last 90s only (Phase 4 reads 30s, give buffer)
    const _cutoff=_now-90000;
    while(_ring.length>0&&_ring[0].ts<_cutoff)_ring.shift();
    // Hard cap of 60 entries (defensive against price-change spam)
    if(_ring.length>60)_ring.splice(0,_ring.length-60);
  },[kalshiYesPrice]);
  // Lookup helper — returns Kalshi YES price ~N seconds ago, or null if no
  //   data within ±5s of target. Uses linear search; ring is small (<60).
  const _kalshiYesAtAgo=React.useCallback((secsAgo)=>{
    const _ring=_kalshiYesRingRef.current;
    if(!_ring||_ring.length===0)return null;
    const _target=Date.now()-(secsAgo*1000);
    const _tolMs=5000;
    let _best=null;
    let _bestDist=Infinity;
    for(const e of _ring){
      const d=Math.abs(e.ts-_target);
      if(d<_bestDist&&d<=_tolMs){
        _best=e;_bestDist=d;
      }
    }
    return _best?_best.yes:null;
  },[]);
  // V10.2.11 — console hook to inspect the 30s history ring. Useful for
  //   verifying Phase 4 has data, debugging stale-ring issues, or just
  //   sanity-checking the velocity input.
  useEffect(()=>{
    if(typeof window==='undefined')return;
    window.__taraKalshiRing=()=>{
      const _ring=_kalshiYesRingRef.current||[];
      const _now=Date.now();
      const _summary=_ring.map(e=>({
        ts:new Date(e.ts).toLocaleTimeString(),
        ageSec:Math.floor((_now-e.ts)/1000),
        yes:e.yes,
      }));
      console.group('%c━━━ Kalshi 30s history ring ━━━','color:#23B981;font-weight:bold;font-size:13px');
      console.info(`Entries: ${_ring.length} (capped at 60, pruned >90s)`);
      if(_ring.length>0){
        const _span=Math.floor((_ring[_ring.length-1].ts-_ring[0].ts)/1000);
        console.info(`Span: ${_span}s from oldest to newest`);
      }
      console.info(`Current Phase 4 input: kalshiYes30sAgo = ${_kalshiYesAtAgo(30)} (${_kalshiYesAtAgo(30)===null?'NO DATA — ring too cold':'OK'})`);
      console.table(_summary);
      console.groupEnd();
      return{ring:_ring,kalshiYes30sAgo:_kalshiYesAtAgo(30),kalshiYes60sAgo:_kalshiYesAtAgo(60)};
    };
  },[_kalshiYesAtAgo]);
  // V8.9.0: Kalshi YES price history for smart-money divergence detector. Captures
  //   each non-null Kalshi update with timestamp, kept to last 60s. Used to detect
  //   when Kalshi moves >5¢ in 30s while spot stays flat (positioning play / squeeze).
  const kalshiHistoryRef=useRef([]);
  // V9.7.9: telemetry slot updated by taraCall IIFE on each tick. Holds the urgency
  //   state and Kalshi velocity at the moment of the most recent computation. Read
  //   by the lifecycle effect at lock time so the trade log entry can capture
  //   "did urgency fire on this lock?" / "what was Kalshi velocity at lock?".
  const _v97DiagRef=useRef({urgencyApplied:false,urgencyMult:1.0,kalshiVelocity30s:null,timestamp:0});
  useEffect(()=>{
    if(kalshiYesPrice==null||!Number.isFinite(kalshiYesPrice))return;
    const now=Date.now();
    kalshiHistoryRef.current.push({yes:kalshiYesPrice,time:now});
    // Keep last 60s only
    kalshiHistoryRef.current=kalshiHistoryRef.current.filter(k=>now-k.time<60000);
  },[kalshiYesPrice]);
  // V3.0: Kalshi-published strike for the active window. When available, this is the strike
  //       Tara will suggest at window open instead of using the live BTC price as the strike.
  //       This eliminates the strike-misalignment problem where Tara's strike differed by a
  //       few bps from Kalshi's strike (because they use CF Benchmarks, we use Coinbase).
  const[kalshiStrike,setKalshiStrike]=useState(null);
  // V3.0: Mirror to ref so the window-rollover handler can read the latest Kalshi strike
  //       at the exact moment a new window opens, without depending on render cycles.
  const kalshiStrikeRef=useRef(null);
  // V6.3.1: Fetch-on-demand ref. The Kalshi poll effect assigns its fetchKalshi function
  //   here so other code (window rollover + strike retry effect) can trigger immediate
  //   refetches without waiting for the 30s interval cycle.
  const triggerKalshiFetchRef=useRef(null);
  useEffect(()=>{kalshiStrikeRef.current=kalshiStrike;},[kalshiStrike]);
  // V3.2.1: Cache last-successful Kalshi response across 503 outages. When Kalshi's API
  //   returns HTTP 503 (Service Unavailable), instead of falling back to live spot, we
  //   reuse the previous response if it's recent enough (≤90s old). 503s are usually
  //   transient — rate limiting or maintenance — and the strike doesn't change second-to-second.
  //   When the cache is older than 90s we let it expire so we don't show truly stale data.
  const lastKalshiSuccessRef=useRef({BTC:{at:0,strike:null,yesPrice:null,activeMarket:null,debug:null}});
  // V3.0: The actual market ticker so we can re-find the same market at settlement time.
  const[kalshiActiveMarket,setKalshiActiveMarket]=useState(null);
  // V3.1.11: surface Kalshi extraction status in UI so user can diagnose without DevTools
  const[kalshiDebug,setKalshiDebug]=useState({ok:null,reason:'not yet polled',totalMarkets:0,matchingClose:0,bestStrike:null,sampleFields:[]});
  // streakData moved below tradeLog declaration
  // V8.9.3: 3-way time format. timeFormat is the source of truth ('local' | 'utc' | 'est').
  //   useLocalTime kept as a derived boolean so the ~25 existing consumers don't all need
  //   refactoring at once. Consumers that need to distinguish UTC from EST read timeFormat.
  //   Migration from old boolean: taraV110TZ='false' → 'est', everything else → 'local'.
  const[timeFormat,setTimeFormat]=useState(()=>{
    try{
      const stored=localStorage.getItem('taraTimeFormat');
      if(stored==='local'||stored==='utc'||stored==='est')return stored;
      const oldTz=localStorage.getItem('taraV110TZ');
      if(oldTz==='false')return 'est';
      return 'local';
    }catch(_){return 'local';}
  });
  const useLocalTime=timeFormat==='local';
  // back-compat shim for any code that still calls setUseLocalTime
  const setUseLocalTime=(v)=>setTimeFormat(v?'local':'est');
  // V6.5.8: ASSET — which crypto Tara is monitoring. Default BTC. Persists.
  //   Switching asset resets price history, locks, window data, and re-subscribes
  //   to the new asset's exchange feeds.
  const[currentAsset,setCurrentAssetState]=useState(()=>{
    try{const v=localStorage.getItem('tara_current_asset')||ASSET_DEFAULT;return ASSET_KEYS.includes(v)?v:ASSET_DEFAULT;}catch(e){return ASSET_DEFAULT;}
  });
  const currentAssetRef=useRef(currentAsset);
  currentAssetRef.current=currentAsset;
  useEffect(()=>{try{localStorage.setItem('tara_current_asset',currentAsset);}catch(e){}},[currentAsset]);
  // V9.8.4: Live price source — Coinbase by default, user-switchable to Kraken
  //   or Bitstamp via the FEED pill in the header. See PRICE_SOURCES registry.
  //   Persists per-device. Switching doesn't affect candle history (still Coinbase).
  const[priceSource,setPriceSourceState]=useState(()=>{
    try{const v=localStorage.getItem('tara_price_source')||PRICE_SOURCE_DEFAULT;return (v&&PRICE_SOURCES[v]&&v!=='brti')?v:PRICE_SOURCE_DEFAULT;}catch(e){return PRICE_SOURCE_DEFAULT;}
  });
  const setPriceSource=(s)=>{
    if(!PRICE_SOURCES[s])return;
    setPriceSourceState(s);
    try{localStorage.setItem('tara_price_source',s);}catch(_){}
    // Reset stale tracker on switch — fresh source, fresh clock
    lastPriceChangeRef.current={price:null,time:Date.now()};
  };
  const priceSourceRef=useRef(priceSource);
  priceSourceRef.current=priceSource;
  // V9.8.4: STALE-TICK GUARD. Tracks when price ACTUALLY CHANGED (not just polled).
  //   If frozen >60s, banner shows + new locks blocked. Prevents Tara from acting
  //   on dead data when an exchange has a streaming-side incident (Coinbase did
  //   tonight per TradingView "Data issue" banner).
  const lastPriceChangeRef=useRef({price:null,time:Date.now()});
  const[feedStaleSeconds,setFeedStaleSeconds]=useState(0);
  const feedFrozen=feedStaleSeconds>=60; // hard threshold — locks paused
  const feedSlow=feedStaleSeconds>=30&&feedStaleSeconds<60; // soft warning
  const feedFrozenRef=useRef(feedFrozen);
  feedFrozenRef.current=feedFrozen;
  // V6.5.7: SPEED DIAL — 0-100, 50 default. Lower = faster lock (fewer samples,
  //   shorter search phase, lower thresholds). Higher = more patient. Persists.
  // V10.9.7: speedDial state removed — dial removed entirely.
  // ── V8.2: TRADING SETTINGS ──────────────────────────────────────────────
  // Bet size, win payout, anti-tilt cooldown enable, Discord alert filter, TP/SL rules.
  // All localStorage-only (per-device prefs, no need to sync). Defaults match typical
  // Kalshi 15m crypto trade: $10 bet → ~$8.50 net win after fees on 50% YES contracts.
  const[tradingSettings,setTradingSettings]=useState(()=>{
    try{
      const v=JSON.parse(localStorage.getItem('taraTradingSettings_v1')||'{}');
      return{
        betSize:Number(v.betSize)>0?Number(v.betSize):10,
        winPayout:Number(v.winPayout)>0?Number(v.winPayout):8.5,
        antiTiltEnabled:v.antiTiltEnabled!==false, // default ON
        antiTiltMinutes:Number(v.antiTiltMinutes)>0?Number(v.antiTiltMinutes):15,
        antiTiltStreakLen:Number(v.antiTiltStreakLen)>=3?Number(v.antiTiltStreakLen):4,
        highEdgeAlertOnly:!!v.highEdgeAlertOnly, // default OFF (all alerts)
        highEdgeMinPp:Number(v.highEdgeMinPp)>=5?Number(v.highEdgeMinPp):15,
        takeProfitEnabled:!!v.takeProfitEnabled,
        takeProfitOffer:Number(v.takeProfitOffer)>0?Number(v.takeProfitOffer):0.85,
        cutLossEnabled:!!v.cutLossEnabled,
        cutLossMinutes:Number(v.cutLossMinutes)>0?Number(v.cutLossMinutes):3,
        // V9.9.6: Tier-1 Only Mode. When ON, single / time-cap-commit / no-go-data /
        //   no-go-edge / timer-commit locks are mechanically converted to SIT_OUT
        //   with reasoning surfaced on the call card. Removes the discipline burden —
        //   user can't accidentally take a marginal trade because Tara won't offer it.
        //   Default OFF for backward compatibility — user opts in via Settings panel.
        tier1OnlyMode:!!v.tier1OnlyMode,
        // V9.9.8: Speed dial auto-mode. When ON, the dial automatically follows Tara's
        //   conditions-driven recommendation (computed in MarketContextStrip from
        //   velocityRegime + windowAmplitude + ATR). When OFF (default), user controls
        //   the dial manually as before. User toggles in trading settings; visual state
        //   shows in the MarketContextStrip speed pill.
      };
    }catch(_){return{betSize:10,winPayout:8.5,antiTiltEnabled:true,antiTiltMinutes:15,antiTiltStreakLen:4,highEdgeAlertOnly:false,highEdgeMinPp:15,takeProfitEnabled:false,takeProfitOffer:0.85,cutLossEnabled:false,cutLossMinutes:3,tier1OnlyMode:false};}
  });
  useEffect(()=>{try{localStorage.setItem('taraTradingSettings_v1',JSON.stringify(tradingSettings));}catch(_){}},[tradingSettings]);
  const[showTradingSettings,setShowTradingSettings]=useState(false);
  const[showBestPractices,setShowBestPractices]=useState(false); // V8.5: best-practices modal
  const[showDualFeed,setShowDualFeed]=useState(false); // V10.7.44 layout: dual feed collapsed to header toggle
  const[showHeaderOverflow,setShowHeaderOverflow]=useState(false); // V10.7.44b: ··· overflow panel
  // ── V9.3.0: KALSHI AUTO-EXECUTION STATE ─────────────────────────────────
  // Credentials are localStorage-only and NEVER cloud-synced. Auto-exec defaults
  // OFF and dry-run defaults ON until you've verified your first sandbox order.
  const[kalshiCreds,setKalshiCreds]=useState(()=>{
    try{
      const id=localStorage.getItem('tara_kalshi_api_key_id')||'';
      const pem=localStorage.getItem('tara_kalshi_private_key')||'';
      return{apiKeyId:id,privateKeyPem:pem};
    }catch(_){return{apiKeyId:'',privateKeyPem:''};}
  });
  const _saveKalshiCreds=(next)=>{
    setKalshiCreds(next);
    try{
      if(next.apiKeyId)localStorage.setItem('tara_kalshi_api_key_id',next.apiKeyId);
      else localStorage.removeItem('tara_kalshi_api_key_id');
      if(next.privateKeyPem)localStorage.setItem('tara_kalshi_private_key',next.privateKeyPem);
      else localStorage.removeItem('tara_kalshi_private_key');
    }catch(_){}
  };
  // V10.2.13 — SESSION × TIER OPTIMIZER (infrastructure phase)
  //   User hypothesis: tape-led wins in US-day (directional flow), structural-led
  //   wins in ASIA/EU (thinner flow, levels respected). Audit hooks
  //   __taraTierBySession() etc. validate this. If confirmed, this layer
  //   applies a per-(session,tier) multiplier to Tara's Call confidence.
  //
  //   Three modes (defaulting to 'off' until user audits + confirms):
  //     - 'off':    no multiplier applied; multiplier=1.0 always.
  //     - 'shadow': multiplier computed + logged to CSV; confidence not modified.
  //     - 'live':   multiplier applied to confidence in real-time.
  //
  //   Multipliers are USER-CONFIGURABLE via localStorage:
  //     localStorage.setItem('taraSessionTierMultipliers', JSON.stringify({
  //       ASIA: {structural:1.10, tape:0.95, confluence:1.05},
  //       US:   {structural:0.95, tape:1.10, confluence:1.00},
  //       ...
  //     }))
  //   Default = identity (all 1.0) so enabling 'live' is a no-op until tuned.
  //   Multipliers clamped to [0.7, 1.3] to prevent runaway adjustments.
  const[sessionTierMode,setSessionTierMode]=useState(()=>{
    try{
      const v=localStorage.getItem('taraSessionTierMode');
      return(['off','shadow','live'].includes(v))?v:'off';
    }catch(_){return 'off';}
  });
  useEffect(()=>{try{localStorage.setItem('taraSessionTierMode',sessionTierMode);}catch(_){}},[sessionTierMode]);
  const _sessionTierMultipliersRef=useRef(null);
  const _getSessionTierMultipliers=React.useCallback(()=>{
    try{
      const raw=localStorage.getItem('taraSessionTierMultipliers');
      if(!raw)return{};
      const parsed=JSON.parse(raw);
      if(!parsed||typeof parsed!=='object')return{};
      return parsed;
    }catch(_){return{};}
  },[]);
  const _resolveSessionTierMultiplier=React.useCallback((session,tier)=>{
    if(!session||!tier)return 1.0;
    const mults=_getSessionTierMultipliers();
    const _s=String(session).toUpperCase();
    const _t=String(tier).toLowerCase().includes('tape')?'tape'
      :String(tier).toLowerCase().includes('structural')?'structural'
      :String(tier).toLowerCase().includes('confluence')||String(tier).toLowerCase().includes('super')?'confluence'
      :String(tier).toLowerCase().includes('single')?'single'
      :null;
    if(!_t)return 1.0;
    const raw=Number(mults?.[_s]?.[_t]);
    if(!Number.isFinite(raw)||raw<=0)return 1.0;
    return Math.max(0.7,Math.min(1.3,raw));
  },[_getSessionTierMultipliers]);
  // V10.2.21 — KALSHI-AGREEMENT MULTIPLIER (Priority 2 from 724-trade audit)
  //   Biggest WR lever found in the audit: 18pt swing inside a single regime
  //   based on Kalshi's level of agreement with Tara's call.
  //
  //   Audit findings (TRENDING DOWN, n=337 sliced by Kalshi conviction in Tara's dir):
  //     Kalshi <40% (DISAGREES strongly):  58.3% WR  (n=24)
  //     Kalshi 40-55% (neutral):           62.5% WR  (n=104)
  //     Kalshi 55-70% (mild agree):        66.9% WR  (n=145)
  //     Kalshi 70%+ (STRONGLY agrees):     75.9% WR  (n=54)
  //
  //   Action: scale the auto-exec edge cap by Kalshi-agreement strength.
  //     - Strong agree → LOOSEN edge cap (allow trades with bigger Tara-vs-Kalshi gap)
  //     - Disagree → TIGHTEN edge cap (require tighter math; effectively block more)
  //
  //   Mode default = 'shadow' so users see logged data without behavior change.
  //   Graduate to 'live' after ~30 V10.2.21 trades validate the multiplier.
  const[kalshiAgreeMode,setKalshiAgreeMode]=useState(()=>{
    // V10.2.29 — DEFAULT 'live' (was 'shadow' from V10.2.21).
    //   Hunter preset already sets this live; making it the factory default so
    //   fresh installs / cleared storage gets the validated 18pt-swing WR lever
    //   from the start. To opt out: __taraKalshiAgree('apply',{mode:'shadow'}).
    try{
      const v=localStorage.getItem('taraKalshiAgreeMode');
      return(['off','shadow','live'].includes(v))?v:'live';
    }catch(_){return 'live';}
  });
  useEffect(()=>{try{localStorage.setItem('taraKalshiAgreeMode',kalshiAgreeMode);}catch(_){}},[kalshiAgreeMode]);
  // V10.2.28 — WEBSOCKET REAL-TIME PRICE FEED (opt-in)
  //   Default OFF — existing 30s polling keeps working. When user enables,
  //   Tara opens a WebSocket to Kalshi's public ticker_v2 channel and
  //   sub-second price updates feed kalshiYesPrice directly. Stops & targets
  //   fire ~30x faster (within ~1s instead of up to 30s lag).
  //
  //   Public ticker_v2 doesn't require auth — connect, subscribe, receive.
  //   We use the bid for our SIDE: if Tara is UP, we watch yes_bid; if DOWN,
  //   we watch no_bid. The bid is what we could sell at right now (= the
  //   correct price for "should the stop fire").
  //
  //   Disconnects auto-retry with exponential backoff up to 60s. If the
  //   connection drops entirely, polling continues to deliver prices — this
  //   is additive, not replacement.
  //
  //   Toggle via __taraWebSocket('on') / __taraWebSocket('off') console hook
  //   or the settings UI checkbox.
  const[kalshiWsEnabled,setKalshiWsEnabled]=useState(()=>{
    // V10.2.30 — DEFAULT OFF (was ON in V10.2.29). Reason: client-side WS to
    //   Kalshi's API doesn't work — they require authentication headers on the
    //   WebSocket handshake, and browser WebSocket constructors don't allow
    //   custom headers. To make WS function, a server-side proxy (Vercel API
    //   route) needs to open the WS to Kalshi with auth headers and relay
    //   messages to the browser via a separate WS or SSE channel.
    //
    //   Until that backend work is done, this stays OFF. The 30s REST poll
    //   continues to deliver prices. Manual override: __taraWebSocket('on')
    //   if you want to test, but expect reconnect spam.
    //
    //   Also force-clears any 'on' state from V10.2.29 default-on rollout —
    //   users who hadn't explicitly chosen still got it turned on and saw
    //   reconnect spam. Now they don't.
    try{
      const v=localStorage.getItem('taraKalshiWsEnabled');
      // Only ON if explicitly set to '1' AND user opted in after V10.2.30
      return v==='1';
    }catch(_){return false;}
  });
  useEffect(()=>{try{localStorage.setItem('taraKalshiWsEnabled',kalshiWsEnabled?'1':'0');}catch(_){}},[kalshiWsEnabled]);
  // Track WS connection state for UI surfacing
  const[kalshiWsState,setKalshiWsState]=useState({status:'disconnected',lastUpdate:null,messageCount:0,reconnectAttempts:0});
  // Resolve multiplier from Kalshi's conviction-in-trade-direction.
  //   Returns 1.0 (no-op) when conviction unknown.
  //   Multiplier table is fixed (not derived from history yet — would need
  //   per-(regime × kalshi-bucket) reweighting which is overkill for now).
  //   Future: derive from rolling 200-trade history.
  const _resolveKalshiAgreementMultiplier=React.useCallback((kalshiConvInDir)=>{
    if(!Number.isFinite(kalshiConvInDir))return 1.0;
    // Calibrated from 724-trade audit (May 15 2026):
    //   <40 → 0.70 (audit: 58.3% WR, well below baseline) → tighten cap
    //   40-55 → 0.95 (audit: 62.5% WR, slightly below baseline) → very mild tighten
    //   55-70 → 1.10 (audit: 66.9% WR, slightly above baseline) → mild loosen
    //   70-85 → 1.25 (audit: 75.9% WR, well above baseline) → loosen
    //   85+ → 1.30 (cap; small sample but consistent direction)
    if(kalshiConvInDir<40)return 0.70;
    if(kalshiConvInDir<55)return 0.95;
    if(kalshiConvInDir<70)return 1.10;
    if(kalshiConvInDir<85)return 1.25;
    return 1.30;
  },[]);
  // Console hook to inspect Kalshi-agreement state + sanity-check on history
  useEffect(()=>{
    if(typeof window==='undefined')return;
    window.__taraKalshiAgree=(action,opts)=>{
      // V10.2.22 — 'apply' action mirrors __taraSessionTier('apply'). One-shot
      //   enable that bypasses the React-state-staleness trap (setting
      //   localStorage manually doesn't trigger re-render, so mode stays stuck
      //   at mount value until refresh). 'apply' sets both localStorage AND
      //   React state, so live mode takes effect immediately.
      //   Usage: __taraKalshiAgree('apply', {mode:'live'})
      if(action==='apply'){
        const _mode=opts&&['off','shadow','live'].includes(opts.mode)?opts.mode:'live';
        try{
          localStorage.setItem('taraKalshiAgreeMode',_mode);
          setKalshiAgreeMode(_mode);
          console.info('[V10.2.22] kalshi-agree mode applied:',_mode,'— effective immediately');
        }catch(e){console.error('[apply] failed:',e?.message);}
        return{mode:_mode};
      }
      // Default action: analyze
      const log=JSON.parse(localStorage.getItem('taraCallLog_v1')||'[]');
      // V10.2.22 — field name FIX. V10.2.21 used e?.direction which is the CSV
      //   export name, but in-memory entries use e.dir (and outcomeDir). The
      //   filter matched 0 trades. Now reads dir.
      const resolved=log.filter(e=>(e?.result==='WIN'||e?.result==='LOSS')&&e?.kalshiAtLock!=null&&(e?.dir==='UP'||e?.dir==='DOWN'));
      // V10.2.22 — read mode from localStorage rather than React state. Closure
      //   captured stale value at useEffect mount; localStorage is always fresh.
      const _persistedMode=(()=>{try{return localStorage.getItem('taraKalshiAgreeMode')||'shadow';}catch(_){return 'shadow';}})();
      console.group('%c━━━ Kalshi Agreement Analyzer ━━━','color:#23B981;font-weight:bold');
      console.info('Persisted mode: '+_persistedMode);
      console.info('In-app React mode: '+kalshiAgreeMode+(_persistedMode!==kalshiAgreeMode?' (mismatch — refresh required, or run __taraKalshiAgree("apply",{mode:"'+_persistedMode+'"}))':''));
      console.info('Resolved trades with Kalshi data: '+resolved.length);
      if(resolved.length<10){console.info('Need ≥10 resolved trades for analysis.');console.groupEnd();return;}
      // Bucket by Kalshi's conviction in trade direction
      const buckets={'<40':[],'40-55':[],'55-70':[],'70-85':[],'85+':[]};
      resolved.forEach(e=>{
        const _k=Number(e.kalshiAtLock);
        if(!Number.isFinite(_k))return;
        const _kInDir=e.dir==='UP'?_k:(100-_k);
        const _b=_kInDir<40?'<40':_kInDir<55?'40-55':_kInDir<70?'55-70':_kInDir<85?'70-85':'85+';
        buckets[_b].push(e.result==='WIN'?1:0);
      });
      const _table={};
      ['<40','40-55','55-70','70-85','85+'].forEach(b=>{
        const arr=buckets[b];
        const n=arr.length,w=arr.reduce((a,c)=>a+c,0);
        const wr=n>0?(w/n*100).toFixed(1)+'%':'—';
        const _mult=_resolveKalshiAgreementMultiplier(b==='<40'?30:b==='40-55'?47:b==='55-70'?62:b==='70-85'?77:90);
        _table[b]={n,wins:w,wr,multiplier:_mult};
      });
      console.table(_table);
      console.info('Commands:');
      console.info('  __taraKalshiAgree()                      show this');
      console.info('  __taraKalshiAgree(\'apply\',{mode:\'live\'})  enable live (no refresh needed)');
      console.info('  __taraKalshiAgree(\'apply\',{mode:\'shadow\'}) back to shadow');
      console.info('  __taraKalshiAgree(\'apply\',{mode:\'off\'})    disable entirely');
      console.groupEnd();
      return _table;
    };
  },[kalshiAgreeMode,_resolveKalshiAgreementMultiplier,setKalshiAgreeMode]);
  // Console hook: inspect or set current session-tier state
  useEffect(()=>{
    if(typeof window==='undefined')return;
    window.__taraSessionTier=(action,opts)=>{
      // V10.2.14 — 'apply' action: derives + auto-applies in one call. Avoids
      //   the copy/paste trap where Chrome's console abbreviates {...} and
      //   users paste the literal '{...}' which is invalid JSON.
      //   Optional mode: __taraSessionTier('apply', {mode:'shadow'|'live'}).
      //   Defaults to NOT changing mode — user explicitly chooses shadow vs live.
      if(action==='apply'){
        const m=window.__taraSessionTier('derive');
        if(!m){console.error('[apply] derive failed — nothing applied');return null;}
        try{
          localStorage.setItem('taraSessionTierMultipliers',JSON.stringify(m));
          console.info('%c✓ Multipliers saved to localStorage','color:rgb(35,185,129);font-weight:bold;font-size:13px');
          const _wantMode=opts&&['off','shadow','live'].includes(opts.mode)?opts.mode:null;
          if(_wantMode){
            localStorage.setItem('taraSessionTierMode',_wantMode);
            console.info(`%c✓ Mode set to '${_wantMode}'`,'color:rgb(35,185,129);font-weight:bold;font-size:13px');
            console.info('%cRefresh the page (Cmd+R) for changes to take effect.','color:#23B981;font-weight:bold');
          }else{
            const _curMode=localStorage.getItem('taraSessionTierMode')||'off';
            console.info(`%cMode is currently '${_curMode}'.`,'color:rgba(237,237,237,0.65)');
            console.info('To enable shadow logging (safer first step):  __taraSessionTier(\'apply\', {mode:\'shadow\'})');
            console.info('To enable live optimization:                  __taraSessionTier(\'apply\', {mode:\'live\'})');
          }
          return m;
        }catch(e){console.error('[apply] save failed:',e);return null;}
      }
      if(action==='derive'){
        // Auto-derive multipliers from historical taraCallLog
        try{
          const raw=localStorage.getItem('taraCallLog_v1');
          if(!raw){console.warn('no call log');return null;}
          const log=JSON.parse(raw);
          if(!Array.isArray(log)){console.warn('call log malformed');return null;}
          const entries=log.filter(e=>e&&(e.result==='WIN'||e.result==='LOSS'));
          const wrTable={};
          entries.forEach(e=>{
            const s=String(e.session||'').toUpperCase();
            const t=String(e.tier||'').toLowerCase();
            const tNorm=t.includes('tape')?'tape':t.includes('structural')?'structural':t.includes('confluence')||t.includes('super')?'confluence':t.includes('single')?'single':null;
            if(!s||!tNorm)return;
            if(!wrTable[s])wrTable[s]={};
            if(!wrTable[s][tNorm])wrTable[s][tNorm]={wins:0,n:0};
            wrTable[s][tNorm].n++;
            if(e.result==='WIN')wrTable[s][tNorm].wins++;
          });
          const multipliers={};
          Object.keys(wrTable).forEach(s=>{
            multipliers[s]={};
            const sessTotal=Object.values(wrTable[s]).reduce((a,c)=>({w:a.w+c.wins,n:a.n+c.n}),{w:0,n:0});
            const sessWR=sessTotal.n>0?sessTotal.w/sessTotal.n:0.6;
            Object.keys(wrTable[s]).forEach(t=>{
              const c=wrTable[s][t];
              if(c.n<10){multipliers[s][t]=1.0;return;}
              const tierWR=c.wins/c.n;
              const rawMult=tierWR/sessWR;
              multipliers[s][t]=Math.round(Math.max(0.7,Math.min(1.3,rawMult))*100)/100;
            });
          });
          console.group('%c━━━ Derived session-tier multipliers ━━━','color:#23B981;font-weight:bold');
          console.info('Source: '+entries.length+' resolved trades');
          console.info('Multipliers (clamped to [0.7, 1.3], cells with n<10 → 1.0):');
          console.table(multipliers);
          console.info('%cTo apply these (no copy-paste needed):','color:rgb(35,185,129);font-weight:bold');
          console.info('  __taraSessionTier(\'apply\', {mode:\'shadow\'})  // safer first step');
          console.info('  __taraSessionTier(\'apply\', {mode:\'live\'})    // activate adjustments');
          console.groupEnd();
          return multipliers;
        }catch(e){console.error('derive failed:',e);return null;}
      }
      // Default: show current state
      const cur=_getSessionTierMultipliers();
      console.group('%c━━━ Session-tier optimizer state ━━━','color:#23B981;font-weight:bold');
      console.info('mode:',sessionTierMode);
      console.info('multipliers:',cur);
      console.info('Commands:');
      console.info('  __taraSessionTier()                              show this');
      console.info('  __taraSessionTier(\'derive\')                      compute multipliers from history');
      console.info('  __taraSessionTier(\'apply\')                       derive + save (no mode change)');
      console.info('  __taraSessionTier(\'apply\', {mode:\'shadow\'})      derive + save + set shadow mode');
      console.info('  __taraSessionTier(\'apply\', {mode:\'live\'})        derive + save + set live mode');
      console.groupEnd();
      return{mode:sessionTierMode,multipliers:cur};
    };
  },[sessionTierMode,_getSessionTierMultipliers]);
  const[autoExecSettings,setAutoExecSettings]=useState(()=>{
    try{
      const v=JSON.parse(localStorage.getItem('tara_autoexec_v1')||'{}');
      // V13.4.261: read the migration flag ONCE, here, before the object
      //   literal below is evaluated. The v253 bug was setting the sentinel
      //   inside one property and reading it from another further down, so
      //   source order silently decided which migrations ran.
      const _v261ExitClearPending=(()=>{
        try{return !localStorage.getItem('tara_v13_4_261_exit_clear');}catch(_e){return false;}
      })();
      // V13.4.267: second one-shot, for devices whose fixed rules were written
      //   by a MODE preset AFTER the v261 clear already ran (sentinel set, so
      //   nothing would ever clear them again). Captured here for the same
      //   reason as the one above -- never read inline at the migration sites.
      const _v267PresetExitClear=(()=>{
        try{return !localStorage.getItem('tara_v13_4_267_preset_exit_clear');}catch(_e){return false;}
      })();
      // The exact values _PATIENT and _FAST wrote. Narrow on purpose: a value he
      //   set by hand in Settings is his decision and is left alone.
      const _v267IsPresetTP=(n)=>Number(n)===78||Number(n)===88;
      const _v267IsPresetSL=(n)=>Number(n)===15||Number(n)===20;
      return{
        // V13.4.306 REVERTED: v252 flipped both these to arm-by-default,
        //   reasoning the rebuilt order path (2026-09-05) had never placed a
        //   live order yet so arming it changed nothing in practice at the
        //   time. That justification no longer holds -- Mission Mode is being
        //   wired to size real orders off live Kelly math (see
        //   project_tara_autoexec_simplify memory), so a fresh install or a
        //   new device arming for real money with zero explicit action is a
        //   real hazard now, not a historical curiosity. Back to the safe
        //   direction: a stored value (true OR false) still always wins for
        //   anyone with existing settings -- this only changes what a
        //   missing/unparseable value falls back to.
        enabled:v.enabled===true,                    // master toggle — disarmed by default
        dryRun:v.dryRun!==false,                      // default ON — simulate unless explicitly stored false
        // V10.2.4: same safe-defaults migration as maxAutoTradesPerDay below.
        maxBetPerTrade:(()=>{
          let _v=Number(v.maxBetPerTrade)>0?Number(v.maxBetPerTrade):25;
          try{
            if(!localStorage.getItem('tara_v10_2_4_safe_defaults_applied')){
              const _atOldDefaults=(Number(v.maxBetPerTrade)===25&&Number(v.maxDailyLoss)===50&&Number(v.maxAutoTradesPerDay)===10);
              if(_atOldDefaults){_v=2;try{console.info('[V10.2.4] safe-defaults migration: maxBetPerTrade 25→2');}catch(_){}}
            }
          }catch(_){}
          return _v;
        })(),
        maxDailyLoss:(()=>{
          let _v=Number(v.maxDailyLoss)>0?Number(v.maxDailyLoss):50;
          try{
            if(!localStorage.getItem('tara_v10_2_4_safe_defaults_applied')){
              const _atOldDefaults=(Number(v.maxBetPerTrade)===25&&Number(v.maxDailyLoss)===50&&Number(v.maxAutoTradesPerDay)===10);
              if(_atOldDefaults){_v=5;try{console.info('[V10.2.4] safe-defaults migration: maxDailyLoss 50→5');}catch(_){}}
            }
          }catch(_){}
          return _v;
        })(),
        // V10.2.12 — audit-optimal: 3→2. Earlier cooldown trigger prevents tilt
        //   on a 2-loss streak rather than waiting for a third. Matches BALANCED
        //   + SURGEON preset values. Only fires if user is on the prior default (3).
        cooldownLossStreak:(()=>{
          let _v=Number(v.cooldownLossStreak)>=2?Number(v.cooldownLossStreak):3;
          try{
            if(!localStorage.getItem('tara_v10_2_12_audit_phase2_applied')){
              if(Number(v.cooldownLossStreak)===3||v.cooldownLossStreak==null){
                _v=2;
                try{console.info('[V10.2.12] audit-optimal: cooldownLossStreak 3→2');}catch(_){}
              }
            }
          }catch(_){}
          return _v;
        })(),
        // V10.2.12 — audit-optimal: 20→30 min. Longer cooldown after streak gives
        //   real reset before re-engagement. Matches BALANCED + SURGEON presets.
        cooldownMinutes:(()=>{
          let _v=Number(v.cooldownMinutes)>0?Number(v.cooldownMinutes):20;
          try{
            if(!localStorage.getItem('tara_v10_2_12_audit_phase2_applied')){
              if(Number(v.cooldownMinutes)===20||v.cooldownMinutes==null){
                _v=30;
                try{console.info('[V10.2.12] audit-optimal: cooldownMinutes 20→30');}catch(_){}
              }
            }
          }catch(_){}
          return _v;
        })(),
        // V10.2.x — runaway protection: hard cap on number of auto-exec trades per
        //   UTC day. When reached, autoExecCooldownUntil is set to UTC midnight so
        //   the existing cooldown UI surfaces the block. Default 10 — conservative
        //   for low-balance accounts; user should raise once they trust the system.
        //   Set to 0 to disable (not recommended).
        // V10.2.4: SAFE-DEFAULTS MIGRATION. Detect users on the V10.2.x defaults
        //   (maxBet=25, maxDailyLoss=50, maxAutoTradesPerDay=10) and bump them to
        //   safer recommended values (2 / 5 / 5) since user feedback shows these
        //   were never customized but the high values cost real money in the loop
        //   bug. Sentinel 'tara_v10_2_4_safe_defaults_applied' prevents double-run.
        //   If user has customized ANY of the three, leave all three alone.
        maxAutoTradesPerDay:(()=>{
          let _v=Number.isFinite(Number(v.maxAutoTradesPerDay))&&Number(v.maxAutoTradesPerDay)>=0?Number(v.maxAutoTradesPerDay):10;
          try{
            if(!localStorage.getItem('tara_v10_2_4_safe_defaults_applied')){
              const _atOldDefaults=(Number(v.maxBetPerTrade)===25&&Number(v.maxDailyLoss)===50&&Number(v.maxAutoTradesPerDay)===10);
              if(_atOldDefaults){
                _v=5;
                try{console.info('[V10.2.4] safe-defaults migration: maxAutoTradesPerDay 10→5');}catch(_){}
              }
              localStorage.setItem('tara_v10_2_4_safe_defaults_applied','1');
            }
          }catch(_){}
          return _v;
        })(),
        // V10.2.4: per-window hard cap on auto-exec attempts. Default 1 — matches
        //   V9.18.3 hard-cap behavior. Raising to N allows up to N entries on the
        //   same window (e.g. averaging in, scalp pyramiding). WARNING: raising
        //   this re-enables some failure modes the V9.18.3 cap was designed to
        //   prevent. Keep at 1 unless you have a specific reason.
        maxAutoTradesPerWindow:1, // V10.2.24: hard floor — value forced to 1 regardless of input
        slippageCents:Number(v.slippageCents)>=0?Number(v.slippageCents):2,
        // V10.2.8 — AUDIT-OPTIMAL MIGRATION for autoExitOffer (88→82), maxEdgePt
        //   (15→10), and skipTimeCapCommit (false→true). Each one is independently
        //   migrated ONLY if it currently equals the prior default — any custom
        //   value is preserved. Rationale: May 14 755-trade audit showed
        //   - take-profit 82¢ books winners more reliably than 88¢ at +17pt entry
        //   - edge cap 10pt cuts the 65% WR tail (audit: edge >25pt = 65%, 0-10pt = 70.6%)
        //   - skipTimeCapCommit=true filters the 62.4% WR cluster (audit: time-cap = 50% of volume)
        //   Combined expected WR lift: ~66% → ~72% on the surviving trades.
        //   Sentinel 'tara_v10_2_8_audit_optimal_applied' guarantees single-run.
        autoExitOffer:(()=>{
          // V13.4.253: default OFF (0 = no fixed take-profit). Measured negative
          //   at 8 of 9 levels (70..95c) over 454 replayed calls: 99% of WINS
          //   touch 80c+ against 34% of losses, so a fixed level truncates
          //   nearly every winner to spare a third of the losers, and books a
          //   second fee. The trailing stop in the exit checker replaces it.
          let _v=Number(v.autoExitOffer)>0?Number(v.autoExitOffer):0;
          try{
            // one-time: clear a stored fixed TP that came from a preset (80..92c)
            // V13.4.267: second condition catches a preset-written TP on a device
            //   the v261 clear already ran on. 88c and 78c both sit UNDER the 90c
            //   trail arm, so leaving one in place silently disables the trailing
            //   stop -- the one exit rule that measured as working.
            if((_v261ExitClearPending||(_v267PresetExitClear&&_v267IsPresetTP(v.autoExitOffer)))&&Number(v.autoExitOffer)>0){
              try{console.info('[V13.4.267] fixed take-profit '+v.autoExitOffer+'c -> off (measured loser; under the '+TRAIL_ARM_C+'c trail arm)');}catch(_){}
              _v=0;
            }
            if(!localStorage.getItem('tara_v10_2_8_audit_optimal_applied')&&_v>0){
              // Migrate ONLY if user is on prior default (88 or 85). Otherwise preserve.
              if(Number(v.autoExitOffer)===88||Number(v.autoExitOffer)===85){
                _v=82;
                try{console.info('[V10.2.8] audit-optimal: autoExitOffer →82¢ (was '+v.autoExitOffer+'¢)');}catch(_){}
              }
            }
          }catch(_){}
          return _v;
        })(),
        // V13.4.292: REMOVED the `autoExitSecLeft` field that used to sit here
        //   (`Number(v.autoExitSecLeft)>0?...:20`). The exit executor read THIS
        //   key, but every UI control, every preset, and both mode buttons wrote
        //   `timeExitSecLeft` instead -- a name that never appears in the object
        //   this hydrator builds. Since nothing ever wrote `autoExitSecLeft`,
        //   `Number(undefined)>0` was always false and it was permanently 20 on
        //   every install, old or new. Net effect: every filled position was
        //   force-closed with 20s left in the window regardless of what the
        //   "Time exit" control showed (0 = "no time exit," believed and
        //   default everywhere) -- a winner sitting at 95c got sold at the bid
        //   instead of settling at 100 for free. The executor now reads
        //   `timeExitSecLeft` below, the key everything actually writes.
        // V13.4.253: fixed take-profit defaults OFF. Measured negative at 8 of
        //   9 levels (70..95c) across 454 replayed calls -- 99% of wins touch
        //   80c+ against 34% of losses, so a fixed level truncates the winners.
        //   A stored value still wins, so the setting keeps working.
        // V9.19.26: opt-in skip of time-cap-commit / timer-commit locks.
        //   Default OFF — A/B test by enabling. May 14 audit: 322 of 642 resolved
        //   trades (50.2%) were time-cap commits, won only 62.4% (vs 78.5% for
        //   normal commits). The 90-119s sub-band was 54.6% (coin flip).
        //   When ON, auto-exec skips these. Manual click still fires them.
        // V10.2.8 — audit-optimal: OFF→ON. Time-cap-commit cluster won only 62.4%
        //   in 642-trade audit vs 78.5% for normal commits. Biggest single WR lever.
        //   Manual click still fires time-cap locks. Sentinel sets here (last in
        //   the V10.2.8 trio so all three migrations have a chance to fire first).
        skipTimeCapCommit:(()=>{
          let _v=!!v.skipTimeCapCommit;
          try{
            if(!localStorage.getItem('tara_v10_2_8_audit_optimal_applied')){
              if(!v.skipTimeCapCommit){
                _v=true;
                try{console.info('[V10.2.8] audit-optimal: skipTimeCapCommit OFF→ON');}catch(_){}
              }
              // Last of the V10.2.8 trio — set the sentinel here
              localStorage.setItem('tara_v10_2_8_audit_optimal_applied','1');
            }
            // V13.4.253: same place, same idiom — set the exit-rules sentinel once
            //   the hydrator has run, so the fixed TP/SL clear happens exactly once
            //   and a deliberate re-enable afterwards is never undone.
            // V13.4.261: new sentinel. The v253 one is already '1' everywhere,
            //   so the corrected stop-loss clear would never fire under it.
            if(_v261ExitClearPending){
              localStorage.setItem('tara_v13_4_261_exit_clear','1');
            }
            // V13.4.267: safe to set here only because the flag is captured once
            //   at the top of the hydrator, not read inline. That is the whole
            //   lesson of the v253 bug.
            if(_v267PresetExitClear){
              localStorage.setItem('tara_v13_4_267_preset_exit_clear','1');
            }
          }catch(_){}
          return _v;
        })(),
        // V10.2.34 — TCC SMART BYPASS. When true (default), the skipTimeCapCommit
        //   gate above is softened: TCC trades that match ANY of {ASIA session,
        //   Kalshi≥55% + qScore≥50, RANGE-CHOP regime} are allowed through.
        //   Empty/null persisted value → true. Explicit false → strict block-all.
        tccSmartBypass:(v.tccSmartBypass===false)?false:true,
        // V10.0.0: Tara's Trade timing engine mode (Phase 4).
        //   'off'      — Phase 4 disabled entirely
        //   'shadow'   — runs on every lock, logs decision to call log, does NOT affect trades
        //   'advisory' — decision shown in UI but doesn't block (Session 4+)
        //   'pregate'  — live, blocks/delays per decision (Session 4+, manual click bypasses)
        //   Session 2 (current): only 'off' and 'shadow' are functional.
        //   advisory/pregate are stubbed until Session 4 promotes them.
        //
        //   V10.2.0: Default flipped from 'off' to 'shadow' now that the Supabase
        //   migration is complete. One-shot upgrade below: existing users who saved
        //   'off' in localStorage during V10.0.0/V10.1.0 are auto-bumped to 'shadow'
        //   on first load after this deploy. Sentinel key 'tara_phase4_v10_2_upgraded'
        //   ensures this happens only once — after upgrade, explicit user choices
        //   (including reverting to 'off') are fully respected.
        // V10.2.12 — audit-optimal: shadow→advisory. Shadow and advisory both
        //   evaluate Phase 4 and log to CSV; the only difference is advisory
        //   surfaces the decision badge prominently in the predictor header.
        //   Defaulting to advisory gives the user immediate visibility into
        //   what Phase 4 thinks, without blocking any trades (pregate would
        //   block; that's a deliberate per-user opt-in).
        //   This migration is THE LAST one in the V10.2.12 audit-optimal trio,
        //   so it sets the shared sentinel `tara_v10_2_12_audit_phase2_applied`
        //   after all three (cooldownLossStreak, cooldownMinutes, tradeTimingMode)
        //   have had a chance to run.
        tradeTimingMode:(()=>{
          let _m=v.tradeTimingMode;
          try{
            // Original V10.2.0 upgrade — flip from off/unset to shadow
            if(!localStorage.getItem('tara_phase4_v10_2_upgraded')){
              _m='shadow';
              localStorage.setItem('tara_phase4_v10_2_upgraded','1');
              try{console.info('[Phase4] V10.2.0 upgrade — tradeTimingMode set to shadow (was '+(v.tradeTimingMode||'unset')+')');}catch(_){}
            }
            // V10.2.12 — bump shadow→advisory (no behavior change — both log
            //   the decision; advisory just makes the badge a "real" UI signal
            //   the user pays attention to)
            if(!localStorage.getItem('tara_v10_2_12_audit_phase2_applied')){
              if(_m==='shadow'||v.tradeTimingMode==='shadow'){
                _m='advisory';
                try{console.info('[V10.2.12] audit-optimal: tradeTimingMode shadow→advisory');}catch(_){}
              }
              // Set the shared sentinel here — last of the three V10.2.12 migrations
              localStorage.setItem('tara_v10_2_12_audit_phase2_applied','1');
            }
          }catch(_){}
          return(['off','shadow','advisory','pregate'].includes(_m))?_m:'advisory';
        })(),
        // V9.19.24: edge filter cap. Default 15pt — based on May 14 audit of 642
        //   resolved trades. WR by edge bucket: +0-10pt → 70.6%, +10-20pt → 65.1%,
        //   +20-30pt → 60.2%, +30+pt → 65.2%. Cap at 15pt keeps the sweet-spot
        //   trades (small positive edge) and contrarian gems (negative edge),
        //   blocks the "market already priced" zone. Set to 0 to disable filter.
        // V10.2.8 — audit-optimal: 15→10 (filters 65% WR tail, keeps ~85% of volume)
        maxEdgePt:(()=>{
          let _v=Number(v.maxEdgePt)>=0?Number(v.maxEdgePt):15;
          try{
            if(!localStorage.getItem('tara_v10_2_8_audit_optimal_applied')){
              if(Number(v.maxEdgePt)===15||v.maxEdgePt==null){
                _v=10;
                try{console.info('[V10.2.8] audit-optimal: maxEdgePt 15→10');}catch(_){}
              }
            }
          }catch(_){}
          return _v;
        })(),
        // ── V9.6.0: ADVANCED ENTRY FILTERS ──────────────────────────────
        // Per-asset enable. Default both ON. When OFF, auto-exec skips that asset
        // even if the master toggle is on. Useful for "auto-trade BTC only".
        enabledAssets:{
          BTC:v.enabledAssets?.BTC!==false,

        },
        // Per-window-type enable. Default both ON.
        enabledWindowTypes:{
          '15m':v.enabledWindowTypes?.['15m']!==false,
          '5m':v.enabledWindowTypes?.['5m']!==false,
        },
        // Minimum tier. 'any' = no filter. Other values lock out lower tiers:
        //   'tape' = tape-led or higher (excludes single)
        //   'structural' = structural-led or higher
        //   'confluence' = confluence or higher
        //   'super' = super-confluence ONLY — most conservative, lowest fire rate
        minTier:v.minTier||'any',
        // Minimum quality gate score (0-100). 0 = no filter.
        minQualityScore:Number(v.minQualityScore)>=0?Number(v.minQualityScore):0,
        // Minimum directional conviction (0-50, where conviction = |posterior-50|).
        minConviction:Number(v.minConviction)>=0?Number(v.minConviction):0,
        // Skip when V9.4.0 marginal-zone caution is attached. Conservative default OFF.
        skipMarginalCaution:!!v.skipMarginalCaution,
        blockUrgencyApplied:!!v.blockUrgencyApplied, // V9.18.9
        // Lock-stability requirement: wait N seconds after lock fires before placing
        // the order. Filters out instant-flip locks. 0 = fire immediately on lock.
        lockStabilitySec:Number(v.lockStabilitySec)>=0?Number(v.lockStabilitySec):0,
        // ── V9.6.0: ADVANCED EXIT LOGIC ──────────────────────────────────
        // Stop-loss: exit if our side drops by N¢ from the fill price. 0 = disabled.
        stopLossDeltaCents:(()=>{
          // V13.4.253: default OFF (0 = no fixed stop). Measured negative at ALL
          //   7 levels tested (10..40c) over the same 454 calls -- the shipped
           //  presets sat at 10-18c, inside that range. Superseded by the
          //   trailing stop, which does not cap a winner that keeps running.
          let _v=Number(v.stopLossDeltaCents)>=0?Number(v.stopLossDeltaCents):0;
          try{
            // V13.4.267: same second condition. This is the one actually found live
            //   on the dev origin -- stopLossDeltaCents 15 with the v261 sentinel
            //   already set, i.e. written by a MODE click after the clear had run.
            if((_v261ExitClearPending||(_v267PresetExitClear&&_v267IsPresetSL(_v)))&&_v>0){
              try{console.info('[V13.4.267] fixed stop-loss '+_v+'c -> off (measured loser)');}catch(_){}
              _v=0;
            }
          }catch(_){}
          // V13.4.292: REMOVED the V10.2.5 one-shot migration that used to sit here
          //   ("if(_v===0){_v=15;}" gated on 'tara_v10_2_5_stoploss_default_applied").
          //   It ran AFTER the v267 clear above and was untouched by it -- the clear's
          //   guard is `_v>0`, so it sees a fresh 0 and skips, then V10.2.5 promotes
          //   that same 0 straight to 15. Every fresh install, cleared browser
          //   profile, incognito window, or the second Vercel origin (separate
          //   localStorage) booted with a 15c fixed stop-loss armed and logged only
          //   to console -- inside the band measured negative at all 7 levels
          //   tested. The v267 fix five lines up cannot catch this: it only clears
          //   an already-nonzero value, and this migration is what MADE it nonzero.
          //   Removing the migration stops it happening to a NEW device, but does
          //   nothing for a device it already hit -- verified live on this dev
          //   origin, which loaded with stopLossDeltaCents:15 and the
          //   'tara_v10_2_5_stoploss_default_applied' sentinel already set. One-shot
          //   retroactive clear, gated on that sentinel actually existing (proof
          //   this exact migration ran here) AND the value still being exactly 15
          //   (its only possible output) -- a value the user later hand-typed to
          //   something else, or hand-typed to 15 AFTER this fix shipped, is left
          //   alone by either condition failing.
          try{
            if(localStorage.getItem('tara_v10_2_5_stoploss_default_applied')
               &&!localStorage.getItem('tara_v13_4_292_v10_2_5_undo')&&_v===15){
              try{console.info('[V13.4.292] fixed stop-loss 15c -> off (undoing the V10.2.5 migration that wrote it; measured loser at every level tested)');}catch(_){}
              _v=0;
            }
            localStorage.setItem('tara_v13_4_292_v10_2_5_undo','1');
          }catch(_){}
          try{
            // V10.2.31 — clamp persisted SL to max 50¢. If a user had set a wide
            //   stop pre-V10.2.31 (max was 90¢), bring it back into the safe range
            //   on next load. Logs the clamp so it's visible — not silent.
            if(_v>50){
              try{console.warn(`[V10.2.31] Clamping persisted stopLossDeltaCents ${_v}¢ → 50¢ (max). Wide stops are dangerous; 0 (off) is the measured-correct default.`);}catch(_){}
              _v=50;
            }
          }catch(_){}
          return _v;
        })(),
        // Time-based exit: exit when fewer than N seconds remain in the window.
        // 0 = disabled (let it settle naturally on Kalshi).
        timeExitSecLeft:Number(v.timeExitSecLeft)>=0?Number(v.timeExitSecLeft):0,
        // ── V9.6.0: POSITION SIZING ──────────────────────────────────────
        // 'fixed': always uses tradingSettings.betSize, capped at maxBetPerTrade
        // 'confidence': scales between confidenceLowBet and confidenceHighBet by conviction
        // 'kelly': uses Kelly fraction × confidenceHighBet (most aggressive)
        sizingMode:v.sizingMode||'fixed',
        confidenceLowBet:Number(v.confidenceLowBet)>0?Number(v.confidenceLowBet):5,
        confidenceHighBet:Number(v.confidenceHighBet)>0?Number(v.confidenceHighBet):25,
        // V9.18.4: 0-100 dial. 0 = pure fixed bet, 100 = pure Kelly.
        kellyBlend:Number.isFinite(Number(v.kellyBlend))?Math.max(0,Math.min(100,Number(v.kellyBlend))):50,
        // V9.18.4: entry mode — how to express stake. Translates to dollars
        //   internally (Kalshi API only takes contracts × cents).
        entryMode:['dollars','contracts','percent'].includes(v.entryMode)?v.entryMode:'dollars',
        entryContracts:Number(v.entryContracts)>0?Math.min(500,Number(v.entryContracts)):5,
        entryPercentBalance:Number(v.entryPercentBalance)>0?Math.min(100,Number(v.entryPercentBalance)):10,
        // ── V9.7.4: SMART ENTRY LADDER ────────────────────────────────────
        // When enabled, posts limit BELOW current offer to capture spread.
        // If unfilled after `entryLadderStepSec`, cancels and re-posts closer
        // to the offer. After `entryLadderMaxSteps` steps, takes the market
        // (current offer + slippage). Net effect: most fills save 1-2¢/contract,
        // a small minority of fast-moving setups never fill at all.
        // V13.4.249 — LADDER ON BY DEFAULT, one-time migration.
        //   V13.4.228 measured this on 440 settled calls: crossing the spread
        //   costs -1.85c per contract, resting a cent inside earns +1.00c, a
        //   2.85c swing that is larger than the signal's gross edge. The
        //   checkbox has existed since V9.7.4 but nothing implemented the walk
        //   until V13.4.248, so "off" was the only behaviour there had ever
        //   been and every stored `false` is a default nobody chose. Flip it
        //   once, then respect the setting forever after — same shape as the
        //   V10.2.5 stop-loss and V10.2.8 audit-optimal migrations above.
        entryLadderEnabled:(()=>{
          let _v=!!v.entryLadderEnabled;
          try{
            if(!localStorage.getItem('tara_v13_4_249_ladder_default_applied')){
              if(!_v){
                _v=true;
                try{console.info('[V13.4.249] entry ladder ON by default — rest instead of cross (V13.4.228: +2.85c/contract swing)');}catch(_e){}
              }
              localStorage.setItem('tara_v13_4_249_ladder_default_applied','1');
            }
            // V13.4.348: explicit user request 2026-09-11 -- turn the ladder
            //   off. One-time FORCED flip, same shape as the V13.4.249
            //   migration right above (which is exactly why a plain code
            //   default change would not have worked here: this account's
            //   autoExecSettings already carries a real, persisted true
            //   value from that migration, and the normal fallback path
            //   above only fills in when a value is missing -- it never
            //   overrides one that's already set). Runs once, then respects
            //   whatever the user chooses afterward, including turning it
            //   back on via the Trading Settings checkbox.
            if(!localStorage.getItem('tara_v13_4_348_ladder_disabled_applied')){
              _v=false;
              try{console.info('[V13.4.348] entry ladder OFF per explicit request -- takes the offer immediately instead of resting for a better price');}catch(_e){}
              localStorage.setItem('tara_v13_4_348_ladder_disabled_applied','1');
            }
          }catch(_e){}
          return _v;
        })(),
        entryLadderUndercutCents:Number(v.entryLadderUndercutCents)>0?Number(v.entryLadderUndercutCents):2, // start 2¢ below
        entryLadderStepSec:Number(v.entryLadderStepSec)>=2?Number(v.entryLadderStepSec):8, // wait 8s per step
        entryLadderMaxSteps:Number(v.entryLadderMaxSteps)>=1?Number(v.entryLadderMaxSteps):2, // 2 ladder rungs before market
        // ── V9.17.5: PATIENT ENTRY ────────────────────────────────────────
        // When Tara locks UP/DOWN but current offer is OUTSIDE the value zone,
        // wait for price to enter the zone before placing the order. If max
        // wait time expires without entry, gives up (no order placed).
        //
        // Example: Tara locks UP, current offer is 70¢, value zone max is 55¢.
        // Patient entry waits up to 90s. If price drops to 55¢ or below,
        // place the order at that price. If 90s elapses without entry,
        // skip the trade.
        //
        // Also re-checks engine direction during the wait: if direction flips
        // (e.g., engine now reads DOWN), cancels the patient wait.
        patientEntryEnabled:!!v.patientEntryEnabled, // default OFF — opt-in
        patientEntryMaxCents:Number(v.patientEntryMaxCents)>0?Number(v.patientEntryMaxCents):55, // wait for offer ≤ this
        patientEntryMaxWaitSec:Number(v.patientEntryMaxWaitSec)>=10?Number(v.patientEntryMaxWaitSec):90, // give up after 90s
        // ── V9.17.5: SMART EXITS ──────────────────────────────────────────
        // When in position, monitor tape direction every poll. If tape reverses
        // against position with conviction ≥ threshold AND position has profit,
        // suggest early exit (lock in gains before reversal).
        //
        // If momentum is strong WITH position (tape conviction ≥ threshold and
        // same direction), extend autoExitOffer by N cents (let it run).
        smartExitsEnabled:!!v.smartExitsEnabled, // default OFF — opt-in
        smartExitReverseConviction:Number(v.smartExitReverseConviction)>=50?Number(v.smartExitReverseConviction):70, // tape conviction needed for early exit
        smartExitMinProfitCents:Number(v.smartExitMinProfitCents)>=0?Number(v.smartExitMinProfitCents):5, // min profit before considering early exit
        smartExitExtendOnStrength:!!v.smartExitExtendOnStrength, // when tape strong WITH position, extend target
        smartExitExtendCents:Number(v.smartExitExtendCents)>=0?Number(v.smartExitExtendCents):5, // extend target by N¢ when extending
        signalSource:(v.signalSource==='lock'||v.signalSource==='snapshot')?v.signalSource:'snapshot', // V9.17.22
      };
    }catch(_){
      // V10.2.29 — Factory defaults are now HUNTER preset values (was BALANCED).
      //   Hunter is calibrated for the current momentum-heavy market and stacks
      //   every V10.2.x WR lever (Kalshi-agree live, qScoreV2 retune, session-
      //   tier optimizer). Fresh installs / cleared storage start here. Users
      //   who manually customize still keep their settings (this catch only
      //   fires on parse failure / no localStorage entry).
      //   Hunter targets: 70-72% WR, 6-10 trades/day.
      return{
        // V13.4.306 REVERTED, same reasoning as the migration path above:
        //   fresh-install/parse-failure now lands disarmed, not armed. Every
        //   other Hunter value below (sizing, filters, cooldown) still applies
        //   -- only these two flags changed.
        enabled:false,dryRun:true,
        // Hunter sizing
        maxBetPerTrade:2.5,maxDailyLoss:6,maxAutoTradesPerDay:8,maxAutoTradesPerWindow:1,
        // Hunter filters
        minTier:'tape',minQualityScore:40,minConviction:0,
        maxEdgePt:8,
        skipTimeCapCommit:true,
        tccSmartBypass:true,skipMarginalCaution:true,
        blockUrgencyApplied:false,lockStabilitySec:0,
        // Hunter exits
        autoExitOffer:0,   // V13.4.253: fixed TP off by default. V13.4.292: autoExitSecLeft
                           //   removed here too -- the executor no longer reads it.
        stopLossDeltaCents:0,timeExitSecLeft:0,   // V13.4.253: fixed stop off by default
        // Hunter cooldown
        cooldownLossStreak:3,cooldownMinutes:30,
        // Phase 4 advisory (badge shows, doesn't block)
        tradeTimingMode:'advisory',
        // Universal
        slippageCents:2,
        enabledAssets:{BTC:true},
        enabledWindowTypes:{'15m':true,'5m':true},
        sizingMode:'fixed',
        confidenceLowBet:5,confidenceHighBet:25,kellyBlend:50,
        entryMode:'dollars',entryContracts:5,entryPercentBalance:10,
        entryLadderEnabled:false,entryLadderUndercutCents:2,entryLadderStepSec:8,entryLadderMaxSteps:2,
        patientEntryEnabled:false,patientEntryMaxCents:55,patientEntryMaxWaitSec:90,
        smartExitsEnabled:false,smartExitReverseConviction:70,smartExitMinProfitCents:5,
        smartExitExtendOnStrength:false,smartExitExtendCents:5,
        signalSource:'snapshot',
      };
    }
  });
  useEffect(()=>{try{localStorage.setItem('tara_autoexec_v1',JSON.stringify(autoExecSettings));}catch(_){}},[autoExecSettings]);
  // V13.4.318: "Bet size ($)" under Position Sizing -- Stats Display (used only
  //   for the flat-rate "today" P&L estimate in todayData, tradingBetSize -- see
  //   tradingSettings.betSize's other read sites) had no link to Max bet/trade,
  //   the field the dollar-preset buttons and the real auto-exec sizing path
  //   (_resolveStakeDollars) actually use. Clicking a preset changed real order
  //   sizing but silently left this stats-only number stale, understating or
  //   overstating "today"'s P&L estimate by however far the two had drifted.
  //   Auto-mirror betSize to maxBetPerTrade on every change (preset click,
  //   Advanced free-text edit, mission math, anything); rescale winPayout by
  //   the same ratio so whatever avg-cost-per-contract assumption was implied
  //   by the PRIOR betSize/winPayout pair is preserved rather than reset to an
  //   arbitrary default. Skipped when maxBetPerTrade isn't a positive number
  //   (e.g. contracts entry mode, where a single dollar figure isn't
  //   meaningful) -- betSize keeps whatever value it last had.
  useEffect(()=>{
    const _cap=Number(autoExecSettings?.maxBetPerTrade);
    if(!(_cap>0))return;
    setTradingSettings(prev=>{
      const _prevBet=Number(prev?.betSize)||10;
      if(Math.abs(_prevBet-_cap)<0.005)return prev; // already in sync
      const _prevPayout=Number(prev?.winPayout)||8.5;
      const _ratio=_prevBet>0?(_prevPayout/_prevBet):0.85;
      return{...prev,betSize:_cap,winPayout:Math.round(_cap*_ratio*100)/100};
    });
  },[autoExecSettings?.maxBetPerTrade]);
  // ── V9.7.0: MISSION MODE ─────────────────────────────────────────────────
  // Bankroll-target trading. User configures a starting amount, target, end
  // date, and drawdown floor. Tara sizes each trade using fractional Kelly
  // with cluster-specific WR. Hard stops at floor and target.
  // Stored in localStorage AND mirrored to localStorage 'tara_mission_v1'.
  // Cloud sync deliberately not added — this is a personal account state and
  // shouldn't share across devices/users.
  const[mission,setMission]=useState(()=>{
    try{
      const v=JSON.parse(localStorage.getItem('tara_mission_v1')||'{}');
      return{
        active:!!v.active,
        startBankroll:Number(v.startBankroll)>0?Number(v.startBankroll):0,
        currentBankroll:Number(v.currentBankroll)>=0?Number(v.currentBankroll):0,
        target:Number(v.target)>0?Number(v.target):0,
        startDate:v.startDate||null,         // ISO string
        endDate:v.endDate||null,             // ISO string
        floor:Number(v.floor)>=0?Number(v.floor):0,
        // Tunable Kelly aggression. 0.25 = quarter Kelly (conservative default).
        // 0.5 = half Kelly. 1.0 = full Kelly (aggressive, higher bust risk).
        kellyMult:Number(v.kellyMult)>0?Number(v.kellyMult):0.25,
        maxBetFraction:Number(v.maxBetFraction)>0?Number(v.maxBetFraction):0.15,
        // Status: 'active' | 'hit' | 'busted' | 'expired' | 'paused'
        status:v.status||'inactive',
        // Trade tracking
        tradesAttempted:Number(v.tradesAttempted)||0,
        tradesWon:Number(v.tradesWon)||0,
        tradesLost:Number(v.tradesLost)||0,
        // Last update timestamp for the bankroll
        lastBankrollUpdate:Number(v.lastBankrollUpdate)||0,
        // History of bankroll points for sparkline {t, b}[]
        bankrollHistory:Array.isArray(v.bankrollHistory)?v.bankrollHistory.slice(-200):[],
        // V13.4.309: real dedup for the settlement effect below -- taraCallLog
        //   entry ids already folded into currentBankroll, so a re-render (or a
        //   reload, since this is part of the persisted mission blob, unlike
        //   the useRef it replaces) never double-counts the same window.
        settledWindowIds:Array.isArray(v.settledWindowIds)?v.settledWindowIds.slice(-500):[],
        // Windows resolved WIN/LOSS with no matching settlement-ledger entry
        //   (manual trade, pre-v13.4.309 trade, or a reload between fill and
        //   settlement -- the ledger is a plain ref, not persisted). Counted
        //   toward trades/WR but skipped for the bankroll delta; this number
        //   is how that gap gets disclosed instead of silently misreported.
        bankrollDataGaps:Number(v.bankrollDataGaps)||0,
      };
    }catch(_){return{active:false,startBankroll:0,currentBankroll:0,target:0,startDate:null,endDate:null,floor:0,kellyMult:0.25,maxBetFraction:0.15,status:'inactive',tradesAttempted:0,tradesWon:0,tradesLost:0,lastBankrollUpdate:0,bankrollHistory:[],settledWindowIds:[],bankrollDataGaps:0};}
  });
  useEffect(()=>{try{localStorage.setItem('tara_mission_v1',JSON.stringify(mission));}catch(_){}},[mission]);
  // Kill switch is in-memory + localStorage. When engaged, all auto-exec is gated off
  // INSTANTLY regardless of autoExecSettings.enabled. Survives reloads.
  const[killSwitchEngaged,setKillSwitchEngaged]=useState(()=>{
    try{return localStorage.getItem('tara_kalshi_kill')==='1';}catch(_){return false;}
  });
  useEffect(()=>{try{if(killSwitchEngaged)localStorage.setItem('tara_kalshi_kill','1');else localStorage.removeItem('tara_kalshi_kill');}catch(_){}},[killSwitchEngaged]);
  // Auto-exec cooldown — set when loss streak triggers, blocks new orders for cooldownMinutes.
  const[autoExecCooldownUntil,setAutoExecCooldownUntil]=useState(()=>{
    try{const v=Number(localStorage.getItem('tara_autoexec_cooldown')||'0');return v>Date.now()?v:0;}catch(_){return 0;}
  });
  useEffect(()=>{try{if(autoExecCooldownUntil>Date.now())localStorage.setItem('tara_autoexec_cooldown',String(autoExecCooldownUntil));else localStorage.removeItem('tara_autoexec_cooldown');}catch(_){}},[autoExecCooldownUntil]);
  // Active auto-exec order state. Tracks the order Tara placed for the current lock.
  // Cleared when the position closes (window roll, manual close, or auto-exit fill).
  // Shape: written by _runEntry / _runExit as
  //   {status, dir, ticker, dryRun, manual, offerCents, requestedCount, windowId,
  //    asset, placedAt, fairValueCents, attempts, ladderPath, at,
  //    rungCents, takesSpread, step,
  //    filledCount, filledAtCents, tookSpread, savedVsTakeCents, kalshiAtFill,
  //    exitReason, exitOrder, exitAtCents, pnlCentsPerContract,
  //    reason (on error)}
  // V13.4.291: the previous version of this comment described
  //   {orderId, side, count, limitCents, placedAt, fillPrice, exitOrderId, error}
  //   -- a contract NO writer ever honoured. Readers trusted it and hand-read
  //   those names, which is the origin of this file's worst bug family: the log's
  //   entry-pricing block, the auto-exec status panel, the position reconciler and
  //   the persisted realized-P&L block were ALL dead, silently, for their whole
  //   lifetime. Read through readOrderState rather than these keys; it accepts both
  //   the real names and the legacy ones. If you add a field here, add a writer.
  const[autoOrderState,setAutoOrderState]=useState(()=>readExecutionState(localStorage));
  useEffect(()=>{try{writeExecutionState(localStorage,autoOrderState);}catch{console.warn('[execution] durable state unavailable; new entry will fail closed');}},[autoOrderState]);
  const autoOrderStateRef=useRef(autoOrderState);
  useEffect(()=>{autoOrderStateRef.current=autoOrderState;},[autoOrderState]);
  const[exchangeAudit,setExchangeAudit]=useState({status:'idle',at:0,audit:null,errors:[]});
  const refreshExchangeAudit=useCallback(async()=>{
    const creds=kalshiCreds||{};
    if(!creds.apiKeyId||!creds.privateKeyPem){
      setExchangeAudit({status:'unavailable',at:Date.now(),audit:null,errors:['Connect Kalshi to read account activity.']});
      return null;
    }
    setExchangeAudit(prev=>({...prev,status:'loading',errors:[]}));
    // Read-only, on demand, never synced to the public Call store. A cursor
    // limit or a failed endpoint produces a partial audit, never fake profit.
    const fetchPages=(base,key)=>fetchAllExecutionPages(path=>kalshiAuthedFetch({apiKeyId:creds.apiKeyId,privateKeyPem:creds.privateKeyPem,method:'GET',path,timeoutMs:12000}),base,key);
    try{
      const [orders,fills,historicalFills,settlements,historicalOrders,positions,balance]=await Promise.all([
        fetchPages('/portfolio/orders','orders'),
        fetchPages('/portfolio/fills','fills'),
        fetchPages('/historical/fills','fills'),
        fetchPages('/portfolio/settlements','settlements'),
        fetchPages('/historical/orders','orders'),
        fetchPages('/portfolio/positions?count_filter=position','market_positions'),
        kalshiAuthedFetch({apiKeyId:creds.apiKeyId,privateKeyPem:creds.privateKeyPem,method:'GET',path:'/portfolio/balance',timeoutMs:12000}),
      ]);
      const allFills=[...fills.items,...historicalFills.items];
      const allOrders=[...orders.items,...historicalOrders.items];
      const audit=buildExchangeAudit({orders:allOrders,fills:allFills,
        settlements:settlements.items,
        complete:{orders:orders.ok,fills:fills.ok,historicalFills:historicalFills.ok,settlements:settlements.ok},
        localEvents:_autoExecLogGet()});
      audit.risk=accountRiskSnapshot({orders:allOrders,fills:allFills,positions:positions.items,balance:balance.data,
        complete:{orders:orders.ok,historicalOrders:historicalOrders.ok,fills:fills.ok,historicalFills:historicalFills.ok,settlements:settlements.ok,positions:positions.ok,balance:balance.ok}});
      const errors=[orders,fills,historicalFills,settlements,historicalOrders,positions,balance].filter(r=>!r.ok).map(r=>r.error||r.reason||'Account endpoint unavailable');
      setExchangeAudit({status:errors.length?'partial':'ready',at:Date.now(),audit,errors});
      return audit;
    }catch(error){
      setExchangeAudit({status:'unavailable',at:Date.now(),audit:null,
        errors:[String(error?.message||error)]});
      return null;
    }
  },[kalshiCreds]);
  // V10.2.10 — POSITION RECONCILIATION (Phase 2). Polls /portfolio/positions
  //   every 30s when there's reason to believe Tara should be tracking
  //   something on Kalshi. Detects three categories of drift between Tara's
  //   internal state and the actual Kalshi book:
  //
  //   1. PHANTOM (Tara has it, Kalshi doesn't):
  //      - autoOrderState says filled, but Kalshi shows 0 contracts on that
  //        ticker. Means user manually closed on Kalshi or order errored
  //        after partial fill. Auto-exec exit would fail.
  //      - manualKalshiEntry says N contracts at X¢, but Kalshi shows 0.
  //        Means user forgot to clear the entry after closing.
  //
  //   2. UNDERCOUNT (Tara has more than Kalshi):
  //      - autoOrderState says filled 4 contracts, Kalshi shows 2. Partial
  //        close on Kalshi side.
  //
  //   3. UNKNOWN (Kalshi has it, Tara doesn't):
  //      - Tara state empty but Kalshi shows positions on the active ticker.
  //        Means user manually entered without telling Tara. Tara's analysis
  //        is operating on the wrong position basis.
  //
  //   The poll does NOT auto-correct (too risky — could submit wrong orders).
  //   It surfaces a banner so the user can resolve manually:
  //   - Tap the banner → console gets full diff
  //   - Or run window.__taraPositionReconciliation() any time
  //
  //   Shape: {
  //     status: 'in-sync' | 'drift' | 'error' | 'idle',
  //     driftCount: number,
  //     driftDetails: [{kind, ticker, taraCount, kalshiCount, side}],
  //     kalshiPositions: [{ticker, count, side}],
  //     lastCheckAt: ms,
  //     lastErrorMsg: string | null,
  //   }
  const[positionReconciliation,setPositionReconciliation]=useState({
    status:'idle',driftCount:0,driftDetails:[],kalshiPositions:[],lastCheckAt:0,lastSuccessAt:0,lastErrorMsg:null,
  });
  // Refs so the poll function reads current values without re-running on every state change
  const _manualKalshiEntryRef=useRef(null);
  const _userPositionRef=useRef(null);
  const _kalshiActiveMarketRef=useRef(null);
  // Per-window dedup so we never double-fire on the same lock (e.g., on a forceRender pulse).
  const _autoExecLastFiredKeyRef=useRef('');
  // V9.17.27: track last logged key for the manual-eval log so we don't spam
  //   the console every analysis pulse when autoOrderState is stuck in 'error'.
  const _autoExecLastLoggedKeyRef=useRef('');
  // V10.0.0 Phase 4: holds the Tara's Trade timing engine decision from the
  //   most recent lock evaluation. Read by the call-log writer downstream to
  //   attach tradeTimingDecision/Score/Reason CSV columns. Shape:
  //     { decision, score, reason, waitForCondition, waitMaxSec, mode, ts }
  const _phase4DecisionRef=useRef(null);
  // Connection status from a manual ping
  const[kalshiPingState,setKalshiPingState]=useState({state:'idle',msg:'',balance:null,at:0});
  // V8.2: Anti-tilt cooldown state — set when streak reaches threshold, blocks new entries
  //   for tradingSettings.antiTiltMinutes. Cleared on first WIN. Persists across reloads.
  const[tiltLockUntil,setTiltLockUntil]=useState(()=>{
    try{
      const v=Number(localStorage.getItem('taraTiltLockUntil_v1')||'0');
      return v>Date.now()?v:0;
    }catch(_){return 0;}
  });
  useEffect(()=>{
    try{
      if(tiltLockUntil>Date.now())localStorage.setItem('taraTiltLockUntil_v1',String(tiltLockUntil));
      else localStorage.removeItem('taraTiltLockUntil_v1');
    }catch(_){}
  },[tiltLockUntil]);
  // V138: Premium Mode removed entirely. The asymmetric blocks it carried (US session skip,
  //       weak RC skip, SS/HVC DOWN block, MTF Confluence cross-window veto) were over-filtering.
  //       Score math is now the only directional bias.
  // V9.10.9: premiumMode state removed — was already commented out; deleting the comments.
  const newsSentiment=useNewsSentiment(); // V114
  // V138: Clean up stale Premium Mode localStorage entry from previous installs
  useEffect(()=>{try{localStorage.removeItem('taraPremiumMode');}catch(e){}},[]);
  const[mobileTab,setMobileTab]=useState('signal'); // signal | chart | logs
  // V9.16: New simplified default layout. simpleMode=true shows the new
  //   Overview layout (call + coach + chart + session bar). simpleMode=false
  //   shows the V9.15 legacy layout exactly as before — failsafe during the
  //   transition.
  // overviewTab tracks which secondary tab is active under the Overview layout.
  //   Default 'overview' = primary view. Other tabs reuse existing modals/strips.
  // V13.4.38: advanced view retired -- simpleMode is permanently true. The
  //   advanced (V9.15 fallback) layout code is left in place but unreachable,
  //   so this is fully reversible if it's ever wanted back. setSimpleMode is
  //   kept as a no-op so any lingering callers don't error.
  const[simpleMode]=useState(true);
  const setSimpleMode=()=>{};
  // V13.4.254: removed a mount effect that wrote taraSimpleMode_v1='true' and
  //   was never read. It set a flag nothing consulted.
  const[overviewTab,setOverviewTab]=useState('overview'); // overview | tape | schedule | stats | brain | settings
  // V9.17: SCALPER state. Separate from Tara's lock state. State machine:
  //   IDLE → SUGGEST → HOLD (after user enters) → exit → COOLDOWN → IDLE
  // Settings persisted; log persisted; suggestion timestamps in memory only.
  const[scalperSettings,setScalperSettings]=useState(()=>loadScalperSettings());
  useEffect(()=>{saveScalperSettings(scalperSettings);},[scalperSettings]);
  const[scalperLog,setScalperLog]=useState(()=>loadScalperLog());
  useEffect(()=>{saveScalperLog(scalperLog);},[scalperLog]);
  // scalperState: active position (if any) — null when no open scalp
  //   { side, entryCents, entryTime, contracts, suggestionRead, asset, suggestedTargetCents, suggestedStopCents }
  const[scalperPosition,setScalperPosition]=useState(()=>{
    try{const s=localStorage.getItem('taraScalperPosition_v1');return s?JSON.parse(s):null;}catch(_){return null;}
  });
  useEffect(()=>{
    try{
      if(scalperPosition)localStorage.setItem('taraScalperPosition_v1',JSON.stringify(scalperPosition));
      else localStorage.removeItem('taraScalperPosition_v1');
    }catch(_){}
  },[scalperPosition]);
  // Ref for tracking suggestion timestamps (hourly cap math). Not persisted.
  const scalperSuggestionsRef=React.useRef([]);
  // Ref for the latest dismissed suggestion — so we don't loop re-emitting same setup
  const scalperDismissedRef=React.useRef({signature:null,until:0});
  const[resolution,setResolution]=useState('1m'); // chart timeframe: 1m | 5m | 15m | 1h
  const[currentPrice,setCurrentPrice]=useState(null);
  const[tickDirection,setTickDirection]=useState(null);
  const currentPriceRef=useRef(null);
  // ─── V10.5.1: CROSS-EXCHANGE BRTI APPROXIMATION ────────────────────────
  //   Kalshi settles on BRTI (Bitcoin Real-Time Index) — a 60-second trimmed
  //   mean from 8 institutional exchanges. We can't get the BRTI feed directly
  //   (institutional pricing), but we can APPROXIMATE it by polling 4 consumer
  //   exchanges (Coinbase, Kraken, OKX, Bitstamp) and computing a trimmed mean.
  //
  //   Phase 1 (V10.5.1): Just COLLECT and TELEMETER. Compare brtiApprox to
  //   Tara's single-source currentPrice. If they diverge meaningfully, we'll
  //   know — Phase 2 will wire it into the engine.
  //
  //   Sample shape: { p: number, t: timestamp, sources: [{name, price}] }
  //   State holds: { current: trimmedMean, samples: [last 60s], lastUpdate, diverged }
  const[brtiApprox,setBrtiApprox]=useState(null);
  // V10.7.59b: ref so BRTI price interval always reads latest value (stale closure fix)
  const brtiApproxRef=useRef(null);
  useEffect(()=>{brtiApproxRef.current=brtiApprox;},[brtiApprox]);
  const _brtiSamplesRef=useRef([]); // rolling 60s window of multi-exchange samples
  // V10.9.10: rolling EMA offset (bps) of each hot exchange (OKX/Bybit) vs the
  //   CB/KR anchor mean — used to bias-correct hot feeds onto the CF-Benchmark
  //   scale so they stay usable in the blend even when the anchors drop out.
  const _brtiOffsetRef=useRef({});
  // ─── V9.8.18: TARA TOAST SYSTEM ─────────────────────────────────────────
  // Stack of active popup notifications. State is array-of-toasts; per-kind cooldowns
  //   live on a ref so successive setToasts calls don't lose dedup state. pushToast
  //   enforces cooldown and a 3-toast cap. dismissToasts handles single + 'all'.
  const[toasts,setToasts]=useState([]);
  const _toastCooldownRef=useRef({}); // {kind: lastFiredMs}
  const pushToast=React.useCallback((kind,title,body,options)=>{
    const _opts=options||{};
    const _now=Date.now();
    const _cooldown=_opts.cooldown!=null?_opts.cooldown:60000;
    const _lastFired=_toastCooldownRef.current[kind]||0;
    if(_now-_lastFired<_cooldown)return; // throttled — drop silently
    _toastCooldownRef.current[kind]=_now;
    const id=_now+Math.random();
    const expireAt=_now+(_opts.durationMs||5500);
    setToasts(prev=>{
      const trimmed=prev.length>=3?prev.slice(prev.length-2):prev;
      return[...trimmed,{id,kind,title,body,expireAt,color:_opts.color,icon:_opts.icon,url:_opts.url}];
    });
  },[]);
  const dismissToasts=React.useCallback((idsOrAll)=>{
    if(idsOrAll==='all'){setToasts([]);return;}
    setToasts(prev=>prev.filter(t=>!idsOrAll.includes(t.id)));
  },[]);
  // V9.8.18: rolling 60s price trail for pump/dump detection. Captures every
  //   currentPrice tick so we can compute "price change over last 30s" cheaply
  //   on each new tick. Trimmed to 60s window so memory stays bounded.
  const _priceTrailRef=useRef([]); // [{t, p}]
  const _prevVelRegimeRef=useRef('NORMAL');
  // ────────────────────────────────────────────────────────────────────────
  // V7.6: per-asset price cache. Background feed keeps the inactive asset's price warm
  //   so tab switches don't show stale data. Shape: { BTC: {price, time}, ETH: {...} }
  const priceByAssetRef=useRef({});
  // V7.7: SHADOW TARA — per-asset directional view computed in the background for the
  //   inactive asset. Shape: { BTC: {leanDir, confidence, posterior, strike, currentPrice,
  //   regime, updatedAt}, ETH: {...} }. Updated every 5s via the shadow useEffect.
  const shadowTaraByAssetRef=useRef({});
  // V9.6.1: shadow diagnostics — per-asset failure counters and last-error string.
  //   Surfaced in DualAssetCallStrip on tap to debug "feed stale" without devtools.
  const shadowDiagRef=useRef({});
  const tickHistoryRef=useRef([]);
  const priceMemoryRef=useRef([]);
  const lastPriceSourceRef=useRef({source:'none',time:0});
  // V10.7.86: Seed tickHistoryRef with synthetic ticks from warm state candles.
  //   Real ticks accumulate from WebSocket from now, but pre-seeding with candle
  //   close prices gives tape flow and velocity calculations something to work with
  //   immediately rather than waiting 30-60 seconds for real ticks to accumulate.
  useEffect(()=>{
    if(_warmState?.history?.length>=5&&tickHistoryRef.current.length===0){
      try{
        const syntheticTicks=[];
        const now=Date.now();
        // Create 2 synthetic ticks per candle (open + close) going back through history
        // Mark as synthetic so they don't count toward whale detection
        _warmState.history.slice(0,20).forEach((candle,i)=>{
          const age=(i+1)*60000; // space them 1 min apart, oldest furthest back
          syntheticTicks.push({p:candle.o,s:0.1,t:'B',time:now-age-30000,ex:'warm',synthetic:true});
          syntheticTicks.push({p:candle.c,s:0.1,t:'B',time:now-age,ex:'warm',synthetic:true});
        });
        tickHistoryRef.current=syntheticTicks.sort((a,b)=>a.time-b.time);
        try{console.info(`[V10.7.86] Seeded ${syntheticTicks.length} synthetic ticks from warm candles`);}catch(_){}
      }catch(_){}
    }
  },[]);
  // V10.7.86: WARM STATE RESTORE — seed candle history from sessionStorage on mount.
  //   Previously every tab open started with empty history: 0 candles, blank tape, no ADX/BBW.
  //   The engine needs ~20 candles for stable ADX, ~50 ticks for tape flow, ~5 min for VWAP.
  //   Without warm state, first 3-5 windows have partial signals and worse calls.
  //   Fix: save candles + tfCandles to sessionStorage on every update (fast, in-tab memory).
  //   On mount, seed history instantly from the saved state so signals are warm immediately.
  //   sessionStorage survives refresh but not tab close — that's fine, candles are fresh enough.
  //   Age gate: discard warm state older than 5 minutes (candles would be stale anyway).
  const _WARM_STATE_KEY='taraWarmState_v1';
  const _WARM_STATE_MAX_AGE_MS=5*60*1000; // 5 minutes
  const _loadWarmState=()=>{
    try{
      const raw=sessionStorage.getItem(_WARM_STATE_KEY);
      if(!raw)return null;
      const ws=JSON.parse(raw);
      if(!ws||!ws.savedAt)return null;
      if(ws.asset!==currentAsset)return null;
      if(Date.now()-ws.savedAt>_WARM_STATE_MAX_AGE_MS)return null;
      return ws;
    }catch(_){return null;}
  };
  const _warmState=_loadWarmState();
  // Startup eligibility is evaluated from candle coverage and real live
  // observations below, rather than a cold-at-mount flag held for 75 minutes.
  const[history,setHistory]=useState(()=>{
    // Seed from warm state if available and fresh
    if(_warmState?.history?.length>=10){
      try{console.info(`[V10.7.86] Warm state restore: ${_warmState.history.length} candles from ${Math.round((Date.now()-_warmState.savedAt)/1000)}s ago`);}catch(_){}
      return _warmState.history;
    }
    return [];
  });
  const[orderBook,setOrderBook]=useState({localBuy:0,localSell:0,imbalance:1,bands:null});
  // V10.7.86: Save candle history to sessionStorage on every update (debounced 2s).
  //   Restored on next mount via the warm state init above. Keeps signals warm across
  //   refreshes and short tab reopens. sessionStorage is cleared when browser tab closes,
  //   which is fine — data >5min old is discarded anyway.
  const _warmSaveRef=useRef(null);
  useEffect(()=>{
    if(history.length<5)return;
    if(_warmSaveRef.current)clearTimeout(_warmSaveRef.current);
    _warmSaveRef.current=setTimeout(()=>{
      try{
        sessionStorage.setItem(_WARM_STATE_KEY,JSON.stringify({
          savedAt:Date.now(),
          asset:currentAsset,
          history:history.slice(0,100),
        }));
      }catch(_){}
    },2000);
  },[history,currentAsset]);
  const[liquidations,setLiquidations]=useState([]);
  const[targetMargin,setTargetMargin]=useState(0);
  // V5.7.5: Mirror targetMargin in a ref so deferred rollover scoring (~8s after rollover)
  //   can read the latest value. After rollover, Kalshi's fetch refreshes targetMargin to
  //   the NEW window's strike — which equals the OLD window's close price per Kalshi's feed.
  //   Reading it from a ref dodges the closure-capture problem in the setTimeout.
  const targetMarginRef=useRef(0);
  useEffect(()=>{targetMarginRef.current=targetMargin;},[targetMargin]);
  const hasSetInitialMargin=useRef(false);
  // V5.5.5: When true, signals the fill-on-tick effect to set strike from next currentPrice.
  //   Set true at window rollover or page-load, set false after fill happens.
  const needsCapturedPriceRef=useRef(false);
  // Auto-strike: tracks the window's opening price, fetched at each new window
  const windowOpenPriceRef=useRef(0);
  // V10.8.2: Mid-window cushion history — tracks price vs Kalshi strike every 30s.
  //   Answers: has price been consistently above/below the strike, or oscillating?
  //   {t: elapsed_ms, cushionBps: (spot-strike)/strike*10000, spotPrice}
  const _windowCushionHistoryRef=useRef([]);
  const _postLockTrajRef=useRef({}); // V13.4.0: post-lock cushion trajectory {windowId:{ticks,correct,peakBps,everAhead,reversed,wasAhead}} for reversal-vs-wrong-from-start
  // V13.4.165: primaryDeviceIdRef + its cloudWatch removed with the secondary-device
  //   guard. Nothing reads settings/primaryDevice any more, and an unused realtime
  //   subscription is not free -- it held a live channel and burned egress to keep a
  //   value nothing consumed.
  const _syncLatencyRef=useRef([]);
  const _syncLatencySeenRef=useRef(new Set());
  const _cushionLastPushedRef=useRef(0);
  // V3.1.7+: Window amplitude tracking — high/low since window open. Reset on rollover.
  //         Powers the "is this a wild window or a dead window" classification.
  // V3.1.9: Also track WHEN high/low were first reached (windowHighTime / windowLowTime)
  //         so we can detect SPIKE-FADE (motion early) vs LATE-BREAK (motion late).
  const windowHighRef=useRef(0);
  const windowLowRef=useRef(0);
  const windowHighTimeRef=useRef(0);
  const windowLowTimeRef=useRef(0);
  const windowOpenTimeRef=useRef(0);
  // V10.9.14: track the sequence of windowAmplitude labels seen during the live
  //   window, so each trade can record what window type it locked into AND whether
  //   that read changed during the window (e.g. opened GRIND, became WHIPSAW).
  //   Answers: how stable is the live window-type read, and does locking after a
  //   transition lose more? Also stamps the active price feed + lock latency.
  const _windowTypeSeqRef=useRef([]);
  // V10.9.3: AUTONOMOUS THEORY LAB — fully additive, dormant, zero-input.
  //   _theoryPendingRef holds the four theory calls computed for the CURRENT
  //   window (set at window open), keyed by windowId. When that window settles
  //   (detected via `pastWindows`), the calls are scored against the actual
  //   outcome and pushed into _theoryLog. Weight=0 always — these never touch
  //   totalScore, posterior, or any lock decision. Pure shadow tracking.
  const _theoryPendingRef=useRef(null); // {windowId, range_call, momentum_call, first_move_fade_call, crowd_fade_call, prev_high, prev_low, first_move_dir, crowd_pct, round_number_flag, strike, openTime}
  const _theoryLastWindowRef=useRef(''); // separate from lastWindowRef — own rollover detection
  const _theoryFirstMoveRef=useRef(null); // {wid, openPrice, openTime, sampled:boolean}
  const _theoryScoredWidsRef=useRef(new Set()); // dedup — don't double-score a window
  const[theoryLog,setTheoryLog]=useState([]); // {windowId,time,...calls,...correct flags}
  const theoryLogRef=useRef([]);
  useEffect(()=>{theoryLogRef.current=theoryLog;},[theoryLog]);
  const[theoryStats,setTheoryStats]=useState({
    range:{n:0,correct:0},momentum:{n:0,correct:0},
    firstMoveFade:{n:0,correct:0},crowdFade:{n:0,correct:0},
  });
  const[theoryPromoNotified,setTheoryPromoNotified]=useState({}); // {theoryKey:true} — one-time promotion suggestion

  // V9.1.8: Track when the user opened the app this session. If they open mid-window
  //   with little time left, that's a real "I missed this window" SITOUT and should
  //   count. If the engine simply didn't form a call during a normal window, we no
  //   longer log it as a SITOUT — let the window roll over without an entry.
  const _mountTimeRef=useRef(Date.now());
  // V9.2.0: Rollover grace — timestamp of last window transition. Engine IIFE checks
  //   this to force SEARCH mode for 5s after rollover, preventing stale-analysis
  //   instant-lock on the new window.
  const _rolloverGraceRef=useRef(0);
  // V13.4.347: loss-streak cooldown, explicit user request 2026-09-11 ("a
  //   cooldown of 2 windows where tara's call lock sits out after 2 losses
  //   in a row"). windowsRemaining counts down by 1 at every window
  //   rollover (below); triggeredForId is the settled id of the most
  //   recent loss that already started a cooldown, so re-checking the same
  //   still-most-recent pair on every re-render doesn't re-arm it forever.
  //   Read from the taraCall post-processor cascade further down.
  const _lossCooldownRef=useRef({windowsRemaining:0,triggeredForId:null});
  // V9.2.2: ML model state — declared here, training effect runs after taraCallLog is available (below).
  const learning=useLearningRegistry();
  const[taraMLModel,setTaraMLModel]=useState(()=>{
    try{
      const stored=localStorage.getItem('taraML_model_v1');
      // Preserve the old file for audit, but do not activate an in-sample fit.
      if(stored)return null;
    }catch(_){}
    return null;
  });
  const _mlTrainDebounceRef=useRef(null);
  // V9.2.1: Cross-asset correlation. Stores the OTHER asset's recent price data
  //   received via BroadcastChannel from the other tab. Used by the engine to
  //   detect correlation (BTC dumps → penalize ETH UP, etc).
  const _otherAssetRef=useRef({asset:null,price:0,price5mAgo:0,deltaBps:0,lastUpdate:0});
  // Broadcast this tab's price to other tabs every 5s
  useEffect(()=>{
    if(!_crossAssetChannel||!currentPrice||!currentAsset)return;
    const _send=()=>{
      try{
        _crossAssetChannel.postMessage({
          type:'PRICE_UPDATE',
          asset:currentAsset,
          price:currentPrice,
          windowOpenPrice:windowOpenPriceRef.current||0,
          at:Date.now(),
        });
      }catch(_){}
    };
    _send();
    const iv=setInterval(_send,5000);
    return()=>clearInterval(iv);
  },[currentPrice,currentAsset]);
  // Listen for the other asset's price
  useEffect(()=>{
    if(!_crossAssetChannel)return;
    const _handler=(ev)=>{
      const d=ev.data;
      if(!d||d.type!=='PRICE_UPDATE'||d.asset===currentAsset)return;
      const _prev=_otherAssetRef.current;
      const _price5mAgo=(_prev.asset===d.asset&&_prev.price>0&&(Date.now()-_prev.lastUpdate)>4000)?_prev.price:d.windowOpenPrice||d.price;
      const _delta=_price5mAgo>0?((d.price-_price5mAgo)/_price5mAgo)*10000:0;
      _otherAssetRef.current={asset:d.asset,price:d.price,price5mAgo:_price5mAgo,deltaBps:_delta,lastUpdate:Date.now()};
    };
    _crossAssetChannel.addEventListener('message',_handler);
    return()=>_crossAssetChannel.removeEventListener('message',_handler);
  },[currentAsset]);
  // ── V8.5: WITHIN-WINDOW CONTEXT TRACKING ────────────────────────────────
  // Captures whale activity + macro shocks DURING the active window so we can
  //   classify loss patterns at close (LATE_REVERSAL vs WHALE_SPIKE vs MACRO_SHOCK).
  // Reset on rollover.
  const windowWhaleStatsRef=useRef({
    count:0,                         // total whale prints during window
    contraryUsd:0,                   // largest whale opposing user's position (USD)
    contraryClockSec:null,           // clockSec remaining when largest contrary fired
    contrarySide:null,               // 'BUY' or 'SELL'
  });
  const windowMaxGeoRiskRef=useRef(0); // peak geoRisk seen during window (0-1 scale)
  const windowHadMacroEventRef=useRef(false); // any macro event fired during window
  // When user manually edits strike, this is true — we skip auto-set until next window
  const isManualStrikeRef=useRef(false);
  const[strikeMode,setStrikeMode]=useState('auto'); // 'auto' | 'manual'
  const[strikeConfirmed,setStrikeConfirmed]=useState(true); // false while user is typing — gates analysis
  // Strike confirmation: when a new window starts, show pending price with OK button
  const[pendingStrike,setPendingStrike]=useState(null); // null | number — awaiting user OK
  const[betAmount,setBetAmount]=useState(0);
  const[maxPayout,setMaxPayout]=useState(0);
  const[currentOffer,setCurrentOffer]=useState('');
  const[windowType,setWindowType]=useState('15m');
  const[chartRes,setChartRes]=useState('1m');
  const[timeState,setTimeState]=useState({currentTime:'',startWindow:'',nextWindow:'',nextWindowMs:0,minsRemaining:0,secsRemaining:0,currentHour:0});
  const[isLoading,setIsLoading]=useState(true);
  // ── COMMITTED LOCK STATE MACHINE ──
  // These refs form an immutable lock per window. Once locked, only reality caps can unlock.
  const taraAdviceRef=useRef('SEARCHING...');
  // V6.1.3: ENGINE STICKINESS. Once engine first hits CONFIRMED in a window, lock that
  //   direction. Display can transition to EXIT WARNING if conditions reverse, but never
  //   flips to opposite direction or back to FORMING/CLEARED. User: "once a directional
  //   advice on the predictor it can't flip. Can only advise me to exit/cashout if it
  //   goes bad." Reset on window change.
  const engineLockedDirRef=useRef(null);
  const lockedCallRef=useRef(null);        // null | {dir:'UP'|'DOWN', lockedAt:timestamp, lockedPosterior:number, lockedRegime:string, lockPrice:number}
  // V5.6.11: Cooldown after lock release. Prevents rapid flip-flopping (engine LOCKED UP →
  //   triggers release on a noise tick → immediately re-locks DOWN within seconds). After
  //   any lock release, engine must scan for 45s before re-locking. This kills the visible
  //   "advisor keeps flipping" behavior the user reported.
  const lockReleasedAtRef=useRef(0);
  // V9.8.14: throttle ref for mid-trade time-series capture. Engine ticks fire sub-second
  //   while a lock is active; we only want a snapshot every ~5s so call-log entries don't
  //   bloat. ~17 minutes of 5s samples = 200 entries × ~80 bytes = 16 KB per call. Safe.
  const _lastTimeSeriesAtRef=useRef(0);
  // V5.7.8: Confluence state ref — engine cooldown reads this to shorten the post-release
  //   window (45s → 15s) when current candidate is confluent. Updated each cycle by the
  //   Tara's call gate based on _ctx.isConfluent / isSuperConfluent.
  const confluenceStateRef=useRef({isConfluent:false,isSuperConfluent:false});
  const posteriorHistoryRef=useRef([]);    // rolling 10-sample history for confirming lock
  const biasCountRef=useRef({UP:0,DOWN:0}); // consecutive samples in same direction before lock
  const peakOfferRef=useRef(0);
  const liveCoachReversalRef=useRef({fired:false,peakBps:0,drawdownBps:0}); // V13.4.2: stamped by LiveTradeCoach's 'Drawing down from peak' urgent card; read at rollover
  const hasReversedRef=useRef(false);
  // Tracks whether user manually closed the trade this window (prevents double-scoring at rollover)
  // Values: null=no trade, 'WIN'=user cashed out profit, 'LOSS'=user cut losses
  const manuallyClosedRef=useRef(null);
  const[positionEntry,setPositionEntry]=useState(null);
  // V13.4.351: positionEntry is an advisory marker, not proof of a Kalshi fill.
  // The reconciliation poll needs the marker's source so a confirmed auto no-fill
  // can clear only the auto-follow marker, never a real manually logged position.
  const _positionEntryRef=useRef(null);
  _positionEntryRef.current=positionEntry;
  // V7.10.5: scorecards now persist to localStorage AND sync to cloud. Previously the
  //   user's personal W/L tally was in-memory only — reset every reload AND inconsistent
  //   across devices. Initial value reads localStorage seed (cached from last session).
  // V9.1.9: scorecards is now DERIVED from taraCallLog after that state is declared.
  //   Stubs here for refs that are used in later useEffects. Real assignment happens
  //   below the taraCallLog state via React.useMemo (~line 12125 area).
  // V9.1.9: setScorecards is a no-op stub. Legacy callers (applyBaseline path,
  //   migrations) call this — but with derive-from-log it's redundant. The log
  //   itself is what carries scorecard data now. Stub returns void.
  const setScorecards=()=>{ /* derive-from-log; no-op for backward compat */ };
  // V9.1.9: hydration ref kept for legacy reads but no longer used to gate writes.
  const _scorecardsHydratedRef=useRef(true);
  // V3.2.4: Tara's Call scorecard — separate from general prediction. Tracks UP/DOWN/SIT_OUT
  //   decisions where Tara has applied an additional conviction filter. SIT_OUT trades don't
  //   count for or against. Lets us measure "would Tara's selective calls win at higher rate?"
  // V5.7.1: taraScorecards is now DERIVED from taraCallLog instead of being its own
  //   incrementally-updated state. This eliminates the multi-device double-counting:
  //   each device incrementing locally + max-merging in cloud was racy (if PC bumped to 4
  //   and synced, then tab synced to 4, then tab bumped its own copy to 5, both ended
  //   at 5 for what should have been a single +1). With derive-from-log, the dedup'd
  //   call log is the single source of truth — no separate counter to drift.
  // V5.7.1 declarations moved to AFTER taraCallLog (V5.7.4 hotfix — TDZ fix).
  const _callLogHydratedRef=useRef(false);
  // V9.1.9: _scorecardsHydratedRef now declared earlier with the derived scorecards
  //   block. Legacy V7.10.5 hydration sentinel removed.
  // V3.2.4: Snapshot Tara's Call at endgame freeze. Whatever Tara is calling when the
  //   final 90s zone begins becomes "Tara's Call for this round." It gets resolved when
  //   the window rolls over. This ref persists across the snapshot→rollover transition.
  //   The snapshot is reset (to null) on rollover so the next window starts fresh.
  const taraCallSnapshotRef=useRef(null);
  // V9.18.2: per-window entry-price override (in cents, our-side i.e. UP→yes/DOWN→no).
  //   Set by the trade-ticket edit mode in ScalperAdvisorPanel. Cleared on
  //   window roll by the panel itself. The auto-exec entry effect reads this
  //   right before computing the limit price — if non-null and in [1,99] it
  //   replaces Tara's computed limit. Null = use default behavior (Tara's mid).
  const ticketEntryOverrideRef=useRef(null);
  // V9.18.3: hard cap 1 auto-exec placement attempt per window. Keys are
  //   `${windowId}|${asset}` strings. Added to the set the moment an order
  //   is dispatched (even if it errors). Cleared on window roll (handled in
  //   the entry effect — windowId in key naturally invalidates old entries).
  //   Manual override clears the specific windowId so the next entry-effect
  //   re-fire is allowed exactly once.
  // V9.19.4: persisted to localStorage with 24h TTL. Survives page refresh so
  //   a refresh mid-window can't bypass the cap. Auto-prunes stale entries.
  //   Storage shape: { [attemptKey]: epochMs }. attemptKey = `${windowId}|${asset}`.
  //   We keep a parallel Map(key→ts) so persists preserve original add timestamps
  //   (otherwise every persist refreshed TTL and entries never expired).
  const _attemptedWindowsTsRef=useRef(new Map());
  const _attemptedWindowsRef=useRef((()=>{
    try{
      const _raw=localStorage.getItem('taraAttemptedWindows_v1');
      if(!_raw)return new Set();
      const _parsed=JSON.parse(_raw);
      if(!_parsed||typeof _parsed!=='object')return new Set();
      const _now=Date.now();
      const _maxAge=24*60*60*1000; // 24h
      const _alive=new Set();
      for(const k of Object.keys(_parsed)){
        const t=Number(_parsed[k])||0;
        if(_now-t<_maxAge){
          _alive.add(k);
          _attemptedWindowsTsRef.current.set(k,t);
        }
      }
      try{console.info('[V9.19.4] hard-cap hydrated:',_alive.size,'live entries (TTL 24h)');}catch(_){}
      return _alive;
    }catch(_){return new Set();}
  })());
  // V10.2.4: per-window attempt COUNT (Map<key,count>). Backs the new
  //   maxAutoTradesPerWindow setting. In-memory only (resets on page refresh
  //   — that's OK because the Set above persists, and a fresh count of 0 with
  //   the Set entry still present is treated as 1 via the post-refresh
  //   fallback in the hard cap check below). Incremented on each placement.
  const _windowAttemptCountRef=useRef(new Map());
  // V10.2.24 — SHARED CROSS-SYSTEM 1-TRADE-PER-WINDOW HARD FLOOR.
  //   Problem this solves: auto-exec subsystems (main Tara auto-exec,
  //   scalper auto-exec, future Phase-4-pregate, future weak-cluster filter,
  //   etc.) all maintain their own counters. They mutually-exclude at the
  //   SETTINGS level (UI keeps them from both being 'enabled' at once), but
  //   if a user has main auto-exec off and scalper on, scalper has no
  //   per-window cap of its own — it just respects a 'don't fire while Tara
  //   is locked' guard. Multiple scalper trades CAN occur per Kalshi window.
  //
  //   This ref is the SHARED truth: any auto-exec subsystem that fires must
  //   call _claimWindowSlot(key, system). It returns true only if no slot
  //   has been claimed for that window yet. False blocks the trade.
  //
  //   Hard floor — NOT user-configurable. The maxAutoTradesPerWindow setting
  //   is now bounded to 1 in this ref (any value above 1 is ignored). Per-
  //   subsystem behavior (Tara's own counter, scalper's dedup, etc.) still
  //   runs as additional safeguards, but THIS ref is the final word.
  //
  //   Map shape: <windowKey, {system, ts, dir}>
  //   windowKey = `${asset}_${windowType}_${windowId}` (matches existing
  //   _attemptKey shape used in V10.2.4 path).
  //
  //   Cleared on rollover via the existing _windowAttemptCountRef.clear()
  //   site — same lifecycle, same key shape, so no separate GC needed.
  const _sharedWindowSlotRef=useRef(new Map());
  const _claimWindowSlot=React.useCallback((key,system,extra)=>{
    if(!key||!system)return{ok:false,reason:'missing key or system'};
    const _existing=_sharedWindowSlotRef.current.get(key);
    if(_existing){
      // Same system re-checking is OK — return true so it can proceed with its
      // own logic; this is just a defensive same-tick re-entry case.
      if(_existing.system===system)return{ok:true,owner:_existing,reentry:true};
      // Different system trying to fire after another already did. Block.
      try{console.info(`[V10.2.24] shared 1-trade floor: ${system} blocked, ${_existing.system} already fired in window ${key}`);}catch(_){}
      return{ok:false,reason:`window already used by ${_existing.system}`,owner:_existing};
    }
    _sharedWindowSlotRef.current.set(key,{system,ts:Date.now(),...(extra||{})});
    try{console.info(`[V10.2.24] shared 1-trade floor: ${system} claimed window ${key}`);}catch(_){}
    return{ok:true,claimed:true};
  },[]);
  // Console hook to inspect slot state — useful for debugging "why didn't scalper fire?"
  useEffect(()=>{
    if(typeof window==='undefined')return;
    window.__taraWindowSlots=()=>{
      // Light GC on read: drop entries older than 24h to bound map growth
      const _now=Date.now();
      const _TTL=24*60*60*1000;
      for(const[k,v]of _sharedWindowSlotRef.current.entries()){
        if(v?.ts&&(_now-v.ts)>_TTL)_sharedWindowSlotRef.current.delete(k);
      }
      const _entries=Array.from(_sharedWindowSlotRef.current.entries()).map(([k,v])=>({
        window:k,
        system:v.system,
        firedAt:new Date(v.ts).toLocaleTimeString(),
        dir:v.dir||'—',
      }));
      console.group('%c━━━ Shared 1-Trade-Per-Window Slots ━━━','color:#23B981;font-weight:bold');
      if(_entries.length===0)console.info('No windows claimed in this session.');
      else console.table(_entries);
      console.groupEnd();
      return _entries;
    };
  },[]);
  // V10.2.24: GC on a 1-hour cadence so map doesn't grow unbounded
  useEffect(()=>{
    const _gc=()=>{
      const _now=Date.now();
      const _TTL=24*60*60*1000;
      for(const[k,v]of _sharedWindowSlotRef.current.entries()){
        if(v?.ts&&(_now-v.ts)>_TTL)_sharedWindowSlotRef.current.delete(k);
      }
    };
    const _h=setInterval(_gc,60*60*1000);
    return()=>clearInterval(_h);
  },[]);
  // V9.19.4: persist write helper. Preserves each key's original timestamp.
  //   New keys (in set but not in ts map) get stamped now; existing keys keep
  //   their original stamp so TTL counts from first attempt, not last persist.
  const _persistAttemptedWindows=React.useCallback(()=>{
    try{
      const _now=Date.now();
      const _obj={};
      // Sync ts map with current set membership
      for(const k of _attemptedWindowsRef.current){
        if(!_attemptedWindowsTsRef.current.has(k)){
          _attemptedWindowsTsRef.current.set(k,_now);
        }
        _obj[k]=_attemptedWindowsTsRef.current.get(k);
      }
      // Drop ts entries whose set membership was removed
      for(const k of [..._attemptedWindowsTsRef.current.keys()]){
        if(!_attemptedWindowsRef.current.has(k)){
          _attemptedWindowsTsRef.current.delete(k);
        }
      }
      localStorage.setItem('taraAttemptedWindows_v1',JSON.stringify(_obj));
    }catch(_){}
  },[]);
  // V9.18.4: rate-limit smart-exit diagnostic logs to once per ~3s.
  const _smartExitLogLastRef=useRef(0);
  // V10.7.9 — SMART CASHOUT peak tracking.
  //   Tracks peak unrealized profit per auto-exec position so we can:
  //   (1) trailing-stop on retrace from peak
  //   (2) lock profits in late-window
  //   (3) asymmetric loss cut faster than fixed stop-loss
  //   Keyed by orderId so each position has its own peak. Reset implicitly
  //   when orderId changes.
  const _v10_7_9_peakRef=useRef({orderId:null,peakCents:0});
  // V6.2.6: Force-call refs. User-initiated overrides via buttons on Tara's Call card.
  //   softHintRef: timestamp of last "Hint to Lock Faster" press. Active for 10s after press.
  //     Lowers q-floor 30→20, conv-floor 10→6, and trims sample requirements by 40%.
  //     EDGE GUARD, tape-super-opposes, Kalshi-extreme blocks all still apply.
  //   hardForceRef: timestamp of last "Override Gates" press. Active for 5s after press.
  //     Commits Tara's current lean (or pulls direction from posterior if still SCANNING).
  //     Bypasses ALL safety gates including edge guard. Confirmed by dialog before set.
  //   Both reset on window change.
  const softHintRef=useRef(0);
  const hardForceRef=useRef(0);
  // V10.4.1 Module A — delay gate state. Tracks per-window delay duration
  // and the conditions when delay first triggered (for telemetry).
  const _v104_1_delayStateRef=useRef(null);
  const _v1364_oppRef=useRef(null);/*V13.4.64: per-window peak-opportunity tracker (conviction+entry+timing) to diagnose missed early locks. Additive, no behavior change.*/
  // V10.7.81: Flat-gap watch state. When gap=0 at lock time, instead of sitting out
  //   immediately, Tara enters WATCHING mode. She checks every 30s whether:
  //   (a) gap has developed to ≥8pt (real directional signal formed), AND
  //   (b) entry price is still ≤68¢ (good odds remaining), AND
  //   (c) ≥5 min left in window.
  //   If all three: lock. If gap never develops or price too expensive: sit out.
  //   {windowId, startedAt, direction (which way gap was leaning when watch started)}
  const _flatGapWatchRef=useRef(null);
  // V10.7.70: Revised abort guard. V10.7.68 used max-3-aborts-per-window which
  //   caused windows like [31,69,31,69] to lock out after 3 aborts even when
  //   a high-confidence signal appeared later. The window was scanned the whole time.
  //   New approach: track {windowId, abortCount, lastAbortAt}.
  //   Reset abortCount when: (a) new window, OR (b) 5+ minutes since last abort.
  //   This allows re-engagement after Kalshi settles, while still preventing
  //   rapid consecutive loops (3 aborts within <5min = still locked out).
  const _v104_1_abortCountRef=useRef({windowId:null,count:0,lastAbortAt:0});
  const _v104_1_pendingDelayStampRef=useRef(null);
  // V6.2.7: KALSHI ENTRY WINDOW tracking. Replaces the time-based hard cap.
  //   kalshiWasBelowThreshUp: has Kalshi YES (UP price) ever been ≤65% this window?
  //   kalshiWasBelowThreshDown: has Kalshi NO (100-YES, DOWN-implied) ever been ≤65% this window?
  //   These let us detect "window opened with Kalshi already pricing in, then reset" — the
  //   only valid entry case when Kalshi starts above the threshold. Reset on window change.
  const kalshiWasBelowThreshUpRef=useRef(false);
  const kalshiWasBelowThreshDownRef=useRef(false);
  // V6.5.1: track timestamp of most-recent Kalshi-below-threshold per direction.
  //   Used by the 30s dip-grace check — if Kalshi dipped below threshold any time in
  //   the last 30s, entry window is still considered open even if currently above.
  const kalshiLastBelowThreshUpRef=useRef(0);
  const kalshiLastBelowThreshDownRef=useRef(0);
  // V4.2: Track consecutive frames Tara has a directional call. Used to determine when
  //   she's confident enough to lock early — fast for clean setups, slower for mixed.
  const taraCallSampleRef=useRef({dir:null,count:0});
  // V6.4: Regime history — a sliding 3-min log of {atrBps, regime, range, timestamp} samples.
  //   Used by the regime-transition detector to spot when conditions are shifting from
  //   trending → range-bound or vice versa. Sampled once every 5s.
  const regimeHistoryRef=useRef([]);
  // V6.4: Multi-Signal Pivot tracking. Records "shift events" — moments when a signal flipped
  //   direction. When 3+ events fire within 30s same direction, that's outside-context info
  //   arriving inside the data; sample requirement drops to 2 ticks for fast pivot.
  const signalPivotRef=useRef({events:[],lastTapeSign:null,lastAsymSign:null,lastFgtSign:null});
  // V5.7.2: Sample rate tracker — last 8 measurements of (timestamp, count) so we can compute
  //   actual samples-per-second over the recent window. Used for honest "decision in ~Xs"
  //   estimates that account for samples only accumulating when conviction ≥ floor.
  const taraSampleRateRef=useRef([]);
  // V5.6.1: TARA CALL LOG — per-call memory. Every call she commits (UP/DOWN/SIT_OUT) appends
  //   one entry; on rollover scoring the entry gets a result. This is "Tara's training records"
  //   — the user-visible audit trail of what she called, why, and how it resolved. Persisted
  //   to Firestore so it accumulates across sessions and devices. Capped at 500 most-recent
  //   entries to keep payload size sane.
  const[taraCallLog,setTaraCallLog]=useCallLedger(()=>{
    try{
      // V10.7.95: Load from localStorage cache first (fast paint).
      // IndexedDB (primary store) is loaded async via useEffect below.
      // V10.7.60: Load from localStorage, then merge with sessionStorage.
      //   sessionStorage holds the last 200 entries (survives refresh, cleared on tab close).
      //   This ensures recent trades are always available even if localStorage was trimmed.
      const stored=localStorage.getItem('taraCallLog_v1');
      const session=sessionStorage.getItem('taraCallLog_session');
      // V10.9.6: prefer the DEEP minified cache (up to TARA_CALL_LOG_CAP entries) over the
      //   1000-entry fast cache. This is what makes the full trade count survive
      //   a reload even when IndexedDB is empty/unavailable.
      let _deepParsed=null;
      try{
        const _deepRaw=localStorage.getItem('taraCallLog_deep');
        if(_deepRaw){
          const _dp=JSON.parse(_deepRaw);
          if(Array.isArray(_dp)&&_dp.length)_deepParsed=_dp;
        }
      }catch(_){}
      let parsed=[];
      if(stored){
        const _p=JSON.parse(stored);
        if(Array.isArray(_p))parsed=_p;
      }
      // If the deep cache has more entries than the fast cache, use it as the base.
      if(_deepParsed&&_deepParsed.length>parsed.length){
        parsed=_deepParsed;
      }
      // Merge sessionStorage entries — they may be more recent than localStorage
      if(session){
        try{
          const _s=JSON.parse(session);
          if(Array.isArray(_s)&&_s.length>0){
            const _byKey=new Map();
            parsed.forEach(e=>{if(e?.windowId)_byKey.set((e.asset||'BTC')+'|'+e.windowId+'|'+(e.windowType||''),e);});
            _s.forEach(e=>{
              if(!e?.windowId)return;
              const k=(e.asset||'BTC')+'|'+e.windowId+'|'+(e.windowType||'');
              const ex=_byKey.get(k);
              // sessionStorage wins if it has a resolved entry and localStorage has pending/missing
              if(!ex||(ex.result!=='WIN'&&ex.result!=='LOSS'&&(e.result==='WIN'||e.result==='LOSS'))){
                _byKey.set(k,e);
              }
            });
            parsed=Array.from(_byKey.values()).sort((a,b)=>(a.id||0)-(b.id||0));
          }
        }catch(_){}
      }
      parsed=recoverCapturedCalls(parsed,localStorage);
      if(!parsed.length)return[];
      // V6.3.4: One-time sanitize pass on load. Drops corrupt entries from before the
      //   scoring fix:
      //     - Closing price >20% away from strike (e.g. $41K close on $80K strike)
      //     - LOSS where outcomeDir matches the call direction (UP call won, marked LOSS)
      //     - WIN where outcomeDir opposes the call direction (data flip)
      //   Also dedupes by windowId+windowType, keeping the resolved entry if duplicates exist.
      const seen=new Map();
      // V8.8.6: include asset in the key so BTC + ETH at the same window slot coexist
      const _key=(e)=>(e.asset||'BTC')+'|'+(e.windowId||'')+'|'+(e.windowType||'');
      let dropped=0;
      let resolved=0;
      parsed.forEach(e=>{
        if(!e||!e.windowType)return;
        // V6.5.3: Auto-fix SITOUT entries that actually had a directional call.
        //   When a snapshot was lost between lock and rollover (page refresh, cloud-restore
        //   race), the entry was logged with dir='UP' or 'DOWN' but result was SITOUT.
        //   If we have valid strike + close, compute the real WIN/LOSS retroactively.
        if(e.result==='SITOUT'&&(e.dir==='UP'||e.dir==='DOWN')&&e.strike&&e.closingPrice&&Math.abs(e.closingPrice-e.strike)/e.strike<0.20){
          const _outcome=e.closingPrice>e.strike?'UP':e.closingPrice<e.strike?'DOWN':null;
          if(_outcome){
            e.result=e.dir===_outcome?'WIN':'LOSS';
            e.outcomeDir=_outcome;
            resolved++;
          }
        }
        // V7.0.5: Auto-fix asset tag from strike magnitude.
        // V7.1: BTC + ETH only. Strikes <$100 are legacy SOL/DOGE entries — drop them
        //   silently from the log (their data was unreliable anyway).
        if(e.strike&&Number.isFinite(Number(e.strike))){
          const s=Number(e.strike);
          if(s<100){
            try{console.info('[V7.1] Dropping legacy SOL/DOGE entry — strike',s,'(below ETH range)');}catch(_){}
            dropped++;
            return;
          }
          let _inferred=null;
          if(s>=10000)_inferred='BTC';
          // ETH removed
          if(_inferred&&e.asset!==_inferred){
            try{console.info('[V7.1] Migrating entry asset',e.asset||'(none)','→',_inferred,'(strike',s,')');}catch(_){}
            e.asset=_inferred;
            resolved++;
          }
          // Drop entries still tagged as SOL/DOGE even if strike doesn't disambiguate
          if(e.asset==='SOL'||e.asset==='DOGE'){
            try{console.info('[V7.1] Dropping legacy',e.asset,'entry');}catch(_){}
            dropped++;
            return;
          }
        }
        // Sanity check on strike/close ratio
        if(e.result==='WIN'||e.result==='LOSS'){
          if(e.strike&&e.closingPrice&&Math.abs(e.closingPrice-e.strike)/e.strike>0.20){
            dropped++;
            return; // corrupt — drop
          }
          // Logical consistency: if outcomeDir is set, it should match WIN/LOSS expectation
          if(e.outcomeDir&&(e.dir==='UP'||e.dir==='DOWN')){
            const _shouldBeWin=e.dir===e.outcomeDir;
            if(_shouldBeWin&&e.result==='LOSS'){dropped++;return;}
            if(!_shouldBeWin&&e.result==='WIN'){dropped++;return;}
          }
        }
        const k=_key(e);
        const existing=seen.get(k);
        if(!existing){seen.set(k,e);return;}
        // Resolved beats unresolved
        if((e.result==='WIN'||e.result==='LOSS')&&(existing.result==='SITOUT'||existing.result==='NO_TRADE')){seen.set(k,e);return;}
        if((existing.result==='WIN'||existing.result==='LOSS')&&(e.result==='SITOUT'||e.result==='NO_TRADE'))return;
        if((existing.result==='SITOUT'||existing.result==='NO_TRADE')&&(e.dir==='UP'||e.dir==='DOWN')&&e.result!=='SITOUT'&&e.result!=='NO_TRADE'){seen.set(k,e);return;}
        if((e.result==='SITOUT'||e.result==='NO_TRADE')&&(existing.dir==='UP'||existing.dir==='DOWN')&&existing.result!=='SITOUT'&&existing.result!=='NO_TRADE')return;
        if(existing.result&&!e.result)return;
        if(!existing.result&&e.result){seen.set(k,e);return;}
        // Both resolved: keep newer (higher id). Both unresolved: keep newer.
        // V10.7.91 fix: was keeping OLDER entry (lower id) — backwards.
        if((e.id||0)>(existing.id||0))seen.set(k,e);
      });
      const cleaned=Array.from(seen.values()).filter(e=>!e||(e.asset||'BTC')==='BTC').sort((a,b)=>(a.id||0)-(b.id||0));
      if(dropped>0){
        try{console.warn('[V6.5.3 cleanup] Dropped',dropped,'corrupt log entries');}catch(_){}
      }
      if(resolved>0){
        try{console.info('[V6.5.3 cleanup] Resolved',resolved,'misclassified SITOUT → WIN/LOSS');}catch(_){}
      }
      return cleaned;
    }catch(e){return recoverCapturedCalls([],localStorage);}
  },()=>taraCallSnapshotRef.current);
  useEffect(()=>{
    // A queued React logger / recovered archive can arrive after rollover's
    // first sweep. Finalize that same captured sit-out too, without waiting for
    // another window, a quote or a reload. Never create a row for an empty gap.
    setTaraCallLog(prev=>finalizeClosedSitouts(prev,{now:Date.now(),asset:currentAssetRef.current||'BTC',windowType}));
  },[taraCallLog,timeState.nextWindowMs,windowType,currentAsset]);
  // V10.7.89: Register window-level Kalshi credentials getter for Memory modal reconcile
  React.useEffect(()=>{
    window._taraGetKalshiCreds=()=>({
      apiKeyId:kalshiCreds?.apiKeyId||'',
      privateKeyPem:kalshiCreds?.privateKeyPem||'',
    });
    return()=>{try{delete window._taraGetKalshiCreds;}catch(_){}};
  },[kalshiCreds]);
  //   call setTaraCallLog without needing it passed through props.
  //   Avoids prop-drilling through TaraCallCard -> TaraMemoryStrip -> TaraMemoryModal.
  React.useEffect(()=>{
    window._taraImportCallLog=(importedEntries)=>{      if(!Array.isArray(importedEntries))return 0;
      const btcEntries=importedEntries.filter(e=>e&&(e.asset||'BTC')==='BTC');
      setTaraCallLog(prev=>{
        const map=new Map();
        [...prev,...btcEntries].forEach(e=>{
          if(!e)return;
          const key=(e.asset||'BTC')+'|'+(e.windowId||'')+'|'+(e.windowType||'');
          const existing=map.get(key);
          if(!existing)map.set(key,e);
          else if((existing.result==='SITOUT'||existing.result==='NO_TRADE')&&(e.dir==='UP'||e.dir==='DOWN')&&e.result!=='SITOUT'&&e.result!=='NO_TRADE')map.set(key,e);
          else if((e.result==='SITOUT'||e.result==='NO_TRADE')&&(existing.dir==='UP'||existing.dir==='DOWN')&&existing.result!=='SITOUT'&&existing.result!=='NO_TRADE'){}
          else if(!existing.result&&e.result)map.set(key,e);
          else if((e.result==='WIN'||e.result==='LOSS')&&(existing.result==='SITOUT'||existing.result==='NO_TRADE'))map.set(key,e); /* V13.3.3: real trade outranks stored sit-out */
          else if(existing.result&&e.result&&((e.manualEdit===true&&existing.manualEdit!==true)||(existing.result===e.result&&(e.id||0)>(existing.id||0))))map.set(key,e); /* V13.3.1: immutable settlement - no cross-device WIN/LOSS flip */
        });
        const merged=Array.from(map.values()).sort((a,b)=>(a.id||0)-(b.id||0));
        // V10.7.91: Force immediate localStorage save — don't rely on debounced useEffect.
        //   Also write a backup key so if taraCallLog_v1 gets clobbered, we can recover.
        try{
          const _cap=typeof TARA_CALL_LOG_CAP!=='undefined'?TARA_CALL_LOG_CAP:5000;
          const _save=normalizeCallLedger(merged,prev,taraCallSnapshotRef.current).slice(-_cap);
          _idbWrite('taraCallLog',_save).catch(()=>{});          // primary: unlimited
          localStorage.setItem('taraCallLog_v1',JSON.stringify(_save.slice(-1000))); // fast cache
          // V10.9.6: write DEEP minified cache (up to TARA_CALL_LOG_CAP) so the full imported
          //   count survives reload even if IndexedDB fails. Without this, reload
          //   fell back to the 1000-entry fast cache — the "base stayed at 500/1000"
          //   bug the user reported.
          const _MK=new Set(['id','windowId','windowType','asset','dir','call','result','strike','strikeAtLock','closingPrice','kalshiAtLock','kalshiAtClose','outcomeDir','resolvedAt','tier','isStructuralLed','isSuperConfluent','isConfluent','isTapeLed','isRisingConfluence','isUserForced','confidence','betAmt','maxPay','manualEdit','wasOverriddenNoTrade','tapeSuperStrong','tapeStronglyAgrees','convictionAtLock','qAtLock','noGoCategory','netCents','kalshiLeadAtLock','spotAtLock','distBpsAtLock','volBpsAtLock','v101ShadowAtLock','confluenceAtLock','trajAtLock','flipAtLock']);
          CALL_EVIDENCE_FIELDS.forEach(k=>_MK.add(k));
          const _mini=_save.slice(-_cap).map(e=>{if(!e)return e;const o={};for(const k in e){if(_MK.has(k))o[k]=e[k];}return o;});
          for(let _c=_mini.length;_c>=200;_c=Math.floor(_c*0.8)){
            try{localStorage.setItem('taraCallLog_deep',JSON.stringify(_mini.slice(-_c)));localStorage.setItem('taraCallLog_deepCount',String(_mini.length));break;}catch(_e){if(_c<=200)break;}
          }
          // V13.4.254: dropped taraCallLog_importedAt / _importedCount writes --
          //   neither was ever read.
          sessionStorage.setItem('taraCallLog_session',JSON.stringify(_save.slice(-200)));
        }catch(_){}
        // Trigger cloud sync after state commits
        setTimeout(()=>{
          try{
            if(typeof cloudWriteDebouncedRMW==='function'){
              cloudWriteDebouncedRMW(
                'memory/taraCallLog',
                ()=>(typeof taraCallLogRef!=='undefined'?taraCallLogRef.current||[]:merged),
                (cloudData,localEntries)=>{
                  const cloudEntries=(cloudData&&Array.isArray(cloudData.entries))?cloudData.entries:[];
                  const cMap=new Map();
                  [...cloudEntries,...localEntries].forEach(e=>{
                    if(!e)return;
                    const k=(e.asset||'BTC')+'|'+(e.windowId||'')+'|'+(e.windowType||'');
                    const ex=cMap.get(k);
                    if(!ex)cMap.set(k,e);
                    else if((ex.result==='SITOUT'||ex.result==='NO_TRADE')&&(e.dir==='UP'||e.dir==='DOWN')&&e.result!=='SITOUT'&&e.result!=='NO_TRADE')cMap.set(k,e);
                    else if((e.result==='SITOUT'||e.result==='NO_TRADE')&&(ex.dir==='UP'||ex.dir==='DOWN')&&ex.result!=='SITOUT'&&ex.result!=='NO_TRADE'){}
                    else if(!ex.result&&e.result)cMap.set(k,e);
                    else if((e.result==='WIN'||e.result==='LOSS')&&(ex.result==='SITOUT'||ex.result==='NO_TRADE'))cMap.set(k,e); /* V13.3.5: real trade outranks stored sit-out */
                  });
                  return{entries:Array.from(cMap.values()).sort((a,b)=>(a.id||0)-(b.id||0)).slice(-_TARA_CLOUD_LOG_CAP).map(_stripEntryForCloud)}; // V13.4.187: was unstripped
                },
                500, // very short debounce — this is the import flush
              );
            }
          }catch(_){}
        },200);
        return merged;
      });
      return btcEntries.length;
    };
    // _taraApplyReconcile: used by reconcile apply buttons.
    //   Problem with the old approach: _taraImportCallLog merges via _shouldReplace.
    //   When an entry already exists with the same id and is already resolved,
    //   _shouldReplace returns false and the reconcile patch is silently ignored.
    //   Fix: read localStorage directly, apply patches by id (no merge logic),
    //   write back, then set React state directly with the result.
    //   This is atomic, no timing issues, no merge collisions.
    // V13.4.43: one-time cleanup of engine predictor reads that older reconcile
    //   wrongly stamped WIN/LOSS onto. betAmt==null => never a committed lock.
    //   DRY-RUN by default. Run _taraCleanPredictorReads({dryRun:false}) to apply.
    window._taraCleanPredictorReads=async(opts)=>{
      const dryRun=!(opts&&opts.dryRun===false);
      let _log=null;
      try{_log=await _idbRead('taraCallLog');}catch(_){}
      if(!Array.isArray(_log)||!_log.length)_log=[...(taraCallLogRef.current||[])];
      const _hits=_log.filter(e=>e&&e.betAmt==null&&(e.result==='WIN'||e.result==='LOSS'));
      const _w=_hits.filter(e=>e.result==='WIN').length;
      const _l=_hits.filter(e=>e.result==='LOSS').length;
      console.log('[cleanPredictorReads] '+_hits.length+' predictor reads carry W/L ('+_w+'W / '+_l+'L).'+(dryRun?' DRY RUN - nothing changed. Run _taraCleanPredictorReads({dryRun:false}) to apply.':' Applying...'));
      if(dryRun||!_hits.length)return _hits.length;
      const _map=new Map();
      for(const e of _hits)_map.set(e.id,{result:'NO_TRADE',outcomeDir:null,_wlWas:e.result,_predictorCleaned:true});
      const _applied=(typeof window._taraApplyReconcile==='function')?await window._taraApplyReconcile(_map):0;
      console.log('[cleanPredictorReads] cleared '+_applied+' entries. Tally now reflects committed call locks only.');
      return _applied;
    };
    window._taraApplyReconcile=async(updateMap,{automatic=false}={})=>{
      // updateMap: Map of entryId → {result, strike, closingPrice, ...}
      if(!updateMap||!updateMap.size)return 0;
      // V10.9.1 FIX: Read from IndexedDB (full history, potentially 1000s of entries),
      //   NOT localStorage (which is capped at a 500-entry fast-read cache).
      //   The old code read localStorage_v1 (500 entries), patched it, and wrote
      //   that 500-entry slice back to IDB as the new "full" history — silently
      //   deleting every older trade from IDB on every reconcile. This is why
      //   "reconcile only keeps recent trades" and "I have to re-import every
      //   fresh load" were the same underlying bug: IDB kept shrinking to 500.
      let _log=null;
      try{
        _log=await _idbRead('taraCallLog');
      }catch(_){}
      if(!Array.isArray(_log)||!_log.length){
        // Fallback: in-memory state (already has full history if IDB load-on-mount ran)
        _log=[...(taraCallLogRef.current||[])];
      }
      // Also merge in current in-memory state — covers any trades locked since
      //   IDB was last written (the periodic save is debounced).
      if(Array.isArray(taraCallLogRef.current)&&taraCallLogRef.current.length){
        const _seen=new Map(_log.filter(e=>e&&e.id).map(e=>[e.id,e]));
        for(const e of taraCallLogRef.current){
          if(!e||!e.id)continue;
          const ex=_seen.get(e.id);
          if(!ex)_seen.set(e.id,e);
          else if(!ex.result&&e.result)_seen.set(e.id,e);
        }
        _log=Array.from(_seen.values()).sort((a,b)=>(a.id||0)-(b.id||0));
      }
      let applied=0;
      const _patched=_log.map(e=>{
        if(!e||!e.id)return e;
        const upd=updateMap.get(e.id);
        if(!upd)return e;
        const next=automatic?applyAutomaticCallSettlement(e,upd):
          amendCallRecord(e,{...upd,manualEdit:true,manualEditedAt:Date.now()},{source:upd.officialSettlement?'official-settlement':'reconcile'});
        if(next!==e)applied++;
        return next;
      });
      // Write FULL patched history back to IndexedDB (no slicing to 500).
      //   localStorage cache still gets the last-1000 slice for fast first paint,
      //   plus the deep minified cache (up to TARA_CALL_LOG_CAP) so corrected results survive
      //   reload even if IndexedDB is unavailable.
      try{
        const _cap=typeof TARA_CALL_LOG_CAP!=='undefined'?TARA_CALL_LOG_CAP:5000;
        const _save=normalizeCallLedger(_patched,taraCallLogRef.current,taraCallSnapshotRef.current).slice(-_cap);
        await _idbWrite('taraCallLog',_save).catch(()=>{});     // primary: FULL history
        localStorage.setItem('taraCallLog_v1',JSON.stringify(_save.slice(-1000))); // fast cache
        const _MK=new Set(['id','windowId','windowType','asset','dir','call','result','strike','strikeAtLock','closingPrice','kalshiAtLock','kalshiAtClose','outcomeDir','resolvedAt','tier','isStructuralLed','isSuperConfluent','isConfluent','isTapeLed','isRisingConfluence','isUserForced','confidence','betAmt','maxPay','manualEdit','wasOverriddenNoTrade','tapeSuperStrong','tapeStronglyAgrees','convictionAtLock','qAtLock','noGoCategory','netCents','kalshiLeadAtLock','spotAtLock','distBpsAtLock','volBpsAtLock','v101ShadowAtLock','confluenceAtLock','trajAtLock','flipAtLock']);
        CALL_EVIDENCE_FIELDS.forEach(k=>_MK.add(k));
        const _mini=_save.slice(-_cap).map(e=>{if(!e)return e;const o={};for(const k in e){if(_MK.has(k))o[k]=e[k];}return o;});
        for(let _c=_mini.length;_c>=200;_c=Math.floor(_c*0.8)){
          try{localStorage.setItem('taraCallLog_deep',JSON.stringify(_mini.slice(-_c)));localStorage.setItem('taraCallLog_deepCount',String(_mini.length));break;}catch(_e){if(_c<=200)break;}
        }
        sessionStorage.setItem('taraCallLog_session',JSON.stringify(_save.slice(-200)));
      }catch(_){}
      // Set React state to the full patched history — not just the 500-cache slice
      setTaraCallLog(_patched.slice(-(typeof TARA_CALL_LOG_CAP!=='undefined'?TARA_CALL_LOG_CAP:5000)));
      // V10.9.19: force an immediate re-render so the on-screen log reflects the
      //   applied fixes right away. The setTaraCallLog above updates state, but the
      //   memoized log view + cloud-restore listener could otherwise leave the
      //   display looking stale until the next reload ("applied but not saved" report).
      try{setForceRender(p=>p+1);}catch(_){}
      // Verify the write actually landed in IDB (catches silent quota/write failures).
      try{
        const _check=await _idbRead('taraCallLog').catch(()=>null);
        const _appliedIds=Array.from(updateMap.keys());
        const _landed=Array.isArray(_check)?_appliedIds.filter(id=>{
          const _e=_check.find(x=>x&&x.id===id);
          return _e&&_e.manualEdit===true;
        }).length:0;
        console.info(`[reconcile] applied ${applied} fixes · ${_landed}/${_appliedIds.length} verified persisted in IDB`);
        if(_landed<_appliedIds.length){
          console.warn('[reconcile] some fixes did not persist to IDB — possible quota/write failure. Data is still in React state + localStorage.');
        }
      }catch(_){}
      return applied;
    };
    // _taraForceSave: flush current state to IndexedDB + localStorage immediately
    window._taraForceSave=()=>{
      try{
        const _cur=taraCallLogRef.current||[];
        if(!_cur.length)return;
        const _cap=typeof TARA_CALL_LOG_CAP!=='undefined'?TARA_CALL_LOG_CAP:5000;
        const _save=_cur.slice(-_cap);
        _idbWrite('taraCallLog',_save).catch(()=>{});          // primary
        localStorage.setItem('taraCallLog_v1',JSON.stringify(_save.slice(-1000))); // cache (V10.9.5: 500->1000)
        sessionStorage.setItem('taraCallLog_session',JSON.stringify(_save.slice(-200)));
      }catch(_){}
    };
    return()=>{
      try{delete window._taraImportCallLog;}catch(_){}
      try{delete window._taraApplyReconcile;}catch(_){}
      try{delete window._taraForceSave;}catch(_){}
    };
  },[setTaraCallLog]);
  // V5.7.1: taraScorecards is DERIVED from taraCallLog instead of incrementally tracked.
  //   Eliminates multi-device double-counting: a sync race could otherwise turn one LOSS
  //   into 2 (PC bumped 3→4 + synced, tab synced to 4 + bumped its copy to 5, both end at 5).
  //   With derive-from-log, the dedup'd log is the single source of truth — no counter to
  //   drift. Declaration order matters: this MUST be after the taraCallLog useState.
  // V7.0.2: filter by currentAsset. Memory entries tagged with `asset` since V6.5.8.
  //   Pre-V6.5.8 entries default to BTC (since BTC was the only asset before then).
  const taraScorecards=React.useMemo(()=>{
    const out={'15m':{wins:0,losses:0,sitouts:0},'5m':{wins:0,losses:0,sitouts:0}};
    // V13.4.163: count the SAME window the cloud holds, not the whole local log.
    //   The card is labelled "last N" and must actually mean it. The local log can
    //   hold up to TARA_CALL_LOG_CAP (10k) entries, so counting all of it produced
    //   6,601 under a "last 1,000" heading.
    const _recentLog=_deduplicateByWindow((taraCallLog||[]).slice(-_TARA_CLOUD_LOG_CAP));
    _recentLog.forEach(e=>{
      if(!e||!e.windowType||!e.result)return;
      const _entryAsset=e.asset||'BTC';
      if(_entryAsset!==currentAsset)return; // filter to current asset
      // V10.7.57: exclude no-trade override calls from the scorecard.
      //   wasOverriddenNoTrade=true means Tara herself flagged "no edge, would not
      //   trade this." Counting these as wins/losses misled the record — the user
      //   sees "Tara's Record 803W-497L" thinking those are real trades. They're
      //   not. Now the scorecard reflects only trades Tara would actually take.
      // V10.7.57: exclude no-trade overrides. V10.7.82: also exclude no-go-data (stale price).
      if(e.wasOverriddenNoTrade===true)return;
      // V10.7.84b: mirror _isRealTrade fix — only exclude no-go-data entries that never
      // resolved (no closingPrice). Entries with closingPrice settled on Kalshi and count.
      if(e.tier==='no-go-data'&&!e.closingPrice)return;
      const wt=e.windowType;
      if(!out[wt])out[wt]={wins:0,losses:0,sitouts:0};
      if(_isRealTrade(e)&&e.result==='WIN')out[wt].wins++;
      else if(_isRealTrade(e)&&e.result==='LOSS')out[wt].losses++;
      else if(e.result==='SITOUT')out[wt].sitouts++;
    });
    // V13.4.163: the V13.4.73 all-time RATCHET is REMOVED.
    //   It kept a monotonic per-(asset,windowType) tally in
    //   localStorage['taraAllTimeRecord_v2'] that could only ever grow, explicitly
    //   "so the displayed record never shrinks". Three problems, all now disqualifying:
    //     1. It was a LOCAL-ONLY source of truth. That total existed on one machine
    //        and nowhere else -- not in Supabase, not in any export. Exactly the
    //        local dependency we are removing.
    //     2. It made the "last N" label a lie: the ratchet showed 3041W/1672L/1888SO
    //        = 6,601 entries under a "last 1,000" heading, because the number never
    //        came from the log window at all.
    //     3. Monotonic-by-design means it could never reflect a cap change, a resync,
    //        or a corrected result. It only ratcheted up.
    //   The record is now simply counted from the last _TARA_CLOUD_LOG_CAP entries --
    //   the same window Supabase holds -- so what you see is what is actually stored,
    //   on any device. It WILL read lower than before; the old number was inflated by
    //   history the cloud no longer keeps. Full history remains in your exports.
    //   Clean up the stale key so it cannot be resurrected or confuse a future reader.
    try{localStorage.removeItem('taraAllTimeRecord_v2');}catch(_eR){}
    return out;
  },[taraCallLog,currentAsset]);
  // V9.1.9: scorecards (lifetime, all assets) — derived from taraCallLog.
  //   This replaces the legacy useState that had broken max-per-cell merge sync.
  //   Same source of truth as taraScorecards; no separate sync path needed.
  // V9.10.9: filter to entries created on THIS device. The personal scorecard (the
  //   "PERSONAL SCORECARD · this device only" panel) should reflect just this device's
  //   trades — previously the useMemo summed ALL of taraCallLog, which is cloud-shared,
  //   so every device showed the same totals despite the "this device only" label being
  //   technically accurate at the storage layer. New entries get a `device` field stamped
  //   at the three creation sites (engine UP/DOWN lock, _logSnapshotEntry, userForceCommit
  //   — see V9.10.9 stamps there). Legacy entries (created before this ship) have no
  //   `device` field and are counted for all devices — preserves the current totals so
  //   the user doesn't suddenly see "0W·0L" on day one. Going forward, scorecards diverge.
  const scorecards=React.useMemo(()=>{
    const out={'15m':{wins:0,losses:0},'5m':{wins:0,losses:0}};
    _canonicalTrades(taraCallLog||[]).forEach(e=>{
      if(!e||!e.windowType||!e.result)return;
      // Device filter: skip entries created on a different device. Untagged (legacy)
      //   entries fall through and count, so existing totals aren't lost on upgrade.
      if(e.device&&e.device!==TARA_DEVICE_LABEL)return;
      // V13.4.175 FIX: this panel never got the V10.7.57/V10.7.84b exclusions that
      //   taraScorecards ("Tara's Record") already has, so it counted
      //   wasOverriddenNoTrade entries (Tara flagged "no edge, would not trade this")
      //   and unresolved no-go-data entries as real wins/losses. Two panels on the
      //   same screen, same underlying window, showing different W-L for it — one
      //   inflated by trades that were never actually taken.
      if(e.wasOverriddenNoTrade===true)return;
      if(e.tier==='no-go-data'&&!e.closingPrice)return;
      const wt=e.windowType;
      if(!out[wt])out[wt]={wins:0,losses:0};
      if(e.result==='WIN')out[wt].wins++;
      else if(e.result==='LOSS')out[wt].losses++;
    });
    return out;
  },[taraCallLog]);
  // V7.0.2: per-asset call log shown in Memory modal + cards. Engine still reads full log
  //   for learnings — filtering only happens at display layer.
  const displayedCallLog=React.useMemo(()=>{
    return(taraCallLog||[]).filter(e=>!!e&&(e.asset||'BTC')==='BTC');
  },[taraCallLog,currentAsset]);
  // V13.4.254: removed a localStorage write of taraCallScorecards_v1. Its own
  //   comment claimed "cached for fast paint on next mount" and nothing ever
  //   read it -- taraScorecards is a useMemo over taraCallLog, so it is
  //   recomputed on every mount and the cache could never have been consulted.
  // Model fitting runs in the scheduled shadow trainer, not in the browser.
  // V9.2.3: STREAK — derived from call log, surfaces in UI as a badge.
  const currentStreak=React.useMemo(()=>computeStreak(taraCallLog,currentAsset),[taraCallLog,currentAsset]);
  // V9.2.3: TIME-OF-DAY MODEL — per-hour WR boost map, fed into engine.
  const timeOfDayBoostMap=React.useMemo(()=>computeTimeOfDayModel(taraCallLog,currentAsset),[taraCallLog,currentAsset]);
  // V9.2.3: Widget data exposure moved to after taraCall is declared (avoids TDZ).
  // V9.2.3: Cleanup handler
  const handleCleanupLog=React.useCallback(()=>{
    const{cleaned,report}=taraCleanupLog(taraCallLog);
    const total=report.removed+report.backfilled+report.deduped;
    if(total===0){alert('Log is clean — no changes needed.');return;}
    const msg=`Cleanup results:\n• ${report.removed} broken entries removed\n• ${report.backfilled} entries backfilled\n• ${report.deduped} duplicates removed\n\nApply changes?`;
    if(!window.confirm(msg))return;
    setTaraCallLog(cleaned);
  },[taraCallLog]);
  const taraCallLogRef=useRef(taraCallLog);
  const _callLogPublicationKeyRef=useRef(null);
  taraCallLogRef.current=taraCallLog;
  useEffect(()=>{_lifecycleConfirmLedger(taraCallLog);},[taraCallLog]);
  const _learningCloudSourceRef=useRef(null);
  // V13.4.347: arm the loss-streak cooldown when the two MOST RECENT settled
  //   (WIN/LOSS only -- SIT_OUT/NO_TRADE/pending entries are skipped, they
  //   are neither a win nor a loss) results are both LOSS. triggeredForId
  //   guards against re-arming on every re-render while that same pair
  //   stays the most recent -- only a genuinely NEW settled loss can arm it
  //   again. If a loss lands right after a cooldown ends, this correctly
  //   re-arms (the new loss + the one before it are still "2 in a row").
  useEffect(()=>{
    const _settled=taraCallLog
      .filter(e=>e&&(e.dir==='UP'||e.dir==='DOWN')&&(e.result==='WIN'||e.result==='LOSS'))
      .sort((a,b)=>b.id-a.id);
    if(_settled.length<2)return;
    const[_latest,_prev]=_settled;
    if(_latest.result==='LOSS'&&_prev.result==='LOSS'&&_lossCooldownRef.current.triggeredForId!==_latest.id){
      _lossCooldownRef.current={windowsRemaining:2,triggeredForId:_latest.id};
      try{console.info('[V13.4.347] loss-streak cooldown armed -- 2 losses in a row, sitting out the next 2 windows');}catch(_){}
    }
  },[taraCallLog]);
  // V8.1: Track if we have user-action writes pending pre-hydration. If true, force a
  //   write the moment cloudWatch hydrates so those entries don't get silently dropped.
  const _logWritePendingRef=useRef(false);
  // V12.8: multi-device sync heartbeat refs.
  //   _lastBeatSigRef    — last signature this device wrote to the heartbeat (throttle).
  //   _lastCatchUpPullRef — timestamp of last catch-up pull (cooldown, anti-storm).
  //   _catchUpInFlightRef — true while a catch-up pull is awaiting cloud read (guard).
  const _lastBeatSigRef=useRef(null);
  const _lastCatchUpPullRef=useRef(0);
  const _catchUpInFlightRef=useRef(false);
  // V8.3: Helper to merge two arrays of call log entries. Used by both RMW write
  //   and the BroadcastChannel handler. Mirrors the cloudWatch merge logic.
  const _mergeCallLogEntries=React.useCallback((existing,incoming,{cap=TARA_CALL_LOG_CAP}={})=>{
    // V8.8.6: asset in dedup key so BTC + ETH coexist for the same windowId
    const _key=(e)=>(e?.asset||'BTC')+'|'+(e?.windowId||'')+'|'+(e?.windowType||'');
    const _shouldReplace=(prev,inc)=>{
      // V10.9.20 FIX: a manual/reconcile correction is the highest source of truth.
      //   Bug: reconcile patches an entry IN PLACE (same id), so when both prev and
      //   inc were "resolved", Rule 2 (keep higher id) saw equal ids → kept the OLD
      //   pre-correction entry → the reconcile fix reverted on the very next save and
      //   on reload, and re-reconcile kept finding the same issue. Now: if inc is
      //   manualEdit and prev is not, inc always wins. If both are manualEdit, the
      //   newer manualEditedAt wins.
      const _pm=prev.manualEdit===true, _im=inc.manualEdit===true;
      if(_im&&!_pm)return true;
      if(_pm&&!_im)return false;
      if(_pm&&_im)return(inc.manualEditedAt||0)>=(prev.manualEditedAt||0);
      // V13.3.3: a real trade (WIN/LOSS) outranks a SITOUT/NO_TRADE record of the
      //   same window, regardless of order or id. SITOUT means no position; if any
      //   copy shows the user held a position that won or lost, that is what happened.
      //   Fixes force-locked wins reverting to sit-outs on refresh/cloud/IDB merge
      //   (13.3.1 first-resolved-wins had made SITOUT immutable). manualEdit checked
      //   above, so a user-marked SITOUT still wins.
      if((inc.result==='WIN'||inc.result==='LOSS')&&(prev.result==='SITOUT'||prev.result==='NO_TRADE'))return true;
      if((prev.result==='WIN'||prev.result==='LOSS')&&(inc.result==='SITOUT'||inc.result==='NO_TRADE'))return false;
      // Rule 1: resolved beats unresolved always
      if((prev.dir==='UP'||prev.dir==='DOWN')&&(inc.result==='SITOUT'||inc.result==='NO_TRADE')&&prev.result!=='SITOUT'&&prev.result!=='NO_TRADE')return false;
      if((inc.dir==='UP'||inc.dir==='DOWN')&&(prev.result==='SITOUT'||prev.result==='NO_TRADE')&&inc.result!=='SITOUT'&&inc.result!=='NO_TRADE')return true;
      if(!prev.result&&inc.result)return true;
      if(prev.result&&!inc.result)return false;
      // Rule 2: between two resolved entries, keep the newer one (higher id).
      //   On equal ids (same entry, no manual edit either side), keep inc so the
      //   freshest in-memory copy wins rather than a stale cache copy.
      if(prev.result&&inc.result){if(prev.result===inc.result)return(inc.id||0)>=(prev.id||0);try{_auditLogEvent('settlement-conflict',{windowId:prev.windowId||inc.windowId||'',windowType:prev.windowType||inc.windowType||'',kept:prev.result,rejected:inc.result,prevId:prev.id||0,incId:inc.id||0,prevCall:prev.call||prev.dir||null,incCall:inc.call||inc.dir||null});}catch(_){}return false;} /* V13.3.1: settlement immutable - keep first-resolved, never flip via merge */
      // Rule 3: between two unresolved entries, keep the newer one
      return(inc.id||0)>=(prev.id||0);
    };
    // V13.1: STICKY TELEMETRY. Fields are stamped at LOCK (rich, pending). When the
    //   window RESOLVES ~15min later, Rule 1 (resolved beats unresolved) lets a LEAN
    //   resolved copy (from the minified _deep cache or a 2nd device) REPLACE the rich
    //   pending entry wholesale, wiping telemetry. Root cause of the 3/3596 stamp rate.
    //   Fix: whichever copy wins the tiebreak, backfill any sticky field it lacks from
    //   the loser. Once any copy carries telemetry, it survives every future merge.
    const _STICKY_TELEMETRY=['signalScoresAtLock','regimeV12','adxAtLock','bbwRankAtLock','atrpAtLock','whipsawAtLock','isHighVolAtLock','isTrendAtLock','isChopAtLock','isCompressingAtLock','priceAboveMedianAtLock','secondsIntoWindow','atSecondsLeft','kalshiPriceAgeMs','last60sDriftBps','smcSweepScore','smcFvgScore','fastLockFired','earlyLockFired','earlyLockTier','taraVersion','device','htDir','stDir','trendAligned','trendConfirmScore','postLockEverAhead','postLockPeakBps','postLockPctCorrect','postLockReversed','reversalDamperApplied','reversalDamperMult','liveCoachReversalFired','liveCoachReversalPeakBps','liveCoachReversalDrawdownBps','posterior','qScore','qScoreV2','fgt','regime','rawPosteriorAtLock','calibratedPosteriorAtLock','oppNowCount','peakConv','peakEntry','peakSecsLeft','oppFirstNowConv','oppFirstNowEntry','oppFirstNowSecsLeft','feedVia','feedRejectReason','feedPriceAccepted','tapeSuperStrong','tapeStronglyAgrees','convictionAtLock','qAtLock','netCents','kalshiLeadAtLock','spotAtLock','distBpsAtLock','volBpsAtLock','v101ShadowAtLock','confluenceAtLock','trajAtLock','flipAtLock','_v10_5_1_brti','_diag_brtiRefLive'];
    _STICKY_TELEMETRY.push(...CALL_EVIDENCE_FIELDS);
    const _coalesceSticky=(winner,loser)=>{
      if(!winner||!loser)return winner;
      let _out=winner;
      for(let _i=0;_i<_STICKY_TELEMETRY.length;_i++){
        const _f=_STICKY_TELEMETRY[_i];const _wv=winner[_f];
        if(_wv===undefined||_wv===null||_wv===''){
          const _lv=loser[_f];
          if(_lv!==undefined&&_lv!==null&&_lv!==''){if(_out===winner)_out={...winner};_out[_f]=_lv;}
        }
      }
      return _out;
    };
    const byKey=new Map();
    (existing||[]).forEach(e=>{
      if(!e||!e.windowId)return;
      byKey.set(_key(e),e);
    });
    (incoming||[]).forEach(e=>{
      if(!e||!e.windowId)return;
      const k=_key(e);
      const ex=byKey.get(k);
      if(!ex)byKey.set(k,e);
      else if(_shouldReplace(ex,e))byKey.set(k,_coalesceSticky(e,ex));
      else byKey.set(k,_coalesceSticky(ex,e)); // V13.1: loser may carry telemetry winner lacks
    });
    return normalizeCallLedger(Array.from(byKey.values()).sort((a,b)=>(a.id||0)-(b.id||0)).slice(-cap),existing||[]);
  },[]);
  const[callStorageStatus,setCallStorageStatus]=useState({state:'checking'});
  useEffect(()=>{
    // V10.7.60 — ROBUST LOCALSTORAGE WRITE
    //   Prior code: try{localStorage.setItem(...JSON.stringify(full_log))}catch(e){}
    //   Problem 1: signalScoresAtLock (~25 fields) + reasoning (~40 strings) = ~2KB extra
    //     per entry. 1400 entries × 3KB = 4.2MB — over Chrome's 5MB localStorage limit.
    //     The catch silently swallowed QuotaExceededError → writes failed → data lost on refresh.
    //   Problem 2: No user-visible error. Data loss was invisible until refresh.
    //   Fix: strip verbose fields before localStorage write (same as cloud write).
    //     If still too large, progressively trim oldest entries until it fits.
    //     Surface persistent failures via a ref so the UI can warn the user.
    let _persistCancelled=false;
    let _archiveRetryTimer=null,_archiveRetryDelay=10000;
    const _persistLocal=async()=>{ // Trailing flush: never discard a load/update inside the throttle interval.
    _lastLocalPersistAt=Date.now();
    const _stripForStorage=(e)=>{
      if(!e)return e;
      // V10.7.95: Drop ALL _v10_* telemetry from stored entries.
      //   These 72 fields average 3.8KB/entry of pure developer debugging data
      //   (_v10_2_36_rangeOverrideFired etc). None are read after the version ships.
      //   Result: 5KB/entry → 1.2KB/entry (77% reduction), cap raised 2000→4000.
      const{reasoning:_b,signalScoresAtLock:_sss,...rest}=e;
      const _scores=_sss?Object.fromEntries(
        Object.entries(_sss).filter(([k])=>{
          if(!k.startsWith('_'))return true; // keep scored signals (gap, flow, etc)
          if(/^_ml_/.test(k))return true;    // keep ML training fields
          if(k==='_spdDivBps'||k==='_spdStatus')return true; // V13.4.82: keep spotPerpDiv telemetry
          return false;                       // drop all _v10_* telemetry inside signals
        })
      ):null;
      const _restClean=Object.fromEntries(Object.entries(rest).filter(([k])=>{
        if(!k.startsWith('_'))return true;   // keep all normal fields
        if(/^_ml_/.test(k))return true;      // keep ML training fields
        if(/^_cushion/.test(k))return true;  // V10.8.6: keep cushion history fields
        return false;                         // drop all _v10_* telemetry from entry
      }));
      return{..._restClean,...(_scores?{signalScoresAtLock:_scores}:{})};
    };
    // V10.9.6: MINIFIER for the DEEP localStorage cache. Keeps only the fields
    //   needed for tier stats, P&L, display, and reconcile matching (~480 bytes
    //   vs ~1.2KB stripped vs ~3.7KB full). At 480 bytes, 5000 entries = ~2.3MB,
    //   which fits inside localStorage's ~5MB limit. This is the fallback base
    //   when IndexedDB is unavailable or was truncated, so the user no longer
    //   drops to "last 500" on reload — the full count survives in localStorage.
    const _MINI_KEEP=new Set(['id','windowId','windowType','asset','dir','call','result',
      'strike','strikeAtLock','closingPrice','kalshiAtLock','kalshiAtClose','outcomeDir',
      'resolvedAt','tier','isStructuralLed','isSuperConfluent','isConfluent','isTapeLed',
      'isRisingConfluence','isUserForced','confidence','betAmt','maxPay','manualEdit',
      'wasOverriddenNoTrade','noGoCategory',
      // V13.4.239: contract-price peak after the lock. Exit rules cannot be
      //   scored without the price the position actually reached; the underlying
      //   move alone forced the take-profit question to be estimated instead of
      //   measured. Two ints.
      'postLockPeakValueC','postLockFirstValueC','postLockPeakBps','postLockReversed',
      // V13.4.242: ADAPTER TELEMETRY. Sixth instance of the drift bug the notes
      //   below describe. Every field that records whether an adaptive layer
      //   actually fired was stripped here, so "is Tara learning" could only be
      //   answered from the handful of entries that had not yet round-tripped
      //   (52 of 457). regimeCal is the one loop demonstrably doing work --
      //   applied on 51 of 52, keyed per regime+direction, shifting on n=176..459
      //   samples -- and its evidence was being deleted on every reload.
      //   All small scalars; regimeCalKey is ~16 bytes ("RANGE-CHOP|DOWN").
      // V13.4.243: pattern-engine summary. The full patternsAtLockV9_11_0 is
      //   deliberately NOT whitelisted -- ~600 bytes with perTF. These three
      //   scalars carry the part that is actually being scored.
      'patDirScore','patConfluence','patDominantDir',
      'regimeCalApplied','regimeCalKey','regimeCalShift','regimeCalN',
      'reversalDamperApplied','reversalDamperMult',
      'fgtCounterApplied','convBeforeFgtCap','urgencyApplied','ulpApplied',
      // V13.1: telemetry must survive the deep-cache minify (was the local lean source)
      'regimeV12','adxAtLock','bbwRankAtLock','atrpAtLock','whipsawAtLock','isHighVolAtLock',
      'isTrendAtLock','isChopAtLock','isCompressingAtLock','priceAboveMedianAtLock',
      'secondsIntoWindow','atSecondsLeft','kalshiPriceAgeMs','last60sDriftBps','smcSweepScore',
      'smcFvgScore','fastLockFired','earlyLockFired','earlyLockTier','taraVersion','device',
      'htDir','stDir','trendAligned','trendConfirmScore',
      // V13.4.31: signalScoresAtLock was NEVER in this whitelist, meaning any
      //   entry that survived a reload via this fallback cache permanently lost
      //   its raw signal telemetry (Grand Trend, structure/technical scores,
      //   regime multiplier detail) -- confirmed via a fresh export showing 0
      //   of 5117 entries had this field despite it being set at lock time in
      //   4 places in the code. _stripForStorage (the IndexedDB path) already
      //   preserves a filtered version of this field; this just brings the
      //   fallback cache in line so the data survives regardless of which
      //   storage tier the app happens to hydrate from.
      'posterior','qScore','qScoreV2','fgt','regime','rawPosteriorAtLock','calibratedPosteriorAtLock','oppNowCount','peakConv','peakEntry','peakSecsLeft','oppFirstNowConv','oppFirstNowEntry','oppFirstNowSecsLeft','feedVia','feedRejectReason','feedPriceAccepted',/*V13.4.67: calibration+opp+feed fields were stripped by deep-cache minify, so posterior/qScore/fgt were lost on every reload -- same bug class as signalScoresAtLock*/'tapeSuperStrong','tapeStronglyAgrees','convictionAtLock','qAtLock',/*V13.4.70 tape internals*/
      // V13.4.84: kalshiLeadAtLock was added to _MK and _STICKY_TELEMETRY in 13.4.83 but
      //   DELIBERATELY skipped here on the wrong theory that this set is an emergency
      //   minimal cache where an object field would bloat. It is not -- it is the deep-cache
      //   minifier that decides what survives a reload, and signalScoresAtLock (much larger)
      //   already lives here. Result: 83 shipped and the field still persisted 0 times while
      //   kalshiLead fired 3x. Same bug class the V13.4.31 and V13.4.67 comments above warn
      //   about. ~80 bytes when present, null otherwise.
      /*V13.4.211: the execution quote fields were NEVER in this whitelist, so any entry rehydrated from the deep-cache fallback lost its bid/ask/spread and dropped out of the Execution panel's cohort. Same bug class as the V13.4.31/67/84 notes above -- fourth time a field set has been added at lock time and forgotten here.*/'kalshiBidAtLock','kalshiAskAtLock','kalshiSpreadAtLock','kalshiQuoteAgeMs','execCostAtLock','midToExecCents',
      'signalScoresAtLock','netCents','kalshiLeadAtLock','spotAtLock','distBpsAtLock','volBpsAtLock','v101ShadowAtLock','confluenceAtLock','trajAtLock','flipAtLock']);
    CALL_EVIDENCE_FIELDS.forEach(k=>_MINI_KEEP.add(k));
    const _minifyEntry=(e)=>{
      if(!e)return e;
      // V13.4.212 DRIFT GUARD. Four separate field sets have been added at lock
      //   time and forgotten in _MINI_KEEP, each discovered months later by
      //   noticing the data was gone (see the V13.4.31/67/84/211 notes above).
      //   The failure is silent by construction: the key is simply not copied.
      //   This warns once per session about SCALAR fields that would be dropped
      //   -- scalars because all four misses were numbers/booleans/short
      //   strings. Verbose payloads and _-prefixed internals are dropped
      //   deliberately and stay quiet, so this only fires on the real mistake.
      if(!_MINI_AUDITED){
        _MINI_AUDITED=true;
        try{
          const _missed=[];
          for(const k in e){
            if(_MINI_KEEP.has(k))continue;
            if(k.charAt(0)==='_')continue;           // internal/debug, dropped on purpose
            const v=e[k];const t=typeof v;
            // V13.4.238: the 40-char cutoff was itself a silent miss. `reason` is
            //   the field that explains every decision this app makes, and a
            //   typical one runs ~70 chars ("V10.7.97 gap guard: directional-lock
            //   needs gap>=10 aligned, got 0pt (flat)"), so it sat just above the
            //   line and was never flagged. Measured consequence: 94 sit-outs in
            //   the live log carry tier and confidence but no reason at all — they
            //   went through the cloud round-trip and came back stripped. Two
            //   analyses this session stalled on exactly that.
            //
            //   Guessing intent from length is what created the hole. Long strings
            //   that are dropped ON PURPOSE are now named in _MINI_DROP_OK, and
            //   anything else gets flagged regardless of size. A field being
            //   verbose is not evidence that losing it was intended.
            //   The explicit list is the whole test for strings — no length term.
            //   Keeping `v.length<=40` alongside it would OR the two, so a short
            //   deliberate drop like `caution` or `time` would still be flagged and
            //   the noise would train the warning to be ignored.
            const _scalar=(v===null||t==='number'||t==='boolean'
              ||(t==='string'&&!_MINI_DROP_OK.has(k)));
            // V13.4.212b: also flag SMALL PLAIN OBJECTS. The scalar rule alone
            //   missed V13.4.31 (signalScoresAtLock) -- the very first instance
            //   of this bug -- because it is an object. Arrays stay excluded:
            //   reasoning / timeSeries / releaseSignalsHistory are verbose
            //   payloads dropped on purpose. A false positive here costs one
            //   line of console noise once per session; a false negative costs
            //   another silent months-long data loss.
            const _smallObj=(v&&t==='object'&&!Array.isArray(v)&&Object.keys(v).length<=30);
            if(_scalar||_smallObj)_missed.push(k);
          }
          if(_missed.length){
            console.warn('[V13.4.212] _MINI_KEEP drift: '+_missed.length+
              ' scalar field(s) are written at lock time but NOT whitelisted, so they are lost '+
              'whenever an entry rehydrates from the deep cache -> '+_missed.join(', '));
          }
        }catch(_e){/* a diagnostic must never break the write path */}
      }
      const o={};
      for(const k in e){if(_MINI_KEEP.has(k))o[k]=e[k];}
      return o;
    };
    // V10.7.88b: RMW (Read-Modify-Write) for localStorage.
    //   Previously: direct overwrite. Problem: with BTC + ETH tabs both open, ETH tab's
    //   localStorage write would overwrite BTC's freshly-resolved entries with its own
    //   stale unresolved copies of those BTC entries. Resolution: WIN/LOSS disappeared
    //   from BTC tab on reload. Same race existed in reverse.
    //   Fix: read current localStorage, merge with local state using _mergeCallLogEntries
    //   (which always prefers resolved over unresolved), then write the merged result.
    //   This is the same RMW pattern used for Supabase cloud writes.
    const _stripped=taraCallLog.slice(-TARA_CALL_LOG_CAP).map(_stripForStorage);
    let _mergedForStorage=_stripped;
    try{
      const _currentRaw=localStorage.getItem('taraCallLog_v1');
      if(_currentRaw){
        const _current=JSON.parse(_currentRaw);
        if(Array.isArray(_current)&&_current.length>0){
          _mergedForStorage=_mergeCallLogEntries(_current,_stripped).filter(e=>!e||(e.asset||'BTC')==='BTC').slice(-TARA_CALL_LOG_CAP).map(_stripForStorage);
        }
      }
    }catch(_){_mergedForStorage=_stripped;}
    // Serialize archive read/merge/write, and acknowledge only transaction completion.
    // The durable archive is NOT the rolling UI/cloud window: never cap its history.
    const archived=await updateIndexedRecord(_openIDB,_IDB_STORE,'taraCallLog',previous=>
      _mergeCallLogEntries(Array.isArray(previous)?previous:[],_mergedForStorage,{cap:Infinity}).map(_stripForStorage));
    const _cacheStatus=persistCallCaches(localStorage,_mergedForStorage,{project:_minifyEntry,archiveSaved:archived.ok});
    _cacheStatus.archiveRows=archived.value?.length??null;
    _cacheStatus.checkedAt=Date.now();
    if(!archived.ok)_cacheStatus.error=archived.error||_cacheStatus.error;
    if(!_persistCancelled)setCallStorageStatus(_cacheStatus);
    if(_cacheStatus.state==='degraded'&&!_persistCancelled){
      _archiveRetryTimer=setTimeout(()=>{if(!_persistCancelled)_persistLocal();},_archiveRetryDelay);
      _archiveRetryDelay=Math.min(60000,_archiveRetryDelay*2);
    }else{_archiveRetryDelay=10000;}
    if(archived.ok)acknowledgeCapturedCalls(localStorage,archived.value);
    else if(_cacheStatus.fastRows>0){
      try{acknowledgeCapturedCalls(localStorage,JSON.parse(localStorage.getItem('taraCallLog_v1')||'[]'));}catch(_){}
    }
    const _saved=_cacheStatus.fastRows>0;
    // Also persist to sessionStorage as a safety net (cleared on tab close but survives refresh)
    if(_saved){
      try{
        const _session=_mergedForStorage.slice(-200);
        sessionStorage.setItem('taraCallLog_session',JSON.stringify(_session));
      }catch(_){}
    }
    };
    const _persistTimer=setTimeout(_persistLocal,Math.max(0,2500-(Date.now()-_lastLocalPersistAt)));
    if(_callLogHydratedRef.current){
      const _publicationKey=callLogPublicationKey(taraCallLogRef.current.slice(-TARA_CALL_LOG_CAP));
      const _criticalChange=_callLogPublicationKeyRef.current!==_publicationKey;
      _callLogPublicationKeyRef.current=_publicationKey;
      // V8.3: Switch from simple cloudWriteDebounced to RMW. Reads current cloud first,
      //   merges with local, writes union — protects against last-write-wins clobbering
      //   when multiple tabs (BTC + ETH) write to the same memory/taraCallLog doc.
      cloudWriteDebouncedRMW(
        'memory/taraCallLog',
        ()=>taraCallLogRef.current.slice(-TARA_CALL_LOG_CAP),
        (cloudData,localEntries)=>{
          const cloudEntries=(cloudData&&Array.isArray(cloudData.entries))?cloudData.entries:[];
          return callLogCloudPayload(cloudEntries,localEntries,_mergeCallLogEntries,_stripEntryForCloud,_TARA_CLOUD_LOG_CAP);
        },
        // New originals / settlement evidence bypass the ordinary telemetry batch.
        // Existing per-document owner, CAS, exact no-op gate and retry stay intact.
        _criticalChange?1500:60000,
      );
      // V12.8: bump the tiny realtime heartbeat with this device's post-write signature.
      //   RMW so the doc only ever moves UP (element-wise max). A device that is behind
      //   writing its lower sig will NOT pull the heartbeat back down. Throttled so it
      //   only fires when our local signature actually advanced since last beat.
      try{
        const _localSig=_computeLogSig(taraCallLogRef.current);
        const _last=_lastBeatSigRef.current;
        if(!_last||_sigBehind(_last,_localSig)){
          _lastBeatSigRef.current=_localSig;
          cloudWriteDebouncedRMW(
            _SYNC_HEARTBEAT_PATH,
            ()=>_computeLogSig(taraCallLogRef.current),
            (cloudData,localSig)=>{
              const _cloud=cloudData&&cloudData.sig?cloudData.sig:null;
              const _next=_sigMax(_cloud,localSig);
              if(_cloud&&!_sigBehind(_cloud,localSig))return null;
              return{sig:_next,by:_taraDeviceId,version:TARA_BUILD_VERSION,ts:Date.now()};
            },
            3000,
          );
        }
      }catch(_hbErr){}
    } else if(taraCallLog.length>0){
      // Pre-hydration but we have data → mark pending. Once cloudWatch fires we'll flush.
      _logWritePendingRef.current=true;
    }
    return()=>{_persistCancelled=true;clearTimeout(_persistTimer);clearTimeout(_archiveRetryTimer);};
  },[taraCallLog]);
  // V10.7.95: IndexedDB async load on mount.
  //   localStorage gives instant first paint. IDB then loads the full history
  //   (potentially 5000+ entries) and merges it in. Any entries in IDB that
  //   aren't in localStorage (old trades that fell off the 500-entry cache) get
  //   added back. Resolved always beats unresolved.
  const _idbLoadedRef=useRef(false);
  useEffect(()=>{
    if(_idbLoadedRef.current)return;
    _idbLoadedRef.current=true;
    _idbRead('taraCallLog').then(idbEntries=>{
      if(!Array.isArray(idbEntries)||!idbEntries.length)return;
      setTaraCallLog(prev=>{
        if(idbEntries.length<=prev.length){
          // IDB has same or fewer — check if it has older entries not in prev,
          //   OR different results (post-reconcile corrections) on shared ids.
          const prevById=new Map(prev.map(e=>[e?.id,e]));
          const hasOlder=idbEntries.some(e=>e&&!prevById.has(e.id));
          const hasDifferentResult=idbEntries.some(e=>{
            if(!e)return false;const p=prevById.get(e.id);
            return p&&e.result&&p.result&&e.result!==p.result;
          });
          if(!hasOlder&&!hasDifferentResult)return prev; // truly nothing new
        }
        // Merge: IDB entries + current state, resolved wins, dedup by windowId
        const map=new Map();
        [...idbEntries,...prev].forEach(e=>{
          if(!e)return;
          const key=(e.asset||'BTC')+'|'+(e.windowId||String(e.id||''))+'|'+(e.windowType||'15m');
          const ex=map.get(key);
          if(!ex){map.set(key,e);return;}
          if((ex.result==='SITOUT'||ex.result==='NO_TRADE')&&(e.dir==='UP'||e.dir==='DOWN')&&e.result!=='SITOUT'&&e.result!=='NO_TRADE'){map.set(key,e);return;}
          if((e.result==='SITOUT'||e.result==='NO_TRADE')&&(ex.dir==='UP'||ex.dir==='DOWN')&&ex.result!=='SITOUT'&&ex.result!=='NO_TRADE')return;
          if(!ex.result&&e.result){map.set(key,e);return;}
          if((e.result==='WIN'||e.result==='LOSS')&&(ex.result==='SITOUT'||ex.result==='NO_TRADE')){map.set(key,e);return;} /* V13.3.3: real trade outranks stored sit-out */
          if(ex.result&&e.result&&((e.manualEdit===true&&ex.manualEdit!==true)||(ex.result===e.result&&(e.id||0)>(ex.id||0)))){map.set(key,e);return;} /* V13.3.1: immutable settlement - reconcile passes, divergent flips blocked */
        });
        const merged=Array.from(map.values()).sort((a,b)=>(a.id||0)-(b.id||0));
        // V10.9.5: don't bail on equal length alone — IDB may hold the same COUNT
        //   but corrected results (post-reconcile) or different entries than the
        //   localStorage cache. Compare resolved-count and a cheap content hash so
        //   a reconcile that changed W/L (but not count) still loads as base.
        const _resolvedCount=(arr)=>arr.reduce((n,e)=>n+((e&&(e.result==='WIN'||e.result==='LOSS'))?1:0),0);
        const _sig=(arr)=>{let h=0;for(const e of arr){if(!e)continue;const s=(e.id||0)+'|'+(e.result||'');for(let i=0;i<s.length;i++){h=(h*31+s.charCodeAt(i))|0;}}return h;};
        if(merged.length===prev.length&&_resolvedCount(merged)===_resolvedCount(prev)&&_sig(merged)===_sig(prev))return prev;
        try{console.info(`[IDB] Loaded ${merged.length} entries from IndexedDB (${idbEntries.length} stored, ${prev.length} in localStorage cache)`);}catch(_){}
        return merged;
      });
    }).catch(()=>{});
  },[]);
  // ──────────────────────────────────────────────────────────────────────────────────
  // V10.9.3: AUTONOMOUS THEORY LAB — load persisted log on mount, derive stats.
  //   Fully additive/dormant. Loads from its own IDB key 'taraTheoryLog' —
  //   never touches taraCallLog or any live-trading storage.
  // ──────────────────────────────────────────────────────────────────────────────────
  const _theoryIdbLoadedRef=useRef(false);
  useEffect(()=>{
    if(_theoryIdbLoadedRef.current)return;
    _theoryIdbLoadedRef.current=true;
    _idbRead('taraTheoryLog').then(entries=>{
      if(!Array.isArray(entries)||!entries.length)return;
      setTheoryLog(entries);
      entries.forEach(e=>{if(e&&e.windowId)_theoryScoredWidsRef.current.add(e.windowId);});
      const stats={
        range:{n:0,correct:0},momentum:{n:0,correct:0},
        firstMoveFade:{n:0,correct:0},crowdFade:{n:0,correct:0},
      };
      entries.forEach(e=>{
        if(!e)return;
        const bump=(key,correct)=>{if(correct===null||correct===undefined)return;stats[key].n++;if(correct)stats[key].correct++;};
        bump('range',e.range_correct);
        bump('momentum',e.momentum_correct);
        bump('firstMoveFade',e.first_move_fade_correct);
        bump('crowdFade',e.crowd_fade_correct);
      });
      setTheoryStats(stats);
    }).catch(()=>{});
  },[]);

  // ──────────────────────────────────────────────────────────────────────────────────
  // V10.9.3 THEORY LAB — EFFECT 1: per-tick tracking (own high/low, first-move
  //   sample at open+90s, crowd_pct capture). Runs every currentPrice tick.
  //   Fully isolated from windowHighRef/windowLowRef/kalshiYesPrice consumers —
  //   reads them but never writes to shared refs/state.
  // ──────────────────────────────────────────────────────────────────────────────────
  const _theoryHighLowRef=useRef({high:0,low:0});
  useEffect(()=>{
    if(currentAsset!=='BTC')return;
    if(!currentPrice||!Number.isFinite(currentPrice)||currentPrice<=0)return;
    try{
      // Track this window's high/low independently (used as prev_high/prev_low
      //   for the NEXT window's RANGE CALL once this window closes).
      const hl=_theoryHighLowRef.current;
      if(hl.high===0||currentPrice>hl.high)hl.high=currentPrice;
      if(hl.low===0||currentPrice<hl.low)hl.low=currentPrice;

      // FIRST-MOVE-FADE: sample at open+90s.
      const fm=_theoryFirstMoveRef.current;
      if(fm&&!fm.sampled&&Date.now()-fm.openTime>=90000){
        fm.sampled=true;
        const _moveBps=fm.openPrice>0?((currentPrice-fm.openPrice)/fm.openPrice)*10000:0;
        const _dir=Math.abs(_moveBps)<3?'FLAT':(_moveBps>0?'UP':'DOWN');
        const _fade=_dir==='FLAT'?'FLAT':(_dir==='UP'?'DOWN':'UP');
        const _p=_theoryPendingRef.current;
        if(_p&&_p.windowId===fm.wid){
          _p.first_move_dir=_dir;
          _p.first_move_fade_call=_fade;
        }
      }

      // CROWD-FADE: capture first available kalshiYesPrice for this window.
      const _p2=_theoryPendingRef.current;
      if(_p2&&_p2.crowd_pct===undefined&&kalshiYesPrice!=null&&Number.isFinite(kalshiYesPrice)){
        const _cp=Number(kalshiYesPrice);
        _p2.crowd_pct=_cp;
        // minority side = fade the crowd. Near-50/50 → SKIP (no edge either way).
        _p2.crowd_fade_call=_cp>52?'DOWN':(_cp<48?'UP':'SKIP');
      }
    }catch(_){}
  },[currentPrice,kalshiYesPrice,currentAsset]);

  // ──────────────────────────────────────────────────────────────────────────────────
  // V10.9.3 THEORY LAB — EFFECT 2: ROLLOVER. Own rollover detection (separate
  //   from lastWindowRef used by the main resolution effect, to stay fully
  //   isolated). On each window boundary:
  //     Phase A — score the PREVIOUS window's 4 theory calls against its
  //       approximate close (spot price at the rollover instant — a shadow
  //       system doesn't need Kalshi-perfect settlement precision).
  //     Phase B — seed the NEW window's pending record with prev_high/prev_low
  //       (from _theoryHighLowRef, the window that just closed) and the just-
  //       closed window's outcome (for MOMENTUM CALL, filled once the new
  //       strike is known — see Effect 3).
  // ──────────────────────────────────────────────────────────────────────────────────
  useEffect(()=>{
    if(currentAsset!=='BTC')return;
    if(!timeState.nextWindow)return;
    if(timeState.nextWindow===_theoryLastWindowRef.current)return;
    const _firstRun=_theoryLastWindowRef.current==='';
    _theoryLastWindowRef.current=timeState.nextWindow;
    if(_firstRun){
      // Initialize tracking for the currently-open window without scoring
      //   anything (no prior pending record exists yet).
      _theoryHighLowRef.current={high:0,low:0};
      return;
    }
    try{
      const _closePrice=Number(currentPriceRef.current||currentPrice)||0;
      const _prevPending=_theoryPendingRef.current;

      // ── Phase A: score the window that just closed ──
      if(_prevPending&&_closePrice>0&&_prevPending.strike>0&&!_theoryScoredWidsRef.current.has(_prevPending.windowId)){
        const _outcomeDir=_closePrice>_prevPending.strike?'UP':(_closePrice<_prevPending.strike?'DOWN':null);
        if(_outcomeDir){
          _theoryScoredWidsRef.current.add(_prevPending.windowId);
          const _correctOf=(call)=>(call==null||call==='SKIP'||call==='FLAT')?null:(call===_outcomeDir);
          const _entry={
            id:Date.now(),
            windowId:_prevPending.windowId,
            time:Date.now(),
            asset:'BTC',
            windowType:_prevPending.windowType||windowType,
            strike:_prevPending.strike,
            closingPrice:_closePrice,
            closeSource:'spot-at-rollover', // approximation — shadow system, not used for live trading
            outcomeDir:_outcomeDir,
            prev_high:_prevPending.prev_high??null,
            prev_low:_prevPending.prev_low??null,
            first_move_dir:_prevPending.first_move_dir??null,
            crowd_pct:_prevPending.crowd_pct??null,
            round_number_flag:!!_prevPending.round_number_flag,
            range_call:_prevPending.range_call??null,
            momentum_call:_prevPending.momentum_call??null,
            first_move_fade_call:_prevPending.first_move_fade_call??null,
            crowd_fade_call:_prevPending.crowd_fade_call??null,
            range_correct:_correctOf(_prevPending.range_call),
            momentum_correct:_correctOf(_prevPending.momentum_call),
            first_move_fade_correct:_correctOf(_prevPending.first_move_fade_call),
            crowd_fade_correct:_correctOf(_prevPending.crowd_fade_call),
          };
          setTheoryLog(prev=>{
            const next=[...prev,_entry].slice(-2000);
            _idbWrite('taraTheoryLog',next).catch(()=>{});
            return next;
          });
          setTheoryStats(prev=>{
            const np={range:{...prev.range},momentum:{...prev.momentum},firstMoveFade:{...prev.firstMoveFade},crowdFade:{...prev.crowdFade}};
            const _bump=(key,correct)=>{if(correct===null)return;np[key]={n:np[key].n+1,correct:np[key].correct+(correct?1:0)};};
            _bump('range',_entry.range_correct);
            _bump('momentum',_entry.momentum_correct);
            _bump('firstMoveFade',_entry.first_move_fade_correct);
            _bump('crowdFade',_entry.crowd_fade_correct);
            return np;
          });
        }
      }

      // ── Phase B: seed the new window's pending record ──
      const _newWid=computeWindowId(windowType);
      const _justClosedHL=_theoryHighLowRef.current;
      let _justClosedOutcome=null;
      if(_prevPending&&_closePrice>0&&_prevPending.strike>0){
        _justClosedOutcome=_closePrice>_prevPending.strike?'UP':(_closePrice<_prevPending.strike?'DOWN':null);
      }
      _theoryPendingRef.current={
        windowId:_newWid,
        windowType,
        strike:0, // filled by Effect 3 once the new strike is known
        prev_high:_justClosedHL.high||null,
        prev_low:_justClosedHL.low||null,
        prevOutcomeDir:_justClosedOutcome,
        range_call:null,
        momentum_call:null,
        first_move_dir:undefined,
        first_move_fade_call:undefined,
        crowd_pct:undefined,
        crowd_fade_call:undefined,
        round_number_flag:false,
      };
      // Reset trackers for the new window
      _theoryHighLowRef.current={high:0,low:0};
      _theoryFirstMoveRef.current=null;
    }catch(_){}
  },[timeState.nextWindow,currentAsset,windowType,currentPrice]);

  // ──────────────────────────────────────────────────────────────────────────────────
  // V10.9.3 THEORY LAB — EFFECT 3: fill strike-dependent fields once the new
  //   window's Kalshi strike (targetMargin) becomes known. Computes RANGE CALL
  //   (prev window's high/low vs new strike), MOMENTUM CALL (prev window's
  //   settled direction), ROUND NUMBER FLAG, and sets up the first-move timer
  //   + opportunistic crowd_pct capture (in case kalshiYesPrice is already
  //   available the instant the strike lands).
  // ──────────────────────────────────────────────────────────────────────────────────
  useEffect(()=>{
    if(currentAsset!=='BTC')return;
    const _p=_theoryPendingRef.current;
    if(!_p||_p.strike>0)return; // already filled, or no pending record yet
    if(!targetMargin||!Number.isFinite(targetMargin)||targetMargin<=0)return;
    if(_p.windowId!==computeWindowId(windowType))return; // stale (window already rolled again)
    try{
      _p.strike=targetMargin;

      // RANGE CALL: where does the new strike sit within the previous window's range?
      if(_p.prev_high!=null&&_p.prev_low!=null&&_p.prev_high>_p.prev_low){
        const _range=_p.prev_high-_p.prev_low;
        const _pos=(targetMargin-_p.prev_low)/_range; // 0=at prev low, 1=at prev high
        if(_pos<=(1/3))_p.range_call='UP';
        else if(_pos>=(2/3))_p.range_call='DOWN';
        else _p.range_call='SKIP';
      } else {
        _p.range_call=null; // no previous-window range yet (first window of session)
      }

      // MOMENTUM CALL: same direction as the previous window's settled outcome
      _p.momentum_call=_p.prevOutcomeDir||null;

      // ROUND NUMBER FLAG (display only, not scored): within $75 of nearest $X,000 or $X,500
      const _mod500=((targetMargin%500)+500)%500;
      const _distTo500=Math.min(_mod500,500-_mod500);
      _p.round_number_flag=_distTo500<=75;

      // FIRST-MOVE-FADE: start the open/open+90s sampler
      _theoryFirstMoveRef.current={wid:_p.windowId,openPrice:targetMargin,openTime:Date.now(),sampled:false};

      // CROWD-FADE: opportunistic immediate capture if Kalshi yes-price already cached
      if(_p.crowd_pct===undefined&&kalshiYesPrice!=null&&Number.isFinite(kalshiYesPrice)){
        const _cp=Number(kalshiYesPrice);
        _p.crowd_pct=_cp;
        _p.crowd_fade_call=_cp>52?'DOWN':(_cp<48?'UP':'SKIP');
      }
    }catch(_){}
  },[targetMargin,windowType,currentAsset,kalshiYesPrice]);

  // ──────────────────────────────────────────────────────────────────────────────────
  // V10.9.3 THEORY LAB — PROMOTION NOTIFICATION. One-time toast when any theory
  //   crosses 60%+ WR over 50+ scored rounds. Never auto-promotes — promotion
  //   (setting a live signal weight) requires the user's explicit action
  //   elsewhere; this is informational only.
  // ──────────────────────────────────────────────────────────────────────────────────
  useEffect(()=>{
    try{
      const _labels={range:'Range Call',momentum:'Momentum Call',firstMoveFade:'First-Move-Fade Call',crowdFade:'Crowd-Fade Call'};
      Object.entries(theoryStats).forEach(([key,v])=>{
        if(theoryPromoNotified[key])return;
        if(v.n>=50&&(v.correct/v.n)>=0.60){
          setTheoryPromoNotified(prev=>({...prev,[key]:true}));
          const _wr=Math.round((v.correct/v.n)*100);
          try{
            pushToast?.('theory-promo',`Theory Lab: ${_labels[key]}`,`Hitting ${_wr}% over ${v.n} rounds — consider promoting to a live signal weight.`,{durationMs:8000,cooldown:0});
          }catch(_){}
        }
      });
    }catch(_){}
  },[theoryStats,theoryPromoNotified]);
  // V10.7.86c: key now uses id (timestamp) instead of result so every state change
  //   broadcasts. Old key (windowId|windowType|result) meant a resolved entry and its
  //   pending version had different keys — the pending version would re-broadcast even
  //   after the resolved version was already synced, causing stale overwrites.
  //   New key: windowId|windowType|id — unique per snapshot, no stale replay.
  const _broadcastInitRef=useRef(false);
  const _broadcastedKeysRef=useRef(new Set());
  const _receivedFromBroadcastRef=useRef(new Set());
  useEffect(()=>{
    if(!_broadcastInitRef.current){
      taraCallLog.forEach(e=>{
        const k=(e?.windowId||'')+'|'+(e?.windowType||'')+'|'+(e?.id||'');
        _broadcastedKeysRef.current.add(k);
      });
      _broadcastInitRef.current=true;
      return;
    }
    taraCallLog.forEach(e=>{
      const k=(e?.windowId||'')+'|'+(e?.windowType||'')+'|'+(e?.id||'');
      if(_broadcastedKeysRef.current.has(k))return;
      _broadcastedKeysRef.current.add(k);
      if(_receivedFromBroadcastRef.current.has(k))return;
      try{broadcastToOtherTabs('callLogEntry',{entry:e});}catch(_){}
    });
  },[taraCallLog]);
  useEffect(()=>{
    const unsub=subscribeToOtherTabs((msg)=>{
      if(!msg||msg.type!=='callLogEntry')return;
      const incoming=msg.payload?.entry;
      if(!incoming||!incoming.windowId)return;
      const k=(incoming.windowId||'')+'|'+(incoming.windowType||'')+'|'+(incoming.id||'');
      _receivedFromBroadcastRef.current.add(k);
      setTaraCallLog(prev=>{
        const merged=_mergeCallLogEntries(prev,[incoming]);
        if(merged.length===prev.length){
          const _samePos=merged.every((e,i)=>e===prev[i]);
          if(_samePos)return prev;
        }
        return merged;
      });
    });
    return unsub;
  },[_mergeCallLogEntries]);
  // V5.6.2: Real-time listener for the call log. Merges by id — cloud entries that local
  //   doesn't have get added; cloud entries that local has but with a result populated
  //   (resolved on another device after the window rolled over) overwrite local. Sorted
  //   by id (timestamp), capped at 500.
  // V5.6.9: Backfill windowId on entries that don't have one (legacy from V5.6.8 and earlier).
  //   Computes from e.time + e.windowType so that legacy entries from the same window get
  //   the same windowId as new entries — and dedup collapses them together.
  const _backfillCallLogId=React.useCallback((e)=>{
    if(!e||e.windowId)return e;
    if(!e.time||!e.windowType)return e;
    const winMs=e.windowType==='15m'?900000:300000;
    const bucket=Math.floor(e.time/winMs)*winMs;
    return{...e,windowId:`${e.windowType}-${new Date(bucket).toISOString()}`};
  },[]);
  // V13.4.338: extracted out of the cloudWatch callback below so it can ALSO
  //   run from a plain mount-time effect, independent of cloud data ever
  //   arriving. Before this, the ENTIRE scan lived inside
  //   cloudWatch('memory/taraCallLog',(d)=>{...if(!d...)return;...}) -- if
  //   Supabase never answers (billing/quota restriction, confirmed live this
  //   session as 402s on tara_state), this scan never ran at all, on any
  //   device, no matter how many times the app was reopened. This function
  //   only reads refs (taraCallLogRef, pendingResolutionRef), so it is safe
  //   to call from either trigger.
  const _scanStalePending=React.useCallback((maxAgeMs)=>{
    // V8.1: Stale-pending resolver. Scan for entries that should have been
    //   resolved by now (window closed >15min ago, still pending). Queues them for the
    //   settlement fetcher. Fixes the 13-hour-old pending entries from user's call log.
    // V13.4.342: maxAgeMs is now an optional override (default stays 15min)
    //   so the 20s resolver interval below can ALSO call this with a much
    //   shorter floor -- see that call site for why.
    const _maxAge=Number.isFinite(maxAgeMs)&&maxAgeMs>0?maxAgeMs:15*60*1000;
    try{
      const _now=Date.now();
      const _stale=(taraCallLogRef.current||[]).filter(e=>{
        if(!e)return false;
        const needsOfficial=e.originalDecision?.learningFeatures&&isDirection(e.dir)&&!e.officialSettlement&&!e.manualEdit&&!e.manualEditedAt;
        if(e.result&&!needsOfficial)return false;
        if(needsOfficial&&_now-windowCloseMs(e.windowId)>86400000)return false;
        if(e.dir==='NO_TRADE')return false; // explicit no-go, never had a result expected
        const _winMs=e.windowType==='15m'?900000:300000;
        if(!e.windowId)return false;
        const _winStart=e.windowId.match(/-(.+)$/)?.[1];
        if(!_winStart)return false;
        const _closeTime=new Date(_winStart).getTime()+_winMs;
        return _now-_closeTime>(needsOfficial?30000:_maxAge);
      });
      if(_stale.length>0&&pendingResolutionRef.current){
        // Add to settlement queue if not already there
        _stale.forEach(e=>{
          const _winStart=e.windowId.match(/-(.+)$/)?.[1];
          if(!_winStart)return;
          const _winMs=e.windowType==='15m'?900000:300000;
          const _closeTime=new Date(_winStart).getTime()+_winMs;
          if(!pendingResolutionRef.current.some(p=>p.tradeId===e.id)){
            pendingResolutionRef.current.push({
              tradeId:e.id,
              windowCloseTime:_closeTime,
              attempts:0,
              asset:e.asset||'BTC',
              windowType:e.windowType,
              strike:e.strike,
              dir:e.dir,
              _v8_1_stalePending:true,
              // V13.4.248: tells the resolver below which array actually holds
              //   this entry. Without it every item queued from here got looked
              //   up in tradeLogRef -- a different, largely-vestigial array --
              //   found nothing, and was silently dropped after 5 minutes with
              //   its taraCallLog entry never touched. Confirmed live: two real
              //   entries (2026-08-25, 2026-08-28) sat at result:null indefinitely,
              //   one for 4 days, until corrected by hand against Kalshi's
              //   settled market for each ticker.
              source:'taraCallLog',
            });
          }
        });
        try{console.info('[V8.1] Queued',_stale.length,'stale pending entries for re-resolve');}catch(_){}
      }
    }catch(_){}
  },[]);
  // V13.4.338: mount-time trigger, independent of cloud sync. A short delay
  //   lets localStorage-hydrated state settle into taraCallLogRef first.
  useEffect(()=>{
    const t=setTimeout(_scanStalePending,3000);
    return()=>clearTimeout(t);
  },[_scanStalePending]);
  useEffect(()=>{
    const unsub=cloudWatch('memory/taraCallLog',(d)=>{
      _callLogHydratedRef.current=true;
      if(Array.isArray(d?.entries))_learningCloudSourceRef.current=learningSummarySource(d.entries);
      if(!d||!Array.isArray(d.entries)||!d.entries.length)return;
      try{
        const _now=Date.now();
        let _newLatencyRows=false;
        for(const _e of d.entries){
          if(!_e||!_e.id||!_e.device||_e.device===_taraDeviceId)continue;
          if(_syncLatencySeenRef.current.has(_e.id))continue;
          _syncLatencySeenRef.current.add(_e.id);
          _syncLatencyRef.current.push({windowId:_e.windowId,fromDevice:_e.device,committedAt:_e.id,receivedAt:_now,latencyMs:_now-_e.id});
          _newLatencyRows=true;
        }
        if(_newLatencyRows){
          if(_syncLatencyRef.current.length>500)_syncLatencyRef.current=_syncLatencyRef.current.slice(-500);
          // V13.4.254: dropped a tara_syncLatencyLog_v1 write -- never read back.
          //   _syncLatencyRef is still live in memory for the sync panel.
          if(typeof window!=='undefined')window._taraSyncLatencyLog=_syncLatencyRef.current;
        }
      }catch(_){}
      setTaraCallLog(prev=>{
        // V5.6.9: Dedup by windowId+windowType, not by entry id. Multiple devices commit
        //   their own entries with different ids for the same window — these need to
        //   coalesce to ONE row per window. Prefer resolved over unresolved; if both
        //   resolved with same outcome, prefer earliest committed (lowest id).
        //   Legacy entries get windowId backfilled from time+windowType.
        // V6.5: Sanitize at merge time too. Without this, corrupt entries dropped at load
        //   can be re-introduced when Firestore re-syncs (explains why $41K close survived).
        // V6.5.3: Also auto-resolve misclassified SITOUT entries on incoming cloud data —
        //   if entry has dir='UP'/'DOWN' + valid strike + valid close, compute the real WIN/LOSS.
        const _isCorrupt=(e)=>{
          if(!e||(e.result!=='WIN'&&e.result!=='LOSS'))return false;
          if(e.strike&&e.closingPrice&&Math.abs(e.closingPrice-e.strike)/e.strike>0.20)return true;
          if(e.outcomeDir&&(e.dir==='UP'||e.dir==='DOWN')){
            const _shouldBeWin=e.dir===e.outcomeDir;
            if(_shouldBeWin&&e.result==='LOSS')return true;
            if(!_shouldBeWin&&e.result==='WIN')return true;
          }
          return false;
        };
        // V7.0.5: Detect & fix misclassified asset tags. The V7.0.3 stale-closure bug
        //   caused some entries (esp. SOL trades) to be tagged as ETH. Strike magnitude
        //   makes the right asset unambiguous: BTC strikes are 10K+, ETH 100-10K, SOL 1-1000,
        //   DOGE under 1. If the entry's asset tag doesn't match the strike magnitude,
        //   correct it. Untagged entries (pre-V6.5.8) get the inferred asset.
        // V7.1: BTC + ETH only.
        const _inferAssetFromStrike=(strike)=>{
          if(!strike||!Number.isFinite(Number(strike)))return null;
          const s=Number(strike);
          if(s>=10000)return'BTC';
          return'BTC';
          return null; // strikes <$100 are legacy SOL/DOGE — caller drops them
        };
        const _fixAsset=(e)=>{
          if(!e||!e.strike)return e;
          const _inferred=_inferAssetFromStrike(e.strike);
          if(!_inferred)return null; // signal to drop (legacy SOL/DOGE entry)
          if(e.asset===_inferred)return e;
          try{console.info('[V7.1] Fixing entry asset tag:',e.asset||'(none)','→',_inferred,'(strike',e.strike,')');}catch(_){}
          return{...e,asset:_inferred};
        };
        const _autoResolve=(e)=>{
          if(!e||e.result!=='SITOUT')return e;
          if((e.dir!=='UP'&&e.dir!=='DOWN')||!e.strike||!e.closingPrice)return e;
          if(Math.abs(e.closingPrice-e.strike)/e.strike>=0.20)return e;
          const _outcome=e.closingPrice>e.strike?'UP':e.closingPrice<e.strike?'DOWN':null;
          if(!_outcome)return e;
          return{...e,result:e.dir===_outcome?'WIN':'LOSS',outcomeDir:_outcome};
        };
        // V8.8.6: asset in dedup key so BTC + ETH coexist for the same windowId
        const _key=(e)=>(e.asset||'BTC')+'|'+e.windowId+'|'+e.windowType;
        const byKey=new Map();
        const _shouldReplace=(existing,incoming)=>{
          // V10.7.60 — LOCALSTORAGE IS AUTHORITATIVE
          //   Supabase grace period means cloud data can be days stale.
          //   localStorage + sessionStorage = source of truth. Cloud = additive only.
          //   1. Local resolved → NEVER replace
          //   2. Local pending + cloud resolved → replace (cloud has the outcome)
          //   3. Both pending → keep local (fresher)
          const _existingResolved=existing.result==='WIN'||existing.result==='LOSS';
          const _incomingResolved=incoming.result==='WIN'||incoming.result==='LOSS';
          if(_existingResolved)return false;
          if(!_existingResolved&&_incomingResolved)return true;
          return false;
        };
        // V8.8.4 TDZ FIX: `changed` MUST be declared before any forEach that touches it.
        //   V8.7 introduced the prev.forEach normalization pass above d.entries.forEach
        //   but didn't move the `let changed=false` up with it — so when prev held real
        //   call-log entries from localStorage AND normalization triggered, the callback
        //   assigned `changed=true` before the `let` ran. TDZ. Minified, it surfaced as
        //   "Cannot access 'ee' before initialization"; unminified V8.8.4 surfaced it as
        //   "Cannot access 'changed' before initialization" with a real stack frame in
        //   this useState lazy initializer. Empty-localStorage envs (jsdom, fresh
        //   Chromium) never iterated, so the bug only fired for users with logged calls.
        let changed=false;
        prev.forEach(e=>{
          let f=_backfillCallLogId(e);
          if(!f||!f.windowId)return;
          // V8.7: Normalize entry — backfill derivable fields (closingGapBps, asset default, etc)
          const _normalized=normalizeCallLogEntry(f);
          if(_normalized&&_normalized!==f){f=_normalized;changed=true;}
          f=_fixAsset(f);
          if(!f)return; // V7.1: legacy SOL/DOGE entry — drop
          f=_autoResolve(f);
          // V10.9.24: NEVER drop a local entry on cloud sync. The old _isCorrupt drop
          //   silently deleted hand-corrected trades whose outcomeDir had gone stale
          //   (result said WIN, outcomeDir still said the losing side) - that was the
          //   'trades vanish after reconcile/sync' data loss. Heal instead: trust the
          //   stored result (manual = Kalshi truth; auto = self-consistent) and snap
          //   outcomeDir to match so the entry stays counted and never re-flags.
          if((f.result==='WIN'||f.result==='LOSS')&&(f.dir==='UP'||f.dir==='DOWN')){
            const _wantOD=f.result==='WIN'?f.dir:(f.dir==='UP'?'DOWN':'UP');
            if(f.outcomeDir&&f.outcomeDir!==_wantOD){f={...f,outcomeDir:_wantOD};changed=true;}
          }
          byKey.set(_key(f),f);
        });
        d.entries.forEach(raw=>{
          let e=_backfillCallLogId(raw);
          if(!e||!e.id||!e.windowId)return;
          const _withAsset=_fixAsset(e);
          if(_withAsset===null){changed=true;return;} // V7.1: legacy SOL/DOGE — drop
          if(_withAsset!==e){e=_withAsset;changed=true;}
          const _resolved=_autoResolve(e);
          if(_resolved!==e){e=_resolved;changed=true;}
          if(_isCorrupt(e)){changed=true;return;}
          const k=_key(e);
          const existing=byKey.get(k);
          if(!existing){byKey.set(k,e);changed=true;}
          else if(_shouldReplace(existing,e)){byKey.set(k,e);changed=true;}
          // V13.4.176 FIX: _shouldReplace's "local resolved -> NEVER replace" rule
          //   (V10.7.60) is deliberate and stays -- it exists specifically to stop a
          //   stale cloud read from clobbering a fresher local resolution. But it also
          //   means a genuine cloud-side correction (e.g. a manual Supabase fix after
          //   a bad local auto-resolve) can NEVER reach this device, forever, with no
          //   visibility that it's being refused. Surface the disagreement instead of
          //   silently swallowing it, so a real correction doesn't just vanish.
          else if(existing.result&&e.result&&existing.result!==e.result&&
              (existing.result==='WIN'||existing.result==='LOSS')&&(e.result==='WIN'||e.result==='LOSS')){
            try{console.warn('[V13.4.176] call-log entry '+k+' resolved differently on cloud ('+e.result+') than local ('+existing.result+') -- local kept per V10.7.60, cloud version discarded. If cloud is the correction, this device will not see it.');}catch(_w){}
          }
        });
        // Detect if prev itself had duplicates that backfill would now collapse
        if(!changed&&Array.from(byKey.values()).length!==prev.length)changed=true;
        if(!changed)return prev;
        return Array.from(byKey.values()).sort((a,b)=>(a.id||0)-(b.id||0)).slice(-TARA_CALL_LOG_CAP);
      });
      // V8.1: Force-flush any pending pre-hydration writes. Without this, calls made
      //   during the first 1-3s of page load (before cloudWatch hydrates) were silently
      //   lost — never written to cloud.
      // V8.3: Use RMW to merge with cloud (which by now has just been hydrated from the
      //   snapshot we're processing). This avoids clobbering any other tab's recent entries.
      if(_logWritePendingRef.current){
        _logWritePendingRef.current=false;
        // Fire after the setState above commits — small timeout to let React flush.
        setTimeout(()=>{
          try{
            const _curr=taraCallLogRef.current||[];
            if(_curr.length>0){
              cloudWriteDebouncedRMW(
                'memory/taraCallLog',
                ()=>taraCallLogRef.current.slice(-TARA_CALL_LOG_CAP),
                (cloudData,localEntries)=>{
                  const cloudEntries=(cloudData&&Array.isArray(cloudData.entries))?cloudData.entries:[];
                  const merged=_mergeCallLogEntries(cloudEntries,localEntries);
                  return{entries:merged.slice(-_TARA_CLOUD_LOG_CAP).map(_stripEntryForCloud)}; // V13.4.187: was unstripped
                },
                50, // very short debounce — this IS the flush
              );
            }
          }catch(_){}
        },100);
      }
      // V13.4.338: this used to be the ONLY place the stale-pending scan ran
      //   (see _scanStalePending above, defined once and shared) -- now it's
      //   just one of two triggers, the other being a plain mount effect.
      _scanStalePending();
    });
    return unsub;
  },[_backfillCallLogId,_scanStalePending]);
  // V12.8: SYNC-HEARTBEAT SUBSCRIBER + CATCH-UP PULL
  //   Subscribes to the tiny realtime heartbeat. When the cloud signature is ahead of
  //   this device's local log signature, another device has committed entries we have
  //   not seen. We then do ONE catch-up read of the big memory/taraCallLog doc and merge
  //   it in via _mergeCallLogEntries (resolved-beats-pending, manualEdit-wins). Guarded
  //   by a cooldown + in-flight flag so two behind devices cannot pull-storm each
  //   other; once converged, local==cloud and no further pulls fire.
  //
  // V13.4.247 EGRESS: cooldown raised 20s -> 3min. `_sigBehind` fires on a
  //   difference of exactly ONE resolved trade or ONE newer id -- no threshold --
  //   so with ~450+ trades/day pool-wide and several devices listening, nearly
  //   every settlement anywhere made every other device "behind" and eligible to
  //   pull. At a 1.36MB doc size that measured 75 full pulls/day from the two
  //   live origins alone: ~102MB/day, the majority of a 5GB/month egress budget
  //   already sitting at 85% (query_logs, 2026-08-29). This doc is the
  //   historical record used for review and telemetry, not the live trading
  //   state (that is state/currentLock_BTC_15m, a separate ~2.4KB doc on its
  //   own fast path) -- being a few minutes behind on it costs nothing a live
  //   lock decision depends on, so the cooldown can be this long safely.
  useEffect(()=>{
    const _doCatchUpPull=async(reason)=>{
      if(_catchUpInFlightRef.current)return;
      const _now=Date.now();
      if(_now-_lastCatchUpPullRef.current<180000)return; // 3min cooldown
      _catchUpInFlightRef.current=true;
      _lastCatchUpPullRef.current=_now;
      try{
        const _cloudData=await cloudSupabaseRead('memory/taraCallLog');
        const _cloudEntries=(_cloudData&&Array.isArray(_cloudData.entries))?_cloudData.entries:[];
        if(!_cloudEntries.length){_catchUpInFlightRef.current=false;return;}
        setTaraCallLog(prev=>{
          const _merged=_mergeCallLogEntries(prev,_cloudEntries);
          // Only commit to state if the merge actually changed something, to avoid
          //   a render/write loop (a state change re-fires the big-log write effect).
          const _ps=_computeLogSig(prev),_ms=_computeLogSig(_merged);
          if(callLogSyncSignature(_merged,_stripEntryForCloud)===callLogSyncSignature(prev,_stripEntryForCloud))return prev;
          try{console.info('[V12.8 SYNC] catch-up pull ('+reason+'): '+prev.length+' -> '+_merged.length+' entries, resolved '+_ps.resolved+' -> '+_ms.resolved);}catch(_){}
          return _merged;
        });
      }catch(e){
        try{console.warn('[V12.8 SYNC] catch-up pull failed',e&&e.message);}catch(_){}
      }finally{
        _catchUpInFlightRef.current=false;
      }
    };
    const _unsub=cloudWatch(_SYNC_HEARTBEAT_PATH,(d)=>{
      if(!d||!d.sig)return;
      // Ignore our own beats (we are already at or above that signature).
      if(d.by===_taraDeviceId)return;
      // V13.4.233: the heartbeat has always carried the peer's build and nothing
      //   ever read it. Every device writes to the SAME doc paths — there is no
      //   per-device or per-deployment scoping — so a device on an old build is
      //   writing its locks and settlements into the same shared record as this
      //   one. That matters because the gates that made this profitable landed in
      //   specific versions (the 70-84c dead-zone block in v13.4.178, the
      //   wrong-day weather ladder fix in v13.4.219), and a device below those is
      //   still making the trades they were built to stop. Recorded here so the
      //   sync panel can say so; comparison is on the v13.4.N build number.
      try{_notePeerBuild(d.by,d.version);}catch(_){}
      const _localSig=_computeLogSig(taraCallLogRef.current);
      if(_sigBehind(_localSig,d.sig)){
        _doCatchUpPull('heartbeat from '+(d.by||'?'));
      }
    });
    return ()=>{try{_unsub&&_unsub();}catch(_){}};
  },[_mergeCallLogEntries]);
  // V7.10.5: PERSONAL SCORECARDS persist + sync. Was in-memory only — every reload reset
  //   to defaults, and no two devices ever agreed. Now: localStorage cached, cloud-synced
  //   via scorecards/personal, max-per-cell merge so any device's increments propagate
  //   without double-counting. (Resets on a single device temporarily lose to max-merge,
  //   but that's a rare action and the user can re-clear once cloud catches up.)
  // V8.3: Switched to RMW write — reads cloud, takes max-per-cell, writes back. Solves
  //   multi-tab clobbering where Tab A's +1 and Tab B's +1 collapsed to one increment.
  // V9.1.9: Legacy scorecards/personal cloud sync DISABLED. Was max-per-cell merge
  //   which produced phantom totals across devices (e.g. Device A 50W/30L + Device B
  //   5W/60L max-merged to 50W/60L = 110 trades neither device ever had). Replaced
  //   by deriving scorecards from taraCallLog directly — the log is already cloud-
  //   synced via memory/taraCallLog with proper transactional first-write-wins merge.
  //   The cloudWatch listener is kept as a NO-OP for backward compatibility — if
  //   another device on V9.1.8 or earlier writes to scorecards/personal, we ignore.
  const _scorecardsRef=useRef(scorecards);
  _scorecardsRef.current=scorecards;
  useEffect(()=>{
    // Cache local copy for legacy reads (BrainView "Recent record" lifetime stat).
    try{localStorage.setItem('taraPersonalScorecards_v1',JSON.stringify(scorecards));}catch(e){}
    // No cloud write — derive-from-log is the source of truth now.
  },[scorecards]);
  // V9.2.0: Personal scorecard is LOCAL ONLY — no cloudWatch listener.
  //   User feedback: "personal record isn't meant for sync, it is local and for
  //   personal devices only." Removed. The local copy persists via localStorage.
  //   Other devices keep their own personal record. Tara's call log + memory
  //   continue to sync globally as a shared learning corpus.
  // V5.6.6: PAST WINDOWS HISTORY — record of how each window resolved. Captured at rollover
  //   regardless of whether Tara called or sat out. Lets users pattern-match recent market
  //   behavior. Capped at 50 most recent. Cloud-synced so the history matches across devices.
  const[pastWindows,setPastWindows]=useState(()=>{
    try{const stored=localStorage.getItem('taraPastWindows_v1');if(stored)return JSON.parse(stored);}catch(e){}
    return[];
  });
  const _pastWindowsHydratedRef=useRef(false);
  // V8.3: Ref for RMW writes to access freshest local state
  const _pastWindowsRef=useRef(pastWindows);
  _pastWindowsRef.current=pastWindows;
  useEffect(()=>{
    try{localStorage.setItem('taraPastWindows_v1',JSON.stringify(pastWindows.slice(-50)));}catch(e){}
    if(_pastWindowsHydratedRef.current){
      // V8.3: RMW merge — protects against multi-tab clobbering
      cloudWriteDebouncedRMW(
        'history/pastWindows',
        ()=>_pastWindowsRef.current.slice(-50),
        (cloudData,localEntries)=>{
          const cloudEntries=(cloudData&&Array.isArray(cloudData.entries))?cloudData.entries:[];
          // V8.8.6: asset in dedup key so BTC + ETH past-window snapshots coexist
          const _key=(e)=>(e?.asset||'BTC')+'|'+(e?.windowId||'')+'|'+(e?.windowType||'');
          const byKey=new Map();
          cloudEntries.forEach(e=>{if(e&&e.windowId)byKey.set(_key(e),e);});
          localEntries.forEach(e=>{if(e&&e.windowId&&!byKey.has(_key(e)))byKey.set(_key(e),e);});
          const merged=Array.from(byKey.values()).sort((a,b)=>(a.time||0)-(b.time||0)).slice(-50);
          // Skip if no change vs cloud
          if(merged.length===cloudEntries.length){
            const _cloudKeys=new Set(cloudEntries.map(_key));
            if(merged.every(e=>_cloudKeys.has(_key(e))))return null;
          }
          return{entries:merged};
        },
        // V10.7.85: throttle to 30s to reduce realtime message usage
        30000,
      );
    }
  },[pastWindows]);
  useEffect(()=>{
    const unsub=cloudWatch('history/pastWindows',(d)=>{
      _pastWindowsHydratedRef.current=true;
      if(!d||!Array.isArray(d.entries)||!d.entries.length)return;
      setPastWindows(prev=>{
        // V5.6.9: Dedup by windowId. Backfill from time+windowType for legacy entries
        //   so duplicates from before this version collapse together.
        const _backfill=(e)=>{
          if(!e||e.windowId)return e;
          if(!e.time||!e.windowType)return e;
          const winMs=e.windowType==='15m'?900000:300000;
          const bucket=Math.floor(e.time/winMs)*winMs;
          return{...e,windowId:`${e.windowType}-${new Date(bucket).toISOString()}`};
        };
        // V8.8.6: asset in dedup key so BTC + ETH past-window snapshots coexist
        const _key=(e)=>(e?.asset||'BTC')+'|'+e.windowId+'|'+e.windowType;
        const byKey=new Map();
        prev.forEach(e=>{const f=_backfill(e);if(f&&f.windowId)byKey.set(_key(f),f);});
        let changed=false;
        d.entries.forEach(raw=>{
          const e=_backfill(raw);
          if(!e||!e.id||!e.windowId)return;
          const k=_key(e);
          if(!byKey.has(k)){byKey.set(k,e);changed=true;}
          else{
            const existing=byKey.get(k);
            if((e.id||0)<(existing.id||0)){byKey.set(k,e);changed=true;}
          }
        });
        if(!changed&&Array.from(byKey.values()).length!==prev.length)changed=true;
        if(!changed)return prev;
        return Array.from(byKey.values()).sort((a,b)=>(a.id||0)-(b.id||0)).slice(-50);
      });
    });
    return unsub;
  },[]);
  // V5.6.7: SELF-LEARNING SYSTEM. After every resolved Tara call, we recompute
  //   per-category win rates from the call log (single source of truth — no drift)
  //   and derive multipliers that bend the gates toward what's actually been working.
  //   Categories: regime, direction, session (Asia/EU/US), tier (exceptional/strong/...)
  //   Multipliers only kick in once a category has ≥5 resolved trades — below that,
  //   sample size is too small to trust.
  const[taraLearnings,setTaraLearnings]=useState(()=>{
    try{const stored=localStorage.getItem('taraLearnings_v1');if(stored)return JSON.parse(stored);}catch(e){}
    return{byRegime:{},byDirection:{},byTier:{},bySession:{},multipliers:{regimeConvAdjust:{},directionConvAdjust:{},sessionConvAdjust:{},tierSamplesAdjust:{}},lastUpdated:0,totalResolved:0};
  });
  const taraLearningsRef=useRef(taraLearnings);
  taraLearningsRef.current=taraLearnings;
  // V10.9.7: speedDialRef removed — dial removed entirely.
  const _learningsHydratedRef=useRef(false);
  const [learningSummaryHydrated,setLearningSummaryHydrated]=useState(false);
  // V8.6: Ref so RMW write reads freshest local state at debounce-fire time
  const _learningsRef=useRef(taraLearnings);
  _learningsRef.current=taraLearnings;
  useEffect(()=>{
    try{localStorage.setItem('taraLearnings_v1',JSON.stringify(taraLearnings));}catch(e){}
    if(_learningsHydratedRef.current){
      // V8.6: RMW with "more samples wins". taraLearnings is derived from taraCallLog —
      //   each device recomputes independently. Without RMW, two devices recomputing in
      //   parallel could clobber each other (Device A finishes last → overwrites Device B's
      //   correct stats). With RMW, the device with MORE total resolved trades wins,
      //   which guarantees we keep the version that's seen the most data.
      cloudWriteDebouncedRMW(
        'learnings/tara',
        ()=>_learningsRef.current,
        (cloudData,localData)=>{
          // Equal-size rolling windows and corrected records invalidate by
          // evidence contents, not by a lifetime maximum sample count.
          if(preferLearningSummary(localData,cloudData,{canonicalSource:_learningCloudSourceRef.current}))return localData;
          return null;
        },
        // V10.7.85: throttle to 120s
        120000,
      );
    }
  },[taraLearnings,learningSummaryHydrated]);
  useEffect(()=>{
    const unsub=cloudWatch('learnings/tara',(d)=>{
      _learningsHydratedRef.current=true;
      setLearningSummaryHydrated(true);
      if(!d||typeof d!=='object')return;
      // This is a display cache, never an independent decision authority.
      // The local canonical ledger effect below recomputes the actual summary.
      setTaraLearnings(prev=>{
        if(prev?.learningProtocol===LEARNING_PROTOCOL||d.learningProtocol!==LEARNING_PROTOCOL)return prev;
        return d;
      });
    });
    return unsub;
  },[]);
  // V5.6.8 LEARNING REDESIGN: Default learning does NOT make Tara more selective.
  //   Floors stay fixed (Q≥30, conv≥10) except in extreme cases (<25% win rate over
  //   15+ trades on a regime — a true safety valve, not the default response).
  //   Active learning instead does:
  //     - Per regime+direction combo: adjust SPEED (faster on proven combos, slower on
  //       weak ones) and CONFIDENCE (boost on winning combos, dampen on losing)
  //     - Per tier: adjust SPEED (faster on proven tiers, slightly slower on weak)
  //   The intent: keep calling, just pick direction better and lock smarter.
  const _deriveMultipliers=React.useCallback((stats)=>{
    const out={
      regimeFloorSafety:{},     // tighten ONLY for absolute-bad regimes (<25% over 15+)
      regimeDirSpeedAdj:{},     // {`regime|UP`: -0.20, `regime|DOWN`: +0.20, ...}
      regimeDirConfBoost:{},    // {`regime|UP`: +5, ...}
      tierSamplesAdjust:{},
      // V5.7.8: Confluence-bucket multipliers — three buckets ('super-confluence', 'confluence', 'single-signal').
      //   confluenceConfBoost: {bucket: ±N pts} applied to confidence at lock time.
      //   Lets Tara learn "I'm 90% on confluent calls but 55% on single-signal" and reflect
      //   that learned skew into the confidence number she shows you.
      confluenceConfBoost:{},
    };
    // Safety valve — only kicks in for absolute-bad regimes
    Object.entries(stats.byRegime||{}).forEach(([k,v])=>{
      if(!v||(v.wins+v.losses)<15){out.regimeFloorSafety[k]=0;return;}
      const wr=v.wins/(v.wins+v.losses);
      out.regimeFloorSafety[k]=wr<0.25?5:0;
    });
    // Per regime+direction speed and confidence (the main learning signal)
    Object.entries(stats.byRegimeDir||{}).forEach(([k,v])=>{
      if(!v||(v.wins+v.losses)<5){out.regimeDirSpeedAdj[k]=0;out.regimeDirConfBoost[k]=0;return;}
      const wr=v.wins/(v.wins+v.losses);
      if(wr>=0.75){out.regimeDirSpeedAdj[k]=-0.20;out.regimeDirConfBoost[k]=5;}
      else if(wr<0.40){out.regimeDirSpeedAdj[k]=0.20;out.regimeDirConfBoost[k]=-5;}
      else if(wr<0.55){out.regimeDirSpeedAdj[k]=0.05;out.regimeDirConfBoost[k]=-2;}
      else{out.regimeDirSpeedAdj[k]=0;out.regimeDirConfBoost[k]=0;}
    });
    // Per tier speed (proven tiers lock faster)
    Object.entries(stats.byTier||{}).forEach(([k,v])=>{
      if(!v||(v.wins+v.losses)<5){out.tierSamplesAdjust[k]=0;return;}
      const wr=v.wins/(v.wins+v.losses);
      if(wr>=0.75)out.tierSamplesAdjust[k]=-0.15;
      else if(wr<0.50)out.tierSamplesAdjust[k]=0.15;
      else out.tierSamplesAdjust[k]=0;
    });
    // V5.7.8: Per-confluence-bucket confidence adjustment. n>=5 needed before any adjust.
    Object.entries(stats.byConfluence||{}).forEach(([k,v])=>{
      if(!v||(v.wins+v.losses)<5){out.confluenceConfBoost[k]=0;return;}
      const wr=v.wins/(v.wins+v.losses);
      if(wr>=0.80)out.confluenceConfBoost[k]=+8;
      else if(wr>=0.65)out.confluenceConfBoost[k]=+4;
      else if(wr<0.45)out.confluenceConfBoost[k]=-6;
      else if(wr<0.55)out.confluenceConfBoost[k]=-3;
      else out.confluenceConfBoost[k]=0;
    });
    return out;
  },[]);
  // Recompute learnings from a full call log. Called after each resolution.
  const _recomputeLearningsFromLog=React.useCallback((log)=>{
    const stats={byRegime:{},byDirection:{},byTier:{},bySession:{},byRegimeDir:{},byConfluence:{},byEdge:{},byLossPattern:{}};
    let total=0;
    (log||[]).forEach(e=>{
      if(!verifiedSummaryCall(e)||(e.asset||'BTC')!=='BTC'||(e.windowType||'15m')!=='15m')return;
      if(e.dir!=='UP'&&e.dir!=='DOWN')return;
      const won=e.result==='WIN';
      total++;
      const _bump=(bucket,key)=>{
        if(!key)return;
        bucket[key]=bucket[key]||{wins:0,losses:0};
        bucket[key][won?'wins':'losses']++;
      };
      _bump(stats.byRegime,e.regime||'UNKNOWN');
      _bump(stats.byDirection,e.dir);
      _bump(stats.byTier,e.tier||'default');
      _bump(stats.bySession,e.session||'UNKNOWN');
      // V5.6.8: regime+direction combo — the most actionable learning bucket
      _bump(stats.byRegimeDir,(e.regime||'UNKNOWN')+'|'+e.dir);
      // V5.7.8: confluence bucket — three states. Older entries pre-V5.7.7 lack the flags
      //   so they bucket as 'single-signal' (most conservative assumption).
      // V6.0.1: rising-confluence is a fourth bucket — early-entry tier with lower-bar setup.
      // V6.0.7: tape-led is a fifth bucket — fast-lock on overwhelming tape, separate WR.
      const _conflBucket=e.isStructuralLed?'structural-led':e.isSuperConfluent?'super-confluence':e.isConfluent?'confluence':e.isTapeLed?'tape-led':e.isRisingConfluence?'rising-confluence':'single-signal';
      _bump(stats.byConfluence,_conflBucket);
      // V6.0.8: edge bucket — tracks WR by entry profitability. The metric that matters
      //   most for Kalshi: same WR at +15pt edge ≫ same WR at -10pt edge.
      if(e.kalshiAtLock!=null&&e.posterior!=null){
        const _taraDirConf=e.dir==='UP'?e.posterior:(100-e.posterior);
        const _kalshiDirConf=e.dir==='UP'?e.kalshiAtLock:(100-e.kalshiAtLock);
        const _edge=_taraDirConf-_kalshiDirConf;
        const _edgeBucket=_edge>=15?'big-edge':_edge>=5?'good-edge':_edge>=-5?'tight':'late';
        _bump(stats.byEdge,_edgeBucket);
      }
      // V8.5: loss-pattern bucket (only LOSSes have a pattern, but bucket all results
      //   for pattern-WR computation: WIN trades implicitly have pattern=null → not counted).
      if(e.result==='LOSS'&&e.lossPattern){
        _bump(stats.byLossPattern,e.lossPattern);
      }
    });
    const multipliers=_deriveMultipliers(stats);
    const source=learningSummarySource(log);
    setTaraLearnings(prev=>prev.learningProtocol===LEARNING_PROTOCOL&&prev.source?.signature===source.signature?prev:
      {...stats,multipliers,lastUpdated:Date.now(),totalResolved:total,learningProtocol:LEARNING_PROTOCOL,source,scope:'BTC_15m'});
  },[_deriveMultipliers]);
  useEffect(()=>{_recomputeLearningsFromLog(taraCallLog);},[taraCallLog,_recomputeLearningsFromLog]);
  // V5.6: Save/clear the active window's lock state to Firestore. Refresh restores from this.
  //   _persistLock writes whatever the refs currently hold (engine lock + Tara snapshot +
  //   sample formation progress). _clearLock retires local refs only; the shared
  //   document is replaced conditionally when a new current window publishes.
  const _hasRestoredLockRef=useRef(false);
  // V7.10.3: separate ref that flips true ONLY after cloudRead('state/currentLock')
  //   resolves. Lifecycle commit paths (V7.8 Path A/B, V7.9 NO_TRADE) gate on this so
  //   a tab opened mid-window doesn't fire its own commit before cloud data arrives —
  //   which would create a different snapshot per tab for the same window.
  const _cloudRestoreCompletedRef=useRef(false);
  const _cloudRestoreErrorRef=useRef(null);
  const _firstSharedRestoreCompletedRef=useRef(false);
  const _sharedLedgerLookupRef=useRef(null);
  const _sharedRestoreGenerationRef=useRef(0);
  // V7.10.4: lock path now includes asset + windowType so BTC-15m and ETH-5m don't share
  //   the same Firestore doc. Without this, switching assets between browsers means
  //   Browser A's BTC lock would be clobbered by Browser B's ETH state.
  const _lockCloudPath=()=>`state/currentLock_${currentAssetRef.current||currentAsset||'BTC'}_${windowType||'15m'}`;
  // V7.10.5: mirror of cloud's lock doc — updated by cloudWatch every time onSnapshot fires.
  //   _persistLock reads this to decide whether to write back. Convergence rule: whoever
  //   committed FIRST (earliest _committedAt on the snapshot) wins. Other browsers defer.
  const _cloudLockMirrorRef=useRef(null);
  const _lastSharedProgressPersistRef=useRef({at:0,windowId:null,engineAt:null});
  const callCommitHoldRef=useRef(null);
  const callGateHoldRef=useRef(null); // diagnostic only; never a lock/persistence condition
  const callDecisionTraceRef=useRef(null);
  const currentCallBlockerRef=useRef(null);
  const _completeSharedRestore=()=>{
    _cloudRestoreErrorRef.current=null;_cloudRestoreCompletedRef.current=true;
    _hasRestoredLockRef.current=true;_firstSharedRestoreCompletedRef.current=true;
    if(callGateHoldRef.current?.code?.startsWith('shared-lock-restore')){
      callGateHoldRef.current=null;
      if(currentCallBlockerRef.current?.code?.startsWith('shared-lock-restore'))currentCallBlockerRef.current=null;
    }
  };
  const _prepareCallCommit=(snapshot)=>{
    const windowId=computeWindowId(windowType);
    const decision=assessCallCommit(snapshot,{windowId,windowType,asset:currentAssetRef.current||'BTC',quote:_kalshiQuote,readiness:startupReadiness,now:Date.now()});
    if(decision.action==='wait'){
      callCommitHoldRef.current={windowId,asset:currentAssetRef.current||'BTC',
        code:'commit-check',direction:isDirection(snapshot?.call)?snapshot.call:null,
        reason:decision.reason,at:Date.now(),details:{action:decision.action,
          secondsLeft:decision.secondsLeft,cutoff:decision.cutoff,confidence:snapshot?.confidence??null}};
      currentCallBlockerRef.current={...(currentCallBlockerRef.current||{}),
        code:'commit-check',reason:decision.reason,windowId,asset:currentAssetRef.current||'BTC',
        dataReady:!!startupReadiness.ready,observedAt:callCommitHoldRef.current.at,
        proposedDirection:callCommitHoldRef.current.direction,
        lastGate:null,
        gate:{...callCommitHoldRef.current.details,code:'commit-check',reason:decision.reason,
          at:callCommitHoldRef.current.at,direction:callCommitHoldRef.current.direction}};
      callDecisionTraceRef.current=appendDecisionTrace(callDecisionTraceRef.current,{windowId,at:Date.now(),
        code:'commit-check',reason:decision.reason,direction:callCommitHoldRef.current.direction,
        intendedDirection:snapshot?.wouldHaveBeen||snapshot?.direction||null,
        rejectedTier:snapshot?.tier||null,confidence:snapshot?.confidence??null,
        secondsLeft:decision.secondsLeft,dataReady:!!startupReadiness.ready,gate:callCommitHoldRef.current.details});
      if(taraCallSnapshotRef.current===snapshot)taraCallSnapshotRef.current=null;
      lockedCallRef.current=null;
      return false;
    }
    if(decision.action==='sitout'){
      snapshot.blockedDecision ||= currentCallBlockerRef.current;
      Object.assign(snapshot,deadlineSitout(snapshot,decision));
      lockedCallRef.current=null;
    }
    if(decision.action==='allow'||decision.action==='manual'){
      snapshot.callPolicy={version:CALL_POLICY_VERSION,action:decision.action,cutoff:decision.cutoff??null};
      callCommitHoldRef.current=null;
    }
    return true;
  };
  const _sealCallSnapshot=(snapshot)=>{
    if(!snapshot)return;
    if(!_prepareCallCommit(snapshot))return false;
    const _wid=computeWindowId(windowType);
    Object.assign(snapshot,hydrateDecisionQuote(snapshot,{quote:_kalshiQuote,windowId:_wid,asset:currentAssetRef.current||'BTC',now:Date.now()}));
    if(!snapshot.locked||!['UP','DOWN','SIT_OUT'].includes(snapshot.call))return;
    snapshot._committedAt ||= Date.now();
    snapshot.windowId ||= _wid;
    snapshot.windowType ||= windowType;
    snapshot.asset ||= currentAssetRef.current||'BTC';
    snapshot.taraVersion ||= TARA_BUILD_VERSION;
    snapshot.rawPosteriorAtLock ??= analysis?.rawPosteriorUncalibrated ?? null;
    snapshot.calibratedPosteriorAtLock ??= analysis?.rawProbAbove ?? null;
    snapshot.strikeAtLock ??= (Number(kalshiStrike)||Number(targetMarginRef.current)||null);
    if(snapshot.call==='SIT_OUT'){
      snapshot._intendedDir ||= (snapshot.direction==='UP'||snapshot.direction==='DOWN')?snapshot.direction:null;
    }
    snapshot.dir=snapshot.call;
    snapshot.direction=snapshot.call;
    if(!snapshot.originalDecision){
      callDecisionTraceRef.current=appendDecisionTrace(callDecisionTraceRef.current,{windowId:_wid,at:Date.now(),
        code:snapshot.call==='SIT_OUT'?'sitout-committed':'call-committed',direction:snapshot.call,
        confidence:snapshot.confidence??null,reason:snapshot.reason||'',gate:snapshot.blockedDecision?.gate||null});
    }
    snapshot.decisionTrace ||= captureDecisionTrace(callDecisionTraceRef.current,_wid);
    snapshot.forecastAvailability ||= forecastAvailability(analysis?.fgtResults);
    if(!snapshot.originalDecision){
      snapshot.decisionEvidence=callDecisionEvidence(snapshot,analysis,tapeWindows);
      snapshot.formationAtLock=JSON.parse(JSON.stringify(taraCallSampleRef.current));
    }
    if(!snapshot.originalDecision&&analysis?.learningFeatures){
      snapshot.learningFeatures=JSON.parse(JSON.stringify(analysis.learningFeatures));
      snapshot.learningDataReady=startupReadiness.ready===true;
      snapshot.learningPredictions=learningPredictions(snapshot.learningFeatures,{profile:`${snapshot.asset}_${snapshot.windowType}`,key:callKey(snapshot),at:snapshot._committedAt});
    }
    snapshot.originalDecision ||= captureOriginalDecision(snapshot);
    try{_lifecycleRecordSnapshot(snapshot.windowId,snapshot.windowType,snapshot.asset,snapshot,'canonical-seal');}catch(_){}
    if(!journalCapturedCall(localStorage,snapshot)){
      try{console.warn('[Call journal] Capture is not durable locally; archive/cloud persistence will retry.');}catch(_){}
    }
  };
  const _persistLock=()=>{
    if(!_cloudRestoreCompletedRef.current)return; // never publish before a successful shared read
    if(!taraCallSnapshotRef.current&&callCommitHoldRef.current?.windowId===computeWindowId(windowType))return;
    if(_sealCallSnapshot(taraCallSnapshotRef.current)===false)return;
    if(!_sbClient)return; // V10.2.0: now gates on Supabase, not Firestore
    // Stamp _committedAt on the snapshot the FIRST time we persist with non-null data.
    //   This is the canonical "when did this browser commit" timestamp, used for first-
    //   write-wins comparison across devices. Set once, never updated.
    const _snap=taraCallSnapshotRef.current;
    if(_snap&&!_snap._committedAt){
      _snap._committedAt=Date.now();
    }
    // V7.10.5 first-write-wins gate: if cloud already has a snapshot for THIS exact window
    //   that was committed earlier than ours (>1s tolerance for clock skew), don't override
    //   it. Cloud's earlier snapshot is the canonical truth — we'll converge to it via
    //   cloudWatch's adoption logic instead.
    const _cloud=_cloudLockMirrorRef.current;
    const _expectedWid=computeWindowId(windowType);
    if(_snap&&_cloud?.taraSnapshot&&_cloud.windowId===_expectedWid){
      const _cloudT=Number(_cloud.taraSnapshot._committedAt)||0;
      const _localT=Number(_snap._committedAt)||0;
      if(_cloudT>0&&_localT>0&&_cloudT+1000<_localT){
        // Cloud is meaningfully earlier — don't override
        return;
      }
    }
    const _doc={
      windowId:_expectedWid,
      windowType,
      asset:currentAssetRef.current||currentAsset||'BTC',
      engineLock:lockedCallRef.current?{...lockedCallRef.current}:null,
      taraSnapshot:_snap?{..._snap}:null,
      taraSamples:taraCallSampleRef.current?{...taraCallSampleRef.current}:null,
      savedAt:Date.now(),
    };
    cacheSharedDecision(localStorage,_doc,{windowId:_expectedWid,windowType,asset:_doc.asset,now:Date.now()});
    // Backup cadence is not decision cadence: avoid a conditional server read
    // for every forming tick. A newly committed Call/engine lock is immediate.
    const engineAt=Number(_doc.engineLock?._committedAt)||Number(_doc.engineLock?.lockedAt)||null;
    const previous=_lastSharedProgressPersistRef.current;
    if(!_snap&&previous.windowId===_expectedWid&&previous.engineAt===engineAt&&Date.now()-previous.at<10000)return;
    _lastSharedProgressPersistRef.current={at:Date.now(),windowId:_expectedWid,engineAt};
    cloudWriteDebounced(_lockCloudPath(),_doc,300);
  };
  // Clear this tab's stale mirror only. Deleting a shared mutable path at mount
  // or rollover could erase another device's valid decision for the new window.
  const _clearLock=()=>{_cloudLockMirrorRef.current=null;callCommitHoldRef.current=null;
    callGateHoldRef.current=null;currentCallBlockerRef.current=null;callDecisionTraceRef.current=null;};
  const _applySharedDecision=(data,source='shared')=>{
    if(source==='manual')_sharedRestoreGenerationRef.current++;
    const context={windowId:computeWindowId(windowType),windowType,asset:currentAssetRef.current||'BTC',now:Date.now()};
    if(source!=='local')_cloudLockMirrorRef.current=matchesSharedWindow(data,context)?data:null;
    if(data&&!matchesSharedWindow(data,context))return false;
    const cached=readDecisionCache(localStorage,context);
    const recorded=ledgerDecision(taraCallLogRef.current,context);
    const local=chooseSharedSnapshot(taraCallSnapshotRef.current,chooseSharedSnapshot(cached?.taraSnapshot,recorded,context),context);
    const winner=chooseSharedSnapshot(data?.taraSnapshot,local,context);
    let changed=false;
    if(!winner&&taraCallSnapshotRef.current&&!activeCallSnapshot(taraCallSnapshotRef.current,context)){
      taraCallSnapshotRef.current=null;changed=true;
    }
    if(winner&&sharedSnapshotIdentity(winner)!==sharedSnapshotIdentity(taraCallSnapshotRef.current)){
      const previous=taraCallSnapshotRef.current;
      taraCallSnapshotRef.current=winner;changed=true;
      if(previous?.locked&&isDirection(previous.call)&&isDirection(winner.call)&&previous.call!==winner.call){
        try{pushToast('lock-restored-'+context.windowId,`Shared Call restored: ${winner.call}`,
          `An earlier captured ${winner.call} decision is authoritative. The prior ${previous.call} alert is not re-sent.`,
          {color:'#23B981',durationMs:12000,cooldown:0});}catch(_){}
      }
    }
    let saved=null;try{saved=JSON.parse(localStorage.getItem('taraLockedTimeSeries_v1')||'null');}catch(_){}
    const engine=restoredEngineLock(data?.engineLock,context,lockedCallRef.current,saved);
    const localEngine=restoredEngineLock(lockedCallRef.current,context);
    if(!engine&&!localEngine&&lockedCallRef.current){
      lockedCallRef.current=null;engineLockedDirRef.current=null;taraAdviceRef.current='SEARCHING...';changed=true;
    }
    if(engine&&(!localEngine||(Number(engine._committedAt)||Number(engine.lockedAt)||0)+1000<
      (Number(localEngine._committedAt)||Number(localEngine.lockedAt)||0))){
      lockedCallRef.current=engine;engineLockedDirRef.current=engine.dir;
      taraAdviceRef.current=engine.dir==='UP'?'UP - CONFIRMED':'DOWN - CONFIRMED';changed=true;
    }
    // Engine-only locks and forming samples never become a committed Tara Call.
    if(data?.taraSamples&&!winner&&!taraCallSampleRef.current?.dir&&isDirection(data.taraSamples.dir)){
      const count=Number(data.taraSamples.count);
      if(Number.isFinite(count)&&count>=0){taraCallSampleRef.current={dir:data.taraSamples.dir,count};changed=true;}
    }
    const canonical={...(data||cached||{}),...context,taraSnapshot:winner,
      engineLock:lockedCallRef.current, taraSamples:taraCallSampleRef.current};
    if(winner)cacheSharedDecision(localStorage,canonical,context);
    if(changed)setForceRender(p=>p+1);
    return changed;
  };
  // V7.10.5: regimeMemory now persists + syncs. Was in-memory only with hardcoded V144
  //   seeds — never saved on update, never synced. Now: localStorage cached, cloud-synced
  //   via learnings/regimeMemory, max-per-cell merge for cross-device convergence.
  const[regimeMemory,setRegimeMemory]=useState(()=>{
    // V10.2.1: seeds refreshed from 755-trade audit. Old seeds were V144-era and badly
    //   overfit (TRENDING DOWN seeded 87.5% from n=16; reality at n=352 is 65.3%).
    //   RANGE-CHOP seeded as the WEAKEST regime; reality is the STRONGEST at 78.2%.
    //   Note: stored localStorage values OVERWRITE these seeds on hydrate, so this
    //   only affects fresh installs / unknown regimes — but those biases were leaking
    //   into cold-start calibration and ML weights.
    //   HIGH VOL CHOP kept at original (n=12 audit sample too small to trust).
    //   LONG SQUEEZE still 0/0 — never observed in 755 trades.
    // V10.7.86b: REGIME MEMORY IS NOW PER-ASSET.
    //   Bug: single shared key caused ETH losses to tighten BTC thresholds and vice versa.
    //   Fix: asset-specific key. BTC migrates old shared key on first load.
    // V10.7.86c: seeds updated from 1,678 BTC trades
    const _seeds={
      'TRENDING UP':   {wins:58,losses:37},   // 61% WR (n=95)
      'TRENDING DOWN': {wins:230,losses:120},  // 66% WR (n=350)
      'HIGH VOL CHOP': {wins:38,losses:7},     // 84% WR (n=45)
      'SHORT SQUEEZE': {wins:156,losses:84},   // 65% WR (n=240)
      'LONG SQUEEZE':  {wins:0,losses:0},      // never observed
      'RANGE-CHOP':    {wins:562,losses:385},  // 59% WR (n=947)
      'COMPRESSING':   {wins:0,losses:0},      // rare
    };
    const _assetKey='taraRegimeMemory_BTC_v1';
    try{
      const stored=localStorage.getItem(_assetKey);
      if(stored){
        const p=JSON.parse(stored);
        if(p&&typeof p==='object'){
          const out={..._seeds};
          Object.keys(p).forEach(k=>{if(p[k]&&typeof p[k]==='object')out[k]={wins:Number(p[k].wins)||0,losses:Number(p[k].losses)||0};});
          return out;
        }
      }
      if(currentAsset==='BTC'){
        const legacy=localStorage.getItem('taraRegimeMemory_v1');
        if(legacy){
          const p=JSON.parse(legacy);
          if(p&&typeof p==='object'){
            const out={..._seeds};
            Object.keys(p).forEach(k=>{if(p[k]&&typeof p[k]==='object')out[k]={wins:Number(p[k].wins)||0,losses:Number(p[k].losses)||0};});
            try{localStorage.setItem(_assetKey,JSON.stringify(out));}catch(_){}
            return out;
          }
        }
      }
    }catch(e){}
    return _seeds;
  });
  const _regimeMemoryHydratedRef=useRef(false);
  // V8.6: Ref so RMW write can read freshest local state
  const _regimeMemoryRef=useRef(regimeMemory);
  _regimeMemoryRef.current=regimeMemory;
  useEffect(()=>{
    try{
      // V10.7.86b: save to asset-specific key
      localStorage.setItem(`taraRegimeMemory_${currentAsset}_v1`,JSON.stringify(regimeMemory));
    }catch(e){}
    if(_regimeMemoryHydratedRef.current){
      // V8.6: RMW with max-per-cell merge. Was simple cloudWriteDebounced (last-write-wins)
      //   which caused cross-device increment loss — Device A's regime training would
      //   overwrite Device B's regime training when both updated the same regime keys.
      cloudWriteDebouncedRMW(
        'learnings/regimeMemory_BTC',
        ()=>_regimeMemoryRef.current,
        (cloudData,localData)=>{
          const _allKeys=new Set([...Object.keys(cloudData||{}),...Object.keys(localData||{})]);
          const merged={};
          let changed=false;
          _allKeys.forEach(k=>{
            const lW=Number(localData?.[k]?.wins)||0,lL=Number(localData?.[k]?.losses)||0;
            const cW=Number(cloudData?.[k]?.wins)||0,cL=Number(cloudData?.[k]?.losses)||0;
            const mW=Math.max(lW,cW),mL=Math.max(lL,cL);
            merged[k]={wins:mW,losses:mL};
            if(mW!==cW||mL!==cL)changed=true;
          });
          // Skip write if no change vs cloud
          return changed?merged:null;
        },
        // V10.7.85: throttle to 120s
        120000,
      );
    }
  },[regimeMemory]);
  useEffect(()=>{
    const unsub=cloudWatch('learnings/regimeMemory_BTC',(d)=>{
      _regimeMemoryHydratedRef.current=true;
      if(!d||typeof d!=='object')return;
      setRegimeMemory(prev=>{
        const _allKeys=new Set([...Object.keys(prev||{}),...Object.keys(d||{})]);
        const next={};
        let changed=false;
        _allKeys.forEach(k=>{
          const lW=Number(prev?.[k]?.wins)||0,lL=Number(prev?.[k]?.losses)||0;
          const cW=Number(d?.[k]?.wins)||0,cL=Number(d?.[k]?.losses)||0;
          const merged={wins:Math.max(lW,cW),losses:Math.max(lL,cL)};
          if(merged.wins!==lW||merged.losses!==lL)changed=true;
          next[k]=merged;
        });
        return changed?next:prev;
      });
    });
    return unsub;
  },[]);
  const lastRegimeRef=useRef('RANGE-CHOP');
  const windowSignalDirRef=useRef(null); // tracks first FORMING direction this window — lock must match
  // ── TARA SELF-TRAINING STATE ──
  // V7.1: Per-asset weights. Each asset gets its OWN adaptive weights and regime weights
  //   so BTC trades don't pollute ETH calibration and vice versa. Both seed from the
  //   same V144 prior at session start — they diverge as trades resolve in-session.
  //   Backward-compat: `adaptiveWeights` and `regimeWeights` still expose the ACTIVE
  //   asset's slice so all downstream consumers (signal scoring, regime mapping, learning
  //   updates) work without modification. Setters wrap in a {asset → slice} update.
  const[adaptiveWeightsByAsset,setAdaptiveWeightsByAsset]=useState(()=>{
    // V7.8: Hydrate per-asset weights independently. localStorage keys are per-asset
    //   so BTC and ETH can diverge across sessions.
    const _seed=loadWeights();
    const _loadAsset=(key,fallback)=>{
      try{const s=localStorage.getItem(key);if(s){const w=JSON.parse(s);if(w&&typeof w.gap==='number')return w;}}catch(e){}
      return{...fallback};
    };
    return{
      BTC:_loadAsset('taraWeightsByAsset_BTC_v1',_seed),

    };
  });
  const[regimeWeightsByAsset,setRegimeWeightsByAsset]=useState(()=>{
    const _seed=loadRegimeWeights();
    const _loadRegAsset=(key,fallback)=>{
      try{const s=localStorage.getItem(key);if(s){const w=JSON.parse(s);if(w&&typeof w==='object')return w;}}catch(e){}
      return{...fallback};
    };
    return{
      BTC:_loadRegAsset('taraRegimeWeightsByAsset_BTC_v1',_seed),

    };
  });
  // ── V9.11.0 LAYER 3: SESSION-OF-DAY ADAPTIVE WEIGHTS ─────────────────────
  // Shape: {BTC:{ASIA:{...weights}, EU:{...}, US:{...}, OFFHOURS:{...}}, ETH:{...}}
  //   Tracks per-session signal weight preferences. ASIA may favor flow/regime while
  //   NY-AM may favor momentum/gap. Each session weights set is updated independently
  //   in applyTradeLearning based on resolved trades in that session.
  // Hydration: localStorage per-asset. Cloud sync deferred to a future ship (the
  //   existing weight sync infrastructure is opinionated about asset+regime keys; adding
  //   a third dimension cleanly is a separate refactor).
  // Engine consumption: when computing W, we blend session×regime weights when both are
  //   available, fall back to regime, then asset, then DEFAULT_WEIGHTS (see engine).
  const[sessionWeightsByAsset,setSessionWeightsByAsset]=useState(()=>{
    const _seed=loadWeights();
    const _emptySessionMap=()=>({
      ASIA:{..._seed},
      EU:{..._seed},
      US:{..._seed},
      OFFHOURS:{..._seed},
    });
    const _loadSessAsset=(key)=>{
      try{
        const s=localStorage.getItem(key);
        if(s){
          const w=JSON.parse(s);
          if(w&&typeof w==='object'&&w.ASIA&&w.EU&&w.US&&w.OFFHOURS)return w;
        }
      }catch(e){}
      return _emptySessionMap();
    };
    return{
      BTC:_loadSessAsset('taraSessionWeightsByAsset_BTC_v1'),

    };
  });
  useEffect(()=>{
    try{
      localStorage.setItem('taraSessionWeightsByAsset_BTC_v1',JSON.stringify(sessionWeightsByAsset.BTC||{}));

    }catch(e){}
  },[sessionWeightsByAsset]);
  const sessionWeightsByAssetRef=useRef(sessionWeightsByAsset);
  useEffect(()=>{sessionWeightsByAssetRef.current=sessionWeightsByAsset;},[sessionWeightsByAsset]);
  // V7.8: Persist per-asset weights to localStorage on every change. Cloud sync added
  //   below via cloudWriteDebounced. Hydrate from cloud once on mount via cloudWatch.
  useEffect(()=>{
    try{
      localStorage.setItem('taraWeightsByAsset_BTC_v1',JSON.stringify(adaptiveWeightsByAsset.BTC||{}));

    }catch(e){}
  },[adaptiveWeightsByAsset]);
  useEffect(()=>{
    try{
      localStorage.setItem('taraRegimeWeightsByAsset_BTC_v1',JSON.stringify(regimeWeightsByAsset.BTC||{}));

    }catch(e){}
  },[regimeWeightsByAsset]);
  // V7.8: Cloud sync at learnings/taraWeights — separate doc from learnings/tara (which
  //   holds statistical buckets). Debounced 2s. Hydrate-once on mount.
  // V8.6: Switched to RMW with per-asset-slice timestamp merge. Each slice (BTC.adaptive,
  //   ETH.adaptive, BTC.regime, ETH.regime) tracks its own updatedAt. When two devices
  //   update different assets simultaneously, both slices preserve. Eliminates the
  //   cross-device clobbering bug where Browser A's BTC training would erase Browser B's
  //   ETH training (and vice versa).
  const _weightsCloudHydratedRef=useRef(false);
  const _weightsByAssetUpdatedAtRef=useRef({BTC:{adaptive:0,regime:0}});
  // Bump local timestamps whenever weights change (we set the timestamp before write fires,
  //   so the RMW merge sees a fresh local timestamp). updatedAt is set per-asset based on
  //   which asset was active at the time of the change.
  // No automatic weight republishing: hydration is not a training event.
  useEffect(()=>{
    const unsub=cloudWatch('learnings/taraWeights',(d)=>{
      if(!d||typeof d!=='object')return;
      if(typeof d.adaptive?.BTC?.gap!=='number'&&!d.regime?.BTC)return;
      // Load the existing saved baseline once. Subsequent old-client gradient
      // writes cannot silently alter this release's live scoring weights.
      if(_weightsCloudHydratedRef.current)return;
      _weightsCloudHydratedRef.current=true;
      // V8.6: Merge cloud → local based on per-slice timestamps. Cloud slice adopted only
      //   if its updatedAt is newer than local's (or local hasn't been touched yet).
      const cBTCt=d.updatedAtBTC?.adaptive||0;
      const cBTCrt=d.updatedAtBTC?.regime||0;
      if(d.adaptive&&typeof d.adaptive==='object'){
        setAdaptiveWeightsByAsset(prev=>{
          const _next={...prev};
          if(d.adaptive.BTC&&typeof d.adaptive.BTC.gap==='number'){
            const lT=_weightsByAssetUpdatedAtRef.current.BTC.adaptive||0;
            if(cBTCt>=lT){_next.BTC=d.adaptive.BTC;_weightsByAssetUpdatedAtRef.current.BTC.adaptive=cBTCt;}
          }
          if(false){ // ETH removed

          }
          return _next;
        });
      }
      if(d.regime&&typeof d.regime==='object'){
        setRegimeWeightsByAsset(prev=>{
          const _next={...prev};
          if(d.regime.BTC){
            const lT=_weightsByAssetUpdatedAtRef.current.BTC.regime||0;
            if(cBTCrt>=lT){_next.BTC=d.regime.BTC;_weightsByAssetUpdatedAtRef.current.BTC.regime=cBTCrt;}
          }
          if(false){ // ETH removed

          }
          return _next;
        });
      }
    });
    return unsub;
  },[]);
  const adaptiveWeights=adaptiveWeightsByAsset[currentAsset]||adaptiveWeightsByAsset.BTC;
  const regimeWeights=regimeWeightsByAsset[currentAsset]||regimeWeightsByAsset.BTC;
  // V9.11.0: extract session-weight slice for the current asset. The engine receives
  //   the full session map for THIS asset (e.g. {ASIA:{...}, EU:{...}, ...}) and picks
  //   the right session-specific weight inside its weight-blending logic. Cheaper than
  //   pre-computing in the component since the session is determined at engine-eval time.
  const sessionWeights=sessionWeightsByAsset[currentAsset]||sessionWeightsByAsset.BTC;
  // V7.1: setAdaptiveWeights/setRegimeWeights default to the active asset's slice.
  //   Optionally accept an explicit asset string ('BTC' or 'ETH') so that learning
  //   updates from a resolved trade always target THAT trade's asset, even if the
  //   user switched assets between lock and resolution.
  const setAdaptiveWeights=(updaterOrValue,explicitAsset)=>{
    setAdaptiveWeightsByAsset(prev=>{
      const _asset='BTC';
      const _curSlice=prev[_asset]||prev.BTC;
      const _newSlice=typeof updaterOrValue==='function'?updaterOrValue(_curSlice):updaterOrValue;
      return{...prev,[_asset]:_newSlice};
    });
  };
  const setRegimeWeights=(updaterOrValue,explicitAsset)=>{
    setRegimeWeightsByAsset(prev=>{
      const _asset='BTC';
      const _curSlice=prev[_asset]||prev.BTC;
      const _newSlice=typeof updaterOrValue==='function'?updaterOrValue(_curSlice):updaterOrValue;
      return{...prev,[_asset]:_newSlice};
    });
  };
  const adaptiveWeightsByAssetRef=useRef(adaptiveWeightsByAsset);
  adaptiveWeightsByAssetRef.current=adaptiveWeightsByAsset;
  const regimeWeightsByAssetRef=useRef(regimeWeightsByAsset);
  regimeWeightsByAssetRef.current=regimeWeightsByAsset;
  // V7.1: Centralized learning helper. Use the TRADE's asset (not the active one)
  //   to update both adaptive and regime weights. Filters newLog to same-asset trades
  //   for updateWeights' rolling-window calculations.
  const applyTradeLearning=()=>{
    // Live event-by-event gradients stay frozen at their saved values. The
    // scheduled trainer derives shadow revisions once per verified window;
    // replay/refresh/manual trade outcomes cannot mutate live weights.
    return;
  };
  const[tradeLog,setTradeLog]=useState(()=>loadTradeLog());

  // V9.1.3: STATS RECONCILIATION — validate taraCallLog integrity on first hydration.
  //   Tara's scorecard (taraScorecards) is already useMemo-derived from taraCallLog so
  //   it can't drift mathematically — it's always whatever the log shows. The risk is
  //   the LOG itself being inconsistent: duplicates, malformed entries, cross-asset
  //   bleed, etc. This effect dedups on (asset, windowId, windowType) keeping the
  //   FIRST entry (preserving V8.8.6 first-write-wins semantics) and surfaces a
  //   diagnostic if anything was cleaned up.
  const _logValidatedRef=useRef(false);
  useEffect(()=>{
    if(_logValidatedRef.current)return;
    if(!Array.isArray(taraCallLog)||taraCallLog.length===0)return;
    _logValidatedRef.current=true;
    // Dedup: keep first occurrence by (asset, windowId, windowType)
    const _seen=new Set();
    const _deduped=[];
    let _dups=0,_invalid=0;
    for(const e of taraCallLog){
      if(!e||typeof e!=='object'){_invalid++;continue;}
      if(!e.windowId||!e.windowType){_invalid++;_deduped.push(e);continue;} // keep but flag
      const _asset=e.asset||'BTC';
      const _key=`${_asset}|${e.windowId}|${e.windowType}`;
      if(_seen.has(_key)){_dups++;continue;}
      _seen.add(_key);
      _deduped.push(e);
    }
    if(_dups>0||_invalid>0){
      console.info('[V9.1.3 log validation] cleaned taraCallLog',
        {before:taraCallLog.length,after:_deduped.length,duplicatesRemoved:_dups,invalidEntries:_invalid});
      if(_dups>0){
        // Only update state if we actually removed dups — invalid-but-kept entries don't count
        setTaraCallLog(_deduped);
      }
    }
  },[taraCallLog]); // eslint-disable-line react-hooks/exhaustive-deps
  // Streak analysis — V7.1: per-asset. ETH and BTC have separate streak tracking.
  const streakData=useMemo(()=>{
    const _src=(tradeLog||[]).filter(t=>t&&(t.asset||'BTC')===currentAsset);
    const recent=_src.filter(t=>t.result).slice(-10);
    if(recent.length<3)return{streak:0,type:'neutral',last5WR:null,warning:false,strongWarn:false,upBias:0,dnBias:0};
    let streak=0;
    const lastResult=recent[recent.length-1].result;
    for(let i=recent.length-1;i>=0;i--){
      if(recent[i].result===lastResult)streak++;
      else break;
    }
    const last5=recent.slice(-5);
    const last5W=last5.filter(t=>t.result==='WIN').length;
    const last5WR=last5.length>=3?Math.round((last5W/last5.length)*100):null;
    const warning=lastResult==='LOSS'&&streak>=3;
    const strongWarn=lastResult==='LOSS'&&streak>=5;
    // V134: Per-direction bias correction
    // If recent UP calls have low WR and DOWN have high WR, tilt against UP
    const recent20=_src.filter(t=>t.result).slice(-20);
    const recentUps=recent20.filter(t=>t.dir==='UP');
    const recentDns=recent20.filter(t=>t.dir==='DOWN');
    let upBias=0,dnBias=0;
    if(recentUps.length>=4){
      const upWR=recentUps.filter(t=>t.result==='WIN').length/recentUps.length;
      // upWR < 0.45 → -10 quality penalty for UP locks (recent UP cold)
      // upWR > 0.75 → +5 boost for UP (recent UP hot)
      if(upWR<0.40)upBias=-12;
      else if(upWR<0.50)upBias=-6;
      else if(upWR>0.75)upBias=+5;
    }
    if(recentDns.length>=4){
      const dnWR=recentDns.filter(t=>t.result==='WIN').length/recentDns.length;
      if(dnWR<0.40)dnBias=-12;
      else if(dnWR<0.50)dnBias=-6;
      else if(dnWR>0.75)dnBias=+5;
    }
    return{streak,type:lastResult==='WIN'?'hot':'cold',last5WR,warning,strongWarn,upBias,dnBias};
  },[tradeLog,currentAsset]);
  const tradeLogRef=useRef([]);
  tradeLogRef.current=tradeLog;
  // ── V8.0: TODAY DATA + STREAK FROM CLOUD-PERSISTED LOG ─────────────────────
  // Everything below derives from taraCallLog (Firestore-synced, survives reloads).
  //   tradeLog above is in-memory only post-V5.5b → unreliable for cross-session views.
  // todayData: per-asset slice of today's calls + W/L/P&L/streak/heatmap/hourly.
  // V8.2: extended with dollar P&L, walk-forward WR (7d/30d/lifetime), edge bucket WR,
  //   asset-rotation divergence, Kelly fraction.
  const todayData=useMemo(()=>{
    const _now=new Date();
    const _todayKey=_fmtDateTz(_now,timeFormat,{year:'numeric',month:'2-digit',day:'2-digit'});
    const _src=(taraCallLog||[]).filter(e=>e&&(e.time||e.id)&&(e.asset||'BTC')===currentAsset); // V10.9.28: id fallback
    // Today only — same calendar day in viewer's selected timezone (V9.7.3)
    const todayCalls=_src.filter(e=>{
      try{return _fmtDateTz(new Date(e.time||e.id),timeFormat,{year:'numeric',month:'2-digit',day:'2-digit'})===_todayKey;}
      catch(_){return false;}
    });
    const todayScored=_canonicalTrades(todayCalls);
    const wins=todayScored.filter(e=>e.result==='WIN').length;
    const losses=todayScored.filter(e=>e.result==='LOSS').length;
    const sitouts=todayCalls.filter(e=>e.result==='SITOUT').length;
    const pending=todayCalls.filter(e=>!e.result&&e.dir!=='NO_TRADE').length;
    const resolved=wins+losses;
    const wr=resolved>0?Math.round((wins/resolved)*100):null;
    // V8.2: Dollar P&L. Net per win = winPayout. Net per loss = -betSize. Sitouts/no-trades = 0.
    const _bet=Number(tradingSettings?.betSize)||10;
    const _payout=Number(tradingSettings?.winPayout)||8.5;
    const dollarPnL=Math.round(((wins*_payout)-(losses*_bet))*100)/100;
    // Streak from RESOLVED calls only — newest first, walk back
    const _resolvedAll=_canonicalTrades(_src).slice(-30);
    let streak=0,streakType='neutral',lastResult=null;
    if(_resolvedAll.length>0){
      lastResult=_resolvedAll[_resolvedAll.length-1].result;
      for(let i=_resolvedAll.length-1;i>=0;i--){
        if(_resolvedAll[i].result===lastResult)streak++;
        else break;
      }
      streakType=lastResult==='WIN'?'hot':'cold';
    }
    const tilt=lastResult==='LOSS'&&streak>=3;
    const strongTilt=lastResult==='LOSS'&&streak>=5;
    const heater=lastResult==='WIN'&&streak>=4;
    // Cooldown — minutes since last LOSS. Used to suggest "wait a bit" after a fresh loss.
    let cooldownMinSinceLoss=null;
    for(let i=_resolvedAll.length-1;i>=0;i--){
      if(_resolvedAll[i].result==='LOSS'){
        cooldownMinSinceLoss=Math.round((_now.getTime()-_resolvedAll[i].time)/60000);
        break;
      }
    }
    const inCooldown=cooldownMinSinceLoss!=null&&cooldownMinSinceLoss<3;
    // Recent calls heatmap — last 20 resolved
    const recentForHeatmap=_resolvedAll.slice(-20);
    // Hourly bucket for today (UTC) — for daily P&L curve
    const hourlyBuckets={};
    todayScored.forEach(e=>{
      try{
        const h=new Date(e.time||e.id).getUTCHours(); // V10.9.28
        if(!hourlyBuckets[h])hourlyBuckets[h]={wins:0,losses:0};
        hourlyBuckets[h][e.result==='WIN'?'wins':'losses']+=1;
      }catch(_){}
    });
    // V10.7.82b: Use canonical filter for all WR computations.
    //   Previously: raw WIN/LOSS filter → included no-go-data (wifi losses) + wasOverriddenNoTrade
    //   Now: _isRealTrade() + deduplicated by windowId = single source of truth
    const _allResolved=_canonicalTrades((taraCallLog||[]).filter(e=>e&&(e.asset||'BTC')===currentAsset));
    const _windowWR=(daysBack)=>{
      const cutoff=_now.getTime()-daysBack*24*60*60*1000;
      const inWindow=_allResolved.filter(e=>e.time>=cutoff);
      const w=inWindow.filter(e=>e.result==='WIN').length;
      const total=inWindow.length;
      return{wr:total>0?Math.round((w/total)*100):null,wins:w,losses:total-w,total};
    };
    const wr7d=_windowWR(7);
    const wr30d=_windowWR(30);
    const wrLifetime={
      wr:_allResolved.length>0?Math.round((_allResolved.filter(e=>e.result==='WIN').length/_allResolved.length)*100):null,
      wins:_allResolved.filter(e=>e.result==='WIN').length,
      losses:_allResolved.filter(e=>e.result==='LOSS').length,
      total:_allResolved.length,
    };
    // ── V8.2: Edge-bucket WR — from per-call edge vs Kalshi at lock time ──
    //   big-edge: +15+ vs Kalshi · good-edge: +5..+15 · tight: -5..+5 · late: < -5
    const edgeBuckets={'big-edge':{wins:0,losses:0},'good-edge':{wins:0,losses:0},'tight':{wins:0,losses:0},'late':{wins:0,losses:0}};
    _allResolved.forEach(e=>{
      if(e.kalshiAtLock==null||e.posterior==null)return;
      const _taraDirConf=e.dir==='UP'?Number(e.posterior):(100-Number(e.posterior));
      const _kalshiDirConf=e.dir==='UP'?Number(e.kalshiAtLock):(100-Number(e.kalshiAtLock));
      const _edge=_taraDirConf-_kalshiDirConf;
      const _bkt=_edge>=15?'big-edge':_edge>=5?'good-edge':_edge>=-5?'tight':'late';
      edgeBuckets[_bkt][e.result==='WIN'?'wins':'losses']+=1;
    });
    // ── V8.2: Per-asset recent-WR for asset-rotation hint ──
    const _otherAsset='BTC';
    const _recent24h=(taraCallLog||[]).filter(e=>{const _t=e&&(e.time||e.id);return _t&&(_now.getTime()-_t)<24*60*60*1000&&(e.result==='WIN'||e.result==='LOSS');}); // V10.9.28: id fallback
    const _curRecent=_recent24h.filter(e=>(e.asset||'BTC')===currentAsset);
    const _otherRecent=_recent24h.filter(e=>(e.asset||'BTC')===_otherAsset);
    let assetRotation=null;
    if(_curRecent.length>=4&&_otherRecent.length>=4){
      const _curWR=_curRecent.filter(e=>e.result==='WIN').length/_curRecent.length;
      const _othWR=_otherRecent.filter(e=>e.result==='WIN').length/_otherRecent.length;
      const _diff=(_othWR-_curWR)*100;
      if(_diff>=15){
        assetRotation={
          suggest:_otherAsset,
          curWR:Math.round(_curWR*100),
          curN:_curRecent.length,
          othWR:Math.round(_othWR*100),
          othN:_otherRecent.length,
          diff:Math.round(_diff),
        };
      }
    }
    // ── V8.2: Kelly criterion (quarter-Kelly for safety) ──
    //   kelly_f = (WR * b - (1-WR)) / b   where b = winPayout / betSize (odds received)
    let kelly=null;
    const _kSrc=wr7d.total>=20?wr7d:wr30d.total>=20?wr30d:wrLifetime;
    if(_kSrc.total>=15&&_kSrc.wr!=null){
      const _p=_kSrc.wr/100;
      const _b=_payout/_bet;
      const _f=(_p*_b-(1-_p))/_b;
      const _qK=_f/4; // quarter-Kelly is industry standard for safety
      const _suggestBet=Math.max(0,_qK*100); // express as % of bankroll
      kelly={
        fullF:Math.round(_f*1000)/10, // %
        quarterF:Math.round(_qK*1000)/10, // %
        suggestPct:Math.round(_suggestBet*10)/10, // % of bankroll
        sourceN:_kSrc.total,
        sourceLabel:_kSrc===wr7d?'last 7d':_kSrc===wr30d?'last 30d':'lifetime',
        edge:_f>0,
      };
    }
    // ── V8.5: Loss-pattern stats ──
    const lossPatternStats={};
    let _totalLosses=0;
    _allResolved.forEach(e=>{
      if(e.result!=='LOSS')return;
      _totalLosses++;
      const p=e.lossPattern||'UNCLASSIFIED';
      lossPatternStats[p]=(lossPatternStats[p]||0)+1;
    });
    let topLossPattern=null;
    if(_totalLosses>=5){
      const _entries=Object.entries(lossPatternStats).filter(([k])=>k!=='UNCLASSIFIED');
      if(_entries.length>0){
        _entries.sort((a,b)=>b[1]-a[1]);
        const[_topKey,_topCount]=_entries[0];
        topLossPattern={
          key:_topKey,
          count:_topCount,
          pct:Math.round((_topCount/_totalLosses)*100),
          totalLosses:_totalLosses,
        };
      }
    }
    // V13.2.3: today's sit-out composition - answers "am I sitting out too much?" inline.
    //   By favored-side cost (kalshiAtLock): >=85 = priced-out (right dir, no payout left),
    //   <=35 = deep-underdog/fade, 35-85 = valid-range coinflip (the only debatable band).
    //   Priced-out + fade are always correct passes; a high coinflip share is the one tell
    //   that the gates may be flinching on tradeable windows.
    let _soPriced=0,_soFade=0,_soFlip=0,_soNoCost=0;
    todayCalls.forEach(e=>{
      if(e.result!=='SITOUT')return;
      const _c=(typeof e.kalshiAtLock==='number')?e.kalshiAtLock:null;
      if(_c==null||_c<=0)_soNoCost++;
      else if(_c>=85)_soPriced++;
      else if(_c<=35)_soFade++;
      else _soFlip++;
    });
    const _soCosted=_soPriced+_soFade+_soFlip;
    let _sitoutMix=null;
    if(sitouts>0){
      const _detail=`${_soPriced} priced-out \u00b7 ${_soFade} fade \u00b7 ${_soFlip} coinflip`+(_soNoCost?` \u00b7 ${_soNoCost} no-price`:'');
      const _flipShare=_soCosted>0?_soFlip/_soCosted:0;
      let _verdict,_col,_bg;
      if(_soCosted<4){_verdict='thin';_col='rgba(237,237,237,0.55)';_bg='rgba(237,237,237,0.06)';}
      else if(_flipShare>0.40){_verdict='check gates';_col='#23B981';_bg='rgba(35,185,129,0.12)';}
      else if(_flipShare<=0.20){_verdict='dead tape';_col='rgba(237,237,237,0.60)';_bg='rgba(237,237,237,0.06)';}
      else{_verdict='selective';_col='rgb(35,185,129)';_bg='rgba(35,185,129,0.10)';}
      _sitoutMix={verdict:_verdict,detail:_detail,color:_col,bg:_bg,priced:_soPriced,fade:_soFade,flip:_soFlip,noCost:_soNoCost,flipShare:_flipShare};
    }
    return{
      todayCalls,wins,losses,sitouts,pending,resolved,wr,
      sitoutMix:_sitoutMix,
      dollarPnL,
      streak,streakType,lastResult,tilt,strongTilt,heater,
      cooldownMinSinceLoss,inCooldown,
      recentForHeatmap,hourlyBuckets,
      // V8.2 additions
      wr7d,wr30d,wrLifetime,
      edgeBuckets,
      assetRotation,
      kelly,
      // V8.5 additions
      lossPatternStats,
      topLossPattern,
    };
  },[taraCallLog,currentAsset,tradingSettings,timeFormat]);
  // Best windows hint — for the day-of-week we're in, find the user's best historical hours
  //   from the personal scorecards (hour×day grid). Helps user time their sessions.
  // ── V8.2: TILT COOLDOWN AUTO-TRIGGER ───────────────────────────────────
  // When streak reaches user-configured threshold (default 4 losses), set tiltLockUntil
  // for the configured duration. Cleared automatically when next WIN arrives.
  useEffect(()=>{
    if(!todayData)return;
    // V10.4.1a: antiTilt merged into autoExec cooldown (single source of truth).
    //   Hard-disabled here to prevent double-cooldown firing. Saved settings
    //   are preserved in localStorage for backward compat but no longer active.
    return;
    // eslint-disable-next-line no-unreachable
    if(!tradingSettings.antiTiltEnabled)return;
    // Trigger on streak reaching threshold
    if(todayData.lastResult==='LOSS'&&todayData.streak>=tradingSettings.antiTiltStreakLen){
      const _now=Date.now();
      const _newUntil=_now+tradingSettings.antiTiltMinutes*60*1000;
      // Only trigger fresh — don't re-trigger if already in cooldown
      if(tiltLockUntil<_now){
        setTiltLockUntil(_newUntil);
        try{console.warn('[V8.2] Tilt cooldown engaged:',todayData.streak,'losses · until',new Date(_newUntil).toLocaleTimeString());}catch(_){}
      }
    }
    // Clear on first win after cooldown started
    if(todayData.lastResult==='WIN'&&tiltLockUntil>0){
      setTiltLockUntil(0);
    }
  },[todayData,tradingSettings.antiTiltEnabled,tradingSettings.antiTiltStreakLen,tradingSettings.antiTiltMinutes]);
  const bestWindowsToday=useMemo(()=>{
    // V13.4.257 FIX: this used to read scorecards[`${dayName}-${h}`], a key shape
    //   the scorecards memo never produces (it only makes '15m' and '5m'). Every
    //   hour hit the !cell continue, so _todayHours stayed empty and this returned
    //   null on every render -- PreWindowPrepCard and DecisionalOverlay have never
    //   shown an hour. Tallied from taraCallLog directly now, which is the source
    //   TradeScheduleStrip already uses successfully.
    const _now=new Date();
    const dayName=['SUN','MON','TUE','WED','THU','FRI','SAT'][_now.getUTCDay()];
    const _dayIdx=_now.getUTCDay();
    const _byHour={};
    (taraCallLog||[]).forEach(e=>{
      if(!e||(e.result!=='WIN'&&e.result!=='LOSS'))return;
      if(e.wasOverriddenNoTrade===true)return;
      if(e.tier==='no-go-data'&&!e.closingPrice)return;
      const _t=Number(e.id);
      if(!Number.isFinite(_t)||_t<=0)return;
      const _d=new Date(_t);
      if(_d.getUTCDay()!==_dayIdx)return;          // this weekday only
      const _h=_d.getUTCHours();
      if(!_byHour[_h])_byHour[_h]={wins:0,losses:0};
      if(e.result==='WIN')_byHour[_h].wins++; else _byHour[_h].losses++;
    });
    const _todayHours=[];
    for(let h=0;h<24;h++){
      const cell=_byHour[h];
      if(!cell)continue;
      const w=cell.wins||0,l=cell.losses||0,total=w+l;
      if(total<3)continue; // need ≥3 trades to be meaningful
      const wr=total>0?w/total:0;
      _todayHours.push({hour:h,wins:w,losses:l,total,wr:Math.round(wr*100)});
    }
    // Sort by WR desc, take top 3 with WR≥60%
    const _best=_todayHours.filter(h=>h.wr>=60).sort((a,b)=>b.wr-a.wr).slice(0,3);
    if(_best.length===0)return null;
    // Find the soonest upcoming best hour
    const curH=_now.getUTCHours();
    const _nextBest=_best.map(h=>({...h,minsUntil:h.hour>=curH?(h.hour-curH)*60-_now.getUTCMinutes():(24-curH+h.hour)*60-_now.getUTCMinutes()})).sort((a,b)=>a.minsUntil-b.minsUntil)[0];
    return{best:_best,dayName,nextBest:_nextBest,inBestWindow:_best.some(h=>h.hour===curH)};
  },[taraCallLog]);
  // V8.0: Conviction trajectory tracker. Records taraCall.posterior every 5s into a rolling
  //   2-min ring buffer. Lets us tell whether conviction is BUILDING, STABLE, or FADING since
  //   commit — surfaced as a small arrow on the call card.
  // V8.0 HOTFIX: ref declared here (no taraCall dep), sampler effect + trajectory useMemo
  //   are deferred to after `taraCall` is declared further down (TDZ otherwise).
  const convictionHistoryRef=useRef([]);
  // Reset conviction history on rollover/asset switch — safe here, no taraCall dep
  useEffect(()=>{
    convictionHistoryRef.current=[];
  },[windowType,currentAsset,timeState?.startWindow]);
  const pendingTradeRef=useRef(null);
  // V2.6: Queue of trades waiting on Kalshi settlement confirmation due to marginal closing gap.
  //       Each entry: { tradeId, windowCloseTime (ms), attempts }
  const pendingResolutionRef=useRef([]);
  const[showAnalytics,setShowAnalytics]=useState(false);
  const[showGuide,setShowGuide]=useState(false);
  const[selectedTradeId,setSelectedTradeId]=useState(null); // for editable trade log
  const[discordStatusMsg,setDiscordStatusMsg]=useState('');
  // V7.1: Per-asset calibration. Each asset uses ONLY its own trades to build the
  //   posterior calibration buckets, regime stats, signal accuracy, session/hourly
  //   performance. ETH starts with empty data (will be SCANNING-heavy until 10-20
  //   trades accumulate); BTC keeps its full history. Trades without an asset tag
  //   (legacy pre-V7.1) default to BTC. Cross-asset pollution is now impossible.
  // V9.8.17 — DATA SOURCE FIX. Previously this filtered `tradeLog` (the user's
  //   manual-trades log), which has been a no-op since V6.2.5 stubbed `saveTradeLog`.
  //   That meant every learning structure built off this — buildCalibration,
  //   buildRegimeDirWR, buildRegimeDirCalibration, buildSessionRegimeThresh,
  //   buildSignalAccuracy, buildSessionPerf, buildHourlyPerf — was being fed an
  //   empty array and silently returning default-prior data. The most visible
  //   symptom: V9.7.6's `regimeCalApplied` gate (L5077) requires `_entry.n >= 10`
  //   and never saw a non-empty entry, so calibration of overconfident posteriors
  //   never happened. CSV audit on 2026-05-08 confirmed regimeCalApplied=N in
  //   258/258 resolved BTC trades, while 75-94% conviction buckets ran 10-28pt
  //   below stated WR — the exact gap the gate was designed to close.
  //   Fix: source from `taraCallLog` (which has been accumulating Tara's resolved
  //   calls all along). Filter by asset for cross-asset isolation.
  const _tradeLogForCurrentAsset=useMemo(()=>{
    return(taraCallLog||[]).filter(t=>!!t&&(t.asset||'BTC')==='BTC'&&verifiedSummaryCall(t));
  },[taraCallLog,currentAsset]);
  const calibration=useMemo(()=>buildCalibration(_tradeLogForCurrentAsset),[_tradeLogForCurrentAsset]);
  const regimeDirWR=useMemo(()=>buildRegimeDirWR(_tradeLogForCurrentAsset),[_tradeLogForCurrentAsset]); // V134
  // V9.5.0: per-(regime,dir) blend weights for posterior calibration. Different from
  // regimeDirWR (which is just stats for UI) — this carries the {n, wr, weight}
  // tuple the engine uses to pull the posterior toward observed WR.
  const regimeDirCalibration=useMemo(()=>buildRegimeDirCalibration(_tradeLogForCurrentAsset),[_tradeLogForCurrentAsset]);
  const sessionRegimeThresh=useMemo(()=>buildSessionRegimeThresh(_tradeLogForCurrentAsset),[_tradeLogForCurrentAsset]); // V134
  const signalAccuracy=useMemo(()=>buildSignalAccuracy(_tradeLogForCurrentAsset),[_tradeLogForCurrentAsset]);
  const sessionPerf=useMemo(()=>buildSessionPerf(_tradeLogForCurrentAsset),[_tradeLogForCurrentAsset]);
  const hourlyPerf=useMemo(()=>buildHourlyPerf(_tradeLogForCurrentAsset),[_tradeLogForCurrentAsset]);
  // V9.8.18: per-(day-of-week, hour) performance grid + day-of-week aggregate.
  //   Powers DayAwareScheduleHeader. Rebuilt whenever the call log changes.
  const dayHourPerf=useMemo(()=>buildDayHourPerf(_tradeLogForCurrentAsset),[_tradeLogForCurrentAsset]);
  // V9.8.18: derived day context — today's name, weekend/holiday flag, activity
  //   level, rank vs other days, today's best hours, next upcoming strong window.
  //   Recomputed every minute so "minsUntil next window" stays fresh; also
  //   rebuilds when the call log changes.
  const[_dayContextTick,setDayContextTick]=useState(0);
  useEffect(()=>{
    const id=setInterval(()=>setDayContextTick(t=>t+1),60000);
    return()=>clearInterval(id);
  },[]);
  const dayContext=useMemo(()=>buildDayContext(dayHourPerf,new Date()),[dayHourPerf,_dayContextTick]);
  const[forceRender,setForceRender]=useState(0);
  // V9.17.19: manual auto-exec trigger nonce. Bumped when user clicks "place
  //   order on Tara's call" button. The entry effect has this in its deps so
  //   it RELIABLY re-runs (the prior forceRender→analysis chain wasn't always
  //   making the entry effect re-evaluate).
  const[_manualAutoExecNonce,_setManualAutoExecNonce]=useState(0);
  // V9.17.21: visible click feedback so user knows the button worked WITHOUT
  //   opening DevTools. Stores {at, msg, color} — clears after 5s.
  const[_manualOrderFeedback,_setManualOrderFeedback]=useState(null);
  const[isChatOpen,setIsChatOpen]=useState(false);
  const[chatLog,setChatLog]=useState([{role:"tara",text:"Ask about this window's call, signals, or AutoTrade. A local order marker is not an exchange-confirmed fill; verify real positions on Kalshi."}]);
  const[chatInput,setChatInput]=useState('');
  const lastWindowRef=useRef('');
  // V13.4.335: companion to lastWindowRef holding the window boundary as a
  //   raw epoch ms number, not a locale-formatted display string. See the
  //   rollover-detection effect below (timeState.nextWindowMs) for why this
  //   exists as a SEPARATE value rather than just fixing lastWindowRef.
  const _lastWindowMsRef=useRef(0);
  const[userPosition,setUserPosition]=useState(null);
  // V10.2.9 — MANUAL KALSHI ENTRY RECORDER
  //   Solves the V10.2.7 disconnect: when user clicks ENTERED UP/DOWN, that
  //   only sets a flag — Tara has no idea what they actually paid on Kalshi
  //   or how many contracts they hold. Result: the trade ticket shows a
  //   forward-looking preview ("will buy 1 @ 64¢") that has nothing to do
  //   with the real position. Unrealized P&L is wrong. Stop/target distance
  //   is wrong.
  //
  //   Now: when userPosition is set, a small input row appears under the
  //   ENTERED buttons. User enters their actual fill ¢ + contract count.
  //   The ticket then computes against THAT real entry instead of preview.
  //
  //   Storage: localStorage key 'tara_manual_kalshi_entry_v1'. Shape:
  //   {entryCents, contracts, side:'UP'|'DOWN', time}. Restored on refresh
  //   if it's < 30min old (any older = stale, user probably forgot to clear).
  //   Auto-cleared when userPosition is toggled off (handleManualSync close).
  const[manualKalshiEntry,setManualKalshiEntry]=useState(()=>{
    try{
      const raw=localStorage.getItem('tara_manual_kalshi_entry_v1');
      if(!raw)return null;
      const p=JSON.parse(raw);
      if(!p||typeof p!=='object')return null;
      if(!(p.entryCents>0&&p.entryCents<100))return null;
      if(!(p.contracts>0))return null;
      if(p.side!=='UP'&&p.side!=='DOWN')return null;
      // Stale guard: anything older than 30 min is dropped (user probably
      //   closed the position and forgot to clear it from Tara).
      if(Date.now()-(p.time||0)>30*60*1000)return null;
      return p;
    }catch(_){return null;}
  });
  // Persist manualKalshiEntry across renders
  useEffect(()=>{
    try{
      if(manualKalshiEntry){
        localStorage.setItem('tara_manual_kalshi_entry_v1',JSON.stringify(manualKalshiEntry));
      }else{
        localStorage.removeItem('tara_manual_kalshi_entry_v1');
      }
    }catch(_){}
  },[manualKalshiEntry]);
  // Auto-clear manualKalshiEntry when userPosition is cleared (toggle off,
  //   asset switch, window roll — all of which already null userPosition).
  // V13.4.278: this fired on EVERY PAGE LOAD and silently deleted the recorded
  //   fill. manualKalshiEntry hydrates synchronously from localStorage in its
  //   useState initializer, but userPosition starts null and is restored later,
  //   so the first render always looked like "position cleared while a fill
  //   exists" and wiped it. The user was then holding a position the app said
  //   had no fill price -- which is exactly what the dashboard was showing.
  //   Fix: clear only on a real TRANSITION from held to not-held. A null on the
  //   first render is "not restored yet", not "position closed".
  const _prevUserPosRef=useRef(undefined);
  useEffect(()=>{
    const _prev=_prevUserPosRef.current;
    _prevUserPosRef.current=userPosition;
    if(_prev===undefined)return;            // first render — nothing to compare against
    if(!_prev)return;                        // was already flat; not a transition
    if(!userPosition&&manualKalshiEntry){
      try{console.info('[V13.4.278] clearing manualKalshiEntry — position went '+_prev+' -> none');}catch(_){}
      setManualKalshiEntry(null);
    }
  },[userPosition,manualKalshiEntry]);
  // V10.2.10 — sync refs for reconciliation poll (poll function reads from these
  //   so it doesn't need to re-mount on every state change)
  _manualKalshiEntryRef.current=manualKalshiEntry;
  _userPositionRef.current=userPosition;
  // V9.10.5: showHelp state removed — opened the "What's New" modal which was stale at
  //   v8.8 and removed in V9.10.3 (button) + V9.10.5 (modal body). Orphan state cleaned.
  const[soundEnabled,setSoundEnabled]=useState(()=>{
    try{const s=localStorage.getItem('taraSoundEnabled');return s==='1';}catch(e){return false;}
  });
  const audioCtxRef=useRef(null);
  const soundEnabledRef=useRef(false);
  soundEnabledRef.current=soundEnabled;
  useEffect(()=>{try{localStorage.setItem('taraSoundEnabled',soundEnabled?'1':'0');}catch(e){}},[soundEnabled]);
  // V7.2.1: When sound was persisted as enabled but no AudioContext exists yet (next page
  //   load), wait for the FIRST user interaction to bootstrap audio. Browsers require a
  //   user gesture to unlock audio. Without this, sounds fire silently into a suspended
  //   context and never play.
  useEffect(()=>{
    if(!soundEnabled)return;
    if(audioCtxRef.current)return;
    const _bootAudio=()=>{
      try{
        const Ctx=window.AudioContext||window.webkitAudioContext;
        if(!Ctx)return;
        audioCtxRef.current=new Ctx();
        if(audioCtxRef.current.state==='suspended')audioCtxRef.current.resume();
      }catch(e){}
      window.removeEventListener('pointerdown',_bootAudio,true);
      window.removeEventListener('keydown',_bootAudio,true);
    };
    window.addEventListener('pointerdown',_bootAudio,true);
    window.addEventListener('keydown',_bootAudio,true);
    return()=>{
      window.removeEventListener('pointerdown',_bootAudio,true);
      window.removeEventListener('keydown',_bootAudio,true);
    };
  },[soundEnabled]);
  // V6.0: master alert volume (0..1), persisted to localStorage. UI exposes slider next to
  //   the sound button when sound is on. Default 0.7 — audible but not startling.
  const[alertVolume,setAlertVolume]=useState(()=>{
    try{const s=localStorage.getItem('taraAlertVolume');if(s!=null)return Math.max(0,Math.min(1,parseFloat(s)));}catch(e){}
    return 0.7;
  });
  const alertVolumeRef=useRef(alertVolume);
  alertVolumeRef.current=alertVolume;
  useEffect(()=>{try{localStorage.setItem('taraAlertVolume',String(alertVolume));}catch(e){}},[alertVolume]);
  // V6.0: dedup — prevent the same alert from firing twice in quick succession.
  const lastAlertRef=useRef({type:null,at:0});

  const handleSoundToggle=()=>{
    const next=!soundEnabled;
    setSoundEnabled(next);
    if(next){
      try{
        const Ctx=window.AudioContext||window.webkitAudioContext;
        if(!audioCtxRef.current)audioCtxRef.current=new Ctx();
        if(audioCtxRef.current.state==='suspended')audioCtxRef.current.resume();
        // V6.0: confirmation chime — soft major-third glide instead of single beep.
        const ctx=audioCtxRef.current;
        const t0=ctx.currentTime;
        const _vol=alertVolumeRef.current||0.7;
        const note=(freq,when,dur,vol)=>{
          const o=ctx.createOscillator(),g=ctx.createGain();
          o.type='sine';o.frequency.value=freq;
          g.gain.setValueAtTime(0.0001,t0+when);
          g.gain.exponentialRampToValueAtTime(vol*_vol,t0+when+0.008);
          g.gain.exponentialRampToValueAtTime(0.0001,t0+when+dur);
          o.connect(g);g.connect(ctx.destination);
          o.start(t0+when);o.stop(t0+when+dur);
        };
        note(659.25,0,0.18,0.06);    // E5
        note(987.77,0.10,0.30,0.07); // B5 (perfect fifth above)
      }catch(e){}
    }
  };

  // V6.0: Layered voice engine. Each call to voice() creates one note with envelope shaping,
  //   optional harmonic layer, optional filter sweep, optional stereo pan. Clean attacks
  //   (8ms ramp) eliminate clicks. Harmonic layering gives warmth — pure sine is too thin.
  //   Filter sweeps add "ping" character without harshness.
  const playAlert=(type)=>{
    if(!soundEnabledRef.current)return;
    // Dedup: same type within 500ms is ignored
    const _now=Date.now();
    if(lastAlertRef.current.type===type&&_now-lastAlertRef.current.at<500)return;
    lastAlertRef.current={type,at:_now};
    try{
      const Ctx=window.AudioContext||window.webkitAudioContext;
      if(!audioCtxRef.current)audioCtxRef.current=new Ctx();
      const ctx=audioCtxRef.current;
      const masterVol=alertVolumeRef.current||0.7;
      // Master gain — multiplied into every voice via individual envelope volumes
      const voice=(opts)=>{
        const{freq,when=0,dur=0.3,vol=0.1,wave='sine',harmonic=null,pan=0,filter=null}=opts;
        const t0=ctx.currentTime+when;
        const finalVol=vol*masterVol;
        // Envelope — smooth attack avoids clicks, exponential decay tail
        const env=ctx.createGain();
        const attack=0.008;
        env.gain.setValueAtTime(0.0001,t0);
        env.gain.exponentialRampToValueAtTime(Math.max(0.0001,finalVol),t0+attack);
        env.gain.exponentialRampToValueAtTime(0.0001,t0+dur);
        // Routing: env → (filter?) → (pan?) → destination
        let tail=env;
        if(filter){
          const f=ctx.createBiquadFilter();
          f.type='lowpass';
          f.frequency.setValueAtTime(filter.start||800,t0);
          f.frequency.exponentialRampToValueAtTime(filter.end||4000,t0+(filter.dur||dur*0.5));
          f.Q.value=filter.Q||1;
          env.connect(f);
          tail=f;
        }
        if(pan!==0&&typeof ctx.createStereoPanner==='function'){
          const p=ctx.createStereoPanner();
          p.pan.value=Math.max(-1,Math.min(1,pan));
          tail.connect(p);
          p.connect(ctx.destination);
        } else {
          tail.connect(ctx.destination);
        }
        // Primary oscillator
        const o=ctx.createOscillator();
        o.type=wave;
        o.frequency.value=freq;
        o.connect(env);
        o.start(t0);
        o.stop(t0+dur);
        // Optional harmonic layer (octave/fifth/whatever) for richness
        if(harmonic){
          const hg=ctx.createGain();
          const hVol=finalVol*(harmonic.vol||0.3);
          hg.gain.setValueAtTime(0.0001,t0);
          hg.gain.exponentialRampToValueAtTime(Math.max(0.0001,hVol),t0+attack);
          hg.gain.exponentialRampToValueAtTime(0.0001,t0+dur);
          hg.connect(tail===env?env:env); // route through same envelope path
          // Actually route harmonic through its OWN tail so filter applies independently
          hg.disconnect();
          let hTail=hg;
          if(filter){
            const hf=ctx.createBiquadFilter();
            hf.type='lowpass';
            hf.frequency.setValueAtTime(filter.start||800,t0);
            hf.frequency.exponentialRampToValueAtTime(filter.end||4000,t0+(filter.dur||dur*0.5));
            hf.Q.value=filter.Q||1;
            hg.connect(hf);
            hTail=hf;
          }
          if(pan!==0&&typeof ctx.createStereoPanner==='function'){
            const hp=ctx.createStereoPanner();
            hp.pan.value=Math.max(-1,Math.min(1,pan));
            hTail.connect(hp);
            hp.connect(ctx.destination);
          } else {
            hTail.connect(ctx.destination);
          }
          const h=ctx.createOscillator();
          h.type=harmonic.wave||'sine';
          h.frequency.value=freq*(harmonic.ratio||2);
          h.connect(hg);
          h.start(t0);
          h.stop(t0+dur);
        }
      };
      // V6.0: Sound recipes per alert type. Designed to be musical and distinct so you can
      //   identify the event without looking at the screen. Pan: UP slightly right, DOWN
      //   slightly left — subtle directional cue.
      const playSequence=()=>{
        if(type==='lock-up'){
          // Two-note rising fifth, warm — Tara committed UP
          voice({freq:523.25,when:0,   dur:0.25,vol:0.10,harmonic:{ratio:2,vol:0.30},pan:0.15});
          voice({freq:783.99,when:0.16,dur:0.40,vol:0.12,harmonic:{ratio:2,vol:0.30},pan:0.15});
        } else if(type==='lock-down'){
          // Falling fifth, warm — Tara committed DOWN
          voice({freq:659.25,when:0,   dur:0.25,vol:0.10,harmonic:{ratio:2,vol:0.25},pan:-0.15});
          voice({freq:392.00,when:0.16,dur:0.40,vol:0.12,harmonic:{ratio:2,vol:0.25},pan:-0.15});
        } else if(type==='confluence-lock-up'){
          // Three-note major arpeggio C-E-G with bright capping ping. Super-confluence UP.
          voice({freq:523.25,when:0,   dur:0.20,vol:0.10,harmonic:{ratio:2,vol:0.40,wave:'triangle'},pan:0.18});
          voice({freq:659.25,when:0.13,dur:0.20,vol:0.10,harmonic:{ratio:2,vol:0.40,wave:'triangle'},pan:0.18});
          voice({freq:783.99,when:0.26,dur:0.50,vol:0.13,harmonic:{ratio:2,vol:0.40,wave:'triangle'},pan:0.18,filter:{start:1200,end:5000,dur:0.15}});
          voice({freq:1046.50,when:0.36,dur:0.30,vol:0.06,harmonic:{ratio:2,vol:0.30}});
        } else if(type==='confluence-lock-down'){
          // Three-note minor descending G-Eb-C with cap. Super-confluence DOWN.
          voice({freq:783.99,when:0,   dur:0.20,vol:0.10,harmonic:{ratio:2,vol:0.40,wave:'triangle'},pan:-0.18});
          voice({freq:622.25,when:0.13,dur:0.20,vol:0.10,harmonic:{ratio:2,vol:0.40,wave:'triangle'},pan:-0.18});
          voice({freq:523.25,when:0.26,dur:0.50,vol:0.13,harmonic:{ratio:2,vol:0.40,wave:'triangle'},pan:-0.18,filter:{start:800,end:2500,dur:0.15}});
          voice({freq:261.63,when:0.36,dur:0.30,vol:0.06,harmonic:{ratio:2,vol:0.30}});
        } else if(type==='early-entry-up'){
          // V6.0.1: Quick two-note chirp upward + bright filter ping. Lighter than full
          //   confluence — signals "early action" without the gravitas of full commitment.
          voice({freq:659.25,when:0,   dur:0.13,vol:0.08,harmonic:{ratio:2,vol:0.30},pan:0.12,filter:{start:800,end:4000,dur:0.06}});
          voice({freq:880,   when:0.10,dur:0.25,vol:0.10,harmonic:{ratio:2,vol:0.30},pan:0.12,filter:{start:800,end:4000,dur:0.06}});
        } else if(type==='early-entry-down'){
          // V6.0.1: Quick two-note chirp downward — symmetric to early-entry-up
          voice({freq:587.33,when:0,   dur:0.13,vol:0.08,harmonic:{ratio:2,vol:0.25},pan:-0.12,filter:{start:800,end:3000,dur:0.06}});
          voice({freq:440,   when:0.10,dur:0.25,vol:0.10,harmonic:{ratio:2,vol:0.25},pan:-0.12,filter:{start:800,end:3000,dur:0.06}});
        } else if(type==='entry'){
          // Three-pulse alert with bright filter sweep — "act now"
          voice({freq:880, when:0,   dur:0.10,vol:0.10,filter:{start:600,end:5000,dur:0.04}});
          voice({freq:880, when:0.12,dur:0.10,vol:0.10,filter:{start:600,end:5000,dur:0.04}});
          voice({freq:1108,when:0.24,dur:0.18,vol:0.12,filter:{start:600,end:5000,dur:0.04}});
        } else if(type==='profit'){
          // Sparkling pentatonic ascent — celebratory but not silly
          voice({freq:523.25,when:0,   dur:0.13,vol:0.08,harmonic:{ratio:2,vol:0.30}});
          voice({freq:659.25,when:0.10,dur:0.13,vol:0.08,harmonic:{ratio:2,vol:0.30}});
          voice({freq:783.99,when:0.20,dur:0.13,vol:0.08,harmonic:{ratio:2,vol:0.30}});
          voice({freq:1046.50,when:0.30,dur:0.40,vol:0.10,harmonic:{ratio:2,vol:0.40}});
        } else if(type==='warning'){
          // Two-note descending tritone — uneasy but not panic
          voice({freq:466.16,when:0,   dur:0.20,vol:0.10,wave:'triangle',harmonic:{ratio:2,vol:0.20}});
          voice({freq:329.63,when:0.18,dur:0.30,vol:0.10,wave:'triangle',harmonic:{ratio:2,vol:0.20}});
        } else if(type==='emergency'){
          // Rapid pulsing low — urgent, gets attention
          for(let i=0;i<4;i++){
            voice({freq:196,when:i*0.13,dur:0.10,vol:0.13,wave:'sawtooth',filter:{start:200,end:1000,dur:0.04,Q:2}});
          }
        } else if(type==='tape-veto'){
          // Soft "stand-down" — high-to-low filter sweep, brief
          voice({freq:392,when:0,dur:0.40,vol:0.06,filter:{start:1500,end:200,dur:0.35}});
        } else if(type==='window-open'){
          // Subtle bell — barely noticeable, just a gentle transition cue
          voice({freq:880,when:0,dur:0.5,vol:0.04,harmonic:{ratio:2,vol:0.30}});
        } else {
          voice({freq:440,dur:0.20,vol:0.06});
        }
      };
      // V3.1.2 BUGFIX: Don't return early on suspended context. Browsers auto-suspend audio
      // contexts after tab inactivity — previously this silently dropped every alert that
      // happened to occur while suspended. Now we await resume() and then play.
      if(ctx.state==='suspended'){
        const r=ctx.resume();
        if(r&&typeof r.then==='function'){r.then(()=>{try{playSequence();}catch(e){}}).catch(()=>{});}
        else {playSequence();}
      } else {
        playSequence();
      }
    }catch(e){}
  };
  const[showWhaleLog,setShowWhaleLog]=useState(false);
  const[showTheoryLab,setShowTheoryLab]=useState(false); // V10.9.3 Theory Lab panel toggle
  const velocityRef=useVelocity(tickHistoryRef,currentPrice,targetMargin);
  const bloomberg=useDerivativesData(); // legacy object name retained for engine compatibility
  const economicCalendar=useEconomicCalendar();
  const depthFlash=useDepthFlash(); // V134: 2.5s order book polling
  const tfCandles=useMultiTFCandles(currentAsset,priceSource); // V134: HPotter FGT multi-timeframe data — V9.8.5 source-aware
  // V9.11.0 LAYER 2: pull funding / OI / basis from Bybit perpetuals every 30s.
  //   Feeds the futures-signal block in the engine. See useFuturesData hook above.
  const futuresData=useFuturesData(currentAsset);
  // V9.12: 4-asset macro shock detector. SOL+XRP returns get fetched here and
  //   combined with BTC+ETH (from priceSource / shadowRef) inside the engine to
  //   compute synchrony. Hook is global — same data for both BTC and ETH views.
  const macroShockData=useMacroShockData();
  // V9.14: spot-vs-perp divergence. Polls Coinbase spot every 10s, compares
  //   against bloomberg perp mark price. Output feeds the spotPerpDiv signal.
  const spotPerpDiv=useSpotPerpDivergence(currentAsset,bloomberg);
  // V10.7.65: Whale Alert on-chain signal
  const whaleAlert=useWhaleAlert(currentAsset);
  // V10.7.73: New high-signal data sources
  const deribitOptions=useDeribitOptions();   // IV skew — smart money positioning
  const liqSignal=useLiqSignal();             // liquidations + CB premium + funding
  // V10.7.76: Liquidation heatmap + OI (highest-impact new signal)
  const liqHeatmap=useLiqHeatmap();
  const{tapeRef,globalFlow,ticksRef,whaleLog,flowSignal,tapeWindows}=useGlobalTape(currentAsset);
  // V10.8.7: expose tapeRef globally for useWhaleAlert tape-based whale detection
  React.useEffect(()=>{
    window._taraGlobalTapeRef=tapeRef;
    return()=>{try{delete window._taraGlobalTapeRef;}catch(_){}};
  },[tapeRef]);
  // V9.17.1: SCALPER ENGINE — always ticks (was gated by enabled in V9.17.0).
  //   The panel header "tara reads" needs live data even when scalper is OFF,
  //   so users see what the engine sees as a contextual signal. Suggestions
  //   are gated separately in the panel via state machine.
  const scalperRead=useScalperEngine({
    tapeWindows,globalFlow,velocityRef,spotPerpDiv,depthFlash,macroShockData,currentAsset,
    enabled:true,
  });
  // V9.17.4: KALSHI BALANCE TRACKING. Polls /portfolio/balance every 30s when
  //   credentials are present. Replaces removed local P&L tracker with real
  //   Kalshi-source data. UTC day baseline for "today's delta" display.
  const kalshiBalance=useKalshiBalance({kalshiCreds,timeFormat}); // V10.2.x: timeFormat for day-roll
  const marketSessions=useMemo(()=>getMarketSessions(),[timeState.currentHour]);
  const[klines,setKlines]=useState([]);
  // V8.1: Movement risk pulse — combines vol acceleration, volume spikes, tape imbalance,
  //   whale density into a 0-100 score every 2s. Wired here because all input refs are
  //   in scope. lastRegimeRef passed by reference so the hook always reads the latest
  //   regime without needing dependency rerun.
  const movementRisk=useMovementRisk(velocityRef,tapeRef,ticksRef,whaleLog,lastRegimeRef);
  // ── V8.3: MULTI-TAB AWARENESS ──────────────────────────────────────────
  // Heartbeat broadcast every 5s with this tab's current asset. Peer tabs receive it
  //   and add this tab to their peer list. Used to show TabPresencePill.
  const[peerTabs,setPeerTabs]=useState([]);
  useEffect(()=>{
    const beat=()=>broadcastToOtherTabs('heartbeat',{tabId:getCurrentTabId(),currentAsset:currentAssetRef.current});
    beat(); // immediate
    const iv=setInterval(beat,5000);
    return()=>clearInterval(iv);
  },[]);
  useEffect(()=>{
    // Re-broadcast immediately when asset changes so peers see updated state
    broadcastToOtherTabs('heartbeat',{tabId:getCurrentTabId(),currentAsset});
  },[currentAsset]);
  useEffect(()=>{
    return subscribeToPeerTabs(setPeerTabs);
  },[]);

  // ── V8.5: WINDOW CONTEXT TRACKING (whale prints + geoRisk during active window) ──
  // Updates windowWhaleStatsRef + windowMaxGeoRiskRef whenever whaleLog or newsSentiment
  // changes, but ONLY if there's an active position. Refs reset at window open.
  // Used at trade resolution to classify shock losses.
  useEffect(()=>{
    if(!userPosition||!windowOpenTimeRef.current)return;
    const _winOpen=windowOpenTimeRef.current;
    const _now=Date.now();
    const _winSec=windowType==='15m'?900:300;
    // ── Whale tracking ── find whales that fired during this window
    const _windowWhales=(whaleLog||[]).filter(w=>w&&w.time>=_winOpen);
    if(_windowWhales.length>0){
      // Contrary direction: opposing the user's position. UP user → SELL whales hurt.
      const _contrarySide=userPosition==='UP'?'SELL':'BUY';
      const _contraryWhales=_windowWhales.filter(w=>w.side===_contrarySide);
      if(_contraryWhales.length>0){
        // Find the largest contrary whale
        const _largest=_contraryWhales.reduce((a,b)=>(b.usd||0)>(a.usd||0)?b:a,_contraryWhales[0]);
        if((_largest.usd||0)>(windowWhaleStatsRef.current.contraryUsd||0)){
          const _elapsedMs=_largest.time-_winOpen;
          const _clockSec=Math.max(0,_winSec-Math.round(_elapsedMs/1000));
          windowWhaleStatsRef.current={
            count:_windowWhales.length,
            contraryUsd:_largest.usd||0,
            contraryClockSec:_clockSec,
            contrarySide:_contrarySide,
          };
        } else {
          windowWhaleStatsRef.current.count=_windowWhales.length;
        }
      } else {
        windowWhaleStatsRef.current.count=_windowWhales.length;
      }
    }
    // ── GeoRisk tracking ── peak risk during window
    const _curRisk=Number(newsSentiment?.geoRisk)||0;
    if(_curRisk>windowMaxGeoRiskRef.current){
      windowMaxGeoRiskRef.current=_curRisk;
    }
  },[whaleLog,newsSentiment,userPosition,windowType]);

  useEffect(()=>{setIsMounted(true);
    // ── V8.7.3: Service worker registration DISABLED ──
    // After being hit by stale-cache TDZ crashes twice, we no longer register a service
    // worker. The module-level IIFE at the top of App.jsx unconditionally unregisters
    // any existing SW on every page load and clears Cache Storage. This guarantees
    // every page load fetches fresh code from the server. Tradeoff: PWA installability
    // (offline cache) is lost, but reliability is gained. If we ever re-enable SW, the
    // sw.js file itself needs to be a no-op cache-bypass worker, not a precaching one.
    // V4.6 AUTO FRESH-START: If user has any prior baseline version stored, wipe all training
    //   data automatically on first load of V4.6. Per user: 'i asked for a fresh start right'.
    //   This fires once on version-bump, then never again.
    try{
      const lastSyncedVersion=localStorage.getItem('taraBaselineVersion');
      const FRESH_START_MARKER='taraFreshStartV46';
      const alreadyWiped=localStorage.getItem(FRESH_START_MARKER);
      if(!alreadyWiped&&lastSyncedVersion&&lastSyncedVersion!==BASELINE_VERSION){
        // Wipe everything training-related
        localStorage.removeItem('taraTradeLogV110');
        localStorage.removeItem('taraV110Score');
        localStorage.removeItem('taraScoreV110');
        localStorage.removeItem('taraWeightsV110');
        localStorage.removeItem('taraCalibrationV110');
        localStorage.removeItem('taraV110Mem');
        localStorage.removeItem('taraCallScorecards_v1');
        Object.keys(localStorage).filter(k=>k.startsWith('taraV110RW_')).forEach(k=>localStorage.removeItem(k));
        // Mark as wiped so we don't re-wipe on subsequent loads
        localStorage.setItem(FRESH_START_MARKER,'1');
        localStorage.setItem('taraBaselineVersion',BASELINE_VERSION);
        console.info('[Tara] V4.6 fresh-start migration completed — all training data wiped');
        // Reload to re-init from clean state
        window.location.reload();
        return;
      }
      // First-ever install — just stamp the version
      if(!lastSyncedVersion){
        localStorage.setItem('taraBaselineVersion',BASELINE_VERSION);
        localStorage.setItem(FRESH_START_MARKER,'1');
      }
    }catch(e){}
    // V134: Baseline version check — detect if a newer baseline has shipped (post-fresh-start)
    try{
      const lastSyncedVersion=localStorage.getItem('taraBaselineVersion');
      if(lastSyncedVersion&&lastSyncedVersion!==BASELINE_VERSION){
        console.info('[Tara] New baseline available:',BASELINE_VERSION,'(you have:',lastSyncedVersion,')');
        setBaselineDrift(true);
      } else if(!lastSyncedVersion){
        localStorage.setItem('taraBaselineVersion',BASELINE_VERSION);
      }
    }catch(e){}
    try{
      // V7.10.5: scorecards are now read at useState init from taraPersonalScorecards_v1.
      //   This block now only runs ONE-TIME MIGRATION from legacy keys (taraV110Score /
      //   taraScoreV110) if the new key is empty. BASELINE_RECORD no longer auto-applied
      //   — users actually save their own scores now via the new persistence path.
      const _alreadyMigrated=localStorage.getItem('taraPersonalScorecards_v1');
      if(!_alreadyMigrated){
        const sNew=localStorage.getItem('taraV110Score');
        const sOld=localStorage.getItem('taraScoreV110');
        let chosen=null;
        if(sNew){try{const p=JSON.parse(sNew);if(p?.['15m']?.wins!=null)chosen=p;}catch(e){}}
        if(!chosen&&sOld){try{const p=JSON.parse(sOld);if(p?.['15m']?.wins!=null)chosen=p;}catch(e){}}
        if(chosen){
          setScorecards(chosen);
          // Stamp the new key so we don't migrate again on next mount
          try{localStorage.setItem('taraPersonalScorecards_v1',JSON.stringify(chosen));}catch(e){}
        }
      }
      const m=localStorage.getItem('taraV110Mem');
      // V7.10.5: regimeMemory migration is similar — only if new key is empty
      const _regimeMigrated=localStorage.getItem('taraRegimeMemory_v1');
      if(!_regimeMigrated&&m){
        try{
          const _parsed=JSON.parse(m);
          if(_parsed&&typeof _parsed==='object')setRegimeMemory(_parsed);
        }catch(e){}
      }
      // V7.0: Per-asset webhooks. V7.1: BTC + ETH only.
      try{
        const _newHooks=localStorage.getItem('taraV70Hooks');
        if(_newHooks){
          const _parsed=JSON.parse(_newHooks);
          if(_parsed&&typeof _parsed==='object')setDiscordWebhooks({BTC:_parsed.BTC||''});
        } else {
          // Legacy migration: copy single webhook into BTC slot
          const _old=localStorage.getItem('taraV110Hook');
          if(_old)setDiscordWebhooks({BTC:_old});
        }
      }catch(e){}
      const tz=localStorage.getItem('taraV110TZ');if(tz!=null)setUseLocalTime(tz==='true');
      // Username migration: always sync to current version, never keep stale Vxxx strings
      const du=localStorage.getItem('taraV110DU');
      const cleanDU=(du&&!new RegExp('V1[0-9][0-9]').test(du||''))?du:TARA_VERSION_DISPLAY; // V9.8.16: dynamic version (was hardcoded "Tara 7.10.6")
      setDiscordUsername(cleanDU);
      if(cleanDU!==du)localStorage.setItem('taraV110DU',cleanDU); // write back corrected value
      const da=localStorage.getItem('taraV110DA');if(da)setDiscordAvatar(da);}catch(e){};},[]);
  // V5.5b: Personal scorecard (taraV110Score) and regime memory (taraV110Mem) no longer
  //   persist — they're derived from user trades, which user wants excluded from saved
  //   stats/training. Discord settings (preference, not trade data) still persist.
  useEffect(()=>{if(!isMounted)return;try{
    // V7.0: per-asset webhooks
    localStorage.setItem('taraV70Hooks',JSON.stringify(discordWebhooks));
    // Legacy back-compat: keep taraV110Hook synced to BTC's webhook
    localStorage.setItem('taraV110Hook',discordWebhooks.BTC||'');
    localStorage.setItem('taraV110TZ',String(useLocalTime));
    localStorage.setItem('taraTimeFormat',timeFormat); // V8.9.3: 3-way format persistence
    localStorage.setItem('taraV110DU',discordUsername);
    localStorage.setItem('taraV110DA',discordAvatar);
  }catch(e){};},[discordWebhooks,useLocalTime,discordUsername,discordAvatar,isMounted]);

  useEffect(()=>{if(!currentPrice)return;const iv=setInterval(()=>{priceMemoryRef.current.push({p:currentPrice,time:Date.now()});priceMemoryRef.current=priceMemoryRef.current.filter(t=>Date.now()-t.time<600000);},2000);return()=>clearInterval(iv);},[currentPrice]);
  // V6.4: Regime history sampler. Records {atrBps, regime, rangeBps, timestamp} every 5s.
  //   Keeps last 3 minutes (36 samples). Used by transition detector to spot regime change.
  useEffect(()=>{
    const iv=setInterval(()=>{
      const a=analysisRef.current;
      if(!a)return;
      regimeHistoryRef.current.push({
        t:Date.now(),
        atr:a.atrBps||0,
        regime:a.regime||'',
        range:a.rangeBps||0,
        velocity:a.velocityRegime||'',
      });
      regimeHistoryRef.current=regimeHistoryRef.current.filter(s=>Date.now()-s.t<180000);
    },5000);
    return()=>clearInterval(iv);
  },[]);

  // V3.1.7+/V3.1.9: Window amplitude tracking — update high/low refs on each price tick.
  //   Also records WHEN high/low were first reached for structure detection.
  useEffect(()=>{
    if(!currentPrice||!windowOpenPriceRef.current||windowOpenPriceRef.current<=0)return;
    const _now=Date.now();
    if(windowHighRef.current===0||currentPrice>windowHighRef.current){
      windowHighRef.current=currentPrice;
      windowHighTimeRef.current=_now;
    }
    if(windowLowRef.current===0||currentPrice<windowLowRef.current){
      windowLowRef.current=currentPrice;
      windowLowTimeRef.current=_now;
    }
  },[currentPrice]);

  // Kline fetch with dual fallback — V9.8.5 source-aware (was Coinbase only)
  useEffect(()=>{
    const _cfg=ASSET_CONFIG[currentAsset]||ASSET_CONFIG.BTC;
    const _bnSym=_cfg.binance||'BTCUSDT';
    // V10.7.59b: BRTI is virtual — no candleUrl. Fall back to Coinbase candles.
    //   Without this fix: _src.candleUrl throws → liveHistory empty → engine stuck.
    const _src=PRICE_SOURCES[priceSource]?.virtual
      ?PRICE_SOURCES.coinbase
      :(PRICE_SOURCES[priceSource]||PRICE_SOURCES[PRICE_SOURCE_DEFAULT]);
    const fetch_=async()=>{
      try{
        const cbMap={'1m':60,'3m':60,'5m':300,'15m':900,'30m':900,'1h':3600};
        const gran=cbMap[chartRes]||60;
        const r=await fetch(_src.candleUrl(_cfg,currentAsset,gran),{cache:'no-store'});
        if(!r.ok)throw new Error(_src.label+' '+r.status);
        const d=await r.json();
        const _candles=_src.parseCandles(d);
        if(_candles.length===0)throw new Error('empty');
        // Chart consumer wants oldest-first → reverse from newest-first
        const f=_candles.map(c=>({time:c.time,o:c.o,h:c.h,l:c.l,c:c.c,v:c.v})).reverse();
        setKlines(f);
      }catch(e){
        // V9.7.5: Bybit fallback (Binance global blocked in US). Kept as last resort
        //   even though it's typically blocked too — better than nothing if both
        //   primary and active source fail simultaneously.
        try{
          const ivMap={'1m':'1','3m':'3','5m':'5','15m':'15','30m':'30','1h':'60'};
          const ivb=ivMap[chartRes]||'1';
          const r2=await fetch(`/api/bybit/market/kline?category=linear&symbol=${_bnSym}&interval=${ivb}&limit=200`);
          const j=await r2.json();
          if(j?.retCode!==0||!Array.isArray(j?.result?.list))throw new Error('bybit bad');
          // Bybit returns newest-first; reverse to oldest-first for chart.
          setKlines(j.result.list.map(c=>({time:parseInt(c[0])/1000,o:parseFloat(c[1]),h:parseFloat(c[2]),l:parseFloat(c[3]),c:parseFloat(c[4]),v:parseFloat(c[5])})).reverse());
        }catch(e2){console.warn('All chart APIs blocked');}
      }
    };
    fetch_();const _h=taraSetVizInterval(fetch_,10000,60000);return()=>_h.clear();
  },[chartRes,currentAsset,priceSource]);

  // Live price — V9.8.4 source-aware (Coinbase / Kraken / Bitstamp via PRICE_SOURCES)
  //   Tracks lastPriceChangeRef so the stale-tick guard can detect frozen feeds.
  useEffect(()=>{
    // V10.7.58: BRTI virtual source — when user selects BRTI in feed pill, currentPrice
    //   comes directly from brtiApprox.instant (the 3-exchange trimmed mean already
    //   computed separately). No new network calls. Skip the REST poll entirely.
    // V10.7.73c: BRTI feed removed — no longer in PRICE_SOURCES
    let last=0;let _lastTradeId=0;const _cfg=ASSET_CONFIG[currentAsset]||ASSET_CONFIG.BTC;const _src=PRICE_SOURCES[priceSource]||PRICE_SOURCES[PRICE_SOURCE_DEFAULT];const f=async()=>{try{const _url=_src.url(_cfg,currentAsset);const r=await fetch(_url,{cache:'no-store'});if(!r.ok)return;const d=await r.json();/* V9.8.12: stale CDN guard — Coinbase ticker has monotonic trade_id. If a fetch returns
   an OLDER id than we've already seen, the edge cache is replaying a stale response.
   V10.7.73b FIX: was rejecting SAME id too (=== check). If CDN serves same id but price
   is identical, that's valid (BTC not moving). Only reject strictly OLDER ids.
   Also: always update lastPriceSourceRef even if we reject the price value — this
   prevents the stale guard from firing just because BTC price hasn't changed. */
if(typeof _src.parseTradeId==='function'){const _newId=_src.parseTradeId(d);if(_newId&&_lastTradeId&&_newId<_lastTradeId){
  // A replay is not a fresh observation; preserve the last successful receipt time.
  return; // discard the stale price value
}if(_newId)_lastTradeId=_newId;}const p=_src.parsePrice(d);if(p&&Number.isFinite(p)){const now=Date.now();
              if(assessSpotResponse(_src.label,d,p,now).status!=='fresh')return;
              // V10.7.73b: Update lastPriceSourceRef on EVERY successful fetch.
              //   Previously only updated when price changed — but if BTC is flat
              //   for 60s, price doesn't change, sourceRef doesn't update, stale
              //   guard fires even though feed is perfectly healthy.
              lastPriceSourceRef.current={source:'rest',time:now,exchange:priceSource};
              if(p!==lastPriceChangeRef.current.price){lastPriceChangeRef.current={price:p,time:now};}currentPriceRef.current=p;if(now-last>300){setCurrentPrice(prev=>{if(prev!==null&&p!==prev)setTickDirection(p>prev?'up':'down');return p;});last=now;}const _sz=_src.parseSize(d)||0.1;tickHistoryRef.current.push({p,s:_sz,t:'B',time:now,ex:_src.label});}}catch(e){}};f();const iv=setInterval(f,1500);return()=>clearInterval(iv);
  // V10.7.58d: removed brtiApprox?.instant from deps — was causing effect to
  //   tear down + recreate every 3s when on BRTI feed, causing scanning/connecting
  //   thrash. The BRTI interval reads brtiApprox via closure, no re-run needed.
  },[currentAsset,priceSource]);

  // V10.5.1 — CROSS-EXCHANGE BRTI APPROXIMATION
  //   V10.9.21: server proxy route REMOVED (brti.js deleted from the Vercel
  //   project). The client-side constituent blend below (CB/KR/OKX/Bybit, hot
  //   feeds bias-corrected toward the CB/KR anchor) is now the sole BRTI
  //   reference. It was already the live path (telemetry showed src:3, not the
  //   proxy), so removing it just stops a dead per-poll fetch that 404'd.
  useEffect(()=>{
    const _cfg=ASSET_CONFIG[currentAsset]||ASSET_CONFIG.BTC;
    let stopped=false,busy=false;
    const _fetchOne=async(url,parsePrice,name)=>{
      try{
        const r=await fetch(url,{cache:'no-store',signal:AbortSignal.timeout(5000)});
        if(!r.ok)throw new Error(`HTTP ${r.status}`);
        const d=await r.json();
        const p=parsePrice(d);
        return assessSpotResponse(name,d,p);
      }catch(_){return {name,price:null,receivedAt:null,status:'unavailable',timestampBasis:'unknown'};}
    };
    const _poll=async()=>{
      if(stopped||busy)return;busy=true;
      try{
      // ── Constituent fallback (V10.9.10) ──────────────────────────────────
      //   ROOT-CAUSE FIX: the old fallback used Gemini + Bitstamp, whose public
      //   APIs fail CORS from the browser — so this blend almost never reached
      //   the 2-source minimum (1 of 2737 trades had BRTI telemetry). When
      //   Coinbase's own fetch hiccupped, the blend died entirely and Tara fell
      //   back to the raw single-feed price.
      //   New constituents are the feeds Tara already proves she CAN reach from
      //   the browser (they power the live chart + tape): Coinbase, Kraken, OKX,
      //   Bybit. CB + KR track CF Benchmark closely; OKX + Bybit run hot (~+50-70
      //   on BTC) so we BIAS-CORRECT them toward the CB/KR consensus before
      //   including them. This keeps the blend alive even if any one feed drops,
      //   and keeps it CF-accurate even when only the hot feeds are up.
      const _cb=PRICE_SOURCES.coinbase;
      const _kr=PRICE_SOURCES.kraken;
      const _okx=PRICE_SOURCES.okx;
      const _bybitUrl=`/api/bybit/market/tickers?category=linear&symbol=${currentAsset==='BTC'?'BTCUSDT':'ETHUSDT'}`;
      const _results=await Promise.all([
        _fetchOne(_cb.url(_cfg,currentAsset),_cb.parsePrice,'CB'),
        _fetchOne(_kr.url(_cfg,currentAsset),_kr.parsePrice,'KR'),
        _okx?_fetchOne(_okx.url(_cfg,currentAsset),_okx.parsePrice,'OKX'):Promise.resolve(null),
        _fetchOne(_bybitUrl,(d)=>{const v=d?.result?.list?.[0]?.lastPrice;return v?parseFloat(v):null;},'BYB'),
      ]);
      if(stopped)return;
      const sourceHealth=_results.filter(Boolean);
      let _ok=sourceHealth.filter(x=>x.price>0&&x.status==='fresh');
      if(_ok.length<1){setBrtiApprox(prev=>({...prev,sourceHealth,status:'unavailable'}));return;}
      // ── Bias-correct the hot exchanges toward the CB/KR anchor ──
      //   Maintain a rolling offset (in bps) for each hot feed vs the anchor mean.
      //   When the anchor (CB and/or KR) is present, update the offset; always
      //   apply the latest known offset to OKX/Bybit so their contribution is
      //   pulled onto the CF-Benchmark scale.
      const _anchorVals=_ok.filter(x=>x.name==='CB'||x.name==='KR').map(x=>x.price);
      const _anchorMean=_anchorVals.length?_anchorVals.reduce((s,p)=>s+p,0)/_anchorVals.length:null;
      if(_anchorMean){
        for(const x of _ok){
          if(x.name==='OKX'||x.name==='BYB'){
            const _offBps=((x.price-_anchorMean)/_anchorMean)*10000;
            const _prev=_brtiOffsetRef.current[x.name];
            // EMA the offset (alpha 0.1) so it adapts slowly, ignores spikes
            _brtiOffsetRef.current[x.name]=_prev==null?_offBps:(_prev*0.9+_offBps*0.1);
          }
        }
      }
      // Apply correction: subtract each hot feed's learned offset
      _ok=_ok.map(x=>{
        if((x.name==='OKX'||x.name==='BYB')&&_brtiOffsetRef.current[x.name]!=null){
          const _corr=x.price/(1+_brtiOffsetRef.current[x.name]/10000);
          return{...x,price:_corr,corrected:true};
        }
        return x;
      });
      // Trimmed mean: drop min and max if ≥3 sources, simple mean if 2, raw if 1
      let _trimmed;
      if(_ok.length>=3){
        const _sorted=[..._ok].sort((a,b)=>a.price-b.price);
        const _middle=_sorted.slice(1,-1); // drop top + bottom
        _trimmed=_middle.reduce((s,x)=>s+x.price,0)/_middle.length;
      }else if(_ok.length===2){
        _trimmed=(_ok[0].price+_ok[1].price)/2;
      }else{
        _trimmed=_ok[0].price; // single source — better than nothing, keeps blend alive
      }
      const _now=Date.now();
      // Add to rolling 60s window
      _brtiSamplesRef.current.push({p:_trimmed,t:_now,sources:_ok.map(x=>x.name)});
      _brtiSamplesRef.current=_brtiSamplesRef.current.filter(s=>_now-s.t<60000);
      // 60s-average matches Kalshi's real BRTI window
      const _samples=_brtiSamplesRef.current;
      if(_samples.length===0)return;
      const _avgPrice=_samples.reduce((s,x)=>s+x.p,0)/_samples.length;
      // Divergence from single-source feed
      const _singlePrice=Number(currentPriceRef.current)||null;
      const _divBps=(_singlePrice&&_avgPrice)?((_singlePrice-_avgPrice)/_avgPrice)*10000:null;
      setBrtiApprox({
        current:Math.round(_avgPrice*100)/100,
        // V10.7.58d: instant uses 5-sample rolling mean instead of single trimmed mean.
        //   CF BRTI publishes every 1s using a partition methodology — a single 3s-old
        //   spot price was too noisy. 5 samples × 1.5s = ~7.5s smoothing, which
        //   better represents the short-term price CF uses for settlement.
        instant:Math.round((_samples.slice(-5).reduce((s,x)=>s+x.p,0)/Math.min(_samples.length,5))*100)/100,
        samples60s:_samples.length,
        sources:_ok.map(x=>x.name),
        sourceCount:_ok.length,
        // V10.9.10: health/telemetry — whether the CF-anchor (CB/KR) was present
        //   this tick, and how many hot feeds were bias-corrected into the blend.
        hasAnchor:_ok.some(x=>x.name==='CB'||x.name==='KR'),
        correctedCount:_ok.filter(x=>x.corrected).length,
        divergenceBps:_divBps!=null?Math.round(_divBps*10)/10:null,
        lastUpdate:_now,
        sourceHealth,status:_ok.length>=2?'live':'partial',benchmark:'cross-exchange-estimate-not-BRTI',
      });
      }finally{busy=false;}
    };
    _poll(); // immediate
    // V10.7.58d: 1.5s poll (was 3s). CF publishes BRTI every second.
    //   40 samples/min gives a much tighter 60s rolling mean.
    const iv=setInterval(_poll,1500); // 40 samples/min
    return()=>{stopped=true;clearInterval(iv);};
  },[currentAsset]);

  // V9.8.4: Stale-tick monitor — runs once per second, computes age of last
  //   price change. UI reads feedStaleSeconds for banner + lock-gate decisions.
  useEffect(()=>{
    const iv=setInterval(()=>{
      const _ageSec=Math.round((Date.now()-lastPriceChangeRef.current.time)/1000);
      setFeedStaleSeconds(_ageSec);
    },1000);
    return()=>clearInterval(iv);
  },[]);

  // BTC has one primary price/analysis owner; advisory mirrors follow readiness.

  // Heavy data — V9.8.6: candles AND order book follow active source
  //   Both endpoints normalize to a common shape via parseCandles/parseBook so the
  //   downstream forEach + band math works identically across all 3 sources.
  useEffect(()=>{const _cfg=ASSET_CONFIG[currentAsset]||ASSET_CONFIG.BTC;const _bookRange=Math.max(150,(targetMargin||0)*0.002);const _src=PRICE_SOURCES[priceSource]?.virtual?PRICE_SOURCES.coinbase:(PRICE_SOURCES[priceSource]||PRICE_SOURCES[PRICE_SOURCE_DEFAULT]);const f=async()=>{try{const gran=windowType==='15m'?900:300;try{const r=await fetch(_src.candleUrl(_cfg,currentAsset,gran),{cache:'no-store'});if(r.ok){const d=await r.json();const _candles=_src.parseCandles(d);if(_candles.length>0)setHistory(_candles.slice(0,100));}}catch(_e){}const r2=await fetch(_src.bookUrl(_cfg,currentAsset),{cache:'no-store'});if(r2.ok){const d2raw=await r2.json();const d2=_src.parseBook(d2raw);if(d2?.bids&&d2?.asks){let lb=0,ls=0;d2.bids.forEach(([p,s])=>{if(p<=targetMargin&&p>=targetMargin-_bookRange)lb+=parseFloat(s);});d2.asks.forEach(([p,s])=>{if(p>=targetMargin&&p<=targetMargin+_bookRange)ls+=parseFloat(s);});
    // V9.1.4: multi-band depth — TIGHT (0.05%), CLOSE (0.1%), STANDARD (0.2%), WIDE (0.5%).
    //   Each band captures bid and ask sizes within that distance from strike. Fed to
    //   DepthStrip for the 4-column breakdown that mirrors TapeStrip's time windows.
    const _strike=Number(targetMargin)||0;
    const _bands={tight:{b:0,a:0},close:{b:0,a:0},std:{b:0,a:0},wide:{b:0,a:0}};
    if(_strike>0){
      const _r05=_strike*0.0005,_r10=_strike*0.001,_r20=_strike*0.002,_r50=_strike*0.005;
      d2.bids.forEach(([p,s])=>{const _p=parseFloat(p),_sz=parseFloat(s);if(!Number.isFinite(_p)||!Number.isFinite(_sz)||_p>_strike)return;const _diff=_strike-_p;if(_diff<=_r05)_bands.tight.b+=_sz*_p;if(_diff<=_r10)_bands.close.b+=_sz*_p;if(_diff<=_r20)_bands.std.b+=_sz*_p;if(_diff<=_r50)_bands.wide.b+=_sz*_p;});
      d2.asks.forEach(([p,s])=>{const _p=parseFloat(p),_sz=parseFloat(s);if(!Number.isFinite(_p)||!Number.isFinite(_sz)||_p<_strike)return;const _diff=_p-_strike;if(_diff<=_r05)_bands.tight.a+=_sz*_p;if(_diff<=_r10)_bands.close.a+=_sz*_p;if(_diff<=_r20)_bands.std.a+=_sz*_p;if(_diff<=_r50)_bands.wide.a+=_sz*_p;});
    }
    setOrderBook({localBuy:lb,localSell:ls,imbalance:ls===0?1:lb/ls,bands:_bands});}}setIsLoading(false);}catch(e){setIsLoading(false);}};f();const iv=setInterval(f,5000);return()=>clearInterval(iv);},[targetMargin,windowType,currentAsset,priceSource]);

  // ── AUTO-STRIKE: Kalshi (15m) / Polymarket (5m) / Coinbase fallback ──
  const[strikeSource,setStrikeSource]=useState('auto');

  // ── AUTO-STRIKE: capture the live spot price at the exact window boundary ──
  // currentPriceRef always holds the freshest price from the Coinbase feed (updated every 1.5s)
  // At the moment a window rolls over, that price IS the opening price of the new window
  // No API calls, no CORS, no parsing — zero latency, always accurate
  const setWindowOpenStrike=(price)=>{
    if(isManualStrikeRef.current)return;
    // V5.5.5 / V6.3.1: Strike comes from Kalshi cache OR stays blank. No live-spot fallback.
    //   User: "if kalshi cached properly from the current window keep trying till it is found.
    //   dont do live websocket. if kalshi keeps failing then be blank".
    //   Step 1: At rollover, immediately set strike from Kalshi cache if available.
    //   Step 2: If no Kalshi cache, set BLANK and let the fast-retry effect below keep
    //           polling Kalshi every 2s until strike lands (or window closes).
    //   Step 3: User can still manually type a strike anytime.
    const _now=Date.now();
    windowHighTimeRef.current=_now;
    windowLowTimeRef.current=_now;
    windowOpenTimeRef.current=_now;
    // V8.5: Reset within-window context tracking
    windowWhaleStatsRef.current={count:0,contraryUsd:0,contraryClockSec:null,contrarySide:null};
    windowMaxGeoRiskRef.current=0;
    windowHadMacroEventRef.current=false;
    // V10.8.2: Reset cushion history for new window
    _windowCushionHistoryRef.current=[];
    _cushionLastPushedRef.current=0;
    _windowTypeSeqRef.current=[]; // V10.9.14: reset window-type transition tracker
    setStrikeMode('manual');
    setPendingStrike(null);
    // Try Kalshi cache first (best source — preset strike from market)
    const _kStrike=kalshiStrikeRef.current;
    const _kStrikeValid=_kStrike!=null&&Number.isFinite(_kStrike)&&_kStrike>0; // V7.0.5: asset-agnostic
    if(_kStrikeValid){
      // V6.3.5: Use exact Kalshi value, never round. Kalshi strikes can be e.g. $80,036.93;
      //   rounding to $80,037 introduces fractional gap distortion that flips marginal trades.
      const p=Number(_kStrike);
      windowOpenPriceRef.current=p;
      windowHighRef.current=p;
      windowLowRef.current=p;
      setTargetMargin(p);
      setStrikeConfirmed(true);
      setStrikeSource('kalshi');
      hasSetInitialMargin.current=true;
      needsCapturedPriceRef.current=false;
      return;
    }
    // No Kalshi yet — leave strike BLANK. The kalshi-retry effect (below) will fast-poll
    //   Kalshi until it lands. windowOpenPriceRef stays at live-spot for scoring fallback
    //   (never displayed), captured by the existing capture effect on next currentPrice tick.
    windowOpenPriceRef.current=0;
    windowHighRef.current=0;
    windowLowRef.current=0;
    setTargetMargin(0);
    setStrikeConfirmed(false);
    setStrikeSource('manual');
    needsCapturedPriceRef.current=true;  // signals next-tick-fill effect to capture windowOpenPriceRef from spot
    hasSetInitialMargin.current=true;
  };

  // Alias so call-sites using the old name still work
  const fetchWindowOpenPrice=useCallback((wType)=>{
    setWindowOpenStrike(currentPriceRef.current);
  },[]);

  // V5.5.5 patch: Strike capture effect — Kalshi-ONLY per user request.
  //   'if this happens keep the strike price blank and dont pull from live, i will
  //    enter myself'. If Kalshi cached → fill. Otherwise leave strike blank for
  //    manual entry. windowOpenPriceRef still gets captured from live spot for
  //    scoring fallback purposes only (so Tara's record updates even with blank strike).
  useEffect(()=>{
    if(!currentPrice)return;
    const _firstTime=!hasSetInitialMargin.current;
    const _needsFill=needsCapturedPriceRef.current;
    if(!_firstTime&&!_needsFill)return;
    // Always capture windowOpenPriceRef from live for scoring-fallback (never displayed).
    const _now=Date.now();
    if(windowOpenPriceRef.current===0){
      windowOpenPriceRef.current=Math.round(currentPrice);
      windowHighRef.current=Math.round(currentPrice);
      windowLowRef.current=Math.round(currentPrice);
      windowHighTimeRef.current=_now;
      windowLowTimeRef.current=_now;
      windowOpenTimeRef.current=_now;
    }
    // Strike-fill: ONLY if Kalshi cached. No live-spot fallback per user spec.
    const _kStrike=kalshiStrikeRef.current;
    const _kStrikeValid=_kStrike!=null&&Number.isFinite(_kStrike)&&_kStrike>0; // V7.0.5: asset-agnostic
    if(_kStrikeValid){
      // V6.3.5: Exact Kalshi value, no rounding
      const p=Number(_kStrike);
      windowOpenPriceRef.current=p;
      windowHighRef.current=p;
      windowLowRef.current=p;
      setTargetMargin(p);
      setStrikeConfirmed(true);
      setStrikeMode('manual');
      setStrikeSource('kalshi');
      hasSetInitialMargin.current=true;
      needsCapturedPriceRef.current=false;
    } else {
      // No Kalshi yet — leave strike blank, but mark as initialized so the page-load
      //   path doesn't fight with the rollover path. Kalshi auto-set effect will fill
      //   when/if data arrives. User can enter manually anytime.
      hasSetInitialMargin.current=true;
      // Keep needsCapturedPriceRef true so we re-check next tick (in case Kalshi arrives)
    }
  },[currentPrice]);

  // V3.1.7: Kalshi strike re-snap. The window-open event fires before Kalshi's API has
  //   returned, so the strike usually gets set to live spot first. When Kalshi data arrives
  // V5.0: Kalshi strike auto-set works on page-open AND new window. Per user:
  //   'when i open the page anytime or when the new window start can it automatically
  //    set itself, i'll still be checking and confirming'. Removed the 60s elapsed
  //   cutoff that was preventing auto-snap on mid-window page opens.
  useEffect(()=>{
    if(!kalshiStrike||!Number.isFinite(Number(kalshiStrike))||Number(kalshiStrike)<=0)return; // V7.0.5: asset-agnostic
    if(isManualStrikeRef.current)return;       // user typed their own strike — respect it
    // V6.3.5: Exact value comparison, no rounding
    if(strikeSource==='kalshi'&&Math.abs((targetMargin||0)-Number(kalshiStrike))<0.01)return; // already set, no change
    const exact=Number(kalshiStrike);
    windowOpenPriceRef.current=exact;
    setTargetMargin(exact);
    setPendingStrike(null);
    setStrikeMode('manual');
    setStrikeSource('kalshi');
    setStrikeConfirmed(true); // V3.2.4: auto-confirm Kalshi strike, no extra tap needed
  },[kalshiStrike,strikeSource,targetMargin]);

  // V6.3.1: KALSHI STRIKE FAST-RETRY. When strike is blank and window has recently opened,
  //   fire Kalshi fetches every 2s until strike lands or 30s elapsed. Replaces V6.3.0's
  //   live-spot fallback. Per user: "if kalshi cached properly from the current window keep
  //   trying till it is found. dont do live websocket. if kalshi keeps failing then be blank".
  //   Stops automatically once strike is set (any source) or window is past 30s into its life.
  useEffect(()=>{
    if(windowType!=='15m')return;            // 5m doesn't use Kalshi
    if(targetMargin>0)return;                 // strike already set — no need to retry
    if(isManualStrikeRef.current)return;     // user is typing manually — don't fight them
    if(!triggerKalshiFetchRef.current)return; // poll effect not yet mounted
    // Only retry during the first ~30s of the window. After that, Kalshi probably never
    //   listed this strike (which is a real signal — keep blank, let user decide).
    const _windowOpen=windowOpenTimeRef.current||0;
    const _msSinceOpen=Date.now()-_windowOpen;
    if(_msSinceOpen>30000)return;
    // Fire immediate retry, then keep retrying every 2s
    triggerKalshiFetchRef.current();
    const iv=setInterval(()=>{
      // Stop if any of the conditions changed mid-flight
      if(targetMargin>0||isManualStrikeRef.current||!triggerKalshiFetchRef.current){clearInterval(iv);return;}
      const _ms=Date.now()-(windowOpenTimeRef.current||0);
      if(_ms>30000){clearInterval(iv);return;}
      triggerKalshiFetchRef.current();
    },2000);
    return()=>clearInterval(iv);
  },[targetMargin,windowType,timeState.nextWindow]);

  const liveHistory=useMemo(()=>{if(history.length===0||!currentPrice)return history;const u=[...history];u[0]={...u[0],c:currentPrice,h:Math.max(u[0].h||currentPrice,currentPrice),l:Math.min(u[0].l||currentPrice,currentPrice)};return u;},[history,currentPrice]);

  // Time
  useEffect(()=>{const u=()=>{const now=new Date();const ms=now.getTime();const iMs=(windowType==='15m'?15:5)*60*1000;const nMs=(Math.floor(ms/iMs)+1)*iMs;const nW=new Date(nMs);const sW=new Date(nMs-iMs);const diff=nW.getTime()-now.getTime();
    // V8.9.3: 3-way time format
    const tz=timeFormat==='utc'?{timeZone:'UTC'}:timeFormat==='est'?{timeZone:'America/New_York'}:undefined;
    let ct,sw,nw;try{ct=now.toLocaleTimeString('en-US',{...tz,hour12:true,hour:'2-digit',minute:'2-digit',second:'2-digit'});sw=sW.toLocaleTimeString('en-US',{...tz,hour12:true,hour:'2-digit',minute:'2-digit'});nw=nW.toLocaleTimeString('en-US',{...tz,hour12:true,hour:'2-digit',minute:'2-digit'});}catch(e){ct=now.toLocaleTimeString('en-US',{hour12:true,hour:'2-digit',minute:'2-digit',second:'2-digit'});sw=sW.toLocaleTimeString('en-US',{hour12:true,hour:'2-digit',minute:'2-digit'});nw=nW.toLocaleTimeString('en-US',{hour12:true,hour:'2-digit',minute:'2-digit'});}setTimeState({currentTime:String(ct),startWindow:String(sw),nextWindow:String(nw),nextWindowMs:nMs,minsRemaining:Math.floor(diff/60000),secsRemaining:Math.floor((diff%60000)/1000),currentHour:now.getHours()});};u();const t=setInterval(u,1000);return()=>clearInterval(t);},[windowType,timeFormat]);
  // V13.4.335: nextWindowMs is the raw epoch-ms window boundary computed just
  //   above, BEFORE it gets locale-formatted into nextWindow/startWindow for
  //   display. timeFormat is a dependency of this very effect (LOCAL/UTC/EST,
  //   the "Time format: click to cycle" control) -- switching it recomputes
  //   nextWindow as a DIFFERENT STRING for the exact same real window (e.g.
  //   "3:00 AM" -> "07:00"). The rollover-detection effect below used to
  //   compare that formatted string directly, so toggling the display format
  //   mid-window looked identical to a real window change -- silently wiping
  //   autoOrderState (and every other per-window ref) out from under a live,
  //   already-filled position with no error, no warning, nothing. Reported
  //   live as auto-exec tracking "going missing suddenly" for no apparent
  //   reason after a real fill. nextWindowMs never changes on a format
  //   switch, only on an actual window boundary, so it's what rollover
  //   detection should have been keyed on from the start.

  // Position status
  // V13.4.351: positionEntry is advisory (and is also used by auto-follow), so a
  // confirmed auto no-fill must suppress it immediately. Otherwise the header can
  // say POSITION UP while reconciliation has already proved Tara:0/Kalshi:0.
  const _confirmedNoFillForPosition=!!(positionReconciliation&&positionReconciliation.status==='drift'
    &&Array.isArray(positionReconciliation.driftDetails)
    &&positionEntry&&positionEntry.source==='auto-follow'
    &&positionReconciliation.driftDetails.some(d=>d&&d.kind==='unknown-resolved-nofill'));
  const positionStatus=useMemo(()=>{
    if(_confirmedNoFillForPosition)return null;
    if(!positionEntry||!currentPrice)return null;
    const{price:entry,side}=positionEntry;
    const pnlPct=side==='UP'?((currentPrice-entry)/entry)*100:((entry-currentPrice)/entry)*100;
    return{entry,side,pnlPct,isStopHit:pnlPct<=-30};
  },[positionEntry,currentPrice,betAmount,_confirmedNoFillForPosition]);

  // V9.1.9: updateScore is a no-op stub. Was incrementing scorecards state directly
  //   but with derive-from-log the log itself drives the count. Legacy callers pass
  //   through harmlessly.
  const updateScore=(type,wl,amt)=>{};

  // v14.7.12: only the durable saved-Call feed may send to Discord.



  const recordPnL=(won,trade)=>{
    const bet=trade?.betAmt||0;
    const pay=trade?.maxPay||0;
    if(bet<=0)return; // no bet amount entered, skip
    const delta=won?(pay-bet):-bet; // WIN: profit = maxPayout-bet; LOSS: lose bet
    setLifetimePnL(prev=>{
      const next=prev+delta;
      const _now=Date.now();
      _pnlUpdatedAtRef.current=_now;
      try{
        localStorage.setItem('taraV110PnL',String(next));
        localStorage.setItem('taraV110PnL_updatedAt',String(_now)); // V7.10.5
      }catch(e){}
      return next;
    });
  };
  // ── KALSHI LIVE MARKET REFERENCE ── polls every 30s for YES price on active BTC market
  // V143: Fetches for both 5m and 15m markets. Previously 15m-only meant 5m windows had
  //       zero Kalshi reference, so the EDGE metric never appeared. Also resets the price to
  //       null on window change so a stale previous-strike price doesn't get displayed.
  // V3.0: Extracts Kalshi's published strike and the active market ticker.
  // V3.1.2: Strike extraction made strike-aware. Kalshi BTC markets are published as a
  //         SERIES of strikes per close_time (20+ markets for each window). Now filter by
  //         close_time first, then within that find market closest to current spot.
  // V3.1.7: More robust extraction. Tries more field names. Exposes debug state to
  //         window.__taraKalshiDebug so the user can inspect what Kalshi returned. Also
  //         logs to engine reasoning so failures show up in the engine log.
  useEffect(()=>{
    setKalshiYesPrice(null); // reset on window change so we don't show stale prior-strike price
    setKalshiStrike(null);
    setKalshiActiveMarket(null);
    // V4.2: Kalshi fetch ONLY on 15m windows. Per user request — 5m markets either don't
    //   exist on Kalshi as KXBTC5M or they're returning errors that pollute the diagnostic.
    //   15m windows are what we trade primarily; 5m falls back to live spot.
    if(windowType!=='15m'){
      setKalshiDebug({ok:null,reason:'5m window — Kalshi disabled (15m only)',totalMarkets:0,matchingClose:0,bestStrike:null,sampleFields:[]});
      return;
    }
    // V3.2.2: Helper to retry the Kalshi fetch up to 3 times with backoff on 503/transient errors
    // V3.2.4: Removed the `Accept: application/json` header. That header is "non-simple" per
    //   CORS spec and forces a preflight OPTIONS request before the GET. Kalshi's CORS handling
    //   for OPTIONS may return 503 even when the GET would succeed — and the browser surfaces
    //   the 503 from OPTIONS as if the GET failed. Without that header, the browser does a
    //   simple GET (no preflight) and the request goes through cleanly.
    const fetchWithRetry=async(url,attempts=3,validate=null)=>{
      let lastError=null;
      for(let i=0;i<attempts;i++){
        try{
          // V7.10.7: bumped timeout 3500→5000ms. Free CORS proxies under load occasionally
          //   need 4+ seconds to respond. 3.5s was rejecting working-but-slow paths.
          const r=await fetch(url,{signal:AbortSignal.timeout(5000)});
          // 503 = Service Unavailable, 502 = Bad Gateway, 504 = Gateway Timeout, 429 = Rate Limited.
          // All transient — retry. Other 4xx/5xx are not retried.
          if(r.status===503||r.status===502||r.status===504||r.status===429){
            lastError={status:r.status,reason:`HTTP ${r.status}`};
            if(i<attempts-1){
              const delay=1000*Math.pow(2,i); // 1s, 2s, 4s
              await new Promise(res=>setTimeout(res,delay));
              continue;
            }
            return{ok:false,...lastError};
          }
          if(!r.ok){
            return{ok:false,status:r.status,reason:`HTTP ${r.status}`};
          }
          // V13.4.204: read as TEXT first. r.json() on an HTML error page throws
          //   "Unexpected token '<'", which the catch below reported as a generic
          //   "fetch error" -- indistinguishable from a dead network, so a proxy
          //   quietly serving captcha/error HTML kept getting raced forever.
          //   Naming it makes the health tracker able to retire that proxy.
          const _txt=await r.text();
          const _head=(_txt||'').trimStart().slice(0,1);
          if(!_txt||_head==='<'){
            return{ok:false,status:r.status,reason:'proxy returned HTML, not JSON'};
          }
          let data;
          try{data=JSON.parse(_txt);}
          catch(_pe){return{ok:false,status:r.status,reason:'invalid JSON from proxy'};}
          // V13.4.204: a 200 carrying {} or an error object used to WIN the race
          //   (Promise.any resolves on first success), so a junk-but-parseable
          //   proxy could beat the good first-party paths and poison the feed.
          if(validate&&!validate(data)){
            return{ok:false,status:r.status,reason:'payload failed shape check'};
          }
          return{ok:true,data};
        }catch(e){
          lastError={status:0,reason:`fetch error: ${(e.message||String(e)).slice(0,60)}`};
          if(i<attempts-1){
            const delay=1000*Math.pow(2,i);
            await new Promise(res=>setTimeout(res,delay));
            continue;
          }
        }
      }
      return{ok:false,...(lastError||{status:0,reason:'unknown error'})};
    };
    const fetchKalshi=async()=>{
      try{
        const now=Date.now();
        const intervalMin=windowType==='5m'?5:15;
        const iMs=intervalMin*60*1000;
        const nextMs=Math.ceil((now+500)/iMs)*iMs;
        // V3.2.2: Use correct series. Kalshi has multiple BTC series:
        //   KXBTC15M  — 15-minute BTC up/down markets (the ones we trade)
        //   KXBTCD    — 1-hour BTC range markets
        //   KXBTC     — longer-term "BTC above $X by date" markets (NOT what we want)
        // V3.2.3: Direct requests from this origin keep getting 503'd by Kalshi.
        //   Authenticated/origin-allowlisted clients get through, but unauthenticated browser
        //   requests are throttled. Route through corsproxy.io which caches responses (~10s)
        //   and uses different IPs — this is a known pattern for indie traders. Fall back to
        //   direct fetch if proxy itself fails.
        // V4.4 KALSHI BREAKTHROUGH: Switch from /markets to /events?with_nested_markets=true.
        //   Per docs review: /events scoped by min_close_ts returns just events closing soon,
        //   not all 200 markets across the series. Different rate-limit bucket. Smaller payload.
        //   Markets are nested by event — each 15m event has 2 markets (greater/less strike).
        //   Field names also migrated: yes_bid_dollars (string like "0.5600"), floor_strike
        //   (number, the actual strike value), cap_strike. Old yes_ask/yes_bid/last_price are
        //   deprecated. floor_strike IS the strike — no ticker regex needed.
        // V6.5.8: per-asset series ticker — KXBTC15M / KXETH15M / KXSOL15M / KXDOGE15M
        const _assetCfg=ASSET_CONFIG[currentAssetRef.current]||ASSET_CONFIG.BTC;
        const _seriesTicker=`${_assetCfg.kalshiSeriesTicker}15M`;
        // min_close_ts: events with at least one market closing after (now - 5min). Captures
        //   the active window plus a buffer for clock skew between us and Kalshi.
        const _minCloseTs=Math.floor(Date.now()/1000)-300;
        const _eventsUrl=`https://api.elections.kalshi.com/trade-api/v2/events?series_ticker=${_seriesTicker}&with_nested_markets=true&status=open&limit=50&min_close_ts=${_minCloseTs}`;
        // V7.10.7: build same URL with relative path so Vercel rewrite (vercel.json) proxies
        //   through deployed origin's edge — no CORS, first-party, fastest reliable path on
        //   production. On local dev (vite without vercel dev) this 404s harmlessly and the
        //   other proxies still race in parallel via Promise.any.
        const _vercelUrl=`/api/kalshi/events?series_ticker=${_seriesTicker}&with_nested_markets=true&status=open&limit=50&min_close_ts=${_minCloseTs}`;
        // V5.2: PARALLEL multi-proxy strategy — fire all paths at once, take fastest success.
        //   User feedback: 'fetches but it takes a min ish to get it. can it be instant.'
        //   Sequential fetch was up to 18s (6s × 3 timeouts) when first paths fail. Parallel
        //   gets us the strike from whichever proxy responds first — typically 1-3s.
        // V7.10.7: 6 parallel racers now (added vercel rewrite path). _via tag shows which won.
        // V13.4.204: an events payload must actually carry an events array.
        //   allorigins wraps the body in .contents, so it validates separately.
        const _okEvents=(d)=>!!(d&&Array.isArray(d.events));
        // V13.4.204: PROXY HEALTH. A path that has failed 3+ times in a row is
        //   benched for 90s instead of being raced on every poll. Re-probed after
        //   that, so an outage is temporary and never permanent. Keeps a dead
        //   proxy from contributing latency and log noise indefinitely.
        const _H=_KFEED_HEALTH;
        const _benched=(name)=>{
          const h=_H[name];
          return !!(h&&h.fails>=3&&(Date.now()-h.at)<90000);
        };
        const _mark=(name,ok,reason)=>{
          const h=_H[name]||(_H[name]={fails:0,at:0,last:null});
          if(ok){h.fails=0;h.last='ok';}
          else{h.fails++;h.at=Date.now();h.last=reason||'fail';}
        };
        // V13.4.237: every racer promise is created eagerly, but the second tier is
        //   only awaited when the FIRST tier fails. On the normal path -- first-party
        //   wins -- the four CORS-proxy promises reject with nobody listening, and
        //   each one becomes an unhandled rejection. Measured on a single page load:
        //   18 of them, all from corsproxy / allorigins / codetabs / thingproxy,
        //   repeating on every feed poll.
        //
        //   Functionally harmless (the tiering works, that is the point of racing)
        //   but it fires window.onunhandledrejection continuously and buries real
        //   errors in the console -- which is how the genuinely broken things in this
        //   file have stayed hidden before.
        //
        //   Attaching a no-op handler marks the rejection as handled WITHOUT
        //   consuming it: Promise.any still sees the original promise reject, and the
        //   tiering behaves exactly as before.
        const _racer=(name,url,validate,post)=>{
          const p=_benched(name)
            ?Promise.reject(new Error(name+' benched ('+(_H[name]?.last||'fail')+')'))
            :fetchWithRetry(url,1,validate).then(r=>{
              const out=post?post(r):r;
              if(out&&out.ok){_mark(name,true);return{...out,_via:name};}
              _mark(name,false,(out&&out.reason)||r.reason);
              throw new Error((out&&out.reason)||r.reason||name+' fail');
            }).catch(e=>{_mark(name,false,e&&e.message);throw e;});
          p.catch(()=>{});
          return p;
        };
        // Only first-party exchange paths: no untrusted CORS relays or eager seven-way fan-out.
        const _publicUrl=`/api/kalshi-public/events?series_ticker=${_seriesTicker}&with_nested_markets=true&status=open&limit=50&min_close_ts=${_minCloseTs}`;
        let result;
        try{result=await _racer('kalshi-public',_publicUrl,_okEvents);}
        catch(primaryError){
          try{result=await _racer('kalshi-direct-proxy',_vercelUrl,_okEvents);}
          catch(fallbackError){result={ok:false,reason:`Exchange market data unavailable: ${fallbackError.message}`.slice(0,180)};}
        }
        if(!result.ok){
          // V5.5.5 patch: Cache TTL extended to 15min (900s) since the strike for a given
          //   Kalshi event doesn't change once the event opens — using a 15-min-old strike
          //   value for the same window is safe.
          // V7.0.3: read per-asset cache slot. The fallback was BTC-only before, so
          //   SOL/DOGE failures showed BTC strikes (the 42115 bug).
          const _curAsset=currentAssetRef.current||'BTC';
          const cache=(lastKalshiSuccessRef.current||{})[_curAsset]||{at:0,strike:null,yesPrice:null,activeMarket:null};
          const cacheAgeS=(Date.now()-cache.at)/1000;
          if(cache.at>0&&cacheAgeS<=900&&cache.strike!=null){
            // Keep the cached strike/yes price visible — they don't change second-to-second
            setKalshiStrike(cache.strike);
            if(cache.yesPrice!=null)setKalshiYesPrice(cache.yesPrice);
            if(cache.activeMarket)setKalshiActiveMarket(cache.activeMarket);
            setKalshiDebug({ok:false,reason:`${result.reason} · using cached ${_curAsset} strike (${cacheAgeS.toFixed(0)}s old)`,totalMarkets:0,matchingClose:0,bestStrike:cache.strike,sampleFields:[]});
          } else {
            // No usable cache — user falls back to live spot
            if(typeof window!=='undefined')window.__taraKalshiDebug={ok:false,via:'failed',...result};/*V13.4.63: stamp via so feedVia shows fetch-failure instead of going silently null*/
            setKalshiDebug({ok:false,reason:result.reason,totalMarkets:0,matchingClose:0,bestStrike:null,sampleFields:[]});
          }
          return;
        }
        const d=result.data;
        // /events response shape: {events: [{event_ticker, markets: [{...}], ...}]}
        const events=d.events||[];
        // Flatten nested markets, tagging each with its parent event_ticker for matching
        const markets=[];
        for(const ev of events){
          const evMarkets=ev.markets||[];
          for(const m of evMarkets){
            markets.push({...m, _event_ticker:ev.event_ticker});
          }
        }
        // V4.4: New extractStrike — floor_strike and cap_strike are now numbers per Kalshi's
        //   migrated schema. Fall through to legacy fields + ticker regex if those are absent
        //   (e.g. old cached responses or other series).
        // V7.0.7: ASSET-AGNOSTIC. Previous version had n>1000&&n<10000000 BTC-range filters
        //   in the candidate loop AND in every regex fallback — rejected ALL SOL ($84) and
        //   DOGE ($0.45) strikes outright, even when Kalshi returned them correctly in
        //   floor_strike. Now just checks finite + positive. Regex patterns accept any decimal.
        const _extractStrike=(m)=>{
          // Direct numeric fields, in order of likelihood for v2 schema
          const candidates=[
            m.floor_strike,m.cap_strike,m.strike_price,m.strike,
            m.expected_expiration_value,m.expiration_value,m.settlement_value,
          ];
          for(const c of candidates){
            if(c==null)continue;
            const n=Number(c);
            if(isFinite(n)&&n>0)return n;
          }
          // Ticker pattern matching — fallback for legacy schema or unusual events
          // V7.0.7: patterns now match any positive number including sub-dollar (DOGE 0.45)
          //   and 2-digit dollar (SOL 84.33), not just 4-7 digit BTC values.
          if(m.ticker){
            const t1=m.ticker.match(/-T(\d+(?:\.\d+)?)/);
            if(t1){const n=Number(t1[1]);if(isFinite(n)&&n>0)return n;}
            const t2=m.ticker.match(/-(\d+(?:\.\d+)?)$/);
            if(t2){const n=Number(t2[1]);if(isFinite(n)&&n>0)return n;}
          }
          // Subtitle text fallback. V7.0.7: accept any positive float.
          const titleText=(m.subtitle||m.title||m.yes_sub_title||m.no_sub_title||m.event_ticker||m._event_ticker||'')+' ';
          const tm=titleText.match(/\$?([\d,]+(?:\.\d+)?)/g);
          if(tm){
            for(const match of tm){
              const n=Number(match.replace(/[$,]/g,''));
              if(isFinite(n)&&n>0)return n;
            }
          }
          return null;
        };
        // Filter to markets whose close_time matches our active window
        const tolMs=Math.floor(intervalMin*60*1000/2);
        const matchingClose=markets.filter(m=>{
          const closeMs=m.close_time?new Date(m.close_time).getTime():0;
          return closeMs && Math.abs(closeMs-nextMs)<tolMs;
        });
        if(typeof window!=='undefined'){
          // Expose debug state for inspection in DevTools
          window.__taraKalshiDebug={
            ok:true,
            endpoint:'events',
            via:result._via||null, // V7.10.7: which proxy delivered the data
            totalEvents:events.length,
            totalMarkets:markets.length,
            matchingClose:matchingClose.length,
            sampleFields:markets[0]?Object.keys(markets[0]):[],
            sampleTicker:markets[0]?.ticker||null,
            sampleEvent:markets[0]?._event_ticker||null,
            sampleStrike:markets[0]?.floor_strike||markets[0]?.cap_strike||null,
            sampleYesBid:markets[0]?.yes_bid_dollars||null,
          };
        }
        if(matchingClose.length===0){
          setKalshiDebug({ok:true,reason:`${events.length} events · ${markets.length} markets · 0 matching close_time`,totalMarkets:markets.length,matchingClose:0,bestStrike:null,sampleFields:markets[0]?Object.keys(markets[0]).slice(0,8):[]});
          return;
        }
        // V4.4: For 15m KXBTC events, each event has 2 markets — strike_type='greater' (above)
        //   and strike_type='less' (below) — bracketed by floor_strike/cap_strike. We want the
        //   "greater" market (will price be ABOVE strike) since that's the up/down direction
        //   we trade. If no strike_type field, fall through to closest-strike-to-spot logic.
        const _curPrice=currentPriceRef.current||0;
        let best=null,bestStrikeDiff=Infinity,bestStrike=null;
        // Prefer markets matching strike_type='greater' for our UP/DOWN semantics
        const greaterMarkets=matchingClose.filter(m=>m.strike_type==='greater'||m.strike_type==='greater_or_equal');
        const candidatePool=greaterMarkets.length>0?greaterMarkets:matchingClose;
        for(const m of candidatePool){
          const _s=_extractStrike(m);
          // V7.0.5: removed BTC-specific range guard (_s<1000||_s>10000000). Was rejecting
          //   ALL valid SOL/DOGE strikes ($200 and $0.45 ranges) — which is why DOGE/SOL
          //   strike picking fell through to the blind candidatePool[0] fallback below
          //   (often picking a wrong-side market). Just skip null/non-finite/zero.
          if(_s==null||!Number.isFinite(_s)||_s<=0)continue;
          if(_curPrice>0){
            const diff=Math.abs(_s-_curPrice);
            if(diff<bestStrikeDiff){bestStrikeDiff=diff;best=m;bestStrike=_s;}
          } else if(best===null){
            best=m; bestStrike=_s;
          }
        }
        if(!best){
          best=candidatePool[0];
          bestStrike=_extractStrike(best);
        }
        if(typeof window!=='undefined'){
          window.__taraKalshiDebug.bestStrike=bestStrike;
          window.__taraKalshiDebug.bestTicker=best?.ticker;
          window.__taraKalshiDebug.bestStrikeType=best?.strike_type;
        }
        // V3.1.11: Mirror to React state for UI surface
        setKalshiDebug({
          ok:true,
          reason:bestStrike?`/events ok via ${result._via||'unknown'} · ${events.length} events · matched ${best?.ticker||'unknown'} (${bestStrike})`:`${matchingClose.length} matching markets but no strike extractable`,
          totalMarkets:markets.length,
          matchingClose:matchingClose.length,
          bestStrike,
          bestTicker:best?.ticker||null,
          via:result._via||null, // V7.10.7: which proxy delivered the data
          sampleFields:markets[0]?Object.keys(markets[0]).slice(0,8):[],
        });
        // V4.4: New YES price extraction. Kalshi migrated from yes_ask (cents integer) to
        //   yes_ask_dollars (dollars string like "0.5600"). Convert to cents for UI consistency
        //   since the rest of Tara's code expects cents (0-100 range).
        const yesAskDollars=best.yes_ask_dollars??null;
        const yesBidDollars=best.yes_bid_dollars??null;
        const lastPriceDollars=best.last_price_dollars??null;
        // Prefer mid of bid+ask if both present, else fall back individually, else last price
        let yesCents=null;
        if(yesAskDollars!=null&&yesBidDollars!=null){
          const mid=(parseFloat(yesAskDollars)+parseFloat(yesBidDollars))/2;
          if(isFinite(mid))yesCents=Math.round(mid*100);
        } else if(yesAskDollars!=null){
          const v=parseFloat(yesAskDollars);
          if(isFinite(v))yesCents=Math.round(v*100);
        } else if(yesBidDollars!=null){
          const v=parseFloat(yesBidDollars);
          if(isFinite(v))yesCents=Math.round(v*100);
        } else if(lastPriceDollars!=null){
          const v=parseFloat(lastPriceDollars);
          if(isFinite(v))yesCents=Math.round(v*100);
        } else {
          // Final fallback to legacy fields
          const yes=best.yes_ask??best.yes_bid??best.last_price??null;
          if(yes!=null)yesCents=Number(yes);
        }
        // V13.4.328: TRUST GUARD MOVED EARLIER + EXTENDED TO GATE THE TICKER,
        //   NOT JUST THE DISPLAYED PRICE. This check (V13.4.59/62) used to run
        //   AFTER _kalshiQuote -- which carries .ticker, the value every real
        //   order AND the position-reconciliation check use to identify the
        //   traded contract -- was already unconditionally assigned from
        //   `best`, and setKalshiStrike/setKalshiActiveMarket just below were
        //   unconditional too. So an untrusted pick (wrong-window, off-strike,
        //   thin-book poison) could still overwrite the ACTIVE TICKER even
        //   while its price was correctly rejected -- a wrong ticker is worse
        //   than a wrong price: it means the order, and every later
        //   reconciliation check, targets a different contract than intended.
        //   Reported live: Tara's displayed strike ($78,318.76) didn't match
        //   Kalshi's own site for the same window ($78,366.94), and a real
        //   fill on Kalshi went completely untracked because of it -- the
        //   position-reconciliation self-heal (V13.4.322) even told the user
        //   "confirmed no fill, safe to ignore" on a position Kalshi's own
        //   site showed as real, filled, and cashable. The trust check now
        //   runs FIRST and gates _kalshiQuote/setKalshiStrike/
        //   setKalshiActiveMarket together with the price -- an untrusted
        //   pick changes nothing, keeping the last trusted ticker/strike/
        //   quote exactly like the price already did.
        let _kalshiPriceTrusted=false;
        // V13.4.337: hoisted out of the `if(_kalshiPriceTrusted)` block below --
        //   it used to be `const`-declared THERE (block-scoped) while the cache
        //   write further down (a separate, sibling `if(bestStrike>0)` block)
        //   read it unconditionally. Every successful fetch with a valid strike
        //   threw "_activeMarket is not defined", landing in the outer catch and
        //   stamping window.__taraKalshiDebug as {ok:false,via:'error',...} --
        //   masking a real success as a hard failure, and (worse) preventing
        //   lastKalshiSuccessRef from ever caching a strike, so the ONE fallback
        //   this function has for a transient proxy hiccup (up to 15min of
        //   cached strike) never had anything in it. Confirmed live via
        //   window.__taraKalshiDebug on the user's own device.
        let _activeMarket=null;
        {
          const _spotNow=currentPriceRef.current||0;
          const _strikeOffPct=(_spotNow>0&&bestStrike>0)?Math.abs(bestStrike-_spotNow)/_spotNow:1;
          const _secsLeft=(nextMs-Date.now())/1000;
          const _priceExtreme=yesCents!=null&&(yesCents<=5||yesCents>=95);
          // V13.4.62: reject (a) non-at-the-money picks (strike >1% off spot), and (b) prices that
          //   are implausibly extreme with real time left -- an at-the-money 15m market cannot sit
          //   at <=5 / >=95c with >3min to go (thin-book or stale wrong-window quote). Inside the
          //   last 3min genuine extremes are allowed. Last trusted price is kept on reject.
          const _strikeOK=_spotNow>0&&bestStrike>0&&_strikeOffPct<=0.01;
          const _extremeTooEarly=_priceExtreme&&_secsLeft>180;
          const _trustPrice=_strikeOK&&!_extremeTooEarly;
          _kalshiPriceTrusted=_trustPrice;
          if(typeof window!=='undefined'&&window.__taraKalshiDebug){
            window.__taraKalshiDebug.priceAccepted=_trustPrice;
            window.__taraKalshiDebug.rejectedPrice=_trustPrice?null:yesCents;
            window.__taraKalshiDebug.rejectReason=_trustPrice?null:(!_strikeOK?('strike-off-'+(Math.round(_strikeOffPct*1000)/10)+'pct'):'extreme-early');
          }
          if(yesCents!=null&&isFinite(yesCents)&&_trustPrice){
            setKalshiYesPrice(yesCents);
          }
        }
        if(_kalshiPriceTrusted){
          // V13.4.142: record the raw bid/ask behind that mid, so executable entry cost
          //   (ask for YES, 100-bid for NO) is knowable at lock time. See _kalshiQuote.
          //   Legacy integer-cent fields are accepted as a fallback for older payloads.
          try{
            const _bidC=yesBidDollars!=null?Math.round(parseFloat(yesBidDollars)*100)
                       :(best.yes_bid!=null?Number(best.yes_bid):null);
            const _askC=yesAskDollars!=null?Math.round(parseFloat(yesAskDollars)*100)
                       :(best.yes_ask!=null?Number(best.yes_ask):null);
            const _bidOK=_bidC!=null&&isFinite(_bidC)&&_bidC>=0&&_bidC<=100;
            const _askOK=_askC!=null&&isFinite(_askC)&&_askC>=0&&_askC<=100;
            _kalshiQuote={
              bid:_bidOK?_bidC:null,
              ask:_askOK?_askC:null,
              mid:(yesCents!=null&&isFinite(yesCents))?yesCents:null,
              spread:(_bidOK&&_askOK)?(_askC-_bidC):null,
              at:Date.now(),
              ticker:best.ticker||null,
              closeTime:best.close_time||null,
              strike:bestStrike,
            };
          }catch(_e){/* quote capture must never break the price feed */}
          if(bestStrike!=null&&bestStrike>1000&&bestStrike<10000000){
            setKalshiStrike(bestStrike);
          }
          _activeMarket=best.ticker?{ticker:best.ticker,closeTime:best.close_time,strike:bestStrike,strikeType:best.strike_type,event:best._event_ticker}:null;
          if(_activeMarket)setKalshiActiveMarket(_activeMarket);
        }
        // V7.0.3: per-asset cache. Removed the bestStrike>1000 guard which was BTC-only —
        //   SOL ($200) and DOGE ($0.45) strikes were being rejected entirely from cache.
        if(bestStrike!=null&&bestStrike>0){
          const _curAssetW=currentAssetRef.current||'BTC';
          if(!lastKalshiSuccessRef.current)lastKalshiSuccessRef.current={};
          lastKalshiSuccessRef.current[_curAssetW]={
            at:Date.now(),
            strike:bestStrike,
            yesPrice:(yesCents!=null&&_kalshiPriceTrusted)?yesCents:(lastKalshiSuccessRef.current[_curAssetW]?.yesPrice??null),/*V13.4.63: never cache a guard-rejected price; keep last trusted so fetch-failure fallback cannot re-inject poison*/
            activeMarket:_activeMarket,
            debug:null,
          };
        }
      }catch(e){
        if(typeof window!=='undefined')window.__taraKalshiDebug={ok:false,via:'error',error:e.message||String(e)};
        setKalshiDebug({ok:false,reason:`error: ${e.message||String(e).slice(0,40)}`,totalMarkets:0,matchingClose:0,bestStrike:null,sampleFields:[]});
      }
    };
    fetchKalshi();
    // V6.3.1: Expose fetchKalshi via ref so external code (window rollover) can trigger refetches
    triggerKalshiFetchRef.current=fetchKalshi;
    // V11.2.6 - poll 15s (was 30s). kalshiAtLock is stamped from this feed; at 30s the
    //   lock price could be a full window-move stale, which fed wrong-side fights. 15s
    //   halves staleness. This is a Vercel-proxy/Kalshi REST call (small JSON), NOT a
    //   Supabase read, so it does not touch the 5GB Supabase egress cap. True per-second
    //   live still needs the server-side authed WS proxy (separate build).
    // V13.4.98: BACKGROUND-TAB BILLING FIX. The V13.4.46-49 wake-lock + silent-audio
    //   keep-alive was built specifically to defeat Chrome's background-tab throttle so
    //   Tara would not miss windows while minimized -- it worked, backgrounded sit-outs
    //   fell from 63%%. But that means EVERY proxied poll, including this 8s Kalshi fetch,
    //   keeps firing at full speed in a minimized or forgotten tab, with no cost signal
    //   until the Vercel account gets paused. Observed: 3M edge requests against a 1M
    //   free cap -- 3x over, code-level estimate was only ~5-10%% of that. The keep-alive
    //   mechanism is NOT touched; a hidden tab still gets fresh prices, just less often.
    //   Foreground stays 8s. Hidden backs off to HIDDEN_POLL_MS (default 30s -- still far
    //   under the 900s window, so lock pricing freshness is not meaningfully worse) and
    //   snaps back to 8s the instant the tab regains focus (the existing onVisible refetch
    //   below already forces one fetch on that transition). Dial: localStorage
    //   'taraHiddenPollMs' (0 disables backoff = restores exact old behavior).
    const HIDDEN_POLL_MS=(function(){try{const v=parseInt(localStorage.getItem('taraHiddenPollMs'),10);return(Number.isFinite(v)&&v>=0)?v:30000;}catch(_e){return 30000;}})();
    let ivMs=8000,iv=setInterval(fetchKalshi,ivMs);
    const _rearm=()=>{
      const want=(HIDDEN_POLL_MS>0&&document.visibilityState==='hidden')?HIDDEN_POLL_MS:8000;
      if(want!==ivMs){clearInterval(iv);ivMs=want;iv=setInterval(fetchKalshi,ivMs);}
    };
    document.addEventListener('visibilitychange',_rearm);
    // V13.4.98: lightweight usage counter so proxy load is visible in devtools instead
    //   of silent until the account gets paused. Read any time via localStorage.
    try{
      const _origFetch=window.fetch;
      if(!window.__taraProxyCountInstalled){
        window.__taraProxyCountInstalled=true;
        window.fetch=function(u,...rest){
          try{
            const s=typeof u==='string'?u:(u&&u.url)||'';
            if(s.startsWith('/api/')){
              const key='taraProxyReqCount_'+new Date().toISOString().slice(0,10);
              const n=(parseInt(localStorage.getItem(key)||'0',10)||0)+1;
              localStorage.setItem(key,String(n));
            }
          }catch(_e){}
          return _origFetch.apply(this,[u,...rest]);
        };
      }
    }catch(_e){}
    // V5.0: Refetch when tab becomes visible again — background tabs can stall the interval
    const onVisible=()=>{if(document.visibilityState==='visible')fetchKalshi();};
    document.addEventListener('visibilitychange',onVisible);
    return()=>{clearInterval(iv);document.removeEventListener('visibilitychange',onVisible);triggerKalshiFetchRef.current=null;};
  },[windowType,timeState.nextWindow,currentAsset]);

  // V10.2.28 — WEBSOCKET PRICE FEED EFFECT
  //   Opens a WebSocket to Kalshi's public ticker_v2 channel when:
  //     - kalshiWsEnabled is true (user opted in)
  //     - kalshiActiveMarket.ticker is known (we have a market to subscribe to)
  //   Updates kalshiYesPrice on each ticker message (~once per market change,
  //   often 100ms-1s cadence on liquid markets).
  //
  //   Auto-reconnect with exponential backoff (1s, 2s, 4s, 8s, max 60s).
  //   If WS fails entirely, polling continues — they're additive.
  //   Closes cleanly on unmount or market change.
  useEffect(()=>{
    if(!kalshiWsEnabled)return;
    if(!kalshiActiveMarket?.ticker)return;
    let _ws=null;
    let _reconnectTimer=null;
    let _reconnectDelay=1000;
    let _wsAttempts=0; // V10.7.63: track attempts in closure (not on timer number)
    let _stopped=false;
    let _messageCount=0;
    const _connect=()=>{
      if(_stopped)return;
      try{
        // Kalshi public WebSocket — no auth needed for market data channels
        _ws=new WebSocket('wss://api.elections.kalshi.com/trade-api/ws/v2');
        setKalshiWsState(prev=>({...prev,status:'connecting'}));
        _ws.onopen=()=>{
          if(_stopped){try{_ws.close();}catch(_){};return;}
          try{
            _ws.send(JSON.stringify({
              id:Date.now()&0xffffff,
              cmd:'subscribe',
              params:{
                channels:['ticker_v2'],
                market_tickers:[kalshiActiveMarket.ticker],
              },
            }));
            _reconnectDelay=1000; // reset on successful open
            setKalshiWsState(prev=>({...prev,status:'connected',reconnectAttempts:0}));
            try{console.info('[V10.2.28] Kalshi WS connected, subscribed to',kalshiActiveMarket.ticker);}catch(_){}
          }catch(e){
            try{console.warn('[V10.2.28] Kalshi WS subscribe failed:',e?.message);}catch(_){}
          }
        };
        _ws.onmessage=(evt)=>{
          if(_stopped)return;
          try{
            const msg=JSON.parse(evt.data);
            // Ticker messages have: { type: 'ticker_v2', msg: { market_ticker, yes_bid, yes_ask, no_bid, no_ask, ts } }
            if(msg?.type==='ticker_v2'&&msg.msg&&msg.msg.market_ticker===kalshiActiveMarket.ticker){
              const _yesBid=Number(msg.msg.yes_bid);
              // yes_bid is what someone is willing to buy YES at = our sell price for YES
              // (this is the "current value if I exit now" — exactly what stop/target should react to)
              if(Number.isFinite(_yesBid)&&_yesBid>=0&&_yesBid<=100){
                setKalshiYesPrice(_yesBid);
                _messageCount++;
                // Throttled state update — only every 10 messages to avoid React thrashing
                if(_messageCount%10===0){
                  setKalshiWsState(prev=>({...prev,lastUpdate:Date.now(),messageCount:_messageCount}));
                }
              }
            }
          }catch(_){/* malformed message — ignore */}
        };
        _ws.onerror=(e)=>{
          try{console.warn('[V10.2.28] Kalshi WS error:',e?.message||'unknown');}catch(_){}
          setKalshiWsState(prev=>({...prev,status:'error'}));
        };
        _ws.onclose=(e)=>{
          if(_stopped)return;
          // V10.2.30 — give up after 5 attempts. Kalshi WS requires auth that
          //   we can't provide from a browser. Endless reconnects spam console
          //   and waste battery. After 5 failures, stop trying — user can
          //   re-enable explicitly if conditions change.
          // V10.7.63: fixed TypeError — setTimeout() returns a number in browsers,
          //   not an object. Storing ._attempts on a number threw TypeError on every
          //   reconnect. Now tracked in _wsAttempts closure variable instead.
          _wsAttempts=(_wsAttempts||0)+1;
          if(_wsAttempts>=5){
            try{console.warn(`[V10.2.30] Kalshi WS gave up after ${_wsAttempts} attempts. Likely needs server-side proxy for auth. Disabling.`);}catch(_){}
            setKalshiWsState(prev=>({...prev,status:'gave-up',reconnectAttempts:_wsAttempts}));
            try{localStorage.setItem('taraKalshiWsEnabled','0');}catch(_){}
            return;
          }
          try{console.info(`[V10.2.28] Kalshi WS closed (code=${e?.code}) — reconnecting in ${_reconnectDelay/1000}s (attempt ${_wsAttempts}/5)`);}catch(_){}
          setKalshiWsState(prev=>({...prev,status:'reconnecting',reconnectAttempts:_wsAttempts}));
          _reconnectTimer=setTimeout(()=>{
            _reconnectDelay=Math.min(60000,_reconnectDelay*2);
            _connect();
          },_reconnectDelay);
        };
      }catch(e){
        try{console.warn('[V10.2.28] Kalshi WS init failed:',e?.message);}catch(_){}
        setKalshiWsState(prev=>({...prev,status:'failed'}));
      }
    };
    _connect();
    return()=>{
      _stopped=true;
      if(_reconnectTimer)clearTimeout(_reconnectTimer);
      if(_ws){try{_ws.close();}catch(_){}}
      setKalshiWsState({status:'disconnected',lastUpdate:null,messageCount:0,reconnectAttempts:0});
    };
  },[kalshiWsEnabled,kalshiActiveMarket?.ticker]);

  // V10.2.28 — Console hook to toggle WebSocket without UI
  useEffect(()=>{
    if(typeof window==='undefined')return;
    window.__taraWebSocket=(action)=>{
      if(action==='on'){
        setKalshiWsEnabled(true);
        try{localStorage.setItem('taraKalshiWsEnabled','1');}catch(_){}
        console.info('[V10.2.28] Kalshi WebSocket ENABLED — sub-second price feed active');
        return{status:'on'};
      }
      if(action==='off'){
        setKalshiWsEnabled(false);
        try{localStorage.setItem('taraKalshiWsEnabled','0');}catch(_){}
        console.info('[V10.2.28] Kalshi WebSocket DISABLED — back to 30s polling only');
        return{status:'off'};
      }
      console.group('%c━━━ Kalshi WebSocket State ━━━','color:#23B981;font-weight:bold');
      console.info('Enabled: '+(kalshiWsEnabled?'YES':'NO'));
      console.info('Status: '+(kalshiWsState?.status||'unknown'));
      console.info('Message count: '+(kalshiWsState?.messageCount||0));
      console.info('Reconnect attempts: '+(kalshiWsState?.reconnectAttempts||0));
      console.info('Last update: '+(kalshiWsState?.lastUpdate?new Date(kalshiWsState.lastUpdate).toLocaleTimeString():'never'));
      console.info('');
      console.info('Commands:');
      console.info('  __taraWebSocket(\'on\')   enable real-time feed');
      console.info('  __taraWebSocket(\'off\')  disable, back to polling');
      console.groupEnd();
      return{enabled:kalshiWsEnabled,state:kalshiWsState};
    };
  },[kalshiWsEnabled,kalshiWsState]);

  // V10.2.31 — HUNTER DRIFT CHECK + RE-APPLY console hooks.
  //   Purpose: catch silent drift from preset values. After applying Hunter,
  //   users sometimes manually tweak a setting and forget.
  //   __taraHunterCheck() compares current autoExec values vs the Hunter spec.
  //   __taraHunterReapply() resets all Hunter-touched fields to spec values.
  useEffect(()=>{
    if(typeof window==='undefined')return;
    // Hunter spec from V10.2.25 preset
    // V13.4.292: autoExitOffer:82/stopLossDeltaCents:13 REMOVED from this spec.
    //   Both were stripped from the live HUNTER preset button itself (they are
    //   measured losers, and 82c disabled the trailing stop). Leaving them here
    //   meant __taraHunterCheck flagged the correct, safe 0/0 state as "DRIFT"
    //   and told the user to run __taraHunterReapply() to "fix" it — which
    //   would silently re-arm both losing rules. minQualityScore was also stale
    //   (40 here vs the live button's 30, loosened in V10.6.5); corrected to match.
    const HUNTER_SPEC={
      minTier:'tape',
      minQualityScore:30,
      maxEdgePt:8,
      tradeTimingMode:'advisory',
      skipTimeCapCommit:true,
      tccSmartBypass:true,
      skipMarginalCaution:true,
      maxAutoTradesPerDay:8,
      maxAutoTradesPerWindow:1,
      maxBetPerTrade:2.5,
      maxDailyLoss:6,
      cooldownLossStreak:3,
      cooldownMinutes:30,
    };
    window.__taraHunterCheck=()=>{
      const ae=autoExecSettings||{};
      const _rows={};
      let _drifted=0;
      Object.entries(HUNTER_SPEC).forEach(([k,expected])=>{
        const actual=ae[k];
        // Compare numbers loosely, strings strictly
        const _match=(typeof expected==='number')
          ?Math.abs(Number(actual)-expected)<0.01
          :actual===expected;
        _rows[k]={expected,current:actual,status:_match?'✓':'✗ DRIFT'};
        if(!_match)_drifted++;
      });
      // Also check Kalshi-agree live
      const _kam=(typeof localStorage!=='undefined')?localStorage.getItem('taraKalshiAgreeMode'):null;
      _rows.kalshiAgreeMode={expected:'live',current:_kam,status:_kam==='live'?'✓':'✗ DRIFT'};
      if(_kam!=='live')_drifted++;
      console.group('%c━━━ Hunter Drift Check ━━━','color:'+(_drifted===0?'rgb(35,185,129)':'#E8455E')+';font-weight:bold');
      console.info(_drifted===0?'✓ All Hunter values match spec':`✗ ${_drifted} field${_drifted===1?'':'s'} drifted from Hunter spec`);
      console.table(_rows);
      if(_drifted>0){
        console.info('To reset: __taraHunterReapply()');
      }
      console.groupEnd();
      return{drifted:_drifted,rows:_rows};
    };
    window.__taraHunterReapply=()=>{
      if(typeof setAutoExecSettings!=='function'){
        console.error('setAutoExecSettings unavailable');
        return null;
      }
      setAutoExecSettings(prev=>({...prev,...HUNTER_SPEC}));
      try{
        localStorage.setItem('taraKalshiAgreeMode','live');
        if(typeof setKalshiAgreeMode==='function')setKalshiAgreeMode('live');
      }catch(_){}
      console.info('[V10.2.31] Hunter spec re-applied across '+Object.keys(HUNTER_SPEC).length+' fields + Kalshi-agree live');
      return{applied:true,fields:Object.keys(HUNTER_SPEC).length+1};
    };
  },[autoExecSettings,setKalshiAgreeMode]);

  // V2.6: Kalshi settlement resolver for marginal trades.
  //   Trades with |closingGapBps| < 10 are queued for verification because Tara's local price
  //   feed and Kalshi's CF Benchmarks settlement index can disagree near strike. This effect
  //   polls Kalshi's settled-markets endpoint, finds the just-closed market by close_time,
  //   and uses Kalshi's authoritative result.
  //   Falls back to local-feed result after 5 minutes of failed polling.
  useEffect(()=>{
    const resolverIv=setInterval(async()=>{
      // V13.4.342: FAST STALE-PENDING SWEEP. _scanStalePending's OTHER two
      //   triggers (mount+3s, Supabase cloud updates) don't guarantee a check
      //   soon after _trySettle's own rollover-triggered ~68s poll gives up --
      //   a directional call whose Kalshi strike lands at, say, 90s or a few
      //   minutes post-close (well within Kalshi's normal range) sat fully
      //   unattended for up to 15 minutes despite THIS SAME 20s interval
      //   already running and able to resolve it immediately once queued.
      //   90s floor is a safety buffer past _trySettle's own window (8s+12x5s
      //   ≈68s) so the two mechanisms never race the same entry.
      try{_scanStalePending(30000);}catch(_){}
      if(!pendingResolutionRef.current||pendingResolutionRef.current.length===0)return;
      const now=Date.now();
      // Process each pending resolution
      for(const pending of [...pendingResolutionRef.current]){
        const ageSec=(now-pending.windowCloseTime)/1000;
        // Don't query Kalshi for at least 30s after window close — they need time to settle
        if(ageSec<30)continue;
        // V13.4.338: a SIT_OUT doesn't need Kalshi's settlement at all -- the
        //   decision not to trade was already made at commit time. Without
        //   this, a SIT_OUT that only reached resolution through THIS queue
        //   (rather than live, at rollover) fell through to the taraCallLog
        //   branch below, where `finalResult=_entry.dir===kalshiOutcomeDir?
        //   'WIN':'LOSS'` unconditionally turns any dir that isn't 'UP'/'DOWN'
        //   into a false LOSS -- a sit-out scored as a loss it never took.
        if(pending.source==='taraCallLog'){
          const _stalledEntry=(taraCallLogRef.current||[]).find(t=>t.id===pending.tradeId);
          if(_stalledEntry&&_stalledEntry.dir==='SIT_OUT'){
            if(typeof window._taraApplyReconcile==='function'){
              try{await window._taraApplyReconcile(new Map([[pending.tradeId,{result:'SITOUT',resolution:'backfill-sitout-immediate'}]]),{automatic:true});}catch(_){}
            }
            pendingResolutionRef.current=pendingResolutionRef.current.filter(p=>p.tradeId!==pending.tradeId);
            continue;
          }
          if(_stalledEntry?.originalDecision?.marketTicker||_stalledEntry?.marketTicker){
            const _captured=await readCapturedCallSettlement(_stalledEntry);
            if(_captured.ok&&typeof window._taraApplyReconcile==='function'){
              await window._taraApplyReconcile(new Map([[pending.tradeId,_captured.patch]]),{automatic:true});
              pendingResolutionRef.current=pendingResolutionRef.current.filter(p=>p.tradeId!==pending.tradeId);
            }
            // An identity-bearing Call must never substitute another contract
            // from the recent list when its own read failed or is not settled.
            continue;
          }
        }
        // V13.4.248: taraCallLog-sourced items (the V8.1 stale-pending backfill,
        //   tagged source:'taraCallLog') do not belong to the tradeLog branch below
        //   at all -- that branch looks the id up in tradeLogRef, a different array,
        //   finds nothing, and used to silently drop the item here with the real
        //   taraCallLog entry never touched. There is also no localResultGuess for
        //   these to fall back to, and fabricating a WIN/LOSS guess would corrupt
        //   the very record this app's win-rate and telemetry are measured from.
        //   Kalshi keeps settled-market history indefinitely, but the settled-events
        //   fetch below only returns the ~50 most recent per series (~12.5h for a
        //   15m series) -- so past that horizon this specific query can never find
        //   the match. Keep retrying up to 24h in case of a slow settlement or a
        //   transient outage, then stop WITHOUT writing a result: an honest null is
        //   correct here, a guessed one is not.
        if(pending.source==='taraCallLog'){
          if(ageSec>86400){
            try{console.warn('[V13.4.248] giving up on stale pending after 24h (unreachable via the recent-settled-events window):',pending.tradeId);}catch(_){}
            pendingResolutionRef.current=pendingResolutionRef.current.filter(p=>p.tradeId!==pending.tradeId);
            continue;
          }
          // fall through to the Kalshi-fetch block below on every other tick
        } else if(ageSec>300){
          // After 5 minutes of failed polling, fall back to local-feed result
          const trade=tradeLogRef.current.find(t=>t.id===pending.tradeId);
          if(trade&&trade.result==='PENDING-VERIFY'){
            const finalResult=trade.localResultGuess||'LOSS';
            const newLog=tradeLogRef.current.map(t=>t.id===pending.tradeId?{...t,result:finalResult,kalshiResolutionFailed:true}:t);
            saveTradeLog(newLog);setTradeLog(newLog);
            // V7.1: per-asset learning — uses trade.asset, not currentAsset
            const _resolved=newLog.find(t=>t.id===pending.tradeId);
            applyTradeLearning(newLog,_resolved,finalResult);
          }
          pendingResolutionRef.current=pendingResolutionRef.current.filter(p=>p.tradeId!==pending.tradeId);
          continue;
        }
        // Try to fetch settlement from Kalshi (15m only — 5m series may not exist)
        if(pending.windowType!=='15m'){
          // 5m settlement falls back to local close determination — no Kalshi verify
          continue;
        }
        try{
          pending.attempts++;
          // V4.4: Use /events endpoint (smaller payload, different rate-limit bucket)
          // V7.5: use the asset tagged on the pending resolution at lock time, NOT the
          //   currently-active asset. Without this, ETH trades that resolve while user is
          //   on BTC would query KXBTC15M and never find their settlement → stays PENDING
          //   forever. Result: ETH trades silently disappearing from the win/loss log.
          const _pendingAsset=pending.asset||currentAssetRef.current||'BTC';
          const _assetCfgS=ASSET_CONFIG[_pendingAsset]||ASSET_CONFIG.BTC;
          const _settlePath=`events?series_ticker=${_assetCfgS.kalshiSeriesTicker}15M&with_nested_markets=true&status=settled&limit=50`;
          const _settleUrl=`https://api.elections.kalshi.com/trade-api/v2/${_settlePath}`;
          // V7.10.7: 3-tier fallback. Cloudflare proxy (no CORS) → corsproxy.io → direct.
          //   Settlement resolution is critical for confirmed wins/losses, so this gets the
          //   same resilience as the main strike fetch.
          // V13.4.336: r.ok alone is not proof of a real answer -- a same-origin proxy
          //   path that no longer exists server-side (e.g. the Vercel->Cloudflare move
          //   silently orphaning /api/kalshi/*) 200s with the SPA's own index.html
          //   instead of erroring, so this used to "succeed" tier 1 forever, never try
          //   tiers 2/3, then silently die on the JSON.parse below every 20s. Same
          //   HTML-body guard _wxJson already uses elsewhere in this file.
          const _isHtml=(t)=>!t||t.trimStart().startsWith('<');
          let r=await fetch(`/api/kalshi/${_settlePath}`,{signal:AbortSignal.timeout(6000)}).catch(()=>null);
          let _rt=(r&&r.ok)?await r.text().catch(()=>''):'';
          if(_isHtml(_rt)){
            r=await fetch(
              `https://corsproxy.io/?url=${encodeURIComponent(_settleUrl)}`,
              {signal:AbortSignal.timeout(8000)}
            ).catch(()=>null);
            _rt=(r&&r.ok)?await r.text().catch(()=>''):'';
          }
          if(_isHtml(_rt)){
            r=await fetch(_settleUrl,{signal:AbortSignal.timeout(8000)}).catch(()=>null);
            _rt=(r&&r.ok)?await r.text().catch(()=>''):'';
          }
          if(_isHtml(_rt))continue;
          const d=JSON.parse(_rt);
          // V4.4: Flatten nested markets from events response
          const events=d.events||[];
          const markets=[];
          for(const ev of events){
            const evMarkets=ev.markets||[];
            for(const m of evMarkets){
              markets.push({...m, _event_ticker:ev.event_ticker});
            }
          }
          // Match by close_time near our window close — prefer 'greater' strike_type for UP/DOWN semantics
          const target=pending.windowCloseTime;
          let best=null,bestDiff=Infinity;
          // First pass: only 'greater' strike_type markets
          for(const m of markets){
            if(m.strike_type!=='greater'&&m.strike_type!=='greater_or_equal')continue;
            const closeMs=m.close_time?new Date(m.close_time).getTime():0;
            if(!closeMs)continue;
            const diff=Math.abs(closeMs-target);
            if(diff<bestDiff){bestDiff=diff;best=m;}
          }
          // Second pass: any market if no 'greater' found (legacy fallback)
          if(!best){
            for(const m of markets){
              const closeMs=m.close_time?new Date(m.close_time).getTime():0;
              if(!closeMs)continue;
              const diff=Math.abs(closeMs-target);
              if(diff<bestDiff){bestDiff=diff;best=m;}
            }
          }
          // Must be within 90 seconds and have a result
          if(best&&bestDiff<90000&&best.result){
            // Kalshi result: 'yes' = market resolved YES (price closed >= strike → UP wins)
            //                'no'  = market resolved NO (price closed < strike → DOWN wins)
            const kalshiOutcomeDir=best.result==='yes'?'UP':best.result==='no'?'DOWN':null;
            // V4.4: Extract Kalshi's settled price using new field names. Prefer settlement_value_dollars.
            const _kalshiSettlement=(()=>{
              if(best.settlement_value_dollars!=null){const v=parseFloat(best.settlement_value_dollars);if(isFinite(v))return v;}
              if(best.settlement_value!=null)return Number(best.settlement_value);
              if(best.expiration_value!=null)return Number(best.expiration_value);
              if(best.final_value!=null)return Number(best.final_value);
              if(best.settled_price!=null)return Number(best.settled_price);
              // Fall back to floor_strike — we know the strike, not the actual settled price,
              // but it's better than nothing for trade resolution
              if(best.floor_strike!=null)return Number(best.floor_strike);
              return null;
            })();
            if(kalshiOutcomeDir){
              // V13.4.248: taraCallLog-sourced items are resolved through the SAME
              //   apply path the reconcile panel itself uses (window._taraApplyReconcile)
              //   -- full IDB history, every cache tier, manualEdit stamp, cloud
              //   propagation via the existing taraCallLog write effect. Not
              //   applyTradeLearning: that function mutates the tradeLog-side adaptive
              //   weight store, a different schema than what a taraCallLog entry carries.
              if(pending.source==='taraCallLog'){
                const _entry=(taraCallLogRef.current||[]).find(t=>t.id===pending.tradeId);
                if(_entry&&typeof window._taraApplyReconcile==='function'){
                  const _verified=settlementPatch(_entry,best);
                  await window._taraApplyReconcile(new Map([[pending.tradeId,_verified.ok
                    ?{..._verified.patch}
                    :{recordIntegrity:{version:1,status:'review-required',reason:_verified.reason}}
                  ]]));
                }
              } else {
                const trade=tradeLogRef.current.find(t=>t.id===pending.tradeId);
                if(trade){
                  const finalResult=trade.dir===kalshiOutcomeDir?'WIN':'LOSS';
                  const newLog=tradeLogRef.current.map(t=>t.id===pending.tradeId?{
                    ...t,
                    result:finalResult,
                    outcomeDir:kalshiOutcomeDir,
                    kalshiResolved:true,
                    kalshiClosingPrice:_kalshiSettlement,  // V3.0: canonical Kalshi-settled close
                  }:t);
                  saveTradeLog(newLog);setTradeLog(newLog);
                  // V7.1: per-asset learning. Replaces separate setAdaptiveWeights+setRegimeWeights.
                  const _resolved=newLog.find(t=>t.id===pending.tradeId);
                  applyTradeLearning(newLog,_resolved,finalResult);
                }
              }
              pendingResolutionRef.current=pendingResolutionRef.current.filter(p=>p.tradeId!==pending.tradeId);
            }
          }
        }catch(e){
          // Silent fail — try again next interval
        }
      }
    },20000); // every 20s
    return()=>clearInterval(resolverIv);
  },[adaptiveWeights]);
  // Window rollover
  useEffect(()=>{if(timeState.nextWindowMs&&timeState.nextWindowMs!==_lastWindowMsRef.current){
        // All clock, restoration, lifecycle and log identities use epoch windows.
        // In particular, never retire a Call before its actual boundary.
        const _intervalMs=windowType==='15m'?900000:300000;
        if(timeState.nextWindowMs!==(Math.floor(Date.now()/_intervalMs)+1)*_intervalMs)return;
        const _previousClose=_lastWindowMsRef.current;
        const _closedWindowId=`${windowType}-${new Date((_previousClose>0?_previousClose:Math.floor(Date.now()/_intervalMs)*_intervalMs)-_intervalMs).toISOString()}`;
        setTaraCallLog(prev=>finalizeClosedSitouts(prev,{now:Date.now(),asset:currentAssetRef.current||'BTC',windowType}));
        if(currentPrice!==null){if(lastWindowRef.current!==''){
          let won=false,active=false;
          // V3.2.4: General scorecard no longer auto-updates. Per user: scoring is decoupled
          //   from general predictions. The user enters/closes positions for their own use,
          //   and we DO track personal W-L when they take a position — that's their personal
          //   trading record. Tara's Call has a separate scorecard.
          //
          //   Three scorecards now:
          //     scorecards       — personal: only updates when userPosition!==null
          //     taraScorecards   — Tara's Call: updates on her own conviction-filtered decisions
          //     general prediction itself does not drive any scorecard
          if(manuallyClosedRef.current!==null){
            // User manually closed — result already captured at the moment of close
            won=manuallyClosedRef.current==='WIN';
            active=!!pendingTradeRef.current;
            // Personal scorecard already updated at exit time, no double-count here
          } else if(userPosition==='UP'){
            active=true;
            if(currentPrice>targetMargin){won=true;updateScore(windowType,'wins',1);}
            else updateScore(windowType,'losses',1);
          } else if(userPosition==='DOWN'){
            active=true;
            if(currentPrice<targetMargin){won=true;updateScore(windowType,'wins',1);}
            else updateScore(windowType,'losses',1);
          }
          // If no position was entered, no scoring and no broadcast
          if(active){
            // V7.10.5: regimeMemory bump moved to _logResult (Tara's call resolution).
            //   regimeMemory feeds INTO the engine as learning signal — it should track
            //   Tara's call outcomes (per-regime), not user's position outcomes. Previously
            //   bumping here meant: (a) regimeMemory only updated when user took position,
            //   missing Tara's watch-only calls; (b) when user did take position, both this
            //   line AND _logResult would bump it = double-count. Now: single source of truth.
            // Broadcast result of the trade user was actually in
            if(manuallyClosedRef.current===null){
              recordPnL(won,pendingTradeRef.current);
              // Show post-window recap toast
              const gapBpsRecap=targetMargin>0?((currentPrice-targetMargin)/targetMargin*10000):0;
              setWindowRecap({won,dir:userPosition,closePrice:currentPrice,strike:targetMargin,gapBps:gapBpsRecap,regime:lastRegimeRef.current});
              setTimeout(()=>setWindowRecap(null),6000);
              // Note: scorecard wins/losses NOT pre-incremented since we no longer auto-score the general
              const winsNow=scorecards[windowType]?.wins||0,lossesNow=scorecards[windowType]?.losses||0;
              // V9.1: SUPPRESSED. User-triggered (window closing on user's position). Tara-only broadcasts.
              // broadcastToDiscord('CLOSE',{window:windowType,won,dir:userPosition,regime:lastRegimeRef.current,price:currentPrice,strike:targetMargin,wins:winsNow,losses:lossesNow});
            }
            // Resolve training trade
            // V2.6: Marginal-close handling. When |closingGapBps| < 10 (Tara's local feed and Kalshi's
            //       settlement index can disagree near strike), defer to Kalshi's API instead of
            //       auto-recording. Otherwise record immediately.
            if(pendingTradeRef.current&&pendingTradeRef.current.result===null){
              const _closingGap=targetMargin>0?((currentPrice-targetMargin)/targetMargin)*10000:0;
              const _isMarginal=Math.abs(_closingGap)<10;
              const _winCtx={
                windowWhaleCount:windowWhaleStatsRef.current.count,
                windowContraryWhaleUsd:windowWhaleStatsRef.current.contraryUsd,
                windowContraryWhaleClockSec:windowWhaleStatsRef.current.contraryClockSec,
                windowContraryWhaleSide:windowWhaleStatsRef.current.contrarySide,
                windowMaxGeoRisk:windowMaxGeoRiskRef.current,
                windowHadMacroEvent:windowHadMacroEventRef.current,
              };
              const baseResolved={...pendingTradeRef.current,closingPrice:currentPrice,strikePrice:targetMargin,
                /* V145 closing telemetry */ closingGapBps:_closingGap,
                kalshiAtClose:kalshiYesPrice!=null?Number(kalshiYesPrice):null,
                resolvedTimestampISO:new Date().toISOString(),
                // V9.19.2 Phase 3: capture entry/exit fill prices from autoOrderState
                //   for predictor P&L computation. Only meaningful for autoExec
                //   trades — manual trades leave these null. Stored as integer
                //   cents (our side). realizedPnLCents = exit - entry per contract.
                //   For dollar P&L: realizedPnLCents * count / 100.
                ...((()=>{
                  const _aos=autoOrderStateRef.current;
                  if(!_aos||!pendingTradeRef.current?.autoExec)return{};
                  // V13.4.291: read through the order authority. These five fields
                  //   are PERSISTED to the trade record, and every one of them was
                  //   null or 0 on every auto-exec trade ever logged: fillPrice,
                  //   exitFillPrice and count are names the writer never sets.
                  const _ordR=readOrderState(_aos);
                  const _entry=_ordR.fillCents;                 // our-side already
                  // exitAtCents is the RAW YES price sent to Kalshi; entry is
                  //   our-side. Subtracting them directly is the exact inversion
                  //   fixed in V13.4.290 for the ticket -- it only ever happened to
                  //   be right for UP. Convert before comparing.
                  const _exit=(_ordR.exitCents!=null)
                    ?(_ordR.dir==='DOWN'?(100-_ordR.exitCents):_ordR.exitCents):null;
                  const _contracts=_ordR.count||0;
                  // The writer already computes this correctly at exit time against
                  //   _exitValueCents (our-side both ends); prefer it and only fall
                  //   back to the local subtraction when it is absent.
                  const _realized=Number.isFinite(_ordR.pnlPerContract)
                    ?_ordR.pnlPerContract
                    :((_entry!=null&&_exit!=null)?(_exit-_entry):null);
                  return{
                    entryFillCents:_entry,
                    exitFillCents:_exit,
                    contractsFilled:_contracts,
                    realizedPnLCents:_realized,
                    realizedPnLDollars:_realized!=null?Number(((_realized*_contracts)/100).toFixed(2)):null,
                  };
                })()),
                ..._winCtx,
              };
              if(_isMarginal){
                // Mark as pending-verify and queue for Kalshi resolution
                const pendingTrade={...baseResolved,result:'PENDING-VERIFY',localResultGuess:won?'WIN':'LOSS',marginalClose:true};
                const newLog=[...tradeLogRef.current,pendingTrade];
                saveTradeLog(newLog);setTradeLog(newLog);
                // Don't update weights / scorecard yet — wait for Kalshi confirmation
                pendingResolutionRef.current=[...(pendingResolutionRef.current||[]),{
                  tradeId:pendingTrade.id,
                  windowCloseTime:Date.now(),
                  attempts:0,
                  // V7.5: tag with asset at lock time so the deferred Kalshi fetcher can
                  //   query the correct series ticker even if user has switched assets.
                  asset:pendingTrade.asset||currentAssetRef.current||'BTC',
                }];
              } else {
                // Unambiguous close — record locally as before
                const result=won?'WIN':'LOSS';
                // V8.5: Classify loss pattern immediately for shock-aware learning
                const _withResult={...baseResolved,result};
                const _lossPattern=classifyLossPattern(_withResult);
                const resolvedTrade={..._withResult,lossPattern:_lossPattern};
                const newLog=[...tradeLogRef.current,resolvedTrade];
                saveTradeLog(newLog);setTradeLog(newLog);
                applyTradeLearning(newLog,resolvedTrade,result); // V7.1: per-asset · V8.5: shock-aware inside
                // V10.7.60: immediate sessionStorage flush after resolution
                //   localStorage write is async (via useEffect) — if the tab crashes
                //   between resolution and the effect running, the trade is lost.
                //   sessionStorage write here is synchronous and immediate.
                try{
                  const _sess=newLog.slice(-200).map(e=>{if(!e)return e;const{signalScoresAtLock:_a,reasoning:_b,...r}=e;return r;});
                  sessionStorage.setItem('taraCallLog_session',JSON.stringify(_sess));
                }catch(_){}
              }
              pendingTradeRef.current=null;
            }
          }
        }}
        // ── AUTO-STRIKE: propose live price at new window start ──
        // V6.4.1: Clear Kalshi cache state IMMEDIATELY (before setWindowOpenStrike reads it)
        //   so the rollover doesn't snap the previous window's strike onto the new window.
        //   The Kalshi useEffect's own clear runs later in the same React tick, but
        //   setWindowOpenStrike below reads kalshiStrikeRef.current synchronously — the ref
        //   must be nulled BEFORE that read. The fast-retry effect will pull the new strike.
        // V10.7.45: Record rollover into lifecycle audit for the just-closed window.
        //   The just-closed window's id = lastWindowRef.current.
        try{
          const _justClosedWid=_closedWindowId;
          const _rolloverAsset=currentAssetRef.current||currentAsset||'BTC';
          if(_justClosedWid)_lifecycleRecordRollover(_justClosedWid,_rolloverAsset);
        }catch(_){}
        // V10.7.45: Open lifecycle record for the NEW window
        try{
          const _newWid=computeWindowId(windowType);
          const _newAsset=currentAssetRef.current||currentAsset||'BTC';
          _lifecycleRecordOpen(_newWid,windowType,_newAsset);
        }catch(_){}
        kalshiStrikeRef.current=null;
        setKalshiStrike(null);
        setKalshiYesPrice(null);
        setKalshiActiveMarket(null);
        isManualStrikeRef.current=false;
        hasSetInitialMargin.current=false;
        setWindowOpenStrike(currentPriceRef.current||currentPrice);
        // V6.4.1: Force-trigger an immediate Kalshi refetch — don't wait for the next
        //   2s retry tick. The new window's strike should land within 1-2s.
        if(triggerKalshiFetchRef.current)triggerKalshiFetchRef.current();
        // V6.0: subtle bell on window rollover — tells you a new window opened without looking
        playAlert('window-open');
        // NOTE: do NOT setPendingStrike(null) here — setWindowOpenStrike just set it
        mtfLocksRef.current[windowType]=null; // clear this timeframe's lock on rollover
        // V3.2.4: Resolve Tara's Call for the round just closed.
        //   This runs whether or not the user actively traded — Tara has her own scorecard.
        //   If snapshot is null (window closed before endgame freeze fired), treat as SIT_OUT.
        // V5.5.5: Use windowOpenPriceRef as strike fallback if targetMargin happens to be 0
        //   at close — this can happen if Kalshi came in after rollover and user cleared it.
        // V7.5: ASSET-AWARE SCORING. Was reading targetMargin/currentPrice directly — but if
        //   user switched from ETH to BTC mid-ETH-window, targetMargin now holds BTC's strike
        //   and the resolution would fail the 20% sanity check. Find the pending entry for
        //   THIS specific window and use its locked-in strike. The entry was tagged with its
        //   asset at lock time (V7.0.7 belt-and-suspenders) so we can identify the right one.
        const _winMs=windowType==='15m'?900000:300000;
        const _wid=_closedWindowId;
        // Look up the entry that was logged for this exact window. It already has the strike.
        // Use ref to avoid stale closure — taraCallLog is not in this effect's dep array.
        // V10.7.87: filter by asset — both BTC and ETH share the same windowId format
        //   (e.g. "15m-2026-06-03T08:00:00.000Z"). Without asset filter, the BTC tab
        //   could find the ETH entry and use its strike (~$2500) to score a BTC trade.
        const _logSnap=taraCallLogRef.current||[];
        const _tabAsset='BTC';
        const _pendingEntry=_logSnap.find(e=>e&&e.windowId===_wid&&e.result===null&&(e.asset||'BTC')===_tabAsset);
        try{var _ptjR=_postLockTrajRef.current[_wid];if(_ptjR&&_ptjR.ticks>0){var _evA=!!_ptjR.everAhead;var _pkB=Math.round(_ptjR.peakBps*10)/10;var _pcC=Math.round((_ptjR.correct/_ptjR.ticks)*100)/100;var _rvD=!!_ptjR.reversed;setTaraCallLog(function(prev){return prev.map(function(e){return (e&&e.windowId===_wid&&(e.asset||'BTC')===_tabAsset&&e.postLockEverAhead===undefined)?Object.assign({},e,{postLockEverAhead:_evA,postLockPeakBps:_pkB,postLockPctCorrect:_pcC,postLockReversed:_rvD,postLockPeakValueC:(_ptjR.peakValC>=0?Math.round(_ptjR.peakValC):null),postLockFirstValueC:(_ptjR.entryValC>=0?Math.round(_ptjR.entryValC):null)}):e;});});delete _postLockTrajRef.current[_wid];}}catch(_){}
        try{if(liveCoachReversalRef&&liveCoachReversalRef.current){var _lcrF=liveCoachReversalRef.current;if(_lcrF.fired){var _lcrFired=true,_lcrPeak=Math.round(_lcrF.peakBps*10)/10,_lcrDD=Math.round(_lcrF.drawdownBps*10)/10;setTaraCallLog(function(prev){return prev.map(function(e){return (e&&e.windowId===_wid&&(e.asset||'BTC')===_tabAsset&&e.liveCoachReversalFired===undefined)?Object.assign({},e,{liveCoachReversalFired:_lcrFired,liveCoachReversalPeakBps:_lcrPeak,liveCoachReversalDrawdownBps:_lcrDD}):e;});});}}}catch(_){}
        const _entryAsset=_pendingEntry?.asset||_tabAsset;
        const _entryStrike=Number(_pendingEntry?.strike)||0;
        // Prefer entry's strike (asset-correct, locked-in), else current targetMargin if it
        //   matches asset, else windowOpenPriceRef. _entryStrike covers the cross-asset case.
        const _scoringStrike=_entryStrike>0?_entryStrike:(targetMargin>0?targetMargin:(windowOpenPriceRef.current||0));
        // V5.7.5: Capture rollover-instant spot as fallback close price. The DEFERRED scorer
        //   runs ~8s later and prefers the just-refreshed Kalshi strike (= the just-closed
        //   window's true close per Kalshi's settlement feed) over this spot snapshot.
        // V7.5: only use spot if it matches asset magnitude (BTC≥10K, ETH 100-10K). Cross-
        //   asset spot would fail sanity and stall resolution.
        const _spotNow=currentPriceRef.current||currentPrice;
        const _spotMatchesAsset=_spotNow>0&&_spotNow>=10000;
        const _closeAtRolloverSpot=_spotMatchesAsset?_spotNow:0;
        const _capturedSnap=taraCallSnapshotRef.current;
        const _capturedWindowType=windowType;
        // V8.6.2: Capture window context NOW, before the next window resets the refs
        //   (windowHighRef etc.). The deferred _resolveScore runs 5-60s later — by
        //   then refs are wiped. _capturedWindowCtx is merged into the call log entry
        //   at resolve time so Tara has the data for shock-aware learning.
        const _capturedWindowCtx={
          windowWhaleCount:windowWhaleStatsRef.current?.count||0,
          windowContraryWhaleUsd:windowWhaleStatsRef.current?.contraryUsd||0,
          windowContraryWhaleClockSec:windowWhaleStatsRef.current?.contraryClockSec||null,
          windowContraryWhaleSide:windowWhaleStatsRef.current?.contrarySide||null,
          windowMaxGeoRisk:windowMaxGeoRiskRef.current||0,
          windowHadMacroEvent:!!windowHadMacroEventRef.current,
        };
        if(_scoringStrike>0){
          // V6.3.5: KALSHI-ONLY SCORING per user request: "make sure wins and losses are
          //   recorded only when we pull in the new strike price for that window timing
          //   from kalshi". The new window's Kalshi strike = the just-closed window's close.
          //   We retry up to 60s for Kalshi to deliver. If it never does, leave the entry
          //   as PENDING (no result) instead of falling back to spot — spot at the boundary
          //   instant disagrees with Kalshi's settlement and was the source of bad scores.
          const _newWindowStrikeAtRollover=targetMarginRef.current; // capture for delta-detection
          const _resolveScore=(closeFromKalshi,attempt)=>{
            // Sanity: close price must be within 20% of strike. Beyond that = corrupt data.
            const _priceLooksValid=closeFromKalshi>0&&_scoringStrike>0&&Math.abs(closeFromKalshi-_scoringStrike)/_scoringStrike<0.20;
            if(!_priceLooksValid){
              try{console.warn('[V6.3.5 scoring] Kalshi close out of sanity range',{strike:_scoringStrike,close:closeFromKalshi,attempt});}catch(_){}
              return false; // refuse to score
            }
            const _outcomeDir=closeFromKalshi>_scoringStrike?'UP':closeFromKalshi<_scoringStrike?'DOWN':null;
            const _gap=_scoringStrike>0?((closeFromKalshi-_scoringStrike)/_scoringStrike)*10000:0;
            // V5.6.6: past-windows tracker — every window recorded regardless of Tara's call.
            if(_outcomeDir){
              const _winMs=_capturedWindowType==='15m'?900000:300000;
              const _pwid=_wid; // captured boundary, not a later retry's clock
              // V8.8.6: tag past-window snapshot with asset so BTC + ETH at the same
              //   window slot coexist in the past-windows pill / history. Without this,
              //   whichever asset closed first wrote the snapshot and the second was
              //   silently dropped.
              const _pwAsset=currentAssetRef.current||currentAsset||'BTC';
              const _pastEntry={
                id:Date.now(),
                windowId:_pwid,
                time:Date.now(),
                windowType:_capturedWindowType,
                asset:_pwAsset,
                strike:_scoringStrike,
                closingPrice:closeFromKalshi,
                closeSource:'kalshi',
                dir:_outcomeDir,
                gapBps:_gap,
              };
              const _sixtySecAgo=Date.now()-60000;
              setPastWindows(prev=>{
                // V8.8.6: dedup check now keys on asset too — BTC's snapshot for a
                //   window doesn't suppress ETH's snapshot for the same window.
                if(prev.some(e=>e&&(e.asset||'BTC')===_pwAsset&&((e.windowId&&e.windowId===_pwid)||(e.windowType===_capturedWindowType&&e.time>_sixtySecAgo))))return prev;
                return [...prev,_pastEntry].slice(-50);
              });
            }
            // Resolve call log entry by windowId match (V6.3.4).
            const _capturedWindowId=_wid;
            // V9.10.5: Capture timeSeries + releaseSignalsHistory from lockedCallRef NOW,
            //   before the rollover effect clears the ref. _logResult runs inside an 8s+
            //   setTimeout (via _trySettle), by which time lockedCallRef.current is null —
            //   so the V9.8.14 merge was always falling back to []. That's why every
            //   resolved entry showed `timeSeries: []` in CSV exports despite the per-5s
            //   push at L21609 (V9.8.14 capture) running correctly. Closure-capturing here
            //   means the data is preserved through the timing race regardless of when
            //   _logResult actually fires.
            // V10.2.19: also capture from taraCallSnapshotRef as fallback. Snapshot
            //   commits (ng-snap, cap-snap, mix-snap, kalshi-snap) populate the
            //   snapshot ref's timeSeries instead of lockedCallRef. Without this
            //   line, 81% of resolved trades captured null.
            const _capturedTimeSeries=
              (typeof lockedCallRef!=='undefined'&&Array.isArray(lockedCallRef.current?.timeSeries)&&lockedCallRef.current.timeSeries.length>0)
                ?[...lockedCallRef.current.timeSeries]
                :(typeof taraCallSnapshotRef!=='undefined'&&Array.isArray(taraCallSnapshotRef.current?.timeSeries)&&taraCallSnapshotRef.current.timeSeries.length>0)
                  ?[...taraCallSnapshotRef.current.timeSeries]
                  :null;
            const _capturedReleaseHistory=(typeof lockedCallRef!=='undefined'&&Array.isArray(lockedCallRef.current?.releaseSignalsHistory))?[...lockedCallRef.current.releaseSignalsHistory]:null;
            const _logResult=(resultStr)=>{
              // V8.8.6: capture the asset this resolution is FOR. Without filtering by
              //   asset, when BTC and ETH both have unresolved entries for the same
              //   windowId (now possible after V8.8.6 dedup-key fix), the find() could
              //   pick either one and write the wrong asset's outcome onto it.
              const _resolveAsset='BTC';
              setTaraCallLog(prev=>{
                let idx=prev.map((e,i)=>({e,i})).find(({e})=>e&&(e.asset||'BTC')===_resolveAsset&&e.windowId===_capturedWindowId&&e.result===null&&(e.dir!=='SIT_OUT'||resultStr==='SITOUT'));
                if(!idx)return prev;
                if(idx.e.result!==null)return prev;
                // V6.5.3: If we're about to log SITOUT but the entry actually has a directional
                //   call (dir='UP' or 'DOWN'), resolve from the entry's dir instead. The
                //   captured snapshot can be lost (page refresh, cloud-restore race) but the
                //   log entry was already persisted with the real call data.
                let _resolvedResult=resultStr;
                if(resultStr==='SITOUT'&&(idx.e.dir==='UP'||idx.e.dir==='DOWN')&&_outcomeDir){
                  _resolvedResult=idx.e.dir===_outcomeDir?'WIN':'LOSS';
                }
                let _pathMetrics={}; // V9.10.8: empty spread; path metrics derive from timeSeries below
                let _classifiedLossPattern=null;
                const next=[...prev];
                const _mergedEntry={
                  ...idx.e,
                  // New-protocol directional Calls remain pending until the
                  // exact captured market supplies its official outcome.
                  result:idx.e.originalDecision?.learningFeatures&&isDirection(idx.e.dir)?null:_resolvedResult,
                  provisionalResult:idx.e.originalDecision?.learningFeatures&&isDirection(idx.e.dir)?_resolvedResult:null,
                  outcomeDir:_outcomeDir,
                  netCents:taraNetCents({...idx.e,result:_resolvedResult,dir:idx.e.dir}),/*V13.4.79 realized money*/
                  strike:_scoringStrike,
                  closingPrice:closeFromKalshi,
                  closeSource:'kalshi',
                  gapBps:_gap,
                  resolvedAt:Date.now(),
                  // V8.6.2 — V8.5 fields now actually populate
                  ..._pathMetrics,
                  ..._capturedWindowCtx,
                  closingGapBps:_gap,
                  kalshiAtClose:typeof kalshiYesPrice!=='undefined'&&kalshiYesPrice!=null?Number(kalshiYesPrice):null,
                  // V10.7.54: Capture actual entry pricing for profitability analysis.
                  //   Priority order:
                  //     1. auto-exec fill (autoOrderStateRef) — actual filled price
                  //     2. manual entry (manualKalshiEntry) — user-logged actual fill
                  //     3. estimate from kalshiAtLock — best-effort when nothing logged
                  //   Without entryPrice we can't compute true EV per trade.
                  ...(()=>{
                    // 1. Auto-exec path
                    const _aos=(typeof autoOrderStateRef!=='undefined')?autoOrderStateRef.current:null;
                    const _matchAuto=_aos&&_aos.windowId===_capturedWindowId&&_aos.asset===_resolveAsset;
                    // V13.4.291: this block read fillPrice / limitCents / attempts /
                    //   ladderPath / fairValueCents / kalshiAtFill off autoOrderState.
                    //   The writer sets NONE of those names -- it writes filledAtCents
                    //   and rungCents, and never wrote the last four at all. So the
                    //   `if` was false on every trade ever placed and this returned
                    //   nothing, every time. That is the actual reason the note at the
                    //   top of this file records entrySlippageBps / kalshiAtFill /
                    //   entryFairValue as populated ZERO times across 6,541 entries and
                    //   entrySource as never once 'auto-exec-filled': not that fills
                    //   never happened, but that the log was reading keys nobody wrote.
                    //   Execution cost is called the single largest unknown in the
                    //   system there; it was never unmeasurable, just never recorded.
                    //   Now routed through readOrderState, the one authority that knows
                    //   both the real names and the legacy ones.
                    const _ordLog=readOrderState(_aos);
                    if(_matchAuto&&(_ordLog.fillCents!=null||_ordLog.limitCents!=null)){
                      const _filled=_ordLog.fillCents!=null;
                      const _fair=Number(_aos.fairValueCents);
                      const _fairOk=Number.isFinite(_fair);
                      return{
                        entryPrice:Number(_ordLog.fillCents??_ordLog.limitCents)||null,
                        entrySource:_filled?'auto-exec-filled':'auto-exec-limit',
                        entryAttempts:Number(_aos.attempts)||1,
                        entryLadderPath:Array.isArray(_aos.ladderPath)?_aos.ladderPath.slice(0,10):null,
                        entryFairValue:_fairOk?_fair:null,
                        // NOTE: the key says Bps, the value is and always was CENTS to
                        //   2dp. Keeping the historical key so the persisted schema and
                        //   every export stay readable; the unit is stated here rather
                        //   than silently corrected. Both terms are our-side (see
                        //   _execFairCents) so a DOWN fill cannot invert.
                        entrySlippageBps:(_filled&&_fairOk)
                          ?Math.round((Number(_ordLog.fillCents)-_fair)*100)/100:null,
                        kalshiAtFill:Number.isFinite(Number(_aos.kalshiAtFill))?Number(_aos.kalshiAtFill):null,
                      };
                    }
                    // 2. Manual entry path
                    const _mke=(typeof _manualKalshiEntryRef!=='undefined')?_manualKalshiEntryRef.current:null;
                    if(_mke&&_mke.entryCents!=null){
                      return{
                        entryPrice:Number(_mke.entryCents)||null,
                        entrySource:'manual-logged',
                        entryContracts:Number(_mke.contracts)||null,
                        entryTimeMs:Number(_mke.time)||null,
                      };
                    }
                    // 3. Auto-capture from kalshiAtLock (V10.7.69)
                    //   kalshiAtLock is captured on every lock — it's the Kalshi YES price
                    //   at the moment Tara locked. This IS the entry price (pre-slippage).
                    //   No manual input needed. Using it as entryPrice directly (not estimate).
                    //   For UP bets: entry cost = kalshiAtLock cents
                    //   For DOWN bets: entry cost = (100 - kalshiAtLock) cents
                    //   Breakeven WR = (entryPrice + 6) / 100 (6¢ Kalshi fee)
                    const _kalshiAtLock=idx.e.kalshiAtLock!=null?Number(idx.e.kalshiAtLock):null;
                    const _dir=idx.e.dir;
                    const _autoEntry=_kalshiAtLock!=null
                      ?(_dir==='UP'?_kalshiAtLock:(100-_kalshiAtLock))
                      :null;
                    return{
                      entryPrice:_autoEntry,        // use as real entry price
                      entryPriceEstimate:_autoEntry, // keep for backwards compat
                      entrySource:_autoEntry!=null?'kalshi-at-lock':'unknown',
                    };
                  })(),
                  // V13.4.315: tag with the SAME real settlement-ledger data the
                  //   Mission-bankroll effect already reads (v13.4.309,
                  //   _execSettlementLedgerRef, keyed 'BTC|'+windowId at fill
                  //   time). Before this, NOTHING ever wrote autoExec:true or
                  //   realizedPnLDollars onto a taraCallLog entry for the real
                  //   Kalshi engine -- only handleManualSync's separate opts.autoExec
                  //   path could set them, and nothing ever calls it with
                  //   autoExec:true. Confirmed live: a dry-run trade fired through
                  //   the real entry effect, held to a real WIN, and its log entry
                  //   carried neither field -- the auto-exec P&L strip
                  //   (ScalperAdvisorPanel's _renderPredictorHeader) counted it as
                  //   "manual" and could never show a dollar figure, no matter how
                  //   many real trades fired. Mirrors the mission effect's exact
                  //   ledger lookup + hold-to-settlement math (lines ~46655-46674)
                  //   so the two can never disagree about the same trade.
                  ...(()=>{
                    const _ledgerKey=(idx.e.asset||'BTC')+'|'+idx.e.windowId;
                    const _ledger=(typeof _execSettlementLedgerRef!=='undefined')?_execSettlementLedgerRef.current[_ledgerKey]:null;
                    if(!_ledger)return{};
                    // V13.4.346: a failed-attempt-only ledger entry (see the
                    //   entry-ladder's no-fill branch) has no fill data to
                    //   compute P&L from at all -- stamp that a real attempt
                    //   was made and why it didn't fill, so this is finally
                    //   distinguishable from "auto-exec never even tried,"
                    //   which used to look identical (both: no autoExec field).
                    if(_ledger.filled===false){
                      return{
                        autoExecAttempted:true,
                        autoExecAttempts:_ledger.attempts||null,
                        autoExecLastReason:_ledger.lastReason||null,
                      };
                    }
                    let _realized=null;
                    if(_ledger.exitedAt!=null&&_ledger.realizedPnLDollars!=null){
                      // Closed early (trailing stop / take-profit / cut-loss / time
                      //   exit) -- the real exit fill, not settlement math.
                      _realized=_ledger.realizedPnLDollars;
                    } else if(_ledger.exitedAt==null&&_ledger.stakeDollars!=null&&_ledger.filledCount!=null&&_ledger.filledFeesCents!=null){
                      // Held to natural settlement -- standard win/lose-max-payout
                      //   math on the REAL stake/count this window actually filled.
                      // V13.4.316: _resolvedResult reflects whether TARA'S SETTLED
                      //   CALL was right (idx.e.dir vs _outcomeDir) -- but the real
                      //   position was entered on the ENGINE LOCK (_ledger.dir),
                      //   which can differ from the settled call on the same
                      //   window. Using _resolvedResult here silently computed the
                      //   WRONG SIGN on exactly those disagreement windows: a real
                      //   long-UP position that lost would be reported as a profit
                      //   if the settled call later flipped to DOWN and DOWN won.
                      //   Compare the REAL position's own direction against the
                      //   real market outcome directly -- both are already on hand
                      //   (_ledger.dir, _outcomeDir) -- so this can never disagree
                      //   with what was actually bought. Falls back to
                      //   _resolvedResult only on the rare entry with no recorded
                      //   _outcomeDir at all, rather than computing nothing.
                      const _maxPayout=Number(_ledger.filledCount)*1.0;
                      const _ledgerWon=(_ledger.dir==='UP'||_ledger.dir==='DOWN')&&(_outcomeDir==='UP'||_outcomeDir==='DOWN')
                        ?(_ledger.dir===_outcomeDir)
                        :(_resolvedResult==='WIN');
                      _realized=(_ledgerWon?(_maxPayout-_ledger.stakeDollars):-_ledger.stakeDollars)-_ledger.filledFeesCents/100;
                    }
                    return{
                      autoExec:true,realizedPnLDollars:_realized,
                      // V13.4.316: persist what the ledger captured at entry
                      //   time onto the PERSISTED log entry (the ledger itself
                      //   is in-memory only and gone on the next reload) -- the
                      //   first durable record of whether the engine lock and
                      //   Tara's settled call agreed on this window, and which
                      //   one the real order actually traded on.
                      execSignalSource:_ledger.signalSource||null,
                      execEngineLockDir:_ledger.engineLockDirAtEntry||null,
                      execSnapshotDir:_ledger.snapshotDirAtEntry||null,
                    };
                  })(),
                  // V9.10.5: prefer closure-captured timeSeries + releaseSignalsHistory
                  //   from before the rollover clear. lockedCallRef.current is null by now
                  //   in the typical case (rollover effect already cleared refs before this
                  //   setTimeout-based merge fires). The captured const path is the reliable
                  //   one. Defensive fallback chain in case capture returned null.
                  // V10.2.19: also fall back to taraCallSnapshotRef.timeSeries — snapshot
                  //   commits (ng-snap / cap-snap / mix-snap / kalshi-snap) populate the
                  //   snapshot ref instead of lockedCallRef, so this is the only place the
                  //   data lives for those trades. Without this fallback, 81% of resolved
                  //   trades had timeSeriesLen=0.
                  timeSeries:_capturedTimeSeries
                    ||(typeof lockedCallRef!=='undefined'&&lockedCallRef.current?.timeSeries)
                    ||(typeof taraCallSnapshotRef!=='undefined'&&taraCallSnapshotRef.current?.timeSeries)
                    ||idx.e.timeSeries
                    ||[],
                  releaseSignalsHistory:_capturedReleaseHistory||(typeof lockedCallRef!=='undefined'&&lockedCallRef.current?.releaseSignalsHistory)||idx.e.releaseSignalsHistory||[],
                };
                // ── V9.10.8: DERIVE MID-TRADE EXCURSION METRICS FROM timeSeries ──
                // The entry shape has had placeholder fields for these since V8.6 but they
                //   were never wired (None on every export). With V9.10.8 cloud-restore-
                //   preservation fix delivering full timeSeries arrays, we can finally
                //   compute them honestly. Five fields:
                //
                //   maxAdverseExcursionBps  — worst point against the call's direction.
                //     For UP call: most negative gap (price below strike) seen mid-trade.
                //     For DOWN call: most positive gap (price above strike).
                //   maxFavorableExcursionBps — best point in the call's direction.
                //   peakClockSec / troughClockSec — seconds-into-lock when those extremes hit.
                //   last60sDriftBps — net gap change in the final 60 seconds of the window.
                //
                // These are the inputs the reversal-coach study needs to answer "could
                //   Tara have seen the reversal mid-trade?" Without these, every analysis
                //   has to re-derive from the raw timeSeries array. Now they're stamped on
                //   each entry, queryable directly from CSV.
                try{
                  const _ts=_mergedEntry.timeSeries;
                  if(Array.isArray(_ts)&&_ts.length>=2){
                    const _dirSign=_mergedEntry.dir==='UP'?1:(_mergedEntry.dir==='DOWN'?-1:0);
                    if(_dirSign!==0){
                      let _maxAdverse=0, _maxFavorable=0, _peakClk=null, _troughClk=null;
                      for(const _s of _ts){
                        const _g=Number(_s?.g);
                        const _t=Number(_s?.t);
                        if(!Number.isFinite(_g)||!Number.isFinite(_t))continue;
                        // Signed excursion in the call's direction: positive = favorable, negative = adverse
                        const _signed=_g*_dirSign;
                        if(_signed>_maxFavorable){_maxFavorable=_signed; _peakClk=_t;}
                        if(_signed<_maxAdverse){_maxAdverse=_signed; _troughClk=_t;}
                      }
                      _mergedEntry.maxAdverseExcursionBps=Math.round(_maxAdverse*10)/10;
                      _mergedEntry.maxFavorableExcursionBps=Math.round(_maxFavorable*10)/10;
                      _mergedEntry.peakClockSec=_peakClk;
                      _mergedEntry.troughClockSec=_troughClk;
                    }
                    // Last-60s drift — net gap change in final minute, sign-agnostic.
                    //   Useful for "did Tara's win come from a final-minute push or hold-the-line?"
                    const _lastT=Number(_ts[_ts.length-1]?.t);
                    if(Number.isFinite(_lastT)){
                      const _cutoffT=_lastT-60;
                      let _at60Ago=null, _atEnd=Number(_ts[_ts.length-1]?.g);
                      // Walk backwards for the first sample <= _cutoffT
                      for(let _i=_ts.length-1;_i>=0;_i--){
                        const _t=Number(_ts[_i]?.t);
                        if(Number.isFinite(_t)&&_t<=_cutoffT){_at60Ago=Number(_ts[_i]?.g);break;}
                      }
                      if(Number.isFinite(_at60Ago)&&Number.isFinite(_atEnd)){
                        _mergedEntry.last60sDriftBps=Math.round((_atEnd-_at60Ago)*10)/10;
                      }
                    }
                  }
                }catch(_e){try{console.warn('[V9.10.8] derived-fields computation failed',_e?.message);}catch(_){}}
                // ── end V9.10.8 ──
                // Classify loss pattern only on LOSS — uses the just-merged entry. V8.7 guarded.
                if(_resolvedResult==='LOSS'){
                  try{_classifiedLossPattern=classifyLossPattern(_mergedEntry);}
                  catch(err){try{console.warn('[V8.7] classifyLossPattern failed',err?.message);}catch(_){}}
                  if(_classifiedLossPattern)_mergedEntry.lossPattern=_classifiedLossPattern;
                }
                next[idx.i]=_mergedEntry;
                setTimeout(()=>_recomputeLearningsFromLog(next),0);
                // V7.10.5: TARA SELF-LEARNING. When her call resolves WIN/LOSS, feed it
                //   into the gradient-descent learner (applyTradeLearning) — was previously
                //   gated on user position. Now Tara learns from EVERY resolved call,
                //   regardless of whether user entered. Synthesizes a trade record from
                //   the resolved entry: signals come from signalScoresAtLock (V6.3.5),
                //   posterior from rawPosteriorAtLock or posterior, dir from the entry,
                //   asset from the entry's tag.
                if(next[idx.i]?.result===_resolvedResult&&(_resolvedResult==='WIN'||_resolvedResult==='LOSS')){
                  const _entry=next[idx.i];
                  // V7.10.5: bump regimeMemory from Tara's resolved call too. Was previously
                  //   only incrementing on user-position resolutions (line 9664), so the
                  //   per-regime stats were incomplete when user only watched.
                  const _regimeKey=_entry?.regime||'RANGE-CHOP';
                  // V10.7.86b: only update regimeMemory for trades matching current asset
                  const _entryAsset=_entry?.asset||'BTC';
                  if(_regimeKey&&_entryAsset===currentAsset){
                    setRegimeMemory(prev=>{
                      const u={...prev};
                      if(!u[_regimeKey])u[_regimeKey]={wins:0,losses:0};
                      else u[_regimeKey]={...u[_regimeKey]};
                      if(_resolvedResult==='WIN')u[_regimeKey].wins=(u[_regimeKey].wins||0)+1;
                      else u[_regimeKey].losses=(u[_regimeKey].losses||0)+1;
                      return u;
                    });
                  }
                  const _hasSignals=_entry?.signalScoresAtLock&&typeof _entry.signalScoresAtLock==='object'&&Object.keys(_entry.signalScoresAtLock).length>0;
                  if(_hasSignals&&(_entry.dir==='UP'||_entry.dir==='DOWN')){
                    // V8.6.2: spread the MERGED entry (_entry = next[idx.i]), not idx.e (pre-merge).
                    //   This carries V8.5 lossPattern + path metrics into applyTradeLearning so
                    //   shock-aware learning actually triggers. Pre-V8.6.2 the spread was idx.e
                    //   which lacked all V8.5 fields — shock detection was effectively dead.
                    const _tradeRecord={
                      ..._entry,
                      asset:_entry.asset||'BTC',
                      dir:_entry.dir,
                      signals:_entry.signalScoresAtLock,
                      posterior:Number(_entry.rawPosteriorAtLock)||Number(_entry.posterior)||50,
                      regime:_entry.regime||'',
                      result:_resolvedResult,
                    };
                    // Synthesize a same-asset trade-log slice from the call log for the
                    //   rolling-window calc inside updateWeights. Each entry must have
                    //   signals + posterior + dir to qualify.
                    const _sameAssetLog=next.filter(e=>{
                      if(!e||(e.result!=='WIN'&&e.result!=='LOSS'))return false;
                      if((e.asset||'BTC')!==(_tradeRecord.asset))return false;
                      if(!e.signalScoresAtLock||Object.keys(e.signalScoresAtLock||{}).length===0)return false;
                      return e.dir==='UP'||e.dir==='DOWN';
                    }).map(e=>({
                      asset:e.asset||'BTC',
                      dir:e.dir,
                      signals:e.signalScoresAtLock,
                      posterior:Number(e.rawPosteriorAtLock)||Number(e.posterior)||50,
                      regime:e.regime||'',
                      result:e.result,
                    }));
                    setTimeout(()=>{
                      try{applyTradeLearning(_sameAssetLog,_tradeRecord,_resolvedResult);}
                      catch(err){console.warn('[V7.10.5 self-learn] applyTradeLearning failed',err);}
                    },20);
                  }
                }
                return next;
              });
            };
            if(!_capturedSnap||_capturedSnap.call==='SIT_OUT'){
              _logResult('SITOUT');
            } else if(_capturedSnap.call==='NO_TRADE'||_capturedSnap.isNoGo){
              // V7.10.1: NO_TRADE entries already have result:'NO_TRADE' from V7.9 snapshot.
              //   No win/loss to compute. Skip scoring — the entry exists in memory with
              //   its specific no-go reason for analytics.
            } else if(_outcomeDir&&(_capturedSnap.call==='UP'||_capturedSnap.call==='DOWN')){
              const _won=_capturedSnap.call===_outcomeDir;
              _logResult(_won?'WIN':'LOSS');
            }
            return true; // scored successfully
          };
          // Retry loop: poll targetMarginRef until Kalshi value differs from rollover instant.
          //   If Kalshi never refreshes within 60s, leave entry PENDING.
          //   For 5m windows, Kalshi doesn't apply — log SITOUT with no scoring (5m doesn't
          //   trade against Kalshi). Better than scoring against unverified spot data.
          if(_capturedWindowType==='5m'){
            // 5m windows: log as SITOUT (no Kalshi market exists for 5m BTC).
            // V9.1.8: Same gate as _logSnapshotEntry — only if user opened the app late.
            //   If a snapshot was created earlier in the window (before this resolve),
            //   it might be UP/DOWN/NO_TRADE — those keep their results. The SITOUT
            //   resolution here only applies when result was still null at rollover.
            const _resolveNow=Date.now();
            const _resolveMsSinceMount=_resolveNow-(_mountTimeRef.current||_resolveNow);
            const _userOpenedLate5m=_resolveMsSinceMount<=30000;
            const _capturedWindowId=_capturedSnap?.windowId||(()=>{
              const _winMs=300000;
              const _justClosedBucket=Math.floor(Date.now()/_winMs)*_winMs-_winMs;
              return `5m-${new Date(_justClosedBucket).toISOString()}`;
            })();
            setTaraCallLog(prev=>{
              let idx=prev.map((e,i)=>({e,i})).find(({e})=>e&&(e.asset||'BTC')===_tabAsset&&e.windowId===_capturedWindowId&&e.result===null&&(e.dir!=='SIT_OUT'||true)); // V13.4.120 FIX: resultStr was out of scope here (this branch always resolves to SITOUT), replaced with the literal it always evaluated as intending
              if(!idx){
                idx=[...prev].map((e,i)=>({e,i})).reverse().find(({e})=>e&&(e.asset||'BTC')===_tabAsset&&e.result===null&&e.windowType===_capturedWindowType&&(e.dir!=='SIT_OUT'||true)); // V13.4.120 FIX: same as above
              }
              if(!idx)return prev;
              if(idx.e.result!==null)return prev;
              // V9.1.8: Only mark as SITOUT if user opened the app late. Otherwise drop
              //   the unresolved entry — it's not actionable.
              if(!_userOpenedLate5m){
                try{console.info('[V9.1.8] Dropping ambiguous 5m unresolved entry (user did not open late)');}catch(_){}
                const next=prev.filter((_,i)=>i!==idx.i);
                return next;
              }
              const next=[...prev];
              next[idx.i]={...idx.e,result:'SITOUT',resolvedAt:Date.now(),resolution:'5m-no-kalshi'};
              setTimeout(()=>_recomputeLearningsFromLog(next),0);
              return next;
            });
          } else if(!_capturedSnap||_capturedSnap.call==='SIT_OUT'){
            // Closed captured sit-outs were finalized above, independent of
            // price availability. A missing snapshot is NOT a new sit-out and
            // must never grade the latest unrelated pending directional row.
          } else {
            // 15m windows: poll for Kalshi-derived close (= new window's strike from Kalshi).
            let _attempts=0;
            const _maxAttempts=12; // 12 × 5s = 60s
            const _trySettle=()=>{
              _attempts++;
              // V7.5: only trust targetMarginRef if user is still on the trade's asset.
              //   If they switched, targetMarginRef holds a different asset's strike and
              //   would fail sanity check anyway. In that case, fall back to the spot we
              //   captured at rollover (which is asset-validated above).
              const _userOnSameAsset=(currentAssetRef.current||'BTC')===_entryAsset;
              const _fresh=_userOnSameAsset?targetMarginRef.current:0;
              const _kalshiChanged=_fresh>0&&_fresh!==_newWindowStrikeAtRollover;
              if(_kalshiChanged&&_resolveScore(Number(_fresh),_attempts)){
                return; // success — Kalshi delivered new strike
              }
              if(_attempts<_maxAttempts){
                setTimeout(_trySettle,5000);
              } else {
                // V7.5: Gave up after 60s of polling. If user is on a different asset,
                //   try spot fallback (asset-matched). Last resort to avoid forever-PENDING.
                if(!_userOnSameAsset&&_closeAtRolloverSpot>0){
                  try{console.info('[V7.5 scoring] Cross-asset fallback to spot for',_entryAsset,'trade');}catch(_){}
                  _resolveScore(_closeAtRolloverSpot,_attempts);
                } else {
                  try{console.warn('[V6.3.5 scoring] Kalshi never delivered new strike for window',{capturedWindowType:_capturedWindowType,strike:_scoringStrike,attempts:_attempts,asset:_entryAsset});}catch(_){}
                }
              }
            };
            // First attempt after 8s (gives Kalshi poll time to land)
            setTimeout(_trySettle,8000);
          }
        }
        // V10.7.62: Clear snapshot IMMEDIATELY at rollover (revert V10.7.61 deferred clear).
        //   V10.7.61's 70s deferred clear caused Discord to re-broadcast the previous
        //   window's lock at the start of every new window (snapshot still set + sentLock reset).
        //   Scoring uses _capturedSnap local variable (captured before clear) — ref not needed.
        taraCallSnapshotRef.current=null;
        taraCallSampleRef.current={dir:null,count:0};
        taraSampleRateRef.current=[]; // V5.7.2: clear rate history for new window
        // V13.4.290 (ORD-2): retire terminal auto-order state at the rollover so
        //   the new ticket cannot render the prior window's entry/exit/result.
        // V13.4.356: do NOT discard unresolved real-money state at this boundary.
        //   A new window can arrive while an entry ladder, an exit verification, or
        //   an exchange position is still unresolved. Clearing here made the next
        //   reconciliation blind to that old ticker; an async callback could then
        //   recreate only `{status:'filled'}` or `{status:'exiting'}` after the
        //   cleanup, losing the ticker/count needed to protect the real position.
        //   Carry the state with an explicit rolloverPending marker instead. The
        //   reconciler clears it only after Kalshi has confirmed the old position
        //   is flat (with its existing two-poll debounce). This may briefly block
        //   the next auto entry, but it cannot silently abandon live money.
        const _rollAos=autoOrderStateRef.current;
        const _rollReason=String(_rollAos?.reason||'');
        const _rollExitUncertain=!!(_rollAos&&(
          (_rollAos.status==='unknown'&&_rollReason==='exit-unconfirmed-verify-kalshi-directly')
          ||(_rollAos.status==='error'&&(_rollReason.startsWith('exit failed:')||_rollReason.startsWith('exit threw:')))
        ));
        const _rollNeedsCarry=!!(_rollAos&&!_rollAos.dryRun&&_rollAos.ticker&&(
          _rollExitUncertain
          ||_rollAos.status==='placing'
          ||_rollAos.status==='resting'
          ||_rollAos.status==='filled'
          ||_rollAos.status==='exiting'
          ||_rollAos.status==='exit-pending'
          ||(_rollAos.status==='unknown'&&_rollReason!=='exit-unconfirmed-verify-kalshi-directly')
          ||(_rollAos.status==='error'&&_rollReason==='no-fill')
        ));
        if(_rollNeedsCarry){
          setAutoOrderState(prev=>prev&&prev.ticker===_rollAos.ticker
            ?{...prev,rolloverPending:true,rolloverAt:Date.now()}
            :prev);
        }else if(isUnresolvedPaperFill(_rollAos)){
          // Keep a held paper fill visible until its ORIGINAL contract has an
          // official result. The private expiry worker also recovers old fills
          // whose UI state was already cleared by an earlier build.
          setAutoOrderState(prev=>prev&&prev.ticker===_rollAos.ticker&&prev.intentId===_rollAos.intentId
            ?{...prev,rolloverPending:true,paperSettlementPending:true,rolloverAt:Date.now()}
            :prev);
        }else{
          setAutoOrderState(null);
        }
        taraAdviceRef.current='SEARCHING...';engineLockedDirRef.current=null;lockedCallRef.current=null;lockReleasedAtRef.current=0;try{localStorage.removeItem('taraLockedTimeSeries_v1');}catch(_){} /* V9.11.2 */posteriorHistoryRef.current=[];biasCountRef.current={UP:0,DOWN:0};hasReversedRef.current=false;manuallyClosedRef.current=null;windowSignalDirRef.current=null;softHintRef.current=0;hardForceRef.current=0;kalshiWasBelowThreshUpRef.current=false;kalshiWasBelowThreshDownRef.current=false;kalshiLastBelowThreshUpRef.current=0;kalshiLastBelowThreshDownRef.current=0;setUserPosition(null);setPositionEntry(null);lastWindowRef.current=timeState.nextWindow;_lastWindowMsRef.current=timeState.nextWindowMs;tickHistoryRef.current=[];setCurrentOffer('');setBetAmount(0);setMaxPayout(0);peakOfferRef.current=0;hasSetInitialMargin.current=true;
        // V10.7.62: force re-render immediately after refs clear so UI flushes
        //   stale lock display. lockedCallRef is a ref (not state) — without this,
        //   old lock stays visible until next organic render.
        setForceRender(p=>p+1);
        _clearLock(); // retire local mirror; never delete another tab's decision
        // Canonical window validation rejects prior-window documents. The new
        // window's conditional publication replaces old state without deletion.
        // V13.4.254: removed a cloudWrite('state/taraSnapshot',null) here -- it
        //   deleted a doc that nothing in this file ever writes or reads.
        _hasRestoredLockRef.current=false; // allow restore on next window if user refreshes
        _cloudRestoreCompletedRef.current=false; // V7.10.3: re-arm cloud-restore gate for new window
        // V9.2.0: Stamp rollover time. Engine IIFE checks this to force SEARCH mode for
        //   5 seconds after rollover — prevents stale analysis from instant-locking the
        //   new window before fresh price data arrives.
        _rolloverGraceRef.current=Date.now();
        // V13.4.347: loss-streak cooldown counts down by exactly one window
        //   per real rollover -- this effect fires once per window
        //   transition, which is exactly the unit "2 windows" means.
        if(_lossCooldownRef.current.windowsRemaining>0){
          _lossCooldownRef.current={..._lossCooldownRef.current,windowsRemaining:_lossCooldownRef.current.windowsRemaining-1};
        }
        }},[timeState.nextWindowMs,currentPrice,windowType,userPosition]);
  // V10.7.60: removed targetMargin + adaptiveWeights from rollover deps.
  //   targetMargin changes every Kalshi poll (every 1.5s) — was re-running the rollover
  //   effect mid-window, causing scanning flashes and stale-lock glitches.
  //   adaptiveWeights changes after every resolved trade — was re-running rollover
  //   after each WIN/LOSS, causing intermittent double-scanning.
  //   Neither needs to trigger rollover — only window transition does.

  useEffect(()=>{if(userPosition===null){peakOfferRef.current=0;liveCoachReversalRef.current={fired:false,peakBps:0,drawdownBps:0};}else{const o=parseFloat(currentOffer)||0;if(o>peakOfferRef.current)peakOfferRef.current=o;}},[currentOffer,userPosition]);

  // V5.6: LOCK RESTORE — runs once on mount once currentPrice is alive.
  //   Reads cloud state/currentLock. If the saved windowId matches the window we're in
  //   right now, restore the engine lock + Tara snapshot + sample formation progress.
  //   This is the fix for "refresh and Tara/the predictor flip" — they no longer recompute
  //   from scratch; they pick up exactly where they were.
  //
  //   What happens if the saved lock is now objectively wrong (price ran hard against)?
  //   We restore it anyway — the lock-release effect already running every tick will fire
  //   the appropriate safety release (rugpull/spike/deep-adverse-gap) on the very next tick.
  //   So we honor the historical commitment but let live data invalidate it normally.
  // V10.9.30: stable "feed is alive" boolean. Flips false->true ONCE when the first
  //   price arrives and then stays true across ticks, so the lock-watch effect below
  //   subscribes once per window/asset instead of re-subscribing on every tick.
  const _priceAlive=!!currentPrice;
  // V7.10.4: REAL-TIME LISTENER. Replaced one-shot cloudRead with cloudWatch so cross-
  //   browser sync actually works. When Browser A locks, Browser B's onSnapshot fires within
  //   ~1s and adopts the snapshot. Path is per-asset+windowType (state/currentLock_BTC_15m
  //   etc) so different markets don't clobber each other. Adoption is conservative: only
  //   adopts if local has no snapshot — never overrides a local commit. The watcher acts
  //   as the cloud-restore-completed signal too: first message (data or null) flips the
  //   gate so lifecycle commits can fire if cloud is genuinely empty.
  // V7.10.5: convergence rule upgraded to FIRST-WRITE-WINS. The handler now also mirrors
  //   the cloud doc to _cloudLockMirrorRef so _persistLock can read it. If cloud's snapshot
  //   was committed meaningfully earlier than local's (>1s tolerance), local is OVERRIDDEN
  //   with cloud's — the earlier commit wins. _persistLock won't re-write because it sees
  //   the same earlier commit in its mirror and defers. This converges both browsers to the
  //   FIRST commit, not the last (which last-write-wins would do).
  useEffect(()=>{
    _cloudRestoreCompletedRef.current=false;
    _cloudRestoreErrorRef.current=null;
    _applySharedDecision(null,'local'); // immutable cache/ledger capture, never a new Call
    if(_SB_PAUSED||!_sbClient){_cloudRestoreCompletedRef.current=true;return;}
    if(!_priceAlive||!timeState.nextWindowMs||!currentAsset||!windowType)return;
    let cancelled=false,retryTimer=null,retries=0,receipt=0;
    const expectedWid=computeWindowId(windowType),asset=currentAssetRef.current||'BTC',path=_lockCloudPath();
    const active=()=>!cancelled&&expectedWid===computeWindowId(windowType)&&asset===(currentAssetRef.current||'BTC');
    const onValue=async(d,status='ok',errorReason=null)=>{
      if(!active())return;
      const token=++receipt;
      const generation=++_sharedRestoreGenerationRef.current;
      if(status!=='ok'){
        _cloudRestoreErrorRef.current=errorReason||'Shared decision read failed; retaining any captured local Call and retrying. No replacement decision will be published.';
        if(!retryTimer)retryTimer=setTimeout(()=>{
          retryTimer=null;if(!active())return;
          cloudSupabaseRead(path,{throwOnError:true}).then(value=>onValue(value,'ok')).catch(()=>onValue(null,'error'));
        },Math.min(30000,4000*2**Math.min(retries++,3)));
        setForceRender(p=>p+1);return;
      }
      if(retryTimer){clearTimeout(retryTimer);retryTimer=null;}
      const context={windowId:expectedWid,windowType,asset,now:Date.now()};
      // A deleted shared slot is not proof that no Call was ever captured.
      // Consult the canonical cloud ledger once on cold load, or on a late
      // return with no captured decision. Never poll the large ledger per tick.
      const hasCapture=restoredSnapshot(taraCallSnapshotRef.current,context)||
        (matchesSharedWindow(d,context)&&restoredSnapshot(d?.taraSnapshot,context));
      const secondsLeft=(Date.parse(expectedWid.replace(/^(5m|15m)-/,''))+(windowType==='15m'?900000:300000)-Date.now())/1000;
      if(!hasCapture&&(!_firstSharedRestoreCompletedRef.current||secondsLeft<=callLockDeadline(windowType))&&
        _sharedLedgerLookupRef.current!==expectedWid){
        try{
          const ledger=await cloudSupabaseRead('memory/taraCallLog',{throwOnError:true});
          if(!active()||token!==receipt||generation!==_sharedRestoreGenerationRef.current)return;
          const captured=ledgerDecision(ledger?.entries||[],{...context,now:Date.now()});
          if(captured)_applySharedDecision({windowId:expectedWid,windowType,asset,taraSnapshot:captured},'ledger');
          _sharedLedgerLookupRef.current=expectedWid;
        }catch(e){
          if(!active()||token!==receipt||generation!==_sharedRestoreGenerationRef.current)return;
          onValue(null,'error','Shared decision history lookup failed; retaining captured local decisions and retrying before creating a replacement.');return;
        }
      }
      if(!active()||token!==receipt||generation!==_sharedRestoreGenerationRef.current)return;
      _applySharedDecision(d,'watch');
      _completeSharedRestore();
      if(taraCallSnapshotRef.current&&sharedSnapshotIdentity(restoredSnapshot(d?.taraSnapshot,context))!==
        sharedSnapshotIdentity(taraCallSnapshotRef.current))_persistLock();
      setForceRender(p=>p+1);
    };
    const unsub=cloudWatch(path,onValue);
    return()=>{cancelled=true;if(retryTimer)clearTimeout(retryTimer);unsub();};
  },[_priceAlive,windowType,currentAsset,timeState.nextWindowMs]);

  // Wake and manual restore share canonical epoch window IDs and one adopter.
  useEffect(()=>{
    if(_SB_PAUSED||!_sbClient)return;
    let cancelled=false,busy=false,lastAt=0;
    const resync=async()=>{
      const now=Date.now();if(busy||now-lastAt<1500)return;
      const wid=computeWindowId(windowType),asset=currentAssetRef.current||'BTC',path=_lockCloudPath();
      const generation=++_sharedRestoreGenerationRef.current;
      busy=true;lastAt=now;
      try{
        const d=await cloudSupabaseRead(path,{throwOnError:true});
        if(cancelled||generation!==_sharedRestoreGenerationRef.current||wid!==computeWindowId(windowType)||asset!==(currentAssetRef.current||'BTC'))return;
        const context={windowId:wid,windowType,asset,now:Date.now()};
        _applySharedDecision(null,'local');
        if(!restoredSnapshot(taraCallSnapshotRef.current,context)&&!(matchesSharedWindow(d,context)&&restoredSnapshot(d?.taraSnapshot,context))&&
          _sharedLedgerLookupRef.current!==wid){
          const ledger=await cloudSupabaseRead('memory/taraCallLog',{throwOnError:true});
          if(cancelled||generation!==_sharedRestoreGenerationRef.current||wid!==computeWindowId(windowType)||asset!==(currentAssetRef.current||'BTC'))return;
          const captured=ledgerDecision(ledger?.entries||[],{...context,now:Date.now()});
          if(captured)_applySharedDecision({windowId:wid,windowType,asset,taraSnapshot:captured},'ledger');
          _sharedLedgerLookupRef.current=wid;
        }
        _applySharedDecision(d,'wake');_completeSharedRestore();setForceRender(p=>p+1);
        if(taraCallSnapshotRef.current&&sharedSnapshotIdentity(restoredSnapshot(d?.taraSnapshot,context))!==
          sharedSnapshotIdentity(taraCallSnapshotRef.current))_persistLock();
      }catch(e){if(!cancelled&&generation===_sharedRestoreGenerationRef.current&&wid===computeWindowId(windowType)&&asset===(currentAssetRef.current||'BTC')){_cloudRestoreErrorRef.current='Shared decision wake read failed; retained captured decision. Waiting for a successful retry.';setForceRender(p=>p+1);}}
      finally{busy=false;}
    };
    const onVis=()=>{if(document.visibilityState==='visible')resync();};
    document.addEventListener('visibilitychange',onVis);window.addEventListener('focus',resync);
    return()=>{cancelled=true;document.removeEventListener('visibilitychange',onVis);window.removeEventListener('focus',resync);};
  },[windowType,currentAsset]);

  // V12.9: WAKE-TIME MISSED-WINDOW DETECTOR
  //   The decision engine runs on the main thread, driven by price/analysis updates.
  //   When the tab is backgrounded (or the phone is locked), the browser throttles or
  //   suspends that work, so windows that open AND close while hidden get NO logged
  //   decision - they become silent holes in the record (the 14 missed-window gaps/day).
  //   We can't run the engine while suspended, but we CAN make the gap honest: on wake,
  //   enumerate every window whose close fell inside the hidden interval, and for any with
  //   no existing log entry, write a distinct MISSED marker.
  //
  //   The marker uses result:'MISSED' - invisible to every WR/EV/sit-out tally (those all
  //   filter WIN|LOSS or SITOUT) - and id=window-open time, so a real trade for the same
  //   window (locked after open, higher id) always wins the merge. A missed marker can
  //   never overwrite a real entry, and it propagates via the V12.8 heartbeat like any
  //   other entry. Purely additive: it records a hole, it does not fabricate a result.
  const _hiddenAtRef=useRef(0);
  const _lastMissedScanRef=useRef(0);
  useEffect(()=>{
    const _winMs=()=>windowType==='15m'?900000:300000;
    const _onHidden=()=>{ if(document.visibilityState==='hidden'&&_hiddenAtRef.current===0)_hiddenAtRef.current=Date.now(); };
    const _scanMissed=()=>{
      // V13.4.56: DISABLED - user does not want missed/backgrounded windows logged,
      //   shown, or saved; empty timeline slots are fine. Detector left wired but inert.
      //   Remove the next line to re-enable MISSED-marker backfill.
      return;
      const _hiddenAt=_hiddenAtRef.current;
      _hiddenAtRef.current=0; // consume
      if(!_hiddenAt)return;
      const _now=Date.now();
      if(_now-_lastMissedScanRef.current<3000)return; // de-dupe vis+focus double-fire
      _lastMissedScanRef.current=_now;
      // Only a real suspension misses windows. A brief blur (< 2 min) does not stall the
      //   engine meaningfully, so ignore short hidden gaps to avoid false MISSED markers.
      if(_now-_hiddenAt<120000)return;
      const _wm=_winMs();
      // A window is missed if its CLOSE time fell within (_hiddenAt, _now]. Enumerate
      //   window-open buckets whose close (open+_wm) lands in that interval.
      const _firstClose=Math.ceil(_hiddenAt/_wm)*_wm; // first window-close at/after hidden
      if(_firstClose> _now-30000)return; // nothing fully closed+settled while hidden
      const _wt=windowType||'15m';
      const _asset=currentAssetRef.current||currentAsset||'BTC';
      // Build set of windowIds already present so we never double-log.
      const _have=new Set();
      for(const e of (taraCallLogRef.current||[])){ if(e&&e.windowId)_have.add((e.asset||'BTC')+'|'+e.windowId); }
      const _markers=[];
      const _settleBefore=_now-30000; // window must have closed >=30s ago
      for(let _close=_firstClose; _close<=_settleBefore; _close+=_wm){
        const _open=_close-_wm;
        // Skip the window currently in progress (its close is in the future handled by <=_now).
        const _wid=_wt+'-'+new Date(_open).toISOString();
        if(_have.has(_asset+'|'+_wid))continue;
        _markers.push({
          id:_open,                       // window-open time: always < any real lock id
          time:_open,
          windowId:_wid,
          windowType:_wt,
          asset:_asset,
          result:'MISSED',
          dir:null,call:'SIT_OUT',
          tier:'missed-backgrounded',
          noGoCategory:'missed-backgrounded',
          _missedWhileBackgrounded:true,
          confidence:0,
        });
      }
      if(!_markers.length)return;
      // Cap the backfill (e.g. overnight sleep) so we don't flood the log. Keep the most
      //   recent N; stamp the truncated count on the newest marker for diagnostics.
      const _CAP=12;
      let _final=_markers;
      if(_markers.length>_CAP){
        const _dropped=_markers.length-_CAP;
        _final=_markers.slice(-_CAP);
        _final[_final.length-1]._missedTruncatedCount=_dropped;
      }
      try{console.info('[V12.9 MISSED] hidden '+Math.round((_now-_hiddenAt)/60000)+'min, logging '+_final.length+' missed '+_wt+' window(s)'+(_markers.length>_CAP?(' (+'+(_markers.length-_CAP)+' older truncated)'):''));}catch(_){}
      setTaraCallLog(prev=>{
        const _byKey=new Set((prev||[]).map(e=>e&&e.windowId?((e.asset||'BTC')+'|'+e.windowId):''));
        const _add=_final.filter(m=>!_byKey.has(m.asset+'|'+m.windowId));
        if(!_add.length)return prev;
        return [...prev,..._add].sort((a,b)=>(a.id||0)-(b.id||0));
      });
    };
    const _onVis=()=>{ if(document.visibilityState==='visible')_scanMissed(); else _onHidden(); };
    const _onFocus=()=>_scanMissed();
    const _onBlur=()=>_onHidden();
    document.addEventListener('visibilitychange',_onVis);
    window.addEventListener('focus',_onFocus);
    window.addEventListener('blur',_onBlur);
    return ()=>{
      document.removeEventListener('visibilitychange',_onVis);
      window.removeEventListener('focus',_onFocus);
      window.removeEventListener('blur',_onBlur);
    };
  },[windowType,currentAsset]);

  // ── MAIN ANALYSIS ──
  const analysis=useMemo(()=>{
    try{
      // Block analysis while user is still typing strike — wait for OK/Enter confirmation
      if(!strikeConfirmed&&strikeMode==='manual'&&targetMargin>0)return null;
      if(!currentPrice||liveHistory.length<30||!targetMargin||!isMounted||!velocityRef.current)return null;
      const is15m=windowType==='15m';
      const intervalSeconds=is15m?900:300;
      const clockSeconds=(timeState.minsRemaining*60)+timeState.secsRemaining;
      const timeFraction=Math.max(0,Math.min(1,1-(clockSeconds/intervalSeconds)));
      const isEndgameLock=is15m?(clockSeconds<90):(clockSeconds<45);
      // V9.19.13: restore isEarlyWindow — engine returns it in analysis object
      //   (line ~26207). V9.19.8 cleanup removed the declaration thinking it was
      //   dead because my grep only checked local-scope readers, not the return
      //   object referencing it via shorthand. Caused MATH CRASH on the live UI.
      const isEarlyWindow=is15m?((intervalSeconds-clockSeconds)<300):((intervalSeconds-clockSeconds)<90);
      // V13.4.100: compute the V101 shadow posterior here where all its inputs are in
      //   scope; attached to the returned analysis object below as v101Shadow so it can
      //   be stamped at lock time next to the real posterior. Read-only, decides nothing.
      const _v101Shadow=computeV101ShadowPosterior({
        currentPrice,liveHistory,targetMargin,globalFlow,bloomberg,
        velocityRef,priceMemoryRef,is15m,timeFraction,
      });
      // V13.4.102: confluence shadow. Direction comes from the same posterior-derived lean
      //   used elsewhere (>50=UP), NOT from taraCall's dir (not in scope here -- taraCall
      //   runs after analysis). Computed only when a real lean exists.
      const _confDir=(_v101Shadow&&_v101Shadow.posterior>50)?'UP':(_v101Shadow&&_v101Shadow.posterior<50)?'DOWN':null;
      const _confluence=computeConfluenceScore({
        liveHistory,currentPrice,targetMargin,
        flowImbalance:globalFlow&&globalFlow.imbalance,dir:_confDir,
      });

      // V110 weighted posterior (adaptive)
      // V3.1.7: tapeRef and ticksRef passed for volume-flow signal computation
      // V3.1.7+: windowHighRef/windowLowRef passed for window-amplitude awareness
      // V3.1.9: windowHighTime/windowLowTime/windowOpenTime for structure detection (TRENDING/WHIPSAW/etc.)
      // V9.5.0: kalshiLead — Kalshi-spot divergence (opponent modeling). Computed here
      //   so we can also surface it in the UI without re-running the engine.
      // V9.5.0: regimeDirCalibration — per-(regime,dir) blend weights pulling the posterior
      //   toward observed WR. Engine applies this after global posterior calibration.
      const _kalshiLead=computeKalshiLead({
        kalshiHist:kalshiHistoryRef.current||[],
        tickHist:tickHistoryRef.current||[],
        nowMs:Date.now(),
      });
      // V10.2.36 — compute recent window moves (bps) for RANGE-CHOP override.
      //   Pull last ~15 resolved entries from taraCallLog for this asset,
      //   compute (closingPrice - strike) / strike in bps. Caller passes this
      //   into the analyzer; if avg abs < 10 bps, regime gets forced to
      //   RANGE-CHOP regardless of drift1m. Empty/short array → no override.
      const _recentWindowMovesBps=(()=>{
        try{
          const _log=taraCallLogRef.current||[];
          const _asset=currentAsset||'BTC';
          const _resolved=_log.filter(e=>
            e&&e.asset===_asset&&
            (e.result==='WIN'||e.result==='LOSS')&&
            Number.isFinite(e.strike)&&Number.isFinite(e.closingPrice)&&
            e.strike>0
          ).slice(-15);
          return _resolved.map(e=>((e.closingPrice-e.strike)/e.strike)*10000);
        }catch(_){return[];}
      })();
      // V10.4.0: Build the rolling-window calibration table from the live call log
      //   and pass it into the engine. Auto-recalibrates every render. Cells with
      //   n<V104_CALIBRATION_TRUST_FLOOR fall back to seed values automatically.
      const _v104Table=(()=>{
        try{return buildV104Table(taraCallLogRef.current||[],currentAsset);}
        catch(_){return V104_SEED_CALIBRATION;}
      })();
      // V10.6.2 — DoW × hour table built fresh per render. Cached automatically
      //   by React render cycle. Same recency weighting as V10.6.1.
      const _dowHourTable=(()=>{
        try{return buildDowHourCalibration(taraCallLogRef.current||[]);}
        catch(_){return null;}
      })();
      // V10.9.1 / V10.9.10: REFERENCE PRICE FOR STRIKE-DISTANCE CALCS.
      //   Kalshi settles against CF Benchmark. The selected chart feed (Coinbase
      //   via TradingView, or OKX) can break or run $50-70 off that benchmark.
      //   Using it for strike-vs-spot math misjudges cushion/gap by that amount,
      //   enough to flip a no-go-edge sitout on a window that's actually fine.
      //   Fix: brtiApprox (V10.9.10) blends the browser-reachable feeds Tara
      //   already proves she can fetch — Coinbase + Kraken (CF anchors) + OKX +
      //   Bybit (bias-corrected onto the anchor scale) — into a CF-accurate mean
      //   at 1.5s. ALL strike-distance math uses this blend, never the raw chart
      //   feed, so a broken chart feed is purely cosmetic for trading.
      //   Gate: need >=2 sources OR (>=1 source WITH the CF anchor present and a
      //   warmed 60s window), and at least 5 samples. Falls back to currentPrice
      //   only during the first ~10s warmup or a total feed blackout.
      const _brtiReady=brtiApprox&&brtiApprox.current>0&&brtiApprox.samples60s>=5&&
        (brtiApprox.sourceCount>=2||(brtiApprox.sourceCount>=1&&brtiApprox.hasAnchor));
      const _refPrice=_brtiReady?brtiApprox.current:currentPrice;

      // V10.8.2: MID-WINDOW CUSHION TRACKER.
      //   Every 30 seconds, push a snapshot of how far price is from the Kalshi strike.
      //   This builds a history of whether price has been consistently on one side
      //   of the strike — which is far more predictive than a single snapshot at lock time.
      const _kStrikeNow=kalshiStrikeRef.current;
      const _cushionNow=Date.now();
      if(_kStrikeNow>0&&_refPrice>0&&_cushionNow-(_cushionLastPushedRef.current||0)>=30000){
        _cushionLastPushedRef.current=_cushionNow;
        const _elapsed=_cushionNow-(windowOpenTimeRef.current||_cushionNow);
        const _cushBps=(_refPrice-_kStrikeNow)/_kStrikeNow*10000;
        _windowCushionHistoryRef.current.push({t:_elapsed,cushionBps:_cushBps,spot:_refPrice});
        // Keep max 30 snapshots (15 minutes / 30s)
        if(_windowCushionHistoryRef.current.length>30)_windowCushionHistoryRef.current.shift();
        try{var _lcT=lockedCallRef.current;if(_lcT&&(_lcT.dir==='UP'||_lcT.dir==='DOWN')&&_lcT.windowId){var _dsT=_lcT.dir==='UP'?1:-1;var _aheadT=_cushBps*_dsT;var _wT=_lcT.windowId;var _ptj=_postLockTrajRef.current[_wT];if(!_ptj){_ptj={ticks:0,correct:0,peakBps:-1e9,everAhead:false,reversed:false,wasAhead:false,peakValC:-1,entryValC:-1};_postLockTrajRef.current[_wT]=_ptj;var _ksT=Object.keys(_postLockTrajRef.current);if(_ksT.length>40)delete _postLockTrajRef.current[_ksT[0]];}_ptj.ticks++;if(_aheadT>0){_ptj.correct++;_ptj.everAhead=true;}if(_aheadT>_ptj.peakBps)_ptj.peakBps=_aheadT;try{var _kyT=Number(_kYesRef.current);if(Number.isFinite(_kyT)&&_kyT>0&&_kyT<100){var _valT=(_lcT.dir==='UP')?_kyT:(100-_kyT);if(_ptj.entryValC<0)_ptj.entryValC=_valT;if(_valT>_ptj.peakValC)_ptj.peakValC=_valT;}}catch(_kE){}if(_aheadT>=5)_ptj.wasAhead=true;if(_ptj.wasAhead&&_aheadT<0)_ptj.reversed=true;}}catch(_){}
      }

      // V10.9.2: FRESH WHALE NETSCORE — computed every scoring tick (~1s) directly
      //   from tapeRef.current.whaleAlerts, instead of the useWhaleAlert hook's
      //   state which only refreshes every 5s.
      //   User-reported symptom: "we got a whale alert for buy/sell, then an
      //   opposite print came in almost instantly that didn't show up in the
      //   signal." Root cause: the hook recomputes netScore on its own 5s
      //   interval. A single large print can dominate that score for up to
      //   ~5 seconds even after an offsetting print arrives — and if Tara locks
      //   inside that window, she's scoring on a transient imbalance that's
      //   already been (or is about to be) cancelled out.
      //   Fix: recompute the same {netScore, direction, transactionCount,
      //   available} shape inline at the same ~1s cadence as price/gap/flow,
      //   from the shared whaleAlerts array (now capped at 60 instead of 20 —
      //   V10.9.2 — so a burst of prints doesn't truncate the 3-min lookback).
      let _whaleFresh=whaleAlert;
      try{
        const _waAlerts=tapeRef?.current?.whaleAlerts;
        if(Array.isArray(_waAlerts)&&_waAlerts.length){
          const _waNow=Date.now();
          const _waRecent=_waAlerts.filter(a=>a&&_waNow-a.time<180000);
          if(!_waRecent.length){
            _whaleFresh={netScore:0,direction:'neutral',transactionCount:0,available:true};
          }else{
            let _waBuy=0,_waSell=0;
            _waRecent.forEach(a=>{if(a.side==='BUY')_waBuy+=a.usd;else _waSell+=a.usd;});
            const _waTotal=_waBuy+_waSell;
            if(_waTotal<100000){
              _whaleFresh={netScore:0,direction:'neutral',transactionCount:_waRecent.length,available:true};
            }else{
              const _waRatio=_waBuy/_waTotal;
              const _waRaw=Math.max(-6,Math.min(6,(_waRatio-0.5)*12));
              _whaleFresh={
                netScore:Math.round(_waRaw*10)/10,
                direction:_waRaw>1?'bullish':_waRaw<-1?'bearish':'neutral',
                transactionCount:_waRecent.length,
                available:true,
                buyUSD:Math.round(_waBuy/1000),
                sellUSD:Math.round(_waSell/1000),
              };
            }
          }
        }
      }catch(_){}

      const eng=computeV99Posterior({currentPrice,referencePrice:_refPrice,liveHistory,targetMargin,globalFlow,bloomberg,velocityRef,tickHistoryRef,priceMemoryRef,windowType,timeFraction,clockSeconds,is15m,regimeMemory,adaptiveWeights,regimeWeights,sessionWeights,currentRegime:lastRegimeRef.current||'RANGE-CHOP',calibration,windowOpenPrice:windowOpenPriceRef.current||0,depthFlash,tfCandles,futuresData,tapeRef,tradeTicksRef:ticksRef,windowHigh:windowHighRef.current||0,windowLow:windowLowRef.current||0,windowHighTime:windowHighTimeRef.current||0,windowLowTime:windowLowTimeRef.current||0,windowOpenTime:windowOpenTimeRef.current||0,otherAssetData:_otherAssetRef.current,mlModel:taraMLModel,timeOfDayBoost:timeOfDayBoostMap,kalshiLead:_kalshiLead,regimeDirCalibration,macroShockData,currentAsset,tapeWindows,econCalRisk:computeEconCalendarRisk(),spotPerpDiv,whaleAlert:_whaleFresh,deribitOptions,liqSignal,liqHeatmap,recentWindowMovesBps:_recentWindowMovesBps,_v104Table,_dowHourTable,kalshiStrike:kalshiStrikeRef.current});
      const{posterior,regime,upThreshold,downThreshold,reasoning,atrBps,realGapBps,drift1m,drift5m,accel,pnlSlope,tickSlope,aggrFlow,isRugPull,isPostDecay,bb,velocityRegime,velocityScalars}=eng;
      lastRegimeRef.current=regime;

      // ══════════════════════════════════════════════════════
      // COMMITTED LOCK STATE MACHINE
      // Rules:
      //  1. Only lock after N consecutive aligned samples (no one-tick locks)
      //  2. Once locked, STAY locked for the window — never flip direction
      //  3. Only two unlock conditions: reality cap (gap > 50bps wrong) OR rugpull
      //  4. Endgame (last 90s/45s): freeze whatever state we're in
      // ══════════════════════════════════════════════════════
      // Session-aware thresholds based on historical WR:
      // EU 65%, ASIA 64%, US 59%, OFF-HOURS 55%
      const _sess=getMarketSessions().dominant;
      const _sessThreshAdj=_sess==='EU'?-3:_sess==='ASIA'?-1:_sess==='US'?3:5;
      // V134: Adaptive threshold per session×regime — tighter where Tara historically struggles
      const _srKey=(getMarketSessions().dominant||'')+':'+regime;
      const _srThreshAdj=sessionRegimeThresh?.[_srKey]||0;
      // V141: LOCK_THRESHOLD_UP defined further down as LOCK_THRESHOLD_UP_EFFECTIVE,
      //       computed alongside LOCK_THRESHOLD_DN_EFFECTIVE so both share the regime-aware logic.
      // V141 ROOT FIX: bullCount and bearCount now use the regime-aware effective thresholds
      //                (defined a few lines below). Previously bearCount used a separate
      //                hard-coded `(is15m?30:32) - sessAdj` that ignored regime and didn't include
      //                the session×regime adaptive adjustment. Result: even when the regime-specific
      //                effective threshold said "DOWN should fire at 36 in TD," the bear-counter
      //                was still using 30 to decide if recent ticks qualified. This was a silent
      //                bypass of the regime threshold logic.
      //                Defining bullCount/bearCount AFTER the effective thresholds.

      // ── IMPROVEMENT 2: Regime-gated consecutive requirement ──────────────
      // RANGE-CHOP is 56% WR (near coin flip) — needs one extra confirmation sample
      // TRENDING DOWN is 86% WR — can fire faster (2 samples)
      // ── IMPROVEMENT 3: Session multiplier on consecutive requirement ─────
      // US session 59% WR — needs one extra sample to reduce false locks
      const _regime=lastRegimeRef.current||'RANGE-CHOP';
      const _regimeConsecAdj=_regime==='RANGE-CHOP'?1:_regime==='HIGH VOL CHOP'?1:_regime==='TRENDING DOWN'?-1:0;
      const _sessConsecAdj=_sess==='US'?1:_sess==='OFF-HOURS'?1:0;
      // V113: Velocity-adaptive consecutive samples — slow markets need more confirmation, fast need less
      const _velSampleAdj=velocityScalars?.samples||1.0;
      // V134: Reduced base samples — Tara was waiting too long for confirmations
      // 15m: 3→2 samples (saves ~10s per call), 5m: 2→2 (no change, minimum)
      const _baseConsec=(is15m?2:2)+_regimeConsecAdj+_sessConsecAdj;
      // V134: Strong trajectory? Lock faster. Reduces sample requirement when direction is clear.
      const _trajStrength=Math.abs(eng.trajectoryAdj||0);
      const _trajShortcut=_trajStrength>=12?-2:_trajStrength>=8?-1:0;
      // V136: FGT alignment shortcut — FGT is now the primary signal, treat alignment
      //       the same way EXTREME velocity is treated. 4/4 = full fast-track (-2 samples),
      //       3/4 = partial (-1).
      const _fgtAlignAbs=Math.abs(eng.mtfAlignment||0);
      const _fgtShortcut=_fgtAlignAbs>=4?-2:_fgtAlignAbs>=3?-1:0;
      // V134: EXTREME velocity = lock at 1 sample regardless
      const _isExtreme=eng.velocityRegime==='EXTREME';
      // V136: FGT 4/4 alignment now also forces minimum-sample lock, matching EXTREME treatment
      const _isFgtFullAlign=_fgtAlignAbs>=4;
      // V134: PUSH-EARLIER — backtest shows sweet spot is 300-700s (73%+ WR) vs >820s (56.6% WR)
      // After 600s elapsed, drop sample requirement by 1 to grab setups before they degrade
      const _pastSweetSpot=clockSeconds>=600&&is15m;
      const _push=_pastSweetSpot?-1:0;
      // V135: SIGNAL CONSENSUS OVERRIDE — when 5+ of 6 raw signals strongly agree on a direction,
      //       drop the sample requirement by 1. The previous +12 quality bonus (line ~4048) was
      //       only helping the quality gate, not the sample-count gate. Both gates need the override.
      const _consensusUpStrong=Object.values(eng.rawSignalScores||{}).filter(s=>s>5).length>=5;
      const _consensusDnStrong=Object.values(eng.rawSignalScores||{}).filter(s=>s<-5).length>=5;
      const _consensusOverride=(_consensusUpStrong||_consensusDnStrong)?-1:0;
      // V146.1 LOOSEN 2: Strong-conviction shortcut. When calibrated posterior is already 10+ points
      //   beyond the regime threshold (e.g. UP threshold 62, posterior at 72+), the conviction is
      //   high enough that waiting a 2nd sample (~3 sec) is unnecessary. Joins the existing FGT 4/4
      //   and trajectory ±7 shortcuts. Doesn't loosen marginal locks — only fast-tracks obvious ones.
      const _strongConvictionUp=posterior>=72; // ≥10 above default 62 threshold
      const _strongConvictionDn=posterior<=22; // ≤10 below default 32 threshold
      const _convictionShortcut=(_strongConvictionUp||_strongConvictionDn)?-1:0;
      // V2.5: Sample-count floor raised 1 → 2 for non-extreme setups. The previous floor of 1
      //       meant that when multiple shortcuts piled on (FGT 3/4 + strong traj + signal consensus +
      //       high conviction = -4 against a baseline of 2), Tara could lock on a single tick.
      //       That produced "she answers instantly without taking a moment to understand the move."
      //       Now: at least 2 confirming samples required (~3-6s of confirmation) for ALL non-extreme
      //       setups. EXTREME velocity and FGT 4/4 keep the 1-sample fast track — those cases are
      //       genuinely obvious and don't need extra waiting.
      // V2.8: Extended fast-track. Was only EXTREME or FGT 4/4 → 1 sample. Now FGT 3/4 also fast-tracks.
      //       When 3 of 4 timeframes agree on direction, the primary signal is already strongly
      //       confirming — requiring 2 sample-ticks of agreement is redundant cautious-ness that
      //       made Tara miss valid setups. Marginal setups still need 2.
      const _isFgtStrongAlign=Math.abs(eng.mtfAlignment||0)>=3;
      const CONSECUTIVE_NEEDED=(_isExtreme||_isFgtFullAlign||_isFgtStrongAlign)?1:Math.max(2,Math.round(_baseConsec*_velSampleAdj)+_trajShortcut+_fgtShortcut+_push+_consensusOverride+_convictionShortcut);

      // ── IMPROVEMENT 4: Late lock penalty ─────────────────────────────────
      // Very late locks (>820s elapsed in 15m = last 80s) get suppressed
      // Data shows losses lock avg 777s vs wins 744s — very late = higher risk
      const elapsedSeconds=(is15m?900:300)-clockSeconds;
      const isVeryLateLock=is15m?(elapsedSeconds>800):(elapsedSeconds>250); // V112: tighter — losses cluster in last 100s
      // Late lock warning zone (700-820s elapsed) — show indicator, allow but note
      const isLateLockZone=is15m?(elapsedSeconds>700&&elapsedSeconds<=820):(elapsedSeconds>220&&elapsedSeconds<=260);
      // V13.4.249: hard lock deadline. No NEW lock may form once this many seconds
      //   remain -- clockSeconds counts DOWN, so this is "more than 7 minutes left"
      //   on a 15m window. Previously locks were merely penalised this late
      //   (isLateLockZone -8, isVeryLateLock -20) and still allowed until ~100s
      //   remained. An existing lock is untouched; this only blocks forming a new one.
      const _pastLockDeadline=clockSeconds<=callLockDeadline(windowType);

      // Add current posterior to history (capped at 12 samples)
      posteriorHistoryRef.current.push(posterior);
      if(posteriorHistoryRef.current.length>12)posteriorHistoryRef.current.shift();

      // Count consecutive bullish/bearish samples from recent history
      const recentHist=posteriorHistoryRef.current.slice(-6);
      // V134: POSTERIOR VOLATILITY CHECK — if posterior swung 20+ pts in last 30s, refuse lock
        // Catches the v5s-momentum-flip scenarios where trajectory whipsaws between UP and DOWN
        const _recentSpan=recentHist.length>=4?(Math.max(...recentHist.slice(-6))-Math.min(...recentHist.slice(-6))):0;
        const _posteriorVolatile=_recentSpan>=22;
        if(_posteriorVolatile&&clockSeconds>20){reasoning.push(`[STAB] Posterior volatile (${_recentSpan.toFixed(0)}pt range last 6 ticks) — locks suppressed`);}
      // bullCount/bearCount moved below — they need the effective thresholds, which depend on regime.

      // ── DOWN REGIME GATE ──────────────────────────────────────────────────
      // Data: DOWN in SHORT SQUEEZE = 50% WR (coin flip), DOWN in HVC = 48.4% WR (below coin flip)
      // Only trust DOWN in TRENDING DOWN (93%) and RANGE-CHOP (51%, still weak)
      // In SHORT SQUEEZE: DOWN requires 2× the normal consecutive samples AND stricter threshold
      // V138: SHORT SQUEEZE removed from _downGated. _upGated only includes HVC + RC,
      //       so this matches the UP side. The previous asymmetry made DOWN's lock threshold
      //       2 points tighter in SS (28 vs 30 / 26 vs 28).
      const _downGated=regime==='HIGH VOL CHOP'||regime==='RANGE-CHOP'; // V112: RC DOWN only 53% — gate it
      // V141: _downTDOnly variable removed — was unused after threshold rebuild.
      // V144 ROOT FIX: Threshold ladder rebuilt — UP side recalibrated for calibrated-posterior
      //                 space, DOWN side keeps V141 values since DOWN posterior calibration is null.
      //
      //   Mapping: raw → calibrated (UP side)
      //     raw 70 → cal 60  (was below 72, still below threshold mostly)
      //     raw 80 → cal 67  (used to clear most thresholds, now barely clears default 65)
      //     raw 90 → cal 87  (still strong)
      //
      //   New thresholds (in calibrated UP space, raw DOWN space):
      //     TRENDING DOWN  (favors DN)   UP ≥75 cal  DOWN ≤36 raw
      //     LONG SQUEEZE   (favors DN)   UP ≥75 cal  DOWN ≤36 raw
      //     RANGE-CHOP     (default)     UP ≥65 cal  DOWN ≤28 raw
      //     HIGH VOL CHOP  (default)     UP ≥65 cal  DOWN ≤28 raw
      //     TRENDING UP    (favors UP)   UP ≥60 cal  DOWN ≤20 raw
      //     SHORT SQUEEZE  (favors UP)   UP ≥60 cal  DOWN ≤20 raw
      //
      //   This maintains V143's firing balance (UP/DN ratio ~1.6x in seed) while making
      //   high-conf UP locks honest about their actual win probability.
      let _baseUpThr, _baseDnThr;
      // V3.2.4: TRENDING DOWN — apply UP threshold raise to lock gate. Was UP 75/DN 36.
      //   In a downtrending regime, UP should be very expensive. Now UP 85, DN 26 (let DOWN
      //   fire on more reasonable conviction in the regime designed to favor it).
      if(regime==='TRENDING DOWN'){_baseUpThr=is15m?85:83; _baseDnThr=is15m?26:28;}
      else if(regime==='LONG SQUEEZE'){_baseUpThr=is15m?75:73; _baseDnThr=is15m?36:34;}
      // V10.2.44 PHASE 4 — TIGHTENED TRENDING UP base threshold from 60/20 to 65/25.
      //   Audit: TRENDING UP had the WORST WR of real regimes (61.3% on n=93),
      //   yet had the LOOSEST UP threshold. The asymmetry was backwards. Now
      //   65/25 brings TU in line with the other regimes (closer to TD's 85/26
      //   and SS's 72/26). UP locks in TU now require slightly more conviction.
      //   Expected: TU WR rises from 61% toward 65-66%.
      else if(regime==='TRENDING UP'){_baseUpThr=is15m?65:63; _baseDnThr=is15m?25:27;}
      // V3.2.4: Apply the SS rebalance from V3.2.1 to the actual lock-gate thresholds.
      //   The line 2067 fix changed the regime classifier values but those are dead-ish; the
      //   real lock decision uses these `_baseUpThr` / `_baseDnThr` values. So V3.2.1's
      //   intent (UP 64→72, DN 22→26) wasn't actually being applied.
      //   SS in lock gate: UP 64 / DN 22 was 14pt up-bias asymmetry (UP only needed 14 from
      //   neutral, DN needed 28 — exactly 2× harder). Now UP 72 / DN 26: 22 vs 24 — symmetric.
      else if(regime==='SHORT SQUEEZE'){_baseUpThr=is15m?72:70; _baseDnThr=is15m?26:28;}
      // V2.8: RANGE-CHOP / HIGH VOL CHOP — symmetric thresholds. UP and DOWN now require equal conviction.
      //       Was UP 62 / DN 32 (12pt vs 18pt asymmetry favoring UP).
      //       Now UP 65 / DN 35 (both 15pt of conviction from neutral 50).
      //       Direct response to UP-bias pattern — UP fires too easily in unclear conditions.
      else {_baseUpThr=is15m?65:63; _baseDnThr=is15m?35:37;}
      // V10.2.40.2 — LOW-CONFIDENCE DAMPENING NOW APPLIES TO LOCK DECISIONS.
      //   V10.2.40 originally dampened upThreshold/downThreshold, but those vars
      //   are unused by the lock state machine (dead-variable note at line 8388).
      //   Real lock decisions read _baseUpThr/_baseDnThr from the regime-string
      //   ladder above. This fix applies dampening AFTER that ladder runs, BEFORE
      //   LOCK_THRESHOLD_UP/DN_EFFECTIVE is computed — so dampening now actually
      //   affects whether a lock fires.
      //
      //   When regime confidence < 45% AND regime is directional (not chop/highvol),
      //   blend _baseUpThr/_baseDnThr 50/50 toward neutral chop values. This makes
      //   it harder to commit to a directional lock when the regime classification
      //   is weakly supported by evidence.
      //
      //   Why not flip the regime to RANGE-CHOP instead: that would also affect
      //   regime weights, regime calibration, qScoreV2 — bigger surface, bigger
      //   risk. Dampening just the threshold is the minimum change to get the
      //   intended directional skepticism without disrupting other consumers.
      const _engConf=Number(eng._v10_2_40_regimeConfidence||0);
      const _engPrimary=eng._v10_2_40_primaryRegime||'';
      if(_engConf>0&&_engConf<0.45&&regime!=='RANGE-CHOP'&&regime!=='HIGH VOL CHOP'){
        const _neutralUp=is15m?65:63;
        const _neutralDn=is15m?35:37;
        const _origUp=_baseUpThr,_origDn=_baseDnThr;
        _baseUpThr=Math.round(_baseUpThr*0.5+_neutralUp*0.5);
        _baseDnThr=Math.round(_baseDnThr*0.5+_neutralDn*0.5);
        try{console.info('[V10.2.40.2 LOCK-DAMPEN] regime',regime,'conf',Math.round(_engConf*100)+'%','primary='+_engPrimary,'thresholds',_origUp+'/'+_origDn,'→',_baseUpThr+'/'+_baseDnThr);}catch(_){}
      }
      const LOCK_THRESHOLD_DN_EFFECTIVE=_baseDnThr-_sessThreshAdj-_srThreshAdj;
      const LOCK_THRESHOLD_UP_EFFECTIVE=_baseUpThr+_sessThreshAdj+_srThreshAdj;
      // V141: Now we can compute bullCount/bearCount with the regime-aware effective thresholds.
      const bullCount=_posteriorVolatile?0:recentHist.filter(p=>p>=LOCK_THRESHOLD_UP_EFFECTIVE).length;
      const bearCount=_posteriorVolatile?0:recentHist.filter(p=>p<=LOCK_THRESHOLD_DN_EFFECTIVE).length;
      // Rebalanced: DOWN was too hesitant (+2), UP was firing too fast in weak regimes
      // DOWN now: +1 extra in gated regimes (not +2). UP: +1 in HVC/RC weak UP regimes.
      // V134: Gated regimes get +1 unless trajectory is strong (then no penalty)
      // V134: Match UP's sample requirement — was +1 in gated regimes, dropped that
      // (the gated regimes already get tighter threshold + signal-consensus bar)
      const CONSECUTIVE_NEEDED_DN=CONSECUTIVE_NEEDED;
      // UP gate: HIGH VOL CHOP and RANGE-CHOP UP calls need one extra sample too (55-59% WR)
      const _upGated=regime==='HIGH VOL CHOP'||regime==='RANGE-CHOP';
      // V134: UP gate skipped only when UP trajectory (positive trajectoryAdj) — was symmetric
      // Strong DOWN trajectory should NOT speed up UP locks
      // V135: Threshold lowered 8 → 7 to match new ±12 trajectory cap and gate consistency
      // V139: FGT 4/4 UP also bypasses the +1 sample penalty. Parallel to trajectory bypass.
      const _trajFavorsUp=eng.trajectoryAdj>=7;
      const _fgtFavorsUp=(eng.mtfAlignment||0)>=4;
      const CONSECUTIVE_NEEDED_UP=_upGated&&!_trajFavorsUp&&!_fgtFavorsUp
        ? Math.max(2,CONSECUTIVE_NEEDED+1)
        : CONSECUTIVE_NEEDED;
      // V134: ADD symmetric DOWN gate skip when DOWN trajectory is strong
      // (mirror of UP behavior — don't slow legitimate DOWN moves)
      // V135: Threshold lowered -8 → -7 for symmetry with UP side
      // V139: FGT 4/4 DOWN also enables the fast-track in choppy regimes.
      const _trajFavorsDn=eng.trajectoryAdj<=-7;
      const _fgtFavorsDn=(eng.mtfAlignment||0)<=-4;
      const _dnFastTrack=(_trajFavorsDn||_fgtFavorsDn)&&(regime==='RANGE-CHOP'||regime==='HIGH VOL CHOP');
      const CONSECUTIVE_NEEDED_DN_EFFECTIVE=_dnFastTrack
        ? Math.max(1,CONSECUTIVE_NEEDED-1) // give DOWN same shortcut UP gets
        : CONSECUTIVE_NEEDED_DN;

      // ── Phase 1: Pre-lock ──
      if(!lockedCallRef.current){
        const avgRecent=recentHist.reduce((a,b)=>a+b,0)/(recentHist.length||1);

        // Track first FORMING direction this window — commit to it, no flipping
        // V134: Whipsaw guard — track recent direction commitment, refuse small-move flips
        if(!windowSignalDirRef.current){
          if(avgRecent>=58)windowSignalDirRef.current='UP';
          else if(avgRecent<=42)windowSignalDirRef.current='DOWN';
        } else {
          // V134: Whipsaw guard with post-release relaxation
          // While locked → require decisive posterior (>=70 / <=30) to flip
          // After release (lock cleared) → allow flip at modest posterior (>=58 / <=42)
          //   because lock-release-then-opposite-direction is a valid recovery pattern
          const wantFlip=(windowSignalDirRef.current==='DOWN'&&avgRecent>=58)||(windowSignalDirRef.current==='UP'&&avgRecent<=42);
          const decisive=avgRecent>=70||avgRecent<=30;
          const moderate=avgRecent>=58||avgRecent<=42;
          const noActiveLock=!lockedCallRef.current;
          const allowedNow=decisive||(moderate&&noActiveLock);
          if(wantFlip&&!allowedNow){
            reasoning.push(`[WHIPSAW] Refusing flip from ${windowSignalDirRef.current} — posterior ${avgRecent.toFixed(0)} not decisive`);
          } else if(wantFlip&&allowedNow){
            reasoning.push(`[FLIP] Direction flip allowed at posterior ${avgRecent.toFixed(0)}${noActiveLock?' (post-release)':' (decisive)'}`);
            windowSignalDirRef.current=avgRecent>=50?'UP':'DOWN';
          }
        }
        const committedDir=windowSignalDirRef.current; // null until first FORMING signal

        // V140: DELIBERATION GATE REMOVED — was inverted (used `clockSeconds<X` where clockSeconds
        //       is time REMAINING, so it fired near the END of the window instead of the start, where
        //       the endgame freeze at clockSeconds<90 already blocks). Effectively dead code since V134
        //       — locks have been firing freely from second 1 (subject to all other gates) and we never
        //       missed the protection. Removing for clarity.
        // V134: Late lock allowed if trajectory is very strong (legitimate late breakouts)
        // V139: FGT 4/4 alignment also bypasses this. With FGT promoted to primary signal in V136,
        //       a 4/4 alignment in the late window is exactly the kind of high-conviction setup we want
        //       to lock — even if the kinematic trajectory is ambivalent.
        // V13.4.327 REMOVED: this outer isVeryLateLock/_trajStrength/_fgtAlignAbs
        //   check used to skip UP AND DOWN evaluation entirely (both branches
        //   below are `else if` off this same condition) whenever the window
        //   was very late without an unusually strong trajectory/FGT read --
        //   a hard block on lock formation woven into the shared decision
        //   chain, not a separable gate. Per the explicit instruction to let
        //   Tara lock on her own timing, removed so both directions are
        //   always evaluated on their own merits, at any point in the window.
        if(bullCount>=CONSECUTIVE_NEEDED_UP&&posterior>=LOCK_THRESHOLD_UP_EFFECTIVE&&!isEndgameLock&&!(eng.mtfAlignment!==undefined&&eng.mtfAlignment<=-3)){
          // V146.1 FIX A: Added `posterior>=LOCK_THRESHOLD_UP_EFFECTIVE` to mirror DOWN side's
          //   existing check. Previously UP could fire when historical samples cleared the
          //   threshold even if current posterior had since dropped back. DOWN required both
          //   simultaneously. This created a small UP-favoring asymmetry in wobbling markets.
          //   Now both directions require: samples-cleared AND current-posterior-still-confirming.
          // V134 extra logging: announce when MTF would've blocked
          if(eng.mtfAlignment!==undefined&&eng.mtfAlignment<=-2&&eng.mtfAlignment>-3)reasoning.push(`[MTF-WARN] UP lock proceeding but ${Math.abs(eng.mtfAlignment).toFixed(1).replace(/\.0$/,'')}/5 timeframes against UP`);
          // ── Quality gate: suppress lock if score too low ──
          const _rm=regimeMemory[regime]||{wins:0,losses:0};
          const _rt=_rm.wins+_rm.losses;
          const _rWR=_rt>5?(_rm.wins/_rt)*100:60;
          const _sessQ={'EU':67,'ASIA':62,'US':57,'OFF-HOURS':55}[_sess]||57;
          const _dsAdj=getMarketSessions().dsAdj||0; // V114: day×session quality bonus/penalty
          const _streakAdj=streakData?.warning?(streakData.strongWarn?-15:-8):(streakData?.type==='hot'&&streakData?.streak>=4?+4:0); const _dirBiasUp=streakData?.upBias||0; if(_dirBiasUp!==0)reasoning.push(`[DIR-BIAS] UP recent WR adj: ${_dirBiasUp}`); // V134
          // V13.4.327 REMOVED: the V134 "surgical late-FOMO penalty" (-15 to
          //   -25 off the quality score whenever the lock formed late in a
          //   choppy regime without unanimous signal agreement) -- a timing-
          //   based quality penalty, removed alongside the hard block above
          //   per the same instruction. _isChoppyRegime is still used below
          //   by the unrelated choppy-quality-floor check, so it stays.
          const _isChoppyRegime=regime==='RANGE-CHOP'||regime==='SHORT SQUEEZE'||regime==='HIGH VOL CHOP';
          const _lateFomoPenalty=0;
          const _qScore=Math.min(40,Math.max(0,(Math.abs(posterior-50)-10)*1.6))+Math.min(30,(_rWR-50)*0.6)+Math.min(15,(_sessQ-50)*0.6)+_dsAdj+_streakAdj+_lateFomoPenalty+_dirBiasUp+(eng.windowDriftBps>25?-Math.min(12,eng.windowExhaustionPenalty||0):0)+(eng.realGapBps<-25?-Math.min(20,Math.abs(eng.realGapBps)*0.4):0)+(newsSentiment?(newsSentiment.score>2?+5:newsSentiment.score<-2?(-8):0):0)+(newsSentiment?.geoRisk>=0.5?-Math.round(8*newsSentiment.geoRisk):0)+(()=>{
            // V134: Sentiment-Trajectory interaction
            const tAdj=eng.trajectoryAdj||0;
            const sScore=newsSentiment?.score||0;
            // Both agree bullish: bonus
            if(tAdj>5&&sScore>2)return+5;
            // Strong disagreement (one says UP, other says DOWN)
            if(tAdj>5&&sScore<-2)return-8;
            if(tAdj<-5&&sScore>2)return-5;
            return 0;
          })(); // V114-V134: news sentiment + trajectory interaction for UP
          // V134: Signal consensus boost — when 5+ signals strongly agree, this IS A+
          const _consensusUp=Object.values(eng.rawSignalScores||{}).filter(s=>s>5).length;
          const _consensusBoost=_consensusUp>=5?+12:_consensusUp>=4?+5:0;
          if(_consensusBoost>0)reasoning.push(`[CONSENSUS] ${_consensusUp}/6 UP signals strongly agree — quality boost +${_consensusBoost}`);
          // V136: FGT QUALITY BOOST — primary signal contributes directly to quality gate.
          //       4/4 UP = +18 quality, 3/4 UP = +10, 2/4 UP = +4. Mirrors signal consensus boost.
          //       The score-side boost (mtfBonus) raises posterior, this raises quality so the
          //       same trade clears the choppy-regime quality≥62 gate.
          const _fgtUpQuality=(eng.mtfAlignment||0)>=4?18:(eng.mtfAlignment||0)>=3?10:(eng.mtfAlignment||0)>=2?4:0;
          if(_fgtUpQuality>0)reasoning.push(`[FGT-Q] FGT ${eng.mtfAlignment.toFixed(1)}/4 UP — quality boost +${_fgtUpQuality}`);
          // V145: Geopolitical / macro risk haircut. When dual-source news scanner picks up
          //       elevated geo/macro keywords with breaking-news recency, apply small quality penalty.
          //       Light mode — no full lockout.
          if(newsSentiment?.geoRisk>=0.5){
            const _geoCut=Math.round(8*newsSentiment.geoRisk);
            const _topic=(newsSentiment.geoTopic||'').slice(0,80);
            reasoning.push(`[GEO-RISK] News scanner flagged ${(newsSentiment.geoRisk*100).toFixed(0)}% risk — quality −${_geoCut}${_topic?' · '+_topic:''}`);
          }
          const _quality=Math.max(0,Math.min(100,_qScore+5+_consensusBoost+_fgtUpQuality));
          // V114: Macro event check — BLACKOUT and OBSERVE = no new locks
          const _macroUP=getMacroEventState();
          if(_macroUP.state==='BLACKOUT'){
            taraAdviceRef.current=`MACRO BLACKOUT (${_macroUP.event.name} in ${_macroUP.minutesUntil}m)`;
          } else if(_macroUP.state==='OBSERVE'){
            taraAdviceRef.current=`OBSERVING ${_macroUP.event.name}`;
          } else if(newsSentiment?.hasBreaking){
            taraAdviceRef.current='BREAKING NEWS — OBSERVE';
          } else if(_quality<45){
            taraAdviceRef.current='LOW QUALITY — SITTING OUT';
          } else if((eng.trajectoryAdj||0)<=-7&&(eng.mtfAlignment||0)<4){
            // V3.1.3: V2.8 FGT support gate REMOVED. Data audit on 56 trades showed it would have
            //   blocked 29 UP trades winning at 62% — ABOVE the overall 64% WR. The original 5-loss
            //   cluster (02:51-05:09) was a temporal artifact, not a structural failure mode.
            //   Gating against it was overcorrection that suppressed a winning category of trades.
            //   Other V2.8 changes (symmetric thresholds, SHORT SQUEEZE UP raised to 64,
            //   fast-track extended to FGT 3/4) remain in place — those addressed real measured
            //   asymmetries.
            // V135: Boundary fix — was `< -8`, missed trajectoryAdj exactly -8 (rare but possible).
            //       Threshold tightened -8 → -7 to match the symmetric DOWN side and respect the new ±12 cap.
            // V139: FGT 4/4 UP overrides this rejection. When all four timeframes scream UP,
            //       a backward-looking trajectory shouldn't be allowed to block the lock.
            taraAdviceRef.current='UP REJECTED — trajectory says DOWN';
            reasoning.push(`[GATE] UP blocked: trajectory ${eng.trajectoryAdj.toFixed(0)} contradicts UP call`);
          } else if(_isChoppyRegime&&_quality<50){
            // V134: choppy regime quality floor
            // V139: 62 → 55
            // V146.1 LOOSEN 3: 55 → 50. With FGT now contributing +18 to quality (V136), strong-FGT
            //       setups already clear ~73 quality on their own. Lowering to 50 mainly affects
            //       non-FGT marginal setups in choppy regimes — modest expansion.
            taraAdviceRef.current='WEAK SETUP — '+regime+' needs stronger signal';
            reasoning.push(`[GATE] UP blocked: quality ${_quality.toFixed(0)} < 50 in ${regime}`);
          } else {
          // ── Direction flip guard: if FORMING DOWN already fired, don't lock UP ──
          // V138: Premium Mode and the MTF Confluence gate it carried have been removed.
          //       Premium added asymmetric blocks (US session skip, weak RC skip, SS/HVC DOWN block,
          //       MTF cross-window veto). With FGT as the primary signal these were over-filtering.
          const dirAllowed=(!committedDir||committedDir==='UP')&&!_pastLockDeadline;
          if(!dirAllowed){
            taraAdviceRef.current='SEARCHING...';
          } else {
            // V113+V134: Momentum confirmation with velocity-adaptive tolerance
            // If trajectory strongly favors UP (trajectoryAdj > 5), be more permissive
            const recent=(velocityRef?.current?.v5s||0);
            const _momTol=velocityScalars?.momentumTol||0.5;
            const _trajFavorsUp=(eng.trajectoryAdj||0)>5;
            const _effectiveMomTol=_trajFavorsUp?_momTol*2.5:_momTol; // wider tolerance when trajectory agrees
            const momentumOK=recent>=-_effectiveMomTol;
            if(!momentumOK){
              taraAdviceRef.current='UP - WAITING FOR MOMENTUM';
            } else if(Date.now()-lockReleasedAtRef.current<(confluenceStateRef.current.isConfluent?15000:45000)){
              // V5.6.11: post-release cooldown — must scan for 45s before re-locking.
              // V5.7.8: shortened to 15s when current candidate is confluent — orthogonal-signal
              //   agreement is strong enough evidence to re-engage faster after a release.
              const _coolMs=confluenceStateRef.current.isConfluent?15000:45000;
              taraAdviceRef.current='UP - SCANNING (post-release cooldown)';
              const _wait=Math.ceil((_coolMs-(Date.now()-lockReleasedAtRef.current))/1000);
              reasoning.push(`[LOCK] UP candidate held — cooldown ${formatDuration(_wait)} remaining${confluenceStateRef.current.isConfluent?' (shortened — confluence)':''}`);
            } else {
              // V9.9.4: compute reversalRisk INLINE at engine-lock formation. Pre-V9.9.4
              //   the risk was only computed at the lifecycle commit path (L23597) — but
              //   that's a separate gate that fires later and isn't always reached. Result:
              //   the chip on TaraCallCard never displayed because lockedCallRef.current
              //   was set here without reversalRisk and the lifecycle path's mutation came
              //   too late (or not at all) for the user to see. Compute it here so the chip
              //   appears the moment the lock confirms — before the user even sees the
              //   "UP - CONFIRMED" status.
              const _ec=eng?._ctx||{};
              const _ucTier=_ec.isStructuralLed?'structural-led':
                            _ec.isSuperConfluent?'super-confluence':
                            _ec.isTapeLed?'tape-led':
                            _ec.isConfluent?'confluence':
                            _ec.isRisingConfluence?'rising-confluence':
                            'single';
              const _rrUp=computeReversalRisk({
                tier:_ucTier,
                regime:regime||'',
                dir:'UP',
                qScore:Math.round(_ec.q||eng?.q||0),
                mtfAlignment:_ec.fgtAbs?(eng?.mtfAlignment||0):(eng?.mtfAlignment),
                secondsIntoWindow:windowOpenTimeRef.current?Math.round((Date.now()-windowOpenTimeRef.current)/1000):null,
                whaleLog:typeof whaleLog!=='undefined'?whaleLog:[],
                dayContext:typeof dayContext!=='undefined'?dayContext:null,
              });
              lockedCallRef.current={dir:'UP',lockedAt:Date.now(),_committedAt:Date.now(),lockedPosterior:posterior,lockedRegime:regime,lockPrice:currentPrice,isLateLock:isLateLockZone,lockedSignals:eng.rawSignalScores?{...eng.rawSignalScores}:null,reversalRisk:_rrUp,tier:_ucTier,windowId:computeWindowId(windowType)}; // V134/V9.1.3: snapshot signals at lock + first-write-wins stamp; V9.9.5: tier persisted; V9.17.17: windowId stamped so auto-exec can reject stale locks from prior windows
              taraAdviceRef.current='UP - CONFIRMED';
              biasCountRef.current={UP:0,DOWN:0};
              _persistLock(); // V5.6: cloud-save so refresh restores this lock
            }
          }
          } // close quality gate else

        // V137: SS/HVC DOWN block is now FGT-aware. Was unconditional unless rug-pull or 25+ bps adverse.
        //       Now: also overrideable by FGT 3/4+ DOWN alignment. With V136 FGT-as-primary and V135 SS
        //       price-action veto, the V112 assumption that "DOWN-in-SS is a coinflip" no longer holds —
        //       FGT alignment is strong enough evidence to overcome regime hesitancy.
        } else if((bearCount>=CONSECUTIVE_NEEDED_DN_EFFECTIVE||(isRugPull&&posterior<=45))&&posterior<=LOCK_THRESHOLD_DN_EFFECTIVE&&!isEndgameLock&&!(eng.mtfAlignment!==undefined&&eng.mtfAlignment>=3)){
          // V134: BACKTEST FINDING — DOWN locks in HVC/SS are coin flips (50% WR over 40 trades)
          // Refuse unless rug-pull active or significant gap below strike (>25bps)
          if(eng.mtfAlignment!==undefined&&eng.mtfAlignment>=2&&eng.mtfAlignment<3)reasoning.push(`[MTF-WARN] DOWN lock proceeding but ${eng.mtfAlignment.toFixed(1).replace(/\.0$/,'')}/5 timeframes against DOWN`);
          // ── Quality gate for DOWN ──
          const _rm2=regimeMemory[regime]||{wins:0,losses:0};
          const _rt2=_rm2.wins+_rm2.losses;
          const _rWR2=_rt2>5?(_rm2.wins/_rt2)*100:60;
          const _sessQ2={'EU':67,'ASIA':62,'US':57,'OFF-HOURS':55}[_sess]||57;
          const _dsAdj2=getMarketSessions().dsAdj||0;
          const _streakAdj2=streakData?.warning?(streakData.strongWarn?-15:-8):(streakData?.type==='hot'&&streakData?.streak>=4?+4:0); const _dirBiasDn=streakData?.dnBias||0; if(_dirBiasDn!==0)reasoning.push(`[DIR-BIAS] DOWN recent WR adj: ${_dirBiasDn}`);
          // V13.4.327 REMOVED: DOWN-side mirror of the UP-side late-FOMO
          //   penalty removal above -- same reasoning, same instruction.
          //   _isChoppyRegime2 stays; the unrelated choppy-quality-floor
          //   check below still uses it.
          const _isChoppyRegime2=(regime==='RANGE-CHOP'||regime==='SHORT SQUEEZE'||regime==='HIGH VOL CHOP')&&regime!=='TRENDING DOWN';
          const _lateFomoPenalty2=0;
          const _qScore2=Math.min(40,Math.max(0,(Math.abs(posterior-50)-10)*1.6))+Math.min(30,(_rWR2-50)*0.6)+Math.min(15,(_sessQ2-50)*0.6)+_dsAdj2+_streakAdj2+_lateFomoPenalty2+_dirBiasDn+(eng.windowDriftBps<-25?-Math.min(12,eng.windowExhaustionPenalty||0):0)+(eng.realGapBps>25?-Math.min(20,Math.abs(eng.realGapBps)*0.4):0)+(newsSentiment?(newsSentiment.score<-2?+5:newsSentiment.score>2?(-8):0):0)+(newsSentiment?.geoRisk>=0.5?-Math.round(8*newsSentiment.geoRisk):0)+(()=>{
            const tAdj=eng.trajectoryAdj||0;
            const sScore=newsSentiment?.score||0;
            // Both agree bearish: bonus
            if(tAdj<-5&&sScore<-2)return+5;
            if(tAdj<-5&&sScore>2)return-8;
            if(tAdj>5&&sScore<-2)return-5;
            return 0;
          })(); // V114-V134: news sentiment + trajectory interaction for DOWN (inverted)
          const _consensusDn=Object.values(eng.rawSignalScores||{}).filter(s=>s<-5).length;
          const _consensusBoost2=_consensusDn>=5?+12:_consensusDn>=4?+5:0;
          if(_consensusBoost2>0)reasoning.push(`[CONSENSUS] ${_consensusDn}/6 DOWN signals strongly agree — quality boost +${_consensusBoost2}`);
          // V136: FGT QUALITY BOOST mirroring UP side
          const _fgtDnQuality=(eng.mtfAlignment||0)<=-4?18:(eng.mtfAlignment||0)<=-3?10:(eng.mtfAlignment||0)<=-2?4:0;
          if(_fgtDnQuality>0)reasoning.push(`[FGT-Q] FGT ${eng.mtfAlignment.toFixed(1)}/4 DOWN — quality boost +${_fgtDnQuality}`);
          // V145: Geopolitical / macro risk haircut (mirror of UP-side).
          if(newsSentiment?.geoRisk>=0.5){
            const _geoCut2=Math.round(8*newsSentiment.geoRisk);
            const _topic2=(newsSentiment.geoTopic||'').slice(0,80);
            reasoning.push(`[GEO-RISK] News scanner flagged ${(newsSentiment.geoRisk*100).toFixed(0)}% risk — quality −${_geoCut2}${_topic2?' · '+_topic2:''}`);
          }
          const _quality2=Math.max(0,Math.min(100,_qScore2+5+_consensusBoost2+_fgtDnQuality));
          const _macroDN=getMacroEventState();
          if(_macroDN.state==='BLACKOUT'){
            taraAdviceRef.current=`MACRO BLACKOUT (${_macroDN.event.name} in ${_macroDN.minutesUntil}m)`;
          } else if(_macroDN.state==='OBSERVE'){
            taraAdviceRef.current=`OBSERVING ${_macroDN.event.name}`;
          } else if(newsSentiment?.hasBreaking){
            taraAdviceRef.current='BREAKING NEWS — OBSERVE';
          } else if(_quality2<45){
            taraAdviceRef.current='LOW QUALITY — SITTING OUT';
          } else if((eng.trajectoryAdj||0)>=7&&(eng.mtfAlignment||0)>-4){
            // V3.1.3: V2.8 DOWN-side FGT support gate REMOVED, mirroring UP-side removal.
            //   Audit showed 3 DOWN-gated trades all won (100% WR). Same conclusion as UP side —
            //   the gate was overcorrection.
            // V135: Boundary fix mirroring UP side — was `> 8`, tightened to `>= 7`.
            // V139: FGT 4/4 DOWN overrides this rejection (mtfAlignment <= -4).
            taraAdviceRef.current='DOWN REJECTED — trajectory says UP';
            reasoning.push(`[GATE] DOWN blocked: trajectory ${eng.trajectoryAdj.toFixed(0)} contradicts DOWN`);
          } else if(_isChoppyRegime2&&_quality2<50){
            // V134/V139/V146.1: choppy quality floor 62 → 55 → 50 (mirror of UP side).
            taraAdviceRef.current='WEAK SETUP — '+regime+' needs stronger signal';
            reasoning.push(`[GATE] DOWN blocked: quality ${_quality2.toFixed(0)} < 50 in ${regime}`);
          } else {
          // ── Direction flip guard: if FORMING UP already fired, don't lock DOWN ──
          // V138: Removed the SS/HVC DOWN-block that required rug-pull / 25+bps adverse / FGT-3of4
          //       (V112 → V137 evolution). Was the last asymmetric block; UP had no equivalent.
          //       Score math (regime bonus, FGT contribution, signal alignment) is now the only
          //       directional filter. Premium Mode + MTF Confluence gate also removed.
          const dirAllowed=(!committedDir||committedDir==='DOWN')&&!_pastLockDeadline;
          if(!dirAllowed){
            taraAdviceRef.current='SEARCHING...';
          } else {
            // V113+V134: Trajectory-aware momentum confirmation
            const recent=(velocityRef?.current?.v5s||0);
            const _momTol=velocityScalars?.momentumTol||0.5;
            const _trajFavorsDn=(eng.trajectoryAdj||0)<-5;
            const _effectiveMomTol=_trajFavorsDn?_momTol*2.5:_momTol;
            const momentumOK=recent<=_effectiveMomTol;
            if(!momentumOK){
              taraAdviceRef.current='DOWN - WAITING FOR MOMENTUM';
            } else if(Date.now()-lockReleasedAtRef.current<(confluenceStateRef.current.isConfluent?15000:45000)){
              // V5.7.8: shortened cooldown on confluence — see UP path comment.
              const _coolMs=confluenceStateRef.current.isConfluent?15000:45000;
              taraAdviceRef.current='DOWN - SCANNING (post-release cooldown)';
              const _wait=Math.ceil((_coolMs-(Date.now()-lockReleasedAtRef.current))/1000);
              reasoning.push(`[LOCK] DOWN candidate held — cooldown ${formatDuration(_wait)} remaining${confluenceStateRef.current.isConfluent?' (shortened — confluence)':''}`);
            } else {
              // V9.9.4: same inline reversalRisk computation as UP path above.
              const _ecD=eng?._ctx||{};
              const _dcTier=_ecD.isStructuralLed?'structural-led':
                            _ecD.isSuperConfluent?'super-confluence':
                            _ecD.isTapeLed?'tape-led':
                            _ecD.isConfluent?'confluence':
                            _ecD.isRisingConfluence?'rising-confluence':
                            'single';
              const _rrDown=computeReversalRisk({
                tier:_dcTier,
                regime:regime||'',
                dir:'DOWN',
                qScore:Math.round(_ecD.q||eng?.q||0),
                mtfAlignment:eng?.mtfAlignment,
                secondsIntoWindow:windowOpenTimeRef.current?Math.round((Date.now()-windowOpenTimeRef.current)/1000):null,
                whaleLog:typeof whaleLog!=='undefined'?whaleLog:[],
                dayContext:typeof dayContext!=='undefined'?dayContext:null,
              });
              lockedCallRef.current={dir:'DOWN',lockedAt:Date.now(),_committedAt:Date.now(),lockedPosterior:posterior,lockedRegime:regime,lockPrice:currentPrice,isLateLock:isLateLockZone,lockedSignals:eng.rawSignalScores?{...eng.rawSignalScores}:null,rugPullLock:isRugPull,reversalRisk:_rrDown,tier:_dcTier,windowId:computeWindowId(windowType)}; // V9.9.5: tier persisted; V9.17.17: windowId stamped so auto-exec can reject stale locks from prior windows
              if(isRugPull&&bearCount<CONSECUTIVE_NEEDED_DN)reasoning.push(`[RUG-FIRE] Rug pull detected — DOWN locked early at posterior ${posterior.toFixed(0)}`); // V134: snapshot signals at lock
              taraAdviceRef.current='DOWN - CONFIRMED';
              biasCountRef.current={UP:0,DOWN:0};
              _persistLock(); // V5.6: cloud-save so refresh restores this lock
            }
          }
          } // close quality gate else

        } else {
          // V140: SMART DELIBERATION REMOVED — same inverted-variable issue as the lock-side gate.
          //       Was using `clockSeconds<_delibMin` which fires near end of window (clockSeconds = remaining).
          //       Removed to clean up the misleading "DELIBERATING — Xs left" display.
          // V134: SIT-OUT requires multiple confirmations of "no edge"
          // Not just weak signals — also weak FGT alignment AND no significant gap to strike
          const _upConsensus=Object.values(eng.rawSignalScores||{}).filter(s=>s>3).length;
          const _dnConsensus=Object.values(eng.rawSignalScores||{}).filter(s=>s<-3).length;
          const _signalsWeak=_upConsensus<3&&_dnConsensus<3;
          const _midPosterior=avgRecent>=44&&avgRecent<=56; // V134: tightened from 42-58 to 44-56
          const _pastDeliberation=timeFraction>0.35;
          // V134: FGT must also be ambiguous (≤2 timeframes aligned)
          const _fgtAmbiguous=Math.abs(eng.mtfAlignment||0)<=2;
          // V134: Gap must be small (price near strike, no clear directional pressure)
          const _gapSmall=Math.abs(eng.realGapBps||0)<20;
          const _shouldSitOut=_pastDeliberation&&_midPosterior&&_signalsWeak&&_fgtAmbiguous&&_gapSmall;
          // V134: Sticky sit-out releases on ANY clear signal
          // Old version only released on extreme posterior (>=68/<=32) — too strict
          // New: any of 3 conditions exit sit-out: posterior break, FGT clear, big gap
          // V136: FGT is primary — 2/4 alignment is enough to break out of sit-out (was 3/4).
          //       Even a moderate FGT signal should reset Tara from a sticky sit-out state and
          //       let her re-evaluate in the FGT-aligned direction.
          const _fgtClear=Math.abs(eng.mtfAlignment||0)>=2;
          const _gapBig=Math.abs(eng.realGapBps||0)>=25;
          const _posteriorBreak=avgRecent>=60||avgRecent<=40;
          const _exitSitOut=_fgtClear||_gapBig||_posteriorBreak;
          if(taraAdviceRef.current?.includes('SITTING OUT')&&!_exitSitOut){
            taraAdviceRef.current='SITTING OUT — Mixed signals, no edge';
          } else if(taraAdviceRef.current?.includes('SITTING OUT')&&_exitSitOut){
            // Exiting sit-out — log why and let the rest of the logic re-evaluate
            const reason=_fgtClear?`FGT now ${eng.mtfAlignment>0?'UP':'DOWN'} ${Math.abs(eng.mtfAlignment).toFixed(1).replace(/\.0$/,'')}/4`:
                         _gapBig?`gap widened to ${eng.realGapBps.toFixed(0)}bps`:
                         `posterior moved to ${avgRecent.toFixed(0)}`;
            reasoning.push(`[SIT-OUT-EXIT] Releasing sit-out: ${reason}`);
            // Don't set state here — fall through to FORMING/SEARCHING below
          // V13.4.327 REMOVED: `else if(isVeryLateLock){taraAdviceRef.current=
          //   'NO CALL';}` -- explicitly refused to keep searching/analyzing
          //   once very late in the window even when neither direction had
          //   cleared its sample threshold yet. Removed so this case falls
          //   through to the normal sit-out/analyzing logic below instead of
          //   a timing-based hard stop.
          } else if(_shouldSitOut){
            taraAdviceRef.current='SITTING OUT — Mixed signals, no edge';
            reasoning.push(`[SIT-OUT] Mid-window posterior ${avgRecent.toFixed(0)}, consensus UP:${_upConsensus} DN:${_dnConsensus} — no edge`);
          } else {
            // V141: ANALYZING state with bounded search window.
            // For the first ~5-60s of a window (5m = 5-30s, 15m = 5-60s), explicitly show
            // ANALYZING instead of FORMING/SEARCHING. This signals to the trader that Tara is
            // actively scanning for direction. After the search window expires, fall through
            // to the regular FORMING/SEARCHING state machine.
            const _elapsedSec=Math.max(0,(is15m?900:300)-clockSeconds);
            const _searchMin=is15m?5:5;
            const _searchMax=is15m?60:30;
            // FGT alignment shortens the search — we already know the direction
            const _fgtAlignAbs2=Math.abs(eng.mtfAlignment||0);
            const _searchEnd=_fgtAlignAbs2>=4?_searchMin:_fgtAlignAbs2>=3?(_searchMin+10):_searchMax;
            const _inSearch=_elapsedSec<_searchEnd&&!eng.isRugPull&&eng.velocityRegime!=='EXTREME';
            if(_inSearch){
              const _secsRem=Math.max(1,_searchEnd-_elapsedSec);
              const _hint=avgRecent>=55?' · leaning UP':avgRecent<=45?' · leaning DOWN':'';
              taraAdviceRef.current=`ANALYZING — searching for signal [${_secsRem}s]${_hint}`;
            } else if(avgRecent>=58&&!isEndgameLock){
              taraAdviceRef.current=`UP (FORMING)${isLateLockZone?' LATE':''}`;
            } else if(avgRecent<=42&&!isEndgameLock){
              taraAdviceRef.current=`DOWN (FORMING)${isLateLockZone?' LATE':''}`;
            } else {
              taraAdviceRef.current='SEARCHING...';
            }
          }
        }
      }

      // ── Phase 2: Post-lock — check only EXTREME unlock conditions ──
      if(lockedCallRef.current){
        const lock=lockedCallRef.current;
        const gapBps=targetMargin>0?((currentPrice-targetMargin)/targetMargin)*10000:0;
        // V134: Posterior decay tracking
        const lockedPost=lock.lockedPosterior||50;
        const postDelta=lock.dir==='UP'?(lockedPost-posterior):(posterior-lockedPost);
        // V151: Posterior decay release with severity-tiered post-lock-stability handling.
        //
        //   Two thresholds:
        //     - SEVERE (postDelta >= 25): bypasses the 30s post-lock-stability window entirely.
        //       A 25+ pt collapse in <30s isn't noise — it's the underlying score moving against
        //       the lock fast. Holding through this is how Tara stayed in the V150 bad-lock case
        //       (locked at 87%, dropped to 57% within seconds, was stuck waiting for the 30s
        //       stability window to expire while gap turned adverse).
        //     - SOFT (postDelta >= 15): still requires 30s stability — small drops near lock time
        //       can be momentum-tick noise that recovers. Keeps prior V134/V135 behavior here.
        const _sinceLockMs=Date.now()-(lock.lockedAt||Date.now());
        const _postLockStable=_sinceLockMs>30000;
        const _severeCollapse=postDelta>=35; // V5.5.5: 25 → 35, less twitchy
        const decayCollapse=_severeCollapse||(postDelta>=22&&_postLockStable); // V5.5.5: 15 → 22
        // V135: Deep-adverse threshold lowered 55 → 30 bps. 55 bps on $75K BTC is a $400 move —
        //       by the time you're that far wrong, the round is already lost. 30 bps is enough
        //       adverse to recognize a broken call while still being above tick noise.
        // V3.1.7+/V3.1.9: Window-type-aware. Each window character has different normal-noise
        //   boundaries. WHIPSAW is the most extreme — adverse moves of 30-40bps are noise here,
        //   not signal. DEAD is the tightest — anything is meaningful. Per-type thresholds:
        //     WHIPSAW    50bps  — high amplitude with constant reversals; releasing on noise destroys winners
        //     SPIKE-FADE 25bps  — early move exhausted, late adverse moves real (front-loaded action)
        //     TRENDING   35bps  — established direction; tighter than WHIPSAW but wider than NORMAL
        //     LATE-BREAK 30bps  — standard once break is underway
        //     RANGE      20bps  — mean-reversion windows punish wrong-side commitment
        //     GRIND      30bps  — slow trends, normal default
        //     DEAD       15bps  — every move is signal, release fast
        //     OPENING    30bps  — fallback default
        const _ampLabel=eng.windowAmplitude?.label||'NORMAL';
        const _deepWrongBps=
          _ampLabel==='WHIPSAW'?50:
          _ampLabel==='TRENDING'?35:
          _ampLabel==='SPIKE-FADE'?25:
          _ampLabel==='RANGE'?20:
          _ampLabel==='DEAD'?15:
          /* LATE-BREAK / GRIND / OPENING / NORMAL */ 30;
        const deepWrong=(lock.dir==='UP'&&gapBps<-_deepWrongBps)||(lock.dir==='DOWN'&&gapBps>_deepWrongBps);
        const catastrophicRugpull=(isRugPull&&showRugPullAlerts&&lock.dir==='UP')||(isRugPull&&lock.dir==='UP'&&posterior<10);
        // V140: Mirror — catastrophic upward spike releases DOWN locks immediately.
        //       Faster than waiting for deepWrong (gap > 30 bps adverse). Symmetric to rug-pull.
        const catastrophicSpike=(eng.isMoonshot&&lock.dir==='DOWN')||(eng.isMoonshot&&lock.dir==='DOWN'&&posterior>90);
        // V5.5.5: Soft-flip releases tightened. User reports 'locks and releases in a min
        //   or so' on the engine general prediction. Each soft release now requires more
        //   decisive evidence before firing. Safety releases (rugpull/spike/deep adverse)
        //   left unchanged — those are real reversals.
        let signalRegimeChange=false;
        let flippedSignals=[];
        if(lock.lockedSignals&&eng.rawSignalScores&&_postLockStable){
          const lockSign=lock.dir==='UP'?1:-1;
          let flipCount=0;
          ['gap','momentum','structure','flow','technical','regime'].forEach(k=>{
            const at=lock.lockedSignals[k]||0;
            const now=eng.rawSignalScores[k]||0;
            const wasWith=Math.sign(at)===lockSign&&Math.abs(at)>3;
            const nowAgainst=Math.sign(now)===-lockSign&&Math.abs(now)>3;
            if(wasWith&&nowAgainst){flipCount++;flippedSignals.push(k);}
          });
          // V5.5.5: 4+ signals flipped (was 3+). Requires stronger consensus reversal.
          if(flipCount>=4)signalRegimeChange=true;
        }
        // V5.5.5: Trajectory flip threshold ±7 → ±10. Higher bar to release.
        const trajFlipAgainst=(lock.dir==='UP'&&(eng.trajectoryAdj||0)<=-10&&_postLockStable)||
                              (lock.dir==='DOWN'&&(eng.trajectoryAdj||0)>=10&&_postLockStable);
        // V5.5.5: FGT flip threshold 2/4 → 3/4. Harder to flip out.
        const fgtFlipAgainst=_postLockStable&&(
          (lock.dir==='UP'&&(eng.mtfAlignment||0)<=-3)||
          (lock.dir==='DOWN'&&(eng.mtfAlignment||0)>=3)
        );
        // V5.5.5: Regime revalidation tightened — trajectory must lean more decisively against.
        const _bullishRegimes=['SHORT SQUEEZE','TRENDING UP'];
        const _bearishRegimes=['TRENDING DOWN'];
        const _lockedInBullishRg=_bullishRegimes.includes(lock.lockedRegime||'');
        const _lockedInBearishRg=_bearishRegimes.includes(lock.lockedRegime||'');
        const _nowInBullishRg=_bullishRegimes.includes(regime);
        const _nowInBearishRg=_bearishRegimes.includes(regime);
        const _trajLeansDn=(eng.trajectoryAdj||0)<=-8; // V5.5.5: -5 → -8
        const _trajLeansUp=(eng.trajectoryAdj||0)>=8;  // V5.5.5: +5 → +8
        const regimeRevalidation=_postLockStable&&(
          (lock.dir==='UP'&&_lockedInBullishRg&&!_nowInBullishRg&&_trajLeansDn)||
          (lock.dir==='DOWN'&&_lockedInBearishRg&&!_nowInBearishRg&&_trajLeansUp)
        );
        if(deepWrong||catastrophicRugpull||catastrophicSpike||decayCollapse||signalRegimeChange||trajFlipAgainst||fgtFlipAgainst||regimeRevalidation){
          // V9.8.14: STICKY LOCK (Option A) — release reasons are now advisor-only signals.
          //   The lock itself NEVER clears mid-window. Pre-V9.8.14 these conditions ran
          //   `lockedCallRef.current = null` + 45s cooldown + allowed an opposite-direction
          //   re-lock in the same window. User feedback: "i don't want tara's call to flip
          //   her lock once locked." Now: every Tara call lives the full window and resolves
          //   on its committed direction at expiry. Cleaner W/L data, no whipsaw.
          //   The user-facing warnings still fire — V9.8.13 made the in-trade coach read
          //   live posterior, so TARA REVERSED / STRUCTURE BROKE / CONDITIONS ERODING all
          //   surface from the live-engine path independently of this block.
          //   We DO record the would-have-released signals on the lock object as a history
          //   array, useful for CSV audit ("the engine wanted to release at +6:42 — was the
          //   trade actually compromised at that moment?").
          if(!lockedCallRef.current.releaseSignalsHistory)lockedCallRef.current.releaseSignalsHistory=[];
          lockedCallRef.current.releaseSignalsHistory.push({
            at:Date.now(),
            secsIntoLock:Math.round((Date.now()-(lockedCallRef.current.lockedAt||Date.now()))/1000),
            deepWrong:!!deepWrong,catastrophicRugpull:!!catastrophicRugpull,
            catastrophicSpike:!!catastrophicSpike,decayCollapse:!!decayCollapse,
            signalRegimeChange:!!signalRegimeChange,trajFlipAgainst:!!trajFlipAgainst,
            fgtFlipAgainst:!!fgtFlipAgainst,regimeRevalidation:!!regimeRevalidation,
            posteriorAtSignal:Math.round(posterior),
            gapBpsAtSignal:Math.round(gapBps),
            trajAtSignal:eng.trajectoryAdj!=null?Math.round(eng.trajectoryAdj*10)/10:null,
            fgtAtSignal:eng.mtfAlignment!=null?Math.round(eng.mtfAlignment*10)/10:null,
          });
          // Cap history at 50 entries to keep call-log size bounded
          if(lockedCallRef.current.releaseSignalsHistory.length>50){
            lockedCallRef.current.releaseSignalsHistory=lockedCallRef.current.releaseSignalsHistory.slice(-50);
          }
          if(catastrophicRugpull)reasoning.push(`[STICKY-LOCK] Would have released (catastrophic rug pull) — lock held`);
          else if(catastrophicSpike)reasoning.push(`[STICKY-LOCK] Would have released (catastrophic upward spike) — lock held`);
          else if(signalRegimeChange)reasoning.push(`[STICKY-LOCK] Would have released (signal regime flipped: ${flippedSignals.join(',')}) — lock held`);
          else if(decayCollapse)reasoning.push(`[STICKY-LOCK] Would have released (posterior decayed ${postDelta.toFixed(0)} pts) — lock held`);
          else if(trajFlipAgainst)reasoning.push(`[STICKY-LOCK] Would have released (trajectory ${(eng.trajectoryAdj||0).toFixed(0)} flipped against ${lock.dir}) — lock held`);
          else if(fgtFlipAgainst)reasoning.push(`[STICKY-LOCK] Would have released (FGT now ${(eng.mtfAlignment||0)>0?'UP':'DOWN'} ${Math.abs(eng.mtfAlignment||0).toFixed(1).replace(/\.0$/,'')}/4 against ${lock.dir}) — lock held`);
          else if(regimeRevalidation)reasoning.push(`[STICKY-LOCK] Would have released (${lock.lockedRegime} regime gone) — lock held`);
          else if(deepWrong)reasoning.push(`[STICKY-LOCK] Would have released (adverse gap ${gapBps.toFixed(0)} bps) — lock held`);
        }
        // Always — hold the lock, regardless of any signals above. Lock is sticky.
        taraAdviceRef.current=lock.dir==='UP'?'UP - CONFIRMED':'DOWN - CONFIRMED';
      }

      // ── V9.8.14: MID-TRADE TIME-SERIES CAPTURE ─────────────────────────────
      //   Logs a snapshot every ~5s while a Tara call is locked. Foundation for
      //   V9.9.0 predictive reversal training data and post-hoc audit of "would-
      //   have-released" sticky-lock signals. No UI change, no behavior change —
      //   pure data accumulation. Each snapshot ~80 bytes; capped at 200 entries
      //   (~17 minutes of 5s sampling, well over a 15m window) to bound memory.
      //   Resolution merge (line ~18860) copies this onto the call-log entry at
      //   window expiry so it lands in the CSV/JSON exports.
      //
      // V10.2.19 — CAPTURE FROM SNAPSHOT TOO. The original V9.8.14 capture only
      //   fired when lockedCallRef.current was set (engine lock). But ~81% of
      //   resolved trades commit via the SNAPSHOT path (ng-snap / cap-snap /
      //   mix-snap / kalshi-snap) which sets taraCallSnapshotRef.current but
      //   NOT lockedCallRef.current. Capture never started for those trades —
      //   timeSeriesLen=0 on 588 of 724 trades in May 3-15 audit, breaking
      //   maxFavorable/AdverseExcursionBps + smart-exit validation downstream.
      //
      //   Fix: capture whenever EITHER ref is set. Store onto whichever ref is
      //   available (preferring lockedCallRef when present for backward compat;
      //   falling back to snapshotRef otherwise). Resolution merge reads from
      //   both refs and uses whichever has data.
      const _captureTarget=lockedCallRef.current||taraCallSnapshotRef.current;
      if(_captureTarget){
        const _now=Date.now();
        if(_now-_lastTimeSeriesAtRef.current>=5000){
          _lastTimeSeriesAtRef.current=_now;
          // lockedAt fallback: engine lock has .lockedAt, snapshot has _committedAt
          //   or atSecondsLeft-derived. Use whatever's available, default to now
          //   (means first sample is at t=0).
          const _lockedAt=_captureTarget.lockedAt
            ||_captureTarget._committedAt
            ||_captureTarget.commitTime
            ||_now;
          if(!_captureTarget.timeSeries)_captureTarget.timeSeries=[];
          if(_captureTarget.timeSeries.length<200){
            const _kPctTs=kalshiYesPrice!=null&&Number.isFinite(kalshiYesPrice)?Number(kalshiYesPrice):null;
            _captureTarget.timeSeries.push({
              t:Math.round((_now-_lockedAt)/1000), // seconds since lock/commit
              p:Math.round(posterior),
              g:Math.round(realGapBps||0),
              k:_kPctTs!=null?Math.round(_kPctTs):null,
              f:eng.mtfAlignment!=null?Math.round(eng.mtfAlignment*10)/10:null,
              tr:eng.trajectoryAdj!=null?Math.round(eng.trajectoryAdj*10)/10:null,
              rg:regime||null,
            });
            // V9.11.2: persist to localStorage on every push. Audit showed timeSeries
            //   had only 9% coverage on resolved entries — runtime-only state was lost
            //   on refresh / cloud-adoption mid-trade, leaving the array empty at
            //   resolution. localStorage write is cheap (~16KB max for full 200-sample
            //   array, well under quota). Restored on app load via the lock-restore
            //   effect so accumulated samples survive across refreshes.
            // V10.2.19: persist whichever ref we just pushed to. Cloud-restore reads
            //   into lockedCallRef on adoption, but for snapshot-only commits we still
            //   persist locally so capture survives refresh.
            try{
              const _persistShape={
                lockedAt:_lockedAt,
                dir:_captureTarget.dir||_captureTarget.call||null,
                windowId:_captureTarget.windowId||computeWindowId(windowType),
                ts:_captureTarget.timeSeries,
                source:lockedCallRef.current===_captureTarget?'engineLock':'snapshot',
              };
              localStorage.setItem('taraLockedTimeSeries_v1',JSON.stringify(_persistShape));
            }catch(_){/* quota exceeded or storage unavailable — silent fail OK */}
          }
        }
      }

      // ── Phase 3: Endgame freeze ──
      if(isEndgameLock){
        if(!taraAdviceRef.current.includes('CONFIRMED')&&taraAdviceRef.current!=='SIGNAL CLEARED'){
          taraAdviceRef.current='NO CALL'; // too late to lock
        }
      }

      // ── User manual override always wins ──
      // V137: When user is in a position, surface Tara's internal lock state so they can see
      //       if she's released or is screaming exit. Was: forced display to UP/DOWN-LOCKED
      //       regardless of internal state, which masked release events from the trader.
      let activePrediction;
      if(userPosition!==null){
        const _internal=taraAdviceRef.current||'';
        if(_internal==='SIGNAL CLEARED'){
          activePrediction=userPosition==='UP'?'UP - SIGNAL CLEARED':'DOWN - SIGNAL CLEARED';
        } else {
          activePrediction=userPosition==='UP'?'UP - CONFIRMED':'DOWN - CONFIRMED';
        }
      } else {
        activePrediction=taraAdviceRef.current;
      }
      // V6.1.3: ENGINE STICKINESS. Once engine commits UP/DOWN CONFIRMED in a window,
      //   that direction is locked. Internal state can keep flipping freely, but the
      //   displayed activePrediction stays committed. The only exception: if internal
      //   state shows the OPPOSITE direction is now CONFIRMED, show EXIT WARNING — never
      //   flip the call. Capture first commit so subsequent renders enforce stickiness.
      // V7.2.1: ONLY enforce stickiness when the user actually has a position. Without a
      //   position, the user wants live updates of Tara's current view — they're not
      //   committed to anything, so "EXIT WARNING" is meaningless and confusing. The
      //   previous behavior was: engine briefly confirms UP, captures stickiness, then
      //   flips to DOWN → display shows "UP - EXIT WARNING" while header says SITTING OUT.
      if(userPosition){
        if(engineLockedDirRef.current===null){
          if(activePrediction==='UP - CONFIRMED')engineLockedDirRef.current='UP';
          else if(activePrediction==='DOWN - CONFIRMED')engineLockedDirRef.current='DOWN';
        } else if(engineLockedDirRef.current==='UP'){
          if(activePrediction.includes('DOWN - CONFIRMED')||activePrediction.includes('DOWN (FORMING)')){
            activePrediction='UP - EXIT WARNING'; // engine reversed, alert user but don't flip
          } else if(activePrediction.includes('CLOSED')||activePrediction.includes('SIT OUT')){
            // Allow CLOSED/SIT OUT — these are user-initiated terminal states
          } else {
            activePrediction='UP - CONFIRMED'; // anything else (FORMING/CLEARED/ANALYZING) — stay committed
          }
        } else if(engineLockedDirRef.current==='DOWN'){
          if(activePrediction.includes('UP - CONFIRMED')||activePrediction.includes('UP (FORMING)')){
            activePrediction='DOWN - EXIT WARNING';
          } else if(activePrediction.includes('CLOSED')||activePrediction.includes('SIT OUT')){
            // Allow terminal states
          } else {
            activePrediction='DOWN - CONFIRMED';
          }
        }
      } else {
        // V7.2.1: User has no position. Drop any stale stickiness so a fresh window
        //   doesn't display a phantom EXIT WARNING from a prior engine commit.
        engineLockedDirRef.current=null;
      }
      let textColor='text-zinc-500';
      if(activePrediction.includes('UP - CONFIRMED'))textColor='text-emerald-400';
      else if(activePrediction.includes('DOWN - CONFIRMED'))textColor='text-rose-400';
      else if(activePrediction.includes('EXIT WARNING'))textColor='text-amber-400 animate-pulse'; // V6.1.3: clear visual alert
      else if(activePrediction.includes('ANALYZING'))textColor='text-indigo-400';
      else if(activePrediction.includes('SITTING OUT'))textColor='text-amber-400';
      else if(activePrediction.includes('UP (FORMING)'))textColor='text-emerald-600';
      else if(activePrediction.includes('DOWN (FORMING)'))textColor='text-rose-600';
      else if(activePrediction==='SIGNAL CLEARED')textColor='text-amber-400';
      // V138: PREMIUM: textColor branch removed — Premium Mode no longer exists

      const isUP=activePrediction.includes('UP'),isDN=activePrediction.includes('DOWN');
      const currentOdds=(!isUP&&!isDN)?50:(isUP?posterior:(100-posterior));
      const offerVal=parseFloat(currentOffer)||0;
      const liveEstValue=isUP?maxPayout*(posterior/100):(isDN?maxPayout*((100-posterior)/100):0);
      const livePnL=offerVal>0?(offerVal-betAmount):(liveEstValue-betAmount);
      let kellyPct=0;
      if((isUP||isDN)&&betAmount>0&&maxPayout>betAmount){const b=(maxPayout-betAmount)/betAmount;const p=currentOdds/100;const k=((p*b)-(1-p))/b;kellyPct=Math.max(0,(k/2)*100);}

      // V110 Smart Advisor — lock-state-aware
      const _advisorResult=computeAdvisor({userPosition,positionStatus,currentOdds,offerVal,betAmount,maxPayout,clockSeconds,windowType,tickSlope,isRugPull,showRugPullAlerts,hasReversedRef,peakOfferRef,posterior,targetMargin,currentPrice,minsRemaining:timeState.minsRemaining,secsRemaining:timeState.secsRemaining,accel,pnlSlope,atrBps,activePrediction,regime,lockInfo:lockedCallRef.current?{dir:lockedCallRef.current.dir,lockedAt:lockedCallRef.current.lockedAt,lockedPosterior:lockedCallRef.current.lockedPosterior,lockPrice:lockedCallRef.current.lockPrice,lockRegime:lockedCallRef.current.lockedRegime}:null,
        // V6.2.8: Pass Tara's state — what she's locked, what she'd lean if scanning, what she
        //   sat out on. Used for two things:
        //     (1) When user has no position: minimal display showing just Tara's confidence/edge
        //     (2) When user has a manual position: cross-reference. Alert if Tara opposes; show
        //         her view inline; let advisor optimize the user's chosen direction.
        // V6.2.9 TDZ FIX: taraCall is declared AFTER this useMemo block. Referencing it inside
        //   this closure created a temporal-dead-zone ReferenceError ("Cannot access 'We' before
        //   initialization") which crashed the entire predictor. Now we only reference refs
        //   and in-scope variables — taraCall isn't needed here because the snapshot ref carries
        //   the committed state, and posterior is the live lean value.
        taraSnapshot:taraCallSnapshotRef.current?{
          call:taraCallSnapshotRef.current.call,
          direction:taraCallSnapshotRef.current.direction,
          confidence:taraCallSnapshotRef.current.confidence,
          atPosterior:taraCallSnapshotRef.current.atPosterior,
          kalshiAtLock:taraCallSnapshotRef.current.kalshiAtLock,
          isUserForced:taraCallSnapshotRef.current.isUserForced,
          // V13.4.275: `locked` was NOT copied here. The advisor then had no way to
          //   tell a committed lock from an uncommitted snapshot, so it inferred one
          //   from direction alone and printed "Locked DOWN" with an ENTER button on
          //   a round Tara had not committed. Same object, one missing field, two
          //   panels contradicting each other on screen.
          locked:taraCallSnapshotRef.current.locked,
          // Carried so the advisor can express confidence the same way THIS TRADE
          //   does (50 + conviction) instead of re-deriving it from atPosterior and
          //   landing on a different number for the same call.
          conviction:taraCallSnapshotRef.current.conviction,
        }:null,
        // Tara's current lean during scanning — derived from sample ref (claimed direction)
        //   and posterior conviction. taraCallSampleRef is a ref so safe to access here.
        taraLean:(()=>{
          const _claimedDir=taraCallSampleRef.current?.dir;
          const _conv=Math.abs((posterior||50)-50);
          if(_claimedDir&&_conv>=5)return{dir:_claimedDir,conviction:_conv};
          return null;
        })(),
        // Tara's current posterior (same as engine posterior — she derives from this)
        taraPosterior:posterior,
        // Live Kalshi for edge math
        kalshiYesPrice:typeof kalshiYesPrice!=='undefined'?kalshiYesPrice:null,
        // V9.7.6: pass Kalshi YES price history (60s rolling) so the advisor can
        //   read short-term price velocity. Smarter HOLD/EXIT advice based on whether
        //   Kalshi is moving with us or against us, not just internal conviction.
        kalshiHist:kalshiHistoryRef?.current||[],
        // V6.4: structural exhaustion signal for active exit alert
        convictionAsymmetry:eng.convictionAsymmetry||null,
      });

      // V145.2: HPotter-based projections for 5m / 15m / 1h panels
      //
      //   The previous projection model used a flat linear extrapolation:
      //     price = currentPrice * (1 + drift1m_bps_per_min * minutes_ahead)
      //   That ignores Tara's actual primary signal (FGT) and produces straight-line forecasts
      //   that disconnect from the engine's posterior call.
      //
      //   New model: blend HPotter Future Grand Trend with linear drift, weighted by
      //   FGT validity and proximity to the FGT's forecast horizon.
      //
      //   Mapping:
      //     5min panel  → 1m FGT (forecast horizon ~30 min)
      //     15min panel → 1m FGT extended (~30 min) + 3m FGT bias
      //     1hour panel → 3m FGT (forecast horizon ~90 min)
      //
      //   For each timestep t minutes ahead:
      //     - Compute FGT-implied price by linear interpolation from now → FGT horizon
      //     - Compute linear-drift price (legacy fallback)
      //     - Blend: weight=0.7 FGT when FGT valid + within horizon, else 0
      // V149: Projection rebuild — three sanity layers added to fix the "BTC moons by morning" bug.
      //
      //   1. VOLATILITY CAP: Cap projected move magnitude at 1.5× recent ATR per hour. ATR-based
      //      ceiling stops the linear-drift hockey-stick from running away. If projection wants to
      //      go +977bps over 7 hours but ATR says BTC's typical 1h range is ±50bps, we cap.
      //
      //   2. CONFIDENCE PATH-FADE: Multiply projected move by (confidence/100)^1.5. When confidence
      //      drops to 60%, the visible move shrinks to ~46% of raw HPotter target. This makes the
      //      bar visualization match Tara's actual certainty — no more big bars on low-confidence rows.
      //
      //   3. PANEL HORIZON LIMITS: 1-hour panel previously projected 8 hours forward. 3m HPotter
      //      forecast horizon is only 90 min. Now: 5m panel keeps 8 steps (fits within 1m HPotter's
      //      30 min horizon), 15m panel cap at 6 steps (90 min, within 3m HPotter), 1-hour panel
      //      cap at 3 steps (3 hours, with explicit "extrapolated" warning past horizon).
      const getHP=(msAgo)=>{const t=Date.now()-msAgo;const m=priceMemoryRef.current;if(!m||m.length===0)return currentPrice;let c=m[0];for(let i=m.length-1;i>=0;i--){if(m[i].time<=t){c=m[i];break;}}return c.p;};
      let trendBps=isNaN(drift1m)?0:drift1m;
      if(isUP&&trendBps<=0)trendBps=2;if(isDN&&trendBps>=0)trendBps=-2;

      // ATR-based 1-hour volatility envelope. Typical BTC 1h move is ~ATR × √60 (Brownian scaling).
      // V149: we use this to cap how far projections can drift from current price.
      const _atrNow=eng.atrBps||15; // fallback 15bps
      const oneHourEnvelopeBps=_atrNow*Math.sqrt(60); // ≈ 116bps typical 1h range when ATR=15
      // Absolute cap multiplier — projection can't exceed 1.5× the typical 1h envelope per hour.
      const VOL_CAP_MULT=1.5;

      // Pull HPotter forecasts for the projection horizons
      const fgt1m=eng.fgtResults?.['1m'];   // forecast ~30 min ahead
      const fgt3m=eng.fgtResults?.['3m'];   // forecast ~90 min ahead
      const fgt5m=eng.fgtResults?.['5m'];   // forecast ~200 min ahead — used for 1h panel as backup
      const fgt1mHorizonMin=30;
      const fgt3mHorizonMin=90;
      const fgt5mHorizonMin=200;

      // Returns a price for `minAhead` minutes from now, blending HPotter + linear drift,
      // capped by ATR-volatility envelope and faded by confidence.
      const projectPrice=(minAhead,primaryFgt,primaryHorizon,fallbackFgt,fallbackHorizon,confidence)=>{
        // Linear-drift baseline
        const linearPriceRaw=currentPrice*(1+(trendBps/10000)*minAhead);
        // Decide which FGT to use
        let fgt=null,horizon=null;
        if(primaryFgt&&primaryFgt.valid&&minAhead<=primaryHorizon*1.1){
          fgt=primaryFgt;horizon=primaryHorizon;
        } else if(fallbackFgt&&fallbackFgt.valid){
          fgt=fallbackFgt;horizon=fallbackHorizon;
        }
        let projectedPrice;
        if(!fgt||!fgt.valid){
          projectedPrice=linearPriceRaw;
        } else {
          const t=Math.min(1,minAhead/horizon);
          const fgtPrice=currentPrice+(fgt.fcast-currentPrice)*t;
          const fgtWeight=minAhead<=horizon?0.7:Math.max(0.3,0.7-((minAhead-horizon)/horizon)*0.4);
          projectedPrice=fgtPrice*fgtWeight+linearPriceRaw*(1-fgtWeight);
        }
        // V149 LAYER 1 — VOLATILITY CAP
        // Compute proposed move in bps
        const proposedMoveBps=((projectedPrice-currentPrice)/currentPrice)*10000;
        // Cap by typical 1h envelope, scaled by minutes-ahead. Sub-linear growth (sqrt) to reflect
        // that volatility doesn't accumulate linearly.
        const maxAbsMoveBps=VOL_CAP_MULT*oneHourEnvelopeBps*Math.sqrt(minAhead/60);
        const clampedMoveBps=Math.max(-maxAbsMoveBps,Math.min(maxAbsMoveBps,proposedMoveBps));
        // V149 LAYER 2 — CONFIDENCE PATH-FADE
        // Pull projected move toward zero based on Tara's confidence.
        // 90% conf → 0.85× weight, 70% conf → 0.59×, 60% conf → 0.46×, 50% conf → 0.35×.
        const confFactor=Math.pow(Math.max(0.3,(confidence||50)/100),1.5);
        const finalMoveBps=clampedMoveBps*confFactor;
        return currentPrice*(1+finalMoveBps/10000);
      };

      // Per-panel FGT routing + horizon limits
      const genTimeline=(min,steps,primaryFgt,primaryHorizon,fallbackFgt,fallbackHorizon,baseConf)=>{
        const out=[],iMs=min*60*1000,now=Date.now();
        let nT=Math.ceil(now/iMs)*iMs;
        if(nT-now<iMs*0.1)nT+=iMs;
        // V8.9.3: 3-way time format
        const tz=timeFormat==='utc'?{timeZone:'UTC'}:timeFormat==='est'?{timeZone:'America/New_York'}:undefined;
        for(let i=0;i<steps;i++){
          const sT=nT+(i*iMs);
          const minAhead=(sT-now)/60000;
          // Per-row confidence decays with distance from now
          const rowConf=Math.max(30,baseConf-i*4); // -4% per step
          const p=projectPrice(minAhead,primaryFgt,primaryHorizon,fallbackFgt,fallbackHorizon,rowConf);
          const d=new Date(sT);
          let ts=`${d.getHours().toString().padStart(2,'0')}:${d.getMinutes().toString().padStart(2,'0')}`;
          try{ts=d.toLocaleTimeString('en-US',{...tz,hour12:true,hour:'numeric',minute:'2-digit'});}catch(e){}
          // V149 LAYER 3: Mark rows beyond model horizon as 'extrapolated'
          const extrapolated=minAhead>primaryHorizon&&(!fallbackFgt||!fallbackFgt.valid||minAhead>fallbackHorizon);
          out.push({timeStr:ts,timestamp:Math.floor(sT/1000),price:p,extrapolated});
        }
        return out;
      };
      // V149: 5-MIN — 8 steps × 5min = 40 min total (well within 1m HPotter horizon)
      // V149: 15-MIN — 6 steps × 15min = 90 min total (matches 3m HPotter horizon — was 8 steps = 120 min)
      // V149: 1-HOUR — 3 steps × 60min = 180 min total (within 3m HPotter horizon — was 8 steps = 480 min!)
      const t5=genTimeline(5,8,fgt1m,fgt1mHorizonMin,fgt3m,fgt3mHorizonMin,Math.min(95,posterior+5));
      const t15=genTimeline(15,6,fgt1m,fgt1mHorizonMin,fgt3m,fgt3mHorizonMin,posterior);
      const t60=genTimeline(60,3,fgt3m,fgt3mHorizonMin,fgt5m,fgt5mHorizonMin,Math.max(40,posterior-15));
      const projections=[
        {id:'5m',time:'5 MIN',price:t5[0]?.price||currentPrice,conf:Math.min(95,posterior+5),timeline:t5,fgtSrc:fgt1m?.valid?'1m HPotter':'linear'},
        {id:'15m',time:'15 MIN',price:t15[0]?.price||currentPrice,conf:posterior,timeline:t15,fgtSrc:fgt1m?.valid?'1m HPotter→3m':'linear'},
        {id:'1h',time:'1 HOUR',price:t60[0]?.price||currentPrice,conf:Math.max(40,posterior-15),timeline:t60,fgtSrc:fgt3m?.valid?'3m HPotter':'linear'},
      ];

      // Multi-timeframe confluence: check if the OTHER timeframe has a recent lock in same direction
      const _otherTF=windowType==='15m'?'5m':'15m';
      const _otherLock=mtfLocksRef.current[_otherTF];
      const _thisLock=lockedCallRef.current;
      const mtfAligned=_thisLock&&_otherLock&&_otherLock.dir===_thisLock.dir&&(Date.now()-_otherLock.lockedAt)<20*60*1000;
      const mtfOpposed=_thisLock&&_otherLock&&_otherLock.dir!==_thisLock.dir&&(Date.now()-_otherLock.lockedAt)<20*60*1000;
      // V138: expose session info for UI badge
      const _sessInfo=getMarketSessions();
      // V7.2: Confidence ceiling at 85%. Actual WR is ~64% across all trades, with high-conf
      //   super-confluence trades historically peaking around 80-85%. A "99% confidence" lock
      //   that loses 35% of the time is calibration theater. Cap displayed confidence at 85%
      //   to give honest signal. Engine still uses raw posterior internally for decisions; this
      //   only affects the displayed number on the call card and broadcasts.
      const _rawConfNum=isDN?(100-posterior):posterior;
      const _displayConf=Math.min(85,Math.max(0,_rawConfNum));
      return{confidence:String(_displayConf.toFixed(1)),prediction:String(activePrediction),textColor:String(textColor),rawProbAbove:Number(posterior),regime:String(regime),session:String(_sessInfo.dominant),sessionDayRating:String(_sessInfo.dsRating),sessionDayKey:String(_sessInfo.dsKey),sessionDayAdj:Number(_sessInfo.dsAdj),velocityRegime:String(velocityRegime||'NORMAL'),trajectoryAdj:Number(eng.trajectoryAdj||0),projectedPrice:Number(eng.projectedPrice||0),projectedGapBps:Number(eng.projectedGapBps||0),reasoning,atrBps:Number(atrBps),realGapBps:Number(realGapBps),clockSeconds:Number(clockSeconds),isSystemLocked:Boolean(isEndgameLock),isPostDecay:Boolean(isPostDecay),isRugPull:Boolean(isRugPull),bb,livePnL:Number(livePnL),liveEstValue:Number(liveEstValue),kellyPct:Number(kellyPct),projections,advisor:_advisorResult,currentOdds:Number(currentOdds),aggrFlow:Number(aggrFlow),isEarlyWindow:Boolean(isEarlyWindow),consecutive:eng.consecutive,volRatio:Number(eng.volRatio),mtfAligned:Boolean(mtfAligned),mtfOpposed:Boolean(mtfOpposed),isLateLockZone:Boolean(isLateLockZone),isVeryLateLock:Boolean(isVeryLateLock),consecutiveNeeded:Number(CONSECUTIVE_NEEDED),
        // V10.9.2 FIX: surface _refPrice (BRTI-corrected reference price) so code
        //   outside this useMemo (_logSnapshotEntry, tier-multiplier block in the
        //   lock state machine) can read analysis.referencePrice instead of
        //   referencing the out-of-scope local _refPrice — which would throw
        //   ReferenceError at runtime on every lock (caught here before ship).
        referencePrice:Number(_refPrice)||Number(currentPrice)||0,
        // V13.4.100: V101 shadow posterior, computed above where its inputs are in scope.
        v101Shadow:_v101Shadow,
        confluence:_confluence,
        // V148.1: surface rawSignalScores and mtfAlignment to consumers (V147 Score Breakdown
        //         panel was reading these but they weren't in the return — every bar showed 0).
        mtfAlignment:eng.mtfAlignment,
        fgtResults:eng.fgtResults||{},
        // V6.2.0: structural indicators surfaced for Tara's gate + score breakdown UI
        trendChannel:eng.trendChannel||null,
        grandTrend:eng.grandTrend||null,
        // V6.2.1: multi-TF alignment across all structural signals (1m/5m/15m × 2 indicators)
        structAlignment:eng.structAlignment||null,
        // V6.3.5: surface raw signal scores + tape windows for log analysis
        rawSignalScores:eng.rawSignalScores||null,
        learningFeatures:eng.learningFeatures||null,
        tapeWindows:tapeWindows||null,
        // V9.7.9: diagnostic state from V9.7.x gates — stamped on trade log entries
        //   for post-hoc audit ("did the cap fire?" / "did the damper change the lock?").
        diagnosticsV97:eng.diagnosticsV97||null,
        // V10.2.36-40 — Regime detection improvement stamps. Pulled through from
        //   the engine return so the call log entry committer can persist them.
        //   These enable the post-hoc audit snippets in the dashboard:
        //   "did the range overrides fire in this trade? did it win?"
        _v10_2_36_rangeOverrideFired:eng._v10_2_36_rangeOverrideFired===true,
        _v10_2_36_rangeOverrideAvgBps:eng._v10_2_36_rangeOverrideAvgBps??null,
        _v10_2_37_quietOverrideFired:eng._v10_2_37_quietOverrideFired===true,
        _v10_2_37_quietOverrideRatio:eng._v10_2_37_quietOverrideRatio??null,
        _v10_2_37_quietRealizedBps:eng._v10_2_37_quietRealizedBps??null,
        _v10_2_37_quietExpectedBps:eng._v10_2_37_quietExpectedBps??null,
        _v10_2_37_mtfUpAligned:eng._v10_2_37_mtfUpAligned===true,
        _v10_2_37_mtfDnAligned:eng._v10_2_37_mtfDnAligned===true,
        _v10_2_38_srRangeDetected:eng._v10_2_38_srRangeDetected===true,
        _v10_2_38_srRangeBps:eng._v10_2_38_srRangeBps??null,
        _v10_2_38_srTouchesHigh:eng._v10_2_38_srTouchesHigh??0,
        _v10_2_38_srTouchesLow:eng._v10_2_38_srTouchesLow??0,
        _v10_2_38_srRangeHigh:eng._v10_2_38_srRangeHigh??null,
        _v10_2_38_srRangeLow:eng._v10_2_38_srRangeLow??null,
        _v10_2_39_htfFlatOverride:eng._v10_2_39_htfFlatOverride===true,
        _v10_2_39_htfVetoFired:eng._v10_2_39_htfVetoFired===true,
        _v10_2_39_htf1hDriftBps:eng._v10_2_39_htf1hDriftBps??null,
        _v10_2_39_htf4hDriftBps:eng._v10_2_39_htf4hDriftBps??null,
        _v10_2_39_htf1hLabel:eng._v10_2_39_htf1hLabel||null,
        _v10_2_39_htf4hLabel:eng._v10_2_39_htf4hLabel||null,
        _v10_2_40_regimeProbs:eng._v10_2_40_regimeProbs||null,
        _v10_2_40_regimeConfidence:eng._v10_2_40_regimeConfidence??null,
        _v10_2_40_primaryRegime:eng._v10_2_40_primaryRegime||null,
        _v10_2_40_dampened:eng._v10_2_40_dampened===true,
        _v10_2_43_regimeMult:eng._v10_2_43_regimeMult??1.0,
        _v10_2_43_structOrig:eng._v10_2_43_structOrig??0,
        _v10_2_43_techOrig:eng._v10_2_43_techOrig??0,
        _v10_2_44_haircutApplied:eng._v10_2_44_haircutApplied||null,
        _v10_2_44_postBeforeHaircut:eng._v10_2_44_postBeforeHaircut??null,
        _v10_2_50_chopAmpDelta:eng._v10_2_50_chopAmpDelta??0,
        _v10_2_50_htfChopBoost:eng._v10_2_50_htfChopBoost||null,
        _v10_2_51_chopConfluence:eng._v10_2_51_chopConfluence||null,
        _v10_3_0_rangeData:eng._v10_3_0_rangeData||null,
        _v10_3_0_rangeFadeFired:eng._v10_3_0_rangeFadeFired||null,
        _v10_3_0_midRangeSitout:eng._v10_3_0_midRangeSitout===true,
        _v10_3_0_rangeBroken:eng._v10_3_0_rangeBroken===true,
        _v10_3_1_coinFlip:eng._v10_3_1_coinFlip||null,
        _v10_3_1_trendingUpPenalty:eng._v10_3_1_trendingUpPenalty||null,
        _v10_3_3_rangeHybrid:eng._v10_3_3_rangeHybrid||null,
        _v10_4_0_calibration:eng._v10_4_0_calibration||null,
        _v10_4_0_regimeCaps:eng._v10_4_0_regimeCaps||null,
        _v10_4_3_windowCtx:eng._v10_4_3_windowCtx||null,
        // V9.10.10 → V9.11.0: pass through pattern + futures telemetry blocks so the
        //   entry-stamping code at L23722/23792/23991/24733 can read them.
        //   BUG FIX V9.11.1: these were being computed by the engine but stripped from
        //     the analysis useMemo return shape, so patternsAtLock / patternsAtLockV9_11_0
        //     / futuresAtLock all stamped null on every entry from V9.10.10 forward.
        //     Diagnosed when user CSV showed empty HTF columns despite the engine log
        //     showing [HTF-PATTERNS] reasoning lines firing.
        patternsV9_10_10:eng.patternsV9_10_10||null,
        patternsV9_11_0:eng.patternsV9_11_0||null,
        futuresV9_11_0:eng.futuresV9_11_0||null,
        // V6.4: conviction asymmetry detector
        convictionAsymmetry:eng.convictionAsymmetry||null,
        rangeBps:Number(eng.rangeBps||0), // V152
        // V9.11.3: also expose the engine's UNCALIBRATED raw posterior alongside the
        //   calibrated value already in rawProbAbove. Misleading legacy: `rawProbAbove`
        //   has always been the CALIBRATED value (engine's finalPosterior), not the
        //   raw. Now both are available separately so trade-log telemetry can capture
        //   both `rawPosteriorAtLock` (uncalibrated) and `calibratedPosteriorAtLock`
        //   (calibrated) distinctly. The Brier-score calibration learning loop needs
        //   both to compute calibration error per resolved trade — previously they
        //   were stamping the same value, hiding the entire calibration signal.
        rawPosteriorUncalibrated:Number(eng.rawPosterior||eng.posterior||50),
        // V5.5: Pass through threshold values used by the lock state machine. These were
        //   computed inside the analysis useMemo but never exposed to consumers, so the
        //   projections panel showed `||65` and `||35` defaults instead of regime-adjusted values.
        upThreshold:Number(upThreshold),
        downThreshold:Number(downThreshold),
        // V3.1.7: volume-flow + window-amplitude signals (informational, surfaced via UI chips)
        volFlow:eng.volFlow||null,
        windowAmplitude:eng.windowAmplitude||null,
        // V3.1.11: within-window candle pattern (informational, surfaced via UI chip)
        candlePattern:eng.candlePattern||null,
        // V9.5.0: opponent-modeling signal (Kalshi-spot divergence). null when no divergence.
        kalshiLead:eng.kalshiLead||null,
        // V9.15: HISTORICAL ANALOGS. K-nearest-neighbor lookup over resolved trades
        //   in the call log. The matcher builds a 16-dim feature vector from the
        //   current engine state (regime, session, hour, posterior, ATR, signal
        //   scores) and finds the 12 most similar historical entries. Exposed here
        //   so the UI can display analog WR alongside the engine's posterior, and
        //   so trade-log telemetry stamps the analog snapshot at lock time.
        //
        //   Returns null when call log is too small (<10 valid entries) or current
        //   setup is incomplete. UI handles null gracefully.
        //
        //   Cost: ~16K floating-point ops per recompute. Negligible.
        historicalAnalogs:(()=>{
          try{
            return taraFindHistoricalAnalogs(
              // Wrap eng output to match what taraExtractMatchFeatures expects from "live"
              {
                rawProbAbove:eng.posterior,
                regime:eng.regime,
                session:typeof getMarketSessions==='function'?getMarketSessions().dominant:null,
                atrBps:eng.atrBps,
                rawSignalScores:eng.rawSignalScores,
                lockInfo:lockedCallRef.current?{dir:lockedCallRef.current.dir}:null,
              },
              taraCallLog,
              currentAsset,
              12,
            );
          }catch(_){return null;}
        })(),
        lockInfo:lockedCallRef.current?{dir:lockedCallRef.current.dir,lockedAt:lockedCallRef.current.lockedAt,lockedPosterior:lockedCallRef.current.lockedPosterior,lockPrice:lockedCallRef.current.lockPrice,lockRegime:lockedCallRef.current.lockedRegime}:null,
        bullCount:Number(bullCount),bearCount:Number(bearCount)};
    }catch(err){return{prediction:'ERROR',rawProbAbove:50,projections:[],reasoning:[err.stack||String(err)],textColor:'text-rose-500',advisor:{label:'MATH CRASH',reason:String(err),color:'rose',animate:false,hasAction:false},regime:'ERROR'};}
  },[currentPrice,liveHistory,targetMargin,timeState.minsRemaining,timeState.secsRemaining,timeState.currentHour,orderBook,forceRender,betAmount,maxPayout,currentOffer,globalFlow,userPosition,windowType,isMounted,showRugPullAlerts,positionStatus,velocityRef,bloomberg,useLocalTime,regimeMemory,regimeWeights,strikeConfirmed,adaptiveWeights,calibration,tfCandles,futuresData,sessionWeights,taraCallLog,currentAsset]);
  // V6.4: keep analysisRef live so non-hooked code (regime sampler, etc.) can read it
  const analysisRef=useRef(null);
  useEffect(()=>{analysisRef.current=analysis;},[analysis]);

  // ── V10.9.14: WINDOW-TYPE TRANSITION TRACKING ──
  //   Record each distinct windowAmplitude label seen during the live window (with
  //   the second-into-window it first appeared). Lets every trade stamp the type at
  //   lock + the full transition path, so we can measure how stable the live read
  //   is and whether locking after a transition (e.g. GRIND>WHIPSAW) loses more.
  //   V10.9.15 FIX: must live AFTER the analysis useMemo (it reads analysis); placing
  //   it earlier caused a temporal-dead-zone ReferenceError crash on load.
  useEffect(()=>{
    const _label=analysis?.windowAmplitude?.label;
    if(!_label||_label==='OPENING')return;
    const _seq=_windowTypeSeqRef.current;
    if(!Array.isArray(_seq))return;
    if(_seq.length&&_seq[_seq.length-1].label===_label)return; // no change
    const _sec=windowOpenTimeRef.current?Math.round((Date.now()-windowOpenTimeRef.current)/1000):null;
    _seq.push({label:_label,atSec:_sec});
    if(_seq.length>12)_seq.shift(); // cap memory
  },[analysis?.windowAmplitude?.label]);

  // V6.4: MULTI-SIGNAL PIVOT DETECTOR. Tracks sign-flips across (a) tape buy% crossing 50,
  //   (b) asymmetry score crossing zero, (c) FGT alignment crossing zero. When 3+ flips
  //   fire same direction within 30 seconds, sample requirement drops 50%.
  useEffect(()=>{
    if(!analysis)return;
    const _now=Date.now();
    const piv=signalPivotRef.current;
    // Tape sign: dollar-weighted 30s buy%, +1 if >55, -1 if <45, 0 otherwise
    const _tape30=analysis?.tapeWindows?.w30?.buyPct;
    const _tapeSign=_tape30!=null?(_tape30>=55?1:_tape30<=45?-1:0):null;
    if(_tapeSign!=null&&piv.lastTapeSign!=null&&_tapeSign!==piv.lastTapeSign&&_tapeSign!==0){
      piv.events.push({src:'tape',dir:_tapeSign>0?'UP':'DOWN',t:_now});
    }
    if(_tapeSign!=null)piv.lastTapeSign=_tapeSign;
    // Asymmetry sign-direction
    const _asym=analysis?.convictionAsymmetry;
    if(_asym?.valid&&_asym.latestDir&&_asym.score>=20){
      const _aSign=_asym.latestDir==='UP'?1:-1;
      if(piv.lastAsymSign!=null&&_aSign!==piv.lastAsymSign){
        piv.events.push({src:'asym',dir:_asym.latestDir,t:_now});
      }
      piv.lastAsymSign=_aSign;
    }
    // FGT sign
    const _fgt=analysis?.mtfAlignment;
    const _fgtSign=_fgt!=null?(_fgt>0.5?1:_fgt<-0.5?-1:0):null;
    if(_fgtSign!=null&&piv.lastFgtSign!=null&&_fgtSign!==piv.lastFgtSign&&_fgtSign!==0){
      piv.events.push({src:'fgt',dir:_fgtSign>0?'UP':'DOWN',t:_now});
    }
    if(_fgtSign!=null)piv.lastFgtSign=_fgtSign;
    // Trim events older than 30s
    piv.events=piv.events.filter(e=>_now-e.t<=30000);
  },[analysis]);
  // Helper read: how many simultaneous-direction shifts fired in last 30s?
  const computeMultiSignalPivot=()=>{
    const piv=signalPivotRef.current;
    const _now=Date.now();
    const recent=piv.events.filter(e=>_now-e.t<=30000);
    const upEvents=recent.filter(e=>e.dir==='UP').length;
    const dnEvents=recent.filter(e=>e.dir==='DOWN').length;
    if(upEvents>=3)return{active:true,dir:'UP',count:upEvents};
    if(dnEvents>=3)return{active:true,dir:'DOWN',count:dnEvents};
    return{active:false,dir:null,count:0};
  };

  // V6.4: REGIME TRANSITION DETECTOR. Reads regimeHistoryRef (sampled every 5s) and
  //   computes whether the regime is stable or actively transitioning. Returns one of:
  //     - 'stable': last 6 samples (30s) all same regime, ATR change <30%
  //     - 'transitioning-to-trend': ATR expanding + range expanding
  //     - 'transitioning-to-range': ATR contracting + range contracting
  //     - 'flux': regime changed in last 30s, no clear new direction yet
  //   Forces re-eval every 5 seconds via forceRender (the regime sampler runs at 5s).
  const regimeTransition=useMemo(()=>{
    const hist=regimeHistoryRef.current;
    if(!hist||hist.length<6)return{state:'unknown',score:0,fromRegime:null,toRegime:null};
    const recent=hist.slice(-12); // last ~60s
    const _regimes=recent.map(s=>s.regime).filter(Boolean);
    const _firstReg=_regimes[0];
    const _lastReg=_regimes[_regimes.length-1];
    // ATR / range trend over the window
    const atrs=recent.map(s=>s.atr).filter(a=>a>0);
    const ranges=recent.map(s=>s.range).filter(r=>r>0);
    if(atrs.length<3)return{state:'unknown',score:0,fromRegime:_firstReg,toRegime:_lastReg};
    const atrFirst=atrs[0],atrLast=atrs[atrs.length-1];
    const rangeFirst=ranges[0]||1,rangeLast=ranges[ranges.length-1]||1;
    const atrChange=(atrLast-atrFirst)/Math.max(0.1,atrFirst);
    const rangeChange=(rangeLast-rangeFirst)/Math.max(0.1,rangeFirst);
    // Regime label changes count
    const _regChanges=new Set(_regimes).size;
    if(_regChanges>=3){
      // Multiple regime labels in 60s — flux
      return{state:'flux',score:50,fromRegime:_firstReg,toRegime:_lastReg};
    }
    // Sustained ATR + range expansion = trend regime forming
    if(atrChange>0.3&&rangeChange>0.2){
      return{state:'transitioning-to-trend',score:Math.min(100,Math.round((atrChange+rangeChange)*100)),fromRegime:_firstReg,toRegime:_lastReg};
    }
    // Sustained ATR + range contraction = range regime forming
    if(atrChange<-0.3&&rangeChange<-0.2){
      return{state:'transitioning-to-range',score:Math.min(100,Math.round(Math.abs(atrChange+rangeChange)*100)),fromRegime:_firstReg,toRegime:_lastReg};
    }
    // Stable
    return{state:'stable',score:0,fromRegime:_firstReg,toRegime:_lastReg};
  },[analysis?.atrBps,analysis?.regime,analysis?.rangeBps]);
  const regimeTransitionRef=useRef(null);
  useEffect(()=>{regimeTransitionRef.current=regimeTransition;},[regimeTransition]);

  // ── QUALITY GATE — plain function call, no useMemo to avoid any TDZ risk ──
  // V10.2.8 — QSCORE INVERSION FIXED. V1 formula was anti-correlated with WR
  //   in the 755-trade audit (q70+ won 60.6%, q0-19 won 73.9%). Root cause was
  //   the `ps` term weighting `Math.abs(posterior-50)` at 40/90 of the total —
  //   high-conviction predictions were systematically lower-WR than low-conviction
  //   ones. V2 formula (designed in V9.19.10, shadowed since then) replaces this
  //   with weights anchored to audit-measured group ranks:
  //     • regimeCalApplied  → +25 (audited 71.9% vs 65.6% without)
  //     • session (Asia=20, OFF=16, EU=11, US=7) — audit ranking
  //     • regimeWR (max +20) — same direction as v1, less weight
  //     • posterior magnitude (max +10) — DOWN-weighted from v1's 40
  //     • late-lock penalty (-20) — preserved
  //     • base (+5) — preserved
  //   The V1 formula is preserved as _computeQualityV1Legacy below for forensic
  //   comparison via `__taraAuditStats()`. Going forward `qScore` in the log
  //   reflects V2 numbers; old log entries keep their V1 numbers (immutable).
  //   `qScoreV2` field in the log is now redundant with `qScore` (both = V2).
  //   We keep it stamped for the next ~50 trades so audit can confirm monotonicity
  //   before fully retiring the duplicate column.
  const _computeQuality=(ana,regMem)=>{
    if(!ana)return{score:0,label:'LOW',color:'rose',components:null};
    try{
      const rg=ana.regime||'RANGE-CHOP';
      const rm=regMem[rg]||{wins:0,losses:0};
      const rt=rm.wins+rm.losses;
      const rWR=rt>5?(rm.wins/rt)*100:60;
      const sess=getMarketSessions().dominant;
      const sessionPts={'ASIA':20,'OFF-HOURS':16,'EU':11,'US':7}[sess]||7;
      const regimeCalPts=ana?.diagnosticsV97?.regimeCal?.applied?25:0;
      const regimeWRPts=Math.min(20,Math.max(0,(rWR-50)*0.4));
      const pt=ana.rawProbAbove||50;
      // V10.2.20 — POSTERIOR component retuned from 724-trade audit.
      //   May 15 audit findings:
      //     • Overall post×WR: posterior magnitude is NON-MONOTONIC for single-tier
      //       (which is 78% of trades). Specifically:
      //         <10pt:  80.0% WR  (n=18)
      //         10-20:  76.3% WR  (n=84)
      //         20-30:  65.0% WR  (n=377)
      //         30+:    64.7% WR  (n=88)
      //       Higher conviction = LOWER WR for single-tier.
      //     • Inside TRENDING UP × 30+pt posterior:  54.5% WR
      //     • Inside SHORT SQUEEZE × 30+pt posterior: 54.8% WR
      //       Both momentum regimes show high posterior = overconfidence.
      //   Fix: zero out postPts in momentum regimes (TRENDING UP / SHORT SQUEEZE);
      //   keep it modest elsewhere. Net effect: qScoreV2 stops giving false signal
      //   to "extreme posterior" trades that statistically don't deliver.
      const _momentumRegime=rg==='TRENDING UP'||rg==='SHORT SQUEEZE';
      const postPts=_momentumRegime?0:Math.min(10,Math.max(0,(Math.abs(pt-50)-15)*0.4));
      // V10.2.20 — LATE-LOCK PENALTY dropped (was -20 very-late / -8 late zone).
      //   May 15 audit (qScoreV2 components, n=28 V2 trades):
      //     wins: late component avg -0.91
      //     losses: late component avg 0.00
      //   Wins had the late penalty applied MORE OFTEN than losses, meaning late
      //   locks were WINNING. Penalty was directionally wrong. Setting to 0 until
      //   we have more V2 data — if a later audit shows late locks DO underperform
      //   on tighter filters, we can reinstate or invert.
      //   V1 forensic legacy preserved at _computeQualityV1Legacy for comparison.
      const latePenalty=0;
      const base=5;
      const raw=regimeCalPts+sessionPts+regimeWRPts+postPts+latePenalty+base;
      const score=Math.max(0,Math.min(100,Math.round(raw)));
      return{
        score,
        label:score>=75?'HIGH':score>=55?'MODERATE':'LOW',
        color:score>=75?'emerald':score>=55?'amber':'rose',
        components:{regimeCalPts,sessionPts,regimeWRPts,postPts,latePenalty,base,
          // V10.2.20 — diagnostic flags for audit hooks
          _v10_2_20_postZeroed:_momentumRegime,
          _v10_2_20_lateDropped:true,
        },
      };
    }catch(e){return{score:0,label:'LOW',color:'rose',components:null};}
  };
  // V10.2.8 — V1 formula preserved for forensic comparison. NOT used in
  //   production. Re-enable via `__taraQualityCompare()` console hook if needed.
  const _computeQualityV1Legacy=(ana,regMem)=>{
    if(!ana)return{score:0,label:'LOW',color:'rose'};
    try{
      const rg=ana.regime||'RANGE-CHOP';
      const rm=regMem[rg]||{wins:0,losses:0};
      const rt=rm.wins+rm.losses;
      const rWR=rt>5?(rm.wins/rt)*100:60;
      const sWR={'EU':62,'ASIA':68,'US':65,'OFF-HOURS':66}[getMarketSessions().dominant]||65;
      const pt=ana.rawProbAbove||50;
      const ps=Math.min(40,Math.max(0,(Math.abs(pt-50)-15)*1.6));
      const cp=ana.isVeryLateLock?-20:ana.isLateLockZone?-8:0;
      const raw=ps+Math.min(30,(rWR-50)*0.6)+Math.min(15,(sWR-50)*0.6)+cp;
      const score=Math.max(0,Math.min(100,raw+5));
      return{score,label:score>=75?'HIGH':score>=55?'MODERATE':'LOW',color:score>=75?'emerald':score>=55?'amber':'rose'};
    }catch(e){return{score:0,label:'LOW',color:'rose'};}
  };
  // V9.19.10: SHADOW qScore v2 — informational only, NOT consumed by lock decisions.
  //   Stamped to taraCallLog as `qScoreV2` so the user can audit how V2 weights
  //   would have scored real trades. After 50+ trades on V9.18.10 (post-Kalshi-fix),
  //   run a calibration check: bucket WR by qScoreV2 and compare against the v1
  //   inversion (qScore 70+ won 58%, qScore 20-29 won 79%). If v2 shows monotonic
  //   WR vs. score, that's the green light to make it the live signal.
  //   Weights based on 616-trade audit, May 2026:
  //     • regimeCalApplied → +25 (measured WR 71.9% vs 65.6% without)
  //     • session (Asia=20, OFF=16, EU=11, US=7) — measured WR ranking
  //     • regimeWR (max +20) — same direction as v1 but slightly down-weighted
  //     • posterior magnitude (max +10) — DOWN-weighted vs v1's 40 (posterior is broken)
  //     • late-lock penalty (-20) — preserved from v1
  //     • base bias (+5) — preserved
  //   Tier info (super/confluence/tape/structural) is NOT available at score-time
  //   (tier classification happens later in _computeTaraCall), so v2 omits it. The
  //   downstream audit can correlate qScoreV2 vs. tier outcome in the CSV.
  const _computeQualityV2=(ana,regMem)=>{
    if(!ana)return{score:0,label:'LOW',color:'rose',components:null};
    try{
      const rg=ana.regime||'RANGE-CHOP';
      const rm=regMem[rg]||{wins:0,losses:0};
      const rt=rm.wins+rm.losses;
      const rWR=rt>5?(rm.wins/rt)*100:60;
      const sess=getMarketSessions().dominant;
      const sessionPts={'ASIA':20,'OFF-HOURS':16,'EU':11,'US':7}[sess]||7;
      const regimeCalPts=ana?.diagnosticsV97?.regimeCal?.applied?25:0;
      const regimeWRPts=Math.min(20,Math.max(0,(rWR-50)*0.4));
      const pt=ana.rawProbAbove||50;
      // V10.2.20 — same retune as _computeQuality (zero out post in momentum
      //   regimes, drop late penalty). Keeping V2 in sync with V1-live so the
      //   qScoreV2 CSV column remains a 1:1 match to the live qScore.
      const _momentumRegime=rg==='TRENDING UP'||rg==='SHORT SQUEEZE';
      const postPts=_momentumRegime?0:Math.min(10,Math.max(0,(Math.abs(pt-50)-15)*0.4));
      const latePenalty=0;
      const base=5;
      const raw=regimeCalPts+sessionPts+regimeWRPts+postPts+latePenalty+base;
      const score=Math.max(0,Math.min(100,Math.round(raw)));
      return{
        score,
        label:score>=75?'HIGH':score>=55?'MODERATE':'LOW',
        color:score>=75?'emerald':score>=55?'amber':'rose',
        // V10.2.21 — added diagnostic flags to V2 components (forgotten in V10.2.20).
        //   These flags let users verify V10.2.20 retune is active on their trades.
        components:{regimeCalPts,sessionPts,regimeWRPts,postPts,latePenalty,base,
          _v10_2_20_postZeroed:_momentumRegime,
          _v10_2_20_lateDropped:true,
        },
      };
    }catch(e){return{score:0,label:'LOW',color:'rose',components:null};}
  };
  const qualityGate=_computeQuality(analysis,regimeMemory);
  const qualityGateV2=_computeQualityV2(analysis,regimeMemory);
  const _startupNow=Date.now();
  const _startupSource=PRICE_SOURCES[priceSource]?.virtual?null:PRICE_SOURCES[priceSource]?.label;
  const _startupSpotAt=Math.max(lastPriceSourceRef.current?.exchange===priceSource?(lastPriceSourceRef.current.time||0):0,
    ...(brtiApprox?.sourceHealth||[]).filter(s=>s.status==='fresh'&&(!_startupSource||s.name===_startupSource)).map(s=>Number(s.receivedAt)||0));
  const _startupUserHold=(()=>{try{const minutes=Number(localStorage.getItem('taraColdStartWarmupMin'));return minutes>0&&minutes<=180?_mountTimeRef.current+minutes*60000:0;}catch{return 0;}})();
  const startupReadiness=useStartupReadiness({asset:currentAsset,source:priceSource,history,tfCandles,
    spot:currentPrice,spotAt:_startupSpotAt,analysis,intervalSeconds:windowType==='15m'?900:300,
    minimumUntil:_startupUserHold,now:_startupNow});
  const _hourlyObservation={spot:currentPrice,spotAt:_startupSpotAt,source:priceSource,asset:currentAsset,readiness:startupReadiness};
  useEffect(()=>{
    if(currentAsset!=='BTC')return;
    const previous=shadowDiagRef.current.BTC;
    const diag=previous?.mode==='primary-mirror'?previous:{mode:'primary-mirror',ticksAttempted:0,
      ticksSucceeded:0,lastSuccess:0,lastError:null,candleFails:0,candleEmpty:0,priceMissing:0,kalshiFails:0,engineErrors:0};
    shadowDiagRef.current.BTC=diag;diag.ticksAttempted++;
    const view=primaryShadowView({analysis,asset:currentAsset,price:currentPrice,strike:targetMargin,
      ticker:kalshiActiveMarket?.ticker,closeTime:kalshiActiveMarket?.close_time,
      windowId:computeWindowId(windowType),ready:startupReadiness.ready,spotAt:_startupSpotAt});
    if(view){priceByAssetRef.current.BTC={price:currentPrice,time:_startupSpotAt};shadowTaraByAssetRef.current.BTC=view;diag.ticksSucceeded++;diag.lastSuccess=view.updatedAt;diag.lastError=null;}
    else{diag.lastError=!startupReadiness.ready?startupReadiness.reason:analysis?.prediction==='ERROR'?(analysis.reasoning?.[0]||'Primary analysis unavailable.'):'Waiting for valid primary analysis.';
      diag.lastErrorAt=Date.now();if(analysis?.prediction==='ERROR')diag.engineErrors++;
      if(shadowTaraByAssetRef.current.BTC)shadowTaraByAssetRef.current.BTC={...shadowTaraByAssetRef.current.BTC,updatedAt:0};}
  },[analysis,currentAsset,currentPrice,targetMargin,windowType,kalshiActiveMarket?.ticker,startupReadiness.ready,_startupSpotAt]);

  // V3.2.4: TARA'S CALL — a separate, higher-conviction decision layer.
  //   The "general prediction" (analysis.prediction / posterior) tells you the most likely
  //   outcome given current signals. Tara's Call adds an explicit conviction filter — she
  //   only "calls" UP or DOWN when MULTIPLE signals confirm. Otherwise she sits out.
  //
  //   Decision tree (in order):
  //     1. Quality < 50           → SIT OUT (low signal quality, sit-out trades)
  //     2. |Posterior - 50| < 12  → SIT OUT (no real conviction either way)
  //     3. |FGT| < 1.5/4          → SIT OUT (primary multi-timeframe signal silent)
  //     4. Window DEAD/WHIPSAW + quality<70 → SIT OUT (regime hostile to direction trades)
  //     5. VOL-FLOW divergent against direction → SIT OUT (tape disagrees with price)
  //     6. Otherwise → match general prediction direction
  //
  //   The result is tracked separately in taraScorecards. SIT_OUT counts neither way.
  //   This lets us measure: do Tara's selective calls win at higher rate than the firehose?
  // V5.6.4 REDESIGN: Slower, more deliberate calls. User feedback: "Tara needs to stop
  //   locking so quickly, now most of her decision are being 50-50 at that point it is
  //   a coin flip." New flow:
  //     1. SEARCH phase (first 20s of every window): observe only, no commit possible
  //     2. SIGNAL phase: direction emerges, gates measure conviction
  //     3. LOCK: only when conviction is real and persistent (handled in lifecycle effect)
  //     4. Coin-flip → SIT_OUT (handled by tightened gates + posterior-history check)
  //     5. Kalshi sanity check: if market disagrees materially, require higher conviction
  const taraCall=(()=>{
    const _post=analysis?.rawProbAbove;
    if(!startupReadiness.ready)return{call:'SIT_OUT',reason:startupReadiness.reason,confidence:0,startupBlocked:true};
    if(!analysis||_post==null||isNaN(_post)){
      return{call:'SIT_OUT',reason:'Engine still loading — analysis not ready yet',confidence:0};
    }
    const post=_post;
    const conviction=Math.abs(post-50);
    const dir=post>=50?'UP':'DOWN';
    const fgtAbs=Math.abs(analysis.mtfAlignment||0);
    // V6.4: surface conviction asymmetry for use in exhaustion gate
    const convictionAsymmetry=analysis.convictionAsymmetry||null;
    const winType=analysis.windowAmplitude?.label||'NORMAL';
    const vfLabel=analysis.volFlow?.label||'';
    const q=Math.round(qualityGate?.score||0);
    const _totalSec=windowType==='15m'?900:300;
    const _elapsed=_totalSec-((timeState.minsRemaining*60)+timeState.secsRemaining);
    const _remaining=Math.max(0,_totalSec-_elapsed);
    // V5.6.4: Mandatory search phase — first 10s, observe only.
    // V6.5: Reduced from 20s → 10s. Faster cadence target: lock within 1-2 minutes for
    //   strong signals. 10s is enough to filter Kalshi initial-price snap noise without
    //   forcing slow ramp-up on clear setups.
    // V5.6.4: Mandatory search phase — first 10s, observe only.
    // V10.9.7: speed dial removed. Search phase fixed at its neutral (dial=50,
    //   multiplier=1.0) value of 10s — the tier ladder, V10.9.0 signal-agreement
    //   gate, V10.8.2 cushion history, and V9.8.0 ULP handle adaptive timing.
    let _searchPhase=10;
    // V9.8.0: URGENT LOCK PROTOCOL — when Kalshi is moving in our direction, halve
    //   the search phase. Combined with the lifecycle ULP (50% needSamples cut), a
    //   moving market gets locked in ~12s instead of ~33s. Detect urgency inline
    //   here too rather than relying on _v97DiagRef (which updates from this same
    //   function — circular dependency for first tick of urgency).
    let _ulpEngActive=false;
    if(typeof kalshiHistoryRef!=='undefined'&&kalshiHistoryRef.current?.length>=3){
      const _khE=kalshiHistoryRef.current;
      const _nowE=Date.now();
      const _recentE=_khE.filter(k=>_nowE-k.time<=2000);
      const _thenE=_khE.filter(k=>_nowE-k.time>=28000&&_nowE-k.time<=32000);
      if(_recentE.length>0&&_thenE.length>0){
        const _deltaE=_recentE[_recentE.length-1].yes-_thenE[0].yes;
        // post is computed earlier in taraCall; if available use it, else fall back to lean
        const _postE=typeof post!=='undefined'?post:50;
        if((_postE>=50&&_deltaE>=4)||(_postE<50&&_deltaE<=-4)){
          _ulpEngActive=true;
          _searchPhase=Math.max(1,Math.round(_searchPhase*0.5));
        }
      }
    }
    if(_elapsed<_searchPhase){
      return{call:'SIT_OUT',reason:`Searching for direction (${_searchPhase-_elapsed}s of observation remaining${_ulpEngActive?' · ULP':''})`,confidence:0,direction:dir,conviction,phase:'SEARCH'};
    }
    // V9.2.0: ROLLOVER GRACE — force SEARCH mode for 5s after window rollover.
    //   Bug: when window rolls over, timeState.minsRemaining might still be 0 from the
    //   OLD window → _elapsed computes as 900 → search phase gate passes → engine
    //   instantly forms a call from stale analysis. This grace period ensures Tara
    //   always observes fresh price data before locking the new window.
    const _msSinceRollover=Date.now()-(_rolloverGraceRef.current||0);
    if(_rolloverGraceRef.current>0&&_msSinceRollover<5000){
      return{call:'SIT_OUT',reason:`New window — observing fresh data (${Math.ceil((5000-_msSinceRollover)/1000)}s)`,confidence:0,direction:dir,conviction,phase:'SEARCH'};
    }
    // V5.6.4: HARD floors — no time-decay shenanigans. If conviction is below this, sit out
    //   no matter how late in the window. Coin-flip detection.
    // V5.6.8: HARD floors — fixed by default. Learning does NOT make Tara more selective
    //   except via a safety valve for absolute-bad regimes (<25% win rate over 15+ trades).
    //   Active learning lives in lifecycle (samples) and confidence (display), not here.
    const _learn=taraLearningsRef.current;
    const _safety=Math.max(0,Math.min(8,_learn?.multipliers?.regimeFloorSafety?.[analysis.regime]||0));
    // V10.9.7: speed dial removed. Multiplier fixed at its neutral (dial=50) value
    //   of 1.0 — Q_FLOOR and CONV_FLOOR run at their tuned baseline (30/8 in
    //   RANGE-CHOP), with regime-floor-safety and soft-hint still applying on top.
    const _speedMultEng=1.0;
    // V6.2.6: SOFT HINT MODE — user pressed "Hint to Lock Faster" recently. Lower floors
    //   for 10 seconds. Tape-opposes, Kalshi-extreme, and edge-guard remain enforced.
    const _softHintActive=softHintRef.current>0&&(Date.now()-softHintRef.current)<10000;
    const _qBase=_softHintActive?20:(27+_safety);/*V13.4.51: 30->27, more chop locks*/
    const Q_FLOOR=Math.max(10,Math.round(_qBase*_speedMultEng));
    // V6.5.1: CONV_FLOOR 10 → 5. V6.5.7: also dial-aware. V7.2: base 5 → 7.
    //   V10.7.69: raised to 8. V10.7.75: regime-adaptive conviction floor.
    //   RANGE-CHOP: 8 (default, already tuned)
    //   TRENDING: 6 — trend is your friend, lower bar to lock with momentum
    //   SHORT SQUEEZE: 10 — explosive/risky, need stronger conviction
    //   HIGH VOL CHOP: 6 — your 84% WR regime, trust it more
    const _regimeForFloor=_softHintActive?'':(analysis?.regime||'RANGE-CHOP');
    const _regimeConvBase=_regimeForFloor==='TRENDING UP'||_regimeForFloor==='TRENDING DOWN'||_regimeForFloor==='STRONG TREND'?6
      :_regimeForFloor==='SHORT SQUEEZE'?10
      :_regimeForFloor==='LONG SQUEEZE'?10    // same as short squeeze
      :_regimeForFloor==='COMPRESSING'?12     // highest floor — direction unconfirmed
      :_regimeForFloor==='HIGH VOL CHOP'?6
      :7; // RANGE-CHOP default (V13.4.51: 8->7, coin-flip floor 6->5)
    const _convBase=_softHintActive?3:(_regimeConvBase+_safety);
    // V9.7.6: ADAPTIVE LOCK URGENCY — when Kalshi is moving fast in Tara's predicted
    //   direction, drop the conviction floor by 30%. Logic: if Kalshi YES moved ≥4¢ in
    //   the last 30s and the move agrees with Tara's lean, we're being told the market
    //   is pricing in the move RIGHT NOW. Waiting for higher conviction = paying worse
    //   entry price. Lock NOW.
    let _urgencyMult=1.0;
    let _urgencyReason=null;
    let _kalshiVelocity30s=null; // V9.7.9 telemetry: capture velocity always
    if(typeof kalshiHistoryRef!=='undefined'&&kalshiHistoryRef.current?.length>=3){
      const _kh=kalshiHistoryRef.current;
      const _now=Date.now();
      const _recent=_kh.filter(k=>_now-k.time<=2000); // last 2s
      const _then=_kh.filter(k=>_now-k.time>=28000&&_now-k.time<=32000); // ~30s ago
      if(_recent.length>0&&_then.length>0){
        const _yesNow=_recent[_recent.length-1].yes;
        const _yesThen=_then[0].yes;
        const _delta=_yesNow-_yesThen;
        _kalshiVelocity30s=_delta; // always capture for telemetry
        // Tara's lean direction: post>50 means UP lean (YES rising = good)
        //                        post<50 means DOWN lean (YES falling = good)
        const _agrees=(post>=50&&_delta>=4)||(post<50&&_delta<=-4);
        if(_agrees){
          _urgencyMult=0.70; // 30% floor reduction
          _urgencyReason=`Kalshi YES ${_delta>0?'+':''}${_delta.toFixed(0)}¢/30s in our direction — dropping floor 30%`;
        }
      }
    }
    const CONV_FLOOR=Math.max(1,Math.round(_convBase*_speedMultEng*_urgencyMult));
    // V9.7.6: surface urgency state via console.info (won't crash if unhooked)
    if(_urgencyReason){try{console.info('[urgency]',_urgencyReason,'CONV_FLOOR=',CONV_FLOOR);}catch(_){}}
    // V9.7.9: stash urgency + kalshi velocity on a ref so the lifecycle effect can
    //   read them at lock time and stamp onto the trade log entry.
    if(typeof _v97DiagRef!=='undefined'&&_v97DiagRef?.current){
      _v97DiagRef.current={
        urgencyApplied:_urgencyMult<1.0,
        urgencyMult:_urgencyMult,
        kalshiVelocity30s:_kalshiVelocity30s,
        timestamp:Date.now(),
      };
    }
    // Tape consensus (multi-window 15s/30s/60s buy%)
    const _tape15=tapeWindows?.w15?.buyPct??50;
    const _tape30=tapeWindows?.w30?.buyPct??50;
    const _tape60=tapeWindows?.w60?.buyPct??50;
    const _tapes=[_tape15,_tape30,_tape60];
    const _upAgrees=_tapes.filter(p=>p>=60).length>=2;
    const _downAgrees=_tapes.filter(p=>p<=40).length>=2;
    const tapeAgreesUP=_upAgrees&&dir==='UP';
    const tapeAgreesDOWN=_downAgrees&&dir==='DOWN';
    const tapeStronglyAgrees=tapeAgreesUP||tapeAgreesDOWN;
    const _upSuper=_tapes.filter(p=>p>=70).length>=2;
    const _downSuper=_tapes.filter(p=>p<=30).length>=2;
    const tapeSuperStrong=(_upSuper&&dir==='UP')||(_downSuper&&dir==='DOWN');
    const tapeBuyPct=dir==='UP'?Math.max(..._tapes):Math.min(..._tapes);
    // V5.7.7: TAPE-OPPOSES-DIRECTION detection. The tape is the only signal that can't lie —
    //   it's what buyers and sellers ARE doing, no smoothing or extrapolation. When two of
    //   the three tape windows show ≥60% in the OPPOSITE direction of the candidate, the
    //   call is fighting actual order flow. Last round's failure: TAPE 79% buy at 30s, model
    //   wanted to lock DOWN. This veto would have prevented the lock.
    const _upOpposes=_tapes.filter(p=>p>=60).length>=2&&dir==='DOWN';
    const _downOpposes=_tapes.filter(p=>p<=40).length>=2&&dir==='UP';
    const tapeOpposes=_upOpposes||_downOpposes;
    const _upSuperOpposes=_tapes.filter(p=>p>=70).length>=2&&dir==='DOWN';
    const _downSuperOpposes=_tapes.filter(p=>p<=30).length>=2&&dir==='UP';
    const tapeSuperOpposes=_upSuperOpposes||_downSuperOpposes;
    // V5.6.4: KALSHI CROSS-CHECK — market price is a third opinion. Use as sanity check:
    //   - If Kalshi clearly agrees with our direction → boost confidence, allow faster lock
    //   - If Kalshi clearly disagrees → require materially higher conviction or sit out
    //   kalshiYesPrice is 0-100, representing market's P(price closes above strike) = P(UP).
    const _kPct=kalshiYesPrice;
    const kalshiAvail=_kPct!=null&&_kPct>0&&_kPct<100;
    const kalshiAgrees=kalshiAvail&&((dir==='UP'&&_kPct>=55)||(dir==='DOWN'&&_kPct<=45));
    const kalshiStronglyAgrees=kalshiAvail&&((dir==='UP'&&_kPct>=65)||(dir==='DOWN'&&_kPct<=35));
    // Disagreement: market sees the opposite direction with material conviction
    const kalshiDisagrees=kalshiAvail&&((dir==='UP'&&_kPct<=40)||(dir==='DOWN'&&_kPct>=60));
    const kalshiStronglyDisagrees=kalshiAvail&&((dir==='UP'&&_kPct<=30)||(dir==='DOWN'&&_kPct>=70));
    // V5.7.7: Kalshi extreme certainty threshold — when market is ≥90% one way and we're
    //   calling opposite, the disagreement is severe enough that we need extraordinary
    //   evidence to override (BOTH tape alignment AND pattern alignment).
    const kalshiExtremeDisagrees=kalshiAvail&&((dir==='UP'&&_kPct<=10)||(dir==='DOWN'&&_kPct>=90));
    // V5.7.7: STRIKE-FAVORABLE — is current price already on the right side of the strike?
    //   For UP candidate: price > strike means we're already where we want to settle.
    //   For DOWN candidate: price < strike likewise. This is a "material evidence" signal.
    const _gapBpsForDir=analysis?.realGapBps||0;
    const strikeFavorable=(dir==='UP'&&_gapBpsForDir>0)||(dir==='DOWN'&&_gapBpsForDir<0);
    // V5.7.7: CONFLUENCE detection — all four primary signals point the same way.
    //   When this fires, we have orthogonal confirmation and can fast-lock in 5-8 samples
    //   regardless of the slow-tier criteria. Last round's clean DOWN: tape, FGT, score
    //   sign, strike all DOWN → confluent. The win round vs the failure round is exactly
    //   this distinction. Note this evaluates per-direction (UP confluence has all-UP
    //   conditions, DOWN confluence has all-DOWN).
    const _scoreSignAgrees=(dir==='UP'&&conviction>=10)||(dir==='DOWN'&&conviction>=10); // post>= +10pt for UP, <=-10pt for DOWN
    const _fgtSignAgrees=(dir==='UP'&&(analysis?.mtfAlignment||0)>=0.7)||(dir==='DOWN'&&(analysis?.mtfAlignment||0)<=-0.7);
    const isConfluent=tapeStronglyAgrees&&_fgtSignAgrees&&_scoreSignAgrees&&strikeFavorable;
    const isSuperConfluent=tapeSuperStrong&&_fgtSignAgrees&&_scoreSignAgrees&&strikeFavorable&&!kalshiDisagrees;
    // V6.0.1: RISING-CONFLUENCE — early-entry tier. By the time isConfluent fires, the
    //   move has often already happened — Kalshi reads 80-90% and the entry is too late.
    //   This catches setups while they're BUILDING. Looser thresholds on each signal but
    //   still requires multiple to lean: 15s tape leading, FGT pointing right way (≥0.4
    //   not 0.7), conviction ≥8 (not 10), price still actionable. Locks in 3 samples.
    const _15sLeads=(dir==='UP'&&_tape15>=60)||(dir==='DOWN'&&_tape15<=40);
    const _fgtLeansDir=(dir==='UP'&&(analysis?.mtfAlignment||0)>=0.4)||(dir==='DOWN'&&(analysis?.mtfAlignment||0)<=-0.4);
    const _convLean=conviction>=8;
    // V6.0.7: TAPE-LED FAST-LOCK. The user's screenshot: tape was 90% one-sided across
    //   ALL windows but Tara was sitting at WATCHING because q=11 fell below the 30 floor.
    //   That's wrong — when tape is overwhelming (≥70% in ≥2 of 3 windows), it IS the
    //   signal. Quality score is a derived aggregate that lags actual order flow; waiting
    //   for it when raw tape data is unanimous defeats the purpose of having a real-time
    //   bot. Tape-led bypasses q-floor and conv-floor, locks in 5 samples (~5s).
    //   V6.1.2: TIGHTENED. Real-world test caught the failure mode: window opens, price
    //   ticks UP slightly for ~1 minute, tape briefly registers buy, Tara locks UP — but
    //   the bigger trajectory was DOWN and price reverses. Now tape-led requires ALSO
    //   either FGT-direction-aligned (multi-timeframe pattern agrees) OR strike already
    //   strongly favorable (price already on the right side by ≥8bps). Either confirmation
    //   shows the bigger picture isn't fighting the tape signal. Plus: tape-led blocked
    //   entirely in RANGE-CHOP regime — chop is where most "swing" windows live and tape
    //   is unreliable in chop.
    const _strikeReasonable=strikeFavorable||Math.abs(_gapBpsForDir)<=30;
    const _strikeStronglyFavorable=strikeFavorable&&Math.abs(_gapBpsForDir)>=8;
    const _fgtAlignsDir=_fgtSignAgrees&&fgtAbs>=0.7;
    const _isChopRegime=['RANGE-CHOP','CHOPPY','RANGE-BOUND'].includes(analysis.regime);
    // V7.2: TAPE-EXTREME GUARD. When 15s tape is at flush extremes (≥95% one direction)
    //   the move is more likely capitulation/exhaustion than continuation. Reversal odds
    //   spike. Block tape-led tier in this state — wait for tape to step back from the
    //   wall. Real-world example: 10:15-10:30 trade where 15s tape was 99.5% SELL, Tara
    //   tape-led DOWN at 99% conf, price reversed +6.8 bps and trade lost. Guard catches
    //   this exact pattern.
    const _tapeAtFlushExtreme=(dir==='UP'&&_tape15>=95)||(dir==='DOWN'&&_tape15<=5);
    // V7.2: GAP-NOT-MARGINAL. TAPE-LED was originally permissive on gap because tape itself
    //   was the signal. But marginal gap (price 0-2 bps from strike) means the binary outcome
    //   is genuinely uncertain — even strong tape can't compensate. Require gap ≥3 bps in
    //   direction at minimum for tape-led. Combined with the extreme guard above, this kills
    //   the "strong tape at near-strike inflection point" failure pattern.
    const _gapNotMarginal=strikeFavorable&&Math.abs(_gapBpsForDir)>=3;
    const _isTapeLedBase=tapeSuperStrong&&conviction>=4&&!tapeOpposes&&!kalshiExtremeDisagrees&&_strikeReasonable
      &&(_fgtAlignsDir||_strikeStronglyFavorable)
      &&!_isChopRegime
      &&!_tapeAtFlushExtreme   // V7.2: extreme tape = exhaustion, not continuation
      &&_gapNotMarginal;       // V7.2: gap must be ≥3 bps in direction
    // V9.4.0: ASYMMETRIC TAPE-LED FLOOR — UP setups need a higher bar than DOWN.
    //   V9.2.3 measured WR (4-day, n=185): tape-led × UP = 20% (n=5).
    //   tape-led × DOWN = 69% (n=26). Tape confirms fades better than it
    //   confirms pumps. For UP, additionally require tape ≥75% buy on the
    //   active windows AND conviction ≥8pt AND FGT magnitude ≥1.5/4.
    //   For DOWN, base check unchanged.
    const _tapeLedUpExtra=dir!=='UP'||conviction>=6;/*V13.4.72: UP was blocked by a triple bar (tapeBuyPct>=75 && conviction>=8 && fgtAbs>=1.5) while DOWN needed nothing. Data: UP wins as much as DOWN (63.8 vs 64.1) and UP tape-led wins MORE (75 vs 72), but only 4 UP vs 67 DOWN ever qualified. tapeSuperStrong in the base already ensures strong UP tape, so the triple was double-counting tape strength for UP. Relaxed to a light conviction>=6 hedge; the late-FOMO penalty still guards pump-chasing.*/
    const isTapeLed=_isTapeLedBase&&_tapeLedUpExtra;
    // V6.2.0: STRUCTURAL-LED TIER — primary fast-lock based on grand trend + trend channel.
    //   These are the user's preferred indicators: multi-hour structural context, looking
    //   at where price IS GOING, not where order flow already showed up.
    // V6.2.1: REWRITTEN to match user's actual workflow. He switches between 1m/5m/15m on
    //   TradingView and only takes a call when indicators agree across timeframes. So
    //   structural-led now requires ≥4 of 6 readings (Trend Channel × 3 TFs + Grand Trend
    //   × 3 TFs) to point the lock direction. Single-timeframe agreement isn't enough.
    const _gt=analysis?.grandTrend;
    const _tc=analysis?.trendChannel;
    const _struct=analysis?.structAlignment;
    // Cross-TF cross-indicator alignment in the lock direction
    const _structAgreesDir=_struct?.dir===dir&&_struct?.count>=4; // 4 of 6 signals minimum
    // V6.2.0: Channel position alignment (5m): for UP locks, want price in lower half (room to run up).
    //   For DOWN locks, want price in upper half. Mid-channel = no edge.
    // V9.2.0: EXHAUSTION GATE added. When channelPos is at extreme contrary to call direction
    //   (< 0.20 for DOWN, > 0.80 for UP), actively BLOCK structural-led — price is at the
    //   channel boundary and likely to bounce. This was the core bug in the 50% WR structural
    //   tier: 9 of 12 losses aligned with trend exhaustion.
    const _tcPosFavors=_tc?.valid&&((dir==='UP'&&_tc.channelPos<=0.45)||(dir==='DOWN'&&_tc.channelPos>=0.55));
    const _tcExhausted=_tc?.valid&&((dir==='UP'&&_tc.channelPos>=0.85)||(dir==='DOWN'&&_tc.channelPos<=0.15));
    // V9.2.0: slopeBpsPerBar check — if the 5m trend channel slope is flat or contrary
    //   to the call direction, structural alignment is weaker than it looks. The slope was
    //   computed but never read by the engine (identified in the call log analysis).
    const _tcSlope=Number(_tc?.slopeBpsPerBar)||0;
    const _tcSlopeContrary=(dir==='UP'&&_tcSlope<-1.0)||(dir==='DOWN'&&_tcSlope>1.0);
    // Grand trend strength on 5m needs to be meaningful too (not just neutral with tied counts)
    const _gtStrengthOk=_gt?.valid&&_gt.strengthBps>=20;
    // V9.2.0: GT slope check — same as TC slope. Was ignored, now penalizes contrary slope.
    const _gtSlope=Number(_gt?.slopeBpsPerBar)||0;
    const _gtSlopeContrary=(dir==='UP'&&_gtSlope<-1.0)||(dir==='DOWN'&&_gtSlope>1.0);
    // Strong version: 5+ of 6 align AND strength is real (the "perfect alignment" you'd see
    //   on TradingView when all timeframes scream the same direction)
    // V9.2.0: Exhaustion blocks even _structStrong — channelPos at boundary = bounce risk.
    const _structStrong=_struct?.dir===dir&&_struct?.count>=5&&_gtStrengthOk&&!_tcExhausted;
    // Standard structural-led: 4 of 6 align + favorable channel position + meaningful strength
    // V9.2.0: Added exhaustion block + slope-contrary penalty
    let isStructuralLed=(_structStrong||(_structAgreesDir&&(_tcPosFavors||_gtStrengthOk)))
      &&!_tcExhausted              // V9.2.0: exhaustion gate
      &&!(_tcSlopeContrary&&_gtSlopeContrary) // V9.2.0: BOTH slopes contrary = wrong direction
      &&conviction>=6
      &&!tapeSuperOpposes
      &&_strikeReasonable
      &&!kalshiExtremeDisagrees;
    // V10.6.6 — PREGATE PREDICTOR (early-commit on 15m alignment)
    //   When in the first 4 minutes of window AND 15m TC is fully aligned with
    //   our direction AND Kalshi is still cheap for our side AND no major veto,
    //   force the structural-led tier. This captures the "I can see this from
    //   the 15m chart but Tara's slower signals haven't built up yet" pattern.
    //   Triggers MORE locks, but only the high-confidence early ones — not the
    //   deadline-forced weak ones (those still go through the standard path).
    //   Fix: use _elapsed (seconds into window, already in scope) not params.*
    //   V10.6.7: loosened thresholds — pregate now fires when Tara is still
    //   forming a view but 15m structure already shows alignment. The original
    //   thresholds (TC slope ≥1.0, kalshi ≤40/≥60) required perfect setups that
    //   rarely materialize in chop. Real-world need is to catch the "Kalshi
    //   is still 45-55 because no one knows, but 15m has a slight lean" moments.
    const _secsIntoWindow=Number(_elapsed)||0;
    const _earlyWindow=_secsIntoWindow<300; // V10.6.7: was 240 → first 5 minutes (was 4)
    const _tcFullyAligned=_tc?.valid&&(
      (dir==='UP'&&_tcSlope>=0.5&&!_tcExhausted)||  // V10.6.7: was 1.0 → 0.5 (catches modest slopes)
      (dir==='DOWN'&&_tcSlope<=-0.5&&!_tcExhausted)
    );
    const _kForDirCheap=kalshiAvail&&(
      (dir==='UP'&&_kPct<=48)||   // V10.6.7: was 40 → 48 (catches 45-48 zone)
      (dir==='DOWN'&&_kPct>=52)   // V10.6.7: was 60 → 52
    );
    const _v10_6_6_pregate=_earlyWindow&&_tcFullyAligned&&_kForDirCheap
      &&conviction>=3 // V10.6.7: was 4 → 3 — trust 15m structure when Tara hasn't formed view yet
      &&!tapeSuperOpposes // tape veto still respected
      &&!kalshiExtremeDisagrees;
    if(_v10_6_6_pregate&&!isStructuralLed){
      isStructuralLed=true; // promote to structural-led tier
      // V10.6.6: reasoning array isn't in scope here — telemetry tracked via tier label downstream
    }
    const _strikeActionable=strikeFavorable||Math.abs(_gapBpsForDir)<=20;
    // Rising-confluence requires 3 of {15s tape, FGT-leans, score-leans, strike-actionable}
    //   plus existing vetoes still apply. Don't fire if already isConfluent — we have a
    //   stronger signal then.
    const _risingScore=(_15sLeads?1:0)+(_fgtLeansDir?1:0)+(_convLean?1:0)+(_strikeActionable?1:0);
    // V6.0.2: TIGHTENED — require ALL 4 leading conditions (was 3/4) AND no Kalshi
    //   mild disagreement (was: only extreme blocked). User constraint: WR must not
    //   dip below 70%. The 3/4 threshold was too permissive for high-WR target.
    const isRisingConfluence=!isConfluent&&_risingScore>=4&&!tapeOpposes&&!kalshiDisagrees;
    const _dirLead=`${dir} lean (${conviction.toFixed(0)}pt)`;
    // V7.8: Removed the dial-tier-block SIT_OUT (was V7.4). User: "remove skips completely,
    //   we trade each round." Dial position now only affects sample-count multipliers and
    //   search-phase length — NOT whether to commit. Tier-blocked trades still happen,
    //   they just take more samples to confirm. The 90s hard cap (lifecycle effect) ensures
    //   we always exit with a directional call.
    // V7.4: SPEED DIAL TIER FILTER. The dial now determines the MINIMUM tier that can lock.
    //   Previously the dial only adjusted Q_FLOOR / CONV_FLOOR / sample-count multipliers —
    //   too subtle to feel. Now patient mode actively sits out lower-tier trades:
    //     dial >=80 (patient/strict): only super-confluence + structural-led (~20-30% of setups)
    //     dial 50-79 (balanced):       also allows confluence (skips tape-led + rising)
    //     dial 30-49 (mid-fast):       also allows tape-led + rising
    //     dial <30  (fast):            all tiers including single-signal (current behavior)
    //   Per-tier WR audit (V7.2) showed super and structural have meaningfully higher WR than
    //   tape-led — so patient mode trading those trades only should lift WR materially.
    // V7.8: Dial-tier block REMOVED. User said "remove skips completely". The dial now
    //   only affects sample-count multiplier and search-phase length. Tier checks still
    //   inform tier labeling for analytics (per-tier WR audit) but no longer block locks.
    //   Q-floor and conviction-floor checks below are similarly relaxed via the always-
    //   commit ladder in the lifecycle (Path A/B/C above).
    // V10.3.2 — HIGH-CONVICTION BYPASS for Q_FLOOR, CONV_FLOOR, FGT-mixed.
    //   May 16 audit: 5 sit-outs blocked legitimate high-conviction calls that would have
    //   won (01:13 quality 35<39 with posterior 23, 01:43 FGT-mixed with posterior 76,
    //   02:59/09:43/13:43 coin-flip 5-8pt with posteriors 56-58 that ended up correct).
    //   When posterior is strong (≥72% UP or ≤28% DOWN), bypass these soft gates.
    //   Kalshi-extreme and tape-opposes guards REMAIN ENFORCED — those caught real errors
    //   (10:28, 12:58) and shouldn't be loosened.
    const _v10_3_2_highConv=(post>=72)||(post<=28);
    // Gate 1: quality floor
    // V6.0.7: tape-led bypass. When tape is super-strong agreeing, q-floor is irrelevant —
    //   raw order flow is direct evidence; quality score is a lagging aggregate.
    // V10.3.2: high-conviction bypass added — strong posterior overrides quality requirement.
    if(q<Q_FLOOR&&!isTapeLed&&!_v10_3_2_highConv){
      return{call:'SIT_OUT',reason:`Signal weak — quality ${q}/${Q_FLOOR}`,confidence:0,direction:dir,conviction,phase:'WATCHING'};
    }
    // Gate 2: conviction floor (THE coin-flip filter)
    // V6.0.7: tape-led bypass — tape ≥70% across windows is enough directional evidence
    //   even when posterior is still developing.
    // V10.3.2: LOWERED THRESHOLD from base 7 (~9pt at dial 65) → effective ~6pt.
    //   Achieved via direct comparison: require conviction < CONV_FLOOR-2 to sit out.
    //   This converts borderline coin-flips (conviction 5-8) into locks. Audit showed
    //   3 of 5 such cases would have won today.
    // V13.4.101: LOCK-ON-LEAN. User request: only sit out on a GENUINE coin flip; if there
    //   is a real directional lean, lock it, and keep structural checks (exhaustion, tape,
    //   regime flux, Kalshi disagreement, FGT) exactly as they are -- those already run as
    //   separate gates below/around this one and are UNCHANGED. Audit of 342 sit-outs with
    //   posterior logged: only 5%% (17) were within 5pt of 50 -- a genuine coin flip. The
    //   other 95%% (325) had a real lean and sat out for OTHER reasons anyway. Simulating
    //   force-lock on every non-coin-flip sit-out: 69.0%% WR, EV -0.13 +/- 3.39 c/ct --
    //   statistically breakeven, not a loser, so this is worth doing for the volume you
    //   asked for. ONE exception found and deliberately NOT touched here: the v1123-ev-gate
    //   post-lock override (separate code path, ~line 41308) measured -24.81 +/- 20.79 c/ct
    //   if bypassed (n=16, thin but a clean, large signal) -- it still runs after this and
    //   can still override a lock this gate allows through.
    //   Mechanism: widen the conviction floor ONLY when conviction is well clear of true
    //   neutral (>=COIN_FLIP_PT off 50, default 5 -- matches the 45-55pt band the archive
    //   was measured on). Between the old floor and this new one, a lock now proceeds to
    //   the structural gates below instead of stopping here. Dial: localStorage
    //   'taraCoinFlipPt' (default 5); 'taraLockOnLean'='off' restores the old strict floor.
    const _lolOn=(function(){try{return localStorage.getItem('taraLockOnLean')!=='off';}catch(_e){return true;}})();
    const _coinFlipPt=(function(){try{const v=parseFloat(localStorage.getItem('taraCoinFlipPt'));return(Number.isFinite(v)&&v>0&&v<=20)?v:5;}catch(_e){return 5;}})();
    const _v10_3_2_coinFlipEffective=(_lolOn&&conviction>=_coinFlipPt)?0:Math.max(1,CONV_FLOOR-2);
    if(conviction<_v10_3_2_coinFlipEffective&&!isTapeLed&&!_v10_3_2_highConv){
      return{call:'SIT_OUT',reason:`Coin flip — only ${conviction.toFixed(0)}pt off neutral (need ${_v10_3_2_coinFlipEffective})`,confidence:0,direction:dir,conviction,phase:'WATCHING'};
    }
    // V5.7.7 Gate 2.5: TAPE-OPPOSES veto. The most direct fix for FGT-alone failures.
    //   When two tape windows show ≥70% the OTHER way, no posterior can save this call —
    //   actual order flow is the truth. Even 60%+ opposition with no confluence is a hard veto.
    if(tapeSuperOpposes){
      return{call:'SIT_OUT',reason:`${_dirLead} — tape ${(dir==='UP'?(100-tapeBuyPct):tapeBuyPct).toFixed(0)}% opposite, fighting flow`,confidence:0,direction:dir,conviction,phase:'WATCHING'};
    }
    // V6.2.2: When Kalshi is ALREADY extreme (≥85% in our direction), it cannot rescue
    //   tape opposition. Real-world failure: tape was 78% sell, Tara locked UP because
    //   Kalshi at 90% "strongly agrees" — but Kalshi at 90% means zero edge (LATE ENTRY,
    //   -9pt). Correct behavior is sit out: the market has already priced in everything,
    //   we can't profitably fade tape just because we agree with the priced-in side.
    const _kalshiAlreadyExtreme=kalshiAvail&&((dir==='UP'&&_kPct>=85)||(dir==='DOWN'&&_kPct<=15));
    if(tapeOpposes&&!kalshiStronglyAgrees){
      return{call:'SIT_OUT',reason:`${_dirLead} — tape ${(dir==='UP'?(100-tapeBuyPct):tapeBuyPct).toFixed(0)}% opposite, no Kalshi rescue`,confidence:0,direction:dir,conviction,phase:'WATCHING'};
    }
    if(tapeOpposes&&_kalshiAlreadyExtreme){
      return{call:'SIT_OUT',reason:`${_dirLead} — tape opposes & Kalshi ${_kPct}% means no edge (late entry)`,confidence:0,direction:dir,conviction,phase:'WATCHING'};
    }
    // V6.4: ASYMMETRY EXHAUSTION GATE. When the latest swing strongly opposes our intended
    //   lock direction AND it's meaningfully stronger than the prior swing in our direction,
    //   that's structural exhaustion — the trend we want to ride is fading. Block the lock.
    //   "This down move is stronger than the up move was" → don't lock UP into a fading trend.
    const _asym=convictionAsymmetry;
    if(_asym?.valid&&_asym.score>=40&&_asym.latestDir&&_asym.latestDir!==dir){
      // Latest swing is opposite our lock direction and meaningfully strong (40+ asymmetry).
      //   This is the exhaustion case humans intuit. Sit out, wait for trend to actually resume.
      return{call:'SIT_OUT',reason:`${_dirLead} — exhaustion: ${_asym.latestDir} swing ${_asym.score}pt stronger than prior ${dir} swing`,confidence:0,direction:dir,conviction,phase:'WATCHING'};
    }
    // V6.4: REGIME TRANSITION GATE. If regime is in flux (multiple labels in last 60s),
    //   sit out — conditions are too unstable to commit. If transitioning-to-range and we're
    //   trying to ride a trend, that's the trend dying — sit out unless conviction is very high.
    const _regTrans=regimeTransitionRef.current;
    if(_regTrans?.state==='flux'){
      return{call:'SIT_OUT',reason:`${_dirLead} — regime in flux (${_regTrans.fromRegime}→${_regTrans.toRegime}), waiting`,confidence:0,direction:dir,conviction,phase:'WATCHING'};
    }
    if(_regTrans?.state==='transitioning-to-range'&&conviction<25){
      return{call:'SIT_OUT',reason:`${_dirLead} — trend dying (transitioning to range), conviction ${Math.round(conviction)}pt insufficient`,confidence:0,direction:dir,conviction,phase:'WATCHING'};
    }
    // V6.5: SHORT SQUEEZE TRAP GATE. Evidence-based fix from V6.4.0 export analysis.
    //   Data: structural-led × SHORT SQUEEZE = 4W/4L (50% WR). All 4 losses were UP calls
    //   with FGT 0.0/4. Tape-led × SHORT SQUEEZE = 1W/2L (33% WR), same pattern.
    //   SHORT SQUEEZE is a temporary spike from forced short-covering — it LOOKS like
    //   bullish momentum but typically exhausts within 1-2 windows. Without FGT confirming
    //   the underlying trend (≥1.5/4 same direction), the squeeze is fading, not continuing.
    //   Block UP locks in SHORT SQUEEZE unless FGT clearly confirms.
    const _isShortSqueeze=analysis.regime==='SHORT SQUEEZE';
    const _fgtMag=Math.abs(analysis.mtfAlignment||0);
    const _fgtAgreesDir=(analysis.mtfAlignment>0&&dir==='UP')||(analysis.mtfAlignment<0&&dir==='DOWN');
    // V9.4.0: SHORT SQUEEZE × UP further restriction. V9.2.3 measured WR (n=40
    //   in regime, of which 40 were UP): 56.1% — the new weakest regime now
    //   that V9.2.3 fixed TRENDING UP. Inside that regime: tape-led × UP was
    //   1/5 wins (20%), super-tier × UP was 0/2, tape+super combined cluster
    //   was 0/5. Existing gate required FGT ≥1.5/4 for structural/tape locks.
    //   New requirements per tier:
    //     • structural-led / tape-led: FGT must agree AND magnitude ≥2.5/4
    //     • super-confluence: FGT must agree AND magnitude ≥3.0/4
    //   These keep the squeeze-continuation case (strong FGT) but eliminate
    //   the marginal cases where the squeeze was actually exhausting.
    if(_isShortSqueeze&&dir==='UP'){
      if((isStructuralLed||isTapeLed)&&(!_fgtAgreesDir||_fgtMag<2.5)){
        return{call:'SIT_OUT',reason:`${_dirLead} — SHORT SQUEEZE without strong FGT confirmation (need ≥2.5/4 in direction, have ${_fgtMag.toFixed(1)}) — squeeze likely exhausting`,confidence:0,direction:dir,conviction,phase:'WATCHING'};
      }
      if(isSuperConfluent&&(!_fgtAgreesDir||_fgtMag<3.0)){
        return{call:'SIT_OUT',reason:`${_dirLead} — SHORT SQUEEZE super tier needs FGT ≥3.0/4 in direction (have ${_fgtMag.toFixed(1)}) — confluence without trend confirmation lost 0/2 last week`,confidence:0,direction:dir,conviction,phase:'WATCHING'};
      }
    }
    // V5.7.7 Gate 2.6: Kalshi extreme-disagreement gate. When market is ≥90% certain the
    //   other way, we need BOTH tape alignment AND pattern alignment to commit. Otherwise sit.
    if(kalshiExtremeDisagrees&&!(tapeStronglyAgrees&&_fgtSignAgrees)){
      return{call:'SIT_OUT',reason:`${_dirLead} — Kalshi ${_kPct}% says opposite, no override`,confidence:0,direction:dir,conviction,phase:'WATCHING'};
    }
    // Gate 3: Kalshi material disagreement — market sees opposite direction strongly.
    //   V6.0.9: Tightened to conv ≥30 (was ≥25). User feedback: Tara fights Kalshi too often
    //   and loses on those bets. Keep the override threshold high so we only fade Kalshi
    //   when posterior is at 80%+ in our direction.
    if(kalshiStronglyDisagrees&&conviction<30){
      return{call:'SIT_OUT',reason:`${_dirLead} — Kalshi ${_kPct}% opposite, need stronger conviction`,confidence:0,direction:dir,conviction,phase:'WATCHING'};
    }
    // V6.0.9 Gate 3b: Mild Kalshi disagreement (≥60% opposite) now also needs some override
    //   conviction. Was unguarded before — Tara would call against Kalshi at 60% with just
    //   conv 10. That's the source of "wrong-position trades" the user described.
    if(kalshiDisagrees&&!kalshiStronglyDisagrees&&conviction<18&&!isTapeLed){
      return{call:'SIT_OUT',reason:`${_dirLead} — Kalshi ${_kPct}% disagrees, conviction insufficient to fade`,confidence:0,direction:dir,conviction,phase:'WATCHING'};
    }
    // Gate 4: FGT support — primary multi-timeframe signal must show some pulse
    // V10.3.2: high-conviction bypass — strong posterior (≥72 or ≤28) overrides FGT-mixed.
    //   Audit: 01:43 had posterior 76 UP sat out for FGT 0.0/4 mixed, actual was UP.
    const isFastTrackRegime=['TRENDING UP','TRENDING DOWN','SHORT SQUEEZE','LONG SQUEEZE'].includes(analysis.regime);
    const fgtMin=tapeStronglyAgrees||kalshiStronglyAgrees?0.5:1.0; // V5.6.4: was 0.0/0.4/0.7
    if(fgtAbs<fgtMin&&!isFastTrackRegime&&!_v10_3_2_highConv){
      const availability=forecastAvailability(analysis.fgtResults);
      const unavailable=availability.validCount<availability.total;
      const reason=unavailable?`${_dirLead} — FGT forecasts ${availability.status} (${availability.validCount}/${availability.total} available); insufficient alignment ${fgtAbs.toFixed(1)}/4`:
        `${_dirLead} — FGT ${fgtAbs.toFixed(1)}/4 mixed across timeframes`;
      return{call:'SIT_OUT',reason,confidence:0,direction:dir,conviction,phase:'WATCHING',
        signalBlocker:{code:unavailable?'forecast-unavailable':'forecast-mixed',details:{alignment:fgtAbs,requiredAlignment:fgtMin,availability}}};
    }
    // V6.0.9 Gate 4.5: LATE-COMMIT GUARD. When elapsed > 70% of window, only high-quality
    //   tiers (super-confluence, confluence, tape-led, exceptional, strong+) can commit.
    //   Below that, sit out — late single-signal locks were the source of "confirmed DOWN
    //   with 4 minutes left" timing complaints. Better to have NO call than a panic-late one.
    const _elapsedFrac=_elapsed/(_remaining+_elapsed||1);
    const _isLateWindow=_elapsedFrac>=0.70;
    const _isHighTier=isSuperConfluent||isConfluent||isTapeLed||isRisingConfluence||(q>=75&&conviction>=18);
    if(_isLateWindow&&!_isHighTier){
      return{call:'SIT_OUT',reason:`${_dirLead} — too late for single-signal (${Math.round(_elapsedFrac*100)}% elapsed)`,confidence:0,direction:dir,conviction,phase:'WATCHING'};
    }
    // V6.0.3: Gate 5 (added in 6.0.2 — high-WR quality threshold) REMOVED. The user
    //   correctly pointed out that "below default tier" is not the same as "coin flip."
    //   Existing gates already filter coin-flips (Q_FLOOR=30, CONV_FLOOR=10, tape opposes,
    //   tape super-strong opposes, Kalshi extreme, Kalshi strong-disagree+low-conv, FGT min).
    //   If a setup gets past these, it has directional information — Tara should call it,
    //   just slowly. The 2-3 min lock-time cap is enforced by capping needSamples, not
    //   by refusing the call. WR is protected by the confluence-bucket self-learning
    //   which adjusts confidence per-bucket based on observed WR.
    // SOFT gates — apply confidence haircuts instead of blocking
    let confidenceHaircut=0;
    let haircutReasons=[];
    if(winType==='DEAD'||winType==='WHIPSAW'){
      if(q<50&&!tapeStronglyAgrees&&!kalshiStronglyAgrees){
        return{call:'SIT_OUT',reason:`${_dirLead} — ${winType} regime + q${q} no consensus from tape/kalshi`,confidence:0,direction:dir,conviction,phase:'WATCHING'};
      }
      confidenceHaircut+=15;
      haircutReasons.push(`${winType} regime`);
    }
    const isDivergent=
      (dir==='UP'&&(vfLabel==='BUY-DIVERGENT'||vfLabel==='BUY-FAILING'))||
      (dir==='DOWN'&&(vfLabel==='SELL-DIVERGENT'||vfLabel==='SELL-FAILING'));
    if(isDivergent){
      if(q<50&&conviction<18&&!tapeStronglyAgrees){
        return{call:'SIT_OUT',reason:`${_dirLead} — tape ${vfLabel} disagrees, q+conv weak`,confidence:0,direction:dir,conviction,phase:'WATCHING'};
      }
      confidenceHaircut+=15;
      haircutReasons.push(`tape ${vfLabel}`);
    }
    if(kalshiDisagrees&&!kalshiStronglyDisagrees){
      // Mild Kalshi disagreement (40-45 / 55-60) — apply haircut, don't block
      confidenceHaircut+=10;
      haircutReasons.push(`kalshi ${_kPct}% disagrees`);
    }
    // All gates passed — Tara forms a call
    const _confBase=Math.min(95,Math.round(conviction*1.5+q*0.4));
    const _tapeBoost=tapeSuperStrong?15:tapeStronglyAgrees?8:0;
    const _kalshiBoost=kalshiStronglyAgrees?10:kalshiAgrees?5:0;
    // V5.7.7: Confluence boost. When tape, FGT, score, AND strike all agree, confidence
    //   gets a meaningful bump — this is the "this is the kind of call we want" reward.
    const _confluenceBoost=isSuperConfluent?12:isConfluent?6:0;
    // V5.6.8: Learning boost — when current regime+direction matches historically winning
    //   combos, add small confidence boost. When current matches historically losing combos,
    //   subtract. This is how Tara learns to PICK BETTER without sitting out more.
    // V6.5: SHORT SQUEEZE clamp. Evidence from V6.4.0 export: the learned UP bias in
    //   SHORT SQUEEZE is anti-correlated with outcomes (4 of 4 structural-led losses had
    //   "+5 learned UP preference" in the reason). The bias captures brief upward spikes
    //   that registered as wins before reversing. Clamp this regime to ±2 until we have
    //   enough confirmed-loss samples to trust the inverse direction.
    const _regDirKey=(analysis.regime||'UNKNOWN')+'|'+dir;
    const _rawLearnBoost=_learn?.multipliers?.regimeDirConfBoost?.[_regDirKey]||0;
    const _isSqueezeRegime=analysis.regime==='SHORT SQUEEZE'||analysis.regime==='LONG SQUEEZE';
    const _learnCap=_isSqueezeRegime?2:8;
    const _learnConfBoost=0; // V14.7: verified probability calibration already supplies this feedback
    // V5.7.8: Learned confluence-bucket boost. Bucket determined by current confluence state.
    //   Historical WR for this bucket informs the boost/haircut.
    // V6.0.1: rising-confluence is its own bucket — separate WR tracking from full confluence.
    // V6.0.7: tape-led is its own bucket too — separate WR tracking from full confluence.
    const _conflBucket=isStructuralLed?'structural-led':isSuperConfluent?'super-confluence':isConfluent?'confluence':isTapeLed?'tape-led':isRisingConfluence?'rising-confluence':'single-signal';
    const _learnConflBoost=0; // do not reward the same historical outcome twice
    // V6.5.3 BUGFIX: cap final confidence at 99. Without this cap, base (95) + tape boost
    //   (+10) + kalshi boost (+10) + confluence boost (+12) + learning boosts could
    //   exceed 100 — screenshot showed "123% confidence" which is mathematically impossible.
    //   Floor stays at 25 (a forming call is at least 25% confident).
    const _confidence=Math.max(25,Math.min(99,_confBase-confidenceHaircut+_tapeBoost+_kalshiBoost+_confluenceBoost+_learnConfBoost+_learnConflBoost));
    const _reasonParts=[`${conviction.toFixed(0)}pt ${dir} lean`,`FGT ${fgtAbs.toFixed(1)}/4`,`q${q}`];
    if(isSuperConfluent)_reasonParts.unshift(`SUPER-CONFLUENCE — tape+FGT+score+strike all ${dir}`);
    else if(isConfluent)_reasonParts.unshift(`CONFLUENCE — tape+FGT+score+strike all ${dir}`);
    else if(isTapeLed)_reasonParts.unshift(`TAPE-LED — tape ≥70% ${dir} across windows, fast-locking`);
    else if(isRisingConfluence)_reasonParts.unshift(`RISING — early entry, ${_risingScore}/4 leading ${dir}`);
    if(tapeSuperStrong)_reasonParts.push(`tape super-strong (${tapeBuyPct.toFixed(0)}%)`);
    else if(tapeStronglyAgrees)_reasonParts.push(`tape ${tapeBuyPct.toFixed(0)}%`);
    if(kalshiStronglyAgrees)_reasonParts.push(`kalshi ${_kPct}% confirms`);
    else if(kalshiAgrees)_reasonParts.push(`kalshi ${_kPct}% leans ${dir}`);
    if(haircutReasons.length>0)_reasonParts.push(`-${confidenceHaircut} for ${haircutReasons.join(' + ')}`);
    if(_learnConfBoost>0)_reasonParts.push(`+${_learnConfBoost} learned ${dir} preference in ${analysis.regime}`);
    else if(_learnConfBoost<0)_reasonParts.push(`${_learnConfBoost} historical ${dir} weakness in ${analysis.regime}`);
    if(_learnConflBoost>0)_reasonParts.push(`+${_learnConflBoost} from ${_conflBucket} history`);
    else if(_learnConflBoost<0)_reasonParts.push(`${_learnConflBoost} from ${_conflBucket} history`);
    // V9.4.0: MARGINAL-ZONE CAUTION. V9.2.3 measured: |gap| < 10bps trades
    //   landed at 59.3% WR (n=86). Same posterior in clean-move windows hit
    //   85.7%. The marginal-zone losses concentrate in `single` tier with low
    //   ATR + low quality. Per user mandate ("cautions instead of skips"),
    //   we still call directionally — but attach a caution string so the call
    //   card surfaces the warning chip and the user can size down.
    let _marginalCaution=null;
    const _isSingleTier=!isSuperConfluent&&!isConfluent&&!isStructuralLed&&!isTapeLed&&!isRisingConfluence;
    // V9.5.2: BUGFIX — atrBps is not in scope inside the taraCall IIFE (it's a local
    //   inside the analysis useMemo). Read it from the analysis object instead.
    //   This was crashing every render with ReferenceError: atrBps is not defined.
    const _atrBpsNum=Number(analysis?.atrBps)||0;
    const _isLowVol=_atrBpsNum>0&&_atrBpsNum<8;
    const _isLowQ=q<50;
    if(_isSingleTier&&_isLowVol&&_isLowQ){
      _marginalCaution=`marginal zone — ATR ${_atrBpsNum.toFixed(1)}bps, q${q} · 59% WR baseline · size down`;
      _reasonParts.push('⚠ marginal');
    }
    // V9.7.6: WEAK-CLUSTER CAUTION (soft — not a gate). Per user mandate "we are
    //   not missing out calling on trades", we keep firing on these clusters but
    //   attach a caution chip so the user knows the setup is historically thin.
    //   Largest weak cluster from 2026-05-07 audit: SHORT SQUEEZE × UP × single
    //   running 63% WR (n=27) when stated 75-85%. User can size down or skip.
    if(!_marginalCaution&&_isSingleTier&&analysis?.regime==='SHORT SQUEEZE'&&dir==='UP'){
      _marginalCaution=`thin setup — SHORT SQUEEZE × UP × single tier · 63% baseline (n=27) · honest conf vs displayed`;
      _reasonParts.push('⚠ thin');
    }
    if(!_marginalCaution&&_isSingleTier&&analysis?.regime==='TRENDING UP'&&dir==='UP'&&isStructuralLed===false){
      // Catch the new weak cluster: TRENDING UP × structural at 37.5% (n=8 — small but bleeding)
      // Single-tier in TRENDING UP is fine (70% WR), so this only fires if it somehow gets here
    }
    // V10.2.13 — session × tier multiplier (data-driven, opt-in via sessionTierMode)
    //   When live: applies a multiplier to confidence per (current session, current tier).
    //   When shadow: computes the would-be multiplier and logs it for CSV but doesn't change _confidence.
    //   When off: no effect.
    //   Multipliers default to 1.0 (identity) unless user has populated localStorage
    //   via __taraSessionTier('derive') or manual setting.
    // V10.2.17 — multiplier is NO LONGER applied to display confidence. The
    //   V10.2.16 application to _confidence was directionally wrong (higher
    //   conviction made edge bigger which made auto-exec MORE conservative,
    //   opposite of "trust this combo more"). The multiplier now ONLY affects
    //   the auto-exec edge cap (line ~30760), where mult>1 loosens the cap
    //   for strong tier×session combos. The cosmetic confidence display in
    //   the UI now matches what the model actually computed (raw posterior),
    //   so the headline number is honest. CSV columns still log mult/mode/
    //   applied for forensics.
    let _sessionTierMult=1.0;
    let _sessionTierApplied=false;
    try{
      const _curSession=(typeof getMarketSessions==='function'?getMarketSessions().dominant:null)||'';
      const _curTier=isSuperConfluent?'confluence':isConfluent?'confluence':isStructuralLed?'structural':isTapeLed?'tape':_isSingleTier?'single':null;
      _sessionTierMult=_resolveSessionTierMultiplier(_curSession,_curTier);
      // 'applied' now means "would have affected edge cap" — the live decision point.
      //   Set true when in live mode AND multiplier deviates from identity.
      _sessionTierApplied=sessionTierMode==='live'&&_sessionTierMult!==1.0;
    }catch(_){}
    return{
      call:dir,reason:_reasonParts.join(' · '),confidence:_confidence,direction:dir,conviction,phase:'FORMING',
      caution:_marginalCaution, // V9.4.0: surfaces in TaraCallCard caution chip
      // Pass through to lifecycle so it can compute needSamples consistently.
      // V10.2.13: include session-tier diag so the CSV writer can attach columns.
      _ctx:{q,conviction,fgtAbs,regime:analysis.regime,winType,tapeStronglyAgrees,tapeSuperStrong,kalshiAgrees,kalshiStronglyAgrees,_remaining,_elapsed,isConfluent,isSuperConfluent,isRisingConfluence,isTapeLed,isStructuralLed,strikeFavorable,marginalCaution:_marginalCaution,
        _sessionTierMode:sessionTierMode,
        _sessionTierMult:Math.round(_sessionTierMult*1000)/1000,
        _sessionTierApplied:_sessionTierApplied,
        _confidencePreAdjust:_confidence, // V10.2.17: alias for backward compat — same as confidence now
      },
    };
  })();
  // V10.6.8b — LEGACY DEAD-WINDOW-ONLY SITOUT RULE (opt-in only)
  // V14.4.6: the history below describes the old mode, not current policy.
  // NO_SITOUT_MODE=false keeps failed quality checks as waits before cutoff.
  // User rule (May 19, 1pm): "sit out ONLY when the price is literally not
  //   moving at all." Every other sitout reason gets overridden into a
  //   directional commit using posterior (which is the synthesized view of
  //   tape + structure + FGT + recent windows + everything else).
  //
  // Sitout PRESERVED only when:
  //   1. Engine still loading / data not ready (physical constraint)
  //   2. New window observation phase (first 5s - physical constraint)
  //   3. Search phase active (engine literally collecting samples)
  //   4. DEAD WINDOW: windowAmplitude.rangeBps < 5 AND >=2 min elapsed
  //      (genuinely no price movement to predict)
  //
  // Everything else (signal disagreements, floors, kalshi conflicts, tape
  //   conflicts) → committed to posterior direction. The posterior already
  //   encodes those signals' disagreement as weaker conviction — but a lean
  //   is a lean. Take the side, ride the movement.
  // V14.4.6: use directional probability, including a genuine zero P(UP).
  // Failed quality checks cannot be promoted while NO_SITOUT_MODE is false.
  const _v10_6_8_postProcess=(call)=>postProcessLegacyCall(call,analysis,{allowQualityOverride:NO_SITOUT_MODE});
  // V10.6.8: apply force-commit post-processor IN PLACE on taraCall object.
  //   Using Object.assign mutates the const-bound object reference (const
  //   prevents rebinding, not mutation). This means ALL downstream consumers
  //   automatically see the forced commits without needing to rename refs.
  const _v10_6_8_forced=_v10_6_8_postProcess(taraCall);
  if(_v10_6_8_forced&&_v10_6_8_forced!==taraCall){
    Object.keys(_v10_6_8_forced).forEach(k=>{taraCall[k]=_v10_6_8_forced[k];});
  }

  // V13.4.71: DECENT-ODDS GATE -- sit out the coin-flip mush (Kalshi 40-60c without tape) + untradeable
  //   price extremes (<15c / >=85c). Runs AFTER the V10.6.8 force-commit (the only SIT_OUT->lock
  //   converter); every later post-processor guards on call==='UP'||'DOWN' so they respect this sit-out.
  //   Backtest on 4035 locks: cuts low-edge volume, lifts WR 64->68%; held 52-63% vs 47-56% in cold regime.
  //   Off-switch: localStorage 'taraDecentOddsGate'='off'.
  try{
    const _dogOn=(function(){try{return localStorage.getItem('taraDecentOddsGate')!=='off';}catch(_){return true;}})();
    if(_dogOn&&taraCall&&(taraCall.call==='UP'||taraCall.call==='DOWN')){
      // Live analysis has no kalshiAtLock before a sealed Call. Read the live
      // YES quote, not an absent or historical lock field; identity/age still
      // have to pass the unchanged commit quote guard.
      const _kEntry=typeof kalshiYesPrice==='number'&&Number.isFinite(kalshiYesPrice)?kalshiYesPrice:null;
      const _tapeOk=!!(taraCall.isTapeLed||(taraCall._ctx&&taraCall._ctx.isTapeLed));
      if(_kEntry!=null){
        // V13.4.77: REVERTED the 13.4.74 EV ceiling -- it was a pricing bug. It gated on raw
        //   kalshiAtLock (Kalshi YES price), which is the entry cost ONLY for UP locks; a DOWN
        //   lock's cost is 100-kalshiAtLock. So the 60c ceiling blocked profitable cheap-DOWN
        //   underdog bets and mid UP-favorite locks -- about -3209c of real profit removed.
        //   The raw-kalshiAtLock EXTREMES are symmetric and correct: <15 or >=85 means one side
        //   pays a <15c fill-mirage and the other an over-priced >=85c favorite (the only -EV
        //   bucket). Direction-aware refinement can come later; this restores known-good 71.
        const _extreme=!NO_ENTRY_GATES&&(_kEntry<15||_kEntry>=85);
        // V13.4.78: coin-flip gate now DEFAULT OFF. Correct-pricing backtest (1143 zone locks):
        //   40-60c coin-flip is +3.6c/ct in EVERY regime (RANGE-CHOP +9.6, HIGH-VOL-CHOP +3.6),
        //   max drawdown 7.6 contracts vs +41 profit. The old tape-allow caught only n=4, so the
        //   gate was a blanket ban on 1139 profitable locks (-3960c). Dropping it lifts engine
        //   P&L +5758c->+9718c for +0.5ct more drawdown. Opt back in: localStorage 'taraCoinFlipGate'='on'.
        const _cfGateOn=(function(){try{return localStorage.getItem('taraCoinFlipGate')==='on';}catch(_e3){return false;}})();
        const _coinFlipNoTape=_cfGateOn&&(_kEntry>=40&&_kEntry<60&&!_tapeOk);
        // V13.4.90: ENTRY-QUALITY GATE -- the fix for 'I keep entering at 70c'.
        //   Measured on 1230 timed+priced locks, DIRECTION-AWARE cost (kEntry for UP,
        //   100-kEntry for DOWN -- the pricing error that caused the 13.4.74 loss):
        //     unfiltered            n=1230 avg cost 61c EV +3.55c/ct
        //     block >2/3 window left OR cost>=70c -> n=461 avg cost 51c EV +7.80c/ct
        //   Keeps 82%% of realized profit on 37%% of the trades at 10c cheaper entries.
        //   WHY: early locks are Tara chasing an obvious move and PAYING for the
        //   obviousness -- 700-900s left averages 64c and earns -0.04c/ct, while 0-120s
        //   left averages 57c and earns +10.42c/ct. Same tiers, opposite outcomes, and
        //   the split is price-driven: time-cap-commit returns +9.97 under 50c but
        //   -0.28 at 65-80c. So this gates on PRICE and TIMING, never on direction.
        //   ROBUSTNESS (the check that killed the UP/DOWN rewire and the calendar table):
        //   holds in BOTH chronological halves -- H1 +11.14 vs +6.48 unfiltered,
        //   H2 +5.36 vs +0.62. Timing is a window FRACTION so 5m windows scale correctly.
        //   Dials: 'taraEntryQuality'='off' disables; 'taraEntryMaxCost' (default 70);
        //   'taraEntryMaxFrac' (default 0.667 = block when >2/3 of the window remains).
        // V13.4.91: DEFAULTS MADE INERT. The 13.4.90 defaults (70c / 0.667) doubled EV per
        //   contract but cut lock count to 37%% and REDUCED total realized profit 4366c ->
        //   3598c. Higher EV/ct on fewer trades only wins if you size up ~2.7x to hold
        //   exposure constant -- that is a sizing decision, not a signal fix, so it must be
        //   opt-in. Every volume-preserving variant (87-90%% kept) gained only ~+0.25c/ct and
        //   FAILED split-half (H1 got worse). Defaults now match the known-good 13.4.77 gate
        //   (85c extreme block, no timing block) = zero behavior change. Turn on when sizing
        //   up: taraEntryMaxCost=70 and taraEntryMaxFrac=0.667.
        // V13.4.103: BAND MODE, ON BY DEFAULT per explicit user request. Restricting to
        //   30-60c measured on 4246 locks: n=1764 (42%% of volume), EV +5.10 +/- 2.32 c/ct --
        //   the best EV/contract of anything tested, holds in both chronological halves
        //   (H1 +5.79, H2 +3.75). The honest tradeoff, stated plainly: total realized
        //   profit drops from 14704c to 8999c (-39%%) because 58%% of volume is skipped.
        //   This is a fewer-trades-higher-quality choice, not a free upgrade -- shipped
        //   anyway because the user asked for it explicitly, twice, with that tradeoff
        //   already stated. Reverse this (or widen the band) if the volume cut feels wrong
        //   in practice: taraEntryQuality='off' fully restores pre-band behavior.
        const _eqOn=(function(){try{return localStorage.getItem('taraEntryQuality')!=='off';}catch(_e4){return true;}})();
        const _eqMinCost=getEntryMinCost();
        const _eqMaxCost=getEntryMaxCost();
        const _eqMaxFrac=(function(){try{const v=parseFloat(localStorage.getItem('taraEntryMaxFrac'));return(Number.isFinite(v)&&v>0&&v<=1)?v:1.0;}catch(_e6){return 1.0;}})();
        const _eqCost=(taraCall.call==='UP')?_kEntry:(100-_kEntry);
        const _totalSec=windowType==='15m'?900:300; // V13.4.120 FIX: was undefined -- silently threw and swallowed the entire entry-quality/traj-bypass block below.
        const _eqSecsLeft=(function(){try{return Math.max(0,(timeState.minsRemaining*60)+timeState.secsRemaining);}catch(_e7){return null;}})();
        const _eqFracLeft=(_eqSecsLeft!=null&&_totalSec>0)?(_eqSecsLeft/_totalSec):null;
        // V13.4.108: TRAJ-EARLY BYPASS, explicit user request -- 'lock on the projected
        //   side as early as possible, do not care about odds'. Off by default: this skips
        //   the entry-band price check (not the extreme/coin-flip safety gates) ONLY when
        //   Tara's own trajectory projection (analysis.projectedGapBps) agrees with the
        //   call direction. It does not fire on price/tape alone -- there must be a real,
        //   fresh trajectory read pointing the same way as the call, which is a materially
        //   different bar than 'any lean'. UNMEASURED as a strategy: TRAJ itself only
        //   started being logged in 13.4.105, so there is no realized-money track record
        //   yet for trusting it enough to override price. V13.4.109: made ON BY DEFAULT
        //   per explicit follow-up request -- no console/localStorage step should be
        //   required. Off-switch preserved for if this needs to be walked back once real
        //   outcomes accumulate: localStorage 'taraTrajEarlyLock'='off'.
        const _trajEarlyOn=(function(){try{return localStorage.getItem('taraTrajEarlyLock')!=='off';}catch(_e12){return true;}})();
        const _trajGapBps=Number(analysis?.projectedGapBps)||0;
        const _trajDir=_trajGapBps>0?'UP':(_trajGapBps<0?'DOWN':null);
        const _trajAgrees=_trajEarlyOn&&_trajDir===taraCall.call&&_trajGapBps!==0;
        // V13.4.110: stash what direction TRAJ actually agreed with, and whether the
        //   bypass fired, on the OBJECT ITSELF (survives the taraCall[k]=... merges every
        //   override below does, since they copy whatever keys exist onto taraCall in
        //   place -- confirmed by reading V10.7.4/19/11's own merge code, all three use
        //   Object.keys(result).forEach(k=>taraCall[k]=result[k])). See the correction
        //   check right before persistence for why this matters.
        if(_trajAgrees){taraCall._trajBypassDir=taraCall.call;taraCall._trajBypassFired=true;}
        const _tooExpensive=!NO_ENTRY_GATES&&_eqOn&&Number.isFinite(_eqCost)&&_eqCost>=_eqMaxCost&&!_trajAgrees;
        const _tooCheap=!NO_ENTRY_GATES&&_eqOn&&Number.isFinite(_eqCost)&&_eqCost<_eqMinCost&&!_trajAgrees;
        const _tooEarly=!NO_ENTRY_GATES&&_eqOn&&_eqFracLeft!=null&&_eqFracLeft>_eqMaxFrac&&!_trajAgrees;
        if(_extreme||_coinFlipNoTape||_tooExpensive||_tooCheap||_tooEarly){
          taraCall.call='SIT_OUT';
          taraCall.reason=(_tooExpensive||_tooCheap||_tooEarly)
            ?('[V13.4.103] entry band '+_eqMinCost.toFixed(0)+'-'+_eqMaxCost.toFixed(0)+'c: '+
              (_tooExpensive?('entry '+_eqCost.toFixed(0)+'c >= '+_eqMaxCost.toFixed(0)+'c -- paying up, this bucket earns far less'):
               _tooCheap?('entry '+_eqCost.toFixed(0)+'c < '+_eqMinCost.toFixed(0)+'c -- too cheap, low win-rate lottery zone'):
               ('too early -- '+Math.round(_eqSecsLeft)+'s left ('+Math.round(100*_eqFracLeft)+'% of window)'))+
              '. Waiting for a price inside the band.')
            :('[V13.4.77] decent-odds gate: '+(_extreme?('untradeable extreme '+_kEntry.toFixed(0)+'c'):('coin-flip '+_kEntry.toFixed(0)+'c, no tape')))
          taraCall._decentOddsGated=true;
          taraCall._decentOddsKEntry=_kEntry;
        }
      }
    }
  }catch(_){}

  // V13.4.347: EXPLICIT COST-BAND LOCK, direct user request 2026-09-11 --
  //   "only lock between 44-77c... it can lock whenever in the window at
  //   her own confidence." No timing restriction at all, deliberately --
  //   this ONLY gates on the direction-aware entry price. Independent of
  //   the entry-quality band above (currently fully inert: NO_ENTRY_GATES
  //   forces its min/max to 0/100) -- not reusing that machinery on
  //   purpose, since NO_ENTRY_GATES also controls unrelated edge-watch/EV
  //   gates this request never asked to re-enable. Runs after the V10.6.8
  //   force-commit like every other gate in this cascade, so it needs no
  //   whitelist entry there -- it only ever converts UP/DOWN to SIT_OUT,
  //   never the other way. Off-switch: localStorage 'taraCostBandGate'='off'.
  //   Override band: 'taraCostBandMin'/'taraCostBandMax' (cents).
  try{
    const _cbPolicy=readCallCostBand(localStorage);
    const _cbOn=_cbPolicy.enabled;
    if(_cbOn&&taraCall&&(taraCall.call==='UP'||taraCall.call==='DOWN')){
      const _cbEntry=typeof kalshiYesPrice==='number'&&Number.isFinite(kalshiYesPrice)?kalshiYesPrice:null;
      if(_cbEntry!=null){
        const _cbMin=_cbPolicy.minCost;
        const _cbMax=_cbPolicy.maxCost;
        // Direction-aware: kalshiAtLock is the raw Kalshi YES price, the
        //   entry cost ONLY for an UP lock -- a DOWN lock's real cost is
        //   100-kalshiAtLock (the exact pricing bug this file already
        //   fixed once before, V13.4.77's own comment above documents it).
        const _cbCost=(taraCall.call==='UP')?_cbEntry:(100-_cbEntry);
        if(_cbCost<_cbMin||_cbCost>_cbMax){
          taraCall.call='SIT_OUT';
          taraCall.reason='[V13.4.347] cost band '+_cbMin.toFixed(0)+'-'+_cbMax.toFixed(0)+'c: entry '+_cbCost.toFixed(0)+'c is outside the band. Waiting for a price inside it.';
          taraCall.noGoCategory='cost-band-sitout';
          taraCall._costBandGated=true;
          taraCall._costBandEntry=_cbCost;
        }
      }
    }
  }catch(_){}

  // V13.4.347: LOSS-STREAK COOLDOWN, direct user request 2026-09-11 -- "a
  //   cooldown of 2 windows where tara's call lock sits out after 2 losses
  //   in a row." _lossCooldownRef is armed by the taraCallLog watcher and
  //   counted down by exactly one per real window rollover (both declared
  //   near _rolloverGraceRef / taraCallLogRef). Same "runs after the
  //   force-commit, only ever downgrades UP/DOWN to SIT_OUT" shape as
  //   every other gate in this cascade -- no whitelist entry needed.
  //   Off-switch: localStorage 'taraLossCooldownGate'='off'.
  try{
    const _lcOn=(function(){try{return localStorage.getItem('taraLossCooldownGate')!=='off';}catch(_){return true;}})();
    if(_lcOn&&taraCall&&(taraCall.call==='UP'||taraCall.call==='DOWN')&&_lossCooldownRef.current.windowsRemaining>0){
      taraCall.call='SIT_OUT';
      taraCall.reason='[V13.4.347] loss-streak cooldown: 2 losses in a row, sitting out for '+_lossCooldownRef.current.windowsRemaining+' more window(s).';
      taraCall.noGoCategory='loss-streak-cooldown';
      taraCall._lossCooldownGated=true;
      taraCall._lossCooldownWindowsRemaining=_lossCooldownRef.current.windowsRemaining;
    }
  }catch(_){}

  // ═══════════════════════════════════════════════════════════════════════════
  // V14.7: one forecast owns automatic direction. Reversal/trajectory
  // features already participate upstream; their diagnostics cannot mutate a
  // qualified candidate, reuse its tier flags or synthesize a new probability.
  taraCall.decisionEvidence=callDecisionEvidence(taraCall,analysis,tapeWindows);
  const _v10_7_6_reversalCheck=(postValue)=>{
    const _p=Number(postValue);
    if(postValue==null||postValue===''||!Number.isFinite(_p)||_p<0||_p>100||_p===50)return{flipped:false,dir:null,reasons:[]};
    const _proposedDir=_p>50?'UP':'DOWN';
    return{flipped:false,dir:_proposedDir,conf:Math.round(directionalConfidence(_p,_proposedDir)),reasons:[]};
  };
  // Three coordinated effects, all gated by the same enable + kill-switch checks:
  //   1. ENTRY  — when lockedCallRef transitions to a new committed lock
  //   2. POLL   — while an order is working, check status every 2s
  //   3. EXIT   — when offer ≥ autoExitOffer OR seconds-left ≤ timeExitSecLeft, sell out
  // ───────────────────────────────────────────────────────────────────────────

  // V9.17.18: MANUAL PLACE-ORDER on Tara's call.
  //   Tara's snapshot (taraCallSnapshotRef) is set on time-cap-commit, timer-commit,
  //   no-go-data, and no-go-edge paths — but those paths do NOT write to
  //   lockedCallRef.current (the engine-lock ref auto-exec reads). Result: when
  //   Tara hard-commits at the time cap, auto-exec sits out unless the engine
  //   independently locks later (which may or may not happen).
  //   This callback lets the user manually trigger auto-exec on Tara's call by
  //   synthesizing a lock object that the entry effect will fire on. The
  //   `_bypassAutoExecFilters` flag tells the entry effect to skip soft filters
  //   (tier, quality, conviction, marginal caution) since the user explicitly
  //   opted in. Hard rails (kill switch, cooldowns, asset/window filters, bet
  //   size cap, anti-tilt) still apply.
  // ── ENTRY EFFECT ──────────────────────────────────────────────────────────
  // Watches the engine lock and, when auto-exec is armed, walks the entry
  // ladder and records the outcome in autoOrderState.
  //
  // DEFAULTS ARE DISARMED AND DRY RUN. Both live in the autoExecSettings
  // initialiser above and nothing here changes them: `enabled` is !!v.enabled,
  // so false unless explicitly stored true, and `dryRun` is v.dryRun!==false,
  // so true unless explicitly stored false. With stock settings the effect
  // never even starts its timer, and once armed it still simulates until
  // dryRun is deliberately turned off.
  //
  // Why a poll rather than a dependency on a lock value: lockedCallRef is a
  // ref, so writing a lock does not re-render and an effect keyed on state
  // would miss it. The timer only exists while armed, so a disarmed app pays
  // nothing for it.
  //
  // Rails, hardest and cheapest first. Every one of these returns a reason
  // string so a skip is explainable rather than silent:
  //   1. armed              — autoExecSettings.enabled
  //   2. kill switch        — user-engaged, blocks auto and manual alike
  //   3. credentials
  //   4. a real lock
  //   5. one order per lock — dedupe on lock identity so a re-render, a
  //                           settings change or an overlapping tick cannot
  //                           double-fire
  //   6. nothing in flight  — never stack on a live autoOrderState
  //   7. usable live quote  — reads _execCostCents, which enforces a max age
  //                           and gives the cost on the side actually taken.
  //                           Deliberately NOT kalshiAtLock, which is the mid
  //                           and optimistic by half the spread on every trade
  //   8. bet size cap       — maxBetPerTrade, in dollars
  //
  // The walk aborts if the lock disappears, flips direction, or the kill
  // switch engages mid-rest, so a reversal cancels rather than fills into it.
  const _entryFiredForRef=useRef(null);
  const _liveRiskReadRef=useRef({at:0,promise:null});
  const _entryBusyRef=useRef(false);
  // V13.4.309: bankroll-settlement ledger. Keyed by 'BTC|'+windowId. Written
  //   by _runEntry (fill) and _runExit (early exit) below; read by the
  //   mission settlement effect further down, which needs real fill/exit
  //   data to price a window's WIN/LOSS correctly -- autoOrderState itself
  //   gets overwritten by the next window, so this is the only place that
  //   data survives long enough. A plain ref, not persisted: a reload
  //   between fill and settlement loses that window's entry here, which the
  //   settlement effect treats as an honest gap (mission.bankrollDataGaps),
  //   never a silently wrong number.
  const _execSettlementLedgerRef=useRef(restoreExecutionLedger(localStorage));
  const _paperExpiryHealthRef=useRef({at:0,settled:0,failures:[]});
  useEffect(()=>{
    let stopped=false,busy=false,nextRead=0,cloudCalls=null,cloudCallsAt=0,cloudCallsFailedAt=0;
    const tick=async()=>{
      if(stopped||busy||Date.now()<nextRead)return;
      busy=true;
      try{
        const result=await recoverExpiredPaperExecutions({storage:localStorage,now:Date.now(),
          readState:()=>autoOrderStateRef.current,
          withLock:typeof navigator!=='undefined'&&navigator.locks?(name,fn)=>navigator.locks.request('tara-execution:'+name,fn):null,
          readOriginalCalls:async()=>{
            const local=taraCallLogRef.current||[];
            // A failed read of the multi-MB ledger waits 10 minutes. Retrying it
            // every tick kept the database pinned on Oct 26 after it slowed down.
            if((cloudCalls===null||Date.now()-cloudCallsAt>30*60000)&&Date.now()-cloudCallsFailedAt>10*60000){
              try{const d=await cloudSupabaseRead('memory/taraCallLog',{throwOnError:true});cloudCalls=Array.isArray(d)?d:d?.entries||[];cloudCallsAt=Date.now();cloudCallsFailedAt=0;}
              catch{cloudCallsFailedAt=Date.now();}
            }
            if(cloudCalls===null){if(local.length)return local;throw Error('Original Call record unavailable');}
            return [...local,...cloudCalls];
          },
          readMarket:async ticker=>{
            for(const path of hourlyPublicPaths('markets/'+encodeURIComponent(ticker),API_BASE)){
              const r=await readHourlyPublic(path,{validate:d=>!!d?.market});if(r.ok)return r.data.market;
            }
            throw Error('Original paper market unavailable');
          },
          onSettled:(entry,state)=>{
            _execSettlementLedgerRef.current[(entry.asset||'BTC')+'|'+entry.windowId]={...entry,enteredAt:entry.at};
            if(!stopped&&state)setAutoOrderState(prev=>prev?.dryRun===true&&prev.intentId===entry.id&&prev.ticker===entry.ticker&&['filled','settlement-pending'].includes(prev.status)?state:prev);
          },
        });
        _paperExpiryHealthRef.current={at:Date.now(),settled:result.settled.length,failures:result.failures};
        if(result.failures.length)nextRead=Date.now()+60000;
      }catch{_paperExpiryHealthRef.current={at:Date.now(),settled:0,failures:[{reason:'paper-settlement-unavailable'}]};nextRead=Date.now()+60000;}
      finally{busy=false;}
    };
    tick();const timer=setInterval(tick,20000);return()=>{stopped=true;clearInterval(timer);};
  },[]);
  const _executionControlRef=useRef(null);
  _executionControlRef.current={enabled:autoExecSettings?.enabled,kill:killSwitchEngaged,windowId:computeWindowId(windowType)};

  // Stake, always clamped to the per-trade cap. 'percent' has no balance in
  // scope here, so it falls back to the cap rather than guessing large.
  // V13.4.310: Mission Mode sizes off real fractional-Kelly math instead of
  //   the flat cap, when active. 'contracts' entry mode above still always
  //   wins -- unchanged, existing convention: an explicit contract count
  //   overrides any sizing mode. lockedCallRef is a ref (stable identity,
  //   correctly omitted from deps below) read directly rather than added as
  //   a new parameter, so this call site is untouched.
  const _resolveStakeDollars=useCallback((s,costCents)=>{
    const cap=Math.max(0,Number(s?.maxBetPerTrade)||0);
    if(!(cap>0))return 0;
    if(s?.entryMode==='contracts'&&costCents>0){
      const n=Math.max(1,Math.floor(Number(s.entryContracts)||0));
      return Math.min(cap,(n*costCents)/100);
    }
    if(mission&&mission.active&&mission.status==='active'){
      return _missionResolveStakeDollars({
        mission,regimeDirCal:regimeDirCalibration,lock:lockedCallRef.current,
        costCents,fallbackDollars:cap,
      });
    }
    return cap;
  },[mission,regimeDirCalibration]);

  const _runEntry=useCallback(async({manual=false}={})=>{
    const s=autoExecSettings||{};
    const blocked=(reason,extra={})=>{
      _autoExecLogPushBlockOnce({type:'blocked',guard:String(reason).split(':')[0],reason,
        asset:'BTC',windowId:computeWindowId(windowType),dryRun:s.dryRun!==false,
        manual:!!manual,...extra});
      return{ok:false,reason};
    };
    if(_entryBusyRef.current)return blocked('busy');
    if(!s.enabled)return blocked('disarmed');
    if(killSwitchEngaged)return blocked('kill-switch');
    // V13.4.349: THE REAL loss-streak cooldown gate. The V13.4.347 check
    //   lives on the `taraCall` object's post-processor chain -- but THIS
    //   function (the one that actually calls kalshiPlaceOrder) reads
    //   taraCallSnapshotRef directly a few lines below (see _signalSource),
    //   a separate commit pipeline `taraCall` never feeds into. Confirmed
    //   live: a real DOWN lock at 42c filled with the 347 gates already
    //   deployed, because they were gating an object nothing here reads.
    //   This is the one chokepoint that moves real money regardless of
    //   which upstream mechanism (taraCall, the snapshot/tier system, or
    //   the engine lock) decided the direction, so the gate belongs here,
    //   not upstream of it. Off-switch shared with the 347 gate:
    //   localStorage 'taraLossCooldownGate'='off'.
    try{
      if(localStorage.getItem('taraLossCooldownGate')!=='off'&&_lossCooldownRef.current.windowsRemaining>0){
        return blocked('loss-streak-cooldown:'+_lossCooldownRef.current.windowsRemaining+'-windows-left');
      }
    }catch(_){}
    // Risk caps are checked against the durable execution budget below.
    // Live entry still fails closed before any authenticated write.
    // Dry run never reaches the network — kalshiPlaceOrder and friends return
    // synthetic orders — so requiring credentials to simulate would block the
    // exact rehearsal this mode exists for. Live runs still demand them.
    const dryRun=(autoExecSettings||{}).dryRun!==false;
    const _signalSource=s.signalSource==='lock'?'lock':'snapshot';
    const _entryMode=checkEntryMode({dryRun,signalSource:_signalSource});
    // This gate is at the only entry chokepoint. Existing live positions may
    // still be exited and reconciled, but no new real orders can be sent while
    // account-wide loss/exposure limits and fill attribution remain unverified.
    if(!_entryMode.ok)return blocked(_entryMode.reason);
    const _entryWindow=computeWindowId(windowType);
    const _entryClose=windowCloseMs(_entryWindow);
    if(!Number.isFinite(_entryClose)||(_entryClose-Date.now())/1000<=callLockDeadline(windowType))return blocked('execution-cutoff');
    const creds=kalshiCreds||{};
    if(!dryRun&&(!creds.apiKeyId||!creds.privateKeyPem))return blocked('no-credentials');

    // V13.4.316: signal source, genuinely wired for the first time.
    //   'snapshot' (default) waits for Tara's call to actually commit to a
    //   tradeable side before firing -- routed through readLockState, the one
    //   place in this file that correctly separates "committed" from
    //   "tradeable" (see readLockState's own doc comment: three separate
    //   panels each hand-rolled this wrong before that helper existed).
    //   'lock' is the engine's own immediate internal lock -- fires earlier,
    //   can diverge from the settled call. The default here matches the
    //   ALREADY-DOCUMENTED default from the settings UI (V9.17.22: "snapshot"
    //   because it's the same signal the displayed win-rate is measured
    //   against; "lock" is legacy pre-V9.17.22 behavior) -- that setting has
    //   simply never been read by a real order before this version. A
    //   separate toggle existed in the old AUTO-EXEC card too, but only ever
    //   changed a display label, never reaching here either.
    let lock;
    if(_signalSource==='snapshot'){
      const _snapLock=readLockState(taraCallSnapshotRef.current);
      lock=_snapLock.tradeable?{
        dir:_snapLock.dir,
        lockedAt:(taraCallSnapshotRef.current&&taraCallSnapshotRef.current._committedAt)||Date.now(),
        windowId:computeWindowId(windowType),
      }:null;
    } else {
      lock=lockedCallRef.current;
    }
    const dir=lock&&lock.dir;
    if(dir!=='UP'&&dir!=='DOWN')return blocked('no-lock');

    // V13.4.316: capture BOTH signals at the moment of entry, regardless of
    //   which one is actually being traded on -- this is the data the disagreement
    //   question needed and never had: taraCallLog only ever persisted the settled
    //   call's direction, never the engine lock's, so no historical comparison was
    //   ever possible. Written onto the settlement ledger below so it survives
    //   into analysis later without touching the hot path.
    const _engineLockDirAtEntry=(lockedCallRef.current&&lockedCallRef.current.dir)||null;
    const _snapshotDirAtEntry=readLockState(taraCallSnapshotRef.current).dir;

    const lockKey=String(lock.lockedAt||0)+':'+dir;
    if(_entryFiredForRef.current===lockKey)return blocked('already-fired',{dir});

    const st=autoOrderState&&autoOrderState.status;
    // V13.4.351: reconciliation's terminal no-fill outcome is retryable for
    // this still-open lock, just like the original clean error state. It is a
    // confirmed absence of a position, not an order that remains in flight.
    if(st&&st!=='exited'&&st!=='error'&&st!=='no-fill')return blocked('order-in-flight:'+st,{dir});

    // Executable cost per contract on the side we are actually taking.
    const costCents=_execCostCents(dir,30000);
    if(costCents==null||!_quoteUsable(_kalshiQuote))return blocked('no-usable-quote',{dir});
    const _entryQuote=inspectDecisionQuote(_kalshiQuote,{
      windowId:computeWindowId(windowType),asset:currentAssetRef.current||'BTC',now:Date.now(),
      ticker:_signalSource==='snapshot'?taraCallSnapshotRef.current?.originalDecision?.marketTicker:undefined,
    });
    if(!_entryQuote.ok)return blocked('quote-integrity:'+_entryQuote.reason,{dir});
    // V13.4.349: THE REAL cost-band gate -- see the loss-cooldown comment
    //   above for why this needs to live here and not (only) on `taraCall`.
    //   costCents is already the live, direction-aware executable cost on
    //   the side actually being taken (_execCostCents' own job) -- gating
    //   HERE, at the moment of attempting the order, is more correct than
    //   the lock-time price anyway, since it's the exact number that
    //   decides what gets paid. Off-switch/override shared with the 347
    //   gate: localStorage 'taraCostBandGate'/'taraCostBandMin'/'taraCostBandMax'.
    try{
      if(localStorage.getItem('taraCostBandGate')!=='off'){
        const _rcbMin=(function(){try{const v=parseFloat(localStorage.getItem('taraCostBandMin'));return(Number.isFinite(v)&&v>=0&&v<100)?v:44;}catch(_){return 44;}})();
        const _rcbMax=(function(){try{const v=parseFloat(localStorage.getItem('taraCostBandMax'));return(Number.isFinite(v)&&v>0&&v<=100)?v:77;}catch(_){return 77;}})();
        if(costCents<_rcbMin||costCents>_rcbMax){
          return blocked('cost-band:'+costCents.toFixed(0)+'c-outside-'+_rcbMin.toFixed(0)+'-'+_rcbMax.toFixed(0),{dir});
        }
      }
    }catch(_){}
    const ticker=_kalshiQuote&&_kalshiQuote.ticker;
    if(!ticker)return blocked('no-ticker',{dir});
    // Freeze paper identity before any awaited entry work; a later rollover
    // must not attach the next contract's strike to this fill.
    const _paperOriginal=taraCallSnapshotRef.current;
    const _paperIdentity=dryRun&&_paperOriginal?.windowId===lock.windowId&&_paperOriginal.originalDecision?.marketTicker===ticker
      ?capturePaperMarketIdentity({ticker,windowId:lock.windowId,asset:'BTC',strike:_paperOriginal.originalDecision.strike,callId:_paperOriginal.id}):null;

    const stake=_resolveStakeDollars(s,costCents);
    if(!(stake>0))return blocked('no-stake',{dir,ticker});

    let intentId;
    try{intentId=await executionIntentId({account:dryRun?'simulation':creds.apiKeyId,asset:'BTC',windowId:_entryWindow});}
    catch{return blocked('execution-journal-unavailable',{dir,ticker});}
    // V14.7.16: a live entry reads the Kalshi account right here. The budget
    //   check refuses account risk older than 15s, and the audit used to refresh
    //   only from the panel button, so live entries could never pass it. The
    //   2s entry timer must not turn a refusal into a 7-endpoint read every
    //   tick, so ticks share one read per 15s (Kalshi's limit also covers exits).
    let _riskAudit=exchangeAudit.audit;
    if(!dryRun){
      const _read=_liveRiskReadRef.current;
      if(!_read.promise||Date.now()-_read.at>=15000){_read.at=Date.now();_read.promise=refreshExchangeAudit();}
      _riskAudit=await _read.promise;
    }
    if(!dryRun&&(_entryClose-Date.now())/1000<=callLockDeadline(windowType))return blocked('execution-cutoff',{dir,ticker});
    const reservation=await reserveExecutionIntent({storage:localStorage,id:intentId,windowId:_entryWindow,stake,dryRun,
      now:Date.now(),settings:{...s,cooldownUntil:autoExecCooldownUntil},
      withLock:typeof navigator!=='undefined'&&navigator.locks?(name,fn)=>navigator.locks.request('tara-execution:'+name,fn):null,
      accountRisk:_riskAudit?.risk?{..._riskAudit.risk,windowTrades:_riskAudit.risk.tradesByTicker?.[ticker]||0}:null});
    if(!reservation.ok){
      // A cap or cooldown refusal stands for the rest of this lock; retrying
      //   it every tick would only re-read the account.
      if(!dryRun&&['daily-trade-cap','window-trade-cap','daily-loss-and-exposure-cap','execution-cooldown','intent-already-recorded'].includes(reservation.reason))_entryFiredForRef.current=lockKey;
      return blocked(reservation.reason,{dir,ticker});
    }

    _entryFiredForRef.current=lockKey;
    _entryBusyRef.current=true;
    // V13.4.290 (ORD-4): the partial-fill explainer's own gate
    //   (`autoOrderState.requestedCount>0`) has been unreachable since it was written
    //   -- no write anywhere ever set this field, so the guard was always false and
    //   the '⚠ partial fill' warning could never render, on a genuine partial fill or
    //   otherwise. Stamped here, at the one place the intended size is actually known:
    //   how many whole contracts this stake buys at the quoted per-contract cost. The
    //   ladder may fill at slightly different rungs as it works the price, which is
    //   exactly the comparison "requested vs filled" needs to make.
    const requestedCount=affordableContracts(stake,costCents);
    // V13.4.291: placedAt, fairValueCents, attempts and ladderPath are all read
    //   downstream (the "waiting Ns" line, the 15s stale-limit estimate, and the
    //   trade log's entryFairValue / entrySlippageBps / entryAttempts /
    //   entryLadderPath) but NOTHING in the file ever wrote them -- the shape
    //   comment further down describes a contract no writer honoured. `placedAt`
    //   fell back to Date.now(), so the wait timer read 0s forever and the stale
    //   check never fired. Stamp them here, where the reference price is known and
    //   still fresh: _quoteUsable was asserted three lines up.
    setAutoOrderState({
      status:'placing',dir,ticker,dryRun,intentId,manual:!!manual,
      offerCents:costCents,requestedCount,
      // V13.4.291: the trade log gates its whole auto-exec entry-pricing block on
      //   `_aos.windowId===_capturedWindowId && _aos.asset===_resolveAsset`, and
      //   neither field was ever stamped -- so that gate was false on every trade
      //   regardless of the field-name bugs below it. The lock already carries a
      //   windowId (stamped so auto-exec can reject stale locks from prior
      //   windows); reuse exactly that one so the log matches the same identity
      //   the entry itself was authorised against.
      windowId:(lock&&lock.windowId)||null,
      asset:'BTC',
      placedAt:Date.now(),
      // Our-side mid at the moment we decided to enter. Slippage is measured
      //   against THIS, not against the fill, so it captures the whole cost of
      //   getting in rather than just the last rung.
      fairValueCents:_execFairCents(dir,30000),
      attempts:0,ladderPath:[],
      at:Date.now(),
    });

    try{
      const res=await kalshiRunEntryLadder({
        apiKeyId:creds.apiKeyId,privateKeyPem:creds.privateKeyPem,
        ticker,dir,betDollars:stake,offerCents:costCents,
        settings:s,dryRun,clientOrderId:intentId,
        shouldAbort:()=>{
          const controls=_executionControlRef.current;
          if(!controls?.enabled||controls.kill||controls.windowId!==_entryWindow||(_entryClose-Date.now())/1000<=callLockDeadline(windowType))return true;
          if(!inspectDecisionQuote(_kalshiQuote,{windowId:_entryWindow,ticker,asset:'BTC',now:Date.now()}).ok)return true;
          // V13.4.316: abort against whichever signal this entry is actually
          //   trading on -- aborting on the engine lock changing while trading
          //   off Tara's settled call (or vice versa) would cancel a perfectly
          //   good ladder over a signal this order was never using.
          if(_signalSource==='snapshot'){
            const _l=readLockState(taraCallSnapshotRef.current);
            return !_l.tradeable||_l.dir!==dir;
          }
          const l=lockedCallRef.current;
          return !l||l.dir!==dir;
        },
        onRung:(rung)=>setAutoOrderState(prev=>Object.assign({},prev||{},{
          // V13.4.345: dir/ticker/dryRun restated from THIS closure's own
          //   scope, not left to `prev` to carry forward -- see the fill/
          //   error writes below for why.
          dir,ticker,dryRun,
          status:rung.takesSpread?'placing':'resting',
          rungCents:rung.priceCents,takesSpread:!!rung.takesSpread,step:rung.step,
          // V13.4.291: the ladder's actual walk. entryLadderPath and entryAttempts
          //   were read from fields no writer set, so every logged trade claimed
          //   exactly 1 attempt and a null path however far the ladder had to work
          //   the price. Capped at 10 -- the log slices to 10 anyway.
          attempts:(Number(prev&&prev.attempts)||0)+1,
          ladderPath:[...(Array.isArray(prev&&prev.ladderPath)?prev.ladderPath:[]),
            {step:rung.step,cents:rung.priceCents,tookSpread:!!rung.takesSpread}].slice(-10),
        })),
        onPlaced:({orderId,clientOrderId,rung})=>{updateExecutionIntent(localStorage,intentId,{status:'placed',orderId});_autoExecLogPush({type:'placed',asset:'BTC',
          windowId:lock.windowId,dir,ticker,dryRun,manual:!!manual,
          orderId:orderId||null,clientOrderId:clientOrderId||null,
          limitCents:rung.priceCents,requestedCount});},
      });
      if(!res.ok){
        // V13.4.322: kalshiRunEntryLadder now returns a distinct reason when
        //   it genuinely cannot tell whether the order filled (every status
        //   check failed, the cancel also failed) instead of folding that
        //   into the same 'no-fill' every real non-fill also produces.
        //   Surface it as its own status so the UI shows something that says
        //   "go check Kalshi directly," not a normal-looking error banner
        //   that reads as "nothing happened, 0 contracts."
        const _statusUnknown=res.reason==='unknown-verify-kalshi-directly';
        _autoExecLogPush({type:_statusUnknown?'unknown':'no-fill',asset:'BTC',
          windowId:lock.windowId,dir,ticker,dryRun,manual:!!manual,
          orderId:res.orderId||null,reason:res.reason});
        // V13.4.346: THE ACTUAL "auto-exec doesn't place orders" bug. This
        //   branch already sets status:'error' specifically so the
        //   order-in-flight gate above (`st!=='error'`) lets a FUTURE call
        //   retry -- but _entryFiredForRef.current was set unconditionally
        //   at the top of this attempt and NEVER reset anywhere in the file
        //   (confirmed by grep: exactly 2 references, the check and this one
        //   set). The already-fired check runs BEFORE the order-in-flight
        //   check and has no concept of success vs failure, so it permanently
        //   blocked every later tick of the 2s auto-exec timer for this exact
        //   lock regardless of what happened here -- a confirmed clean
        //   no-fill (price ran away from the ladder, exactly the "KALSHI
        //   ERROR ... no-fill" case reported live) got exactly ONE attempt
        //   per window, ever, then silent permanent giving-up for the
        //   remaining ~15 minutes even if price came back to a fillable
        //   level seconds later. This is almost certainly why most committed
        //   calls never produced a real order at all: not a fill-tracking
        //   loss, an attempt that failed once and was never allowed to try
        //   again. Reset ONLY on a CONFIRMED no-fill (_statusUnknown false)
        //   -- deliberately NOT on 'unknown', where the original order might
        //   have actually filled and a retry would risk a real double-fill.
        //   This mirrors the order-in-flight gate's own existing distinction
        //   exactly; it was just never applied here too.
        // The intent survives errors/reloads. Never send a new POST to find
        // out whether a previous POST filled; the exchange read is authority.
        updateExecutionIntent(localStorage,intentId,{status:_statusUnknown?'unknown':'no-fill',reason:res.reason,orderId:res.orderId||null});
        setAutoOrderState(prev=>Object.assign({},prev||{},{
          dir,ticker,dryRun,windowId:(lock&&lock.windowId)||null,asset:'BTC',
          status:_statusUnknown?'unknown':'error',
          reason:res.reason,orderId:res.orderId||null,at:Date.now(),
          // V13.4.346: durable attempt record. Until now a failed attempt
          //   left NO trace in taraCallLog -- only a completed round trip
          //   (status:'exited', which stamps autoExec below) was ever
          //   visible after the fact, so "never attempted" and "attempted,
          //   no-fill" were indistinguishable from the log alone (the exact
          //   ambiguity flagged live tonight). attemptCount/lastAttemptReason
          //   persist across retries via the prev spread above.
          attemptCount:(Number(prev&&prev.attemptCount)||0)+1,
          lastAttemptReason:res.reason,
        }));
        // V13.4.346: mirror the success-path ledger write (below) so a
        //   FAILED attempt is ALSO durably visible on the settled taraCallLog
        //   entry, not just in the ephemeral live autoOrderState above --
        //   otherwise this fix's own effect (retries actually happening now)
        //   would be invisible in the exported log the same way the original
        //   bug was. filled:false is what the settlement-merge reader below
        //   checks to distinguish this from a real fill.
        if(lock&&lock.windowId){
          const _prevAttempt=_execSettlementLedgerRef.current['BTC|'+lock.windowId]||{};
          _execSettlementLedgerRef.current['BTC|'+lock.windowId]={
            ..._prevAttempt,
            filled:false,dir,dryRun,
            attempts:(Number(_prevAttempt.attempts)||0)+1,
            lastReason:res.reason,lastAttemptAt:Date.now(),
          };
        }
        return res;
      }
      const filledAt=res.normalized?.fillPriceCents??null;
      const _execFilledCount=(res.normalized&&res.normalized.filledCount)||Number(res.order&&res.order.count)||0;
      updateExecutionIntent(localStorage,intentId,{status:'filled',filled:true,dir,ticker,orderId:res.order?.order_id,filledCount:_execFilledCount,filledAtCents:filledAt,filledFeesCents:res.normalized?.feeCents??null,
        ...(dryRun?{paperMarketIdentity:_paperIdentity}:{})});
      _autoExecLogPush({type:'filled',asset:'BTC',windowId:lock.windowId,dir,ticker,
        dryRun,manual:!!manual,orderId:res.order?.order_id||null,
        clientOrderId:res.order?.client_order_id||null,
        filledCount:_execFilledCount,filledAtCents:filledAt});
      // V13.4.345: dir/ticker restated explicitly here rather than left to
      //   `prev` to carry forward. Flagged as a real, unconfirmed risk back
      //   in v13.4.331 and never actually fixed: the window-rollover cleanup
      //   effect calls setAutoOrderState(null) unconditionally on every
      //   window change (~line 42217), with no check for an in-flight entry.
      //   If that fires between the placement-time write (which DOES set
      //   dir/ticker explicitly, a few lines up) and this fill callback,
      //   `prev` is null here and the merge would have produced a
      //   status:'filled' record silently missing both fields -- exactly
      //   the shape _computeDrift's own guard (`&&_aos.ticker`) skips over
      //   entirely, hiding a real fill from reconciliation with zero
      //   warning. dir/ticker/dryRun are already sitting in this exact
      //   closure (used two lines above for kalshiRunEntryLadder's own
      //   call) -- restating them costs nothing and makes every write in
      //   this function self-sufficient regardless of what `prev` holds.
      setAutoOrderState(prev=>Object.assign({},prev||{},{
        dir,ticker,status:'filled',dryRun,intentId,windowId:(lock&&lock.windowId)||null,asset:'BTC',order:res.order,filledFeesCents:res.normalized?.feeCents??null,
        filledCount:_execFilledCount,
        filledAtCents:filledAt,
        tookSpread:!!(res.rung&&res.rung.takesSpread),
        // Realised saving versus crossing — the number V13.4.228 says decides
        // whether arming this was worth doing at all.
        savedVsTakeCents:filledAt==null?null:costCents-filledAt,
        // V13.4.291: raw YES mid at the moment of the fill. Stored raw (not
        //   our-side) to match kalshiAtLock, the field it is meant to be compared
        //   against; fairValueCents above is the our-side figure that pairs with
        //   filledAtCents. Keeping the two scales explicit is the whole lesson of
        //   the V13.4.290 P&L inversion.
        kalshiAtFill:(Number.isFinite(Number(_kalshiQuote&&_kalshiQuote.mid))&&_kalshiQuote.at
          &&(Date.now()-_kalshiQuote.at)<=30000)?Number(_kalshiQuote.mid):null,
        at:Date.now(),
      }));
      // V13.4.309: bankroll-settlement ledger entry -- real fill data, keyed
      //   by this window's id so it survives past this position even after
      //   autoOrderState is overwritten by the next window.
      if(lock&&lock.windowId){
        const _priorAttempts=Number((_execSettlementLedgerRef.current['BTC|'+lock.windowId]||{}).attempts)||0;
        _execSettlementLedgerRef.current['BTC|'+lock.windowId]={
          filled:true, // V13.4.346: explicit, so the reader below can tell this
                       //   apart from a failed-attempt-only record (filled:false)
          attempts:_priorAttempts+1, // carries forward any earlier no-fill retries
          dir,filledCount:_execFilledCount,filledAtCents:filledAt,filledFeesCents:res.normalized?.feeCents??null,
          stakeDollars:filledAt==null?null:(filledAt*_execFilledCount)/100,
          dryRun,enteredAt:Date.now(),
          // V13.4.316: which signal this entry actually traded on, plus what
          //   BOTH signals said at the moment of entry -- so a future pass can
          //   finally answer "how often do they disagree, and who wins when
          //   they do" instead of guessing. Ledger is in-memory only (survives
          //   a page reload no better than anything else here), but it's the
          //   first version of this file that has ever recorded it at all.
          signalSource:_signalSource,
          engineLockDirAtEntry:_engineLockDirAtEntry,
          snapshotDirAtEntry:_snapshotDirAtEntry,
        };
      }
      return res;
    }catch(e){
      const msg=String((e&&e.message)||e);
      _autoExecLogPush({type:'error',asset:'BTC',windowId:lock?.windowId||null,
        dir,ticker,dryRun,manual:!!manual,reason:msg});
      setAutoOrderState(prev=>Object.assign({},prev||{}, {
        dir,ticker,dryRun,windowId:(lock&&lock.windowId)||null,asset:'BTC',
        status:'error',reason:msg,at:Date.now(),
      }));
      return{ok:false,reason:msg};
    }finally{
      _entryBusyRef.current=false;
    }
  },[autoExecSettings,autoExecCooldownUntil,exchangeAudit,refreshExchangeAudit,killSwitchEngaged,kalshiCreds,autoOrderState,lockedCallRef,taraCallSnapshotRef,windowType,_resolveStakeDollars]);

  // V9.17.18: MANUAL PLACE-ORDER on Tara's call. Tara's snapshot paths commit
  //   without writing lockedCallRef, so the automatic path can sit out; this
  //   lets the user fire on Tara's call explicitly. Every hard rail in
  //   _runEntry still applies, dryRun included.
  const _handlePlaceOrderOnTaraCall=useCallback(()=>{
    _runEntry({manual:true}).then(r=>{
      try{console.info('[entry] manual:',(r&&r.ok)?'filled':'skipped ('+(r&&r.reason)+')');}catch(_e){}
    });
  },[_runEntry]);

  useEffect(()=>{
    const s=autoExecSettings||{};
    if(!s.enabled)return;            // disarmed: no timer, nothing runs
    if(killSwitchEngaged)return;
    let stopped=false;
    const tick=()=>{
      if(stopped)return;
      _runEntry({}).then(r=>{
        if(stopped||!r||r.ok)return;
        const quiet=r.reason==='already-fired'||r.reason==='no-lock'||r.reason==='busy'||r.reason==='disarmed';
        if(!quiet){try{console.info('[entry] auto skipped:',r.reason);}catch(_e){}}
      });
    };
    const id=setInterval(tick,2000);
    return()=>{stopped=true;clearInterval(id);};
  },[autoExecSettings,killSwitchEngaged,_runEntry]);

  // ── EXIT MANAGEMENT ───────────────────────────────────────────────────────
  // An armed entry that nothing closes is worse than no automation at all, so
  // these run whenever a position is open. Three paths race and the first to
  // fire wins, which is how the settings panel has always described it:
  //
  //   take-profit  exit value >= autoExitOffer
  //   time         seconds left <= timeExitSecLeft
  //   stop-loss    exit value <= fill - stopLossDeltaCents   (0 disables)
  //
  // Prices come from _exitValueCents, the mirror of the entry helper: getting
  // out crosses the other side of the book, so a long YES is marked at the bid
  // and a long NO at 100 - ask. A stale quote returns null and the position is
  // held rather than closed on a price we cannot trust.
  //
  // Exits are NOT laddered. The ladder exists to shave a cent on entry, where
  // not filling just means no trade; on a stop or a closing bell, not filling
  // means carrying the position, so these cross deliberately.
  //
  // KILL SWITCH: it blocks entries but deliberately does NOT block exits. A
  // stop that stops working the moment you hit the big red button is a trap,
  // and the greater risk is an open position nobody is managing. Flip the
  // shouldHold check below if you would rather it froze everything.
  const _exitBusyRef=useRef(false);
  const _exitFiredForRef=useRef(null);

  // V13.4.253: per-fill high-water mark for the trailing stop. Keyed on
  //   ticker+fill time so a fresh position resets rather than inheriting.
  const _exitTrailRef=useRef({key:null,peak:-1,armed:false});
  // V13.4.329: per-ticker consecutive-empty-poll counter for the position
  //   reconciliation drift check. A single 30s snapshot showing "no matching
  //   position, no same-side position either" is not proof of absence --
  //   Kalshi's /portfolio/positions can lag a few seconds behind a fill that
  //   just posted, and a real fill caught mid-propagation on one snapshot
  //   used to be declared "CONFIRMED NO FILL -- safe to ignore" outright, on
  //   the same single-poll basis v328 already fixed for the ticker-mismatch
  //   case. Reported live: a real 1-contract fill, side matching Tara's own
  //   locked direction, correctly absent from `positions` on one poll and
  //   present moments later on Kalshi's own site -- the ticker/side logic
  //   from v328 was correct and ran as designed, the missing piece was time.
  const _driftMissTrackerRef=useRef({});
  // V13.4.343: holds the reconciliation poll's own `_tick` function so the
  //   drift banner's manual "retry" button can trigger the SAME check the
  //   30s timer runs, on demand -- not a second, divergent implementation.
  //   Assigned where `_tick` is defined below; read only from the button.
  const _manualReconcileNowRef=useRef(null);
  // V13.4.344: optional overrideState lets a caller that has ALREADY
  //   confirmed a real position directly against Kalshi (e.g. the drift
  //   banner's "unknown-resolved-filled"/"unknown-to-tara" cases, where
  //   autoOrderState never reached status:'filled' because the fill was
  //   never tracked in the first place) supply {status:'filled',dir,
  //   ticker,filledCount,dryRun:false} and have this function close the
  //   REAL confirmed position exactly as if it had been tracked normally.
  //   Deliberately does NOT need filledAtCents -- placing/verifying a
  //   closing order never reads it; only the post-exit P&L line below
  //   does, and it already tolerates that being absent (stays null rather
  //   than fabricating a number). The automated stop-loss/take-profit/
  //   trailing-stop monitor is untouched -- it only ever calls this with
  //   no override, reading the real (still-honestly-unknown) autoOrderState.
  const _runExit=useCallback(async(why,overrideState)=>{
    if(_exitBusyRef.current)return{ok:false,reason:'busy'};
    const st=overrideState||autoOrderState;
    if(!st||st.status!=='filled')return{ok:false,reason:'no-position'};
    const dir=st.dir;
    if(dir!=='UP'&&dir!=='DOWN')return{ok:false,reason:'no-dir'};
    const dryRun=st.dryRun!==false;
    const creds=kalshiCreds||{};
    if(!dryRun&&(!creds.apiKeyId||!creds.privateKeyPem))return{ok:false,reason:'no-credentials'};
    const count=Number(st.filledCount);
    if(!Number.isInteger(count))return{ok:false,reason:'invalid-position-count'};
    if(!(count>0))return{ok:false,reason:'no-count'};
    const limitCents=_exitYesLimitCents(dir,30000,st.ticker);
    if(limitCents==null)return{ok:false,reason:'no-usable-quote'};

    const key=String(st.at||0);
    if(_exitFiredForRef.current===key)return{ok:false,reason:'already-exiting'};
    _exitFiredForRef.current=key;
    _exitBusyRef.current=true;

     setAutoOrderState(prev=>Object.assign({},prev||{}, {
       dir,ticker:st.ticker,dryRun,windowId:st.windowId||null,asset:st.asset||'BTC',
       filledCount:st.filledCount,filledAtCents:st.filledAtCents,
       status:'exiting',exitReason:why,at:Date.now(),
     }));
    try{
      const res=await kalshiExitPosition({
        apiKeyId:creds.apiKeyId,privateKeyPem:creds.privateKeyPem,
        ticker:st.ticker,side:dir==='UP'?'yes':'no',
        count,limitCents,dryRun,positionId:st.intentId||st.order?.order_id||st.orderId,
      });
      if(!res.ok){
        // Leave the ref set so a failed exit does not retry in a tight loop;
        // it is surfaced instead, because silently hammering a broken exit is
        // how a bad position gets worse.
         setAutoOrderState(prev=>Object.assign({},prev||{}, {
           dir,ticker:st.ticker,dryRun,windowId:st.windowId||null,asset:st.asset||'BTC',
           filledCount:st.filledCount,filledAtCents:st.filledAtCents,
           status:'error',reason:'exit failed: '+res.reason,exitReason:why,at:Date.now(),
         }));
        _autoExecLogPush({type:'unknown',asset:st.asset||'BTC',windowId:st.windowId||null,
          dir,ticker:st.ticker,dryRun,reason:'exit failed: '+res.reason});
        return res;
      }
      // V13.4.322: VERIFY THE EXIT ACTUALLY FILLED. This crosses the spread
      //   deliberately (see "Exits are NOT laddered" above), so it should
      //   resolve almost immediately -- but a successful POST only means
      //   Kalshi ACCEPTED the order, not that it matched yet
      //   (kalshiNormalizeOrder's own doc comment: current Kalshi statuses
      //   are 'resting'|'canceled'|'executed', and a POST response can still
      //   say 'resting' for an instant even on a crossing order). Before this
      //   version, res.ok alone was treated as "the position is closed" --
      //   the mirror image of the entry-side bug fixed the same version: an
      //   exit that hadn't actually filled yet could get marked 'exited' and
      //   stop being tracked while still open for real on Kalshi. Brief poll
      //   only (this is time-sensitive risk management, not an entry ladder).
      let _exitOrderObj=res.order;
      let _exitNorm=kalshiNormalizeOrder(_exitOrderObj);
      const _exitOrderId=_exitOrderObj?.order_id;
      for(let _i=0;_i<3&&(_exitNorm.status!=='filled'||!_exitNorm.countKnown||_exitNorm.filledCount!==count);_i++){
        await new Promise(r=>setTimeout(r,1000));
        const _look=await kalshiGetOrder({apiKeyId:creds.apiKeyId,privateKeyPem:creds.privateKeyPem,orderId:_exitOrderId,dryRun});
        if(_look.ok){_exitOrderObj=_look.order;_exitNorm=kalshiNormalizeOrder(_look.order);}
      }
      if(_exitNorm.status!=='filled'||!_exitNorm.countKnown||_exitNorm.filledCount!==count){
        // Could not confirm the exit actually closed the position. Leave the
        //   resting exit order alone -- it is still the one thing trying to
        //   get this position closed; canceling it here would remove that
        //   with nothing left in its place. Surface as unknown rather than
        //   silently reporting a clean 'exited' that may not be true.
         setAutoOrderState(prev=>Object.assign({},prev||{},{
           dir,ticker:st.ticker,dryRun,windowId:st.windowId||null,asset:st.asset||'BTC',
           filledCount:st.filledCount,filledAtCents:st.filledAtCents,
           status:'unknown',reason:'exit-unconfirmed-verify-kalshi-directly',
           exitReason:why,orderId:_exitOrderId||null,at:Date.now(),
         }));
        _autoExecLogPush({type:'unknown',asset:st.asset||'BTC',windowId:st.windowId||null,
          dir,ticker:st.ticker,dryRun,orderId:_exitOrderId||null,
          reason:'exit-unconfirmed-verify-kalshi-directly'});
        return{ok:false,reason:'exit-unconfirmed-verify-kalshi-directly',order:_exitOrderObj,orderId:_exitOrderId};
      }
      res.order=_exitOrderObj;
      let _verifiedExit=dryRun?_exitNorm:null;
      if(!dryRun)try{
        const fills=await fetchAllExecutionPages(path=>kalshiAuthedFetch({apiKeyId:creds.apiKeyId,privateKeyPem:creds.privateKeyPem,method:'GET',path}),'/portfolio/fills?order_id='+encodeURIComponent(_exitOrderId),'fills');
        if(fills.ok)_verifiedExit=summarizeOrderFills(fills.items,{ticker:st.ticker,orderId:_exitOrderId,side:dir==='UP'?'yes':'no',action:'sell',count});
      }catch{/* Confirmed closure survives a cost-history outage; P&L stays unknown. */}
      const exitVal=_verifiedExit?.fillPriceCents??null;
      // Realised move per contract against the entry fill, so a session can
      // be judged on what it actually got rather than on the signal. Uses
      // st.filledAtCents (captured at the top of this function) rather than
      // prev.filledAtCents -- same value, since nothing between them touches
      // it, but hoisted so the settlement ledger below can reuse it too.
      // V13.4.344: explicit st.filledAtCents!=null check added. Number(null)
      //   is 0, not NaN -- Number.isFinite(Number(null)) is TRUE, so without
      //   this an unknown entry price (the drift-banner override's own
      //   filledAtCents:null, deliberately unguessed) would have silently
      //   priced the entry at $0.00 instead of reporting P&L as unknown,
      //   fabricating a wildly wrong profit number. Caught by this session's
      //   own verification test before it ever shipped.
      const _execPnlPerContract=(exitVal!=null&&st.filledAtCents!=null&&Number.isFinite(Number(st.filledAtCents))&&st.filledFeesCents!=null&&_verifiedExit?.feeCents!=null)
        ?(exitVal-Number(st.filledAtCents)-(st.filledFeesCents+_verifiedExit.feeCents)/count):null;
      if(st.intentId)try{updateExecutionIntent(localStorage,st.intentId,{status:'exited',exitedAt:Date.now(),exitAtCents:exitVal,pnlCentsPerContract:_execPnlPerContract,realizedPnLDollars:_execPnlPerContract==null?null:_execPnlPerContract*count/100});}catch{}
       setAutoOrderState(prev=>Object.assign({},prev||{},{
         dir,ticker:st.ticker,dryRun,windowId:st.windowId||null,asset:st.asset||'BTC',
         filledCount:st.filledCount,filledAtCents:st.filledAtCents,
         status:'exited',exitReason:why,exitOrder:res.order,
         exitAtCents:exitVal,
        pnlCentsPerContract:_execPnlPerContract,
        at:Date.now(),
      }));
      _autoExecLogPush({type:'exited',asset:st.asset||'BTC',windowId:st.windowId||null,
        dir,ticker:st.ticker,dryRun,orderId:_exitOrderId||null,
        exitReason:why,filledCount:st.filledCount,pnlCentsPerContract:_execPnlPerContract});
      // V13.4.309: merge the real exit into this window's ledger entry
      //   (written at fill time, above) so the settlement effect can price
      //   an early exit (trailing stop / take-profit / cut-loss / time exit)
      //   from real data instead of falling back to hold-to-settlement math.
      if(st.windowId){
        const _ledgerKey=(st.asset||'BTC')+'|'+st.windowId;
        const _prevLedger=_execSettlementLedgerRef.current[_ledgerKey]||{};
        _execSettlementLedgerRef.current[_ledgerKey]={
          ..._prevLedger,
          exitedAt:Date.now(),exitReason:why,exitAtCents:exitVal,
          pnlCentsPerContract:_execPnlPerContract,
          realizedPnLDollars:(_execPnlPerContract!=null&&Number.isFinite(Number(st.filledCount)))
            ?Number(((_execPnlPerContract*Number(st.filledCount))/100).toFixed(2))
            :null,
        };
      }
      return res;
    }catch(e){
      const msg=String((e&&e.message)||e);
      _autoExecLogPush({type:'unknown',asset:st.asset||'BTC',windowId:st.windowId||null,
        dir,ticker:st.ticker,dryRun,reason:'exit threw: '+msg});
       setAutoOrderState(prev=>Object.assign({},prev||{}, {
         dir,ticker:st.ticker,dryRun,windowId:st.windowId||null,asset:st.asset||'BTC',
         filledCount:st.filledCount,filledAtCents:st.filledAtCents,
         status:'error',reason:'exit threw: '+msg,at:Date.now(),
       }));
      return{ok:false,reason:msg};
    }finally{
      _exitBusyRef.current=false;
    }
  },[autoOrderState,kalshiCreds]);

  // V13.4.309: MISSION BANKROLL SETTLEMENT. Order sizing is untouched by this
  //   effect -- it only ever READS taraCallLog and the settlement ledger
  //   above, and writes mission.currentBankroll/tradesWon/etc. A bug here can
  //   misreport a dashboard number; it cannot mis-size a real order. That is
  //   deliberate staging (see project_tara_autoexec_simplify memory) -- sizing
  //   is wired to read this in a later version, once this has been watched.
  //
  //   All the actual bankroll math runs inside the setMission functional
  //   updater against `prev`, never against the outer `mission` closure --
  //   React guarantees `prev` is the latest state even if this effect fires
  //   more than once before a re-render, so settledWindowIds can never be
  //   read stale and double-count the same window twice.
  useEffect(()=>{
    if(!mission.active||mission.status!=='active')return;
    const log=Array.isArray(taraCallLog)?taraCallLog:[];
    if(!log.length)return;

    setMission(prev=>{
      // V13.4.309 FIX (caught live, before this ever shipped): filtering only
      //   on settledWindowIds meant a mission started today would treat its
      //   ENTIRE pre-existing taraCallLog history as "newly resolved" on its
      //   very first render -- confirmed live, a fresh mission against a
      //   470-trade history instantly stamped tradesAttempted:470,
      //   bankrollDataGaps:470. `id` is a Date.now() timestamp (stamped at
      //   lock time), so gating on prev.startDate keeps a mission scoped to
      //   trades that happened during IT, not the app's whole lifetime.
      const _startMs=prev.startDate?new Date(prev.startDate).getTime():0;
      const _already=new Set(prev.settledWindowIds||[]);
      // Directional only -- SIT_OUT and any other non-UP/DOWN call never had
      //   a real position, so it can't move the bankroll either way.
      const _fresh=log.filter(e=>e&&(e.result==='WIN'||e.result==='LOSS')&&(e.dir==='UP'||e.dir==='DOWN')
        &&Number(e.id)>=_startMs&&!_already.has(e.id));
      if(!_fresh.length)return prev; // nothing new -- bail without a state change

      let _bankroll=prev.currentBankroll;
      let _won=prev.tradesWon,_lost=prev.tradesLost,_attempted=prev.tradesAttempted;
      let _gaps=prev.bankrollDataGaps||0;
      const _newIds=[...(prev.settledWindowIds||[])];
      const _historyAdds=[];

      for(const e of _fresh){
        _newIds.push(e.id);
        _attempted++;
        if(e.result==='WIN')_won++;else _lost++;

        const _ledger=_execSettlementLedgerRef.current[(e.asset||'BTC')+'|'+e.windowId];
        let _delta=null;
        if(_ledger&&_ledger.exitedAt!=null&&_ledger.realizedPnLDollars!=null){
          // Closed early (trailing stop / take-profit / cut-loss / time
          //   exit) -- the real exit fill, not settlement math.
          _delta=_ledger.realizedPnLDollars;
        }else if(_ledger&&_ledger.exitedAt==null&&_ledger.stakeDollars!=null&&_ledger.filledCount!=null&&_ledger.filledFeesCents!=null){
          // Held to natural settlement -- standard win/lose-max-payout math,
          //   on the REAL stake/count this window actually filled at (never
          //   the settings-based betAmt/maxPay fields, which are dead --
          //   always 0, see project_tara_autoexec_simplify memory).
          // V13.4.316: e.result reflects whether TARA'S SETTLED CALL was right,
          //   but the real position was entered on the ENGINE LOCK (_ledger.dir),
          //   which can differ from the settled call on the same window. Using
          //   e.result here silently computed the wrong sign on exactly those
          //   disagreement windows. Compare the real position's own direction
          //   against the real market outcome (e.outcomeDir) directly instead --
          //   this can never disagree with what was actually bought. Falls back
          //   to e.result only when outcomeDir wasn't recorded at all.
          const _maxPayout=Number(_ledger.filledCount)*1.0;
          const _ledgerWon=(_ledger.dir==='UP'||_ledger.dir==='DOWN')&&(e.outcomeDir==='UP'||e.outcomeDir==='DOWN')
            ?(_ledger.dir===e.outcomeDir)
            :(e.result==='WIN');
          _delta=(_ledgerWon?(_maxPayout-_ledger.stakeDollars):-_ledger.stakeDollars)-_ledger.filledFeesCents/100;
        }else{
          // No ledger entry -- manual trade, a trade from before this
          //   version, or a reload between fill and settlement (the ledger
          //   is a plain ref, not persisted). Count it, skip the bankroll
          //   delta, disclose the gap rather than guess.
          _gaps++;
        }
        if(_delta!=null){
          _bankroll=Math.max(0,_bankroll+_delta);
          _historyAdds.push({t:Date.now(),b:_bankroll});
        }
      }

      const _status=(prev.target>0&&_bankroll>=prev.target)?'hit'
        :(_bankroll<=prev.floor)?'busted'
        :prev.status;

      return{
        ...prev,
        currentBankroll:_bankroll,
        tradesAttempted:_attempted,
        tradesWon:_won,
        tradesLost:_lost,
        bankrollDataGaps:_gaps,
        settledWindowIds:_newIds.slice(-500),
        bankrollHistory:[...(prev.bankrollHistory||[]),..._historyAdds].slice(-200),
        lastBankrollUpdate:Date.now(),
        status:_status,
      };
    });
  },[taraCallLog,mission.active,mission.status]);

  useEffect(()=>{
    const st=autoOrderState;
    if(!st||st.status!=='filled')return;      // only while actually holding
    const s=autoExecSettings||{};
    const dir=st.dir;
    if(dir!=='UP'&&dir!=='DOWN')return;
    let stopped=false;

    const check=()=>{
      if(stopped||_exitBusyRef.current)return;
      const value=_exitValueCents(dir,30000,st.ticker);
      if(value==null)return;                  // stale quote: hold, do not guess

      const secsLeft=(Number(timeState&&timeState.minsRemaining)||0)*60
                    +(Number(timeState&&timeState.secsRemaining)||0);
      const fill=Number(st.filledAtCents);
      const slDelta=Math.max(0,Number(s.stopLossDeltaCents)||0);
      const tp=Number(s.autoExitOffer)>0?Number(s.autoExitOffer):null;
      // V13.4.292: was `s.autoExitSecLeft`, a key nothing in the file ever wrote --
      //   every UI control, preset, and mode button write `timeExitSecLeft`
      //   instead. `autoExitSecLeft` therefore always evaluated to its own
      //   hardcoded fallback, permanently forcing a 20s time exit on every
      //   position regardless of what the "Time exit" control showed (0 = "no
      //   time exit," the believed and actual default for everyone). Now reads
      //   the key every surface actually writes, so 0 genuinely means off.
      const tExit=Number(s.timeExitSecLeft)>0?Number(s.timeExitSecLeft):null;

      // V13.4.253: TRAILING stop, the only exit rule that measured positive.
      //   Arm once the position is worth TRAIL_ARM_C, then exit if it gives
      //   back TRAIL_GIVEBACK_C from its own high. Peak is tracked per fill so
      //   a new position never inherits the last one_s high.
      const _tKey=String(st.ticker||"")+":"+String(st.filledAt||st.at||0);
      const _tr=_exitTrailRef.current;
      if(_tr.key!==_tKey){_exitTrailRef.current={key:_tKey,peak:value,armed:false};}
      const _t=_exitTrailRef.current;
      if(value>_t.peak)_t.peak=value;
      if(value>=TRAIL_ARM_C)_t.armed=true;

      let why=null;
      if(_t.armed&&(_t.peak-value)>=TRAIL_GIVEBACK_C){
        why='trailing stop '+value+'c, '+(_t.peak-value)+'c off its '+_t.peak+'c high';
      }else if(slDelta>0&&Number.isFinite(fill)&&value<=(fill-slDelta)){
        why='stop-loss '+value+'c <= '+(fill-slDelta)+'c';
      }else if(tp!=null&&value>=tp){
        why='take-profit '+value+'c >= '+tp+'c';
      }else if(tExit!=null&&secsLeft>0&&secsLeft<=tExit){
        why='time '+Math.round(secsLeft)+'s left <= '+tExit+'s';
      }
      if(!why)return;
      _runExit(why).then(r=>{
        try{console.info('[exit]',why,'->',(r&&r.ok)?'closed':'failed ('+(r&&r.reason)+')');}catch(_e){}
      });
    };

    const id=setInterval(check,2000);
    check();
    return()=>{stopped=true;clearInterval(id);};
  },[autoOrderState,autoExecSettings,timeState,_runExit]);

  // ── V10.2.10 — POSITION RECONCILIATION POLL ────────────────────────────────
  // Polls /portfolio/positions every 30s and detects three drift categories
  // between Tara's internal state and Kalshi's actual book. See state comment
  // at the positionReconciliation declaration for the categories. Polling only
  // runs when there's reason to expect a position (autoOrderState set OR
  // manualKalshiEntry set OR userPosition set OR auto-exec enabled). Outside
  // those cases it's idle.
  //
  // The drift detector lives outside React state so it can be invoked
  // independently from the console hook. It reads through refs to current
  // Tara state — the poll's interval doesn't need to re-mount on every render.
  useEffect(()=>{
    _kalshiActiveMarketRef.current=kalshiActiveMarket;
  },[kalshiActiveMarket]);
  useEffect(()=>{
    const _hasCreds=!!(kalshiCreds.apiKeyId&&kalshiCreds.privateKeyPem);
    if(!_hasCreds)return;
    // Only poll when there's a reason — saves Kalshi rate quota and avoids
    //   alarming on inactive users.
    const _shouldPoll=()=>{
      if(autoExecSettings?.enabled)return true;
      if(autoOrderStateRef.current)return true;
      if(_manualKalshiEntryRef.current)return true;
      if(_userPositionRef.current)return true;
      // V13.4.355: reload recovery. A live Kalshi position can outlive Tara's
      // in-memory autoOrderState/userPosition (or AutoTrade can be disarmed
      // after an entry). Once the active ticker is known, keep polling so an
      // exchange-only position is surfaced instead of becoming invisible until
      // the user manually recreates a local marker. This remains one positions
      // read per 30s and does not submit or retry anything.
      if(_kalshiActiveMarketRef.current?.ticker)return true;
      return false;
    };
    let _stopped=false;
    let _inflight=false;
    const _computeDrift=(positions)=>{
      const _details=[];
      const _aos=autoOrderStateRef.current;
      const _mke=_manualKalshiEntryRef.current;
      const _up=_userPositionRef.current;
      const _km=_kalshiActiveMarketRef.current;
      const _activeTicker=_km?.ticker||null;
      // V13.4.329: consecutive-miss debounce for the two "confidently declare
      //   absence" branches below (phantom-auto, unknown-resolved-nofill). A
      //   single 30s poll finding no match is not proof -- see the ref
      //   declaration comment for the live incident this fixes. Keyed by
      //   kind+ticker so unrelated branches/orders don't share a counter;
      //   cleared whenever there's no order to reconcile so it can't leak
      //   across windows.
      if(!_aos)_driftMissTrackerRef.current={};
      const _driftMiss=(key)=>{
        const _t=_driftMissTrackerRef.current;
        _t[key]=(_t[key]||0)+1;
        return _t[key];
      };
      const _driftHit=(key)=>{ delete _driftMissTrackerRef.current[key]; };
      // PHANTOM: Tara thinks she has a live position
      // V10.7.32: also reconcile when status='exit-pending' (V10.7.26 exit
      //   limit placed but not yet filled — we want to verify the position
      //   is still open on Kalshi until fill completes).
      // V13.4.319: exclude dry-run fills. A dry-run "filled" order never
      //   reaches the real Kalshi API (kalshiPlaceOrder short-circuits to a
      //   synthetic response), so positions.find() can never match it --
      //   this fired 'phantom-auto' on EVERY dry-run auto-exec trade,
      //   unconditionally, framed as "likely closed manually or order
      //   errored" when nothing had gone wrong at all. Reported live: the
      //   banner was constantly up while testing in the recommended
      //   dry-run-with-real-credentials setup (Trading Settings' own
      //   walkthrough Step 2/4). Real-money drift detection is untouched --
      //   this only skips the comparison when there is provably no real
      //   order to compare against.
      if(_aos&&!_aos.dryRun&&(_aos.status==='filled'||_aos.status==='exiting'||_aos.status==='exit-pending')&&_aos.ticker){
        const _match=positions.find(p=>p.ticker===_aos.ticker);
        // V13.4.291: this read _aos.count, a name the writer never sets, so
        //   Number(_aos.count) was NaN at every one of these five call sites.
        //   Two consequences, and the second is the dangerous one:
        //     • the phantom note read "thinks you hold 0 contract(s)";
        //     • `_match.count !== NaN` is ALWAYS true, so a matched, perfectly
        //       correct position raised a count-mismatch alert every single time.
        //   A safety reconciler that cries wolf on every live position trains you
        //   to ignore it, and then a real mismatch is invisible. Route through the
        //   authority, and only compare when the count is actually known.
        const _ordRec=readOrderState(_aos);
        const _taraCount=_ordRec.count||0;
        if(!_match){
          // V13.4.328: same safety fix as the unknown-resolved-nofill case
          //   below -- a ticker miss here doesn't prove the position is
          //   closed, only that it isn't listed under THIS ticker. Check for
          //   a same-side position elsewhere before declaring it phantom.
          const _expectedSide=_aos.side||(_aos.dir==='UP'?'yes':'no');
          const _sameSideOpen=positions.filter(p=>p.side===_expectedSide);
          if(_sameSideOpen.length===0){
            // V13.4.329: require 2 consecutive empty polls (>=30s of
            //   sustained absence) before declaring this -- see ref comment
            //   above _activeTicker for the live incident this fixes.
            const _misses=_driftMiss('phantom-auto:'+_aos.ticker);
            if(_misses<2){
              _details.push({
                kind:'phantom-auto-pending',
                ticker:_aos.ticker,
                taraCount:_taraCount,
                kalshiCount:0,
                side:_expectedSide,
                note:`Kalshi shows no position under this ticker on this check -- rechecking before concluding anything. If this was a fill moments ago, this can just be API propagation lag.`,
              });
            }else{
              _details.push({
                kind:'phantom-auto',
                ticker:_aos.ticker,
                taraCount:_taraCount,
                kalshiCount:0,
                side:_expectedSide,
                note:`Tara's auto-exec thinks you hold ${_taraCount} contract(s) but Kalshi shows none — likely closed manually or order errored`,
              });
            }
          }else{
            _driftHit('phantom-auto:'+_aos.ticker);
            _details.push({
              kind:'ticker-mismatch-auto',
              ticker:_aos.ticker,
              taraCount:_taraCount,
              kalshiCount:_sameSideOpen.reduce((a,p)=>a+p.count,0),
              side:_expectedSide,
              note:`Kalshi shows no position under the tracked ticker (${_aos.ticker}), but ${_sameSideOpen.length} other open ${_expectedSide.toUpperCase()} position(s) exist: ${_sameSideOpen.map(p=>p.ticker+' x'+p.count).join(', ')}. This may be a ticker mismatch -- check Kalshi directly before assuming this position is closed.`,
            });
          }
        }else{
          _driftHit('phantom-auto:'+_aos.ticker);
          const _expectedSide=_aos.side||(_aos.dir==='UP'?'yes':'no');
          if(_expectedSide&&_match.side!==_expectedSide){
            _details.push({
              kind:'side-mismatch-auto',
              ticker:_aos.ticker,
              taraCount:_taraCount,
              kalshiCount:_match.count,
              side:_match.side,
              note:`AutoTrade expects ${_expectedSide.toUpperCase()} on this ticker, but Kalshi shows ${_match.side.toUpperCase()}. Do not treat this as the intended position — verify the exchange position before any retry or exit.`,
            });
          }else if(_taraCount>0&&_match.count!==_taraCount){
            _details.push({
              kind:'count-mismatch-auto',
              ticker:_aos.ticker,
              taraCount:_taraCount,
              kalshiCount:_match.count,
              side:_match.side,
              note:`Auto-exec thinks ${_taraCount} contracts, Kalshi shows ${_match.count}`,
            });
          }
        }
      }
      // V13.4.322: SELF-HEALING FOR 'unknown' fill status. kalshiRunEntryLadder
      //   and _runExit now leave autoOrderState at status:'unknown' (instead
      //   of guessing) when they genuinely could not confirm whether an order
      //   filled -- see the verify-before-cancel / verify-before-exited fixes
      //   in _runEntry/_runExit. This poll already has Kalshi's real book in
      //   hand every 30s; use it to resolve the unknown definitively instead
      //   of leaving the user staring at an ambiguous state indefinitely.
      //   Deliberately does NOT auto-recover autoOrderState back to 'filled'
      //   with a guessed entry price -- Kalshi's positions endpoint does not
      //   expose average fill price in a field this file has verified, and a
      //   wrong guessed price would corrupt stop-loss/take-profit math worse
      //   than having no automated protection at all. Surfacing the confirmed
      //   truth quickly is the safe half of this fix; recovering full exit
      //   management from it is a separate, deliberately unstarted project.
      // V13.4.330: ALSO cover status:'error',reason:'no-fill'. Reported live:
      //   the entry ladder genuinely exhausted every rung believing each
      //   cancel was clean (the v330 cancel-race fix above closes the most
      //   likely cause, but this net catches it regardless of cause) and set
      //   status:'error' -- a status this self-healing check never looked at,
      //   so a real fill hidden behind a false 'no-fill' had NO safety net at
      //   all until the user checked Kalshi's own site by hand. 'error' with
      //   any other reason (bad-args, no-stake, a pre-order validation
      //   failure with no HTTP call ever made) is not a fill-uncertain state
      //   and stays out of scope -- only 'no-fill' specifically represents
      //   "we sent real orders and are not sure what happened to them."
      if(_aos&&!_aos.dryRun&&((_aos.status==='unknown'&&_aos.reason!=='exit-unconfirmed-verify-kalshi-directly')||(_aos.status==='error'&&_aos.reason==='no-fill'))&&_aos.ticker){
        const _match=positions.find(p=>p.ticker===_aos.ticker);
        if(_match&&_match.count>0){
          _driftHit('unknown-nofill:'+_aos.ticker);
          _details.push({
            kind:'unknown-resolved-filled',
            ticker:_aos.ticker,
            taraCount:0,
            kalshiCount:_match.count,
            side:_match.side,
            note:`Confirmed on Kalshi: this DID fill (${_match.count} contract(s) on ${_match.side.toUpperCase()}). Auto-exec lost track of the fill, so stop-loss/take-profit/trailing-stop will NOT manage this position. Use Exit Position below to close it, or manage it directly on Kalshi.`,
          });
        }else{
          // V13.4.328: SAFETY FIX. "No position under the tracked ticker" is
          //   not the same fact as "no position exists" -- a ticker mismatch
          //   (the market-selection logic picking a different strike/
          //   contract than the one that actually filled, a Kalshi ticker-
          //   format drift, etc.) produces the exact same zero-match result
          //   as a genuine non-fill. Reported live: this precise case,
          //   Kalshi's own site showing a real filled 3-contract position on
          //   the correct side while this code confidently told the user
          //   "confirmed no fill, safe to ignore." Only declare a clean
          //   no-fill when Kalshi reports NO open position on the expected
          //   side at all; if any same-side position exists elsewhere, this
          //   is an unresolved mismatch, not a safe negative.
          const _expectedSide=_aos.dir==='UP'?'yes':'no';
          const _sameSideOpen=positions.filter(p=>p.side===_expectedSide);
          if(_sameSideOpen.length===0){
            // V13.4.329: even a genuine same-side absence can be a one-poll
            //   snapshot taken before a just-placed fill propagated into
            //   Kalshi's /portfolio/positions. Reported live: exact match on
            //   Tara's own locked direction and side, absent on one 30s
            //   check, present on Kalshi's own site moments later. Require 2
            //   consecutive empty polls before calling this "Confirmed."
            const _misses=_driftMiss('unknown-nofill:'+_aos.ticker);
            if(_misses<2){
              _details.push({
                kind:'unknown-nofill-pending',
                ticker:_aos.ticker,
                taraCount:0,
                kalshiCount:0,
                side:_expectedSide,
                note:'Kalshi shows no position on this check -- rechecking before concluding this is a non-fill. If the order just placed, this can be propagation lag.',
              });
            }else{
              _details.push({
                kind:'unknown-resolved-nofill',
                ticker:_aos.ticker,
                taraCount:0,
                kalshiCount:0,
                side:_expectedSide,
                note:'Confirmed on Kalshi: this did not fill. Safe to ignore -- no real position exists for this window.',
              });
            }
          }else{
            _driftHit('unknown-nofill:'+_aos.ticker);
            _details.push({
              kind:'unknown-ticker-mismatch',
              ticker:_aos.ticker,
              taraCount:0,
              kalshiCount:_sameSideOpen.reduce((a,p)=>a+p.count,0),
              side:_expectedSide,
              note:`Kalshi shows no position under the tracked ticker (${_aos.ticker}), but ${_sameSideOpen.length} other open ${_expectedSide.toUpperCase()} position(s) exist: ${_sameSideOpen.map(p=>p.ticker+' x'+p.count).join(', ')}. This may be a ticker mismatch, not a genuine non-fill -- check Kalshi directly before assuming this is safe to ignore.`,
            });
          }
        }
      }
      // V13.4.354: EXIT-UNCERTAINTY RECONCILIATION. An exit can be accepted
      // without filling, or the response can fail after Kalshi has accepted it.
      // _runExit correctly records that as unknown/error and refuses to retry,
      // but the old reconciler only watched filled/exiting states. That left a
      // real open position with no banner after an uncertain exit. Use the
      // exchange position snapshot as the safety backstop: an open match stays
      // urgent; two consecutive empty snapshots are required before calling it
      // flat. This never places a second exit order.
      const _exitReason=String(_aos?.reason||'');
      const _exitUncertain=!!(_aos&&!_aos.dryRun&&_aos.ticker&&(
        (_aos.status==='unknown'&&_exitReason==='exit-unconfirmed-verify-kalshi-directly')
        ||(_aos.status==='error'&&(_exitReason.startsWith('exit failed:')||_exitReason.startsWith('exit threw:')))
      ));
      if(_exitUncertain){
        const _exitMatch=positions.find(p=>p.ticker===_aos.ticker&&p.count>0);
        const _exitRec=readOrderState(_aos);
        const _exitExpectedSide=_aos.side||(_aos.dir==='UP'?'yes':'no');
        const _exitKey='exit-unconfirmed:'+_aos.ticker;
        if(_exitMatch){
          _driftHit(_exitKey);
          _details.push({
            kind:'exit-unconfirmed-open',
            ticker:_aos.ticker,
            taraCount:_exitRec.count,
            kalshiCount:_exitMatch.count,
            side:_exitMatch.side,
            note:`Exit is not confirmed, and Kalshi still shows ${_exitMatch.count} ${_exitMatch.side.toUpperCase()} contract(s). Do not submit a duplicate exit until the original order is verified directly.` ,
          });
        }else{
          const _misses=_driftMiss(_exitKey);
          _details.push({
            kind:_misses<2?'exit-verification-pending':'exit-confirmed-flat',
            ticker:_aos.ticker,
            taraCount:_exitRec.count,
            kalshiCount:0,
            side:_exitExpectedSide,
            note:_misses<2
              ?'Kalshi shows no position on this check after an uncertain exit — rechecking before declaring it closed.'
              :'Kalshi shows no position on two consecutive checks after the uncertain exit. Treat the position as flat, but review the original exit order in the exchange history.',
          });
        }
      }
      // PHANTOM: manualKalshiEntry stale (user closed but didn't tell Tara)
      if(_mke&&_activeTicker&&_up){
        const _match=positions.find(p=>p.ticker===_activeTicker);
        const _expectedSide=_mke.side==='UP'?'yes':'no';
        if(!_match){
          _details.push({
            kind:'phantom-manual',
            ticker:_activeTicker,
            taraCount:Number(_mke.contracts)||0,
            kalshiCount:0,
            side:_expectedSide,
            note:`Logged manual fill says ${_mke.contracts} × ${_mke.entryCents}¢ but Kalshi shows no position — clear the logged fill if you've closed`,
          });
        }else if(_match.count!==Number(_mke.contracts)||_match.side!==_expectedSide){
          _details.push({
            kind:'count-mismatch-manual',
            ticker:_activeTicker,
            taraCount:Number(_mke.contracts)||0,
            kalshiCount:_match.count,
            side:_match.side,
            note:`Logged ${_mke.contracts} contracts on ${_expectedSide}; Kalshi shows ${_match.count} on ${_match.side}`,
          });
        }
      }
      // UNKNOWN: Kalshi has a position on the active ticker that Tara doesn't know about
      if(_activeTicker&&!_aos&&!_mke){
        const _match=positions.find(p=>p.ticker===_activeTicker);
        if(_match){
          _details.push({
            kind:'unknown-to-tara',
            ticker:_activeTicker,
            taraCount:0,
            kalshiCount:_match.count,
            side:_match.side,
            note:`Kalshi shows ${_match.count} ${_match.side.toUpperCase()} on the active window — Tara doesn't know about this. Use Exit Position below to close it, log it via ENTERED ${_match.side==='yes'?'UP':'DOWN'} + Real Kalshi fill, or close on Kalshi directly.`,
          });
        }
      }
      return _details;
    };
    const _tick=async(force=false)=>{
      if(_stopped)return;
      if(_inflight)return;
      if(!force&&!_shouldPoll()){
        // Nothing to reconcile — keep state as 'idle' but don't burn API calls
        return;
      }
      _inflight=true;
      try{
        const _res=await kalshiFetchPositions({apiKeyId:kalshiCreds.apiKeyId,privateKeyPem:kalshiCreds.privateKeyPem});
        if(_stopped)return;
        if(!_res.ok){
          setPositionReconciliation(prev=>({
            ...prev,
            status:'error',
            lastErrorMsg:_res.reason||'unknown',
            lastCheckAt:Date.now(),
          }));
          return;
        }
        const _details=_computeDrift(_res.positions);
        // V13.4.351: confirmed auto no-fill must retire the local advisory marker.
        // Before this, auto-follow called handleManualSync() and created a visible
        // UP/DOWN position before Kalshi had confirmed a fill. When the order later
        // resolved as a genuine no-fill, the banner correctly said Tara:0/Kalshi:0
        // but the header still rendered that stale marker as a live position.
        const _confirmedNoFill=_details.filter(d=>d&&d.kind==='unknown-resolved-nofill');
        const _aosNow=autoOrderStateRef.current;
        const _entryNow=_positionEntryRef.current;
        if(_confirmedNoFill.length>0&&_aosNow&&(_aosNow.status==='unknown'||(_aosNow.status==='error'&&_aosNow.reason==='no-fill'))){
          const _autoFollowMarker=!!(_entryNow&&_entryNow.source==='auto-follow'
            &&_entryNow.side===_aosNow.dir
            &&_userPositionRef.current===_aosNow.dir);
          if(_autoFollowMarker){
            setUserPosition(null);
            setPositionEntry(null);
            setCurrentOffer('');
            try{console.info('[V13.4.351] confirmed auto no-fill: cleared advisory auto-follow marker',_aosNow.ticker);}catch(_e){}
          }
          // Keep the terminal outcome in the order state so every consumer agrees
          // that this attempt is flat and no longer treats it as an unresolved fill.
          setAutoOrderState(prev=>{
            if(!prev||prev.ticker!==_aosNow.ticker||!(prev.status==='unknown'||(prev.status==='error'&&prev.reason==='no-fill')))return prev;
            return{...prev,status:'no-fill',noFillConfirmed:true,noFillConfirmedAt:Date.now()};
          });
        }
        // V13.4.356: a carried real-money order may legitimately span the
        //   window boundary. Once the exchange snapshot has been empty for the
        //   same two-poll debounce used by the drift detector, retire only that
        //   carried state. Never do this while the entry/exit request itself is
        //   still running, and never retire a ticker-mismatch/open result.
        const _rolloverFlat=!!(_aosNow?.rolloverPending
          &&!_entryBusyRef.current&&!_exitBusyRef.current
          &&_details.some(d=>d&&d.ticker===_aosNow.ticker
            &&(d.kind==='phantom-auto'||d.kind==='unknown-resolved-nofill'||d.kind==='exit-confirmed-flat')));
        if(_rolloverFlat){
          setAutoOrderState(prev=>prev&&prev.rolloverPending&&prev.ticker===_aosNow.ticker?null:prev);
        }
        setPositionReconciliation({
          status:_details.length>0?'drift':'in-sync',
          driftCount:_details.length,
          driftDetails:_details,
          kalshiPositions:_res.positions,
          lastCheckAt:Date.now(),
          lastSuccessAt:Date.now(),
          lastErrorMsg:null,
        });
      }catch(e){
        // Silent on network errors — don't flag drift if we can't even reach Kalshi
        if(!_stopped){
          setPositionReconciliation(prev=>({
            ...prev,
            status:'error',
            lastErrorMsg:String(e?.message||e),
            lastCheckAt:Date.now(),
          }));
        }
      }finally{
        _inflight=false;
      }
    };
    // First check after 5s (give the app time to settle), then every 30s
    const _firstHandle=setTimeout(_tick,5000);
    const _intervalHandle=setInterval(_tick,30000);
    // V13.4.343: expose this exact check for the drift banner's manual retry.
    _manualReconcileNowRef.current=()=>_tick(true);
    return ()=>{
      _stopped=true;
      _manualReconcileNowRef.current=null;
      clearTimeout(_firstHandle);
      clearInterval(_intervalHandle);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  },[kalshiCreds.apiKeyId,kalshiCreds.privateKeyPem,autoExecSettings?.enabled]);

  // V10.2.10 — console hook for on-demand reconciliation diagnostics
  useEffect(()=>{
    if(typeof window==='undefined')return;
    window.__taraPositionReconciliation=async()=>{
      try{
        if(!kalshiCreds.apiKeyId||!kalshiCreds.privateKeyPem){
          console.warn('No Kalshi credentials configured');
          return null;
        }
        const _res=await kalshiFetchPositions({apiKeyId:kalshiCreds.apiKeyId,privateKeyPem:kalshiCreds.privateKeyPem});
        console.group('%c━━━ Position reconciliation ━━━','color:#23B981;font-weight:bold;font-size:13px');
        if(!_res.ok){
          console.error('Kalshi fetch failed:',_res.reason);
          console.groupEnd();
          return{ok:false,reason:_res.reason};
        }
        console.info('Kalshi positions:',_res.positions);
        console.info('Tara autoOrderState:',autoOrderStateRef.current);
        console.info('Tara manualKalshiEntry:',_manualKalshiEntryRef.current);
        console.info('Tara userPosition:',_userPositionRef.current);
        console.info('Active market ticker:',_kalshiActiveMarketRef.current?.ticker||'(none)');
        console.info('Last poll result:',positionReconciliation);
        console.groupEnd();
        return{ok:true,kalshi:_res.positions,tara:{autoOrderState:autoOrderStateRef.current,manualKalshiEntry:_manualKalshiEntryRef.current,userPosition:_userPositionRef.current},lastReconciliation:positionReconciliation};
      }catch(e){
        console.error('Reconciliation error:',e);
        return{ok:false,error:String(e?.message||e)};
      }
    };
  },[kalshiCreds.apiKeyId,kalshiCreds.privateKeyPem,positionReconciliation]);

  // ── V13.4.332: COST/TIMING CAUTION WATCH (non-blocking, data-motivated) ────
  // A 1000-trade log analysis (2026-09-10) found that paying 70c+ (the market
  // already very confident) while 10+ minutes remain in the window was the
  // single worst-performing combination checked: 58.3% WR, -6.0c/trade average
  // across the full log. The pattern held the same losing direction in both a
  // chronological train split (n=9) and test split (n=27), but neither half
  // alone clears significance (p=0.674, p=0.200) -- the sample is too thin to
  // trust as a hard rule yet. Per this session's own standing bar (the
  // isTrendAtLock sit-out in V13.4.324 only shipped once BOTH halves cleared
  // significance), this does not get to be a gate. It gets a caution instead --
  // exactly the same "surface it, don't block it" treatment V9.7.6's
  // WEAK-CLUSTER CAUTION already uses for a thin-but-real cluster.
  //
  // Deliberately implemented as a single post-commit watcher rather than
  // threading a check into every tier's own commit branch (directional-lock,
  // time-cap-commit, patient, structural-led, no-sitout-commit, ... each
  // builds its own snapshot object independently) -- one shared checkpoint on
  // the committed snapshot covers all of them uniformly and can't miss one.
  // Only ever ADDS to snap.caution/reason, never changes call/direction/size,
  // and only runs once per snapshot (_costTimeWatchChecked guards against
  // rechecking every tick). The underlying fields (kalshiAtLock, atSecondsLeft)
  // are already recorded on every log entry regardless of this effect, so
  // re-validating this pattern later needs no schema change.
  useEffect(()=>{
    const iv=setInterval(()=>{
      const snap=taraCallSnapshotRef.current;
      if(!snap||!snap.locked||snap.call==='SIT_OUT'||snap._costTimeWatchChecked)return;
      const cost=Number(snap.kalshiAtLock);
      const secLeft=Number(snap.atSecondsLeft);
      if(!Number.isFinite(cost)||!Number.isFinite(secLeft)){
        taraCallSnapshotRef.current={...snap,_costTimeWatchChecked:true};
        return;
      }
      if(cost>=70&&secLeft>=600){
        const _note='cost/timing watch -- 70c+ with 10+min left, historically the weakest combination in the log (58% WR, -6c avg, unproven at current sample size) -- not blocked, just flagged';
        taraCallSnapshotRef.current={
          ...snap,
          caution:snap.caution?`${snap.caution} · ${_note}`:_note,
          _costTimeWatchChecked:true,
        };
      }else{
        taraCallSnapshotRef.current={...snap,_costTimeWatchChecked:true};
      }
    },1000);
    return()=>clearInterval(iv);
  },[]);

  // ── POLL EFFECT ───────────────────────────────────────────────────────────
  // While an order is in 'placing'/'submitted'/'resting'/'partially_filled' state,
  // poll its status every 2s. On 'filled', stamp fillPrice. On 'canceled'/'rejected',
  // surface the error.

  // ─────────────────────────────────────────────────────────────────────
  // V10.7.21 — EXIT-POLL EFFECT (matches entry poll architecture)
  // ─────────────────────────────────────────────────────────────────────
  //   Polls the exit order every 2s while status='exit-pending'. When the
  //   limit fills, sets status='exited' with actual fill price. After 15s
  //   of no fill, cancels the limit and places a MARKET order as fallback
  //   so the exit actually happens (no stuck limits resting in the book).

  // V9.7.4: 1Hz tick used by the ladder lifecycle effect below to re-evaluate
  //   the age check every second while resting. Declared HERE (before the
  //   effect that references it in its deps) to avoid a TDZ ReferenceError.
  //   The interval is only ARMED when the ladder is enabled and an order is
  //   actually resting — see the small useEffect right after this state.
  const[_ladderTick,setLadderTick]=useState(0);

  // ── V9.7.4: ENTRY LADDER LIFECYCLE EFFECT ────────────────────────────────
  // Watches resting auto-exec orders that were placed via the entry ladder
  // (`ladderEnabled === true`). When the current step has been resting for
  // `entryLadderStepSec`, cancel + replace at the next price tier:
  //   step 0 → step 1: cancel undercut limit, replace at current offer
  //   step 1 → step 2: cancel limit, take market (current offer + slippage)
  //
  // Honest limitations:
  //   - Cancel-then-place is two separate API calls; if the cancel succeeds but
  //     the replace fails, we end up with no order. Status becomes 'error'
  //     and the user sees it in the LiveTradeCoach status strip.
  //   - If the order fills DURING the cancel call, Kalshi will reject the cancel
  //     and we treat that as "filled, no replace needed" by checking status.
  //   - Disabled in dry-run: the dry-run order auto-fills at 3s in the POLL
  //     effect, so the ladder never triggers in dry mode. This is intentional
  //     — verify the ladder logic with live orders only, after dry-run plumbing
  //     is confirmed working.

  // ═══════════════════════════════════════════════════════════════════════════
  // V9.17.3: SCALPER AUTO-EXEC EFFECT (re-added with mutual exclusivity)
  // ═══════════════════════════════════════════════════════════════════════════
  // When scalperSettings.autoExecEnabled is ON, automatically place a Kalshi
  // limit order on SUGGEST. Mutual exclusivity with Tara auto-exec is enforced
  // in two ways:
  //   1. Settings UI: enabling one auto-disables the other (in TradingSettingsModal)
  //   2. Runtime conflict guard: if Tara has a locked position on the same market,
  //      block scalper auto-exec (extra safety net even if settings get inconsistent)
  // Manual entry/exit is the user's path otherwise (still works regardless of toggle).
  const scalperOrderStateRef=React.useRef({lastFiredSignature:null,lastFiredAt:0,inFlight:false});

  // ── EXIT EFFECT ───────────────────────────────────────────────────────────
  // After an order fills, three exit paths (whichever fires first):
  //   (a) Take profit: Kalshi YES-side offer reaches autoExitOffer
  //   (b) Stop loss: our side drops by stopLossDeltaCents from fill price (V9.6.0)
  //   (c) Time exit: fewer than timeExitSecLeft seconds remain in window (V9.6.0)
  // Each path fires a sell. If none fires, the position settles naturally on Kalshi.

  // ── CLEANUP ON WINDOW ROLL ────────────────────────────────────────────────
  // When the window changes, clear stale auto-order state. The trade itself was either
  // exited (exit effect above) or settled naturally on Kalshi. New window → fresh slate.
  const _lastSeenWindowIdRef=useRef('');

  // ── DAILY P&L ROLLUP FROM TRADE LOG ──────────────────────────────────────
  // When a new resolved trade lands in taraCallLog AND it has an autoExec marker,
  // update autoExecDayPnL. This is the cooldown / daily-loss-cap data source.
  // Also trigger loss-streak cooldown when threshold hit.
  const _lastSeenAutoExecTradeIdRef=useRef(0);

  // ── V9.7.0: MISSION BANKROLL ROLLUP FROM TRADE LOG ───────────────────────
  // When a new auto-exec trade with a missionInfo stamp settles, update the
  // mission bankroll, trade counters, and check status transitions
  // (target hit / drawdown floor breached).

  // V8.0 HOTFIX: conviction sampler + trajectory derivation. Located here (not where the
  //   ref is declared earlier) because they reference `taraCall` which only finishes being
  //   declared on the line above. Putting the dep array on `[taraCall]` further up caused
  //   a TDZ ReferenceError.
  useEffect(()=>{
    // V13.4.290 CORRECTION (R3): v285's field-name fix was inert. The dep array here
    //   is `[taraCall]`, and taraCall is a fresh object literal from its own IIFE on
    //   EVERY render (~L44282) -- and the app re-renders every second because the
    //   window clock's setInterval writes fresh timeState every 1000ms. So this
    //   effect's cleanup ran roughly one second after each setInterval call, and the
    //   5000ms callback below was never once reached. convictionHistoryRef stayed
    //   permanently empty, convictionTrajectory stayed permanently 'UNKNOWN', and the
    //   BUILDING/FADING arrow never rendered -- exactly as the comment already here
    //   said, except it was describing a still-broken build, not a fixed one.
    //   Mounted once instead, reading the live posterior through analysisRef
    //   (~L44089, already kept current on every `analysis` change by its own effect)
    //   rather than closing over the `analysis` prop directly -- `analysis` is a
    //   useMemo whose own deps include timeState.minsRemaining/secsRemaining, so it
    //   is exactly as unstable a dependency as taraCall was.
    const iv=setInterval(()=>{
      const _post=Number(analysisRef.current?.rawProbAbove);
      if(!Number.isFinite(_post))return;
      const _now=Date.now();
      convictionHistoryRef.current.push({time:_now,post:_post});
      // 2-min ring buffer
      const _cutoff=_now-120000;
      convictionHistoryRef.current=convictionHistoryRef.current.filter(h=>h.time>=_cutoff);
    },5000);
    return()=>clearInterval(iv);
  },[]);
  // BUILDING / STABLE / FADING / UNKNOWN — recompute on each posterior tick
  const convictionTrajectory=useMemo(()=>{
    const h=convictionHistoryRef.current;
    if(!h||h.length<4)return{state:'UNKNOWN',delta:0};
    const _now=Date.now();
    const _early=h.filter(p=>_now-p.time>30000); // ≥30s old
    const _recent=h.filter(p=>_now-p.time<=15000); // last 15s
    if(_early.length<2||_recent.length<2)return{state:'UNKNOWN',delta:0};
    const _eAvg=_early.reduce((s,p)=>s+p.post,0)/_early.length;
    const _rAvg=_recent.reduce((s,p)=>s+p.post,0)/_recent.length;
    const _eConv=Math.abs(_eAvg-50);
    const _rConv=Math.abs(_rAvg-50);
    const _delta=_rConv-_eConv;
    if(_delta>=2)return{state:'BUILDING',delta:_delta};
    if(_delta<=-2)return{state:'FADING',delta:_delta};
    return{state:'STABLE',delta:_delta};
    // V13.4.284: dep was taraCall?.posterior — permanently undefined, so this memo
    //   never recomputed on a posterior tick even once the ref does fill.
    // V13.4.285 CORRECTION: taraCall?.rawProbAbove is undefined too; analysis is the
    //   object that carries the posterior.
  },[analysis?.rawProbAbove]);
  // V5.7.8: mirror confluence into the ref so the engine cooldown gate can read it
  confluenceStateRef.current={
    isConfluent:taraCall?._ctx?.isConfluent||taraCall?._ctx?.isRisingConfluence||false,
    isSuperConfluent:taraCall?._ctx?.isSuperConfluent||false,
  };
  // V4.3: Attach lifecycle data so the Tara's Call card can render phase/samples/snapshot.
  //   Refs read at render time — same data the lifecycle effect uses.
  const _activeCallSnapshot=activeCallSnapshot(taraCallSnapshotRef.current,{windowId:computeWindowId(windowType),windowType,asset:currentAsset,now:Date.now()});
  if(!startupReadiness.ready&&!_activeCallSnapshot){
    Object.assign(taraCall,{call:'SIT_OUT',direction:null,confidence:0,startupBlocked:true,reason:startupReadiness.reason});
  }
  taraCall.samples=taraCallSampleRef.current?.count||0;
  taraCall.snapshot=_activeCallSnapshot;
  taraCall.blockedDecision=_activeCallSnapshot?.blockedDecision||describeCallBlocker({call:taraCall,
    readiness:startupReadiness,hold:callCommitHoldRef.current,gateHold:callGateHoldRef.current,samples:taraCallSampleRef.current,
    quote:_kalshiQuote,windowId:computeWindowId(windowType),asset:currentAsset,now:Date.now()});
  currentCallBlockerRef.current=taraCall.blockedDecision;
  // Observation only: the research hook has no order, arm, or live-lock callback.
  const lockResearch=useLockStudy({
    windowId:computeWindowId(windowType),windowType,asset:currentAsset,
    quote:_kalshiQuote,probabilityUpRaw:analysis?.rawPosteriorUncalibrated??analysis?.rawProbAbove,
    probabilityUpCurrent:analysis?.rawProbAbove,probabilitySource:analysis?.rawPosteriorUncalibrated!=null?'rawPosteriorUncalibrated':'legacy-rawProbAbove-fallback',signalScore:taraCall.confidence,
    currentDecision:_activeCallSnapshot,referencePrice:analysis?.referencePrice,
    spot:currentPrice,strike:kalshiStrike,atrBps:analysis?.atrBps,
    tape15:analysis?.tapeWindows?.w15?.buyPct,tape30:analysis?.tapeWindows?.w30?.buyPct,
    tape60:analysis?.tapeWindows?.w60?.buyPct,momentum:analysis?.rawSignalScores?.momentum,regime:analysis?.regime,
    learningFeatures:analysis?.learningFeatures,dataReady:startupReadiness.ready,
    dataProvenance:{calendarStatus:economicCalendar.status,calendarSources:economicCalendar.sources.map(s=>({id:s.id,status:s.status,fetchedAt:s.fetchedAt})),derivativesStatus:bloomberg.status,derivativesAt:bloomberg.lastUpdate,referenceSources:brtiApprox?.sourceHealth||[],referenceIsBenchmark:false},
  },{device:_taraDeviceId,version:TARA_BUILD_VERSION,publish:async(path,data)=>{
    if(!_sbClient)throw new Error('Research cloud unavailable');
    const {error}=await _sbClient.from('tara_state').upsert({doc_path:path,data,updated_at:new Date().toISOString()},{onConflict:'doc_path'});
    if(error)throw error;
  }});
  // V13.4.76: AUTO-FOLLOW LOCK (advisory). When Tara locks a real UP/DOWN call, auto-copy
  //   it into the tracked position so the live advisor + coach (hold / take-profit /
  //   cut-loss / reversal alerts) start managing it immediately -- identical to tapping
  //   'Got in on Tara's call', just automatic. PURELY ADVISORY: sets userPosition only,
  //   never places a Kalshi order (that engine was removed in 13.4.75). One entry per
  //   window; respects a manual exit (won't re-enter the same window) and the anti-tilt
  //   cooldown. Off-switch: localStorage 'taraAutoFollowLock'='off'. ASCII-only.
  const _autoFollowedWindowRef=React.useRef(null);
  const _afSnap=taraCall&&taraCall.snapshot;
  const _afLocked=!!(_afSnap&&_afSnap.locked===true);
  const _afCall=_afSnap?_afSnap.call:null;
  React.useEffect(()=>{
    try{
      var _on=(function(){try{return localStorage.getItem('taraAutoFollowLock')!=='off';}catch(_e){return true;}})();
      if(!_on)return;
      if(!_afLocked)return;
      if(_afCall!=='UP'&&_afCall!=='DOWN')return;
      if(_afSnap&&(_afSnap.wasOverriddenNoTrade===true||_afSnap.wasOverriddenSitOut===true))return;
      var _wid=null;try{_wid=computeWindowId(windowType);}catch(_e2){_wid=null;}
      if(!_wid)return;
      if(_autoFollowedWindowRef.current===_wid)return;
      if(userPosition!==null)return;
      if(typeof tiltLockUntil!=='undefined'&&tiltLockUntil>Date.now())return;
      if(typeof handleManualSync!=='function')return;
      _autoFollowedWindowRef.current=_wid;
      handleManualSync(_afCall,{autoFollow:true});
    }catch(_eR){}
  },[_afLocked,_afCall,userPosition,windowType]);
  // V9.2.3: Expose widget data on window for the popup to read.
  useEffect(()=>{
    const _snap=taraCall?.snapshot;
    const _dir=_snap?.call;
    const _conf=_snap?.posterior?Math.round(_snap.posterior):'';
    const _secLeft=(timeState?.minsRemaining||0)*60+(timeState?.secsRemaining||0);
    const _timer=`${Math.floor(_secLeft/60)}:${String(_secLeft%60).padStart(2,'0')}`;
    window.__taraWidgetData={
      dir:_dir||null,conf:_conf,price:currentPrice,asset:currentAsset,
      wt:windowType,timer:_timer,
      streakType:currentStreak?.type,streakCount:currentStreak?.count||0,
    };
  },[taraCall,currentPrice,currentAsset,windowType,timeState,currentStreak]);
  // V7.4: Ensure _ctx exists and has q + conviction so the speed-dial UI can surface
  //   the live blocker (e.g., "quality 22 / need 39"). Most SIT_OUT returns from the
  //   lock callback omit _ctx, leaving the UI without a way to read these.
  if(!taraCall._ctx)taraCall._ctx={};
  if(taraCall._ctx.q==null)taraCall._ctx.q=qualityGate?.score||0;
  if(taraCall._ctx.conviction==null)taraCall._ctx.conviction=Math.abs((analysis?.rawProbAbove||50)-50);
  // V5.5d: Compute needSamples for display — mirrors lifecycle (clean=1, good=2, default=3,
  //   hostile=5) with per-60s time-decay.
  (()=>{
    const _q=qualityGate?.score||0;
    const _fgtAbs=Math.abs(analysis?.mtfAlignment||0);
    const _regime=analysis?.regime||'';
    const _cleanRegime=['TRENDING UP','TRENDING DOWN','SHORT SQUEEZE','LONG SQUEEZE'].includes(_regime);
    const _winType=analysis?.windowAmplitude?.label;
    const _hostile=_winType==='DEAD'||_winType==='WHIPSAW';
    // V5.6.4: Use the same _ctx the gate logic computed if available — keeps display in sync
    //   with the actual lock criteria. Falls back to local read of qualityGate if missing.
    const _ctx=taraCall._ctx;
    const _conv=_ctx?.conviction??Math.abs((analysis?.rawProbAbove||50)-50);
    const _tapeStronglyAgrees=_ctx?.tapeStronglyAgrees||false;
    const _tapeSuperStrong=_ctx?.tapeSuperStrong||false;
    const _kalshiAgrees=_ctx?.kalshiAgrees||false;
    const _kalshiStronglyAgrees=_ctx?.kalshiStronglyAgrees||false;
    const _totalSec=windowType==='15m'?900:300;
    const _elapsed=_totalSec-((timeState.minsRemaining*60)+timeState.secsRemaining);
    const _remaining=Math.max(0,_totalSec-_elapsed);
    // V5.6.4: Tier-based sample requirements (one sample ≈ one second).
    //   Tier −1 (rising-confluence, ~8s): V6.0.1 — early entry while move builds.
    //                                     4 of 4 leading conditions: 15s tape leads, FGT
    //                                     points right way, conviction leans, strike actionable.
    //                                     V6.0.2: tightened to 4/4 + 8 samples.
    //   Tier 0 (super-confluence, ~5s):   TAPE+FGT+SCORE+STRIKE all agree, tape super-strong, no Kalshi disagreement
    //   Tier 0b (confluence, ~8s):        TAPE+FGT+SCORE+STRIKE all agree
    //   Tier 1 (exceptional, ~15s):       q≥85, conv≥20, FGT≥3.5, clean+!hostile, tape super-strong, kalshi confirms
    //   Tier 2 (strong+,     ~30s):       q≥75, conv≥18, FGT≥3.0, !hostile, tape strongly agrees, kalshi agrees
    //   Tier 3 (strong,      ~60s):       q≥70, conv≥15, FGT≥2.5, !hostile, AND (tape OR kalshi agrees)
    //   Tier 4 (default,     ~90s):       q≥60, conv≥12, FGT≥1.5, !hostile  ← high-quality lock
    //   Tier 5 (patient,    ~130s):       anything else passing the coin-flip gates
    // V6.0.3: Patient tier RESTORED. V6.0.2 removed it via Gate 5; user feedback was that
    //   non-coin-flip setups should still get called, not skipped. Existing coin-flip
    //   gates (Q_FLOOR, CONV_FLOOR, tape veto, Kalshi extreme, FGT min) already filter
    //   the genuinely-no-information cases. Patient capped at 130 samples (~130-180s
    //   at typical conv) to honor the 2-3 min cap.
    const _isConf=_ctx?.isConfluent||false;
    const _isSuperConf=_ctx?.isSuperConfluent||false;
    const _isRisingConf=_ctx?.isRisingConfluence||false;
    const _isTapeLed=_ctx?.isTapeLed||false;
    const _isStructuralLed=_ctx?.isStructuralLed||false;
    let _need;
    if(_isStructuralLed){
      _need=3; // V6.2.0: STRUCTURAL-LED — grand trend + channel align with direction. ~3s lock.
    } else if(_isSuperConf){
      _need=3; // V6.1.3: 5 → 3 samples (~3s). All quality guards already hold; no need to dwell.
    } else if(_isTapeLed){
      // V6.0.7: tape-led — fastest tier alongside super-confluence.
      // V6.1.2: now requires FGT-aligned OR strike strongly favorable + non-chop regime.
      // V6.1.3: 5 → 3 samples. Quality guards added in V6.1.2 mean we can lock fast.
      _need=3;
    } else if(_isConf){
      _need=5; // V6.1.3: 8 → 5
    } else if(_isRisingConf){
      _need=6; // V6.1.3: 8 → 6
    } else if(_q>=85&&_conv>=20&&_fgtAbs>=3.5&&_cleanRegime&&!_hostile&&_tapeSuperStrong&&_kalshiStronglyAgrees){
      _need=7; // V6.1.3: 10 → 7
    } else if(_q>=75&&_conv>=18&&_fgtAbs>=3.0&&!_hostile&&_tapeStronglyAgrees&&_kalshiAgrees){
      _need=15; // V6.1.3: 20 → 15
    } else if(_q>=70&&_conv>=15&&_fgtAbs>=2.5&&!_hostile&&(_tapeStronglyAgrees||_kalshiStronglyAgrees||_kalshiAgrees)){
      _need=30; // V6.1.3: 45 → 30
    } else if(_q>=60&&_conv>=12&&_fgtAbs>=1.5&&!_hostile){
      _need=60; // V6.1.3: 90 → 60
    } else {
      // V6.1.3: Patient tier eliminated. Below this quality, sit out.
      _need=100;
    }
    // V6.0.3 / V6.1.3: HARD CAP — no lock-yes path may exceed 100 samples regardless of tier.
    if(_need>100)_need=100;
    // V6.2.6: Soft hint trims sample requirement by 40%, floor of 2 to stay meaningful
    const _hintActive=softHintRef.current>0&&(Date.now()-softHintRef.current)<10000;
    if(_hintActive)_need=Math.max(2,Math.round(_need*0.6));
    // V5.7.2: cap at remaining time minus 90s buffer so lock has time to be meaningful
    const _maxWithBuffer=Math.max(15,_remaining-90);
    taraCall.needSamples=Math.min(_need,_maxWithBuffer);
    // V5.7.2: Honest ETA calculation. Track sample-count history with timestamps so we can
    //   compute actual accumulation rate. Samples only accrue when conviction ≥ floor —
    //   if conviction has been mostly below floor, rate is near zero and the old "1Hz
    //   assumption" was lying. Now we measure what's actually happening.
    const _now=Date.now();
    const _hist=taraSampleRateRef.current;
    // Only push a new datapoint when count actually changed, or every 5s as heartbeat
    const _last=_hist[_hist.length-1];
    if(!_last||_last.count!==taraCall.samples||_now-_last.t>=5000){
      _hist.push({t:_now,count:taraCall.samples});
      while(_hist.length>8)_hist.shift();
    }
    // Compute rate from oldest-to-newest in history (must span ≥10s for stable estimate)
    const _first=_hist[0];
    const _spanMs=_first?_now-_first.t:0;
    const _gain=_first?taraCall.samples-_first.count:0;
    const _rate=_spanMs>=10000?(_gain/(_spanMs/1000)):null; // samples per second
    if(_rate==null||_rate<0.1){
      // Stalled — no useful estimate. Display will show "scanning, conviction not firm enough"
      taraCall.lockEtaSec=null;
      taraCall.lockEtaStalled=true;
    } else {
      const _remainNeeded=Math.max(0,taraCall.needSamples-taraCall.samples);
      taraCall.lockEtaSec=Math.round(_remainNeeded/_rate);
      taraCall.lockEtaStalled=false;
    }
    // V7.2: propagate lockEtaStalled to _ctx so the speed-dial display can read it.
    //   Without this the "stalled" branch in the ETA UI never fires and the display
    //   glues at "~1s" once virtual samples count down past zero.
    if(taraCall&&taraCall._ctx){taraCall._ctx.lockEtaStalled=taraCall.lockEtaStalled===true;}
  })();

  // V3.2.4: Snapshot Tara's Call when endgame freeze first fires. This pins Tara's
  //   call for this round. Subsequent posterior shifts within the freeze zone don't
  //   change what gets scored. Reset on each window rollover (handled in rollover effect).
  // V4.2: Two snapshot paths now — early-lock when conditions are clean enough to commit
  //   without waiting, OR endgame-freeze fallback for everything else. Also resets sample
  //   counter when call direction flips.
  useEffect(()=>{
    // A delay-gate abort can log a snapshot before the normal lock path runs.
    // Initialize these stamps at effect entry so the hoisted logger never sees
    // a temporal-dead-zone binding on that early path.
    let _earlyLockFired=false;
    let _earlyLockTier=null;
    let _fastLockFired=false;
    if(activeCallSnapshot(taraCallSnapshotRef.current,{windowId:computeWindowId(windowType),windowType,asset:currentAssetRef.current||'BTC',now:Date.now()}))return;
    taraCallSnapshotRef.current=null; // a previous window is not this window's decision
    const tc=taraCall;
    const isCall=tc.call==='UP'||tc.call==='DOWN';
    const _traceDecision=(code,reason,gate={})=>{
      const windowId=computeWindowId(windowType),at=Date.now();
      const direction=isDirection(tc.call)?tc.call:null;
      const k=kalshiYesPrice==null?NaN:Number(kalshiYesPrice);
      callDecisionTraceRef.current=appendDecisionTrace(callDecisionTraceRef.current,{windowId,at,code,reason,direction,
        confidence:tc.confidence??null,directionalPrice:direction&&Number.isFinite(k)?(direction==='UP'?k:100-k):null,
        secondsLeft:(Date.parse(windowId.replace(/^(5m|15m)-/,''))+(windowType==='15m'?900000:300000)-at)/1000,
        samples:taraCallSampleRef.current?.count??0,claimedDirection:taraCallSampleRef.current?.dir||null,
        requiredSamples:tc._ctx?.needSamples??null,dataReady:!!startupReadiness.ready,
        quoteIssue:inspectDecisionQuote(_kalshiQuote,{windowId,asset:currentAssetRef.current||'BTC',now:at}).reason||null,gate});
    };
    // Observation only. Stamp the branch which actually returns without a lock,
    // before the next render/deadline, instead of reporting sample formation.
    const _noteGateHold=(code,reason,details={})=>{
      const windowId=computeWindowId(windowType),asset=currentAssetRef.current||'BTC',now=Date.now();
      callGateHoldRef.current={code,reason,details,windowId,asset,at:now,
        direction:isDirection(tc.call)?tc.call:null,scope:details.scope||'direction'};
      currentCallBlockerRef.current=describeCallBlocker({call:tc,readiness:startupReadiness,
        hold:callCommitHoldRef.current,gateHold:callGateHoldRef.current,
        samples:taraCallSampleRef.current,quote:_kalshiQuote,windowId,asset,now});
      tc.blockedDecision=currentCallBlockerRef.current;
      _traceDecision(code,reason,details);
    };
    // Read the shared window first so a late-opening tab cannot replace a
    // pre-existing Call with a new deadline sitout.
    if(!_cloudRestoreCompletedRef.current&&!(hardForceRef.current>0&&Date.now()-hardForceRef.current<5000)){
      _noteGateHold(_cloudRestoreErrorRef.current?'shared-lock-restore-error':'shared-lock-restore',
        _cloudRestoreErrorRef.current||'Waiting for the shared window decision to finish loading.',{scope:'window'});
      return;
    }

    // The clock cannot create a new automatic Call after this deadline.
    // Existing snapshots returned above; explicit manual overrides remain tagged.
    const _decisionSecondsLeft=(Date.parse(computeWindowId(windowType).replace(/^(5m|15m)-/,''))+(windowType==='15m'?900000:300000)-Date.now())/1000;
    if(_decisionSecondsLeft<=callLockDeadline(windowType)&&!(hardForceRef.current>0&&Date.now()-hardForceRef.current<5000)){
      const reason=windowType==='15m'?'No qualifying Call before the 7-minute cutoff. Sitting out this window.':'No qualifying Call before the 30-second cutoff. Sitting out this window.';
      taraCallSnapshotRef.current=deadlineSitout({atPosterior:analysis?.rawProbAbove,blockedDecision:currentCallBlockerRef.current},{reason,secondsLeft:_decisionSecondsLeft,cutoff:callLockDeadline(windowType)});
      lockedCallRef.current=null;
      _logSnapshotEntry(taraCallSnapshotRef.current);
      _persistLock();
      return;
    }

    if(!startupReadiness.ready&&!(hardForceRef.current>0&&Date.now()-hardForceRef.current<5000)){
      if(_flatGapWatchRef.current)_flatGapWatchRef.current.earlyConfirmation=null;
      return;
    }

    // ─────────────────────────────────────────────────────────────────────
    // V10.4.1 MODULE A — EV-AWARE DELAY GATE
    // ─────────────────────────────────────────────────────────────────────
    // Audit (May 18): trades at k$60+ bled $5+ regardless of WR. The asymmetric
    //   payout makes expensive entries EV-negative even at 70%+ WR. Module A
    //   delays the lock when entry is expensive AND conviction sub-85% AND
    //   we have time to wait. Never SKIPs (existing coin-flip detector handles
    //   that). Just waits for one of: cheaper price, stronger conviction, or
    //   the 180s-remaining deadline that forces a lock.
    //
    // Feature flag: USE_V104_1_DELAY_GATE — set false for instant rollback.
    // Telemetry: _v10_4_1_delayed, _v10_4_1_delayTicks, _v10_4_1_lockTrigger
    if(USE_V104_1_DELAY_GATE&&isCall&&!(hardForceRef.current>0&&(Date.now()-hardForceRef.current)<5000)){
      const _kNow=typeof kalshiYesPrice!=='undefined'&&kalshiYesPrice!=null?Number(kalshiYesPrice):null;
      const _convNow=tc.confidence||0;
      const _dirNow=tc.call;
      // V10.7.70: Time-aware abort guard. V10.7.75: regime-specific cooldown.
      //   RANGE-CHOP: 5min (Kalshi oscillates, need time to settle)
      //   TRENDING: 8min (trend holds → wait longer before re-engaging)
      //   SHORT SQUEEZE: 2min (squeeze resolves fast → re-engage sooner)
      //   HIGH VOL CHOP: 3min (volatile but directional clears faster)
      const _curWid=computeWindowId(windowType);
      const _now5=Date.now();
      const _abortRegime=analysis?.regime||'RANGE-CHOP';
      const _cooldownMs=_abortRegime==='TRENDING UP'||_abortRegime==='TRENDING DOWN'||_abortRegime==='STRONG TREND'?480000
        :_abortRegime==='SHORT SQUEEZE'||_abortRegime==='LONG SQUEEZE'?120000
        :_abortRegime==='COMPRESSING'?240000
        :_abortRegime==='HIGH VOL CHOP'?180000
        :300000; // RANGE-CHOP: 5min default
      const _isNewWindow=_v104_1_abortCountRef.current.windowId!==_curWid;
      const _cooldownElapsed=(_now5-(_v104_1_abortCountRef.current.lastAbortAt||0))>_cooldownMs;
      if(_isNewWindow||_cooldownElapsed){
        _v104_1_abortCountRef.current={windowId:_curWid,count:0,lastAbortAt:0};
      }
      const MAX_ABORTS_PER_BURST=3;
      if(_v104_1_abortCountRef.current.count>=MAX_ABORTS_PER_BURST){
        const remainingMs=Math.max(0,_cooldownMs-(_now5-(_v104_1_abortCountRef.current.lastAbortAt||0)));
        _noteGateHold('adverse-kalshi-cooldown',`Adverse Kalshi cooldown: ${_v104_1_abortCountRef.current.count} aborts; ${Math.ceil(remainingMs/1000)}s remaining.`,
          {scope:'window',abortCount:_v104_1_abortCountRef.current.count,maxAborts:MAX_ABORTS_PER_BURST,
            cooldownMs:_cooldownMs,remainingMs,regime:_abortRegime});
        return; // in cooldown — don't retry until cooldown passes or new window
      }
      // Kalshi price FROM TARA'S DIRECTION — if Tara says DOWN and Kalshi YES is 35,
      //   then betting DOWN costs ~$0.65 (100-35). So "expensive entry" for DOWN
      //   means kalshiYesPrice is LOW. Flip per direction.
      const _kForDir=_kNow!=null?(_dirNow==='UP'?_kNow:(100-_kNow)):null;
      const _secsLeft=timeState.minsRemaining*60+timeState.secsRemaining;
      // Trigger: expensive entry (≥50¢) + marginal conviction (<85%) + time to wait (>180s)
      // V10.6.3: effective threshold is V104_1_KALSHI_EXPENSIVE_THRESHOLD minus
      //   slippage because the displayed kalshiYesPrice will become MORE expensive
      //   at actual fill time. A trade showing k$49 will fill at k$50+.
      const _slippageAdjustedThreshold=V104_1_KALSHI_EXPENSIVE_THRESHOLD-V106_3_AVG_SLIPPAGE_CENTS;
      const _expensiveEntry=_kForDir!=null&&_kForDir>=_slippageAdjustedThreshold;
      const _marginalConv=_convNow<V104_1_CONVICTION_STRONG_THRESHOLD;
      const _hasTimeToWait=_secsLeft>V104_1_DEADLINE_SECONDS_LEFT;
      // ═══════════════════════════════════════════════════════════════════
      // V11.2 DYNAMIC TIMING CONTROLLER
      //   Philosophy: call most windows, minimal sit-outs, win through TIMING.
      //   Early (first ~2m): lock ONLY on strong conviction AND good odds.
      //   Middle: patient — wait, let tape/momentum clarify.
      //   Late (<3m): lock when direction clear, accept up to ~72c.
      //   Final (<75s): accept up to ~80c if price clearly swung our way.
      //   Replaces the old expensive&&marginal&&time wait condition with a
      //   phase-aware controller. The hard deadline below still forces a lock.
      const _winLenSec112=windowType==='15m'?900:300;
      // The authoritative deadline above ends automatic 15m Calls at 420s.
      // The inherited late/final phases below are reachable only for 5m Calls;
      // they must never be used as a promised late rescue for 15m windows.
      const _elapsed112=_winLenSec112-_secsLeft;
      const _isEarly112=_secsLeft>(_winLenSec112-120);   // first 2 min
      const _isLate112=_secsLeft<=180;                    // last 3 min
      const _isFinal112=_secsLeft<=75;                    // last ~75s
      // Conviction bands (confidence is Tara's fused tape+momentum+posterior read)
      const _convStrong112=_convNow>=75;
      const _convClear112=_convNow>=62;   // 'momentum clear enough to act'
      // V13.4.123 FIX: user intent clarified -- 'decent odds, not long shots' meant
      //   'lock the good-enough read early,' not 'require near-certainty before acting.'
      //   Early and Mid phases below required _convStrong112 (75%) to lock at all --
      //   Late only required 62%. That's backwards from what was asked: a genuine 65-74%
      //   read (decent, not a coin-flip) was structurally FORCED to wait through the
      //   first ~12 minutes of a 15m window and only became lockable once the window
      //   had already drifted into its final 3 minutes -- the exact late-chase risk the
      //   user was trying to avoid in the first place. _convDecent112 lets early/mid
      //   lock on the same real-conviction bar as late, instead of demanding near-
      //   certainty just because there's still time on the clock.
      const _convDecent112=_convNow>=65;
      // V13.4.122 FIX: this used to escalate the allowed price as the window closed
      //   (63/68/72/80c by phase) -- but the universal entry-cost guard in
      //   _logSnapshotEntry (made genuinely universal in V13.4.121) enforces a SINGLE
      //   flat band (default 30-60c) with no phase exception. Real data the next
      //   morning: 5 of 20 windows had this controller approve a 61-65c lock at 72-75%
      //   conviction, only to have the guard immediately veto it to SIT_OUT -- two
      //   mechanisms fighting, 90% sitout rate, zero trades where there should have
      //   been real ones. Reading the SAME dial the guard uses removes the
      //   contradiction: if this controller says lock, the guard will agree, every
      //   time. Also directly matches the user's own ask (no late-window price
      //   creep, no chasing an already-decided side into a reversal).
      // V14.4.6: the intentionally separate44-77c Call band also governs
      // timing. Legacy EV filters may be off without disabling this user rule.
      const _v13422_bandMax=Math.min(getEntryMaxCost(),readCallCostBand(localStorage).maxCost);
      const _oddsCeil112=_v13422_bandMax;
      const _oddsOk112=_kForDir==null||_kForDir<=_oddsCeil112;
      let _v112Wait=false,_v112Timing='now',_v112Why='';
      if(_isEarly112){
        // Early: discipline — strong conviction AND good odds, else be patient.
        if(_convDecent112&&_oddsOk112){_v112Timing='early';_v112Why=`Early lock: ${_convNow.toFixed(0)}% conviction at ${_kForDir!=null?_kForDir.toFixed(0)+'c':'good'} odds`;}
        else{_v112Wait=true;_v112Timing='wait';_v112Why=_convDecent112?`Patient: conviction decent but ${_kForDir!=null?_kForDir.toFixed(0)+'c':''} odds rich early — wait for price`:`Patient: ${_convNow.toFixed(0)}% conviction building — let price action decide`;}
      }else if(_isLate112){
        // Late: lock when direction is clear; accept higher odds. Only a true
        //   coin-flip into the final seconds is left to park (downstream sit-out).
        if(_convClear112&&_oddsOk112){_v112Timing=_isFinal112?'late':'now';_v112Why=`${_isFinal112?'Final':'Late'} lock: ${_convNow.toFixed(0)}% conviction, direction clear at ${_kForDir!=null?_kForDir.toFixed(0)+'c':'good'} odds`;}
        else if(!_convClear112&&_isFinal112){_v112Wait=true;_v112Timing='wait';_v112Why=`V13.4.121: genuine coin-flip into final ${_secsLeft}s (${_convNow.toFixed(0)}% conviction) — sitting out rather than force-locking a marginal read`;}
        else{_v112Wait=true;_v112Timing='wait';_v112Why=`Patient (late): ${_convNow.toFixed(0)}% conviction, waiting for a clear swing`;}
      }else{
        // Middle: patient default. Lock only on strong conviction + acceptable odds.
        if(_convDecent112&&_oddsOk112){_v112Timing='now';_v112Why=`Mid lock: ${_convNow.toFixed(0)}% conviction at ${_kForDir!=null?_kForDir.toFixed(0)+'c':'good'} odds`;}
        else{_v112Wait=true;_v112Timing='wait';_v112Why=`Patient: letting tape/momentum decide (${_convNow.toFixed(0)}% conviction${_kForDir!=null&&!_oddsOk112?', '+_kForDir.toFixed(0)+'c rich':''})`;}
      }
      // Hard deadline always wins — never wait past it, UNLESS the read is a
      //   genuine coin-flip (weak conviction). V13.4.121: forcing a lock on a coin-flip
      //   at the deadline is exactly the time-cap-commit pattern that bled -457c over 84
      //   trades in July 2026 archive data despite a 60.7% win rate -- many small wins,
      //   a few expensive losses. Real conviction still always locks by the deadline;
      //   a coin-flip now resolves as a genuine sit-out instead.
      if(!_hasTimeToWait){
        if(_convClear112){
          _v112Wait=false;
          if(_v112Timing==='wait'){_v112Timing='now';_v112Why=`Deadline lock (${_secsLeft}s left): committing at ${_convNow.toFixed(0)}% conviction`;}
        }else{
          _v112Wait=true;_v112Timing='wait';_v112Why=`V13.4.121: deadline reached (${_secsLeft}s left) with only ${_convNow.toFixed(0)}% conviction — sitting out rather than force-locking a coin flip`;
        }
      }
      // Expose live timing recommendation on taraCall for the card (during wait).
      try{tc._v112Timing=_v112Timing;tc._v112Why=_v112Why;tc._v112OddsCeil=_oddsCeil112;tc._v112KForDir=_kForDir;}catch(_){}
      _v112Live={timing:_v112Timing,why:_v112Why,oddsCeil:_oddsCeil112,kForDir:_kForDir,at:Date.now()};
      // V13.4.64: OPPORTUNITY TRACE. Per window, best confidence while entry was actionable
      //   (35-70c) and whether the timing engine ever said now early with time left. Answers
      //   was there a lockable early moment we sat out. Additive, changes no behavior.
      try{
        const _oppWid=computeWindowId(windowType);
        if(!_v1364_oppRef.current||_v1364_oppRef.current.windowId!==_oppWid){
          _v1364_oppRef.current={windowId:_oppWid,peakConv:-1,peakEntry:null,peakSecsLeft:null,nowCount:0,firstNowConv:null,firstNowEntry:null,firstNowSecsLeft:null};
        }
        const _opp=_v1364_oppRef.current;
        const _oppActionable=_kForDir!=null&&_kForDir>=35&&_kForDir<=70;
        if(_oppActionable&&typeof _convNow==='number'&&_convNow>_opp.peakConv){
          _opp.peakConv=_convNow;_opp.peakEntry=Math.round(_kForDir);_opp.peakSecsLeft=Math.round(_secsLeft);
        }
        if(_v112Timing==='now'&&_oppActionable&&_secsLeft>=120){
          _opp.nowCount++;
          if(_opp.firstNowConv==null){_opp.firstNowConv=Math.round(_convNow);_opp.firstNowEntry=Math.round(_kForDir);_opp.firstNowSecsLeft=Math.round(_secsLeft);}
        }
      }catch(_){}
      if(_v112Wait){
        if(_flatGapWatchRef.current)_flatGapWatchRef.current.earlyConfirmation=null;
        _noteGateHold(_convNow<(_isLate112?62:65)?'timing-confidence':!_oddsOk112?'timing-price':'timing-wait',_v112Why,
          {controller:'v112',timing:_v112Timing,phase:_isEarly112?'early':_isLate112?'late':'mid',
            confidence:_convNow,requiredConfidence:_isLate112?62:65,
            directionalPrice:_kForDir,maxDirectionalPrice:_oddsCeil112,oddsOk:_oddsOk112,
            secondsLeft:_secsLeft,cutoff:callLockDeadline(windowType)});
        // Track delay duration per window
        if(!_v104_1_delayStateRef.current||_v104_1_delayStateRef.current.windowId!==computeWindowId(windowType)){
          _v104_1_delayStateRef.current={windowId:computeWindowId(windowType),ticks:0,firstSeenAt:Date.now(),firstKForDir:_kForDir,firstConv:_convNow,firstDir:_dirNow};
          // V10.7.51: stamp lifecycle when delay state opens. Helps diagnose "Tara
          //   computed but never logged" — if delay opens but never resolves to a
          //   lock or abort log, we see exactly where things stalled.
          try{
            const _lcWid=computeWindowId(windowType);
            const _lcAsset=currentAssetRef.current||currentAsset||'BTC';
            _lifecycleRecordSnapshot(_lcWid,windowType,_lcAsset,{call:_dirNow,direction:_dirNow,tier:'v10.4.1-delay-opened',confidence:_convNow},'v10.4.1-delay-open');
          }catch(_){}
        }
        _v104_1_delayStateRef.current.ticks++;
        // Don't lock this tick — let conditions evolve. Re-evaluated next tick.
        return;
      }
      // The timing controller now permits progress. Do not leave its previous
      // wait reason masking a later sample/commit gate in this same tick.
      if(callGateHoldRef.current?.windowId===computeWindowId(windowType)&&
         callGateHoldRef.current.code?.startsWith('timing-')){
        callGateHoldRef.current=null;
        tc.blockedDecision=currentCallBlockerRef.current=describeCallBlocker({call:tc,readiness:startupReadiness,
          hold:callCommitHoldRef.current,samples:taraCallSampleRef.current,quote:_kalshiQuote,windowId:computeWindowId(windowType),
          asset:currentAssetRef.current||'BTC',now:Date.now()});
      }
      _traceDecision('timing-approved',_v112Why,{controller:'v112',timing:_v112Timing,confidence:_convNow,
        requiredConfidence:_isLate112?62:65,directionalPrice:_kForDir,maxDirectionalPrice:_oddsCeil112});
      // Timing permits progress, not necessarily a lock — later checks still apply.
      if(_v104_1_delayStateRef.current&&_v104_1_delayStateRef.current.windowId===computeWindowId(windowType)){
        let _trigger='conditions-improved';
        if(!_hasTimeToWait)_trigger='deadline';
        else if(!_expensiveEntry)_trigger='price-improved';
        else if(!_marginalConv)_trigger='conv-strengthened';
        // V10.7.48 — ADVERSE-KALSHI GUARD.
        //   When trigger is 'price-improved', the "improvement" means kForDir has
        //   FALLEN below the expensive threshold. If it fell because Kalshi YES
        //   moved AGAINST our direction (i.e., market consensus turned opposite
        //   while we waited), then this "improvement" is adverse selection — we're
        //   locking into a position the market just priced as worse.
        //
        //   Audit data (May 21 5-loss streak):
        //     19:16 DOWN: firstKForDir 54 → 52 (drop 2pts)  — borderline, allow
        //     19:31 DOWN: firstKForDir 56 → 44 (drop 12pts) — ADVERSE — should abort
        //     19:46 DOWN: firstKForDir 55 → 46 (drop 9pts)  — ADVERSE — should abort
        //     20:01 DOWN: firstKForDir 60 → 38 (drop 22pts) — ADVERSE — should abort
        //   All 3 adverse cases lost. The "cheap" DOWN entries happened because
        //   market got more bullish during the wait. Locking DOWN at the exact
        //   moment consensus turned UP = worst-case adverse selection.
        //
        //   Fix: if drop ≥ 8pts AND trigger is 'price-improved', skip the lock.
        //   The window keeps developing. Either signals stabilize and re-trigger,
        //   or window closes without trade.
        const _v1074_8_firstKForDir=_v104_1_delayStateRef.current.firstKForDir;
        const _v1074_8_drop=(typeof _v1074_8_firstKForDir==='number'&&typeof _kForDir==='number')
          ?(_v1074_8_firstKForDir-_kForDir):0;
        // V10.7.84: Direction-flip abort REMOVED.
        //   Audit 2026-06-03: 157/166 adverse aborts were direction-flip events, all caused
        //   by Kalshi bouncing 8-20pts during the patient-wait delay — normal spread noise in
        //   a 50/50 market. Tara was right on direction but the flip abort chased the noise,
        //   locked the OPPOSITE direction, and that reversed. Every window was looping 3-8x.
        //   Direction flips during delay = Kalshi noise. Only abort on a SUSTAINED large drop.
        // V10.7.84: Raise drop threshold 8 -> 20pts and require >=30s in delay state.
        //   8-12pt drops are normal Kalshi spread movement. 20+ pts sustained = real signal
        //   that market consensus moved hard against us. 30s minimum prevents opening-tick
        //   noise from aborting before we've had time to observe real movement.
        const _v1074_8_delayMs=Date.now()-_v104_1_delayStateRef.current.firstSeenAt;
        const _v1074_8_isAdverse=_trigger==='price-improved'&&_v1074_8_drop>=20&&_v1074_8_delayMs>=30000;
        if(_v1074_8_isAdverse){
          // Audit log the abort, clear delay state, then return WITHOUT locking.
          _auditLogEvent('v10_7_48_adverse_kalshi_abort',{
            windowId:computeWindowId(windowType),
            firstDir:_v104_1_delayStateRef.current.firstDir,
            currentDir:_dirNow,
            firstKForDir:_v1074_8_firstKForDir,
            finalKForDir:_kForDir,
            kalshiDrop:_v1074_8_drop,
            dirFlipped:false,
            delaySeconds:Math.round(_v1074_8_delayMs/1000),
            reason:'kalshi-moved-against-us',
          });
          // V10.7.51: lifecycle telemetry + Memory entry for transparency.
          //   Was: abort returned silently, Memory had no record but Discord still
          //   showed "LOCKED UP" from a later lock attempt in the same window. User
          //   couldn't tell whether trade happened or didn't.
          //   Now: abort writes a sit-out entry to Memory with the abort reasoning.
          try{
            const _lcWid=computeWindowId(windowType);
            const _lcAsset=currentAssetRef.current||currentAsset||'BTC';
            _lifecycleRecordSnapshot(_lcWid,windowType,_lcAsset,{call:'SIT_OUT',direction:_v104_1_delayStateRef.current.firstDir,tier:'adverse-kalshi-abort',confidence:_convNow},'v10.7.48-abort');
          }catch(_){}
          try{
            const _abortSnap={
              call:'SIT_OUT',direction:'SIT_OUT',
              confidence:_convNow,
              reason:`Adverse Kalshi: kForDir dropped ${_v1074_8_drop}pts (${_v1074_8_firstKForDir}->${_kForDir}) sustained >=20pts after >=30s patient wait — abort (V10.7.84)`,
              atSecondsLeft:timeState.minsRemaining*60+timeState.secsRemaining,
              atPosterior:analysis?.rawProbAbove,
              kalshiAtLock:_kNow,
              locked:true,earlyLock:false,
              isConfluent:false,isSuperConfluent:false,isRisingConfluence:false,isTapeLed:false,isStructuralLed:false,
              samples:0,needSamples:0,
              tier:'adverse-kalshi-abort',
              session:(typeof getMarketSessions==='function'?getMarketSessions():{}).dominant||'UNKNOWN',
              regime:analysis?.regime||'',
              qScore:Math.round(_qFast||0),
              fgt:analysis?.mtfAlignment,
              // V10.7.51: lean direction so UI can show what would have happened
              leanDir:_v104_1_delayStateRef.current.firstDir,
              leanPct:_convNow,
              _v10_7_51_abortType:'kalshi-adverse',
              _v10_7_51_firstDir:_v104_1_delayStateRef.current.firstDir,
              _v10_7_51_currentDir:_dirNow,
              _v10_7_51_firstKForDir:_v1074_8_firstKForDir,
              _v10_7_51_finalKForDir:_kForDir,
              _v10_7_51_kalshiDrop:_v1074_8_drop,
              _v10_7_51_delaySeconds:Math.round(_v1074_8_delayMs/1000),
            };
            _logSnapshotEntry(_abortSnap);
          }catch(e){
            try{console.error('[V10.7.51] abort log failed:',e);}catch(_){}
          }
          try{console.warn('[V10.7.48] Adverse Kalshi abort:',{dropPts:_v1074_8_drop,dirFlipped:false,firstDir:_v104_1_delayStateRef.current?.firstDir,currentDir:_dirNow});}catch(_){}
          // V10.7.70: increment abort count + timestamp for cooldown logic
          _v104_1_abortCountRef.current.count=(_v104_1_abortCountRef.current.count||0)+1;
          _v104_1_abortCountRef.current.lastAbortAt=Date.now();
          _v104_1_delayStateRef.current=null; // clear state — let window keep developing
          _noteGateHold('adverse-kalshi-abort',`Adverse Kalshi: directional price dropped ${_v1074_8_drop}pts (${_v1074_8_firstKForDir}->${_kForDir}) during ${Math.round(_v1074_8_delayMs/1000)}s patient wait.`,
            {confidence:_convNow,firstDirectionalPrice:_v1074_8_firstKForDir,directionalPrice:_kForDir,
              drop:_v1074_8_drop,requiredDrop:20,waitMs:_v1074_8_delayMs,requiredWaitMs:30000,
              abortCount:_v104_1_abortCountRef.current.count});
          return; // skip lock this tick
        }
        _v104_1_pendingDelayStampRef.current={
          delayed:true,
          delayTicks:_v104_1_delayStateRef.current.ticks,
          delaySeconds:Math.round((Date.now()-_v104_1_delayStateRef.current.firstSeenAt)/1000),
          firstKForDir:_v104_1_delayStateRef.current.firstKForDir,
          firstConv:_v104_1_delayStateRef.current.firstConv,
          finalKForDir:_kForDir,
          finalConv:_convNow,
          lockTrigger:_trigger,
        };
        _v104_1_delayStateRef.current=null; // clear for next window
      }
    }
    // ─────────────────────────────────────────────────────────────────────

    // V6.2.6: HARD FORCE early-path. If user pressed Override Gates, commit immediately
    //   regardless of tc.call state. Direction picked from posterior — whichever side has
    //   the most confidence right now. Bypasses ALL safeguards. This is the explicit
    //   "I see something Tara doesn't" override.
    const _hardForceOnEntry=hardForceRef.current>0&&(Date.now()-hardForceRef.current)<5000;    if(_hardForceOnEntry){
      const _post=analysis?.rawProbAbove??50;
      const _forceDir=_post>=50?'UP':'DOWN';
      const _forceSession=(typeof getMarketSessions==='function'?getMarketSessions():{})?.dominant||'UNKNOWN';
      const _forceK=typeof kalshiYesPrice!=='undefined'&&kalshiYesPrice!=null?Number(kalshiYesPrice):null;
      taraCallSnapshotRef.current={
        call:_forceDir,direction:_forceDir,
        confidence:Math.max(50,Math.round(_forceDir==='UP'?_post:(100-_post))),
        reason:`User force-override: gates bypassed, ${_forceDir} based on ${_forceDir==='UP'?Math.round(_post):Math.round(100-_post)}% posterior`,
        atSecondsLeft:timeState.minsRemaining*60+timeState.secsRemaining,
        atPosterior:_post,
        kalshiAtLock:_forceK,
        locked:true,earlyLock:true,
        isConfluent:false,isSuperConfluent:false,isRisingConfluence:false,isTapeLed:false,isStructuralLed:false,
        isUserForced:true,
        samples:0,needSamples:0,
        tier:'user-forced',
        session:_forceSession,
        regime:analysis?.regime||'',
        qScore:Math.round(qualityGate?.score||0),qScoreV2:Math.round(qualityGateV2?.score||0),qScoreV2Components:qualityGateV2?.components||null,
        fgt:analysis?.mtfAlignment,
        _committedAt:Date.now(), // V9.1.3: stamp at creation so first-write-wins works pre-persist
      };
      hardForceRef.current=0; // consume the force press
      _persistLock();
      // Sound
      try{playAlert(_forceDir==='UP'?'lock-up':'lock-down');}catch(_){}
      // Log entry
      const _wid=computeWindowId(windowType);
      const _entry={
        id:Date.now(),time:Date.now(),windowType,windowId:_wid,
        // V6.4: explicit timestamps. lockedAt = wall-clock at lock; windowOpenedAt = when window started.
        //   secondsIntoWindow = how long Tara took before locking (0 = instant, e.g. force).
        lockedAt:Date.now(),
        windowOpenedAt:windowOpenTimeRef.current||null,
        secondsIntoWindow:windowOpenTimeRef.current?Math.round((Date.now()-windowOpenTimeRef.current)/1000):null,
        // V7.10.6: market phase stamp
        phase:(typeof getPhaseKey==='function'?getPhaseKey(new Date().getUTCHours(),new Date().getUTCMinutes()):null),
        regime:analysis?.regime||'',dir:_forceDir,confidence:taraCallSnapshotRef.current.confidence,
        posterior:_post,qScore:Math.round(qualityGate?.score||0),qScoreV2:Math.round(qualityGateV2?.score||0),qScoreV2Components:qualityGateV2?.components||null,fgt:analysis?.mtfAlignment,
        tier:'user-forced',session:_forceSession,kalshiAtLock:_forceK,
        // V9.9.0: reversal risk on forced locks too — user may want awareness even
        //   when overriding. tier='user-forced' is its own bucket so structural-tier
        //   weight doesn't apply here, but regime/whale/FGT signals still fire.
        reversalRisk:computeReversalRisk({
          tier:'user-forced',
          regime:analysis?.regime||'',
          dir:_forceDir,
          qScore:Math.round(qualityGate?.score||0),qScoreV2:Math.round(qualityGateV2?.score||0),qScoreV2Components:qualityGateV2?.components||null,
          mtfAlignment:analysis?.mtfAlignment,
          secondsIntoWindow:windowOpenTimeRef.current?Math.round((Date.now()-windowOpenTimeRef.current)/1000):null,
          whaleLog,
          dayContext,
        }),
        // V9.7.6: dial setting at force — same telemetry as for engine locks
        dialAtLock:50, // V10.9.7: dial removed — neutral value kept for CSV/export schema stability
        isUserForced:true,reason:taraCallSnapshotRef.current.reason,
        // V7.0.7: tag asset for user-forced entries (was missing — defaulted to BTC on display).
        asset:currentAssetRef.current||currentAsset||'BTC',
        // V10.2.18 — Phase 4 + sessionTier stamps on user-force commit. Even
        //   user-force trades benefit from the audit data — they happen in a
        //   real session × tier context, and Phase 4 may have evaluated. Stamps
        //   are null-safe; if Phase 4 hadn't run yet for this window, decision
        //   is null which the audit hooks handle correctly.
        tradeTimingDecision:_phase4DecisionRef.current?_phase4DecisionRef.current.decision:null,
        tradeTimingScore:_phase4DecisionRef.current?_phase4DecisionRef.current.score:null,
        tradeTimingReason:_phase4DecisionRef.current?_phase4DecisionRef.current.reason:null,
        tradeTimingMode:_phase4DecisionRef.current?_phase4DecisionRef.current.mode:null,
        // V10.2.23 — per-factor impact breakdown for offline calibration
        tradeTimingFactors:_phase4DecisionRef.current?_phase4DecisionRef.current.factors||null:null,
        tradeTimingFactorCount:_phase4DecisionRef.current?_phase4DecisionRef.current.factorCount||0:0,
        sessionTierMode:taraCall?._ctx?._sessionTierMode||null,
        sessionTierMult:taraCall?._ctx?._sessionTierMult!=null?taraCall._ctx._sessionTierMult:null,
        sessionTierApplied:taraCall?._ctx?._sessionTierApplied===true,
        // V9.10.9: stamp device label so personal scorecard filters to this device only.
        // V9.10.10: stamp pattern detection state at lock time.
        patternsAtLock:analysis?.patternsV9_10_10||null,
        // V9.11.0: also stamp HTF-aware pattern data + futures data on entries.
        //   patternsAtLock above remains for backward compat; new analysis should
        //   read patternsAtLockV9_11_0 (per-TF + confluence) and futuresAtLock.
        patternsAtLockV9_11_0:analysis?.patternsV9_11_0||null,
        // V13.4.243: 3-scalar mirror of the line above. The fat object is
        //   stripped by the deep-cache minify, so the pattern engine's own
        //   telemetry survives on well under half the log. These three are on
        //   the _MINI_KEEP list and cost ~15 bytes.
        patDirScore:(()=>{const _v=Number(analysis?.patternsV9_11_0?.directionalScore);return Number.isFinite(_v)?Math.round(_v):null;})(),
        patConfluence:(()=>{const _v=Number(analysis?.patternsV9_11_0?.confluence);return Number.isFinite(_v)?_v:null;})(),
        patDominantDir:analysis?.patternsV9_11_0?.dominantDirection||null,
          // V9.15: stamp K-nearest historical analogs at lock time. Stored compactly
          //   (top 3 entries + summary) to keep log size manageable. The full 12-entry
          //   list is exposed via the live `analysis.historicalAnalogs` for UI only.
          historicalAnalogsAtLock:(()=>{
            const _h=analysis?.historicalAnalogs;
            if(!_h||!_h.valid)return null;
            return{
              analogWR:_h.analogWR,
              analogN:_h.analogN,
              avgSimilarity:_h.avgSimilarity,
              confidence:_h.analogConfidence,
              top3:(_h.topK||[]).slice(0,3).map(t=>({
                id:t.id,similarity:t.similarity,result:t.result,
                regime:t.regime,session:t.session,dir:t.dir,
              })),
            };
          })(),
        futuresAtLock:analysis?.futuresV9_11_0||null,
          // V10.2.36-40 — Regime detection improvement stamps persisted to call log.
          _v10_2_36_rangeOverrideFired:analysis?._v10_2_36_rangeOverrideFired===true,
          _v10_2_36_rangeOverrideAvgBps:analysis?._v10_2_36_rangeOverrideAvgBps??null,
          _v10_2_37_quietOverrideFired:analysis?._v10_2_37_quietOverrideFired===true,
          _v10_2_37_quietOverrideRatio:analysis?._v10_2_37_quietOverrideRatio??null,
          _v10_2_37_quietRealizedBps:analysis?._v10_2_37_quietRealizedBps??null,
          _v10_2_37_quietExpectedBps:analysis?._v10_2_37_quietExpectedBps??null,
          _v10_2_37_mtfUpAligned:analysis?._v10_2_37_mtfUpAligned===true,
          _v10_2_37_mtfDnAligned:analysis?._v10_2_37_mtfDnAligned===true,
          _v10_2_38_srRangeDetected:analysis?._v10_2_38_srRangeDetected===true,
          _v10_2_38_srRangeBps:analysis?._v10_2_38_srRangeBps??null,
          _v10_2_38_srTouchesHigh:analysis?._v10_2_38_srTouchesHigh??0,
          _v10_2_38_srTouchesLow:analysis?._v10_2_38_srTouchesLow??0,
          _v10_2_39_htfFlatOverride:analysis?._v10_2_39_htfFlatOverride===true,
          _v10_2_39_htfVetoFired:analysis?._v10_2_39_htfVetoFired===true,
          _v10_2_39_htf1hDriftBps:analysis?._v10_2_39_htf1hDriftBps??null,
          _v10_2_39_htf4hDriftBps:analysis?._v10_2_39_htf4hDriftBps??null,
          _v10_2_39_htf1hLabel:analysis?._v10_2_39_htf1hLabel||null,
          _v10_2_39_htf4hLabel:analysis?._v10_2_39_htf4hLabel||null,
          _v10_2_40_regimeProbs:analysis?._v10_2_40_regimeProbs||null,
          _v10_2_40_regimeConfidence:analysis?._v10_2_40_regimeConfidence??null,
          _v10_2_40_primaryRegime:analysis?._v10_2_40_primaryRegime||null,
          _v10_2_40_dampened:analysis?._v10_2_40_dampened===true,
          _v10_2_43_regimeMult:analysis?._v10_2_43_regimeMult??1.0,
          _v10_2_43_structOrig:analysis?._v10_2_43_structOrig??0,
          _v10_2_43_techOrig:analysis?._v10_2_43_techOrig??0,
          _v10_2_44_haircutApplied:analysis?._v10_2_44_haircutApplied||null,
          _v10_2_44_postBeforeHaircut:analysis?._v10_2_44_postBeforeHaircut??null,
          _v10_2_50_chopAmpDelta:analysis?._v10_2_50_chopAmpDelta??0,
          _v10_2_50_htfChopBoost:analysis?._v10_2_50_htfChopBoost||null,
          _v10_2_51_chopConfluence:analysis?._v10_2_51_chopConfluence||null,
          _v10_3_0_rangeData:analysis?._v10_3_0_rangeData||null,
          _v10_3_0_rangeFadeFired:analysis?._v10_3_0_rangeFadeFired||null,
          _v10_3_0_midRangeSitout:analysis?._v10_3_0_midRangeSitout===true,
          _v10_3_0_rangeBroken:analysis?._v10_3_0_rangeBroken===true,
          _v10_3_1_coinFlip:analysis?._v10_3_1_coinFlip||null,
          _v10_3_1_trendingUpPenalty:analysis?._v10_3_1_trendingUpPenalty||null,
          _v10_3_3_rangeHybrid:analysis?._v10_3_3_rangeHybrid||null,
          _v10_4_0_calibration:analysis?._v10_4_0_calibration||null,
          _v10_4_0_regimeCaps:analysis?._v10_4_0_regimeCaps||null,
          _v10_4_3_windowCtx:analysis?._v10_4_3_windowCtx||null,
          _v10_5_1_brti:brtiApprox?{current:brtiApprox.current,divBps:brtiApprox.divergenceBps,n:brtiApprox.samples60s,src:brtiApprox.sourceCount}:null, _diag_brtiRefLive:(brtiApproxRef&&brtiApproxRef.current)?{current:brtiApproxRef.current.current,divBps:brtiApproxRef.current.divergenceBps,n:brtiApproxRef.current.samples60s,src:brtiApproxRef.current.sourceCount}:null,
          _v10_6_2_dowHour:analysis?._v10_6_2_dowHour||null,
          _v10_7_4_flipped:taraCall?._v10_7_4_flipped?{originalDir:taraCall._v10_7_4_originalDir,votes:taraCall._v10_7_4_votes,reasons:taraCall._v10_7_4_voteReasons}:null,
          _v10_7_11_universalFlipped:taraCall?._v10_7_11_universalFlipped?{flipType:taraCall._v10_7_11_flipType,originalDir:taraCall._v10_7_11_originalDir,votes:taraCall._v10_7_11_votes,reasons:taraCall._v10_7_11_reasons,gapBps:taraCall._v10_7_11_gapBps,driftBps:taraCall._v10_7_11_driftBps,minsLeft:taraCall._v10_7_11_minsLeft}:null,
          _v10_7_19_balanced:taraCall?._v10_7_19_balanced?{downPct:taraCall._v10_7_19_downPct,originalDir:taraCall._v10_7_19_originalDir,originalConf:taraCall._v10_7_19_originalConf}:null,
          _v10_4_1_delayed:_v104_1_pendingDelayStampRef.current||null,
        result:null,
      };
      setTaraCallLog(prev=>{
        // V9.10.4: dedup includes asset so BTC + ETH writes for the same window don't
        //   clobber each other. Was missing asset → cross-asset writes at the same window
        //   slot silently dropped the second one.
        const _entryAsset=_entry.asset||'BTC';
        const _existing=prev.find(e=>e&&e.windowId===_wid&&e.windowType===windowType&&(e.asset||'BTC')===_entryAsset);
        if(_existing){
          _auditLogEvent('userForceCommit-dedup-blocked',{
            source:'userForceCommit',
            wid:_wid,
            asset:_entryAsset,
            windowType,
            newEntry:{dir:_entry.dir,tier:_entry.tier,confidence:_entry.confidence,time:_entry.time,isUserForced:true},
            existingEntry:{id:_existing.id,dir:_existing.dir,tier:_existing.tier,result:_existing.result,time:_existing.time},
          });
          return prev;
        }
        return [...prev,_entry].slice(-TARA_CALL_LOG_CAP);
      });
      return;
    }
    // V10.2.15 — ROLLOVER GRACE BLOCKS LIFECYCLE COMMITS.
    //   The engine IIFE (line 29722) correctly returns SIT_OUT for the first 5s
    //   after a new window starts ("observing fresh data"). BUT the lifecycle
    //   commit paths below read _commitDir from analysis.rawProbAbove DIRECTLY,
    //   not from tc.call. So they could fire snapshot commits during the grace
    //   period, producing the "LOCKED UP + observing fresh data (5s)" sticky
    //   state the user reported in V10.2.14.
    //
    //   Fix: respect the grace at the lifecycle level too. Hard-force above is
    //   intentionally exempted (user is explicitly overriding everything).
    //   All other auto-commit paths defer until grace expires.
    const _msSinceRolloverLC=Date.now()-(_rolloverGraceRef.current||0);
    if(_rolloverGraceRef.current>0&&_msSinceRolloverLC<5000){
      _noteGateHold('rollover-grace',`Observing the new window: ${Math.ceil((5000-_msSinceRolloverLC)/1000)}s grace remaining.`,
        {scope:'window',remainingMs:5000-_msSinceRolloverLC,requiredMs:5000});
      return; // grace active — let the engine's SIT_OUT render, no auto-commits
    }
    // Track sample stability — if direction flips, reset count
    let ref=taraCallSampleRef.current;
    if(!isCall){
      if(_flatGapWatchRef.current)_flatGapWatchRef.current.earlyConfirmation=null;
      _noteGateHold(tc.signalBlocker?.code||'signal',tc.reason||'Waiting for a qualifying signal.',tc.signalBlocker?.details||{});
      // V5.6.11 FIX: DO NOT reset accumulated samples on every gate-dip. Previously this
      //   wiped progress whenever conviction dipped below floor for even one tick — meaning
      //   if conviction wobbled around 10pt, samples never accumulated and Tara stayed in
      //   SCANNING forever. Now: preserve count. Conviction rebound resumes accumulation
      //   from where it left off. Only the rollover effect resets samples for a new window.
      // Endgame freeze still triggers a snapshot for SIT_OUT path
      if(analysis?.isSystemLocked){
        // V10.7.5 — DEAD-WINDOW-ONLY SITOUT RULE
        //   When system reaches end-of-window without a commit, check if window
        //   is actually dead. If not, force-commit using posterior direction.
        //   This catches the case where engine stayed in SCANNING/WATCHING forever.
        const _wa=analysis?.windowAmplitude;
        const _rangeBps=_wa?.rangeBps||0;
        const _isDeadWindow=_wa?.label!=='OPENING'&&_rangeBps<5;
        const _postNow=Number(analysis?.rawProbAbove)||50;
        if(!_isDeadWindow&&_postNow!==50){
          // V14.2: an absent quote is recoverable before the real deadline.
          // Keep sampling instead of freezing a false missing-price sit-out.
          const _lateQuote=inspectDecisionQuote(_kalshiQuote,{windowId:computeWindowId(windowType),asset:currentAssetRef.current||'BTC',now:Date.now()});
          if(!_lateQuote.ok&&(timeState.minsRemaining*60+timeState.secsRemaining)>LOCK_DEADLINE_SEC)return;
          // Force-commit instead of ambiguous SITOUT
          // V10.7.6 — check for reversal signals before committing
          const _v107_6=_v10_7_6_reversalCheck(_postNow,analysis);
          const _forceDir=_v107_6.dir;
          const _forceConf=_v107_6.conf;
          taraCallSnapshotRef.current={
            call:_forceDir,direction:_forceDir,confidence:_forceConf,
            reason:`Directional lock · ${_forceDir} ${(_forceConf-50).toFixed(0)}pt · late-window momentum (${_rangeBps.toFixed(0)}bps range)`,
            atSecondsLeft:timeState.minsRemaining*60+timeState.secsRemaining,
            atPosterior:_postNow,
            locked:true,earlyLock:false,
            _v10_7_5_forced:true,
            _v10_7_5_originalGuard:'isSystemLocked',
            _v10_7_6_reversalFlipped:_v107_6.flipped,
            _v10_7_6_revVotes:_v107_6.revVotes,
            _v10_7_6_revReasons:_v107_6.reasons,
            _v10_7_6_originalDir:_v107_6.flipped?_v107_6.originalDir:null,
            _v10_7_5_rangeBps:_rangeBps,
            tier:'late-window-forced',
            kalshiAtLock:_lateQuote.ok?_lateQuote.mid:null,
          };
          // Log the force-commit entry
          _logSnapshotEntry(taraCallSnapshotRef.current);
          _persistLock(); // persist the validated decision, never a pre-guard copy
          return;
        }
        // V13.4.326: this was the one sit-out branch in the whole commit
        //   effect that never routed through _applyNoSitout -- every sibling
        //   branch was rewired in V13.4.249/250 to convert like everything
        //   else not explicitly kept; this one was missed, making it an
        //   accidental, undocumented always-on gate (it also hardcoded the
        //   log entry's dir:'SIT_OUT' below regardless of outcome, so even
        //   wrapping the snapshot alone would have left the log entry lying
        //   about what actually happened).
        //   Can't just call _applyNoSitout here -- it's defined later in this
        //   same effect (const, so calling it this early is a temporal-dead-
        //   zone crash, not just a logic bug), and this branch's own early
        //   `return` means it can't be moved down to after that definition
        //   without risking the sample-accumulation logic between here and
        //   there. So: mirror its exact logic inline, using only the
        //   already-available top-level constants (_SITOUT_KEEP,
        //   NO_SITOUT_MODE, LOCK_DEADLINE_SEC) it itself relies on.
        let _sitFinal={
          call:'SIT_OUT',direction:null,confidence:tc.confidence,reason:tc.reason,
          atSecondsLeft:timeState.minsRemaining*60+timeState.secsRemaining,
          atPosterior:analysis.rawProbAbove,
          locked:true,
          earlyLock:false,
          tier:'sitout',
          isNoGo:true,
          noGoCategory:'sitout',
        };
        if(NO_SITOUT_MODE&&!_SITOUT_KEEP.has('sitout')&&
           (timeState.minsRemaining*60+timeState.secsRemaining)>LOCK_DEADLINE_SEC){
          const _sitPost=Number(analysis?.rawProbAbove);
          const _sitDir=Number.isFinite(_sitPost)?(_sitPost>=50?'UP':'DOWN'):null;
          if(_sitDir){
            _sitFinal={
              call:_sitDir,direction:_sitDir,dir:_sitDir,
              confidence:Math.round(_sitDir==='UP'?_sitPost:(100-_sitPost)),
              reason:tc.reason,
              atSecondsLeft:timeState.minsRemaining*60+timeState.secsRemaining,
              atPosterior:analysis.rawProbAbove,
              locked:true,earlyLock:false,
              wasOverriddenNoTrade:false,noGoCategory:null,isNoGo:false,
              tier:'no-sitout-commit',
              _noSitoutFrom:'sitout',
              caution:'Committed on the lean — sit-outs are off (was: sitout)',
            };
          }
        }
        taraCallSnapshotRef.current=_sitFinal;
        _persistLock(); // V5.6: cloud-save SIT_OUT commit
        // V5.6.1: log entry — committed SIT_OUT (or, post-conversion, a real commit)
        // V5.6.9: windowId for dedup
        const _sitWid=computeWindowId(windowType);
        const _sitSessionInfo=typeof getMarketSessions==='function'?getMarketSessions():null;
        const _sitSession=_sitSessionInfo?.dominant||analysis?.session?.name||'UNKNOWN';
        const _entry={
          id:Date.now(),time:Date.now(),windowType,
          windowId:_sitWid,
          regime:analysis?.regime||'',
          // V13.4.326: was hardcoded 'SIT_OUT'/tc.confidence regardless of
          //   what _applyNoSitout actually decided -- now reflects the real
          //   outcome. `call` added alongside `dir` for the same reason
          //   V10.8.6 added it elsewhere in this file: downstream analysis
          //   reads `call`, not just `dir`.
          call:_sitFinal.call,dir:_sitFinal.call,confidence:_sitFinal.confidence,
          posterior:analysis?.rawProbAbove,
          qScore:Math.round(qualityGate?.score||0),qScoreV2:Math.round(qualityGateV2?.score||0),qScoreV2Components:qualityGateV2?.components||null,
          fgt:analysis?.mtfAlignment,
          tier:_sitFinal.tier||'sitout',
          session:_sitSession,
          // V7.10.6: market phase stamp
          phase:(typeof getPhaseKey==='function'?getPhaseKey(new Date().getUTCHours(),new Date().getUTCMinutes()):null),
          reason:_sitFinal.reason,
          // V7.0.7: tag asset for SIT_OUT entries (was missing — defaulted to BTC on display).
          asset:currentAssetRef.current||currentAsset||'BTC',
          // V10.2.18 — Phase 4 + sessionTier stamps on SIT_OUT commit. SIT_OUT
          //   entries are valuable for shadow validation — they tell us which
          //   Phase 4 decisions correctly chose NOT to fire. Without these, we
          //   can't compute "abort decisions led to actual losses avoided" stats.
          tradeTimingDecision:_phase4DecisionRef.current?_phase4DecisionRef.current.decision:null,
          tradeTimingScore:_phase4DecisionRef.current?_phase4DecisionRef.current.score:null,
          tradeTimingReason:_phase4DecisionRef.current?_phase4DecisionRef.current.reason:null,
          tradeTimingMode:_phase4DecisionRef.current?_phase4DecisionRef.current.mode:null,
          // V10.2.23 — per-factor impact breakdown for offline calibration
          tradeTimingFactors:_phase4DecisionRef.current?_phase4DecisionRef.current.factors||null:null,
          tradeTimingFactorCount:_phase4DecisionRef.current?_phase4DecisionRef.current.factorCount||0:0,
          sessionTierMode:taraCall?._ctx?._sessionTierMode||null,
          sessionTierMult:taraCall?._ctx?._sessionTierMult!=null?taraCall._ctx._sessionTierMult:null,
          sessionTierApplied:taraCall?._ctx?._sessionTierApplied===true,
          // V9.10.9: stamp device label so personal scorecard filters to this device only.
          // V9.10.10: stamp pattern detection state at lock time.
          patternsAtLock:analysis?.patternsV9_10_10||null,
        // V9.11.0: also stamp HTF-aware pattern data + futures data on entries.
        //   patternsAtLock above remains for backward compat; new analysis should
        //   read patternsAtLockV9_11_0 (per-TF + confluence) and futuresAtLock.
        patternsAtLockV9_11_0:analysis?.patternsV9_11_0||null,
        // V13.4.243: 3-scalar mirror of the line above. The fat object is
        //   stripped by the deep-cache minify, so the pattern engine's own
        //   telemetry survives on well under half the log. These three are on
        //   the _MINI_KEEP list and cost ~15 bytes.
        patDirScore:(()=>{const _v=Number(analysis?.patternsV9_11_0?.directionalScore);return Number.isFinite(_v)?Math.round(_v):null;})(),
        patConfluence:(()=>{const _v=Number(analysis?.patternsV9_11_0?.confluence);return Number.isFinite(_v)?_v:null;})(),
        patDominantDir:analysis?.patternsV9_11_0?.dominantDirection||null,
          // V9.15: stamp K-nearest historical analogs at lock time. Stored compactly
          //   (top 3 entries + summary) to keep log size manageable. The full 12-entry
          //   list is exposed via the live `analysis.historicalAnalogs` for UI only.
          historicalAnalogsAtLock:(()=>{
            const _h=analysis?.historicalAnalogs;
            if(!_h||!_h.valid)return null;
            return{
              analogWR:_h.analogWR,
              analogN:_h.analogN,
              avgSimilarity:_h.avgSimilarity,
              confidence:_h.analogConfidence,
              top3:(_h.topK||[]).slice(0,3).map(t=>({
                id:t.id,similarity:t.similarity,result:t.result,
                regime:t.regime,session:t.session,dir:t.dir,
              })),
            };
          })(),
        futuresAtLock:analysis?.futuresV9_11_0||null,
          // V10.2.36-40 — Regime detection improvement stamps persisted to call log.
          _v10_2_36_rangeOverrideFired:analysis?._v10_2_36_rangeOverrideFired===true,
          _v10_2_36_rangeOverrideAvgBps:analysis?._v10_2_36_rangeOverrideAvgBps??null,
          _v10_2_37_quietOverrideFired:analysis?._v10_2_37_quietOverrideFired===true,
          _v10_2_37_quietOverrideRatio:analysis?._v10_2_37_quietOverrideRatio??null,
          _v10_2_37_quietRealizedBps:analysis?._v10_2_37_quietRealizedBps??null,
          _v10_2_37_quietExpectedBps:analysis?._v10_2_37_quietExpectedBps??null,
          _v10_2_37_mtfUpAligned:analysis?._v10_2_37_mtfUpAligned===true,
          _v10_2_37_mtfDnAligned:analysis?._v10_2_37_mtfDnAligned===true,
          _v10_2_38_srRangeDetected:analysis?._v10_2_38_srRangeDetected===true,
          _v10_2_38_srRangeBps:analysis?._v10_2_38_srRangeBps??null,
          _v10_2_38_srTouchesHigh:analysis?._v10_2_38_srTouchesHigh??0,
          _v10_2_38_srTouchesLow:analysis?._v10_2_38_srTouchesLow??0,
          _v10_2_39_htfFlatOverride:analysis?._v10_2_39_htfFlatOverride===true,
          _v10_2_39_htfVetoFired:analysis?._v10_2_39_htfVetoFired===true,
          _v10_2_39_htf1hDriftBps:analysis?._v10_2_39_htf1hDriftBps??null,
          _v10_2_39_htf4hDriftBps:analysis?._v10_2_39_htf4hDriftBps??null,
          _v10_2_39_htf1hLabel:analysis?._v10_2_39_htf1hLabel||null,
          _v10_2_39_htf4hLabel:analysis?._v10_2_39_htf4hLabel||null,
          _v10_2_40_regimeProbs:analysis?._v10_2_40_regimeProbs||null,
          _v10_2_40_regimeConfidence:analysis?._v10_2_40_regimeConfidence??null,
          _v10_2_40_primaryRegime:analysis?._v10_2_40_primaryRegime||null,
          _v10_2_40_dampened:analysis?._v10_2_40_dampened===true,
          _v10_2_43_regimeMult:analysis?._v10_2_43_regimeMult??1.0,
          _v10_2_43_structOrig:analysis?._v10_2_43_structOrig??0,
          _v10_2_43_techOrig:analysis?._v10_2_43_techOrig??0,
          _v10_2_44_haircutApplied:analysis?._v10_2_44_haircutApplied||null,
          _v10_2_44_postBeforeHaircut:analysis?._v10_2_44_postBeforeHaircut??null,
          _v10_2_50_chopAmpDelta:analysis?._v10_2_50_chopAmpDelta??0,
          _v10_2_50_htfChopBoost:analysis?._v10_2_50_htfChopBoost||null,
          _v10_2_51_chopConfluence:analysis?._v10_2_51_chopConfluence||null,
          _v10_3_0_rangeData:analysis?._v10_3_0_rangeData||null,
          _v10_3_0_rangeFadeFired:analysis?._v10_3_0_rangeFadeFired||null,
          _v10_3_0_midRangeSitout:analysis?._v10_3_0_midRangeSitout===true,
          _v10_3_0_rangeBroken:analysis?._v10_3_0_rangeBroken===true,
          _v10_3_1_coinFlip:analysis?._v10_3_1_coinFlip||null,
          _v10_3_1_trendingUpPenalty:analysis?._v10_3_1_trendingUpPenalty||null,
          _v10_3_3_rangeHybrid:analysis?._v10_3_3_rangeHybrid||null,
          _v10_4_0_calibration:analysis?._v10_4_0_calibration||null,
          _v10_4_0_regimeCaps:analysis?._v10_4_0_regimeCaps||null,
          _v10_4_3_windowCtx:analysis?._v10_4_3_windowCtx||null,
          _v10_5_1_brti:brtiApprox?{current:brtiApprox.current,divBps:brtiApprox.divergenceBps,n:brtiApprox.samples60s,src:brtiApprox.sourceCount}:null, _diag_brtiRefLive:(brtiApproxRef&&brtiApproxRef.current)?{current:brtiApproxRef.current.current,divBps:brtiApproxRef.current.divergenceBps,n:brtiApproxRef.current.samples60s,src:brtiApproxRef.current.sourceCount}:null,
          _v10_6_2_dowHour:analysis?._v10_6_2_dowHour||null,
          _v10_7_4_flipped:taraCall?._v10_7_4_flipped?{originalDir:taraCall._v10_7_4_originalDir,votes:taraCall._v10_7_4_votes,reasons:taraCall._v10_7_4_voteReasons}:null,
          _v10_7_11_universalFlipped:taraCall?._v10_7_11_universalFlipped?{flipType:taraCall._v10_7_11_flipType,originalDir:taraCall._v10_7_11_originalDir,votes:taraCall._v10_7_11_votes,reasons:taraCall._v10_7_11_reasons,gapBps:taraCall._v10_7_11_gapBps,driftBps:taraCall._v10_7_11_driftBps,minsLeft:taraCall._v10_7_11_minsLeft}:null,
          _v10_7_19_balanced:taraCall?._v10_7_19_balanced?{downPct:taraCall._v10_7_19_downPct,originalDir:taraCall._v10_7_19_originalDir,originalConf:taraCall._v10_7_19_originalConf}:null,
          _v10_4_1_delayed:_v104_1_pendingDelayStampRef.current||null,
          result:null, // populated at rollover
        };
        setTaraCallLog(prev=>{
          // V9.10.4: per-asset dedup (see V9.10.4 comment above)
          const _sitAsset=_entry.asset||'BTC';
          if(prev.some(e=>e&&e.windowId===_sitWid&&e.windowType===windowType&&(e.asset||'BTC')===_sitAsset))return prev;
          return [...prev,_entry].slice(-TARA_CALL_LOG_CAP);
        });
      }
      return;
    }
    if(ref.dir!==null&&ref.dir!==tc.call){
      // A provisional lean is not a committed Call. Re-form the new qualified
      // candidate with fresh samples; never borrow the old direction's progress.
      // The snapshot guard at effect entry makes committed Calls immutable.
      _traceDecision('formation-direction-reset',`Reforming ${tc.call}; discarded ${ref.count} ${ref.dir} samples.`,
        {previousDirection:ref.dir,previousSamples:ref.count,newDirection:tc.call});
      ref=taraCallSampleRef.current={dir:null,count:0};
    }
    const _formation=advanceCallFormation(taraCallSampleRef.current,{direction:tc.call,
      windowId:computeWindowId(windowType),asset:currentAssetRef.current||'BTC',source:priceSourceRef.current,
      spotAt:lastPriceSourceRef.current?.time,quote:_kalshiQuote,probabilityUp:analysis?.rawProbAbove,now:Date.now()});
    taraCallSampleRef.current=_formation.state;
    if(!_formation.fresh){
      const _earlySpotAt=Number(lastPriceSourceRef.current?.time);
      if(_flatGapWatchRef.current&&(!inspectDecisionQuote(_kalshiQuote,{windowId:computeWindowId(windowType),asset:currentAssetRef.current||'BTC',now:Date.now()}).ok||
        !Number.isFinite(_earlySpotAt)||Date.now()-_earlySpotAt>15000||_earlySpotAt>Date.now()+2000)){
        _flatGapWatchRef.current.earlyConfirmation=null;
      }
      _noteGateHold('sample-freshness',_formation.reason,{samples:_formation.state.count,scope:'window'});
      return;
    }
    const samples=taraCallSampleRef.current.count;
    // V5.5d: Use claimed direction for snapshot, not necessarily current tc.call
    const claimedDir=taraCallSampleRef.current.dir;
    // V5.6.4: Tier-based commit thresholds — same logic as display side. One sample ≈ one
    //   second. NO MORE TIME-DECAY: the decay was the cause of "locks too fast late in
    //   window" — Tara would lock with 1 confirmation if forming kicked in at minute 2+.
    //   Now needSamples is fixed by signal strength alone, capped only by remaining-window
    //   buffer (so we don't lock with 0s left to score).
    const q=qualityGate?.score||0;
    const fgtAbs=Math.abs(analysis?.mtfAlignment||0);
    const regime=analysis?.regime||'';
    const cleanRegime=['TRENDING UP','TRENDING DOWN','SHORT SQUEEZE','LONG SQUEEZE'].includes(regime);
    const winType=analysis?.windowAmplitude?.label;
    const hostileWindow=winType==='DEAD'||winType==='WHIPSAW';
    const _ctx=tc._ctx;
    const conv=_ctx?.conviction??Math.abs((analysis?.rawProbAbove||50)-50);
    const tapeStronglyAgrees=_ctx?.tapeStronglyAgrees||false;
    const tapeSuperStrong=_ctx?.tapeSuperStrong||false;
    const kalshiAgrees=_ctx?.kalshiAgrees||false;
    const kalshiStronglyAgrees=_ctx?.kalshiStronglyAgrees||false;
    const totalSec=windowType==='15m'?900:300;
    const elapsedSec=totalSec-((timeState.minsRemaining*60)+timeState.secsRemaining);
    const remaining=Math.max(0,totalSec-elapsedSec);
    // V6.5.7 ETA: write a baseline estimate IMMEDIATELY so the UI always has a value,
    //   even before any direction is claimed. Refined below once tier is determined.
    // V10.9.7: speed dial removed — baseline ETA uses neutral (dial=50) values:
    //   10s search phase, 30 base samples.
    {
      const _searchPhaseEarly=10;
      const _searchRem=Math.max(0,_searchPhaseEarly-elapsedSec);
      const _baseSamples=30;
      const _samplesRem=Math.max(0,_baseSamples-samples);
      const _baselineEta=_searchRem+_samplesRem;
      if(tc&&tc._ctx){tc._ctx.lockEtaSec=_baselineEta;tc._ctx.samples=samples;tc._ctx.needSamples=_baseSamples;tc._ctx.tierLabel=claimedDir?'forming':'scanning';}
    }
    // V6.2.7 / V6.5.1 / V7.7: KALSHI ENTRY WINDOW. Tara can take any amount of time to form
    //   a call as long as Kalshi for the chosen direction stays at or below
    //   KALSHI_ENTRY_THRESH. V7.7 raised 70 → 75. V10.7.71: conviction-scaled threshold.
    //   Formula: maxEntry = posterior - 8 (8pt covers 6¢ fee + 2¢ safety margin)
    //   Hard cap 85¢ (need 91%+ WR to profit above this — not achievable).
    //   Floor 55¢ (always allow entries below 55¢ even at low conviction).
    //   Examples: post=65% → 57¢ max | post=70% → 62¢ | post=75% → 67¢ | post=80% → 72¢
    //   User: "lock if she's very sure" — high conviction = higher allowed price. Never
    //   blocks profitable entries; only blocks clearly negative-EV ones.
    // V13.4.284: BOTH reads here were phantom fields — neither `analysis.posterior`
    //   nor `tc.posterior` is ever written (the engine exposes `rawProbAbove`). So
    //   this was permanently 50, and the whole conviction-scaling design above —
    //   "post=75% -> 67c max", "high conviction = higher allowed price" — never
    //   operated. KALSHI_ENTRY_THRESH collapsed to max(_entryFloor, 50-buffer),
    //   i.e. welded to its FLOOR (52c, or 55c in squeeze/compressing) on every
    //   window regardless of how sure Tara was. A lower threshold is MORE
    //   restrictive, so this silently refused to register entries on exactly her
    //   most confident reads.
    //   Also note the intent: the worked examples are all >50 (65/70/75/80), so the
    //   input is DIRECTIONAL confidence, not raw P(UP). Raw would pin every DOWN
    //   call back to the floor via the Math.max(50,...) clamp — 25% UP is a strong
    //   DOWN read, not a weak one. Expressed as 50+|p-50|, matching readLockState.
    const _postNow=(()=>{
      const _a=Number(analysis?.rawProbAbove);
      if(Number.isFinite(_a))return _a;
      // V13.4.285: the old `tc` fallback is gone — taraCall never carries this field,
      //   so it was dead weight that made the expression look like it had a backup.
      return 50;
    })();
    const _convictionNow=Math.max(50,Math.min(95,50+Math.abs(_postNow-50)));
    //   V10.7.71: conviction-scaled threshold. V10.7.74: floor raised 55¢ → 45¢.
    //   V10.7.75: regime-adaptive thresholds.
    //   RANGE-CHOP:   max(45, posterior-8)  — current, well-tuned
    //   TRENDING:     max(45, posterior-6)  — trend locks more reliable, allow higher price
    //   SHORT SQUEEZE: max(50, posterior-10) — riskier entries, tighter threshold
    //   HIGH VOL CHOP: max(45, posterior-5)  — best regime (84% WR), most room
    const _regimeNow=analysis?.regime||'RANGE-CHOP';
    const _entryBuffer=_regimeNow==='HIGH VOL CHOP'?5
      :_regimeNow==='TRENDING UP'||_regimeNow==='TRENDING DOWN'||_regimeNow==='STRONG TREND'?6
      :_regimeNow==='SHORT SQUEEZE'?10
      :_regimeNow==='LONG SQUEEZE'?10   // same as short squeeze — fast explosive move
      :_regimeNow==='COMPRESSING'?12    // very tight threshold — direction unconfirmed
      :8; // RANGE-CHOP default
    // V10.7.77: raise floor 45→52. 45-52¢ entries win at 55% WR, -1.2¢/trade EV.
    //   Not worth taking. 52-65¢ wins at 73% WR, +14¢/trade.
    const _entryFloor=(_regimeNow==='SHORT SQUEEZE'||_regimeNow==='LONG SQUEEZE'||_regimeNow==='COMPRESSING')?55:52;
    const KALSHI_ENTRY_THRESH=Math.min(85,Math.max(_entryFloor,Math.round(_convictionNow-_entryBuffer)));
    const KALSHI_DIP_GRACE_MS=45000;
    const _kPctNow=typeof kalshiYesPrice!=='undefined'&&kalshiYesPrice!=null?Number(kalshiYesPrice):null;
    // Track timestamp of most-recent Kalshi-below-threshold per direction
    if(_kPctNow!=null){
      if(_kPctNow<=KALSHI_ENTRY_THRESH){kalshiWasBelowThreshUpRef.current=true;kalshiLastBelowThreshUpRef.current=Date.now();}
      if((100-_kPctNow)<=KALSHI_ENTRY_THRESH){kalshiWasBelowThreshDownRef.current=true;kalshiLastBelowThreshDownRef.current=Date.now();}
      // V10.7.74b: hard absolute block if either direction costs >85¢
      // was-below logic misses DOWN trades where YES=2-4¢ (DOWN cost=96-98¢)
      // because that cost was NEVER ≤threshold so _wasBelow stays false
      if(_kPctNow>85){kalshiWasBelowThreshUpRef.current=false;kalshiLastBelowThreshUpRef.current=0;}
      if((100-_kPctNow)>85){kalshiWasBelowThreshDownRef.current=false;kalshiLastBelowThreshDownRef.current=0;}
    }
    // V6.1.2: EARLY SIT-OUT FOR MIXED CONDITIONS — kept as a separate guard. Time-based fast
    //   sit-out for cases where signals genuinely never align. This is NOT a cap, just an
    //   early-exit when nothing's forming.
    const _qFast=qualityGate?.score||0;
    const _hasAnyConfluence=(taraCall?._ctx?.isSuperConfluent)||(taraCall?._ctx?.isConfluent)||(taraCall?._ctx?.isTapeLed)||(taraCall?._ctx?.isRisingConfluence)||(taraCall?._ctx?.isStructuralLed);
    // V7.8: ALWAYS-COMMIT MODEL. User: "remove skips completely, we trade each round. we
    //   take the call instantly or within 1-2mins". Three-tier commit ladder:
    //     • True coin flip (posterior 45-55% AND ≤90s elapsed) → keep waiting (sampling
    //       can resolve the ambiguity)
    //     • Posterior >55% or <45% → COMMIT IMMEDIATELY to leaning direction with caution
    //       note proportional to confidence
    //     • ≥90s elapsed regardless → HARD COMMIT to current posterior direction (or UP
    //       if exactly 50). User said: 1-2 min cap.
    //   No more "Decision timer expired SIT_OUT", no more "couldn't form a call", no more
    //   dial-tier blocks producing sit-out. Tara takes a call every window.
    const _post=Number(analysis?.rawProbAbove);
    const _postKnown=Number.isFinite(_post);
    // V10.8.2 FIX: _commitDir was null when _postKnown=false. call=null on snapshots
    //   blocked ML field stamping and cushion history (both require non-null call).
    //   Fallback to claimedDir so the direction is always set on the snapshot.
    const _commitDir=_postKnown?(_post>=50?'UP':'DOWN'):(claimedDir||null);
    const _commitConf=_postKnown?Math.round(_commitDir==='UP'?_post:(100-_post)):50;
    // V13.4.249: turns a sit-out snapshot into a commit on Tara's own lean.
    //   Applied at every snapshot assignment below; a snapshot that is already
    //   directional passes straight through, so it is safe to wrap all of them.
    const _secsLeftNow=()=>(timeState.minsRemaining*60)+timeState.secsRemaining;
    const _applyNoSitout=(snap)=>{
      try{
        if(!NO_SITOUT_MODE||!snap||snap.call!=='SIT_OUT')return snap;
        // V13.4.291: this asked ONLY for noGoCategory, but the two snapshots
        //   _SITOUT_KEEP names write their label as `tier` and never set
        //   noGoCategory at all -- see tier:'no-go-price-unavailable-sitout' and
        //   tier:'kalshi-window-closed' further down this same effect. So the
        //   test was _SITOUT_KEEP.has(undefined), permanently false, and the
        //   keep-set has been unreachable since it was introduced in V13.4.249.
        //   Both "physical impossibility" cases were therefore converted into
        //   directional commits -- the price-unavailable one carrying
        //   kalshiAtLock:null, which is precisely the blind commit against an
        //   absent market that _SITOUT_KEEP's own comment says it exists to
        //   prevent. The file already treats these two fields as interchangeable
        //   labels elsewhere (_snapTierLc reads `tier||noGoCategory`; the
        //   edge-watch check asks both), so ask both here too.
        if(_SITOUT_KEEP.has(snap.tier)||_SITOUT_KEEP.has(snap.noGoCategory))return snap;   // market-side, keep
        if(_secsLeftNow()<=LOCK_DEADLINE_SEC)return snap;     // past the deadline
        const _d=(snap._intendedDir==='UP'||snap._intendedDir==='DOWN')?snap._intendedDir
          :(_commitDir==='UP'||_commitDir==='DOWN')?_commitDir:null;
        if(!_d)return snap;                                   // no lean to commit to
        return{...snap,
          call:_d,direction:_d,dir:_d,
          // V13.4.287: this spread `...snap` inherited the source sit-out's
          //   locked:false, so a REAL directional commit came out claiming it was
          //   not committed. readLockState reads exactly that flag, so THIS TRADE,
          //   the decision clock and the advisor all said LEANING/deciding on a
          //   round that was already committed, logged, and scored into the record.
          //   A converted sit-out IS a commit — say so.
          locked:true,
          wasOverriddenNoTrade:false,
          noGoCategory:null,
          // V13.4.291: the spread inherited isNoGo:true from the source sit-out
          //   (late-trend-oppose and tier1-only-skip both set it). _logSnapshotEntry
          //   stamps `result: snapshot.isNoGo ? 'NO_TRADE' : null`, and the resolver
          //   only ever picks up entries with `result===null`. So every sit-out this
          //   converted into a REAL directional commit was logged pre-resolved as
          //   NO_TRADE and could never settle to WIN or LOSS -- a live trade that
          //   auto-exec would place, permanently missing from the record and from
          //   every win-rate, ML and calibration filter (all of which key on
          //   `t.result`). Worse than a wrong number: an invisible, non-random hole,
          //   since these are by definition the marginal windows Tara wanted to skip.
          //   A converted sit-out is a trade; let it resolve like one.
          isNoGo:false,
          confidence:snap.confidence||_commitConf,
          tier:'no-sitout-commit',
          _noSitoutFrom:snap.noGoCategory||snap.tier||'sitout',
          _noSitoutWas:snap.reason||snap.caution||null,
          reason:`Sit-outs are off: Tara committed ${_d} on the live lean. Original sit-out gate: ${snap.reason||snap.caution||'noted without further detail'}`,
          caution:'Committed on the lean — sit-outs are off (was: '+(snap.noGoCategory||'sitout')+')',
        };
      }catch(_e){return snap;}
    };
    const _isCoinFlip=_postKnown&&_post>=45&&_post<=55;
    // V13.4.180: PATIENCE FLOOR 90s -> 210s on 15m. The old 90s ("1-2min range user
    //   wants") predates the timing data; measured on the cloud log, in-band (55-69c)
    //   EV by lock time on 15m:
    //     locked with 12-15m left (elapsed <180s):  n=89  +0.26c/ct  (breakeven)
    //     locked with  9-12m left (180-360s):       n=55  +11.84c/ct
    //     locked with   6-9m left (360-540s):       n=18  +19.17c/ct
    //   Same prices, 12-19c/ct apart purely on timing. The self-selected early locks
    //   (gap+tape+odds -> V12.5 tiers at 90/120/180s) earned their speed and keep it;
    //   what moves is the CLOCK-FORCED commit -- "time's up" 10% into a 15-minute
    //   window is not patience running out, it's patience never existing. At 210s the
    //   forced commits land inside the measured-good 9-12m bucket. Worst case a lean
    //   committable at 90s is priced ahead by 210s -- then the V13.4.178 band turns it
    //   into a sit-out, which costs ~nothing (that volume measured +0.26).
    //   5m windows keep 90s: the timing table above is 15m-only, don't extrapolate.
    //   Override: localStorage 'taraTimeCapSec' (seconds).
    const _hardCapFloorSec=(function(){
      try{const v=parseFloat(localStorage.getItem('taraTimeCapSec'));
        if(Number.isFinite(v)&&v>=30&&v<=600)return v;}catch(_e){}
      return windowType==='15m'?210:90;
    })();
    const _hardCapElapsed=elapsedSec>=_hardCapFloorSec;
    // V7.10.1: CRITICAL FIX. Helper to write a Tara-call log entry whenever any of the
    //   V7.8/V7.9 snapshot paths fire. Without this, NO_TRADE / time-cap-commit / timer-
    //   commit / kalshi-window-closed all set snapshot ref but never wrote to taraCallLog.
    //   Result: rollover scoring couldn't find pending entries → trades never resolved →
    //   memory + scorecards stopped updating for both BTC and ETH. Bug shipped in V7.8 and
    //   compounded each version since.
    // Early-lock and fast-lock stamps are initialized at effect entry, before
    // every possible call to this hoisted helper (including delay-gate exits).
    function _logSnapshotEntry(snapshot){
      if(!snapshot)return;
      if(!_prepareCallCommit(snapshot))return;
      Object.assign(snapshot,hydrateDecisionQuote(snapshot,{quote:_kalshiQuote,windowId:computeWindowId(windowType),asset:currentAssetRef.current||'BTC',now:Date.now()}));
      // V13.4.127 attempted to wire up flipAtLock here -- turned out to be dead code.
      //   A separate, later object-literal construction site (the main automatic
      //   pipeline's entry builder, ~L42160) ALREADY captures this same data under a
      //   different, pre-existing field name -- _v10_7_4_flipped (with originalDir,
      //   votes, reasons) -- and that field wins, silently overwriting whatever this
      //   mutation set. V13.4.130: removed the dead mutation. Use _v10_7_4_flipped on
      //   logged entries for flip analysis, not flipAtLock (that field is fed by an
      //   older, apparently-inert mechanism, _v10_7_11_universalFlipped, and is a
      //   separate, still-unresolved issue from the one this comment used to describe).
      // V13.4.165: SECONDARY-DEVICE GUARD REMOVED.
      //   It rewrote any committed lock to SIT_OUT on every device whose id did not
      //   match settings/primaryDevice, leaving tier and reason intact -- so the log
      //   read "Directional lock - DOWN -16pt - structural momentum" on an entry that
      //   displayed as a sit-out. Live cost: a full day of muted 15m locks (last real
      //   lock 08-10 01:02) because the registered primary (dev-nv198o-nmu4, set
      //   08-09 03:22) was a browser no longer in use.
      //
      //   It also fails silently -- the only trace was a `caution` field buried in the
      //   call log, no banner, nothing in the main UI -- and the device id lives in
      //   localStorage, so clearing browser data or opening a private window silently
      //   demotes you to "secondary" and mutes every lock.
      //
      //   Removed rather than made visible, because it contradicts the product rule:
      //   Tara is ONE shared, live decision -- if she locks a direction, every device
      //   shows that same direction at the same time. Per-device divergence is the bug.
      //
      //   The problem it was built for (two browsers committing conflicting locks) is
      //   already solved, better, by the FIRST-WRITE-WINS convergence on
      //   state/currentLock_<asset>_<windowType> (see the cloudWatch adopt logic):
      //   whichever device commits first wins, every other device adopts that exact
      //   snapshot within ~1s, and _persistLock defers instead of re-writing. That
      //   yields one identical lock everywhere WITHOUT muting any device.
      //   settings/primaryDevice is now inert; the Sync menu control is retired too.
      // ═══════════════════════════════════════════════════════════════════
      // V13.4.145 — WIRE UP THE V11 SELECTIVITY ENGINE.
      //
      // _v11Gate has existed since V11 and was NEVER CALLED — 130 lines of
      // tier gates, session blackouts, Kalshi-edge checks, quality scoring and
      // size guidance that had never influenced a single trade. The V13.4.144
      // dead-zone gate shipped into it earlier today was, for the same reason,
      // a no-op.
      //
      // Applied HERE because this is the one universal commit point every
      // snapshot passes through (made genuinely universal in V13.4.121), and
      // because the secondary-device guard directly above already establishes
      // that mutating a snapshot to SIT_OUT at this point is safe: the object
      // is the same one taraCallSnapshotRef.current holds, so the UI, the
      // persisted lock and the log all see the same decision.
      //
      // Replayed over n=3,255 real-traded calls, priced net of fees and of the
      // measured mid->exec drag, keeping only the sub-gates whose blocked
      // trades were genuinely EV-negative (see the note in _v11Gate):
      //     before  n=3255  netEV +0.40c/contract
      //     after   n=1888  netEV +1.97c/contract   (~5x, ~20 trades/day)
      //
      // ── V13.4.159: NOW OFF BY DEFAULT ───────────────────────────────────
      // Turned on in V13.4.145 on the strength of a replay. Three days of live
      // Supabase data do not justify keeping it on:
      //   * it fired 30 times, ALL of them 'v11-kalshi-edge', and ZERO of those
      //     30 carry a recorded would-be outcome -- so its value is literally
      //     unmeasurable from what we log. A gate that cannot be scored should
      //     not be running against real money.
      //   * sit-out rate over the same period went 50% -> 77%.
      //   * the user's read of it, which the above supports: it blocks calls
      //     they want to see and it has not earned that.
      // The replay that justified it used mid-priced log rows and could not see
      // the exits or the real fills, so it was never the strong evidence it
      // looked like. Left in place, defaulted OFF, so it can be re-enabled and
      // measured properly once blocked sit-outs record outcomes.
      // ENABLE: localStorage 'taraV11Gate' = '1'.
      // ═══════════════════════════════════════════════════════════════════
      try{
        const _v11On=(()=>{try{return localStorage.getItem('taraV11Gate')==='1';}catch(_e){return false;}})();
        if(_v11On&&snapshot&&!snapshot._v11Checked&&
           (snapshot.call==='UP'||snapshot.call==='DOWN')){
          snapshot._v11Checked=true;
          const _g=_v11Gate(snapshot);
          if(_g&&_g.allow){
            // Passed: attach quality + size guidance so the card and any sizing
            //   logic can use it. No behavioural change to the call itself.
            snapshot.v11Quality=_g.qualityScore;
            snapshot.v11Size=_g.sizeGuidance;
            snapshot.v11Why=_g.why;
            snapshot.v11Prime=!!_g.primeSetup;
            snapshot.v11Late=!!_g.isLate;
            snapshot.v11Expanding=!!_g.isExpanding;
          }else if(_g&&!NO_ENTRY_GATES){
            // V13.4.264: was `else if(_g)`. Now honours NO_ENTRY_GATES like every
            //   other gate. When gates are off the v11* telemetry fields above are
            //   simply not stamped, which is correct -- there was no gate decision.
            // Blocked: convert to a sit-out shaped like every other sit-out in
            //   this file (see the V10.7.97 weak-gap sitout for the template),
            //   preserving the intended direction for analytics.
            snapshot._intendedDir=snapshot._intendedDir||snapshot.call;
            snapshot.call='SIT_OUT';
            snapshot.direction='SIT_OUT';
            snapshot.dir='SIT_OUT';
            snapshot.wasOverriddenNoTrade=true;
            snapshot.noGoCategory=_g.category||'v11-gate';
            snapshot.tier='v11-sitout';
            snapshot.v11Blocked=_g.category||'v11-gate';
            snapshot.caution=`V11 gate: ${_g.reason||'blocked'}`;
            snapshot.reason=`V11 selectivity gate — ${_g.reason||'blocked'}`;
          }
        }
      }catch(_e){
        // A gate failure must never stop a call being logged.
        try{console.warn('[V13.4.145] v11 gate error:',_e?.message);}catch(_){}
      }
      // V10.7.82: Universal entry cost guard — block entries outside valid range
      //   regardless of which code path created the snapshot.
      //   Data showed 5 entries >85¢ and 2 entries <35¢ slipping through.
      //   These were on no-go-edge/time-cap-commit paths that bypass the main gate.
      // V12.2: no-go-edge and no-go-coinflip-late tiers arrive with wasOverriddenNoTrade=true
      //   already set (they are 'override' trades by design). That flag was gating BOTH the
      //   cost guard AND the EV gates — so a 1¢ entry in SQUEEZE with whale opposing and
      //   quality=0 passed through with no checks at all. Edge-watch tiers must still run
      //   through every guard — they just can't be blocked by the override flag alone.
      const _isEdgeWatchTier=snapshot?.tier==='no-go-edge'||snapshot?.tier==='no-go-coinflip-late';
      // V13.4.121 FIX: the exemptions above (wasOverriddenNoTrade bypass + no-go-data
      //   skip) let real trades slip through with no cost check at all. Real trade data
      //   (July 2026) shows locks up to 100c and 90-98c LOSSES specifically tagged
      //   no-go-data / kalshi-no-reset / mixed-sitout -- exactly the tiers this guard
      //   was supposed to cover but didn't. Made genuinely universal: every tier, every
      //   path, no exemptions except an already-decided SIT_OUT.
      // V13.4.125 REVERT (partial, explicit user request -- 'B, more locks always
      //   preferred'): no-go-edge / no-go-coinflip-late are original-design 'override'
      //   trades -- the intent (predates this session) was for them to lock anyway with
      //   a pricing caution rather than sit out, even outside the 30-60c band. Making the
      //   guard universal last session silently reversed that decision; restoring the
      //   edge-watch exemption specifically. Stated cost, for the record: real archive
      //   data has at least one no-go-edge LOSS at 98c -- this exemption reopens that
      //   exposure. Every OTHER tier (no-go-data, kalshi-no-reset, mixed-sitout, and any
      //   generic wasOverriddenNoTrade case) stays fully guarded -- those were never the
      //   tier this exemption was meant to cover.
      if(snapshot&&snapshot.locked&&snapshot.call!=='SIT_OUT'&&!_isEdgeWatchTier){
        const _snapKal=snapshot.kalshiAtLock!=null?Number(snapshot.kalshiAtLock):null;
        const _snapDir=snapshot.call||snapshot.direction;
        // V13.4.132 FIX: when the price feed is down (_snapKal null), this whole block
        //   used to just get skipped -- no price check at all, trade locks anyway. Real
        //   data: 157 no-go-data trades in the archive, ALL of them tier='no-go-data'
        //   ('Data integrity issue -- Kalshi feed unavailable' is literally the reason
        //   text), and not one of them ever sat out -- 89W/68L placed blind, with no
        //   reference price to even know what was paid. A missing price isn't a reason
        //   to skip the gate, it's the strongest possible reason to trigger it.
        if(_snapKal==null&&_snapDir){
          snapshot.call='SIT_OUT';
          snapshot.wasOverriddenNoTrade=true;
          snapshot.caution=`No Kalshi price at lock (feed unavailable) — sitting out, can't safely price an entry blind (V13.4.132)`;
        }else if(_snapKal!=null&&_snapDir){
          const _snapCost=_snapDir==='UP'?_snapKal:(100-_snapKal);
          // V13.4.118: this guard hardcoded 35/85, a THIRD price range different from
          //   the 30-60 dial every other gate tonight (103/107/111/112) reads. Found from a
          //   real screenshot: a 32c lock would pass the newer band (>=30) but get blocked
          //   here (<35) -- contradictory behavior between two mechanisms doing the same
          //   job. Unified to the same localStorage dial so there is exactly ONE band
          //   definition across the whole app, not five.
          const _v1282Min=getEntryMinCost();
          // V13.4.129: time-cap-commit gets its own tighter ceiling. Real data (37
          //   trades on the 25-75c band, two checks 24h apart): split by whether the
          //   price was reachable under the OLD 60c ceiling --
          //     <65c (old-band range):        n=10  WR=80%  net +97c
          //     >=65c (widened-band-only):     n=5  WR=40%  net -260c
          //   The tier's overall 67% WR looked fine but was propped up entirely by easy
          //   cheap wins -- the high-price bucket the widening specifically unlocked is
          //   what's losing. Control check: directional-lock's >=65c trades over the same
          //   window were 100% WR, +159c -- so this is NOT 'high price is risky' in
          //   general, it's specific to time-cap-commit, where a high price means 'ran
          //   out of patience,' not 'strong signal.' Every other tier keeps the full
          //   25-75c band earned by directional-lock/no-go-edge's real performance.
          // V13.4.178: the V13.4.129 time-cap clamp (65c) is REMOVED. It was set from
          //   n=5 real trades (>=65c under the old 75c band -- a range whose worst part,
          //   70-74c, the new global 70c ceiling now removes for every tier anyway).
          //   The clamp's actual effect, measured on the cloud log: 48 time-cap windows
          //   converted to SIT_OUT at 65-69c whose posterior lean went on to win 70.8%
          //   (+3.75c/ct hypothetical). That is exactly the "less sitouts" volume the
          //   user keeps asking for, in the proven-profitable band. Time-cap now uses
          //   the same 55-70c band as every other normal tier.
          const _v1282Max=getEntryMaxCost();
          if(_snapCost<_v1282Min||_snapCost>=_v1282Max){
            // Entry cost out of range — convert to sit-out
            snapshot.call='SIT_OUT';
            snapshot.wasOverriddenNoTrade=true;
            snapshot.caution=`Entry cost ${_snapCost.toFixed(0)}¢ outside valid range (${_v1282Min.toFixed(0)}-${_v1282Max.toFixed(0)}¢) — sitting out (unified band guard)`;
          }
        }
      }
      // V13.4.130: no-go-edge/no-go-coinflip-late TAIL CAP ONLY -- explicit user
      //   instruction was 'B, more locks always preferred,' so this does NOT reinstate
      //   the full band guard for these tiers. But real data (Aug 2, n=37 no-go-edge):
      //   70% WR yet net -135c -- wins averaged 88.7c cost (paying 0-15c margin), losses
      //   averaged 60.3c (including one 100c LOSS, near-total stake). At the extreme end
      //   the payoff math is hopeless regardless of win rate: a win at 95c+ pays under 5c,
      //   a loss at 95c+ costs the entire stake -- needs >95% WR just to break even, which
      //   nothing here is claiming. This blocks ONLY that unwinnable tail (outside 5-95c);
      //   everything from 5c to 95c still locks freely, same as before this fix.
      if(snapshot&&snapshot.locked&&snapshot.call!=='SIT_OUT'&&_isEdgeWatchTier){
        const _ewKal=snapshot.kalshiAtLock!=null?Number(snapshot.kalshiAtLock):null;
        const _ewDir=snapshot.call||snapshot.direction;
        if(_ewKal!=null&&_ewDir){
          const _ewCost=_ewDir==='UP'?_ewKal:(100-_ewKal);
          // V13.4.178: edge-watch banding replaces the flat 5-95 tail cap. This
          //   DELIBERATELY narrows the V13.4.125 "B, more locks always preferred"
          //   exemption, on the user's newer standing instruction ("get me wins
          //   overall at decent odds"). The no-go-edge book, measured (n=207,
          //   -4.03c/ct overall = the single worst tier total at -834c):
          //     <55c   n=22  WR 22.7%  -14.5c/ct   blocked now
          //     55-69c n=36  WR 61.1%   -2.6c/ct   still allowed (near-breakeven;
          //                                        blocking ~breakeven volume is the
          //                                        V13.4.146 mistake, not repeated)
          //     70-84c n=92  WR 61-77%  -11.1/-1.7 blocked now (the dead zone)
          //     85-94c n=44  WR 97.7%   +7.7c/ct   KEPT OPEN -- the one island the
          //                                        plain 55-70 band would have killed;
          //                                        this is why edge-watch keeps its own
          //                                        banding instead of the global dial
          //     95c+   n=13  WR 84.6%  -15.1c/ct   blocked (was already, via tail cap)
          // ── FIRST POST-SHIP READ (n=82 resolved, 2026-08-14..19). Recorded here so
          //    neither session re-litigates this from scratch. The gates did exactly
          //    what they were built to do:
          //        <55c      PRE n=36  -7.19c/ct (-259c)  -> POST n=0
          //        70-84c    PRE n=119 -10.26c/ct(-1221c) -> POST n=1
          //        WR        PRE 67.5%                    -> POST 73.2%
          //    Aggregate EV is NOT yet distinguishable (PRE +0.16 vs POST -0.32c/ct,
          //    ~1.5sd apart; daily EV swings -68..+43c/ct dwarf the effect at this n).
          //    Do NOT read that as the band failing, and do NOT retune on it.
          //  ── V13.4.209 RE-READ at n=98 post-v178 (first read was n=82) ──
          //    Verdict on the band change: it did what it was built to do.
          //        below 55c   PRE n=35 -> POST n=1
          //        70-84c      PRE n=117 -> POST n=1
          //        win rate    66.7% -> 72.4%
          //        EV/contract -0.34c -> +0.20c   (crossed zero, but ~0.1sd
          //                                        apart at n=98 -- not proven)
          //    THE ISLAND HAS DRIFTED TO ITS BREAK-EVEN LINE, exactly as the
          //    note below warned. Post-v178: n=30, WR 90.0%, avg cost 89.9c.
          //    Break-even at 89.9c IS 89.9% WR, so the margin is 0.1pt and the
          //    pocket is running at +0.07c/ct. Pre-v178 it was 97.0% / +7.15c.
          //    One additional loss in those 30 takes it to roughly -3c/ct.
          //    NOT ACTED ON, deliberately. All-time the island is still
          //    +3.78c/ct over n=63, and the sub-buckets are non-monotonic
          //    (85-87c: n=20 +9.0pt margin / 88-90c: n=12 -5.5pt / 91-94c:
          //    n=31 +4.0pt), so cutting the middle slice would be fitting to
          //    n=12 -- the same error that got earlier gates reverted.
          //    Re-measure at n>=60 POST before touching it.
          //    Broader conclusion worth carrying: the whole book is near
          //    break-even per contract either way, so further gate tuning is
          //    not where the money is. Execution is -- resting instead of
          //    crossing the spread is worth ~3.6c/ct, an order of magnitude
          //    more than the differences being argued over here.
          //    THE ONE THING TO WATCH -- this island is now 34% of resolved volume
          //    (28/82) vs 11% pre-ship, a direct consequence of funnelling no-go-edge
          //    into 55-69 or here. Its break-even is WR = 89.9% at the observed 89.9c
          //    average cost; it is running 92.9% post-ship vs 97.3% pre-ship. That is
          //    a ~3pt margin on a third of the book. If island WR drifts under ~90%
          //    it flips from the tier's best pocket to its worst, fast, because a loss
          //    here costs ~90c and a win pays ~10c. Re-measure before widening it.
          const _ewBlocked=_ewCost<55||(_ewCost>=70&&_ewCost<85)||_ewCost>=95;
          // V13.4.179: GAP-OPPOSED GUARD, edge-watch edition. Every other commit path
          //   has one (V10.7.97 directional-lock, V10.8.1 time-cap) -- edge-watch was
          //   the one tier exempt, and it shows: the cloud log's gap-opposed locks are
          //   16/25 no-go-edge, at 25% WR / -15.5c/ct. Locking a direction while price
          //   sits on the WRONG side of the strike loses 3 times out of 4 regardless
          //   of what Kalshi charges for it. Computed the same way distBpsAtLock is
          //   measured: aligned gap <= 0 means price at-or-past the strike against us.
          const _ewGapAligned=(Number(targetMargin)>0&&Number(currentPrice)>0)
            ?(((_ewDir==='UP'?(currentPrice-targetMargin):(targetMargin-currentPrice))/targetMargin)*10000)
            :null;
          const _ewGapOpposed=_ewGapAligned!=null&&_ewGapAligned<=0;
          // V13.4.264: THIS is the gate that kept sitting out on v256 and v262
          //   after v249/v251 were supposed to have ended sit-outs. It hardcodes
          //   55 / 70-84 / 95 instead of reading getEntryMinCost()/getEntryMaxCost(),
          //   so turning that dial to 0-100 left this one enforcing the old band on
          //   every edge-watch tier. Same flag reaches it now.
          //   Stamped rather than silent, so what the gate WOULD have blocked stays
          //   answerable from the existing log with no new plumbing.
          if((_ewBlocked||_ewGapOpposed)&&NO_ENTRY_GATES){
            snapshot._v264WouldHaveSatOut=_ewGapOpposed?'gap-opposed':'cost-band';
            snapshot._v264WouldHaveCost=Math.round(_ewCost);
          }
          if((_ewBlocked||_ewGapOpposed)&&!NO_ENTRY_GATES){
            const _ewZone=_ewGapOpposed?`gap-opposed (price ${Math.abs(_ewGapAligned).toFixed(0)}bps on the wrong side of the strike — 25% WR measured)`:_ewCost<55?'below 55c (22.7% WR, -14.5c/ct measured)':(_ewCost>=95?'95c+ (needs >95% WR to break even)':'the 70-84c dead zone (-11.1c/ct measured at 70-74)');
            snapshot.call='SIT_OUT';
            snapshot.wasOverriddenNoTrade=true;
            snapshot.caution=`Entry ${_ewGapOpposed?'':'cost '+_ewCost.toFixed(0)+'¢ '}in ${_ewZone} — sitting out (V13.4.${_ewGapOpposed?'179 gap guard':'178 edge-watch banding'})`;
          }
        }
      }
      //   These capture context the engine doesn't use as signals — needed for
      // V11.2.3 EV GATES â data-driven sit-out rules from 2,821-trade analysis.
      //   Rule A: cost 50-60c with no regime stamp â 50% WR, -5.2c EV (n=706).
      //   Rule B: SHORT SQUEEZE regime â 25% WR, pure noise (n=12).
      //   Rule C: HTF direction disagrees with call â 40% WR (n=22, when populated).
      //   All three convert to SIT_OUT in-place so lifecycle/dedup paths are unaffected.
      // V11.2.6 PRICE FRESHNESS STAMP - kalshiAtLock comes from kalshiYesPrice, which
      //   updates on a 15s REST poll (WS is off; Kalshi requires auth headers the browser
      //   cannot set). So the stamped price can be up to one poll-cycle stale. We never
      //   measured this. Stamp the age now so staleness is visible in the log, and so the
      //   fighting-the-market gate below can refuse to act on a price it cannot trust.
      let _v1126_priceAgeMs=null;
      try{
        const _v1126_asset=currentAssetRef.current||currentAsset||'BTC';
        const _v1126_lastAt=lastKalshiSuccessRef.current?.[_v1126_asset]?.at||0;
        if(_v1126_lastAt>0){
          _v1126_priceAgeMs=Date.now()-_v1126_lastAt;
          if(snapshot)snapshot.kalshiPriceAgeMs=_v1126_priceAgeMs;
        }
      }catch(_v1126_age_err){}
      // V13.4.54: FEED-SOURCE TRACE. Records which proxy delivered the price and whether
      //   the picked market matched the current window. Diagnoses wrong-window reads that
      //   fed extreme prices (1/98/99/100) into the Kalshi guards -> excess sit-outs.
      //   feedMatchingClose 0 or >1 => window-match filter issue; feedVia => which source won.
      try{
        if(snapshot&&typeof window!=='undefined'&&window.__taraKalshiDebug){
          const _v13454_fd=window.__taraKalshiDebug;
          snapshot.feedVia=_v13454_fd.via!=null?_v13454_fd.via:null;
          snapshot.feedRejectReason=_v13454_fd.rejectReason!=null?_v13454_fd.rejectReason:null;
          snapshot.feedPriceAccepted=_v13454_fd.priceAccepted!=null?_v13454_fd.priceAccepted:null;
          snapshot.feedMatchingClose=_v13454_fd.matchingClose!=null?_v13454_fd.matchingClose:null;
          snapshot.feedTotalMarkets=_v13454_fd.totalMarkets!=null?_v13454_fd.totalMarkets:null;
          snapshot.feedBestTicker=_v13454_fd.bestTicker!=null?_v13454_fd.bestTicker:null;
          snapshot.feedBestStrike=_v13454_fd.bestStrike!=null?_v13454_fd.bestStrike:null;
        }
      }catch(_v13454_trace_err){}
      if(snapshot&&snapshot.locked&&snapshot.call!=='SIT_OUT'&&(snapshot.wasOverriddenNoTrade!==true||_isEdgeWatchTier)){
        try{
          const _g_kal=snapshot.kalshiAtLock!=null?Number(snapshot.kalshiAtLock):null;
          const _g_dir=snapshot.call||snapshot.direction;
          const _g_regime=snapshot.regime||analysis?.regime||'';
          const _g_htfDir=snapshot.htfDominantDir||(analysis?.htfDominantDir)||null;
          const _g_sit=(why)=>{
            // V13.4.264: several rule branches below call this. Neutralising it here
            //   covers all of them at once, rather than guarding each rule and
            //   missing one -- which is exactly how v249 came to miss these gates.
            if(NO_ENTRY_GATES){snapshot._v264WouldHaveSatOut='ev-gate';return;}
            snapshot.call='SIT_OUT';snapshot.wasOverriddenNoTrade=true;
            snapshot.noGoCategory='v1123-ev-gate';
            snapshot.caution=`V11.2.3 EV gate: ${why}`;
          };
          if(_g_kal!=null&&_g_dir){
            const _g_cost=_g_dir==='UP'?_g_kal:(100-_g_kal);
            const _g_hasQSignal=(snapshot.qScore!=null&&Number(snapshot.qScore)>=40)||(snapshot.qScoreV2!=null&&Number(snapshot.qScoreV2)>=40);
            const _g_isTapeTier=snapshot.tier==='tape'||snapshot.tier==='tape-led';
            // V11.2.6 RULE D - FIGHTING-THE-MARKET GATE.
            //   Locking the side the Kalshi market prices as the underdog. Last-500 data:
            //     side WITH market favorite = 70% WR (n=445); AGAINST favorite = 58% WR (n=55).
            //   Scales with how hard we fight (margin past 50 = 50 - our cost):
            //     fought 0-5pts  (cost 45-50): 75% WR (n=20) - legit tie-break, KEEP
            //     fought 5-10pts (cost 40-45): 47% WR (n=17) - BLOCK
            //     fought 10-15pts(cost 35-40): 50% WR (n=10) - BLOCK
            //   cost<35 already blocked by the V10.7.82 cost guard above. Rule D covers
            //   the 35-45c underdog band. Tape-led carved out (order-flow can lead price).
            const _g_isFightBleed=_g_cost>=35&&_g_cost<45;
            // V11.2.6 STALE-PRICE GUARD - if the lock price is older than ~40s (a missed
            //   15s poll) AND we are taking the underdog side at all (cost<50), the gap we
            //   are reading cannot be trusted. Betting against the favorite on a stale price
            //   is the exact wrong-side failure mode. Sit out.
            const _g_stale=_v1126_priceAgeMs!=null&&_v1126_priceAgeMs>40000;
            if(_g_isFightBleed&&!_g_isTapeTier){
              _g_sit(`fighting market favorite (our cost ${_g_cost.toFixed(0)}c = underdog by ${(50-_g_cost).toFixed(0)}pts) - 47-50% WR vs 70% with-market`);
            }
            else if(_g_stale&&_g_cost<50&&!_g_isTapeTier){
              _g_sit(`stale Kalshi price (${(_v1126_priceAgeMs/1000).toFixed(0)}s old) on underdog ${_g_dir} (cost ${_g_cost.toFixed(0)}c) - cannot trust gap, sit out`);
            }
            // Rule A: no-signal 50-60c sit-out.
            //   With regime OR qScore>=40 OR tape tier, 50-60c runs 71-78% WR — take the trade.
            //   With NO regime AND NO qScore AND NOT tape: 54% WR, -5c EV (n=706) — sit out.
            else if(_g_cost>=50&&_g_cost<60&&!_g_regime&&!_g_hasQSignal&&!_g_isTapeTier){
              _g_sit(`cost ${_g_cost.toFixed(0)}c in 50-60c band, no regime/qScore/tape signal — 54% WR, -5c EV when blind`);
            }
            // Rule B: SHORT SQUEEZE is untradeable (25% WR)
            else if(_g_regime==='SHORT SQUEEZE'){
              _g_sit('SHORT SQUEEZE regime â 25% WR, forced-covering distorts all signals');
            }
            // Rule C: HTF direction explicitly opposes call (only when field is populated)
            else if(_g_htfDir&&_g_htfDir!==_g_dir){
              _g_sit(`HTF direction ${_g_htfDir} opposes ${_g_dir} call â 40% WR when HTF disagrees`);
            }
          }
        }catch(_ev_gate_err){}
      }
      //   XGBoost to learn something new beyond what the posterior already encodes.
      //   Fields: abortCount, bbwRank, timeFraction, kalshiVelocity, windowsSinceReset
      // V10.7.86: removed 'snapshot.locked' gate — was preventing ML fields from stamping
      //   on valid directional snapshots. 0/76 entries had ML fields on V10.7.79+.
      //   Now stamps on any real directional call (not sitout, not override, not no-go-data).
      if(snapshot&&snapshot.call!=='SIT_OUT'&&(snapshot.dir||snapshot.call)!=='SIT_OUT'&&snapshot.wasOverriddenNoTrade!==true&&snapshot.tier!=='no-go-data'){
        try{
          // V10.8.9: normalize null call. 10/32 V10.8.8 entries still had call=null —
          //   a path sets direction but not call. Fix at the convergence point.
          if(snapshot.call!=='UP'&&snapshot.call!=='DOWN'){
            const _normDir=snapshot.direction==='UP'||snapshot.direction==='DOWN'?snapshot.direction:null;
            if(_normDir)snapshot.call=_normDir;
            else if(Number.isFinite(snapshot.atPosterior))snapshot.call=snapshot.atPosterior>=50?'UP':'DOWN';
          }
          snapshot._ml_abortCount=_v104_1_abortCountRef.current?.count||0;
          snapshot._ml_bbwRank=analysis?.rawSignalScores?._bbwRank??null;
          snapshot._ml_timeFraction=Math.round((1-((timeState.minsRemaining*60+timeState.secsRemaining)/(windowType==='15m'?900:300)))*100)/100;
          snapshot._ml_kalshiVelocity=typeof kalshiYesPrice!=='undefined'&&kalshiYesPrice!=null
            ?Number(kalshiYesPrice)-Number(snapshot.kalshiAtLock||kalshiYesPrice)
            :null;
          snapshot._ml_cohRatio=(analysis?.rawSignalScores?._v10_7_64_cohRatio)??null;
          // V10.8.9: CUSHION INTEL FOR ALL PATHS.
          //   Was only stamped on the main confluence-path lock snap — but most trades
          //   (directional-lock, patient, time-cap, no-go-edge) take alternate paths
          //   and never got it. 0/32 cushion coverage on V10.8.8. Compute here at the
          //   convergence point from _windowCushionHistoryRef directly.
          const _lgHist=_windowCushionHistoryRef.current||[];
          const _lgDirSign=snapshot.call==='UP'?1:(snapshot.call==='DOWN'?-1:0);
          const _lgStrike=kalshiStrikeRef.current||0;
          // V10.9.2 FIX: _refPrice is a local const inside the `analysis` useMemo
          //   (different closure) — referencing it here threw ReferenceError at
          //   runtime on every lock. Read analysis.referencePrice instead (stamped
          //   into the useMemo's return value for exactly this reason).
          const _lgRefPrice=Number(analysis?.referencePrice)||Number(currentPrice)||0;
          if(_lgHist.length>=3&&_lgDirSign!==0&&_lgStrike>0&&_lgRefPrice>0){
            const _lgCorrect=_lgHist.filter(h=>h.cushionBps*_lgDirSign>0).length;
            const _lgPct=_lgCorrect/_lgHist.length;
            const _lgRecent=_lgHist.slice(-2).reduce((s,h)=>s+h.cushionBps*_lgDirSign,0)/2;
            const _lgEarly=_lgHist.slice(0,2).reduce((s,h)=>s+h.cushionBps*_lgDirSign,0)/2;
            snapshot._cushionPctCorrect=Math.round(_lgPct*100)/100;
            snapshot._cushionTrend=Math.round((_lgRecent-_lgEarly)*10)/10;
            // V10.9.1/V10.9.2: BRTI-corrected reference price via analysis.referencePrice
            snapshot._cushionNowBps=Math.round(((_lgRefPrice-_lgStrike)/_lgStrike*10000*_lgDirSign)*10)/10;
            snapshot._cushionSnapshots=_lgHist.length;
          }
        }catch(_){}
      }
      if(_sealCallSnapshot(snapshot)===false)return;
      // V10.7.45: Lifecycle telemetry — record EVERY call regardless of what happens next.
      //   This lets us distinguish "log call never fired" from "log call fired but pushed nothing".
      try{
        const _lcWid=computeWindowId(windowType);
        const _lcAsset=currentAssetRef.current||currentAsset||'BTC';
        _lifecycleRecordLogCall(_lcWid,windowType,_lcAsset,snapshot,'_logSnapshotEntry-entry');
      }catch(_){}
      try{
        // V10.4.1: clear the pending delay stamp ref after one snapshot fires
        //   so subsequent entries in the same window don't re-stamp it. The
        //   stamp gets included into the entry via the spread sites elsewhere.
        const _v104_1_clearDelayAfterLog=()=>{_v104_1_pendingDelayStampRef.current=null;};
        setTimeout(_v104_1_clearDelayAfterLog,0);
        // V9.1.8 → V10.7.47: SITOUT logging gate REMOVED.
        //   Was: skip ambiguous SIT_OUT entries unless user opened app late. Reasoning
        //   was "not actionable, just a quiet rollover." But user audit showed 70+ silent
        //   sitouts in 2 days — most firing at 30-70s into 15min windows. No visibility,
        //   no chance to correct. V10.7.47 logs them all as `mixed-sitout` entries with
        //   leanDir/leanPct stamps so the Memory shows the full picture.
        //   The earlier `windowOpenTimeRef`/`_msSinceMount` audit context is preserved
        //   inline below for diagnostic purposes; just no longer a skip gate.
        const _isAmbiguousSitOut=snapshot&&snapshot.call==='SIT_OUT'&&!snapshot.isNoGo;
        if(_isAmbiguousSitOut){
          const _now=Date.now();
          const _msSinceMount=_now-(_mountTimeRef.current||_now);
          const _winLenSec=(windowType==='15m'?900:300);
          const _elapsedSec=windowOpenTimeRef.current?Math.max(0,(_now-windowOpenTimeRef.current)/1000):0;
          const _secsLeft=Math.max(0,_winLenSec-_elapsedSec);
          try{console.info('[V10.7.47] Mixed-sitout logging (V9.1.8 filter removed). Window left:',Math.round(_secsLeft),'s, mounted',Math.round(_msSinceMount/1000),'s ago, leanDir:',snapshot.leanDir||'(none)');}catch(_){}
          _auditLogEvent('v10_7_47_mixed_sitout_logged',{
            source:'_logSnapshotEntry',
            windowType,
            msSinceMount:Math.round(_msSinceMount),
            secsLeftInWindow:Math.round(_secsLeft),
            leanDir:snapshot.leanDir||null,
            leanPct:snapshot.leanPct||null,
          });
        }
        const _wid=computeWindowId(windowType);
        // Asset tag with strike-magnitude inference (mirrors V7.1 logic from main lock path).
        const _refAsset=currentAssetRef.current||currentAsset||'BTC';
        const _strike=Number(targetMargin||targetMarginRef.current||0);
        let _assetTag='BTC';
        if(_strike>=100){
          const _inferred='BTC';
          if(_inferred!==_assetTag){
            try{console.warn('[V7.10.1] Asset tag override at snapshot log:',_assetTag,'→',_inferred,'(strike',_strike,')');}catch(_){}
            _assetTag=_inferred;
          }
        }
        const _entry={
          id:Date.now(),time:Date.now(),windowType,
          windowId:_wid,
          originalDecision:snapshot.originalDecision,
          callPolicy:snapshot.callPolicy||null,
          decisionTrace:snapshot.decisionTrace||null,
          forecastAvailability:snapshot.forecastAvailability||null,
          blockedDecision:snapshot.blockedDecision||null,
          marketTicker:snapshot.marketTicker||null,
          marketCloseTime:snapshot.marketCloseTime||null,
          regime:analysis?.regime||'',
          dir:snapshot.call==='NO_TRADE'?(snapshot.direction||'NO_TRADE'):snapshot.call,
          confidence:snapshot.confidence||0,
          posterior:(Number.isFinite(Number(analysis?.rawProbAbove))?Number(analysis.rawProbAbove):(Number.isFinite(Number(snapshot.atPosterior))?Number(snapshot.atPosterior):null)),/*V13.4.65: log LIVE taraCall.rawProbAbove; snapshot.atPosterior was NaN from stale analysis so posterior logged null on every lock, starving calibration*/
          qScore:snapshot.qScore||Math.round(qualityGate?.score||0),
          fgt:snapshot.fgt||analysis?.mtfAlignment,
          tier:snapshot.tier||'unknown',
          feedVia:snapshot.feedVia!=null?snapshot.feedVia:null,/*V13.4.64: persist feed trace; was stamped on snapshot but dropped by the hand-built entry*/
          feedRejectReason:snapshot.feedRejectReason!=null?snapshot.feedRejectReason:null,
          feedPriceAccepted:snapshot.feedPriceAccepted!=null?snapshot.feedPriceAccepted:null,
          ...(()=>{const _o=_v1364_oppRef.current;if(!_o||_o.windowId!==_wid)return{peakConv:null,peakEntry:null,peakSecsLeft:null,oppNowCount:0,oppFirstNowConv:null,oppFirstNowEntry:null,oppFirstNowSecsLeft:null};return{peakConv:_o.peakConv>=0?Math.round(_o.peakConv):null,peakEntry:_o.peakEntry,peakSecsLeft:_o.peakSecsLeft,oppNowCount:_o.nowCount,oppFirstNowConv:_o.firstNowConv,oppFirstNowEntry:_o.firstNowEntry,oppFirstNowSecsLeft:_o.firstNowSecsLeft};})(),/*V13.4.64: per-window peak opportunity for missed-early-lock diagnosis*/
          // V7.10.6: stamp the current market phase on every entry. Future memory views
          //   show this as a context badge so each call carries the trading-environment
          //   context it was made in. Inference fallback for legacy entries via timestamp.
          phase:(typeof getPhaseKey==='function'?getPhaseKey(new Date().getUTCHours(),new Date().getUTCMinutes()):null),
          session:snapshot.session||(typeof getMarketSessions==='function'?getMarketSessions():{})?.dominant||'UNKNOWN',
          reason:snapshot.reason||'',
          earlyLock:false,
          isNoGo:!!snapshot.isNoGo,
          // V13.4.237: never leave a sit-out uncategorised. Measured on the live
          //   1,000-call log: 554 sit-outs, of which 145 that HAD a directional read
          //   carried noGoCategory null. Some gate paths stamp a category
          //   (v11-gate, v1123-ev-gate, weak-gap-directional-sitout) and others do
          //   not, so the largest single block of vetoed calls could not be
          //   attributed to any gate — which makes them untunable, since you cannot
          //   loosen or tighten a rule you cannot identify.
          //
          //   Falls back to classifying the reason text, then to an explicit marker
          //   so "no gate recorded" is distinguishable from "field absent on a legacy
          //   entry". Logging only: this never touches a trading decision.
          noGoCategory:snapshot.noGoCategory||_deriveSitoutCategory(snapshot)||null,
          caution:snapshot.caution||null,
          isConfluent:!!snapshot.isConfluent,
          isSuperConfluent:!!snapshot.isSuperConfluent,
          isRisingConfluence:!!snapshot.isRisingConfluence,
          isTapeLed:!!snapshot.isTapeLed,
          tapeSuperStrong:(typeof taraCall!=='undefined'&&taraCall&&taraCall._ctx)?!!taraCall._ctx.tapeSuperStrong:false,
          tapeStronglyAgrees:(typeof taraCall!=='undefined'&&taraCall&&taraCall._ctx)?!!taraCall._ctx.tapeStronglyAgrees:false,
          convictionAtLock:(typeof taraCall!=='undefined'&&taraCall&&taraCall._ctx&&Number.isFinite(taraCall._ctx.conviction))?taraCall._ctx.conviction:null,
          qAtLock:(typeof taraCall!=='undefined'&&taraCall&&taraCall._ctx&&Number.isFinite(taraCall._ctx.q))?taraCall._ctx.q:null,/*V13.4.70: persist tape-strength internals (super vs strong, conviction, q) so we can backtest expanding the tape-led gate*/
          isStructuralLed:!!snapshot.isStructuralLed,
          rawPosteriorAtLock:analysis?.rawPosteriorUncalibrated??analysis?.rawProbAbove??null, /* V9.11.3: distinguish raw vs calibrated */
          calibratedPosteriorAtLock:analysis?.rawProbAbove??null, /* V9.11.2 fix: was reading analysis.posterior but useMemo exposes it as rawProbAbove */
          signalScoresAtLock:(typeof taraCall!=='undefined'&&taraCall&&taraCall.rawSignalScores)?{...taraCall.rawSignalScores}:(analysis?.rawSignalScores?{...analysis.rawSignalScores}:null),/*V13.4.66: prefer LIVE taraCall.rawSignalScores; analysis was stale so signals logged on only 3% of locks, blocking tape-weight backtest*/
          // V10.7.53: capture critical missing fields. Was: strikeAtLock/baselineVersion/reasoning not persisted on most entries.
          //   strikeAtLock — required for gap-at-lock vs gap-at-close audit; lets us understand losses.
          //   baselineVersion — required to filter analyses by engine version cleanly.
          //   reasoning — capture engine log so we can see WHY each call was made post-hoc.
          strikeAtLock:Number(targetMargin)||0,
          // V13.4.85: fair-value inputs (see note on the other lock paths).
          spotAtLock:Number(currentPrice)||0,
          distBpsAtLock:(Number(targetMargin)>0&&Number(currentPrice)>0)?Math.round(((Number(currentPrice)-Number(targetMargin))/Number(targetMargin))*1000000)/100:null,
          volBpsAtLock:Number.isFinite(analysis?.atrBps)?Math.round(analysis.atrBps*10)/10:null,
          baselineVersion:typeof BASELINE_VERSION!=='undefined'?BASELINE_VERSION:null,
          reasoning:Array.isArray(analysis?.reasoning)?analysis.reasoning.slice(0,40):null,
          // V10.7.50: Direction bias stamps — record recent UP/DOWN ratio and WR-per-direction
          //   at lock time. Enables auditing whether bias correlates with losing streaks
          //   and validates whether V10.7.49 deadzone guard is structurally fixing the bias.
          ...(()=>{
            const _resolved=taraCallLogRef.current.filter(e=>e&&(e.result==='WIN'||e.result==='LOSS'));
            const _last30=_resolved.slice(-30);
            if(_last30.length<10)return{};
            const _up=_last30.filter(e=>e.dir==='UP');
            const _dn=_last30.filter(e=>e.dir==='DOWN');
            const _upWR=_up.length?_up.filter(e=>e.result==='WIN').length/_up.length:null;
            const _dnWR=_dn.length?_dn.filter(e=>e.result==='WIN').length/_dn.length:null;
            return{
              _v10_7_50_recentDirN:_last30.length,
              _v10_7_50_recentUpPct:Math.round((_up.length/_last30.length)*100),
              _v10_7_50_recentDnPct:Math.round((_dn.length/_last30.length)*100),
              _v10_7_50_recentUpWR:_upWR!=null?Math.round(_upWR*1000)/10:null,
              _v10_7_50_recentDnWR:_dnWR!=null?Math.round(_dnWR*1000)/10:null,
            };
          })(),
          tapeAtLock:analysis?.tapeWindows?{
            w15Buy:analysis.tapeWindows.w15?.buyPct??null,
            w30Buy:analysis.tapeWindows.w30?.buyPct??null,
            w60Buy:analysis.tapeWindows.w60?.buyPct??null,
          }:null,
          atrBpsAtLock:analysis?.atrBps??null,
          kalshiAtLock:snapshot.kalshiAtLock!=null?snapshot.kalshiAtLock:(typeof kalshiYesPrice!=='undefined'&&kalshiYesPrice!=null?Number(kalshiYesPrice):null),
          // V13.4.142: executable-cost fields. kalshiAtLock above is a MID and is NOT
          //   tradeable; these record what entry would actually have cost. See the
          //   _kalshiQuote block near the top of this file for the full rationale.
          //   execCostAtLock is the number to use for EV work:
          //     true EV/contract = (winRate × 100) − execCostAtLock − Kalshi fee
          //   All null-safe: null simply means no fresh quote, and consumers should
          //   fall back to the mid-derived cost (and know it is optimistic).
          // V13.4.211: was one try/catch around the whole block with
          //   catch(_e){return{};} -- so a throw anywhere (most likely
          //   _execCostCents) discarded EVERY field and the entry carried no
          //   quote keys at all. Measured effect: 27 of 100 recent resolved
          //   trades had no key, and the 'patient' (0/6) and 'structural-led'
          //   (0/2) tiers never had one, while 'directional-lock' was 7/7.
          //   Each piece is now computed independently and the keys are ALWAYS
          //   emitted. A null now honestly means "no quote at that moment"
          //   rather than "the stamp crashed" -- which is the distinction the
          //   Execution panel relies on to report its excluded count truthfully.
          ...(()=>{
            const _dirNow=(()=>{try{return snapshot.call||snapshot.direction||null;}catch(_e){return null;}})();
            const _q=(()=>{try{return (typeof _kalshiQuote!=='undefined')?_kalshiQuote:null;}catch(_e){return null;}})();
            // V13.4.214: an empty book was being written as spread:0, and those
            //   fake zeros drag the Execution panel's measured spread down --
            //   understating the very cost it exists to expose.
            const _fresh=!!(_q&&_q.at&&(Date.now()-_q.at)<60000&&_quoteUsable(_q));
            const _num=(v)=>{const n=Number(v);return Number.isFinite(n)?n:null;};
            let _exec=null;
            try{_exec=_execCostCents(_dirNow);}catch(_e){_exec=null;}
            let _midCost=null;
            try{
              const _m=snapshot.kalshiAtLock!=null?Number(snapshot.kalshiAtLock)
                      :(typeof kalshiYesPrice!=='undefined'&&kalshiYesPrice!=null?Number(kalshiYesPrice):null);
              if(_m!=null&&isFinite(_m))_midCost=_dirNow==='UP'?_m:(_dirNow==='DOWN'?(100-_m):null);
            }catch(_e){_midCost=null;}
            return{
              kalshiBidAtLock:_fresh?_num(_q.bid):null,
              kalshiAskAtLock:_fresh?_num(_q.ask):null,
              kalshiSpreadAtLock:_fresh?_num(_q.spread):null,
              kalshiQuoteAgeMs:_fresh?(Date.now()-_q.at):null,
              execCostAtLock:_num(_exec),
              // How much the tradeable price is worse than the mid we score on.
              //   This is the per-trade execution drag, in cents.
              midToExecCents:(_exec!=null&&_midCost!=null)?Math.round((_exec-_midCost)*100)/100:null,
            };
          })(),
          // Strike at lock — needed by V7.5 cross-asset rollover scoring
          strike:_strike,
          asset:_assetTag,
          // V9.10.9: stamp device label so personal scorecard filters to this device only.
          // V9.10.10: stamp pattern detection state at lock time.
          patternsAtLock:analysis?.patternsV9_10_10||null,
        // V9.11.0: also stamp HTF-aware pattern data + futures data on entries.
        //   patternsAtLock above remains for backward compat; new analysis should
        //   read patternsAtLockV9_11_0 (per-TF + confluence) and futuresAtLock.
        patternsAtLockV9_11_0:analysis?.patternsV9_11_0||null,
        // V13.4.243: 3-scalar mirror of the line above. The fat object is
        //   stripped by the deep-cache minify, so the pattern engine's own
        //   telemetry survives on well under half the log. These three are on
        //   the _MINI_KEEP list and cost ~15 bytes.
        patDirScore:(()=>{const _v=Number(analysis?.patternsV9_11_0?.directionalScore);return Number.isFinite(_v)?Math.round(_v):null;})(),
        patConfluence:(()=>{const _v=Number(analysis?.patternsV9_11_0?.confluence);return Number.isFinite(_v)?_v:null;})(),
        patDominantDir:analysis?.patternsV9_11_0?.dominantDirection||null,
          // V9.15: stamp K-nearest historical analogs at lock time. Stored compactly
          //   (top 3 entries + summary) to keep log size manageable. The full 12-entry
          //   list is exposed via the live `analysis.historicalAnalogs` for UI only.
          historicalAnalogsAtLock:(()=>{
            const _h=analysis?.historicalAnalogs;
            if(!_h||!_h.valid)return null;
            return{
              analogWR:_h.analogWR,
              analogN:_h.analogN,
              avgSimilarity:_h.avgSimilarity,
              confidence:_h.analogConfidence,
              top3:(_h.topK||[]).slice(0,3).map(t=>({
                id:t.id,similarity:t.similarity,result:t.result,
                regime:t.regime,session:t.session,dir:t.dir,
              })),
            };
          })(),
        futuresAtLock:analysis?.futuresV9_11_0||null,
          // V10.2.36-40 — Regime detection improvement stamps persisted to call log.
          _v10_2_36_rangeOverrideFired:analysis?._v10_2_36_rangeOverrideFired===true,
          _v10_2_36_rangeOverrideAvgBps:analysis?._v10_2_36_rangeOverrideAvgBps??null,
          _v10_2_37_quietOverrideFired:analysis?._v10_2_37_quietOverrideFired===true,
          _v10_2_37_quietOverrideRatio:analysis?._v10_2_37_quietOverrideRatio??null,
          _v10_2_37_quietRealizedBps:analysis?._v10_2_37_quietRealizedBps??null,
          _v10_2_37_quietExpectedBps:analysis?._v10_2_37_quietExpectedBps??null,
          _v10_2_37_mtfUpAligned:analysis?._v10_2_37_mtfUpAligned===true,
          _v10_2_37_mtfDnAligned:analysis?._v10_2_37_mtfDnAligned===true,
          _v10_2_38_srRangeDetected:analysis?._v10_2_38_srRangeDetected===true,
          _v10_2_38_srRangeBps:analysis?._v10_2_38_srRangeBps??null,
          _v10_2_38_srTouchesHigh:analysis?._v10_2_38_srTouchesHigh??0,
          _v10_2_38_srTouchesLow:analysis?._v10_2_38_srTouchesLow??0,
          _v10_2_39_htfFlatOverride:analysis?._v10_2_39_htfFlatOverride===true,
          _v10_2_39_htfVetoFired:analysis?._v10_2_39_htfVetoFired===true,
          _v10_2_39_htf1hDriftBps:analysis?._v10_2_39_htf1hDriftBps??null,
          _v10_2_39_htf4hDriftBps:analysis?._v10_2_39_htf4hDriftBps??null,
          _v10_2_39_htf1hLabel:analysis?._v10_2_39_htf1hLabel||null,
          _v10_2_39_htf4hLabel:analysis?._v10_2_39_htf4hLabel||null,
          _v10_2_40_regimeProbs:analysis?._v10_2_40_regimeProbs||null,
          _v10_2_40_regimeConfidence:analysis?._v10_2_40_regimeConfidence??null,
          _v10_2_40_primaryRegime:analysis?._v10_2_40_primaryRegime||null,
          _v10_2_40_dampened:analysis?._v10_2_40_dampened===true,
          _v10_2_43_regimeMult:analysis?._v10_2_43_regimeMult??1.0,
          _v10_2_43_structOrig:analysis?._v10_2_43_structOrig??0,
          _v10_2_43_techOrig:analysis?._v10_2_43_techOrig??0,
          _v10_2_44_haircutApplied:analysis?._v10_2_44_haircutApplied||null,
          _v10_2_44_postBeforeHaircut:analysis?._v10_2_44_postBeforeHaircut??null,
          _v10_2_50_chopAmpDelta:analysis?._v10_2_50_chopAmpDelta??0,
          _v10_2_50_htfChopBoost:analysis?._v10_2_50_htfChopBoost||null,
          _v10_2_51_chopConfluence:analysis?._v10_2_51_chopConfluence||null,
          _v10_3_0_rangeData:analysis?._v10_3_0_rangeData||null,
          _v10_3_0_rangeFadeFired:analysis?._v10_3_0_rangeFadeFired||null,
          _v10_3_0_midRangeSitout:analysis?._v10_3_0_midRangeSitout===true,
          _v10_3_0_rangeBroken:analysis?._v10_3_0_rangeBroken===true,
          _v10_3_1_coinFlip:analysis?._v10_3_1_coinFlip||null,
          _v10_3_1_trendingUpPenalty:analysis?._v10_3_1_trendingUpPenalty||null,
          _v10_3_3_rangeHybrid:analysis?._v10_3_3_rangeHybrid||null,
          _v10_4_0_calibration:analysis?._v10_4_0_calibration||null,
          _v10_4_0_regimeCaps:analysis?._v10_4_0_regimeCaps||null,
          _v10_4_3_windowCtx:analysis?._v10_4_3_windowCtx||null,
          _v10_5_1_brti:brtiApprox?{current:brtiApprox.current,divBps:brtiApprox.divergenceBps,n:brtiApprox.samples60s,src:brtiApprox.sourceCount}:null, _diag_brtiRefLive:(brtiApproxRef&&brtiApproxRef.current)?{current:brtiApproxRef.current.current,divBps:brtiApproxRef.current.divergenceBps,n:brtiApproxRef.current.samples60s,src:brtiApproxRef.current.sourceCount}:null,
          _v10_6_2_dowHour:analysis?._v10_6_2_dowHour||null,
          _v10_7_4_flipped:taraCall?._v10_7_4_flipped?{originalDir:taraCall._v10_7_4_originalDir,votes:taraCall._v10_7_4_votes,reasons:taraCall._v10_7_4_voteReasons}:null,
          _v10_7_11_universalFlipped:taraCall?._v10_7_11_universalFlipped?{flipType:taraCall._v10_7_11_flipType,originalDir:taraCall._v10_7_11_originalDir,votes:taraCall._v10_7_11_votes,reasons:taraCall._v10_7_11_reasons,gapBps:taraCall._v10_7_11_gapBps,driftBps:taraCall._v10_7_11_driftBps,minsLeft:taraCall._v10_7_11_minsLeft}:null,
          _v10_7_19_balanced:taraCall?._v10_7_19_balanced?{downPct:taraCall._v10_7_19_downPct,originalDir:taraCall._v10_7_19_originalDir,originalConf:taraCall._v10_7_19_originalConf}:null,
          _v10_4_1_delayed:_v104_1_pendingDelayStampRef.current||null,
          samples:snapshot.samples||0,
          needSamples:snapshot.needSamples||0,
          // V13.4.339: was `snapshot.isNoGo?'NO_TRADE':null`. isNoGo is set on
          //   every quality-based sit-out gate (trend-at-lock, late-trend-oppose,
          //   tier1-only-skip, the generic 'sitout' tier) -- NOT only on the rare
          //   case of a genuinely absent market. `call:'NO_TRADE'` is never
          //   actually assigned anywhere in this file (grep-confirmed) -- every
          //   one of these snapshots has call:'SIT_OUT'. So this line was
          //   immediately pre-resolving most sit-outs to 'NO_TRADE' at commit
          //   time, before they ever reached the rollover/backfill resolution
          //   pipeline (both of which search for result===null) -- exactly the
          //   "sitout left as no trade" the user reported, unrelated to and not
          //   fixed by v338's resolution-reliability work, since these entries
          //   were never pending in the first place. Only ONE sit-out gate
          //   (edge-sitout) happened not to set isNoGo, which is the only
          //   reason any sit-out was ever seen correctly settling as SITOUT.
          //   Always start pending; call:'SIT_OUT' resolves to result:'SITOUT'
          //   via the SAME immediate-at-rollover path v338 already ships for
          //   every other sit-out category -- one terminal state, not two.
          result:null,
          // V10.0.0 Phase 4: shadow timing decision. Populated from
          //   _phase4DecisionRef when auto-exec entry effect ran. May be null
          //   for snapshots logged outside the auto-exec entry path
          //   (e.g., pure user-force snapshots that don't trigger auto-exec).
          tradeTimingDecision:_phase4DecisionRef.current?_phase4DecisionRef.current.decision:null,
          tradeTimingScore:_phase4DecisionRef.current?_phase4DecisionRef.current.score:null,
          tradeTimingReason:_phase4DecisionRef.current?_phase4DecisionRef.current.reason:null,
          tradeTimingMode:_phase4DecisionRef.current?_phase4DecisionRef.current.mode:null,
          // V10.2.23 — per-factor impact breakdown for offline calibration
          tradeTimingFactors:_phase4DecisionRef.current?_phase4DecisionRef.current.factors||null:null,
          tradeTimingFactorCount:_phase4DecisionRef.current?_phase4DecisionRef.current.factorCount||0:0,
          // V10.2.13: session × tier optimizer telemetry. _ctx comes from taraCall
          //   IIFE return; fields are stamped whenever taraCall ran for this lock.
          //   Mode='off' produces 1.0 mult / false applied (no-op baseline). Shadow
          //   computes the mult but doesn't change confidence; applied is false but
          //   mult tells us what live would have done. Live: applied=true if mult≠1.0.
          sessionTierMode:taraCall?._ctx?._sessionTierMode||null,
          sessionTierMult:taraCall?._ctx?._sessionTierMult!=null?taraCall._ctx._sessionTierMult:null,
          sessionTierApplied:taraCall?._ctx?._sessionTierApplied===true,
          // V9.2.1: Bet amount + max payout for P&L tracking. Captured at snapshot
          //   time so each trade carries its actual dollar amounts. P&L computed at
          //   resolution: WIN = maxPay - betAmt, LOSS = -betAmt.
          betAmt:Number(betAmount)||0,
          maxPay:Number(maxPayout)||0,
          // V10.8.6 FIX: copy ML + cushion fields from snapshot into the entry.
          //   These were stamped on `snapshot` (lines ~40119) but the entry was built
          //   field-by-field and never copied them, so they died on the snapshot object.
          //   This is why ML fields + cushion history showed 0/N across all exports
          //   despite the stamping code running correctly. Root cause of the persistent
          //   "ML fields empty" bug across V10.7.86 → V10.8.5.
          _ml_abortCount:snapshot._ml_abortCount??null,
          _ml_bbwRank:snapshot._ml_bbwRank??null,
          _ml_timeFraction:snapshot._ml_timeFraction??null,
          _ml_kalshiVelocity:snapshot._ml_kalshiVelocity??null,
          _ml_cohRatio:snapshot._ml_cohRatio??null,
          _cushionPctCorrect:snapshot._cushionPctCorrect??null,
          _cushionTrend:snapshot._cushionTrend??null,
          _cushionNowBps:snapshot._cushionNowBps??null,
          _cushionSnapshots:snapshot._cushionSnapshots??null,
          _cushionMult:snapshot._cushionMult??null,
          // V10.8.6: also carry the canonical call field (UP/DOWN/SIT_OUT) explicitly.
          //   `dir` was set but `call` was missing from entries, breaking downstream
          //   analysis that reads `call`.
          call:snapshot.call||snapshot.direction||null,
          // V12.0: regime classifier telemetry — V10.7.36 computes these every tick
          //   but they were never persisted. Stamping here enables the V12.1 adaptive
          //   gate (regime-specific lock bars) to be tuned from real data, not guesses.
          regimeV12:analysis?.rawSignalScores?._v10736regime||'',
          adxAtLock:analysis?.rawSignalScores?._adx??null,
          bbwRankAtLock:analysis?.rawSignalScores?._bbwRank??null,
          atrpAtLock:analysis?.rawSignalScores?._atrp??null,
          whipsawAtLock:analysis?.rawSignalScores?._whipsaw??null,
          isHighVolAtLock:analysis?.rawSignalScores?._isHighVol??null,
          isTrendAtLock:analysis?.rawSignalScores?._isTrend??null,
          isChopAtLock:analysis?.rawSignalScores?._isChop??null,
          isCompressingAtLock:analysis?.rawSignalScores?._isCompressing??null,
          priceAboveMedianAtLock:analysis?.rawSignalScores?._priceAboveMedian??null,
          // V12.0: lock timing — secondsIntoWindow was in the CSV header since V9.7.6
          //   but stamped at only 2% (most paths never wrote it). Compute from timeState
          //   which is always in scope. atSecondsLeft = inverse (time remaining at lock).
          secondsIntoWindow:(()=>{try{const _tot=windowType==='15m'?900:300;const _rem=Math.max(0,(timeState.minsRemaining||0)*60+(timeState.secsRemaining||0));return Math.round(Math.max(0,_tot-_rem));}catch(_){return null;}})(),
          atSecondsLeft:(()=>{try{return Math.round(Math.max(0,(timeState.minsRemaining||0)*60+(timeState.secsRemaining||0)));}catch(_){return null;}})(),
          // V12.0: Kalshi price age (V11.2.6 stamps this on snapshot; forward to entry).
          kalshiPriceAgeMs:snapshot.kalshiPriceAgeMs??null,
          // V12.0: last60sDriftBps — was in CSV header but never stamped on entries.
          last60sDriftBps:analysis?.last60sDriftBps??analysis?.last60sDrift??null,
          smcSweepScore:analysis?.rawSignalScores?._sweep??null,
          smcFvgScore:analysis?.rawSignalScores?._fvg??null,
          fastLockFired:_fastLockFired===true||null,
          earlyLockFired:_earlyLockFired===true||null,
          earlyLockTier:_earlyLockTier||null,
          // V12.6: stamp build version on every entry — tells us which device/build
          //   fired the lock. 0% stamp rate on 'version' = all entries came from stale
          //   cached builds. Hard-refresh every device when deploying.
          taraVersion:TARA_BUILD_VERSION,
          device:_taraDeviceId,
          htDir:analysis?.rawSignalScores?._trendConfirm?.htDir||null,
          stDir:analysis?.rawSignalScores?._trendConfirm?.stDir||null,
          trendAligned:analysis?.rawSignalScores?._trendConfirm?.aligned===true?'Y':(analysis?.rawSignalScores?._trendConfirm?(analysis.rawSignalScores._trendConfirm.conflict?'CONFLICT':'N'):null),
          trendConfirmScore:analysis?.rawSignalScores?._trendConfirmScore??null,
          reversalDamperApplied:analysis?.diagnosticsV97?.reversalDamper?.applied||false,
          reversalDamperMult:analysis?.diagnosticsV97?.reversalDamper?.mult??1.0,
        };
        setTaraCallLog(prev=>{
          // V9.10.4: per-asset dedup so BTC + ETH snapshots for the same window slot
          //   don't clobber each other. Was bug: when both assets locked the same
          //   window, second one's entry got silently dropped.
          const _logAsset=_entry.asset||'BTC';
          const _existingIdx=prev.findIndex(e=>e&&e.windowId===_wid&&e.windowType===windowType&&(e.asset||'BTC')===_logAsset);
          const _existing=_existingIdx>=0?prev[_existingIdx]:null;
          if(_existing){
            // V10.9.19 FIX: dedup must not permanently block a later REAL lock.
            //   Bug (seen 2026-06-15): Tara logs SIT_OUT early in a window, then forms
            //   a genuine directional lock later in the SAME window — the second entry
            //   matched the existing SIT_OUT and was dropped (return prev), so the real
            //   trade never registered. This is the "locks missing / registering wrong"
            //   report. A SIT_OUT (or an unresolved/leaner entry) is NOT a true duplicate
            //   of a later directional commit — it should be UPGRADED, not blocked.
            const _exCall=_existing.call||_existing.dir||'SIT_OUT';
            const _newCall=_entry.call||_entry.dir||'SIT_OUT';
            const _exResolved=_existing.result==='WIN'||_existing.result==='LOSS';
            const _newDirectional=_newCall==='UP'||_newCall==='DOWN';
            const _exSitout=_exCall==='SIT_OUT';
            // Upgrade conditions (replace existing with new):
            //   1. existing is SIT_OUT and new is a real directional call, OR
            //   2. existing is unresolved and new carries a resolved result, OR
            //   3. both directional same dir but new is richer (has signalScores, existing doesn't)
            const _shouldUpgrade=
              (!_exResolved && _exSitout && _newDirectional) ||
              (!_exResolved && (_entry.result==='WIN'||_entry.result==='LOSS')) ||
              (!_exResolved && _exCall===_newCall && !_existing.signalScoresAtLock && !!_entry.signalScoresAtLock);
            if(_shouldUpgrade){
              try{_lifecycleRecordLogResult(_wid,_logAsset,'upgraded',{from:_exCall,to:_newCall,existingId:_existing.id});}catch(_){}
              try{_lifecycleRecordConfirmed(_wid,_logAsset);}catch(_){}
              // Preserve the existing id/timestamp so cloud/reconcile keep tracking the
              //   same record, but take the new (richer/directional) content.
              const _merged={..._entry,id:_existing.id||_entry.id,time:_existing.time||_entry.time};
              const _next=[...prev];
              _next[_existingIdx]=_merged;
              return _next;
            }
            // Otherwise: genuine duplicate — audit + block as before.
            _auditLogEvent('logSnapshotEntry-dedup-blocked',{
              source:'_logSnapshotEntry',
              wid:_wid,
              asset:_logAsset,
              windowType,
              newEntry:{dir:_entry.dir,tier:_entry.tier,confidence:_entry.confidence,result:_entry.result,time:_entry.time},
              existingEntry:{id:_existing.id,dir:_existing.dir,tier:_existing.tier,result:_existing.result,time:_existing.time},
            });
            try{_lifecycleRecordLogResult(_wid,_logAsset,'dedup-blocked',{existingId:_existing.id,existingResult:_existing.result});}catch(_){}
            try{_lifecycleRecordConfirmed(_wid,_logAsset);}catch(_){}
            return prev;
          }
          // V10.7.45: This is the success path. Mark lifecycle.
          try{_lifecycleRecordLogResult(_wid,_logAsset,'pushed',null);_lifecycleRecordConfirmed(_wid,_logAsset);}catch(_){}
          return [...prev,_entry].slice(-TARA_CALL_LOG_CAP);
        });
        // V10.7.8 — DEFENSIVE RETRY for rollover race condition
        //   User report: "rollover window doesnt get logged or saved properly sometimes.
        //   it still scans and locks though. refreshing the page works."
        //   The initial setTaraCallLog can get its update lost during rollover state
        //   churn (cloud-restore re-arm, refs clearing). Defensive retry 800ms later
        //   re-attempts the add. Dedup logic above is the no-op fallback — if the
        //   first push succeeded, retry sees the existing entry and returns prev.
        setTimeout(()=>{
          try{
            setTaraCallLog(prev=>{
              const _logAsset=_entry.asset||'BTC';
              const _existing=prev.find(e=>e&&e.windowId===_wid&&e.windowType===windowType&&(e.asset||'BTC')===_logAsset);
              if(_existing)return prev; // first push succeeded, dedup catches retry
              // First push was lost to rollover race — re-add now
              try{console.info('[V10.7.8] rollover-race retry rescued entry',{wid:_wid,asset:_logAsset,dir:_entry.dir,tier:_entry.tier});}catch(_){}
              _auditLogEvent('v10_7_8_rollover_retry_rescued',{
                source:'_logSnapshotEntry-retry',
                wid:_wid,
                asset:_logAsset,
                dir:_entry.dir,
                tier:_entry.tier,
                msSinceOriginal:800,
              });
              // V10.7.45: Mark lifecycle as rescued
              try{_lifecycleRecordLogResult(_wid,_logAsset,'retry-rescued-800ms',null);_lifecycleRecordConfirmed(_wid,_logAsset);}catch(_){}
              return [...prev,_entry].slice(-TARA_CALL_LOG_CAP);
            });
          }catch(_){}
        },800);
        // V10.7.8 — second retry at 2500ms in case the 800ms one ALSO got lost to a
        //   prolonged rollover state propagation. Three chances is enough; if all fail
        //   there's a deeper problem worth investigating.
        setTimeout(()=>{
          try{
            setTaraCallLog(prev=>{
              const _logAsset=_entry.asset||'BTC';
              const _existing=prev.find(e=>e&&e.windowId===_wid&&e.windowType===windowType&&(e.asset||'BTC')===_logAsset);
              if(_existing)return prev;
              try{console.warn('[V10.7.8] rollover-race second-retry rescued entry — first retry also lost',{wid:_wid,asset:_logAsset});}catch(_){}
              _auditLogEvent('v10_7_8_rollover_retry_2_rescued',{
                source:'_logSnapshotEntry-retry-2',
                wid:_wid,
                asset:_logAsset,
                dir:_entry.dir,
                tier:_entry.tier,
                msSinceOriginal:2500,
              });
              // V10.7.45: Mark lifecycle as rescued
              try{_lifecycleRecordLogResult(_wid,_logAsset,'retry-rescued-2500ms',null);_lifecycleRecordConfirmed(_wid,_logAsset);}catch(_){}
              return [...prev,_entry].slice(-TARA_CALL_LOG_CAP);
            });
          }catch(_){}
        },2500);
      }catch(e){
        try{console.error('[V7.10.1] _logSnapshotEntry failed',e);}catch(_){}
        // V9.10.7: surface caught throws to audit log. Before this, _logSnapshotEntry
        //   failures showed up only in DevTools console — invisible after the fact.
        _auditLogEvent('logSnapshotEntry-threw',{
          source:'_logSnapshotEntry',
          error:e?.message||String(e),
          stack:(e?.stack||'').split('\n').slice(0,5).join('\n'),
          snapshotCall:snapshot?.call,
          snapshotTier:snapshot?.tier,
          snapshotConfidence:snapshot?.confidence,
          snapshotNoGoCategory:snapshot?.noGoCategory||null,
        });
      }
    };
    // V7.9: NO-GO CLASSIFIER. Three explicit no-go categories. These are the only cases
    //   where sitting out is mathematically correct (vs stylistic timidity which V7.8 killed):
    //     (a) NEGATIVE EDGE: Kalshi has already priced the move; paying spread to be correct.
    //     (b) DATA UNAVAILABLE: price feed stale or Kalshi missing — calling without data.
    //     (c) NO RESOLUTION TIME: ≤20s remaining + true coin-flip posterior + tight gap.
    //   These render with a different visual treatment (red NO TRADE chip, no entry button).
    //   Distinguished from V7.8 caution commits which still let user enter at their discretion.
    let _noGoReason=null;
    let _noGoTier=null;
    let _noGoEdgeThreshold=null; // V10.7.87: hoisted so snapshot can stamp it
    let _earlyFlatGapProof=null;
    // V7.10.3: gated on _cloudRestoreCompletedRef.current to prevent commit-before-cloud-restore.
    if(taraCallSnapshotRef.current===null&&_postKnown&&_commitDir&&_cloudRestoreCompletedRef.current){
      // V10.3.0 — MID-RANGE SIT-OUT: in healthy RANGE-CHOP, if price is mid-range
      //   (PIR 30-70) and no fade signal fired, there's no edge. Force sit-out.
      //   The engine flagged this via _v10_3_0_midRangeSitout. Only fires when
      //   range is healthy (not breaking) — broken-range chop falls through to
      //   normal logic in case the chop is transitioning to trend.
      // V10.3.1 — CONVICTION GATE: don't sit-out mid-range when Tara has earned
      //   genuine high conviction (≥75% or ≤25%). Audit showed RANGE-CHOP at
      //   75-84% conviction wins 71% (n=31) — these are legitimate locks. The
      //   mid-range sit-out should only apply when conviction is ALSO weak
      //   (<70% AND >30%), meaning truly no edge in any direction.
      const _midRangeChopConvWeak=_commitConf!=null&&_commitConf<70&&_commitConf>30;
      if(analysis?._v10_3_0_midRangeSitout===true&&!analysis?._v10_3_0_rangeFadeFired&&_midRangeChopConvWeak){
        // V10.7.5 — dead-window-only sitout rule (mid-range chop guard)
        const _wa=analysis?.windowAmplitude;
        const _rangeBps=_wa?.rangeBps||0;
        const _isDeadWindow=_wa?.label!=='OPENING'&&_rangeBps<5;
        // V10.7.90: WEAK-GAP GATE on directional-lock.
        //   Audit (last 150 trades): directional-lock tier = 49% WR (38W/39L) — a coin
        //   flip that bleeds EV after entry cost. Root cause: it fires in RANGE-CHOP
        //   with no real gap signal. Breakdown by gap strength: flat gap (-5..3) = 44%,
        //   gap mid (10-20) = 59%, gap strong (20+) = 65%. The directional-lock only
        //   wins when gap confirms. So: if gap is weak (<10 aligned), sit out.
        // V10.7.97 FIX: was Math.abs(_gapAligned)<10 which only caught FLAT gap.
        //   Opposed gap (gap aligned = -27) has abs 27 so it PASSED the gate and locked.
        //   Audit (1865 trades): directional-lock gap opposed (<-10) = 29% WR, -12¢ EV.
        //   directional-lock gap flat (-10..10) = 52% WR. gap mid (10-25) = 66%. strong = 71%.
        //   The lock only earns its keep at gap >= 10 ALIGNED. Drop Math.abs so anything
        //   below +10 aligned (flat OR opposed) sits out. Keeps the 66-71% buckets.
        const _gapAligned=Number(analysis?.rawSignalScores?.gap||0)*(_post>=50?1:-1);
        const _gapTooWeakForLock=_gapAligned<10;
        if(!_isDeadWindow&&_gapTooWeakForLock){
          // Gap flat or opposed — directional-lock is 29-52% WR here. Sit out.
          const _gapDesc=_gapAligned<-10?'opposed':'flat';
          const _sitSnap={
            call:'SIT_OUT',direction:'SIT_OUT',
            dir:'SIT_OUT',_intendedDir:claimedDir||(_post>=50?'UP':'DOWN'), // V10.9.25: analytics
            confidence:_commitConf,
            caution:`Gap ${_gapDesc} (${_gapAligned.toFixed(0)}pt) in range-chop — directional-lock is ${_gapAligned<-10?'29':'52'}% WR here, sit out (V10.7.97)`,
            reason:`V10.7.97 gap guard: directional-lock needs gap>=10 aligned, got ${_gapAligned.toFixed(0)}pt (${_gapDesc})`,
            wasOverriddenNoTrade:true,
            noGoCategory:'weak-gap-directional-sitout',
            atSecondsLeft:timeState.minsRemaining*60+timeState.secsRemaining,
            atPosterior:_post,kalshiAtLock:_kPctNow,
            locked:true,earlyLock:false,
            isConfluent:false,isSuperConfluent:false,isRisingConfluence:false,isTapeLed:false,isStructuralLed:false,
            samples:0,needSamples:0,
            tier:'weak-gap-sitout',
            session:(typeof getMarketSessions==='function'?getMarketSessions():{}).dominant||'UNKNOWN',
            regime:analysis?.regime||'',
            qScore:Math.round(_qFast),
            fgt:analysis?.mtfAlignment,
            _committedAt:Date.now(),
            _v10790_gapAligned:_gapAligned,
          };
          taraCallSnapshotRef.current=_applyNoSitout(_sitSnap);
          _logSnapshotEntry(taraCallSnapshotRef.current);
          _persistLock();
          return;
        }
        if(!_isDeadWindow){
          // Strong-enough gap (|gap|>=10) — keep the original directional lock.
          const _v107_6=_v10_7_6_reversalCheck(_post,analysis);
          // V10.8.2 FIX: _v10_7_6_reversalCheck returns dir=null when posterior=50.
          //   This caused call=null on the snapshot, which blocked ML field stamping
          //   and cushion history (both gate on call!=='SIT_OUT' and non-null call).
          //   Fallback to claimedDir (the direction the engine settled on) when null.
          const _forceDir=_v107_6.dir||claimedDir||(_post>=50?'UP':'DOWN');
          const _forceConf=_v107_6.conf||_commitConf||50;
          const _forceSnap={
            call:_forceDir,direction:_forceDir,
            confidence:_forceConf,
            caution:`directional lock · ${_forceDir} · ${_rangeBps.toFixed(0)}bps range momentum`,
            reason:`Directional lock · ${_forceDir} ${(_forceConf-50).toFixed(0)}pt · structural momentum in ${_rangeBps.toFixed(0)}bps window`,
            atSecondsLeft:timeState.minsRemaining*60+timeState.secsRemaining,
            atPosterior:_post,
            kalshiAtLock:_kPctNow,
            locked:true,earlyLock:false,
            isConfluent:false,isSuperConfluent:false,isRisingConfluence:false,isTapeLed:false,isStructuralLed:false,
            samples:0,needSamples:0,
            tier:'directional-lock',
            session:(typeof getMarketSessions==='function'?getMarketSessions():{}).dominant||'UNKNOWN',
            regime:analysis?.regime||'',
            qScore:Math.round(_qFast),
            fgt:analysis?.mtfAlignment,
            _committedAt:Date.now(),
            _v10_7_5_forced:true,
            _v10_7_5_originalGuard:'V10.3.0-midrange',
            _v10_7_6_reversalFlipped:_v107_6.flipped,
            _v10_7_6_revVotes:_v107_6.revVotes,
            _v10_7_6_revReasons:_v107_6.reasons,
            _v10_7_6_originalDir:_v107_6.flipped?_v107_6.originalDir:null,
            _v10_7_5_rangeBps:_rangeBps,
            _v10790_gapAligned:_gapAligned,
            _v112Timing:(_v112Live.at&&Date.now()-_v112Live.at<30000?_v112Live.timing:null),
            _v112Why:(_v112Live.at&&Date.now()-_v112Live.at<30000?_v112Live.why:null),
          };
          taraCallSnapshotRef.current=_applyNoSitout(_forceSnap);
          _logSnapshotEntry(taraCallSnapshotRef.current);
          _persistLock();
          return;
        }
        const _rd=analysis?._v10_3_0_rangeData||{};
        const _sitSnap={
          call:'SIT_OUT',direction:'SIT_OUT',
          confidence:_commitConf||0,
          caution:`Mid-range chop — PIR ${_rd.pir||'?'}% (range $${_rd.rangeLow||'?'}-$${_rd.rangeHigh||'?'}), no edge in middle of range → sit out`,
          reason:`V10.3.0 mid-range sit-out: PIR ${_rd.pir}% in healthy chop range, no fade signal`,
          wasOverriddenNoTrade:true,
          noGoCategory:'mid-range-chop-sitout',
          atSecondsLeft:timeState.minsRemaining*60+timeState.secsRemaining,
          atPosterior:_post,
          kalshiAtLock:_kPctNow,
          locked:true,earlyLock:false,
          isConfluent:false,isSuperConfluent:false,isRisingConfluence:false,isTapeLed:false,isStructuralLed:false,
          samples:0,needSamples:0,
          tier:'mid-range-chop-sitout',
          session:(typeof getMarketSessions==='function'?getMarketSessions():{}).dominant||'UNKNOWN',
          regime:analysis?.regime||'',
          qScore:Math.round(_qFast),
          fgt:analysis?.mtfAlignment,
          _committedAt:Date.now(),
        };
        taraCallSnapshotRef.current=_applyNoSitout(_sitSnap);
        _logSnapshotEntry(taraCallSnapshotRef.current);
        _persistLock();
        return;
      }
      // (a) Legacy NEGATIVE EDGE — honor the same entry-filter switch as timing.
      // The explicit Call cost band and physical/data qualification remain live.
      if(!NO_ENTRY_GATES&&_kPctNow!=null&&_commitConf!=null){
        const _kalshiForCommit=_commitDir==='UP'?_kPctNow:(100-_kPctNow);
        const _edgeAtCommit=_commitConf-_kalshiForCommit;
        // V10.8.4: FLAT NO-GO-EDGE THRESHOLD — removed session-based adjustments.
        //   Session-aware thresholds (-15pt Asia, -12pt Asia late, -9pt EU preopen)
        //   were added to handle thin order books in off-hours. But they had a side
        //   effect: in liquid NY sessions, the flat -7pt allowed trades where Kalshi
        //   had correctly priced the edge away.
        //   User guidance: judge every window on its own merit, not what time it is.
        //   Threshold: -7pt flat. Thin OB (<$500K): -3pt (was -7pt-4pt = -11pt). 
        const _obDepthUSD=(depthFlash?.liqLongUSDLive||0)+(depthFlash?.liqShortUSDLive||0);
        const _isThinBook=_obDepthUSD>0&&_obDepthUSD<500000;
        const _thresh=_isThinBook?-3:-7; // thin book: wider sitout, liquid: standard
        if(_edgeAtCommit<=_thresh){
          _noGoEdgeThreshold=_thresh;
          _noGoReason=`Edge ${Math.round(_edgeAtCommit)}pt vs Kalshi — market priced ${_commitDir} at ${Math.round(_kalshiForCommit)}%`
            +(_isThinBook?` (thin book $${Math.round(_obDepthUSD/1000)}K depth)`:'')+`, threshold ${_thresh}pt`;
          _noGoTier='no-go-edge';
        }
      }
      // (a2) V10.7.90 — NY PRE-OPEN DEAD ZONE (12:00-13:00 UTC).
      // (b) DATA UNAVAILABLE — price feed stale (>30s) or Kalshi missing
      if(!_noGoReason){
        const _priceStaleness=lastPriceSourceRef.current?.time?(Date.now()-lastPriceSourceRef.current.time):Infinity;
        // V10.7.73b: Raised stale threshold from 30s → 60s.
        //   30s was too aggressive — brief API hiccups or BRTI proxy latency spikes
        //   were falsely triggering no-go-data on BRTI feed, creating phantom
        //   "OVERRIDE (no-trade)" Discord messages the user never initiated.
        const _priceStale=_priceStaleness>60000;
        const _kalshiMissing=_kPctNow==null;
        if(_priceStale||_kalshiMissing){
          const _which=[];
          if(_priceStale)_which.push(`price feed ${Math.round(_priceStaleness/1000)}s stale`);
          if(_kalshiMissing)_which.push('Kalshi feed unavailable');
          _noGoReason=`Data integrity issue — ${_which.join(', ')}`;
          _noGoTier='no-go-data';
        }
      }
      // (c) NO RESOLUTION TIME — late-window true coin flip with no directional resolution
      if(!_noGoReason){
        const _secsLeftLocal=remaining; // available in this scope
        const _gapAbsBpsLocal=targetMargin>0?Math.abs((currentPrice-targetMargin)/targetMargin*10000):0;
        if(_secsLeftLocal<=20&&_post>=47&&_post<=53&&_gapAbsBpsLocal<=2){
          _noGoReason=`No resolution time — ${_secsLeftLocal}s left, posterior ${Math.round(_post)}%, gap ${_gapAbsBpsLocal.toFixed(1)}bps from strike`;
          _noGoTier='no-go-coinflip-late';
        }
      }
      // (d) FLAT GAP WATCH — V10.7.81
      //   Instead of sitting out flat-gap windows immediately, Tara watches for gap
      //   to develop. Lock when: gap≥8pt AND entry≤68¢ AND ≥5min left.
      //   Sit out when: time runs out (<5min) with gap still flat.
      if(!_noGoReason){
        const _gapScore=Math.abs(Number(analysis?.rawSignalScores?.gap)||0);
        const _isChopRegime=analysis?.regime==='RANGE-CHOP'||analysis?.regime==='COMPRESSING';
        const _extSignal=Math.max(
          Math.abs(Number(analysis?.rawSignalScores?.liqHeatmap)||0),
          Math.abs(Number(analysis?.rawSignalScores?.deribitOptions)||0),
          Math.abs(Number(analysis?.rawSignalScores?.liqSignal)||0)
        );
        const _secsLeft=timeState.minsRemaining*60+timeState.secsRemaining;
        const _curWid=computeWindowId(windowType);
        // V10.7.90: raised flat-gap watch trigger from gap<5 to gap<8.
        //   Audit: gap weak (3-10) band = 33% WR. Old <5 threshold let 5-8 through
        //   to directional-lock where it lost. Now the watch covers it.
        if(_isChopRegime&&_gapScore<8&&_extSignal<8){
          if(_flatGapWatchRef.current?.windowId!==_curWid){
            _flatGapWatchRef.current={windowId:_curWid,startedAt:Date.now()};
          }
          const _gapDeveloped=_gapScore>=8;
          const _activeDirWatch=tc?.call==='UP'||tc?.call==='DOWN'?tc.call:(tc?.direction||null);
          const _kNowWatch=typeof kalshiYesPrice!=='undefined'&&kalshiYesPrice!=null?Number(kalshiYesPrice):null;
          const _kalshiForDirWatch=(_kNowWatch!=null&&_activeDirWatch)?(_activeDirWatch==='UP'?_kNowWatch:(100-_kNowWatch)):null;
          const _entryOK=_kalshiForDirWatch!=null&&_kalshiForDirWatch<=68;
          if(_gapDeveloped&&_entryOK&&_secsLeft>=300){
            // Gap developed with good odds — allow lock, fall through normally
            _flatGapWatchRef.current=null;
          } else if(_secsLeft<300){
            // Time ran out flat — sit out
            const _watchAge=Math.round((Date.now()-(_flatGapWatchRef.current?.startedAt||Date.now()))/1000);
            _noGoReason=`Flat gap watched ${_watchAge}s — never developed (${_gapScore.toFixed(0)}pt at close)`;
            _noGoTier='no-go-edge';
          } else {
            // Strong, cheap early views may continue to the NORMAL tier/sample
            // checks using separate fresh confirmations. This never creates a
            // direction, reuses an old signal, or forces a timer-based Call.
            const _earlyBand=readCallCostBand(localStorage);
            const _early=observeEarlyFlatGap(_flatGapWatchRef.current.earlyConfirmation,{
              asset:currentAssetRef.current||'BTC',windowType,windowId:_curWid,now:Date.now(),
              direction:tc.call,source:priceSourceRef.current,ready:startupReadiness.ready,
              score:tc.confidence,probabilityUp:analysis?.rawProbAbove,mtfAlignment:analysis?.mtfAlignment,
              tapeWindows,quote:_kalshiQuote,spotAt:lastPriceSourceRef.current?.time,
              referencePrice:analysis?.referencePrice??currentPrice,strike:targetMargin,
              minCost:_earlyBand.minCost,maxCost:_earlyBand.maxCost,
              candidateQualified:isCall&&!tc.startupBlocked&&!tc.isNoGo&&!tc.wasOverriddenNoTrade});
            _flatGapWatchRef.current.earlyConfirmation=_early.state;
            if(_early.eligible&&_early.fresh){
              _earlyFlatGapProof=_early;
            }else{
              _noteGateHold('flat-gap-signal',`Waiting for a directional gap: ${_gapScore.toFixed(1)}pt, need 8pt or external signal ≥8pt.`,
                {gapScore:_gapScore,requiredGap:8,externalSignal:_extSignal,requiredExternalSignal:8,secondsLeft:_secsLeft,
                  earlyConfirmation:_early.reason});
              return;
            }
          }
        } else if(_flatGapWatchRef.current?.windowId===_curWid){
          _flatGapWatchRef.current=null;
        }
      }
      // (e) MODERATE-GAP-IN-CHOP SIT-OUT -- V13.0
      //   Most-repeated finding across all history: gap 10-25bps from strike + Kalshi
      //   priced 55-65c on our side + CHOP = -1.2c/trade over n=262. At 55-65c the edge
      //   is often marginally positive, so these slip past no-go-edge; the gap is moderate
      //   (not <8 score) so flat-gap watch never catches them; and it is not a range-
      //   position cut so mid-range-chop-sitout misses them too. Genuine coverage gap. In
      //   chop a moderate gap at mid-odds is a coin flip you overpay for: price oscillates
      //   back across the strike before close.
      //   Uses the RELIABLE chop classifier (_isChop/_isHighVol), NOT analysis.regime
      //   (stuck on RANGE-CHOP ~90% of windows -- would fire everywhere).
      //   Builds a real SIT_OUT (the default _noGoReason path commits-with-caution, it
      //   does not sit out). Overridable when there is strong confirmation. Bands are
      //   named constants -- tune after the next CSV shows live fire-rate + WR.
      if(!_noGoReason){
        const _MGC_GAP_LO=10,_MGC_GAP_HI=25;   // moderate gap band (bps from strike)
        const _MGC_K_LO=55,_MGC_K_HI=65;        // mid-odds band (cents, our side)
        const _mgcRss=analysis?.rawSignalScores||{};
        const _mgcChop=_mgcRss._isChop===true||_mgcRss._isHighVol===true;
        const _mgcGapBps=targetMargin>0?Math.abs((currentPrice-targetMargin)/targetMargin*10000):0;
        const _mgcKForDir=_commitDir==='UP'?_kPctNow:(_kPctNow!=null?(100-_kPctNow):null);
        const _mgcInGap=_mgcGapBps>=_MGC_GAP_LO&&_mgcGapBps<=_MGC_GAP_HI;
        const _mgcInOdds=_mgcKForDir!=null&&_mgcKForDir>=_MGC_K_LO&&_mgcKForDir<=_MGC_K_HI;
        if(_mgcChop&&_mgcInGap&&_mgcInOdds&&_commitDir){
          // Override paths -- strong confirmation rescues the trade.
          const _mgcTC=_mgcRss._trendConfirm||null;
          const _mgcTapeWith=(tapeStronglyAgrees||tapeSuperStrong)===true;
          const _mgcTrendWith=!!(_mgcTC&&_mgcTC.strongAligned&&_mgcTC.dir===_commitDir);
          const _mgcEdge=(_commitConf!=null&&_mgcKForDir!=null)?(_commitConf-_mgcKForDir):0;
          const _mgcMispriced=_mgcGapBps>=20&&_mgcEdge>=5;
          const _mgcOverride=_mgcTapeWith||_mgcTrendWith||_mgcMispriced;
          if(!_mgcOverride){
            const _mgcSit={
              call:'SIT_OUT',direction:'SIT_OUT',
              confidence:_commitConf||0,
              caution:`Moderate gap in chop -- ${_mgcGapBps.toFixed(0)}bps off strike, Kalshi ${Math.round(_mgcKForDir)}c on ${_commitDir}, chop regime -> coin-flip you'd overpay for (sit out)`,
              reason:`V13.0 mod-gap-chop sit-out: ${_mgcGapBps.toFixed(0)}bps gap, ${Math.round(_mgcKForDir)}c, chop (-1.2c/trade bucket, n=262)`,
              wasOverriddenNoTrade:true,
              noGoCategory:'mod-gap-chop-sitout',
              atSecondsLeft:timeState.minsRemaining*60+timeState.secsRemaining,
              atPosterior:_post,
              kalshiAtLock:_kPctNow,
              locked:true,earlyLock:false,
              isConfluent:false,isSuperConfluent:false,isRisingConfluence:false,isTapeLed:false,isStructuralLed:false,
              samples:0,needSamples:0,
              tier:'mod-gap-chop-sitout',
              session:(typeof getMarketSessions==='function'?getMarketSessions():{}).dominant||'UNKNOWN',
              regime:analysis?.regime||'',
              qScore:Math.round(_qFast),
              fgt:analysis?.mtfAlignment,
              _committedAt:Date.now(),
              _mgcGapBps:Math.round(_mgcGapBps),
              _mgcKForDir:Math.round(_mgcKForDir),
              _mgcEdge:Math.round(_mgcEdge),
            };
            try{console.info('[V13.0 MOD-GAP-CHOP] sit-out: '+_mgcGapBps.toFixed(0)+'bps, '+Math.round(_mgcKForDir)+'c on '+_commitDir+', chop');}catch(_){}
            taraCallSnapshotRef.current=_applyNoSitout(_mgcSit);
            _logSnapshotEntry(taraCallSnapshotRef.current);
            _persistLock();
            return;
          }
        }
      }
      if(_noGoReason){
        // V13.4.326 REMOVED: the V8.9.3 "true coin flip" exception used to
        //   silently `return` here with no snapshot at all -- no log entry,
        //   no tier name, invisible to every check in this file -- whenever
        //   the late-window posterior sat at 47-53% with a near-zero gap.
        //   That was a real, explicit decision gate ("sample more, don't fake
        //   conviction"), just an unusually well-justified one at the time.
        //   Per the current, explicit instruction to remove every gate and
        //   let the engine take every window at whatever odds it actually
        //   sees, this is superseded: removing the early return lets
        //   execution fall through to the normal tier-ladder/sample-
        //   accumulation logic below, same as any other tick -- it does not
        //   force an immediate decision, it just stops hard-blocking this one
        //   narrow case from ever reaching that logic at all.
        //
        //   Cases (a) edge-bad and (b) data-missing still commit per V8.9.2 — those
        //   have a clear directional read, just disadvantageous price or unreliable
        //   data. Surfaced as cautions.
        // V10.3.0 — NO-GO-EDGE IN CHOP → SIT_OUT, not warned-lock.
        //   Empirical data from May 16 batch: 10 no-go-edge trades in RANGE-CHOP
        //   resulted in 7W/3L = 70% WR, BUT -$0.128 per trade in real money because
        //   the asymmetric Kalshi payout (win $0.16, lose $0.80) requires 83%+ WR
        //   to break even. Locking these is mathematically negative EV.
        //   Fix: in RANGE-CHOP, no-go-edge converts to SIT_OUT. Outside chop, keep
        //   warned-lock behavior (trends can still be tradeable with thin edge).
        // V10.7.86: NO-GO-EDGE CHOP SITOUT REMOVED.
        //   V10.3.0 original audit: 7W/3L = 70% WR at ~80¢ avg cost — negative EV.
        //   V10.7.86 audit (n=83 over 1678 trades): 87% WR at avg 85¢ entry = +$11.75/trade EV.
        //   At every edge level (-5 to -20+pt) WR is 80-91%. Kalshi being priced ahead
        //   of Tara in RANGE-CHOP = Kalshi is consistently WRONG. This gate was blocking
        //   free money. Removed. Falls through to standard warned-lock below (line ~40046).
        //   Kept the dead-window detection as a guard for truly dead windows only.
        if(_noGoTier==='no-go-edge'&&analysis?.regime==='RANGE-CHOP'){
          const _wa=analysis?.windowAmplitude;
          const _rangeBps=_wa?.rangeBps||0;
          const _isDeadWindow=_wa?.label!=='OPENING'&&_rangeBps<2; // tighter dead-window threshold
          if(_isDeadWindow){
            // Truly dead window (<2bps range) — still sit out, no edge possible
            const _sitSnap={
              call:'SIT_OUT',direction:'SIT_OUT',
              confidence:_commitConf,
              caution:`Dead window (${_rangeBps.toFixed(1)}bps range) + ${_noGoReason} — sitting out`,
              reason:`V10.7.86 dead-window sitout: ${_noGoReason}`,
              wasOverriddenNoTrade:true,
              noGoCategory:'no-go-edge-dead-window',
              atSecondsLeft:timeState.minsRemaining*60+timeState.secsRemaining,
              atPosterior:_post,
              kalshiAtLock:_kPctNow,
              locked:true,earlyLock:false,
              isConfluent:false,isSuperConfluent:false,isRisingConfluence:false,isTapeLed:false,isStructuralLed:false,
              samples:0,needSamples:0,
              tier:'no-go-edge-dead-window',
              session:(typeof getMarketSessions==='function'?getMarketSessions():{}).dominant||'UNKNOWN',
              regime:analysis?.regime||'',
              qScore:Math.round(_qFast),
              fgt:analysis?.mtfAlignment,
              _committedAt:Date.now(),
            };
            taraCallSnapshotRef.current=_applyNoSitout(_sitSnap);
            _logSnapshotEntry(taraCallSnapshotRef.current);
            _persistLock();
            return;
          }
          // Non-dead window: fall through to warned-lock below
        }
        // V8.9.2: REMOVED no-trade gating for cases (a)+(b). User mandate: every round
        //   Tara picks a direction; the original no-go reason becomes a CAUTION badge
        //   so the user knows to be skeptical, but they get the call.
        //
        // V13.4.111: this commit path (no-go-edge / directional-lock / etc) is COMPLETELY
        //   SEPARATE from the entry-band gate at ~L39463 -- different function, _commitDir/
        //   _commitConf defined at L41412, never passes through _eqOn/_tooExpensive/
        //   _tooCheap at all. Confirmed from real data: 10 of 12 locks stamped 13.4.107-110
        //   landed here at 61-85c, all tagged no-go-edge/time-cap-commit/directional-lock,
        //   none carrying _trajBypassFired -- this is not the TRAJ-early bypass firing as
        //   intended, it is a second lock path the band was never wired to. The V8.9.2
        //   'always commit' mandate is preserved -- only the PRICE is now checked, same
        //   band/dials as the main gate, same TRAJ-early exemption if that is genuinely why
        //   the price is being accepted.
        // V13.4.161 FIX: Number(null) is 0, not NaN, so a genuinely MISSING price
        //   (_kPctNow===null, the exact case that sets _noGoReason to "Kalshi feed
        //   unavailable" a few lines above) was passing Number.isFinite() as if it
        //   were a real 0c quote, then converting to 100c for a DOWN call. Real
        //   screenshot: "[V13.4.111] DOWN 63% wanted to lock at 100c -- outside
        //   25-75c band (orig: Data integrity issue -- Kalshi feed unavailable)" --
        //   there was no 100c quote; there was no quote at all. Require _kPctNow to
        //   actually be a number before deriving a cost from it.
        const _ngK=(typeof _kPctNow==='number')?_kPctNow:NaN;
        const _ngCost=Number.isFinite(_ngK)?(_commitDir==='UP'?_ngK:(100-_ngK)):null;
        const _ngOn=(function(){try{return localStorage.getItem('taraEntryQuality')!=='off';}catch(_eng1){return true;}})();
        const _ngMin=getEntryMinCost();
        const _ngMax=getEntryMaxCost();
        const _ngTrajOn=(function(){try{return localStorage.getItem('taraTrajEarlyLock')!=='off';}catch(_eng4){return true;}})();
        const _ngTrajGap=Number(analysis?.projectedGapBps)||0;
        const _ngTrajDir=_ngTrajGap>0?'UP':(_ngTrajGap<0?'DOWN':null);
        const _ngTrajAgrees=_ngTrajOn&&_ngTrajDir===_commitDir&&_ngTrajGap!==0;
        // V13.4.161: a genuinely missing price (_ngCost null because _kPctNow was
        //   null, not because the guard is off) is the SAME "missing price" case
        //   V13.4.132 already sits out for on the universal cost guard above --
        //   "a missing price isn't a reason to skip the gate, it's the strongest
        //   reason to trigger it". This path previously fell through to a BLIND
        //   commit (real direction, kalshiAtLock:null, no idea what it costs) any
        //   time the band check couldn't run. Match the established precedent.
        // V13.4.326: dropped the _ngOn dependency here specifically. This is
        //   the one genuine physical-impossibility case (no price at all, so
        //   nothing can be sized or costed) -- it shouldn't be tied to the
        //   taraEntryQuality toggle, an unrelated cost-band setting. If that
        //   toggle is ever set to 'off', this null-price guard must still
        //   fire; _ngOn stays governing the actual cost-band check below,
        //   which is a real quality decision and correctly neutralized by it.
        if(_ngCost==null&&_kPctNow==null){
          const _ngNoDataSnap={
            call:'SIT_OUT',direction:null,confidence:0,
            caution:null,
            reason:`${_commitDir} ${_commitConf}% wanted to lock but no Kalshi price is available -- sitting out rather than commit blind (V13.4.161)`,
            atSecondsLeft:timeState.minsRemaining*60+timeState.secsRemaining,
            atPosterior:_post,kalshiAtLock:null,
            locked:true,earlyLock:false,
            isConfluent:false,isSuperConfluent:false,isRisingConfluence:false,isTapeLed:false,isStructuralLed:false,
            samples:0,needSamples:0,
            tier:'no-go-price-unavailable-sitout',
            session:(typeof getMarketSessions==='function'?getMarketSessions():{}).dominant||'UNKNOWN',
            regime:analysis?.regime||'',
            qScore:Math.round(_qFast),
            fgt:analysis?.mtfAlignment,
            _committedAt:Date.now(),
          };
          taraCallSnapshotRef.current=_applyNoSitout(_ngNoDataSnap);
          _logSnapshotEntry(taraCallSnapshotRef.current);
          _persistLock();
          return;
        }
        if(_ngOn&&_ngCost!=null&&(_ngCost<_ngMin||_ngCost>=_ngMax)&&!_ngTrajAgrees){
          const _ngSitSnap={
            call:'SIT_OUT',direction:null,confidence:0,
            caution:null,
            // V13.4.169: this string is user-facing (it renders in the call card and the
            //   log), so it drops the version tag and the double hyphen.
            reason:`${_commitDir} ${_commitConf}% wanted to lock at ${_ngCost.toFixed(0)}c – outside the ${_ngMin.toFixed(0)}-${_ngMax.toFixed(0)}c band. Sitting out rather than pay a bad price. (${_noGoReason})`,
            atSecondsLeft:timeState.minsRemaining*60+timeState.secsRemaining,
            atPosterior:_post,kalshiAtLock:_kPctNow,
            locked:true,earlyLock:false,
            isConfluent:false,isSuperConfluent:false,isRisingConfluence:false,isTapeLed:false,isStructuralLed:false,
            samples:0,needSamples:0,
            tier:'no-go-edge-band-sitout',
            session:(typeof getMarketSessions==='function'?getMarketSessions():{}).dominant||'UNKNOWN',
            regime:analysis?.regime||'',
            qScore:Math.round(_qFast),
            fgt:analysis?.mtfAlignment,
            _committedAt:Date.now(),
          };
          taraCallSnapshotRef.current=_applyNoSitout(_ngSitSnap);
          _logSnapshotEntry(taraCallSnapshotRef.current);
          _persistLock();
          return;
        }
        const _ngSnap={
          call:_commitDir,direction:_commitDir,
          confidence:_commitConf,
          caution:_noGoReason,
          wasOverriddenNoTrade:true,
          noGoCategory:_noGoTier,
          reason:`${_commitDir} ${_commitConf}% — ⚠ ${_noGoReason}`,
          atSecondsLeft:timeState.minsRemaining*60+timeState.secsRemaining,
          atPosterior:_post,
          kalshiAtLock:_kPctNow,
          locked:true,earlyLock:false,
          isConfluent:false,isSuperConfluent:false,isRisingConfluence:false,isTapeLed:false,isStructuralLed:false,
          samples:0,needSamples:0,
          tier:_noGoTier,
          session:(typeof getMarketSessions==='function'?getMarketSessions():{}).dominant||'UNKNOWN',
          regime:analysis?.regime||'',
          qScore:Math.round(_qFast),
          fgt:analysis?.mtfAlignment,
          _committedAt:Date.now(),
          // V10.7.87: audit fields — phase + liquidity context at time of no-go-edge
          _v10787_phase:(()=>{try{return getPhaseKey(new Date().getUTCHours(),new Date().getUTCMinutes());}catch(_){return null;}})(),
          _v10787_obDepthUSD:(depthFlash?.liqLongUSDLive||0)+(depthFlash?.liqShortUSDLive||0),
          _v10787_isThinBook:((depthFlash?.liqLongUSDLive||0)+(depthFlash?.liqShortUSDLive||0))>0&&((depthFlash?.liqLongUSDLive||0)+(depthFlash?.liqShortUSDLive||0))<500000,
          _v10787_edgeThreshold:_noGoEdgeThreshold||null,
        };
        taraCallSnapshotRef.current=_applyNoSitout(_ngSnap);
        _logSnapshotEntry(taraCallSnapshotRef.current); // V7.10.1: write to call log
        _persistLock();
        return;
      }
      // Path A: Hard cap at 90s — commit no matter what.
      // V7.10.3: BUT only after cloud restore has completed. Otherwise a tab opened
      //   mid-window will fire Path A on the FIRST analysis tick (because elapsedSec
      //   is already > 90), creating a snapshot that may conflict with what other tabs
      //   already committed. _cloudRestoreCompletedRef flips true only after cloudRead
      //   resolves — distinct from _hasRestoredLockRef which gates the effect re-running.
      if(ALLOW_CLOCK_FORCED_CALLS&&_hardCapElapsed&&_cloudRestoreCompletedRef.current&&taraCallSnapshotRef.current===null){
        // V10.2.50 — CHOP TIME-CAP GUARD: if regime is RANGE-CHOP and conviction
        //   is sub-baseline, the historical WR of time-cap-commit in chop is
        //   well below 50%. Converts marginal time-cap commits to sit-outs.
        // V10.3.0 — RAISED THRESHOLD 68→78. May 16 audit showed all 5 chop
        //   time-cap commits were at 71-76% conf, with 2W/3L (40% WR) — below the
        //   58% break-even on time-cap pricing. The old 68% threshold was too low.
        //   At 78%, only genuinely high-conviction commits go through; everything
        //   else (the 70-77% band that's been losing) becomes a sit-out.
        // V10.3.3 — LOWERED 78→72. The 78 threshold blocked legitimate
        //   high-conviction calls (May 16 02:23 PM: 76% DOWN with PIR 100% + Flow
        //   -46 + 3-TF doubleTop confluence — sat out and DOWN won). 72% is the
        //   middle ground: still blocks the truly marginal 65-71% range that
        //   historically loses, but lets 72%+ conviction calls through.
        const _v10250ChopGuard=(analysis?.regime==='RANGE-CHOP'&&_commitConf<72);
        if(_v10250ChopGuard){
          // V10.7.5 — DEAD-WINDOW-ONLY SITOUT RULE
          //   User rule: never sit out except when window is literally dead.
          //   Check windowAmplitude — if rangeBps<5 AND past opening phase, true dead.
          //   Otherwise: convert chop time-cap sitout to force-commit using posterior.
          // V10.7.48 — DEADZONE GUARD: when posterior is in 45-55 ("coin flip"),
          //   force-commit historical WR is 42% (12 calls, May 21 audit). Adding a
          //   deadzone guard: only force-commit when posterior shows real conviction
          //   (outside 45-55). True coin flips → sit out, even in non-dead windows.
          //   This is the "if she genuinely has no conviction, sit out" rule.
          const _wa=analysis?.windowAmplitude;
          const _rangeBps=_wa?.rangeBps||0;
          const _isDeadWindow=_wa?.label!=='OPENING'&&_rangeBps<5;
          const _isTrueDeadzone=_postKnown&&_post>=45&&_post<=55; // V10.7.48: posterior coin flip
          // V10.7.82b: Flow + momentum override — if posterior is borderline coin-flip
          //   (45-55%) BUT flow AND momentum both strongly agree with direction,
          //   it's not actually a coin flip. Strong directional tape overrides deadzone.
          //   This prevents sitting out on "UP 53% with flow=+20, momentum=+18" setups.
          const _rss=analysis?.rawSignalScores||{};
          const _callDir=taraCall?.call;
          const _flowVal=Number(_rss.flow||0);
          const _momVal=Number(_rss.momentum||0);
          const _flowAligned=_callDir==='UP'?_flowVal>=15:_flowVal<=-15;
          const _momAligned=_callDir==='UP'?_momVal>=10:_momVal<=-10;
          const _hasStrongTape=_flowAligned&&_momAligned;
          const _hasStrongGap=Math.abs(Number((analysis?.rawSignalScores||{}).gap||0))>=15;
          const _effectiveDeadzone=_isTrueDeadzone&&!_hasStrongTape&&!_hasStrongGap;
          // V10.7.88: FLAT-GAP TIME-CAP GATE.
          //   If gap is still flat at time-cap, this is the same situation as the
          //   flat-gap watch sitting out at <5min left. The time-cap bypasses the
          //   flat-gap watch entirely, allowing flat-gap directional locks to sneak
          //   through. Audit 2026-06-03: 7/12 BTC losses were flat-gap time-cap
          //   commits. All-time flat-gap WR=51%, avg EV=+0.8¢ — barely above 0.
          //   When gap is flat AND no external signal AND cost in the 44-65¢ zone:
          //   sit out. Same threshold as flat-gap watch (gap<5, extSignal<8).
          const _gapAtCap=Math.abs(Number(analysis?.rawSignalScores?.gap||0));
          const _extAtCap=Math.max(
            Math.abs(Number(analysis?.rawSignalScores?.liqHeatmap||0)),
            Math.abs(Number(analysis?.rawSignalScores?.deribitOptions||0)),
            Math.abs(Number(analysis?.rawSignalScores?.liqSignal||0))
          );
          const _kForDir=_commitDir==='UP'?_kPctNow:(100-(_kPctNow||50));
          const _isFlatGapTimeCap=_gapAtCap<5&&_extAtCap<8&&_kForDir!=null&&_kForDir>=44&&_kForDir<=65&&(analysis?.regime==='RANGE-CHOP'||analysis?.regime==='COMPRESSING'); // V13.4.120 FIX: _isChopRegime was out of scope here (undeclared, uncaught -- no enclosing try/catch), inlined the same check used at its point of declaration (L42387)
          // V10.8.1: GAP-OPPOSED GATE on time-cap-commit path.
          //   The directional-lock gate (V10.7.97) catches gap<10 aligned early.
          //   But the time-cap path bypasses it entirely — gap-opposed trades
          //   were slipping through at 17% WR. Apply the same rule here.
          const _gapAlignedCap=Number(analysis?.rawSignalScores?.gap||0)*(_commitDir==='UP'?1:-1);
          const _gapOpposedAtCap=_gapAlignedCap<10;
          if(!_isDeadWindow&&!_effectiveDeadzone&&!_isFlatGapTimeCap&&!_gapOpposedAtCap){
            // Force-commit to posterior direction. Skip the sitout snapshot entirely.
            // V10.7.6 — check for reversal signals before committing
            const _v107_6=_v10_7_6_reversalCheck(_post,analysis);
            const _forceDir=_v107_6.dir;
            const _forceConf=_v107_6.conf;
            const _forceSnap={
              call:_forceDir,direction:_forceDir,
              confidence:_forceConf,
              caution:`directional lock · ${_forceDir} · ${_rangeBps.toFixed(0)}bps range momentum`,
              reason:`Directional lock · ${_forceDir} ${(_forceConf-50).toFixed(0)}pt · structural momentum in ${_rangeBps.toFixed(0)}bps window`,
              atSecondsLeft:timeState.minsRemaining*60+timeState.secsRemaining,
              atPosterior:_post,
              kalshiAtLock:_kPctNow,
              locked:true,earlyLock:false,
              isConfluent:false,isSuperConfluent:false,isRisingConfluence:false,isTapeLed:false,isStructuralLed:false,
              samples:0,needSamples:0,
              tier:'directional-lock',
              session:(typeof getMarketSessions==='function'?getMarketSessions():{}).dominant||'UNKNOWN',
              regime:analysis?.regime||'',
              qScore:Math.round(_qFast),
              fgt:analysis?.mtfAlignment,
              _committedAt:Date.now(),
              _v10_7_5_forced:true,
              _v10_7_5_originalGuard:'V10.2.50',
              _v10_7_6_reversalFlipped:_v107_6.flipped,
              _v10_7_6_revVotes:_v107_6.revVotes,
              _v10_7_6_revReasons:_v107_6.reasons,
              _v10_7_6_originalDir:_v107_6.flipped?_v107_6.originalDir:null,
              _v10_7_5_rangeBps:_rangeBps,
              _v112Timing:(_v112Live.at&&Date.now()-_v112Live.at<30000?_v112Live.timing:null),
              _v112Why:(_v112Live.at&&Date.now()-_v112Live.at<30000?_v112Live.why:null),
            };
            taraCallSnapshotRef.current=_applyNoSitout(_forceSnap);
            _logSnapshotEntry(taraCallSnapshotRef.current);
            _persistLock();
            return;
          }
          // Genuine sit-out — dead window, true coin-flip, flat-gap, OR gap-opposed at time-cap.
          const _sitReason=_effectiveDeadzone
            ?`Coin-flip posterior ${_commitConf}% at time-cap — no real conviction, sit out (V10.7.48 deadzone guard)`
            :_isFlatGapTimeCap
            ?`Flat gap at time-cap — gap ${_gapAtCap.toFixed(1)}pt, ext ${_extAtCap.toFixed(1)}pt, entry ${_kForDir?.toFixed(0)}¢ — 51% WR historically, sit out (V10.7.88)`
            :_gapOpposedAtCap
            ?`Gap opposed at time-cap — gap aligned ${_gapAlignedCap.toFixed(0)}pt < 10, 17% WR, sit out (V10.8.1)`
            :`V10.2.50 chop time-cap guard: RANGE-CHOP regime + ${_commitConf}% conv at ${Math.round(elapsedSec)}s — historically loses, sit out`;
          const _sitTier=_effectiveDeadzone?'deadzone-sitout':_isFlatGapTimeCap?'flat-gap-timecap-sitout':_gapOpposedAtCap?'gap-opposed-timecap-sitout':'chop-time-cap-sitout';
          const _sitCaution=_effectiveDeadzone
            ?`Deadzone guard — posterior ${_commitConf}% in 45-55 coin-flip zone → sit out (V10.7.48)`
            :_isFlatGapTimeCap
            ?`Flat gap — price at strike, no directional signal → sit out (V10.7.88)`
            :_gapOpposedAtCap
            ?`Gap opposed (${_gapAlignedCap.toFixed(0)}pt) at time-cap — 17% WR, sit out (V10.8.1)`
            :`Chop guard — ${_commitConf}% in RANGE-CHOP at time-cap, sub-baseline conviction → sit out (V10.2.50)`;
          const _sitSnap={
            call:'SIT_OUT',direction:'SIT_OUT',
            confidence:_commitConf,
            caution:_sitCaution,
            reason:_sitReason,
            atSecondsLeft:timeState.minsRemaining*60+timeState.secsRemaining,
            atPosterior:_post,
            kalshiAtLock:_kPctNow,
            locked:true,earlyLock:false,
            isConfluent:false,isSuperConfluent:false,isRisingConfluence:false,isTapeLed:false,isStructuralLed:false,
            samples:0,needSamples:0,
            tier:_sitTier,
            session:(typeof getMarketSessions==='function'?getMarketSessions():{}).dominant||'UNKNOWN',
            regime:analysis?.regime||'',
            qScore:Math.round(_qFast),
            fgt:analysis?.mtfAlignment,
            _committedAt:Date.now(),
            _v10_7_48_effectiveDeadzone:_effectiveDeadzone,
            _v10_7_48_postAtSitout:_post,
          };
          taraCallSnapshotRef.current=_applyNoSitout(_sitSnap);
          _logSnapshotEntry(taraCallSnapshotRef.current);
          _persistLock();
          return;
        }
        // V13.4.107: the entry-band gate (13.4.103, 30-60c default) never applied here --
        //   this deadline-forced commit builds and persists its own snapshot directly,
        //   bypassing every price gate including the original 13.4.77 extreme-price block.
        //   Found from real data: two post-band locks landed at 78c and 83c, both from
        //   this exact path (tier time-cap-commit / no-go-edge feeding into it). The clock
        //   running out is a real reason to stop SCANNING, but it is not a reason to accept
        //   any price -- if the deadline arrives and the only available price is outside
        //   the band, sit out instead of forcing a bad-priced commit.
        const _capKEntry=Number.isFinite(_kPctNow)?_kPctNow:null;
        const _capCost=(_capKEntry!=null)?(_commitDir==='UP'?_capKEntry:(100-_capKEntry)):null;
        const _capOn=(function(){try{return localStorage.getItem('taraEntryQuality')!=='off';}catch(_e9){return true;}})();
        const _capMin=getEntryMinCost();
        const _capMax=getEntryMaxCost();
        // V13.4.108: same TRAJ-early bypass as the main gate, applied here too so the
        //   deadline path is consistent with the normal path.
        const _capTrajOn=(function(){try{return localStorage.getItem('taraTrajEarlyLock')!=='off';}catch(_e13){return true;}})();
        const _capTrajGap=Number(analysis?.projectedGapBps)||0;
        const _capTrajDir=_capTrajGap>0?'UP':(_capTrajGap<0?'DOWN':null);
        const _capTrajAgrees=_capTrajOn&&_capTrajDir===_commitDir&&_capTrajGap!==0;
        if(_capOn&&_capCost!=null&&(_capCost<_capMin||_capCost>=_capMax)&&!_capTrajAgrees){
          const _capSitReason=`[V13.4.107] deadline hit but entry ${_capCost.toFixed(0)}c outside ${_capMin.toFixed(0)}-${_capMax.toFixed(0)}c band -- sitting out rather than forcing a bad price`;
          const _capSitSnap={
            call:'SIT_OUT',direction:null,confidence:0,
            caution:null,reason:_capSitReason,
            atSecondsLeft:timeState.minsRemaining*60+timeState.secsRemaining,
            atPosterior:_post,kalshiAtLock:_kPctNow,
            locked:true,earlyLock:false,
            isConfluent:false,isSuperConfluent:false,isRisingConfluence:false,isTapeLed:false,isStructuralLed:false,
            samples:0,needSamples:0,
            tier:'time-cap-band-sitout',
            session:(typeof getMarketSessions==='function'?getMarketSessions():{}).dominant||'UNKNOWN',
            regime:analysis?.regime||'',
            qScore:Math.round(_qFast),
            fgt:analysis?.mtfAlignment,
            _committedAt:Date.now(),
          };
          taraCallSnapshotRef.current=_applyNoSitout(_capSitSnap);
          _logSnapshotEntry(taraCallSnapshotRef.current);
          _persistLock();
          return;
        }
        // V13.4.324: TREND-AT-LOCK SIT-OUT. Audit of 375 real-tier calls (train/test
        //   split, both halves agree): when _isTrend is true at the moment of lock,
        //   time-cap-commit wins 46.4% (n=141 total, n=28 in the true bucket) vs
        //   71.7% when false -- a 25-point gap, z=2.49 on the pooled set. This is the
        //   first quality-based sit-out reintroduced since V13.4.250 removed all of
        //   them; it earns the exception by being the one signal that held up out of
        //   18 candidate fields tested the same way (train/test consistency), unlike
        //   the ideas that prompted this audit (a loss-streak cooldown, regime-
        //   adaptive entry timing), both of which measured flat or reversed on the
        //   same data. directional-lock is deliberately excluded -- its own logic
        //   already requires _isTrend as a POSITIVE signal for one confirmation path.
        if(analysis?.rawSignalScores?._isTrend===true){
          const _talSnap={
            call:'SIT_OUT',direction:null,confidence:0,
            caution:null,reason:`[V13.4.324] trend-at-lock -- time-cap-commit measures 46.4% WR when trending vs 71.7% when not (n=141, z=2.49) -- sitting out instead`,
            atSecondsLeft:timeState.minsRemaining*60+timeState.secsRemaining,
            atPosterior:_post,kalshiAtLock:_kPctNow,
            locked:true,earlyLock:false,
            isConfluent:false,isSuperConfluent:false,isRisingConfluence:false,isTapeLed:false,isStructuralLed:false,
            samples:0,needSamples:0,
            tier:'trend-at-lock-sitout',
            session:(typeof getMarketSessions==='function'?getMarketSessions():{}).dominant||'UNKNOWN',
            regime:analysis?.regime||'',
            qScore:Math.round(_qFast),
            fgt:analysis?.mtfAlignment,
            isNoGo:true,
            noGoCategory:'trend-at-lock-sitout',
            wouldHaveBeen:_commitDir,
            wouldHaveBeenTier:'time-cap-commit',
            _committedAt:Date.now(),
          };
          taraCallSnapshotRef.current=_applyNoSitout(_talSnap);
          _logSnapshotEntry(taraCallSnapshotRef.current);
          _persistLock();
          return;
        }
        const _cautionLevel=_commitConf>=70?'firm':_commitConf>=60?'leaning':_commitConf>=55?'tentative':'low-confidence';
        const _cautionNote=_commitConf>=70?null:`${_cautionLevel} call — confidence ${_commitConf}% (commit by time cap)`;
        const _capSnap={
          call:_commitDir,direction:_commitDir,
          confidence:_commitConf,
          caution:_cautionNote, // V7.8: surfaced in UI when present
          reason:`Auto-commit at ${Math.round(elapsedSec)}s — posterior ${_commitConf}% ${_commitDir}${_cautionNote?' · '+_cautionNote:''}`,
          atSecondsLeft:timeState.minsRemaining*60+timeState.secsRemaining,
          atPosterior:_post,
          kalshiAtLock:_kPctNow,
          locked:true,earlyLock:false,
          isConfluent:false,isSuperConfluent:false,isRisingConfluence:false,isTapeLed:false,isStructuralLed:false,
          samples:0,needSamples:0,
          tier:'time-cap-commit',
          session:(typeof getMarketSessions==='function'?getMarketSessions():{}).dominant||'UNKNOWN',
          regime:analysis?.regime||'',
          qScore:Math.round(_qFast),
          fgt:analysis?.mtfAlignment,
          _committedAt:Date.now(), // V9.1.3: stamp at creation
        };
        taraCallSnapshotRef.current=_applyNoSitout(_capSnap);
        _logSnapshotEntry(taraCallSnapshotRef.current); // V7.10.1: write to call log
        _persistLock();
        return;
      }
      // Path B: NOT a coin flip AND timer-ETA hit zero → commit instead of sit-out
      const _etaSec=tc?._ctx?.lockEtaSec;
      const _etaSamples=tc?._ctx?.samples;
      const _etaNeed=tc?._ctx?.needSamples;
      const _etaExpired=_etaSec!=null&&_etaSec<=0&&_etaSamples!=null&&_etaNeed!=null&&_etaSamples<_etaNeed;
      if(ALLOW_CLOCK_FORCED_CALLS&&_etaExpired&&!_isCoinFlip&&_cloudRestoreCompletedRef.current){
        // V13.4.112: FOURTH separate commit path found -- 'Path B: timer commit', triggered
        //   by sample-count ETA expiry rather than the hard elapsedSec deadline (that was
        //   Path A, patched in 107). Same problem: builds and persists _timerSnap directly,
        //   no price gate at all. Found by re-auditing this exact region after a user report
        //   of a 72c UP lock (tier time-cap-commit reported, but this sibling path shares
        //   the same lack of protection and was worth closing regardless of which one fired).
        const _tmK=Number.isFinite(_kPctNow)?_kPctNow:null;
        const _tmCost=(_tmK!=null)?(_commitDir==='UP'?_tmK:(100-_tmK)):null;
        const _tmOn=(function(){try{return localStorage.getItem('taraEntryQuality')!=='off';}catch(_etm1){return true;}})();
        const _tmMin=getEntryMinCost();
        const _tmMax=getEntryMaxCost();
        const _tmTrajOn=(function(){try{return localStorage.getItem('taraTrajEarlyLock')!=='off';}catch(_etm4){return true;}})();
        const _tmTrajGap=Number(analysis?.projectedGapBps)||0;
        const _tmTrajDir=_tmTrajGap>0?'UP':(_tmTrajGap<0?'DOWN':null);
        const _tmTrajAgrees=_tmTrajOn&&_tmTrajDir===_commitDir&&_tmTrajGap!==0;
        if(_tmOn&&_tmCost!=null&&(_tmCost<_tmMin||_tmCost>=_tmMax)&&!_tmTrajAgrees){
          const _tmSitSnap={
            call:'SIT_OUT',direction:null,confidence:0,
            caution:null,
            reason:`[V13.4.112] timer commit hit but entry ${_tmCost.toFixed(0)}c outside ${_tmMin.toFixed(0)}-${_tmMax.toFixed(0)}c band -- sitting out rather than forcing a bad price`,
            atSecondsLeft:timeState.minsRemaining*60+timeState.secsRemaining,
            atPosterior:_post,kalshiAtLock:_kPctNow,
            locked:true,earlyLock:false,
            isConfluent:false,isSuperConfluent:false,isRisingConfluence:false,isTapeLed:false,isStructuralLed:false,
            samples:_etaSamples,needSamples:_etaNeed,
            tier:'timer-commit-band-sitout',
            session:(typeof getMarketSessions==='function'?getMarketSessions():{}).dominant||'UNKNOWN',
            regime:analysis?.regime||'',
            qScore:Math.round(_qFast),
            fgt:analysis?.mtfAlignment,
            _committedAt:Date.now(),
          };
          taraCallSnapshotRef.current=_applyNoSitout(_tmSitSnap);
          _logSnapshotEntry(taraCallSnapshotRef.current);
          _persistLock();
          return;
        }
        const _cautionLevel=_commitConf>=70?'firm':_commitConf>=60?'leaning':'tentative';
        const _cautionNote=_commitConf>=70?null:`${_cautionLevel} call — confidence ${_commitConf}%`;
        const _timerSnap={
          call:_commitDir,direction:_commitDir,
          confidence:_commitConf,
          caution:_cautionNote,
          reason:`Timer commit — posterior ${_commitConf}% ${_commitDir} (not coin-flip)${_cautionNote?' · '+_cautionNote:''}`,
          atSecondsLeft:timeState.minsRemaining*60+timeState.secsRemaining,
          atPosterior:_post,
          kalshiAtLock:_kPctNow,
          locked:true,earlyLock:false,
          isConfluent:false,isSuperConfluent:false,isRisingConfluence:false,isTapeLed:false,isStructuralLed:false,
          samples:_etaSamples,needSamples:_etaNeed,
          tier:'timer-commit',
          session:(typeof getMarketSessions==='function'?getMarketSessions():{}).dominant||'UNKNOWN',
          regime:analysis?.regime||'',
          qScore:Math.round(_qFast),
          fgt:analysis?.mtfAlignment,
          _committedAt:Date.now(), // V9.1.3: stamp at creation
        };
        taraCallSnapshotRef.current=_applyNoSitout(_timerSnap);
        _logSnapshotEntry(taraCallSnapshotRef.current); // V7.10.1: write to call log
        _persistLock();
        return;
      }
      // Path C: timer expired but it's a coin flip — KEEP WAITING for samples to resolve
      if(_etaExpired&&_isCoinFlip){
        _noteGateHold('formation-coinflip',`Waiting for direction: posterior ${_post.toFixed(1)}%, timer expired but read remains a coin flip.`,{posterior:_post,samples});
        return; // skip this tick; resolution will come from sample formation or 90s cap
      }
    }
    // V10.7.47: Dial-aware mixed-sitout threshold + lean stamp.
    //   Was: bail at 30s flat regardless of dial. Result: Tara was giving up on 14m+
    //   of window development to chase a "fast" decision that wasn't actually fast,
    //   just early. Fix: dial controls patience before commit. Faster dial = bail sooner,
    //   patient dial = wait longer for signals to clarify.
    //   Formula: base 30s + (dial/100) * (winLen * 0.4)
    //     15m window: 30s @ dial=0 → 6m30s @ dial=100
    //     5m  window: 30s @ dial=0 → 2m30s @ dial=100
    //   ALSO: stamp leanDir based on posterior so UI can show "would have leaned X".
    //   Lock timing for clear signals is UNAFFECTED — this only delays the sit-out commitment.
    // V10.8.4: FIXED SITOUT FLOOR — removed speed dial dependency.
    //   Speed dial at 50 was setting sitout at 3.5min. User guidance:
    //   "lock within 3-5 minutes when tradeable." Hardcode to 5 minutes
    //   for truly unresolvable windows (no confluence AND quality < 25).
    //   This is the last resort — confluence windows lock much earlier via tiers.
    const _v1074_winLen=windowType==='15m'?900:300;
    const _v1074_sitoutMinSec=_v1074_winLen*0.33; // 5min for 15m, 1.65min for 5m
        // V13.4.58: CHEAP-ENTRY AUTO-LOCK (user rule). Locks the leaned direction hands-off,
    //   BEFORE mixed-sitout gives up, when our side is enterable at <=70c with non-negative
    //   edge and the tape is not fighting it. Validated on the user own log: <=70c entries
    //   are the most profitable bucket (+4.4c/contract vs +1.3c for >70c). DROPS the quality
    //   and conviction floors (the gates that over-fire), KEEPS the guards that protect money:
    //   min entry cost >=35c (adverse-selection floor), out-of-deadzone lean >=5pt, non-negative
    //   edge (our read >= market price for our side), tape-not-opposing, and no-confluence
    //   (confluence still locks via its richer tier). Tagged cheap-entry for live EV tracking.
    //   CAVEAT: the <=70c read comes from the Kalshi feed; on a wrong-window feed this misfires.
    if(false&&taraCallSnapshotRef.current===null&&!_hasAnyConfluence&&_kPctNow!=null&&samples>0&&elapsedSec>=90){/* V13.4.61: cheap-entry DISABLED - locked low-quality windows where Tara conf 76-80 but WR 33% (anti-calibrated); good cheap entries already lock via normal tiers. Reversible: remove false&& to re-enable. */
      const _cePost=Number(analysis?.rawProbAbove)||50;
      const _ceLean=_cePost>=50?'UP':'DOWN';
      const _ceDev=Math.abs(_cePost-50);
      const _ceConf=_ceLean==='UP'?Math.round(_cePost):Math.round(100-_cePost);
      const _ceCost=_ceLean==='UP'?_kPctNow:(100-_kPctNow);
      const _ceTw=analysis?.tapeWindows||{};
      const _ceT=[_ceTw.w15?.buyPct,_ceTw.w30?.buyPct,_ceTw.w60?.buyPct].filter(v=>typeof v==='number');
      const _ceUpTape=_ceT.filter(p=>p>=60).length>=2;
      const _ceDownTape=_ceT.filter(p=>p<=40).length>=2;
      const _ceTapeOpposes=(_ceLean==='UP'&&_ceDownTape)||(_ceLean==='DOWN'&&_ceUpTape);
      if(_ceDev>=5&&_ceCost>=35&&_ceCost<=70&&!_ceTapeOpposes&&_ceConf>=_ceCost){
        const _ceSnap={
          call:_ceLean,direction:_ceLean,
          confidence:_ceConf,
          caution:`Cheap-entry lock - ${_ceLean} at ${Math.round(_ceCost)}c, edge +${Math.round(_ceConf-_ceCost)}pt, tape clear`,
          reason:`V13.4.58 cheap-entry: side ${Math.round(_ceCost)}c (<=70c line), lean ${Math.round(_ceDev)}pt ${_ceLean}, edge +${Math.round(_ceConf-_ceCost)}pt - locking instead of sit-out`,
          atSecondsLeft:timeState.minsRemaining*60+timeState.secsRemaining,
          atPosterior:_cePost,
          kalshiAtLock:_kPctNow,
          locked:true,earlyLock:false,
          isConfluent:false,isSuperConfluent:false,isRisingConfluence:false,isTapeLed:false,isStructuralLed:false,
          samples,needSamples:0,
          tier:'cheap-entry',
          session:(typeof getMarketSessions==='function'?getMarketSessions():{}).dominant||'UNKNOWN',
          regime:analysis?.regime||'',
          qScore:Math.round(_qFast),
          fgt:analysis?.mtfAlignment,
          _committedAt:Date.now(),
          _v13458_cheapEntry:true,
          _v13458_entryCost:Math.round(_ceCost),
          _v13458_edge:Math.round(_ceConf-_ceCost),
        };
        taraCallSnapshotRef.current=_applyNoSitout(_ceSnap);
        _logSnapshotEntry(taraCallSnapshotRef.current);
        _persistLock();
        return;
      }
    }
    if(elapsedSec>=_v1074_sitoutMinSec&&!_hasAnyConfluence&&_qFast<25&&taraCallSnapshotRef.current===null){
      const _v1074_post=Number(analysis?.rawProbAbove)||50;
      const _v1074_lean=_v1074_post>=50?'UP':'DOWN';
      const _v1074_leanPct=_v1074_lean==='UP'?Math.round(_v1074_post):Math.round(100-_v1074_post);
      const _mixSnap={
        call:'SIT_OUT',direction:null,confidence:0,
        reason:`Mixed signals at ${Math.round(elapsedSec)}s — sitting out`,
        atSecondsLeft:timeState.minsRemaining*60+timeState.secsRemaining,
        atPosterior:analysis?.rawProbAbove,
        kalshiAtLock:_kPctNow,
        locked:true,earlyLock:false,
        isConfluent:false,isSuperConfluent:false,isRisingConfluence:false,isTapeLed:false,isStructuralLed:false,
        samples,needSamples:0,
        tier:'mixed-sitout',
        session:(typeof getMarketSessions==='function'?getMarketSessions():{}).dominant||'UNKNOWN',
        regime:analysis?.regime||'',
        qScore:Math.round(_qFast),
        fgt:analysis?.mtfAlignment,
        // V10.7.47: directional lean stamps for UI hint + audit trail
        leanDir:_v1074_lean,
        leanPct:_v1074_leanPct,
        _v10_7_47_sitoutThresholdSec:Math.round(_v1074_sitoutMinSec),
        _v10_7_47_dialAtSitout:50, // dial removed V10.8.4
      };
      taraCallSnapshotRef.current=_applyNoSitout(_mixSnap);
      _logSnapshotEntry(taraCallSnapshotRef.current); // V7.10.1
      _persistLock();
      return;
    }
    // V6.2.7: Kalshi-window check. Only fires when there's an active direction lean (claimed
    //   or current tc.call) — else we're still scanning, no direction to gate on.
    const _activeDir=taraCallSampleRef.current.dir||(tc?.call==='UP'||tc?.call==='DOWN'?tc.call:null);
    // V14.4.6: a disabled legacy EV gate cannot veto the intentional Call band.
    if(!NO_ENTRY_GATES&&_activeDir&&_kPctNow!=null&&taraCallSnapshotRef.current===null){
      const _kalshiForDir=_activeDir==='UP'?_kPctNow:(100-_kPctNow);
      const _wasBelow=_activeDir==='UP'?kalshiWasBelowThreshUpRef.current:kalshiWasBelowThreshDownRef.current;
      // V6.5.1: dip-grace check. If Kalshi was below threshold within the last 30s,
      //   the entry window is still open even if currently above. Handles the case where
      //   Kalshi dipped briefly to 60, climbed back to 68 — Tara should still lock.
      const _lastBelowTs=_activeDir==='UP'?kalshiLastBelowThreshUpRef.current:kalshiLastBelowThreshDownRef.current;
      const _withinGrace=_lastBelowTs>0&&(Date.now()-_lastBelowTs)<=KALSHI_DIP_GRACE_MS;

      // V13.4.326 REMOVED: V10.7.80's MINIMUM ENTRY COST block (was: <35c on
      //   our side silently aborted the tick with no snapshot, no log entry,
      //   no tier name at all -- invisible to every log-based check in this
      //   file, and not touched by NO_ENTRY_GATES despite that flag
      //   supposedly opening the cost band everywhere). This was a real
      //   EV/quality decision gate ("adverse selection"), not a physical
      //   constraint, per the explicit instruction to remove all such gates
      //   and let the engine take every window at whatever odds it actually
      //   sees, then re-add only what data proves out.
      // Currently above threshold AND has been above all window so far AND not in grace → window closed.
      if(_kalshiForDir>KALSHI_ENTRY_THRESH&&_wasBelow&&!_withinGrace&&samples>0){
        // Was previously actionable, now isn't — entry window closed mid-flight
        const _kwcSnap={
          call:'SIT_OUT',direction:null,confidence:0,
          reason:`Kalshi ${Math.round(_kalshiForDir)}% ${_activeDir} — past entry threshold, chance gone`,
          atSecondsLeft:timeState.minsRemaining*60+timeState.secsRemaining,
          atPosterior:analysis?.rawProbAbove,
          kalshiAtLock:_kPctNow,
          locked:true,earlyLock:false,
          isConfluent:false,isSuperConfluent:false,isRisingConfluence:false,isTapeLed:false,isStructuralLed:false,
          samples,needSamples:0,
          tier:'kalshi-window-closed',
          session:(typeof getMarketSessions==='function'?getMarketSessions():{}).dominant||'UNKNOWN',
          regime:analysis?.regime||'',
          qScore:Math.round(qualityGate?.score||0),qScoreV2:Math.round(qualityGateV2?.score||0),qScoreV2Components:qualityGateV2?.components||null,
          fgt:analysis?.mtfAlignment,
        };
        taraCallSnapshotRef.current=_applyNoSitout(_kwcSnap);
        _logSnapshotEntry(taraCallSnapshotRef.current); // V7.10.1
        _persistLock();
        return;
      }
      // Window opened with Kalshi already above threshold (and never dropped) AND we're
      //   past 70% elapsed → no reset is coming, sit out.
      const _elapsedFrac=elapsedSec/(windowType==='15m'?900:300);
      if(!_wasBelow&&_elapsedFrac>=0.70){
        const _knrSnap={
          call:'SIT_OUT',direction:null,confidence:0,
          reason:`Kalshi started above ${KALSHI_ENTRY_THRESH}% ${_activeDir}, never reset — sitting out`,
          atSecondsLeft:timeState.minsRemaining*60+timeState.secsRemaining,
          atPosterior:analysis?.rawProbAbove,
          kalshiAtLock:_kPctNow,
          locked:true,earlyLock:false,
          isConfluent:false,isSuperConfluent:false,isRisingConfluence:false,isTapeLed:false,isStructuralLed:false,
          samples,needSamples:0,
          tier:'kalshi-no-reset',
          session:(typeof getMarketSessions==='function'?getMarketSessions():{}).dominant||'UNKNOWN',
          regime:analysis?.regime||'',
          qScore:Math.round(qualityGate?.score||0),qScoreV2:Math.round(qualityGateV2?.score||0),qScoreV2Components:qualityGateV2?.components||null,
          fgt:analysis?.mtfAlignment,
        };
        taraCallSnapshotRef.current=_applyNoSitout(_knrSnap);
        _logSnapshotEntry(taraCallSnapshotRef.current); // V7.10.1
        _persistLock();
        return;
      }
    }
    // V5.6.7: Five tiers now. Tier 1.5 fills the gap between "perfect" (rare) and
    //   "strong" (1 min). Catches the "fast clean market" case the user worried about
    //   missing. Magnitude-weighted samples — high conviction locks faster.
    // V6.1.3 CRITICAL FIX: Was missing confluence/super-confluence/tape-led/rising tiers
    //   entirely. Display showed "5/8 samples needed" but lifecycle waited for 100+
    //   because it used the default tier. That was the source of "slow locks even when
    //   confluence flags fired." Now lifecycle ladder checks confluence first, matching
    //   the display side. V6.1.3 also speeds up all tiers (super 5→3, conf 8→5, etc).
    const _isLcSuperConf=tc?._ctx?.isSuperConfluent||false;
    const _isLcConf=tc?._ctx?.isConfluent||false;
    const _isLcTapeLed=tc?._ctx?.isTapeLed||false;
    const _isLcRising=tc?._ctx?.isRisingConfluence||false;
    const _isLcStructural=tc?._ctx?.isStructuralLed||false;
    // V10.8.2: CUSHION HISTORY INTELLIGENCE.
    //   Use the mid-window price history to understand whether price has been
    //   consistently on the right side of the strike, or oscillating near it.
    //   This is the "timing" signal — not just where price IS now, but where it's
    //   BEEN throughout the window.
    //
    //   pctTimeCorrectSide: fraction of snapshots where price was on the winning side
    //     e.g. for an UP call, fraction where spot > kalshi_strike
    //     >= 0.80 = price has been above strike 80%+ of window time → high conviction
    //     <= 0.40 = price has been below strike most of the time → wrong direction
    //
    //   cushionTrend: is the gap between price and strike growing or shrinking?
    //     positive = cushion is growing (moving away from strike = safer)
    //     negative = cushion is shrinking (converging on strike = danger)
    //
    //   This modifies needSamples:
    //     Consistent + growing → 0.6× (lock 40% faster — evidence is clear)
    //     Consistent + flat    → 0.8× (lock 20% faster)
    //     Oscillating near line → 1.5× (wait longer — no clear side)
    const _hist=_windowCushionHistoryRef.current;
    let _cushionMult=1.0;
    let _cushionIntel=null;
    const _callDirSign=tc?.call==='UP'?1:(tc?.call==='DOWN'?-1:0);
    if(_hist.length>=3&&_callDirSign!==0&&kalshiStrikeRef.current>0){
      // pctTimeCorrectSide
      const _correctTicks=_hist.filter(h=>h.cushionBps*_callDirSign>0).length;
      const _pctCorrect=_correctTicks/_hist.length;
      // cushionTrend — compare last 2 snapshots to first 2
      const _recentAvg=_hist.slice(-2).reduce((s,h)=>s+h.cushionBps*_callDirSign,0)/2;
      const _earlyAvg=_hist.slice(0,2).reduce((s,h)=>s+h.cushionBps*_callDirSign,0)/2;
      const _trend=_recentAvg-_earlyAvg; // positive = cushion growing toward direction
      // Current cushion in bps (aligned with call direction)
      // V10.9.1/V10.9.2: BRTI-corrected reference price via analysis.referencePrice
      //   (_refPrice itself is a local const inside the `analysis` useMemo and is
      //   not in scope here — referencing it directly threw ReferenceError at runtime)
      const _tmRefPrice=Number(analysis?.referencePrice)||Number(currentPrice)||0;
      const _cushionNowBps=(_tmRefPrice-kalshiStrikeRef.current)/kalshiStrikeRef.current*10000*_callDirSign;
      _cushionIntel={pctCorrect:_pctCorrect,trend:_trend,cushionNow:_cushionNowBps,snapshots:_hist.length};
      if(_pctCorrect>=0.80&&_trend>0&&_cushionNowBps>15){
        // Price has been consistently on the right side AND moving away from strike
        // This is the clearest possible signal — lock faster
        _cushionMult=0.60;
      } else if(_pctCorrect>=0.70&&_cushionNowBps>10){
        // Price consistently right side, decent cushion
        _cushionMult=0.80;
      } else if(_pctCorrect<=0.45){
        // Price has been on the WRONG side more than half the time
        // Don't lock until direction clarifies
        _cushionMult=1.80;
      } else if(_pctCorrect<=0.60&&Math.abs(_cushionNowBps)<8){
        // Price is oscillating near the line — wait for it to commit
        _cushionMult=1.40;
      }
    }

    // V10.8.9: SIGNAL-AGREEMENT GATE (replaces V10.8.6 flow-only gate).
    //   All-time data (772 trades w/ signals):
    //     2+ of {gap,flow,mom} aligned = 66% WR, +3.9¢ EV  (n=587)
    //     only 1 of 3 aligned          = 46% WR, -5.4¢ EV  (n=185)  ← the bleed
    //   Lone-vote breakdown: gap-alone 40%, mom-alone 20%, flow-alone 75% (rare).
    //   Rule: when fewer than 2 of the 3 core signals confirm the direction,
    //   require 2× confirmation samples before locking. Window can still develop
    //   agreement and lock, or reach time-cap. NOT a sitout — patient entry.
    //   Exception: very strong gap (>=25 aligned) carries alone — locks normally.
    const _gapAlignedForGate=Number(analysis?.rawSignalScores?.gap||0)*(_post>=50?1:-1);
    const _flowAlignedForGate=Number(analysis?.rawSignalScores?.flow||0)*(_post>=50?1:-1);
    const _momAlignedForGate=Number(analysis?.rawSignalScores?.momentum||0)*(_post>=50?1:-1);
    const _nAligned=(_gapAlignedForGate>3?1:0)+(_flowAlignedForGate>3?1:0)+(_momAlignedForGate>3?1:0);
    const _veryStrongGapAlone=_gapAlignedForGate>=25;
    const _flowConfMult=(_nAligned<2&&!_veryStrongGapAlone)?2.0:1.0;

    let needSamples,tierLabel;
    if(_isLcStructural){
      // V9.11.3: needSamples 3 → 12. Audit of 46 structural-led trades showed 57% WR
      //   (vs 69% for non-structural-led). At 3 samples (~3 seconds), the tier was
      //   committing on grand-trend + channel alignment alone, bypassing all other
      //   quality gates. Required 12 samples now lets other signals (gap, momentum,
      //   flow, htf-patterns) catch up and either confirm or override. Still faster
      //   than confluence (5 samples after V9.11.3? — no, confluence stays at 4).
      //   The 12-sample bar means structural-led must hold for ~12s before locking,
      //   giving time for the structural reading to be vetted against tape/flow.
      needSamples=12;tierLabel='structural-led';
    } else if(_isLcSuperConf){
      needSamples=3;tierLabel='super-confluence';   // V6.1.3: ~3s
    } else if(_isLcTapeLed){
      needSamples=3;tierLabel='tape-led';            // V6.1.3: ~3s (V6.1.2 quality guards in place)
    } else if(_isLcConf){
      needSamples=4;tierLabel='confluence';          // V6.5: 5 → 4
    } else if(_isLcRising){
      needSamples=5;tierLabel='rising-confluence';   // V6.5: 6 → 5
    } else if(q>=85&&conv>=20&&fgtAbs>=3.5&&cleanRegime&&!hostileWindow&&tapeSuperStrong&&kalshiStronglyAgrees){
      needSamples=5;tierLabel='exceptional';         // V6.5: 7 → 5
    } else if(q>=75&&conv>=18&&fgtAbs>=3.0&&!hostileWindow&&tapeStronglyAgrees&&kalshiAgrees){
      needSamples=10;tierLabel='strong+';            // V6.5: 15 → 10
    } else if(q>=70&&conv>=15&&fgtAbs>=2.5&&!hostileWindow&&(tapeStronglyAgrees||kalshiStronglyAgrees||kalshiAgrees)){
      needSamples=20;tierLabel='strong';             // V6.5: 30 → 20
    } else if(q>=55&&conv>=10&&fgtAbs>=1.5&&!hostileWindow){
      needSamples=30;tierLabel='default';            // V6.5: 60 → 30 (~30s — fast cadence)
    } else {
      needSamples=60;tierLabel='patient';            // V6.5: 100 → 60 (~1min cap)
    }
    // V5.6.7: LEARNING — apply tier-specific samples adjustment from past performance.
    //   If tier X has been winning >75%, lock faster (it's reliable). If <55%, lock slower
    //   (require more confirmation). _learnings.multipliers.tierSamplesAdjust is computed
    //   from historical tier outcomes and is a fraction (-0.15 to +0.25).
    const _learnings=taraLearningsRef.current;
    const _tierAdjustFrac=_learnings?.multipliers?.tierSamplesAdjust?.[tierLabel]||0;
    // V5.6.8: regime+direction speed adjustment — the active learning signal.
    //   Proven combos lock 20% faster. Weak combos take 20% more confirmation
    //   (but never sit out — that was the V5.6.7 mistake the user rejected).
    const _regDirKey=(analysis?.regime||'UNKNOWN')+'|'+claimedDir;
    const _regDirSpeedAdj=_learnings?.multipliers?.regimeDirSpeedAdj?.[_regDirKey]||0;
    needSamples=Math.round(needSamples*(1+_tierAdjustFrac)*(1+_regDirSpeedAdj)*_cushionMult*_flowConfMult);
    // V6.2.6: Soft hint trims sample requirement by 40% on lifecycle side too. Floor of 2.
    const _lcHintActive=softHintRef.current>0&&(Date.now()-softHintRef.current)<10000;
    if(_lcHintActive)needSamples=Math.max(2,Math.round(needSamples*0.6));
    // V6.4: Multi-signal pivot — when 3+ signals shifted same direction in last 30s and
    //   the direction matches our intended commit, drop sample requirement by 50%. This is
    //   "outside-context info arriving inside the data" — react fast.
    const _pivot=computeMultiSignalPivot();
    if(_pivot.active&&_pivot.dir===(claimedDir||tc.call)){
      needSamples=Math.max(2,Math.round(needSamples*0.5));
    }
    // V6.2.6: Hard force — commit immediately, bypass sample requirement entirely
    const _hardForceActive=hardForceRef.current>0&&(Date.now()-hardForceRef.current)<5000;
    // V10.9.7: speed dial removed. needSamples and search phase run at the neutral
    //   (dial=50, multiplier=1.0) baseline — the tier ladder above already set
    //   needSamples per signal quality, V10.9.0 scaled it for signal agreement,
    //   and V10.8.2 scaled it for cushion history. ULP (V9.8.0, below) still
    //   applies its own 50% cut when Kalshi is moving fast in Tara's direction.
    const _speedMultLc=1.0;
    // Cap so lock has time to be scored after committing
    needSamples=Math.min(needSamples,Math.max(15,remaining-90));
    // V9.8.0: URGENT LOCK PROTOCOL — when V9.7.6 urgency is active (Kalshi YES
    //   moving ≥4¢/30s in Tara's direction), cut needSamples by 50% and skip
    //   remaining search phase. Stops Tara from waiting 30s while Kalshi prices
    //   in the move from 50¢ → 70¢. The previous V9.7.6 only dropped the
    //   conviction floor by 30% — it didn't change samples. So lock still took
    //   25-40 seconds even when the market was actively moving.
    //   With ULP: lock fires in ~12s instead of ~33s on a moving market.
    let _ulpApplied=false;
    let _samplesNeededOriginal=needSamples;
    let _searchPhaseSkipped=false;
    if(_v97DiagRef?.current?.urgencyApplied){
      _ulpApplied=true;
      // Cut samples by 50%, floor of 1
      needSamples=Math.max(1,Math.round(needSamples*0.5));
      // Mark for telemetry that search phase will be force-skipped if past 50%
      _searchPhaseSkipped=true; // signal — actual skip happens in search check below
      try{console.info('[ULP] urgency active — needSamples',_samplesNeededOriginal,'→',needSamples,'· search phase fast-tracked');}catch(_){}
    }
    // V6.5.7: ETA — expose how many seconds until commit at current dial. Engine ticks
    //   roughly 1Hz, so samplesRemaining ≈ secondsRemaining. Plus search phase if still in it.
    const _etaSamplesRem=Math.max(0,needSamples-samples);
    // V7.5: search phase floor 3 → 2 at low dial; multiplier same as samples
    const _searchPhaseLc=Math.max(2,Math.round(10*_speedMultLc));
    // V9.8.0: when ULP active, cut search phase to 50% — if past that, skip entirely
    const _effectiveSearchPhase=_ulpApplied?Math.max(1,Math.round(_searchPhaseLc*0.5)):_searchPhaseLc;
    const _etaSearchRem=Math.max(0,_effectiveSearchPhase-elapsedSec);
    const _lockEtaSec=_etaSearchRem+_etaSamplesRem;
    if(tc&&tc._ctx){tc._ctx.lockEtaSec=_lockEtaSec;tc._ctx.samples=samples;tc._ctx.needSamples=needSamples;tc._ctx.tierLabel=tierLabel;tc._ctx.ulpApplied=_ulpApplied;tc._ctx.samplesNeededOriginal=_samplesNeededOriginal;}
    if(taraCallSnapshotRef.current!==null)return; // already committed — don't overwrite
    // V6.5.7 INVERSION RELEASE: stop the "original lean held through flip" failure mode.
    //   The user screenshot showed Tara locking DOWN even though tape was 98% super-strong UP
    //   and regime was TRENDING UP — because claimedDir was set early and never released.
    //   Now: if claimed direction strongly opposes current tape (>=70% the other way) AND
    //   FGT no longer agrees (≤1 timeframe in claim direction), release the claim entirely
    //   and reset samples. Tara will then re-form from current conditions.
    if(claimedDir&&samples>0){
      const _w30=analysis?.tapeWindows?.w30?.buyPct;
      const _w60=analysis?.tapeWindows?.w60?.buyPct;
      const _curBuyPct=_w30!=null&&_w60!=null?Math.max(_w30,_w60):(_w30??_w60);
      const _tapeOpposesClaim=_curBuyPct!=null&&((claimedDir==='UP'&&_curBuyPct<=30)||(claimedDir==='DOWN'&&_curBuyPct>=70));
      const _fgtVal=analysis?.mtfAlignment||0;
      const _fgtAgreesClaim=(claimedDir==='UP'&&_fgtVal>=1.5)||(claimedDir==='DOWN'&&_fgtVal<=-1.5);
      if(_tapeOpposesClaim&&!_fgtAgreesClaim){
        // Conditions inverted strongly. Release claim + reset samples to re-form.
        taraCallSampleRef.current={dir:null,count:0};
        if(_flatGapWatchRef.current)_flatGapWatchRef.current.earlyConfirmation=null;
        _noteGateHold('formation-tape-release',`Releasing provisional ${claimedDir}: tape ${_curBuyPct}% opposes it and FGT ${_fgtVal} does not confirm.`,
          {previousDirection:claimedDir,previousSamples:samples,tapeBuyPct:_curBuyPct,fgt:_fgtVal});
        try{console.info('[V6.5.7] claimedDir',claimedDir,'released — tape',_curBuyPct,'% opposes, FGT',_fgtVal);}catch(_){}
        return; // skip this lock attempt; next tick will re-form from current conditions
      }
    }
    // V6.5.7: Hard-force is now a TRUE instant lock. If pressed AND any directional lean
    //   exists (claimed or current), commit RIGHT NOW with whatever data we have.
    const _instantForceReady=_hardForceActive&&(claimedDir==='UP'||claimedDir==='DOWN'||tc.call==='UP'||tc.call==='DOWN');
    // V9.8.4: STALE-FEED GUARD — refuse to commit a new lock when live tick feed
    //   has been frozen ≥60s. Tara would otherwise lock on dead data the engine
    //   thinks is real. User can switch to backup feed via FEED pill in header.
    //   _instantForceReady (hard user-force) bypasses this — if the user is
    //   manually forcing a lock, they've made the call.
    if(feedFrozenRef.current&&!_instantForceReady){
      if(_flatGapWatchRef.current)_flatGapWatchRef.current.earlyConfirmation=null;
      _noteGateHold('feed-frozen','Live tick feed is frozen; refusing a new Call.',{samples,requiredSamples:needSamples});
      // Don't fire lock. Engine continues running so user sees what it would do,
      // but no entry is created and no sound fires. UI shows the FEED FROZEN
      // banner; the call card will show a stale-feed footer note.
      return;
    }
    if(_earlyFlatGapProof){
      const _earlyRequired=Math.max(3,needSamples);
      if(!earlyFlatGapReady(_earlyFlatGapProof,_earlyRequired,Date.now())){
        _noteGateHold('early-flat-gap-confirmation',`Confirming early ${tc.call}: ${_earlyFlatGapProof.evidence.samples.toFixed(1)}/${_earlyRequired} fresh stability samples.`,
          {..._earlyFlatGapProof.evidence,requiredSamples:_earlyRequired});
        return;
      }
      _traceDecision('early-flat-gap-confirmed',`Fresh early ${tc.call} confirmation at ${_earlyFlatGapProof.evidence.entryCost}c; normal commit checks remain.`,
        {..._earlyFlatGapProof.evidence,requiredSamples:_earlyRequired});
    }
    // V12.5: QUALITY-TRIGGERED EARLY LOCK
    // Root cause of the late-entry / 90c+ problem: needSamples counts ticks, not
    // signal quality. A clear 65c setup at minute 2 waits 60 ticks; by tick 60
    // price has moved to 90c and Tara locks late at terrible EV.
    //
    // Data (last 500): 60-75c band = 72% WR / +5.8c EV. 90c+ = 93% WR / -4.3c EV.
    // Same trades, different entry price. Early = good. Late = wasteful.
    //
    // This block fires BEFORE the sample counter check. When signal quality is
    // HIGH and odds are GOOD, we drop needSamples to 1 and commit now.
    //
    // THREE-TIER system:
    //   TIER 1 — Triple confluence (strongest): gap >= 12bps + Kalshi 62-78c + trending
    //     regime + tape agrees. Fire after just 90s (let signals settle minimally).
    //   TIER 2 — Strong gap + good odds: gap >= 8bps + Kalshi 60-80c + not hostile
    //     window. Fire after 120s.
    //   TIER 3 — Good odds alone: Kalshi 65-75c + direction claimed 60s+ + not chop.
    //     Fire after 180s (3 min). Catches the 'obvious from the start' windows.
    //
    // Guards (any of these blocks all tiers):
    //   - SQUEEZE regime (Rule B absolute block)
    //   - Hostile window (DEAD/WHIPSAW)
    //   - Already past the good-odds window (Kalshi > 82c = too late, use V12.4)
    //   - No claimed direction yet (still in search phase)
    //   - Feed frozen
    try{
      if(
        !taraCallSnapshotRef.current&&    // not already committed
        claimedDir&&                      // direction established
        !_instantForceReady&&             // not a manual force
        !feedFrozenRef.current            // feed alive
      ){
        const _elDir=claimedDir;
        // Gap vs strike (bps, positive = price on claimedDir's side)
        const _elGapBps=targetMargin>0&&currentPrice>0
          ?((claimedDir==='UP'
              ?(currentPrice-targetMargin)
              :(targetMargin-currentPrice))/targetMargin)*10000
          :0;
        // Kalshi price on the claimed side (0-100)
        const _elKalRaw=typeof kalshiYesPrice==='number'?kalshiYesPrice:null;
        const _elKalDir=_elKalRaw!=null
          ?(claimedDir==='UP'?_elKalRaw:(100-_elKalRaw))
          :null;
        const _elRegime=analysis?.regime||'';
        // V12.6.1 FIX: the main-engine regime string is stuck on 'RANGE-CHOP' ~90% of
        //   the time and NEVER emits 'TRENDING UP/DOWN' — so the old string match meant
        //   Tier 1 (needs trending) never fired and Tiers 2/3 (need not-chop) were always
        //   blocked. Use the V10.7.36 classifier booleans instead — those are the real
        //   trend/chop/highvol reads, exposed on rawSignalScores.
        const _elRss=analysis?.rawSignalScores||{};
        const _elIsBadRegime=_elRegime==='SHORT SQUEEZE'||_elRegime==='LONG SQUEEZE';
        const _elIsTrending=_elRss._isTrend===true;
        const _elIsChop=_elRss._isChop===true||_elRss._isHighVol===true;
        const _elHostile=hostileWindow; // DEAD or WHIPSAW — already computed above
        const _elTapeWith=tapeStronglyAgrees||tapeSuperStrong;
        // V12.7: HalfTrend + SuperTrend confirmation for the early-lock decision.
        //   _tcWith   = both indicators aligned WITH the claimed direction
        //   _tcAgainst= both indicators aligned AGAINST the claimed direction
        //   _tcConflict= the two indicators disagree with each other
        const _elTC=_elRss._trendConfirm||null;
        const _tcWith=_elTC&&_elTC.aligned&&_elTC.dir===claimedDir;
        const _tcStrongWith=_tcWith&&_elTC.strongAligned;
        const _tcAgainst=_elTC&&_elTC.aligned&&_elTC.dir&&_elTC.dir!==claimedDir;
        const _tcConflict=_elTC&&_elTC.conflict;
        // Guard: odds already bad (let V12.4 handle the 88c+ case)
        const _elOddsGood=_elKalDir!=null&&_elKalDir>=58&&_elKalDir<=82;
        if(!_elIsBadRegime&&!_elHostile&&_elOddsGood&&_elKalDir!=null){
          // TIER 1: triple confluence — gap + Kalshi sweet spot + trending + tape
          if(
            _elGapBps>=12&&
            _elKalDir>=62&&_elKalDir<=78&&
            _elIsTrending&&
            // V13.4.179: _tcStrongWith REMOVED as a tape substitute. Signal audit on the
            //   cloud log (n=432 resolved with trendConfirm data), cost-controlled to the
            //   55-69c band so this is not the pricing story again:
            //     HT+ST both agree with call:  n=80   WR 60.0%  EV -2.43c/ct
            //     not both agreeing:           n=106  WR 73.6%  EV +10.52c/ct
            //   Same avg cost (62.4 vs 63.1c), 13.6pt WR gap. By the time both lagging
            //   trend indicators confirm a 15m call, the move is spent -- the same
            //   inversion that got htfPatterns zeroed (V10.8.1), one timescale down.
            //   Tape agreement stays as the T1 trigger: it measures +8.45c/ct (n=99,
            //   75.8% WR) -- genuinely predictive. Trend-agree trades are NOT blocked
            //   anywhere (in-band they're ~breakeven; V13.4.146 lesson) -- the engine
            //   just no longer ACCELERATES into them on the strongest early-lock tier.
            _elTapeWith&&
            !_tcAgainst&&                   // V12.7: never fire T1 when both trend indicators oppose
            elapsedSec>=90
          ){
            needSamples=1;
            _earlyLockFired=true;
            _earlyLockTier=1;
            try{console.info('[V12.5 EARLY-LOCK T1] gap',_elGapBps.toFixed(0)+'bps kalshi',_elKalDir.toFixed(0)+'c trending+tape elapsed',elapsedSec+'s');}catch(_){}
          }
          // TIER 2: strong gap + good odds + not hostile
          else if(
            _elGapBps>=8&&
            _elKalDir>=60&&_elKalDir<=80&&
            !_elIsChop&&
            !_tcAgainst&&                   // V12.7: block when both trend indicators oppose
            elapsedSec>=120
          ){
            needSamples=1;
            _earlyLockFired=true;
            _earlyLockTier=2;
            try{console.info('[V12.5 EARLY-LOCK T2] gap',_elGapBps.toFixed(0)+'bps kalshi',_elKalDir.toFixed(0)+'c not-chop elapsed',elapsedSec+'s');}catch(_){}
          }
          // TIER 3: Kalshi in sweet spot + time + not chop
          else if(
            _elKalDir>=65&&_elKalDir<=75&&
            _elGapBps>=4&&
            !_elIsChop&&
            !_tcAgainst&&                   // V12.7: weakest tier — block on any trend opposition
            elapsedSec>=180
          ){
            needSamples=1;
            _earlyLockFired=true;
            _earlyLockTier=3;
            try{console.info('[V12.5 EARLY-LOCK T3] kalshi',_elKalDir.toFixed(0)+'c gap',_elGapBps.toFixed(0)+'bps elapsed',elapsedSec+'s');}catch(_){}
          }
        }
      }
    }catch(_elErr){}

    // V12.4: HIGH-CERTAINTY LATE-WINDOW FAST-LOCK
    //   Problem: when Kalshi is at 88-99c on one side and the gap vs strike agrees,
    //   the trade is as clear as it gets. But the sample accumulation counter doesn't
    //   know what Kalshi says — it just counts ticks. So Tara scans all the way to
    //   the force-commit at end-of-window (isSystemLocked), entering at 94c+ with
    //   near-zero EV instead of locking at 82c earlier with meaningful edge.
    //
    //   Conditions (ALL must hold):
    //     1. Window past 8 minutes (clockSeconds >= 480, timeFraction >= 0.53 on 15m)
    //        — don't fast-lock early; let signals develop normally first half.
    //     2. Kalshi strongly favors the claimed direction by >= 88c
    //        — market has already priced the move, we're just confirming.
    //     3. Gap vs strike agrees with direction (gapForDirection > 0)
    //        — price is already on the right side of the line.
    //     4. A direction is already claimed (claimedDir set) — not a brand-new scan.
    //        This means samples have been accumulating and direction is consistent.
    //     5. Not SQUEEZE regime (Rule B blocks those regardless).
    //     6. Not HIGH VOL CHOP with a late entry (chop can spike through at 95c and reverse).
    //
    //   Action: set needSamples = 1 so the existing commit check fires immediately
    //   on the next condition. Tier stamps as 'fast-lock-high-cert' for audit.
    //   Stamped in log as fastLockFired=true.
        try{
      if(
        !taraCallSnapshotRef.current&&    // not already committed
        claimedDir&&                      // direction established (samples accumulating)
        elapsedSec>=480&&                 // past 8 minutes into the 15m window
        !_instantForceReady               // not a manual force (those use their own path)
      ){
        const _fastKal=analysis?.kalshiForDirection??null; // Kalshi% for claimedDir
        const _fastKalRaw=typeof kalshiYesPrice==='number'?kalshiYesPrice:null;
        // kalshiForDirection = probability on claimedDir's side (already direction-adjusted)
        // Fall back to raw yes price adjusted for direction if kalshiForDirection unavailable
        const _fastKalDir=_fastKal!=null
          ?_fastKal
          :(_fastKalRaw!=null?(claimedDir==='UP'?_fastKalRaw:(100-_fastKalRaw)):null);
        const _fastGapOk=analysis?.rawProbAbove!=null
          ?((claimedDir==='UP'&&analysis.rawProbAbove>50)||(claimedDir==='DOWN'&&analysis.rawProbAbove<50))
          :true; // gap unknown = allow (don't over-restrict)
        const _fastRegime=analysis?.regime||'';
        const _fastIsSqueezeOrHighChop=
          _fastRegime==='SHORT SQUEEZE'||
          _fastRegime==='LONG SQUEEZE'||
          (_fastRegime==='HIGH VOL CHOP'&&elapsedSec>=600); // HVC + <5m left = volatile
        if(
          _fastKalDir!=null&&
          _fastKalDir>=88&&
          _fastGapOk&&
          !_fastIsSqueezeOrHighChop
        ){
          // All conditions met. Reduce needSamples to 1 so the commit check below fires.
          const _prevNeed=needSamples;
          needSamples=1;
          _fastLockFired=true;
          try{console.info('[V12.4 FAST-LOCK] Kalshi',_fastKalDir.toFixed(0)+'c on',claimedDir,
            '· gap OK · needSamples',_prevNeed,'->1 · clockSec',clockSeconds);}catch(_){}
        }
      }
    }catch(_flErr){}
    if(callFormationReady(taraCallSampleRef.current,needSamples,Date.now())||_instantForceReady){
      // V10.8.0: MINIMUM CONVICTION TIME FLOOR.
      //   User trades later in windows anyway — signals at second 3 are noise.
      //   No lock fires in the first 60 seconds regardless of tier.
      //   RANGE-CHOP: 90 second minimum — direction takes longer to establish.
      //   This is not a sitout. It just ensures signals have settled before committing.
      //   User said "fine as long as Tara locks within first 5 minutes" — 90s is 1.5min.
      //   Hard-force bypasses this (manual override always works).
      // V10.8.2: Cushion history handles intelligent timing.
      //   V10.8.0's hard 60s minimum floor removed — it blocked fast confluence locks
      //   (super-confluence should fire in 3s, not 60s) and cost 12% WR on 48 trades.
      //   The cushion multiplier (0.6× to 1.8×) handles timing intelligence without
      //   a blunt override that ignores tier quality.
      //   not current tc.call. If tc.call has flipped temporarily, we still commit to
      //   the original direction Tara was building toward.
      const _committedCall=claimedDir||tc.call;  // claimed wins; tc.call as fallback for safety
      // V5.6.7: Compute current session for learning context
      const _sessionInfo=typeof getMarketSessions==='function'?getMarketSessions():null;
      const _session=_sessionInfo?.dominant||analysis?.session?.name||'UNKNOWN';
      // V6.2.2: EDGE GUARD — final pre-commit check. Compute what edge vs Kalshi would
      //   look like at this moment. If Kalshi has already priced the move (edge ≤-7pt),
      //   skip the commit and snapshot a SIT_OUT instead. Capturing the LATE ENTRY case:
      //   user's screenshot showed Tara locking UP at posterior 81% while Kalshi was 90%
      //   = -9pt edge. Even though Tara was "right" about direction, no edge = no money.
      // V6.2.6: hardForce bypasses this — user explicitly accepted the risk via dialog.
      const _kAtCommit=typeof kalshiYesPrice!=='undefined'&&kalshiYesPrice!=null?Number(kalshiYesPrice):null;
      const _postAtCommit=analysis?.rawProbAbove;
      // V14.4.6: honor the existing legacy entry-filter configuration here too.
      if(!NO_ENTRY_GATES&&_kAtCommit!=null&&_postAtCommit!=null&&!_hardForceActive){
        const _taraDirConf=_committedCall==='UP'?_postAtCommit:(100-_postAtCommit);
        const _kalshiDirConf=_committedCall==='UP'?_kAtCommit:(100-_kAtCommit);
        const _edgeAtCommit=_taraDirConf-_kalshiDirConf;
        if(_edgeAtCommit<=-7){
          const _edgeSnap={
            call:'SIT_OUT',direction:null,confidence:0,
            reason:`Edge would be ${Math.round(_edgeAtCommit)}pt — Kalshi at ${Math.round(_kalshiDirConf)}% is already pricing this in`,
            atSecondsLeft:timeState.minsRemaining*60+timeState.secsRemaining,
            atPosterior:_postAtCommit,
            kalshiAtLock:_kAtCommit,
            locked:true,earlyLock:false,
            isConfluent:false,isSuperConfluent:false,isRisingConfluence:false,isTapeLed:false,isStructuralLed:false,
            samples,needSamples:0,
            tier:'edge-sitout',
            session:_session,
            regime:analysis?.regime||'',
            qScore:Math.round(qualityGate?.score||0),qScoreV2:Math.round(qualityGateV2?.score||0),qScoreV2Components:qualityGateV2?.components||null,
            fgt:analysis?.mtfAlignment,
          };
          taraCallSnapshotRef.current=_applyNoSitout(_edgeSnap);
          _logSnapshotEntry(taraCallSnapshotRef.current); // V7.10.1
          _persistLock();
          return;
        }
      }
      // V12.7: LATE-ENTRY TREND-CONFIRMATION FILTER
      //   For late entries (<5min left), HalfTrend + SuperTrend are the cleanest read on
      //   whether momentum still holds. Rule (per spec): if BOTH indicators point AGAINST
      //   the direction Tara wants to lock on a late entry, strongly discourage the lock
      //   UNLESS gap + tape are both very strong (those override as primary signals).
      //   This is the late-entry quality filter — it does NOT touch early/mid locks.
      {
        const _leSecLeft=timeState.minsRemaining*60+timeState.secsRemaining;
        const _leIsLate=_leSecLeft<=300; // <=5 minutes left
        const _leTC=analysis?.rawSignalScores?._trendConfirm||null;
        const _leDir=_committedCall;
        if(_leIsLate&&_leTC&&_leTC.aligned&&_leTC.dir&&_leDir&&_leTC.dir!==_leDir&&!_instantForceReady){
          // Both HalfTrend AND SuperTrend agree with each other and BOTH oppose the lock.
          // Check the override: only proceed if gap AND tape are both very strong.
          const _leGapStrong=Math.abs(analysis?.realGapBps||0)>=20;
          const _leTapeStrong=tapeSuperStrong===true;
          const _leOverride=_leGapStrong&&_leTapeStrong;
          if(!_leOverride){
            const _leSnap={
              call:'SIT_OUT',direction:null,confidence:0,
              reason:`Late entry (${Math.round(_leSecLeft/60)}m left) but HalfTrend + SuperTrend both ${_leTC.dir} oppose ${_leDir} — momentum against the lock`,
              atSecondsLeft:_leSecLeft,
              atPosterior:analysis?.rawProbAbove,
              kalshiAtLock:typeof kalshiYesPrice!=='undefined'&&kalshiYesPrice!=null?Number(kalshiYesPrice):null,
              locked:true,earlyLock:false,
              isConfluent:false,isSuperConfluent:false,isRisingConfluence:false,isTapeLed:false,isStructuralLed:false,
              samples,needSamples:0,
              tier:'late-trend-oppose-sitout',
              session:_session,
              regime:analysis?.regime||'',
              qScore:Math.round(qualityGate?.score||0),qScoreV2:Math.round(qualityGateV2?.score||0),qScoreV2Components:qualityGateV2?.components||null,
              fgt:analysis?.mtfAlignment,
              isNoGo:true,
              noGoCategory:'late-trend-oppose',
            };
            taraCallSnapshotRef.current=_applyNoSitout(_leSnap);
            _logSnapshotEntry(taraCallSnapshotRef.current);
            _persistLock();
            try{console.info('[V12.7 LATE-TREND-OPPOSE] sit out:',_leTC.dir,'opposes',_leDir,'· secLeft',_leSecLeft);}catch(_){}
            return;
          }
        }
      }
      // V9.9.6: TIER-1 ONLY MODE — convert non-Tier-1 locks to SIT_OUT when enabled.
      //   Tier-1 setups (confluence, super-confluence, structural-led, tape-led,
      //   rising-confluence, patient, exceptional, strong+) have observed WR
      //   meaningfully above Kalshi's pricing. Tier-2/3 (single, default, strong,
      //   time-cap-commit, timer-commit, no-go-data, no-go-edge) often run at-edge
      //   or below — taking them is roughly break-even after Kalshi friction. When
      //   the user enables this toggle, Tara mechanically refuses these marginal
      //   locks. They show as SIT_OUT on the call card with reasoning surfaced;
      //   Discord broadcasts skip per the existing override-commit suppression.
      const _t1Set=new Set(['confluence','super-confluence','structural-led','tape-led','rising-confluence','patient','exceptional','strong+']);
      if(tradingSettings?.tier1OnlyMode&&!_t1Set.has(tierLabel)&&!_instantForceReady){
        const _t1Snap={
          call:'SIT_OUT',direction:null,confidence:0,
          reason:`Tier-1 only mode is active — '${tierLabel||'unknown'}' tier skipped`,
          atSecondsLeft:timeState.minsRemaining*60+timeState.secsRemaining,
          atPosterior:analysis?.rawProbAbove,
          kalshiAtLock:typeof kalshiYesPrice!=='undefined'&&kalshiYesPrice!=null?Number(kalshiYesPrice):null,
          locked:true,earlyLock:false,
          isConfluent:false,isSuperConfluent:false,isRisingConfluence:false,isTapeLed:false,isStructuralLed:false,
          samples,needSamples:0,
          tier:'tier1-only-skip',
          session:_session,
          regime:analysis?.regime||'',
          qScore:Math.round(qualityGate?.score||0),qScoreV2:Math.round(qualityGateV2?.score||0),qScoreV2Components:qualityGateV2?.components||null,
          fgt:analysis?.mtfAlignment,
          isNoGo:true,
          noGoCategory:'tier1-only-skip',
          wasOverriddenSitOut:true, // suppress Discord per V9.0 rule
          // V9.9.6: track the would-have-been call so post-hoc audit can show
          //   "tier1-only mode skipped these would-have-been wins / losses".
          wouldHaveBeen:_committedCall,
          wouldHaveBeenTier:tierLabel,
        };
        taraCallSnapshotRef.current=_applyNoSitout(_t1Snap);
        _logSnapshotEntry(taraCallSnapshotRef.current);
        _persistLock();
        return;
      }
      // V13.4.324: TREND-AT-LOCK SIT-OUT (patient/structural-led). Same audit as the
      //   time-cap-commit gate above: 'patient' measures worse when _isTrend is true
      //   at lock than when false; 'structural-led' shows the same direction on a
      //   smaller sample. directional-lock is deliberately excluded (see the other
      //   gate's comment for why). Snapshot shape follows tier-1-only-mode above.
      if((tierLabel==='patient'||tierLabel==='structural-led')&&analysis?.rawSignalScores?._isTrend===true&&!_instantForceReady){
        const _talSnap2={
          call:'SIT_OUT',direction:null,confidence:0,
          reason:`[V13.4.324] trend-at-lock -- '${tierLabel}' tier measures worse when the market is trending at the moment of lock -- sitting out instead`,
          atSecondsLeft:timeState.minsRemaining*60+timeState.secsRemaining,
          atPosterior:analysis?.rawProbAbove,
          kalshiAtLock:typeof kalshiYesPrice!=='undefined'&&kalshiYesPrice!=null?Number(kalshiYesPrice):null,
          locked:true,earlyLock:false,
          isConfluent:false,isSuperConfluent:false,isRisingConfluence:false,isTapeLed:false,isStructuralLed:false,
          samples,needSamples:0,
          tier:'trend-at-lock-sitout',
          session:_session,
          regime:analysis?.regime||'',
          qScore:Math.round(qualityGate?.score||0),qScoreV2:Math.round(qualityGateV2?.score||0),qScoreV2Components:qualityGateV2?.components||null,
          fgt:analysis?.mtfAlignment,
          isNoGo:true,
          noGoCategory:'trend-at-lock-sitout',
          wouldHaveBeen:_committedCall,
          wouldHaveBeenTier:tierLabel,
        };
        taraCallSnapshotRef.current=_applyNoSitout(_talSnap2);
        _logSnapshotEntry(taraCallSnapshotRef.current);
        _persistLock();
        return;
      }
      const _committedReason=claimedDir===tc.call?tc.reason:`Locked ${_committedCall} — original lean held through flip`;
      taraCallSnapshotRef.current={
        call:_committedCall,direction:_committedCall,confidence:tc.confidence,reason:_committedReason,
        // V9.9.5: tier label persisted on snapshot so Discord broadcast can render it,
        //   and so the call card has an authoritative source after lock formation. Mirrors
        //   the value written to the call log entry at L23673.
        // V10.2.23 — DUPLICATE-KEY WARNING FIX. This site originally set tier:tierLabel,
        //   and ~57 lines below it ALSO set tier:_hardForceActive?'user-forced':tierLabel.
        //   Both keys winning sequentially in JS — the second always overrode the first,
        //   so user-force properly resolved. Warning was benign but masked code smell.
        //   Removed the duplicate; kept the conditional version below.
        atSecondsLeft:timeState.minsRemaining*60+timeState.secsRemaining,
        atPosterior:analysis?.rawProbAbove,
        // V6.5.7: lock-time price + strike + quality + FGT + regime persisted in snapshot.
        //   The lock broadcast reads these so it shows lock-time state, not current state.
        lockPrice:currentPrice,
        strike:targetMargin,
        // V6.0.8: Kalshi YES price (0-100) at the moment Tara locked. Combined with
        //   atPosterior, this gives the "edge" — how much more confident Tara is than
        //   the market about this direction. Edge is what determines profitability,
        //   not WR alone. Same 70% WR at +15pt edge ≫ same 70% WR at -10pt edge.
        kalshiAtLock:typeof kalshiYesPrice!=='undefined'&&kalshiYesPrice!=null?Number(kalshiYesPrice):null,
        locked:true,
        earlyLock:!analysis?.isSystemLocked,
        // V5.7.7: confluence flags persist on the lock snapshot
        isConfluent:tc?._ctx?.isConfluent||false,
        isSuperConfluent:tc?._ctx?.isSuperConfluent||false,
        // V6.0.1: rising-confluence flag also persists for analytics
        isRisingConfluence:tc?._ctx?.isRisingConfluence||false,
        // V6.0.7: tape-led flag also persists for analytics + learning bucket
        isTapeLed:tc?._ctx?.isTapeLed||false,
        // V6.2.0: structural-led flag (grand trend + channel) — separate learning bucket
        isStructuralLed:tc?._ctx?.isStructuralLed||false,
        // V6.3.3: Log structural alignment + raw posterior at lock time. Lets future
        //   loss diagnostics show: "did structural agree when she locked?" and "did
        //   calibration shift the underlying signal heavily?". Without this, every
        //   debugging conversation requires me to guess.
        structAtLock:analysis?.structAlignment?{
          dir:analysis.structAlignment.dir,
          count:analysis.structAlignment.count,
        }:null,
        rawPosteriorAtLock:analysis?.rawPosteriorUncalibrated??analysis?.rawProbAbove??null, /* V9.11.3: distinguish raw vs calibrated */
        calibratedPosteriorAtLock:analysis?.rawProbAbove??null, /* V9.11.2 fix: was reading analysis.posterior but useMemo exposes it as rawProbAbove */
        /* V13.4.284: used the phantom analysis.posterior as the CALIBRATED value and
           rawProbAbove as the RAW one -- both wrong. The two lines directly above
           define the real pair: rawProbAbove IS the calibrated value, and the raw one
           is rawPosteriorUncalibrated (written at ~L43863). analysis.posterior is never
           written at all, so this logged null on every single lock and the calibration
           swing has no history. Correct orientation is calibrated - raw; still null when
           the uncalibrated value is genuinely absent, because a fabricated 0 would read
           as "calibration did nothing" rather than "unknown". */
        calSwingAtLock:(()=>{
          const _cal=Number(analysis?.rawProbAbove);
          const _raw=Number(analysis?.rawPosteriorUncalibrated);
          return (Number.isFinite(_cal)&&Number.isFinite(_raw))?Math.round(_cal-_raw):null;
        })(),
        // V6.3.5: full signal scores + tape consensus at lock for analysis exports
        signalScoresAtLock:(typeof taraCall!=='undefined'&&taraCall&&taraCall.rawSignalScores)?{...taraCall.rawSignalScores}:(analysis?.rawSignalScores?{...analysis.rawSignalScores}:null),/*V13.4.66: prefer LIVE taraCall.rawSignalScores; analysis was stale so signals logged on only 3% of locks, blocking tape-weight backtest*/
        // V10.7.53: persist strike, version, and engine reasoning for full audit trail
        strikeAtLock:Number(targetMargin)||0,
        // V13.4.85: FAIR-VALUE INPUTS. The archive recorded strike and closingPrice but NEVER
        //   where spot sat relative to strike AT LOCK, so no barrier-probability model could be
        //   fitted or validated on history (gapAtEntry was stamped but never whitelisted, so it
        //   persisted 0 times across 4320 settled locks). These are what a distance/vol/time
        //   model needs: with them we can measure BTC's real scaling exponent H instead of
        //   assuming sqrt-t, then price each window and compare against Kalshi. ASCII-only.
        spotAtLock:Number(currentPrice)||0,
        distBpsAtLock:(Number(targetMargin)>0&&Number(currentPrice)>0)?Math.round(((Number(currentPrice)-Number(targetMargin))/Number(targetMargin))*1000000)/100:null,
        volBpsAtLock:Number.isFinite(analysis?.atrBps)?Math.round(analysis.atrBps*10)/10:null,
        baselineVersion:typeof BASELINE_VERSION!=='undefined'?BASELINE_VERSION:null,
        reasoning:Array.isArray(analysis?.reasoning)?analysis.reasoning.slice(0,40):null,
        // V10.7.54: bias stamps now on ALL entry paths (was 59% coverage in V10.7.50 — only on _logSnapshotEntry path)
        ...(()=>{
          const _resolved=taraCallLogRef.current.filter(e=>e&&(e.result==='WIN'||e.result==='LOSS'));
          const _last30=_resolved.slice(-30);
          if(_last30.length<10)return{};
          const _up=_last30.filter(e=>e.dir==='UP');
          const _dn=_last30.filter(e=>e.dir==='DOWN');
          const _upWR=_up.length?_up.filter(e=>e.result==='WIN').length/_up.length:null;
          const _dnWR=_dn.length?_dn.filter(e=>e.result==='WIN').length/_dn.length:null;
          return{
            _v10_7_50_recentDirN:_last30.length,
            _v10_7_50_recentUpPct:Math.round((_up.length/_last30.length)*100),
            _v10_7_50_recentDnPct:Math.round((_dn.length/_last30.length)*100),
            _v10_7_50_recentUpWR:_upWR!=null?Math.round(_upWR*1000)/10:null,
            _v10_7_50_recentDnWR:_dnWR!=null?Math.round(_dnWR*1000)/10:null,
          };
        })(),
        tapeAtLock:analysis?.tapeWindows?{
          w15Buy:analysis.tapeWindows.w15?.buyPct??null,
          w30Buy:analysis.tapeWindows.w30?.buyPct??null,
          w60Buy:analysis.tapeWindows.w60?.buyPct??null,
          w15Total:(analysis.tapeWindows.w15?.buys||0)+(analysis.tapeWindows.w15?.sells||0),
          w30Total:(analysis.tapeWindows.w30?.buys||0)+(analysis.tapeWindows.w30?.sells||0),
          w60Total:(analysis.tapeWindows.w60?.buys||0)+(analysis.tapeWindows.w60?.sells||0),
        }:null,
        atrBpsAtLock:analysis?.atrBps??null,
        windowAmplitudeAtLock:analysis?.windowAmplitude?.label??null,
        // V10.9.14: feed + cadence + window-evolution instrumentation.
        //   feedAtLock — active price feed (coinbase/okx/kraken): answers "CB vs OKX".
        //   lockLatencySec — seconds from window open to lock: measures cadence.
        //   windowTypeTransitions — live window-type path (e.g. GRIND>WHIPSAW).
        feedAtLock:priceSourceRef.current||null,
        lockLatencySec:windowOpenTimeRef.current?Math.round((Date.now()-windowOpenTimeRef.current)/1000):null,
        windowTypeAtLock:analysis?.windowAmplitude?.label??null,
        windowTypeTransitions:Array.isArray(_windowTypeSeqRef.current)&&_windowTypeSeqRef.current.length?_windowTypeSeqRef.current.map(s=>s.label).join('>'):null,
        windowTypeChanged:Array.isArray(_windowTypeSeqRef.current)&&_windowTypeSeqRef.current.length>1,
        // V10.8.2: Cushion history at lock — was price consistently on the right side?
        _cushionPctCorrect:_cushionIntel?.pctCorrect??null,
        _cushionTrend:_cushionIntel?.trend!=null?Math.round(_cushionIntel.trend*10)/10:null,
        _cushionNowBps:_cushionIntel?.cushionNow!=null?Math.round(_cushionIntel.cushionNow*10)/10:null,
        _cushionSnapshots:_cushionIntel?.snapshots??null,
        _cushionMult:_cushionMult!==1.0?_cushionMult:null,
        // V10.7.86: window OHLC at lock time — open/high/low prices for intra-window learning.
        //   Enables entry timing quality analysis and adverse excursion context.
        //   Stored in bps from strike for normalization across different price levels.
        windowOpenPrice:windowOpenPriceRef.current>0?windowOpenPriceRef.current:null,
        windowHighAtLock:windowHighRef.current>0?windowHighRef.current:null,
        windowLowAtLock:windowLowRef.current>0?windowLowRef.current:null,
        windowOpenBpsFromStrike:windowOpenPriceRef.current>0&&targetMargin>0
          ?Math.round(((windowOpenPriceRef.current-targetMargin)/targetMargin)*10000*10)/10:null,
        windowHighBpsFromStrike:windowHighRef.current>0&&targetMargin>0
          ?Math.round(((windowHighRef.current-targetMargin)/targetMargin)*10000*10)/10:null,
        windowLowBpsFromStrike:windowLowRef.current>0&&targetMargin>0
          ?Math.round(((windowLowRef.current-targetMargin)/targetMargin)*10000*10)/10:null,
        // V6.4: conviction asymmetry at lock — was the latest swing stronger or weaker than prior?
        asymmetryAtLock:analysis?.convictionAsymmetry?{
          score:analysis.convictionAsymmetry.score,
          latestDir:analysis.convictionAsymmetry.latestDir,
          priorDir:analysis.convictionAsymmetry.priorDir,
        }:null,
        // V6.2.6: track if user force-buttons influenced this commit
        wasSoftHinted:_lcHintActive||false,
        isUserForced:_hardForceActive||false,
        samples,
        needSamples,
        tier:_hardForceActive?'user-forced':tierLabel,
        session:_session,
        regime:analysis?.regime||'',
        qScore:Math.round(qualityGate?.score||0),qScoreV2:Math.round(qualityGateV2?.score||0),qScoreV2Components:qualityGateV2?.components||null,
        fgt:analysis?.mtfAlignment,
      };
      // Seal once before sounds, persistence or the independent main-path log.
      // Re-persistence cannot move a pre-cutoff decision across the deadline.
      if(_sealCallSnapshot(taraCallSnapshotRef.current)===false)return;
      if(taraCallSnapshotRef.current.call==='SIT_OUT'){
        _logSnapshotEntry(taraCallSnapshotRef.current);
        _persistLock();
        return;
      }
      // V6.0: Fire commit sound. Super-confluence gets the special arpeggio.
      // V6.0.1: Rising-confluence gets a lighter early-entry chirp.
      // V6.0.7: Tape-led also uses the early-entry chirp — same character (fast lock).
      if(tc?._ctx?.isSuperConfluent){
        playAlert(_committedCall==='UP'?'confluence-lock-up':'confluence-lock-down');
      } else if(tc?._ctx?.isRisingConfluence||tc?._ctx?.isTapeLed){
        playAlert(_committedCall==='UP'?'early-entry-up':'early-entry-down');
      } else {
        playAlert(_committedCall==='UP'?'lock-up':'lock-down');
      }
      _persistLock(); // V5.6: cloud-save LOCKED snapshot
      // V9.9.0: PREDICTIVE REVERSAL — compute reversal risk at the moment of commit.
      //   One-shot snapshot of the lock context: tier, regime, FGT, qScore, recent
      //   contrary whale, late-lock flag, day-of-week, holiday. Returns a structured
      //   risk object that gets surfaced as chip + coach + Discord field, and stamped
      //   on the call log entry so we can post-hoc audit "did EXPECTED locks reverse
      //   more often?" once V9.9.2 ships peak-adverse drawdown capture.
      const _reversalRisk=computeReversalRisk({
        tier:tierLabel,
        regime:analysis?.regime||'',
        dir:_committedCall,
        qScore:Math.round(qualityGate?.score||0),qScoreV2:Math.round(qualityGateV2?.score||0),qScoreV2Components:qualityGateV2?.components||null,
        mtfAlignment:analysis?.mtfAlignment,
        secondsIntoWindow:windowOpenTimeRef.current?Math.round((Date.now()-windowOpenTimeRef.current)/1000):null,
        whaleLog,
        dayContext,
      });
      // Mutate lockedCallRef so UI (TaraCallCard, LiveTradeCoach) can read this without
      //   waiting for setTaraCallLog to flush. Refs are observed via forceRender ticks.
      if(lockedCallRef.current){
        lockedCallRef.current.reversalRisk=_reversalRisk;
        // V9.9.6: force Firestore write on every lockedCallRef mutation so the cloud
        //   doc stays current with what the UI is showing. Previously the reversalRisk
        //   stamp could be lost if the tab went inactive before the next regular persist
        //   tick, leading to "refresh fixes it" symptoms reported by user.
        _persistLock();
      }
      // V5.6.1: log entry — committed UP/DOWN
      // V5.6.9: windowId added so cross-device entries dedupe to one log row per window.
      const _wid=computeWindowId(windowType);
      const _entry={
        id:Date.now(),time:Date.now(),windowType,
        windowId:_wid,
        // V9.9.0: stamp reversalRisk on the entry for CSV audit + post-hoc analysis
        reversalRisk:_reversalRisk,
        originalDecision:taraCallSnapshotRef.current.originalDecision,
        callPolicy:taraCallSnapshotRef.current.callPolicy||null,
        decisionTrace:taraCallSnapshotRef.current.decisionTrace||null,
        forecastAvailability:taraCallSnapshotRef.current.forecastAvailability||null,
        // V6.4: explicit lock timestamp + window-open timestamp so we can analyze how long
        //   Tara takes to lock under different conditions. secondsIntoWindow = lockedAt - windowOpenedAt.
        lockedAt:Date.now(),
        windowOpenedAt:windowOpenTimeRef.current||null,
        secondsIntoWindow:windowOpenTimeRef.current?Math.round((Date.now()-windowOpenTimeRef.current)/1000):null,
        // V7.10.6: market phase stamp for context-aware Memory display
        phase:(typeof getPhaseKey==='function'?getPhaseKey(new Date().getUTCHours(),new Date().getUTCMinutes()):null),
        regime:analysis?.regime||'',
        dir:_committedCall,confidence:tc.confidence,
        posterior:analysis?.rawProbAbove,
        qScore:Math.round(qualityGate?.score||0),qScoreV2:Math.round(qualityGateV2?.score||0),qScoreV2Components:qualityGateV2?.components||null,
        fgt:analysis?.mtfAlignment,
        tier:tierLabel,
        session:_session,
        reason:_committedReason,
        earlyLock:!analysis?.isSystemLocked,
        // V5.7.7: confluence flags on the call log row for future learning bucketing
        isConfluent:tc?._ctx?.isConfluent||false,
        isSuperConfluent:tc?._ctx?.isSuperConfluent||false,
        // V6.0.1: rising-confluence is a separate learning bucket
        isRisingConfluence:tc?._ctx?.isRisingConfluence||false,
        // V6.0.7: tape-led is a separate learning bucket
        isTapeLed:tc?._ctx?.isTapeLed||false,
        // V6.2.0: structural-led is a separate learning bucket
        isStructuralLed:tc?._ctx?.isStructuralLed||false,
        // V10.2.18 — Phase 4 + sessionTier stamps on MAIN directional commit.
        //   Previously only _logSnapshotEntry (the late/edge-case helper) had these.
        //   The main UP/DOWN commit path here was missing them, so most resolved
        //   trades had null tradeTiming + null sessionTier fields in the CSV.
        //   Without these, shadow validation of Phase 4 / sessionTier is impossible.
        tradeTimingDecision:_phase4DecisionRef.current?_phase4DecisionRef.current.decision:null,
        tradeTimingScore:_phase4DecisionRef.current?_phase4DecisionRef.current.score:null,
        tradeTimingReason:_phase4DecisionRef.current?_phase4DecisionRef.current.reason:null,
        tradeTimingMode:_phase4DecisionRef.current?_phase4DecisionRef.current.mode:null,
        // V10.2.23 — per-factor impact breakdown for offline calibration
        tradeTimingFactors:_phase4DecisionRef.current?_phase4DecisionRef.current.factors||null:null,
        tradeTimingFactorCount:_phase4DecisionRef.current?_phase4DecisionRef.current.factorCount||0:0,
        sessionTierMode:tc?._ctx?._sessionTierMode||null,
        sessionTierMult:tc?._ctx?._sessionTierMult!=null?tc._ctx._sessionTierMult:null,
        sessionTierApplied:tc?._ctx?._sessionTierApplied===true,
        // V6.3.3: log structural alignment + raw vs calibrated posterior for diagnostics
        structAtLock:analysis?.structAlignment?{
          dir:analysis.structAlignment.dir,
          count:analysis.structAlignment.count,
        }:null,
        rawPosteriorAtLock:analysis?.rawPosteriorUncalibrated??analysis?.rawProbAbove??null, /* V9.11.3: distinguish raw vs calibrated */
        calibratedPosteriorAtLock:analysis?.rawProbAbove??null, /* V9.11.2 fix: was reading analysis.posterior but useMemo exposes it as rawProbAbove */
        /* V13.4.284: used the phantom analysis.posterior as the CALIBRATED value and
           rawProbAbove as the RAW one -- both wrong. The two lines directly above
           define the real pair: rawProbAbove IS the calibrated value, and the raw one
           is rawPosteriorUncalibrated (written at ~L43863). analysis.posterior is never
           written at all, so this logged null on every single lock and the calibration
           swing has no history. Correct orientation is calibrated - raw; still null when
           the uncalibrated value is genuinely absent, because a fabricated 0 would read
           as "calibration did nothing" rather than "unknown". */
        calSwingAtLock:(()=>{
          const _cal=Number(analysis?.rawProbAbove);
          const _raw=Number(analysis?.rawPosteriorUncalibrated);
          return (Number.isFinite(_cal)&&Number.isFinite(_raw))?Math.round(_cal-_raw):null;
        })(),
        // V6.3.5: capture full signal scores + tape consensus at lock for analysis exports
        signalScoresAtLock:(typeof taraCall!=='undefined'&&taraCall&&taraCall.rawSignalScores)?{...taraCall.rawSignalScores}:(analysis?.rawSignalScores?{...analysis.rawSignalScores}:null),/*V13.4.66: prefer LIVE taraCall.rawSignalScores; analysis was stale so signals logged on only 3% of locks, blocking tape-weight backtest*/
        // V10.7.53: persist strike, version, and engine reasoning for full audit trail
        strikeAtLock:Number(targetMargin)||0,
        // V13.4.85: FAIR-VALUE INPUTS. The archive recorded strike and closingPrice but NEVER
        //   where spot sat relative to strike AT LOCK, so no barrier-probability model could be
        //   fitted or validated on history (gapAtEntry was stamped but never whitelisted, so it
        //   persisted 0 times across 4320 settled locks). These are what a distance/vol/time
        //   model needs: with them we can measure BTC's real scaling exponent H instead of
        //   assuming sqrt-t, then price each window and compare against Kalshi. ASCII-only.
        spotAtLock:Number(currentPrice)||0,
        distBpsAtLock:(Number(targetMargin)>0&&Number(currentPrice)>0)?Math.round(((Number(currentPrice)-Number(targetMargin))/Number(targetMargin))*1000000)/100:null,
        volBpsAtLock:Number.isFinite(analysis?.atrBps)?Math.round(analysis.atrBps*10)/10:null,
        baselineVersion:typeof BASELINE_VERSION!=='undefined'?BASELINE_VERSION:null,
        reasoning:Array.isArray(analysis?.reasoning)?analysis.reasoning.slice(0,40):null,
        // V10.7.54: bias stamps now on ALL entry paths (was 59% coverage in V10.7.50 — only on _logSnapshotEntry path)
        ...(()=>{
          const _resolved=taraCallLogRef.current.filter(e=>e&&(e.result==='WIN'||e.result==='LOSS'));
          const _last30=_resolved.slice(-30);
          if(_last30.length<10)return{};
          const _up=_last30.filter(e=>e.dir==='UP');
          const _dn=_last30.filter(e=>e.dir==='DOWN');
          const _upWR=_up.length?_up.filter(e=>e.result==='WIN').length/_up.length:null;
          const _dnWR=_dn.length?_dn.filter(e=>e.result==='WIN').length/_dn.length:null;
          return{
            _v10_7_50_recentDirN:_last30.length,
            _v10_7_50_recentUpPct:Math.round((_up.length/_last30.length)*100),
            _v10_7_50_recentDnPct:Math.round((_dn.length/_last30.length)*100),
            _v10_7_50_recentUpWR:_upWR!=null?Math.round(_upWR*1000)/10:null,
            _v10_7_50_recentDnWR:_dnWR!=null?Math.round(_dnWR*1000)/10:null,
          };
        })(),
        tapeAtLock:analysis?.tapeWindows?{
          w15Buy:analysis.tapeWindows.w15?.buyPct??null,
          w30Buy:analysis.tapeWindows.w30?.buyPct??null,
          w60Buy:analysis.tapeWindows.w60?.buyPct??null,
          w15Total:(analysis.tapeWindows.w15?.buys||0)+(analysis.tapeWindows.w15?.sells||0),
          w30Total:(analysis.tapeWindows.w30?.buys||0)+(analysis.tapeWindows.w30?.sells||0),
          w60Total:(analysis.tapeWindows.w60?.buys||0)+(analysis.tapeWindows.w60?.sells||0),
        }:null,
        atrBpsAtLock:analysis?.atrBps??null,
        windowAmplitudeAtLock:analysis?.windowAmplitude?.label??null,
        // V10.9.14: feed + cadence + window-evolution instrumentation.
        //   feedAtLock — active price feed (coinbase/okx/kraken): answers "CB vs OKX".
        //   lockLatencySec — seconds from window open to lock: measures cadence.
        //   windowTypeTransitions — live window-type path (e.g. GRIND>WHIPSAW).
        feedAtLock:priceSourceRef.current||null,
        lockLatencySec:windowOpenTimeRef.current?Math.round((Date.now()-windowOpenTimeRef.current)/1000):null,
        windowTypeAtLock:analysis?.windowAmplitude?.label??null,
        windowTypeTransitions:Array.isArray(_windowTypeSeqRef.current)&&_windowTypeSeqRef.current.length?_windowTypeSeqRef.current.map(s=>s.label).join('>'):null,
        windowTypeChanged:Array.isArray(_windowTypeSeqRef.current)&&_windowTypeSeqRef.current.length>1,
        // ── V9.7.9: V9.7.x GATE TELEMETRY ─────────────────────────────────
        // Stamp diagnostic state from each gate so post-hoc CSV audit can answer:
        //   "did the FGT cap save trades?" / "did the reversal damper prevent
        //   bad locks?" / "did urgency fire on this entry?". Without these, every
        //   debugging conversation requires me to guess from raw posteriors.
        // ── REVERSAL DAMPER (V9.7.8)
        reversalDamperApplied:analysis?.diagnosticsV97?.reversalDamper?.applied||false,
        reversalDamperMult:analysis?.diagnosticsV97?.reversalDamper?.mult??1.0,
        recentCandleDirsAtLock:analysis?.diagnosticsV97?.reversalDamper?.recentCandleDirs||null,
        // ── FGT-COUNTER GATE (V9.7.8)
        fgtCounterApplied:analysis?.diagnosticsV97?.fgtCounter?.applied||false,
        convBeforeFgtCap:analysis?.diagnosticsV97?.fgtCounter?.before??null,
        // ── REGIME CALIBRATION (V9.5.0 / V9.7.6)
        regimeCalApplied:analysis?.diagnosticsV97?.regimeCal?.applied||false,
        regimeCalKey:analysis?.diagnosticsV97?.regimeCal?.key||null,
        regimeCalWR:analysis?.diagnosticsV97?.regimeCal?.wr??null,
        regimeCalWeight:analysis?.diagnosticsV97?.regimeCal?.weight??null,
        regimeCalShift:analysis?.diagnosticsV97?.regimeCal?.shift??null,
        regimeCalN:analysis?.diagnosticsV97?.regimeCal?.n??null,
        // ── ADAPTIVE LOCK URGENCY (V9.7.6)
        urgencyApplied:_v97DiagRef?.current?.urgencyApplied||false,
        urgencyMult:_v97DiagRef?.current?.urgencyMult??1.0,
        kalshiVelocityAtLock:_v97DiagRef?.current?.kalshiVelocity30s??null,
        // ── URGENT LOCK PROTOCOL (V9.8.0) — fast-track when Kalshi confirms direction
        ulpApplied:tc?._ctx?.ulpApplied||false,
        samplesNeededOriginal:tc?._ctx?.samplesNeededOriginal??null,
        // ──────────────────────────────────────────────────────────────────
        // V6.4: conviction asymmetry at lock
        asymmetryAtLock:analysis?.convictionAsymmetry?{
          score:analysis.convictionAsymmetry.score,
          latestDir:analysis.convictionAsymmetry.latestDir,
          priorDir:analysis.convictionAsymmetry.priorDir,
        }:null,
        // V6.2.6: user force/hint metadata
        wasSoftHinted:_lcHintActive||false,
        isUserForced:_hardForceActive||false,
        // V6.0.8: Kalshi YES price at lock — for edge analysis. Edge = posterior - kalshiAtLock
        //   (UP) or (100-posterior) - (100-kalshiAtLock) (DOWN). Positive edge = Tara more
        //   confident than market = entry profitable; negative edge = Tara late, market
        //   already priced in.
        kalshiAtLock:typeof kalshiYesPrice!=='undefined'&&kalshiYesPrice!=null?Number(kalshiYesPrice):null,
        // V9.7.6: speed dial setting at lock — telemetry to audit whether dial=20 (FAST)
        //   actually produces faster locks than dial=50 (BALANCED). If we see dial=20 trades
        //   averaging the same secondsIntoWindow as dial=50, the dial isn't being applied.
        dialAtLock:50, // V10.9.7: dial removed — neutral value kept for CSV/export schema stability
        samples,needSamples,
        // V6.5.8: tag with active asset so memory can filter per-asset.
        //   V7.0.3: use ref.current to avoid stale closure.
        //   V7.0.7: BELT AND SUSPENDERS — also validate against strike magnitude.
        //   If currentAssetRef.current somehow has a stale value (despite synchronous
        //   ref update on every render) AND we have a strike, infer the right asset
        //   from strike magnitude. BTC>=10K, ETH 100-10K, SOL 1-100, DOGE <1.
        //   Logs when override triggers so this bug becomes visible if it persists.
        //   V7.1: BTC + ETH only. If strike doesn't match (sub-$100), trust ref. Better
        //   to keep the entry tagged with whatever the ref says than to drop it here.
        asset:(()=>{
          const _refAsset=currentAssetRef.current||currentAsset||'BTC';
          const _strike=Number(targetMargin||targetMarginRef.current||0);
          if(_strike>=100){
            const _inferred='BTC';
            if(_inferred!==_refAsset){
              try{console.warn('[V7.1] Asset tag override at entry creation: ref says',_refAsset,'but strike',_strike,'inferred',_inferred);}catch(_){}
              return _inferred;
            }
          }
          // Coerce SOL/DOGE refs to BTC (shouldn't happen in V7.1 but defensive)
          if(_refAsset!=='BTC')return'BTC';
          return _refAsset;
        })(),
        // V9.10.9: stamp device label so personal scorecard filters to this device only.
        // V9.10.10: stamp pattern detection state at lock time. Lets you study "when
        //   trendline-break fired and Tara still committed against it, did she win?"
        //   directly from the call log without re-running the engine.
        patternsAtLock:analysis?.patternsV9_10_10||null,
        // V9.11.0: also stamp HTF-aware pattern data + futures data on entries.
        //   patternsAtLock above remains for backward compat; new analysis should
        //   read patternsAtLockV9_11_0 (per-TF + confluence) and futuresAtLock.
        patternsAtLockV9_11_0:analysis?.patternsV9_11_0||null,
        // V13.4.243: 3-scalar mirror of the line above. The fat object is
        //   stripped by the deep-cache minify, so the pattern engine's own
        //   telemetry survives on well under half the log. These three are on
        //   the _MINI_KEEP list and cost ~15 bytes.
        patDirScore:(()=>{const _v=Number(analysis?.patternsV9_11_0?.directionalScore);return Number.isFinite(_v)?Math.round(_v):null;})(),
        patConfluence:(()=>{const _v=Number(analysis?.patternsV9_11_0?.confluence);return Number.isFinite(_v)?_v:null;})(),
        patDominantDir:analysis?.patternsV9_11_0?.dominantDirection||null,
          // V9.15: stamp K-nearest historical analogs at lock time. Stored compactly
          //   (top 3 entries + summary) to keep log size manageable. The full 12-entry
          //   list is exposed via the live `analysis.historicalAnalogs` for UI only.
          historicalAnalogsAtLock:(()=>{
            const _h=analysis?.historicalAnalogs;
            if(!_h||!_h.valid)return null;
            return{
              analogWR:_h.analogWR,
              analogN:_h.analogN,
              avgSimilarity:_h.avgSimilarity,
              confidence:_h.analogConfidence,
              top3:(_h.topK||[]).slice(0,3).map(t=>({
                id:t.id,similarity:t.similarity,result:t.result,
                regime:t.regime,session:t.session,dir:t.dir,
              })),
            };
          })(),
        futuresAtLock:analysis?.futuresV9_11_0||null,
          // V10.2.36-40 — Regime detection improvement stamps persisted to call log.
          _v10_2_36_rangeOverrideFired:analysis?._v10_2_36_rangeOverrideFired===true,
          _v10_2_36_rangeOverrideAvgBps:analysis?._v10_2_36_rangeOverrideAvgBps??null,
          _v10_2_37_quietOverrideFired:analysis?._v10_2_37_quietOverrideFired===true,
          _v10_2_37_quietOverrideRatio:analysis?._v10_2_37_quietOverrideRatio??null,
          _v10_2_37_quietRealizedBps:analysis?._v10_2_37_quietRealizedBps??null,
          _v10_2_37_quietExpectedBps:analysis?._v10_2_37_quietExpectedBps??null,
          _v10_2_37_mtfUpAligned:analysis?._v10_2_37_mtfUpAligned===true,
          _v10_2_37_mtfDnAligned:analysis?._v10_2_37_mtfDnAligned===true,
          _v10_2_38_srRangeDetected:analysis?._v10_2_38_srRangeDetected===true,
          _v10_2_38_srRangeBps:analysis?._v10_2_38_srRangeBps??null,
          _v10_2_38_srTouchesHigh:analysis?._v10_2_38_srTouchesHigh??0,
          _v10_2_38_srTouchesLow:analysis?._v10_2_38_srTouchesLow??0,
          _v10_2_39_htfFlatOverride:analysis?._v10_2_39_htfFlatOverride===true,
          _v10_2_39_htfVetoFired:analysis?._v10_2_39_htfVetoFired===true,
          _v10_2_39_htf1hDriftBps:analysis?._v10_2_39_htf1hDriftBps??null,
          _v10_2_39_htf4hDriftBps:analysis?._v10_2_39_htf4hDriftBps??null,
          _v10_2_39_htf1hLabel:analysis?._v10_2_39_htf1hLabel||null,
          _v10_2_39_htf4hLabel:analysis?._v10_2_39_htf4hLabel||null,
          _v10_2_40_regimeProbs:analysis?._v10_2_40_regimeProbs||null,
          _v10_2_40_regimeConfidence:analysis?._v10_2_40_regimeConfidence??null,
          _v10_2_40_primaryRegime:analysis?._v10_2_40_primaryRegime||null,
          _v10_2_40_dampened:analysis?._v10_2_40_dampened===true,
          _v10_2_43_regimeMult:analysis?._v10_2_43_regimeMult??1.0,
          _v10_2_43_structOrig:analysis?._v10_2_43_structOrig??0,
          _v10_2_43_techOrig:analysis?._v10_2_43_techOrig??0,
          _v10_2_44_haircutApplied:analysis?._v10_2_44_haircutApplied||null,
          _v10_2_44_postBeforeHaircut:analysis?._v10_2_44_postBeforeHaircut??null,
          _v10_2_50_chopAmpDelta:analysis?._v10_2_50_chopAmpDelta??0,
          _v10_2_50_htfChopBoost:analysis?._v10_2_50_htfChopBoost||null,
          _v10_2_51_chopConfluence:analysis?._v10_2_51_chopConfluence||null,
          _v10_3_0_rangeData:analysis?._v10_3_0_rangeData||null,
          _v10_3_0_rangeFadeFired:analysis?._v10_3_0_rangeFadeFired||null,
          _v10_3_0_midRangeSitout:analysis?._v10_3_0_midRangeSitout===true,
          _v10_3_0_rangeBroken:analysis?._v10_3_0_rangeBroken===true,
          _v10_3_1_coinFlip:analysis?._v10_3_1_coinFlip||null,
          _v10_3_1_trendingUpPenalty:analysis?._v10_3_1_trendingUpPenalty||null,
          _v10_3_3_rangeHybrid:analysis?._v10_3_3_rangeHybrid||null,
          _v10_4_0_calibration:analysis?._v10_4_0_calibration||null,
          _v10_4_0_regimeCaps:analysis?._v10_4_0_regimeCaps||null,
          _v10_4_3_windowCtx:analysis?._v10_4_3_windowCtx||null,
          _v10_5_1_brti:brtiApprox?{current:brtiApprox.current,divBps:brtiApprox.divergenceBps,n:brtiApprox.samples60s,src:brtiApprox.sourceCount}:null, _diag_brtiRefLive:(brtiApproxRef&&brtiApproxRef.current)?{current:brtiApproxRef.current.current,divBps:brtiApproxRef.current.divergenceBps,n:brtiApproxRef.current.samples60s,src:brtiApproxRef.current.sourceCount}:null,
          _v10_6_2_dowHour:analysis?._v10_6_2_dowHour||null,
          _v10_7_4_flipped:taraCall?._v10_7_4_flipped?{originalDir:taraCall._v10_7_4_originalDir,votes:taraCall._v10_7_4_votes,reasons:taraCall._v10_7_4_voteReasons}:null,
          _v10_7_11_universalFlipped:taraCall?._v10_7_11_universalFlipped?{flipType:taraCall._v10_7_11_flipType,originalDir:taraCall._v10_7_11_originalDir,votes:taraCall._v10_7_11_votes,reasons:taraCall._v10_7_11_reasons,gapBps:taraCall._v10_7_11_gapBps,driftBps:taraCall._v10_7_11_driftBps,minsLeft:taraCall._v10_7_11_minsLeft}:null,
          _v10_7_19_balanced:taraCall?._v10_7_19_balanced?{downPct:taraCall._v10_7_19_downPct,originalDir:taraCall._v10_7_19_originalDir,originalConf:taraCall._v10_7_19_originalConf}:null,
          _v10_4_1_delayed:_v104_1_pendingDelayStampRef.current||null,
        result:null, // populated at rollover scoring
      };
      // V5.6.9 / V6.3.4: At-append dedup. Match by windowId AND windowType. If a duplicate
      //   exists (from a re-render, race condition, or another device replicating to cloud),
      //   skip. Dedup key is asset+windowId+windowType.
      // V9.10.4: ADDED asset to dedup key. Previously was windowId+windowType only — meant
      //   BTC + ETH entries for the same time-aligned 15m window silently dropped the
      //   second one. User reported "memory iffy, last 3 windows didn't log" — root cause.
      //   Cross-tab merge and load-time sanitize were already using the correct 3-field
      //   key (per V8.8.6), but local writes from THIS tab were using the broken 2-field
      //   key. So a BTC lock at 00:30 + ETH lock at 00:30 → only one entry saved.
      setTaraCallLog(prev=>{
        const _commitAsset=_entry.asset||'BTC';
        const _existing=prev.find(e=>e&&e.windowId===_wid&&e.windowType===windowType&&(e.asset||'BTC')===_commitAsset);
        if(_existing){
          // V9.10.7: audit every silent dedup drop in the directional commit path.
          _auditLogEvent('directionalCommit-dedup-blocked',{
            source:'directionalCommit',
            wid:_wid,
            asset:_commitAsset,
            windowType,
            newEntry:{dir:_entry.dir,tier:_entry.tier,confidence:_entry.confidence,time:_entry.time},
            existingEntry:{id:_existing.id,dir:_existing.dir,tier:_existing.tier,result:_existing.result,time:_existing.time},
          });
          return prev;
        }
        return [...prev,_entry].slice(-TARA_CALL_LOG_CAP);
      });
    } else {
      // V5.6: Mid-formation persist — refresh during ANALYZING/FORMING restores sample
      //   count so Tara picks up where she left off instead of resetting to 0/N. Debounced
      //   so this doesn't hammer Firestore on every tick.
      _noteGateHold('sample-stability',`Forming ${tc.call}: ${samples.toFixed(1)}/${needSamples} fresh stability samples.`,
        {samples,requiredSamples:needSamples,tier:tierLabel,confidence:tc.confidence});
      _persistLock();
    }
  },[analysis?.isSystemLocked,taraCall.call,taraCall.direction,taraCall.confidence,timeState.minsRemaining,timeState.secsRemaining,analysis?.rawProbAbove,taraCall.reason,qualityGate?.score,analysis?.mtfAlignment,analysis?.regime,analysis?.windowAmplitude?.label]);

  // ── LOCK BROADCAST EFFECT — Two-stage: SIGNAL fires when FORMING, LOCK fires on commit ──
  const lastBroadcastLockRef=useRef(null);
  useEffect(()=>{
    if(!analysis?.lockInfo)return;
    const lock=analysis.lockInfo;
    if(lastBroadcastLockRef.current===lock.lockedAt)return;
    lastBroadcastLockRef.current=lock.lockedAt;
    // Record this lock for multi-timeframe confluence tracking
    mtfLocksRef.current[windowType]={dir:lock.dir,lockedAt:lock.lockedAt,posterior:lock.lockedPosterior};
    // ── STAGE 2: LOCK confirmed — broadcast the actionable call ──
    const wins=scorecards[windowType]?.wins||0,losses=scorecards[windowType]?.losses||0;
    const record=`${wins}W-${losses}L-${windowType}`;
    // V113: SUPPRESSED auto-broadcast for LOCK — user must click "Send to Discord"
    // Sound alert still plays so user knows the lock fired
    playAlert(lock.dir==='UP'?'lock-up':'lock-down');
  },[analysis?.lockInfo?.lockedAt]);

  // V134: Audio alerts for trajectory milestones, breaking news, MTF confluence
  const prevTrajRef=useRef(0);
  const prevBreakingRef=useRef(false);
  const prevMtfRef=useRef(false);
  useEffect(()=>{
    if(!soundEnabled)return;
    // Trajectory crossed +12 or -12 threshold
    const traj=analysis?.trajectoryAdj||0;
    const prev=prevTrajRef.current;
    if(Math.abs(traj)>=12&&Math.abs(prev)<12){
      playAlert('entry'); // strong directional move detected
    }
    prevTrajRef.current=traj;
  },[analysis?.trajectoryAdj]);
  useEffect(()=>{
    if(!soundEnabled)return;
    if(newsSentiment?.hasBreaking&&!prevBreakingRef.current){
      playAlert('warning'); // breaking news detected
    }
    prevBreakingRef.current=newsSentiment?.hasBreaking||false;
  },[newsSentiment?.hasBreaking]);
  useEffect(()=>{
    if(!soundEnabled)return;
    // MTF confluence: 5m and 15m both locked same direction recently
    const otherTF=windowType==='5m'?'15m':'5m';
    const otherLock=mtfLocksRef.current[otherTF];
    const myLock=analysis?.lockInfo;
    const fresh=otherLock&&(Date.now()-otherLock.lockedAt)<20*60*1000;
    const aligned=fresh&&myLock&&otherLock.dir===myLock.dir;
    if(aligned&&!prevMtfRef.current){
      playAlert('profit'); // MTF agreement - high conviction
    }
    prevMtfRef.current=aligned||false;
  },[analysis?.lockInfo?.lockedAt,windowType]);

  // ── STAGE 1: SIGNAL broadcast when FORMING is first detected ──
  const lastFormingBroadcastRef=useRef(null);
  useEffect(()=>{
    if(!analysis?.prediction)return;
    const isForming=analysis.prediction.includes('FORMING');
    if(!isForming)return;
    const dir=analysis.prediction.includes('UP')?'UP':'DOWN';
    // One SIGNAL broadcast per direction per window — use window start time as key
    const formingKey=`${timeState.startWindow||timeState.nextWindow}`; // 1 broadcast per window total
    if(lastFormingBroadcastRef.current===formingKey)return;
    // V113: SUPPRESSED auto-broadcast for SIGNAL/FORMING — user must click "Send to Discord"
    // Just mark as broadcasted to prevent repeats; actual broadcast is manual
    lastFormingBroadcastRef.current=formingKey;
  },[analysis?.prediction]);

  // Desktop notifications remain local. Discord reads only saved original Calls.
  const taraBrowserNoticeRef=useRef(null);
  useEffect(()=>{
    const snap=taraCallSnapshotRef.current;
    if(!snap?.originalDecision||snap.originalDecision.commitPolicy?.action!=='allow'||
      !isDirection(snap.call)||snap.windowId!==computeWindowId(windowType)||
      Date.now()-snap._committedAt<2000)return;
    const key=callKey(snap);if(taraBrowserNoticeRef.current===key)return;
    taraBrowserNoticeRef.current=key;
    try{if(typeof Notification!=='undefined'&&Notification.permission==='granted'){
      const n=new Notification(`Tara ${snap.call} · captured Call`,{
        body:`${snap.confidence}/100 signal score · ${snap.regime||'—'} · paper Call, not a fill`,
        tag:`tara-lock-${key}`,icon:'/favicon.ico'});
      setTimeout(()=>{try{n.close();}catch(_){}},8000);
    }}catch(_){}
  },[timeState.minsRemaining,timeState.secsRemaining,windowType,taraCall.call]);

  // ── WHALE AUTO-BROADCAST ─────────────────────────────────────────────────
  // Only fires when: streak ≥4 AND net delta >$500K AND 5-min cooldown passed
  // Also checks spot/futures alignment for accuracy flag
  const lastWhaleBroadcastRef=useRef({time:0,dir:null});
  // V7.0.7: Records the timestamp of the last asset switch. The broadcast effect (and
  //   broadcastToDiscord itself) suppress messages within 2.5s of a switch — gives
  //   currentPrice/strike/snapshot/whale state time to refresh on the new asset.
  //   Without this, switching ETH→BTC could fire a Discord message with ETH price/strike
  //   in the embed since some refs/state hadn't repopulated yet.
  const lastAssetSwitchAtRef=useRef(0);
  // ── V9.8.18: PUMP/DUMP + VELOCITY-FLIP TOAST DETECTORS ──────────────
  // Runs every currentPrice tick. Maintains a 60s rolling price trail; on each
  //   tick, computes the bps move over the last 30s. ≥20 bps in either direction
  //   fires a price-pump or price-dump toast. 60s per-kind cooldown enforced
  //   inside pushToast. Skips during initial mount / asset switch (no trail yet).
  useEffect(()=>{
    if(!Number.isFinite(currentPrice)||!currentPrice||currentPrice<=0)return;
    const _now=Date.now();
    const arr=_priceTrailRef.current;
    arr.push({t:_now,p:currentPrice});
    // Trim to last 60s
    while(arr.length>0&&_now-arr[0].t>60000)arr.shift();
    if(arr.length<2)return;
    // Find oldest point that's at least 25s old (gives a 25-30s reference window).
    //   If we don't have 25s of data yet, skip — too noisy on partial samples.
    const _25sAgo=_now-25000;
    const _ref=arr.find(e=>e.t<=_25sAgo);
    if(!_ref||_ref.p<=0)return;
    const _bps=(currentPrice-_ref.p)/_ref.p*10000;
    if(Math.abs(_bps)>=20){
      const _isUp=_bps>0;
      pushToast(
        _isUp?'price-pump':'price-dump',
        `${currentAsset||'BTC'} ${_isUp?'pumping ▲':'dumping ▼'}`,
        `${_bps>0?'+':''}${_bps.toFixed(0)} bps in 30s · $${currentPrice.toFixed(0)}`,
        {color:_isUp?'#6FA98C':'rgba(232,69,94,0.72)',icon:_isUp?'⚡':'⚡',durationMs:6000,cooldown:60000}
      );
    }
  },[currentPrice,currentAsset,pushToast]);
  // V9.8.18: velocity regime flip — when the engine's velocity classification
  //   jumps from NORMAL/SLOW into FAST or EXTREME, surface as toast. Captures
  //   "tape pace just changed" without requiring a 30s lag like price-bps.
  //   Cooldown 90s so a flickering NORMAL↔FAST regime doesn't spam.
  useEffect(()=>{
    const _cur=analysis?.velocityRegime||'NORMAL';
    const _prev=_prevVelRegimeRef.current;
    _prevVelRegimeRef.current=_cur;
    if(_cur===_prev)return;
    if(_cur==='FAST'&&(_prev==='NORMAL'||_prev==='SLOW')){
      pushToast('vel-fast',`${currentAsset||'BTC'} velocity → FAST`,'Tape pace picking up · windows may shorten',
        {color:'#23B981',icon:'〰',durationMs:5500,cooldown:90000});
    }else if(_cur==='EXTREME'){
      pushToast('vel-extreme',`${currentAsset||'BTC'} velocity → EXTREME`,'News-spike pace · use caution',
        {color:'rgba(232,69,94,0.72)',icon:'⚡⚡',durationMs:7000,cooldown:90000});
    }
  },[analysis?.velocityRegime,currentAsset,pushToast]);

  // ── V134: FLOW INTELLIGENCE — STRICTLY EVENT-TRIGGERED ──────────────
  // Opens ONLY on concerning events. Auto-collapses after 30s unless activity continues.
  // Triggers: whale-print STRONG cross, streak ≥5, $750K delta, velocity regime jump,
  // per-exchange divergence spike, sudden order book wall flip.
  const prevStreakRef=useRef(0);
  const prevScoreRef=useRef(0);
  const prevVelRegimeRef=useRef('NORMAL');
  const prevDeltaSignRef=useRef(0);
  const autoOpenedRef=useRef(false);
  const autoCloseTimerRef=useRef(null);
  const lastAutoOpenTimeRef=useRef(0);
  const lastActivityAtRef=useRef(0); // V134: track when last meaningful activity happened
  useEffect(()=>{
    const fs=flowSignal;
    const now=Date.now();
    // V148.1: Cooldown raised 30s → 60s. Was opening too frequently.
    const sinceLastOpen=now-lastAutoOpenTimeRef.current;
    if(sinceLastOpen<60000)return;
    const prevStreak=prevStreakRef.current;
    const curDelta=fs.netDelta90s||0;
    const curDeltaSign=curDelta>=0?1:-1;
    // ── V148.1: Auto-open ONLY on concerning whale activity ──
    //   Removed triggers (too noisy, fire on non-whale events):
    //     - justHitStrong (flow score 80+) — opens on any strong tape, not specifically whale
    //     - deltaFlip — flutters constantly in choppy markets
    //     - velRegimeJump — velocity has nothing to do with whales
    //     - exchangeDiverge — 5bps Binance/Bybit gap is normal microstructure
    //   Remaining triggers — genuine whale events:
    const massiveWhale=Math.abs(curDelta)>=1000000;        // V148.1: $750K → $1M (was firing too often)
    const sustainedWhaleStreak=fs.streakCount>=7&&prevStreak<7;  // V148.1: 5 → 7 (need sustained pressure)
    // Update prev refs (track all so future logic can still reference)
    prevScoreRef.current=fs.score;
    prevStreakRef.current=fs.streakCount;
    prevDeltaSignRef.current=curDeltaSign;
    prevVelRegimeRef.current=analysis?.velocityRegime||'NORMAL';
    const triggered=massiveWhale||sustainedWhaleStreak;
    if(triggered){
      setShowWhaleLog(true);
      autoOpenedRef.current=true;
      lastAutoOpenTimeRef.current=now;
      lastActivityAtRef.current=now;
      // V134: 30 second view window unless new activity — extends timer if more triggers.
      // V3.1.7 BUGFIX: Previous version ran clearTimeout in the effect's cleanup, which
      //   meant every subsequent flowSignal tick (deps change) cleared the auto-close timer.
      //   Result: panel stayed open indefinitely as long as flow kept moving (which is always
      //   in live markets). Now the timer is only cleared when triggered re-fires fresh.
      // V6.5.7: Activity-extension removed. Live markets always have activity, so panel
      //   never closed. Open on whale trigger → show for 25s → close. New trigger after
      //   60s cooldown re-opens.
      if(autoCloseTimerRef.current)clearTimeout(autoCloseTimerRef.current);
      autoCloseTimerRef.current=setTimeout(()=>{
        if(autoOpenedRef.current){setShowWhaleLog(false);autoOpenedRef.current=false;}
      },25000);
    }
    // V3.1.7: removed the cleanup that cleared autoCloseTimerRef on every dep change.
    //         The timer should persist across flow ticks once started.
  },[flowSignal.score,flowSignal.streakCount,flowSignal.netDelta90s,flowSignal.bnDivBps,analysis?.velocityRegime]);

  // V2.2.2: Manual-open auto-close. When user manually clicks FLOW to open the panel,
  //         start a 90s idle timer. Resets on real whale activity (score or streak rising).
  //         Without this, the panel stayed open indefinitely after any manual click — visible
  //         and obstructive long after the user had moved on.
  // V3.1.7: Hard safety net — close FlowPanel after 3 minutes regardless of how it opened.
  //         Even if other timers misfire or get cleared, this guarantees the panel doesn't
  //         stay obstructively visible indefinitely.
  const hardCloseTimerRef=useRef(null);
  useEffect(()=>{
    if(!showWhaleLog){
      if(hardCloseTimerRef.current){clearTimeout(hardCloseTimerRef.current);hardCloseTimerRef.current=null;}
      return;
    }
    if(hardCloseTimerRef.current)clearTimeout(hardCloseTimerRef.current);
    hardCloseTimerRef.current=setTimeout(()=>{
      setShowWhaleLog(false);
      autoOpenedRef.current=false;
    },60000); // V6.5.7: 60s hard cap (was 180s)
    return()=>{if(hardCloseTimerRef.current)clearTimeout(hardCloseTimerRef.current);};
  },[showWhaleLog,setShowWhaleLog]);

  // V2.3 / V3.1.2: Manual-open auto-close. When user manually clicks FLOW to open the panel,
  //         start a 90s idle timer. Resets on real whale activity (score or streak rising).
  //         V3.1.2 BUGFIX: Previous version re-ran the effect on every flowSignal change,
  //         which meant the timer was being cleared and reset on every scoring tick (constant
  //         activity in live markets). Now the effect only depends on showWhaleLog itself,
  //         and reads flowSignal through a ref inside the timer callback.
  const manualCloseTimerRef=useRef(null);
  const lastFlowSignalActivityRef=useRef({score:0,streak:0});
  const flowSignalRef=useRef(flowSignal);
  useEffect(()=>{flowSignalRef.current=flowSignal;},[flowSignal]);
  useEffect(()=>{
    if(!showWhaleLog){
      // Panel closed — clear the idle timer
      if(manualCloseTimerRef.current){clearTimeout(manualCloseTimerRef.current);manualCloseTimerRef.current=null;}
      return;
    }
    // Only set the manual auto-close when this open was NOT triggered by autoOpen
    if(autoOpenedRef.current)return;
    // Initialize activity baseline from current flowSignal at open
    const fs0=flowSignalRef.current||{};
    lastFlowSignalActivityRef.current={score:fs0.score||0,streak:fs0.streakCount||0};
    if(manualCloseTimerRef.current)clearTimeout(manualCloseTimerRef.current);
    const _scheduleClose=(delayMs)=>{
      manualCloseTimerRef.current=setTimeout(()=>{
        const cur={score:flowSignalRef.current?.score||0,streak:flowSignalRef.current?.streakCount||0};
        const last=lastFlowSignalActivityRef.current;
        const meaningfulActivity=Math.abs(cur.score-last.score)>=15||cur.streak-last.streak>=3;
        if(meaningfulActivity){
          // Real activity — extend by another 60s
          lastFlowSignalActivityRef.current=cur;
          _scheduleClose(60000);
        } else {
          setShowWhaleLog(false);
        }
      },delayMs);
    };
    _scheduleClose(90000);
    return()=>{if(manualCloseTimerRef.current)clearTimeout(manualCloseTimerRef.current);};
  },[showWhaleLog,setShowWhaleLog]);

  // V9.7.1: TAP ANYWHERE TO CLOSE FlowPanel.
  //   User feedback: when Flow Intelligence auto-opens on whale activity, it stays
  //   open through the 25s/60s timers even when the user has visually moved on.
  //   This effect closes it on the first pointer-down anywhere outside the panel.
  //   Inside the panel: no close (otherwise can't read or interact). The panel's
  //   own ✕ button remains.
  useEffect(()=>{
    if(!showWhaleLog)return;
    // Wait one frame before arming, otherwise the very click that *opened* the
    // panel (e.g., the FLOW button) immediately triggers a close.
    let _armed=false;
    const _armTimer=setTimeout(()=>{_armed=true;},250);
    const _onTap=(e)=>{
      if(!_armed)return;
      // Check if the click landed inside the panel. The panel is a fixed-position
      // div with a unique class signature — we walk up the DOM tree looking for it.
      let _node=e.target;
      while(_node&&_node!==document.body){
        if(_node.classList&&_node.classList.contains('fixed')&&_node.classList.contains('top-11')&&_node.classList.contains('right-0')){
          // Inside the panel — don't close.
          return;
        }
        _node=_node.parentElement;
      }
      setShowWhaleLog(false);
      autoOpenedRef.current=false;
    };
    // pointerdown fires before click, on both mouse and touch. Capture phase so
    // we see it before any inner-element handlers can stop propagation.
    document.addEventListener('pointerdown',_onTap,true);
    return()=>{
      clearTimeout(_armTimer);
      document.removeEventListener('pointerdown',_onTap,true);
    };
  },[showWhaleLog,setShowWhaleLog]);

  // Whale analysis remains on the dashboard. It never posts to the BTC Call feed.

  // ── ADVISOR SOUND ALERTS — fires when advisor label changes to something critical ──
  const lastAdvisorLabelRef=useRef('');
  useEffect(()=>{
    const label=analysis?.advisor?.label||'';
    if(!label||label===lastAdvisorLabelRef.current)return;
    lastAdvisorLabelRef.current=label;
    if(label.includes('ENTRY SIGNAL'))                playAlert('entry');
    else if(label.includes('TAKE MAX PROFIT')||label.includes('TRAILING STOP')||label.includes('SCALP PROFIT')||label.includes('SECURE PROFIT')||label.includes('MAX PROFIT ZONE'))
                                                       playAlert('profit');
    else if(label.includes('CUT LOSSES'))              playAlert('warning');
    else if(label.includes('STOP HIT')||label.includes('RUG PULL')||label.includes('EXIT NOW'))
                                                       playAlert('emergency');
  },[analysis?.advisor?.label]);

  // ── V8.6: FORCE RESYNC FROM CLOUD ──────────────────────────────────────
  // Manually re-pulls all sync paths from cloud and applies to local state. Used when
  // user opens a fresh browser/device and wants to make sure everything is synced before
  // trading. Also useful as a "hard reset" when something looks off. Bypasses the
  // hydration-once gates by calling cloudRead directly.
  const[forceResyncing,setForceResyncing]=React.useState(false);
  const forceResyncFromCloud=async()=>{
    setForceResyncing(true);
    try{
      console.info('[V8.6] Force resync starting...');
      // V9.2.0: scorecards/personal removed from force-resync paths — personal
      //   scorecard is LOCAL ONLY now. Tara's shared learning corpus (callLog,
      //   pastWindows, learnings, weights) continues to sync globally.
      const _paths=[
        'memory/taraCallLog',
        'history/pastWindows',
        'state/lifetimePnL',
        'learnings/tara',
        'learnings/regimeMemory',
        'learnings/taraWeights',
        // V8.6.1: Active lock paths — one per asset+window combo. Force Resync now also
        //   pulls these so a fresh browser pressing Force Resync mid-window sees the lock
        //   immediately rather than waiting for cloudWatch to settle.
        'state/currentLock_BTC_15m',
        'state/currentLock_BTC_5m',

      ];
      const _results=await Promise.all(_paths.map(p=>cloudRead(p).then(d=>({path:p,data:d})).catch(e=>({path:p,error:e?.message}))));
      let _applied=0;
      _results.forEach(({path,data,error})=>{
        if(error){console.warn('[V8.6] Resync failed for',path,error);return;}
        if(!data){console.info('[V8.6] No cloud data for',path);return;}
        try{
          if(path==='memory/taraCallLog'&&Array.isArray(data.entries)){
            // V9.1.4: MERGE instead of replace. Was setTaraCallLog(data.entries) which
            //   silently nuked any unsynced local entries (cloud might have fewer if a
            //   write hadn't propagated yet). Now we union local + cloud via the same
            //   merge logic the regular cloudWatch handler uses — first-write-wins,
            //   resolved beats unresolved.
            setTaraCallLog(prev=>{
              const merged=_mergeCallLogEntries(prev,data.entries);
              return merged;
            });_applied++;
          } else if(path==='history/pastWindows'&&Array.isArray(data.entries)){
            // V9.1.4: MERGE instead of replace. windowId+windowType+asset dedup.
            setPastWindows(prev=>{
              const _key=(e)=>(e?.asset||'BTC')+'|'+(e?.windowId||'')+'|'+(e?.windowType||'');
              const byKey=new Map();
              (prev||[]).forEach(e=>{if(e&&e.windowId)byKey.set(_key(e),e);});
              data.entries.forEach(e=>{
                if(!e||!e.windowId)return;
                const k=_key(e);
                const existing=byKey.get(k);
                if(!existing){byKey.set(k,e);return;}
                // Older id wins (first-write-wins)
                if((e.id||0)<(existing.id||0))byKey.set(k,e);
              });
              return Array.from(byKey.values()).sort((a,b)=>(a.id||0)-(b.id||0)).slice(-50);
            });_applied++;
          } else if(path==='state/lifetimePnL'&&typeof data.value==='number'){
            // V9.1.4: keep the higher-magnitude or more-recent value. PnL is a running
            //   sum so just take whichever is newer by updatedAt; fall back to local if
            //   cloud is unstamped (old write).
            setLifetimePnL(prev=>{
              const _cT=Number(data.updatedAt)||0;
              const _lT=Number(_pnlUpdatedAtRef.current)||0;
              if(_cT>_lT)return data.value;
              return prev;
            });
            _pnlUpdatedAtRef.current=Math.max(_pnlUpdatedAtRef.current||0,Number(data.updatedAt)||Date.now());_applied++;
          } else if(path==='learnings/tara'&&typeof data==='object'&&data.totalResolved>=0){
            setTaraLearnings(data);_applied++;
          } else if(path==='learnings/regimeMemory'&&typeof data==='object'){
            setRegimeMemory(data);_applied++;
          } else if(path==='learnings/taraWeights'&&typeof data==='object'){
            if(data.adaptive){
              setAdaptiveWeightsByAsset(prev=>({
                BTC:data.adaptive.BTC&&typeof data.adaptive.BTC.gap==='number'?data.adaptive.BTC:prev.BTC,

              }));
            }
            if(data.regime){
              setRegimeWeightsByAsset(prev=>({
                BTC:data.regime.BTC||prev.BTC,

              }));
            }
            // Update timestamp tracking
            if(data.updatedAtBTC)_weightsByAssetUpdatedAtRef.current.BTC=data.updatedAtBTC;

            _applied++;
          } else if(path.startsWith('state/currentLock_')){
            // V8.6.1: Lock paths — only adopt if it matches the user's currently-viewed
            //   asset+window AND the lock's windowId is the live window. Otherwise the doc
            //   is from a stale window and would just confuse the UI.
            const _expectedPath=`state/currentLock_${currentAsset||'BTC'}_${windowType||'15m'}`;
            if(path!==_expectedPath){
              // This is a different asset/window's lock — count as applied (pulled fresh)
              //   but don't touch local state. The cloudWatch listener for that path handles
              //   it when the user switches to that asset/window.
              _applied++;
            } else {
              const _liveWid=computeWindowId(windowType||'15m');
              if(data.windowId!==_liveWid){
                // Lock is from an older window — skip (auto-cleanup happens at next rollover)
                console.info('[V8.6.1] Lock for',path,'is from stale window',data.windowId,'(live:',_liveWid,') — skipping');
              } else {
                _applySharedDecision(data,'manual');
                _completeSharedRestore();
                _applied++;
                console.info('[shared-decision] Applied canonical decision from',path);
              }
            }
          }
        }catch(e){console.warn('[V8.6] Apply failed for',path,e?.message);}
      });
      console.info('[V8.6] Force resync complete:',_applied,'paths applied');
      // Brief feedback in chat
      try{
        setChatLog(prev=>[...prev,{role:'tara',text:`Force resync complete — applied ${_applied} of ${_paths.length} sync paths from cloud.`}]);
      }catch(_){}
    }catch(e){
      console.warn('[V8.6] Force resync error',e?.message);
      try{
        setChatLog(prev=>[...prev,{role:'tara',text:`Force resync failed: ${e?.message||'unknown error'}.`}]);
      }catch(_){}
    } finally {
      setForceResyncing(false);
    }
  };

  // V9.1.6: BASELINE MECHANISM ─────────────────────────────────────────────
  // User feedback: auto-merge sync still has subtle race conditions across devices.
  // Solution: explicit user-controlled baseline operations.
  //   - saveAsBaseline: dump current local state to baseline/canonical (the user
  //     designates THIS device's state as the canonical truth).
  //   - applyBaseline: read baseline/canonical and OVERWRITE local with it (a fresh
  //     device or a device that's drifted pulls the canonical truth).
  // Stored at separate Firestore path 'baseline/canonical' so it doesn't interfere
  //   with the regular sync paths. Last-saved metadata included for confirmation UI.
  const[baselineMeta,setBaselineMeta]=React.useState(null); // {savedAt, sourceDevice, sizes}
  const[baselineBusy,setBaselineBusy]=React.useState(false);
  const[syncMenuOpen,setSyncMenuOpen]=React.useState(false);
  // V13.4.14: stable callback refs so TodayPnLPill/SyncStatusPill can be React.memo'd
  //   without the memo being defeated by a fresh inline arrow function every render.
  const _onTodayPnLClick=React.useCallback(()=>setShowTradingSettings(true),[]);
  const _onSyncStatusClick=React.useCallback(()=>{if(forceResyncing||baselineBusy)return;setSyncMenuOpen(true);setShowHeaderOverflow(false);},[forceResyncing,baselineBusy]);
  const[scheduleModalMain,setScheduleModalMain]=React.useState(false);
  // V9.2.2: Dedicated analytics page
  const[analyticsPageOpen,setAnalyticsPageOpen]=React.useState(false);
  // Lightweight device label so user can distinguish baselines they saved earlier
  const _deviceLabel=React.useMemo(()=>{
    try{
      const _ua=navigator.userAgent||'';
      const _isMobile=/Mobi|Android|iPhone|iPad|iPod/i.test(_ua);
      const _platform=_isMobile?(/iPhone|iPad|iPod/i.test(_ua)?'iOS':'Android'):
        /Mac/i.test(_ua)?'Mac':/Windows/i.test(_ua)?'Win':/Linux/i.test(_ua)?'Linux':'Web';
      const _browser=/Chrome\/[\d.]+/i.test(_ua)&&!/Edg/i.test(_ua)?'Chrome':
        /Edg\//i.test(_ua)?'Edge':/Firefox\//i.test(_ua)?'Firefox':/Safari\//i.test(_ua)?'Safari':'Browser';
      return`${_platform} ${_browser}`;
    }catch(_){return'unknown';}
  },[]);
  // Read existing baseline metadata on mount so the UI can display "last saved"
  React.useEffect(()=>{
    if(!_sbClient)return; // V10.2.0: was _fbDb
    let cancelled=false;
    // V10.9.30 EGRESS: was cloudRead() which downloads the whole 2.67MB baseline doc
    //   (it embeds a full copy of the call log) just to show a 'last saved' label.
    //   Read only the tiny metadata columns from the JSON instead via a narrow select.
    (async()=>{
      try{
        if(!_sbClient)return;
        const {data:_row}=await _sbClient.from('tara_state')
          .select('data->savedAt, data->sourceDevice, data->sizes')
          .eq('doc_path','baseline/canonical').maybeSingle();
        if(cancelled||!_row)return;
        setBaselineMeta({
          savedAt:Number(_row.savedAt)||0,
          sourceDevice:String(_row.sourceDevice||'unknown'),
          sizes:_row.sizes||null,
        });
      }catch(_){}
    })();
    return()=>{cancelled=true;};
  },[]);
  const saveAsBaseline=async()=>{
    if(baselineBusy)return;
    // Confirm with summary of what's about to be saved as baseline
    const _logCount=Array.isArray(taraCallLogRef.current)?taraCallLogRef.current.length:0;
    const _resolvedCount=(taraCallLogRef.current||[]).filter(e=>e&&(e.result==='WIN'||e.result==='LOSS')).length;
    const _winCount=(taraCallLogRef.current||[]).filter(e=>e&&e.result==='WIN').length;
    const _lossCount=(taraCallLogRef.current||[]).filter(e=>e&&e.result==='LOSS').length;
    const _sitoutCount=(taraCallLogRef.current||[]).filter(e=>e&&e.result==='SITOUT').length;
    const _pastCount=Array.isArray(pastWindows)?pastWindows.length:0;
    const _pnl=Number(_pnlRef.current)||0;
    const _msg=`Save THIS device (${_deviceLabel}) as the canonical baseline?\n\n`+
      `This will overwrite the current baseline in cloud:\n\n`+
      `• Tara call log: ${_logCount} entries (${_winCount}W · ${_lossCount}L · ${_sitoutCount}SO)\n`+
      `• Past windows: ${_pastCount}\n`+
      `• Lifetime P&L: ${_pnl>=0?'+':''}$${_pnl.toFixed(2)}\n`+
      `• Personal scorecard 15m: ${scorecards['15m']?.wins||0}W-${scorecards['15m']?.losses||0}L\n`+
      `• Personal scorecard 5m: ${scorecards['5m']?.wins||0}W-${scorecards['5m']?.losses||0}L\n\n`+
      `Other devices can then "Apply Baseline" to receive this exact state.`;
    if(!window.confirm(_msg))return;
    setBaselineBusy(true);
    try{
      // Snapshot ALL local state. Use refs/current values for accuracy.
      // V9.2.0: scorecards removed from baseline payload — personal data, local only.
      const _stripForCloud=(e)=>{
        if(!e)return e;
        return _stripEntryForCloud(e); // V13.4.187: single shared definition
      };
      // V10.7.58: strip verbose fields before baseline save to avoid Supabase payload limit.
      //   signalScoresAtLock (~25 fields) + reasoning (~40 strings) = ~1.5KB per entry.
      //   Stripping cuts baseline size from ~4MB → ~1.1MB for 1400 entries.
      //   These fields stay in local taraCallLog for analysis — not needed in cloud baseline.
      const _strippedLog=(taraCallLogRef.current||[]).slice(-TARA_CALL_LOG_CAP).map(_stripForCloud);
      const _estSizeKB=Math.round(JSON.stringify(_strippedLog).length/1024);
      try{console.info(`[V10.7.58] Baseline payload: ${_strippedLog.length} entries, ~${_estSizeKB}KB`);}catch(_){}
      const _payload={
        v:2, // bumped — schema change (no scorecards)
        savedAt:Date.now(),
        sourceDevice:_deviceLabel,
        data:{
          taraCallLog:_strippedLog,
          pastWindows:(Array.isArray(pastWindows)?pastWindows:[]).slice(-50),
          lifetimePnL:Number(_pnlRef.current)||0,
          pnlUpdatedAt:Number(_pnlUpdatedAtRef.current)||Date.now(),
          taraLearnings:taraLearnings||null,
          regimeMemory:regimeMemory||null,
          adaptiveWeightsByAsset:adaptiveWeightsByAsset||null,
          regimeWeightsByAsset:regimeWeightsByAsset||null,
        },
        sizes:{
          taraCallLog:_logCount,
          resolved:_resolvedCount,
          wins:_winCount,
          losses:_lossCount,
          sitouts:_sitoutCount,
          pastWindows:_pastCount,
        },
      };
      await cloudWrite('baseline/canonical',_payload);
      // V9.1.8: ALSO push to live cloud paths so other devices' cloudWatch listeners
      //   receive this state within ~1s without needing to manually Apply Baseline.
      // V9.2.0: scorecards/personal write removed — that's a local-only path now.
      try{
        const _liveWrites=[];
        _liveWrites.push(cloudWrite('memory/taraCallLog',{entries:_strippedLog.slice(-_TARA_CLOUD_LOG_CAP),_baselineSave:_payload.savedAt}));
        _liveWrites.push(cloudWrite('history/pastWindows',{entries:_payload.data.pastWindows,_baselineSave:_payload.savedAt}));
        _liveWrites.push(cloudWrite('state/lifetimePnL',{value:_payload.data.lifetimePnL,updatedAt:_payload.data.pnlUpdatedAt,_baselineSave:_payload.savedAt}));
        if(_payload.data.taraLearnings){
          _liveWrites.push(cloudWrite('learnings/tara',{..._payload.data.taraLearnings,_baselineSave:_payload.savedAt}));
        }
        await Promise.all(_liveWrites);
        console.info('[V9.2.0] Pushed baseline to',_liveWrites.length,'live cloud paths (scorecard kept local).');
      }catch(_e){
        console.warn('[V9.1.8] Live-path push partial fail',_e?.message);
      }
      setBaselineMeta({savedAt:_payload.savedAt,sourceDevice:_payload.sourceDevice,sizes:_payload.sizes});
      try{setChatLog(prev=>[...prev,{role:'tara',text:`Saved as baseline from ${_deviceLabel}. Other devices can now Apply Baseline to receive ${_logCount} log entries · ${_winCount}W-${_lossCount}L-${_sitoutCount}SO · ${_pastCount} past windows · $${_pnl.toFixed(2)} lifetime P&L.`}]);}catch(_){}
      console.info('[V9.1.6] Saved baseline:',_payload.sizes,'from',_deviceLabel);
    }catch(e){
      console.warn('[V9.1.6] Save baseline failed',e?.message);
      try{setChatLog(prev=>[...prev,{role:'tara',text:`Save baseline failed: ${e?.message||'unknown error'}.`}]);}catch(_){}
      alert('Save baseline failed: '+(e?.message||'unknown error'));
    } finally {
      setBaselineBusy(false);
    }
  };
  const applyBaseline=async()=>{
    if(baselineBusy)return;
    setBaselineBusy(true);
    try{
      const _baseline=await cloudRead('baseline/canonical');
      if(!_baseline||!_baseline.data){
        alert('No baseline found in cloud. Save a baseline from another device first.');
        setBaselineBusy(false);
        return;
      }
      const _d=_baseline.data;
      const _savedDate=_baseline.savedAt?new Date(_baseline.savedAt).toLocaleString():'unknown';
      const _src=_baseline.sourceDevice||'unknown';
      const _sz=_baseline.sizes||{};
      const _logCount=_sz.taraCallLog!=null?_sz.taraCallLog:(Array.isArray(_d.taraCallLog)?_d.taraCallLog.length:0);
      const _winCount=_sz.wins!=null?_sz.wins:(Array.isArray(_d.taraCallLog)?_d.taraCallLog.filter(e=>e&&e.result==='WIN').length:0);
      const _lossCount=_sz.losses!=null?_sz.losses:(Array.isArray(_d.taraCallLog)?_d.taraCallLog.filter(e=>e&&e.result==='LOSS').length:0);
      const _sitoutCount=_sz.sitouts!=null?_sz.sitouts:(Array.isArray(_d.taraCallLog)?_d.taraCallLog.filter(e=>e&&e.result==='SITOUT').length:0);
      const _pastCount=_sz.pastWindows!=null?_sz.pastWindows:(Array.isArray(_d.pastWindows)?_d.pastWindows.length:0);
      const _pnl=Number(_d.lifetimePnL)||0;
      // Local state for "you're about to lose" warning
      const _localLogCount=Array.isArray(taraCallLogRef.current)?taraCallLogRef.current.length:0;
      const _msg=`Apply baseline from ${_src} (saved ${_savedDate})?\n\n`+
        `This will OVERWRITE local with the baseline:\n\n`+
        `• Tara call log: ${_logCount} entries (${_winCount}W · ${_lossCount}L · ${_sitoutCount}SO)\n`+
        `• Past windows: ${_pastCount}\n`+
        `• Lifetime P&L: ${_pnl>=0?'+':''}$${_pnl.toFixed(2)}\n`+
        `• Personal scorecard 15m: ${_d.scorecards?.['15m']?.wins||0}W-${_d.scorecards?.['15m']?.losses||0}L\n`+
        `• Personal scorecard 5m: ${_d.scorecards?.['5m']?.wins||0}W-${_d.scorecards?.['5m']?.losses||0}L\n\n`+
        `Your CURRENT local has ${_localLogCount} log entries — these will be REPLACED, not merged. `+
        `If you have unique trades on this device that aren't in the baseline, save THIS device as baseline first instead.`;
      if(!window.confirm(_msg)){setBaselineBusy(false);return;}
      // V9.1.8: BASELINE PROPAGATION FIX. Was just setting local React state — but the
      //   live cloud paths (memory/taraCallLog, scorecards/personal, etc.) still had
      //   the OLD pre-baseline data. So:
      //   (a) the cloudWatch listener would re-emit old data and merge it back into
      //       local on the next firestore tick, undoing the baseline,
      //   (b) other devices doing Force Resync would pull from the live paths and get
      //       the pre-baseline state, so the baseline never propagated.
      //   Fix: push the baseline to live cloud paths immediately so the regular sync
      //   mechanism distributes it, AND set local state so this device sees it without
      //   waiting for the cloudWatch round-trip.
      // RAW REPLACE — this is the user's explicit choice
      if(Array.isArray(_d.taraCallLog))setTaraCallLog(_d.taraCallLog);
      // V9.1.9: scorecards derive from taraCallLog now — applying the log alone
      //   updates the displayed scoreboard automatically. Old payload still
      //   carries scorecards for backward compat but we don't apply it directly.
      if(Array.isArray(_d.pastWindows))setPastWindows(_d.pastWindows);
      if(typeof _d.lifetimePnL==='number'){
        setLifetimePnL(_d.lifetimePnL);
        _pnlUpdatedAtRef.current=Number(_d.pnlUpdatedAt)||Date.now();
      }
      if(_d.taraLearnings&&typeof _d.taraLearnings==='object'){
        setTaraLearnings(_d.taraLearnings);
      }
      if(_d.regimeMemory&&typeof _d.regimeMemory==='object'){
        setRegimeMemory(_d.regimeMemory);
      }
      if(_d.adaptiveWeightsByAsset&&typeof _d.adaptiveWeightsByAsset==='object'){
        setAdaptiveWeightsByAsset(_d.adaptiveWeightsByAsset);
      }
      if(_d.regimeWeightsByAsset&&typeof _d.regimeWeightsByAsset==='object'){
        setRegimeWeightsByAsset(_d.regimeWeightsByAsset);
      }
      // V9.1.8: Push to LIVE cloud paths so other devices receive via normal sync.
      //   This is the fix for "I save baseline on laptop, apply on phone, but new
      //   device still missing trades." Now the live paths get the baseline data,
      //   so any device doing force-resync OR just sitting with cloudWatch active
      //   will receive it within the listener latency (~1s).
      try{
        const _writes=[];
        if(Array.isArray(_d.taraCallLog)){
          _writes.push(cloudWrite('memory/taraCallLog',{entries:_d.taraCallLog.slice(-_TARA_CLOUD_LOG_CAP).map(_stripEntryForCloud),_baselineApply:Date.now()}));
        }
        // V9.2.0: scorecards write removed — personal record is local-only.
        if(Array.isArray(_d.pastWindows)){
          _writes.push(cloudWrite('history/pastWindows',{entries:_d.pastWindows,_baselineApply:Date.now()}));
        }
        if(typeof _d.lifetimePnL==='number'){
          _writes.push(cloudWrite('state/lifetimePnL',{value:_d.lifetimePnL,updatedAt:Date.now(),_baselineApply:Date.now()}));
        }
        if(_d.taraLearnings&&typeof _d.taraLearnings==='object'){
          _writes.push(cloudWrite('learnings/tara',{..._d.taraLearnings,_baselineApply:Date.now()}));
        }
        await Promise.all(_writes);
        console.info('[V9.2.0] Pushed baseline to',_writes.length,'live cloud paths (scorecard kept local).');
      }catch(_e){
        console.warn('[V9.2.0] Live-path push partial fail',_e?.message);
      }
      try{setChatLog(prev=>[...prev,{role:'tara',text:`Applied baseline from ${_src} (${_savedDate}). Local now has ${_logCount} log entries · ${_winCount}W-${_lossCount}L-${_sitoutCount}SO · ${_pastCount} past windows · $${_pnl.toFixed(2)} lifetime P&L.`}]);}catch(_){}
      console.info('[V9.1.6] Applied baseline:',_sz,'from',_src);
    }catch(e){
      console.warn('[V9.1.6] Apply baseline failed',e?.message);
      try{setChatLog(prev=>[...prev,{role:'tara',text:`Apply baseline failed: ${e?.message||'unknown error'}.`}]);}catch(_){}
      alert('Apply baseline failed: '+(e?.message||'unknown error'));
    } finally {
      setBaselineBusy(false);
    }
  };

  // V9.1.10: NUCLEAR RECOVERY OPS — bypass every merge/debounce/hydration guard.
  //   Use these when normal sync isn't producing convergence and the user just wants
  //   ONE device's data to be canonical truth.

  // Force push: write THIS device's log + pastWindows + pnl + learnings directly
  //   to live cloud paths, replacing whatever's there. No merge.
  const forcePushToCloud=async()=>{
    if(baselineBusy||forceResyncing)return;
    const _log=(taraCallLogRef.current||[]).slice(-TARA_CALL_LOG_CAP);
    const _logCount=_log.length;
    const _wins=_log.filter(e=>e&&e.result==='WIN').length;
    const _losses=_log.filter(e=>e&&e.result==='LOSS').length;
    const _sitouts=_log.filter(e=>e&&e.result==='SITOUT').length;
    const _past=(Array.isArray(pastWindows)?pastWindows:[]).slice(-50);
    const _pnl=Number(_pnlRef.current)||0;
    const _msg=`FORCE PUSH this device's data to cloud, REPLACING what's there?\n\n`+
      `• ${_logCount} call log entries (${_wins}W · ${_losses}L · ${_sitouts}SO)\n`+
      `• ${_past.length} past windows\n`+
      `• ${_pnl>=0?'+':''}$${_pnl.toFixed(2)} lifetime P&L\n\n`+
      `This OVERWRITES cloud unconditionally. Other devices listening will receive this data within ~1s.\n\n`+
      `Use this when normal sync isn't converging and you want THIS device to be the truth.`;
    if(!window.confirm(_msg))return;
    setBaselineBusy(true);
    try{
      const _writes=[];
      // V13.4.187: was dropping signalScoresAtLock outright -- one Force Push wiped
      //   the signal history for every entry in the cloud doc. Uses the canonical
      //   stripper now, which keeps the 6-signal slim.
      const _stripEntry=_stripEntryForCloud;
      _writes.push(cloudWrite('memory/taraCallLog',{entries:_log.map(_stripEntry).slice(-_TARA_CLOUD_LOG_CAP),_forcePush:Date.now(),_pushedBy:_deviceLabel}));
      _writes.push(cloudWrite('history/pastWindows',{entries:_past,_forcePush:Date.now()}));
      _writes.push(cloudWrite('state/lifetimePnL',{value:_pnl,updatedAt:Date.now(),_forcePush:Date.now()}));
      if(taraLearnings)_writes.push(cloudWrite('learnings/tara',{...taraLearnings,_forcePush:Date.now()}));
      const _results=await Promise.all(_writes);
      const _ok=_results.filter(r=>r===true).length;
      try{setChatLog(prev=>[...prev,{role:'tara',text:`Force push complete from ${_deviceLabel}. ${_ok}/${_writes.length} cloud writes succeeded. ${_logCount} log entries · ${_wins}W·${_losses}L·${_sitouts}SO pushed. Other devices will receive within ~1s.`}]);}catch(_){}
      console.info('[V9.1.10] Force push:',_ok,'/',_writes.length,'writes succeeded');
      if(_ok<_writes.length){
        alert(`Force push partially succeeded (${_ok}/${_writes.length} writes). Check the SyncStatusPill for the error.`);
      }
    }catch(e){
      console.warn('[V9.1.10] Force push failed',e?.message);
      alert('Force push failed: '+(e?.message||'unknown error'));
    }finally{
      setBaselineBusy(false);
    }
  };

  // Force pull: read live cloud paths and OVERWRITE local with whatever's there.
  //   No merge, no protection for unique local entries. Use when this device is
  //   stale and just wants whatever the canonical cloud state is.
  const forcePullFromCloud=async()=>{
    if(baselineBusy||forceResyncing)return;
    setBaselineBusy(true);
    try{
      const _log=await cloudRead('memory/taraCallLog');
      const _past=await cloudRead('history/pastWindows');
      const _pnl=await cloudRead('state/lifetimePnL');
      const _learn=await cloudRead('learnings/tara');
      const _logEntries=Array.isArray(_log?.entries)?_log.entries:[];
      const _pastEntries=Array.isArray(_past?.entries)?_past.entries:[];
      const _localCount=Array.isArray(taraCallLogRef.current)?taraCallLogRef.current.length:0;
      const _wins=_logEntries.filter(e=>e&&e.result==='WIN').length;
      const _losses=_logEntries.filter(e=>e&&e.result==='LOSS').length;
      const _sitouts=_logEntries.filter(e=>e&&e.result==='SITOUT').length;
      const _msg=`FORCE PULL cloud data and REPLACE local?\n\n`+
        `Cloud has:\n`+
        `• ${_logEntries.length} call log entries (${_wins}W · ${_losses}L · ${_sitouts}SO)\n`+
        `• ${_pastEntries.length} past windows\n`+
        `• $${(Number(_pnl?.value)||0).toFixed(2)} P&L\n\n`+
        `This device currently has ${_localCount} log entries — they will be REPLACED.\n\n`+
        `Use this when this device is stale and you want it to mirror cloud exactly.`;
      if(!window.confirm(_msg)){setBaselineBusy(false);return;}
      // Hard-replace local state. Bypasses cloudWatch merge logic.
      setTaraCallLog(_logEntries);
      setPastWindows(_pastEntries);
      if(_pnl?.value!=null){
        setLifetimePnL(Number(_pnl.value)||0);
        _pnlUpdatedAtRef.current=Number(_pnl.updatedAt)||Date.now();
      }
      if(_learn&&typeof _learn==='object'&&_learn.totalResolved>=0){
        setTaraLearnings(_learn);
      }
      try{setChatLog(prev=>[...prev,{role:'tara',text:`Force pull complete on ${_deviceLabel}. Local replaced with ${_logEntries.length} log entries · ${_wins}W·${_losses}L·${_sitouts}SO from cloud.`}]);}catch(_){}
      console.info('[V9.1.10] Force pull complete:',_logEntries.length,'entries');
    }catch(e){
      console.warn('[V9.1.10] Force pull failed',e?.message);
      alert('Force pull failed: '+(e?.message||'unknown error'));
    }finally{
      setBaselineBusy(false);
    }
  };

  // V13.4.170: hourly locks were invisible unless you happened to be looking at the
  //   ladder card -- no Discord alert, no notification, no sound. That is why they get
  //   missed. Same three channels the 15m lock already uses.
  const _onHourlyLock=React.useCallback((lk)=>{
    if(!lk||!lk.side)return;
    try{
      if(typeof Notification!=='undefined'&&Notification.permission==='granted'){
        new Notification(`Tara hourly · ${lk.side} @ ${Number(lk.strike).toLocaleString('en-US')}`,{
          body:`${Math.round(Number(lk.cost)||0)}c entry${lk.minsAtLock!=null?` · ${Math.round(Number(lk.minsAtLock))}m left`:''}`,
          tag:`tara-hourly-${lk.ticker}-${lk.side}`,
        });
      }
    }catch(_e){}
    // V13.4.175 FIX: pushToast is (kind,title,body,options) -- this was calling it as
    //   (title,kind), so 'gold' landed in the `title` slot and rendered on screen
    //   literally, while `body` was left undefined. The one in-app channel of the
    //   three (Discord/Notification/toast) silently showed the word "gold" instead of
    //   the actual lock. `kind` must also be stable across a lock's own re-renders --
    //   used as-is here since ticker+side is already unique per lock.
    try{pushToast&&pushToast(
      `hourly-lock-${lk.ticker}-${lk.side}`,
      `Hourly lock: ${lk.side} @ ${Number(lk.strike).toLocaleString('en-US')}`,
      `${Math.round(Number(lk.cost)||0)}c entry${lk.minsAtLock!=null?` · ${Math.round(Number(lk.minsAtLock))}m left`:''}`,
      {color:'#23B981',durationMs:6000}
    );}catch(_e){}
  },[]);

  const _hourlyEngine=useHourlyEngine({..._hourlyObservation,taraCall,onHourlyLock:_onHourlyLock});
  const discordCoachObservationRef=useRef(null);
  const btcDiscordDelivery=useDiscordCalls({entries:taraCallLog,hourly:_hourlyEngine.rec,
    webhook:discordWebhook,username:discordUsername,avatar:discordAvatar,enabled:isMounted,version:TARA_VERSION_DISPLAY,
    readAdvisories:()=>({asset:currentAsset,ready:startupReadiness.ready,spot:currentPrice,spotAt:_startupSpotAt,
      coach:discordCoachObservationRef.current,quote:_kalshiQuote,flow:showWhaleAlerts?flowSignal:null,
      ticks:ticksRef.current,spotTicks:tickHistoryRef.current})});
  const _hourlyOnLeft=(()=>{try{return localStorage.getItem('taraCoachLeft')==='1';}catch{return false;}})();

  const handleManualSync=(dir,opts)=>{
    // V9.3.0: opts={autoExec, force}. autoExec=true tags the resulting trade entry so
    // the auto-exec daily-P&L and loss-streak cooldown can attribute it. force=true
    // bypasses the toggle-off behavior when called by the auto-exec entry effect (which
    // already gates on userPosition===null upstream).
    const _autoExec=!!(opts&&opts.autoExec);
    const _force=!!(opts&&opts.force);
    const _autoFollow=!!(opts&&opts.autoFollow);
    // V8.2: Anti-tilt cooldown gate. If active and direction is a new entry (not a flip
    //   on existing position), block it with a confirm. EXIT/flip actions are unblocked.
    // V9.3.0: skip the confirm() prompt when autoExec — guardrails already blocked it
    if(tradingSettings?.antiTiltEnabled&&tiltLockUntil>Date.now()&&userPosition!==dir){
      if(_autoExec)return; // upstream guardrail already filters this; defensive
      const _msLeft=tiltLockUntil-Date.now();
      const _minLeft=Math.ceil(_msLeft/60000);
      const _confirmed=window.confirm(`Tilt cooldown active (${_minLeft}m left). You're ${todayData?.streak||0} losses in a row. Tilt entries statistically lose more. Override and proceed?`);
      if(!_confirmed)return;
      setTiltLockUntil(0); // user explicitly overrode — clear the lock
    }
    // If switching sides mid-trade: broadcast LOSS for exited trade FIRST, then open new
    if(userPosition!==null&&userPosition!==dir){
      hasReversedRef.current=true;
      if(pendingTradeRef.current&&pendingTradeRef.current.result===null){
        // Score and log the exited trade as LOSS immediately
        const exitLog={...pendingTradeRef.current,result:'LOSS',closingPrice:currentPrice,strikePrice:targetMargin,reversed:true,
          closingGapBps:targetMargin>0?((currentPrice-targetMargin)/targetMargin)*10000:0,
          kalshiAtClose:kalshiYesPrice!=null?Number(kalshiYesPrice):null,
          resolvedTimestampISO:new Date().toISOString()};
        const newLog1=[...tradeLogRef.current,exitLog];
        saveTradeLog(newLog1);setTradeLog(newLog1);
        applyTradeLearning(newLog1,exitLog,'LOSS'); // V7.1: per-asset
        updateScore(windowType,'losses',1);
        // Broadcast the LOSS for the trade being exited — before the new entry
        const gapBpsExit=targetMargin>0?((currentPrice-targetMargin)/targetMargin)*10000:0;
        // V9.1: SUPPRESSED. User-triggered (reversal exit). Tara-only broadcasts now.
        // broadcastToDiscord('EXIT',{
        //   result:'LOSS',
        //   action:`REVERSED — exiting ${userPosition}`,
        //   price:currentPrice,
        //   strike:targetMargin,
        //   gap:gapBpsExit,
        //   clock:`${timeState.minsRemaining}m ${timeState.secsRemaining}s`,
        //   regime:lastRegimeRef.current,
        //   dir:userPosition,
        // });
        pendingTradeRef.current=null;
      }
    }
    if(userPosition===dir&&!_force){taraAdviceRef.current='SEARCHING...';setUserPosition(null);setPositionEntry(null);setForceRender(p=>p+1);return;}
    taraAdviceRef.current=String(dir);setUserPosition(String(dir));
    if(currentPrice){
      setPositionEntry({price:currentPrice,side:dir,time:Date.now(),source:_autoFollow?'auto-follow':'manual'});
      const gapBps=targetMargin>0?((currentPrice-targetMargin)/targetMargin)*10000:0;
      // V145: Expanded trade telemetry. Previously only stored {id, dir, posterior, regime,
      //       clockAtLock, hour, session, windowType, signals, result, betAmt, maxPay}.
      //       Missing: entry price (you can't analyze gap behavior without it), strike at lock
      //       (only resolution-time strike was kept), Kalshi context (so we can verify edge),
      //       FGT alignment (the primary signal!), raw vs calibrated posterior split.
      const eng=lockedCallRef.current;
      // V13.4.110: STALE-BYPASS CORRECTION. Found via code audit, not a live report -- the
      //   entry-band TRAJ-early bypass (13.4.108) reads taraCall.call BEFORE seven separate
      //   override mechanisms (BRTI, TRAJ-priority, bounce, pump/dump-reversion, reversal-
      //   evidence, direction-balance-floor) get a chance to flip it. Those overrides mutate
      //   taraCall in place via Object.keys(result).forEach(k=>taraCall[k]=result[k]), so by
      //   the time execution reaches HERE, taraCall.call may be a DIFFERENT direction than
      //   whichever one TRAJ actually agreed with. If so, the price-band exemption no longer
      //   applies to what actually locked -- re-check it here rather than let a stale bypass
      //   slip a bad-priced flipped call through. Does not relocate the 40-line override
      //   chain (too risky to restructure blind after the 13.4.102 crash); corrects the
      //   outcome at this point instead, which runs after every override has landed and
      //   before the trade snapshot below reads taraCall.call as final.
      //   NOTE (self-caught): the first version of this fix put the correction INSIDE the
      //   object literal below as a bare IIFE statement -- invalid syntax (object literals
      //   only take key:value pairs). Caught by the real vite build before shipping, not
      //   after -- exactly the check this session adopted after the 13.4.102 crash.
      try{
        if(taraCall._trajBypassFired&&taraCall._trajBypassDir!==taraCall.call&&(taraCall.call==='UP'||taraCall.call==='DOWN')){
          const _finalK=Number(taraCall.kalshiAtLock);
          const _finalCost=Number.isFinite(_finalK)?(taraCall.call==='UP'?_finalK:(100-_finalK)):null;
          const _bMin=getEntryMinCost();
          const _bMax=getEntryMaxCost();
          const _bOn=(function(){try{return localStorage.getItem('taraEntryQuality')!=='off';}catch(_eb3){return true;}})();
          if(_bOn&&_finalCost!=null&&(_finalCost<_bMin||_finalCost>=_bMax)){
            taraCall.call='SIT_OUT';
            taraCall.direction=null;
            taraCall.reason=`[V13.4.110] stale bypass corrected -- direction flipped to ${taraCall._trajBypassDir==='UP'?'DOWN':'UP'} after TRAJ-early cleared the band for ${taraCall._trajBypassDir}, and the new direction prices at ${_finalCost.toFixed(0)}c (outside ${_bMin.toFixed(0)}-${_bMax.toFixed(0)}c). Sitting out rather than lock a flipped call the bypass never actually approved.`;
          }
        }
      }catch(_eb4){}
      pendingTradeRef.current={
        id:Date.now(),dir,
        // V7.1: tag every trade with the asset (BTC or ETH) so calibration, weights, and
        //   regime stats can split per-asset. Uses ref to avoid stale closure on switches.
        asset:currentAssetRef.current||currentAsset||'BTC',
        posterior:eng?.lockedPosterior||analysis?.rawProbAbove||50, // calibrated posterior at lock
        rawPosterior:analysis?.rawPosterior||analysis?.rawProbAbove||50, // V145: pre-calibration posterior
        regime:lastRegimeRef.current,
        clockAtLock:timeState.minsRemaining*60+timeState.secsRemaining,
        hour:new Date().getHours(),session:getMarketSessions().dominant,windowType,
        signals:eng?.lockedSignals||analysis?.rawSignalScores||{},result:null,/*V13.4.68: feed learner the LIVE locked signal vector (eng.lockedSignals) not the stale analysis closure -- was writing all-zero signals so updateWeights hit the <3-nonzero gate and never learned*/
        betAmt:betAmount||0,maxPay:maxPayout||0,
        // V145 NEW FIELDS
        entryPrice:currentPrice||0,                              // BTC price when we entered
        strikeAtLock:targetMargin||0,                            // strike at the moment of lock
        gapAtEntry:gapBps,                                       // bps gap to strike at entry
        kalshiAtLock:kalshiYesPrice!=null?Number(kalshiYesPrice):null, // Kalshi YES price (UP %) at lock
        fgtAlignment:analysis?.mtfAlignment||0,                  // -4 to +4 multi-timeframe FGT
        rangeBps:analysis?.rangeBps||0,                          // V152: range position at lock (σ from window open)
        geoRisk:newsSentiment?.geoRisk||0,                       // V145: geo/macro risk at lock (0-1)
        geoTopic:newsSentiment?.geoTopic||null,                  // V145: top headline driving the risk
        windowOpenPrice:windowOpenPriceRef.current||0,           // BTC at window start
        // V3.1.7+/V3.1.9: window type classification at lock — for later per-type WR audit
        windowAmpLabel:analysis?.windowAmplitude?.label||null,
        windowRangeBpsAtLock:analysis?.windowAmplitude?.rangeBps||0,
        windowDirChanges:analysis?.windowAmplitude?.directionChanges||0,
        windowMotionTiming:analysis?.windowAmplitude?.motionTiming||null,
        // V3.1.11: candle pattern at lock — for per-pattern WR audit
        candlePatternAtLock:analysis?.candlePattern?.label||null,
        candlePatternScore:analysis?.candlePattern?.score||0,
        qualityScore:qualityGate?.score||null,                   // Tara's quality gate score
        timestampISO:new Date().toISOString(),                   // wall-clock for date analysis
        // V9.3.0: tag trades placed by Kalshi auto-execution so the daily P&L rollup,
        // loss-streak cooldown, and analytics can split auto-exec from manual.
        autoExec:_autoExec,
        // V9.5.0: opponent-modeling snapshot at lock — null if no divergence detected,
        // else {dir, kDelta, spotDeltaBps, score}. Lets us audit "did kalshi-lead
        // signals predict outcomes" once we have ≥30 examples in the log.
        kalshiLeadAtLock:analysis?.kalshiLead?{
          dir:analysis.kalshiLead.dir,
          kDelta:analysis.kalshiLead.kDelta,
          spotDeltaBps:analysis.kalshiLead.spotDeltaBps,
          score:analysis.kalshiLead.score,
          aligned:analysis.kalshiLead.dir===dir,
        }:null,
        // V13.4.100: V101 shadow posterior at lock time. dirAgree tells us, per lock,
        //   whether the old approach would have called the same direction as the real
        //   engine -- combined with netCents (already logged) this is enough to compute
        //   V101's real EV/contract had it been trading, once enough locks accumulate.
        v101ShadowAtLock:analysis?.v101Shadow?{
          posterior:analysis.v101Shadow.posterior,
          regime:analysis.v101Shadow.regime,
          dir:analysis.v101Shadow.posterior>50?'UP':(analysis.v101Shadow.posterior<50?'DOWN':null),
          dirAgree:(analysis.v101Shadow.posterior>50?'UP':(analysis.v101Shadow.posterior<50?'DOWN':null))===dir,
        }:null,
        // V13.4.102: confluence shadow at lock. score/aligned tells us, per settled lock,
        //   whether the Pine-ported 3/4 confluence would have said 'lock now' or 'wait' --
        //   combined with netCents this is enough to test the actual ask (aligned=fast,
        //   tricky=wait) once enough locks accumulate.
        confluenceAtLock:analysis?.confluence?{
          score:analysis.confluence.score,
          aligned:analysis.confluence.aligned,
          trendReady:analysis.confluence.trendReady,
          candleReady:analysis.confluence.candleReady,
          distanceReady:analysis.confluence.distanceReady,
          tapeReady:analysis.confluence.tapeReady,
          noChaseDistATR:analysis.confluence.noChaseDistATR,
        }:null,
        // V13.4.105: TRAJ AUDIT LOG. TRAJ has existed since ~V10.7.11/23 (a documented
        //   May 20 case: Tara locked DOWN while her own TRAJ said UP at +11bps -- a
        //   TRAJ-priority override was built for exactly that, but only fires when the
        //   opposing posterior is <70%%. User's Jul 28 screenshot is the same failure again:
        //   TRAJ projected +39bps UP, but posterior was 77%% DOWN (>=70 guard), so the
        //   override correctly stayed silent by its own rule -- and TRAJ was right, price
        //   spiked up hard right after. Whether 70%% is the right guard, or should be 75/80,
        //   or scale with TRAJ magnitude, is unknown -- because until now NO lock has EVER
        //   logged its TRAJ direction/magnitude/agreement. Zero locks in the full 6042-entry
        //   archive mention TRAJ. Same gap every other signal had before netCents/V101/
        //   confluence -- live and tuned for months, never auditable. Fixed here the same
        //   way: log everything, decide nothing new yet.
        trajAtLock:(Number.isFinite(analysis?.projectedGapBps)&&analysis.projectedGapBps!==0)?{
          gapBps:Math.round(analysis.projectedGapBps*10)/10,
          trajDir:analysis.projectedGapBps>0?'UP':'DOWN',
          trajectoryAdj:Number(analysis?.trajectoryAdj)||0,
          agreesWithCall:(analysis.projectedGapBps>0?'UP':'DOWN')===dir,
          postAtLock:Number(taraCall?.confidence)||null,
          wasStrongOpposite:(Number(taraCall?.confidence)||0)>=70&&(analysis.projectedGapBps>0?'UP':'DOWN')!==dir,
        }:null,
        // V13.4.106: FLIP-OVERRIDE AUDIT LOG. Discovered while investigating a BRTI
        //   contrarian-amplified flip (Coinbase -5.6bps vs 3-exchange mean, 'fighting
        //   consensus 34%') that fired against TRAJ/Grand-Trend/Structure/Momentum/Gap all
        //   reading DOWN. That one override has never had a single logged outcome despite
        //   two tuning passes (V10.5.2, V10.7.20 amplifier). Checking further: SEVEN
        //   separate override mechanisms all stamp _v10_7_11_flipType on their return
        //   object (reversal-evidence, pump-reversion, dump-reversion, traj-priority,
        //   strike-distance, bounce, brti-contrarian-amplified/brti-divergence) -- NONE
        //   were ever whitelisted for persistence. The entire override layer has been
        //   silently unauditable since V10.7.11. Also note: the 34%% posterior that
        //   triggered 'strong consensus' amplification is only 16pt off center -- the
        //   _wasStrongConsensus>=15 threshold may be firing the most aggressive amplifier
        //   tier on what is itself a fairly weak underlying read. Cannot say whether that
        //   matters without real outcomes -- this stamp is what makes that checkable.
        flipAtLock:taraCall?._v10_7_11_universalFlipped?{
          flipType:taraCall._v10_7_11_flipType||null,
          originalDir:taraCall._v10_7_11_originalDir||null,
          finalDir:dir,
          postAtLock:Number(taraCall?.confidence)||null,
          brtiDivBps:Number.isFinite(taraCall._v10_7_11_brtiDivBps)?taraCall._v10_7_11_brtiDivBps:null,
          brtiAmplified:taraCall._v10_7_20_amplified===true,
        }:null,
        // V9.7.0: mission info stamp from current auto-order (if mission was active at lock)
        missionInfo:autoOrderStateRef.current?.missionInfo||null,
      };
      // Broadcast the new entry after the reversal loss
      // V9.1: SUPPRESSED. User mandate — only Tara's own locks (TARA_LOCK at line ~17049)
      //   and whale alerts broadcast to Discord. User-triggered actions (manual entry,
      //   exit, force-close) are screen-only.
      // broadcastToDiscord('LOCK',{dir,price:currentPrice,strike:targetMargin,gap:gapBps,clock:`${timeState.minsRemaining}m ${timeState.secsRemaining}s`,regime:lastRegimeRef.current,posterior:analysis?.rawProbAbove||0});
    }
    setForceRender(p=>p+1);
  };

  const executeAction=(target,advisorLabel)=>{
    if(target==='UP'||target==='DOWN'){handleManualSync(target);return;}
    if(target==='CASH'||target==='SIT OUT'){
      const hasActiveLock=taraAdviceRef.current.includes('CONFIRMED');
      const gapBps=targetMargin>0?((currentPrice-targetMargin)/targetMargin)*10000:0;
      const result=target==='CASH'?'WIN':'LOSS';
      if(hasActiveLock&&manuallyClosedRef.current===null){
        manuallyClosedRef.current=result;
        // V4.1: Personal scorecard updates on manual close. User took action, that counts.
        if(result==='WIN')updateScore(windowType,'wins',1);
        else updateScore(windowType,'losses',1);
        if(pendingTradeRef.current&&pendingTradeRef.current.result===null){
          const resolvedTrade={...pendingTradeRef.current,result,closingPrice:currentPrice,strikePrice:targetMargin,earlyExit:true,
            closingGapBps:targetMargin>0?((currentPrice-targetMargin)/targetMargin)*10000:0,
            kalshiAtClose:kalshiYesPrice!=null?Number(kalshiYesPrice):null,
            resolvedTimestampISO:new Date().toISOString()};
          const newLog=[...tradeLogRef.current,resolvedTrade];
          saveTradeLog(newLog);setTradeLog(newLog);
          applyTradeLearning(newLog,resolvedTrade,result); // V7.1: per-asset
          recordPnL(result==='WIN',resolvedTrade);
          pendingTradeRef.current=null;
        }
        // Broadcast exit/switch action since user explicitly confirmed it
        // V9.1: SUPPRESSED. User-triggered (taking advisor action). Tara-only broadcasts.
        // broadcastToDiscord('EXIT',{result,action:advisorLabel||target,price:currentPrice,strike:targetMargin,gap:gapBps,clock:`${timeState.minsRemaining}m ${timeState.secsRemaining}s`,regime:lastRegimeRef.current});
      }
      setUserPosition(null);setPositionEntry(null);setCurrentOffer('');
      taraAdviceRef.current='CLOSED';
      setForceRender(p=>p+1);
    }
  };

  // V113: Manual Discord broadcast — user clicks OK button to send a signal/lock
  const lastManualBroadcastRef=useRef({key:'',type:''});
  // V134: Animated sync flow — shows progress overlay with staged updates
  const runSyncWithProgress=async()=>{
    const stages=[
      {pct:10,label:'Clearing local cache...',delay:200},
      {pct:25,label:`Loading baseline trades (${SEED_TRADES.length})...`,delay:400},
      {pct:45,label:`Restoring scorecard (${BASELINE_RECORD['15m'].wins}W-${BASELINE_RECORD['15m'].losses}L)...`,delay:300},
      {pct:65,label:'Resetting adaptive weights...',delay:300},
      {pct:80,label:'Updating regime memory...',delay:300},
      {pct:95,label:'Stamping baseline version...',delay:200},
    ];
    setSyncState({active:true,stage:stages[0].label,progress:5,complete:false,error:null});
    try{
      for(const s of stages){
        await new Promise(r=>setTimeout(r,s.delay));
        setSyncState({active:true,stage:s.label,progress:s.pct,complete:false,error:null});
      }
      const ok=resetToLatestBaseline();
      if(!ok)throw new Error('localStorage write failed');
      setSyncState({active:true,stage:'✓ Sync complete!',progress:100,complete:true,error:null});
      await new Promise(r=>setTimeout(r,1500));
      window.location.reload();
    }catch(e){
      setSyncState({active:true,stage:'',progress:0,complete:false,error:e.message||'Sync failed'});
    }
  };

  const broadcastSignalManual=null; // saved, confirmed Calls send automatically

  const handleChatSubmit=(e)=>{if(e.key!=='Enter'||!chatInput.trim())return;const ut=chatInput.trim();const log=[...chatLog,{role:'user',text:ut}];setChatLog(log);setChatInput('');setTimeout(()=>{let r='';const u=ut.toLowerCase();if(u.includes('/daily')){
      // V10.9.9: /daily — on-demand daily summary to Discord.
      //   EV computed as cents-per-trade from kalshiAtLock (entry cost): WIN nets
      //   (100-cost)¢, LOSS nets -cost¢. This is the per-trade EV measure, not
      //   dollar P&L (which depends on bet sizing settings) — per user request.
      const _now=new Date();
      const _todayKey=_fmtDateTz(_now,timeFormat,{year:'numeric',month:'2-digit',day:'2-digit'});
      const _src=(taraCallLog||[]).filter(e=>e&&(e.time||e.id)&&(e.asset||'BTC')===currentAsset); // V10.9.28: id fallback
      const _today=_src.filter(e=>{try{return _fmtDateTz(new Date(e.time||e.id),timeFormat,{year:'numeric',month:'2-digit',day:'2-digit'})===_todayKey;}catch(_){return false;}});
      const _resolved=_today.filter(e=>e.result==='WIN'||e.result==='LOSS');
      const _evOf=(e)=>{
        const k=e.kalshiAtLock;if(k==null||!Number.isFinite(Number(k)))return null;
        const cost=e.dir==='UP'?Number(k):100-Number(k);
        return e.result==='WIN'?(100-cost):-cost;
      };
      const _evs=_resolved.map(_evOf).filter(v=>v!=null);
      const avgEvCents=_evs.length?_evs.reduce((a,b)=>a+b,0)/_evs.length:null;
      const totalEvCents=_evs.length?_evs.reduce((a,b)=>a+b,0):null;
      const wins=_resolved.filter(e=>e.result==='WIN').length;
      const losses=_resolved.length-wins;
      const sitouts=_today.filter(e=>e.result==='SITOUT').length;
      const wr=_resolved.length?Math.round((wins/_resolved.length)*100):null;
      // Streak from resolved, newest-first
      let streakStr='—';
      if(_resolved.length){
        const last=_resolved[_resolved.length-1].result;
        let n=0;for(let i=_resolved.length-1;i>=0;i--){if(_resolved[i].result===last)n++;else break;}
        streakStr=n>=2?`${n}${last==='WIN'?'W':'L'} streak`:'—';
      }
      // Per-tier breakdown
      const _tierMap={};
      _resolved.forEach(e=>{
        const t=e.tier||'?';
        if(!_tierMap[t])_tierMap[t]={wins:0,losses:0,evs:[]};
        _tierMap[t][e.result==='WIN'?'wins':'losses']++;
        const ev=_evOf(e);if(ev!=null)_tierMap[t].evs.push(ev);
      });
      const tierBreakdown=Object.entries(_tierMap).map(([tier,v])=>{
        const tot=v.wins+v.losses;
        return{tier,wins:v.wins,losses:v.losses,wr:tot?Math.round((v.wins/tot)*100):0,
          avgEv:v.evs.length?v.evs.reduce((a,b)=>a+b,0)/v.evs.length:null,total:tot};
      }).sort((a,b)=>b.total-a.total);
      const _byEv=tierBreakdown.filter(t=>t.avgEv!=null&&t.total>=2);
      const bestTier=_byEv.length?(()=>{const t=_byEv.reduce((a,b)=>b.avgEv>a.avgEv?b:a);return`${t.tier} (${t.avgEv>=0?'+':''}${t.avgEv.toFixed(1)}¢)`;})():'—';
      const worstTier=_byEv.length?(()=>{const t=_byEv.reduce((a,b)=>b.avgEv<a.avgEv?b:a);return`${t.tier} (${t.avgEv>=0?'+':''}${t.avgEv.toFixed(1)}¢)`;})():'—';
      r=`Dashboard summary only: ${wins}W-${losses}L (${wr??'—'}%). Discord records update on the original confirmed Call; no summary blast.`;
    }else if(u.includes('/broadcast')){r='Only saved, confirmed BTC Calls send automatically. No manual signal posted.';}else if(u.includes('why')||u.includes('explain'))r=`Posterior UP: ${Number(analysis?.rawProbAbove||0).toFixed(1)}%. Regime: ${analysis?.regime}. Signal composite output. Ask 'whale' or 'position'.`;else if(u.includes('whale'))r=whaleLog.length>0?whaleLog.slice(0,8).map(w=>{const d=new Date(w.time);return`${_fmtTimeTz(d,timeFormat,{hour12:false,hour:'2-digit',minute:'2-digit',second:'2-digit'})} ${w.src} ${w.side} $${(w.usd/1000).toFixed(0)}K @ $${w.price.toFixed(0)}`;}).join('\n'):'No whale trades yet.';else if(u.includes('position'))r=positionStatus?`${positionStatus.side} @ $${positionStatus.entry.toFixed(2)} | PnL: ${positionStatus.pnlPct>0?'+':''}${positionStatus.pnlPct.toFixed(1)}% | ${positionStatus.isStopHit?'STOP HIT':'Safe'}`:'No active position.';else if(u.includes('session'))r=`Active: ${marketSessions.sessions.map(s=>`${s.flag} ${s.name}`).join(' + ')} | Dominant: ${marketSessions.dominant}`;else r=`P(UP): ${Number(analysis?.rawProbAbove||0).toFixed(1)}%. Advisor: ${analysis?.advisor?.label||'—'}. Try: why | whale | position | session | /broadcast | /daily`;setChatLog([...log,{role:'tara',text:r}]);},400);};

  const handleWindowToggle=(t)=>{if(t!=='15m')return;/* v13.3.0: 5m removed */if(t===windowType)return;setWindowType(String(t));setPendingStrike(null);taraAdviceRef.current='SEARCHING...';engineLockedDirRef.current=null;lockedCallRef.current=null;lockReleasedAtRef.current=0;try{localStorage.removeItem('taraLockedTimeSeries_v1');}catch(_){} /* V9.11.2 */posteriorHistoryRef.current=[];biasCountRef.current={UP:0,DOWN:0};hasReversedRef.current=false;manuallyClosedRef.current=null;windowSignalDirRef.current=null;softHintRef.current=0;hardForceRef.current=0;kalshiWasBelowThreshUpRef.current=false;kalshiWasBelowThreshDownRef.current=false;kalshiLastBelowThreshUpRef.current=0;kalshiLastBelowThreshDownRef.current=0;isManualStrikeRef.current=false;hasSetInitialMargin.current=false;fetchWindowOpenPrice(t);setUserPosition(null);setPositionEntry(null);setCurrentOffer('');setBetAmount(0);setMaxPayout(0);lastWindowRef.current='';peakOfferRef.current=0;_hasRestoredLockRef.current=false;_cloudRestoreCompletedRef.current=false;/* V7.10.4: also clear snapshot+samples so 15m↔5m doesn't carry stale state across window types */if(taraCallSnapshotRef.current!==undefined)taraCallSnapshotRef.current=null;if(taraCallSampleRef.current)taraCallSampleRef.current={dir:null,count:0};_rolloverGraceRef.current=Date.now();setForceRender(p=>p+1);};
  // V6.5.8: Asset switch handler. Same state-reset pattern as window toggle, plus
  //   clears price history (since BTC ticks are useless for ETH). Triggers re-fetch
  //   of Kalshi market for the new asset on next poll.
  const setCurrentAsset=(a)=>{
    if(a===currentAsset||a!=='BTC')return;
    // V7.0.7: stamp the switch time + reset broadcast flags + clear whale broadcast cooldown.
    //   Discord broadcasts within 2.5s after this fire-suppress (see broadcastToDiscord).
    //   This stops cross-asset stale-data messages: previously switching ETH→BTC could
    //   trigger an instant broadcast carrying ETH's price because state/refs hadn't
    //   stabilized yet on the new asset.
    lastAssetSwitchAtRef.current=Date.now();
    if(taraBroadcastRef.current)taraBroadcastRef.current={key:null,sentScan:false,sentSignal:false,sentLock:false,sentSitout:false};
    if(lastWhaleBroadcastRef.current)lastWhaleBroadcastRef.current={time:Date.now(),dir:null}; // 5min cooldown from now
    if(lastManualBroadcastRef.current)lastManualBroadcastRef.current={key:null,type:null};
    // V10.7.86: clear warm state on asset switch
    try{sessionStorage.removeItem('taraWarmState_v1');sessionStorage.removeItem('taraTFCandles_v1');}catch(_){}
    setCurrentAssetState(a);

    // Reset all window-bound state — fresh start on the new asset
    setPendingStrike(null);
    // V7.6: warm-switch using cached price from background feed. If the inactive asset
    //   was being polled in background, we have a recent price ready — display it
    //   immediately instead of showing null for 1-2s while feed reconnects.
    const _cached=priceByAssetRef.current?.[a];
    if(_cached&&_cached.price>0&&(Date.now()-_cached.time)<8000){
      setCurrentPrice(_cached.price);
      currentPriceRef.current=_cached.price;
    } else {
      setCurrentPrice(null);
    }
    setLiveHistory([]);
    setHistory([]);
    setKalshiStrike(null);
    setKalshiYesPrice(null);
    setTargetMargin(0); // V7.0.2: critical — strike was bleeding through
    setStrikeConfirmed(false);
    setUserPosition(null);
    setPositionEntry(null);
    
    setCurrentOffer('');
    setBetAmount(0);
    setMaxPayout(0);
    taraAdviceRef.current='SEARCHING...';
    engineLockedDirRef.current=null;
    lockedCallRef.current=null;
    lockReleasedAtRef.current=0;
    posteriorHistoryRef.current=[];
    biasCountRef.current={UP:0,DOWN:0};
    hasReversedRef.current=false;
    manuallyClosedRef.current=null;
    windowSignalDirRef.current=null;
    softHintRef.current=0;
    hardForceRef.current=0;
    kalshiWasBelowThreshUpRef.current=false;
    kalshiWasBelowThreshDownRef.current=false;
    kalshiLastBelowThreshUpRef.current=0;
    kalshiLastBelowThreshDownRef.current=0;
    isManualStrikeRef.current=false;
    hasSetInitialMargin.current=false;
    if(tickHistoryRef.current)tickHistoryRef.current=[];
    if(ticksRef&&ticksRef.current)ticksRef.current=[];
    if(taraCallSampleRef.current)taraCallSampleRef.current={dir:null,count:0};
    if(taraCallSnapshotRef.current!==undefined)taraCallSnapshotRef.current=null;
    if(kalshiStrikeRef)kalshiStrikeRef.current=null;
    if(targetMarginRef)targetMarginRef.current=0;
    if(windowOpenPriceRef)windowOpenPriceRef.current=0;
    if(windowHighRef)windowHighRef.current=0;
    if(windowLowRef)windowLowRef.current=0;
    if(windowHighTimeRef)windowHighTimeRef.current=0;
    if(windowLowTimeRef)windowLowTimeRef.current=0;
    if(windowOpenTimeRef)windowOpenTimeRef.current=0;
    if(currentPriceRef)currentPriceRef.current=null;
    if(velocityRef&&velocityRef.current)velocityRef.current={v1s:0,v5s:0,v15s:0,v30s:0,accel:0,jerk:0,peakPnL:0,troughPnL:0,pnlSlope:0};
    lastWindowRef.current='';
    _lastWindowMsRef.current=0;
    peakOfferRef.current=0;
    _hasRestoredLockRef.current=false;
    _cloudRestoreCompletedRef.current=false; // V7.10.3
    setForceRender(p=>p+1);
  };

  // V8.9.0: Smart-money detectors. Hook polls every 1s, returns 4 signals
  //   {pinning, retailCrowd, kalshiSpot, absorption}, each with detected/side/score/reason.
  //   Strip renders compact pills near the call card.
  //   IMPORTANT: hook MUST be called above the !isMounted early return to keep
  //   the hook count stable across renders (React error #310 if it isn't).
  const _smartMoneySignals=useSmartMoneyDetectors({
    tickHistoryRef,
    tradeTicksRef:ticksRef,
    kalshiHistoryRef,
    currentPrice,
    targetMargin,
    atrBps:analysis?.atrBps,
    clockSeconds:timeState?.secsRemaining,
  });

  if(!isMounted)return<div className={'min-h-screen bg-[#050508] flex items-center justify-center text-[#EDEDED]/50 font-serif text-xl animate-pulse'}>Initializing {TARA_VERSION_DISPLAY}...</div>;

  const totalDOM=(orderBook.localBuy+orderBook.localSell)||1;
  const buyPct=(orderBook.localBuy/totalDOM)*100;
  const sellPct=(orderBook.localSell/totalDOM)*100;
  const advisor=analysis?.advisor||{label:'CONNECTING...',reason:'Fetching market data...',color:'zinc',animate:false,hasAction:false};
  const _openWorkspace=(id)=>{
    setShowSports(false);setShowWeather(false);setShowHeaderOverflow(false);
    setAnalyticsPageOpen(id==='analytics');setShowBrain(id==='brain');setWorkspaceFocus(id);
    document.getElementById('tara-workspace-nav')?.scrollIntoView({behavior:'smooth',block:'start'});
  };

  return(
    <div data-tara-theme={simpleMode?'simple':'advanced'} data-tara-view={showSports?'sports':showWeather?'weather':'home'} data-tara-version="14" className={'tara-live-shell min-h-screen bg-[#050508] text-[#EDEDED] font-sans flex flex-col selection:bg-[#EDEDED]/20'} style={{fontSize:"16px",lineHeight:"1.5",overflowX:"hidden",maxWidth:"100vw"}}>
      {/* V9.8.18: Toast notifications — pump/dump + velocity-flip alerts. Fixed
          top-right, auto-close after a few seconds, dismiss on outside-click or
          tap. Stack capped at 3 concurrent. */}
      <TaraToastStack toasts={toasts} onDismiss={dismissToasts}/>
      {/* V8.8.8: Spike alert keyframes + overlay */}
      <style>{`
        @keyframes taraSpikeAlertIn {
          from { opacity: 0; transform: translateX(-50%) translateY(-12px) scale(0.96); }
          to   { opacity: 1; transform: translateX(-50%) translateY(0) scale(1); }
        }
        @keyframes taraSpikePulse {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(1.015); }
        }
        @keyframes taraReversalPulse {
          0%, 100% { box-shadow: 0 0 0 0 rgba(232,69,94,0.0); }
          50%      { box-shadow: 0 0 0 4px rgba(232,69,94,0.10); }
        }
      `}</style>
      <SpikeAlertHookHost
        tickHistoryRef={tickHistoryRef}
        lockedCallRef={lockedCallRef}
        currentAsset={currentAsset}
        atrBps={analysis?.atrBps}
      />
      
      {/* V134: Session-start status check */}
      {/* V9.10.3: SessionStartCheck removed per user request — was triggered by the
          pulsing 📋 button (also removed). Component definition retained in the file
          but unused; can be cleaned up in a follow-up. */}
      {showStats&&<StatsView tradeLog={tradeLog} scorecards={scorecards} taraCallLog={displayedCallLog} onClose={()=>setShowStats(false)} timeFormat={timeFormat}/>}
      {/* v13.4.152: SportsView is rendered inside <main> as a view swap, not
          here as an overlay. */}
      {/* V9.2.2: Dedicated Analytics Page */}
      {/* Analytics and Brain render inside the common workspace below. */}
      {/* V9.16: schedule modal hoisted to top level so simpleMode can open it */}
      {scheduleModalMain&&taraCallLog&&(
        <TradeScheduleModal
          taraCallLog={taraCallLog}
          currentAsset={currentAsset}
          timeFormat={timeFormat}
          onClose={()=>setScheduleModalMain(false)}
        />
      )}

      {/* V9.1.6: Sync menu — opens when SyncStatusPill is clicked. Three options:
          force resync (merge), save as baseline (this device → canonical), apply
          baseline (canonical → this device). Designed for the "my laptop has the
          most data, want to push it to phone" workflow. */}
      {syncMenuOpen&&(
        <SyncMenuModal
          onClose={()=>setSyncMenuOpen(false)}
          onForceResync={()=>{setSyncMenuOpen(false);forceResyncFromCloud();}}
          onSaveBaseline={()=>{setSyncMenuOpen(false);saveAsBaseline();}}
          onApplyBaseline={()=>{setSyncMenuOpen(false);applyBaseline();}}
          onCleanup={()=>{handleCleanupLog();}}
          forceResyncing={forceResyncing}
          baselineBusy={baselineBusy}
          baselineMeta={baselineMeta}
          baselineDrift={baselineDrift}
          deviceLabel={_deviceLabel}
          healthData={(()=>{
            const _priceFresh=currentPrice>0&&(Date.now()-(analysis?.lastPriceTick||Date.now()))<10000;
            const _kalshiOk=kalshiYesPrice!=null&&Number(kalshiYesPrice)>0;
            const _kalshiCached=!_kalshiOk&&kalshiDebug?.ok===false&&kalshiDebug?.reason?.includes('cached');
            const _uptimeMs=Date.now()-(_mountTimeRef?.current||Date.now());
            const _uptimeMin=Math.floor(_uptimeMs/60000);
            const _uptimeStr=_uptimeMin>=60?`${Math.floor(_uptimeMin/60)}h ${_uptimeMin%60}m`:`${_uptimeMin}m`;
            return{
              priceFresh:_priceFresh||currentPrice>0,
              kalshiOk:_kalshiOk,
              kalshiCached:!!_kalshiCached,
              // V9.10.5: Replaced TODO/hardcoded true with the real cloud sync status.
              //   _cloudSyncStatus is updated by cloudWrite/cloudDelete/cloudWatch — set to
              //   'error' on any failed write/read, set to 'ok' on successful one. 'disabled'
              //   if Firestore isn't initialized. We read it via the existing module-level
              //   getter rather than subscribing, since healthData is recomputed on every
              //   render from the SyncMenuModal's parent and the staleness window is small.
              //   The pill now goes red when writes are actually failing, not just always green.
              firestoreOk:(()=>{
                try{
                  const _st=typeof _cloudSyncStatus!=='undefined'?_cloudSyncStatus:null;
                  if(!_st||_st.state==='disabled')return false;
                  if(_st.state==='error'){
                    // If the last error was >2min ago AND we've had at least one ok since,
                    //   consider it recovered. lastOk should be more recent than lastError.
                    const _errAt=_st.lastError?.at||0;
                    const _okAt=_st.lastOk||0;
                    if(_okAt>_errAt)return true;
                    if(_errAt&&Date.now()-_errAt<120000)return false;
                    return true; // stale error, recovered
                  }
                  return true; // 'ok' or 'idle'
                }catch(_){return true;}
              })(),
              engineMs:analysis?Math.round(analysis.computeTimeMs||0):null,
              mlActive:!!taraMLModel,
              mlAccuracy:taraMLModel?Math.round(taraMLModel.accuracy*100):0,
              mlTrades:taraMLModel?.nTrades||0,
              uptime:_uptimeStr,
            };
          })()}
          localCounts={(()=>{
            const _btc=(taraCallLog||[]).filter(e=>e&&(e.asset||'BTC')==='BTC');
            const _eth=(taraCallLog||[]).filter(e=>e&&e.asset==='ETH');
            return{
              taraCallLog:Array.isArray(taraCallLog)?taraCallLog.length:0,
              wins:(taraCallLog||[]).filter(e=>e&&e.result==='WIN').length,
              losses:(taraCallLog||[]).filter(e=>e&&e.result==='LOSS').length,
              sitouts:(taraCallLog||[]).filter(e=>e&&e.result==='SITOUT').length,
              btc:{n:_btc.length,wins:_btc.filter(e=>e.result==='WIN').length,losses:_btc.filter(e=>e.result==='LOSS').length,sitouts:_btc.filter(e=>e.result==='SITOUT').length},
              eth:{n:_eth.length,wins:_eth.filter(e=>e.result==='WIN').length,losses:_eth.filter(e=>e.result==='LOSS').length,sitouts:_eth.filter(e=>e.result==='SITOUT').length},
              pastWindows:Array.isArray(pastWindows)?pastWindows.length:0,
              pnl:Number(_pnlRef.current)||0,
              // V9.2.0: personal scorecard — local-only, displayed for clarity
              personalW:Number(scorecards?.['15m']?.wins)||0,
              personalL:Number(scorecards?.['15m']?.losses)||0,
            };
          })()}
        />
      )}

      {/* V134: Sync progress overlay */}
      {syncState&&syncState.active&&(
        <div className="fixed inset-0 bg-black/80 z-[60] flex items-center justify-center p-4">
          <div className="bg-[#0A0A0E] rounded-[10px] border border-indigo-500/30 p-6 max-w-md w-full shadow-2xl">
            <div className="text-xs uppercase tracking-widest text-indigo-300 font-bold mb-3">
              {syncState.complete?'✓ Sync Complete':syncState.error?'⚠ Sync Failed':'🔄 Syncing Tara'}
            </div>
            <div className={'text-sm font-bold mb-4 leading-snug '+(syncState.error?'text-rose-300':syncState.complete?'text-emerald-300':'text-white')}>
              {syncState.error?syncState.error:syncState.stage}
            </div>
            {!syncState.error&&(
              <div className="w-full bg-[#050508] rounded-full h-2 overflow-hidden">
                <div className={'h-full transition-all duration-300 '+(syncState.complete?'bg-emerald-500':'bg-indigo-500')} style={{width:syncState.progress+'%'}}/>
              </div>
            )}
            <div className="text-[10px] text-[#EDEDED]/40 mt-3 text-center">
              {syncState.complete?'Reloading in a moment...':syncState.error?'Try again from Settings':syncState.progress+'% complete'}
            </div>
          </div>
        </div>
      )}

      {/* V134: SYNC PROGRESS OVERLAY ── shows during baseline sync */}
      {syncState.active&&(
        <div className="fixed inset-0 z-[100] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4">
          <div className={'bg-[#101014] border rounded-xl shadow-2xl max-w-sm w-full p-6 '+(syncState.error?'border-rose-500/50':syncState.complete?'border-emerald-500/50':'border-indigo-500/40')}>
            <div className="flex items-center gap-3 mb-4">
              <span className="text-2xl">{syncState.error?'⚠️':syncState.complete?'✓':'⟳'}</span>
              <div>
                <div className={'text-xs uppercase tracking-widest font-bold '+(syncState.error?'text-rose-400':syncState.complete?'text-emerald-400':'text-indigo-400')}>
                  {syncState.error?'Sync Failed':syncState.complete?'Sync Complete':'Syncing Baseline'}
                </div>
                <div className="text-[10px] text-[#EDEDED]/40 mt-0.5">baseline {BASELINE_VERSION}</div>
              </div>
            </div>

            {/* Stage label */}
            <div className="text-sm text-[#EDEDED]/85 mb-3 min-h-[20px]">
              {syncState.error||syncState.stage||'Preparing...'}
            </div>

            {/* Progress bar */}
            <div className="h-2 bg-[#050508] rounded-full overflow-hidden mb-2">
              <div
                className={'h-full transition-all duration-300 '+(syncState.error?'bg-rose-500':syncState.complete?'bg-emerald-500':'bg-indigo-500')}
                style={{width:`${syncState.progress}%`}}
              />
            </div>
            <div className="flex justify-between text-[10px] text-[#EDEDED]/40 font-mono">
              <span>{syncState.error?'Error':syncState.complete?'Done':'Working'}</span>
              <span>{syncState.progress}%</span>
            </div>

            {syncState.complete&&(
              <div className="mt-4 text-[11px] text-emerald-400/80 text-center">
                Reloading in a moment...
              </div>
            )}
            {syncState.error&&(
              <button onClick={()=>setSyncState({active:false,stage:'',progress:0,complete:false,error:null})} className="mt-4 w-full py-2 bg-rose-500/15 hover:bg-rose-500/25 border border-rose-500/30 rounded-lg text-xs font-bold uppercase tracking-wide text-rose-300">
                Dismiss
              </button>
            )}
          </div>
        </div>
      )}

      {/* V134: Learning toast removed — was crashing on minified prod build, will revisit */}
      {/* ── STICKY HEADER — V10.7.44b CLEAN ── */}
      <header className={'tara-header sticky top-0 z-40 bg-[#050508] backdrop-blur-md border-b border-[#24242E] px-2 sm:px-4 py-2 shrink-0'}>
        {/* V13.4.224: wraps instead of clipping. At 375px this row holds "Tara"
            (27px), the P&L pill (102px) and five icon buttons (230px) = 359px of
            content in 323px of space, and the excess was being cut off by the
            app shell's overflow-x:hidden rather than scrolled or wrapped. The
            badges are already hidden below sm and none of what remains is safe to
            drop -- the lane nav is how you change lane and the P&L is what you
            check most -- so the row is allowed to take a second line on narrow
            screens. It never wraps at sm and above, where it all fits. */}
        <div className="max-w-[1600px] mx-auto flex flex-wrap items-center gap-1.5 sm:gap-2">

          {/* LEFT: Logo + version + balance */}
          <div className="flex items-center gap-1.5 shrink-0 tara-brand-lockup">
            <span className="tara-brandmark" aria-hidden="true"><svg viewBox="0 0 32 32" focusable="false"><path d="M16 1.5 18.8 13.2 30.5 16 18.8 18.8 16 30.5 13.2 18.8 1.5 16 13.2 13.2Z"/></svg></span>
            <div className="tara-brand-copy">
              <h1 className="text-base sm:text-lg font-serif tracking-tight text-white">TARA</h1>
              <small>DECISION ENGINE · V14.7.16</small>
            </div>
            {/* V13.4.299: was a green-filled, green-bordered chip with a pulsing
                green dot. The build number is not an outcome, so under the
                mockup's one-accent rule it has no claim on green at all -- and
                the mockup renders it as plain mono text at 34% ink. */}
            {/* V13.4.299b: font-sans removed -- it beat the global mono rule at
                equal specificity, so the build number was the one label still in
                the sans face. The mockup sets it in .m. */}
            {/* V13.1: telemetry-health badge - confirms rich-entry stamping after deploy */}
            {(()=>{try{
              const _tl=Array.isArray(taraCallLogRef.current)?taraCallLogRef.current:[];
              const _real=_tl.filter(e=>e&&e.result!=='MISSED'&&!e._missedWhileBackgrounded).slice(-50);
              const _n=_real.length; if(_n===0)return null;
              const _rich=_real.filter(e=>!!e.taraVersion).length;
              const _pct=_rich/_n;
              // V13.4.299: chip chrome dropped, and green with it. Telemetry
              //   health is not a trade outcome, so under the mockup's one-accent
              //   rule a healthy reading is simply quiet neutral text. A degraded
              //   stamp rate IS a caution, which is what gold is reserved for.
              const _col=_pct>=0.9?{fg:'rgba(237,237,237,0.46)'}
                        :_pct>=0.5?{fg:'#D4A03A'}
                        :{fg:'#E8455E'};
              return React.createElement('span',{
                className:'hidden sm:flex items-center gap-1 text-[9px] font-bold tracking-[0.12em] uppercase cursor-default',
                style:{color:_col.fg},
                title:`Telemetry stamp rate on the last ${_n} real entries: ${_rich}/${_n} carry full signal data (regime, chop, SMC, trend, lock timing). Climbs to ${_n}/${_n} once every trading device runs v13.1+. Persistently low = a device is on a stale cached build - hard-refresh it.`,
              },`TEL ${_rich}/${_n}`);
            }catch(_){return null;}})()}
            {_SB_PAUSED&&React.createElement('span',{
              // V13.4.299: chip chrome dropped; gold text alone carries it, which
              //   is the mockup's treatment for a caution.
              className:'hidden sm:flex items-center gap-1 text-[9px] font-bold tracking-[0.14em] uppercase cursor-default',
              style:{color:'#D4A03A'},
              title:'Cloud sync paused until ~Jun 18 — data saving to this browser only. Open Memory to backup.',
            },'💾 LOCAL')}
            <span className="hidden sm:flex items-center gap-1 text-[9px] font-bold tracking-[0.14em] uppercase cursor-default" style={{color:'#D4A03A'}} title={startupReadiness.reason} data-startup-ready={startupReadiness.ready}>
              {startupReadiness.ready?'● DATA READY':startupReadiness.code==='startup-sampling'?`WARMING · ${Math.min(startupReadiness.samples,50)}/50`:'DATA WAIT'}
            </span>
            <KalshiBalancePill kalshiBalance={kalshiBalance}/>
          </div>

          {/* TODAY P&L belongs to the BTC execution lane. Keeping it in the
              global header made Sports and Weather read like crypto tabs. */}
          {!showSports&&!showWeather&&<TodayPnLPill todayData={todayData} onClick={_onTodayPnLClick}/>}

          {/* SPACER */}
          <div className="flex-1"/>

          {/* RIGHT: asset selector + window toggle + sound + ··· */}
          <div className="tara-header-controls flex items-center gap-1.5 shrink-0">

            {/* BTC */}
            <div className={'tara-modebar flex bg-[#0A0A0E] border border-[#1B1B22] rounded-lg p-0.5 shrink-0'}>
              {ASSET_KEYS.map(k=>{
                const _c=ASSET_CONFIG[k];
const _active=currentAsset===k&&!showSports&&!showWeather&&!showBrain&&!analyticsPageOpen;
                const _shadow=!_active?shadowTaraByAssetRef.current?.[k]:null;
                const _shadowFresh=_shadow&&(Date.now()-(_shadow.updatedAt||0))<15000;
                const _shadowLean=_shadowFresh&&_shadow.leanDir!=='NEUTRAL'?_shadow:null;
                const _leanColor=_shadowLean?(_shadowLean.leanDir==='UP'?'rgb(35,185,129)':'rgb(232,69,94)'):null;
                return(
                  <button key={k} aria-current={_active?'page':undefined} onClick={()=>{setShowWeather(false);setShowSports(false);setShowBrain(false);setShowAnalytics(false);setAnalyticsPageOpen(false);setShowStats(false);setWorkspaceFocus('overview');setCurrentAsset(k);}}
                    className={`px-2 sm:px-2.5 py-1 text-xs uppercase font-bold tracking-wide rounded-lg transition-all flex items-center gap-1 ${_active?'shadow-md':'text-[#EDEDED]/40 hover:text-[#EDEDED]/80'}`}
                    style={_active?{background:_c.color+'22',color:_c.color,border:'1px solid '+_c.color+'66'}:{}}
                    title={_active?_c.label:_shadowLean?`${_c.label} — Tara leans ${_shadowLean.leanDir} ${_shadowLean.confidence}%`:_c.label}
                  >
                    <span className="text-[10px]">Trade</span>
                    {_shadowLean&&<span className="ml-0.5 text-[8px] font-bold tabular-nums leading-none" style={{color:_leanColor}}>{_shadowLean.leanDir==='UP'?'▲':'▼'}{_shadowLean.confidence}</span>}
                  </button>
                );
              })}

              {/* v13.4.150: SPORTS sits in the same pill as BTC so the two read
                  as one toggle — the two things Tara actually tracks. BTC is a
                  mode (currentAsset), Sports is an overlay, but from the user's
                  side both are just "which board am I looking at". */}
              <div className="w-px my-1 bg-[#2A2A34] mx-0.5"/>
              {/* V13.4.246: BTC/Sports/Weather is one exclusive toggle, not two
                  independent switches — closing whichever else is open on every
                  click, the same as clicking BTC's own asset buttons already
                  behaved. Two booleans that could both be true is how Weather's
                  full-screen view ended up rendering on top of Sports instead of
                  replacing it. */}
              <button aria-current={showSports?'page':undefined} onClick={()=>{setShowWeather(false);setShowBrain(false);setShowAnalytics(false);setAnalyticsPageOpen(false);setShowStats(false);setWorkspaceFocus('overview');setShowSports(v=>!v);}}
                className={`px-2 sm:px-2.5 py-1 text-xs uppercase font-bold tracking-wide rounded-lg transition-all flex items-center gap-1 ${showSports?'shadow-md':'text-[#EDEDED]/40 hover:text-[#EDEDED]/80'}`}
                style={showSports?{background:T2_GOLD+'22',color:T2_GOLD,border:'1px solid '+T2_GOLD+'66'}:{}}
                title="Sports picks and record"
              >
                <span className="text-[10px]">Sports</span>
              </button>
              {/* V13.4.217: weather lane */}
              <button aria-current={showWeather?'page':undefined} onClick={()=>{setShowSports(false);setShowBrain(false);setShowAnalytics(false);setAnalyticsPageOpen(false);setShowStats(false);setWorkspaceFocus('overview');setShowWeather(v=>!v);}}
                className={`px-2 sm:px-2.5 py-1 text-xs uppercase font-bold tracking-wide rounded-lg transition-all flex items-center gap-1 ${showWeather?'shadow-md':'text-[#EDEDED]/40 hover:text-[#EDEDED]/80'}`}
                style={showWeather?{background:T2_GOLD+'22',color:T2_GOLD,border:'1px solid '+T2_GOLD+'66'}:{}}
                title="Daily-high temperature ladder"
              >
                <span className="text-[10px]">Weather</span>
              </button>
              <button aria-current={analyticsPageOpen?'page':undefined} onClick={()=>{setShowSports(false);setShowWeather(false);setShowBrain(false);setShowAnalytics(false);setAnalyticsPageOpen(true);setWorkspaceFocus('analytics');setShowHeaderOverflow(false);}} className="tara-header-mode-button" title="Analytics">Analytics</button>
              <button aria-current={showBrain?'page':undefined} onClick={()=>{setShowSports(false);setShowWeather(false);setShowAnalytics(false);setAnalyticsPageOpen(false);setShowBrain(true);setWorkspaceFocus('brain');setShowHeaderOverflow(false);}} className="tara-header-mode-button" title="Tara's Brain">Brain</button>
            </div>

            <div className="tara-status-chips" aria-label="Live feed status">
              <span className={showSports||showWeather||freshFeedStatus(lastPriceSourceRef.current?.time,15000)!=='fresh'?'is-warn':'is-ok'}>{showSports?'● SPORTS BOARD':showWeather?'● WEATHER SCAN':'● SPOT '+freshFeedStatus(lastPriceSourceRef.current?.time,15000).toUpperCase()}</span>
              <span className={showSports||showWeather?'is-ok':kalshiPingState?.ok===false?'is-warn':'is-ok'} title="Market data status only; exchange account positions are verified separately in Execution">{showSports?'↕ SNAPSHOT MODEL':showWeather?'↕ NWS · KALSHI':'↕ KALSHI MARKET '+(kalshiPingState?.ok===false?'CHECK':'DATA')}</span>
            </div>

            <button type="button" className="tara-v14-all-tools" onClick={()=>_openWorkspace('logs')}>All tools ↗</button>

            {/* window fixed at 15m, 5m removed v13.3.0 */}

            {/* Sound */}
            <button onClick={handleSoundToggle} className={`p-1.5 rounded-lg border transition-colors ${soundEnabled?'bg-indigo-500/20 border-indigo-500/40 text-indigo-400':'border-[#24242E] text-[#EDEDED]/40'}`} title={soundEnabled?'Sound on':'Sound off'}>
              {soundEnabled?<IC.Vol2 className="w-3.5 h-3.5"/>:<IC.VolX className="w-3.5 h-3.5"/>}
            </button>

            {/* ··· overflow toggle */}
            <div className="relative">
              <button
                onClick={()=>setShowHeaderOverflow(v=>!v)}
                className={`p-1.5 rounded-lg border transition-colors text-sm leading-none font-bold ${showHeaderOverflow?'bg-[#EDEDED]/10 border-[#2A2A34] text-[#EDEDED]/80':'border-[#24242E] text-[#EDEDED]/40 hover:text-[#EDEDED]/70'}`}
                title="More options"
              >···</button>

              {/* Overflow panel */}
              {showHeaderOverflow&&(
                <div
                  className="absolute right-0 top-full mt-1.5 z-50 rounded-xl border border-[#2A2A34] shadow-2xl overflow-hidden"
                  style={{background:'rgba(18,20,19,0.98)',backdropFilter:'blur(16px)',minWidth:'260px'}}
                  onMouseLeave={()=>setShowHeaderOverflow(false)}
                >
                  {/* Section: Market */}
                  <div className="px-3 pt-2.5 pb-1">
                    <div className="text-[9px] uppercase tracking-[0.15em] text-[#EDEDED]/30 mb-1.5">Market</div>
                    <div className="flex flex-wrap gap-1.5 items-center">
                      <div className="flex items-center gap-1 text-xs">{marketSessions.sessions.map((s,i)=><span key={i} className={`${s.color} opacity-80`}>{s.flag}</span>)}</div>
                      <MovementRiskPill movementRisk={movementRisk}/>
                      <StreakTiltPill todayData={todayData}/>
                    </div>
                  </div>
                  <div className="h-px bg-[#EDEDED]/8 mx-3"/>

                  {/* Section: Trade */}
                  <div className="px-3 py-2">
                    <div className="text-[9px] uppercase tracking-[0.15em] text-[#EDEDED]/30 mb-1.5">Trade</div>
                    <div className="flex flex-wrap gap-1.5">
                      {/* Tier-1 Only */}
                      {(()=>{
                        const _on=!!tradingSettings?.tier1OnlyMode;
                        const _pr={current:null};
                        return(
                          <button
                            onPointerDown={()=>{_pr.current=setTimeout(()=>{_pr.current=null;setShowTradingSettings(true);},500);}}
                            onPointerUp={()=>{if(_pr.current){clearTimeout(_pr.current);_pr.current=null;setTradingSettings(prev=>({...prev,tier1OnlyMode:!prev?.tier1OnlyMode}));}}}
                            onPointerLeave={()=>{if(_pr.current){clearTimeout(_pr.current);_pr.current=null;}}}
                            className="px-2 py-1 rounded-lg text-[10px] uppercase font-bold tracking-wider transition-colors select-none"
                            style={_on?{color:'rgb(35,185,129)',background:'rgba(35,185,129,0.10)',border:'1px solid rgba(35,185,129,0.35)'}:{color:'rgba(237,237,237,0.55)',background:'#0E0E12',border:'1px solid rgba(237,237,237,0.15)'}}
                            title={_on?'Tier-1 Only ON — tap to allow all. Long-press for settings.':'All tiers. Tap to enable Tier-1 Only. Long-press for settings.'}
                          >{_on?'★ TIER-1':'TIERS · ALL'}</button>
                        );
                      })()}
                      {/* Auto-exec */}
                      <button onClick={()=>setShowTradingSettings(true)}
                        className="px-2 py-1 rounded-lg text-[10px] uppercase font-bold tracking-wider transition-colors"
                        style={killSwitchEngaged||autoExecSettings.enabled&&autoExecSettings.dryRun===false?{color:'#E8455E',background:'rgba(232,69,94,0.15)',border:'1px solid rgba(232,69,94,0.40)'}:autoExecSettings.dryRun?{color:'#23B981',background:'rgba(212,162,76,0.10)',border:'1px solid rgba(212,162,76,0.30)'}:{color:'rgba(237,237,237,0.35)',background:'#0E0E12',border:'1px solid #24242E'}}
                        title="Auto-exec settings"
                      >{killSwitchEngaged?'⛔ KILLED':autoExecSettings.enabled&&autoExecSettings.dryRun===false?'LIVE PAUSED':autoExecSettings.dryRun?'DRY · AUTO':'AUTO · OFF'}</button>
                      {/* shadow feed */}
                      {(()=>{
                        const _dfa='BTC';
                        const _dfs=shadowTaraByAssetRef.current?.[_dfa];
                        const _dff=_dfs&&(Date.now()-(_dfs.updatedAt||0))<8000;
                        const _dfl=_dff?_dfs.leanDir:null;
                        const _dfc=_dff?Math.round(_dfs.confidence||0):null;
                        const _dfcfg=ASSET_CONFIG[_dfa]||{};
                        const _dflc=_dfl==='UP'?'rgb(35,185,129)':_dfl==='DOWN'?'rgb(232,69,94)':'rgba(237,237,237,0.45)';
                        return(
                          <button onClick={()=>setShowDualFeed(v=>!v)}
                            className="flex items-center gap-1 px-2 py-1 rounded-lg text-[10px] font-bold uppercase tracking-wider transition-colors select-none"
                            style={showDualFeed?{background:'rgba(237,237,237,0.08)',border:'1px solid rgba(237,237,237,0.25)',color:'rgba(237,237,237,0.80)'}:{background:'#0E0E12',border:'1px solid rgba(237,237,237,0.12)',color:'rgba(237,237,237,0.40)'}}
                          >
                            <span style={{color:_dfcfg.color||'rgba(237,237,237,0.55)'}}>{_dfcfg.icon||'?'}</span>
                            <span>{_dfa}</span>
                            {!_dff?<span className="text-rose-400/70">STALE</span>:<span style={{color:_dflc}}>{_dfl==='UP'?'▲':_dfl==='DOWN'?'▼':'–'}{_dfc!=null?_dfc+'%':''}</span>}
                            <span style={{opacity:0.4}}>{showDualFeed?'▲':'▼'}</span>
                          </button>
                        );
                      })()}
                    </div>
                  </div>
                  <div className="h-px bg-[#EDEDED]/8 mx-3"/>

                  {/* Section: System */}
                  <div className="px-3 py-2">
                    <div className="text-[9px] uppercase tracking-[0.15em] text-[#EDEDED]/30 mb-1.5">System</div>
                    <div className="flex flex-wrap gap-1.5 items-center">
                      <SyncStatusPill onClick={_onSyncStatusClick}/>
                      <TabPresencePill peerTabs={peerTabs}/>
                      {/* Time format */}
                      <div className="flex flex-col items-center cursor-pointer px-1.5 py-0.5 rounded-lg border border-[#24242E] hover:border-indigo-500/30 transition-colors" onClick={()=>setTimeFormat(timeFormat==='local'?'utc':timeFormat==='utc'?'est':'local')}>
                        <span className="text-[10px] text-[#EDEDED]/55 uppercase tracking-wider font-bold">{timeFormat.toUpperCase()}</span>
                      </div>
                      {/* V13.4.38: advanced-view toggle removed -- simple view only */}
                      {/* Volume */}
                      {soundEnabled&&(
                        <div className="flex items-center gap-1" title="Alert volume">
                          <IC.Vol2 className="w-3 h-3 text-[#EDEDED]/40"/>
                          <input type="range" min="0" max="1" step="0.05" value={alertVolume} onChange={e=>setAlertVolume(parseFloat(e.target.value))} className="w-16 h-1 cursor-pointer" style={{accentColor:'#818CF8'}}/>
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="h-px bg-[#EDEDED]/8 mx-3"/>

                  {/* Section: Tools */}
                  <div className="px-3 py-2 pb-2.5">
                    <div className="text-[9px] uppercase tracking-[0.15em] text-[#EDEDED]/30 mb-1.5">Tools</div>
                    <div className="flex flex-wrap gap-1.5">
                      <button onClick={()=>{setShowStats(true);setShowHeaderOverflow(false);}} className="p-1.5 rounded-lg text-xs font-bold transition-colors" style={{background:T2_GOLD_GLOW,color:T2_GOLD,border:'0.5px solid '+T2_GOLD_BORDER}} title="Performance Stats">📊 Stats</button>
                      {/* v13.4.150: Sports moved out of the overflow menu and
                          into the header pill next to BTC. */}
                      <button onClick={()=>{setShowBrain(true);setWorkspaceFocus('brain');setShowHeaderOverflow(false);}} className="p-1.5 rounded-lg text-xs font-bold transition-colors" style={{background:T2_GOLD_GLOW,color:T2_GOLD,border:'0.5px solid '+T2_GOLD_BORDER}} title="Tara's Brain">🧠 Brain</button>
                      <button onClick={()=>{setShowBestPractices(true);setShowHeaderOverflow(false);}} className="p-1.5 rounded-lg text-xs font-bold transition-colors" style={{background:T2_GOLD_GLOW,color:T2_GOLD,border:'0.5px solid '+T2_GOLD_BORDER}} title="Best Practices">📖 Guide</button>
                      <button onClick={()=>{setShowGuide(true);setShowHeaderOverflow(false);}} className="p-1.5 rounded-lg border border-indigo-500/30 bg-indigo-500/10 text-indigo-400 hover:bg-indigo-500/20 transition-colors text-xs" title="How Tara Works">? Help</button>
                      <FlowBtn flowSignal={flowSignal} active={showWhaleLog} onClick={()=>{setShowWhaleLog(!showWhaleLog);setShowHeaderOverflow(false);}} cls="flex"/>
                      <TheoryLabBtn active={showTheoryLab} onClick={()=>{setShowTheoryLab(!showTheoryLab);setShowHeaderOverflow(false);}} cls="flex"/>
                      <button onClick={()=>{setShowSettings(true);setShowHeaderOverflow(false);}} className="p-1.5 rounded-lg border border-[#24242E] text-[#EDEDED]/40 hover:text-indigo-400 transition-colors" title="Feed Settings"><IC.Link className="w-3.5 h-3.5"/></button>
                      <button onClick={()=>{setShowAnalytics(true);setShowHeaderOverflow(false);}} className="p-1.5 rounded-lg border border-[#24242E] text-[#EDEDED]/40 hover:text-indigo-400 transition-colors" title="Training Engine"><IC.BarChart className="w-3.5 h-3.5"/></button>
                      <button onClick={()=>{setAnalyticsPageOpen(true);setWorkspaceFocus('analytics');setShowHeaderOverflow(false);}} className="p-1.5 rounded-lg border border-indigo-500/20 text-indigo-400/60 hover:text-indigo-400 transition-colors text-xs" title="Analytics Page">📊 Analytics</button>
                      {/* V10.7.45: Window lifecycle audit export — diagnostic for missing-log bugs */}
                      <button
                        onClick={()=>{
                          try{
                            const _lifecycleRaw=_loadLifecycle();
                            const _audit=_loadAuditBuffer();
                            // V10.9.23: cross-stamp lifecycle against the REAL call log (ground truth).
                            //   The in-flight logCall/confirmed recorders only fire on the
                            //   _logSnapshotEntry path; entries committed via other paths left
                            //   logCalls empty / logConfirmedAt null, making logged trades look
                            //   'missing.' If an entry for the window exists in taraCallLog, it
                            //   WAS logged - mark it so, killing the false 'not logged' alarms.
                            const _logIndex=new Map();
                            for(const _e of (taraCallLog||[])){
                              if(!_e||!_e.windowId)continue;
                              const _k=_e.windowId+'|'+(_e.asset||'BTC');
                              if(!_logIndex.has(_k))_logIndex.set(_k,[]);
                              _logIndex.get(_k).push(_e.result||'pending');
                            }
                            const _lifecycle=(_lifecycleRaw||[]).map(r=>{
                              const _k=(r&&r.wid)+'|'+((r&&r.asset)||'BTC');
                              const _results=_logIndex.get(_k)||null;
                              const _logged=!!(_results&&_results.length);
                              return {...r,loggedInCallLog:_logged,callLogResults:_results,
                                diagnosis:_logged?'ok-in-calllog':(r&&r.diagnosis)||null,
                                logConfirmedAt:(r&&r.logConfirmedAt)||(_logged?'(in-calllog)':null)};
                            });
                            const _missingCount=_lifecycle.filter(r=>!r.loggedInCallLog&&r.snapshotSets&&r.snapshotSets.length>0).length;
                            const _payload={
                              exportedAt:new Date().toISOString(),
                              baselineVersion:typeof BASELINE_VERSION!=='undefined'?BASELINE_VERSION:'unknown',
                              trulyMissingWindows:_missingCount,
                              windowLifecycle:_lifecycle,
                              auditEvents:_audit,
                              note:'V10.9.23 diagnostic export. loggedInCallLog is cross-checked against the real taraCallLog (ground truth). trulyMissingWindows counts windows that had calls but no matching log entry; 0 means nothing was lost. Empty logCalls/logConfirmedAt with loggedInCallLog=true is just stale in-flight telemetry, not data loss.',
                            };
                            const _json=JSON.stringify(_payload,null,2);
                            const _blob=new Blob([_json],{type:'application/json'});
                            const _url=URL.createObjectURL(_blob);
                            const _a=document.createElement('a');
                            const _ts=new Date().toISOString().replace(/[:.]/g,'-').slice(0,19);
                            _a.href=_url;
                            _a.download=`tara-window-audit-${_ts}.json`;
                            document.body.appendChild(_a);
                            _a.click();
                            document.body.removeChild(_a);
                            setTimeout(()=>URL.revokeObjectURL(_url),100);
                            setShowHeaderOverflow(false);
                          }catch(e){
                            alert('Window audit export failed: '+(e.message||String(e)));
                          }
                        }}
                        className="p-1.5 rounded-lg border border-amber-500/20 text-amber-400/70 hover:text-amber-400 transition-colors text-xs"
                        title="Export window lifecycle audit (V10.7.45). Click after a missing-log incident — shows EXACTLY which window broke down where."
                      >🔍 Window Audit</button>
                    </div>
                  </div>
                </div>
              )}
            </div>

          </div>
        </div>
      </header>

      {/* V9.8.4: STALE-FEED BANNER. Only visible when feed is frozen (>=60s).
              Tucks below the sticky header. Provides one-click switching to
              backup sources without making the user fish for the FEED pill.
              Slow (30-60s) state is shown only via the FEED pill — not loud
              enough yet to deserve a banner. */}
      {feedFrozen&&(
        <div className="sticky top-[44px] sm:top-[52px] z-40 px-2 sm:px-4 py-2 border-b" style={{
          background:'rgba(232,69,94,0.13)',
          borderColor:'rgba(232,69,94,0.45)',
        }}>
          <div className="max-w-[1600px] mx-auto flex flex-wrap items-center gap-2 sm:gap-3 text-[11px] sm:text-xs">
            <span className="font-bold tracking-wide text-rose-300">⚠ FEED FROZEN</span>
            <span className="text-rose-200/85">
              {PRICE_SOURCES[priceSource].name} · last change {feedStaleSeconds}s ago · new locks paused
            </span>
            <div className="flex items-center gap-1.5 ml-auto">
              {PRICE_SOURCE_KEYS.filter(k=>k!==priceSource).map(k=>(
                <button
                  key={k}
                  onClick={()=>setPriceSource(k)}
                  className="px-2 py-1 rounded-lg border text-[10px] sm:text-[11px] font-bold tracking-wide transition-colors hover:bg-white/10"
                  style={{
                    borderColor:'rgba(237,237,237,0.25)',
                    color:'#EDEDED',
                    background:'rgba(255,255,255,0.05)',
                  }}
                >
                  Try {PRICE_SOURCES[k].name}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* The live posterior, quality, FGT, geo and pending-resolution indicators
          now share the workspace heading instead of taking a second full-width
          header row. A live posterior is never presented as the locked Call. */}

      {/* ── MAIN CONTENT ── */}
      {/* V9.16.4: Single layout for both modes. Same V9.15 grid structure, same
          components, same positions — identical JSX in both modes. The visual
          "simple" difference is applied via CSS overrides triggered by the
          data-tara-theme attribute on <main>. This guarantees zero feature loss
          (impossible to miss components when the JSX is the same) and lets us
          iterate visual polish without restructuring. */}
      <>
      <main
        className="tara-main flex-1 w-full max-w-[1600px] mx-auto px-2 sm:px-3 lg:px-4 py-2 sm:py-3 flex flex-col gap-3 min-h-0 min-w-0 overflow-x-hidden"
      >

        {/* v13.4.152: SPORTS is a view swap, not an overlay — the header toggle
            changes what <main> shows, the same way the asset button changes the
            board. The BTC tree below stays MOUNTED and merely hidden: unmounting
            it would tear down the price feeds, websocket tape and tick history
            and re-initialise them on every trip to Sports and back. */}
        {showSports&&<SportsView onClose={()=>setShowSports(false)}/>}
        {showWeather&&<WeatherView onClose={()=>setShowWeather(false)} weatherPicks={weatherPicks}/>}
        <div className={(showSports||showWeather)?'hidden':'contents'}>

        {/* V7.10.6: Market context strip */}
        <MarketContextStrip
          useLocalTime={useLocalTime}
          timeFormat={timeFormat}
          taraLearnings={taraLearnings}
          taraCallLog={taraCallLog}
          currentAsset={currentAsset}
          analysis={analysis}
          currentStreak={currentStreak}
        />

        {/* V13.4.293: Today card moved to the center column. */}


        {/* V13.4.293: Price/Strike/Depth + Tape Flow moved to the center column. */}

        {/* V9.1.1: TapeStrip relocated — now renders between TaraCallCard and
            ProjectionsCard for tighter signal-to-decision adjacency. */}

        {/* V9.17.6: Round ticket info MERGED INTO Tara's Advisor panel below
            (which renders inside PredictionContent / left column). User said
            they wanted those calls shown in the advisor panel, not as a
            separate stacked card. No standalone render here. */}


        {/* V9.17.2: Scalper panel moved into PredictionContent (left column),
            replacing the engine prediction hero. No standalone render here. */}

        {/* V9.1.1: TradeScheduleStrip relocated — now renders inside RightPanel
            between Engine Log and Live Feeds. Full-schedule modal opens via the
            ⊕ button on the strip header. */}

        {/* V8.2: Anti-tilt cooldown — most urgent banner, blocks new locks */}
        {/* V13.4.255: AntiTiltCooldownBanner removed — see the note at its
            former definition. tiltLockUntil is only ever set to 0, so it never rendered. */}


        {/* V9.7.0: Mission Mode compact widget — only renders when active */}
        <MissionPanel
          mission={mission}
          setMission={setMission}
          regimeDirCalibration={regimeDirCalibration}
          killSwitchEngaged={killSwitchEngaged}
          setKillSwitchEngaged={setKillSwitchEngaged}
          compact={true}
        />

        {/* V10.7.44: Dual feed strip — only renders when header toggle is ON */}
        {showDualFeed&&<DualAssetCallStrip
          currentAsset={currentAsset}
          onSwitch={setCurrentAsset}
          taraCall={taraCall}
          kalshiYesPrice={kalshiYesPrice}
          currentPrice={currentPrice}
          targetMargin={targetMargin}
          shadowRef={shadowTaraByAssetRef}
          diagRef={shadowDiagRef}
          timeState={timeState}
        />}

        {/* V13.4.263: LiveTradeCoach moved from here down into the column that
            holds Tara’s Call, so the locked call and the live read on it are one
            block instead of two features 300 lines apart. Props unchanged. */}

        {/* V8.2: Asset rotation suggestion */}
        {/* V13.4.255: AssetRotationHint removed — _otherAsset is hardcoded to
            BTC, the same asset as current, so it could never suggest a rotation. */}

        {/* V8.2: Pre-window prep (30s before next window) */}
        <PreWindowPrepCard
          timeState={timeState}
          bestWindowsToday={bestWindowsToday}
          todayData={todayData}
          marketCtx={getMarketContext(new Date())}
        />

        {/* V8.2: Last-loss post-mortem (auto-show after fresh loss, dismissible) */}
        <LastLossPostmortem
          todayData={todayData}
          taraCallLog={taraCallLog}
          currentAsset={currentAsset}
        />


        {/* V8.4: PerformanceCard moved into the prediction column (bottom) — fills the
            blank space below PredictionContent and uses the lg:auto-rows-fr column height
            efficiently. Renders responsively at narrower column widths. */}

        {/* V8.2: Trading settings modal — V9.3.0: now also Kalshi auto-exec controls */}
        <TradingSettingsModal
          taraCallLog={taraCallLog}
          open={showTradingSettings}
          onClose={()=>setShowTradingSettings(false)}
          settings={tradingSettings}
          setSettings={setTradingSettings}
          kalshiCreds={kalshiCreds}
          saveKalshiCreds={_saveKalshiCreds}
          autoExecSettings={autoExecSettings}
          setAutoExecSettings={setAutoExecSettings}
          killSwitchEngaged={killSwitchEngaged}
          setKillSwitchEngaged={setKillSwitchEngaged}
          kalshiPingState={kalshiPingState}
          setKalshiPingState={setKalshiPingState}
          autoExecCooldownUntil={autoExecCooldownUntil}
          setAutoExecCooldownUntil={setAutoExecCooldownUntil}
          mission={mission}
          setMission={setMission}
          regimeDirCalibration={regimeDirCalibration}
          scalperSettings={scalperSettings}
          setScalperSettings={setScalperSettings}
          kalshiAgreeMode={kalshiAgreeMode}
          setKalshiAgreeMode={setKalshiAgreeMode}
        />

        {/* V8.5: Best Practices modal — comprehensive trader's guide */}
        <BestPracticesModal
          open={showBestPractices}
          onClose={()=>setShowBestPractices(false)}
        />


        {/* V5.3: pendingStrike confirmation banner removed per user request.
            Strike auto-confirms at window open (live spot) and Kalshi overrides when its data
            arrives. User can still manually edit the strike pill any time. */}

        {/* ── WINDOW RECAP TOAST ── */}
        {windowRecap&&(
          <div className={`shrink-0 rounded-xl border-2 px-4 py-3 flex items-center gap-3 animate-pulse-once transition-all ${windowRecap.won?'bg-emerald-500/10 border-emerald-500/40':'bg-rose-500/10 border-rose-500/40'}`}>
            <span className="text-2xl">{windowRecap.won?'W':'L'}</span>
            <div className="flex-1 min-w-0">
              <div className={`text-sm font-serif font-bold ${windowRecap.won?'text-emerald-400':'text-rose-400'}`}>
                {windowRecap.won?'WIN':'LOSS'} · {windowRecap.dir} · {windowRecap.regime}
              </div>
              <div className={'text-xs text-[#EDEDED]/50 mt-0.5'}>
                Strike <span className="text-white">${(windowRecap.strike||0).toLocaleString(undefined,{minimumFractionDigits:2,maximumFractionDigits:2})}</span>
                {' → '}Close <span className="text-white">${(windowRecap.closePrice||0).toLocaleString(undefined,{minimumFractionDigits:2,maximumFractionDigits:2})}</span>
                {' · '}<span className={windowRecap.gapBps>=0?'text-emerald-400':'text-rose-400'}>{windowRecap.gapBps>=0?'+':''}{windowRecap.gapBps.toFixed(1)} bps</span>
              </div>
            </div>
            <button onClick={()=>setWindowRecap(null)} className={'text-[#EDEDED]/30 hover:text-white text-lg leading-none'}>×</button>
          </div>
        )}

        {/* V13.4.305 REMOVED: MobileTabBar. Found while checking the mobile
            "Analytics" tab -- this was a second, older mobile tab-switcher
            rendering directly below the newer Signal/Chart/Analytics one
            (V13.4.224 dashboard rebuild), both driving the SAME mobileTab
            state. Three of its four tabs (signal/logs/chart) exactly
            duplicated the newer bar under different labels (its "LOGS" and
            the newer bar's "Analytics" are the same id). Its fourth,
            "Targets" (id: projections), pointed at ProjectionsCard, whose
            only real content -- TaraCallCard -- carries its own independent
            `hidden lg:block`, so Targets showed nothing but an empty bordered
            box on any phone, at any width, regardless of this bar's own
            mobileTab gating. Its second row (Brain/Stats/Sports shortcuts)
            was justified by an on-mount comment claiming the header hides
            those at phone width "with no way to reach them otherwise" -- no
            longer true: Brain and Stats are in the header's ··· overflow
            menu, and Sports got its own always-visible icon pill in the
            header back in V13.4.150. Nothing here was reachable ONLY through
            this bar. See project_tara_dashboard_mockup_rebuild memory. */}

        {/* V2.1: Grid changed from equal 3-col to 1.25fr/1fr/1fr at lg+ — prediction card promoted as hero.
                  V2.2.1: tightened from 1.5fr → 1.25fr (1.5 was overshooting visually). auto-rows-fr restored
                  so supporting columns match hero height — the empty-space fix is now done at the
                  card-content level (sections inside cards distribute), not at the grid level.
            V6.2.3: Removed md:grid-cols-2 because it created orphan 3rd-card at tablet widths
                  (the right panel sat alone on a second row). Now goes 1-col → 3-col at lg
                  with no awkward intermediate stage. Tablet users get cleaner stacked layout. */}
        {/* V8.4: min-w-0 on grid + columns prevents content overflow from forcing
            the grid to stretch wider than viewport. auto-rows-fr keeps cols same height. */}
        <div className="tara-approved-columns" data-workspace={workspaceFocus} data-overview-expanded={expandedOverview}>
        <div className="tara-approved-primary" id="tara-primary-surface" data-workspace={workspaceFocus} data-overview-expanded={expandedOverview}>
        <TaraWorkspaceNav
          setShowAnalytics={setAnalyticsPageOpen}
          setShowBrain={setShowBrain}
          setShowHeaderOverflow={setShowHeaderOverflow}
          activeView={workspaceFocus}
          setActiveView={setWorkspaceFocus}
        />
        {workspaceFocus==='overview'&&<><div className="tara-v14-page-lead"><div><h2>Your current window</h2><p>The locked Call, its record and the market in one place.</p></div><button className="tara-v145-settings-button" onClick={()=>setShowTradingSettings(true)}>Window settings</button></div><TaraLiveMarketMetrics asset={currentAsset} priceSource={priceSource} currentPrice={currentPrice} targetMargin={targetMargin} strikeSource={strikeSource} activeTicker={kalshiActiveMarket?.ticker||null} timeState={timeState} windowType={windowType} quote={_kalshiQuote} windowId={computeWindowId(windowType)}/></>}
        {workspaceFocus!=='overview'&&<TaraWorkspaceHeading view={workspaceFocus} analysis={analysis} qualityGate={qualityGate} newsSentiment={newsSentiment} pendingKalshiCount={tradeLog.filter(t=>t.result==='PENDING-VERIFY').length} snapshot={_activeCallSnapshot}/>}
        {workspaceFocus!=='memory'&&callStorageStatus.state==='degraded'&&<TaraStorageStatus status={callStorageStatus}/>}
        {workspaceFocus==='overview'&&<TaraPageBrief
          taraCall={taraCall}
          snapshot={_activeCallSnapshot}
          autoExecSettings={autoExecSettings}
          autoOrderState={autoOrderState}
          userPosition={userPosition}
          positionReconciliation={positionReconciliation}
          manualKalshiEntry={manualKalshiEntry}
          activeTicker={kalshiActiveMarket?.ticker||null}
          taraScorecards={taraScorecards}
          taraCallLog={displayedCallLog}
          todayData={todayData}
          timeState={timeState}
          windowType={windowType}
          onNavigate={_openWorkspace}
        />}
        {(workspaceFocus==='overview'||workspaceFocus==='execution')&&<TaraExecutionOverview
          snapshot={_activeCallSnapshot}
          autoExecSettings={autoExecSettings}
          autoOrderState={autoOrderState}
          userPosition={userPosition}
          positionReconciliation={positionReconciliation}
          manualKalshiEntry={manualKalshiEntry}
          activeTicker={kalshiActiveMarket?.ticker||null}
          canReconcile={!!(kalshiCreds.apiKeyId&&kalshiCreds.privateKeyPem)}
          onReconcile={()=>_manualReconcileNowRef.current?.()}
          onDetails={()=>setWorkspaceFocus('execution')}
          onOpenSettings={()=>setShowTradingSettings(true)}
          compact={workspaceFocus==='overview'&&!expandedOverview}
        />}
        {workspaceFocus==='execution'&&<TaraExchangeAuditPanel state={exchangeAudit}
          onRefresh={refreshExchangeAudit}
          connected={!!(kalshiCreds.apiKeyId&&kalshiCreds.privateKeyPem)}/>}
        {workspaceFocus==='overview'&&windowType==='15m'&&<p className="text-[11px] text-[#EDEDED]/55 px-1 py-2" role="status">
          Automatic Calls close at 7:00 remaining.
          {!_activeCallSnapshot&&callCommitHoldRef.current?.windowId===computeWindowId(windowType)&&<> {callCommitHoldRef.current.reason}</>}
        </p>}
        {['overview','logs','schedule'].includes(workspaceFocus)&&<DiscordDeliveryPanel delivery={btcDiscordDelivery}/>}
        {workspaceFocus==='overview'&&!expandedOverview&&<><TaraOverviewMarket
          compactRead asset={currentAsset} priceSource={priceSource} resolution={resolution} setResolution={setResolution}
          currentPrice={currentPrice} targetMargin={targetMargin} strikeSource={strikeSource}
          activeTicker={kalshiActiveMarket?.ticker||null} timeState={timeState} pastWindows={pastWindows}
          windowType={windowType} timeFormat={timeFormat} orderBook={orderBook} tapeWindows={tapeWindows}
          smartMoneySignals={_smartMoneySignals} qualityGate={qualityGate} onNavigate={_openWorkspace}
        /><TaraHourlyQuick onNavigate={_openWorkspace}/></>}
        {workspaceFocus==='overview'&&<button className="tara-overview-toggle" type="button" aria-expanded={expandedOverview} aria-controls="tara-execution-surface tara-schedule-surface" onClick={()=>setExpandedOverview(v=>!v)}>
          <span><b>{expandedOverview?'Hide full control room':'Show full control room'}</b><small>Signals · TradingView · news · memory · hourly · all original panels</small></span>
          <i aria-hidden="true">{expandedOverview?'−':'+'}</i>
        </button>}
        {workspaceFocus==='logs'&&<TaraToolsWorkspace
          positionReconciliation={positionReconciliation}
          activeTicker={kalshiActiveMarket?.ticker||null}
          canReconcile={!!(kalshiCreds.apiKeyId&&kalshiCreds.privateKeyPem)}
          onReconcile={()=>_manualReconcileNowRef.current?.()}
          onOpenSync={_onSyncStatusClick}
          onOpenTradingSettings={()=>setShowTradingSettings(true)}
          onOpenFeedSettings={()=>setShowSettings(true)}
          onOpenStats={()=>setShowStats(true)}
          onOpenTraining={()=>setShowAnalytics(true)}
          onOpenGuide={()=>setShowBestPractices(true)}
          onOpenHelp={()=>setShowGuide(true)}
          onOpenFlow={()=>setShowWhaleLog(true)}
          onOpenTheory={()=>setShowTheoryLab(true)}
        />}
        {workspaceFocus==='analytics'&&analyticsPageOpen&&<TaraAnalyticsPage embedded learning={learning} asset={currentAsset} windowType={windowType} taraCallLog={taraCallLog} taraMLModel={taraMLModel} lockResearch={lockResearch} onClose={()=>{setAnalyticsPageOpen(false);setWorkspaceFocus('overview');}} timeFormat={timeFormat}/>}
        {workspaceFocus==='brain'&&showBrain&&<BrainView embedded learning={learning} snapshot={_activeCallSnapshot} analysis={analysis} qualityGate={qualityGate} scorecards={scorecards} baseline={BASELINE_RECORD} kalshiDebug={kalshiDebug} strikeSource={strikeSource} strikeMode={strikeMode} taraCall={taraCall} taraScorecards={taraScorecards} windowType={windowType} onClose={()=>{setShowBrain(false);setWorkspaceFocus('overview');}}/>}
        {workspaceFocus==='schedule'&&<TaraHourlyRecordSummary/>}
        {workspaceFocus==='signals'&&<><TaraLiveMarketMetrics asset={currentAsset} priceSource={priceSource} currentPrice={currentPrice} targetMargin={targetMargin} strikeSource={strikeSource} activeTicker={kalshiActiveMarket?.ticker||null} timeState={timeState} windowType={windowType} quote={_kalshiQuote} windowId={computeWindowId(windowType)}/><TaraSignalsSummary analysis={analysis} candidate={taraCall} lock={readLockState(_activeCallSnapshot)} orderBook={orderBook} tapeWindows={tapeWindows} smartMoneySignals={_smartMoneySignals} qualityGate={qualityGate} deadline={callLockDeadline(windowType)}/></>}
        {/* V13.4.298: lg:auto-rows-fr + the grid default (stretch) forced every
            column to the height of the tallest one, showing as 400-600px of
            blank space under the shorter columns' last card once all 3 columns
            were filled with real content (measured live: center column ~24%
            blank, right column ~22%, on a 2011px-tall left column). lg:items-start
            lets each column size to its own content instead -- the tradeoff is
            the columns' bottom edges no longer line up. */}
<TaraDetailEnvelope enabled={workspaceFocus==='signals'}><div className="tara-live-grid" id="tara-execution-surface">
          
          {/* V13.4.271: THIS TRADE leads the page. The mockup puts it top-left as
              the first and largest thing on screen, because it is the only card that
              answers "what is happening with my money right now". It was sitting in
              the middle column under the strike, depth and tape chrome.
              The wrapper is the grid child now, so the column count is unchanged --
              same pattern as the middle column. (V13.4.298: columns no longer
              height-match; each sizes to its own content.) */}
           <div className="flex flex-col gap-3 min-w-0">
          {/* V13.4.320 — POSITION RECONCILIATION DRIFT BANNER, relocated here from
              inside the legacy "prediction card" (now hidden on desktop, see its
              wrapper below) so it stays visible regardless. Real safety check for
              auto-exec: flags when Tara's tracked position doesn't match Kalshi's
              actual book. Shows ONLY when the 30s poll detects drift (v13.4.319
              fixed a false-positive on dry-run fills). */}
          <PositionReconciliationBanner positionReconciliation={positionReconciliation}
            onRetryNow={()=>_manualReconcileNowRef.current&&_manualReconcileNowRef.current()}
            onExitNow={()=>{
              // V13.4.344: 'unknown-resolved-filled' and 'unknown-to-tara' are
              //   the two drift kinds where Kalshi has ALREADY confirmed one
              //   specific real, open position (ticker+side+count) that
              //   autoOrderState never reached status:'filled' for -- exactly
              //   the "lost tracking" case. Build the override _runExit needs
              //   directly from that confirmed data instead of relying on
              //   the (here, wrong-shaped) real autoOrderState. Every other
              //   drift kind (phantom, count-mismatch, ticker-mismatch) is
              //   either already correctly handled by real autoOrderState or
              //   is genuinely ambiguous about WHICH ticker to close -- left
              //   on the no-override path, same as before.
              const _exitable=(positionReconciliation?.driftDetails||[])
                .find(d=>(d.kind==='unknown-resolved-filled'||d.kind==='unknown-to-tara')&&d.ticker&&d.kalshiCount>0);
              const _override=_exitable?{
                status:'filled',
                dir:_exitable.side==='yes'?'UP':'DOWN',
                ticker:_exitable.ticker,
                filledCount:_exitable.kalshiCount,
                dryRun:false,
                filledAtCents:null,
                at:Date.now(),
              }:null;
              return _runExit('manual-user-exit',_override);
            }}/>
          {/* V13.4.268: THIS TRADE -- the card from the mockup. One trade, three
              numbered stages, always present. Replaces the scattered TARA'S CALL
              headline + TRADE COACH + auto-exec status that all described the same
              round from three different columns. */}
          <details className="tara-trade-details">
          <summary>TRADE COACH · ORDER DETAILS · SCALPER CONTROLS</summary>
          <ThisTradeCard
            taraCall={taraCall}
            snapshot={_activeCallSnapshot}
            analysis={analysis}
            timeState={timeState}
            windowType={windowType}
            kalshiYesPrice={kalshiYesPrice}
            autoOrderState={autoOrderState}
            userPosition={userPosition}
            manualKalshiEntry={manualKalshiEntry}
            trailPeakCents={_exitTrailRef.current?.peak}
            tickHistoryRef={tickHistoryRef}
            targetMargin={targetMargin}
            currentPrice={currentPrice}
            autoExecSettings={autoExecSettings}
            timeFormat={timeFormat}
            killSwitchEngaged={killSwitchEngaged}
            setShowTradingSettings={setShowTradingSettings}
          >
            <ScalperAdvisorPanel
              scalperRead={scalperRead}
              scalperSettings={scalperSettings}
              scalperPosition={scalperPosition}
              setScalperPosition={setScalperPosition}
              scalperLog={scalperLog}
              setScalperLog={setScalperLog}
              scalperSuggestionsRef={scalperSuggestionsRef}
              scalperDismissedRef={scalperDismissedRef}
              kalshiYesPrice={kalshiYesPrice}
              kalshiActiveMarket={kalshiActiveMarket}
              currentAsset={currentAsset}
              tiltLockUntil={tiltLockUntil}
              autoExecSettings={autoExecSettings}
              setShowTradingSettings={setShowTradingSettings}
              setTradingSettings={setTradingSettings}
              setAutoExecSettings={setAutoExecSettings}
              ticketEntryOverrideRef={ticketEntryOverrideRef}
              taraCall={taraCall}
              analysis={analysis}
              tradingSettings={tradingSettings}
              autoOrderState={autoOrderState}
              userPosition={userPosition}
              manualKalshiEntry={manualKalshiEntry}
              windowType={windowType}
              timeState={timeState}
              useLocalTime={useLocalTime}
              timeFormat={timeFormat}
              onPlaceOrderOnTaraCall={_handlePlaceOrderOnTaraCall}
              manualOrderFeedback={_manualOrderFeedback}
              killSwitchEngaged={killSwitchEngaged}
              onClearKillSwitch={()=>setKillSwitchEngaged(false)}
              onClearAutoOrder={()=>setAutoOrderState(null)}
              taraCallLog={taraCallLog}
              kalshiBalance={kalshiBalance}
              taraSnapshotForTicket={_activeCallSnapshot?{
                call:_activeCallSnapshot.call,
                direction:_activeCallSnapshot.direction,
                tier:_activeCallSnapshot.tier,
                confidence:_activeCallSnapshot.confidence,
                caution:_activeCallSnapshot.caution,
              }:null}
            />
          </ThisTradeCard>
          </details>
          {/* V13.4.294: Conviction + Entry Pricing, matching the mockup's left
              column order (This Trade -> Conviction -> Entry Pricing). Both
              extracted from TaraCallCard, which skips them here on desktop
              (desktopSplit) -- see project_tara_dashboard_mockup_rebuild memory.
              V13.4.298: wrapped hidden lg:block -- this div had no mobile gate,
              so both showed a second time on mobile alongside TaraCallCard's
              own bundled Conviction Meter / Execution Row (mobile's call site
              never sets desktopSplit, by design, so it still renders them). */}
          <div className="hidden lg:flex lg:flex-col gap-3">
          <ConvictionMeterCard taraCall={taraCall} analysis={analysis}/>
          <EntryPricingCard taraCall={taraCall}/>
          </div>
          {/* ── PREDICTION CARD ── */}
          {/* V13.4.320: hidden on desktop (auto-exec only, per direct answer --
              "fold the whole lower card away, keep only what auto-exec needs").
              This card's countdown/progress-bar/quality-read duplicated THIS
              TRADE above it, and its actionable content (Quality Gate detail,
              Entry Checklist, "ENTERED UP/DOWN" manual-sync buttons,
              ManualKalshiEntryInput) is the MANUAL trading path -- not used.
              The one real safety piece it held, Position Reconciliation Drift,
              was relocated above THIS TRADE so it stays visible. Mobile is
              untouched: this remains the "signal" tab's primary content there,
              same as before -- only lg:hidden is new. */}
          <details className="tara-research"><summary>Manual position controls & detailed call evidence</summary>
          <div className="flex flex-col relative min-w-0">
            <div className="absolute top-0 left-0 w-full h-px rounded-t-xl" style={{background:'linear-gradient(to right, transparent, '+T2_GOLD_BORDER+' 30%, '+T2_GOLD_BORDER+' 70%, transparent)'}}></div>
            <T2Stamp code="PRED · 015"/>
            <div className="flex justify-between items-center mb-3 shrink-0">
              <div className="flex items-center gap-2 min-w-0">
                <div onClick={()=>setTimeFormat(timeFormat==='local'?'utc':timeFormat==='utc'?'est':'local')} className={'flex items-center gap-1.5 bg-[#050508] border border-[#24242E] px-2 py-1 rounded-full text-xs font-bold uppercase tracking-wide cursor-pointer hover:border-indigo-500/30 transition-colors'} title={`Time format: ${timeFormat.toUpperCase()} · click to cycle`}>
                  <IC.Clock className="w-4 h-4"/>
                  <span className={'text-[#EDEDED]/60 hidden sm:inline'}>{timeState.startWindow}–{timeState.nextWindow} {timeFormat.toUpperCase()}</span>
                  <span className="text-white font-bold text-sm">{timeState.minsRemaining}m {timeState.secsRemaining}s</span>
                  {analysis?.isPostDecay&&<span className="text-amber-400">⚡</span>}
                </div>
                {/* V5.6.6: Past-windows tracker — Kalshi-style outcome history pill */}
                <PastWindowsPill pastWindows={pastWindows} windowType={windowType} timeFormat={timeFormat}/>
              </div>
              <button onClick={()=>{
                // Force exit: score based on whether offer > bet (profit) or position in loss
                if(userPosition&&manuallyClosedRef.current===null&&taraAdviceRef.current.includes('CONFIRMED')){
                  const offerVal=parseFloat(currentOffer)||0;
                  const inProfit=offerVal>betAmount||(positionStatus&&positionStatus.pnlPct>0);
                  const result=inProfit?'WIN':'LOSS';
                  manuallyClosedRef.current=result;
                  // V4.1: Personal scorecard updates on force exit.
                  if(result==='WIN')updateScore(windowType,'wins',1);else updateScore(windowType,'losses',1);
                  if(pendingTradeRef.current&&pendingTradeRef.current.result===null){
                    const resolvedTrade={...pendingTradeRef.current,result,closingPrice:currentPrice,strikePrice:targetMargin,forceExit:true,
                      closingGapBps:targetMargin>0?((currentPrice-targetMargin)/targetMargin)*10000:0,
                      kalshiAtClose:kalshiYesPrice!=null?Number(kalshiYesPrice):null,
                      resolvedTimestampISO:new Date().toISOString()};
                    const newLog=[...tradeLogRef.current,resolvedTrade];saveTradeLog(newLog);setTradeLog(newLog);
                    // Personal force exits remain in the personal record, not
                    // the exact-market supervised learning corpus.
                    pendingTradeRef.current=null;
                  }
                }
                setUserPosition(null);setPositionEntry(null);taraAdviceRef.current='CLOSED';setForceRender(p=>p+1);
              }} className={'px-2 py-1 rounded-lg text-xs font-bold uppercase tracking-wide flex items-center gap-1 transition-colors'} style={{
                background:T2_COPPER_BG,
                color:T2_COPPER,
                border:'1px solid '+T2_COPPER_BORDER,
              }}>
                <IC.Alert className="w-4 h-4"/>Force Exit
              </button>
            </div>

            {/* V5.1: Phase strip with MINUTE-MARKER decision-progress timeline.
                User asked for 'count progress timer for a decision to be made in 1min,
                2min, 3min etc — not a general timer of progress of the window.'
                Bar now shows 1m markers; current position highlighted. Countdown shows
                'decision in Xs' = time until next gate-relax + check moment. */}
            {(()=>{
              const totalSec=windowType==='15m'?900:300;
              const elapsed=totalSec-((timeState.minsRemaining*60)+timeState.secsRemaining);
              if(elapsed<=0)return null;
              const tc=taraCall;
              // V13.4.288: was "a snapshot exists and is not a sit-out". Same
              //   presence-is-commitment mistake; now asks the authority.
              const isLocked=readLockState(_activeCallSnapshot).tradeable;
              const isSitOut=_activeCallSnapshot?.call==='SIT_OUT'||(tc?.call==='SIT_OUT'&&analysis?.isSystemLocked);
              const isCall=tc?.call==='UP'||tc?.call==='DOWN';
              const samplesLeft=Math.max(0,(tc?.needSamples||3)-(tc?.samples||0));
              // Window time-remaining
              const remSec=Math.max(0,totalSec-elapsed);
              const remMin=Math.floor(remSec/60),remRem=remSec%60;
              const remLabel=remMin>0?`${remMin}m ${String(remRem).padStart(2,'0')}s left`:`${remRem}s left`;
              // Window elapsed fraction
              const elapsedFrac=Math.min(1,elapsed/totalSec);
              const totalMin=totalSec/60;
              // V5.6.5: Phase label uses the same four-state model — no in-progress flicker.
              //   Tara is either SCANNING, LOCKED, SAT OUT, or pre-snapshot ANALYZING.
              //   Direction colors only show post-commit so the bar doesn't flash UP/DOWN
              //   based on internal lean.
              let label='SCANNING',accent=T2_GOLD,countdownText='gathering data';
              if(isLocked){
                const _lockDir=_activeCallSnapshot?.call;
                label='LOCKED';
                accent=_lockDir==='UP'?'rgba(52,211,153,0.7)':_lockDir==='DOWN'?'rgba(232,69,94,0.7)':T2_GOLD;
                countdownText=remLabel;
              } else if(isSitOut){
                label='SITTING OUT';
                accent='rgba(212,162,76,0.5)';
                countdownText=remLabel;
              } else {
                // Pre-commit: pure scanning. No FORMING/WATCHING reveal.
                const _need=tc?.needSamples||100;
                const _samples=tc?.samples||0;
                if(_samples>=5&&_need>0){
                  if(tc?.lockEtaStalled||tc?.lockEtaSec==null){
                    countdownText='waiting for stronger signal';
                  } else if(tc.lockEtaSec===0){
                    countdownText='committing this tick';
                  } else if(tc.lockEtaSec>remSec){
                    // V13.4.173: the ETA is derived purely from the sample-accumulation
                    //   rate and was never compared against the window clock, so at a
                    //   low rate late in a window it would print e.g. "decision in ~2m 0s"
                    //   with 40s left -- promising a decision that cannot arrive. The
                    //   number is honest; the framing was not.
                    countdownText=`unlikely to lock this window (~${formatDuration(tc.lockEtaSec)} needed, ${formatDuration(remSec)} left)`;
                  } else {
                    countdownText=`decision in ~${formatDuration(tc.lockEtaSec)}`;
                  }
                } else if(elapsed<20){
                  countdownText=`observing — ${20-elapsed}s of search remaining`;
                } else {
                  countdownText='scanning for edge';
                }
              }
              // Minute markers along the bar (skip 0, mark every minute up to total)
              const minMarkers=[];
              for(let m=1;m<totalMin;m++){
                const isPast=elapsed>=m*60;
                minMarkers.push({pos:(m/totalMin)*100,m,isPast});
              }
              return(
                <div className="mb-3 px-1">
                  <div className="flex justify-between items-baseline mb-1 gap-2">
                    <span className="text-[9px] uppercase tracking-[0.18em] text-[#EDEDED]/55 font-bold shrink-0">Tara · {label}</span>
                    <span className={`text-[10px] tabular-nums tracking-wide truncate ${(isCall&&!isLocked)||isLocked?'text-[#EDEDED]/85 font-bold':'text-[#EDEDED]/45'}`}>{countdownText}</span>
                  </div>
                  <div className="relative h-1.5 bg-[#050508] rounded-full overflow-hidden">
                    {/* Minute tick markers */}
                    {minMarkers.map((mk,i)=>(
                      <div key={i} className="absolute top-0 bottom-0 w-px" style={{
                        left:mk.pos+'%',
                        background:mk.isPast?'rgba(35,185,129,0.4)':'rgba(237,237,237,0.15)',
                      }}></div>
                    ))}
                    {/* Progress fill — fills with elapsed time (always meaningful) */}
                    <div className="absolute top-0 bottom-0 left-0 transition-all duration-700" style={{
                      width:(elapsedFrac*100).toFixed(1)+'%',
                      background:accent,
                      opacity:0.85,
                    }}></div>
                    {/* Position marker — small dot at current elapsed time */}
                    <div className="absolute top-0 bottom-0 w-0.5 transition-all duration-700" style={{
                      left:(elapsedFrac*100).toFixed(1)+'%',
                      background:isCall||isLocked?accent:T2_GOLD,
                      transform:'translateX(-50%)',
                      boxShadow:'0 0 4px '+(isCall||isLocked?accent:T2_GOLD),
                    }}></div>
                  </div>
                  {/* Minute labels below — only show if window is 15m (cluttered on 5m) */}
                  {totalMin>=10&&(
                    <div className="flex justify-between mt-0.5 px-px text-[7px] text-[#EDEDED]/30 tabular-nums">
                      <span>0</span><span>5m</span><span>10m</span><span>15m</span>
                    </div>
                  )}
                </div>
              );
            })()}

        {/* V13.4.293: Smart Money moved to the center column. */}

            {/* V5.6.1: Tara's Call on mobile signal tab — moved here from the projections tab.
                V6.2.3: lg:hidden (was md:hidden) since responsive layout now switches at lg. */}
            <TaraCallCard taraCall={taraCall} taraScorecards={taraScorecards} taraCallLog={displayedCallLog} windowType={windowType} timeState={timeState} analysis={analysis} taraLearnings={taraLearnings} kalshiYesPrice={kalshiYesPrice} useLocalTime={useLocalTime} timeFormat={timeFormat} convictionTrajectory={convictionTrajectory} todayData={todayData} movementRisk={movementRisk} bestWindowsToday={bestWindowsToday} handleManualSync={handleManualSync} userPosition={userPosition} reversalRisk={lockedCallRef.current?.reversalRisk||null} onSoftHint={()=>{softHintRef.current=Date.now();setForceRender(p=>p+1);}} onHardForce={()=>{hardForceRef.current=Date.now();setForceRender(p=>p+1);}} onEditEntry={(entryId,newValue,field)=>{
              // V9.9.3: same dual-axis edit logic as desktop ProjectionsCard call site.
              const _field=field||'result';
              setTaraCallLog(prev=>{
                const next=prev.map(e=>{
                  if(e.id!==entryId)return e;
                  // V10.7.89: fullUpdate path — apply all fields from reconcile
                  if(_field==='__fullUpdate__'&&newValue&&typeof newValue==='object'){
                    return{...e,...newValue,manualEdit:true,manualEditedAt:Date.now()};
                  }
                  const _valUp=String(newValue||'').toUpperCase();
                  if(_field==='direction'){
                    if(_valUp!=='UP'&&_valUp!=='DOWN'&&_valUp!=='SIT_OUT')return e;
                    let _autoResult=e.result;
                    if(_valUp==='SIT_OUT')_autoResult='SITOUT';
                    else if(typeof e.closingPrice==='number'&&typeof e.strike==='number'&&e.strike>0){
                      const _wonByPrice=_valUp==='UP'?(e.closingPrice>=e.strike):(e.closingPrice<e.strike);
                      _autoResult=_wonByPrice?'WIN':'LOSS';
                    }
                    return{...e,dir:_valUp,result:_autoResult,manualEdit:true,manualEditedAt:Date.now()};
                  }else{
                    const valid=_valUp==='WIN'||_valUp==='LOSS'||_valUp==='SITOUT';
                    if(!valid)return e;
                    return{...e,result:_valUp,manualEdit:true,manualEditedAt:Date.now()};
                  }
                });
                setTimeout(()=>_recomputeLearningsFromLog(next),0);
                return next;
              });
            }} onDeleteEntry={(entryId)=>{
              // V10.7.43: hard delete from log. Used by "Clear All Unverifiable" in Storage Audit.
              setTaraCallLog(prev=>{
                const next=prev.filter(e=>e.id!==entryId);
                setTimeout(()=>_recomputeLearningsFromLog(next),0);
                return next;
              });
            }} className="lg:hidden"/>

            {/* V9.1.2: TapeStrip relocated to compact bar next to Depth of Market. */}

            <PredictionContent strikeConfirmed={strikeConfirmed} strikeMode={strikeMode} targetMargin={targetMargin} isLoading={isLoading} analysis={analysis} currentPrice={currentPrice} qualityGate={qualityGate} userPosition={userPosition} manualKalshiEntry={manualKalshiEntry} setManualKalshiEntry={setManualKalshiEntry} timeState={timeState} streakData={streakData} handleManualSync={handleManualSync} getMarketSessions={getMarketSessions} executeAction={executeAction} broadcastSignalManual={broadcastSignalManual} discordWebhook={discordWebhook} regimeDirWR={regimeDirWR} kalshiYesPrice={kalshiYesPrice} newsSentiment={newsSentiment} taraCall={taraCall} taraScorecards={taraScorecards} windowType={windowType} brtiApprox={brtiApprox}/>


            {/* V9.10.2: PerformanceCard removed — consolidated into UnifiedTodayCard
                above. The mt-auto + pt-3 wrapper that pinned it to column bottom no
                longer has content; left empty in case the prediction column ever
                needs a bottom element again. (V13.4.298: mt-auto no longer has
                anything to push against now that columns aren't stretched.) */}
            <div className="mt-auto pt-3 min-w-0"/>
          </div>
          </details>
          </div>

          {/* ── V111: PROJECTIONS CARD (col 2 - 5m/15m/1h tabs) ──
              v13.4.153: wrapped so News + Live Feeds can sit directly beneath
              Tara's Call. The wrapper is the grid child now, so the column
              count is unchanged. (V13.4.298: columns no longer height-match.) */}
           <div className="flex flex-col gap-3 min-w-0" id="tara-signals-surface">
          {/* V13.4.263: stages 2 and 3 of the trade -- how it is going, and what
              the auto-exec did about it. Returns null with no open position, so it
              leads this column only while a trade is live, which is the one time
              the live read outranks the call sitting under it. */}
          {/* V8.7: Live Trade Coach — situational advisory while in a trade */}
          {/* V9.3.0: also shows Kalshi auto-order status + kill switch when active */}
          <LiveTradeCoach
            userPosition={userPosition}
            positionStatus={positionStatus}
            taraCall={taraCall}
            analysis={analysis}
            movementRisk={movementRisk}
            currentPrice={currentPrice}
            targetMargin={targetMargin}
            timeState={timeState}
            kalshiYesPrice={kalshiYesPrice}
            currentOffer={currentOffer}
            whaleLog={whaleLog}
            tradingSettings={tradingSettings}
            todayData={todayData}
            tickHistoryRef={tickHistoryRef}
            autoOrderState={autoOrderState}
            killSwitchEngaged={killSwitchEngaged}
            onKillSwitch={()=>setKillSwitchEngaged(v=>!v)}
            onUrgentCoachAlert={payload=>{discordCoachObservationRef.current={...payload,callKey:callKey(_activeCallSnapshot)};}}
            liveCoachReversalRef={liveCoachReversalRef}
            reversalRisk={lockedCallRef.current?.reversalRisk||null}
            lockedSnapshotDir={isDirection(_activeCallSnapshot?.call)?_activeCallSnapshot.call:null}
            lockedSnapshot={_activeCallSnapshot}
            onClearAutoOrder={()=>setAutoOrderState(null)}
          />
        {/* V13.4.293: relocated from a full-width stack above the grid, and
            from column 1, into the center column -- matching the approved
            mockup, whose center column is Price+Strike+Depth+Chart, then Smart
            Money, then Tape Flow, then Today. See project_tara_dashboard_mockup_rebuild
            memory for the full before/after mapping. */}
        {/* STATS BAR */}
        <div className={'bg-[#0A0A0E] rounded-[10px] border border-[#1B1B22] relative overflow-hidden shrink-0'}>
          <div className="absolute top-0 left-0 w-full h-0.5 bg-gradient-to-r from-emerald-500 via-indigo-500 to-purple-500 opacity-70"></div>
          <div className="p-2 sm:p-3 flex flex-wrap lg:flex-nowrap lg:flex-row lg:items-center gap-2 sm:gap-3 overflow-x-hidden">
            
            {/* Strike — auto or manual + live price (V10.7.44a) */}
            <div className="flex min-w-0 w-full lg:w-auto col-span-1 gap-2 items-start">
              {/* LEFT: feed toggle + big live price */}
              <div className="flex flex-col shrink-0 min-w-[80px]">
                {/* Feed source button — moved here from header */}
                <button
                  onClick={()=>{
                    const _i=PRICE_SOURCE_KEYS.indexOf(priceSource);
                    const _next=PRICE_SOURCE_KEYS[(_i+1)%PRICE_SOURCE_KEYS.length];
                    setPriceSource(_next);
                  }}
                  className="flex items-center gap-1 text-[9px] font-bold tracking-wider px-1.5 py-0.5 rounded-lg border transition-colors mb-1 self-start"
                  style={{background:feedFrozen?'rgba(232,69,94,0.12)':feedSlow?'rgba(35,185,129,0.12)':'rgba(255,255,255,0.04)',borderColor:feedFrozen?'rgba(232,69,94,0.45)':feedSlow?'rgba(35,185,129,0.40)':'rgba(255,255,255,0.15)',color:feedFrozen?'#fb7185':feedSlow?'#23B981':'rgba(237,237,237,0.55)'}}
                  title={`Live price source: ${PRICE_SOURCES[priceSource].name}. Click to cycle. ${feedFrozen?`FROZEN ${feedStaleSeconds}s`:feedSlow?`Slow (${feedStaleSeconds}s)`:'Live'}`}
                >
                  <span>FEED</span>
                  <span className="opacity-50">·</span>
                  <span>{PRICE_SOURCES[priceSource].label}</span>
                  {feedFrozen&&<span className="opacity-90">·{feedStaleSeconds}s</span>}
                </button>
                {/* V10.9.11b: REF (CF-Benchmark blend) health badge — placed right
                    under the FEED button so feed status + reference status sit
                    together. Green = blend live & CF-accurate; amber = degraded
                    (falling back toward the raw chart feed). This is the visible
                    answer to "is the OKX offset being corrected right now." */}
                {(()=>{
                  const _b=brtiApprox;
                  const _ready=_b&&_b.current>0&&_b.samples60s>=5&&(_b.sourceCount>=2||(_b.sourceCount>=1&&_b.hasAnchor));
                  const _div=_b&&_b.divergenceBps!=null?_b.divergenceBps:null;
                  const _label=!_b||!(_b.current>0)?'warming':_ready?`${_b.sourceCount}src`:`${_b.sourceCount||0}src·deg`;
                  const _ok=_ready;
                  const _title=!_b||!(_b.current>0)
                    ?'Reference blend warming up — using chart feed for now'
                    :`CF-Benchmark blend · ${_b.sourceCount} source${_b.sourceCount===1?'':'s'} (${(_b.sources||[]).join('+')||'—'})${_b.correctedCount?` · ${_b.correctedCount} bias-corrected`:''}${_b.hasAnchor?' · anchor live':' · NO CB/KR anchor'} · ${_b.samples60s} samples${_div!=null?` · chart ${_div>=0?'+':''}${_div}bps vs blend`:''}`;
                  return(
                    <div
                      className="flex items-center gap-1 text-[9px] font-bold tracking-wider px-1.5 py-0.5 rounded-lg border self-start mt-1"
                      style={{
                        background:_ok?'rgba(35,185,129,0.10)':'rgba(35,185,129,0.10)',
                        borderColor:_ok?'rgba(35,185,129,0.30)':'rgba(35,185,129,0.40)',
                        color:_ok?'rgb(35,185,129)':'#23B981',
                      }}
                      title={_title}
                    >
                      <span>REF</span>
                      <span className="opacity-50">·</span>
                      <span className="tabular-nums">{_label}</span>
                      {_div!=null&&Math.abs(_div)>=8&&<span className="opacity-80">{_div>=0?'+':''}{Math.round(_div)}</span>}
                    </div>
                  );
                })()}
                {/* Big live price */}
                {currentPrice>0&&(
                  <div className="flex flex-col">
                    {/* V13.4.299b: font-serif dropped. It was the one thing on the
                        page overriding the mono rule on the single most prominent
                        number, so the live price rendered in Space Grotesk while
                        every other figure went mono. The mockup sets the price in
                        .m like the rest. Colour stays: a ticking price up/down is
                        an outcome, which is what green/red is for. */}
                    <div className={`flex items-center gap-1 font-bold tabular-nums ${tickDirection==='up'?'text-emerald-400':tickDirection==='down'?'text-rose-400':'text-white'}`}>
                      <IC.Zap className={`w-3 h-3 shrink-0 ${tickDirection==='up'?'text-emerald-400':tickDirection==='down'?'text-rose-400':'text-[#EDEDED]/30'}`}/>
                      <span className="text-lg sm:text-xl leading-tight">${currentPrice.toLocaleString(undefined,{minimumFractionDigits:2,maximumFractionDigits:2})}</span>
                    </div>
                    {targetMargin>0&&(
                      <span className={`text-[10px] font-bold px-1 py-0.5 rounded-lg mt-0.5 self-start ${currentPrice>=targetMargin?'text-emerald-400 bg-emerald-500/10':'text-rose-400 bg-rose-500/10'}`}>
                        {currentPrice>=targetMargin?'+':''}{(((currentPrice-targetMargin)/targetMargin)*10000).toFixed(0)}bps
                      </span>
                    )}
                  </div>
                )}
              </div>
              {/* Divider */}
              <div className="w-px self-stretch bg-[#EDEDED]/10 shrink-0"/>
              {/* RIGHT: strike label + input */}
              <div className="flex flex-col flex-1 min-w-0">
                <div className="flex items-center justify-between mb-1 gap-2 min-w-0">
                  <div className={'text-xs text-[#EDEDED]/40 uppercase tracking-wide shrink-0'}>Strike</div>
                  <span
                    onClick={()=>{isManualStrikeRef.current=false;hasSetInitialMargin.current=false;setWindowOpenStrike(currentPriceRef.current||currentPrice);}}
                    title={(()=>{
                      const baseTooltip=strikeSource==='kalshi'?'Strike from Kalshi · click to re-capture':strikeMode==='auto'?'Live spot price at window open · click to re-capture':'Manual override · click to restore live';
                      const dbg=kalshiDebug;
                      let dbgLine='';
                      if(dbg.ok===null)dbgLine='\n\nKalshi: not yet polled';
                      else if(dbg.ok===false)dbgLine=`\n\nKalshi extraction FAILED: ${dbg.reason}`;
                      else if(dbg.bestStrike)dbgLine=`\n\nKalshi: ${dbg.reason}`;
                      else dbgLine=`\n\nKalshi: ${dbg.totalMarkets} markets · ${dbg.matchingClose} matching close · NO STRIKE EXTRACTABLE\nFields: ${(dbg.sampleFields||[]).slice(0,5).join(', ')}`;
                      return baseTooltip+dbgLine;
                    })()}
                    className={`text-[10px] px-1.5 py-0.5 rounded-lg cursor-pointer select-none font-bold transition-colors ${strikeSource==='kalshi'?'bg-emerald-500/15 text-emerald-400 border border-emerald-500/30':strikeMode==='auto'?'bg-emerald-500/15 text-emerald-400 border border-emerald-500/30':'bg-amber-500/15 text-amber-400 border border-amber-500/30 hover:bg-emerald-500/15 hover:text-emerald-400'}`}
                  >{strikeSource==='kalshi'?'KLSH':strikeMode==='auto'?'LIVE':'MANUAL'}</span>
                </div>
                <div className="flex items-center gap-1">
                  <IC.Crosshair className="w-4 h-4 text-indigo-400 hidden sm:block"/>
                  <input type="number"
                    value={targetMargin===0?'':targetMargin}
                    onChange={e=>{const v=Number(e.target.value);setTargetMargin(v);isManualStrikeRef.current=true;setStrikeMode('manual');setPendingStrike(null);setStrikeConfirmed(false);}}
                    onKeyDown={e=>{if(e.key==='Enter'&&targetMargin>0){isManualStrikeRef.current=true;setStrikeMode('manual');setPendingStrike(null);setStrikeConfirmed(true);e.target.blur();}}}
                    onBlur={()=>{}}
                    className={'bg-transparent text-white font-serif text-base sm:text-lg w-full focus:outline-none border-b border-[#24242E] focus:border-indigo-400'}
                    placeholder="Auto-set"
                  />
                  {targetMargin>0&&strikeMode==='manual'&&(
                    <button
                      onClick={()=>{isManualStrikeRef.current=true;setStrikeMode('manual');setPendingStrike(null);setStrikeConfirmed(true);}}
                      className="shrink-0 px-2 py-0.5 rounded-lg text-[10px] font-bold uppercase tracking-wide bg-indigo-500 hover:bg-indigo-400 text-white transition-colors">OK ✓</button>
                  )}
                </div>
                {strikeSource!=='kalshi'&&kalshiDebug.ok!==null&&(
                  <div className="text-[9px] text-[#EDEDED]/35 mt-1 font-mono">
                    {kalshiDebug.ok===false?(
                      <span className="text-rose-400/70">Kalshi: {kalshiDebug.reason}</span>
                    ):kalshiDebug.bestStrike?(
                      <span className="text-emerald-400/60">Kalshi {kalshiDebug.bestStrike} from {kalshiDebug.bestTicker?.slice(-20)||'?'}</span>
                    ):(
                      <span className="text-amber-400/60">Kalshi: {kalshiDebug.totalMarkets} mkts · {kalshiDebug.matchingClose} matching · no strike → fields: {(kalshiDebug.sampleFields||[]).slice(0,4).join(' ')}</span>
                    )}
                  </div>
                )}
              </div>
            </div>
            {/* V13.4.200: Bet/Max Win + Live Offer inputs REMOVED from the strike
                bar at user request. Consequence, recorded because it is not
                obvious: betAmount/maxPayout fed recordPnL (which early-returns
                on bet<=0), the Kelly sizing calc, and offer/peak tracking, so
                lifetime P&L via that path no longer accumulates. The state and
                every consumer are left intact, so re-adding an input anywhere
                (e.g. Settings) restores it with no other change. */}
            <div className={'w-px h-8 bg-[#EDEDED]/10 hidden lg:block lg:ml-auto'}></div>

            {/* Position / Score */}
            <div className="col-span-2 lg:col-span-none lg:ml-auto flex flex-col">
              {positionStatus?(
                <div className={'bg-[#050508] border border-amber-500/20 rounded-lg p-1.5'}>
                  <div className="flex justify-between text-xs mb-1"><span className={'text-[#EDEDED]/40 uppercase tracking-wide'}>POSITION</span><span className={positionStatus.side==='UP'?'text-emerald-400 font-bold':'text-rose-400 font-bold'}>{positionStatus.side} @ ${(positionStatus.entry||0).toFixed(0)}</span></div>
                  <div className="flex justify-between items-center">
                    <span className={`text-lg font-serif font-bold ${positionStatus.pnlPct>0?'text-emerald-400':'text-rose-400'}`}>{positionStatus.pnlPct>0?'+':''}{positionStatus.pnlPct.toFixed(1)}%</span>
                    <span className={`text-xs font-bold uppercase ${positionStatus.isStopHit?'text-rose-500 animate-pulse':'text-[#EDEDED]/30'}`}>{positionStatus.isStopHit?'STOP HIT':'SAFE'}</span>
                  </div>
                </div>
              ):null /* V9.19.3: Personal Scorecard UI removed per user — manual W/L tracking deprecated in favor of taraCallLog-derived stats (V9.19.2 WR pill + Predictor P&L strip). Underlying `scorecards` state + localStorage `taraPersonalScorecards_v1` preserved so loss-streak cooldown and Discord broadcasts keep working. */}
            </div>
          </div>

          {/* V9.1.5: Upgraded compact DOM strip — same compact form as V9.1.2 but
              with quality dots, dominant percentage prominent, and a 4-cell band
              breakdown beneath the main bar (TIGHT/CLOSE/STD/WIDE).
              Reads orderBook.bands populated by the level-2 fetch. */}
          {(()=>{
            const _bands=orderBook?.bands||null;
            const _hlBids=Number(_bands?.std?.b)||orderBook.localBuy*currentPrice||0;
            const _hlAsks=Number(_bands?.std?.a)||orderBook.localSell*currentPrice||0;
            const _hlTotal=_hlBids+_hlAsks;
            const _hlBidPct=_hlTotal>0?(_hlBids/_hlTotal)*100:50;
            const _hlAskPct=100-_hlBidPct;
            const _domDominantPct=Math.max(_hlBidPct,_hlAskPct);
            const _MIN_DOM=50000;
            const _aboveFloor=_hlTotal>=_MIN_DOM;
            // Quality: do STD and WIDE bands agree at >=58% strength?
            let _qLevel='thin';
            if(_bands){
              const _wideTot=(Number(_bands.wide?.b)||0)+(Number(_bands.wide?.a)||0);
              if(_aboveFloor&&_wideTot>=_MIN_DOM){
                const _stdSide=_hlBidPct>=50?'BID':'ASK';
                const _stdStr=Math.max(_hlBidPct,100-_hlBidPct);
                const _widePct=_wideTot>0?((Number(_bands.wide?.b)||0)/_wideTot)*100:50;
                const _wideSide=_widePct>=50?'BID':'ASK';
                const _wideStr=Math.max(_widePct,100-_widePct);
                const _agree=_stdSide===_wideSide;
                let _score=0;
                if(_agree)_score+=1;
                if(_stdStr>=58)_score+=1;
                if(_wideStr>=58)_score+=1;
                if(_wideTot>=500000)_score+=1;
                _qLevel=_score>=3?'high':_score>=2?'medium':'low';
              }
            }
            const _qColor=_qLevel==='high'?'#6FA98C':_qLevel==='medium'?'#23B981':_qLevel==='low'?'rgba(232,69,94,0.72)':null;
            const _qLabel=_qLevel==='high'?'STRONG':_qLevel==='medium'?'MIXED':_qLevel==='low'?'WEAK':null;
            const _renderCell=(name,band,floor=_MIN_DOM)=>{
              const _b=Number(band?.b)||0,_a=Number(band?.a)||0;
              const _t=_b+_a;
              const _af=_t>=floor;
              const _p=_t>0?(_b/_t)*100:50;
              const _isBid=_p>=50;
              const _color=!_af?'rgba(237,237,237,0.30)':_isBid?'#6FA98C':'rgba(232,69,94,0.72)';
              const _disp=Math.max(_p,100-_p);
              return React.createElement('div',{key:name,className:'text-center'},
                React.createElement('div',{className:'text-[9px] uppercase tracking-wider text-[#EDEDED]/50 font-medium leading-tight'},name),
                React.createElement('div',{className:'text-[10px] font-bold tabular-nums leading-tight',style:{color:_color}},_af?_disp.toFixed(0)+'%':'—')
              );
            };
            return(
              <div className="px-3 pb-2 block">
                <div className="flex items-baseline justify-between mb-1 gap-2 min-w-0">
                  <div className="flex items-baseline gap-2 min-w-0">
                    <span className="text-xs text-[#EDEDED]/50 uppercase tracking-wide whitespace-nowrap">Depth of Market</span>
                    {_qColor&&React.createElement('span',{className:'inline-flex items-center gap-1'},
                      React.createElement('span',{className:'inline-flex gap-0.5'},
                        [0,1,2].map(i=>React.createElement('span',{
                          key:i,className:'inline-block w-1 h-1 rounded-full',
                          style:{background:(_qLevel==='high'||(_qLevel==='medium'&&i<2)||(_qLevel==='low'&&i<1))?_qColor:'rgba(237,237,237,0.18)'},
                        }))
                      ),
                      React.createElement('span',{className:'text-[8px] uppercase tracking-[0.16em] font-bold',style:{color:_qColor}},_qLabel)
                    )}
                  </div>
                  <span className="text-xs text-[#EDEDED]/40 tabular-nums">
                    <span className="text-emerald-400/85 font-bold">{_hlBidPct.toFixed(0)}%</span>
                    <span className="text-[#EDEDED]/25 mx-1">BID /</span>
                    <span className="text-rose-400/85 font-bold">{_hlAskPct.toFixed(0)}%</span>
                    <span className="text-[#EDEDED]/25 ml-1">ASK</span>
                  </span>
                </div>
                <div className="w-full h-1 bg-[#050508] rounded-full overflow-hidden flex">
                  <div style={{width:`${_hlBidPct}%`}} className="h-full bg-emerald-500/70 transition-all duration-300"></div>
                  <div style={{width:`${_hlAskPct}%`}} className="h-full bg-rose-500/70 transition-all duration-300"></div>
                </div>
                {_bands&&(
                  <div className="grid grid-cols-4 gap-2 mt-1">
                    {_renderCell('TIGHT',_bands.tight)}
                    {_renderCell('CLOSE',_bands.close)}
                    {_renderCell('STD',_bands.std)}
                    {_renderCell('WIDE',_bands.wide)}
                  </div>
                )}
              </div>
            );
          })()}
          {/* V13.4.303 REMOVED: the "this window vs strike" chart. Direct
              request -- "take off the graph too, its making my pc lag and
              taking up too much resources". The SVG polyline redraws on every
              live tick, which is exactly the kind of per-frame layout+paint
              work that adds up on a lower-powered machine. WindowStrikeChart
              had no other call site (v301 removed the ThisTradeCard duplicate),
              so the component definition is deleted too rather than left dead. */}
        </div>

        {/* V13.4.293: Smart Money, relocated here from column 1 (it used to
            render bare inside the giant prediction card). SmartMoneyStrip itself
            has no panel chrome of its own -- it expects a parent panel, which this
            wrapper now provides, matching every other center-column card. */}
        <div className="bg-[#0A0A0E] p-3 rounded-[10px] border border-[#1B1B22] shrink-0">
          <SmartMoneyStrip signals={_smartMoneySignals} lockDir={lockedCallRef.current?.dir||null}/>
        </div>

        {/* V13.4.293: Tape Flow, split out of the old combined stats-bar div
            into its own panel -- the mockup shows it as a separate card, not
            merged with price/strike/depth. */}
        <div className={'bg-[#0A0A0E] rounded-[10px] border border-[#1B1B22] relative overflow-hidden shrink-0'}>
          {/* V9.1.5: Upgraded compact Tape Flow strip — same compact form as V9.1.2
              but with quality dots, dominant percentage prominent, and a 4-cell time
              breakdown beneath the main bar (5s/15s/30s/60s). */}
          {tapeWindows&&(()=>{
            const _w5=tapeWindows.w5||{},_w15=tapeWindows.w15||{},_w30=tapeWindows.w30||{},_w60=tapeWindows.w60||{};
            const _tBuy=_w30.buys||0,_tSell=_w30.sells||0;
            const _tTotal=_tBuy+_tSell;
            if(_tTotal<=0)return null;
            const _tBuyPct=(_tBuy/_tTotal)*100;
            const _tSellPct=100-_tBuyPct;
            const _hlAboveFloor=_tTotal>=TAPE_FLOORS.w30;
            const _fmt=(n)=>n>=1e6?'$'+(n/1e6).toFixed(1)+'M':n>=1e3?'$'+(n/1e3).toFixed(0)+'K':'$'+Math.round(n);
            // Quality: do 30s + 60s agree directionally with strength + meaningful volume?
            let _qLevel='thin';
            const _w60Tot=(_w60.buys||0)+(_w60.sells||0);
            if(_hlAboveFloor&&_w60Tot>=TAPE_FLOORS.w60){
              const _w30Pct=_w30.buyPct!=null?_w30.buyPct:_tBuyPct;
              const _w60Pct=_w60.buyPct!=null?_w60.buyPct:50;
              const _w30Side=_w30Pct>=50?'BUY':'SELL';
              const _w60Side=_w60Pct>=50?'BUY':'SELL';
              const _w30Str=Math.max(_w30Pct,100-_w30Pct);
              const _w60Str=Math.max(_w60Pct,100-_w60Pct);
              let _score=0;
              if(_w30Side===_w60Side)_score+=1;
              if(_w30Str>=60)_score+=1;
              if(_w60Str>=60)_score+=1;
              if(_w60Tot>=500000)_score+=1;
              _qLevel=_score>=3?'high':_score>=2?'medium':'low';
            }
            const _qColor=_qLevel==='high'?'#6FA98C':_qLevel==='medium'?'#23B981':_qLevel==='low'?'rgba(232,69,94,0.72)':null;
            const _qLabel=_qLevel==='high'?'STRONG':_qLevel==='medium'?'MIXED':_qLevel==='low'?'WEAK':null;
            const _renderTapeCell=(name,w,floor)=>{
              const _t=(w.buys||0)+(w.sells||0);
              const _af=_t>=floor;
              const _p=w.buyPct!=null?w.buyPct:50;
              const _isBuy=_p>=50;
              const _color=!_af?'rgba(237,237,237,0.30)':_isBuy?'#6FA98C':'rgba(232,69,94,0.72)';
              const _disp=Math.max(_p,100-_p);
              return React.createElement('div',{key:name,className:'text-center'},
                React.createElement('div',{className:'text-[9px] uppercase tracking-wider text-[#EDEDED]/50 font-medium leading-tight'},name),
                React.createElement('div',{className:'text-[10px] font-bold tabular-nums leading-tight',style:{color:_color}},_af?_disp.toFixed(0)+'%':'—')
              );
            };
            const _domDominantPct=Math.max(_tBuyPct,_tSellPct);
            return(
              <div className="px-3 pb-2 block">
                <div className="flex items-baseline justify-between mb-1 gap-2 min-w-0">
                  <div className="flex items-baseline gap-2 min-w-0">
                    <span className="text-xs text-[#EDEDED]/50 uppercase tracking-wide whitespace-nowrap">Tape Flow · 30s</span>
                    {_qColor&&React.createElement('span',{className:'inline-flex items-center gap-1'},
                      React.createElement('span',{className:'inline-flex gap-0.5'},
                        [0,1,2].map(i=>React.createElement('span',{
                          key:i,className:'inline-block w-1 h-1 rounded-full',
                          style:{background:(_qLevel==='high'||(_qLevel==='medium'&&i<2)||(_qLevel==='low'&&i<1))?_qColor:'rgba(237,237,237,0.18)'},
                        }))
                      ),
                      React.createElement('span',{className:'text-[8px] uppercase tracking-[0.16em] font-bold',style:{color:_qColor}},_qLabel)
                    )}
                  </div>
                  <span className="text-xs text-[#EDEDED]/40 tabular-nums">
                    <span className="text-emerald-400/85 font-bold">{_fmt(_tBuy)}</span>
                    <span className="text-[#EDEDED]/25 mx-1">BUY /</span>
                    <span className="text-rose-400/85 font-bold">{_fmt(_tSell)}</span>
                    <span className="text-[#EDEDED]/25 ml-1">SELL</span>
                  </span>
                </div>
                <div className="w-full h-1 bg-[#050508] rounded-full overflow-hidden flex">
                  <div style={{width:`${_tBuyPct}%`}} className="h-full bg-emerald-500/70 transition-all duration-300"></div>
                  <div style={{width:`${_tSellPct}%`}} className="h-full bg-rose-500/70 transition-all duration-300"></div>
                </div>
                <div className="grid grid-cols-4 gap-2 mt-1">
                  {_renderTapeCell('5S',_w5,TAPE_FLOORS.w5)}
                  {_renderTapeCell('15S',_w15,TAPE_FLOORS.w15)}
                  {_renderTapeCell('30S',_w30,TAPE_FLOORS.w30)}
                  {_renderTapeCell('60S',_w60,TAPE_FLOORS.w60)}
                </div>
              </div>
            );
          })()}
        </div>

        {/* V9.10.2: Unified Today card */}
        <UnifiedTodayCard
          todayData={todayData}
          bestWindowsToday={bestWindowsToday}
          tickHistoryRef={tickHistoryRef}
          upcomingMacro={getUpcomingMacroEvents(new Date(),24)}
          timeFormat={timeFormat}
          settings={tradingSettings}
        />

          <ProjectionsCard hourlyContext={{engine:_hourlyEngine}} analysis={analysis} mobileTab={mobileTab} taraCall={taraCall} taraScorecards={taraScorecards} taraCallLog={displayedCallLog} windowType={windowType} timeState={timeState} taraLearnings={taraLearnings} kalshiYesPrice={kalshiYesPrice} useLocalTime={useLocalTime} timeFormat={timeFormat} convictionTrajectory={convictionTrajectory} todayData={todayData} movementRisk={movementRisk} bestWindowsToday={bestWindowsToday} handleManualSync={handleManualSync} userPosition={userPosition} tapeWindows={tapeWindows} whaleLog={whaleLog} orderBook={orderBook} targetMargin={targetMargin} reversalRisk={lockedCallRef.current?.reversalRisk||null} onHourlyLock={_onHourlyLock} onSoftHint={()=>{softHintRef.current=Date.now();setForceRender(p=>p+1);}} onHardForce={()=>{hardForceRef.current=Date.now();setForceRender(p=>p+1);}} onEditEntry={(entryId,newValue,field)=>{
            // V9.9.3: dual-axis edit. field === 'direction' edits e.dir, 'result' edits e.result.
            //   Default field is 'result' for backward compat. Both axes mark manualEdit + timestamp.
            //   Direction edit recomputes the result automatically if we have closing price + strike,
            //   so user doesn't have to fix two things separately when the underlying issue is one.
            const _field=field||'result';
            setTaraCallLog(prev=>{
              const next=prev.map(e=>{
                if(e.id!==entryId)return e;
                // V10.7.89: fullUpdate path — apply all fields from reconcile
                if(_field==='__fullUpdate__'&&newValue&&typeof newValue==='object'){
                  return{...e,...newValue,manualEdit:true,manualEditedAt:Date.now()};
                }
                const _valUp=String(newValue||'').toUpperCase();
                if(_field==='direction'){
                  if(_valUp!=='UP'&&_valUp!=='DOWN'&&_valUp!=='SIT_OUT')return e;
                  // Auto-recompute result if we have closing+strike data
                  let _autoResult=e.result;
                  if(_valUp==='SIT_OUT')_autoResult='SITOUT';
                  else if(typeof e.closingPrice==='number'&&typeof e.strike==='number'&&e.strike>0){
                    const _wonByPrice=_valUp==='UP'?(e.closingPrice>=e.strike):(e.closingPrice<e.strike);
                    _autoResult=_wonByPrice?'WIN':'LOSS';
                  }
                  return{...e,dir:_valUp,result:_autoResult,manualEdit:true,manualEditedAt:Date.now()};
                }else{
                  // result edit
                  const valid=_valUp==='WIN'||_valUp==='LOSS'||_valUp==='SITOUT';
                  if(!valid)return e;
                  return{...e,result:_valUp,manualEdit:true,manualEditedAt:Date.now()};
                }
              });
              setTimeout(()=>_recomputeLearningsFromLog(next),0);
              return next;
            });
          }} onDeleteEntry={(entryId)=>{
            // V10.7.43: hard delete from log. Used by "Clear All Unverifiable" in Storage Audit.
            setTaraCallLog(prev=>{
              const next=prev.filter(e=>e.id!==entryId);
              setTimeout(()=>_recomputeLearningsFromLog(next),0);
              return next;
            });
          }}/>

          {/* V13.4.294: News + Live Feeds moved to column 3, matching the
              mockup's right column (Record -> Risk -> Live Feeds -> Memory ->
              News). See project_tara_dashboard_mockup_rebuild memory. */}
          {/* V13.4.276: schedule moved here from the prediction column. Its own
              comment above records the original ask -- "put schedule in the news
              place" -- and it had drifted into column 1 instead, where it was 455px
              of the 2,275px that made that column the tallest and left this one with
              1,420px of dead space. It is reference material, not live trade state,
              so it belongs beside the news rather than under the auto-exec controls. */}
            {/* V9.2.0: Schedule relocated from RightPanel to projections column.
                User feedback: "put schedule in the news place." */}
            {scheduleModalMain&&taraCallLog&&(
              <TradeScheduleModal
                taraCallLog={taraCallLog}
                currentAsset={currentAsset}
                timeFormat={timeFormat}
                onClose={()=>setScheduleModalMain(false)}
              />
            )}
          </div>{/* /Tara's Call column */}

          {/* ── V111: RIGHT PANEL - Engine Log (col 3) ──
              V13.4.294: wrapped so Record/News+LiveFeeds/Memory can sit above it,
              matching the mockup's right column. RightPanel's own content
              (Hourly Ladder, Trade Coach, Score Breakdown, Engine Log) isn't in
              the mockup as such but is kept -- same "keep extra, don't delete"
              call as pass 1 made for MissionPanel. See
              project_tara_dashboard_mockup_rebuild memory for what's still not
              matched here (Risk banner not built yet; Hourly Ladder belongs in
              its own bottom row per the mockup, not column 3 -- deferred). */}
           <div id="tara-market-surface" className="tara-market-surface">{(workspaceFocus!=='overview'||expandedOverview)&&<ChartBottomCard resolution={resolution} setResolution={setResolution} asset={currentAsset} priceSource={priceSource}/>}</div>
           <div className="flex flex-col gap-3 min-w-0" id="tara-context-surface">
          {/* V13.4.298: this whole block (Record/Risk/News+LiveFeeds/Memory) is
              new to column 3 as of the dashboard-mockup-rebuild passes, and none
              of it had mobile-visibility gating -- it sat in a plain,
              always-rendered div, so at mobile widths (grid-cols-1, everything
              stacks vertically) it showed up a SECOND time alongside
              TaraCallCard's own mobile-bundled Record/Memory (desktopSplit is
              never set on that call site, by design, so it still renders its
              own copies). hidden lg:block restricts this block to desktop,
              where it's genuinely additive, restoring what every commit message
              for these passes already claimed: mobile is unaffected. */}
          <div className="tara-context-stack flex flex-col gap-3 min-w-0">
          <div className="tara-risk-stack"><RiskBannerCard movementRisk={movementRisk}/></div>
          {/* V13.4.294: News + Live Feeds, relocated here from column 2. */}
          <div className={'tara-news-stack bg-[#0A0A0E] p-3 sm:p-4 rounded-[10px] border border-[#1B1B22] flex flex-col gap-3 relative min-w-0'}>
            <T2Stamp code="FEED · 016"/>
            <MarketIntelligencePanel calendar={economicCalendar} derivatives={bloomberg} reference={brtiApprox} quote={_kalshiQuote} spotSource={lastPriceSourceRef.current}/>
            <NewsFeedCard timeFormat={timeFormat} pushToast={pushToast} calendar={economicCalendar}/>
            <div className="pt-3" style={{borderTop:'1px solid '+T2_GOLD_GLOW}}>
              <LiveFeedsCard tapeRef={tapeRef} bloomberg={bloomberg} whaleLog={whaleLog} timeFormat={timeFormat}/>
            </div>
          </div>
          {/* V13.4.294: Memory, relocated here from inside TaraCallCard (which
              skips it on desktop now -- desktopSplit). Same onEditEntry/
              onDeleteEntry logic as the ProjectionsCard call site above --
              duplicated rather than hoisted to a shared handler, matching this
              file's existing convention of per-call-site inline edit/delete
              closures (the mobile TaraCallCard call site has its own copy too). */}
          <div className="tara-memory-stack">{workspaceFocus==='memory'&&<TaraStorageStatus status={callStorageStatus}/>}<TaraMemoryStrip fullPage={workspaceFocus==='memory'} taraCallLog={displayedCallLog||[]} windowType={windowType} taraLearnings={taraLearnings} useLocalTime={useLocalTime} timeFormat={timeFormat} onEditEntry={(entryId,newValue,field)=>{
            const _field=field||'result';
            setTaraCallLog(prev=>{
              const next=prev.map(e=>{
                if(e.id!==entryId)return e;
                if(_field==='__fullUpdate__'&&newValue&&typeof newValue==='object'){
                  return{...e,...newValue,manualEdit:true,manualEditedAt:Date.now()};
                }
                const _valUp=String(newValue||'').toUpperCase();
                if(_field==='direction'){
                  if(_valUp!=='UP'&&_valUp!=='DOWN'&&_valUp!=='SIT_OUT')return e;
                  let _autoResult=e.result;
                  if(_valUp==='SIT_OUT')_autoResult='SITOUT';
                  else if(typeof e.closingPrice==='number'&&typeof e.strike==='number'&&e.strike>0){
                    const _wonByPrice=_valUp==='UP'?(e.closingPrice>=e.strike):(e.closingPrice<e.strike);
                    _autoResult=_wonByPrice?'WIN':'LOSS';
                  }
                  return{...e,dir:_valUp,result:_autoResult,manualEdit:true,manualEditedAt:Date.now()};
                }else{
                  const valid=_valUp==='WIN'||_valUp==='LOSS'||_valUp==='SITOUT';
                  if(!valid)return e;
                  return{...e,result:_valUp,manualEdit:true,manualEditedAt:Date.now()};
                }
              });
              setTimeout(()=>_recomputeLearningsFromLog(next),0);
              return next;
            });
          }} onDeleteEntry={(entryId)=>{
            setTaraCallLog(prev=>{
              const next=prev.filter(e=>e.id!==entryId);
              setTimeout(()=>_recomputeLearningsFromLog(next),0);
              return next;
            });
          }}/></div>
          </div>
          <RightPanel analysis={analysis} tapeRef={tapeRef} whaleLog={whaleLog} bloomberg={bloomberg} currentPrice={currentPrice} mobileTab={mobileTab} taraCallLog={taraCallLog} currentAsset={currentAsset} timeFormat={timeFormat} pushToast={pushToast}
            taraCall={taraCall} lockedSnapshotDir={lockedCallRef.current?.dir||null} lockedSnapshot={_activeCallSnapshot} kalshiYesPrice={kalshiYesPrice} timeState={timeState} windowType={windowType} userPosition={userPosition}
            onHourlyLock={_onHourlyLock}/>
          </div>
        </div>

        </TaraDetailEnvelope>
        {/* V13.4.295: HOURLY + SCHEDULE row -- matches the mockup's row below
            the main 3-column grid. HourlyLadderPanel moved here from inside
            RightPanel (column 3); ScheduleBySessionCard is new. lg:grid-cols-2
            so both are visible together on desktop; single column on mobile,
            same convention as the main grid above. */}
        <div className="tara-hourly-row grid grid-cols-1 lg:grid-cols-2 gap-3 shrink-0 min-w-0" id="tara-schedule-surface">
          {!_hourlyOnLeft&&<HourlyLadderPanel engine={_hourlyEngine}/>}
          <TaraDetailEnvelope enabled={workspaceFocus==='schedule'} label="Hourly schedule & session performance">
          <ScheduleBySessionCard taraCallLog={taraCallLog}/>
          <div className="tara-trade-schedule min-w-0">
            <DayAwareScheduleHeader dayContext={dayContext}/>
            <TradeScheduleStrip taraCallLog={taraCallLog} currentAsset={currentAsset} timeFormat={timeFormat} onOpenFullSchedule={()=>setScheduleModalMain(true)}/>
          </div>
          </TaraDetailEnvelope>
        </div>

        {/* ── V111: TRADINGVIEW CHART (full-width bottom row) ── */}
        </div>
        {(workspaceFocus==='execution'||(workspaceFocus==='overview'&&expandedOverview))&&<TaraApprovedRail
          autoExecSettings={autoExecSettings}
          mission={mission}
          killSwitchEngaged={killSwitchEngaged}
          setShowTradingSettings={setShowTradingSettings}
          movementRisk={movementRisk}
          userPosition={userPosition}
          positionReconciliation={positionReconciliation}
          autoOrderState={autoOrderState}
          manualKalshiEntry={manualKalshiEntry}
          activeTicker={kalshiActiveMarket?.ticker||null}
          taraScorecards={taraScorecards}
          windowType={windowType}
          compact={workspaceFocus==='overview'}
        />}
        </div>

      {/* ── FLOW INTELLIGENCE PANEL ── */}
      <FlowPanel showWhaleLog={showWhaleLog} setShowWhaleLog={setShowWhaleLog} flowSignal={flowSignal} tapeRef={tapeRef} whaleLog={whaleLog} bloomberg={bloomberg} currentPrice={currentPrice} timeState={timeState} timeFormat={timeFormat}/>
      <TheoryLabPanel show={showTheoryLab} setShow={setShowTheoryLab} theoryStats={theoryStats} theoryLog={theoryLog}/>

      {/* Settings */}
      {showSettings&&(
        <div className={'fixed inset-0 z-[100] bg-black/80 backdrop-blur-sm flex items-center justify-center p-2 sm:p-4'}>
          <div className={'bg-[#0A0A0E] border border-[#1B1B22] rounded-[10px] w-full max-w-lg shadow-2xl max-h-[92vh] overflow-y-auto'} style={{scrollbarWidth:'thin'}}>
            <div className="p-4 sm:p-6">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-base sm:text-lg font-serif text-white flex items-center gap-2"><IC.Link className="w-5 h-5 text-indigo-400"/>Discord Integration</h2>
                <button onClick={()=>{setShowSettings(false);setDiscordStatusMsg('');}} className={'text-[#EDEDED]/50 hover:text-white'}><IC.X className="w-5 h-5"/></button>
              </div>

              <p className={'text-xs text-[#EDEDED]/60 mb-3 leading-relaxed'}>Confirmed BTC 15m and hourly Calls lead the feed. Useful coach/whale context is limited to two notes per 15-minute window, at least five minutes apart, with stable duplicate suppression. Results and recent rates edit the original Call quietly. Trading stays paused.</p>

              {/* V7.0.1: Per-asset webhooks — strict, no fallback */}
              <div className="space-y-2 mb-4">
                {['BTC'].map(k=>{
                  const _c=ASSET_CONFIG[k];
                  const _val=discordWebhooks[k]||'';
                  return(
                    <div key={k} className="flex items-stretch gap-2">
                      <div className="flex items-center justify-center px-3 rounded-lg shrink-0 font-bold text-sm border" style={{background:_c.color+'15',color:_c.color,borderColor:_c.color+'44',minWidth:64}}>
                        <span className="mr-1.5">{_c.icon}</span>
                        <span className="text-[10px] uppercase tracking-wider">{_c.label}</span>
                      </div>
                      <input
                        type="password"
                        value={_val}
                        onChange={e=>setDiscordWebhooks(prev=>({...prev,[k]:e.target.value}))}
                        placeholder={_val?'':'Optional · empty = no broadcasts for this asset'}
                        className={'flex-1 bg-[#050508] border border-[#2A2A34] rounded-lg px-3 py-2.5 text-xs focus:outline-none focus:border-indigo-400 text-white font-mono'}
                        style={_val?{borderColor:_c.color+'66'}:{}}
                      />
                    </div>
                  );
                })}
              </div>

              {/* Bot name + avatar */}
              <div className="grid grid-cols-2 gap-2 mb-3">
                <div>
                  <label className={'text-[10px] text-[#EDEDED]/40 uppercase tracking-wide mb-1 block'}>Bot Display Name</label>
                  <input type="text" value={discordUsername} onChange={e=>setDiscordUsername(e.target.value)} placeholder="Tara Terminal V110" className={'w-full bg-[#050508] border border-[#2A2A34] rounded-lg px-3 py-2 text-xs focus:outline-none focus:border-indigo-400 text-white'}/>
                </div>
                <div>
                  <label className={'text-[10px] text-[#EDEDED]/40 uppercase tracking-wide mb-1 block'}>Avatar Image URL</label>
                  <input type="url" value={discordAvatar} onChange={e=>setDiscordAvatar(e.target.value)} placeholder="https://i.imgur.com/..." className={'w-full bg-[#050508] border border-[#2A2A34] rounded-lg px-3 py-2 text-xs focus:outline-none focus:border-indigo-400 text-white'}/>
                </div>
              </div>
              {discordAvatar&&(
                <div className={'flex items-center gap-2 mb-3 p-2 bg-[#050508] rounded-lg border border-[#24242E]'}>
                  <img src={discordAvatar} alt="Bot avatar preview" className={'w-8 h-8 rounded-full object-cover border border-[#2A2A34]'} onError={e=>e.target.style.display='none'}/>
                  <div>
                    <div className="text-xs font-bold text-white">{discordUsername||'Tara Terminal V110'}</div>
                    <div className={'text-[10px] text-[#EDEDED]/40'}>Preview of how bot appears in Discord</div>
                  </div>
                </div>
              )}
              <div className="flex gap-2 mb-5">
                <button onClick={()=>setShowSettings(false)} className="flex-1 bg-indigo-500 hover:bg-indigo-400 text-white font-bold py-2 rounded-lg text-xs uppercase tracking-wide transition-colors">Save</button>
                <button onClick={async()=>{
                  const destination=discordDestination(discordWebhook);
                  if(!destination){setDiscordStatusMsg('BTC webhook missing or invalid — no message sent.');return;}
                  try{await verifyDiscordDestination(destination);setDiscordStatusMsg('Existing tara-btc connection verified — no test message sent.');}
                  catch{setDiscordStatusMsg('Connection not verified — check the existing tara-btc webhook. No message sent.');}
                  setTimeout(()=>setDiscordStatusMsg(''),3000);
                }} className={'px-4 py-2 border border-indigo-500/30 text-indigo-400 rounded-lg text-xs font-bold uppercase tracking-wide hover:bg-[#8C98A8]/10 transition-colors'}>Check connection</button>
              </div>

              {/* V111: Sync to baseline training data */}
              <div className={'mb-3 p-3 rounded-lg bg-[#050508] border border-[#24242E]'}>
                <div className={'text-[11px] text-[#EDEDED]/70 mb-2 leading-relaxed'}>
                  <strong className={'text-emerald-400'}>Sync to Latest Training</strong> · Refreshes Tara to the latest baked baseline (487W-302L · 33W-25L 5m · 57 trades trained · V3.1). Use when switching devices.
                </div>
                <button onClick={()=>{
                  if(window.confirm('Reset Tara to the latest baseline training data? Adaptive weights and trade history reset.')){
                    runSyncWithProgress();
                  }
                }} disabled={syncState.active} className={'w-full px-3 py-2 bg-emerald-500/10 border border-emerald-500/30 text-emerald-400 rounded-lg text-xs font-bold uppercase tracking-wide hover:bg-emerald-500/20 disabled:opacity-50 disabled:cursor-not-allowed'}>
                  {syncState.active?'Syncing...':'Sync to Latest Baseline'}
                </button>
              </div>

              {discordStatusMsg&&<div className={'mb-3 text-xs text-center text-emerald-400 bg-emerald-500/10 border border-emerald-500/20 rounded-lg py-2'}>{discordStatusMsg}</div>}

              <DiscordDeliveryPanel delivery={btcDiscordDelivery} details/>
            </div>
          </div>
        </div>
      )}

      {/* Chat */}
      <div className={`fixed ${showSports||showWeather?'bottom-4':'bottom-[68px]'} lg:bottom-4 right-4 z-50 flex flex-col items-end transition-all ${isChatOpen?'w-[90vw] sm:w-80':'w-auto'}`}>
        {isChatOpen&&(
          <div className={'bg-[#101014] border border-[#2A2A34] shadow-2xl rounded-xl w-full mb-3 overflow-hidden flex flex-col h-[55vh] sm:h-96'}>
            <div className={'bg-[#050508] p-2.5 flex justify-between items-center border-b border-[#24242E]'}><span className="text-xs font-bold uppercase tracking-wide flex items-center gap-2"><IC.Msg className="w-3.5 h-3.5 text-indigo-400"/>Chat with {TARA_VERSION_DISPLAY}</span><button onClick={()=>setIsChatOpen(false)} className="opacity-50 hover:opacity-100"><IC.X className="w-4 h-4"/></button></div>
            <div className={'flex-1 overflow-y-auto p-3 space-y-3 bg-[#161616]'} style={{scrollbarWidth:'thin'}}>
              {chatLog.map((msg,i)=>(
                <div key={i} className={`flex flex-col ${msg.role==='user'?'items-end':'items-start'}`}>
                  <span className={`text-xs uppercase opacity-30 mb-1 ${msg.role==='user'?'mr-1':'ml-1'}`}>{msg.role}</span>
                  <div className={`text-xs p-2 rounded-lg max-w-[88%] leading-relaxed whitespace-pre-wrap ${msg.role==='user'?'bg-indigo-500/20 text-indigo-100 border border-indigo-500/30 rounded-tr-none':'bg-[#2A2D2C] text-[#EDEDED] border border-[#24242E] rounded-tl-none'}`}>{msg.text}</div>
                </div>
              ))}
            </div>
            <div className={'p-2.5 bg-[#050508] border-t border-[#24242E]'}><input type="text" value={chatInput} onChange={e=>setChatInput(e.target.value)} onKeyDown={handleChatSubmit} placeholder={`Ask about ${windowType} window...`} className={'w-full bg-[#0A0A0E] border border-[#1B1B22] rounded-lg px-3 py-2 text-xs focus:outline-none focus:border-indigo-400 text-white'}/></div>
          </div>
        )}
        {!isChatOpen&&<button onClick={()=>setIsChatOpen(true)} className={'bg-indigo-500 hover:bg-indigo-400 text-white p-3 rounded-full shadow-lg border border-indigo-400/50 transition-transform hover:scale-105'}><IC.Msg className="w-5 h-5"/></button>}
      </div>

      {/* V9.2.1: MOBILE STICKY BOTTOM BAR — always visible on phones. Shows current
          asset, window type, Tara's call direction, and quick-access buttons.
          Hidden on desktop (lg:hidden). Fixed to bottom with safe-area padding. */}
      {!showSports&&!showWeather&&<div className="fixed bottom-0 left-0 right-0 z-40 lg:hidden safe-bottom" style={{background:'#050508',borderTop:'1px solid rgba(35,185,129,0.15)',backdropFilter:'blur(8px)'}}>
        <div className="flex items-center justify-between px-3 py-2 gap-2">
          {/* Asset + Window */}
          <div className="flex items-center gap-1.5">
            <span className="px-2 py-1.5 rounded-lg text-xs font-bold min-h-[36px] flex items-center" style={{background:'rgba(247,147,26,0.15)',color:'rgb(247,147,26)',border:'1px solid rgba(247,147,26,0.3)'}}>
              {'₿ BTC · 15m'}
            </span>
          </div>
          {/* Current call direction + price */}
          <div className="flex items-center gap-2 text-xs tabular-nums">
            {/* V13.4.288: this badge had exactly two visual states and picked between
                them with `call==='UP' ? UP : DN`. Every non-UP value therefore rendered
                as a DOWN call — so a committed SIT_OUT showed "▼ DN 62%" in rose, with a
                confidence attached, while TARA'S CALL and THIS TRADE both read SITTING
                OUT. This is the one element pinned on screen at all times on mobile, and
                a reader had no way to tell a sit-out from a short.
                Routed through readLockState: `tradeable` is the only thing that earns a
                direction arrow, a committed sit-out gets its own state, and anything
                uncommitted stays "scanning". */}
            {(()=>{
              const _ls=readLockState(taraCall?.snapshot);
              if(_ls.tradeable){
                const _up=_ls.dir==='UP';
                return(
                  <span className={'font-bold px-2 py-1 rounded-lg '+(_up?'bg-emerald-500/15 text-emerald-400 border border-emerald-500/30':'bg-rose-500/15 text-rose-400 border border-rose-500/30')}>
                    {_up?'▲ UP':'▼ DN'} {_ls.confidence>0?_ls.confidence+'%':''}
                  </span>
                );
              }
              if(_ls.satOut){
                // V13.4.290 CORRECTION: this shipped as v288 using bg-amber-500/12
                //   text-amber-400/90, Tailwind UTILITY classes. index.html:76-78 aliases
                //   the entire amber/orange/yellow family onto the WIN GREEN precisely so a
                //   stray utility class can never paint something sit-out -- amber-400 is
                //   byte-identical to emerald-400, #23B981. So this "fixed" badge rendered a
                //   green pill, same size and border as the LOCKED UP pill, for the majority
                //   state of the bar. Worse than the bug it replaced, which was at least a
                //   visibly different colour (rose). The real token, used inline (never as a
                //   class) everywhere else in this file, is T2_SITOUT/_FG/_BG.
                //   Also drops the "(leaned X)" suffix (MBR-2): it can add ~80px to a pill
                //   that already has none to spare on a 375px phone with no wrap protection,
                //   pushing the fixed bar to a second line. The lean is still shown, just not
                //   here -- TaraCallCard already prints "would lean X * N%" for it.
                return(
                  <span className="font-bold px-2 py-1 rounded-lg whitespace-nowrap"
                        style={{color:T2_SITOUT_FG,background:T2_SITOUT_BG,border:'1px solid rgba(212,160,58,0.30)'}}>
                    SIT OUT
                  </span>
                );
              }
              return <span className="text-[#EDEDED]/40 italic">scanning</span>;
            })()}
            <span className="text-white font-bold">${Number(currentPrice).toLocaleString(undefined,{maximumFractionDigits:0})}</span>
          </div>
          {/* Quick actions */}
          <div className="flex items-center gap-1">
            <button onClick={()=>setShowStats(true)} className="p-2 rounded-lg hover:bg-[#EDEDED]/5 text-[#EDEDED]/50 min-h-[36px]" title="Stats"><IC.BarChart className="w-4 h-4"/></button>
            <button onClick={()=>setSyncMenuOpen(true)} className="p-2 rounded-lg hover:bg-[#EDEDED]/5 text-[#EDEDED]/50 min-h-[36px]" title="Sync"><IC.Globe className="w-4 h-4"/></button>
          </div>
        </div>
      </div>}

      {/* Help */}
      {/* ── ANALYTICS / TRAINING DASHBOARD ── */}
      {showAnalytics&&(
        <div className={'fixed inset-0 z-[100] bg-black/85 backdrop-blur-sm flex items-center justify-center p-2 sm:p-4'}>
          <div className={'bg-[#0A0A0E] border border-[#1B1B22] rounded-[10px] w-full max-w-3xl max-h-[95vh] mx-2 sm:mx-0 overflow-y-auto shadow-2xl'} style={{scrollbarWidth:'thin'}}>
            <div className={'sticky top-0 bg-[#101014] border-b border-[#24242E] p-4 flex justify-between items-center z-10'}>
              <div>
                <h2 className="text-base font-serif text-white flex items-center gap-2"><IC.BarChart className="w-5 h-5 text-indigo-400"/>Tara Training Engine</h2>
                {(()=>{
                  const _resolvedTara=(taraCallLog||[]).filter(e=>e&&(e.result==='WIN'||e.result==='LOSS')&&(e.dir==='UP'||e.dir==='DOWN')).length;
                  const _sitouts=(taraCallLog||[]).filter(e=>e&&e.result==='SITOUT').length;
                  return (
                    <p className={'text-xs text-[#EDEDED]/40 mt-0.5'}>
                      {tradeLog.length} manual trades · <span className="text-amber-400/85">{_resolvedTara} Tara calls</span>{_sitouts>0&&<span className="text-[#EDEDED]/35"> · {_sitouts} sat out</span>} · Weights auto-update from Tara's resolved calls
                    </p>
                  );
                })()}
              </div>
              <div className="flex items-center gap-2">
                <button onClick={()=>{
                  // V152: Expanded export — adds V145 telemetry + V152 rangePosition.
                  // outcomeDir column = direction implied by close vs strike (does NOT use t.dir).
                  // dir = the direction Tara LOCKED, distinct from the outcome.
                  const headers='id,timestampISO,dir,outcomeDir,result,posterior,rawPosterior,regime,clockAtLock,hour,session,windowType,'+
                                'gap,momentum,structure,flow,technical,regime_s,rangePosition,'+
                                'entryPrice,closingPrice,strikeAtLock,strikePrice,gapAtEntry,closingGapBps,'+
                                'kalshiAtLock,kalshiAtClose,fgtAlignment,rangeBps,qualityScore,'+
                                'geoRisk,geoTopic,reversed,earlyExit,forceExit';
                  const csv=[headers].concat(
                    tradeLog.map(t=>{
                      // V152: Compute outcomeDir from close vs strike (independent of t.dir).
                      // This is the actual outcome direction, separate from Tara's call.
                      let outcomeDir='';
                      if(t.closingPrice!=null&&t.strikePrice!=null){
                        outcomeDir=t.closingPrice>=t.strikePrice?'UP':'DOWN';
                      }
                      const safe=(v)=>v==null?'':String(v).replace(/[",\n]/g,' ');
                      const num=(v,d=2)=>v==null||isNaN(v)?'':Number(v).toFixed(d);
                      return [
                        t.id,safe(t.timestampISO),t.dir||'',outcomeDir,t.result||'PENDING',
                        num(t.posterior,1),num(t.rawPosterior,1),safe(t.regime),
                        t.clockAtLock||0,t.hour||0,safe(t.session),safe(t.windowType),
                        num(t.signals?.gap),num(t.signals?.momentum),num(t.signals?.structure),
                        num(t.signals?.flow),num(t.signals?.technical),num(t.signals?.regime),num(t.signals?.rangePosition),
                        num(t.entryPrice,2),num(t.closingPrice,2),num(t.strikeAtLock,2),num(t.strikePrice,2),
                        num(t.gapAtEntry,1),num(t.closingGapBps,1),
                        num(t.kalshiAtLock,1),num(t.kalshiAtClose,1),
                        t.fgtAlignment||0,num(t.rangeBps,2),num(t.qualityScore,0),
                        num(t.geoRisk,2),safe(t.geoTopic||'').slice(0,80),
                        t.reversed?1:0,t.earlyExit?1:0,t.forceExit?1:0,
                      ].join(',');
                    })
                  ).join('\n');
                  const a=document.createElement('a');a.href='data:text/csv;charset=utf-8,'+encodeURIComponent(csv);a.download='tara_training_data.csv';a.click();
                }} className={'px-3 py-1 text-xs font-bold uppercase tracking-wide rounded-lg bg-indigo-500/20 border border-indigo-500/30 text-indigo-400 hover:bg-indigo-500/30 transition-colors'}>Export CSV</button>
                <button onClick={()=>{if(confirm('Reset all training data and weights? Cannot undo.')){setAdaptiveWeights({...DEFAULT_WEIGHTS});setTradeLog([]);saveWeights({...DEFAULT_WEIGHTS});saveTradeLog([]);pendingTradeRef.current=null;}}} className={'px-3 py-1 text-xs font-bold uppercase tracking-wide rounded-lg bg-rose-500/20 border border-rose-500/30 text-rose-400 hover:bg-rose-500/30 transition-colors'}>Reset</button>
                <button onClick={()=>{setShowAnalytics(false);setSelectedTradeId(null);}} className={'text-[#EDEDED]/50 hover:text-white'}><IC.X className="w-5 h-5"/></button>
              </div>
            </div>
            <div className="p-4 space-y-5">

              {/* V134: BACKTEST PANEL — replay all historical trades through current logic */}
              <section>
                <h3 className="text-xs font-bold uppercase tracking-wide text-emerald-400 mb-3">🔬 Backtest — Historical Performance Audit</h3>
                {(()=>{
                  const bt=runFullBacktest(tradeLog);
                  if(!bt.ready)return(<div className="text-xs text-[#EDEDED]/50 p-3 bg-[#050508] rounded-lg">{bt.note}</div>);
                  const fmt=p=>(p*100).toFixed(1)+'%';
                  return(<div className="space-y-3">
                    {/* Top metrics row */}
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                      <div className="bg-[#050508] rounded-lg p-2.5 border border-[#24242E]">
                        <div className="text-[10px] uppercase tracking-wide text-[#EDEDED]/40 font-bold">Overall WR</div>
                        <div className={'text-lg font-mono font-bold '+(bt.wr>=0.65?'text-emerald-400':bt.wr>=0.55?'text-amber-400':'text-rose-400')}>{fmt(bt.wr)}</div>
                        <div className="text-[10px] text-[#EDEDED]/30">{bt.n} trades</div>
                      </div>
                      <div className="bg-[#050508] rounded-lg p-2.5 border border-[#24242E]">
                        <div className="text-[10px] uppercase tracking-wide text-[#EDEDED]/40 font-bold">Brier Score</div>
                        <div className={'text-lg font-mono font-bold '+(bt.brier<0.15?'text-emerald-400':bt.brier<0.20?'text-amber-400':'text-rose-400')}>{bt.brier.toFixed(3)}</div>
                        <div className="text-[10px] text-[#EDEDED]/30">{bt.brier<0.10?'Excellent':bt.brier<0.15?'Strong':bt.brier<0.20?'Useful':bt.brier<0.24?'Marginal':'No skill'}</div>
                      </div>
                      <div className="bg-[#050508] rounded-lg p-2.5 border border-[#24242E]">
                        <div className="text-[10px] uppercase tracking-wide text-[#EDEDED]/40 font-bold">UP / DOWN</div>
                        <div className="text-sm font-mono">
                          <span className="text-emerald-400">{fmt(bt.upWR)}</span>
                          <span className="text-[#EDEDED]/30 mx-1">/</span>
                          <span className="text-rose-400">{fmt(bt.dnWR)}</span>
                        </div>
                        <div className="text-[10px] text-[#EDEDED]/30">direction split</div>
                      </div>
                      <div className="bg-[#050508] rounded-lg p-2.5 border border-[#24242E]">
                        <div className="text-[10px] uppercase tracking-wide text-[#EDEDED]/40 font-bold">Streaks</div>
                        <div className="text-sm font-mono">
                          <span className="text-emerald-400">{bt.maxWinStreak}W</span>
                          <span className="text-[#EDEDED]/30 mx-1">/</span>
                          <span className="text-rose-400">{bt.maxLossStreak}L</span>
                        </div>
                        <div className="text-[10px] text-[#EDEDED]/30">max consecutive</div>
                      </div>
                    </div>
                    {/* Filter scenarios — what WR if we'd applied different filters? */}
                    <div className="bg-[#050508] rounded-lg p-3 border border-[#24242E]">
                      <div className="text-[10px] uppercase tracking-wide text-[#EDEDED]/40 font-bold mb-2">Selectivity Scenarios — "What if I'd only taken these?"</div>
                      <div className="space-y-1.5">
                        {Object.entries(bt.filterScenarios).filter(([k,v])=>v).map(([name,s])=>{
                          const diff=s.wr-bt.wr;
                          const lift=diff*100;
                          return(<div key={name} className="flex items-center justify-between text-xs">
                            <span className="text-[#EDEDED]/70">{name}</span>
                            <div className="flex items-center gap-3">
                              <span className="text-[#EDEDED]/40 text-[10px] font-mono">{s.n}t</span>
                              <span className={'font-mono font-bold '+(s.wr>=0.70?'text-emerald-400':s.wr>=0.60?'text-amber-400':'text-rose-400')}>{fmt(s.wr)}</span>
                              <span className={'font-mono text-[10px] w-12 text-right '+(lift>=2?'text-emerald-400':lift<=-2?'text-rose-400':'text-[#EDEDED]/30')}>{lift>=0?'+':''}{lift.toFixed(1)}</span>
                            </div>
                          </div>);
                        })}
                      </div>
                    </div>
                    {/* Per-regime breakdown */}
                    <div className="bg-[#050508] rounded-lg p-3 border border-[#24242E]">
                      <div className="text-[10px] uppercase tracking-wide text-[#EDEDED]/40 font-bold mb-2">Per-Regime Performance</div>
                      <div className="space-y-1.5">
                        {Object.entries(bt.regimePerf).sort((a,b)=>b[1].n-a[1].n).map(([name,s])=>{
                          const wr=s.wins/s.n;
                          return(<div key={name} className="flex items-center justify-between text-xs">
                            <span className="text-[#EDEDED]/70">{name}</span>
                            <div className="flex items-center gap-3">
                              <span className="text-[#EDEDED]/40 text-[10px] font-mono">{s.n}t</span>
                              <span className={'font-mono font-bold '+(wr>=0.65?'text-emerald-400':wr>=0.55?'text-amber-400':'text-rose-400')}>{fmt(wr)}</span>
                            </div>
                          </div>);
                        })}
                      </div>
                    </div>
                    {/* Lock timing */}
                    <div className="bg-[#050508] rounded-lg p-3 border border-[#24242E]">
                      <div className="text-[10px] uppercase tracking-wide text-[#EDEDED]/40 font-bold mb-2">Performance by Lock Timing</div>
                      <div className="space-y-1.5">
                        {Object.entries(bt.clockPerf).map(([name,s])=>{
                          if(s.n===0)return null;
                          const wr=s.wins/s.n;
                          return(<div key={name} className="flex items-center justify-between text-xs">
                            <span className="text-[#EDEDED]/70">{name}</span>
                            <div className="flex items-center gap-3">
                              <span className="text-[#EDEDED]/40 text-[10px] font-mono">{s.n}t</span>
                              <span className={'font-mono font-bold '+(wr>=0.65?'text-emerald-400':wr>=0.55?'text-amber-400':'text-rose-400')}>{fmt(wr)}</span>
                            </div>
                          </div>);
                        })}
                      </div>
                    </div>
                    {/* Confidence calibration */}
                    <div className="bg-[#050508] rounded-lg p-3 border border-[#24242E]">
                      <div className="text-[10px] uppercase tracking-wide text-[#EDEDED]/40 font-bold mb-2">Calibration: Predicted vs Actual</div>
                      <div className="space-y-1">
                        {Object.entries(bt.confBuckets).filter(([_,s])=>s.n>0).map(([conf,s])=>{
                          const wr=s.wins/s.n;
                          const expected=parseInt(conf)/100+0.025;
                          const drift=wr-expected;
                          return(<div key={conf} className="flex items-center justify-between text-xs">
                            <span className="text-[#EDEDED]/70">{conf}-{parseInt(conf)+5}%</span>
                            <div className="flex items-center gap-3">
                              <span className="text-[#EDEDED]/40 text-[10px] font-mono">{s.n}t</span>
                              <span className="font-mono">{fmt(wr)}</span>
                              <span className={'font-mono text-[10px] w-12 text-right '+(Math.abs(drift)<0.05?'text-emerald-400':Math.abs(drift)<0.10?'text-amber-400':'text-rose-400')}>{drift>=0?'+':''}{(drift*100).toFixed(1)}</span>
                            </div>
                          </div>);
                        })}
                      </div>
                    </div>
                  </div>);
                })()}
              </section>

              {/* Adaptive Weights */}
              <section>
                <h3 className="text-xs font-bold uppercase tracking-wide text-indigo-400 mb-3">Adaptive Signal Weights · <span className={'text-[#EDEDED]/40 normal-case font-normal'}>{lastRegimeRef.current||'Global'} active</span></h3>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                  {Object.entries(adaptiveWeights).map(([key,val])=>{
                    const def=DEFAULT_WEIGHTS[key]||20;const pct=(val/55)*100;
                    const acc=signalAccuracy[key];const wrPct=acc?.total>=3?((acc.right/acc.total)*100).toFixed(0):null;
                    const delta=val-def;
                    return(<div key={key} className={'bg-[#050508] rounded-lg p-2.5 border border-[#24242E]'}>
                      <div className="flex justify-between items-center mb-1.5">
                        <span className={'text-xs font-bold uppercase tracking-wide text-[#EDEDED]/70'}>{key}</span>
                        <div className="flex items-center gap-1.5">
                          {wrPct&&<span className={'text-xs text-indigo-400/80'}>{wrPct}% acc</span>}
                          <span className={`text-xs font-mono font-bold ${delta>0?'text-emerald-400':delta<0?'text-rose-400':'text-[#EDEDED]/50'}`}>{val.toFixed(1)}</span>
                          <span className={`text-xs ${delta>0?'text-emerald-400':delta<0?'text-rose-400':'text-[#EDEDED]/30'}`}>{delta>0?'+':''}{delta.toFixed(1)}</span>
                        </div>
                      </div>
                      <div className="w-full h-1.5 bg-[#101014] rounded-full overflow-hidden">
                        <div className={`h-full rounded-full transition-all duration-500 ${delta>2?'bg-emerald-500':delta<-2?'bg-rose-500':'bg-indigo-500'}`} style={{width:`${Math.min(100,pct)}%`}}/>
                      </div>
                      <div className={'flex justify-between mt-0.5 text-xs text-[#EDEDED]/20'}>
                        <span>default: {def}</span>
                        <span>{acc?.total||0} samples</span>
                      </div>
                    </div>);
                  })}
                </div>
              </section>

              {/* Calibration */}
              <section>
                <h3 className="text-xs font-bold uppercase tracking-wide text-amber-400 mb-3">Probability Calibration (posterior accuracy)</h3>
                {tradeLog.filter(t=>t.result).length<10?(
                  <div className={'text-xs text-[#EDEDED]/40 italic text-center py-4 bg-[#050508] rounded-lg border border-[#24242E]'}>Need 10+ resolved trades to calibrate. Currently: {tradeLog.filter(t=>t.result).length}</div>
                ):(
                  <div className="grid grid-cols-5 sm:grid-cols-10 gap-1">
                    {[0,10,20,30,40,50,60,70,80,90].map(b=>{
                      const calVal=calibration[b];const isNull=calVal==null;
                      const diff=isNull?0:calVal-b;
                      return(<div key={b} className={'bg-[#050508] rounded-lg p-1.5 border border-[#24242E] text-center'}>
                        <div className={'text-xs text-[#EDEDED]/30 mb-1'}>{b}-{b+10}%</div>
                        <div className={`text-xs font-bold font-mono ${isNull?'text-[#EDEDED]/20':Math.abs(diff)<5?'text-emerald-400':Math.abs(diff)<15?'text-amber-400':'text-rose-400'}`}>
                          {isNull?'—':`${calVal.toFixed(0)}%`}
                        </div>
                        {!isNull&&<div className={`text-xs ${diff>0?'text-emerald-400':diff<0?'text-rose-400':'text-[#EDEDED]/30'}`}>{diff>0?'+':''}{diff.toFixed(0)}</div>}
                      </div>);
                    })}
                  </div>
                )}
                <p className={'text-xs text-[#EDEDED]/30 mt-2'}>Green = well-calibrated. Red = raw posterior is over- or under-estimating actual win rate. Tara applies calibration automatically after 3+ samples per bucket.</p>
              </section>

              {/* Session Performance */}
              <section>
                <h3 className="text-xs font-bold uppercase tracking-wide text-emerald-400 mb-3">Performance by Session</h3>
                {(()=>{
                  // V5.6.9: Merge Tara's session stats with manual session stats so cards reflect
                  //   both data sources. Show as combined header with Tara breakdown underneath.
                  const _taraBySession={};
                  (taraCallLog||[]).forEach(e=>{
                    if(!e||(e.result!=='WIN'&&e.result!=='LOSS'))return;
                    if(e.dir!=='UP'&&e.dir!=='DOWN')return;
                    const s=e.session||'OFF-HOURS';
                    _taraBySession[s]=_taraBySession[s]||{wins:0,losses:0};
                    _taraBySession[s][e.result==='WIN'?'wins':'losses']++;
                  });
                  const allSessions=['ASIA','EU','US','OFF-HOURS'];
                  return (
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                      {allSessions.map((sess)=>{
                        const manual=sessionPerf?.[sess]||{wins:0,losses:0};
                        const tara=_taraBySession[sess]||{wins:0,losses:0};
                        const totalManual=manual.wins+manual.losses;
                        const totalTara=tara.wins+tara.losses;
                        const totalAll=totalManual+totalTara;
                        const wins=manual.wins+tara.wins;
                        const wr=totalAll>0?((wins/totalAll)*100):0;
                        return(<div key={sess} className={'bg-[#050508] rounded-lg p-2.5 border border-[#24242E] text-center'}>
                          <div className={'text-xs font-bold text-[#EDEDED]/70 mb-1'}>{sess}</div>
                          <div className={`text-lg font-serif font-bold ${wr>=60?'text-emerald-400':wr>=45?'text-amber-400':totalAll>0?'text-rose-400':'text-[#EDEDED]/35'}`}>{totalAll>0?`${wr.toFixed(0)}%`:'—'}</div>
                          <div className={'text-[10px] text-[#EDEDED]/55 mt-0.5'}>{wins}W · {(manual.losses+tara.losses)}L</div>
                          {totalTara>0&&<div className={'text-[9px] text-amber-400/70 mt-0.5'}>{totalTara} from Tara</div>}
                        </div>);
                      })}
                    </div>
                  );
                })()}
              </section>

              {/* Hourly Heatmap */}
              <section>
                <h3 className="text-xs font-bold uppercase tracking-wide text-purple-400 mb-3">Performance by Hour (local)</h3>
                {Object.keys(hourlyPerf).length<3?<div className={'text-xs text-[#EDEDED]/40 italic text-center py-4 bg-[#050508] rounded-lg border border-[#24242E]'}>Need more trades to build hourly map.</div>:(
                <div className="grid grid-cols-6 sm:grid-cols-12 gap-1">
                  {Array.from({length:24},(_,h)=>{
                    const d=hourlyPerf[h];const total=d?(d.wins+d.losses):0;const wr=total>0?((d.wins/total)*100):null;
                    return(<div key={h} className="rounded-lg p-1 text-center" style={{background:wr==null?'rgba(237,237,237,0.03)':wr>=65?'rgba(52,211,153,0.2)':wr>=45?'rgba(212,162,76,0.15)':'rgba(251,113,133,0.2)'}}>
                      <div className={'text-xs text-[#EDEDED]/30'}>{h}h</div>
                      <div className={`text-xs font-bold ${wr==null?'text-[#EDEDED]/20':wr>=65?'text-emerald-400':wr>=45?'text-amber-400':'text-rose-400'}`}>{wr!=null?`${wr.toFixed(0)}%`:'·'}</div>
                    </div>);
                  })}
                </div>)}
              </section>

              {/* Recent Trade Log — Editable */}
              <section>
                <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                  <h3 className={'text-xs font-bold uppercase tracking-wide text-[#EDEDED]/60'}>Trade Log ({tradeLog.length} total)</h3>
                  <p className={'text-[10px] text-[#EDEDED]/30'}>Tap a trade to edit · Fix any wrong WIN or LOSS</p>
                </div>

                {/* Edit panel — shows when a trade is selected */}
                {selectedTradeId&&(()=>{
                  const selected=tradeLog.find(t=>t.id===selectedTradeId);
                  if(!selected)return null;
                  const correctTrade=(id,newResult)=>{
                    const oldTrade=tradeLog.find(t=>t.id===id);
                    const newLog=tradeLog.map(t=>t.id!==id?t:{...t,result:newResult,manuallyEdited:true});
                    saveTradeLog(newLog);setTradeLog(newLog);
                    if(oldTrade?.result&&oldTrade.result!==newResult){
                      const wType=oldTrade.windowType||windowType;
                      if(oldTrade.result==='WIN'){updateScore(wType,'wins',-1);updateScore(wType,'losses',1);}
                      else{updateScore(wType,'losses',-1);updateScore(wType,'wins',1);}
                    }
                    // V7.1: per-asset — use the edited trade's asset (not currentAsset)
                    const _editedTrade=newLog.find(t=>t.id===oldTrade?.id);
                    applyTradeLearning(newLog,_editedTrade||oldTrade,newResult);
                    setSelectedTradeId(null);
                  };
                  const deleteTrade=(id)=>{
                    const oldTrade=tradeLog.find(t=>t.id===id);
                    const newLog=tradeLog.filter(t=>t.id!==id);
                    saveTradeLog(newLog);setTradeLog(newLog);
                    if(oldTrade?.result){const wType=oldTrade.windowType||windowType;updateScore(wType,oldTrade.result==='WIN'?'wins':'losses',-1);}
                    setSelectedTradeId(null);
                  };
                  return(
                    <div className={'mb-3 p-3 rounded-xl border-2 border-indigo-500/40 bg-[#050508]'}>
                      <div className="flex items-center justify-between mb-2 gap-2 flex-wrap">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className={`font-bold text-sm ${selected.dir==='UP'?'text-emerald-400':'text-rose-400'}`}>{selected.dir}</span>
                          <span className={'text-xs text-[#EDEDED]/50'}>{_fmtTimeTz(new Date(selected.id),timeFormat,{hour:'2-digit',minute:'2-digit',hour12:true})}</span>
                          <span className={'text-xs text-[#EDEDED]/40 hidden sm:inline'}>{selected.posterior?.toFixed(0)}% · {selected.regime}</span>
                          {selected.manuallyEdited&&<span className={'text-[10px] bg-amber-500/20 text-amber-400 border border-amber-500/30 px-1.5 py-0.5 rounded-lg'}>edited</span>}
                        </div>
                        <button onClick={()=>setSelectedTradeId(null)} className={'text-[#EDEDED]/40 hover:text-white p-1 ml-auto'}><IC.X className="w-4 h-4"/></button>
                      </div>
                      <div className={'text-xs text-[#EDEDED]/50 mb-3'}>
                        Logged as: <span className={`font-bold ${selected.result==='WIN'?'text-emerald-400':selected.result==='LOSS'?'text-rose-400':'text-[#EDEDED]/30'}`}>{selected.result||'PENDING'}</span>
                        {selected.earlyExit&&' · early exit'}{selected.forceExit&&' · force exit'}
                      </div>
                      <div className="flex gap-2">
                        <button onClick={()=>correctTrade(selected.id,'WIN')}
                          className={`flex-1 py-2.5 rounded-lg text-xs font-bold uppercase tracking-wide border transition-all ${selected.result==='WIN'?'bg-emerald-500/30 border-emerald-400 text-emerald-300':'border-emerald-500/30 text-emerald-500 hover:bg-emerald-500/15'}`}>
                          ✓ Mark WIN
                        </button>
                        <button onClick={()=>correctTrade(selected.id,'LOSS')}
                          className={`flex-1 py-2.5 rounded-lg text-xs font-bold uppercase tracking-wide border transition-all ${selected.result==='LOSS'?'bg-rose-500/30 border-rose-400 text-rose-300':'border-rose-500/30 text-rose-500 hover:bg-rose-500/15'}`}>
                          ✗ Mark LOSS
                        </button>
                        <button onClick={()=>deleteTrade(selected.id)}
                          className={'px-3 py-2.5 rounded-lg text-xs font-bold border border-zinc-500/30 text-zinc-500 hover:bg-zinc-500/15 hover:text-zinc-300 transition-all'}>
                          Delete
                        </button>
                      </div>
                      <p className={'text-[10px] text-[#EDEDED]/30 mt-2 text-center'}>Updates scorecard and retrains signal weights</p>
                    </div>
                  );
                })()}

                {/* Trade rows */}
                <div className="space-y-1.5 max-h-64 overflow-y-auto pr-1" style={{scrollbarWidth:'thin'}}>
                  {tradeLog.length===0?(
                    <div className={'text-xs text-[#EDEDED]/30 italic text-center py-6'}>No trades yet — trades log automatically when Tara locks and the window closes.</div>
                  ):tradeLog.slice(-30).reverse().map((t,i)=>{
                    const d=new Date(t.id);
                    const isSel=selectedTradeId===t.id;
                    return(
                      <button key={t.id||i} onClick={()=>setSelectedTradeId(isSel?null:t.id)}
                        className={`w-full flex items-center gap-2 text-xs p-2.5 rounded-lg border transition-all text-left ${
                          isSel?'border-indigo-500/60 bg-indigo-500/10 ring-1 ring-indigo-500/30'
                          :t.result==='WIN'?'border-emerald-500/20 bg-emerald-500/5 hover:border-emerald-500/40'
                          :t.result==='LOSS'?'border-rose-500/20 bg-rose-500/5 hover:border-rose-500/40'
                          :'border-[#24242E] hover:border-[#2A2A34]'}`}>
                        <div className={`w-2 h-2 rounded-full shrink-0 ${isSel?'bg-indigo-400':t.result==='WIN'?'bg-emerald-500':t.result==='LOSS'?'bg-rose-500':'bg-[#EDEDED]/20'}`}/>
                        <span className={'text-[#EDEDED]/40 font-mono shrink-0 text-[10px] hidden sm:inline'}>{_fmtDateTz(d,timeFormat,{month:'short',day:'numeric'})} </span>
                        <span className={'text-[#EDEDED]/40 font-mono shrink-0 text-[10px]'}>{_fmtTimeTz(d,timeFormat,{hour:'2-digit',minute:'2-digit',hour12:true})}</span>
                        <span className={`font-bold shrink-0 ${t.dir==='UP'?'text-emerald-400':'text-rose-400'}`}>{t.dir}</span>
                        <span className={'text-[#EDEDED]/40 text-[10px] shrink-0'}>{t.posterior?.toFixed(0)}%</span>
                        <span className={'text-[#EDEDED]/25 text-[10px] truncate hidden md:block'}>{t.regime}</span>
                        <span className={'text-[#EDEDED]/25 text-[10px] ml-auto shrink-0'}>{t.session}</span>
                        <span className={`font-bold shrink-0 min-w-[36px] text-right ${t.result==='WIN'?'text-emerald-400':t.result==='LOSS'?'text-rose-400':'text-[#EDEDED]/25'}`}>{t.result||'—'}</span>
                        {t.manuallyEdited&&<span className={'text-amber-400/60 shrink-0 text-[10px]'}>✎</span>}
                      </button>
                    );
                  })}
                </div>
              </section>

              {/* ── HOURLY HEATMAP ── */}
              <section>
                <h3 className="text-xs font-bold uppercase tracking-wide text-indigo-400 mb-3">Hourly Win Rate Heatmap (24h UTC) — {windowType.toUpperCase()}</h3>
                {/* Window tab selector */}
                <div className="flex gap-1 mb-3">
                  {['15m','5m'].map(wt=>(
                    <button key={wt} onClick={()=>{}} className={`px-2 py-0.5 rounded-lg text-[10px] font-bold uppercase border ${windowType===wt?'bg-indigo-500/20 border-indigo-500/40 text-indigo-400':'border-[#24242E] text-[#EDEDED]/30'}`}>{wt}</button>
                  ))}
                  <span className={'text-[9px] text-[#EDEDED]/25 ml-2 self-center'}>Showing trades from your active window</span>
                </div>
                <div className="grid grid-cols-8 sm:grid-cols-12 gap-1">
                  {Array.from({length:24},(_,h)=>{
                    const trades=tradeLog.filter(t=>t.result&&t.hour===h&&(!t.windowType||t.windowType===windowType));
                    const wins=trades.filter(t=>t.result==='WIN').length;
                    const wr=trades.length>=2?(wins/trades.length)*100:null;
                    const intensity=wr===null?0:wr>=75?4:wr>=60?3:wr>=50?2:wr>=40?1:0;
                    const colors=['bg-[#EDEDED]/5','bg-rose-500/30','bg-amber-500/30','bg-emerald-500/30','bg-emerald-500/60'];
                    const labels=['No data','<50%','50–60%','60–75%','≥75%'];
                    return(
                      <div key={h} className={`${colors[intensity]} rounded-lg p-1.5 text-center cursor-default`} title={`Hour ${h}:00 UTC · ${trades.length} trades${wr!==null?' · '+wr.toFixed(0)+'% WR':' — need ≥2 trades'}`}>
                        <div className={'text-[9px] text-[#EDEDED]/40 font-mono'}>{String(h).padStart(2,'0')}</div>
                        {wr!==null?(
                          <div className={`text-[10px] font-bold ${wr>=60?'text-emerald-300':wr>=50?'text-amber-300':'text-rose-300'}`}>{wr.toFixed(0)}%</div>
                        ):(
                          <div className={'text-[10px] text-[#EDEDED]/20'}>—</div>
                        )}
                        {trades.length>0&&<div className={'text-[8px] text-[#EDEDED]/25'}>{trades.length}t</div>}
                      </div>
                    );
                  })}
                </div>
                <div className="flex items-center gap-3 mt-2 flex-wrap">
                  {[['bg-rose-500/30','<50%'],['bg-amber-500/30','50–60%'],['bg-emerald-500/30','60–75%'],['bg-emerald-500/60','≥75%'],['bg-[#EDEDED]/5','No data']].map(([c,l])=>(
                    <div key={l} className="flex items-center gap-1">
                      <div className={`w-3 h-3 rounded-lg ${c}`}></div>
                      <span className={'text-[9px] text-[#EDEDED]/40'}>{l}</span>
                    </div>
                  ))}
                </div>
              </section>

              {/* ── PER-REGIME WEIGHTS ── */}
              <section>
                <h3 className="text-xs font-bold uppercase tracking-wide text-indigo-400 mb-3">Per-Regime Signal Weights</h3>
                <div className="space-y-3">
                  {Object.entries(regimeWeights).map(([rg,w])=>{
                    // V9.10.1: was reading from `tradeLog` which is the legacy USER-trade
                    //   log that was stubbed to no-op in V5.5b per user request. That made
                    //   every regime panel show "0 trades · no data" even though weights
                    //   were actually learning from 400+ Tara calls. The underlying
                    //   adaptive weights (the numbers below) ARE correctly updated from
                    //   taraCallLog via V7.10.5's self-learn path at L20445. This was
                    //   purely a display bug — fix is to read from the right source.
                    const rgTrades=(taraCallLog||[]).filter(t=>(t.result==='WIN'||t.result==='LOSS')&&t.regime===rg);
                    const rgWins=rgTrades.filter(t=>t.result==='WIN').length;
                    const isActive=lastRegimeRef.current===rg;
                    return(
                      <div key={rg} className={`p-2.5 rounded-lg border ${isActive?'border-indigo-500/40 bg-indigo-500/5':'border-[#24242E] bg-[#050508]'}`}>
                        <div className="flex justify-between items-center mb-2">
                          <span className={`text-xs font-bold uppercase ${isActive?'text-indigo-400':'text-[#EDEDED]/50'}`}>{rg}{isActive&&' ◀ active'}</span>
                          <span className={'text-[10px] text-[#EDEDED]/30'}>{rgTrades.length} trades · {rgTrades.length>0?(rgWins/rgTrades.length*100).toFixed(0)+'% WR':'no data'}</span>
                        </div>
                        <div className="grid grid-cols-3 sm:grid-cols-6 gap-1">
                          {Object.entries(w).map(([k,v])=>(
                            <div key={k} className="text-center">
                              <div className={'text-[9px] text-[#EDEDED]/30 uppercase tracking-wide'}>{k.slice(0,3)}</div>
                              <div className={'text-[11px] font-mono font-bold text-[#EDEDED]/70'}>{v.toFixed(1)}</div>
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </section>

              {/* V145: ── PERFORMANCE BY HOUR & DAY-OF-WEEK ── */}
              <section>
                <h3 className="text-xs font-bold uppercase tracking-wide text-indigo-400 mb-3">Performance Patterns</h3>
                {(()=>{
                  // V9.10.1: same fix as Per-Regime Signal Weights above — was reading
                  //   `tradeLog` (the legacy user-trade log stubbed in V5.5b) instead of
                  //   taraCallLog. Hour/day/session breakdowns all said "no data" even
                  //   with 400+ Tara calls populated.
                  const completed=(taraCallLog||[]).filter(t=>t.result==='WIN'||t.result==='LOSS');
                  if(completed.length===0)return<div className={'text-xs text-[#EDEDED]/30 italic'}>No completed trades yet.</div>;

                  // Aggregate by hour of day (0-23)
                  const byHour={};
                  // Aggregate by day-of-week (Mon-Sun)
                  const byDOW={};
                  // Aggregate by session
                  const bySession={};

                  completed.forEach(t=>{
                    // Use timestampISO if present (V145+), else fall back to id (timestamp)
                    let d;
                    if(t.timestampISO){d=new Date(t.timestampISO);}
                    else{d=new Date(t.id/1);}
                    const isValid=!isNaN(d.getTime())&&d.getFullYear()>2020;
                    const h=isValid?d.getHours():(t.hour||0);
                    const dow=isValid?d.getDay():null; // 0=Sun, 1=Mon ...
                    if(!byHour[h])byHour[h]={W:0,L:0};
                    byHour[h][t.result==='WIN'?'W':'L']++;
                    if(dow!=null){
                      if(!byDOW[dow])byDOW[dow]={W:0,L:0};
                      byDOW[dow][t.result==='WIN'?'W':'L']++;
                    }
                    const sess=t.session||'—';
                    if(!bySession[sess])bySession[sess]={W:0,L:0};
                    bySession[sess][t.result==='WIN'?'W':'L']++;
                  });

                  const dowLabels=['Sun','Mon','Tue','Wed','Thu','Fri','Sat'];
                  const formatHour=h=>{const h12=h%12||12;return `${h12}${h<12?'a':'p'}`;};
                  const cellCls=(wr,n)=>n<2?'bg-[#050508] text-[#EDEDED]/25':wr>=65?'bg-emerald-500/15 text-emerald-300':wr>=55?'bg-amber-500/10 text-amber-300':wr>=45?'bg-[#050508] text-[#EDEDED]/50':'bg-rose-500/10 text-rose-300';

                  return(
                    <div className="space-y-4">
                      {/* By session (most actionable) */}
                      <div>
                        <div className={'text-[10px] uppercase tracking-wide text-[#EDEDED]/40 font-bold mb-2'}>By Session</div>
                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                          {['EU','ASIA','US','OFF-HOURS'].map(s=>{
                            const v=bySession[s]||{W:0,L:0};
                            const n=v.W+v.L;
                            const wr=n>0?(100*v.W/n):0;
                            return(
                              <div key={s} className={`p-2 rounded-lg border border-[#24242E] ${cellCls(wr,n)}`}>
                                <div className="text-[10px] uppercase tracking-wide font-bold opacity-70">{s}</div>
                                <div className="text-base font-mono font-bold mt-0.5">{n>0?wr.toFixed(0)+'%':'—'}</div>
                                <div className="text-[9px] opacity-60">{v.W}W-{v.L}L · {n} trades</div>
                              </div>
                            );
                          })}
                        </div>
                      </div>

                      {/* By day of week */}
                      <div>
                        <div className={'text-[10px] uppercase tracking-wide text-[#EDEDED]/40 font-bold mb-2'}>By Day of Week</div>
                        <div className="grid grid-cols-7 gap-1">
                          {dowLabels.map((lbl,idx)=>{
                            const v=byDOW[idx]||{W:0,L:0};
                            const n=v.W+v.L;
                            const wr=n>0?(100*v.W/n):0;
                            return(
                              <div key={lbl} className={`p-1.5 rounded-lg text-center border border-[#24242E] ${cellCls(wr,n)}`}>
                                <div className="text-[9px] uppercase tracking-wide opacity-70">{lbl}</div>
                                <div className="text-xs font-mono font-bold mt-0.5">{n>0?wr.toFixed(0)+'%':'—'}</div>
                                <div className="text-[8px] opacity-60">{n>0?`${v.W}-${v.L}`:'—'}</div>
                              </div>
                            );
                          })}
                        </div>
                      </div>

                      {/* By hour of day */}
                      <div>
                        <div className={'text-[10px] uppercase tracking-wide text-[#EDEDED]/40 font-bold mb-2'}>By Hour of Day (local)</div>
                        <div className="grid grid-cols-12 gap-1">
                          {Array.from({length:24}).map((_,h)=>{
                            const v=byHour[h]||{W:0,L:0};
                            const n=v.W+v.L;
                            const wr=n>0?(100*v.W/n):0;
                            return(
                              <div key={h} className={`p-1 rounded-lg text-center border border-[#24242E] ${cellCls(wr,n)}`} title={n>0?`${formatHour(h)}: ${v.W}W-${v.L}L (${wr.toFixed(0)}%)`:`${formatHour(h)}: no data`}>
                                <div className="text-[8px] opacity-70">{formatHour(h)}</div>
                                <div className="text-[10px] font-mono font-bold">{n>0?wr.toFixed(0):'—'}</div>
                              </div>
                            );
                          })}
                        </div>
                        <div className={'text-[9px] text-[#EDEDED]/30 mt-1 italic'}>Cells with &lt; 2 trades shown as dashes. Hover for detail.</div>
                      </div>

                      {/* Color legend */}
                      <div className="flex items-center gap-3 text-[9px] text-[#EDEDED]/40 pt-1 border-t border-[#24242E]">
                        <span className="font-bold uppercase tracking-wide">Legend</span>
                        <div className="flex items-center gap-1"><div className="w-3 h-3 rounded-lg bg-emerald-500/15"></div><span>≥65% WR</span></div>
                        <div className="flex items-center gap-1"><div className="w-3 h-3 rounded-lg bg-amber-500/10"></div><span>55-64%</span></div>
                        <div className="flex items-center gap-1"><div className="w-3 h-3 rounded-lg bg-[#050508] border border-[#2A2A34]"></div><span>45-54%</span></div>
                        <div className="flex items-center gap-1"><div className="w-3 h-3 rounded-lg bg-rose-500/10"></div><span>&lt; 45%</span></div>
                      </div>
                    </div>
                  );
                })()}
              </section>

              {/* ── TRADE LOG — Day by Day ── */}
              <section>
                <h3 className="text-xs font-bold uppercase tracking-wide text-indigo-400 mb-3">Trade Log — by Day</h3>
                {(()=>{
                  // Group trades by calendar date
                  const byDay={};
                  [...tradeLog].filter(t=>t.result).reverse().forEach(t=>{
                    const d=new Date(t.id/1);
                    const isValidDate=!isNaN(d.getTime())&&d.getFullYear()>2020;
                    const key=isValidDate?_fmtDateTz(d,timeFormat,{weekday:'short',month:'short',day:'numeric',year:'numeric'}):'Unknown date';
                    if(!byDay[key])byDay[key]=[];
                    byDay[key].push(t);
                  });
                  const days=Object.entries(byDay).slice(0,14); // show last 14 days
                  if(days.length===0)return<div className={'text-xs text-[#EDEDED]/30 italic'}>No completed trades yet.</div>;
                  return days.map(([day,trades])=>{
                    const wins=trades.filter(t=>t.result==='WIN').length;
                    const wr=Math.round(wins/trades.length*100);
                    return(
                      <div key={day} className="mb-3">
                        {/* Day header */}
                        <div className={'flex items-center justify-between mb-1.5 pb-1 border-b border-[#24242E]'}>
                          <span className={'text-[10px] font-bold uppercase tracking-wide text-[#EDEDED]/50'}>{day}</span>
                          <div className="flex items-center gap-2">
                            <span className="text-[9px] text-emerald-400 font-mono">{wins}W</span>
                            <span className="text-[9px] text-rose-400 font-mono">{trades.length-wins}L</span>
                            <span className={`text-[9px] font-bold ${wr>=65?'text-emerald-400':wr>=50?'text-amber-400':'text-rose-400'}`}>{wr}%</span>
                          </div>
                        </div>
                        {/* Trades for that day */}
                        <div className="space-y-1">
                          {trades.map((t,i)=>{
                            const d=new Date(t.id/1);
                            // V9.8.15: trade-history time honors timeFormat (was hardcoded local)
                            const timeStr=isNaN(d.getTime())?'—':_fmtTimeTz(d,timeFormat,{hour:'2-digit',minute:'2-digit',hour12:true});
                            // V145: if expanded telemetry present, show entry → close and Kalshi edge
                            const hasV145=t.entryPrice!=null&&t.entryPrice>0&&t.closingPrice!=null;
                            const moveBps=hasV145&&t.entryPrice>0?Math.round(((t.closingPrice-t.entryPrice)/t.entryPrice)*10000):null;
                            const kalshiEdge=t.kalshiAtLock!=null&&t.posterior!=null?(t.dir==='UP'?(t.posterior-t.kalshiAtLock):((100-t.posterior)-(100-t.kalshiAtLock))):null;
                            return(
                              <div key={i} className={`px-2 py-1 rounded-lg border ${t.result==='WIN'?'bg-emerald-500/5 border-emerald-500/15':'bg-rose-500/5 border-rose-500/15'}`}>
                                <div className="flex items-center gap-2 text-[10px]">
                                  <span className={'font-mono text-[#EDEDED]/30 shrink-0 w-14'}>{timeStr}</span>
                                  <span className={`font-bold w-8 shrink-0 ${t.dir==='UP'?'text-emerald-400':'text-rose-400'}`}>{t.dir}</span>
                                  <span className={'text-[#EDEDED]/40 shrink-0'}>{t.windowType||'15m'}</span>
                                  <span className={'text-[#EDEDED]/30 flex-1 truncate'}>{t.regime||'—'}</span>
                                  <span className={'text-[#EDEDED]/25 shrink-0'}>{t.session||'—'}</span>
                                  <span className={`font-bold shrink-0 ${t.result==='WIN'?'text-emerald-400':'text-rose-400'}`}>{t.result}</span>
                                </div>
                                {hasV145&&(
                                  <div className="flex items-center gap-2 text-[9px] mt-1 pt-1 border-t border-[#24242E] text-[#EDEDED]/40">
                                    <span className="font-mono">@${Math.round(t.entryPrice).toLocaleString()}</span>
                                    <span>→</span>
                                    <span className="font-mono">${Math.round(t.closingPrice).toLocaleString()}</span>
                                    {moveBps!=null&&<span className={`font-mono ${moveBps>=0?'text-emerald-400/60':'text-rose-400/60'}`}>{moveBps>=0?'+':''}{moveBps}bps</span>}
                                    {t.posterior!=null&&<span className="ml-auto">Tara {t.posterior.toFixed(0)}%</span>}
                                    {kalshiEdge!=null&&<span className={`${kalshiEdge>=15?'text-emerald-400':kalshiEdge>=0?'text-[#EDEDED]/40':'text-amber-400'}`}>edge {kalshiEdge>=0?'+':''}{kalshiEdge.toFixed(0)}</span>}
                                    {t.fgtAlignment!=null&&Math.abs(t.fgtAlignment)>=2&&<span className="text-purple-400/60">FGT {Math.abs(t.fgtAlignment).toFixed(1).replace(/\.0$/,'')}/4 {t.fgtAlignment>0?'↑':'↓'}</span>}
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    );
                  });
                })()}
              </section>

              {/* Training Tips */}
              <section className={'bg-indigo-500/5 border border-indigo-500/15 rounded-xl p-3'}>
                <h3 className="text-xs font-bold uppercase tracking-wide text-indigo-400 mb-2">How to Train Tara Faster</h3>
                <div className={'text-xs text-[#EDEDED]/60 space-y-1 leading-relaxed'}>
                  <p>• <strong className="text-indigo-300">Every window auto-updates weights.</strong> The more she trades, the more accurate her signal weights become.</p>
                  <p>• <strong className="text-indigo-300">Export CSV</strong> and run external regression (Python sklearn) on 500+ trades to get optimal weights, then paste them back.</p>
                  <p>• <strong className="text-indigo-300">Best regime to focus on:</strong> Look at session performance — if US session is 70%+ WR, run exclusively during US hours.</p>
                  <p>• <strong className="text-indigo-300">Calibration corrects overconfidence</strong> — if Tara says 80% but only wins 60% of those, calibration fixes the displayed number after 3+ samples.</p>
                  <p>• <strong className="text-indigo-300">Accuracy improves with volume:</strong> need 100+ trades in the log for weights to converge and calibration to become reliable.</p>
                </div>
              </section>
            </div>
          </div>
        </div>
      )}

      {/* ── HOW TARA WORKS GUIDE ── */}
      {showGuide&&(
        <div className={'fixed inset-0 z-[100] bg-black/90 backdrop-blur-sm flex items-center justify-center p-2 sm:p-4'}>
          <div className={'bg-[#0A0A0E] border border-[#1B1B22] rounded-[10px] w-full max-w-2xl max-h-[95vh] mx-2 sm:mx-0 overflow-y-auto shadow-2xl'} style={{scrollbarWidth:'thin'}}>
            <div className={'sticky top-0 bg-[#101014] border-b border-[#24242E] p-4 flex justify-between items-center z-10'}>
              <div>
                <h2 className="text-base sm:text-lg font-serif text-white flex items-center gap-2">
                  <span className="text-indigo-400 text-xl font-bold">?</span> How {TARA_VERSION_DISPLAY} Works
                </h2>
                <p className={'text-xs text-[#EDEDED]/40 mt-0.5'}>Complete guide — predictions, learning, advisor, and best practices</p>
              </div>
              <button onClick={()=>setShowGuide(false)} className={'text-[#EDEDED]/50 hover:text-white p-1'}><IC.X className="w-5 h-5"/></button>
            </div>
            <div className={'p-4 sm:p-6 space-y-6 text-sm text-[#EDEDED]/80'}>

              {/* V8.7: CURRENT-STATE OVERVIEW — replaces stale V134 summary */}
              <section className={'bg-[#101014] border-2 rounded-lg p-4'} style={{borderColor:'rgba(35,185,129,0.30)'}}>
                <h3 className={'font-bold uppercase tracking-wide mb-3 text-xs'} style={{color:T2_GOLD}}>★ Current Tara — V8.7 Overview</h3>
                <div className="space-y-3 text-xs leading-relaxed text-[#EDEDED]/75">
                  <p><strong className="text-white">What Tara does:</strong> Reads BTC price action, tape flow, regime and multi-timeframe alignment to predict whether price will close above or below the window strike. She makes a Call only when the existing rules qualify it. Verified original outcomes feed separate shadow training and forward validation; a live model change requires policy review.</p>
                  <p><strong className="text-white">Per-asset everything:</strong> BTC and ETH have separate weights, regime memories, scorecards, and learning histories. Switching assets shows that asset's data — they don't blend.</p>
                  <p><strong className="text-white">Cloud-synced across all your devices:</strong> Trades, scorecards, weights, and learning all sync to Supabase. Open Tara on any browser, on any device, and you see the same data. The SyncStatusPill in the header shows sync health (green = synced, amber = writing, rose = error). Click it to force a fresh resync.</p>
                  <p><strong className="text-white">Multi-tab safe:</strong> Open Tara in 5 tabs at once — they all share state via BroadcastChannel + atomic Supabase transactions. The TabPresencePill shows when peer tabs are active. No more lost trades from concurrent writes.</p>
                </div>
              </section>

              {/* V8.7: LEARNING SYSTEM — current */}
              <section className={'bg-[#101014] border rounded-lg p-4'} style={{borderColor:'rgba(168,85,247,0.30)'}}>
                <h3 className={'font-bold uppercase tracking-wide mb-3 text-xs'} style={{color:'rgba(168,85,247,0.95)'}}>🧠 How Tara Learns Every Trade</h3>
                <div className="space-y-2.5 text-xs leading-relaxed text-[#EDEDED]/70">
                  <p><strong className="text-white">Verified shadow learning · v14.6:</strong> Scheduled training runs independently of the dashboard. Separate profiles keep 15-minute, 5-minute, hourly, Sports and Weather evidence apart. New models are evaluated on chronological holdout and independently witnessed forward predictions; only verified original captures qualify.</p>
                  <ol className="list-decimal pl-5 space-y-1">
                    <li><strong className="text-white">Saved adaptive weights</strong> — retained unchanged. Unvalidated event-by-event gradient writes are frozen; a replay, refresh or manual result cannot train the live model.</li>
                    <li><strong className="text-white">Frozen shadow candidates</strong> — never trained on their test labels. A shadow improvement requires policy review before live activation.</li>
                    <li><strong className="text-white">Statistical buckets</strong> — recomputes WR across 7 dimensions: regime, direction, tier, session, regime+direction combo, confluence type, edge bucket vs Kalshi.</li>
                    <li><strong className="text-white">Multipliers</strong> — derived from buckets, fed back into decision gates (regime safety floor, regime+direction speed adjustment, confidence boosts).</li>
                    <li><strong className="text-white">Calibration engine</strong> — maps her displayed posterior to actual historical WR.</li>
                  </ol>
                  <p className="pt-1"><strong className="text-white">Path-aware learning (V8.5):</strong> Every resolved trade now records max favorable/adverse excursion, peak time, last-60s drift, and a loss pattern classification (WHALE_SPIKE, MACRO_SHOCK, LATE_REVERSAL, MID_REVERSAL, EARLY_PEAK, WRONG_FROM_START).</p>
                  <p><strong className="text-white">Honest exclusions:</strong> Missing browser observations are not sit-outs. Edited, reconstructed, partial, stale-quote and unverified outcomes stay out of new-model training. Sports needs independent settlement proof; source-record wins alone are not accepted as that proof.</p>
                  <p><strong className="text-white">Saved fields per trade:</strong> 25+ fields including signal scores at lock, regime, session, phase, tape windows, FGT alignment, Kalshi edge at lock, closing price + Kalshi at close, path metrics, window context (whale activity, geoRisk), loss pattern. Open the export button on the call log to download.</p>
                </div>
              </section>

              {/* V8.7: SYNC ARCHITECTURE */}
              <section className={'bg-[#101014] border rounded-lg p-4'} style={{borderColor:'rgba(35,185,129,0.30)'}}>
                <h3 className={'font-bold uppercase tracking-wide mb-3 text-xs'} style={{color:'#23B981'}}>☁ Cross-Device Sync (V8.6)</h3>
                <div className="space-y-2 text-xs leading-relaxed text-[#EDEDED]/70">
                  <p>All 11 sync paths use atomic Firestore transactions with smart merge functions:</p>
                  <ul className="list-disc pl-5 space-y-0.5 text-[11px]">
                    <li><code className="text-[10px] bg-[#050508] px-1">memory/taraCallLog</code> — windowId-keyed merge, prefer resolved over unresolved</li>
                    <li><code className="text-[10px] bg-[#050508] px-1">scorecards/personal</code> — max-per-cell W/L</li>
                    <li><code className="text-[10px] bg-[#050508] px-1">history/pastWindows</code> — windowId union</li>
                    <li><code className="text-[10px] bg-[#050508] px-1">learnings/tara</code> — evidence-content signature and canonical-ledger watermark, not a maximum lifetime count</li>
                    <li><code className="text-[10px] bg-[#050508] px-1">learnings/regimeMemory</code> — max-per-cell</li>
                    <li><code className="text-[10px] bg-[#050508] px-1">learnings/taraWeights</code> — per-asset slice timestamps</li>
                    <li><code className="text-[10px] bg-[#050508] px-1">state/lifetimePnL</code> — timestamp inside transaction</li>
                    <li><code className="text-[10px] bg-[#050508] px-1">state/currentLock_*</code> (×4) — first-write-wins per window with windowId validation</li>
                  </ul>
                  <p className="pt-1"><strong className="text-white">Force resync</strong> — click the SyncStatusPill, confirm, and Tara re-pulls all 11 paths from cloud and applies fresh data to local state. Useful when opening a new browser or when something looks stale.</p>
                </div>
              </section>

              {/* V8.7: BEST PRACTICES — current, updated for V8.7 features */}
              <section className={'bg-emerald-500/5 border border-emerald-500/20 rounded-lg p-4'}>
                <h3 className={'text-emerald-400 font-bold uppercase tracking-wide mb-3 text-xs'}>🏆 How to Trade With Tara (V8.7)</h3>
                <div className="space-y-2.5 text-xs leading-relaxed text-[#EDEDED]/70">
                  <div className="flex gap-3"><span className="text-emerald-400 font-bold shrink-0 w-5">1.</span><p><strong className="text-white">Open the Best Practices guide first</strong> — the gold 📖 button in the header. It has 9 sections covering when to enter, skip, take profit, cut loss, plus position sizing and anti-tilt psychology. Spend 10 minutes there before your first trade.</p></div>
                  <div className="flex gap-3"><span className="text-emerald-400 font-bold shrink-0 w-5">2.</span><p><strong className="text-white">Watch the Live Trade Coach while in a trade</strong> — V8.7 adds situational guidance that shows up between Tara's call and the action buttons. It tells you what's happening NOW, what your move is, what to watch for, what to hope for, what to avoid. Replaces the static guide with live advisory cards.</p></div>
                  <div className="flex gap-3"><span className="text-emerald-400 font-bold shrink-0 w-5">3.</span><p><strong className="text-white">Read the Session Awareness expanded view</strong> — click the phase strip at the top. V8.7 adds: Expected movement, Volume guidance, What Tara does this phase, and a Coin-flip risk rating. If it says DEADZONE, half-size or skip.</p></div>
                  <div className="flex gap-3"><span className="text-emerald-400 font-bold shrink-0 w-5">4.</span><p><strong className="text-white">Trust positive Kalshi edge</strong> — when Tara says 80% but Kalshi is offering 60%, that 20-point edge is the highest-WR setup in your data. Take it confidently. Negative edge means Tara is behind the market — skip.</p></div>
                  <div className="flex gap-3"><span className="text-emerald-400 font-bold shrink-0 w-5">5.</span><p><strong className="text-white">Trust her snapshot, not the engine ticks</strong> — once Tara locks (snapshot fires), she's committed for the window. The engine state can wobble; her snapshot doesn't. The Brain view (V8.7) now correctly reflects the snapshot.</p></div>
                  <div className="flex gap-3"><span className="text-emerald-400 font-bold shrink-0 w-5">6.</span><p><strong className="text-white">Respect the tilt cooldown</strong> — 4 losses in a row triggers a 15-min forced break. The override exists but is psychologically expensive. The trade right after a 4-loss streak has the worst WR you'll ever produce.</p></div>
                  <div className="flex gap-3"><span className="text-emerald-400 font-bold shrink-0 w-5">7.</span><p><strong className="text-white">Use the SyncStatusPill</strong> — green dot means everything is up-to-date. If you switch browsers or devices and something looks off, click the pill → confirm to force-resync.</p></div>
                  <div className="flex gap-3"><span className="text-emerald-400 font-bold shrink-0 w-5">8.</span><p><strong className="text-white">Configure Trading Settings</strong> — click your daily P&L pill in the header. Set your standard bet size, win payout, anti-tilt threshold, take-profit target, and stop-loss target. Tara uses these for all the dollar-PnL math and TP/SL banner triggers.</p></div>
                  <div className="flex gap-3"><span className="text-emerald-400 font-bold shrink-0 w-5">9.</span><p><strong className="text-white">Send to Discord only when you actually trade</strong> — Tara doesn't auto-broadcast. Click the 📡 button only when you take the position.</p></div>
                  <div className="flex gap-3"><span className="text-emerald-400 font-bold shrink-0 w-5">10.</span><p><strong className="text-white">Skip more than you trade</strong> — most edge concentrates in your top 4-6 hours. Ignoring low-quality windows is profitable, not lazy. Filtering out negative-edge entries alone often saves more than any other improvement.</p></div>
                </div>
              </section>

              {/* DETAIL SECTIONS BELOW — original content for reference */}
              <section>
                <h3 className={'text-indigo-400 font-bold uppercase tracking-wide mb-3 text-xs border-b border-indigo-500/20 pb-1'}>📊 Prediction States — What Each One Means</h3>
                <div className="space-y-3">
                  <div className={'bg-[#050508] rounded-lg p-3 border border-[#24242E]'}>
                    <div className="flex items-center gap-2 mb-1"><span className="text-zinc-400 font-bold text-xs">SCANNING...</span><span className={'text-[10px] text-[#EDEDED]/30 uppercase tracking-wide'}>Do nothing</span></div>
                    <p className={'text-xs leading-relaxed text-[#EDEDED]/60'}>Tara's composite score is between 42–58% — a coin flip zone. No structural edge exists right now. Do not enter. Most tools show a number at all times to look busy — Tara shows nothing when there's genuinely nothing to show.</p>
                  </div>
                  <div className={'bg-[#050508] rounded-lg p-3 border border-amber-500/15'}>
                    <div className="flex items-center gap-2 mb-1"><span className="text-amber-400 font-bold text-xs">UP (FORMING) or DOWN (FORMING)</span><span className={'text-[10px] text-[#EDEDED]/30 uppercase tracking-wide'}>Get ready</span></div>
                    <p className={'text-xs leading-relaxed text-[#EDEDED]/60'}>Posterior has crossed 58%+ or below 42% — there's a lean — but not enough consecutive readings yet to commit. The forming progress bar shows how close she is to locking. You can enter here for more time in the trade, but it's higher risk than waiting for the lock.</p>
                  </div>
                  <div className={'bg-[#050508] rounded-lg p-3 border border-emerald-500/20'}>
                    <div className="flex items-center gap-2 mb-1"><span className="text-emerald-400 font-bold text-xs">UP — LOCKED 🔒</span><span className={'text-[10px] text-[#EDEDED]/30 uppercase tracking-wide'}>Entry signal — act now</span></div>
                    <p className={'text-xs leading-relaxed text-[#EDEDED]/60'}>2 consecutive readings (15m) or 1 consecutive (5m) all above the regime-specific threshold (60-75 depending on regime). Tara has committed for the window. She will NOT change this prediction without a 25+ point posterior collapse or severe trajectory flip. The lock state machine is designed to commit and stay committed. This is the <strong className="text-white">primary state to enter on.</strong></p>
                  </div>
                  <div className={'bg-[#050508] rounded-lg p-3 border border-rose-500/20'}>
                    <div className="flex items-center gap-2 mb-1"><span className="text-rose-400 font-bold text-xs">DOWN — LOCKED 🔒</span><span className={'text-[10px] text-[#EDEDED]/30 uppercase tracking-wide'}>Entry signal — act now</span></div>
                    <p className={'text-xs leading-relaxed text-[#EDEDED]/60'}>Same as UP — LOCKED but bearish. Posterior consistently below the regime-specific DOWN threshold (20-36 depending on regime — V2.8 made these symmetric with UP-side thresholds in choppy regimes). If you missed the entry window and it's late, the advisor will say WINDOW CLOSING — don't chase it.</p>
                  </div>
                  <div className={'bg-[#050508] rounded-lg p-3 border border-zinc-500/15'}>
                    <div className="flex items-center gap-2 mb-1"><span className="text-zinc-400 font-bold text-xs">NO CALL — WINDOW CLOSED — LOCK RELEASED</span><span className="text-[10px] text-rose-400 uppercase tracking-wide">Sit out</span></div>
                    <p className={'text-xs leading-relaxed text-[#EDEDED]/60'}><strong className="text-white">NO CALL:</strong> Never reached threshold before endgame. Skip this round.<br/><strong className="text-white">WINDOW CLOSED:</strong> Last 90s (15m) or 45s (5m) with no lock. Too late to enter safely.<br/><strong className="text-white">LOCK RELEASED:</strong> Posterior collapsed 25+ points or trajectory/FGT flipped against direction. Tara released — respect it immediately.</p>
                  </div>
                </div>
              </section>

              {/* STRIKE PRICE */}
              <section>
                <h3 className={'text-emerald-400 font-bold uppercase tracking-wide mb-3 text-xs border-b border-emerald-500/20 pb-1'}>🎯 Strike Price — Auto vs Manual</h3>
                <div className={'bg-[#050508] rounded-lg p-3 border border-[#24242E] space-y-2 text-xs leading-relaxed text-[#EDEDED]/60'}>
                  <p>The strike is automatically set to the <strong className="text-white">opening price of each window candle</strong> — fetched directly from Coinbase at the start of every 5m or 15m period. This matches what the binary options platform uses as the strike.</p>
                  <p>The <strong className="text-emerald-400">AUTO</strong> badge means Tara is tracking window opens automatically. Click it to reset to auto after a manual override.</p>
                  <p>Type any price in the Strike input to switch to <strong className="text-amber-400">MANUAL</strong> mode — useful if your platform uses a slightly different strike. Manual mode resets to auto at each new window.</p>
                </div>
              </section>

              {/* SIGNALS */}
              <section>
                <h3 className={'text-purple-400 font-bold uppercase tracking-wide mb-3 text-xs border-b border-purple-500/20 pb-1'}>⚙️ How Tara Builds Her Prediction</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs">
                  {[
                    {name:'Gap Gravity (W:35)',desc:'Distance + direction from strike, amplified by time decay. The further in profit with less time remaining, the stronger this signal.'},
                    {name:'Momentum (W:30)',desc:'1m, 5m, 15m drift readings. When all three align in the same direction, the signal gets 1.5× multiplied.'},
                    {name:'Candle Structure (W:15)',desc:'Counts consecutive same-direction candles. 3+ green with volume surge = strong bullish confirmation.'},
                    {name:'Flow Imbalance (W:20)',desc:'Real-time buy/sell delta from Binance Futures + Bybit WebSockets. Whale buying pressure directly boosts UP posterior.'},
                    {name:'Technical (W:25)',desc:'RSI divergence, VWAP position, Bollinger Band squeeze, price channel. Prevents chasing overbought tops.'},
                    {name:'Funding & Regime (W:15)',desc:'Detects SHORT SQUEEZE (retail short + whales buying), LONG SQUEEZE, TRENDING, CHOP. Adjusts thresholds per regime.'},
                  ].map((s,i)=>(
                    <div key={i} className={'bg-[#050508] rounded-lg p-2.5 border border-[#24242E]'}>
                      <div className="text-indigo-300 font-bold mb-1">{s.name}</div>
                      <div className={'text-[#EDEDED]/50 leading-relaxed'}>{s.desc}</div>
                    </div>
                  ))}
                </div>
                <p className={'text-xs text-[#EDEDED]/40 mt-2 leading-relaxed'}>Weights are not fixed — they adapt automatically after every trade using gradient descent. Signals that contributed to correct predictions grow; signals that contributed to losses shrink.</p>
              </section>

              {/* LEARNING */}
              <section>
                <h3 className={'text-amber-400 font-bold uppercase tracking-wide mb-3 text-xs border-b border-amber-500/20 pb-1'}>🧠 How Tara Learns After Every Trade</h3>
                <div className={'space-y-2 text-xs leading-relaxed text-[#EDEDED]/60'}>
                  <div className="flex gap-3"><span className="text-indigo-400 font-bold shrink-0">1.</span><p><strong className="text-white">Lock logging:</strong> When a lock fires, all 6 raw signal scores + posterior + regime + time + session are saved to a trade log.</p></div>
                  <div className="flex gap-3"><span className="text-indigo-400 font-bold shrink-0">2.</span><p><strong className="text-white">Result resolution:</strong> At window close (or manual cashout or cut), WIN or LOSS is attached to the trade record.</p></div>
                  <div className="flex gap-3"><span className="text-indigo-400 font-bold shrink-0">3.</span><p><strong className="text-white">Gradient descent:</strong> Signals that contributed correctly get their weight increased. Signals that were misleading get reduced. Learning rate: 0.8.</p></div>
                  <div className="flex gap-3"><span className="text-indigo-400 font-bold shrink-0">4.</span><p><strong className="text-white">Calibration:</strong> After 3+ trades per posterior bucket, she corrects overconfidence. If she said 80% but only won 60% of those, the displayed confidence adjusts to reflect reality.</p></div>
                  <div className="flex gap-3"><span className="text-indigo-400 font-bold shrink-0">5.</span><p><strong className="text-white">Session & hourly tracking:</strong> Tracks win rates by ASIA, EU, US session and by hour. Check the Training panel (📊 button) to find your best windows.</p></div>
                  <div className="flex gap-3"><span className="text-indigo-400 font-bold shrink-0">6.</span><p><strong className="text-white">Convergence:</strong> Weights stabilize meaningfully after ~80–100 trades. Export the CSV from Training panel and run Python logistic regression to get mathematically optimal weights.</p></div>
                </div>
              </section>

              {/* ADVISOR */}
              <section>
                <h3 className={'text-rose-400 font-bold uppercase tracking-wide mb-3 text-xs border-b border-rose-500/20 pb-1'}>🔔 Advisor Calls — In-Trade Management</h3>
                <div className="space-y-1.5 text-xs">
                  {[
                    {label:'30% STOP HIT',color:'text-rose-500',desc:'Hard floor. Position down 30% from entry. Exit immediately — no argument.'},
                    {label:'RUG PULL DETECTED',color:'text-rose-500',desc:'Tick slope + flow both collapsing while long. Flash crash in progress. Exit.'},
                    {label:'EXIT NOW — ODDS COLLAPSED',color:'text-rose-400',desc:'Win rate below 20% while in trade. Exit immediately and wait for Tara next lock signal.'},
                    {label:'TAKE MAX PROFIT',color:'text-emerald-400',desc:'Offer 40%+ above bet AND momentum reversing. Lock in exceptional returns before they vanish.'},
                    {label:'TRAILING STOP HIT',color:'text-emerald-400',desc:'Offer pulled back 12% from its peak. Momentum turned — exit before more slips.'},
                    {label:'SCALP PROFIT',color:'text-emerald-400',desc:'12%+ profit, window under 15% remaining, momentum fading. Take it — time kills you here.'},
                    {label:'CUT LOSSES — NOW',color:'text-rose-400',desc:'Win rate <38% + adverse acceleration. Getting worse faster. Exit preserves capital.'},
                    {label:'HOLD STRONG',color:'text-emerald-400',desc:'Winning + momentum aligned. Do nothing. This is the hardest discipline in trading.'},
                    {label:'RECOVERY IN PROGRESS',color:'text-amber-400',desc:'Losing but momentum just flipped toward you. Give it room — don\'t panic exit at the worst moment.'},
                  ].map((a,i)=>(
                    <div key={i} className={'flex gap-3 bg-[#050508] rounded-lg p-2 border border-[#24242E]'}>
                      <span className={`${a.color} font-bold shrink-0 min-w-[140px] text-[10px] uppercase tracking-wide`}>{a.label}</span>
                      <span className={'text-[#EDEDED]/50 text-[11px] leading-snug'}>{a.desc}</span>
                    </div>
                  ))}
                </div>
              </section>

              {/* BEST PRACTICES */}
              <section>
                <h3 className={'text-white font-bold uppercase tracking-wide mb-3 text-xs border-b border-[#24242E] pb-1'}>✅ Best Practices for Maximum Edge</h3>
                <div className={'space-y-2 text-xs leading-relaxed text-[#EDEDED]/60'}>
                  <p>🔒 <strong className="text-white">Only enter on LOCKED signals.</strong> Skip FORMING, skip SCANNING. The win rate difference between LOCKED and FORMING entries is significant.</p>
                  <p>⏰ <strong className="text-white">Enter immediately when the advisor fires ENTRY SIGNAL.</strong> The lock has been held for N consecutive samples — extra waiting only reduces your time in the trade.</p>
                  <p>📊 <strong className="text-white">Check the Training panel regularly.</strong> If ASIA session shows 40% WR but US shows 72%, only trade during US hours. This alone can dramatically improve your score.</p>
                  <p>💰 <strong className="text-white">Use Kelly criterion.</strong> The % shown under the posteriors is the mathematically optimal fraction of bankroll to risk. If Kelly says 8%, don't bet 40%.</p>
                  <p>✂️ <strong className="text-white">Never fight CUT LOSSES — NOW.</strong> It requires 3 simultaneous bearish signals. When all three fire together, the trade is structurally broken.</p>
                  <p>💎 <strong className="text-white">Always hit SCALP PROFIT near end of window.</strong> This is the most chronically ignored signal and chronically correct. Time decay in the final 90 seconds is ruthless.</p>
                  <p>📈 <strong className="text-white">Sync your position with the Entered UP or DOWN buttons.</strong> This activates the 30% stop guard and gives Tara accurate P&L context for advisor calls.</p>
                  <p>🏦 <strong className="text-white">HIGH VOL CHOP regime = avoid.</strong> Tara raises thresholds in choppy markets but even a LOCKED signal in CHOP has lower reliability. Session timing matters most here.</p>
                </div>
              </section>

              {/* STRIKE + DISCORD */}
              <section>
                <h3 className={'text-indigo-400 font-bold uppercase tracking-wide mb-3 text-xs border-b border-indigo-500/20 pb-1'}>🔗 Discord Integration</h3>
                <div className={'bg-[#050508] rounded-lg p-3 border border-[#24242E] text-xs leading-relaxed text-[#EDEDED]/60'}>
                  <p>Paste your Discord webhook URL in Settings (🔗 button). Tara will auto-broadcast:</p>
                  <ul className="mt-2 space-y-1 list-disc pl-4">
                    <li>Lock commits (with posterior, regime, gap, clock remaining)</li>
                    <li>Round closures (WIN or LOSS, closing price, regime recorded)</li>
                    <li>Manual broadcast command in chat sends a live signal embed</li>
                  </ul>
                </div>
              </section>

            </div>
          </div>
        </div>
      )}


      <style>{taraDesignStyles}</style>
      <style>{taraV14Styles+taraV145Styles}</style>
      {/* V2.1: Bottom status strip — terminal-style context bar. Frees the cards from
              displaying context that doesn't change minute-to-minute. Wraps gracefully on mobile. */}
      <div className="bg-[#0A0B0A] border-t border-[#24242E] px-3 sm:px-4 py-1.5 mt-2 -mx-2 sm:-mx-3 lg:-mx-4 -mb-2 sm:-mb-3 shrink-0">
        <div className="max-w-[1600px] mx-auto flex flex-wrap items-center gap-x-3 gap-y-1 text-[8px] sm:text-[9px] tracking-[0.04em]">
          {(()=>{
            const regimeLabel=analysis?.regime||'—';
            const sessionLabel=analysis?.session||'—';
            const velLabel=analysis?.velocityRegime||'NORMAL';
            const macroState=getMacroEventState?.()||{state:'CLEAR'};
            const macroCls=macroState.state==='BLACKOUT'?'text-rose-300':['OBSERVE','UNKNOWN'].includes(macroState.state)?'text-amber-300':'text-emerald-300';
            const geoRisk=newsSentiment?.geoRisk||0;
            const geoLabel=geoRisk>=0.7?'HIGH':geoRisk>=0.5?'ELEVATED':geoRisk>=0.3?'WATCH':'CLEAR';
            const geoColor=geoRisk>=0.5?T2_COPPER:geoRisk>=0.3?'rgba(201,125,74,0.6)':'rgba(35,185,129,0.7)';
            // V13.4.256: the per-device 15m tally that used to sit at the end of this
            //   bar was removed -- see the note at its former render site below.
            return(
              <>
                <span className="text-[#EDEDED]/35 uppercase tracking-wide">Regime</span>
                <span className="text-[#EDEDED]/70">{regimeLabel}</span>
                {(()=>{
                  // V10.7.75: show which regime strategy is active
                  const _r=analysis?.regime||'';
                  const _mode=_r==='HIGH VOL CHOP'?{label:'HIGH VOL MODE',cls:'text-emerald-400'}
                    :_r==='TRENDING UP'||_r==='TRENDING DOWN'||_r==='STRONG TREND'?{label:'TREND MODE',cls:'text-sky-400'}
                    :_r==='SHORT SQUEEZE'?{label:'SQUEEZE MODE',cls:'text-amber-400'}
                    :_r==='LONG SQUEEZE'?{label:'LONG SQUEEZE',cls:'text-orange-400'}
                    :_r==='COMPRESSING'?{label:'COILING ⚡',cls:'text-violet-400'}
                    :null;
                  if(!_mode)return null;
                  return <span className={`${_mode.cls} font-medium ml-0.5`}>({_mode.label})</span>;
                })()}
                <span className="text-[#EDEDED]/15">·</span>
                <span className="text-[#EDEDED]/35 uppercase tracking-wide">Session</span>
                <span className="text-[#EDEDED]/70">{sessionLabel}</span>
                <span className="text-[#EDEDED]/15">·</span>
                <span className="text-[#EDEDED]/35 uppercase tracking-wide">Vel</span>
                <span className="text-[#EDEDED]/70">{velLabel}</span>
                <span className="text-[#EDEDED]/15 hidden sm:inline">·</span>
                <span className="text-[#EDEDED]/35 uppercase tracking-wide hidden sm:inline">Macro</span>
                <span className={'hidden sm:inline '+macroCls}>{macroState.state||'CLEAR'}</span>
                <span className="text-[#EDEDED]/15 hidden sm:inline">·</span>
                <span className="text-[#EDEDED]/35 uppercase tracking-wide hidden sm:inline">Geo</span>
                <span className="hidden sm:inline" style={{color:geoColor}}>{geoLabel}{geoRisk>=0.3?` ${(geoRisk*100).toFixed(0)}%`:''}</span>
                {(()=>{
                  // V10.7.76: Show nearest liquidation wall in status bar
                  const _wall=liqHeatmap?.nearestWallSide;
                  if(!_wall||!liqHeatmap?.nearestWallDistBps)return null;
                  const _wDist=liqHeatmap.nearestWallDistBps;
                  const _wSize=liqHeatmap.nearestWallSize;
                  const _wColor=_wall==='above'?'rgba(35,185,129,0.7)':'rgba(232,69,94,0.7)';
                  const _wArrow=_wall==='above'?'↑':'↓';
                  if(_wDist>150)return null; // only show if within 150bps
                  return(
                    <>
                      <span className="text-[#EDEDED]/15 hidden sm:inline">·</span>
                      <span className="text-[#EDEDED]/35 uppercase tracking-wide hidden sm:inline">Wall</span>
                      <span className="hidden sm:inline font-medium" style={{color:_wColor}}>{_wArrow}{_wDist}bps ${_wSize}M</span>
                    </>
                  );
                })()}
                {/* V13.4.256: removed the per-device 15m record (e.g. "15M · 58W-37L · 61.1%").
                    It read as a second, contradictory record next to TARA'S RECORD, but it was
                    the same call log with one extra filter -- scorecards skips entries whose
                    e.device is not this device, so it showed only calls made on THIS machine
                    while the header record counts the whole pool. He is always on one machine,
                    so the split was noise that looked like a disagreement. The scorecards memo
                    stays: StatsView and the sync panel still read it. */}
              </>
            );
          })()}
        </div>
      </div>

        </div>{/* /BTC view — hidden while Sports is showing */}
    </main>
    </>
    </div>
  );
}

// ── V8.8.2: NO-RELOAD SW KILL ──────────────────────────────────────────────
// V8.8.1 caused a reload loop on the user's browser (visible in screen recording).
// V8.8.2 removes the reload entirely. The IIFE now just unregisters any service
// workers and clears caches in the background, letting React render normally on
// first load. If the deployed bundle itself is broken, ErrorBoundary catches it.
// If React can't mount at all, the 5-second safety net surfaces a manual recovery
// button. No more spasms under any circumstance.
(()=>{
  try{
    if(typeof window==='undefined'||typeof navigator==='undefined')return;
    // 5-second safety net: if React hasn't mounted anything by then, show a helpful
    // message + manual hard-refresh button. This prevents the user from staring at a
    // blank white screen with no recourse.
    setTimeout(()=>{
      try{
        const rootEl=document.getElementById('root');
        if(rootEl&&!rootEl.hasChildNodes()){
          rootEl.innerHTML='<div style="min-height:100vh;background:#050508;color:#EDEDED;padding:32px;font-family:ui-monospace,SFMono-Regular,Menlo,Consolas,monospace;display:flex;align-items:center;justify-content:center"><div style="max-width:520px"><h1 style="color:#23B981;font-size:18px;margin:0 0 16px 0;font-weight:bold;letter-spacing:0.18em;text-transform:uppercase">Tara isn\'t loading</h1><p style="color:rgba(237,237,237,0.75);margin:0 0 16px 0;line-height:1.6;font-size:13px">React didn\'t mount within 5 seconds. The deployed bundle may be stuck behind a stale cache. Click below to clear everything and reload once.</p><button onclick="(async()=>{try{if(\'serviceWorker\' in navigator){const r=await navigator.serviceWorker.getRegistrations();await Promise.all(r.map(x=>x.unregister().catch(()=>{})));}}catch(_){}try{if(\'caches\' in window){const k=await caches.keys();await Promise.all(k.map(x=>caches.delete(x).catch(()=>{})));}}catch(_){}try{sessionStorage.clear();}catch(_){}window.location.reload();})()" style="background:#10b981;color:white;border:none;padding:12px 20px;border-radius:6px;font-weight:bold;cursor:pointer;font-size:13px">Clear cache and reload</button><p style="color:rgba(237,237,237,0.4);margin:16px 0 0 0;font-size:11px">Your trade history, weights, and learning are safe — they live in Firestore and will repopulate on reload. This only clears the browser cache.</p></div></div>';
        }
      }catch(_){}
    },5000);
    if(!('serviceWorker' in navigator))return;
    // Background SW + cache cleanup. NO reload. Fire and forget.
    try{console.info('[V8.8.2] Background SW + cache cleanup (no reload).');}catch(_){}
    navigator.serviceWorker.getRegistrations().then(async(regs)=>{
      if(regs&&regs.length>0){
        try{console.info('[V8.8.2] Unregistering',regs.length,'service worker(s).');}catch(_){}
        try{await Promise.all(regs.map(r=>r.unregister().catch(()=>false)));}catch(_){}
      }
      try{
        if('caches' in window){
          const keys=await caches.keys();
          if(keys.length>0){
            await Promise.all(keys.map(k=>caches.delete(k).catch(()=>{})));
            try{console.info('[V8.8.2] Cleared',keys.length,'cache(s).');}catch(_){}
          }
        }
      }catch(_){}
    }).catch(()=>{});
  }catch(_){}
})();

export default function App(){return<ErrorBoundary><TaraApp/></ErrorBoundary>;}
